(function () {
/**
 * @license AngularJS v1.8.3
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window) {'use strict';

/* exported
  minErrConfig,
  errorHandlingConfig,
  isValidObjectMaxDepth
*/

var minErrConfig = {
  objectMaxDepth: 5,
  urlErrorParamsEnabled: true
};

/**
 * @ngdoc function
 * @name angular.errorHandlingConfig
 * @module ng
 * @kind function
 *
 * @description
 * Configure several aspects of error handling in AngularJS if used as a setter or return the
 * current configuration if used as a getter. The following options are supported:
 *
 * - **objectMaxDepth**: The maximum depth to which objects are traversed when stringified for error messages.
 *
 * Omitted or undefined options will leave the corresponding configuration values unchanged.
 *
 * @param {Object=} config - The configuration object. May only contain the options that need to be
 *     updated. Supported keys:
 *
 * * `objectMaxDepth`  **{Number}** - The max depth for stringifying objects. Setting to a
 *   non-positive or non-numeric value, removes the max depth limit.
 *   Default: 5
 *
 * * `urlErrorParamsEnabled`  **{Boolean}** - Specifies whether the generated error url will
 *   contain the parameters of the thrown error. Disabling the parameters can be useful if the
 *   generated error url is very long.
 *
 *   Default: true. When used without argument, it returns the current value.
 */
function errorHandlingConfig(config) {
  if (isObject(config)) {
    if (isDefined(config.objectMaxDepth)) {
      minErrConfig.objectMaxDepth = isValidObjectMaxDepth(config.objectMaxDepth) ? config.objectMaxDepth : NaN;
    }
    if (isDefined(config.urlErrorParamsEnabled) && isBoolean(config.urlErrorParamsEnabled)) {
      minErrConfig.urlErrorParamsEnabled = config.urlErrorParamsEnabled;
    }
  } else {
    return minErrConfig;
  }
}

/**
 * @private
 * @param {Number} maxDepth
 * @return {boolean}
 */
function isValidObjectMaxDepth(maxDepth) {
  return isNumber(maxDepth) && maxDepth > 0;
}


/**
 * @description
 *
 * This object provides a utility for producing rich Error messages within
 * AngularJS. It can be called as follows:
 *
 * var exampleMinErr = minErr('example');
 * throw exampleMinErr('one', 'This {0} is {1}', foo, bar);
 *
 * The above creates an instance of minErr in the example namespace. The
 * resulting error will have a namespaced error code of example.one.  The
 * resulting error will replace {0} with the value of foo, and {1} with the
 * value of bar. The object is not restricted in the number of arguments it can
 * take.
 *
 * If fewer arguments are specified than necessary for interpolation, the extra
 * interpolation markers will be preserved in the final string.
 *
 * Since data will be parsed statically during a build step, some restrictions
 * are applied with respect to how minErr instances are created and called.
 * Instances should have names of the form namespaceMinErr for a minErr created
 * using minErr('namespace'). Error codes, namespaces and template strings
 * should all be static strings, not variables or general expressions.
 *
 * @param {string} module The namespace to use for the new minErr instance.
 * @param {function} ErrorConstructor Custom error constructor to be instantiated when returning
 *   error from returned function, for cases when a particular type of error is useful.
 * @returns {function(code:string, template:string, ...templateArgs): Error} minErr instance
 */

function minErr(module, ErrorConstructor) {
  ErrorConstructor = ErrorConstructor || Error;

  var url = 'https://errors.angularjs.org/1.8.3/';
  var regex = url.replace('.', '\\.') + '[\\s\\S]*';
  var errRegExp = new RegExp(regex, 'g');

  return function() {
    var code = arguments[0],
      template = arguments[1],
      message = '[' + (module ? module + ':' : '') + code + '] ',
      templateArgs = sliceArgs(arguments, 2).map(function(arg) {
        return toDebugString(arg, minErrConfig.objectMaxDepth);
      }),
      paramPrefix, i;

    // A minErr message has two parts: the message itself and the url that contains the
    // encoded message.
    // The message's parameters can contain other error messages which also include error urls.
    // To prevent the messages from getting too long, we strip the error urls from the parameters.

    message += template.replace(/\{\d+\}/g, function(match) {
      var index = +match.slice(1, -1);

      if (index < templateArgs.length) {
        return templateArgs[index].replace(errRegExp, '');
      }

      return match;
    });

    message += '\n' + url + (module ? module + '/' : '') + code;

    if (minErrConfig.urlErrorParamsEnabled) {
      for (i = 0, paramPrefix = '?'; i < templateArgs.length; i++, paramPrefix = '&') {
        message += paramPrefix + 'p' + i + '=' + encodeURIComponent(templateArgs[i]);
      }
    }

    return new ErrorConstructor(message);
  };
}

/* We need to tell ESLint what variables are being exported */
/* exported
  angular,
  msie,
  jqLite,
  jQuery,
  slice,
  splice,
  push,
  toString,
  minErrConfig,
  errorHandlingConfig,
  isValidObjectMaxDepth,
  ngMinErr,
  angularModule,
  uid,
  REGEX_STRING_REGEXP,
  VALIDITY_STATE_PROPERTY,

  lowercase,
  uppercase,
  nodeName_,
  isArrayLike,
  forEach,
  forEachSorted,
  reverseParams,
  nextUid,
  setHashKey,
  extend,
  toInt,
  inherit,
  merge,
  noop,
  identity,
  valueFn,
  isUndefined,
  isDefined,
  isObject,
  isBlankObject,
  isString,
  isNumber,
  isNumberNaN,
  isDate,
  isError,
  isArray,
  isFunction,
  isRegExp,
  isWindow,
  isScope,
  isFile,
  isFormData,
  isBlob,
  isBoolean,
  isPromiseLike,
  trim,
  escapeForRegexp,
  isElement,
  makeMap,
  includes,
  arrayRemove,
  copy,
  simpleCompare,
  equals,
  csp,
  jq,
  concat,
  sliceArgs,
  bind,
  toJsonReplacer,
  toJson,
  fromJson,
  convertTimezoneToLocal,
  timezoneToOffset,
  addDateMinutes,
  startingTag,
  tryDecodeURIComponent,
  parseKeyValue,
  toKeyValue,
  encodeUriSegment,
  encodeUriQuery,
  angularInit,
  bootstrap,
  getTestability,
  snake_case,
  bindJQuery,
  assertArg,
  assertArgFn,
  assertNotHasOwnProperty,
  getter,
  getBlockNodes,
  hasOwnProperty,
  createMap,
  stringify,
  UNSAFE_restoreLegacyJqLiteXHTMLReplacement,

  NODE_TYPE_ELEMENT,
  NODE_TYPE_ATTRIBUTE,
  NODE_TYPE_TEXT,
  NODE_TYPE_COMMENT,
  NODE_TYPE_DOCUMENT,
  NODE_TYPE_DOCUMENT_FRAGMENT
*/

////////////////////////////////////

/**
 * @ngdoc module
 * @name ng
 * @module ng
 * @installation
 * @description
 *
 * The ng module is loaded by default when an AngularJS application is started. The module itself
 * contains the essential components for an AngularJS application to function. The table below
 * lists a high level breakdown of each of the services/factories, filters, directives and testing
 * components available within this core module.
 *
 */

var REGEX_STRING_REGEXP = /^\/(.+)\/([a-z]*)$/;

// The name of a form control's ValidityState property.
// This is used so that it's possible for internal tests to create mock ValidityStates.
var VALIDITY_STATE_PROPERTY = 'validity';


var hasOwnProperty = Object.prototype.hasOwnProperty;

/**
 * @private
 *
 * @description Converts the specified string to lowercase.
 * @param {string} string String to be converted to lowercase.
 * @returns {string} Lowercased string.
 */
var lowercase = function(string) {return isString(string) ? string.toLowerCase() : string;};

/**
 * @private
 *
 * @description Converts the specified string to uppercase.
 * @param {string} string String to be converted to uppercase.
 * @returns {string} Uppercased string.
 */
var uppercase = function(string) {return isString(string) ? string.toUpperCase() : string;};


var
    msie,             // holds major version number for IE, or NaN if UA is not IE.
    jqLite,           // delay binding since jQuery could be loaded after us.
    jQuery,           // delay binding
    slice             = [].slice,
    splice            = [].splice,
    push              = [].push,
    toString          = Object.prototype.toString,
    getPrototypeOf    = Object.getPrototypeOf,
    ngMinErr          = minErr('ng'),

    /** @name angular */
    angular           = window.angular || (window.angular = {}),
    angularModule,
    uid               = 0;

// Support: IE 9-11 only
/**
 * documentMode is an IE-only property
 * http://msdn.microsoft.com/en-us/library/ie/cc196988(v=vs.85).aspx
 */
msie = window.document.documentMode;


/**
 * @private
 * @param {*} obj
 * @return {boolean} Returns true if `obj` is an array or array-like object (NodeList, Arguments,
 *                   String ...)
 */
function isArrayLike(obj) {

  // `null`, `undefined` and `window` are not array-like
  if (obj == null || isWindow(obj)) return false;

  // arrays, strings and jQuery/jqLite objects are array like
  // * jqLite is either the jQuery or jqLite constructor function
  // * we have to check the existence of jqLite first as this method is called
  //   via the forEach method when constructing the jqLite object in the first place
  if (isArray(obj) || isString(obj) || (jqLite && obj instanceof jqLite)) return true;

  // Support: iOS 8.2 (not reproducible in simulator)
  // "length" in obj used to prevent JIT error (gh-11508)
  var length = 'length' in Object(obj) && obj.length;

  // NodeList objects (with `item` method) and
  // other objects with suitable length characteristics are array-like
  return isNumber(length) && (length >= 0 && (length - 1) in obj || typeof obj.item === 'function');

}

/**
 * @ngdoc function
 * @name angular.forEach
 * @module ng
 * @kind function
 *
 * @description
 * Invokes the `iterator` function once for each item in `obj` collection, which can be either an
 * object or an array. The `iterator` function is invoked with `iterator(value, key, obj)`, where `value`
 * is the value of an object property or an array element, `key` is the object property key or
 * array element index and obj is the `obj` itself. Specifying a `context` for the function is optional.
 *
 * It is worth noting that `.forEach` does not iterate over inherited properties because it filters
 * using the `hasOwnProperty` method.
 *
 * Unlike ES262's
 * [Array.prototype.forEach](http://www.ecma-international.org/ecma-262/5.1/#sec-15.4.4.18),
 * providing 'undefined' or 'null' values for `obj` will not throw a TypeError, but rather just
 * return the value provided.
 *
   ```js
     var values = {name: 'misko', gender: 'male'};
     var log = [];
     angular.forEach(values, function(value, key) {
       this.push(key + ': ' + value);
     }, log);
     expect(log).toEqual(['name: misko', 'gender: male']);
   ```
 *
 * @param {Object|Array} obj Object to iterate over.
 * @param {Function} iterator Iterator function.
 * @param {Object=} context Object to become context (`this`) for the iterator function.
 * @returns {Object|Array} Reference to `obj`.
 */

function forEach(obj, iterator, context) {
  var key, length;
  if (obj) {
    if (isFunction(obj)) {
      for (key in obj) {
        if (key !== 'prototype' && key !== 'length' && key !== 'name' && obj.hasOwnProperty(key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else if (isArray(obj) || isArrayLike(obj)) {
      var isPrimitive = typeof obj !== 'object';
      for (key = 0, length = obj.length; key < length; key++) {
        if (isPrimitive || key in obj) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else if (obj.forEach && obj.forEach !== forEach) {
        obj.forEach(iterator, context, obj);
    } else if (isBlankObject(obj)) {
      // createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
      for (key in obj) {
        iterator.call(context, obj[key], key, obj);
      }
    } else if (typeof obj.hasOwnProperty === 'function') {
      // Slow path for objects inheriting Object.prototype, hasOwnProperty check needed
      for (key in obj) {
        if (obj.hasOwnProperty(key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else {
      // Slow path for objects which do not have a method `hasOwnProperty`
      for (key in obj) {
        if (hasOwnProperty.call(obj, key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    }
  }
  return obj;
}

function forEachSorted(obj, iterator, context) {
  var keys = Object.keys(obj).sort();
  for (var i = 0; i < keys.length; i++) {
    iterator.call(context, obj[keys[i]], keys[i]);
  }
  return keys;
}


/**
 * when using forEach the params are value, key, but it is often useful to have key, value.
 * @param {function(string, *)} iteratorFn
 * @returns {function(*, string)}
 */
function reverseParams(iteratorFn) {
  return function(value, key) {iteratorFn(key, value);};
}

/**
 * A consistent way of creating unique IDs in angular.
 *
 * Using simple numbers allows us to generate 28.6 million unique ids per second for 10 years before
 * we hit number precision issues in JavaScript.
 *
 * Math.pow(2,53) / 60 / 60 / 24 / 365 / 10 = 28.6M
 *
 * @returns {number} an unique alpha-numeric string
 */
function nextUid() {
  return ++uid;
}


/**
 * Set or clear the hashkey for an object.
 * @param obj object
 * @param h the hashkey (!truthy to delete the hashkey)
 */
function setHashKey(obj, h) {
  if (h) {
    obj.$$hashKey = h;
  } else {
    delete obj.$$hashKey;
  }
}


function baseExtend(dst, objs, deep) {
  var h = dst.$$hashKey;

  for (var i = 0, ii = objs.length; i < ii; ++i) {
    var obj = objs[i];
    if (!isObject(obj) && !isFunction(obj)) continue;
    var keys = Object.keys(obj);
    for (var j = 0, jj = keys.length; j < jj; j++) {
      var key = keys[j];
      var src = obj[key];

      if (deep && isObject(src)) {
        if (isDate(src)) {
          dst[key] = new Date(src.valueOf());
        } else if (isRegExp(src)) {
          dst[key] = new RegExp(src);
        } else if (src.nodeName) {
          dst[key] = src.cloneNode(true);
        } else if (isElement(src)) {
          dst[key] = src.clone();
        } else {
          if (key !== '__proto__') {
            if (!isObject(dst[key])) dst[key] = isArray(src) ? [] : {};
            baseExtend(dst[key], [src], true);
          }
        }
      } else {
        dst[key] = src;
      }
    }
  }

  setHashKey(dst, h);
  return dst;
}

/**
 * @ngdoc function
 * @name angular.extend
 * @module ng
 * @kind function
 *
 * @description
 * Extends the destination object `dst` by copying own enumerable properties from the `src` object(s)
 * to `dst`. You can specify multiple `src` objects. If you want to preserve original objects, you can do so
 * by passing an empty object as the target: `var object = angular.extend({}, object1, object2)`.
 *
 * **Note:** Keep in mind that `angular.extend` does not support recursive merge (deep copy). Use
 * {@link angular.merge} for this.
 *
 * @param {Object} dst Destination object.
 * @param {...Object} src Source object(s).
 * @returns {Object} Reference to `dst`.
 */
function extend(dst) {
  return baseExtend(dst, slice.call(arguments, 1), false);
}


/**
* @ngdoc function
* @name angular.merge
* @module ng
* @kind function
*
* @description
* Deeply extends the destination object `dst` by copying own enumerable properties from the `src` object(s)
* to `dst`. You can specify multiple `src` objects. If you want to preserve original objects, you can do so
* by passing an empty object as the target: `var object = angular.merge({}, object1, object2)`.
*
* Unlike {@link angular.extend extend()}, `merge()` recursively descends into object properties of source
* objects, performing a deep copy.
*
* @deprecated
* sinceVersion="1.6.5"
* This function is deprecated, but will not be removed in the 1.x lifecycle.
* There are edge cases (see {@link angular.merge#known-issues known issues}) that are not
* supported by this function. We suggest using another, similar library for all-purpose merging,
* such as [lodash's merge()](https://lodash.com/docs/4.17.4#merge).
*
* @knownIssue
* This is a list of (known) object types that are not handled correctly by this function:
* - [`Blob`](https://developer.mozilla.org/docs/Web/API/Blob)
* - [`MediaStream`](https://developer.mozilla.org/docs/Web/API/MediaStream)
* - [`CanvasGradient`](https://developer.mozilla.org/docs/Web/API/CanvasGradient)
* - AngularJS {@link $rootScope.Scope scopes};
*
* `angular.merge` also does not support merging objects with circular references.
*
* @param {Object} dst Destination object.
* @param {...Object} src Source object(s).
* @returns {Object} Reference to `dst`.
*/
function merge(dst) {
  return baseExtend(dst, slice.call(arguments, 1), true);
}



function toInt(str) {
  return parseInt(str, 10);
}

var isNumberNaN = Number.isNaN || function isNumberNaN(num) {
  // eslint-disable-next-line no-self-compare
  return num !== num;
};


function inherit(parent, extra) {
  return extend(Object.create(parent), extra);
}

/**
 * @ngdoc function
 * @name angular.noop
 * @module ng
 * @kind function
 *
 * @description
 * A function that performs no operations. This function can be useful when writing code in the
 * functional style.
   ```js
     function foo(callback) {
       var result = calculateResult();
       (callback || angular.noop)(result);
     }
   ```
 */
function noop() {}
noop.$inject = [];


/**
 * @ngdoc function
 * @name angular.identity
 * @module ng
 * @kind function
 *
 * @description
 * A function that returns its first argument. This function is useful when writing code in the
 * functional style.
 *
   ```js
   function transformer(transformationFn, value) {
     return (transformationFn || angular.identity)(value);
   };

   // E.g.
   function getResult(fn, input) {
     return (fn || angular.identity)(input);
   };

   getResult(function(n) { return n * 2; }, 21);   // returns 42
   getResult(null, 21);                            // returns 21
   getResult(undefined, 21);                       // returns 21
   ```
 *
 * @param {*} value to be returned.
 * @returns {*} the value passed in.
 */
function identity($) {return $;}
identity.$inject = [];


function valueFn(value) {return function valueRef() {return value;};}

function hasCustomToString(obj) {
  return isFunction(obj.toString) && obj.toString !== toString;
}


/**
 * @ngdoc function
 * @name angular.isUndefined
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is undefined.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is undefined.
 */
function isUndefined(value) {return typeof value === 'undefined';}


/**
 * @ngdoc function
 * @name angular.isDefined
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is defined.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is defined.
 */
function isDefined(value) {return typeof value !== 'undefined';}


/**
 * @ngdoc function
 * @name angular.isObject
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is an `Object`. Unlike `typeof` in JavaScript, `null`s are not
 * considered to be objects. Note that JavaScript arrays are objects.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Object` but not `null`.
 */
function isObject(value) {
  // http://jsperf.com/isobject4
  return value !== null && typeof value === 'object';
}


/**
 * Determine if a value is an object with a null prototype
 *
 * @returns {boolean} True if `value` is an `Object` with a null prototype
 */
function isBlankObject(value) {
  return value !== null && typeof value === 'object' && !getPrototypeOf(value);
}


/**
 * @ngdoc function
 * @name angular.isString
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a `String`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `String`.
 */
function isString(value) {return typeof value === 'string';}


/**
 * @ngdoc function
 * @name angular.isNumber
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a `Number`.
 *
 * This includes the "special" numbers `NaN`, `+Infinity` and `-Infinity`.
 *
 * If you wish to exclude these then you can use the native
 * [`isFinite'](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/isFinite)
 * method.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Number`.
 */
function isNumber(value) {return typeof value === 'number';}


/**
 * @ngdoc function
 * @name angular.isDate
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a value is a date.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Date`.
 */
function isDate(value) {
  return toString.call(value) === '[object Date]';
}


/**
 * @ngdoc function
 * @name angular.isArray
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is an `Array`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Array`.
 */
function isArray(arr) {
  return Array.isArray(arr) || arr instanceof Array;
}

/**
 * @description
 * Determines if a reference is an `Error`.
 * Loosely based on https://www.npmjs.com/package/iserror
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Error`.
 */
function isError(value) {
  var tag = toString.call(value);
  switch (tag) {
    case '[object Error]': return true;
    case '[object Exception]': return true;
    case '[object DOMException]': return true;
    default: return value instanceof Error;
  }
}

/**
 * @ngdoc function
 * @name angular.isFunction
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a `Function`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Function`.
 */
function isFunction(value) {return typeof value === 'function';}


/**
 * Determines if a value is a regular expression object.
 *
 * @private
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `RegExp`.
 */
function isRegExp(value) {
  return toString.call(value) === '[object RegExp]';
}


/**
 * Checks if `obj` is a window object.
 *
 * @private
 * @param {*} obj Object to check
 * @returns {boolean} True if `obj` is a window obj.
 */
function isWindow(obj) {
  return obj && obj.window === obj;
}


function isScope(obj) {
  return obj && obj.$evalAsync && obj.$watch;
}


function isFile(obj) {
  return toString.call(obj) === '[object File]';
}


function isFormData(obj) {
  return toString.call(obj) === '[object FormData]';
}


function isBlob(obj) {
  return toString.call(obj) === '[object Blob]';
}


function isBoolean(value) {
  return typeof value === 'boolean';
}


function isPromiseLike(obj) {
  return obj && isFunction(obj.then);
}


var TYPED_ARRAY_REGEXP = /^\[object (?:Uint8|Uint8Clamped|Uint16|Uint32|Int8|Int16|Int32|Float32|Float64)Array]$/;
function isTypedArray(value) {
  return value && isNumber(value.length) && TYPED_ARRAY_REGEXP.test(toString.call(value));
}

function isArrayBuffer(obj) {
  return toString.call(obj) === '[object ArrayBuffer]';
}


var trim = function(value) {
  return isString(value) ? value.trim() : value;
};

// Copied from:
// http://docs.closure-library.googlecode.com/git/local_closure_goog_string_string.js.source.html#line1021
// Prereq: s is a string.
var escapeForRegexp = function(s) {
  return s
    .replace(/([-()[\]{}+?*.$^|,:#<!\\])/g, '\\$1')
    // eslint-disable-next-line no-control-regex
    .replace(/\x08/g, '\\x08');
};


/**
 * @ngdoc function
 * @name angular.isElement
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a DOM element (or wrapped jQuery element).
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a DOM element (or wrapped jQuery element).
 */
function isElement(node) {
  return !!(node &&
    (node.nodeName  // We are a direct element.
    || (node.prop && node.attr && node.find)));  // We have an on and find method part of jQuery API.
}

/**
 * @param str 'key1,key2,...'
 * @returns {object} in the form of {key1:true, key2:true, ...}
 */
function makeMap(str) {
  var obj = {}, items = str.split(','), i;
  for (i = 0; i < items.length; i++) {
    obj[items[i]] = true;
  }
  return obj;
}


function nodeName_(element) {
  return lowercase(element.nodeName || (element[0] && element[0].nodeName));
}

function includes(array, obj) {
  return Array.prototype.indexOf.call(array, obj) !== -1;
}

function arrayRemove(array, value) {
  var index = array.indexOf(value);
  if (index >= 0) {
    array.splice(index, 1);
  }
  return index;
}

/**
 * @ngdoc function
 * @name angular.copy
 * @module ng
 * @kind function
 *
 * @description
 * Creates a deep copy of `source`, which should be an object or an array. This functions is used
 * internally, mostly in the change-detection code. It is not intended as an all-purpose copy
 * function, and has several limitations (see below).
 *
 * * If no destination is supplied, a copy of the object or array is created.
 * * If a destination is provided, all of its elements (for arrays) or properties (for objects)
 *   are deleted and then all elements/properties from the source are copied to it.
 * * If `source` is not an object or array (inc. `null` and `undefined`), `source` is returned.
 * * If `source` is identical to `destination` an exception will be thrown.
 *
 * <br />
 *
 * <div class="alert alert-warning">
 *   Only enumerable properties are taken into account. Non-enumerable properties (both on `source`
 *   and on `destination`) will be ignored.
 * </div>
 *
 * <div class="alert alert-warning">
 *   `angular.copy` does not check if destination and source are of the same type. It's the
 *   developer's responsibility to make sure they are compatible.
 * </div>
 *
 * @knownIssue
 * This is a non-exhaustive list of object types / features that are not handled correctly by
 * `angular.copy`. Note that since this functions is used by the change detection code, this
 * means binding or watching objects of these types (or that include these types) might not work
 * correctly.
 * - [`File`](https://developer.mozilla.org/docs/Web/API/File)
 * - [`Map`](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Map)
 * - [`ImageData`](https://developer.mozilla.org/docs/Web/API/ImageData)
 * - [`MediaStream`](https://developer.mozilla.org/docs/Web/API/MediaStream)
 * - [`Set`](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Set)
 * - [`WeakMap`](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/WeakMap)
 * - [`getter`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/get)/
 *   [`setter`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/set)
 *
 * @param {*} source The source that will be used to make a copy. Can be any type, including
 *     primitives, `null`, and `undefined`.
 * @param {(Object|Array)=} destination Destination into which the source is copied. If provided,
 *     must be of the same type as `source`.
 * @returns {*} The copy or updated `destination`, if `destination` was specified.
 *
 * @example
  <example module="copyExample" name="angular-copy">
    <file name="index.html">
      <div ng-controller="ExampleController">
        <form novalidate class="simple-form">
          <label>Name: <input type="text" ng-model="user.name" /></label><br />
          <label>Age:  <input type="number" ng-model="user.age" /></label><br />
          Gender: <label><input type="radio" ng-model="user.gender" value="male" />male</label>
                  <label><input type="radio" ng-model="user.gender" value="female" />female</label><br />
          <button ng-click="reset()">RESET</button>
          <button ng-click="update(user)">SAVE</button>
        </form>
        <pre>form = {{user | json}}</pre>
        <pre>leader = {{leader | json}}</pre>
      </div>
    </file>
    <file name="script.js">
      // Module: copyExample
      angular.
        module('copyExample', []).
        controller('ExampleController', ['$scope', function($scope) {
          $scope.leader = {};

          $scope.reset = function() {
            // Example with 1 argument
            $scope.user = angular.copy($scope.leader);
          };

          $scope.update = function(user) {
            // Example with 2 arguments
            angular.copy(user, $scope.leader);
          };

          $scope.reset();
        }]);
    </file>
  </example>
 */
function copy(source, destination, maxDepth) {
  var stackSource = [];
  var stackDest = [];
  maxDepth = isValidObjectMaxDepth(maxDepth) ? maxDepth : NaN;

  if (destination) {
    if (isTypedArray(destination) || isArrayBuffer(destination)) {
      throw ngMinErr('cpta', 'Can\'t copy! TypedArray destination cannot be mutated.');
    }
    if (source === destination) {
      throw ngMinErr('cpi', 'Can\'t copy! Source and destination are identical.');
    }

    // Empty the destination object
    if (isArray(destination)) {
      destination.length = 0;
    } else {
      forEach(destination, function(value, key) {
        if (key !== '$$hashKey') {
          delete destination[key];
        }
      });
    }

    stackSource.push(source);
    stackDest.push(destination);
    return copyRecurse(source, destination, maxDepth);
  }

  return copyElement(source, maxDepth);

  function copyRecurse(source, destination, maxDepth) {
    maxDepth--;
    if (maxDepth < 0) {
      return '...';
    }
    var h = destination.$$hashKey;
    var key;
    if (isArray(source)) {
      for (var i = 0, ii = source.length; i < ii; i++) {
        destination.push(copyElement(source[i], maxDepth));
      }
    } else if (isBlankObject(source)) {
      // createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
      for (key in source) {
        destination[key] = copyElement(source[key], maxDepth);
      }
    } else if (source && typeof source.hasOwnProperty === 'function') {
      // Slow path, which must rely on hasOwnProperty
      for (key in source) {
        if (source.hasOwnProperty(key)) {
          destination[key] = copyElement(source[key], maxDepth);
        }
      }
    } else {
      // Slowest path --- hasOwnProperty can't be called as a method
      for (key in source) {
        if (hasOwnProperty.call(source, key)) {
          destination[key] = copyElement(source[key], maxDepth);
        }
      }
    }
    setHashKey(destination, h);
    return destination;
  }

  function copyElement(source, maxDepth) {
    // Simple values
    if (!isObject(source)) {
      return source;
    }

    // Already copied values
    var index = stackSource.indexOf(source);
    if (index !== -1) {
      return stackDest[index];
    }

    if (isWindow(source) || isScope(source)) {
      throw ngMinErr('cpws',
        'Can\'t copy! Making copies of Window or Scope instances is not supported.');
    }

    var needsRecurse = false;
    var destination = copyType(source);

    if (destination === undefined) {
      destination = isArray(source) ? [] : Object.create(getPrototypeOf(source));
      needsRecurse = true;
    }

    stackSource.push(source);
    stackDest.push(destination);

    return needsRecurse
      ? copyRecurse(source, destination, maxDepth)
      : destination;
  }

  function copyType(source) {
    switch (toString.call(source)) {
      case '[object Int8Array]':
      case '[object Int16Array]':
      case '[object Int32Array]':
      case '[object Float32Array]':
      case '[object Float64Array]':
      case '[object Uint8Array]':
      case '[object Uint8ClampedArray]':
      case '[object Uint16Array]':
      case '[object Uint32Array]':
        return new source.constructor(copyElement(source.buffer), source.byteOffset, source.length);

      case '[object ArrayBuffer]':
        // Support: IE10
        if (!source.slice) {
          // If we're in this case we know the environment supports ArrayBuffer
          /* eslint-disable no-undef */
          var copied = new ArrayBuffer(source.byteLength);
          new Uint8Array(copied).set(new Uint8Array(source));
          /* eslint-enable */
          return copied;
        }
        return source.slice(0);

      case '[object Boolean]':
      case '[object Number]':
      case '[object String]':
      case '[object Date]':
        return new source.constructor(source.valueOf());

      case '[object RegExp]':
        var re = new RegExp(source.source, source.toString().match(/[^/]*$/)[0]);
        re.lastIndex = source.lastIndex;
        return re;

      case '[object Blob]':
        return new source.constructor([source], {type: source.type});
    }

    if (isFunction(source.cloneNode)) {
      return source.cloneNode(true);
    }
  }
}


// eslint-disable-next-line no-self-compare
function simpleCompare(a, b) { return a === b || (a !== a && b !== b); }


/**
 * @ngdoc function
 * @name angular.equals
 * @module ng
 * @kind function
 *
 * @description
 * Determines if two objects or two values are equivalent. Supports value types, regular
 * expressions, arrays and objects.
 *
 * Two objects or values are considered equivalent if at least one of the following is true:
 *
 * * Both objects or values pass `===` comparison.
 * * Both objects or values are of the same type and all of their properties are equal by
 *   comparing them with `angular.equals`.
 * * Both values are NaN. (In JavaScript, NaN == NaN => false. But we consider two NaN as equal)
 * * Both values represent the same regular expression (In JavaScript,
 *   /abc/ == /abc/ => false. But we consider two regular expressions as equal when their textual
 *   representation matches).
 *
 * During a property comparison, properties of `function` type and properties with names
 * that begin with `$` are ignored.
 *
 * Scope and DOMWindow objects are being compared only by identify (`===`).
 *
 * @param {*} o1 Object or value to compare.
 * @param {*} o2 Object or value to compare.
 * @returns {boolean} True if arguments are equal.
 *
 * @example
   <example module="equalsExample" name="equalsExample">
     <file name="index.html">
      <div ng-controller="ExampleController">
        <form novalidate>
          <h3>User 1</h3>
          Name: <input type="text" ng-model="user1.name">
          Age: <input type="number" ng-model="user1.age">

          <h3>User 2</h3>
          Name: <input type="text" ng-model="user2.name">
          Age: <input type="number" ng-model="user2.age">

          <div>
            <br/>
            <input type="button" value="Compare" ng-click="compare()">
          </div>
          User 1: <pre>{{user1 | json}}</pre>
          User 2: <pre>{{user2 | json}}</pre>
          Equal: <pre>{{result}}</pre>
        </form>
      </div>
    </file>
    <file name="script.js">
        angular.module('equalsExample', []).controller('ExampleController', ['$scope', function($scope) {
          $scope.user1 = {};
          $scope.user2 = {};
          $scope.compare = function() {
            $scope.result = angular.equals($scope.user1, $scope.user2);
          };
        }]);
    </file>
  </example>
 */
function equals(o1, o2) {
  if (o1 === o2) return true;
  if (o1 === null || o2 === null) return false;
  // eslint-disable-next-line no-self-compare
  if (o1 !== o1 && o2 !== o2) return true; // NaN === NaN
  var t1 = typeof o1, t2 = typeof o2, length, key, keySet;
  if (t1 === t2 && t1 === 'object') {
    if (isArray(o1)) {
      if (!isArray(o2)) return false;
      if ((length = o1.length) === o2.length) {
        for (key = 0; key < length; key++) {
          if (!equals(o1[key], o2[key])) return false;
        }
        return true;
      }
    } else if (isDate(o1)) {
      if (!isDate(o2)) return false;
      return simpleCompare(o1.getTime(), o2.getTime());
    } else if (isRegExp(o1)) {
      if (!isRegExp(o2)) return false;
      return o1.toString() === o2.toString();
    } else {
      if (isScope(o1) || isScope(o2) || isWindow(o1) || isWindow(o2) ||
        isArray(o2) || isDate(o2) || isRegExp(o2)) return false;
      keySet = createMap();
      for (key in o1) {
        if (key.charAt(0) === '$' || isFunction(o1[key])) continue;
        if (!equals(o1[key], o2[key])) return false;
        keySet[key] = true;
      }
      for (key in o2) {
        if (!(key in keySet) &&
            key.charAt(0) !== '$' &&
            isDefined(o2[key]) &&
            !isFunction(o2[key])) return false;
      }
      return true;
    }
  }
  return false;
}

var csp = function() {
  if (!isDefined(csp.rules)) {


    var ngCspElement = (window.document.querySelector('[ng-csp]') ||
                    window.document.querySelector('[data-ng-csp]'));

    if (ngCspElement) {
      var ngCspAttribute = ngCspElement.getAttribute('ng-csp') ||
                    ngCspElement.getAttribute('data-ng-csp');
      csp.rules = {
        noUnsafeEval: !ngCspAttribute || (ngCspAttribute.indexOf('no-unsafe-eval') !== -1),
        noInlineStyle: !ngCspAttribute || (ngCspAttribute.indexOf('no-inline-style') !== -1)
      };
    } else {
      csp.rules = {
        noUnsafeEval: noUnsafeEval(),
        noInlineStyle: false
      };
    }
  }

  return csp.rules;

  function noUnsafeEval() {
    try {
      // eslint-disable-next-line no-new, no-new-func
      new Function('');
      return false;
    } catch (e) {
      return true;
    }
  }
};

/**
 * @ngdoc directive
 * @module ng
 * @name ngJq
 *
 * @element ANY
 * @param {string=} ngJq the name of the library available under `window`
 * to be used for angular.element
 * @description
 * Use this directive to force the angular.element library.  This should be
 * used to force either jqLite by leaving ng-jq blank or setting the name of
 * the jquery variable under window (eg. jQuery).
 *
 * Since AngularJS looks for this directive when it is loaded (doesn't wait for the
 * DOMContentLoaded event), it must be placed on an element that comes before the script
 * which loads angular. Also, only the first instance of `ng-jq` will be used and all
 * others ignored.
 *
 * @example
 * This example shows how to force jqLite using the `ngJq` directive to the `html` tag.
 ```html
 <!doctype html>
 <html ng-app ng-jq>
 ...
 ...
 </html>
 ```
 * @example
 * This example shows how to use a jQuery based library of a different name.
 * The library name must be available at the top most 'window'.
 ```html
 <!doctype html>
 <html ng-app ng-jq="jQueryLib">
 ...
 ...
 </html>
 ```
 */
var jq = function() {
  if (isDefined(jq.name_)) return jq.name_;
  var el;
  var i, ii = ngAttrPrefixes.length, prefix, name;
  for (i = 0; i < ii; ++i) {
    prefix = ngAttrPrefixes[i];
    el = window.document.querySelector('[' + prefix.replace(':', '\\:') + 'jq]');
    if (el) {
      name = el.getAttribute(prefix + 'jq');
      break;
    }
  }

  return (jq.name_ = name);
};

function concat(array1, array2, index) {
  return array1.concat(slice.call(array2, index));
}

function sliceArgs(args, startIndex) {
  return slice.call(args, startIndex || 0);
}


/**
 * @ngdoc function
 * @name angular.bind
 * @module ng
 * @kind function
 *
 * @description
 * Returns a function which calls function `fn` bound to `self` (`self` becomes the `this` for
 * `fn`). You can supply optional `args` that are prebound to the function. This feature is also
 * known as [partial application](http://en.wikipedia.org/wiki/Partial_application), as
 * distinguished from [function currying](http://en.wikipedia.org/wiki/Currying#Contrast_with_partial_function_application).
 *
 * @param {Object} self Context which `fn` should be evaluated in.
 * @param {function()} fn Function to be bound.
 * @param {...*} args Optional arguments to be prebound to the `fn` function call.
 * @returns {function()} Function that wraps the `fn` with all the specified bindings.
 */
function bind(self, fn) {
  var curryArgs = arguments.length > 2 ? sliceArgs(arguments, 2) : [];
  if (isFunction(fn) && !(fn instanceof RegExp)) {
    return curryArgs.length
      ? function() {
          return arguments.length
            ? fn.apply(self, concat(curryArgs, arguments, 0))
            : fn.apply(self, curryArgs);
        }
      : function() {
          return arguments.length
            ? fn.apply(self, arguments)
            : fn.call(self);
        };
  } else {
    // In IE, native methods are not functions so they cannot be bound (note: they don't need to be).
    return fn;
  }
}


function toJsonReplacer(key, value) {
  var val = value;

  if (typeof key === 'string' && key.charAt(0) === '$' && key.charAt(1) === '$') {
    val = undefined;
  } else if (isWindow(value)) {
    val = '$WINDOW';
  } else if (value &&  window.document === value) {
    val = '$DOCUMENT';
  } else if (isScope(value)) {
    val = '$SCOPE';
  }

  return val;
}


/**
 * @ngdoc function
 * @name angular.toJson
 * @module ng
 * @kind function
 *
 * @description
 * Serializes input into a JSON-formatted string. Properties with leading $$ characters will be
 * stripped since AngularJS uses this notation internally.
 *
 * @param {Object|Array|Date|string|number|boolean} obj Input to be serialized into JSON.
 * @param {boolean|number} [pretty=2] If set to true, the JSON output will contain newlines and whitespace.
 *    If set to an integer, the JSON output will contain that many spaces per indentation.
 * @returns {string|undefined} JSON-ified string representing `obj`.
 * @knownIssue
 *
 * The Safari browser throws a `RangeError` instead of returning `null` when it tries to stringify a `Date`
 * object with an invalid date value. The only reliable way to prevent this is to monkeypatch the
 * `Date.prototype.toJSON` method as follows:
 *
 * ```
 * var _DatetoJSON = Date.prototype.toJSON;
 * Date.prototype.toJSON = function() {
 *   try {
 *     return _DatetoJSON.call(this);
 *   } catch(e) {
 *     if (e instanceof RangeError) {
 *       return null;
 *     }
 *     throw e;
 *   }
 * };
 * ```
 *
 * See https://github.com/angular/angular.js/pull/14221 for more information.
 */
function toJson(obj, pretty) {
  if (isUndefined(obj)) return undefined;
  if (!isNumber(pretty)) {
    pretty = pretty ? 2 : null;
  }
  return JSON.stringify(obj, toJsonReplacer, pretty);
}


/**
 * @ngdoc function
 * @name angular.fromJson
 * @module ng
 * @kind function
 *
 * @description
 * Deserializes a JSON string.
 *
 * @param {string} json JSON string to deserialize.
 * @returns {Object|Array|string|number} Deserialized JSON string.
 */
function fromJson(json) {
  return isString(json)
      ? JSON.parse(json)
      : json;
}


var ALL_COLONS = /:/g;
function timezoneToOffset(timezone, fallback) {
  // Support: IE 9-11 only, Edge 13-15+
  // IE/Edge do not "understand" colon (`:`) in timezone
  timezone = timezone.replace(ALL_COLONS, '');
  var requestedTimezoneOffset = Date.parse('Jan 01, 1970 00:00:00 ' + timezone) / 60000;
  return isNumberNaN(requestedTimezoneOffset) ? fallback : requestedTimezoneOffset;
}


function addDateMinutes(date, minutes) {
  date = new Date(date.getTime());
  date.setMinutes(date.getMinutes() + minutes);
  return date;
}


function convertTimezoneToLocal(date, timezone, reverse) {
  reverse = reverse ? -1 : 1;
  var dateTimezoneOffset = date.getTimezoneOffset();
  var timezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
  return addDateMinutes(date, reverse * (timezoneOffset - dateTimezoneOffset));
}


/**
 * @returns {string} Returns the string representation of the element.
 */
function startingTag(element) {
  element = jqLite(element).clone().empty();
  var elemHtml = jqLite('<div></div>').append(element).html();
  try {
    return element[0].nodeType === NODE_TYPE_TEXT ? lowercase(elemHtml) :
        elemHtml.
          match(/^(<[^>]+>)/)[1].
          replace(/^<([\w-]+)/, function(match, nodeName) {return '<' + lowercase(nodeName);});
  } catch (e) {
    return lowercase(elemHtml);
  }

}


/////////////////////////////////////////////////

/**
 * Tries to decode the URI component without throwing an exception.
 *
 * @private
 * @param str value potential URI component to check.
 * @returns {boolean} True if `value` can be decoded
 * with the decodeURIComponent function.
 */
function tryDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    // Ignore any invalid uri component.
  }
}


/**
 * Parses an escaped url query string into key-value pairs.
 * @returns {Object.<string,boolean|Array>}
 */
function parseKeyValue(/**string*/keyValue) {
  var obj = {};
  forEach((keyValue || '').split('&'), function(keyValue) {
    var splitPoint, key, val;
    if (keyValue) {
      key = keyValue = keyValue.replace(/\+/g,'%20');
      splitPoint = keyValue.indexOf('=');
      if (splitPoint !== -1) {
        key = keyValue.substring(0, splitPoint);
        val = keyValue.substring(splitPoint + 1);
      }
      key = tryDecodeURIComponent(key);
      if (isDefined(key)) {
        val = isDefined(val) ? tryDecodeURIComponent(val) : true;
        if (!hasOwnProperty.call(obj, key)) {
          obj[key] = val;
        } else if (isArray(obj[key])) {
          obj[key].push(val);
        } else {
          obj[key] = [obj[key],val];
        }
      }
    }
  });
  return obj;
}

function toKeyValue(obj) {
  var parts = [];
  forEach(obj, function(value, key) {
    if (isArray(value)) {
      forEach(value, function(arrayValue) {
        parts.push(encodeUriQuery(key, true) +
                   (arrayValue === true ? '' : '=' + encodeUriQuery(arrayValue, true)));
      });
    } else {
    parts.push(encodeUriQuery(key, true) +
               (value === true ? '' : '=' + encodeUriQuery(value, true)));
    }
  });
  return parts.length ? parts.join('&') : '';
}


/**
 * We need our custom method because encodeURIComponent is too aggressive and doesn't follow
 * http://www.ietf.org/rfc/rfc3986.txt with regards to the character set (pchar) allowed in path
 * segments:
 *    segment       = *pchar
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 */
function encodeUriSegment(val) {
  return encodeUriQuery(val, true).
             replace(/%26/gi, '&').
             replace(/%3D/gi, '=').
             replace(/%2B/gi, '+');
}


/**
 * This method is intended for encoding *key* or *value* parts of query component. We need a custom
 * method because encodeURIComponent is too aggressive and encodes stuff that doesn't have to be
 * encoded per http://tools.ietf.org/html/rfc3986:
 *    query         = *( pchar / "/" / "?" )
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 */
function encodeUriQuery(val, pctEncodeSpaces) {
  return encodeURIComponent(val).
             replace(/%40/gi, '@').
             replace(/%3A/gi, ':').
             replace(/%24/g, '$').
             replace(/%2C/gi, ',').
             replace(/%3B/gi, ';').
             replace(/%20/g, (pctEncodeSpaces ? '%20' : '+'));
}

var ngAttrPrefixes = ['ng-', 'data-ng-', 'ng:', 'x-ng-'];

function getNgAttribute(element, ngAttr) {
  var attr, i, ii = ngAttrPrefixes.length;
  for (i = 0; i < ii; ++i) {
    attr = ngAttrPrefixes[i] + ngAttr;
    if (isString(attr = element.getAttribute(attr))) {
      return attr;
    }
  }
  return null;
}

function allowAutoBootstrap(document) {
  var script = document.currentScript;

  if (!script) {
    // Support: IE 9-11 only
    // IE does not have `document.currentScript`
    return true;
  }

  // If the `currentScript` property has been clobbered just return false, since this indicates a probable attack
  if (!(script instanceof window.HTMLScriptElement || script instanceof window.SVGScriptElement)) {
    return false;
  }

  var attributes = script.attributes;
  var srcs = [attributes.getNamedItem('src'), attributes.getNamedItem('href'), attributes.getNamedItem('xlink:href')];

  return srcs.every(function(src) {
    if (!src) {
      return true;
    }
    if (!src.value) {
      return false;
    }

    var link = document.createElement('a');
    link.href = src.value;

    if (document.location.origin === link.origin) {
      // Same-origin resources are always allowed, even for banned URL schemes.
      return true;
    }
    // Disabled bootstrapping unless angular.js was loaded from a known scheme used on the web.
    // This is to prevent angular.js bundled with browser extensions from being used to bypass the
    // content security policy in web pages and other browser extensions.
    switch (link.protocol) {
      case 'http:':
      case 'https:':
      case 'ftp:':
      case 'blob:':
      case 'file:':
      case 'data:':
        return true;
      default:
        return false;
    }
  });
}

// Cached as it has to run during loading so that document.currentScript is available.
var isAutoBootstrapAllowed = allowAutoBootstrap(window.document);

/**
 * @ngdoc directive
 * @name ngApp
 * @module ng
 *
 * @element ANY
 * @param {angular.Module} ngApp an optional application
 *   {@link angular.module module} name to load.
 * @param {boolean=} ngStrictDi if this attribute is present on the app element, the injector will be
 *   created in "strict-di" mode. This means that the application will fail to invoke functions which
 *   do not use explicit function annotation (and are thus unsuitable for minification), as described
 *   in {@link guide/di the Dependency Injection guide}, and useful debugging info will assist in
 *   tracking down the root of these bugs.
 *
 * @description
 *
 * Use this directive to **auto-bootstrap** an AngularJS application. The `ngApp` directive
 * designates the **root element** of the application and is typically placed near the root element
 * of the page - e.g. on the `<body>` or `<html>` tags.
 *
 * There are a few things to keep in mind when using `ngApp`:
 * - only one AngularJS application can be auto-bootstrapped per HTML document. The first `ngApp`
 *   found in the document will be used to define the root element to auto-bootstrap as an
 *   application. To run multiple applications in an HTML document you must manually bootstrap them using
 *   {@link angular.bootstrap} instead.
 * - AngularJS applications cannot be nested within each other.
 * - Do not use a directive that uses {@link ng.$compile#transclusion transclusion} on the same element as `ngApp`.
 *   This includes directives such as {@link ng.ngIf `ngIf`}, {@link ng.ngInclude `ngInclude`} and
 *   {@link ngRoute.ngView `ngView`}.
 *   Doing this misplaces the app {@link ng.$rootElement `$rootElement`} and the app's {@link auto.$injector injector},
 *   causing animations to stop working and making the injector inaccessible from outside the app.
 *
 * You can specify an **AngularJS module** to be used as the root module for the application.  This
 * module will be loaded into the {@link auto.$injector} when the application is bootstrapped. It
 * should contain the application code needed or have dependencies on other modules that will
 * contain the code. See {@link angular.module} for more information.
 *
 * In the example below if the `ngApp` directive were not placed on the `html` element then the
 * document would not be compiled, the `AppController` would not be instantiated and the `{{ a+b }}`
 * would not be resolved to `3`.
 *
 * @example
 *
 * ### Simple Usage
 *
 * `ngApp` is the easiest, and most common way to bootstrap an application.
 *
 <example module="ngAppDemo" name="ng-app">
   <file name="index.html">
   <div ng-controller="ngAppDemoController">
     I can add: {{a}} + {{b}} =  {{ a+b }}
   </div>
   </file>
   <file name="script.js">
   angular.module('ngAppDemo', []).controller('ngAppDemoController', function($scope) {
     $scope.a = 1;
     $scope.b = 2;
   });
   </file>
 </example>
 *
 * @example
 *
 * ### With `ngStrictDi`
 *
 * Using `ngStrictDi`, you would see something like this:
 *
 <example ng-app-included="true" name="strict-di">
   <file name="index.html">
   <div ng-app="ngAppStrictDemo" ng-strict-di>
       <div ng-controller="GoodController1">
           I can add: {{a}} + {{b}} =  {{ a+b }}

           <p>This renders because the controller does not fail to
              instantiate, by using explicit annotation style (see
              script.js for details)
           </p>
       </div>

       <div ng-controller="GoodController2">
           Name: <input ng-model="name"><br />
           Hello, {{name}}!

           <p>This renders because the controller does not fail to
              instantiate, by using explicit annotation style
              (see script.js for details)
           </p>
       </div>

       <div ng-controller="BadController">
           I can add: {{a}} + {{b}} =  {{ a+b }}

           <p>The controller could not be instantiated, due to relying
              on automatic function annotations (which are disabled in
              strict mode). As such, the content of this section is not
              interpolated, and there should be an error in your web console.
           </p>
       </div>
   </div>
   </file>
   <file name="script.js">
   angular.module('ngAppStrictDemo', [])
     // BadController will fail to instantiate, due to relying on automatic function annotation,
     // rather than an explicit annotation
     .controller('BadController', function($scope) {
       $scope.a = 1;
       $scope.b = 2;
     })
     // Unlike BadController, GoodController1 and GoodController2 will not fail to be instantiated,
     // due to using explicit annotations using the array style and $inject property, respectively.
     .controller('GoodController1', ['$scope', function($scope) {
       $scope.a = 1;
       $scope.b = 2;
     }])
     .controller('GoodController2', GoodController2);
     function GoodController2($scope) {
       $scope.name = 'World';
     }
     GoodController2.$inject = ['$scope'];
   </file>
   <file name="style.css">
   div[ng-controller] {
       margin-bottom: 1em;
       -webkit-border-radius: 4px;
       border-radius: 4px;
       border: 1px solid;
       padding: .5em;
   }
   div[ng-controller^=Good] {
       border-color: #d6e9c6;
       background-color: #dff0d8;
       color: #3c763d;
   }
   div[ng-controller^=Bad] {
       border-color: #ebccd1;
       background-color: #f2dede;
       color: #a94442;
       margin-bottom: 0;
   }
   </file>
 </example>
 */
function angularInit(element, bootstrap) {
  var appElement,
      module,
      config = {};

  // The element `element` has priority over any other element.
  forEach(ngAttrPrefixes, function(prefix) {
    var name = prefix + 'app';

    if (!appElement && element.hasAttribute && element.hasAttribute(name)) {
      appElement = element;
      module = element.getAttribute(name);
    }
  });
  forEach(ngAttrPrefixes, function(prefix) {
    var name = prefix + 'app';
    var candidate;

    if (!appElement && (candidate = element.querySelector('[' + name.replace(':', '\\:') + ']'))) {
      appElement = candidate;
      module = candidate.getAttribute(name);
    }
  });
  if (appElement) {
    if (!isAutoBootstrapAllowed) {
      window.console.error('AngularJS: disabling automatic bootstrap. <script> protocol indicates ' +
          'an extension, document.location.href does not match.');
      return;
    }
    config.strictDi = getNgAttribute(appElement, 'strict-di') !== null;
    bootstrap(appElement, module ? [module] : [], config);
  }
}

/**
 * @ngdoc function
 * @name angular.bootstrap
 * @module ng
 * @description
 * Use this function to manually start up AngularJS application.
 *
 * For more information, see the {@link guide/bootstrap Bootstrap guide}.
 *
 * AngularJS will detect if it has been loaded into the browser more than once and only allow the
 * first loaded script to be bootstrapped and will report a warning to the browser console for
 * each of the subsequent scripts. This prevents strange results in applications, where otherwise
 * multiple instances of AngularJS try to work on the DOM.
 *
 * <div class="alert alert-warning">
 * **Note:** Protractor based end-to-end tests cannot use this function to bootstrap manually.
 * They must use {@link ng.directive:ngApp ngApp}.
 * </div>
 *
 * <div class="alert alert-warning">
 * **Note:** Do not bootstrap the app on an element with a directive that uses {@link ng.$compile#transclusion transclusion},
 * such as {@link ng.ngIf `ngIf`}, {@link ng.ngInclude `ngInclude`} and {@link ngRoute.ngView `ngView`}.
 * Doing this misplaces the app {@link ng.$rootElement `$rootElement`} and the app's {@link auto.$injector injector},
 * causing animations to stop working and making the injector inaccessible from outside the app.
 * </div>
 *
 * ```html
 * <!doctype html>
 * <html>
 * <body>
 * <div ng-controller="WelcomeController">
 *   {{greeting}}
 * </div>
 *
 * <script src="angular.js"></script>
 * <script>
 *   var app = angular.module('demo', [])
 *   .controller('WelcomeController', function($scope) {
 *       $scope.greeting = 'Welcome!';
 *   });
 *   angular.bootstrap(document, ['demo']);
 * </script>
 * </body>
 * </html>
 * ```
 *
 * @param {DOMElement} element DOM element which is the root of AngularJS application.
 * @param {Array<String|Function|Array>=} modules an array of modules to load into the application.
 *     Each item in the array should be the name of a predefined module or a (DI annotated)
 *     function that will be invoked by the injector as a `config` block.
 *     See: {@link angular.module modules}
 * @param {Object=} config an object for defining configuration options for the application. The
 *     following keys are supported:
 *
 * * `strictDi` - disable automatic function annotation for the application. This is meant to
 *   assist in finding bugs which break minified code. Defaults to `false`.
 *
 * @returns {auto.$injector} Returns the newly created injector for this app.
 */
function bootstrap(element, modules, config) {
  if (!isObject(config)) config = {};
  var defaultConfig = {
    strictDi: false
  };
  config = extend(defaultConfig, config);
  var doBootstrap = function() {
    element = jqLite(element);

    if (element.injector()) {
      var tag = (element[0] === window.document) ? 'document' : startingTag(element);
      // Encode angle brackets to prevent input from being sanitized to empty string #8683.
      throw ngMinErr(
          'btstrpd',
          'App already bootstrapped with this element \'{0}\'',
          tag.replace(/</,'&lt;').replace(/>/,'&gt;'));
    }

    modules = modules || [];
    modules.unshift(['$provide', function($provide) {
      $provide.value('$rootElement', element);
    }]);

    if (config.debugInfoEnabled) {
      // Pushing so that this overrides `debugInfoEnabled` setting defined in user's `modules`.
      modules.push(['$compileProvider', function($compileProvider) {
        $compileProvider.debugInfoEnabled(true);
      }]);
    }

    modules.unshift('ng');
    var injector = createInjector(modules, config.strictDi);
    injector.invoke(['$rootScope', '$rootElement', '$compile', '$injector',
       function bootstrapApply(scope, element, compile, injector) {
        scope.$apply(function() {
          element.data('$injector', injector);
          compile(element)(scope);
        });
      }]
    );
    return injector;
  };

  var NG_ENABLE_DEBUG_INFO = /^NG_ENABLE_DEBUG_INFO!/;
  var NG_DEFER_BOOTSTRAP = /^NG_DEFER_BOOTSTRAP!/;

  if (window && NG_ENABLE_DEBUG_INFO.test(window.name)) {
    config.debugInfoEnabled = true;
    window.name = window.name.replace(NG_ENABLE_DEBUG_INFO, '');
  }

  if (window && !NG_DEFER_BOOTSTRAP.test(window.name)) {
    return doBootstrap();
  }

  window.name = window.name.replace(NG_DEFER_BOOTSTRAP, '');
  angular.resumeBootstrap = function(extraModules) {
    forEach(extraModules, function(module) {
      modules.push(module);
    });
    return doBootstrap();
  };

  if (isFunction(angular.resumeDeferredBootstrap)) {
    angular.resumeDeferredBootstrap();
  }
}

/**
 * @ngdoc function
 * @name angular.reloadWithDebugInfo
 * @module ng
 * @description
 * Use this function to reload the current application with debug information turned on.
 * This takes precedence over a call to `$compileProvider.debugInfoEnabled(false)`.
 *
 * See {@link ng.$compileProvider#debugInfoEnabled} for more.
 */
function reloadWithDebugInfo() {
  window.name = 'NG_ENABLE_DEBUG_INFO!' + window.name;
  window.location.reload();
}

/**
 * @name angular.getTestability
 * @module ng
 * @description
 * Get the testability service for the instance of AngularJS on the given
 * element.
 * @param {DOMElement} element DOM element which is the root of AngularJS application.
 */
function getTestability(rootElement) {
  var injector = angular.element(rootElement).injector();
  if (!injector) {
    throw ngMinErr('test',
      'no injector found for element argument to getTestability');
  }
  return injector.get('$$testability');
}

var SNAKE_CASE_REGEXP = /[A-Z]/g;
function snake_case(name, separator) {
  separator = separator || '_';
  return name.replace(SNAKE_CASE_REGEXP, function(letter, pos) {
    return (pos ? separator : '') + letter.toLowerCase();
  });
}

var bindJQueryFired = false;
function bindJQuery() {
  var originalCleanData;

  if (bindJQueryFired) {
    return;
  }

  // bind to jQuery if present;
  var jqName = jq();
  jQuery = isUndefined(jqName) ? window.jQuery :   // use jQuery (if present)
           !jqName             ? undefined     :   // use jqLite
                                 window[jqName];   // use jQuery specified by `ngJq`

  // Use jQuery if it exists with proper functionality, otherwise default to us.
  // AngularJS 1.2+ requires jQuery 1.7+ for on()/off() support.
  // AngularJS 1.3+ technically requires at least jQuery 2.1+ but it may work with older
  // versions. It will not work for sure with jQuery <1.7, though.
  if (jQuery && jQuery.fn.on) {
    jqLite = jQuery;
    extend(jQuery.fn, {
      scope: JQLitePrototype.scope,
      isolateScope: JQLitePrototype.isolateScope,
      controller: /** @type {?} */ (JQLitePrototype).controller,
      injector: JQLitePrototype.injector,
      inheritedData: JQLitePrototype.inheritedData
    });
  } else {
    jqLite = JQLite;
  }

  // All nodes removed from the DOM via various jqLite/jQuery APIs like .remove()
  // are passed through jqLite/jQuery.cleanData. Monkey-patch this method to fire
  // the $destroy event on all removed nodes.
  originalCleanData = jqLite.cleanData;
  jqLite.cleanData = function(elems) {
    var events;
    for (var i = 0, elem; (elem = elems[i]) != null; i++) {
      events = (jqLite._data(elem) || {}).events;
      if (events && events.$destroy) {
        jqLite(elem).triggerHandler('$destroy');
      }
    }
    originalCleanData(elems);
  };

  angular.element = jqLite;

  // Prevent double-proxying.
  bindJQueryFired = true;
}

/**
 * @ngdoc function
 * @name angular.UNSAFE_restoreLegacyJqLiteXHTMLReplacement
 * @module ng
 * @kind function
 *
 * @description
 * Restores the pre-1.8 behavior of jqLite that turns XHTML-like strings like
 * `<div /><span />` to `<div></div><span></span>` instead of `<div><span></span></div>`.
 * The new behavior is a security fix. Thus, if you need to call this function, please try to adjust
 * your code for this change and remove your use of this function as soon as possible.

 * Note that this only patches jqLite. If you use jQuery 3.5.0 or newer, please read the
 * [jQuery 3.5 upgrade guide](https://jquery.com/upgrade-guide/3.5/) for more details
 * about the workarounds.
 */
function UNSAFE_restoreLegacyJqLiteXHTMLReplacement() {
  JQLite.legacyXHTMLReplacement = true;
}

/**
 * throw error if the argument is falsy.
 */
function assertArg(arg, name, reason) {
  if (!arg) {
    throw ngMinErr('areq', 'Argument \'{0}\' is {1}', (name || '?'), (reason || 'required'));
  }
  return arg;
}

function assertArgFn(arg, name, acceptArrayAnnotation) {
  if (acceptArrayAnnotation && isArray(arg)) {
      arg = arg[arg.length - 1];
  }

  assertArg(isFunction(arg), name, 'not a function, got ' +
      (arg && typeof arg === 'object' ? arg.constructor.name || 'Object' : typeof arg));
  return arg;
}

/**
 * throw error if the name given is hasOwnProperty
 * @param  {String} name    the name to test
 * @param  {String} context the context in which the name is used, such as module or directive
 */
function assertNotHasOwnProperty(name, context) {
  if (name === 'hasOwnProperty') {
    throw ngMinErr('badname', 'hasOwnProperty is not a valid {0} name', context);
  }
}

/**
 * Return the value accessible from the object by path. Any undefined traversals are ignored
 * @param {Object} obj starting object
 * @param {String} path path to traverse
 * @param {boolean} [bindFnToScope=true]
 * @returns {Object} value as accessible by path
 */
//TODO(misko): this function needs to be removed
function getter(obj, path, bindFnToScope) {
  if (!path) return obj;
  var keys = path.split('.');
  var key;
  var lastInstance = obj;
  var len = keys.length;

  for (var i = 0; i < len; i++) {
    key = keys[i];
    if (obj) {
      obj = (lastInstance = obj)[key];
    }
  }
  if (!bindFnToScope && isFunction(obj)) {
    return bind(lastInstance, obj);
  }
  return obj;
}

/**
 * Return the DOM siblings between the first and last node in the given array.
 * @param {Array} array like object
 * @returns {Array} the inputted object or a jqLite collection containing the nodes
 */
function getBlockNodes(nodes) {
  // TODO(perf): update `nodes` instead of creating a new object?
  var node = nodes[0];
  var endNode = nodes[nodes.length - 1];
  var blockNodes;

  for (var i = 1; node !== endNode && (node = node.nextSibling); i++) {
    if (blockNodes || nodes[i] !== node) {
      if (!blockNodes) {
        blockNodes = jqLite(slice.call(nodes, 0, i));
      }
      blockNodes.push(node);
    }
  }

  return blockNodes || nodes;
}


/**
 * Creates a new object without a prototype. This object is useful for lookup without having to
 * guard against prototypically inherited properties via hasOwnProperty.
 *
 * Related micro-benchmarks:
 * - http://jsperf.com/object-create2
 * - http://jsperf.com/proto-map-lookup/2
 * - http://jsperf.com/for-in-vs-object-keys2
 *
 * @returns {Object}
 */
function createMap() {
  return Object.create(null);
}

function stringify(value) {
  if (value == null) { // null || undefined
    return '';
  }
  switch (typeof value) {
    case 'string':
      break;
    case 'number':
      value = '' + value;
      break;
    default:
      if (hasCustomToString(value) && !isArray(value) && !isDate(value)) {
        value = value.toString();
      } else {
        value = toJson(value);
      }
  }

  return value;
}

var NODE_TYPE_ELEMENT = 1;
var NODE_TYPE_ATTRIBUTE = 2;
var NODE_TYPE_TEXT = 3;
var NODE_TYPE_COMMENT = 8;
var NODE_TYPE_DOCUMENT = 9;
var NODE_TYPE_DOCUMENT_FRAGMENT = 11;

/**
 * @ngdoc type
 * @name angular.Module
 * @module ng
 * @description
 *
 * Interface for configuring AngularJS {@link angular.module modules}.
 */

function setupModuleLoader(window) {

  var $injectorMinErr = minErr('$injector');
  var ngMinErr = minErr('ng');

  function ensure(obj, name, factory) {
    return obj[name] || (obj[name] = factory());
  }

  var angular = ensure(window, 'angular', Object);

  // We need to expose `angular.$$minErr` to modules such as `ngResource` that reference it during bootstrap
  angular.$$minErr = angular.$$minErr || minErr;

  return ensure(angular, 'module', function() {
    /** @type {Object.<string, angular.Module>} */
    var modules = {};

    /**
     * @ngdoc function
     * @name angular.module
     * @module ng
     * @description
     *
     * The `angular.module` is a global place for creating, registering and retrieving AngularJS
     * modules.
     * All modules (AngularJS core or 3rd party) that should be available to an application must be
     * registered using this mechanism.
     *
     * Passing one argument retrieves an existing {@link angular.Module},
     * whereas passing more than one argument creates a new {@link angular.Module}
     *
     *
     * # Module
     *
     * A module is a collection of services, directives, controllers, filters, and configuration information.
     * `angular.module` is used to configure the {@link auto.$injector $injector}.
     *
     * ```js
     * // Create a new module
     * var myModule = angular.module('myModule', []);
     *
     * // register a new service
     * myModule.value('appName', 'MyCoolApp');
     *
     * // configure existing services inside initialization blocks.
     * myModule.config(['$locationProvider', function($locationProvider) {
     *   // Configure existing providers
     *   $locationProvider.hashPrefix('!');
     * }]);
     * ```
     *
     * Then you can create an injector and load your modules like this:
     *
     * ```js
     * var injector = angular.injector(['ng', 'myModule'])
     * ```
     *
     * However it's more likely that you'll just use
     * {@link ng.directive:ngApp ngApp} or
     * {@link angular.bootstrap} to simplify this process for you.
     *
     * @param {!string} name The name of the module to create or retrieve.
     * @param {!Array.<string>=} requires If specified then new module is being created. If
     *        unspecified then the module is being retrieved for further configuration.
     * @param {Function=} configFn Optional configuration function for the module. Same as
     *        {@link angular.Module#config Module#config()}.
     * @returns {angular.Module} new module with the {@link angular.Module} api.
     */
    return function module(name, requires, configFn) {

      var info = {};

      var assertNotHasOwnProperty = function(name, context) {
        if (name === 'hasOwnProperty') {
          throw ngMinErr('badname', 'hasOwnProperty is not a valid {0} name', context);
        }
      };

      assertNotHasOwnProperty(name, 'module');
      if (requires && modules.hasOwnProperty(name)) {
        modules[name] = null;
      }
      return ensure(modules, name, function() {
        if (!requires) {
          throw $injectorMinErr('nomod', 'Module \'{0}\' is not available! You either misspelled ' +
             'the module name or forgot to load it. If registering a module ensure that you ' +
             'specify the dependencies as the second argument.', name);
        }

        /** @type {!Array.<Array.<*>>} */
        var invokeQueue = [];

        /** @type {!Array.<Function>} */
        var configBlocks = [];

        /** @type {!Array.<Function>} */
        var runBlocks = [];

        var config = invokeLater('$injector', 'invoke', 'push', configBlocks);

        /** @type {angular.Module} */
        var moduleInstance = {
          // Private state
          _invokeQueue: invokeQueue,
          _configBlocks: configBlocks,
          _runBlocks: runBlocks,

          /**
           * @ngdoc method
           * @name angular.Module#info
           * @module ng
           *
           * @param {Object=} info Information about the module
           * @returns {Object|Module} The current info object for this module if called as a getter,
           *                          or `this` if called as a setter.
           *
           * @description
           * Read and write custom information about this module.
           * For example you could put the version of the module in here.
           *
           * ```js
           * angular.module('myModule', []).info({ version: '1.0.0' });
           * ```
           *
           * The version could then be read back out by accessing the module elsewhere:
           *
           * ```
           * var version = angular.module('myModule').info().version;
           * ```
           *
           * You can also retrieve this information during runtime via the
           * {@link $injector#modules `$injector.modules`} property:
           *
           * ```js
           * var version = $injector.modules['myModule'].info().version;
           * ```
           */
          info: function(value) {
            if (isDefined(value)) {
              if (!isObject(value)) throw ngMinErr('aobj', 'Argument \'{0}\' must be an object', 'value');
              info = value;
              return this;
            }
            return info;
          },

          /**
           * @ngdoc property
           * @name angular.Module#requires
           * @module ng
           *
           * @description
           * Holds the list of modules which the injector will load before the current module is
           * loaded.
           */
          requires: requires,

          /**
           * @ngdoc property
           * @name angular.Module#name
           * @module ng
           *
           * @description
           * Name of the module.
           */
          name: name,


          /**
           * @ngdoc method
           * @name angular.Module#provider
           * @module ng
           * @param {string} name service name
           * @param {Function} providerType Construction function for creating new instance of the
           *                                service.
           * @description
           * See {@link auto.$provide#provider $provide.provider()}.
           */
          provider: invokeLaterAndSetModuleName('$provide', 'provider'),

          /**
           * @ngdoc method
           * @name angular.Module#factory
           * @module ng
           * @param {string} name service name
           * @param {Function} providerFunction Function for creating new instance of the service.
           * @description
           * See {@link auto.$provide#factory $provide.factory()}.
           */
          factory: invokeLaterAndSetModuleName('$provide', 'factory'),

          /**
           * @ngdoc method
           * @name angular.Module#service
           * @module ng
           * @param {string} name service name
           * @param {Function} constructor A constructor function that will be instantiated.
           * @description
           * See {@link auto.$provide#service $provide.service()}.
           */
          service: invokeLaterAndSetModuleName('$provide', 'service'),

          /**
           * @ngdoc method
           * @name angular.Module#value
           * @module ng
           * @param {string} name service name
           * @param {*} object Service instance object.
           * @description
           * See {@link auto.$provide#value $provide.value()}.
           */
          value: invokeLater('$provide', 'value'),

          /**
           * @ngdoc method
           * @name angular.Module#constant
           * @module ng
           * @param {string} name constant name
           * @param {*} object Constant value.
           * @description
           * Because the constants are fixed, they get applied before other provide methods.
           * See {@link auto.$provide#constant $provide.constant()}.
           */
          constant: invokeLater('$provide', 'constant', 'unshift'),

           /**
           * @ngdoc method
           * @name angular.Module#decorator
           * @module ng
           * @param {string} name The name of the service to decorate.
           * @param {Function} decorFn This function will be invoked when the service needs to be
           *                           instantiated and should return the decorated service instance.
           * @description
           * See {@link auto.$provide#decorator $provide.decorator()}.
           */
          decorator: invokeLaterAndSetModuleName('$provide', 'decorator', configBlocks),

          /**
           * @ngdoc method
           * @name angular.Module#animation
           * @module ng
           * @param {string} name animation name
           * @param {Function} animationFactory Factory function for creating new instance of an
           *                                    animation.
           * @description
           *
           * **NOTE**: animations take effect only if the **ngAnimate** module is loaded.
           *
           *
           * Defines an animation hook that can be later used with
           * {@link $animate $animate} service and directives that use this service.
           *
           * ```js
           * module.animation('.animation-name', function($inject1, $inject2) {
           *   return {
           *     eventName : function(element, done) {
           *       //code to run the animation
           *       //once complete, then run done()
           *       return function cancellationFunction(element) {
           *         //code to cancel the animation
           *       }
           *     }
           *   }
           * })
           * ```
           *
           * See {@link ng.$animateProvider#register $animateProvider.register()} and
           * {@link ngAnimate ngAnimate module} for more information.
           */
          animation: invokeLaterAndSetModuleName('$animateProvider', 'register'),

          /**
           * @ngdoc method
           * @name angular.Module#filter
           * @module ng
           * @param {string} name Filter name - this must be a valid AngularJS expression identifier
           * @param {Function} filterFactory Factory function for creating new instance of filter.
           * @description
           * See {@link ng.$filterProvider#register $filterProvider.register()}.
           *
           * <div class="alert alert-warning">
           * **Note:** Filter names must be valid AngularJS {@link expression} identifiers, such as `uppercase` or `orderBy`.
           * Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
           * your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
           * (`myapp_subsection_filterx`).
           * </div>
           */
          filter: invokeLaterAndSetModuleName('$filterProvider', 'register'),

          /**
           * @ngdoc method
           * @name angular.Module#controller
           * @module ng
           * @param {string|Object} name Controller name, or an object map of controllers where the
           *    keys are the names and the values are the constructors.
           * @param {Function} constructor Controller constructor function.
           * @description
           * See {@link ng.$controllerProvider#register $controllerProvider.register()}.
           */
          controller: invokeLaterAndSetModuleName('$controllerProvider', 'register'),

          /**
           * @ngdoc method
           * @name angular.Module#directive
           * @module ng
           * @param {string|Object} name Directive name, or an object map of directives where the
           *    keys are the names and the values are the factories.
           * @param {Function} directiveFactory Factory function for creating new instance of
           * directives.
           * @description
           * See {@link ng.$compileProvider#directive $compileProvider.directive()}.
           */
          directive: invokeLaterAndSetModuleName('$compileProvider', 'directive'),

          /**
           * @ngdoc method
           * @name angular.Module#component
           * @module ng
           * @param {string|Object} name Name of the component in camelCase (i.e. `myComp` which will match `<my-comp>`),
           *    or an object map of components where the keys are the names and the values are the component definition objects.
           * @param {Object} options Component definition object (a simplified
           *    {@link ng.$compile#directive-definition-object directive definition object})
           *
           * @description
           * See {@link ng.$compileProvider#component $compileProvider.component()}.
           */
          component: invokeLaterAndSetModuleName('$compileProvider', 'component'),

          /**
           * @ngdoc method
           * @name angular.Module#config
           * @module ng
           * @param {Function} configFn Execute this function on module load. Useful for service
           *    configuration.
           * @description
           * Use this method to configure services by injecting their
           * {@link angular.Module#provider `providers`}, e.g. for adding routes to the
           * {@link ngRoute.$routeProvider $routeProvider}.
           *
           * Note that you can only inject {@link angular.Module#provider `providers`} and
           * {@link angular.Module#constant `constants`} into this function.
           *
           * For more about how to configure services, see
           * {@link providers#provider-recipe Provider Recipe}.
           */
          config: config,

          /**
           * @ngdoc method
           * @name angular.Module#run
           * @module ng
           * @param {Function} initializationFn Execute this function after injector creation.
           *    Useful for application initialization.
           * @description
           * Use this method to register work which should be performed when the injector is done
           * loading all modules.
           */
          run: function(block) {
            runBlocks.push(block);
            return this;
          }
        };

        if (configFn) {
          config(configFn);
        }

        return moduleInstance;

        /**
         * @param {string} provider
         * @param {string} method
         * @param {String=} insertMethod
         * @returns {angular.Module}
         */
        function invokeLater(provider, method, insertMethod, queue) {
          if (!queue) queue = invokeQueue;
          return function() {
            queue[insertMethod || 'push']([provider, method, arguments]);
            return moduleInstance;
          };
        }

        /**
         * @param {string} provider
         * @param {string} method
         * @returns {angular.Module}
         */
        function invokeLaterAndSetModuleName(provider, method, queue) {
          if (!queue) queue = invokeQueue;
          return function(recipeName, factoryFunction) {
            if (factoryFunction && isFunction(factoryFunction)) factoryFunction.$$moduleName = name;
            queue.push([provider, method, arguments]);
            return moduleInstance;
          };
        }
      });
    };
  });

}

/* global shallowCopy: true */

/**
 * Creates a shallow copy of an object, an array or a primitive.
 *
 * Assumes that there are no proto properties for objects.
 */
function shallowCopy(src, dst) {
  if (isArray(src)) {
    dst = dst || [];

    for (var i = 0, ii = src.length; i < ii; i++) {
      dst[i] = src[i];
    }
  } else if (isObject(src)) {
    dst = dst || {};

    for (var key in src) {
      if (!(key.charAt(0) === '$' && key.charAt(1) === '$')) {
        dst[key] = src[key];
      }
    }
  }

  return dst || src;
}

/* exported toDebugString */

function serializeObject(obj, maxDepth) {
  var seen = [];

  // There is no direct way to stringify object until reaching a specific depth
  // and a very deep object can cause a performance issue, so we copy the object
  // based on this specific depth and then stringify it.
  if (isValidObjectMaxDepth(maxDepth)) {
    // This file is also included in `angular-loader`, so `copy()` might not always be available in
    // the closure. Therefore, it is lazily retrieved as `angular.copy()` when needed.
    obj = angular.copy(obj, null, maxDepth);
  }
  return JSON.stringify(obj, function(key, val) {
    val = toJsonReplacer(key, val);
    if (isObject(val)) {

      if (seen.indexOf(val) >= 0) return '...';

      seen.push(val);
    }
    return val;
  });
}

function toDebugString(obj, maxDepth) {
  if (typeof obj === 'function') {
    return obj.toString().replace(/ \{[\s\S]*$/, '');
  } else if (isUndefined(obj)) {
    return 'undefined';
  } else if (typeof obj !== 'string') {
    return serializeObject(obj, maxDepth);
  }
  return obj;
}

/* global angularModule: true,
  version: true,

  $CompileProvider,

  htmlAnchorDirective,
  inputDirective,
  hiddenInputBrowserCacheDirective,
  formDirective,
  scriptDirective,
  selectDirective,
  optionDirective,
  ngBindDirective,
  ngBindHtmlDirective,
  ngBindTemplateDirective,
  ngClassDirective,
  ngClassEvenDirective,
  ngClassOddDirective,
  ngCloakDirective,
  ngControllerDirective,
  ngFormDirective,
  ngHideDirective,
  ngIfDirective,
  ngIncludeDirective,
  ngIncludeFillContentDirective,
  ngInitDirective,
  ngNonBindableDirective,
  ngPluralizeDirective,
  ngRefDirective,
  ngRepeatDirective,
  ngShowDirective,
  ngStyleDirective,
  ngSwitchDirective,
  ngSwitchWhenDirective,
  ngSwitchDefaultDirective,
  ngOptionsDirective,
  ngTranscludeDirective,
  ngModelDirective,
  ngListDirective,
  ngChangeDirective,
  patternDirective,
  patternDirective,
  requiredDirective,
  requiredDirective,
  minlengthDirective,
  minlengthDirective,
  maxlengthDirective,
  maxlengthDirective,
  ngValueDirective,
  ngModelOptionsDirective,
  ngAttributeAliasDirectives,
  ngEventDirectives,

  $AnchorScrollProvider,
  $AnimateProvider,
  $CoreAnimateCssProvider,
  $$CoreAnimateJsProvider,
  $$CoreAnimateQueueProvider,
  $$AnimateRunnerFactoryProvider,
  $$AnimateAsyncRunFactoryProvider,
  $BrowserProvider,
  $CacheFactoryProvider,
  $ControllerProvider,
  $DateProvider,
  $DocumentProvider,
  $$IsDocumentHiddenProvider,
  $ExceptionHandlerProvider,
  $FilterProvider,
  $$ForceReflowProvider,
  $InterpolateProvider,
  $$IntervalFactoryProvider,
  $IntervalProvider,
  $HttpProvider,
  $HttpParamSerializerProvider,
  $HttpParamSerializerJQLikeProvider,
  $HttpBackendProvider,
  $xhrFactoryProvider,
  $jsonpCallbacksProvider,
  $LocationProvider,
  $LogProvider,
  $$MapProvider,
  $ParseProvider,
  $RootScopeProvider,
  $QProvider,
  $$QProvider,
  $$SanitizeUriProvider,
  $SceProvider,
  $SceDelegateProvider,
  $SnifferProvider,
  $$TaskTrackerFactoryProvider,
  $TemplateCacheProvider,
  $TemplateRequestProvider,
  $$TestabilityProvider,
  $TimeoutProvider,
  $$RAFProvider,
  $WindowProvider,
  $$jqLiteProvider,
  $$CookieReaderProvider
*/


/**
 * @ngdoc object
 * @name angular.version
 * @module ng
 * @description
 * An object that contains information about the current AngularJS version.
 *
 * This object has the following properties:
 *
 * - `full` – `{string}` – Full version string, such as "0.9.18".
 * - `major` – `{number}` – Major version number, such as "0".
 * - `minor` – `{number}` – Minor version number, such as "9".
 * - `dot` – `{number}` – Dot version number, such as "18".
 * - `codeName` – `{string}` – Code name of the release, such as "jiggling-armfat".
 */
var version = {
  // These placeholder strings will be replaced by grunt's `build` task.
  // They need to be double- or single-quoted.
  full: '1.8.3',
  major: 1,
  minor: 8,
  dot: 3,
  codeName: 'ultimate-farewell'
};


function publishExternalAPI(angular) {
  extend(angular, {
    'errorHandlingConfig': errorHandlingConfig,
    'bootstrap': bootstrap,
    'copy': copy,
    'extend': extend,
    'merge': merge,
    'equals': equals,
    'element': jqLite,
    'forEach': forEach,
    'injector': createInjector,
    'noop': noop,
    'bind': bind,
    'toJson': toJson,
    'fromJson': fromJson,
    'identity': identity,
    'isUndefined': isUndefined,
    'isDefined': isDefined,
    'isString': isString,
    'isFunction': isFunction,
    'isObject': isObject,
    'isNumber': isNumber,
    'isElement': isElement,
    'isArray': isArray,
    'version': version,
    'isDate': isDate,
    'callbacks': {$$counter: 0},
    'getTestability': getTestability,
    'reloadWithDebugInfo': reloadWithDebugInfo,
    'UNSAFE_restoreLegacyJqLiteXHTMLReplacement': UNSAFE_restoreLegacyJqLiteXHTMLReplacement,
    '$$minErr': minErr,
    '$$csp': csp,
    '$$encodeUriSegment': encodeUriSegment,
    '$$encodeUriQuery': encodeUriQuery,
    '$$lowercase': lowercase,
    '$$stringify': stringify,
    '$$uppercase': uppercase
  });

  angularModule = setupModuleLoader(window);

  angularModule('ng', ['ngLocale'], ['$provide',
    function ngModule($provide) {
      // $$sanitizeUriProvider needs to be before $compileProvider as it is used by it.
      $provide.provider({
        $$sanitizeUri: $$SanitizeUriProvider
      });
      $provide.provider('$compile', $CompileProvider).
        directive({
            a: htmlAnchorDirective,
            input: inputDirective,
            textarea: inputDirective,
            form: formDirective,
            script: scriptDirective,
            select: selectDirective,
            option: optionDirective,
            ngBind: ngBindDirective,
            ngBindHtml: ngBindHtmlDirective,
            ngBindTemplate: ngBindTemplateDirective,
            ngClass: ngClassDirective,
            ngClassEven: ngClassEvenDirective,
            ngClassOdd: ngClassOddDirective,
            ngCloak: ngCloakDirective,
            ngController: ngControllerDirective,
            ngForm: ngFormDirective,
            ngHide: ngHideDirective,
            ngIf: ngIfDirective,
            ngInclude: ngIncludeDirective,
            ngInit: ngInitDirective,
            ngNonBindable: ngNonBindableDirective,
            ngPluralize: ngPluralizeDirective,
            ngRef: ngRefDirective,
            ngRepeat: ngRepeatDirective,
            ngShow: ngShowDirective,
            ngStyle: ngStyleDirective,
            ngSwitch: ngSwitchDirective,
            ngSwitchWhen: ngSwitchWhenDirective,
            ngSwitchDefault: ngSwitchDefaultDirective,
            ngOptions: ngOptionsDirective,
            ngTransclude: ngTranscludeDirective,
            ngModel: ngModelDirective,
            ngList: ngListDirective,
            ngChange: ngChangeDirective,
            pattern: patternDirective,
            ngPattern: patternDirective,
            required: requiredDirective,
            ngRequired: requiredDirective,
            minlength: minlengthDirective,
            ngMinlength: minlengthDirective,
            maxlength: maxlengthDirective,
            ngMaxlength: maxlengthDirective,
            ngValue: ngValueDirective,
            ngModelOptions: ngModelOptionsDirective
        }).
        directive({
          ngInclude: ngIncludeFillContentDirective,
          input: hiddenInputBrowserCacheDirective
        }).
        directive(ngAttributeAliasDirectives).
        directive(ngEventDirectives);
      $provide.provider({
        $anchorScroll: $AnchorScrollProvider,
        $animate: $AnimateProvider,
        $animateCss: $CoreAnimateCssProvider,
        $$animateJs: $$CoreAnimateJsProvider,
        $$animateQueue: $$CoreAnimateQueueProvider,
        $$AnimateRunner: $$AnimateRunnerFactoryProvider,
        $$animateAsyncRun: $$AnimateAsyncRunFactoryProvider,
        $browser: $BrowserProvider,
        $cacheFactory: $CacheFactoryProvider,
        $controller: $ControllerProvider,
        $document: $DocumentProvider,
        $$isDocumentHidden: $$IsDocumentHiddenProvider,
        $exceptionHandler: $ExceptionHandlerProvider,
        $filter: $FilterProvider,
        $$forceReflow: $$ForceReflowProvider,
        $interpolate: $InterpolateProvider,
        $interval: $IntervalProvider,
        $$intervalFactory: $$IntervalFactoryProvider,
        $http: $HttpProvider,
        $httpParamSerializer: $HttpParamSerializerProvider,
        $httpParamSerializerJQLike: $HttpParamSerializerJQLikeProvider,
        $httpBackend: $HttpBackendProvider,
        $xhrFactory: $xhrFactoryProvider,
        $jsonpCallbacks: $jsonpCallbacksProvider,
        $location: $LocationProvider,
        $log: $LogProvider,
        $parse: $ParseProvider,
        $rootScope: $RootScopeProvider,
        $q: $QProvider,
        $$q: $$QProvider,
        $sce: $SceProvider,
        $sceDelegate: $SceDelegateProvider,
        $sniffer: $SnifferProvider,
        $$taskTrackerFactory: $$TaskTrackerFactoryProvider,
        $templateCache: $TemplateCacheProvider,
        $templateRequest: $TemplateRequestProvider,
        $$testability: $$TestabilityProvider,
        $timeout: $TimeoutProvider,
        $window: $WindowProvider,
        $$rAF: $$RAFProvider,
        $$jqLite: $$jqLiteProvider,
        $$Map: $$MapProvider,
        $$cookieReader: $$CookieReaderProvider
      });
    }
  ])
  .info({ angularVersion: '1.8.3' });
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/* global
  JQLitePrototype: true,
  BOOLEAN_ATTR: true,
  ALIASED_ATTR: true
*/

//////////////////////////////////
//JQLite
//////////////////////////////////

/**
 * @ngdoc function
 * @name angular.element
 * @module ng
 * @kind function
 *
 * @description
 * Wraps a raw DOM element or HTML string as a [jQuery](http://jquery.com) element.
 *
 * If jQuery is available, `angular.element` is an alias for the
 * [jQuery](http://api.jquery.com/jQuery/) function. If jQuery is not available, `angular.element`
 * delegates to AngularJS's built-in subset of jQuery, called "jQuery lite" or **jqLite**.
 *
 * jqLite is a tiny, API-compatible subset of jQuery that allows
 * AngularJS to manipulate the DOM in a cross-browser compatible way. jqLite implements only the most
 * commonly needed functionality with the goal of having a very small footprint.
 *
 * To use `jQuery`, simply ensure it is loaded before the `angular.js` file. You can also use the
 * {@link ngJq `ngJq`} directive to specify that jqlite should be used over jQuery, or to use a
 * specific version of jQuery if multiple versions exist on the page.
 *
 * <div class="alert alert-info">**Note:** All element references in AngularJS are always wrapped with jQuery or
 * jqLite (such as the element argument in a directive's compile / link function). They are never raw DOM references.</div>
 *
 * <div class="alert alert-warning">**Note:** Keep in mind that this function will not find elements
 * by tag name / CSS selector. For lookups by tag name, try instead `angular.element(document).find(...)`
 * or `$document.find()`, or use the standard DOM APIs, e.g. `document.querySelectorAll()`.</div>
 *
 * ## AngularJS's jqLite
 * jqLite provides only the following jQuery methods:
 *
 * - [`addClass()`](http://api.jquery.com/addClass/) - Does not support a function as first argument
 * - [`after()`](http://api.jquery.com/after/)
 * - [`append()`](http://api.jquery.com/append/) - Contrary to jQuery, this doesn't clone elements
 *   so will not work correctly when invoked on a jqLite object containing more than one DOM node
 * - [`attr()`](http://api.jquery.com/attr/) - Does not support functions as parameters
 * - [`bind()`](http://api.jquery.com/bind/) (_deprecated_, use [`on()`](http://api.jquery.com/on/)) - Does not support namespaces, selectors or eventData
 * - [`children()`](http://api.jquery.com/children/) - Does not support selectors
 * - [`clone()`](http://api.jquery.com/clone/)
 * - [`contents()`](http://api.jquery.com/contents/)
 * - [`css()`](http://api.jquery.com/css/) - Only retrieves inline-styles, does not call `getComputedStyle()`.
 *   As a setter, does not convert numbers to strings or append 'px', and also does not have automatic property prefixing.
 * - [`data()`](http://api.jquery.com/data/)
 * - [`detach()`](http://api.jquery.com/detach/)
 * - [`empty()`](http://api.jquery.com/empty/)
 * - [`eq()`](http://api.jquery.com/eq/)
 * - [`find()`](http://api.jquery.com/find/) - Limited to lookups by tag name
 * - [`hasClass()`](http://api.jquery.com/hasClass/)
 * - [`html()`](http://api.jquery.com/html/)
 * - [`next()`](http://api.jquery.com/next/) - Does not support selectors
 * - [`on()`](http://api.jquery.com/on/) - Does not support namespaces, selectors or eventData
 * - [`off()`](http://api.jquery.com/off/) - Does not support namespaces, selectors or event object as parameter
 * - [`one()`](http://api.jquery.com/one/) - Does not support namespaces or selectors
 * - [`parent()`](http://api.jquery.com/parent/) - Does not support selectors
 * - [`prepend()`](http://api.jquery.com/prepend/)
 * - [`prop()`](http://api.jquery.com/prop/)
 * - [`ready()`](http://api.jquery.com/ready/) (_deprecated_, use `angular.element(callback)` instead of `angular.element(document).ready(callback)`)
 * - [`remove()`](http://api.jquery.com/remove/)
 * - [`removeAttr()`](http://api.jquery.com/removeAttr/) - Does not support multiple attributes
 * - [`removeClass()`](http://api.jquery.com/removeClass/) - Does not support a function as first argument
 * - [`removeData()`](http://api.jquery.com/removeData/)
 * - [`replaceWith()`](http://api.jquery.com/replaceWith/)
 * - [`text()`](http://api.jquery.com/text/)
 * - [`toggleClass()`](http://api.jquery.com/toggleClass/) - Does not support a function as first argument
 * - [`triggerHandler()`](http://api.jquery.com/triggerHandler/) - Passes a dummy event object to handlers
 * - [`unbind()`](http://api.jquery.com/unbind/) (_deprecated_, use [`off()`](http://api.jquery.com/off/)) - Does not support namespaces or event object as parameter
 * - [`val()`](http://api.jquery.com/val/)
 * - [`wrap()`](http://api.jquery.com/wrap/)
 *
 * jqLite also provides a method restoring pre-1.8 insecure treatment of XHTML-like tags.
 * This legacy behavior turns input like `<div /><span />` to `<div></div><span></span>`
 * instead of `<div><span></span></div>` like version 1.8 & newer do. To restore it, invoke:
 * ```js
 * angular.UNSAFE_restoreLegacyJqLiteXHTMLReplacement();
 * ```
 * Note that this only patches jqLite. If you use jQuery 3.5.0 or newer, please read the
 * [jQuery 3.5 upgrade guide](https://jquery.com/upgrade-guide/3.5/) for more details
 * about the workarounds.
 *
 * ## jQuery/jqLite Extras
 * AngularJS also provides the following additional methods and events to both jQuery and jqLite:
 *
 * ### Events
 * - `$destroy` - AngularJS intercepts all jqLite/jQuery's DOM destruction apis and fires this event
 *    on all DOM nodes being removed.  This can be used to clean up any 3rd party bindings to the DOM
 *    element before it is removed.
 *
 * ### Methods
 * - `controller(name)` - retrieves the controller of the current element or its parent. By default
 *   retrieves controller associated with the `ngController` directive. If `name` is provided as
 *   camelCase directive name, then the controller for this directive will be retrieved (e.g.
 *   `'ngModel'`).
 * - `injector()` - retrieves the injector of the current element or its parent.
 * - `scope()` - retrieves the {@link ng.$rootScope.Scope scope} of the current
 *   element or its parent. Requires {@link guide/production#disabling-debug-data Debug Data} to
 *   be enabled.
 * - `isolateScope()` - retrieves an isolate {@link ng.$rootScope.Scope scope} if one is attached directly to the
 *   current element. This getter should be used only on elements that contain a directive which starts a new isolate
 *   scope. Calling `scope()` on this element always returns the original non-isolate scope.
 *   Requires {@link guide/production#disabling-debug-data Debug Data} to be enabled.
 * - `inheritedData()` - same as `data()`, but walks up the DOM until a value is found or the top
 *   parent element is reached.
 *
 * @knownIssue You cannot spy on `angular.element` if you are using Jasmine version 1.x. See
 * https://github.com/angular/angular.js/issues/14251 for more information.
 *
 * @param {string|DOMElement} element HTML string or DOMElement to be wrapped into jQuery.
 * @returns {Object} jQuery object.
 */

JQLite.expando = 'ng339';

var jqCache = JQLite.cache = {},
    jqId = 1;

/*
 * !!! This is an undocumented "private" function !!!
 */
JQLite._data = function(node) {
  //jQuery always returns an object on cache miss
  return this.cache[node[this.expando]] || {};
};

function jqNextId() { return ++jqId; }


var DASH_LOWERCASE_REGEXP = /-([a-z])/g;
var MS_HACK_REGEXP = /^-ms-/;
var MOUSE_EVENT_MAP = { mouseleave: 'mouseout', mouseenter: 'mouseover' };
var jqLiteMinErr = minErr('jqLite');

/**
 * Converts kebab-case to camelCase.
 * There is also a special case for the ms prefix starting with a lowercase letter.
 * @param name Name to normalize
 */
function cssKebabToCamel(name) {
    return kebabToCamel(name.replace(MS_HACK_REGEXP, 'ms-'));
}

function fnCamelCaseReplace(all, letter) {
  return letter.toUpperCase();
}

/**
 * Converts kebab-case to camelCase.
 * @param name Name to normalize
 */
function kebabToCamel(name) {
  return name
    .replace(DASH_LOWERCASE_REGEXP, fnCamelCaseReplace);
}

var SINGLE_TAG_REGEXP = /^<([\w-]+)\s*\/?>(?:<\/\1>|)$/;
var HTML_REGEXP = /<|&#?\w+;/;
var TAG_NAME_REGEXP = /<([\w:-]+)/;
var XHTML_TAG_REGEXP = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([\w:-]+)[^>]*)\/>/gi;

// Table parts need to be wrapped with `<table>` or they're
// stripped to their contents when put in a div.
// XHTML parsers do not magically insert elements in the
// same way that tag soup parsers do, so we cannot shorten
// this by omitting <tbody> or other required elements.
var wrapMap = {
  thead: ['table'],
  col: ['colgroup', 'table'],
  tr: ['tbody', 'table'],
  td: ['tr', 'tbody', 'table']
};

wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
wrapMap.th = wrapMap.td;

// Support: IE <10 only
// IE 9 requires an option wrapper & it needs to have the whole table structure
// set up in advance; assigning `"<td></td>"` to `tr.innerHTML` doesn't work, etc.
var wrapMapIE9 = {
  option: [1, '<select multiple="multiple">', '</select>'],
  _default: [0, '', '']
};

for (var key in wrapMap) {
  var wrapMapValueClosing = wrapMap[key];
  var wrapMapValue = wrapMapValueClosing.slice().reverse();
  wrapMapIE9[key] = [wrapMapValue.length, '<' + wrapMapValue.join('><') + '>', '</' + wrapMapValueClosing.join('></') + '>'];
}

wrapMapIE9.optgroup = wrapMapIE9.option;

function jqLiteIsTextNode(html) {
  return !HTML_REGEXP.test(html);
}

function jqLiteAcceptsData(node) {
  // The window object can accept data but has no nodeType
  // Otherwise we are only interested in elements (1) and documents (9)
  var nodeType = node.nodeType;
  return nodeType === NODE_TYPE_ELEMENT || !nodeType || nodeType === NODE_TYPE_DOCUMENT;
}

function jqLiteHasData(node) {
  for (var key in jqCache[node.ng339]) {
    return true;
  }
  return false;
}

function jqLiteBuildFragment(html, context) {
  var tmp, tag, wrap, finalHtml,
      fragment = context.createDocumentFragment(),
      nodes = [], i;

  if (jqLiteIsTextNode(html)) {
    // Convert non-html into a text node
    nodes.push(context.createTextNode(html));
  } else {
    // Convert html into DOM nodes
    tmp = fragment.appendChild(context.createElement('div'));
    tag = (TAG_NAME_REGEXP.exec(html) || ['', ''])[1].toLowerCase();
    finalHtml = JQLite.legacyXHTMLReplacement ?
      html.replace(XHTML_TAG_REGEXP, '<$1></$2>') :
      html;

    if (msie < 10) {
      wrap = wrapMapIE9[tag] || wrapMapIE9._default;
      tmp.innerHTML = wrap[1] + finalHtml + wrap[2];

      // Descend through wrappers to the right content
      i = wrap[0];
      while (i--) {
        tmp = tmp.firstChild;
      }
    } else {
      wrap = wrapMap[tag] || [];

      // Create wrappers & descend into them
      i = wrap.length;
      while (--i > -1) {
        tmp.appendChild(window.document.createElement(wrap[i]));
        tmp = tmp.firstChild;
      }

      tmp.innerHTML = finalHtml;
    }

    nodes = concat(nodes, tmp.childNodes);

    tmp = fragment.firstChild;
    tmp.textContent = '';
  }

  // Remove wrapper from fragment
  fragment.textContent = '';
  fragment.innerHTML = ''; // Clear inner HTML
  forEach(nodes, function(node) {
    fragment.appendChild(node);
  });

  return fragment;
}

function jqLiteParseHTML(html, context) {
  context = context || window.document;
  var parsed;

  if ((parsed = SINGLE_TAG_REGEXP.exec(html))) {
    return [context.createElement(parsed[1])];
  }

  if ((parsed = jqLiteBuildFragment(html, context))) {
    return parsed.childNodes;
  }

  return [];
}

function jqLiteWrapNode(node, wrapper) {
  var parent = node.parentNode;

  if (parent) {
    parent.replaceChild(wrapper, node);
  }

  wrapper.appendChild(node);
}


// IE9-11 has no method "contains" in SVG element and in Node.prototype. Bug #10259.
var jqLiteContains = window.Node.prototype.contains || /** @this */ function(arg) {
  // eslint-disable-next-line no-bitwise
  return !!(this.compareDocumentPosition(arg) & 16);
};

/////////////////////////////////////////////
function JQLite(element) {
  if (element instanceof JQLite) {
    return element;
  }

  var argIsString;

  if (isString(element)) {
    element = trim(element);
    argIsString = true;
  }
  if (!(this instanceof JQLite)) {
    if (argIsString && element.charAt(0) !== '<') {
      throw jqLiteMinErr('nosel', 'Looking up elements via selectors is not supported by jqLite! See: http://docs.angularjs.org/api/angular.element');
    }
    return new JQLite(element);
  }

  if (argIsString) {
    jqLiteAddNodes(this, jqLiteParseHTML(element));
  } else if (isFunction(element)) {
    jqLiteReady(element);
  } else {
    jqLiteAddNodes(this, element);
  }
}

function jqLiteClone(element) {
  return element.cloneNode(true);
}

function jqLiteDealoc(element, onlyDescendants) {
  if (!onlyDescendants && jqLiteAcceptsData(element)) jqLite.cleanData([element]);

  if (element.querySelectorAll) {
    jqLite.cleanData(element.querySelectorAll('*'));
  }
}

function isEmptyObject(obj) {
  var name;

  for (name in obj) {
    return false;
  }
  return true;
}

function removeIfEmptyData(element) {
  var expandoId = element.ng339;
  var expandoStore = expandoId && jqCache[expandoId];

  var events = expandoStore && expandoStore.events;
  var data = expandoStore && expandoStore.data;

  if ((!data || isEmptyObject(data)) && (!events || isEmptyObject(events))) {
    delete jqCache[expandoId];
    element.ng339 = undefined; // don't delete DOM expandos. IE and Chrome don't like it
  }
}

function jqLiteOff(element, type, fn, unsupported) {
  if (isDefined(unsupported)) throw jqLiteMinErr('offargs', 'jqLite#off() does not support the `selector` argument');

  var expandoStore = jqLiteExpandoStore(element);
  var events = expandoStore && expandoStore.events;
  var handle = expandoStore && expandoStore.handle;

  if (!handle) return; //no listeners registered

  if (!type) {
    for (type in events) {
      if (type !== '$destroy') {
        element.removeEventListener(type, handle);
      }
      delete events[type];
    }
  } else {

    var removeHandler = function(type) {
      var listenerFns = events[type];
      if (isDefined(fn)) {
        arrayRemove(listenerFns || [], fn);
      }
      if (!(isDefined(fn) && listenerFns && listenerFns.length > 0)) {
        element.removeEventListener(type, handle);
        delete events[type];
      }
    };

    forEach(type.split(' '), function(type) {
      removeHandler(type);
      if (MOUSE_EVENT_MAP[type]) {
        removeHandler(MOUSE_EVENT_MAP[type]);
      }
    });
  }

  removeIfEmptyData(element);
}

function jqLiteRemoveData(element, name) {
  var expandoId = element.ng339;
  var expandoStore = expandoId && jqCache[expandoId];

  if (expandoStore) {
    if (name) {
      delete expandoStore.data[name];
    } else {
      expandoStore.data = {};
    }

    removeIfEmptyData(element);
  }
}


function jqLiteExpandoStore(element, createIfNecessary) {
  var expandoId = element.ng339,
      expandoStore = expandoId && jqCache[expandoId];

  if (createIfNecessary && !expandoStore) {
    element.ng339 = expandoId = jqNextId();
    expandoStore = jqCache[expandoId] = {events: {}, data: {}, handle: undefined};
  }

  return expandoStore;
}


function jqLiteData(element, key, value) {
  if (jqLiteAcceptsData(element)) {
    var prop;

    var isSimpleSetter = isDefined(value);
    var isSimpleGetter = !isSimpleSetter && key && !isObject(key);
    var massGetter = !key;
    var expandoStore = jqLiteExpandoStore(element, !isSimpleGetter);
    var data = expandoStore && expandoStore.data;

    if (isSimpleSetter) { // data('key', value)
      data[kebabToCamel(key)] = value;
    } else {
      if (massGetter) {  // data()
        return data;
      } else {
        if (isSimpleGetter) { // data('key')
          // don't force creation of expandoStore if it doesn't exist yet
          return data && data[kebabToCamel(key)];
        } else { // mass-setter: data({key1: val1, key2: val2})
          for (prop in key) {
            data[kebabToCamel(prop)] = key[prop];
          }
        }
      }
    }
  }
}

function jqLiteHasClass(element, selector) {
  if (!element.getAttribute) return false;
  return ((' ' + (element.getAttribute('class') || '') + ' ').replace(/[\n\t]/g, ' ').
      indexOf(' ' + selector + ' ') > -1);
}

function jqLiteRemoveClass(element, cssClasses) {
  if (cssClasses && element.setAttribute) {
    var existingClasses = (' ' + (element.getAttribute('class') || '') + ' ')
                            .replace(/[\n\t]/g, ' ');
    var newClasses = existingClasses;

    forEach(cssClasses.split(' '), function(cssClass) {
      cssClass = trim(cssClass);
      newClasses = newClasses.replace(' ' + cssClass + ' ', ' ');
    });

    if (newClasses !== existingClasses) {
      element.setAttribute('class', trim(newClasses));
    }
  }
}

function jqLiteAddClass(element, cssClasses) {
  if (cssClasses && element.setAttribute) {
    var existingClasses = (' ' + (element.getAttribute('class') || '') + ' ')
                            .replace(/[\n\t]/g, ' ');
    var newClasses = existingClasses;

    forEach(cssClasses.split(' '), function(cssClass) {
      cssClass = trim(cssClass);
      if (newClasses.indexOf(' ' + cssClass + ' ') === -1) {
        newClasses += cssClass + ' ';
      }
    });

    if (newClasses !== existingClasses) {
      element.setAttribute('class', trim(newClasses));
    }
  }
}


function jqLiteAddNodes(root, elements) {
  // THIS CODE IS VERY HOT. Don't make changes without benchmarking.

  if (elements) {

    // if a Node (the most common case)
    if (elements.nodeType) {
      root[root.length++] = elements;
    } else {
      var length = elements.length;

      // if an Array or NodeList and not a Window
      if (typeof length === 'number' && elements.window !== elements) {
        if (length) {
          for (var i = 0; i < length; i++) {
            root[root.length++] = elements[i];
          }
        }
      } else {
        root[root.length++] = elements;
      }
    }
  }
}


function jqLiteController(element, name) {
  return jqLiteInheritedData(element, '$' + (name || 'ngController') + 'Controller');
}

function jqLiteInheritedData(element, name, value) {
  // if element is the document object work with the html element instead
  // this makes $(document).scope() possible
  if (element.nodeType === NODE_TYPE_DOCUMENT) {
    element = element.documentElement;
  }
  var names = isArray(name) ? name : [name];

  while (element) {
    for (var i = 0, ii = names.length; i < ii; i++) {
      if (isDefined(value = jqLite.data(element, names[i]))) return value;
    }

    // If dealing with a document fragment node with a host element, and no parent, use the host
    // element as the parent. This enables directives within a Shadow DOM or polyfilled Shadow DOM
    // to lookup parent controllers.
    element = element.parentNode || (element.nodeType === NODE_TYPE_DOCUMENT_FRAGMENT && element.host);
  }
}

function jqLiteEmpty(element) {
  jqLiteDealoc(element, true);
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}

function jqLiteRemove(element, keepData) {
  if (!keepData) jqLiteDealoc(element);
  var parent = element.parentNode;
  if (parent) parent.removeChild(element);
}


function jqLiteDocumentLoaded(action, win) {
  win = win || window;
  if (win.document.readyState === 'complete') {
    // Force the action to be run async for consistent behavior
    // from the action's point of view
    // i.e. it will definitely not be in a $apply
    win.setTimeout(action);
  } else {
    // No need to unbind this handler as load is only ever called once
    jqLite(win).on('load', action);
  }
}

function jqLiteReady(fn) {
  function trigger() {
    window.document.removeEventListener('DOMContentLoaded', trigger);
    window.removeEventListener('load', trigger);
    fn();
  }

  // check if document is already loaded
  if (window.document.readyState === 'complete') {
    window.setTimeout(fn);
  } else {
    // We can not use jqLite since we are not done loading and jQuery could be loaded later.

    // Works for modern browsers and IE9
    window.document.addEventListener('DOMContentLoaded', trigger);

    // Fallback to window.onload for others
    window.addEventListener('load', trigger);
  }
}

//////////////////////////////////////////
// Functions which are declared directly.
//////////////////////////////////////////
var JQLitePrototype = JQLite.prototype = {
  ready: jqLiteReady,
  toString: function() {
    var value = [];
    forEach(this, function(e) { value.push('' + e);});
    return '[' + value.join(', ') + ']';
  },

  eq: function(index) {
      return (index >= 0) ? jqLite(this[index]) : jqLite(this[this.length + index]);
  },

  length: 0,
  push: push,
  sort: [].sort,
  splice: [].splice
};

//////////////////////////////////////////
// Functions iterating getter/setters.
// these functions return self on setter and
// value on get.
//////////////////////////////////////////
var BOOLEAN_ATTR = {};
forEach('multiple,selected,checked,disabled,readOnly,required,open'.split(','), function(value) {
  BOOLEAN_ATTR[lowercase(value)] = value;
});
var BOOLEAN_ELEMENTS = {};
forEach('input,select,option,textarea,button,form,details'.split(','), function(value) {
  BOOLEAN_ELEMENTS[value] = true;
});
var ALIASED_ATTR = {
  'ngMinlength': 'minlength',
  'ngMaxlength': 'maxlength',
  'ngMin': 'min',
  'ngMax': 'max',
  'ngPattern': 'pattern',
  'ngStep': 'step'
};

function getBooleanAttrName(element, name) {
  // check dom last since we will most likely fail on name
  var booleanAttr = BOOLEAN_ATTR[name.toLowerCase()];

  // booleanAttr is here twice to minimize DOM access
  return booleanAttr && BOOLEAN_ELEMENTS[nodeName_(element)] && booleanAttr;
}

function getAliasedAttrName(name) {
  return ALIASED_ATTR[name];
}

forEach({
  data: jqLiteData,
  removeData: jqLiteRemoveData,
  hasData: jqLiteHasData,
  cleanData: function jqLiteCleanData(nodes) {
    for (var i = 0, ii = nodes.length; i < ii; i++) {
      jqLiteRemoveData(nodes[i]);
      jqLiteOff(nodes[i]);
    }
  }
}, function(fn, name) {
  JQLite[name] = fn;
});

forEach({
  data: jqLiteData,
  inheritedData: jqLiteInheritedData,

  scope: function(element) {
    // Can't use jqLiteData here directly so we stay compatible with jQuery!
    return jqLite.data(element, '$scope') || jqLiteInheritedData(element.parentNode || element, ['$isolateScope', '$scope']);
  },

  isolateScope: function(element) {
    // Can't use jqLiteData here directly so we stay compatible with jQuery!
    return jqLite.data(element, '$isolateScope') || jqLite.data(element, '$isolateScopeNoTemplate');
  },

  controller: jqLiteController,

  injector: function(element) {
    return jqLiteInheritedData(element, '$injector');
  },

  removeAttr: function(element, name) {
    element.removeAttribute(name);
  },

  hasClass: jqLiteHasClass,

  css: function(element, name, value) {
    name = cssKebabToCamel(name);

    if (isDefined(value)) {
      element.style[name] = value;
    } else {
      return element.style[name];
    }
  },

  attr: function(element, name, value) {
    var ret;
    var nodeType = element.nodeType;
    if (nodeType === NODE_TYPE_TEXT || nodeType === NODE_TYPE_ATTRIBUTE || nodeType === NODE_TYPE_COMMENT ||
      !element.getAttribute) {
      return;
    }

    var lowercasedName = lowercase(name);
    var isBooleanAttr = BOOLEAN_ATTR[lowercasedName];

    if (isDefined(value)) {
      // setter

      if (value === null || (value === false && isBooleanAttr)) {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, isBooleanAttr ? lowercasedName : value);
      }
    } else {
      // getter

      ret = element.getAttribute(name);

      if (isBooleanAttr && ret !== null) {
        ret = lowercasedName;
      }
      // Normalize non-existing attributes to undefined (as jQuery).
      return ret === null ? undefined : ret;
    }
  },

  prop: function(element, name, value) {
    if (isDefined(value)) {
      element[name] = value;
    } else {
      return element[name];
    }
  },

  text: (function() {
    getText.$dv = '';
    return getText;

    function getText(element, value) {
      if (isUndefined(value)) {
        var nodeType = element.nodeType;
        return (nodeType === NODE_TYPE_ELEMENT || nodeType === NODE_TYPE_TEXT) ? element.textContent : '';
      }
      element.textContent = value;
    }
  })(),

  val: function(element, value) {
    if (isUndefined(value)) {
      if (element.multiple && nodeName_(element) === 'select') {
        var result = [];
        forEach(element.options, function(option) {
          if (option.selected) {
            result.push(option.value || option.text);
          }
        });
        return result;
      }
      return element.value;
    }
    element.value = value;
  },

  html: function(element, value) {
    if (isUndefined(value)) {
      return element.innerHTML;
    }
    jqLiteDealoc(element, true);
    element.innerHTML = value;
  },

  empty: jqLiteEmpty
}, function(fn, name) {
  /**
   * Properties: writes return selection, reads return first value
   */
  JQLite.prototype[name] = function(arg1, arg2) {
    var i, key;
    var nodeCount = this.length;

    // jqLiteHasClass has only two arguments, but is a getter-only fn, so we need to special-case it
    // in a way that survives minification.
    // jqLiteEmpty takes no arguments but is a setter.
    if (fn !== jqLiteEmpty &&
        (isUndefined((fn.length === 2 && (fn !== jqLiteHasClass && fn !== jqLiteController)) ? arg1 : arg2))) {
      if (isObject(arg1)) {

        // we are a write, but the object properties are the key/values
        for (i = 0; i < nodeCount; i++) {
          if (fn === jqLiteData) {
            // data() takes the whole object in jQuery
            fn(this[i], arg1);
          } else {
            for (key in arg1) {
              fn(this[i], key, arg1[key]);
            }
          }
        }
        // return self for chaining
        return this;
      } else {
        // we are a read, so read the first child.
        // TODO: do we still need this?
        var value = fn.$dv;
        // Only if we have $dv do we iterate over all, otherwise it is just the first element.
        var jj = (isUndefined(value)) ? Math.min(nodeCount, 1) : nodeCount;
        for (var j = 0; j < jj; j++) {
          var nodeValue = fn(this[j], arg1, arg2);
          value = value ? value + nodeValue : nodeValue;
        }
        return value;
      }
    } else {
      // we are a write, so apply to all children
      for (i = 0; i < nodeCount; i++) {
        fn(this[i], arg1, arg2);
      }
      // return self for chaining
      return this;
    }
  };
});

function createEventHandler(element, events) {
  var eventHandler = function(event, type) {
    // jQuery specific api
    event.isDefaultPrevented = function() {
      return event.defaultPrevented;
    };

    var eventFns = events[type || event.type];
    var eventFnsLength = eventFns ? eventFns.length : 0;

    if (!eventFnsLength) return;

    if (isUndefined(event.immediatePropagationStopped)) {
      var originalStopImmediatePropagation = event.stopImmediatePropagation;
      event.stopImmediatePropagation = function() {
        event.immediatePropagationStopped = true;

        if (event.stopPropagation) {
          event.stopPropagation();
        }

        if (originalStopImmediatePropagation) {
          originalStopImmediatePropagation.call(event);
        }
      };
    }

    event.isImmediatePropagationStopped = function() {
      return event.immediatePropagationStopped === true;
    };

    // Some events have special handlers that wrap the real handler
    var handlerWrapper = eventFns.specialHandlerWrapper || defaultHandlerWrapper;

    // Copy event handlers in case event handlers array is modified during execution.
    if ((eventFnsLength > 1)) {
      eventFns = shallowCopy(eventFns);
    }

    for (var i = 0; i < eventFnsLength; i++) {
      if (!event.isImmediatePropagationStopped()) {
        handlerWrapper(element, event, eventFns[i]);
      }
    }
  };

  // TODO: this is a hack for angularMocks/clearDataCache that makes it possible to deregister all
  //       events on `element`
  eventHandler.elem = element;
  return eventHandler;
}

function defaultHandlerWrapper(element, event, handler) {
  handler.call(element, event);
}

function specialMouseHandlerWrapper(target, event, handler) {
  // Refer to jQuery's implementation of mouseenter & mouseleave
  // Read about mouseenter and mouseleave:
  // http://www.quirksmode.org/js/events_mouse.html#link8
  var related = event.relatedTarget;
  // For mousenter/leave call the handler if related is outside the target.
  // NB: No relatedTarget if the mouse left/entered the browser window
  if (!related || (related !== target && !jqLiteContains.call(target, related))) {
    handler.call(target, event);
  }
}

//////////////////////////////////////////
// Functions iterating traversal.
// These functions chain results into a single
// selector.
//////////////////////////////////////////
forEach({
  removeData: jqLiteRemoveData,

  on: function jqLiteOn(element, type, fn, unsupported) {
    if (isDefined(unsupported)) throw jqLiteMinErr('onargs', 'jqLite#on() does not support the `selector` or `eventData` parameters');

    // Do not add event handlers to non-elements because they will not be cleaned up.
    if (!jqLiteAcceptsData(element)) {
      return;
    }

    var expandoStore = jqLiteExpandoStore(element, true);
    var events = expandoStore.events;
    var handle = expandoStore.handle;

    if (!handle) {
      handle = expandoStore.handle = createEventHandler(element, events);
    }

    // http://jsperf.com/string-indexof-vs-split
    var types = type.indexOf(' ') >= 0 ? type.split(' ') : [type];
    var i = types.length;

    var addHandler = function(type, specialHandlerWrapper, noEventListener) {
      var eventFns = events[type];

      if (!eventFns) {
        eventFns = events[type] = [];
        eventFns.specialHandlerWrapper = specialHandlerWrapper;
        if (type !== '$destroy' && !noEventListener) {
          element.addEventListener(type, handle);
        }
      }

      eventFns.push(fn);
    };

    while (i--) {
      type = types[i];
      if (MOUSE_EVENT_MAP[type]) {
        addHandler(MOUSE_EVENT_MAP[type], specialMouseHandlerWrapper);
        addHandler(type, undefined, true);
      } else {
        addHandler(type);
      }
    }
  },

  off: jqLiteOff,

  one: function(element, type, fn) {
    element = jqLite(element);

    //add the listener twice so that when it is called
    //you can remove the original function and still be
    //able to call element.off(ev, fn) normally
    element.on(type, function onFn() {
      element.off(type, fn);
      element.off(type, onFn);
    });
    element.on(type, fn);
  },

  replaceWith: function(element, replaceNode) {
    var index, parent = element.parentNode;
    jqLiteDealoc(element);
    forEach(new JQLite(replaceNode), function(node) {
      if (index) {
        parent.insertBefore(node, index.nextSibling);
      } else {
        parent.replaceChild(node, element);
      }
      index = node;
    });
  },

  children: function(element) {
    var children = [];
    forEach(element.childNodes, function(element) {
      if (element.nodeType === NODE_TYPE_ELEMENT) {
        children.push(element);
      }
    });
    return children;
  },

  contents: function(element) {
    return element.contentDocument || element.childNodes || [];
  },

  append: function(element, node) {
    var nodeType = element.nodeType;
    if (nodeType !== NODE_TYPE_ELEMENT && nodeType !== NODE_TYPE_DOCUMENT_FRAGMENT) return;

    node = new JQLite(node);

    for (var i = 0, ii = node.length; i < ii; i++) {
      var child = node[i];
      element.appendChild(child);
    }
  },

  prepend: function(element, node) {
    if (element.nodeType === NODE_TYPE_ELEMENT) {
      var index = element.firstChild;
      forEach(new JQLite(node), function(child) {
        element.insertBefore(child, index);
      });
    }
  },

  wrap: function(element, wrapNode) {
    jqLiteWrapNode(element, jqLite(wrapNode).eq(0).clone()[0]);
  },

  remove: jqLiteRemove,

  detach: function(element) {
    jqLiteRemove(element, true);
  },

  after: function(element, newElement) {
    var index = element, parent = element.parentNode;

    if (parent) {
      newElement = new JQLite(newElement);

      for (var i = 0, ii = newElement.length; i < ii; i++) {
        var node = newElement[i];
        parent.insertBefore(node, index.nextSibling);
        index = node;
      }
    }
  },

  addClass: jqLiteAddClass,
  removeClass: jqLiteRemoveClass,

  toggleClass: function(element, selector, condition) {
    if (selector) {
      forEach(selector.split(' '), function(className) {
        var classCondition = condition;
        if (isUndefined(classCondition)) {
          classCondition = !jqLiteHasClass(element, className);
        }
        (classCondition ? jqLiteAddClass : jqLiteRemoveClass)(element, className);
      });
    }
  },

  parent: function(element) {
    var parent = element.parentNode;
    return parent && parent.nodeType !== NODE_TYPE_DOCUMENT_FRAGMENT ? parent : null;
  },

  next: function(element) {
    return element.nextElementSibling;
  },

  find: function(element, selector) {
    if (element.getElementsByTagName) {
      return element.getElementsByTagName(selector);
    } else {
      return [];
    }
  },

  clone: jqLiteClone,

  triggerHandler: function(element, event, extraParameters) {

    var dummyEvent, eventFnsCopy, handlerArgs;
    var eventName = event.type || event;
    var expandoStore = jqLiteExpandoStore(element);
    var events = expandoStore && expandoStore.events;
    var eventFns = events && events[eventName];

    if (eventFns) {
      // Create a dummy event to pass to the handlers
      dummyEvent = {
        preventDefault: function() { this.defaultPrevented = true; },
        isDefaultPrevented: function() { return this.defaultPrevented === true; },
        stopImmediatePropagation: function() { this.immediatePropagationStopped = true; },
        isImmediatePropagationStopped: function() { return this.immediatePropagationStopped === true; },
        stopPropagation: noop,
        type: eventName,
        target: element
      };

      // If a custom event was provided then extend our dummy event with it
      if (event.type) {
        dummyEvent = extend(dummyEvent, event);
      }

      // Copy event handlers in case event handlers array is modified during execution.
      eventFnsCopy = shallowCopy(eventFns);
      handlerArgs = extraParameters ? [dummyEvent].concat(extraParameters) : [dummyEvent];

      forEach(eventFnsCopy, function(fn) {
        if (!dummyEvent.isImmediatePropagationStopped()) {
          fn.apply(element, handlerArgs);
        }
      });
    }
  }
}, function(fn, name) {
  /**
   * chaining functions
   */
  JQLite.prototype[name] = function(arg1, arg2, arg3) {
    var value;

    for (var i = 0, ii = this.length; i < ii; i++) {
      if (isUndefined(value)) {
        value = fn(this[i], arg1, arg2, arg3);
        if (isDefined(value)) {
          // any function which returns a value needs to be wrapped
          value = jqLite(value);
        }
      } else {
        jqLiteAddNodes(value, fn(this[i], arg1, arg2, arg3));
      }
    }
    return isDefined(value) ? value : this;
  };
});

// bind legacy bind/unbind to on/off
JQLite.prototype.bind = JQLite.prototype.on;
JQLite.prototype.unbind = JQLite.prototype.off;


// Provider for private $$jqLite service
/** @this */
function $$jqLiteProvider() {
  this.$get = function $$jqLite() {
    return extend(JQLite, {
      hasClass: function(node, classes) {
        if (node.attr) node = node[0];
        return jqLiteHasClass(node, classes);
      },
      addClass: function(node, classes) {
        if (node.attr) node = node[0];
        return jqLiteAddClass(node, classes);
      },
      removeClass: function(node, classes) {
        if (node.attr) node = node[0];
        return jqLiteRemoveClass(node, classes);
      }
    });
  };
}

/**
 * Computes a hash of an 'obj'.
 * Hash of a:
 *  string is string
 *  number is number as string
 *  object is either result of calling $$hashKey function on the object or uniquely generated id,
 *         that is also assigned to the $$hashKey property of the object.
 *
 * @param obj
 * @returns {string} hash string such that the same input will have the same hash string.
 *         The resulting string key is in 'type:hashKey' format.
 */
function hashKey(obj, nextUidFn) {
  var key = obj && obj.$$hashKey;

  if (key) {
    if (typeof key === 'function') {
      key = obj.$$hashKey();
    }
    return key;
  }

  var objType = typeof obj;
  if (objType === 'function' || (objType === 'object' && obj !== null)) {
    key = obj.$$hashKey = objType + ':' + (nextUidFn || nextUid)();
  } else {
    key = objType + ':' + obj;
  }

  return key;
}

// A minimal ES2015 Map implementation.
// Should be bug/feature equivalent to the native implementations of supported browsers
// (for the features required in Angular).
// See https://kangax.github.io/compat-table/es6/#test-Map
var nanKey = Object.create(null);
function NgMapShim() {
  this._keys = [];
  this._values = [];
  this._lastKey = NaN;
  this._lastIndex = -1;
}
NgMapShim.prototype = {
  _idx: function(key) {
    if (key !== this._lastKey) {
      this._lastKey = key;
      this._lastIndex = this._keys.indexOf(key);
    }
    return this._lastIndex;
  },
  _transformKey: function(key) {
    return isNumberNaN(key) ? nanKey : key;
  },
  get: function(key) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    if (idx !== -1) {
      return this._values[idx];
    }
  },
  has: function(key) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    return idx !== -1;
  },
  set: function(key, value) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    if (idx === -1) {
      idx = this._lastIndex = this._keys.length;
    }
    this._keys[idx] = key;
    this._values[idx] = value;

    // Support: IE11
    // Do not `return this` to simulate the partial IE11 implementation
  },
  delete: function(key) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    if (idx === -1) {
      return false;
    }
    this._keys.splice(idx, 1);
    this._values.splice(idx, 1);
    this._lastKey = NaN;
    this._lastIndex = -1;
    return true;
  }
};

// For now, always use `NgMapShim`, even if `window.Map` is available. Some native implementations
// are still buggy (often in subtle ways) and can cause hard-to-debug failures. When native `Map`
// implementations get more stable, we can reconsider switching to `window.Map` (when available).
var NgMap = NgMapShim;

var $$MapProvider = [/** @this */function() {
  this.$get = [function() {
    return NgMap;
  }];
}];

/**
 * @ngdoc function
 * @module ng
 * @name angular.injector
 * @kind function
 *
 * @description
 * Creates an injector object that can be used for retrieving services as well as for
 * dependency injection (see {@link guide/di dependency injection}).
 *
 * @param {Array.<string|Function>} modules A list of module functions or their aliases. See
 *     {@link angular.module}. The `ng` module must be explicitly added.
 * @param {boolean=} [strictDi=false] Whether the injector should be in strict mode, which
 *     disallows argument name annotation inference.
 * @returns {injector} Injector object. See {@link auto.$injector $injector}.
 *
 * @example
 * Typical usage
 * ```js
 *   // create an injector
 *   var $injector = angular.injector(['ng']);
 *
 *   // use the injector to kick off your application
 *   // use the type inference to auto inject arguments, or use implicit injection
 *   $injector.invoke(function($rootScope, $compile, $document) {
 *     $compile($document)($rootScope);
 *     $rootScope.$digest();
 *   });
 * ```
 *
 * Sometimes you want to get access to the injector of a currently running AngularJS app
 * from outside AngularJS. Perhaps, you want to inject and compile some markup after the
 * application has been bootstrapped. You can do this using the extra `injector()` added
 * to JQuery/jqLite elements. See {@link angular.element}.
 *
 * *This is fairly rare but could be the case if a third party library is injecting the
 * markup.*
 *
 * In the following example a new block of HTML containing a `ng-controller`
 * directive is added to the end of the document body by JQuery. We then compile and link
 * it into the current AngularJS scope.
 *
 * ```js
 * var $div = $('<div ng-controller="MyCtrl">{{content.label}}</div>');
 * $(document.body).append($div);
 *
 * angular.element(document).injector().invoke(function($compile) {
 *   var scope = angular.element($div).scope();
 *   $compile($div)(scope);
 * });
 * ```
 */


/**
 * @ngdoc module
 * @name auto
 * @installation
 * @description
 *
 * Implicit module which gets automatically added to each {@link auto.$injector $injector}.
 */

var ARROW_ARG = /^([^(]+?)=>/;
var FN_ARGS = /^[^(]*\(\s*([^)]*)\)/m;
var FN_ARG_SPLIT = /,/;
var FN_ARG = /^\s*(_?)(\S+?)\1\s*$/;
var STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;
var $injectorMinErr = minErr('$injector');

function stringifyFn(fn) {
  return Function.prototype.toString.call(fn);
}

function extractArgs(fn) {
  var fnText = stringifyFn(fn).replace(STRIP_COMMENTS, ''),
      args = fnText.match(ARROW_ARG) || fnText.match(FN_ARGS);
  return args;
}

function anonFn(fn) {
  // For anonymous functions, showing at the very least the function signature can help in
  // debugging.
  var args = extractArgs(fn);
  if (args) {
    return 'function(' + (args[1] || '').replace(/[\s\r\n]+/, ' ') + ')';
  }
  return 'fn';
}

function annotate(fn, strictDi, name) {
  var $inject,
      argDecl,
      last;

  if (typeof fn === 'function') {
    if (!($inject = fn.$inject)) {
      $inject = [];
      if (fn.length) {
        if (strictDi) {
          if (!isString(name) || !name) {
            name = fn.name || anonFn(fn);
          }
          throw $injectorMinErr('strictdi',
            '{0} is not using explicit annotation and cannot be invoked in strict mode', name);
        }
        argDecl = extractArgs(fn);
        forEach(argDecl[1].split(FN_ARG_SPLIT), function(arg) {
          arg.replace(FN_ARG, function(all, underscore, name) {
            $inject.push(name);
          });
        });
      }
      fn.$inject = $inject;
    }
  } else if (isArray(fn)) {
    last = fn.length - 1;
    assertArgFn(fn[last], 'fn');
    $inject = fn.slice(0, last);
  } else {
    assertArgFn(fn, 'fn', true);
  }
  return $inject;
}

///////////////////////////////////////

/**
 * @ngdoc service
 * @name $injector
 *
 * @description
 *
 * `$injector` is used to retrieve object instances as defined by
 * {@link auto.$provide provider}, instantiate types, invoke methods,
 * and load modules.
 *
 * The following always holds true:
 *
 * ```js
 *   var $injector = angular.injector();
 *   expect($injector.get('$injector')).toBe($injector);
 *   expect($injector.invoke(function($injector) {
 *     return $injector;
 *   })).toBe($injector);
 * ```
 *
 * ## Injection Function Annotation
 *
 * JavaScript does not have annotations, and annotations are needed for dependency injection. The
 * following are all valid ways of annotating function with injection arguments and are equivalent.
 *
 * ```js
 *   // inferred (only works if code not minified/obfuscated)
 *   $injector.invoke(function(serviceA){});
 *
 *   // annotated
 *   function explicit(serviceA) {};
 *   explicit.$inject = ['serviceA'];
 *   $injector.invoke(explicit);
 *
 *   // inline
 *   $injector.invoke(['serviceA', function(serviceA){}]);
 * ```
 *
 * ### Inference
 *
 * In JavaScript calling `toString()` on a function returns the function definition. The definition
 * can then be parsed and the function arguments can be extracted. This method of discovering
 * annotations is disallowed when the injector is in strict mode.
 * *NOTE:* This does not work with minification, and obfuscation tools since these tools change the
 * argument names.
 *
 * ### `$inject` Annotation
 * By adding an `$inject` property onto a function the injection parameters can be specified.
 *
 * ### Inline
 * As an array of injection names, where the last item in the array is the function to call.
 */

/**
 * @ngdoc property
 * @name $injector#modules
 * @type {Object}
 * @description
 * A hash containing all the modules that have been loaded into the
 * $injector.
 *
 * You can use this property to find out information about a module via the
 * {@link angular.Module#info `myModule.info(...)`} method.
 *
 * For example:
 *
 * ```
 * var info = $injector.modules['ngAnimate'].info();
 * ```
 *
 * **Do not use this property to attempt to modify the modules after the application
 * has been bootstrapped.**
 */


/**
 * @ngdoc method
 * @name $injector#get
 *
 * @description
 * Return an instance of the service.
 *
 * @param {string} name The name of the instance to retrieve.
 * @param {string=} caller An optional string to provide the origin of the function call for error messages.
 * @return {*} The instance.
 */

/**
 * @ngdoc method
 * @name $injector#invoke
 *
 * @description
 * Invoke the method and supply the method arguments from the `$injector`.
 *
 * @param {Function|Array.<string|Function>} fn The injectable function to invoke. Function parameters are
 *   injected according to the {@link guide/di $inject Annotation} rules.
 * @param {Object=} self The `this` for the invoked method.
 * @param {Object=} locals Optional object. If preset then any argument names are read from this
 *                         object first, before the `$injector` is consulted.
 * @returns {*} the value returned by the invoked `fn` function.
 */

/**
 * @ngdoc method
 * @name $injector#has
 *
 * @description
 * Allows the user to query if the particular service exists.
 *
 * @param {string} name Name of the service to query.
 * @returns {boolean} `true` if injector has given service.
 */

/**
 * @ngdoc method
 * @name $injector#instantiate
 * @description
 * Create a new instance of JS type. The method takes a constructor function, invokes the new
 * operator, and supplies all of the arguments to the constructor function as specified by the
 * constructor annotation.
 *
 * @param {Function} Type Annotated constructor function.
 * @param {Object=} locals Optional object. If preset then any argument names are read from this
 * object first, before the `$injector` is consulted.
 * @returns {Object} new instance of `Type`.
 */

/**
 * @ngdoc method
 * @name $injector#annotate
 *
 * @description
 * Returns an array of service names which the function is requesting for injection. This API is
 * used by the injector to determine which services need to be injected into the function when the
 * function is invoked. There are three ways in which the function can be annotated with the needed
 * dependencies.
 *
 * #### Argument names
 *
 * The simplest form is to extract the dependencies from the arguments of the function. This is done
 * by converting the function into a string using `toString()` method and extracting the argument
 * names.
 * ```js
 *   // Given
 *   function MyController($scope, $route) {
 *     // ...
 *   }
 *
 *   // Then
 *   expect(injector.annotate(MyController)).toEqual(['$scope', '$route']);
 * ```
 *
 * You can disallow this method by using strict injection mode.
 *
 * This method does not work with code minification / obfuscation. For this reason the following
 * annotation strategies are supported.
 *
 * #### The `$inject` property
 *
 * If a function has an `$inject` property and its value is an array of strings, then the strings
 * represent names of services to be injected into the function.
 * ```js
 *   // Given
 *   var MyController = function(obfuscatedScope, obfuscatedRoute) {
 *     // ...
 *   }
 *   // Define function dependencies
 *   MyController['$inject'] = ['$scope', '$route'];
 *
 *   // Then
 *   expect(injector.annotate(MyController)).toEqual(['$scope', '$route']);
 * ```
 *
 * #### The array notation
 *
 * It is often desirable to inline Injected functions and that's when setting the `$inject` property
 * is very inconvenient. In these situations using the array notation to specify the dependencies in
 * a way that survives minification is a better choice:
 *
 * ```js
 *   // We wish to write this (not minification / obfuscation safe)
 *   injector.invoke(function($compile, $rootScope) {
 *     // ...
 *   });
 *
 *   // We are forced to write break inlining
 *   var tmpFn = function(obfuscatedCompile, obfuscatedRootScope) {
 *     // ...
 *   };
 *   tmpFn.$inject = ['$compile', '$rootScope'];
 *   injector.invoke(tmpFn);
 *
 *   // To better support inline function the inline annotation is supported
 *   injector.invoke(['$compile', '$rootScope', function(obfCompile, obfRootScope) {
 *     // ...
 *   }]);
 *
 *   // Therefore
 *   expect(injector.annotate(
 *      ['$compile', '$rootScope', function(obfus_$compile, obfus_$rootScope) {}])
 *    ).toEqual(['$compile', '$rootScope']);
 * ```
 *
 * @param {Function|Array.<string|Function>} fn Function for which dependent service names need to
 * be retrieved as described above.
 *
 * @param {boolean=} [strictDi=false] Disallow argument name annotation inference.
 *
 * @returns {Array.<string>} The names of the services which the function requires.
 */
/**
 * @ngdoc method
 * @name $injector#loadNewModules
 *
 * @description
 *
 * **This is a dangerous API, which you use at your own risk!**
 *
 * Add the specified modules to the current injector.
 *
 * This method will add each of the injectables to the injector and execute all of the config and run
 * blocks for each module passed to the method.
 *
 * If a module has already been loaded into the injector then it will not be loaded again.
 *
 * * The application developer is responsible for loading the code containing the modules; and for
 * ensuring that lazy scripts are not downloaded and executed more often that desired.
 * * Previously compiled HTML will not be affected by newly loaded directives, filters and components.
 * * Modules cannot be unloaded.
 *
 * You can use {@link $injector#modules `$injector.modules`} to check whether a module has been loaded
 * into the injector, which may indicate whether the script has been executed already.
 *
 * @example
 * Here is an example of loading a bundle of modules, with a utility method called `getScript`:
 *
 * ```javascript
 * app.factory('loadModule', function($injector) {
 *   return function loadModule(moduleName, bundleUrl) {
 *     return getScript(bundleUrl).then(function() { $injector.loadNewModules([moduleName]); });
 *   };
 * })
 * ```
 *
 * @param {Array<String|Function|Array>=} mods an array of modules to load into the application.
 *     Each item in the array should be the name of a predefined module or a (DI annotated)
 *     function that will be invoked by the injector as a `config` block.
 *     See: {@link angular.module modules}
 */


/**
 * @ngdoc service
 * @name $provide
 *
 * @description
 *
 * The {@link auto.$provide $provide} service has a number of methods for registering components
 * with the {@link auto.$injector $injector}. Many of these functions are also exposed on
 * {@link angular.Module}.
 *
 * An AngularJS **service** is a singleton object created by a **service factory**.  These **service
 * factories** are functions which, in turn, are created by a **service provider**.
 * The **service providers** are constructor functions. When instantiated they must contain a
 * property called `$get`, which holds the **service factory** function.
 *
 * When you request a service, the {@link auto.$injector $injector} is responsible for finding the
 * correct **service provider**, instantiating it and then calling its `$get` **service factory**
 * function to get the instance of the **service**.
 *
 * Often services have no configuration options and there is no need to add methods to the service
 * provider.  The provider will be no more than a constructor function with a `$get` property. For
 * these cases the {@link auto.$provide $provide} service has additional helper methods to register
 * services without specifying a provider.
 *
 * * {@link auto.$provide#provider provider(name, provider)} - registers a **service provider** with the
 *     {@link auto.$injector $injector}
 * * {@link auto.$provide#constant constant(name, obj)} - registers a value/object that can be accessed by
 *     providers and services.
 * * {@link auto.$provide#value value(name, obj)} - registers a value/object that can only be accessed by
 *     services, not providers.
 * * {@link auto.$provide#factory factory(name, fn)} - registers a service **factory function**
 *     that will be wrapped in a **service provider** object, whose `$get` property will contain the
 *     given factory function.
 * * {@link auto.$provide#service service(name, Fn)} - registers a **constructor function**
 *     that will be wrapped in a **service provider** object, whose `$get` property will instantiate
 *      a new object using the given constructor function.
 * * {@link auto.$provide#decorator decorator(name, decorFn)} - registers a **decorator function** that
 *      will be able to modify or replace the implementation of another service.
 *
 * See the individual methods for more information and examples.
 */

/**
 * @ngdoc method
 * @name $provide#provider
 * @description
 *
 * Register a **provider function** with the {@link auto.$injector $injector}. Provider functions
 * are constructor functions, whose instances are responsible for "providing" a factory for a
 * service.
 *
 * Service provider names start with the name of the service they provide followed by `Provider`.
 * For example, the {@link ng.$log $log} service has a provider called
 * {@link ng.$logProvider $logProvider}.
 *
 * Service provider objects can have additional methods which allow configuration of the provider
 * and its service. Importantly, you can configure what kind of service is created by the `$get`
 * method, or how that service will act. For example, the {@link ng.$logProvider $logProvider} has a
 * method {@link ng.$logProvider#debugEnabled debugEnabled}
 * which lets you specify whether the {@link ng.$log $log} service will log debug messages to the
 * console or not.
 *
 * It is possible to inject other providers into the provider function,
 * but the injected provider must have been defined before the one that requires it.
 *
 * @param {string} name The name of the instance. NOTE: the provider will be available under `name +
                        'Provider'` key.
 * @param {(Object|function())} provider If the provider is:
 *
 *   - `Object`: then it should have a `$get` method. The `$get` method will be invoked using
 *     {@link auto.$injector#invoke $injector.invoke()} when an instance needs to be created.
 *   - `Constructor`: a new instance of the provider will be created using
 *     {@link auto.$injector#instantiate $injector.instantiate()}, then treated as `object`.
 *
 * @returns {Object} registered provider instance

 * @example
 *
 * The following example shows how to create a simple event tracking service and register it using
 * {@link auto.$provide#provider $provide.provider()}.
 *
 * ```js
 *  // Define the eventTracker provider
 *  function EventTrackerProvider() {
 *    var trackingUrl = '/track';
 *
 *    // A provider method for configuring where the tracked events should been saved
 *    this.setTrackingUrl = function(url) {
 *      trackingUrl = url;
 *    };
 *
 *    // The service factory function
 *    this.$get = ['$http', function($http) {
 *      var trackedEvents = {};
 *      return {
 *        // Call this to track an event
 *        event: function(event) {
 *          var count = trackedEvents[event] || 0;
 *          count += 1;
 *          trackedEvents[event] = count;
 *          return count;
 *        },
 *        // Call this to save the tracked events to the trackingUrl
 *        save: function() {
 *          $http.post(trackingUrl, trackedEvents);
 *        }
 *      };
 *    }];
 *  }
 *
 *  describe('eventTracker', function() {
 *    var postSpy;
 *
 *    beforeEach(module(function($provide) {
 *      // Register the eventTracker provider
 *      $provide.provider('eventTracker', EventTrackerProvider);
 *    }));
 *
 *    beforeEach(module(function(eventTrackerProvider) {
 *      // Configure eventTracker provider
 *      eventTrackerProvider.setTrackingUrl('/custom-track');
 *    }));
 *
 *    it('tracks events', inject(function(eventTracker) {
 *      expect(eventTracker.event('login')).toEqual(1);
 *      expect(eventTracker.event('login')).toEqual(2);
 *    }));
 *
 *    it('saves to the tracking url', inject(function(eventTracker, $http) {
 *      postSpy = spyOn($http, 'post');
 *      eventTracker.event('login');
 *      eventTracker.save();
 *      expect(postSpy).toHaveBeenCalled();
 *      expect(postSpy.mostRecentCall.args[0]).not.toEqual('/track');
 *      expect(postSpy.mostRecentCall.args[0]).toEqual('/custom-track');
 *      expect(postSpy.mostRecentCall.args[1]).toEqual({ 'login': 1 });
 *    }));
 *  });
 * ```
 */

/**
 * @ngdoc method
 * @name $provide#factory
 * @description
 *
 * Register a **service factory**, which will be called to return the service instance.
 * This is short for registering a service where its provider consists of only a `$get` property,
 * which is the given service factory function.
 * You should use {@link auto.$provide#factory $provide.factory(getFn)} if you do not need to
 * configure your service in a provider.
 *
 * @param {string} name The name of the instance.
 * @param {Function|Array.<string|Function>} $getFn The injectable $getFn for the instance creation.
 *                      Internally this is a short hand for `$provide.provider(name, {$get: $getFn})`.
 * @returns {Object} registered provider instance
 *
 * @example
 * Here is an example of registering a service
 * ```js
 *   $provide.factory('ping', ['$http', function($http) {
 *     return function ping() {
 *       return $http.send('/ping');
 *     };
 *   }]);
 * ```
 * You would then inject and use this service like this:
 * ```js
 *   someModule.controller('Ctrl', ['ping', function(ping) {
 *     ping();
 *   }]);
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#service
 * @description
 *
 * Register a **service constructor**, which will be invoked with `new` to create the service
 * instance.
 * This is short for registering a service where its provider's `$get` property is a factory
 * function that returns an instance instantiated by the injector from the service constructor
 * function.
 *
 * Internally it looks a bit like this:
 *
 * ```
 * {
 *   $get: function() {
 *     return $injector.instantiate(constructor);
 *   }
 * }
 * ```
 *
 *
 * You should use {@link auto.$provide#service $provide.service(class)} if you define your service
 * as a type/class.
 *
 * @param {string} name The name of the instance.
 * @param {Function|Array.<string|Function>} constructor An injectable class (constructor function)
 *     that will be instantiated.
 * @returns {Object} registered provider instance
 *
 * @example
 * Here is an example of registering a service using
 * {@link auto.$provide#service $provide.service(class)}.
 * ```js
 *   var Ping = function($http) {
 *     this.$http = $http;
 *   };
 *
 *   Ping.$inject = ['$http'];
 *
 *   Ping.prototype.send = function() {
 *     return this.$http.get('/ping');
 *   };
 *   $provide.service('ping', Ping);
 * ```
 * You would then inject and use this service like this:
 * ```js
 *   someModule.controller('Ctrl', ['ping', function(ping) {
 *     ping.send();
 *   }]);
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#value
 * @description
 *
 * Register a **value service** with the {@link auto.$injector $injector}, such as a string, a
 * number, an array, an object or a function. This is short for registering a service where its
 * provider's `$get` property is a factory function that takes no arguments and returns the **value
 * service**. That also means it is not possible to inject other services into a value service.
 *
 * Value services are similar to constant services, except that they cannot be injected into a
 * module configuration function (see {@link angular.Module#config}) but they can be overridden by
 * an AngularJS {@link auto.$provide#decorator decorator}.
 *
 * @param {string} name The name of the instance.
 * @param {*} value The value.
 * @returns {Object} registered provider instance
 *
 * @example
 * Here are some examples of creating value services.
 * ```js
 *   $provide.value('ADMIN_USER', 'admin');
 *
 *   $provide.value('RoleLookup', { admin: 0, writer: 1, reader: 2 });
 *
 *   $provide.value('halfOf', function(value) {
 *     return value / 2;
 *   });
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#constant
 * @description
 *
 * Register a **constant service** with the {@link auto.$injector $injector}, such as a string,
 * a number, an array, an object or a function. Like the {@link auto.$provide#value value}, it is not
 * possible to inject other services into a constant.
 *
 * But unlike {@link auto.$provide#value value}, a constant can be
 * injected into a module configuration function (see {@link angular.Module#config}) and it cannot
 * be overridden by an AngularJS {@link auto.$provide#decorator decorator}.
 *
 * @param {string} name The name of the constant.
 * @param {*} value The constant value.
 * @returns {Object} registered instance
 *
 * @example
 * Here a some examples of creating constants:
 * ```js
 *   $provide.constant('SHARD_HEIGHT', 306);
 *
 *   $provide.constant('MY_COLOURS', ['red', 'blue', 'grey']);
 *
 *   $provide.constant('double', function(value) {
 *     return value * 2;
 *   });
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#decorator
 * @description
 *
 * Register a **decorator function** with the {@link auto.$injector $injector}. A decorator function
 * intercepts the creation of a service, allowing it to override or modify the behavior of the
 * service. The return value of the decorator function may be the original service, or a new service
 * that replaces (or wraps and delegates to) the original service.
 *
 * You can find out more about using decorators in the {@link guide/decorators} guide.
 *
 * @param {string} name The name of the service to decorate.
 * @param {Function|Array.<string|Function>} decorator This function will be invoked when the service needs to be
 *    provided and should return the decorated service instance. The function is called using
 *    the {@link auto.$injector#invoke injector.invoke} method and is therefore fully injectable.
 *    Local injection arguments:
 *
 *    * `$delegate` - The original service instance, which can be replaced, monkey patched, configured,
 *      decorated or delegated to.
 *
 * @example
 * Here we decorate the {@link ng.$log $log} service to convert warnings to errors by intercepting
 * calls to {@link ng.$log#error $log.warn()}.
 * ```js
 *   $provide.decorator('$log', ['$delegate', function($delegate) {
 *     $delegate.warn = $delegate.error;
 *     return $delegate;
 *   }]);
 * ```
 */


function createInjector(modulesToLoad, strictDi) {
  strictDi = (strictDi === true);
  var INSTANTIATING = {},
      providerSuffix = 'Provider',
      path = [],
      loadedModules = new NgMap(),
      providerCache = {
        $provide: {
            provider: supportObject(provider),
            factory: supportObject(factory),
            service: supportObject(service),
            value: supportObject(value),
            constant: supportObject(constant),
            decorator: decorator
          }
      },
      providerInjector = (providerCache.$injector =
          createInternalInjector(providerCache, function(serviceName, caller) {
            if (angular.isString(caller)) {
              path.push(caller);
            }
            throw $injectorMinErr('unpr', 'Unknown provider: {0}', path.join(' <- '));
          })),
      instanceCache = {},
      protoInstanceInjector =
          createInternalInjector(instanceCache, function(serviceName, caller) {
            var provider = providerInjector.get(serviceName + providerSuffix, caller);
            return instanceInjector.invoke(
                provider.$get, provider, undefined, serviceName);
          }),
      instanceInjector = protoInstanceInjector;

  providerCache['$injector' + providerSuffix] = { $get: valueFn(protoInstanceInjector) };
  instanceInjector.modules = providerInjector.modules = createMap();
  var runBlocks = loadModules(modulesToLoad);
  instanceInjector = protoInstanceInjector.get('$injector');
  instanceInjector.strictDi = strictDi;
  forEach(runBlocks, function(fn) { if (fn) instanceInjector.invoke(fn); });

  instanceInjector.loadNewModules = function(mods) {
    forEach(loadModules(mods), function(fn) { if (fn) instanceInjector.invoke(fn); });
  };


  return instanceInjector;

  ////////////////////////////////////
  // $provider
  ////////////////////////////////////

  function supportObject(delegate) {
    return function(key, value) {
      if (isObject(key)) {
        forEach(key, reverseParams(delegate));
      } else {
        return delegate(key, value);
      }
    };
  }

  function provider(name, provider_) {
    assertNotHasOwnProperty(name, 'service');
    if (isFunction(provider_) || isArray(provider_)) {
      provider_ = providerInjector.instantiate(provider_);
    }
    if (!provider_.$get) {
      throw $injectorMinErr('pget', 'Provider \'{0}\' must define $get factory method.', name);
    }
    return (providerCache[name + providerSuffix] = provider_);
  }

  function enforceReturnValue(name, factory) {
    return /** @this */ function enforcedReturnValue() {
      var result = instanceInjector.invoke(factory, this);
      if (isUndefined(result)) {
        throw $injectorMinErr('undef', 'Provider \'{0}\' must return a value from $get factory method.', name);
      }
      return result;
    };
  }

  function factory(name, factoryFn, enforce) {
    return provider(name, {
      $get: enforce !== false ? enforceReturnValue(name, factoryFn) : factoryFn
    });
  }

  function service(name, constructor) {
    return factory(name, ['$injector', function($injector) {
      return $injector.instantiate(constructor);
    }]);
  }

  function value(name, val) { return factory(name, valueFn(val), false); }

  function constant(name, value) {
    assertNotHasOwnProperty(name, 'constant');
    providerCache[name] = value;
    instanceCache[name] = value;
  }

  function decorator(serviceName, decorFn) {
    var origProvider = providerInjector.get(serviceName + providerSuffix),
        orig$get = origProvider.$get;

    origProvider.$get = function() {
      var origInstance = instanceInjector.invoke(orig$get, origProvider);
      return instanceInjector.invoke(decorFn, null, {$delegate: origInstance});
    };
  }

  ////////////////////////////////////
  // Module Loading
  ////////////////////////////////////
  function loadModules(modulesToLoad) {
    assertArg(isUndefined(modulesToLoad) || isArray(modulesToLoad), 'modulesToLoad', 'not an array');
    var runBlocks = [], moduleFn;
    forEach(modulesToLoad, function(module) {
      if (loadedModules.get(module)) return;
      loadedModules.set(module, true);

      function runInvokeQueue(queue) {
        var i, ii;
        for (i = 0, ii = queue.length; i < ii; i++) {
          var invokeArgs = queue[i],
              provider = providerInjector.get(invokeArgs[0]);

          provider[invokeArgs[1]].apply(provider, invokeArgs[2]);
        }
      }

      try {
        if (isString(module)) {
          moduleFn = angularModule(module);
          instanceInjector.modules[module] = moduleFn;
          runBlocks = runBlocks.concat(loadModules(moduleFn.requires)).concat(moduleFn._runBlocks);
          runInvokeQueue(moduleFn._invokeQueue);
          runInvokeQueue(moduleFn._configBlocks);
        } else if (isFunction(module)) {
            runBlocks.push(providerInjector.invoke(module));
        } else if (isArray(module)) {
            runBlocks.push(providerInjector.invoke(module));
        } else {
          assertArgFn(module, 'module');
        }
      } catch (e) {
        if (isArray(module)) {
          module = module[module.length - 1];
        }
        if (e.message && e.stack && e.stack.indexOf(e.message) === -1) {
          // Safari & FF's stack traces don't contain error.message content
          // unlike those of Chrome and IE
          // So if stack doesn't contain message, we create a new string that contains both.
          // Since error.stack is read-only in Safari, I'm overriding e and not e.stack here.
          // eslint-disable-next-line no-ex-assign
          e = e.message + '\n' + e.stack;
        }
        throw $injectorMinErr('modulerr', 'Failed to instantiate module {0} due to:\n{1}',
                  module, e.stack || e.message || e);
      }
    });
    return runBlocks;
  }

  ////////////////////////////////////
  // internal Injector
  ////////////////////////////////////

  function createInternalInjector(cache, factory) {

    function getService(serviceName, caller) {
      if (cache.hasOwnProperty(serviceName)) {
        if (cache[serviceName] === INSTANTIATING) {
          throw $injectorMinErr('cdep', 'Circular dependency found: {0}',
                    serviceName + ' <- ' + path.join(' <- '));
        }
        return cache[serviceName];
      } else {
        try {
          path.unshift(serviceName);
          cache[serviceName] = INSTANTIATING;
          cache[serviceName] = factory(serviceName, caller);
          return cache[serviceName];
        } catch (err) {
          if (cache[serviceName] === INSTANTIATING) {
            delete cache[serviceName];
          }
          throw err;
        } finally {
          path.shift();
        }
      }
    }


    function injectionArgs(fn, locals, serviceName) {
      var args = [],
          $inject = createInjector.$$annotate(fn, strictDi, serviceName);

      for (var i = 0, length = $inject.length; i < length; i++) {
        var key = $inject[i];
        if (typeof key !== 'string') {
          throw $injectorMinErr('itkn',
                  'Incorrect injection token! Expected service name as string, got {0}', key);
        }
        args.push(locals && locals.hasOwnProperty(key) ? locals[key] :
                                                         getService(key, serviceName));
      }
      return args;
    }

    function isClass(func) {
      // Support: IE 9-11 only
      // IE 9-11 do not support classes and IE9 leaks with the code below.
      if (msie || typeof func !== 'function') {
        return false;
      }
      var result = func.$$ngIsClass;
      if (!isBoolean(result)) {
        result = func.$$ngIsClass = /^class\b/.test(stringifyFn(func));
      }
      return result;
    }

    function invoke(fn, self, locals, serviceName) {
      if (typeof locals === 'string') {
        serviceName = locals;
        locals = null;
      }

      var args = injectionArgs(fn, locals, serviceName);
      if (isArray(fn)) {
        fn = fn[fn.length - 1];
      }

      if (!isClass(fn)) {
        // http://jsperf.com/angularjs-invoke-apply-vs-switch
        // #5388
        return fn.apply(self, args);
      } else {
        args.unshift(null);
        return new (Function.prototype.bind.apply(fn, args))();
      }
    }


    function instantiate(Type, locals, serviceName) {
      // Check if Type is annotated and use just the given function at n-1 as parameter
      // e.g. someModule.factory('greeter', ['$window', function(renamed$window) {}]);
      var ctor = (isArray(Type) ? Type[Type.length - 1] : Type);
      var args = injectionArgs(Type, locals, serviceName);
      // Empty object at position 0 is ignored for invocation with `new`, but required.
      args.unshift(null);
      return new (Function.prototype.bind.apply(ctor, args))();
    }


    return {
      invoke: invoke,
      instantiate: instantiate,
      get: getService,
      annotate: createInjector.$$annotate,
      has: function(name) {
        return providerCache.hasOwnProperty(name + providerSuffix) || cache.hasOwnProperty(name);
      }
    };
  }
}

createInjector.$$annotate = annotate;

/**
 * @ngdoc provider
 * @name $anchorScrollProvider
 * @this
 *
 * @description
 * Use `$anchorScrollProvider` to disable automatic scrolling whenever
 * {@link ng.$location#hash $location.hash()} changes.
 */
function $AnchorScrollProvider() {

  var autoScrollingEnabled = true;

  /**
   * @ngdoc method
   * @name $anchorScrollProvider#disableAutoScrolling
   *
   * @description
   * By default, {@link ng.$anchorScroll $anchorScroll()} will automatically detect changes to
   * {@link ng.$location#hash $location.hash()} and scroll to the element matching the new hash.<br />
   * Use this method to disable automatic scrolling.
   *
   * If automatic scrolling is disabled, one must explicitly call
   * {@link ng.$anchorScroll $anchorScroll()} in order to scroll to the element related to the
   * current hash.
   */
  this.disableAutoScrolling = function() {
    autoScrollingEnabled = false;
  };

  /**
   * @ngdoc service
   * @name $anchorScroll
   * @kind function
   * @requires $window
   * @requires $location
   * @requires $rootScope
   *
   * @description
   * When called, it scrolls to the element related to the specified `hash` or (if omitted) to the
   * current value of {@link ng.$location#hash $location.hash()}, according to the rules specified
   * in the
   * [HTML5 spec](http://www.w3.org/html/wg/drafts/html/master/browsers.html#an-indicated-part-of-the-document).
   *
   * It also watches the {@link ng.$location#hash $location.hash()} and automatically scrolls to
   * match any anchor whenever it changes. This can be disabled by calling
   * {@link ng.$anchorScrollProvider#disableAutoScrolling $anchorScrollProvider.disableAutoScrolling()}.
   *
   * Additionally, you can use its {@link ng.$anchorScroll#yOffset yOffset} property to specify a
   * vertical scroll-offset (either fixed or dynamic).
   *
   * @param {string=} hash The hash specifying the element to scroll to. If omitted, the value of
   *                       {@link ng.$location#hash $location.hash()} will be used.
   *
   * @property {(number|function|jqLite)} yOffset
   * If set, specifies a vertical scroll-offset. This is often useful when there are fixed
   * positioned elements at the top of the page, such as navbars, headers etc.
   *
   * `yOffset` can be specified in various ways:
   * - **number**: A fixed number of pixels to be used as offset.<br /><br />
   * - **function**: A getter function called everytime `$anchorScroll()` is executed. Must return
   *   a number representing the offset (in pixels).<br /><br />
   * - **jqLite**: A jqLite/jQuery element to be used for specifying the offset. The distance from
   *   the top of the page to the element's bottom will be used as offset.<br />
   *   **Note**: The element will be taken into account only as long as its `position` is set to
   *   `fixed`. This option is useful, when dealing with responsive navbars/headers that adjust
   *   their height and/or positioning according to the viewport's size.
   *
   * <br />
   * <div class="alert alert-warning">
   * In order for `yOffset` to work properly, scrolling should take place on the document's root and
   * not some child element.
   * </div>
   *
   * @example
     <example module="anchorScrollExample" name="anchor-scroll">
       <file name="index.html">
         <div id="scrollArea" ng-controller="ScrollController">
           <a ng-click="gotoBottom()">Go to bottom</a>
           <a id="bottom"></a> You're at the bottom!
         </div>
       </file>
       <file name="script.js">
         angular.module('anchorScrollExample', [])
           .controller('ScrollController', ['$scope', '$location', '$anchorScroll',
             function($scope, $location, $anchorScroll) {
               $scope.gotoBottom = function() {
                 // set the location.hash to the id of
                 // the element you wish to scroll to.
                 $location.hash('bottom');

                 // call $anchorScroll()
                 $anchorScroll();
               };
             }]);
       </file>
       <file name="style.css">
         #scrollArea {
           height: 280px;
           overflow: auto;
         }

         #bottom {
           display: block;
           margin-top: 2000px;
         }
       </file>
     </example>
   *
   * <hr />
   * The example below illustrates the use of a vertical scroll-offset (specified as a fixed value).
   * See {@link ng.$anchorScroll#yOffset $anchorScroll.yOffset} for more details.
   *
   * @example
     <example module="anchorScrollOffsetExample" name="anchor-scroll-offset">
       <file name="index.html">
         <div class="fixed-header" ng-controller="headerCtrl">
           <a href="" ng-click="gotoAnchor(x)" ng-repeat="x in [1,2,3,4,5]">
             Go to anchor {{x}}
           </a>
         </div>
         <div id="anchor{{x}}" class="anchor" ng-repeat="x in [1,2,3,4,5]">
           Anchor {{x}} of 5
         </div>
       </file>
       <file name="script.js">
         angular.module('anchorScrollOffsetExample', [])
           .run(['$anchorScroll', function($anchorScroll) {
             $anchorScroll.yOffset = 50;   // always scroll by 50 extra pixels
           }])
           .controller('headerCtrl', ['$anchorScroll', '$location', '$scope',
             function($anchorScroll, $location, $scope) {
               $scope.gotoAnchor = function(x) {
                 var newHash = 'anchor' + x;
                 if ($location.hash() !== newHash) {
                   // set the $location.hash to `newHash` and
                   // $anchorScroll will automatically scroll to it
                   $location.hash('anchor' + x);
                 } else {
                   // call $anchorScroll() explicitly,
                   // since $location.hash hasn't changed
                   $anchorScroll();
                 }
               };
             }
           ]);
       </file>
       <file name="style.css">
         body {
           padding-top: 50px;
         }

         .anchor {
           border: 2px dashed DarkOrchid;
           padding: 10px 10px 200px 10px;
         }

         .fixed-header {
           background-color: rgba(0, 0, 0, 0.2);
           height: 50px;
           position: fixed;
           top: 0; left: 0; right: 0;
         }

         .fixed-header > a {
           display: inline-block;
           margin: 5px 15px;
         }
       </file>
     </example>
   */
  this.$get = ['$window', '$location', '$rootScope', function($window, $location, $rootScope) {
    var document = $window.document;

    // Helper function to get first anchor from a NodeList
    // (using `Array#some()` instead of `angular#forEach()` since it's more performant
    //  and working in all supported browsers.)
    function getFirstAnchor(list) {
      var result = null;
      Array.prototype.some.call(list, function(element) {
        if (nodeName_(element) === 'a') {
          result = element;
          return true;
        }
      });
      return result;
    }

    function getYOffset() {

      var offset = scroll.yOffset;

      if (isFunction(offset)) {
        offset = offset();
      } else if (isElement(offset)) {
        var elem = offset[0];
        var style = $window.getComputedStyle(elem);
        if (style.position !== 'fixed') {
          offset = 0;
        } else {
          offset = elem.getBoundingClientRect().bottom;
        }
      } else if (!isNumber(offset)) {
        offset = 0;
      }

      return offset;
    }

    function scrollTo(elem) {
      if (elem) {
        elem.scrollIntoView();

        var offset = getYOffset();

        if (offset) {
          // `offset` is the number of pixels we should scroll UP in order to align `elem` properly.
          // This is true ONLY if the call to `elem.scrollIntoView()` initially aligns `elem` at the
          // top of the viewport.
          //
          // IF the number of pixels from the top of `elem` to the end of the page's content is less
          // than the height of the viewport, then `elem.scrollIntoView()` will align the `elem` some
          // way down the page.
          //
          // This is often the case for elements near the bottom of the page.
          //
          // In such cases we do not need to scroll the whole `offset` up, just the difference between
          // the top of the element and the offset, which is enough to align the top of `elem` at the
          // desired position.
          var elemTop = elem.getBoundingClientRect().top;
          $window.scrollBy(0, elemTop - offset);
        }
      } else {
        $window.scrollTo(0, 0);
      }
    }

    function scroll(hash) {
      // Allow numeric hashes
      hash = isString(hash) ? hash : isNumber(hash) ? hash.toString() : $location.hash();
      var elm;

      // empty hash, scroll to the top of the page
      if (!hash) scrollTo(null);

      // element with given id
      else if ((elm = document.getElementById(hash))) scrollTo(elm);

      // first anchor with given name :-D
      else if ((elm = getFirstAnchor(document.getElementsByName(hash)))) scrollTo(elm);

      // no element and hash === 'top', scroll to the top of the page
      else if (hash === 'top') scrollTo(null);
    }

    // does not scroll when user clicks on anchor link that is currently on
    // (no url change, no $location.hash() change), browser native does scroll
    if (autoScrollingEnabled) {
      $rootScope.$watch(function autoScrollWatch() {return $location.hash();},
        function autoScrollWatchAction(newVal, oldVal) {
          // skip the initial scroll if $location.hash is empty
          if (newVal === oldVal && newVal === '') return;

          jqLiteDocumentLoaded(function() {
            $rootScope.$evalAsync(scroll);
          });
        });
    }

    return scroll;
  }];
}

var $animateMinErr = minErr('$animate');
var ELEMENT_NODE = 1;
var NG_ANIMATE_CLASSNAME = 'ng-animate';

function mergeClasses(a,b) {
  if (!a && !b) return '';
  if (!a) return b;
  if (!b) return a;
  if (isArray(a)) a = a.join(' ');
  if (isArray(b)) b = b.join(' ');
  return a + ' ' + b;
}

function extractElementNode(element) {
  for (var i = 0; i < element.length; i++) {
    var elm = element[i];
    if (elm.nodeType === ELEMENT_NODE) {
      return elm;
    }
  }
}

function splitClasses(classes) {
  if (isString(classes)) {
    classes = classes.split(' ');
  }

  // Use createMap() to prevent class assumptions involving property names in
  // Object.prototype
  var obj = createMap();
  forEach(classes, function(klass) {
    // sometimes the split leaves empty string values
    // incase extra spaces were applied to the options
    if (klass.length) {
      obj[klass] = true;
    }
  });
  return obj;
}

// if any other type of options value besides an Object value is
// passed into the $animate.method() animation then this helper code
// will be run which will ignore it. While this patch is not the
// greatest solution to this, a lot of existing plugins depend on
// $animate to either call the callback (< 1.2) or return a promise
// that can be changed. This helper function ensures that the options
// are wiped clean incase a callback function is provided.
function prepareAnimateOptions(options) {
  return isObject(options)
      ? options
      : {};
}

var $$CoreAnimateJsProvider = /** @this */ function() {
  this.$get = noop;
};

// this is prefixed with Core since it conflicts with
// the animateQueueProvider defined in ngAnimate/animateQueue.js
var $$CoreAnimateQueueProvider = /** @this */ function() {
  var postDigestQueue = new NgMap();
  var postDigestElements = [];

  this.$get = ['$$AnimateRunner', '$rootScope',
       function($$AnimateRunner,   $rootScope) {
    return {
      enabled: noop,
      on: noop,
      off: noop,
      pin: noop,

      push: function(element, event, options, domOperation) {
        if (domOperation) {
          domOperation();
        }

        options = options || {};
        if (options.from) {
          element.css(options.from);
        }
        if (options.to) {
          element.css(options.to);
        }

        if (options.addClass || options.removeClass) {
          addRemoveClassesPostDigest(element, options.addClass, options.removeClass);
        }

        var runner = new $$AnimateRunner();

        // since there are no animations to run the runner needs to be
        // notified that the animation call is complete.
        runner.complete();
        return runner;
      }
    };


    function updateData(data, classes, value) {
      var changed = false;
      if (classes) {
        classes = isString(classes) ? classes.split(' ') :
                  isArray(classes) ? classes : [];
        forEach(classes, function(className) {
          if (className) {
            changed = true;
            data[className] = value;
          }
        });
      }
      return changed;
    }

    function handleCSSClassChanges() {
      forEach(postDigestElements, function(element) {
        var data = postDigestQueue.get(element);
        if (data) {
          var existing = splitClasses(element.attr('class'));
          var toAdd = '';
          var toRemove = '';
          forEach(data, function(status, className) {
            var hasClass = !!existing[className];
            if (status !== hasClass) {
              if (status) {
                toAdd += (toAdd.length ? ' ' : '') + className;
              } else {
                toRemove += (toRemove.length ? ' ' : '') + className;
              }
            }
          });

          forEach(element, function(elm) {
            if (toAdd) {
              jqLiteAddClass(elm, toAdd);
            }
            if (toRemove) {
              jqLiteRemoveClass(elm, toRemove);
            }
          });
          postDigestQueue.delete(element);
        }
      });
      postDigestElements.length = 0;
    }


    function addRemoveClassesPostDigest(element, add, remove) {
      var data = postDigestQueue.get(element) || {};

      var classesAdded = updateData(data, add, true);
      var classesRemoved = updateData(data, remove, false);

      if (classesAdded || classesRemoved) {

        postDigestQueue.set(element, data);
        postDigestElements.push(element);

        if (postDigestElements.length === 1) {
          $rootScope.$$postDigest(handleCSSClassChanges);
        }
      }
    }
  }];
};

/**
 * @ngdoc provider
 * @name $animateProvider
 *
 * @description
 * Default implementation of $animate that doesn't perform any animations, instead just
 * synchronously performs DOM updates and resolves the returned runner promise.
 *
 * In order to enable animations the `ngAnimate` module has to be loaded.
 *
 * To see the functional implementation check out `src/ngAnimate/animate.js`.
 */
var $AnimateProvider = ['$provide', /** @this */ function($provide) {
  var provider = this;
  var classNameFilter = null;
  var customFilter = null;

  this.$$registeredAnimations = Object.create(null);

   /**
   * @ngdoc method
   * @name $animateProvider#register
   *
   * @description
   * Registers a new injectable animation factory function. The factory function produces the
   * animation object which contains callback functions for each event that is expected to be
   * animated.
   *
   *   * `eventFn`: `function(element, ... , doneFunction, options)`
   *   The element to animate, the `doneFunction` and the options fed into the animation. Depending
   *   on the type of animation additional arguments will be injected into the animation function. The
   *   list below explains the function signatures for the different animation methods:
   *
   *   - setClass: function(element, addedClasses, removedClasses, doneFunction, options)
   *   - addClass: function(element, addedClasses, doneFunction, options)
   *   - removeClass: function(element, removedClasses, doneFunction, options)
   *   - enter, leave, move: function(element, doneFunction, options)
   *   - animate: function(element, fromStyles, toStyles, doneFunction, options)
   *
   *   Make sure to trigger the `doneFunction` once the animation is fully complete.
   *
   * ```js
   *   return {
   *     //enter, leave, move signature
   *     eventFn : function(element, done, options) {
   *       //code to run the animation
   *       //once complete, then run done()
   *       return function endFunction(wasCancelled) {
   *         //code to cancel the animation
   *       }
   *     }
   *   }
   * ```
   *
   * @param {string} name The name of the animation (this is what the class-based CSS value will be compared to).
   * @param {Function} factory The factory function that will be executed to return the animation
   *                           object.
   */
  this.register = function(name, factory) {
    if (name && name.charAt(0) !== '.') {
      throw $animateMinErr('notcsel', 'Expecting class selector starting with \'.\' got \'{0}\'.', name);
    }

    var key = name + '-animation';
    provider.$$registeredAnimations[name.substr(1)] = key;
    $provide.factory(key, factory);
  };

  /**
   * @ngdoc method
   * @name $animateProvider#customFilter
   *
   * @description
   * Sets and/or returns the custom filter function that is used to "filter" animations, i.e.
   * determine if an animation is allowed or not. When no filter is specified (the default), no
   * animation will be blocked. Setting the `customFilter` value will only allow animations for
   * which the filter function's return value is truthy.
   *
   * This allows to easily create arbitrarily complex rules for filtering animations, such as
   * allowing specific events only, or enabling animations on specific subtrees of the DOM, etc.
   * Filtering animations can also boost performance for low-powered devices, as well as
   * applications containing a lot of structural operations.
   *
   * <div class="alert alert-success">
   *   **Best Practice:**
   *   Keep the filtering function as lean as possible, because it will be called for each DOM
   *   action (e.g. insertion, removal, class change) performed by "animation-aware" directives.
   *   See {@link guide/animations#which-directives-support-animations- here} for a list of built-in
   *   directives that support animations.
   *   Performing computationally expensive or time-consuming operations on each call of the
   *   filtering function can make your animations sluggish.
   * </div>
   *
   * **Note:** If present, `customFilter` will be checked before
   * {@link $animateProvider#classNameFilter classNameFilter}.
   *
   * @param {Function=} filterFn - The filter function which will be used to filter all animations.
   *   If a falsy value is returned, no animation will be performed. The function will be called
   *   with the following arguments:
   *   - **node** `{DOMElement}` - The DOM element to be animated.
   *   - **event** `{String}` - The name of the animation event (e.g. `enter`, `leave`, `addClass`
   *     etc).
   *   - **options** `{Object}` - A collection of options/styles used for the animation.
   * @return {Function} The current filter function or `null` if there is none set.
   */
  this.customFilter = function(filterFn) {
    if (arguments.length === 1) {
      customFilter = isFunction(filterFn) ? filterFn : null;
    }

    return customFilter;
  };

  /**
   * @ngdoc method
   * @name $animateProvider#classNameFilter
   *
   * @description
   * Sets and/or returns the CSS class regular expression that is checked when performing
   * an animation. Upon bootstrap the classNameFilter value is not set at all and will
   * therefore enable $animate to attempt to perform an animation on any element that is triggered.
   * When setting the `classNameFilter` value, animations will only be performed on elements
   * that successfully match the filter expression. This in turn can boost performance
   * for low-powered devices as well as applications containing a lot of structural operations.
   *
   * **Note:** If present, `classNameFilter` will be checked after
   * {@link $animateProvider#customFilter customFilter}. If `customFilter` is present and returns
   * false, `classNameFilter` will not be checked.
   *
   * @param {RegExp=} expression The className expression which will be checked against all animations
   * @return {RegExp} The current CSS className expression value. If null then there is no expression value
   */
  this.classNameFilter = function(expression) {
    if (arguments.length === 1) {
      classNameFilter = (expression instanceof RegExp) ? expression : null;
      if (classNameFilter) {
        var reservedRegex = new RegExp('[(\\s|\\/)]' + NG_ANIMATE_CLASSNAME + '[(\\s|\\/)]');
        if (reservedRegex.test(classNameFilter.toString())) {
          classNameFilter = null;
          throw $animateMinErr('nongcls', '$animateProvider.classNameFilter(regex) prohibits accepting a regex value which matches/contains the "{0}" CSS class.', NG_ANIMATE_CLASSNAME);
        }
      }
    }
    return classNameFilter;
  };

  this.$get = ['$$animateQueue', function($$animateQueue) {
    function domInsert(element, parentElement, afterElement) {
      // if for some reason the previous element was removed
      // from the dom sometime before this code runs then let's
      // just stick to using the parent element as the anchor
      if (afterElement) {
        var afterNode = extractElementNode(afterElement);
        if (afterNode && !afterNode.parentNode && !afterNode.previousElementSibling) {
          afterElement = null;
        }
      }
      if (afterElement) {
        afterElement.after(element);
      } else {
        parentElement.prepend(element);
      }
    }

    /**
     * @ngdoc service
     * @name $animate
     * @description The $animate service exposes a series of DOM utility methods that provide support
     * for animation hooks. The default behavior is the application of DOM operations, however,
     * when an animation is detected (and animations are enabled), $animate will do the heavy lifting
     * to ensure that animation runs with the triggered DOM operation.
     *
     * By default $animate doesn't trigger any animations. This is because the `ngAnimate` module isn't
     * included and only when it is active then the animation hooks that `$animate` triggers will be
     * functional. Once active then all structural `ng-` directives will trigger animations as they perform
     * their DOM-related operations (enter, leave and move). Other directives such as `ngClass`,
     * `ngShow`, `ngHide` and `ngMessages` also provide support for animations.
     *
     * It is recommended that the`$animate` service is always used when executing DOM-related procedures within directives.
     *
     * To learn more about enabling animation support, click here to visit the
     * {@link ngAnimate ngAnimate module page}.
     */
    return {
      // we don't call it directly since non-existant arguments may
      // be interpreted as null within the sub enabled function

      /**
       *
       * @ngdoc method
       * @name $animate#on
       * @kind function
       * @description Sets up an event listener to fire whenever the animation event (enter, leave, move, etc...)
       *    has fired on the given element or among any of its children. Once the listener is fired, the provided callback
       *    is fired with the following params:
       *
       * ```js
       * $animate.on('enter', container,
       *    function callback(element, phase) {
       *      // cool we detected an enter animation within the container
       *    }
       * );
       * ```
       *
       * <div class="alert alert-warning">
       * **Note**: Generally, the events that are fired correspond 1:1 to `$animate` method names,
       * e.g. {@link ng.$animate#addClass addClass()} will fire `addClass`, and {@link ng.ngClass}
       * will fire `addClass` if classes are added, and `removeClass` if classes are removed.
       * However, there are two exceptions:
       *
       * <ul>
       *   <li>if both an {@link ng.$animate#addClass addClass()} and a
       *   {@link ng.$animate#removeClass removeClass()} action are performed during the same
       *   animation, the event fired will be `setClass`. This is true even for `ngClass`.</li>
       *   <li>an {@link ng.$animate#animate animate()} call that adds and removes classes will fire
       *   the `setClass` event, but if it either removes or adds classes,
       *   it will fire `animate` instead.</li>
       * </ul>
       *
       * </div>
       *
       * @param {string} event the animation event that will be captured (e.g. enter, leave, move, addClass, removeClass, etc...)
       * @param {DOMElement} container the container element that will capture each of the animation events that are fired on itself
       *     as well as among its children
       * @param {Function} callback the callback function that will be fired when the listener is triggered.
       *
       * The arguments present in the callback function are:
       * * `element` - The captured DOM element that the animation was fired on.
       * * `phase` - The phase of the animation. The two possible phases are **start** (when the animation starts) and **close** (when it ends).
       * * `data` - an object with these properties:
       *     * addClass - `{string|null}` - space-separated CSS classes to add to the element
       *     * removeClass - `{string|null}` - space-separated CSS classes to remove from the element
       *     * from - `{Object|null}` - CSS properties & values at the beginning of the animation
       *     * to - `{Object|null}` - CSS properties & values at the end of the animation
       *
       * Note that the callback does not trigger a scope digest. Wrap your call into a
       * {@link $rootScope.Scope#$apply scope.$apply} to propagate changes to the scope.
       */
      on: $$animateQueue.on,

      /**
       *
       * @ngdoc method
       * @name $animate#off
       * @kind function
       * @description Deregisters an event listener based on the event which has been associated with the provided element. This method
       * can be used in three different ways depending on the arguments:
       *
       * ```js
       * // remove all the animation event listeners listening for `enter`
       * $animate.off('enter');
       *
       * // remove listeners for all animation events from the container element
       * $animate.off(container);
       *
       * // remove all the animation event listeners listening for `enter` on the given element and its children
       * $animate.off('enter', container);
       *
       * // remove the event listener function provided by `callback` that is set
       * // to listen for `enter` on the given `container` as well as its children
       * $animate.off('enter', container, callback);
       * ```
       *
       * @param {string|DOMElement} event|container the animation event (e.g. enter, leave, move,
       * addClass, removeClass, etc...), or the container element. If it is the element, all other
       * arguments are ignored.
       * @param {DOMElement=} container the container element the event listener was placed on
       * @param {Function=} callback the callback function that was registered as the listener
       */
      off: $$animateQueue.off,

      /**
       * @ngdoc method
       * @name $animate#pin
       * @kind function
       * @description Associates the provided element with a host parent element to allow the element to be animated even if it exists
       *    outside of the DOM structure of the AngularJS application. By doing so, any animation triggered via `$animate` can be issued on the
       *    element despite being outside the realm of the application or within another application. Say for example if the application
       *    was bootstrapped on an element that is somewhere inside of the `<body>` tag, but we wanted to allow for an element to be situated
       *    as a direct child of `document.body`, then this can be achieved by pinning the element via `$animate.pin(element)`. Keep in mind
       *    that calling `$animate.pin(element, parentElement)` will not actually insert into the DOM anywhere; it will just create the association.
       *
       *    Note that this feature is only active when the `ngAnimate` module is used.
       *
       * @param {DOMElement} element the external element that will be pinned
       * @param {DOMElement} parentElement the host parent element that will be associated with the external element
       */
      pin: $$animateQueue.pin,

      /**
       *
       * @ngdoc method
       * @name $animate#enabled
       * @kind function
       * @description Used to get and set whether animations are enabled or not on the entire application or on an element and its children. This
       * function can be called in four ways:
       *
       * ```js
       * // returns true or false
       * $animate.enabled();
       *
       * // changes the enabled state for all animations
       * $animate.enabled(false);
       * $animate.enabled(true);
       *
       * // returns true or false if animations are enabled for an element
       * $animate.enabled(element);
       *
       * // changes the enabled state for an element and its children
       * $animate.enabled(element, true);
       * $animate.enabled(element, false);
       * ```
       *
       * @param {DOMElement=} element the element that will be considered for checking/setting the enabled state
       * @param {boolean=} enabled whether or not the animations will be enabled for the element
       *
       * @return {boolean} whether or not animations are enabled
       */
      enabled: $$animateQueue.enabled,

      /**
       * @ngdoc method
       * @name $animate#cancel
       * @kind function
       * @description Cancels the provided animation and applies the end state of the animation.
       * Note that this does not cancel the underlying operation, e.g. the setting of classes or
       * adding the element to the DOM.
       *
       * @param {animationRunner} animationRunner An animation runner returned by an $animate function.
       *
       * @example
        <example module="animationExample" deps="angular-animate.js" animations="true" name="animate-cancel">
          <file name="app.js">
            angular.module('animationExample', ['ngAnimate']).component('cancelExample', {
              templateUrl: 'template.html',
              controller: function($element, $animate) {
                this.runner = null;

                this.addClass = function() {
                  this.runner = $animate.addClass($element.find('div'), 'red');
                  var ctrl = this;
                  this.runner.finally(function() {
                    ctrl.runner = null;
                  });
                };

                this.removeClass = function() {
                  this.runner = $animate.removeClass($element.find('div'), 'red');
                  var ctrl = this;
                  this.runner.finally(function() {
                    ctrl.runner = null;
                  });
                };

                this.cancel = function() {
                  $animate.cancel(this.runner);
                };
              }
            });
          </file>
          <file name="template.html">
            <p>
              <button id="add" ng-click="$ctrl.addClass()">Add</button>
              <button ng-click="$ctrl.removeClass()">Remove</button>
              <br>
              <button id="cancel" ng-click="$ctrl.cancel()" ng-disabled="!$ctrl.runner">Cancel</button>
              <br>
              <div id="target">CSS-Animated Text</div>
            </p>
          </file>
          <file name="index.html">
            <cancel-example></cancel-example>
          </file>
          <file name="style.css">
            .red-add, .red-remove {
              transition: all 4s cubic-bezier(0.250, 0.460, 0.450, 0.940);
            }

            .red,
            .red-add.red-add-active {
              color: #FF0000;
              font-size: 40px;
            }

            .red-remove.red-remove-active {
              font-size: 10px;
              color: black;
            }

          </file>
        </example>
       */
      cancel: function(runner) {
        if (runner.cancel) {
          runner.cancel();
        }
      },

      /**
       *
       * @ngdoc method
       * @name $animate#enter
       * @kind function
       * @description Inserts the element into the DOM either after the `after` element (if provided) or
       *   as the first child within the `parent` element and then triggers an animation.
       *   A promise is returned that will be resolved during the next digest once the animation
       *   has completed.
       *
       * @param {DOMElement} element the element which will be inserted into the DOM
       * @param {DOMElement} parent the parent element which will append the element as
       *   a child (so long as the after element is not present)
       * @param {DOMElement=} after the sibling element after which the element will be appended
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Runner} the animation runner
       */
      enter: function(element, parent, after, options) {
        parent = parent && jqLite(parent);
        after = after && jqLite(after);
        parent = parent || after.parent();
        domInsert(element, parent, after);
        return $$animateQueue.push(element, 'enter', prepareAnimateOptions(options));
      },

      /**
       *
       * @ngdoc method
       * @name $animate#move
       * @kind function
       * @description Inserts (moves) the element into its new position in the DOM either after
       *   the `after` element (if provided) or as the first child within the `parent` element
       *   and then triggers an animation. A promise is returned that will be resolved
       *   during the next digest once the animation has completed.
       *
       * @param {DOMElement} element the element which will be moved into the new DOM position
       * @param {DOMElement} parent the parent element which will append the element as
       *   a child (so long as the after element is not present)
       * @param {DOMElement=} after the sibling element after which the element will be appended
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Runner} the animation runner
       */
      move: function(element, parent, after, options) {
        parent = parent && jqLite(parent);
        after = after && jqLite(after);
        parent = parent || after.parent();
        domInsert(element, parent, after);
        return $$animateQueue.push(element, 'move', prepareAnimateOptions(options));
      },

      /**
       * @ngdoc method
       * @name $animate#leave
       * @kind function
       * @description Triggers an animation and then removes the element from the DOM.
       * When the function is called a promise is returned that will be resolved during the next
       * digest once the animation has completed.
       *
       * @param {DOMElement} element the element which will be removed from the DOM
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Runner} the animation runner
       */
      leave: function(element, options) {
        return $$animateQueue.push(element, 'leave', prepareAnimateOptions(options), function() {
          element.remove();
        });
      },

      /**
       * @ngdoc method
       * @name $animate#addClass
       * @kind function
       *
       * @description Triggers an addClass animation surrounding the addition of the provided CSS class(es). Upon
       *   execution, the addClass operation will only be handled after the next digest and it will not trigger an
       *   animation if element already contains the CSS class or if the class is removed at a later step.
       *   Note that class-based animations are treated differently compared to structural animations
       *   (like enter, move and leave) since the CSS classes may be added/removed at different points
       *   depending if CSS or JavaScript animations are used.
       *
       * @param {DOMElement} element the element which the CSS classes will be applied to
       * @param {string} className the CSS class(es) that will be added (multiple classes are separated via spaces)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Runner} animationRunner the animation runner
       */
      addClass: function(element, className, options) {
        options = prepareAnimateOptions(options);
        options.addClass = mergeClasses(options.addclass, className);
        return $$animateQueue.push(element, 'addClass', options);
      },

      /**
       * @ngdoc method
       * @name $animate#removeClass
       * @kind function
       *
       * @description Triggers a removeClass animation surrounding the removal of the provided CSS class(es). Upon
       *   execution, the removeClass operation will only be handled after the next digest and it will not trigger an
       *   animation if element does not contain the CSS class or if the class is added at a later step.
       *   Note that class-based animations are treated differently compared to structural animations
       *   (like enter, move and leave) since the CSS classes may be added/removed at different points
       *   depending if CSS or JavaScript animations are used.
       *
       * @param {DOMElement} element the element which the CSS classes will be applied to
       * @param {string} className the CSS class(es) that will be removed (multiple classes are separated via spaces)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Runner} the animation runner
       */
      removeClass: function(element, className, options) {
        options = prepareAnimateOptions(options);
        options.removeClass = mergeClasses(options.removeClass, className);
        return $$animateQueue.push(element, 'removeClass', options);
      },

      /**
       * @ngdoc method
       * @name $animate#setClass
       * @kind function
       *
       * @description Performs both the addition and removal of a CSS classes on an element and (during the process)
       *    triggers an animation surrounding the class addition/removal. Much like `$animate.addClass` and
       *    `$animate.removeClass`, `setClass` will only evaluate the classes being added/removed once a digest has
       *    passed. Note that class-based animations are treated differently compared to structural animations
       *    (like enter, move and leave) since the CSS classes may be added/removed at different points
       *    depending if CSS or JavaScript animations are used.
       *
       * @param {DOMElement} element the element which the CSS classes will be applied to
       * @param {string} add the CSS class(es) that will be added (multiple classes are separated via spaces)
       * @param {string} remove the CSS class(es) that will be removed (multiple classes are separated via spaces)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Runner} the animation runner
       */
      setClass: function(element, add, remove, options) {
        options = prepareAnimateOptions(options);
        options.addClass = mergeClasses(options.addClass, add);
        options.removeClass = mergeClasses(options.removeClass, remove);
        return $$animateQueue.push(element, 'setClass', options);
      },

      /**
       * @ngdoc method
       * @name $animate#animate
       * @kind function
       *
       * @description Performs an inline animation on the element which applies the provided to and from CSS styles to the element.
       * If any detected CSS transition, keyframe or JavaScript matches the provided className value, then the animation will take
       * on the provided styles. For example, if a transition animation is set for the given className, then the provided `from` and
       * `to` styles will be applied alongside the given transition. If the CSS style provided in `from` does not have a corresponding
       * style in `to`, the style in `from` is applied immediately, and no animation is run.
       * If a JavaScript animation is detected then the provided styles will be given in as function parameters into the `animate`
       * method (or as part of the `options` parameter):
       *
       * ```js
       * ngModule.animation('.my-inline-animation', function() {
       *   return {
       *     animate : function(element, from, to, done, options) {
       *       //animation
       *       done();
       *     }
       *   }
       * });
       * ```
       *
       * @param {DOMElement} element the element which the CSS styles will be applied to
       * @param {object} from the from (starting) CSS styles that will be applied to the element and across the animation.
       * @param {object} to the to (destination) CSS styles that will be applied to the element and across the animation.
       * @param {string=} className an optional CSS class that will be applied to the element for the duration of the animation. If
       *    this value is left as empty then a CSS class of `ng-inline-animate` will be applied to the element.
       *    (Note that if no animation is detected then this value will not be applied to the element.)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Runner} the animation runner
       */
      animate: function(element, from, to, className, options) {
        options = prepareAnimateOptions(options);
        options.from = options.from ? extend(options.from, from) : from;
        options.to   = options.to   ? extend(options.to, to)     : to;

        className = className || 'ng-inline-animate';
        options.tempClasses = mergeClasses(options.tempClasses, className);
        return $$animateQueue.push(element, 'animate', options);
      }
    };
  }];
}];

var $$AnimateAsyncRunFactoryProvider = /** @this */ function() {
  this.$get = ['$$rAF', function($$rAF) {
    var waitQueue = [];

    function waitForTick(fn) {
      waitQueue.push(fn);
      if (waitQueue.length > 1) return;
      $$rAF(function() {
        for (var i = 0; i < waitQueue.length; i++) {
          waitQueue[i]();
        }
        waitQueue = [];
      });
    }

    return function() {
      var passed = false;
      waitForTick(function() {
        passed = true;
      });
      return function(callback) {
        if (passed) {
          callback();
        } else {
          waitForTick(callback);
        }
      };
    };
  }];
};

var $$AnimateRunnerFactoryProvider = /** @this */ function() {
  this.$get = ['$q', '$sniffer', '$$animateAsyncRun', '$$isDocumentHidden', '$timeout',
       function($q,   $sniffer,   $$animateAsyncRun,   $$isDocumentHidden,   $timeout) {

    var INITIAL_STATE = 0;
    var DONE_PENDING_STATE = 1;
    var DONE_COMPLETE_STATE = 2;

    AnimateRunner.chain = function(chain, callback) {
      var index = 0;

      next();
      function next() {
        if (index === chain.length) {
          callback(true);
          return;
        }

        chain[index](function(response) {
          if (response === false) {
            callback(false);
            return;
          }
          index++;
          next();
        });
      }
    };

    AnimateRunner.all = function(runners, callback) {
      var count = 0;
      var status = true;
      forEach(runners, function(runner) {
        runner.done(onProgress);
      });

      function onProgress(response) {
        status = status && response;
        if (++count === runners.length) {
          callback(status);
        }
      }
    };

    function AnimateRunner(host) {
      this.setHost(host);

      var rafTick = $$animateAsyncRun();
      var timeoutTick = function(fn) {
        $timeout(fn, 0, false);
      };

      this._doneCallbacks = [];
      this._tick = function(fn) {
        if ($$isDocumentHidden()) {
          timeoutTick(fn);
        } else {
          rafTick(fn);
        }
      };
      this._state = 0;
    }

    AnimateRunner.prototype = {
      setHost: function(host) {
        this.host = host || {};
      },

      done: function(fn) {
        if (this._state === DONE_COMPLETE_STATE) {
          fn();
        } else {
          this._doneCallbacks.push(fn);
        }
      },

      progress: noop,

      getPromise: function() {
        if (!this.promise) {
          var self = this;
          this.promise = $q(function(resolve, reject) {
            self.done(function(status) {
              if (status === false) {
                reject();
              } else {
                resolve();
              }
            });
          });
        }
        return this.promise;
      },

      then: function(resolveHandler, rejectHandler) {
        return this.getPromise().then(resolveHandler, rejectHandler);
      },

      'catch': function(handler) {
        return this.getPromise()['catch'](handler);
      },

      'finally': function(handler) {
        return this.getPromise()['finally'](handler);
      },

      pause: function() {
        if (this.host.pause) {
          this.host.pause();
        }
      },

      resume: function() {
        if (this.host.resume) {
          this.host.resume();
        }
      },

      end: function() {
        if (this.host.end) {
          this.host.end();
        }
        this._resolve(true);
      },

      cancel: function() {
        if (this.host.cancel) {
          this.host.cancel();
        }
        this._resolve(false);
      },

      complete: function(response) {
        var self = this;
        if (self._state === INITIAL_STATE) {
          self._state = DONE_PENDING_STATE;
          self._tick(function() {
            self._resolve(response);
          });
        }
      },

      _resolve: function(response) {
        if (this._state !== DONE_COMPLETE_STATE) {
          forEach(this._doneCallbacks, function(fn) {
            fn(response);
          });
          this._doneCallbacks.length = 0;
          this._state = DONE_COMPLETE_STATE;
        }
      }
    };

    return AnimateRunner;
  }];
};

/* exported $CoreAnimateCssProvider */

/**
 * @ngdoc service
 * @name $animateCss
 * @kind object
 * @this
 *
 * @description
 * This is the core version of `$animateCss`. By default, only when the `ngAnimate` is included,
 * then the `$animateCss` service will actually perform animations.
 *
 * Click here {@link ngAnimate.$animateCss to read the documentation for $animateCss}.
 */
var $CoreAnimateCssProvider = function() {
  this.$get = ['$$rAF', '$q', '$$AnimateRunner', function($$rAF, $q, $$AnimateRunner) {

    return function(element, initialOptions) {
      // all of the animation functions should create
      // a copy of the options data, however, if a
      // parent service has already created a copy then
      // we should stick to using that
      var options = initialOptions || {};
      if (!options.$$prepared) {
        options = copy(options);
      }

      // there is no point in applying the styles since
      // there is no animation that goes on at all in
      // this version of $animateCss.
      if (options.cleanupStyles) {
        options.from = options.to = null;
      }

      if (options.from) {
        element.css(options.from);
        options.from = null;
      }

      var closed, runner = new $$AnimateRunner();
      return {
        start: run,
        end: run
      };

      function run() {
        $$rAF(function() {
          applyAnimationContents();
          if (!closed) {
            runner.complete();
          }
          closed = true;
        });
        return runner;
      }

      function applyAnimationContents() {
        if (options.addClass) {
          element.addClass(options.addClass);
          options.addClass = null;
        }
        if (options.removeClass) {
          element.removeClass(options.removeClass);
          options.removeClass = null;
        }
        if (options.to) {
          element.css(options.to);
          options.to = null;
        }
      }
    };
  }];
};

/* global getHash: true, stripHash: false */

function getHash(url) {
  var index = url.indexOf('#');
  return index === -1 ? '' : url.substr(index);
}

function trimEmptyHash(url) {
  return url.replace(/#$/, '');
}

/**
 * ! This is a private undocumented service !
 *
 * @name $browser
 * @requires $log
 * @description
 * This object has two goals:
 *
 * - hide all the global state in the browser caused by the window object
 * - abstract away all the browser specific features and inconsistencies
 *
 * For tests we provide {@link ngMock.$browser mock implementation} of the `$browser`
 * service, which can be used for convenient testing of the application without the interaction with
 * the real browser apis.
 */
/**
 * @param {object} window The global window object.
 * @param {object} document jQuery wrapped document.
 * @param {object} $log window.console or an object with the same interface.
 * @param {object} $sniffer $sniffer service
 */
function Browser(window, document, $log, $sniffer, $$taskTrackerFactory) {
  var self = this,
      location = window.location,
      history = window.history,
      setTimeout = window.setTimeout,
      clearTimeout = window.clearTimeout,
      pendingDeferIds = {},
      taskTracker = $$taskTrackerFactory($log);

  self.isMock = false;

  //////////////////////////////////////////////////////////////
  // Task-tracking API
  //////////////////////////////////////////////////////////////

  // TODO(vojta): remove this temporary api
  self.$$completeOutstandingRequest = taskTracker.completeTask;
  self.$$incOutstandingRequestCount = taskTracker.incTaskCount;

  // TODO(vojta): prefix this method with $$ ?
  self.notifyWhenNoOutstandingRequests = taskTracker.notifyWhenNoPendingTasks;

  //////////////////////////////////////////////////////////////
  // URL API
  //////////////////////////////////////////////////////////////

  var cachedState, lastHistoryState,
      lastBrowserUrl = location.href,
      baseElement = document.find('base'),
      pendingLocation = null,
      getCurrentState = !$sniffer.history ? noop : function getCurrentState() {
        try {
          return history.state;
        } catch (e) {
          // MSIE can reportedly throw when there is no state (UNCONFIRMED).
        }
      };

  cacheState();

  /**
   * @name $browser#url
   *
   * @description
   * GETTER:
   * Without any argument, this method just returns current value of `location.href` (with a
   * trailing `#` stripped of if the hash is empty).
   *
   * SETTER:
   * With at least one argument, this method sets url to new value.
   * If html5 history api supported, `pushState`/`replaceState` is used, otherwise
   * `location.href`/`location.replace` is used.
   * Returns its own instance to allow chaining.
   *
   * NOTE: this api is intended for use only by the `$location` service. Please use the
   * {@link ng.$location $location service} to change url.
   *
   * @param {string} url New url (when used as setter)
   * @param {boolean=} replace Should new url replace current history record?
   * @param {object=} state State object to use with `pushState`/`replaceState`
   */
  self.url = function(url, replace, state) {
    // In modern browsers `history.state` is `null` by default; treating it separately
    // from `undefined` would cause `$browser.url('/foo')` to change `history.state`
    // to undefined via `pushState`. Instead, let's change `undefined` to `null` here.
    if (isUndefined(state)) {
      state = null;
    }

    // Android Browser BFCache causes location, history reference to become stale.
    if (location !== window.location) location = window.location;
    if (history !== window.history) history = window.history;

    // setter
    if (url) {
      var sameState = lastHistoryState === state;

      // Normalize the inputted URL
      url = urlResolve(url).href;

      // Don't change anything if previous and current URLs and states match. This also prevents
      // IE<10 from getting into redirect loop when in LocationHashbangInHtml5Url mode.
      // See https://github.com/angular/angular.js/commit/ffb2701
      if (lastBrowserUrl === url && (!$sniffer.history || sameState)) {
        return self;
      }
      var sameBase = lastBrowserUrl && stripHash(lastBrowserUrl) === stripHash(url);
      lastBrowserUrl = url;
      lastHistoryState = state;
      // Don't use history API if only the hash changed
      // due to a bug in IE10/IE11 which leads
      // to not firing a `hashchange` nor `popstate` event
      // in some cases (see #9143).
      if ($sniffer.history && (!sameBase || !sameState)) {
        history[replace ? 'replaceState' : 'pushState'](state, '', url);
        cacheState();
      } else {
        if (!sameBase) {
          pendingLocation = url;
        }
        if (replace) {
          location.replace(url);
        } else if (!sameBase) {
          location.href = url;
        } else {
          location.hash = getHash(url);
        }
        if (location.href !== url) {
          pendingLocation = url;
        }
      }
      if (pendingLocation) {
        pendingLocation = url;
      }
      return self;
    // getter
    } else {
      // - pendingLocation is needed as browsers don't allow to read out
      //   the new location.href if a reload happened or if there is a bug like in iOS 9 (see
      //   https://openradar.appspot.com/22186109).
      return trimEmptyHash(pendingLocation || location.href);
    }
  };

  /**
   * @name $browser#state
   *
   * @description
   * This method is a getter.
   *
   * Return history.state or null if history.state is undefined.
   *
   * @returns {object} state
   */
  self.state = function() {
    return cachedState;
  };

  var urlChangeListeners = [],
      urlChangeInit = false;

  function cacheStateAndFireUrlChange() {
    pendingLocation = null;
    fireStateOrUrlChange();
  }

  // This variable should be used *only* inside the cacheState function.
  var lastCachedState = null;
  function cacheState() {
    // This should be the only place in $browser where `history.state` is read.
    cachedState = getCurrentState();
    cachedState = isUndefined(cachedState) ? null : cachedState;

    // Prevent callbacks fo fire twice if both hashchange & popstate were fired.
    if (equals(cachedState, lastCachedState)) {
      cachedState = lastCachedState;
    }

    lastCachedState = cachedState;
    lastHistoryState = cachedState;
  }

  function fireStateOrUrlChange() {
    var prevLastHistoryState = lastHistoryState;
    cacheState();

    if (lastBrowserUrl === self.url() && prevLastHistoryState === cachedState) {
      return;
    }

    lastBrowserUrl = self.url();
    lastHistoryState = cachedState;
    forEach(urlChangeListeners, function(listener) {
      listener(self.url(), cachedState);
    });
  }

  /**
   * @name $browser#onUrlChange
   *
   * @description
   * Register callback function that will be called, when url changes.
   *
   * It's only called when the url is changed from outside of AngularJS:
   * - user types different url into address bar
   * - user clicks on history (forward/back) button
   * - user clicks on a link
   *
   * It's not called when url is changed by $browser.url() method
   *
   * The listener gets called with new url as parameter.
   *
   * NOTE: this api is intended for use only by the $location service. Please use the
   * {@link ng.$location $location service} to monitor url changes in AngularJS apps.
   *
   * @param {function(string)} listener Listener function to be called when url changes.
   * @return {function(string)} Returns the registered listener fn - handy if the fn is anonymous.
   */
  self.onUrlChange = function(callback) {
    // TODO(vojta): refactor to use node's syntax for events
    if (!urlChangeInit) {
      // We listen on both (hashchange/popstate) when available, as some browsers don't
      // fire popstate when user changes the address bar and don't fire hashchange when url
      // changed by push/replaceState

      // html5 history api - popstate event
      if ($sniffer.history) jqLite(window).on('popstate', cacheStateAndFireUrlChange);
      // hashchange event
      jqLite(window).on('hashchange', cacheStateAndFireUrlChange);

      urlChangeInit = true;
    }

    urlChangeListeners.push(callback);
    return callback;
  };

  /**
   * @private
   * Remove popstate and hashchange handler from window.
   *
   * NOTE: this api is intended for use only by $rootScope.
   */
  self.$$applicationDestroyed = function() {
    jqLite(window).off('hashchange popstate', cacheStateAndFireUrlChange);
  };

  /**
   * Checks whether the url has changed outside of AngularJS.
   * Needs to be exported to be able to check for changes that have been done in sync,
   * as hashchange/popstate events fire in async.
   */
  self.$$checkUrlChange = fireStateOrUrlChange;

  //////////////////////////////////////////////////////////////
  // Misc API
  //////////////////////////////////////////////////////////////

  /**
   * @name $browser#baseHref
   *
   * @description
   * Returns current <base href>
   * (always relative - without domain)
   *
   * @returns {string} The current base href
   */
  self.baseHref = function() {
    var href = baseElement.attr('href');
    return href ? href.replace(/^(https?:)?\/\/[^/]*/, '') : '';
  };

  /**
   * @name $browser#defer
   * @param {function()} fn A function, who's execution should be deferred.
   * @param {number=} [delay=0] Number of milliseconds to defer the function execution.
   * @param {string=} [taskType=DEFAULT_TASK_TYPE] The type of task that is deferred.
   * @returns {*} DeferId that can be used to cancel the task via `$browser.defer.cancel()`.
   *
   * @description
   * Executes a fn asynchronously via `setTimeout(fn, delay)`.
   *
   * Unlike when calling `setTimeout` directly, in test this function is mocked and instead of using
   * `setTimeout` in tests, the fns are queued in an array, which can be programmatically flushed
   * via `$browser.defer.flush()`.
   *
   */
  self.defer = function(fn, delay, taskType) {
    var timeoutId;

    delay = delay || 0;
    taskType = taskType || taskTracker.DEFAULT_TASK_TYPE;

    taskTracker.incTaskCount(taskType);
    timeoutId = setTimeout(function() {
      delete pendingDeferIds[timeoutId];
      taskTracker.completeTask(fn, taskType);
    }, delay);
    pendingDeferIds[timeoutId] = taskType;

    return timeoutId;
  };


  /**
   * @name $browser#defer.cancel
   *
   * @description
   * Cancels a deferred task identified with `deferId`.
   *
   * @param {*} deferId Token returned by the `$browser.defer` function.
   * @returns {boolean} Returns `true` if the task hasn't executed yet and was successfully
   *                    canceled.
   */
  self.defer.cancel = function(deferId) {
    if (pendingDeferIds.hasOwnProperty(deferId)) {
      var taskType = pendingDeferIds[deferId];
      delete pendingDeferIds[deferId];
      clearTimeout(deferId);
      taskTracker.completeTask(noop, taskType);
      return true;
    }
    return false;
  };

}

/** @this */
function $BrowserProvider() {
  this.$get = ['$window', '$log', '$sniffer', '$document', '$$taskTrackerFactory',
       function($window,   $log,   $sniffer,   $document,   $$taskTrackerFactory) {
    return new Browser($window, $document, $log, $sniffer, $$taskTrackerFactory);
  }];
}

/**
 * @ngdoc service
 * @name $cacheFactory
 * @this
 *
 * @description
 * Factory that constructs {@link $cacheFactory.Cache Cache} objects and gives access to
 * them.
 *
 * ```js
 *
 *  var cache = $cacheFactory('cacheId');
 *  expect($cacheFactory.get('cacheId')).toBe(cache);
 *  expect($cacheFactory.get('noSuchCacheId')).not.toBeDefined();
 *
 *  cache.put("key", "value");
 *  cache.put("another key", "another value");
 *
 *  // We've specified no options on creation
 *  expect(cache.info()).toEqual({id: 'cacheId', size: 2});
 *
 * ```
 *
 *
 * @param {string} cacheId Name or id of the newly created cache.
 * @param {object=} options Options object that specifies the cache behavior. Properties:
 *
 *   - `{number=}` `capacity` — turns the cache into LRU cache.
 *
 * @returns {object} Newly created cache object with the following set of methods:
 *
 * - `{object}` `info()` — Returns id, size, and options of cache.
 * - `{{*}}` `put({string} key, {*} value)` — Puts a new key-value pair into the cache and returns
 *   it.
 * - `{{*}}` `get({string} key)` — Returns cached value for `key` or undefined for cache miss.
 * - `{void}` `remove({string} key)` — Removes a key-value pair from the cache.
 * - `{void}` `removeAll()` — Removes all cached values.
 * - `{void}` `destroy()` — Removes references to this cache from $cacheFactory.
 *
 * @example
   <example module="cacheExampleApp" name="cache-factory">
     <file name="index.html">
       <div ng-controller="CacheController">
         <input ng-model="newCacheKey" placeholder="Key">
         <input ng-model="newCacheValue" placeholder="Value">
         <button ng-click="put(newCacheKey, newCacheValue)">Cache</button>

         <p ng-if="keys.length">Cached Values</p>
         <div ng-repeat="key in keys">
           <span ng-bind="key"></span>
           <span>: </span>
           <b ng-bind="cache.get(key)"></b>
         </div>

         <p>Cache Info</p>
         <div ng-repeat="(key, value) in cache.info()">
           <span ng-bind="key"></span>
           <span>: </span>
           <b ng-bind="value"></b>
         </div>
       </div>
     </file>
     <file name="script.js">
       angular.module('cacheExampleApp', []).
         controller('CacheController', ['$scope', '$cacheFactory', function($scope, $cacheFactory) {
           $scope.keys = [];
           $scope.cache = $cacheFactory('cacheId');
           $scope.put = function(key, value) {
             if (angular.isUndefined($scope.cache.get(key))) {
               $scope.keys.push(key);
             }
             $scope.cache.put(key, angular.isUndefined(value) ? null : value);
           };
         }]);
     </file>
     <file name="style.css">
       p {
         margin: 10px 0 3px;
       }
     </file>
   </example>
 */
function $CacheFactoryProvider() {

  this.$get = function() {
    var caches = {};

    function cacheFactory(cacheId, options) {
      if (cacheId in caches) {
        throw minErr('$cacheFactory')('iid', 'CacheId \'{0}\' is already taken!', cacheId);
      }

      var size = 0,
          stats = extend({}, options, {id: cacheId}),
          data = createMap(),
          capacity = (options && options.capacity) || Number.MAX_VALUE,
          lruHash = createMap(),
          freshEnd = null,
          staleEnd = null;

      /**
       * @ngdoc type
       * @name $cacheFactory.Cache
       *
       * @description
       * A cache object used to store and retrieve data, primarily used by
       * {@link $templateRequest $templateRequest} and the {@link ng.directive:script script}
       * directive to cache templates and other data.
       *
       * ```js
       *  angular.module('superCache')
       *    .factory('superCache', ['$cacheFactory', function($cacheFactory) {
       *      return $cacheFactory('super-cache');
       *    }]);
       * ```
       *
       * Example test:
       *
       * ```js
       *  it('should behave like a cache', inject(function(superCache) {
       *    superCache.put('key', 'value');
       *    superCache.put('another key', 'another value');
       *
       *    expect(superCache.info()).toEqual({
       *      id: 'super-cache',
       *      size: 2
       *    });
       *
       *    superCache.remove('another key');
       *    expect(superCache.get('another key')).toBeUndefined();
       *
       *    superCache.removeAll();
       *    expect(superCache.info()).toEqual({
       *      id: 'super-cache',
       *      size: 0
       *    });
       *  }));
       * ```
       */
      return (caches[cacheId] = {

        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#put
         * @kind function
         *
         * @description
         * Inserts a named entry into the {@link $cacheFactory.Cache Cache} object to be
         * retrieved later, and incrementing the size of the cache if the key was not already
         * present in the cache. If behaving like an LRU cache, it will also remove stale
         * entries from the set.
         *
         * It will not insert undefined values into the cache.
         *
         * @param {string} key the key under which the cached data is stored.
         * @param {*} value the value to store alongside the key. If it is undefined, the key
         *    will not be stored.
         * @returns {*} the value stored.
         */
        put: function(key, value) {
          if (isUndefined(value)) return;
          if (capacity < Number.MAX_VALUE) {
            var lruEntry = lruHash[key] || (lruHash[key] = {key: key});

            refresh(lruEntry);
          }

          if (!(key in data)) size++;
          data[key] = value;

          if (size > capacity) {
            this.remove(staleEnd.key);
          }

          return value;
        },

        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#get
         * @kind function
         *
         * @description
         * Retrieves named data stored in the {@link $cacheFactory.Cache Cache} object.
         *
         * @param {string} key the key of the data to be retrieved
         * @returns {*} the value stored.
         */
        get: function(key) {
          if (capacity < Number.MAX_VALUE) {
            var lruEntry = lruHash[key];

            if (!lruEntry) return;

            refresh(lruEntry);
          }

          return data[key];
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#remove
         * @kind function
         *
         * @description
         * Removes an entry from the {@link $cacheFactory.Cache Cache} object.
         *
         * @param {string} key the key of the entry to be removed
         */
        remove: function(key) {
          if (capacity < Number.MAX_VALUE) {
            var lruEntry = lruHash[key];

            if (!lruEntry) return;

            if (lruEntry === freshEnd) freshEnd = lruEntry.p;
            if (lruEntry === staleEnd) staleEnd = lruEntry.n;
            link(lruEntry.n,lruEntry.p);

            delete lruHash[key];
          }

          if (!(key in data)) return;

          delete data[key];
          size--;
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#removeAll
         * @kind function
         *
         * @description
         * Clears the cache object of any entries.
         */
        removeAll: function() {
          data = createMap();
          size = 0;
          lruHash = createMap();
          freshEnd = staleEnd = null;
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#destroy
         * @kind function
         *
         * @description
         * Destroys the {@link $cacheFactory.Cache Cache} object entirely,
         * removing it from the {@link $cacheFactory $cacheFactory} set.
         */
        destroy: function() {
          data = null;
          stats = null;
          lruHash = null;
          delete caches[cacheId];
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#info
         * @kind function
         *
         * @description
         * Retrieve information regarding a particular {@link $cacheFactory.Cache Cache}.
         *
         * @returns {object} an object with the following properties:
         *   <ul>
         *     <li>**id**: the id of the cache instance</li>
         *     <li>**size**: the number of entries kept in the cache instance</li>
         *     <li>**...**: any additional properties from the options object when creating the
         *       cache.</li>
         *   </ul>
         */
        info: function() {
          return extend({}, stats, {size: size});
        }
      });


      /**
       * makes the `entry` the freshEnd of the LRU linked list
       */
      function refresh(entry) {
        if (entry !== freshEnd) {
          if (!staleEnd) {
            staleEnd = entry;
          } else if (staleEnd === entry) {
            staleEnd = entry.n;
          }

          link(entry.n, entry.p);
          link(entry, freshEnd);
          freshEnd = entry;
          freshEnd.n = null;
        }
      }


      /**
       * bidirectionally links two entries of the LRU linked list
       */
      function link(nextEntry, prevEntry) {
        if (nextEntry !== prevEntry) {
          if (nextEntry) nextEntry.p = prevEntry; //p stands for previous, 'prev' didn't minify
          if (prevEntry) prevEntry.n = nextEntry; //n stands for next, 'next' didn't minify
        }
      }
    }


  /**
   * @ngdoc method
   * @name $cacheFactory#info
   *
   * @description
   * Get information about all the caches that have been created
   *
   * @returns {Object} - key-value map of `cacheId` to the result of calling `cache#info`
   */
    cacheFactory.info = function() {
      var info = {};
      forEach(caches, function(cache, cacheId) {
        info[cacheId] = cache.info();
      });
      return info;
    };


  /**
   * @ngdoc method
   * @name $cacheFactory#get
   *
   * @description
   * Get access to a cache object by the `cacheId` used when it was created.
   *
   * @param {string} cacheId Name or id of a cache to access.
   * @returns {object} Cache object identified by the cacheId or undefined if no such cache.
   */
    cacheFactory.get = function(cacheId) {
      return caches[cacheId];
    };


    return cacheFactory;
  };
}

/**
 * @ngdoc service
 * @name $templateCache
 * @this
 *
 * @description
 * `$templateCache` is a {@link $cacheFactory.Cache Cache object} created by the
 * {@link ng.$cacheFactory $cacheFactory}.
 *
 * The first time a template is used, it is loaded in the template cache for quick retrieval. You
 * can load templates directly into the cache in a `script` tag, by using {@link $templateRequest},
 * or by consuming the `$templateCache` service directly.
 *
 * Adding via the `script` tag:
 *
 * ```html
 *   <script type="text/ng-template" id="templateId.html">
 *     <p>This is the content of the template</p>
 *   </script>
 * ```
 *
 * **Note:** the `script` tag containing the template does not need to be included in the `head` of
 * the document, but it must be a descendent of the {@link ng.$rootElement $rootElement} (e.g.
 * element with {@link ngApp} attribute), otherwise the template will be ignored.
 *
 * Adding via the `$templateCache` service:
 *
 * ```js
 * var myApp = angular.module('myApp', []);
 * myApp.run(function($templateCache) {
 *   $templateCache.put('templateId.html', 'This is the content of the template');
 * });
 * ```
 *
 * To retrieve the template later, simply use it in your component:
 * ```js
 * myApp.component('myComponent', {
 *    templateUrl: 'templateId.html'
 * });
 * ```
 *
 * or get it via the `$templateCache` service:
 * ```js
 * $templateCache.get('templateId.html')
 * ```
 *
 */
function $TemplateCacheProvider() {
  this.$get = ['$cacheFactory', function($cacheFactory) {
    return $cacheFactory('templates');
  }];
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables like document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/* ! VARIABLE/FUNCTION NAMING CONVENTIONS THAT APPLY TO THIS FILE!
 *
 * DOM-related variables:
 *
 * - "node" - DOM Node
 * - "element" - DOM Element or Node
 * - "$node" or "$element" - jqLite-wrapped node or element
 *
 *
 * Compiler related stuff:
 *
 * - "linkFn" - linking fn of a single directive
 * - "nodeLinkFn" - function that aggregates all linking fns for a particular node
 * - "childLinkFn" -  function that aggregates all linking fns for child nodes of a particular node
 * - "compositeLinkFn" - function that aggregates all linking fns for a compilation root (nodeList)
 */


/**
 * @ngdoc service
 * @name $compile
 * @kind function
 *
 * @description
 * Compiles an HTML string or DOM into a template and produces a template function, which
 * can then be used to link {@link ng.$rootScope.Scope `scope`} and the template together.
 *
 * The compilation is a process of walking the DOM tree and matching DOM elements to
 * {@link ng.$compileProvider#directive directives}.
 *
 * <div class="alert alert-warning">
 * **Note:** This document is an in-depth reference of all directive options.
 * For a gentle introduction to directives with examples of common use cases,
 * see the {@link guide/directive directive guide}.
 * </div>
 *
 * ## Comprehensive Directive API
 *
 * There are many different options for a directive.
 *
 * The difference resides in the return value of the factory function.
 * You can either return a {@link $compile#directive-definition-object Directive Definition Object (see below)}
 * that defines the directive properties, or just the `postLink` function (all other properties will have
 * the default values).
 *
 * <div class="alert alert-success">
 * **Best Practice:** It's recommended to use the "directive definition object" form.
 * </div>
 *
 * Here's an example directive declared with a Directive Definition Object:
 *
 * ```js
 *   var myModule = angular.module(...);
 *
 *   myModule.directive('directiveName', function factory(injectables) {
 *     var directiveDefinitionObject = {
 *       {@link $compile#-priority- priority}: 0,
 *       {@link $compile#-template- template}: '<div></div>', // or // function(tElement, tAttrs) { ... },
 *       // or
 *       // {@link $compile#-templateurl- templateUrl}: 'directive.html', // or // function(tElement, tAttrs) { ... },
 *       {@link $compile#-transclude- transclude}: false,
 *       {@link $compile#-restrict- restrict}: 'A',
 *       {@link $compile#-templatenamespace- templateNamespace}: 'html',
 *       {@link $compile#-scope- scope}: false,
 *       {@link $compile#-controller- controller}: function($scope, $element, $attrs, $transclude, otherInjectables) { ... },
 *       {@link $compile#-controlleras- controllerAs}: 'stringIdentifier',
 *       {@link $compile#-bindtocontroller- bindToController}: false,
 *       {@link $compile#-require- require}: 'siblingDirectiveName', // or // ['^parentDirectiveName', '?optionalDirectiveName', '?^optionalParent'],
 *       {@link $compile#-multielement- multiElement}: false,
 *       {@link $compile#-compile- compile}: function compile(tElement, tAttrs, transclude) {
 *         return {
 *            {@link $compile#pre-linking-function pre}: function preLink(scope, iElement, iAttrs, controller) { ... },
 *            {@link $compile#post-linking-function post}: function postLink(scope, iElement, iAttrs, controller) { ... }
 *         }
 *         // or
 *         // return function postLink( ... ) { ... }
 *       },
 *       // or
 *       // {@link $compile#-link- link}: {
 *       //  {@link $compile#pre-linking-function pre}: function preLink(scope, iElement, iAttrs, controller) { ... },
 *       //  {@link $compile#post-linking-function post}: function postLink(scope, iElement, iAttrs, controller) { ... }
 *       // }
 *       // or
 *       // {@link $compile#-link- link}: function postLink( ... ) { ... }
 *     };
 *     return directiveDefinitionObject;
 *   });
 * ```
 *
 * <div class="alert alert-warning">
 * **Note:** Any unspecified options will use the default value. You can see the default values below.
 * </div>
 *
 * Therefore the above can be simplified as:
 *
 * ```js
 *   var myModule = angular.module(...);
 *
 *   myModule.directive('directiveName', function factory(injectables) {
 *     var directiveDefinitionObject = {
 *       link: function postLink(scope, iElement, iAttrs) { ... }
 *     };
 *     return directiveDefinitionObject;
 *     // or
 *     // return function postLink(scope, iElement, iAttrs) { ... }
 *   });
 * ```
 *
 * ### Life-cycle hooks
 * Directive controllers can provide the following methods that are called by AngularJS at points in the life-cycle of the
 * directive:
 * * `$onInit()` - Called on each controller after all the controllers on an element have been constructed and
 *   had their bindings initialized (and before the pre &amp; post linking functions for the directives on
 *   this element). This is a good place to put initialization code for your controller.
 * * `$onChanges(changesObj)` - Called whenever one-way (`<`) or interpolation (`@`) bindings are updated. The
 *   `changesObj` is a hash whose keys are the names of the bound properties that have changed, and the values are an
 *   object of the form `{ currentValue, previousValue, isFirstChange() }`. Use this hook to trigger updates within a
 *   component such as cloning the bound value to prevent accidental mutation of the outer value. Note that this will
 *   also be called when your bindings are initialized.
 * * `$doCheck()` - Called on each turn of the digest cycle. Provides an opportunity to detect and act on
 *   changes. Any actions that you wish to take in response to the changes that you detect must be
 *   invoked from this hook; implementing this has no effect on when `$onChanges` is called. For example, this hook
 *   could be useful if you wish to perform a deep equality check, or to check a Date object, changes to which would not
 *   be detected by AngularJS's change detector and thus not trigger `$onChanges`. This hook is invoked with no arguments;
 *   if detecting changes, you must store the previous value(s) for comparison to the current values.
 * * `$onDestroy()` - Called on a controller when its containing scope is destroyed. Use this hook for releasing
 *   external resources, watches and event handlers. Note that components have their `$onDestroy()` hooks called in
 *   the same order as the `$scope.$broadcast` events are triggered, which is top down. This means that parent
 *   components will have their `$onDestroy()` hook called before child components.
 * * `$postLink()` - Called after this controller's element and its children have been linked. Similar to the post-link
 *   function this hook can be used to set up DOM event handlers and do direct DOM manipulation.
 *   Note that child elements that contain `templateUrl` directives will not have been compiled and linked since
 *   they are waiting for their template to load asynchronously and their own compilation and linking has been
 *   suspended until that occurs.
 *
 * #### Comparison with life-cycle hooks in the new Angular
 * The new Angular also uses life-cycle hooks for its components. While the AngularJS life-cycle hooks are similar there are
 * some differences that you should be aware of, especially when it comes to moving your code from AngularJS to Angular:
 *
 * * AngularJS hooks are prefixed with `$`, such as `$onInit`. Angular hooks are prefixed with `ng`, such as `ngOnInit`.
 * * AngularJS hooks can be defined on the controller prototype or added to the controller inside its constructor.
 *   In Angular you can only define hooks on the prototype of the Component class.
 * * Due to the differences in change-detection, you may get many more calls to `$doCheck` in AngularJS than you would to
 *   `ngDoCheck` in Angular.
 * * Changes to the model inside `$doCheck` will trigger new turns of the digest loop, which will cause the changes to be
 *   propagated throughout the application.
 *   Angular does not allow the `ngDoCheck` hook to trigger a change outside of the component. It will either throw an
 *   error or do nothing depending upon the state of `enableProdMode()`.
 *
 * #### Life-cycle hook examples
 *
 * This example shows how you can check for mutations to a Date object even though the identity of the object
 * has not changed.
 *
 * <example name="doCheckDateExample" module="do-check-module">
 *   <file name="app.js">
 *     angular.module('do-check-module', [])
 *       .component('app', {
 *         template:
 *           'Month: <input ng-model="$ctrl.month" ng-change="$ctrl.updateDate()">' +
 *           'Date: {{ $ctrl.date }}' +
 *           '<test date="$ctrl.date"></test>',
 *         controller: function() {
 *           this.date = new Date();
 *           this.month = this.date.getMonth();
 *           this.updateDate = function() {
 *             this.date.setMonth(this.month);
 *           };
 *         }
 *       })
 *       .component('test', {
 *         bindings: { date: '<' },
 *         template:
 *           '<pre>{{ $ctrl.log | json }}</pre>',
 *         controller: function() {
 *           var previousValue;
 *           this.log = [];
 *           this.$doCheck = function() {
 *             var currentValue = this.date && this.date.valueOf();
 *             if (previousValue !== currentValue) {
 *               this.log.push('doCheck: date mutated: ' + this.date);
 *               previousValue = currentValue;
 *             }
 *           };
 *         }
 *       });
 *   </file>
 *   <file name="index.html">
 *     <app></app>
 *   </file>
 * </example>
 *
 * This example show how you might use `$doCheck` to trigger changes in your component's inputs even if the
 * actual identity of the component doesn't change. (Be aware that cloning and deep equality checks on large
 * arrays or objects can have a negative impact on your application performance.)
 *
 * <example name="doCheckArrayExample" module="do-check-module">
 *   <file name="index.html">
 *     <div ng-init="items = []">
 *       <button ng-click="items.push(items.length)">Add Item</button>
 *       <button ng-click="items = []">Reset Items</button>
 *       <pre>{{ items }}</pre>
 *       <test items="items"></test>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *      angular.module('do-check-module', [])
 *        .component('test', {
 *          bindings: { items: '<' },
 *          template:
 *            '<pre>{{ $ctrl.log | json }}</pre>',
 *          controller: function() {
 *            this.log = [];
 *
 *            this.$doCheck = function() {
 *              if (this.items_ref !== this.items) {
 *                this.log.push('doCheck: items changed');
 *                this.items_ref = this.items;
 *              }
 *              if (!angular.equals(this.items_clone, this.items)) {
 *                this.log.push('doCheck: items mutated');
 *                this.items_clone = angular.copy(this.items);
 *              }
 *            };
 *          }
 *        });
 *   </file>
 * </example>
 *
 *
 * ### Directive Definition Object
 *
 * The directive definition object provides instructions to the {@link ng.$compile
 * compiler}. The attributes are:
 *
 * #### `multiElement`
 * When this property is set to true (default is `false`), the HTML compiler will collect DOM nodes between
 * nodes with the attributes `directive-name-start` and `directive-name-end`, and group them
 * together as the directive elements. It is recommended that this feature be used on directives
 * which are not strictly behavioral (such as {@link ngClick}), and which
 * do not manipulate or replace child nodes (such as {@link ngInclude}).
 *
 * #### `priority`
 * When there are multiple directives defined on a single DOM element, sometimes it
 * is necessary to specify the order in which the directives are applied. The `priority` is used
 * to sort the directives before their `compile` functions get called. Priority is defined as a
 * number. Directives with greater numerical `priority` are compiled first. Pre-link functions
 * are also run in priority order, but post-link functions are run in reverse order. The order
 * of directives with the same priority is undefined. The default priority is `0`.
 *
 * #### `terminal`
 * If set to true then the current `priority` will be the last set of directives
 * which will execute (any directives at the current priority will still execute
 * as the order of execution on same `priority` is undefined). Note that expressions
 * and other directives used in the directive's template will also be excluded from execution.
 *
 * #### `scope`
 * The scope property can be `false`, `true`, or an object:
 *
 * * **`false` (default):** No scope will be created for the directive. The directive will use its
 * parent's scope.
 *
 * * **`true`:** A new child scope that prototypically inherits from its parent will be created for
 * the directive's element. If multiple directives on the same element request a new scope,
 * only one new scope is created.
 *
 * * **`{...}` (an object hash):** A new "isolate" scope is created for the directive's template.
 * The 'isolate' scope differs from normal scope in that it does not prototypically
 * inherit from its parent scope. This is useful when creating reusable components, which should not
 * accidentally read or modify data in the parent scope. Note that an isolate scope
 * directive without a `template` or `templateUrl` will not apply the isolate scope
 * to its children elements.
 *
 * The 'isolate' scope object hash defines a set of local scope properties derived from attributes on the
 * directive's element. These local properties are useful for aliasing values for templates. The keys in
 * the object hash map to the name of the property on the isolate scope; the values define how the property
 * is bound to the parent scope, via matching attributes on the directive's element:
 *
 * * `@` or `@attr` - bind a local scope property to the value of DOM attribute. The result is
 *   always a string since DOM attributes are strings. If no `attr` name is specified then the
 *   attribute name is assumed to be the same as the local name. Given `<my-component
 *   my-attr="hello {{name}}">` and the isolate scope definition `scope: { localName:'@myAttr' }`,
 *   the directive's scope property `localName` will reflect the interpolated value of `hello
 *   {{name}}`. As the `name` attribute changes so will the `localName` property on the directive's
 *   scope. The `name` is read from the parent scope (not the directive's scope).
 *
 * * `=` or `=attr` - set up a bidirectional binding between a local scope property and an expression
 *   passed via the attribute `attr`. The expression is evaluated in the context of the parent scope.
 *   If no `attr` name is specified then the attribute name is assumed to be the same as the local
 *   name. Given `<my-component my-attr="parentModel">` and the isolate scope definition `scope: {
 *   localModel: '=myAttr' }`, the property `localModel` on the directive's scope will reflect the
 *   value of `parentModel` on the parent scope. Changes to `parentModel` will be reflected in
 *   `localModel` and vice versa. If the binding expression is non-assignable, or if the attribute
 *   isn't  optional and doesn't exist, an exception
 *   ({@link error/$compile/nonassign `$compile:nonassign`}) will be thrown upon discovering changes
 *   to the local value, since it will be impossible to sync them back to the parent scope.
 *
 *   By default, the {@link ng.$rootScope.Scope#$watch `$watch`}
 *   method is used for tracking changes, and the equality check is based on object identity.
 *   However, if an object literal or an array literal is passed as the binding expression, the
 *   equality check is done by value (using the {@link angular.equals} function). It's also possible
 *   to watch the evaluated value shallowly with {@link ng.$rootScope.Scope#$watchCollection
 *   `$watchCollection`}: use `=*` or `=*attr`
 *
  * * `<` or `<attr` - set up a one-way (one-directional) binding between a local scope property and an
 *   expression passed via the attribute `attr`. The expression is evaluated in the context of the
 *   parent scope. If no `attr` name is specified then the attribute name is assumed to be the same as the
 *   local name.
 *
 *   For example, given `<my-component my-attr="parentModel">` and directive definition of
 *   `scope: { localModel:'<myAttr' }`, then the isolated scope property `localModel` will reflect the
 *   value of `parentModel` on the parent scope. Any changes to `parentModel` will be reflected
 *   in `localModel`, but changes in `localModel` will not reflect in `parentModel`. There are however
 *   two caveats:
 *     1. one-way binding does not copy the value from the parent to the isolate scope, it simply
 *     sets the same value. That means if your bound value is an object, changes to its properties
 *     in the isolated scope will be reflected in the parent scope (because both reference the same object).
 *     2. one-way binding watches changes to the **identity** of the parent value. That means the
 *     {@link ng.$rootScope.Scope#$watch `$watch`} on the parent value only fires if the reference
 *     to the value has changed. In most cases, this should not be of concern, but can be important
 *     to know if you one-way bind to an object, and then replace that object in the isolated scope.
 *     If you now change a property of the object in your parent scope, the change will not be
 *     propagated to the isolated scope, because the identity of the object on the parent scope
 *     has not changed. Instead you must assign a new object.
 *
 *   One-way binding is useful if you do not plan to propagate changes to your isolated scope bindings
 *   back to the parent. However, it does not make this completely impossible.
 *
 *   By default, the {@link ng.$rootScope.Scope#$watch `$watch`}
 *   method is used for tracking changes, and the equality check is based on object identity.
 *   It's also possible to watch the evaluated value shallowly with
 *   {@link ng.$rootScope.Scope#$watchCollection `$watchCollection`}: use `<*` or `<*attr`
 *
 * * `&` or `&attr` - provides a way to execute an expression in the context of the parent scope. If
 *   no `attr` name is specified then the attribute name is assumed to be the same as the local name.
 *   Given `<my-component my-attr="count = count + value">` and the isolate scope definition `scope: {
 *   localFn:'&myAttr' }`, the isolate scope property `localFn` will point to a function wrapper for
 *   the `count = count + value` expression. Often it's desirable to pass data from the isolated scope
 *   via an expression to the parent scope. This can be done by passing a map of local variable names
 *   and values into the expression wrapper fn. For example, if the expression is `increment(amount)`
 *   then we can specify the amount value by calling the `localFn` as `localFn({amount: 22})`.
 *
 * All 4 kinds of bindings (`@`, `=`, `<`, and `&`) can be made optional by adding `?` to the expression.
 * The marker must come after the mode and before the attribute name.
 * See the {@link error/$compile/iscp Invalid Isolate Scope Definition error} for definition examples.
 * This is useful to refine the interface directives provide.
 * One subtle difference between optional and non-optional happens **when the binding attribute is not
 * set**:
 * - the binding is optional: the property will not be defined
 * - the binding is not optional: the property is defined
 *
 * ```js
 *app.directive('testDir', function() {
    return {
      scope: {
        notoptional: '=',
        optional: '=?',
      },
      bindToController: true,
      controller: function() {
        this.$onInit = function() {
          console.log(this.hasOwnProperty('notoptional')) // true
          console.log(this.hasOwnProperty('optional')) // false
        }
      }
    }
  })
 *```
 *
 *
 * ##### Combining directives with different scope defintions
 *
 * In general it's possible to apply more than one directive to one element, but there might be limitations
 * depending on the type of scope required by the directives. The following points will help explain these limitations.
 * For simplicity only two directives are taken into account, but it is also applicable for several directives:
 *
 * * **no scope** + **no scope** => Two directives which don't require their own scope will use their parent's scope
 * * **child scope** + **no scope** =>  Both directives will share one single child scope
 * * **child scope** + **child scope** =>  Both directives will share one single child scope
 * * **isolated scope** + **no scope** =>  The isolated directive will use it's own created isolated scope. The other directive will use
 * its parent's scope
 * * **isolated scope** + **child scope** =>  **Won't work!** Only one scope can be related to one element. Therefore these directives cannot
 * be applied to the same element.
 * * **isolated scope** + **isolated scope**  =>  **Won't work!** Only one scope can be related to one element. Therefore these directives
 * cannot be applied to the same element.
 *
 *
 * #### `bindToController`
 * This property is used to bind scope properties directly to the controller. It can be either
 * `true` or an object hash with the same format as the `scope` property.
 *
 * When an isolate scope is used for a directive (see above), `bindToController: true` will
 * allow a component to have its properties bound to the controller, rather than to scope.
 *
 * After the controller is instantiated, the initial values of the isolate scope bindings will be bound to the controller
 * properties. You can access these bindings once they have been initialized by providing a controller method called
 * `$onInit`, which is called after all the controllers on an element have been constructed and had their bindings
 * initialized.
 *
 * It is also possible to set `bindToController` to an object hash with the same format as the `scope` property.
 * This will set up the scope bindings to the controller directly. Note that `scope` can still be used
 * to define which kind of scope is created. By default, no scope is created. Use `scope: {}` to create an isolate
 * scope (useful for component directives).
 *
 * If both `bindToController` and `scope` are defined and have object hashes, `bindToController` overrides `scope`.
 *
 *
 * #### `controller`
 * Controller constructor function. The controller is instantiated before the
 * pre-linking phase and can be accessed by other directives (see
 * `require` attribute). This allows the directives to communicate with each other and augment
 * each other's behavior. The controller is injectable (and supports bracket notation) with the following locals:
 *
 * * `$scope` - Current scope associated with the element
 * * `$element` - Current element
 * * `$attrs` - Current attributes object for the element
 * * `$transclude` - A transclude linking function pre-bound to the correct transclusion scope:
 *   `function([scope], cloneLinkingFn, futureParentElement, slotName)`:
 *    * `scope`: (optional) override the scope.
 *    * `cloneLinkingFn`: (optional) argument to create clones of the original transcluded content.
 *    * `futureParentElement` (optional):
 *        * defines the parent to which the `cloneLinkingFn` will add the cloned elements.
 *        * default: `$element.parent()` resp. `$element` for `transclude:'element'` resp. `transclude:true`.
 *        * only needed for transcludes that are allowed to contain non html elements (e.g. SVG elements)
 *          and when the `cloneLinkingFn` is passed,
 *          as those elements need to created and cloned in a special way when they are defined outside their
 *          usual containers (e.g. like `<svg>`).
 *        * See also the `directive.templateNamespace` property.
 *    * `slotName`: (optional) the name of the slot to transclude. If falsy (e.g. `null`, `undefined` or `''`)
 *      then the default transclusion is provided.
 *    The `$transclude` function also has a method on it, `$transclude.isSlotFilled(slotName)`, which returns
 *    `true` if the specified slot contains content (i.e. one or more DOM nodes).
 *
 * #### `require`
 * Require another directive and inject its controller as the fourth argument to the linking function. The
 * `require` property can be a string, an array or an object:
 * * a **string** containing the name of the directive to pass to the linking function
 * * an **array** containing the names of directives to pass to the linking function. The argument passed to the
 * linking function will be an array of controllers in the same order as the names in the `require` property
 * * an **object** whose property values are the names of the directives to pass to the linking function. The argument
 * passed to the linking function will also be an object with matching keys, whose values will hold the corresponding
 * controllers.
 *
 * If the `require` property is an object and `bindToController` is truthy, then the required controllers are
 * bound to the controller using the keys of the `require` property. This binding occurs after all the controllers
 * have been constructed but before `$onInit` is called.
 * If the name of the required controller is the same as the local name (the key), the name can be
 * omitted. For example, `{parentDir: '^^'}` is equivalent to `{parentDir: '^^parentDir'}`.
 * See the {@link $compileProvider#component} helper for an example of how this can be used.
 * If no such required directive(s) can be found, or if the directive does not have a controller, then an error is
 * raised (unless no link function is specified and the required controllers are not being bound to the directive
 * controller, in which case error checking is skipped). The name can be prefixed with:
 *
 * * (no prefix) - Locate the required controller on the current element. Throw an error if not found.
 * * `?` - Attempt to locate the required controller or pass `null` to the `link` fn if not found.
 * * `^` - Locate the required controller by searching the element and its parents. Throw an error if not found.
 * * `^^` - Locate the required controller by searching the element's parents. Throw an error if not found.
 * * `?^` - Attempt to locate the required controller by searching the element and its parents or pass
 *   `null` to the `link` fn if not found.
 * * `?^^` - Attempt to locate the required controller by searching the element's parents, or pass
 *   `null` to the `link` fn if not found.
 *
 *
 * #### `controllerAs`
 * Identifier name for a reference to the controller in the directive's scope.
 * This allows the controller to be referenced from the directive template. This is especially
 * useful when a directive is used as component, i.e. with an `isolate` scope. It's also possible
 * to use it in a directive without an `isolate` / `new` scope, but you need to be aware that the
 * `controllerAs` reference might overwrite a property that already exists on the parent scope.
 *
 *
 * #### `restrict`
 * String of subset of `EACM` which restricts the directive to a specific directive
 * declaration style. If omitted, the defaults (elements and attributes) are used.
 *
 * * `E` - Element name (default): `<my-directive></my-directive>`
 * * `A` - Attribute (default): `<div my-directive="exp"></div>`
 * * `C` - Class: `<div class="my-directive: exp;"></div>`
 * * `M` - Comment: `<!-- directive: my-directive exp -->`
 *
 *
 * #### `templateNamespace`
 * String representing the document type used by the markup in the template.
 * AngularJS needs this information as those elements need to be created and cloned
 * in a special way when they are defined outside their usual containers like `<svg>` and `<math>`.
 *
 * * `html` - All root nodes in the template are HTML. Root nodes may also be
 *   top-level elements such as `<svg>` or `<math>`.
 * * `svg` - The root nodes in the template are SVG elements (excluding `<math>`).
 * * `math` - The root nodes in the template are MathML elements (excluding `<svg>`).
 *
 * If no `templateNamespace` is specified, then the namespace is considered to be `html`.
 *
 * #### `template`
 * HTML markup that may:
 * * Replace the contents of the directive's element (default).
 * * Replace the directive's element itself (if `replace` is true - DEPRECATED).
 * * Wrap the contents of the directive's element (if `transclude` is true).
 *
 * Value may be:
 *
 * * A string. For example `<div red-on-hover>{{delete_str}}</div>`.
 * * A function which takes two arguments `tElement` and `tAttrs` (described in the `compile`
 *   function api below) and returns a string value.
 *
 *
 * #### `templateUrl`
 * This is similar to `template` but the template is loaded from the specified URL, asynchronously.
 *
 * Because template loading is asynchronous the compiler will suspend compilation of directives on that element
 * for later when the template has been resolved.  In the meantime it will continue to compile and link
 * sibling and parent elements as though this element had not contained any directives.
 *
 * The compiler does not suspend the entire compilation to wait for templates to be loaded because this
 * would result in the whole app "stalling" until all templates are loaded asynchronously - even in the
 * case when only one deeply nested directive has `templateUrl`.
 *
 * Template loading is asynchronous even if the template has been preloaded into the {@link $templateCache}.
 *
 * You can specify `templateUrl` as a string representing the URL or as a function which takes two
 * arguments `tElement` and `tAttrs` (described in the `compile` function api below) and returns
 * a string value representing the url.  In either case, the template URL is passed through {@link
 * $sce#getTrustedResourceUrl $sce.getTrustedResourceUrl}.
 *
 *
 * #### `replace`
 * <div class="alert alert-danger">
 * **Note:** `replace` is deprecated in AngularJS and has been removed in the new Angular (v2+).
 * </div>
 *
 * Specifies what the template should replace. Defaults to `false`.
 *
 * * `true` - the template will replace the directive's element.
 * * `false` - the template will replace the contents of the directive's element.
 *
 * The replacement process migrates all of the attributes / classes from the old element to the new
 * one. See the {@link guide/directive#template-expanding-directive
 * Directives Guide} for an example.
 *
 * There are very few scenarios where element replacement is required for the application function,
 * the main one being reusable custom components that are used within SVG contexts
 * (because SVG doesn't work with custom elements in the DOM tree).
 *
 * #### `transclude`
 * Extract the contents of the element where the directive appears and make it available to the directive.
 * The contents are compiled and provided to the directive as a **transclusion function**. See the
 * {@link $compile#transclusion Transclusion} section below.
 *
 *
 * #### `compile`
 *
 * ```js
 *   function compile(tElement, tAttrs, transclude) { ... }
 * ```
 *
 * The compile function deals with transforming the template DOM. Since most directives do not do
 * template transformation, it is not used often. The compile function takes the following arguments:
 *
 *   * `tElement` - template element - The element where the directive has been declared. It is
 *     safe to do template transformation on the element and child elements only.
 *
 *   * `tAttrs` - template attributes - Normalized list of attributes declared on this element shared
 *     between all directive compile functions.
 *
 *   * `transclude` -  [*DEPRECATED*!] A transclude linking function: `function(scope, cloneLinkingFn)`
 *
 * <div class="alert alert-warning">
 * **Note:** The template instance and the link instance may be different objects if the template has
 * been cloned. For this reason it is **not** safe to do anything other than DOM transformations that
 * apply to all cloned DOM nodes within the compile function. Specifically, DOM listener registration
 * should be done in a linking function rather than in a compile function.
 * </div>

 * <div class="alert alert-warning">
 * **Note:** The compile function cannot handle directives that recursively use themselves in their
 * own templates or compile functions. Compiling these directives results in an infinite loop and
 * stack overflow errors.
 *
 * This can be avoided by manually using `$compile` in the postLink function to imperatively compile
 * a directive's template instead of relying on automatic template compilation via `template` or
 * `templateUrl` declaration or manual compilation inside the compile function.
 * </div>
 *
 * <div class="alert alert-danger">
 * **Note:** The `transclude` function that is passed to the compile function is deprecated, as it
 *   e.g. does not know about the right outer scope. Please use the transclude function that is passed
 *   to the link function instead.
 * </div>

 * A compile function can have a return value which can be either a function or an object.
 *
 * * returning a (post-link) function - is equivalent to registering the linking function via the
 *   `link` property of the config object when the compile function is empty.
 *
 * * returning an object with function(s) registered via `pre` and `post` properties - allows you to
 *   control when a linking function should be called during the linking phase. See info about
 *   pre-linking and post-linking functions below.
 *
 *
 * #### `link`
 * This property is used only if the `compile` property is not defined.
 *
 * ```js
 *   function link(scope, iElement, iAttrs, controller, transcludeFn) { ... }
 * ```
 *
 * The link function is responsible for registering DOM listeners as well as updating the DOM. It is
 * executed after the template has been cloned. This is where most of the directive logic will be
 * put.
 *
 *   * `scope` - {@link ng.$rootScope.Scope Scope} - The scope to be used by the
 *     directive for registering {@link ng.$rootScope.Scope#$watch watches}.
 *
 *   * `iElement` - instance element - The element where the directive is to be used. It is safe to
 *     manipulate the children of the element only in `postLink` function since the children have
 *     already been linked.
 *
 *   * `iAttrs` - instance attributes - Normalized list of attributes declared on this element shared
 *     between all directive linking functions.
 *
 *   * `controller` - the directive's required controller instance(s) - Instances are shared
 *     among all directives, which allows the directives to use the controllers as a communication
 *     channel. The exact value depends on the directive's `require` property:
 *       * no controller(s) required: the directive's own controller, or `undefined` if it doesn't have one
 *       * `string`: the controller instance
 *       * `array`: array of controller instances
 *
 *     If a required controller cannot be found, and it is optional, the instance is `null`,
 *     otherwise the {@link error:$compile:ctreq Missing Required Controller} error is thrown.
 *
 *     Note that you can also require the directive's own controller - it will be made available like
 *     any other controller.
 *
 *   * `transcludeFn` - A transclude linking function pre-bound to the correct transclusion scope.
 *     This is the same as the `$transclude` parameter of directive controllers,
 *     see {@link ng.$compile#-controller- the controller section for details}.
 *     `function([scope], cloneLinkingFn, futureParentElement)`.
 *
 * #### Pre-linking function
 *
 * Executed before the child elements are linked. Not safe to do DOM transformation since the
 * compiler linking function will fail to locate the correct elements for linking.
 *
 * #### Post-linking function
 *
 * Executed after the child elements are linked.
 *
 * Note that child elements that contain `templateUrl` directives will not have been compiled
 * and linked since they are waiting for their template to load asynchronously and their own
 * compilation and linking has been suspended until that occurs.
 *
 * It is safe to do DOM transformation in the post-linking function on elements that are not waiting
 * for their async templates to be resolved.
 *
 *
 * ### Transclusion
 *
 * Transclusion is the process of extracting a collection of DOM elements from one part of the DOM and
 * copying them to another part of the DOM, while maintaining their connection to the original AngularJS
 * scope from where they were taken.
 *
 * Transclusion is used (often with {@link ngTransclude}) to insert the
 * original contents of a directive's element into a specified place in the template of the directive.
 * The benefit of transclusion, over simply moving the DOM elements manually, is that the transcluded
 * content has access to the properties on the scope from which it was taken, even if the directive
 * has isolated scope.
 * See the {@link guide/directive#creating-a-directive-that-wraps-other-elements Directives Guide}.
 *
 * This makes it possible for the widget to have private state for its template, while the transcluded
 * content has access to its originating scope.
 *
 * <div class="alert alert-warning">
 * **Note:** When testing an element transclude directive you must not place the directive at the root of the
 * DOM fragment that is being compiled. See {@link guide/unit-testing#testing-transclusion-directives
 * Testing Transclusion Directives}.
 * </div>
 *
 * There are three kinds of transclusion depending upon whether you want to transclude just the contents of the
 * directive's element, the entire element or multiple parts of the element contents:
 *
 * * `true` - transclude the content (i.e. the child nodes) of the directive's element.
 * * `'element'` - transclude the whole of the directive's element including any directives on this
 *   element that are defined at a lower priority than this directive. When used, the `template`
 *   property is ignored.
 * * **`{...}` (an object hash):** - map elements of the content onto transclusion "slots" in the template.
 *
 * **Multi-slot transclusion** is declared by providing an object for the `transclude` property.
 *
 * This object is a map where the keys are the name of the slot to fill and the value is an element selector
 * used to match the HTML to the slot. The element selector should be in normalized form (e.g. `myElement`)
 * and will match the standard element variants (e.g. `my-element`, `my:element`, `data-my-element`, etc).
 *
 * For further information check out the guide on {@link guide/directive#matching-directives Matching Directives}.
 *
 * If the element selector is prefixed with a `?` then that slot is optional.
 *
 * For example, the transclude object `{ slotA: '?myCustomElement' }` maps `<my-custom-element>` elements to
 * the `slotA` slot, which can be accessed via the `$transclude` function or via the {@link ngTransclude} directive.
 *
 * Slots that are not marked as optional (`?`) will trigger a compile time error if there are no matching elements
 * in the transclude content. If you wish to know if an optional slot was filled with content, then you can call
 * `$transclude.isSlotFilled(slotName)` on the transclude function passed to the directive's link function and
 * injectable into the directive's controller.
 *
 *
 * #### Transclusion Functions
 *
 * When a directive requests transclusion, the compiler extracts its contents and provides a **transclusion
 * function** to the directive's `link` function and `controller`. This transclusion function is a special
 * **linking function** that will return the compiled contents linked to a new transclusion scope.
 *
 * <div class="alert alert-info">
 * If you are just using {@link ngTransclude} then you don't need to worry about this function, since
 * ngTransclude will deal with it for us.
 * </div>
 *
 * If you want to manually control the insertion and removal of the transcluded content in your directive
 * then you must use this transclude function. When you call a transclude function it returns a jqLite/JQuery
 * object that contains the compiled DOM, which is linked to the correct transclusion scope.
 *
 * When you call a transclusion function you can pass in a **clone attach function**. This function accepts
 * two parameters, `function(clone, scope) { ... }`, where the `clone` is a fresh compiled copy of your transcluded
 * content and the `scope` is the newly created transclusion scope, which the clone will be linked to.
 *
 * <div class="alert alert-info">
 * **Best Practice**: Always provide a `cloneFn` (clone attach function) when you call a transclude function
 * since you then get a fresh clone of the original DOM and also have access to the new transclusion scope.
 * </div>
 *
 * It is normal practice to attach your transcluded content (`clone`) to the DOM inside your **clone
 * attach function**:
 *
 * ```js
 * var transcludedContent, transclusionScope;
 *
 * $transclude(function(clone, scope) {
 *   element.append(clone);
 *   transcludedContent = clone;
 *   transclusionScope = scope;
 * });
 * ```
 *
 * Later, if you want to remove the transcluded content from your DOM then you should also destroy the
 * associated transclusion scope:
 *
 * ```js
 * transcludedContent.remove();
 * transclusionScope.$destroy();
 * ```
 *
 * <div class="alert alert-info">
 * **Best Practice**: if you intend to add and remove transcluded content manually in your directive
 * (by calling the transclude function to get the DOM and calling `element.remove()` to remove it),
 * then you are also responsible for calling `$destroy` on the transclusion scope.
 * </div>
 *
 * The built-in DOM manipulation directives, such as {@link ngIf}, {@link ngSwitch} and {@link ngRepeat}
 * automatically destroy their transcluded clones as necessary so you do not need to worry about this if
 * you are simply using {@link ngTransclude} to inject the transclusion into your directive.
 *
 *
 * #### Transclusion Scopes
 *
 * When you call a transclude function it returns a DOM fragment that is pre-bound to a **transclusion
 * scope**. This scope is special, in that it is a child of the directive's scope (and so gets destroyed
 * when the directive's scope gets destroyed) but it inherits the properties of the scope from which it
 * was taken.
 *
 * For example consider a directive that uses transclusion and isolated scope. The DOM hierarchy might look
 * like this:
 *
 * ```html
 * <div ng-app>
 *   <div isolate>
 *     <div transclusion>
 *     </div>
 *   </div>
 * </div>
 * ```
 *
 * The `$parent` scope hierarchy will look like this:
 *
   ```
   - $rootScope
     - isolate
       - transclusion
   ```
 *
 * but the scopes will inherit prototypically from different scopes to their `$parent`.
 *
   ```
   - $rootScope
     - transclusion
   - isolate
   ```
 *
 *
 * ### Attributes
 *
 * The {@link ng.$compile.directive.Attributes Attributes} object - passed as a parameter in the
 * `link()` or `compile()` functions. It has a variety of uses.
 *
 * * *Accessing normalized attribute names:* Directives like `ngBind` can be expressed in many ways:
 *   `ng:bind`, `data-ng-bind`, or `x-ng-bind`. The attributes object allows for normalized access
 *   to the attributes.
 *
 * * *Directive inter-communication:* All directives share the same instance of the attributes
 *   object which allows the directives to use the attributes object as inter directive
 *   communication.
 *
 * * *Supports interpolation:* Interpolation attributes are assigned to the attribute object
 *   allowing other directives to read the interpolated value.
 *
 * * *Observing interpolated attributes:* Use `$observe` to observe the value changes of attributes
 *   that contain interpolation (e.g. `src="{{bar}}"`). Not only is this very efficient but it's also
 *   the only way to easily get the actual value because during the linking phase the interpolation
 *   hasn't been evaluated yet and so the value is at this time set to `undefined`.
 *
 * ```js
 * function linkingFn(scope, elm, attrs, ctrl) {
 *   // get the attribute value
 *   console.log(attrs.ngModel);
 *
 *   // change the attribute
 *   attrs.$set('ngModel', 'new value');
 *
 *   // observe changes to interpolated attribute
 *   attrs.$observe('ngModel', function(value) {
 *     console.log('ngModel has changed value to ' + value);
 *   });
 * }
 * ```
 *
 * ## Example
 *
 * <div class="alert alert-warning">
 * **Note**: Typically directives are registered with `module.directive`. The example below is
 * to illustrate how `$compile` works.
 * </div>
 *
 <example module="compileExample" name="compile">
   <file name="index.html">
    <script>
      angular.module('compileExample', [], function($compileProvider) {
        // Configure new 'compile' directive by passing a directive
        // factory function. The factory function injects '$compile'.
        $compileProvider.directive('compile', function($compile) {
          // The directive factory creates a link function.
          return function(scope, element, attrs) {
            scope.$watch(
              function(scope) {
                // Watch the 'compile' expression for changes.
                return scope.$eval(attrs.compile);
              },
              function(value) {
                // When the 'compile' expression changes
                // assign it into the current DOM.
                element.html(value);

                // Compile the new DOM and link it to the current scope.
                // NOTE: we only compile '.childNodes' so that we
                // don't get into an infinite loop compiling ourselves.
                $compile(element.contents())(scope);
              }
            );
          };
        });
      })
      .controller('GreeterController', ['$scope', function($scope) {
        $scope.name = 'AngularJS';
        $scope.html = 'Hello {{name}}';
      }]);
    </script>
    <div ng-controller="GreeterController">
      <input ng-model="name"> <br/>
      <textarea ng-model="html"></textarea> <br/>
      <div compile="html"></div>
    </div>
   </file>
   <file name="protractor.js" type="protractor">
     it('should auto compile', function() {
       var textarea = $('textarea');
       var output = $('div[compile]');
       // The initial state reads 'Hello AngularJS'.
       expect(output.getText()).toBe('Hello AngularJS');
       textarea.clear();
       textarea.sendKeys('{{name}}!');
       expect(output.getText()).toBe('AngularJS!');
     });
   </file>
 </example>

 *
 *
 * @param {string|DOMElement} element Element or HTML string to compile into a template function.
 * @param {function(angular.Scope, cloneAttachFn=)} transclude function available to directives - DEPRECATED.
 *
 * <div class="alert alert-danger">
 * **Note:** Passing a `transclude` function to the $compile function is deprecated, as it
 *   e.g. will not use the right outer scope. Please pass the transclude function as a
 *   `parentBoundTranscludeFn` to the link function instead.
 * </div>
 *
 * @param {number} maxPriority only apply directives lower than given priority (Only effects the
 *                 root element(s), not their children)
 * @returns {function(scope, cloneAttachFn=, options=)} a link function which is used to bind template
 * (a DOM element/tree) to a scope. Where:
 *
 *  * `scope` - A {@link ng.$rootScope.Scope Scope} to bind to.
 *  * `cloneAttachFn` - If `cloneAttachFn` is provided, then the link function will clone the
 *  `template` and call the `cloneAttachFn` function allowing the caller to attach the
 *  cloned elements to the DOM document at the appropriate place. The `cloneAttachFn` is
 *  called as: <br/> `cloneAttachFn(clonedElement, scope)` where:
 *
 *      * `clonedElement` - is a clone of the original `element` passed into the compiler.
 *      * `scope` - is the current scope with which the linking function is working with.
 *
 *  * `options` - An optional object hash with linking options. If `options` is provided, then the following
 *  keys may be used to control linking behavior:
 *
 *      * `parentBoundTranscludeFn` - the transclude function made available to
 *        directives; if given, it will be passed through to the link functions of
 *        directives found in `element` during compilation.
 *      * `transcludeControllers` - an object hash with keys that map controller names
 *        to a hash with the key `instance`, which maps to the controller instance;
 *        if given, it will make the controllers available to directives on the compileNode:
 *        ```
 *        {
 *          parent: {
 *            instance: parentControllerInstance
 *          }
 *        }
 *        ```
 *      * `futureParentElement` - defines the parent to which the `cloneAttachFn` will add
 *        the cloned elements; only needed for transcludes that are allowed to contain non HTML
 *        elements (e.g. SVG elements). See also the `directive.controller` property.
 *
 * Calling the linking function returns the element of the template. It is either the original
 * element passed in, or the clone of the element if the `cloneAttachFn` is provided.
 *
 * After linking the view is not updated until after a call to `$digest`, which typically is done by
 * AngularJS automatically.
 *
 * If you need access to the bound view, there are two ways to do it:
 *
 * - If you are not asking the linking function to clone the template, create the DOM element(s)
 *   before you send them to the compiler and keep this reference around.
 *   ```js
 *     var element = angular.element('<p>{{total}}</p>');
 *     $compile(element)(scope);
 *   ```
 *
 * - if on the other hand, you need the element to be cloned, the view reference from the original
 *   example would not point to the clone, but rather to the original template that was cloned. In
 *   this case, you can access the clone either via the `cloneAttachFn` or the value returned by the
 *   linking function:
 *   ```js
 *     var templateElement = angular.element('<p>{{total}}</p>');
 *     var clonedElement = $compile(templateElement)(scope, function(clonedElement, scope) {
 *       // Attach the clone to DOM document at the right place.
 *     });
 *
 *     // Now we have reference to the cloned DOM via `clonedElement`.
 *     // NOTE: The `clonedElement` returned by the linking function is the same as the
 *     //       `clonedElement` passed to `cloneAttachFn`.
 *   ```
 *
 *
 * For information on how the compiler works, see the
 * {@link guide/compiler AngularJS HTML Compiler} section of the Developer Guide.
 *
 * @knownIssue
 *
 * ### Double Compilation
 *
   Double compilation occurs when an already compiled part of the DOM gets
   compiled again. This is an undesired effect and can lead to misbehaving directives, performance issues,
   and memory leaks. Refer to the Compiler Guide {@link guide/compiler#double-compilation-and-how-to-avoid-it
   section on double compilation} for an in-depth explanation and ways to avoid it.

 * @knownIssue

   ### Issues with `replace: true`
 *
 * <div class="alert alert-danger">
 *   **Note**: {@link $compile#-replace- `replace: true`} is deprecated and not recommended to use,
 *   mainly due to the issues listed here. It has been completely removed in the new Angular.
 * </div>
 *
 * #### Attribute values are not merged
 *
 * When a `replace` directive encounters the same attribute on the original and the replace node,
 * it will simply deduplicate the attribute and join the values with a space or with a `;` in case of
 * the `style` attribute.
 * ```html
 * Original Node: <span class="original" style="color: red;"></span>
 * Replace Template: <span class="replaced" style="background: blue;"></span>
 * Result: <span class="original replaced" style="color: red; background: blue;"></span>
 * ```
 *
 * That means attributes that contain AngularJS expressions will not be merged correctly, e.g.
 * {@link ngShow} or {@link ngClass} will cause a {@link $parse} error:
 *
 * ```html
 * Original Node: <span ng-class="{'something': something}" ng-show="!condition"></span>
 * Replace Template: <span ng-class="{'else': else}" ng-show="otherCondition"></span>
 * Result: <span ng-class="{'something': something} {'else': else}" ng-show="!condition otherCondition"></span>
 * ```
 *
 * See issue [#5695](https://github.com/angular/angular.js/issues/5695).
 *
 * #### Directives are not deduplicated before compilation
 *
 * When the original node and the replace template declare the same directive(s), they will be
 * {@link guide/compiler#double-compilation-and-how-to-avoid-it compiled twice} because the compiler
 * does not deduplicate them. In many cases, this is not noticeable, but e.g. {@link ngModel} will
 * attach `$formatters` and `$parsers` twice.
 *
 * See issue [#2573](https://github.com/angular/angular.js/issues/2573).
 *
 * #### `transclude: element` in the replace template root can have unexpected effects
 *
 * When the replace template has a directive at the root node that uses
 * {@link $compile#-transclude- `transclude: element`}, e.g.
 * {@link ngIf} or {@link ngRepeat}, the DOM structure or scope inheritance can be incorrect.
 * See the following issues:
 *
 * - Incorrect scope on replaced element:
 * [#9837](https://github.com/angular/angular.js/issues/9837)
 * - Different DOM between `template` and `templateUrl`:
 * [#10612](https://github.com/angular/angular.js/issues/14326)
 *
 */

/**
 * @ngdoc directive
 * @name ngProp
 * @restrict A
 * @element ANY
 *
 * @usage
 *
 * ```html
 * <ANY ng-prop-propname="expression">
 * </ANY>
 * ```
 *
 * or with uppercase letters in property (e.g. "propName"):
 *
 *
 * ```html
 * <ANY ng-prop-prop_name="expression">
 * </ANY>
 * ```
 *
 *
 * @description
 * The `ngProp` directive binds an expression to a DOM element property.
 * `ngProp` allows writing to arbitrary properties by including
 * the property name in the attribute, e.g. `ng-prop-value="'my value'"` binds 'my value' to
 * the `value` property.
 *
 * Usually, it's not necessary to write to properties in AngularJS, as the built-in directives
 * handle the most common use cases (instead of the above example, you would use {@link ngValue}).
 *
 * However, [custom elements](https://developer.mozilla.org/docs/Web/Web_Components/Using_custom_elements)
 * often use custom properties to hold data, and `ngProp` can be used to provide input to these
 * custom elements.
 *
 * ## Binding to camelCase properties
 *
 * Since HTML attributes are case-insensitive, camelCase properties like `innerHTML` must be escaped.
 * AngularJS uses the underscore (_) in front of a character to indicate that it is uppercase, so
 * `innerHTML`  must be written as `ng-prop-inner_h_t_m_l="expression"` (Note that this is just an
 * example, and for binding HTML {@link ngBindHtml} should be used.
 *
 * ## Security
 *
 * Binding expressions to arbitrary properties poses a security risk, as  properties like `innerHTML`
 * can insert potentially dangerous HTML into the application, e.g. script tags that execute
 * malicious code.
 * For this reason, `ngProp` applies Strict Contextual Escaping with the {@link ng.$sce $sce service}.
 * This means vulnerable properties require their content to be "trusted", based on the
 * context of the property. For example, the `innerHTML` is in the `HTML` context, and the
 * `iframe.src` property is in the `RESOURCE_URL` context, which requires that values written to
 * this property are trusted as a `RESOURCE_URL`.
 *
 * This can be set explicitly by calling $sce.trustAs(type, value) on the value that is
 * trusted before passing it to the `ng-prop-*` directive. There are exist shorthand methods for
 * each context type in the form of {@link ng.$sce#trustAsResourceUrl $sce.trustAsResourceUrl()} et al.
 *
 * In some cases you can also rely upon automatic sanitization of untrusted values - see below.
 *
 * Based on the context, other options may exist to mark a value as trusted / configure the behavior
 * of {@link ng.$sce}. For example, to restrict the `RESOURCE_URL` context to specific origins, use
 * the {@link $sceDelegateProvider#trustedResourceUrlList trustedResourceUrlList()}
 * and {@link $sceDelegateProvider#bannedResourceUrlList bannedResourceUrlList()}.
 *
 * {@link ng.$sce#what-trusted-context-types-are-supported- Find out more about the different context types}.
 *
 * ### HTML Sanitization
 *
 * By default, `$sce` will throw an error if it detects untrusted HTML content, and will not bind the
 * content.
 * However, if you include the {@link ngSanitize ngSanitize module}, it will try to sanitize the
 * potentially dangerous HTML, e.g. strip non-trusted tags and attributes when binding to
 * `innerHTML`.
 *
 * @example
 * ### Binding to different contexts
 *
 * <example name="ngProp" module="exampleNgProp">
 *   <file name="app.js">
 *     angular.module('exampleNgProp', [])
 *       .component('main', {
 *         templateUrl: 'main.html',
 *         controller: function($sce) {
 *           this.safeContent = '<strong>Safe content</strong>';
 *           this.unsafeContent = '<button onclick="alert(\'Hello XSS!\')">Click for XSS</button>';
 *           this.trustedUnsafeContent = $sce.trustAsHtml(this.unsafeContent);
 *         }
 *       });
 *   </file>
 *   <file name="main.html">
 *     <div>
 *       <div class="prop-unit">
 *         Binding to a property without security context:
 *         <div class="prop-binding" ng-prop-inner_text="$ctrl.safeContent"></div>
 *         <span class="prop-note">innerText</span> (safeContent)
 *       </div>
 *
 *       <div class="prop-unit">
 *         "Safe" content that requires a security context will throw because the contents could potentially be dangerous ...
 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.safeContent"></div>
 *         <span class="prop-note">innerHTML</span> (safeContent)
 *       </div>
 *
 *       <div class="prop-unit">
 *         ... so that actually dangerous content cannot be executed:
 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.unsafeContent"></div>
 *         <span class="prop-note">innerHTML</span> (unsafeContent)
 *       </div>
 *
 *       <div class="prop-unit">
 *         ... but unsafe Content that has been trusted explicitly works - only do this if you are 100% sure!
 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.trustedUnsafeContent"></div>
 *         <span class="prop-note">innerHTML</span> (trustedUnsafeContent)
 *       </div>
 *     </div>
 *   </file>
 *   <file name="index.html">
 *     <main></main>
 *   </file>
 *   <file name="styles.css">
 *     .prop-unit {
 *       margin-bottom: 10px;
 *     }
 *
 *     .prop-binding {
 *       min-height: 30px;
 *       border: 1px solid blue;
 *     }
 *
 *     .prop-note {
 *       font-family: Monospace;
 *     }
 *   </file>
 * </example>
 *
 *
 * @example
 * ### Binding to innerHTML with ngSanitize
 *
 * <example name="ngProp" module="exampleNgProp" deps="angular-sanitize.js">
 *   <file name="app.js">
 *     angular.module('exampleNgProp', ['ngSanitize'])
 *       .component('main', {
 *         templateUrl: 'main.html',
 *         controller: function($sce) {
 *           this.safeContent = '<strong>Safe content</strong>';
 *           this.unsafeContent = '<button onclick="alert(\'Hello XSS!\')">Click for XSS</button>';
 *           this.trustedUnsafeContent = $sce.trustAsHtml(this.unsafeContent);
 *         }
 *       });
 *   </file>
 *   <file name="main.html">
 *     <div>
 *       <div class="prop-unit">
 *         "Safe" content will be sanitized ...
 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.safeContent"></div>
 *         <span class="prop-note">innerHTML</span> (safeContent)
 *       </div>
 *
 *       <div class="prop-unit">
 *         ... as will dangerous content:
 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.unsafeContent"></div>
 *         <span class="prop-note">innerHTML</span> (unsafeContent)
 *       </div>
 *
 *       <div class="prop-unit">
 *         ... and content that has been trusted explicitly works the same as without ngSanitize:
 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.trustedUnsafeContent"></div>
 *         <span class="prop-note">innerHTML</span> (trustedUnsafeContent)
 *       </div>
 *     </div>
 *   </file>
 *   <file name="index.html">
 *     <main></main>
 *   </file>
 *   <file name="styles.css">
 *     .prop-unit {
 *       margin-bottom: 10px;
 *     }
 *
 *     .prop-binding {
 *       min-height: 30px;
 *       border: 1px solid blue;
 *     }
 *
 *     .prop-note {
 *       font-family: Monospace;
 *     }
 *   </file>
 * </example>
 *
 */

/** @ngdoc directive
 * @name ngOn
 * @restrict A
 * @element ANY
 *
 * @usage
 *
 * ```html
 * <ANY ng-on-eventname="expression">
 * </ANY>
 * ```
 *
 * or with uppercase letters in property (e.g. "eventName"):
 *
 *
 * ```html
 * <ANY ng-on-event_name="expression">
 * </ANY>
 * ```
 *
 * @description
 * The `ngOn` directive adds an event listener to a DOM element via
 * {@link angular.element angular.element().on()}, and evaluates an expression when the event is
 * fired.
 * `ngOn` allows adding listeners for arbitrary events by including
 * the event name in the attribute, e.g. `ng-on-drop="onDrop()"` executes the 'onDrop()' expression
 * when the `drop` event is fired.
 *
 * AngularJS provides specific directives for many events, such as {@link ngClick}, so in most
 * cases it is not necessary to use `ngOn`. However, AngularJS does not support all events
 * (e.g. the `drop` event in the example above), and new events might be introduced in later DOM
 * standards.
 *
 * Another use-case for `ngOn` is listening to
 * [custom events](https://developer.mozilla.org/docs/Web/Guide/Events/Creating_and_triggering_events)
 * fired by
 * [custom elements](https://developer.mozilla.org/docs/Web/Web_Components/Using_custom_elements).
 *
 * ## Binding to camelCase properties
 *
 * Since HTML attributes are case-insensitive, camelCase properties like `myEvent` must be escaped.
 * AngularJS uses the underscore (_) in front of a character to indicate that it is uppercase, so
 * `myEvent` must be written as `ng-on-my_event="expression"`.
 *
 * @example
 * ### Bind to built-in DOM events
 *
 * <example name="ngOn" module="exampleNgOn">
 *   <file name="app.js">
 *     angular.module('exampleNgOn', [])
 *       .component('main', {
 *         templateUrl: 'main.html',
 *         controller: function() {
 *           this.clickCount = 0;
 *           this.mouseoverCount = 0;
 *
 *           this.loadingState = 0;
 *         }
 *       });
 *   </file>
 *   <file name="main.html">
 *     <div>
 *       This is equivalent to `ngClick` and `ngMouseover`:<br>
 *       <button
 *         ng-on-click="$ctrl.clickCount = $ctrl.clickCount + 1"
 *         ng-on-mouseover="$ctrl.mouseoverCount = $ctrl.mouseoverCount + 1">Click or mouseover</button><br>
 *       clickCount: {{$ctrl.clickCount}}<br>
 *       mouseover: {{$ctrl.mouseoverCount}}
 *
 *       <hr>
 *
 *       For the `error` and `load` event on images no built-in AngularJS directives exist:<br>
 *       <img src="thisimagedoesnotexist.png" ng-on-error="$ctrl.loadingState = -1" ng-on-load="$ctrl.loadingState = 1"><br>
 *       <div ng-switch="$ctrl.loadingState">
 *         <span ng-switch-when="0">Image is loading</span>
 *         <span ng-switch-when="-1">Image load error</span>
 *         <span ng-switch-when="1">Image loaded successfully</span>
 *       </div>
 *     </div>
 *   </file>
 *   <file name="index.html">
 *     <main></main>
 *   </file>
 * </example>
 *
 *
 * @example
 * ### Bind to custom DOM events
 *
 * <example name="ngOnCustom" module="exampleNgOn">
 *   <file name="app.js">
 *     angular.module('exampleNgOn', [])
 *       .component('main', {
 *         templateUrl: 'main.html',
 *         controller: function() {
 *           this.eventLog = '';
 *
 *           this.listener = function($event) {
 *             this.eventLog = 'Event with type "' + $event.type + '" fired at ' + $event.detail;
 *           };
 *         }
 *       })
 *       .component('childComponent', {
 *         templateUrl: 'child.html',
 *         controller: function($element) {
 *           this.fireEvent = function() {
 *             var event = new CustomEvent('customtype', { detail: new Date()});
 *
 *             $element[0].dispatchEvent(event);
 *           };
 *         }
 *       });
 *   </file>
 *   <file name="main.html">
 *     <child-component ng-on-customtype="$ctrl.listener($event)"></child-component><br>
 *     <span>Event log: {{$ctrl.eventLog}}</span>
 *   </file>
 *   <file name="child.html">
      <button ng-click="$ctrl.fireEvent()">Fire custom event</button>
 *   </file>
 *   <file name="index.html">
 *     <main></main>
 *   </file>
 * </example>
 */

var $compileMinErr = minErr('$compile');

function UNINITIALIZED_VALUE() {}
var _UNINITIALIZED_VALUE = new UNINITIALIZED_VALUE();

/**
 * @ngdoc provider
 * @name $compileProvider
 *
 * @description
 */
$CompileProvider.$inject = ['$provide', '$$sanitizeUriProvider'];
/** @this */
function $CompileProvider($provide, $$sanitizeUriProvider) {
  var hasDirectives = {},
      Suffix = 'Directive',
      COMMENT_DIRECTIVE_REGEXP = /^\s*directive:\s*([\w-]+)\s+(.*)$/,
      CLASS_DIRECTIVE_REGEXP = /(([\w-]+)(?::([^;]+))?;?)/,
      ALL_OR_NOTHING_ATTRS = makeMap('ngSrc,ngSrcset,src,srcset'),
      REQUIRE_PREFIX_REGEXP = /^(?:(\^\^?)?(\?)?(\^\^?)?)?/;

  // Ref: http://developers.whatwg.org/webappapis.html#event-handler-idl-attributes
  // The assumption is that future DOM event attribute names will begin with
  // 'on' and be composed of only English letters.
  var EVENT_HANDLER_ATTR_REGEXP = /^(on[a-z]+|formaction)$/;
  var bindingCache = createMap();

  function parseIsolateBindings(scope, directiveName, isController) {
    var LOCAL_REGEXP = /^([@&]|[=<](\*?))(\??)\s*([\w$]*)$/;

    var bindings = createMap();

    forEach(scope, function(definition, scopeName) {
      definition = definition.trim();

      if (definition in bindingCache) {
        bindings[scopeName] = bindingCache[definition];
        return;
      }
      var match = definition.match(LOCAL_REGEXP);

      if (!match) {
        throw $compileMinErr('iscp',
            'Invalid {3} for directive \'{0}\'.' +
            ' Definition: {... {1}: \'{2}\' ...}',
            directiveName, scopeName, definition,
            (isController ? 'controller bindings definition' :
            'isolate scope definition'));
      }

      bindings[scopeName] = {
        mode: match[1][0],
        collection: match[2] === '*',
        optional: match[3] === '?',
        attrName: match[4] || scopeName
      };
      if (match[4]) {
        bindingCache[definition] = bindings[scopeName];
      }
    });

    return bindings;
  }

  function parseDirectiveBindings(directive, directiveName) {
    var bindings = {
      isolateScope: null,
      bindToController: null
    };
    if (isObject(directive.scope)) {
      if (directive.bindToController === true) {
        bindings.bindToController = parseIsolateBindings(directive.scope,
                                                         directiveName, true);
        bindings.isolateScope = {};
      } else {
        bindings.isolateScope = parseIsolateBindings(directive.scope,
                                                     directiveName, false);
      }
    }
    if (isObject(directive.bindToController)) {
      bindings.bindToController =
          parseIsolateBindings(directive.bindToController, directiveName, true);
    }
    if (bindings.bindToController && !directive.controller) {
      // There is no controller
      throw $compileMinErr('noctrl',
            'Cannot bind to controller without directive \'{0}\'s controller.',
            directiveName);
    }
    return bindings;
  }

  function assertValidDirectiveName(name) {
    var letter = name.charAt(0);
    if (!letter || letter !== lowercase(letter)) {
      throw $compileMinErr('baddir', 'Directive/Component name \'{0}\' is invalid. The first character must be a lowercase letter', name);
    }
    if (name !== name.trim()) {
      throw $compileMinErr('baddir',
            'Directive/Component name \'{0}\' is invalid. The name should not contain leading or trailing whitespaces',
            name);
    }
  }

  function getDirectiveRequire(directive) {
    var require = directive.require || (directive.controller && directive.name);

    if (!isArray(require) && isObject(require)) {
      forEach(require, function(value, key) {
        var match = value.match(REQUIRE_PREFIX_REGEXP);
        var name = value.substring(match[0].length);
        if (!name) require[key] = match[0] + key;
      });
    }

    return require;
  }

  function getDirectiveRestrict(restrict, name) {
    if (restrict && !(isString(restrict) && /[EACM]/.test(restrict))) {
      throw $compileMinErr('badrestrict',
          'Restrict property \'{0}\' of directive \'{1}\' is invalid',
          restrict,
          name);
    }

    return restrict || 'EA';
  }

  /**
   * @ngdoc method
   * @name $compileProvider#directive
   * @kind function
   *
   * @description
   * Register a new directive with the compiler.
   *
   * @param {string|Object} name Name of the directive in camel-case (i.e. `ngBind` which will match
   *    as `ng-bind`), or an object map of directives where the keys are the names and the values
   *    are the factories.
   * @param {Function|Array} directiveFactory An injectable directive factory function. See the
   *    {@link guide/directive directive guide} and the {@link $compile compile API} for more info.
   * @returns {ng.$compileProvider} Self for chaining.
   */
  this.directive = function registerDirective(name, directiveFactory) {
    assertArg(name, 'name');
    assertNotHasOwnProperty(name, 'directive');
    if (isString(name)) {
      assertValidDirectiveName(name);
      assertArg(directiveFactory, 'directiveFactory');
      if (!hasDirectives.hasOwnProperty(name)) {
        hasDirectives[name] = [];
        $provide.factory(name + Suffix, ['$injector', '$exceptionHandler',
          function($injector, $exceptionHandler) {
            var directives = [];
            forEach(hasDirectives[name], function(directiveFactory, index) {
              try {
                var directive = $injector.invoke(directiveFactory);
                if (isFunction(directive)) {
                  directive = { compile: valueFn(directive) };
                } else if (!directive.compile && directive.link) {
                  directive.compile = valueFn(directive.link);
                }
                directive.priority = directive.priority || 0;
                directive.index = index;
                directive.name = directive.name || name;
                directive.require = getDirectiveRequire(directive);
                directive.restrict = getDirectiveRestrict(directive.restrict, name);
                directive.$$moduleName = directiveFactory.$$moduleName;
                directives.push(directive);
              } catch (e) {
                $exceptionHandler(e);
              }
            });
            return directives;
          }]);
      }
      hasDirectives[name].push(directiveFactory);
    } else {
      forEach(name, reverseParams(registerDirective));
    }
    return this;
  };

  /**
   * @ngdoc method
   * @name $compileProvider#component
   * @module ng
   * @param {string|Object} name Name of the component in camelCase (i.e. `myComp` which will match `<my-comp>`),
   *    or an object map of components where the keys are the names and the values are the component definition objects.
   * @param {Object} options Component definition object (a simplified
   *    {@link ng.$compile#directive-definition-object directive definition object}),
   *    with the following properties (all optional):
   *
   *    - `controller` – `{(string|function()=}` – controller constructor function that should be
   *      associated with newly created scope or the name of a {@link ng.$compile#-controller-
   *      registered controller} if passed as a string. An empty `noop` function by default.
   *    - `controllerAs` – `{string=}` – identifier name for to reference the controller in the component's scope.
   *      If present, the controller will be published to scope under the `controllerAs` name.
   *      If not present, this will default to be `$ctrl`.
   *    - `template` – `{string=|function()=}` – html template as a string or a function that
   *      returns an html template as a string which should be used as the contents of this component.
   *      Empty string by default.
   *
   *      If `template` is a function, then it is {@link auto.$injector#invoke injected} with
   *      the following locals:
   *
   *      - `$element` - Current element
   *      - `$attrs` - Current attributes object for the element
   *
   *    - `templateUrl` – `{string=|function()=}` – path or function that returns a path to an html
   *      template that should be used  as the contents of this component.
   *
   *      If `templateUrl` is a function, then it is {@link auto.$injector#invoke injected} with
   *      the following locals:
   *
   *      - `$element` - Current element
   *      - `$attrs` - Current attributes object for the element
   *
   *    - `bindings` – `{object=}` – defines bindings between DOM attributes and component properties.
   *      Component properties are always bound to the component controller and not to the scope.
   *      See {@link ng.$compile#-bindtocontroller- `bindToController`}.
   *    - `transclude` – `{boolean=}` – whether {@link $compile#transclusion content transclusion} is enabled.
   *      Disabled by default.
   *    - `require` - `{Object<string, string>=}` - requires the controllers of other directives and binds them to
   *      this component's controller. The object keys specify the property names under which the required
   *      controllers (object values) will be bound. See {@link ng.$compile#-require- `require`}.
   *    - `$...` – additional properties to attach to the directive factory function and the controller
   *      constructor function. (This is used by the component router to annotate)
   *
   * @returns {ng.$compileProvider} the compile provider itself, for chaining of function calls.
   * @description
   * Register a **component definition** with the compiler. This is a shorthand for registering a special
   * type of directive, which represents a self-contained UI component in your application. Such components
   * are always isolated (i.e. `scope: {}`) and are always restricted to elements (i.e. `restrict: 'E'`).
   *
   * Component definitions are very simple and do not require as much configuration as defining general
   * directives. Component definitions usually consist only of a template and a controller backing it.
   *
   * In order to make the definition easier, components enforce best practices like use of `controllerAs`,
   * `bindToController`. They always have **isolate scope** and are restricted to elements.
   *
   * Here are a few examples of how you would usually define components:
   *
   * ```js
   *   var myMod = angular.module(...);
   *   myMod.component('myComp', {
   *     template: '<div>My name is {{$ctrl.name}}</div>',
   *     controller: function() {
   *       this.name = 'shahar';
   *     }
   *   });
   *
   *   myMod.component('myComp', {
   *     template: '<div>My name is {{$ctrl.name}}</div>',
   *     bindings: {name: '@'}
   *   });
   *
   *   myMod.component('myComp', {
   *     templateUrl: 'views/my-comp.html',
   *     controller: 'MyCtrl',
   *     controllerAs: 'ctrl',
   *     bindings: {name: '@'}
   *   });
   *
   * ```
   * For more examples, and an in-depth guide, see the {@link guide/component component guide}.
   *
   * <br />
   * See also {@link ng.$compileProvider#directive $compileProvider.directive()}.
   */
  this.component = function registerComponent(name, options) {
    if (!isString(name)) {
      forEach(name, reverseParams(bind(this, registerComponent)));
      return this;
    }

    var controller = options.controller || function() {};

    function factory($injector) {
      function makeInjectable(fn) {
        if (isFunction(fn) || isArray(fn)) {
          return /** @this */ function(tElement, tAttrs) {
            return $injector.invoke(fn, this, {$element: tElement, $attrs: tAttrs});
          };
        } else {
          return fn;
        }
      }

      var template = (!options.template && !options.templateUrl ? '' : options.template);
      var ddo = {
        controller: controller,
        controllerAs: identifierForController(options.controller) || options.controllerAs || '$ctrl',
        template: makeInjectable(template),
        templateUrl: makeInjectable(options.templateUrl),
        transclude: options.transclude,
        scope: {},
        bindToController: options.bindings || {},
        restrict: 'E',
        require: options.require
      };

      // Copy annotations (starting with $) over to the DDO
      forEach(options, function(val, key) {
        if (key.charAt(0) === '$') ddo[key] = val;
      });

      return ddo;
    }

    // TODO(pete) remove the following `forEach` before we release 1.6.0
    // The component-router@0.2.0 looks for the annotations on the controller constructor
    // Nothing in AngularJS looks for annotations on the factory function but we can't remove
    // it from 1.5.x yet.

    // Copy any annotation properties (starting with $) over to the factory and controller constructor functions
    // These could be used by libraries such as the new component router
    forEach(options, function(val, key) {
      if (key.charAt(0) === '$') {
        factory[key] = val;
        // Don't try to copy over annotations to named controller
        if (isFunction(controller)) controller[key] = val;
      }
    });

    factory.$inject = ['$injector'];

    return this.directive(name, factory);
  };


  /**
   * @ngdoc method
   * @name $compileProvider#aHrefSanitizationTrustedUrlList
   * @kind function
   *
   * @description
   * Retrieves or overrides the default regular expression that is used for determining trusted safe
   * urls during a[href] sanitization.
   *
   * The sanitization is a security measure aimed at preventing XSS attacks via html links.
   *
   * Any url about to be assigned to a[href] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `aHrefSanitizationTrustedUrlList`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to trust urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.aHrefSanitizationTrustedUrlList = function(regexp) {
    if (isDefined(regexp)) {
      $$sanitizeUriProvider.aHrefSanitizationTrustedUrlList(regexp);
      return this;
    } else {
      return $$sanitizeUriProvider.aHrefSanitizationTrustedUrlList();
    }
  };


  /**
   * @ngdoc method
   * @name $compileProvider#aHrefSanitizationWhitelist
   * @kind function
   *
   * @deprecated
   * sinceVersion="1.8.1"
   *
   * This method is deprecated. Use {@link $compileProvider#aHrefSanitizationTrustedUrlList
   * aHrefSanitizationTrustedUrlList} instead.
   */
  Object.defineProperty(this, 'aHrefSanitizationWhitelist', {
    get: function() {
      return this.aHrefSanitizationTrustedUrlList;
    },
    set: function(value) {
      this.aHrefSanitizationTrustedUrlList = value;
    }
  });


  /**
   * @ngdoc method
   * @name $compileProvider#imgSrcSanitizationTrustedUrlList
   * @kind function
   *
   * @description
   * Retrieves or overrides the default regular expression that is used for determining trusted safe
   * urls during img[src] sanitization.
   *
   * The sanitization is a security measure aimed at prevent XSS attacks via html links.
   *
   * Any url about to be assigned to img[src] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `imgSrcSanitizationTrustedUrlList`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to trust urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.imgSrcSanitizationTrustedUrlList = function(regexp) {
    if (isDefined(regexp)) {
      $$sanitizeUriProvider.imgSrcSanitizationTrustedUrlList(regexp);
      return this;
    } else {
      return $$sanitizeUriProvider.imgSrcSanitizationTrustedUrlList();
    }
  };


  /**
   * @ngdoc method
   * @name $compileProvider#imgSrcSanitizationWhitelist
   * @kind function
   *
   * @deprecated
   * sinceVersion="1.8.1"
   *
   * This method is deprecated. Use {@link $compileProvider#imgSrcSanitizationTrustedUrlList
   * imgSrcSanitizationTrustedUrlList} instead.
   */
  Object.defineProperty(this, 'imgSrcSanitizationWhitelist', {
    get: function() {
      return this.imgSrcSanitizationTrustedUrlList;
    },
    set: function(value) {
      this.imgSrcSanitizationTrustedUrlList = value;
    }
  });

  /**
   * @ngdoc method
   * @name  $compileProvider#debugInfoEnabled
   *
   * @param {boolean=} enabled update the debugInfoEnabled state if provided, otherwise just return the
   * current debugInfoEnabled state
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   *
   * @kind function
   *
   * @description
   * Call this method to enable/disable various debug runtime information in the compiler such as adding
   * binding information and a reference to the current scope on to DOM elements.
   * If enabled, the compiler will add the following to DOM elements that have been bound to the scope
   * * `ng-binding` CSS class
   * * `ng-scope` and `ng-isolated-scope` CSS classes
   * * `$binding` data property containing an array of the binding expressions
   * * Data properties used by the {@link angular.element#methods `scope()`/`isolateScope()` methods} to return
   *   the element's scope.
   * * Placeholder comments will contain information about what directive and binding caused the placeholder.
   *   E.g. `<!-- ngIf: shouldShow() -->`.
   *
   * You may want to disable this in production for a significant performance boost. See
   * {@link guide/production#disabling-debug-data Disabling Debug Data} for more.
   *
   * The default value is true.
   */
  var debugInfoEnabled = true;
  this.debugInfoEnabled = function(enabled) {
    if (isDefined(enabled)) {
      debugInfoEnabled = enabled;
      return this;
    }
    return debugInfoEnabled;
  };

  /**
   * @ngdoc method
   * @name  $compileProvider#strictComponentBindingsEnabled
   *
   * @param {boolean=} enabled update the strictComponentBindingsEnabled state if provided,
   * otherwise return the current strictComponentBindingsEnabled state.
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   *
   * @kind function
   *
   * @description
   * Call this method to enable / disable the strict component bindings check. If enabled, the
   * compiler will enforce that all scope / controller bindings of a
   * {@link $compileProvider#directive directive} / {@link $compileProvider#component component}
   * that are not set as optional with `?`, must be provided when the directive is instantiated.
   * If not provided, the compiler will throw the
   * {@link error/$compile/missingattr $compile:missingattr error}.
   *
   * The default value is false.
   */
  var strictComponentBindingsEnabled = false;
  this.strictComponentBindingsEnabled = function(enabled) {
    if (isDefined(enabled)) {
      strictComponentBindingsEnabled = enabled;
      return this;
    }
    return strictComponentBindingsEnabled;
  };

  var TTL = 10;
  /**
   * @ngdoc method
   * @name $compileProvider#onChangesTtl
   * @description
   *
   * Sets the number of times `$onChanges` hooks can trigger new changes before giving up and
   * assuming that the model is unstable.
   *
   * The current default is 10 iterations.
   *
   * In complex applications it's possible that dependencies between `$onChanges` hooks and bindings will result
   * in several iterations of calls to these hooks. However if an application needs more than the default 10
   * iterations to stabilize then you should investigate what is causing the model to continuously change during
   * the `$onChanges` hook execution.
   *
   * Increasing the TTL could have performance implications, so you should not change it without proper justification.
   *
   * @param {number} limit The number of `$onChanges` hook iterations.
   * @returns {number|object} the current limit (or `this` if called as a setter for chaining)
   */
  this.onChangesTtl = function(value) {
    if (arguments.length) {
      TTL = value;
      return this;
    }
    return TTL;
  };

  var commentDirectivesEnabledConfig = true;
  /**
   * @ngdoc method
   * @name $compileProvider#commentDirectivesEnabled
   * @description
   *
   * It indicates to the compiler
   * whether or not directives on comments should be compiled.
   * Defaults to `true`.
   *
   * Calling this function with false disables the compilation of directives
   * on comments for the whole application.
   * This results in a compilation performance gain,
   * as the compiler doesn't have to check comments when looking for directives.
   * This should however only be used if you are sure that no comment directives are used in
   * the application (including any 3rd party directives).
   *
   * @param {boolean} enabled `false` if the compiler may ignore directives on comments
   * @returns {boolean|object} the current value (or `this` if called as a setter for chaining)
   */
  this.commentDirectivesEnabled = function(value) {
    if (arguments.length) {
      commentDirectivesEnabledConfig = value;
      return this;
    }
    return commentDirectivesEnabledConfig;
  };


  var cssClassDirectivesEnabledConfig = true;
  /**
   * @ngdoc method
   * @name $compileProvider#cssClassDirectivesEnabled
   * @description
   *
   * It indicates to the compiler
   * whether or not directives on element classes should be compiled.
   * Defaults to `true`.
   *
   * Calling this function with false disables the compilation of directives
   * on element classes for the whole application.
   * This results in a compilation performance gain,
   * as the compiler doesn't have to check element classes when looking for directives.
   * This should however only be used if you are sure that no class directives are used in
   * the application (including any 3rd party directives).
   *
   * @param {boolean} enabled `false` if the compiler may ignore directives on element classes
   * @returns {boolean|object} the current value (or `this` if called as a setter for chaining)
   */
  this.cssClassDirectivesEnabled = function(value) {
    if (arguments.length) {
      cssClassDirectivesEnabledConfig = value;
      return this;
    }
    return cssClassDirectivesEnabledConfig;
  };


  /**
   * The security context of DOM Properties.
   * @private
   */
  var PROP_CONTEXTS = createMap();

  /**
   * @ngdoc method
   * @name $compileProvider#addPropertySecurityContext
   * @description
   *
   * Defines the security context for DOM properties bound by ng-prop-*.
   *
   * @param {string} elementName The element name or '*' to match any element.
   * @param {string} propertyName The DOM property name.
   * @param {string} ctx The {@link $sce} security context in which this value is safe for use, e.g. `$sce.URL`
   * @returns {object} `this` for chaining
   */
  this.addPropertySecurityContext = function(elementName, propertyName, ctx) {
    var key = (elementName.toLowerCase() + '|' + propertyName.toLowerCase());

    if (key in PROP_CONTEXTS && PROP_CONTEXTS[key] !== ctx) {
      throw $compileMinErr('ctxoverride', 'Property context \'{0}.{1}\' already set to \'{2}\', cannot override to \'{3}\'.', elementName, propertyName, PROP_CONTEXTS[key], ctx);
    }

    PROP_CONTEXTS[key] = ctx;
    return this;
  };

  /* Default property contexts.
   *
   * Copy of https://github.com/angular/angular/blob/6.0.6/packages/compiler/src/schema/dom_security_schema.ts#L31-L58
   * Changing:
   * - SecurityContext.* => SCE_CONTEXTS/$sce.*
   * - STYLE => CSS
   * - various URL => MEDIA_URL
   * - *|formAction, form|action URL => RESOURCE_URL (like the attribute)
   */
  (function registerNativePropertyContexts() {
    function registerContext(ctx, values) {
      forEach(values, function(v) { PROP_CONTEXTS[v.toLowerCase()] = ctx; });
    }

    registerContext(SCE_CONTEXTS.HTML, [
      'iframe|srcdoc',
      '*|innerHTML',
      '*|outerHTML'
    ]);
    registerContext(SCE_CONTEXTS.CSS, ['*|style']);
    registerContext(SCE_CONTEXTS.URL, [
      'area|href',       'area|ping',
      'a|href',          'a|ping',
      'blockquote|cite',
      'body|background',
      'del|cite',
      'input|src',
      'ins|cite',
      'q|cite'
    ]);
    registerContext(SCE_CONTEXTS.MEDIA_URL, [
      'audio|src',
      'img|src',    'img|srcset',
      'source|src', 'source|srcset',
      'track|src',
      'video|src',  'video|poster'
    ]);
    registerContext(SCE_CONTEXTS.RESOURCE_URL, [
      '*|formAction',
      'applet|code',      'applet|codebase',
      'base|href',
      'embed|src',
      'frame|src',
      'form|action',
      'head|profile',
      'html|manifest',
      'iframe|src',
      'link|href',
      'media|src',
      'object|codebase',  'object|data',
      'script|src'
    ]);
  })();


  this.$get = [
            '$injector', '$interpolate', '$exceptionHandler', '$templateRequest', '$parse',
            '$controller', '$rootScope', '$sce', '$animate',
    function($injector,   $interpolate,   $exceptionHandler,   $templateRequest,   $parse,
             $controller,   $rootScope,   $sce,   $animate) {

    var SIMPLE_ATTR_NAME = /^\w/;
    var specialAttrHolder = window.document.createElement('div');


    var commentDirectivesEnabled = commentDirectivesEnabledConfig;
    var cssClassDirectivesEnabled = cssClassDirectivesEnabledConfig;


    var onChangesTtl = TTL;
    // The onChanges hooks should all be run together in a single digest
    // When changes occur, the call to trigger their hooks will be added to this queue
    var onChangesQueue;

    // This function is called in a $$postDigest to trigger all the onChanges hooks in a single digest
    function flushOnChangesQueue() {
      try {
        if (!(--onChangesTtl)) {
          // We have hit the TTL limit so reset everything
          onChangesQueue = undefined;
          throw $compileMinErr('infchng', '{0} $onChanges() iterations reached. Aborting!\n', TTL);
        }
        // We must run this hook in an apply since the $$postDigest runs outside apply
        $rootScope.$apply(function() {
          for (var i = 0, ii = onChangesQueue.length; i < ii; ++i) {
            try {
              onChangesQueue[i]();
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          // Reset the queue to trigger a new schedule next time there is a change
          onChangesQueue = undefined;
        });
      } finally {
        onChangesTtl++;
      }
    }


    function sanitizeSrcset(value, invokeType) {
      if (!value) {
        return value;
      }
      if (!isString(value)) {
        throw $compileMinErr('srcset', 'Can\'t pass trusted values to `{0}`: "{1}"', invokeType, value.toString());
      }

      // Such values are a bit too complex to handle automatically inside $sce.
      // Instead, we sanitize each of the URIs individually, which works, even dynamically.

      // It's not possible to work around this using `$sce.trustAsMediaUrl`.
      // If you want to programmatically set explicitly trusted unsafe URLs, you should use
      // `$sce.trustAsHtml` on the whole `img` tag and inject it into the DOM using the
      // `ng-bind-html` directive.

      var result = '';

      // first check if there are spaces because it's not the same pattern
      var trimmedSrcset = trim(value);
      //                (   999x   ,|   999w   ,|   ,|,   )
      var srcPattern = /(\s+\d+x\s*,|\s+\d+w\s*,|\s+,|,\s+)/;
      var pattern = /\s/.test(trimmedSrcset) ? srcPattern : /(,)/;

      // split srcset into tuple of uri and descriptor except for the last item
      var rawUris = trimmedSrcset.split(pattern);

      // for each tuples
      var nbrUrisWith2parts = Math.floor(rawUris.length / 2);
      for (var i = 0; i < nbrUrisWith2parts; i++) {
        var innerIdx = i * 2;
        // sanitize the uri
        result += $sce.getTrustedMediaUrl(trim(rawUris[innerIdx]));
        // add the descriptor
        result += ' ' + trim(rawUris[innerIdx + 1]);
      }

      // split the last item into uri and descriptor
      var lastTuple = trim(rawUris[i * 2]).split(/\s/);

      // sanitize the last uri
      result += $sce.getTrustedMediaUrl(trim(lastTuple[0]));

      // and add the last descriptor if any
      if (lastTuple.length === 2) {
        result += (' ' + trim(lastTuple[1]));
      }
      return result;
    }


    function Attributes(element, attributesToCopy) {
      if (attributesToCopy) {
        var keys = Object.keys(attributesToCopy);
        var i, l, key;

        for (i = 0, l = keys.length; i < l; i++) {
          key = keys[i];
          this[key] = attributesToCopy[key];
        }
      } else {
        this.$attr = {};
      }

      this.$$element = element;
    }

    Attributes.prototype = {
      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$normalize
       * @kind function
       *
       * @description
       * Converts an attribute name (e.g. dash/colon/underscore-delimited string, optionally prefixed with `x-` or
       * `data-`) to its normalized, camelCase form.
       *
       * Also there is special case for Moz prefix starting with upper case letter.
       *
       * For further information check out the guide on {@link guide/directive#matching-directives Matching Directives}
       *
       * @param {string} name Name to normalize
       */
      $normalize: directiveNormalize,


      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$addClass
       * @kind function
       *
       * @description
       * Adds the CSS class value specified by the classVal parameter to the element. If animations
       * are enabled then an animation will be triggered for the class addition.
       *
       * @param {string} classVal The className value that will be added to the element
       */
      $addClass: function(classVal) {
        if (classVal && classVal.length > 0) {
          $animate.addClass(this.$$element, classVal);
        }
      },

      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$removeClass
       * @kind function
       *
       * @description
       * Removes the CSS class value specified by the classVal parameter from the element. If
       * animations are enabled then an animation will be triggered for the class removal.
       *
       * @param {string} classVal The className value that will be removed from the element
       */
      $removeClass: function(classVal) {
        if (classVal && classVal.length > 0) {
          $animate.removeClass(this.$$element, classVal);
        }
      },

      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$updateClass
       * @kind function
       *
       * @description
       * Adds and removes the appropriate CSS class values to the element based on the difference
       * between the new and old CSS class values (specified as newClasses and oldClasses).
       *
       * @param {string} newClasses The current CSS className value
       * @param {string} oldClasses The former CSS className value
       */
      $updateClass: function(newClasses, oldClasses) {
        var toAdd = tokenDifference(newClasses, oldClasses);
        if (toAdd && toAdd.length) {
          $animate.addClass(this.$$element, toAdd);
        }

        var toRemove = tokenDifference(oldClasses, newClasses);
        if (toRemove && toRemove.length) {
          $animate.removeClass(this.$$element, toRemove);
        }
      },

      /**
       * Set a normalized attribute on the element in a way such that all directives
       * can share the attribute. This function properly handles boolean attributes.
       * @param {string} key Normalized key. (ie ngAttribute)
       * @param {string|boolean} value The value to set. If `null` attribute will be deleted.
       * @param {boolean=} writeAttr If false, does not write the value to DOM element attribute.
       *     Defaults to true.
       * @param {string=} attrName Optional none normalized name. Defaults to key.
       */
      $set: function(key, value, writeAttr, attrName) {
        // TODO: decide whether or not to throw an error if "class"
        // is set through this function since it may cause $updateClass to
        // become unstable.

        var node = this.$$element[0],
            booleanKey = getBooleanAttrName(node, key),
            aliasedKey = getAliasedAttrName(key),
            observer = key,
            nodeName;

        if (booleanKey) {
          this.$$element.prop(key, value);
          attrName = booleanKey;
        } else if (aliasedKey) {
          this[aliasedKey] = value;
          observer = aliasedKey;
        }

        this[key] = value;

        // translate normalized key to actual key
        if (attrName) {
          this.$attr[key] = attrName;
        } else {
          attrName = this.$attr[key];
          if (!attrName) {
            this.$attr[key] = attrName = snake_case(key, '-');
          }
        }

        nodeName = nodeName_(this.$$element);

        // Sanitize img[srcset] values.
        if (nodeName === 'img' && key === 'srcset') {
          this[key] = value = sanitizeSrcset(value, '$set(\'srcset\', value)');
        }

        if (writeAttr !== false) {
          if (value === null || isUndefined(value)) {
            this.$$element.removeAttr(attrName);
          } else {
            if (SIMPLE_ATTR_NAME.test(attrName)) {
              // jQuery skips special boolean attrs treatment in XML nodes for
              // historical reasons and hence AngularJS cannot freely call
              // `.attr(attrName, false) with such attributes. To avoid issues
              // in XHTML, call `removeAttr` in such cases instead.
              // See https://github.com/jquery/jquery/issues/4249
              if (booleanKey && value === false) {
                this.$$element.removeAttr(attrName);
              } else {
                this.$$element.attr(attrName, value);
              }
            } else {
              setSpecialAttr(this.$$element[0], attrName, value);
            }
          }
        }

        // fire observers
        var $$observers = this.$$observers;
        if ($$observers) {
          forEach($$observers[observer], function(fn) {
            try {
              fn(value);
            } catch (e) {
              $exceptionHandler(e);
            }
          });
        }
      },


      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$observe
       * @kind function
       *
       * @description
       * Observes an interpolated attribute.
       *
       * The observer function will be invoked once during the next `$digest` following
       * compilation. The observer is then invoked whenever the interpolated value
       * changes.
       *
       * @param {string} key Normalized key. (ie ngAttribute) .
       * @param {function(interpolatedValue)} fn Function that will be called whenever
                the interpolated value of the attribute changes.
       *        See the {@link guide/interpolation#how-text-and-attribute-bindings-work Interpolation
       *        guide} for more info.
       * @returns {function()} Returns a deregistration function for this observer.
       */
      $observe: function(key, fn) {
        var attrs = this,
            $$observers = (attrs.$$observers || (attrs.$$observers = createMap())),
            listeners = ($$observers[key] || ($$observers[key] = []));

        listeners.push(fn);
        $rootScope.$evalAsync(function() {
          if (!listeners.$$inter && attrs.hasOwnProperty(key) && !isUndefined(attrs[key])) {
            // no one registered attribute interpolation function, so lets call it manually
            fn(attrs[key]);
          }
        });

        return function() {
          arrayRemove(listeners, fn);
        };
      }
    };

    function setSpecialAttr(element, attrName, value) {
      // Attributes names that do not start with letters (such as `(click)`) cannot be set using `setAttribute`
      // so we have to jump through some hoops to get such an attribute
      // https://github.com/angular/angular.js/pull/13318
      specialAttrHolder.innerHTML = '<span ' + attrName + '>';
      var attributes = specialAttrHolder.firstChild.attributes;
      var attribute = attributes[0];
      // We have to remove the attribute from its container element before we can add it to the destination element
      attributes.removeNamedItem(attribute.name);
      attribute.value = value;
      element.attributes.setNamedItem(attribute);
    }

    function safeAddClass($element, className) {
      try {
        $element.addClass(className);
      } catch (e) {
        // ignore, since it means that we are trying to set class on
        // SVG element, where class name is read-only.
      }
    }


    var startSymbol = $interpolate.startSymbol(),
        endSymbol = $interpolate.endSymbol(),
        denormalizeTemplate = (startSymbol === '{{' && endSymbol  === '}}')
            ? identity
            : function denormalizeTemplate(template) {
              return template.replace(/\{\{/g, startSymbol).replace(/}}/g, endSymbol);
        },
        NG_PREFIX_BINDING = /^ng(Attr|Prop|On)([A-Z].*)$/;
    var MULTI_ELEMENT_DIR_RE = /^(.+)Start$/;

    compile.$$addBindingInfo = debugInfoEnabled ? function $$addBindingInfo($element, binding) {
      var bindings = $element.data('$binding') || [];

      if (isArray(binding)) {
        bindings = bindings.concat(binding);
      } else {
        bindings.push(binding);
      }

      $element.data('$binding', bindings);
    } : noop;

    compile.$$addBindingClass = debugInfoEnabled ? function $$addBindingClass($element) {
      safeAddClass($element, 'ng-binding');
    } : noop;

    compile.$$addScopeInfo = debugInfoEnabled ? function $$addScopeInfo($element, scope, isolated, noTemplate) {
      var dataName = isolated ? (noTemplate ? '$isolateScopeNoTemplate' : '$isolateScope') : '$scope';
      $element.data(dataName, scope);
    } : noop;

    compile.$$addScopeClass = debugInfoEnabled ? function $$addScopeClass($element, isolated) {
      safeAddClass($element, isolated ? 'ng-isolate-scope' : 'ng-scope');
    } : noop;

    compile.$$createComment = function(directiveName, comment) {
      var content = '';
      if (debugInfoEnabled) {
        content = ' ' + (directiveName || '') + ': ';
        if (comment) content += comment + ' ';
      }
      return window.document.createComment(content);
    };

    return compile;

    //================================

    function compile($compileNodes, transcludeFn, maxPriority, ignoreDirective,
                        previousCompileContext) {
      if (!($compileNodes instanceof jqLite)) {
        // jquery always rewraps, whereas we need to preserve the original selector so that we can
        // modify it.
        $compileNodes = jqLite($compileNodes);
      }
      var compositeLinkFn =
              compileNodes($compileNodes, transcludeFn, $compileNodes,
                           maxPriority, ignoreDirective, previousCompileContext);
      compile.$$addScopeClass($compileNodes);
      var namespace = null;
      return function publicLinkFn(scope, cloneConnectFn, options) {
        if (!$compileNodes) {
          throw $compileMinErr('multilink', 'This element has already been linked.');
        }
        assertArg(scope, 'scope');

        if (previousCompileContext && previousCompileContext.needsNewScope) {
          // A parent directive did a replace and a directive on this element asked
          // for transclusion, which caused us to lose a layer of element on which
          // we could hold the new transclusion scope, so we will create it manually
          // here.
          scope = scope.$parent.$new();
        }

        options = options || {};
        var parentBoundTranscludeFn = options.parentBoundTranscludeFn,
          transcludeControllers = options.transcludeControllers,
          futureParentElement = options.futureParentElement;

        // When `parentBoundTranscludeFn` is passed, it is a
        // `controllersBoundTransclude` function (it was previously passed
        // as `transclude` to directive.link) so we must unwrap it to get
        // its `boundTranscludeFn`
        if (parentBoundTranscludeFn && parentBoundTranscludeFn.$$boundTransclude) {
          parentBoundTranscludeFn = parentBoundTranscludeFn.$$boundTransclude;
        }

        if (!namespace) {
          namespace = detectNamespaceForChildElements(futureParentElement);
        }
        var $linkNode;
        if (namespace !== 'html') {
          // When using a directive with replace:true and templateUrl the $compileNodes
          // (or a child element inside of them)
          // might change, so we need to recreate the namespace adapted compileNodes
          // for call to the link function.
          // Note: This will already clone the nodes...
          $linkNode = jqLite(
            wrapTemplate(namespace, jqLite('<div></div>').append($compileNodes).html())
          );
        } else if (cloneConnectFn) {
          // important!!: we must call our jqLite.clone() since the jQuery one is trying to be smart
          // and sometimes changes the structure of the DOM.
          $linkNode = JQLitePrototype.clone.call($compileNodes);
        } else {
          $linkNode = $compileNodes;
        }

        if (transcludeControllers) {
          for (var controllerName in transcludeControllers) {
            $linkNode.data('$' + controllerName + 'Controller', transcludeControllers[controllerName].instance);
          }
        }

        compile.$$addScopeInfo($linkNode, scope);

        if (cloneConnectFn) cloneConnectFn($linkNode, scope);
        if (compositeLinkFn) compositeLinkFn(scope, $linkNode, $linkNode, parentBoundTranscludeFn);

        if (!cloneConnectFn) {
          $compileNodes = compositeLinkFn = null;
        }
        return $linkNode;
      };
    }

    function detectNamespaceForChildElements(parentElement) {
      // TODO: Make this detect MathML as well...
      var node = parentElement && parentElement[0];
      if (!node) {
        return 'html';
      } else {
        return nodeName_(node) !== 'foreignobject' && toString.call(node).match(/SVG/) ? 'svg' : 'html';
      }
    }

    /**
     * Compile function matches each node in nodeList against the directives. Once all directives
     * for a particular node are collected their compile functions are executed. The compile
     * functions return values - the linking functions - are combined into a composite linking
     * function, which is the a linking function for the node.
     *
     * @param {NodeList} nodeList an array of nodes or NodeList to compile
     * @param {function(angular.Scope, cloneAttachFn=)} transcludeFn A linking function, where the
     *        scope argument is auto-generated to the new child of the transcluded parent scope.
     * @param {DOMElement=} $rootElement If the nodeList is the root of the compilation tree then
     *        the rootElement must be set the jqLite collection of the compile root. This is
     *        needed so that the jqLite collection items can be replaced with widgets.
     * @param {number=} maxPriority Max directive priority.
     * @returns {Function} A composite linking function of all of the matched directives or null.
     */
    function compileNodes(nodeList, transcludeFn, $rootElement, maxPriority, ignoreDirective,
                            previousCompileContext) {
      var linkFns = [],
          // `nodeList` can be either an element's `.childNodes` (live NodeList)
          // or a jqLite/jQuery collection or an array
          notLiveList = isArray(nodeList) || (nodeList instanceof jqLite),
          attrs, directives, nodeLinkFn, childNodes, childLinkFn, linkFnFound, nodeLinkFnFound;


      for (var i = 0; i < nodeList.length; i++) {
        attrs = new Attributes();

        // Support: IE 11 only
        // Workaround for #11781 and #14924
        if (msie === 11) {
          mergeConsecutiveTextNodes(nodeList, i, notLiveList);
        }

        // We must always refer to `nodeList[i]` hereafter,
        // since the nodes can be replaced underneath us.
        directives = collectDirectives(nodeList[i], [], attrs, i === 0 ? maxPriority : undefined,
                                        ignoreDirective);

        nodeLinkFn = (directives.length)
            ? applyDirectivesToNode(directives, nodeList[i], attrs, transcludeFn, $rootElement,
                                      null, [], [], previousCompileContext)
            : null;

        if (nodeLinkFn && nodeLinkFn.scope) {
          compile.$$addScopeClass(attrs.$$element);
        }

        childLinkFn = (nodeLinkFn && nodeLinkFn.terminal ||
                      !(childNodes = nodeList[i].childNodes) ||
                      !childNodes.length)
            ? null
            : compileNodes(childNodes,
                 nodeLinkFn ? (
                  (nodeLinkFn.transcludeOnThisElement || !nodeLinkFn.templateOnThisElement)
                     && nodeLinkFn.transclude) : transcludeFn);

        if (nodeLinkFn || childLinkFn) {
          linkFns.push(i, nodeLinkFn, childLinkFn);
          linkFnFound = true;
          nodeLinkFnFound = nodeLinkFnFound || nodeLinkFn;
        }

        //use the previous context only for the first element in the virtual group
        previousCompileContext = null;
      }

      // return a linking function if we have found anything, null otherwise
      return linkFnFound ? compositeLinkFn : null;

      function compositeLinkFn(scope, nodeList, $rootElement, parentBoundTranscludeFn) {
        var nodeLinkFn, childLinkFn, node, childScope, i, ii, idx, childBoundTranscludeFn;
        var stableNodeList;


        if (nodeLinkFnFound) {
          // copy nodeList so that if a nodeLinkFn removes or adds an element at this DOM level our
          // offsets don't get screwed up
          var nodeListLength = nodeList.length;
          stableNodeList = new Array(nodeListLength);

          // create a sparse array by only copying the elements which have a linkFn
          for (i = 0; i < linkFns.length; i += 3) {
            idx = linkFns[i];
            stableNodeList[idx] = nodeList[idx];
          }
        } else {
          stableNodeList = nodeList;
        }

        for (i = 0, ii = linkFns.length; i < ii;) {
          node = stableNodeList[linkFns[i++]];
          nodeLinkFn = linkFns[i++];
          childLinkFn = linkFns[i++];

          if (nodeLinkFn) {
            if (nodeLinkFn.scope) {
              childScope = scope.$new();
              compile.$$addScopeInfo(jqLite(node), childScope);
            } else {
              childScope = scope;
            }

            if (nodeLinkFn.transcludeOnThisElement) {
              childBoundTranscludeFn = createBoundTranscludeFn(
                  scope, nodeLinkFn.transclude, parentBoundTranscludeFn);

            } else if (!nodeLinkFn.templateOnThisElement && parentBoundTranscludeFn) {
              childBoundTranscludeFn = parentBoundTranscludeFn;

            } else if (!parentBoundTranscludeFn && transcludeFn) {
              childBoundTranscludeFn = createBoundTranscludeFn(scope, transcludeFn);

            } else {
              childBoundTranscludeFn = null;
            }

            nodeLinkFn(childLinkFn, childScope, node, $rootElement, childBoundTranscludeFn);

          } else if (childLinkFn) {
            childLinkFn(scope, node.childNodes, undefined, parentBoundTranscludeFn);
          }
        }
      }
    }

    function mergeConsecutiveTextNodes(nodeList, idx, notLiveList) {
      var node = nodeList[idx];
      var parent = node.parentNode;
      var sibling;

      if (node.nodeType !== NODE_TYPE_TEXT) {
        return;
      }

      while (true) {
        sibling = parent ? node.nextSibling : nodeList[idx + 1];
        if (!sibling || sibling.nodeType !== NODE_TYPE_TEXT) {
          break;
        }

        node.nodeValue = node.nodeValue + sibling.nodeValue;

        if (sibling.parentNode) {
          sibling.parentNode.removeChild(sibling);
        }
        if (notLiveList && sibling === nodeList[idx + 1]) {
          nodeList.splice(idx + 1, 1);
        }
      }
    }

    function createBoundTranscludeFn(scope, transcludeFn, previousBoundTranscludeFn) {
      function boundTranscludeFn(transcludedScope, cloneFn, controllers, futureParentElement, containingScope) {

        if (!transcludedScope) {
          transcludedScope = scope.$new(false, containingScope);
          transcludedScope.$$transcluded = true;
        }

        return transcludeFn(transcludedScope, cloneFn, {
          parentBoundTranscludeFn: previousBoundTranscludeFn,
          transcludeControllers: controllers,
          futureParentElement: futureParentElement
        });
      }

      // We need  to attach the transclusion slots onto the `boundTranscludeFn`
      // so that they are available inside the `controllersBoundTransclude` function
      var boundSlots = boundTranscludeFn.$$slots = createMap();
      for (var slotName in transcludeFn.$$slots) {
        if (transcludeFn.$$slots[slotName]) {
          boundSlots[slotName] = createBoundTranscludeFn(scope, transcludeFn.$$slots[slotName], previousBoundTranscludeFn);
        } else {
          boundSlots[slotName] = null;
        }
      }

      return boundTranscludeFn;
    }

    /**
     * Looks for directives on the given node and adds them to the directive collection which is
     * sorted.
     *
     * @param node Node to search.
     * @param directives An array to which the directives are added to. This array is sorted before
     *        the function returns.
     * @param attrs The shared attrs object which is used to populate the normalized attributes.
     * @param {number=} maxPriority Max directive priority.
     */
    function collectDirectives(node, directives, attrs, maxPriority, ignoreDirective) {
      var nodeType = node.nodeType,
          attrsMap = attrs.$attr,
          match,
          nodeName,
          className;

      switch (nodeType) {
        case NODE_TYPE_ELEMENT: /* Element */

          nodeName = nodeName_(node);

          // use the node name: <directive>
          addDirective(directives,
              directiveNormalize(nodeName), 'E', maxPriority, ignoreDirective);

          // iterate over the attributes
          for (var attr, name, nName, value, ngPrefixMatch, nAttrs = node.attributes,
                   j = 0, jj = nAttrs && nAttrs.length; j < jj; j++) {
            var attrStartName = false;
            var attrEndName = false;

            var isNgAttr = false, isNgProp = false, isNgEvent = false;
            var multiElementMatch;

            attr = nAttrs[j];
            name = attr.name;
            value = attr.value;

            nName = directiveNormalize(name.toLowerCase());

            // Support ng-attr-*, ng-prop-* and ng-on-*
            if ((ngPrefixMatch = nName.match(NG_PREFIX_BINDING))) {
              isNgAttr = ngPrefixMatch[1] === 'Attr';
              isNgProp = ngPrefixMatch[1] === 'Prop';
              isNgEvent = ngPrefixMatch[1] === 'On';

              // Normalize the non-prefixed name
              name = name.replace(PREFIX_REGEXP, '')
                .toLowerCase()
                .substr(4 + ngPrefixMatch[1].length).replace(/_(.)/g, function(match, letter) {
                  return letter.toUpperCase();
                });

            // Support *-start / *-end multi element directives
            } else if ((multiElementMatch = nName.match(MULTI_ELEMENT_DIR_RE)) && directiveIsMultiElement(multiElementMatch[1])) {
              attrStartName = name;
              attrEndName = name.substr(0, name.length - 5) + 'end';
              name = name.substr(0, name.length - 6);
            }

            if (isNgProp || isNgEvent) {
              attrs[nName] = value;
              attrsMap[nName] = attr.name;

              if (isNgProp) {
                addPropertyDirective(node, directives, nName, name);
              } else {
                addEventDirective(directives, nName, name);
              }
            } else {
              // Update nName for cases where a prefix was removed
              // NOTE: the .toLowerCase() is unnecessary and causes https://github.com/angular/angular.js/issues/16624 for ng-attr-*
              nName = directiveNormalize(name.toLowerCase());
              attrsMap[nName] = name;

              if (isNgAttr || !attrs.hasOwnProperty(nName)) {
                attrs[nName] = value;
                if (getBooleanAttrName(node, nName)) {
                  attrs[nName] = true; // presence means true
                }
              }

              addAttrInterpolateDirective(node, directives, value, nName, isNgAttr);
              addDirective(directives, nName, 'A', maxPriority, ignoreDirective, attrStartName,
                            attrEndName);
            }
          }

          if (nodeName === 'input' && node.getAttribute('type') === 'hidden') {
            // Hidden input elements can have strange behaviour when navigating back to the page
            // This tells the browser not to try to cache and reinstate previous values
            node.setAttribute('autocomplete', 'off');
          }

          // use class as directive
          if (!cssClassDirectivesEnabled) break;
          className = node.className;
          if (isObject(className)) {
              // Maybe SVGAnimatedString
              className = className.animVal;
          }
          if (isString(className) && className !== '') {
            while ((match = CLASS_DIRECTIVE_REGEXP.exec(className))) {
              nName = directiveNormalize(match[2]);
              if (addDirective(directives, nName, 'C', maxPriority, ignoreDirective)) {
                attrs[nName] = trim(match[3]);
              }
              className = className.substr(match.index + match[0].length);
            }
          }
          break;
        case NODE_TYPE_TEXT: /* Text Node */
          addTextInterpolateDirective(directives, node.nodeValue);
          break;
        case NODE_TYPE_COMMENT: /* Comment */
          if (!commentDirectivesEnabled) break;
          collectCommentDirectives(node, directives, attrs, maxPriority, ignoreDirective);
          break;
      }

      directives.sort(byPriority);
      return directives;
    }

    function collectCommentDirectives(node, directives, attrs, maxPriority, ignoreDirective) {
      // function created because of performance, try/catch disables
      // the optimization of the whole function #14848
      try {
        var match = COMMENT_DIRECTIVE_REGEXP.exec(node.nodeValue);
        if (match) {
          var nName = directiveNormalize(match[1]);
          if (addDirective(directives, nName, 'M', maxPriority, ignoreDirective)) {
            attrs[nName] = trim(match[2]);
          }
        }
      } catch (e) {
        // turns out that under some circumstances IE9 throws errors when one attempts to read
        // comment's node value.
        // Just ignore it and continue. (Can't seem to reproduce in test case.)
      }
    }

    /**
     * Given a node with a directive-start it collects all of the siblings until it finds
     * directive-end.
     * @param node
     * @param attrStart
     * @param attrEnd
     * @returns {*}
     */
    function groupScan(node, attrStart, attrEnd) {
      var nodes = [];
      var depth = 0;
      if (attrStart && node.hasAttribute && node.hasAttribute(attrStart)) {
        do {
          if (!node) {
            throw $compileMinErr('uterdir',
                      'Unterminated attribute, found \'{0}\' but no matching \'{1}\' found.',
                      attrStart, attrEnd);
          }
          if (node.nodeType === NODE_TYPE_ELEMENT) {
            if (node.hasAttribute(attrStart)) depth++;
            if (node.hasAttribute(attrEnd)) depth--;
          }
          nodes.push(node);
          node = node.nextSibling;
        } while (depth > 0);
      } else {
        nodes.push(node);
      }

      return jqLite(nodes);
    }

    /**
     * Wrapper for linking function which converts normal linking function into a grouped
     * linking function.
     * @param linkFn
     * @param attrStart
     * @param attrEnd
     * @returns {Function}
     */
    function groupElementsLinkFnWrapper(linkFn, attrStart, attrEnd) {
      return function groupedElementsLink(scope, element, attrs, controllers, transcludeFn) {
        element = groupScan(element[0], attrStart, attrEnd);
        return linkFn(scope, element, attrs, controllers, transcludeFn);
      };
    }

    /**
     * A function generator that is used to support both eager and lazy compilation
     * linking function.
     * @param eager
     * @param $compileNodes
     * @param transcludeFn
     * @param maxPriority
     * @param ignoreDirective
     * @param previousCompileContext
     * @returns {Function}
     */
    function compilationGenerator(eager, $compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext) {
      var compiled;

      if (eager) {
        return compile($compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext);
      }
      return /** @this */ function lazyCompilation() {
        if (!compiled) {
          compiled = compile($compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext);

          // Null out all of these references in order to make them eligible for garbage collection
          // since this is a potentially long lived closure
          $compileNodes = transcludeFn = previousCompileContext = null;
        }
        return compiled.apply(this, arguments);
      };
    }

    /**
     * Once the directives have been collected, their compile functions are executed. This method
     * is responsible for inlining directive templates as well as terminating the application
     * of the directives if the terminal directive has been reached.
     *
     * @param {Array} directives Array of collected directives to execute their compile function.
     *        this needs to be pre-sorted by priority order.
     * @param {Node} compileNode The raw DOM node to apply the compile functions to
     * @param {Object} templateAttrs The shared attribute function
     * @param {function(angular.Scope, cloneAttachFn=)} transcludeFn A linking function, where the
     *                                                  scope argument is auto-generated to the new
     *                                                  child of the transcluded parent scope.
     * @param {JQLite} jqCollection If we are working on the root of the compile tree then this
     *                              argument has the root jqLite array so that we can replace nodes
     *                              on it.
     * @param {Object=} originalReplaceDirective An optional directive that will be ignored when
     *                                           compiling the transclusion.
     * @param {Array.<Function>} preLinkFns
     * @param {Array.<Function>} postLinkFns
     * @param {Object} previousCompileContext Context used for previous compilation of the current
     *                                        node
     * @returns {Function} linkFn
     */
    function applyDirectivesToNode(directives, compileNode, templateAttrs, transcludeFn,
                                   jqCollection, originalReplaceDirective, preLinkFns, postLinkFns,
                                   previousCompileContext) {
      previousCompileContext = previousCompileContext || {};

      var terminalPriority = -Number.MAX_VALUE,
          newScopeDirective = previousCompileContext.newScopeDirective,
          controllerDirectives = previousCompileContext.controllerDirectives,
          newIsolateScopeDirective = previousCompileContext.newIsolateScopeDirective,
          templateDirective = previousCompileContext.templateDirective,
          nonTlbTranscludeDirective = previousCompileContext.nonTlbTranscludeDirective,
          hasTranscludeDirective = false,
          hasTemplate = false,
          hasElementTranscludeDirective = previousCompileContext.hasElementTranscludeDirective,
          $compileNode = templateAttrs.$$element = jqLite(compileNode),
          directive,
          directiveName,
          $template,
          replaceDirective = originalReplaceDirective,
          childTranscludeFn = transcludeFn,
          linkFn,
          didScanForMultipleTransclusion = false,
          mightHaveMultipleTransclusionError = false,
          directiveValue;

      // executes all directives on the current element
      for (var i = 0, ii = directives.length; i < ii; i++) {
        directive = directives[i];
        var attrStart = directive.$$start;
        var attrEnd = directive.$$end;

        // collect multiblock sections
        if (attrStart) {
          $compileNode = groupScan(compileNode, attrStart, attrEnd);
        }
        $template = undefined;

        if (terminalPriority > directive.priority) {
          break; // prevent further processing of directives
        }

        directiveValue = directive.scope;

        if (directiveValue) {

          // skip the check for directives with async templates, we'll check the derived sync
          // directive when the template arrives
          if (!directive.templateUrl) {
            if (isObject(directiveValue)) {
              // This directive is trying to add an isolated scope.
              // Check that there is no scope of any kind already
              assertNoDuplicate('new/isolated scope', newIsolateScopeDirective || newScopeDirective,
                                directive, $compileNode);
              newIsolateScopeDirective = directive;
            } else {
              // This directive is trying to add a child scope.
              // Check that there is no isolated scope already
              assertNoDuplicate('new/isolated scope', newIsolateScopeDirective, directive,
                                $compileNode);
            }
          }

          newScopeDirective = newScopeDirective || directive;
        }

        directiveName = directive.name;

        // If we encounter a condition that can result in transclusion on the directive,
        // then scan ahead in the remaining directives for others that may cause a multiple
        // transclusion error to be thrown during the compilation process.  If a matching directive
        // is found, then we know that when we encounter a transcluded directive, we need to eagerly
        // compile the `transclude` function rather than doing it lazily in order to throw
        // exceptions at the correct time
        if (!didScanForMultipleTransclusion && ((directive.replace && (directive.templateUrl || directive.template))
            || (directive.transclude && !directive.$$tlb))) {
                var candidateDirective;

                for (var scanningIndex = i + 1; (candidateDirective = directives[scanningIndex++]);) {
                    if ((candidateDirective.transclude && !candidateDirective.$$tlb)
                        || (candidateDirective.replace && (candidateDirective.templateUrl || candidateDirective.template))) {
                        mightHaveMultipleTransclusionError = true;
                        break;
                    }
                }

                didScanForMultipleTransclusion = true;
        }

        if (!directive.templateUrl && directive.controller) {
          controllerDirectives = controllerDirectives || createMap();
          assertNoDuplicate('\'' + directiveName + '\' controller',
              controllerDirectives[directiveName], directive, $compileNode);
          controllerDirectives[directiveName] = directive;
        }

        directiveValue = directive.transclude;

        if (directiveValue) {
          hasTranscludeDirective = true;

          // Special case ngIf and ngRepeat so that we don't complain about duplicate transclusion.
          // This option should only be used by directives that know how to safely handle element transclusion,
          // where the transcluded nodes are added or replaced after linking.
          if (!directive.$$tlb) {
            assertNoDuplicate('transclusion', nonTlbTranscludeDirective, directive, $compileNode);
            nonTlbTranscludeDirective = directive;
          }

          if (directiveValue === 'element') {
            hasElementTranscludeDirective = true;
            terminalPriority = directive.priority;
            $template = $compileNode;
            $compileNode = templateAttrs.$$element =
                jqLite(compile.$$createComment(directiveName, templateAttrs[directiveName]));
            compileNode = $compileNode[0];
            replaceWith(jqCollection, sliceArgs($template), compileNode);

            childTranscludeFn = compilationGenerator(mightHaveMultipleTransclusionError, $template, transcludeFn, terminalPriority,
                                        replaceDirective && replaceDirective.name, {
                                          // Don't pass in:
                                          // - controllerDirectives - otherwise we'll create duplicates controllers
                                          // - newIsolateScopeDirective or templateDirective - combining templates with
                                          //   element transclusion doesn't make sense.
                                          //
                                          // We need only nonTlbTranscludeDirective so that we prevent putting transclusion
                                          // on the same element more than once.
                                          nonTlbTranscludeDirective: nonTlbTranscludeDirective
                                        });
          } else {

            var slots = createMap();

            if (!isObject(directiveValue)) {
              $template = jqLite(jqLiteClone(compileNode)).contents();
            } else {

              // We have transclusion slots,
              // collect them up, compile them and store their transclusion functions
              $template = window.document.createDocumentFragment();

              var slotMap = createMap();
              var filledSlots = createMap();

              // Parse the element selectors
              forEach(directiveValue, function(elementSelector, slotName) {
                // If an element selector starts with a ? then it is optional
                var optional = (elementSelector.charAt(0) === '?');
                elementSelector = optional ? elementSelector.substring(1) : elementSelector;

                slotMap[elementSelector] = slotName;

                // We explicitly assign `null` since this implies that a slot was defined but not filled.
                // Later when calling boundTransclusion functions with a slot name we only error if the
                // slot is `undefined`
                slots[slotName] = null;

                // filledSlots contains `true` for all slots that are either optional or have been
                // filled. This is used to check that we have not missed any required slots
                filledSlots[slotName] = optional;
              });

              // Add the matching elements into their slot
              forEach($compileNode.contents(), function(node) {
                var slotName = slotMap[directiveNormalize(nodeName_(node))];
                if (slotName) {
                  filledSlots[slotName] = true;
                  slots[slotName] = slots[slotName] || window.document.createDocumentFragment();
                  slots[slotName].appendChild(node);
                } else {
                  $template.appendChild(node);
                }
              });

              // Check for required slots that were not filled
              forEach(filledSlots, function(filled, slotName) {
                if (!filled) {
                  throw $compileMinErr('reqslot', 'Required transclusion slot `{0}` was not filled.', slotName);
                }
              });

              for (var slotName in slots) {
                if (slots[slotName]) {
                  // Only define a transclusion function if the slot was filled
                  var slotCompileNodes = jqLite(slots[slotName].childNodes);
                  slots[slotName] = compilationGenerator(mightHaveMultipleTransclusionError, slotCompileNodes, transcludeFn);
                }
              }

              $template = jqLite($template.childNodes);
            }

            $compileNode.empty(); // clear contents
            childTranscludeFn = compilationGenerator(mightHaveMultipleTransclusionError, $template, transcludeFn, undefined,
                undefined, { needsNewScope: directive.$$isolateScope || directive.$$newScope});
            childTranscludeFn.$$slots = slots;
          }
        }

        if (directive.template) {
          hasTemplate = true;
          assertNoDuplicate('template', templateDirective, directive, $compileNode);
          templateDirective = directive;

          directiveValue = (isFunction(directive.template))
              ? directive.template($compileNode, templateAttrs)
              : directive.template;

          directiveValue = denormalizeTemplate(directiveValue);

          if (directive.replace) {
            replaceDirective = directive;
            if (jqLiteIsTextNode(directiveValue)) {
              $template = [];
            } else {
              $template = removeComments(wrapTemplate(directive.templateNamespace, trim(directiveValue)));
            }
            compileNode = $template[0];

            if ($template.length !== 1 || compileNode.nodeType !== NODE_TYPE_ELEMENT) {
              throw $compileMinErr('tplrt',
                  'Template for directive \'{0}\' must have exactly one root element. {1}',
                  directiveName, '');
            }

            replaceWith(jqCollection, $compileNode, compileNode);

            var newTemplateAttrs = {$attr: {}};

            // combine directives from the original node and from the template:
            // - take the array of directives for this element
            // - split it into two parts, those that already applied (processed) and those that weren't (unprocessed)
            // - collect directives from the template and sort them by priority
            // - combine directives as: processed + template + unprocessed
            var templateDirectives = collectDirectives(compileNode, [], newTemplateAttrs);
            var unprocessedDirectives = directives.splice(i + 1, directives.length - (i + 1));

            if (newIsolateScopeDirective || newScopeDirective) {
              // The original directive caused the current element to be replaced but this element
              // also needs to have a new scope, so we need to tell the template directives
              // that they would need to get their scope from further up, if they require transclusion
              markDirectiveScope(templateDirectives, newIsolateScopeDirective, newScopeDirective);
            }
            directives = directives.concat(templateDirectives).concat(unprocessedDirectives);
            mergeTemplateAttributes(templateAttrs, newTemplateAttrs);

            ii = directives.length;
          } else {
            $compileNode.html(directiveValue);
          }
        }

        if (directive.templateUrl) {
          hasTemplate = true;
          assertNoDuplicate('template', templateDirective, directive, $compileNode);
          templateDirective = directive;

          if (directive.replace) {
            replaceDirective = directive;
          }

          // eslint-disable-next-line no-func-assign
          nodeLinkFn = compileTemplateUrl(directives.splice(i, directives.length - i), $compileNode,
              templateAttrs, jqCollection, hasTranscludeDirective && childTranscludeFn, preLinkFns, postLinkFns, {
                controllerDirectives: controllerDirectives,
                newScopeDirective: (newScopeDirective !== directive) && newScopeDirective,
                newIsolateScopeDirective: newIsolateScopeDirective,
                templateDirective: templateDirective,
                nonTlbTranscludeDirective: nonTlbTranscludeDirective
              });
          ii = directives.length;
        } else if (directive.compile) {
          try {
            linkFn = directive.compile($compileNode, templateAttrs, childTranscludeFn);
            var context = directive.$$originalDirective || directive;
            if (isFunction(linkFn)) {
              addLinkFns(null, bind(context, linkFn), attrStart, attrEnd);
            } else if (linkFn) {
              addLinkFns(bind(context, linkFn.pre), bind(context, linkFn.post), attrStart, attrEnd);
            }
          } catch (e) {
            $exceptionHandler(e, startingTag($compileNode));
          }
        }

        if (directive.terminal) {
          nodeLinkFn.terminal = true;
          terminalPriority = Math.max(terminalPriority, directive.priority);
        }

      }

      nodeLinkFn.scope = newScopeDirective && newScopeDirective.scope === true;
      nodeLinkFn.transcludeOnThisElement = hasTranscludeDirective;
      nodeLinkFn.templateOnThisElement = hasTemplate;
      nodeLinkFn.transclude = childTranscludeFn;

      previousCompileContext.hasElementTranscludeDirective = hasElementTranscludeDirective;

      // might be normal or delayed nodeLinkFn depending on if templateUrl is present
      return nodeLinkFn;

      ////////////////////

      function addLinkFns(pre, post, attrStart, attrEnd) {
        if (pre) {
          if (attrStart) pre = groupElementsLinkFnWrapper(pre, attrStart, attrEnd);
          pre.require = directive.require;
          pre.directiveName = directiveName;
          if (newIsolateScopeDirective === directive || directive.$$isolateScope) {
            pre = cloneAndAnnotateFn(pre, {isolateScope: true});
          }
          preLinkFns.push(pre);
        }
        if (post) {
          if (attrStart) post = groupElementsLinkFnWrapper(post, attrStart, attrEnd);
          post.require = directive.require;
          post.directiveName = directiveName;
          if (newIsolateScopeDirective === directive || directive.$$isolateScope) {
            post = cloneAndAnnotateFn(post, {isolateScope: true});
          }
          postLinkFns.push(post);
        }
      }

      function nodeLinkFn(childLinkFn, scope, linkNode, $rootElement, boundTranscludeFn) {
        var i, ii, linkFn, isolateScope, controllerScope, elementControllers, transcludeFn, $element,
            attrs, scopeBindingInfo;

        if (compileNode === linkNode) {
          attrs = templateAttrs;
          $element = templateAttrs.$$element;
        } else {
          $element = jqLite(linkNode);
          attrs = new Attributes($element, templateAttrs);
        }

        controllerScope = scope;
        if (newIsolateScopeDirective) {
          isolateScope = scope.$new(true);
        } else if (newScopeDirective) {
          controllerScope = scope.$parent;
        }

        if (boundTranscludeFn) {
          // track `boundTranscludeFn` so it can be unwrapped if `transcludeFn`
          // is later passed as `parentBoundTranscludeFn` to `publicLinkFn`
          transcludeFn = controllersBoundTransclude;
          transcludeFn.$$boundTransclude = boundTranscludeFn;
          // expose the slots on the `$transclude` function
          transcludeFn.isSlotFilled = function(slotName) {
            return !!boundTranscludeFn.$$slots[slotName];
          };
        }

        if (controllerDirectives) {
          elementControllers = setupControllers($element, attrs, transcludeFn, controllerDirectives, isolateScope, scope, newIsolateScopeDirective);
        }

        if (newIsolateScopeDirective) {
          // Initialize isolate scope bindings for new isolate scope directive.
          compile.$$addScopeInfo($element, isolateScope, true, !(templateDirective && (templateDirective === newIsolateScopeDirective ||
              templateDirective === newIsolateScopeDirective.$$originalDirective)));
          compile.$$addScopeClass($element, true);
          isolateScope.$$isolateBindings =
              newIsolateScopeDirective.$$isolateBindings;
          scopeBindingInfo = initializeDirectiveBindings(scope, attrs, isolateScope,
                                        isolateScope.$$isolateBindings,
                                        newIsolateScopeDirective);
          if (scopeBindingInfo.removeWatches) {
            isolateScope.$on('$destroy', scopeBindingInfo.removeWatches);
          }
        }

        // Initialize bindToController bindings
        for (var name in elementControllers) {
          var controllerDirective = controllerDirectives[name];
          var controller = elementControllers[name];
          var bindings = controllerDirective.$$bindings.bindToController;

          controller.instance = controller();
          $element.data('$' + controllerDirective.name + 'Controller', controller.instance);
          controller.bindingInfo =
            initializeDirectiveBindings(controllerScope, attrs, controller.instance, bindings, controllerDirective);
          }

        // Bind the required controllers to the controller, if `require` is an object and `bindToController` is truthy
        forEach(controllerDirectives, function(controllerDirective, name) {
          var require = controllerDirective.require;
          if (controllerDirective.bindToController && !isArray(require) && isObject(require)) {
            extend(elementControllers[name].instance, getControllers(name, require, $element, elementControllers));
          }
        });

        // Handle the init and destroy lifecycle hooks on all controllers that have them
        forEach(elementControllers, function(controller) {
          var controllerInstance = controller.instance;
          if (isFunction(controllerInstance.$onChanges)) {
            try {
              controllerInstance.$onChanges(controller.bindingInfo.initialChanges);
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          if (isFunction(controllerInstance.$onInit)) {
            try {
              controllerInstance.$onInit();
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          if (isFunction(controllerInstance.$doCheck)) {
            controllerScope.$watch(function() { controllerInstance.$doCheck(); });
            controllerInstance.$doCheck();
          }
          if (isFunction(controllerInstance.$onDestroy)) {
            controllerScope.$on('$destroy', function callOnDestroyHook() {
              controllerInstance.$onDestroy();
            });
          }
        });

        // PRELINKING
        for (i = 0, ii = preLinkFns.length; i < ii; i++) {
          linkFn = preLinkFns[i];
          invokeLinkFn(linkFn,
              linkFn.isolateScope ? isolateScope : scope,
              $element,
              attrs,
              linkFn.require && getControllers(linkFn.directiveName, linkFn.require, $element, elementControllers),
              transcludeFn
          );
        }

        // RECURSION
        // We only pass the isolate scope, if the isolate directive has a template,
        // otherwise the child elements do not belong to the isolate directive.
        var scopeToChild = scope;
        if (newIsolateScopeDirective && (newIsolateScopeDirective.template || newIsolateScopeDirective.templateUrl === null)) {
          scopeToChild = isolateScope;
        }
        if (childLinkFn) {
          childLinkFn(scopeToChild, linkNode.childNodes, undefined, boundTranscludeFn);
        }

        // POSTLINKING
        for (i = postLinkFns.length - 1; i >= 0; i--) {
          linkFn = postLinkFns[i];
          invokeLinkFn(linkFn,
              linkFn.isolateScope ? isolateScope : scope,
              $element,
              attrs,
              linkFn.require && getControllers(linkFn.directiveName, linkFn.require, $element, elementControllers),
              transcludeFn
          );
        }

        // Trigger $postLink lifecycle hooks
        forEach(elementControllers, function(controller) {
          var controllerInstance = controller.instance;
          if (isFunction(controllerInstance.$postLink)) {
            controllerInstance.$postLink();
          }
        });

        // This is the function that is injected as `$transclude`.
        // Note: all arguments are optional!
        function controllersBoundTransclude(scope, cloneAttachFn, futureParentElement, slotName) {
          var transcludeControllers;
          // No scope passed in:
          if (!isScope(scope)) {
            slotName = futureParentElement;
            futureParentElement = cloneAttachFn;
            cloneAttachFn = scope;
            scope = undefined;
          }

          if (hasElementTranscludeDirective) {
            transcludeControllers = elementControllers;
          }
          if (!futureParentElement) {
            futureParentElement = hasElementTranscludeDirective ? $element.parent() : $element;
          }
          if (slotName) {
            // slotTranscludeFn can be one of three things:
            //  * a transclude function - a filled slot
            //  * `null` - an optional slot that was not filled
            //  * `undefined` - a slot that was not declared (i.e. invalid)
            var slotTranscludeFn = boundTranscludeFn.$$slots[slotName];
            if (slotTranscludeFn) {
              return slotTranscludeFn(scope, cloneAttachFn, transcludeControllers, futureParentElement, scopeToChild);
            } else if (isUndefined(slotTranscludeFn)) {
              throw $compileMinErr('noslot',
               'No parent directive that requires a transclusion with slot name "{0}". ' +
               'Element: {1}',
               slotName, startingTag($element));
            }
          } else {
            return boundTranscludeFn(scope, cloneAttachFn, transcludeControllers, futureParentElement, scopeToChild);
          }
        }
      }
    }

    function getControllers(directiveName, require, $element, elementControllers) {
      var value;

      if (isString(require)) {
        var match = require.match(REQUIRE_PREFIX_REGEXP);
        var name = require.substring(match[0].length);
        var inheritType = match[1] || match[3];
        var optional = match[2] === '?';

        //If only parents then start at the parent element
        if (inheritType === '^^') {
          $element = $element.parent();
        //Otherwise attempt getting the controller from elementControllers in case
        //the element is transcluded (and has no data) and to avoid .data if possible
        } else {
          value = elementControllers && elementControllers[name];
          value = value && value.instance;
        }

        if (!value) {
          var dataName = '$' + name + 'Controller';

          if (inheritType === '^^' && $element[0] && $element[0].nodeType === NODE_TYPE_DOCUMENT) {
            // inheritedData() uses the documentElement when it finds the document, so we would
            // require from the element itself.
            value = null;
          } else {
            value = inheritType ? $element.inheritedData(dataName) : $element.data(dataName);
          }
        }

        if (!value && !optional) {
          throw $compileMinErr('ctreq',
              'Controller \'{0}\', required by directive \'{1}\', can\'t be found!',
              name, directiveName);
        }
      } else if (isArray(require)) {
        value = [];
        for (var i = 0, ii = require.length; i < ii; i++) {
          value[i] = getControllers(directiveName, require[i], $element, elementControllers);
        }
      } else if (isObject(require)) {
        value = {};
        forEach(require, function(controller, property) {
          value[property] = getControllers(directiveName, controller, $element, elementControllers);
        });
      }

      return value || null;
    }

    function setupControllers($element, attrs, transcludeFn, controllerDirectives, isolateScope, scope, newIsolateScopeDirective) {
      var elementControllers = createMap();
      for (var controllerKey in controllerDirectives) {
        var directive = controllerDirectives[controllerKey];
        var locals = {
          $scope: directive === newIsolateScopeDirective || directive.$$isolateScope ? isolateScope : scope,
          $element: $element,
          $attrs: attrs,
          $transclude: transcludeFn
        };

        var controller = directive.controller;
        if (controller === '@') {
          controller = attrs[directive.name];
        }

        var controllerInstance = $controller(controller, locals, true, directive.controllerAs);

        // For directives with element transclusion the element is a comment.
        // In this case .data will not attach any data.
        // Instead, we save the controllers for the element in a local hash and attach to .data
        // later, once we have the actual element.
        elementControllers[directive.name] = controllerInstance;
        $element.data('$' + directive.name + 'Controller', controllerInstance.instance);
      }
      return elementControllers;
    }

    // Depending upon the context in which a directive finds itself it might need to have a new isolated
    // or child scope created. For instance:
    // * if the directive has been pulled into a template because another directive with a higher priority
    // asked for element transclusion
    // * if the directive itself asks for transclusion but it is at the root of a template and the original
    // element was replaced. See https://github.com/angular/angular.js/issues/12936
    function markDirectiveScope(directives, isolateScope, newScope) {
      for (var j = 0, jj = directives.length; j < jj; j++) {
        directives[j] = inherit(directives[j], {$$isolateScope: isolateScope, $$newScope: newScope});
      }
    }

    /**
     * looks up the directive and decorates it with exception handling and proper parameters. We
     * call this the boundDirective.
     *
     * @param {string} name name of the directive to look up.
     * @param {string} location The directive must be found in specific format.
     *   String containing any of theses characters:
     *
     *   * `E`: element name
     *   * `A': attribute
     *   * `C`: class
     *   * `M`: comment
     * @returns {boolean} true if directive was added.
     */
    function addDirective(tDirectives, name, location, maxPriority, ignoreDirective, startAttrName,
                          endAttrName) {
      if (name === ignoreDirective) return null;
      var match = null;
      if (hasDirectives.hasOwnProperty(name)) {
        for (var directive, directives = $injector.get(name + Suffix),
            i = 0, ii = directives.length; i < ii; i++) {
          directive = directives[i];
          if ((isUndefined(maxPriority) || maxPriority > directive.priority) &&
               directive.restrict.indexOf(location) !== -1) {
            if (startAttrName) {
              directive = inherit(directive, {$$start: startAttrName, $$end: endAttrName});
            }
            if (!directive.$$bindings) {
              var bindings = directive.$$bindings =
                  parseDirectiveBindings(directive, directive.name);
              if (isObject(bindings.isolateScope)) {
                directive.$$isolateBindings = bindings.isolateScope;
              }
            }
            tDirectives.push(directive);
            match = directive;
          }
        }
      }
      return match;
    }


    /**
     * looks up the directive and returns true if it is a multi-element directive,
     * and therefore requires DOM nodes between -start and -end markers to be grouped
     * together.
     *
     * @param {string} name name of the directive to look up.
     * @returns true if directive was registered as multi-element.
     */
    function directiveIsMultiElement(name) {
      if (hasDirectives.hasOwnProperty(name)) {
        for (var directive, directives = $injector.get(name + Suffix),
            i = 0, ii = directives.length; i < ii; i++) {
          directive = directives[i];
          if (directive.multiElement) {
            return true;
          }
        }
      }
      return false;
    }

    /**
     * When the element is replaced with HTML template then the new attributes
     * on the template need to be merged with the existing attributes in the DOM.
     * The desired effect is to have both of the attributes present.
     *
     * @param {object} dst destination attributes (original DOM)
     * @param {object} src source attributes (from the directive template)
     */
    function mergeTemplateAttributes(dst, src) {
      var srcAttr = src.$attr,
          dstAttr = dst.$attr;

      // reapply the old attributes to the new element
      forEach(dst, function(value, key) {
        if (key.charAt(0) !== '$') {
          if (src[key] && src[key] !== value) {
            if (value.length) {
              value += (key === 'style' ? ';' : ' ') + src[key];
            } else {
              value = src[key];
            }
          }
          dst.$set(key, value, true, srcAttr[key]);
        }
      });

      // copy the new attributes on the old attrs object
      forEach(src, function(value, key) {
        // Check if we already set this attribute in the loop above.
        // `dst` will never contain hasOwnProperty as DOM parser won't let it.
        // You will get an "InvalidCharacterError: DOM Exception 5" error if you
        // have an attribute like "has-own-property" or "data-has-own-property", etc.
        if (!dst.hasOwnProperty(key) && key.charAt(0) !== '$') {
          dst[key] = value;

          if (key !== 'class' && key !== 'style') {
            dstAttr[key] = srcAttr[key];
          }
        }
      });
    }


    function compileTemplateUrl(directives, $compileNode, tAttrs,
        $rootElement, childTranscludeFn, preLinkFns, postLinkFns, previousCompileContext) {
      var linkQueue = [],
          afterTemplateNodeLinkFn,
          afterTemplateChildLinkFn,
          beforeTemplateCompileNode = $compileNode[0],
          origAsyncDirective = directives.shift(),
          derivedSyncDirective = inherit(origAsyncDirective, {
            templateUrl: null, transclude: null, replace: null, $$originalDirective: origAsyncDirective
          }),
          templateUrl = (isFunction(origAsyncDirective.templateUrl))
              ? origAsyncDirective.templateUrl($compileNode, tAttrs)
              : origAsyncDirective.templateUrl,
          templateNamespace = origAsyncDirective.templateNamespace;

      $compileNode.empty();

      $templateRequest(templateUrl)
        .then(function(content) {
          var compileNode, tempTemplateAttrs, $template, childBoundTranscludeFn;

          content = denormalizeTemplate(content);

          if (origAsyncDirective.replace) {
            if (jqLiteIsTextNode(content)) {
              $template = [];
            } else {
              $template = removeComments(wrapTemplate(templateNamespace, trim(content)));
            }
            compileNode = $template[0];

            if ($template.length !== 1 || compileNode.nodeType !== NODE_TYPE_ELEMENT) {
              throw $compileMinErr('tplrt',
                  'Template for directive \'{0}\' must have exactly one root element. {1}',
                  origAsyncDirective.name, templateUrl);
            }

            tempTemplateAttrs = {$attr: {}};
            replaceWith($rootElement, $compileNode, compileNode);
            var templateDirectives = collectDirectives(compileNode, [], tempTemplateAttrs);

            if (isObject(origAsyncDirective.scope)) {
              // the original directive that caused the template to be loaded async required
              // an isolate scope
              markDirectiveScope(templateDirectives, true);
            }
            directives = templateDirectives.concat(directives);
            mergeTemplateAttributes(tAttrs, tempTemplateAttrs);
          } else {
            compileNode = beforeTemplateCompileNode;
            $compileNode.html(content);
          }

          directives.unshift(derivedSyncDirective);

          afterTemplateNodeLinkFn = applyDirectivesToNode(directives, compileNode, tAttrs,
              childTranscludeFn, $compileNode, origAsyncDirective, preLinkFns, postLinkFns,
              previousCompileContext);
          forEach($rootElement, function(node, i) {
            if (node === compileNode) {
              $rootElement[i] = $compileNode[0];
            }
          });
          afterTemplateChildLinkFn = compileNodes($compileNode[0].childNodes, childTranscludeFn);

          while (linkQueue.length) {
            var scope = linkQueue.shift(),
                beforeTemplateLinkNode = linkQueue.shift(),
                linkRootElement = linkQueue.shift(),
                boundTranscludeFn = linkQueue.shift(),
                linkNode = $compileNode[0];

            if (scope.$$destroyed) continue;

            if (beforeTemplateLinkNode !== beforeTemplateCompileNode) {
              var oldClasses = beforeTemplateLinkNode.className;

              if (!(previousCompileContext.hasElementTranscludeDirective &&
                  origAsyncDirective.replace)) {
                // it was cloned therefore we have to clone as well.
                linkNode = jqLiteClone(compileNode);
              }
              replaceWith(linkRootElement, jqLite(beforeTemplateLinkNode), linkNode);

              // Copy in CSS classes from original node
              safeAddClass(jqLite(linkNode), oldClasses);
            }
            if (afterTemplateNodeLinkFn.transcludeOnThisElement) {
              childBoundTranscludeFn = createBoundTranscludeFn(scope, afterTemplateNodeLinkFn.transclude, boundTranscludeFn);
            } else {
              childBoundTranscludeFn = boundTranscludeFn;
            }
            afterTemplateNodeLinkFn(afterTemplateChildLinkFn, scope, linkNode, $rootElement,
              childBoundTranscludeFn);
          }
          linkQueue = null;
        }).catch(function(error) {
          if (isError(error)) {
            $exceptionHandler(error);
          }
        });

      return function delayedNodeLinkFn(ignoreChildLinkFn, scope, node, rootElement, boundTranscludeFn) {
        var childBoundTranscludeFn = boundTranscludeFn;
        if (scope.$$destroyed) return;
        if (linkQueue) {
          linkQueue.push(scope,
                         node,
                         rootElement,
                         childBoundTranscludeFn);
        } else {
          if (afterTemplateNodeLinkFn.transcludeOnThisElement) {
            childBoundTranscludeFn = createBoundTranscludeFn(scope, afterTemplateNodeLinkFn.transclude, boundTranscludeFn);
          }
          afterTemplateNodeLinkFn(afterTemplateChildLinkFn, scope, node, rootElement, childBoundTranscludeFn);
        }
      };
    }


    /**
     * Sorting function for bound directives.
     */
    function byPriority(a, b) {
      var diff = b.priority - a.priority;
      if (diff !== 0) return diff;
      if (a.name !== b.name) return (a.name < b.name) ? -1 : 1;
      return a.index - b.index;
    }

    function assertNoDuplicate(what, previousDirective, directive, element) {

      function wrapModuleNameIfDefined(moduleName) {
        return moduleName ?
          (' (module: ' + moduleName + ')') :
          '';
      }

      if (previousDirective) {
        throw $compileMinErr('multidir', 'Multiple directives [{0}{1}, {2}{3}] asking for {4} on: {5}',
            previousDirective.name, wrapModuleNameIfDefined(previousDirective.$$moduleName),
            directive.name, wrapModuleNameIfDefined(directive.$$moduleName), what, startingTag(element));
      }
    }


    function addTextInterpolateDirective(directives, text) {
      var interpolateFn = $interpolate(text, true);
      if (interpolateFn) {
        directives.push({
          priority: 0,
          compile: function textInterpolateCompileFn(templateNode) {
            var templateNodeParent = templateNode.parent(),
                hasCompileParent = !!templateNodeParent.length;

            // When transcluding a template that has bindings in the root
            // we don't have a parent and thus need to add the class during linking fn.
            if (hasCompileParent) compile.$$addBindingClass(templateNodeParent);

            return function textInterpolateLinkFn(scope, node) {
              var parent = node.parent();
              if (!hasCompileParent) compile.$$addBindingClass(parent);
              compile.$$addBindingInfo(parent, interpolateFn.expressions);
              scope.$watch(interpolateFn, function interpolateFnWatchAction(value) {
                node[0].nodeValue = value;
              });
            };
          }
        });
      }
    }


    function wrapTemplate(type, template) {
      type = lowercase(type || 'html');
      switch (type) {
      case 'svg':
      case 'math':
        var wrapper = window.document.createElement('div');
        wrapper.innerHTML = '<' + type + '>' + template + '</' + type + '>';
        return wrapper.childNodes[0].childNodes;
      default:
        return template;
      }
    }


    function getTrustedAttrContext(nodeName, attrNormalizedName) {
      if (attrNormalizedName === 'srcdoc') {
        return $sce.HTML;
      }
      // All nodes with src attributes require a RESOURCE_URL value, except for
      // img and various html5 media nodes, which require the MEDIA_URL context.
      if (attrNormalizedName === 'src' || attrNormalizedName === 'ngSrc') {
        if (['img', 'video', 'audio', 'source', 'track'].indexOf(nodeName) === -1) {
          return $sce.RESOURCE_URL;
        }
        return $sce.MEDIA_URL;
      } else if (attrNormalizedName === 'xlinkHref') {
        // Some xlink:href are okay, most aren't
        if (nodeName === 'image') return $sce.MEDIA_URL;
        if (nodeName === 'a') return $sce.URL;
        return $sce.RESOURCE_URL;
      } else if (
          // Formaction
          (nodeName === 'form' && attrNormalizedName === 'action') ||
          // If relative URLs can go where they are not expected to, then
          // all sorts of trust issues can arise.
          (nodeName === 'base' && attrNormalizedName === 'href') ||
          // links can be stylesheets or imports, which can run script in the current origin
          (nodeName === 'link' && attrNormalizedName === 'href')
      ) {
        return $sce.RESOURCE_URL;
      } else if (nodeName === 'a' && (attrNormalizedName === 'href' ||
                                 attrNormalizedName === 'ngHref')) {
        return $sce.URL;
      }
    }

    function getTrustedPropContext(nodeName, propNormalizedName) {
      var prop = propNormalizedName.toLowerCase();
      return PROP_CONTEXTS[nodeName + '|' + prop] || PROP_CONTEXTS['*|' + prop];
    }

    function sanitizeSrcsetPropertyValue(value) {
      return sanitizeSrcset($sce.valueOf(value), 'ng-prop-srcset');
    }
    function addPropertyDirective(node, directives, attrName, propName) {
      if (EVENT_HANDLER_ATTR_REGEXP.test(propName)) {
        throw $compileMinErr('nodomevents', 'Property bindings for HTML DOM event properties are disallowed');
      }

      var nodeName = nodeName_(node);
      var trustedContext = getTrustedPropContext(nodeName, propName);

      var sanitizer = identity;
      // Sanitize img[srcset] + source[srcset] values.
      if (propName === 'srcset' && (nodeName === 'img' || nodeName === 'source')) {
        sanitizer = sanitizeSrcsetPropertyValue;
      } else if (trustedContext) {
        sanitizer = $sce.getTrusted.bind($sce, trustedContext);
      }

      directives.push({
        priority: 100,
        compile: function ngPropCompileFn(_, attr) {
          var ngPropGetter = $parse(attr[attrName]);
          var ngPropWatch = $parse(attr[attrName], function sceValueOf(val) {
            // Unwrap the value to compare the actual inner safe value, not the wrapper object.
            return $sce.valueOf(val);
          });

          return {
            pre: function ngPropPreLinkFn(scope, $element) {
              function applyPropValue() {
                var propValue = ngPropGetter(scope);
                $element[0][propName] = sanitizer(propValue);
              }

              applyPropValue();
              scope.$watch(ngPropWatch, applyPropValue);
            }
          };
        }
      });
    }

    function addEventDirective(directives, attrName, eventName) {
      directives.push(
        createEventDirective($parse, $rootScope, $exceptionHandler, attrName, eventName, /*forceAsync=*/false)
      );
    }

    function addAttrInterpolateDirective(node, directives, value, name, isNgAttr) {
      var nodeName = nodeName_(node);
      var trustedContext = getTrustedAttrContext(nodeName, name);
      var mustHaveExpression = !isNgAttr;
      var allOrNothing = ALL_OR_NOTHING_ATTRS[name] || isNgAttr;

      var interpolateFn = $interpolate(value, mustHaveExpression, trustedContext, allOrNothing);

      // no interpolation found -> ignore
      if (!interpolateFn) return;

      if (name === 'multiple' && nodeName === 'select') {
        throw $compileMinErr('selmulti',
            'Binding to the \'multiple\' attribute is not supported. Element: {0}',
            startingTag(node));
      }

      if (EVENT_HANDLER_ATTR_REGEXP.test(name)) {
        throw $compileMinErr('nodomevents', 'Interpolations for HTML DOM event attributes are disallowed');
      }

      directives.push({
        priority: 100,
        compile: function() {
            return {
              pre: function attrInterpolatePreLinkFn(scope, element, attr) {
                var $$observers = (attr.$$observers || (attr.$$observers = createMap()));

                // If the attribute has changed since last $interpolate()ed
                var newValue = attr[name];
                if (newValue !== value) {
                  // we need to interpolate again since the attribute value has been updated
                  // (e.g. by another directive's compile function)
                  // ensure unset/empty values make interpolateFn falsy
                  interpolateFn = newValue && $interpolate(newValue, true, trustedContext, allOrNothing);
                  value = newValue;
                }

                // if attribute was updated so that there is no interpolation going on we don't want to
                // register any observers
                if (!interpolateFn) return;

                // initialize attr object so that it's ready in case we need the value for isolate
                // scope initialization, otherwise the value would not be available from isolate
                // directive's linking fn during linking phase
                attr[name] = interpolateFn(scope);

                ($$observers[name] || ($$observers[name] = [])).$$inter = true;
                (attr.$$observers && attr.$$observers[name].$$scope || scope).
                  $watch(interpolateFn, function interpolateFnWatchAction(newValue, oldValue) {
                    //special case for class attribute addition + removal
                    //so that class changes can tap into the animation
                    //hooks provided by the $animate service. Be sure to
                    //skip animations when the first digest occurs (when
                    //both the new and the old values are the same) since
                    //the CSS classes are the non-interpolated values
                    if (name === 'class' && newValue !== oldValue) {
                      attr.$updateClass(newValue, oldValue);
                    } else {
                      attr.$set(name, newValue);
                    }
                  });
              }
            };
          }
      });
    }


    /**
     * This is a special jqLite.replaceWith, which can replace items which
     * have no parents, provided that the containing jqLite collection is provided.
     *
     * @param {JqLite=} $rootElement The root of the compile tree. Used so that we can replace nodes
     *                               in the root of the tree.
     * @param {JqLite} elementsToRemove The jqLite element which we are going to replace. We keep
     *                                  the shell, but replace its DOM node reference.
     * @param {Node} newNode The new DOM node.
     */
    function replaceWith($rootElement, elementsToRemove, newNode) {
      var firstElementToRemove = elementsToRemove[0],
          removeCount = elementsToRemove.length,
          parent = firstElementToRemove.parentNode,
          i, ii;

      if ($rootElement) {
        for (i = 0, ii = $rootElement.length; i < ii; i++) {
          if ($rootElement[i] === firstElementToRemove) {
            $rootElement[i++] = newNode;
            for (var j = i, j2 = j + removeCount - 1,
                     jj = $rootElement.length;
                 j < jj; j++, j2++) {
              if (j2 < jj) {
                $rootElement[j] = $rootElement[j2];
              } else {
                delete $rootElement[j];
              }
            }
            $rootElement.length -= removeCount - 1;

            // If the replaced element is also the jQuery .context then replace it
            // .context is a deprecated jQuery api, so we should set it only when jQuery set it
            // http://api.jquery.com/context/
            if ($rootElement.context === firstElementToRemove) {
              $rootElement.context = newNode;
            }
            break;
          }
        }
      }

      if (parent) {
        parent.replaceChild(newNode, firstElementToRemove);
      }

      // Append all the `elementsToRemove` to a fragment. This will...
      // - remove them from the DOM
      // - allow them to still be traversed with .nextSibling
      // - allow a single fragment.qSA to fetch all elements being removed
      var fragment = window.document.createDocumentFragment();
      for (i = 0; i < removeCount; i++) {
        fragment.appendChild(elementsToRemove[i]);
      }

      if (jqLite.hasData(firstElementToRemove)) {
        // Copy over user data (that includes AngularJS's $scope etc.). Don't copy private
        // data here because there's no public interface in jQuery to do that and copying over
        // event listeners (which is the main use of private data) wouldn't work anyway.
        jqLite.data(newNode, jqLite.data(firstElementToRemove));

        // Remove $destroy event listeners from `firstElementToRemove`
        jqLite(firstElementToRemove).off('$destroy');
      }

      // Cleanup any data/listeners on the elements and children.
      // This includes invoking the $destroy event on any elements with listeners.
      jqLite.cleanData(fragment.querySelectorAll('*'));

      // Update the jqLite collection to only contain the `newNode`
      for (i = 1; i < removeCount; i++) {
        delete elementsToRemove[i];
      }
      elementsToRemove[0] = newNode;
      elementsToRemove.length = 1;
    }


    function cloneAndAnnotateFn(fn, annotation) {
      return extend(function() { return fn.apply(null, arguments); }, fn, annotation);
    }


    function invokeLinkFn(linkFn, scope, $element, attrs, controllers, transcludeFn) {
      try {
        linkFn(scope, $element, attrs, controllers, transcludeFn);
      } catch (e) {
        $exceptionHandler(e, startingTag($element));
      }
    }

    function strictBindingsCheck(attrName, directiveName) {
      if (strictComponentBindingsEnabled) {
        throw $compileMinErr('missingattr',
          'Attribute \'{0}\' of \'{1}\' is non-optional and must be set!',
          attrName, directiveName);
      }
    }

    // Set up $watches for isolate scope and controller bindings.
    function initializeDirectiveBindings(scope, attrs, destination, bindings, directive) {
      var removeWatchCollection = [];
      var initialChanges = {};
      var changes;

      forEach(bindings, function initializeBinding(definition, scopeName) {
        var attrName = definition.attrName,
        optional = definition.optional,
        mode = definition.mode, // @, =, <, or &
        lastValue,
        parentGet, parentSet, compare, removeWatch;

        switch (mode) {

          case '@':
            if (!optional && !hasOwnProperty.call(attrs, attrName)) {
              strictBindingsCheck(attrName, directive.name);
              destination[scopeName] = attrs[attrName] = undefined;

            }
            removeWatch = attrs.$observe(attrName, function(value) {
              if (isString(value) || isBoolean(value)) {
                var oldValue = destination[scopeName];
                recordChanges(scopeName, value, oldValue);
                destination[scopeName] = value;
              }
            });
            attrs.$$observers[attrName].$$scope = scope;
            lastValue = attrs[attrName];
            if (isString(lastValue)) {
              // If the attribute has been provided then we trigger an interpolation to ensure
              // the value is there for use in the link fn
              destination[scopeName] = $interpolate(lastValue)(scope);
            } else if (isBoolean(lastValue)) {
              // If the attributes is one of the BOOLEAN_ATTR then AngularJS will have converted
              // the value to boolean rather than a string, so we special case this situation
              destination[scopeName] = lastValue;
            }
            initialChanges[scopeName] = new SimpleChange(_UNINITIALIZED_VALUE, destination[scopeName]);
            removeWatchCollection.push(removeWatch);
            break;

          case '=':
            if (!hasOwnProperty.call(attrs, attrName)) {
              if (optional) break;
              strictBindingsCheck(attrName, directive.name);
              attrs[attrName] = undefined;
            }
            if (optional && !attrs[attrName]) break;

            parentGet = $parse(attrs[attrName]);
            if (parentGet.literal) {
              compare = equals;
            } else {
              compare = simpleCompare;
            }
            parentSet = parentGet.assign || function() {
              // reset the change, or we will throw this exception on every $digest
              lastValue = destination[scopeName] = parentGet(scope);
              throw $compileMinErr('nonassign',
                  'Expression \'{0}\' in attribute \'{1}\' used with directive \'{2}\' is non-assignable!',
                  attrs[attrName], attrName, directive.name);
            };
            lastValue = destination[scopeName] = parentGet(scope);
            var parentValueWatch = function parentValueWatch(parentValue) {
              if (!compare(parentValue, destination[scopeName])) {
                // we are out of sync and need to copy
                if (!compare(parentValue, lastValue)) {
                  // parent changed and it has precedence
                  destination[scopeName] = parentValue;
                } else {
                  // if the parent can be assigned then do so
                  parentSet(scope, parentValue = destination[scopeName]);
                }
              }
              lastValue = parentValue;
              return lastValue;
            };
            parentValueWatch.$stateful = true;
            if (definition.collection) {
              removeWatch = scope.$watchCollection(attrs[attrName], parentValueWatch);
            } else {
              removeWatch = scope.$watch($parse(attrs[attrName], parentValueWatch), null, parentGet.literal);
            }
            removeWatchCollection.push(removeWatch);
            break;

          case '<':
            if (!hasOwnProperty.call(attrs, attrName)) {
              if (optional) break;
              strictBindingsCheck(attrName, directive.name);
              attrs[attrName] = undefined;
            }
            if (optional && !attrs[attrName]) break;

            parentGet = $parse(attrs[attrName]);
            var isLiteral = parentGet.literal;

            var initialValue = destination[scopeName] = parentGet(scope);
            initialChanges[scopeName] = new SimpleChange(_UNINITIALIZED_VALUE, destination[scopeName]);

            removeWatch = scope[definition.collection ? '$watchCollection' : '$watch'](parentGet, function parentValueWatchAction(newValue, oldValue) {
              if (oldValue === newValue) {
                if (oldValue === initialValue || (isLiteral && equals(oldValue, initialValue))) {
                  return;
                }
                oldValue = initialValue;
              }
              recordChanges(scopeName, newValue, oldValue);
              destination[scopeName] = newValue;
            });

            removeWatchCollection.push(removeWatch);
            break;

          case '&':
            if (!optional && !hasOwnProperty.call(attrs, attrName)) {
              strictBindingsCheck(attrName, directive.name);
            }
            // Don't assign Object.prototype method to scope
            parentGet = attrs.hasOwnProperty(attrName) ? $parse(attrs[attrName]) : noop;

            // Don't assign noop to destination if expression is not valid
            if (parentGet === noop && optional) break;

            destination[scopeName] = function(locals) {
              return parentGet(scope, locals);
            };
            break;
        }
      });

      function recordChanges(key, currentValue, previousValue) {
        if (isFunction(destination.$onChanges) && !simpleCompare(currentValue, previousValue)) {
          // If we have not already scheduled the top level onChangesQueue handler then do so now
          if (!onChangesQueue) {
            scope.$$postDigest(flushOnChangesQueue);
            onChangesQueue = [];
          }
          // If we have not already queued a trigger of onChanges for this controller then do so now
          if (!changes) {
            changes = {};
            onChangesQueue.push(triggerOnChangesHook);
          }
          // If the has been a change on this property already then we need to reuse the previous value
          if (changes[key]) {
            previousValue = changes[key].previousValue;
          }
          // Store this change
          changes[key] = new SimpleChange(previousValue, currentValue);
        }
      }

      function triggerOnChangesHook() {
        destination.$onChanges(changes);
        // Now clear the changes so that we schedule onChanges when more changes arrive
        changes = undefined;
      }

      return {
        initialChanges: initialChanges,
        removeWatches: removeWatchCollection.length && function removeWatches() {
          for (var i = 0, ii = removeWatchCollection.length; i < ii; ++i) {
            removeWatchCollection[i]();
          }
        }
      };
    }
  }];
}

function SimpleChange(previous, current) {
  this.previousValue = previous;
  this.currentValue = current;
}
SimpleChange.prototype.isFirstChange = function() { return this.previousValue === _UNINITIALIZED_VALUE; };


var PREFIX_REGEXP = /^((?:x|data)[:\-_])/i;
var SPECIAL_CHARS_REGEXP = /[:\-_]+(.)/g;

/**
 * Converts all accepted directives format into proper directive name.
 * @param name Name to normalize
 */
function directiveNormalize(name) {
  return name
    .replace(PREFIX_REGEXP, '')
    .replace(SPECIAL_CHARS_REGEXP, function(_, letter, offset) {
      return offset ? letter.toUpperCase() : letter;
    });
}

/**
 * @ngdoc type
 * @name $compile.directive.Attributes
 *
 * @description
 * A shared object between directive compile / linking functions which contains normalized DOM
 * element attributes. The values reflect current binding state `{{ }}`. The normalization is
 * needed since all of these are treated as equivalent in AngularJS:
 *
 * ```
 *    <span ng:bind="a" ng-bind="a" data-ng-bind="a" x-ng-bind="a">
 * ```
 */

/**
 * @ngdoc property
 * @name $compile.directive.Attributes#$attr
 *
 * @description
 * A map of DOM element attribute names to the normalized name. This is
 * needed to do reverse lookup from normalized name back to actual name.
 */


/**
 * @ngdoc method
 * @name $compile.directive.Attributes#$set
 * @kind function
 *
 * @description
 * Set DOM element attribute value.
 *
 *
 * @param {string} name Normalized element attribute name of the property to modify. The name is
 *          reverse-translated using the {@link ng.$compile.directive.Attributes#$attr $attr}
 *          property to the original name.
 * @param {string} value Value to set the attribute to. The value can be an interpolated string.
 */



/**
 * Closure compiler type information
 */

function nodesetLinkingFn(
  /* angular.Scope */ scope,
  /* NodeList */ nodeList,
  /* Element */ rootElement,
  /* function(Function) */ boundTranscludeFn
) {}

function directiveLinkingFn(
  /* nodesetLinkingFn */ nodesetLinkingFn,
  /* angular.Scope */ scope,
  /* Node */ node,
  /* Element */ rootElement,
  /* function(Function) */ boundTranscludeFn
) {}

function tokenDifference(str1, str2) {
  var values = '',
      tokens1 = str1.split(/\s+/),
      tokens2 = str2.split(/\s+/);

  outer:
  for (var i = 0; i < tokens1.length; i++) {
    var token = tokens1[i];
    for (var j = 0; j < tokens2.length; j++) {
      if (token === tokens2[j]) continue outer;
    }
    values += (values.length > 0 ? ' ' : '') + token;
  }
  return values;
}

function removeComments(jqNodes) {
  jqNodes = jqLite(jqNodes);
  var i = jqNodes.length;

  if (i <= 1) {
    return jqNodes;
  }

  while (i--) {
    var node = jqNodes[i];
    if (node.nodeType === NODE_TYPE_COMMENT ||
       (node.nodeType === NODE_TYPE_TEXT && node.nodeValue.trim() === '')) {
         splice.call(jqNodes, i, 1);
    }
  }
  return jqNodes;
}

var $controllerMinErr = minErr('$controller');


var CNTRL_REG = /^(\S+)(\s+as\s+([\w$]+))?$/;
function identifierForController(controller, ident) {
  if (ident && isString(ident)) return ident;
  if (isString(controller)) {
    var match = CNTRL_REG.exec(controller);
    if (match) return match[3];
  }
}


/**
 * @ngdoc provider
 * @name $controllerProvider
 * @this
 *
 * @description
 * The {@link ng.$controller $controller service} is used by AngularJS to create new
 * controllers.
 *
 * This provider allows controller registration via the
 * {@link ng.$controllerProvider#register register} method.
 */
function $ControllerProvider() {
  var controllers = {};

  /**
   * @ngdoc method
   * @name $controllerProvider#has
   * @param {string} name Controller name to check.
   */
  this.has = function(name) {
    return controllers.hasOwnProperty(name);
  };

  /**
   * @ngdoc method
   * @name $controllerProvider#register
   * @param {string|Object} name Controller name, or an object map of controllers where the keys are
   *    the names and the values are the constructors.
   * @param {Function|Array} constructor Controller constructor fn (optionally decorated with DI
   *    annotations in the array notation).
   */
  this.register = function(name, constructor) {
    assertNotHasOwnProperty(name, 'controller');
    if (isObject(name)) {
      extend(controllers, name);
    } else {
      controllers[name] = constructor;
    }
  };

  this.$get = ['$injector', function($injector) {

    /**
     * @ngdoc service
     * @name $controller
     * @requires $injector
     *
     * @param {Function|string} constructor If called with a function then it's considered to be the
     *    controller constructor function. Otherwise it's considered to be a string which is used
     *    to retrieve the controller constructor using the following steps:
     *
     *    * check if a controller with given name is registered via `$controllerProvider`
     *    * check if evaluating the string on the current scope returns a constructor
     *
     *    The string can use the `controller as property` syntax, where the controller instance is published
     *    as the specified property on the `scope`; the `scope` must be injected into `locals` param for this
     *    to work correctly.
     *
     * @param {Object} locals Injection locals for Controller.
     * @return {Object} Instance of given controller.
     *
     * @description
     * `$controller` service is responsible for instantiating controllers.
     *
     * It's just a simple call to {@link auto.$injector $injector}, but extracted into
     * a service, so that one can override this service with [BC version](https://gist.github.com/1649788).
     */
    return function $controller(expression, locals, later, ident) {
      // PRIVATE API:
      //   param `later` --- indicates that the controller's constructor is invoked at a later time.
      //                     If true, $controller will allocate the object with the correct
      //                     prototype chain, but will not invoke the controller until a returned
      //                     callback is invoked.
      //   param `ident` --- An optional label which overrides the label parsed from the controller
      //                     expression, if any.
      var instance, match, constructor, identifier;
      later = later === true;
      if (ident && isString(ident)) {
        identifier = ident;
      }

      if (isString(expression)) {
        match = expression.match(CNTRL_REG);
        if (!match) {
          throw $controllerMinErr('ctrlfmt',
            'Badly formed controller string \'{0}\'. ' +
            'Must match `__name__ as __id__` or `__name__`.', expression);
        }
        constructor = match[1];
        identifier = identifier || match[3];
        expression = controllers.hasOwnProperty(constructor)
            ? controllers[constructor]
            : getter(locals.$scope, constructor, true);

        if (!expression) {
          throw $controllerMinErr('ctrlreg',
            'The controller with the name \'{0}\' is not registered.', constructor);
        }

        assertArgFn(expression, constructor, true);
      }

      if (later) {
        // Instantiate controller later:
        // This machinery is used to create an instance of the object before calling the
        // controller's constructor itself.
        //
        // This allows properties to be added to the controller before the constructor is
        // invoked. Primarily, this is used for isolate scope bindings in $compile.
        //
        // This feature is not intended for use by applications, and is thus not documented
        // publicly.
        // Object creation: http://jsperf.com/create-constructor/2
        var controllerPrototype = (isArray(expression) ?
          expression[expression.length - 1] : expression).prototype;
        instance = Object.create(controllerPrototype || null);

        if (identifier) {
          addIdentifier(locals, identifier, instance, constructor || expression.name);
        }

        return extend(function $controllerInit() {
          var result = $injector.invoke(expression, instance, locals, constructor);
          if (result !== instance && (isObject(result) || isFunction(result))) {
            instance = result;
            if (identifier) {
              // If result changed, re-assign controllerAs value to scope.
              addIdentifier(locals, identifier, instance, constructor || expression.name);
            }
          }
          return instance;
        }, {
          instance: instance,
          identifier: identifier
        });
      }

      instance = $injector.instantiate(expression, locals, constructor);

      if (identifier) {
        addIdentifier(locals, identifier, instance, constructor || expression.name);
      }

      return instance;
    };

    function addIdentifier(locals, identifier, instance, name) {
      if (!(locals && isObject(locals.$scope))) {
        throw minErr('$controller')('noscp',
          'Cannot export controller \'{0}\' as \'{1}\'! No $scope object provided via `locals`.',
          name, identifier);
      }

      locals.$scope[identifier] = instance;
    }
  }];
}

/**
 * @ngdoc service
 * @name $document
 * @requires $window
 * @this
 *
 * @description
 * A {@link angular.element jQuery or jqLite} wrapper for the browser's `window.document` object.
 *
 * @example
   <example module="documentExample" name="document">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <p>$document title: <b ng-bind="title"></b></p>
         <p>window.document title: <b ng-bind="windowTitle"></b></p>
       </div>
     </file>
     <file name="script.js">
       angular.module('documentExample', [])
         .controller('ExampleController', ['$scope', '$document', function($scope, $document) {
           $scope.title = $document[0].title;
           $scope.windowTitle = angular.element(window.document)[0].title;
         }]);
     </file>
   </example>
 */
function $DocumentProvider() {
  this.$get = ['$window', function(window) {
    return jqLite(window.document);
  }];
}


/**
 * @private
 * @this
 * Listens for document visibility change and makes the current status accessible.
 */
function $$IsDocumentHiddenProvider() {
  this.$get = ['$document', '$rootScope', function($document, $rootScope) {
    var doc = $document[0];
    var hidden = doc && doc.hidden;

    $document.on('visibilitychange', changeListener);

    $rootScope.$on('$destroy', function() {
      $document.off('visibilitychange', changeListener);
    });

    function changeListener() {
      hidden = doc.hidden;
    }

    return function() {
      return hidden;
    };
  }];
}

/**
 * @ngdoc service
 * @name $exceptionHandler
 * @requires ng.$log
 * @this
 *
 * @description
 * Any uncaught exception in AngularJS expressions is delegated to this service.
 * The default implementation simply delegates to `$log.error` which logs it into
 * the browser console.
 *
 * In unit tests, if `angular-mocks.js` is loaded, this service is overridden by
 * {@link ngMock.$exceptionHandler mock $exceptionHandler} which aids in testing.
 *
 * ## Example:
 *
 * The example below will overwrite the default `$exceptionHandler` in order to (a) log uncaught
 * errors to the backend for later inspection by the developers and (b) to use `$log.warn()` instead
 * of `$log.error()`.
 *
 * ```js
 *   angular.
 *     module('exceptionOverwrite', []).
 *     factory('$exceptionHandler', ['$log', 'logErrorsToBackend', function($log, logErrorsToBackend) {
 *       return function myExceptionHandler(exception, cause) {
 *         logErrorsToBackend(exception, cause);
 *         $log.warn(exception, cause);
 *       };
 *     }]);
 * ```
 *
 * <hr />
 * Note, that code executed in event-listeners (even those registered using jqLite's `on`/`bind`
 * methods) does not delegate exceptions to the {@link ng.$exceptionHandler $exceptionHandler}
 * (unless executed during a digest).
 *
 * If you wish, you can manually delegate exceptions, e.g.
 * `try { ... } catch(e) { $exceptionHandler(e); }`
 *
 * @param {Error} exception Exception associated with the error.
 * @param {string=} cause Optional information about the context in which
 *       the error was thrown.
 *
 */
function $ExceptionHandlerProvider() {
  this.$get = ['$log', function($log) {
    return function(exception, cause) {
      $log.error.apply($log, arguments);
    };
  }];
}

var $$ForceReflowProvider = /** @this */ function() {
  this.$get = ['$document', function($document) {
    return function(domNode) {
      //the line below will force the browser to perform a repaint so
      //that all the animated elements within the animation frame will
      //be properly updated and drawn on screen. This is required to
      //ensure that the preparation animation is properly flushed so that
      //the active state picks up from there. DO NOT REMOVE THIS LINE.
      //DO NOT OPTIMIZE THIS LINE. THE MINIFIER WILL REMOVE IT OTHERWISE WHICH
      //WILL RESULT IN AN UNPREDICTABLE BUG THAT IS VERY HARD TO TRACK DOWN AND
      //WILL TAKE YEARS AWAY FROM YOUR LIFE.
      if (domNode) {
        if (!domNode.nodeType && domNode instanceof jqLite) {
          domNode = domNode[0];
        }
      } else {
        domNode = $document[0].body;
      }
      return domNode.offsetWidth + 1;
    };
  }];
};

var APPLICATION_JSON = 'application/json';
var CONTENT_TYPE_APPLICATION_JSON = {'Content-Type': APPLICATION_JSON + ';charset=utf-8'};
var JSON_START = /^\[|^\{(?!\{)/;
var JSON_ENDS = {
  '[': /]$/,
  '{': /}$/
};
var JSON_PROTECTION_PREFIX = /^\)]\}',?\n/;
var $httpMinErr = minErr('$http');

function serializeValue(v) {
  if (isObject(v)) {
    return isDate(v) ? v.toISOString() : toJson(v);
  }
  return v;
}


/** @this */
function $HttpParamSerializerProvider() {
  /**
   * @ngdoc service
   * @name $httpParamSerializer
   * @description
   *
   * Default {@link $http `$http`} params serializer that converts objects to strings
   * according to the following rules:
   *
   * * `{'foo': 'bar'}` results in `foo=bar`
   * * `{'foo': Date.now()}` results in `foo=2015-04-01T09%3A50%3A49.262Z` (`toISOString()` and encoded representation of a Date object)
   * * `{'foo': ['bar', 'baz']}` results in `foo=bar&foo=baz` (repeated key for each array element)
   * * `{'foo': {'bar':'baz'}}` results in `foo=%7B%22bar%22%3A%22baz%22%7D` (stringified and encoded representation of an object)
   *
   * Note that serializer will sort the request parameters alphabetically.
   */

  this.$get = function() {
    return function ngParamSerializer(params) {
      if (!params) return '';
      var parts = [];
      forEachSorted(params, function(value, key) {
        if (value === null || isUndefined(value) || isFunction(value)) return;
        if (isArray(value)) {
          forEach(value, function(v) {
            parts.push(encodeUriQuery(key)  + '=' + encodeUriQuery(serializeValue(v)));
          });
        } else {
          parts.push(encodeUriQuery(key) + '=' + encodeUriQuery(serializeValue(value)));
        }
      });

      return parts.join('&');
    };
  };
}

/** @this */
function $HttpParamSerializerJQLikeProvider() {
  /**
   * @ngdoc service
   * @name $httpParamSerializerJQLike
   *
   * @description
   *
   * Alternative {@link $http `$http`} params serializer that follows
   * jQuery's [`param()`](http://api.jquery.com/jquery.param/) method logic.
   * The serializer will also sort the params alphabetically.
   *
   * To use it for serializing `$http` request parameters, set it as the `paramSerializer` property:
   *
   * ```js
   * $http({
   *   url: myUrl,
   *   method: 'GET',
   *   params: myParams,
   *   paramSerializer: '$httpParamSerializerJQLike'
   * });
   * ```
   *
   * It is also possible to set it as the default `paramSerializer` in the
   * {@link $httpProvider#defaults `$httpProvider`}.
   *
   * Additionally, you can inject the serializer and use it explicitly, for example to serialize
   * form data for submission:
   *
   * ```js
   * .controller(function($http, $httpParamSerializerJQLike) {
   *   //...
   *
   *   $http({
   *     url: myUrl,
   *     method: 'POST',
   *     data: $httpParamSerializerJQLike(myData),
   *     headers: {
   *       'Content-Type': 'application/x-www-form-urlencoded'
   *     }
   *   });
   *
   * });
   * ```
   *
   */
  this.$get = function() {
    return function jQueryLikeParamSerializer(params) {
      if (!params) return '';
      var parts = [];
      serialize(params, '', true);
      return parts.join('&');

      function serialize(toSerialize, prefix, topLevel) {
        if (isArray(toSerialize)) {
          forEach(toSerialize, function(value, index) {
            serialize(value, prefix + '[' + (isObject(value) ? index : '') + ']');
          });
        } else if (isObject(toSerialize) && !isDate(toSerialize)) {
          forEachSorted(toSerialize, function(value, key) {
            serialize(value, prefix +
                (topLevel ? '' : '[') +
                key +
                (topLevel ? '' : ']'));
          });
        } else {
          if (isFunction(toSerialize)) {
            toSerialize = toSerialize();
          }
          parts.push(encodeUriQuery(prefix) + '=' +
              (toSerialize == null ? '' : encodeUriQuery(serializeValue(toSerialize))));
        }
      }
    };
  };
}

function defaultHttpResponseTransform(data, headers) {
  if (isString(data)) {
    // Strip json vulnerability protection prefix and trim whitespace
    var tempData = data.replace(JSON_PROTECTION_PREFIX, '').trim();

    if (tempData) {
      var contentType = headers('Content-Type');
      var hasJsonContentType = contentType && (contentType.indexOf(APPLICATION_JSON) === 0);

      if (hasJsonContentType || isJsonLike(tempData)) {
        try {
          data = fromJson(tempData);
        } catch (e) {
          if (!hasJsonContentType) {
            return data;
          }
          throw $httpMinErr('baddata', 'Data must be a valid JSON object. Received: "{0}". ' +
          'Parse error: "{1}"', data, e);
        }
      }
    }
  }

  return data;
}

function isJsonLike(str) {
    var jsonStart = str.match(JSON_START);
    return jsonStart && JSON_ENDS[jsonStart[0]].test(str);
}

/**
 * Parse headers into key value object
 *
 * @param {string} headers Raw headers as a string
 * @returns {Object} Parsed headers as key value object
 */
function parseHeaders(headers) {
  var parsed = createMap(), i;

  function fillInParsed(key, val) {
    if (key) {
      parsed[key] = parsed[key] ? parsed[key] + ', ' + val : val;
    }
  }

  if (isString(headers)) {
    forEach(headers.split('\n'), function(line) {
      i = line.indexOf(':');
      fillInParsed(lowercase(trim(line.substr(0, i))), trim(line.substr(i + 1)));
    });
  } else if (isObject(headers)) {
    forEach(headers, function(headerVal, headerKey) {
      fillInParsed(lowercase(headerKey), trim(headerVal));
    });
  }

  return parsed;
}


/**
 * Returns a function that provides access to parsed headers.
 *
 * Headers are lazy parsed when first requested.
 * @see parseHeaders
 *
 * @param {(string|Object)} headers Headers to provide access to.
 * @returns {function(string=)} Returns a getter function which if called with:
 *
 *   - if called with an argument returns a single header value or null
 *   - if called with no arguments returns an object containing all headers.
 */
function headersGetter(headers) {
  var headersObj;

  return function(name) {
    if (!headersObj) headersObj =  parseHeaders(headers);

    if (name) {
      var value = headersObj[lowercase(name)];
      if (value === undefined) {
        value = null;
      }
      return value;
    }

    return headersObj;
  };
}


/**
 * Chain all given functions
 *
 * This function is used for both request and response transforming
 *
 * @param {*} data Data to transform.
 * @param {function(string=)} headers HTTP headers getter fn.
 * @param {number} status HTTP status code of the response.
 * @param {(Function|Array.<Function>)} fns Function or an array of functions.
 * @returns {*} Transformed data.
 */
function transformData(data, headers, status, fns) {
  if (isFunction(fns)) {
    return fns(data, headers, status);
  }

  forEach(fns, function(fn) {
    data = fn(data, headers, status);
  });

  return data;
}


function isSuccess(status) {
  return 200 <= status && status < 300;
}


/**
 * @ngdoc provider
 * @name $httpProvider
 * @this
 *
 * @description
 * Use `$httpProvider` to change the default behavior of the {@link ng.$http $http} service.
 */
function $HttpProvider() {
  /**
   * @ngdoc property
   * @name $httpProvider#defaults
   * @description
   *
   * Object containing default values for all {@link ng.$http $http} requests.
   *
   * - **`defaults.cache`** - {boolean|Object} - A boolean value or object created with
   * {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of HTTP responses
   * by default. See {@link $http#caching $http Caching} for more information.
   *
   * - **`defaults.headers`** - {Object} - Default headers for all $http requests.
   * Refer to {@link ng.$http#setting-http-headers $http} for documentation on
   * setting default headers.
   *     - **`defaults.headers.common`**
   *     - **`defaults.headers.post`**
   *     - **`defaults.headers.put`**
   *     - **`defaults.headers.patch`**
   *
   * - **`defaults.jsonpCallbackParam`** - `{string}` - the name of the query parameter that passes the name of the
   * callback in a JSONP request. The value of this parameter will be replaced with the expression generated by the
   * {@link $jsonpCallbacks} service. Defaults to `'callback'`.
   *
   * - **`defaults.paramSerializer`** - `{string|function(Object<string,string>):string}` - A function
   *  used to the prepare string representation of request parameters (specified as an object).
   *  If specified as string, it is interpreted as a function registered with the {@link auto.$injector $injector}.
   *  Defaults to {@link ng.$httpParamSerializer $httpParamSerializer}.
   *
   * - **`defaults.transformRequest`** -
   * `{Array<function(data, headersGetter)>|function(data, headersGetter)}` -
   * An array of functions (or a single function) which are applied to the request data.
   * By default, this is an array with one request transformation function:
   *
   *   - If the `data` property of the request configuration object contains an object, serialize it
   *     into JSON format.
   *
   * - **`defaults.transformResponse`** -
   * `{Array<function(data, headersGetter, status)>|function(data, headersGetter, status)}` -
   * An array of functions (or a single function) which are applied to the response data. By default,
   * this is an array which applies one response transformation function that does two things:
   *
   *  - If XSRF prefix is detected, strip it
   *    (see {@link ng.$http#security-considerations Security Considerations in the $http docs}).
   *  - If the `Content-Type` is `application/json` or the response looks like JSON,
   *    deserialize it using a JSON parser.
   *
   * - **`defaults.xsrfCookieName`** - {string} - Name of cookie containing the XSRF token.
   * Defaults value is `'XSRF-TOKEN'`.
   *
   * - **`defaults.xsrfHeaderName`** - {string} - Name of HTTP header to populate with the
   * XSRF token. Defaults value is `'X-XSRF-TOKEN'`.
   *
   */
  var defaults = this.defaults = {
    // transform incoming response data
    transformResponse: [defaultHttpResponseTransform],

    // transform outgoing request data
    transformRequest: [function(d) {
      return isObject(d) && !isFile(d) && !isBlob(d) && !isFormData(d) ? toJson(d) : d;
    }],

    // default headers
    headers: {
      common: {
        'Accept': 'application/json, text/plain, */*'
      },
      post:   shallowCopy(CONTENT_TYPE_APPLICATION_JSON),
      put:    shallowCopy(CONTENT_TYPE_APPLICATION_JSON),
      patch:  shallowCopy(CONTENT_TYPE_APPLICATION_JSON)
    },

    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',

    paramSerializer: '$httpParamSerializer',

    jsonpCallbackParam: 'callback'
  };

  var useApplyAsync = false;
  /**
   * @ngdoc method
   * @name $httpProvider#useApplyAsync
   * @description
   *
   * Configure $http service to combine processing of multiple http responses received at around
   * the same time via {@link ng.$rootScope.Scope#$applyAsync $rootScope.$applyAsync}. This can result in
   * significant performance improvement for bigger applications that make many HTTP requests
   * concurrently (common during application bootstrap).
   *
   * Defaults to false. If no value is specified, returns the current configured value.
   *
   * @param {boolean=} value If true, when requests are loaded, they will schedule a deferred
   *    "apply" on the next tick, giving time for subsequent requests in a roughly ~10ms window
   *    to load and share the same digest cycle.
   *
   * @returns {boolean|Object} If a value is specified, returns the $httpProvider for chaining.
   *    otherwise, returns the current configured value.
   */
  this.useApplyAsync = function(value) {
    if (isDefined(value)) {
      useApplyAsync = !!value;
      return this;
    }
    return useApplyAsync;
  };

  /**
   * @ngdoc property
   * @name $httpProvider#interceptors
   * @description
   *
   * Array containing service factories for all synchronous or asynchronous {@link ng.$http $http}
   * pre-processing of request or postprocessing of responses.
   *
   * These service factories are ordered by request, i.e. they are applied in the same order as the
   * array, on request, but reverse order, on response.
   *
   * {@link ng.$http#interceptors Interceptors detailed info}
   */
  var interceptorFactories = this.interceptors = [];

  /**
   * @ngdoc property
   * @name $httpProvider#xsrfTrustedOrigins
   * @description
   *
   * Array containing URLs whose origins are trusted to receive the XSRF token. See the
   * {@link ng.$http#security-considerations Security Considerations} sections for more details on
   * XSRF.
   *
   * **Note:** An "origin" consists of the [URI scheme](https://en.wikipedia.org/wiki/URI_scheme),
   * the [hostname](https://en.wikipedia.org/wiki/Hostname) and the
   * [port number](https://en.wikipedia.org/wiki/Port_(computer_networking). For `http:` and
   * `https:`, the port number can be omitted if using th default ports (80 and 443 respectively).
   * Examples: `http://example.com`, `https://api.example.com:9876`
   *
   * <div class="alert alert-warning">
   *   It is not possible to trust specific URLs/paths. The `path`, `query` and `fragment` parts
   *   of a URL will be ignored. For example, `https://foo.com/path/bar?query=baz#fragment` will be
   *   treated as `https://foo.com`, meaning that **all** requests to URLs starting with
   *   `https://foo.com/` will include the XSRF token.
   * </div>
   *
   * @example
   *
   * ```js
   * // App served from `https://example.com/`.
   * angular.
   *   module('xsrfTrustedOriginsExample', []).
   *   config(['$httpProvider', function($httpProvider) {
   *     $httpProvider.xsrfTrustedOrigins.push('https://api.example.com');
   *   }]).
   *   run(['$http', function($http) {
   *     // The XSRF token will be sent.
   *     $http.get('https://api.example.com/preferences').then(...);
   *
   *     // The XSRF token will NOT be sent.
   *     $http.get('https://stats.example.com/activity').then(...);
   *   }]);
   * ```
   */
  var xsrfTrustedOrigins = this.xsrfTrustedOrigins = [];

  /**
   * @ngdoc property
   * @name $httpProvider#xsrfWhitelistedOrigins
   * @description
   *
   * @deprecated
   * sinceVersion="1.8.1"
   *
   * This property is deprecated. Use {@link $httpProvider#xsrfTrustedOrigins xsrfTrustedOrigins}
   * instead.
   */
  Object.defineProperty(this, 'xsrfWhitelistedOrigins', {
    get: function() {
      return this.xsrfTrustedOrigins;
    },
    set: function(origins) {
      this.xsrfTrustedOrigins = origins;
    }
  });

  this.$get = ['$browser', '$httpBackend', '$$cookieReader', '$cacheFactory', '$rootScope', '$q', '$injector', '$sce',
      function($browser, $httpBackend, $$cookieReader, $cacheFactory, $rootScope, $q, $injector, $sce) {

    var defaultCache = $cacheFactory('$http');

    /**
     * Make sure that default param serializer is exposed as a function
     */
    defaults.paramSerializer = isString(defaults.paramSerializer) ?
      $injector.get(defaults.paramSerializer) : defaults.paramSerializer;

    /**
     * Interceptors stored in reverse order. Inner interceptors before outer interceptors.
     * The reversal is needed so that we can build up the interception chain around the
     * server request.
     */
    var reversedInterceptors = [];

    forEach(interceptorFactories, function(interceptorFactory) {
      reversedInterceptors.unshift(isString(interceptorFactory)
          ? $injector.get(interceptorFactory) : $injector.invoke(interceptorFactory));
    });

    /**
     * A function to check request URLs against a list of allowed origins.
     */
    var urlIsAllowedOrigin = urlIsAllowedOriginFactory(xsrfTrustedOrigins);

    /**
     * @ngdoc service
     * @kind function
     * @name $http
     * @requires ng.$httpBackend
     * @requires $cacheFactory
     * @requires $rootScope
     * @requires $q
     * @requires $injector
     *
     * @description
     * The `$http` service is a core AngularJS service that facilitates communication with the remote
     * HTTP servers via the browser's [XMLHttpRequest](https://developer.mozilla.org/en/xmlhttprequest)
     * object or via [JSONP](http://en.wikipedia.org/wiki/JSONP).
     *
     * For unit testing applications that use `$http` service, see
     * {@link ngMock.$httpBackend $httpBackend mock}.
     *
     * For a higher level of abstraction, please check out the {@link ngResource.$resource
     * $resource} service.
     *
     * The $http API is based on the {@link ng.$q deferred/promise APIs} exposed by
     * the $q service. While for simple usage patterns this doesn't matter much, for advanced usage
     * it is important to familiarize yourself with these APIs and the guarantees they provide.
     *
     *
     * ## General usage
     * The `$http` service is a function which takes a single argument — a {@link $http#usage configuration object} —
     * that is used to generate an HTTP request and returns  a {@link ng.$q promise} that is
     * resolved (request success) or rejected (request failure) with a
     * {@link ng.$http#$http-returns response} object.
     *
     * ```js
     *   // Simple GET request example:
     *   $http({
     *     method: 'GET',
     *     url: '/someUrl'
     *   }).then(function successCallback(response) {
     *       // this callback will be called asynchronously
     *       // when the response is available
     *     }, function errorCallback(response) {
     *       // called asynchronously if an error occurs
     *       // or server returns response with an error status.
     *     });
     * ```
     *
     *
     * ## Shortcut methods
     *
     * Shortcut methods are also available. All shortcut methods require passing in the URL, and
     * request data must be passed in for POST/PUT requests. An optional config can be passed as the
     * last argument.
     *
     * ```js
     *   $http.get('/someUrl', config).then(successCallback, errorCallback);
     *   $http.post('/someUrl', data, config).then(successCallback, errorCallback);
     * ```
     *
     * Complete list of shortcut methods:
     *
     * - {@link ng.$http#get $http.get}
     * - {@link ng.$http#head $http.head}
     * - {@link ng.$http#post $http.post}
     * - {@link ng.$http#put $http.put}
     * - {@link ng.$http#delete $http.delete}
     * - {@link ng.$http#jsonp $http.jsonp}
     * - {@link ng.$http#patch $http.patch}
     *
     *
     * ## Writing Unit Tests that use $http
     * When unit testing (using {@link ngMock ngMock}), it is necessary to call
     * {@link ngMock.$httpBackend#flush $httpBackend.flush()} to flush each pending
     * request using trained responses.
     *
     * ```
     * $httpBackend.expectGET(...);
     * $http.get(...);
     * $httpBackend.flush();
     * ```
     *
     * ## Setting HTTP Headers
     *
     * The $http service will automatically add certain HTTP headers to all requests. These defaults
     * can be fully configured by accessing the `$httpProvider.defaults.headers` configuration
     * object, which currently contains this default configuration:
     *
     * - `$httpProvider.defaults.headers.common` (headers that are common for all requests):
     *   - <code>Accept: application/json, text/plain, \*&#65279;/&#65279;\*</code>
     * - `$httpProvider.defaults.headers.post`: (header defaults for POST requests)
     *   - `Content-Type: application/json`
     * - `$httpProvider.defaults.headers.put` (header defaults for PUT requests)
     *   - `Content-Type: application/json`
     *
     * To add or overwrite these defaults, simply add or remove a property from these configuration
     * objects. To add headers for an HTTP method other than POST or PUT, simply add a new object
     * with the lowercased HTTP method name as the key, e.g.
     * `$httpProvider.defaults.headers.get = { 'My-Header' : 'value' }`.
     *
     * The defaults can also be set at runtime via the `$http.defaults` object in the same
     * fashion. For example:
     *
     * ```
     * module.run(function($http) {
     *   $http.defaults.headers.common.Authorization = 'Basic YmVlcDpib29w';
     * });
     * ```
     *
     * In addition, you can supply a `headers` property in the config object passed when
     * calling `$http(config)`, which overrides the defaults without changing them globally.
     *
     * To explicitly remove a header automatically added via $httpProvider.defaults.headers on a per request basis,
     * Use the `headers` property, setting the desired header to `undefined`. For example:
     *
     * ```js
     * var req = {
     *  method: 'POST',
     *  url: 'http://example.com',
     *  headers: {
     *    'Content-Type': undefined
     *  },
     *  data: { test: 'test' }
     * }
     *
     * $http(req).then(function(){...}, function(){...});
     * ```
     *
     * ## Transforming Requests and Responses
     *
     * Both requests and responses can be transformed using transformation functions: `transformRequest`
     * and `transformResponse`. These properties can be a single function that returns
     * the transformed value (`function(data, headersGetter, status)`) or an array of such transformation functions,
     * which allows you to `push` or `unshift` a new transformation function into the transformation chain.
     *
     * <div class="alert alert-warning">
     * **Note:** AngularJS does not make a copy of the `data` parameter before it is passed into the `transformRequest` pipeline.
     * That means changes to the properties of `data` are not local to the transform function (since Javascript passes objects by reference).
     * For example, when calling `$http.get(url, $scope.myObject)`, modifications to the object's properties in a transformRequest
     * function will be reflected on the scope and in any templates where the object is data-bound.
     * To prevent this, transform functions should have no side-effects.
     * If you need to modify properties, it is recommended to make a copy of the data, or create new object to return.
     * </div>
     *
     * ### Default Transformations
     *
     * The `$httpProvider` provider and `$http` service expose `defaults.transformRequest` and
     * `defaults.transformResponse` properties. If a request does not provide its own transformations
     * then these will be applied.
     *
     * You can augment or replace the default transformations by modifying these properties by adding to or
     * replacing the array.
     *
     * AngularJS provides the following default transformations:
     *
     * Request transformations (`$httpProvider.defaults.transformRequest` and `$http.defaults.transformRequest`) is
     * an array with one function that does the following:
     *
     * - If the `data` property of the request configuration object contains an object, serialize it
     *   into JSON format.
     *
     * Response transformations (`$httpProvider.defaults.transformResponse` and `$http.defaults.transformResponse`) is
     * an array with one function that does the following:
     *
     *  - If XSRF prefix is detected, strip it (see Security Considerations section below).
     *  - If the `Content-Type` is `application/json` or the response looks like JSON,
   *      deserialize it using a JSON parser.
     *
     *
     * ### Overriding the Default Transformations Per Request
     *
     * If you wish to override the request/response transformations only for a single request then provide
     * `transformRequest` and/or `transformResponse` properties on the configuration object passed
     * into `$http`.
     *
     * Note that if you provide these properties on the config object the default transformations will be
     * overwritten. If you wish to augment the default transformations then you must include them in your
     * local transformation array.
     *
     * The following code demonstrates adding a new response transformation to be run after the default response
     * transformations have been run.
     *
     * ```js
     * function appendTransform(defaults, transform) {
     *
     *   // We can't guarantee that the default transformation is an array
     *   defaults = angular.isArray(defaults) ? defaults : [defaults];
     *
     *   // Append the new transformation to the defaults
     *   return defaults.concat(transform);
     * }
     *
     * $http({
     *   url: '...',
     *   method: 'GET',
     *   transformResponse: appendTransform($http.defaults.transformResponse, function(value) {
     *     return doTransform(value);
     *   })
     * });
     * ```
     *
     *
     * ## Caching
     *
     * {@link ng.$http `$http`} responses are not cached by default. To enable caching, you must
     * set the config.cache value or the default cache value to TRUE or to a cache object (created
     * with {@link ng.$cacheFactory `$cacheFactory`}). If defined, the value of config.cache takes
     * precedence over the default cache value.
     *
     * In order to:
     *   * cache all responses - set the default cache value to TRUE or to a cache object
     *   * cache a specific response - set config.cache value to TRUE or to a cache object
     *
     * If caching is enabled, but neither the default cache nor config.cache are set to a cache object,
     * then the default `$cacheFactory("$http")` object is used.
     *
     * The default cache value can be set by updating the
     * {@link ng.$http#defaults `$http.defaults.cache`} property or the
     * {@link $httpProvider#defaults `$httpProvider.defaults.cache`} property.
     *
     * When caching is enabled, {@link ng.$http `$http`} stores the response from the server using
     * the relevant cache object. The next time the same request is made, the response is returned
     * from the cache without sending a request to the server.
     *
     * Take note that:
     *
     *   * Only GET and JSONP requests are cached.
     *   * The cache key is the request URL including search parameters; headers are not considered.
     *   * Cached responses are returned asynchronously, in the same way as responses from the server.
     *   * If multiple identical requests are made using the same cache, which is not yet populated,
     *     one request will be made to the server and remaining requests will return the same response.
     *   * A cache-control header on the response does not affect if or how responses are cached.
     *
     *
     * ## Interceptors
     *
     * Before you start creating interceptors, be sure to understand the
     * {@link ng.$q $q and deferred/promise APIs}.
     *
     * For purposes of global error handling, authentication, or any kind of synchronous or
     * asynchronous pre-processing of request or postprocessing of responses, it is desirable to be
     * able to intercept requests before they are handed to the server and
     * responses before they are handed over to the application code that
     * initiated these requests. The interceptors leverage the {@link ng.$q
     * promise APIs} to fulfill this need for both synchronous and asynchronous pre-processing.
     *
     * The interceptors are service factories that are registered with the `$httpProvider` by
     * adding them to the `$httpProvider.interceptors` array. The factory is called and
     * injected with dependencies (if specified) and returns the interceptor.
     *
     * There are two kinds of interceptors (and two kinds of rejection interceptors):
     *
     *   * `request`: interceptors get called with a http {@link $http#usage config} object. The function is free to
     *     modify the `config` object or create a new one. The function needs to return the `config`
     *     object directly, or a promise containing the `config` or a new `config` object.
     *   * `requestError`: interceptor gets called when a previous interceptor threw an error or
     *     resolved with a rejection.
     *   * `response`: interceptors get called with http `response` object. The function is free to
     *     modify the `response` object or create a new one. The function needs to return the `response`
     *     object directly, or as a promise containing the `response` or a new `response` object.
     *   * `responseError`: interceptor gets called when a previous interceptor threw an error or
     *     resolved with a rejection.
     *
     *
     * ```js
     *   // register the interceptor as a service
     *   $provide.factory('myHttpInterceptor', function($q, dependency1, dependency2) {
     *     return {
     *       // optional method
     *       'request': function(config) {
     *         // do something on success
     *         return config;
     *       },
     *
     *       // optional method
     *      'requestError': function(rejection) {
     *         // do something on error
     *         if (canRecover(rejection)) {
     *           return responseOrNewPromise
     *         }
     *         return $q.reject(rejection);
     *       },
     *
     *
     *
     *       // optional method
     *       'response': function(response) {
     *         // do something on success
     *         return response;
     *       },
     *
     *       // optional method
     *      'responseError': function(rejection) {
     *         // do something on error
     *         if (canRecover(rejection)) {
     *           return responseOrNewPromise
     *         }
     *         return $q.reject(rejection);
     *       }
     *     };
     *   });
     *
     *   $httpProvider.interceptors.push('myHttpInterceptor');
     *
     *
     *   // alternatively, register the interceptor via an anonymous factory
     *   $httpProvider.interceptors.push(function($q, dependency1, dependency2) {
     *     return {
     *      'request': function(config) {
     *          // same as above
     *       },
     *
     *       'response': function(response) {
     *          // same as above
     *       }
     *     };
     *   });
     * ```
     *
     * ## Security Considerations
     *
     * When designing web applications, consider security threats from:
     *
     * - [JSON vulnerability](http://haacked.com/archive/2008/11/20/anatomy-of-a-subtle-json-vulnerability.aspx)
     * - [XSRF](http://en.wikipedia.org/wiki/Cross-site_request_forgery)
     *
     * Both server and the client must cooperate in order to eliminate these threats. AngularJS comes
     * pre-configured with strategies that address these issues, but for this to work backend server
     * cooperation is required.
     *
     * ### JSON Vulnerability Protection
     *
     * A [JSON vulnerability](http://haacked.com/archive/2008/11/20/anatomy-of-a-subtle-json-vulnerability.aspx)
     * allows third party website to turn your JSON resource URL into
     * [JSONP](http://en.wikipedia.org/wiki/JSONP) request under some conditions. To
     * counter this your server can prefix all JSON requests with following string `")]}',\n"`.
     * AngularJS will automatically strip the prefix before processing it as JSON.
     *
     * For example if your server needs to return:
     * ```js
     * ['one','two']
     * ```
     *
     * which is vulnerable to attack, your server can return:
     * ```js
     * )]}',
     * ['one','two']
     * ```
     *
     * AngularJS will strip the prefix, before processing the JSON.
     *
     *
     * ### Cross Site Request Forgery (XSRF) Protection
     *
     * [XSRF](http://en.wikipedia.org/wiki/Cross-site_request_forgery) is an attack technique by
     * which the attacker can trick an authenticated user into unknowingly executing actions on your
     * website. AngularJS provides a mechanism to counter XSRF. When performing XHR requests, the
     * $http service reads a token from a cookie (by default, `XSRF-TOKEN`) and sets it as an HTTP
     * header (by default `X-XSRF-TOKEN`). Since only JavaScript that runs on your domain could read
     * the cookie, your server can be assured that the XHR came from JavaScript running on your
     * domain.
     *
     * To take advantage of this, your server needs to set a token in a JavaScript readable session
     * cookie called `XSRF-TOKEN` on the first HTTP GET request. On subsequent XHR requests the
     * server can verify that the cookie matches the `X-XSRF-TOKEN` HTTP header, and therefore be
     * sure that only JavaScript running on your domain could have sent the request. The token must
     * be unique for each user and must be verifiable by the server (to prevent the JavaScript from
     * making up its own tokens). We recommend that the token is a digest of your site's
     * authentication cookie with a [salt](https://en.wikipedia.org/wiki/Salt_(cryptography&#41;)
     * for added security.
     *
     * The header will &mdash; by default &mdash; **not** be set for cross-domain requests. This
     * prevents unauthorized servers (e.g. malicious or compromised 3rd-party APIs) from gaining
     * access to your users' XSRF tokens and exposing them to Cross Site Request Forgery. If you
     * want to, you can trust additional origins to also receive the XSRF token, by adding them
     * to {@link ng.$httpProvider#xsrfTrustedOrigins xsrfTrustedOrigins}. This might be
     * useful, for example, if your application, served from `example.com`, needs to access your API
     * at `api.example.com`.
     * See {@link ng.$httpProvider#xsrfTrustedOrigins $httpProvider.xsrfTrustedOrigins} for
     * more details.
     *
     * <div class="alert alert-danger">
     *   **Warning**<br />
     *   Only trusted origins that you have control over and make sure you understand the
     *   implications of doing so.
     * </div>
     *
     * The name of the cookie and the header can be specified using the `xsrfCookieName` and
     * `xsrfHeaderName` properties of either `$httpProvider.defaults` at config-time,
     * `$http.defaults` at run-time, or the per-request config object.
     *
     * In order to prevent collisions in environments where multiple AngularJS apps share the
     * same domain or subdomain, we recommend that each application uses a unique cookie name.
     *
     *
     * @param {object} config Object describing the request to be made and how it should be
     *    processed. The object has following properties:
     *
     *    - **method** – `{string}` – HTTP method (e.g. 'GET', 'POST', etc)
     *    - **url** – `{string|TrustedObject}` – Absolute or relative URL of the resource that is being requested;
     *      or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     *    - **params** – `{Object.<string|Object>}` – Map of strings or objects which will be serialized
     *      with the `paramSerializer` and appended as GET parameters.
     *    - **data** – `{string|Object}` – Data to be sent as the request message data.
     *    - **headers** – `{Object}` – Map of strings or functions which return strings representing
     *      HTTP headers to send to the server. If the return value of a function is null, the
     *      header will not be sent. Functions accept a config object as an argument.
     *    - **eventHandlers** - `{Object}` - Event listeners to be bound to the XMLHttpRequest object.
     *      To bind events to the XMLHttpRequest upload object, use `uploadEventHandlers`.
     *      The handler will be called in the context of a `$apply` block.
     *    - **uploadEventHandlers** - `{Object}` - Event listeners to be bound to the XMLHttpRequest upload
     *      object. To bind events to the XMLHttpRequest object, use `eventHandlers`.
     *      The handler will be called in the context of a `$apply` block.
     *    - **xsrfHeaderName** – `{string}` – Name of HTTP header to populate with the XSRF token.
     *    - **xsrfCookieName** – `{string}` – Name of cookie containing the XSRF token.
     *    - **transformRequest** –
     *      `{function(data, headersGetter)|Array.<function(data, headersGetter)>}` –
     *      transform function or an array of such functions. The transform function takes the http
     *      request body and headers and returns its transformed (typically serialized) version.
     *      See {@link ng.$http#overriding-the-default-transformations-per-request
     *      Overriding the Default Transformations}
     *    - **transformResponse** –
     *      `{function(data, headersGetter, status)|Array.<function(data, headersGetter, status)>}` –
     *      transform function or an array of such functions. The transform function takes the http
     *      response body, headers and status and returns its transformed (typically deserialized) version.
     *      See {@link ng.$http#overriding-the-default-transformations-per-request
     *      Overriding the Default Transformations}
     *    - **paramSerializer** - `{string|function(Object<string,string>):string}` - A function used to
     *      prepare the string representation of request parameters (specified as an object).
     *      If specified as string, it is interpreted as function registered with the
     *      {@link $injector $injector}, which means you can create your own serializer
     *      by registering it as a {@link auto.$provide#service service}.
     *      The default serializer is the {@link $httpParamSerializer $httpParamSerializer};
     *      alternatively, you can use the {@link $httpParamSerializerJQLike $httpParamSerializerJQLike}
     *    - **cache** – `{boolean|Object}` – A boolean value or object created with
     *      {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of the HTTP response.
     *      See {@link $http#caching $http Caching} for more information.
     *    - **timeout** – `{number|Promise}` – timeout in milliseconds, or {@link ng.$q promise}
     *      that should abort the request when resolved.
     *
     *      A numerical timeout or a promise returned from {@link ng.$timeout $timeout}, will set
     *      the `xhrStatus` in the {@link $http#$http-returns response} to "timeout", and any other
     *      resolved promise will set it to "abort", following standard XMLHttpRequest behavior.
     *
     *    - **withCredentials** - `{boolean}` - whether to set the `withCredentials` flag on the
     *      XHR object. See [requests with credentials](https://developer.mozilla.org/docs/Web/HTTP/Access_control_CORS#Requests_with_credentials)
     *      for more information.
     *    - **responseType** - `{string}` - see
     *      [XMLHttpRequest.responseType](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest#xmlhttprequest-responsetype).
     *
     * @returns {HttpPromise} A {@link ng.$q `Promise}` that will be resolved (request success)
     *   or rejected (request failure) with a response object.
     *
     *   The response object has these properties:
     *
     *   - **data** – `{string|Object}` – The response body transformed with
     *     the transform functions.
     *   - **status** – `{number}` – HTTP status code of the response.
     *   - **headers** – `{function([headerName])}` – Header getter function.
     *   - **config** – `{Object}` – The configuration object that was used
     *     to generate the request.
     *   - **statusText** – `{string}` – HTTP status text of the response.
     *   - **xhrStatus** – `{string}` – Status of the XMLHttpRequest
     *     (`complete`, `error`, `timeout` or `abort`).
     *
     *
     *   A response status code between 200 and 299 is considered a success status
     *   and will result in the success callback being called. Any response status
     *   code outside of that range is considered an error status and will result
     *   in the error callback being called.
     *   Also, status codes less than -1 are normalized to zero. -1 usually means
     *   the request was aborted, e.g. using a `config.timeout`. More information
     *   about the status might be available in the `xhrStatus` property.
     *
     *   Note that if the response is a redirect, XMLHttpRequest will transparently
     *   follow it, meaning that the outcome (success or error) will be determined
     *   by the final response status code.
     *
     *
     * @property {Array.<Object>} pendingRequests Array of config objects for currently pending
     *   requests. This is primarily meant to be used for debugging purposes.
     *
     *
     * @example
<example module="httpExample" name="http-service">
<file name="index.html">
  <div ng-controller="FetchController">
    <select ng-model="method" aria-label="Request method">
      <option>GET</option>
      <option>JSONP</option>
    </select>
    <input type="text" ng-model="url" size="80" aria-label="URL" />
    <button id="fetchbtn" ng-click="fetch()">fetch</button><br>
    <button id="samplegetbtn" ng-click="updateModel('GET', 'http-hello.html')">Sample GET</button>
    <button id="samplejsonpbtn"
      ng-click="updateModel('JSONP',
                    'https://angularjs.org/greet.php?name=Super%20Hero')">
      Sample JSONP
    </button>
    <button id="invalidjsonpbtn"
      ng-click="updateModel('JSONP', 'https://angularjs.org/doesntexist')">
        Invalid JSONP
      </button>
    <pre>http status code: {{status}}</pre>
    <pre>http response data: {{data}}</pre>
  </div>
</file>
<file name="script.js">
  angular.module('httpExample', [])
    .config(['$sceDelegateProvider', function($sceDelegateProvider) {
      // We must add the JSONP endpoint that we are using to the trusted list to show that we trust it
      $sceDelegateProvider.trustedResourceUrlList([
        'self',
        'https://angularjs.org/**'
      ]);
    }])
    .controller('FetchController', ['$scope', '$http', '$templateCache',
      function($scope, $http, $templateCache) {
        $scope.method = 'GET';
        $scope.url = 'http-hello.html';

        $scope.fetch = function() {
          $scope.code = null;
          $scope.response = null;

          $http({method: $scope.method, url: $scope.url, cache: $templateCache}).
            then(function(response) {
              $scope.status = response.status;
              $scope.data = response.data;
            }, function(response) {
              $scope.data = response.data || 'Request failed';
              $scope.status = response.status;
          });
        };

        $scope.updateModel = function(method, url) {
          $scope.method = method;
          $scope.url = url;
        };
      }]);
</file>
<file name="http-hello.html">
  Hello, $http!
</file>
<file name="protractor.js" type="protractor">
  var status = element(by.binding('status'));
  var data = element(by.binding('data'));
  var fetchBtn = element(by.id('fetchbtn'));
  var sampleGetBtn = element(by.id('samplegetbtn'));
  var invalidJsonpBtn = element(by.id('invalidjsonpbtn'));

  it('should make an xhr GET request', function() {
    sampleGetBtn.click();
    fetchBtn.click();
    expect(status.getText()).toMatch('200');
    expect(data.getText()).toMatch(/Hello, \$http!/);
  });

// Commented out due to flakes. See https://github.com/angular/angular.js/issues/9185
// it('should make a JSONP request to angularjs.org', function() {
//   var sampleJsonpBtn = element(by.id('samplejsonpbtn'));
//   sampleJsonpBtn.click();
//   fetchBtn.click();
//   expect(status.getText()).toMatch('200');
//   expect(data.getText()).toMatch(/Super Hero!/);
// });

  it('should make JSONP request to invalid URL and invoke the error handler',
      function() {
    invalidJsonpBtn.click();
    fetchBtn.click();
    expect(status.getText()).toMatch('0');
    expect(data.getText()).toMatch('Request failed');
  });
</file>
</example>
     */
    function $http(requestConfig) {

      if (!isObject(requestConfig)) {
        throw minErr('$http')('badreq', 'Http request configuration must be an object.  Received: {0}', requestConfig);
      }

      if (!isString($sce.valueOf(requestConfig.url))) {
        throw minErr('$http')('badreq', 'Http request configuration url must be a string or a $sce trusted object.  Received: {0}', requestConfig.url);
      }

      var config = extend({
        method: 'get',
        transformRequest: defaults.transformRequest,
        transformResponse: defaults.transformResponse,
        paramSerializer: defaults.paramSerializer,
        jsonpCallbackParam: defaults.jsonpCallbackParam
      }, requestConfig);

      config.headers = mergeHeaders(requestConfig);
      config.method = uppercase(config.method);
      config.paramSerializer = isString(config.paramSerializer) ?
          $injector.get(config.paramSerializer) : config.paramSerializer;

      $browser.$$incOutstandingRequestCount('$http');

      var requestInterceptors = [];
      var responseInterceptors = [];
      var promise = $q.resolve(config);

      // apply interceptors
      forEach(reversedInterceptors, function(interceptor) {
        if (interceptor.request || interceptor.requestError) {
          requestInterceptors.unshift(interceptor.request, interceptor.requestError);
        }
        if (interceptor.response || interceptor.responseError) {
          responseInterceptors.push(interceptor.response, interceptor.responseError);
        }
      });

      promise = chainInterceptors(promise, requestInterceptors);
      promise = promise.then(serverRequest);
      promise = chainInterceptors(promise, responseInterceptors);
      promise = promise.finally(completeOutstandingRequest);

      return promise;


      function chainInterceptors(promise, interceptors) {
        for (var i = 0, ii = interceptors.length; i < ii;) {
          var thenFn = interceptors[i++];
          var rejectFn = interceptors[i++];

          promise = promise.then(thenFn, rejectFn);
        }

        interceptors.length = 0;

        return promise;
      }

      function completeOutstandingRequest() {
        $browser.$$completeOutstandingRequest(noop, '$http');
      }

      function executeHeaderFns(headers, config) {
        var headerContent, processedHeaders = {};

        forEach(headers, function(headerFn, header) {
          if (isFunction(headerFn)) {
            headerContent = headerFn(config);
            if (headerContent != null) {
              processedHeaders[header] = headerContent;
            }
          } else {
            processedHeaders[header] = headerFn;
          }
        });

        return processedHeaders;
      }

      function mergeHeaders(config) {
        var defHeaders = defaults.headers,
            reqHeaders = extend({}, config.headers),
            defHeaderName, lowercaseDefHeaderName, reqHeaderName;

        defHeaders = extend({}, defHeaders.common, defHeaders[lowercase(config.method)]);

        // using for-in instead of forEach to avoid unnecessary iteration after header has been found
        defaultHeadersIteration:
        for (defHeaderName in defHeaders) {
          lowercaseDefHeaderName = lowercase(defHeaderName);

          for (reqHeaderName in reqHeaders) {
            if (lowercase(reqHeaderName) === lowercaseDefHeaderName) {
              continue defaultHeadersIteration;
            }
          }

          reqHeaders[defHeaderName] = defHeaders[defHeaderName];
        }

        // execute if header value is a function for merged headers
        return executeHeaderFns(reqHeaders, shallowCopy(config));
      }

      function serverRequest(config) {
        var headers = config.headers;
        var reqData = transformData(config.data, headersGetter(headers), undefined, config.transformRequest);

        // strip content-type if data is undefined
        if (isUndefined(reqData)) {
          forEach(headers, function(value, header) {
            if (lowercase(header) === 'content-type') {
              delete headers[header];
            }
          });
        }

        if (isUndefined(config.withCredentials) && !isUndefined(defaults.withCredentials)) {
          config.withCredentials = defaults.withCredentials;
        }

        // send request
        return sendReq(config, reqData).then(transformResponse, transformResponse);
      }

      function transformResponse(response) {
        // make a copy since the response must be cacheable
        var resp = extend({}, response);
        resp.data = transformData(response.data, response.headers, response.status,
                                  config.transformResponse);
        return (isSuccess(response.status))
          ? resp
          : $q.reject(resp);
      }
    }

    $http.pendingRequests = [];

    /**
     * @ngdoc method
     * @name $http#get
     *
     * @description
     * Shortcut method to perform `GET` request.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
     * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
     * See {@link ng.$http#$http-returns `$http()` return value}.
     */

    /**
     * @ngdoc method
     * @name $http#delete
     *
     * @description
     * Shortcut method to perform `DELETE` request.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
     * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
     * See {@link ng.$http#$http-returns `$http()` return value}.
     */

    /**
     * @ngdoc method
     * @name $http#head
     *
     * @description
     * Shortcut method to perform `HEAD` request.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
     * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
     * See {@link ng.$http#$http-returns `$http()` return value}.
     */

    /**
     * @ngdoc method
     * @name $http#jsonp
     *
     * @description
     * Shortcut method to perform `JSONP` request.
     *
     * Note that, since JSONP requests are sensitive because the response is given full access to the browser,
     * the url must be declared, via {@link $sce} as a trusted resource URL.
     * You can trust a URL by adding it to the trusted resource URL list via
     * {@link $sceDelegateProvider#trustedResourceUrlList  `$sceDelegateProvider.trustedResourceUrlList`} or
     * by explicitly trusting the URL via {@link $sce#trustAsResourceUrl `$sce.trustAsResourceUrl(url)`}.
     *
     * You should avoid generating the URL for the JSONP request from user provided data.
     * Provide additional query parameters via `params` property of the `config` parameter, rather than
     * modifying the URL itself.
     *
     * JSONP requests must specify a callback to be used in the response from the server. This callback
     * is passed as a query parameter in the request. You must specify the name of this parameter by
     * setting the `jsonpCallbackParam` property on the request config object.
     *
     * ```
     * $http.jsonp('some/trusted/url', {jsonpCallbackParam: 'callback'})
     * ```
     *
     * You can also specify a default callback parameter name in `$http.defaults.jsonpCallbackParam`.
     * Initially this is set to `'callback'`.
     *
     * <div class="alert alert-danger">
     * You can no longer use the `JSON_CALLBACK` string as a placeholder for specifying where the callback
     * parameter value should go.
     * </div>
     *
     * If you would like to customise where and how the callbacks are stored then try overriding
     * or decorating the {@link $jsonpCallbacks} service.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
     * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
     * See {@link ng.$http#$http-returns `$http()` return value}.
     */
    createShortMethods('get', 'delete', 'head', 'jsonp');

    /**
     * @ngdoc method
     * @name $http#post
     *
     * @description
     * Shortcut method to perform `POST` request.
     *
     * @param {string} url Relative or absolute URL specifying the destination of the request
     * @param {*} data Request content
     * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
     * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
     * See {@link ng.$http#$http-returns `$http()` return value}.
     */

    /**
     * @ngdoc method
     * @name $http#put
     *
     * @description
     * Shortcut method to perform `PUT` request.
     *
     * @param {string} url Relative or absolute URL specifying the destination of the request
     * @param {*} data Request content
     * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
     * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
     * See {@link ng.$http#$http-returns `$http()` return value}.
     */

     /**
      * @ngdoc method
      * @name $http#patch
      *
      * @description
      * Shortcut method to perform `PATCH` request.
      *
      * @param {string} url Relative or absolute URL specifying the destination of the request
      * @param {*} data Request content
      * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
      * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
      * See {@link ng.$http#$http-returns `$http()` return value}.
      */
    createShortMethodsWithData('post', 'put', 'patch');

        /**
         * @ngdoc property
         * @name $http#defaults
         *
         * @description
         * Runtime equivalent of the `$httpProvider.defaults` property. Allows configuration of
         * default headers, withCredentials as well as request and response transformations.
         *
         * See "Setting HTTP Headers" and "Transforming Requests and Responses" sections above.
         */
    $http.defaults = defaults;


    return $http;


    function createShortMethods(names) {
      forEach(arguments, function(name) {
        $http[name] = function(url, config) {
          return $http(extend({}, config || {}, {
            method: name,
            url: url
          }));
        };
      });
    }


    function createShortMethodsWithData(name) {
      forEach(arguments, function(name) {
        $http[name] = function(url, data, config) {
          return $http(extend({}, config || {}, {
            method: name,
            url: url,
            data: data
          }));
        };
      });
    }


    /**
     * Makes the request.
     *
     * !!! ACCESSES CLOSURE VARS:
     * $httpBackend, defaults, $log, $rootScope, defaultCache, $http.pendingRequests
     */
    function sendReq(config, reqData) {
      var deferred = $q.defer(),
          promise = deferred.promise,
          cache,
          cachedResp,
          reqHeaders = config.headers,
          isJsonp = lowercase(config.method) === 'jsonp',
          url = config.url;

      if (isJsonp) {
        // JSONP is a pretty sensitive operation where we're allowing a script to have full access to
        // our DOM and JS space.  So we require that the URL satisfies SCE.RESOURCE_URL.
        url = $sce.getTrustedResourceUrl(url);
      } else if (!isString(url)) {
        // If it is not a string then the URL must be a $sce trusted object
        url = $sce.valueOf(url);
      }

      url = buildUrl(url, config.paramSerializer(config.params));

      if (isJsonp) {
        // Check the url and add the JSONP callback placeholder
        url = sanitizeJsonpCallbackParam(url, config.jsonpCallbackParam);
      }

      $http.pendingRequests.push(config);
      promise.then(removePendingReq, removePendingReq);

      if ((config.cache || defaults.cache) && config.cache !== false &&
          (config.method === 'GET' || config.method === 'JSONP')) {
        cache = isObject(config.cache) ? config.cache
            : isObject(/** @type {?} */ (defaults).cache)
              ? /** @type {?} */ (defaults).cache
              : defaultCache;
      }

      if (cache) {
        cachedResp = cache.get(url);
        if (isDefined(cachedResp)) {
          if (isPromiseLike(cachedResp)) {
            // cached request has already been sent, but there is no response yet
            cachedResp.then(resolvePromiseWithResult, resolvePromiseWithResult);
          } else {
            // serving from cache
            if (isArray(cachedResp)) {
              resolvePromise(cachedResp[1], cachedResp[0], shallowCopy(cachedResp[2]), cachedResp[3], cachedResp[4]);
            } else {
              resolvePromise(cachedResp, 200, {}, 'OK', 'complete');
            }
          }
        } else {
          // put the promise for the non-transformed response into cache as a placeholder
          cache.put(url, promise);
        }
      }


      // if we won't have the response in cache, set the xsrf headers and
      // send the request to the backend
      if (isUndefined(cachedResp)) {
        var xsrfValue = urlIsAllowedOrigin(config.url)
            ? $$cookieReader()[config.xsrfCookieName || defaults.xsrfCookieName]
            : undefined;
        if (xsrfValue) {
          reqHeaders[(config.xsrfHeaderName || defaults.xsrfHeaderName)] = xsrfValue;
        }

        $httpBackend(config.method, url, reqData, done, reqHeaders, config.timeout,
            config.withCredentials, config.responseType,
            createApplyHandlers(config.eventHandlers),
            createApplyHandlers(config.uploadEventHandlers));
      }

      return promise;

      function createApplyHandlers(eventHandlers) {
        if (eventHandlers) {
          var applyHandlers = {};
          forEach(eventHandlers, function(eventHandler, key) {
            applyHandlers[key] = function(event) {
              if (useApplyAsync) {
                $rootScope.$applyAsync(callEventHandler);
              } else if ($rootScope.$$phase) {
                callEventHandler();
              } else {
                $rootScope.$apply(callEventHandler);
              }

              function callEventHandler() {
                eventHandler(event);
              }
            };
          });
          return applyHandlers;
        }
      }


      /**
       * Callback registered to $httpBackend():
       *  - caches the response if desired
       *  - resolves the raw $http promise
       *  - calls $apply
       */
      function done(status, response, headersString, statusText, xhrStatus) {
        if (cache) {
          if (isSuccess(status)) {
            cache.put(url, [status, response, parseHeaders(headersString), statusText, xhrStatus]);
          } else {
            // remove promise from the cache
            cache.remove(url);
          }
        }

        function resolveHttpPromise() {
          resolvePromise(response, status, headersString, statusText, xhrStatus);
        }

        if (useApplyAsync) {
          $rootScope.$applyAsync(resolveHttpPromise);
        } else {
          resolveHttpPromise();
          if (!$rootScope.$$phase) $rootScope.$apply();
        }
      }


      /**
       * Resolves the raw $http promise.
       */
      function resolvePromise(response, status, headers, statusText, xhrStatus) {
        //status: HTTP response status code, 0, -1 (aborted by timeout / promise)
        status = status >= -1 ? status : 0;

        (isSuccess(status) ? deferred.resolve : deferred.reject)({
          data: response,
          status: status,
          headers: headersGetter(headers),
          config: config,
          statusText: statusText,
          xhrStatus: xhrStatus
        });
      }

      function resolvePromiseWithResult(result) {
        resolvePromise(result.data, result.status, shallowCopy(result.headers()), result.statusText, result.xhrStatus);
      }

      function removePendingReq() {
        var idx = $http.pendingRequests.indexOf(config);
        if (idx !== -1) $http.pendingRequests.splice(idx, 1);
      }
    }


    function buildUrl(url, serializedParams) {
      if (serializedParams.length > 0) {
        url += ((url.indexOf('?') === -1) ? '?' : '&') + serializedParams;
      }
      return url;
    }

    function sanitizeJsonpCallbackParam(url, cbKey) {
      var parts = url.split('?');
      if (parts.length > 2) {
        // Throw if the url contains more than one `?` query indicator
        throw $httpMinErr('badjsonp', 'Illegal use more than one "?", in url, "{1}"', url);
      }
      var params = parseKeyValue(parts[1]);
      forEach(params, function(value, key) {
        if (value === 'JSON_CALLBACK') {
          // Throw if the url already contains a reference to JSON_CALLBACK
          throw $httpMinErr('badjsonp', 'Illegal use of JSON_CALLBACK in url, "{0}"', url);
        }
        if (key === cbKey) {
          // Throw if the callback param was already provided
          throw $httpMinErr('badjsonp', 'Illegal use of callback param, "{0}", in url, "{1}"', cbKey, url);
        }
      });

      // Add in the JSON_CALLBACK callback param value
      url += ((url.indexOf('?') === -1) ? '?' : '&') + cbKey + '=JSON_CALLBACK';

      return url;
    }
  }];
}

/**
 * @ngdoc service
 * @name $xhrFactory
 * @this
 *
 * @description
 * Factory function used to create XMLHttpRequest objects.
 *
 * Replace or decorate this service to create your own custom XMLHttpRequest objects.
 *
 * ```
 * angular.module('myApp', [])
 * .factory('$xhrFactory', function() {
 *   return function createXhr(method, url) {
 *     return new window.XMLHttpRequest({mozSystem: true});
 *   };
 * });
 * ```
 *
 * @param {string} method HTTP method of the request (GET, POST, PUT, ..)
 * @param {string} url URL of the request.
 */
function $xhrFactoryProvider() {
  this.$get = function() {
    return function createXhr() {
      return new window.XMLHttpRequest();
    };
  };
}

/**
 * @ngdoc service
 * @name $httpBackend
 * @requires $jsonpCallbacks
 * @requires $document
 * @requires $xhrFactory
 * @this
 *
 * @description
 * HTTP backend used by the {@link ng.$http service} that delegates to
 * XMLHttpRequest object or JSONP and deals with browser incompatibilities.
 *
 * You should never need to use this service directly, instead use the higher-level abstractions:
 * {@link ng.$http $http} or {@link ngResource.$resource $resource}.
 *
 * During testing this implementation is swapped with {@link ngMock.$httpBackend mock
 * $httpBackend} which can be trained with responses.
 */
function $HttpBackendProvider() {
  this.$get = ['$browser', '$jsonpCallbacks', '$document', '$xhrFactory', function($browser, $jsonpCallbacks, $document, $xhrFactory) {
    return createHttpBackend($browser, $xhrFactory, $browser.defer, $jsonpCallbacks, $document[0]);
  }];
}

function createHttpBackend($browser, createXhr, $browserDefer, callbacks, rawDocument) {
  // TODO(vojta): fix the signature
  return function(method, url, post, callback, headers, timeout, withCredentials, responseType, eventHandlers, uploadEventHandlers) {
    url = url || $browser.url();

    if (lowercase(method) === 'jsonp') {
      var callbackPath = callbacks.createCallback(url);
      var jsonpDone = jsonpReq(url, callbackPath, function(status, text) {
        // jsonpReq only ever sets status to 200 (OK), 404 (ERROR) or -1 (WAITING)
        var response = (status === 200) && callbacks.getResponse(callbackPath);
        completeRequest(callback, status, response, '', text, 'complete');
        callbacks.removeCallback(callbackPath);
      });
    } else {

      var xhr = createXhr(method, url);
      var abortedByTimeout = false;

      xhr.open(method, url, true);
      forEach(headers, function(value, key) {
        if (isDefined(value)) {
            xhr.setRequestHeader(key, value);
        }
      });

      xhr.onload = function requestLoaded() {
        var statusText = xhr.statusText || '';

        // responseText is the old-school way of retrieving response (supported by IE9)
        // response/responseType properties were introduced in XHR Level2 spec (supported by IE10)
        var response = ('response' in xhr) ? xhr.response : xhr.responseText;

        // normalize IE9 bug (http://bugs.jquery.com/ticket/1450)
        var status = xhr.status === 1223 ? 204 : xhr.status;

        // fix status code when it is 0 (0 status is undocumented).
        // Occurs when accessing file resources or on Android 4.1 stock browser
        // while retrieving files from application cache.
        if (status === 0) {
          status = response ? 200 : urlResolve(url).protocol === 'file' ? 404 : 0;
        }

        completeRequest(callback,
            status,
            response,
            xhr.getAllResponseHeaders(),
            statusText,
            'complete');
      };

      var requestError = function() {
        // The response is always empty
        // See https://xhr.spec.whatwg.org/#request-error-steps and https://fetch.spec.whatwg.org/#concept-network-error
        completeRequest(callback, -1, null, null, '', 'error');
      };

      var requestAborted = function() {
        completeRequest(callback, -1, null, null, '', abortedByTimeout ? 'timeout' : 'abort');
      };

      var requestTimeout = function() {
        // The response is always empty
        // See https://xhr.spec.whatwg.org/#request-error-steps and https://fetch.spec.whatwg.org/#concept-network-error
        completeRequest(callback, -1, null, null, '', 'timeout');
      };

      xhr.onerror = requestError;
      xhr.ontimeout = requestTimeout;
      xhr.onabort = requestAborted;

      forEach(eventHandlers, function(value, key) {
        xhr.addEventListener(key, value);
      });

      forEach(uploadEventHandlers, function(value, key) {
        xhr.upload.addEventListener(key, value);
      });

      if (withCredentials) {
        xhr.withCredentials = true;
      }

      if (responseType) {
        try {
          xhr.responseType = responseType;
        } catch (e) {
          // WebKit added support for the json responseType value on 09/03/2013
          // https://bugs.webkit.org/show_bug.cgi?id=73648. Versions of Safari prior to 7 are
          // known to throw when setting the value "json" as the response type. Other older
          // browsers implementing the responseType
          //
          // The json response type can be ignored if not supported, because JSON payloads are
          // parsed on the client-side regardless.
          if (responseType !== 'json') {
            throw e;
          }
        }
      }

      xhr.send(isUndefined(post) ? null : post);
    }

    // Since we are using xhr.abort() when a request times out, we have to set a flag that
    // indicates to requestAborted if the request timed out or was aborted.
    //
    // http.timeout = numerical timeout   timeout
    // http.timeout = $timeout            timeout
    // http.timeout = promise             abort
    // xhr.abort()                        abort (The xhr object is normally inaccessible, but
    //                                    can be exposed with the xhrFactory)
    if (timeout > 0) {
      var timeoutId = $browserDefer(function() {
        timeoutRequest('timeout');
      }, timeout);
    } else if (isPromiseLike(timeout)) {
      timeout.then(function() {
        timeoutRequest(isDefined(timeout.$$timeoutId) ? 'timeout' : 'abort');
      });
    }

    function timeoutRequest(reason) {
      abortedByTimeout = reason === 'timeout';
      if (jsonpDone) {
        jsonpDone();
      }
      if (xhr) {
        xhr.abort();
      }
    }

    function completeRequest(callback, status, response, headersString, statusText, xhrStatus) {
      // cancel timeout and subsequent timeout promise resolution
      if (isDefined(timeoutId)) {
        $browserDefer.cancel(timeoutId);
      }
      jsonpDone = xhr = null;

      callback(status, response, headersString, statusText, xhrStatus);
    }
  };

  function jsonpReq(url, callbackPath, done) {
    url = url.replace('JSON_CALLBACK', callbackPath);
    // we can't use jQuery/jqLite here because jQuery does crazy stuff with script elements, e.g.:
    // - fetches local scripts via XHR and evals them
    // - adds and immediately removes script elements from the document
    var script = rawDocument.createElement('script'), callback = null;
    script.type = 'text/javascript';
    script.src = url;
    script.async = true;

    callback = function(event) {
      script.removeEventListener('load', callback);
      script.removeEventListener('error', callback);
      rawDocument.body.removeChild(script);
      script = null;
      var status = -1;
      var text = 'unknown';

      if (event) {
        if (event.type === 'load' && !callbacks.wasCalled(callbackPath)) {
          event = { type: 'error' };
        }
        text = event.type;
        status = event.type === 'error' ? 404 : 200;
      }

      if (done) {
        done(status, text);
      }
    };

    script.addEventListener('load', callback);
    script.addEventListener('error', callback);
    rawDocument.body.appendChild(script);
    return callback;
  }
}

var $interpolateMinErr = angular.$interpolateMinErr = minErr('$interpolate');
$interpolateMinErr.throwNoconcat = function(text) {
  throw $interpolateMinErr('noconcat',
      'Error while interpolating: {0}\nStrict Contextual Escaping disallows ' +
      'interpolations that concatenate multiple expressions when a trusted value is ' +
      'required.  See http://docs.angularjs.org/api/ng.$sce', text);
};

$interpolateMinErr.interr = function(text, err) {
  return $interpolateMinErr('interr', 'Can\'t interpolate: {0}\n{1}', text, err.toString());
};

/**
 * @ngdoc provider
 * @name $interpolateProvider
 * @this
 *
 * @description
 *
 * Used for configuring the interpolation markup. Defaults to `{{` and `}}`.
 *
 * <div class="alert alert-danger">
 * This feature is sometimes used to mix different markup languages, e.g. to wrap an AngularJS
 * template within a Python Jinja template (or any other template language). Mixing templating
 * languages is **very dangerous**. The embedding template language will not safely escape AngularJS
 * expressions, so any user-controlled values in the template will cause Cross Site Scripting (XSS)
 * security bugs!
 * </div>
 *
 * @example
<example name="custom-interpolation-markup" module="customInterpolationApp">
<file name="index.html">
<script>
  var customInterpolationApp = angular.module('customInterpolationApp', []);

  customInterpolationApp.config(function($interpolateProvider) {
    $interpolateProvider.startSymbol('//');
    $interpolateProvider.endSymbol('//');
  });


  customInterpolationApp.controller('DemoController', function() {
      this.label = "This binding is brought you by // interpolation symbols.";
  });
</script>
<div ng-controller="DemoController as demo">
    //demo.label//
</div>
</file>
<file name="protractor.js" type="protractor">
  it('should interpolate binding with custom symbols', function() {
    expect(element(by.binding('demo.label')).getText()).toBe('This binding is brought you by // interpolation symbols.');
  });
</file>
</example>
 */
function $InterpolateProvider() {
  var startSymbol = '{{';
  var endSymbol = '}}';

  /**
   * @ngdoc method
   * @name $interpolateProvider#startSymbol
   * @description
   * Symbol to denote start of expression in the interpolated string. Defaults to `{{`.
   *
   * @param {string=} value new value to set the starting symbol to.
   * @returns {string|self} Returns the symbol when used as getter and self if used as setter.
   */
  this.startSymbol = function(value) {
    if (value) {
      startSymbol = value;
      return this;
    }
    return startSymbol;
  };

  /**
   * @ngdoc method
   * @name $interpolateProvider#endSymbol
   * @description
   * Symbol to denote the end of expression in the interpolated string. Defaults to `}}`.
   *
   * @param {string=} value new value to set the ending symbol to.
   * @returns {string|self} Returns the symbol when used as getter and self if used as setter.
   */
  this.endSymbol = function(value) {
    if (value) {
      endSymbol = value;
      return this;
    }
    return endSymbol;
  };


  this.$get = ['$parse', '$exceptionHandler', '$sce', function($parse, $exceptionHandler, $sce) {
    var startSymbolLength = startSymbol.length,
        endSymbolLength = endSymbol.length,
        escapedStartRegexp = new RegExp(startSymbol.replace(/./g, escape), 'g'),
        escapedEndRegexp = new RegExp(endSymbol.replace(/./g, escape), 'g');

    function escape(ch) {
      return '\\\\\\' + ch;
    }

    function unescapeText(text) {
      return text.replace(escapedStartRegexp, startSymbol).
        replace(escapedEndRegexp, endSymbol);
    }

    // TODO: this is the same as the constantWatchDelegate in parse.js
    function constantWatchDelegate(scope, listener, objectEquality, constantInterp) {
      var unwatch = scope.$watch(function constantInterpolateWatch(scope) {
        unwatch();
        return constantInterp(scope);
      }, listener, objectEquality);
      return unwatch;
    }

    /**
     * @ngdoc service
     * @name $interpolate
     * @kind function
     *
     * @requires $parse
     * @requires $sce
     *
     * @description
     *
     * Compiles a string with markup into an interpolation function. This service is used by the
     * HTML {@link ng.$compile $compile} service for data binding. See
     * {@link ng.$interpolateProvider $interpolateProvider} for configuring the
     * interpolation markup.
     *
     *
     * ```js
     *   var $interpolate = ...; // injected
     *   var exp = $interpolate('Hello {{name | uppercase}}!');
     *   expect(exp({name:'AngularJS'})).toEqual('Hello ANGULARJS!');
     * ```
     *
     * `$interpolate` takes an optional fourth argument, `allOrNothing`. If `allOrNothing` is
     * `true`, the interpolation function will return `undefined` unless all embedded expressions
     * evaluate to a value other than `undefined`.
     *
     * ```js
     *   var $interpolate = ...; // injected
     *   var context = {greeting: 'Hello', name: undefined };
     *
     *   // default "forgiving" mode
     *   var exp = $interpolate('{{greeting}} {{name}}!');
     *   expect(exp(context)).toEqual('Hello !');
     *
     *   // "allOrNothing" mode
     *   exp = $interpolate('{{greeting}} {{name}}!', false, null, true);
     *   expect(exp(context)).toBeUndefined();
     *   context.name = 'AngularJS';
     *   expect(exp(context)).toEqual('Hello AngularJS!');
     * ```
     *
     * `allOrNothing` is useful for interpolating URLs. `ngSrc` and `ngSrcset` use this behavior.
     *
     * #### Escaped Interpolation
     * $interpolate provides a mechanism for escaping interpolation markers. Start and end markers
     * can be escaped by preceding each of their characters with a REVERSE SOLIDUS U+005C (backslash).
     * It will be rendered as a regular start/end marker, and will not be interpreted as an expression
     * or binding.
     *
     * This enables web-servers to prevent script injection attacks and defacing attacks, to some
     * degree, while also enabling code examples to work without relying on the
     * {@link ng.directive:ngNonBindable ngNonBindable} directive.
     *
     * **For security purposes, it is strongly encouraged that web servers escape user-supplied data,
     * replacing angle brackets (&lt;, &gt;) with &amp;lt; and &amp;gt; respectively, and replacing all
     * interpolation start/end markers with their escaped counterparts.**
     *
     * Escaped interpolation markers are only replaced with the actual interpolation markers in rendered
     * output when the $interpolate service processes the text. So, for HTML elements interpolated
     * by {@link ng.$compile $compile}, or otherwise interpolated with the `mustHaveExpression` parameter
     * set to `true`, the interpolated text must contain an unescaped interpolation expression. As such,
     * this is typically useful only when user-data is used in rendering a template from the server, or
     * when otherwise untrusted data is used by a directive.
     *
     * <example name="interpolation">
     *  <file name="index.html">
     *    <div ng-init="username='A user'">
     *      <p ng-init="apptitle='Escaping demo'">{{apptitle}}: \{\{ username = "defaced value"; \}\}
     *        </p>
     *      <p><strong>{{username}}</strong> attempts to inject code which will deface the
     *        application, but fails to accomplish their task, because the server has correctly
     *        escaped the interpolation start/end markers with REVERSE SOLIDUS U+005C (backslash)
     *        characters.</p>
     *      <p>Instead, the result of the attempted script injection is visible, and can be removed
     *        from the database by an administrator.</p>
     *    </div>
     *  </file>
     * </example>
     *
     * @knownIssue
     * It is currently not possible for an interpolated expression to contain the interpolation end
     * symbol. For example, `{{ '}}' }}` will be incorrectly interpreted as `{{ ' }}` + `' }}`, i.e.
     * an interpolated expression consisting of a single-quote (`'`) and the `' }}` string.
     *
     * @knownIssue
     * All directives and components must use the standard `{{` `}}` interpolation symbols
     * in their templates. If you change the application interpolation symbols the {@link $compile}
     * service will attempt to denormalize the standard symbols to the custom symbols.
     * The denormalization process is not clever enough to know not to replace instances of the standard
     * symbols where they would not normally be treated as interpolation symbols. For example in the following
     * code snippet the closing braces of the literal object will get incorrectly denormalized:
     *
     * ```
     * <div data-context='{"context":{"id":3,"type":"page"}}">
     * ```
     *
     * The workaround is to ensure that such instances are separated by whitespace:
     * ```
     * <div data-context='{"context":{"id":3,"type":"page"} }">
     * ```
     *
     * See https://github.com/angular/angular.js/pull/14610#issuecomment-219401099 for more information.
     *
     * @param {string} text The text with markup to interpolate.
     * @param {boolean=} mustHaveExpression if set to true then the interpolation string must have
     *    embedded expression in order to return an interpolation function. Strings with no
     *    embedded expression will return null for the interpolation function.
     * @param {string=} trustedContext when provided, the returned function passes the interpolated
     *    result through {@link ng.$sce#getTrusted $sce.getTrusted(interpolatedResult,
     *    trustedContext)} before returning it.  Refer to the {@link ng.$sce $sce} service that
     *    provides Strict Contextual Escaping for details.
     * @param {boolean=} allOrNothing if `true`, then the returned function returns undefined
     *    unless all embedded expressions evaluate to a value other than `undefined`.
     * @returns {function(context)} an interpolation function which is used to compute the
     *    interpolated string. The function has these parameters:
     *
     * - `context`: evaluation context for all expressions embedded in the interpolated text
     */
    function $interpolate(text, mustHaveExpression, trustedContext, allOrNothing) {
      var contextAllowsConcatenation = trustedContext === $sce.URL || trustedContext === $sce.MEDIA_URL;

      // Provide a quick exit and simplified result function for text with no interpolation
      if (!text.length || text.indexOf(startSymbol) === -1) {
        if (mustHaveExpression) return;

        var unescapedText = unescapeText(text);
        if (contextAllowsConcatenation) {
          unescapedText = $sce.getTrusted(trustedContext, unescapedText);
        }
        var constantInterp = valueFn(unescapedText);
        constantInterp.exp = text;
        constantInterp.expressions = [];
        constantInterp.$$watchDelegate = constantWatchDelegate;

        return constantInterp;
      }

      allOrNothing = !!allOrNothing;
      var startIndex,
          endIndex,
          index = 0,
          expressions = [],
          parseFns,
          textLength = text.length,
          exp,
          concat = [],
          expressionPositions = [],
          singleExpression;


      while (index < textLength) {
        if (((startIndex = text.indexOf(startSymbol, index)) !== -1) &&
             ((endIndex = text.indexOf(endSymbol, startIndex + startSymbolLength)) !== -1)) {
          if (index !== startIndex) {
            concat.push(unescapeText(text.substring(index, startIndex)));
          }
          exp = text.substring(startIndex + startSymbolLength, endIndex);
          expressions.push(exp);
          index = endIndex + endSymbolLength;
          expressionPositions.push(concat.length);
          concat.push(''); // Placeholder that will get replaced with the evaluated expression.
        } else {
          // we did not find an interpolation, so we have to add the remainder to the separators array
          if (index !== textLength) {
            concat.push(unescapeText(text.substring(index)));
          }
          break;
        }
      }

      singleExpression = concat.length === 1 && expressionPositions.length === 1;
      // Intercept expression if we need to stringify concatenated inputs, which may be SCE trusted
      // objects rather than simple strings
      // (we don't modify the expression if the input consists of only a single trusted input)
      var interceptor = contextAllowsConcatenation && singleExpression ? undefined : parseStringifyInterceptor;
      parseFns = expressions.map(function(exp) { return $parse(exp, interceptor); });

      // Concatenating expressions makes it hard to reason about whether some combination of
      // concatenated values are unsafe to use and could easily lead to XSS.  By requiring that a
      // single expression be used for some $sce-managed secure contexts (RESOURCE_URLs mostly),
      // we ensure that the value that's used is assigned or constructed by some JS code somewhere
      // that is more testable or make it obvious that you bound the value to some user controlled
      // value.  This helps reduce the load when auditing for XSS issues.

      // Note that URL and MEDIA_URL $sce contexts do not need this, since `$sce` can sanitize the values
      // passed to it. In that case, `$sce.getTrusted` will be called on either the single expression
      // or on the overall concatenated string (losing trusted types used in the mix, by design).
      // Both these methods will sanitize plain strings. Also, HTML could be included, but since it's
      // only used in srcdoc attributes, this would not be very useful.

      if (!mustHaveExpression || expressions.length) {
        var compute = function(values) {
          for (var i = 0, ii = expressions.length; i < ii; i++) {
            if (allOrNothing && isUndefined(values[i])) return;
            concat[expressionPositions[i]] = values[i];
          }

          if (contextAllowsConcatenation) {
            // If `singleExpression` then `concat[0]` might be a "trusted" value or `null`, rather than a string
            return $sce.getTrusted(trustedContext, singleExpression ? concat[0] : concat.join(''));
          } else if (trustedContext && concat.length > 1) {
            // This context does not allow more than one part, e.g. expr + string or exp + exp.
            $interpolateMinErr.throwNoconcat(text);
          }
          // In an unprivileged context or only one part: just concatenate and return.
          return concat.join('');
        };

        return extend(function interpolationFn(context) {
            var i = 0;
            var ii = expressions.length;
            var values = new Array(ii);

            try {
              for (; i < ii; i++) {
                values[i] = parseFns[i](context);
              }

              return compute(values);
            } catch (err) {
              $exceptionHandler($interpolateMinErr.interr(text, err));
            }

          }, {
          // all of these properties are undocumented for now
          exp: text, //just for compatibility with regular watchers created via $watch
          expressions: expressions,
          $$watchDelegate: function(scope, listener) {
            var lastValue;
            return scope.$watchGroup(parseFns, /** @this */ function interpolateFnWatcher(values, oldValues) {
              var currValue = compute(values);
              listener.call(this, currValue, values !== oldValues ? lastValue : currValue, scope);
              lastValue = currValue;
            });
          }
        });
      }

      function parseStringifyInterceptor(value) {
        try {
          // In concatenable contexts, getTrusted comes at the end, to avoid sanitizing individual
          // parts of a full URL. We don't care about losing the trustedness here.
          // In non-concatenable contexts, where there is only one expression, this interceptor is
          // not applied to the expression.
          value = (trustedContext && !contextAllowsConcatenation) ?
                    $sce.getTrusted(trustedContext, value) :
                    $sce.valueOf(value);
          return allOrNothing && !isDefined(value) ? value : stringify(value);
        } catch (err) {
          $exceptionHandler($interpolateMinErr.interr(text, err));
        }
      }
    }


    /**
     * @ngdoc method
     * @name $interpolate#startSymbol
     * @description
     * Symbol to denote the start of expression in the interpolated string. Defaults to `{{`.
     *
     * Use {@link ng.$interpolateProvider#startSymbol `$interpolateProvider.startSymbol`} to change
     * the symbol.
     *
     * @returns {string} start symbol.
     */
    $interpolate.startSymbol = function() {
      return startSymbol;
    };


    /**
     * @ngdoc method
     * @name $interpolate#endSymbol
     * @description
     * Symbol to denote the end of expression in the interpolated string. Defaults to `}}`.
     *
     * Use {@link ng.$interpolateProvider#endSymbol `$interpolateProvider.endSymbol`} to change
     * the symbol.
     *
     * @returns {string} end symbol.
     */
    $interpolate.endSymbol = function() {
      return endSymbol;
    };

    return $interpolate;
  }];
}

var $intervalMinErr = minErr('$interval');

/** @this */
function $IntervalProvider() {
  this.$get = ['$$intervalFactory', '$window',
       function($$intervalFactory,   $window) {
    var intervals = {};
    var setIntervalFn = function(tick, delay, deferred) {
      var id = $window.setInterval(tick, delay);
      intervals[id] = deferred;
      return id;
    };
    var clearIntervalFn = function(id) {
      $window.clearInterval(id);
      delete intervals[id];
    };

    /**
     * @ngdoc service
     * @name $interval
     *
     * @description
     * AngularJS's wrapper for `window.setInterval`. The `fn` function is executed every `delay`
     * milliseconds.
     *
     * The return value of registering an interval function is a promise. This promise will be
     * notified upon each tick of the interval, and will be resolved after `count` iterations, or
     * run indefinitely if `count` is not defined. The value of the notification will be the
     * number of iterations that have run.
     * To cancel an interval, call `$interval.cancel(promise)`.
     *
     * In tests you can use {@link ngMock.$interval#flush `$interval.flush(millis)`} to
     * move forward by `millis` milliseconds and trigger any functions scheduled to run in that
     * time.
     *
     * <div class="alert alert-warning">
     * **Note**: Intervals created by this service must be explicitly destroyed when you are finished
     * with them.  In particular they are not automatically destroyed when a controller's scope or a
     * directive's element are destroyed.
     * You should take this into consideration and make sure to always cancel the interval at the
     * appropriate moment.  See the example below for more details on how and when to do this.
     * </div>
     *
     * @param {function()} fn A function that should be called repeatedly. If no additional arguments
     *   are passed (see below), the function is called with the current iteration count.
     * @param {number} delay Number of milliseconds between each function call.
     * @param {number=} [count=0] Number of times to repeat. If not set, or 0, will repeat
     *   indefinitely.
     * @param {boolean=} [invokeApply=true] If set to `false` skips model dirty checking, otherwise
     *   will invoke `fn` within the {@link ng.$rootScope.Scope#$apply $apply} block.
     * @param {...*=} Pass additional parameters to the executed function.
     * @returns {promise} A promise which will be notified on each iteration. It will resolve once all iterations of the interval complete.
     *
     * @example
     * <example module="intervalExample" name="interval-service">
     * <file name="index.html">
     *   <script>
     *     angular.module('intervalExample', [])
     *       .controller('ExampleController', ['$scope', '$interval',
     *         function($scope, $interval) {
     *           $scope.format = 'M/d/yy h:mm:ss a';
     *           $scope.blood_1 = 100;
     *           $scope.blood_2 = 120;
     *
     *           var stop;
     *           $scope.fight = function() {
     *             // Don't start a new fight if we are already fighting
     *             if ( angular.isDefined(stop) ) return;
     *
     *             stop = $interval(function() {
     *               if ($scope.blood_1 > 0 && $scope.blood_2 > 0) {
     *                 $scope.blood_1 = $scope.blood_1 - 3;
     *                 $scope.blood_2 = $scope.blood_2 - 4;
     *               } else {
     *                 $scope.stopFight();
     *               }
     *             }, 100);
     *           };
     *
     *           $scope.stopFight = function() {
     *             if (angular.isDefined(stop)) {
     *               $interval.cancel(stop);
     *               stop = undefined;
     *             }
     *           };
     *
     *           $scope.resetFight = function() {
     *             $scope.blood_1 = 100;
     *             $scope.blood_2 = 120;
     *           };
     *
     *           $scope.$on('$destroy', function() {
     *             // Make sure that the interval is destroyed too
     *             $scope.stopFight();
     *           });
     *         }])
     *       // Register the 'myCurrentTime' directive factory method.
     *       // We inject $interval and dateFilter service since the factory method is DI.
     *       .directive('myCurrentTime', ['$interval', 'dateFilter',
     *         function($interval, dateFilter) {
     *           // return the directive link function. (compile function not needed)
     *           return function(scope, element, attrs) {
     *             var format,  // date format
     *                 stopTime; // so that we can cancel the time updates
     *
     *             // used to update the UI
     *             function updateTime() {
     *               element.text(dateFilter(new Date(), format));
     *             }
     *
     *             // watch the expression, and update the UI on change.
     *             scope.$watch(attrs.myCurrentTime, function(value) {
     *               format = value;
     *               updateTime();
     *             });
     *
     *             stopTime = $interval(updateTime, 1000);
     *
     *             // listen on DOM destroy (removal) event, and cancel the next UI update
     *             // to prevent updating time after the DOM element was removed.
     *             element.on('$destroy', function() {
     *               $interval.cancel(stopTime);
     *             });
     *           }
     *         }]);
     *   </script>
     *
     *   <div>
     *     <div ng-controller="ExampleController">
     *       <label>Date format: <input ng-model="format"></label> <hr/>
     *       Current time is: <span my-current-time="format"></span>
     *       <hr/>
     *       Blood 1 : <font color='red'>{{blood_1}}</font>
     *       Blood 2 : <font color='red'>{{blood_2}}</font>
     *       <button type="button" data-ng-click="fight()">Fight</button>
     *       <button type="button" data-ng-click="stopFight()">StopFight</button>
     *       <button type="button" data-ng-click="resetFight()">resetFight</button>
     *     </div>
     *   </div>
     *
     * </file>
     * </example>
     */
    var interval = $$intervalFactory(setIntervalFn, clearIntervalFn);

    /**
     * @ngdoc method
     * @name $interval#cancel
     *
     * @description
     * Cancels a task associated with the `promise`.
     *
     * @param {Promise=} promise returned by the `$interval` function.
     * @returns {boolean} Returns `true` if the task was successfully canceled.
     */
    interval.cancel = function(promise) {
      if (!promise) return false;

      if (!promise.hasOwnProperty('$$intervalId')) {
        throw $intervalMinErr('badprom',
            '`$interval.cancel()` called with a promise that was not generated by `$interval()`.');
      }

      if (!intervals.hasOwnProperty(promise.$$intervalId)) return false;

      var id = promise.$$intervalId;
      var deferred = intervals[id];

      // Interval cancels should not report an unhandled promise.
      markQExceptionHandled(deferred.promise);
      deferred.reject('canceled');
      clearIntervalFn(id);

      return true;
    };

    return interval;
  }];
}

/** @this */
function $$IntervalFactoryProvider() {
  this.$get = ['$browser', '$q', '$$q', '$rootScope',
       function($browser,   $q,   $$q,   $rootScope) {
    return function intervalFactory(setIntervalFn, clearIntervalFn) {
      return function intervalFn(fn, delay, count, invokeApply) {
        var hasParams = arguments.length > 4,
            args = hasParams ? sliceArgs(arguments, 4) : [],
            iteration = 0,
            skipApply = isDefined(invokeApply) && !invokeApply,
            deferred = (skipApply ? $$q : $q).defer(),
            promise = deferred.promise;

        count = isDefined(count) ? count : 0;

        function callback() {
          if (!hasParams) {
            fn(iteration);
          } else {
            fn.apply(null, args);
          }
        }

        function tick() {
          if (skipApply) {
            $browser.defer(callback);
          } else {
            $rootScope.$evalAsync(callback);
          }
          deferred.notify(iteration++);

          if (count > 0 && iteration >= count) {
            deferred.resolve(iteration);
            clearIntervalFn(promise.$$intervalId);
          }

          if (!skipApply) $rootScope.$apply();
        }

        promise.$$intervalId = setIntervalFn(tick, delay, deferred, skipApply);

        return promise;
      };
    };
  }];
}

/**
 * @ngdoc service
 * @name $jsonpCallbacks
 * @requires $window
 * @description
 * This service handles the lifecycle of callbacks to handle JSONP requests.
 * Override this service if you wish to customise where the callbacks are stored and
 * how they vary compared to the requested url.
 */
var $jsonpCallbacksProvider = /** @this */ function() {
  this.$get = function() {
    var callbacks = angular.callbacks;
    var callbackMap = {};

    function createCallback(callbackId) {
      var callback = function(data) {
        callback.data = data;
        callback.called = true;
      };
      callback.id = callbackId;
      return callback;
    }

    return {
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#createCallback
       * @param {string} url the url of the JSONP request
       * @returns {string} the callback path to send to the server as part of the JSONP request
       * @description
       * {@link $httpBackend} calls this method to create a callback and get hold of the path to the callback
       * to pass to the server, which will be used to call the callback with its payload in the JSONP response.
       */
      createCallback: function(url) {
        var callbackId = '_' + (callbacks.$$counter++).toString(36);
        var callbackPath = 'angular.callbacks.' + callbackId;
        var callback = createCallback(callbackId);
        callbackMap[callbackPath] = callbacks[callbackId] = callback;
        return callbackPath;
      },
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#wasCalled
       * @param {string} callbackPath the path to the callback that was sent in the JSONP request
       * @returns {boolean} whether the callback has been called, as a result of the JSONP response
       * @description
       * {@link $httpBackend} calls this method to find out whether the JSONP response actually called the
       * callback that was passed in the request.
       */
      wasCalled: function(callbackPath) {
        return callbackMap[callbackPath].called;
      },
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#getResponse
       * @param {string} callbackPath the path to the callback that was sent in the JSONP request
       * @returns {*} the data received from the response via the registered callback
       * @description
       * {@link $httpBackend} calls this method to get hold of the data that was provided to the callback
       * in the JSONP response.
       */
      getResponse: function(callbackPath) {
        return callbackMap[callbackPath].data;
      },
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#removeCallback
       * @param {string} callbackPath the path to the callback that was sent in the JSONP request
       * @description
       * {@link $httpBackend} calls this method to remove the callback after the JSONP request has
       * completed or timed-out.
       */
      removeCallback: function(callbackPath) {
        var callback = callbackMap[callbackPath];
        delete callbacks[callback.id];
        delete callbackMap[callbackPath];
      }
    };
  };
};

/**
 * @ngdoc service
 * @name $locale
 *
 * @description
 * $locale service provides localization rules for various AngularJS components. As of right now the
 * only public api is:
 *
 * * `id` – `{string}` – locale id formatted as `languageId-countryId` (e.g. `en-us`)
 */

/* global stripHash: true */

var PATH_MATCH = /^([^?#]*)(\?([^#]*))?(#(.*))?$/,
    DEFAULT_PORTS = {'http': 80, 'https': 443, 'ftp': 21};
var $locationMinErr = minErr('$location');


/**
 * Encode path using encodeUriSegment, ignoring forward slashes
 *
 * @param {string} path Path to encode
 * @returns {string}
 */
function encodePath(path) {
  var segments = path.split('/'),
      i = segments.length;

  while (i--) {
    // decode forward slashes to prevent them from being double encoded
    segments[i] = encodeUriSegment(segments[i].replace(/%2F/g, '/'));
  }

  return segments.join('/');
}

function decodePath(path, html5Mode) {
  var segments = path.split('/'),
      i = segments.length;

  while (i--) {
    segments[i] = decodeURIComponent(segments[i]);
    if (html5Mode) {
      // encode forward slashes to prevent them from being mistaken for path separators
      segments[i] = segments[i].replace(/\//g, '%2F');
    }
  }

  return segments.join('/');
}

function normalizePath(pathValue, searchValue, hashValue) {
  var search = toKeyValue(searchValue),
    hash = hashValue ? '#' + encodeUriSegment(hashValue) : '',
    path = encodePath(pathValue);

  return path + (search ? '?' + search : '') + hash;
}

function parseAbsoluteUrl(absoluteUrl, locationObj) {
  var parsedUrl = urlResolve(absoluteUrl);

  locationObj.$$protocol = parsedUrl.protocol;
  locationObj.$$host = parsedUrl.hostname;
  locationObj.$$port = toInt(parsedUrl.port) || DEFAULT_PORTS[parsedUrl.protocol] || null;
}

var DOUBLE_SLASH_REGEX = /^\s*[\\/]{2,}/;
function parseAppUrl(url, locationObj, html5Mode) {

  if (DOUBLE_SLASH_REGEX.test(url)) {
    throw $locationMinErr('badpath', 'Invalid url "{0}".', url);
  }

  var prefixed = (url.charAt(0) !== '/');
  if (prefixed) {
    url = '/' + url;
  }
  var match = urlResolve(url);
  var path = prefixed && match.pathname.charAt(0) === '/' ? match.pathname.substring(1) : match.pathname;
  locationObj.$$path = decodePath(path, html5Mode);
  locationObj.$$search = parseKeyValue(match.search);
  locationObj.$$hash = decodeURIComponent(match.hash);

  // make sure path starts with '/';
  if (locationObj.$$path && locationObj.$$path.charAt(0) !== '/') {
    locationObj.$$path = '/' + locationObj.$$path;
  }
}

function startsWith(str, search) {
  return str.slice(0, search.length) === search;
}

/**
 *
 * @param {string} base
 * @param {string} url
 * @returns {string} returns text from `url` after `base` or `undefined` if it does not begin with
 *                   the expected string.
 */
function stripBaseUrl(base, url) {
  if (startsWith(url, base)) {
    return url.substr(base.length);
  }
}

function stripHash(url) {
  var index = url.indexOf('#');
  return index === -1 ? url : url.substr(0, index);
}

function stripFile(url) {
  return url.substr(0, stripHash(url).lastIndexOf('/') + 1);
}

/* return the server only (scheme://host:port) */
function serverBase(url) {
  return url.substring(0, url.indexOf('/', url.indexOf('//') + 2));
}


/**
 * LocationHtml5Url represents a URL
 * This object is exposed as $location service when HTML5 mode is enabled and supported
 *
 * @constructor
 * @param {string} appBase application base URL
 * @param {string} appBaseNoFile application base URL stripped of any filename
 * @param {string} basePrefix URL path prefix
 */
function LocationHtml5Url(appBase, appBaseNoFile, basePrefix) {
  this.$$html5 = true;
  basePrefix = basePrefix || '';
  parseAbsoluteUrl(appBase, this);


  /**
   * Parse given HTML5 (regular) URL string into properties
   * @param {string} url HTML5 URL
   * @private
   */
  this.$$parse = function(url) {
    var pathUrl = stripBaseUrl(appBaseNoFile, url);
    if (!isString(pathUrl)) {
      throw $locationMinErr('ipthprfx', 'Invalid url "{0}", missing path prefix "{1}".', url,
          appBaseNoFile);
    }

    parseAppUrl(pathUrl, this, true);

    if (!this.$$path) {
      this.$$path = '/';
    }

    this.$$compose();
  };

  this.$$normalizeUrl = function(url) {
    return appBaseNoFile + url.substr(1); // first char is always '/'
  };

  this.$$parseLinkUrl = function(url, relHref) {
    if (relHref && relHref[0] === '#') {
      // special case for links to hash fragments:
      // keep the old url and only replace the hash fragment
      this.hash(relHref.slice(1));
      return true;
    }
    var appUrl, prevAppUrl;
    var rewrittenUrl;


    if (isDefined(appUrl = stripBaseUrl(appBase, url))) {
      prevAppUrl = appUrl;
      if (basePrefix && isDefined(appUrl = stripBaseUrl(basePrefix, appUrl))) {
        rewrittenUrl = appBaseNoFile + (stripBaseUrl('/', appUrl) || appUrl);
      } else {
        rewrittenUrl = appBase + prevAppUrl;
      }
    } else if (isDefined(appUrl = stripBaseUrl(appBaseNoFile, url))) {
      rewrittenUrl = appBaseNoFile + appUrl;
    } else if (appBaseNoFile === url + '/') {
      rewrittenUrl = appBaseNoFile;
    }
    if (rewrittenUrl) {
      this.$$parse(rewrittenUrl);
    }
    return !!rewrittenUrl;
  };
}


/**
 * LocationHashbangUrl represents URL
 * This object is exposed as $location service when developer doesn't opt into html5 mode.
 * It also serves as the base class for html5 mode fallback on legacy browsers.
 *
 * @constructor
 * @param {string} appBase application base URL
 * @param {string} appBaseNoFile application base URL stripped of any filename
 * @param {string} hashPrefix hashbang prefix
 */
function LocationHashbangUrl(appBase, appBaseNoFile, hashPrefix) {

  parseAbsoluteUrl(appBase, this);


  /**
   * Parse given hashbang URL into properties
   * @param {string} url Hashbang URL
   * @private
   */
  this.$$parse = function(url) {
    var withoutBaseUrl = stripBaseUrl(appBase, url) || stripBaseUrl(appBaseNoFile, url);
    var withoutHashUrl;

    if (!isUndefined(withoutBaseUrl) && withoutBaseUrl.charAt(0) === '#') {

      // The rest of the URL starts with a hash so we have
      // got either a hashbang path or a plain hash fragment
      withoutHashUrl = stripBaseUrl(hashPrefix, withoutBaseUrl);
      if (isUndefined(withoutHashUrl)) {
        // There was no hashbang prefix so we just have a hash fragment
        withoutHashUrl = withoutBaseUrl;
      }

    } else {
      // There was no hashbang path nor hash fragment:
      // If we are in HTML5 mode we use what is left as the path;
      // Otherwise we ignore what is left
      if (this.$$html5) {
        withoutHashUrl = withoutBaseUrl;
      } else {
        withoutHashUrl = '';
        if (isUndefined(withoutBaseUrl)) {
          appBase = url;
          /** @type {?} */ (this).replace();
        }
      }
    }

    parseAppUrl(withoutHashUrl, this, false);

    this.$$path = removeWindowsDriveName(this.$$path, withoutHashUrl, appBase);

    this.$$compose();

    /*
     * In Windows, on an anchor node on documents loaded from
     * the filesystem, the browser will return a pathname
     * prefixed with the drive name ('/C:/path') when a
     * pathname without a drive is set:
     *  * a.setAttribute('href', '/foo')
     *   * a.pathname === '/C:/foo' //true
     *
     * Inside of AngularJS, we're always using pathnames that
     * do not include drive names for routing.
     */
    function removeWindowsDriveName(path, url, base) {
      /*
      Matches paths for file protocol on windows,
      such as /C:/foo/bar, and captures only /foo/bar.
      */
      var windowsFilePathExp = /^\/[A-Z]:(\/.*)/;

      var firstPathSegmentMatch;

      //Get the relative path from the input URL.
      if (startsWith(url, base)) {
        url = url.replace(base, '');
      }

      // The input URL intentionally contains a first path segment that ends with a colon.
      if (windowsFilePathExp.exec(url)) {
        return path;
      }

      firstPathSegmentMatch = windowsFilePathExp.exec(path);
      return firstPathSegmentMatch ? firstPathSegmentMatch[1] : path;
    }
  };

  this.$$normalizeUrl = function(url) {
    return appBase + (url ? hashPrefix + url : '');
  };

  this.$$parseLinkUrl = function(url, relHref) {
    if (stripHash(appBase) === stripHash(url)) {
      this.$$parse(url);
      return true;
    }
    return false;
  };
}


/**
 * LocationHashbangUrl represents URL
 * This object is exposed as $location service when html5 history api is enabled but the browser
 * does not support it.
 *
 * @constructor
 * @param {string} appBase application base URL
 * @param {string} appBaseNoFile application base URL stripped of any filename
 * @param {string} hashPrefix hashbang prefix
 */
function LocationHashbangInHtml5Url(appBase, appBaseNoFile, hashPrefix) {
  this.$$html5 = true;
  LocationHashbangUrl.apply(this, arguments);

  this.$$parseLinkUrl = function(url, relHref) {
    if (relHref && relHref[0] === '#') {
      // special case for links to hash fragments:
      // keep the old url and only replace the hash fragment
      this.hash(relHref.slice(1));
      return true;
    }

    var rewrittenUrl;
    var appUrl;

    if (appBase === stripHash(url)) {
      rewrittenUrl = url;
    } else if ((appUrl = stripBaseUrl(appBaseNoFile, url))) {
      rewrittenUrl = appBase + hashPrefix + appUrl;
    } else if (appBaseNoFile === url + '/') {
      rewrittenUrl = appBaseNoFile;
    }
    if (rewrittenUrl) {
      this.$$parse(rewrittenUrl);
    }
    return !!rewrittenUrl;
  };

  this.$$normalizeUrl = function(url) {
    // include hashPrefix in $$absUrl when $$url is empty so IE9 does not reload page because of removal of '#'
    return appBase + hashPrefix + url;
  };
}


var locationPrototype = {

  /**
   * Ensure absolute URL is initialized.
   * @private
   */
  $$absUrl:'',

  /**
   * Are we in html5 mode?
   * @private
   */
  $$html5: false,

  /**
   * Has any change been replacing?
   * @private
   */
  $$replace: false,

  /**
   * Compose url and update `url` and `absUrl` property
   * @private
   */
  $$compose: function() {
    this.$$url = normalizePath(this.$$path, this.$$search, this.$$hash);
    this.$$absUrl = this.$$normalizeUrl(this.$$url);
    this.$$urlUpdatedByLocation = true;
  },

  /**
   * @ngdoc method
   * @name $location#absUrl
   *
   * @description
   * This method is getter only.
   *
   * Return full URL representation with all segments encoded according to rules specified in
   * [RFC 3986](http://www.ietf.org/rfc/rfc3986.txt).
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var absUrl = $location.absUrl();
   * // => "http://example.com/#/some/path?foo=bar&baz=xoxo"
   * ```
   *
   * @return {string} full URL
   */
  absUrl: locationGetter('$$absUrl'),

  /**
   * @ngdoc method
   * @name $location#url
   *
   * @description
   * This method is getter / setter.
   *
   * Return URL (e.g. `/path?a=b#hash`) when called without any parameter.
   *
   * Change path, search and hash, when called with parameter and return `$location`.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var url = $location.url();
   * // => "/some/path?foo=bar&baz=xoxo"
   * ```
   *
   * @param {string=} url New URL without base prefix (e.g. `/path?a=b#hash`)
   * @return {string} url
   */
  url: function(url) {
    if (isUndefined(url)) {
      return this.$$url;
    }

    var match = PATH_MATCH.exec(url);
    if (match[1] || url === '') this.path(decodeURIComponent(match[1]));
    if (match[2] || match[1] || url === '') this.search(match[3] || '');
    this.hash(match[5] || '');

    return this;
  },

  /**
   * @ngdoc method
   * @name $location#protocol
   *
   * @description
   * This method is getter only.
   *
   * Return protocol of current URL.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var protocol = $location.protocol();
   * // => "http"
   * ```
   *
   * @return {string} protocol of current URL
   */
  protocol: locationGetter('$$protocol'),

  /**
   * @ngdoc method
   * @name $location#host
   *
   * @description
   * This method is getter only.
   *
   * Return host of current URL.
   *
   * Note: compared to the non-AngularJS version `location.host` which returns `hostname:port`, this returns the `hostname` portion only.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var host = $location.host();
   * // => "example.com"
   *
   * // given URL http://user:password@example.com:8080/#/some/path?foo=bar&baz=xoxo
   * host = $location.host();
   * // => "example.com"
   * host = location.host;
   * // => "example.com:8080"
   * ```
   *
   * @return {string} host of current URL.
   */
  host: locationGetter('$$host'),

  /**
   * @ngdoc method
   * @name $location#port
   *
   * @description
   * This method is getter only.
   *
   * Return port of current URL.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var port = $location.port();
   * // => 80
   * ```
   *
   * @return {Number} port
   */
  port: locationGetter('$$port'),

  /**
   * @ngdoc method
   * @name $location#path
   *
   * @description
   * This method is getter / setter.
   *
   * Return path of current URL when called without any parameter.
   *
   * Change path when called with parameter and return `$location`.
   *
   * Note: Path should always begin with forward slash (/), this method will add the forward slash
   * if it is missing.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var path = $location.path();
   * // => "/some/path"
   * ```
   *
   * @param {(string|number)=} path New path
   * @return {(string|object)} path if called with no parameters, or `$location` if called with a parameter
   */
  path: locationGetterSetter('$$path', function(path) {
    path = path !== null ? path.toString() : '';
    return path.charAt(0) === '/' ? path : '/' + path;
  }),

  /**
   * @ngdoc method
   * @name $location#search
   *
   * @description
   * This method is getter / setter.
   *
   * Return search part (as object) of current URL when called without any parameter.
   *
   * Change search part when called with parameter and return `$location`.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var searchObject = $location.search();
   * // => {foo: 'bar', baz: 'xoxo'}
   *
   * // set foo to 'yipee'
   * $location.search('foo', 'yipee');
   * // $location.search() => {foo: 'yipee', baz: 'xoxo'}
   * ```
   *
   * @param {string|Object.<string>|Object.<Array.<string>>} search New search params - string or
   * hash object.
   *
   * When called with a single argument the method acts as a setter, setting the `search` component
   * of `$location` to the specified value.
   *
   * If the argument is a hash object containing an array of values, these values will be encoded
   * as duplicate search parameters in the URL.
   *
   * @param {(string|Number|Array<string>|boolean)=} paramValue If `search` is a string or number, then `paramValue`
   * will override only a single search property.
   *
   * If `paramValue` is an array, it will override the property of the `search` component of
   * `$location` specified via the first argument.
   *
   * If `paramValue` is `null`, the property specified via the first argument will be deleted.
   *
   * If `paramValue` is `true`, the property specified via the first argument will be added with no
   * value nor trailing equal sign.
   *
   * @return {Object} If called with no arguments returns the parsed `search` object. If called with
   * one or more arguments returns `$location` object itself.
   */
  search: function(search, paramValue) {
    switch (arguments.length) {
      case 0:
        return this.$$search;
      case 1:
        if (isString(search) || isNumber(search)) {
          search = search.toString();
          this.$$search = parseKeyValue(search);
        } else if (isObject(search)) {
          search = copy(search, {});
          // remove object undefined or null properties
          forEach(search, function(value, key) {
            if (value == null) delete search[key];
          });

          this.$$search = search;
        } else {
          throw $locationMinErr('isrcharg',
              'The first argument of the `$location#search()` call must be a string or an object.');
        }
        break;
      default:
        if (isUndefined(paramValue) || paramValue === null) {
          delete this.$$search[search];
        } else {
          this.$$search[search] = paramValue;
        }
    }

    this.$$compose();
    return this;
  },

  /**
   * @ngdoc method
   * @name $location#hash
   *
   * @description
   * This method is getter / setter.
   *
   * Returns the hash fragment when called without any parameters.
   *
   * Changes the hash fragment when called with a parameter and returns `$location`.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo#hashValue
   * var hash = $location.hash();
   * // => "hashValue"
   * ```
   *
   * @param {(string|number)=} hash New hash fragment
   * @return {string} hash
   */
  hash: locationGetterSetter('$$hash', function(hash) {
    return hash !== null ? hash.toString() : '';
  }),

  /**
   * @ngdoc method
   * @name $location#replace
   *
   * @description
   * If called, all changes to $location during the current `$digest` will replace the current history
   * record, instead of adding a new one.
   */
  replace: function() {
    this.$$replace = true;
    return this;
  }
};

forEach([LocationHashbangInHtml5Url, LocationHashbangUrl, LocationHtml5Url], function(Location) {
  Location.prototype = Object.create(locationPrototype);

  /**
   * @ngdoc method
   * @name $location#state
   *
   * @description
   * This method is getter / setter.
   *
   * Return the history state object when called without any parameter.
   *
   * Change the history state object when called with one parameter and return `$location`.
   * The state object is later passed to `pushState` or `replaceState`.
   *
   * NOTE: This method is supported only in HTML5 mode and only in browsers supporting
   * the HTML5 History API (i.e. methods `pushState` and `replaceState`). If you need to support
   * older browsers (like IE9 or Android < 4.0), don't use this method.
   *
   * @param {object=} state State object for pushState or replaceState
   * @return {object} state
   */
  Location.prototype.state = function(state) {
    if (!arguments.length) {
      return this.$$state;
    }

    if (Location !== LocationHtml5Url || !this.$$html5) {
      throw $locationMinErr('nostate', 'History API state support is available only ' +
        'in HTML5 mode and only in browsers supporting HTML5 History API');
    }
    // The user might modify `stateObject` after invoking `$location.state(stateObject)`
    // but we're changing the $$state reference to $browser.state() during the $digest
    // so the modification window is narrow.
    this.$$state = isUndefined(state) ? null : state;
    this.$$urlUpdatedByLocation = true;

    return this;
  };
});


function locationGetter(property) {
  return /** @this */ function() {
    return this[property];
  };
}


function locationGetterSetter(property, preprocess) {
  return /** @this */ function(value) {
    if (isUndefined(value)) {
      return this[property];
    }

    this[property] = preprocess(value);
    this.$$compose();

    return this;
  };
}


/**
 * @ngdoc service
 * @name $location
 *
 * @requires $rootElement
 *
 * @description
 * The $location service parses the URL in the browser address bar (based on the
 * [window.location](https://developer.mozilla.org/en/window.location)) and makes the URL
 * available to your application. Changes to the URL in the address bar are reflected into
 * $location service and changes to $location are reflected into the browser address bar.
 *
 * **The $location service:**
 *
 * - Exposes the current URL in the browser address bar, so you can
 *   - Watch and observe the URL.
 *   - Change the URL.
 * - Synchronizes the URL with the browser when the user
 *   - Changes the address bar.
 *   - Clicks the back or forward button (or clicks a History link).
 *   - Clicks on a link.
 * - Represents the URL object as a set of methods (protocol, host, port, path, search, hash).
 *
 * For more information see {@link guide/$location Developer Guide: Using $location}
 */

/**
 * @ngdoc provider
 * @name $locationProvider
 * @this
 *
 * @description
 * Use the `$locationProvider` to configure how the application deep linking paths are stored.
 */
function $LocationProvider() {
  var hashPrefix = '!',
      html5Mode = {
        enabled: false,
        requireBase: true,
        rewriteLinks: true
      };

  /**
   * @ngdoc method
   * @name $locationProvider#hashPrefix
   * @description
   * The default value for the prefix is `'!'`.
   * @param {string=} prefix Prefix for hash part (containing path and search)
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   */
  this.hashPrefix = function(prefix) {
    if (isDefined(prefix)) {
      hashPrefix = prefix;
      return this;
    } else {
      return hashPrefix;
    }
  };

  /**
   * @ngdoc method
   * @name $locationProvider#html5Mode
   * @description
   * @param {(boolean|Object)=} mode If boolean, sets `html5Mode.enabled` to value.
   *   If object, sets `enabled`, `requireBase` and `rewriteLinks` to respective values. Supported
   *   properties:
   *   - **enabled** – `{boolean}` – (default: false) If true, will rely on `history.pushState` to
   *     change urls where supported. Will fall back to hash-prefixed paths in browsers that do not
   *     support `pushState`.
   *   - **requireBase** - `{boolean}` - (default: `true`) When html5Mode is enabled, specifies
   *     whether or not a <base> tag is required to be present. If `enabled` and `requireBase` are
   *     true, and a base tag is not present, an error will be thrown when `$location` is injected.
   *     See the {@link guide/$location $location guide for more information}
   *   - **rewriteLinks** - `{boolean|string}` - (default: `true`) When html5Mode is enabled,
   *     enables/disables URL rewriting for relative links. If set to a string, URL rewriting will
   *     only happen on links with an attribute that matches the given string. For example, if set
   *     to `'internal-link'`, then the URL will only be rewritten for `<a internal-link>` links.
   *     Note that [attribute name normalization](guide/directive#normalization) does not apply
   *     here, so `'internalLink'` will **not** match `'internal-link'`.
   *
   * @returns {Object} html5Mode object if used as getter or itself (chaining) if used as setter
   */
  this.html5Mode = function(mode) {
    if (isBoolean(mode)) {
      html5Mode.enabled = mode;
      return this;
    } else if (isObject(mode)) {

      if (isBoolean(mode.enabled)) {
        html5Mode.enabled = mode.enabled;
      }

      if (isBoolean(mode.requireBase)) {
        html5Mode.requireBase = mode.requireBase;
      }

      if (isBoolean(mode.rewriteLinks) || isString(mode.rewriteLinks)) {
        html5Mode.rewriteLinks = mode.rewriteLinks;
      }

      return this;
    } else {
      return html5Mode;
    }
  };

  /**
   * @ngdoc event
   * @name $location#$locationChangeStart
   * @eventType broadcast on root scope
   * @description
   * Broadcasted before a URL will change.
   *
   * This change can be prevented by calling
   * `preventDefault` method of the event. See {@link ng.$rootScope.Scope#$on} for more
   * details about event object. Upon successful change
   * {@link ng.$location#$locationChangeSuccess $locationChangeSuccess} is fired.
   *
   * The `newState` and `oldState` parameters may be defined only in HTML5 mode and when
   * the browser supports the HTML5 History API.
   *
   * @param {Object} angularEvent Synthetic event object.
   * @param {string} newUrl New URL
   * @param {string=} oldUrl URL that was before it was changed.
   * @param {string=} newState New history state object
   * @param {string=} oldState History state object that was before it was changed.
   */

  /**
   * @ngdoc event
   * @name $location#$locationChangeSuccess
   * @eventType broadcast on root scope
   * @description
   * Broadcasted after a URL was changed.
   *
   * The `newState` and `oldState` parameters may be defined only in HTML5 mode and when
   * the browser supports the HTML5 History API.
   *
   * @param {Object} angularEvent Synthetic event object.
   * @param {string} newUrl New URL
   * @param {string=} oldUrl URL that was before it was changed.
   * @param {string=} newState New history state object
   * @param {string=} oldState History state object that was before it was changed.
   */

  this.$get = ['$rootScope', '$browser', '$sniffer', '$rootElement', '$window',
      function($rootScope, $browser, $sniffer, $rootElement, $window) {
    var $location,
        LocationMode,
        baseHref = $browser.baseHref(), // if base[href] is undefined, it defaults to ''
        initialUrl = $browser.url(),
        appBase;

    if (html5Mode.enabled) {
      if (!baseHref && html5Mode.requireBase) {
        throw $locationMinErr('nobase',
          '$location in HTML5 mode requires a <base> tag to be present!');
      }
      appBase = serverBase(initialUrl) + (baseHref || '/');
      LocationMode = $sniffer.history ? LocationHtml5Url : LocationHashbangInHtml5Url;
    } else {
      appBase = stripHash(initialUrl);
      LocationMode = LocationHashbangUrl;
    }
    var appBaseNoFile = stripFile(appBase);

    $location = new LocationMode(appBase, appBaseNoFile, '#' + hashPrefix);
    $location.$$parseLinkUrl(initialUrl, initialUrl);

    $location.$$state = $browser.state();

    var IGNORE_URI_REGEXP = /^\s*(javascript|mailto):/i;

    // Determine if two URLs are equal despite potentially having different encoding/normalizing
    //  such as $location.absUrl() vs $browser.url()
    // See https://github.com/angular/angular.js/issues/16592
    function urlsEqual(a, b) {
      return a === b || urlResolve(a).href === urlResolve(b).href;
    }

    function setBrowserUrlWithFallback(url, replace, state) {
      var oldUrl = $location.url();
      var oldState = $location.$$state;
      try {
        $browser.url(url, replace, state);

        // Make sure $location.state() returns referentially identical (not just deeply equal)
        // state object; this makes possible quick checking if the state changed in the digest
        // loop. Checking deep equality would be too expensive.
        $location.$$state = $browser.state();
      } catch (e) {
        // Restore old values if pushState fails
        $location.url(oldUrl);
        $location.$$state = oldState;

        throw e;
      }
    }

    $rootElement.on('click', function(event) {
      var rewriteLinks = html5Mode.rewriteLinks;
      // TODO(vojta): rewrite link when opening in new tab/window (in legacy browser)
      // currently we open nice url link and redirect then

      if (!rewriteLinks || event.ctrlKey || event.metaKey || event.shiftKey || event.which === 2 || event.button === 2) return;

      var elm = jqLite(event.target);

      // traverse the DOM up to find first A tag
      while (nodeName_(elm[0]) !== 'a') {
        // ignore rewriting if no A tag (reached root element, or no parent - removed from document)
        if (elm[0] === $rootElement[0] || !(elm = elm.parent())[0]) return;
      }

      if (isString(rewriteLinks) && isUndefined(elm.attr(rewriteLinks))) return;

      var absHref = elm.prop('href');
      // get the actual href attribute - see
      // http://msdn.microsoft.com/en-us/library/ie/dd347148(v=vs.85).aspx
      var relHref = elm.attr('href') || elm.attr('xlink:href');

      if (isObject(absHref) && absHref.toString() === '[object SVGAnimatedString]') {
        // SVGAnimatedString.animVal should be identical to SVGAnimatedString.baseVal, unless during
        // an animation.
        absHref = urlResolve(absHref.animVal).href;
      }

      // Ignore when url is started with javascript: or mailto:
      if (IGNORE_URI_REGEXP.test(absHref)) return;

      if (absHref && !elm.attr('target') && !event.isDefaultPrevented()) {
        if ($location.$$parseLinkUrl(absHref, relHref)) {
          // We do a preventDefault for all urls that are part of the AngularJS application,
          // in html5mode and also without, so that we are able to abort navigation without
          // getting double entries in the location history.
          event.preventDefault();
          // update location manually
          if ($location.absUrl() !== $browser.url()) {
            $rootScope.$apply();
          }
        }
      }
    });


    // rewrite hashbang url <> html5 url
    if ($location.absUrl() !== initialUrl) {
      $browser.url($location.absUrl(), true);
    }

    var initializing = true;

    // update $location when $browser url changes
    $browser.onUrlChange(function(newUrl, newState) {

      if (!startsWith(newUrl, appBaseNoFile)) {
        // If we are navigating outside of the app then force a reload
        $window.location.href = newUrl;
        return;
      }

      $rootScope.$evalAsync(function() {
        var oldUrl = $location.absUrl();
        var oldState = $location.$$state;
        var defaultPrevented;
        $location.$$parse(newUrl);
        $location.$$state = newState;

        defaultPrevented = $rootScope.$broadcast('$locationChangeStart', newUrl, oldUrl,
            newState, oldState).defaultPrevented;

        // if the location was changed by a `$locationChangeStart` handler then stop
        // processing this location change
        if ($location.absUrl() !== newUrl) return;

        if (defaultPrevented) {
          $location.$$parse(oldUrl);
          $location.$$state = oldState;
          setBrowserUrlWithFallback(oldUrl, false, oldState);
        } else {
          initializing = false;
          afterLocationChange(oldUrl, oldState);
        }
      });
      if (!$rootScope.$$phase) $rootScope.$digest();
    });

    // update browser
    $rootScope.$watch(function $locationWatch() {
      if (initializing || $location.$$urlUpdatedByLocation) {
        $location.$$urlUpdatedByLocation = false;

        var oldUrl = $browser.url();
        var newUrl = $location.absUrl();
        var oldState = $browser.state();
        var currentReplace = $location.$$replace;
        var urlOrStateChanged = !urlsEqual(oldUrl, newUrl) ||
          ($location.$$html5 && $sniffer.history && oldState !== $location.$$state);

        if (initializing || urlOrStateChanged) {
          initializing = false;

          $rootScope.$evalAsync(function() {
            var newUrl = $location.absUrl();
            var defaultPrevented = $rootScope.$broadcast('$locationChangeStart', newUrl, oldUrl,
                $location.$$state, oldState).defaultPrevented;

            // if the location was changed by a `$locationChangeStart` handler then stop
            // processing this location change
            if ($location.absUrl() !== newUrl) return;

            if (defaultPrevented) {
              $location.$$parse(oldUrl);
              $location.$$state = oldState;
            } else {
              if (urlOrStateChanged) {
                setBrowserUrlWithFallback(newUrl, currentReplace,
                                          oldState === $location.$$state ? null : $location.$$state);
              }
              afterLocationChange(oldUrl, oldState);
            }
          });
        }
      }

      $location.$$replace = false;

      // we don't need to return anything because $evalAsync will make the digest loop dirty when
      // there is a change
    });

    return $location;

    function afterLocationChange(oldUrl, oldState) {
      $rootScope.$broadcast('$locationChangeSuccess', $location.absUrl(), oldUrl,
        $location.$$state, oldState);
    }
}];
}

/**
 * @ngdoc service
 * @name $log
 * @requires $window
 *
 * @description
 * Simple service for logging. Default implementation safely writes the message
 * into the browser's console (if present).
 *
 * The main purpose of this service is to simplify debugging and troubleshooting.
 *
 * To reveal the location of the calls to `$log` in the JavaScript console,
 * you can "blackbox" the AngularJS source in your browser:
 *
 * [Mozilla description of blackboxing](https://developer.mozilla.org/en-US/docs/Tools/Debugger/How_to/Black_box_a_source).
 * [Chrome description of blackboxing](https://developer.chrome.com/devtools/docs/blackboxing).
 *
 * Note: Not all browsers support blackboxing.
 *
 * The default is to log `debug` messages. You can use
 * {@link ng.$logProvider ng.$logProvider#debugEnabled} to change this.
 *
 * @example
   <example module="logExample" name="log-service">
     <file name="script.js">
       angular.module('logExample', [])
         .controller('LogController', ['$scope', '$log', function($scope, $log) {
           $scope.$log = $log;
           $scope.message = 'Hello World!';
         }]);
     </file>
     <file name="index.html">
       <div ng-controller="LogController">
         <p>Reload this page with open console, enter text and hit the log button...</p>
         <label>Message:
         <input type="text" ng-model="message" /></label>
         <button ng-click="$log.log(message)">log</button>
         <button ng-click="$log.warn(message)">warn</button>
         <button ng-click="$log.info(message)">info</button>
         <button ng-click="$log.error(message)">error</button>
         <button ng-click="$log.debug(message)">debug</button>
       </div>
     </file>
   </example>
 */

/**
 * @ngdoc provider
 * @name $logProvider
 * @this
 *
 * @description
 * Use the `$logProvider` to configure how the application logs messages
 */
function $LogProvider() {
  var debug = true,
      self = this;

  /**
   * @ngdoc method
   * @name $logProvider#debugEnabled
   * @description
   * @param {boolean=} flag enable or disable debug level messages
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   */
  this.debugEnabled = function(flag) {
    if (isDefined(flag)) {
      debug = flag;
      return this;
    } else {
      return debug;
    }
  };

  this.$get = ['$window', function($window) {
    // Support: IE 9-11, Edge 12-14+
    // IE/Edge display errors in such a way that it requires the user to click in 4 places
    // to see the stack trace. There is no way to feature-detect it so there's a chance
    // of the user agent sniffing to go wrong but since it's only about logging, this shouldn't
    // break apps. Other browsers display errors in a sensible way and some of them map stack
    // traces along source maps if available so it makes sense to let browsers display it
    // as they want.
    var formatStackTrace = msie || /\bEdge\//.test($window.navigator && $window.navigator.userAgent);

    return {
      /**
       * @ngdoc method
       * @name $log#log
       *
       * @description
       * Write a log message
       */
      log: consoleLog('log'),

      /**
       * @ngdoc method
       * @name $log#info
       *
       * @description
       * Write an information message
       */
      info: consoleLog('info'),

      /**
       * @ngdoc method
       * @name $log#warn
       *
       * @description
       * Write a warning message
       */
      warn: consoleLog('warn'),

      /**
       * @ngdoc method
       * @name $log#error
       *
       * @description
       * Write an error message
       */
      error: consoleLog('error'),

      /**
       * @ngdoc method
       * @name $log#debug
       *
       * @description
       * Write a debug message
       */
      debug: (function() {
        var fn = consoleLog('debug');

        return function() {
          if (debug) {
            fn.apply(self, arguments);
          }
        };
      })()
    };

    function formatError(arg) {
      if (isError(arg)) {
        if (arg.stack && formatStackTrace) {
          arg = (arg.message && arg.stack.indexOf(arg.message) === -1)
              ? 'Error: ' + arg.message + '\n' + arg.stack
              : arg.stack;
        } else if (arg.sourceURL) {
          arg = arg.message + '\n' + arg.sourceURL + ':' + arg.line;
        }
      }
      return arg;
    }

    function consoleLog(type) {
      var console = $window.console || {},
          logFn = console[type] || console.log || noop;

      return function() {
        var args = [];
        forEach(arguments, function(arg) {
          args.push(formatError(arg));
        });
        // Support: IE 9 only
        // console methods don't inherit from Function.prototype in IE 9 so we can't
        // call `logFn.apply(console, args)` directly.
        return Function.prototype.apply.call(logFn, console, args);
      };
    }
  }];
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

var $parseMinErr = minErr('$parse');

var objectValueOf = {}.constructor.prototype.valueOf;

// Sandboxing AngularJS Expressions
// ------------------------------
// AngularJS expressions are no longer sandboxed. So it is now even easier to access arbitrary JS code by
// various means such as obtaining a reference to native JS functions like the Function constructor.
//
// As an example, consider the following AngularJS expression:
//
//   {}.toString.constructor('alert("evil JS code")')
//
// It is important to realize that if you create an expression from a string that contains user provided
// content then it is possible that your application contains a security vulnerability to an XSS style attack.
//
// See https://docs.angularjs.org/guide/security


function getStringValue(name) {
  // Property names must be strings. This means that non-string objects cannot be used
  // as keys in an object. Any non-string object, including a number, is typecasted
  // into a string via the toString method.
  // -- MDN, https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Operators/Property_accessors#Property_names
  //
  // So, to ensure that we are checking the same `name` that JavaScript would use, we cast it
  // to a string. It's not always possible. If `name` is an object and its `toString` method is
  // 'broken' (doesn't return a string, isn't a function, etc.), an error will be thrown:
  //
  // TypeError: Cannot convert object to primitive value
  //
  // For performance reasons, we don't catch this error here and allow it to propagate up the call
  // stack. Note that you'll get the same error in JavaScript if you try to access a property using
  // such a 'broken' object as a key.
  return name + '';
}


var OPERATORS = createMap();
forEach('+ - * / % === !== == != < > <= >= && || ! = |'.split(' '), function(operator) { OPERATORS[operator] = true; });
var ESCAPE = {'n':'\n', 'f':'\f', 'r':'\r', 't':'\t', 'v':'\v', '\'':'\'', '"':'"'};


/////////////////////////////////////////


/**
 * @constructor
 */
var Lexer = function Lexer(options) {
  this.options = options;
};

Lexer.prototype = {
  constructor: Lexer,

  lex: function(text) {
    this.text = text;
    this.index = 0;
    this.tokens = [];

    while (this.index < this.text.length) {
      var ch = this.text.charAt(this.index);
      if (ch === '"' || ch === '\'') {
        this.readString(ch);
      } else if (this.isNumber(ch) || ch === '.' && this.isNumber(this.peek())) {
        this.readNumber();
      } else if (this.isIdentifierStart(this.peekMultichar())) {
        this.readIdent();
      } else if (this.is(ch, '(){}[].,;:?')) {
        this.tokens.push({index: this.index, text: ch});
        this.index++;
      } else if (this.isWhitespace(ch)) {
        this.index++;
      } else {
        var ch2 = ch + this.peek();
        var ch3 = ch2 + this.peek(2);
        var op1 = OPERATORS[ch];
        var op2 = OPERATORS[ch2];
        var op3 = OPERATORS[ch3];
        if (op1 || op2 || op3) {
          var token = op3 ? ch3 : (op2 ? ch2 : ch);
          this.tokens.push({index: this.index, text: token, operator: true});
          this.index += token.length;
        } else {
          this.throwError('Unexpected next character ', this.index, this.index + 1);
        }
      }
    }
    return this.tokens;
  },

  is: function(ch, chars) {
    return chars.indexOf(ch) !== -1;
  },

  peek: function(i) {
    var num = i || 1;
    return (this.index + num < this.text.length) ? this.text.charAt(this.index + num) : false;
  },

  isNumber: function(ch) {
    return ('0' <= ch && ch <= '9') && typeof ch === 'string';
  },

  isWhitespace: function(ch) {
    // IE treats non-breaking space as \u00A0
    return (ch === ' ' || ch === '\r' || ch === '\t' ||
            ch === '\n' || ch === '\v' || ch === '\u00A0');
  },

  isIdentifierStart: function(ch) {
    return this.options.isIdentifierStart ?
        this.options.isIdentifierStart(ch, this.codePointAt(ch)) :
        this.isValidIdentifierStart(ch);
  },

  isValidIdentifierStart: function(ch) {
    return ('a' <= ch && ch <= 'z' ||
            'A' <= ch && ch <= 'Z' ||
            '_' === ch || ch === '$');
  },

  isIdentifierContinue: function(ch) {
    return this.options.isIdentifierContinue ?
        this.options.isIdentifierContinue(ch, this.codePointAt(ch)) :
        this.isValidIdentifierContinue(ch);
  },

  isValidIdentifierContinue: function(ch, cp) {
    return this.isValidIdentifierStart(ch, cp) || this.isNumber(ch);
  },

  codePointAt: function(ch) {
    if (ch.length === 1) return ch.charCodeAt(0);
    // eslint-disable-next-line no-bitwise
    return (ch.charCodeAt(0) << 10) + ch.charCodeAt(1) - 0x35FDC00;
  },

  peekMultichar: function() {
    var ch = this.text.charAt(this.index);
    var peek = this.peek();
    if (!peek) {
      return ch;
    }
    var cp1 = ch.charCodeAt(0);
    var cp2 = peek.charCodeAt(0);
    if (cp1 >= 0xD800 && cp1 <= 0xDBFF && cp2 >= 0xDC00 && cp2 <= 0xDFFF) {
      return ch + peek;
    }
    return ch;
  },

  isExpOperator: function(ch) {
    return (ch === '-' || ch === '+' || this.isNumber(ch));
  },

  throwError: function(error, start, end) {
    end = end || this.index;
    var colStr = (isDefined(start)
            ? 's ' + start +  '-' + this.index + ' [' + this.text.substring(start, end) + ']'
            : ' ' + end);
    throw $parseMinErr('lexerr', 'Lexer Error: {0} at column{1} in expression [{2}].',
        error, colStr, this.text);
  },

  readNumber: function() {
    var number = '';
    var start = this.index;
    while (this.index < this.text.length) {
      var ch = lowercase(this.text.charAt(this.index));
      if (ch === '.' || this.isNumber(ch)) {
        number += ch;
      } else {
        var peekCh = this.peek();
        if (ch === 'e' && this.isExpOperator(peekCh)) {
          number += ch;
        } else if (this.isExpOperator(ch) &&
            peekCh && this.isNumber(peekCh) &&
            number.charAt(number.length - 1) === 'e') {
          number += ch;
        } else if (this.isExpOperator(ch) &&
            (!peekCh || !this.isNumber(peekCh)) &&
            number.charAt(number.length - 1) === 'e') {
          this.throwError('Invalid exponent');
        } else {
          break;
        }
      }
      this.index++;
    }
    this.tokens.push({
      index: start,
      text: number,
      constant: true,
      value: Number(number)
    });
  },

  readIdent: function() {
    var start = this.index;
    this.index += this.peekMultichar().length;
    while (this.index < this.text.length) {
      var ch = this.peekMultichar();
      if (!this.isIdentifierContinue(ch)) {
        break;
      }
      this.index += ch.length;
    }
    this.tokens.push({
      index: start,
      text: this.text.slice(start, this.index),
      identifier: true
    });
  },

  readString: function(quote) {
    var start = this.index;
    this.index++;
    var string = '';
    var rawString = quote;
    var escape = false;
    while (this.index < this.text.length) {
      var ch = this.text.charAt(this.index);
      rawString += ch;
      if (escape) {
        if (ch === 'u') {
          var hex = this.text.substring(this.index + 1, this.index + 5);
          if (!hex.match(/[\da-f]{4}/i)) {
            this.throwError('Invalid unicode escape [\\u' + hex + ']');
          }
          this.index += 4;
          string += String.fromCharCode(parseInt(hex, 16));
        } else {
          var rep = ESCAPE[ch];
          string = string + (rep || ch);
        }
        escape = false;
      } else if (ch === '\\') {
        escape = true;
      } else if (ch === quote) {
        this.index++;
        this.tokens.push({
          index: start,
          text: rawString,
          constant: true,
          value: string
        });
        return;
      } else {
        string += ch;
      }
      this.index++;
    }
    this.throwError('Unterminated quote', start);
  }
};

var AST = function AST(lexer, options) {
  this.lexer = lexer;
  this.options = options;
};

AST.Program = 'Program';
AST.ExpressionStatement = 'ExpressionStatement';
AST.AssignmentExpression = 'AssignmentExpression';
AST.ConditionalExpression = 'ConditionalExpression';
AST.LogicalExpression = 'LogicalExpression';
AST.BinaryExpression = 'BinaryExpression';
AST.UnaryExpression = 'UnaryExpression';
AST.CallExpression = 'CallExpression';
AST.MemberExpression = 'MemberExpression';
AST.Identifier = 'Identifier';
AST.Literal = 'Literal';
AST.ArrayExpression = 'ArrayExpression';
AST.Property = 'Property';
AST.ObjectExpression = 'ObjectExpression';
AST.ThisExpression = 'ThisExpression';
AST.LocalsExpression = 'LocalsExpression';

// Internal use only
AST.NGValueParameter = 'NGValueParameter';

AST.prototype = {
  ast: function(text) {
    this.text = text;
    this.tokens = this.lexer.lex(text);

    var value = this.program();

    if (this.tokens.length !== 0) {
      this.throwError('is an unexpected token', this.tokens[0]);
    }

    return value;
  },

  program: function() {
    var body = [];
    while (true) {
      if (this.tokens.length > 0 && !this.peek('}', ')', ';', ']'))
        body.push(this.expressionStatement());
      if (!this.expect(';')) {
        return { type: AST.Program, body: body};
      }
    }
  },

  expressionStatement: function() {
    return { type: AST.ExpressionStatement, expression: this.filterChain() };
  },

  filterChain: function() {
    var left = this.expression();
    while (this.expect('|')) {
      left = this.filter(left);
    }
    return left;
  },

  expression: function() {
    return this.assignment();
  },

  assignment: function() {
    var result = this.ternary();
    if (this.expect('=')) {
      if (!isAssignable(result)) {
        throw $parseMinErr('lval', 'Trying to assign a value to a non l-value');
      }

      result = { type: AST.AssignmentExpression, left: result, right: this.assignment(), operator: '='};
    }
    return result;
  },

  ternary: function() {
    var test = this.logicalOR();
    var alternate;
    var consequent;
    if (this.expect('?')) {
      alternate = this.expression();
      if (this.consume(':')) {
        consequent = this.expression();
        return { type: AST.ConditionalExpression, test: test, alternate: alternate, consequent: consequent};
      }
    }
    return test;
  },

  logicalOR: function() {
    var left = this.logicalAND();
    while (this.expect('||')) {
      left = { type: AST.LogicalExpression, operator: '||', left: left, right: this.logicalAND() };
    }
    return left;
  },

  logicalAND: function() {
    var left = this.equality();
    while (this.expect('&&')) {
      left = { type: AST.LogicalExpression, operator: '&&', left: left, right: this.equality()};
    }
    return left;
  },

  equality: function() {
    var left = this.relational();
    var token;
    while ((token = this.expect('==','!=','===','!=='))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.relational() };
    }
    return left;
  },

  relational: function() {
    var left = this.additive();
    var token;
    while ((token = this.expect('<', '>', '<=', '>='))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.additive() };
    }
    return left;
  },

  additive: function() {
    var left = this.multiplicative();
    var token;
    while ((token = this.expect('+','-'))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.multiplicative() };
    }
    return left;
  },

  multiplicative: function() {
    var left = this.unary();
    var token;
    while ((token = this.expect('*','/','%'))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.unary() };
    }
    return left;
  },

  unary: function() {
    var token;
    if ((token = this.expect('+', '-', '!'))) {
      return { type: AST.UnaryExpression, operator: token.text, prefix: true, argument: this.unary() };
    } else {
      return this.primary();
    }
  },

  primary: function() {
    var primary;
    if (this.expect('(')) {
      primary = this.filterChain();
      this.consume(')');
    } else if (this.expect('[')) {
      primary = this.arrayDeclaration();
    } else if (this.expect('{')) {
      primary = this.object();
    } else if (this.selfReferential.hasOwnProperty(this.peek().text)) {
      primary = copy(this.selfReferential[this.consume().text]);
    } else if (this.options.literals.hasOwnProperty(this.peek().text)) {
      primary = { type: AST.Literal, value: this.options.literals[this.consume().text]};
    } else if (this.peek().identifier) {
      primary = this.identifier();
    } else if (this.peek().constant) {
      primary = this.constant();
    } else {
      this.throwError('not a primary expression', this.peek());
    }

    var next;
    while ((next = this.expect('(', '[', '.'))) {
      if (next.text === '(') {
        primary = {type: AST.CallExpression, callee: primary, arguments: this.parseArguments() };
        this.consume(')');
      } else if (next.text === '[') {
        primary = { type: AST.MemberExpression, object: primary, property: this.expression(), computed: true };
        this.consume(']');
      } else if (next.text === '.') {
        primary = { type: AST.MemberExpression, object: primary, property: this.identifier(), computed: false };
      } else {
        this.throwError('IMPOSSIBLE');
      }
    }
    return primary;
  },

  filter: function(baseExpression) {
    var args = [baseExpression];
    var result = {type: AST.CallExpression, callee: this.identifier(), arguments: args, filter: true};

    while (this.expect(':')) {
      args.push(this.expression());
    }

    return result;
  },

  parseArguments: function() {
    var args = [];
    if (this.peekToken().text !== ')') {
      do {
        args.push(this.filterChain());
      } while (this.expect(','));
    }
    return args;
  },

  identifier: function() {
    var token = this.consume();
    if (!token.identifier) {
      this.throwError('is not a valid identifier', token);
    }
    return { type: AST.Identifier, name: token.text };
  },

  constant: function() {
    // TODO check that it is a constant
    return { type: AST.Literal, value: this.consume().value };
  },

  arrayDeclaration: function() {
    var elements = [];
    if (this.peekToken().text !== ']') {
      do {
        if (this.peek(']')) {
          // Support trailing commas per ES5.1.
          break;
        }
        elements.push(this.expression());
      } while (this.expect(','));
    }
    this.consume(']');

    return { type: AST.ArrayExpression, elements: elements };
  },

  object: function() {
    var properties = [], property;
    if (this.peekToken().text !== '}') {
      do {
        if (this.peek('}')) {
          // Support trailing commas per ES5.1.
          break;
        }
        property = {type: AST.Property, kind: 'init'};
        if (this.peek().constant) {
          property.key = this.constant();
          property.computed = false;
          this.consume(':');
          property.value = this.expression();
        } else if (this.peek().identifier) {
          property.key = this.identifier();
          property.computed = false;
          if (this.peek(':')) {
            this.consume(':');
            property.value = this.expression();
          } else {
            property.value = property.key;
          }
        } else if (this.peek('[')) {
          this.consume('[');
          property.key = this.expression();
          this.consume(']');
          property.computed = true;
          this.consume(':');
          property.value = this.expression();
        } else {
          this.throwError('invalid key', this.peek());
        }
        properties.push(property);
      } while (this.expect(','));
    }
    this.consume('}');

    return {type: AST.ObjectExpression, properties: properties };
  },

  throwError: function(msg, token) {
    throw $parseMinErr('syntax',
        'Syntax Error: Token \'{0}\' {1} at column {2} of the expression [{3}] starting at [{4}].',
          token.text, msg, (token.index + 1), this.text, this.text.substring(token.index));
  },

  consume: function(e1) {
    if (this.tokens.length === 0) {
      throw $parseMinErr('ueoe', 'Unexpected end of expression: {0}', this.text);
    }

    var token = this.expect(e1);
    if (!token) {
      this.throwError('is unexpected, expecting [' + e1 + ']', this.peek());
    }
    return token;
  },

  peekToken: function() {
    if (this.tokens.length === 0) {
      throw $parseMinErr('ueoe', 'Unexpected end of expression: {0}', this.text);
    }
    return this.tokens[0];
  },

  peek: function(e1, e2, e3, e4) {
    return this.peekAhead(0, e1, e2, e3, e4);
  },

  peekAhead: function(i, e1, e2, e3, e4) {
    if (this.tokens.length > i) {
      var token = this.tokens[i];
      var t = token.text;
      if (t === e1 || t === e2 || t === e3 || t === e4 ||
          (!e1 && !e2 && !e3 && !e4)) {
        return token;
      }
    }
    return false;
  },

  expect: function(e1, e2, e3, e4) {
    var token = this.peek(e1, e2, e3, e4);
    if (token) {
      this.tokens.shift();
      return token;
    }
    return false;
  },

  selfReferential: {
    'this': {type: AST.ThisExpression },
    '$locals': {type: AST.LocalsExpression }
  }
};

function ifDefined(v, d) {
  return typeof v !== 'undefined' ? v : d;
}

function plusFn(l, r) {
  if (typeof l === 'undefined') return r;
  if (typeof r === 'undefined') return l;
  return l + r;
}

function isStateless($filter, filterName) {
  var fn = $filter(filterName);
  return !fn.$stateful;
}

var PURITY_ABSOLUTE = 1;
var PURITY_RELATIVE = 2;

// Detect nodes which could depend on non-shallow state of objects
function isPure(node, parentIsPure) {
  switch (node.type) {
    // Computed members might invoke a stateful toString()
    case AST.MemberExpression:
      if (node.computed) {
        return false;
      }
      break;

    // Unary always convert to primative
    case AST.UnaryExpression:
      return PURITY_ABSOLUTE;

    // The binary + operator can invoke a stateful toString().
    case AST.BinaryExpression:
      return node.operator !== '+' ? PURITY_ABSOLUTE : false;

    // Functions / filters probably read state from within objects
    case AST.CallExpression:
      return false;
  }

  return (undefined === parentIsPure) ? PURITY_RELATIVE : parentIsPure;
}

function findConstantAndWatchExpressions(ast, $filter, parentIsPure) {
  var allConstants;
  var argsToWatch;
  var isStatelessFilter;

  var astIsPure = ast.isPure = isPure(ast, parentIsPure);

  switch (ast.type) {
  case AST.Program:
    allConstants = true;
    forEach(ast.body, function(expr) {
      findConstantAndWatchExpressions(expr.expression, $filter, astIsPure);
      allConstants = allConstants && expr.expression.constant;
    });
    ast.constant = allConstants;
    break;
  case AST.Literal:
    ast.constant = true;
    ast.toWatch = [];
    break;
  case AST.UnaryExpression:
    findConstantAndWatchExpressions(ast.argument, $filter, astIsPure);
    ast.constant = ast.argument.constant;
    ast.toWatch = ast.argument.toWatch;
    break;
  case AST.BinaryExpression:
    findConstantAndWatchExpressions(ast.left, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.right, $filter, astIsPure);
    ast.constant = ast.left.constant && ast.right.constant;
    ast.toWatch = ast.left.toWatch.concat(ast.right.toWatch);
    break;
  case AST.LogicalExpression:
    findConstantAndWatchExpressions(ast.left, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.right, $filter, astIsPure);
    ast.constant = ast.left.constant && ast.right.constant;
    ast.toWatch = ast.constant ? [] : [ast];
    break;
  case AST.ConditionalExpression:
    findConstantAndWatchExpressions(ast.test, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.alternate, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.consequent, $filter, astIsPure);
    ast.constant = ast.test.constant && ast.alternate.constant && ast.consequent.constant;
    ast.toWatch = ast.constant ? [] : [ast];
    break;
  case AST.Identifier:
    ast.constant = false;
    ast.toWatch = [ast];
    break;
  case AST.MemberExpression:
    findConstantAndWatchExpressions(ast.object, $filter, astIsPure);
    if (ast.computed) {
      findConstantAndWatchExpressions(ast.property, $filter, astIsPure);
    }
    ast.constant = ast.object.constant && (!ast.computed || ast.property.constant);
    ast.toWatch = ast.constant ? [] : [ast];
    break;
  case AST.CallExpression:
    isStatelessFilter = ast.filter ? isStateless($filter, ast.callee.name) : false;
    allConstants = isStatelessFilter;
    argsToWatch = [];
    forEach(ast.arguments, function(expr) {
      findConstantAndWatchExpressions(expr, $filter, astIsPure);
      allConstants = allConstants && expr.constant;
      argsToWatch.push.apply(argsToWatch, expr.toWatch);
    });
    ast.constant = allConstants;
    ast.toWatch = isStatelessFilter ? argsToWatch : [ast];
    break;
  case AST.AssignmentExpression:
    findConstantAndWatchExpressions(ast.left, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.right, $filter, astIsPure);
    ast.constant = ast.left.constant && ast.right.constant;
    ast.toWatch = [ast];
    break;
  case AST.ArrayExpression:
    allConstants = true;
    argsToWatch = [];
    forEach(ast.elements, function(expr) {
      findConstantAndWatchExpressions(expr, $filter, astIsPure);
      allConstants = allConstants && expr.constant;
      argsToWatch.push.apply(argsToWatch, expr.toWatch);
    });
    ast.constant = allConstants;
    ast.toWatch = argsToWatch;
    break;
  case AST.ObjectExpression:
    allConstants = true;
    argsToWatch = [];
    forEach(ast.properties, function(property) {
      findConstantAndWatchExpressions(property.value, $filter, astIsPure);
      allConstants = allConstants && property.value.constant;
      argsToWatch.push.apply(argsToWatch, property.value.toWatch);
      if (property.computed) {
        //`{[key]: value}` implicitly does `key.toString()` which may be non-pure
        findConstantAndWatchExpressions(property.key, $filter, /*parentIsPure=*/false);
        allConstants = allConstants && property.key.constant;
        argsToWatch.push.apply(argsToWatch, property.key.toWatch);
      }
    });
    ast.constant = allConstants;
    ast.toWatch = argsToWatch;
    break;
  case AST.ThisExpression:
    ast.constant = false;
    ast.toWatch = [];
    break;
  case AST.LocalsExpression:
    ast.constant = false;
    ast.toWatch = [];
    break;
  }
}

function getInputs(body) {
  if (body.length !== 1) return;
  var lastExpression = body[0].expression;
  var candidate = lastExpression.toWatch;
  if (candidate.length !== 1) return candidate;
  return candidate[0] !== lastExpression ? candidate : undefined;
}

function isAssignable(ast) {
  return ast.type === AST.Identifier || ast.type === AST.MemberExpression;
}

function assignableAST(ast) {
  if (ast.body.length === 1 && isAssignable(ast.body[0].expression)) {
    return {type: AST.AssignmentExpression, left: ast.body[0].expression, right: {type: AST.NGValueParameter}, operator: '='};
  }
}

function isLiteral(ast) {
  return ast.body.length === 0 ||
      ast.body.length === 1 && (
      ast.body[0].expression.type === AST.Literal ||
      ast.body[0].expression.type === AST.ArrayExpression ||
      ast.body[0].expression.type === AST.ObjectExpression);
}

function isConstant(ast) {
  return ast.constant;
}

function ASTCompiler($filter) {
  this.$filter = $filter;
}

ASTCompiler.prototype = {
  compile: function(ast) {
    var self = this;
    this.state = {
      nextId: 0,
      filters: {},
      fn: {vars: [], body: [], own: {}},
      assign: {vars: [], body: [], own: {}},
      inputs: []
    };
    findConstantAndWatchExpressions(ast, self.$filter);
    var extra = '';
    var assignable;
    this.stage = 'assign';
    if ((assignable = assignableAST(ast))) {
      this.state.computing = 'assign';
      var result = this.nextId();
      this.recurse(assignable, result);
      this.return_(result);
      extra = 'fn.assign=' + this.generateFunction('assign', 's,v,l');
    }
    var toWatch = getInputs(ast.body);
    self.stage = 'inputs';
    forEach(toWatch, function(watch, key) {
      var fnKey = 'fn' + key;
      self.state[fnKey] = {vars: [], body: [], own: {}};
      self.state.computing = fnKey;
      var intoId = self.nextId();
      self.recurse(watch, intoId);
      self.return_(intoId);
      self.state.inputs.push({name: fnKey, isPure: watch.isPure});
      watch.watchId = key;
    });
    this.state.computing = 'fn';
    this.stage = 'main';
    this.recurse(ast);
    var fnString =
      // The build and minification steps remove the string "use strict" from the code, but this is done using a regex.
      // This is a workaround for this until we do a better job at only removing the prefix only when we should.
      '"' + this.USE + ' ' + this.STRICT + '";\n' +
      this.filterPrefix() +
      'var fn=' + this.generateFunction('fn', 's,l,a,i') +
      extra +
      this.watchFns() +
      'return fn;';

    // eslint-disable-next-line no-new-func
    var fn = (new Function('$filter',
        'getStringValue',
        'ifDefined',
        'plus',
        fnString))(
          this.$filter,
          getStringValue,
          ifDefined,
          plusFn);
    this.state = this.stage = undefined;
    return fn;
  },

  USE: 'use',

  STRICT: 'strict',

  watchFns: function() {
    var result = [];
    var inputs = this.state.inputs;
    var self = this;
    forEach(inputs, function(input) {
      result.push('var ' + input.name + '=' + self.generateFunction(input.name, 's'));
      if (input.isPure) {
        result.push(input.name, '.isPure=' + JSON.stringify(input.isPure) + ';');
      }
    });
    if (inputs.length) {
      result.push('fn.inputs=[' + inputs.map(function(i) { return i.name; }).join(',') + '];');
    }
    return result.join('');
  },

  generateFunction: function(name, params) {
    return 'function(' + params + '){' +
        this.varsPrefix(name) +
        this.body(name) +
        '};';
  },

  filterPrefix: function() {
    var parts = [];
    var self = this;
    forEach(this.state.filters, function(id, filter) {
      parts.push(id + '=$filter(' + self.escape(filter) + ')');
    });
    if (parts.length) return 'var ' + parts.join(',') + ';';
    return '';
  },

  varsPrefix: function(section) {
    return this.state[section].vars.length ? 'var ' + this.state[section].vars.join(',') + ';' : '';
  },

  body: function(section) {
    return this.state[section].body.join('');
  },

  recurse: function(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck) {
    var left, right, self = this, args, expression, computed;
    recursionFn = recursionFn || noop;
    if (!skipWatchIdCheck && isDefined(ast.watchId)) {
      intoId = intoId || this.nextId();
      this.if_('i',
        this.lazyAssign(intoId, this.computedMember('i', ast.watchId)),
        this.lazyRecurse(ast, intoId, nameId, recursionFn, create, true)
      );
      return;
    }
    switch (ast.type) {
    case AST.Program:
      forEach(ast.body, function(expression, pos) {
        self.recurse(expression.expression, undefined, undefined, function(expr) { right = expr; });
        if (pos !== ast.body.length - 1) {
          self.current().body.push(right, ';');
        } else {
          self.return_(right);
        }
      });
      break;
    case AST.Literal:
      expression = this.escape(ast.value);
      this.assign(intoId, expression);
      recursionFn(intoId || expression);
      break;
    case AST.UnaryExpression:
      this.recurse(ast.argument, undefined, undefined, function(expr) { right = expr; });
      expression = ast.operator + '(' + this.ifDefined(right, 0) + ')';
      this.assign(intoId, expression);
      recursionFn(expression);
      break;
    case AST.BinaryExpression:
      this.recurse(ast.left, undefined, undefined, function(expr) { left = expr; });
      this.recurse(ast.right, undefined, undefined, function(expr) { right = expr; });
      if (ast.operator === '+') {
        expression = this.plus(left, right);
      } else if (ast.operator === '-') {
        expression = this.ifDefined(left, 0) + ast.operator + this.ifDefined(right, 0);
      } else {
        expression = '(' + left + ')' + ast.operator + '(' + right + ')';
      }
      this.assign(intoId, expression);
      recursionFn(expression);
      break;
    case AST.LogicalExpression:
      intoId = intoId || this.nextId();
      self.recurse(ast.left, intoId);
      self.if_(ast.operator === '&&' ? intoId : self.not(intoId), self.lazyRecurse(ast.right, intoId));
      recursionFn(intoId);
      break;
    case AST.ConditionalExpression:
      intoId = intoId || this.nextId();
      self.recurse(ast.test, intoId);
      self.if_(intoId, self.lazyRecurse(ast.alternate, intoId), self.lazyRecurse(ast.consequent, intoId));
      recursionFn(intoId);
      break;
    case AST.Identifier:
      intoId = intoId || this.nextId();
      if (nameId) {
        nameId.context = self.stage === 'inputs' ? 's' : this.assign(this.nextId(), this.getHasOwnProperty('l', ast.name) + '?l:s');
        nameId.computed = false;
        nameId.name = ast.name;
      }
      self.if_(self.stage === 'inputs' || self.not(self.getHasOwnProperty('l', ast.name)),
        function() {
          self.if_(self.stage === 'inputs' || 's', function() {
            if (create && create !== 1) {
              self.if_(
                self.isNull(self.nonComputedMember('s', ast.name)),
                self.lazyAssign(self.nonComputedMember('s', ast.name), '{}'));
            }
            self.assign(intoId, self.nonComputedMember('s', ast.name));
          });
        }, intoId && self.lazyAssign(intoId, self.nonComputedMember('l', ast.name))
        );
      recursionFn(intoId);
      break;
    case AST.MemberExpression:
      left = nameId && (nameId.context = this.nextId()) || this.nextId();
      intoId = intoId || this.nextId();
      self.recurse(ast.object, left, undefined, function() {
        self.if_(self.notNull(left), function() {
          if (ast.computed) {
            right = self.nextId();
            self.recurse(ast.property, right);
            self.getStringValue(right);
            if (create && create !== 1) {
              self.if_(self.not(self.computedMember(left, right)), self.lazyAssign(self.computedMember(left, right), '{}'));
            }
            expression = self.computedMember(left, right);
            self.assign(intoId, expression);
            if (nameId) {
              nameId.computed = true;
              nameId.name = right;
            }
          } else {
            if (create && create !== 1) {
              self.if_(self.isNull(self.nonComputedMember(left, ast.property.name)), self.lazyAssign(self.nonComputedMember(left, ast.property.name), '{}'));
            }
            expression = self.nonComputedMember(left, ast.property.name);
            self.assign(intoId, expression);
            if (nameId) {
              nameId.computed = false;
              nameId.name = ast.property.name;
            }
          }
        }, function() {
          self.assign(intoId, 'undefined');
        });
        recursionFn(intoId);
      }, !!create);
      break;
    case AST.CallExpression:
      intoId = intoId || this.nextId();
      if (ast.filter) {
        right = self.filter(ast.callee.name);
        args = [];
        forEach(ast.arguments, function(expr) {
          var argument = self.nextId();
          self.recurse(expr, argument);
          args.push(argument);
        });
        expression = right + '(' + args.join(',') + ')';
        self.assign(intoId, expression);
        recursionFn(intoId);
      } else {
        right = self.nextId();
        left = {};
        args = [];
        self.recurse(ast.callee, right, left, function() {
          self.if_(self.notNull(right), function() {
            forEach(ast.arguments, function(expr) {
              self.recurse(expr, ast.constant ? undefined : self.nextId(), undefined, function(argument) {
                args.push(argument);
              });
            });
            if (left.name) {
              expression = self.member(left.context, left.name, left.computed) + '(' + args.join(',') + ')';
            } else {
              expression = right + '(' + args.join(',') + ')';
            }
            self.assign(intoId, expression);
          }, function() {
            self.assign(intoId, 'undefined');
          });
          recursionFn(intoId);
        });
      }
      break;
    case AST.AssignmentExpression:
      right = this.nextId();
      left = {};
      this.recurse(ast.left, undefined, left, function() {
        self.if_(self.notNull(left.context), function() {
          self.recurse(ast.right, right);
          expression = self.member(left.context, left.name, left.computed) + ast.operator + right;
          self.assign(intoId, expression);
          recursionFn(intoId || expression);
        });
      }, 1);
      break;
    case AST.ArrayExpression:
      args = [];
      forEach(ast.elements, function(expr) {
        self.recurse(expr, ast.constant ? undefined : self.nextId(), undefined, function(argument) {
          args.push(argument);
        });
      });
      expression = '[' + args.join(',') + ']';
      this.assign(intoId, expression);
      recursionFn(intoId || expression);
      break;
    case AST.ObjectExpression:
      args = [];
      computed = false;
      forEach(ast.properties, function(property) {
        if (property.computed) {
          computed = true;
        }
      });
      if (computed) {
        intoId = intoId || this.nextId();
        this.assign(intoId, '{}');
        forEach(ast.properties, function(property) {
          if (property.computed) {
            left = self.nextId();
            self.recurse(property.key, left);
          } else {
            left = property.key.type === AST.Identifier ?
                       property.key.name :
                       ('' + property.key.value);
          }
          right = self.nextId();
          self.recurse(property.value, right);
          self.assign(self.member(intoId, left, property.computed), right);
        });
      } else {
        forEach(ast.properties, function(property) {
          self.recurse(property.value, ast.constant ? undefined : self.nextId(), undefined, function(expr) {
            args.push(self.escape(
                property.key.type === AST.Identifier ? property.key.name :
                  ('' + property.key.value)) +
                ':' + expr);
          });
        });
        expression = '{' + args.join(',') + '}';
        this.assign(intoId, expression);
      }
      recursionFn(intoId || expression);
      break;
    case AST.ThisExpression:
      this.assign(intoId, 's');
      recursionFn(intoId || 's');
      break;
    case AST.LocalsExpression:
      this.assign(intoId, 'l');
      recursionFn(intoId || 'l');
      break;
    case AST.NGValueParameter:
      this.assign(intoId, 'v');
      recursionFn(intoId || 'v');
      break;
    }
  },

  getHasOwnProperty: function(element, property) {
    var key = element + '.' + property;
    var own = this.current().own;
    if (!own.hasOwnProperty(key)) {
      own[key] = this.nextId(false, element + '&&(' + this.escape(property) + ' in ' + element + ')');
    }
    return own[key];
  },

  assign: function(id, value) {
    if (!id) return;
    this.current().body.push(id, '=', value, ';');
    return id;
  },

  filter: function(filterName) {
    if (!this.state.filters.hasOwnProperty(filterName)) {
      this.state.filters[filterName] = this.nextId(true);
    }
    return this.state.filters[filterName];
  },

  ifDefined: function(id, defaultValue) {
    return 'ifDefined(' + id + ',' + this.escape(defaultValue) + ')';
  },

  plus: function(left, right) {
    return 'plus(' + left + ',' + right + ')';
  },

  return_: function(id) {
    this.current().body.push('return ', id, ';');
  },

  if_: function(test, alternate, consequent) {
    if (test === true) {
      alternate();
    } else {
      var body = this.current().body;
      body.push('if(', test, '){');
      alternate();
      body.push('}');
      if (consequent) {
        body.push('else{');
        consequent();
        body.push('}');
      }
    }
  },

  not: function(expression) {
    return '!(' + expression + ')';
  },

  isNull: function(expression) {
    return expression + '==null';
  },

  notNull: function(expression) {
    return expression + '!=null';
  },

  nonComputedMember: function(left, right) {
    var SAFE_IDENTIFIER = /^[$_a-zA-Z][$_a-zA-Z0-9]*$/;
    var UNSAFE_CHARACTERS = /[^$_a-zA-Z0-9]/g;
    if (SAFE_IDENTIFIER.test(right)) {
      return left + '.' + right;
    } else {
      return left  + '["' + right.replace(UNSAFE_CHARACTERS, this.stringEscapeFn) + '"]';
    }
  },

  computedMember: function(left, right) {
    return left + '[' + right + ']';
  },

  member: function(left, right, computed) {
    if (computed) return this.computedMember(left, right);
    return this.nonComputedMember(left, right);
  },

  getStringValue: function(item) {
    this.assign(item, 'getStringValue(' + item + ')');
  },

  lazyRecurse: function(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck) {
    var self = this;
    return function() {
      self.recurse(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck);
    };
  },

  lazyAssign: function(id, value) {
    var self = this;
    return function() {
      self.assign(id, value);
    };
  },

  stringEscapeRegex: /[^ a-zA-Z0-9]/g,

  stringEscapeFn: function(c) {
    return '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4);
  },

  escape: function(value) {
    if (isString(value)) return '\'' + value.replace(this.stringEscapeRegex, this.stringEscapeFn) + '\'';
    if (isNumber(value)) return value.toString();
    if (value === true) return 'true';
    if (value === false) return 'false';
    if (value === null) return 'null';
    if (typeof value === 'undefined') return 'undefined';

    throw $parseMinErr('esc', 'IMPOSSIBLE');
  },

  nextId: function(skip, init) {
    var id = 'v' + (this.state.nextId++);
    if (!skip) {
      this.current().vars.push(id + (init ? '=' + init : ''));
    }
    return id;
  },

  current: function() {
    return this.state[this.state.computing];
  }
};


function ASTInterpreter($filter) {
  this.$filter = $filter;
}

ASTInterpreter.prototype = {
  compile: function(ast) {
    var self = this;
    findConstantAndWatchExpressions(ast, self.$filter);
    var assignable;
    var assign;
    if ((assignable = assignableAST(ast))) {
      assign = this.recurse(assignable);
    }
    var toWatch = getInputs(ast.body);
    var inputs;
    if (toWatch) {
      inputs = [];
      forEach(toWatch, function(watch, key) {
        var input = self.recurse(watch);
        input.isPure = watch.isPure;
        watch.input = input;
        inputs.push(input);
        watch.watchId = key;
      });
    }
    var expressions = [];
    forEach(ast.body, function(expression) {
      expressions.push(self.recurse(expression.expression));
    });
    var fn = ast.body.length === 0 ? noop :
             ast.body.length === 1 ? expressions[0] :
             function(scope, locals) {
               var lastValue;
               forEach(expressions, function(exp) {
                 lastValue = exp(scope, locals);
               });
               return lastValue;
             };
    if (assign) {
      fn.assign = function(scope, value, locals) {
        return assign(scope, locals, value);
      };
    }
    if (inputs) {
      fn.inputs = inputs;
    }
    return fn;
  },

  recurse: function(ast, context, create) {
    var left, right, self = this, args;
    if (ast.input) {
      return this.inputs(ast.input, ast.watchId);
    }
    switch (ast.type) {
    case AST.Literal:
      return this.value(ast.value, context);
    case AST.UnaryExpression:
      right = this.recurse(ast.argument);
      return this['unary' + ast.operator](right, context);
    case AST.BinaryExpression:
      left = this.recurse(ast.left);
      right = this.recurse(ast.right);
      return this['binary' + ast.operator](left, right, context);
    case AST.LogicalExpression:
      left = this.recurse(ast.left);
      right = this.recurse(ast.right);
      return this['binary' + ast.operator](left, right, context);
    case AST.ConditionalExpression:
      return this['ternary?:'](
        this.recurse(ast.test),
        this.recurse(ast.alternate),
        this.recurse(ast.consequent),
        context
      );
    case AST.Identifier:
      return self.identifier(ast.name, context, create);
    case AST.MemberExpression:
      left = this.recurse(ast.object, false, !!create);
      if (!ast.computed) {
        right = ast.property.name;
      }
      if (ast.computed) right = this.recurse(ast.property);
      return ast.computed ?
        this.computedMember(left, right, context, create) :
        this.nonComputedMember(left, right, context, create);
    case AST.CallExpression:
      args = [];
      forEach(ast.arguments, function(expr) {
        args.push(self.recurse(expr));
      });
      if (ast.filter) right = this.$filter(ast.callee.name);
      if (!ast.filter) right = this.recurse(ast.callee, true);
      return ast.filter ?
        function(scope, locals, assign, inputs) {
          var values = [];
          for (var i = 0; i < args.length; ++i) {
            values.push(args[i](scope, locals, assign, inputs));
          }
          var value = right.apply(undefined, values, inputs);
          return context ? {context: undefined, name: undefined, value: value} : value;
        } :
        function(scope, locals, assign, inputs) {
          var rhs = right(scope, locals, assign, inputs);
          var value;
          if (rhs.value != null) {
            var values = [];
            for (var i = 0; i < args.length; ++i) {
              values.push(args[i](scope, locals, assign, inputs));
            }
            value = rhs.value.apply(rhs.context, values);
          }
          return context ? {value: value} : value;
        };
    case AST.AssignmentExpression:
      left = this.recurse(ast.left, true, 1);
      right = this.recurse(ast.right);
      return function(scope, locals, assign, inputs) {
        var lhs = left(scope, locals, assign, inputs);
        var rhs = right(scope, locals, assign, inputs);
        lhs.context[lhs.name] = rhs;
        return context ? {value: rhs} : rhs;
      };
    case AST.ArrayExpression:
      args = [];
      forEach(ast.elements, function(expr) {
        args.push(self.recurse(expr));
      });
      return function(scope, locals, assign, inputs) {
        var value = [];
        for (var i = 0; i < args.length; ++i) {
          value.push(args[i](scope, locals, assign, inputs));
        }
        return context ? {value: value} : value;
      };
    case AST.ObjectExpression:
      args = [];
      forEach(ast.properties, function(property) {
        if (property.computed) {
          args.push({key: self.recurse(property.key),
                     computed: true,
                     value: self.recurse(property.value)
          });
        } else {
          args.push({key: property.key.type === AST.Identifier ?
                          property.key.name :
                          ('' + property.key.value),
                     computed: false,
                     value: self.recurse(property.value)
          });
        }
      });
      return function(scope, locals, assign, inputs) {
        var value = {};
        for (var i = 0; i < args.length; ++i) {
          if (args[i].computed) {
            value[args[i].key(scope, locals, assign, inputs)] = args[i].value(scope, locals, assign, inputs);
          } else {
            value[args[i].key] = args[i].value(scope, locals, assign, inputs);
          }
        }
        return context ? {value: value} : value;
      };
    case AST.ThisExpression:
      return function(scope) {
        return context ? {value: scope} : scope;
      };
    case AST.LocalsExpression:
      return function(scope, locals) {
        return context ? {value: locals} : locals;
      };
    case AST.NGValueParameter:
      return function(scope, locals, assign) {
        return context ? {value: assign} : assign;
      };
    }
  },

  'unary+': function(argument, context) {
    return function(scope, locals, assign, inputs) {
      var arg = argument(scope, locals, assign, inputs);
      if (isDefined(arg)) {
        arg = +arg;
      } else {
        arg = 0;
      }
      return context ? {value: arg} : arg;
    };
  },
  'unary-': function(argument, context) {
    return function(scope, locals, assign, inputs) {
      var arg = argument(scope, locals, assign, inputs);
      if (isDefined(arg)) {
        arg = -arg;
      } else {
        arg = -0;
      }
      return context ? {value: arg} : arg;
    };
  },
  'unary!': function(argument, context) {
    return function(scope, locals, assign, inputs) {
      var arg = !argument(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary+': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      var rhs = right(scope, locals, assign, inputs);
      var arg = plusFn(lhs, rhs);
      return context ? {value: arg} : arg;
    };
  },
  'binary-': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      var rhs = right(scope, locals, assign, inputs);
      var arg = (isDefined(lhs) ? lhs : 0) - (isDefined(rhs) ? rhs : 0);
      return context ? {value: arg} : arg;
    };
  },
  'binary*': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) * right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary/': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) / right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary%': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) % right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary===': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) === right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary!==': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) !== right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary==': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      // eslint-disable-next-line eqeqeq
      var arg = left(scope, locals, assign, inputs) == right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary!=': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      // eslint-disable-next-line eqeqeq
      var arg = left(scope, locals, assign, inputs) != right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary<': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) < right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary>': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) > right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary<=': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) <= right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary>=': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) >= right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary&&': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) && right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary||': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) || right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'ternary?:': function(test, alternate, consequent, context) {
    return function(scope, locals, assign, inputs) {
      var arg = test(scope, locals, assign, inputs) ? alternate(scope, locals, assign, inputs) : consequent(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  value: function(value, context) {
    return function() { return context ? {context: undefined, name: undefined, value: value} : value; };
  },
  identifier: function(name, context, create) {
    return function(scope, locals, assign, inputs) {
      var base = locals && (name in locals) ? locals : scope;
      if (create && create !== 1 && base && base[name] == null) {
        base[name] = {};
      }
      var value = base ? base[name] : undefined;
      if (context) {
        return {context: base, name: name, value: value};
      } else {
        return value;
      }
    };
  },
  computedMember: function(left, right, context, create) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      var rhs;
      var value;
      if (lhs != null) {
        rhs = right(scope, locals, assign, inputs);
        rhs = getStringValue(rhs);
        if (create && create !== 1) {
          if (lhs && !(lhs[rhs])) {
            lhs[rhs] = {};
          }
        }
        value = lhs[rhs];
      }
      if (context) {
        return {context: lhs, name: rhs, value: value};
      } else {
        return value;
      }
    };
  },
  nonComputedMember: function(left, right, context, create) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      if (create && create !== 1) {
        if (lhs && lhs[right] == null) {
          lhs[right] = {};
        }
      }
      var value = lhs != null ? lhs[right] : undefined;
      if (context) {
        return {context: lhs, name: right, value: value};
      } else {
        return value;
      }
    };
  },
  inputs: function(input, watchId) {
    return function(scope, value, locals, inputs) {
      if (inputs) return inputs[watchId];
      return input(scope, value, locals);
    };
  }
};

/**
 * @constructor
 */
function Parser(lexer, $filter, options) {
  this.ast = new AST(lexer, options);
  this.astCompiler = options.csp ? new ASTInterpreter($filter) :
                                   new ASTCompiler($filter);
}

Parser.prototype = {
  constructor: Parser,

  parse: function(text) {
    var ast = this.getAst(text);
    var fn = this.astCompiler.compile(ast.ast);
    fn.literal = isLiteral(ast.ast);
    fn.constant = isConstant(ast.ast);
    fn.oneTime = ast.oneTime;
    return fn;
  },

  getAst: function(exp) {
    var oneTime = false;
    exp = exp.trim();

    if (exp.charAt(0) === ':' && exp.charAt(1) === ':') {
      oneTime = true;
      exp = exp.substring(2);
    }
    return {
      ast: this.ast.ast(exp),
      oneTime: oneTime
    };
  }
};

function getValueOf(value) {
  return isFunction(value.valueOf) ? value.valueOf() : objectValueOf.call(value);
}

///////////////////////////////////

/**
 * @ngdoc service
 * @name $parse
 * @kind function
 *
 * @description
 *
 * Converts AngularJS {@link guide/expression expression} into a function.
 *
 * ```js
 *   var getter = $parse('user.name');
 *   var setter = getter.assign;
 *   var context = {user:{name:'AngularJS'}};
 *   var locals = {user:{name:'local'}};
 *
 *   expect(getter(context)).toEqual('AngularJS');
 *   setter(context, 'newValue');
 *   expect(context.user.name).toEqual('newValue');
 *   expect(getter(context, locals)).toEqual('local');
 * ```
 *
 *
 * @param {string} expression String expression to compile.
 * @returns {function(context, locals)} a function which represents the compiled expression:
 *
 *    * `context` – `{object}` – an object against which any expressions embedded in the strings
 *      are evaluated against (typically a scope object).
 *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
 *      `context`.
 *
 *    The returned function also has the following properties:
 *      * `literal` – `{boolean}` – whether the expression's top-level node is a JavaScript
 *        literal.
 *      * `constant` – `{boolean}` – whether the expression is made entirely of JavaScript
 *        constant literals.
 *      * `assign` – `{?function(context, value)}` – if the expression is assignable, this will be
 *        set to a function to change its value on the given context.
 *
 */


/**
 * @ngdoc provider
 * @name $parseProvider
 * @this
 *
 * @description
 * `$parseProvider` can be used for configuring the default behavior of the {@link ng.$parse $parse}
 *  service.
 */
function $ParseProvider() {
  var cache = createMap();
  var literals = {
    'true': true,
    'false': false,
    'null': null,
    'undefined': undefined
  };
  var identStart, identContinue;

  /**
   * @ngdoc method
   * @name $parseProvider#addLiteral
   * @description
   *
   * Configure $parse service to add literal values that will be present as literal at expressions.
   *
   * @param {string} literalName Token for the literal value. The literal name value must be a valid literal name.
   * @param {*} literalValue Value for this literal. All literal values must be primitives or `undefined`.
   *
   **/
  this.addLiteral = function(literalName, literalValue) {
    literals[literalName] = literalValue;
  };

 /**
  * @ngdoc method
  * @name $parseProvider#setIdentifierFns
  *
  * @description
  *
  * Allows defining the set of characters that are allowed in AngularJS expressions. The function
  * `identifierStart` will get called to know if a given character is a valid character to be the
  * first character for an identifier. The function `identifierContinue` will get called to know if
  * a given character is a valid character to be a follow-up identifier character. The functions
  * `identifierStart` and `identifierContinue` will receive as arguments the single character to be
  * identifier and the character code point. These arguments will be `string` and `numeric`. Keep in
  * mind that the `string` parameter can be two characters long depending on the character
  * representation. It is expected for the function to return `true` or `false`, whether that
  * character is allowed or not.
  *
  * Since this function will be called extensively, keep the implementation of these functions fast,
  * as the performance of these functions have a direct impact on the expressions parsing speed.
  *
  * @param {function=} identifierStart The function that will decide whether the given character is
  *   a valid identifier start character.
  * @param {function=} identifierContinue The function that will decide whether the given character is
  *   a valid identifier continue character.
  */
  this.setIdentifierFns = function(identifierStart, identifierContinue) {
    identStart = identifierStart;
    identContinue = identifierContinue;
    return this;
  };

  this.$get = ['$filter', function($filter) {
    var noUnsafeEval = csp().noUnsafeEval;
    var $parseOptions = {
          csp: noUnsafeEval,
          literals: copy(literals),
          isIdentifierStart: isFunction(identStart) && identStart,
          isIdentifierContinue: isFunction(identContinue) && identContinue
        };
    $parse.$$getAst = $$getAst;
    return $parse;

    function $parse(exp, interceptorFn) {
      var parsedExpression, cacheKey;

      switch (typeof exp) {
        case 'string':
          exp = exp.trim();
          cacheKey = exp;

          parsedExpression = cache[cacheKey];

          if (!parsedExpression) {
            var lexer = new Lexer($parseOptions);
            var parser = new Parser(lexer, $filter, $parseOptions);
            parsedExpression = parser.parse(exp);

            cache[cacheKey] = addWatchDelegate(parsedExpression);
          }
          return addInterceptor(parsedExpression, interceptorFn);

        case 'function':
          return addInterceptor(exp, interceptorFn);

        default:
          return addInterceptor(noop, interceptorFn);
      }
    }

    function $$getAst(exp) {
      var lexer = new Lexer($parseOptions);
      var parser = new Parser(lexer, $filter, $parseOptions);
      return parser.getAst(exp).ast;
    }

    function expressionInputDirtyCheck(newValue, oldValueOfValue, compareObjectIdentity) {

      if (newValue == null || oldValueOfValue == null) { // null/undefined
        return newValue === oldValueOfValue;
      }

      if (typeof newValue === 'object') {

        // attempt to convert the value to a primitive type
        // TODO(docs): add a note to docs that by implementing valueOf even objects and arrays can
        //             be cheaply dirty-checked
        newValue = getValueOf(newValue);

        if (typeof newValue === 'object' && !compareObjectIdentity) {
          // objects/arrays are not supported - deep-watching them would be too expensive
          return false;
        }

        // fall-through to the primitive equality check
      }

      //Primitive or NaN
      // eslint-disable-next-line no-self-compare
      return newValue === oldValueOfValue || (newValue !== newValue && oldValueOfValue !== oldValueOfValue);
    }

    function inputsWatchDelegate(scope, listener, objectEquality, parsedExpression, prettyPrintExpression) {
      var inputExpressions = parsedExpression.inputs;
      var lastResult;

      if (inputExpressions.length === 1) {
        var oldInputValueOf = expressionInputDirtyCheck; // init to something unique so that equals check fails
        inputExpressions = inputExpressions[0];
        return scope.$watch(function expressionInputWatch(scope) {
          var newInputValue = inputExpressions(scope);
          if (!expressionInputDirtyCheck(newInputValue, oldInputValueOf, inputExpressions.isPure)) {
            lastResult = parsedExpression(scope, undefined, undefined, [newInputValue]);
            oldInputValueOf = newInputValue && getValueOf(newInputValue);
          }
          return lastResult;
        }, listener, objectEquality, prettyPrintExpression);
      }

      var oldInputValueOfValues = [];
      var oldInputValues = [];
      for (var i = 0, ii = inputExpressions.length; i < ii; i++) {
        oldInputValueOfValues[i] = expressionInputDirtyCheck; // init to something unique so that equals check fails
        oldInputValues[i] = null;
      }

      return scope.$watch(function expressionInputsWatch(scope) {
        var changed = false;

        for (var i = 0, ii = inputExpressions.length; i < ii; i++) {
          var newInputValue = inputExpressions[i](scope);
          if (changed || (changed = !expressionInputDirtyCheck(newInputValue, oldInputValueOfValues[i], inputExpressions[i].isPure))) {
            oldInputValues[i] = newInputValue;
            oldInputValueOfValues[i] = newInputValue && getValueOf(newInputValue);
          }
        }

        if (changed) {
          lastResult = parsedExpression(scope, undefined, undefined, oldInputValues);
        }

        return lastResult;
      }, listener, objectEquality, prettyPrintExpression);
    }

    function oneTimeWatchDelegate(scope, listener, objectEquality, parsedExpression, prettyPrintExpression) {
      var isDone = parsedExpression.literal ? isAllDefined : isDefined;
      var unwatch, lastValue;

      var exp = parsedExpression.$$intercepted || parsedExpression;
      var post = parsedExpression.$$interceptor || identity;

      var useInputs = parsedExpression.inputs && !exp.inputs;

      // Propagate the literal/inputs/constant attributes
      // ... but not oneTime since we are handling it
      oneTimeWatch.literal = parsedExpression.literal;
      oneTimeWatch.constant = parsedExpression.constant;
      oneTimeWatch.inputs = parsedExpression.inputs;

      // Allow other delegates to run on this wrapped expression
      addWatchDelegate(oneTimeWatch);

      unwatch = scope.$watch(oneTimeWatch, listener, objectEquality, prettyPrintExpression);

      return unwatch;

      function unwatchIfDone() {
        if (isDone(lastValue)) {
          unwatch();
        }
      }

      function oneTimeWatch(scope, locals, assign, inputs) {
        lastValue = useInputs && inputs ? inputs[0] : exp(scope, locals, assign, inputs);
        if (isDone(lastValue)) {
          scope.$$postDigest(unwatchIfDone);
        }
        return post(lastValue);
      }
    }

    function isAllDefined(value) {
      var allDefined = true;
      forEach(value, function(val) {
        if (!isDefined(val)) allDefined = false;
      });
      return allDefined;
    }

    function constantWatchDelegate(scope, listener, objectEquality, parsedExpression) {
      var unwatch = scope.$watch(function constantWatch(scope) {
        unwatch();
        return parsedExpression(scope);
      }, listener, objectEquality);
      return unwatch;
    }

    function addWatchDelegate(parsedExpression) {
      if (parsedExpression.constant) {
        parsedExpression.$$watchDelegate = constantWatchDelegate;
      } else if (parsedExpression.oneTime) {
        parsedExpression.$$watchDelegate = oneTimeWatchDelegate;
      } else if (parsedExpression.inputs) {
        parsedExpression.$$watchDelegate = inputsWatchDelegate;
      }

      return parsedExpression;
    }

    function chainInterceptors(first, second) {
      function chainedInterceptor(value) {
        return second(first(value));
      }
      chainedInterceptor.$stateful = first.$stateful || second.$stateful;
      chainedInterceptor.$$pure = first.$$pure && second.$$pure;

      return chainedInterceptor;
    }

    function addInterceptor(parsedExpression, interceptorFn) {
      if (!interceptorFn) return parsedExpression;

      // Extract any existing interceptors out of the parsedExpression
      // to ensure the original parsedExpression is always the $$intercepted
      if (parsedExpression.$$interceptor) {
        interceptorFn = chainInterceptors(parsedExpression.$$interceptor, interceptorFn);
        parsedExpression = parsedExpression.$$intercepted;
      }

      var useInputs = false;

      var fn = function interceptedExpression(scope, locals, assign, inputs) {
        var value = useInputs && inputs ? inputs[0] : parsedExpression(scope, locals, assign, inputs);
        return interceptorFn(value);
      };

      // Maintain references to the interceptor/intercepted
      fn.$$intercepted = parsedExpression;
      fn.$$interceptor = interceptorFn;

      // Propagate the literal/oneTime/constant attributes
      fn.literal = parsedExpression.literal;
      fn.oneTime = parsedExpression.oneTime;
      fn.constant = parsedExpression.constant;

      // Treat the interceptor like filters.
      // If it is not $stateful then only watch its inputs.
      // If the expression itself has no inputs then use the full expression as an input.
      if (!interceptorFn.$stateful) {
        useInputs = !parsedExpression.inputs;
        fn.inputs = parsedExpression.inputs ? parsedExpression.inputs : [parsedExpression];

        if (!interceptorFn.$$pure) {
          fn.inputs = fn.inputs.map(function(e) {
              // Remove the isPure flag of inputs when it is not absolute because they are now wrapped in a
              // non-pure interceptor function.
              if (e.isPure === PURITY_RELATIVE) {
                return function depurifier(s) { return e(s); };
              }
              return e;
            });
        }
      }

      return addWatchDelegate(fn);
    }
  }];
}

/**
 * @ngdoc service
 * @name $q
 * @requires $rootScope
 *
 * @description
 * A service that helps you run functions asynchronously, and use their return values (or exceptions)
 * when they are done processing.
 *
 * This is a [Promises/A+](https://promisesaplus.com/)-compliant implementation of promises/deferred
 * objects inspired by [Kris Kowal's Q](https://github.com/kriskowal/q).
 *
 * $q can be used in two fashions --- one which is more similar to Kris Kowal's Q or jQuery's Deferred
 * implementations, and the other which resembles ES6 (ES2015) promises to some degree.
 *
 * ## $q constructor
 *
 * The streamlined ES6 style promise is essentially just using $q as a constructor which takes a `resolver`
 * function as the first argument. This is similar to the native Promise implementation from ES6,
 * see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise).
 *
 * While the constructor-style use is supported, not all of the supporting methods from ES6 promises are
 * available yet.
 *
 * It can be used like so:
 *
 * ```js
 *   // for the purpose of this example let's assume that variables `$q` and `okToGreet`
 *   // are available in the current lexical scope (they could have been injected or passed in).
 *
 *   function asyncGreet(name) {
 *     // perform some asynchronous operation, resolve or reject the promise when appropriate.
 *     return $q(function(resolve, reject) {
 *       setTimeout(function() {
 *         if (okToGreet(name)) {
 *           resolve('Hello, ' + name + '!');
 *         } else {
 *           reject('Greeting ' + name + ' is not allowed.');
 *         }
 *       }, 1000);
 *     });
 *   }
 *
 *   var promise = asyncGreet('Robin Hood');
 *   promise.then(function(greeting) {
 *     alert('Success: ' + greeting);
 *   }, function(reason) {
 *     alert('Failed: ' + reason);
 *   });
 * ```
 *
 * Note: progress/notify callbacks are not currently supported via the ES6-style interface.
 *
 * Note: unlike ES6 behavior, an exception thrown in the constructor function will NOT implicitly reject the promise.
 *
 * However, the more traditional CommonJS-style usage is still available, and documented below.
 *
 * [The CommonJS Promise proposal](http://wiki.commonjs.org/wiki/Promises) describes a promise as an
 * interface for interacting with an object that represents the result of an action that is
 * performed asynchronously, and may or may not be finished at any given point in time.
 *
 * From the perspective of dealing with error handling, deferred and promise APIs are to
 * asynchronous programming what `try`, `catch` and `throw` keywords are to synchronous programming.
 *
 * ```js
 *   // for the purpose of this example let's assume that variables `$q` and `okToGreet`
 *   // are available in the current lexical scope (they could have been injected or passed in).
 *
 *   function asyncGreet(name) {
 *     var deferred = $q.defer();
 *
 *     setTimeout(function() {
 *       deferred.notify('About to greet ' + name + '.');
 *
 *       if (okToGreet(name)) {
 *         deferred.resolve('Hello, ' + name + '!');
 *       } else {
 *         deferred.reject('Greeting ' + name + ' is not allowed.');
 *       }
 *     }, 1000);
 *
 *     return deferred.promise;
 *   }
 *
 *   var promise = asyncGreet('Robin Hood');
 *   promise.then(function(greeting) {
 *     alert('Success: ' + greeting);
 *   }, function(reason) {
 *     alert('Failed: ' + reason);
 *   }, function(update) {
 *     alert('Got notification: ' + update);
 *   });
 * ```
 *
 * At first it might not be obvious why this extra complexity is worth the trouble. The payoff
 * comes in the way of guarantees that promise and deferred APIs make, see
 * https://github.com/kriskowal/uncommonjs/blob/master/promises/specification.md.
 *
 * Additionally the promise api allows for composition that is very hard to do with the
 * traditional callback ([CPS](http://en.wikipedia.org/wiki/Continuation-passing_style)) approach.
 * For more on this please see the [Q documentation](https://github.com/kriskowal/q) especially the
 * section on serial or parallel joining of promises.
 *
 * ## The Deferred API
 *
 * A new instance of deferred is constructed by calling `$q.defer()`.
 *
 * The purpose of the deferred object is to expose the associated Promise instance as well as APIs
 * that can be used for signaling the successful or unsuccessful completion, as well as the status
 * of the task.
 *
 * **Methods**
 *
 * - `resolve(value)` – resolves the derived promise with the `value`. If the value is a rejection
 *   constructed via `$q.reject`, the promise will be rejected instead.
 * - `reject(reason)` – rejects the derived promise with the `reason`. This is equivalent to
 *   resolving it with a rejection constructed via `$q.reject`.
 * - `notify(value)` - provides updates on the status of the promise's execution. This may be called
 *   multiple times before the promise is either resolved or rejected.
 *
 * **Properties**
 *
 * - promise – `{Promise}` – promise object associated with this deferred.
 *
 *
 * ## The Promise API
 *
 * A new promise instance is created when a deferred instance is created and can be retrieved by
 * calling `deferred.promise`.
 *
 * The purpose of the promise object is to allow for interested parties to get access to the result
 * of the deferred task when it completes.
 *
 * **Methods**
 *
 * - `then(successCallback, [errorCallback], [notifyCallback])` – regardless of when the promise was or
 *   will be resolved or rejected, `then` calls one of the success or error callbacks asynchronously
 *   as soon as the result is available. The callbacks are called with a single argument: the result
 *   or rejection reason. Additionally, the notify callback may be called zero or more times to
 *   provide a progress indication, before the promise is resolved or rejected.
 *
 *   This method *returns a new promise* which is resolved or rejected via the return value of the
 *   `successCallback`, `errorCallback` (unless that value is a promise, in which case it is resolved
 *   with the value which is resolved in that promise using
 *   [promise chaining](http://www.html5rocks.com/en/tutorials/es6/promises/#toc-promises-queues)).
 *   It also notifies via the return value of the `notifyCallback` method. The promise cannot be
 *   resolved or rejected from the notifyCallback method. The errorCallback and notifyCallback
 *   arguments are optional.
 *
 * - `catch(errorCallback)` – shorthand for `promise.then(null, errorCallback)`
 *
 * - `finally(callback, notifyCallback)` – allows you to observe either the fulfillment or rejection of a promise,
 *   but to do so without modifying the final value. This is useful to release resources or do some
 *   clean-up that needs to be done whether the promise was rejected or resolved. See the [full
 *   specification](https://github.com/kriskowal/q/wiki/API-Reference#promisefinallycallback) for
 *   more information.
 *
 * ## Chaining promises
 *
 * Because calling the `then` method of a promise returns a new derived promise, it is easily
 * possible to create a chain of promises:
 *
 * ```js
 *   promiseB = promiseA.then(function(result) {
 *     return result + 1;
 *   });
 *
 *   // promiseB will be resolved immediately after promiseA is resolved and its value
 *   // will be the result of promiseA incremented by 1
 * ```
 *
 * It is possible to create chains of any length and since a promise can be resolved with another
 * promise (which will defer its resolution further), it is possible to pause/defer resolution of
 * the promises at any point in the chain. This makes it possible to implement powerful APIs like
 * $http's response interceptors.
 *
 *
 * ## Differences between Kris Kowal's Q and $q
 *
 *  There are two main differences:
 *
 * - $q is integrated with the {@link ng.$rootScope.Scope} Scope model observation
 *   mechanism in AngularJS, which means faster propagation of resolution or rejection into your
 *   models and avoiding unnecessary browser repaints, which would result in flickering UI.
 * - Q has many more features than $q, but that comes at a cost of bytes. $q is tiny, but contains
 *   all the important functionality needed for common async tasks.
 *
 * ## Testing
 *
 *  ```js
 *    it('should simulate promise', inject(function($q, $rootScope) {
 *      var deferred = $q.defer();
 *      var promise = deferred.promise;
 *      var resolvedValue;
 *
 *      promise.then(function(value) { resolvedValue = value; });
 *      expect(resolvedValue).toBeUndefined();
 *
 *      // Simulate resolving of promise
 *      deferred.resolve(123);
 *      // Note that the 'then' function does not get called synchronously.
 *      // This is because we want the promise API to always be async, whether or not
 *      // it got called synchronously or asynchronously.
 *      expect(resolvedValue).toBeUndefined();
 *
 *      // Propagate promise resolution to 'then' functions using $apply().
 *      $rootScope.$apply();
 *      expect(resolvedValue).toEqual(123);
 *    }));
 *  ```
 *
 * @param {function(function, function)} resolver Function which is responsible for resolving or
 *   rejecting the newly created promise. The first parameter is a function which resolves the
 *   promise, the second parameter is a function which rejects the promise.
 *
 * @returns {Promise} The newly created promise.
 */
/**
 * @ngdoc provider
 * @name $qProvider
 * @this
 *
 * @description
 */
function $QProvider() {
  var errorOnUnhandledRejections = true;
  this.$get = ['$rootScope', '$exceptionHandler', function($rootScope, $exceptionHandler) {
    return qFactory(function(callback) {
      $rootScope.$evalAsync(callback);
    }, $exceptionHandler, errorOnUnhandledRejections);
  }];

  /**
   * @ngdoc method
   * @name $qProvider#errorOnUnhandledRejections
   * @kind function
   *
   * @description
   * Retrieves or overrides whether to generate an error when a rejected promise is not handled.
   * This feature is enabled by default.
   *
   * @param {boolean=} value Whether to generate an error when a rejected promise is not handled.
   * @returns {boolean|ng.$qProvider} Current value when called without a new value or self for
   *    chaining otherwise.
   */
  this.errorOnUnhandledRejections = function(value) {
    if (isDefined(value)) {
      errorOnUnhandledRejections = value;
      return this;
    } else {
      return errorOnUnhandledRejections;
    }
  };
}

/** @this */
function $$QProvider() {
  var errorOnUnhandledRejections = true;
  this.$get = ['$browser', '$exceptionHandler', function($browser, $exceptionHandler) {
    return qFactory(function(callback) {
      $browser.defer(callback);
    }, $exceptionHandler, errorOnUnhandledRejections);
  }];

  this.errorOnUnhandledRejections = function(value) {
    if (isDefined(value)) {
      errorOnUnhandledRejections = value;
      return this;
    } else {
      return errorOnUnhandledRejections;
    }
  };
}

/**
 * Constructs a promise manager.
 *
 * @param {function(function)} nextTick Function for executing functions in the next turn.
 * @param {function(...*)} exceptionHandler Function into which unexpected exceptions are passed for
 *     debugging purposes.
 * @param {boolean=} errorOnUnhandledRejections Whether an error should be generated on unhandled
 *     promises rejections.
 * @returns {object} Promise manager.
 */
function qFactory(nextTick, exceptionHandler, errorOnUnhandledRejections) {
  var $qMinErr = minErr('$q', TypeError);
  var queueSize = 0;
  var checkQueue = [];

  /**
   * @ngdoc method
   * @name ng.$q#defer
   * @kind function
   *
   * @description
   * Creates a `Deferred` object which represents a task which will finish in the future.
   *
   * @returns {Deferred} Returns a new instance of deferred.
   */
  function defer() {
    return new Deferred();
  }

  function Deferred() {
    var promise = this.promise = new Promise();
    //Non prototype methods necessary to support unbound execution :/
    this.resolve = function(val) { resolvePromise(promise, val); };
    this.reject = function(reason) { rejectPromise(promise, reason); };
    this.notify = function(progress) { notifyPromise(promise, progress); };
  }


  function Promise() {
    this.$$state = { status: 0 };
  }

  extend(Promise.prototype, {
    then: function(onFulfilled, onRejected, progressBack) {
      if (isUndefined(onFulfilled) && isUndefined(onRejected) && isUndefined(progressBack)) {
        return this;
      }
      var result = new Promise();

      this.$$state.pending = this.$$state.pending || [];
      this.$$state.pending.push([result, onFulfilled, onRejected, progressBack]);
      if (this.$$state.status > 0) scheduleProcessQueue(this.$$state);

      return result;
    },

    'catch': function(callback) {
      return this.then(null, callback);
    },

    'finally': function(callback, progressBack) {
      return this.then(function(value) {
        return handleCallback(value, resolve, callback);
      }, function(error) {
        return handleCallback(error, reject, callback);
      }, progressBack);
    }
  });

  function processQueue(state) {
    var fn, promise, pending;

    pending = state.pending;
    state.processScheduled = false;
    state.pending = undefined;
    try {
      for (var i = 0, ii = pending.length; i < ii; ++i) {
        markQStateExceptionHandled(state);
        promise = pending[i][0];
        fn = pending[i][state.status];
        try {
          if (isFunction(fn)) {
            resolvePromise(promise, fn(state.value));
          } else if (state.status === 1) {
            resolvePromise(promise, state.value);
          } else {
            rejectPromise(promise, state.value);
          }
        } catch (e) {
          rejectPromise(promise, e);
          // This error is explicitly marked for being passed to the $exceptionHandler
          if (e && e.$$passToExceptionHandler === true) {
            exceptionHandler(e);
          }
        }
      }
    } finally {
      --queueSize;
      if (errorOnUnhandledRejections && queueSize === 0) {
        nextTick(processChecks);
      }
    }
  }

  function processChecks() {
    // eslint-disable-next-line no-unmodified-loop-condition
    while (!queueSize && checkQueue.length) {
      var toCheck = checkQueue.shift();
      if (!isStateExceptionHandled(toCheck)) {
        markQStateExceptionHandled(toCheck);
        var errorMessage = 'Possibly unhandled rejection: ' + toDebugString(toCheck.value);
        if (isError(toCheck.value)) {
          exceptionHandler(toCheck.value, errorMessage);
        } else {
          exceptionHandler(errorMessage);
        }
      }
    }
  }

  function scheduleProcessQueue(state) {
    if (errorOnUnhandledRejections && !state.pending && state.status === 2 && !isStateExceptionHandled(state)) {
      if (queueSize === 0 && checkQueue.length === 0) {
        nextTick(processChecks);
      }
      checkQueue.push(state);
    }
    if (state.processScheduled || !state.pending) return;
    state.processScheduled = true;
    ++queueSize;
    nextTick(function() { processQueue(state); });
  }

  function resolvePromise(promise, val) {
    if (promise.$$state.status) return;
    if (val === promise) {
      $$reject(promise, $qMinErr(
        'qcycle',
        'Expected promise to be resolved with value other than itself \'{0}\'',
        val));
    } else {
      $$resolve(promise, val);
    }

  }

  function $$resolve(promise, val) {
    var then;
    var done = false;
    try {
      if (isObject(val) || isFunction(val)) then = val.then;
      if (isFunction(then)) {
        promise.$$state.status = -1;
        then.call(val, doResolve, doReject, doNotify);
      } else {
        promise.$$state.value = val;
        promise.$$state.status = 1;
        scheduleProcessQueue(promise.$$state);
      }
    } catch (e) {
      doReject(e);
    }

    function doResolve(val) {
      if (done) return;
      done = true;
      $$resolve(promise, val);
    }
    function doReject(val) {
      if (done) return;
      done = true;
      $$reject(promise, val);
    }
    function doNotify(progress) {
      notifyPromise(promise, progress);
    }
  }

  function rejectPromise(promise, reason) {
    if (promise.$$state.status) return;
    $$reject(promise, reason);
  }

  function $$reject(promise, reason) {
    promise.$$state.value = reason;
    promise.$$state.status = 2;
    scheduleProcessQueue(promise.$$state);
  }

  function notifyPromise(promise, progress) {
    var callbacks = promise.$$state.pending;

    if ((promise.$$state.status <= 0) && callbacks && callbacks.length) {
      nextTick(function() {
        var callback, result;
        for (var i = 0, ii = callbacks.length; i < ii; i++) {
          result = callbacks[i][0];
          callback = callbacks[i][3];
          try {
            notifyPromise(result, isFunction(callback) ? callback(progress) : progress);
          } catch (e) {
            exceptionHandler(e);
          }
        }
      });
    }
  }

  /**
   * @ngdoc method
   * @name $q#reject
   * @kind function
   *
   * @description
   * Creates a promise that is resolved as rejected with the specified `reason`. This api should be
   * used to forward rejection in a chain of promises. If you are dealing with the last promise in
   * a promise chain, you don't need to worry about it.
   *
   * When comparing deferreds/promises to the familiar behavior of try/catch/throw, think of
   * `reject` as the `throw` keyword in JavaScript. This also means that if you "catch" an error via
   * a promise error callback and you want to forward the error to the promise derived from the
   * current promise, you have to "rethrow" the error by returning a rejection constructed via
   * `reject`.
   *
   * ```js
   *   promiseB = promiseA.then(function(result) {
   *     // success: do something and resolve promiseB
   *     //          with the old or a new result
   *     return result;
   *   }, function(reason) {
   *     // error: handle the error if possible and
   *     //        resolve promiseB with newPromiseOrValue,
   *     //        otherwise forward the rejection to promiseB
   *     if (canHandle(reason)) {
   *      // handle the error and recover
   *      return newPromiseOrValue;
   *     }
   *     return $q.reject(reason);
   *   });
   * ```
   *
   * @param {*} reason Constant, message, exception or an object representing the rejection reason.
   * @returns {Promise} Returns a promise that was already resolved as rejected with the `reason`.
   */
  function reject(reason) {
    var result = new Promise();
    rejectPromise(result, reason);
    return result;
  }

  function handleCallback(value, resolver, callback) {
    var callbackOutput = null;
    try {
      if (isFunction(callback)) callbackOutput = callback();
    } catch (e) {
      return reject(e);
    }
    if (isPromiseLike(callbackOutput)) {
      return callbackOutput.then(function() {
        return resolver(value);
      }, reject);
    } else {
      return resolver(value);
    }
  }

  /**
   * @ngdoc method
   * @name $q#when
   * @kind function
   *
   * @description
   * Wraps an object that might be a value or a (3rd party) then-able promise into a $q promise.
   * This is useful when you are dealing with an object that might or might not be a promise, or if
   * the promise comes from a source that can't be trusted.
   *
   * @param {*} value Value or a promise
   * @param {Function=} successCallback
   * @param {Function=} errorCallback
   * @param {Function=} progressCallback
   * @returns {Promise} Returns a promise of the passed value or promise
   */


  function when(value, callback, errback, progressBack) {
    var result = new Promise();
    resolvePromise(result, value);
    return result.then(callback, errback, progressBack);
  }

  /**
   * @ngdoc method
   * @name $q#resolve
   * @kind function
   *
   * @description
   * Alias of {@link ng.$q#when when} to maintain naming consistency with ES6.
   *
   * @param {*} value Value or a promise
   * @param {Function=} successCallback
   * @param {Function=} errorCallback
   * @param {Function=} progressCallback
   * @returns {Promise} Returns a promise of the passed value or promise
   */
  var resolve = when;

  /**
   * @ngdoc method
   * @name $q#all
   * @kind function
   *
   * @description
   * Combines multiple promises into a single promise that is resolved when all of the input
   * promises are resolved.
   *
   * @param {Array.<Promise>|Object.<Promise>} promises An array or hash of promises.
   * @returns {Promise} Returns a single promise that will be resolved with an array/hash of values,
   *   each value corresponding to the promise at the same index/key in the `promises` array/hash.
   *   If any of the promises is resolved with a rejection, this resulting promise will be rejected
   *   with the same rejection value.
   */

  function all(promises) {
    var result = new Promise(),
        counter = 0,
        results = isArray(promises) ? [] : {};

    forEach(promises, function(promise, key) {
      counter++;
      when(promise).then(function(value) {
        results[key] = value;
        if (!(--counter)) resolvePromise(result, results);
      }, function(reason) {
        rejectPromise(result, reason);
      });
    });

    if (counter === 0) {
      resolvePromise(result, results);
    }

    return result;
  }

  /**
   * @ngdoc method
   * @name $q#race
   * @kind function
   *
   * @description
   * Returns a promise that resolves or rejects as soon as one of those promises
   * resolves or rejects, with the value or reason from that promise.
   *
   * @param {Array.<Promise>|Object.<Promise>} promises An array or hash of promises.
   * @returns {Promise} a promise that resolves or rejects as soon as one of the `promises`
   * resolves or rejects, with the value or reason from that promise.
   */

  function race(promises) {
    var deferred = defer();

    forEach(promises, function(promise) {
      when(promise).then(deferred.resolve, deferred.reject);
    });

    return deferred.promise;
  }

  function $Q(resolver) {
    if (!isFunction(resolver)) {
      throw $qMinErr('norslvr', 'Expected resolverFn, got \'{0}\'', resolver);
    }

    var promise = new Promise();

    function resolveFn(value) {
      resolvePromise(promise, value);
    }

    function rejectFn(reason) {
      rejectPromise(promise, reason);
    }

    resolver(resolveFn, rejectFn);

    return promise;
  }

  // Let's make the instanceof operator work for promises, so that
  // `new $q(fn) instanceof $q` would evaluate to true.
  $Q.prototype = Promise.prototype;

  $Q.defer = defer;
  $Q.reject = reject;
  $Q.when = when;
  $Q.resolve = resolve;
  $Q.all = all;
  $Q.race = race;

  return $Q;
}

function isStateExceptionHandled(state) {
  return !!state.pur;
}
function markQStateExceptionHandled(state) {
  state.pur = true;
}
function markQExceptionHandled(q) {
  // Built-in `$q` promises will always have a `$$state` property. This check is to allow
  // overwriting `$q` with a different promise library (e.g. Bluebird + angular-bluebird-promises).
  // (Currently, this is the only method that might be called with a promise, even if it is not
  // created by the built-in `$q`.)
  if (q.$$state) {
    markQStateExceptionHandled(q.$$state);
  }
}

/** @this */
function $$RAFProvider() { //rAF
  this.$get = ['$window', '$timeout', function($window, $timeout) {
    var requestAnimationFrame = $window.requestAnimationFrame ||
                                $window.webkitRequestAnimationFrame;

    var cancelAnimationFrame = $window.cancelAnimationFrame ||
                               $window.webkitCancelAnimationFrame ||
                               $window.webkitCancelRequestAnimationFrame;

    var rafSupported = !!requestAnimationFrame;
    var raf = rafSupported
      ? function(fn) {
          var id = requestAnimationFrame(fn);
          return function() {
            cancelAnimationFrame(id);
          };
        }
      : function(fn) {
          var timer = $timeout(fn, 16.66, false); // 1000 / 60 = 16.666
          return function() {
            $timeout.cancel(timer);
          };
        };

    raf.supported = rafSupported;

    return raf;
  }];
}

/**
 * DESIGN NOTES
 *
 * The design decisions behind the scope are heavily favored for speed and memory consumption.
 *
 * The typical use of scope is to watch the expressions, which most of the time return the same
 * value as last time so we optimize the operation.
 *
 * Closures construction is expensive in terms of speed as well as memory:
 *   - No closures, instead use prototypical inheritance for API
 *   - Internal state needs to be stored on scope directly, which means that private state is
 *     exposed as $$____ properties
 *
 * Loop operations are optimized by using while(count--) { ... }
 *   - This means that in order to keep the same order of execution as addition we have to add
 *     items to the array at the beginning (unshift) instead of at the end (push)
 *
 * Child scopes are created and removed often
 *   - Using an array would be slow since inserts in the middle are expensive; so we use linked lists
 *
 * There are fewer watches than observers. This is why you don't want the observer to be implemented
 * in the same way as watch. Watch requires return of the initialization function which is expensive
 * to construct.
 */


/**
 * @ngdoc provider
 * @name $rootScopeProvider
 * @description
 *
 * Provider for the $rootScope service.
 */

/**
 * @ngdoc method
 * @name $rootScopeProvider#digestTtl
 * @description
 *
 * Sets the number of `$digest` iterations the scope should attempt to execute before giving up and
 * assuming that the model is unstable.
 *
 * The current default is 10 iterations.
 *
 * In complex applications it's possible that the dependencies between `$watch`s will result in
 * several digest iterations. However if an application needs more than the default 10 digest
 * iterations for its model to stabilize then you should investigate what is causing the model to
 * continuously change during the digest.
 *
 * Increasing the TTL could have performance implications, so you should not change it without
 * proper justification.
 *
 * @param {number} limit The number of digest iterations.
 */


/**
 * @ngdoc service
 * @name $rootScope
 * @this
 *
 * @description
 *
 * Every application has a single root {@link ng.$rootScope.Scope scope}.
 * All other scopes are descendant scopes of the root scope. Scopes provide separation
 * between the model and the view, via a mechanism for watching the model for changes.
 * They also provide event emission/broadcast and subscription facility. See the
 * {@link guide/scope developer guide on scopes}.
 */
function $RootScopeProvider() {
  var TTL = 10;
  var $rootScopeMinErr = minErr('$rootScope');
  var lastDirtyWatch = null;
  var applyAsyncId = null;

  this.digestTtl = function(value) {
    if (arguments.length) {
      TTL = value;
    }
    return TTL;
  };

  function createChildScopeClass(parent) {
    function ChildScope() {
      this.$$watchers = this.$$nextSibling =
          this.$$childHead = this.$$childTail = null;
      this.$$listeners = {};
      this.$$listenerCount = {};
      this.$$watchersCount = 0;
      this.$id = nextUid();
      this.$$ChildScope = null;
      this.$$suspended = false;
    }
    ChildScope.prototype = parent;
    return ChildScope;
  }

  this.$get = ['$exceptionHandler', '$parse', '$browser',
      function($exceptionHandler, $parse, $browser) {

    function destroyChildScope($event) {
        $event.currentScope.$$destroyed = true;
    }

    function cleanUpScope($scope) {

      // Support: IE 9 only
      if (msie === 9) {
        // There is a memory leak in IE9 if all child scopes are not disconnected
        // completely when a scope is destroyed. So this code will recurse up through
        // all this scopes children
        //
        // See issue https://github.com/angular/angular.js/issues/10706
        if ($scope.$$childHead) {
          cleanUpScope($scope.$$childHead);
        }
        if ($scope.$$nextSibling) {
          cleanUpScope($scope.$$nextSibling);
        }
      }

      // The code below works around IE9 and V8's memory leaks
      //
      // See:
      // - https://code.google.com/p/v8/issues/detail?id=2073#c26
      // - https://github.com/angular/angular.js/issues/6794#issuecomment-38648909
      // - https://github.com/angular/angular.js/issues/1313#issuecomment-10378451

      $scope.$parent = $scope.$$nextSibling = $scope.$$prevSibling = $scope.$$childHead =
          $scope.$$childTail = $scope.$root = $scope.$$watchers = null;
    }

    /**
     * @ngdoc type
     * @name $rootScope.Scope
     *
     * @description
     * A root scope can be retrieved using the {@link ng.$rootScope $rootScope} key from the
     * {@link auto.$injector $injector}. Child scopes are created using the
     * {@link ng.$rootScope.Scope#$new $new()} method. (Most scopes are created automatically when
     * compiled HTML template is executed.) See also the {@link guide/scope Scopes guide} for
     * an in-depth introduction and usage examples.
     *
     *
     * ## Inheritance
     * A scope can inherit from a parent scope, as in this example:
     * ```js
         var parent = $rootScope;
         var child = parent.$new();

         parent.salutation = "Hello";
         expect(child.salutation).toEqual('Hello');

         child.salutation = "Welcome";
         expect(child.salutation).toEqual('Welcome');
         expect(parent.salutation).toEqual('Hello');
     * ```
     *
     * When interacting with `Scope` in tests, additional helper methods are available on the
     * instances of `Scope` type. See {@link ngMock.$rootScope.Scope ngMock Scope} for additional
     * details.
     *
     *
     * @param {Object.<string, function()>=} providers Map of service factory which need to be
     *                                       provided for the current scope. Defaults to {@link ng}.
     * @param {Object.<string, *>=} instanceCache Provides pre-instantiated services which should
     *                              append/override services provided by `providers`. This is handy
     *                              when unit-testing and having the need to override a default
     *                              service.
     * @returns {Object} Newly created scope.
     *
     */
    function Scope() {
      this.$id = nextUid();
      this.$$phase = this.$parent = this.$$watchers =
                     this.$$nextSibling = this.$$prevSibling =
                     this.$$childHead = this.$$childTail = null;
      this.$root = this;
      this.$$destroyed = false;
      this.$$suspended = false;
      this.$$listeners = {};
      this.$$listenerCount = {};
      this.$$watchersCount = 0;
      this.$$isolateBindings = null;
    }

    /**
     * @ngdoc property
     * @name $rootScope.Scope#$id
     *
     * @description
     * Unique scope ID (monotonically increasing) useful for debugging.
     */

     /**
      * @ngdoc property
      * @name $rootScope.Scope#$parent
      *
      * @description
      * Reference to the parent scope.
      */

      /**
       * @ngdoc property
       * @name $rootScope.Scope#$root
       *
       * @description
       * Reference to the root scope.
       */

    Scope.prototype = {
      constructor: Scope,
      /**
       * @ngdoc method
       * @name $rootScope.Scope#$new
       * @kind function
       *
       * @description
       * Creates a new child {@link ng.$rootScope.Scope scope}.
       *
       * The parent scope will propagate the {@link ng.$rootScope.Scope#$digest $digest()} event.
       * The scope can be removed from the scope hierarchy using {@link ng.$rootScope.Scope#$destroy $destroy()}.
       *
       * {@link ng.$rootScope.Scope#$destroy $destroy()} must be called on a scope when it is
       * desired for the scope and its child scopes to be permanently detached from the parent and
       * thus stop participating in model change detection and listener notification by invoking.
       *
       * @param {boolean} isolate If true, then the scope does not prototypically inherit from the
       *         parent scope. The scope is isolated, as it can not see parent scope properties.
       *         When creating widgets, it is useful for the widget to not accidentally read parent
       *         state.
       *
       * @param {Scope} [parent=this] The {@link ng.$rootScope.Scope `Scope`} that will be the `$parent`
       *                              of the newly created scope. Defaults to `this` scope if not provided.
       *                              This is used when creating a transclude scope to correctly place it
       *                              in the scope hierarchy while maintaining the correct prototypical
       *                              inheritance.
       *
       * @returns {Object} The newly created child scope.
       *
       */
      $new: function(isolate, parent) {
        var child;

        parent = parent || this;

        if (isolate) {
          child = new Scope();
          child.$root = this.$root;
        } else {
          // Only create a child scope class if somebody asks for one,
          // but cache it to allow the VM to optimize lookups.
          if (!this.$$ChildScope) {
            this.$$ChildScope = createChildScopeClass(this);
          }
          child = new this.$$ChildScope();
        }
        child.$parent = parent;
        child.$$prevSibling = parent.$$childTail;
        if (parent.$$childHead) {
          parent.$$childTail.$$nextSibling = child;
          parent.$$childTail = child;
        } else {
          parent.$$childHead = parent.$$childTail = child;
        }

        // When the new scope is not isolated or we inherit from `this`, and
        // the parent scope is destroyed, the property `$$destroyed` is inherited
        // prototypically. In all other cases, this property needs to be set
        // when the parent scope is destroyed.
        // The listener needs to be added after the parent is set
        if (isolate || parent !== this) child.$on('$destroy', destroyChildScope);

        return child;
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$watch
       * @kind function
       *
       * @description
       * Registers a `listener` callback to be executed whenever the `watchExpression` changes.
       *
       * - The `watchExpression` is called on every call to {@link ng.$rootScope.Scope#$digest
       *   $digest()} and should return the value that will be watched. (`watchExpression` should not change
       *   its value when executed multiple times with the same input because it may be executed multiple
       *   times by {@link ng.$rootScope.Scope#$digest $digest()}. That is, `watchExpression` should be
       *   [idempotent](http://en.wikipedia.org/wiki/Idempotence).)
       * - The `listener` is called only when the value from the current `watchExpression` and the
       *   previous call to `watchExpression` are not equal (with the exception of the initial run,
       *   see below). Inequality is determined according to reference inequality,
       *   [strict comparison](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Comparison_Operators)
       *    via the `!==` Javascript operator, unless `objectEquality == true`
       *   (see next point)
       * - When `objectEquality == true`, inequality of the `watchExpression` is determined
       *   according to the {@link angular.equals} function. To save the value of the object for
       *   later comparison, the {@link angular.copy} function is used. This therefore means that
       *   watching complex objects will have adverse memory and performance implications.
       * - This should not be used to watch for changes in objects that are (or contain)
       *   [File](https://developer.mozilla.org/docs/Web/API/File) objects due to limitations with {@link angular.copy `angular.copy`}.
       * - The watch `listener` may change the model, which may trigger other `listener`s to fire.
       *   This is achieved by rerunning the watchers until no changes are detected. The rerun
       *   iteration limit is 10 to prevent an infinite loop deadlock.
       *
       *
       * If you want to be notified whenever {@link ng.$rootScope.Scope#$digest $digest} is called,
       * you can register a `watchExpression` function with no `listener`. (Be prepared for
       * multiple calls to your `watchExpression` because it will execute multiple times in a
       * single {@link ng.$rootScope.Scope#$digest $digest} cycle if a change is detected.)
       *
       * After a watcher is registered with the scope, the `listener` fn is called asynchronously
       * (via {@link ng.$rootScope.Scope#$evalAsync $evalAsync}) to initialize the
       * watcher. In rare cases, this is undesirable because the listener is called when the result
       * of `watchExpression` didn't change. To detect this scenario within the `listener` fn, you
       * can compare the `newVal` and `oldVal`. If these two values are identical (`===`) then the
       * listener was called due to initialization.
       *
       *
       *
       * @example
       * ```js
           // let's assume that scope was dependency injected as the $rootScope
           var scope = $rootScope;
           scope.name = 'misko';
           scope.counter = 0;

           expect(scope.counter).toEqual(0);
           scope.$watch('name', function(newValue, oldValue) {
             scope.counter = scope.counter + 1;
           });
           expect(scope.counter).toEqual(0);

           scope.$digest();
           // the listener is always called during the first $digest loop after it was registered
           expect(scope.counter).toEqual(1);

           scope.$digest();
           // but now it will not be called unless the value changes
           expect(scope.counter).toEqual(1);

           scope.name = 'adam';
           scope.$digest();
           expect(scope.counter).toEqual(2);



           // Using a function as a watchExpression
           var food;
           scope.foodCounter = 0;
           expect(scope.foodCounter).toEqual(0);
           scope.$watch(
             // This function returns the value being watched. It is called for each turn of the $digest loop
             function() { return food; },
             // This is the change listener, called when the value returned from the above function changes
             function(newValue, oldValue) {
               if ( newValue !== oldValue ) {
                 // Only increment the counter if the value changed
                 scope.foodCounter = scope.foodCounter + 1;
               }
             }
           );
           // No digest has been run so the counter will be zero
           expect(scope.foodCounter).toEqual(0);

           // Run the digest but since food has not changed count will still be zero
           scope.$digest();
           expect(scope.foodCounter).toEqual(0);

           // Update food and run digest.  Now the counter will increment
           food = 'cheeseburger';
           scope.$digest();
           expect(scope.foodCounter).toEqual(1);

       * ```
       *
       *
       *
       * @param {(function()|string)} watchExpression Expression that is evaluated on each
       *    {@link ng.$rootScope.Scope#$digest $digest} cycle. A change in the return value triggers
       *    a call to the `listener`.
       *
       *    - `string`: Evaluated as {@link guide/expression expression}
       *    - `function(scope)`: called with current `scope` as a parameter.
       * @param {function(newVal, oldVal, scope)} listener Callback called whenever the value
       *    of `watchExpression` changes.
       *
       *    - `newVal` contains the current value of the `watchExpression`
       *    - `oldVal` contains the previous value of the `watchExpression`
       *    - `scope` refers to the current scope
       * @param {boolean=} [objectEquality=false] Compare for object equality using {@link angular.equals} instead of
       *     comparing for reference equality.
       * @returns {function()} Returns a deregistration function for this listener.
       */
      $watch: function(watchExp, listener, objectEquality, prettyPrintExpression) {
        var get = $parse(watchExp);
        var fn = isFunction(listener) ? listener : noop;

        if (get.$$watchDelegate) {
          return get.$$watchDelegate(this, fn, objectEquality, get, watchExp);
        }
        var scope = this,
            array = scope.$$watchers,
            watcher = {
              fn: fn,
              last: initWatchVal,
              get: get,
              exp: prettyPrintExpression || watchExp,
              eq: !!objectEquality
            };

        lastDirtyWatch = null;

        if (!array) {
          array = scope.$$watchers = [];
          array.$$digestWatchIndex = -1;
        }
        // we use unshift since we use a while loop in $digest for speed.
        // the while loop reads in reverse order.
        array.unshift(watcher);
        array.$$digestWatchIndex++;
        incrementWatchersCount(this, 1);

        return function deregisterWatch() {
          var index = arrayRemove(array, watcher);
          if (index >= 0) {
            incrementWatchersCount(scope, -1);
            if (index < array.$$digestWatchIndex) {
              array.$$digestWatchIndex--;
            }
          }
          lastDirtyWatch = null;
        };
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$watchGroup
       * @kind function
       *
       * @description
       * A variant of {@link ng.$rootScope.Scope#$watch $watch()} where it watches an array of `watchExpressions`.
       * If any one expression in the collection changes the `listener` is executed.
       *
       * - The items in the `watchExpressions` array are observed via the standard `$watch` operation. Their return
       *   values are examined for changes on every call to `$digest`.
       * - The `listener` is called whenever any expression in the `watchExpressions` array changes.
       *
       * @param {Array.<string|Function(scope)>} watchExpressions Array of expressions that will be individually
       * watched using {@link ng.$rootScope.Scope#$watch $watch()}
       *
       * @param {function(newValues, oldValues, scope)} listener Callback called whenever the return value of any
       *    expression in `watchExpressions` changes
       *    The `newValues` array contains the current values of the `watchExpressions`, with the indexes matching
       *    those of `watchExpression`
       *    and the `oldValues` array contains the previous values of the `watchExpressions`, with the indexes matching
       *    those of `watchExpression`
       *    The `scope` refers to the current scope.
       * @returns {function()} Returns a de-registration function for all listeners.
       */
      $watchGroup: function(watchExpressions, listener) {
        var oldValues = new Array(watchExpressions.length);
        var newValues = new Array(watchExpressions.length);
        var deregisterFns = [];
        var self = this;
        var changeReactionScheduled = false;
        var firstRun = true;

        if (!watchExpressions.length) {
          // No expressions means we call the listener ASAP
          var shouldCall = true;
          self.$evalAsync(function() {
            if (shouldCall) listener(newValues, newValues, self);
          });
          return function deregisterWatchGroup() {
            shouldCall = false;
          };
        }

        if (watchExpressions.length === 1) {
          // Special case size of one
          return this.$watch(watchExpressions[0], function watchGroupAction(value, oldValue, scope) {
            newValues[0] = value;
            oldValues[0] = oldValue;
            listener(newValues, (value === oldValue) ? newValues : oldValues, scope);
          });
        }

        forEach(watchExpressions, function(expr, i) {
          var unwatchFn = self.$watch(expr, function watchGroupSubAction(value) {
            newValues[i] = value;
            if (!changeReactionScheduled) {
              changeReactionScheduled = true;
              self.$evalAsync(watchGroupAction);
            }
          });
          deregisterFns.push(unwatchFn);
        });

        function watchGroupAction() {
          changeReactionScheduled = false;

          try {
            if (firstRun) {
              firstRun = false;
              listener(newValues, newValues, self);
            } else {
              listener(newValues, oldValues, self);
            }
          } finally {
            for (var i = 0; i < watchExpressions.length; i++) {
              oldValues[i] = newValues[i];
            }
          }
        }

        return function deregisterWatchGroup() {
          while (deregisterFns.length) {
            deregisterFns.shift()();
          }
        };
      },


      /**
       * @ngdoc method
       * @name $rootScope.Scope#$watchCollection
       * @kind function
       *
       * @description
       * Shallow watches the properties of an object and fires whenever any of the properties change
       * (for arrays, this implies watching the array items; for object maps, this implies watching
       * the properties). If a change is detected, the `listener` callback is fired.
       *
       * - The `obj` collection is observed via standard $watch operation and is examined on every
       *   call to $digest() to see if any items have been added, removed, or moved.
       * - The `listener` is called whenever anything within the `obj` has changed. Examples include
       *   adding, removing, and moving items belonging to an object or array.
       *
       *
       * @example
       * ```js
          $scope.names = ['igor', 'matias', 'misko', 'james'];
          $scope.dataCount = 4;

          $scope.$watchCollection('names', function(newNames, oldNames) {
            $scope.dataCount = newNames.length;
          });

          expect($scope.dataCount).toEqual(4);
          $scope.$digest();

          //still at 4 ... no changes
          expect($scope.dataCount).toEqual(4);

          $scope.names.pop();
          $scope.$digest();

          //now there's been a change
          expect($scope.dataCount).toEqual(3);
       * ```
       *
       *
       * @param {string|function(scope)} obj Evaluated as {@link guide/expression expression}. The
       *    expression value should evaluate to an object or an array which is observed on each
       *    {@link ng.$rootScope.Scope#$digest $digest} cycle. Any shallow change within the
       *    collection will trigger a call to the `listener`.
       *
       * @param {function(newCollection, oldCollection, scope)} listener a callback function called
       *    when a change is detected.
       *    - The `newCollection` object is the newly modified data obtained from the `obj` expression
       *    - The `oldCollection` object is a copy of the former collection data.
       *      Due to performance considerations, the`oldCollection` value is computed only if the
       *      `listener` function declares two or more arguments.
       *    - The `scope` argument refers to the current scope.
       *
       * @returns {function()} Returns a de-registration function for this listener. When the
       *    de-registration function is executed, the internal watch operation is terminated.
       */
      $watchCollection: function(obj, listener) {
        // Mark the interceptor as
        // ... $$pure when literal since the instance will change when any input changes
        $watchCollectionInterceptor.$$pure = $parse(obj).literal;
        // ... $stateful when non-literal since we must read the state of the collection
        $watchCollectionInterceptor.$stateful = !$watchCollectionInterceptor.$$pure;

        var self = this;
        // the current value, updated on each dirty-check run
        var newValue;
        // a shallow copy of the newValue from the last dirty-check run,
        // updated to match newValue during dirty-check run
        var oldValue;
        // a shallow copy of the newValue from when the last change happened
        var veryOldValue;
        // only track veryOldValue if the listener is asking for it
        var trackVeryOldValue = (listener.length > 1);
        var changeDetected = 0;
        var changeDetector = $parse(obj, $watchCollectionInterceptor);
        var internalArray = [];
        var internalObject = {};
        var initRun = true;
        var oldLength = 0;

        function $watchCollectionInterceptor(_value) {
          newValue = _value;
          var newLength, key, bothNaN, newItem, oldItem;

          // If the new value is undefined, then return undefined as the watch may be a one-time watch
          if (isUndefined(newValue)) return;

          if (!isObject(newValue)) { // if primitive
            if (oldValue !== newValue) {
              oldValue = newValue;
              changeDetected++;
            }
          } else if (isArrayLike(newValue)) {
            if (oldValue !== internalArray) {
              // we are transitioning from something which was not an array into array.
              oldValue = internalArray;
              oldLength = oldValue.length = 0;
              changeDetected++;
            }

            newLength = newValue.length;

            if (oldLength !== newLength) {
              // if lengths do not match we need to trigger change notification
              changeDetected++;
              oldValue.length = oldLength = newLength;
            }
            // copy the items to oldValue and look for changes.
            for (var i = 0; i < newLength; i++) {
              oldItem = oldValue[i];
              newItem = newValue[i];

              // eslint-disable-next-line no-self-compare
              bothNaN = (oldItem !== oldItem) && (newItem !== newItem);
              if (!bothNaN && (oldItem !== newItem)) {
                changeDetected++;
                oldValue[i] = newItem;
              }
            }
          } else {
            if (oldValue !== internalObject) {
              // we are transitioning from something which was not an object into object.
              oldValue = internalObject = {};
              oldLength = 0;
              changeDetected++;
            }
            // copy the items to oldValue and look for changes.
            newLength = 0;
            for (key in newValue) {
              if (hasOwnProperty.call(newValue, key)) {
                newLength++;
                newItem = newValue[key];
                oldItem = oldValue[key];

                if (key in oldValue) {
                  // eslint-disable-next-line no-self-compare
                  bothNaN = (oldItem !== oldItem) && (newItem !== newItem);
                  if (!bothNaN && (oldItem !== newItem)) {
                    changeDetected++;
                    oldValue[key] = newItem;
                  }
                } else {
                  oldLength++;
                  oldValue[key] = newItem;
                  changeDetected++;
                }
              }
            }
            if (oldLength > newLength) {
              // we used to have more keys, need to find them and destroy them.
              changeDetected++;
              for (key in oldValue) {
                if (!hasOwnProperty.call(newValue, key)) {
                  oldLength--;
                  delete oldValue[key];
                }
              }
            }
          }
          return changeDetected;
        }

        function $watchCollectionAction() {
          if (initRun) {
            initRun = false;
            listener(newValue, newValue, self);
          } else {
            listener(newValue, veryOldValue, self);
          }

          // make a copy for the next time a collection is changed
          if (trackVeryOldValue) {
            if (!isObject(newValue)) {
              //primitive
              veryOldValue = newValue;
            } else if (isArrayLike(newValue)) {
              veryOldValue = new Array(newValue.length);
              for (var i = 0; i < newValue.length; i++) {
                veryOldValue[i] = newValue[i];
              }
            } else { // if object
              veryOldValue = {};
              for (var key in newValue) {
                if (hasOwnProperty.call(newValue, key)) {
                  veryOldValue[key] = newValue[key];
                }
              }
            }
          }
        }

        return this.$watch(changeDetector, $watchCollectionAction);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$digest
       * @kind function
       *
       * @description
       * Processes all of the {@link ng.$rootScope.Scope#$watch watchers} of the current scope and
       * its children. Because a {@link ng.$rootScope.Scope#$watch watcher}'s listener can change
       * the model, the `$digest()` keeps calling the {@link ng.$rootScope.Scope#$watch watchers}
       * until no more listeners are firing. This means that it is possible to get into an infinite
       * loop. This function will throw `'Maximum iteration limit exceeded.'` if the number of
       * iterations exceeds 10.
       *
       * Usually, you don't call `$digest()` directly in
       * {@link ng.directive:ngController controllers} or in
       * {@link ng.$compileProvider#directive directives}.
       * Instead, you should call {@link ng.$rootScope.Scope#$apply $apply()} (typically from within
       * a {@link ng.$compileProvider#directive directive}), which will force a `$digest()`.
       *
       * If you want to be notified whenever `$digest()` is called,
       * you can register a `watchExpression` function with
       * {@link ng.$rootScope.Scope#$watch $watch()} with no `listener`.
       *
       * In unit tests, you may need to call `$digest()` to simulate the scope life cycle.
       *
       * @example
       * ```js
           var scope = ...;
           scope.name = 'misko';
           scope.counter = 0;

           expect(scope.counter).toEqual(0);
           scope.$watch('name', function(newValue, oldValue) {
             scope.counter = scope.counter + 1;
           });
           expect(scope.counter).toEqual(0);

           scope.$digest();
           // the listener is always called during the first $digest loop after it was registered
           expect(scope.counter).toEqual(1);

           scope.$digest();
           // but now it will not be called unless the value changes
           expect(scope.counter).toEqual(1);

           scope.name = 'adam';
           scope.$digest();
           expect(scope.counter).toEqual(2);
       * ```
       *
       */
      $digest: function() {
        var watch, value, last, fn, get,
            watchers,
            dirty, ttl = TTL,
            next, current, target = asyncQueue.length ? $rootScope : this,
            watchLog = [],
            logIdx, asyncTask;

        beginPhase('$digest');
        // Check for changes to browser url that happened in sync before the call to $digest
        $browser.$$checkUrlChange();

        if (this === $rootScope && applyAsyncId !== null) {
          // If this is the root scope, and $applyAsync has scheduled a deferred $apply(), then
          // cancel the scheduled $apply and flush the queue of expressions to be evaluated.
          $browser.defer.cancel(applyAsyncId);
          flushApplyAsync();
        }

        lastDirtyWatch = null;

        do { // "while dirty" loop
          dirty = false;
          current = target;

          // It's safe for asyncQueuePosition to be a local variable here because this loop can't
          // be reentered recursively. Calling $digest from a function passed to $evalAsync would
          // lead to a '$digest already in progress' error.
          for (var asyncQueuePosition = 0; asyncQueuePosition < asyncQueue.length; asyncQueuePosition++) {
            try {
              asyncTask = asyncQueue[asyncQueuePosition];
              fn = asyncTask.fn;
              fn(asyncTask.scope, asyncTask.locals);
            } catch (e) {
              $exceptionHandler(e);
            }
            lastDirtyWatch = null;
          }
          asyncQueue.length = 0;

          traverseScopesLoop:
          do { // "traverse the scopes" loop
            if ((watchers = !current.$$suspended && current.$$watchers)) {
              // process our watches
              watchers.$$digestWatchIndex = watchers.length;
              while (watchers.$$digestWatchIndex--) {
                try {
                  watch = watchers[watchers.$$digestWatchIndex];
                  // Most common watches are on primitives, in which case we can short
                  // circuit it with === operator, only when === fails do we use .equals
                  if (watch) {
                    get = watch.get;
                    if ((value = get(current)) !== (last = watch.last) &&
                        !(watch.eq
                            ? equals(value, last)
                            : (isNumberNaN(value) && isNumberNaN(last)))) {
                      dirty = true;
                      lastDirtyWatch = watch;
                      watch.last = watch.eq ? copy(value, null) : value;
                      fn = watch.fn;
                      fn(value, ((last === initWatchVal) ? value : last), current);
                      if (ttl < 5) {
                        logIdx = 4 - ttl;
                        if (!watchLog[logIdx]) watchLog[logIdx] = [];
                        watchLog[logIdx].push({
                          msg: isFunction(watch.exp) ? 'fn: ' + (watch.exp.name || watch.exp.toString()) : watch.exp,
                          newVal: value,
                          oldVal: last
                        });
                      }
                    } else if (watch === lastDirtyWatch) {
                      // If the most recently dirty watcher is now clean, short circuit since the remaining watchers
                      // have already been tested.
                      dirty = false;
                      break traverseScopesLoop;
                    }
                  }
                } catch (e) {
                  $exceptionHandler(e);
                }
              }
            }

            // Insanity Warning: scope depth-first traversal
            // yes, this code is a bit crazy, but it works and we have tests to prove it!
            // this piece should be kept in sync with the traversal in $broadcast
            // (though it differs due to having the extra check for $$suspended and does not
            // check $$listenerCount)
            if (!(next = ((!current.$$suspended && current.$$watchersCount && current.$$childHead) ||
                (current !== target && current.$$nextSibling)))) {
              while (current !== target && !(next = current.$$nextSibling)) {
                current = current.$parent;
              }
            }
          } while ((current = next));

          // `break traverseScopesLoop;` takes us to here

          if ((dirty || asyncQueue.length) && !(ttl--)) {
            clearPhase();
            throw $rootScopeMinErr('infdig',
                '{0} $digest() iterations reached. Aborting!\n' +
                'Watchers fired in the last 5 iterations: {1}',
                TTL, watchLog);
          }

        } while (dirty || asyncQueue.length);

        clearPhase();

        // postDigestQueuePosition isn't local here because this loop can be reentered recursively.
        while (postDigestQueuePosition < postDigestQueue.length) {
          try {
            postDigestQueue[postDigestQueuePosition++]();
          } catch (e) {
            $exceptionHandler(e);
          }
        }
        postDigestQueue.length = postDigestQueuePosition = 0;

        // Check for changes to browser url that happened during the $digest
        // (for which no event is fired; e.g. via `history.pushState()`)
        $browser.$$checkUrlChange();
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$suspend
       * @kind function
       *
       * @description
       * Suspend watchers of this scope subtree so that they will not be invoked during digest.
       *
       * This can be used to optimize your application when you know that running those watchers
       * is redundant.
       *
       * **Warning**
       *
       * Suspending scopes from the digest cycle can have unwanted and difficult to debug results.
       * Only use this approach if you are confident that you know what you are doing and have
       * ample tests to ensure that bindings get updated as you expect.
       *
       * Some of the things to consider are:
       *
       * * Any external event on a directive/component will not trigger a digest while the hosting
       *   scope is suspended - even if the event handler calls `$apply()` or `$rootScope.$digest()`.
       * * Transcluded content exists on a scope that inherits from outside a directive but exists
       *   as a child of the directive's containing scope. If the containing scope is suspended the
       *   transcluded scope will also be suspended, even if the scope from which the transcluded
       *   scope inherits is not suspended.
       * * Multiple directives trying to manage the suspended status of a scope can confuse each other:
       *    * A call to `$suspend()` on an already suspended scope is a no-op.
       *    * A call to `$resume()` on a non-suspended scope is a no-op.
       *    * If two directives suspend a scope, then one of them resumes the scope, the scope will no
       *      longer be suspended. This could result in the other directive believing a scope to be
       *      suspended when it is not.
       * * If a parent scope is suspended then all its descendants will be also excluded from future
       *   digests whether or not they have been suspended themselves. Note that this also applies to
       *   isolate child scopes.
       * * Calling `$digest()` directly on a descendant of a suspended scope will still run the watchers
       *   for that scope and its descendants. When digesting we only check whether the current scope is
       *   locally suspended, rather than checking whether it has a suspended ancestor.
       * * Calling `$resume()` on a scope that has a suspended ancestor will not cause the scope to be
       *   included in future digests until all its ancestors have been resumed.
       * * Resolved promises, e.g. from explicit `$q` deferreds and `$http` calls, trigger `$apply()`
       *   against the `$rootScope` and so will still trigger a global digest even if the promise was
       *   initiated by a component that lives on a suspended scope.
       */
      $suspend: function() {
        this.$$suspended = true;
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$isSuspended
       * @kind function
       *
       * @description
       * Call this method to determine if this scope has been explicitly suspended. It will not
       * tell you whether an ancestor has been suspended.
       * To determine if this scope will be excluded from a digest triggered at the $rootScope,
       * for example, you must check all its ancestors:
       *
       * ```
       * function isExcludedFromDigest(scope) {
       *   while(scope) {
       *     if (scope.$isSuspended()) return true;
       *     scope = scope.$parent;
       *   }
       *   return false;
       * ```
       *
       * Be aware that a scope may not be included in digests if it has a suspended ancestor,
       * even if `$isSuspended()` returns false.
       *
       * @returns true if the current scope has been suspended.
       */
      $isSuspended: function() {
        return this.$$suspended;
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$resume
       * @kind function
       *
       * @description
       * Resume watchers of this scope subtree in case it was suspended.
       *
       * See {@link $rootScope.Scope#$suspend} for information about the dangers of using this approach.
       */
      $resume: function() {
        this.$$suspended = false;
      },

      /**
       * @ngdoc event
       * @name $rootScope.Scope#$destroy
       * @eventType broadcast on scope being destroyed
       *
       * @description
       * Broadcasted when a scope and its children are being destroyed.
       *
       * Note that, in AngularJS, there is also a `$destroy` jQuery event, which can be used to
       * clean up DOM bindings before an element is removed from the DOM.
       */

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$destroy
       * @kind function
       *
       * @description
       * Removes the current scope (and all of its children) from the parent scope. Removal implies
       * that calls to {@link ng.$rootScope.Scope#$digest $digest()} will no longer
       * propagate to the current scope and its children. Removal also implies that the current
       * scope is eligible for garbage collection.
       *
       * The `$destroy()` is usually used by directives such as
       * {@link ng.directive:ngRepeat ngRepeat} for managing the
       * unrolling of the loop.
       *
       * Just before a scope is destroyed, a `$destroy` event is broadcasted on this scope.
       * Application code can register a `$destroy` event handler that will give it a chance to
       * perform any necessary cleanup.
       *
       * Note that, in AngularJS, there is also a `$destroy` jQuery event, which can be used to
       * clean up DOM bindings before an element is removed from the DOM.
       */
      $destroy: function() {
        // We can't destroy a scope that has been already destroyed.
        if (this.$$destroyed) return;
        var parent = this.$parent;

        this.$broadcast('$destroy');
        this.$$destroyed = true;

        if (this === $rootScope) {
          //Remove handlers attached to window when $rootScope is removed
          $browser.$$applicationDestroyed();
        }

        incrementWatchersCount(this, -this.$$watchersCount);
        for (var eventName in this.$$listenerCount) {
          decrementListenerCount(this, this.$$listenerCount[eventName], eventName);
        }

        // sever all the references to parent scopes (after this cleanup, the current scope should
        // not be retained by any of our references and should be eligible for garbage collection)
        if (parent && parent.$$childHead === this) parent.$$childHead = this.$$nextSibling;
        if (parent && parent.$$childTail === this) parent.$$childTail = this.$$prevSibling;
        if (this.$$prevSibling) this.$$prevSibling.$$nextSibling = this.$$nextSibling;
        if (this.$$nextSibling) this.$$nextSibling.$$prevSibling = this.$$prevSibling;

        // Disable listeners, watchers and apply/digest methods
        this.$destroy = this.$digest = this.$apply = this.$evalAsync = this.$applyAsync = noop;
        this.$on = this.$watch = this.$watchGroup = function() { return noop; };
        this.$$listeners = {};

        // Disconnect the next sibling to prevent `cleanUpScope` destroying those too
        this.$$nextSibling = null;
        cleanUpScope(this);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$eval
       * @kind function
       *
       * @description
       * Executes the `expression` on the current scope and returns the result. Any exceptions in
       * the expression are propagated (uncaught). This is useful when evaluating AngularJS
       * expressions.
       *
       * @example
       * ```js
           var scope = ng.$rootScope.Scope();
           scope.a = 1;
           scope.b = 2;

           expect(scope.$eval('a+b')).toEqual(3);
           expect(scope.$eval(function(scope){ return scope.a + scope.b; })).toEqual(3);
       * ```
       *
       * @param {(string|function())=} expression An AngularJS expression to be executed.
       *
       *    - `string`: execute using the rules as defined in  {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with the current `scope` parameter.
       *
       * @param {(object)=} locals Local variables object, useful for overriding values in scope.
       * @returns {*} The result of evaluating the expression.
       */
      $eval: function(expr, locals) {
        return $parse(expr)(this, locals);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$evalAsync
       * @kind function
       *
       * @description
       * Executes the expression on the current scope at a later point in time.
       *
       * The `$evalAsync` makes no guarantees as to when the `expression` will be executed, only
       * that:
       *
       *   - it will execute after the function that scheduled the evaluation (preferably before DOM
       *     rendering).
       *   - at least one {@link ng.$rootScope.Scope#$digest $digest cycle} will be performed after
       *     `expression` execution.
       *
       * Any exceptions from the execution of the expression are forwarded to the
       * {@link ng.$exceptionHandler $exceptionHandler} service.
       *
       * __Note:__ if this function is called outside of a `$digest` cycle, a new `$digest` cycle
       * will be scheduled. However, it is encouraged to always call code that changes the model
       * from within an `$apply` call. That includes code evaluated via `$evalAsync`.
       *
       * @param {(string|function())=} expression An AngularJS expression to be executed.
       *
       *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with the current `scope` parameter.
       *
       * @param {(object)=} locals Local variables object, useful for overriding values in scope.
       */
      $evalAsync: function(expr, locals) {
        // if we are outside of an $digest loop and this is the first time we are scheduling async
        // task also schedule async auto-flush
        if (!$rootScope.$$phase && !asyncQueue.length) {
          $browser.defer(function() {
            if (asyncQueue.length) {
              $rootScope.$digest();
            }
          }, null, '$evalAsync');
        }

        asyncQueue.push({scope: this, fn: $parse(expr), locals: locals});
      },

      $$postDigest: function(fn) {
        postDigestQueue.push(fn);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$apply
       * @kind function
       *
       * @description
       * `$apply()` is used to execute an expression in AngularJS from outside of the AngularJS
       * framework. (For example from browser DOM events, setTimeout, XHR or third party libraries).
       * Because we are calling into the AngularJS framework we need to perform proper scope life
       * cycle of {@link ng.$exceptionHandler exception handling},
       * {@link ng.$rootScope.Scope#$digest executing watches}.
       *
       * **Life cycle: Pseudo-Code of `$apply()`**
       *
       * ```js
           function $apply(expr) {
             try {
               return $eval(expr);
             } catch (e) {
               $exceptionHandler(e);
             } finally {
               $root.$digest();
             }
           }
       * ```
       *
       *
       * Scope's `$apply()` method transitions through the following stages:
       *
       * 1. The {@link guide/expression expression} is executed using the
       *    {@link ng.$rootScope.Scope#$eval $eval()} method.
       * 2. Any exceptions from the execution of the expression are forwarded to the
       *    {@link ng.$exceptionHandler $exceptionHandler} service.
       * 3. The {@link ng.$rootScope.Scope#$watch watch} listeners are fired immediately after the
       *    expression was executed using the {@link ng.$rootScope.Scope#$digest $digest()} method.
       *
       *
       * @param {(string|function())=} exp An AngularJS expression to be executed.
       *
       *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with current `scope` parameter.
       *
       * @returns {*} The result of evaluating the expression.
       */
      $apply: function(expr) {
        try {
          beginPhase('$apply');
          try {
            return this.$eval(expr);
          } finally {
            clearPhase();
          }
        } catch (e) {
          $exceptionHandler(e);
        } finally {
          try {
            $rootScope.$digest();
          } catch (e) {
            $exceptionHandler(e);
            // eslint-disable-next-line no-unsafe-finally
            throw e;
          }
        }
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$applyAsync
       * @kind function
       *
       * @description
       * Schedule the invocation of $apply to occur at a later time. The actual time difference
       * varies across browsers, but is typically around ~10 milliseconds.
       *
       * This can be used to queue up multiple expressions which need to be evaluated in the same
       * digest.
       *
       * @param {(string|function())=} exp An AngularJS expression to be executed.
       *
       *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with current `scope` parameter.
       */
      $applyAsync: function(expr) {
        var scope = this;
        if (expr) {
          applyAsyncQueue.push($applyAsyncExpression);
        }
        expr = $parse(expr);
        scheduleApplyAsync();

        function $applyAsyncExpression() {
          scope.$eval(expr);
        }
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$on
       * @kind function
       *
       * @description
       * Listens on events of a given type. See {@link ng.$rootScope.Scope#$emit $emit} for
       * discussion of event life cycle.
       *
       * The event listener function format is: `function(event, args...)`. The `event` object
       * passed into the listener has the following attributes:
       *
       *   - `targetScope` - `{Scope}`: the scope on which the event was `$emit`-ed or
       *     `$broadcast`-ed.
       *   - `currentScope` - `{Scope}`: the scope that is currently handling the event. Once the
       *     event propagates through the scope hierarchy, this property is set to null.
       *   - `name` - `{string}`: name of the event.
       *   - `stopPropagation` - `{function=}`: calling `stopPropagation` function will cancel
       *     further event propagation (available only for events that were `$emit`-ed).
       *   - `preventDefault` - `{function}`: calling `preventDefault` sets `defaultPrevented` flag
       *     to true.
       *   - `defaultPrevented` - `{boolean}`: true if `preventDefault` was called.
       *
       * @param {string} name Event name to listen on.
       * @param {function(event, ...args)} listener Function to call when the event is emitted.
       * @returns {function()} Returns a deregistration function for this listener.
       */
      $on: function(name, listener) {
        var namedListeners = this.$$listeners[name];
        if (!namedListeners) {
          this.$$listeners[name] = namedListeners = [];
        }
        namedListeners.push(listener);

        var current = this;
        do {
          if (!current.$$listenerCount[name]) {
            current.$$listenerCount[name] = 0;
          }
          current.$$listenerCount[name]++;
        } while ((current = current.$parent));

        var self = this;
        return function() {
          var indexOfListener = namedListeners.indexOf(listener);
          if (indexOfListener !== -1) {
            // Use delete in the hope of the browser deallocating the memory for the array entry,
            // while not shifting the array indexes of other listeners.
            // See issue https://github.com/angular/angular.js/issues/16135
            delete namedListeners[indexOfListener];
            decrementListenerCount(self, 1, name);
          }
        };
      },


      /**
       * @ngdoc method
       * @name $rootScope.Scope#$emit
       * @kind function
       *
       * @description
       * Dispatches an event `name` upwards through the scope hierarchy notifying the
       * registered {@link ng.$rootScope.Scope#$on} listeners.
       *
       * The event life cycle starts at the scope on which `$emit` was called. All
       * {@link ng.$rootScope.Scope#$on listeners} listening for `name` event on this scope get
       * notified. Afterwards, the event traverses upwards toward the root scope and calls all
       * registered listeners along the way. The event will stop propagating if one of the listeners
       * cancels it.
       *
       * Any exception emitted from the {@link ng.$rootScope.Scope#$on listeners} will be passed
       * onto the {@link ng.$exceptionHandler $exceptionHandler} service.
       *
       * @param {string} name Event name to emit.
       * @param {...*} args Optional one or more arguments which will be passed onto the event listeners.
       * @return {Object} Event object (see {@link ng.$rootScope.Scope#$on}).
       */
      $emit: function(name, args) {
        var empty = [],
            namedListeners,
            scope = this,
            stopPropagation = false,
            event = {
              name: name,
              targetScope: scope,
              stopPropagation: function() {stopPropagation = true;},
              preventDefault: function() {
                event.defaultPrevented = true;
              },
              defaultPrevented: false
            },
            listenerArgs = concat([event], arguments, 1),
            i, length;

        do {
          namedListeners = scope.$$listeners[name] || empty;
          event.currentScope = scope;
          for (i = 0, length = namedListeners.length; i < length; i++) {

            // if listeners were deregistered, defragment the array
            if (!namedListeners[i]) {
              namedListeners.splice(i, 1);
              i--;
              length--;
              continue;
            }
            try {
              //allow all listeners attached to the current scope to run
              namedListeners[i].apply(null, listenerArgs);
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          //if any listener on the current scope stops propagation, prevent bubbling
          if (stopPropagation) {
            break;
          }
          //traverse upwards
          scope = scope.$parent;
        } while (scope);

        event.currentScope = null;

        return event;
      },


      /**
       * @ngdoc method
       * @name $rootScope.Scope#$broadcast
       * @kind function
       *
       * @description
       * Dispatches an event `name` downwards to all child scopes (and their children) notifying the
       * registered {@link ng.$rootScope.Scope#$on} listeners.
       *
       * The event life cycle starts at the scope on which `$broadcast` was called. All
       * {@link ng.$rootScope.Scope#$on listeners} listening for `name` event on this scope get
       * notified. Afterwards, the event propagates to all direct and indirect scopes of the current
       * scope and calls all registered listeners along the way. The event cannot be canceled.
       *
       * Any exception emitted from the {@link ng.$rootScope.Scope#$on listeners} will be passed
       * onto the {@link ng.$exceptionHandler $exceptionHandler} service.
       *
       * @param {string} name Event name to broadcast.
       * @param {...*} args Optional one or more arguments which will be passed onto the event listeners.
       * @return {Object} Event object, see {@link ng.$rootScope.Scope#$on}
       */
      $broadcast: function(name, args) {
        var target = this,
            current = target,
            next = target,
            event = {
              name: name,
              targetScope: target,
              preventDefault: function() {
                event.defaultPrevented = true;
              },
              defaultPrevented: false
            };

        if (!target.$$listenerCount[name]) return event;

        var listenerArgs = concat([event], arguments, 1),
            listeners, i, length;

        //down while you can, then up and next sibling or up and next sibling until back at root
        while ((current = next)) {
          event.currentScope = current;
          listeners = current.$$listeners[name] || [];
          for (i = 0, length = listeners.length; i < length; i++) {
            // if listeners were deregistered, defragment the array
            if (!listeners[i]) {
              listeners.splice(i, 1);
              i--;
              length--;
              continue;
            }

            try {
              listeners[i].apply(null, listenerArgs);
            } catch (e) {
              $exceptionHandler(e);
            }
          }

          // Insanity Warning: scope depth-first traversal
          // yes, this code is a bit crazy, but it works and we have tests to prove it!
          // this piece should be kept in sync with the traversal in $digest
          // (though it differs due to having the extra check for $$listenerCount and
          // does not check $$suspended)
          if (!(next = ((current.$$listenerCount[name] && current.$$childHead) ||
              (current !== target && current.$$nextSibling)))) {
            while (current !== target && !(next = current.$$nextSibling)) {
              current = current.$parent;
            }
          }
        }

        event.currentScope = null;
        return event;
      }
    };

    var $rootScope = new Scope();

    //The internal queues. Expose them on the $rootScope for debugging/testing purposes.
    var asyncQueue = $rootScope.$$asyncQueue = [];
    var postDigestQueue = $rootScope.$$postDigestQueue = [];
    var applyAsyncQueue = $rootScope.$$applyAsyncQueue = [];

    var postDigestQueuePosition = 0;

    return $rootScope;


    function beginPhase(phase) {
      if ($rootScope.$$phase) {
        throw $rootScopeMinErr('inprog', '{0} already in progress', $rootScope.$$phase);
      }

      $rootScope.$$phase = phase;
    }

    function clearPhase() {
      $rootScope.$$phase = null;
    }

    function incrementWatchersCount(current, count) {
      do {
        current.$$watchersCount += count;
      } while ((current = current.$parent));
    }

    function decrementListenerCount(current, count, name) {
      do {
        current.$$listenerCount[name] -= count;

        if (current.$$listenerCount[name] === 0) {
          delete current.$$listenerCount[name];
        }
      } while ((current = current.$parent));
    }

    /**
     * function used as an initial value for watchers.
     * because it's unique we can easily tell it apart from other values
     */
    function initWatchVal() {}

    function flushApplyAsync() {
      while (applyAsyncQueue.length) {
        try {
          applyAsyncQueue.shift()();
        } catch (e) {
          $exceptionHandler(e);
        }
      }
      applyAsyncId = null;
    }

    function scheduleApplyAsync() {
      if (applyAsyncId === null) {
        applyAsyncId = $browser.defer(function() {
          $rootScope.$apply(flushApplyAsync);
        }, null, '$applyAsync');
      }
    }
  }];
}

/**
 * @ngdoc service
 * @name $rootElement
 *
 * @description
 * The root element of AngularJS application. This is either the element where {@link
 * ng.directive:ngApp ngApp} was declared or the element passed into
 * {@link angular.bootstrap}. The element represents the root element of application. It is also the
 * location where the application's {@link auto.$injector $injector} service gets
 * published, and can be retrieved using `$rootElement.injector()`.
 */


// the implementation is in angular.bootstrap

/**
 * @this
 * @description
 * Private service to sanitize uris for links and images. Used by $compile and $sanitize.
 */
function $$SanitizeUriProvider() {

  var aHrefSanitizationTrustedUrlList = /^\s*(https?|s?ftp|mailto|tel|file):/,
    imgSrcSanitizationTrustedUrlList = /^\s*((https?|ftp|file|blob):|data:image\/)/;

  /**
   * @description
   * Retrieves or overrides the default regular expression that is used for determining trusted safe
   * urls during a[href] sanitization.
   *
   * The sanitization is a security measure aimed at prevent XSS attacks via HTML anchor links.
   *
   * Any url due to be assigned to an `a[href]` attribute via interpolation is marked as requiring
   * the $sce.URL security context. When interpolation occurs a call is made to `$sce.trustAsUrl(url)`
   * which in turn may call `$$sanitizeUri(url, isMedia)` to sanitize the potentially malicious URL.
   *
   * If the URL matches the `aHrefSanitizationTrustedUrlList` regular expression, it is returned unchanged.
   *
   * If there is no match the URL is returned prefixed with `'unsafe:'` to ensure that when it is written
   * to the DOM it is inactive and potentially malicious code will not be executed.
   *
   * @param {RegExp=} regexp New regexp to trust urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.aHrefSanitizationTrustedUrlList = function(regexp) {
    if (isDefined(regexp)) {
      aHrefSanitizationTrustedUrlList = regexp;
      return this;
    }
    return aHrefSanitizationTrustedUrlList;
  };


  /**
   * @description
   * Retrieves or overrides the default regular expression that is used for determining trusted safe
   * urls during img[src] sanitization.
   *
   * The sanitization is a security measure aimed at prevent XSS attacks via HTML image src links.
   *
   * Any URL due to be assigned to an `img[src]` attribute via interpolation is marked as requiring
   * the $sce.MEDIA_URL security context. When interpolation occurs a call is made to
   * `$sce.trustAsMediaUrl(url)` which in turn may call `$$sanitizeUri(url, isMedia)` to sanitize
   * the potentially malicious URL.
   *
   * If the URL matches the `imgSrcSanitizationTrustedUrlList` regular expression, it is returned
   * unchanged.
   *
   * If there is no match the URL is returned prefixed with `'unsafe:'` to ensure that when it is written
   * to the DOM it is inactive and potentially malicious code will not be executed.
   *
   * @param {RegExp=} regexp New regexp to trust urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.imgSrcSanitizationTrustedUrlList = function(regexp) {
    if (isDefined(regexp)) {
      imgSrcSanitizationTrustedUrlList = regexp;
      return this;
    }
    return imgSrcSanitizationTrustedUrlList;
  };

  this.$get = function() {
    return function sanitizeUri(uri, isMediaUrl) {
      // if (!uri) return uri;
      var regex = isMediaUrl ? imgSrcSanitizationTrustedUrlList : aHrefSanitizationTrustedUrlList;
      var normalizedVal = urlResolve(uri && uri.trim()).href;
      if (normalizedVal !== '' && !normalizedVal.match(regex)) {
        return 'unsafe:' + normalizedVal;
      }
      return uri;
    };
  };
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/* exported $SceProvider, $SceDelegateProvider */

var $sceMinErr = minErr('$sce');

var SCE_CONTEXTS = {
  // HTML is used when there's HTML rendered (e.g. ng-bind-html, iframe srcdoc binding).
  HTML: 'html',

  // Style statements or stylesheets. Currently unused in AngularJS.
  CSS: 'css',

  // An URL used in a context where it refers to the source of media, which are not expected to be run
  // as scripts, such as an image, audio, video, etc.
  MEDIA_URL: 'mediaUrl',

  // An URL used in a context where it does not refer to a resource that loads code.
  // A value that can be trusted as a URL can also trusted as a MEDIA_URL.
  URL: 'url',

  // RESOURCE_URL is a subtype of URL used where the referred-to resource could be interpreted as
  // code. (e.g. ng-include, script src binding, templateUrl)
  // A value that can be trusted as a RESOURCE_URL, can also trusted as a URL and a MEDIA_URL.
  RESOURCE_URL: 'resourceUrl',

  // Script. Currently unused in AngularJS.
  JS: 'js'
};

// Helper functions follow.

var UNDERSCORE_LOWERCASE_REGEXP = /_([a-z])/g;

function snakeToCamel(name) {
  return name
    .replace(UNDERSCORE_LOWERCASE_REGEXP, fnCamelCaseReplace);
}

function adjustMatcher(matcher) {
  if (matcher === 'self') {
    return matcher;
  } else if (isString(matcher)) {
    // Strings match exactly except for 2 wildcards - '*' and '**'.
    // '*' matches any character except those from the set ':/.?&'.
    // '**' matches any character (like .* in a RegExp).
    // More than 2 *'s raises an error as it's ill defined.
    if (matcher.indexOf('***') > -1) {
      throw $sceMinErr('iwcard',
          'Illegal sequence *** in string matcher.  String: {0}', matcher);
    }
    matcher = escapeForRegexp(matcher).
                  replace(/\\\*\\\*/g, '.*').
                  replace(/\\\*/g, '[^:/.?&;]*');
    return new RegExp('^' + matcher + '$');
  } else if (isRegExp(matcher)) {
    // The only other type of matcher allowed is a Regexp.
    // Match entire URL / disallow partial matches.
    // Flags are reset (i.e. no global, ignoreCase or multiline)
    return new RegExp('^' + matcher.source + '$');
  } else {
    throw $sceMinErr('imatcher',
        'Matchers may only be "self", string patterns or RegExp objects');
  }
}


function adjustMatchers(matchers) {
  var adjustedMatchers = [];
  if (isDefined(matchers)) {
    forEach(matchers, function(matcher) {
      adjustedMatchers.push(adjustMatcher(matcher));
    });
  }
  return adjustedMatchers;
}


/**
 * @ngdoc service
 * @name $sceDelegate
 * @kind function
 *
 * @description
 *
 * `$sceDelegate` is a service that is used by the `$sce` service to provide {@link ng.$sce Strict
 * Contextual Escaping (SCE)} services to AngularJS.
 *
 * For an overview of this service and the functionnality it provides in AngularJS, see the main
 * page for {@link ng.$sce SCE}. The current page is targeted for developers who need to alter how
 * SCE works in their application, which shouldn't be needed in most cases.
 *
 * <div class="alert alert-danger">
 * AngularJS strongly relies on contextual escaping for the security of bindings: disabling or
 * modifying this might cause cross site scripting (XSS) vulnerabilities. For libraries owners,
 * changes to this service will also influence users, so be extra careful and document your changes.
 * </div>
 *
 * Typically, you would configure or override the {@link ng.$sceDelegate $sceDelegate} instead of
 * the `$sce` service to customize the way Strict Contextual Escaping works in AngularJS.  This is
 * because, while the `$sce` provides numerous shorthand methods, etc., you really only need to
 * override 3 core functions (`trustAs`, `getTrusted` and `valueOf`) to replace the way things
 * work because `$sce` delegates to `$sceDelegate` for these operations.
 *
 * Refer {@link ng.$sceDelegateProvider $sceDelegateProvider} to configure this service.
 *
 * The default instance of `$sceDelegate` should work out of the box with little pain.  While you
 * can override it completely to change the behavior of `$sce`, the common case would
 * involve configuring the {@link ng.$sceDelegateProvider $sceDelegateProvider} instead by setting
 * your own trusted and banned resource lists for trusting URLs used for loading AngularJS resources
 * such as templates.  Refer {@link ng.$sceDelegateProvider#trustedResourceUrlList
 * $sceDelegateProvider.trustedResourceUrlList} and {@link
 * ng.$sceDelegateProvider#bannedResourceUrlList $sceDelegateProvider.bannedResourceUrlList}
 */

/**
 * @ngdoc provider
 * @name $sceDelegateProvider
 * @this
 *
 * @description
 *
 * The `$sceDelegateProvider` provider allows developers to configure the {@link ng.$sceDelegate
 * $sceDelegate service}, used as a delegate for {@link ng.$sce Strict Contextual Escaping (SCE)}.
 *
 * The `$sceDelegateProvider` allows one to get/set the `trustedResourceUrlList` and
 * `bannedResourceUrlList` used to ensure that the URLs used for sourcing AngularJS templates and
 * other script-running URLs are safe (all places that use the `$sce.RESOURCE_URL` context). See
 * {@link ng.$sceDelegateProvider#trustedResourceUrlList
 * $sceDelegateProvider.trustedResourceUrlList} and
 * {@link ng.$sceDelegateProvider#bannedResourceUrlList $sceDelegateProvider.bannedResourceUrlList},
 *
 * For the general details about this service in AngularJS, read the main page for {@link ng.$sce
 * Strict Contextual Escaping (SCE)}.
 *
 * **Example**:  Consider the following case. <a name="example"></a>
 *
 * - your app is hosted at url `http://myapp.example.com/`
 * - but some of your templates are hosted on other domains you control such as
 *   `http://srv01.assets.example.com/`, `http://srv02.assets.example.com/`, etc.
 * - and you have an open redirect at `http://myapp.example.com/clickThru?...`.
 *
 * Here is what a secure configuration for this scenario might look like:
 *
 * ```
 *  angular.module('myApp', []).config(function($sceDelegateProvider) {
 *    $sceDelegateProvider.trustedResourceUrlList([
 *      // Allow same origin resource loads.
 *      'self',
 *      // Allow loading from our assets domain.  Notice the difference between * and **.
 *      'http://srv*.assets.example.com/**'
 *    ]);
 *
 *    // The banned resource URL list overrides the trusted resource URL list so the open redirect
 *    // here is blocked.
 *    $sceDelegateProvider.bannedResourceUrlList([
 *      'http://myapp.example.com/clickThru**'
 *    ]);
 *  });
 * ```
 * Note that an empty trusted resource URL list will block every resource URL from being loaded, and will require
 * you to manually mark each one as trusted with `$sce.trustAsResourceUrl`. However, templates
 * requested by {@link ng.$templateRequest $templateRequest} that are present in
 * {@link ng.$templateCache $templateCache} will not go through this check. If you have a mechanism
 * to populate your templates in that cache at config time, then it is a good idea to remove 'self'
 * from the trusted resource URL lsit. This helps to mitigate the security impact of certain types
 * of issues, like for instance attacker-controlled `ng-includes`.
 */

function $SceDelegateProvider() {
  this.SCE_CONTEXTS = SCE_CONTEXTS;

  // Resource URLs can also be trusted by policy.
  var trustedResourceUrlList = ['self'],
      bannedResourceUrlList = [];

  /**
   * @ngdoc method
   * @name $sceDelegateProvider#trustedResourceUrlList
   * @kind function
   *
   * @param {Array=} trustedResourceUrlList When provided, replaces the trustedResourceUrlList with
   *     the value provided.  This must be an array or null.  A snapshot of this array is used so
   *     further changes to the array are ignored.
   *     Follow {@link ng.$sce#resourceUrlPatternItem this link} for a description of the items
   *     allowed in this array.
   *
   * @return {Array} The currently set trusted resource URL array.
   *
   * @description
   * Sets/Gets the list trusted of resource URLs.
   *
   * The **default value** when no `trustedResourceUrlList` has been explicitly set is `['self']`
   * allowing only same origin resource requests.
   *
   * <div class="alert alert-warning">
   * **Note:** the default `trustedResourceUrlList` of 'self' is not recommended if your app shares
   * its origin with other apps! It is a good idea to limit it to only your application's directory.
   * </div>
   */
  this.trustedResourceUrlList = function(value) {
    if (arguments.length) {
      trustedResourceUrlList = adjustMatchers(value);
    }
    return trustedResourceUrlList;
  };

  /**
   * @ngdoc method
   * @name $sceDelegateProvider#resourceUrlWhitelist
   * @kind function
   *
   * @deprecated
   * sinceVersion="1.8.1"
   *
   * This method is deprecated. Use {@link $sceDelegateProvider#trustedResourceUrlList
   * trustedResourceUrlList} instead.
   */
  Object.defineProperty(this, 'resourceUrlWhitelist', {
    get: function() {
      return this.trustedResourceUrlList;
    },
    set: function(value) {
      this.trustedResourceUrlList = value;
    }
  });

  /**
   * @ngdoc method
   * @name $sceDelegateProvider#bannedResourceUrlList
   * @kind function
   *
   * @param {Array=} bannedResourceUrlList When provided, replaces the `bannedResourceUrlList` with
   *     the value provided. This must be an array or null. A snapshot of this array is used so
   *     further changes to the array are ignored.</p><p>
   *     Follow {@link ng.$sce#resourceUrlPatternItem this link} for a description of the items
   *     allowed in this array.</p><p>
   *     The typical usage for the `bannedResourceUrlList` is to **block
   *     [open redirects](http://cwe.mitre.org/data/definitions/601.html)** served by your domain as
   *     these would otherwise be trusted but actually return content from the redirected domain.
   *     </p><p>
   *     Finally, **the banned resource URL list overrides the trusted resource URL list** and has
   *     the final say.
   *
   * @return {Array} The currently set `bannedResourceUrlList` array.
   *
   * @description
   * Sets/Gets the `bannedResourceUrlList` of trusted resource URLs.
   *
   * The **default value** when no trusted resource URL list has been explicitly set is the empty
   * array (i.e. there is no `bannedResourceUrlList`.)
   */
  this.bannedResourceUrlList = function(value) {
    if (arguments.length) {
      bannedResourceUrlList = adjustMatchers(value);
    }
    return bannedResourceUrlList;
  };

  /**
   * @ngdoc method
   * @name $sceDelegateProvider#resourceUrlBlacklist
   * @kind function
   *
   * @deprecated
   * sinceVersion="1.8.1"
   *
   * This method is deprecated. Use {@link $sceDelegateProvider#bannedResourceUrlList
   * bannedResourceUrlList} instead.
   */
  Object.defineProperty(this, 'resourceUrlBlacklist', {
    get: function() {
      return this.bannedResourceUrlList;
    },
    set: function(value) {
      this.bannedResourceUrlList = value;
    }
  });

  this.$get = ['$injector', '$$sanitizeUri', function($injector, $$sanitizeUri) {

    var htmlSanitizer = function htmlSanitizer(html) {
      throw $sceMinErr('unsafe', 'Attempting to use an unsafe value in a safe context.');
    };

    if ($injector.has('$sanitize')) {
      htmlSanitizer = $injector.get('$sanitize');
    }


    function matchUrl(matcher, parsedUrl) {
      if (matcher === 'self') {
        return urlIsSameOrigin(parsedUrl) || urlIsSameOriginAsBaseUrl(parsedUrl);
      } else {
        // definitely a regex.  See adjustMatchers()
        return !!matcher.exec(parsedUrl.href);
      }
    }

    function isResourceUrlAllowedByPolicy(url) {
      var parsedUrl = urlResolve(url.toString());
      var i, n, allowed = false;
      // Ensure that at least one item from the trusted resource URL list allows this url.
      for (i = 0, n = trustedResourceUrlList.length; i < n; i++) {
        if (matchUrl(trustedResourceUrlList[i], parsedUrl)) {
          allowed = true;
          break;
        }
      }
      if (allowed) {
        // Ensure that no item from the banned resource URL list has blocked this url.
        for (i = 0, n = bannedResourceUrlList.length; i < n; i++) {
          if (matchUrl(bannedResourceUrlList[i], parsedUrl)) {
            allowed = false;
            break;
          }
        }
      }
      return allowed;
    }

    function generateHolderType(Base) {
      var holderType = function TrustedValueHolderType(trustedValue) {
        this.$$unwrapTrustedValue = function() {
          return trustedValue;
        };
      };
      if (Base) {
        holderType.prototype = new Base();
      }
      holderType.prototype.valueOf = function sceValueOf() {
        return this.$$unwrapTrustedValue();
      };
      holderType.prototype.toString = function sceToString() {
        return this.$$unwrapTrustedValue().toString();
      };
      return holderType;
    }

    var trustedValueHolderBase = generateHolderType(),
        byType = {};

    byType[SCE_CONTEXTS.HTML] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.CSS] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.MEDIA_URL] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.URL] = generateHolderType(byType[SCE_CONTEXTS.MEDIA_URL]);
    byType[SCE_CONTEXTS.JS] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.RESOURCE_URL] = generateHolderType(byType[SCE_CONTEXTS.URL]);

    /**
     * @ngdoc method
     * @name $sceDelegate#trustAs
     *
     * @description
     * Returns a trusted representation of the parameter for the specified context. This trusted
     * object will later on be used as-is, without any security check, by bindings or directives
     * that require this security context.
     * For instance, marking a string as trusted for the `$sce.HTML` context will entirely bypass
     * the potential `$sanitize` call in corresponding `$sce.HTML` bindings or directives, such as
     * `ng-bind-html`. Note that in most cases you won't need to call this function: if you have the
     * sanitizer loaded, passing the value itself will render all the HTML that does not pose a
     * security risk.
     *
     * See {@link ng.$sceDelegate#getTrusted getTrusted} for the function that will consume those
     * trusted values, and {@link ng.$sce $sce} for general documentation about strict contextual
     * escaping.
     *
     * @param {string} type The context in which this value is safe for use, e.g. `$sce.URL`,
     *     `$sce.RESOURCE_URL`, `$sce.HTML`, `$sce.JS` or `$sce.CSS`.
     *
     * @param {*} value The value that should be considered trusted.
     * @return {*} A trusted representation of value, that can be used in the given context.
     */
    function trustAs(type, trustedValue) {
      var Constructor = (byType.hasOwnProperty(type) ? byType[type] : null);
      if (!Constructor) {
        throw $sceMinErr('icontext',
            'Attempted to trust a value in invalid context. Context: {0}; Value: {1}',
            type, trustedValue);
      }
      if (trustedValue === null || isUndefined(trustedValue) || trustedValue === '') {
        return trustedValue;
      }
      // All the current contexts in SCE_CONTEXTS happen to be strings.  In order to avoid trusting
      // mutable objects, we ensure here that the value passed in is actually a string.
      if (typeof trustedValue !== 'string') {
        throw $sceMinErr('itype',
            'Attempted to trust a non-string value in a content requiring a string: Context: {0}',
            type);
      }
      return new Constructor(trustedValue);
    }

    /**
     * @ngdoc method
     * @name $sceDelegate#valueOf
     *
     * @description
     * If the passed parameter had been returned by a prior call to {@link ng.$sceDelegate#trustAs
     * `$sceDelegate.trustAs`}, returns the value that had been passed to {@link
     * ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}.
     *
     * If the passed parameter is not a value that had been returned by {@link
     * ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}, it must be returned as-is.
     *
     * @param {*} value The result of a prior {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}
     *     call or anything else.
     * @return {*} The `value` that was originally provided to {@link ng.$sceDelegate#trustAs
     *     `$sceDelegate.trustAs`} if `value` is the result of such a call.  Otherwise, returns
     *     `value` unchanged.
     */
    function valueOf(maybeTrusted) {
      if (maybeTrusted instanceof trustedValueHolderBase) {
        return maybeTrusted.$$unwrapTrustedValue();
      } else {
        return maybeTrusted;
      }
    }

    /**
     * @ngdoc method
     * @name $sceDelegate#getTrusted
     *
     * @description
     * Given an object and a security context in which to assign it, returns a value that's safe to
     * use in this context, which was represented by the parameter. To do so, this function either
     * unwraps the safe type it has been given (for instance, a {@link ng.$sceDelegate#trustAs
     * `$sceDelegate.trustAs`} result), or it might try to sanitize the value given, depending on
     * the context and sanitizer availablility.
     *
     * The contexts that can be sanitized are $sce.MEDIA_URL, $sce.URL and $sce.HTML. The first two are available
     * by default, and the third one relies on the `$sanitize` service (which may be loaded through
     * the `ngSanitize` module). Furthermore, for $sce.RESOURCE_URL context, a plain string may be
     * accepted if the resource url policy defined by {@link ng.$sceDelegateProvider#trustedResourceUrlList
     * `$sceDelegateProvider.trustedResourceUrlList`} and {@link ng.$sceDelegateProvider#bannedResourceUrlList
     * `$sceDelegateProvider.bannedResourceUrlList`} accepts that resource.
     *
     * This function will throw if the safe type isn't appropriate for this context, or if the
     * value given cannot be accepted in the context (which might be caused by sanitization not
     * being available, or the value not being recognized as safe).
     *
     * <div class="alert alert-danger">
     * Disabling auto-escaping is extremely dangerous, it usually creates a Cross Site Scripting
     * (XSS) vulnerability in your application.
     * </div>
     *
     * @param {string} type The context in which this value is to be used (such as `$sce.HTML`).
     * @param {*} maybeTrusted The result of a prior {@link ng.$sceDelegate#trustAs
     *     `$sceDelegate.trustAs`} call, or anything else (which will not be considered trusted.)
     * @return {*} A version of the value that's safe to use in the given context, or throws an
     *     exception if this is impossible.
     */
    function getTrusted(type, maybeTrusted) {
      if (maybeTrusted === null || isUndefined(maybeTrusted) || maybeTrusted === '') {
        return maybeTrusted;
      }
      var constructor = (byType.hasOwnProperty(type) ? byType[type] : null);
      // If maybeTrusted is a trusted class instance or subclass instance, then unwrap and return
      // as-is.
      if (constructor && maybeTrusted instanceof constructor) {
        return maybeTrusted.$$unwrapTrustedValue();
      }

      // If maybeTrusted is a trusted class instance but not of the correct trusted type
      // then unwrap it and allow it to pass through to the rest of the checks
      if (isFunction(maybeTrusted.$$unwrapTrustedValue)) {
        maybeTrusted = maybeTrusted.$$unwrapTrustedValue();
      }

      // If we get here, then we will either sanitize the value or throw an exception.
      if (type === SCE_CONTEXTS.MEDIA_URL || type === SCE_CONTEXTS.URL) {
        // we attempt to sanitize non-resource URLs
        return $$sanitizeUri(maybeTrusted.toString(), type === SCE_CONTEXTS.MEDIA_URL);
      } else if (type === SCE_CONTEXTS.RESOURCE_URL) {
        if (isResourceUrlAllowedByPolicy(maybeTrusted)) {
          return maybeTrusted;
        } else {
          throw $sceMinErr('insecurl',
              'Blocked loading resource from url not allowed by $sceDelegate policy.  URL: {0}',
              maybeTrusted.toString());
        }
      } else if (type === SCE_CONTEXTS.HTML) {
        // htmlSanitizer throws its own error when no sanitizer is available.
        return htmlSanitizer(maybeTrusted);
      }
      // Default error when the $sce service has no way to make the input safe.
      throw $sceMinErr('unsafe', 'Attempting to use an unsafe value in a safe context.');
    }

    return { trustAs: trustAs,
             getTrusted: getTrusted,
             valueOf: valueOf };
  }];
}


/**
 * @ngdoc provider
 * @name $sceProvider
 * @this
 *
 * @description
 *
 * The $sceProvider provider allows developers to configure the {@link ng.$sce $sce} service.
 * -   enable/disable Strict Contextual Escaping (SCE) in a module
 * -   override the default implementation with a custom delegate
 *
 * Read more about {@link ng.$sce Strict Contextual Escaping (SCE)}.
 */

/**
 * @ngdoc service
 * @name $sce
 * @kind function
 *
 * @description
 *
 * `$sce` is a service that provides Strict Contextual Escaping services to AngularJS.
 *
 * ## Strict Contextual Escaping
 *
 * Strict Contextual Escaping (SCE) is a mode in which AngularJS constrains bindings to only render
 * trusted values. Its goal is to assist in writing code in a way that (a) is secure by default, and
 * (b) makes auditing for security vulnerabilities such as XSS, clickjacking, etc. a lot easier.
 *
 * ### Overview
 *
 * To systematically block XSS security bugs, AngularJS treats all values as untrusted by default in
 * HTML or sensitive URL bindings. When binding untrusted values, AngularJS will automatically
 * run security checks on them (sanitizations, trusted URL resource, depending on context), or throw
 * when it cannot guarantee the security of the result. That behavior depends strongly on contexts:
 * HTML can be sanitized, but template URLs cannot, for instance.
 *
 * To illustrate this, consider the `ng-bind-html` directive. It renders its value directly as HTML:
 * we call that the *context*. When given an untrusted input, AngularJS will attempt to sanitize it
 * before rendering if a sanitizer is available, and throw otherwise. To bypass sanitization and
 * render the input as-is, you will need to mark it as trusted for that context before attempting
 * to bind it.
 *
 * As of version 1.2, AngularJS ships with SCE enabled by default.
 *
 * ### In practice
 *
 * Here's an example of a binding in a privileged context:
 *
 * ```
 * <input ng-model="userHtml" aria-label="User input">
 * <div ng-bind-html="userHtml"></div>
 * ```
 *
 * Notice that `ng-bind-html` is bound to `userHtml` controlled by the user.  With SCE
 * disabled, this application allows the user to render arbitrary HTML into the DIV, which would
 * be an XSS security bug. In a more realistic example, one may be rendering user comments, blog
 * articles, etc. via bindings. (HTML is just one example of a context where rendering user
 * controlled input creates security vulnerabilities.)
 *
 * For the case of HTML, you might use a library, either on the client side, or on the server side,
 * to sanitize unsafe HTML before binding to the value and rendering it in the document.
 *
 * How would you ensure that every place that used these types of bindings was bound to a value that
 * was sanitized by your library (or returned as safe for rendering by your server?)  How can you
 * ensure that you didn't accidentally delete the line that sanitized the value, or renamed some
 * properties/fields and forgot to update the binding to the sanitized value?
 *
 * To be secure by default, AngularJS makes sure bindings go through that sanitization, or
 * any similar validation process, unless there's a good reason to trust the given value in this
 * context.  That trust is formalized with a function call. This means that as a developer, you
 * can assume all untrusted bindings are safe. Then, to audit your code for binding security issues,
 * you just need to ensure the values you mark as trusted indeed are safe - because they were
 * received from your server, sanitized by your library, etc. You can organize your codebase to
 * help with this - perhaps allowing only the files in a specific directory to do this.
 * Ensuring that the internal API exposed by that code doesn't markup arbitrary values as safe then
 * becomes a more manageable task.
 *
 * In the case of AngularJS' SCE service, one uses {@link ng.$sce#trustAs $sce.trustAs}
 * (and shorthand methods such as {@link ng.$sce#trustAsHtml $sce.trustAsHtml}, etc.) to
 * build the trusted versions of your values.
 *
 * ### How does it work?
 *
 * In privileged contexts, directives and code will bind to the result of {@link ng.$sce#getTrusted
 * $sce.getTrusted(context, value)} rather than to the value directly.  Think of this function as
 * a way to enforce the required security context in your data sink. Directives use {@link
 * ng.$sce#parseAs $sce.parseAs} rather than `$parse` to watch attribute bindings, which performs
 * the {@link ng.$sce#getTrusted $sce.getTrusted} behind the scenes on non-constant literals. Also,
 * when binding without directives, AngularJS will understand the context of your bindings
 * automatically.
 *
 * As an example, {@link ng.directive:ngBindHtml ngBindHtml} uses {@link
 * ng.$sce#parseAsHtml $sce.parseAsHtml(binding expression)}.  Here's the actual code (slightly
 * simplified):
 *
 * ```
 * var ngBindHtmlDirective = ['$sce', function($sce) {
 *   return function(scope, element, attr) {
 *     scope.$watch($sce.parseAsHtml(attr.ngBindHtml), function(value) {
 *       element.html(value || '');
 *     });
 *   };
 * }];
 * ```
 *
 * ### Impact on loading templates
 *
 * This applies both to the {@link ng.directive:ngInclude `ng-include`} directive as well as
 * `templateUrl`'s specified by {@link guide/directive directives}.
 *
 * By default, AngularJS only loads templates from the same domain and protocol as the application
 * document.  This is done by calling {@link ng.$sce#getTrustedResourceUrl
 * $sce.getTrustedResourceUrl} on the template URL.  To load templates from other domains and/or
 * protocols, you may either add them to the {@link ng.$sceDelegateProvider#trustedResourceUrlList
 * trustedResourceUrlList} or {@link ng.$sce#trustAsResourceUrl wrap them} into trusted values.
 *
 * *Please note*:
 * The browser's
 * [Same Origin Policy](https://code.google.com/p/browsersec/wiki/Part2#Same-origin_policy_for_XMLHttpRequest)
 * and [Cross-Origin Resource Sharing (CORS)](http://www.w3.org/TR/cors/)
 * policy apply in addition to this and may further restrict whether the template is successfully
 * loaded.  This means that without the right CORS policy, loading templates from a different domain
 * won't work on all browsers.  Also, loading templates from `file://` URL does not work on some
 * browsers.
 *
 * ### This feels like too much overhead
 *
 * It's important to remember that SCE only applies to interpolation expressions.
 *
 * If your expressions are constant literals, they're automatically trusted and you don't need to
 * call `$sce.trustAs` on them (e.g.
 * `<div ng-bind-html="'<b>implicitly trusted</b>'"></div>`) just works (remember to include the
 * `ngSanitize` module). The `$sceDelegate` will also use the `$sanitize` service if it is available
 * when binding untrusted values to `$sce.HTML` context.
 * AngularJS provides an implementation in `angular-sanitize.js`, and if you
 * wish to use it, you will also need to depend on the {@link ngSanitize `ngSanitize`} module in
 * your application.
 *
 * The included {@link ng.$sceDelegate $sceDelegate} comes with sane defaults to allow you to load
 * templates in `ng-include` from your application's domain without having to even know about SCE.
 * It blocks loading templates from other domains or loading templates over http from an https
 * served document.  You can change these by setting your own custom {@link
 * ng.$sceDelegateProvider#trustedResourceUrlList trusted resource URL list} and {@link
 * ng.$sceDelegateProvider#bannedResourceUrlList banned resource URL list} for matching such URLs.
 *
 * This significantly reduces the overhead.  It is far easier to pay the small overhead and have an
 * application that's secure and can be audited to verify that with much more ease than bolting
 * security onto an application later.
 *
 * <a name="contexts"></a>
 * ### What trusted context types are supported?
 *
 * | Context             | Notes          |
 * |---------------------|----------------|
 * | `$sce.HTML`         | For HTML that's safe to source into the application.  The {@link ng.directive:ngBindHtml ngBindHtml} directive uses this context for bindings. If an unsafe value is encountered and the {@link ngSanitize $sanitize} module is present this will sanitize the value instead of throwing an error. |
 * | `$sce.CSS`          | For CSS that's safe to source into the application.  Currently unused.  Feel free to use it in your own directives. |
 * | `$sce.MEDIA_URL`    | For URLs that are safe to render as media. Is automatically converted from string by sanitizing when needed. |
 * | `$sce.URL`          | For URLs that are safe to follow as links. Is automatically converted from string by sanitizing when needed. Note that `$sce.URL` makes a stronger statement about the URL than `$sce.MEDIA_URL` does and therefore contexts requiring values trusted for `$sce.URL` can be used anywhere that values trusted for `$sce.MEDIA_URL` are required.|
 * | `$sce.RESOURCE_URL` | For URLs that are not only safe to follow as links, but whose contents are also safe to include in your application.  Examples include `ng-include`, `src` / `ngSrc` bindings for tags other than `IMG` (e.g. `IFRAME`, `OBJECT`, etc.)  <br><br>Note that `$sce.RESOURCE_URL` makes a stronger statement about the URL than `$sce.URL` or `$sce.MEDIA_URL` do and therefore contexts requiring values trusted for `$sce.RESOURCE_URL` can be used anywhere that values trusted for `$sce.URL` or `$sce.MEDIA_URL` are required. <br><br> The {@link $sceDelegateProvider#trustedResourceUrlList $sceDelegateProvider#trustedResourceUrlList()} and {@link $sceDelegateProvider#bannedResourceUrlList $sceDelegateProvider#bannedResourceUrlList()} can be used to restrict trusted origins for `RESOURCE_URL` |
 * | `$sce.JS`           | For JavaScript that is safe to execute in your application's context.  Currently unused.  Feel free to use it in your own directives. |
 *
 *
 * <div class="alert alert-warning">
 * Be aware that, before AngularJS 1.7.0, `a[href]` and `img[src]` used to sanitize their
 * interpolated values directly rather than rely upon {@link ng.$sce#getTrusted `$sce.getTrusted`}.
 *
 * **As of 1.7.0, this is no longer the case.**
 *
 * Now such interpolations are marked as requiring `$sce.URL` (for `a[href]`) or `$sce.MEDIA_URL`
 * (for `img[src]`), so that the sanitization happens (via `$sce.getTrusted...`) when the `$interpolate`
 * service evaluates the expressions.
 * </div>
 *
 * There are no CSS or JS context bindings in AngularJS currently, so their corresponding `$sce.trustAs`
 * functions aren't useful yet. This might evolve.
 *
 * ### Format of items in {@link ng.$sceDelegateProvider#trustedResourceUrlList trustedResourceUrlList}/{@link ng.$sceDelegateProvider#bannedResourceUrlList bannedResourceUrlList} <a name="resourceUrlPatternItem"></a>
 *
 *  Each element in these arrays must be one of the following:
 *
 *  - **'self'**
 *    - The special **string**, `'self'`, can be used to match against all URLs of the **same
 *      domain** as the application document using the **same protocol**.
 *  - **String** (except the special value `'self'`)
 *    - The string is matched against the full *normalized / absolute URL* of the resource
 *      being tested (substring matches are not good enough.)
 *    - There are exactly **two wildcard sequences** - `*` and `**`.  All other characters
 *      match themselves.
 *    - `*`: matches zero or more occurrences of any character other than one of the following 6
 *      characters: '`:`', '`/`', '`.`', '`?`', '`&`' and '`;`'.  It's a useful wildcard for use
 *      for matching resource URL lists.
 *    - `**`: matches zero or more occurrences of *any* character.  As such, it's not
 *      appropriate for use in a scheme, domain, etc. as it would match too much.  (e.g.
 *      http://**.example.com/ would match http://evil.com/?ignore=.example.com/ and that might
 *      not have been the intention.)  Its usage at the very end of the path is ok.  (e.g.
 *      http://foo.example.com/templates/**).
 *  - **RegExp** (*see caveat below*)
 *    - *Caveat*:  While regular expressions are powerful and offer great flexibility,  their syntax
 *      (and all the inevitable escaping) makes them *harder to maintain*.  It's easy to
 *      accidentally introduce a bug when one updates a complex expression (imho, all regexes should
 *      have good test coverage).  For instance, the use of `.` in the regex is correct only in a
 *      small number of cases.  A `.` character in the regex used when matching the scheme or a
 *      subdomain could be matched against a `:` or literal `.` that was likely not intended.   It
 *      is highly recommended to use the string patterns and only fall back to regular expressions
 *      as a last resort.
 *    - The regular expression must be an instance of RegExp (i.e. not a string.)  It is
 *      matched against the **entire** *normalized / absolute URL* of the resource being tested
 *      (even when the RegExp did not have the `^` and `$` codes.)  In addition, any flags
 *      present on the RegExp (such as multiline, global, ignoreCase) are ignored.
 *    - If you are generating your JavaScript from some other templating engine (not
 *      recommended, e.g. in issue [#4006](https://github.com/angular/angular.js/issues/4006)),
 *      remember to escape your regular expression (and be aware that you might need more than
 *      one level of escaping depending on your templating engine and the way you interpolated
 *      the value.)  Do make use of your platform's escaping mechanism as it might be good
 *      enough before coding your own.  E.g. Ruby has
 *      [Regexp.escape(str)](http://www.ruby-doc.org/core-2.0.0/Regexp.html#method-c-escape)
 *      and Python has [re.escape](http://docs.python.org/library/re.html#re.escape).
 *      Javascript lacks a similar built in function for escaping.  Take a look at Google
 *      Closure library's [goog.string.regExpEscape(s)](
 *      http://docs.closure-library.googlecode.com/git/closure_goog_string_string.js.source.html#line962).
 *
 * Refer {@link ng.$sceDelegateProvider $sceDelegateProvider} for an example.
 *
 * ### Show me an example using SCE.
 *
 * <example module="mySceApp" deps="angular-sanitize.js" name="sce-service">
 * <file name="index.html">
 *   <div ng-controller="AppController as myCtrl">
 *     <i ng-bind-html="myCtrl.explicitlyTrustedHtml" id="explicitlyTrustedHtml"></i><br><br>
 *     <b>User comments</b><br>
 *     By default, HTML that isn't explicitly trusted (e.g. Alice's comment) is sanitized when
 *     $sanitize is available.  If $sanitize isn't available, this results in an error instead of an
 *     exploit.
 *     <div class="well">
 *       <div ng-repeat="userComment in myCtrl.userComments">
 *         <b>{{userComment.name}}</b>:
 *         <span ng-bind-html="userComment.htmlComment" class="htmlComment"></span>
 *         <br>
 *       </div>
 *     </div>
 *   </div>
 * </file>
 *
 * <file name="script.js">
 *   angular.module('mySceApp', ['ngSanitize'])
 *     .controller('AppController', ['$http', '$templateCache', '$sce',
 *       function AppController($http, $templateCache, $sce) {
 *         var self = this;
 *         $http.get('test_data.json', {cache: $templateCache}).then(function(response) {
 *           self.userComments = response.data;
 *         });
 *         self.explicitlyTrustedHtml = $sce.trustAsHtml(
 *             '<span onmouseover="this.textContent=&quot;Explicitly trusted HTML bypasses ' +
 *             'sanitization.&quot;">Hover over this text.</span>');
 *       }]);
 * </file>
 *
 * <file name="test_data.json">
 * [
 *   { "name": "Alice",
 *     "htmlComment":
 *         "<span onmouseover='this.textContent=\"PWN3D!\"'>Is <i>anyone</i> reading this?</span>"
 *   },
 *   { "name": "Bob",
 *     "htmlComment": "<i>Yes!</i>  Am I the only other one?"
 *   }
 * ]
 * </file>
 *
 * <file name="protractor.js" type="protractor">
 *   describe('SCE doc demo', function() {
 *     it('should sanitize untrusted values', function() {
 *       expect(element.all(by.css('.htmlComment')).first().getAttribute('innerHTML'))
 *           .toBe('<span>Is <i>anyone</i> reading this?</span>');
 *     });
 *
 *     it('should NOT sanitize explicitly trusted values', function() {
 *       expect(element(by.id('explicitlyTrustedHtml')).getAttribute('innerHTML')).toBe(
 *           '<span onmouseover="this.textContent=&quot;Explicitly trusted HTML bypasses ' +
 *           'sanitization.&quot;">Hover over this text.</span>');
 *     });
 *   });
 * </file>
 * </example>
 *
 *
 *
 * ## Can I disable SCE completely?
 *
 * Yes, you can.  However, this is strongly discouraged.  SCE gives you a lot of security benefits
 * for little coding overhead.  It will be much harder to take an SCE disabled application and
 * either secure it on your own or enable SCE at a later stage.  It might make sense to disable SCE
 * for cases where you have a lot of existing code that was written before SCE was introduced and
 * you're migrating them a module at a time. Also do note that this is an app-wide setting, so if
 * you are writing a library, you will cause security bugs applications using it.
 *
 * That said, here's how you can completely disable SCE:
 *
 * ```
 * angular.module('myAppWithSceDisabledmyApp', []).config(function($sceProvider) {
 *   // Completely disable SCE.  For demonstration purposes only!
 *   // Do not use in new projects or libraries.
 *   $sceProvider.enabled(false);
 * });
 * ```
 *
 */

function $SceProvider() {
  var enabled = true;

  /**
   * @ngdoc method
   * @name $sceProvider#enabled
   * @kind function
   *
   * @param {boolean=} value If provided, then enables/disables SCE application-wide.
   * @return {boolean} True if SCE is enabled, false otherwise.
   *
   * @description
   * Enables/disables SCE and returns the current value.
   */
  this.enabled = function(value) {
    if (arguments.length) {
      enabled = !!value;
    }
    return enabled;
  };


  /* Design notes on the default implementation for SCE.
   *
   * The API contract for the SCE delegate
   * -------------------------------------
   * The SCE delegate object must provide the following 3 methods:
   *
   * - trustAs(contextEnum, value)
   *     This method is used to tell the SCE service that the provided value is OK to use in the
   *     contexts specified by contextEnum.  It must return an object that will be accepted by
   *     getTrusted() for a compatible contextEnum and return this value.
   *
   * - valueOf(value)
   *     For values that were not produced by trustAs(), return them as is.  For values that were
   *     produced by trustAs(), return the corresponding input value to trustAs.  Basically, if
   *     trustAs is wrapping the given values into some type, this operation unwraps it when given
   *     such a value.
   *
   * - getTrusted(contextEnum, value)
   *     This function should return the value that is safe to use in the context specified by
   *     contextEnum or throw and exception otherwise.
   *
   * NOTE: This contract deliberately does NOT state that values returned by trustAs() must be
   * opaque or wrapped in some holder object.  That happens to be an implementation detail.  For
   * instance, an implementation could maintain a registry of all trusted objects by context.  In
   * such a case, trustAs() would return the same object that was passed in.  getTrusted() would
   * return the same object passed in if it was found in the registry under a compatible context or
   * throw an exception otherwise.  An implementation might only wrap values some of the time based
   * on some criteria.  getTrusted() might return a value and not throw an exception for special
   * constants or objects even if not wrapped.  All such implementations fulfill this contract.
   *
   *
   * A note on the inheritance model for SCE contexts
   * ------------------------------------------------
   * I've used inheritance and made RESOURCE_URL wrapped types a subtype of URL wrapped types.  This
   * is purely an implementation details.
   *
   * The contract is simply this:
   *
   *     getTrusted($sce.RESOURCE_URL, value) succeeding implies that getTrusted($sce.URL, value)
   *     will also succeed.
   *
   * Inheritance happens to capture this in a natural way. In some future, we may not use
   * inheritance anymore. That is OK because no code outside of sce.js and sceSpecs.js would need to
   * be aware of this detail.
   */

  this.$get = ['$parse', '$sceDelegate', function(
                $parse,   $sceDelegate) {
    // Support: IE 9-11 only
    // Prereq: Ensure that we're not running in IE<11 quirks mode.  In that mode, IE < 11 allow
    // the "expression(javascript expression)" syntax which is insecure.
    if (enabled && msie < 8) {
      throw $sceMinErr('iequirks',
        'Strict Contextual Escaping does not support Internet Explorer version < 11 in quirks ' +
        'mode.  You can fix this by adding the text <!doctype html> to the top of your HTML ' +
        'document.  See http://docs.angularjs.org/api/ng.$sce for more information.');
    }

    var sce = shallowCopy(SCE_CONTEXTS);

    /**
     * @ngdoc method
     * @name $sce#isEnabled
     * @kind function
     *
     * @return {Boolean} True if SCE is enabled, false otherwise.  If you want to set the value, you
     *     have to do it at module config time on {@link ng.$sceProvider $sceProvider}.
     *
     * @description
     * Returns a boolean indicating if SCE is enabled.
     */
    sce.isEnabled = function() {
      return enabled;
    };
    sce.trustAs = $sceDelegate.trustAs;
    sce.getTrusted = $sceDelegate.getTrusted;
    sce.valueOf = $sceDelegate.valueOf;

    if (!enabled) {
      sce.trustAs = sce.getTrusted = function(type, value) { return value; };
      sce.valueOf = identity;
    }

    /**
     * @ngdoc method
     * @name $sce#parseAs
     *
     * @description
     * Converts AngularJS {@link guide/expression expression} into a function.  This is like {@link
     * ng.$parse $parse} and is identical when the expression is a literal constant.  Otherwise, it
     * wraps the expression in a call to {@link ng.$sce#getTrusted $sce.getTrusted(*type*,
     * *result*)}
     *
     * @param {string} type The SCE context in which this result will be used.
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */
    sce.parseAs = function sceParseAs(type, expr) {
      var parsed = $parse(expr);
      if (parsed.literal && parsed.constant) {
        return parsed;
      } else {
        return $parse(expr, function(value) {
          return sce.getTrusted(type, value);
        });
      }
    };

    /**
     * @ngdoc method
     * @name $sce#trustAs
     *
     * @description
     * Delegates to {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}. As such, returns a
     * wrapped object that represents your value, and the trust you have in its safety for the given
     * context. AngularJS can then use that value as-is in bindings of the specified secure context.
     * This is used in bindings for `ng-bind-html`, `ng-include`, and most `src` attribute
     * interpolations. See {@link ng.$sce $sce} for strict contextual escaping.
     *
     * @param {string} type The context in which this value is safe for use, e.g. `$sce.URL`,
     *     `$sce.RESOURCE_URL`, `$sce.HTML`, `$sce.JS` or `$sce.CSS`.
     *
     * @param {*} value The value that that should be considered trusted.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in the context you specified.
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsHtml
     *
     * @description
     * Shorthand method.  `$sce.trustAsHtml(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.HTML, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.HTML` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in `$sce.HTML` context (like `ng-bind-html`).
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsCss
     *
     * @description
     * Shorthand method.  `$sce.trustAsCss(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.CSS, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.CSS` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant
     *     of your `value` in `$sce.CSS` context. This context is currently unused, so there are
     *     almost no reasons to use this function so far.
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsUrl
     *
     * @description
     * Shorthand method.  `$sce.trustAsUrl(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.URL, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.URL` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in `$sce.URL` context. That context is currently unused, so there are almost no reasons
     *     to use this function so far.
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsResourceUrl
     *
     * @description
     * Shorthand method.  `$sce.trustAsResourceUrl(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.RESOURCE_URL, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.RESOURCE_URL` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in `$sce.RESOURCE_URL` context (template URLs in `ng-include`, most `src` attribute
     *     bindings, ...)
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsJs
     *
     * @description
     * Shorthand method.  `$sce.trustAsJs(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.JS, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.JS` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in `$sce.JS` context. That context is currently unused, so there are almost no reasons to
     *     use this function so far.
     */

    /**
     * @ngdoc method
     * @name $sce#getTrusted
     *
     * @description
     * Delegates to {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted`}.  As such,
     * takes any input, and either returns a value that's safe to use in the specified context,
     * or throws an exception. This function is aware of trusted values created by the `trustAs`
     * function and its shorthands, and when contexts are appropriate, returns the unwrapped value
     * as-is. Finally, this function can also throw when there is no way to turn `maybeTrusted` in a
     * safe value (e.g., no sanitization is available or possible.)
     *
     * @param {string} type The context in which this value is to be used.
     * @param {*} maybeTrusted The result of a prior {@link ng.$sce#trustAs
     *     `$sce.trustAs`} call, or anything else (which will not be considered trusted.)
     * @return {*} A version of the value that's safe to use in the given context, or throws an
     *     exception if this is impossible.
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedHtml
     *
     * @description
     * Shorthand method.  `$sce.getTrustedHtml(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.HTML, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.HTML, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedCss
     *
     * @description
     * Shorthand method.  `$sce.getTrustedCss(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.CSS, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.CSS, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedUrl
     *
     * @description
     * Shorthand method.  `$sce.getTrustedUrl(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.URL, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.URL, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedResourceUrl
     *
     * @description
     * Shorthand method.  `$sce.getTrustedResourceUrl(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.RESOURCE_URL, value)`}
     *
     * @param {*} value The value to pass to `$sceDelegate.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.RESOURCE_URL, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedJs
     *
     * @description
     * Shorthand method.  `$sce.getTrustedJs(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.JS, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.JS, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsHtml
     *
     * @description
     * Shorthand method.  `$sce.parseAsHtml(expression string)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.HTML, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsCss
     *
     * @description
     * Shorthand method.  `$sce.parseAsCss(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.CSS, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsUrl
     *
     * @description
     * Shorthand method.  `$sce.parseAsUrl(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.URL, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsResourceUrl
     *
     * @description
     * Shorthand method.  `$sce.parseAsResourceUrl(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.RESOURCE_URL, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsJs
     *
     * @description
     * Shorthand method.  `$sce.parseAsJs(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.JS, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    // Shorthand delegations.
    var parse = sce.parseAs,
        getTrusted = sce.getTrusted,
        trustAs = sce.trustAs;

    forEach(SCE_CONTEXTS, function(enumValue, name) {
      var lName = lowercase(name);
      sce[snakeToCamel('parse_as_' + lName)] = function(expr) {
        return parse(enumValue, expr);
      };
      sce[snakeToCamel('get_trusted_' + lName)] = function(value) {
        return getTrusted(enumValue, value);
      };
      sce[snakeToCamel('trust_as_' + lName)] = function(value) {
        return trustAs(enumValue, value);
      };
    });

    return sce;
  }];
}

/* exported $SnifferProvider */

/**
 * !!! This is an undocumented "private" service !!!
 *
 * @name $sniffer
 * @requires $window
 * @requires $document
 * @this
 *
 * @property {boolean} history Does the browser support html5 history api ?
 * @property {boolean} transitions Does the browser support CSS transition events ?
 * @property {boolean} animations Does the browser support CSS animation events ?
 *
 * @description
 * This is very simple implementation of testing browser's features.
 */
function $SnifferProvider() {
  this.$get = ['$window', '$document', function($window, $document) {
    var eventSupport = {},
        // Chrome Packaged Apps are not allowed to access `history.pushState`.
        // If not sandboxed, they can be detected by the presence of `chrome.app.runtime`
        // (see https://developer.chrome.com/apps/api_index). If sandboxed, they can be detected by
        // the presence of an extension runtime ID and the absence of other Chrome runtime APIs
        // (see https://developer.chrome.com/apps/manifest/sandbox).
        // (NW.js apps have access to Chrome APIs, but do support `history`.)
        isNw = $window.nw && $window.nw.process,
        isChromePackagedApp =
            !isNw &&
            $window.chrome &&
            ($window.chrome.app && $window.chrome.app.runtime ||
                !$window.chrome.app && $window.chrome.runtime && $window.chrome.runtime.id),
        hasHistoryPushState = !isChromePackagedApp && $window.history && $window.history.pushState,
        android =
          toInt((/android (\d+)/.exec(lowercase(($window.navigator || {}).userAgent)) || [])[1]),
        boxee = /Boxee/i.test(($window.navigator || {}).userAgent),
        document = $document[0] || {},
        bodyStyle = document.body && document.body.style,
        transitions = false,
        animations = false;

    if (bodyStyle) {
      // Support: Android <5, Blackberry Browser 10, default Chrome in Android 4.4.x
      // Mentioned browsers need a -webkit- prefix for transitions & animations.
      transitions = !!('transition' in bodyStyle || 'webkitTransition' in bodyStyle);
      animations = !!('animation' in bodyStyle || 'webkitAnimation' in bodyStyle);
    }


    return {
      // Android has history.pushState, but it does not update location correctly
      // so let's not use the history API at all.
      // http://code.google.com/p/android/issues/detail?id=17471
      // https://github.com/angular/angular.js/issues/904

      // older webkit browser (533.9) on Boxee box has exactly the same problem as Android has
      // so let's not use the history API also
      // We are purposefully using `!(android < 4)` to cover the case when `android` is undefined
      history: !!(hasHistoryPushState && !(android < 4) && !boxee),
      hasEvent: function(event) {
        // Support: IE 9-11 only
        // IE9 implements 'input' event it's so fubared that we rather pretend that it doesn't have
        // it. In particular the event is not fired when backspace or delete key are pressed or
        // when cut operation is performed.
        // IE10+ implements 'input' event but it erroneously fires under various situations,
        // e.g. when placeholder changes, or a form is focused.
        if (event === 'input' && msie) return false;

        if (isUndefined(eventSupport[event])) {
          var divElm = document.createElement('div');
          eventSupport[event] = 'on' + event in divElm;
        }

        return eventSupport[event];
      },
      csp: csp(),
      transitions: transitions,
      animations: animations,
      android: android
    };
  }];
}

/**
 * ! This is a private undocumented service !
 *
 * @name $$taskTrackerFactory
 * @description
 * A function to create `TaskTracker` instances.
 *
 * A `TaskTracker` can keep track of pending tasks (grouped by type) and can notify interested
 * parties when all pending tasks (or tasks of a specific type) have been completed.
 *
 * @param {$log} log - A logger instance (such as `$log`). Used to log error during callback
 *     execution.
 *
 * @this
 */
function $$TaskTrackerFactoryProvider() {
  this.$get = valueFn(function(log) { return new TaskTracker(log); });
}

function TaskTracker(log) {
  var self = this;
  var taskCounts = {};
  var taskCallbacks = [];

  var ALL_TASKS_TYPE = self.ALL_TASKS_TYPE = '$$all$$';
  var DEFAULT_TASK_TYPE = self.DEFAULT_TASK_TYPE = '$$default$$';

  /**
   * Execute the specified function and decrement the appropriate `taskCounts` counter.
   * If the counter reaches 0, all corresponding `taskCallbacks` are executed.
   *
   * @param {Function} fn - The function to execute.
   * @param {string=} [taskType=DEFAULT_TASK_TYPE] - The type of task that is being completed.
   */
  self.completeTask = completeTask;

  /**
   * Increase the task count for the specified task type (or the default task type if non is
   * specified).
   *
   * @param {string=} [taskType=DEFAULT_TASK_TYPE] - The type of task whose count will be increased.
   */
  self.incTaskCount = incTaskCount;

  /**
   * Execute the specified callback when all pending tasks have been completed.
   *
   * If there are no pending tasks, the callback is executed immediately. You can optionally limit
   * the tasks that will be waited for to a specific type, by passing a `taskType`.
   *
   * @param {function} callback - The function to call when there are no pending tasks.
   * @param {string=} [taskType=ALL_TASKS_TYPE] - The type of tasks that will be waited for.
   */
  self.notifyWhenNoPendingTasks = notifyWhenNoPendingTasks;

  function completeTask(fn, taskType) {
    taskType = taskType || DEFAULT_TASK_TYPE;

    try {
      fn();
    } finally {
      decTaskCount(taskType);

      var countForType = taskCounts[taskType];
      var countForAll = taskCounts[ALL_TASKS_TYPE];

      // If at least one of the queues (`ALL_TASKS_TYPE` or `taskType`) is empty, run callbacks.
      if (!countForAll || !countForType) {
        var getNextCallback = !countForAll ? getLastCallback : getLastCallbackForType;
        var nextCb;

        while ((nextCb = getNextCallback(taskType))) {
          try {
            nextCb();
          } catch (e) {
            log.error(e);
          }
        }
      }
    }
  }

  function decTaskCount(taskType) {
    taskType = taskType || DEFAULT_TASK_TYPE;
    if (taskCounts[taskType]) {
      taskCounts[taskType]--;
      taskCounts[ALL_TASKS_TYPE]--;
    }
  }

  function getLastCallback() {
    var cbInfo = taskCallbacks.pop();
    return cbInfo && cbInfo.cb;
  }

  function getLastCallbackForType(taskType) {
    for (var i = taskCallbacks.length - 1; i >= 0; --i) {
      var cbInfo = taskCallbacks[i];
      if (cbInfo.type === taskType) {
        taskCallbacks.splice(i, 1);
        return cbInfo.cb;
      }
    }
  }

  function incTaskCount(taskType) {
    taskType = taskType || DEFAULT_TASK_TYPE;
    taskCounts[taskType] = (taskCounts[taskType] || 0) + 1;
    taskCounts[ALL_TASKS_TYPE] = (taskCounts[ALL_TASKS_TYPE] || 0) + 1;
  }

  function notifyWhenNoPendingTasks(callback, taskType) {
    taskType = taskType || ALL_TASKS_TYPE;
    if (!taskCounts[taskType]) {
      callback();
    } else {
      taskCallbacks.push({type: taskType, cb: callback});
    }
  }
}

var $templateRequestMinErr = minErr('$templateRequest');

/**
 * @ngdoc provider
 * @name $templateRequestProvider
 * @this
 *
 * @description
 * Used to configure the options passed to the {@link $http} service when making a template request.
 *
 * For example, it can be used for specifying the "Accept" header that is sent to the server, when
 * requesting a template.
 */
function $TemplateRequestProvider() {

  var httpOptions;

  /**
   * @ngdoc method
   * @name $templateRequestProvider#httpOptions
   * @description
   * The options to be passed to the {@link $http} service when making the request.
   * You can use this to override options such as the "Accept" header for template requests.
   *
   * The {@link $templateRequest} will set the `cache` and the `transformResponse` properties of the
   * options if not overridden here.
   *
   * @param {string=} value new value for the {@link $http} options.
   * @returns {string|self} Returns the {@link $http} options when used as getter and self if used as setter.
   */
  this.httpOptions = function(val) {
    if (val) {
      httpOptions = val;
      return this;
    }
    return httpOptions;
  };

  /**
   * @ngdoc service
   * @name $templateRequest
   *
   * @description
   * The `$templateRequest` service runs security checks then downloads the provided template using
   * `$http` and, upon success, stores the contents inside of `$templateCache`. If the HTTP request
   * fails or the response data of the HTTP request is empty, a `$compile` error will be thrown (the
   * exception can be thwarted by setting the 2nd parameter of the function to true). Note that the
   * contents of `$templateCache` are trusted, so the call to `$sce.getTrustedUrl(tpl)` is omitted
   * when `tpl` is of type string and `$templateCache` has the matching entry.
   *
   * If you want to pass custom options to the `$http` service, such as setting the Accept header you
   * can configure this via {@link $templateRequestProvider#httpOptions}.
   *
   * `$templateRequest` is used internally by {@link $compile}, {@link ngRoute.$route}, and directives such
   * as {@link ngInclude} to download and cache templates.
   *
   * 3rd party modules should use `$templateRequest` if their services or directives are loading
   * templates.
   *
   * @param {string|TrustedResourceUrl} tpl The HTTP request template URL
   * @param {boolean=} ignoreRequestError Whether or not to ignore the exception when the request fails or the template is empty
   *
   * @return {Promise} a promise for the HTTP response data of the given URL.
   *
   * @property {number} totalPendingRequests total amount of pending template requests being downloaded.
   */
  this.$get = ['$exceptionHandler', '$templateCache', '$http', '$q', '$sce',
    function($exceptionHandler, $templateCache, $http, $q, $sce) {

      function handleRequestFn(tpl, ignoreRequestError) {
        handleRequestFn.totalPendingRequests++;

        // We consider the template cache holds only trusted templates, so
        // there's no need to go through adding the template again to the trusted
        // resources for keys that already are included in there. This also makes
        // AngularJS accept any script directive, no matter its name. However, we
        // still need to unwrap trusted types.
        if (!isString(tpl) || isUndefined($templateCache.get(tpl))) {
          tpl = $sce.getTrustedResourceUrl(tpl);
        }

        var transformResponse = $http.defaults && $http.defaults.transformResponse;

        if (isArray(transformResponse)) {
          transformResponse = transformResponse.filter(function(transformer) {
            return transformer !== defaultHttpResponseTransform;
          });
        } else if (transformResponse === defaultHttpResponseTransform) {
          transformResponse = null;
        }

        return $http.get(tpl, extend({
            cache: $templateCache,
            transformResponse: transformResponse
          }, httpOptions))
          .finally(function() {
            handleRequestFn.totalPendingRequests--;
          })
          .then(function(response) {
            return $templateCache.put(tpl, response.data);
          }, handleError);

        function handleError(resp) {
          if (!ignoreRequestError) {
            resp = $templateRequestMinErr('tpload',
                'Failed to load template: {0} (HTTP status: {1} {2})',
                tpl, resp.status, resp.statusText);

            $exceptionHandler(resp);
          }

          return $q.reject(resp);
        }
      }

      handleRequestFn.totalPendingRequests = 0;

      return handleRequestFn;
    }
  ];
}

/** @this */
function $$TestabilityProvider() {
  this.$get = ['$rootScope', '$browser', '$location',
       function($rootScope,   $browser,   $location) {

    /**
     * @name $testability
     *
     * @description
     * The private $$testability service provides a collection of methods for use when debugging
     * or by automated test and debugging tools.
     */
    var testability = {};

    /**
     * @name $$testability#findBindings
     *
     * @description
     * Returns an array of elements that are bound (via ng-bind or {{}})
     * to expressions matching the input.
     *
     * @param {Element} element The element root to search from.
     * @param {string} expression The binding expression to match.
     * @param {boolean} opt_exactMatch If true, only returns exact matches
     *     for the expression. Filters and whitespace are ignored.
     */
    testability.findBindings = function(element, expression, opt_exactMatch) {
      var bindings = element.getElementsByClassName('ng-binding');
      var matches = [];
      forEach(bindings, function(binding) {
        var dataBinding = angular.element(binding).data('$binding');
        if (dataBinding) {
          forEach(dataBinding, function(bindingName) {
            if (opt_exactMatch) {
              var matcher = new RegExp('(^|\\s)' + escapeForRegexp(expression) + '(\\s|\\||$)');
              if (matcher.test(bindingName)) {
                matches.push(binding);
              }
            } else {
              if (bindingName.indexOf(expression) !== -1) {
                matches.push(binding);
              }
            }
          });
        }
      });
      return matches;
    };

    /**
     * @name $$testability#findModels
     *
     * @description
     * Returns an array of elements that are two-way found via ng-model to
     * expressions matching the input.
     *
     * @param {Element} element The element root to search from.
     * @param {string} expression The model expression to match.
     * @param {boolean} opt_exactMatch If true, only returns exact matches
     *     for the expression.
     */
    testability.findModels = function(element, expression, opt_exactMatch) {
      var prefixes = ['ng-', 'data-ng-', 'ng\\:'];
      for (var p = 0; p < prefixes.length; ++p) {
        var attributeEquals = opt_exactMatch ? '=' : '*=';
        var selector = '[' + prefixes[p] + 'model' + attributeEquals + '"' + expression + '"]';
        var elements = element.querySelectorAll(selector);
        if (elements.length) {
          return elements;
        }
      }
    };

    /**
     * @name $$testability#getLocation
     *
     * @description
     * Shortcut for getting the location in a browser agnostic way. Returns
     *     the path, search, and hash. (e.g. /path?a=b#hash)
     */
    testability.getLocation = function() {
      return $location.url();
    };

    /**
     * @name $$testability#setLocation
     *
     * @description
     * Shortcut for navigating to a location without doing a full page reload.
     *
     * @param {string} url The location url (path, search and hash,
     *     e.g. /path?a=b#hash) to go to.
     */
    testability.setLocation = function(url) {
      if (url !== $location.url()) {
        $location.url(url);
        $rootScope.$digest();
      }
    };

    /**
     * @name $$testability#whenStable
     *
     * @description
     * Calls the callback when all pending tasks are completed.
     *
     * Types of tasks waited for include:
     * - Pending timeouts (via {@link $timeout}).
     * - Pending HTTP requests (via {@link $http}).
     * - In-progress route transitions (via {@link $route}).
     * - Pending tasks scheduled via {@link $rootScope#$applyAsync}.
     * - Pending tasks scheduled via {@link $rootScope#$evalAsync}.
     *   These include tasks scheduled via `$evalAsync()` indirectly (such as {@link $q} promises).
     *
     * @param {function} callback
     */
    testability.whenStable = function(callback) {
      $browser.notifyWhenNoOutstandingRequests(callback);
    };

    return testability;
  }];
}

var $timeoutMinErr = minErr('$timeout');

/** @this */
function $TimeoutProvider() {
  this.$get = ['$rootScope', '$browser', '$q', '$$q', '$exceptionHandler',
       function($rootScope,   $browser,   $q,   $$q,   $exceptionHandler) {

    var deferreds = {};


    /**
     * @ngdoc service
     * @name $timeout
     *
     * @description
     * AngularJS's wrapper for `window.setTimeout`. The `fn` function is wrapped into a try/catch
     * block and delegates any exceptions to
     * {@link ng.$exceptionHandler $exceptionHandler} service.
     *
     * The return value of calling `$timeout` is a promise, which will be resolved when
     * the delay has passed and the timeout function, if provided, is executed.
     *
     * To cancel a timeout request, call `$timeout.cancel(promise)`.
     *
     * In tests you can use {@link ngMock.$timeout `$timeout.flush()`} to
     * synchronously flush the queue of deferred functions.
     *
     * If you only want a promise that will be resolved after some specified delay
     * then you can call `$timeout` without the `fn` function.
     *
     * @param {function()=} fn A function, whose execution should be delayed.
     * @param {number=} [delay=0] Delay in milliseconds.
     * @param {boolean=} [invokeApply=true] If set to `false` skips model dirty checking, otherwise
     *   will invoke `fn` within the {@link ng.$rootScope.Scope#$apply $apply} block.
     * @param {...*=} Pass additional parameters to the executed function.
     * @returns {Promise} Promise that will be resolved when the timeout is reached. The promise
     *   will be resolved with the return value of the `fn` function.
     *
     */
    function timeout(fn, delay, invokeApply) {
      if (!isFunction(fn)) {
        invokeApply = delay;
        delay = fn;
        fn = noop;
      }

      var args = sliceArgs(arguments, 3),
          skipApply = (isDefined(invokeApply) && !invokeApply),
          deferred = (skipApply ? $$q : $q).defer(),
          promise = deferred.promise,
          timeoutId;

      timeoutId = $browser.defer(function() {
        try {
          deferred.resolve(fn.apply(null, args));
        } catch (e) {
          deferred.reject(e);
          $exceptionHandler(e);
        } finally {
          delete deferreds[promise.$$timeoutId];
        }

        if (!skipApply) $rootScope.$apply();
      }, delay, '$timeout');

      promise.$$timeoutId = timeoutId;
      deferreds[timeoutId] = deferred;

      return promise;
    }


    /**
     * @ngdoc method
     * @name $timeout#cancel
     *
     * @description
     * Cancels a task associated with the `promise`. As a result of this, the promise will be
     * resolved with a rejection.
     *
     * @param {Promise=} promise Promise returned by the `$timeout` function.
     * @returns {boolean} Returns `true` if the task hasn't executed yet and was successfully
     *   canceled.
     */
    timeout.cancel = function(promise) {
      if (!promise) return false;

      if (!promise.hasOwnProperty('$$timeoutId')) {
        throw $timeoutMinErr('badprom',
            '`$timeout.cancel()` called with a promise that was not generated by `$timeout()`.');
      }

      if (!deferreds.hasOwnProperty(promise.$$timeoutId)) return false;

      var id = promise.$$timeoutId;
      var deferred = deferreds[id];

      // Timeout cancels should not report an unhandled promise.
      markQExceptionHandled(deferred.promise);
      deferred.reject('canceled');
      delete deferreds[id];

      return $browser.defer.cancel(id);
    };

    return timeout;
  }];
}

// NOTE:  The usage of window and document instead of $window and $document here is
// deliberate.  This service depends on the specific behavior of anchor nodes created by the
// browser (resolving and parsing URLs) that is unlikely to be provided by mock objects and
// cause us to break tests.  In addition, when the browser resolves a URL for XHR, it
// doesn't know about mocked locations and resolves URLs to the real document - which is
// exactly the behavior needed here.  There is little value is mocking these out for this
// service.
var urlParsingNode = window.document.createElement('a');
var originUrl = urlResolve(window.location.href);
var baseUrlParsingNode;

urlParsingNode.href = 'http://[::1]';

// Support: IE 9-11 only, Edge 16-17 only (fixed in 18 Preview)
// IE/Edge don't wrap IPv6 addresses' hostnames in square brackets
// when parsed out of an anchor element.
var ipv6InBrackets = urlParsingNode.hostname === '[::1]';

/**
 *
 * Implementation Notes for non-IE browsers
 * ----------------------------------------
 * Assigning a URL to the href property of an anchor DOM node, even one attached to the DOM,
 * results both in the normalizing and parsing of the URL.  Normalizing means that a relative
 * URL will be resolved into an absolute URL in the context of the application document.
 * Parsing means that the anchor node's host, hostname, protocol, port, pathname and related
 * properties are all populated to reflect the normalized URL.  This approach has wide
 * compatibility - Safari 1+, Mozilla 1+ etc.  See
 * http://www.aptana.com/reference/html/api/HTMLAnchorElement.html
 *
 * Implementation Notes for IE
 * ---------------------------
 * IE <= 10 normalizes the URL when assigned to the anchor node similar to the other
 * browsers.  However, the parsed components will not be set if the URL assigned did not specify
 * them.  (e.g. if you assign a.href = "foo", then a.protocol, a.host, etc. will be empty.)  We
 * work around that by performing the parsing in a 2nd step by taking a previously normalized
 * URL (e.g. by assigning to a.href) and assigning it a.href again.  This correctly populates the
 * properties such as protocol, hostname, port, etc.
 *
 * References:
 *   http://developer.mozilla.org/en-US/docs/Web/API/HTMLAnchorElement
 *   http://www.aptana.com/reference/html/api/HTMLAnchorElement.html
 *   http://url.spec.whatwg.org/#urlutils
 *   https://github.com/angular/angular.js/pull/2902
 *   http://james.padolsey.com/javascript/parsing-urls-with-the-dom/
 *
 * @kind function
 * @param {string|object} url The URL to be parsed. If `url` is not a string, it will be returned
 *     unchanged.
 * @description Normalizes and parses a URL.
 * @returns {object} Returns the normalized URL as a dictionary.
 *
 *   | member name   | Description                                                            |
 *   |---------------|------------------------------------------------------------------------|
 *   | href          | A normalized version of the provided URL if it was not an absolute URL |
 *   | protocol      | The protocol without the trailing colon                                |
 *   | host          | The host and port (if the port is non-default) of the normalizedUrl    |
 *   | search        | The search params, minus the question mark                             |
 *   | hash          | The hash string, minus the hash symbol                                 |
 *   | hostname      | The hostname                                                           |
 *   | port          | The port, without ":"                                                  |
 *   | pathname      | The pathname, beginning with "/"                                       |
 *
 */
function urlResolve(url) {
  if (!isString(url)) return url;

  var href = url;

  // Support: IE 9-11 only
  if (msie) {
    // Normalize before parse.  Refer Implementation Notes on why this is
    // done in two steps on IE.
    urlParsingNode.setAttribute('href', href);
    href = urlParsingNode.href;
  }

  urlParsingNode.setAttribute('href', href);

  var hostname = urlParsingNode.hostname;

  if (!ipv6InBrackets && hostname.indexOf(':') > -1) {
    hostname = '[' + hostname + ']';
  }

  return {
    href: urlParsingNode.href,
    protocol: urlParsingNode.protocol ? urlParsingNode.protocol.replace(/:$/, '') : '',
    host: urlParsingNode.host,
    search: urlParsingNode.search ? urlParsingNode.search.replace(/^\?/, '') : '',
    hash: urlParsingNode.hash ? urlParsingNode.hash.replace(/^#/, '') : '',
    hostname: hostname,
    port: urlParsingNode.port,
    pathname: (urlParsingNode.pathname.charAt(0) === '/')
      ? urlParsingNode.pathname
      : '/' + urlParsingNode.pathname
  };
}

/**
 * Parse a request URL and determine whether this is a same-origin request as the application
 * document.
 *
 * @param {string|object} requestUrl The url of the request as a string that will be resolved
 * or a parsed URL object.
 * @returns {boolean} Whether the request is for the same origin as the application document.
 */
function urlIsSameOrigin(requestUrl) {
  return urlsAreSameOrigin(requestUrl, originUrl);
}

/**
 * Parse a request URL and determine whether it is same-origin as the current document base URL.
 *
 * Note: The base URL is usually the same as the document location (`location.href`) but can
 * be overriden by using the `<base>` tag.
 *
 * @param {string|object} requestUrl The url of the request as a string that will be resolved
 * or a parsed URL object.
 * @returns {boolean} Whether the URL is same-origin as the document base URL.
 */
function urlIsSameOriginAsBaseUrl(requestUrl) {
  return urlsAreSameOrigin(requestUrl, getBaseUrl());
}

/**
 * Create a function that can check a URL's origin against a list of allowed/trusted origins.
 * The current location's origin is implicitly trusted.
 *
 * @param {string[]} trustedOriginUrls - A list of URLs (strings), whose origins are trusted.
 *
 * @returns {Function} - A function that receives a URL (string or parsed URL object) and returns
 *     whether it is of an allowed origin.
 */
function urlIsAllowedOriginFactory(trustedOriginUrls) {
  var parsedAllowedOriginUrls = [originUrl].concat(trustedOriginUrls.map(urlResolve));

  /**
   * Check whether the specified URL (string or parsed URL object) has an origin that is allowed
   * based on a list of trusted-origin URLs. The current location's origin is implicitly
   * trusted.
   *
   * @param {string|Object} requestUrl - The URL to be checked (provided as a string that will be
   *     resolved or a parsed URL object).
   *
   * @returns {boolean} - Whether the specified URL is of an allowed origin.
   */
  return function urlIsAllowedOrigin(requestUrl) {
    var parsedUrl = urlResolve(requestUrl);
    return parsedAllowedOriginUrls.some(urlsAreSameOrigin.bind(null, parsedUrl));
  };
}

/**
 * Determine if two URLs share the same origin.
 *
 * @param {string|Object} url1 - First URL to compare as a string or a normalized URL in the form of
 *     a dictionary object returned by `urlResolve()`.
 * @param {string|object} url2 - Second URL to compare as a string or a normalized URL in the form
 *     of a dictionary object returned by `urlResolve()`.
 *
 * @returns {boolean} - True if both URLs have the same origin, and false otherwise.
 */
function urlsAreSameOrigin(url1, url2) {
  url1 = urlResolve(url1);
  url2 = urlResolve(url2);

  return (url1.protocol === url2.protocol &&
          url1.host === url2.host);
}

/**
 * Returns the current document base URL.
 * @returns {string}
 */
function getBaseUrl() {
  if (window.document.baseURI) {
    return window.document.baseURI;
  }

  // `document.baseURI` is available everywhere except IE
  if (!baseUrlParsingNode) {
    baseUrlParsingNode = window.document.createElement('a');
    baseUrlParsingNode.href = '.';

    // Work-around for IE bug described in Implementation Notes. The fix in `urlResolve()` is not
    // suitable here because we need to track changes to the base URL.
    baseUrlParsingNode = baseUrlParsingNode.cloneNode(false);
  }
  return baseUrlParsingNode.href;
}

/**
 * @ngdoc service
 * @name $window
 * @this
 *
 * @description
 * A reference to the browser's `window` object. While `window`
 * is globally available in JavaScript, it causes testability problems, because
 * it is a global variable. In AngularJS we always refer to it through the
 * `$window` service, so it may be overridden, removed or mocked for testing.
 *
 * Expressions, like the one defined for the `ngClick` directive in the example
 * below, are evaluated with respect to the current scope.  Therefore, there is
 * no risk of inadvertently coding in a dependency on a global value in such an
 * expression.
 *
 * @example
   <example module="windowExample" name="window-service">
     <file name="index.html">
       <script>
         angular.module('windowExample', [])
           .controller('ExampleController', ['$scope', '$window', function($scope, $window) {
             $scope.greeting = 'Hello, World!';
             $scope.doGreeting = function(greeting) {
               $window.alert(greeting);
             };
           }]);
       </script>
       <div ng-controller="ExampleController">
         <input type="text" ng-model="greeting" aria-label="greeting" />
         <button ng-click="doGreeting(greeting)">ALERT</button>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
      it('should display the greeting in the input box', function() {
       element(by.model('greeting')).sendKeys('Hello, E2E Tests');
       // If we click the button it will block the test runner
       // element(':button').click();
      });
     </file>
   </example>
 */
function $WindowProvider() {
  this.$get = valueFn(window);
}

/**
 * @name $$cookieReader
 * @requires $document
 *
 * @description
 * This is a private service for reading cookies used by $http and ngCookies
 *
 * @return {Object} a key/value map of the current cookies
 */
function $$CookieReader($document) {
  var rawDocument = $document[0] || {};
  var lastCookies = {};
  var lastCookieString = '';

  function safeGetCookie(rawDocument) {
    try {
      return rawDocument.cookie || '';
    } catch (e) {
      return '';
    }
  }

  function safeDecodeURIComponent(str) {
    try {
      return decodeURIComponent(str);
    } catch (e) {
      return str;
    }
  }

  return function() {
    var cookieArray, cookie, i, index, name;
    var currentCookieString = safeGetCookie(rawDocument);

    if (currentCookieString !== lastCookieString) {
      lastCookieString = currentCookieString;
      cookieArray = lastCookieString.split('; ');
      lastCookies = {};

      for (i = 0; i < cookieArray.length; i++) {
        cookie = cookieArray[i];
        index = cookie.indexOf('=');
        if (index > 0) { //ignore nameless cookies
          name = safeDecodeURIComponent(cookie.substring(0, index));
          // the first value that is seen for a cookie is the most
          // specific one.  values for the same cookie name that
          // follow are for less specific paths.
          if (isUndefined(lastCookies[name])) {
            lastCookies[name] = safeDecodeURIComponent(cookie.substring(index + 1));
          }
        }
      }
    }
    return lastCookies;
  };
}

$$CookieReader.$inject = ['$document'];

/** @this */
function $$CookieReaderProvider() {
  this.$get = $$CookieReader;
}

/* global currencyFilter: true,
 dateFilter: true,
 filterFilter: true,
 jsonFilter: true,
 limitToFilter: true,
 lowercaseFilter: true,
 numberFilter: true,
 orderByFilter: true,
 uppercaseFilter: true,
 */

/**
 * @ngdoc provider
 * @name $filterProvider
 * @description
 *
 * Filters are just functions which transform input to an output. However filters need to be
 * Dependency Injected. To achieve this a filter definition consists of a factory function which is
 * annotated with dependencies and is responsible for creating a filter function.
 *
 * <div class="alert alert-warning">
 * **Note:** Filter names must be valid AngularJS {@link expression} identifiers, such as `uppercase` or `orderBy`.
 * Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
 * your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
 * (`myapp_subsection_filterx`).
 * </div>
 *
 * ```js
 *   // Filter registration
 *   function MyModule($provide, $filterProvider) {
 *     // create a service to demonstrate injection (not always needed)
 *     $provide.value('greet', function(name){
 *       return 'Hello ' + name + '!';
 *     });
 *
 *     // register a filter factory which uses the
 *     // greet service to demonstrate DI.
 *     $filterProvider.register('greet', function(greet){
 *       // return the filter function which uses the greet service
 *       // to generate salutation
 *       return function(text) {
 *         // filters need to be forgiving so check input validity
 *         return text && greet(text) || text;
 *       };
 *     });
 *   }
 * ```
 *
 * The filter function is registered with the `$injector` under the filter name suffix with
 * `Filter`.
 *
 * ```js
 *   it('should be the same instance', inject(
 *     function($filterProvider) {
 *       $filterProvider.register('reverse', function(){
 *         return ...;
 *       });
 *     },
 *     function($filter, reverseFilter) {
 *       expect($filter('reverse')).toBe(reverseFilter);
 *     });
 * ```
 *
 *
 * For more information about how AngularJS filters work, and how to create your own filters, see
 * {@link guide/filter Filters} in the AngularJS Developer Guide.
 */

/**
 * @ngdoc service
 * @name $filter
 * @kind function
 * @description
 * Filters are used for formatting data displayed to the user.
 *
 * They can be used in view templates, controllers or services. AngularJS comes
 * with a collection of [built-in filters](api/ng/filter), but it is easy to
 * define your own as well.
 *
 * The general syntax in templates is as follows:
 *
 * ```html
 * {{ expression [| filter_name[:parameter_value] ... ] }}
 * ```
 *
 * @param {String} name Name of the filter function to retrieve
 * @return {Function} the filter function
 * @example
   <example name="$filter" module="filterExample">
     <file name="index.html">
       <div ng-controller="MainCtrl">
        <h3>{{ originalText }}</h3>
        <h3>{{ filteredText }}</h3>
       </div>
     </file>

     <file name="script.js">
      angular.module('filterExample', [])
      .controller('MainCtrl', function($scope, $filter) {
        $scope.originalText = 'hello';
        $scope.filteredText = $filter('uppercase')($scope.originalText);
      });
     </file>
   </example>
  */
$FilterProvider.$inject = ['$provide'];
/** @this */
function $FilterProvider($provide) {
  var suffix = 'Filter';

  /**
   * @ngdoc method
   * @name $filterProvider#register
   * @param {string|Object} name Name of the filter function, or an object map of filters where
   *    the keys are the filter names and the values are the filter factories.
   *
   *    <div class="alert alert-warning">
   *    **Note:** Filter names must be valid AngularJS {@link expression} identifiers, such as `uppercase` or `orderBy`.
   *    Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
   *    your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
   *    (`myapp_subsection_filterx`).
   *    </div>
    * @param {Function} factory If the first argument was a string, a factory function for the filter to be registered.
   * @returns {Object} Registered filter instance, or if a map of filters was provided then a map
   *    of the registered filter instances.
   */
  function register(name, factory) {
    if (isObject(name)) {
      var filters = {};
      forEach(name, function(filter, key) {
        filters[key] = register(key, filter);
      });
      return filters;
    } else {
      return $provide.factory(name + suffix, factory);
    }
  }
  this.register = register;

  this.$get = ['$injector', function($injector) {
    return function(name) {
      return $injector.get(name + suffix);
    };
  }];

  ////////////////////////////////////////

  /* global
    currencyFilter: false,
    dateFilter: false,
    filterFilter: false,
    jsonFilter: false,
    limitToFilter: false,
    lowercaseFilter: false,
    numberFilter: false,
    orderByFilter: false,
    uppercaseFilter: false
  */

  register('currency', currencyFilter);
  register('date', dateFilter);
  register('filter', filterFilter);
  register('json', jsonFilter);
  register('limitTo', limitToFilter);
  register('lowercase', lowercaseFilter);
  register('number', numberFilter);
  register('orderBy', orderByFilter);
  register('uppercase', uppercaseFilter);
}

/**
 * @ngdoc filter
 * @name filter
 * @kind function
 *
 * @description
 * Selects a subset of items from `array` and returns it as a new array.
 *
 * @param {Array} array The source array.
 * <div class="alert alert-info">
 *   **Note**: If the array contains objects that reference themselves, filtering is not possible.
 * </div>
 * @param {string|Object|function()} expression The predicate to be used for selecting items from
 *   `array`.
 *
 *   Can be one of:
 *
 *   - `string`: The string is used for matching against the contents of the `array`. All strings or
 *     objects with string properties in `array` that match this string will be returned. This also
 *     applies to nested object properties.
 *     The predicate can be negated by prefixing the string with `!`.
 *
 *   - `Object`: A pattern object can be used to filter specific properties on objects contained
 *     by `array`. For example `{name:"M", phone:"1"}` predicate will return an array of items
 *     which have property `name` containing "M" and property `phone` containing "1". A special
 *     property name (`$` by default) can be used (e.g. as in `{$: "text"}`) to accept a match
 *     against any property of the object or its nested object properties. That's equivalent to the
 *     simple substring match with a `string` as described above. The special property name can be
 *     overwritten, using the `anyPropertyKey` parameter.
 *     The predicate can be negated by prefixing the string with `!`.
 *     For example `{name: "!M"}` predicate will return an array of items which have property `name`
 *     not containing "M".
 *
 *     Note that a named property will match properties on the same level only, while the special
 *     `$` property will match properties on the same level or deeper. E.g. an array item like
 *     `{name: {first: 'John', last: 'Doe'}}` will **not** be matched by `{name: 'John'}`, but
 *     **will** be matched by `{$: 'John'}`.
 *
 *   - `function(value, index, array)`: A predicate function can be used to write arbitrary filters.
 *     The function is called for each element of the array, with the element, its index, and
 *     the entire array itself as arguments.
 *
 *     The final result is an array of those elements that the predicate returned true for.
 *
 * @param {function(actual, expected)|true|false} [comparator] Comparator which is used in
 *     determining if values retrieved using `expression` (when it is not a function) should be
 *     considered a match based on the expected value (from the filter expression) and actual
 *     value (from the object in the array).
 *
 *   Can be one of:
 *
 *   - `function(actual, expected)`:
 *     The function will be given the object value and the predicate value to compare and
 *     should return true if both values should be considered equal.
 *
 *   - `true`: A shorthand for `function(actual, expected) { return angular.equals(actual, expected)}`.
 *     This is essentially strict comparison of expected and actual.
 *
 *   - `false`: A short hand for a function which will look for a substring match in a case
 *     insensitive way. Primitive values are converted to strings. Objects are not compared against
 *     primitives, unless they have a custom `toString` method (e.g. `Date` objects).
 *
 *
 *   Defaults to `false`.
 *
 * @param {string} [anyPropertyKey] The special property name that matches against any property.
 *     By default `$`.
 *
 * @example
   <example name="filter-filter">
     <file name="index.html">
       <div ng-init="friends = [{name:'John', phone:'555-1276'},
                                {name:'Mary', phone:'800-BIG-MARY'},
                                {name:'Mike', phone:'555-4321'},
                                {name:'Adam', phone:'555-5678'},
                                {name:'Julie', phone:'555-8765'},
                                {name:'Juliette', phone:'555-5678'}]"></div>

       <label>Search: <input ng-model="searchText"></label>
       <table id="searchTextResults">
         <tr><th>Name</th><th>Phone</th></tr>
         <tr ng-repeat="friend in friends | filter:searchText">
           <td>{{friend.name}}</td>
           <td>{{friend.phone}}</td>
         </tr>
       </table>
       <hr>
       <label>Any: <input ng-model="search.$"></label> <br>
       <label>Name only <input ng-model="search.name"></label><br>
       <label>Phone only <input ng-model="search.phone"></label><br>
       <label>Equality <input type="checkbox" ng-model="strict"></label><br>
       <table id="searchObjResults">
         <tr><th>Name</th><th>Phone</th></tr>
         <tr ng-repeat="friendObj in friends | filter:search:strict">
           <td>{{friendObj.name}}</td>
           <td>{{friendObj.phone}}</td>
         </tr>
       </table>
     </file>
     <file name="protractor.js" type="protractor">
       var expectFriendNames = function(expectedNames, key) {
         element.all(by.repeater(key + ' in friends').column(key + '.name')).then(function(arr) {
           arr.forEach(function(wd, i) {
             expect(wd.getText()).toMatch(expectedNames[i]);
           });
         });
       };

       it('should search across all fields when filtering with a string', function() {
         var searchText = element(by.model('searchText'));
         searchText.clear();
         searchText.sendKeys('m');
         expectFriendNames(['Mary', 'Mike', 'Adam'], 'friend');

         searchText.clear();
         searchText.sendKeys('76');
         expectFriendNames(['John', 'Julie'], 'friend');
       });

       it('should search in specific fields when filtering with a predicate object', function() {
         var searchAny = element(by.model('search.$'));
         searchAny.clear();
         searchAny.sendKeys('i');
         expectFriendNames(['Mary', 'Mike', 'Julie', 'Juliette'], 'friendObj');
       });
       it('should use a equal comparison when comparator is true', function() {
         var searchName = element(by.model('search.name'));
         var strict = element(by.model('strict'));
         searchName.clear();
         searchName.sendKeys('Julie');
         strict.click();
         expectFriendNames(['Julie'], 'friendObj');
       });
     </file>
   </example>
 */

function filterFilter() {
  return function(array, expression, comparator, anyPropertyKey) {
    if (!isArrayLike(array)) {
      if (array == null) {
        return array;
      } else {
        throw minErr('filter')('notarray', 'Expected array but received: {0}', array);
      }
    }

    anyPropertyKey = anyPropertyKey || '$';
    var expressionType = getTypeForFilter(expression);
    var predicateFn;
    var matchAgainstAnyProp;

    switch (expressionType) {
      case 'function':
        predicateFn = expression;
        break;
      case 'boolean':
      case 'null':
      case 'number':
      case 'string':
        matchAgainstAnyProp = true;
        // falls through
      case 'object':
        predicateFn = createPredicateFn(expression, comparator, anyPropertyKey, matchAgainstAnyProp);
        break;
      default:
        return array;
    }

    return Array.prototype.filter.call(array, predicateFn);
  };
}

// Helper functions for `filterFilter`
function createPredicateFn(expression, comparator, anyPropertyKey, matchAgainstAnyProp) {
  var shouldMatchPrimitives = isObject(expression) && (anyPropertyKey in expression);
  var predicateFn;

  if (comparator === true) {
    comparator = equals;
  } else if (!isFunction(comparator)) {
    comparator = function(actual, expected) {
      if (isUndefined(actual)) {
        // No substring matching against `undefined`
        return false;
      }
      if ((actual === null) || (expected === null)) {
        // No substring matching against `null`; only match against `null`
        return actual === expected;
      }
      if (isObject(expected) || (isObject(actual) && !hasCustomToString(actual))) {
        // Should not compare primitives against objects, unless they have custom `toString` method
        return false;
      }

      actual = lowercase('' + actual);
      expected = lowercase('' + expected);
      return actual.indexOf(expected) !== -1;
    };
  }

  predicateFn = function(item) {
    if (shouldMatchPrimitives && !isObject(item)) {
      return deepCompare(item, expression[anyPropertyKey], comparator, anyPropertyKey, false);
    }
    return deepCompare(item, expression, comparator, anyPropertyKey, matchAgainstAnyProp);
  };

  return predicateFn;
}

function deepCompare(actual, expected, comparator, anyPropertyKey, matchAgainstAnyProp, dontMatchWholeObject) {
  var actualType = getTypeForFilter(actual);
  var expectedType = getTypeForFilter(expected);

  if ((expectedType === 'string') && (expected.charAt(0) === '!')) {
    return !deepCompare(actual, expected.substring(1), comparator, anyPropertyKey, matchAgainstAnyProp);
  } else if (isArray(actual)) {
    // In case `actual` is an array, consider it a match
    // if ANY of it's items matches `expected`
    return actual.some(function(item) {
      return deepCompare(item, expected, comparator, anyPropertyKey, matchAgainstAnyProp);
    });
  }

  switch (actualType) {
    case 'object':
      var key;
      if (matchAgainstAnyProp) {
        for (key in actual) {
          // Under certain, rare, circumstances, key may not be a string and `charAt` will be undefined
          // See: https://github.com/angular/angular.js/issues/15644
          if (key.charAt && (key.charAt(0) !== '$') &&
              deepCompare(actual[key], expected, comparator, anyPropertyKey, true)) {
            return true;
          }
        }
        return dontMatchWholeObject ? false : deepCompare(actual, expected, comparator, anyPropertyKey, false);
      } else if (expectedType === 'object') {
        for (key in expected) {
          var expectedVal = expected[key];
          if (isFunction(expectedVal) || isUndefined(expectedVal)) {
            continue;
          }

          var matchAnyProperty = key === anyPropertyKey;
          var actualVal = matchAnyProperty ? actual : actual[key];
          if (!deepCompare(actualVal, expectedVal, comparator, anyPropertyKey, matchAnyProperty, matchAnyProperty)) {
            return false;
          }
        }
        return true;
      } else {
        return comparator(actual, expected);
      }
    case 'function':
      return false;
    default:
      return comparator(actual, expected);
  }
}

// Used for easily differentiating between `null` and actual `object`
function getTypeForFilter(val) {
  return (val === null) ? 'null' : typeof val;
}

var MAX_DIGITS = 22;
var DECIMAL_SEP = '.';
var ZERO_CHAR = '0';

/**
 * @ngdoc filter
 * @name currency
 * @kind function
 *
 * @description
 * Formats a number as a currency (ie $1,234.56). When no currency symbol is provided, default
 * symbol for current locale is used.
 *
 * @param {number} amount Input to filter.
 * @param {string=} symbol Currency symbol or identifier to be displayed.
 * @param {number=} fractionSize Number of decimal places to round the amount to, defaults to default max fraction size for current locale
 * @returns {string} Formatted number.
 *
 *
 * @example
   <example module="currencyExample" name="currency-filter">
     <file name="index.html">
       <script>
         angular.module('currencyExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.amount = 1234.56;
           }]);
       </script>
       <div ng-controller="ExampleController">
         <input type="number" ng-model="amount" aria-label="amount"> <br>
         default currency symbol ($): <span id="currency-default">{{amount | currency}}</span><br>
         custom currency identifier (USD$): <span id="currency-custom">{{amount | currency:"USD$"}}</span><br>
         no fractions (0): <span id="currency-no-fractions">{{amount | currency:"USD$":0}}</span>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should init with 1234.56', function() {
         expect(element(by.id('currency-default')).getText()).toBe('$1,234.56');
         expect(element(by.id('currency-custom')).getText()).toBe('USD$1,234.56');
         expect(element(by.id('currency-no-fractions')).getText()).toBe('USD$1,235');
       });
       it('should update', function() {
         if (browser.params.browser === 'safari') {
           // Safari does not understand the minus key. See
           // https://github.com/angular/protractor/issues/481
           return;
         }
         element(by.model('amount')).clear();
         element(by.model('amount')).sendKeys('-1234');
         expect(element(by.id('currency-default')).getText()).toBe('-$1,234.00');
         expect(element(by.id('currency-custom')).getText()).toBe('-USD$1,234.00');
         expect(element(by.id('currency-no-fractions')).getText()).toBe('-USD$1,234');
       });
     </file>
   </example>
 */
currencyFilter.$inject = ['$locale'];
function currencyFilter($locale) {
  var formats = $locale.NUMBER_FORMATS;
  return function(amount, currencySymbol, fractionSize) {
    if (isUndefined(currencySymbol)) {
      currencySymbol = formats.CURRENCY_SYM;
    }

    if (isUndefined(fractionSize)) {
      fractionSize = formats.PATTERNS[1].maxFrac;
    }

    // If the currency symbol is empty, trim whitespace around the symbol
    var currencySymbolRe = !currencySymbol ? /\s*\u00A4\s*/g : /\u00A4/g;

    // if null or undefined pass it through
    return (amount == null)
        ? amount
        : formatNumber(amount, formats.PATTERNS[1], formats.GROUP_SEP, formats.DECIMAL_SEP, fractionSize).
            replace(currencySymbolRe, currencySymbol);
  };
}

/**
 * @ngdoc filter
 * @name number
 * @kind function
 *
 * @description
 * Formats a number as text.
 *
 * If the input is null or undefined, it will just be returned.
 * If the input is infinite (Infinity or -Infinity), the Infinity symbol '∞' or '-∞' is returned, respectively.
 * If the input is not a number an empty string is returned.
 *
 *
 * @param {number|string} number Number to format.
 * @param {(number|string)=} fractionSize Number of decimal places to round the number to.
 * If this is not provided then the fraction size is computed from the current locale's number
 * formatting pattern. In the case of the default locale, it will be 3.
 * @returns {string} Number rounded to `fractionSize` appropriately formatted based on the current
 *                   locale (e.g., in the en_US locale it will have "." as the decimal separator and
 *                   include "," group separators after each third digit).
 *
 * @example
   <example module="numberFilterExample" name="number-filter">
     <file name="index.html">
       <script>
         angular.module('numberFilterExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.val = 1234.56789;
           }]);
       </script>
       <div ng-controller="ExampleController">
         <label>Enter number: <input ng-model='val'></label><br>
         Default formatting: <span id='number-default'>{{val | number}}</span><br>
         No fractions: <span>{{val | number:0}}</span><br>
         Negative number: <span>{{-val | number:4}}</span>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should format numbers', function() {
         expect(element(by.id('number-default')).getText()).toBe('1,234.568');
         expect(element(by.binding('val | number:0')).getText()).toBe('1,235');
         expect(element(by.binding('-val | number:4')).getText()).toBe('-1,234.5679');
       });

       it('should update', function() {
         element(by.model('val')).clear();
         element(by.model('val')).sendKeys('3374.333');
         expect(element(by.id('number-default')).getText()).toBe('3,374.333');
         expect(element(by.binding('val | number:0')).getText()).toBe('3,374');
         expect(element(by.binding('-val | number:4')).getText()).toBe('-3,374.3330');
      });
     </file>
   </example>
 */
numberFilter.$inject = ['$locale'];
function numberFilter($locale) {
  var formats = $locale.NUMBER_FORMATS;
  return function(number, fractionSize) {

    // if null or undefined pass it through
    return (number == null)
        ? number
        : formatNumber(number, formats.PATTERNS[0], formats.GROUP_SEP, formats.DECIMAL_SEP,
                       fractionSize);
  };
}

/**
 * Parse a number (as a string) into three components that can be used
 * for formatting the number.
 *
 * (Significant bits of this parse algorithm came from https://github.com/MikeMcl/big.js/)
 *
 * @param  {string} numStr The number to parse
 * @return {object} An object describing this number, containing the following keys:
 *  - d : an array of digits containing leading zeros as necessary
 *  - i : the number of the digits in `d` that are to the left of the decimal point
 *  - e : the exponent for numbers that would need more than `MAX_DIGITS` digits in `d`
 *
 */
function parse(numStr) {
  var exponent = 0, digits, numberOfIntegerDigits;
  var i, j, zeros;

  // Decimal point?
  if ((numberOfIntegerDigits = numStr.indexOf(DECIMAL_SEP)) > -1) {
    numStr = numStr.replace(DECIMAL_SEP, '');
  }

  // Exponential form?
  if ((i = numStr.search(/e/i)) > 0) {
    // Work out the exponent.
    if (numberOfIntegerDigits < 0) numberOfIntegerDigits = i;
    numberOfIntegerDigits += +numStr.slice(i + 1);
    numStr = numStr.substring(0, i);
  } else if (numberOfIntegerDigits < 0) {
    // There was no decimal point or exponent so it is an integer.
    numberOfIntegerDigits = numStr.length;
  }

  // Count the number of leading zeros.
  for (i = 0; numStr.charAt(i) === ZERO_CHAR; i++) { /* empty */ }

  if (i === (zeros = numStr.length)) {
    // The digits are all zero.
    digits = [0];
    numberOfIntegerDigits = 1;
  } else {
    // Count the number of trailing zeros
    zeros--;
    while (numStr.charAt(zeros) === ZERO_CHAR) zeros--;

    // Trailing zeros are insignificant so ignore them
    numberOfIntegerDigits -= i;
    digits = [];
    // Convert string to array of digits without leading/trailing zeros.
    for (j = 0; i <= zeros; i++, j++) {
      digits[j] = +numStr.charAt(i);
    }
  }

  // If the number overflows the maximum allowed digits then use an exponent.
  if (numberOfIntegerDigits > MAX_DIGITS) {
    digits = digits.splice(0, MAX_DIGITS - 1);
    exponent = numberOfIntegerDigits - 1;
    numberOfIntegerDigits = 1;
  }

  return { d: digits, e: exponent, i: numberOfIntegerDigits };
}

/**
 * Round the parsed number to the specified number of decimal places
 * This function changed the parsedNumber in-place
 */
function roundNumber(parsedNumber, fractionSize, minFrac, maxFrac) {
    var digits = parsedNumber.d;
    var fractionLen = digits.length - parsedNumber.i;

    // determine fractionSize if it is not specified; `+fractionSize` converts it to a number
    fractionSize = (isUndefined(fractionSize)) ? Math.min(Math.max(minFrac, fractionLen), maxFrac) : +fractionSize;

    // The index of the digit to where rounding is to occur
    var roundAt = fractionSize + parsedNumber.i;
    var digit = digits[roundAt];

    if (roundAt > 0) {
      // Drop fractional digits beyond `roundAt`
      digits.splice(Math.max(parsedNumber.i, roundAt));

      // Set non-fractional digits beyond `roundAt` to 0
      for (var j = roundAt; j < digits.length; j++) {
        digits[j] = 0;
      }
    } else {
      // We rounded to zero so reset the parsedNumber
      fractionLen = Math.max(0, fractionLen);
      parsedNumber.i = 1;
      digits.length = Math.max(1, roundAt = fractionSize + 1);
      digits[0] = 0;
      for (var i = 1; i < roundAt; i++) digits[i] = 0;
    }

    if (digit >= 5) {
      if (roundAt - 1 < 0) {
        for (var k = 0; k > roundAt; k--) {
          digits.unshift(0);
          parsedNumber.i++;
        }
        digits.unshift(1);
        parsedNumber.i++;
      } else {
        digits[roundAt - 1]++;
      }
    }

    // Pad out with zeros to get the required fraction length
    for (; fractionLen < Math.max(0, fractionSize); fractionLen++) digits.push(0);


    // Do any carrying, e.g. a digit was rounded up to 10
    var carry = digits.reduceRight(function(carry, d, i, digits) {
      d = d + carry;
      digits[i] = d % 10;
      return Math.floor(d / 10);
    }, 0);
    if (carry) {
      digits.unshift(carry);
      parsedNumber.i++;
    }
}

/**
 * Format a number into a string
 * @param  {number} number       The number to format
 * @param  {{
 *           minFrac, // the minimum number of digits required in the fraction part of the number
 *           maxFrac, // the maximum number of digits required in the fraction part of the number
 *           gSize,   // number of digits in each group of separated digits
 *           lgSize,  // number of digits in the last group of digits before the decimal separator
 *           negPre,  // the string to go in front of a negative number (e.g. `-` or `(`))
 *           posPre,  // the string to go in front of a positive number
 *           negSuf,  // the string to go after a negative number (e.g. `)`)
 *           posSuf   // the string to go after a positive number
 *         }} pattern
 * @param  {string} groupSep     The string to separate groups of number (e.g. `,`)
 * @param  {string} decimalSep   The string to act as the decimal separator (e.g. `.`)
 * @param  {[type]} fractionSize The size of the fractional part of the number
 * @return {string}              The number formatted as a string
 */
function formatNumber(number, pattern, groupSep, decimalSep, fractionSize) {

  if (!(isString(number) || isNumber(number)) || isNaN(number)) return '';

  var isInfinity = !isFinite(number);
  var isZero = false;
  var numStr = Math.abs(number) + '',
      formattedText = '',
      parsedNumber;

  if (isInfinity) {
    formattedText = '\u221e';
  } else {
    parsedNumber = parse(numStr);

    roundNumber(parsedNumber, fractionSize, pattern.minFrac, pattern.maxFrac);

    var digits = parsedNumber.d;
    var integerLen = parsedNumber.i;
    var exponent = parsedNumber.e;
    var decimals = [];
    isZero = digits.reduce(function(isZero, d) { return isZero && !d; }, true);

    // pad zeros for small numbers
    while (integerLen < 0) {
      digits.unshift(0);
      integerLen++;
    }

    // extract decimals digits
    if (integerLen > 0) {
      decimals = digits.splice(integerLen, digits.length);
    } else {
      decimals = digits;
      digits = [0];
    }

    // format the integer digits with grouping separators
    var groups = [];
    if (digits.length >= pattern.lgSize) {
      groups.unshift(digits.splice(-pattern.lgSize, digits.length).join(''));
    }
    while (digits.length > pattern.gSize) {
      groups.unshift(digits.splice(-pattern.gSize, digits.length).join(''));
    }
    if (digits.length) {
      groups.unshift(digits.join(''));
    }
    formattedText = groups.join(groupSep);

    // append the decimal digits
    if (decimals.length) {
      formattedText += decimalSep + decimals.join('');
    }

    if (exponent) {
      formattedText += 'e+' + exponent;
    }
  }
  if (number < 0 && !isZero) {
    return pattern.negPre + formattedText + pattern.negSuf;
  } else {
    return pattern.posPre + formattedText + pattern.posSuf;
  }
}

function padNumber(num, digits, trim, negWrap) {
  var neg = '';
  if (num < 0 || (negWrap && num <= 0)) {
    if (negWrap) {
      num = -num + 1;
    } else {
      num = -num;
      neg = '-';
    }
  }
  num = '' + num;
  while (num.length < digits) num = ZERO_CHAR + num;
  if (trim) {
    num = num.substr(num.length - digits);
  }
  return neg + num;
}


function dateGetter(name, size, offset, trim, negWrap) {
  offset = offset || 0;
  return function(date) {
    var value = date['get' + name]();
    if (offset > 0 || value > -offset) {
      value += offset;
    }
    if (value === 0 && offset === -12) value = 12;
    return padNumber(value, size, trim, negWrap);
  };
}

function dateStrGetter(name, shortForm, standAlone) {
  return function(date, formats) {
    var value = date['get' + name]();
    var propPrefix = (standAlone ? 'STANDALONE' : '') + (shortForm ? 'SHORT' : '');
    var get = uppercase(propPrefix + name);

    return formats[get][value];
  };
}

function timeZoneGetter(date, formats, offset) {
  var zone = -1 * offset;
  var paddedZone = (zone >= 0) ? '+' : '';

  paddedZone += padNumber(Math[zone > 0 ? 'floor' : 'ceil'](zone / 60), 2) +
                padNumber(Math.abs(zone % 60), 2);

  return paddedZone;
}

function getFirstThursdayOfYear(year) {
    // 0 = index of January
    var dayOfWeekOnFirst = (new Date(year, 0, 1)).getDay();
    // 4 = index of Thursday (+1 to account for 1st = 5)
    // 11 = index of *next* Thursday (+1 account for 1st = 12)
    return new Date(year, 0, ((dayOfWeekOnFirst <= 4) ? 5 : 12) - dayOfWeekOnFirst);
}

function getThursdayThisWeek(datetime) {
    return new Date(datetime.getFullYear(), datetime.getMonth(),
      // 4 = index of Thursday
      datetime.getDate() + (4 - datetime.getDay()));
}

function weekGetter(size) {
   return function(date) {
      var firstThurs = getFirstThursdayOfYear(date.getFullYear()),
         thisThurs = getThursdayThisWeek(date);

      var diff = +thisThurs - +firstThurs,
         result = 1 + Math.round(diff / 6.048e8); // 6.048e8 ms per week

      return padNumber(result, size);
   };
}

function ampmGetter(date, formats) {
  return date.getHours() < 12 ? formats.AMPMS[0] : formats.AMPMS[1];
}

function eraGetter(date, formats) {
  return date.getFullYear() <= 0 ? formats.ERAS[0] : formats.ERAS[1];
}

function longEraGetter(date, formats) {
  return date.getFullYear() <= 0 ? formats.ERANAMES[0] : formats.ERANAMES[1];
}

var DATE_FORMATS = {
  yyyy: dateGetter('FullYear', 4, 0, false, true),
    yy: dateGetter('FullYear', 2, 0, true, true),
     y: dateGetter('FullYear', 1, 0, false, true),
  MMMM: dateStrGetter('Month'),
   MMM: dateStrGetter('Month', true),
    MM: dateGetter('Month', 2, 1),
     M: dateGetter('Month', 1, 1),
  LLLL: dateStrGetter('Month', false, true),
    dd: dateGetter('Date', 2),
     d: dateGetter('Date', 1),
    HH: dateGetter('Hours', 2),
     H: dateGetter('Hours', 1),
    hh: dateGetter('Hours', 2, -12),
     h: dateGetter('Hours', 1, -12),
    mm: dateGetter('Minutes', 2),
     m: dateGetter('Minutes', 1),
    ss: dateGetter('Seconds', 2),
     s: dateGetter('Seconds', 1),
     // while ISO 8601 requires fractions to be prefixed with `.` or `,`
     // we can be just safely rely on using `sss` since we currently don't support single or two digit fractions
   sss: dateGetter('Milliseconds', 3),
  EEEE: dateStrGetter('Day'),
   EEE: dateStrGetter('Day', true),
     a: ampmGetter,
     Z: timeZoneGetter,
    ww: weekGetter(2),
     w: weekGetter(1),
     G: eraGetter,
     GG: eraGetter,
     GGG: eraGetter,
     GGGG: longEraGetter
};

var DATE_FORMATS_SPLIT = /((?:[^yMLdHhmsaZEwG']+)|(?:'(?:[^']|'')*')|(?:E+|y+|M+|L+|d+|H+|h+|m+|s+|a|Z|G+|w+))([\s\S]*)/,
    NUMBER_STRING = /^-?\d+$/;

/**
 * @ngdoc filter
 * @name date
 * @kind function
 *
 * @description
 *   Formats `date` to a string based on the requested `format`.
 *
 *   `format` string can be composed of the following elements:
 *
 *   * `'yyyy'`: 4 digit representation of year (e.g. AD 1 => 0001, AD 2010 => 2010)
 *   * `'yy'`: 2 digit representation of year, padded (00-99). (e.g. AD 2001 => 01, AD 2010 => 10)
 *   * `'y'`: 1 digit representation of year, e.g. (AD 1 => 1, AD 199 => 199)
 *   * `'MMMM'`: Month in year (January-December)
 *   * `'MMM'`: Month in year (Jan-Dec)
 *   * `'MM'`: Month in year, padded (01-12)
 *   * `'M'`: Month in year (1-12)
 *   * `'LLLL'`: Stand-alone month in year (January-December)
 *   * `'dd'`: Day in month, padded (01-31)
 *   * `'d'`: Day in month (1-31)
 *   * `'EEEE'`: Day in Week,(Sunday-Saturday)
 *   * `'EEE'`: Day in Week, (Sun-Sat)
 *   * `'HH'`: Hour in day, padded (00-23)
 *   * `'H'`: Hour in day (0-23)
 *   * `'hh'`: Hour in AM/PM, padded (01-12)
 *   * `'h'`: Hour in AM/PM, (1-12)
 *   * `'mm'`: Minute in hour, padded (00-59)
 *   * `'m'`: Minute in hour (0-59)
 *   * `'ss'`: Second in minute, padded (00-59)
 *   * `'s'`: Second in minute (0-59)
 *   * `'sss'`: Millisecond in second, padded (000-999)
 *   * `'a'`: AM/PM marker
 *   * `'Z'`: 4 digit (+sign) representation of the timezone offset (-1200-+1200)
 *   * `'ww'`: Week of year, padded (00-53). Week 01 is the week with the first Thursday of the year
 *   * `'w'`: Week of year (0-53). Week 1 is the week with the first Thursday of the year
 *   * `'G'`, `'GG'`, `'GGG'`: The abbreviated form of the era string (e.g. 'AD')
 *   * `'GGGG'`: The long form of the era string (e.g. 'Anno Domini')
 *
 *   `format` string can also be one of the following predefined
 *   {@link guide/i18n localizable formats}:
 *
 *   * `'medium'`: equivalent to `'MMM d, y h:mm:ss a'` for en_US locale
 *     (e.g. Sep 3, 2010 12:05:08 PM)
 *   * `'short'`: equivalent to `'M/d/yy h:mm a'` for en_US  locale (e.g. 9/3/10 12:05 PM)
 *   * `'fullDate'`: equivalent to `'EEEE, MMMM d, y'` for en_US  locale
 *     (e.g. Friday, September 3, 2010)
 *   * `'longDate'`: equivalent to `'MMMM d, y'` for en_US  locale (e.g. September 3, 2010)
 *   * `'mediumDate'`: equivalent to `'MMM d, y'` for en_US  locale (e.g. Sep 3, 2010)
 *   * `'shortDate'`: equivalent to `'M/d/yy'` for en_US locale (e.g. 9/3/10)
 *   * `'mediumTime'`: equivalent to `'h:mm:ss a'` for en_US locale (e.g. 12:05:08 PM)
 *   * `'shortTime'`: equivalent to `'h:mm a'` for en_US locale (e.g. 12:05 PM)
 *
 *   `format` string can contain literal values. These need to be escaped by surrounding with single quotes (e.g.
 *   `"h 'in the morning'"`). In order to output a single quote, escape it - i.e., two single quotes in a sequence
 *   (e.g. `"h 'o''clock'"`).
 *
 *   Any other characters in the `format` string will be output as-is.
 *
 * @param {(Date|number|string)} date Date to format either as Date object, milliseconds (string or
 *    number) or various ISO 8601 datetime string formats (e.g. yyyy-MM-ddTHH:mm:ss.sssZ and its
 *    shorter versions like yyyy-MM-ddTHH:mmZ, yyyy-MM-dd or yyyyMMddTHHmmssZ). If no timezone is
 *    specified in the string input, the time is considered to be in the local timezone.
 * @param {string=} format Formatting rules (see Description). If not specified,
 *    `mediumDate` is used.
 * @param {string=} timezone Timezone to be used for formatting. It understands UTC/GMT and the
 *    continental US time zone abbreviations, but for general use, use a time zone offset, for
 *    example, `'+0430'` (4 hours, 30 minutes east of the Greenwich meridian)
 *    If not specified, the timezone of the browser will be used.
 * @returns {string} Formatted string or the input if input is not recognized as date/millis.
 *
 * @example
   <example name="filter-date">
     <file name="index.html">
       <span ng-non-bindable>{{1288323623006 | date:'medium'}}</span>:
           <span>{{1288323623006 | date:'medium'}}</span><br>
       <span ng-non-bindable>{{1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'}}</span>:
          <span>{{1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'}}</span><br>
       <span ng-non-bindable>{{1288323623006 | date:'MM/dd/yyyy @ h:mma'}}</span>:
          <span>{{'1288323623006' | date:'MM/dd/yyyy @ h:mma'}}</span><br>
       <span ng-non-bindable>{{1288323623006 | date:"MM/dd/yyyy 'at' h:mma"}}</span>:
          <span>{{'1288323623006' | date:"MM/dd/yyyy 'at' h:mma"}}</span><br>
     </file>
     <file name="protractor.js" type="protractor">
       it('should format date', function() {
         expect(element(by.binding("1288323623006 | date:'medium'")).getText()).
            toMatch(/Oct 2\d, 2010 \d{1,2}:\d{2}:\d{2} (AM|PM)/);
         expect(element(by.binding("1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'")).getText()).
            toMatch(/2010-10-2\d \d{2}:\d{2}:\d{2} (-|\+)?\d{4}/);
         expect(element(by.binding("'1288323623006' | date:'MM/dd/yyyy @ h:mma'")).getText()).
            toMatch(/10\/2\d\/2010 @ \d{1,2}:\d{2}(AM|PM)/);
         expect(element(by.binding("'1288323623006' | date:\"MM/dd/yyyy 'at' h:mma\"")).getText()).
            toMatch(/10\/2\d\/2010 at \d{1,2}:\d{2}(AM|PM)/);
       });
     </file>
   </example>
 */
dateFilter.$inject = ['$locale'];
function dateFilter($locale) {


  var R_ISO8601_STR = /^(\d{4})-?(\d\d)-?(\d\d)(?:T(\d\d)(?::?(\d\d)(?::?(\d\d)(?:\.(\d+))?)?)?(Z|([+-])(\d\d):?(\d\d))?)?$/;
                     // 1        2       3         4          5          6          7          8  9     10      11
  function jsonStringToDate(string) {
    var match;
    if ((match = string.match(R_ISO8601_STR))) {
      var date = new Date(0),
          tzHour = 0,
          tzMin  = 0,
          dateSetter = match[8] ? date.setUTCFullYear : date.setFullYear,
          timeSetter = match[8] ? date.setUTCHours : date.setHours;

      if (match[9]) {
        tzHour = toInt(match[9] + match[10]);
        tzMin = toInt(match[9] + match[11]);
      }
      dateSetter.call(date, toInt(match[1]), toInt(match[2]) - 1, toInt(match[3]));
      var h = toInt(match[4] || 0) - tzHour;
      var m = toInt(match[5] || 0) - tzMin;
      var s = toInt(match[6] || 0);
      var ms = Math.round(parseFloat('0.' + (match[7] || 0)) * 1000);
      timeSetter.call(date, h, m, s, ms);
      return date;
    }
    return string;
  }


  return function(date, format, timezone) {
    var text = '',
        parts = [],
        fn, match;

    format = format || 'mediumDate';
    format = $locale.DATETIME_FORMATS[format] || format;
    if (isString(date)) {
      date = NUMBER_STRING.test(date) ? toInt(date) : jsonStringToDate(date);
    }

    if (isNumber(date)) {
      date = new Date(date);
    }

    if (!isDate(date) || !isFinite(date.getTime())) {
      return date;
    }

    while (format) {
      match = DATE_FORMATS_SPLIT.exec(format);
      if (match) {
        parts = concat(parts, match, 1);
        format = parts.pop();
      } else {
        parts.push(format);
        format = null;
      }
    }

    var dateTimezoneOffset = date.getTimezoneOffset();
    if (timezone) {
      dateTimezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
      date = convertTimezoneToLocal(date, timezone, true);
    }
    forEach(parts, function(value) {
      fn = DATE_FORMATS[value];
      text += fn ? fn(date, $locale.DATETIME_FORMATS, dateTimezoneOffset)
                 : value === '\'\'' ? '\'' : value.replace(/(^'|'$)/g, '').replace(/''/g, '\'');
    });

    return text;
  };
}


/**
 * @ngdoc filter
 * @name json
 * @kind function
 *
 * @description
 *   Allows you to convert a JavaScript object into JSON string.
 *
 *   This filter is mostly useful for debugging. When using the double curly {{value}} notation
 *   the binding is automatically converted to JSON.
 *
 * @param {*} object Any JavaScript object (including arrays and primitive types) to filter.
 * @param {number=} spacing The number of spaces to use per indentation, defaults to 2.
 * @returns {string} JSON string.
 *
 *
 * @example
   <example name="filter-json">
     <file name="index.html">
       <pre id="default-spacing">{{ {'name':'value'} | json }}</pre>
       <pre id="custom-spacing">{{ {'name':'value'} | json:4 }}</pre>
     </file>
     <file name="protractor.js" type="protractor">
       it('should jsonify filtered objects', function() {
         expect(element(by.id('default-spacing')).getText()).toMatch(/\{\n {2}"name": ?"value"\n}/);
         expect(element(by.id('custom-spacing')).getText()).toMatch(/\{\n {4}"name": ?"value"\n}/);
       });
     </file>
   </example>
 *
 */
function jsonFilter() {
  return function(object, spacing) {
    if (isUndefined(spacing)) {
        spacing = 2;
    }
    return toJson(object, spacing);
  };
}


/**
 * @ngdoc filter
 * @name lowercase
 * @kind function
 * @description
 * Converts string to lowercase.
 *
 * See the {@link ng.uppercase uppercase filter documentation} for a functionally identical example.
 *
 * @see angular.lowercase
 */
var lowercaseFilter = valueFn(lowercase);


/**
 * @ngdoc filter
 * @name uppercase
 * @kind function
 * @description
 * Converts string to uppercase.
 * @example
   <example module="uppercaseFilterExample" name="filter-uppercase">
     <file name="index.html">
       <script>
         angular.module('uppercaseFilterExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.title = 'This is a title';
           }]);
       </script>
       <div ng-controller="ExampleController">
         <!-- This title should be formatted normally -->
         <h1>{{title}}</h1>
         <!-- This title should be capitalized -->
         <h1>{{title | uppercase}}</h1>
       </div>
     </file>
   </example>
 */
var uppercaseFilter = valueFn(uppercase);

/**
 * @ngdoc filter
 * @name limitTo
 * @kind function
 *
 * @description
 * Creates a new array or string containing only a specified number of elements. The elements are
 * taken from either the beginning or the end of the source array, string or number, as specified by
 * the value and sign (positive or negative) of `limit`. Other array-like objects are also supported
 * (e.g. array subclasses, NodeLists, jqLite/jQuery collections etc). If a number is used as input,
 * it is converted to a string.
 *
 * @param {Array|ArrayLike|string|number} input - Array/array-like, string or number to be limited.
 * @param {string|number} limit - The length of the returned array or string. If the `limit` number
 *     is positive, `limit` number of items from the beginning of the source array/string are copied.
 *     If the number is negative, `limit` number  of items from the end of the source array/string
 *     are copied. The `limit` will be trimmed if it exceeds `array.length`. If `limit` is undefined,
 *     the input will be returned unchanged.
 * @param {(string|number)=} begin - Index at which to begin limitation. As a negative index,
 *     `begin` indicates an offset from the end of `input`. Defaults to `0`.
 * @returns {Array|string} A new sub-array or substring of length `limit` or less if the input had
 *     less than `limit` elements.
 *
 * @example
   <example module="limitToExample" name="limit-to-filter">
     <file name="index.html">
       <script>
         angular.module('limitToExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.numbers = [1,2,3,4,5,6,7,8,9];
             $scope.letters = "abcdefghi";
             $scope.longNumber = 2345432342;
             $scope.numLimit = 3;
             $scope.letterLimit = 3;
             $scope.longNumberLimit = 3;
           }]);
       </script>
       <div ng-controller="ExampleController">
         <label>
            Limit {{numbers}} to:
            <input type="number" step="1" ng-model="numLimit">
         </label>
         <p>Output numbers: {{ numbers | limitTo:numLimit }}</p>
         <label>
            Limit {{letters}} to:
            <input type="number" step="1" ng-model="letterLimit">
         </label>
         <p>Output letters: {{ letters | limitTo:letterLimit }}</p>
         <label>
            Limit {{longNumber}} to:
            <input type="number" step="1" ng-model="longNumberLimit">
         </label>
         <p>Output long number: {{ longNumber | limitTo:longNumberLimit }}</p>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       var numLimitInput = element(by.model('numLimit'));
       var letterLimitInput = element(by.model('letterLimit'));
       var longNumberLimitInput = element(by.model('longNumberLimit'));
       var limitedNumbers = element(by.binding('numbers | limitTo:numLimit'));
       var limitedLetters = element(by.binding('letters | limitTo:letterLimit'));
       var limitedLongNumber = element(by.binding('longNumber | limitTo:longNumberLimit'));

       it('should limit the number array to first three items', function() {
         expect(numLimitInput.getAttribute('value')).toBe('3');
         expect(letterLimitInput.getAttribute('value')).toBe('3');
         expect(longNumberLimitInput.getAttribute('value')).toBe('3');
         expect(limitedNumbers.getText()).toEqual('Output numbers: [1,2,3]');
         expect(limitedLetters.getText()).toEqual('Output letters: abc');
         expect(limitedLongNumber.getText()).toEqual('Output long number: 234');
       });

       // There is a bug in safari and protractor that doesn't like the minus key
       // it('should update the output when -3 is entered', function() {
       //   numLimitInput.clear();
       //   numLimitInput.sendKeys('-3');
       //   letterLimitInput.clear();
       //   letterLimitInput.sendKeys('-3');
       //   longNumberLimitInput.clear();
       //   longNumberLimitInput.sendKeys('-3');
       //   expect(limitedNumbers.getText()).toEqual('Output numbers: [7,8,9]');
       //   expect(limitedLetters.getText()).toEqual('Output letters: ghi');
       //   expect(limitedLongNumber.getText()).toEqual('Output long number: 342');
       // });

       it('should not exceed the maximum size of input array', function() {
         numLimitInput.clear();
         numLimitInput.sendKeys('100');
         letterLimitInput.clear();
         letterLimitInput.sendKeys('100');
         longNumberLimitInput.clear();
         longNumberLimitInput.sendKeys('100');
         expect(limitedNumbers.getText()).toEqual('Output numbers: [1,2,3,4,5,6,7,8,9]');
         expect(limitedLetters.getText()).toEqual('Output letters: abcdefghi');
         expect(limitedLongNumber.getText()).toEqual('Output long number: 2345432342');
       });
     </file>
   </example>
*/
function limitToFilter() {
  return function(input, limit, begin) {
    if (Math.abs(Number(limit)) === Infinity) {
      limit = Number(limit);
    } else {
      limit = toInt(limit);
    }
    if (isNumberNaN(limit)) return input;

    if (isNumber(input)) input = input.toString();
    if (!isArrayLike(input)) return input;

    begin = (!begin || isNaN(begin)) ? 0 : toInt(begin);
    begin = (begin < 0) ? Math.max(0, input.length + begin) : begin;

    if (limit >= 0) {
      return sliceFn(input, begin, begin + limit);
    } else {
      if (begin === 0) {
        return sliceFn(input, limit, input.length);
      } else {
        return sliceFn(input, Math.max(0, begin + limit), begin);
      }
    }
  };
}

function sliceFn(input, begin, end) {
  if (isString(input)) return input.slice(begin, end);

  return slice.call(input, begin, end);
}

/**
 * @ngdoc filter
 * @name orderBy
 * @kind function
 *
 * @description
 * Returns an array containing the items from the specified `collection`, ordered by a `comparator`
 * function based on the values computed using the `expression` predicate.
 *
 * For example, `[{id: 'foo'}, {id: 'bar'}] | orderBy:'id'` would result in
 * `[{id: 'bar'}, {id: 'foo'}]`.
 *
 * The `collection` can be an Array or array-like object (e.g. NodeList, jQuery object, TypedArray,
 * String, etc).
 *
 * The `expression` can be a single predicate, or a list of predicates each serving as a tie-breaker
 * for the preceding one. The `expression` is evaluated against each item and the output is used
 * for comparing with other items.
 *
 * You can change the sorting order by setting `reverse` to `true`. By default, items are sorted in
 * ascending order.
 *
 * The comparison is done using the `comparator` function. If none is specified, a default, built-in
 * comparator is used (see below for details - in a nutshell, it compares numbers numerically and
 * strings alphabetically).
 *
 * ### Under the hood
 *
 * Ordering the specified `collection` happens in two phases:
 *
 * 1. All items are passed through the predicate (or predicates), and the returned values are saved
 *    along with their type (`string`, `number` etc). For example, an item `{label: 'foo'}`, passed
 *    through a predicate that extracts the value of the `label` property, would be transformed to:
 *    ```
 *    {
 *      value: 'foo',
 *      type: 'string',
 *      index: ...
 *    }
 *    ```
 *    **Note:** `null` values use `'null'` as their type.
 * 2. The comparator function is used to sort the items, based on the derived values, types and
 *    indices.
 *
 * If you use a custom comparator, it will be called with pairs of objects of the form
 * `{value: ..., type: '...', index: ...}` and is expected to return `0` if the objects are equal
 * (as far as the comparator is concerned), `-1` if the 1st one should be ranked higher than the
 * second, or `1` otherwise.
 *
 * In order to ensure that the sorting will be deterministic across platforms, if none of the
 * specified predicates can distinguish between two items, `orderBy` will automatically introduce a
 * dummy predicate that returns the item's index as `value`.
 * (If you are using a custom comparator, make sure it can handle this predicate as well.)
 *
 * If a custom comparator still can't distinguish between two items, then they will be sorted based
 * on their index using the built-in comparator.
 *
 * Finally, in an attempt to simplify things, if a predicate returns an object as the extracted
 * value for an item, `orderBy` will try to convert that object to a primitive value, before passing
 * it to the comparator. The following rules govern the conversion:
 *
 * 1. If the object has a `valueOf()` method that returns a primitive, its return value will be
 *    used instead.<br />
 *    (If the object has a `valueOf()` method that returns another object, then the returned object
 *    will be used in subsequent steps.)
 * 2. If the object has a custom `toString()` method (i.e. not the one inherited from `Object`) that
 *    returns a primitive, its return value will be used instead.<br />
 *    (If the object has a `toString()` method that returns another object, then the returned object
 *    will be used in subsequent steps.)
 * 3. No conversion; the object itself is used.
 *
 * ### The default comparator
 *
 * The default, built-in comparator should be sufficient for most usecases. In short, it compares
 * numbers numerically, strings alphabetically (and case-insensitively), for objects falls back to
 * using their index in the original collection, sorts values of different types by type and puts
 * `undefined` and `null` values at the end of the sorted list.
 *
 * More specifically, it follows these steps to determine the relative order of items:
 *
 * 1. If the compared values are of different types:
 *    - If one of the values is undefined, consider it "greater than" the other.
 *    - Else if one of the values is null, consider it "greater than" the other.
 *    - Else compare the types themselves alphabetically.
 * 2. If both values are of type `string`, compare them alphabetically in a case- and
 *    locale-insensitive way.
 * 3. If both values are objects, compare their indices instead.
 * 4. Otherwise, return:
 *    -  `0`, if the values are equal (by strict equality comparison, i.e. using `===`).
 *    - `-1`, if the 1st value is "less than" the 2nd value (compared using the `<` operator).
 *    -  `1`, otherwise.
 *
 * **Note:** If you notice numbers not being sorted as expected, make sure they are actually being
 *           saved as numbers and not strings.
 * **Note:** For the purpose of sorting, `null` and `undefined` are considered "greater than"
 *           any other value (with undefined "greater than" null). This effectively means that `null`
 *           and `undefined` values end up at the end of a list sorted in ascending order.
 * **Note:** `null` values use `'null'` as their type to be able to distinguish them from objects.
 *
 * @param {Array|ArrayLike} collection - The collection (array or array-like object) to sort.
 * @param {(Function|string|Array.<Function|string>)=} expression - A predicate (or list of
 *    predicates) to be used by the comparator to determine the order of elements.
 *
 *    Can be one of:
 *
 *    - `Function`: A getter function. This function will be called with each item as argument and
 *      the return value will be used for sorting.
 *    - `string`: An AngularJS expression. This expression will be evaluated against each item and the
 *      result will be used for sorting. For example, use `'label'` to sort by a property called
 *      `label` or `'label.substring(0, 3)'` to sort by the first 3 characters of the `label`
 *      property.<br />
 *      (The result of a constant expression is interpreted as a property name to be used for
 *      comparison. For example, use `'"special name"'` (note the extra pair of quotes) to sort by a
 *      property called `special name`.)<br />
 *      An expression can be optionally prefixed with `+` or `-` to control the sorting direction,
 *      ascending or descending. For example, `'+label'` or `'-label'`. If no property is provided,
 *      (e.g. `'+'` or `'-'`), the collection element itself is used in comparisons.
 *    - `Array`: An array of function and/or string predicates. If a predicate cannot determine the
 *      relative order of two items, the next predicate is used as a tie-breaker.
 *
 * **Note:** If the predicate is missing or empty then it defaults to `'+'`.
 *
 * @param {boolean=} reverse - If `true`, reverse the sorting order.
 * @param {(Function)=} comparator - The comparator function used to determine the relative order of
 *    value pairs. If omitted, the built-in comparator will be used.
 *
 * @returns {Array} - The sorted array.
 *
 *
 * @example
 * ### Ordering a table with `ngRepeat`
 *
 * The example below demonstrates a simple {@link ngRepeat ngRepeat}, where the data is sorted by
 * age in descending order (expression is set to `'-age'`). The `comparator` is not set, which means
 * it defaults to the built-in comparator.
 *
   <example name="orderBy-static" module="orderByExample1">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <table class="friends">
           <tr>
             <th>Name</th>
             <th>Phone Number</th>
             <th>Age</th>
           </tr>
           <tr ng-repeat="friend in friends | orderBy:'-age'">
             <td>{{friend.name}}</td>
             <td>{{friend.phone}}</td>
             <td>{{friend.age}}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample1', [])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.friends = [
             {name: 'John',   phone: '555-1212',  age: 10},
             {name: 'Mary',   phone: '555-9876',  age: 19},
             {name: 'Mike',   phone: '555-4321',  age: 21},
             {name: 'Adam',   phone: '555-5678',  age: 35},
             {name: 'Julie',  phone: '555-8765',  age: 29}
           ];
         }]);
     </file>
     <file name="style.css">
       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var names = element.all(by.repeater('friends').column('friend.name'));

       it('should sort friends by age in reverse order', function() {
         expect(names.get(0).getText()).toBe('Adam');
         expect(names.get(1).getText()).toBe('Julie');
         expect(names.get(2).getText()).toBe('Mike');
         expect(names.get(3).getText()).toBe('Mary');
         expect(names.get(4).getText()).toBe('John');
       });
     </file>
   </example>
 * <hr />
 *
 * @example
 * ### Changing parameters dynamically
 *
 * All parameters can be changed dynamically. The next example shows how you can make the columns of
 * a table sortable, by binding the `expression` and `reverse` parameters to scope properties.
 *
   <example name="orderBy-dynamic" module="orderByExample2">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <pre>Sort by = {{propertyName}}; reverse = {{reverse}}</pre>
         <hr/>
         <button ng-click="propertyName = null; reverse = false">Set to unsorted</button>
         <hr/>
         <table class="friends">
           <tr>
             <th>
               <button ng-click="sortBy('name')">Name</button>
               <span class="sortorder" ng-show="propertyName === 'name'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('phone')">Phone Number</button>
               <span class="sortorder" ng-show="propertyName === 'phone'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('age')">Age</button>
               <span class="sortorder" ng-show="propertyName === 'age'" ng-class="{reverse: reverse}"></span>
             </th>
           </tr>
           <tr ng-repeat="friend in friends | orderBy:propertyName:reverse">
             <td>{{friend.name}}</td>
             <td>{{friend.phone}}</td>
             <td>{{friend.age}}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample2', [])
         .controller('ExampleController', ['$scope', function($scope) {
           var friends = [
             {name: 'John',   phone: '555-1212',  age: 10},
             {name: 'Mary',   phone: '555-9876',  age: 19},
             {name: 'Mike',   phone: '555-4321',  age: 21},
             {name: 'Adam',   phone: '555-5678',  age: 35},
             {name: 'Julie',  phone: '555-8765',  age: 29}
           ];

           $scope.propertyName = 'age';
           $scope.reverse = true;
           $scope.friends = friends;

           $scope.sortBy = function(propertyName) {
             $scope.reverse = ($scope.propertyName === propertyName) ? !$scope.reverse : false;
             $scope.propertyName = propertyName;
           };
         }]);
     </file>
     <file name="style.css">
       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }

       .sortorder:after {
         content: '\25b2';   // BLACK UP-POINTING TRIANGLE
       }
       .sortorder.reverse:after {
         content: '\25bc';   // BLACK DOWN-POINTING TRIANGLE
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var unsortButton = element(by.partialButtonText('unsorted'));
       var nameHeader = element(by.partialButtonText('Name'));
       var phoneHeader = element(by.partialButtonText('Phone'));
       var ageHeader = element(by.partialButtonText('Age'));
       var firstName = element(by.repeater('friends').column('friend.name').row(0));
       var lastName = element(by.repeater('friends').column('friend.name').row(4));

       it('should sort friends by some property, when clicking on the column header', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         phoneHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Mary');

         nameHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('Mike');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');
       });

       it('should sort friends in reverse order, when clicking on the same column', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');

         ageHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');
       });

       it('should restore the original order, when clicking "Set to unsorted"', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         unsortButton.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Julie');
       });
     </file>
   </example>
 * <hr />
 *
 * @example
 * ### Using `orderBy` inside a controller
 *
 * It is also possible to call the `orderBy` filter manually, by injecting `orderByFilter`, and
 * calling it with the desired parameters. (Alternatively, you could inject the `$filter` factory
 * and retrieve the `orderBy` filter with `$filter('orderBy')`.)
 *
   <example name="orderBy-call-manually" module="orderByExample3">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <pre>Sort by = {{propertyName}}; reverse = {{reverse}}</pre>
         <hr/>
         <button ng-click="sortBy(null)">Set to unsorted</button>
         <hr/>
         <table class="friends">
           <tr>
             <th>
               <button ng-click="sortBy('name')">Name</button>
               <span class="sortorder" ng-show="propertyName === 'name'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('phone')">Phone Number</button>
               <span class="sortorder" ng-show="propertyName === 'phone'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('age')">Age</button>
               <span class="sortorder" ng-show="propertyName === 'age'" ng-class="{reverse: reverse}"></span>
             </th>
           </tr>
           <tr ng-repeat="friend in friends">
             <td>{{friend.name}}</td>
             <td>{{friend.phone}}</td>
             <td>{{friend.age}}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample3', [])
         .controller('ExampleController', ['$scope', 'orderByFilter', function($scope, orderBy) {
           var friends = [
             {name: 'John',   phone: '555-1212',  age: 10},
             {name: 'Mary',   phone: '555-9876',  age: 19},
             {name: 'Mike',   phone: '555-4321',  age: 21},
             {name: 'Adam',   phone: '555-5678',  age: 35},
             {name: 'Julie',  phone: '555-8765',  age: 29}
           ];

           $scope.propertyName = 'age';
           $scope.reverse = true;
           $scope.friends = orderBy(friends, $scope.propertyName, $scope.reverse);

           $scope.sortBy = function(propertyName) {
             $scope.reverse = (propertyName !== null && $scope.propertyName === propertyName)
                 ? !$scope.reverse : false;
             $scope.propertyName = propertyName;
             $scope.friends = orderBy(friends, $scope.propertyName, $scope.reverse);
           };
         }]);
     </file>
     <file name="style.css">
       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }

       .sortorder:after {
         content: '\25b2';   // BLACK UP-POINTING TRIANGLE
       }
       .sortorder.reverse:after {
         content: '\25bc';   // BLACK DOWN-POINTING TRIANGLE
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var unsortButton = element(by.partialButtonText('unsorted'));
       var nameHeader = element(by.partialButtonText('Name'));
       var phoneHeader = element(by.partialButtonText('Phone'));
       var ageHeader = element(by.partialButtonText('Age'));
       var firstName = element(by.repeater('friends').column('friend.name').row(0));
       var lastName = element(by.repeater('friends').column('friend.name').row(4));

       it('should sort friends by some property, when clicking on the column header', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         phoneHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Mary');

         nameHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('Mike');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');
       });

       it('should sort friends in reverse order, when clicking on the same column', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');

         ageHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');
       });

       it('should restore the original order, when clicking "Set to unsorted"', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         unsortButton.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Julie');
       });
     </file>
   </example>
 * <hr />
 *
 * @example
 * ### Using a custom comparator
 *
 * If you have very specific requirements about the way items are sorted, you can pass your own
 * comparator function. For example, you might need to compare some strings in a locale-sensitive
 * way. (When specifying a custom comparator, you also need to pass a value for the `reverse`
 * argument - passing `false` retains the default sorting order, i.e. ascending.)
 *
   <example name="orderBy-custom-comparator" module="orderByExample4">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <div class="friends-container custom-comparator">
           <h3>Locale-sensitive Comparator</h3>
           <table class="friends">
             <tr>
               <th>Name</th>
               <th>Favorite Letter</th>
             </tr>
             <tr ng-repeat="friend in friends | orderBy:'favoriteLetter':false:localeSensitiveComparator">
               <td>{{friend.name}}</td>
               <td>{{friend.favoriteLetter}}</td>
             </tr>
           </table>
         </div>
         <div class="friends-container default-comparator">
           <h3>Default Comparator</h3>
           <table class="friends">
             <tr>
               <th>Name</th>
               <th>Favorite Letter</th>
             </tr>
             <tr ng-repeat="friend in friends | orderBy:'favoriteLetter'">
               <td>{{friend.name}}</td>
               <td>{{friend.favoriteLetter}}</td>
             </tr>
           </table>
         </div>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample4', [])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.friends = [
             {name: 'John',   favoriteLetter: 'Ä'},
             {name: 'Mary',   favoriteLetter: 'Ü'},
             {name: 'Mike',   favoriteLetter: 'Ö'},
             {name: 'Adam',   favoriteLetter: 'H'},
             {name: 'Julie',  favoriteLetter: 'Z'}
           ];

           $scope.localeSensitiveComparator = function(v1, v2) {
             // If we don't get strings, just compare by index
             if (v1.type !== 'string' || v2.type !== 'string') {
               return (v1.index < v2.index) ? -1 : 1;
             }

             // Compare strings alphabetically, taking locale into account
             return v1.value.localeCompare(v2.value);
           };
         }]);
     </file>
     <file name="style.css">
       .friends-container {
         display: inline-block;
         margin: 0 30px;
       }

       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var container = element(by.css('.custom-comparator'));
       var names = container.all(by.repeater('friends').column('friend.name'));

       it('should sort friends by favorite letter (in correct alphabetical order)', function() {
         expect(names.get(0).getText()).toBe('John');
         expect(names.get(1).getText()).toBe('Adam');
         expect(names.get(2).getText()).toBe('Mike');
         expect(names.get(3).getText()).toBe('Mary');
         expect(names.get(4).getText()).toBe('Julie');
       });
     </file>
   </example>
 *
 */
orderByFilter.$inject = ['$parse'];
function orderByFilter($parse) {
  return function(array, sortPredicate, reverseOrder, compareFn) {

    if (array == null) return array;
    if (!isArrayLike(array)) {
      throw minErr('orderBy')('notarray', 'Expected array but received: {0}', array);
    }

    if (!isArray(sortPredicate)) { sortPredicate = [sortPredicate]; }
    if (sortPredicate.length === 0) { sortPredicate = ['+']; }

    var predicates = processPredicates(sortPredicate);

    var descending = reverseOrder ? -1 : 1;

    // Define the `compare()` function. Use a default comparator if none is specified.
    var compare = isFunction(compareFn) ? compareFn : defaultCompare;

    // The next three lines are a version of a Swartzian Transform idiom from Perl
    // (sometimes called the Decorate-Sort-Undecorate idiom)
    // See https://en.wikipedia.org/wiki/Schwartzian_transform
    var compareValues = Array.prototype.map.call(array, getComparisonObject);
    compareValues.sort(doComparison);
    array = compareValues.map(function(item) { return item.value; });

    return array;

    function getComparisonObject(value, index) {
      // NOTE: We are adding an extra `tieBreaker` value based on the element's index.
      // This will be used to keep the sort stable when none of the input predicates can
      // distinguish between two elements.
      return {
        value: value,
        tieBreaker: {value: index, type: 'number', index: index},
        predicateValues: predicates.map(function(predicate) {
          return getPredicateValue(predicate.get(value), index);
        })
      };
    }

    function doComparison(v1, v2) {
      for (var i = 0, ii = predicates.length; i < ii; i++) {
        var result = compare(v1.predicateValues[i], v2.predicateValues[i]);
        if (result) {
          return result * predicates[i].descending * descending;
        }
      }

      return (compare(v1.tieBreaker, v2.tieBreaker) || defaultCompare(v1.tieBreaker, v2.tieBreaker)) * descending;
    }
  };

  function processPredicates(sortPredicates) {
    return sortPredicates.map(function(predicate) {
      var descending = 1, get = identity;

      if (isFunction(predicate)) {
        get = predicate;
      } else if (isString(predicate)) {
        if ((predicate.charAt(0) === '+' || predicate.charAt(0) === '-')) {
          descending = predicate.charAt(0) === '-' ? -1 : 1;
          predicate = predicate.substring(1);
        }
        if (predicate !== '') {
          get = $parse(predicate);
          if (get.constant) {
            var key = get();
            get = function(value) { return value[key]; };
          }
        }
      }
      return {get: get, descending: descending};
    });
  }

  function isPrimitive(value) {
    switch (typeof value) {
      case 'number': /* falls through */
      case 'boolean': /* falls through */
      case 'string':
        return true;
      default:
        return false;
    }
  }

  function objectValue(value) {
    // If `valueOf` is a valid function use that
    if (isFunction(value.valueOf)) {
      value = value.valueOf();
      if (isPrimitive(value)) return value;
    }
    // If `toString` is a valid function and not the one from `Object.prototype` use that
    if (hasCustomToString(value)) {
      value = value.toString();
      if (isPrimitive(value)) return value;
    }

    return value;
  }

  function getPredicateValue(value, index) {
    var type = typeof value;
    if (value === null) {
      type = 'null';
    } else if (type === 'object') {
      value = objectValue(value);
    }
    return {value: value, type: type, index: index};
  }

  function defaultCompare(v1, v2) {
    var result = 0;
    var type1 = v1.type;
    var type2 = v2.type;

    if (type1 === type2) {
      var value1 = v1.value;
      var value2 = v2.value;

      if (type1 === 'string') {
        // Compare strings case-insensitively
        value1 = value1.toLowerCase();
        value2 = value2.toLowerCase();
      } else if (type1 === 'object') {
        // For basic objects, use the position of the object
        // in the collection instead of the value
        if (isObject(value1)) value1 = v1.index;
        if (isObject(value2)) value2 = v2.index;
      }

      if (value1 !== value2) {
        result = value1 < value2 ? -1 : 1;
      }
    } else {
      result = (type1 === 'undefined') ? 1 :
        (type2 === 'undefined') ? -1 :
        (type1 === 'null') ? 1 :
        (type2 === 'null') ? -1 :
        (type1 < type2) ? -1 : 1;
    }

    return result;
  }
}

function ngDirective(directive) {
  if (isFunction(directive)) {
    directive = {
      link: directive
    };
  }
  directive.restrict = directive.restrict || 'AC';
  return valueFn(directive);
}

/**
 * @ngdoc directive
 * @name a
 * @restrict E
 *
 * @description
 * Modifies the default behavior of the html a tag so that the default action is prevented when
 * the href attribute is empty.
 *
 * For dynamically creating `href` attributes for a tags, see the {@link ng.ngHref `ngHref`} directive.
 */
var htmlAnchorDirective = valueFn({
  restrict: 'E',
  compile: function(element, attr) {
    if (!attr.href && !attr.xlinkHref) {
      return function(scope, element) {
        // If the linked element is not an anchor tag anymore, do nothing
        if (element[0].nodeName.toLowerCase() !== 'a') return;

        // SVGAElement does not use the href attribute, but rather the 'xlinkHref' attribute.
        var href = toString.call(element.prop('href')) === '[object SVGAnimatedString]' ?
                   'xlink:href' : 'href';
        element.on('click', function(event) {
          // if we have no href url, then don't navigate anywhere.
          if (!element.attr(href)) {
            event.preventDefault();
          }
        });
      };
    }
  }
});

/**
 * @ngdoc directive
 * @name ngHref
 * @restrict A
 * @priority 99
 *
 * @description
 * Using AngularJS markup like `{{hash}}` in an href attribute will
 * make the link go to the wrong URL if the user clicks it before
 * AngularJS has a chance to replace the `{{hash}}` markup with its
 * value. Until AngularJS replaces the markup the link will be broken
 * and will most likely return a 404 error. The `ngHref` directive
 * solves this problem.
 *
 * The wrong way to write it:
 * ```html
 * <a href="http://www.gravatar.com/avatar/{{hash}}">link1</a>
 * ```
 *
 * The correct way to write it:
 * ```html
 * <a ng-href="http://www.gravatar.com/avatar/{{hash}}">link1</a>
 * ```
 *
 * @element A
 * @param {template} ngHref any string which can contain `{{}}` markup.
 *
 * @example
 * This example shows various combinations of `href`, `ng-href` and `ng-click` attributes
 * in links and their different behaviors:
    <example name="ng-href">
      <file name="index.html">
        <input ng-model="value" /><br />
        <a id="link-1" href ng-click="value = 1">link 1</a> (link, don't reload)<br />
        <a id="link-2" href="" ng-click="value = 2">link 2</a> (link, don't reload)<br />
        <a id="link-3" ng-href="/{{'123'}}">link 3</a> (link, reload!)<br />
        <a id="link-4" href="" name="xx" ng-click="value = 4">anchor</a> (link, don't reload)<br />
        <a id="link-5" name="xxx" ng-click="value = 5">anchor</a> (no link)<br />
        <a id="link-6" ng-href="{{value}}">link</a> (link, change location)
      </file>
      <file name="protractor.js" type="protractor">
        it('should execute ng-click but not reload when href without value', function() {
          element(by.id('link-1')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('1');
          expect(element(by.id('link-1')).getAttribute('href')).toBe('');
        });

        it('should execute ng-click but not reload when href empty string', function() {
          element(by.id('link-2')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('2');
          expect(element(by.id('link-2')).getAttribute('href')).toBe('');
        });

        it('should execute ng-click and change url when ng-href specified', function() {
          expect(element(by.id('link-3')).getAttribute('href')).toMatch(/\/123$/);

          element(by.id('link-3')).click();

          // At this point, we navigate away from an AngularJS page, so we need
          // to use browser.driver to get the base webdriver.

          browser.wait(function() {
            return browser.driver.getCurrentUrl().then(function(url) {
              return url.match(/\/123$/);
            });
          }, 5000, 'page should navigate to /123');
        });

        it('should execute ng-click but not reload when href empty string and name specified', function() {
          element(by.id('link-4')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('4');
          expect(element(by.id('link-4')).getAttribute('href')).toBe('');
        });

        it('should execute ng-click but not reload when no href but name specified', function() {
          element(by.id('link-5')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('5');
          expect(element(by.id('link-5')).getAttribute('href')).toBe(null);
        });

        it('should only change url when only ng-href', function() {
          element(by.model('value')).clear();
          element(by.model('value')).sendKeys('6');
          expect(element(by.id('link-6')).getAttribute('href')).toMatch(/\/6$/);

          element(by.id('link-6')).click();

          // At this point, we navigate away from an AngularJS page, so we need
          // to use browser.driver to get the base webdriver.
          browser.wait(function() {
            return browser.driver.getCurrentUrl().then(function(url) {
              return url.match(/\/6$/);
            });
          }, 5000, 'page should navigate to /6');
        });
      </file>
    </example>
 */

/**
 * @ngdoc directive
 * @name ngSrc
 * @restrict A
 * @priority 99
 *
 * @description
 * Using AngularJS markup like `{{hash}}` in a `src` attribute doesn't
 * work right: The browser will fetch from the URL with the literal
 * text `{{hash}}` until AngularJS replaces the expression inside
 * `{{hash}}`. The `ngSrc` directive solves this problem.
 *
 * The buggy way to write it:
 * ```html
 * <img src="http://www.gravatar.com/avatar/{{hash}}" alt="Description"/>
 * ```
 *
 * The correct way to write it:
 * ```html
 * <img ng-src="http://www.gravatar.com/avatar/{{hash}}" alt="Description" />
 * ```
 *
 * @element IMG
 * @param {template} ngSrc any string which can contain `{{}}` markup.
 */

/**
 * @ngdoc directive
 * @name ngSrcset
 * @restrict A
 * @priority 99
 *
 * @description
 * Using AngularJS markup like `{{hash}}` in a `srcset` attribute doesn't
 * work right: The browser will fetch from the URL with the literal
 * text `{{hash}}` until AngularJS replaces the expression inside
 * `{{hash}}`. The `ngSrcset` directive solves this problem.
 *
 * The buggy way to write it:
 * ```html
 * <img srcset="http://www.gravatar.com/avatar/{{hash}} 2x" alt="Description"/>
 * ```
 *
 * The correct way to write it:
 * ```html
 * <img ng-srcset="http://www.gravatar.com/avatar/{{hash}} 2x" alt="Description" />
 * ```
 *
 * @element IMG
 * @param {template} ngSrcset any string which can contain `{{}}` markup.
 */

/**
 * @ngdoc directive
 * @name ngDisabled
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * This directive sets the `disabled` attribute on the element (typically a form control,
 * e.g. `input`, `button`, `select` etc.) if the
 * {@link guide/expression expression} inside `ngDisabled` evaluates to truthy.
 *
 * A special directive is necessary because we cannot use interpolation inside the `disabled`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * @example
    <example name="ng-disabled">
      <file name="index.html">
        <label>Click me to toggle: <input type="checkbox" ng-model="checked"></label><br/>
        <button ng-model="button" ng-disabled="checked">Button</button>
      </file>
      <file name="protractor.js" type="protractor">
        it('should toggle button', function() {
          expect(element(by.css('button')).getAttribute('disabled')).toBeFalsy();
          element(by.model('checked')).click();
          expect(element(by.css('button')).getAttribute('disabled')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @param {expression} ngDisabled If the {@link guide/expression expression} is truthy,
 *     then the `disabled` attribute will be set on the element
 */


/**
 * @ngdoc directive
 * @name ngChecked
 * @restrict A
 * @priority 100
 *
 * @description
 * Sets the `checked` attribute on the element, if the expression inside `ngChecked` is truthy.
 *
 * Note that this directive should not be used together with {@link ngModel `ngModel`},
 * as this can lead to unexpected behavior.
 *
 * A special directive is necessary because we cannot use interpolation inside the `checked`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * @example
    <example name="ng-checked">
      <file name="index.html">
        <label>Check me to check both: <input type="checkbox" ng-model="leader"></label><br/>
        <input id="checkFollower" type="checkbox" ng-checked="leader" aria-label="Follower input">
      </file>
      <file name="protractor.js" type="protractor">
        it('should check both checkBoxes', function() {
          expect(element(by.id('checkFollower')).getAttribute('checked')).toBeFalsy();
          element(by.model('leader')).click();
          expect(element(by.id('checkFollower')).getAttribute('checked')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element INPUT
 * @param {expression} ngChecked If the {@link guide/expression expression} is truthy,
 *     then the `checked` attribute will be set on the element
 */


/**
 * @ngdoc directive
 * @name ngReadonly
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * Sets the `readonly` attribute on the element, if the expression inside `ngReadonly` is truthy.
 * Note that `readonly` applies only to `input` elements with specific types. [See the input docs on
 * MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly) for more information.
 *
 * A special directive is necessary because we cannot use interpolation inside the `readonly`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * @example
    <example name="ng-readonly">
      <file name="index.html">
        <label>Check me to make text readonly: <input type="checkbox" ng-model="checked"></label><br/>
        <input type="text" ng-readonly="checked" value="I'm AngularJS" aria-label="Readonly field" />
      </file>
      <file name="protractor.js" type="protractor">
        it('should toggle readonly attr', function() {
          expect(element(by.css('[type="text"]')).getAttribute('readonly')).toBeFalsy();
          element(by.model('checked')).click();
          expect(element(by.css('[type="text"]')).getAttribute('readonly')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element INPUT
 * @param {expression} ngReadonly If the {@link guide/expression expression} is truthy,
 *     then special attribute "readonly" will be set on the element
 */


/**
 * @ngdoc directive
 * @name ngSelected
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * Sets the `selected` attribute on the element, if the expression inside `ngSelected` is truthy.
 *
 * A special directive is necessary because we cannot use interpolation inside the `selected`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * <div class="alert alert-warning">
 *   **Note:** `ngSelected` does not interact with the `select` and `ngModel` directives, it only
 *   sets the `selected` attribute on the element. If you are using `ngModel` on the select, you
 *   should not use `ngSelected` on the options, as `ngModel` will set the select value and
 *   selected options.
 * </div>
 *
 * @example
    <example name="ng-selected">
      <file name="index.html">
        <label>Check me to select: <input type="checkbox" ng-model="selected"></label><br/>
        <select aria-label="ngSelected demo">
          <option>Hello!</option>
          <option id="greet" ng-selected="selected">Greetings!</option>
        </select>
      </file>
      <file name="protractor.js" type="protractor">
        it('should select Greetings!', function() {
          expect(element(by.id('greet')).getAttribute('selected')).toBeFalsy();
          element(by.model('selected')).click();
          expect(element(by.id('greet')).getAttribute('selected')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element OPTION
 * @param {expression} ngSelected If the {@link guide/expression expression} is truthy,
 *     then special attribute "selected" will be set on the element
 */

/**
 * @ngdoc directive
 * @name ngOpen
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * Sets the `open` attribute on the element, if the expression inside `ngOpen` is truthy.
 *
 * A special directive is necessary because we cannot use interpolation inside the `open`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * ## A note about browser compatibility
 *
 * Internet Explorer and Edge do not support the `details` element, it is
 * recommended to use {@link ng.ngShow} and {@link ng.ngHide} instead.
 *
 * @example
     <example name="ng-open">
       <file name="index.html">
         <label>Toggle details: <input type="checkbox" ng-model="open"></label><br/>
         <details id="details" ng-open="open">
            <summary>List</summary>
            <ul>
              <li>Apple</li>
              <li>Orange</li>
              <li>Durian</li>
            </ul>
         </details>
       </file>
       <file name="protractor.js" type="protractor">
         it('should toggle open', function() {
           expect(element(by.id('details')).getAttribute('open')).toBeFalsy();
           element(by.model('open')).click();
           expect(element(by.id('details')).getAttribute('open')).toBeTruthy();
         });
       </file>
     </example>
 *
 * @element DETAILS
 * @param {expression} ngOpen If the {@link guide/expression expression} is truthy,
 *     then special attribute "open" will be set on the element
 */

var ngAttributeAliasDirectives = {};

// boolean attrs are evaluated
forEach(BOOLEAN_ATTR, function(propName, attrName) {
  // binding to multiple is not supported
  if (propName === 'multiple') return;

  function defaultLinkFn(scope, element, attr) {
    scope.$watch(attr[normalized], function ngBooleanAttrWatchAction(value) {
      attr.$set(attrName, !!value);
    });
  }

  var normalized = directiveNormalize('ng-' + attrName);
  var linkFn = defaultLinkFn;

  if (propName === 'checked') {
    linkFn = function(scope, element, attr) {
      // ensuring ngChecked doesn't interfere with ngModel when both are set on the same input
      if (attr.ngModel !== attr[normalized]) {
        defaultLinkFn(scope, element, attr);
      }
    };
  }

  ngAttributeAliasDirectives[normalized] = function() {
    return {
      restrict: 'A',
      priority: 100,
      link: linkFn
    };
  };
});

// aliased input attrs are evaluated
forEach(ALIASED_ATTR, function(htmlAttr, ngAttr) {
  ngAttributeAliasDirectives[ngAttr] = function() {
    return {
      priority: 100,
      link: function(scope, element, attr) {
        //special case ngPattern when a literal regular expression value
        //is used as the expression (this way we don't have to watch anything).
        if (ngAttr === 'ngPattern' && attr.ngPattern.charAt(0) === '/') {
          var match = attr.ngPattern.match(REGEX_STRING_REGEXP);
          if (match) {
            attr.$set('ngPattern', new RegExp(match[1], match[2]));
            return;
          }
        }

        scope.$watch(attr[ngAttr], function ngAttrAliasWatchAction(value) {
          attr.$set(ngAttr, value);
        });
      }
    };
  };
});

// ng-src, ng-srcset, ng-href are interpolated
forEach(['src', 'srcset', 'href'], function(attrName) {
  var normalized = directiveNormalize('ng-' + attrName);
  ngAttributeAliasDirectives[normalized] = ['$sce', function($sce) {
    return {
      priority: 99, // it needs to run after the attributes are interpolated
      link: function(scope, element, attr) {
        var propName = attrName,
            name = attrName;

        if (attrName === 'href' &&
            toString.call(element.prop('href')) === '[object SVGAnimatedString]') {
          name = 'xlinkHref';
          attr.$attr[name] = 'xlink:href';
          propName = null;
        }

        // We need to sanitize the url at least once, in case it is a constant
        // non-interpolated attribute.
        attr.$set(normalized, $sce.getTrustedMediaUrl(attr[normalized]));

        attr.$observe(normalized, function(value) {
          if (!value) {
            if (attrName === 'href') {
              attr.$set(name, null);
            }
            return;
          }

          attr.$set(name, value);

          // Support: IE 9-11 only
          // On IE, if "ng:src" directive declaration is used and "src" attribute doesn't exist
          // then calling element.setAttribute('src', 'foo') doesn't do anything, so we need
          // to set the property as well to achieve the desired effect.
          // We use attr[attrName] value since $set might have sanitized the url.
          if (msie && propName) element.prop(propName, attr[name]);
        });
      }
    };
  }];
});

/* global -nullFormCtrl, -PENDING_CLASS, -SUBMITTED_CLASS
 */
var nullFormCtrl = {
  $addControl: noop,
  $getControls: valueFn([]),
  $$renameControl: nullFormRenameControl,
  $removeControl: noop,
  $setValidity: noop,
  $setDirty: noop,
  $setPristine: noop,
  $setSubmitted: noop,
  $$setSubmitted: noop
},
PENDING_CLASS = 'ng-pending',
SUBMITTED_CLASS = 'ng-submitted';

function nullFormRenameControl(control, name) {
  control.$name = name;
}

/**
 * @ngdoc type
 * @name form.FormController
 *
 * @property {boolean} $pristine True if user has not interacted with the form yet.
 * @property {boolean} $dirty True if user has already interacted with the form.
 * @property {boolean} $valid True if all of the containing forms and controls are valid.
 * @property {boolean} $invalid True if at least one containing control or form is invalid.
 * @property {boolean} $submitted True if user has submitted the form even if its invalid.
 *
 * @property {Object} $pending An object hash, containing references to controls or forms with
 *  pending validators, where:
 *
 *  - keys are validations tokens (error names).
 *  - values are arrays of controls or forms that have a pending validator for the given error name.
 *
 * See {@link form.FormController#$error $error} for a list of built-in validation tokens.
 *
 * @property {Object} $error An object hash, containing references to controls or forms with failing
 *  validators, where:
 *
 *  - keys are validation tokens (error names),
 *  - values are arrays of controls or forms that have a failing validator for the given error name.
 *
 *  Built-in validation tokens:
 *  - `email`
 *  - `max`
 *  - `maxlength`
 *  - `min`
 *  - `minlength`
 *  - `number`
 *  - `pattern`
 *  - `required`
 *  - `url`
 *  - `date`
 *  - `datetimelocal`
 *  - `time`
 *  - `week`
 *  - `month`
 *
 * @description
 * `FormController` keeps track of all its controls and nested forms as well as the state of them,
 * such as being valid/invalid or dirty/pristine.
 *
 * Each {@link ng.directive:form form} directive creates an instance
 * of `FormController`.
 *
 */
//asks for $scope to fool the BC controller module
FormController.$inject = ['$element', '$attrs', '$scope', '$animate', '$interpolate'];
function FormController($element, $attrs, $scope, $animate, $interpolate) {
  this.$$controls = [];

  // init state
  this.$error = {};
  this.$$success = {};
  this.$pending = undefined;
  this.$name = $interpolate($attrs.name || $attrs.ngForm || '')($scope);
  this.$dirty = false;
  this.$pristine = true;
  this.$valid = true;
  this.$invalid = false;
  this.$submitted = false;
  this.$$parentForm = nullFormCtrl;

  this.$$element = $element;
  this.$$animate = $animate;

  setupValidity(this);
}

FormController.prototype = {
  /**
   * @ngdoc method
   * @name form.FormController#$rollbackViewValue
   *
   * @description
   * Rollback all form controls pending updates to the `$modelValue`.
   *
   * Updates may be pending by a debounced event or because the input is waiting for a some future
   * event defined in `ng-model-options`. This method is typically needed by the reset button of
   * a form that uses `ng-model-options` to pend updates.
   */
  $rollbackViewValue: function() {
    forEach(this.$$controls, function(control) {
      control.$rollbackViewValue();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$commitViewValue
   *
   * @description
   * Commit all form controls pending updates to the `$modelValue`.
   *
   * Updates may be pending by a debounced event or because the input is waiting for a some future
   * event defined in `ng-model-options`. This method is rarely needed as `NgModelController`
   * usually handles calling this in response to input events.
   */
  $commitViewValue: function() {
    forEach(this.$$controls, function(control) {
      control.$commitViewValue();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$addControl
   * @param {object} control control object, either a {@link form.FormController} or an
   * {@link ngModel.NgModelController}
   *
   * @description
   * Register a control with the form. Input elements using ngModelController do this automatically
   * when they are linked.
   *
   * Note that the current state of the control will not be reflected on the new parent form. This
   * is not an issue with normal use, as freshly compiled and linked controls are in a `$pristine`
   * state.
   *
   * However, if the method is used programmatically, for example by adding dynamically created controls,
   * or controls that have been previously removed without destroying their corresponding DOM element,
   * it's the developers responsibility to make sure the current state propagates to the parent form.
   *
   * For example, if an input control is added that is already `$dirty` and has `$error` properties,
   * calling `$setDirty()` and `$validate()` afterwards will propagate the state to the parent form.
   */
  $addControl: function(control) {
    // Breaking change - before, inputs whose name was "hasOwnProperty" were quietly ignored
    // and not added to the scope.  Now we throw an error.
    assertNotHasOwnProperty(control.$name, 'input');
    this.$$controls.push(control);

    if (control.$name) {
      this[control.$name] = control;
    }

    control.$$parentForm = this;
  },

  /**
   * @ngdoc method
   * @name form.FormController#$getControls
   * @returns {Array} the controls that are currently part of this form
   *
   * @description
   * This method returns a **shallow copy** of the controls that are currently part of this form.
   * The controls can be instances of {@link form.FormController `FormController`}
   * ({@link ngForm "child-forms"}) and of {@link ngModel.NgModelController `NgModelController`}.
   * If you need access to the controls of child-forms, you have to call `$getControls()`
   * recursively on them.
   * This can be used for example to iterate over all controls to validate them.
   *
   * The controls can be accessed normally, but adding to, or removing controls from the array has
   * no effect on the form. Instead, use {@link form.FormController#$addControl `$addControl()`} and
   * {@link form.FormController#$removeControl `$removeControl()`} for this use-case.
   * Likewise, adding a control to, or removing a control from the form is not reflected
   * in the shallow copy. That means you should get a fresh copy from `$getControls()` every time
   * you need access to the controls.
   */
  $getControls: function() {
    return shallowCopy(this.$$controls);
  },

  // Private API: rename a form control
  $$renameControl: function(control, newName) {
    var oldName = control.$name;

    if (this[oldName] === control) {
      delete this[oldName];
    }
    this[newName] = control;
    control.$name = newName;
  },

  /**
   * @ngdoc method
   * @name form.FormController#$removeControl
   * @param {object} control control object, either a {@link form.FormController} or an
   * {@link ngModel.NgModelController}
   *
   * @description
   * Deregister a control from the form.
   *
   * Input elements using ngModelController do this automatically when they are destroyed.
   *
   * Note that only the removed control's validation state (`$errors`etc.) will be removed from the
   * form. `$dirty`, `$submitted` states will not be changed, because the expected behavior can be
   * different from case to case. For example, removing the only `$dirty` control from a form may or
   * may not mean that the form is still `$dirty`.
   */
  $removeControl: function(control) {
    if (control.$name && this[control.$name] === control) {
      delete this[control.$name];
    }
    forEach(this.$pending, function(value, name) {
      // eslint-disable-next-line no-invalid-this
      this.$setValidity(name, null, control);
    }, this);
    forEach(this.$error, function(value, name) {
      // eslint-disable-next-line no-invalid-this
      this.$setValidity(name, null, control);
    }, this);
    forEach(this.$$success, function(value, name) {
      // eslint-disable-next-line no-invalid-this
      this.$setValidity(name, null, control);
    }, this);

    arrayRemove(this.$$controls, control);
    control.$$parentForm = nullFormCtrl;
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setDirty
   *
   * @description
   * Sets the form to a dirty state.
   *
   * This method can be called to add the 'ng-dirty' class and set the form to a dirty
   * state (ng-dirty class). This method will also propagate to parent forms.
   */
  $setDirty: function() {
    this.$$animate.removeClass(this.$$element, PRISTINE_CLASS);
    this.$$animate.addClass(this.$$element, DIRTY_CLASS);
    this.$dirty = true;
    this.$pristine = false;
    this.$$parentForm.$setDirty();
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setPristine
   *
   * @description
   * Sets the form to its pristine state.
   *
   * This method sets the form's `$pristine` state to true, the `$dirty` state to false, removes
   * the `ng-dirty` class and adds the `ng-pristine` class. Additionally, it sets the `$submitted`
   * state to false.
   *
   * This method will also propagate to all the controls contained in this form.
   *
   * Setting a form back to a pristine state is often useful when we want to 'reuse' a form after
   * saving or resetting it.
   */
  $setPristine: function() {
    this.$$animate.setClass(this.$$element, PRISTINE_CLASS, DIRTY_CLASS + ' ' + SUBMITTED_CLASS);
    this.$dirty = false;
    this.$pristine = true;
    this.$submitted = false;
    forEach(this.$$controls, function(control) {
      control.$setPristine();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setUntouched
   *
   * @description
   * Sets the form to its untouched state.
   *
   * This method can be called to remove the 'ng-touched' class and set the form controls to their
   * untouched state (ng-untouched class).
   *
   * Setting a form controls back to their untouched state is often useful when setting the form
   * back to its pristine state.
   */
  $setUntouched: function() {
    forEach(this.$$controls, function(control) {
      control.$setUntouched();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setSubmitted
   *
   * @description
   * Sets the form to its `$submitted` state. This will also set `$submitted` on all child and
   * parent forms of the form.
   */
  $setSubmitted: function() {
    var rootForm = this;
    while (rootForm.$$parentForm && (rootForm.$$parentForm !== nullFormCtrl)) {
      rootForm = rootForm.$$parentForm;
    }
    rootForm.$$setSubmitted();
  },

  $$setSubmitted: function() {
    this.$$animate.addClass(this.$$element, SUBMITTED_CLASS);
    this.$submitted = true;
    forEach(this.$$controls, function(control) {
      if (control.$$setSubmitted) {
        control.$$setSubmitted();
      }
    });
  }
};

/**
 * @ngdoc method
 * @name form.FormController#$setValidity
 *
 * @description
 * Change the validity state of the form, and notify the parent form (if any).
 *
 * Application developers will rarely need to call this method directly. It is used internally, by
 * {@link ngModel.NgModelController#$setValidity NgModelController.$setValidity()}, to propagate a
 * control's validity state to the parent `FormController`.
 *
 * @param {string} validationErrorKey Name of the validator. The `validationErrorKey` will be
 *        assigned to either `$error[validationErrorKey]` or `$pending[validationErrorKey]` (for
 *        unfulfilled `$asyncValidators`), so that it is available for data-binding. The
 *        `validationErrorKey` should be in camelCase and will get converted into dash-case for
 *        class name. Example: `myError` will result in `ng-valid-my-error` and
 *        `ng-invalid-my-error` classes and can be bound to as `{{ someForm.$error.myError }}`.
 * @param {boolean} isValid Whether the current state is valid (true), invalid (false), pending
 *        (undefined),  or skipped (null). Pending is used for unfulfilled `$asyncValidators`.
 *        Skipped is used by AngularJS when validators do not run because of parse errors and when
 *        `$asyncValidators` do not run because any of the `$validators` failed.
 * @param {NgModelController | FormController} controller - The controller whose validity state is
 *        triggering the change.
 */
addSetValidityMethod({
  clazz: FormController,
  set: function(object, property, controller) {
    var list = object[property];
    if (!list) {
      object[property] = [controller];
    } else {
      var index = list.indexOf(controller);
      if (index === -1) {
        list.push(controller);
      }
    }
  },
  unset: function(object, property, controller) {
    var list = object[property];
    if (!list) {
      return;
    }
    arrayRemove(list, controller);
    if (list.length === 0) {
      delete object[property];
    }
  }
});

/**
 * @ngdoc directive
 * @name ngForm
 * @restrict EAC
 *
 * @description
 * Helper directive that makes it possible to create control groups inside a
 * {@link ng.directive:form `form`} directive.
 * These "child forms" can be used, for example, to determine the validity of a sub-group of
 * controls.
 *
 * <div class="alert alert-danger">
 * **Note**: `ngForm` cannot be used as a replacement for `<form>`, because it lacks its
 * [built-in HTML functionality](https://html.spec.whatwg.org/#the-form-element).
 * Specifically, you cannot submit `ngForm` like a `<form>` tag. That means,
 * you cannot send data to the server with `ngForm`, or integrate it with
 * {@link ng.directive:ngSubmit `ngSubmit`}.
 * </div>
 *
 * @param {string=} ngForm|name Name of the form. If specified, the form controller will
 *                              be published into the related scope, under this name.
 *
 */

 /**
 * @ngdoc directive
 * @name form
 * @restrict E
 *
 * @description
 * Directive that instantiates
 * {@link form.FormController FormController}.
 *
 * If the `name` attribute is specified, the form controller is published onto the current scope under
 * this name.
 *
 * ## Alias: {@link ng.directive:ngForm `ngForm`}
 *
 * In AngularJS, forms can be nested. This means that the outer form is valid when all of the child
 * forms are valid as well. However, browsers do not allow nesting of `<form>` elements, so
 * AngularJS provides the {@link ng.directive:ngForm `ngForm`} directive, which behaves identically to
 * `form` but can be nested. Nested forms can be useful, for example, if the validity of a sub-group
 * of controls needs to be determined.
 *
 * ## CSS classes
 *  - `ng-valid` is set if the form is valid.
 *  - `ng-invalid` is set if the form is invalid.
 *  - `ng-pending` is set if the form is pending.
 *  - `ng-pristine` is set if the form is pristine.
 *  - `ng-dirty` is set if the form is dirty.
 *  - `ng-submitted` is set if the form was submitted.
 *
 * Keep in mind that ngAnimate can detect each of these classes when added and removed.
 *
 *
 * ## Submitting a form and preventing the default action
 *
 * Since the role of forms in client-side AngularJS applications is different than in classical
 * roundtrip apps, it is desirable for the browser not to translate the form submission into a full
 * page reload that sends the data to the server. Instead some javascript logic should be triggered
 * to handle the form submission in an application-specific way.
 *
 * For this reason, AngularJS prevents the default action (form submission to the server) unless the
 * `<form>` element has an `action` attribute specified.
 *
 * You can use one of the following two ways to specify what javascript method should be called when
 * a form is submitted:
 *
 * - {@link ng.directive:ngSubmit ngSubmit} directive on the form element
 * - {@link ng.directive:ngClick ngClick} directive on the first
  *  button or input field of type submit (input[type=submit])
 *
 * To prevent double execution of the handler, use only one of the {@link ng.directive:ngSubmit ngSubmit}
 * or {@link ng.directive:ngClick ngClick} directives.
 * This is because of the following form submission rules in the HTML specification:
 *
 * - If a form has only one input field then hitting enter in this field triggers form submit
 * (`ngSubmit`)
 * - if a form has 2+ input fields and no buttons or input[type=submit] then hitting enter
 * doesn't trigger submit
 * - if a form has one or more input fields and one or more buttons or input[type=submit] then
 * hitting enter in any of the input fields will trigger the click handler on the *first* button or
 * input[type=submit] (`ngClick`) *and* a submit handler on the enclosing form (`ngSubmit`)
 *
 * Any pending `ngModelOptions` changes will take place immediately when an enclosing form is
 * submitted. Note that `ngClick` events will occur before the model is updated. Use `ngSubmit`
 * to have access to the updated model.
 *
 * @animations
 * Animations in ngForm are triggered when any of the associated CSS classes are added and removed.
 * These classes are: `.ng-pristine`, `.ng-dirty`, `.ng-invalid` and `.ng-valid` as well as any
 * other validations that are performed within the form. Animations in ngForm are similar to how
 * they work in ngClass and animations can be hooked into using CSS transitions, keyframes as well
 * as JS animations.
 *
 * The following example shows a simple way to utilize CSS transitions to style a form element
 * that has been rendered as invalid after it has been validated:
 *
 * <pre>
 * //be sure to include ngAnimate as a module to hook into more
 * //advanced animations
 * .my-form {
 *   transition:0.5s linear all;
 *   background: white;
 * }
 * .my-form.ng-invalid {
 *   background: red;
 *   color:white;
 * }
 * </pre>
 *
 * @example
    <example name="ng-form" deps="angular-animate.js" animations="true" fixBase="true" module="formExample">
      <file name="index.html">
       <script>
         angular.module('formExample', [])
           .controller('FormController', ['$scope', function($scope) {
             $scope.userType = 'guest';
           }]);
       </script>
       <style>
        .my-form {
          transition:all linear 0.5s;
          background: transparent;
        }
        .my-form.ng-invalid {
          background: red;
        }
       </style>
       <form name="myForm" ng-controller="FormController" class="my-form">
         userType: <input name="input" ng-model="userType" required>
         <span class="error" ng-show="myForm.input.$error.required">Required!</span><br>
         <code>userType = {{userType}}</code><br>
         <code>myForm.input.$valid = {{myForm.input.$valid}}</code><br>
         <code>myForm.input.$error = {{myForm.input.$error}}</code><br>
         <code>myForm.$valid = {{myForm.$valid}}</code><br>
         <code>myForm.$error.required = {{!!myForm.$error.required}}</code><br>
        </form>
      </file>
      <file name="protractor.js" type="protractor">
        it('should initialize to model', function() {
          var userType = element(by.binding('userType'));
          var valid = element(by.binding('myForm.input.$valid'));

          expect(userType.getText()).toContain('guest');
          expect(valid.getText()).toContain('true');
        });

        it('should be invalid if empty', function() {
          var userType = element(by.binding('userType'));
          var valid = element(by.binding('myForm.input.$valid'));
          var userInput = element(by.model('userType'));

          userInput.clear();
          userInput.sendKeys('');

          expect(userType.getText()).toEqual('userType =');
          expect(valid.getText()).toContain('false');
        });
      </file>
    </example>
 *
 * @param {string=} name Name of the form. If specified, the form controller will be published into
 *                       related scope, under this name.
 */
var formDirectiveFactory = function(isNgForm) {
  return ['$timeout', '$parse', function($timeout, $parse) {
    var formDirective = {
      name: 'form',
      restrict: isNgForm ? 'EAC' : 'E',
      require: ['form', '^^?form'], //first is the form's own ctrl, second is an optional parent form
      controller: FormController,
      compile: function ngFormCompile(formElement, attr) {
        // Setup initial state of the control
        formElement.addClass(PRISTINE_CLASS).addClass(VALID_CLASS);

        var nameAttr = attr.name ? 'name' : (isNgForm && attr.ngForm ? 'ngForm' : false);

        return {
          pre: function ngFormPreLink(scope, formElement, attr, ctrls) {
            var controller = ctrls[0];

            // if `action` attr is not present on the form, prevent the default action (submission)
            if (!('action' in attr)) {
              // we can't use jq events because if a form is destroyed during submission the default
              // action is not prevented. see #1238
              //
              // IE 9 is not affected because it doesn't fire a submit event and try to do a full
              // page reload if the form was destroyed by submission of the form via a click handler
              // on a button in the form. Looks like an IE9 specific bug.
              var handleFormSubmission = function(event) {
                scope.$apply(function() {
                  controller.$commitViewValue();
                  controller.$setSubmitted();
                });

                event.preventDefault();
              };

              formElement[0].addEventListener('submit', handleFormSubmission);

              // unregister the preventDefault listener so that we don't not leak memory but in a
              // way that will achieve the prevention of the default action.
              formElement.on('$destroy', function() {
                $timeout(function() {
                  formElement[0].removeEventListener('submit', handleFormSubmission);
                }, 0, false);
              });
            }

            var parentFormCtrl = ctrls[1] || controller.$$parentForm;
            parentFormCtrl.$addControl(controller);

            var setter = nameAttr ? getSetter(controller.$name) : noop;

            if (nameAttr) {
              setter(scope, controller);
              attr.$observe(nameAttr, function(newValue) {
                if (controller.$name === newValue) return;
                setter(scope, undefined);
                controller.$$parentForm.$$renameControl(controller, newValue);
                setter = getSetter(controller.$name);
                setter(scope, controller);
              });
            }
            formElement.on('$destroy', function() {
              controller.$$parentForm.$removeControl(controller);
              setter(scope, undefined);
              extend(controller, nullFormCtrl); //stop propagating child destruction handlers upwards
            });
          }
        };
      }
    };

    return formDirective;

    function getSetter(expression) {
      if (expression === '') {
        //create an assignable expression, so forms with an empty name can be renamed later
        return $parse('this[""]').assign;
      }
      return $parse(expression).assign || noop;
    }
  }];
};

var formDirective = formDirectiveFactory();
var ngFormDirective = formDirectiveFactory(true);



// helper methods
function setupValidity(instance) {
  instance.$$classCache = {};
  instance.$$classCache[INVALID_CLASS] = !(instance.$$classCache[VALID_CLASS] = instance.$$element.hasClass(VALID_CLASS));
}
function addSetValidityMethod(context) {
  var clazz = context.clazz,
      set = context.set,
      unset = context.unset;

  clazz.prototype.$setValidity = function(validationErrorKey, state, controller) {
    if (isUndefined(state)) {
      createAndSet(this, '$pending', validationErrorKey, controller);
    } else {
      unsetAndCleanup(this, '$pending', validationErrorKey, controller);
    }
    if (!isBoolean(state)) {
      unset(this.$error, validationErrorKey, controller);
      unset(this.$$success, validationErrorKey, controller);
    } else {
      if (state) {
        unset(this.$error, validationErrorKey, controller);
        set(this.$$success, validationErrorKey, controller);
      } else {
        set(this.$error, validationErrorKey, controller);
        unset(this.$$success, validationErrorKey, controller);
      }
    }
    if (this.$pending) {
      cachedToggleClass(this, PENDING_CLASS, true);
      this.$valid = this.$invalid = undefined;
      toggleValidationCss(this, '', null);
    } else {
      cachedToggleClass(this, PENDING_CLASS, false);
      this.$valid = isObjectEmpty(this.$error);
      this.$invalid = !this.$valid;
      toggleValidationCss(this, '', this.$valid);
    }

    // re-read the state as the set/unset methods could have
    // combined state in this.$error[validationError] (used for forms),
    // where setting/unsetting only increments/decrements the value,
    // and does not replace it.
    var combinedState;
    if (this.$pending && this.$pending[validationErrorKey]) {
      combinedState = undefined;
    } else if (this.$error[validationErrorKey]) {
      combinedState = false;
    } else if (this.$$success[validationErrorKey]) {
      combinedState = true;
    } else {
      combinedState = null;
    }

    toggleValidationCss(this, validationErrorKey, combinedState);
    this.$$parentForm.$setValidity(validationErrorKey, combinedState, this);
  };

  function createAndSet(ctrl, name, value, controller) {
    if (!ctrl[name]) {
      ctrl[name] = {};
    }
    set(ctrl[name], value, controller);
  }

  function unsetAndCleanup(ctrl, name, value, controller) {
    if (ctrl[name]) {
      unset(ctrl[name], value, controller);
    }
    if (isObjectEmpty(ctrl[name])) {
      ctrl[name] = undefined;
    }
  }

  function cachedToggleClass(ctrl, className, switchValue) {
    if (switchValue && !ctrl.$$classCache[className]) {
      ctrl.$$animate.addClass(ctrl.$$element, className);
      ctrl.$$classCache[className] = true;
    } else if (!switchValue && ctrl.$$classCache[className]) {
      ctrl.$$animate.removeClass(ctrl.$$element, className);
      ctrl.$$classCache[className] = false;
    }
  }

  function toggleValidationCss(ctrl, validationErrorKey, isValid) {
    validationErrorKey = validationErrorKey ? '-' + snake_case(validationErrorKey, '-') : '';

    cachedToggleClass(ctrl, VALID_CLASS + validationErrorKey, isValid === true);
    cachedToggleClass(ctrl, INVALID_CLASS + validationErrorKey, isValid === false);
  }
}

function isObjectEmpty(obj) {
  if (obj) {
    for (var prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false;
      }
    }
  }
  return true;
}

/* global
  VALID_CLASS: false,
  INVALID_CLASS: false,
  PRISTINE_CLASS: false,
  DIRTY_CLASS: false,
  ngModelMinErr: false
*/

// Regex code was initially obtained from SO prior to modification: https://stackoverflow.com/questions/3143070/javascript-regex-iso-datetime#answer-3143231
var ISO_DATE_REGEXP = /^\d{4,}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+(?:[+-][0-2]\d:[0-5]\d|Z)$/;
// See valid URLs in RFC3987 (http://tools.ietf.org/html/rfc3987)
// Note: We are being more lenient, because browsers are too.
//   1. Scheme
//   2. Slashes
//   3. Username
//   4. Password
//   5. Hostname
//   6. Port
//   7. Path
//   8. Query
//   9. Fragment
//                 1111111111111111 222   333333    44444        55555555555555555555555     666     77777777     8888888     999
var URL_REGEXP = /^[a-z][a-z\d.+-]*:\/*(?:[^:@]+(?::[^@]+)?@)?(?:[^\s:/?#]+|\[[a-f\d:]+])(?::\d+)?(?:\/[^?#]*)?(?:\?[^#]*)?(?:#.*)?$/i;
// eslint-disable-next-line max-len
var EMAIL_REGEXP = /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
var NUMBER_REGEXP = /^\s*(-|\+)?(\d+|(\d*(\.\d*)))([eE][+-]?\d+)?\s*$/;
var DATE_REGEXP = /^(\d{4,})-(\d{2})-(\d{2})$/;
var DATETIMELOCAL_REGEXP = /^(\d{4,})-(\d\d)-(\d\d)T(\d\d):(\d\d)(?::(\d\d)(\.\d{1,3})?)?$/;
var WEEK_REGEXP = /^(\d{4,})-W(\d\d)$/;
var MONTH_REGEXP = /^(\d{4,})-(\d\d)$/;
var TIME_REGEXP = /^(\d\d):(\d\d)(?::(\d\d)(\.\d{1,3})?)?$/;

var PARTIAL_VALIDATION_EVENTS = 'keydown wheel mousedown';
var PARTIAL_VALIDATION_TYPES = createMap();
forEach('date,datetime-local,month,time,week'.split(','), function(type) {
  PARTIAL_VALIDATION_TYPES[type] = true;
});

var inputType = {

  /**
   * @ngdoc input
   * @name input[text]
   *
   * @description
   * Standard HTML text input with AngularJS data binding, inherited by most of the `input` elements.
   *
   *
   * @param {string} ngModel Assignable AngularJS expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} required Adds `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
   *    interaction with the input element.
   * @param {boolean=} [ngTrim=true] If set to false AngularJS will not automatically trim the input.
   *    This parameter is ignored for input[type=password] controls, which will never trim the
   *    input.
   *
   * @example
      <example name="text-input-directive" module="textInputExample">
        <file name="index.html">
         <script>
           angular.module('textInputExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.example = {
                 text: 'guest',
                 word: /^\s*\w*\s*$/
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>Single word:
             <input type="text" name="input" ng-model="example.text"
                    ng-pattern="example.word" required ng-trim="false">
           </label>
           <div role="alert">
             <span class="error" ng-show="myForm.input.$error.required">
               Required!</span>
             <span class="error" ng-show="myForm.input.$error.pattern">
               Single word only!</span>
           </div>
           <code>text = {{example.text}}</code><br/>
           <code>myForm.input.$valid = {{myForm.input.$valid}}</code><br/>
           <code>myForm.input.$error = {{myForm.input.$error}}</code><br/>
           <code>myForm.$valid = {{myForm.$valid}}</code><br/>
           <code>myForm.$error.required = {{!!myForm.$error.required}}</code><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          var text = element(by.binding('example.text'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('example.text'));

          it('should initialize to model', function() {
            expect(text.getText()).toContain('guest');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');

            expect(text.getText()).toEqual('text =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if multi word', function() {
            input.clear();
            input.sendKeys('hello world');

            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'text': textInputType,

    /**
     * @ngdoc input
     * @name input[date]
     *
     * @description
     * Input with date validation and transformation. In browsers that do not yet support
     * the HTML5 date input, a text element will be used. In that case, text must be entered in a valid ISO-8601
     * date format (yyyy-MM-dd), for example: `2009-01-06`. Since many
     * modern browsers do not yet support this input type, it is important to provide cues to users on the
     * expected input format via a placeholder or label.
     *
     * The model must always be a Date object, otherwise AngularJS will throw an error.
     * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
     *
     * The timezone to be used to read/write the `Date` instance in the model can be defined using
     * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
     *
     * @param {string} ngModel Assignable AngularJS expression to data-bind to.
     * @param {string=} name Property name of the form under which the control is published.
     * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`. This must be a
     *   valid ISO date string (yyyy-MM-dd). You can also use interpolation inside this attribute
     *   (e.g. `min="{{minDate | date:'yyyy-MM-dd'}}"`). Note that `min` will also add native HTML5
     *   constraint validation.
     * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`. This must be
     *   a valid ISO date string (yyyy-MM-dd). You can also use interpolation inside this attribute
     *   (e.g. `max="{{maxDate | date:'yyyy-MM-dd'}}"`). Note that `max` will also add native HTML5
     *   constraint validation.
     * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO date string
     *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
     * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO date string
     *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
     * @param {string=} required Sets `required` validation error key if the value is not entered.
     * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
     *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
     *    `required` when you want to data-bind to the `required` attribute.
     * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
     *    interaction with the input element.
     *
     * @example
     <example name="date-input-directive" module="dateInputExample">
     <file name="index.html">
       <script>
          angular.module('dateInputExample', [])
            .controller('DateController', ['$scope', function($scope) {
              $scope.example = {
                value: new Date(2013, 9, 22)
              };
            }]);
       </script>
       <form name="myForm" ng-controller="DateController as dateCtrl">
          <label for="exampleInput">Pick a date in 2013:</label>
          <input type="date" id="exampleInput" name="input" ng-model="example.value"
              placeholder="yyyy-MM-dd" min="2013-01-01" max="2013-12-31" required />
          <div role="alert">
            <span class="error" ng-show="myForm.input.$error.required">
                Required!</span>
            <span class="error" ng-show="myForm.input.$error.date">
                Not a valid date!</span>
           </div>
           <tt>value = {{example.value | date: "yyyy-MM-dd"}}</tt><br/>
           <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
           <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
           <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
           <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
       </form>
     </file>
     <file name="protractor.js" type="protractor">
        var value = element(by.binding('example.value | date: "yyyy-MM-dd"'));
        var valid = element(by.binding('myForm.input.$valid'));

        // currently protractor/webdriver does not support
        // sending keys to all known HTML5 input controls
        // for various browsers (see https://github.com/angular/protractor/issues/562).
        function setInput(val) {
          // set the value of the element and force validation.
          var scr = "var ipt = document.getElementById('exampleInput'); " +
          "ipt.value = '" + val + "';" +
          "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
          browser.executeScript(scr);
        }

        it('should initialize to model', function() {
          expect(value.getText()).toContain('2013-10-22');
          expect(valid.getText()).toContain('myForm.input.$valid = true');
        });

        it('should be invalid if empty', function() {
          setInput('');
          expect(value.getText()).toEqual('value =');
          expect(valid.getText()).toContain('myForm.input.$valid = false');
        });

        it('should be invalid if over max', function() {
          setInput('2015-01-01');
          expect(value.getText()).toContain('');
          expect(valid.getText()).toContain('myForm.input.$valid = false');
        });
     </file>
     </example>
     */
  'date': createDateInputType('date', DATE_REGEXP,
         createDateParser(DATE_REGEXP, ['yyyy', 'MM', 'dd']),
         'yyyy-MM-dd'),

   /**
    * @ngdoc input
    * @name input[datetime-local]
    *
    * @description
    * Input with datetime validation and transformation. In browsers that do not yet support
    * the HTML5 date input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
    * local datetime format (yyyy-MM-ddTHH:mm:ss), for example: `2010-12-28T14:57:00`.
    *
    * The model must always be a Date object, otherwise AngularJS will throw an error.
    * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
    *
    * The timezone to be used to read/write the `Date` instance in the model can be defined using
    * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
    *
    * The format of the displayed time can be adjusted with the
    * {@link ng.directive:ngModelOptions#ngModelOptions-arguments ngModelOptions} `timeSecondsFormat`
    * and `timeStripZeroSeconds`.
    *
    * @param {string} ngModel Assignable AngularJS expression to data-bind to.
    * @param {string=} name Property name of the form under which the control is published.
    * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
    *   This must be a valid ISO datetime format (yyyy-MM-ddTHH:mm:ss). You can also use interpolation
    *   inside this attribute (e.g. `min="{{minDatetimeLocal | date:'yyyy-MM-ddTHH:mm:ss'}}"`).
    *   Note that `min` will also add native HTML5 constraint validation.
    * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
    *   This must be a valid ISO datetime format (yyyy-MM-ddTHH:mm:ss). You can also use interpolation
    *   inside this attribute (e.g. `max="{{maxDatetimeLocal | date:'yyyy-MM-ddTHH:mm:ss'}}"`).
    *   Note that `max` will also add native HTML5 constraint validation.
    * @param {(date|string)=} ngMin Sets the `min` validation error key to the Date / ISO datetime string
    *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
    * @param {(date|string)=} ngMax Sets the `max` validation error key to the Date / ISO datetime string
    *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
    * @param {string=} required Sets `required` validation error key if the value is not entered.
    * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
    *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
    *    `required` when you want to data-bind to the `required` attribute.
    * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
    *    interaction with the input element.
    *
    * @example
    <example name="datetimelocal-input-directive" module="dateExample">
    <file name="index.html">
      <script>
        angular.module('dateExample', [])
          .controller('DateController', ['$scope', function($scope) {
            $scope.example = {
              value: new Date(2010, 11, 28, 14, 57)
            };
          }]);
      </script>
      <form name="myForm" ng-controller="DateController as dateCtrl">
        <label for="exampleInput">Pick a date between in 2013:</label>
        <input type="datetime-local" id="exampleInput" name="input" ng-model="example.value"
            placeholder="yyyy-MM-ddTHH:mm:ss" min="2001-01-01T00:00:00" max="2013-12-31T00:00:00" required />
        <div role="alert">
          <span class="error" ng-show="myForm.input.$error.required">
              Required!</span>
          <span class="error" ng-show="myForm.input.$error.datetimelocal">
              Not a valid date!</span>
        </div>
        <tt>value = {{example.value | date: "yyyy-MM-ddTHH:mm:ss"}}</tt><br/>
        <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
        <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
        <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
        <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
      </form>
    </file>
    <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "yyyy-MM-ddTHH:mm:ss"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('2010-12-28T14:57:00');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('2015-01-01T23:59:00');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
    </file>
    </example>
    */
  'datetime-local': createDateInputType('datetimelocal', DATETIMELOCAL_REGEXP,
      createDateParser(DATETIMELOCAL_REGEXP, ['yyyy', 'MM', 'dd', 'HH', 'mm', 'ss', 'sss']),
      'yyyy-MM-ddTHH:mm:ss.sss'),

  /**
   * @ngdoc input
   * @name input[time]
   *
   * @description
   * Input with time validation and transformation. In browsers that do not yet support
   * the HTML5 time input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
   * local time format (HH:mm:ss), for example: `14:57:00`. Model must be a Date object. This binding will always output a
   * Date object to the model of January 1, 1970, or local date `new Date(1970, 0, 1, HH, mm, ss)`.
   *
   * The model must always be a Date object, otherwise AngularJS will throw an error.
   * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
   *
   * The timezone to be used to read/write the `Date` instance in the model can be defined using
   * {@link ng.directive:ngModelOptions#ngModelOptions-arguments ngModelOptions}. By default,
   * this is the timezone of the browser.
   *
   * The format of the displayed time can be adjusted with the
   * {@link ng.directive:ngModelOptions#ngModelOptions-arguments ngModelOptions} `timeSecondsFormat`
   * and `timeStripZeroSeconds`.
   *
   * @param {string} ngModel Assignable AngularJS expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
   *   This must be a valid ISO time format (HH:mm:ss). You can also use interpolation inside this
   *   attribute (e.g. `min="{{minTime | date:'HH:mm:ss'}}"`). Note that `min` will also add
   *   native HTML5 constraint validation.
   * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
   *   This must be a valid ISO time format (HH:mm:ss). You can also use interpolation inside this
   *   attribute (e.g. `max="{{maxTime | date:'HH:mm:ss'}}"`). Note that `max` will also add
   *   native HTML5 constraint validation.
   * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO time string the
   *   `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
   * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO time string the
   *   `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
   <example name="time-input-directive" module="timeExample">
   <file name="index.html">
     <script>
      angular.module('timeExample', [])
        .controller('DateController', ['$scope', function($scope) {
          $scope.example = {
            value: new Date(1970, 0, 1, 14, 57, 0)
          };
        }]);
     </script>
     <form name="myForm" ng-controller="DateController as dateCtrl">
        <label for="exampleInput">Pick a time between 8am and 5pm:</label>
        <input type="time" id="exampleInput" name="input" ng-model="example.value"
            placeholder="HH:mm:ss" min="08:00:00" max="17:00:00" required />
        <div role="alert">
          <span class="error" ng-show="myForm.input.$error.required">
              Required!</span>
          <span class="error" ng-show="myForm.input.$error.time">
              Not a valid date!</span>
        </div>
        <tt>value = {{example.value | date: "HH:mm:ss"}}</tt><br/>
        <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
        <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
        <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
        <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
     </form>
   </file>
   <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "HH:mm:ss"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('14:57:00');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('23:59:00');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
   </file>
   </example>
   */
  'time': createDateInputType('time', TIME_REGEXP,
      createDateParser(TIME_REGEXP, ['HH', 'mm', 'ss', 'sss']),
     'HH:mm:ss.sss'),

   /**
    * @ngdoc input
    * @name input[week]
    *
    * @description
    * Input with week-of-the-year validation and transformation to Date. In browsers that do not yet support
    * the HTML5 week input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
    * week format (yyyy-W##), for example: `2013-W02`.
    *
    * The model must always be a Date object, otherwise AngularJS will throw an error.
    * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
    *
    * The value of the resulting Date object will be set to Thursday at 00:00:00 of the requested week,
    * due to ISO-8601 week numbering standards. Information on ISO's system for numbering the weeks of the
    * year can be found at: https://en.wikipedia.org/wiki/ISO_8601#Week_dates
    *
    * The timezone to be used to read/write the `Date` instance in the model can be defined using
    * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
    *
    * @param {string} ngModel Assignable AngularJS expression to data-bind to.
    * @param {string=} name Property name of the form under which the control is published.
    * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
    *   This must be a valid ISO week format (yyyy-W##). You can also use interpolation inside this
    *   attribute (e.g. `min="{{minWeek | date:'yyyy-Www'}}"`). Note that `min` will also add
    *   native HTML5 constraint validation.
    * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
    *   This must be a valid ISO week format (yyyy-W##). You can also use interpolation inside this
    *   attribute (e.g. `max="{{maxWeek | date:'yyyy-Www'}}"`). Note that `max` will also add
    *   native HTML5 constraint validation.
    * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO week string
    *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
    * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO week string
    *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
    * @param {string=} required Sets `required` validation error key if the value is not entered.
    * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
    *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
    *    `required` when you want to data-bind to the `required` attribute.
    * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
    *    interaction with the input element.
    *
    * @example
    <example name="week-input-directive" module="weekExample">
    <file name="index.html">
      <script>
      angular.module('weekExample', [])
        .controller('DateController', ['$scope', function($scope) {
          $scope.example = {
            value: new Date(2013, 0, 3)
          };
        }]);
      </script>
      <form name="myForm" ng-controller="DateController as dateCtrl">
        <label>Pick a date between in 2013:
          <input id="exampleInput" type="week" name="input" ng-model="example.value"
                 placeholder="YYYY-W##" min="2012-W32"
                 max="2013-W52" required />
        </label>
        <div role="alert">
          <span class="error" ng-show="myForm.input.$error.required">
              Required!</span>
          <span class="error" ng-show="myForm.input.$error.week">
              Not a valid date!</span>
        </div>
        <tt>value = {{example.value | date: "yyyy-Www"}}</tt><br/>
        <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
        <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
        <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
        <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
      </form>
    </file>
    <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "yyyy-Www"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('2013-W01');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('2015-W01');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
    </file>
    </example>
    */
  'week': createDateInputType('week', WEEK_REGEXP, weekParser, 'yyyy-Www'),

  /**
   * @ngdoc input
   * @name input[month]
   *
   * @description
   * Input with month validation and transformation. In browsers that do not yet support
   * the HTML5 month input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
   * month format (yyyy-MM), for example: `2009-01`.
   *
   * The model must always be a Date object, otherwise AngularJS will throw an error.
   * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
   * If the model is not set to the first of the month, the next view to model update will set it
   * to the first of the month.
   *
   * The timezone to be used to read/write the `Date` instance in the model can be defined using
   * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
   *
   * @param {string} ngModel Assignable AngularJS expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
   *   This must be a valid ISO month format (yyyy-MM). You can also use interpolation inside this
   *   attribute (e.g. `min="{{minMonth | date:'yyyy-MM'}}"`). Note that `min` will also add
   *   native HTML5 constraint validation.
   * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
   *   This must be a valid ISO month format (yyyy-MM). You can also use interpolation inside this
   *   attribute (e.g. `max="{{maxMonth | date:'yyyy-MM'}}"`). Note that `max` will also add
   *   native HTML5 constraint validation.
   * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO week string
   *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
   * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO week string
   *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.

   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
   <example name="month-input-directive" module="monthExample">
   <file name="index.html">
     <script>
      angular.module('monthExample', [])
        .controller('DateController', ['$scope', function($scope) {
          $scope.example = {
            value: new Date(2013, 9, 1)
          };
        }]);
     </script>
     <form name="myForm" ng-controller="DateController as dateCtrl">
       <label for="exampleInput">Pick a month in 2013:</label>
       <input id="exampleInput" type="month" name="input" ng-model="example.value"
          placeholder="yyyy-MM" min="2013-01" max="2013-12" required />
       <div role="alert">
         <span class="error" ng-show="myForm.input.$error.required">
            Required!</span>
         <span class="error" ng-show="myForm.input.$error.month">
            Not a valid month!</span>
       </div>
       <tt>value = {{example.value | date: "yyyy-MM"}}</tt><br/>
       <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
       <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
       <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
       <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
     </form>
   </file>
   <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "yyyy-MM"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('2013-10');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('2015-01');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
   </file>
   </example>
   */
  'month': createDateInputType('month', MONTH_REGEXP,
     createDateParser(MONTH_REGEXP, ['yyyy', 'MM']),
     'yyyy-MM'),

  /**
   * @ngdoc input
   * @name input[number]
   *
   * @description
   * Text input with number validation and transformation. Sets the `number` validation
   * error if not a valid number.
   *
   * <div class="alert alert-warning">
   * The model must always be of type `number` otherwise AngularJS will throw an error.
   * Be aware that a string containing a number is not enough. See the {@link ngModel:numfmt}
   * error docs for more information and an example of how to convert your model if necessary.
   * </div>
   *
   *
   *
   * @knownIssue
   *
   * ### HTML5 constraint validation and `allowInvalid`
   *
   * In browsers that follow the
   * [HTML5 specification](https://html.spec.whatwg.org/multipage/forms.html#number-state-%28type=number%29),
   * `input[number]` does not work as expected with {@link ngModelOptions `ngModelOptions.allowInvalid`}.
   * If a non-number is entered in the input, the browser will report the value as an empty string,
   * which means the view / model values in `ngModel` and subsequently the scope value
   * will also be an empty string.
   *
   * @knownIssue
   *
   * ### Large numbers and `step` validation
   *
   * The `step` validation will not work correctly for very large numbers (e.g. 9999999999) due to
   * Javascript's arithmetic limitations. If you need to handle large numbers, purpose-built
   * libraries (e.g. https://github.com/MikeMcl/big.js/), can be included into AngularJS by
   * {@link guide/forms#modifying-built-in-validators overwriting the validators}
   * for `number` and / or `step`, or by {@link guide/forms#custom-validation applying custom validators}
   * to an `input[text]` element. The source for `input[number]` type can be used as a starting
   * point for both implementations.
   *
   * @param {string} ngModel Assignable AngularJS expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
   *    Can be interpolated.
   * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
   *    Can be interpolated.
   * @param {string=} ngMin Like `min`, sets the `min` validation error key if the value entered is less than `ngMin`,
   *    but does not trigger HTML5 native validation. Takes an expression.
   * @param {string=} ngMax Like `max`, sets the `max` validation error key if the value entered is greater than `ngMax`,
   *    but does not trigger HTML5 native validation. Takes an expression.
   * @param {string=} step Sets the `step` validation error key if the value entered does not fit the `step` constraint.
   *    Can be interpolated.
   * @param {string=} ngStep Like `step`, sets the `step` validation error key if the value entered does not fit the `ngStep` constraint,
   *    but does not trigger HTML5 native validation. Takes an expression.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="number-input-directive" module="numberExample">
        <file name="index.html">
         <script>
           angular.module('numberExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.example = {
                 value: 12
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>Number:
             <input type="number" name="input" ng-model="example.value"
                    min="0" max="99" required>
          </label>
           <div role="alert">
             <span class="error" ng-show="myForm.input.$error.required">
               Required!</span>
             <span class="error" ng-show="myForm.input.$error.number">
               Not valid number!</span>
           </div>
           <tt>value = {{example.value}}</tt><br/>
           <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
           <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
           <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
           <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          var value = element(by.binding('example.value'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('example.value'));

          it('should initialize to model', function() {
            expect(value.getText()).toContain('12');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');
            expect(value.getText()).toEqual('value =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if over max', function() {
            input.clear();
            input.sendKeys('123');
            expect(value.getText()).toEqual('value =');
            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'number': numberInputType,


  /**
   * @ngdoc input
   * @name input[url]
   *
   * @description
   * Text input with URL validation. Sets the `url` validation error key if the content is not a
   * valid URL.
   *
   * <div class="alert alert-warning">
   * **Note:** `input[url]` uses a regex to validate urls that is derived from the regex
   * used in Chromium. If you need stricter validation, you can use `ng-pattern` or modify
   * the built-in validators (see the {@link guide/forms Forms guide})
   * </div>
   *
   * @param {string} ngModel Assignable AngularJS expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="url-input-directive" module="urlExample">
        <file name="index.html">
         <script>
           angular.module('urlExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.url = {
                 text: 'http://google.com'
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>URL:
             <input type="url" name="input" ng-model="url.text" required>
           <label>
           <div role="alert">
             <span class="error" ng-show="myForm.input.$error.required">
               Required!</span>
             <span class="error" ng-show="myForm.input.$error.url">
               Not valid url!</span>
           </div>
           <tt>text = {{url.text}}</tt><br/>
           <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
           <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
           <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
           <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
           <tt>myForm.$error.url = {{!!myForm.$error.url}}</tt><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          var text = element(by.binding('url.text'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('url.text'));

          it('should initialize to model', function() {
            expect(text.getText()).toContain('http://google.com');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');

            expect(text.getText()).toEqual('text =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if not url', function() {
            input.clear();
            input.sendKeys('box');

            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'url': urlInputType,


  /**
   * @ngdoc input
   * @name input[email]
   *
   * @description
   * Text input with email validation. Sets the `email` validation error key if not a valid email
   * address.
   *
   * <div class="alert alert-warning">
   * **Note:** `input[email]` uses a regex to validate email addresses that is derived from the regex
   * used in Chromium, which may not fulfill your app's requirements.
   * If you need stricter (e.g. requiring a top-level domain), or more relaxed validation
   * (e.g. allowing IPv6 address literals) you can use `ng-pattern` or
   * modify the built-in validators (see the {@link guide/forms Forms guide}).
   * </div>
   *
   * @param {string} ngModel Assignable AngularJS expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="email-input-directive" module="emailExample">
        <file name="index.html">
         <script>
           angular.module('emailExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.email = {
                 text: 'me@example.com'
               };
             }]);
         </script>
           <form name="myForm" ng-controller="ExampleController">
             <label>Email:
               <input type="email" name="input" ng-model="email.text" required>
             </label>
             <div role="alert">
               <span class="error" ng-show="myForm.input.$error.required">
                 Required!</span>
               <span class="error" ng-show="myForm.input.$error.email">
                 Not valid email!</span>
             </div>
             <tt>text = {{email.text}}</tt><br/>
             <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
             <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
             <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
             <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
             <tt>myForm.$error.email = {{!!myForm.$error.email}}</tt><br/>
           </form>
         </file>
        <file name="protractor.js" type="protractor">
          var text = element(by.binding('email.text'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('email.text'));

          it('should initialize to model', function() {
            expect(text.getText()).toContain('me@example.com');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');
            expect(text.getText()).toEqual('text =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if not email', function() {
            input.clear();
            input.sendKeys('xxx');

            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'email': emailInputType,


  /**
   * @ngdoc input
   * @name input[radio]
   *
   * @description
   * HTML radio button.
   *
   * **Note:**<br>
   * All inputs controlled by {@link ngModel ngModel} (including those of type `radio`) will use the
   * value of their `name` attribute to determine the property under which their
   * {@link ngModel.NgModelController NgModelController} will be published on the parent
   * {@link form.FormController FormController}. Thus, if you use the same `name` for multiple
   * inputs of a form (e.g. a group of radio inputs), only _one_ `NgModelController` will be
   * published on the parent `FormController` under that name. The rest of the controllers will
   * continue to work as expected, but you won't be able to access them as properties on the parent
   * `FormController`.
   *
   * <div class="alert alert-info">
   *   <p>
   *     In plain HTML forms, the `name` attribute is used to identify groups of radio inputs, so
   *     that the browser can manage their state (checked/unchecked) based on the state of other
   *     inputs in the same group.
   *   </p>
   *   <p>
   *     In AngularJS forms, this is not necessary. The input's state will be updated based on the
   *     value of the underlying model data.
   *   </p>
   * </div>
   *
   * <div class="alert alert-success">
   *   If you omit the `name` attribute on a radio input, `ngModel` will automatically assign it a
   *   unique name.
   * </div>
   *
   * @param {string} ngModel Assignable AngularJS expression to data-bind to.
   * @param {string} value The value to which the `ngModel` expression should be set when selected.
   *    Note that `value` only supports `string` values, i.e. the scope model needs to be a string,
   *    too. Use `ngValue` if you need complex models (`number`, `object`, ...).
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
   *    interaction with the input element.
   * @param {string} ngValue AngularJS expression to which `ngModel` will be be set when the radio
   *    is selected. Should be used instead of the `value` attribute if you need
   *    a non-string `ngModel` (`boolean`, `array`, ...).
   *
   * @example
      <example name="radio-input-directive" module="radioExample">
        <file name="index.html">
         <script>
           angular.module('radioExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.color = {
                 name: 'blue'
               };
               $scope.specialValue = {
                 "id": "12345",
                 "value": "green"
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>
             <input type="radio" ng-model="color.name" value="red">
             Red
           </label><br/>
           <label>
             <input type="radio" ng-model="color.name" ng-value="specialValue">
             Green
           </label><br/>
           <label>
             <input type="radio" ng-model="color.name" value="blue">
             Blue
           </label><br/>
           <tt>color = {{color.name | json}}</tt><br/>
          </form>
          Note that `ng-value="specialValue"` sets radio item's value to be the value of `$scope.specialValue`.
        </file>
        <file name="protractor.js" type="protractor">
          it('should change state', function() {
            var inputs = element.all(by.model('color.name'));
            var color = element(by.binding('color.name'));

            expect(color.getText()).toContain('blue');

            inputs.get(0).click();
            expect(color.getText()).toContain('red');

            inputs.get(1).click();
            expect(color.getText()).toContain('green');
          });
        </file>
      </example>
   */
  'radio': radioInputType,

  /**
   * @ngdoc input
   * @name input[range]
   *
   * @description
   * Native range input with validation and transformation.
   *
   * The model for the range input must always be a `Number`.
   *
   * IE9 and other browsers that do not support the `range` type fall back
   * to a text input without any default values for `min`, `max` and `step`. Model binding,
   * validation and number parsing are nevertheless supported.
   *
   * Browsers that support range (latest Chrome, Safari, Firefox, Edge) treat `input[range]`
   * in a way that never allows the input to hold an invalid value. That means:
   * - any non-numerical value is set to `(max + min) / 2`.
   * - any numerical value that is less than the current min val, or greater than the current max val
   * is set to the min / max val respectively.
   * - additionally, the current `step` is respected, so the nearest value that satisfies a step
   * is used.
   *
   * See the [HTML Spec on input[type=range]](https://www.w3.org/TR/html5/forms.html#range-state-(type=range))
   * for more info.
   *
   * This has the following consequences for AngularJS:
   *
   * Since the element value should always reflect the current model value, a range input
   * will set the bound ngModel expression to the value that the browser has set for the
   * input element. For example, in the following input `<input type="range" ng-model="model.value">`,
   * if the application sets `model.value = null`, the browser will set the input to `'50'`.
   * AngularJS will then set the model to `50`, to prevent input and model value being out of sync.
   *
   * That means the model for range will immediately be set to `50` after `ngModel` has been
   * initialized. It also means a range input can never have the required error.
   *
   * This does not only affect changes to the model value, but also to the values of the `min`,
   * `max`, and `step` attributes. When these change in a way that will cause the browser to modify
   * the input value, AngularJS will also update the model value.
   *
   * Automatic value adjustment also means that a range input element can never have the `required`,
   * `min`, or `max` errors.
   *
   * However, `step` is currently only fully implemented by Firefox. Other browsers have problems
   * when the step value changes dynamically - they do not adjust the element value correctly, but
   * instead may set the `stepMismatch` error. If that's the case, the AngularJS will set the `step`
   * error on the input, and set the model to `undefined`.
   *
   * Note that `input[range]` is not compatible with`ngMax`, `ngMin`, and `ngStep`, because they do
   * not set the `min` and `max` attributes, which means that the browser won't automatically adjust
   * the input value based on their values, and will always assume min = 0, max = 100, and step = 1.
   *
   * @param {string}  ngModel Assignable AngularJS expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation to ensure that the value entered is greater
   *                  than `min`. Can be interpolated.
   * @param {string=} max Sets the `max` validation to ensure that the value entered is less than `max`.
   *                  Can be interpolated.
   * @param {string=} step Sets the `step` validation to ensure that the value entered matches the `step`
   *                  Can be interpolated.
   * @param {expression=} ngChange AngularJS expression to be executed when the ngModel value changes due
   *                      to user interaction with the input element.
   * @param {expression=} ngChecked If the expression is truthy, then the `checked` attribute will be set on the
   *                      element. **Note** : `ngChecked` should not be used alongside `ngModel`.
   *                      Checkout {@link ng.directive:ngChecked ngChecked} for usage.
   *
   * @example
      <example name="range-input-directive" module="rangeExample">
        <file name="index.html">
          <script>
            angular.module('rangeExample', [])
              .controller('ExampleController', ['$scope', function($scope) {
                $scope.value = 75;
                $scope.min = 10;
                $scope.max = 90;
              }]);
          </script>
          <form name="myForm" ng-controller="ExampleController">

            Model as range: <input type="range" name="range" ng-model="value" min="{{min}}"  max="{{max}}">
            <hr>
            Model as number: <input type="number" ng-model="value"><br>
            Min: <input type="number" ng-model="min"><br>
            Max: <input type="number" ng-model="max"><br>
            value = <code>{{value}}</code><br/>
            myForm.range.$valid = <code>{{myForm.range.$valid}}</code><br/>
            myForm.range.$error = <code>{{myForm.range.$error}}</code>
          </form>
        </file>
      </example>

   * ## Range Input with ngMin & ngMax attributes

   * @example
      <example name="range-input-directive-ng" module="rangeExample">
        <file name="index.html">
          <script>
            angular.module('rangeExample', [])
              .controller('ExampleController', ['$scope', function($scope) {
                $scope.value = 75;
                $scope.min = 10;
                $scope.max = 90;
              }]);
          </script>
          <form name="myForm" ng-controller="ExampleController">
            Model as range: <input type="range" name="range" ng-model="value" ng-min="min" ng-max="max">
            <hr>
            Model as number: <input type="number" ng-model="value"><br>
            Min: <input type="number" ng-model="min"><br>
            Max: <input type="number" ng-model="max"><br>
            value = <code>{{value}}</code><br/>
            myForm.range.$valid = <code>{{myForm.range.$valid}}</code><br/>
            myForm.range.$error = <code>{{myForm.range.$error}}</code>
          </form>
        </file>
      </example>

   */
  'range': rangeInputType,

  /**
   * @ngdoc input
   * @name input[checkbox]
   *
   * @description
   * HTML checkbox.
   *
   * @param {string} ngModel Assignable AngularJS expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {expression=} ngTrueValue The value to which the expression should be set when selected.
   * @param {expression=} ngFalseValue The value to which the expression should be set when not selected.
   * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="checkbox-input-directive" module="checkboxExample">
        <file name="index.html">
         <script>
           angular.module('checkboxExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.checkboxModel = {
                value1 : true,
                value2 : 'YES'
              };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>Value1:
             <input type="checkbox" ng-model="checkboxModel.value1">
           </label><br/>
           <label>Value2:
             <input type="checkbox" ng-model="checkboxModel.value2"
                    ng-true-value="'YES'" ng-false-value="'NO'">
            </label><br/>
           <tt>value1 = {{checkboxModel.value1}}</tt><br/>
           <tt>value2 = {{checkboxModel.value2}}</tt><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          it('should change state', function() {
            var value1 = element(by.binding('checkboxModel.value1'));
            var value2 = element(by.binding('checkboxModel.value2'));

            expect(value1.getText()).toContain('true');
            expect(value2.getText()).toContain('YES');

            element(by.model('checkboxModel.value1')).click();
            element(by.model('checkboxModel.value2')).click();

            expect(value1.getText()).toContain('false');
            expect(value2.getText()).toContain('NO');
          });
        </file>
      </example>
   */
  'checkbox': checkboxInputType,

  'hidden': noop,
  'button': noop,
  'submit': noop,
  'reset': noop,
  'file': noop
};

function stringBasedInputType(ctrl) {
  ctrl.$formatters.push(function(value) {
    return ctrl.$isEmpty(value) ? value : value.toString();
  });
}

function textInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
  stringBasedInputType(ctrl);
}

function baseInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  var type = lowercase(element[0].type);

  // In composition mode, users are still inputting intermediate text buffer,
  // hold the listener until composition is done.
  // More about composition events: https://developer.mozilla.org/en-US/docs/Web/API/CompositionEvent
  if (!$sniffer.android) {
    var composing = false;

    element.on('compositionstart', function() {
      composing = true;
    });

    // Support: IE9+
    element.on('compositionupdate', function(ev) {
      // End composition when ev.data is empty string on 'compositionupdate' event.
      // When the input de-focusses (e.g. by clicking away), IE triggers 'compositionupdate'
      // instead of 'compositionend'.
      if (isUndefined(ev.data) || ev.data === '') {
        composing = false;
      }
    });

    element.on('compositionend', function() {
      composing = false;
      listener();
    });
  }

  var timeout;

  var listener = function(ev) {
    if (timeout) {
      $browser.defer.cancel(timeout);
      timeout = null;
    }
    if (composing) return;
    var value = element.val(),
        event = ev && ev.type;

    // By default we will trim the value
    // If the attribute ng-trim exists we will avoid trimming
    // If input type is 'password', the value is never trimmed
    if (type !== 'password' && (!attr.ngTrim || attr.ngTrim !== 'false')) {
      value = trim(value);
    }

    // If a control is suffering from bad input (due to native validators), browsers discard its
    // value, so it may be necessary to revalidate (by calling $setViewValue again) even if the
    // control's value is the same empty value twice in a row.
    if (ctrl.$viewValue !== value || (value === '' && ctrl.$$hasNativeValidators)) {
      ctrl.$setViewValue(value, event);
    }
  };

  // if the browser does support "input" event, we are fine - except on IE9 which doesn't fire the
  // input event on backspace, delete or cut
  if ($sniffer.hasEvent('input')) {
    element.on('input', listener);
  } else {
    var deferListener = function(ev, input, origValue) {
      if (!timeout) {
        timeout = $browser.defer(function() {
          timeout = null;
          if (!input || input.value !== origValue) {
            listener(ev);
          }
        });
      }
    };

    element.on('keydown', /** @this */ function(event) {
      var key = event.keyCode;

      // ignore
      //    command            modifiers                   arrows
      if (key === 91 || (15 < key && key < 19) || (37 <= key && key <= 40)) return;

      deferListener(event, this, this.value);
    });

    // if user modifies input value using context menu in IE, we need "paste", "cut" and "drop" events to catch it
    if ($sniffer.hasEvent('paste')) {
      element.on('paste cut drop', deferListener);
    }
  }

  // if user paste into input using mouse on older browser
  // or form autocomplete on newer browser, we need "change" event to catch it
  element.on('change', listener);

  // Some native input types (date-family) have the ability to change validity without
  // firing any input/change events.
  // For these event types, when native validators are present and the browser supports the type,
  // check for validity changes on various DOM events.
  if (PARTIAL_VALIDATION_TYPES[type] && ctrl.$$hasNativeValidators && type === attr.type) {
    element.on(PARTIAL_VALIDATION_EVENTS, /** @this */ function(ev) {
      if (!timeout) {
        var validity = this[VALIDITY_STATE_PROPERTY];
        var origBadInput = validity.badInput;
        var origTypeMismatch = validity.typeMismatch;
        timeout = $browser.defer(function() {
          timeout = null;
          if (validity.badInput !== origBadInput || validity.typeMismatch !== origTypeMismatch) {
            listener(ev);
          }
        });
      }
    });
  }

  ctrl.$render = function() {
    // Workaround for Firefox validation #12102.
    var value = ctrl.$isEmpty(ctrl.$viewValue) ? '' : ctrl.$viewValue;
    if (element.val() !== value) {
      element.val(value);
    }
  };
}

function weekParser(isoWeek, existingDate) {
  if (isDate(isoWeek)) {
    return isoWeek;
  }

  if (isString(isoWeek)) {
    WEEK_REGEXP.lastIndex = 0;
    var parts = WEEK_REGEXP.exec(isoWeek);
    if (parts) {
      var year = +parts[1],
          week = +parts[2],
          hours = 0,
          minutes = 0,
          seconds = 0,
          milliseconds = 0,
          firstThurs = getFirstThursdayOfYear(year),
          addDays = (week - 1) * 7;

      if (existingDate) {
        hours = existingDate.getHours();
        minutes = existingDate.getMinutes();
        seconds = existingDate.getSeconds();
        milliseconds = existingDate.getMilliseconds();
      }

      return new Date(year, 0, firstThurs.getDate() + addDays, hours, minutes, seconds, milliseconds);
    }
  }

  return NaN;
}

function createDateParser(regexp, mapping) {
  return function(iso, previousDate) {
    var parts, map;

    if (isDate(iso)) {
      return iso;
    }

    if (isString(iso)) {
      // When a date is JSON'ified to wraps itself inside of an extra
      // set of double quotes. This makes the date parsing code unable
      // to match the date string and parse it as a date.
      if (iso.charAt(0) === '"' && iso.charAt(iso.length - 1) === '"') {
        iso = iso.substring(1, iso.length - 1);
      }
      if (ISO_DATE_REGEXP.test(iso)) {
        return new Date(iso);
      }
      regexp.lastIndex = 0;
      parts = regexp.exec(iso);

      if (parts) {
        parts.shift();
        if (previousDate) {
          map = {
            yyyy: previousDate.getFullYear(),
            MM: previousDate.getMonth() + 1,
            dd: previousDate.getDate(),
            HH: previousDate.getHours(),
            mm: previousDate.getMinutes(),
            ss: previousDate.getSeconds(),
            sss: previousDate.getMilliseconds() / 1000
          };
        } else {
          map = { yyyy: 1970, MM: 1, dd: 1, HH: 0, mm: 0, ss: 0, sss: 0 };
        }

        forEach(parts, function(part, index) {
          if (index < mapping.length) {
            map[mapping[index]] = +part;
          }
        });

        var date = new Date(map.yyyy, map.MM - 1, map.dd, map.HH, map.mm, map.ss || 0, map.sss * 1000 || 0);
        if (map.yyyy < 100) {
          // In the constructor, 2-digit years map to 1900-1999.
          // Use `setFullYear()` to set the correct year.
          date.setFullYear(map.yyyy);
        }

        return date;
      }
    }

    return NaN;
  };
}

function createDateInputType(type, regexp, parseDate, format) {
  return function dynamicDateInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter, $parse) {
    badInputChecker(scope, element, attr, ctrl, type);
    baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

    var isTimeType = type === 'time' || type === 'datetimelocal';
    var previousDate;
    var previousTimezone;

    ctrl.$parsers.push(function(value) {
      if (ctrl.$isEmpty(value)) return null;

      if (regexp.test(value)) {
        // Note: We cannot read ctrl.$modelValue, as there might be a different
        // parser/formatter in the processing chain so that the model
        // contains some different data format!
        return parseDateAndConvertTimeZoneToLocal(value, previousDate);
      }
      ctrl.$$parserName = type;
      return undefined;
    });

    ctrl.$formatters.push(function(value) {
      if (value && !isDate(value)) {
        throw ngModelMinErr('datefmt', 'Expected `{0}` to be a date', value);
      }
      if (isValidDate(value)) {
        previousDate = value;
        var timezone = ctrl.$options.getOption('timezone');

        if (timezone) {
          previousTimezone = timezone;
          previousDate = convertTimezoneToLocal(previousDate, timezone, true);
        }

        return formatter(value, timezone);
      } else {
        previousDate = null;
        previousTimezone = null;
        return '';
      }
    });

    if (isDefined(attr.min) || attr.ngMin) {
      var minVal = attr.min || $parse(attr.ngMin)(scope);
      var parsedMinVal = parseObservedDateValue(minVal);

      ctrl.$validators.min = function(value) {
        return !isValidDate(value) || isUndefined(parsedMinVal) || parseDate(value) >= parsedMinVal;
      };
      attr.$observe('min', function(val) {
        if (val !== minVal) {
          parsedMinVal = parseObservedDateValue(val);
          minVal = val;
          ctrl.$validate();
        }
      });
    }

    if (isDefined(attr.max) || attr.ngMax) {
      var maxVal = attr.max || $parse(attr.ngMax)(scope);
      var parsedMaxVal = parseObservedDateValue(maxVal);

      ctrl.$validators.max = function(value) {
        return !isValidDate(value) || isUndefined(parsedMaxVal) || parseDate(value) <= parsedMaxVal;
      };
      attr.$observe('max', function(val) {
        if (val !== maxVal) {
          parsedMaxVal = parseObservedDateValue(val);
          maxVal = val;
          ctrl.$validate();
        }
      });
    }

    function isValidDate(value) {
      // Invalid Date: getTime() returns NaN
      return value && !(value.getTime && value.getTime() !== value.getTime());
    }

    function parseObservedDateValue(val) {
      return isDefined(val) && !isDate(val) ? parseDateAndConvertTimeZoneToLocal(val) || undefined : val;
    }

    function parseDateAndConvertTimeZoneToLocal(value, previousDate) {
      var timezone = ctrl.$options.getOption('timezone');

      if (previousTimezone && previousTimezone !== timezone) {
        // If the timezone has changed, adjust the previousDate to the default timezone
        // so that the new date is converted with the correct timezone offset
        previousDate = addDateMinutes(previousDate, timezoneToOffset(previousTimezone));
      }

      var parsedDate = parseDate(value, previousDate);

      if (!isNaN(parsedDate) && timezone) {
        parsedDate = convertTimezoneToLocal(parsedDate, timezone);
      }
      return parsedDate;
    }

    function formatter(value, timezone) {
      var targetFormat = format;

      if (isTimeType && isString(ctrl.$options.getOption('timeSecondsFormat'))) {
        targetFormat = format
          .replace('ss.sss', ctrl.$options.getOption('timeSecondsFormat'))
          .replace(/:$/, '');
      }

      var formatted =  $filter('date')(value, targetFormat, timezone);

      if (isTimeType && ctrl.$options.getOption('timeStripZeroSeconds')) {
        formatted = formatted.replace(/(?::00)?(?:\.000)?$/, '');
      }

      return formatted;
    }
  };
}

function badInputChecker(scope, element, attr, ctrl, parserName) {
  var node = element[0];
  var nativeValidation = ctrl.$$hasNativeValidators = isObject(node.validity);
  if (nativeValidation) {
    ctrl.$parsers.push(function(value) {
      var validity = element.prop(VALIDITY_STATE_PROPERTY) || {};
      if (validity.badInput || validity.typeMismatch) {
        ctrl.$$parserName = parserName;
        return undefined;
      }

      return value;
    });
  }
}

function numberFormatterParser(ctrl) {
  ctrl.$parsers.push(function(value) {
    if (ctrl.$isEmpty(value))      return null;
    if (NUMBER_REGEXP.test(value)) return parseFloat(value);

    ctrl.$$parserName = 'number';
    return undefined;
  });

  ctrl.$formatters.push(function(value) {
    if (!ctrl.$isEmpty(value)) {
      if (!isNumber(value)) {
        throw ngModelMinErr('numfmt', 'Expected `{0}` to be a number', value);
      }
      value = value.toString();
    }
    return value;
  });
}

function parseNumberAttrVal(val) {
  if (isDefined(val) && !isNumber(val)) {
    val = parseFloat(val);
  }
  return !isNumberNaN(val) ? val : undefined;
}

function isNumberInteger(num) {
  // See http://stackoverflow.com/questions/14636536/how-to-check-if-a-variable-is-an-integer-in-javascript#14794066
  // (minus the assumption that `num` is a number)

  // eslint-disable-next-line no-bitwise
  return (num | 0) === num;
}

function countDecimals(num) {
  var numString = num.toString();
  var decimalSymbolIndex = numString.indexOf('.');

  if (decimalSymbolIndex === -1) {
    if (-1 < num && num < 1) {
      // It may be in the exponential notation format (`1e-X`)
      var match = /e-(\d+)$/.exec(numString);

      if (match) {
        return Number(match[1]);
      }
    }

    return 0;
  }

  return numString.length - decimalSymbolIndex - 1;
}

function isValidForStep(viewValue, stepBase, step) {
  // At this point `stepBase` and `step` are expected to be non-NaN values
  // and `viewValue` is expected to be a valid stringified number.
  var value = Number(viewValue);

  var isNonIntegerValue = !isNumberInteger(value);
  var isNonIntegerStepBase = !isNumberInteger(stepBase);
  var isNonIntegerStep = !isNumberInteger(step);

  // Due to limitations in Floating Point Arithmetic (e.g. `0.3 - 0.2 !== 0.1` or
  // `0.5 % 0.1 !== 0`), we need to convert all numbers to integers.
  if (isNonIntegerValue || isNonIntegerStepBase || isNonIntegerStep) {
    var valueDecimals = isNonIntegerValue ? countDecimals(value) : 0;
    var stepBaseDecimals = isNonIntegerStepBase ? countDecimals(stepBase) : 0;
    var stepDecimals = isNonIntegerStep ? countDecimals(step) : 0;

    var decimalCount = Math.max(valueDecimals, stepBaseDecimals, stepDecimals);
    var multiplier = Math.pow(10, decimalCount);

    value = value * multiplier;
    stepBase = stepBase * multiplier;
    step = step * multiplier;

    if (isNonIntegerValue) value = Math.round(value);
    if (isNonIntegerStepBase) stepBase = Math.round(stepBase);
    if (isNonIntegerStep) step = Math.round(step);
  }

  return (value - stepBase) % step === 0;
}

function numberInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter, $parse) {
  badInputChecker(scope, element, attr, ctrl, 'number');
  numberFormatterParser(ctrl);
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

  var parsedMinVal;

  if (isDefined(attr.min) || attr.ngMin) {
    var minVal = attr.min || $parse(attr.ngMin)(scope);
    parsedMinVal = parseNumberAttrVal(minVal);

    ctrl.$validators.min = function(modelValue, viewValue) {
      return ctrl.$isEmpty(viewValue) || isUndefined(parsedMinVal) || viewValue >= parsedMinVal;
    };

    attr.$observe('min', function(val) {
      if (val !== minVal) {
        parsedMinVal = parseNumberAttrVal(val);
        minVal = val;
        // TODO(matsko): implement validateLater to reduce number of validations
        ctrl.$validate();
      }
    });
  }

  if (isDefined(attr.max) || attr.ngMax) {
    var maxVal = attr.max || $parse(attr.ngMax)(scope);
    var parsedMaxVal = parseNumberAttrVal(maxVal);

    ctrl.$validators.max = function(modelValue, viewValue) {
      return ctrl.$isEmpty(viewValue) || isUndefined(parsedMaxVal) || viewValue <= parsedMaxVal;
    };

    attr.$observe('max', function(val) {
      if (val !== maxVal) {
        parsedMaxVal = parseNumberAttrVal(val);
        maxVal = val;
        // TODO(matsko): implement validateLater to reduce number of validations
        ctrl.$validate();
      }
    });
  }

  if (isDefined(attr.step) || attr.ngStep) {
    var stepVal = attr.step || $parse(attr.ngStep)(scope);
    var parsedStepVal = parseNumberAttrVal(stepVal);

    ctrl.$validators.step = function(modelValue, viewValue) {
      return ctrl.$isEmpty(viewValue) || isUndefined(parsedStepVal) ||
        isValidForStep(viewValue, parsedMinVal || 0, parsedStepVal);
    };

    attr.$observe('step', function(val) {
      // TODO(matsko): implement validateLater to reduce number of validations
      if (val !== stepVal) {
        parsedStepVal = parseNumberAttrVal(val);
        stepVal = val;
        ctrl.$validate();
      }

    });

  }
}

function rangeInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  badInputChecker(scope, element, attr, ctrl, 'range');
  numberFormatterParser(ctrl);
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

  var supportsRange = ctrl.$$hasNativeValidators && element[0].type === 'range',
      minVal = supportsRange ? 0 : undefined,
      maxVal = supportsRange ? 100 : undefined,
      stepVal = supportsRange ? 1 : undefined,
      validity = element[0].validity,
      hasMinAttr = isDefined(attr.min),
      hasMaxAttr = isDefined(attr.max),
      hasStepAttr = isDefined(attr.step);

  var originalRender = ctrl.$render;

  ctrl.$render = supportsRange && isDefined(validity.rangeUnderflow) && isDefined(validity.rangeOverflow) ?
    //Browsers that implement range will set these values automatically, but reading the adjusted values after
    //$render would cause the min / max validators to be applied with the wrong value
    function rangeRender() {
      originalRender();
      ctrl.$setViewValue(element.val());
    } :
    originalRender;

  if (hasMinAttr) {
    minVal = parseNumberAttrVal(attr.min);

    ctrl.$validators.min = supportsRange ?
      // Since all browsers set the input to a valid value, we don't need to check validity
      function noopMinValidator() { return true; } :
      // non-support browsers validate the min val
      function minValidator(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || isUndefined(minVal) || viewValue >= minVal;
      };

    setInitialValueAndObserver('min', minChange);
  }

  if (hasMaxAttr) {
    maxVal = parseNumberAttrVal(attr.max);

    ctrl.$validators.max = supportsRange ?
      // Since all browsers set the input to a valid value, we don't need to check validity
      function noopMaxValidator() { return true; } :
      // non-support browsers validate the max val
      function maxValidator(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || isUndefined(maxVal) || viewValue <= maxVal;
      };

    setInitialValueAndObserver('max', maxChange);
  }

  if (hasStepAttr) {
    stepVal = parseNumberAttrVal(attr.step);

    ctrl.$validators.step = supportsRange ?
      function nativeStepValidator() {
        // Currently, only FF implements the spec on step change correctly (i.e. adjusting the
        // input element value to a valid value). It's possible that other browsers set the stepMismatch
        // validity error instead, so we can at least report an error in that case.
        return !validity.stepMismatch;
      } :
      // ngStep doesn't set the setp attr, so the browser doesn't adjust the input value as setting step would
      function stepValidator(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || isUndefined(stepVal) ||
               isValidForStep(viewValue, minVal || 0, stepVal);
      };

    setInitialValueAndObserver('step', stepChange);
  }

  function setInitialValueAndObserver(htmlAttrName, changeFn) {
    // interpolated attributes set the attribute value only after a digest, but we need the
    // attribute value when the input is first rendered, so that the browser can adjust the
    // input value based on the min/max value
    element.attr(htmlAttrName, attr[htmlAttrName]);
    var oldVal = attr[htmlAttrName];
    attr.$observe(htmlAttrName, function wrappedObserver(val) {
      if (val !== oldVal) {
        oldVal = val;
        changeFn(val);
      }
    });
  }

  function minChange(val) {
    minVal = parseNumberAttrVal(val);
    // ignore changes before model is initialized
    if (isNumberNaN(ctrl.$modelValue)) {
      return;
    }

    if (supportsRange) {
      var elVal = element.val();
      // IE11 doesn't set the el val correctly if the minVal is greater than the element value
      if (minVal > elVal) {
        elVal = minVal;
        element.val(elVal);
      }
      ctrl.$setViewValue(elVal);
    } else {
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    }
  }

  function maxChange(val) {
    maxVal = parseNumberAttrVal(val);
    // ignore changes before model is initialized
    if (isNumberNaN(ctrl.$modelValue)) {
      return;
    }

    if (supportsRange) {
      var elVal = element.val();
      // IE11 doesn't set the el val correctly if the maxVal is less than the element value
      if (maxVal < elVal) {
        element.val(maxVal);
        // IE11 and Chrome don't set the value to the minVal when max < min
        elVal = maxVal < minVal ? minVal : maxVal;
      }
      ctrl.$setViewValue(elVal);
    } else {
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    }
  }

  function stepChange(val) {
    stepVal = parseNumberAttrVal(val);
    // ignore changes before model is initialized
    if (isNumberNaN(ctrl.$modelValue)) {
      return;
    }

    // Some browsers don't adjust the input value correctly, but set the stepMismatch error
    if (!supportsRange) {
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    } else if (ctrl.$viewValue !== element.val()) {
      ctrl.$setViewValue(element.val());
    }
  }
}

function urlInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  // Note: no badInputChecker here by purpose as `url` is only a validation
  // in browsers, i.e. we can always read out input.value even if it is not valid!
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
  stringBasedInputType(ctrl);

  ctrl.$validators.url = function(modelValue, viewValue) {
    var value = modelValue || viewValue;
    return ctrl.$isEmpty(value) || URL_REGEXP.test(value);
  };
}

function emailInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  // Note: no badInputChecker here by purpose as `url` is only a validation
  // in browsers, i.e. we can always read out input.value even if it is not valid!
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
  stringBasedInputType(ctrl);

  ctrl.$validators.email = function(modelValue, viewValue) {
    var value = modelValue || viewValue;
    return ctrl.$isEmpty(value) || EMAIL_REGEXP.test(value);
  };
}

function radioInputType(scope, element, attr, ctrl) {
  var doTrim = !attr.ngTrim || trim(attr.ngTrim) !== 'false';
  // make the name unique, if not defined
  if (isUndefined(attr.name)) {
    element.attr('name', nextUid());
  }

  var listener = function(ev) {
    var value;
    if (element[0].checked) {
      value = attr.value;
      if (doTrim) {
        value = trim(value);
      }
      ctrl.$setViewValue(value, ev && ev.type);
    }
  };

  element.on('change', listener);

  ctrl.$render = function() {
    var value = attr.value;
    if (doTrim) {
      value = trim(value);
    }
    element[0].checked = (value === ctrl.$viewValue);
  };

  attr.$observe('value', ctrl.$render);
}

function parseConstantExpr($parse, context, name, expression, fallback) {
  var parseFn;
  if (isDefined(expression)) {
    parseFn = $parse(expression);
    if (!parseFn.constant) {
      throw ngModelMinErr('constexpr', 'Expected constant expression for `{0}`, but saw ' +
                                   '`{1}`.', name, expression);
    }
    return parseFn(context);
  }
  return fallback;
}

function checkboxInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter, $parse) {
  var trueValue = parseConstantExpr($parse, scope, 'ngTrueValue', attr.ngTrueValue, true);
  var falseValue = parseConstantExpr($parse, scope, 'ngFalseValue', attr.ngFalseValue, false);

  var listener = function(ev) {
    ctrl.$setViewValue(element[0].checked, ev && ev.type);
  };

  element.on('change', listener);

  ctrl.$render = function() {
    element[0].checked = ctrl.$viewValue;
  };

  // Override the standard `$isEmpty` because the $viewValue of an empty checkbox is always set to `false`
  // This is because of the parser below, which compares the `$modelValue` with `trueValue` to convert
  // it to a boolean.
  ctrl.$isEmpty = function(value) {
    return value === false;
  };

  ctrl.$formatters.push(function(value) {
    return equals(value, trueValue);
  });

  ctrl.$parsers.push(function(value) {
    return value ? trueValue : falseValue;
  });
}


/**
 * @ngdoc directive
 * @name textarea
 * @restrict E
 *
 * @description
 * HTML textarea element control with AngularJS data-binding. The data-binding and validation
 * properties of this element are exactly the same as those of the
 * {@link ng.directive:input input element}.
 *
 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} required Sets `required` validation error key if the value is not entered.
 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
 *    `required` when you want to data-bind to the `required` attribute.
 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
 *    minlength.
 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of any
 *    length.
 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
 *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
 *    If the expression evaluates to a RegExp object, then this is used directly.
 *    If the expression evaluates to a string, then it will be converted to a RegExp
 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
 *    `new RegExp('^abc$')`.<br />
 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
 *    start at the index of the last search's match, thus not taking the whole input value into
 *    account.
 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
 *    interaction with the input element.
 * @param {boolean=} [ngTrim=true] If set to false AngularJS will not automatically trim the input.
 *
 * @knownIssue
 *
 * When specifying the `placeholder` attribute of `<textarea>`, Internet Explorer will temporarily
 * insert the placeholder value as the textarea's content. If the placeholder value contains
 * interpolation (`{{ ... }}`), an error will be logged in the console when AngularJS tries to update
 * the value of the by-then-removed text node. This doesn't affect the functionality of the
 * textarea, but can be undesirable.
 *
 * You can work around this Internet Explorer issue by using `ng-attr-placeholder` instead of
 * `placeholder` on textareas, whenever you need interpolation in the placeholder value. You can
 * find more details on `ngAttr` in the
 * [Interpolation](guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes) section of the
 * Developer Guide.
 */


/**
 * @ngdoc directive
 * @name input
 * @restrict E
 *
 * @description
 * HTML input element control. When used together with {@link ngModel `ngModel`}, it provides data-binding,
 * input state control, and validation.
 * Input control follows HTML5 input types and polyfills the HTML5 validation behavior for older browsers.
 *
 * <div class="alert alert-warning">
 * **Note:** Not every feature offered is available for all input types.
 * Specifically, data binding and event handling via `ng-model` is unsupported for `input[file]`.
 * </div>
 *
 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} required Sets `required` validation error key if the value is not entered.
 * @param {boolean=} ngRequired Sets `required` attribute if set to true
 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
 *    minlength.
 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of any
 *    length.
 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
 *    value does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
 *    If the expression evaluates to a RegExp object, then this is used directly.
 *    If the expression evaluates to a string, then it will be converted to a RegExp
 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
 *    `new RegExp('^abc$')`.<br />
 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
 *    start at the index of the last search's match, thus not taking the whole input value into
 *    account.
 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
 *    interaction with the input element.
 * @param {boolean=} [ngTrim=true] If set to false AngularJS will not automatically trim the input.
 *    This parameter is ignored for input[type=password] controls, which will never trim the
 *    input.
 *
 * @example
    <example name="input-directive" module="inputExample">
      <file name="index.html">
       <script>
          angular.module('inputExample', [])
            .controller('ExampleController', ['$scope', function($scope) {
              $scope.user = {name: 'guest', last: 'visitor'};
            }]);
       </script>
       <div ng-controller="ExampleController">
         <form name="myForm">
           <label>
              User name:
              <input type="text" name="userName" ng-model="user.name" required>
           </label>
           <div role="alert">
             <span class="error" ng-show="myForm.userName.$error.required">
              Required!</span>
           </div>
           <label>
              Last name:
              <input type="text" name="lastName" ng-model="user.last"
              ng-minlength="3" ng-maxlength="10">
           </label>
           <div role="alert">
             <span class="error" ng-show="myForm.lastName.$error.minlength">
               Too short!</span>
             <span class="error" ng-show="myForm.lastName.$error.maxlength">
               Too long!</span>
           </div>
         </form>
         <hr>
         <tt>user = {{user}}</tt><br/>
         <tt>myForm.userName.$valid = {{myForm.userName.$valid}}</tt><br/>
         <tt>myForm.userName.$error = {{myForm.userName.$error}}</tt><br/>
         <tt>myForm.lastName.$valid = {{myForm.lastName.$valid}}</tt><br/>
         <tt>myForm.lastName.$error = {{myForm.lastName.$error}}</tt><br/>
         <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
         <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
         <tt>myForm.$error.minlength = {{!!myForm.$error.minlength}}</tt><br/>
         <tt>myForm.$error.maxlength = {{!!myForm.$error.maxlength}}</tt><br/>
       </div>
      </file>
      <file name="protractor.js" type="protractor">
        var user = element(by.exactBinding('user'));
        var userNameValid = element(by.binding('myForm.userName.$valid'));
        var lastNameValid = element(by.binding('myForm.lastName.$valid'));
        var lastNameError = element(by.binding('myForm.lastName.$error'));
        var formValid = element(by.binding('myForm.$valid'));
        var userNameInput = element(by.model('user.name'));
        var userLastInput = element(by.model('user.last'));

        it('should initialize to model', function() {
          expect(user.getText()).toContain('{"name":"guest","last":"visitor"}');
          expect(userNameValid.getText()).toContain('true');
          expect(formValid.getText()).toContain('true');
        });

        it('should be invalid if empty when required', function() {
          userNameInput.clear();
          userNameInput.sendKeys('');

          expect(user.getText()).toContain('{"last":"visitor"}');
          expect(userNameValid.getText()).toContain('false');
          expect(formValid.getText()).toContain('false');
        });

        it('should be valid if empty when min length is set', function() {
          userLastInput.clear();
          userLastInput.sendKeys('');

          expect(user.getText()).toContain('{"name":"guest","last":""}');
          expect(lastNameValid.getText()).toContain('true');
          expect(formValid.getText()).toContain('true');
        });

        it('should be invalid if less than required min length', function() {
          userLastInput.clear();
          userLastInput.sendKeys('xx');

          expect(user.getText()).toContain('{"name":"guest"}');
          expect(lastNameValid.getText()).toContain('false');
          expect(lastNameError.getText()).toContain('minlength');
          expect(formValid.getText()).toContain('false');
        });

        it('should be invalid if longer than max length', function() {
          userLastInput.clear();
          userLastInput.sendKeys('some ridiculously long name');

          expect(user.getText()).toContain('{"name":"guest"}');
          expect(lastNameValid.getText()).toContain('false');
          expect(lastNameError.getText()).toContain('maxlength');
          expect(formValid.getText()).toContain('false');
        });
      </file>
    </example>
 */
var inputDirective = ['$browser', '$sniffer', '$filter', '$parse',
    function($browser, $sniffer, $filter, $parse) {
  return {
    restrict: 'E',
    require: ['?ngModel'],
    link: {
      pre: function(scope, element, attr, ctrls) {
        if (ctrls[0]) {
          (inputType[lowercase(attr.type)] || inputType.text)(scope, element, attr, ctrls[0], $sniffer,
                                                              $browser, $filter, $parse);
        }
      }
    }
  };
}];


var hiddenInputBrowserCacheDirective = function() {
  var valueProperty = {
    configurable: true,
    enumerable: false,
    get: function() {
      return this.getAttribute('value') || '';
    },
    set: function(val) {
      this.setAttribute('value', val);
    }
  };

  return {
    restrict: 'E',
    priority: 200,
    compile: function(_, attr) {
      if (lowercase(attr.type) !== 'hidden') {
        return;
      }

      return {
        pre: function(scope, element, attr, ctrls) {
          var node = element[0];

          // Support: Edge
          // Moving the DOM around prevents autofillling
          if (node.parentNode) {
            node.parentNode.insertBefore(node, node.nextSibling);
          }

          // Support: FF, IE
          // Avoiding direct assignment to .value prevents autofillling
          if (Object.defineProperty) {
            Object.defineProperty(node, 'value', valueProperty);
          }
        }
      };
    }
  };
};



var CONSTANT_VALUE_REGEXP = /^(true|false|\d+)$/;
/**
 * @ngdoc directive
 * @name ngValue
 * @restrict A
 * @priority 100
 *
 * @description
 * Binds the given expression to the value of the element.
 *
 * It is mainly used on {@link input[radio] `input[radio]`} and option elements,
 * so that when the element is selected, the {@link ngModel `ngModel`} of that element (or its
 * {@link select `select`} parent element) is set to the bound value. It is especially useful
 * for dynamically generated lists using {@link ngRepeat `ngRepeat`}, as shown below.
 *
 * It can also be used to achieve one-way binding of a given expression to an input element
 * such as an `input[text]` or a `textarea`, when that element does not use ngModel.
 *
 * @element ANY
 * @param {string=} ngValue AngularJS expression, whose value will be bound to the `value` attribute
 * and `value` property of the element.
 *
 * @example
    <example name="ngValue-directive" module="valueExample">
      <file name="index.html">
       <script>
          angular.module('valueExample', [])
            .controller('ExampleController', ['$scope', function($scope) {
              $scope.names = ['pizza', 'unicorns', 'robots'];
              $scope.my = { favorite: 'unicorns' };
            }]);
       </script>
        <form ng-controller="ExampleController">
          <h2>Which is your favorite?</h2>
            <label ng-repeat="name in names" for="{{name}}">
              {{name}}
              <input type="radio"
                     ng-model="my.favorite"
                     ng-value="name"
                     id="{{name}}"
                     name="favorite">
            </label>
          <div>You chose {{my.favorite}}</div>
        </form>
      </file>
      <file name="protractor.js" type="protractor">
        var favorite = element(by.binding('my.favorite'));

        it('should initialize to model', function() {
          expect(favorite.getText()).toContain('unicorns');
        });
        it('should bind the values to the inputs', function() {
          element.all(by.model('my.favorite')).get(0).click();
          expect(favorite.getText()).toContain('pizza');
        });
      </file>
    </example>
 */
var ngValueDirective = function() {
  /**
   *  inputs use the value attribute as their default value if the value property is not set.
   *  Once the value property has been set (by adding input), it will not react to changes to
   *  the value attribute anymore. Setting both attribute and property fixes this behavior, and
   *  makes it possible to use ngValue as a sort of one-way bind.
   */
  function updateElementValue(element, attr, value) {
    // Support: IE9 only
    // In IE9 values are converted to string (e.g. `input.value = null` results in `input.value === 'null'`).
    var propValue = isDefined(value) ? value : (msie === 9) ? '' : null;
    element.prop('value', propValue);
    attr.$set('value', value);
  }

  return {
    restrict: 'A',
    priority: 100,
    compile: function(tpl, tplAttr) {
      if (CONSTANT_VALUE_REGEXP.test(tplAttr.ngValue)) {
        return function ngValueConstantLink(scope, elm, attr) {
          var value = scope.$eval(attr.ngValue);
          updateElementValue(elm, attr, value);
        };
      } else {
        return function ngValueLink(scope, elm, attr) {
          scope.$watch(attr.ngValue, function valueWatchAction(value) {
            updateElementValue(elm, attr, value);
          });
        };
      }
    }
  };
};

/**
 * @ngdoc directive
 * @name ngBind
 * @restrict AC
 *
 * @description
 * The `ngBind` attribute tells AngularJS to replace the text content of the specified HTML element
 * with the value of a given expression, and to update the text content when the value of that
 * expression changes.
 *
 * Typically, you don't use `ngBind` directly, but instead you use the double curly markup like
 * `{{ expression }}` which is similar but less verbose.
 *
 * It is preferable to use `ngBind` instead of `{{ expression }}` if a template is momentarily
 * displayed by the browser in its raw state before AngularJS compiles it. Since `ngBind` is an
 * element attribute, it makes the bindings invisible to the user while the page is loading.
 *
 * An alternative solution to this problem would be using the
 * {@link ng.directive:ngCloak ngCloak} directive.
 *
 *
 * @element ANY
 * @param {expression} ngBind {@link guide/expression Expression} to evaluate.
 *
 * @example
 * Enter a name in the Live Preview text box; the greeting below the text box changes instantly.
   <example module="bindExample" name="ng-bind">
     <file name="index.html">
       <script>
         angular.module('bindExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.name = 'Whirled';
           }]);
       </script>
       <div ng-controller="ExampleController">
         <label>Enter name: <input type="text" ng-model="name"></label><br>
         Hello <span ng-bind="name"></span>!
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-bind', function() {
         var nameInput = element(by.model('name'));

         expect(element(by.binding('name')).getText()).toBe('Whirled');
         nameInput.clear();
         nameInput.sendKeys('world');
         expect(element(by.binding('name')).getText()).toBe('world');
       });
     </file>
   </example>
 */
var ngBindDirective = ['$compile', function($compile) {
  return {
    restrict: 'AC',
    compile: function ngBindCompile(templateElement) {
      $compile.$$addBindingClass(templateElement);
      return function ngBindLink(scope, element, attr) {
        $compile.$$addBindingInfo(element, attr.ngBind);
        element = element[0];
        scope.$watch(attr.ngBind, function ngBindWatchAction(value) {
          element.textContent = stringify(value);
        });
      };
    }
  };
}];


/**
 * @ngdoc directive
 * @name ngBindTemplate
 *
 * @description
 * The `ngBindTemplate` directive specifies that the element
 * text content should be replaced with the interpolation of the template
 * in the `ngBindTemplate` attribute.
 * Unlike `ngBind`, the `ngBindTemplate` can contain multiple `{{` `}}`
 * expressions. This directive is needed since some HTML elements
 * (such as TITLE and OPTION) cannot contain SPAN elements.
 *
 * @element ANY
 * @param {string} ngBindTemplate template of form
 *   <tt>{{</tt> <tt>expression</tt> <tt>}}</tt> to eval.
 *
 * @example
 * Try it here: enter text in text box and watch the greeting change.
   <example module="bindExample" name="ng-bind-template">
     <file name="index.html">
       <script>
         angular.module('bindExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.salutation = 'Hello';
             $scope.name = 'World';
           }]);
       </script>
       <div ng-controller="ExampleController">
        <label>Salutation: <input type="text" ng-model="salutation"></label><br>
        <label>Name: <input type="text" ng-model="name"></label><br>
        <pre ng-bind-template="{{salutation}} {{name}}!"></pre>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-bind', function() {
         var salutationElem = element(by.binding('salutation'));
         var salutationInput = element(by.model('salutation'));
         var nameInput = element(by.model('name'));

         expect(salutationElem.getText()).toBe('Hello World!');

         salutationInput.clear();
         salutationInput.sendKeys('Greetings');
         nameInput.clear();
         nameInput.sendKeys('user');

         expect(salutationElem.getText()).toBe('Greetings user!');
       });
     </file>
   </example>
 */
var ngBindTemplateDirective = ['$interpolate', '$compile', function($interpolate, $compile) {
  return {
    compile: function ngBindTemplateCompile(templateElement) {
      $compile.$$addBindingClass(templateElement);
      return function ngBindTemplateLink(scope, element, attr) {
        var interpolateFn = $interpolate(element.attr(attr.$attr.ngBindTemplate));
        $compile.$$addBindingInfo(element, interpolateFn.expressions);
        element = element[0];
        attr.$observe('ngBindTemplate', function(value) {
          element.textContent = isUndefined(value) ? '' : value;
        });
      };
    }
  };
}];


/**
 * @ngdoc directive
 * @name ngBindHtml
 *
 * @description
 * Evaluates the expression and inserts the resulting HTML into the element in a secure way. By default,
 * the resulting HTML content will be sanitized using the {@link ngSanitize.$sanitize $sanitize} service.
 * To utilize this functionality, ensure that `$sanitize` is available, for example, by including {@link
 * ngSanitize} in your module's dependencies (not in core AngularJS). In order to use {@link ngSanitize}
 * in your module's dependencies, you need to include "angular-sanitize.js" in your application.
 *
 * You may also bypass sanitization for values you know are safe. To do so, bind to
 * an explicitly trusted value via {@link ng.$sce#trustAsHtml $sce.trustAsHtml}.  See the example
 * under {@link ng.$sce#show-me-an-example-using-sce- Strict Contextual Escaping (SCE)}.
 *
 * Note: If a `$sanitize` service is unavailable and the bound value isn't explicitly trusted, you
 * will have an exception (instead of an exploit.)
 *
 * @element ANY
 * @param {expression} ngBindHtml {@link guide/expression Expression} to evaluate.
 *
 * @example

   <example module="bindHtmlExample" deps="angular-sanitize.js" name="ng-bind-html">
     <file name="index.html">
       <div ng-controller="ExampleController">
        <p ng-bind-html="myHTML"></p>
       </div>
     </file>

     <file name="script.js">
       angular.module('bindHtmlExample', ['ngSanitize'])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.myHTML =
              'I am an <code>HTML</code>string with ' +
              '<a href="#">links!</a> and other <em>stuff</em>';
         }]);
     </file>

     <file name="protractor.js" type="protractor">
       it('should check ng-bind-html', function() {
         expect(element(by.binding('myHTML')).getText()).toBe(
             'I am an HTMLstring with links! and other stuff');
       });
     </file>
   </example>
 */
var ngBindHtmlDirective = ['$sce', '$parse', '$compile', function($sce, $parse, $compile) {
  return {
    restrict: 'A',
    compile: function ngBindHtmlCompile(tElement, tAttrs) {
      var ngBindHtmlGetter = $parse(tAttrs.ngBindHtml);
      var ngBindHtmlWatch = $parse(tAttrs.ngBindHtml, function sceValueOf(val) {
        // Unwrap the value to compare the actual inner safe value, not the wrapper object.
        return $sce.valueOf(val);
      });
      $compile.$$addBindingClass(tElement);

      return function ngBindHtmlLink(scope, element, attr) {
        $compile.$$addBindingInfo(element, attr.ngBindHtml);

        scope.$watch(ngBindHtmlWatch, function ngBindHtmlWatchAction() {
          // The watched value is the unwrapped value. To avoid re-escaping, use the direct getter.
          var value = ngBindHtmlGetter(scope);
          element.html($sce.getTrustedHtml(value) || '');
        });
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngChange
 * @restrict A
 *
 * @description
 * Evaluate the given expression when the user changes the input.
 * The expression is evaluated immediately, unlike the JavaScript onchange event
 * which only triggers at the end of a change (usually, when the user leaves the
 * form element or presses the return key).
 *
 * The `ngChange` expression is only evaluated when a change in the input value causes
 * a new value to be committed to the model.
 *
 * It will not be evaluated:
 * * if the value returned from the `$parsers` transformation pipeline has not changed
 * * if the input has continued to be invalid since the model will stay `null`
 * * if the model is changed programmatically and not by a change to the input value
 *
 *
 * Note, this directive requires `ngModel` to be present.
 *
 * @element ANY
 * @param {expression} ngChange {@link guide/expression Expression} to evaluate upon change
 * in input value.
 *
 * @example
 * <example name="ngChange-directive" module="changeExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('changeExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.counter = 0;
 *           $scope.change = function() {
 *             $scope.counter++;
 *           };
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <input type="checkbox" ng-model="confirmed" ng-change="change()" id="ng-change-example1" />
 *       <input type="checkbox" ng-model="confirmed" id="ng-change-example2" />
 *       <label for="ng-change-example2">Confirmed</label><br />
 *       <tt>debug = {{confirmed}}</tt><br/>
 *       <tt>counter = {{counter}}</tt><br/>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     var counter = element(by.binding('counter'));
 *     var debug = element(by.binding('confirmed'));
 *
 *     it('should evaluate the expression if changing from view', function() {
 *       expect(counter.getText()).toContain('0');
 *
 *       element(by.id('ng-change-example1')).click();
 *
 *       expect(counter.getText()).toContain('1');
 *       expect(debug.getText()).toContain('true');
 *     });
 *
 *     it('should not evaluate the expression if changing from model', function() {
 *       element(by.id('ng-change-example2')).click();

 *       expect(counter.getText()).toContain('0');
 *       expect(debug.getText()).toContain('true');
 *     });
 *   </file>
 * </example>
 */
var ngChangeDirective = valueFn({
  restrict: 'A',
  require: 'ngModel',
  link: function(scope, element, attr, ctrl) {
    ctrl.$viewChangeListeners.push(function() {
      scope.$eval(attr.ngChange);
    });
  }
});

/* exported
  ngClassDirective,
  ngClassEvenDirective,
  ngClassOddDirective
*/

function classDirective(name, selector) {
  name = 'ngClass' + name;
  var indexWatchExpression;

  return ['$parse', function($parse) {
    return {
      restrict: 'AC',
      link: function(scope, element, attr) {
        var classCounts = element.data('$classCounts');
        var oldModulo = true;
        var oldClassString;

        if (!classCounts) {
          // Use createMap() to prevent class assumptions involving property
          // names in Object.prototype
          classCounts = createMap();
          element.data('$classCounts', classCounts);
        }

        if (name !== 'ngClass') {
          if (!indexWatchExpression) {
            indexWatchExpression = $parse('$index', function moduloTwo($index) {
              // eslint-disable-next-line no-bitwise
              return $index & 1;
            });
          }

          scope.$watch(indexWatchExpression, ngClassIndexWatchAction);
        }

        scope.$watch($parse(attr[name], toClassString), ngClassWatchAction);

        function addClasses(classString) {
          classString = digestClassCounts(split(classString), 1);
          attr.$addClass(classString);
        }

        function removeClasses(classString) {
          classString = digestClassCounts(split(classString), -1);
          attr.$removeClass(classString);
        }

        function updateClasses(oldClassString, newClassString) {
          var oldClassArray = split(oldClassString);
          var newClassArray = split(newClassString);

          var toRemoveArray = arrayDifference(oldClassArray, newClassArray);
          var toAddArray = arrayDifference(newClassArray, oldClassArray);

          var toRemoveString = digestClassCounts(toRemoveArray, -1);
          var toAddString = digestClassCounts(toAddArray, 1);

          attr.$addClass(toAddString);
          attr.$removeClass(toRemoveString);
        }

        function digestClassCounts(classArray, count) {
          var classesToUpdate = [];

          forEach(classArray, function(className) {
            if (count > 0 || classCounts[className]) {
              classCounts[className] = (classCounts[className] || 0) + count;
              if (classCounts[className] === +(count > 0)) {
                classesToUpdate.push(className);
              }
            }
          });

          return classesToUpdate.join(' ');
        }

        function ngClassIndexWatchAction(newModulo) {
          // This watch-action should run before the `ngClassWatchAction()`, thus it
          // adds/removes `oldClassString`. If the `ngClass` expression has changed as well, the
          // `ngClassWatchAction()` will update the classes.
          if (newModulo === selector) {
            addClasses(oldClassString);
          } else {
            removeClasses(oldClassString);
          }

          oldModulo = newModulo;
        }

        function ngClassWatchAction(newClassString) {
          if (oldModulo === selector) {
            updateClasses(oldClassString, newClassString);
          }

          oldClassString = newClassString;
        }
      }
    };
  }];

  // Helpers
  function arrayDifference(tokens1, tokens2) {
    if (!tokens1 || !tokens1.length) return [];
    if (!tokens2 || !tokens2.length) return tokens1;

    var values = [];

    outer:
    for (var i = 0; i < tokens1.length; i++) {
      var token = tokens1[i];
      for (var j = 0; j < tokens2.length; j++) {
        if (token === tokens2[j]) continue outer;
      }
      values.push(token);
    }

    return values;
  }

  function split(classString) {
    return classString && classString.split(' ');
  }

  function toClassString(classValue) {
    if (!classValue) return classValue;

    var classString = classValue;

    if (isArray(classValue)) {
      classString = classValue.map(toClassString).join(' ');
    } else if (isObject(classValue)) {
      classString = Object.keys(classValue).
        filter(function(key) { return classValue[key]; }).
        join(' ');
    } else if (!isString(classValue)) {
      classString = classValue + '';
    }

    return classString;
  }
}

/**
 * @ngdoc directive
 * @name ngClass
 * @restrict AC
 * @element ANY
 *
 * @description
 * The `ngClass` directive allows you to dynamically set CSS classes on an HTML element by databinding
 * an expression that represents all classes to be added.
 *
 * The directive operates in three different ways, depending on which of three types the expression
 * evaluates to:
 *
 * 1. If the expression evaluates to a string, the string should be one or more space-delimited class
 * names.
 *
 * 2. If the expression evaluates to an object, then for each key-value pair of the
 * object with a truthy value the corresponding key is used as a class name.
 *
 * 3. If the expression evaluates to an array, each element of the array should either be a string as in
 * type 1 or an object as in type 2. This means that you can mix strings and objects together in an array
 * to give you more control over what CSS classes appear. See the code below for an example of this.
 *
 *
 * The directive won't add duplicate classes if a particular class was already set.
 *
 * When the expression changes, the previously added classes are removed and only then are the
 * new classes added.
 *
 * @knownIssue
 * You should not use {@link guide/interpolation interpolation} in the value of the `class`
 * attribute, when using the `ngClass` directive on the same element.
 * See {@link guide/interpolation#known-issues here} for more info.
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#addClass addClass}       | just before the class is applied to the element   |
 * | {@link ng.$animate#removeClass removeClass} | just before the class is removed from the element |
 * | {@link ng.$animate#setClass setClass} | just before classes are added and classes are removed from the element at the same time |
 *
 * ### ngClass and pre-existing CSS3 Transitions/Animations
   The ngClass directive still supports CSS3 Transitions/Animations even if they do not follow the ngAnimate CSS naming structure.
   Upon animation ngAnimate will apply supplementary CSS classes to track the start and end of an animation, but this will not hinder
   any pre-existing CSS transitions already on the element. To get an idea of what happens during a class-based animation, be sure
   to view the step by step details of {@link $animate#addClass $animate.addClass} and
   {@link $animate#removeClass $animate.removeClass}.
 *
 * @param {expression} ngClass {@link guide/expression Expression} to eval. The result
 *   of the evaluation can be a string representing space delimited class
 *   names, an array, or a map of class names to boolean values. In the case of a map, the
 *   names of the properties whose values are truthy will be added as css classes to the
 *   element.
 *
 * @example
 * ### Basic
   <example name="ng-class">
     <file name="index.html">
       <p ng-class="{strike: deleted, bold: important, 'has-error': error}">Map Syntax Example</p>
       <label>
          <input type="checkbox" ng-model="deleted">
          deleted (apply "strike" class)
       </label><br>
       <label>
          <input type="checkbox" ng-model="important">
          important (apply "bold" class)
       </label><br>
       <label>
          <input type="checkbox" ng-model="error">
          error (apply "has-error" class)
       </label>
       <hr>
       <p ng-class="style">Using String Syntax</p>
       <input type="text" ng-model="style"
              placeholder="Type: bold strike red" aria-label="Type: bold strike red">
       <hr>
       <p ng-class="[style1, style2, style3]">Using Array Syntax</p>
       <input ng-model="style1"
              placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red"><br>
       <input ng-model="style2"
              placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red 2"><br>
       <input ng-model="style3"
              placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red 3"><br>
       <hr>
       <p ng-class="[style4, {orange: warning}]">Using Array and Map Syntax</p>
       <input ng-model="style4" placeholder="Type: bold, strike" aria-label="Type: bold, strike"><br>
       <label><input type="checkbox" ng-model="warning"> warning (apply "orange" class)</label>
     </file>
     <file name="style.css">
       .strike {
           text-decoration: line-through;
       }
       .bold {
           font-weight: bold;
       }
       .red {
           color: red;
       }
       .has-error {
           color: red;
           background-color: yellow;
       }
       .orange {
           color: orange;
       }
     </file>
     <file name="protractor.js" type="protractor">
       var ps = element.all(by.css('p'));

       it('should let you toggle the class', function() {

         expect(ps.first().getAttribute('class')).not.toMatch(/bold/);
         expect(ps.first().getAttribute('class')).not.toMatch(/has-error/);

         element(by.model('important')).click();
         expect(ps.first().getAttribute('class')).toMatch(/bold/);

         element(by.model('error')).click();
         expect(ps.first().getAttribute('class')).toMatch(/has-error/);
       });

       it('should let you toggle string example', function() {
         expect(ps.get(1).getAttribute('class')).toBe('');
         element(by.model('style')).clear();
         element(by.model('style')).sendKeys('red');
         expect(ps.get(1).getAttribute('class')).toBe('red');
       });

       it('array example should have 3 classes', function() {
         expect(ps.get(2).getAttribute('class')).toBe('');
         element(by.model('style1')).sendKeys('bold');
         element(by.model('style2')).sendKeys('strike');
         element(by.model('style3')).sendKeys('red');
         expect(ps.get(2).getAttribute('class')).toBe('bold strike red');
       });

       it('array with map example should have 2 classes', function() {
         expect(ps.last().getAttribute('class')).toBe('');
         element(by.model('style4')).sendKeys('bold');
         element(by.model('warning')).click();
         expect(ps.last().getAttribute('class')).toBe('bold orange');
       });
     </file>
   </example>

   @example
   ### Animations

   The example below demonstrates how to perform animations using ngClass.

   <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-class">
     <file name="index.html">
      <input id="setbtn" type="button" value="set" ng-click="myVar='my-class'">
      <input id="clearbtn" type="button" value="clear" ng-click="myVar=''">
      <br>
      <span class="base-class" ng-class="myVar">Sample Text</span>
     </file>
     <file name="style.css">
       .base-class {
         transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
       }

       .base-class.my-class {
         color: red;
         font-size:3em;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-class', function() {
         expect(element(by.css('.base-class')).getAttribute('class')).not.
           toMatch(/my-class/);

         element(by.id('setbtn')).click();

         expect(element(by.css('.base-class')).getAttribute('class')).
           toMatch(/my-class/);

         element(by.id('clearbtn')).click();

         expect(element(by.css('.base-class')).getAttribute('class')).not.
           toMatch(/my-class/);
       });
     </file>
   </example>
 */
var ngClassDirective = classDirective('', true);

/**
 * @ngdoc directive
 * @name ngClassOdd
 * @restrict AC
 *
 * @description
 * The `ngClassOdd` and `ngClassEven` directives work exactly as
 * {@link ng.directive:ngClass ngClass}, except they work in
 * conjunction with `ngRepeat` and take effect only on odd (even) rows.
 *
 * This directive can be applied only within the scope of an
 * {@link ng.directive:ngRepeat ngRepeat}.
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#addClass addClass}       | just before the class is applied to the element   |
 * | {@link ng.$animate#removeClass removeClass} | just before the class is removed from the element |
 *
 * @element ANY
 * @param {expression} ngClassOdd {@link guide/expression Expression} to eval. The result
 *   of the evaluation can be a string representing space delimited class names or an array.
 *
 * @example
   <example name="ng-class-odd">
     <file name="index.html">
        <ol ng-init="names=['John', 'Mary', 'Cate', 'Suz']">
          <li ng-repeat="name in names">
           <span ng-class-odd="'odd'" ng-class-even="'even'">
             {{name}}
           </span>
          </li>
        </ol>
     </file>
     <file name="style.css">
       .odd {
         color: red;
       }
       .even {
         color: blue;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-class-odd and ng-class-even', function() {
         expect(element(by.repeater('name in names').row(0).column('name')).getAttribute('class')).
           toMatch(/odd/);
         expect(element(by.repeater('name in names').row(1).column('name')).getAttribute('class')).
           toMatch(/even/);
       });
     </file>
   </example>
 *
 * <hr />
 * @example
 * An example on how to implement animations using `ngClassOdd`:
 *
   <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-class-odd-animate">
     <file name="index.html">
       <div ng-init="items=['Item 3', 'Item 2', 'Item 1', 'Item 0']">
         <button ng-click="items.unshift('Item ' + items.length)">Add item</button>
         <hr />
         <table>
           <tr ng-repeat="item in items" ng-class-odd="'odd'">
             <td>{{ item }}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="style.css">
       .odd {
         background: rgba(255, 255, 0, 0.25);
       }

       .odd-add, .odd-remove {
         transition: 1.5s;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should add new entries to the beginning of the list', function() {
         var button = element(by.buttonText('Add item'));
         var rows = element.all(by.repeater('item in items'));

         expect(rows.count()).toBe(4);
         expect(rows.get(0).getText()).toBe('Item 3');
         expect(rows.get(1).getText()).toBe('Item 2');

         button.click();

         expect(rows.count()).toBe(5);
         expect(rows.get(0).getText()).toBe('Item 4');
         expect(rows.get(1).getText()).toBe('Item 3');
       });

       it('should add odd class to odd entries', function() {
         var button = element(by.buttonText('Add item'));
         var rows = element.all(by.repeater('item in items'));

         expect(rows.get(0).getAttribute('class')).toMatch(/odd/);
         expect(rows.get(1).getAttribute('class')).not.toMatch(/odd/);

         button.click();

         expect(rows.get(0).getAttribute('class')).toMatch(/odd/);
         expect(rows.get(1).getAttribute('class')).not.toMatch(/odd/);
       });
     </file>
   </example>
 */
var ngClassOddDirective = classDirective('Odd', 0);

/**
 * @ngdoc directive
 * @name ngClassEven
 * @restrict AC
 *
 * @description
 * The `ngClassOdd` and `ngClassEven` directives work exactly as
 * {@link ng.directive:ngClass ngClass}, except they work in
 * conjunction with `ngRepeat` and take effect only on odd (even) rows.
 *
 * This directive can be applied only within the scope of an
 * {@link ng.directive:ngRepeat ngRepeat}.
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#addClass addClass}       | just before the class is applied to the element   |
 * | {@link ng.$animate#removeClass removeClass} | just before the class is removed from the element |
 *
 * @element ANY
 * @param {expression} ngClassEven {@link guide/expression Expression} to eval. The
 *   result of the evaluation can be a string representing space delimited class names or an array.
 *
 * @example
   <example name="ng-class-even">
     <file name="index.html">
        <ol ng-init="names=['John', 'Mary', 'Cate', 'Suz']">
          <li ng-repeat="name in names">
           <span ng-class-odd="'odd'" ng-class-even="'even'">
             {{name}} &nbsp; &nbsp; &nbsp;
           </span>
          </li>
        </ol>
     </file>
     <file name="style.css">
       .odd {
         color: red;
       }
       .even {
         color: blue;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-class-odd and ng-class-even', function() {
         expect(element(by.repeater('name in names').row(0).column('name')).getAttribute('class')).
           toMatch(/odd/);
         expect(element(by.repeater('name in names').row(1).column('name')).getAttribute('class')).
           toMatch(/even/);
       });
     </file>
   </example>
 *
 * <hr />
 * @example
 * An example on how to implement animations using `ngClassEven`:
 *
   <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-class-even-animate">
     <file name="index.html">
       <div ng-init="items=['Item 3', 'Item 2', 'Item 1', 'Item 0']">
         <button ng-click="items.unshift('Item ' + items.length)">Add item</button>
         <hr />
         <table>
           <tr ng-repeat="item in items" ng-class-even="'even'">
             <td>{{ item }}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="style.css">
       .even {
         background: rgba(255, 255, 0, 0.25);
       }

       .even-add, .even-remove {
         transition: 1.5s;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should add new entries to the beginning of the list', function() {
         var button = element(by.buttonText('Add item'));
         var rows = element.all(by.repeater('item in items'));

         expect(rows.count()).toBe(4);
         expect(rows.get(0).getText()).toBe('Item 3');
         expect(rows.get(1).getText()).toBe('Item 2');

         button.click();

         expect(rows.count()).toBe(5);
         expect(rows.get(0).getText()).toBe('Item 4');
         expect(rows.get(1).getText()).toBe('Item 3');
       });

       it('should add even class to even entries', function() {
         var button = element(by.buttonText('Add item'));
         var rows = element.all(by.repeater('item in items'));

         expect(rows.get(0).getAttribute('class')).not.toMatch(/even/);
         expect(rows.get(1).getAttribute('class')).toMatch(/even/);

         button.click();

         expect(rows.get(0).getAttribute('class')).not.toMatch(/even/);
         expect(rows.get(1).getAttribute('class')).toMatch(/even/);
       });
     </file>
   </example>
 */
var ngClassEvenDirective = classDirective('Even', 1);

/**
 * @ngdoc directive
 * @name ngCloak
 * @restrict AC
 *
 * @description
 * The `ngCloak` directive is used to prevent the AngularJS html template from being briefly
 * displayed by the browser in its raw (uncompiled) form while your application is loading. Use this
 * directive to avoid the undesirable flicker effect caused by the html template display.
 *
 * The directive can be applied to the `<body>` element, but the preferred usage is to apply
 * multiple `ngCloak` directives to small portions of the page to permit progressive rendering
 * of the browser view.
 *
 * `ngCloak` works in cooperation with the following css rule embedded within `angular.js` and
 * `angular.min.js`.
 * For CSP mode please add `angular-csp.css` to your html file (see {@link ng.directive:ngCsp ngCsp}).
 *
 * ```css
 * [ng\:cloak], [ng-cloak], [data-ng-cloak], [x-ng-cloak], .ng-cloak, .x-ng-cloak {
 *   display: none !important;
 * }
 * ```
 *
 * When this css rule is loaded by the browser, all html elements (including their children) that
 * are tagged with the `ngCloak` directive are hidden. When AngularJS encounters this directive
 * during the compilation of the template it deletes the `ngCloak` element attribute, making
 * the compiled element visible.
 *
 * For the best result, the `angular.js` script must be loaded in the head section of the html
 * document; alternatively, the css rule above must be included in the external stylesheet of the
 * application.
 *
 * @element ANY
 *
 * @example
   <example name="ng-cloak">
     <file name="index.html">
        <div id="template1" ng-cloak>{{ 'hello' }}</div>
        <div id="template2" class="ng-cloak">{{ 'world' }}</div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should remove the template directive and css class', function() {
         expect($('#template1').getAttribute('ng-cloak')).
           toBeNull();
         expect($('#template2').getAttribute('ng-cloak')).
           toBeNull();
       });
     </file>
   </example>
 *
 */
var ngCloakDirective = ngDirective({
  compile: function(element, attr) {
    attr.$set('ngCloak', undefined);
    element.removeClass('ng-cloak');
  }
});

/**
 * @ngdoc directive
 * @name ngController
 *
 * @description
 * The `ngController` directive attaches a controller class to the view. This is a key aspect of how angular
 * supports the principles behind the Model-View-Controller design pattern.
 *
 * MVC components in angular:
 *
 * * Model — Models are the properties of a scope; scopes are attached to the DOM where scope properties
 *   are accessed through bindings.
 * * View — The template (HTML with data bindings) that is rendered into the View.
 * * Controller — The `ngController` directive specifies a Controller class; the class contains business
 *   logic behind the application to decorate the scope with functions and values
 *
 * Note that you can also attach controllers to the DOM by declaring it in a route definition
 * via the {@link ngRoute.$route $route} service. A common mistake is to declare the controller
 * again using `ng-controller` in the template itself.  This will cause the controller to be attached
 * and executed twice.
 *
 * @element ANY
 * @scope
 * @priority 500
 * @param {expression} ngController Name of a constructor function registered with the current
 * {@link ng.$controllerProvider $controllerProvider} or an {@link guide/expression expression}
 * that on the current scope evaluates to a constructor function.
 *
 * The controller instance can be published into a scope property by specifying
 * `ng-controller="as propertyName"`.
 *
 * @example
 * Here is a simple form for editing user contact information. Adding, removing, clearing, and
 * greeting are methods declared on the controller (see source tab). These methods can
 * easily be called from the AngularJS markup. Any changes to the data are automatically reflected
 * in the View without the need for a manual update.
 *
 * Two different declaration styles are included below:
 *
 * * one binds methods and properties directly onto the controller using `this`:
 * `ng-controller="SettingsController1 as settings"`
 * * one injects `$scope` into the controller:
 * `ng-controller="SettingsController2"`
 *
 * The second option is more common in the AngularJS community, and is generally used in boilerplates
 * and in this guide. However, there are advantages to binding properties directly to the controller
 * and avoiding scope.
 *
 * * Using `controller as` makes it obvious which controller you are accessing in the template when
 * multiple controllers apply to an element.
 * * If you are writing your controllers as classes you have easier access to the properties and
 * methods, which will appear on the scope, from inside the controller code.
 * * Since there is always a `.` in the bindings, you don't have to worry about prototypal
 * inheritance masking primitives.
 *
 * This example demonstrates the `controller as` syntax.
 *
 * <example name="ngControllerAs" module="controllerAsExample">
 *   <file name="index.html">
 *    <div id="ctrl-as-exmpl" ng-controller="SettingsController1 as settings">
 *      <label>Name: <input type="text" ng-model="settings.name"/></label>
 *      <button ng-click="settings.greet()">greet</button><br/>
 *      Contact:
 *      <ul>
 *        <li ng-repeat="contact in settings.contacts">
 *          <select ng-model="contact.type" aria-label="Contact method" id="select_{{$index}}">
 *             <option>phone</option>
 *             <option>email</option>
 *          </select>
 *          <input type="text" ng-model="contact.value" aria-labelledby="select_{{$index}}" />
 *          <button ng-click="settings.clearContact(contact)">clear</button>
 *          <button ng-click="settings.removeContact(contact)" aria-label="Remove">X</button>
 *        </li>
 *        <li><button ng-click="settings.addContact()">add</button></li>
 *     </ul>
 *    </div>
 *   </file>
 *   <file name="app.js">
 *    angular.module('controllerAsExample', [])
 *      .controller('SettingsController1', SettingsController1);
 *
 *    function SettingsController1() {
 *      this.name = 'John Smith';
 *      this.contacts = [
 *        {type: 'phone', value: '408 555 1212'},
 *        {type: 'email', value: 'john.smith@example.org'}
 *      ];
 *    }
 *
 *    SettingsController1.prototype.greet = function() {
 *      alert(this.name);
 *    };
 *
 *    SettingsController1.prototype.addContact = function() {
 *      this.contacts.push({type: 'email', value: 'yourname@example.org'});
 *    };
 *
 *    SettingsController1.prototype.removeContact = function(contactToRemove) {
 *     var index = this.contacts.indexOf(contactToRemove);
 *      this.contacts.splice(index, 1);
 *    };
 *
 *    SettingsController1.prototype.clearContact = function(contact) {
 *      contact.type = 'phone';
 *      contact.value = '';
 *    };
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     it('should check controller as', function() {
 *       var container = element(by.id('ctrl-as-exmpl'));
 *         expect(container.element(by.model('settings.name'))
 *           .getAttribute('value')).toBe('John Smith');
 *
 *       var firstRepeat =
 *           container.element(by.repeater('contact in settings.contacts').row(0));
 *       var secondRepeat =
 *           container.element(by.repeater('contact in settings.contacts').row(1));
 *
 *       expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *           .toBe('408 555 1212');
 *
 *       expect(secondRepeat.element(by.model('contact.value')).getAttribute('value'))
 *           .toBe('john.smith@example.org');
 *
 *       firstRepeat.element(by.buttonText('clear')).click();
 *
 *       expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *           .toBe('');
 *
 *       container.element(by.buttonText('add')).click();
 *
 *       expect(container.element(by.repeater('contact in settings.contacts').row(2))
 *           .element(by.model('contact.value'))
 *           .getAttribute('value'))
 *           .toBe('yourname@example.org');
 *     });
 *   </file>
 * </example>
 *
 * This example demonstrates the "attach to `$scope`" style of controller.
 *
 * <example name="ngController" module="controllerExample">
 *  <file name="index.html">
 *   <div id="ctrl-exmpl" ng-controller="SettingsController2">
 *     <label>Name: <input type="text" ng-model="name"/></label>
 *     <button ng-click="greet()">greet</button><br/>
 *     Contact:
 *     <ul>
 *       <li ng-repeat="contact in contacts">
 *         <select ng-model="contact.type" id="select_{{$index}}">
 *            <option>phone</option>
 *            <option>email</option>
 *         </select>
 *         <input type="text" ng-model="contact.value" aria-labelledby="select_{{$index}}" />
 *         <button ng-click="clearContact(contact)">clear</button>
 *         <button ng-click="removeContact(contact)">X</button>
 *       </li>
 *       <li>[ <button ng-click="addContact()">add</button> ]</li>
 *    </ul>
 *   </div>
 *  </file>
 *  <file name="app.js">
 *   angular.module('controllerExample', [])
 *     .controller('SettingsController2', ['$scope', SettingsController2]);
 *
 *   function SettingsController2($scope) {
 *     $scope.name = 'John Smith';
 *     $scope.contacts = [
 *       {type:'phone', value:'408 555 1212'},
 *       {type:'email', value:'john.smith@example.org'}
 *     ];
 *
 *     $scope.greet = function() {
 *       alert($scope.name);
 *     };
 *
 *     $scope.addContact = function() {
 *       $scope.contacts.push({type:'email', value:'yourname@example.org'});
 *     };
 *
 *     $scope.removeContact = function(contactToRemove) {
 *       var index = $scope.contacts.indexOf(contactToRemove);
 *       $scope.contacts.splice(index, 1);
 *     };
 *
 *     $scope.clearContact = function(contact) {
 *       contact.type = 'phone';
 *       contact.value = '';
 *     };
 *   }
 *  </file>
 *  <file name="protractor.js" type="protractor">
 *    it('should check controller', function() {
 *      var container = element(by.id('ctrl-exmpl'));
 *
 *      expect(container.element(by.model('name'))
 *          .getAttribute('value')).toBe('John Smith');
 *
 *      var firstRepeat =
 *          container.element(by.repeater('contact in contacts').row(0));
 *      var secondRepeat =
 *          container.element(by.repeater('contact in contacts').row(1));
 *
 *      expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *          .toBe('408 555 1212');
 *      expect(secondRepeat.element(by.model('contact.value')).getAttribute('value'))
 *          .toBe('john.smith@example.org');
 *
 *      firstRepeat.element(by.buttonText('clear')).click();
 *
 *      expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *          .toBe('');
 *
 *      container.element(by.buttonText('add')).click();
 *
 *      expect(container.element(by.repeater('contact in contacts').row(2))
 *          .element(by.model('contact.value'))
 *          .getAttribute('value'))
 *          .toBe('yourname@example.org');
 *    });
 *  </file>
 *</example>

 */
var ngControllerDirective = [function() {
  return {
    restrict: 'A',
    scope: true,
    controller: '@',
    priority: 500
  };
}];

/**
 * @ngdoc directive
 * @name ngCsp
 *
 * @restrict A
 * @element ANY
 * @description
 *
 * AngularJS has some features that can conflict with certain restrictions that are applied when using
 * [CSP (Content Security Policy)](https://developer.mozilla.org/en/Security/CSP) rules.
 *
 * If you intend to implement CSP with these rules then you must tell AngularJS not to use these
 * features.
 *
 * This is necessary when developing things like Google Chrome Extensions or Universal Windows Apps.
 *
 *
 * The following default rules in CSP affect AngularJS:
 *
 * * The use of `eval()`, `Function(string)` and similar functions to dynamically create and execute
 * code from strings is forbidden. AngularJS makes use of this in the {@link $parse} service to
 * provide a 30% increase in the speed of evaluating AngularJS expressions. (This CSP rule can be
 * disabled with the CSP keyword `unsafe-eval`, but it is generally not recommended as it would
 * weaken the protections offered by CSP.)
 *
 * * The use of inline resources, such as inline `<script>` and `<style>` elements, are forbidden.
 * This prevents apps from injecting custom styles directly into the document. AngularJS makes use of
 * this to include some CSS rules (e.g. {@link ngCloak} and {@link ngHide}). To make these
 * directives work when a CSP rule is blocking inline styles, you must link to the `angular-csp.css`
 * in your HTML manually. (This CSP rule can be disabled with the CSP keyword `unsafe-inline`, but
 * it is generally not recommended as it would weaken the protections offered by CSP.)
 *
 * If you do not provide `ngCsp` then AngularJS tries to autodetect if CSP is blocking dynamic code
 * creation from strings (e.g., `unsafe-eval` not specified in CSP header) and automatically
 * deactivates this feature in the {@link $parse} service. This autodetection, however, triggers a
 * CSP error to be logged in the console:
 *
 * ```
 * Refused to evaluate a string as JavaScript because 'unsafe-eval' is not an allowed source of
 * script in the following Content Security Policy directive: "default-src 'self'". Note that
 * 'script-src' was not explicitly set, so 'default-src' is used as a fallback.
 * ```
 *
 * This error is harmless but annoying. To prevent the error from showing up, put the `ngCsp`
 * directive on an element of the HTML document that appears before the `<script>` tag that loads
 * the `angular.js` file.
 *
 * *Note: This directive is only available in the `ng-csp` and `data-ng-csp` attribute form.*
 *
 * You can specify which of the CSP related AngularJS features should be deactivated by providing
 * a value for the `ng-csp` attribute. The options are as follows:
 *
 * * no-inline-style: this stops AngularJS from injecting CSS styles into the DOM
 *
 * * no-unsafe-eval: this stops AngularJS from optimizing $parse with unsafe eval of strings
 *
 * You can use these values in the following combinations:
 *
 *
 * * No declaration means that AngularJS will assume that you can do inline styles, but it will do
 * a runtime check for unsafe-eval. E.g. `<body>`. This is backwardly compatible with previous
 * versions of AngularJS.
 *
 * * A simple `ng-csp` (or `data-ng-csp`) attribute will tell AngularJS to deactivate both inline
 * styles and unsafe eval. E.g. `<body ng-csp>`. This is backwardly compatible with previous
 * versions of AngularJS.
 *
 * * Specifying only `no-unsafe-eval` tells AngularJS that we must not use eval, but that we can
 * inject inline styles. E.g. `<body ng-csp="no-unsafe-eval">`.
 *
 * * Specifying only `no-inline-style` tells AngularJS that we must not inject styles, but that we can
 * run eval - no automatic check for unsafe eval will occur. E.g. `<body ng-csp="no-inline-style">`
 *
 * * Specifying both `no-unsafe-eval` and `no-inline-style` tells AngularJS that we must not inject
 * styles nor use eval, which is the same as an empty: ng-csp.
 * E.g.`<body ng-csp="no-inline-style;no-unsafe-eval">`
 *
 * @example
 *
 * This example shows how to apply the `ngCsp` directive to the `html` tag.
   ```html
     <!doctype html>
     <html ng-app ng-csp>
     ...
     ...
     </html>
   ```

  <!-- Note: the `.csp` suffix in the example name triggers CSP mode in our http server! -->
  <example name="example.csp" module="cspExample" ng-csp="true">
    <file name="index.html">
      <div ng-controller="MainController as ctrl">
        <div>
          <button ng-click="ctrl.inc()" id="inc">Increment</button>
          <span id="counter">
            {{ctrl.counter}}
          </span>
        </div>

        <div>
          <button ng-click="ctrl.evil()" id="evil">Evil</button>
          <span id="evilError">
            {{ctrl.evilError}}
          </span>
        </div>
      </div>
    </file>
    <file name="script.js">
       angular.module('cspExample', [])
         .controller('MainController', function MainController() {
            this.counter = 0;
            this.inc = function() {
              this.counter++;
            };
            this.evil = function() {
              try {
                eval('1+2'); // eslint-disable-line no-eval
              } catch (e) {
                this.evilError = e.message;
              }
            };
          });
    </file>
    <file name="protractor.js" type="protractor">
      var util, webdriver;

      var incBtn = element(by.id('inc'));
      var counter = element(by.id('counter'));
      var evilBtn = element(by.id('evil'));
      var evilError = element(by.id('evilError'));

      function getAndClearSevereErrors() {
        return browser.manage().logs().get('browser').then(function(browserLog) {
          return browserLog.filter(function(logEntry) {
            return logEntry.level.value > webdriver.logging.Level.WARNING.value;
          });
        });
      }

      function clearErrors() {
        getAndClearSevereErrors();
      }

      function expectNoErrors() {
        getAndClearSevereErrors().then(function(filteredLog) {
          expect(filteredLog.length).toEqual(0);
          if (filteredLog.length) {
            console.log('browser console errors: ' + util.inspect(filteredLog));
          }
        });
      }

      function expectError(regex) {
        getAndClearSevereErrors().then(function(filteredLog) {
          var found = false;
          filteredLog.forEach(function(log) {
            if (log.message.match(regex)) {
              found = true;
            }
          });
          if (!found) {
            throw new Error('expected an error that matches ' + regex);
          }
        });
      }

      beforeEach(function() {
        util = require('util');
        webdriver = require('selenium-webdriver');
      });

      // For now, we only test on Chrome,
      // as Safari does not load the page with Protractor's injected scripts,
      // and Firefox webdriver always disables content security policy (#6358)
      if (browser.params.browser !== 'chrome') {
        return;
      }

      it('should not report errors when the page is loaded', function() {
        // clear errors so we are not dependent on previous tests
        clearErrors();
        // Need to reload the page as the page is already loaded when
        // we come here
        browser.driver.getCurrentUrl().then(function(url) {
          browser.get(url);
        });
        expectNoErrors();
      });

      it('should evaluate expressions', function() {
        expect(counter.getText()).toEqual('0');
        incBtn.click();
        expect(counter.getText()).toEqual('1');
        expectNoErrors();
      });

      it('should throw and report an error when using "eval"', function() {
        evilBtn.click();
        expect(evilError.getText()).toMatch(/Content Security Policy/);
        expectError(/Content Security Policy/);
      });
    </file>
  </example>
  */

// `ngCsp` is not implemented as a proper directive any more, because we need it be processed while
// we bootstrap the app (before `$parse` is instantiated). For this reason, we just have the `csp()`
// fn that looks for the `ng-csp` attribute anywhere in the current doc.

/**
 * @ngdoc directive
 * @name ngClick
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * The ngClick directive allows you to specify custom behavior when
 * an element is clicked.
 *
 * @param {expression} ngClick {@link guide/expression Expression} to evaluate upon
 * click. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-click">
     <file name="index.html">
      <button ng-click="count = count + 1" ng-init="count=0">
        Increment
      </button>
      <span>
        count: {{count}}
      </span>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-click', function() {
         expect(element(by.binding('count')).getText()).toMatch('0');
         element(by.css('button')).click();
         expect(element(by.binding('count')).getText()).toMatch('1');
       });
     </file>
   </example>
 */
/*
 * A collection of directives that allows creation of custom event handlers that are defined as
 * AngularJS expressions and are compiled and executed within the current scope.
 */
var ngEventDirectives = {};

// For events that might fire synchronously during DOM manipulation
// we need to execute their event handlers asynchronously using $evalAsync,
// so that they are not executed in an inconsistent state.
var forceAsyncEvents = {
  'blur': true,
  'focus': true
};
forEach(
  'click dblclick mousedown mouseup mouseover mouseout mousemove mouseenter mouseleave keydown keyup keypress submit focus blur copy cut paste'.split(' '),
  function(eventName) {
    var directiveName = directiveNormalize('ng-' + eventName);
    ngEventDirectives[directiveName] = ['$parse', '$rootScope', '$exceptionHandler', function($parse, $rootScope, $exceptionHandler) {
      return createEventDirective($parse, $rootScope, $exceptionHandler, directiveName, eventName, forceAsyncEvents[eventName]);
    }];
  }
);

function createEventDirective($parse, $rootScope, $exceptionHandler, directiveName, eventName, forceAsync) {
  return {
    restrict: 'A',
    compile: function($element, attr) {
      // NOTE:
      // We expose the powerful `$event` object on the scope that provides access to the Window,
      // etc. This is OK, because expressions are not sandboxed any more (and the expression
      // sandbox was never meant to be a security feature anyway).
      var fn = $parse(attr[directiveName]);
      return function ngEventHandler(scope, element) {
        element.on(eventName, function(event) {
          var callback = function() {
            fn(scope, {$event: event});
          };

          if (!$rootScope.$$phase) {
            scope.$apply(callback);
          } else if (forceAsync) {
            scope.$evalAsync(callback);
          } else {
            try {
              callback();
            } catch (error) {
              $exceptionHandler(error);
            }
          }
        });
      };
    }
  };
}

/**
 * @ngdoc directive
 * @name ngDblclick
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * The `ngDblclick` directive allows you to specify custom behavior on a dblclick event.
 *
 * @param {expression} ngDblclick {@link guide/expression Expression} to evaluate upon
 * a dblclick. (The Event object is available as `$event`)
 *
 * @example
   <example name="ng-dblclick">
     <file name="index.html">
      <button ng-dblclick="count = count + 1" ng-init="count=0">
        Increment (on double click)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMousedown
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * The ngMousedown directive allows you to specify custom behavior on mousedown event.
 *
 * @param {expression} ngMousedown {@link guide/expression Expression} to evaluate upon
 * mousedown. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mousedown">
     <file name="index.html">
      <button ng-mousedown="count = count + 1" ng-init="count=0">
        Increment (on mouse down)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMouseup
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * Specify custom behavior on mouseup event.
 *
 * @param {expression} ngMouseup {@link guide/expression Expression} to evaluate upon
 * mouseup. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseup">
     <file name="index.html">
      <button ng-mouseup="count = count + 1" ng-init="count=0">
        Increment (on mouse up)
      </button>
      count: {{count}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngMouseover
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * Specify custom behavior on mouseover event.
 *
 * @param {expression} ngMouseover {@link guide/expression Expression} to evaluate upon
 * mouseover. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseover">
     <file name="index.html">
      <button ng-mouseover="count = count + 1" ng-init="count=0">
        Increment (when mouse is over)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMouseenter
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * Specify custom behavior on mouseenter event.
 *
 * @param {expression} ngMouseenter {@link guide/expression Expression} to evaluate upon
 * mouseenter. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseenter">
     <file name="index.html">
      <button ng-mouseenter="count = count + 1" ng-init="count=0">
        Increment (when mouse enters)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMouseleave
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * Specify custom behavior on mouseleave event.
 *
 * @param {expression} ngMouseleave {@link guide/expression Expression} to evaluate upon
 * mouseleave. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseleave">
     <file name="index.html">
      <button ng-mouseleave="count = count + 1" ng-init="count=0">
        Increment (when mouse leaves)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMousemove
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * Specify custom behavior on mousemove event.
 *
 * @param {expression} ngMousemove {@link guide/expression Expression} to evaluate upon
 * mousemove. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mousemove">
     <file name="index.html">
      <button ng-mousemove="count = count + 1" ng-init="count=0">
        Increment (when mouse moves)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngKeydown
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * Specify custom behavior on keydown event.
 *
 * @param {expression} ngKeydown {@link guide/expression Expression} to evaluate upon
 * keydown. (Event object is available as `$event` and can be interrogated for keyCode, altKey, etc.)
 *
 * @example
   <example name="ng-keydown">
     <file name="index.html">
      <input ng-keydown="count = count + 1" ng-init="count=0">
      key down count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngKeyup
 * @restrict A
 * @element ANY
 * @priority 0
 *
 * @description
 * Specify custom behavior on keyup event.
 *
 * @param {expression} ngKeyup {@link guide/expression Expression} to evaluate upon
 * keyup. (Event object is available as `$event` and can be interrogated for keyCode, altKey, etc.)
 *
 * @example
   <example name="ng-keyup">
     <file name="index.html">
       <p>Typing in the input box below updates the key count</p>
       <input ng-keyup="count = count + 1" ng-init="count=0"> key up count: {{count}}

       <p>Typing in the input box below updates the keycode</p>
       <input ng-keyup="event=$event">
       <p>event keyCode: {{ event.keyCode }}</p>
       <p>event altKey: {{ event.altKey }}</p>
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngKeypress
 * @restrict A
 * @element ANY
 *
 * @description
 * Specify custom behavior on keypress event.
 *
 * @param {expression} ngKeypress {@link guide/expression Expression} to evaluate upon
 * keypress. ({@link guide/expression#-event- Event object is available as `$event`}
 * and can be interrogated for keyCode, altKey, etc.)
 *
 * @example
   <example name="ng-keypress">
     <file name="index.html">
      <input ng-keypress="count = count + 1" ng-init="count=0">
      key press count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngSubmit
 * @restrict A
 * @element form
 * @priority 0
 *
 * @description
 * Enables binding AngularJS expressions to onsubmit events.
 *
 * Additionally it prevents the default action (which for form means sending the request to the
 * server and reloading the current page), but only if the form does not contain `action`,
 * `data-action`, or `x-action` attributes.
 *
 * <div class="alert alert-warning">
 * **Warning:** Be careful not to cause "double-submission" by using both the `ngClick` and
 * `ngSubmit` handlers together. See the
 * {@link form#submitting-a-form-and-preventing-the-default-action `form` directive documentation}
 * for a detailed discussion of when `ngSubmit` may be triggered.
 * </div>
 *
 * @param {expression} ngSubmit {@link guide/expression Expression} to eval.
 * ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example module="submitExample" name="ng-submit">
     <file name="index.html">
      <script>
        angular.module('submitExample', [])
          .controller('ExampleController', ['$scope', function($scope) {
            $scope.list = [];
            $scope.text = 'hello';
            $scope.submit = function() {
              if ($scope.text) {
                $scope.list.push(this.text);
                $scope.text = '';
              }
            };
          }]);
      </script>
      <form ng-submit="submit()" ng-controller="ExampleController">
        Enter text and hit enter:
        <input type="text" ng-model="text" name="text" />
        <input type="submit" id="submit" value="Submit" />
        <pre>list={{list}}</pre>
      </form>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-submit', function() {
         expect(element(by.binding('list')).getText()).toBe('list=[]');
         element(by.css('#submit')).click();
         expect(element(by.binding('list')).getText()).toContain('hello');
         expect(element(by.model('text')).getAttribute('value')).toBe('');
       });
       it('should ignore empty strings', function() {
         expect(element(by.binding('list')).getText()).toBe('list=[]');
         element(by.css('#submit')).click();
         element(by.css('#submit')).click();
         expect(element(by.binding('list')).getText()).toContain('hello');
        });
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngFocus
 * @restrict A
 * @element window, input, select, textarea, a
 * @priority 0
 *
 * @description
 * Specify custom behavior on focus event.
 *
 * Note: As the `focus` event is executed synchronously when calling `input.focus()`
 * AngularJS executes the expression using `scope.$evalAsync` if the event is fired
 * during an `$apply` to ensure a consistent state.
 *
 * @param {expression} ngFocus {@link guide/expression Expression} to evaluate upon
 * focus. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
 * See {@link ng.directive:ngClick ngClick}
 */

/**
 * @ngdoc directive
 * @name ngBlur
 * @restrict A
 * @element window, input, select, textarea, a
 * @priority 0
 *
 * @description
 * Specify custom behavior on blur event.
 *
 * A [blur event](https://developer.mozilla.org/en-US/docs/Web/Events/blur) fires when
 * an element has lost focus.
 *
 * Note: As the `blur` event is executed synchronously also during DOM manipulations
 * (e.g. removing a focussed input),
 * AngularJS executes the expression using `scope.$evalAsync` if the event is fired
 * during an `$apply` to ensure a consistent state.
 *
 * @param {expression} ngBlur {@link guide/expression Expression} to evaluate upon
 * blur. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
 * See {@link ng.directive:ngClick ngClick}
 */

/**
 * @ngdoc directive
 * @name ngCopy
 * @restrict A
 * @element window, input, select, textarea, a
 * @priority 0
 *
 * @description
 * Specify custom behavior on copy event.
 *
 * @param {expression} ngCopy {@link guide/expression Expression} to evaluate upon
 * copy. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-copy">
     <file name="index.html">
      <input ng-copy="copied=true" ng-init="copied=false; value='copy me'" ng-model="value">
      copied: {{copied}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngCut
 * @restrict A
 * @element window, input, select, textarea, a
 * @priority 0
 *
 * @description
 * Specify custom behavior on cut event.
 *
 * @param {expression} ngCut {@link guide/expression Expression} to evaluate upon
 * cut. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-cut">
     <file name="index.html">
      <input ng-cut="cut=true" ng-init="cut=false; value='cut me'" ng-model="value">
      cut: {{cut}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngPaste
 * @restrict A
 * @element window, input, select, textarea, a
 * @priority 0
 *
 * @description
 * Specify custom behavior on paste event.
 *
 * @param {expression} ngPaste {@link guide/expression Expression} to evaluate upon
 * paste. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-paste">
     <file name="index.html">
      <input ng-paste="paste=true" ng-init="paste=false" placeholder='paste here'>
      pasted: {{paste}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngIf
 * @restrict A
 * @multiElement
 *
 * @description
 * The `ngIf` directive removes or recreates a portion of the DOM tree based on an
 * {expression}. If the expression assigned to `ngIf` evaluates to a false
 * value then the element is removed from the DOM, otherwise a clone of the
 * element is reinserted into the DOM.
 *
 * `ngIf` differs from `ngShow` and `ngHide` in that `ngIf` completely removes and recreates the
 * element in the DOM rather than changing its visibility via the `display` css property.  A common
 * case when this difference is significant is when using css selectors that rely on an element's
 * position within the DOM, such as the `:first-child` or `:last-child` pseudo-classes.
 *
 * Note that when an element is removed using `ngIf` its scope is destroyed and a new scope
 * is created when the element is restored.  The scope created within `ngIf` inherits from
 * its parent scope using
 * [prototypal inheritance](https://github.com/angular/angular.js/wiki/Understanding-Scopes#javascript-prototypal-inheritance).
 * An important implication of this is if `ngModel` is used within `ngIf` to bind to
 * a javascript primitive defined in the parent scope. In this case any modifications made to the
 * variable within the child scope will override (hide) the value in the parent scope.
 *
 * Also, `ngIf` recreates elements using their compiled state. An example of this behavior
 * is if an element's class attribute is directly modified after it's compiled, using something like
 * jQuery's `.addClass()` method, and the element is later removed. When `ngIf` recreates the element
 * the added class will be lost because the original compiled state is used to regenerate the element.
 *
 * Additionally, you can provide animations via the `ngAnimate` module to animate the `enter`
 * and `leave` effects.
 *
 * @animations
 * | Animation                        | Occurs                               |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | just after the `ngIf` contents change and a new DOM element is created and injected into the `ngIf` container |
 * | {@link ng.$animate#leave leave}  | just before the `ngIf` contents are removed from the DOM |
 *
 * @element ANY
 * @scope
 * @priority 600
 * @param {expression} ngIf If the {@link guide/expression expression} is falsy then
 *     the element is removed from the DOM tree. If it is truthy a copy of the compiled
 *     element is added to the DOM tree.
 *
 * @example
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-if">
    <file name="index.html">
      <label>Click me: <input type="checkbox" ng-model="checked" ng-init="checked=true" /></label><br/>
      Show when checked:
      <span ng-if="checked" class="animate-if">
        This is removed when the checkbox is unchecked.
      </span>
    </file>
    <file name="animations.css">
      .animate-if {
        background:white;
        border:1px solid black;
        padding:10px;
      }

      .animate-if.ng-enter, .animate-if.ng-leave {
        transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
      }

      .animate-if.ng-enter,
      .animate-if.ng-leave.ng-leave-active {
        opacity:0;
      }

      .animate-if.ng-leave,
      .animate-if.ng-enter.ng-enter-active {
        opacity:1;
      }
    </file>
  </example>
 */
var ngIfDirective = ['$animate', '$compile', function($animate, $compile) {
  return {
    multiElement: true,
    transclude: 'element',
    priority: 600,
    terminal: true,
    restrict: 'A',
    $$tlb: true,
    link: function($scope, $element, $attr, ctrl, $transclude) {
        var block, childScope, previousElements;
        $scope.$watch($attr.ngIf, function ngIfWatchAction(value) {

          if (value) {
            if (!childScope) {
              $transclude(function(clone, newScope) {
                childScope = newScope;
                clone[clone.length++] = $compile.$$createComment('end ngIf', $attr.ngIf);
                // Note: We only need the first/last node of the cloned nodes.
                // However, we need to keep the reference to the jqlite wrapper as it might be changed later
                // by a directive with templateUrl when its template arrives.
                block = {
                  clone: clone
                };
                $animate.enter(clone, $element.parent(), $element);
              });
            }
          } else {
            if (previousElements) {
              previousElements.remove();
              previousElements = null;
            }
            if (childScope) {
              childScope.$destroy();
              childScope = null;
            }
            if (block) {
              previousElements = getBlockNodes(block.clone);
              $animate.leave(previousElements).done(function(response) {
                if (response !== false) previousElements = null;
              });
              block = null;
            }
          }
        });
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngInclude
 * @restrict ECA
 * @scope
 * @priority -400
 *
 * @description
 * Fetches, compiles and includes an external HTML fragment.
 *
 * By default, the template URL is restricted to the same domain and protocol as the
 * application document. This is done by calling {@link $sce#getTrustedResourceUrl
 * $sce.getTrustedResourceUrl} on it. To load templates from other domains or protocols
 * you may either add them to your {@link ng.$sceDelegateProvider#trustedResourceUrlList trusted
 * resource URL list} or {@link $sce#trustAsResourceUrl wrap them} as trusted values. Refer to
 * AngularJS's {@link ng.$sce Strict Contextual Escaping}.
 *
 * In addition, the browser's
 * [Same Origin Policy](https://code.google.com/p/browsersec/wiki/Part2#Same-origin_policy_for_XMLHttpRequest)
 * and [Cross-Origin Resource Sharing (CORS)](http://www.w3.org/TR/cors/)
 * policy may further restrict whether the template is successfully loaded.
 * For example, `ngInclude` won't work for cross-domain requests on all browsers and for `file://`
 * access on some browsers.
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | when the expression changes, on the new include |
 * | {@link ng.$animate#leave leave}  | when the expression changes, on the old include |
 *
 * The enter and leave animation occur concurrently.
 *
 * @param {string} ngInclude|src AngularJS expression evaluating to URL. If the source is a string constant,
 *                 make sure you wrap it in **single** quotes, e.g. `src="'myPartialTemplate.html'"`.
 * @param {string=} onload Expression to evaluate when a new partial is loaded.
 *                  <div class="alert alert-warning">
 *                  **Note:** When using onload on SVG elements in IE11, the browser will try to call
 *                  a function with the name on the window element, which will usually throw a
 *                  "function is undefined" error. To fix this, you can instead use `data-onload` or a
 *                  different form that {@link guide/directive#normalization matches} `onload`.
 *                  </div>
   *
 * @param {string=} autoscroll Whether `ngInclude` should call {@link ng.$anchorScroll
 *                  $anchorScroll} to scroll the viewport after the content is loaded.
 *
 *                  - If the attribute is not set, disable scrolling.
 *                  - If the attribute is set without value, enable scrolling.
 *                  - Otherwise enable scrolling only if the expression evaluates to truthy value.
 *
 * @example
  <example module="includeExample" deps="angular-animate.js" animations="true" name="ng-include">
    <file name="index.html">
     <div ng-controller="ExampleController">
       <select ng-model="template" ng-options="t.name for t in templates">
        <option value="">(blank)</option>
       </select>
       url of the template: <code>{{template.url}}</code>
       <hr/>
       <div class="slide-animate-container">
         <div class="slide-animate" ng-include="template.url"></div>
       </div>
     </div>
    </file>
    <file name="script.js">
      angular.module('includeExample', ['ngAnimate'])
        .controller('ExampleController', ['$scope', function($scope) {
          $scope.templates =
            [{ name: 'template1.html', url: 'template1.html'},
             { name: 'template2.html', url: 'template2.html'}];
          $scope.template = $scope.templates[0];
        }]);
     </file>
    <file name="template1.html">
      Content of template1.html
    </file>
    <file name="template2.html">
      Content of template2.html
    </file>
    <file name="animations.css">
      .slide-animate-container {
        position:relative;
        background:white;
        border:1px solid black;
        height:40px;
        overflow:hidden;
      }

      .slide-animate {
        padding:10px;
      }

      .slide-animate.ng-enter, .slide-animate.ng-leave {
        transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;

        position:absolute;
        top:0;
        left:0;
        right:0;
        bottom:0;
        display:block;
        padding:10px;
      }

      .slide-animate.ng-enter {
        top:-50px;
      }
      .slide-animate.ng-enter.ng-enter-active {
        top:0;
      }

      .slide-animate.ng-leave {
        top:0;
      }
      .slide-animate.ng-leave.ng-leave-active {
        top:50px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      var templateSelect = element(by.model('template'));
      var includeElem = element(by.css('[ng-include]'));

      it('should load template1.html', function() {
        expect(includeElem.getText()).toMatch(/Content of template1.html/);
      });

      it('should load template2.html', function() {
        if (browser.params.browser === 'firefox') {
          // Firefox can't handle using selects
          // See https://github.com/angular/protractor/issues/480
          return;
        }
        templateSelect.click();
        templateSelect.all(by.css('option')).get(2).click();
        expect(includeElem.getText()).toMatch(/Content of template2.html/);
      });

      it('should change to blank', function() {
        if (browser.params.browser === 'firefox') {
          // Firefox can't handle using selects
          return;
        }
        templateSelect.click();
        templateSelect.all(by.css('option')).get(0).click();
        expect(includeElem.isPresent()).toBe(false);
      });
    </file>
  </example>
 */


/**
 * @ngdoc event
 * @name ngInclude#$includeContentRequested
 * @eventType emit on the scope ngInclude was declared in
 * @description
 * Emitted every time the ngInclude content is requested.
 *
 * @param {Object} angularEvent Synthetic event object.
 * @param {String} src URL of content to load.
 */


/**
 * @ngdoc event
 * @name ngInclude#$includeContentLoaded
 * @eventType emit on the current ngInclude scope
 * @description
 * Emitted every time the ngInclude content is reloaded.
 *
 * @param {Object} angularEvent Synthetic event object.
 * @param {String} src URL of content to load.
 */


/**
 * @ngdoc event
 * @name ngInclude#$includeContentError
 * @eventType emit on the scope ngInclude was declared in
 * @description
 * Emitted when a template HTTP request yields an erroneous response (status < 200 || status > 299)
 *
 * @param {Object} angularEvent Synthetic event object.
 * @param {String} src URL of content to load.
 */
var ngIncludeDirective = ['$templateRequest', '$anchorScroll', '$animate',
                  function($templateRequest,   $anchorScroll,   $animate) {
  return {
    restrict: 'ECA',
    priority: 400,
    terminal: true,
    transclude: 'element',
    controller: angular.noop,
    compile: function(element, attr) {
      var srcExp = attr.ngInclude || attr.src,
          onloadExp = attr.onload || '',
          autoScrollExp = attr.autoscroll;

      return function(scope, $element, $attr, ctrl, $transclude) {
        var changeCounter = 0,
            currentScope,
            previousElement,
            currentElement;

        var cleanupLastIncludeContent = function() {
          if (previousElement) {
            previousElement.remove();
            previousElement = null;
          }
          if (currentScope) {
            currentScope.$destroy();
            currentScope = null;
          }
          if (currentElement) {
            $animate.leave(currentElement).done(function(response) {
              if (response !== false) previousElement = null;
            });
            previousElement = currentElement;
            currentElement = null;
          }
        };

        scope.$watch(srcExp, function ngIncludeWatchAction(src) {
          var afterAnimation = function(response) {
            if (response !== false && isDefined(autoScrollExp) &&
              (!autoScrollExp || scope.$eval(autoScrollExp))) {
                $anchorScroll();
            }
          };
          var thisChangeId = ++changeCounter;

          if (src) {
            //set the 2nd param to true to ignore the template request error so that the inner
            //contents and scope can be cleaned up.
            $templateRequest(src, true).then(function(response) {
              if (scope.$$destroyed) return;

              if (thisChangeId !== changeCounter) return;
              var newScope = scope.$new();
              ctrl.template = response;

              // Note: This will also link all children of ng-include that were contained in the original
              // html. If that content contains controllers, ... they could pollute/change the scope.
              // However, using ng-include on an element with additional content does not make sense...
              // Note: We can't remove them in the cloneAttchFn of $transclude as that
              // function is called before linking the content, which would apply child
              // directives to non existing elements.
              var clone = $transclude(newScope, function(clone) {
                cleanupLastIncludeContent();
                $animate.enter(clone, null, $element).done(afterAnimation);
              });

              currentScope = newScope;
              currentElement = clone;

              currentScope.$emit('$includeContentLoaded', src);
              scope.$eval(onloadExp);
            }, function() {
              if (scope.$$destroyed) return;

              if (thisChangeId === changeCounter) {
                cleanupLastIncludeContent();
                scope.$emit('$includeContentError', src);
              }
            });
            scope.$emit('$includeContentRequested', src);
          } else {
            cleanupLastIncludeContent();
            ctrl.template = null;
          }
        });
      };
    }
  };
}];

// This directive is called during the $transclude call of the first `ngInclude` directive.
// It will replace and compile the content of the element with the loaded template.
// We need this directive so that the element content is already filled when
// the link function of another directive on the same element as ngInclude
// is called.
var ngIncludeFillContentDirective = ['$compile',
  function($compile) {
    return {
      restrict: 'ECA',
      priority: -400,
      require: 'ngInclude',
      link: function(scope, $element, $attr, ctrl) {
        if (toString.call($element[0]).match(/SVG/)) {
          // WebKit: https://bugs.webkit.org/show_bug.cgi?id=135698 --- SVG elements do not
          // support innerHTML, so detect this here and try to generate the contents
          // specially.
          $element.empty();
          $compile(jqLiteBuildFragment(ctrl.template, window.document).childNodes)(scope,
              function namespaceAdaptedClone(clone) {
            $element.append(clone);
          }, {futureParentElement: $element});
          return;
        }

        $element.html(ctrl.template);
        $compile($element.contents())(scope);
      }
    };
  }];

/**
 * @ngdoc directive
 * @name ngInit
 * @restrict AC
 * @priority 450
 * @element ANY
 *
 * @param {expression} ngInit {@link guide/expression Expression} to eval.
 *
 * @description
 * The `ngInit` directive allows you to evaluate an expression in the
 * current scope.
 *
 * <div class="alert alert-danger">
 * This directive can be abused to add unnecessary amounts of logic into your templates.
 * There are only a few appropriate uses of `ngInit`:
 * <ul>
 *   <li>aliasing special properties of {@link ng.directive:ngRepeat `ngRepeat`},
 *     as seen in the demo below.</li>
 *   <li>initializing data during development, or for examples, as seen throughout these docs.</li>
 *   <li>injecting data via server side scripting.</li>
 * </ul>
 *
 * Besides these few cases, you should use {@link guide/component Components} or
 * {@link guide/controller Controllers} rather than `ngInit` to initialize values on a scope.
 * </div>
 *
 * <div class="alert alert-warning">
 * **Note**: If you have assignment in `ngInit` along with a {@link ng.$filter `filter`}, make
 * sure you have parentheses to ensure correct operator precedence:
 * <pre class="prettyprint">
 * `<div ng-init="test1 = ($index | toString)"></div>`
 * </pre>
 * </div>
 *
 * @example
   <example module="initExample" name="ng-init">
     <file name="index.html">
   <script>
     angular.module('initExample', [])
       .controller('ExampleController', ['$scope', function($scope) {
         $scope.list = [['a', 'b'], ['c', 'd']];
       }]);
   </script>
   <div ng-controller="ExampleController">
     <div ng-repeat="innerList in list" ng-init="outerIndex = $index">
       <div ng-repeat="value in innerList" ng-init="innerIndex = $index">
          <span class="example-init">list[ {{outerIndex}} ][ {{innerIndex}} ] = {{value}};</span>
       </div>
     </div>
   </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should alias index positions', function() {
         var elements = element.all(by.css('.example-init'));
         expect(elements.get(0).getText()).toBe('list[ 0 ][ 0 ] = a;');
         expect(elements.get(1).getText()).toBe('list[ 0 ][ 1 ] = b;');
         expect(elements.get(2).getText()).toBe('list[ 1 ][ 0 ] = c;');
         expect(elements.get(3).getText()).toBe('list[ 1 ][ 1 ] = d;');
       });
     </file>
   </example>
 */
var ngInitDirective = ngDirective({
  priority: 450,
  compile: function() {
    return {
      pre: function(scope, element, attrs) {
        scope.$eval(attrs.ngInit);
      }
    };
  }
});

/**
 * @ngdoc directive
 * @name ngList
 * @restrict A
 * @priority 100
 *
 * @param {string=} ngList optional delimiter that should be used to split the value.
 *
 * @description
 * Text input that converts between a delimited string and an array of strings. The default
 * delimiter is a comma followed by a space - equivalent to `ng-list=", "`. You can specify a custom
 * delimiter as the value of the `ngList` attribute - for example, `ng-list=" | "`.
 *
 * The behaviour of the directive is affected by the use of the `ngTrim` attribute.
 * * If `ngTrim` is set to `"false"` then whitespace around both the separator and each
 *   list item is respected. This implies that the user of the directive is responsible for
 *   dealing with whitespace but also allows you to use whitespace as a delimiter, such as a
 *   tab or newline character.
 * * Otherwise whitespace around the delimiter is ignored when splitting (although it is respected
 *   when joining the list items back together) and whitespace around each list item is stripped
 *   before it is added to the model.
 *
 * @example
 * ### Validation
 *
 * <example name="ngList-directive" module="listExample">
 *   <file name="app.js">
 *      angular.module('listExample', [])
 *        .controller('ExampleController', ['$scope', function($scope) {
 *          $scope.names = ['morpheus', 'neo', 'trinity'];
 *        }]);
 *   </file>
 *   <file name="index.html">
 *    <form name="myForm" ng-controller="ExampleController">
 *      <label>List: <input name="namesInput" ng-model="names" ng-list required></label>
 *      <span role="alert">
 *        <span class="error" ng-show="myForm.namesInput.$error.required">
 *        Required!</span>
 *      </span>
 *      <br>
 *      <tt>names = {{names}}</tt><br/>
 *      <tt>myForm.namesInput.$valid = {{myForm.namesInput.$valid}}</tt><br/>
 *      <tt>myForm.namesInput.$error = {{myForm.namesInput.$error}}</tt><br/>
 *      <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
 *      <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
 *     </form>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     var listInput = element(by.model('names'));
 *     var names = element(by.exactBinding('names'));
 *     var valid = element(by.binding('myForm.namesInput.$valid'));
 *     var error = element(by.css('span.error'));
 *
 *     it('should initialize to model', function() {
 *       expect(names.getText()).toContain('["morpheus","neo","trinity"]');
 *       expect(valid.getText()).toContain('true');
 *       expect(error.getCssValue('display')).toBe('none');
 *     });
 *
 *     it('should be invalid if empty', function() {
 *       listInput.clear();
 *       listInput.sendKeys('');
 *
 *       expect(names.getText()).toContain('');
 *       expect(valid.getText()).toContain('false');
 *       expect(error.getCssValue('display')).not.toBe('none');
 *     });
 *   </file>
 * </example>
 *
 * @example
 * ### Splitting on newline
 *
 * <example name="ngList-directive-newlines">
 *   <file name="index.html">
 *    <textarea ng-model="list" ng-list="&#10;" ng-trim="false"></textarea>
 *    <pre>{{ list | json }}</pre>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     it("should split the text by newlines", function() {
 *       var listInput = element(by.model('list'));
 *       var output = element(by.binding('list | json'));
 *       listInput.sendKeys('abc\ndef\nghi');
 *       expect(output.getText()).toContain('[\n  "abc",\n  "def",\n  "ghi"\n]');
 *     });
 *   </file>
 * </example>
 *
 */
var ngListDirective = function() {
  return {
    restrict: 'A',
    priority: 100,
    require: 'ngModel',
    link: function(scope, element, attr, ctrl) {
      var ngList = attr.ngList || ', ';
      var trimValues = attr.ngTrim !== 'false';
      var separator = trimValues ? trim(ngList) : ngList;

      var parse = function(viewValue) {
        // If the viewValue is invalid (say required but empty) it will be `undefined`
        if (isUndefined(viewValue)) return;

        var list = [];

        if (viewValue) {
          forEach(viewValue.split(separator), function(value) {
            if (value) list.push(trimValues ? trim(value) : value);
          });
        }

        return list;
      };

      ctrl.$parsers.push(parse);
      ctrl.$formatters.push(function(value) {
        if (isArray(value)) {
          return value.join(ngList);
        }

        return undefined;
      });

      // Override the standard $isEmpty because an empty array means the input is empty.
      ctrl.$isEmpty = function(value) {
        return !value || !value.length;
      };
    }
  };
};

/* global VALID_CLASS: true,
  INVALID_CLASS: true,
  PRISTINE_CLASS: true,
  DIRTY_CLASS: true,
  UNTOUCHED_CLASS: true,
  TOUCHED_CLASS: true,
  PENDING_CLASS: true,
  addSetValidityMethod: true,
  setupValidity: true,
  defaultModelOptions: false
*/


var VALID_CLASS = 'ng-valid',
    INVALID_CLASS = 'ng-invalid',
    PRISTINE_CLASS = 'ng-pristine',
    DIRTY_CLASS = 'ng-dirty',
    UNTOUCHED_CLASS = 'ng-untouched',
    TOUCHED_CLASS = 'ng-touched',
    EMPTY_CLASS = 'ng-empty',
    NOT_EMPTY_CLASS = 'ng-not-empty';

var ngModelMinErr = minErr('ngModel');

/**
 * @ngdoc type
 * @name ngModel.NgModelController
 * @property {*} $viewValue The actual value from the control's view. For `input` elements, this is a
 * String. See {@link ngModel.NgModelController#$setViewValue} for information about when the $viewValue
 * is set.
 *
 * @property {*} $modelValue The value in the model that the control is bound to.
 *
 * @property {Array.<Function>} $parsers Array of functions to execute, as a pipeline, whenever
 *  the control updates the ngModelController with a new {@link ngModel.NgModelController#$viewValue
    `$viewValue`} from the DOM, usually via user input.
    See {@link ngModel.NgModelController#$setViewValue `$setViewValue()`} for a detailed lifecycle explanation.
    Note that the `$parsers` are not called when the bound ngModel expression changes programmatically.

  The functions are called in array order, each passing
    its return value through to the next. The last return value is forwarded to the
    {@link ngModel.NgModelController#$validators `$validators`} collection.

  Parsers are used to sanitize / convert the {@link ngModel.NgModelController#$viewValue
    `$viewValue`}.

  Returning `undefined` from a parser means a parse error occurred. In that case,
    no {@link ngModel.NgModelController#$validators `$validators`} will run and the `ngModel`
    will be set to `undefined` unless {@link ngModelOptions `ngModelOptions.allowInvalid`}
    is set to `true`. The parse error is stored in `ngModel.$error.parse`.

  This simple example shows a parser that would convert text input value to lowercase:
 * ```js
 * function parse(value) {
 *   if (value) {
 *     return value.toLowerCase();
 *   }
 * }
 * ngModelController.$parsers.push(parse);
 * ```

 *
 * @property {Array.<Function>} $formatters Array of functions to execute, as a pipeline, whenever
    the bound ngModel expression changes programmatically. The `$formatters` are not called when the
    value of the control is changed by user interaction.

  Formatters are used to format / convert the {@link ngModel.NgModelController#$modelValue
    `$modelValue`} for display in the control.

  The functions are called in reverse array order, each passing the value through to the
    next. The last return value is used as the actual DOM value.

  This simple example shows a formatter that would convert the model value to uppercase:

 * ```js
 * function format(value) {
 *   if (value) {
 *     return value.toUpperCase();
 *   }
 * }
 * ngModel.$formatters.push(format);
 * ```
 *
 * @property {Object.<string, function>} $validators A collection of validators that are applied
 *      whenever the model value changes. The key value within the object refers to the name of the
 *      validator while the function refers to the validation operation. The validation operation is
 *      provided with the model value as an argument and must return a true or false value depending
 *      on the response of that validation.
 *
 * ```js
 * ngModel.$validators.validCharacters = function(modelValue, viewValue) {
 *   var value = modelValue || viewValue;
 *   return /[0-9]+/.test(value) &&
 *          /[a-z]+/.test(value) &&
 *          /[A-Z]+/.test(value) &&
 *          /\W+/.test(value);
 * };
 * ```
 *
 * @property {Object.<string, function>} $asyncValidators A collection of validations that are expected to
 *      perform an asynchronous validation (e.g. a HTTP request). The validation function that is provided
 *      is expected to return a promise when it is run during the model validation process. Once the promise
 *      is delivered then the validation status will be set to true when fulfilled and false when rejected.
 *      When the asynchronous validators are triggered, each of the validators will run in parallel and the model
 *      value will only be updated once all validators have been fulfilled. As long as an asynchronous validator
 *      is unfulfilled, its key will be added to the controllers `$pending` property. Also, all asynchronous validators
 *      will only run once all synchronous validators have passed.
 *
 * Please note that if $http is used then it is important that the server returns a success HTTP response code
 * in order to fulfill the validation and a status level of `4xx` in order to reject the validation.
 *
 * ```js
 * ngModel.$asyncValidators.uniqueUsername = function(modelValue, viewValue) {
 *   var value = modelValue || viewValue;
 *
 *   // Lookup user by username
 *   return $http.get('/api/users/' + value).
 *      then(function resolved() {
 *        //username exists, this means validation fails
 *        return $q.reject('exists');
 *      }, function rejected() {
 *        //username does not exist, therefore this validation passes
 *        return true;
 *      });
 * };
 * ```
 *
 * @property {Array.<Function>} $viewChangeListeners Array of functions to execute whenever
 *     a change to {@link ngModel.NgModelController#$viewValue `$viewValue`} has caused a change
 *     to {@link ngModel.NgModelController#$modelValue `$modelValue`}.
 *     It is called with no arguments, and its return value is ignored.
 *     This can be used in place of additional $watches against the model value.
 *
 * @property {Object} $error An object hash with all failing validator ids as keys.
 * @property {Object} $pending An object hash with all pending validator ids as keys.
 *
 * @property {boolean} $untouched True if control has not lost focus yet.
 * @property {boolean} $touched True if control has lost focus.
 * @property {boolean} $pristine True if user has not interacted with the control yet.
 * @property {boolean} $dirty True if user has already interacted with the control.
 * @property {boolean} $valid True if there is no error.
 * @property {boolean} $invalid True if at least one error on the control.
 * @property {string} $name The name attribute of the control.
 *
 * @description
 *
 * `NgModelController` provides API for the {@link ngModel `ngModel`} directive.
 * The controller contains services for data-binding, validation, CSS updates, and value formatting
 * and parsing. It purposefully does not contain any logic which deals with DOM rendering or
 * listening to DOM events.
 * Such DOM related logic should be provided by other directives which make use of
 * `NgModelController` for data-binding to control elements.
 * AngularJS provides this DOM logic for most {@link input `input`} elements.
 * At the end of this page you can find a {@link ngModel.NgModelController#custom-control-example
 * custom control example} that uses `ngModelController` to bind to `contenteditable` elements.
 *
 * @example
 * ### Custom Control Example
 * This example shows how to use `NgModelController` with a custom control to achieve
 * data-binding. Notice how different directives (`contenteditable`, `ng-model`, and `required`)
 * collaborate together to achieve the desired result.
 *
 * `contenteditable` is an HTML5 attribute, which tells the browser to let the element
 * contents be edited in place by the user.
 *
 * We are using the {@link ng.service:$sce $sce} service here and include the {@link ngSanitize $sanitize}
 * module to automatically remove "bad" content like inline event listener (e.g. `<span onclick="...">`).
 * However, as we are using `$sce` the model can still decide to provide unsafe content if it marks
 * that content using the `$sce` service.
 *
 * <example name="NgModelController" module="customControl" deps="angular-sanitize.js">
    <file name="style.css">
      [contenteditable] {
        border: 1px solid black;
        background-color: white;
        min-height: 20px;
      }

      .ng-invalid {
        border: 1px solid red;
      }

    </file>
    <file name="script.js">
      angular.module('customControl', ['ngSanitize']).
        directive('contenteditable', ['$sce', function($sce) {
          return {
            restrict: 'A', // only activate on element attribute
            require: '?ngModel', // get a hold of NgModelController
            link: function(scope, element, attrs, ngModel) {
              if (!ngModel) return; // do nothing if no ng-model

              // Specify how UI should be updated
              ngModel.$render = function() {
                element.html($sce.getTrustedHtml(ngModel.$viewValue || ''));
              };

              // Listen for change events to enable binding
              element.on('blur keyup change', function() {
                scope.$evalAsync(read);
              });
              read(); // initialize

              // Write data to the model
              function read() {
                var html = element.html();
                // When we clear the content editable the browser leaves a <br> behind
                // If strip-br attribute is provided then we strip this out
                if (attrs.stripBr && html === '<br>') {
                  html = '';
                }
                ngModel.$setViewValue(html);
              }
            }
          };
        }]);
    </file>
    <file name="index.html">
      <form name="myForm">
       <div contenteditable
            name="myWidget" ng-model="userContent"
            strip-br="true"
            required>Change me!</div>
        <span ng-show="myForm.myWidget.$error.required">Required!</span>
       <hr>
       <textarea ng-model="userContent" aria-label="Dynamic textarea"></textarea>
      </form>
    </file>
    <file name="protractor.js" type="protractor">
    it('should data-bind and become invalid', function() {
      if (browser.params.browser === 'safari' || browser.params.browser === 'firefox') {
        // SafariDriver can't handle contenteditable
        // and Firefox driver can't clear contenteditables very well
        return;
      }
      var contentEditable = element(by.css('[contenteditable]'));
      var content = 'Change me!';

      expect(contentEditable.getText()).toEqual(content);

      contentEditable.clear();
      contentEditable.sendKeys(protractor.Key.BACK_SPACE);
      expect(contentEditable.getText()).toEqual('');
      expect(contentEditable.getAttribute('class')).toMatch(/ng-invalid-required/);
    });
    </file>
 * </example>
 *
 *
 */
NgModelController.$inject = ['$scope', '$exceptionHandler', '$attrs', '$element', '$parse', '$animate', '$timeout', '$q', '$interpolate'];
function NgModelController($scope, $exceptionHandler, $attr, $element, $parse, $animate, $timeout, $q, $interpolate) {
  this.$viewValue = Number.NaN;
  this.$modelValue = Number.NaN;
  this.$$rawModelValue = undefined; // stores the parsed modelValue / model set from scope regardless of validity.
  this.$validators = {};
  this.$asyncValidators = {};
  this.$parsers = [];
  this.$formatters = [];
  this.$viewChangeListeners = [];
  this.$untouched = true;
  this.$touched = false;
  this.$pristine = true;
  this.$dirty = false;
  this.$valid = true;
  this.$invalid = false;
  this.$error = {}; // keep invalid keys here
  this.$$success = {}; // keep valid keys here
  this.$pending = undefined; // keep pending keys here
  this.$name = $interpolate($attr.name || '', false)($scope);
  this.$$parentForm = nullFormCtrl;
  this.$options = defaultModelOptions;
  this.$$updateEvents = '';
  // Attach the correct context to the event handler function for updateOn
  this.$$updateEventHandler = this.$$updateEventHandler.bind(this);

  this.$$parsedNgModel = $parse($attr.ngModel);
  this.$$parsedNgModelAssign = this.$$parsedNgModel.assign;
  this.$$ngModelGet = this.$$parsedNgModel;
  this.$$ngModelSet = this.$$parsedNgModelAssign;
  this.$$pendingDebounce = null;
  this.$$parserValid = undefined;
  this.$$parserName = 'parse';

  this.$$currentValidationRunId = 0;

  this.$$scope = $scope;
  this.$$rootScope = $scope.$root;
  this.$$attr = $attr;
  this.$$element = $element;
  this.$$animate = $animate;
  this.$$timeout = $timeout;
  this.$$parse = $parse;
  this.$$q = $q;
  this.$$exceptionHandler = $exceptionHandler;

  setupValidity(this);
  setupModelWatcher(this);
}

NgModelController.prototype = {
  $$initGetterSetters: function() {
    if (this.$options.getOption('getterSetter')) {
      var invokeModelGetter = this.$$parse(this.$$attr.ngModel + '()'),
          invokeModelSetter = this.$$parse(this.$$attr.ngModel + '($$$p)');

      this.$$ngModelGet = function($scope) {
        var modelValue = this.$$parsedNgModel($scope);
        if (isFunction(modelValue)) {
          modelValue = invokeModelGetter($scope);
        }
        return modelValue;
      };
      this.$$ngModelSet = function($scope, newValue) {
        if (isFunction(this.$$parsedNgModel($scope))) {
          invokeModelSetter($scope, {$$$p: newValue});
        } else {
          this.$$parsedNgModelAssign($scope, newValue);
        }
      };
    } else if (!this.$$parsedNgModel.assign) {
      throw ngModelMinErr('nonassign', 'Expression \'{0}\' is non-assignable. Element: {1}',
          this.$$attr.ngModel, startingTag(this.$$element));
    }
  },


  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$render
   *
   * @description
   * Called when the view needs to be updated. It is expected that the user of the ng-model
   * directive will implement this method.
   *
   * The `$render()` method is invoked in the following situations:
   *
   * * `$rollbackViewValue()` is called.  If we are rolling back the view value to the last
   *   committed value then `$render()` is called to update the input control.
   * * The value referenced by `ng-model` is changed programmatically and both the `$modelValue` and
   *   the `$viewValue` are different from last time.
   *
   * Since `ng-model` does not do a deep watch, `$render()` is only invoked if the values of
   * `$modelValue` and `$viewValue` are actually different from their previous values. If `$modelValue`
   * or `$viewValue` are objects (rather than a string or number) then `$render()` will not be
   * invoked if you only change a property on the objects.
   */
  $render: noop,

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$isEmpty
   *
   * @description
   * This is called when we need to determine if the value of an input is empty.
   *
   * For instance, the required directive does this to work out if the input has data or not.
   *
   * The default `$isEmpty` function checks whether the value is `undefined`, `''`, `null` or `NaN`.
   *
   * You can override this for input directives whose concept of being empty is different from the
   * default. The `checkboxInputType` directive does this because in its case a value of `false`
   * implies empty.
   *
   * @param {*} value The value of the input to check for emptiness.
   * @returns {boolean} True if `value` is "empty".
   */
  $isEmpty: function(value) {
    // eslint-disable-next-line no-self-compare
    return isUndefined(value) || value === '' || value === null || value !== value;
  },

  $$updateEmptyClasses: function(value) {
    if (this.$isEmpty(value)) {
      this.$$animate.removeClass(this.$$element, NOT_EMPTY_CLASS);
      this.$$animate.addClass(this.$$element, EMPTY_CLASS);
    } else {
      this.$$animate.removeClass(this.$$element, EMPTY_CLASS);
      this.$$animate.addClass(this.$$element, NOT_EMPTY_CLASS);
    }
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setPristine
   *
   * @description
   * Sets the control to its pristine state.
   *
   * This method can be called to remove the `ng-dirty` class and set the control to its pristine
   * state (`ng-pristine` class). A model is considered to be pristine when the control
   * has not been changed from when first compiled.
   */
  $setPristine: function() {
    this.$dirty = false;
    this.$pristine = true;
    this.$$animate.removeClass(this.$$element, DIRTY_CLASS);
    this.$$animate.addClass(this.$$element, PRISTINE_CLASS);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setDirty
   *
   * @description
   * Sets the control to its dirty state.
   *
   * This method can be called to remove the `ng-pristine` class and set the control to its dirty
   * state (`ng-dirty` class). A model is considered to be dirty when the control has been changed
   * from when first compiled.
   */
  $setDirty: function() {
    this.$dirty = true;
    this.$pristine = false;
    this.$$animate.removeClass(this.$$element, PRISTINE_CLASS);
    this.$$animate.addClass(this.$$element, DIRTY_CLASS);
    this.$$parentForm.$setDirty();
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setUntouched
   *
   * @description
   * Sets the control to its untouched state.
   *
   * This method can be called to remove the `ng-touched` class and set the control to its
   * untouched state (`ng-untouched` class). Upon compilation, a model is set as untouched
   * by default, however this function can be used to restore that state if the model has
   * already been touched by the user.
   */
  $setUntouched: function() {
    this.$touched = false;
    this.$untouched = true;
    this.$$animate.setClass(this.$$element, UNTOUCHED_CLASS, TOUCHED_CLASS);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setTouched
   *
   * @description
   * Sets the control to its touched state.
   *
   * This method can be called to remove the `ng-untouched` class and set the control to its
   * touched state (`ng-touched` class). A model is considered to be touched when the user has
   * first focused the control element and then shifted focus away from the control (blur event).
   */
  $setTouched: function() {
    this.$touched = true;
    this.$untouched = false;
    this.$$animate.setClass(this.$$element, TOUCHED_CLASS, UNTOUCHED_CLASS);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$rollbackViewValue
   *
   * @description
   * Cancel an update and reset the input element's value to prevent an update to the `$modelValue`,
   * which may be caused by a pending debounced event or because the input is waiting for some
   * future event.
   *
   * If you have an input that uses `ng-model-options` to set up debounced updates or updates that
   * depend on special events such as `blur`, there can be a period when the `$viewValue` is out of
   * sync with the ngModel's `$modelValue`.
   *
   * In this case, you can use `$rollbackViewValue()` to manually cancel the debounced / future update
   * and reset the input to the last committed view value.
   *
   * It is also possible that you run into difficulties if you try to update the ngModel's `$modelValue`
   * programmatically before these debounced/future events have resolved/occurred, because AngularJS's
   * dirty checking mechanism is not able to tell whether the model has actually changed or not.
   *
   * The `$rollbackViewValue()` method should be called before programmatically changing the model of an
   * input which may have such events pending. This is important in order to make sure that the
   * input field will be updated with the new model value and any pending operations are cancelled.
   *
   * @example
   * <example name="ng-model-cancel-update" module="cancel-update-example">
   *   <file name="app.js">
   *     angular.module('cancel-update-example', [])
   *
   *     .controller('CancelUpdateController', ['$scope', function($scope) {
   *       $scope.model = {value1: '', value2: ''};
   *
   *       $scope.setEmpty = function(e, value, rollback) {
   *         if (e.keyCode === 27) {
   *           e.preventDefault();
   *           if (rollback) {
   *             $scope.myForm[value].$rollbackViewValue();
   *           }
   *           $scope.model[value] = '';
   *         }
   *       };
   *     }]);
   *   </file>
   *   <file name="index.html">
   *     <div ng-controller="CancelUpdateController">
   *       <p>Both of these inputs are only updated if they are blurred. Hitting escape should
   *       empty them. Follow these steps and observe the difference:</p>
   *       <ol>
   *         <li>Type something in the input. You will see that the model is not yet updated</li>
   *         <li>Press the Escape key.
   *           <ol>
   *             <li> In the first example, nothing happens, because the model is already '', and no
   *             update is detected. If you blur the input, the model will be set to the current view.
   *             </li>
   *             <li> In the second example, the pending update is cancelled, and the input is set back
   *             to the last committed view value (''). Blurring the input does nothing.
   *             </li>
   *           </ol>
   *         </li>
   *       </ol>
   *
   *       <form name="myForm" ng-model-options="{ updateOn: 'blur' }">
   *         <div>
   *           <p id="inputDescription1">Without $rollbackViewValue():</p>
   *           <input name="value1" aria-describedby="inputDescription1" ng-model="model.value1"
   *                  ng-keydown="setEmpty($event, 'value1')">
   *           value1: "{{ model.value1 }}"
   *         </div>
   *
   *         <div>
   *           <p id="inputDescription2">With $rollbackViewValue():</p>
   *           <input name="value2" aria-describedby="inputDescription2" ng-model="model.value2"
   *                  ng-keydown="setEmpty($event, 'value2', true)">
   *           value2: "{{ model.value2 }}"
   *         </div>
   *       </form>
   *     </div>
   *   </file>
       <file name="style.css">
          div {
            display: table-cell;
          }
          div:nth-child(1) {
            padding-right: 30px;
          }

        </file>
   * </example>
   */
  $rollbackViewValue: function() {
    this.$$timeout.cancel(this.$$pendingDebounce);
    this.$viewValue = this.$$lastCommittedViewValue;
    this.$render();
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$validate
   *
   * @description
   * Runs each of the registered validators (first synchronous validators and then
   * asynchronous validators).
   * If the validity changes to invalid, the model will be set to `undefined`,
   * unless {@link ngModelOptions `ngModelOptions.allowInvalid`} is `true`.
   * If the validity changes to valid, it will set the model to the last available valid
   * `$modelValue`, i.e. either the last parsed value or the last value set from the scope.
   */
  $validate: function() {

    // ignore $validate before model is initialized
    if (isNumberNaN(this.$modelValue)) {
      return;
    }

    var viewValue = this.$$lastCommittedViewValue;
    // Note: we use the $$rawModelValue as $modelValue might have been
    // set to undefined during a view -> model update that found validation
    // errors. We can't parse the view here, since that could change
    // the model although neither viewValue nor the model on the scope changed
    var modelValue = this.$$rawModelValue;

    var prevValid = this.$valid;
    var prevModelValue = this.$modelValue;

    var allowInvalid = this.$options.getOption('allowInvalid');

    var that = this;
    this.$$runValidators(modelValue, viewValue, function(allValid) {
      // If there was no change in validity, don't update the model
      // This prevents changing an invalid modelValue to undefined
      if (!allowInvalid && prevValid !== allValid) {
        // Note: Don't check this.$valid here, as we could have
        // external validators (e.g. calculated on the server),
        // that just call $setValidity and need the model value
        // to calculate their validity.
        that.$modelValue = allValid ? modelValue : undefined;

        if (that.$modelValue !== prevModelValue) {
          that.$$writeModelToScope();
        }
      }
    });
  },

  $$runValidators: function(modelValue, viewValue, doneCallback) {
    this.$$currentValidationRunId++;
    var localValidationRunId = this.$$currentValidationRunId;
    var that = this;

    // check parser error
    if (!processParseErrors()) {
      validationDone(false);
      return;
    }
    if (!processSyncValidators()) {
      validationDone(false);
      return;
    }
    processAsyncValidators();

    function processParseErrors() {
      var errorKey = that.$$parserName;

      if (isUndefined(that.$$parserValid)) {
        setValidity(errorKey, null);
      } else {
        if (!that.$$parserValid) {
          forEach(that.$validators, function(v, name) {
            setValidity(name, null);
          });
          forEach(that.$asyncValidators, function(v, name) {
            setValidity(name, null);
          });
        }

        // Set the parse error last, to prevent unsetting it, should a $validators key == parserName
        setValidity(errorKey, that.$$parserValid);
        return that.$$parserValid;
      }
      return true;
    }

    function processSyncValidators() {
      var syncValidatorsValid = true;
      forEach(that.$validators, function(validator, name) {
        var result = Boolean(validator(modelValue, viewValue));
        syncValidatorsValid = syncValidatorsValid && result;
        setValidity(name, result);
      });
      if (!syncValidatorsValid) {
        forEach(that.$asyncValidators, function(v, name) {
          setValidity(name, null);
        });
        return false;
      }
      return true;
    }

    function processAsyncValidators() {
      var validatorPromises = [];
      var allValid = true;
      forEach(that.$asyncValidators, function(validator, name) {
        var promise = validator(modelValue, viewValue);
        if (!isPromiseLike(promise)) {
          throw ngModelMinErr('nopromise',
            'Expected asynchronous validator to return a promise but got \'{0}\' instead.', promise);
        }
        setValidity(name, undefined);
        validatorPromises.push(promise.then(function() {
          setValidity(name, true);
        }, function() {
          allValid = false;
          setValidity(name, false);
        }));
      });
      if (!validatorPromises.length) {
        validationDone(true);
      } else {
        that.$$q.all(validatorPromises).then(function() {
          validationDone(allValid);
        }, noop);
      }
    }

    function setValidity(name, isValid) {
      if (localValidationRunId === that.$$currentValidationRunId) {
        that.$setValidity(name, isValid);
      }
    }

    function validationDone(allValid) {
      if (localValidationRunId === that.$$currentValidationRunId) {

        doneCallback(allValid);
      }
    }
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$commitViewValue
   *
   * @description
   * Commit a pending update to the `$modelValue`.
   *
   * Updates may be pending by a debounced event or because the input is waiting for a some future
   * event defined in `ng-model-options`. this method is rarely needed as `NgModelController`
   * usually handles calling this in response to input events.
   */
  $commitViewValue: function() {
    var viewValue = this.$viewValue;

    this.$$timeout.cancel(this.$$pendingDebounce);

    // If the view value has not changed then we should just exit, except in the case where there is
    // a native validator on the element. In this case the validation state may have changed even though
    // the viewValue has stayed empty.
    if (this.$$lastCommittedViewValue === viewValue && (viewValue !== '' || !this.$$hasNativeValidators)) {
      return;
    }
    this.$$updateEmptyClasses(viewValue);
    this.$$lastCommittedViewValue = viewValue;

    // change to dirty
    if (this.$pristine) {
      this.$setDirty();
    }
    this.$$parseAndValidate();
  },

  $$parseAndValidate: function() {
    var viewValue = this.$$lastCommittedViewValue;
    var modelValue = viewValue;
    var that = this;

    this.$$parserValid = isUndefined(modelValue) ? undefined : true;

    // Reset any previous parse error
    this.$setValidity(this.$$parserName, null);
    this.$$parserName = 'parse';

    if (this.$$parserValid) {
      for (var i = 0; i < this.$parsers.length; i++) {
        modelValue = this.$parsers[i](modelValue);
        if (isUndefined(modelValue)) {
          this.$$parserValid = false;
          break;
        }
      }
    }
    if (isNumberNaN(this.$modelValue)) {
      // this.$modelValue has not been touched yet...
      this.$modelValue = this.$$ngModelGet(this.$$scope);
    }
    var prevModelValue = this.$modelValue;
    var allowInvalid = this.$options.getOption('allowInvalid');
    this.$$rawModelValue = modelValue;

    if (allowInvalid) {
      this.$modelValue = modelValue;
      writeToModelIfNeeded();
    }

    // Pass the $$lastCommittedViewValue here, because the cached viewValue might be out of date.
    // This can happen if e.g. $setViewValue is called from inside a parser
    this.$$runValidators(modelValue, this.$$lastCommittedViewValue, function(allValid) {
      if (!allowInvalid) {
        // Note: Don't check this.$valid here, as we could have
        // external validators (e.g. calculated on the server),
        // that just call $setValidity and need the model value
        // to calculate their validity.
        that.$modelValue = allValid ? modelValue : undefined;
        writeToModelIfNeeded();
      }
    });

    function writeToModelIfNeeded() {
      if (that.$modelValue !== prevModelValue) {
        that.$$writeModelToScope();
      }
    }
  },

  $$writeModelToScope: function() {
    this.$$ngModelSet(this.$$scope, this.$modelValue);
    forEach(this.$viewChangeListeners, function(listener) {
      try {
        listener();
      } catch (e) {
        // eslint-disable-next-line no-invalid-this
        this.$$exceptionHandler(e);
      }
    }, this);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setViewValue
   *
   * @description
   * Update the view value.
   *
   * This method should be called when a control wants to change the view value; typically,
   * this is done from within a DOM event handler. For example, the {@link ng.directive:input input}
   * directive calls it when the value of the input changes and {@link ng.directive:select select}
   * calls it when an option is selected.
   *
   * When `$setViewValue` is called, the new `value` will be staged for committing through the `$parsers`
   * and `$validators` pipelines. If there are no special {@link ngModelOptions} specified then the staged
   * value is sent directly for processing through the `$parsers` pipeline. After this, the `$validators` and
   * `$asyncValidators` are called and the value is applied to `$modelValue`.
   * Finally, the value is set to the **expression** specified in the `ng-model` attribute and
   * all the registered change listeners, in the `$viewChangeListeners` list are called.
   *
   * In case the {@link ng.directive:ngModelOptions ngModelOptions} directive is used with `updateOn`
   * and the `default` trigger is not listed, all those actions will remain pending until one of the
   * `updateOn` events is triggered on the DOM element.
   * All these actions will be debounced if the {@link ng.directive:ngModelOptions ngModelOptions}
   * directive is used with a custom debounce for this particular event.
   * Note that a `$digest` is only triggered once the `updateOn` events are fired, or if `debounce`
   * is specified, once the timer runs out.
   *
   * When used with standard inputs, the view value will always be a string (which is in some cases
   * parsed into another type, such as a `Date` object for `input[date]`.)
   * However, custom controls might also pass objects to this method. In this case, we should make
   * a copy of the object before passing it to `$setViewValue`. This is because `ngModel` does not
   * perform a deep watch of objects, it only looks for a change of identity. If you only change
   * the property of the object then ngModel will not realize that the object has changed and
   * will not invoke the `$parsers` and `$validators` pipelines. For this reason, you should
   * not change properties of the copy once it has been passed to `$setViewValue`.
   * Otherwise you may cause the model value on the scope to change incorrectly.
   *
   * <div class="alert alert-info">
   * In any case, the value passed to the method should always reflect the current value
   * of the control. For example, if you are calling `$setViewValue` for an input element,
   * you should pass the input DOM value. Otherwise, the control and the scope model become
   * out of sync. It's also important to note that `$setViewValue` does not call `$render` or change
   * the control's DOM value in any way. If we want to change the control's DOM value
   * programmatically, we should update the `ngModel` scope expression. Its new value will be
   * picked up by the model controller, which will run it through the `$formatters`, `$render` it
   * to update the DOM, and finally call `$validate` on it.
   * </div>
   *
   * @param {*} value value from the view.
   * @param {string} trigger Event that triggered the update.
   */
  $setViewValue: function(value, trigger) {
    this.$viewValue = value;
    if (this.$options.getOption('updateOnDefault')) {
      this.$$debounceViewValueCommit(trigger);
    }
  },

  $$debounceViewValueCommit: function(trigger) {
    var debounceDelay = this.$options.getOption('debounce');

    if (isNumber(debounceDelay[trigger])) {
      debounceDelay = debounceDelay[trigger];
    } else if (isNumber(debounceDelay['default']) &&
      this.$options.getOption('updateOn').indexOf(trigger) === -1
    ) {
      debounceDelay = debounceDelay['default'];
    } else if (isNumber(debounceDelay['*'])) {
      debounceDelay = debounceDelay['*'];
    }

    this.$$timeout.cancel(this.$$pendingDebounce);
    var that = this;
    if (debounceDelay > 0) { // this fails if debounceDelay is an object
      this.$$pendingDebounce = this.$$timeout(function() {
        that.$commitViewValue();
      }, debounceDelay);
    } else if (this.$$rootScope.$$phase) {
      this.$commitViewValue();
    } else {
      this.$$scope.$apply(function() {
        that.$commitViewValue();
      });
    }
  },

  /**
   * @ngdoc method
   *
   * @name ngModel.NgModelController#$overrideModelOptions
   *
   * @description
   *
   * Override the current model options settings programmatically.
   *
   * The previous `ModelOptions` value will not be modified. Instead, a
   * new `ModelOptions` object will inherit from the previous one overriding
   * or inheriting settings that are defined in the given parameter.
   *
   * See {@link ngModelOptions} for information about what options can be specified
   * and how model option inheritance works.
   *
   * <div class="alert alert-warning">
   * **Note:** this function only affects the options set on the `ngModelController`,
   * and not the options on the {@link ngModelOptions} directive from which they might have been
   * obtained initially.
   * </div>
   *
   * <div class="alert alert-danger">
   * **Note:** it is not possible to override the `getterSetter` option.
   * </div>
   *
   * @param {Object} options a hash of settings to override the previous options
   *
   */
  $overrideModelOptions: function(options) {
    this.$options = this.$options.createChild(options);
    this.$$setUpdateOnEvents();
  },

  /**
   * @ngdoc method
   *
   * @name  ngModel.NgModelController#$processModelValue

   * @description
   *
   * Runs the model -> view pipeline on the current
   * {@link ngModel.NgModelController#$modelValue $modelValue}.
   *
   * The following actions are performed by this method:
   *
   * - the `$modelValue` is run through the {@link ngModel.NgModelController#$formatters $formatters}
   * and the result is set to the {@link ngModel.NgModelController#$viewValue $viewValue}
   * - the `ng-empty` or `ng-not-empty` class is set on the element
   * - if the `$viewValue` has changed:
   *   - {@link ngModel.NgModelController#$render $render} is called on the control
   *   - the {@link ngModel.NgModelController#$validators $validators} are run and
   *   the validation status is set.
   *
   * This method is called by ngModel internally when the bound scope value changes.
   * Application developers usually do not have to call this function themselves.
   *
   * This function can be used when the `$viewValue` or the rendered DOM value are not correctly
   * formatted and the `$modelValue` must be run through the `$formatters` again.
   *
   * @example
   * Consider a text input with an autocomplete list (for fruit), where the items are
   * objects with a name and an id.
   * A user enters `ap` and then selects `Apricot` from the list.
   * Based on this, the autocomplete widget will call `$setViewValue({name: 'Apricot', id: 443})`,
   * but the rendered value will still be `ap`.
   * The widget can then call `ctrl.$processModelValue()` to run the model -> view
   * pipeline again, which formats the object to the string `Apricot`,
   * then updates the `$viewValue`, and finally renders it in the DOM.
   *
   * <example module="inputExample" name="ng-model-process">
     <file name="index.html">
      <div ng-controller="inputController" style="display: flex;">
        <div style="margin-right: 30px;">
          Search Fruit:
          <basic-autocomplete items="items" on-select="selectedFruit = item"></basic-autocomplete>
        </div>
        <div>
          Model:<br>
          <pre>{{selectedFruit | json}}</pre>
        </div>
      </div>
     </file>
     <file name="app.js">
      angular.module('inputExample', [])
        .controller('inputController', function($scope) {
          $scope.items = [
            {name: 'Apricot', id: 443},
            {name: 'Clementine', id: 972},
            {name: 'Durian', id: 169},
            {name: 'Jackfruit', id: 982},
            {name: 'Strawberry', id: 863}
          ];
        })
        .component('basicAutocomplete', {
          bindings: {
            items: '<',
            onSelect: '&'
          },
          templateUrl: 'autocomplete.html',
          controller: function($element, $scope) {
            var that = this;
            var ngModel;

            that.$postLink = function() {
              ngModel = $element.find('input').controller('ngModel');

              ngModel.$formatters.push(function(value) {
                return (value && value.name) || value;
              });

              ngModel.$parsers.push(function(value) {
                var match = value;
                for (var i = 0; i < that.items.length; i++) {
                  if (that.items[i].name === value) {
                    match = that.items[i];
                    break;
                  }
                }

                return match;
              });
            };

            that.selectItem = function(item) {
              ngModel.$setViewValue(item);
              ngModel.$processModelValue();
              that.onSelect({item: item});
            };
          }
        });
     </file>
     <file name="autocomplete.html">
       <div>
         <input type="search" ng-model="$ctrl.searchTerm" />
         <ul>
           <li ng-repeat="item in $ctrl.items | filter:$ctrl.searchTerm">
             <button ng-click="$ctrl.selectItem(item)">{{ item.name }}</button>
           </li>
         </ul>
       </div>
     </file>
   * </example>
   *
   */
  $processModelValue: function() {
    var viewValue = this.$$format();

    if (this.$viewValue !== viewValue) {
      this.$$updateEmptyClasses(viewValue);
      this.$viewValue = this.$$lastCommittedViewValue = viewValue;
      this.$render();
      // It is possible that model and view value have been updated during render
      this.$$runValidators(this.$modelValue, this.$viewValue, noop);
    }
  },

  /**
   * This method is called internally to run the $formatters on the $modelValue
   */
  $$format: function() {
    var formatters = this.$formatters,
        idx = formatters.length;

    var viewValue = this.$modelValue;
    while (idx--) {
      viewValue = formatters[idx](viewValue);
    }

    return viewValue;
  },

  /**
   * This method is called internally when the bound scope value changes.
   */
  $$setModelValue: function(modelValue) {
    this.$modelValue = this.$$rawModelValue = modelValue;
    this.$$parserValid = undefined;
    this.$processModelValue();
  },

  $$setUpdateOnEvents: function() {
    if (this.$$updateEvents) {
      this.$$element.off(this.$$updateEvents, this.$$updateEventHandler);
    }

    this.$$updateEvents = this.$options.getOption('updateOn');
    if (this.$$updateEvents) {
      this.$$element.on(this.$$updateEvents, this.$$updateEventHandler);
    }
  },

  $$updateEventHandler: function(ev) {
    this.$$debounceViewValueCommit(ev && ev.type);
  }
};

function setupModelWatcher(ctrl) {
  // model -> value
  // Note: we cannot use a normal scope.$watch as we want to detect the following:
  // 1. scope value is 'a'
  // 2. user enters 'b'
  // 3. ng-change kicks in and reverts scope value to 'a'
  //    -> scope value did not change since the last digest as
  //       ng-change executes in apply phase
  // 4. view should be changed back to 'a'
  ctrl.$$scope.$watch(function ngModelWatch(scope) {
    var modelValue = ctrl.$$ngModelGet(scope);

    // if scope model value and ngModel value are out of sync
    // This cannot be moved to the action function, because it would not catch the
    // case where the model is changed in the ngChange function or the model setter
    if (modelValue !== ctrl.$modelValue &&
      // checks for NaN is needed to allow setting the model to NaN when there's an asyncValidator
      // eslint-disable-next-line no-self-compare
      (ctrl.$modelValue === ctrl.$modelValue || modelValue === modelValue)
    ) {
      ctrl.$$setModelValue(modelValue);
    }

    return modelValue;
  });
}

/**
 * @ngdoc method
 * @name ngModel.NgModelController#$setValidity
 *
 * @description
 * Change the validity state, and notify the form.
 *
 * This method can be called within $parsers/$formatters or a custom validation implementation.
 * However, in most cases it should be sufficient to use the `ngModel.$validators` and
 * `ngModel.$asyncValidators` collections which will call `$setValidity` automatically.
 *
 * @param {string} validationErrorKey Name of the validator. The `validationErrorKey` will be assigned
 *        to either `$error[validationErrorKey]` or `$pending[validationErrorKey]`
 *        (for unfulfilled `$asyncValidators`), so that it is available for data-binding.
 *        The `validationErrorKey` should be in camelCase and will get converted into dash-case
 *        for class name. Example: `myError` will result in `ng-valid-my-error` and `ng-invalid-my-error`
 *        classes and can be bound to as `{{ someForm.someControl.$error.myError }}`.
 * @param {boolean} isValid Whether the current state is valid (true), invalid (false), pending (undefined),
 *                          or skipped (null). Pending is used for unfulfilled `$asyncValidators`.
 *                          Skipped is used by AngularJS when validators do not run because of parse errors and
 *                          when `$asyncValidators` do not run because any of the `$validators` failed.
 */
addSetValidityMethod({
  clazz: NgModelController,
  set: function(object, property) {
    object[property] = true;
  },
  unset: function(object, property) {
    delete object[property];
  }
});


/**
 * @ngdoc directive
 * @name ngModel
 * @restrict A
 * @priority 1
 * @param {expression} ngModel assignable {@link guide/expression Expression} to bind to.
 *
 * @description
 * The `ngModel` directive binds an `input`,`select`, `textarea` (or custom form control) to a
 * property on the scope using {@link ngModel.NgModelController NgModelController},
 * which is created and exposed by this directive.
 *
 * `ngModel` is responsible for:
 *
 * - Binding the view into the model, which other directives such as `input`, `textarea` or `select`
 *   require.
 * - Providing validation behavior (i.e. required, number, email, url).
 * - Keeping the state of the control (valid/invalid, dirty/pristine, touched/untouched, validation errors).
 * - Setting related css classes on the element (`ng-valid`, `ng-invalid`, `ng-dirty`, `ng-pristine`, `ng-touched`,
 *   `ng-untouched`, `ng-empty`, `ng-not-empty`) including animations.
 * - Registering the control with its parent {@link ng.directive:form form}.
 *
 * Note: `ngModel` will try to bind to the property given by evaluating the expression on the
 * current scope. If the property doesn't already exist on this scope, it will be created
 * implicitly and added to the scope.
 *
 * For best practices on using `ngModel`, see:
 *
 *  - [Understanding Scopes](https://github.com/angular/angular.js/wiki/Understanding-Scopes)
 *
 * For basic examples, how to use `ngModel`, see:
 *
 *  - {@link ng.directive:input input}
 *    - {@link input[text] text}
 *    - {@link input[checkbox] checkbox}
 *    - {@link input[radio] radio}
 *    - {@link input[number] number}
 *    - {@link input[email] email}
 *    - {@link input[url] url}
 *    - {@link input[date] date}
 *    - {@link input[datetime-local] datetime-local}
 *    - {@link input[time] time}
 *    - {@link input[month] month}
 *    - {@link input[week] week}
 *  - {@link ng.directive:select select}
 *  - {@link ng.directive:textarea textarea}
 *
 * ## Complex Models (objects or collections)
 *
 * By default, `ngModel` watches the model by reference, not value. This is important to know when
 * binding inputs to models that are objects (e.g. `Date`) or collections (e.g. arrays). If only properties of the
 * object or collection change, `ngModel` will not be notified and so the input will not be  re-rendered.
 *
 * The model must be assigned an entirely new object or collection before a re-rendering will occur.
 *
 * Some directives have options that will cause them to use a custom `$watchCollection` on the model expression
 * - for example, `ngOptions` will do so when a `track by` clause is included in the comprehension expression or
 * if the select is given the `multiple` attribute.
 *
 * The `$watchCollection()` method only does a shallow comparison, meaning that changing properties deeper than the
 * first level of the object (or only changing the properties of an item in the collection if it's an array) will still
 * not trigger a re-rendering of the model.
 *
 * ## CSS classes
 * The following CSS classes are added and removed on the associated input/select/textarea element
 * depending on the validity of the model.
 *
 *  - `ng-valid`: the model is valid
 *  - `ng-invalid`: the model is invalid
 *  - `ng-valid-[key]`: for each valid key added by `$setValidity`
 *  - `ng-invalid-[key]`: for each invalid key added by `$setValidity`
 *  - `ng-pristine`: the control hasn't been interacted with yet
 *  - `ng-dirty`: the control has been interacted with
 *  - `ng-touched`: the control has been blurred
 *  - `ng-untouched`: the control hasn't been blurred
 *  - `ng-pending`: any `$asyncValidators` are unfulfilled
 *  - `ng-empty`: the view does not contain a value or the value is deemed "empty", as defined
 *     by the {@link ngModel.NgModelController#$isEmpty} method
 *  - `ng-not-empty`: the view contains a non-empty value
 *
 * Keep in mind that ngAnimate can detect each of these classes when added and removed.
 *
 * @animations
 * Animations within models are triggered when any of the associated CSS classes are added and removed
 * on the input element which is attached to the model. These classes include: `.ng-pristine`, `.ng-dirty`,
 * `.ng-invalid` and `.ng-valid` as well as any other validations that are performed on the model itself.
 * The animations that are triggered within ngModel are similar to how they work in ngClass and
 * animations can be hooked into using CSS transitions, keyframes as well as JS animations.
 *
 * The following example shows a simple way to utilize CSS transitions to style an input element
 * that has been rendered as invalid after it has been validated:
 *
 * <pre>
 * //be sure to include ngAnimate as a module to hook into more
 * //advanced animations
 * .my-input {
 *   transition:0.5s linear all;
 *   background: white;
 * }
 * .my-input.ng-invalid {
 *   background: red;
 *   color:white;
 * }
 * </pre>
 *
 * @example
 * ### Basic Usage
 * <example deps="angular-animate.js" animations="true" fixBase="true" module="inputExample" name="ng-model">
     <file name="index.html">
       <script>
        angular.module('inputExample', [])
          .controller('ExampleController', ['$scope', function($scope) {
            $scope.val = '1';
          }]);
       </script>
       <style>
         .my-input {
           transition:all linear 0.5s;
           background: transparent;
         }
         .my-input.ng-invalid {
           color:white;
           background: red;
         }
       </style>
       <p id="inputDescription">
        Update input to see transitions when valid/invalid.
        Integer is a valid value.
       </p>
       <form name="testForm" ng-controller="ExampleController">
         <input ng-model="val" ng-pattern="/^\d+$/" name="anim" class="my-input"
                aria-describedby="inputDescription" />
       </form>
     </file>
 * </example>
 *
 * @example
 * ### Binding to a getter/setter
 *
 * Sometimes it's helpful to bind `ngModel` to a getter/setter function.  A getter/setter is a
 * function that returns a representation of the model when called with zero arguments, and sets
 * the internal state of a model when called with an argument. It's sometimes useful to use this
 * for models that have an internal representation that's different from what the model exposes
 * to the view.
 *
 * <div class="alert alert-success">
 * **Best Practice:** It's best to keep getters fast because AngularJS is likely to call them more
 * frequently than other parts of your code.
 * </div>
 *
 * You use this behavior by adding `ng-model-options="{ getterSetter: true }"` to an element that
 * has `ng-model` attached to it. You can also add `ng-model-options="{ getterSetter: true }"` to
 * a `<form>`, which will enable this behavior for all `<input>`s within it. See
 * {@link ng.directive:ngModelOptions `ngModelOptions`} for more.
 *
 * The following example shows how to use `ngModel` with a getter/setter:
 *
 * @example
 * <example name="ngModel-getter-setter" module="getterSetterExample">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <form name="userForm">
           <label>Name:
             <input type="text" name="userName"
                    ng-model="user.name"
                    ng-model-options="{ getterSetter: true }" />
           </label>
         </form>
         <pre>user.name = <span ng-bind="user.name()"></span></pre>
       </div>
     </file>
     <file name="app.js">
       angular.module('getterSetterExample', [])
         .controller('ExampleController', ['$scope', function($scope) {
           var _name = 'Brian';
           $scope.user = {
             name: function(newName) {
              // Note that newName can be undefined for two reasons:
              // 1. Because it is called as a getter and thus called with no arguments
              // 2. Because the property should actually be set to undefined. This happens e.g. if the
              //    input is invalid
              return arguments.length ? (_name = newName) : _name;
             }
           };
         }]);
     </file>
 * </example>
 */
var ngModelDirective = ['$rootScope', function($rootScope) {
  return {
    restrict: 'A',
    require: ['ngModel', '^?form', '^?ngModelOptions'],
    controller: NgModelController,
    // Prelink needs to run before any input directive
    // so that we can set the NgModelOptions in NgModelController
    // before anyone else uses it.
    priority: 1,
    compile: function ngModelCompile(element) {
      // Setup initial state of the control
      element.addClass(PRISTINE_CLASS).addClass(UNTOUCHED_CLASS).addClass(VALID_CLASS);

      return {
        pre: function ngModelPreLink(scope, element, attr, ctrls) {
          var modelCtrl = ctrls[0],
              formCtrl = ctrls[1] || modelCtrl.$$parentForm,
              optionsCtrl = ctrls[2];

          if (optionsCtrl) {
            modelCtrl.$options = optionsCtrl.$options;
          }

          modelCtrl.$$initGetterSetters();

          // notify others, especially parent forms
          formCtrl.$addControl(modelCtrl);

          attr.$observe('name', function(newValue) {
            if (modelCtrl.$name !== newValue) {
              modelCtrl.$$parentForm.$$renameControl(modelCtrl, newValue);
            }
          });

          scope.$on('$destroy', function() {
            modelCtrl.$$parentForm.$removeControl(modelCtrl);
          });
        },
        post: function ngModelPostLink(scope, element, attr, ctrls) {
          var modelCtrl = ctrls[0];
          modelCtrl.$$setUpdateOnEvents();

          function setTouched() {
            modelCtrl.$setTouched();
          }

          element.on('blur', function() {
            if (modelCtrl.$touched) return;

            if ($rootScope.$$phase) {
              scope.$evalAsync(setTouched);
            } else {
              scope.$apply(setTouched);
            }
          });
        }
      };
    }
  };
}];

/* exported defaultModelOptions */
var defaultModelOptions;
var DEFAULT_REGEXP = /(\s+|^)default(\s+|$)/;

/**
 * @ngdoc type
 * @name ModelOptions
 * @description
 * A container for the options set by the {@link ngModelOptions} directive
 */
function ModelOptions(options) {
  this.$$options = options;
}

ModelOptions.prototype = {

  /**
   * @ngdoc method
   * @name ModelOptions#getOption
   * @param {string} name the name of the option to retrieve
   * @returns {*} the value of the option
   * @description
   * Returns the value of the given option
   */
  getOption: function(name) {
    return this.$$options[name];
  },

  /**
   * @ngdoc method
   * @name ModelOptions#createChild
   * @param {Object} options a hash of options for the new child that will override the parent's options
   * @return {ModelOptions} a new `ModelOptions` object initialized with the given options.
   */
  createChild: function(options) {
    var inheritAll = false;

    // make a shallow copy
    options = extend({}, options);

    // Inherit options from the parent if specified by the value `"$inherit"`
    forEach(options, /** @this */ function(option, key) {
      if (option === '$inherit') {
        if (key === '*') {
          inheritAll = true;
        } else {
          options[key] = this.$$options[key];
          // `updateOn` is special so we must also inherit the `updateOnDefault` option
          if (key === 'updateOn') {
            options.updateOnDefault = this.$$options.updateOnDefault;
          }
        }
      } else {
        if (key === 'updateOn') {
          // If the `updateOn` property contains the `default` event then we have to remove
          // it from the event list and set the `updateOnDefault` flag.
          options.updateOnDefault = false;
          options[key] = trim(option.replace(DEFAULT_REGEXP, function() {
            options.updateOnDefault = true;
            return ' ';
          }));
        }
      }
    }, this);

    if (inheritAll) {
      // We have a property of the form: `"*": "$inherit"`
      delete options['*'];
      defaults(options, this.$$options);
    }

    // Finally add in any missing defaults
    defaults(options, defaultModelOptions.$$options);

    return new ModelOptions(options);
  }
};


defaultModelOptions = new ModelOptions({
  updateOn: '',
  updateOnDefault: true,
  debounce: 0,
  getterSetter: false,
  allowInvalid: false,
  timezone: null
});


/**
 * @ngdoc directive
 * @name ngModelOptions
 * @restrict A
 * @priority 10
 *
 * @description
 * This directive allows you to modify the behaviour of {@link ngModel} directives within your
 * application. You can specify an `ngModelOptions` directive on any element. All {@link ngModel}
 * directives will use the options of their nearest `ngModelOptions` ancestor.
 *
 * The `ngModelOptions` settings are found by evaluating the value of the attribute directive as
 * an AngularJS expression. This expression should evaluate to an object, whose properties contain
 * the settings. For example: `<div ng-model-options="{ debounce: 100 }"`.
 *
 * ## Inheriting Options
 *
 * You can specify that an `ngModelOptions` setting should be inherited from a parent `ngModelOptions`
 * directive by giving it the value of `"$inherit"`.
 * Then it will inherit that setting from the first `ngModelOptions` directive found by traversing up the
 * DOM tree. If there is no ancestor element containing an `ngModelOptions` directive then default settings
 * will be used.
 *
 * For example given the following fragment of HTML
 *
 *
 * ```html
 * <div ng-model-options="{ allowInvalid: true, debounce: 200 }">
 *   <form ng-model-options="{ updateOn: 'blur', allowInvalid: '$inherit' }">
 *     <input ng-model-options="{ updateOn: 'default', allowInvalid: '$inherit' }" />
 *   </form>
 * </div>
 * ```
 *
 * the `input` element will have the following settings
 *
 * ```js
 * { allowInvalid: true, updateOn: 'default', debounce: 0 }
 * ```
 *
 * Notice that the `debounce` setting was not inherited and used the default value instead.
 *
 * You can specify that all undefined settings are automatically inherited from an ancestor by
 * including a property with key of `"*"` and value of `"$inherit"`.
 *
 * For example given the following fragment of HTML
 *
 *
 * ```html
 * <div ng-model-options="{ allowInvalid: true, debounce: 200 }">
 *   <form ng-model-options="{ updateOn: 'blur', "*": '$inherit' }">
 *     <input ng-model-options="{ updateOn: 'default', "*": '$inherit' }" />
 *   </form>
 * </div>
 * ```
 *
 * the `input` element will have the following settings
 *
 * ```js
 * { allowInvalid: true, updateOn: 'default', debounce: 200 }
 * ```
 *
 * Notice that the `debounce` setting now inherits the value from the outer `<div>` element.
 *
 * If you are creating a reusable component then you should be careful when using `"*": "$inherit"`
 * since you may inadvertently inherit a setting in the future that changes the behavior of your component.
 *
 *
 * ## Triggering and debouncing model updates
 *
 * The `updateOn` and `debounce` properties allow you to specify a custom list of events that will
 * trigger a model update and/or a debouncing delay so that the actual update only takes place when
 * a timer expires; this timer will be reset after another change takes place.
 *
 * Given the nature of `ngModelOptions`, the value displayed inside input fields in the view might
 * be different from the value in the actual model. This means that if you update the model you
 * should also invoke {@link ngModel.NgModelController#$rollbackViewValue} on the relevant input field in
 * order to make sure it is synchronized with the model and that any debounced action is canceled.
 *
 * The easiest way to reference the control's {@link ngModel.NgModelController#$rollbackViewValue}
 * method is by making sure the input is placed inside a form that has a `name` attribute. This is
 * important because `form` controllers are published to the related scope under the name in their
 * `name` attribute.
 *
 * Any pending changes will take place immediately when an enclosing form is submitted via the
 * `submit` event. Note that `ngClick` events will occur before the model is updated. Use `ngSubmit`
 * to have access to the updated model.
 *
 * ### Overriding immediate updates
 *
 * The following example shows how to override immediate updates. Changes on the inputs within the
 * form will update the model only when the control loses focus (blur event). If `escape` key is
 * pressed while the input field is focused, the value is reset to the value in the current model.
 *
 * <example name="ngModelOptions-directive-blur" module="optionsExample">
 *   <file name="index.html">
 *     <div ng-controller="ExampleController">
 *       <form name="userForm">
 *         <label>
 *           Name:
 *           <input type="text" name="userName"
 *                  ng-model="user.name"
 *                  ng-model-options="{ updateOn: 'blur' }"
 *                  ng-keyup="cancel($event)" />
 *         </label><br />
 *         <label>
 *           Other data:
 *           <input type="text" ng-model="user.data" />
 *         </label><br />
 *       </form>
 *       <pre>user.name = <span ng-bind="user.name"></span></pre>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *     angular.module('optionsExample', [])
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         $scope.user = { name: 'say', data: '' };
 *
 *         $scope.cancel = function(e) {
 *           if (e.keyCode === 27) {
 *             $scope.userForm.userName.$rollbackViewValue();
 *           }
 *         };
 *       }]);
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     var model = element(by.binding('user.name'));
 *     var input = element(by.model('user.name'));
 *     var other = element(by.model('user.data'));
 *
 *     it('should allow custom events', function() {
 *       input.sendKeys(' hello');
 *       input.click();
 *       expect(model.getText()).toEqual('say');
 *       other.click();
 *       expect(model.getText()).toEqual('say hello');
 *     });
 *
 *     it('should $rollbackViewValue when model changes', function() {
 *       input.sendKeys(' hello');
 *       expect(input.getAttribute('value')).toEqual('say hello');
 *       input.sendKeys(protractor.Key.ESCAPE);
 *       expect(input.getAttribute('value')).toEqual('say');
 *       other.click();
 *       expect(model.getText()).toEqual('say');
 *     });
 *   </file>
 * </example>
 *
 * ### Debouncing updates
 *
 * The next example shows how to debounce model changes. Model will be updated only 1 sec after last change.
 * If the `Clear` button is pressed, any debounced action is canceled and the value becomes empty.
 *
 * <example name="ngModelOptions-directive-debounce" module="optionsExample">
 *   <file name="index.html">
 *     <div ng-controller="ExampleController">
 *       <form name="userForm">
 *         Name:
 *         <input type="text" name="userName"
 *                ng-model="user.name"
 *                ng-model-options="{ debounce: 1000 }" />
 *         <button ng-click="userForm.userName.$rollbackViewValue(); user.name=''">Clear</button><br />
 *       </form>
 *       <pre>user.name = <span ng-bind="user.name"></span></pre>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *     angular.module('optionsExample', [])
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         $scope.user = { name: 'say' };
 *       }]);
 *   </file>
 * </example>
 *
 * ### Default events, extra triggers, and catch-all debounce values
 *
 * This example shows the relationship between "default" update events and
 * additional `updateOn` triggers.
 *
 * `default` events are those that are bound to the control, and when fired, update the `$viewValue`
 * via {@link ngModel.NgModelController#$setViewValue $setViewValue}. Every event that is not listed
 * in `updateOn` is considered a "default" event, since different control types have different
 * default events.
 *
 * The control in this example updates by "default", "click", and "blur", with different `debounce`
 * values. You can see that "click" doesn't have an individual `debounce` value -
 * therefore it uses the `*` debounce value.
 *
 * There is also a button that calls {@link ngModel.NgModelController#$setViewValue $setViewValue}
 * directly with a "custom" event. Since "custom" is not defined in the `updateOn` list,
 * it is considered a "default" event and will update the
 * control if "default" is defined in `updateOn`, and will receive the "default" debounce value.
 * Note that this is just to illustrate how custom controls would possibly call `$setViewValue`.
 *
 * You can change the `updateOn` and `debounce` configuration to test different scenarios. This
 * is done with {@link ngModel.NgModelController#$overrideModelOptions $overrideModelOptions}.
 *
  <example name="ngModelOptions-advanced" module="optionsExample">
    <file name="index.html">
       <model-update-demo></model-update-demo>
    </file>
    <file name="app.js">
      angular.module('optionsExample', [])
        .component('modelUpdateDemo', {
          templateUrl: 'template.html',
          controller: function() {
            this.name = 'Chinua';

            this.options = {
              updateOn: 'default blur click',
              debounce: {
                default: 2000,
                blur: 0,
                '*': 1000
              }
            };

            this.updateEvents = function() {
              var eventList = this.options.updateOn.split(' ');
              eventList.push('*');
              var events = {};

              for (var i = 0; i < eventList.length; i++) {
                events[eventList[i]] = this.options.debounce[eventList[i]];
              }

              this.events = events;
            };

            this.updateOptions = function() {
              var options = angular.extend(this.options, {
                updateOn: Object.keys(this.events).join(' ').replace('*', ''),
                debounce: this.events
              });

              this.form.input.$overrideModelOptions(options);
            };

            // Initialize the event form
            this.updateEvents();
          }
        });
    </file>
    <file name="template.html">
      <form name="$ctrl.form">
        Input: <input type="text" name="input" ng-model="$ctrl.name" ng-model-options="$ctrl.options" />
      </form>
      Model: <tt>{{$ctrl.name}}</tt>
      <hr>
      <button ng-click="$ctrl.form.input.$setViewValue('some value', 'custom')">Trigger setViewValue with 'some value' and 'custom' event</button>

      <hr>
      <form ng-submit="$ctrl.updateOptions()">
        <b>updateOn</b><br>
        <input type="text" ng-model="$ctrl.options.updateOn" ng-change="$ctrl.updateEvents()" ng-model-options="{debounce: 500}">

        <table>
          <tr>
            <th>Option</th>
            <th>Debounce value</th>
          </tr>
          <tr ng-repeat="(key, value) in $ctrl.events">
            <td>{{key}}</td>
            <td><input type="number" ng-model="$ctrl.events[key]" /></td>
          </tr>
        </table>

        <br>
        <input type="submit" value="Update options">
      </form>
    </file>
  </example>
 *
 *
 * ## Model updates and validation
 *
 * The default behaviour in `ngModel` is that the model value is set to `undefined` when the
 * validation determines that the value is invalid. By setting the `allowInvalid` property to true,
 * the model will still be updated even if the value is invalid.
 *
 *
 * ## Connecting to the scope
 *
 * By setting the `getterSetter` property to true you are telling ngModel that the `ngModel` expression
 * on the scope refers to a "getter/setter" function rather than the value itself.
 *
 * The following example shows how to bind to getter/setters:
 *
 * <example name="ngModelOptions-directive-getter-setter" module="getterSetterExample">
 *   <file name="index.html">
 *     <div ng-controller="ExampleController">
 *       <form name="userForm">
 *         <label>
 *           Name:
 *           <input type="text" name="userName"
 *                  ng-model="user.name"
 *                  ng-model-options="{ getterSetter: true }" />
 *         </label>
 *       </form>
 *       <pre>user.name = <span ng-bind="user.name()"></span></pre>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *     angular.module('getterSetterExample', [])
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         var _name = 'Brian';
 *         $scope.user = {
 *           name: function(newName) {
 *             return angular.isDefined(newName) ? (_name = newName) : _name;
 *           }
 *         };
 *       }]);
 *   </file>
 * </example>
 *
 *
 * ## Programmatically changing options
 *
 * The `ngModelOptions` expression is only evaluated once when the directive is linked; it is not
 * watched for changes. However, it is possible to override the options on a single
 * {@link ngModel.NgModelController} instance with
 * {@link ngModel.NgModelController#$overrideModelOptions `NgModelController#$overrideModelOptions()`}.
 * See also the example for
 * {@link ngModelOptions#default-events-extra-triggers-and-catch-all-debounce-values
 * Default events, extra triggers, and catch-all debounce values}.
 *
 *
 * ## Specifying timezones
 *
 * You can specify the timezone that date/time input directives expect by providing its name in the
 * `timezone` property.
 *
 *
 * ## Formatting the value of `time` and `datetime-local`
 *
 * With the options `timeSecondsFormat` and `timeStripZeroSeconds` it is possible to adjust the value
 * that is displayed in the control. Note that browsers may apply their own formatting
 * in the user interface.
 *
   <example name="ngModelOptions-time-format" module="timeExample">
     <file name="index.html">
       <time-example></time-example>
     </file>
     <file name="script.js">
        angular.module('timeExample', [])
          .component('timeExample', {
            templateUrl: 'timeExample.html',
            controller: function() {
              this.time = new Date(1970, 0, 1, 14, 57, 0);

              this.options = {
                timeSecondsFormat: 'ss',
                timeStripZeroSeconds: true
              };

              this.optionChange = function() {
                this.timeForm.timeFormatted.$overrideModelOptions(this.options);
                this.time = new Date(this.time);
              };
            }
          });
     </file>
     <file name="timeExample.html">
       <form name="$ctrl.timeForm">
         <strong>Default</strong>:
         <input type="time" ng-model="$ctrl.time" step="any" /><br>
         <strong>With options</strong>:
         <input type="time" name="timeFormatted" ng-model="$ctrl.time" step="any" ng-model-options="$ctrl.options" />
         <br>

         Options:<br>
         <code>timeSecondsFormat</code>:
         <input
           type="text"
           ng-model="$ctrl.options.timeSecondsFormat"
           ng-change="$ctrl.optionChange()">
         <br>
         <code>timeStripZeroSeconds</code>:
         <input
           type="checkbox"
           ng-model="$ctrl.options.timeStripZeroSeconds"
           ng-change="$ctrl.optionChange()">
        </form>
      </file>
 *  </example>
 *
 * @param {Object} ngModelOptions options to apply to {@link ngModel} directives on this element and
 *   and its descendents.
 *
 * **General options**:
 *
 *   - `updateOn`: string specifying which event should the input be bound to. You can set several
 *     events using an space delimited list. There is a special event called `default` that
 *     matches the default events belonging to the control. These are the events that are bound to
 *     the control, and when fired, update the `$viewValue` via `$setViewValue`.
 *
 *     `ngModelOptions` considers every event that is not listed in `updateOn` a "default" event,
 *     since different control types use different default events.
 *
 *     See also the section {@link ngModelOptions#triggering-and-debouncing-model-updates
 *     Triggering and debouncing model updates}.
 *
 *   - `debounce`: integer value which contains the debounce model update value in milliseconds. A
 *     value of 0 triggers an immediate update. If an object is supplied instead, you can specify a
 *     custom value for each event. For example:
 *     ```
 *     ng-model-options="{
 *       updateOn: 'default blur',
 *       debounce: { 'default': 500, 'blur': 0 }
 *     }"
 *     ```
 *     You can use the `*` key to specify a debounce value that applies to all events that are not
 *     specifically listed. In the following example, `mouseup` would have a debounce delay of 1000:
 *     ```
 *     ng-model-options="{
 *       updateOn: 'default blur mouseup',
 *       debounce: { 'default': 500, 'blur': 0, '*': 1000 }
 *     }"
 *     ```
 *   - `allowInvalid`: boolean value which indicates that the model can be set with values that did
 *     not validate correctly instead of the default behavior of setting the model to undefined.
 *   - `getterSetter`: boolean value which determines whether or not to treat functions bound to
 *     `ngModel` as getters/setters.
 *
 *
 *  **Input-type specific options**:
 *
 *   - `timezone`: Defines the timezone to be used to read/write the `Date` instance in the model for
 *     `<input type="date" />`, `<input type="time" />`, ... . It understands UTC/GMT and the
 *     continental US time zone abbreviations, but for general use, use a time zone offset, for
 *     example, `'+0430'` (4 hours, 30 minutes east of the Greenwich meridian)
 *     If not specified, the timezone of the browser will be used.
 *     Note that changing the timezone will have no effect on the current date, and is only applied after
 *     the next input / model change.
 *
 *   - `timeSecondsFormat`: Defines if the `time` and `datetime-local` types should show seconds and
 *     milliseconds. The option follows the format string of {@link date date filter}.
 *     By default, the options is `undefined` which is equal to `'ss.sss'` (seconds and milliseconds).
 *     The other options are `'ss'` (strips milliseconds), and `''` (empty string), which strips both
 *     seconds and milliseconds.
 *     Note that browsers that support `time` and `datetime-local` require the hour and minutes
 *     part of the time string, and may show the value differently in the user interface.
 *     {@link ngModelOptions#formatting-the-value-of-time-and-datetime-local- See the example}.
 *
 *   - `timeStripZeroSeconds`: Defines if the `time` and `datetime-local` types should strip the
 *     seconds and milliseconds from the formatted value if they are zero. This option is applied
 *     after `timeSecondsFormat`.
 *     This option can be used to make the formatting consistent over different browsers, as some
 *     browsers with support for `time` will natively hide the milliseconds and
 *     seconds if they are zero, but others won't, and browsers that don't implement these input
 *     types will always show the full string.
 *     {@link ngModelOptions#formatting-the-value-of-time-and-datetime-local- See the example}.
 *
 */
var ngModelOptionsDirective = function() {
  NgModelOptionsController.$inject = ['$attrs', '$scope'];
  function NgModelOptionsController($attrs, $scope) {
    this.$$attrs = $attrs;
    this.$$scope = $scope;
  }
  NgModelOptionsController.prototype = {
    $onInit: function() {
      var parentOptions = this.parentCtrl ? this.parentCtrl.$options : defaultModelOptions;
      var modelOptionsDefinition = this.$$scope.$eval(this.$$attrs.ngModelOptions);

      this.$options = parentOptions.createChild(modelOptionsDefinition);
    }
  };

  return {
    restrict: 'A',
    // ngModelOptions needs to run before ngModel and input directives
    priority: 10,
    require: {parentCtrl: '?^^ngModelOptions'},
    bindToController: true,
    controller: NgModelOptionsController
  };
};


// shallow copy over values from `src` that are not already specified on `dst`
function defaults(dst, src) {
  forEach(src, function(value, key) {
    if (!isDefined(dst[key])) {
      dst[key] = value;
    }
  });
}

/**
 * @ngdoc directive
 * @name ngNonBindable
 * @restrict AC
 * @priority 1000
 * @element ANY
 *
 * @description
 * The `ngNonBindable` directive tells AngularJS not to compile or bind the contents of the current
 * DOM element, including directives on the element itself that have a lower priority than
 * `ngNonBindable`. This is useful if the element contains what appears to be AngularJS directives
 * and bindings but which should be ignored by AngularJS. This could be the case if you have a site
 * that displays snippets of code, for instance.
 *
 * @example
 * In this example there are two locations where a simple interpolation binding (`{{}}`) is present,
 * but the one wrapped in `ngNonBindable` is left alone.
 *
  <example name="ng-non-bindable">
    <file name="index.html">
      <div>Normal: {{1 + 2}}</div>
      <div ng-non-bindable>Ignored: {{1 + 2}}</div>
    </file>
    <file name="protractor.js" type="protractor">
     it('should check ng-non-bindable', function() {
       expect(element(by.binding('1 + 2')).getText()).toContain('3');
       expect(element.all(by.css('div')).last().getText()).toMatch(/1 \+ 2/);
     });
    </file>
  </example>
 */
var ngNonBindableDirective = ngDirective({ terminal: true, priority: 1000 });

/* exported ngOptionsDirective */

/* global jqLiteRemove */

var ngOptionsMinErr = minErr('ngOptions');

/**
 * @ngdoc directive
 * @name ngOptions
 * @restrict A
 *
 * @description
 *
 * The `ngOptions` attribute can be used to dynamically generate a list of `<option>`
 * elements for the `<select>` element using the array or object obtained by evaluating the
 * `ngOptions` comprehension expression.
 *
 * In many cases, {@link ng.directive:ngRepeat ngRepeat} can be used on `<option>` elements instead of
 * `ngOptions` to achieve a similar result. However, `ngOptions` provides some benefits:
 * - more flexibility in how the `<select>`'s model is assigned via the `select` **`as`** part of the
 * comprehension expression
 * - reduced memory consumption by not creating a new scope for each repeated instance
 * - increased render speed by creating the options in a documentFragment instead of individually
 *
 * When an item in the `<select>` menu is selected, the array element or object property
 * represented by the selected option will be bound to the model identified by the `ngModel`
 * directive.
 *
 * Optionally, a single hard-coded `<option>` element, with the value set to an empty string, can
 * be nested into the `<select>` element. This element will then represent the `null` or "not selected"
 * option. See example below for demonstration.
 *
 * ## Complex Models (objects or collections)
 *
 * By default, `ngModel` watches the model by reference, not value. This is important to know when
 * binding the select to a model that is an object or a collection.
 *
 * One issue occurs if you want to preselect an option. For example, if you set
 * the model to an object that is equal to an object in your collection, `ngOptions` won't be able to set the selection,
 * because the objects are not identical. So by default, you should always reference the item in your collection
 * for preselections, e.g.: `$scope.selected = $scope.collection[3]`.
 *
 * Another solution is to use a `track by` clause, because then `ngOptions` will track the identity
 * of the item not by reference, but by the result of the `track by` expression. For example, if your
 * collection items have an id property, you would `track by item.id`.
 *
 * A different issue with objects or collections is that ngModel won't detect if an object property or
 * a collection item changes. For that reason, `ngOptions` additionally watches the model using
 * `$watchCollection`, when the expression contains a `track by` clause or the the select has the `multiple` attribute.
 * This allows ngOptions to trigger a re-rendering of the options even if the actual object/collection
 * has not changed identity, but only a property on the object or an item in the collection changes.
 *
 * Note that `$watchCollection` does a shallow comparison of the properties of the object (or the items in the collection
 * if the model is an array). This means that changing a property deeper than the first level inside the
 * object/collection will not trigger a re-rendering.
 *
 * ## `select` **`as`**
 *
 * Using `select` **`as`** will bind the result of the `select` expression to the model, but
 * the value of the `<select>` and `<option>` html elements will be either the index (for array data sources)
 * or property name (for object data sources) of the value within the collection. If a **`track by`** expression
 * is used, the result of that expression will be set as the value of the `option` and `select` elements.
 *
 *
 * ### `select` **`as`** and **`track by`**
 *
 * <div class="alert alert-warning">
 * Be careful when using `select` **`as`** and **`track by`** in the same expression.
 * </div>
 *
 * Given this array of items on the $scope:
 *
 * ```js
 * $scope.items = [{
 *   id: 1,
 *   label: 'aLabel',
 *   subItem: { name: 'aSubItem' }
 * }, {
 *   id: 2,
 *   label: 'bLabel',
 *   subItem: { name: 'bSubItem' }
 * }];
 * ```
 *
 * This will work:
 *
 * ```html
 * <select ng-options="item as item.label for item in items track by item.id" ng-model="selected"></select>
 * ```
 * ```js
 * $scope.selected = $scope.items[0];
 * ```
 *
 * but this will not work:
 *
 * ```html
 * <select ng-options="item.subItem as item.label for item in items track by item.id" ng-model="selected"></select>
 * ```
 * ```js
 * $scope.selected = $scope.items[0].subItem;
 * ```
 *
 * In both examples, the **`track by`** expression is applied successfully to each `item` in the
 * `items` array. Because the selected option has been set programmatically in the controller, the
 * **`track by`** expression is also applied to the `ngModel` value. In the first example, the
 * `ngModel` value is `items[0]` and the **`track by`** expression evaluates to `items[0].id` with
 * no issue. In the second example, the `ngModel` value is `items[0].subItem` and the **`track by`**
 * expression evaluates to `items[0].subItem.id` (which is undefined). As a result, the model value
 * is not matched against any `<option>` and the `<select>` appears as having no selected value.
 *
 *
 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
 * @param {comprehension_expression} ngOptions in one of the following forms:
 *
 *   * for array data sources:
 *     * `label` **`for`** `value` **`in`** `array`
 *     * `select` **`as`** `label` **`for`** `value` **`in`** `array`
 *     * `label` **`group by`** `group` **`for`** `value` **`in`** `array`
 *     * `label` **`disable when`** `disable` **`for`** `value` **`in`** `array`
 *     * `label` **`group by`** `group` **`for`** `value` **`in`** `array` **`track by`** `trackexpr`
 *     * `label` **`disable when`** `disable` **`for`** `value` **`in`** `array` **`track by`** `trackexpr`
 *     * `label` **`for`** `value` **`in`** `array` | orderBy:`orderexpr` **`track by`** `trackexpr`
 *        (for including a filter with `track by`)
 *   * for object data sources:
 *     * `label` **`for (`**`key` **`,`** `value`**`) in`** `object`
 *     * `select` **`as`** `label` **`for (`**`key` **`,`** `value`**`) in`** `object`
 *     * `label` **`group by`** `group` **`for (`**`key`**`,`** `value`**`) in`** `object`
 *     * `label` **`disable when`** `disable` **`for (`**`key`**`,`** `value`**`) in`** `object`
 *     * `select` **`as`** `label` **`group by`** `group`
 *         **`for` `(`**`key`**`,`** `value`**`) in`** `object`
 *     * `select` **`as`** `label` **`disable when`** `disable`
 *         **`for` `(`**`key`**`,`** `value`**`) in`** `object`
 *
 * Where:
 *
 *   * `array` / `object`: an expression which evaluates to an array / object to iterate over.
 *   * `value`: local variable which will refer to each item in the `array` or each property value
 *      of `object` during iteration.
 *   * `key`: local variable which will refer to a property name in `object` during iteration.
 *   * `label`: The result of this expression will be the label for `<option>` element. The
 *     `expression` will most likely refer to the `value` variable (e.g. `value.propertyName`).
 *   * `select`: The result of this expression will be bound to the model of the parent `<select>`
 *      element. If not specified, `select` expression will default to `value`.
 *   * `group`: The result of this expression will be used to group options using the `<optgroup>`
 *      DOM element.
 *   * `disable`: The result of this expression will be used to disable the rendered `<option>`
 *      element. Return `true` to disable.
 *   * `trackexpr`: Used when working with an array of objects. The result of this expression will be
 *      used to identify the objects in the array. The `trackexpr` will most likely refer to the
 *     `value` variable (e.g. `value.propertyName`). With this the selection is preserved
 *      even when the options are recreated (e.g. reloaded from the server).
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} required The control is considered valid only if value is entered.
 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
 *    `required` when you want to data-bind to the `required` attribute.
 * @param {string=} ngAttrSize sets the size of the select element dynamically. Uses the
 * {@link guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes ngAttr} directive.
 *
 * @example
    <example module="selectExample" name="select">
      <file name="index.html">
        <script>
        angular.module('selectExample', [])
          .controller('ExampleController', ['$scope', function($scope) {
            $scope.colors = [
              {name:'black', shade:'dark'},
              {name:'white', shade:'light', notAnOption: true},
              {name:'red', shade:'dark'},
              {name:'blue', shade:'dark', notAnOption: true},
              {name:'yellow', shade:'light', notAnOption: false}
            ];
            $scope.myColor = $scope.colors[2]; // red
          }]);
        </script>
        <div ng-controller="ExampleController">
          <ul>
            <li ng-repeat="color in colors">
              <label>Name: <input ng-model="color.name"></label>
              <label><input type="checkbox" ng-model="color.notAnOption"> Disabled?</label>
              <button ng-click="colors.splice($index, 1)" aria-label="Remove">X</button>
            </li>
            <li>
              <button ng-click="colors.push({})">add</button>
            </li>
          </ul>
          <hr/>
          <label>Color (null not allowed):
            <select ng-model="myColor" ng-options="color.name for color in colors"></select>
          </label><br/>
          <label>Color (null allowed):
          <span  class="nullable">
            <select ng-model="myColor" ng-options="color.name for color in colors">
              <option value="">-- choose color --</option>
            </select>
          </span></label><br/>

          <label>Color grouped by shade:
            <select ng-model="myColor" ng-options="color.name group by color.shade for color in colors">
            </select>
          </label><br/>

          <label>Color grouped by shade, with some disabled:
            <select ng-model="myColor"
                  ng-options="color.name group by color.shade disable when color.notAnOption for color in colors">
            </select>
          </label><br/>



          Select <button ng-click="myColor = { name:'not in list', shade: 'other' }">bogus</button>.
          <br/>
          <hr/>
          Currently selected: {{ {selected_color:myColor} }}
          <div style="border:solid 1px black; height:20px"
               ng-style="{'background-color':myColor.name}">
          </div>
        </div>
      </file>
      <file name="protractor.js" type="protractor">
         it('should check ng-options', function() {
           expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('red');
           element.all(by.model('myColor')).first().click();
           element.all(by.css('select[ng-model="myColor"] option')).first().click();
           expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('black');
           element(by.css('.nullable select[ng-model="myColor"]')).click();
           element.all(by.css('.nullable select[ng-model="myColor"] option')).first().click();
           expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('null');
         });
      </file>
    </example>
 */

/* eslint-disable max-len */
//                     //00001111111111000000000002222222222000000000000000000000333333333300000000000000000000000004444444444400000000000005555555555555000000000666666666666600000007777777777777000000000000000888888888800000000000000000009999999999
var NG_OPTIONS_REGEXP = /^\s*([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+group\s+by\s+([\s\S]+?))?(?:\s+disable\s+when\s+([\s\S]+?))?\s+for\s+(?:([$\w][$\w]*)|(?:\(\s*([$\w][$\w]*)\s*,\s*([$\w][$\w]*)\s*\)))\s+in\s+([\s\S]+?)(?:\s+track\s+by\s+([\s\S]+?))?$/;
                        // 1: value expression (valueFn)
                        // 2: label expression (displayFn)
                        // 3: group by expression (groupByFn)
                        // 4: disable when expression (disableWhenFn)
                        // 5: array item variable name
                        // 6: object item key variable name
                        // 7: object item value variable name
                        // 8: collection expression
                        // 9: track by expression
/* eslint-enable */


var ngOptionsDirective = ['$compile', '$document', '$parse', function($compile, $document, $parse) {

  function parseOptionsExpression(optionsExp, selectElement, scope) {

    var match = optionsExp.match(NG_OPTIONS_REGEXP);
    if (!(match)) {
      throw ngOptionsMinErr('iexp',
        'Expected expression in form of ' +
        '\'_select_ (as _label_)? for (_key_,)?_value_ in _collection_\'' +
        ' but got \'{0}\'. Element: {1}',
        optionsExp, startingTag(selectElement));
    }

    // Extract the parts from the ngOptions expression

    // The variable name for the value of the item in the collection
    var valueName = match[5] || match[7];
    // The variable name for the key of the item in the collection
    var keyName = match[6];

    // An expression that generates the viewValue for an option if there is a label expression
    var selectAs = / as /.test(match[0]) && match[1];
    // An expression that is used to track the id of each object in the options collection
    var trackBy = match[9];
    // An expression that generates the viewValue for an option if there is no label expression
    var valueFn = $parse(match[2] ? match[1] : valueName);
    var selectAsFn = selectAs && $parse(selectAs);
    var viewValueFn = selectAsFn || valueFn;
    var trackByFn = trackBy && $parse(trackBy);

    // Get the value by which we are going to track the option
    // if we have a trackFn then use that (passing scope and locals)
    // otherwise just hash the given viewValue
    var getTrackByValueFn = trackBy ?
                              function(value, locals) { return trackByFn(scope, locals); } :
                              function getHashOfValue(value) { return hashKey(value); };
    var getTrackByValue = function(value, key) {
      return getTrackByValueFn(value, getLocals(value, key));
    };

    var displayFn = $parse(match[2] || match[1]);
    var groupByFn = $parse(match[3] || '');
    var disableWhenFn = $parse(match[4] || '');
    var valuesFn = $parse(match[8]);

    var locals = {};
    var getLocals = keyName ? function(value, key) {
      locals[keyName] = key;
      locals[valueName] = value;
      return locals;
    } : function(value) {
      locals[valueName] = value;
      return locals;
    };


    function Option(selectValue, viewValue, label, group, disabled) {
      this.selectValue = selectValue;
      this.viewValue = viewValue;
      this.label = label;
      this.group = group;
      this.disabled = disabled;
    }

    function getOptionValuesKeys(optionValues) {
      var optionValuesKeys;

      if (!keyName && isArrayLike(optionValues)) {
        optionValuesKeys = optionValues;
      } else {
        // if object, extract keys, in enumeration order, unsorted
        optionValuesKeys = [];
        for (var itemKey in optionValues) {
          if (optionValues.hasOwnProperty(itemKey) && itemKey.charAt(0) !== '$') {
            optionValuesKeys.push(itemKey);
          }
        }
      }
      return optionValuesKeys;
    }

    return {
      trackBy: trackBy,
      getTrackByValue: getTrackByValue,
      getWatchables: $parse(valuesFn, function(optionValues) {
        // Create a collection of things that we would like to watch (watchedArray)
        // so that they can all be watched using a single $watchCollection
        // that only runs the handler once if anything changes
        var watchedArray = [];
        optionValues = optionValues || [];

        var optionValuesKeys = getOptionValuesKeys(optionValues);
        var optionValuesLength = optionValuesKeys.length;
        for (var index = 0; index < optionValuesLength; index++) {
          var key = (optionValues === optionValuesKeys) ? index : optionValuesKeys[index];
          var value = optionValues[key];

          var locals = getLocals(value, key);
          var selectValue = getTrackByValueFn(value, locals);
          watchedArray.push(selectValue);

          // Only need to watch the displayFn if there is a specific label expression
          if (match[2] || match[1]) {
            var label = displayFn(scope, locals);
            watchedArray.push(label);
          }

          // Only need to watch the disableWhenFn if there is a specific disable expression
          if (match[4]) {
            var disableWhen = disableWhenFn(scope, locals);
            watchedArray.push(disableWhen);
          }
        }
        return watchedArray;
      }),

      getOptions: function() {

        var optionItems = [];
        var selectValueMap = {};

        // The option values were already computed in the `getWatchables` fn,
        // which must have been called to trigger `getOptions`
        var optionValues = valuesFn(scope) || [];
        var optionValuesKeys = getOptionValuesKeys(optionValues);
        var optionValuesLength = optionValuesKeys.length;

        for (var index = 0; index < optionValuesLength; index++) {
          var key = (optionValues === optionValuesKeys) ? index : optionValuesKeys[index];
          var value = optionValues[key];
          var locals = getLocals(value, key);
          var viewValue = viewValueFn(scope, locals);
          var selectValue = getTrackByValueFn(viewValue, locals);
          var label = displayFn(scope, locals);
          var group = groupByFn(scope, locals);
          var disabled = disableWhenFn(scope, locals);
          var optionItem = new Option(selectValue, viewValue, label, group, disabled);

          optionItems.push(optionItem);
          selectValueMap[selectValue] = optionItem;
        }

        return {
          items: optionItems,
          selectValueMap: selectValueMap,
          getOptionFromViewValue: function(value) {
            return selectValueMap[getTrackByValue(value)];
          },
          getViewValueFromOption: function(option) {
            // If the viewValue could be an object that may be mutated by the application,
            // we need to make a copy and not return the reference to the value on the option.
            return trackBy ? copy(option.viewValue) : option.viewValue;
          }
        };
      }
    };
  }


  // Support: IE 9 only
  // We can't just jqLite('<option>') since jqLite is not smart enough
  // to create it in <select> and IE barfs otherwise.
  var optionTemplate = window.document.createElement('option'),
      optGroupTemplate = window.document.createElement('optgroup');

    function ngOptionsPostLink(scope, selectElement, attr, ctrls) {

      var selectCtrl = ctrls[0];
      var ngModelCtrl = ctrls[1];
      var multiple = attr.multiple;

      // The emptyOption allows the application developer to provide their own custom "empty"
      // option when the viewValue does not match any of the option values.
      for (var i = 0, children = selectElement.children(), ii = children.length; i < ii; i++) {
        if (children[i].value === '') {
          selectCtrl.hasEmptyOption = true;
          selectCtrl.emptyOption = children.eq(i);
          break;
        }
      }

      // The empty option will be compiled and rendered before we first generate the options
      selectElement.empty();

      var providedEmptyOption = !!selectCtrl.emptyOption;

      var unknownOption = jqLite(optionTemplate.cloneNode(false));
      unknownOption.val('?');

      var options;
      var ngOptions = parseOptionsExpression(attr.ngOptions, selectElement, scope);
      // This stores the newly created options before they are appended to the select.
      // Since the contents are removed from the fragment when it is appended,
      // we only need to create it once.
      var listFragment = $document[0].createDocumentFragment();

      // Overwrite the implementation. ngOptions doesn't use hashes
      selectCtrl.generateUnknownOptionValue = function(val) {
        return '?';
      };

      // Update the controller methods for multiple selectable options
      if (!multiple) {

        selectCtrl.writeValue = function writeNgOptionsValue(value) {
          // The options might not be defined yet when ngModel tries to render
          if (!options) return;

          var selectedOption = selectElement[0].options[selectElement[0].selectedIndex];
          var option = options.getOptionFromViewValue(value);

          // Make sure to remove the selected attribute from the previously selected option
          // Otherwise, screen readers might get confused
          if (selectedOption) selectedOption.removeAttribute('selected');

          if (option) {
            // Don't update the option when it is already selected.
            // For example, the browser will select the first option by default. In that case,
            // most properties are set automatically - except the `selected` attribute, which we
            // set always

            if (selectElement[0].value !== option.selectValue) {
              selectCtrl.removeUnknownOption();

              selectElement[0].value = option.selectValue;
              option.element.selected = true;
            }

            option.element.setAttribute('selected', 'selected');
          } else {
            selectCtrl.selectUnknownOrEmptyOption(value);
          }
        };

        selectCtrl.readValue = function readNgOptionsValue() {

          var selectedOption = options.selectValueMap[selectElement.val()];

          if (selectedOption && !selectedOption.disabled) {
            selectCtrl.unselectEmptyOption();
            selectCtrl.removeUnknownOption();
            return options.getViewValueFromOption(selectedOption);
          }
          return null;
        };

        // If we are using `track by` then we must watch the tracked value on the model
        // since ngModel only watches for object identity change
        // FIXME: When a user selects an option, this watch will fire needlessly
        if (ngOptions.trackBy) {
          scope.$watch(
            function() { return ngOptions.getTrackByValue(ngModelCtrl.$viewValue); },
            function() { ngModelCtrl.$render(); }
          );
        }

      } else {

        selectCtrl.writeValue = function writeNgOptionsMultiple(values) {
          // The options might not be defined yet when ngModel tries to render
          if (!options) return;

          // Only set `<option>.selected` if necessary, in order to prevent some browsers from
          // scrolling to `<option>` elements that are outside the `<select>` element's viewport.
          var selectedOptions = values && values.map(getAndUpdateSelectedOption) || [];

          options.items.forEach(function(option) {
            if (option.element.selected && !includes(selectedOptions, option)) {
              option.element.selected = false;
            }
          });
        };


        selectCtrl.readValue = function readNgOptionsMultiple() {
          var selectedValues = selectElement.val() || [],
              selections = [];

          forEach(selectedValues, function(value) {
            var option = options.selectValueMap[value];
            if (option && !option.disabled) selections.push(options.getViewValueFromOption(option));
          });

          return selections;
        };

        // If we are using `track by` then we must watch these tracked values on the model
        // since ngModel only watches for object identity change
        if (ngOptions.trackBy) {

          scope.$watchCollection(function() {
            if (isArray(ngModelCtrl.$viewValue)) {
              return ngModelCtrl.$viewValue.map(function(value) {
                return ngOptions.getTrackByValue(value);
              });
            }
          }, function() {
            ngModelCtrl.$render();
          });

        }
      }

      if (providedEmptyOption) {

        // compile the element since there might be bindings in it
        $compile(selectCtrl.emptyOption)(scope);

        selectElement.prepend(selectCtrl.emptyOption);

        if (selectCtrl.emptyOption[0].nodeType === NODE_TYPE_COMMENT) {
          // This means the empty option has currently no actual DOM node, probably because
          // it has been modified by a transclusion directive.
          selectCtrl.hasEmptyOption = false;

          // Redefine the registerOption function, which will catch
          // options that are added by ngIf etc. (rendering of the node is async because of
          // lazy transclusion)
          selectCtrl.registerOption = function(optionScope, optionEl) {
            if (optionEl.val() === '') {
              selectCtrl.hasEmptyOption = true;
              selectCtrl.emptyOption = optionEl;
              selectCtrl.emptyOption.removeClass('ng-scope');
              // This ensures the new empty option is selected if previously no option was selected
              ngModelCtrl.$render();

              optionEl.on('$destroy', function() {
                var needsRerender = selectCtrl.$isEmptyOptionSelected();

                selectCtrl.hasEmptyOption = false;
                selectCtrl.emptyOption = undefined;

                if (needsRerender) ngModelCtrl.$render();
              });
            }
          };

        } else {
          // remove the class, which is added automatically because we recompile the element and it
          // becomes the compilation root
          selectCtrl.emptyOption.removeClass('ng-scope');
        }

      }

      // We will re-render the option elements if the option values or labels change
      scope.$watchCollection(ngOptions.getWatchables, updateOptions);

      // ------------------------------------------------------------------ //

      function addOptionElement(option, parent) {
        var optionElement = optionTemplate.cloneNode(false);
        parent.appendChild(optionElement);
        updateOptionElement(option, optionElement);
      }

      function getAndUpdateSelectedOption(viewValue) {
        var option = options.getOptionFromViewValue(viewValue);
        var element = option && option.element;

        if (element && !element.selected) element.selected = true;

        return option;
      }

      function updateOptionElement(option, element) {
        option.element = element;
        element.disabled = option.disabled;
        // Support: IE 11 only, Edge 12-13 only
        // NOTE: The label must be set before the value, otherwise IE 11 & Edge create unresponsive
        // selects in certain circumstances when multiple selects are next to each other and display
        // the option list in listbox style, i.e. the select is [multiple], or specifies a [size].
        // See https://github.com/angular/angular.js/issues/11314 for more info.
        // This is unfortunately untestable with unit / e2e tests
        if (option.label !== element.label) {
          element.label = option.label;
          element.textContent = option.label;
        }
        element.value = option.selectValue;
      }

      function updateOptions() {
        var previousValue = options && selectCtrl.readValue();

        // We must remove all current options, but cannot simply set innerHTML = null
        // since the providedEmptyOption might have an ngIf on it that inserts comments which we
        // must preserve.
        // Instead, iterate over the current option elements and remove them or their optgroup
        // parents
        if (options) {

          for (var i = options.items.length - 1; i >= 0; i--) {
            var option = options.items[i];
            if (isDefined(option.group)) {
              jqLiteRemove(option.element.parentNode);
            } else {
              jqLiteRemove(option.element);
            }
          }
        }

        options = ngOptions.getOptions();

        var groupElementMap = {};

        options.items.forEach(function addOption(option) {
          var groupElement;

          if (isDefined(option.group)) {

            // This option is to live in a group
            // See if we have already created this group
            groupElement = groupElementMap[option.group];

            if (!groupElement) {

              groupElement = optGroupTemplate.cloneNode(false);
              listFragment.appendChild(groupElement);

              // Update the label on the group element
              // "null" is special cased because of Safari
              groupElement.label = option.group === null ? 'null' : option.group;

              // Store it for use later
              groupElementMap[option.group] = groupElement;
            }

            addOptionElement(option, groupElement);

          } else {

            // This option is not in a group
            addOptionElement(option, listFragment);
          }
        });

        selectElement[0].appendChild(listFragment);

        ngModelCtrl.$render();

        // Check to see if the value has changed due to the update to the options
        if (!ngModelCtrl.$isEmpty(previousValue)) {
          var nextValue = selectCtrl.readValue();
          var isNotPrimitive = ngOptions.trackBy || multiple;
          if (isNotPrimitive ? !equals(previousValue, nextValue) : previousValue !== nextValue) {
            ngModelCtrl.$setViewValue(nextValue);
            ngModelCtrl.$render();
          }
        }
      }
  }

  return {
    restrict: 'A',
    terminal: true,
    require: ['select', 'ngModel'],
    link: {
      pre: function ngOptionsPreLink(scope, selectElement, attr, ctrls) {
        // Deactivate the SelectController.register method to prevent
        // option directives from accidentally registering themselves
        // (and unwanted $destroy handlers etc.)
        ctrls[0].registerOption = noop;
      },
      post: ngOptionsPostLink
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngPluralize
 * @restrict EA
 *
 * @description
 * `ngPluralize` is a directive that displays messages according to en-US localization rules.
 * These rules are bundled with angular.js, but can be overridden
 * (see {@link guide/i18n AngularJS i18n} dev guide). You configure ngPluralize directive
 * by specifying the mappings between
 * [plural categories](http://unicode.org/repos/cldr-tmp/trunk/diff/supplemental/language_plural_rules.html)
 * and the strings to be displayed.
 *
 * ## Plural categories and explicit number rules
 * There are two
 * [plural categories](http://unicode.org/repos/cldr-tmp/trunk/diff/supplemental/language_plural_rules.html)
 * in AngularJS's default en-US locale: "one" and "other".
 *
 * While a plural category may match many numbers (for example, in en-US locale, "other" can match
 * any number that is not 1), an explicit number rule can only match one number. For example, the
 * explicit number rule for "3" matches the number 3. There are examples of plural categories
 * and explicit number rules throughout the rest of this documentation.
 *
 * ## Configuring ngPluralize
 * You configure ngPluralize by providing 2 attributes: `count` and `when`.
 * You can also provide an optional attribute, `offset`.
 *
 * The value of the `count` attribute can be either a string or an {@link guide/expression
 * AngularJS expression}; these are evaluated on the current scope for its bound value.
 *
 * The `when` attribute specifies the mappings between plural categories and the actual
 * string to be displayed. The value of the attribute should be a JSON object.
 *
 * The following example shows how to configure ngPluralize:
 *
 * ```html
 * <ng-pluralize count="personCount"
                 when="{'0': 'Nobody is viewing.',
 *                      'one': '1 person is viewing.',
 *                      'other': '{} people are viewing.'}">
 * </ng-pluralize>
 *```
 *
 * In the example, `"0: Nobody is viewing."` is an explicit number rule. If you did not
 * specify this rule, 0 would be matched to the "other" category and "0 people are viewing"
 * would be shown instead of "Nobody is viewing". You can specify an explicit number rule for
 * other numbers, for example 12, so that instead of showing "12 people are viewing", you can
 * show "a dozen people are viewing".
 *
 * You can use a set of closed braces (`{}`) as a placeholder for the number that you want substituted
 * into pluralized strings. In the previous example, AngularJS will replace `{}` with
 * <span ng-non-bindable>`{{personCount}}`</span>. The closed braces `{}` is a placeholder
 * for <span ng-non-bindable>{{numberExpression}}</span>.
 *
 * If no rule is defined for a category, then an empty string is displayed and a warning is generated.
 * Note that some locales define more categories than `one` and `other`. For example, fr-fr defines `few` and `many`.
 *
 * ## Configuring ngPluralize with offset
 * The `offset` attribute allows further customization of pluralized text, which can result in
 * a better user experience. For example, instead of the message "4 people are viewing this document",
 * you might display "John, Kate and 2 others are viewing this document".
 * The offset attribute allows you to offset a number by any desired value.
 * Let's take a look at an example:
 *
 * ```html
 * <ng-pluralize count="personCount" offset=2
 *               when="{'0': 'Nobody is viewing.',
 *                      '1': '{{person1}} is viewing.',
 *                      '2': '{{person1}} and {{person2}} are viewing.',
 *                      'one': '{{person1}}, {{person2}} and one other person are viewing.',
 *                      'other': '{{person1}}, {{person2}} and {} other people are viewing.'}">
 * </ng-pluralize>
 * ```
 *
 * Notice that we are still using two plural categories(one, other), but we added
 * three explicit number rules 0, 1 and 2.
 * When one person, perhaps John, views the document, "John is viewing" will be shown.
 * When three people view the document, no explicit number rule is found, so
 * an offset of 2 is taken off 3, and AngularJS uses 1 to decide the plural category.
 * In this case, plural category 'one' is matched and "John, Mary and one other person are viewing"
 * is shown.
 *
 * Note that when you specify offsets, you must provide explicit number rules for
 * numbers from 0 up to and including the offset. If you use an offset of 3, for example,
 * you must provide explicit number rules for 0, 1, 2 and 3. You must also provide plural strings for
 * plural categories "one" and "other".
 *
 * @param {string|expression} count The variable to be bound to.
 * @param {string} when The mapping between plural category to its corresponding strings.
 * @param {number=} offset Offset to deduct from the total number.
 *
 * @example
    <example module="pluralizeExample" name="ng-pluralize">
      <file name="index.html">
        <script>
          angular.module('pluralizeExample', [])
            .controller('ExampleController', ['$scope', function($scope) {
              $scope.person1 = 'Igor';
              $scope.person2 = 'Misko';
              $scope.personCount = 1;
            }]);
        </script>
        <div ng-controller="ExampleController">
          <label>Person 1:<input type="text" ng-model="person1" value="Igor" /></label><br/>
          <label>Person 2:<input type="text" ng-model="person2" value="Misko" /></label><br/>
          <label>Number of People:<input type="text" ng-model="personCount" value="1" /></label><br/>

          <!--- Example with simple pluralization rules for en locale --->
          Without Offset:
          <ng-pluralize count="personCount"
                        when="{'0': 'Nobody is viewing.',
                               'one': '1 person is viewing.',
                               'other': '{} people are viewing.'}">
          </ng-pluralize><br>

          <!--- Example with offset --->
          With Offset(2):
          <ng-pluralize count="personCount" offset=2
                        when="{'0': 'Nobody is viewing.',
                               '1': '{{person1}} is viewing.',
                               '2': '{{person1}} and {{person2}} are viewing.',
                               'one': '{{person1}}, {{person2}} and one other person are viewing.',
                               'other': '{{person1}}, {{person2}} and {} other people are viewing.'}">
          </ng-pluralize>
        </div>
      </file>
      <file name="protractor.js" type="protractor">
        it('should show correct pluralized string', function() {
          var withoutOffset = element.all(by.css('ng-pluralize')).get(0);
          var withOffset = element.all(by.css('ng-pluralize')).get(1);
          var countInput = element(by.model('personCount'));

          expect(withoutOffset.getText()).toEqual('1 person is viewing.');
          expect(withOffset.getText()).toEqual('Igor is viewing.');

          countInput.clear();
          countInput.sendKeys('0');

          expect(withoutOffset.getText()).toEqual('Nobody is viewing.');
          expect(withOffset.getText()).toEqual('Nobody is viewing.');

          countInput.clear();
          countInput.sendKeys('2');

          expect(withoutOffset.getText()).toEqual('2 people are viewing.');
          expect(withOffset.getText()).toEqual('Igor and Misko are viewing.');

          countInput.clear();
          countInput.sendKeys('3');

          expect(withoutOffset.getText()).toEqual('3 people are viewing.');
          expect(withOffset.getText()).toEqual('Igor, Misko and one other person are viewing.');

          countInput.clear();
          countInput.sendKeys('4');

          expect(withoutOffset.getText()).toEqual('4 people are viewing.');
          expect(withOffset.getText()).toEqual('Igor, Misko and 2 other people are viewing.');
        });
        it('should show data-bound names', function() {
          var withOffset = element.all(by.css('ng-pluralize')).get(1);
          var personCount = element(by.model('personCount'));
          var person1 = element(by.model('person1'));
          var person2 = element(by.model('person2'));
          personCount.clear();
          personCount.sendKeys('4');
          person1.clear();
          person1.sendKeys('Di');
          person2.clear();
          person2.sendKeys('Vojta');
          expect(withOffset.getText()).toEqual('Di, Vojta and 2 other people are viewing.');
        });
      </file>
    </example>
 */
var ngPluralizeDirective = ['$locale', '$interpolate', '$log', function($locale, $interpolate, $log) {
  var BRACE = /{}/g,
      IS_WHEN = /^when(Minus)?(.+)$/;

  return {
    link: function(scope, element, attr) {
      var numberExp = attr.count,
          whenExp = attr.$attr.when && element.attr(attr.$attr.when), // we have {{}} in attrs
          offset = attr.offset || 0,
          whens = scope.$eval(whenExp) || {},
          whensExpFns = {},
          startSymbol = $interpolate.startSymbol(),
          endSymbol = $interpolate.endSymbol(),
          braceReplacement = startSymbol + numberExp + '-' + offset + endSymbol,
          watchRemover = angular.noop,
          lastCount;

      forEach(attr, function(expression, attributeName) {
        var tmpMatch = IS_WHEN.exec(attributeName);
        if (tmpMatch) {
          var whenKey = (tmpMatch[1] ? '-' : '') + lowercase(tmpMatch[2]);
          whens[whenKey] = element.attr(attr.$attr[attributeName]);
        }
      });
      forEach(whens, function(expression, key) {
        whensExpFns[key] = $interpolate(expression.replace(BRACE, braceReplacement));

      });

      scope.$watch(numberExp, function ngPluralizeWatchAction(newVal) {
        var count = parseFloat(newVal);
        var countIsNaN = isNumberNaN(count);

        if (!countIsNaN && !(count in whens)) {
          // If an explicit number rule such as 1, 2, 3... is defined, just use it.
          // Otherwise, check it against pluralization rules in $locale service.
          count = $locale.pluralCat(count - offset);
        }

        // If both `count` and `lastCount` are NaN, we don't need to re-register a watch.
        // In JS `NaN !== NaN`, so we have to explicitly check.
        if ((count !== lastCount) && !(countIsNaN && isNumberNaN(lastCount))) {
          watchRemover();
          var whenExpFn = whensExpFns[count];
          if (isUndefined(whenExpFn)) {
            if (newVal != null) {
              $log.debug('ngPluralize: no rule defined for \'' + count + '\' in ' + whenExp);
            }
            watchRemover = noop;
            updateElementText();
          } else {
            watchRemover = scope.$watch(whenExpFn, updateElementText);
          }
          lastCount = count;
        }
      });

      function updateElementText(newText) {
        element.text(newText || '');
      }
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngRef
 * @restrict A
 *
 * @description
 * The `ngRef` attribute tells AngularJS to assign the controller of a component (or a directive)
 * to the given property in the current scope. It is also possible to add the jqlite-wrapped DOM
 * element to the scope.
 *
 * If the element with `ngRef` is destroyed `null` is assigned to the property.
 *
 * Note that if you want to assign from a child into the parent scope, you must initialize the
 * target property on the parent scope, otherwise `ngRef` will assign on the child scope.
 * This commonly happens when assigning elements or components wrapped in {@link ngIf} or
 * {@link ngRepeat}. See the second example below.
 *
 *
 * @element ANY
 * @param {string} ngRef property name - A valid AngularJS expression identifier to which the
 *                       controller or jqlite-wrapped DOM element will be bound.
 * @param {string=} ngRefRead read value - The name of a directive (or component) on this element,
 *                            or the special string `$element`. If a name is provided, `ngRef` will
 *                            assign the matching controller. If `$element` is provided, the element
 *                            itself is assigned (even if a controller is available).
 *
 *
 * @example
 * ### Simple toggle
 * This example shows how the controller of the component toggle
 * is reused in the template through the scope to use its logic.
 * <example name="ng-ref-component" module="myApp">
 *   <file name="index.html">
 *     <my-toggle ng-ref="myToggle"></my-toggle>
 *     <button ng-click="myToggle.toggle()">Toggle</button>
 *     <div ng-show="myToggle.isOpen()">
 *       You are using a component in the same template to show it.
 *     </div>
 *   </file>
 *   <file name="index.js">
 *     angular.module('myApp', [])
 *     .component('myToggle', {
 *       controller: function ToggleController() {
 *         var opened = false;
 *         this.isOpen = function() { return opened; };
 *         this.toggle = function() { opened = !opened; };
 *       }
 *     });
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *      it('should publish the toggle into the scope', function() {
 *        var toggle = element(by.buttonText('Toggle'));
 *        expect(toggle.evaluate('myToggle.isOpen()')).toEqual(false);
 *        toggle.click();
 *        expect(toggle.evaluate('myToggle.isOpen()')).toEqual(true);
 *      });
 *   </file>
 * </example>
 *
 * @example
 * ### ngRef inside scopes
 * This example shows how `ngRef` works with child scopes. The `ngRepeat`-ed `myWrapper` components
 * are assigned to the scope of `myRoot`, because the `toggles` property has been initialized.
 * The repeated `myToggle` components are published to the child scopes created by `ngRepeat`.
 * `ngIf` behaves similarly - the assignment of `myToggle` happens in the `ngIf` child scope,
 * because the target property has not been initialized on the `myRoot` component controller.
 *
 * <example name="ng-ref-scopes" module="myApp">
 *   <file name="index.html">
 *     <my-root></my-root>
 *   </file>
 *   <file name="index.js">
 *     angular.module('myApp', [])
 *     .component('myRoot', {
 *       templateUrl: 'root.html',
 *       controller: function() {
 *         this.wrappers = []; // initialize the array so that the wrappers are assigned into the parent scope
 *       }
 *     })
 *     .component('myToggle', {
 *       template: '<strong>myToggle</strong><button ng-click="$ctrl.toggle()" ng-transclude></button>',
 *       transclude: true,
 *       controller: function ToggleController() {
 *         var opened = false;
 *         this.isOpen = function() { return opened; };
 *         this.toggle = function() { opened = !opened; };
 *       }
 *     })
 *     .component('myWrapper', {
 *       transclude: true,
 *       template: '<strong>myWrapper</strong>' +
 *         '<div>ngRepeatToggle.isOpen(): {{$ctrl.ngRepeatToggle.isOpen() | json}}</div>' +
 *         '<my-toggle ng-ref="$ctrl.ngRepeatToggle"><ng-transclude></ng-transclude></my-toggle>'
 *     });
 *   </file>
 *   <file name="root.html">
 *     <strong>myRoot</strong>
 *     <my-toggle ng-ref="$ctrl.outerToggle">Outer Toggle</my-toggle>
 *     <div>outerToggle.isOpen(): {{$ctrl.outerToggle.isOpen() | json}}</div>
 *     <div><em>wrappers assigned to root</em><br>
 *     <div ng-repeat="wrapper in $ctrl.wrappers">
 *       wrapper.ngRepeatToggle.isOpen(): {{wrapper.ngRepeatToggle.isOpen() | json}}
 *     </div>
 *
 *     <ul>
 *       <li ng-repeat="(index, value) in [1,2,3]">
 *         <strong>ngRepeat</strong>
 *         <div>outerToggle.isOpen(): {{$ctrl.outerToggle.isOpen() | json}}</div>
 *         <my-wrapper ng-ref="$ctrl.wrappers[index]">ngRepeat Toggle {{$index + 1}}</my-wrapper>
 *       </li>
 *     </ul>
 *
 *     <div>ngIfToggle.isOpen(): {{ngIfToggle.isOpen()}} // This is always undefined because it's
 *       assigned to the child scope created by ngIf.
 *     </div>
 *     <div ng-if="true">
          <strong>ngIf</strong>
 *        <my-toggle ng-ref="ngIfToggle">ngIf Toggle</my-toggle>
 *        <div>ngIfToggle.isOpen(): {{ngIfToggle.isOpen() | json}}</div>
 *        <div>outerToggle.isOpen(): {{$ctrl.outerToggle.isOpen() | json}}</div>
 *     </div>
 *   </file>
 *   <file name="styles.css">
 *     ul {
 *       list-style: none;
 *       padding-left: 0;
 *     }
 *
 *     li[ng-repeat] {
 *       background: lightgreen;
 *       padding: 8px;
 *       margin: 8px;
 *     }
 *
 *     [ng-if] {
 *       background: lightgrey;
 *       padding: 8px;
 *     }
 *
 *     my-root {
 *       background: lightgoldenrodyellow;
 *       padding: 8px;
 *       display: block;
 *     }
 *
 *     my-wrapper {
 *       background: lightsalmon;
 *       padding: 8px;
 *       display: block;
 *     }
 *
 *     my-toggle {
 *       background: lightblue;
 *       padding: 8px;
 *       display: block;
 *     }
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *      var OuterToggle = function() {
 *        this.toggle = function() {
 *          element(by.buttonText('Outer Toggle')).click();
 *        };
 *        this.isOpen = function() {
 *          return element.all(by.binding('outerToggle.isOpen()')).first().getText();
 *        };
 *      };
 *      var NgRepeatToggle = function(i) {
 *        var parent = element.all(by.repeater('(index, value) in [1,2,3]')).get(i - 1);
 *        this.toggle = function() {
 *          element(by.buttonText('ngRepeat Toggle ' + i)).click();
 *        };
 *        this.isOpen = function() {
 *          return parent.element(by.binding('ngRepeatToggle.isOpen() | json')).getText();
 *        };
 *        this.isOuterOpen = function() {
 *          return parent.element(by.binding('outerToggle.isOpen() | json')).getText();
 *        };
 *      };
 *      var NgRepeatToggles = function() {
 *        var toggles = [1,2,3].map(function(i) { return new NgRepeatToggle(i); });
 *        this.forEach = function(fn) {
 *          toggles.forEach(fn);
 *        };
 *        this.isOuterOpen = function(i) {
 *          return toggles[i - 1].isOuterOpen();
 *        };
 *      };
 *      var NgIfToggle = function() {
 *        var parent = element(by.css('[ng-if]'));
 *        this.toggle = function() {
 *          element(by.buttonText('ngIf Toggle')).click();
 *        };
 *        this.isOpen = function() {
 *          return by.binding('ngIfToggle.isOpen() | json').getText();
 *        };
 *        this.isOuterOpen = function() {
 *          return parent.element(by.binding('outerToggle.isOpen() | json')).getText();
 *        };
 *      };
 *
 *      it('should toggle the outer toggle', function() {
 *        var outerToggle = new OuterToggle();
 *        expect(outerToggle.isOpen()).toEqual('outerToggle.isOpen(): false');
 *        outerToggle.toggle();
 *        expect(outerToggle.isOpen()).toEqual('outerToggle.isOpen(): true');
 *      });
 *
 *      it('should toggle all outer toggles', function() {
 *        var outerToggle = new OuterToggle();
 *        var repeatToggles = new NgRepeatToggles();
 *        var ifToggle = new NgIfToggle();
 *        expect(outerToggle.isOpen()).toEqual('outerToggle.isOpen(): false');
 *        expect(repeatToggles.isOuterOpen(1)).toEqual('outerToggle.isOpen(): false');
 *        expect(repeatToggles.isOuterOpen(2)).toEqual('outerToggle.isOpen(): false');
 *        expect(repeatToggles.isOuterOpen(3)).toEqual('outerToggle.isOpen(): false');
 *        expect(ifToggle.isOuterOpen()).toEqual('outerToggle.isOpen(): false');
 *        outerToggle.toggle();
 *        expect(outerToggle.isOpen()).toEqual('outerToggle.isOpen(): true');
 *        expect(repeatToggles.isOuterOpen(1)).toEqual('outerToggle.isOpen(): true');
 *        expect(repeatToggles.isOuterOpen(2)).toEqual('outerToggle.isOpen(): true');
 *        expect(repeatToggles.isOuterOpen(3)).toEqual('outerToggle.isOpen(): true');
 *        expect(ifToggle.isOuterOpen()).toEqual('outerToggle.isOpen(): true');
 *      });
 *
 *      it('should toggle each repeat iteration separately', function() {
 *        var repeatToggles = new NgRepeatToggles();
 *
 *        repeatToggles.forEach(function(repeatToggle) {
 *          expect(repeatToggle.isOpen()).toEqual('ngRepeatToggle.isOpen(): false');
 *          expect(repeatToggle.isOuterOpen()).toEqual('outerToggle.isOpen(): false');
 *          repeatToggle.toggle();
 *          expect(repeatToggle.isOpen()).toEqual('ngRepeatToggle.isOpen(): true');
 *          expect(repeatToggle.isOuterOpen()).toEqual('outerToggle.isOpen(): false');
 *        });
 *      });
 *   </file>
 * </example>
 *
 */

var ngRefMinErr = minErr('ngRef');

var ngRefDirective = ['$parse', function($parse) {
  return {
    priority: -1, // Needed for compatibility with element transclusion on the same element
    restrict: 'A',
    compile: function(tElement, tAttrs) {
      // Get the expected controller name, converts <data-some-thing> into "someThing"
      var controllerName = directiveNormalize(nodeName_(tElement));

      // Get the expression for value binding
      var getter = $parse(tAttrs.ngRef);
      var setter = getter.assign || function() {
        throw ngRefMinErr('nonassign', 'Expression in ngRef="{0}" is non-assignable!', tAttrs.ngRef);
      };

      return function(scope, element, attrs) {
        var refValue;

        if (attrs.hasOwnProperty('ngRefRead')) {
          if (attrs.ngRefRead === '$element') {
            refValue = element;
          } else {
            refValue = element.data('$' + attrs.ngRefRead + 'Controller');

            if (!refValue) {
              throw ngRefMinErr(
                'noctrl',
                'The controller for ngRefRead="{0}" could not be found on ngRef="{1}"',
                attrs.ngRefRead,
                tAttrs.ngRef
              );
            }
          }
        } else {
          refValue = element.data('$' + controllerName + 'Controller');
        }

        refValue = refValue || element;

        setter(scope, refValue);

        // when the element is removed, remove it (nullify it)
        element.on('$destroy', function() {
          // only remove it if value has not changed,
          // because animations (and other procedures) may duplicate elements
          if (getter(scope) === refValue) {
            setter(scope, null);
          }
        });
      };
    }
  };
}];

/* exported ngRepeatDirective */

/**
 * @ngdoc directive
 * @name ngRepeat
 * @multiElement
 * @restrict A
 *
 * @description
 * The `ngRepeat` directive instantiates a template once per item from a collection. Each template
 * instance gets its own scope, where the given loop variable is set to the current collection item,
 * and `$index` is set to the item index or key.
 *
 * Special properties are exposed on the local scope of each template instance, including:
 *
 * | Variable  | Type            | Details                                                                     |
 * |-----------|-----------------|-----------------------------------------------------------------------------|
 * | `$index`  | {@type number}  | iterator offset of the repeated element (0..length-1)                       |
 * | `$first`  | {@type boolean} | true if the repeated element is first in the iterator.                      |
 * | `$middle` | {@type boolean} | true if the repeated element is between the first and last in the iterator. |
 * | `$last`   | {@type boolean} | true if the repeated element is last in the iterator.                       |
 * | `$even`   | {@type boolean} | true if the iterator position `$index` is even (otherwise false).           |
 * | `$odd`    | {@type boolean} | true if the iterator position `$index` is odd (otherwise false).            |
 *
 * <div class="alert alert-info">
 *   Creating aliases for these properties is possible with {@link ng.directive:ngInit `ngInit`}.
 *   This may be useful when, for instance, nesting ngRepeats.
 * </div>
 *
 *
 * ## Iterating over object properties
 *
 * It is possible to get `ngRepeat` to iterate over the properties of an object using the following
 * syntax:
 *
 * ```js
 * <div ng-repeat="(key, value) in myObj"> ... </div>
 * ```
 *
 * However, there are a few limitations compared to array iteration:
 *
 * - The JavaScript specification does not define the order of keys
 *   returned for an object, so AngularJS relies on the order returned by the browser
 *   when running `for key in myObj`. Browsers generally follow the strategy of providing
 *   keys in the order in which they were defined, although there are exceptions when keys are deleted
 *   and reinstated. See the
 *   [MDN page on `delete` for more info](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/delete#Cross-browser_notes).
 *
 * - `ngRepeat` will silently *ignore* object keys starting with `$`, because
 *   it's a prefix used by AngularJS for public (`$`) and private (`$$`) properties.
 *
 * - The built-in filters {@link ng.orderBy orderBy} and {@link ng.filter filter} do not work with
 *   objects, and will throw an error if used with one.
 *
 * If you are hitting any of these limitations, the recommended workaround is to convert your object into an array
 * that is sorted into the order that you prefer before providing it to `ngRepeat`. You could
 * do this with a filter such as [toArrayFilter](http://ngmodules.org/modules/angular-toArrayFilter)
 * or implement a `$watch` on the object yourself.
 *
 *
 * ## Tracking and Duplicates
 *
 * `ngRepeat` uses {@link $rootScope.Scope#$watchCollection $watchCollection} to detect changes in
 * the collection. When a change happens, `ngRepeat` then makes the corresponding changes to the DOM:
 *
 * * When an item is added, a new instance of the template is added to the DOM.
 * * When an item is removed, its template instance is removed from the DOM.
 * * When items are reordered, their respective templates are reordered in the DOM.
 *
 * To minimize creation of DOM elements, `ngRepeat` uses a function
 * to "keep track" of all items in the collection and their corresponding DOM elements.
 * For example, if an item is added to the collection, `ngRepeat` will know that all other items
 * already have DOM elements, and will not re-render them.
 *
 * All different types of tracking functions, their syntax, and their support for duplicate
 * items in collections can be found in the
 * {@link ngRepeat#ngRepeat-arguments ngRepeat expression description}.
 *
 * <div class="alert alert-success">
 * **Best Practice:** If you are working with objects that have a unique identifier property, you
 * should track by this identifier instead of the object instance,
 * e.g. `item in items track by item.id`.
 * Should you reload your data later, `ngRepeat` will not have to rebuild the DOM elements for items
 * it has already rendered, even if the JavaScript objects in the collection have been substituted
 * for new ones. For large collections, this significantly improves rendering performance.
 * </div>
 *
 * ### Effects of DOM Element re-use
 *
 * When DOM elements are re-used, ngRepeat updates the scope for the element, which will
 * automatically update any active bindings on the template. However, other
 * functionality will not be updated, because the element is not re-created:
 *
 * - Directives are not re-compiled
 * - {@link guide/expression#one-time-binding one-time expressions} on the repeated template are not
 * updated if they have stabilized.
 *
 * The above affects all kinds of element re-use due to tracking, but may be especially visible
 * when tracking by `$index` due to the way ngRepeat re-uses elements.
 *
 * The following example shows the effects of different actions with tracking:

  <example module="ngRepeat" name="ngRepeat-tracking" deps="angular-animate.js" animations="true">
    <file name="script.js">
      angular.module('ngRepeat', ['ngAnimate']).controller('repeatController', function($scope) {
        var friends = [
          {name:'John', age:25},
          {name:'Mary', age:40},
          {name:'Peter', age:85}
        ];

        $scope.removeFirst = function() {
          $scope.friends.shift();
        };

        $scope.updateAge = function() {
          $scope.friends.forEach(function(el) {
            el.age = el.age + 5;
          });
        };

        $scope.copy = function() {
          $scope.friends = angular.copy($scope.friends);
        };

        $scope.reset = function() {
          $scope.friends = angular.copy(friends);
        };

        $scope.reset();
      });
    </file>
    <file name="index.html">
      <div ng-controller="repeatController">
        <ol>
          <li>When you click "Update Age", only the first list updates the age, because all others have
          a one-time binding on the age property. If you then click "Copy", the current friend list
          is copied, and now the second list updates the age, because the identity of the collection items
          has changed and the list must be re-rendered. The 3rd and 4th list stay the same, because all the
          items are already known according to their tracking functions.
          </li>
          <li>When you click "Remove First", the 4th list has the wrong age on both remaining items. This is
          due to tracking by $index: when the first collection item is removed, ngRepeat reuses the first
          DOM element for the new first collection item, and so on. Since the age property is one-time
          bound, the value remains from the collection item which was previously at this index.
          </li>
        </ol>

        <button ng-click="removeFirst()">Remove First</button>
        <button ng-click="updateAge()">Update Age</button>
        <button ng-click="copy()">Copy</button>
        <br><button ng-click="reset()">Reset List</button>
        <br>
        <code>track by $id(friend)</code> (default):
        <ul class="example-animate-container">
          <li class="animate-repeat" ng-repeat="friend in friends">
            {{friend.name}} is {{friend.age}} years old.
          </li>
        </ul>
        <code>track by $id(friend)</code> (default), with age one-time binding:
        <ul class="example-animate-container">
          <li class="animate-repeat" ng-repeat="friend in friends">
            {{friend.name}} is {{::friend.age}} years old.
          </li>
        </ul>
        <code>track by friend.name</code>, with age one-time binding:
        <ul class="example-animate-container">
          <li class="animate-repeat" ng-repeat="friend in friends track by friend.name">
            {{friend.name}}  is {{::friend.age}} years old.
          </li>
        </ul>
        <code>track by $index</code>, with age one-time binding:
        <ul class="example-animate-container">
          <li class="animate-repeat" ng-repeat="friend in friends track by $index">
            {{friend.name}} is {{::friend.age}} years old.
          </li>
        </ul>
      </div>
    </file>
    <file name="animations.css">
      .example-animate-container {
        background:white;
        border:1px solid black;
        list-style:none;
        margin:0;
        padding:0 10px;
      }

      .animate-repeat {
        line-height:30px;
        list-style:none;
        box-sizing:border-box;
      }

      .animate-repeat.ng-move,
      .animate-repeat.ng-enter,
      .animate-repeat.ng-leave {
        transition:all linear 0.5s;
      }

      .animate-repeat.ng-leave.ng-leave-active,
      .animate-repeat.ng-move,
      .animate-repeat.ng-enter {
        opacity:0;
        max-height:0;
      }

      .animate-repeat.ng-leave,
      .animate-repeat.ng-move.ng-move-active,
      .animate-repeat.ng-enter.ng-enter-active {
        opacity:1;
        max-height:30px;
      }
    </file>
  </example>

 *
 * ## Special repeat start and end points
 * To repeat a series of elements instead of just one parent element, ngRepeat (as well as other ng directives) supports extending
 * the range of the repeater by defining explicit start and end points by using **ng-repeat-start** and **ng-repeat-end** respectively.
 * The **ng-repeat-start** directive works the same as **ng-repeat**, but will repeat all the HTML code (including the tag it's defined on)
 * up to and including the ending HTML tag where **ng-repeat-end** is placed.
 *
 * The example below makes use of this feature:
 * ```html
 *   <header ng-repeat-start="item in items">
 *     Header {{ item }}
 *   </header>
 *   <div class="body">
 *     Body {{ item }}
 *   </div>
 *   <footer ng-repeat-end>
 *     Footer {{ item }}
 *   </footer>
 * ```
 *
 * And with an input of {@type ['A','B']} for the items variable in the example above, the output will evaluate to:
 * ```html
 *   <header>
 *     Header A
 *   </header>
 *   <div class="body">
 *     Body A
 *   </div>
 *   <footer>
 *     Footer A
 *   </footer>
 *   <header>
 *     Header B
 *   </header>
 *   <div class="body">
 *     Body B
 *   </div>
 *   <footer>
 *     Footer B
 *   </footer>
 * ```
 *
 * The custom start and end points for ngRepeat also support all other HTML directive syntax flavors provided in AngularJS (such
 * as **data-ng-repeat-start**, **x-ng-repeat-start** and **ng:repeat-start**).
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter} | when a new item is added to the list or when an item is revealed after a filter |
 * | {@link ng.$animate#leave leave} | when an item is removed from the list or when an item is filtered out |
 * | {@link ng.$animate#move move } | when an adjacent item is filtered out causing a reorder or when the item contents are reordered |
 *
 * See the example below for defining CSS animations with ngRepeat.
 *
 * @element ANY
 * @scope
 * @priority 1000
 * @param {repeat_expression} ngRepeat The expression indicating how to enumerate a collection. These
 *   formats are currently supported:
 *
 *   * `variable in expression` – where variable is the user defined loop variable and `expression`
 *     is a scope expression giving the collection to enumerate.
 *
 *     For example: `album in artist.albums`.
 *
 *   * `(key, value) in expression` – where `key` and `value` can be any user defined identifiers,
 *     and `expression` is the scope expression giving the collection to enumerate.
 *
 *     For example: `(name, age) in {'adam':10, 'amalie':12}`.
 *
 *   * `variable in expression track by tracking_expression` – You can also provide an optional tracking expression
 *     which can be used to associate the objects in the collection with the DOM elements. If no tracking expression
 *     is specified, ng-repeat associates elements by identity. It is an error to have
 *     more than one tracking expression value resolve to the same key. (This would mean that two distinct objects are
 *     mapped to the same DOM element, which is not possible.)
 *
 *     *Default tracking: $id()*: `item in items` is equivalent to `item in items track by $id(item)`.
 *     This implies that the DOM elements will be associated by item identity in the collection.
 *
 *     The built-in `$id()` function can be used to assign a unique
 *     `$$hashKey` property to each item in the collection. This property is then used as a key to associated DOM elements
 *     with the corresponding item in the collection by identity. Moving the same object would move
 *     the DOM element in the same way in the DOM.
 *     Note that the default id function does not support duplicate primitive values (`number`, `string`),
 *     but supports duplictae non-primitive values (`object`) that are *equal* in shape.
 *
 *     *Custom Expression*: It is possible to use any AngularJS expression to compute the tracking
 *     id, for example with a function, or using a property on the collection items.
 *     `item in items track by item.id` is a typical pattern when the items have a unique identifier,
 *     e.g. database id. In this case the object identity does not matter. Two objects are considered
 *     equivalent as long as their `id` property is same.
 *     Tracking by unique identifier is the most performant way and should be used whenever possible.
 *
 *     *$index*: This special property tracks the collection items by their index, and
 *     re-uses the DOM elements that match that index, e.g. `item in items track by $index`. This can
 *     be used for a performance improvement if no unique identfier is available and the identity of
 *     the collection items cannot be easily computed. It also allows duplicates.
 *
 *     <div class="alert alert-warning">
 *       <strong>Note:</strong> Re-using DOM elements can have unforeseen effects. Read the
 *       {@link ngRepeat#tracking-and-duplicates section on tracking and duplicates} for
 *       more info.
 *     </div>
 *
 *     <div class="alert alert-warning">
 *       <strong>Note:</strong> the `track by` expression must come last - after any filters, and the alias expression:
 *       `item in items | filter:searchText as results  track by item.id`
 *     </div>
 *
 *   * `variable in expression as alias_expression` – You can also provide an optional alias expression which will then store the
 *     intermediate results of the repeater after the filters have been applied. Typically this is used to render a special message
 *     when a filter is active on the repeater, but the filtered result set is empty.
 *
 *     For example: `item in items | filter:x as results` will store the fragment of the repeated items as `results`, but only after
 *     the items have been processed through the filter.
 *
 *     Please note that `as [variable name]` is not an operator but rather a part of ngRepeat
 *     micro-syntax so it can be used only after all filters (and not as operator, inside an expression).
 *
 *     For example: `item in items | filter : x | orderBy : order | limitTo : limit as results track by item.id` .
 *
 * @example
 * This example uses `ngRepeat` to display a list of people. A filter is used to restrict the displayed
 * results by name or by age. New (entering) and removed (leaving) items are animated.
  <example module="ngRepeat" name="ngRepeat" deps="angular-animate.js" animations="true">
    <file name="index.html">
      <div ng-controller="repeatController">
        I have {{friends.length}} friends. They are:
        <input type="search" ng-model="q" placeholder="filter friends..." aria-label="filter friends" />
        <ul class="example-animate-container">
          <li class="animate-repeat" ng-repeat="friend in friends | filter:q as results track by friend.name">
            [{{$index + 1}}] {{friend.name}} who is {{friend.age}} years old.
          </li>
          <li class="animate-repeat" ng-if="results.length === 0">
            <strong>No results found...</strong>
          </li>
        </ul>
      </div>
    </file>
    <file name="script.js">
      angular.module('ngRepeat', ['ngAnimate']).controller('repeatController', function($scope) {
        $scope.friends = [
          {name:'John', age:25, gender:'boy'},
          {name:'Jessie', age:30, gender:'girl'},
          {name:'Johanna', age:28, gender:'girl'},
          {name:'Joy', age:15, gender:'girl'},
          {name:'Mary', age:28, gender:'girl'},
          {name:'Peter', age:95, gender:'boy'},
          {name:'Sebastian', age:50, gender:'boy'},
          {name:'Erika', age:27, gender:'girl'},
          {name:'Patrick', age:40, gender:'boy'},
          {name:'Samantha', age:60, gender:'girl'}
        ];
      });
    </file>
    <file name="animations.css">
      .example-animate-container {
        background:white;
        border:1px solid black;
        list-style:none;
        margin:0;
        padding:0 10px;
      }

      .animate-repeat {
        line-height:30px;
        list-style:none;
        box-sizing:border-box;
      }

      .animate-repeat.ng-move,
      .animate-repeat.ng-enter,
      .animate-repeat.ng-leave {
        transition:all linear 0.5s;
      }

      .animate-repeat.ng-leave.ng-leave-active,
      .animate-repeat.ng-move,
      .animate-repeat.ng-enter {
        opacity:0;
        max-height:0;
      }

      .animate-repeat.ng-leave,
      .animate-repeat.ng-move.ng-move-active,
      .animate-repeat.ng-enter.ng-enter-active {
        opacity:1;
        max-height:30px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      var friends = element.all(by.repeater('friend in friends'));

      it('should render initial data set', function() {
        expect(friends.count()).toBe(10);
        expect(friends.get(0).getText()).toEqual('[1] John who is 25 years old.');
        expect(friends.get(1).getText()).toEqual('[2] Jessie who is 30 years old.');
        expect(friends.last().getText()).toEqual('[10] Samantha who is 60 years old.');
        expect(element(by.binding('friends.length')).getText())
            .toMatch("I have 10 friends. They are:");
      });

       it('should update repeater when filter predicate changes', function() {
         expect(friends.count()).toBe(10);

         element(by.model('q')).sendKeys('ma');

         expect(friends.count()).toBe(2);
         expect(friends.get(0).getText()).toEqual('[1] Mary who is 28 years old.');
         expect(friends.last().getText()).toEqual('[2] Samantha who is 60 years old.');
       });
      </file>
    </example>
 */
var ngRepeatDirective = ['$parse', '$animate', '$compile', function($parse, $animate, $compile) {
  var NG_REMOVED = '$$NG_REMOVED';
  var ngRepeatMinErr = minErr('ngRepeat');

  var updateScope = function(scope, index, valueIdentifier, value, keyIdentifier, key, arrayLength) {
    // TODO(perf): generate setters to shave off ~40ms or 1-1.5%
    scope[valueIdentifier] = value;
    if (keyIdentifier) scope[keyIdentifier] = key;
    scope.$index = index;
    scope.$first = (index === 0);
    scope.$last = (index === (arrayLength - 1));
    scope.$middle = !(scope.$first || scope.$last);
    // eslint-disable-next-line no-bitwise
    scope.$odd = !(scope.$even = (index & 1) === 0);
  };

  var getBlockStart = function(block) {
    return block.clone[0];
  };

  var getBlockEnd = function(block) {
    return block.clone[block.clone.length - 1];
  };

  var trackByIdArrayFn = function($scope, key, value) {
    return hashKey(value);
  };

  var trackByIdObjFn = function($scope, key) {
    return key;
  };

  return {
    restrict: 'A',
    multiElement: true,
    transclude: 'element',
    priority: 1000,
    terminal: true,
    $$tlb: true,
    compile: function ngRepeatCompile($element, $attr) {
      var expression = $attr.ngRepeat;
      var ngRepeatEndComment = $compile.$$createComment('end ngRepeat', expression);

      var match = expression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+track\s+by\s+([\s\S]+?))?\s*$/);

      if (!match) {
        throw ngRepeatMinErr('iexp', 'Expected expression in form of \'_item_ in _collection_[ track by _id_]\' but got \'{0}\'.',
            expression);
      }

      var lhs = match[1];
      var rhs = match[2];
      var aliasAs = match[3];
      var trackByExp = match[4];

      match = lhs.match(/^(?:(\s*[$\w]+)|\(\s*([$\w]+)\s*,\s*([$\w]+)\s*\))$/);

      if (!match) {
        throw ngRepeatMinErr('iidexp', '\'_item_\' in \'_item_ in _collection_\' should be an identifier or \'(_key_, _value_)\' expression, but got \'{0}\'.',
            lhs);
      }
      var valueIdentifier = match[3] || match[1];
      var keyIdentifier = match[2];

      if (aliasAs && (!/^[$a-zA-Z_][$a-zA-Z0-9_]*$/.test(aliasAs) ||
          /^(null|undefined|this|\$index|\$first|\$middle|\$last|\$even|\$odd|\$parent|\$root|\$id)$/.test(aliasAs))) {
        throw ngRepeatMinErr('badident', 'alias \'{0}\' is invalid --- must be a valid JS identifier which is not a reserved name.',
          aliasAs);
      }

      var trackByIdExpFn;

      if (trackByExp) {
        var hashFnLocals = {$id: hashKey};
        var trackByExpGetter = $parse(trackByExp);

        trackByIdExpFn = function($scope, key, value, index) {
          // assign key, value, and $index to the locals so that they can be used in hash functions
          if (keyIdentifier) hashFnLocals[keyIdentifier] = key;
          hashFnLocals[valueIdentifier] = value;
          hashFnLocals.$index = index;
          return trackByExpGetter($scope, hashFnLocals);
        };
      }

      return function ngRepeatLink($scope, $element, $attr, ctrl, $transclude) {

        // Store a list of elements from previous run. This is a hash where key is the item from the
        // iterator, and the value is objects with following properties.
        //   - scope: bound scope
        //   - clone: previous element.
        //   - index: position
        //
        // We are using no-proto object so that we don't need to guard against inherited props via
        // hasOwnProperty.
        var lastBlockMap = createMap();

        //watch props
        $scope.$watchCollection(rhs, function ngRepeatAction(collection) {
          var index, length,
              previousNode = $element[0],     // node that cloned nodes should be inserted after
                                              // initialized to the comment node anchor
              nextNode,
              // Same as lastBlockMap but it has the current state. It will become the
              // lastBlockMap on the next iteration.
              nextBlockMap = createMap(),
              collectionLength,
              key, value, // key/value of iteration
              trackById,
              trackByIdFn,
              collectionKeys,
              block,       // last object information {scope, element, id}
              nextBlockOrder,
              elementsToRemove;

          if (aliasAs) {
            $scope[aliasAs] = collection;
          }

          if (isArrayLike(collection)) {
            collectionKeys = collection;
            trackByIdFn = trackByIdExpFn || trackByIdArrayFn;
          } else {
            trackByIdFn = trackByIdExpFn || trackByIdObjFn;
            // if object, extract keys, in enumeration order, unsorted
            collectionKeys = [];
            for (var itemKey in collection) {
              if (hasOwnProperty.call(collection, itemKey) && itemKey.charAt(0) !== '$') {
                collectionKeys.push(itemKey);
              }
            }
          }

          collectionLength = collectionKeys.length;
          nextBlockOrder = new Array(collectionLength);

          // locate existing items
          for (index = 0; index < collectionLength; index++) {
            key = (collection === collectionKeys) ? index : collectionKeys[index];
            value = collection[key];
            trackById = trackByIdFn($scope, key, value, index);
            if (lastBlockMap[trackById]) {
              // found previously seen block
              block = lastBlockMap[trackById];
              delete lastBlockMap[trackById];
              nextBlockMap[trackById] = block;
              nextBlockOrder[index] = block;
            } else if (nextBlockMap[trackById]) {
              // if collision detected. restore lastBlockMap and throw an error
              forEach(nextBlockOrder, function(block) {
                if (block && block.scope) lastBlockMap[block.id] = block;
              });
              throw ngRepeatMinErr('dupes',
                  'Duplicates in a repeater are not allowed. Use \'track by\' expression to specify unique keys. Repeater: {0}, Duplicate key: {1}, Duplicate value: {2}',
                  expression, trackById, value);
            } else {
              // new never before seen block
              nextBlockOrder[index] = {id: trackById, scope: undefined, clone: undefined};
              nextBlockMap[trackById] = true;
            }
          }

          // Clear the value property from the hashFnLocals object to prevent a reference to the last value
          // being leaked into the ngRepeatCompile function scope
          if (hashFnLocals) {
            hashFnLocals[valueIdentifier] = undefined;
          }

          // remove leftover items
          for (var blockKey in lastBlockMap) {
            block = lastBlockMap[blockKey];
            elementsToRemove = getBlockNodes(block.clone);
            $animate.leave(elementsToRemove);
            if (elementsToRemove[0].parentNode) {
              // if the element was not removed yet because of pending animation, mark it as deleted
              // so that we can ignore it later
              for (index = 0, length = elementsToRemove.length; index < length; index++) {
                elementsToRemove[index][NG_REMOVED] = true;
              }
            }
            block.scope.$destroy();
          }

          // we are not using forEach for perf reasons (trying to avoid #call)
          for (index = 0; index < collectionLength; index++) {
            key = (collection === collectionKeys) ? index : collectionKeys[index];
            value = collection[key];
            block = nextBlockOrder[index];

            if (block.scope) {
              // if we have already seen this object, then we need to reuse the
              // associated scope/element

              nextNode = previousNode;

              // skip nodes that are already pending removal via leave animation
              do {
                nextNode = nextNode.nextSibling;
              } while (nextNode && nextNode[NG_REMOVED]);

              if (getBlockStart(block) !== nextNode) {
                // existing item which got moved
                $animate.move(getBlockNodes(block.clone), null, previousNode);
              }
              previousNode = getBlockEnd(block);
              updateScope(block.scope, index, valueIdentifier, value, keyIdentifier, key, collectionLength);
            } else {
              // new item which we don't know about
              $transclude(function ngRepeatTransclude(clone, scope) {
                block.scope = scope;
                // http://jsperf.com/clone-vs-createcomment
                var endNode = ngRepeatEndComment.cloneNode(false);
                clone[clone.length++] = endNode;

                $animate.enter(clone, null, previousNode);
                previousNode = endNode;
                // Note: We only need the first/last node of the cloned nodes.
                // However, we need to keep the reference to the jqlite wrapper as it might be changed later
                // by a directive with templateUrl when its template arrives.
                block.clone = clone;
                nextBlockMap[block.id] = block;
                updateScope(block.scope, index, valueIdentifier, value, keyIdentifier, key, collectionLength);
              });
            }
          }
          lastBlockMap = nextBlockMap;
        });
      };
    }
  };
}];

var NG_HIDE_CLASS = 'ng-hide';
var NG_HIDE_IN_PROGRESS_CLASS = 'ng-hide-animate';
/**
 * @ngdoc directive
 * @name ngShow
 * @multiElement
 *
 * @description
 * The `ngShow` directive shows or hides the given HTML element based on the expression provided to
 * the `ngShow` attribute.
 *
 * The element is shown or hidden by removing or adding the `.ng-hide` CSS class onto the element.
 * The `.ng-hide` CSS class is predefined in AngularJS and sets the display style to none (using an
 * `!important` flag). For CSP mode please add `angular-csp.css` to your HTML file (see
 * {@link ng.directive:ngCsp ngCsp}).
 *
 * ```html
 * <!-- when $scope.myValue is truthy (element is visible) -->
 * <div ng-show="myValue"></div>
 *
 * <!-- when $scope.myValue is falsy (element is hidden) -->
 * <div ng-show="myValue" class="ng-hide"></div>
 * ```
 *
 * When the `ngShow` expression evaluates to a falsy value then the `.ng-hide` CSS class is added
 * to the class attribute on the element causing it to become hidden. When truthy, the `.ng-hide`
 * CSS class is removed from the element causing the element not to appear hidden.
 *
 * ## Why is `!important` used?
 *
 * You may be wondering why `!important` is used for the `.ng-hide` CSS class. This is because the
 * `.ng-hide` selector can be easily overridden by heavier selectors. For example, something as
 * simple as changing the display style on a HTML list item would make hidden elements appear
 * visible. This also becomes a bigger issue when dealing with CSS frameworks.
 *
 * By using `!important`, the show and hide behavior will work as expected despite any clash between
 * CSS selector specificity (when `!important` isn't used with any conflicting styles). If a
 * developer chooses to override the styling to change how to hide an element then it is just a
 * matter of using `!important` in their own CSS code.
 *
 * ### Overriding `.ng-hide`
 *
 * By default, the `.ng-hide` class will style the element with `display: none !important`. If you
 * wish to change the hide behavior with `ngShow`/`ngHide`, you can simply overwrite the styles for
 * the `.ng-hide` CSS class. Note that the selector that needs to be used is actually
 * `.ng-hide:not(.ng-hide-animate)` to cope with extra animation classes that can be added.
 *
 * ```css
 * .ng-hide:not(.ng-hide-animate) {
 *   /&#42; These are just alternative ways of hiding an element &#42;/
 *   display: block!important;
 *   position: absolute;
 *   top: -9999px;
 *   left: -9999px;
 * }
 * ```
 *
 * By default you don't need to override anything in CSS and the animations will work around the
 * display style.
 *
 * @animations
 * | Animation                                           | Occurs                                                                                                        |
 * |-----------------------------------------------------|---------------------------------------------------------------------------------------------------------------|
 * | {@link $animate#addClass addClass} `.ng-hide`       | After the `ngShow` expression evaluates to a non truthy value and just before the contents are set to hidden. |
 * | {@link $animate#removeClass removeClass} `.ng-hide` | After the `ngShow` expression evaluates to a truthy value and just before contents are set to visible.        |
 *
 * Animations in `ngShow`/`ngHide` work with the show and hide events that are triggered when the
 * directive expression is true and false. This system works like the animation system present with
 * `ngClass` except that you must also include the `!important` flag to override the display
 * property so that the elements are not actually hidden during the animation.
 *
 * ```css
 * /&#42; A working example can be found at the bottom of this page. &#42;/
 * .my-element.ng-hide-add, .my-element.ng-hide-remove {
 *   transition: all 0.5s linear;
 * }
 *
 * .my-element.ng-hide-add { ... }
 * .my-element.ng-hide-add.ng-hide-add-active { ... }
 * .my-element.ng-hide-remove { ... }
 * .my-element.ng-hide-remove.ng-hide-remove-active { ... }
 * ```
 *
 * Keep in mind that, as of AngularJS version 1.3, there is no need to change the display property
 * to block during animation states - ngAnimate will automatically handle the style toggling for you.
 *
 * @element ANY
 * @param {expression} ngShow If the {@link guide/expression expression} is truthy/falsy then the
 *                            element is shown/hidden respectively.
 *
 * @example
 * A simple example, animating the element's opacity:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-show-simple">
    <file name="index.html">
      Show: <input type="checkbox" ng-model="checked" aria-label="Toggle ngShow"><br />
      <div class="check-element animate-show-hide" ng-show="checked">
        I show up when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      .animate-show-hide.ng-hide {
        opacity: 0;
      }

      .animate-show-hide.ng-hide-add,
      .animate-show-hide.ng-hide-remove {
        transition: all linear 0.5s;
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngShow', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(false);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(true);
      });
    </file>
  </example>
 *
 * <hr />
 * @example
 * A more complex example, featuring different show/hide animations:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-show-complex">
    <file name="index.html">
      Show: <input type="checkbox" ng-model="checked" aria-label="Toggle ngShow"><br />
      <div class="check-element funky-show-hide" ng-show="checked">
        I show up when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      body {
        overflow: hidden;
        perspective: 1000px;
      }

      .funky-show-hide.ng-hide-add {
        transform: rotateZ(0);
        transform-origin: right;
        transition: all 0.5s ease-in-out;
      }

      .funky-show-hide.ng-hide-add.ng-hide-add-active {
        transform: rotateZ(-135deg);
      }

      .funky-show-hide.ng-hide-remove {
        transform: rotateY(90deg);
        transform-origin: left;
        transition: all 0.5s ease;
      }

      .funky-show-hide.ng-hide-remove.ng-hide-remove-active {
        transform: rotateY(0);
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngShow', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(false);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(true);
      });
    </file>
  </example>
 *
 * @knownIssue
 *
 * ### Flickering when using ngShow to toggle between elements
 *
 * When using {@link ngShow} and / or {@link ngHide} to toggle between elements, it can
 * happen that both the element to show and the element to hide are visible for a very short time.
 *
 * This usually happens when the {@link ngAnimate ngAnimate module} is included, but no actual animations
 * are defined for {@link ngShow} / {@link ngHide}. Internet Explorer is affected more often than
 * other browsers.
 *
 * There are several way to mitigate this problem:
 *
 * - {@link guide/animations#how-to-selectively-enable-disable-and-skip-animations Disable animations on the affected elements}.
 * - Use {@link ngIf} or {@link ngSwitch} instead of {@link ngShow} / {@link ngHide}.
 * - Use the special CSS selector `ng-hide.ng-hide-animate` to set `{display: none}` or similar on the affected elements.
 * - Use `ng-class="{'ng-hide': expression}` instead of instead of {@link ngShow} / {@link ngHide}.
 * - Define an animation on the affected elements.
 */
var ngShowDirective = ['$animate', function($animate) {
  return {
    restrict: 'A',
    multiElement: true,
    link: function(scope, element, attr) {
      scope.$watch(attr.ngShow, function ngShowWatchAction(value) {
        // we're adding a temporary, animation-specific class for ng-hide since this way
        // we can control when the element is actually displayed on screen without having
        // to have a global/greedy CSS selector that breaks when other animations are run.
        // Read: https://github.com/angular/angular.js/issues/9103#issuecomment-58335845
        $animate[value ? 'removeClass' : 'addClass'](element, NG_HIDE_CLASS, {
          tempClasses: NG_HIDE_IN_PROGRESS_CLASS
        });
      });
    }
  };
}];


/**
 * @ngdoc directive
 * @name ngHide
 * @multiElement
 *
 * @description
 * The `ngHide` directive shows or hides the given HTML element based on the expression provided to
 * the `ngHide` attribute.
 *
 * The element is shown or hidden by removing or adding the `.ng-hide` CSS class onto the element.
 * The `.ng-hide` CSS class is predefined in AngularJS and sets the display style to none (using an
 * `!important` flag). For CSP mode please add `angular-csp.css` to your HTML file (see
 * {@link ng.directive:ngCsp ngCsp}).
 *
 * ```html
 * <!-- when $scope.myValue is truthy (element is hidden) -->
 * <div ng-hide="myValue" class="ng-hide"></div>
 *
 * <!-- when $scope.myValue is falsy (element is visible) -->
 * <div ng-hide="myValue"></div>
 * ```
 *
 * When the `ngHide` expression evaluates to a truthy value then the `.ng-hide` CSS class is added
 * to the class attribute on the element causing it to become hidden. When falsy, the `.ng-hide`
 * CSS class is removed from the element causing the element not to appear hidden.
 *
 * ## Why is `!important` used?
 *
 * You may be wondering why `!important` is used for the `.ng-hide` CSS class. This is because the
 * `.ng-hide` selector can be easily overridden by heavier selectors. For example, something as
 * simple as changing the display style on a HTML list item would make hidden elements appear
 * visible. This also becomes a bigger issue when dealing with CSS frameworks.
 *
 * By using `!important`, the show and hide behavior will work as expected despite any clash between
 * CSS selector specificity (when `!important` isn't used with any conflicting styles). If a
 * developer chooses to override the styling to change how to hide an element then it is just a
 * matter of using `!important` in their own CSS code.
 *
 * ### Overriding `.ng-hide`
 *
 * By default, the `.ng-hide` class will style the element with `display: none !important`. If you
 * wish to change the hide behavior with `ngShow`/`ngHide`, you can simply overwrite the styles for
 * the `.ng-hide` CSS class. Note that the selector that needs to be used is actually
 * `.ng-hide:not(.ng-hide-animate)` to cope with extra animation classes that can be added.
 *
 * ```css
 * .ng-hide:not(.ng-hide-animate) {
 *   /&#42; These are just alternative ways of hiding an element &#42;/
 *   display: block!important;
 *   position: absolute;
 *   top: -9999px;
 *   left: -9999px;
 * }
 * ```
 *
 * By default you don't need to override in CSS anything and the animations will work around the
 * display style.
 *
 * @animations
 * | Animation                                           | Occurs                                                                                                     |
 * |-----------------------------------------------------|------------------------------------------------------------------------------------------------------------|
 * | {@link $animate#addClass addClass} `.ng-hide`       | After the `ngHide` expression evaluates to a truthy value and just before the contents are set to hidden.  |
 * | {@link $animate#removeClass removeClass} `.ng-hide` | After the `ngHide` expression evaluates to a non truthy value and just before contents are set to visible. |
 *
 * Animations in `ngShow`/`ngHide` work with the show and hide events that are triggered when the
 * directive expression is true and false. This system works like the animation system present with
 * `ngClass` except that you must also include the `!important` flag to override the display
 * property so that the elements are not actually hidden during the animation.
 *
 * ```css
 * /&#42; A working example can be found at the bottom of this page. &#42;/
 * .my-element.ng-hide-add, .my-element.ng-hide-remove {
 *   transition: all 0.5s linear;
 * }
 *
 * .my-element.ng-hide-add { ... }
 * .my-element.ng-hide-add.ng-hide-add-active { ... }
 * .my-element.ng-hide-remove { ... }
 * .my-element.ng-hide-remove.ng-hide-remove-active { ... }
 * ```
 *
 * Keep in mind that, as of AngularJS version 1.3, there is no need to change the display property
 * to block during animation states - ngAnimate will automatically handle the style toggling for you.
 *
 * @element ANY
 * @param {expression} ngHide If the {@link guide/expression expression} is truthy/falsy then the
 *                            element is hidden/shown respectively.
 *
 * @example
 * A simple example, animating the element's opacity:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-hide-simple">
    <file name="index.html">
      Hide: <input type="checkbox" ng-model="checked" aria-label="Toggle ngHide"><br />
      <div class="check-element animate-show-hide" ng-hide="checked">
        I hide when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      .animate-show-hide.ng-hide {
        opacity: 0;
      }

      .animate-show-hide.ng-hide-add,
      .animate-show-hide.ng-hide-remove {
        transition: all linear 0.5s;
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngHide', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(true);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(false);
      });
    </file>
  </example>
 *
 * <hr />
 * @example
 * A more complex example, featuring different show/hide animations:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-hide-complex">
    <file name="index.html">
      Hide: <input type="checkbox" ng-model="checked" aria-label="Toggle ngHide"><br />
      <div class="check-element funky-show-hide" ng-hide="checked">
        I hide when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      body {
        overflow: hidden;
        perspective: 1000px;
      }

      .funky-show-hide.ng-hide-add {
        transform: rotateZ(0);
        transform-origin: right;
        transition: all 0.5s ease-in-out;
      }

      .funky-show-hide.ng-hide-add.ng-hide-add-active {
        transform: rotateZ(-135deg);
      }

      .funky-show-hide.ng-hide-remove {
        transform: rotateY(90deg);
        transform-origin: left;
        transition: all 0.5s ease;
      }

      .funky-show-hide.ng-hide-remove.ng-hide-remove-active {
        transform: rotateY(0);
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngHide', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(true);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(false);
      });
    </file>
  </example>
 *
 * @knownIssue
 *
 * ### Flickering when using ngHide to toggle between elements
 *
 * When using {@link ngShow} and / or {@link ngHide} to toggle between elements, it can
 * happen that both the element to show and the element to hide are visible for a very short time.
 *
 * This usually happens when the {@link ngAnimate ngAnimate module} is included, but no actual animations
 * are defined for {@link ngShow} / {@link ngHide}. Internet Explorer is affected more often than
 * other browsers.
 *
 * There are several way to mitigate this problem:
 *
 * - {@link guide/animations#how-to-selectively-enable-disable-and-skip-animations Disable animations on the affected elements}.
 * - Use {@link ngIf} or {@link ngSwitch} instead of {@link ngShow} / {@link ngHide}.
 * - Use the special CSS selector `ng-hide.ng-hide-animate` to set `{display: none}` or similar on the affected elements.
 * - Use `ng-class="{'ng-hide': expression}` instead of instead of {@link ngShow} / {@link ngHide}.
 * - Define an animation on the affected elements.
 */
var ngHideDirective = ['$animate', function($animate) {
  return {
    restrict: 'A',
    multiElement: true,
    link: function(scope, element, attr) {
      scope.$watch(attr.ngHide, function ngHideWatchAction(value) {
        // The comment inside of the ngShowDirective explains why we add and
        // remove a temporary class for the show/hide animation
        $animate[value ? 'addClass' : 'removeClass'](element,NG_HIDE_CLASS, {
          tempClasses: NG_HIDE_IN_PROGRESS_CLASS
        });
      });
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngStyle
 * @restrict AC
 *
 * @description
 * The `ngStyle` directive allows you to set CSS style on an HTML element conditionally.
 *
 * @knownIssue
 * You should not use {@link guide/interpolation interpolation} in the value of the `style`
 * attribute, when using the `ngStyle` directive on the same element.
 * See {@link guide/interpolation#known-issues here} for more info.
 *
 * @element ANY
 * @param {expression} ngStyle
 *
 * {@link guide/expression Expression} which evals to an
 * object whose keys are CSS style names and values are corresponding values for those CSS
 * keys.
 *
 * Since some CSS style names are not valid keys for an object, they must be quoted.
 * See the 'background-color' style in the example below.
 *
 * @example
   <example name="ng-style">
     <file name="index.html">
        <input type="button" value="set color" ng-click="myStyle={color:'red'}">
        <input type="button" value="set background" ng-click="myStyle={'background-color':'blue'}">
        <input type="button" value="clear" ng-click="myStyle={}">
        <br/>
        <span ng-style="myStyle">Sample Text</span>
        <pre>myStyle={{myStyle}}</pre>
     </file>
     <file name="style.css">
       span {
         color: black;
       }
     </file>
     <file name="protractor.js" type="protractor">
       var colorSpan = element(by.css('span'));

       it('should check ng-style', function() {
         expect(colorSpan.getCssValue('color')).toMatch(/rgba\(0, 0, 0, 1\)|rgb\(0, 0, 0\)/);
         element(by.css('input[value=\'set color\']')).click();
         expect(colorSpan.getCssValue('color')).toMatch(/rgba\(255, 0, 0, 1\)|rgb\(255, 0, 0\)/);
         element(by.css('input[value=clear]')).click();
         expect(colorSpan.getCssValue('color')).toMatch(/rgba\(0, 0, 0, 1\)|rgb\(0, 0, 0\)/);
       });
     </file>
   </example>
 */
var ngStyleDirective = ngDirective(function(scope, element, attr) {
  scope.$watchCollection(attr.ngStyle, function ngStyleWatchAction(newStyles, oldStyles) {
    if (oldStyles && (newStyles !== oldStyles)) {
      forEach(oldStyles, function(val, style) { element.css(style, ''); });
    }
    if (newStyles) element.css(newStyles);
  });
});

/**
 * @ngdoc directive
 * @name ngSwitch
 * @restrict EA
 *
 * @description
 * The `ngSwitch` directive is used to conditionally swap DOM structure on your template based on a scope expression.
 * Elements within `ngSwitch` but without `ngSwitchWhen` or `ngSwitchDefault` directives will be preserved at the location
 * as specified in the template.
 *
 * The directive itself works similar to ngInclude, however, instead of downloading template code (or loading it
 * from the template cache), `ngSwitch` simply chooses one of the nested elements and makes it visible based on which element
 * matches the value obtained from the evaluated expression. In other words, you define a container element
 * (where you place the directive), place an expression on the **`on="..."` attribute**
 * (or the **`ng-switch="..."` attribute**), define any inner elements inside of the directive and place
 * a when attribute per element. The when attribute is used to inform ngSwitch which element to display when the on
 * expression is evaluated. If a matching expression is not found via a when attribute then an element with the default
 * attribute is displayed.
 *
 * <div class="alert alert-info">
 * Be aware that the attribute values to match against cannot be expressions. They are interpreted
 * as literal string values to match against.
 * For example, **`ng-switch-when="someVal"`** will match against the string `"someVal"` not against the
 * value of the expression `$scope.someVal`.
 * </div>

 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | after the ngSwitch contents change and the matched child element is placed inside the container |
 * | {@link ng.$animate#leave leave}  | after the ngSwitch contents change and just before the former contents are removed from the DOM |
 *
 * @usage
 *
 * ```
 * <ANY ng-switch="expression">
 *   <ANY ng-switch-when="matchValue1">...</ANY>
 *   <ANY ng-switch-when="matchValue2">...</ANY>
 *   <ANY ng-switch-default>...</ANY>
 * </ANY>
 * ```
 *
 *
 * @scope
 * @priority 1200
 * @param {*} ngSwitch|on expression to match against <code>ng-switch-when</code>.
 * On child elements add:
 *
 * * `ngSwitchWhen`: the case statement to match against. If match then this
 *   case will be displayed. If the same match appears multiple times, all the
 *   elements will be displayed. It is possible to associate multiple values to
 *   the same `ngSwitchWhen` by defining the optional attribute
 *   `ngSwitchWhenSeparator`. The separator will be used to split the value of
 *   the `ngSwitchWhen` attribute into multiple tokens, and the element will show
 *   if any of the `ngSwitch` evaluates to any of these tokens.
 * * `ngSwitchDefault`: the default case when no other case match. If there
 *   are multiple default cases, all of them will be displayed when no other
 *   case match.
 *
 *
 * @example
  <example module="switchExample" deps="angular-animate.js" animations="true" name="ng-switch">
    <file name="index.html">
      <div ng-controller="ExampleController">
        <select ng-model="selection" ng-options="item for item in items">
        </select>
        <code>selection={{selection}}</code>
        <hr/>
        <div class="animate-switch-container"
          ng-switch on="selection">
            <div class="animate-switch" ng-switch-when="settings|options" ng-switch-when-separator="|">Settings Div</div>
            <div class="animate-switch" ng-switch-when="home">Home Span</div>
            <div class="animate-switch" ng-switch-default>default</div>
        </div>
      </div>
    </file>
    <file name="script.js">
      angular.module('switchExample', ['ngAnimate'])
        .controller('ExampleController', ['$scope', function($scope) {
          $scope.items = ['settings', 'home', 'options', 'other'];
          $scope.selection = $scope.items[0];
        }]);
    </file>
    <file name="animations.css">
      .animate-switch-container {
        position:relative;
        background:white;
        border:1px solid black;
        height:40px;
        overflow:hidden;
      }

      .animate-switch {
        padding:10px;
      }

      .animate-switch.ng-animate {
        transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;

        position:absolute;
        top:0;
        left:0;
        right:0;
        bottom:0;
      }

      .animate-switch.ng-leave.ng-leave-active,
      .animate-switch.ng-enter {
        top:-50px;
      }
      .animate-switch.ng-leave,
      .animate-switch.ng-enter.ng-enter-active {
        top:0;
      }
    </file>
    <file name="protractor.js" type="protractor">
      var switchElem = element(by.css('[ng-switch]'));
      var select = element(by.model('selection'));

      it('should start in settings', function() {
        expect(switchElem.getText()).toMatch(/Settings Div/);
      });
      it('should change to home', function() {
        select.all(by.css('option')).get(1).click();
        expect(switchElem.getText()).toMatch(/Home Span/);
      });
      it('should change to settings via "options"', function() {
        select.all(by.css('option')).get(2).click();
        expect(switchElem.getText()).toMatch(/Settings Div/);
      });
      it('should select default', function() {
        select.all(by.css('option')).get(3).click();
        expect(switchElem.getText()).toMatch(/default/);
      });
    </file>
  </example>
 */
var ngSwitchDirective = ['$animate', '$compile', function($animate, $compile) {
  return {
    require: 'ngSwitch',

    // asks for $scope to fool the BC controller module
    controller: ['$scope', function NgSwitchController() {
     this.cases = {};
    }],
    link: function(scope, element, attr, ngSwitchController) {
      var watchExpr = attr.ngSwitch || attr.on,
          selectedTranscludes = [],
          selectedElements = [],
          previousLeaveAnimations = [],
          selectedScopes = [];

      var spliceFactory = function(array, index) {
          return function(response) {
            if (response !== false) array.splice(index, 1);
          };
      };

      scope.$watch(watchExpr, function ngSwitchWatchAction(value) {
        var i, ii;

        // Start with the last, in case the array is modified during the loop
        while (previousLeaveAnimations.length) {
          $animate.cancel(previousLeaveAnimations.pop());
        }

        for (i = 0, ii = selectedScopes.length; i < ii; ++i) {
          var selected = getBlockNodes(selectedElements[i].clone);
          selectedScopes[i].$destroy();
          var runner = previousLeaveAnimations[i] = $animate.leave(selected);
          runner.done(spliceFactory(previousLeaveAnimations, i));
        }

        selectedElements.length = 0;
        selectedScopes.length = 0;

        if ((selectedTranscludes = ngSwitchController.cases['!' + value] || ngSwitchController.cases['?'])) {
          forEach(selectedTranscludes, function(selectedTransclude) {
            selectedTransclude.transclude(function(caseElement, selectedScope) {
              selectedScopes.push(selectedScope);
              var anchor = selectedTransclude.element;
              caseElement[caseElement.length++] = $compile.$$createComment('end ngSwitchWhen');
              var block = { clone: caseElement };

              selectedElements.push(block);
              $animate.enter(caseElement, anchor.parent(), anchor);
            });
          });
        }
      });
    }
  };
}];

var ngSwitchWhenDirective = ngDirective({
  transclude: 'element',
  priority: 1200,
  require: '^ngSwitch',
  multiElement: true,
  link: function(scope, element, attrs, ctrl, $transclude) {

    var cases = attrs.ngSwitchWhen.split(attrs.ngSwitchWhenSeparator).sort().filter(
      // Filter duplicate cases
      function(element, index, array) { return array[index - 1] !== element; }
    );

    forEach(cases, function(whenCase) {
      ctrl.cases['!' + whenCase] = (ctrl.cases['!' + whenCase] || []);
      ctrl.cases['!' + whenCase].push({ transclude: $transclude, element: element });
    });
  }
});

var ngSwitchDefaultDirective = ngDirective({
  transclude: 'element',
  priority: 1200,
  require: '^ngSwitch',
  multiElement: true,
  link: function(scope, element, attr, ctrl, $transclude) {
    ctrl.cases['?'] = (ctrl.cases['?'] || []);
    ctrl.cases['?'].push({ transclude: $transclude, element: element });
   }
});

/**
 * @ngdoc directive
 * @name ngTransclude
 * @restrict EAC
 *
 * @description
 * Directive that marks the insertion point for the transcluded DOM of the nearest parent directive that uses transclusion.
 *
 * You can specify that you want to insert a named transclusion slot, instead of the default slot, by providing the slot name
 * as the value of the `ng-transclude` or `ng-transclude-slot` attribute.
 *
 * If the transcluded content is not empty (i.e. contains one or more DOM nodes, including whitespace text nodes), any existing
 * content of this element will be removed before the transcluded content is inserted.
 * If the transcluded content is empty (or only whitespace), the existing content is left intact. This lets you provide fallback
 * content in the case that no transcluded content is provided.
 *
 * @element ANY
 *
 * @param {string} ngTransclude|ngTranscludeSlot the name of the slot to insert at this point. If this is not provided, is empty
 *                                               or its value is the same as the name of the attribute then the default slot is used.
 *
 * @example
 * ### Basic transclusion
 * This example demonstrates basic transclusion of content into a component directive.
 * <example name="simpleTranscludeExample" module="transcludeExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('transcludeExample', [])
 *        .directive('pane', function(){
 *           return {
 *             restrict: 'E',
 *             transclude: true,
 *             scope: { title:'@' },
 *             template: '<div style="border: 1px solid black;">' +
 *                         '<div style="background-color: gray">{{title}}</div>' +
 *                         '<ng-transclude></ng-transclude>' +
 *                       '</div>'
 *           };
 *       })
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         $scope.title = 'Lorem Ipsum';
 *         $scope.text = 'Neque porro quisquam est qui dolorem ipsum quia dolor...';
 *       }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <input ng-model="title" aria-label="title"> <br/>
 *       <textarea ng-model="text" aria-label="text"></textarea> <br/>
 *       <pane title="{{title}}"><span>{{text}}</span></pane>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *      it('should have transcluded', function() {
 *        var titleElement = element(by.model('title'));
 *        titleElement.clear();
 *        titleElement.sendKeys('TITLE');
 *        var textElement = element(by.model('text'));
 *        textElement.clear();
 *        textElement.sendKeys('TEXT');
 *        expect(element(by.binding('title')).getText()).toEqual('TITLE');
 *        expect(element(by.binding('text')).getText()).toEqual('TEXT');
 *      });
 *   </file>
 * </example>
 *
 * @example
 * ### Transclude fallback content
 * This example shows how to use `NgTransclude` with fallback content, that
 * is displayed if no transcluded content is provided.
 *
 * <example module="transcludeFallbackContentExample" name="ng-transclude">
 * <file name="index.html">
 * <script>
 * angular.module('transcludeFallbackContentExample', [])
 * .directive('myButton', function(){
 *             return {
 *               restrict: 'E',
 *               transclude: true,
 *               scope: true,
 *               template: '<button style="cursor: pointer;">' +
 *                           '<ng-transclude>' +
 *                             '<b style="color: red;">Button1</b>' +
 *                           '</ng-transclude>' +
 *                         '</button>'
 *             };
 *         });
 * </script>
 * <!-- fallback button content -->
 * <my-button id="fallback"></my-button>
 * <!-- modified button content -->
 * <my-button id="modified">
 *   <i style="color: green;">Button2</i>
 * </my-button>
 * </file>
 * <file name="protractor.js" type="protractor">
 * it('should have different transclude element content', function() {
 *          expect(element(by.id('fallback')).getText()).toBe('Button1');
 *          expect(element(by.id('modified')).getText()).toBe('Button2');
 *        });
 * </file>
 * </example>
 *
 * @example
 * ### Multi-slot transclusion
 * This example demonstrates using multi-slot transclusion in a component directive.
 * <example name="multiSlotTranscludeExample" module="multiSlotTranscludeExample">
 *   <file name="index.html">
 *    <style>
 *      .title, .footer {
 *        background-color: gray
 *      }
 *    </style>
 *    <div ng-controller="ExampleController">
 *      <input ng-model="title" aria-label="title"> <br/>
 *      <textarea ng-model="text" aria-label="text"></textarea> <br/>
 *      <pane>
 *        <pane-title><a ng-href="{{link}}">{{title}}</a></pane-title>
 *        <pane-body><p>{{text}}</p></pane-body>
 *      </pane>
 *    </div>
 *   </file>
 *   <file name="app.js">
 *    angular.module('multiSlotTranscludeExample', [])
 *     .directive('pane', function() {
 *        return {
 *          restrict: 'E',
 *          transclude: {
 *            'title': '?paneTitle',
 *            'body': 'paneBody',
 *            'footer': '?paneFooter'
 *          },
 *          template: '<div style="border: 1px solid black;">' +
 *                      '<div class="title" ng-transclude="title">Fallback Title</div>' +
 *                      '<div ng-transclude="body"></div>' +
 *                      '<div class="footer" ng-transclude="footer">Fallback Footer</div>' +
 *                    '</div>'
 *        };
 *    })
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.title = 'Lorem Ipsum';
 *      $scope.link = 'https://google.com';
 *      $scope.text = 'Neque porro quisquam est qui dolorem ipsum quia dolor...';
 *    }]);
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *      it('should have transcluded the title and the body', function() {
 *        var titleElement = element(by.model('title'));
 *        titleElement.clear();
 *        titleElement.sendKeys('TITLE');
 *        var textElement = element(by.model('text'));
 *        textElement.clear();
 *        textElement.sendKeys('TEXT');
 *        expect(element(by.css('.title')).getText()).toEqual('TITLE');
 *        expect(element(by.binding('text')).getText()).toEqual('TEXT');
 *        expect(element(by.css('.footer')).getText()).toEqual('Fallback Footer');
 *      });
 *   </file>
 * </example>
 */
var ngTranscludeMinErr = minErr('ngTransclude');
var ngTranscludeDirective = ['$compile', function($compile) {
  return {
    restrict: 'EAC',
    compile: function ngTranscludeCompile(tElement) {

      // Remove and cache any original content to act as a fallback
      var fallbackLinkFn = $compile(tElement.contents());
      tElement.empty();

      return function ngTranscludePostLink($scope, $element, $attrs, controller, $transclude) {

        if (!$transclude) {
          throw ngTranscludeMinErr('orphan',
          'Illegal use of ngTransclude directive in the template! ' +
          'No parent directive that requires a transclusion found. ' +
          'Element: {0}',
          startingTag($element));
        }


        // If the attribute is of the form: `ng-transclude="ng-transclude"` then treat it like the default
        if ($attrs.ngTransclude === $attrs.$attr.ngTransclude) {
          $attrs.ngTransclude = '';
        }
        var slotName = $attrs.ngTransclude || $attrs.ngTranscludeSlot;

        // If the slot is required and no transclusion content is provided then this call will throw an error
        $transclude(ngTranscludeCloneAttachFn, null, slotName);

        // If the slot is optional and no transclusion content is provided then use the fallback content
        if (slotName && !$transclude.isSlotFilled(slotName)) {
          useFallbackContent();
        }

        function ngTranscludeCloneAttachFn(clone, transcludedScope) {
          if (clone.length && notWhitespace(clone)) {
            $element.append(clone);
          } else {
            useFallbackContent();
            // There is nothing linked against the transcluded scope since no content was available,
            // so it should be safe to clean up the generated scope.
            transcludedScope.$destroy();
          }
        }

        function useFallbackContent() {
          // Since this is the fallback content rather than the transcluded content,
          // we link against the scope of this directive rather than the transcluded scope
          fallbackLinkFn($scope, function(clone) {
            $element.append(clone);
          });
        }

        function notWhitespace(nodes) {
          for (var i = 0, ii = nodes.length; i < ii; i++) {
            var node = nodes[i];
            if (node.nodeType !== NODE_TYPE_TEXT || node.nodeValue.trim()) {
              return true;
            }
          }
        }
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name script
 * @restrict E
 *
 * @description
 * Load the content of a `<script>` element into {@link ng.$templateCache `$templateCache`}, so that the
 * template can be used by {@link ng.directive:ngInclude `ngInclude`},
 * {@link ngRoute.directive:ngView `ngView`}, or {@link guide/directive directives}. The type of the
 * `<script>` element must be specified as `text/ng-template`, and a cache name for the template must be
 * assigned through the element's `id`, which can then be used as a directive's `templateUrl`.
 *
 * @param {string} type Must be set to `'text/ng-template'`.
 * @param {string} id Cache name of the template.
 *
 * @example
  <example  name="script-tag">
    <file name="index.html">
      <script type="text/ng-template" id="/tpl.html">
        Content of the template.
      </script>

      <a ng-click="currentTpl='/tpl.html'" id="tpl-link">Load inlined template</a>
      <div id="tpl-content" ng-include src="currentTpl"></div>
    </file>
    <file name="protractor.js" type="protractor">
      it('should load template defined inside script tag', function() {
        element(by.css('#tpl-link')).click();
        expect(element(by.css('#tpl-content')).getText()).toMatch(/Content of the template/);
      });
    </file>
  </example>
 */
var scriptDirective = ['$templateCache', function($templateCache) {
  return {
    restrict: 'E',
    terminal: true,
    compile: function(element, attr) {
      if (attr.type === 'text/ng-template') {
        var templateUrl = attr.id,
            text = element[0].text;

        $templateCache.put(templateUrl, text);
      }
    }
  };
}];

/* exported selectDirective, optionDirective */

var noopNgModelController = { $setViewValue: noop, $render: noop };

function setOptionSelectedStatus(optionEl, value) {
  optionEl.prop('selected', value);
  /**
   * When unselecting an option, setting the property to null / false should be enough
   * However, screenreaders might react to the selected attribute instead, see
   * https://github.com/angular/angular.js/issues/14419
   * Note: "selected" is a boolean attr and will be removed when the "value" arg in attr() is false
   * or null
   */
  optionEl.attr('selected', value);
}

/**
 * @ngdoc type
 * @name  select.SelectController
 *
 * @description
 * The controller for the {@link ng.select select} directive. The controller exposes
 * a few utility methods that can be used to augment the behavior of a regular or an
 * {@link ng.ngOptions ngOptions} select element.
 *
 * @example
 * ### Set a custom error when the unknown option is selected
 *
 * This example sets a custom error "unknownValue" on the ngModelController
 * when the select element's unknown option is selected, i.e. when the model is set to a value
 * that is not matched by any option.
 *
 * <example name="select-unknown-value-error" module="staticSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="testSelect"> Single select: </label><br>
 *     <select name="testSelect" ng-model="selected" unknown-value-error>
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *     <span class="error" ng-if="myForm.testSelect.$error.unknownValue">
 *       Error: The current model doesn't match any option</span><br>
 *
 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
 *   </form>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('staticSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.selected = null;
 *
 *      $scope.forceUnknownOption = function() {
 *        $scope.selected = 'nonsense';
 *      };
 *   }])
 *   .directive('unknownValueError', function() {
 *     return {
 *       require: ['ngModel', 'select'],
 *       link: function(scope, element, attrs, ctrls) {
 *         var ngModelCtrl = ctrls[0];
 *         var selectCtrl = ctrls[1];
 *
 *         ngModelCtrl.$validators.unknownValue = function(modelValue, viewValue) {
 *           if (selectCtrl.$isUnknownOptionSelected()) {
 *             return false;
 *           }
 *
 *           return true;
 *         };
 *       }
 *
 *     };
 *   });
 * </file>
 *</example>
 *
 *
 * @example
 * ### Set the "required" error when the unknown option is selected.
 *
 * By default, the "required" error on the ngModelController is only set on a required select
 * when the empty option is selected. This example adds a custom directive that also sets the
 * error when the unknown option is selected.
 *
 * <example name="select-unknown-value-required" module="staticSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="testSelect"> Select: </label><br>
 *     <select name="testSelect" ng-model="selected" required unknown-value-required>
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *     <span class="error" ng-if="myForm.testSelect.$error.required">Error: Please select a value</span><br>
 *
 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
 *   </form>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('staticSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.selected = null;
 *
 *      $scope.forceUnknownOption = function() {
 *        $scope.selected = 'nonsense';
 *      };
 *   }])
 *   .directive('unknownValueRequired', function() {
 *     return {
 *       priority: 1, // This directive must run after the required directive has added its validator
 *       require: ['ngModel', 'select'],
 *       link: function(scope, element, attrs, ctrls) {
 *         var ngModelCtrl = ctrls[0];
 *         var selectCtrl = ctrls[1];
 *
 *         var originalRequiredValidator = ngModelCtrl.$validators.required;
 *
 *         ngModelCtrl.$validators.required = function() {
 *           if (attrs.required && selectCtrl.$isUnknownOptionSelected()) {
 *             return false;
 *           }
 *
 *           return originalRequiredValidator.apply(this, arguments);
 *         };
 *       }
 *     };
 *   });
 * </file>
 * <file name="protractor.js" type="protractor">
 *  it('should show the error message when the unknown option is selected', function() {

      var error = element(by.className('error'));

      expect(error.getText()).toBe('Error: Please select a value');

      element(by.cssContainingText('option', 'Option 1')).click();

      expect(error.isPresent()).toBe(false);

      element(by.tagName('button')).click();

      expect(error.getText()).toBe('Error: Please select a value');
    });
 * </file>
 *</example>
 *
 *
 */
var SelectController =
        ['$element', '$scope', /** @this */ function($element, $scope) {

  var self = this,
      optionsMap = new NgMap();

  self.selectValueMap = {}; // Keys are the hashed values, values the original values

  // If the ngModel doesn't get provided then provide a dummy noop version to prevent errors
  self.ngModelCtrl = noopNgModelController;
  self.multiple = false;

  // The "unknown" option is one that is prepended to the list if the viewValue
  // does not match any of the options. When it is rendered the value of the unknown
  // option is '? XXX ?' where XXX is the hashKey of the value that is not known.
  //
  // Support: IE 9 only
  // We can't just jqLite('<option>') since jqLite is not smart enough
  // to create it in <select> and IE barfs otherwise.
  self.unknownOption = jqLite(window.document.createElement('option'));

  // The empty option is an option with the value '' that the application developer can
  // provide inside the select. It is always selectable and indicates that a "null" selection has
  // been made by the user.
  // If the select has an empty option, and the model of the select is set to "undefined" or "null",
  // the empty option is selected.
  // If the model is set to a different unmatched value, the unknown option is rendered and
  // selected, i.e both are present, because a "null" selection and an unknown value are different.
  self.hasEmptyOption = false;
  self.emptyOption = undefined;

  self.renderUnknownOption = function(val) {
    var unknownVal = self.generateUnknownOptionValue(val);
    self.unknownOption.val(unknownVal);
    $element.prepend(self.unknownOption);
    setOptionSelectedStatus(self.unknownOption, true);
    $element.val(unknownVal);
  };

  self.updateUnknownOption = function(val) {
    var unknownVal = self.generateUnknownOptionValue(val);
    self.unknownOption.val(unknownVal);
    setOptionSelectedStatus(self.unknownOption, true);
    $element.val(unknownVal);
  };

  self.generateUnknownOptionValue = function(val) {
    return '? ' + hashKey(val) + ' ?';
  };

  self.removeUnknownOption = function() {
    if (self.unknownOption.parent()) self.unknownOption.remove();
  };

  self.selectEmptyOption = function() {
    if (self.emptyOption) {
      $element.val('');
      setOptionSelectedStatus(self.emptyOption, true);
    }
  };

  self.unselectEmptyOption = function() {
    if (self.hasEmptyOption) {
      setOptionSelectedStatus(self.emptyOption, false);
    }
  };

  $scope.$on('$destroy', function() {
    // disable unknown option so that we don't do work when the whole select is being destroyed
    self.renderUnknownOption = noop;
  });

  // Read the value of the select control, the implementation of this changes depending
  // upon whether the select can have multiple values and whether ngOptions is at work.
  self.readValue = function readSingleValue() {
    var val = $element.val();
    // ngValue added option values are stored in the selectValueMap, normal interpolations are not
    var realVal = val in self.selectValueMap ? self.selectValueMap[val] : val;

    if (self.hasOption(realVal)) {
      return realVal;
    }

    return null;
  };


  // Write the value to the select control, the implementation of this changes depending
  // upon whether the select can have multiple values and whether ngOptions is at work.
  self.writeValue = function writeSingleValue(value) {
    // Make sure to remove the selected attribute from the previously selected option
    // Otherwise, screen readers might get confused
    var currentlySelectedOption = $element[0].options[$element[0].selectedIndex];
    if (currentlySelectedOption) setOptionSelectedStatus(jqLite(currentlySelectedOption), false);

    if (self.hasOption(value)) {
      self.removeUnknownOption();

      var hashedVal = hashKey(value);
      $element.val(hashedVal in self.selectValueMap ? hashedVal : value);

      // Set selected attribute and property on selected option for screen readers
      var selectedOption = $element[0].options[$element[0].selectedIndex];
      setOptionSelectedStatus(jqLite(selectedOption), true);
    } else {
      self.selectUnknownOrEmptyOption(value);
    }
  };


  // Tell the select control that an option, with the given value, has been added
  self.addOption = function(value, element) {
    // Skip comment nodes, as they only pollute the `optionsMap`
    if (element[0].nodeType === NODE_TYPE_COMMENT) return;

    assertNotHasOwnProperty(value, '"option value"');
    if (value === '') {
      self.hasEmptyOption = true;
      self.emptyOption = element;
    }
    var count = optionsMap.get(value) || 0;
    optionsMap.set(value, count + 1);
    // Only render at the end of a digest. This improves render performance when many options
    // are added during a digest and ensures all relevant options are correctly marked as selected
    scheduleRender();
  };

  // Tell the select control that an option, with the given value, has been removed
  self.removeOption = function(value) {
    var count = optionsMap.get(value);
    if (count) {
      if (count === 1) {
        optionsMap.delete(value);
        if (value === '') {
          self.hasEmptyOption = false;
          self.emptyOption = undefined;
        }
      } else {
        optionsMap.set(value, count - 1);
      }
    }
  };

  // Check whether the select control has an option matching the given value
  self.hasOption = function(value) {
    return !!optionsMap.get(value);
  };

  /**
   * @ngdoc method
   * @name select.SelectController#$hasEmptyOption
   *
   * @description
   *
   * Returns `true` if the select element currently has an empty option
   * element, i.e. an option that signifies that the select is empty / the selection is null.
   *
   */
  self.$hasEmptyOption = function() {
    return self.hasEmptyOption;
  };

  /**
   * @ngdoc method
   * @name select.SelectController#$isUnknownOptionSelected
   *
   * @description
   *
   * Returns `true` if the select element's unknown option is selected. The unknown option is added
   * and automatically selected whenever the select model doesn't match any option.
   *
   */
  self.$isUnknownOptionSelected = function() {
    // Presence of the unknown option means it is selected
    return $element[0].options[0] === self.unknownOption[0];
  };

  /**
   * @ngdoc method
   * @name select.SelectController#$isEmptyOptionSelected
   *
   * @description
   *
   * Returns `true` if the select element has an empty option and this empty option is currently
   * selected. Returns `false` if the select element has no empty option or it is not selected.
   *
   */
  self.$isEmptyOptionSelected = function() {
    return self.hasEmptyOption && $element[0].options[$element[0].selectedIndex] === self.emptyOption[0];
  };

  self.selectUnknownOrEmptyOption = function(value) {
    if (value == null && self.emptyOption) {
      self.removeUnknownOption();
      self.selectEmptyOption();
    } else if (self.unknownOption.parent().length) {
      self.updateUnknownOption(value);
    } else {
      self.renderUnknownOption(value);
    }
  };

  var renderScheduled = false;
  function scheduleRender() {
    if (renderScheduled) return;
    renderScheduled = true;
    $scope.$$postDigest(function() {
      renderScheduled = false;
      self.ngModelCtrl.$render();
    });
  }

  var updateScheduled = false;
  function scheduleViewValueUpdate(renderAfter) {
    if (updateScheduled) return;

    updateScheduled = true;

    $scope.$$postDigest(function() {
      if ($scope.$$destroyed) return;

      updateScheduled = false;
      self.ngModelCtrl.$setViewValue(self.readValue());
      if (renderAfter) self.ngModelCtrl.$render();
    });
  }


  self.registerOption = function(optionScope, optionElement, optionAttrs, interpolateValueFn, interpolateTextFn) {

    if (optionAttrs.$attr.ngValue) {
      // The value attribute is set by ngValue
      var oldVal, hashedVal;
      optionAttrs.$observe('value', function valueAttributeObserveAction(newVal) {

        var removal;
        var previouslySelected = optionElement.prop('selected');

        if (isDefined(hashedVal)) {
          self.removeOption(oldVal);
          delete self.selectValueMap[hashedVal];
          removal = true;
        }

        hashedVal = hashKey(newVal);
        oldVal = newVal;
        self.selectValueMap[hashedVal] = newVal;
        self.addOption(newVal, optionElement);
        // Set the attribute directly instead of using optionAttrs.$set - this stops the observer
        // from firing a second time. Other $observers on value will also get the result of the
        // ngValue expression, not the hashed value
        optionElement.attr('value', hashedVal);

        if (removal && previouslySelected) {
          scheduleViewValueUpdate();
        }

      });
    } else if (interpolateValueFn) {
      // The value attribute is interpolated
      optionAttrs.$observe('value', function valueAttributeObserveAction(newVal) {
        // This method is overwritten in ngOptions and has side-effects!
        self.readValue();

        var removal;
        var previouslySelected = optionElement.prop('selected');

        if (isDefined(oldVal)) {
          self.removeOption(oldVal);
          removal = true;
        }
        oldVal = newVal;
        self.addOption(newVal, optionElement);

        if (removal && previouslySelected) {
          scheduleViewValueUpdate();
        }
      });
    } else if (interpolateTextFn) {
      // The text content is interpolated
      optionScope.$watch(interpolateTextFn, function interpolateWatchAction(newVal, oldVal) {
        optionAttrs.$set('value', newVal);
        var previouslySelected = optionElement.prop('selected');
        if (oldVal !== newVal) {
          self.removeOption(oldVal);
        }
        self.addOption(newVal, optionElement);

        if (oldVal && previouslySelected) {
          scheduleViewValueUpdate();
        }
      });
    } else {
      // The value attribute is static
      self.addOption(optionAttrs.value, optionElement);
    }


    optionAttrs.$observe('disabled', function(newVal) {

      // Since model updates will also select disabled options (like ngOptions),
      // we only have to handle options becoming disabled, not enabled

      if (newVal === 'true' || newVal && optionElement.prop('selected')) {
        if (self.multiple) {
          scheduleViewValueUpdate(true);
        } else {
          self.ngModelCtrl.$setViewValue(null);
          self.ngModelCtrl.$render();
        }
      }
    });

    optionElement.on('$destroy', function() {
      var currentValue = self.readValue();
      var removeValue = optionAttrs.value;

      self.removeOption(removeValue);
      scheduleRender();

      if (self.multiple && currentValue && currentValue.indexOf(removeValue) !== -1 ||
          currentValue === removeValue
      ) {
        // When multiple (selected) options are destroyed at the same time, we don't want
        // to run a model update for each of them. Instead, run a single update in the $$postDigest
        scheduleViewValueUpdate(true);
      }
    });
  };
}];

/**
 * @ngdoc directive
 * @name select
 * @restrict E
 *
 * @description
 * HTML `select` element with AngularJS data-binding.
 *
 * The `select` directive is used together with {@link ngModel `ngModel`} to provide data-binding
 * between the scope and the `<select>` control (including setting default values).
 * It also handles dynamic `<option>` elements, which can be added using the {@link ngRepeat `ngRepeat}` or
 * {@link ngOptions `ngOptions`} directives.
 *
 * When an item in the `<select>` menu is selected, the value of the selected option will be bound
 * to the model identified by the `ngModel` directive. With static or repeated options, this is
 * the content of the `value` attribute or the textContent of the `<option>`, if the value attribute is missing.
 * Value and textContent can be interpolated.
 *
 * The {@link select.SelectController select controller} exposes utility functions that can be used
 * to manipulate the select's behavior.
 *
 * ## Matching model and option values
 *
 * In general, the match between the model and an option is evaluated by strictly comparing the model
 * value against the value of the available options.
 *
 * If you are setting the option value with the option's `value` attribute, or textContent, the
 * value will always be a `string` which means that the model value must also be a string.
 * Otherwise the `select` directive cannot match them correctly.
 *
 * To bind the model to a non-string value, you can use one of the following strategies:
 * - the {@link ng.ngOptions `ngOptions`} directive
 *   ({@link ng.select#using-select-with-ngoptions-and-setting-a-default-value})
 * - the {@link ng.ngValue `ngValue`} directive, which allows arbitrary expressions to be
 *   option values ({@link ng.select#using-ngvalue-to-bind-the-model-to-an-array-of-objects Example})
 * - model $parsers / $formatters to convert the string value
 *   ({@link ng.select#binding-select-to-a-non-string-value-via-ngmodel-parsing-formatting Example})
 *
 * If the viewValue of `ngModel` does not match any of the options, then the control
 * will automatically add an "unknown" option, which it then removes when the mismatch is resolved.
 *
 * Optionally, a single hard-coded `<option>` element, with the value set to an empty string, can
 * be nested into the `<select>` element. This element will then represent the `null` or "not selected"
 * option. See example below for demonstration.
 *
 * ## Choosing between `ngRepeat` and `ngOptions`
 *
 * In many cases, `ngRepeat` can be used on `<option>` elements instead of {@link ng.directive:ngOptions
 * ngOptions} to achieve a similar result. However, `ngOptions` provides some benefits:
 * - more flexibility in how the `<select>`'s model is assigned via the `select` **`as`** part of the
 * comprehension expression
 * - reduced memory consumption by not creating a new scope for each repeated instance
 * - increased render speed by creating the options in a documentFragment instead of individually
 *
 * Specifically, select with repeated options slows down significantly starting at 2000 options in
 * Chrome and Internet Explorer / Edge.
 *
 *
 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} multiple Allows multiple options to be selected. The selected values will be
 *     bound to the model as an array.
 * @param {string=} required Sets `required` validation error key if the value is not entered.
 * @param {string=} ngRequired Adds required attribute and required validation constraint to
 * the element when the ngRequired expression evaluates to true. Use ngRequired instead of required
 * when you want to data-bind to the required attribute.
 * @param {string=} ngChange AngularJS expression to be executed when selected option(s) changes due to user
 *    interaction with the select element.
 * @param {string=} ngOptions sets the options that the select is populated with and defines what is
 * set on the model on selection. See {@link ngOptions `ngOptions`}.
 * @param {string=} ngAttrSize sets the size of the select element dynamically. Uses the
 * {@link guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes ngAttr} directive.
 *
 *
 * @example
 * ### Simple `select` elements with static options
 *
 * <example name="static-select" module="staticSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="singleSelect"> Single select: </label><br>
 *     <select name="singleSelect" ng-model="data.singleSelect">
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *
 *     <label for="singleSelect"> Single select with "not selected" option and dynamic option values: </label><br>
 *     <select name="singleSelect" id="singleSelect" ng-model="data.singleSelect">
 *       <option value="">---Please select---</option> <!-- not selected / blank option -->
 *       <option value="{{data.option1}}">Option 1</option> <!-- interpolation -->
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
 *     <tt>singleSelect = {{data.singleSelect}}</tt>
 *
 *     <hr>
 *     <label for="multipleSelect"> Multiple select: </label><br>
 *     <select name="multipleSelect" id="multipleSelect" ng-model="data.multipleSelect" multiple>
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *       <option value="option-3">Option 3</option>
 *     </select><br>
 *     <tt>multipleSelect = {{data.multipleSelect}}</tt><br/>
 *   </form>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('staticSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       singleSelect: null,
 *       multipleSelect: [],
 *       option1: 'option-1'
 *      };
 *
 *      $scope.forceUnknownOption = function() {
 *        $scope.data.singleSelect = 'nonsense';
 *      };
 *   }]);
 * </file>
 *</example>
 *
 * @example
 * ### Using `ngRepeat` to generate `select` options
 * <example name="select-ngrepeat" module="ngrepeatSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="repeatSelect"> Repeat select: </label>
 *     <select name="repeatSelect" id="repeatSelect" ng-model="data.model">
 *       <option ng-repeat="option in data.availableOptions" value="{{option.id}}">{{option.name}}</option>
 *     </select>
 *   </form>
 *   <hr>
 *   <tt>model = {{data.model}}</tt><br/>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('ngrepeatSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       model: null,
 *       availableOptions: [
 *         {id: '1', name: 'Option A'},
 *         {id: '2', name: 'Option B'},
 *         {id: '3', name: 'Option C'}
 *       ]
 *      };
 *   }]);
 * </file>
 *</example>
 *
 * @example
 * ### Using `ngValue` to bind the model to an array of objects
 * <example name="select-ngvalue" module="ngvalueSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="ngvalueselect"> ngvalue select: </label>
 *     <select size="6" name="ngvalueselect" ng-model="data.model" multiple>
 *       <option ng-repeat="option in data.availableOptions" ng-value="option.value">{{option.name}}</option>
 *     </select>
 *   </form>
 *   <hr>
 *   <pre>model = {{data.model | json}}</pre><br/>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('ngvalueSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       model: null,
 *       availableOptions: [
           {value: 'myString', name: 'string'},
           {value: 1, name: 'integer'},
           {value: true, name: 'boolean'},
           {value: null, name: 'null'},
           {value: {prop: 'value'}, name: 'object'},
           {value: ['a'], name: 'array'}
 *       ]
 *      };
 *   }]);
 * </file>
 *</example>
 *
 * @example
 * ### Using `select` with `ngOptions` and setting a default value
 * See the {@link ngOptions ngOptions documentation} for more `ngOptions` usage examples.
 *
 * <example name="select-with-default-values" module="defaultValueSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="mySelect">Make a choice:</label>
 *     <select name="mySelect" id="mySelect"
 *       ng-options="option.name for option in data.availableOptions track by option.id"
 *       ng-model="data.selectedOption"></select>
 *   </form>
 *   <hr>
 *   <tt>option = {{data.selectedOption}}</tt><br/>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('defaultValueSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       availableOptions: [
 *         {id: '1', name: 'Option A'},
 *         {id: '2', name: 'Option B'},
 *         {id: '3', name: 'Option C'}
 *       ],
 *       selectedOption: {id: '3', name: 'Option C'} //This sets the default value of the select in the ui
 *       };
 *   }]);
 * </file>
 *</example>
 *
 * @example
 * ### Binding `select` to a non-string value via `ngModel` parsing / formatting
 *
 * <example name="select-with-non-string-options" module="nonStringSelect">
 *   <file name="index.html">
 *     <select ng-model="model.id" convert-to-number>
 *       <option value="0">Zero</option>
 *       <option value="1">One</option>
 *       <option value="2">Two</option>
 *     </select>
 *     {{ model }}
 *   </file>
 *   <file name="app.js">
 *     angular.module('nonStringSelect', [])
 *       .run(function($rootScope) {
 *         $rootScope.model = { id: 2 };
 *       })
 *       .directive('convertToNumber', function() {
 *         return {
 *           require: 'ngModel',
 *           link: function(scope, element, attrs, ngModel) {
 *             ngModel.$parsers.push(function(val) {
 *               return parseInt(val, 10);
 *             });
 *             ngModel.$formatters.push(function(val) {
 *               return '' + val;
 *             });
 *           }
 *         };
 *       });
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     it('should initialize to model', function() {
 *       expect(element(by.model('model.id')).$('option:checked').getText()).toEqual('Two');
 *     });
 *   </file>
 * </example>
 *
 */
var selectDirective = function() {

  return {
    restrict: 'E',
    require: ['select', '?ngModel'],
    controller: SelectController,
    priority: 1,
    link: {
      pre: selectPreLink,
      post: selectPostLink
    }
  };

  function selectPreLink(scope, element, attr, ctrls) {

      var selectCtrl = ctrls[0];
      var ngModelCtrl = ctrls[1];

      // if ngModel is not defined, we don't need to do anything but set the registerOption
      // function to noop, so options don't get added internally
      if (!ngModelCtrl) {
        selectCtrl.registerOption = noop;
        return;
      }


      selectCtrl.ngModelCtrl = ngModelCtrl;

      // When the selected item(s) changes we delegate getting the value of the select control
      // to the `readValue` method, which can be changed if the select can have multiple
      // selected values or if the options are being generated by `ngOptions`
      element.on('change', function() {
        selectCtrl.removeUnknownOption();
        scope.$apply(function() {
          ngModelCtrl.$setViewValue(selectCtrl.readValue());
        });
      });

      // If the select allows multiple values then we need to modify how we read and write
      // values from and to the control; also what it means for the value to be empty and
      // we have to add an extra watch since ngModel doesn't work well with arrays - it
      // doesn't trigger rendering if only an item in the array changes.
      if (attr.multiple) {
        selectCtrl.multiple = true;

        // Read value now needs to check each option to see if it is selected
        selectCtrl.readValue = function readMultipleValue() {
          var array = [];
          forEach(element.find('option'), function(option) {
            if (option.selected && !option.disabled) {
              var val = option.value;
              array.push(val in selectCtrl.selectValueMap ? selectCtrl.selectValueMap[val] : val);
            }
          });
          return array;
        };

        // Write value now needs to set the selected property of each matching option
        selectCtrl.writeValue = function writeMultipleValue(value) {
          forEach(element.find('option'), function(option) {
            var shouldBeSelected = !!value && (includes(value, option.value) ||
                                               includes(value, selectCtrl.selectValueMap[option.value]));
            var currentlySelected = option.selected;

            // Support: IE 9-11 only, Edge 12-15+
            // In IE and Edge adding options to the selection via shift+click/UP/DOWN
            // will de-select already selected options if "selected" on those options was set
            // more than once (i.e. when the options were already selected)
            // So we only modify the selected property if necessary.
            // Note: this behavior cannot be replicated via unit tests because it only shows in the
            // actual user interface.
            if (shouldBeSelected !== currentlySelected) {
              setOptionSelectedStatus(jqLite(option), shouldBeSelected);
            }

          });
        };

        // we have to do it on each watch since ngModel watches reference, but
        // we need to work of an array, so we need to see if anything was inserted/removed
        var lastView, lastViewRef = NaN;
        scope.$watch(function selectMultipleWatch() {
          if (lastViewRef === ngModelCtrl.$viewValue && !equals(lastView, ngModelCtrl.$viewValue)) {
            lastView = shallowCopy(ngModelCtrl.$viewValue);
            ngModelCtrl.$render();
          }
          lastViewRef = ngModelCtrl.$viewValue;
        });

        // If we are a multiple select then value is now a collection
        // so the meaning of $isEmpty changes
        ngModelCtrl.$isEmpty = function(value) {
          return !value || value.length === 0;
        };

      }
    }

    function selectPostLink(scope, element, attrs, ctrls) {
      // if ngModel is not defined, we don't need to do anything
      var ngModelCtrl = ctrls[1];
      if (!ngModelCtrl) return;

      var selectCtrl = ctrls[0];

      // We delegate rendering to the `writeValue` method, which can be changed
      // if the select can have multiple selected values or if the options are being
      // generated by `ngOptions`.
      // This must be done in the postLink fn to prevent $render to be called before
      // all nodes have been linked correctly.
      ngModelCtrl.$render = function() {
        selectCtrl.writeValue(ngModelCtrl.$viewValue);
      };
    }
};


// The option directive is purely designed to communicate the existence (or lack of)
// of dynamically created (and destroyed) option elements to their containing select
// directive via its controller.
var optionDirective = ['$interpolate', function($interpolate) {
  return {
    restrict: 'E',
    priority: 100,
    compile: function(element, attr) {
      var interpolateValueFn, interpolateTextFn;

      if (isDefined(attr.ngValue)) {
        // Will be handled by registerOption
      } else if (isDefined(attr.value)) {
        // If the value attribute is defined, check if it contains an interpolation
        interpolateValueFn = $interpolate(attr.value, true);
      } else {
        // If the value attribute is not defined then we fall back to the
        // text content of the option element, which may be interpolated
        interpolateTextFn = $interpolate(element.text(), true);
        if (!interpolateTextFn) {
          attr.$set('value', element.text());
        }
      }

      return function(scope, element, attr) {
        // This is an optimization over using ^^ since we don't want to have to search
        // all the way to the root of the DOM for every single option element
        var selectCtrlName = '$selectController',
            parent = element.parent(),
            selectCtrl = parent.data(selectCtrlName) ||
              parent.parent().data(selectCtrlName); // in case we are in optgroup

        if (selectCtrl) {
          selectCtrl.registerOption(scope, element, attr, interpolateValueFn, interpolateTextFn);
        }
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngRequired
 * @restrict A
 *
 * @param {expression} ngRequired AngularJS expression. If it evaluates to `true`, it sets the
 *                                `required` attribute to the element and adds the `required`
 *                                {@link ngModel.NgModelController#$validators `validator`}.
 *
 * @description
 *
 * ngRequired adds the required {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for {@link input `input`} and {@link select `select`} controls, but can also be
 * applied to custom controls.
 *
 * The directive sets the `required` attribute on the element if the AngularJS expression inside
 * `ngRequired` evaluates to true. A special directive for setting `required` is necessary because we
 * cannot use interpolation inside `required`. See the {@link guide/interpolation interpolation guide}
 * for more info.
 *
 * The validator will set the `required` error key to true if the `required` attribute is set and
 * calling {@link ngModel.NgModelController#$isEmpty `NgModelController.$isEmpty`} with the
 * {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`} returns `true`. For example, the
 * `$isEmpty()` implementation for `input[text]` checks the length of the `$viewValue`. When developing
 * custom controls, `$isEmpty()` can be overwritten to account for a $viewValue that is not string-based.
 *
 * @example
 * <example name="ngRequiredDirective" module="ngRequiredExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngRequiredExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.required = true;
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="required">Toggle required: </label>
 *         <input type="checkbox" ng-model="required" id="required" />
 *         <br>
 *         <label for="input">This input must be filled if `required` is true: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-required="required" /><br>
 *         <hr>
 *         required error set? = <code>{{form.input.$error.required}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var required = element(by.binding('form.input.$error.required'));
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should set the required error', function() {
         expect(required.getText()).toContain('true');

         input.sendKeys('123');
         expect(required.getText()).not.toContain('true');
         expect(model.getText()).toContain('123');
       });
 *   </file>
 * </example>
 */
var requiredDirective = ['$parse', function($parse) {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;
      // For boolean attributes like required, presence means true
      var value = attr.hasOwnProperty('required') || $parse(attr.ngRequired)(scope);

      if (!attr.ngRequired) {
        // force truthy in case we are on non input element
        // (input elements do this automatically for boolean attributes like required)
        attr.required = true;
      }

      ctrl.$validators.required = function(modelValue, viewValue) {
        return !value || !ctrl.$isEmpty(viewValue);
      };

      attr.$observe('required', function(newVal) {

        if (value !== newVal) {
          value = newVal;
          ctrl.$validate();
        }
      });
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngPattern
 * @restrict A
 *
 * @param {expression|RegExp} ngPattern AngularJS expression that must evaluate to a `RegExp` or a `String`
 *                                      parsable into a `RegExp`, or a `RegExp` literal. See above for
 *                                      more details.
 *
 * @description
 *
 * ngPattern adds the pattern {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
 *
 * The validator sets the `pattern` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
 * does not match a RegExp which is obtained from the `ngPattern` attribute value:
 * - the value is an AngularJS expression:
 *   - If the expression evaluates to a RegExp object, then this is used directly.
 *   - If the expression evaluates to a string, then it will be converted to a RegExp after wrapping it
 *     in `^` and `$` characters. For instance, `"abc"` will be converted to `new RegExp('^abc$')`.
 * - If the value is a RegExp literal, e.g. `ngPattern="/^\d+$/"`, it is used directly.
 *
 * <div class="alert alert-info">
 * **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
 * start at the index of the last search's match, thus not taking the whole input value into
 * account.
 * </div>
 *
 * <div class="alert alert-info">
 * **Note:** This directive is also added when the plain `pattern` attribute is used, with two
 * differences:
 * <ol>
 *   <li>
 *     `ngPattern` does not set the `pattern` attribute and therefore HTML5 constraint validation is
 *     not available.
 *   </li>
 *   <li>
 *     The `ngPattern` attribute must be an expression, while the `pattern` value must be
 *     interpolated.
 *   </li>
 * </ol>
 * </div>
 *
 * @example
 * <example name="ngPatternDirective" module="ngPatternExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngPatternExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.regex = '\\d+';
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="regex">Set a pattern (regex string): </label>
 *         <input type="text" ng-model="regex" id="regex" />
 *         <br>
 *         <label for="input">This input is restricted by the current pattern: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-pattern="regex" /><br>
 *         <hr>
 *         input valid? = <code>{{form.input.$valid}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should validate the input with the default pattern', function() {
         input.sendKeys('aaa');
         expect(model.getText()).not.toContain('aaa');

         input.clear().then(function() {
           input.sendKeys('123');
           expect(model.getText()).toContain('123');
         });
       });
 *   </file>
 * </example>
 */
var patternDirective = ['$parse', function($parse) {
  return {
    restrict: 'A',
    require: '?ngModel',
    compile: function(tElm, tAttr) {
      var patternExp;
      var parseFn;

      if (tAttr.ngPattern) {
        patternExp = tAttr.ngPattern;

        // ngPattern might be a scope expression, or an inlined regex, which is not parsable.
        // We get value of the attribute here, so we can compare the old and the new value
        // in the observer to avoid unnecessary validations
        if (tAttr.ngPattern.charAt(0) === '/' && REGEX_STRING_REGEXP.test(tAttr.ngPattern)) {
          parseFn = function() { return tAttr.ngPattern; };
        } else {
          parseFn = $parse(tAttr.ngPattern);
        }
      }

      return function(scope, elm, attr, ctrl) {
        if (!ctrl) return;

        var attrVal = attr.pattern;

        if (attr.ngPattern) {
          attrVal = parseFn(scope);
        } else {
          patternExp = attr.pattern;
        }

        var regexp = parsePatternAttr(attrVal, patternExp, elm);

        attr.$observe('pattern', function(newVal) {
          var oldRegexp = regexp;

          regexp = parsePatternAttr(newVal, patternExp, elm);

          if ((oldRegexp && oldRegexp.toString()) !== (regexp && regexp.toString())) {
            ctrl.$validate();
          }
        });

        ctrl.$validators.pattern = function(modelValue, viewValue) {
          // HTML5 pattern constraint validates the input value, so we validate the viewValue
          return ctrl.$isEmpty(viewValue) || isUndefined(regexp) || regexp.test(viewValue);
        };
      };
    }

  };
}];

/**
 * @ngdoc directive
 * @name ngMaxlength
 * @restrict A
 *
 * @param {expression} ngMaxlength AngularJS expression that must evaluate to a `Number` or `String`
 *                                 parsable into a `Number`. Used as value for the `maxlength`
 *                                 {@link ngModel.NgModelController#$validators validator}.
 *
 * @description
 *
 * ngMaxlength adds the maxlength {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
 *
 * The validator sets the `maxlength` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
 * is longer than the integer obtained by evaluating the AngularJS expression given in the
 * `ngMaxlength` attribute value.
 *
 * <div class="alert alert-info">
 * **Note:** This directive is also added when the plain `maxlength` attribute is used, with two
 * differences:
 * <ol>
 *   <li>
 *     `ngMaxlength` does not set the `maxlength` attribute and therefore HTML5 constraint
 *     validation is not available.
 *   </li>
 *   <li>
 *     The `ngMaxlength` attribute must be an expression, while the `maxlength` value must be
 *     interpolated.
 *   </li>
 * </ol>
 * </div>
 *
 * @example
 * <example name="ngMaxlengthDirective" module="ngMaxlengthExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngMaxlengthExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.maxlength = 5;
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="maxlength">Set a maxlength: </label>
 *         <input type="number" ng-model="maxlength" id="maxlength" />
 *         <br>
 *         <label for="input">This input is restricted by the current maxlength: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-maxlength="maxlength" /><br>
 *         <hr>
 *         input valid? = <code>{{form.input.$valid}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should validate the input with the default maxlength', function() {
         input.sendKeys('abcdef');
         expect(model.getText()).not.toContain('abcdef');

         input.clear().then(function() {
           input.sendKeys('abcde');
           expect(model.getText()).toContain('abcde');
         });
       });
 *   </file>
 * </example>
 */
var maxlengthDirective = ['$parse', function($parse) {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;

      var maxlength = attr.maxlength || $parse(attr.ngMaxlength)(scope);
      var maxlengthParsed = parseLength(maxlength);

      attr.$observe('maxlength', function(value) {
        if (maxlength !== value) {
          maxlengthParsed = parseLength(value);
          maxlength = value;
          ctrl.$validate();
        }
      });
      ctrl.$validators.maxlength = function(modelValue, viewValue) {
        return (maxlengthParsed < 0) || ctrl.$isEmpty(viewValue) || (viewValue.length <= maxlengthParsed);
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngMinlength
 * @restrict A
 *
 * @param {expression} ngMinlength AngularJS expression that must evaluate to a `Number` or `String`
 *                                 parsable into a `Number`. Used as value for the `minlength`
 *                                 {@link ngModel.NgModelController#$validators validator}.
 *
 * @description
 *
 * ngMinlength adds the minlength {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
 *
 * The validator sets the `minlength` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
 * is shorter than the integer obtained by evaluating the AngularJS expression given in the
 * `ngMinlength` attribute value.
 *
 * <div class="alert alert-info">
 * **Note:** This directive is also added when the plain `minlength` attribute is used, with two
 * differences:
 * <ol>
 *   <li>
 *     `ngMinlength` does not set the `minlength` attribute and therefore HTML5 constraint
 *     validation is not available.
 *   </li>
 *   <li>
 *     The `ngMinlength` value must be an expression, while the `minlength` value must be
 *     interpolated.
 *   </li>
 * </ol>
 * </div>
 *
 * @example
 * <example name="ngMinlengthDirective" module="ngMinlengthExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngMinlengthExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.minlength = 3;
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="minlength">Set a minlength: </label>
 *         <input type="number" ng-model="minlength" id="minlength" />
 *         <br>
 *         <label for="input">This input is restricted by the current minlength: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-minlength="minlength" /><br>
 *         <hr>
 *         input valid? = <code>{{form.input.$valid}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should validate the input with the default minlength', function() {
         input.sendKeys('ab');
         expect(model.getText()).not.toContain('ab');

         input.sendKeys('abc');
         expect(model.getText()).toContain('abc');
       });
 *   </file>
 * </example>
 */
var minlengthDirective = ['$parse', function($parse) {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;

      var minlength = attr.minlength || $parse(attr.ngMinlength)(scope);
      var minlengthParsed = parseLength(minlength) || -1;

      attr.$observe('minlength', function(value) {
        if (minlength !== value) {
          minlengthParsed = parseLength(value) || -1;
          minlength = value;
          ctrl.$validate();
        }

      });
      ctrl.$validators.minlength = function(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || viewValue.length >= minlengthParsed;
      };
    }
  };
}];


function parsePatternAttr(regex, patternExp, elm) {
  if (!regex) return undefined;

  if (isString(regex)) {
    regex = new RegExp('^' + regex + '$');
  }

  if (!regex.test) {
    throw minErr('ngPattern')('noregexp',
      'Expected {0} to be a RegExp but was {1}. Element: {2}', patternExp,
      regex, startingTag(elm));
  }

  return regex;
}

function parseLength(val) {
  var intVal = toInt(val);
  return isNumberNaN(intVal) ? -1 : intVal;
}

if (window.angular.bootstrap) {
  // AngularJS is already loaded, so we can return here...
  if (window.console) {
    console.log('WARNING: Tried to load AngularJS more than once.');
  }
  return;
}

// try to bind to jquery now so that one can write jqLite(fn)
// but we will rebind on bootstrap again.
bindJQuery();

publishExternalAPI(angular);

angular.module("ngLocale", [], ["$provide", function($provide) {
var PLURAL_CATEGORY = {ZERO: "zero", ONE: "one", TWO: "two", FEW: "few", MANY: "many", OTHER: "other"};
function getDecimals(n) {
  n = n + '';
  var i = n.indexOf('.');
  return (i == -1) ? 0 : n.length - i - 1;
}

function getVF(n, opt_precision) {
  var v = opt_precision;

  if (undefined === v) {
    v = Math.min(getDecimals(n), 3);
  }

  var base = Math.pow(10, v);
  var f = ((n * base) | 0) % base;
  return {v: v, f: f};
}

$provide.value("$locale", {
  "DATETIME_FORMATS": {
    "AMPMS": [
      "AM",
      "PM"
    ],
    "DAY": [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday"
    ],
    "ERANAMES": [
      "Before Christ",
      "Anno Domini"
    ],
    "ERAS": [
      "BC",
      "AD"
    ],
    "FIRSTDAYOFWEEK": 6,
    "MONTH": [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ],
    "SHORTDAY": [
      "Sun",
      "Mon",
      "Tue",
      "Wed",
      "Thu",
      "Fri",
      "Sat"
    ],
    "SHORTMONTH": [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ],
    "STANDALONEMONTH": [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ],
    "WEEKENDRANGE": [
      5,
      6
    ],
    "fullDate": "EEEE, MMMM d, y",
    "longDate": "MMMM d, y",
    "medium": "MMM d, y h:mm:ss a",
    "mediumDate": "MMM d, y",
    "mediumTime": "h:mm:ss a",
    "short": "M/d/yy h:mm a",
    "shortDate": "M/d/yy",
    "shortTime": "h:mm a"
  },
  "NUMBER_FORMATS": {
    "CURRENCY_SYM": "$",
    "DECIMAL_SEP": ".",
    "GROUP_SEP": ",",
    "PATTERNS": [
      {
        "gSize": 3,
        "lgSize": 3,
        "maxFrac": 3,
        "minFrac": 0,
        "minInt": 1,
        "negPre": "-",
        "negSuf": "",
        "posPre": "",
        "posSuf": ""
      },
      {
        "gSize": 3,
        "lgSize": 3,
        "maxFrac": 2,
        "minFrac": 2,
        "minInt": 1,
        "negPre": "-\u00a4",
        "negSuf": "",
        "posPre": "\u00a4",
        "posSuf": ""
      }
    ]
  },
  "id": "en-us",
  "localeID": "en_US",
  "pluralCat": function(n, opt_precision) {  var i = n | 0;  var vf = getVF(n, opt_precision);  if (i == 1 && vf.v == 0) {    return PLURAL_CATEGORY.ONE;  }  return PLURAL_CATEGORY.OTHER;}
});
}]);

  jqLite(function() {
    angularInit(window.document, bootstrap);
  });

})(window);

!window.angular.$$csp().noInlineStyle && window.angular.element(document.head).prepend(window.angular.element('<style>').text('@charset "UTF-8";[ng\\:cloak],[ng-cloak],[data-ng-cloak],[x-ng-cloak],.ng-cloak,.x-ng-cloak,.ng-hide:not(.ng-hide-animate){display:none !important;}ng\\:form{display:block;}.ng-animate-shim{visibility:hidden;}.ng-anchor{position:absolute;}'));
})();
(function () {
/**
 * @license AngularJS v1.8.1
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/* global shallowCopy: true */

/**
 * Creates a shallow copy of an object, an array or a primitive.
 *
 * Assumes that there are no proto properties for objects.
 */
function shallowCopy(src, dst) {
  if (isArray(src)) {
    dst = dst || [];

    for (var i = 0, ii = src.length; i < ii; i++) {
      dst[i] = src[i];
    }
  } else if (isObject(src)) {
    dst = dst || {};

    for (var key in src) {
      if (!(key.charAt(0) === '$' && key.charAt(1) === '$')) {
        dst[key] = src[key];
      }
    }
  }

  return dst || src;
}

/* global routeToRegExp: true */

/**
 * @param {string} path - The path to parse. (It is assumed to have query and hash stripped off.)
 * @param {Object} opts - Options.
 * @return {Object} - An object containing an array of path parameter names (`keys`) and a regular
 *     expression (`regexp`) that can be used to identify a matching URL and extract the path
 *     parameter values.
 *
 * @description
 * Parses the given path, extracting path parameter names and a regular expression to match URLs.
 *
 * Originally inspired by `pathRexp` in `visionmedia/express/lib/utils.js`.
 */
function routeToRegExp(path, opts) {
  var keys = [];

  var pattern = path
    .replace(/([().])/g, '\\$1')
    .replace(/(\/)?:(\w+)(\*\?|[?*])?/g, function(_, slash, key, option) {
      var optional = option === '?' || option === '*?';
      var star = option === '*' || option === '*?';
      keys.push({name: key, optional: optional});
      slash = slash || '';
      return (
        (optional ? '(?:' + slash : slash + '(?:') +
        (star ? '(.+?)' : '([^/]+)') +
        (optional ? '?)?' : ')')
      );
    })
    .replace(/([/$*])/g, '\\$1');

  if (opts.ignoreTrailingSlashes) {
    pattern = pattern.replace(/\/+$/, '') + '/*';
  }

  return {
    keys: keys,
    regexp: new RegExp(
      '^' + pattern + '(?:[?#]|$)',
      opts.caseInsensitiveMatch ? 'i' : ''
    )
  };
}

/* global routeToRegExp: false */
/* global shallowCopy: false */

// `isArray` and `isObject` are necessary for `shallowCopy()` (included via `src/shallowCopy.js`).
// They are initialized inside the `$RouteProvider`, to ensure `window.angular` is available.
var isArray;
var isObject;
var isDefined;
var noop;

/**
 * @ngdoc module
 * @name ngRoute
 * @description
 *
 * The `ngRoute` module provides routing and deeplinking services and directives for AngularJS apps.
 *
 * ## Example
 * See {@link ngRoute.$route#examples $route} for an example of configuring and using `ngRoute`.
 *
 */
/* global -ngRouteModule */
var ngRouteModule = angular.
  module('ngRoute', []).
  info({ angularVersion: '1.8.1' }).
  provider('$route', $RouteProvider).
  // Ensure `$route` will be instantiated in time to capture the initial `$locationChangeSuccess`
  // event (unless explicitly disabled). This is necessary in case `ngView` is included in an
  // asynchronously loaded template.
  run(instantiateRoute);
var $routeMinErr = angular.$$minErr('ngRoute');
var isEagerInstantiationEnabled;


/**
 * @ngdoc provider
 * @name $routeProvider
 * @this
 *
 * @description
 *
 * Used for configuring routes.
 *
 * ## Example
 * See {@link ngRoute.$route#examples $route} for an example of configuring and using `ngRoute`.
 *
 * ## Dependencies
 * Requires the {@link ngRoute `ngRoute`} module to be installed.
 */
function $RouteProvider() {
  isArray = angular.isArray;
  isObject = angular.isObject;
  isDefined = angular.isDefined;
  noop = angular.noop;

  function inherit(parent, extra) {
    return angular.extend(Object.create(parent), extra);
  }

  var routes = {};

  /**
   * @ngdoc method
   * @name $routeProvider#when
   *
   * @param {string} path Route path (matched against `$location.path`). If `$location.path`
   *    contains redundant trailing slash or is missing one, the route will still match and the
   *    `$location.path` will be updated to add or drop the trailing slash to exactly match the
   *    route definition.
   *
   *    * `path` can contain named groups starting with a colon: e.g. `:name`. All characters up
   *        to the next slash are matched and stored in `$routeParams` under the given `name`
   *        when the route matches.
   *    * `path` can contain named groups starting with a colon and ending with a star:
   *        e.g.`:name*`. All characters are eagerly stored in `$routeParams` under the given `name`
   *        when the route matches.
   *    * `path` can contain optional named groups with a question mark: e.g.`:name?`.
   *
   *    For example, routes like `/color/:color/largecode/:largecode*\/edit` will match
   *    `/color/brown/largecode/code/with/slashes/edit` and extract:
   *
   *    * `color: brown`
   *    * `largecode: code/with/slashes`.
   *
   *
   * @param {Object} route Mapping information to be assigned to `$route.current` on route
   *    match.
   *
   *    Object properties:
   *
   *    - `controller` – `{(string|Function)=}` – Controller fn that should be associated with
   *      newly created scope or the name of a {@link angular.Module#controller registered
   *      controller} if passed as a string.
   *    - `controllerAs` – `{string=}` – An identifier name for a reference to the controller.
   *      If present, the controller will be published to scope under the `controllerAs` name.
   *    - `template` – `{(string|Function)=}` – html template as a string or a function that
   *      returns an html template as a string which should be used by {@link
   *      ngRoute.directive:ngView ngView} or {@link ng.directive:ngInclude ngInclude} directives.
   *      This property takes precedence over `templateUrl`.
   *
   *      If `template` is a function, it will be called with the following parameters:
   *
   *      - `{Array.<Object>}` - route parameters extracted from the current
   *        `$location.path()` by applying the current route
   *
   *      One of `template` or `templateUrl` is required.
   *
   *    - `templateUrl` – `{(string|Function)=}` – path or function that returns a path to an html
   *      template that should be used by {@link ngRoute.directive:ngView ngView}.
   *
   *      If `templateUrl` is a function, it will be called with the following parameters:
   *
   *      - `{Array.<Object>}` - route parameters extracted from the current
   *        `$location.path()` by applying the current route
   *
   *      One of `templateUrl` or `template` is required.
   *
   *    - `resolve` - `{Object.<string, Function>=}` - An optional map of dependencies which should
   *      be injected into the controller. If any of these dependencies are promises, the router
   *      will wait for them all to be resolved or one to be rejected before the controller is
   *      instantiated.
   *      If all the promises are resolved successfully, the values of the resolved promises are
   *      injected and {@link ngRoute.$route#$routeChangeSuccess $routeChangeSuccess} event is
   *      fired. If any of the promises are rejected the
   *      {@link ngRoute.$route#$routeChangeError $routeChangeError} event is fired.
   *      For easier access to the resolved dependencies from the template, the `resolve` map will
   *      be available on the scope of the route, under `$resolve` (by default) or a custom name
   *      specified by the `resolveAs` property (see below). This can be particularly useful, when
   *      working with {@link angular.Module#component components} as route templates.<br />
   *      <div class="alert alert-warning">
   *        **Note:** If your scope already contains a property with this name, it will be hidden
   *        or overwritten. Make sure, you specify an appropriate name for this property, that
   *        does not collide with other properties on the scope.
   *      </div>
   *      The map object is:
   *
   *      - `key` – `{string}`: a name of a dependency to be injected into the controller.
   *      - `factory` - `{string|Function}`: If `string` then it is an alias for a service.
   *        Otherwise if function, then it is {@link auto.$injector#invoke injected}
   *        and the return value is treated as the dependency. If the result is a promise, it is
   *        resolved before its value is injected into the controller. Be aware that
   *        `ngRoute.$routeParams` will still refer to the previous route within these resolve
   *        functions.  Use `$route.current.params` to access the new route parameters, instead.
   *
   *    - `resolveAs` - `{string=}` - The name under which the `resolve` map will be available on
   *      the scope of the route. If omitted, defaults to `$resolve`.
   *
   *    - `redirectTo` – `{(string|Function)=}` – value to update
   *      {@link ng.$location $location} path with and trigger route redirection.
   *
   *      If `redirectTo` is a function, it will be called with the following parameters:
   *
   *      - `{Object.<string>}` - route parameters extracted from the current
   *        `$location.path()` by applying the current route templateUrl.
   *      - `{string}` - current `$location.path()`
   *      - `{Object}` - current `$location.search()`
   *
   *      The custom `redirectTo` function is expected to return a string which will be used
   *      to update `$location.url()`. If the function throws an error, no further processing will
   *      take place and the {@link ngRoute.$route#$routeChangeError $routeChangeError} event will
   *      be fired.
   *
   *      Routes that specify `redirectTo` will not have their controllers, template functions
   *      or resolves called, the `$location` will be changed to the redirect url and route
   *      processing will stop. The exception to this is if the `redirectTo` is a function that
   *      returns `undefined`. In this case the route transition occurs as though there was no
   *      redirection.
   *
   *    - `resolveRedirectTo` – `{Function=}` – a function that will (eventually) return the value
   *      to update {@link ng.$location $location} URL with and trigger route redirection. In
   *      contrast to `redirectTo`, dependencies can be injected into `resolveRedirectTo` and the
   *      return value can be either a string or a promise that will be resolved to a string.
   *
   *      Similar to `redirectTo`, if the return value is `undefined` (or a promise that gets
   *      resolved to `undefined`), no redirection takes place and the route transition occurs as
   *      though there was no redirection.
   *
   *      If the function throws an error or the returned promise gets rejected, no further
   *      processing will take place and the
   *      {@link ngRoute.$route#$routeChangeError $routeChangeError} event will be fired.
   *
   *      `redirectTo` takes precedence over `resolveRedirectTo`, so specifying both on the same
   *      route definition, will cause the latter to be ignored.
   *
   *    - `[reloadOnUrl=true]` - `{boolean=}` - reload route when any part of the URL changes
   *      (including the path) even if the new URL maps to the same route.
   *
   *      If the option is set to `false` and the URL in the browser changes, but the new URL maps
   *      to the same route, then a `$routeUpdate` event is broadcasted on the root scope (without
   *      reloading the route).
   *
   *    - `[reloadOnSearch=true]` - `{boolean=}` - reload route when only `$location.search()`
   *      or `$location.hash()` changes.
   *
   *      If the option is set to `false` and the URL in the browser changes, then a `$routeUpdate`
   *      event is broadcasted on the root scope (without reloading the route).
   *
   *      <div class="alert alert-warning">
   *        **Note:** This option has no effect if `reloadOnUrl` is set to `false`.
   *      </div>
   *
   *    - `[caseInsensitiveMatch=false]` - `{boolean=}` - match routes without being case sensitive
   *
   *      If the option is set to `true`, then the particular route can be matched without being
   *      case sensitive
   *
   * @returns {Object} self
   *
   * @description
   * Adds a new route definition to the `$route` service.
   */
  this.when = function(path, route) {
    //copy original route object to preserve params inherited from proto chain
    var routeCopy = shallowCopy(route);
    if (angular.isUndefined(routeCopy.reloadOnUrl)) {
      routeCopy.reloadOnUrl = true;
    }
    if (angular.isUndefined(routeCopy.reloadOnSearch)) {
      routeCopy.reloadOnSearch = true;
    }
    if (angular.isUndefined(routeCopy.caseInsensitiveMatch)) {
      routeCopy.caseInsensitiveMatch = this.caseInsensitiveMatch;
    }
    routes[path] = angular.extend(
      routeCopy,
      {originalPath: path},
      path && routeToRegExp(path, routeCopy)
    );

    // create redirection for trailing slashes
    if (path) {
      var redirectPath = (path[path.length - 1] === '/')
            ? path.substr(0, path.length - 1)
            : path + '/';

      routes[redirectPath] = angular.extend(
        {originalPath: path, redirectTo: path},
        routeToRegExp(redirectPath, routeCopy)
      );
    }

    return this;
  };

  /**
   * @ngdoc property
   * @name $routeProvider#caseInsensitiveMatch
   * @description
   *
   * A boolean property indicating if routes defined
   * using this provider should be matched using a case insensitive
   * algorithm. Defaults to `false`.
   */
  this.caseInsensitiveMatch = false;

  /**
   * @ngdoc method
   * @name $routeProvider#otherwise
   *
   * @description
   * Sets route definition that will be used on route change when no other route definition
   * is matched.
   *
   * @param {Object|string} params Mapping information to be assigned to `$route.current`.
   * If called with a string, the value maps to `redirectTo`.
   * @returns {Object} self
   */
  this.otherwise = function(params) {
    if (typeof params === 'string') {
      params = {redirectTo: params};
    }
    this.when(null, params);
    return this;
  };

  /**
   * @ngdoc method
   * @name $routeProvider#eagerInstantiationEnabled
   * @kind function
   *
   * @description
   * Call this method as a setter to enable/disable eager instantiation of the
   * {@link ngRoute.$route $route} service upon application bootstrap. You can also call it as a
   * getter (i.e. without any arguments) to get the current value of the
   * `eagerInstantiationEnabled` flag.
   *
   * Instantiating `$route` early is necessary for capturing the initial
   * {@link ng.$location#$locationChangeStart $locationChangeStart} event and navigating to the
   * appropriate route. Usually, `$route` is instantiated in time by the
   * {@link ngRoute.ngView ngView} directive. Yet, in cases where `ngView` is included in an
   * asynchronously loaded template (e.g. in another directive's template), the directive factory
   * might not be called soon enough for `$route` to be instantiated _before_ the initial
   * `$locationChangeSuccess` event is fired. Eager instantiation ensures that `$route` is always
   * instantiated in time, regardless of when `ngView` will be loaded.
   *
   * The default value is true.
   *
   * **Note**:<br />
   * You may want to disable the default behavior when unit-testing modules that depend on
   * `ngRoute`, in order to avoid an unexpected request for the default route's template.
   *
   * @param {boolean=} enabled - If provided, update the internal `eagerInstantiationEnabled` flag.
   *
   * @returns {*} The current value of the `eagerInstantiationEnabled` flag if used as a getter or
   *     itself (for chaining) if used as a setter.
   */
  isEagerInstantiationEnabled = true;
  this.eagerInstantiationEnabled = function eagerInstantiationEnabled(enabled) {
    if (isDefined(enabled)) {
      isEagerInstantiationEnabled = enabled;
      return this;
    }

    return isEagerInstantiationEnabled;
  };


  this.$get = ['$rootScope',
               '$location',
               '$routeParams',
               '$q',
               '$injector',
               '$templateRequest',
               '$sce',
               '$browser',
      function($rootScope, $location, $routeParams, $q, $injector, $templateRequest, $sce, $browser) {

    /**
     * @ngdoc service
     * @name $route
     * @requires $location
     * @requires $routeParams
     *
     * @property {Object} current Reference to the current route definition.
     * The route definition contains:
     *
     *   - `controller`: The controller constructor as defined in the route definition.
     *   - `locals`: A map of locals which is used by {@link ng.$controller $controller} service for
     *     controller instantiation. The `locals` contain
     *     the resolved values of the `resolve` map. Additionally the `locals` also contain:
     *
     *     - `$scope` - The current route scope.
     *     - `$template` - The current route template HTML.
     *
     *     The `locals` will be assigned to the route scope's `$resolve` property. You can override
     *     the property name, using `resolveAs` in the route definition. See
     *     {@link ngRoute.$routeProvider $routeProvider} for more info.
     *
     * @property {Object} routes Object with all route configuration Objects as its properties.
     *
     * @description
     * `$route` is used for deep-linking URLs to controllers and views (HTML partials).
     * It watches `$location.url()` and tries to map the path to an existing route definition.
     *
     * Requires the {@link ngRoute `ngRoute`} module to be installed.
     *
     * You can define routes through {@link ngRoute.$routeProvider $routeProvider}'s API.
     *
     * The `$route` service is typically used in conjunction with the
     * {@link ngRoute.directive:ngView `ngView`} directive and the
     * {@link ngRoute.$routeParams `$routeParams`} service.
     *
     * @example
     * This example shows how changing the URL hash causes the `$route` to match a route against the
     * URL, and the `ngView` pulls in the partial.
     *
     * <example name="$route-service" module="ngRouteExample"
     *          deps="angular-route.js" fixBase="true">
     *   <file name="index.html">
     *     <div ng-controller="MainController">
     *       Choose:
     *       <a href="Book/Moby">Moby</a> |
     *       <a href="Book/Moby/ch/1">Moby: Ch1</a> |
     *       <a href="Book/Gatsby">Gatsby</a> |
     *       <a href="Book/Gatsby/ch/4?key=value">Gatsby: Ch4</a> |
     *       <a href="Book/Scarlet">Scarlet Letter</a><br/>
     *
     *       <div ng-view></div>
     *
     *       <hr />
     *
     *       <pre>$location.path() = {{$location.path()}}</pre>
     *       <pre>$route.current.templateUrl = {{$route.current.templateUrl}}</pre>
     *       <pre>$route.current.params = {{$route.current.params}}</pre>
     *       <pre>$route.current.scope.name = {{$route.current.scope.name}}</pre>
     *       <pre>$routeParams = {{$routeParams}}</pre>
     *     </div>
     *   </file>
     *
     *   <file name="book.html">
     *     controller: {{name}}<br />
     *     Book Id: {{params.bookId}}<br />
     *   </file>
     *
     *   <file name="chapter.html">
     *     controller: {{name}}<br />
     *     Book Id: {{params.bookId}}<br />
     *     Chapter Id: {{params.chapterId}}
     *   </file>
     *
     *   <file name="script.js">
     *     angular.module('ngRouteExample', ['ngRoute'])
     *
     *      .controller('MainController', function($scope, $route, $routeParams, $location) {
     *          $scope.$route = $route;
     *          $scope.$location = $location;
     *          $scope.$routeParams = $routeParams;
     *      })
     *
     *      .controller('BookController', function($scope, $routeParams) {
     *          $scope.name = 'BookController';
     *          $scope.params = $routeParams;
     *      })
     *
     *      .controller('ChapterController', function($scope, $routeParams) {
     *          $scope.name = 'ChapterController';
     *          $scope.params = $routeParams;
     *      })
     *
     *     .config(function($routeProvider, $locationProvider) {
     *       $routeProvider
     *        .when('/Book/:bookId', {
     *         templateUrl: 'book.html',
     *         controller: 'BookController',
     *         resolve: {
     *           // I will cause a 1 second delay
     *           delay: function($q, $timeout) {
     *             var delay = $q.defer();
     *             $timeout(delay.resolve, 1000);
     *             return delay.promise;
     *           }
     *         }
     *       })
     *       .when('/Book/:bookId/ch/:chapterId', {
     *         templateUrl: 'chapter.html',
     *         controller: 'ChapterController'
     *       });
     *
     *       // configure html5 to get links working on jsfiddle
     *       $locationProvider.html5Mode(true);
     *     });
     *
     *   </file>
     *
     *   <file name="protractor.js" type="protractor">
     *     it('should load and compile correct template', function() {
     *       element(by.linkText('Moby: Ch1')).click();
     *       var content = element(by.css('[ng-view]')).getText();
     *       expect(content).toMatch(/controller: ChapterController/);
     *       expect(content).toMatch(/Book Id: Moby/);
     *       expect(content).toMatch(/Chapter Id: 1/);
     *
     *       element(by.partialLinkText('Scarlet')).click();
     *
     *       content = element(by.css('[ng-view]')).getText();
     *       expect(content).toMatch(/controller: BookController/);
     *       expect(content).toMatch(/Book Id: Scarlet/);
     *     });
     *   </file>
     * </example>
     */

    /**
     * @ngdoc event
     * @name $route#$routeChangeStart
     * @eventType broadcast on root scope
     * @description
     * Broadcasted before a route change. At this  point the route services starts
     * resolving all of the dependencies needed for the route change to occur.
     * Typically this involves fetching the view template as well as any dependencies
     * defined in `resolve` route property. Once  all of the dependencies are resolved
     * `$routeChangeSuccess` is fired.
     *
     * The route change (and the `$location` change that triggered it) can be prevented
     * by calling `preventDefault` method of the event. See {@link ng.$rootScope.Scope#$on}
     * for more details about event object.
     *
     * @param {Object} angularEvent Synthetic event object.
     * @param {Route} next Future route information.
     * @param {Route} current Current route information.
     */

    /**
     * @ngdoc event
     * @name $route#$routeChangeSuccess
     * @eventType broadcast on root scope
     * @description
     * Broadcasted after a route change has happened successfully.
     * The `resolve` dependencies are now available in the `current.locals` property.
     *
     * {@link ngRoute.directive:ngView ngView} listens for the directive
     * to instantiate the controller and render the view.
     *
     * @param {Object} angularEvent Synthetic event object.
     * @param {Route} current Current route information.
     * @param {Route|Undefined} previous Previous route information, or undefined if current is
     * first route entered.
     */

    /**
     * @ngdoc event
     * @name $route#$routeChangeError
     * @eventType broadcast on root scope
     * @description
     * Broadcasted if a redirection function fails or any redirection or resolve promises are
     * rejected.
     *
     * @param {Object} angularEvent Synthetic event object
     * @param {Route} current Current route information.
     * @param {Route} previous Previous route information.
     * @param {Route} rejection The thrown error or the rejection reason of the promise. Usually
     * the rejection reason is the error that caused the promise to get rejected.
     */

    /**
     * @ngdoc event
     * @name $route#$routeUpdate
     * @eventType broadcast on root scope
     * @description
     * Broadcasted if the same instance of a route (including template, controller instance,
     * resolved dependencies, etc.) is being reused. This can happen if either `reloadOnSearch` or
     * `reloadOnUrl` has been set to `false`.
     *
     * @param {Object} angularEvent Synthetic event object
     * @param {Route} current Current/previous route information.
     */

    var forceReload = false,
        preparedRoute,
        preparedRouteIsUpdateOnly,
        $route = {
          routes: routes,

          /**
           * @ngdoc method
           * @name $route#reload
           *
           * @description
           * Causes `$route` service to reload the current route even if
           * {@link ng.$location $location} hasn't changed.
           *
           * As a result of that, {@link ngRoute.directive:ngView ngView}
           * creates new scope and reinstantiates the controller.
           */
          reload: function() {
            forceReload = true;

            var fakeLocationEvent = {
              defaultPrevented: false,
              preventDefault: function fakePreventDefault() {
                this.defaultPrevented = true;
                forceReload = false;
              }
            };

            $rootScope.$evalAsync(function() {
              prepareRoute(fakeLocationEvent);
              if (!fakeLocationEvent.defaultPrevented) commitRoute();
            });
          },

          /**
           * @ngdoc method
           * @name $route#updateParams
           *
           * @description
           * Causes `$route` service to update the current URL, replacing
           * current route parameters with those specified in `newParams`.
           * Provided property names that match the route's path segment
           * definitions will be interpolated into the location's path, while
           * remaining properties will be treated as query params.
           *
           * @param {!Object<string, string>} newParams mapping of URL parameter names to values
           */
          updateParams: function(newParams) {
            if (this.current && this.current.$$route) {
              newParams = angular.extend({}, this.current.params, newParams);
              $location.path(interpolate(this.current.$$route.originalPath, newParams));
              // interpolate modifies newParams, only query params are left
              $location.search(newParams);
            } else {
              throw $routeMinErr('norout', 'Tried updating route with no current route');
            }
          }
        };

    $rootScope.$on('$locationChangeStart', prepareRoute);
    $rootScope.$on('$locationChangeSuccess', commitRoute);

    return $route;

    /////////////////////////////////////////////////////

    /**
     * @param on {string} current url
     * @param route {Object} route regexp to match the url against
     * @return {?Object}
     *
     * @description
     * Check if the route matches the current url.
     *
     * Inspired by match in
     * visionmedia/express/lib/router/router.js.
     */
    function switchRouteMatcher(on, route) {
      var keys = route.keys,
          params = {};

      if (!route.regexp) return null;

      var m = route.regexp.exec(on);
      if (!m) return null;

      for (var i = 1, len = m.length; i < len; ++i) {
        var key = keys[i - 1];

        var val = m[i];

        if (key && val) {
          params[key.name] = val;
        }
      }
      return params;
    }

    function prepareRoute($locationEvent) {
      var lastRoute = $route.current;

      preparedRoute = parseRoute();
      preparedRouteIsUpdateOnly = isNavigationUpdateOnly(preparedRoute, lastRoute);

      if (!preparedRouteIsUpdateOnly && (lastRoute || preparedRoute)) {
        if ($rootScope.$broadcast('$routeChangeStart', preparedRoute, lastRoute).defaultPrevented) {
          if ($locationEvent) {
            $locationEvent.preventDefault();
          }
        }
      }
    }

    function commitRoute() {
      var lastRoute = $route.current;
      var nextRoute = preparedRoute;

      if (preparedRouteIsUpdateOnly) {
        lastRoute.params = nextRoute.params;
        angular.copy(lastRoute.params, $routeParams);
        $rootScope.$broadcast('$routeUpdate', lastRoute);
      } else if (nextRoute || lastRoute) {
        forceReload = false;
        $route.current = nextRoute;

        var nextRoutePromise = $q.resolve(nextRoute);

        $browser.$$incOutstandingRequestCount('$route');

        nextRoutePromise.
          then(getRedirectionData).
          then(handlePossibleRedirection).
          then(function(keepProcessingRoute) {
            return keepProcessingRoute && nextRoutePromise.
              then(resolveLocals).
              then(function(locals) {
                // after route change
                if (nextRoute === $route.current) {
                  if (nextRoute) {
                    nextRoute.locals = locals;
                    angular.copy(nextRoute.params, $routeParams);
                  }
                  $rootScope.$broadcast('$routeChangeSuccess', nextRoute, lastRoute);
                }
              });
          }).catch(function(error) {
            if (nextRoute === $route.current) {
              $rootScope.$broadcast('$routeChangeError', nextRoute, lastRoute, error);
            }
          }).finally(function() {
            // Because `commitRoute()` is called from a `$rootScope.$evalAsync` block (see
            // `$locationWatch`), this `$$completeOutstandingRequest()` call will not cause
            // `outstandingRequestCount` to hit zero.  This is important in case we are redirecting
            // to a new route which also requires some asynchronous work.

            $browser.$$completeOutstandingRequest(noop, '$route');
          });
      }
    }

    function getRedirectionData(route) {
      var data = {
        route: route,
        hasRedirection: false
      };

      if (route) {
        if (route.redirectTo) {
          if (angular.isString(route.redirectTo)) {
            data.path = interpolate(route.redirectTo, route.params);
            data.search = route.params;
            data.hasRedirection = true;
          } else {
            var oldPath = $location.path();
            var oldSearch = $location.search();
            var newUrl = route.redirectTo(route.pathParams, oldPath, oldSearch);

            if (angular.isDefined(newUrl)) {
              data.url = newUrl;
              data.hasRedirection = true;
            }
          }
        } else if (route.resolveRedirectTo) {
          return $q.
            resolve($injector.invoke(route.resolveRedirectTo)).
            then(function(newUrl) {
              if (angular.isDefined(newUrl)) {
                data.url = newUrl;
                data.hasRedirection = true;
              }

              return data;
            });
        }
      }

      return data;
    }

    function handlePossibleRedirection(data) {
      var keepProcessingRoute = true;

      if (data.route !== $route.current) {
        keepProcessingRoute = false;
      } else if (data.hasRedirection) {
        var oldUrl = $location.url();
        var newUrl = data.url;

        if (newUrl) {
          $location.
            url(newUrl).
            replace();
        } else {
          newUrl = $location.
            path(data.path).
            search(data.search).
            replace().
            url();
        }

        if (newUrl !== oldUrl) {
          // Exit out and don't process current next value,
          // wait for next location change from redirect
          keepProcessingRoute = false;
        }
      }

      return keepProcessingRoute;
    }

    function resolveLocals(route) {
      if (route) {
        var locals = angular.extend({}, route.resolve);
        angular.forEach(locals, function(value, key) {
          locals[key] = angular.isString(value) ?
              $injector.get(value) :
              $injector.invoke(value, null, null, key);
        });
        var template = getTemplateFor(route);
        if (angular.isDefined(template)) {
          locals['$template'] = template;
        }
        return $q.all(locals);
      }
    }

    function getTemplateFor(route) {
      var template, templateUrl;
      if (angular.isDefined(template = route.template)) {
        if (angular.isFunction(template)) {
          template = template(route.params);
        }
      } else if (angular.isDefined(templateUrl = route.templateUrl)) {
        if (angular.isFunction(templateUrl)) {
          templateUrl = templateUrl(route.params);
        }
        if (angular.isDefined(templateUrl)) {
          route.loadedTemplateUrl = $sce.valueOf(templateUrl);
          template = $templateRequest(templateUrl);
        }
      }
      return template;
    }

    /**
     * @returns {Object} the current active route, by matching it against the URL
     */
    function parseRoute() {
      // Match a route
      var params, match;
      angular.forEach(routes, function(route, path) {
        if (!match && (params = switchRouteMatcher($location.path(), route))) {
          match = inherit(route, {
            params: angular.extend({}, $location.search(), params),
            pathParams: params});
          match.$$route = route;
        }
      });
      // No route matched; fallback to "otherwise" route
      return match || routes[null] && inherit(routes[null], {params: {}, pathParams:{}});
    }

    /**
     * @param {Object} newRoute - The new route configuration (as returned by `parseRoute()`).
     * @param {Object} oldRoute - The previous route configuration (as returned by `parseRoute()`).
     * @returns {boolean} Whether this is an "update-only" navigation, i.e. the URL maps to the same
     *                    route and it can be reused (based on the config and the type of change).
     */
    function isNavigationUpdateOnly(newRoute, oldRoute) {
      // IF this is not a forced reload
      return !forceReload
          // AND both `newRoute`/`oldRoute` are defined
          && newRoute && oldRoute
          // AND they map to the same Route Definition Object
          && (newRoute.$$route === oldRoute.$$route)
          // AND `reloadOnUrl` is disabled
          && (!newRoute.reloadOnUrl
              // OR `reloadOnSearch` is disabled
              || (!newRoute.reloadOnSearch
                  // AND both routes have the same path params
                  && angular.equals(newRoute.pathParams, oldRoute.pathParams)
              )
          );
    }

    /**
     * @returns {string} interpolation of the redirect path with the parameters
     */
    function interpolate(string, params) {
      var result = [];
      angular.forEach((string || '').split(':'), function(segment, i) {
        if (i === 0) {
          result.push(segment);
        } else {
          var segmentMatch = segment.match(/(\w+)(?:[?*])?(.*)/);
          var key = segmentMatch[1];
          result.push(params[key]);
          result.push(segmentMatch[2] || '');
          delete params[key];
        }
      });
      return result.join('');
    }
  }];
}

instantiateRoute.$inject = ['$injector'];
function instantiateRoute($injector) {
  if (isEagerInstantiationEnabled) {
    // Instantiate `$route`
    $injector.get('$route');
  }
}

ngRouteModule.provider('$routeParams', $RouteParamsProvider);


/**
 * @ngdoc service
 * @name $routeParams
 * @requires $route
 * @this
 *
 * @description
 * The `$routeParams` service allows you to retrieve the current set of route parameters.
 *
 * Requires the {@link ngRoute `ngRoute`} module to be installed.
 *
 * The route parameters are a combination of {@link ng.$location `$location`}'s
 * {@link ng.$location#search `search()`} and {@link ng.$location#path `path()`}.
 * The `path` parameters are extracted when the {@link ngRoute.$route `$route`} path is matched.
 *
 * In case of parameter name collision, `path` params take precedence over `search` params.
 *
 * The service guarantees that the identity of the `$routeParams` object will remain unchanged
 * (but its properties will likely change) even when a route change occurs.
 *
 * Note that the `$routeParams` are only updated *after* a route change completes successfully.
 * This means that you cannot rely on `$routeParams` being correct in route resolve functions.
 * Instead you can use `$route.current.params` to access the new route's parameters.
 *
 * @example
 * ```js
 *  // Given:
 *  // URL: http://server.com/index.html#/Chapter/1/Section/2?search=moby
 *  // Route: /Chapter/:chapterId/Section/:sectionId
 *  //
 *  // Then
 *  $routeParams ==> {chapterId:'1', sectionId:'2', search:'moby'}
 * ```
 */
function $RouteParamsProvider() {
  this.$get = function() { return {}; };
}

ngRouteModule.directive('ngView', ngViewFactory);
ngRouteModule.directive('ngView', ngViewFillContentFactory);


/**
 * @ngdoc directive
 * @name ngView
 * @restrict ECA
 *
 * @description
 * `ngView` is a directive that complements the {@link ngRoute.$route $route} service by
 * including the rendered template of the current route into the main layout (`index.html`) file.
 * Every time the current route changes, the included view changes with it according to the
 * configuration of the `$route` service.
 *
 * Requires the {@link ngRoute `ngRoute`} module to be installed.
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | when the new element is inserted to the DOM |
 * | {@link ng.$animate#leave leave}  | when the old element is removed from to the DOM  |
 *
 * The enter and leave animation occur concurrently.
 *
 * @scope
 * @priority 400
 * @param {string=} onload Expression to evaluate whenever the view updates.
 *
 * @param {string=} autoscroll Whether `ngView` should call {@link ng.$anchorScroll
 *                  $anchorScroll} to scroll the viewport after the view is updated.
 *
 *                  - If the attribute is not set, disable scrolling.
 *                  - If the attribute is set without value, enable scrolling.
 *                  - Otherwise enable scrolling only if the `autoscroll` attribute value evaluated
 *                    as an expression yields a truthy value.
 * @example
    <example name="ngView-directive" module="ngViewExample"
             deps="angular-route.js;angular-animate.js"
             animations="true" fixBase="true">
      <file name="index.html">
        <div ng-controller="MainCtrl as main">
          Choose:
          <a href="Book/Moby">Moby</a> |
          <a href="Book/Moby/ch/1">Moby: Ch1</a> |
          <a href="Book/Gatsby">Gatsby</a> |
          <a href="Book/Gatsby/ch/4?key=value">Gatsby: Ch4</a> |
          <a href="Book/Scarlet">Scarlet Letter</a><br/>

          <div class="view-animate-container">
            <div ng-view class="view-animate"></div>
          </div>
          <hr />

          <pre>$location.path() = {{main.$location.path()}}</pre>
          <pre>$route.current.templateUrl = {{main.$route.current.templateUrl}}</pre>
          <pre>$route.current.params = {{main.$route.current.params}}</pre>
          <pre>$routeParams = {{main.$routeParams}}</pre>
        </div>
      </file>

      <file name="book.html">
        <div>
          controller: {{book.name}}<br />
          Book Id: {{book.params.bookId}}<br />
        </div>
      </file>

      <file name="chapter.html">
        <div>
          controller: {{chapter.name}}<br />
          Book Id: {{chapter.params.bookId}}<br />
          Chapter Id: {{chapter.params.chapterId}}
        </div>
      </file>

      <file name="animations.css">
        .view-animate-container {
          position:relative;
          height:100px!important;
          background:white;
          border:1px solid black;
          height:40px;
          overflow:hidden;
        }

        .view-animate {
          padding:10px;
        }

        .view-animate.ng-enter, .view-animate.ng-leave {
          transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 1.5s;

          display:block;
          width:100%;
          border-left:1px solid black;

          position:absolute;
          top:0;
          left:0;
          right:0;
          bottom:0;
          padding:10px;
        }

        .view-animate.ng-enter {
          left:100%;
        }
        .view-animate.ng-enter.ng-enter-active {
          left:0;
        }
        .view-animate.ng-leave.ng-leave-active {
          left:-100%;
        }
      </file>

      <file name="script.js">
        angular.module('ngViewExample', ['ngRoute', 'ngAnimate'])
          .config(['$routeProvider', '$locationProvider',
            function($routeProvider, $locationProvider) {
              $routeProvider
                .when('/Book/:bookId', {
                  templateUrl: 'book.html',
                  controller: 'BookCtrl',
                  controllerAs: 'book'
                })
                .when('/Book/:bookId/ch/:chapterId', {
                  templateUrl: 'chapter.html',
                  controller: 'ChapterCtrl',
                  controllerAs: 'chapter'
                });

              $locationProvider.html5Mode(true);
          }])
          .controller('MainCtrl', ['$route', '$routeParams', '$location',
            function MainCtrl($route, $routeParams, $location) {
              this.$route = $route;
              this.$location = $location;
              this.$routeParams = $routeParams;
          }])
          .controller('BookCtrl', ['$routeParams', function BookCtrl($routeParams) {
            this.name = 'BookCtrl';
            this.params = $routeParams;
          }])
          .controller('ChapterCtrl', ['$routeParams', function ChapterCtrl($routeParams) {
            this.name = 'ChapterCtrl';
            this.params = $routeParams;
          }]);

      </file>

      <file name="protractor.js" type="protractor">
        it('should load and compile correct template', function() {
          element(by.linkText('Moby: Ch1')).click();
          var content = element(by.css('[ng-view]')).getText();
          expect(content).toMatch(/controller: ChapterCtrl/);
          expect(content).toMatch(/Book Id: Moby/);
          expect(content).toMatch(/Chapter Id: 1/);

          element(by.partialLinkText('Scarlet')).click();

          content = element(by.css('[ng-view]')).getText();
          expect(content).toMatch(/controller: BookCtrl/);
          expect(content).toMatch(/Book Id: Scarlet/);
        });
      </file>
    </example>
 */


/**
 * @ngdoc event
 * @name ngView#$viewContentLoaded
 * @eventType emit on the current ngView scope
 * @description
 * Emitted every time the ngView content is reloaded.
 */
ngViewFactory.$inject = ['$route', '$anchorScroll', '$animate'];
function ngViewFactory($route, $anchorScroll, $animate) {
  return {
    restrict: 'ECA',
    terminal: true,
    priority: 400,
    transclude: 'element',
    link: function(scope, $element, attr, ctrl, $transclude) {
        var currentScope,
            currentElement,
            previousLeaveAnimation,
            autoScrollExp = attr.autoscroll,
            onloadExp = attr.onload || '';

        scope.$on('$routeChangeSuccess', update);
        update();

        function cleanupLastView() {
          if (previousLeaveAnimation) {
            $animate.cancel(previousLeaveAnimation);
            previousLeaveAnimation = null;
          }

          if (currentScope) {
            currentScope.$destroy();
            currentScope = null;
          }
          if (currentElement) {
            previousLeaveAnimation = $animate.leave(currentElement);
            previousLeaveAnimation.done(function(response) {
              if (response !== false) previousLeaveAnimation = null;
            });
            currentElement = null;
          }
        }

        function update() {
          var locals = $route.current && $route.current.locals,
              template = locals && locals.$template;

          if (angular.isDefined(template)) {
            var newScope = scope.$new();
            var current = $route.current;

            // Note: This will also link all children of ng-view that were contained in the original
            // html. If that content contains controllers, ... they could pollute/change the scope.
            // However, using ng-view on an element with additional content does not make sense...
            // Note: We can't remove them in the cloneAttchFn of $transclude as that
            // function is called before linking the content, which would apply child
            // directives to non existing elements.
            var clone = $transclude(newScope, function(clone) {
              $animate.enter(clone, null, currentElement || $element).done(function onNgViewEnter(response) {
                if (response !== false && angular.isDefined(autoScrollExp)
                  && (!autoScrollExp || scope.$eval(autoScrollExp))) {
                  $anchorScroll();
                }
              });
              cleanupLastView();
            });

            currentElement = clone;
            currentScope = current.scope = newScope;
            currentScope.$emit('$viewContentLoaded');
            currentScope.$eval(onloadExp);
          } else {
            cleanupLastView();
          }
        }
    }
  };
}

// This directive is called during the $transclude call of the first `ngView` directive.
// It will replace and compile the content of the element with the loaded template.
// We need this directive so that the element content is already filled when
// the link function of another directive on the same element as ngView
// is called.
ngViewFillContentFactory.$inject = ['$compile', '$controller', '$route'];
function ngViewFillContentFactory($compile, $controller, $route) {
  return {
    restrict: 'ECA',
    priority: -400,
    link: function(scope, $element) {
      var current = $route.current,
          locals = current.locals;

      $element.html(locals.$template);

      var link = $compile($element.contents());

      if (current.controller) {
        locals.$scope = scope;
        var controller = $controller(current.controller, locals);
        if (current.controllerAs) {
          scope[current.controllerAs] = controller;
        }
        $element.data('$ngControllerController', controller);
        $element.children().data('$ngControllerController', controller);
      }
      scope[current.resolveAs || '$resolve'] = locals;

      link(scope);
    }
  };
}


})(window, window.angular);

})();
(function () {
/**
 * @license AngularJS v1.8.1
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

var $resourceMinErr = angular.$$minErr('$resource');

// Helper functions and regex to lookup a dotted path on an object
// stopping at undefined/null.  The path must be composed of ASCII
// identifiers (just like $parse)
var MEMBER_NAME_REGEX = /^(\.[a-zA-Z_$@][0-9a-zA-Z_$@]*)+$/;

function isValidDottedPath(path) {
  return (path != null && path !== '' && path !== 'hasOwnProperty' &&
      MEMBER_NAME_REGEX.test('.' + path));
}

function lookupDottedPath(obj, path) {
  if (!isValidDottedPath(path)) {
    throw $resourceMinErr('badmember', 'Dotted member path "@{0}" is invalid.', path);
  }
  var keys = path.split('.');
  for (var i = 0, ii = keys.length; i < ii && angular.isDefined(obj); i++) {
    var key = keys[i];
    obj = (obj !== null) ? obj[key] : undefined;
  }
  return obj;
}

/**
 * Create a shallow copy of an object and clear other fields from the destination
 */
function shallowClearAndCopy(src, dst) {
  dst = dst || {};

  angular.forEach(dst, function(value, key) {
    delete dst[key];
  });

  for (var key in src) {
    if (src.hasOwnProperty(key) && !(key.charAt(0) === '$' && key.charAt(1) === '$')) {
      dst[key] = src[key];
    }
  }

  return dst;
}

/**
 * @ngdoc module
 * @name ngResource
 * @description
 *
 * The `ngResource` module provides interaction support with RESTful services
 * via the $resource service.
 *
 * See {@link ngResource.$resourceProvider} and {@link ngResource.$resource} for usage.
 */

/**
 * @ngdoc provider
 * @name $resourceProvider
 *
 * @description
 *
 * Use `$resourceProvider` to change the default behavior of the {@link ngResource.$resource}
 * service.
 *
 * ## Dependencies
 * Requires the {@link ngResource } module to be installed.
 *
 */

/**
 * @ngdoc service
 * @name $resource
 * @requires $http
 * @requires ng.$log
 * @requires $q
 * @requires ng.$timeout
 *
 * @description
 * A factory which creates a resource object that lets you interact with
 * [RESTful](http://en.wikipedia.org/wiki/Representational_State_Transfer) server-side data sources.
 *
 * The returned resource object has action methods which provide high-level behaviors without
 * the need to interact with the low level {@link ng.$http $http} service.
 *
 * Requires the {@link ngResource `ngResource`} module to be installed.
 *
 * By default, trailing slashes will be stripped from the calculated URLs,
 * which can pose problems with server backends that do not expect that
 * behavior.  This can be disabled by configuring the `$resourceProvider` like
 * this:
 *
 * ```js
     app.config(['$resourceProvider', function($resourceProvider) {
       // Don't strip trailing slashes from calculated URLs
       $resourceProvider.defaults.stripTrailingSlashes = false;
     }]);
 * ```
 *
 * @param {string} url A parameterized URL template with parameters prefixed by `:` as in
 *   `/user/:username`. If you are using a URL with a port number (e.g.
 *   `http://example.com:8080/api`), it will be respected.
 *
 *   If you are using a url with a suffix, just add the suffix, like this:
 *   `$resource('http://example.com/resource.json')` or `$resource('http://example.com/:id.json')`
 *   or even `$resource('http://example.com/resource/:resource_id.:format')`
 *   If the parameter before the suffix is empty, :resource_id in this case, then the `/.` will be
 *   collapsed down to a single `.`.  If you need this sequence to appear and not collapse then you
 *   can escape it with `/\.`.
 *
 * @param {Object=} paramDefaults Default values for `url` parameters. These can be overridden in
 *   `actions` methods. If a parameter value is a function, it will be called every time
 *   a param value needs to be obtained for a request (unless the param was overridden). The
 *   function will be passed the current data value as an argument.
 *
 *   Each key value in the parameter object is first bound to url template if present and then any
 *   excess keys are appended to the url search query after the `?`.
 *
 *   Given a template `/path/:verb` and parameter `{verb: 'greet', salutation: 'Hello'}` results in
 *   URL `/path/greet?salutation=Hello`.
 *
 *   If the parameter value is prefixed with `@`, then the value for that parameter will be
 *   extracted from the corresponding property on the `data` object (provided when calling actions
 *   with a request body).
 *   For example, if the `defaultParam` object is `{someParam: '@someProp'}` then the value of
 *   `someParam` will be `data.someProp`.
 *   Note that the parameter will be ignored, when calling a "GET" action method (i.e. an action
 *   method that does not accept a request body).
 *
 * @param {Object.<Object>=} actions Hash with declaration of custom actions that will be available
 *   in addition to the default set of resource actions (see below). If a custom action has the same
 *   key as a default action (e.g. `save`), then the default action will be *overwritten*, and not
 *   extended.
 *
 *   The declaration should be created in the format of {@link ng.$http#usage $http.config}:
 *
 *       {
 *         action1: {method:?, params:?, isArray:?, headers:?, ...},
 *         action2: {method:?, params:?, isArray:?, headers:?, ...},
 *         ...
 *       }
 *
 *   Where:
 *
 *   - **`action`** – {string} – The name of action. This name becomes the name of the method on
 *     your resource object.
 *   - **`method`** – {string} – Case insensitive HTTP method (e.g. `GET`, `POST`, `PUT`,
 *     `DELETE`, `JSONP`, etc).
 *   - **`params`** – {Object=} – Optional set of pre-bound parameters for this action. If any of
 *     the parameter value is a function, it will be called every time when a param value needs to
 *     be obtained for a request (unless the param was overridden). The function will be passed the
 *     current data value as an argument.
 *   - **`url`** – {string} – Action specific `url` override. The url templating is supported just
 *     like for the resource-level urls.
 *   - **`isArray`** – {boolean=} – If true then the returned object for this action is an array,
 *     see `returns` section.
 *   - **`transformRequest`** –
 *     `{function(data, headersGetter)|Array.<function(data, headersGetter)>}` –
 *     Transform function or an array of such functions. The transform function takes the http
 *     request body and headers and returns its transformed (typically serialized) version.
 *     By default, transformRequest will contain one function that checks if the request data is
 *     an object and serializes it using `angular.toJson`. To prevent this behavior, set
 *     `transformRequest` to an empty array: `transformRequest: []`
 *   - **`transformResponse`** –
 *     `{function(data, headersGetter, status)|Array.<function(data, headersGetter, status)>}` –
 *     Transform function or an array of such functions. The transform function takes the HTTP
 *     response body, headers and status and returns its transformed (typically deserialized)
 *     version.
 *     By default, transformResponse will contain one function that checks if the response looks
 *     like a JSON string and deserializes it using `angular.fromJson`. To prevent this behavior,
 *     set `transformResponse` to an empty array: `transformResponse: []`
 *   - **`cache`** – `{boolean|Cache}` – A boolean value or object created with
 *     {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of the HTTP response.
 *     See {@link $http#caching $http Caching} for more information.
 *   - **`timeout`** – `{number}` – Timeout in milliseconds.<br />
 *     **Note:** In contrast to {@link ng.$http#usage $http.config}, {@link ng.$q promises} are
 *     **not** supported in `$resource`, because the same value would be used for multiple requests.
 *     If you are looking for a way to cancel requests, you should use the `cancellable` option.
 *   - **`cancellable`** – `{boolean}` – If true, the request made by a "non-instance" call will be
 *     cancelled (if not already completed) by calling `$cancelRequest()` on the call's return
 *     value. Calling `$cancelRequest()` for a non-cancellable or an already completed/cancelled
 *     request will have no effect.
 *   - **`withCredentials`** – `{boolean}` – Whether to set the `withCredentials` flag on the
 *     XHR object. See
 *     [XMLHttpRequest.withCredentials](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials)
 *     for more information.
 *   - **`responseType`** – `{string}` – See
 *     [XMLHttpRequest.responseType](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType).
 *   - **`interceptor`** – `{Object=}` – The interceptor object has four optional methods -
 *     `request`, `requestError`, `response`, and `responseError`. See
 *     {@link ng.$http#interceptors $http interceptors} for details. Note that
 *     `request`/`requestError` interceptors are applied before calling `$http`, thus before any
 *     global `$http` interceptors. Also, rejecting or throwing an error inside the `request`
 *     interceptor will result in calling the `responseError` interceptor.
 *     The resource instance or collection is available on the `resource` property of the
 *     `http response` object passed to `response`/`responseError` interceptors.
 *     Keep in mind that the associated promise will be resolved with the value returned by the
 *     response interceptors. Make sure you return an appropriate value and not the `response`
 *     object passed as input. For reference, the default `response` interceptor (which gets applied
 *     if you don't specify a custom one) returns `response.resource`.<br />
 *     See {@link ngResource.$resource#using-interceptors below} for an example of using
 *     interceptors in `$resource`.
 *   - **`hasBody`** – `{boolean}` – If true, then the request will have a body.
 *     If not specified, then only POST, PUT and PATCH requests will have a body. *
 * @param {Object} options Hash with custom settings that should extend the
 *   default `$resourceProvider` behavior.  The supported options are:
 *
 *   - **`stripTrailingSlashes`** – {boolean} – If true then the trailing
 *   slashes from any calculated URL will be stripped. (Defaults to true.)
 *   - **`cancellable`** – {boolean} – If true, the request made by a "non-instance" call will be
 *   cancelled (if not already completed) by calling `$cancelRequest()` on the call's return value.
 *   This can be overwritten per action. (Defaults to false.)
 *
 * @returns {Object} A resource "class" object with methods for the default set of resource actions
 *   optionally extended with custom `actions`. The default set contains these actions:
 *   ```js
 *   {
 *     'get':    {method: 'GET'},
 *     'save':   {method: 'POST'},
 *     'query':  {method: 'GET', isArray: true},
 *     'remove': {method: 'DELETE'},
 *     'delete': {method: 'DELETE'}
 *   }
 *   ```
 *
 *   Calling these methods invoke {@link ng.$http} with the specified http method, destination and
 *   parameters. When the data is returned from the server then the object is an instance of the
 *   resource class. The actions `save`, `remove` and `delete` are available on it as methods with
 *   the `$` prefix. This allows you to easily perform CRUD operations (create, read, update,
 *   delete) on server-side data like this:
 *   ```js
 *   var User = $resource('/user/:userId', {userId: '@id'});
 *   User.get({userId: 123}).$promise.then(function(user) {
 *     user.abc = true;
 *     user.$save();
 *   });
 *   ```
 *
 *   It is important to realize that invoking a `$resource` object method immediately returns an
 *   empty reference (object or array depending on `isArray`). Once the data is returned from the
 *   server the existing reference is populated with the actual data. This is a useful trick since
 *   usually the resource is assigned to a model which is then rendered by the view. Having an empty
 *   object results in no rendering, once the data arrives from the server then the object is
 *   populated with the data and the view automatically re-renders itself showing the new data. This
 *   means that in most cases one never has to write a callback function for the action methods.
 *
 *   The action methods on the class object or instance object can be invoked with the following
 *   parameters:
 *
 *   - "class" actions without a body: `Resource.action([parameters], [success], [error])`
 *   - "class" actions with a body: `Resource.action([parameters], postData, [success], [error])`
 *   - instance actions: `instance.$action([parameters], [success], [error])`
 *
 *
 *   When calling instance methods, the instance itself is used as the request body (if the action
 *   should have a body). By default, only actions using `POST`, `PUT` or `PATCH` have request
 *   bodies, but you can use the `hasBody` configuration option to specify whether an action
 *   should have a body or not (regardless of its HTTP method).
 *
 *
 *   Success callback is called with (value (Object|Array), responseHeaders (Function),
 *   status (number), statusText (string)) arguments, where `value` is the populated resource
 *   instance or collection object. The error callback is called with (httpResponse) argument.
 *
 *   Class actions return an empty instance (with the additional properties listed below).
 *   Instance actions return a promise for the operation.
 *
 *   The Resource instances and collections have these additional properties:
 *
 *   - `$promise`: The {@link ng.$q promise} of the original server interaction that created this
 *     instance or collection.
 *
 *     On success, the promise is resolved with the same resource instance or collection object,
 *     updated with data from server. This makes it easy to use in the
 *     {@link ngRoute.$routeProvider `resolve` section of `$routeProvider.when()`} to defer view
 *     rendering until the resource(s) are loaded.
 *
 *     On failure, the promise is rejected with the {@link ng.$http http response} object.
 *
 *     If an interceptor object was provided, the promise will instead be resolved with the value
 *     returned by the response interceptor (on success) or responceError interceptor (on failure).
 *
 *   - `$resolved`: `true` after first server interaction is completed (either with success or
 *      rejection), `false` before that. Knowing if the Resource has been resolved is useful in
 *      data-binding. If there is a response/responseError interceptor and it returns a promise,
 *      `$resolved` will wait for that too.
 *
 *   The Resource instances and collections have these additional methods:
 *
 *   - `$cancelRequest`: If there is a cancellable, pending request related to the instance or
 *      collection, calling this method will abort the request.
 *
 *   The Resource instances have these additional methods:
 *
 *   - `toJSON`: It returns a simple object without any of the extra properties added as part of
 *     the Resource API. This object can be serialized through {@link angular.toJson} safely
 *     without attaching AngularJS-specific fields. Notice that `JSON.stringify` (and
 *     `angular.toJson`) automatically use this method when serializing a Resource instance
 *     (see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#toJSON%28%29_behavior)).
 *
 * @example
 *
 * ### Basic usage
 *
   ```js
     // Define a CreditCard class
     var CreditCard = $resource('/users/:userId/cards/:cardId',
       {userId: 123, cardId: '@id'}, {
         charge: {method: 'POST', params: {charge: true}}
       });

     // We can retrieve a collection from the server
     var cards = CreditCard.query();
         // GET: /users/123/cards
         // server returns: [{id: 456, number: '1234', name: 'Smith'}]

     // Wait for the request to complete
     cards.$promise.then(function() {
       var card = cards[0];

       // Each item is an instance of CreditCard
       expect(card instanceof CreditCard).toEqual(true);

       // Non-GET methods are mapped onto the instances
       card.name = 'J. Smith';
       card.$save();
           // POST: /users/123/cards/456 {id: 456, number: '1234', name: 'J. Smith'}
           // server returns: {id: 456, number: '1234', name: 'J. Smith'}

       // Our custom method is mapped as well (since it uses POST)
       card.$charge({amount: 9.99});
           // POST: /users/123/cards/456?amount=9.99&charge=true {id: 456, number: '1234', name: 'J. Smith'}
     });

     // We can create an instance as well
     var newCard = new CreditCard({number: '0123'});
     newCard.name = 'Mike Smith';

     var savePromise = newCard.$save();
         // POST: /users/123/cards {number: '0123', name: 'Mike Smith'}
         // server returns: {id: 789, number: '0123', name: 'Mike Smith'}

     savePromise.then(function() {
       // Once the promise is resolved, the created instance
       // is populated with the data returned by the server
       expect(newCard.id).toEqual(789);
     });
   ```
 *
 * The object returned from a call to `$resource` is a resource "class" which has one "static"
 * method for each action in the definition.
 *
 * Calling these methods invokes `$http` on the `url` template with the given HTTP `method`,
 * `params` and `headers`.
 *
 * @example
 *
 * ### Accessing the response
 *
 * When the data is returned from the server then the object is an instance of the resource type and
 * all of the non-GET methods are available with `$` prefix. This allows you to easily support CRUD
 * operations (create, read, update, delete) on server-side data.
 *
   ```js
     var User = $resource('/users/:userId', {userId: '@id'});
     User.get({userId: 123}).$promise.then(function(user) {
       user.abc = true;
       user.$save();
     });
   ```
 *
 * It's worth noting that the success callback for `get`, `query` and other methods gets called with
 * the resource instance (populated with the data that came from the server) as well as an `$http`
 * header getter function, the HTTP status code and the response status text. So one could rewrite
 * the above example and get access to HTTP headers as follows:
 *
   ```js
     var User = $resource('/users/:userId', {userId: '@id'});
     User.get({userId: 123}, function(user, getResponseHeaders) {
       user.abc = true;
       user.$save(function(user, putResponseHeaders) {
         // `user` => saved `User` object
         // `putResponseHeaders` => `$http` header getter
       });
     });
   ```
 *
 * @example
 *
 * ### Creating custom actions
 *
 * In this example we create a custom method on our resource to make a PUT request:
 *
   ```js
      var app = angular.module('app', ['ngResource']);

      // Some APIs expect a PUT request in the format URL/object/ID
      // Here we are creating an 'update' method
      app.factory('Notes', ['$resource', function($resource) {
        return $resource('/notes/:id', {id: '@id'}, {
          update: {method: 'PUT'}
        });
      }]);

      // In our controller we get the ID from the URL using `$location`
      app.controller('NotesCtrl', ['$location', 'Notes', function($location, Notes) {
        // First, retrieve the corresponding `Note` object from the server
        // (Assuming a URL of the form `.../notes?id=XYZ`)
        var noteId = $location.search().id;
        var note = Notes.get({id: noteId});

        note.$promise.then(function() {
          note.content = 'Hello, world!';

          // Now call `update` to save the changes on the server
          Notes.update(note);
              // This will PUT /notes/ID with the note object as the request payload

          // Since `update` is a non-GET method, it will also be available on the instance
          // (prefixed with `$`), so we could replace the `Note.update()` call with:
          //note.$update();
        });
      }]);
   ```
 *
 * @example
 *
 * ### Cancelling requests
 *
 * If an action's configuration specifies that it is cancellable, you can cancel the request related
 * to an instance or collection (as long as it is a result of a "non-instance" call):
 *
   ```js
     // ...defining the `Hotel` resource...
     var Hotel = $resource('/api/hotels/:id', {id: '@id'}, {
       // Let's make the `query()` method cancellable
       query: {method: 'get', isArray: true, cancellable: true}
     });

     // ...somewhere in the PlanVacationController...
     ...
     this.onDestinationChanged = function onDestinationChanged(destination) {
       // We don't care about any pending request for hotels
       // in a different destination any more
       if (this.availableHotels) {
         this.availableHotels.$cancelRequest();
       }

       // Let's query for hotels in `destination`
       // (calls: /api/hotels?location=<destination>)
       this.availableHotels = Hotel.query({location: destination});
     };
   ```
 *
 * @example
 *
 * ### Using interceptors
 *
 * You can use interceptors to transform the request or response, perform additional operations, and
 * modify the returned instance/collection. The following example, uses `request` and `response`
 * interceptors to augment the returned instance with additional info:
 *
   ```js
     var Thing = $resource('/api/things/:id', {id: '@id'}, {
       save: {
         method: 'POST',
         interceptor: {
           request: function(config) {
             // Before the request is sent out, store a timestamp on the request config
             config.requestTimestamp = Date.now();
             return config;
           },
           response: function(response) {
             // Get the instance from the response object
             var instance = response.resource;

             // Augment the instance with a custom `saveLatency` property, computed as the time
             // between sending the request and receiving the response.
             instance.saveLatency = Date.now() - response.config.requestTimestamp;

             // Return the instance
             return instance;
           }
         }
       }
     });

     Thing.save({foo: 'bar'}).$promise.then(function(thing) {
       console.log('That thing was saved in ' + thing.saveLatency + 'ms.');
     });
   ```
 *
 */
angular.module('ngResource', ['ng']).
  info({ angularVersion: '1.8.1' }).
  provider('$resource', function ResourceProvider() {
    var PROTOCOL_AND_IPV6_REGEX = /^https?:\/\/\[[^\]]*][^/]*/;

    var provider = this;

    /**
     * @ngdoc property
     * @name $resourceProvider#defaults
     * @description
     * Object containing default options used when creating `$resource` instances.
     *
     * The default values satisfy a wide range of usecases, but you may choose to overwrite any of
     * them to further customize your instances. The available properties are:
     *
     * - **stripTrailingSlashes** – `{boolean}` – If true, then the trailing slashes from any
     *   calculated URL will be stripped.<br />
     *   (Defaults to true.)
     * - **cancellable** – `{boolean}` – If true, the request made by a "non-instance" call will be
     *   cancelled (if not already completed) by calling `$cancelRequest()` on the call's return
     *   value. For more details, see {@link ngResource.$resource}. This can be overwritten per
     *   resource class or action.<br />
     *   (Defaults to false.)
     * - **actions** - `{Object.<Object>}` - A hash with default actions declarations. Actions are
     *   high-level methods corresponding to RESTful actions/methods on resources. An action may
     *   specify what HTTP method to use, what URL to hit, if the return value will be a single
     *   object or a collection (array) of objects etc. For more details, see
     *   {@link ngResource.$resource}. The actions can also be enhanced or overwritten per resource
     *   class.<br />
     *   The default actions are:
     *   ```js
     *   {
     *     get: {method: 'GET'},
     *     save: {method: 'POST'},
     *     query: {method: 'GET', isArray: true},
     *     remove: {method: 'DELETE'},
     *     delete: {method: 'DELETE'}
     *   }
     *   ```
     *
     * #### Example
     *
     * For example, you can specify a new `update` action that uses the `PUT` HTTP verb:
     *
     * ```js
     *   angular.
     *     module('myApp').
     *     config(['$resourceProvider', function ($resourceProvider) {
     *       $resourceProvider.defaults.actions.update = {
     *         method: 'PUT'
     *       };
     *     }]);
     * ```
     *
     * Or you can even overwrite the whole `actions` list and specify your own:
     *
     * ```js
     *   angular.
     *     module('myApp').
     *     config(['$resourceProvider', function ($resourceProvider) {
     *       $resourceProvider.defaults.actions = {
     *         create: {method: 'POST'},
     *         get:    {method: 'GET'},
     *         getAll: {method: 'GET', isArray:true},
     *         update: {method: 'PUT'},
     *         delete: {method: 'DELETE'}
     *       };
     *     });
     * ```
     *
     */
    this.defaults = {
      // Strip slashes by default
      stripTrailingSlashes: true,

      // Make non-instance requests cancellable (via `$cancelRequest()`)
      cancellable: false,

      // Default actions configuration
      actions: {
        'get': {method: 'GET'},
        'save': {method: 'POST'},
        'query': {method: 'GET', isArray: true},
        'remove': {method: 'DELETE'},
        'delete': {method: 'DELETE'}
      }
    };

    this.$get = ['$http', '$log', '$q', '$timeout', function($http, $log, $q, $timeout) {

      var noop = angular.noop,
          forEach = angular.forEach,
          extend = angular.extend,
          copy = angular.copy,
          isArray = angular.isArray,
          isDefined = angular.isDefined,
          isFunction = angular.isFunction,
          isNumber = angular.isNumber,
          encodeUriQuery = angular.$$encodeUriQuery,
          encodeUriSegment = angular.$$encodeUriSegment;

      function Route(template, defaults) {
        this.template = template;
        this.defaults = extend({}, provider.defaults, defaults);
        this.urlParams = {};
      }

      Route.prototype = {
        setUrlParams: function(config, params, actionUrl) {
          var self = this,
            url = actionUrl || self.template,
            val,
            encodedVal,
            protocolAndIpv6 = '';

          var urlParams = self.urlParams = Object.create(null);
          forEach(url.split(/\W/), function(param) {
            if (param === 'hasOwnProperty') {
              throw $resourceMinErr('badname', 'hasOwnProperty is not a valid parameter name.');
            }
            if (!(new RegExp('^\\d+$').test(param)) && param &&
              (new RegExp('(^|[^\\\\]):' + param + '(\\W|$)').test(url))) {
              urlParams[param] = {
                isQueryParamValue: (new RegExp('\\?.*=:' + param + '(?:\\W|$)')).test(url)
              };
            }
          });
          url = url.replace(/\\:/g, ':');
          url = url.replace(PROTOCOL_AND_IPV6_REGEX, function(match) {
            protocolAndIpv6 = match;
            return '';
          });

          params = params || {};
          forEach(self.urlParams, function(paramInfo, urlParam) {
            val = params.hasOwnProperty(urlParam) ? params[urlParam] : self.defaults[urlParam];
            if (isDefined(val) && val !== null) {
              if (paramInfo.isQueryParamValue) {
                encodedVal = encodeUriQuery(val, true);
              } else {
                encodedVal = encodeUriSegment(val);
              }
              url = url.replace(new RegExp(':' + urlParam + '(\\W|$)', 'g'), function(match, p1) {
                return encodedVal + p1;
              });
            } else {
              url = url.replace(new RegExp('(/?):' + urlParam + '(\\W|$)', 'g'), function(match,
                  leadingSlashes, tail) {
                if (tail.charAt(0) === '/') {
                  return tail;
                } else {
                  return leadingSlashes + tail;
                }
              });
            }
          });

          // strip trailing slashes and set the url (unless this behavior is specifically disabled)
          if (self.defaults.stripTrailingSlashes) {
            url = url.replace(/\/+$/, '') || '/';
          }

          // Collapse `/.` if found in the last URL path segment before the query.
          // E.g. `http://url.com/id/.format?q=x` becomes `http://url.com/id.format?q=x`.
          url = url.replace(/\/\.(?=\w+($|\?))/, '.');
          // Replace escaped `/\.` with `/.`.
          // (If `\.` comes from a param value, it will be encoded as `%5C.`.)
          config.url = protocolAndIpv6 + url.replace(/\/(\\|%5C)\./, '/.');


          // set params - delegate param encoding to $http
          forEach(params, function(value, key) {
            if (!self.urlParams[key]) {
              config.params = config.params || {};
              config.params[key] = value;
            }
          });
        }
      };


      function resourceFactory(url, paramDefaults, actions, options) {
        var route = new Route(url, options);

        actions = extend({}, provider.defaults.actions, actions);

        function extractParams(data, actionParams) {
          var ids = {};
          actionParams = extend({}, paramDefaults, actionParams);
          forEach(actionParams, function(value, key) {
            if (isFunction(value)) { value = value(data); }
            ids[key] = value && value.charAt && value.charAt(0) === '@' ?
              lookupDottedPath(data, value.substr(1)) : value;
          });
          return ids;
        }

        function defaultResponseInterceptor(response) {
          return response.resource;
        }

        function Resource(value) {
          shallowClearAndCopy(value || {}, this);
        }

        Resource.prototype.toJSON = function() {
          var data = extend({}, this);
          delete data.$promise;
          delete data.$resolved;
          delete data.$cancelRequest;
          return data;
        };

        forEach(actions, function(action, name) {
          var hasBody = action.hasBody === true || (action.hasBody !== false && /^(POST|PUT|PATCH)$/i.test(action.method));
          var numericTimeout = action.timeout;
          var cancellable = isDefined(action.cancellable) ?
              action.cancellable : route.defaults.cancellable;

          if (numericTimeout && !isNumber(numericTimeout)) {
            $log.debug('ngResource:\n' +
                       '  Only numeric values are allowed as `timeout`.\n' +
                       '  Promises are not supported in $resource, because the same value would ' +
                       'be used for multiple requests. If you are looking for a way to cancel ' +
                       'requests, you should use the `cancellable` option.');
            delete action.timeout;
            numericTimeout = null;
          }

          Resource[name] = function(a1, a2, a3, a4) {
            var params = {}, data, onSuccess, onError;

            switch (arguments.length) {
              case 4:
                onError = a4;
                onSuccess = a3;
                // falls through
              case 3:
              case 2:
                if (isFunction(a2)) {
                  if (isFunction(a1)) {
                    onSuccess = a1;
                    onError = a2;
                    break;
                  }

                  onSuccess = a2;
                  onError = a3;
                  // falls through
                } else {
                  params = a1;
                  data = a2;
                  onSuccess = a3;
                  break;
                }
                // falls through
              case 1:
                if (isFunction(a1)) onSuccess = a1;
                else if (hasBody) data = a1;
                else params = a1;
                break;
              case 0: break;
              default:
                throw $resourceMinErr('badargs',
                  'Expected up to 4 arguments [params, data, success, error], got {0} arguments',
                  arguments.length);
            }

            var isInstanceCall = this instanceof Resource;
            var value = isInstanceCall ? data : (action.isArray ? [] : new Resource(data));
            var httpConfig = {};
            var requestInterceptor = action.interceptor && action.interceptor.request || undefined;
            var requestErrorInterceptor = action.interceptor && action.interceptor.requestError ||
              undefined;
            var responseInterceptor = action.interceptor && action.interceptor.response ||
              defaultResponseInterceptor;
            var responseErrorInterceptor = action.interceptor && action.interceptor.responseError ||
              $q.reject;
            var successCallback = onSuccess ? function(val) {
              onSuccess(val, response.headers, response.status, response.statusText);
            } : undefined;
            var errorCallback = onError || undefined;
            var timeoutDeferred;
            var numericTimeoutPromise;
            var response;

            forEach(action, function(value, key) {
              switch (key) {
                default:
                  httpConfig[key] = copy(value);
                  break;
                case 'params':
                case 'isArray':
                case 'interceptor':
                case 'cancellable':
                  break;
              }
            });

            if (!isInstanceCall && cancellable) {
              timeoutDeferred = $q.defer();
              httpConfig.timeout = timeoutDeferred.promise;

              if (numericTimeout) {
                numericTimeoutPromise = $timeout(timeoutDeferred.resolve, numericTimeout);
              }
            }

            if (hasBody) httpConfig.data = data;
            route.setUrlParams(httpConfig,
              extend({}, extractParams(data, action.params || {}), params),
              action.url);

            // Start the promise chain
            var promise = $q.
              resolve(httpConfig).
              then(requestInterceptor).
              catch(requestErrorInterceptor).
              then($http);

            promise = promise.then(function(resp) {
              var data = resp.data;

              if (data) {
                // Need to convert action.isArray to boolean in case it is undefined
                if (isArray(data) !== (!!action.isArray)) {
                  throw $resourceMinErr('badcfg',
                      'Error in resource configuration for action `{0}`. Expected response to ' +
                      'contain an {1} but got an {2} (Request: {3} {4})', name, action.isArray ? 'array' : 'object',
                    isArray(data) ? 'array' : 'object', httpConfig.method, httpConfig.url);
                }
                if (action.isArray) {
                  value.length = 0;
                  forEach(data, function(item) {
                    if (typeof item === 'object') {
                      value.push(new Resource(item));
                    } else {
                      // Valid JSON values may be string literals, and these should not be converted
                      // into objects. These items will not have access to the Resource prototype
                      // methods, but unfortunately there
                      value.push(item);
                    }
                  });
                } else {
                  var promise = value.$promise;     // Save the promise
                  shallowClearAndCopy(data, value);
                  value.$promise = promise;         // Restore the promise
                }
              }

              resp.resource = value;
              response = resp;
              return responseInterceptor(resp);
            }, function(rejectionOrResponse) {
              rejectionOrResponse.resource = value;
              response = rejectionOrResponse;
              return responseErrorInterceptor(rejectionOrResponse);
            });

            promise = promise['finally'](function() {
              value.$resolved = true;
              if (!isInstanceCall && cancellable) {
                value.$cancelRequest = noop;
                $timeout.cancel(numericTimeoutPromise);
                timeoutDeferred = numericTimeoutPromise = httpConfig.timeout = null;
              }
            });

            // Run the `success`/`error` callbacks, but do not let them affect the returned promise.
            promise.then(successCallback, errorCallback);

            if (!isInstanceCall) {
              // we are creating instance / collection
              // - set the initial promise
              // - return the instance / collection
              value.$promise = promise;
              value.$resolved = false;
              if (cancellable) value.$cancelRequest = cancelRequest;

              return value;
            }

            // instance call
            return promise;

            function cancelRequest(value) {
              promise.catch(noop);
              if (timeoutDeferred !== null) {
                timeoutDeferred.resolve(value);
              }
            }
          };


          Resource.prototype['$' + name] = function(params, success, error) {
            if (isFunction(params)) {
              error = success; success = params; params = {};
            }
            var result = Resource[name].call(this, params, this, success, error);
            return result.$promise || result;
          };
        });

        return Resource;
      }

      return resourceFactory;
    }];
  });


})(window, window.angular);

})();
(function () {
/**
 * @license AngularJS v1.8.1
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

var $sanitizeMinErr = angular.$$minErr('$sanitize');
var bind;
var extend;
var forEach;
var isArray;
var isDefined;
var lowercase;
var noop;
var nodeContains;
var htmlParser;
var htmlSanitizeWriter;

/**
 * @ngdoc module
 * @name ngSanitize
 * @description
 *
 * The `ngSanitize` module provides functionality to sanitize HTML.
 *
 * See {@link ngSanitize.$sanitize `$sanitize`} for usage.
 */

/**
 * @ngdoc service
 * @name $sanitize
 * @kind function
 *
 * @description
 *   Sanitizes an html string by stripping all potentially dangerous tokens.
 *
 *   The input is sanitized by parsing the HTML into tokens. All safe tokens (from a trusted URI list) are
 *   then serialized back to a properly escaped HTML string. This means that no unsafe input can make
 *   it into the returned string.
 *
 *   The trusted URIs for URL sanitization of attribute values is configured using the functions
 *   `aHrefSanitizationTrustedUrlList` and `imgSrcSanitizationTrustedUrlList` of {@link $compileProvider}.
 *
 *   The input may also contain SVG markup if this is enabled via {@link $sanitizeProvider}.
 *
 * @param {string} html HTML input.
 * @returns {string} Sanitized HTML.
 *
 * @example
   <example module="sanitizeExample" deps="angular-sanitize.js" name="sanitize-service">
   <file name="index.html">
     <script>
         angular.module('sanitizeExample', ['ngSanitize'])
           .controller('ExampleController', ['$scope', '$sce', function($scope, $sce) {
             $scope.snippet =
               '<p style="color:blue">an html\n' +
               '<em onmouseover="this.textContent=\'PWN3D!\'">click here</em>\n' +
               'snippet</p>';
             $scope.deliberatelyTrustDangerousSnippet = function() {
               return $sce.trustAsHtml($scope.snippet);
             };
           }]);
     </script>
     <div ng-controller="ExampleController">
        Snippet: <textarea ng-model="snippet" cols="60" rows="3"></textarea>
       <table>
         <tr>
           <td>Directive</td>
           <td>How</td>
           <td>Source</td>
           <td>Rendered</td>
         </tr>
         <tr id="bind-html-with-sanitize">
           <td>ng-bind-html</td>
           <td>Automatically uses $sanitize</td>
           <td><pre>&lt;div ng-bind-html="snippet"&gt;<br/>&lt;/div&gt;</pre></td>
           <td><div ng-bind-html="snippet"></div></td>
         </tr>
         <tr id="bind-html-with-trust">
           <td>ng-bind-html</td>
           <td>Bypass $sanitize by explicitly trusting the dangerous value</td>
           <td>
           <pre>&lt;div ng-bind-html="deliberatelyTrustDangerousSnippet()"&gt;
&lt;/div&gt;</pre>
           </td>
           <td><div ng-bind-html="deliberatelyTrustDangerousSnippet()"></div></td>
         </tr>
         <tr id="bind-default">
           <td>ng-bind</td>
           <td>Automatically escapes</td>
           <td><pre>&lt;div ng-bind="snippet"&gt;<br/>&lt;/div&gt;</pre></td>
           <td><div ng-bind="snippet"></div></td>
         </tr>
       </table>
       </div>
   </file>
   <file name="protractor.js" type="protractor">
     it('should sanitize the html snippet by default', function() {
       expect(element(by.css('#bind-html-with-sanitize div')).getAttribute('innerHTML')).
         toBe('<p>an html\n<em>click here</em>\nsnippet</p>');
     });

     it('should inline raw snippet if bound to a trusted value', function() {
       expect(element(by.css('#bind-html-with-trust div')).getAttribute('innerHTML')).
         toBe("<p style=\"color:blue\">an html\n" +
              "<em onmouseover=\"this.textContent='PWN3D!'\">click here</em>\n" +
              "snippet</p>");
     });

     it('should escape snippet without any filter', function() {
       expect(element(by.css('#bind-default div')).getAttribute('innerHTML')).
         toBe("&lt;p style=\"color:blue\"&gt;an html\n" +
              "&lt;em onmouseover=\"this.textContent='PWN3D!'\"&gt;click here&lt;/em&gt;\n" +
              "snippet&lt;/p&gt;");
     });

     it('should update', function() {
       element(by.model('snippet')).clear();
       element(by.model('snippet')).sendKeys('new <b onclick="alert(1)">text</b>');
       expect(element(by.css('#bind-html-with-sanitize div')).getAttribute('innerHTML')).
         toBe('new <b>text</b>');
       expect(element(by.css('#bind-html-with-trust div')).getAttribute('innerHTML')).toBe(
         'new <b onclick="alert(1)">text</b>');
       expect(element(by.css('#bind-default div')).getAttribute('innerHTML')).toBe(
         "new &lt;b onclick=\"alert(1)\"&gt;text&lt;/b&gt;");
     });
   </file>
   </example>
 */


/**
 * @ngdoc provider
 * @name $sanitizeProvider
 * @this
 *
 * @description
 * Creates and configures {@link $sanitize} instance.
 */
function $SanitizeProvider() {
  var hasBeenInstantiated = false;
  var svgEnabled = false;

  this.$get = ['$$sanitizeUri', function($$sanitizeUri) {
    hasBeenInstantiated = true;
    if (svgEnabled) {
      extend(validElements, svgElements);
    }
    return function(html) {
      var buf = [];
      htmlParser(html, htmlSanitizeWriter(buf, function(uri, isImage) {
        return !/^unsafe:/.test($$sanitizeUri(uri, isImage));
      }));
      return buf.join('');
    };
  }];


  /**
   * @ngdoc method
   * @name $sanitizeProvider#enableSvg
   * @kind function
   *
   * @description
   * Enables a subset of svg to be supported by the sanitizer.
   *
   * <div class="alert alert-warning">
   *   <p>By enabling this setting without taking other precautions, you might expose your
   *   application to click-hijacking attacks. In these attacks, sanitized svg elements could be positioned
   *   outside of the containing element and be rendered over other elements on the page (e.g. a login
   *   link). Such behavior can then result in phishing incidents.</p>
   *
   *   <p>To protect against these, explicitly setup `overflow: hidden` css rule for all potential svg
   *   tags within the sanitized content:</p>
   *
   *   <br>
   *
   *   <pre><code>
   *   .rootOfTheIncludedContent svg {
   *     overflow: hidden !important;
   *   }
   *   </code></pre>
   * </div>
   *
   * @param {boolean=} flag Enable or disable SVG support in the sanitizer.
   * @returns {boolean|$sanitizeProvider} Returns the currently configured value if called
   *    without an argument or self for chaining otherwise.
   */
  this.enableSvg = function(enableSvg) {
    if (isDefined(enableSvg)) {
      svgEnabled = enableSvg;
      return this;
    } else {
      return svgEnabled;
    }
  };


  /**
   * @ngdoc method
   * @name $sanitizeProvider#addValidElements
   * @kind function
   *
   * @description
   * Extends the built-in lists of valid HTML/SVG elements, i.e. elements that are considered safe
   * and are not stripped off during sanitization. You can extend the following lists of elements:
   *
   * - `htmlElements`: A list of elements (tag names) to extend the current list of safe HTML
   *   elements. HTML elements considered safe will not be removed during sanitization. All other
   *   elements will be stripped off.
   *
   * - `htmlVoidElements`: This is similar to `htmlElements`, but marks the elements as
   *   "void elements" (similar to HTML
   *   [void elements](https://rawgit.com/w3c/html/html5.1-2/single-page.html#void-elements)). These
   *   elements have no end tag and cannot have content.
   *
   * - `svgElements`: This is similar to `htmlElements`, but for SVG elements. This list is only
   *   taken into account if SVG is {@link ngSanitize.$sanitizeProvider#enableSvg enabled} for
   *   `$sanitize`.
   *
   * <div class="alert alert-info">
   *   This method must be called during the {@link angular.Module#config config} phase. Once the
   *   `$sanitize` service has been instantiated, this method has no effect.
   * </div>
   *
   * <div class="alert alert-warning">
   *   Keep in mind that extending the built-in lists of elements may expose your app to XSS or
   *   other vulnerabilities. Be very mindful of the elements you add.
   * </div>
   *
   * @param {Array<String>|Object} elements - A list of valid HTML elements or an object with one or
   *   more of the following properties:
   *   - **htmlElements** - `{Array<String>}` - A list of elements to extend the current list of
   *     HTML elements.
   *   - **htmlVoidElements** - `{Array<String>}` - A list of elements to extend the current list of
   *     void HTML elements; i.e. elements that do not have an end tag.
   *   - **svgElements** - `{Array<String>}` - A list of elements to extend the current list of SVG
   *     elements. The list of SVG elements is only taken into account if SVG is
   *     {@link ngSanitize.$sanitizeProvider#enableSvg enabled} for `$sanitize`.
   *
   * Passing an array (`[...]`) is equivalent to passing `{htmlElements: [...]}`.
   *
   * @return {$sanitizeProvider} Returns self for chaining.
   */
  this.addValidElements = function(elements) {
    if (!hasBeenInstantiated) {
      if (isArray(elements)) {
        elements = {htmlElements: elements};
      }

      addElementsTo(svgElements, elements.svgElements);
      addElementsTo(voidElements, elements.htmlVoidElements);
      addElementsTo(validElements, elements.htmlVoidElements);
      addElementsTo(validElements, elements.htmlElements);
    }

    return this;
  };


  /**
   * @ngdoc method
   * @name $sanitizeProvider#addValidAttrs
   * @kind function
   *
   * @description
   * Extends the built-in list of valid attributes, i.e. attributes that are considered safe and are
   * not stripped off during sanitization.
   *
   * **Note**:
   * The new attributes will not be treated as URI attributes, which means their values will not be
   * sanitized as URIs using `$compileProvider`'s
   * {@link ng.$compileProvider#aHrefSanitizationTrustedUrlList aHrefSanitizationTrustedUrlList} and
   * {@link ng.$compileProvider#imgSrcSanitizationTrustedUrlList imgSrcSanitizationTrustedUrlList}.
   *
   * <div class="alert alert-info">
   *   This method must be called during the {@link angular.Module#config config} phase. Once the
   *   `$sanitize` service has been instantiated, this method has no effect.
   * </div>
   *
   * <div class="alert alert-warning">
   *   Keep in mind that extending the built-in list of attributes may expose your app to XSS or
   *   other vulnerabilities. Be very mindful of the attributes you add.
   * </div>
   *
   * @param {Array<String>} attrs - A list of valid attributes.
   *
   * @returns {$sanitizeProvider} Returns self for chaining.
   */
  this.addValidAttrs = function(attrs) {
    if (!hasBeenInstantiated) {
      extend(validAttrs, arrayToMap(attrs, true));
    }
    return this;
  };

  //////////////////////////////////////////////////////////////////////////////////////////////////
  // Private stuff
  //////////////////////////////////////////////////////////////////////////////////////////////////

  bind = angular.bind;
  extend = angular.extend;
  forEach = angular.forEach;
  isArray = angular.isArray;
  isDefined = angular.isDefined;
  lowercase = angular.$$lowercase;
  noop = angular.noop;

  htmlParser = htmlParserImpl;
  htmlSanitizeWriter = htmlSanitizeWriterImpl;

  nodeContains = window.Node.prototype.contains || /** @this */ function(arg) {
    // eslint-disable-next-line no-bitwise
    return !!(this.compareDocumentPosition(arg) & 16);
  };

  // Regular Expressions for parsing tags and attributes
  var SURROGATE_PAIR_REGEXP = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g,
    // Match everything outside of normal chars and " (quote character)
    NON_ALPHANUMERIC_REGEXP = /([^#-~ |!])/g;


  // Good source of info about elements and attributes
  // http://dev.w3.org/html5/spec/Overview.html#semantics
  // http://simon.html5.org/html-elements

  // Safe Void Elements - HTML5
  // http://dev.w3.org/html5/spec/Overview.html#void-elements
  var voidElements = stringToMap('area,br,col,hr,img,wbr');

  // Elements that you can, intentionally, leave open (and which close themselves)
  // http://dev.w3.org/html5/spec/Overview.html#optional-tags
  var optionalEndTagBlockElements = stringToMap('colgroup,dd,dt,li,p,tbody,td,tfoot,th,thead,tr'),
      optionalEndTagInlineElements = stringToMap('rp,rt'),
      optionalEndTagElements = extend({},
                                              optionalEndTagInlineElements,
                                              optionalEndTagBlockElements);

  // Safe Block Elements - HTML5
  var blockElements = extend({}, optionalEndTagBlockElements, stringToMap('address,article,' +
          'aside,blockquote,caption,center,del,dir,div,dl,figure,figcaption,footer,h1,h2,h3,h4,h5,' +
          'h6,header,hgroup,hr,ins,map,menu,nav,ol,pre,section,table,ul'));

  // Inline Elements - HTML5
  var inlineElements = extend({}, optionalEndTagInlineElements, stringToMap('a,abbr,acronym,b,' +
          'bdi,bdo,big,br,cite,code,del,dfn,em,font,i,img,ins,kbd,label,map,mark,q,ruby,rp,rt,s,' +
          'samp,small,span,strike,strong,sub,sup,time,tt,u,var'));

  // SVG Elements
  // https://wiki.whatwg.org/wiki/Sanitization_rules#svg_Elements
  // Note: the elements animate,animateColor,animateMotion,animateTransform,set are intentionally omitted.
  // They can potentially allow for arbitrary javascript to be executed. See #11290
  var svgElements = stringToMap('circle,defs,desc,ellipse,font-face,font-face-name,font-face-src,g,glyph,' +
          'hkern,image,linearGradient,line,marker,metadata,missing-glyph,mpath,path,polygon,polyline,' +
          'radialGradient,rect,stop,svg,switch,text,title,tspan');

  // Blocked Elements (will be stripped)
  var blockedElements = stringToMap('script,style');

  var validElements = extend({},
                                     voidElements,
                                     blockElements,
                                     inlineElements,
                                     optionalEndTagElements);

  //Attributes that have href and hence need to be sanitized
  var uriAttrs = stringToMap('background,cite,href,longdesc,src,xlink:href,xml:base');

  var htmlAttrs = stringToMap('abbr,align,alt,axis,bgcolor,border,cellpadding,cellspacing,class,clear,' +
      'color,cols,colspan,compact,coords,dir,face,headers,height,hreflang,hspace,' +
      'ismap,lang,language,nohref,nowrap,rel,rev,rows,rowspan,rules,' +
      'scope,scrolling,shape,size,span,start,summary,tabindex,target,title,type,' +
      'valign,value,vspace,width');

  // SVG attributes (without "id" and "name" attributes)
  // https://wiki.whatwg.org/wiki/Sanitization_rules#svg_Attributes
  var svgAttrs = stringToMap('accent-height,accumulate,additive,alphabetic,arabic-form,ascent,' +
      'baseProfile,bbox,begin,by,calcMode,cap-height,class,color,color-rendering,content,' +
      'cx,cy,d,dx,dy,descent,display,dur,end,fill,fill-rule,font-family,font-size,font-stretch,' +
      'font-style,font-variant,font-weight,from,fx,fy,g1,g2,glyph-name,gradientUnits,hanging,' +
      'height,horiz-adv-x,horiz-origin-x,ideographic,k,keyPoints,keySplines,keyTimes,lang,' +
      'marker-end,marker-mid,marker-start,markerHeight,markerUnits,markerWidth,mathematical,' +
      'max,min,offset,opacity,orient,origin,overline-position,overline-thickness,panose-1,' +
      'path,pathLength,points,preserveAspectRatio,r,refX,refY,repeatCount,repeatDur,' +
      'requiredExtensions,requiredFeatures,restart,rotate,rx,ry,slope,stemh,stemv,stop-color,' +
      'stop-opacity,strikethrough-position,strikethrough-thickness,stroke,stroke-dasharray,' +
      'stroke-dashoffset,stroke-linecap,stroke-linejoin,stroke-miterlimit,stroke-opacity,' +
      'stroke-width,systemLanguage,target,text-anchor,to,transform,type,u1,u2,underline-position,' +
      'underline-thickness,unicode,unicode-range,units-per-em,values,version,viewBox,visibility,' +
      'width,widths,x,x-height,x1,x2,xlink:actuate,xlink:arcrole,xlink:role,xlink:show,xlink:title,' +
      'xlink:type,xml:base,xml:lang,xml:space,xmlns,xmlns:xlink,y,y1,y2,zoomAndPan', true);

  var validAttrs = extend({},
                                  uriAttrs,
                                  svgAttrs,
                                  htmlAttrs);

  function stringToMap(str, lowercaseKeys) {
    return arrayToMap(str.split(','), lowercaseKeys);
  }

  function arrayToMap(items, lowercaseKeys) {
    var obj = {}, i;
    for (i = 0; i < items.length; i++) {
      obj[lowercaseKeys ? lowercase(items[i]) : items[i]] = true;
    }
    return obj;
  }

  function addElementsTo(elementsMap, newElements) {
    if (newElements && newElements.length) {
      extend(elementsMap, arrayToMap(newElements));
    }
  }

  /**
   * Create an inert document that contains the dirty HTML that needs sanitizing.
   * We use the DOMParser API by default and fall back to createHTMLDocument if DOMParser is not
   * available.
   */
  var getInertBodyElement /* function(html: string): HTMLBodyElement */ = (function(window, document) {
    if (isDOMParserAvailable()) {
      return getInertBodyElement_DOMParser;
    }

    if (!document || !document.implementation) {
      throw $sanitizeMinErr('noinert', 'Can\'t create an inert html document');
    }
    var inertDocument = document.implementation.createHTMLDocument('inert');
    var inertBodyElement = (inertDocument.documentElement || inertDocument.getDocumentElement()).querySelector('body');
    return getInertBodyElement_InertDocument;

    function isDOMParserAvailable() {
      try {
        return !!getInertBodyElement_DOMParser('');
      } catch (e) {
        return false;
      }
    }

    function getInertBodyElement_DOMParser(html) {
      // We add this dummy element to ensure that the rest of the content is parsed as expected
      // e.g. leading whitespace is maintained and tags like `<meta>` do not get hoisted to the `<head>` tag.
      html = '<remove></remove>' + html;
      try {
        var body = new window.DOMParser().parseFromString(html, 'text/html').body;
        body.firstChild.remove();
        return body;
      } catch (e) {
        return undefined;
      }
    }

    function getInertBodyElement_InertDocument(html) {
      inertBodyElement.innerHTML = html;

      // Support: IE 9-11 only
      // strip custom-namespaced attributes on IE<=11
      if (document.documentMode) {
        stripCustomNsAttrs(inertBodyElement);
      }

      return inertBodyElement;
    }
  })(window, window.document);

  /**
   * @example
   * htmlParser(htmlString, {
   *     start: function(tag, attrs) {},
   *     end: function(tag) {},
   *     chars: function(text) {},
   *     comment: function(text) {}
   * });
   *
   * @param {string} html string
   * @param {object} handler
   */
  function htmlParserImpl(html, handler) {
    if (html === null || html === undefined) {
      html = '';
    } else if (typeof html !== 'string') {
      html = '' + html;
    }

    var inertBodyElement = getInertBodyElement(html);
    if (!inertBodyElement) return '';

    //mXSS protection
    var mXSSAttempts = 5;
    do {
      if (mXSSAttempts === 0) {
        throw $sanitizeMinErr('uinput', 'Failed to sanitize html because the input is unstable');
      }
      mXSSAttempts--;

      // trigger mXSS if it is going to happen by reading and writing the innerHTML
      html = inertBodyElement.innerHTML;
      inertBodyElement = getInertBodyElement(html);
    } while (html !== inertBodyElement.innerHTML);

    var node = inertBodyElement.firstChild;
    while (node) {
      switch (node.nodeType) {
        case 1: // ELEMENT_NODE
          handler.start(node.nodeName.toLowerCase(), attrToMap(node.attributes));
          break;
        case 3: // TEXT NODE
          handler.chars(node.textContent);
          break;
      }

      var nextNode;
      if (!(nextNode = node.firstChild)) {
        if (node.nodeType === 1) {
          handler.end(node.nodeName.toLowerCase());
        }
        nextNode = getNonDescendant('nextSibling', node);
        if (!nextNode) {
          while (nextNode == null) {
            node = getNonDescendant('parentNode', node);
            if (node === inertBodyElement) break;
            nextNode = getNonDescendant('nextSibling', node);
            if (node.nodeType === 1) {
              handler.end(node.nodeName.toLowerCase());
            }
          }
        }
      }
      node = nextNode;
    }

    while ((node = inertBodyElement.firstChild)) {
      inertBodyElement.removeChild(node);
    }
  }

  function attrToMap(attrs) {
    var map = {};
    for (var i = 0, ii = attrs.length; i < ii; i++) {
      var attr = attrs[i];
      map[attr.name] = attr.value;
    }
    return map;
  }


  /**
   * Escapes all potentially dangerous characters, so that the
   * resulting string can be safely inserted into attribute or
   * element text.
   * @param value
   * @returns {string} escaped text
   */
  function encodeEntities(value) {
    return value.
      replace(/&/g, '&amp;').
      replace(SURROGATE_PAIR_REGEXP, function(value) {
        var hi = value.charCodeAt(0);
        var low = value.charCodeAt(1);
        return '&#' + (((hi - 0xD800) * 0x400) + (low - 0xDC00) + 0x10000) + ';';
      }).
      replace(NON_ALPHANUMERIC_REGEXP, function(value) {
        return '&#' + value.charCodeAt(0) + ';';
      }).
      replace(/</g, '&lt;').
      replace(/>/g, '&gt;');
  }

  /**
   * create an HTML/XML writer which writes to buffer
   * @param {Array} buf use buf.join('') to get out sanitized html string
   * @returns {object} in the form of {
   *     start: function(tag, attrs) {},
   *     end: function(tag) {},
   *     chars: function(text) {},
   *     comment: function(text) {}
   * }
   */
  function htmlSanitizeWriterImpl(buf, uriValidator) {
    var ignoreCurrentElement = false;
    var out = bind(buf, buf.push);
    return {
      start: function(tag, attrs) {
        tag = lowercase(tag);
        if (!ignoreCurrentElement && blockedElements[tag]) {
          ignoreCurrentElement = tag;
        }
        if (!ignoreCurrentElement && validElements[tag] === true) {
          out('<');
          out(tag);
          forEach(attrs, function(value, key) {
            var lkey = lowercase(key);
            var isImage = (tag === 'img' && lkey === 'src') || (lkey === 'background');
            if (validAttrs[lkey] === true &&
              (uriAttrs[lkey] !== true || uriValidator(value, isImage))) {
              out(' ');
              out(key);
              out('="');
              out(encodeEntities(value));
              out('"');
            }
          });
          out('>');
        }
      },
      end: function(tag) {
        tag = lowercase(tag);
        if (!ignoreCurrentElement && validElements[tag] === true && voidElements[tag] !== true) {
          out('</');
          out(tag);
          out('>');
        }
        // eslint-disable-next-line eqeqeq
        if (tag == ignoreCurrentElement) {
          ignoreCurrentElement = false;
        }
      },
      chars: function(chars) {
        if (!ignoreCurrentElement) {
          out(encodeEntities(chars));
        }
      }
    };
  }


  /**
   * When IE9-11 comes across an unknown namespaced attribute e.g. 'xlink:foo' it adds 'xmlns:ns1' attribute to declare
   * ns1 namespace and prefixes the attribute with 'ns1' (e.g. 'ns1:xlink:foo'). This is undesirable since we don't want
   * to allow any of these custom attributes. This method strips them all.
   *
   * @param node Root element to process
   */
  function stripCustomNsAttrs(node) {
    while (node) {
      if (node.nodeType === window.Node.ELEMENT_NODE) {
        var attrs = node.attributes;
        for (var i = 0, l = attrs.length; i < l; i++) {
          var attrNode = attrs[i];
          var attrName = attrNode.name.toLowerCase();
          if (attrName === 'xmlns:ns1' || attrName.lastIndexOf('ns1:', 0) === 0) {
            node.removeAttributeNode(attrNode);
            i--;
            l--;
          }
        }
      }

      var nextNode = node.firstChild;
      if (nextNode) {
        stripCustomNsAttrs(nextNode);
      }

      node = getNonDescendant('nextSibling', node);
    }
  }

  function getNonDescendant(propName, node) {
    // An element is clobbered if its `propName` property points to one of its descendants
    var nextNode = node[propName];
    if (nextNode && nodeContains.call(node, nextNode)) {
      throw $sanitizeMinErr('elclob', 'Failed to sanitize html because the element is clobbered: {0}', node.outerHTML || node.outerText);
    }
    return nextNode;
  }
}

function sanitizeText(chars) {
  var buf = [];
  var writer = htmlSanitizeWriter(buf, noop);
  writer.chars(chars);
  return buf.join('');
}


// define ngSanitize module and register $sanitize service
angular.module('ngSanitize', [])
  .provider('$sanitize', $SanitizeProvider)
  .info({ angularVersion: '1.8.1' });

/**
 * @ngdoc filter
 * @name linky
 * @kind function
 *
 * @description
 * Finds links in text input and turns them into html links. Supports `http/https/ftp/sftp/mailto` and
 * plain email address links.
 *
 * Requires the {@link ngSanitize `ngSanitize`} module to be installed.
 *
 * @param {string} text Input text.
 * @param {string} [target] Window (`_blank|_self|_parent|_top`) or named frame to open links in.
 * @param {object|function(url)} [attributes] Add custom attributes to the link element.
 *
 *    Can be one of:
 *
 *    - `object`: A map of attributes
 *    - `function`: Takes the url as a parameter and returns a map of attributes
 *
 *    If the map of attributes contains a value for `target`, it overrides the value of
 *    the target parameter.
 *
 *
 * @returns {string} Html-linkified and {@link $sanitize sanitized} text.
 *
 * @usage
   <span ng-bind-html="linky_expression | linky"></span>
 *
 * @example
   <example module="linkyExample" deps="angular-sanitize.js" name="linky-filter">
     <file name="index.html">
       <div ng-controller="ExampleController">
       Snippet: <textarea ng-model="snippet" cols="60" rows="3"></textarea>
       <table>
         <tr>
           <th>Filter</th>
           <th>Source</th>
           <th>Rendered</th>
         </tr>
         <tr id="linky-filter">
           <td>linky filter</td>
           <td>
             <pre>&lt;div ng-bind-html="snippet | linky"&gt;<br>&lt;/div&gt;</pre>
           </td>
           <td>
             <div ng-bind-html="snippet | linky"></div>
           </td>
         </tr>
         <tr id="linky-target">
          <td>linky target</td>
          <td>
            <pre>&lt;div ng-bind-html="snippetWithSingleURL | linky:'_blank'"&gt;<br>&lt;/div&gt;</pre>
          </td>
          <td>
            <div ng-bind-html="snippetWithSingleURL | linky:'_blank'"></div>
          </td>
         </tr>
         <tr id="linky-custom-attributes">
          <td>linky custom attributes</td>
          <td>
            <pre>&lt;div ng-bind-html="snippetWithSingleURL | linky:'_self':{rel: 'nofollow'}"&gt;<br>&lt;/div&gt;</pre>
          </td>
          <td>
            <div ng-bind-html="snippetWithSingleURL | linky:'_self':{rel: 'nofollow'}"></div>
          </td>
         </tr>
         <tr id="escaped-html">
           <td>no filter</td>
           <td><pre>&lt;div ng-bind="snippet"&gt;<br>&lt;/div&gt;</pre></td>
           <td><div ng-bind="snippet"></div></td>
         </tr>
       </table>
     </file>
     <file name="script.js">
       angular.module('linkyExample', ['ngSanitize'])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.snippet =
             'Pretty text with some links:\n' +
             'http://angularjs.org/,\n' +
             'mailto:us@somewhere.org,\n' +
             'another@somewhere.org,\n' +
             'and one more: ftp://127.0.0.1/.';
           $scope.snippetWithSingleURL = 'http://angularjs.org/';
         }]);
     </file>
     <file name="protractor.js" type="protractor">
       it('should linkify the snippet with urls', function() {
         expect(element(by.id('linky-filter')).element(by.binding('snippet | linky')).getText()).
             toBe('Pretty text with some links: http://angularjs.org/, us@somewhere.org, ' +
                  'another@somewhere.org, and one more: ftp://127.0.0.1/.');
         expect(element.all(by.css('#linky-filter a')).count()).toEqual(4);
       });

       it('should not linkify snippet without the linky filter', function() {
         expect(element(by.id('escaped-html')).element(by.binding('snippet')).getText()).
             toBe('Pretty text with some links: http://angularjs.org/, mailto:us@somewhere.org, ' +
                  'another@somewhere.org, and one more: ftp://127.0.0.1/.');
         expect(element.all(by.css('#escaped-html a')).count()).toEqual(0);
       });

       it('should update', function() {
         element(by.model('snippet')).clear();
         element(by.model('snippet')).sendKeys('new http://link.');
         expect(element(by.id('linky-filter')).element(by.binding('snippet | linky')).getText()).
             toBe('new http://link.');
         expect(element.all(by.css('#linky-filter a')).count()).toEqual(1);
         expect(element(by.id('escaped-html')).element(by.binding('snippet')).getText())
             .toBe('new http://link.');
       });

       it('should work with the target property', function() {
        expect(element(by.id('linky-target')).
            element(by.binding("snippetWithSingleURL | linky:'_blank'")).getText()).
            toBe('http://angularjs.org/');
        expect(element(by.css('#linky-target a')).getAttribute('target')).toEqual('_blank');
       });

       it('should optionally add custom attributes', function() {
        expect(element(by.id('linky-custom-attributes')).
            element(by.binding("snippetWithSingleURL | linky:'_self':{rel: 'nofollow'}")).getText()).
            toBe('http://angularjs.org/');
        expect(element(by.css('#linky-custom-attributes a')).getAttribute('rel')).toEqual('nofollow');
       });
     </file>
   </example>
 */
angular.module('ngSanitize').filter('linky', ['$sanitize', function($sanitize) {
  var LINKY_URL_REGEXP =
        /((s?ftp|https?):\/\/|(www\.)|(mailto:)?[A-Za-z0-9._%+-]+@)\S*[^\s.;,(){}<>"\u201d\u2019]/i,
      MAILTO_REGEXP = /^mailto:/i;

  var linkyMinErr = angular.$$minErr('linky');
  var isDefined = angular.isDefined;
  var isFunction = angular.isFunction;
  var isObject = angular.isObject;
  var isString = angular.isString;

  return function(text, target, attributes) {
    if (text == null || text === '') return text;
    if (!isString(text)) throw linkyMinErr('notstring', 'Expected string but received: {0}', text);

    var attributesFn =
      isFunction(attributes) ? attributes :
      isObject(attributes) ? function getAttributesObject() {return attributes;} :
      function getEmptyAttributesObject() {return {};};

    var match;
    var raw = text;
    var html = [];
    var url;
    var i;
    while ((match = raw.match(LINKY_URL_REGEXP))) {
      // We can not end in these as they are sometimes found at the end of the sentence
      url = match[0];
      // if we did not match ftp/http/www/mailto then assume mailto
      if (!match[2] && !match[4]) {
        url = (match[3] ? 'http://' : 'mailto:') + url;
      }
      i = match.index;
      addText(raw.substr(0, i));
      addLink(url, match[0].replace(MAILTO_REGEXP, ''));
      raw = raw.substring(i + match[0].length);
    }
    addText(raw);
    return $sanitize(html.join(''));

    function addText(text) {
      if (!text) {
        return;
      }
      html.push(sanitizeText(text));
    }

    function addLink(url, text) {
      var key, linkAttributes = attributesFn(url);
      html.push('<a ');

      for (key in linkAttributes) {
        html.push(key + '="' + linkAttributes[key] + '" ');
      }

      if (isDefined(target) && !('target' in linkAttributes)) {
        html.push('target="',
                  target,
                  '" ');
      }
      html.push('href="',
                url.replace(/"/g, '&quot;'),
                '">');
      addText(text);
      html.push('</a>');
    }
  };
}]);


})(window, window.angular);

})();
(function () {
/**
 * @license AngularJS v1.8.1
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

var ELEMENT_NODE = 1;
var COMMENT_NODE = 8;

var ADD_CLASS_SUFFIX = '-add';
var REMOVE_CLASS_SUFFIX = '-remove';
var EVENT_CLASS_PREFIX = 'ng-';
var ACTIVE_CLASS_SUFFIX = '-active';
var PREPARE_CLASS_SUFFIX = '-prepare';

var NG_ANIMATE_CLASSNAME = 'ng-animate';
var NG_ANIMATE_CHILDREN_DATA = '$$ngAnimateChildren';

// Detect proper transitionend/animationend event names.
var CSS_PREFIX = '', TRANSITION_PROP, TRANSITIONEND_EVENT, ANIMATION_PROP, ANIMATIONEND_EVENT;

// If unprefixed events are not supported but webkit-prefixed are, use the latter.
// Otherwise, just use W3C names, browsers not supporting them at all will just ignore them.
// Note: Chrome implements `window.onwebkitanimationend` and doesn't implement `window.onanimationend`
// but at the same time dispatches the `animationend` event and not `webkitAnimationEnd`.
// Register both events in case `window.onanimationend` is not supported because of that,
// do the same for `transitionend` as Safari is likely to exhibit similar behavior.
// Also, the only modern browser that uses vendor prefixes for transitions/keyframes is webkit
// therefore there is no reason to test anymore for other vendor prefixes:
// http://caniuse.com/#search=transition
if ((window.ontransitionend === undefined) && (window.onwebkittransitionend !== undefined)) {
  CSS_PREFIX = '-webkit-';
  TRANSITION_PROP = 'WebkitTransition';
  TRANSITIONEND_EVENT = 'webkitTransitionEnd transitionend';
} else {
  TRANSITION_PROP = 'transition';
  TRANSITIONEND_EVENT = 'transitionend';
}

if ((window.onanimationend === undefined) && (window.onwebkitanimationend !== undefined)) {
  CSS_PREFIX = '-webkit-';
  ANIMATION_PROP = 'WebkitAnimation';
  ANIMATIONEND_EVENT = 'webkitAnimationEnd animationend';
} else {
  ANIMATION_PROP = 'animation';
  ANIMATIONEND_EVENT = 'animationend';
}

var DURATION_KEY = 'Duration';
var PROPERTY_KEY = 'Property';
var DELAY_KEY = 'Delay';
var TIMING_KEY = 'TimingFunction';
var ANIMATION_ITERATION_COUNT_KEY = 'IterationCount';
var ANIMATION_PLAYSTATE_KEY = 'PlayState';
var SAFE_FAST_FORWARD_DURATION_VALUE = 9999;

var ANIMATION_DELAY_PROP = ANIMATION_PROP + DELAY_KEY;
var ANIMATION_DURATION_PROP = ANIMATION_PROP + DURATION_KEY;
var TRANSITION_DELAY_PROP = TRANSITION_PROP + DELAY_KEY;
var TRANSITION_DURATION_PROP = TRANSITION_PROP + DURATION_KEY;

var ngMinErr = angular.$$minErr('ng');
function assertArg(arg, name, reason) {
  if (!arg) {
    throw ngMinErr('areq', 'Argument \'{0}\' is {1}', (name || '?'), (reason || 'required'));
  }
  return arg;
}

function mergeClasses(a,b) {
  if (!a && !b) return '';
  if (!a) return b;
  if (!b) return a;
  if (isArray(a)) a = a.join(' ');
  if (isArray(b)) b = b.join(' ');
  return a + ' ' + b;
}

function packageStyles(options) {
  var styles = {};
  if (options && (options.to || options.from)) {
    styles.to = options.to;
    styles.from = options.from;
  }
  return styles;
}

function pendClasses(classes, fix, isPrefix) {
  var className = '';
  classes = isArray(classes)
      ? classes
      : classes && isString(classes) && classes.length
          ? classes.split(/\s+/)
          : [];
  forEach(classes, function(klass, i) {
    if (klass && klass.length > 0) {
      className += (i > 0) ? ' ' : '';
      className += isPrefix ? fix + klass
                            : klass + fix;
    }
  });
  return className;
}

function removeFromArray(arr, val) {
  var index = arr.indexOf(val);
  if (val >= 0) {
    arr.splice(index, 1);
  }
}

function stripCommentsFromElement(element) {
  if (element instanceof jqLite) {
    switch (element.length) {
      case 0:
        return element;

      case 1:
        // there is no point of stripping anything if the element
        // is the only element within the jqLite wrapper.
        // (it's important that we retain the element instance.)
        if (element[0].nodeType === ELEMENT_NODE) {
          return element;
        }
        break;

      default:
        return jqLite(extractElementNode(element));
    }
  }

  if (element.nodeType === ELEMENT_NODE) {
    return jqLite(element);
  }
}

function extractElementNode(element) {
  if (!element[0]) return element;
  for (var i = 0; i < element.length; i++) {
    var elm = element[i];
    if (elm.nodeType === ELEMENT_NODE) {
      return elm;
    }
  }
}

function $$addClass($$jqLite, element, className) {
  forEach(element, function(elm) {
    $$jqLite.addClass(elm, className);
  });
}

function $$removeClass($$jqLite, element, className) {
  forEach(element, function(elm) {
    $$jqLite.removeClass(elm, className);
  });
}

function applyAnimationClassesFactory($$jqLite) {
  return function(element, options) {
    if (options.addClass) {
      $$addClass($$jqLite, element, options.addClass);
      options.addClass = null;
    }
    if (options.removeClass) {
      $$removeClass($$jqLite, element, options.removeClass);
      options.removeClass = null;
    }
  };
}

function prepareAnimationOptions(options) {
  options = options || {};
  if (!options.$$prepared) {
    var domOperation = options.domOperation || noop;
    options.domOperation = function() {
      options.$$domOperationFired = true;
      domOperation();
      domOperation = noop;
    };
    options.$$prepared = true;
  }
  return options;
}

function applyAnimationStyles(element, options) {
  applyAnimationFromStyles(element, options);
  applyAnimationToStyles(element, options);
}

function applyAnimationFromStyles(element, options) {
  if (options.from) {
    element.css(options.from);
    options.from = null;
  }
}

function applyAnimationToStyles(element, options) {
  if (options.to) {
    element.css(options.to);
    options.to = null;
  }
}

function mergeAnimationDetails(element, oldAnimation, newAnimation) {
  var target = oldAnimation.options || {};
  var newOptions = newAnimation.options || {};

  var toAdd = (target.addClass || '') + ' ' + (newOptions.addClass || '');
  var toRemove = (target.removeClass || '') + ' ' + (newOptions.removeClass || '');
  var classes = resolveElementClasses(element.attr('class'), toAdd, toRemove);

  if (newOptions.preparationClasses) {
    target.preparationClasses = concatWithSpace(newOptions.preparationClasses, target.preparationClasses);
    delete newOptions.preparationClasses;
  }

  // noop is basically when there is no callback; otherwise something has been set
  var realDomOperation = target.domOperation !== noop ? target.domOperation : null;

  extend(target, newOptions);

  // TODO(matsko or sreeramu): proper fix is to maintain all animation callback in array and call at last,but now only leave has the callback so no issue with this.
  if (realDomOperation) {
    target.domOperation = realDomOperation;
  }

  if (classes.addClass) {
    target.addClass = classes.addClass;
  } else {
    target.addClass = null;
  }

  if (classes.removeClass) {
    target.removeClass = classes.removeClass;
  } else {
    target.removeClass = null;
  }

  oldAnimation.addClass = target.addClass;
  oldAnimation.removeClass = target.removeClass;

  return target;
}

function resolveElementClasses(existing, toAdd, toRemove) {
  var ADD_CLASS = 1;
  var REMOVE_CLASS = -1;

  var flags = {};
  existing = splitClassesToLookup(existing);

  toAdd = splitClassesToLookup(toAdd);
  forEach(toAdd, function(value, key) {
    flags[key] = ADD_CLASS;
  });

  toRemove = splitClassesToLookup(toRemove);
  forEach(toRemove, function(value, key) {
    flags[key] = flags[key] === ADD_CLASS ? null : REMOVE_CLASS;
  });

  var classes = {
    addClass: '',
    removeClass: ''
  };

  forEach(flags, function(val, klass) {
    var prop, allow;
    if (val === ADD_CLASS) {
      prop = 'addClass';
      allow = !existing[klass] || existing[klass + REMOVE_CLASS_SUFFIX];
    } else if (val === REMOVE_CLASS) {
      prop = 'removeClass';
      allow = existing[klass] || existing[klass + ADD_CLASS_SUFFIX];
    }
    if (allow) {
      if (classes[prop].length) {
        classes[prop] += ' ';
      }
      classes[prop] += klass;
    }
  });

  function splitClassesToLookup(classes) {
    if (isString(classes)) {
      classes = classes.split(' ');
    }

    var obj = {};
    forEach(classes, function(klass) {
      // sometimes the split leaves empty string values
      // incase extra spaces were applied to the options
      if (klass.length) {
        obj[klass] = true;
      }
    });
    return obj;
  }

  return classes;
}

function getDomNode(element) {
  return (element instanceof jqLite) ? element[0] : element;
}

function applyGeneratedPreparationClasses($$jqLite, element, event, options) {
  var classes = '';
  if (event) {
    classes = pendClasses(event, EVENT_CLASS_PREFIX, true);
  }
  if (options.addClass) {
    classes = concatWithSpace(classes, pendClasses(options.addClass, ADD_CLASS_SUFFIX));
  }
  if (options.removeClass) {
    classes = concatWithSpace(classes, pendClasses(options.removeClass, REMOVE_CLASS_SUFFIX));
  }
  if (classes.length) {
    options.preparationClasses = classes;
    element.addClass(classes);
  }
}

function clearGeneratedClasses(element, options) {
  if (options.preparationClasses) {
    element.removeClass(options.preparationClasses);
    options.preparationClasses = null;
  }
  if (options.activeClasses) {
    element.removeClass(options.activeClasses);
    options.activeClasses = null;
  }
}

function blockKeyframeAnimations(node, applyBlock) {
  var value = applyBlock ? 'paused' : '';
  var key = ANIMATION_PROP + ANIMATION_PLAYSTATE_KEY;
  applyInlineStyle(node, [key, value]);
  return [key, value];
}

function applyInlineStyle(node, styleTuple) {
  var prop = styleTuple[0];
  var value = styleTuple[1];
  node.style[prop] = value;
}

function concatWithSpace(a,b) {
  if (!a) return b;
  if (!b) return a;
  return a + ' ' + b;
}

var helpers = {
  blockTransitions: function(node, duration) {
    // we use a negative delay value since it performs blocking
    // yet it doesn't kill any existing transitions running on the
    // same element which makes this safe for class-based animations
    var value = duration ? '-' + duration + 's' : '';
    applyInlineStyle(node, [TRANSITION_DELAY_PROP, value]);
    return [TRANSITION_DELAY_PROP, value];
  }
};

var $$rAFSchedulerFactory = ['$$rAF', function($$rAF) {
  var queue, cancelFn;

  function scheduler(tasks) {
    // we make a copy since RAFScheduler mutates the state
    // of the passed in array variable and this would be difficult
    // to track down on the outside code
    queue = queue.concat(tasks);
    nextTick();
  }

  queue = scheduler.queue = [];

  /* waitUntilQuiet does two things:
   * 1. It will run the FINAL `fn` value only when an uncanceled RAF has passed through
   * 2. It will delay the next wave of tasks from running until the quiet `fn` has run.
   *
   * The motivation here is that animation code can request more time from the scheduler
   * before the next wave runs. This allows for certain DOM properties such as classes to
   * be resolved in time for the next animation to run.
   */
  scheduler.waitUntilQuiet = function(fn) {
    if (cancelFn) cancelFn();

    cancelFn = $$rAF(function() {
      cancelFn = null;
      fn();
      nextTick();
    });
  };

  return scheduler;

  function nextTick() {
    if (!queue.length) return;

    var items = queue.shift();
    for (var i = 0; i < items.length; i++) {
      items[i]();
    }

    if (!cancelFn) {
      $$rAF(function() {
        if (!cancelFn) nextTick();
      });
    }
  }
}];

/**
 * @ngdoc directive
 * @name ngAnimateChildren
 * @restrict AE
 * @element ANY
 *
 * @description
 *
 * ngAnimateChildren allows you to specify that children of this element should animate even if any
 * of the children's parents are currently animating. By default, when an element has an active `enter`, `leave`, or `move`
 * (structural) animation, child elements that also have an active structural animation are not animated.
 *
 * Note that even if `ngAnimateChildren` is set, no child animations will run when the parent element is removed from the DOM (`leave` animation).
 *
 *
 * @param {string} ngAnimateChildren If the value is empty, `true` or `on`,
 *     then child animations are allowed. If the value is `false`, child animations are not allowed.
 *
 * @example
 * <example module="ngAnimateChildren" name="ngAnimateChildren" deps="angular-animate.js" animations="true">
     <file name="index.html">
       <div ng-controller="MainController as main">
         <label>Show container? <input type="checkbox" ng-model="main.enterElement" /></label>
         <label>Animate children? <input type="checkbox" ng-model="main.animateChildren" /></label>
         <hr>
         <div ng-animate-children="{{main.animateChildren}}">
           <div ng-if="main.enterElement" class="container">
             List of items:
             <div ng-repeat="item in [0, 1, 2, 3]" class="item">Item {{item}}</div>
           </div>
         </div>
       </div>
     </file>
     <file name="animations.css">

      .container.ng-enter,
      .container.ng-leave {
        transition: all ease 1.5s;
      }

      .container.ng-enter,
      .container.ng-leave-active {
        opacity: 0;
      }

      .container.ng-leave,
      .container.ng-enter-active {
        opacity: 1;
      }

      .item {
        background: firebrick;
        color: #FFF;
        margin-bottom: 10px;
      }

      .item.ng-enter,
      .item.ng-leave {
        transition: transform 1.5s ease;
      }

      .item.ng-enter {
        transform: translateX(50px);
      }

      .item.ng-enter-active {
        transform: translateX(0);
      }
    </file>
    <file name="script.js">
      angular.module('ngAnimateChildren', ['ngAnimate'])
        .controller('MainController', function MainController() {
          this.animateChildren = false;
          this.enterElement = false;
        });
    </file>
  </example>
 */
var $$AnimateChildrenDirective = ['$interpolate', function($interpolate) {
  return {
    link: function(scope, element, attrs) {
      var val = attrs.ngAnimateChildren;
      if (isString(val) && val.length === 0) { //empty attribute
        element.data(NG_ANIMATE_CHILDREN_DATA, true);
      } else {
        // Interpolate and set the value, so that it is available to
        // animations that run right after compilation
        setData($interpolate(val)(scope));
        attrs.$observe('ngAnimateChildren', setData);
      }

      function setData(value) {
        value = value === 'on' || value === 'true';
        element.data(NG_ANIMATE_CHILDREN_DATA, value);
      }
    }
  };
}];

/* exported $AnimateCssProvider */

var ANIMATE_TIMER_KEY = '$$animateCss';

/**
 * @ngdoc service
 * @name $animateCss
 * @kind object
 *
 * @description
 * The `$animateCss` service is a useful utility to trigger customized CSS-based transitions/keyframes
 * from a JavaScript-based animation or directly from a directive. The purpose of `$animateCss` is NOT
 * to side-step how `$animate` and ngAnimate work, but the goal is to allow pre-existing animations or
 * directives to create more complex animations that can be purely driven using CSS code.
 *
 * Note that only browsers that support CSS transitions and/or keyframe animations are capable of
 * rendering animations triggered via `$animateCss` (bad news for IE9 and lower).
 *
 * ## General Use
 * Once again, `$animateCss` is designed to be used inside of a registered JavaScript animation that
 * is powered by ngAnimate. It is possible to use `$animateCss` directly inside of a directive, however,
 * any automatic control over cancelling animations and/or preventing animations from being run on
 * child elements will not be handled by AngularJS. For this to work as expected, please use `$animate` to
 * trigger the animation and then setup a JavaScript animation that injects `$animateCss` to trigger
 * the CSS animation.
 *
 * The example below shows how we can create a folding animation on an element using `ng-if`:
 *
 * ```html
 * <!-- notice the `fold-animation` CSS class -->
 * <div ng-if="onOff" class="fold-animation">
 *   This element will go BOOM
 * </div>
 * <button ng-click="onOff=true">Fold In</button>
 * ```
 *
 * Now we create the **JavaScript animation** that will trigger the CSS transition:
 *
 * ```js
 * ngModule.animation('.fold-animation', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element, doneFn) {
 *       var height = element[0].offsetHeight;
 *       return $animateCss(element, {
 *         from: { height:'0px' },
 *         to: { height:height + 'px' },
 *         duration: 1 // one second
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * ## More Advanced Uses
 *
 * `$animateCss` is the underlying code that ngAnimate uses to power **CSS-based animations** behind the scenes. Therefore CSS hooks
 * like `.ng-EVENT`, `.ng-EVENT-active`, `.ng-EVENT-stagger` are all features that can be triggered using `$animateCss` via JavaScript code.
 *
 * This also means that just about any combination of adding classes, removing classes, setting styles, dynamically setting a keyframe animation,
 * applying a hardcoded duration or delay value, changing the animation easing or applying a stagger animation are all options that work with
 * `$animateCss`. The service itself is smart enough to figure out the combination of options and examine the element styling properties in order
 * to provide a working animation that will run in CSS.
 *
 * The example below showcases a more advanced version of the `.fold-animation` from the example above:
 *
 * ```js
 * ngModule.animation('.fold-animation', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element, doneFn) {
 *       var height = element[0].offsetHeight;
 *       return $animateCss(element, {
 *         addClass: 'red large-text pulse-twice',
 *         easing: 'ease-out',
 *         from: { height:'0px' },
 *         to: { height:height + 'px' },
 *         duration: 1 // one second
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * Since we're adding/removing CSS classes then the CSS transition will also pick those up:
 *
 * ```css
 * /&#42; since a hardcoded duration value of 1 was provided in the JavaScript animation code,
 * the CSS classes below will be transitioned despite them being defined as regular CSS classes &#42;/
 * .red { background:red; }
 * .large-text { font-size:20px; }
 *
 * /&#42; we can also use a keyframe animation and $animateCss will make it work alongside the transition &#42;/
 * .pulse-twice {
 *   animation: 0.5s pulse linear 2;
 *   -webkit-animation: 0.5s pulse linear 2;
 * }
 *
 * @keyframes pulse {
 *   from { transform: scale(0.5); }
 *   to { transform: scale(1.5); }
 * }
 *
 * @-webkit-keyframes pulse {
 *   from { -webkit-transform: scale(0.5); }
 *   to { -webkit-transform: scale(1.5); }
 * }
 * ```
 *
 * Given this complex combination of CSS classes, styles and options, `$animateCss` will figure everything out and make the animation happen.
 *
 * ## How the Options are handled
 *
 * `$animateCss` is very versatile and intelligent when it comes to figuring out what configurations to apply to the element to ensure the animation
 * works with the options provided. Say for example we were adding a class that contained a keyframe value and we wanted to also animate some inline
 * styles using the `from` and `to` properties.
 *
 * ```js
 * var animator = $animateCss(element, {
 *   from: { background:'red' },
 *   to: { background:'blue' }
 * });
 * animator.start();
 * ```
 *
 * ```css
 * .rotating-animation {
 *   animation:0.5s rotate linear;
 *   -webkit-animation:0.5s rotate linear;
 * }
 *
 * @keyframes rotate {
 *   from { transform: rotate(0deg); }
 *   to { transform: rotate(360deg); }
 * }
 *
 * @-webkit-keyframes rotate {
 *   from { -webkit-transform: rotate(0deg); }
 *   to { -webkit-transform: rotate(360deg); }
 * }
 * ```
 *
 * The missing pieces here are that we do not have a transition set (within the CSS code nor within the `$animateCss` options) and the duration of the animation is
 * going to be detected from what the keyframe styles on the CSS class are. In this event, `$animateCss` will automatically create an inline transition
 * style matching the duration detected from the keyframe style (which is present in the CSS class that is being added) and then prepare both the transition
 * and keyframe animations to run in parallel on the element. Then when the animation is underway the provided `from` and `to` CSS styles will be applied
 * and spread across the transition and keyframe animation.
 *
 * ## What is returned
 *
 * `$animateCss` works in two stages: a preparation phase and an animation phase. Therefore when `$animateCss` is first called it will NOT actually
 * start the animation. All that is going on here is that the element is being prepared for the animation (which means that the generated CSS classes are
 * added and removed on the element). Once `$animateCss` is called it will return an object with the following properties:
 *
 * ```js
 * var animator = $animateCss(element, { ... });
 * ```
 *
 * Now what do the contents of our `animator` variable look like:
 *
 * ```js
 * {
 *   // starts the animation
 *   start: Function,
 *
 *   // ends (aborts) the animation
 *   end: Function
 * }
 * ```
 *
 * To actually start the animation we need to run `animation.start()` which will then return a promise that we can hook into to detect when the animation ends.
 * If we choose not to run the animation then we MUST run `animation.end()` to perform a cleanup on the element (since some CSS classes and styles may have been
 * applied to the element during the preparation phase). Note that all other properties such as duration, delay, transitions and keyframes are just properties
 * and that changing them will not reconfigure the parameters of the animation.
 *
 * ### runner.done() vs runner.then()
 * It is documented that `animation.start()` will return a promise object and this is true, however, there is also an additional method available on the
 * runner called `.done(callbackFn)`. The done method works the same as `.finally(callbackFn)`, however, it does **not trigger a digest to occur**.
 * Therefore, for performance reasons, it's always best to use `runner.done(callback)` instead of `runner.then()`, `runner.catch()` or `runner.finally()`
 * unless you really need a digest to kick off afterwards.
 *
 * Keep in mind that, to make this easier, ngAnimate has tweaked the JS animations API to recognize when a runner instance is returned from $animateCss
 * (so there is no need to call `runner.done(doneFn)` inside of your JavaScript animation code).
 * Check the {@link ngAnimate.$animateCss#usage animation code above} to see how this works.
 *
 * @param {DOMElement} element the element that will be animated
 * @param {object} options the animation-related options that will be applied during the animation
 *
 * * `event` - The DOM event (e.g. enter, leave, move). When used, a generated CSS class of `ng-EVENT` and `ng-EVENT-active` will be applied
 * to the element during the animation. Multiple events can be provided when spaces are used as a separator. (Note that this will not perform any DOM operation.)
 * * `structural` - Indicates that the `ng-` prefix will be added to the event class. Setting to `false` or omitting will turn `ng-EVENT` and
 * `ng-EVENT-active` in `EVENT` and `EVENT-active`. Unused if `event` is omitted.
 * * `easing` - The CSS easing value that will be applied to the transition or keyframe animation (or both).
 * * `transitionStyle` - The raw CSS transition style that will be used (e.g. `1s linear all`).
 * * `keyframeStyle` - The raw CSS keyframe animation style that will be used (e.g. `1s my_animation linear`).
 * * `from` - The starting CSS styles (a key/value object) that will be applied at the start of the animation.
 * * `to` - The ending CSS styles (a key/value object) that will be applied across the animation via a CSS transition.
 * * `addClass` - A space separated list of CSS classes that will be added to the element and spread across the animation.
 * * `removeClass` - A space separated list of CSS classes that will be removed from the element and spread across the animation.
 * * `duration` - A number value representing the total duration of the transition and/or keyframe (note that a value of 1 is 1000ms). If a value of `0`
 * is provided then the animation will be skipped entirely.
 * * `delay` - A number value representing the total delay of the transition and/or keyframe (note that a value of 1 is 1000ms). If a value of `true` is
 * used then whatever delay value is detected from the CSS classes will be mirrored on the elements styles (e.g. by setting delay true then the style value
 * of the element will be `transition-delay: DETECTED_VALUE`). Using `true` is useful when you want the CSS classes and inline styles to all share the same
 * CSS delay value.
 * * `stagger` - A numeric time value representing the delay between successively animated elements
 * ({@link ngAnimate#css-staggering-animations Click here to learn how CSS-based staggering works in ngAnimate.})
 * * `staggerIndex` - The numeric index representing the stagger item (e.g. a value of 5 is equal to the sixth item in the stagger; therefore when a
 *   `stagger` option value of `0.1` is used then there will be a stagger delay of `600ms`)
 * * `applyClassesEarly` - Whether or not the classes being added or removed will be used when detecting the animation. This is set by `$animate` when enter/leave/move animations are fired to ensure that the CSS classes are resolved in time. (Note that this will prevent any transitions from occurring on the classes being added and removed.)
 * * `cleanupStyles` - Whether or not the provided `from` and `to` styles will be removed once
 *    the animation is closed. This is useful for when the styles are used purely for the sake of
 *    the animation and do not have a lasting visual effect on the element (e.g. a collapse and open animation).
 *    By default this value is set to `false`.
 *
 * @return {object} an object with start and end methods and details about the animation.
 *
 * * `start` - The method to start the animation. This will return a `Promise` when called.
 * * `end` - This method will cancel the animation and remove all applied CSS classes and styles.
 */
var ONE_SECOND = 1000;

var ELAPSED_TIME_MAX_DECIMAL_PLACES = 3;
var CLOSING_TIME_BUFFER = 1.5;

var DETECT_CSS_PROPERTIES = {
  transitionDuration:      TRANSITION_DURATION_PROP,
  transitionDelay:         TRANSITION_DELAY_PROP,
  transitionProperty:      TRANSITION_PROP + PROPERTY_KEY,
  animationDuration:       ANIMATION_DURATION_PROP,
  animationDelay:          ANIMATION_DELAY_PROP,
  animationIterationCount: ANIMATION_PROP + ANIMATION_ITERATION_COUNT_KEY
};

var DETECT_STAGGER_CSS_PROPERTIES = {
  transitionDuration:      TRANSITION_DURATION_PROP,
  transitionDelay:         TRANSITION_DELAY_PROP,
  animationDuration:       ANIMATION_DURATION_PROP,
  animationDelay:          ANIMATION_DELAY_PROP
};

function getCssKeyframeDurationStyle(duration) {
  return [ANIMATION_DURATION_PROP, duration + 's'];
}

function getCssDelayStyle(delay, isKeyframeAnimation) {
  var prop = isKeyframeAnimation ? ANIMATION_DELAY_PROP : TRANSITION_DELAY_PROP;
  return [prop, delay + 's'];
}

function computeCssStyles($window, element, properties) {
  var styles = Object.create(null);
  var detectedStyles = $window.getComputedStyle(element) || {};
  forEach(properties, function(formalStyleName, actualStyleName) {
    var val = detectedStyles[formalStyleName];
    if (val) {
      var c = val.charAt(0);

      // only numerical-based values have a negative sign or digit as the first value
      if (c === '-' || c === '+' || c >= 0) {
        val = parseMaxTime(val);
      }

      // by setting this to null in the event that the delay is not set or is set directly as 0
      // then we can still allow for negative values to be used later on and not mistake this
      // value for being greater than any other negative value.
      if (val === 0) {
        val = null;
      }
      styles[actualStyleName] = val;
    }
  });

  return styles;
}

function parseMaxTime(str) {
  var maxValue = 0;
  var values = str.split(/\s*,\s*/);
  forEach(values, function(value) {
    // it's always safe to consider only second values and omit `ms` values since
    // getComputedStyle will always handle the conversion for us
    if (value.charAt(value.length - 1) === 's') {
      value = value.substring(0, value.length - 1);
    }
    value = parseFloat(value) || 0;
    maxValue = maxValue ? Math.max(value, maxValue) : value;
  });
  return maxValue;
}

function truthyTimingValue(val) {
  return val === 0 || val != null;
}

function getCssTransitionDurationStyle(duration, applyOnlyDuration) {
  var style = TRANSITION_PROP;
  var value = duration + 's';
  if (applyOnlyDuration) {
    style += DURATION_KEY;
  } else {
    value += ' linear all';
  }
  return [style, value];
}

// we do not reassign an already present style value since
// if we detect the style property value again we may be
// detecting styles that were added via the `from` styles.
// We make use of `isDefined` here since an empty string
// or null value (which is what getPropertyValue will return
// for a non-existing style) will still be marked as a valid
// value for the style (a falsy value implies that the style
// is to be removed at the end of the animation). If we had a simple
// "OR" statement then it would not be enough to catch that.
function registerRestorableStyles(backup, node, properties) {
  forEach(properties, function(prop) {
    backup[prop] = isDefined(backup[prop])
        ? backup[prop]
        : node.style.getPropertyValue(prop);
  });
}

var $AnimateCssProvider = ['$animateProvider', /** @this */ function($animateProvider) {

  this.$get = ['$window', '$$jqLite', '$$AnimateRunner', '$timeout', '$$animateCache',
               '$$forceReflow', '$sniffer', '$$rAFScheduler', '$$animateQueue',
       function($window,   $$jqLite,   $$AnimateRunner,   $timeout,   $$animateCache,
                $$forceReflow,   $sniffer,   $$rAFScheduler, $$animateQueue) {

    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);

    function computeCachedCssStyles(node, className, cacheKey, allowNoDuration, properties) {
      var timings = $$animateCache.get(cacheKey);

      if (!timings) {
        timings = computeCssStyles($window, node, properties);
        if (timings.animationIterationCount === 'infinite') {
          timings.animationIterationCount = 1;
        }
      }

      // if a css animation has no duration we
      // should mark that so that repeated addClass/removeClass calls are skipped
      var hasDuration = allowNoDuration || (timings.transitionDuration > 0 || timings.animationDuration > 0);

      // we keep putting this in multiple times even though the value and the cacheKey are the same
      // because we're keeping an internal tally of how many duplicate animations are detected.
      $$animateCache.put(cacheKey, timings, hasDuration);

      return timings;
    }

    function computeCachedCssStaggerStyles(node, className, cacheKey, properties) {
      var stagger;
      var staggerCacheKey = 'stagger-' + cacheKey;

      // if we have one or more existing matches of matching elements
      // containing the same parent + CSS styles (which is how cacheKey works)
      // then staggering is possible
      if ($$animateCache.count(cacheKey) > 0) {
        stagger = $$animateCache.get(staggerCacheKey);

        if (!stagger) {
          var staggerClassName = pendClasses(className, '-stagger');

          $$jqLite.addClass(node, staggerClassName);

          stagger = computeCssStyles($window, node, properties);

          // force the conversion of a null value to zero incase not set
          stagger.animationDuration = Math.max(stagger.animationDuration, 0);
          stagger.transitionDuration = Math.max(stagger.transitionDuration, 0);

          $$jqLite.removeClass(node, staggerClassName);

          $$animateCache.put(staggerCacheKey, stagger, true);
        }
      }

      return stagger || {};
    }

    var rafWaitQueue = [];
    function waitUntilQuiet(callback) {
      rafWaitQueue.push(callback);
      $$rAFScheduler.waitUntilQuiet(function() {
        $$animateCache.flush();

        // DO NOT REMOVE THIS LINE OR REFACTOR OUT THE `pageWidth` variable.
        // PLEASE EXAMINE THE `$$forceReflow` service to understand why.
        var pageWidth = $$forceReflow();

        // we use a for loop to ensure that if the queue is changed
        // during this looping then it will consider new requests
        for (var i = 0; i < rafWaitQueue.length; i++) {
          rafWaitQueue[i](pageWidth);
        }
        rafWaitQueue.length = 0;
      });
    }

    function computeTimings(node, className, cacheKey, allowNoDuration) {
      var timings = computeCachedCssStyles(node, className, cacheKey, allowNoDuration, DETECT_CSS_PROPERTIES);
      var aD = timings.animationDelay;
      var tD = timings.transitionDelay;
      timings.maxDelay = aD && tD
          ? Math.max(aD, tD)
          : (aD || tD);
      timings.maxDuration = Math.max(
          timings.animationDuration * timings.animationIterationCount,
          timings.transitionDuration);

      return timings;
    }

    return function init(element, initialOptions) {
      // all of the animation functions should create
      // a copy of the options data, however, if a
      // parent service has already created a copy then
      // we should stick to using that
      var options = initialOptions || {};
      if (!options.$$prepared) {
        options = prepareAnimationOptions(copy(options));
      }

      var restoreStyles = {};
      var node = getDomNode(element);
      if (!node
          || !node.parentNode
          || !$$animateQueue.enabled()) {
        return closeAndReturnNoopAnimator();
      }

      var temporaryStyles = [];
      var classes = element.attr('class');
      var styles = packageStyles(options);
      var animationClosed;
      var animationPaused;
      var animationCompleted;
      var runner;
      var runnerHost;
      var maxDelay;
      var maxDelayTime;
      var maxDuration;
      var maxDurationTime;
      var startTime;
      var events = [];

      if (options.duration === 0 || (!$sniffer.animations && !$sniffer.transitions)) {
        return closeAndReturnNoopAnimator();
      }

      var method = options.event && isArray(options.event)
            ? options.event.join(' ')
            : options.event;

      var isStructural = method && options.structural;
      var structuralClassName = '';
      var addRemoveClassName = '';

      if (isStructural) {
        structuralClassName = pendClasses(method, EVENT_CLASS_PREFIX, true);
      } else if (method) {
        structuralClassName = method;
      }

      if (options.addClass) {
        addRemoveClassName += pendClasses(options.addClass, ADD_CLASS_SUFFIX);
      }

      if (options.removeClass) {
        if (addRemoveClassName.length) {
          addRemoveClassName += ' ';
        }
        addRemoveClassName += pendClasses(options.removeClass, REMOVE_CLASS_SUFFIX);
      }

      // there may be a situation where a structural animation is combined together
      // with CSS classes that need to resolve before the animation is computed.
      // However this means that there is no explicit CSS code to block the animation
      // from happening (by setting 0s none in the class name). If this is the case
      // we need to apply the classes before the first rAF so we know to continue if
      // there actually is a detected transition or keyframe animation
      if (options.applyClassesEarly && addRemoveClassName.length) {
        applyAnimationClasses(element, options);
      }

      var preparationClasses = [structuralClassName, addRemoveClassName].join(' ').trim();
      var fullClassName = classes + ' ' + preparationClasses;
      var hasToStyles = styles.to && Object.keys(styles.to).length > 0;
      var containsKeyframeAnimation = (options.keyframeStyle || '').length > 0;

      // there is no way we can trigger an animation if no styles and
      // no classes are being applied which would then trigger a transition,
      // unless there a is raw keyframe value that is applied to the element.
      if (!containsKeyframeAnimation
           && !hasToStyles
           && !preparationClasses) {
        return closeAndReturnNoopAnimator();
      }

      var stagger, cacheKey = $$animateCache.cacheKey(node, method, options.addClass, options.removeClass);
      if ($$animateCache.containsCachedAnimationWithoutDuration(cacheKey)) {
        preparationClasses = null;
        return closeAndReturnNoopAnimator();
      }

      if (options.stagger > 0) {
        var staggerVal = parseFloat(options.stagger);
        stagger = {
          transitionDelay: staggerVal,
          animationDelay: staggerVal,
          transitionDuration: 0,
          animationDuration: 0
        };
      } else {
        stagger = computeCachedCssStaggerStyles(node, preparationClasses, cacheKey, DETECT_STAGGER_CSS_PROPERTIES);
      }

      if (!options.$$skipPreparationClasses) {
        $$jqLite.addClass(element, preparationClasses);
      }

      var applyOnlyDuration;

      if (options.transitionStyle) {
        var transitionStyle = [TRANSITION_PROP, options.transitionStyle];
        applyInlineStyle(node, transitionStyle);
        temporaryStyles.push(transitionStyle);
      }

      if (options.duration >= 0) {
        applyOnlyDuration = node.style[TRANSITION_PROP].length > 0;
        var durationStyle = getCssTransitionDurationStyle(options.duration, applyOnlyDuration);

        // we set the duration so that it will be picked up by getComputedStyle later
        applyInlineStyle(node, durationStyle);
        temporaryStyles.push(durationStyle);
      }

      if (options.keyframeStyle) {
        var keyframeStyle = [ANIMATION_PROP, options.keyframeStyle];
        applyInlineStyle(node, keyframeStyle);
        temporaryStyles.push(keyframeStyle);
      }

      var itemIndex = stagger
          ? options.staggerIndex >= 0
              ? options.staggerIndex
              : $$animateCache.count(cacheKey)
          : 0;

      var isFirst = itemIndex === 0;

      // this is a pre-emptive way of forcing the setup classes to be added and applied INSTANTLY
      // without causing any combination of transitions to kick in. By adding a negative delay value
      // it forces the setup class' transition to end immediately. We later then remove the negative
      // transition delay to allow for the transition to naturally do it's thing. The beauty here is
      // that if there is no transition defined then nothing will happen and this will also allow
      // other transitions to be stacked on top of each other without any chopping them out.
      if (isFirst && !options.skipBlocking) {
        helpers.blockTransitions(node, SAFE_FAST_FORWARD_DURATION_VALUE);
      }

      var timings = computeTimings(node, fullClassName, cacheKey, !isStructural);
      var relativeDelay = timings.maxDelay;
      maxDelay = Math.max(relativeDelay, 0);
      maxDuration = timings.maxDuration;

      var flags = {};
      flags.hasTransitions          = timings.transitionDuration > 0;
      flags.hasAnimations           = timings.animationDuration > 0;
      flags.hasTransitionAll        = flags.hasTransitions && timings.transitionProperty === 'all';
      flags.applyTransitionDuration = hasToStyles && (
                                        (flags.hasTransitions && !flags.hasTransitionAll)
                                         || (flags.hasAnimations && !flags.hasTransitions));
      flags.applyAnimationDuration  = options.duration && flags.hasAnimations;
      flags.applyTransitionDelay    = truthyTimingValue(options.delay) && (flags.applyTransitionDuration || flags.hasTransitions);
      flags.applyAnimationDelay     = truthyTimingValue(options.delay) && flags.hasAnimations;
      flags.recalculateTimingStyles = addRemoveClassName.length > 0;

      if (flags.applyTransitionDuration || flags.applyAnimationDuration) {
        maxDuration = options.duration ? parseFloat(options.duration) : maxDuration;

        if (flags.applyTransitionDuration) {
          flags.hasTransitions = true;
          timings.transitionDuration = maxDuration;
          applyOnlyDuration = node.style[TRANSITION_PROP + PROPERTY_KEY].length > 0;
          temporaryStyles.push(getCssTransitionDurationStyle(maxDuration, applyOnlyDuration));
        }

        if (flags.applyAnimationDuration) {
          flags.hasAnimations = true;
          timings.animationDuration = maxDuration;
          temporaryStyles.push(getCssKeyframeDurationStyle(maxDuration));
        }
      }

      if (maxDuration === 0 && !flags.recalculateTimingStyles) {
        return closeAndReturnNoopAnimator();
      }

      var activeClasses = pendClasses(preparationClasses, ACTIVE_CLASS_SUFFIX);

      if (options.delay != null) {
        var delayStyle;
        if (typeof options.delay !== 'boolean') {
          delayStyle = parseFloat(options.delay);
          // number in options.delay means we have to recalculate the delay for the closing timeout
          maxDelay = Math.max(delayStyle, 0);
        }

        if (flags.applyTransitionDelay) {
          temporaryStyles.push(getCssDelayStyle(delayStyle));
        }

        if (flags.applyAnimationDelay) {
          temporaryStyles.push(getCssDelayStyle(delayStyle, true));
        }
      }

      // we need to recalculate the delay value since we used a pre-emptive negative
      // delay value and the delay value is required for the final event checking. This
      // property will ensure that this will happen after the RAF phase has passed.
      if (options.duration == null && timings.transitionDuration > 0) {
        flags.recalculateTimingStyles = flags.recalculateTimingStyles || isFirst;
      }

      maxDelayTime = maxDelay * ONE_SECOND;
      maxDurationTime = maxDuration * ONE_SECOND;
      if (!options.skipBlocking) {
        flags.blockTransition = timings.transitionDuration > 0;
        flags.blockKeyframeAnimation = timings.animationDuration > 0 &&
                                       stagger.animationDelay > 0 &&
                                       stagger.animationDuration === 0;
      }

      if (options.from) {
        if (options.cleanupStyles) {
          registerRestorableStyles(restoreStyles, node, Object.keys(options.from));
        }
        applyAnimationFromStyles(element, options);
      }

      if (flags.blockTransition || flags.blockKeyframeAnimation) {
        applyBlocking(maxDuration);
      } else if (!options.skipBlocking) {
        helpers.blockTransitions(node, false);
      }

      // TODO(matsko): for 1.5 change this code to have an animator object for better debugging
      return {
        $$willAnimate: true,
        end: endFn,
        start: function() {
          if (animationClosed) return;

          runnerHost = {
            end: endFn,
            cancel: cancelFn,
            resume: null, //this will be set during the start() phase
            pause: null
          };

          runner = new $$AnimateRunner(runnerHost);

          waitUntilQuiet(start);

          // we don't have access to pause/resume the animation
          // since it hasn't run yet. AnimateRunner will therefore
          // set noop functions for resume and pause and they will
          // later be overridden once the animation is triggered
          return runner;
        }
      };

      function endFn() {
        close();
      }

      function cancelFn() {
        close(true);
      }

      function close(rejected) {
        // if the promise has been called already then we shouldn't close
        // the animation again
        if (animationClosed || (animationCompleted && animationPaused)) return;
        animationClosed = true;
        animationPaused = false;

        if (preparationClasses && !options.$$skipPreparationClasses) {
          $$jqLite.removeClass(element, preparationClasses);
        }

        if (activeClasses) {
          $$jqLite.removeClass(element, activeClasses);
        }

        blockKeyframeAnimations(node, false);
        helpers.blockTransitions(node, false);

        forEach(temporaryStyles, function(entry) {
          // There is only one way to remove inline style properties entirely from elements.
          // By using `removeProperty` this works, but we need to convert camel-cased CSS
          // styles down to hyphenated values.
          node.style[entry[0]] = '';
        });

        applyAnimationClasses(element, options);
        applyAnimationStyles(element, options);

        if (Object.keys(restoreStyles).length) {
          forEach(restoreStyles, function(value, prop) {
            if (value) {
              node.style.setProperty(prop, value);
            } else {
              node.style.removeProperty(prop);
            }
          });
        }

        // the reason why we have this option is to allow a synchronous closing callback
        // that is fired as SOON as the animation ends (when the CSS is removed) or if
        // the animation never takes off at all. A good example is a leave animation since
        // the element must be removed just after the animation is over or else the element
        // will appear on screen for one animation frame causing an overbearing flicker.
        if (options.onDone) {
          options.onDone();
        }

        if (events && events.length) {
          // Remove the transitionend / animationend listener(s)
          element.off(events.join(' '), onAnimationProgress);
        }

        //Cancel the fallback closing timeout and remove the timer data
        var animationTimerData = element.data(ANIMATE_TIMER_KEY);
        if (animationTimerData) {
          $timeout.cancel(animationTimerData[0].timer);
          element.removeData(ANIMATE_TIMER_KEY);
        }

        // if the preparation function fails then the promise is not setup
        if (runner) {
          runner.complete(!rejected);
        }
      }

      function applyBlocking(duration) {
        if (flags.blockTransition) {
          helpers.blockTransitions(node, duration);
        }

        if (flags.blockKeyframeAnimation) {
          blockKeyframeAnimations(node, !!duration);
        }
      }

      function closeAndReturnNoopAnimator() {
        runner = new $$AnimateRunner({
          end: endFn,
          cancel: cancelFn
        });

        // should flush the cache animation
        waitUntilQuiet(noop);
        close();

        return {
          $$willAnimate: false,
          start: function() {
            return runner;
          },
          end: endFn
        };
      }

      function onAnimationProgress(event) {
        event.stopPropagation();
        var ev = event.originalEvent || event;

        if (ev.target !== node) {
          // Since TransitionEvent / AnimationEvent bubble up,
          // we have to ignore events by finished child animations
          return;
        }

        // we now always use `Date.now()` due to the recent changes with
        // event.timeStamp in Firefox, Webkit and Chrome (see #13494 for more info)
        var timeStamp = ev.$manualTimeStamp || Date.now();

        /* Firefox (or possibly just Gecko) likes to not round values up
         * when a ms measurement is used for the animation */
        var elapsedTime = parseFloat(ev.elapsedTime.toFixed(ELAPSED_TIME_MAX_DECIMAL_PLACES));

        /* $manualTimeStamp is a mocked timeStamp value which is set
         * within browserTrigger(). This is only here so that tests can
         * mock animations properly. Real events fallback to event.timeStamp,
         * or, if they don't, then a timeStamp is automatically created for them.
         * We're checking to see if the timeStamp surpasses the expected delay,
         * but we're using elapsedTime instead of the timeStamp on the 2nd
         * pre-condition since animationPauseds sometimes close off early */
        if (Math.max(timeStamp - startTime, 0) >= maxDelayTime && elapsedTime >= maxDuration) {
          // we set this flag to ensure that if the transition is paused then, when resumed,
          // the animation will automatically close itself since transitions cannot be paused.
          animationCompleted = true;
          close();
        }
      }

      function start() {
        if (animationClosed) return;
        if (!node.parentNode) {
          close();
          return;
        }

        // even though we only pause keyframe animations here the pause flag
        // will still happen when transitions are used. Only the transition will
        // not be paused since that is not possible. If the animation ends when
        // paused then it will not complete until unpaused or cancelled.
        var playPause = function(playAnimation) {
          if (!animationCompleted) {
            animationPaused = !playAnimation;
            if (timings.animationDuration) {
              var value = blockKeyframeAnimations(node, animationPaused);
              if (animationPaused) {
                temporaryStyles.push(value);
              } else {
                removeFromArray(temporaryStyles, value);
              }
            }
          } else if (animationPaused && playAnimation) {
            animationPaused = false;
            close();
          }
        };

        // checking the stagger duration prevents an accidentally cascade of the CSS delay style
        // being inherited from the parent. If the transition duration is zero then we can safely
        // rely that the delay value is an intentional stagger delay style.
        var maxStagger = itemIndex > 0
                         && ((timings.transitionDuration && stagger.transitionDuration === 0) ||
                            (timings.animationDuration && stagger.animationDuration === 0))
                         && Math.max(stagger.animationDelay, stagger.transitionDelay);
        if (maxStagger) {
          $timeout(triggerAnimationStart,
                   Math.floor(maxStagger * itemIndex * ONE_SECOND),
                   false);
        } else {
          triggerAnimationStart();
        }

        // this will decorate the existing promise runner with pause/resume methods
        runnerHost.resume = function() {
          playPause(true);
        };

        runnerHost.pause = function() {
          playPause(false);
        };

        function triggerAnimationStart() {
          // just incase a stagger animation kicks in when the animation
          // itself was cancelled entirely
          if (animationClosed) return;

          applyBlocking(false);

          forEach(temporaryStyles, function(entry) {
            var key = entry[0];
            var value = entry[1];
            node.style[key] = value;
          });

          applyAnimationClasses(element, options);
          $$jqLite.addClass(element, activeClasses);

          if (flags.recalculateTimingStyles) {
            fullClassName = node.getAttribute('class') + ' ' + preparationClasses;
            cacheKey = $$animateCache.cacheKey(node, method, options.addClass, options.removeClass);

            timings = computeTimings(node, fullClassName, cacheKey, false);
            relativeDelay = timings.maxDelay;
            maxDelay = Math.max(relativeDelay, 0);
            maxDuration = timings.maxDuration;

            if (maxDuration === 0) {
              close();
              return;
            }

            flags.hasTransitions = timings.transitionDuration > 0;
            flags.hasAnimations = timings.animationDuration > 0;
          }

          if (flags.applyAnimationDelay) {
            relativeDelay = typeof options.delay !== 'boolean' && truthyTimingValue(options.delay)
                  ? parseFloat(options.delay)
                  : relativeDelay;

            maxDelay = Math.max(relativeDelay, 0);
            timings.animationDelay = relativeDelay;
            delayStyle = getCssDelayStyle(relativeDelay, true);
            temporaryStyles.push(delayStyle);
            node.style[delayStyle[0]] = delayStyle[1];
          }

          maxDelayTime = maxDelay * ONE_SECOND;
          maxDurationTime = maxDuration * ONE_SECOND;

          if (options.easing) {
            var easeProp, easeVal = options.easing;
            if (flags.hasTransitions) {
              easeProp = TRANSITION_PROP + TIMING_KEY;
              temporaryStyles.push([easeProp, easeVal]);
              node.style[easeProp] = easeVal;
            }
            if (flags.hasAnimations) {
              easeProp = ANIMATION_PROP + TIMING_KEY;
              temporaryStyles.push([easeProp, easeVal]);
              node.style[easeProp] = easeVal;
            }
          }

          if (timings.transitionDuration) {
            events.push(TRANSITIONEND_EVENT);
          }

          if (timings.animationDuration) {
            events.push(ANIMATIONEND_EVENT);
          }

          startTime = Date.now();
          var timerTime = maxDelayTime + CLOSING_TIME_BUFFER * maxDurationTime;
          var endTime = startTime + timerTime;

          var animationsData = element.data(ANIMATE_TIMER_KEY) || [];
          var setupFallbackTimer = true;
          if (animationsData.length) {
            var currentTimerData = animationsData[0];
            setupFallbackTimer = endTime > currentTimerData.expectedEndTime;
            if (setupFallbackTimer) {
              $timeout.cancel(currentTimerData.timer);
            } else {
              animationsData.push(close);
            }
          }

          if (setupFallbackTimer) {
            var timer = $timeout(onAnimationExpired, timerTime, false);
            animationsData[0] = {
              timer: timer,
              expectedEndTime: endTime
            };
            animationsData.push(close);
            element.data(ANIMATE_TIMER_KEY, animationsData);
          }

          if (events.length) {
            element.on(events.join(' '), onAnimationProgress);
          }

          if (options.to) {
            if (options.cleanupStyles) {
              registerRestorableStyles(restoreStyles, node, Object.keys(options.to));
            }
            applyAnimationToStyles(element, options);
          }
        }

        function onAnimationExpired() {
          var animationsData = element.data(ANIMATE_TIMER_KEY);

          // this will be false in the event that the element was
          // removed from the DOM (via a leave animation or something
          // similar)
          if (animationsData) {
            for (var i = 1; i < animationsData.length; i++) {
              animationsData[i]();
            }
            element.removeData(ANIMATE_TIMER_KEY);
          }
        }
      }
    };
  }];
}];

var $$AnimateCssDriverProvider = ['$$animationProvider', /** @this */ function($$animationProvider) {
  $$animationProvider.drivers.push('$$animateCssDriver');

  var NG_ANIMATE_SHIM_CLASS_NAME = 'ng-animate-shim';
  var NG_ANIMATE_ANCHOR_CLASS_NAME = 'ng-anchor';

  var NG_OUT_ANCHOR_CLASS_NAME = 'ng-anchor-out';
  var NG_IN_ANCHOR_CLASS_NAME = 'ng-anchor-in';

  function isDocumentFragment(node) {
    return node.parentNode && node.parentNode.nodeType === 11;
  }

  this.$get = ['$animateCss', '$rootScope', '$$AnimateRunner', '$rootElement', '$sniffer', '$$jqLite', '$document',
       function($animateCss,   $rootScope,   $$AnimateRunner,   $rootElement,   $sniffer,   $$jqLite,   $document) {

    // only browsers that support these properties can render animations
    if (!$sniffer.animations && !$sniffer.transitions) return noop;

    var bodyNode = $document[0].body;
    var rootNode = getDomNode($rootElement);

    var rootBodyElement = jqLite(
      // this is to avoid using something that exists outside of the body
      // we also special case the doc fragment case because our unit test code
      // appends the $rootElement to the body after the app has been bootstrapped
      isDocumentFragment(rootNode) || bodyNode.contains(rootNode) ? rootNode : bodyNode
    );

    return function initDriverFn(animationDetails) {
      return animationDetails.from && animationDetails.to
          ? prepareFromToAnchorAnimation(animationDetails.from,
                                         animationDetails.to,
                                         animationDetails.classes,
                                         animationDetails.anchors)
          : prepareRegularAnimation(animationDetails);
    };

    function filterCssClasses(classes) {
      //remove all the `ng-` stuff
      return classes.replace(/\bng-\S+\b/g, '');
    }

    function getUniqueValues(a, b) {
      if (isString(a)) a = a.split(' ');
      if (isString(b)) b = b.split(' ');
      return a.filter(function(val) {
        return b.indexOf(val) === -1;
      }).join(' ');
    }

    function prepareAnchoredAnimation(classes, outAnchor, inAnchor) {
      var clone = jqLite(getDomNode(outAnchor).cloneNode(true));
      var startingClasses = filterCssClasses(getClassVal(clone));

      outAnchor.addClass(NG_ANIMATE_SHIM_CLASS_NAME);
      inAnchor.addClass(NG_ANIMATE_SHIM_CLASS_NAME);

      clone.addClass(NG_ANIMATE_ANCHOR_CLASS_NAME);

      rootBodyElement.append(clone);

      var animatorIn, animatorOut = prepareOutAnimation();

      // the user may not end up using the `out` animation and
      // only making use of the `in` animation or vice-versa.
      // In either case we should allow this and not assume the
      // animation is over unless both animations are not used.
      if (!animatorOut) {
        animatorIn = prepareInAnimation();
        if (!animatorIn) {
          return end();
        }
      }

      var startingAnimator = animatorOut || animatorIn;

      return {
        start: function() {
          var runner;

          var currentAnimation = startingAnimator.start();
          currentAnimation.done(function() {
            currentAnimation = null;
            if (!animatorIn) {
              animatorIn = prepareInAnimation();
              if (animatorIn) {
                currentAnimation = animatorIn.start();
                currentAnimation.done(function() {
                  currentAnimation = null;
                  end();
                  runner.complete();
                });
                return currentAnimation;
              }
            }
            // in the event that there is no `in` animation
            end();
            runner.complete();
          });

          runner = new $$AnimateRunner({
            end: endFn,
            cancel: endFn
          });

          return runner;

          function endFn() {
            if (currentAnimation) {
              currentAnimation.end();
            }
          }
        }
      };

      function calculateAnchorStyles(anchor) {
        var styles = {};

        var coords = getDomNode(anchor).getBoundingClientRect();

        // we iterate directly since safari messes up and doesn't return
        // all the keys for the coords object when iterated
        forEach(['width','height','top','left'], function(key) {
          var value = coords[key];
          switch (key) {
            case 'top':
              value += bodyNode.scrollTop;
              break;
            case 'left':
              value += bodyNode.scrollLeft;
              break;
          }
          styles[key] = Math.floor(value) + 'px';
        });
        return styles;
      }

      function prepareOutAnimation() {
        var animator = $animateCss(clone, {
          addClass: NG_OUT_ANCHOR_CLASS_NAME,
          delay: true,
          from: calculateAnchorStyles(outAnchor)
        });

        // read the comment within `prepareRegularAnimation` to understand
        // why this check is necessary
        return animator.$$willAnimate ? animator : null;
      }

      function getClassVal(element) {
        return element.attr('class') || '';
      }

      function prepareInAnimation() {
        var endingClasses = filterCssClasses(getClassVal(inAnchor));
        var toAdd = getUniqueValues(endingClasses, startingClasses);
        var toRemove = getUniqueValues(startingClasses, endingClasses);

        var animator = $animateCss(clone, {
          to: calculateAnchorStyles(inAnchor),
          addClass: NG_IN_ANCHOR_CLASS_NAME + ' ' + toAdd,
          removeClass: NG_OUT_ANCHOR_CLASS_NAME + ' ' + toRemove,
          delay: true
        });

        // read the comment within `prepareRegularAnimation` to understand
        // why this check is necessary
        return animator.$$willAnimate ? animator : null;
      }

      function end() {
        clone.remove();
        outAnchor.removeClass(NG_ANIMATE_SHIM_CLASS_NAME);
        inAnchor.removeClass(NG_ANIMATE_SHIM_CLASS_NAME);
      }
    }

    function prepareFromToAnchorAnimation(from, to, classes, anchors) {
      var fromAnimation = prepareRegularAnimation(from, noop);
      var toAnimation = prepareRegularAnimation(to, noop);

      var anchorAnimations = [];
      forEach(anchors, function(anchor) {
        var outElement = anchor['out'];
        var inElement = anchor['in'];
        var animator = prepareAnchoredAnimation(classes, outElement, inElement);
        if (animator) {
          anchorAnimations.push(animator);
        }
      });

      // no point in doing anything when there are no elements to animate
      if (!fromAnimation && !toAnimation && anchorAnimations.length === 0) return;

      return {
        start: function() {
          var animationRunners = [];

          if (fromAnimation) {
            animationRunners.push(fromAnimation.start());
          }

          if (toAnimation) {
            animationRunners.push(toAnimation.start());
          }

          forEach(anchorAnimations, function(animation) {
            animationRunners.push(animation.start());
          });

          var runner = new $$AnimateRunner({
            end: endFn,
            cancel: endFn // CSS-driven animations cannot be cancelled, only ended
          });

          $$AnimateRunner.all(animationRunners, function(status) {
            runner.complete(status);
          });

          return runner;

          function endFn() {
            forEach(animationRunners, function(runner) {
              runner.end();
            });
          }
        }
      };
    }

    function prepareRegularAnimation(animationDetails) {
      var element = animationDetails.element;
      var options = animationDetails.options || {};

      if (animationDetails.structural) {
        options.event = animationDetails.event;
        options.structural = true;
        options.applyClassesEarly = true;

        // we special case the leave animation since we want to ensure that
        // the element is removed as soon as the animation is over. Otherwise
        // a flicker might appear or the element may not be removed at all
        if (animationDetails.event === 'leave') {
          options.onDone = options.domOperation;
        }
      }

      // We assign the preparationClasses as the actual animation event since
      // the internals of $animateCss will just suffix the event token values
      // with `-active` to trigger the animation.
      if (options.preparationClasses) {
        options.event = concatWithSpace(options.event, options.preparationClasses);
      }

      var animator = $animateCss(element, options);

      // the driver lookup code inside of $$animation attempts to spawn a
      // driver one by one until a driver returns a.$$willAnimate animator object.
      // $animateCss will always return an object, however, it will pass in
      // a flag as a hint as to whether an animation was detected or not
      return animator.$$willAnimate ? animator : null;
    }
  }];
}];

// TODO(matsko): use caching here to speed things up for detection
// TODO(matsko): add documentation
//  by the time...

var $$AnimateJsProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  this.$get = ['$injector', '$$AnimateRunner', '$$jqLite',
       function($injector,   $$AnimateRunner,   $$jqLite) {

    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);
         // $animateJs(element, 'enter');
    return function(element, event, classes, options) {
      var animationClosed = false;

      // the `classes` argument is optional and if it is not used
      // then the classes will be resolved from the element's className
      // property as well as options.addClass/options.removeClass.
      if (arguments.length === 3 && isObject(classes)) {
        options = classes;
        classes = null;
      }

      options = prepareAnimationOptions(options);
      if (!classes) {
        classes = element.attr('class') || '';
        if (options.addClass) {
          classes += ' ' + options.addClass;
        }
        if (options.removeClass) {
          classes += ' ' + options.removeClass;
        }
      }

      var classesToAdd = options.addClass;
      var classesToRemove = options.removeClass;

      // the lookupAnimations function returns a series of animation objects that are
      // matched up with one or more of the CSS classes. These animation objects are
      // defined via the module.animation factory function. If nothing is detected then
      // we don't return anything which then makes $animation query the next driver.
      var animations = lookupAnimations(classes);
      var before, after;
      if (animations.length) {
        var afterFn, beforeFn;
        if (event === 'leave') {
          beforeFn = 'leave';
          afterFn = 'afterLeave'; // TODO(matsko): get rid of this
        } else {
          beforeFn = 'before' + event.charAt(0).toUpperCase() + event.substr(1);
          afterFn = event;
        }

        if (event !== 'enter' && event !== 'move') {
          before = packageAnimations(element, event, options, animations, beforeFn);
        }
        after  = packageAnimations(element, event, options, animations, afterFn);
      }

      // no matching animations
      if (!before && !after) return;

      function applyOptions() {
        options.domOperation();
        applyAnimationClasses(element, options);
      }

      function close() {
        animationClosed = true;
        applyOptions();
        applyAnimationStyles(element, options);
      }

      var runner;

      return {
        $$willAnimate: true,
        end: function() {
          if (runner) {
            runner.end();
          } else {
            close();
            runner = new $$AnimateRunner();
            runner.complete(true);
          }
          return runner;
        },
        start: function() {
          if (runner) {
            return runner;
          }

          runner = new $$AnimateRunner();
          var closeActiveAnimations;
          var chain = [];

          if (before) {
            chain.push(function(fn) {
              closeActiveAnimations = before(fn);
            });
          }

          if (chain.length) {
            chain.push(function(fn) {
              applyOptions();
              fn(true);
            });
          } else {
            applyOptions();
          }

          if (after) {
            chain.push(function(fn) {
              closeActiveAnimations = after(fn);
            });
          }

          runner.setHost({
            end: function() {
              endAnimations();
            },
            cancel: function() {
              endAnimations(true);
            }
          });

          $$AnimateRunner.chain(chain, onComplete);
          return runner;

          function onComplete(success) {
            close(success);
            runner.complete(success);
          }

          function endAnimations(cancelled) {
            if (!animationClosed) {
              (closeActiveAnimations || noop)(cancelled);
              onComplete(cancelled);
            }
          }
        }
      };

      function executeAnimationFn(fn, element, event, options, onDone) {
        var args;
        switch (event) {
          case 'animate':
            args = [element, options.from, options.to, onDone];
            break;

          case 'setClass':
            args = [element, classesToAdd, classesToRemove, onDone];
            break;

          case 'addClass':
            args = [element, classesToAdd, onDone];
            break;

          case 'removeClass':
            args = [element, classesToRemove, onDone];
            break;

          default:
            args = [element, onDone];
            break;
        }

        args.push(options);

        var value = fn.apply(fn, args);
        if (value) {
          if (isFunction(value.start)) {
            value = value.start();
          }

          if (value instanceof $$AnimateRunner) {
            value.done(onDone);
          } else if (isFunction(value)) {
            // optional onEnd / onCancel callback
            return value;
          }
        }

        return noop;
      }

      function groupEventedAnimations(element, event, options, animations, fnName) {
        var operations = [];
        forEach(animations, function(ani) {
          var animation = ani[fnName];
          if (!animation) return;

          // note that all of these animations will run in parallel
          operations.push(function() {
            var runner;
            var endProgressCb;

            var resolved = false;
            var onAnimationComplete = function(rejected) {
              if (!resolved) {
                resolved = true;
                (endProgressCb || noop)(rejected);
                runner.complete(!rejected);
              }
            };

            runner = new $$AnimateRunner({
              end: function() {
                onAnimationComplete();
              },
              cancel: function() {
                onAnimationComplete(true);
              }
            });

            endProgressCb = executeAnimationFn(animation, element, event, options, function(result) {
              var cancelled = result === false;
              onAnimationComplete(cancelled);
            });

            return runner;
          });
        });

        return operations;
      }

      function packageAnimations(element, event, options, animations, fnName) {
        var operations = groupEventedAnimations(element, event, options, animations, fnName);
        if (operations.length === 0) {
          var a, b;
          if (fnName === 'beforeSetClass') {
            a = groupEventedAnimations(element, 'removeClass', options, animations, 'beforeRemoveClass');
            b = groupEventedAnimations(element, 'addClass', options, animations, 'beforeAddClass');
          } else if (fnName === 'setClass') {
            a = groupEventedAnimations(element, 'removeClass', options, animations, 'removeClass');
            b = groupEventedAnimations(element, 'addClass', options, animations, 'addClass');
          }

          if (a) {
            operations = operations.concat(a);
          }
          if (b) {
            operations = operations.concat(b);
          }
        }

        if (operations.length === 0) return;

        // TODO(matsko): add documentation
        return function startAnimation(callback) {
          var runners = [];
          if (operations.length) {
            forEach(operations, function(animateFn) {
              runners.push(animateFn());
            });
          }

          if (runners.length) {
            $$AnimateRunner.all(runners, callback);
          }  else {
            callback();
          }

          return function endFn(reject) {
            forEach(runners, function(runner) {
              if (reject) {
                runner.cancel();
              } else {
                runner.end();
              }
            });
          };
        };
      }
    };

    function lookupAnimations(classes) {
      classes = isArray(classes) ? classes : classes.split(' ');
      var matches = [], flagMap = {};
      for (var i = 0; i < classes.length; i++) {
        var klass = classes[i],
            animationFactory = $animateProvider.$$registeredAnimations[klass];
        if (animationFactory && !flagMap[klass]) {
          matches.push($injector.get(animationFactory));
          flagMap[klass] = true;
        }
      }
      return matches;
    }
  }];
}];

var $$AnimateJsDriverProvider = ['$$animationProvider', /** @this */ function($$animationProvider) {
  $$animationProvider.drivers.push('$$animateJsDriver');
  this.$get = ['$$animateJs', '$$AnimateRunner', function($$animateJs, $$AnimateRunner) {
    return function initDriverFn(animationDetails) {
      if (animationDetails.from && animationDetails.to) {
        var fromAnimation = prepareAnimation(animationDetails.from);
        var toAnimation = prepareAnimation(animationDetails.to);
        if (!fromAnimation && !toAnimation) return;

        return {
          start: function() {
            var animationRunners = [];

            if (fromAnimation) {
              animationRunners.push(fromAnimation.start());
            }

            if (toAnimation) {
              animationRunners.push(toAnimation.start());
            }

            $$AnimateRunner.all(animationRunners, done);

            var runner = new $$AnimateRunner({
              end: endFnFactory(),
              cancel: endFnFactory()
            });

            return runner;

            function endFnFactory() {
              return function() {
                forEach(animationRunners, function(runner) {
                  // at this point we cannot cancel animations for groups just yet. 1.5+
                  runner.end();
                });
              };
            }

            function done(status) {
              runner.complete(status);
            }
          }
        };
      } else {
        return prepareAnimation(animationDetails);
      }
    };

    function prepareAnimation(animationDetails) {
      // TODO(matsko): make sure to check for grouped animations and delegate down to normal animations
      var element = animationDetails.element;
      var event = animationDetails.event;
      var options = animationDetails.options;
      var classes = animationDetails.classes;
      return $$animateJs(element, event, classes, options);
    }
  }];
}];

var NG_ANIMATE_ATTR_NAME = 'data-ng-animate';
var NG_ANIMATE_PIN_DATA = '$ngAnimatePin';
var $$AnimateQueueProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  var PRE_DIGEST_STATE = 1;
  var RUNNING_STATE = 2;
  var ONE_SPACE = ' ';

  var rules = this.rules = {
    skip: [],
    cancel: [],
    join: []
  };

  function getEventData(options) {
    return {
      addClass: options.addClass,
      removeClass: options.removeClass,
      from: options.from,
      to: options.to
    };
  }

  function makeTruthyCssClassMap(classString) {
    if (!classString) {
      return null;
    }

    var keys = classString.split(ONE_SPACE);
    var map = Object.create(null);

    forEach(keys, function(key) {
      map[key] = true;
    });
    return map;
  }

  function hasMatchingClasses(newClassString, currentClassString) {
    if (newClassString && currentClassString) {
      var currentClassMap = makeTruthyCssClassMap(currentClassString);
      return newClassString.split(ONE_SPACE).some(function(className) {
        return currentClassMap[className];
      });
    }
  }

  function isAllowed(ruleType, currentAnimation, previousAnimation) {
    return rules[ruleType].some(function(fn) {
      return fn(currentAnimation, previousAnimation);
    });
  }

  function hasAnimationClasses(animation, and) {
    var a = (animation.addClass || '').length > 0;
    var b = (animation.removeClass || '').length > 0;
    return and ? a && b : a || b;
  }

  rules.join.push(function(newAnimation, currentAnimation) {
    // if the new animation is class-based then we can just tack that on
    return !newAnimation.structural && hasAnimationClasses(newAnimation);
  });

  rules.skip.push(function(newAnimation, currentAnimation) {
    // there is no need to animate anything if no classes are being added and
    // there is no structural animation that will be triggered
    return !newAnimation.structural && !hasAnimationClasses(newAnimation);
  });

  rules.skip.push(function(newAnimation, currentAnimation) {
    // why should we trigger a new structural animation if the element will
    // be removed from the DOM anyway?
    return currentAnimation.event === 'leave' && newAnimation.structural;
  });

  rules.skip.push(function(newAnimation, currentAnimation) {
    // if there is an ongoing current animation then don't even bother running the class-based animation
    return currentAnimation.structural && currentAnimation.state === RUNNING_STATE && !newAnimation.structural;
  });

  rules.cancel.push(function(newAnimation, currentAnimation) {
    // there can never be two structural animations running at the same time
    return currentAnimation.structural && newAnimation.structural;
  });

  rules.cancel.push(function(newAnimation, currentAnimation) {
    // if the previous animation is already running, but the new animation will
    // be triggered, but the new animation is structural
    return currentAnimation.state === RUNNING_STATE && newAnimation.structural;
  });

  rules.cancel.push(function(newAnimation, currentAnimation) {
    // cancel the animation if classes added / removed in both animation cancel each other out,
    // but only if the current animation isn't structural

    if (currentAnimation.structural) return false;

    var nA = newAnimation.addClass;
    var nR = newAnimation.removeClass;
    var cA = currentAnimation.addClass;
    var cR = currentAnimation.removeClass;

    // early detection to save the global CPU shortage :)
    if ((isUndefined(nA) && isUndefined(nR)) || (isUndefined(cA) && isUndefined(cR))) {
      return false;
    }

    return hasMatchingClasses(nA, cR) || hasMatchingClasses(nR, cA);
  });

  this.$get = ['$$rAF', '$rootScope', '$rootElement', '$document', '$$Map',
               '$$animation', '$$AnimateRunner', '$templateRequest', '$$jqLite', '$$forceReflow',
               '$$isDocumentHidden',
       function($$rAF,   $rootScope,   $rootElement,   $document,   $$Map,
                $$animation,   $$AnimateRunner,   $templateRequest,   $$jqLite,   $$forceReflow,
                $$isDocumentHidden) {

    var activeAnimationsLookup = new $$Map();
    var disabledElementsLookup = new $$Map();
    var animationsEnabled = null;

    function removeFromDisabledElementsLookup(evt) {
      disabledElementsLookup.delete(evt.target);
    }

    function postDigestTaskFactory() {
      var postDigestCalled = false;
      return function(fn) {
        // we only issue a call to postDigest before
        // it has first passed. This prevents any callbacks
        // from not firing once the animation has completed
        // since it will be out of the digest cycle.
        if (postDigestCalled) {
          fn();
        } else {
          $rootScope.$$postDigest(function() {
            postDigestCalled = true;
            fn();
          });
        }
      };
    }

    // Wait until all directive and route-related templates are downloaded and
    // compiled. The $templateRequest.totalPendingRequests variable keeps track of
    // all of the remote templates being currently downloaded. If there are no
    // templates currently downloading then the watcher will still fire anyway.
    var deregisterWatch = $rootScope.$watch(
      function() { return $templateRequest.totalPendingRequests === 0; },
      function(isEmpty) {
        if (!isEmpty) return;
        deregisterWatch();

        // Now that all templates have been downloaded, $animate will wait until
        // the post digest queue is empty before enabling animations. By having two
        // calls to $postDigest calls we can ensure that the flag is enabled at the
        // very end of the post digest queue. Since all of the animations in $animate
        // use $postDigest, it's important that the code below executes at the end.
        // This basically means that the page is fully downloaded and compiled before
        // any animations are triggered.
        $rootScope.$$postDigest(function() {
          $rootScope.$$postDigest(function() {
            // we check for null directly in the event that the application already called
            // .enabled() with whatever arguments that it provided it with
            if (animationsEnabled === null) {
              animationsEnabled = true;
            }
          });
        });
      }
    );

    var callbackRegistry = Object.create(null);

    // remember that the `customFilter`/`classNameFilter` are set during the
    // provider/config stage therefore we can optimize here and setup helper functions
    var customFilter = $animateProvider.customFilter();
    var classNameFilter = $animateProvider.classNameFilter();
    var returnTrue = function() { return true; };

    var isAnimatableByFilter = customFilter || returnTrue;
    var isAnimatableClassName = !classNameFilter ? returnTrue : function(node, options) {
      var className = [node.getAttribute('class'), options.addClass, options.removeClass].join(' ');
      return classNameFilter.test(className);
    };

    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);

    function normalizeAnimationDetails(element, animation) {
      return mergeAnimationDetails(element, animation, {});
    }

    // IE9-11 has no method "contains" in SVG element and in Node.prototype. Bug #10259.
    var contains = window.Node.prototype.contains || /** @this */ function(arg) {
      // eslint-disable-next-line no-bitwise
      return this === arg || !!(this.compareDocumentPosition(arg) & 16);
    };

    function findCallbacks(targetParentNode, targetNode, event) {
      var matches = [];
      var entries = callbackRegistry[event];
      if (entries) {
        forEach(entries, function(entry) {
          if (contains.call(entry.node, targetNode)) {
            matches.push(entry.callback);
          } else if (event === 'leave' && contains.call(entry.node, targetParentNode)) {
            matches.push(entry.callback);
          }
        });
      }

      return matches;
    }

    function filterFromRegistry(list, matchContainer, matchCallback) {
      var containerNode = extractElementNode(matchContainer);
      return list.filter(function(entry) {
        var isMatch = entry.node === containerNode &&
                        (!matchCallback || entry.callback === matchCallback);
        return !isMatch;
      });
    }

    function cleanupEventListeners(phase, node) {
      if (phase === 'close' && !node.parentNode) {
        // If the element is not attached to a parentNode, it has been removed by
        // the domOperation, and we can safely remove the event callbacks
        $animate.off(node);
      }
    }

    var $animate = {
      on: function(event, container, callback) {
        var node = extractElementNode(container);
        callbackRegistry[event] = callbackRegistry[event] || [];
        callbackRegistry[event].push({
          node: node,
          callback: callback
        });

        // Remove the callback when the element is removed from the DOM
        jqLite(container).on('$destroy', function() {
          var animationDetails = activeAnimationsLookup.get(node);

          if (!animationDetails) {
            // If there's an animation ongoing, the callback calling code will remove
            // the event listeners. If we'd remove here, the callbacks would be removed
            // before the animation ends
            $animate.off(event, container, callback);
          }
        });
      },

      off: function(event, container, callback) {
        if (arguments.length === 1 && !isString(arguments[0])) {
          container = arguments[0];
          for (var eventType in callbackRegistry) {
            callbackRegistry[eventType] = filterFromRegistry(callbackRegistry[eventType], container);
          }

          return;
        }

        var entries = callbackRegistry[event];
        if (!entries) return;

        callbackRegistry[event] = arguments.length === 1
            ? null
            : filterFromRegistry(entries, container, callback);
      },

      pin: function(element, parentElement) {
        assertArg(isElement(element), 'element', 'not an element');
        assertArg(isElement(parentElement), 'parentElement', 'not an element');
        element.data(NG_ANIMATE_PIN_DATA, parentElement);
      },

      push: function(element, event, options, domOperation) {
        options = options || {};
        options.domOperation = domOperation;
        return queueAnimation(element, event, options);
      },

      // this method has four signatures:
      //  () - global getter
      //  (bool) - global setter
      //  (element) - element getter
      //  (element, bool) - element setter<F37>
      enabled: function(element, bool) {
        var argCount = arguments.length;

        if (argCount === 0) {
          // () - Global getter
          bool = !!animationsEnabled;
        } else {
          var hasElement = isElement(element);

          if (!hasElement) {
            // (bool) - Global setter
            bool = animationsEnabled = !!element;
          } else {
            var node = getDomNode(element);

            if (argCount === 1) {
              // (element) - Element getter
              bool = !disabledElementsLookup.get(node);
            } else {
              // (element, bool) - Element setter
              if (!disabledElementsLookup.has(node)) {
                // The element is added to the map for the first time.
                // Create a listener to remove it on `$destroy` (to avoid memory leak).
                jqLite(element).on('$destroy', removeFromDisabledElementsLookup);
              }
              disabledElementsLookup.set(node, !bool);
            }
          }
        }

        return bool;
      }
    };

    return $animate;

    function queueAnimation(originalElement, event, initialOptions) {
      // we always make a copy of the options since
      // there should never be any side effects on
      // the input data when running `$animateCss`.
      var options = copy(initialOptions);

      var element = stripCommentsFromElement(originalElement);
      var node = getDomNode(element);
      var parentNode = node && node.parentNode;

      options = prepareAnimationOptions(options);

      // we create a fake runner with a working promise.
      // These methods will become available after the digest has passed
      var runner = new $$AnimateRunner();

      // this is used to trigger callbacks in postDigest mode
      var runInNextPostDigestOrNow = postDigestTaskFactory();

      if (isArray(options.addClass)) {
        options.addClass = options.addClass.join(' ');
      }

      if (options.addClass && !isString(options.addClass)) {
        options.addClass = null;
      }

      if (isArray(options.removeClass)) {
        options.removeClass = options.removeClass.join(' ');
      }

      if (options.removeClass && !isString(options.removeClass)) {
        options.removeClass = null;
      }

      if (options.from && !isObject(options.from)) {
        options.from = null;
      }

      if (options.to && !isObject(options.to)) {
        options.to = null;
      }

      // If animations are hard-disabled for the whole application there is no need to continue.
      // There are also situations where a directive issues an animation for a jqLite wrapper that
      // contains only comment nodes. In this case, there is no way we can perform an animation.
      if (!animationsEnabled ||
          !node ||
          !isAnimatableByFilter(node, event, initialOptions) ||
          !isAnimatableClassName(node, options)) {
        close();
        return runner;
      }

      var isStructural = ['enter', 'move', 'leave'].indexOf(event) >= 0;

      var documentHidden = $$isDocumentHidden();

      // This is a hard disable of all animations the element itself, therefore  there is no need to
      // continue further past this point if not enabled
      // Animations are also disabled if the document is currently hidden (page is not visible
      // to the user), because browsers slow down or do not flush calls to requestAnimationFrame
      var skipAnimations = documentHidden || disabledElementsLookup.get(node);
      var existingAnimation = (!skipAnimations && activeAnimationsLookup.get(node)) || {};
      var hasExistingAnimation = !!existingAnimation.state;

      // there is no point in traversing the same collection of parent ancestors if a followup
      // animation will be run on the same element that already did all that checking work
      if (!skipAnimations && (!hasExistingAnimation || existingAnimation.state !== PRE_DIGEST_STATE)) {
        skipAnimations = !areAnimationsAllowed(node, parentNode, event);
      }

      if (skipAnimations) {
        // Callbacks should fire even if the document is hidden (regression fix for issue #14120)
        if (documentHidden) notifyProgress(runner, event, 'start', getEventData(options));
        close();
        if (documentHidden) notifyProgress(runner, event, 'close', getEventData(options));
        return runner;
      }

      if (isStructural) {
        closeChildAnimations(node);
      }

      var newAnimation = {
        structural: isStructural,
        element: element,
        event: event,
        addClass: options.addClass,
        removeClass: options.removeClass,
        close: close,
        options: options,
        runner: runner
      };

      if (hasExistingAnimation) {
        var skipAnimationFlag = isAllowed('skip', newAnimation, existingAnimation);
        if (skipAnimationFlag) {
          if (existingAnimation.state === RUNNING_STATE) {
            close();
            return runner;
          } else {
            mergeAnimationDetails(element, existingAnimation, newAnimation);
            return existingAnimation.runner;
          }
        }
        var cancelAnimationFlag = isAllowed('cancel', newAnimation, existingAnimation);
        if (cancelAnimationFlag) {
          if (existingAnimation.state === RUNNING_STATE) {
            // this will end the animation right away and it is safe
            // to do so since the animation is already running and the
            // runner callback code will run in async
            existingAnimation.runner.end();
          } else if (existingAnimation.structural) {
            // this means that the animation is queued into a digest, but
            // hasn't started yet. Therefore it is safe to run the close
            // method which will call the runner methods in async.
            existingAnimation.close();
          } else {
            // this will merge the new animation options into existing animation options
            mergeAnimationDetails(element, existingAnimation, newAnimation);

            return existingAnimation.runner;
          }
        } else {
          // a joined animation means that this animation will take over the existing one
          // so an example would involve a leave animation taking over an enter. Then when
          // the postDigest kicks in the enter will be ignored.
          var joinAnimationFlag = isAllowed('join', newAnimation, existingAnimation);
          if (joinAnimationFlag) {
            if (existingAnimation.state === RUNNING_STATE) {
              normalizeAnimationDetails(element, newAnimation);
            } else {
              applyGeneratedPreparationClasses($$jqLite, element, isStructural ? event : null, options);

              event = newAnimation.event = existingAnimation.event;
              options = mergeAnimationDetails(element, existingAnimation, newAnimation);

              //we return the same runner since only the option values of this animation will
              //be fed into the `existingAnimation`.
              return existingAnimation.runner;
            }
          }
        }
      } else {
        // normalization in this case means that it removes redundant CSS classes that
        // already exist (addClass) or do not exist (removeClass) on the element
        normalizeAnimationDetails(element, newAnimation);
      }

      // when the options are merged and cleaned up we may end up not having to do
      // an animation at all, therefore we should check this before issuing a post
      // digest callback. Structural animations will always run no matter what.
      var isValidAnimation = newAnimation.structural;
      if (!isValidAnimation) {
        // animate (from/to) can be quickly checked first, otherwise we check if any classes are present
        isValidAnimation = (newAnimation.event === 'animate' && Object.keys(newAnimation.options.to || {}).length > 0)
                            || hasAnimationClasses(newAnimation);
      }

      if (!isValidAnimation) {
        close();
        clearElementAnimationState(node);
        return runner;
      }

      // the counter keeps track of cancelled animations
      var counter = (existingAnimation.counter || 0) + 1;
      newAnimation.counter = counter;

      markElementAnimationState(node, PRE_DIGEST_STATE, newAnimation);

      $rootScope.$$postDigest(function() {
        // It is possible that the DOM nodes inside `originalElement` have been replaced. This can
        // happen if the animated element is a transcluded clone and also has a `templateUrl`
        // directive on it. Therefore, we must recreate `element` in order to interact with the
        // actual DOM nodes.
        // Note: We still need to use the old `node` for certain things, such as looking up in
        //       HashMaps where it was used as the key.

        element = stripCommentsFromElement(originalElement);

        var animationDetails = activeAnimationsLookup.get(node);
        var animationCancelled = !animationDetails;
        animationDetails = animationDetails || {};

        // if addClass/removeClass is called before something like enter then the
        // registered parent element may not be present. The code below will ensure
        // that a final value for parent element is obtained
        var parentElement = element.parent() || [];

        // animate/structural/class-based animations all have requirements. Otherwise there
        // is no point in performing an animation. The parent node must also be set.
        var isValidAnimation = parentElement.length > 0
                                && (animationDetails.event === 'animate'
                                    || animationDetails.structural
                                    || hasAnimationClasses(animationDetails));

        // this means that the previous animation was cancelled
        // even if the follow-up animation is the same event
        if (animationCancelled || animationDetails.counter !== counter || !isValidAnimation) {
          // if another animation did not take over then we need
          // to make sure that the domOperation and options are
          // handled accordingly
          if (animationCancelled) {
            applyAnimationClasses(element, options);
            applyAnimationStyles(element, options);
          }

          // if the event changed from something like enter to leave then we do
          // it, otherwise if it's the same then the end result will be the same too
          if (animationCancelled || (isStructural && animationDetails.event !== event)) {
            options.domOperation();
            runner.end();
          }

          // in the event that the element animation was not cancelled or a follow-up animation
          // isn't allowed to animate from here then we need to clear the state of the element
          // so that any future animations won't read the expired animation data.
          if (!isValidAnimation) {
            clearElementAnimationState(node);
          }

          return;
        }

        // this combined multiple class to addClass / removeClass into a setClass event
        // so long as a structural event did not take over the animation
        event = !animationDetails.structural && hasAnimationClasses(animationDetails, true)
            ? 'setClass'
            : animationDetails.event;

        markElementAnimationState(node, RUNNING_STATE);
        var realRunner = $$animation(element, event, animationDetails.options);

        // this will update the runner's flow-control events based on
        // the `realRunner` object.
        runner.setHost(realRunner);
        notifyProgress(runner, event, 'start', getEventData(options));

        realRunner.done(function(status) {
          close(!status);
          var animationDetails = activeAnimationsLookup.get(node);
          if (animationDetails && animationDetails.counter === counter) {
            clearElementAnimationState(node);
          }
          notifyProgress(runner, event, 'close', getEventData(options));
        });
      });

      return runner;

      function notifyProgress(runner, event, phase, data) {
        runInNextPostDigestOrNow(function() {
          var callbacks = findCallbacks(parentNode, node, event);
          if (callbacks.length) {
            // do not optimize this call here to RAF because
            // we don't know how heavy the callback code here will
            // be and if this code is buffered then this can
            // lead to a performance regression.
            $$rAF(function() {
              forEach(callbacks, function(callback) {
                callback(element, phase, data);
              });
              cleanupEventListeners(phase, node);
            });
          } else {
            cleanupEventListeners(phase, node);
          }
        });
        runner.progress(event, phase, data);
      }

      function close(reject) {
        clearGeneratedClasses(element, options);
        applyAnimationClasses(element, options);
        applyAnimationStyles(element, options);
        options.domOperation();
        runner.complete(!reject);
      }
    }

    function closeChildAnimations(node) {
      var children = node.querySelectorAll('[' + NG_ANIMATE_ATTR_NAME + ']');
      forEach(children, function(child) {
        var state = parseInt(child.getAttribute(NG_ANIMATE_ATTR_NAME), 10);
        var animationDetails = activeAnimationsLookup.get(child);
        if (animationDetails) {
          switch (state) {
            case RUNNING_STATE:
              animationDetails.runner.end();
              /* falls through */
            case PRE_DIGEST_STATE:
              activeAnimationsLookup.delete(child);
              break;
          }
        }
      });
    }

    function clearElementAnimationState(node) {
      node.removeAttribute(NG_ANIMATE_ATTR_NAME);
      activeAnimationsLookup.delete(node);
    }

    /**
     * This fn returns false if any of the following is true:
     * a) animations on any parent element are disabled, and animations on the element aren't explicitly allowed
     * b) a parent element has an ongoing structural animation, and animateChildren is false
     * c) the element is not a child of the body
     * d) the element is not a child of the $rootElement
     */
    function areAnimationsAllowed(node, parentNode, event) {
      var bodyNode = $document[0].body;
      var rootNode = getDomNode($rootElement);

      var bodyNodeDetected = (node === bodyNode) || node.nodeName === 'HTML';
      var rootNodeDetected = (node === rootNode);
      var parentAnimationDetected = false;
      var elementDisabled = disabledElementsLookup.get(node);
      var animateChildren;

      var parentHost = jqLite.data(node, NG_ANIMATE_PIN_DATA);
      if (parentHost) {
        parentNode = getDomNode(parentHost);
      }

      while (parentNode) {
        if (!rootNodeDetected) {
          // AngularJS doesn't want to attempt to animate elements outside of the application
          // therefore we need to ensure that the rootElement is an ancestor of the current element
          rootNodeDetected = (parentNode === rootNode);
        }

        if (parentNode.nodeType !== ELEMENT_NODE) {
          // no point in inspecting the #document element
          break;
        }

        var details = activeAnimationsLookup.get(parentNode) || {};
        // either an enter, leave or move animation will commence
        // therefore we can't allow any animations to take place
        // but if a parent animation is class-based then that's ok
        if (!parentAnimationDetected) {
          var parentNodeDisabled = disabledElementsLookup.get(parentNode);

          if (parentNodeDisabled === true && elementDisabled !== false) {
            // disable animations if the user hasn't explicitly enabled animations on the
            // current element
            elementDisabled = true;
            // element is disabled via parent element, no need to check anything else
            break;
          } else if (parentNodeDisabled === false) {
            elementDisabled = false;
          }
          parentAnimationDetected = details.structural;
        }

        if (isUndefined(animateChildren) || animateChildren === true) {
          var value = jqLite.data(parentNode, NG_ANIMATE_CHILDREN_DATA);
          if (isDefined(value)) {
            animateChildren = value;
          }
        }

        // there is no need to continue traversing at this point
        if (parentAnimationDetected && animateChildren === false) break;

        if (!bodyNodeDetected) {
          // we also need to ensure that the element is or will be a part of the body element
          // otherwise it is pointless to even issue an animation to be rendered
          bodyNodeDetected = (parentNode === bodyNode);
        }

        if (bodyNodeDetected && rootNodeDetected) {
          // If both body and root have been found, any other checks are pointless,
          // as no animation data should live outside the application
          break;
        }

        if (!rootNodeDetected) {
          // If `rootNode` is not detected, check if `parentNode` is pinned to another element
          parentHost = jqLite.data(parentNode, NG_ANIMATE_PIN_DATA);
          if (parentHost) {
            // The pin target element becomes the next parent element
            parentNode = getDomNode(parentHost);
            continue;
          }
        }

        parentNode = parentNode.parentNode;
      }

      var allowAnimation = (!parentAnimationDetected || animateChildren) && elementDisabled !== true;
      return allowAnimation && rootNodeDetected && bodyNodeDetected;
    }

    function markElementAnimationState(node, state, details) {
      details = details || {};
      details.state = state;

      node.setAttribute(NG_ANIMATE_ATTR_NAME, state);

      var oldValue = activeAnimationsLookup.get(node);
      var newValue = oldValue
          ? extend(oldValue, details)
          : details;
      activeAnimationsLookup.set(node, newValue);
    }
  }];
}];

/** @this */
var $$AnimateCacheProvider = function() {

  var KEY = '$$ngAnimateParentKey';
  var parentCounter = 0;
  var cache = Object.create(null);

  this.$get = [function() {
    return {
      cacheKey: function(node, method, addClass, removeClass) {
        var parentNode = node.parentNode;
        var parentID = parentNode[KEY] || (parentNode[KEY] = ++parentCounter);
        var parts = [parentID, method, node.getAttribute('class')];
        if (addClass) {
          parts.push(addClass);
        }
        if (removeClass) {
          parts.push(removeClass);
        }
        return parts.join(' ');
      },

      containsCachedAnimationWithoutDuration: function(key) {
        var entry = cache[key];

        // nothing cached, so go ahead and animate
        // otherwise it should be a valid animation
        return (entry && !entry.isValid) || false;
      },

      flush: function() {
        cache = Object.create(null);
      },

      count: function(key) {
        var entry = cache[key];
        return entry ? entry.total : 0;
      },

      get: function(key) {
        var entry = cache[key];
        return entry && entry.value;
      },

      put: function(key, value, isValid) {
        if (!cache[key]) {
          cache[key] = { total: 1, value: value, isValid: isValid };
        } else {
          cache[key].total++;
          cache[key].value = value;
        }
      }
    };
  }];
};

/* exported $$AnimationProvider */

var $$AnimationProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  var NG_ANIMATE_REF_ATTR = 'ng-animate-ref';

  var drivers = this.drivers = [];

  var RUNNER_STORAGE_KEY = '$$animationRunner';
  var PREPARE_CLASSES_KEY = '$$animatePrepareClasses';

  function setRunner(element, runner) {
    element.data(RUNNER_STORAGE_KEY, runner);
  }

  function removeRunner(element) {
    element.removeData(RUNNER_STORAGE_KEY);
  }

  function getRunner(element) {
    return element.data(RUNNER_STORAGE_KEY);
  }

  this.$get = ['$$jqLite', '$rootScope', '$injector', '$$AnimateRunner', '$$Map', '$$rAFScheduler', '$$animateCache',
       function($$jqLite,   $rootScope,   $injector,   $$AnimateRunner,   $$Map,   $$rAFScheduler, $$animateCache) {

    var animationQueue = [];
    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);

    function sortAnimations(animations) {
      var tree = { children: [] };
      var i, lookup = new $$Map();

      // this is done first beforehand so that the map
      // is filled with a list of the elements that will be animated
      for (i = 0; i < animations.length; i++) {
        var animation = animations[i];
        lookup.set(animation.domNode, animations[i] = {
          domNode: animation.domNode,
          element: animation.element,
          fn: animation.fn,
          children: []
        });
      }

      for (i = 0; i < animations.length; i++) {
        processNode(animations[i]);
      }

      return flatten(tree);

      function processNode(entry) {
        if (entry.processed) return entry;
        entry.processed = true;

        var elementNode = entry.domNode;
        var parentNode = elementNode.parentNode;
        lookup.set(elementNode, entry);

        var parentEntry;
        while (parentNode) {
          parentEntry = lookup.get(parentNode);
          if (parentEntry) {
            if (!parentEntry.processed) {
              parentEntry = processNode(parentEntry);
            }
            break;
          }
          parentNode = parentNode.parentNode;
        }

        (parentEntry || tree).children.push(entry);
        return entry;
      }

      function flatten(tree) {
        var result = [];
        var queue = [];
        var i;

        for (i = 0; i < tree.children.length; i++) {
          queue.push(tree.children[i]);
        }

        var remainingLevelEntries = queue.length;
        var nextLevelEntries = 0;
        var row = [];

        for (i = 0; i < queue.length; i++) {
          var entry = queue[i];
          if (remainingLevelEntries <= 0) {
            remainingLevelEntries = nextLevelEntries;
            nextLevelEntries = 0;
            result.push(row);
            row = [];
          }
          row.push(entry);
          entry.children.forEach(function(childEntry) {
            nextLevelEntries++;
            queue.push(childEntry);
          });
          remainingLevelEntries--;
        }

        if (row.length) {
          result.push(row);
        }

        return result;
      }
    }

    // TODO(matsko): document the signature in a better way
    return function(element, event, options) {
      options = prepareAnimationOptions(options);
      var isStructural = ['enter', 'move', 'leave'].indexOf(event) >= 0;

      // there is no animation at the current moment, however
      // these runner methods will get later updated with the
      // methods leading into the driver's end/cancel methods
      // for now they just stop the animation from starting
      var runner = new $$AnimateRunner({
        end: function() { close(); },
        cancel: function() { close(true); }
      });

      if (!drivers.length) {
        close();
        return runner;
      }

      var classes = mergeClasses(element.attr('class'), mergeClasses(options.addClass, options.removeClass));
      var tempClasses = options.tempClasses;
      if (tempClasses) {
        classes += ' ' + tempClasses;
        options.tempClasses = null;
      }

      if (isStructural) {
        element.data(PREPARE_CLASSES_KEY, 'ng-' + event + PREPARE_CLASS_SUFFIX);
      }

      setRunner(element, runner);

      animationQueue.push({
        // this data is used by the postDigest code and passed into
        // the driver step function
        element: element,
        classes: classes,
        event: event,
        structural: isStructural,
        options: options,
        beforeStart: beforeStart,
        close: close
      });

      element.on('$destroy', handleDestroyedElement);

      // we only want there to be one function called within the post digest
      // block. This way we can group animations for all the animations that
      // were apart of the same postDigest flush call.
      if (animationQueue.length > 1) return runner;

      $rootScope.$$postDigest(function() {
        var animations = [];
        forEach(animationQueue, function(entry) {
          // the element was destroyed early on which removed the runner
          // form its storage. This means we can't animate this element
          // at all and it already has been closed due to destruction.
          if (getRunner(entry.element)) {
            animations.push(entry);
          } else {
            entry.close();
          }
        });

        // now any future animations will be in another postDigest
        animationQueue.length = 0;

        var groupedAnimations = groupAnimations(animations);
        var toBeSortedAnimations = [];

        forEach(groupedAnimations, function(animationEntry) {
          var element = animationEntry.from ? animationEntry.from.element : animationEntry.element;
          var extraClasses = options.addClass;

          extraClasses = (extraClasses ? (extraClasses + ' ') : '') + NG_ANIMATE_CLASSNAME;
          var cacheKey = $$animateCache.cacheKey(element[0], animationEntry.event, extraClasses, options.removeClass);

          toBeSortedAnimations.push({
            element: element,
            domNode: getDomNode(element),
            fn: function triggerAnimationStart() {
              var startAnimationFn, closeFn = animationEntry.close;

              // in the event that we've cached the animation status for this element
              // and it's in fact an invalid animation (something that has duration = 0)
              // then we should skip all the heavy work from here on
              if ($$animateCache.containsCachedAnimationWithoutDuration(cacheKey)) {
                closeFn();
                return;
              }

              // it's important that we apply the `ng-animate` CSS class and the
              // temporary classes before we do any driver invoking since these
              // CSS classes may be required for proper CSS detection.
              animationEntry.beforeStart();

              // in the event that the element was removed before the digest runs or
              // during the RAF sequencing then we should not trigger the animation.
              var targetElement = animationEntry.anchors
                  ? (animationEntry.from.element || animationEntry.to.element)
                  : animationEntry.element;

              if (getRunner(targetElement)) {
                var operation = invokeFirstDriver(animationEntry);
                if (operation) {
                  startAnimationFn = operation.start;
                }
              }

              if (!startAnimationFn) {
                closeFn();
              } else {
                var animationRunner = startAnimationFn();
                animationRunner.done(function(status) {
                  closeFn(!status);
                });
                updateAnimationRunners(animationEntry, animationRunner);
              }
            }
          });
        });

        // we need to sort each of the animations in order of parent to child
        // relationships. This ensures that the child classes are applied at the
        // right time.
        var finalAnimations = sortAnimations(toBeSortedAnimations);
        for (var i = 0; i < finalAnimations.length; i++) {
          var innerArray = finalAnimations[i];
          for (var j = 0; j < innerArray.length; j++) {
            var entry = innerArray[j];
            var element = entry.element;

            // the RAFScheduler code only uses functions
            finalAnimations[i][j] = entry.fn;

            // the first row of elements shouldn't have a prepare-class added to them
            // since the elements are at the top of the animation hierarchy and they
            // will be applied without a RAF having to pass...
            if (i === 0) {
              element.removeData(PREPARE_CLASSES_KEY);
              continue;
            }

            var prepareClassName = element.data(PREPARE_CLASSES_KEY);
            if (prepareClassName) {
              $$jqLite.addClass(element, prepareClassName);
            }
          }
        }

        $$rAFScheduler(finalAnimations);
      });

      return runner;

      // TODO(matsko): change to reference nodes
      function getAnchorNodes(node) {
        var SELECTOR = '[' + NG_ANIMATE_REF_ATTR + ']';
        var items = node.hasAttribute(NG_ANIMATE_REF_ATTR)
              ? [node]
              : node.querySelectorAll(SELECTOR);
        var anchors = [];
        forEach(items, function(node) {
          var attr = node.getAttribute(NG_ANIMATE_REF_ATTR);
          if (attr && attr.length) {
            anchors.push(node);
          }
        });
        return anchors;
      }

      function groupAnimations(animations) {
        var preparedAnimations = [];
        var refLookup = {};
        forEach(animations, function(animation, index) {
          var element = animation.element;
          var node = getDomNode(element);
          var event = animation.event;
          var enterOrMove = ['enter', 'move'].indexOf(event) >= 0;
          var anchorNodes = animation.structural ? getAnchorNodes(node) : [];

          if (anchorNodes.length) {
            var direction = enterOrMove ? 'to' : 'from';

            forEach(anchorNodes, function(anchor) {
              var key = anchor.getAttribute(NG_ANIMATE_REF_ATTR);
              refLookup[key] = refLookup[key] || {};
              refLookup[key][direction] = {
                animationID: index,
                element: jqLite(anchor)
              };
            });
          } else {
            preparedAnimations.push(animation);
          }
        });

        var usedIndicesLookup = {};
        var anchorGroups = {};
        forEach(refLookup, function(operations, key) {
          var from = operations.from;
          var to = operations.to;

          if (!from || !to) {
            // only one of these is set therefore we can't have an
            // anchor animation since all three pieces are required
            var index = from ? from.animationID : to.animationID;
            var indexKey = index.toString();
            if (!usedIndicesLookup[indexKey]) {
              usedIndicesLookup[indexKey] = true;
              preparedAnimations.push(animations[index]);
            }
            return;
          }

          var fromAnimation = animations[from.animationID];
          var toAnimation = animations[to.animationID];
          var lookupKey = from.animationID.toString();
          if (!anchorGroups[lookupKey]) {
            var group = anchorGroups[lookupKey] = {
              structural: true,
              beforeStart: function() {
                fromAnimation.beforeStart();
                toAnimation.beforeStart();
              },
              close: function() {
                fromAnimation.close();
                toAnimation.close();
              },
              classes: cssClassesIntersection(fromAnimation.classes, toAnimation.classes),
              from: fromAnimation,
              to: toAnimation,
              anchors: [] // TODO(matsko): change to reference nodes
            };

            // the anchor animations require that the from and to elements both have at least
            // one shared CSS class which effectively marries the two elements together to use
            // the same animation driver and to properly sequence the anchor animation.
            if (group.classes.length) {
              preparedAnimations.push(group);
            } else {
              preparedAnimations.push(fromAnimation);
              preparedAnimations.push(toAnimation);
            }
          }

          anchorGroups[lookupKey].anchors.push({
            'out': from.element, 'in': to.element
          });
        });

        return preparedAnimations;
      }

      function cssClassesIntersection(a,b) {
        a = a.split(' ');
        b = b.split(' ');
        var matches = [];

        for (var i = 0; i < a.length; i++) {
          var aa = a[i];
          if (aa.substring(0,3) === 'ng-') continue;

          for (var j = 0; j < b.length; j++) {
            if (aa === b[j]) {
              matches.push(aa);
              break;
            }
          }
        }

        return matches.join(' ');
      }

      function invokeFirstDriver(animationDetails) {
        // we loop in reverse order since the more general drivers (like CSS and JS)
        // may attempt more elements, but custom drivers are more particular
        for (var i = drivers.length - 1; i >= 0; i--) {
          var driverName = drivers[i];
          var factory = $injector.get(driverName);
          var driver = factory(animationDetails);
          if (driver) {
            return driver;
          }
        }
      }

      function beforeStart() {
        tempClasses = (tempClasses ? (tempClasses + ' ') : '') + NG_ANIMATE_CLASSNAME;
        $$jqLite.addClass(element, tempClasses);

        var prepareClassName = element.data(PREPARE_CLASSES_KEY);
        if (prepareClassName) {
          $$jqLite.removeClass(element, prepareClassName);
          prepareClassName = null;
        }
      }

      function updateAnimationRunners(animation, newRunner) {
        if (animation.from && animation.to) {
          update(animation.from.element);
          update(animation.to.element);
        } else {
          update(animation.element);
        }

        function update(element) {
          var runner = getRunner(element);
          if (runner) runner.setHost(newRunner);
        }
      }

      function handleDestroyedElement() {
        var runner = getRunner(element);
        if (runner && (event !== 'leave' || !options.$$domOperationFired)) {
          runner.end();
        }
      }

      function close(rejected) {
        element.off('$destroy', handleDestroyedElement);
        removeRunner(element);

        applyAnimationClasses(element, options);
        applyAnimationStyles(element, options);
        options.domOperation();

        if (tempClasses) {
          $$jqLite.removeClass(element, tempClasses);
        }

        runner.complete(!rejected);
      }
    };
  }];
}];

/**
 * @ngdoc directive
 * @name ngAnimateSwap
 * @restrict A
 * @scope
 *
 * @description
 *
 * ngAnimateSwap is a animation-oriented directive that allows for the container to
 * be removed and entered in whenever the associated expression changes. A
 * common usecase for this directive is a rotating banner or slider component which
 * contains one image being present at a time. When the active image changes
 * then the old image will perform a `leave` animation and the new element
 * will be inserted via an `enter` animation.
 *
 * @animations
 * | Animation                        | Occurs                               |
 * |----------------------------------|--------------------------------------|
 * | {@link ng.$animate#enter enter}  | when the new element is inserted to the DOM  |
 * | {@link ng.$animate#leave leave}  | when the old element is removed from the DOM |
 *
 * @example
 * <example name="ngAnimateSwap-directive" module="ngAnimateSwapExample"
 *          deps="angular-animate.js"
 *          animations="true" fixBase="true">
 *   <file name="index.html">
 *     <div class="container" ng-controller="AppCtrl">
 *       <div ng-animate-swap="number" class="cell swap-animation" ng-class="colorClass(number)">
 *         {{ number }}
 *       </div>
 *     </div>
 *   </file>
 *   <file name="script.js">
 *     angular.module('ngAnimateSwapExample', ['ngAnimate'])
 *       .controller('AppCtrl', ['$scope', '$interval', function($scope, $interval) {
 *         $scope.number = 0;
 *         $interval(function() {
 *           $scope.number++;
 *         }, 1000);
 *
 *         var colors = ['red','blue','green','yellow','orange'];
 *         $scope.colorClass = function(number) {
 *           return colors[number % colors.length];
 *         };
 *       }]);
 *   </file>
 *  <file name="animations.css">
 *  .container {
 *    height:250px;
 *    width:250px;
 *    position:relative;
 *    overflow:hidden;
 *    border:2px solid black;
 *  }
 *  .container .cell {
 *    font-size:150px;
 *    text-align:center;
 *    line-height:250px;
 *    position:absolute;
 *    top:0;
 *    left:0;
 *    right:0;
 *    border-bottom:2px solid black;
 *  }
 *  .swap-animation.ng-enter, .swap-animation.ng-leave {
 *    transition:0.5s linear all;
 *  }
 *  .swap-animation.ng-enter {
 *    top:-250px;
 *  }
 *  .swap-animation.ng-enter-active {
 *    top:0px;
 *  }
 *  .swap-animation.ng-leave {
 *    top:0px;
 *  }
 *  .swap-animation.ng-leave-active {
 *    top:250px;
 *  }
 *  .red { background:red; }
 *  .green { background:green; }
 *  .blue { background:blue; }
 *  .yellow { background:yellow; }
 *  .orange { background:orange; }
 *  </file>
 * </example>
 */
var ngAnimateSwapDirective = ['$animate', function($animate) {
  return {
    restrict: 'A',
    transclude: 'element',
    terminal: true,
    priority: 550, // We use 550 here to ensure that the directive is caught before others,
                   // but after `ngIf` (at priority 600).
    link: function(scope, $element, attrs, ctrl, $transclude) {
      var previousElement, previousScope;
      scope.$watchCollection(attrs.ngAnimateSwap || attrs['for'], function(value) {
        if (previousElement) {
          $animate.leave(previousElement);
        }
        if (previousScope) {
          previousScope.$destroy();
          previousScope = null;
        }
        if (value || value === 0) {
          $transclude(function(clone, childScope) {
            previousElement = clone;
            previousScope = childScope;
            $animate.enter(clone, null, $element);
          });
        }
      });
    }
  };
}];

/**
 * @ngdoc module
 * @name ngAnimate
 * @description
 *
 * The `ngAnimate` module provides support for CSS-based animations (keyframes and transitions) as well as JavaScript-based animations via
 * callback hooks. Animations are not enabled by default, however, by including `ngAnimate` the animation hooks are enabled for an AngularJS app.
 *
 * ## Usage
 * Simply put, there are two ways to make use of animations when ngAnimate is used: by using **CSS** and **JavaScript**. The former works purely based
 * using CSS (by using matching CSS selectors/styles) and the latter triggers animations that are registered via `module.animation()`. For
 * both CSS and JS animations the sole requirement is to have a matching `CSS class` that exists both in the registered animation and within
 * the HTML element that the animation will be triggered on.
 *
 * ## Directive Support
 * The following directives are "animation aware":
 *
 * | Directive                                                                     | Supported Animations                                                      |
 * |-------------------------------------------------------------------------------|---------------------------------------------------------------------------|
 * | {@link ng.directive:form#animations form / ngForm}                            | add and remove ({@link ng.directive:form#css-classes various classes})    |
 * | {@link ngAnimate.directive:ngAnimateSwap#animations ngAnimateSwap}            | enter and leave                                                           |
 * | {@link ng.directive:ngClass#animations ngClass / {{class&#125;&#8203;&#125;}  | add and remove                                                            |
 * | {@link ng.directive:ngClassEven#animations ngClassEven}                       | add and remove                                                            |
 * | {@link ng.directive:ngClassOdd#animations ngClassOdd}                         | add and remove                                                            |
 * | {@link ng.directive:ngHide#animations ngHide}                                 | add and remove (the `ng-hide` class)                                      |
 * | {@link ng.directive:ngIf#animations ngIf}                                     | enter and leave                                                           |
 * | {@link ng.directive:ngInclude#animations ngInclude}                           | enter and leave                                                           |
 * | {@link module:ngMessages#animations ngMessage / ngMessageExp}                 | enter and leave                                                           |
 * | {@link module:ngMessages#animations ngMessages}                               | add and remove (the `ng-active`/`ng-inactive` classes)                    |
 * | {@link ng.directive:ngModel#animations ngModel}                               | add and remove ({@link ng.directive:ngModel#css-classes various classes}) |
 * | {@link ng.directive:ngRepeat#animations ngRepeat}                             | enter, leave, and move                                                    |
 * | {@link ng.directive:ngShow#animations ngShow}                                 | add and remove (the `ng-hide` class)                                      |
 * | {@link ng.directive:ngSwitch#animations ngSwitch}                             | enter and leave                                                           |
 * | {@link ngRoute.directive:ngView#animations ngView}                            | enter and leave                                                           |
 *
 * (More information can be found by visiting the documentation associated with each directive.)
 *
 * For a full breakdown of the steps involved during each animation event, refer to the
 * {@link ng.$animate `$animate` API docs}.
 *
 * ## CSS-based Animations
 *
 * CSS-based animations with ngAnimate are unique since they require no JavaScript code at all. By using a CSS class that we reference between our HTML
 * and CSS code we can create an animation that will be picked up by AngularJS when an underlying directive performs an operation.
 *
 * The example below shows how an `enter` animation can be made possible on an element using `ng-if`:
 *
 * ```html
 * <div ng-if="bool" class="fade">
 *    Fade me in out
 * </div>
 * <button ng-click="bool=true">Fade In!</button>
 * <button ng-click="bool=false">Fade Out!</button>
 * ```
 *
 * Notice the CSS class **fade**? We can now create the CSS transition code that references this class:
 *
 * ```css
 * /&#42; The starting CSS styles for the enter animation &#42;/
 * .fade.ng-enter {
 *   transition:0.5s linear all;
 *   opacity:0;
 * }
 *
 * /&#42; The finishing CSS styles for the enter animation &#42;/
 * .fade.ng-enter.ng-enter-active {
 *   opacity:1;
 * }
 * ```
 *
 * The key thing to remember here is that, depending on the animation event (which each of the directives above trigger depending on what's going on) two
 * generated CSS classes will be applied to the element; in the example above we have `.ng-enter` and `.ng-enter-active`. For CSS transitions, the transition
 * code **must** be defined within the starting CSS class (in this case `.ng-enter`). The destination class is what the transition will animate towards.
 *
 * If for example we wanted to create animations for `leave` and `move` (ngRepeat triggers move) then we can do so using the same CSS naming conventions:
 *
 * ```css
 * /&#42; now the element will fade out before it is removed from the DOM &#42;/
 * .fade.ng-leave {
 *   transition:0.5s linear all;
 *   opacity:1;
 * }
 * .fade.ng-leave.ng-leave-active {
 *   opacity:0;
 * }
 * ```
 *
 * We can also make use of **CSS Keyframes** by referencing the keyframe animation within the starting CSS class:
 *
 * ```css
 * /&#42; there is no need to define anything inside of the destination
 * CSS class since the keyframe will take charge of the animation &#42;/
 * .fade.ng-leave {
 *   animation: my_fade_animation 0.5s linear;
 *   -webkit-animation: my_fade_animation 0.5s linear;
 * }
 *
 * @keyframes my_fade_animation {
 *   from { opacity:1; }
 *   to { opacity:0; }
 * }
 *
 * @-webkit-keyframes my_fade_animation {
 *   from { opacity:1; }
 *   to { opacity:0; }
 * }
 * ```
 *
 * Feel free also mix transitions and keyframes together as well as any other CSS classes on the same element.
 *
 * ### CSS Class-based Animations
 *
 * Class-based animations (animations that are triggered via `ngClass`, `ngShow`, `ngHide` and some other directives) have a slightly different
 * naming convention. Class-based animations are basic enough that a standard transition or keyframe can be referenced on the class being added
 * and removed.
 *
 * For example if we wanted to do a CSS animation for `ngHide` then we place an animation on the `.ng-hide` CSS class:
 *
 * ```html
 * <div ng-show="bool" class="fade">
 *   Show and hide me
 * </div>
 * <button ng-click="bool=!bool">Toggle</button>
 *
 * <style>
 * .fade.ng-hide {
 *   transition:0.5s linear all;
 *   opacity:0;
 * }
 * </style>
 * ```
 *
 * All that is going on here with ngShow/ngHide behind the scenes is the `.ng-hide` class is added/removed (when the hidden state is valid). Since
 * ngShow and ngHide are animation aware then we can match up a transition and ngAnimate handles the rest.
 *
 * In addition the addition and removal of the CSS class, ngAnimate also provides two helper methods that we can use to further decorate the animation
 * with CSS styles.
 *
 * ```html
 * <div ng-class="{on:onOff}" class="highlight">
 *   Highlight this box
 * </div>
 * <button ng-click="onOff=!onOff">Toggle</button>
 *
 * <style>
 * .highlight {
 *   transition:0.5s linear all;
 * }
 * .highlight.on-add {
 *   background:white;
 * }
 * .highlight.on {
 *   background:yellow;
 * }
 * .highlight.on-remove {
 *   background:black;
 * }
 * </style>
 * ```
 *
 * We can also make use of CSS keyframes by placing them within the CSS classes.
 *
 *
 * ### CSS Staggering Animations
 * A Staggering animation is a collection of animations that are issued with a slight delay in between each successive operation resulting in a
 * curtain-like effect. The ngAnimate module (versions >=1.2) supports staggering animations and the stagger effect can be
 * performed by creating a **ng-EVENT-stagger** CSS class and attaching that class to the base CSS class used for
 * the animation. The style property expected within the stagger class can either be a **transition-delay** or an
 * **animation-delay** property (or both if your animation contains both transitions and keyframe animations).
 *
 * ```css
 * .my-animation.ng-enter {
 *   /&#42; standard transition code &#42;/
 *   transition: 1s linear all;
 *   opacity:0;
 * }
 * .my-animation.ng-enter-stagger {
 *   /&#42; this will have a 100ms delay between each successive leave animation &#42;/
 *   transition-delay: 0.1s;
 *
 *   /&#42; As of 1.4.4, this must always be set: it signals ngAnimate
 *     to not accidentally inherit a delay property from another CSS class &#42;/
 *   transition-duration: 0s;
 *
 *   /&#42; if you are using animations instead of transitions you should configure as follows:
 *     animation-delay: 0.1s;
 *     animation-duration: 0s; &#42;/
 * }
 * .my-animation.ng-enter.ng-enter-active {
 *   /&#42; standard transition styles &#42;/
 *   opacity:1;
 * }
 * ```
 *
 * Staggering animations work by default in ngRepeat (so long as the CSS class is defined). Outside of ngRepeat, to use staggering animations
 * on your own, they can be triggered by firing multiple calls to the same event on $animate. However, the restrictions surrounding this
 * are that each of the elements must have the same CSS className value as well as the same parent element. A stagger operation
 * will also be reset if one or more animation frames have passed since the multiple calls to `$animate` were fired.
 *
 * The following code will issue the **ng-leave-stagger** event on the element provided:
 *
 * ```js
 * var kids = parent.children();
 *
 * $animate.leave(kids[0]); //stagger index=0
 * $animate.leave(kids[1]); //stagger index=1
 * $animate.leave(kids[2]); //stagger index=2
 * $animate.leave(kids[3]); //stagger index=3
 * $animate.leave(kids[4]); //stagger index=4
 *
 * window.requestAnimationFrame(function() {
 *   //stagger has reset itself
 *   $animate.leave(kids[5]); //stagger index=0
 *   $animate.leave(kids[6]); //stagger index=1
 *
 *   $scope.$digest();
 * });
 * ```
 *
 * Stagger animations are currently only supported within CSS-defined animations.
 *
 * ### The `ng-animate` CSS class
 *
 * When ngAnimate is animating an element it will apply the `ng-animate` CSS class to the element for the duration of the animation.
 * This is a temporary CSS class and it will be removed once the animation is over (for both JavaScript and CSS-based animations).
 *
 * Therefore, animations can be applied to an element using this temporary class directly via CSS.
 *
 * ```css
 * .zipper.ng-animate {
 *   transition:0.5s linear all;
 * }
 * .zipper.ng-enter {
 *   opacity:0;
 * }
 * .zipper.ng-enter.ng-enter-active {
 *   opacity:1;
 * }
 * .zipper.ng-leave {
 *   opacity:1;
 * }
 * .zipper.ng-leave.ng-leave-active {
 *   opacity:0;
 * }
 * ```
 *
 * (Note that the `ng-animate` CSS class is reserved and it cannot be applied on an element directly since ngAnimate will always remove
 * the CSS class once an animation has completed.)
 *
 *
 * ### The `ng-[event]-prepare` class
 *
 * This is a special class that can be used to prevent unwanted flickering / flash of content before
 * the actual animation starts. The class is added as soon as an animation is initialized, but removed
 * before the actual animation starts (after waiting for a $digest).
 * It is also only added for *structural* animations (`enter`, `move`, and `leave`).
 *
 * In practice, flickering can appear when nesting elements with structural animations such as `ngIf`
 * into elements that have class-based animations such as `ngClass`.
 *
 * ```html
 * <div ng-class="{red: myProp}">
 *   <div ng-class="{blue: myProp}">
 *     <div class="message" ng-if="myProp"></div>
 *   </div>
 * </div>
 * ```
 *
 * It is possible that during the `enter` animation, the `.message` div will be briefly visible before it starts animating.
 * In that case, you can add styles to the CSS that make sure the element stays hidden before the animation starts:
 *
 * ```css
 * .message.ng-enter-prepare {
 *   opacity: 0;
 * }
 * ```
 *
 * ### Animating between value changes
 *
 * Sometimes you need to animate between different expression states, whose values
 * don't necessary need to be known or referenced in CSS styles.
 * Unless possible with another {@link ngAnimate#directive-support "animation aware" directive},
 * that specific use case can always be covered with {@link ngAnimate.directive:ngAnimateSwap} as
 * can be seen in {@link ngAnimate.directive:ngAnimateSwap#examples this example}.
 *
 * Note that {@link ngAnimate.directive:ngAnimateSwap} is a *structural directive*, which means it
 * creates a new instance of the element (including any other/child directives it may have) and
 * links it to a new scope every time *swap* happens. In some cases this might not be desirable
 * (e.g. for performance reasons, or when you wish to retain internal state on the original
 * element instance).
 *
 * ## JavaScript-based Animations
 *
 * ngAnimate also allows for animations to be consumed by JavaScript code. The approach is similar to CSS-based animations (where there is a shared
 * CSS class that is referenced in our HTML code) but in addition we need to register the JavaScript animation on the module. By making use of the
 * `module.animation()` module function we can register the animation.
 *
 * Let's see an example of a enter/leave animation using `ngRepeat`:
 *
 * ```html
 * <div ng-repeat="item in items" class="slide">
 *   {{ item }}
 * </div>
 * ```
 *
 * See the **slide** CSS class? Let's use that class to define an animation that we'll structure in our module code by using `module.animation`:
 *
 * ```js
 * myModule.animation('.slide', [function() {
 *   return {
 *     // make note that other events (like addClass/removeClass)
 *     // have different function input parameters
 *     enter: function(element, doneFn) {
 *       jQuery(element).fadeIn(1000, doneFn);
 *
 *       // remember to call doneFn so that AngularJS
 *       // knows that the animation has concluded
 *     },
 *
 *     move: function(element, doneFn) {
 *       jQuery(element).fadeIn(1000, doneFn);
 *     },
 *
 *     leave: function(element, doneFn) {
 *       jQuery(element).fadeOut(1000, doneFn);
 *     }
 *   }
 * }]);
 * ```
 *
 * The nice thing about JS-based animations is that we can inject other services and make use of advanced animation libraries such as
 * greensock.js and velocity.js.
 *
 * If our animation code class-based (meaning that something like `ngClass`, `ngHide` and `ngShow` triggers it) then we can still define
 * our animations inside of the same registered animation, however, the function input arguments are a bit different:
 *
 * ```html
 * <div ng-class="color" class="colorful">
 *   this box is moody
 * </div>
 * <button ng-click="color='red'">Change to red</button>
 * <button ng-click="color='blue'">Change to blue</button>
 * <button ng-click="color='green'">Change to green</button>
 * ```
 *
 * ```js
 * myModule.animation('.colorful', [function() {
 *   return {
 *     addClass: function(element, className, doneFn) {
 *       // do some cool animation and call the doneFn
 *     },
 *     removeClass: function(element, className, doneFn) {
 *       // do some cool animation and call the doneFn
 *     },
 *     setClass: function(element, addedClass, removedClass, doneFn) {
 *       // do some cool animation and call the doneFn
 *     }
 *   }
 * }]);
 * ```
 *
 * ## CSS + JS Animations Together
 *
 * AngularJS 1.4 and higher has taken steps to make the amalgamation of CSS and JS animations more flexible. However, unlike earlier versions of AngularJS,
 * defining CSS and JS animations to work off of the same CSS class will not work anymore. Therefore the example below will only result in **JS animations taking
 * charge of the animation**:
 *
 * ```html
 * <div ng-if="bool" class="slide">
 *   Slide in and out
 * </div>
 * ```
 *
 * ```js
 * myModule.animation('.slide', [function() {
 *   return {
 *     enter: function(element, doneFn) {
 *       jQuery(element).slideIn(1000, doneFn);
 *     }
 *   }
 * }]);
 * ```
 *
 * ```css
 * .slide.ng-enter {
 *   transition:0.5s linear all;
 *   transform:translateY(-100px);
 * }
 * .slide.ng-enter.ng-enter-active {
 *   transform:translateY(0);
 * }
 * ```
 *
 * Does this mean that CSS and JS animations cannot be used together? Do JS-based animations always have higher priority? We can make up for the
 * lack of CSS animations by using the `$animateCss` service to trigger our own tweaked-out, CSS-based animations directly from
 * our own JS-based animation code:
 *
 * ```js
 * myModule.animation('.slide', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element) {
*        // this will trigger `.slide.ng-enter` and `.slide.ng-enter-active`.
 *       return $animateCss(element, {
 *         event: 'enter',
 *         structural: true
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * The nice thing here is that we can save bandwidth by sticking to our CSS-based animation code and we don't need to rely on a 3rd-party animation framework.
 *
 * The `$animateCss` service is very powerful since we can feed in all kinds of extra properties that will be evaluated and fed into a CSS transition or
 * keyframe animation. For example if we wanted to animate the height of an element while adding and removing classes then we can do so by providing that
 * data into `$animateCss` directly:
 *
 * ```js
 * myModule.animation('.slide', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element) {
 *       return $animateCss(element, {
 *         event: 'enter',
 *         structural: true,
 *         addClass: 'maroon-setting',
 *         from: { height:0 },
 *         to: { height: 200 }
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * Now we can fill in the rest via our transition CSS code:
 *
 * ```css
 * /&#42; the transition tells ngAnimate to make the animation happen &#42;/
 * .slide.ng-enter { transition:0.5s linear all; }
 *
 * /&#42; this extra CSS class will be absorbed into the transition
 * since the $animateCss code is adding the class &#42;/
 * .maroon-setting { background:red; }
 * ```
 *
 * And `$animateCss` will figure out the rest. Just make sure to have the `done()` callback fire the `doneFn` function to signal when the animation is over.
 *
 * To learn more about what's possible be sure to visit the {@link ngAnimate.$animateCss $animateCss service}.
 *
 * ## Animation Anchoring (via `ng-animate-ref`)
 *
 * ngAnimate in AngularJS 1.4 comes packed with the ability to cross-animate elements between
 * structural areas of an application (like views) by pairing up elements using an attribute
 * called `ng-animate-ref`.
 *
 * Let's say for example we have two views that are managed by `ng-view` and we want to show
 * that there is a relationship between two components situated in within these views. By using the
 * `ng-animate-ref` attribute we can identify that the two components are paired together and we
 * can then attach an animation, which is triggered when the view changes.
 *
 * Say for example we have the following template code:
 *
 * ```html
 * <!-- index.html -->
 * <div ng-view class="view-animation">
 * </div>
 *
 * <!-- home.html -->
 * <a href="#/banner-page">
 *   <img src="./banner.jpg" class="banner" ng-animate-ref="banner">
 * </a>
 *
 * <!-- banner-page.html -->
 * <img src="./banner.jpg" class="banner" ng-animate-ref="banner">
 * ```
 *
 * Now, when the view changes (once the link is clicked), ngAnimate will examine the
 * HTML contents to see if there is a match reference between any components in the view
 * that is leaving and the view that is entering. It will scan both the view which is being
 * removed (leave) and inserted (enter) to see if there are any paired DOM elements that
 * contain a matching ref value.
 *
 * The two images match since they share the same ref value. ngAnimate will now create a
 * transport element (which is a clone of the first image element) and it will then attempt
 * to animate to the position of the second image element in the next view. For the animation to
 * work a special CSS class called `ng-anchor` will be added to the transported element.
 *
 * We can now attach a transition onto the `.banner.ng-anchor` CSS class and then
 * ngAnimate will handle the entire transition for us as well as the addition and removal of
 * any changes of CSS classes between the elements:
 *
 * ```css
 * .banner.ng-anchor {
 *   /&#42; this animation will last for 1 second since there are
 *          two phases to the animation (an `in` and an `out` phase) &#42;/
 *   transition:0.5s linear all;
 * }
 * ```
 *
 * We also **must** include animations for the views that are being entered and removed
 * (otherwise anchoring wouldn't be possible since the new view would be inserted right away).
 *
 * ```css
 * .view-animation.ng-enter, .view-animation.ng-leave {
 *   transition:0.5s linear all;
 *   position:fixed;
 *   left:0;
 *   top:0;
 *   width:100%;
 * }
 * .view-animation.ng-enter {
 *   transform:translateX(100%);
 * }
 * .view-animation.ng-leave,
 * .view-animation.ng-enter.ng-enter-active {
 *   transform:translateX(0%);
 * }
 * .view-animation.ng-leave.ng-leave-active {
 *   transform:translateX(-100%);
 * }
 * ```
 *
 * Now we can jump back to the anchor animation. When the animation happens, there are two stages that occur:
 * an `out` and an `in` stage. The `out` stage happens first and that is when the element is animated away
 * from its origin. Once that animation is over then the `in` stage occurs which animates the
 * element to its destination. The reason why there are two animations is to give enough time
 * for the enter animation on the new element to be ready.
 *
 * The example above sets up a transition for both the in and out phases, but we can also target the out or
 * in phases directly via `ng-anchor-out` and `ng-anchor-in`.
 *
 * ```css
 * .banner.ng-anchor-out {
 *   transition: 0.5s linear all;
 *
 *   /&#42; the scale will be applied during the out animation,
 *          but will be animated away when the in animation runs &#42;/
 *   transform: scale(1.2);
 * }
 *
 * .banner.ng-anchor-in {
 *   transition: 1s linear all;
 * }
 * ```
 *
 *
 *
 *
 * ### Anchoring Demo
 *
  <example module="anchoringExample"
           name="anchoringExample"
           id="anchoringExample"
           deps="angular-animate.js;angular-route.js"
           animations="true">
    <file name="index.html">
      <a href="#!/">Home</a>
      <hr />
      <div class="view-container">
        <div ng-view class="view"></div>
      </div>
    </file>
    <file name="script.js">
      angular.module('anchoringExample', ['ngAnimate', 'ngRoute'])
        .config(['$routeProvider', function($routeProvider) {
          $routeProvider.when('/', {
            templateUrl: 'home.html',
            controller: 'HomeController as home'
          });
          $routeProvider.when('/profile/:id', {
            templateUrl: 'profile.html',
            controller: 'ProfileController as profile'
          });
        }])
        .run(['$rootScope', function($rootScope) {
          $rootScope.records = [
            { id: 1, title: 'Miss Beulah Roob' },
            { id: 2, title: 'Trent Morissette' },
            { id: 3, title: 'Miss Ava Pouros' },
            { id: 4, title: 'Rod Pouros' },
            { id: 5, title: 'Abdul Rice' },
            { id: 6, title: 'Laurie Rutherford Sr.' },
            { id: 7, title: 'Nakia McLaughlin' },
            { id: 8, title: 'Jordon Blanda DVM' },
            { id: 9, title: 'Rhoda Hand' },
            { id: 10, title: 'Alexandrea Sauer' }
          ];
        }])
        .controller('HomeController', [function() {
          //empty
        }])
        .controller('ProfileController', ['$rootScope', '$routeParams',
            function ProfileController($rootScope, $routeParams) {
          var index = parseInt($routeParams.id, 10);
          var record = $rootScope.records[index - 1];

          this.title = record.title;
          this.id = record.id;
        }]);
    </file>
    <file name="home.html">
      <h2>Welcome to the home page</h1>
      <p>Please click on an element</p>
      <a class="record"
         ng-href="#!/profile/{{ record.id }}"
         ng-animate-ref="{{ record.id }}"
         ng-repeat="record in records">
        {{ record.title }}
      </a>
    </file>
    <file name="profile.html">
      <div class="profile record" ng-animate-ref="{{ profile.id }}">
        {{ profile.title }}
      </div>
    </file>
    <file name="animations.css">
      .record {
        display:block;
        font-size:20px;
      }
      .profile {
        background:black;
        color:white;
        font-size:100px;
      }
      .view-container {
        position:relative;
      }
      .view-container > .view.ng-animate {
        position:absolute;
        top:0;
        left:0;
        width:100%;
        min-height:500px;
      }
      .view.ng-enter, .view.ng-leave,
      .record.ng-anchor {
        transition:0.5s linear all;
      }
      .view.ng-enter {
        transform:translateX(100%);
      }
      .view.ng-enter.ng-enter-active, .view.ng-leave {
        transform:translateX(0%);
      }
      .view.ng-leave.ng-leave-active {
        transform:translateX(-100%);
      }
      .record.ng-anchor-out {
        background:red;
      }
    </file>
  </example>
 *
 * ### How is the element transported?
 *
 * When an anchor animation occurs, ngAnimate will clone the starting element and position it exactly where the starting
 * element is located on screen via absolute positioning. The cloned element will be placed inside of the root element
 * of the application (where ng-app was defined) and all of the CSS classes of the starting element will be applied. The
 * element will then animate into the `out` and `in` animations and will eventually reach the coordinates and match
 * the dimensions of the destination element. During the entire animation a CSS class of `.ng-animate-shim` will be applied
 * to both the starting and destination elements in order to hide them from being visible (the CSS styling for the class
 * is: `visibility:hidden`). Once the anchor reaches its destination then it will be removed and the destination element
 * will become visible since the shim class will be removed.
 *
 * ### How is the morphing handled?
 *
 * CSS Anchoring relies on transitions and keyframes and the internal code is intelligent enough to figure out
 * what CSS classes differ between the starting element and the destination element. These different CSS classes
 * will be added/removed on the anchor element and a transition will be applied (the transition that is provided
 * in the anchor class). Long story short, ngAnimate will figure out what classes to add and remove which will
 * make the transition of the element as smooth and automatic as possible. Be sure to use simple CSS classes that
 * do not rely on DOM nesting structure so that the anchor element appears the same as the starting element (since
 * the cloned element is placed inside of root element which is likely close to the body element).
 *
 * Note that if the root element is on the `<html>` element then the cloned node will be placed inside of body.
 *
 *
 * ## Using $animate in your directive code
 *
 * So far we've explored how to feed in animations into an AngularJS application, but how do we trigger animations within our own directives in our application?
 * By injecting the `$animate` service into our directive code, we can trigger structural and class-based hooks which can then be consumed by animations. Let's
 * imagine we have a greeting box that shows and hides itself when the data changes
 *
 * ```html
 * <greeting-box active="onOrOff">Hi there</greeting-box>
 * ```
 *
 * ```js
 * ngModule.directive('greetingBox', ['$animate', function($animate) {
 *   return function(scope, element, attrs) {
 *     attrs.$observe('active', function(value) {
 *       value ? $animate.addClass(element, 'on') : $animate.removeClass(element, 'on');
 *     });
 *   });
 * }]);
 * ```
 *
 * Now the `on` CSS class is added and removed on the greeting box component. Now if we add a CSS class on top of the greeting box element
 * in our HTML code then we can trigger a CSS or JS animation to happen.
 *
 * ```css
 * /&#42; normally we would create a CSS class to reference on the element &#42;/
 * greeting-box.on { transition:0.5s linear all; background:green; color:white; }
 * ```
 *
 * The `$animate` service contains a variety of other methods like `enter`, `leave`, `animate` and `setClass`. To learn more about what's
 * possible be sure to visit the {@link ng.$animate $animate service API page}.
 *
 *
 * ## Callbacks and Promises
 *
 * When `$animate` is called it returns a promise that can be used to capture when the animation has ended. Therefore if we were to trigger
 * an animation (within our directive code) then we can continue performing directive and scope related activities after the animation has
 * ended by chaining onto the returned promise that animation method returns.
 *
 * ```js
 * // somewhere within the depths of the directive
 * $animate.enter(element, parent).then(function() {
 *   //the animation has completed
 * });
 * ```
 *
 * (Note that earlier versions of AngularJS prior to v1.4 required the promise code to be wrapped using `$scope.$apply(...)`. This is not the case
 * anymore.)
 *
 * In addition to the animation promise, we can also make use of animation-related callbacks within our directives and controller code by registering
 * an event listener using the `$animate` service. Let's say for example that an animation was triggered on our view
 * routing controller to hook into that:
 *
 * ```js
 * ngModule.controller('HomePageController', ['$animate', function($animate) {
 *   $animate.on('enter', ngViewElement, function(element) {
 *     // the animation for this route has completed
 *   }]);
 * }])
 * ```
 *
 * (Note that you will need to trigger a digest within the callback to get AngularJS to notice any scope-related changes.)
 */

var copy;
var extend;
var forEach;
var isArray;
var isDefined;
var isElement;
var isFunction;
var isObject;
var isString;
var isUndefined;
var jqLite;
var noop;

/**
 * @ngdoc service
 * @name $animate
 * @kind object
 *
 * @description
 * The ngAnimate `$animate` service documentation is the same for the core `$animate` service.
 *
 * Click here {@link ng.$animate to learn more about animations with `$animate`}.
 */
angular.module('ngAnimate', [], function initAngularHelpers() {
  // Access helpers from AngularJS core.
  // Do it inside a `config` block to ensure `window.angular` is available.
  noop        = angular.noop;
  copy        = angular.copy;
  extend      = angular.extend;
  jqLite      = angular.element;
  forEach     = angular.forEach;
  isArray     = angular.isArray;
  isString    = angular.isString;
  isObject    = angular.isObject;
  isUndefined = angular.isUndefined;
  isDefined   = angular.isDefined;
  isFunction  = angular.isFunction;
  isElement   = angular.isElement;
})
  .info({ angularVersion: '1.8.1' })
  .directive('ngAnimateSwap', ngAnimateSwapDirective)

  .directive('ngAnimateChildren', $$AnimateChildrenDirective)
  .factory('$$rAFScheduler', $$rAFSchedulerFactory)

  .provider('$$animateQueue', $$AnimateQueueProvider)
  .provider('$$animateCache', $$AnimateCacheProvider)
  .provider('$$animation', $$AnimationProvider)

  .provider('$animateCss', $AnimateCssProvider)
  .provider('$$animateCssDriver', $$AnimateCssDriverProvider)

  .provider('$$animateJs', $$AnimateJsProvider)
  .provider('$$animateJsDriver', $$AnimateJsDriverProvider);


})(window, window.angular);

})();
(function () {
/**
 * @license AngularJS v1.8.1
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/**
 * @ngdoc module
 * @name ngTouch
 * @description
 *
 * The `ngTouch` module provides helpers for touch-enabled devices.
 * The implementation is based on jQuery Mobile touch event handling
 * ([jquerymobile.com](http://jquerymobile.com/)). *
 *
 * See {@link ngTouch.$swipe `$swipe`} for usage.
 *
 * @deprecated
 * sinceVersion="1.7.0"
 * The ngTouch module with the {@link ngTouch.$swipe `$swipe`} service and
 * the {@link ngTouch.ngSwipeLeft} and {@link ngTouch.ngSwipeRight} directives are
 * deprecated. Instead, stand-alone libraries for touch handling and gesture interaction
 * should be used, for example [HammerJS](https://hammerjs.github.io/) (which is also used by
 * Angular).
 */

// define ngTouch module
/* global ngTouch */
var ngTouch = angular.module('ngTouch', []);

ngTouch.info({ angularVersion: '1.8.1' });

function nodeName_(element) {
  return angular.$$lowercase(element.nodeName || (element[0] && element[0].nodeName));
}

/* global ngTouch: false */

    /**
     * @ngdoc service
     * @name $swipe
     *
     * @deprecated
     * sinceVersion="1.7.0"
     *
     * See the {@link ngTouch module} documentation for more information.
     *
     * @description
     * The `$swipe` service is a service that abstracts the messier details of hold-and-drag swipe
     * behavior, to make implementing swipe-related directives more convenient.
     *
     * Requires the {@link ngTouch `ngTouch`} module to be installed.
     *
     * `$swipe` is used by the `ngSwipeLeft` and `ngSwipeRight` directives in `ngTouch`.
     *
     * # Usage
     * The `$swipe` service is an object with a single method: `bind`. `bind` takes an element
     * which is to be watched for swipes, and an object with four handler functions. See the
     * documentation for `bind` below.
     */

ngTouch.factory('$swipe', [function() {
  // The total distance in any direction before we make the call on swipe vs. scroll.
  var MOVE_BUFFER_RADIUS = 10;

  var POINTER_EVENTS = {
    'mouse': {
      start: 'mousedown',
      move: 'mousemove',
      end: 'mouseup'
    },
    'touch': {
      start: 'touchstart',
      move: 'touchmove',
      end: 'touchend',
      cancel: 'touchcancel'
    },
    'pointer': {
      start: 'pointerdown',
      move: 'pointermove',
      end: 'pointerup',
      cancel: 'pointercancel'
    }
  };

  function getCoordinates(event) {
    var originalEvent = event.originalEvent || event;
    var touches = originalEvent.touches && originalEvent.touches.length ? originalEvent.touches : [originalEvent];
    var e = (originalEvent.changedTouches && originalEvent.changedTouches[0]) || touches[0];

    return {
      x: e.clientX,
      y: e.clientY
    };
  }

  function getEvents(pointerTypes, eventType) {
    var res = [];
    angular.forEach(pointerTypes, function(pointerType) {
      var eventName = POINTER_EVENTS[pointerType][eventType];
      if (eventName) {
        res.push(eventName);
      }
    });
    return res.join(' ');
  }

  return {
    /**
     * @ngdoc method
     * @name $swipe#bind
     *
     * @description
     * The main method of `$swipe`. It takes an element to be watched for swipe motions, and an
     * object containing event handlers.
     * The pointer types that should be used can be specified via the optional
     * third argument, which is an array of strings `'mouse'`, `'touch'` and `'pointer'`. By default,
     * `$swipe` will listen for `mouse`, `touch` and `pointer` events.
     *
     * The four events are `start`, `move`, `end`, and `cancel`. `start`, `move`, and `end`
     * receive as a parameter a coordinates object of the form `{ x: 150, y: 310 }` and the raw
     * `event`. `cancel` receives the raw `event` as its single parameter.
     *
     * `start` is called on either `mousedown`, `touchstart` or `pointerdown`. After this event, `$swipe` is
     * watching for `touchmove`, `mousemove` or `pointermove` events. These events are ignored until the total
     * distance moved in either dimension exceeds a small threshold.
     *
     * Once this threshold is exceeded, either the horizontal or vertical delta is greater.
     * - If the horizontal distance is greater, this is a swipe and `move` and `end` events follow.
     * - If the vertical distance is greater, this is a scroll, and we let the browser take over.
     *   A `cancel` event is sent.
     *
     * `move` is called on `mousemove`, `touchmove` and `pointermove` after the above logic has determined that
     * a swipe is in progress.
     *
     * `end` is called when a swipe is successfully completed with a `touchend`, `mouseup` or `pointerup`.
     *
     * `cancel` is called either on a `touchcancel` or `pointercancel`  from the browser, or when we begin scrolling
     * as described above.
     *
     */
    bind: function(element, eventHandlers, pointerTypes) {
      // Absolute total movement, used to control swipe vs. scroll.
      var totalX, totalY;
      // Coordinates of the start position.
      var startCoords;
      // Last event's position.
      var lastPos;
      // Whether a swipe is active.
      var active = false;

      pointerTypes = pointerTypes || ['mouse', 'touch', 'pointer'];
      element.on(getEvents(pointerTypes, 'start'), function(event) {
        startCoords = getCoordinates(event);
        active = true;
        totalX = 0;
        totalY = 0;
        lastPos = startCoords;
        if (eventHandlers['start']) {
          eventHandlers['start'](startCoords, event);
        }
      });
      var events = getEvents(pointerTypes, 'cancel');
      if (events) {
        element.on(events, function(event) {
          active = false;
          if (eventHandlers['cancel']) {
            eventHandlers['cancel'](event);
          }
        });
      }

      element.on(getEvents(pointerTypes, 'move'), function(event) {
        if (!active) return;

        // Android will send a touchcancel if it thinks we're starting to scroll.
        // So when the total distance (+ or - or both) exceeds 10px in either direction,
        // we either:
        // - On totalX > totalY, we send preventDefault() and treat this as a swipe.
        // - On totalY > totalX, we let the browser handle it as a scroll.

        if (!startCoords) return;
        var coords = getCoordinates(event);

        totalX += Math.abs(coords.x - lastPos.x);
        totalY += Math.abs(coords.y - lastPos.y);

        lastPos = coords;

        if (totalX < MOVE_BUFFER_RADIUS && totalY < MOVE_BUFFER_RADIUS) {
          return;
        }

        // One of totalX or totalY has exceeded the buffer, so decide on swipe vs. scroll.
        if (totalY > totalX) {
          // Allow native scrolling to take over.
          active = false;
          if (eventHandlers['cancel']) {
            eventHandlers['cancel'](event);
          }
          return;
        } else {
          // Prevent the browser from scrolling.
          event.preventDefault();
          if (eventHandlers['move']) {
            eventHandlers['move'](coords, event);
          }
        }
      });

      element.on(getEvents(pointerTypes, 'end'), function(event) {
        if (!active) return;
        active = false;
        if (eventHandlers['end']) {
          eventHandlers['end'](getCoordinates(event), event);
        }
      });
    }
  };
}]);

/* global ngTouch: false */

/**
 * @ngdoc directive
 * @name ngSwipeLeft
 *
 * @deprecated
 * sinceVersion="1.7.0"
 *
 * See the {@link ngTouch module} documentation for more information.
 *
 * @description
 * Specify custom behavior when an element is swiped to the left on a touchscreen device.
 * A leftward swipe is a quick, right-to-left slide of the finger.
 * Though ngSwipeLeft is designed for touch-based devices, it will work with a mouse click and drag
 * too.
 *
 * To disable the mouse click and drag functionality, add `ng-swipe-disable-mouse` to
 * the `ng-swipe-left` or `ng-swipe-right` DOM Element.
 *
 * Requires the {@link ngTouch `ngTouch`} module to be installed.
 *
 * @element ANY
 * @param {expression} ngSwipeLeft {@link guide/expression Expression} to evaluate
 * upon left swipe. (Event object is available as `$event`)
 *
 * @example
    <example module="ngSwipeLeftExample" deps="angular-touch.js" name="ng-swipe-left">
      <file name="index.html">
        <div ng-show="!showActions" ng-swipe-left="showActions = true">
          Some list content, like an email in the inbox
        </div>
        <div ng-show="showActions" ng-swipe-right="showActions = false">
          <button ng-click="reply()">Reply</button>
          <button ng-click="delete()">Delete</button>
        </div>
      </file>
      <file name="script.js">
        angular.module('ngSwipeLeftExample', ['ngTouch']);
      </file>
    </example>
 */

/**
 * @ngdoc directive
 * @name ngSwipeRight
 *
 * @deprecated
 * sinceVersion="1.7.0"
 *
 * See the {@link ngTouch module} documentation for more information.
 *
 * @description
 * Specify custom behavior when an element is swiped to the right on a touchscreen device.
 * A rightward swipe is a quick, left-to-right slide of the finger.
 * Though ngSwipeRight is designed for touch-based devices, it will work with a mouse click and drag
 * too.
 *
 * Requires the {@link ngTouch `ngTouch`} module to be installed.
 *
 * @element ANY
 * @param {expression} ngSwipeRight {@link guide/expression Expression} to evaluate
 * upon right swipe. (Event object is available as `$event`)
 *
 * @example
    <example module="ngSwipeRightExample" deps="angular-touch.js" name="ng-swipe-right">
      <file name="index.html">
        <div ng-show="!showActions" ng-swipe-left="showActions = true">
          Some list content, like an email in the inbox
        </div>
        <div ng-show="showActions" ng-swipe-right="showActions = false">
          <button ng-click="reply()">Reply</button>
          <button ng-click="delete()">Delete</button>
        </div>
      </file>
      <file name="script.js">
        angular.module('ngSwipeRightExample', ['ngTouch']);
      </file>
    </example>
 */

function makeSwipeDirective(directiveName, direction, eventName) {
  ngTouch.directive(directiveName, ['$parse', '$swipe', function($parse, $swipe) {
    // The maximum vertical delta for a swipe should be less than 75px.
    var MAX_VERTICAL_DISTANCE = 75;
    // Vertical distance should not be more than a fraction of the horizontal distance.
    var MAX_VERTICAL_RATIO = 0.3;
    // At least a 30px lateral motion is necessary for a swipe.
    var MIN_HORIZONTAL_DISTANCE = 30;

    return function(scope, element, attr) {
      var swipeHandler = $parse(attr[directiveName]);

      var startCoords, valid;

      function validSwipe(coords) {
        // Check that it's within the coordinates.
        // Absolute vertical distance must be within tolerances.
        // Horizontal distance, we take the current X - the starting X.
        // This is negative for leftward swipes and positive for rightward swipes.
        // After multiplying by the direction (-1 for left, +1 for right), legal swipes
        // (ie. same direction as the directive wants) will have a positive delta and
        // illegal ones a negative delta.
        // Therefore this delta must be positive, and larger than the minimum.
        if (!startCoords) return false;
        var deltaY = Math.abs(coords.y - startCoords.y);
        var deltaX = (coords.x - startCoords.x) * direction;
        return valid && // Short circuit for already-invalidated swipes.
            deltaY < MAX_VERTICAL_DISTANCE &&
            deltaX > 0 &&
            deltaX > MIN_HORIZONTAL_DISTANCE &&
            deltaY / deltaX < MAX_VERTICAL_RATIO;
      }

      var pointerTypes = ['touch'];
      if (!angular.isDefined(attr['ngSwipeDisableMouse'])) {
        pointerTypes.push('mouse');
      }
      $swipe.bind(element, {
        'start': function(coords, event) {
          startCoords = coords;
          valid = true;
        },
        'cancel': function(event) {
          valid = false;
        },
        'end': function(coords, event) {
          if (validSwipe(coords)) {
            scope.$apply(function() {
              element.triggerHandler(eventName);
              swipeHandler(scope, {$event: event});
            });
          }
        }
      }, pointerTypes);
    };
  }]);
}

// Left is negative X-coordinate, right is positive.
makeSwipeDirective('ngSwipeLeft', -1, 'swipeleft');
makeSwipeDirective('ngSwipeRight', 1, 'swiperight');



})(window, window.angular);

})();
(function () {
/**
 * @license AngularJS v1.8.1
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/**
 * @ngdoc module
 * @name ngCookies
 * @description
 *
 * The `ngCookies` module provides a convenient wrapper for reading and writing browser cookies.
 *
 * See {@link ngCookies.$cookies `$cookies`} for usage.
 */


angular.module('ngCookies', ['ng']).
  info({ angularVersion: '1.8.1' }).
  /**
   * @ngdoc provider
   * @name $cookiesProvider
   * @description
   * Use `$cookiesProvider` to change the default behavior of the {@link ngCookies.$cookies $cookies} service.
   * */
   provider('$cookies', [/** @this */function $CookiesProvider() {
    /**
     * @ngdoc property
     * @name $cookiesProvider#defaults
     * @description
     *
     * Object containing default options to pass when setting cookies.
     *
     * The object may have following properties:
     *
     * - **path** - `{string}` - The cookie will be available only for this path and its
     *   sub-paths. By default, this is the URL that appears in your `<base>` tag.
     * - **domain** - `{string}` - The cookie will be available only for this domain and
     *   its sub-domains. For security reasons the user agent will not accept the cookie
     *   if the current domain is not a sub-domain of this domain or equal to it.
     * - **expires** - `{string|Date}` - String of the form "Wdy, DD Mon YYYY HH:MM:SS GMT"
     *   or a Date object indicating the exact date/time this cookie will expire.
     * - **secure** - `{boolean}` - If `true`, then the cookie will only be available through a
     *   secured connection.
     * - **samesite** - `{string}` - prevents the browser from sending the cookie along with cross-site requests.
     *   Accepts the values `lax` and `strict`. See the [OWASP Wiki](https://www.owasp.org/index.php/SameSite)
     *   for more info. Note that as of May 2018, not all browsers support `SameSite`,
     *   so it cannot be used as a single measure against Cross-Site-Request-Forgery (CSRF) attacks.
     *
     * Note: By default, the address that appears in your `<base>` tag will be used as the path.
     * This is important so that cookies will be visible for all routes when html5mode is enabled.
     *
     * @example
     *
     * ```js
     * angular.module('cookiesProviderExample', ['ngCookies'])
     *   .config(['$cookiesProvider', function($cookiesProvider) {
     *     // Setting default options
     *     $cookiesProvider.defaults.domain = 'foo.com';
     *     $cookiesProvider.defaults.secure = true;
     *   }]);
     * ```
     **/
    var defaults = this.defaults = {};

    function calcOptions(options) {
      return options ? angular.extend({}, defaults, options) : defaults;
    }

    /**
     * @ngdoc service
     * @name $cookies
     *
     * @description
     * Provides read/write access to browser's cookies.
     *
     * <div class="alert alert-info">
     * Up until AngularJS 1.3, `$cookies` exposed properties that represented the
     * current browser cookie values. In version 1.4, this behavior has changed, and
     * `$cookies` now provides a standard api of getters, setters etc.
     * </div>
     *
     * Requires the {@link ngCookies `ngCookies`} module to be installed.
     *
     * @example
     *
     * ```js
     * angular.module('cookiesExample', ['ngCookies'])
     *   .controller('ExampleController', ['$cookies', function($cookies) {
     *     // Retrieving a cookie
     *     var favoriteCookie = $cookies.get('myFavorite');
     *     // Setting a cookie
     *     $cookies.put('myFavorite', 'oatmeal');
     *   }]);
     * ```
     */
    this.$get = ['$$cookieReader', '$$cookieWriter', function($$cookieReader, $$cookieWriter) {
      return {
        /**
         * @ngdoc method
         * @name $cookies#get
         *
         * @description
         * Returns the value of given cookie key
         *
         * @param {string} key Id to use for lookup.
         * @returns {string} Raw cookie value.
         */
        get: function(key) {
          return $$cookieReader()[key];
        },

        /**
         * @ngdoc method
         * @name $cookies#getObject
         *
         * @description
         * Returns the deserialized value of given cookie key
         *
         * @param {string} key Id to use for lookup.
         * @returns {Object} Deserialized cookie value.
         */
        getObject: function(key) {
          var value = this.get(key);
          return value ? angular.fromJson(value) : value;
        },

        /**
         * @ngdoc method
         * @name $cookies#getAll
         *
         * @description
         * Returns a key value object with all the cookies
         *
         * @returns {Object} All cookies
         */
        getAll: function() {
          return $$cookieReader();
        },

        /**
         * @ngdoc method
         * @name $cookies#put
         *
         * @description
         * Sets a value for given cookie key
         *
         * @param {string} key Id for the `value`.
         * @param {string} value Raw value to be stored.
         * @param {Object=} options Options object.
         *    See {@link ngCookies.$cookiesProvider#defaults $cookiesProvider.defaults}
         */
        put: function(key, value, options) {
          $$cookieWriter(key, value, calcOptions(options));
        },

        /**
         * @ngdoc method
         * @name $cookies#putObject
         *
         * @description
         * Serializes and sets a value for given cookie key
         *
         * @param {string} key Id for the `value`.
         * @param {Object} value Value to be stored.
         * @param {Object=} options Options object.
         *    See {@link ngCookies.$cookiesProvider#defaults $cookiesProvider.defaults}
         */
        putObject: function(key, value, options) {
          this.put(key, angular.toJson(value), options);
        },

        /**
         * @ngdoc method
         * @name $cookies#remove
         *
         * @description
         * Remove given cookie
         *
         * @param {string} key Id of the key-value pair to delete.
         * @param {Object=} options Options object.
         *    See {@link ngCookies.$cookiesProvider#defaults $cookiesProvider.defaults}
         */
        remove: function(key, options) {
          $$cookieWriter(key, undefined, calcOptions(options));
        }
      };
    }];
  }]);

/**
 * @name $$cookieWriter
 * @requires $document
 *
 * @description
 * This is a private service for writing cookies
 *
 * @param {string} name Cookie name
 * @param {string=} value Cookie value (if undefined, cookie will be deleted)
 * @param {Object=} options Object with options that need to be stored for the cookie.
 */
function $$CookieWriter($document, $log, $browser) {
  var cookiePath = $browser.baseHref();
  var rawDocument = $document[0];

  function buildCookieString(name, value, options) {
    var path, expires;
    options = options || {};
    expires = options.expires;
    path = angular.isDefined(options.path) ? options.path : cookiePath;
    if (angular.isUndefined(value)) {
      expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
      value = '';
    }
    if (angular.isString(expires)) {
      expires = new Date(expires);
    }

    var str = encodeURIComponent(name) + '=' + encodeURIComponent(value);
    str += path ? ';path=' + path : '';
    str += options.domain ? ';domain=' + options.domain : '';
    str += expires ? ';expires=' + expires.toUTCString() : '';
    str += options.secure ? ';secure' : '';
    str += options.samesite ? ';samesite=' + options.samesite : '';

    // per http://www.ietf.org/rfc/rfc2109.txt browser must allow at minimum:
    // - 300 cookies
    // - 20 cookies per unique domain
    // - 4096 bytes per cookie
    var cookieLength = str.length + 1;
    if (cookieLength > 4096) {
      $log.warn('Cookie \'' + name +
        '\' possibly not set or overflowed because it was too large (' +
        cookieLength + ' > 4096 bytes)!');
    }

    return str;
  }

  return function(name, value, options) {
    rawDocument.cookie = buildCookieString(name, value, options);
  };
}

$$CookieWriter.$inject = ['$document', '$log', '$browser'];

angular.module('ngCookies').provider('$$cookieWriter', /** @this */ function $$CookieWriterProvider() {
  this.$get = $$CookieWriter;
});


})(window, window.angular);

})();
(function () {
!function(){angular.module("angular-storage",["angular-storage.store"]),angular.module("angular-storage.cookieStorage",[]).service("cookieStorage",["$cookies",function(e){this.set=function(t,r){return e.put(t,r)},this.get=function(t){return e.get(t)},this.remove=function(t){return e.remove(t)}}]),angular.module("angular-storage.internalStore",["angular-storage.localStorage","angular-storage.sessionStorage"]).factory("InternalStore",["$log","$injector",function(e,t){function r(e,r,o,a){this.namespace=e||null,(angular.isUndefined(a)||null==a)&&(a=!0),this.useCache=a,this.delimiter=o||".",this.inMemoryCache={},this.storage=t.get(r||"localStorage")}return r.prototype.getNamespacedKey=function(e){return this.namespace?[this.namespace,e].join(this.delimiter):e},r.prototype.set=function(e,t){this.useCache&&(this.inMemoryCache[e]=t),this.storage.set(this.getNamespacedKey(e),JSON.stringify(t))},r.prototype.get=function(t){var r=null;if(this.useCache&&t in this.inMemoryCache)return this.inMemoryCache[t];var o=this.storage.get(this.getNamespacedKey(t));try{r="undefined"==typeof o||"undefined"===o?void 0:JSON.parse(o),this.useCache&&(this.inMemoryCache[t]=r)}catch(a){e.error("Error parsing saved value",a),this.remove(t)}return r},r.prototype.remove=function(e){this.useCache&&(this.inMemoryCache[e]=null),this.storage.remove(this.getNamespacedKey(e))},r}]),angular.module("angular-storage.localStorage",["angular-storage.cookieStorage"]).service("localStorage",["$window","$injector",function(e,t){var r;try{e.localStorage.setItem("testKey","test"),e.localStorage.removeItem("testKey"),r=!0}catch(o){r=!1}if(r)this.set=function(t,r){return e.localStorage.setItem(t,r)},this.get=function(t){return e.localStorage.getItem(t)},this.remove=function(t){return e.localStorage.removeItem(t)},this.clear=function(){e.localStorage.clear()};else{var a=t.get("cookieStorage");this.set=a.set,this.get=a.get,this.remove=a.remove}}]),angular.module("angular-storage.sessionStorage",["angular-storage.cookieStorage"]).service("sessionStorage",["$window","$injector",function(e,t){var r;try{e.sessionStorage.setItem("testKey","test"),e.sessionStorage.removeItem("testKey"),r=!0}catch(o){r=!1}if(r)this.set=function(t,r){return e.sessionStorage.setItem(t,r)},this.get=function(t){return e.sessionStorage.getItem(t)},this.remove=function(t){return e.sessionStorage.removeItem(t)};else{var a=t.get("cookieStorage");this.set=a.set,this.get=a.get,this.remove=a.remove}}]),angular.module("angular-storage.store",["angular-storage.internalStore"]).provider("store",function(){var e="localStorage",t=!0;this.setStore=function(t){t&&angular.isString(t)&&(e=t)},this.setCaching=function(e){t=!!e},this.$get=["InternalStore",function(r){var o=new r(null,e,null,t);return o.getNamespacedStore=function(e,t,o,a){return new r(e,t,o,a)},o}]})}();
})();
(function () {
/**
 * @license AngularJS v1.8.1
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/**
 * @ngdoc module
 * @name ngAria
 * @description
 *
 * The `ngAria` module provides support for common
 * [<abbr title="Accessible Rich Internet Applications">ARIA</abbr>](http://www.w3.org/TR/wai-aria/)
 * attributes that convey state or semantic information about the application for users
 * of assistive technologies, such as screen readers.
 *
 * ## Usage
 *
 * For ngAria to do its magic, simply include the module `ngAria` as a dependency. The following
 * directives are supported:
 * `ngModel`, `ngChecked`, `ngReadonly`, `ngRequired`, `ngValue`, `ngDisabled`, `ngShow`, `ngHide`,
 * `ngClick`, `ngDblClick`, and `ngMessages`.
 *
 * Below is a more detailed breakdown of the attributes handled by ngAria:
 *
 * | Directive                                   | Supported Attributes                                                                                |
 * |---------------------------------------------|-----------------------------------------------------------------------------------------------------|
 * | {@link ng.directive:ngModel ngModel}        | aria-checked, aria-valuemin, aria-valuemax, aria-valuenow, aria-invalid, aria-required, input roles |
 * | {@link ng.directive:ngDisabled ngDisabled}  | aria-disabled                                                                                       |
 * | {@link ng.directive:ngRequired ngRequired}  | aria-required                                                                                       |
 * | {@link ng.directive:ngChecked ngChecked}    | aria-checked                                                                                        |
 * | {@link ng.directive:ngReadonly ngReadonly}  | aria-readonly                                                                                       |
 * | {@link ng.directive:ngValue ngValue}        | aria-checked                                                                                        |
 * | {@link ng.directive:ngShow ngShow}          | aria-hidden                                                                                         |
 * | {@link ng.directive:ngHide ngHide}          | aria-hidden                                                                                         |
 * | {@link ng.directive:ngDblclick ngDblclick}  | tabindex                                                                                            |
 * | {@link module:ngMessages ngMessages}        | aria-live                                                                                           |
 * | {@link ng.directive:ngClick ngClick}        | tabindex, keydown event, button role                                                                |
 *
 * Find out more information about each directive by reading the
 * {@link guide/accessibility ngAria Developer Guide}.
 *
 * ## Example
 * Using ngDisabled with ngAria:
 * ```html
 * <md-checkbox ng-disabled="disabled">
 * ```
 * Becomes:
 * ```html
 * <md-checkbox ng-disabled="disabled" aria-disabled="true">
 * ```
 *
 * ## Disabling Specific Attributes
 * It is possible to disable individual attributes added by ngAria with the
 * {@link ngAria.$ariaProvider#config config} method. For more details, see the
 * {@link guide/accessibility Developer Guide}.
 *
 * ## Disabling `ngAria` on Specific Elements
 * It is possible to make `ngAria` ignore a specific element, by adding the `ng-aria-disable`
 * attribute on it. Note that only the element itself (and not its child elements) will be ignored.
 */
var ARIA_DISABLE_ATTR = 'ngAriaDisable';

var ngAriaModule = angular.module('ngAria', ['ng']).
                        info({ angularVersion: '1.8.1' }).
                        provider('$aria', $AriaProvider);

/**
* Internal Utilities
*/
var nativeAriaNodeNames = ['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT', 'DETAILS', 'SUMMARY'];

var isNodeOneOf = function(elem, nodeTypeArray) {
  if (nodeTypeArray.indexOf(elem[0].nodeName) !== -1) {
    return true;
  }
};
/**
 * @ngdoc provider
 * @name $ariaProvider
 * @this
 *
 * @description
 *
 * Used for configuring the ARIA attributes injected and managed by ngAria.
 *
 * ```js
 * angular.module('myApp', ['ngAria'], function config($ariaProvider) {
 *   $ariaProvider.config({
 *     ariaValue: true,
 *     tabindex: false
 *   });
 * });
 *```
 *
 * ## Dependencies
 * Requires the {@link ngAria} module to be installed.
 *
 */
function $AriaProvider() {
  var config = {
    ariaHidden: true,
    ariaChecked: true,
    ariaReadonly: true,
    ariaDisabled: true,
    ariaRequired: true,
    ariaInvalid: true,
    ariaValue: true,
    tabindex: true,
    bindKeydown: true,
    bindRoleForClick: true
  };

  /**
   * @ngdoc method
   * @name $ariaProvider#config
   *
   * @param {object} config object to enable/disable specific ARIA attributes
   *
   *  - **ariaHidden** – `{boolean}` – Enables/disables aria-hidden tags
   *  - **ariaChecked** – `{boolean}` – Enables/disables aria-checked tags
   *  - **ariaReadonly** – `{boolean}` – Enables/disables aria-readonly tags
   *  - **ariaDisabled** – `{boolean}` – Enables/disables aria-disabled tags
   *  - **ariaRequired** – `{boolean}` – Enables/disables aria-required tags
   *  - **ariaInvalid** – `{boolean}` – Enables/disables aria-invalid tags
   *  - **ariaValue** – `{boolean}` – Enables/disables aria-valuemin, aria-valuemax and
   *    aria-valuenow tags
   *  - **tabindex** – `{boolean}` – Enables/disables tabindex tags
   *  - **bindKeydown** – `{boolean}` – Enables/disables keyboard event binding on non-interactive
   *    elements (such as `div` or `li`) using ng-click, making them more accessible to users of
   *    assistive technologies
   *  - **bindRoleForClick** – `{boolean}` – Adds role=button to non-interactive elements (such as
   *    `div` or `li`) using ng-click, making them more accessible to users of assistive
   *    technologies
   *
   * @description
   * Enables/disables various ARIA attributes
   */
  this.config = function(newConfig) {
    config = angular.extend(config, newConfig);
  };

  function watchExpr(attrName, ariaAttr, nativeAriaNodeNames, negate) {
    return function(scope, elem, attr) {
      if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

      var ariaCamelName = attr.$normalize(ariaAttr);
      if (config[ariaCamelName] && !isNodeOneOf(elem, nativeAriaNodeNames) && !attr[ariaCamelName]) {
        scope.$watch(attr[attrName], function(boolVal) {
          // ensure boolean value
          boolVal = negate ? !boolVal : !!boolVal;
          elem.attr(ariaAttr, boolVal);
        });
      }
    };
  }
  /**
   * @ngdoc service
   * @name $aria
   *
   * @description
   *
   * The $aria service contains helper methods for applying common
   * [ARIA](http://www.w3.org/TR/wai-aria/) attributes to HTML directives.
   *
   * ngAria injects common accessibility attributes that tell assistive technologies when HTML
   * elements are enabled, selected, hidden, and more. To see how this is performed with ngAria,
   * let's review a code snippet from ngAria itself:
   *
   *```js
   * ngAriaModule.directive('ngDisabled', ['$aria', function($aria) {
   *   return $aria.$$watchExpr('ngDisabled', 'aria-disabled', nativeAriaNodeNames, false);
   * }])
   *```
   * Shown above, the ngAria module creates a directive with the same signature as the
   * traditional `ng-disabled` directive. But this ngAria version is dedicated to
   * solely managing accessibility attributes on custom elements. The internal `$aria` service is
   * used to watch the boolean attribute `ngDisabled`. If it has not been explicitly set by the
   * developer, `aria-disabled` is injected as an attribute with its value synchronized to the
   * value in `ngDisabled`.
   *
   * Because ngAria hooks into the `ng-disabled` directive, developers do not have to do
   * anything to enable this feature. The `aria-disabled` attribute is automatically managed
   * simply as a silent side-effect of using `ng-disabled` with the ngAria module.
   *
   * The full list of directives that interface with ngAria:
   * * **ngModel**
   * * **ngChecked**
   * * **ngReadonly**
   * * **ngRequired**
   * * **ngDisabled**
   * * **ngValue**
   * * **ngShow**
   * * **ngHide**
   * * **ngClick**
   * * **ngDblclick**
   * * **ngMessages**
   *
   * Read the {@link guide/accessibility ngAria Developer Guide} for a thorough explanation of each
   * directive.
   *
   *
   * ## Dependencies
   * Requires the {@link ngAria} module to be installed.
   */
  this.$get = function() {
    return {
      config: function(key) {
        return config[key];
      },
      $$watchExpr: watchExpr
    };
  };
}


ngAriaModule.directive('ngShow', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngShow', 'aria-hidden', [], true);
}])
.directive('ngHide', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngHide', 'aria-hidden', [], false);
}])
.directive('ngValue', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngValue', 'aria-checked', nativeAriaNodeNames, false);
}])
.directive('ngChecked', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngChecked', 'aria-checked', nativeAriaNodeNames, false);
}])
.directive('ngReadonly', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngReadonly', 'aria-readonly', nativeAriaNodeNames, false);
}])
.directive('ngRequired', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngRequired', 'aria-required', nativeAriaNodeNames, false);
}])
.directive('ngModel', ['$aria', function($aria) {

  function shouldAttachAttr(attr, normalizedAttr, elem, allowNonAriaNodes) {
    return $aria.config(normalizedAttr) &&
      !elem.attr(attr) &&
      (allowNonAriaNodes || !isNodeOneOf(elem, nativeAriaNodeNames)) &&
      (elem.attr('type') !== 'hidden' || elem[0].nodeName !== 'INPUT');
  }

  function shouldAttachRole(role, elem) {
    // if element does not have role attribute
    // AND element type is equal to role (if custom element has a type equaling shape) <-- remove?
    // AND element is not in nativeAriaNodeNames
    return !elem.attr('role') && (elem.attr('type') === role) && !isNodeOneOf(elem, nativeAriaNodeNames);
  }

  function getShape(attr, elem) {
    var type = attr.type,
        role = attr.role;

    return ((type || role) === 'checkbox' || role === 'menuitemcheckbox') ? 'checkbox' :
           ((type || role) === 'radio'    || role === 'menuitemradio') ? 'radio' :
           (type === 'range'              || role === 'progressbar' || role === 'slider') ? 'range' : '';
  }

  return {
    restrict: 'A',
    require: 'ngModel',
    priority: 200, //Make sure watches are fired after any other directives that affect the ngModel value
    compile: function(elem, attr) {
      if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

      var shape = getShape(attr, elem);

      return {
        post: function(scope, elem, attr, ngModel) {
          var needsTabIndex = shouldAttachAttr('tabindex', 'tabindex', elem, false);

          function ngAriaWatchModelValue() {
            return ngModel.$modelValue;
          }

          function getRadioReaction(newVal) {
            // Strict comparison would cause a BC
            // eslint-disable-next-line eqeqeq
            var boolVal = (attr.value == ngModel.$viewValue);
            elem.attr('aria-checked', boolVal);
          }

          function getCheckboxReaction() {
            elem.attr('aria-checked', !ngModel.$isEmpty(ngModel.$viewValue));
          }

          switch (shape) {
            case 'radio':
            case 'checkbox':
              if (shouldAttachRole(shape, elem)) {
                elem.attr('role', shape);
              }
              if (shouldAttachAttr('aria-checked', 'ariaChecked', elem, false)) {
                scope.$watch(ngAriaWatchModelValue, shape === 'radio' ?
                    getRadioReaction : getCheckboxReaction);
              }
              if (needsTabIndex) {
                elem.attr('tabindex', 0);
              }
              break;
            case 'range':
              if (shouldAttachRole(shape, elem)) {
                elem.attr('role', 'slider');
              }
              if ($aria.config('ariaValue')) {
                var needsAriaValuemin = !elem.attr('aria-valuemin') &&
                    (attr.hasOwnProperty('min') || attr.hasOwnProperty('ngMin'));
                var needsAriaValuemax = !elem.attr('aria-valuemax') &&
                    (attr.hasOwnProperty('max') || attr.hasOwnProperty('ngMax'));
                var needsAriaValuenow = !elem.attr('aria-valuenow');

                if (needsAriaValuemin) {
                  attr.$observe('min', function ngAriaValueMinReaction(newVal) {
                    elem.attr('aria-valuemin', newVal);
                  });
                }
                if (needsAriaValuemax) {
                  attr.$observe('max', function ngAriaValueMinReaction(newVal) {
                    elem.attr('aria-valuemax', newVal);
                  });
                }
                if (needsAriaValuenow) {
                  scope.$watch(ngAriaWatchModelValue, function ngAriaValueNowReaction(newVal) {
                    elem.attr('aria-valuenow', newVal);
                  });
                }
              }
              if (needsTabIndex) {
                elem.attr('tabindex', 0);
              }
              break;
          }

          if (!attr.hasOwnProperty('ngRequired') && ngModel.$validators.required
            && shouldAttachAttr('aria-required', 'ariaRequired', elem, false)) {
            // ngModel.$error.required is undefined on custom controls
            attr.$observe('required', function() {
              elem.attr('aria-required', !!attr['required']);
            });
          }

          if (shouldAttachAttr('aria-invalid', 'ariaInvalid', elem, true)) {
            scope.$watch(function ngAriaInvalidWatch() {
              return ngModel.$invalid;
            }, function ngAriaInvalidReaction(newVal) {
              elem.attr('aria-invalid', !!newVal);
            });
          }
        }
      };
    }
  };
}])
.directive('ngDisabled', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngDisabled', 'aria-disabled', nativeAriaNodeNames, false);
}])
.directive('ngMessages', function() {
  return {
    restrict: 'A',
    require: '?ngMessages',
    link: function(scope, elem, attr, ngMessages) {
      if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

      if (!elem.attr('aria-live')) {
        elem.attr('aria-live', 'assertive');
      }
    }
  };
})
.directive('ngClick',['$aria', '$parse', function($aria, $parse) {
  return {
    restrict: 'A',
    compile: function(elem, attr) {
      if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

      var fn = $parse(attr.ngClick);
      return function(scope, elem, attr) {

        if (!isNodeOneOf(elem, nativeAriaNodeNames)) {

          if ($aria.config('bindRoleForClick') && !elem.attr('role')) {
            elem.attr('role', 'button');
          }

          if ($aria.config('tabindex') && !elem.attr('tabindex')) {
            elem.attr('tabindex', 0);
          }

          if ($aria.config('bindKeydown') && !attr.ngKeydown && !attr.ngKeypress && !attr.ngKeyup) {
            elem.on('keydown', function(event) {
              var keyCode = event.which || event.keyCode;

              if (keyCode === 13 || keyCode === 32) {
                // If the event is triggered on a non-interactive element ...
                if (nativeAriaNodeNames.indexOf(event.target.nodeName) === -1 && !event.target.isContentEditable) {
                  // ... prevent the default browser behavior (e.g. scrolling when pressing spacebar)
                  // See https://github.com/angular/angular.js/issues/16664
                  event.preventDefault();
                }
                scope.$apply(callback);
              }

              function callback() {
                fn(scope, { $event: event });
              }
            });
          }
        }
      };
    }
  };
}])
.directive('ngDblclick', ['$aria', function($aria) {
  return function(scope, elem, attr) {
    if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

    if ($aria.config('tabindex') && !elem.attr('tabindex') && !isNodeOneOf(elem, nativeAriaNodeNames)) {
      elem.attr('tabindex', 0);
    }
  };
}]);


})(window, window.angular);

})();
(function () {
/*!
 * Chart.js
 * http://chartjs.org/
 * Version: 2.3.0
 *
 * Copyright 2016 Nick Downie
 * Released under the MIT license
 * https://github.com/chartjs/Chart.js/blob/master/LICENSE.md
 */
!function(t){if("object"==typeof exports&&"undefined"!=typeof module)module.exports=t();else if("function"==typeof define&&define.amd)define([],t);else{var e;e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:this,e.Chart=t()}}(function(){return function t(e,a,i){function n(r,l){if(!a[r]){if(!e[r]){var s="function"==typeof require&&require;if(!l&&s)return s(r,!0);if(o)return o(r,!0);var d=new Error("Cannot find module '"+r+"'");throw d.code="MODULE_NOT_FOUND",d}var u=a[r]={exports:{}};e[r][0].call(u.exports,function(t){var a=e[r][1][t];return n(a?a:t)},u,u.exports,t,e,a,i)}return a[r].exports}for(var o="function"==typeof require&&require,r=0;r<i.length;r++)n(i[r]);return n}({1:[function(t,e,a){},{}],2:[function(t,e,a){function i(t){if(t){var e=/^#([a-fA-F0-9]{3})$/,a=/^#([a-fA-F0-9]{6})$/,i=/^rgba?\(\s*([+-]?\d+)\s*,\s*([+-]?\d+)\s*,\s*([+-]?\d+)\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)$/,n=/^rgba?\(\s*([+-]?[\d\.]+)\%\s*,\s*([+-]?[\d\.]+)\%\s*,\s*([+-]?[\d\.]+)\%\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)$/,o=/(\w+)/,r=[0,0,0],l=1,s=t.match(e);if(s){s=s[1];for(var d=0;d<r.length;d++)r[d]=parseInt(s[d]+s[d],16)}else if(s=t.match(a)){s=s[1];for(var d=0;d<r.length;d++)r[d]=parseInt(s.slice(2*d,2*d+2),16)}else if(s=t.match(i)){for(var d=0;d<r.length;d++)r[d]=parseInt(s[d+1]);l=parseFloat(s[4])}else if(s=t.match(n)){for(var d=0;d<r.length;d++)r[d]=Math.round(2.55*parseFloat(s[d+1]));l=parseFloat(s[4])}else if(s=t.match(o)){if("transparent"==s[1])return[0,0,0,0];if(r=y[s[1]],!r)return}for(var d=0;d<r.length;d++)r[d]=v(r[d],0,255);return l=l||0==l?v(l,0,1):1,r[3]=l,r}}function n(t){if(t){var e=/^hsla?\(\s*([+-]?\d+)(?:deg)?\s*,\s*([+-]?[\d\.]+)%\s*,\s*([+-]?[\d\.]+)%\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)/,a=t.match(e);if(a){var i=parseFloat(a[4]),n=v(parseInt(a[1]),0,360),o=v(parseFloat(a[2]),0,100),r=v(parseFloat(a[3]),0,100),l=v(isNaN(i)?1:i,0,1);return[n,o,r,l]}}}function o(t){if(t){var e=/^hwb\(\s*([+-]?\d+)(?:deg)?\s*,\s*([+-]?[\d\.]+)%\s*,\s*([+-]?[\d\.]+)%\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)/,a=t.match(e);if(a){var i=parseFloat(a[4]),n=v(parseInt(a[1]),0,360),o=v(parseFloat(a[2]),0,100),r=v(parseFloat(a[3]),0,100),l=v(isNaN(i)?1:i,0,1);return[n,o,r,l]}}}function r(t){var e=i(t);return e&&e.slice(0,3)}function l(t){var e=n(t);return e&&e.slice(0,3)}function s(t){var e=i(t);return e?e[3]:(e=n(t))?e[3]:(e=o(t))?e[3]:void 0}function d(t){return"#"+x(t[0])+x(t[1])+x(t[2])}function u(t,e){return 1>e||t[3]&&t[3]<1?c(t,e):"rgb("+t[0]+", "+t[1]+", "+t[2]+")"}function c(t,e){return void 0===e&&(e=void 0!==t[3]?t[3]:1),"rgba("+t[0]+", "+t[1]+", "+t[2]+", "+e+")"}function h(t,e){if(1>e||t[3]&&t[3]<1)return f(t,e);var a=Math.round(t[0]/255*100),i=Math.round(t[1]/255*100),n=Math.round(t[2]/255*100);return"rgb("+a+"%, "+i+"%, "+n+"%)"}function f(t,e){var a=Math.round(t[0]/255*100),i=Math.round(t[1]/255*100),n=Math.round(t[2]/255*100);return"rgba("+a+"%, "+i+"%, "+n+"%, "+(e||t[3]||1)+")"}function g(t,e){return 1>e||t[3]&&t[3]<1?p(t,e):"hsl("+t[0]+", "+t[1]+"%, "+t[2]+"%)"}function p(t,e){return void 0===e&&(e=void 0!==t[3]?t[3]:1),"hsla("+t[0]+", "+t[1]+"%, "+t[2]+"%, "+e+")"}function m(t,e){return void 0===e&&(e=void 0!==t[3]?t[3]:1),"hwb("+t[0]+", "+t[1]+"%, "+t[2]+"%"+(void 0!==e&&1!==e?", "+e:"")+")"}function b(t){return k[t.slice(0,3)]}function v(t,e,a){return Math.min(Math.max(e,t),a)}function x(t){var e=t.toString(16).toUpperCase();return e.length<2?"0"+e:e}var y=t(6);e.exports={getRgba:i,getHsla:n,getRgb:r,getHsl:l,getHwb:o,getAlpha:s,hexString:d,rgbString:u,rgbaString:c,percentString:h,percentaString:f,hslString:g,hslaString:p,hwbString:m,keyword:b};var k={};for(var S in y)k[y[S]]=S},{6:6}],3:[function(t,e,a){var i=t(5),n=t(2),o=function(t){if(t instanceof o)return t;if(!(this instanceof o))return new o(t);this.values={rgb:[0,0,0],hsl:[0,0,0],hsv:[0,0,0],hwb:[0,0,0],cmyk:[0,0,0,0],alpha:1};var e;if("string"==typeof t)if(e=n.getRgba(t))this.setValues("rgb",e);else if(e=n.getHsla(t))this.setValues("hsl",e);else{if(!(e=n.getHwb(t)))throw new Error('Unable to parse color from string "'+t+'"');this.setValues("hwb",e)}else if("object"==typeof t)if(e=t,void 0!==e.r||void 0!==e.red)this.setValues("rgb",e);else if(void 0!==e.l||void 0!==e.lightness)this.setValues("hsl",e);else if(void 0!==e.v||void 0!==e.value)this.setValues("hsv",e);else if(void 0!==e.w||void 0!==e.whiteness)this.setValues("hwb",e);else{if(void 0===e.c&&void 0===e.cyan)throw new Error("Unable to parse color from object "+JSON.stringify(t));this.setValues("cmyk",e)}};o.prototype={rgb:function(){return this.setSpace("rgb",arguments)},hsl:function(){return this.setSpace("hsl",arguments)},hsv:function(){return this.setSpace("hsv",arguments)},hwb:function(){return this.setSpace("hwb",arguments)},cmyk:function(){return this.setSpace("cmyk",arguments)},rgbArray:function(){return this.values.rgb},hslArray:function(){return this.values.hsl},hsvArray:function(){return this.values.hsv},hwbArray:function(){var t=this.values;return 1!==t.alpha?t.hwb.concat([t.alpha]):t.hwb},cmykArray:function(){return this.values.cmyk},rgbaArray:function(){var t=this.values;return t.rgb.concat([t.alpha])},hslaArray:function(){var t=this.values;return t.hsl.concat([t.alpha])},alpha:function(t){return void 0===t?this.values.alpha:(this.setValues("alpha",t),this)},red:function(t){return this.setChannel("rgb",0,t)},green:function(t){return this.setChannel("rgb",1,t)},blue:function(t){return this.setChannel("rgb",2,t)},hue:function(t){return t&&(t%=360,t=0>t?360+t:t),this.setChannel("hsl",0,t)},saturation:function(t){return this.setChannel("hsl",1,t)},lightness:function(t){return this.setChannel("hsl",2,t)},saturationv:function(t){return this.setChannel("hsv",1,t)},whiteness:function(t){return this.setChannel("hwb",1,t)},blackness:function(t){return this.setChannel("hwb",2,t)},value:function(t){return this.setChannel("hsv",2,t)},cyan:function(t){return this.setChannel("cmyk",0,t)},magenta:function(t){return this.setChannel("cmyk",1,t)},yellow:function(t){return this.setChannel("cmyk",2,t)},black:function(t){return this.setChannel("cmyk",3,t)},hexString:function(){return n.hexString(this.values.rgb)},rgbString:function(){return n.rgbString(this.values.rgb,this.values.alpha)},rgbaString:function(){return n.rgbaString(this.values.rgb,this.values.alpha)},percentString:function(){return n.percentString(this.values.rgb,this.values.alpha)},hslString:function(){return n.hslString(this.values.hsl,this.values.alpha)},hslaString:function(){return n.hslaString(this.values.hsl,this.values.alpha)},hwbString:function(){return n.hwbString(this.values.hwb,this.values.alpha)},keyword:function(){return n.keyword(this.values.rgb,this.values.alpha)},rgbNumber:function(){var t=this.values.rgb;return t[0]<<16|t[1]<<8|t[2]},luminosity:function(){for(var t=this.values.rgb,e=[],a=0;a<t.length;a++){var i=t[a]/255;e[a]=.03928>=i?i/12.92:Math.pow((i+.055)/1.055,2.4)}return.2126*e[0]+.7152*e[1]+.0722*e[2]},contrast:function(t){var e=this.luminosity(),a=t.luminosity();return e>a?(e+.05)/(a+.05):(a+.05)/(e+.05)},level:function(t){var e=this.contrast(t);return e>=7.1?"AAA":e>=4.5?"AA":""},dark:function(){var t=this.values.rgb,e=(299*t[0]+587*t[1]+114*t[2])/1e3;return 128>e},light:function(){return!this.dark()},negate:function(){for(var t=[],e=0;3>e;e++)t[e]=255-this.values.rgb[e];return this.setValues("rgb",t),this},lighten:function(t){var e=this.values.hsl;return e[2]+=e[2]*t,this.setValues("hsl",e),this},darken:function(t){var e=this.values.hsl;return e[2]-=e[2]*t,this.setValues("hsl",e),this},saturate:function(t){var e=this.values.hsl;return e[1]+=e[1]*t,this.setValues("hsl",e),this},desaturate:function(t){var e=this.values.hsl;return e[1]-=e[1]*t,this.setValues("hsl",e),this},whiten:function(t){var e=this.values.hwb;return e[1]+=e[1]*t,this.setValues("hwb",e),this},blacken:function(t){var e=this.values.hwb;return e[2]+=e[2]*t,this.setValues("hwb",e),this},greyscale:function(){var t=this.values.rgb,e=.3*t[0]+.59*t[1]+.11*t[2];return this.setValues("rgb",[e,e,e]),this},clearer:function(t){var e=this.values.alpha;return this.setValues("alpha",e-e*t),this},opaquer:function(t){var e=this.values.alpha;return this.setValues("alpha",e+e*t),this},rotate:function(t){var e=this.values.hsl,a=(e[0]+t)%360;return e[0]=0>a?360+a:a,this.setValues("hsl",e),this},mix:function(t,e){var a=this,i=t,n=void 0===e?.5:e,o=2*n-1,r=a.alpha()-i.alpha(),l=((o*r===-1?o:(o+r)/(1+o*r))+1)/2,s=1-l;return this.rgb(l*a.red()+s*i.red(),l*a.green()+s*i.green(),l*a.blue()+s*i.blue()).alpha(a.alpha()*n+i.alpha()*(1-n))},toJSON:function(){return this.rgb()},clone:function(){var t,e,a=new o,i=this.values,n=a.values;for(var r in i)i.hasOwnProperty(r)&&(t=i[r],e={}.toString.call(t),"[object Array]"===e?n[r]=t.slice(0):"[object Number]"===e?n[r]=t:console.error("unexpected color value:",t));return a}},o.prototype.spaces={rgb:["red","green","blue"],hsl:["hue","saturation","lightness"],hsv:["hue","saturation","value"],hwb:["hue","whiteness","blackness"],cmyk:["cyan","magenta","yellow","black"]},o.prototype.maxes={rgb:[255,255,255],hsl:[360,100,100],hsv:[360,100,100],hwb:[360,100,100],cmyk:[100,100,100,100]},o.prototype.getValues=function(t){for(var e=this.values,a={},i=0;i<t.length;i++)a[t.charAt(i)]=e[t][i];return 1!==e.alpha&&(a.a=e.alpha),a},o.prototype.setValues=function(t,e){var a,n=this.values,o=this.spaces,r=this.maxes,l=1;if("alpha"===t)l=e;else if(e.length)n[t]=e.slice(0,t.length),l=e[t.length];else if(void 0!==e[t.charAt(0)]){for(a=0;a<t.length;a++)n[t][a]=e[t.charAt(a)];l=e.a}else if(void 0!==e[o[t][0]]){var s=o[t];for(a=0;a<t.length;a++)n[t][a]=e[s[a]];l=e.alpha}if(n.alpha=Math.max(0,Math.min(1,void 0===l?n.alpha:l)),"alpha"===t)return!1;var d;for(a=0;a<t.length;a++)d=Math.max(0,Math.min(r[t][a],n[t][a])),n[t][a]=Math.round(d);for(var u in o)u!==t&&(n[u]=i[t][u](n[t]));return!0},o.prototype.setSpace=function(t,e){var a=e[0];return void 0===a?this.getValues(t):("number"==typeof a&&(a=Array.prototype.slice.call(e)),this.setValues(t,a),this)},o.prototype.setChannel=function(t,e,a){var i=this.values[t];return void 0===a?i[e]:a===i[e]?this:(i[e]=a,this.setValues(t,i),this)},"undefined"!=typeof window&&(window.Color=o),e.exports=o},{2:2,5:5}],4:[function(t,e,a){function i(t){var e,a,i,n=t[0]/255,o=t[1]/255,r=t[2]/255,l=Math.min(n,o,r),s=Math.max(n,o,r),d=s-l;return s==l?e=0:n==s?e=(o-r)/d:o==s?e=2+(r-n)/d:r==s&&(e=4+(n-o)/d),e=Math.min(60*e,360),0>e&&(e+=360),i=(l+s)/2,a=s==l?0:.5>=i?d/(s+l):d/(2-s-l),[e,100*a,100*i]}function n(t){var e,a,i,n=t[0],o=t[1],r=t[2],l=Math.min(n,o,r),s=Math.max(n,o,r),d=s-l;return a=0==s?0:d/s*1e3/10,s==l?e=0:n==s?e=(o-r)/d:o==s?e=2+(r-n)/d:r==s&&(e=4+(n-o)/d),e=Math.min(60*e,360),0>e&&(e+=360),i=s/255*1e3/10,[e,a,i]}function o(t){var e=t[0],a=t[1],n=t[2],o=i(t)[0],r=1/255*Math.min(e,Math.min(a,n)),n=1-1/255*Math.max(e,Math.max(a,n));return[o,100*r,100*n]}function l(t){var e,a,i,n,o=t[0]/255,r=t[1]/255,l=t[2]/255;return n=Math.min(1-o,1-r,1-l),e=(1-o-n)/(1-n)||0,a=(1-r-n)/(1-n)||0,i=(1-l-n)/(1-n)||0,[100*e,100*a,100*i,100*n]}function s(t){return G[JSON.stringify(t)]}function d(t){var e=t[0]/255,a=t[1]/255,i=t[2]/255;e=e>.04045?Math.pow((e+.055)/1.055,2.4):e/12.92,a=a>.04045?Math.pow((a+.055)/1.055,2.4):a/12.92,i=i>.04045?Math.pow((i+.055)/1.055,2.4):i/12.92;var n=.4124*e+.3576*a+.1805*i,o=.2126*e+.7152*a+.0722*i,r=.0193*e+.1192*a+.9505*i;return[100*n,100*o,100*r]}function u(t){var e,a,i,n=d(t),o=n[0],r=n[1],l=n[2];return o/=95.047,r/=100,l/=108.883,o=o>.008856?Math.pow(o,1/3):7.787*o+16/116,r=r>.008856?Math.pow(r,1/3):7.787*r+16/116,l=l>.008856?Math.pow(l,1/3):7.787*l+16/116,e=116*r-16,a=500*(o-r),i=200*(r-l),[e,a,i]}function c(t){return W(u(t))}function h(t){var e,a,i,n,o,r=t[0]/360,l=t[1]/100,s=t[2]/100;if(0==l)return o=255*s,[o,o,o];a=.5>s?s*(1+l):s+l-s*l,e=2*s-a,n=[0,0,0];for(var d=0;3>d;d++)i=r+1/3*-(d-1),0>i&&i++,i>1&&i--,o=1>6*i?e+6*(a-e)*i:1>2*i?a:2>3*i?e+(a-e)*(2/3-i)*6:e,n[d]=255*o;return n}function f(t){var e,a,i=t[0],n=t[1]/100,o=t[2]/100;return 0===o?[0,0,0]:(o*=2,n*=1>=o?o:2-o,a=(o+n)/2,e=2*n/(o+n),[i,100*e,100*a])}function p(t){return o(h(t))}function m(t){return l(h(t))}function v(t){return s(h(t))}function x(t){var e=t[0]/60,a=t[1]/100,i=t[2]/100,n=Math.floor(e)%6,o=e-Math.floor(e),r=255*i*(1-a),l=255*i*(1-a*o),s=255*i*(1-a*(1-o)),i=255*i;switch(n){case 0:return[i,s,r];case 1:return[l,i,r];case 2:return[r,i,s];case 3:return[r,l,i];case 4:return[s,r,i];case 5:return[i,r,l]}}function y(t){var e,a,i=t[0],n=t[1]/100,o=t[2]/100;return a=(2-n)*o,e=n*o,e/=1>=a?a:2-a,e=e||0,a/=2,[i,100*e,100*a]}function k(t){return o(x(t))}function S(t){return l(x(t))}function w(t){return s(x(t))}function C(t){var e,a,i,n,o=t[0]/360,l=t[1]/100,s=t[2]/100,d=l+s;switch(d>1&&(l/=d,s/=d),e=Math.floor(6*o),a=1-s,i=6*o-e,0!=(1&e)&&(i=1-i),n=l+i*(a-l),e){default:case 6:case 0:r=a,g=n,b=l;break;case 1:r=n,g=a,b=l;break;case 2:r=l,g=a,b=n;break;case 3:r=l,g=n,b=a;break;case 4:r=n,g=l,b=a;break;case 5:r=a,g=l,b=n}return[255*r,255*g,255*b]}function M(t){return i(C(t))}function D(t){return n(C(t))}function I(t){return l(C(t))}function A(t){return s(C(t))}function T(t){var e,a,i,n=t[0]/100,o=t[1]/100,r=t[2]/100,l=t[3]/100;return e=1-Math.min(1,n*(1-l)+l),a=1-Math.min(1,o*(1-l)+l),i=1-Math.min(1,r*(1-l)+l),[255*e,255*a,255*i]}function P(t){return i(T(t))}function F(t){return n(T(t))}function R(t){return o(T(t))}function _(t){return s(T(t))}function V(t){var e,a,i,n=t[0]/100,o=t[1]/100,r=t[2]/100;return e=3.2406*n+-1.5372*o+r*-.4986,a=n*-.9689+1.8758*o+.0415*r,i=.0557*n+o*-.204+1.057*r,e=e>.0031308?1.055*Math.pow(e,1/2.4)-.055:e=12.92*e,a=a>.0031308?1.055*Math.pow(a,1/2.4)-.055:a=12.92*a,i=i>.0031308?1.055*Math.pow(i,1/2.4)-.055:i=12.92*i,e=Math.min(Math.max(0,e),1),a=Math.min(Math.max(0,a),1),i=Math.min(Math.max(0,i),1),[255*e,255*a,255*i]}function L(t){var e,a,i,n=t[0],o=t[1],r=t[2];return n/=95.047,o/=100,r/=108.883,n=n>.008856?Math.pow(n,1/3):7.787*n+16/116,o=o>.008856?Math.pow(o,1/3):7.787*o+16/116,r=r>.008856?Math.pow(r,1/3):7.787*r+16/116,e=116*o-16,a=500*(n-o),i=200*(o-r),[e,a,i]}function O(t){return W(L(t))}function B(t){var e,a,i,n,o=t[0],r=t[1],l=t[2];return 8>=o?(a=100*o/903.3,n=7.787*(a/100)+16/116):(a=100*Math.pow((o+16)/116,3),n=Math.pow(a/100,1/3)),e=.008856>=e/95.047?e=95.047*(r/500+n-16/116)/7.787:95.047*Math.pow(r/500+n,3),i=.008859>=i/108.883?i=108.883*(n-l/200-16/116)/7.787:108.883*Math.pow(n-l/200,3),[e,a,i]}function W(t){var e,a,i,n=t[0],o=t[1],r=t[2];return e=Math.atan2(r,o),a=360*e/2/Math.PI,0>a&&(a+=360),i=Math.sqrt(o*o+r*r),[n,i,a]}function z(t){return V(B(t))}function N(t){var e,a,i,n=t[0],o=t[1],r=t[2];return i=r/360*2*Math.PI,e=o*Math.cos(i),a=o*Math.sin(i),[n,e,a]}function H(t){return B(N(t))}function E(t){return z(N(t))}function U(t){return Z[t]}function q(t){return i(U(t))}function j(t){return n(U(t))}function Y(t){return o(U(t))}function K(t){return l(U(t))}function X(t){return u(U(t))}function J(t){return d(U(t))}e.exports={rgb2hsl:i,rgb2hsv:n,rgb2hwb:o,rgb2cmyk:l,rgb2keyword:s,rgb2xyz:d,rgb2lab:u,rgb2lch:c,hsl2rgb:h,hsl2hsv:f,hsl2hwb:p,hsl2cmyk:m,hsl2keyword:v,hsv2rgb:x,hsv2hsl:y,hsv2hwb:k,hsv2cmyk:S,hsv2keyword:w,hwb2rgb:C,hwb2hsl:M,hwb2hsv:D,hwb2cmyk:I,hwb2keyword:A,cmyk2rgb:T,cmyk2hsl:P,cmyk2hsv:F,cmyk2hwb:R,cmyk2keyword:_,keyword2rgb:U,keyword2hsl:q,keyword2hsv:j,keyword2hwb:Y,keyword2cmyk:K,keyword2lab:X,keyword2xyz:J,xyz2rgb:V,xyz2lab:L,xyz2lch:O,lab2xyz:B,lab2rgb:z,lab2lch:W,lch2lab:N,lch2xyz:H,lch2rgb:E};var Z={aliceblue:[240,248,255],antiquewhite:[250,235,215],aqua:[0,255,255],aquamarine:[127,255,212],azure:[240,255,255],beige:[245,245,220],bisque:[255,228,196],black:[0,0,0],blanchedalmond:[255,235,205],blue:[0,0,255],blueviolet:[138,43,226],brown:[165,42,42],burlywood:[222,184,135],cadetblue:[95,158,160],chartreuse:[127,255,0],chocolate:[210,105,30],coral:[255,127,80],cornflowerblue:[100,149,237],cornsilk:[255,248,220],crimson:[220,20,60],cyan:[0,255,255],darkblue:[0,0,139],darkcyan:[0,139,139],darkgoldenrod:[184,134,11],darkgray:[169,169,169],darkgreen:[0,100,0],darkgrey:[169,169,169],darkkhaki:[189,183,107],darkmagenta:[139,0,139],darkolivegreen:[85,107,47],darkorange:[255,140,0],darkorchid:[153,50,204],darkred:[139,0,0],darksalmon:[233,150,122],darkseagreen:[143,188,143],darkslateblue:[72,61,139],darkslategray:[47,79,79],darkslategrey:[47,79,79],darkturquoise:[0,206,209],darkviolet:[148,0,211],deeppink:[255,20,147],deepskyblue:[0,191,255],dimgray:[105,105,105],dimgrey:[105,105,105],dodgerblue:[30,144,255],firebrick:[178,34,34],floralwhite:[255,250,240],forestgreen:[34,139,34],fuchsia:[255,0,255],gainsboro:[220,220,220],ghostwhite:[248,248,255],gold:[255,215,0],goldenrod:[218,165,32],gray:[128,128,128],green:[0,128,0],greenyellow:[173,255,47],grey:[128,128,128],honeydew:[240,255,240],hotpink:[255,105,180],indianred:[205,92,92],indigo:[75,0,130],ivory:[255,255,240],khaki:[240,230,140],lavender:[230,230,250],lavenderblush:[255,240,245],lawngreen:[124,252,0],lemonchiffon:[255,250,205],lightblue:[173,216,230],lightcoral:[240,128,128],lightcyan:[224,255,255],lightgoldenrodyellow:[250,250,210],lightgray:[211,211,211],lightgreen:[144,238,144],lightgrey:[211,211,211],lightpink:[255,182,193],lightsalmon:[255,160,122],lightseagreen:[32,178,170],lightskyblue:[135,206,250],lightslategray:[119,136,153],lightslategrey:[119,136,153],lightsteelblue:[176,196,222],lightyellow:[255,255,224],lime:[0,255,0],limegreen:[50,205,50],linen:[250,240,230],magenta:[255,0,255],maroon:[128,0,0],mediumaquamarine:[102,205,170],mediumblue:[0,0,205],mediumorchid:[186,85,211],mediumpurple:[147,112,219],mediumseagreen:[60,179,113],mediumslateblue:[123,104,238],mediumspringgreen:[0,250,154],mediumturquoise:[72,209,204],mediumvioletred:[199,21,133],midnightblue:[25,25,112],mintcream:[245,255,250],mistyrose:[255,228,225],moccasin:[255,228,181],navajowhite:[255,222,173],navy:[0,0,128],oldlace:[253,245,230],olive:[128,128,0],olivedrab:[107,142,35],orange:[255,165,0],orangered:[255,69,0],orchid:[218,112,214],palegoldenrod:[238,232,170],palegreen:[152,251,152],paleturquoise:[175,238,238],palevioletred:[219,112,147],papayawhip:[255,239,213],peachpuff:[255,218,185],peru:[205,133,63],pink:[255,192,203],plum:[221,160,221],powderblue:[176,224,230],purple:[128,0,128],rebeccapurple:[102,51,153],red:[255,0,0],rosybrown:[188,143,143],royalblue:[65,105,225],saddlebrown:[139,69,19],salmon:[250,128,114],sandybrown:[244,164,96],seagreen:[46,139,87],seashell:[255,245,238],sienna:[160,82,45],silver:[192,192,192],skyblue:[135,206,235],slateblue:[106,90,205],slategray:[112,128,144],slategrey:[112,128,144],snow:[255,250,250],springgreen:[0,255,127],steelblue:[70,130,180],tan:[210,180,140],teal:[0,128,128],thistle:[216,191,216],tomato:[255,99,71],turquoise:[64,224,208],violet:[238,130,238],wheat:[245,222,179],white:[255,255,255],whitesmoke:[245,245,245],yellow:[255,255,0],yellowgreen:[154,205,50]},G={};for(var Q in Z)G[JSON.stringify(Z[Q])]=Q},{}],5:[function(t,e,a){var i=t(4),n=function(){return new d};for(var o in i){n[o+"Raw"]=function(t){return function(e){return"number"==typeof e&&(e=Array.prototype.slice.call(arguments)),i[t](e)}}(o);var r=/(\w+)2(\w+)/.exec(o),l=r[1],s=r[2];n[l]=n[l]||{},n[l][s]=n[o]=function(t){return function(e){"number"==typeof e&&(e=Array.prototype.slice.call(arguments));var a=i[t](e);if("string"==typeof a||void 0===a)return a;for(var n=0;n<a.length;n++)a[n]=Math.round(a[n]);return a}}(o)}var d=function(){this.convs={}};d.prototype.routeSpace=function(t,e){var a=e[0];return void 0===a?this.getValues(t):("number"==typeof a&&(a=Array.prototype.slice.call(e)),this.setValues(t,a))},d.prototype.setValues=function(t,e){return this.space=t,this.convs={},this.convs[t]=e,this},d.prototype.getValues=function(t){var e=this.convs[t];if(!e){var a=this.space,i=this.convs[a];e=n[a][t](i),this.convs[t]=e}return e},["rgb","hsl","hsv","cmyk","keyword"].forEach(function(t){d.prototype[t]=function(e){return this.routeSpace(t,arguments)}}),e.exports=n},{4:4}],6:[function(t,e,a){e.exports={aliceblue:[240,248,255],antiquewhite:[250,235,215],aqua:[0,255,255],aquamarine:[127,255,212],azure:[240,255,255],beige:[245,245,220],bisque:[255,228,196],black:[0,0,0],blanchedalmond:[255,235,205],blue:[0,0,255],blueviolet:[138,43,226],brown:[165,42,42],burlywood:[222,184,135],cadetblue:[95,158,160],chartreuse:[127,255,0],chocolate:[210,105,30],coral:[255,127,80],cornflowerblue:[100,149,237],cornsilk:[255,248,220],crimson:[220,20,60],cyan:[0,255,255],darkblue:[0,0,139],darkcyan:[0,139,139],darkgoldenrod:[184,134,11],darkgray:[169,169,169],darkgreen:[0,100,0],darkgrey:[169,169,169],darkkhaki:[189,183,107],darkmagenta:[139,0,139],darkolivegreen:[85,107,47],darkorange:[255,140,0],darkorchid:[153,50,204],darkred:[139,0,0],darksalmon:[233,150,122],darkseagreen:[143,188,143],darkslateblue:[72,61,139],darkslategray:[47,79,79],darkslategrey:[47,79,79],darkturquoise:[0,206,209],darkviolet:[148,0,211],deeppink:[255,20,147],deepskyblue:[0,191,255],dimgray:[105,105,105],dimgrey:[105,105,105],dodgerblue:[30,144,255],firebrick:[178,34,34],floralwhite:[255,250,240],forestgreen:[34,139,34],fuchsia:[255,0,255],gainsboro:[220,220,220],ghostwhite:[248,248,255],gold:[255,215,0],goldenrod:[218,165,32],gray:[128,128,128],green:[0,128,0],greenyellow:[173,255,47],grey:[128,128,128],honeydew:[240,255,240],hotpink:[255,105,180],indianred:[205,92,92],indigo:[75,0,130],ivory:[255,255,240],khaki:[240,230,140],lavender:[230,230,250],lavenderblush:[255,240,245],lawngreen:[124,252,0],lemonchiffon:[255,250,205],lightblue:[173,216,230],lightcoral:[240,128,128],lightcyan:[224,255,255],lightgoldenrodyellow:[250,250,210],lightgray:[211,211,211],lightgreen:[144,238,144],lightgrey:[211,211,211],lightpink:[255,182,193],lightsalmon:[255,160,122],lightseagreen:[32,178,170],lightskyblue:[135,206,250],lightslategray:[119,136,153],lightslategrey:[119,136,153],lightsteelblue:[176,196,222],lightyellow:[255,255,224],lime:[0,255,0],limegreen:[50,205,50],linen:[250,240,230],magenta:[255,0,255],maroon:[128,0,0],mediumaquamarine:[102,205,170],mediumblue:[0,0,205],mediumorchid:[186,85,211],mediumpurple:[147,112,219],mediumseagreen:[60,179,113],mediumslateblue:[123,104,238],mediumspringgreen:[0,250,154],mediumturquoise:[72,209,204],mediumvioletred:[199,21,133],midnightblue:[25,25,112],mintcream:[245,255,250],mistyrose:[255,228,225],moccasin:[255,228,181],navajowhite:[255,222,173],navy:[0,0,128],oldlace:[253,245,230],olive:[128,128,0],olivedrab:[107,142,35],orange:[255,165,0],orangered:[255,69,0],orchid:[218,112,214],palegoldenrod:[238,232,170],palegreen:[152,251,152],paleturquoise:[175,238,238],palevioletred:[219,112,147],papayawhip:[255,239,213],peachpuff:[255,218,185],peru:[205,133,63],pink:[255,192,203],plum:[221,160,221],powderblue:[176,224,230],purple:[128,0,128],rebeccapurple:[102,51,153],red:[255,0,0],rosybrown:[188,143,143],royalblue:[65,105,225],saddlebrown:[139,69,19],salmon:[250,128,114],sandybrown:[244,164,96],seagreen:[46,139,87],seashell:[255,245,238],sienna:[160,82,45],silver:[192,192,192],skyblue:[135,206,235],slateblue:[106,90,205],slategray:[112,128,144],slategrey:[112,128,144],snow:[255,250,250],springgreen:[0,255,127],steelblue:[70,130,180],tan:[210,180,140],teal:[0,128,128],thistle:[216,191,216],tomato:[255,99,71],turquoise:[64,224,208],violet:[238,130,238],wheat:[245,222,179],white:[255,255,255],whitesmoke:[245,245,245],yellow:[255,255,0],yellowgreen:[154,205,50]}},{}],7:[function(t,e,a){var i=t(27)();t(26)(i),t(22)(i),t(25)(i),t(21)(i),t(23)(i),t(24)(i),t(28)(i),t(32)(i),t(30)(i),t(31)(i),t(33)(i),t(29)(i),t(34)(i),t(35)(i),t(36)(i),t(37)(i),t(38)(i),t(41)(i),t(39)(i),t(40)(i),t(42)(i),t(43)(i),t(44)(i),t(15)(i),t(16)(i),t(17)(i),t(18)(i),t(19)(i),t(20)(i),t(8)(i),t(9)(i),t(10)(i),t(11)(i),t(12)(i),t(13)(i),t(14)(i),window.Chart=e.exports=i},{10:10,11:11,12:12,13:13,14:14,15:15,16:16,17:17,18:18,19:19,20:20,21:21,22:22,23:23,24:24,25:25,26:26,27:27,28:28,29:29,30:30,31:31,32:32,33:33,34:34,35:35,36:36,37:37,38:38,39:39,40:40,41:41,42:42,43:43,44:44,8:8,9:9}],8:[function(t,e,a){"use strict";e.exports=function(t){t.Bar=function(e,a){return a.type="bar",new t(e,a)}}},{}],9:[function(t,e,a){"use strict";e.exports=function(t){t.Bubble=function(e,a){return a.type="bubble",new t(e,a)}}},{}],10:[function(t,e,a){"use strict";e.exports=function(t){t.Doughnut=function(e,a){return a.type="doughnut",new t(e,a)}}},{}],11:[function(t,e,a){"use strict";e.exports=function(t){t.Line=function(e,a){return a.type="line",new t(e,a)}}},{}],12:[function(t,e,a){"use strict";e.exports=function(t){t.PolarArea=function(e,a){return a.type="polarArea",new t(e,a)}}},{}],13:[function(t,e,a){"use strict";e.exports=function(t){t.Radar=function(e,a){return a.options=t.helpers.configMerge({aspectRatio:1},a.options),a.type="radar",new t(e,a)}}},{}],14:[function(t,e,a){"use strict";e.exports=function(t){var e={hover:{mode:"single"},scales:{xAxes:[{type:"linear",position:"bottom",id:"x-axis-1"}],yAxes:[{type:"linear",position:"left",id:"y-axis-1"}]},tooltips:{callbacks:{title:function(){return""},label:function(t){return"("+t.xLabel+", "+t.yLabel+")"}}}};t.defaults.scatter=e,t.controllers.scatter=t.controllers.line,t.Scatter=function(e,a){return a.type="scatter",new t(e,a)}}},{}],15:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.defaults.bar={hover:{mode:"label"},scales:{xAxes:[{type:"category",categoryPercentage:.8,barPercentage:.9,gridLines:{offsetGridLines:!0}}],yAxes:[{type:"linear"}]}},t.controllers.bar=t.DatasetController.extend({dataElementType:t.elements.Rectangle,initialize:function(e,a){t.DatasetController.prototype.initialize.call(this,e,a),this.getMeta().bar=!0},getBarCount:function(){var t=this,a=0;return e.each(t.chart.data.datasets,function(e,i){var n=t.chart.getDatasetMeta(i);n.bar&&t.chart.isDatasetVisible(i)&&++a},t),a},update:function(t){var a=this;e.each(a.getMeta().data,function(e,i){a.updateElement(e,i,t)},a)},updateElement:function(t,a,i){var n=this,o=n.getMeta(),r=n.getScaleForId(o.xAxisID),l=n.getScaleForId(o.yAxisID),s=l.getBasePixel(),d=n.chart.options.elements.rectangle,u=t.custom||{},c=n.getDataset();e.extend(t,{_xScale:r,_yScale:l,_datasetIndex:n.index,_index:a,_model:{x:n.calculateBarX(a,n.index),y:i?s:n.calculateBarY(a,n.index),label:n.chart.data.labels[a],datasetLabel:c.label,base:i?s:n.calculateBarBase(n.index,a),width:n.calculateBarWidth(a),backgroundColor:u.backgroundColor?u.backgroundColor:e.getValueAtIndexOrDefault(c.backgroundColor,a,d.backgroundColor),borderSkipped:u.borderSkipped?u.borderSkipped:d.borderSkipped,borderColor:u.borderColor?u.borderColor:e.getValueAtIndexOrDefault(c.borderColor,a,d.borderColor),borderWidth:u.borderWidth?u.borderWidth:e.getValueAtIndexOrDefault(c.borderWidth,a,d.borderWidth)}}),t.pivot()},calculateBarBase:function(t,e){var a=this,i=a.getMeta(),n=a.getScaleForId(i.yAxisID),o=0;if(n.options.stacked){for(var r=a.chart,l=r.data.datasets,s=Number(l[t].data[e]),d=0;t>d;d++){var u=l[d],c=r.getDatasetMeta(d);if(c.bar&&c.yAxisID===n.id&&r.isDatasetVisible(d)){var h=Number(u.data[e]);o+=0>s?Math.min(h,0):Math.max(h,0)}}return n.getPixelForValue(o)}return n.getBasePixel()},getRuler:function(t){var e,a=this,i=a.getMeta(),n=a.getScaleForId(i.xAxisID),o=a.getBarCount();e="category"===n.options.type?n.getPixelForTick(t+1)-n.getPixelForTick(t):n.width/n.ticks.length;var r=e*n.options.categoryPercentage,l=(e-e*n.options.categoryPercentage)/2,s=r/o;if(n.ticks.length!==a.chart.data.labels.length){var d=n.ticks.length/a.chart.data.labels.length;s*=d}var u=s*n.options.barPercentage,c=s-s*n.options.barPercentage;return{datasetCount:o,tickWidth:e,categoryWidth:r,categorySpacing:l,fullBarWidth:s,barWidth:u,barSpacing:c}},calculateBarWidth:function(t){var e=this.getScaleForId(this.getMeta().xAxisID);if(e.options.barThickness)return e.options.barThickness;var a=this.getRuler(t);return e.options.stacked?a.categoryWidth:a.barWidth},getBarIndex:function(t){var e,a,i=0;for(a=0;t>a;++a)e=this.chart.getDatasetMeta(a),e.bar&&this.chart.isDatasetVisible(a)&&++i;return i},calculateBarX:function(t,e){var a=this,i=a.getMeta(),n=a.getScaleForId(i.xAxisID),o=a.getBarIndex(e),r=a.getRuler(t),l=n.getPixelForValue(null,t,e,a.chart.isCombo);return l-=a.chart.isCombo?r.tickWidth/2:0,n.options.stacked?l+r.categoryWidth/2+r.categorySpacing:l+r.barWidth/2+r.categorySpacing+r.barWidth*o+r.barSpacing/2+r.barSpacing*o},calculateBarY:function(t,e){var a=this,i=a.getMeta(),n=a.getScaleForId(i.yAxisID),o=Number(a.getDataset().data[t]);if(n.options.stacked){for(var r=0,l=0,s=0;e>s;s++){var d=a.chart.data.datasets[s],u=a.chart.getDatasetMeta(s);if(u.bar&&u.yAxisID===n.id&&a.chart.isDatasetVisible(s)){var c=Number(d.data[t]);0>c?l+=c||0:r+=c||0}}return 0>o?n.getPixelForValue(l+o):n.getPixelForValue(r+o)}return n.getPixelForValue(o)},draw:function(t){var a=this,i=t||1;e.each(a.getMeta().data,function(t,e){var n=a.getDataset().data[e];null===n||void 0===n||isNaN(n)||t.transition(i).draw()},a)},setHoverStyle:function(t){var a=this.chart.data.datasets[t._datasetIndex],i=t._index,n=t.custom||{},o=t._model;o.backgroundColor=n.hoverBackgroundColor?n.hoverBackgroundColor:e.getValueAtIndexOrDefault(a.hoverBackgroundColor,i,e.getHoverColor(o.backgroundColor)),o.borderColor=n.hoverBorderColor?n.hoverBorderColor:e.getValueAtIndexOrDefault(a.hoverBorderColor,i,e.getHoverColor(o.borderColor)),o.borderWidth=n.hoverBorderWidth?n.hoverBorderWidth:e.getValueAtIndexOrDefault(a.hoverBorderWidth,i,o.borderWidth)},removeHoverStyle:function(t){var a=this.chart.data.datasets[t._datasetIndex],i=t._index,n=t.custom||{},o=t._model,r=this.chart.options.elements.rectangle;o.backgroundColor=n.backgroundColor?n.backgroundColor:e.getValueAtIndexOrDefault(a.backgroundColor,i,r.backgroundColor),o.borderColor=n.borderColor?n.borderColor:e.getValueAtIndexOrDefault(a.borderColor,i,r.borderColor),o.borderWidth=n.borderWidth?n.borderWidth:e.getValueAtIndexOrDefault(a.borderWidth,i,r.borderWidth)}}),t.defaults.horizontalBar={hover:{mode:"label"},scales:{xAxes:[{type:"linear",position:"bottom"}],yAxes:[{position:"left",type:"category",categoryPercentage:.8,barPercentage:.9,gridLines:{offsetGridLines:!0}}]},elements:{rectangle:{borderSkipped:"left"}},tooltips:{callbacks:{title:function(t,e){var a="";return t.length>0&&(t[0].yLabel?a=t[0].yLabel:e.labels.length>0&&t[0].index<e.labels.length&&(a=e.labels[t[0].index])),a},label:function(t,e){var a=e.datasets[t.datasetIndex].label||"";return a+": "+t.xLabel}}}},t.controllers.horizontalBar=t.controllers.bar.extend({updateElement:function(t,a,i){var n=this,o=n.getMeta(),r=n.getScaleForId(o.xAxisID),l=n.getScaleForId(o.yAxisID),s=r.getBasePixel(),d=t.custom||{},u=n.getDataset(),c=n.chart.options.elements.rectangle;e.extend(t,{_xScale:r,_yScale:l,_datasetIndex:n.index,_index:a,_model:{x:i?s:n.calculateBarX(a,n.index),y:n.calculateBarY(a,n.index),label:n.chart.data.labels[a],datasetLabel:u.label,base:i?s:n.calculateBarBase(n.index,a),height:n.calculateBarHeight(a),backgroundColor:d.backgroundColor?d.backgroundColor:e.getValueAtIndexOrDefault(u.backgroundColor,a,c.backgroundColor),borderSkipped:d.borderSkipped?d.borderSkipped:c.borderSkipped,borderColor:d.borderColor?d.borderColor:e.getValueAtIndexOrDefault(u.borderColor,a,c.borderColor),borderWidth:d.borderWidth?d.borderWidth:e.getValueAtIndexOrDefault(u.borderWidth,a,c.borderWidth)},draw:function(){function t(t){return s[(u+t)%4]}var e=this._chart.ctx,a=this._view,i=a.height/2,n=a.y-i,o=a.y+i,r=a.base-(a.base-a.x),l=a.borderWidth/2;a.borderWidth&&(n+=l,o-=l,r+=l),e.beginPath(),e.fillStyle=a.backgroundColor,e.strokeStyle=a.borderColor,e.lineWidth=a.borderWidth;var s=[[a.base,o],[a.base,n],[r,n],[r,o]],d=["bottom","left","top","right"],u=d.indexOf(a.borderSkipped,0);-1===u&&(u=0),e.moveTo.apply(e,t(0));for(var c=1;4>c;c++)e.lineTo.apply(e,t(c));e.fill(),a.borderWidth&&e.stroke()},inRange:function(t,e){var a=this._view,i=!1;return a&&(i=a.x<a.base?e>=a.y-a.height/2&&e<=a.y+a.height/2&&t>=a.x&&t<=a.base:e>=a.y-a.height/2&&e<=a.y+a.height/2&&t>=a.base&&t<=a.x),i}}),t.pivot()},calculateBarBase:function(t,e){var a=this,i=a.getMeta(),n=a.getScaleForId(i.xAxisID),o=0;
if(n.options.stacked){for(var r=a.chart,l=r.data.datasets,s=Number(l[t].data[e]),d=0;t>d;d++){var u=l[d],c=r.getDatasetMeta(d);if(c.bar&&c.xAxisID===n.id&&r.isDatasetVisible(d)){var h=Number(u.data[e]);o+=0>s?Math.min(h,0):Math.max(h,0)}}return n.getPixelForValue(o)}return n.getBasePixel()},getRuler:function(t){var e,a=this,i=a.getMeta(),n=a.getScaleForId(i.yAxisID),o=a.getBarCount();e="category"===n.options.type?n.getPixelForTick(t+1)-n.getPixelForTick(t):n.width/n.ticks.length;var r=e*n.options.categoryPercentage,l=(e-e*n.options.categoryPercentage)/2,s=r/o;if(n.ticks.length!==a.chart.data.labels.length){var d=n.ticks.length/a.chart.data.labels.length;s*=d}var u=s*n.options.barPercentage,c=s-s*n.options.barPercentage;return{datasetCount:o,tickHeight:e,categoryHeight:r,categorySpacing:l,fullBarHeight:s,barHeight:u,barSpacing:c}},calculateBarHeight:function(t){var e=this,a=e.getScaleForId(e.getMeta().yAxisID);if(a.options.barThickness)return a.options.barThickness;var i=e.getRuler(t);return a.options.stacked?i.categoryHeight:i.barHeight},calculateBarX:function(t,e){var a=this,i=a.getMeta(),n=a.getScaleForId(i.xAxisID),o=Number(a.getDataset().data[t]);if(n.options.stacked){for(var r=0,l=0,s=0;e>s;s++){var d=a.chart.data.datasets[s],u=a.chart.getDatasetMeta(s);if(u.bar&&u.xAxisID===n.id&&a.chart.isDatasetVisible(s)){var c=Number(d.data[t]);0>c?l+=c||0:r+=c||0}}return 0>o?n.getPixelForValue(l+o):n.getPixelForValue(r+o)}return n.getPixelForValue(o)},calculateBarY:function(t,e){var a=this,i=a.getMeta(),n=a.getScaleForId(i.yAxisID),o=a.getBarIndex(e),r=a.getRuler(t),l=n.getPixelForValue(null,t,e,a.chart.isCombo);return l-=a.chart.isCombo?r.tickHeight/2:0,n.options.stacked?l+r.categoryHeight/2+r.categorySpacing:l+r.barHeight/2+r.categorySpacing+r.barHeight*o+r.barSpacing/2+r.barSpacing*o}})}},{}],16:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.defaults.bubble={hover:{mode:"single"},scales:{xAxes:[{type:"linear",position:"bottom",id:"x-axis-0"}],yAxes:[{type:"linear",position:"left",id:"y-axis-0"}]},tooltips:{callbacks:{title:function(){return""},label:function(t,e){var a=e.datasets[t.datasetIndex].label||"",i=e.datasets[t.datasetIndex].data[t.index];return a+": ("+i.x+", "+i.y+", "+i.r+")"}}}},t.controllers.bubble=t.DatasetController.extend({dataElementType:t.elements.Point,update:function(t){var a=this,i=a.getMeta(),n=i.data;e.each(n,function(e,i){a.updateElement(e,i,t)})},updateElement:function(a,i,n){var o=this,r=o.getMeta(),l=o.getScaleForId(r.xAxisID),s=o.getScaleForId(r.yAxisID),d=a.custom||{},u=o.getDataset(),c=u.data[i],h=o.chart.options.elements.point,f=o.index;e.extend(a,{_xScale:l,_yScale:s,_datasetIndex:f,_index:i,_model:{x:n?l.getPixelForDecimal(.5):l.getPixelForValue("object"==typeof c?c:NaN,i,f,o.chart.isCombo),y:n?s.getBasePixel():s.getPixelForValue(c,i,f),radius:n?0:d.radius?d.radius:o.getRadius(c),hitRadius:d.hitRadius?d.hitRadius:e.getValueAtIndexOrDefault(u.hitRadius,i,h.hitRadius)}}),t.DatasetController.prototype.removeHoverStyle.call(o,a,h);var g=a._model;g.skip=d.skip?d.skip:isNaN(g.x)||isNaN(g.y),a.pivot()},getRadius:function(t){return t.r||this.chart.options.elements.point.radius},setHoverStyle:function(a){var i=this;t.DatasetController.prototype.setHoverStyle.call(i,a);var n=i.chart.data.datasets[a._datasetIndex],o=a._index,r=a.custom||{},l=a._model;l.radius=r.hoverRadius?r.hoverRadius:e.getValueAtIndexOrDefault(n.hoverRadius,o,i.chart.options.elements.point.hoverRadius)+i.getRadius(n.data[o])},removeHoverStyle:function(e){var a=this;t.DatasetController.prototype.removeHoverStyle.call(a,e,a.chart.options.elements.point);var i=a.chart.data.datasets[e._datasetIndex].data[e._index],n=e.custom||{},o=e._model;o.radius=n.radius?n.radius:a.getRadius(i)}})}},{}],17:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a=t.defaults;a.doughnut={animation:{animateRotate:!0,animateScale:!1},aspectRatio:1,hover:{mode:"single"},legendCallback:function(t){var e=[];e.push('<ul class="'+t.id+'-legend">');var a=t.data,i=a.datasets,n=a.labels;if(i.length)for(var o=0;o<i[0].data.length;++o)e.push('<li><span style="background-color:'+i[0].backgroundColor[o]+'"></span>'),n[o]&&e.push(n[o]),e.push("</li>");return e.push("</ul>"),e.join("")},legend:{labels:{generateLabels:function(t){var a=t.data;return a.labels.length&&a.datasets.length?a.labels.map(function(i,n){var o=t.getDatasetMeta(0),r=a.datasets[0],l=o.data[n],s=l&&l.custom||{},d=e.getValueAtIndexOrDefault,u=t.options.elements.arc,c=s.backgroundColor?s.backgroundColor:d(r.backgroundColor,n,u.backgroundColor),h=s.borderColor?s.borderColor:d(r.borderColor,n,u.borderColor),f=s.borderWidth?s.borderWidth:d(r.borderWidth,n,u.borderWidth);return{text:i,fillStyle:c,strokeStyle:h,lineWidth:f,hidden:isNaN(r.data[n])||o.data[n].hidden,index:n}}):[]}},onClick:function(t,e){var a,i,n,o=e.index,r=this.chart;for(a=0,i=(r.data.datasets||[]).length;i>a;++a)n=r.getDatasetMeta(a),n.data[o]&&(n.data[o].hidden=!n.data[o].hidden);r.update()}},cutoutPercentage:50,rotation:Math.PI*-.5,circumference:2*Math.PI,tooltips:{callbacks:{title:function(){return""},label:function(t,e){return e.labels[t.index]+": "+e.datasets[t.datasetIndex].data[t.index]}}}},a.pie=e.clone(a.doughnut),e.extend(a.pie,{cutoutPercentage:0}),t.controllers.doughnut=t.controllers.pie=t.DatasetController.extend({dataElementType:t.elements.Arc,linkScales:e.noop,getRingIndex:function(t){for(var e=0,a=0;t>a;++a)this.chart.isDatasetVisible(a)&&++e;return e},update:function(t){var a=this,i=a.chart,n=i.chartArea,o=i.options,r=o.elements.arc,l=n.right-n.left-r.borderWidth,s=n.bottom-n.top-r.borderWidth,d=Math.min(l,s),u={x:0,y:0},c=a.getMeta(),h=o.cutoutPercentage,f=o.circumference;if(f<2*Math.PI){var g=o.rotation%(2*Math.PI);g+=2*Math.PI*(g>=Math.PI?-1:g<-Math.PI?1:0);var p=g+f,m={x:Math.cos(g),y:Math.sin(g)},b={x:Math.cos(p),y:Math.sin(p)},v=0>=g&&p>=0||g<=2*Math.PI&&2*Math.PI<=p,x=g<=.5*Math.PI&&.5*Math.PI<=p||g<=2.5*Math.PI&&2.5*Math.PI<=p,y=g<=-Math.PI&&-Math.PI<=p||g<=Math.PI&&Math.PI<=p,k=g<=.5*-Math.PI&&.5*-Math.PI<=p||g<=1.5*Math.PI&&1.5*Math.PI<=p,S=h/100,w={x:y?-1:Math.min(m.x*(m.x<0?1:S),b.x*(b.x<0?1:S)),y:k?-1:Math.min(m.y*(m.y<0?1:S),b.y*(b.y<0?1:S))},C={x:v?1:Math.max(m.x*(m.x>0?1:S),b.x*(b.x>0?1:S)),y:x?1:Math.max(m.y*(m.y>0?1:S),b.y*(b.y>0?1:S))},M={width:.5*(C.x-w.x),height:.5*(C.y-w.y)};d=Math.min(l/M.width,s/M.height),u={x:(C.x+w.x)*-.5,y:(C.y+w.y)*-.5}}i.borderWidth=a.getMaxBorderWidth(c.data),i.outerRadius=Math.max((d-i.borderWidth)/2,0),i.innerRadius=Math.max(h?i.outerRadius/100*h:1,0),i.radiusLength=(i.outerRadius-i.innerRadius)/i.getVisibleDatasetCount(),i.offsetX=u.x*i.outerRadius,i.offsetY=u.y*i.outerRadius,c.total=a.calculateTotal(),a.outerRadius=i.outerRadius-i.radiusLength*a.getRingIndex(a.index),a.innerRadius=a.outerRadius-i.radiusLength,e.each(c.data,function(e,i){a.updateElement(e,i,t)})},updateElement:function(t,a,i){var n=this,o=n.chart,r=o.chartArea,l=o.options,s=l.animation,d=(r.left+r.right)/2,u=(r.top+r.bottom)/2,c=l.rotation,h=l.rotation,f=n.getDataset(),g=i&&s.animateRotate?0:t.hidden?0:n.calculateCircumference(f.data[a])*(l.circumference/(2*Math.PI)),p=i&&s.animateScale?0:n.innerRadius,m=i&&s.animateScale?0:n.outerRadius,b=e.getValueAtIndexOrDefault;e.extend(t,{_datasetIndex:n.index,_index:a,_model:{x:d+o.offsetX,y:u+o.offsetY,startAngle:c,endAngle:h,circumference:g,outerRadius:m,innerRadius:p,label:b(f.label,a,o.data.labels[a])}});var v=t._model;this.removeHoverStyle(t),i&&s.animateRotate||(0===a?v.startAngle=l.rotation:v.startAngle=n.getMeta().data[a-1]._model.endAngle,v.endAngle=v.startAngle+v.circumference),t.pivot()},removeHoverStyle:function(e){t.DatasetController.prototype.removeHoverStyle.call(this,e,this.chart.options.elements.arc)},calculateTotal:function(){var t,a=this.getDataset(),i=this.getMeta(),n=0;return e.each(i.data,function(e,i){t=a.data[i],isNaN(t)||e.hidden||(n+=Math.abs(t))}),n},calculateCircumference:function(t){var e=this.getMeta().total;return e>0&&!isNaN(t)?2*Math.PI*(t/e):0},getMaxBorderWidth:function(t){for(var e,a,i=0,n=this.index,o=t.length,r=0;o>r;r++)e=t[r]._model?t[r]._model.borderWidth:0,a=t[r]._chart?t[r]._chart.config.data.datasets[n].hoverBorderWidth:0,i=e>i?e:i,i=a>i?a:i;return i}})}},{}],18:[function(t,e,a){"use strict";e.exports=function(t){function e(t,e){return a.getValueOrDefault(t.showLine,e.showLines)}var a=t.helpers;t.defaults.line={showLines:!0,spanGaps:!1,hover:{mode:"label"},scales:{xAxes:[{type:"category",id:"x-axis-0"}],yAxes:[{type:"linear",id:"y-axis-0"}]}},t.controllers.line=t.DatasetController.extend({datasetElementType:t.elements.Line,dataElementType:t.elements.Point,addElementAndReset:function(a){var i=this,n=i.chart.options,o=i.getMeta();t.DatasetController.prototype.addElementAndReset.call(i,a),e(i.getDataset(),n)&&0!==o.dataset._model.tension&&i.updateBezierControlPoints()},update:function(t){var i,n,o,r=this,l=r.getMeta(),s=l.dataset,d=l.data||[],u=r.chart.options,c=u.elements.line,h=r.getScaleForId(l.yAxisID),f=r.getDataset(),g=e(f,u);for(g&&(o=s.custom||{},void 0!==f.tension&&void 0===f.lineTension&&(f.lineTension=f.tension),s._scale=h,s._datasetIndex=r.index,s._children=d,s._model={spanGaps:f.spanGaps?f.spanGaps:u.spanGaps,tension:o.tension?o.tension:a.getValueOrDefault(f.lineTension,c.tension),backgroundColor:o.backgroundColor?o.backgroundColor:f.backgroundColor||c.backgroundColor,borderWidth:o.borderWidth?o.borderWidth:f.borderWidth||c.borderWidth,borderColor:o.borderColor?o.borderColor:f.borderColor||c.borderColor,borderCapStyle:o.borderCapStyle?o.borderCapStyle:f.borderCapStyle||c.borderCapStyle,borderDash:o.borderDash?o.borderDash:f.borderDash||c.borderDash,borderDashOffset:o.borderDashOffset?o.borderDashOffset:f.borderDashOffset||c.borderDashOffset,borderJoinStyle:o.borderJoinStyle?o.borderJoinStyle:f.borderJoinStyle||c.borderJoinStyle,fill:o.fill?o.fill:void 0!==f.fill?f.fill:c.fill,steppedLine:o.steppedLine?o.steppedLine:a.getValueOrDefault(f.steppedLine,c.stepped),cubicInterpolationMode:o.cubicInterpolationMode?o.cubicInterpolationMode:a.getValueOrDefault(f.cubicInterpolationMode,c.cubicInterpolationMode),scaleTop:h.top,scaleBottom:h.bottom,scaleZero:h.getBasePixel()},s.pivot()),i=0,n=d.length;n>i;++i)r.updateElement(d[i],i,t);for(g&&0!==s._model.tension&&r.updateBezierControlPoints(),i=0,n=d.length;n>i;++i)d[i].pivot()},getPointBackgroundColor:function(t,e){var i=this.chart.options.elements.point.backgroundColor,n=this.getDataset(),o=t.custom||{};return o.backgroundColor?i=o.backgroundColor:n.pointBackgroundColor?i=a.getValueAtIndexOrDefault(n.pointBackgroundColor,e,i):n.backgroundColor&&(i=n.backgroundColor),i},getPointBorderColor:function(t,e){var i=this.chart.options.elements.point.borderColor,n=this.getDataset(),o=t.custom||{};return o.borderColor?i=o.borderColor:n.pointBorderColor?i=a.getValueAtIndexOrDefault(n.pointBorderColor,e,i):n.borderColor&&(i=n.borderColor),i},getPointBorderWidth:function(t,e){var i=this.chart.options.elements.point.borderWidth,n=this.getDataset(),o=t.custom||{};return o.borderWidth?i=o.borderWidth:n.pointBorderWidth?i=a.getValueAtIndexOrDefault(n.pointBorderWidth,e,i):n.borderWidth&&(i=n.borderWidth),i},updateElement:function(t,e,i){var n,o,r=this,l=r.getMeta(),s=t.custom||{},d=r.getDataset(),u=r.index,c=d.data[e],h=r.getScaleForId(l.yAxisID),f=r.getScaleForId(l.xAxisID),g=r.chart.options.elements.point,p=r.chart.data.labels||[],m=1===p.length||1===d.data.length||r.chart.isCombo;void 0!==d.radius&&void 0===d.pointRadius&&(d.pointRadius=d.radius),void 0!==d.hitRadius&&void 0===d.pointHitRadius&&(d.pointHitRadius=d.hitRadius),n=f.getPixelForValue("object"==typeof c?c:NaN,e,u,m),o=i?h.getBasePixel():r.calculatePointY(c,e,u),t._xScale=f,t._yScale=h,t._datasetIndex=u,t._index=e,t._model={x:n,y:o,skip:s.skip||isNaN(n)||isNaN(o),radius:s.radius||a.getValueAtIndexOrDefault(d.pointRadius,e,g.radius),pointStyle:s.pointStyle||a.getValueAtIndexOrDefault(d.pointStyle,e,g.pointStyle),backgroundColor:r.getPointBackgroundColor(t,e),borderColor:r.getPointBorderColor(t,e),borderWidth:r.getPointBorderWidth(t,e),tension:l.dataset._model?l.dataset._model.tension:0,steppedLine:l.dataset._model?l.dataset._model.steppedLine:!1,hitRadius:s.hitRadius||a.getValueAtIndexOrDefault(d.pointHitRadius,e,g.hitRadius)}},calculatePointY:function(t,e,a){var i,n,o,r=this,l=r.chart,s=r.getMeta(),d=r.getScaleForId(s.yAxisID),u=0,c=0;if(d.options.stacked){for(i=0;a>i;i++)if(n=l.data.datasets[i],o=l.getDatasetMeta(i),"line"===o.type&&o.yAxisID===d.id&&l.isDatasetVisible(i)){var h=Number(d.getRightValue(n.data[e]));0>h?c+=h||0:u+=h||0}var f=Number(d.getRightValue(t));return 0>f?d.getPixelForValue(c+f):d.getPixelForValue(u+f)}return d.getPixelForValue(t)},updateBezierControlPoints:function(){function t(t,e,a){return Math.max(Math.min(t,a),e)}var e,i,n,o,r,l=this,s=l.getMeta(),d=l.chart.chartArea,u=s.data||[];if(s.dataset._model.spanGaps&&(u=u.filter(function(t){return!t._model.skip})),"monotone"===s.dataset._model.cubicInterpolationMode)a.splineCurveMonotone(u);else for(e=0,i=u.length;i>e;++e)n=u[e],o=n._model,r=a.splineCurve(a.previousItem(u,e)._model,o,a.nextItem(u,e)._model,s.dataset._model.tension),o.controlPointPreviousX=r.previous.x,o.controlPointPreviousY=r.previous.y,o.controlPointNextX=r.next.x,o.controlPointNextY=r.next.y;if(l.chart.options.elements.line.capBezierPoints)for(e=0,i=u.length;i>e;++e)o=u[e]._model,o.controlPointPreviousX=t(o.controlPointPreviousX,d.left,d.right),o.controlPointPreviousY=t(o.controlPointPreviousY,d.top,d.bottom),o.controlPointNextX=t(o.controlPointNextX,d.left,d.right),o.controlPointNextY=t(o.controlPointNextY,d.top,d.bottom)},draw:function(t){var a,i,n=this,o=n.getMeta(),r=o.data||[],l=t||1;for(a=0,i=r.length;i>a;++a)r[a].transition(l);for(e(n.getDataset(),n.chart.options)&&o.dataset.transition(l).draw(),a=0,i=r.length;i>a;++a)r[a].draw()},setHoverStyle:function(t){var e=this.chart.data.datasets[t._datasetIndex],i=t._index,n=t.custom||{},o=t._model;o.radius=n.hoverRadius||a.getValueAtIndexOrDefault(e.pointHoverRadius,i,this.chart.options.elements.point.hoverRadius),o.backgroundColor=n.hoverBackgroundColor||a.getValueAtIndexOrDefault(e.pointHoverBackgroundColor,i,a.getHoverColor(o.backgroundColor)),o.borderColor=n.hoverBorderColor||a.getValueAtIndexOrDefault(e.pointHoverBorderColor,i,a.getHoverColor(o.borderColor)),o.borderWidth=n.hoverBorderWidth||a.getValueAtIndexOrDefault(e.pointHoverBorderWidth,i,o.borderWidth)},removeHoverStyle:function(t){var e=this,i=e.chart.data.datasets[t._datasetIndex],n=t._index,o=t.custom||{},r=t._model;void 0!==i.radius&&void 0===i.pointRadius&&(i.pointRadius=i.radius),r.radius=o.radius||a.getValueAtIndexOrDefault(i.pointRadius,n,e.chart.options.elements.point.radius),r.backgroundColor=e.getPointBackgroundColor(t,n),r.borderColor=e.getPointBorderColor(t,n),r.borderWidth=e.getPointBorderWidth(t,n)}})}},{}],19:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.defaults.polarArea={scale:{type:"radialLinear",lineArc:!0,ticks:{beginAtZero:!0}},animation:{animateRotate:!0,animateScale:!0},startAngle:-.5*Math.PI,aspectRatio:1,legendCallback:function(t){var e=[];e.push('<ul class="'+t.id+'-legend">');var a=t.data,i=a.datasets,n=a.labels;if(i.length)for(var o=0;o<i[0].data.length;++o)e.push('<li><span style="background-color:'+i[0].backgroundColor[o]+'">'),n[o]&&e.push(n[o]),e.push("</span></li>");return e.push("</ul>"),e.join("")},legend:{labels:{generateLabels:function(t){var a=t.data;return a.labels.length&&a.datasets.length?a.labels.map(function(i,n){var o=t.getDatasetMeta(0),r=a.datasets[0],l=o.data[n],s=l.custom||{},d=e.getValueAtIndexOrDefault,u=t.options.elements.arc,c=s.backgroundColor?s.backgroundColor:d(r.backgroundColor,n,u.backgroundColor),h=s.borderColor?s.borderColor:d(r.borderColor,n,u.borderColor),f=s.borderWidth?s.borderWidth:d(r.borderWidth,n,u.borderWidth);return{text:i,fillStyle:c,strokeStyle:h,lineWidth:f,hidden:isNaN(r.data[n])||o.data[n].hidden,index:n}}):[]}},onClick:function(t,e){var a,i,n,o=e.index,r=this.chart;for(a=0,i=(r.data.datasets||[]).length;i>a;++a)n=r.getDatasetMeta(a),n.data[o].hidden=!n.data[o].hidden;r.update()}},tooltips:{callbacks:{title:function(){return""},label:function(t,e){return e.labels[t.index]+": "+t.yLabel}}}},t.controllers.polarArea=t.DatasetController.extend({dataElementType:t.elements.Arc,linkScales:e.noop,update:function(t){var a=this,i=a.chart,n=i.chartArea,o=a.getMeta(),r=i.options,l=r.elements.arc,s=Math.min(n.right-n.left,n.bottom-n.top);i.outerRadius=Math.max((s-l.borderWidth/2)/2,0),i.innerRadius=Math.max(r.cutoutPercentage?i.outerRadius/100*r.cutoutPercentage:1,0),i.radiusLength=(i.outerRadius-i.innerRadius)/i.getVisibleDatasetCount(),a.outerRadius=i.outerRadius-i.radiusLength*a.index,a.innerRadius=a.outerRadius-i.radiusLength,o.count=a.countVisibleElements(),e.each(o.data,function(e,i){a.updateElement(e,i,t)})},updateElement:function(t,a,i){for(var n=this,o=n.chart,r=n.getDataset(),l=o.options,s=l.animation,d=o.scale,u=e.getValueAtIndexOrDefault,c=o.data.labels,h=n.calculateCircumference(r.data[a]),f=d.xCenter,g=d.yCenter,p=0,m=n.getMeta(),b=0;a>b;++b)isNaN(r.data[b])||m.data[b].hidden||++p;var v=l.startAngle,x=t.hidden?0:d.getDistanceFromCenterForValue(r.data[a]),y=v+h*p,k=y+(t.hidden?0:h),S=s.animateScale?0:d.getDistanceFromCenterForValue(r.data[a]);e.extend(t,{_datasetIndex:n.index,_index:a,_scale:d,_model:{x:f,y:g,innerRadius:0,outerRadius:i?S:x,startAngle:i&&s.animateRotate?v:y,endAngle:i&&s.animateRotate?v:k,label:u(c,a,c[a])}}),n.removeHoverStyle(t),t.pivot()},removeHoverStyle:function(e){t.DatasetController.prototype.removeHoverStyle.call(this,e,this.chart.options.elements.arc)},countVisibleElements:function(){var t=this.getDataset(),a=this.getMeta(),i=0;return e.each(a.data,function(e,a){isNaN(t.data[a])||e.hidden||i++}),i},calculateCircumference:function(t){var e=this.getMeta().count;return e>0&&!isNaN(t)?2*Math.PI/e:0}})}},{}],20:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.defaults.radar={scale:{type:"radialLinear"},elements:{line:{tension:0}}},t.controllers.radar=t.DatasetController.extend({datasetElementType:t.elements.Line,dataElementType:t.elements.Point,linkScales:e.noop,addElementAndReset:function(e){t.DatasetController.prototype.addElementAndReset.call(this,e),this.updateBezierControlPoints()},update:function(t){var a=this,i=a.getMeta(),n=i.dataset,o=i.data,r=n.custom||{},l=a.getDataset(),s=a.chart.options.elements.line,d=a.chart.scale;void 0!==l.tension&&void 0===l.lineTension&&(l.lineTension=l.tension),e.extend(i.dataset,{_datasetIndex:a.index,_children:o,_loop:!0,_model:{tension:r.tension?r.tension:e.getValueOrDefault(l.lineTension,s.tension),backgroundColor:r.backgroundColor?r.backgroundColor:l.backgroundColor||s.backgroundColor,borderWidth:r.borderWidth?r.borderWidth:l.borderWidth||s.borderWidth,borderColor:r.borderColor?r.borderColor:l.borderColor||s.borderColor,fill:r.fill?r.fill:void 0!==l.fill?l.fill:s.fill,borderCapStyle:r.borderCapStyle?r.borderCapStyle:l.borderCapStyle||s.borderCapStyle,borderDash:r.borderDash?r.borderDash:l.borderDash||s.borderDash,borderDashOffset:r.borderDashOffset?r.borderDashOffset:l.borderDashOffset||s.borderDashOffset,borderJoinStyle:r.borderJoinStyle?r.borderJoinStyle:l.borderJoinStyle||s.borderJoinStyle,scaleTop:d.top,scaleBottom:d.bottom,scaleZero:d.getBasePosition()}}),i.dataset.pivot(),e.each(o,function(e,i){a.updateElement(e,i,t)},a),a.updateBezierControlPoints()},updateElement:function(t,a,i){var n=this,o=t.custom||{},r=n.getDataset(),l=n.chart.scale,s=n.chart.options.elements.point,d=l.getPointPositionForValue(a,r.data[a]);e.extend(t,{_datasetIndex:n.index,_index:a,_scale:l,_model:{x:i?l.xCenter:d.x,y:i?l.yCenter:d.y,tension:o.tension?o.tension:e.getValueOrDefault(r.tension,n.chart.options.elements.line.tension),radius:o.radius?o.radius:e.getValueAtIndexOrDefault(r.pointRadius,a,s.radius),backgroundColor:o.backgroundColor?o.backgroundColor:e.getValueAtIndexOrDefault(r.pointBackgroundColor,a,s.backgroundColor),borderColor:o.borderColor?o.borderColor:e.getValueAtIndexOrDefault(r.pointBorderColor,a,s.borderColor),borderWidth:o.borderWidth?o.borderWidth:e.getValueAtIndexOrDefault(r.pointBorderWidth,a,s.borderWidth),pointStyle:o.pointStyle?o.pointStyle:e.getValueAtIndexOrDefault(r.pointStyle,a,s.pointStyle),hitRadius:o.hitRadius?o.hitRadius:e.getValueAtIndexOrDefault(r.hitRadius,a,s.hitRadius)}}),t._model.skip=o.skip?o.skip:isNaN(t._model.x)||isNaN(t._model.y)},updateBezierControlPoints:function(){var t=this.chart.chartArea,a=this.getMeta();e.each(a.data,function(i,n){var o=i._model,r=e.splineCurve(e.previousItem(a.data,n,!0)._model,o,e.nextItem(a.data,n,!0)._model,o.tension);o.controlPointPreviousX=Math.max(Math.min(r.previous.x,t.right),t.left),o.controlPointPreviousY=Math.max(Math.min(r.previous.y,t.bottom),t.top),o.controlPointNextX=Math.max(Math.min(r.next.x,t.right),t.left),o.controlPointNextY=Math.max(Math.min(r.next.y,t.bottom),t.top),i.pivot()})},draw:function(t){var a=this.getMeta(),i=t||1;e.each(a.data,function(t){t.transition(i)}),a.dataset.transition(i).draw(),e.each(a.data,function(t){t.draw()})},setHoverStyle:function(t){var a=this.chart.data.datasets[t._datasetIndex],i=t.custom||{},n=t._index,o=t._model;o.radius=i.hoverRadius?i.hoverRadius:e.getValueAtIndexOrDefault(a.pointHoverRadius,n,this.chart.options.elements.point.hoverRadius),o.backgroundColor=i.hoverBackgroundColor?i.hoverBackgroundColor:e.getValueAtIndexOrDefault(a.pointHoverBackgroundColor,n,e.getHoverColor(o.backgroundColor)),o.borderColor=i.hoverBorderColor?i.hoverBorderColor:e.getValueAtIndexOrDefault(a.pointHoverBorderColor,n,e.getHoverColor(o.borderColor)),o.borderWidth=i.hoverBorderWidth?i.hoverBorderWidth:e.getValueAtIndexOrDefault(a.pointHoverBorderWidth,n,o.borderWidth)},removeHoverStyle:function(t){var a=this.chart.data.datasets[t._datasetIndex],i=t.custom||{},n=t._index,o=t._model,r=this.chart.options.elements.point;o.radius=i.radius?i.radius:e.getValueAtIndexOrDefault(a.radius,n,r.radius),o.backgroundColor=i.backgroundColor?i.backgroundColor:e.getValueAtIndexOrDefault(a.pointBackgroundColor,n,r.backgroundColor),o.borderColor=i.borderColor?i.borderColor:e.getValueAtIndexOrDefault(a.pointBorderColor,n,r.borderColor),o.borderWidth=i.borderWidth?i.borderWidth:e.getValueAtIndexOrDefault(a.pointBorderWidth,n,r.borderWidth)}})}},{}],21:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.defaults.global.animation={duration:1e3,easing:"easeOutQuart",onProgress:e.noop,onComplete:e.noop},t.Animation=t.Element.extend({currentStep:null,numSteps:60,easing:"",render:null,onAnimationProgress:null,onAnimationComplete:null}),t.animationService={frameDuration:17,animations:[],dropFrames:0,request:null,addAnimation:function(t,e,a,i){var n=this;i||(t.animating=!0);for(var o=0;o<n.animations.length;++o)if(n.animations[o].chartInstance===t)return void(n.animations[o].animationObject=e);n.animations.push({chartInstance:t,animationObject:e}),1===n.animations.length&&n.requestAnimationFrame()},cancelAnimation:function(t){var a=e.findIndex(this.animations,function(e){return e.chartInstance===t});-1!==a&&(this.animations.splice(a,1),t.animating=!1)},requestAnimationFrame:function(){var t=this;null===t.request&&(t.request=e.requestAnimFrame.call(window,function(){t.request=null,t.startDigest()}))},startDigest:function(){var t=this,e=Date.now(),a=0;t.dropFrames>1&&(a=Math.floor(t.dropFrames),t.dropFrames=t.dropFrames%1);for(var i=0;i<t.animations.length;)null===t.animations[i].animationObject.currentStep&&(t.animations[i].animationObject.currentStep=0),t.animations[i].animationObject.currentStep+=1+a,t.animations[i].animationObject.currentStep>t.animations[i].animationObject.numSteps&&(t.animations[i].animationObject.currentStep=t.animations[i].animationObject.numSteps),t.animations[i].animationObject.render(t.animations[i].chartInstance,t.animations[i].animationObject),t.animations[i].animationObject.onAnimationProgress&&t.animations[i].animationObject.onAnimationProgress.call&&t.animations[i].animationObject.onAnimationProgress.call(t.animations[i].chartInstance,t.animations[i]),t.animations[i].animationObject.currentStep===t.animations[i].animationObject.numSteps?(t.animations[i].animationObject.onAnimationComplete&&t.animations[i].animationObject.onAnimationComplete.call&&t.animations[i].animationObject.onAnimationComplete.call(t.animations[i].chartInstance,t.animations[i]),t.animations[i].chartInstance.animating=!1,t.animations.splice(i,1)):++i;var n=Date.now(),o=(n-e)/t.frameDuration;t.dropFrames+=o,t.animations.length>0&&t.requestAnimationFrame()}}}},{}],22:[function(t,e,a){"use strict";e.exports=function(t){var e=t.canvasHelpers={};e.drawPoint=function(t,e,a,i,n){var o,r,l,s,d,u;if("object"==typeof e&&(o=e.toString(),"[object HTMLImageElement]"===o||"[object HTMLCanvasElement]"===o))return void t.drawImage(e,i-e.width/2,n-e.height/2);if(!(isNaN(a)||0>=a)){switch(e){default:t.beginPath(),t.arc(i,n,a,0,2*Math.PI),t.closePath(),t.fill();break;case"triangle":t.beginPath(),r=3*a/Math.sqrt(3),d=r*Math.sqrt(3)/2,t.moveTo(i-r/2,n+d/3),t.lineTo(i+r/2,n+d/3),t.lineTo(i,n-2*d/3),t.closePath(),t.fill();break;case"rect":u=1/Math.SQRT2*a,t.beginPath(),t.fillRect(i-u,n-u,2*u,2*u),t.strokeRect(i-u,n-u,2*u,2*u);break;case"rectRot":u=1/Math.SQRT2*a,t.beginPath(),t.moveTo(i-u,n),t.lineTo(i,n+u),t.lineTo(i+u,n),t.lineTo(i,n-u),t.closePath(),t.fill();break;case"cross":t.beginPath(),t.moveTo(i,n+a),t.lineTo(i,n-a),t.moveTo(i-a,n),t.lineTo(i+a,n),t.closePath();break;case"crossRot":t.beginPath(),l=Math.cos(Math.PI/4)*a,s=Math.sin(Math.PI/4)*a,t.moveTo(i-l,n-s),t.lineTo(i+l,n+s),t.moveTo(i-l,n+s),t.lineTo(i+l,n-s),t.closePath();break;case"star":t.beginPath(),t.moveTo(i,n+a),t.lineTo(i,n-a),t.moveTo(i-a,n),t.lineTo(i+a,n),l=Math.cos(Math.PI/4)*a,s=Math.sin(Math.PI/4)*a,t.moveTo(i-l,n-s),t.lineTo(i+l,n+s),t.moveTo(i-l,n+s),t.lineTo(i+l,n-s),t.closePath();break;case"line":t.beginPath(),t.moveTo(i-a,n),t.lineTo(i+a,n),t.closePath();break;case"dash":t.beginPath(),t.moveTo(i,n),t.lineTo(i+a,n),t.closePath()}t.stroke()}}}},{}],23:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.types={},t.instances={},t.controllers={},t.Controller=function(a){return this.chart=a,this.config=a.config,this.options=this.config.options=e.configMerge(t.defaults.global,t.defaults[this.config.type],this.config.options||{}),this.id=e.uid(),Object.defineProperty(this,"data",{get:function(){return this.config.data}}),t.instances[this.id]=this,this.options.responsive&&this.resize(!0),this.initialize(),this},e.extend(t.Controller.prototype,{initialize:function(){var e=this;return t.plugins.notify("beforeInit",[e]),e.bindEvents(),e.ensureScalesHaveIDs(),e.buildOrUpdateControllers(),e.buildScales(),e.updateLayout(),e.resetElements(),e.initToolTip(),e.update(),t.plugins.notify("afterInit",[e]),e},clear:function(){return e.clear(this.chart),this},stop:function(){return t.animationService.cancelAnimation(this),this},resize:function(a){var i=this,n=i.chart,o=n.canvas,r=e.getMaximumWidth(o),l=n.aspectRatio,s=i.options.maintainAspectRatio&&isNaN(l)===!1&&isFinite(l)&&0!==l?r/l:e.getMaximumHeight(o),d=n.width!==r||n.height!==s;if(!d)return i;o.width=n.width=r,o.height=n.height=s,e.retinaScale(n);var u={width:r,height:s};return t.plugins.notify("resize",[i,u]),i.options.onResize&&i.options.onResize(i,u),a||(i.stop(),i.update(i.options.responsiveAnimationDuration)),i},ensureScalesHaveIDs:function(){var t=this.options,a=t.scales||{},i=t.scale;e.each(a.xAxes,function(t,e){t.id=t.id||"x-axis-"+e}),e.each(a.yAxes,function(t,e){t.id=t.id||"y-axis-"+e}),i&&(i.id=i.id||"scale")},buildScales:function(){var a=this,i=a.options,n=a.scales={},o=[];i.scales&&(o=o.concat((i.scales.xAxes||[]).map(function(t){return{options:t,dtype:"category"}}),(i.scales.yAxes||[]).map(function(t){return{options:t,dtype:"linear"}}))),i.scale&&o.push({options:i.scale,dtype:"radialLinear",isDefault:!0}),e.each(o,function(i){var o=i.options,r=e.getValueOrDefault(o.type,i.dtype),l=t.scaleService.getScaleConstructor(r);if(l){var s=new l({id:o.id,options:o,ctx:a.chart.ctx,chart:a});n[s.id]=s,i.isDefault&&(a.scale=s)}}),t.scaleService.addScalesToLayout(this)},updateLayout:function(){t.layoutService.update(this,this.chart.width,this.chart.height)},buildOrUpdateControllers:function(){var a=this,i=[],n=[];if(e.each(a.data.datasets,function(e,o){var r=a.getDatasetMeta(o);r.type||(r.type=e.type||a.config.type),i.push(r.type),r.controller?r.controller.updateIndex(o):(r.controller=new t.controllers[r.type](a,o),n.push(r.controller))},a),i.length>1)for(var o=1;o<i.length;o++)if(i[o]!==i[o-1]){a.isCombo=!0;break}return n},resetElements:function(){var t=this;e.each(t.data.datasets,function(e,a){t.getDatasetMeta(a).controller.reset()},t)},update:function(a,i){var n=this;t.plugins.notify("beforeUpdate",[n]),n.tooltip._data=n.data;var o=n.buildOrUpdateControllers();e.each(n.data.datasets,function(t,e){n.getDatasetMeta(e).controller.buildOrUpdateElements()},n),t.layoutService.update(n,n.chart.width,n.chart.height),t.plugins.notify("afterScaleUpdate",[n]),e.each(o,function(t){t.reset()}),n.updateDatasets(),t.plugins.notify("afterUpdate",[n]),n.render(a,i)},updateDatasets:function(){var e,a,i=this;if(t.plugins.notify("beforeDatasetsUpdate",[i])){for(e=0,a=i.data.datasets.length;a>e;++e)i.getDatasetMeta(e).controller.update();t.plugins.notify("afterDatasetsUpdate",[i])}},render:function(a,i){var n=this;t.plugins.notify("beforeRender",[n]);var o=n.options.animation;if(o&&("undefined"!=typeof a&&0!==a||"undefined"==typeof a&&0!==o.duration)){var r=new t.Animation;r.numSteps=(a||o.duration)/16.66,r.easing=o.easing,r.render=function(t,a){var i=e.easingEffects[a.easing],n=a.currentStep/a.numSteps,o=i(n);t.draw(o,n,a.currentStep)},r.onAnimationProgress=o.onProgress,r.onAnimationComplete=o.onComplete,t.animationService.addAnimation(n,r,a,i)}else n.draw(),o&&o.onComplete&&o.onComplete.call&&o.onComplete.call(n);return n},draw:function(a){var i=this,n=a||1;i.clear(),t.plugins.notify("beforeDraw",[i,n]),e.each(i.boxes,function(t){t.draw(i.chartArea)},i),i.scale&&i.scale.draw(),t.plugins.notify("beforeDatasetsDraw",[i,n]),e.each(i.data.datasets,function(t,e){i.isDatasetVisible(e)&&i.getDatasetMeta(e).controller.draw(a)},i,!0),t.plugins.notify("afterDatasetsDraw",[i,n]),i.tooltip.transition(n).draw(),t.plugins.notify("afterDraw",[i,n])},getElementAtEvent:function(t){var a=this,i=e.getRelativePosition(t,a.chart),n=[];return e.each(a.data.datasets,function(t,o){if(a.isDatasetVisible(o)){var r=a.getDatasetMeta(o);e.each(r.data,function(t){return t.inRange(i.x,i.y)?(n.push(t),n):void 0})}}),n.slice(0,1)},getElementsAtEvent:function(t){var a=this,i=e.getRelativePosition(t,a.chart),n=[],o=function(){if(a.data.datasets)for(var t=0;t<a.data.datasets.length;t++){var e=a.getDatasetMeta(t);if(a.isDatasetVisible(t))for(var n=0;n<e.data.length;n++)if(e.data[n].inRange(i.x,i.y))return e.data[n]}}.call(a);return o?(e.each(a.data.datasets,function(t,e){if(a.isDatasetVisible(e)){var i=a.getDatasetMeta(e),r=i.data[o._index];r&&!r._view.skip&&n.push(r)}},a),n):n},getElementsAtXAxis:function(t){var a=this,i=e.getRelativePosition(t,a.chart),n=[],o=function(){if(a.data.datasets)for(var t=0;t<a.data.datasets.length;t++){var e=a.getDatasetMeta(t);if(a.isDatasetVisible(t))for(var n=0;n<e.data.length;n++)if(e.data[n].inLabelRange(i.x,i.y))return e.data[n]}}.call(a);return o?(e.each(a.data.datasets,function(t,i){if(a.isDatasetVisible(i)){var r=a.getDatasetMeta(i),l=e.findIndex(r.data,function(t){return o._model.x===t._model.x});-1===l||r.data[l]._view.skip||n.push(r.data[l])}},a),n):n},getElementsAtEventForMode:function(t,e){var a=this;switch(e){case"single":return a.getElementAtEvent(t);case"label":return a.getElementsAtEvent(t);case"dataset":return a.getDatasetAtEvent(t);case"x-axis":return a.getElementsAtXAxis(t);default:return t}},getDatasetAtEvent:function(t){var e=this.getElementAtEvent(t);
return e.length>0&&(e=this.getDatasetMeta(e[0]._datasetIndex).data),e},getDatasetMeta:function(t){var e=this,a=e.data.datasets[t];a._meta||(a._meta={});var i=a._meta[e.id];return i||(i=a._meta[e.id]={type:null,data:[],dataset:null,controller:null,hidden:null,xAxisID:null,yAxisID:null}),i},getVisibleDatasetCount:function(){for(var t=0,e=0,a=this.data.datasets.length;a>e;++e)this.isDatasetVisible(e)&&t++;return t},isDatasetVisible:function(t){var e=this.getDatasetMeta(t);return"boolean"==typeof e.hidden?!e.hidden:!this.data.datasets[t].hidden},generateLegend:function(){return this.options.legendCallback(this)},destroy:function(){var a=this;a.stop(),a.clear(),e.unbindEvents(a,a.events),e.removeResizeListener(a.chart.canvas.parentNode);var i=a.chart.canvas;i.width=a.chart.width,i.height=a.chart.height,void 0!==a.chart.originalDevicePixelRatio&&a.chart.ctx.scale(1/a.chart.originalDevicePixelRatio,1/a.chart.originalDevicePixelRatio),i.style.width=a.chart.originalCanvasStyleWidth,i.style.height=a.chart.originalCanvasStyleHeight,t.plugins.notify("destroy",[a]),delete t.instances[a.id]},toBase64Image:function(){return this.chart.canvas.toDataURL.apply(this.chart.canvas,arguments)},initToolTip:function(){var e=this;e.tooltip=new t.Tooltip({_chart:e.chart,_chartInstance:e,_data:e.data,_options:e.options.tooltips},e)},bindEvents:function(){var t=this;e.bindEvents(t,t.options.events,function(e){t.eventHandler(e)})},updateHoverStyle:function(t,e,a){var i,n,o,r=a?"setHoverStyle":"removeHoverStyle";switch(e){case"single":t=[t[0]];break;case"label":case"dataset":case"x-axis":break;default:return}for(n=0,o=t.length;o>n;++n)i=t[n],i&&this.getDatasetMeta(i._datasetIndex).controller[r](i)},eventHandler:function(t){var a=this,i=a.tooltip,n=a.options||{},o=n.hover,r=n.tooltips;return a.lastActive=a.lastActive||[],a.lastTooltipActive=a.lastTooltipActive||[],"mouseout"===t.type?(a.active=[],a.tooltipActive=[]):(a.active=a.getElementsAtEventForMode(t,o.mode),a.tooltipActive=a.getElementsAtEventForMode(t,r.mode)),o.onHover&&o.onHover.call(a,a.active),a.legend&&a.legend.handleEvent&&a.legend.handleEvent(t),("mouseup"===t.type||"click"===t.type)&&n.onClick&&n.onClick.call(a,t,a.active),a.lastActive.length&&a.updateHoverStyle(a.lastActive,o.mode,!1),a.active.length&&o.mode&&a.updateHoverStyle(a.active,o.mode,!0),(r.enabled||r.custom)&&(i.initialize(),i._active=a.tooltipActive,i.update(!0)),i.pivot(),a.animating||e.arrayEquals(a.active,a.lastActive)&&e.arrayEquals(a.tooltipActive,a.lastTooltipActive)||(a.stop(),(r.enabled||r.custom)&&i.update(!0),a.render(o.animationDuration,!0)),a.lastActive=a.active,a.lastTooltipActive=a.tooltipActive,a}})}},{}],24:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a=e.noop;t.DatasetController=function(t,e){this.initialize(t,e)},e.extend(t.DatasetController.prototype,{datasetElementType:null,dataElementType:null,initialize:function(t,e){var a=this;a.chart=t,a.index=e,a.linkScales(),a.addElements()},updateIndex:function(t){this.index=t},linkScales:function(){var t=this,e=t.getMeta(),a=t.getDataset();null===e.xAxisID&&(e.xAxisID=a.xAxisID||t.chart.options.scales.xAxes[0].id),null===e.yAxisID&&(e.yAxisID=a.yAxisID||t.chart.options.scales.yAxes[0].id)},getDataset:function(){return this.chart.data.datasets[this.index]},getMeta:function(){return this.chart.getDatasetMeta(this.index)},getScaleForId:function(t){return this.chart.scales[t]},reset:function(){this.update(!0)},createMetaDataset:function(){var t=this,e=t.datasetElementType;return e&&new e({_chart:t.chart.chart,_datasetIndex:t.index})},createMetaData:function(t){var e=this,a=e.dataElementType;return a&&new a({_chart:e.chart.chart,_datasetIndex:e.index,_index:t})},addElements:function(){var t,e,a=this,i=a.getMeta(),n=a.getDataset().data||[],o=i.data;for(t=0,e=n.length;e>t;++t)o[t]=o[t]||a.createMetaData(i,t);i.dataset=i.dataset||a.createMetaDataset()},addElementAndReset:function(t){var e=this,a=e.createMetaData(t);e.getMeta().data.splice(t,0,a),e.updateElement(a,t,!0)},buildOrUpdateElements:function(){var t=this.getMeta(),e=t.data,a=this.getDataset().data.length,i=e.length;if(i>a)e.splice(a,i-a);else if(a>i)for(var n=i;a>n;++n)this.addElementAndReset(n)},update:a,draw:function(t){var a=t||1;e.each(this.getMeta().data,function(t){t.transition(a).draw()})},removeHoverStyle:function(t,a){var i=this.chart.data.datasets[t._datasetIndex],n=t._index,o=t.custom||{},r=e.getValueAtIndexOrDefault,l=t._model;l.backgroundColor=o.backgroundColor?o.backgroundColor:r(i.backgroundColor,n,a.backgroundColor),l.borderColor=o.borderColor?o.borderColor:r(i.borderColor,n,a.borderColor),l.borderWidth=o.borderWidth?o.borderWidth:r(i.borderWidth,n,a.borderWidth)},setHoverStyle:function(t){var a=this.chart.data.datasets[t._datasetIndex],i=t._index,n=t.custom||{},o=e.getValueAtIndexOrDefault,r=e.getHoverColor,l=t._model;l.backgroundColor=n.hoverBackgroundColor?n.hoverBackgroundColor:o(a.hoverBackgroundColor,i,r(l.backgroundColor)),l.borderColor=n.hoverBorderColor?n.hoverBorderColor:o(a.hoverBorderColor,i,r(l.borderColor)),l.borderWidth=n.hoverBorderWidth?n.hoverBorderWidth:o(a.hoverBorderWidth,i,l.borderWidth)}}),t.DatasetController.extend=e.inherits}},{}],25:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.elements={},t.Element=function(t){e.extend(this,t),this.initialize.apply(this,arguments)},e.extend(t.Element.prototype,{initialize:function(){this.hidden=!1},pivot:function(){var t=this;return t._view||(t._view=e.clone(t._model)),t._start=e.clone(t._view),t},transition:function(t){var a=this;return a._view||(a._view=e.clone(a._model)),1===t?(a._view=a._model,a._start=null,a):(a._start||a.pivot(),e.each(a._model,function(i,n){if("_"===n[0]);else if(a._view.hasOwnProperty(n))if(i===a._view[n]);else if("string"==typeof i)try{var o=e.color(a._model[n]).mix(e.color(a._start[n]),t);a._view[n]=o.rgbString()}catch(r){a._view[n]=i}else if("number"==typeof i){var l=void 0!==a._start[n]&&isNaN(a._start[n])===!1?a._start[n]:0;a._view[n]=(a._model[n]-l)*t+l}else a._view[n]=i;else"number"!=typeof i||isNaN(a._view[n])?a._view[n]=i:a._view[n]=i*t},a),a)},tooltipPosition:function(){return{x:this._model.x,y:this._model.y}},hasValue:function(){return e.isNumber(this._model.x)&&e.isNumber(this._model.y)}}),t.Element.extend=e.inherits}},{}],26:[function(t,e,a){"use strict";var i=t(3);e.exports=function(t){function e(t,e,a){var i;return"string"==typeof t?(i=parseInt(t,10),-1!==t.indexOf("%")&&(i=i/100*e.parentNode[a])):i=t,i}function a(t){return void 0!==t&&null!==t&&"none"!==t}function n(t,i,n){var o=document.defaultView,r=t.parentNode,l=o.getComputedStyle(t)[i],s=o.getComputedStyle(r)[i],d=a(l),u=a(s),c=Number.POSITIVE_INFINITY;return d||u?Math.min(d?e(l,t,n):c,u?e(s,r,n):c):"none"}var o=t.helpers={};o.each=function(t,e,a,i){var n,r;if(o.isArray(t))if(r=t.length,i)for(n=r-1;n>=0;n--)e.call(a,t[n],n);else for(n=0;r>n;n++)e.call(a,t[n],n);else if("object"==typeof t){var l=Object.keys(t);for(r=l.length,n=0;r>n;n++)e.call(a,t[l[n]],l[n])}},o.clone=function(t){var e={};return o.each(t,function(t,a){o.isArray(t)?e[a]=t.slice(0):"object"==typeof t&&null!==t?e[a]=o.clone(t):e[a]=t}),e},o.extend=function(t){for(var e=function(e,a){t[a]=e},a=1,i=arguments.length;i>a;a++)o.each(arguments[a],e);return t},o.configMerge=function(e){var a=o.clone(e);return o.each(Array.prototype.slice.call(arguments,1),function(e){o.each(e,function(e,i){if("scales"===i)a[i]=o.scaleMerge(a.hasOwnProperty(i)?a[i]:{},e);else if("scale"===i)a[i]=o.configMerge(a.hasOwnProperty(i)?a[i]:{},t.scaleService.getScaleDefaults(e.type),e);else if(a.hasOwnProperty(i)&&o.isArray(a[i])&&o.isArray(e)){var n=a[i];o.each(e,function(t,e){e<n.length?"object"==typeof n[e]&&null!==n[e]&&"object"==typeof t&&null!==t?n[e]=o.configMerge(n[e],t):n[e]=t:n.push(t)})}else a.hasOwnProperty(i)&&"object"==typeof a[i]&&null!==a[i]&&"object"==typeof e?a[i]=o.configMerge(a[i],e):a[i]=e})}),a},o.scaleMerge=function(e,a){var i=o.clone(e);return o.each(a,function(e,a){"xAxes"===a||"yAxes"===a?i.hasOwnProperty(a)?o.each(e,function(e,n){var r=o.getValueOrDefault(e.type,"xAxes"===a?"category":"linear"),l=t.scaleService.getScaleDefaults(r);n>=i[a].length||!i[a][n].type?i[a].push(o.configMerge(l,e)):e.type&&e.type!==i[a][n].type?i[a][n]=o.configMerge(i[a][n],l,e):i[a][n]=o.configMerge(i[a][n],e)}):(i[a]=[],o.each(e,function(e){var n=o.getValueOrDefault(e.type,"xAxes"===a?"category":"linear");i[a].push(o.configMerge(t.scaleService.getScaleDefaults(n),e))})):i.hasOwnProperty(a)&&"object"==typeof i[a]&&null!==i[a]&&"object"==typeof e?i[a]=o.configMerge(i[a],e):i[a]=e}),i},o.getValueAtIndexOrDefault=function(t,e,a){return void 0===t||null===t?a:o.isArray(t)?e<t.length?t[e]:a:t},o.getValueOrDefault=function(t,e){return void 0===t?e:t},o.indexOf=Array.prototype.indexOf?function(t,e){return t.indexOf(e)}:function(t,e){for(var a=0,i=t.length;i>a;++a)if(t[a]===e)return a;return-1},o.where=function(t,e){if(o.isArray(t)&&Array.prototype.filter)return t.filter(e);var a=[];return o.each(t,function(t){e(t)&&a.push(t)}),a},o.findIndex=Array.prototype.findIndex?function(t,e,a){return t.findIndex(e,a)}:function(t,e,a){a=void 0===a?t:a;for(var i=0,n=t.length;n>i;++i)if(e.call(a,t[i],i,t))return i;return-1},o.findNextWhere=function(t,e,a){(void 0===a||null===a)&&(a=-1);for(var i=a+1;i<t.length;i++){var n=t[i];if(e(n))return n}},o.findPreviousWhere=function(t,e,a){(void 0===a||null===a)&&(a=t.length);for(var i=a-1;i>=0;i--){var n=t[i];if(e(n))return n}},o.inherits=function(t){var e=this,a=t&&t.hasOwnProperty("constructor")?t.constructor:function(){return e.apply(this,arguments)},i=function(){this.constructor=a};return i.prototype=e.prototype,a.prototype=new i,a.extend=o.inherits,t&&o.extend(a.prototype,t),a.__super__=e.prototype,a},o.noop=function(){},o.uid=function(){var t=0;return function(){return t++}}(),o.isNumber=function(t){return!isNaN(parseFloat(t))&&isFinite(t)},o.almostEquals=function(t,e,a){return Math.abs(t-e)<a},o.max=function(t){return t.reduce(function(t,e){return isNaN(e)?t:Math.max(t,e)},Number.NEGATIVE_INFINITY)},o.min=function(t){return t.reduce(function(t,e){return isNaN(e)?t:Math.min(t,e)},Number.POSITIVE_INFINITY)},o.sign=Math.sign?function(t){return Math.sign(t)}:function(t){return t=+t,0===t||isNaN(t)?t:t>0?1:-1},o.log10=Math.log10?function(t){return Math.log10(t)}:function(t){return Math.log(t)/Math.LN10},o.toRadians=function(t){return t*(Math.PI/180)},o.toDegrees=function(t){return t*(180/Math.PI)},o.getAngleFromPoint=function(t,e){var a=e.x-t.x,i=e.y-t.y,n=Math.sqrt(a*a+i*i),o=Math.atan2(i,a);return o<-.5*Math.PI&&(o+=2*Math.PI),{angle:o,distance:n}},o.aliasPixel=function(t){return t%2===0?0:.5},o.splineCurve=function(t,e,a,i){var n=t.skip?e:t,o=e,r=a.skip?e:a,l=Math.sqrt(Math.pow(o.x-n.x,2)+Math.pow(o.y-n.y,2)),s=Math.sqrt(Math.pow(r.x-o.x,2)+Math.pow(r.y-o.y,2)),d=l/(l+s),u=s/(l+s);d=isNaN(d)?0:d,u=isNaN(u)?0:u;var c=i*d,h=i*u;return{previous:{x:o.x-c*(r.x-n.x),y:o.y-c*(r.y-n.y)},next:{x:o.x+h*(r.x-n.x),y:o.y+h*(r.y-n.y)}}},o.EPSILON=Number.EPSILON||1e-14,o.splineCurveMonotone=function(t){var e,a,i,n,r=(t||[]).map(function(t){return{model:t._model,deltaK:0,mK:0}}),l=r.length;for(e=0;l>e;++e)i=r[e],i.model.skip||(a=e>0?r[e-1]:null,n=l-1>e?r[e+1]:null,n&&!n.model.skip&&(i.deltaK=(n.model.y-i.model.y)/(n.model.x-i.model.x)),!a||a.model.skip?i.mK=i.deltaK:!n||n.model.skip?i.mK=a.deltaK:this.sign(a.deltaK)!==this.sign(i.deltaK)?i.mK=0:i.mK=(a.deltaK+i.deltaK)/2);var s,d,u,c;for(e=0;l-1>e;++e)i=r[e],n=r[e+1],i.model.skip||n.model.skip||(o.almostEquals(i.deltaK,0,this.EPSILON)?i.mK=n.mK=0:(s=i.mK/i.deltaK,d=n.mK/i.deltaK,c=Math.pow(s,2)+Math.pow(d,2),9>=c||(u=3/Math.sqrt(c),i.mK=s*u*i.deltaK,n.mK=d*u*i.deltaK)));var h;for(e=0;l>e;++e)i=r[e],i.model.skip||(a=e>0?r[e-1]:null,n=l-1>e?r[e+1]:null,a&&!a.model.skip&&(h=(i.model.x-a.model.x)/3,i.model.controlPointPreviousX=i.model.x-h,i.model.controlPointPreviousY=i.model.y-h*i.mK),n&&!n.model.skip&&(h=(n.model.x-i.model.x)/3,i.model.controlPointNextX=i.model.x+h,i.model.controlPointNextY=i.model.y+h*i.mK))},o.nextItem=function(t,e,a){return a?e>=t.length-1?t[0]:t[e+1]:e>=t.length-1?t[t.length-1]:t[e+1]},o.previousItem=function(t,e,a){return a?0>=e?t[t.length-1]:t[e-1]:0>=e?t[0]:t[e-1]},o.niceNum=function(t,e){var a,i=Math.floor(o.log10(t)),n=t/Math.pow(10,i);return a=e?1.5>n?1:3>n?2:7>n?5:10:1>=n?1:2>=n?2:5>=n?5:10,a*Math.pow(10,i)};var r=o.easingEffects={linear:function(t){return t},easeInQuad:function(t){return t*t},easeOutQuad:function(t){return-1*t*(t-2)},easeInOutQuad:function(t){return(t/=.5)<1?.5*t*t:-0.5*(--t*(t-2)-1)},easeInCubic:function(t){return t*t*t},easeOutCubic:function(t){return 1*((t=t/1-1)*t*t+1)},easeInOutCubic:function(t){return(t/=.5)<1?.5*t*t*t:.5*((t-=2)*t*t+2)},easeInQuart:function(t){return t*t*t*t},easeOutQuart:function(t){return-1*((t=t/1-1)*t*t*t-1)},easeInOutQuart:function(t){return(t/=.5)<1?.5*t*t*t*t:-0.5*((t-=2)*t*t*t-2)},easeInQuint:function(t){return 1*(t/=1)*t*t*t*t},easeOutQuint:function(t){return 1*((t=t/1-1)*t*t*t*t+1)},easeInOutQuint:function(t){return(t/=.5)<1?.5*t*t*t*t*t:.5*((t-=2)*t*t*t*t+2)},easeInSine:function(t){return-1*Math.cos(t/1*(Math.PI/2))+1},easeOutSine:function(t){return 1*Math.sin(t/1*(Math.PI/2))},easeInOutSine:function(t){return-0.5*(Math.cos(Math.PI*t/1)-1)},easeInExpo:function(t){return 0===t?1:1*Math.pow(2,10*(t/1-1))},easeOutExpo:function(t){return 1===t?1:1*(-Math.pow(2,-10*t/1)+1)},easeInOutExpo:function(t){return 0===t?0:1===t?1:(t/=.5)<1?.5*Math.pow(2,10*(t-1)):.5*(-Math.pow(2,-10*--t)+2)},easeInCirc:function(t){return t>=1?t:-1*(Math.sqrt(1-(t/=1)*t)-1)},easeOutCirc:function(t){return 1*Math.sqrt(1-(t=t/1-1)*t)},easeInOutCirc:function(t){return(t/=.5)<1?-0.5*(Math.sqrt(1-t*t)-1):.5*(Math.sqrt(1-(t-=2)*t)+1)},easeInElastic:function(t){var e=1.70158,a=0,i=1;return 0===t?0:1===(t/=1)?1:(a||(a=.3),i<Math.abs(1)?(i=1,e=a/4):e=a/(2*Math.PI)*Math.asin(1/i),-(i*Math.pow(2,10*(t-=1))*Math.sin((1*t-e)*(2*Math.PI)/a)))},easeOutElastic:function(t){var e=1.70158,a=0,i=1;return 0===t?0:1===(t/=1)?1:(a||(a=.3),i<Math.abs(1)?(i=1,e=a/4):e=a/(2*Math.PI)*Math.asin(1/i),i*Math.pow(2,-10*t)*Math.sin((1*t-e)*(2*Math.PI)/a)+1)},easeInOutElastic:function(t){var e=1.70158,a=0,i=1;return 0===t?0:2===(t/=.5)?1:(a||(a=1*(.3*1.5)),i<Math.abs(1)?(i=1,e=a/4):e=a/(2*Math.PI)*Math.asin(1/i),1>t?-.5*(i*Math.pow(2,10*(t-=1))*Math.sin((1*t-e)*(2*Math.PI)/a)):i*Math.pow(2,-10*(t-=1))*Math.sin((1*t-e)*(2*Math.PI)/a)*.5+1)},easeInBack:function(t){var e=1.70158;return 1*(t/=1)*t*((e+1)*t-e)},easeOutBack:function(t){var e=1.70158;return 1*((t=t/1-1)*t*((e+1)*t+e)+1)},easeInOutBack:function(t){var e=1.70158;return(t/=.5)<1?.5*(t*t*(((e*=1.525)+1)*t-e)):.5*((t-=2)*t*(((e*=1.525)+1)*t+e)+2)},easeInBounce:function(t){return 1-r.easeOutBounce(1-t)},easeOutBounce:function(t){return(t/=1)<1/2.75?1*(7.5625*t*t):2/2.75>t?1*(7.5625*(t-=1.5/2.75)*t+.75):2.5/2.75>t?1*(7.5625*(t-=2.25/2.75)*t+.9375):1*(7.5625*(t-=2.625/2.75)*t+.984375)},easeInOutBounce:function(t){return.5>t?.5*r.easeInBounce(2*t):.5*r.easeOutBounce(2*t-1)+.5}};o.requestAnimFrame=function(){return window.requestAnimationFrame||window.webkitRequestAnimationFrame||window.mozRequestAnimationFrame||window.oRequestAnimationFrame||window.msRequestAnimationFrame||function(t){return window.setTimeout(t,1e3/60)}}(),o.cancelAnimFrame=function(){return window.cancelAnimationFrame||window.webkitCancelAnimationFrame||window.mozCancelAnimationFrame||window.oCancelAnimationFrame||window.msCancelAnimationFrame||function(t){return window.clearTimeout(t,1e3/60)}}(),o.getRelativePosition=function(t,e){var a,i,n=t.originalEvent||t,r=t.currentTarget||t.srcElement,l=r.getBoundingClientRect(),s=n.touches;s&&s.length>0?(a=s[0].clientX,i=s[0].clientY):(a=n.clientX,i=n.clientY);var d=parseFloat(o.getStyle(r,"padding-left")),u=parseFloat(o.getStyle(r,"padding-top")),c=parseFloat(o.getStyle(r,"padding-right")),h=parseFloat(o.getStyle(r,"padding-bottom")),f=l.right-l.left-d-c,g=l.bottom-l.top-u-h;return a=Math.round((a-l.left-d)/f*r.width/e.currentDevicePixelRatio),i=Math.round((i-l.top-u)/g*r.height/e.currentDevicePixelRatio),{x:a,y:i}},o.addEvent=function(t,e,a){t.addEventListener?t.addEventListener(e,a):t.attachEvent?t.attachEvent("on"+e,a):t["on"+e]=a},o.removeEvent=function(t,e,a){t.removeEventListener?t.removeEventListener(e,a,!1):t.detachEvent?t.detachEvent("on"+e,a):t["on"+e]=o.noop},o.bindEvents=function(t,e,a){var i=t.events=t.events||{};o.each(e,function(e){i[e]=function(){a.apply(t,arguments)},o.addEvent(t.chart.canvas,e,i[e])})},o.unbindEvents=function(t,e){var a=t.chart.canvas;o.each(e,function(t,e){o.removeEvent(a,e,t)})},o.getConstraintWidth=function(t){return n(t,"max-width","clientWidth")},o.getConstraintHeight=function(t){return n(t,"max-height","clientHeight")},o.getMaximumWidth=function(t){var e=t.parentNode,a=parseInt(o.getStyle(e,"padding-left"),10),i=parseInt(o.getStyle(e,"padding-right"),10),n=e.clientWidth-a-i,r=o.getConstraintWidth(t);return isNaN(r)?n:Math.min(n,r)},o.getMaximumHeight=function(t){var e=t.parentNode,a=parseInt(o.getStyle(e,"padding-top"),10),i=parseInt(o.getStyle(e,"padding-bottom"),10),n=e.clientHeight-a-i,r=o.getConstraintHeight(t);return isNaN(r)?n:Math.min(n,r)},o.getStyle=function(t,e){return t.currentStyle?t.currentStyle[e]:document.defaultView.getComputedStyle(t,null).getPropertyValue(e)},o.retinaScale=function(t){var e=t.ctx,a=t.canvas,i=a.width,n=a.height,o=t.currentDevicePixelRatio=window.devicePixelRatio||1;1!==o&&(a.height=n*o,a.width=i*o,e.scale(o,o),t.originalDevicePixelRatio=t.originalDevicePixelRatio||o),a.style.width=i+"px",a.style.height=n+"px"},o.clear=function(t){t.ctx.clearRect(0,0,t.width,t.height)},o.fontString=function(t,e,a){return e+" "+t+"px "+a},o.longestText=function(t,e,a,i){i=i||{};var n=i.data=i.data||{},r=i.garbageCollect=i.garbageCollect||[];i.font!==e&&(n=i.data={},r=i.garbageCollect=[],i.font=e),t.font=e;var l=0;o.each(a,function(e){void 0!==e&&null!==e&&o.isArray(e)!==!0?l=o.measureText(t,n,r,l,e):o.isArray(e)&&o.each(e,function(e){void 0===e||null===e||o.isArray(e)||(l=o.measureText(t,n,r,l,e))})});var s=r.length/2;if(s>a.length){for(var d=0;s>d;d++)delete n[r[d]];r.splice(0,s)}return l},o.measureText=function(t,e,a,i,n){var o=e[n];return o||(o=e[n]=t.measureText(n).width,a.push(n)),o>i&&(i=o),i},o.numberOfLabelLines=function(t){var e=1;return o.each(t,function(t){o.isArray(t)&&t.length>e&&(e=t.length)}),e},o.drawRoundedRectangle=function(t,e,a,i,n,o){t.beginPath(),t.moveTo(e+o,a),t.lineTo(e+i-o,a),t.quadraticCurveTo(e+i,a,e+i,a+o),t.lineTo(e+i,a+n-o),t.quadraticCurveTo(e+i,a+n,e+i-o,a+n),t.lineTo(e+o,a+n),t.quadraticCurveTo(e,a+n,e,a+n-o),t.lineTo(e,a+o),t.quadraticCurveTo(e,a,e+o,a),t.closePath()},o.color=function(e){return i?i(e instanceof CanvasGradient?t.defaults.global.defaultColor:e):(console.error("Color.js not found!"),e)},o.addResizeListener=function(t,e){var a=document.createElement("iframe"),i="chartjs-hidden-iframe";a.classlist?a.classlist.add(i):a.setAttribute("class",i),a.tabIndex=-1;var n=a.style;n.width="100%",n.display="block",n.border=0,n.height=0,n.margin=0,n.position="absolute",n.left=0,n.right=0,n.top=0,n.bottom=0,t.insertBefore(a,t.firstChild),(a.contentWindow||a).onresize=function(){return e?e():void 0}},o.removeResizeListener=function(t){var e=t.querySelector(".chartjs-hidden-iframe");e&&e.parentNode.removeChild(e)},o.isArray=Array.isArray?function(t){return Array.isArray(t)}:function(t){return"[object Array]"===Object.prototype.toString.call(t)},o.arrayEquals=function(t,e){var a,i,n,r;if(!t||!e||t.length!==e.length)return!1;for(a=0,i=t.length;i>a;++a)if(n=t[a],r=e[a],n instanceof Array&&r instanceof Array){if(!o.arrayEquals(n,r))return!1}else if(n!==r)return!1;return!0},o.callCallback=function(t,e,a){t&&"function"==typeof t.call&&t.apply(a,e)},o.getHoverColor=function(t){return t instanceof CanvasPattern?t:o.color(t).saturate(.5).darken(.1).rgbString()}}},{3:3}],27:[function(t,e,a){"use strict";e.exports=function(){var t=function(e,a){var i=this,n=t.helpers;return i.config=a||{data:{datasets:[]}},e.length&&e[0].getContext&&(e=e[0]),e.getContext&&(e=e.getContext("2d")),i.ctx=e,i.canvas=e.canvas,e.canvas.style.display=e.canvas.style.display||"block",i.width=e.canvas.width||parseInt(n.getStyle(e.canvas,"width"),10)||n.getMaximumWidth(e.canvas),i.height=e.canvas.height||parseInt(n.getStyle(e.canvas,"height"),10)||n.getMaximumHeight(e.canvas),i.aspectRatio=i.width/i.height,(isNaN(i.aspectRatio)||isFinite(i.aspectRatio)===!1)&&(i.aspectRatio=void 0!==a.aspectRatio?a.aspectRatio:2),i.originalCanvasStyleWidth=e.canvas.style.width,i.originalCanvasStyleHeight=e.canvas.style.height,n.retinaScale(i),i.controller=new t.Controller(i),n.addResizeListener(e.canvas.parentNode,function(){i.controller&&i.controller.config.options.responsive&&i.controller.resize()}),i.controller?i.controller:i};return t.defaults={global:{responsive:!0,responsiveAnimationDuration:0,maintainAspectRatio:!0,events:["mousemove","mouseout","click","touchstart","touchmove"],hover:{onHover:null,mode:"single",animationDuration:400},onClick:null,defaultColor:"rgba(0,0,0,0.1)",defaultFontColor:"#666",defaultFontFamily:"'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",defaultFontSize:12,defaultFontStyle:"normal",showLines:!0,elements:{},legendCallback:function(t){var e=[];e.push('<ul class="'+t.id+'-legend">');for(var a=0;a<t.data.datasets.length;a++)e.push('<li><span style="background-color:'+t.data.datasets[a].backgroundColor+'"></span>'),t.data.datasets[a].label&&e.push(t.data.datasets[a].label),e.push("</li>");return e.push("</ul>"),e.join("")}}},t.Chart=t,t}},{}],28:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.layoutService={defaults:{},addBox:function(t,e){t.boxes||(t.boxes=[]),t.boxes.push(e)},removeBox:function(t,e){t.boxes&&t.boxes.splice(t.boxes.indexOf(e),1)},update:function(t,a,i){function n(t){var e,a=t.isHorizontal();a?(e=t.update(t.options.fullWidth?p:k,y),S-=e.height):(e=t.update(x,v),k-=e.width),w.push({horizontal:a,minSize:e,box:t})}function o(t){var a=e.findNextWhere(w,function(e){return e.box===t});if(a)if(t.isHorizontal()){var i={left:C,right:M,top:0,bottom:0};t.update(t.options.fullWidth?p:k,m/2,i)}else t.update(a.minSize.width,S)}function r(t){var a=e.findNextWhere(w,function(e){return e.box===t}),i={left:0,right:0,top:D,bottom:I};a&&t.update(a.minSize.width,S,i)}function l(t){t.isHorizontal()?(t.left=t.options.fullWidth?s:C,t.right=t.options.fullWidth?a-s:C+k,t.top=F,t.bottom=F+t.height,F=t.bottom):(t.left=P,t.right=P+t.width,t.top=D,t.bottom=D+S,P=t.right)}if(t){var s=0,d=0,u=e.where(t.boxes,function(t){return"left"===t.options.position}),c=e.where(t.boxes,function(t){return"right"===t.options.position}),h=e.where(t.boxes,function(t){return"top"===t.options.position}),f=e.where(t.boxes,function(t){return"bottom"===t.options.position}),g=e.where(t.boxes,function(t){return"chartArea"===t.options.position});h.sort(function(t,e){return(e.options.fullWidth?1:0)-(t.options.fullWidth?1:0)}),f.sort(function(t,e){return(t.options.fullWidth?1:0)-(e.options.fullWidth?1:0)});var p=a-2*s,m=i-2*d,b=p/2,v=m/2,x=(a-b)/(u.length+c.length),y=(i-v)/(h.length+f.length),k=p,S=m,w=[];e.each(u.concat(c,h,f),n);var C=s,M=s,D=d,I=d;e.each(u.concat(c),o),e.each(u,function(t){C+=t.width}),e.each(c,function(t){M+=t.width}),e.each(h.concat(f),o),e.each(h,function(t){D+=t.height}),e.each(f,function(t){I+=t.height}),e.each(u.concat(c),r),C=s,M=s,D=d,I=d,e.each(u,function(t){C+=t.width}),e.each(c,function(t){M+=t.width}),e.each(h,function(t){D+=t.height}),e.each(f,function(t){I+=t.height});var A=i-D-I,T=a-C-M;(T!==k||A!==S)&&(e.each(u,function(t){t.height=A}),e.each(c,function(t){t.height=A}),e.each(h,function(t){t.options.fullWidth||(t.width=T)}),e.each(f,function(t){t.options.fullWidth||(t.width=T)}),S=A,k=T);var P=s,F=d;e.each(u.concat(h),l),P+=k,F+=S,e.each(c,l),e.each(f,l),t.chartArea={left:C,top:D,right:C+k,bottom:D+S},e.each(g,function(e){e.left=t.chartArea.left,e.top=t.chartArea.top,e.right=t.chartArea.right,e.bottom=t.chartArea.bottom,e.update(k,S)})}}}}},{}],29:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a=e.noop;t.defaults.global.legend={display:!0,position:"top",fullWidth:!0,reverse:!1,onClick:function(t,e){var a=e.datasetIndex,i=this.chart,n=i.getDatasetMeta(a);n.hidden=null===n.hidden?!i.data.datasets[a].hidden:null,i.update()},onHover:null,labels:{boxWidth:40,padding:10,generateLabels:function(t){var a=t.data;return e.isArray(a.datasets)?a.datasets.map(function(a,i){return{text:a.label,fillStyle:e.isArray(a.backgroundColor)?a.backgroundColor[0]:a.backgroundColor,hidden:!t.isDatasetVisible(i),lineCap:a.borderCapStyle,lineDash:a.borderDash,lineDashOffset:a.borderDashOffset,lineJoin:a.borderJoinStyle,lineWidth:a.borderWidth,strokeStyle:a.borderColor,pointStyle:a.pointStyle,datasetIndex:i}},this):[]}}},t.Legend=t.Element.extend({initialize:function(t){e.extend(this,t),this.legendHitBoxes=[],this.doughnutMode=!1},beforeUpdate:a,update:function(t,e,a){var i=this;return i.beforeUpdate(),i.maxWidth=t,i.maxHeight=e,i.margins=a,i.beforeSetDimensions(),i.setDimensions(),i.afterSetDimensions(),i.beforeBuildLabels(),i.buildLabels(),i.afterBuildLabels(),i.beforeFit(),i.fit(),i.afterFit(),i.afterUpdate(),i.minSize},afterUpdate:a,beforeSetDimensions:a,setDimensions:function(){var t=this;t.isHorizontal()?(t.width=t.maxWidth,t.left=0,t.right=t.width):(t.height=t.maxHeight,t.top=0,t.bottom=t.height),t.paddingLeft=0,t.paddingTop=0,t.paddingRight=0,t.paddingBottom=0,t.minSize={width:0,height:0}},afterSetDimensions:a,beforeBuildLabels:a,buildLabels:function(){var t=this;t.legendItems=t.options.labels.generateLabels.call(t,t.chart),t.options.reverse&&t.legendItems.reverse()},afterBuildLabels:a,beforeFit:a,fit:function(){var a=this,i=a.options,n=i.labels,o=i.display,r=a.ctx,l=t.defaults.global,s=e.getValueOrDefault,d=s(n.fontSize,l.defaultFontSize),u=s(n.fontStyle,l.defaultFontStyle),c=s(n.fontFamily,l.defaultFontFamily),h=e.fontString(d,u,c),f=a.legendHitBoxes=[],g=a.minSize,p=a.isHorizontal();if(p?(g.width=a.maxWidth,g.height=o?10:0):(g.width=o?10:0,g.height=a.maxHeight),o)if(r.font=h,p){var m=a.lineWidths=[0],b=a.legendItems.length?d+n.padding:0;r.textAlign="left",r.textBaseline="top",e.each(a.legendItems,function(t,e){var i=n.usePointStyle?d*Math.sqrt(2):n.boxWidth,o=i+d/2+r.measureText(t.text).width;m[m.length-1]+o+n.padding>=a.width&&(b+=d+n.padding,m[m.length]=a.left),f[e]={left:0,top:0,width:o,height:d},m[m.length-1]+=o+n.padding}),g.height+=b}else{var v=n.padding,x=a.columnWidths=[],y=n.padding,k=0,S=0,w=d+v;e.each(a.legendItems,function(t,e){var a=n.usePointStyle?2*n.boxWidth:n.boxWidth,i=a+d/2+r.measureText(t.text).width;S+w>g.height&&(y+=k+n.padding,x.push(k),k=0,S=0),k=Math.max(k,i),S+=w,f[e]={left:0,top:0,width:i,height:d}}),y+=k,x.push(k),g.width+=y}a.width=g.width,a.height=g.height},afterFit:a,isHorizontal:function(){return"top"===this.options.position||"bottom"===this.options.position},draw:function(){var a=this,i=a.options,n=i.labels,o=t.defaults.global,r=o.elements.line,l=a.width,s=a.lineWidths;if(i.display){var d,u=a.ctx,c=e.getValueOrDefault,h=c(n.fontColor,o.defaultFontColor),f=c(n.fontSize,o.defaultFontSize),g=c(n.fontStyle,o.defaultFontStyle),p=c(n.fontFamily,o.defaultFontFamily),m=e.fontString(f,g,p);u.textAlign="left",u.textBaseline="top",u.lineWidth=.5,u.strokeStyle=h,u.fillStyle=h,u.font=m;var b=n.boxWidth,v=a.legendHitBoxes,x=function(e,a,n){if(!(isNaN(b)||0>=b)){u.save(),u.fillStyle=c(n.fillStyle,o.defaultColor),u.lineCap=c(n.lineCap,r.borderCapStyle),u.lineDashOffset=c(n.lineDashOffset,r.borderDashOffset),u.lineJoin=c(n.lineJoin,r.borderJoinStyle),u.lineWidth=c(n.lineWidth,r.borderWidth),u.strokeStyle=c(n.strokeStyle,o.defaultColor);var l=0===c(n.lineWidth,r.borderWidth);if(u.setLineDash&&u.setLineDash(c(n.lineDash,r.borderDash)),i.labels&&i.labels.usePointStyle){var s=f*Math.SQRT2/2,d=s/Math.SQRT2,h=e+d,g=a+d;t.canvasHelpers.drawPoint(u,n.pointStyle,s,h,g)}else l||u.strokeRect(e,a,b,f),u.fillRect(e,a,b,f);u.restore()}},y=function(t,e,a,i){u.fillText(a.text,b+f/2+t,e),a.hidden&&(u.beginPath(),u.lineWidth=2,u.moveTo(b+f/2+t,e+f/2),u.lineTo(b+f/2+t+i,e+f/2),u.stroke())},k=a.isHorizontal();d=k?{x:a.left+(l-s[0])/2,y:a.top+n.padding,line:0}:{x:a.left+n.padding,y:a.top+n.padding,line:0};var S=f+n.padding;e.each(a.legendItems,function(t,e){var i=u.measureText(t.text).width,o=n.usePointStyle?f+f/2+i:b+f/2+i,r=d.x,c=d.y;k?r+o>=l&&(c=d.y+=S,d.line++,r=d.x=a.left+(l-s[d.line])/2):c+S>a.bottom&&(r=d.x=r+a.columnWidths[d.line]+n.padding,c=d.y=a.top,d.line++),x(r,c,t),v[e].left=r,v[e].top=c,y(r,c,t,i),k?d.x+=o+n.padding:d.y+=S})}},handleEvent:function(t){var a=this,i=a.options,n="mouseup"===t.type?"click":t.type;if("mousemove"===n){if(!i.onHover)return}else{if("click"!==n)return;if(!i.onClick)return}var o=e.getRelativePosition(t,a.chart.chart),r=o.x,l=o.y;if(r>=a.left&&r<=a.right&&l>=a.top&&l<=a.bottom)for(var s=a.legendHitBoxes,d=0;d<s.length;++d){var u=s[d];if(r>=u.left&&r<=u.left+u.width&&l>=u.top&&l<=u.top+u.height){if("click"===n){i.onClick.call(a,t,a.legendItems[d]);break}if("mousemove"===n){i.onHover.call(a,t,a.legendItems[d]);break}}}}}),t.plugins.register({beforeInit:function(e){var a=e.options,i=a.legend;i&&(e.legend=new t.Legend({ctx:e.chart.ctx,options:i,chart:e}),t.layoutService.addBox(e,e.legend))}})}},{}],30:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers.noop;t.plugins={_plugins:[],register:function(t){var e=this._plugins;[].concat(t).forEach(function(t){-1===e.indexOf(t)&&e.push(t)})},unregister:function(t){var e=this._plugins;[].concat(t).forEach(function(t){var a=e.indexOf(t);-1!==a&&e.splice(a,1)})},clear:function(){this._plugins=[]},count:function(){return this._plugins.length},getAll:function(){return this._plugins},notify:function(t,e){var a,i,n=this._plugins,o=n.length;for(a=0;o>a;++a)if(i=n[a],"function"==typeof i[t]&&i[t].apply(i,e||[])===!1)return!1;return!0}},t.PluginBase=t.Element.extend({beforeInit:e,afterInit:e,beforeUpdate:e,afterUpdate:e,beforeDraw:e,afterDraw:e,destroy:e}),t.pluginService=t.plugins}},{}],31:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.defaults.scale={display:!0,position:"left",gridLines:{display:!0,color:"rgba(0, 0, 0, 0.1)",lineWidth:1,drawBorder:!0,drawOnChartArea:!0,drawTicks:!0,tickMarkLength:10,zeroLineWidth:1,zeroLineColor:"rgba(0,0,0,0.25)",offsetGridLines:!1,borderDash:[],borderDashOffset:0},scaleLabel:{labelString:"",display:!1},ticks:{beginAtZero:!1,minRotation:0,maxRotation:50,mirror:!1,padding:10,reverse:!1,display:!0,autoSkip:!0,autoSkipPadding:0,labelOffset:0,callback:function(t){return e.isArray(t)?t:""+t}}},t.Scale=t.Element.extend({beforeUpdate:function(){e.callCallback(this.options.beforeUpdate,[this])},update:function(t,a,i){var n=this;return n.beforeUpdate(),n.maxWidth=t,n.maxHeight=a,n.margins=e.extend({left:0,right:0,top:0,bottom:0},i),n.beforeSetDimensions(),n.setDimensions(),n.afterSetDimensions(),n.beforeDataLimits(),n.determineDataLimits(),n.afterDataLimits(),n.beforeBuildTicks(),n.buildTicks(),n.afterBuildTicks(),n.beforeTickToLabelConversion(),n.convertTicksToLabels(),n.afterTickToLabelConversion(),n.beforeCalculateTickRotation(),n.calculateTickRotation(),n.afterCalculateTickRotation(),n.beforeFit(),n.fit(),n.afterFit(),n.afterUpdate(),n.minSize},afterUpdate:function(){e.callCallback(this.options.afterUpdate,[this])},beforeSetDimensions:function(){e.callCallback(this.options.beforeSetDimensions,[this])},setDimensions:function(){var t=this;t.isHorizontal()?(t.width=t.maxWidth,t.left=0,t.right=t.width):(t.height=t.maxHeight,t.top=0,t.bottom=t.height),t.paddingLeft=0,t.paddingTop=0,t.paddingRight=0,t.paddingBottom=0},afterSetDimensions:function(){e.callCallback(this.options.afterSetDimensions,[this])},beforeDataLimits:function(){e.callCallback(this.options.beforeDataLimits,[this])},determineDataLimits:e.noop,afterDataLimits:function(){e.callCallback(this.options.afterDataLimits,[this]);
},beforeBuildTicks:function(){e.callCallback(this.options.beforeBuildTicks,[this])},buildTicks:e.noop,afterBuildTicks:function(){e.callCallback(this.options.afterBuildTicks,[this])},beforeTickToLabelConversion:function(){e.callCallback(this.options.beforeTickToLabelConversion,[this])},convertTicksToLabels:function(){var t=this;t.ticks=t.ticks.map(function(e,a,i){return t.options.ticks.userCallback?t.options.ticks.userCallback(e,a,i):t.options.ticks.callback(e,a,i)},t)},afterTickToLabelConversion:function(){e.callCallback(this.options.afterTickToLabelConversion,[this])},beforeCalculateTickRotation:function(){e.callCallback(this.options.beforeCalculateTickRotation,[this])},calculateTickRotation:function(){var a=this,i=a.ctx,n=t.defaults.global,o=a.options.ticks,r=e.getValueOrDefault(o.fontSize,n.defaultFontSize),l=e.getValueOrDefault(o.fontStyle,n.defaultFontStyle),s=e.getValueOrDefault(o.fontFamily,n.defaultFontFamily),d=e.fontString(r,l,s);i.font=d;var u,c=i.measureText(a.ticks[0]).width,h=i.measureText(a.ticks[a.ticks.length-1]).width;if(a.labelRotation=o.minRotation||0,a.paddingRight=0,a.paddingLeft=0,a.options.display&&a.isHorizontal()){a.paddingRight=h/2+3,a.paddingLeft=c/2+3,a.longestTextCache||(a.longestTextCache={});for(var f,g,p=e.longestText(i,d,a.ticks,a.longestTextCache),m=p,b=a.getPixelForTick(1)-a.getPixelForTick(0)-6;m>b&&a.labelRotation<o.maxRotation;){if(f=Math.cos(e.toRadians(a.labelRotation)),g=Math.sin(e.toRadians(a.labelRotation)),u=f*c,u+r/2>a.yLabelWidth&&(a.paddingLeft=u+r/2),a.paddingRight=r/2,g*p>a.maxHeight){a.labelRotation--;break}a.labelRotation++,m=f*p}}a.margins&&(a.paddingLeft=Math.max(a.paddingLeft-a.margins.left,0),a.paddingRight=Math.max(a.paddingRight-a.margins.right,0))},afterCalculateTickRotation:function(){e.callCallback(this.options.afterCalculateTickRotation,[this])},beforeFit:function(){e.callCallback(this.options.beforeFit,[this])},fit:function(){var a=this,i=a.minSize={width:0,height:0},n=a.options,o=t.defaults.global,r=n.ticks,l=n.scaleLabel,s=n.gridLines,d=n.display,u=a.isHorizontal(),c=e.getValueOrDefault(r.fontSize,o.defaultFontSize),h=e.getValueOrDefault(r.fontStyle,o.defaultFontStyle),f=e.getValueOrDefault(r.fontFamily,o.defaultFontFamily),g=e.fontString(c,h,f),p=e.getValueOrDefault(l.fontSize,o.defaultFontSize),m=n.gridLines.tickMarkLength;if(u?i.width=a.isFullWidth()?a.maxWidth-a.margins.left-a.margins.right:a.maxWidth:i.width=d&&s.drawTicks?m:0,u?i.height=d&&s.drawTicks?m:0:i.height=a.maxHeight,l.display&&d&&(u?i.height+=1.5*p:i.width+=1.5*p),r.display&&d){a.longestTextCache||(a.longestTextCache={});var b=e.longestText(a.ctx,g,a.ticks,a.longestTextCache),v=e.numberOfLabelLines(a.ticks),x=.5*c;if(u){a.longestLabelWidth=b;var y=Math.sin(e.toRadians(a.labelRotation))*a.longestLabelWidth+c*v+x*v;i.height=Math.min(a.maxHeight,i.height+y),a.ctx.font=g;var k=a.ctx.measureText(a.ticks[0]).width,S=a.ctx.measureText(a.ticks[a.ticks.length-1]).width,w=Math.cos(e.toRadians(a.labelRotation)),C=Math.sin(e.toRadians(a.labelRotation));a.paddingLeft=0!==a.labelRotation?w*k+3:k/2+3,a.paddingRight=0!==a.labelRotation?C*(c/2)+3:S/2+3}else{var M=a.maxWidth-i.width,D=r.mirror;D?b=0:b+=a.options.ticks.padding,M>b?i.width+=b:i.width=a.maxWidth,a.paddingTop=c/2,a.paddingBottom=c/2}}a.margins&&(a.paddingLeft=Math.max(a.paddingLeft-a.margins.left,0),a.paddingTop=Math.max(a.paddingTop-a.margins.top,0),a.paddingRight=Math.max(a.paddingRight-a.margins.right,0),a.paddingBottom=Math.max(a.paddingBottom-a.margins.bottom,0)),a.width=i.width,a.height=i.height},afterFit:function(){e.callCallback(this.options.afterFit,[this])},isHorizontal:function(){return"top"===this.options.position||"bottom"===this.options.position},isFullWidth:function(){return this.options.fullWidth},getRightValue:function(t){return null===t||"undefined"==typeof t?NaN:"number"==typeof t&&isNaN(t)?NaN:"object"==typeof t?t instanceof Date||t.isValid?t:this.getRightValue(this.isHorizontal()?t.x:t.y):t},getLabelForIndex:e.noop,getPixelForValue:e.noop,getValueForPixel:e.noop,getPixelForTick:function(t,e){var a=this;if(a.isHorizontal()){var i=a.width-(a.paddingLeft+a.paddingRight),n=i/Math.max(a.ticks.length-(a.options.gridLines.offsetGridLines?0:1),1),o=n*t+a.paddingLeft;e&&(o+=n/2);var r=a.left+Math.round(o);return r+=a.isFullWidth()?a.margins.left:0}var l=a.height-(a.paddingTop+a.paddingBottom);return a.top+t*(l/(a.ticks.length-1))},getPixelForDecimal:function(t){var e=this;if(e.isHorizontal()){var a=e.width-(e.paddingLeft+e.paddingRight),i=a*t+e.paddingLeft,n=e.left+Math.round(i);return n+=e.isFullWidth()?e.margins.left:0}return e.top+t*e.height},getBasePixel:function(){var t=this,e=t.min,a=t.max;return t.getPixelForValue(t.beginAtZero?0:0>e&&0>a?a:e>0&&a>0?e:0)},draw:function(a){var i=this,n=i.options;if(n.display){var o,r,l=i.ctx,s=t.defaults.global,d=n.ticks,u=n.gridLines,c=n.scaleLabel,h=0!==i.labelRotation,f=d.autoSkip,g=i.isHorizontal();d.maxTicksLimit&&(r=d.maxTicksLimit);var p=e.getValueOrDefault(d.fontColor,s.defaultFontColor),m=e.getValueOrDefault(d.fontSize,s.defaultFontSize),b=e.getValueOrDefault(d.fontStyle,s.defaultFontStyle),v=e.getValueOrDefault(d.fontFamily,s.defaultFontFamily),x=e.fontString(m,b,v),y=u.tickMarkLength,k=e.getValueOrDefault(u.borderDash,s.borderDash),S=e.getValueOrDefault(u.borderDashOffset,s.borderDashOffset),w=e.getValueOrDefault(c.fontColor,s.defaultFontColor),C=e.getValueOrDefault(c.fontSize,s.defaultFontSize),M=e.getValueOrDefault(c.fontStyle,s.defaultFontStyle),D=e.getValueOrDefault(c.fontFamily,s.defaultFontFamily),I=e.fontString(C,M,D),A=e.toRadians(i.labelRotation),T=Math.cos(A),P=i.longestLabelWidth*T;l.fillStyle=p;var F=[];if(g){if(o=!1,h&&(P/=2),(P+d.autoSkipPadding)*i.ticks.length>i.width-(i.paddingLeft+i.paddingRight)&&(o=1+Math.floor((P+d.autoSkipPadding)*i.ticks.length/(i.width-(i.paddingLeft+i.paddingRight)))),r&&i.ticks.length>r)for(;!o||i.ticks.length/(o||1)>r;)o||(o=1),o+=1;f||(o=!1)}var R="right"===n.position?i.left:i.right-y,_="right"===n.position?i.left+y:i.right,V="bottom"===n.position?i.top:i.bottom-y,L="bottom"===n.position?i.top+y:i.bottom;if(e.each(i.ticks,function(t,r){if(void 0!==t&&null!==t){var l=i.ticks.length===r+1,s=o>1&&r%o>0||r%o===0&&r+o>=i.ticks.length;if((!s||l)&&void 0!==t&&null!==t){var c,f;r===("undefined"!=typeof i.zeroLineIndex?i.zeroLineIndex:0)?(c=u.zeroLineWidth,f=u.zeroLineColor):(c=e.getValueAtIndexOrDefault(u.lineWidth,r),f=e.getValueAtIndexOrDefault(u.color,r));var p,m,b,v,x,w,C,M,D,I,T="middle",P="middle";if(g){h||(P="top"===n.position?"bottom":"top"),T=h?"right":"center";var O=i.getPixelForTick(r)+e.aliasPixel(c);D=i.getPixelForTick(r,u.offsetGridLines)+d.labelOffset,I=h?i.top+12:"top"===n.position?i.bottom-y:i.top+y,p=b=x=C=O,m=V,v=L,w=a.top,M=a.bottom}else{"left"===n.position?d.mirror?(D=i.right+d.padding,T="left"):(D=i.right-d.padding,T="right"):d.mirror?(D=i.left-d.padding,T="right"):(D=i.left+d.padding,T="left");var B=i.getPixelForTick(r);B+=e.aliasPixel(c),I=i.getPixelForTick(r,u.offsetGridLines),p=R,b=_,x=a.left,C=a.right,m=v=w=M=B}F.push({tx1:p,ty1:m,tx2:b,ty2:v,x1:x,y1:w,x2:C,y2:M,labelX:D,labelY:I,glWidth:c,glColor:f,glBorderDash:k,glBorderDashOffset:S,rotation:-1*A,label:t,textBaseline:P,textAlign:T})}}}),e.each(F,function(t){if(u.display&&(l.save(),l.lineWidth=t.glWidth,l.strokeStyle=t.glColor,l.setLineDash&&(l.setLineDash(t.glBorderDash),l.lineDashOffset=t.glBorderDashOffset),l.beginPath(),u.drawTicks&&(l.moveTo(t.tx1,t.ty1),l.lineTo(t.tx2,t.ty2)),u.drawOnChartArea&&(l.moveTo(t.x1,t.y1),l.lineTo(t.x2,t.y2)),l.stroke(),l.restore()),d.display){l.save(),l.translate(t.labelX,t.labelY),l.rotate(t.rotation),l.font=x,l.textBaseline=t.textBaseline,l.textAlign=t.textAlign;var a=t.label;if(e.isArray(a))for(var i=0,n=-(a.length-1)*m*.75;i<a.length;++i)l.fillText(""+a[i],0,n),n+=1.5*m;else l.fillText(a,0,0);l.restore()}}),c.display){var O,B,W=0;if(g)O=i.left+(i.right-i.left)/2,B="bottom"===n.position?i.bottom-C/2:i.top+C/2;else{var z="left"===n.position;O=z?i.left+C/2:i.right-C/2,B=i.top+(i.bottom-i.top)/2,W=z?-.5*Math.PI:.5*Math.PI}l.save(),l.translate(O,B),l.rotate(W),l.textAlign="center",l.textBaseline="middle",l.fillStyle=w,l.font=I,l.fillText(c.labelString,0,0),l.restore()}if(u.drawBorder){l.lineWidth=e.getValueAtIndexOrDefault(u.lineWidth,0),l.strokeStyle=e.getValueAtIndexOrDefault(u.color,0);var N=i.left,H=i.right,E=i.top,U=i.bottom,q=e.aliasPixel(l.lineWidth);g?(E=U="top"===n.position?i.bottom:i.top,E+=q,U+=q):(N=H="left"===n.position?i.right:i.left,N+=q,H+=q),l.beginPath(),l.moveTo(N,E),l.lineTo(H,U),l.stroke()}}}})}},{}],32:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.scaleService={constructors:{},defaults:{},registerScaleType:function(t,a,i){this.constructors[t]=a,this.defaults[t]=e.clone(i)},getScaleConstructor:function(t){return this.constructors.hasOwnProperty(t)?this.constructors[t]:void 0},getScaleDefaults:function(a){return this.defaults.hasOwnProperty(a)?e.scaleMerge(t.defaults.scale,this.defaults[a]):{}},updateScaleDefaults:function(t,a){var i=this.defaults;i.hasOwnProperty(t)&&(i[t]=e.extend(i[t],a))},addScalesToLayout:function(a){e.each(a.scales,function(e){t.layoutService.addBox(a,e)})}}}},{}],33:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers;t.defaults.global.title={display:!1,position:"top",fullWidth:!0,fontStyle:"bold",padding:10,text:""};var a=e.noop;t.Title=t.Element.extend({initialize:function(a){var i=this;e.extend(i,a),i.options=e.configMerge(t.defaults.global.title,a.options),i.legendHitBoxes=[]},beforeUpdate:function(){var a=this.chart.options;a&&a.title&&(this.options=e.configMerge(t.defaults.global.title,a.title))},update:function(t,e,a){var i=this;return i.beforeUpdate(),i.maxWidth=t,i.maxHeight=e,i.margins=a,i.beforeSetDimensions(),i.setDimensions(),i.afterSetDimensions(),i.beforeBuildLabels(),i.buildLabels(),i.afterBuildLabels(),i.beforeFit(),i.fit(),i.afterFit(),i.afterUpdate(),i.minSize},afterUpdate:a,beforeSetDimensions:a,setDimensions:function(){var t=this;t.isHorizontal()?(t.width=t.maxWidth,t.left=0,t.right=t.width):(t.height=t.maxHeight,t.top=0,t.bottom=t.height),t.paddingLeft=0,t.paddingTop=0,t.paddingRight=0,t.paddingBottom=0,t.minSize={width:0,height:0}},afterSetDimensions:a,beforeBuildLabels:a,buildLabels:a,afterBuildLabels:a,beforeFit:a,fit:function(){var a=this,i=e.getValueOrDefault,n=a.options,o=t.defaults.global,r=n.display,l=i(n.fontSize,o.defaultFontSize),s=a.minSize;a.isHorizontal()?(s.width=a.maxWidth,s.height=r?l+2*n.padding:0):(s.width=r?l+2*n.padding:0,s.height=a.maxHeight),a.width=s.width,a.height=s.height},afterFit:a,isHorizontal:function(){var t=this.options.position;return"top"===t||"bottom"===t},draw:function(){var a=this,i=a.ctx,n=e.getValueOrDefault,o=a.options,r=t.defaults.global;if(o.display){var l,s,d=n(o.fontSize,r.defaultFontSize),u=n(o.fontStyle,r.defaultFontStyle),c=n(o.fontFamily,r.defaultFontFamily),h=e.fontString(d,u,c),f=0,g=a.top,p=a.left,m=a.bottom,b=a.right;i.fillStyle=n(o.fontColor,r.defaultFontColor),i.font=h,a.isHorizontal()?(l=p+(b-p)/2,s=g+(m-g)/2):(l="left"===o.position?p+d/2:b-d/2,s=g+(m-g)/2,f=Math.PI*("left"===o.position?-.5:.5)),i.save(),i.translate(l,s),i.rotate(f),i.textAlign="center",i.textBaseline="middle",i.fillText(o.text,0,0),i.restore()}}}),t.plugins.register({beforeInit:function(e){var a=e.options,i=a.title;i&&(e.titleBlock=new t.Title({ctx:e.chart.ctx,options:i,chart:e}),t.layoutService.addBox(e,e.titleBlock))}})}},{}],34:[function(t,e,a){"use strict";e.exports=function(t){function e(t,e){return e&&(n.isArray(e)?Array.prototype.push.apply(t,e):t.push(e)),t}function a(t){if(!t.length)return!1;var e,a,i=[],n=[];for(e=0,a=t.length;a>e;++e){var o=t[e];if(o&&o.hasValue()){var r=o.tooltipPosition();i.push(r.x),n.push(r.y)}}var l=0,s=0;for(e=0;e<i.length;++e)i[e]&&(l+=i[e],s+=n[e]);return{x:Math.round(l/i.length),y:Math.round(s/i.length)}}function i(t){var e=t._xScale,a=t._yScale||t._scale,i=t._index,n=t._datasetIndex;return{xLabel:e?e.getLabelForIndex(i,n):"",yLabel:a?a.getLabelForIndex(i,n):"",index:i,datasetIndex:n}}var n=t.helpers;t.defaults.global.tooltips={enabled:!0,custom:null,mode:"single",backgroundColor:"rgba(0,0,0,0.8)",titleFontStyle:"bold",titleSpacing:2,titleMarginBottom:6,titleFontColor:"#fff",titleAlign:"left",bodySpacing:2,bodyFontColor:"#fff",bodyAlign:"left",footerFontStyle:"bold",footerSpacing:2,footerMarginTop:6,footerFontColor:"#fff",footerAlign:"left",yPadding:6,xPadding:6,yAlign:"center",xAlign:"center",caretSize:5,cornerRadius:6,multiKeyBackground:"#fff",callbacks:{beforeTitle:n.noop,title:function(t,e){var a="",i=e.labels,n=i?i.length:0;if(t.length>0){var o=t[0];o.xLabel?a=o.xLabel:n>0&&o.index<n&&(a=i[o.index])}return a},afterTitle:n.noop,beforeBody:n.noop,beforeLabel:n.noop,label:function(t,e){var a=e.datasets[t.datasetIndex].label||"";return a+": "+t.yLabel},labelColor:function(t,e){var a=e.getDatasetMeta(t.datasetIndex),i=a.data[t.index],n=i._view;return{borderColor:n.borderColor,backgroundColor:n.backgroundColor}},afterLabel:n.noop,afterBody:n.noop,beforeFooter:n.noop,footer:n.noop,afterFooter:n.noop}},t.Tooltip=t.Element.extend({initialize:function(){var e=this,a=t.defaults.global,i=e._options,o=n.getValueOrDefault;n.extend(e,{_model:{xPadding:i.xPadding,yPadding:i.yPadding,xAlign:i.xAlign,yAlign:i.yAlign,bodyFontColor:i.bodyFontColor,_bodyFontFamily:o(i.bodyFontFamily,a.defaultFontFamily),_bodyFontStyle:o(i.bodyFontStyle,a.defaultFontStyle),_bodyAlign:i.bodyAlign,bodyFontSize:o(i.bodyFontSize,a.defaultFontSize),bodySpacing:i.bodySpacing,titleFontColor:i.titleFontColor,_titleFontFamily:o(i.titleFontFamily,a.defaultFontFamily),_titleFontStyle:o(i.titleFontStyle,a.defaultFontStyle),titleFontSize:o(i.titleFontSize,a.defaultFontSize),_titleAlign:i.titleAlign,titleSpacing:i.titleSpacing,titleMarginBottom:i.titleMarginBottom,footerFontColor:i.footerFontColor,_footerFontFamily:o(i.footerFontFamily,a.defaultFontFamily),_footerFontStyle:o(i.footerFontStyle,a.defaultFontStyle),footerFontSize:o(i.footerFontSize,a.defaultFontSize),_footerAlign:i.footerAlign,footerSpacing:i.footerSpacing,footerMarginTop:i.footerMarginTop,caretSize:i.caretSize,cornerRadius:i.cornerRadius,backgroundColor:i.backgroundColor,opacity:0,legendColorBackground:i.multiKeyBackground}})},getTitle:function(){var t=this,a=t._options,i=a.callbacks,n=i.beforeTitle.apply(t,arguments),o=i.title.apply(t,arguments),r=i.afterTitle.apply(t,arguments),l=[];return l=e(l,n),l=e(l,o),l=e(l,r)},getBeforeBody:function(){var t=this._options.callbacks.beforeBody.apply(this,arguments);return n.isArray(t)?t:void 0!==t?[t]:[]},getBody:function(t,a){var i=this,o=i._options.callbacks,r=[];return n.each(t,function(t){var n={before:[],lines:[],after:[]};e(n.before,o.beforeLabel.call(i,t,a)),e(n.lines,o.label.call(i,t,a)),e(n.after,o.afterLabel.call(i,t,a)),r.push(n)}),r},getAfterBody:function(){var t=this._options.callbacks.afterBody.apply(this,arguments);return n.isArray(t)?t:void 0!==t?[t]:[]},getFooter:function(){var t=this,a=t._options.callbacks,i=a.beforeFooter.apply(t,arguments),n=a.footer.apply(t,arguments),o=a.afterFooter.apply(t,arguments),r=[];return r=e(r,i),r=e(r,n),r=e(r,o)},update:function(t){var e,o,r=this,l=r._options,s=r._model,d=r._active,u=r._data,c=r._chartInstance;if(d.length){s.opacity=1;var h=[],f=a(d),g=[];for(e=0,o=d.length;o>e;++e)g.push(i(d[e]));l.itemSort&&(g=g.sort(function(t,e){return l.itemSort(t,e,u)})),d.length>1&&n.each(g,function(t){h.push(l.callbacks.labelColor.call(r,t,c))}),n.extend(s,{title:r.getTitle(g,u),beforeBody:r.getBeforeBody(g,u),body:r.getBody(g,u),afterBody:r.getAfterBody(g,u),footer:r.getFooter(g,u),x:Math.round(f.x),y:Math.round(f.y),caretPadding:n.getValueOrDefault(f.padding,2),labelColors:h});var p=r.getTooltipSize(s);r.determineAlignment(p),n.extend(s,r.getBackgroundPoint(s,p))}else r._model.opacity=0;return t&&l.custom&&l.custom.call(r,s),r},getTooltipSize:function(t){var e=this._chart.ctx,a={height:2*t.yPadding,width:0},i=t.body,o=i.reduce(function(t,e){return t+e.before.length+e.lines.length+e.after.length},0);o+=t.beforeBody.length+t.afterBody.length;var r=t.title.length,l=t.footer.length,s=t.titleFontSize,d=t.bodyFontSize,u=t.footerFontSize;a.height+=r*s,a.height+=(r-1)*t.titleSpacing,a.height+=r?t.titleMarginBottom:0,a.height+=o*d,a.height+=o?(o-1)*t.bodySpacing:0,a.height+=l?t.footerMarginTop:0,a.height+=l*u,a.height+=l?(l-1)*t.footerSpacing:0;var c=0,h=function(t){a.width=Math.max(a.width,e.measureText(t).width+c)};return e.font=n.fontString(s,t._titleFontStyle,t._titleFontFamily),n.each(t.title,h),e.font=n.fontString(d,t._bodyFontStyle,t._bodyFontFamily),n.each(t.beforeBody.concat(t.afterBody),h),c=i.length>1?d+2:0,n.each(i,function(t){n.each(t.before,h),n.each(t.lines,h),n.each(t.after,h)}),c=0,e.font=n.fontString(u,t._footerFontStyle,t._footerFontFamily),n.each(t.footer,h),a.width+=2*t.xPadding,a},determineAlignment:function(t){var e=this,a=e._model,i=e._chart,n=e._chartInstance.chartArea;a.y<t.height?a.yAlign="top":a.y>i.height-t.height&&(a.yAlign="bottom");var o,r,l,s,d,u=(n.left+n.right)/2,c=(n.top+n.bottom)/2;"center"===a.yAlign?(o=function(t){return u>=t},r=function(t){return t>u}):(o=function(e){return e<=t.width/2},r=function(e){return e>=i.width-t.width/2}),l=function(e){return e+t.width>i.width},s=function(e){return e-t.width<0},d=function(t){return c>=t?"top":"bottom"},o(a.x)?(a.xAlign="left",l(a.x)&&(a.xAlign="center",a.yAlign=d(a.y))):r(a.x)&&(a.xAlign="right",s(a.x)&&(a.xAlign="center",a.yAlign=d(a.y)))},getBackgroundPoint:function(t,e){var a={x:t.x,y:t.y},i=t.caretSize,n=t.caretPadding,o=t.cornerRadius,r=t.xAlign,l=t.yAlign,s=i+n,d=o+n;return"right"===r?a.x-=e.width:"center"===r&&(a.x-=e.width/2),"top"===l?a.y+=s:"bottom"===l?a.y-=e.height+s:a.y-=e.height/2,"center"===l?"left"===r?a.x+=s:"right"===r&&(a.x-=s):"left"===r?a.x-=d:"right"===r&&(a.x+=d),a},drawCaret:function(t,e,a){var i,o,r,l,s,d,u=this._view,c=this._chart.ctx,h=u.caretSize,f=u.cornerRadius,g=u.xAlign,p=u.yAlign,m=t.x,b=t.y,v=e.width,x=e.height;"center"===p?("left"===g?(i=m,o=i-h,r=i):(i=m+v,o=i+h,r=i),s=b+x/2,l=s-h,d=s+h):("left"===g?(i=m+f,o=i+h,r=o+h):"right"===g?(i=m+v-f,o=i-h,r=o-h):(o=m+v/2,i=o-h,r=o+h),"top"===p?(l=b,s=l-h,d=l):(l=b+x,s=l+h,d=l));var y=n.color(u.backgroundColor);c.fillStyle=y.alpha(a*y.alpha()).rgbString(),c.beginPath(),c.moveTo(i,l),c.lineTo(o,s),c.lineTo(r,d),c.closePath(),c.fill()},drawTitle:function(t,e,a,i){var o=e.title;if(o.length){a.textAlign=e._titleAlign,a.textBaseline="top";var r=e.titleFontSize,l=e.titleSpacing,s=n.color(e.titleFontColor);a.fillStyle=s.alpha(i*s.alpha()).rgbString(),a.font=n.fontString(r,e._titleFontStyle,e._titleFontFamily);var d,u;for(d=0,u=o.length;u>d;++d)a.fillText(o[d],t.x,t.y),t.y+=r+l,d+1===o.length&&(t.y+=e.titleMarginBottom-l)}},drawBody:function(t,e,a,i){var o=e.bodyFontSize,r=e.bodySpacing,l=e.body;a.textAlign=e._bodyAlign,a.textBaseline="top";var s=n.color(e.bodyFontColor),d=s.alpha(i*s.alpha()).rgbString();a.fillStyle=d,a.font=n.fontString(o,e._bodyFontStyle,e._bodyFontFamily);var u=0,c=function(e){a.fillText(e,t.x+u,t.y),t.y+=o+r};n.each(e.beforeBody,c);var h=l.length>1;u=h?o+2:0,n.each(l,function(r,l){n.each(r.before,c),n.each(r.lines,function(r){h&&(a.fillStyle=n.color(e.legendColorBackground).alpha(i).rgbaString(),a.fillRect(t.x,t.y,o,o),a.strokeStyle=n.color(e.labelColors[l].borderColor).alpha(i).rgbaString(),a.strokeRect(t.x,t.y,o,o),a.fillStyle=n.color(e.labelColors[l].backgroundColor).alpha(i).rgbaString(),a.fillRect(t.x+1,t.y+1,o-2,o-2),a.fillStyle=d),c(r)}),n.each(r.after,c)}),u=0,n.each(e.afterBody,c),t.y-=r},drawFooter:function(t,e,a,i){var o=e.footer;if(o.length){t.y+=e.footerMarginTop,a.textAlign=e._footerAlign,a.textBaseline="top";var r=n.color(e.footerFontColor);a.fillStyle=r.alpha(i*r.alpha()).rgbString(),a.font=n.fontString(e.footerFontSize,e._footerFontStyle,e._footerFontFamily),n.each(o,function(i){a.fillText(i,t.x,t.y),t.y+=e.footerFontSize+e.footerSpacing})}},draw:function(){var t=this._chart.ctx,e=this._view;if(0!==e.opacity){var a=this.getTooltipSize(e),i={x:e.x,y:e.y},o=Math.abs(e.opacity<.001)?0:e.opacity;if(this._options.enabled){var r=n.color(e.backgroundColor);t.fillStyle=r.alpha(o*r.alpha()).rgbString(),n.drawRoundedRectangle(t,i.x,i.y,a.width,a.height,e.cornerRadius),t.fill(),this.drawCaret(i,a,o),i.x+=e.xPadding,i.y+=e.yPadding,this.drawTitle(i,e,t,o),this.drawBody(i,e,t,o),this.drawFooter(i,e,t,o)}}}})}},{}],35:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a=t.defaults.global;a.elements.arc={backgroundColor:a.defaultColor,borderColor:"#fff",borderWidth:2},t.elements.Arc=t.Element.extend({inLabelRange:function(t){var e=this._view;return e?Math.pow(t-e.x,2)<Math.pow(e.radius+e.hoverRadius,2):!1},inRange:function(t,a){var i=this._view;if(i){for(var n=e.getAngleFromPoint(i,{x:t,y:a}),o=n.angle,r=n.distance,l=i.startAngle,s=i.endAngle;l>s;)s+=2*Math.PI;for(;o>s;)o-=2*Math.PI;for(;l>o;)o+=2*Math.PI;var d=o>=l&&s>=o,u=r>=i.innerRadius&&r<=i.outerRadius;return d&&u}return!1},tooltipPosition:function(){var t=this._view,e=t.startAngle+(t.endAngle-t.startAngle)/2,a=(t.outerRadius-t.innerRadius)/2+t.innerRadius;return{x:t.x+Math.cos(e)*a,y:t.y+Math.sin(e)*a}},draw:function(){var t=this._chart.ctx,e=this._view,a=e.startAngle,i=e.endAngle;t.beginPath(),t.arc(e.x,e.y,e.outerRadius,a,i),t.arc(e.x,e.y,e.innerRadius,i,a,!0),t.closePath(),t.strokeStyle=e.borderColor,t.lineWidth=e.borderWidth,t.fillStyle=e.backgroundColor,t.fill(),t.lineJoin="bevel",e.borderWidth&&t.stroke()}})}},{}],36:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a=t.defaults.global;t.defaults.global.elements.line={tension:.4,backgroundColor:a.defaultColor,borderWidth:3,borderColor:a.defaultColor,borderCapStyle:"butt",borderDash:[],borderDashOffset:0,borderJoinStyle:"miter",capBezierPoints:!0,fill:!0},t.elements.Line=t.Element.extend({draw:function(){function t(t,e){var a=e._view;e._view.steppedLine===!0?(s.lineTo(a.x,t._view.y),s.lineTo(a.x,a.y)):0===e._view.tension?s.lineTo(a.x,a.y):s.bezierCurveTo(t._view.controlPointNextX,t._view.controlPointNextY,a.controlPointPreviousX,a.controlPointPreviousY,a.x,a.y)}var i=this,n=i._view,o=n.spanGaps,r=n.scaleZero,l=i._loop,s=i._chart.ctx;s.save();var d=i._children.slice(),u=-1;l&&d.length&&d.push(d[0]);var c,h,f,g;if(d.length&&n.fill){for(s.beginPath(),c=0;c<d.length;++c)h=d[c],f=e.previousItem(d,c),g=h._view,0===c?(l?s.moveTo(r.x,r.y):s.moveTo(g.x,r),g.skip||(u=c,s.lineTo(g.x,g.y))):(f=-1===u?f:d[u],g.skip?o||u!==c-1||(l?s.lineTo(r.x,r.y):s.lineTo(f._view.x,r)):(u!==c-1?o&&-1!==u?t(f,h):l?s.lineTo(g.x,g.y):(s.lineTo(g.x,r),s.lineTo(g.x,g.y)):t(f,h),u=c));l||-1===u||s.lineTo(d[u]._view.x,r),s.fillStyle=n.backgroundColor||a.defaultColor,s.closePath(),s.fill()}var p=a.elements.line;for(s.lineCap=n.borderCapStyle||p.borderCapStyle,s.setLineDash&&s.setLineDash(n.borderDash||p.borderDash),s.lineDashOffset=n.borderDashOffset||p.borderDashOffset,s.lineJoin=n.borderJoinStyle||p.borderJoinStyle,s.lineWidth=n.borderWidth||p.borderWidth,s.strokeStyle=n.borderColor||a.defaultColor,s.beginPath(),u=-1,c=0;c<d.length;++c)h=d[c],f=e.previousItem(d,c),g=h._view,0===c?g.skip||(s.moveTo(g.x,g.y),u=c):(f=-1===u?f:d[u],g.skip||(u!==c-1&&!o||-1===u?s.moveTo(g.x,g.y):t(f,h),u=c));s.stroke(),s.restore()}})}},{}],37:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a=t.defaults.global,i=a.defaultColor;a.elements.point={radius:3,pointStyle:"circle",backgroundColor:i,borderWidth:1,borderColor:i,hitRadius:1,hoverRadius:4,hoverBorderWidth:1},t.elements.Point=t.Element.extend({inRange:function(t,e){var a=this._view;return a?Math.pow(t-a.x,2)+Math.pow(e-a.y,2)<Math.pow(a.hitRadius+a.radius,2):!1},inLabelRange:function(t){var e=this._view;return e?Math.pow(t-e.x,2)<Math.pow(e.radius+e.hitRadius,2):!1},tooltipPosition:function(){var t=this._view;return{x:t.x,y:t.y,padding:t.radius+t.borderWidth}},draw:function(){var n=this._view,o=this._chart.ctx,r=n.pointStyle,l=n.radius,s=n.x,d=n.y;n.skip||(o.strokeStyle=n.borderColor||i,o.lineWidth=e.getValueOrDefault(n.borderWidth,a.elements.point.borderWidth),o.fillStyle=n.backgroundColor||i,t.canvasHelpers.drawPoint(o,r,l,s,d))}})}},{}],38:[function(t,e,a){"use strict";e.exports=function(t){var e=t.defaults.global;e.elements.rectangle={backgroundColor:e.defaultColor,borderWidth:0,borderColor:e.defaultColor,borderSkipped:"bottom"},t.elements.Rectangle=t.Element.extend({draw:function(){function t(t){return s[(u+t)%4]}var e=this._chart.ctx,a=this._view,i=a.width/2,n=a.x-i,o=a.x+i,r=a.base-(a.base-a.y),l=a.borderWidth/2;a.borderWidth&&(n+=l,o-=l,r+=l),e.beginPath(),e.fillStyle=a.backgroundColor,e.strokeStyle=a.borderColor,e.lineWidth=a.borderWidth;var s=[[n,a.base],[n,r],[o,r],[o,a.base]],d=["bottom","left","top","right"],u=d.indexOf(a.borderSkipped,0);-1===u&&(u=0),e.moveTo.apply(e,t(0));for(var c=1;4>c;c++)e.lineTo.apply(e,t(c));e.fill(),a.borderWidth&&e.stroke()},height:function(){var t=this._view;return t.base-t.y},inRange:function(t,e){var a=this._view;return a?a.y<a.base?t>=a.x-a.width/2&&t<=a.x+a.width/2&&e>=a.y&&e<=a.base:t>=a.x-a.width/2&&t<=a.x+a.width/2&&e>=a.base&&e<=a.y:!1},inLabelRange:function(t){var e=this._view;return e?t>=e.x-e.width/2&&t<=e.x+e.width/2:!1},tooltipPosition:function(){var t=this._view;return{x:t.x,y:t.y}}})}},{}],39:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a={position:"bottom"},i=t.Scale.extend({getLabels:function(){var t=this.chart.data;return(this.isHorizontal()?t.xLabels:t.yLabels)||t.labels},determineDataLimits:function(){var t=this,a=t.getLabels();t.minIndex=0,t.maxIndex=a.length-1;var i;void 0!==t.options.ticks.min&&(i=e.indexOf(a,t.options.ticks.min),t.minIndex=-1!==i?i:t.minIndex),void 0!==t.options.ticks.max&&(i=e.indexOf(a,t.options.ticks.max),t.maxIndex=-1!==i?i:t.maxIndex),t.min=a[t.minIndex],t.max=a[t.maxIndex]},buildTicks:function(){var t=this,e=t.getLabels();t.ticks=0===t.minIndex&&t.maxIndex===e.length-1?e:e.slice(t.minIndex,t.maxIndex+1)},getLabelForIndex:function(t,e){var a=this,i=a.chart.data,n=a.isHorizontal();return i.xLabels&&n||i.yLabels&&!n?a.getRightValue(i.datasets[e].data[t]):a.ticks[t]},getPixelForValue:function(t,e,a,i){var n=this,o=Math.max(n.maxIndex+1-n.minIndex-(n.options.gridLines.offsetGridLines?0:1),1);if(void 0!==t&&isNaN(e)){var r=n.getLabels(),l=r.indexOf(t);e=-1!==l?l:e}if(n.isHorizontal()){var s=n.width-(n.paddingLeft+n.paddingRight),d=s/o,u=d*(e-n.minIndex)+n.paddingLeft;return(n.options.gridLines.offsetGridLines&&i||n.maxIndex===n.minIndex&&i)&&(u+=d/2),n.left+Math.round(u)}var c=n.height-(n.paddingTop+n.paddingBottom),h=c/o,f=h*(e-n.minIndex)+n.paddingTop;return n.options.gridLines.offsetGridLines&&i&&(f+=h/2),n.top+Math.round(f)},getPixelForTick:function(t,e){return this.getPixelForValue(this.ticks[t],t+this.minIndex,null,e)},getValueForPixel:function(t){var e,a=this,i=Math.max(a.ticks.length-(a.options.gridLines.offsetGridLines?0:1),1),n=a.isHorizontal(),o=n?a.width-(a.paddingLeft+a.paddingRight):a.height-(a.paddingTop+a.paddingBottom),r=o/i;return t-=n?a.left:a.top,a.options.gridLines.offsetGridLines&&(t-=r/2),t-=n?a.paddingLeft:a.paddingTop,e=0>=t?0:Math.round(t/r)},getBasePixel:function(){return this.bottom}});t.scaleService.registerScaleType("category",i,a)}},{}],40:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a={position:"left",ticks:{callback:function(t,a,i){var n=i.length>3?i[2]-i[1]:i[1]-i[0];Math.abs(n)>1&&t!==Math.floor(t)&&(n=t-Math.floor(t));var o=e.log10(Math.abs(n)),r="";if(0!==t){var l=-1*Math.floor(o);l=Math.max(Math.min(l,20),0),r=t.toFixed(l)}else r="0";return r}}},i=t.LinearScaleBase.extend({determineDataLimits:function(){function t(t){return l?t.xAxisID===a.id:t.yAxisID===a.id}var a=this,i=a.options,n=a.chart,o=n.data,r=o.datasets,l=a.isHorizontal();if(a.min=null,a.max=null,i.stacked){var s={};e.each(r,function(o,r){var l=n.getDatasetMeta(r);void 0===s[l.type]&&(s[l.type]={positiveValues:[],negativeValues:[]});var d=s[l.type].positiveValues,u=s[l.type].negativeValues;n.isDatasetVisible(r)&&t(l)&&e.each(o.data,function(t,e){var n=+a.getRightValue(t);isNaN(n)||l.data[e].hidden||(d[e]=d[e]||0,u[e]=u[e]||0,i.relativePoints?d[e]=100:0>n?u[e]+=n:d[e]+=n)})}),e.each(s,function(t){var i=t.positiveValues.concat(t.negativeValues),n=e.min(i),o=e.max(i);a.min=null===a.min?n:Math.min(a.min,n),a.max=null===a.max?o:Math.max(a.max,o)})}else e.each(r,function(i,o){var r=n.getDatasetMeta(o);n.isDatasetVisible(o)&&t(r)&&e.each(i.data,function(t,e){var i=+a.getRightValue(t);isNaN(i)||r.data[e].hidden||(null===a.min?a.min=i:i<a.min&&(a.min=i),null===a.max?a.max=i:i>a.max&&(a.max=i))})});this.handleTickRangeOptions()},getTickLimit:function(){var a,i=this,n=i.options.ticks;if(i.isHorizontal())a=Math.min(n.maxTicksLimit?n.maxTicksLimit:11,Math.ceil(i.width/50));else{var o=e.getValueOrDefault(n.fontSize,t.defaults.global.defaultFontSize);a=Math.min(n.maxTicksLimit?n.maxTicksLimit:11,Math.ceil(i.height/(2*o)))}return a},handleDirectionalChanges:function(){this.isHorizontal()||this.ticks.reverse()},getLabelForIndex:function(t,e){return+this.getRightValue(this.chart.data.datasets[e].data[t])},getPixelForValue:function(t){var e,a,i=this,n=i.paddingLeft,o=i.paddingBottom,r=i.start,l=+i.getRightValue(t),s=i.end-r;return i.isHorizontal()?(a=i.width-(n+i.paddingRight),e=i.left+a/s*(l-r),Math.round(e+n)):(a=i.height-(i.paddingTop+o),e=i.bottom-o-a/s*(l-r),Math.round(e))},getValueForPixel:function(t){var e=this,a=e.isHorizontal(),i=e.paddingLeft,n=e.paddingBottom,o=a?e.width-(i+e.paddingRight):e.height-(e.paddingTop+n),r=(a?t-e.left-i:e.bottom-n-t)/o;return e.start+(e.end-e.start)*r},getPixelForTick:function(t){return this.getPixelForValue(this.ticksAsNumbers[t])}});t.scaleService.registerScaleType("linear",i,a)}},{}],41:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a=e.noop;t.LinearScaleBase=t.Scale.extend({handleTickRangeOptions:function(){var t=this,a=t.options,i=a.ticks;if(i.beginAtZero){var n=e.sign(t.min),o=e.sign(t.max);0>n&&0>o?t.max=0:n>0&&o>0&&(t.min=0)}void 0!==i.min?t.min=i.min:void 0!==i.suggestedMin&&(t.min=Math.min(t.min,i.suggestedMin)),void 0!==i.max?t.max=i.max:void 0!==i.suggestedMax&&(t.max=Math.max(t.max,i.suggestedMax)),t.min===t.max&&(t.max++,i.beginAtZero||t.min--)},getTickLimit:a,handleDirectionalChanges:a,buildTicks:function(){var t=this,a=t.options,i=t.ticks=[],n=a.ticks,o=e.getValueOrDefault,r=t.getTickLimit();r=Math.max(2,r);var l,s=n.fixedStepSize&&n.fixedStepSize>0||n.stepSize&&n.stepSize>0;if(s)l=o(n.fixedStepSize,n.stepSize);else{var d=e.niceNum(t.max-t.min,!1);l=e.niceNum(d/(r-1),!0)}var u=Math.floor(t.min/l)*l,c=Math.ceil(t.max/l)*l,h=(c-u)/l;h=e.almostEquals(h,Math.round(h),l/1e3)?Math.round(h):Math.ceil(h),i.push(void 0!==n.min?n.min:u);for(var f=1;h>f;++f)i.push(u+f*l);i.push(void 0!==n.max?n.max:c),t.handleDirectionalChanges(),t.max=e.max(i),t.min=e.min(i),n.reverse?(i.reverse(),t.start=t.max,t.end=t.min):(t.start=t.min,t.end=t.max)},convertTicksToLabels:function(){var e=this;e.ticksAsNumbers=e.ticks.slice(),e.zeroLineIndex=e.ticks.indexOf(0),t.Scale.prototype.convertTicksToLabels.call(e)}})}},{}],42:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a={position:"left",ticks:{callback:function(t,a,i){var n=t/Math.pow(10,Math.floor(e.log10(t)));return 0===t?"0":1===n||2===n||5===n||0===a||a===i.length-1?t.toExponential():""}}},i=t.Scale.extend({determineDataLimits:function(){function t(t){return d?t.xAxisID===a.id:t.yAxisID===a.id}var a=this,i=a.options,n=i.ticks,o=a.chart,r=o.data,l=r.datasets,s=e.getValueOrDefault,d=a.isHorizontal();if(a.min=null,a.max=null,a.minNotZero=null,i.stacked){var u={};e.each(l,function(n,r){var l=o.getDatasetMeta(r);o.isDatasetVisible(r)&&t(l)&&(void 0===u[l.type]&&(u[l.type]=[]),e.each(n.data,function(t,e){var n=u[l.type],o=+a.getRightValue(t);isNaN(o)||l.data[e].hidden||(n[e]=n[e]||0,i.relativePoints?n[e]=100:n[e]+=o)}))}),e.each(u,function(t){var i=e.min(t),n=e.max(t);a.min=null===a.min?i:Math.min(a.min,i),
a.max=null===a.max?n:Math.max(a.max,n)})}else e.each(l,function(i,n){var r=o.getDatasetMeta(n);o.isDatasetVisible(n)&&t(r)&&e.each(i.data,function(t,e){var i=+a.getRightValue(t);isNaN(i)||r.data[e].hidden||(null===a.min?a.min=i:i<a.min&&(a.min=i),null===a.max?a.max=i:i>a.max&&(a.max=i),0!==i&&(null===a.minNotZero||i<a.minNotZero)&&(a.minNotZero=i))})});a.min=s(n.min,a.min),a.max=s(n.max,a.max),a.min===a.max&&(0!==a.min&&null!==a.min?(a.min=Math.pow(10,Math.floor(e.log10(a.min))-1),a.max=Math.pow(10,Math.floor(e.log10(a.max))+1)):(a.min=1,a.max=10))},buildTicks:function(){for(var t=this,a=t.options,i=a.ticks,n=e.getValueOrDefault,o=t.ticks=[],r=n(i.min,Math.pow(10,Math.floor(e.log10(t.min))));r<t.max;){o.push(r);var l,s;0===r?(l=Math.floor(e.log10(t.minNotZero)),s=Math.round(t.minNotZero/Math.pow(10,l))):(l=Math.floor(e.log10(r)),s=Math.floor(r/Math.pow(10,l))+1),10===s&&(s=1,++l),r=s*Math.pow(10,l)}var d=n(i.max,r);o.push(d),t.isHorizontal()||o.reverse(),t.max=e.max(o),t.min=e.min(o),i.reverse?(o.reverse(),t.start=t.max,t.end=t.min):(t.start=t.min,t.end=t.max)},convertTicksToLabels:function(){this.tickValues=this.ticks.slice(),t.Scale.prototype.convertTicksToLabels.call(this)},getLabelForIndex:function(t,e){return+this.getRightValue(this.chart.data.datasets[e].data[t])},getPixelForTick:function(t){return this.getPixelForValue(this.tickValues[t])},getPixelForValue:function(t){var a,i,n,o=this,r=o.start,l=+o.getRightValue(t),s=o.paddingTop,d=o.paddingBottom,u=o.paddingLeft,c=o.options,h=c.ticks;return o.isHorizontal()?(n=e.log10(o.end)-e.log10(r),0===l?i=o.left+u:(a=o.width-(u+o.paddingRight),i=o.left+a/n*(e.log10(l)-e.log10(r)),i+=u)):(a=o.height-(s+d),0!==r||h.reverse?0===o.end&&h.reverse?(n=e.log10(o.start)-e.log10(o.minNotZero),i=l===o.end?o.top+s:l===o.minNotZero?o.top+s+.02*a:o.top+s+.02*a+.98*a/n*(e.log10(l)-e.log10(o.minNotZero))):(n=e.log10(o.end)-e.log10(r),a=o.height-(s+d),i=o.bottom-d-a/n*(e.log10(l)-e.log10(r))):(n=e.log10(o.end)-e.log10(o.minNotZero),i=l===r?o.bottom-d:l===o.minNotZero?o.bottom-d-.02*a:o.bottom-d-.02*a-.98*a/n*(e.log10(l)-e.log10(o.minNotZero)))),i},getValueForPixel:function(t){var a,i,n=this,o=e.log10(n.end)-e.log10(n.start);return n.isHorizontal()?(i=n.width-(n.paddingLeft+n.paddingRight),a=n.start*Math.pow(10,(t-n.left-n.paddingLeft)*o/i)):(i=n.height-(n.paddingTop+n.paddingBottom),a=Math.pow(10,(n.bottom-n.paddingBottom-t)*o/i)/n.start),a}});t.scaleService.registerScaleType("logarithmic",i,a)}},{}],43:[function(t,e,a){"use strict";e.exports=function(t){var e=t.helpers,a=t.defaults.global,i={display:!0,animate:!0,lineArc:!1,position:"chartArea",angleLines:{display:!0,color:"rgba(0, 0, 0, 0.1)",lineWidth:1},ticks:{showLabelBackdrop:!0,backdropColor:"rgba(255,255,255,0.75)",backdropPaddingY:2,backdropPaddingX:2},pointLabels:{fontSize:10,callback:function(t){return t}}},n=t.LinearScaleBase.extend({getValueCount:function(){return this.chart.data.labels.length},setDimensions:function(){var t=this,i=t.options,n=i.ticks;t.width=t.maxWidth,t.height=t.maxHeight,t.xCenter=Math.round(t.width/2),t.yCenter=Math.round(t.height/2);var o=e.min([t.height,t.width]),r=e.getValueOrDefault(n.fontSize,a.defaultFontSize);t.drawingArea=i.display?o/2-(r/2+n.backdropPaddingY):o/2},determineDataLimits:function(){var t=this,a=t.chart;t.min=null,t.max=null,e.each(a.data.datasets,function(i,n){if(a.isDatasetVisible(n)){var o=a.getDatasetMeta(n);e.each(i.data,function(e,a){var i=+t.getRightValue(e);isNaN(i)||o.data[a].hidden||(null===t.min?t.min=i:i<t.min&&(t.min=i),null===t.max?t.max=i:i>t.max&&(t.max=i))})}}),t.handleTickRangeOptions()},getTickLimit:function(){var t=this.options.ticks,i=e.getValueOrDefault(t.fontSize,a.defaultFontSize);return Math.min(t.maxTicksLimit?t.maxTicksLimit:11,Math.ceil(this.drawingArea/(1.5*i)))},convertTicksToLabels:function(){var e=this;t.LinearScaleBase.prototype.convertTicksToLabels.call(e),e.pointLabels=e.chart.data.labels.map(e.options.pointLabels.callback,e)},getLabelForIndex:function(t,e){return+this.getRightValue(this.chart.data.datasets[e].data[t])},fit:function(){var t,i,n,o,r,l,s,d,u,c,h,f,g=this.options.pointLabels,p=e.getValueOrDefault(g.fontSize,a.defaultFontSize),m=e.getValueOrDefault(g.fontStyle,a.defaultFontStyle),b=e.getValueOrDefault(g.fontFamily,a.defaultFontFamily),v=e.fontString(p,m,b),x=e.min([this.height/2-p-5,this.width/2]),y=this.width,k=0;for(this.ctx.font=v,i=0;i<this.getValueCount();i++){t=this.getPointPosition(i,x),n=this.ctx.measureText(this.pointLabels[i]?this.pointLabels[i]:"").width+5;var S=this.getIndexAngle(i)+Math.PI/2,w=360*S/(2*Math.PI)%360;0===w||180===w?(o=n/2,t.x+o>y&&(y=t.x+o,r=i),t.x-o<k&&(k=t.x-o,s=i)):180>w?t.x+n>y&&(y=t.x+n,r=i):t.x-n<k&&(k=t.x-n,s=i)}u=k,c=Math.ceil(y-this.width),l=this.getIndexAngle(r),d=this.getIndexAngle(s),h=c/Math.sin(l+Math.PI/2),f=u/Math.sin(d+Math.PI/2),h=e.isNumber(h)?h:0,f=e.isNumber(f)?f:0,this.drawingArea=Math.round(x-(f+h)/2),this.setCenterPoint(f,h)},setCenterPoint:function(t,e){var a=this,i=a.width-e-a.drawingArea,n=t+a.drawingArea;a.xCenter=Math.round((n+i)/2+a.left),a.yCenter=Math.round(a.height/2+a.top)},getIndexAngle:function(t){var e=2*Math.PI/this.getValueCount(),a=this.chart.options&&this.chart.options.startAngle?this.chart.options.startAngle:0,i=a*Math.PI*2/360;return t*e-Math.PI/2+i},getDistanceFromCenterForValue:function(t){var e=this;if(null===t)return 0;var a=e.drawingArea/(e.max-e.min);return e.options.reverse?(e.max-t)*a:(t-e.min)*a},getPointPosition:function(t,e){var a=this,i=a.getIndexAngle(t);return{x:Math.round(Math.cos(i)*e)+a.xCenter,y:Math.round(Math.sin(i)*e)+a.yCenter}},getPointPositionForValue:function(t,e){return this.getPointPosition(t,this.getDistanceFromCenterForValue(e))},getBasePosition:function(){var t=this,e=t.min,a=t.max;return t.getPointPositionForValue(0,t.beginAtZero?0:0>e&&0>a?a:e>0&&a>0?e:0)},draw:function(){var t=this,i=t.options,n=i.gridLines,o=i.ticks,r=i.angleLines,l=i.pointLabels,s=e.getValueOrDefault;if(i.display){var d=t.ctx,u=s(o.fontSize,a.defaultFontSize),c=s(o.fontStyle,a.defaultFontStyle),h=s(o.fontFamily,a.defaultFontFamily),f=e.fontString(u,c,h);if(e.each(t.ticks,function(r,l){if(l>0||i.reverse){var c=t.getDistanceFromCenterForValue(t.ticksAsNumbers[l]),h=t.yCenter-c;if(n.display&&0!==l)if(d.strokeStyle=e.getValueAtIndexOrDefault(n.color,l-1),d.lineWidth=e.getValueAtIndexOrDefault(n.lineWidth,l-1),i.lineArc)d.beginPath(),d.arc(t.xCenter,t.yCenter,c,0,2*Math.PI),d.closePath(),d.stroke();else{d.beginPath();for(var g=0;g<t.getValueCount();g++){var p=t.getPointPosition(g,c);0===g?d.moveTo(p.x,p.y):d.lineTo(p.x,p.y)}d.closePath(),d.stroke()}if(o.display){var m=s(o.fontColor,a.defaultFontColor);if(d.font=f,o.showLabelBackdrop){var b=d.measureText(r).width;d.fillStyle=o.backdropColor,d.fillRect(t.xCenter-b/2-o.backdropPaddingX,h-u/2-o.backdropPaddingY,b+2*o.backdropPaddingX,u+2*o.backdropPaddingY)}d.textAlign="center",d.textBaseline="middle",d.fillStyle=m,d.fillText(r,t.xCenter,h)}}}),!i.lineArc){d.lineWidth=r.lineWidth,d.strokeStyle=r.color;for(var g=t.getDistanceFromCenterForValue(i.reverse?t.min:t.max),p=s(l.fontSize,a.defaultFontSize),m=s(l.fontStyle,a.defaultFontStyle),b=s(l.fontFamily,a.defaultFontFamily),v=e.fontString(p,m,b),x=t.getValueCount()-1;x>=0;x--){if(r.display){var y=t.getPointPosition(x,g);d.beginPath(),d.moveTo(t.xCenter,t.yCenter),d.lineTo(y.x,y.y),d.stroke(),d.closePath()}var k=t.getPointPosition(x,g+5),S=s(l.fontColor,a.defaultFontColor);d.font=v,d.fillStyle=S;var w=t.pointLabels,C=this.getIndexAngle(x)+Math.PI/2,M=360*C/(2*Math.PI)%360;0===M||180===M?d.textAlign="center":180>M?d.textAlign="left":d.textAlign="right",90===M||270===M?d.textBaseline="middle":M>270||90>M?d.textBaseline="bottom":d.textBaseline="top",d.fillText(w[x]?w[x]:"",k.x,k.y)}}}}});t.scaleService.registerScaleType("radialLinear",n,i)}},{}],44:[function(t,e,a){"use strict";var i=t(1);i="function"==typeof i?i:window.moment,e.exports=function(t){var e=t.helpers,a={units:[{name:"millisecond",steps:[1,2,5,10,20,50,100,250,500]},{name:"second",steps:[1,2,5,10,30]},{name:"minute",steps:[1,2,5,10,30]},{name:"hour",steps:[1,2,3,6,12]},{name:"day",steps:[1,2,5]},{name:"week",maxStep:4},{name:"month",maxStep:3},{name:"quarter",maxStep:4},{name:"year",maxStep:!1}]},n={position:"bottom",time:{parser:!1,format:!1,unit:!1,round:!1,displayFormat:!1,isoWeekday:!1,minUnit:"millisecond",displayFormats:{millisecond:"h:mm:ss.SSS a",second:"h:mm:ss a",minute:"h:mm:ss a",hour:"MMM D, hA",day:"ll",week:"ll",month:"MMM YYYY",quarter:"[Q]Q - YYYY",year:"YYYY"}},ticks:{autoSkip:!1}},o=t.Scale.extend({initialize:function(){if(!i)throw new Error("Chart.js - Moment.js could not be found! You must include it before Chart.js to use the time scale. Download at https://momentjs.com");t.Scale.prototype.initialize.call(this)},getLabelMoment:function(t,e){return null===t||null===e?null:"undefined"!=typeof this.labelMoments[t]?this.labelMoments[t][e]:null},getLabelDiff:function(t,e){var a=this;return null===t||null===e?null:(void 0===a.labelDiffs&&a.buildLabelDiffs(),"undefined"!=typeof a.labelDiffs[t]?a.labelDiffs[t][e]:null)},getMomentStartOf:function(t){var e=this;return"week"===e.options.time.unit&&e.options.time.isoWeekday!==!1?t.clone().startOf("isoWeek").isoWeekday(e.options.time.isoWeekday):t.clone().startOf(e.tickUnit)},determineDataLimits:function(){var t=this;t.labelMoments=[];var a=[];t.chart.data.labels&&t.chart.data.labels.length>0?(e.each(t.chart.data.labels,function(e){var i=t.parseTime(e);i.isValid()&&(t.options.time.round&&i.startOf(t.options.time.round),a.push(i))},t),t.firstTick=i.min.call(t,a),t.lastTick=i.max.call(t,a)):(t.firstTick=null,t.lastTick=null),e.each(t.chart.data.datasets,function(n,o){var r=[],l=t.chart.isDatasetVisible(o);"object"==typeof n.data[0]&&null!==n.data[0]?e.each(n.data,function(e){var a=t.parseTime(t.getRightValue(e));a.isValid()&&(t.options.time.round&&a.startOf(t.options.time.round),r.push(a),l&&(t.firstTick=null!==t.firstTick?i.min(t.firstTick,a):a,t.lastTick=null!==t.lastTick?i.max(t.lastTick,a):a))},t):r=a,t.labelMoments.push(r)},t),t.options.time.min&&(t.firstTick=t.parseTime(t.options.time.min)),t.options.time.max&&(t.lastTick=t.parseTime(t.options.time.max)),t.firstTick=(t.firstTick||i()).clone(),t.lastTick=(t.lastTick||i()).clone()},buildLabelDiffs:function(){var t=this;t.labelDiffs=[];var a=[];t.chart.data.labels&&t.chart.data.labels.length>0&&e.each(t.chart.data.labels,function(e){var i=t.parseTime(e);i.isValid()&&(t.options.time.round&&i.startOf(t.options.time.round),a.push(i.diff(t.firstTick,t.tickUnit,!0)))},t),e.each(t.chart.data.datasets,function(i){var n=[];"object"==typeof i.data[0]&&null!==i.data[0]?e.each(i.data,function(e){var a=t.parseTime(t.getRightValue(e));a.isValid()&&(t.options.time.round&&a.startOf(t.options.time.round),n.push(a.diff(t.firstTick,t.tickUnit,!0)))},t):n=a,t.labelDiffs.push(n)},t)},buildTicks:function(){var i=this;i.ctx.save();var n=e.getValueOrDefault(i.options.ticks.fontSize,t.defaults.global.defaultFontSize),o=e.getValueOrDefault(i.options.ticks.fontStyle,t.defaults.global.defaultFontStyle),r=e.getValueOrDefault(i.options.ticks.fontFamily,t.defaults.global.defaultFontFamily),l=e.fontString(n,o,r);if(i.ctx.font=l,i.ticks=[],i.unitScale=1,i.scaleSizeInUnits=0,i.options.time.unit)i.tickUnit=i.options.time.unit||"day",i.displayFormat=i.options.time.displayFormats[i.tickUnit],i.scaleSizeInUnits=i.lastTick.diff(i.firstTick,i.tickUnit,!0),i.unitScale=e.getValueOrDefault(i.options.time.unitStepSize,1);else{var s=i.isHorizontal()?i.width-(i.paddingLeft+i.paddingRight):i.height-(i.paddingTop+i.paddingBottom),d=i.tickFormatFunction(i.firstTick,0,[]),u=i.ctx.measureText(d).width,c=Math.cos(e.toRadians(i.options.ticks.maxRotation)),h=Math.sin(e.toRadians(i.options.ticks.maxRotation));u=u*c+n*h;var f=s/u;i.tickUnit=i.options.time.minUnit,i.scaleSizeInUnits=i.lastTick.diff(i.firstTick,i.tickUnit,!0),i.displayFormat=i.options.time.displayFormats[i.tickUnit];for(var g=0,p=a.units[g];g<a.units.length;){if(i.unitScale=1,e.isArray(p.steps)&&Math.ceil(i.scaleSizeInUnits/f)<e.max(p.steps)){for(var m=0;m<p.steps.length;++m)if(p.steps[m]>=Math.ceil(i.scaleSizeInUnits/f)){i.unitScale=e.getValueOrDefault(i.options.time.unitStepSize,p.steps[m]);break}break}if(p.maxStep===!1||Math.ceil(i.scaleSizeInUnits/f)<p.maxStep){i.unitScale=e.getValueOrDefault(i.options.time.unitStepSize,Math.ceil(i.scaleSizeInUnits/f));break}++g,p=a.units[g],i.tickUnit=p.name;var b=i.firstTick.diff(i.getMomentStartOf(i.firstTick),i.tickUnit,!0),v=i.getMomentStartOf(i.lastTick.clone().add(1,i.tickUnit)).diff(i.lastTick,i.tickUnit,!0);i.scaleSizeInUnits=i.lastTick.diff(i.firstTick,i.tickUnit,!0)+b+v,i.displayFormat=i.options.time.displayFormats[p.name]}}var x;if(i.options.time.min?x=i.getMomentStartOf(i.firstTick):(i.firstTick=i.getMomentStartOf(i.firstTick),x=i.firstTick),!i.options.time.max){var y=i.getMomentStartOf(i.lastTick),k=y.diff(i.lastTick,i.tickUnit,!0);0>k?i.lastTick=i.getMomentStartOf(i.lastTick.add(1,i.tickUnit)):k>=0&&(i.lastTick=y),i.scaleSizeInUnits=i.lastTick.diff(i.firstTick,i.tickUnit,!0)}i.options.time.displayFormat&&(i.displayFormat=i.options.time.displayFormat),i.ticks.push(i.firstTick.clone());for(var S=1;S<=i.scaleSizeInUnits;++S){var w=x.clone().add(S,i.tickUnit);if(i.options.time.max&&w.diff(i.lastTick,i.tickUnit,!0)>=0)break;S%i.unitScale===0&&i.ticks.push(w)}var C=i.ticks[i.ticks.length-1].diff(i.lastTick,i.tickUnit);(0!==C||0===i.scaleSizeInUnits)&&(i.options.time.max?(i.ticks.push(i.lastTick.clone()),i.scaleSizeInUnits=i.lastTick.diff(i.ticks[0],i.tickUnit,!0)):(i.ticks.push(i.lastTick.clone()),i.scaleSizeInUnits=i.lastTick.diff(i.firstTick,i.tickUnit,!0))),i.ctx.restore(),i.labelDiffs=void 0},getLabelForIndex:function(t,e){var a=this,i=a.chart.data.labels&&t<a.chart.data.labels.length?a.chart.data.labels[t]:"";return"object"==typeof a.chart.data.datasets[e].data[0]&&(i=a.getRightValue(a.chart.data.datasets[e].data[t])),a.options.time.tooltipFormat&&(i=a.parseTime(i).format(a.options.time.tooltipFormat)),i},tickFormatFunction:function(t,a,i){var n=t.format(this.displayFormat),o=this.options.ticks,r=e.getValueOrDefault(o.callback,o.userCallback);return r?r(n,a,i):n},convertTicksToLabels:function(){var t=this;t.tickMoments=t.ticks,t.ticks=t.ticks.map(t.tickFormatFunction,t)},getPixelForValue:function(t,e,a){var i=this,n=null;if(void 0!==e&&void 0!==a&&(n=i.getLabelDiff(a,e)),null===n&&(t&&t.isValid||(t=i.parseTime(i.getRightValue(t))),t&&t.isValid&&t.isValid()&&(n=t.diff(i.firstTick,i.tickUnit,!0))),null!==n){var o=0!==n?n/i.scaleSizeInUnits:n;if(i.isHorizontal()){var r=i.width-(i.paddingLeft+i.paddingRight),l=r*o+i.paddingLeft;return i.left+Math.round(l)}var s=i.height-(i.paddingTop+i.paddingBottom),d=s*o+i.paddingTop;return i.top+Math.round(d)}},getPixelForTick:function(t){return this.getPixelForValue(this.tickMoments[t],null,null)},getValueForPixel:function(t){var e=this,a=e.isHorizontal()?e.width-(e.paddingLeft+e.paddingRight):e.height-(e.paddingTop+e.paddingBottom),n=(t-(e.isHorizontal()?e.left+e.paddingLeft:e.top+e.paddingTop))/a;return n*=e.scaleSizeInUnits,e.firstTick.clone().add(i.duration(n,e.tickUnit).asSeconds(),"seconds")},parseTime:function(t){var e=this;return"string"==typeof e.options.time.parser?i(t,e.options.time.parser):"function"==typeof e.options.time.parser?e.options.time.parser(t):"function"==typeof t.getMonth||"number"==typeof t?i(t):t.isValid&&t.isValid()?t:"string"!=typeof e.options.time.format&&e.options.time.format.call?(console.warn("options.time.format is deprecated and replaced by options.time.parser. See http://nnnick.github.io/Chart.js/docs-v2/#scales-time-scale"),e.options.time.format(t)):i(t,e.options.time.format)}});t.scaleService.registerScaleType("time",o,n)}},{1:1}]},{},[7])(7)});
})();
(function () {
/*!
 * angular-chart.js - An angular.js wrapper for Chart.js
 * http://jtblin.github.io/angular-chart.js/
 * Version: 1.1.1
 *
 * Copyright 2016 Jerome Touffe-Blin
 * Released under the BSD-2-Clause license
 * https://github.com/jtblin/angular-chart.js/blob/master/LICENSE
 */
!function(t){"use strict";if("object"==typeof exports)module.exports=t("undefined"!=typeof angular?angular:require("angular"),"undefined"!=typeof Chart?Chart:require("chart.js"));else if("function"==typeof define&&define.amd)define(["angular","chart"],t);else{if("undefined"==typeof angular)throw new Error("AngularJS framework needs to be included, see https://angularjs.org/");if("undefined"==typeof Chart)throw new Error("Chart.js library needs to be included, see http://jtblin.github.io/angular-chart.js/");t(angular,Chart)}}(function(t,r){"use strict";function e(){var e={responsive:!0},a={Chart:r,getOptions:function(r){var a=r&&e[r]||{};return t.extend({},e,a)}};this.setOptions=function(r,n){n?e[r]=t.merge(e[r]||{},n):(n=r,e=t.merge(e,n)),t.merge(a.Chart.defaults,e)},this.$get=function(){return a}}function a(e,a){function o(t,r,a){var n=D(t,r);if(C(r)&&k(t,r,a,n)){var o=a[0],c=o.getContext("2d");r.chartGetColor=y(r);var i=b(t,r);F(r),r.chart=new e.Chart(c,{type:t,data:i,options:n}),r.$emit("chart-create",r.chart),A(o,r)}}function c(t,r){return!!(t&&r&&t.length&&r.length)&&(Array.isArray(t[0])?t.length===r.length&&t.every(function(t,e){return t.length===r[e].length}):r.reduce(i,0)>0&&t.length===r.length)}function i(t,r){return t+r}function u(r,e,a){var n={point:void 0,points:void 0};return function(o){var c=r.chart.getElementAtEvent||r.chart.getPointAtEvent,i=r.chart.getElementsAtEvent||r.chart.getPointsAtEvent;if(i){var u=i.call(r.chart,o),l=c?c.call(r.chart,o)[0]:void 0;a!==!1&&(t.equals(n.points,u)||t.equals(n.point,l))||(n.point=l,n.points=u,r[e](u,o,l))}}}function l(a,n){for(var o=t.copy(n.chartColors||e.getOptions(a).chartColors||r.defaults.global.colors),c=o.length<n.chartData.length;o.length<n.chartData.length;)o.push(n.chartGetColor());return c&&(n.chartColors=o),o.map(h)}function h(t){return"string"==typeof t&&"r"===t[0]?f(v(t)):"string"==typeof t&&"#"===t[0]?f(p(t.substr(1))):"object"==typeof t&&null!==t?t:s()}function s(){var t=[d(0,255),d(0,255),d(0,255)];return f(t)}function f(t){var r=t[3]||1;return t=t.slice(0,3),{backgroundColor:g(t,.2),pointBackgroundColor:g(t,r),pointHoverBackgroundColor:g(t,.8),borderColor:g(t,r),pointBorderColor:"#fff",pointHoverBorderColor:g(t,r)}}function d(t,r){return Math.floor(Math.random()*(r-t+1))+t}function g(t,r){return n?"rgb("+t.join(",")+")":"rgba("+t.concat(r).join(",")+")"}function p(t){var r=parseInt(t,16),e=r>>16&255,a=r>>8&255,n=255&r;return[e,a,n]}function v(t){var r=t.match(/^rgba?\(([\d,.]+)\)$/);if(!r)throw new Error("Cannot parse rgb value");return t=r[1].split(","),t.map(Number)}function C(t){return t.chartData&&t.chartData.length}function y(t){return"function"==typeof t.chartGetColor?t.chartGetColor:s}function b(t,r){var e=l(t,r);return Array.isArray(r.chartData[0])?m(r.chartLabels,r.chartData,r.chartSeries||[],e,r.chartDatasetOverride):w(r.chartLabels,r.chartData,e,r.chartDatasetOverride)}function m(r,e,a,n,o){return{labels:r,datasets:e.map(function(r,e){var c=t.extend({},n[e],{label:a[e],data:r});return o&&o.length>=e&&t.merge(c,o[e]),c})}}function w(r,e,a,n){var o={labels:r,datasets:[{data:e,backgroundColor:a.map(function(t){return t.pointBackgroundColor}),hoverBackgroundColor:a.map(function(t){return t.backgroundColor})}]};return n&&t.merge(o.datasets[0],n),o}function D(r,a){return t.extend({},e.getOptions(r),a.chartOptions)}function A(r,e){r.onclick=e.chartClick?u(e,"chartClick",!1):t.noop,r.onmousemove=e.chartHover?u(e,"chartHover",!0):t.noop}function B(t,r){Array.isArray(r.chartData[0])?r.chart.data.datasets.forEach(function(r,e){r.data=t[e]}):r.chart.data.datasets[0].data=t,r.chart.update(),r.$emit("chart-update",r.chart)}function $(t){return!t||Array.isArray(t)&&!t.length||"object"==typeof t&&!Object.keys(t).length}function k(t,r,e,n){return!n.responsive||0!==e[0].clientHeight||(a(function(){o(t,r,e)},50,!1),!1)}function F(t){t.chart&&(t.chart.destroy(),t.$emit("chart-destroy",t.chart))}return function(r){return{restrict:"CA",scope:{chartGetColor:"=?",chartType:"=",chartData:"=?",chartLabels:"=?",chartOptions:"=?",chartSeries:"=?",chartColors:"=?",chartClick:"=?",chartHover:"=?",chartDatasetOverride:"=?"},link:function(e,a){function i(t,n){if(!t||!t.length||Array.isArray(t[0])&&!t[0].length)return void F(e);var i=r||e.chartType;if(i)return e.chart&&c(t,n)?B(t,e):void o(i,e,a)}function u(n,c){if(!$(n)&&!t.equals(n,c)){var i=r||e.chartType;i&&o(i,e,a)}}function l(r,n){$(r)||t.equals(r,n)||o(r,e,a)}n&&window.G_vmlCanvasManager.initElement(a[0]),e.$watch("chartData",i,!0),e.$watch("chartSeries",u,!0),e.$watch("chartLabels",u,!0),e.$watch("chartOptions",u,!0),e.$watch("chartColors",u,!0),e.$watch("chartDatasetOverride",u,!0),e.$watch("chartType",l,!1),e.$on("$destroy",function(){F(e)}),e.$on("$resize",function(){e.chart&&e.chart.resize()})}}}}r.defaults.global.multiTooltipTemplate="<%if (datasetLabel){%><%=datasetLabel%>: <%}%><%= value %>",r.defaults.global.tooltips.mode="label",r.defaults.global.elements.line.borderWidth=2,r.defaults.global.elements.rectangle.borderWidth=2,r.defaults.global.legend.display=!1,r.defaults.global.colors=["#97BBCD","#DCDCDC","#F7464A","#46BFBD","#FDB45C","#949FB1","#4D5360"];var n="object"==typeof window.G_vmlCanvasManager&&null!==window.G_vmlCanvasManager&&"function"==typeof window.G_vmlCanvasManager.initElement;return n&&(r.defaults.global.animation=!1),t.module("chart.js",[]).provider("ChartJs",e).factory("ChartJsFactory",["ChartJs","$timeout",a]).directive("chartBase",["ChartJsFactory",function(t){return new t}]).directive("chartLine",["ChartJsFactory",function(t){return new t("line")}]).directive("chartBar",["ChartJsFactory",function(t){return new t("bar")}]).directive("chartHorizontalBar",["ChartJsFactory",function(t){return new t("horizontalBar")}]).directive("chartRadar",["ChartJsFactory",function(t){return new t("radar")}]).directive("chartDoughnut",["ChartJsFactory",function(t){return new t("doughnut")}]).directive("chartPie",["ChartJsFactory",function(t){return new t("pie")}]).directive("chartPolarArea",["ChartJsFactory",function(t){return new t("polarArea")}]).directive("chartBubble",["ChartJsFactory",function(t){return new t("bubble")}]).name});
//# sourceMappingURL=angular-chart.min.js.map

})();
(function () {

/*! pako 2.1.0 https://github.com/nodeca/pako @license (MIT AND Zlib) */
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
  typeof define === 'function' && define.amd ? define(['exports'], factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.pako = {}));
})(this, (function (exports) { 'use strict';

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.

  /* eslint-disable space-unary-ops */

  /* Public constants ==========================================================*/
  /* ===========================================================================*/

  //const Z_FILTERED          = 1;
  //const Z_HUFFMAN_ONLY      = 2;
  //const Z_RLE               = 3;
  var Z_FIXED$1 = 4;
  //const Z_DEFAULT_STRATEGY  = 0;

  /* Possible values of the data_type field (though see inflate()) */
  var Z_BINARY = 0;
  var Z_TEXT = 1;
  //const Z_ASCII             = 1; // = Z_TEXT
  var Z_UNKNOWN$1 = 2;

  /*============================================================================*/

  function zero$1(buf) {
    var len = buf.length;
    while (--len >= 0) {
      buf[len] = 0;
    }
  }

  // From zutil.h

  var STORED_BLOCK = 0;
  var STATIC_TREES = 1;
  var DYN_TREES = 2;
  /* The three kinds of block type */

  var MIN_MATCH$1 = 3;
  var MAX_MATCH$1 = 258;
  /* The minimum and maximum match lengths */

  // From deflate.h
  /* ===========================================================================
   * Internal compression state.
   */

  var LENGTH_CODES$1 = 29;
  /* number of length codes, not counting the special END_BLOCK code */

  var LITERALS$1 = 256;
  /* number of literal bytes 0..255 */

  var L_CODES$1 = LITERALS$1 + 1 + LENGTH_CODES$1;
  /* number of Literal or Length codes, including the END_BLOCK code */

  var D_CODES$1 = 30;
  /* number of distance codes */

  var BL_CODES$1 = 19;
  /* number of codes used to transfer the bit lengths */

  var HEAP_SIZE$1 = 2 * L_CODES$1 + 1;
  /* maximum heap size */

  var MAX_BITS$1 = 15;
  /* All codes must not exceed MAX_BITS bits */

  var Buf_size = 16;
  /* size of bit buffer in bi_buf */

  /* ===========================================================================
   * Constants
   */

  var MAX_BL_BITS = 7;
  /* Bit length codes must not exceed MAX_BL_BITS bits */

  var END_BLOCK = 256;
  /* end of block literal code */

  var REP_3_6 = 16;
  /* repeat previous bit length 3-6 times (2 bits of repeat count) */

  var REPZ_3_10 = 17;
  /* repeat a zero length 3-10 times  (3 bits of repeat count) */

  var REPZ_11_138 = 18;
  /* repeat a zero length 11-138 times  (7 bits of repeat count) */

  /* eslint-disable comma-spacing,array-bracket-spacing */
  var extra_lbits = /* extra bits for each length code */
  new Uint8Array([0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0]);
  var extra_dbits = /* extra bits for each distance code */
  new Uint8Array([0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13]);
  var extra_blbits = /* extra bits for each bit length code */
  new Uint8Array([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2, 3, 7]);
  var bl_order = new Uint8Array([16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15]);
  /* eslint-enable comma-spacing,array-bracket-spacing */

  /* The lengths of the bit length codes are sent in order of decreasing
   * probability, to avoid transmitting the lengths for unused bit length codes.
   */

  /* ===========================================================================
   * Local data. These are initialized only once.
   */

  // We pre-fill arrays with 0 to avoid uninitialized gaps

  var DIST_CODE_LEN = 512; /* see definition of array dist_code below */

  // !!!! Use flat array instead of structure, Freq = i*2, Len = i*2+1
  var static_ltree = new Array((L_CODES$1 + 2) * 2);
  zero$1(static_ltree);
  /* The static literal tree. Since the bit lengths are imposed, there is no
   * need for the L_CODES extra codes used during heap construction. However
   * The codes 286 and 287 are needed to build a canonical tree (see _tr_init
   * below).
   */

  var static_dtree = new Array(D_CODES$1 * 2);
  zero$1(static_dtree);
  /* The static distance tree. (Actually a trivial tree since all codes use
   * 5 bits.)
   */

  var _dist_code = new Array(DIST_CODE_LEN);
  zero$1(_dist_code);
  /* Distance codes. The first 256 values correspond to the distances
   * 3 .. 258, the last 256 values correspond to the top 8 bits of
   * the 15 bit distances.
   */

  var _length_code = new Array(MAX_MATCH$1 - MIN_MATCH$1 + 1);
  zero$1(_length_code);
  /* length code for each normalized match length (0 == MIN_MATCH) */

  var base_length = new Array(LENGTH_CODES$1);
  zero$1(base_length);
  /* First normalized length for each code (0 = MIN_MATCH) */

  var base_dist = new Array(D_CODES$1);
  zero$1(base_dist);
  /* First normalized distance for each code (0 = distance of 1) */

  function StaticTreeDesc(static_tree, extra_bits, extra_base, elems, max_length) {
    this.static_tree = static_tree; /* static tree or NULL */
    this.extra_bits = extra_bits; /* extra bits for each code or NULL */
    this.extra_base = extra_base; /* base index for extra_bits */
    this.elems = elems; /* max number of elements in the tree */
    this.max_length = max_length; /* max bit length for the codes */

    // show if `static_tree` has data or dummy - needed for monomorphic objects
    this.has_stree = static_tree && static_tree.length;
  }
  var static_l_desc;
  var static_d_desc;
  var static_bl_desc;
  function TreeDesc(dyn_tree, stat_desc) {
    this.dyn_tree = dyn_tree; /* the dynamic tree */
    this.max_code = 0; /* largest code with non zero frequency */
    this.stat_desc = stat_desc; /* the corresponding static tree */
  }

  var d_code = function d_code(dist) {
    return dist < 256 ? _dist_code[dist] : _dist_code[256 + (dist >>> 7)];
  };

  /* ===========================================================================
   * Output a short LSB first on the stream.
   * IN assertion: there is enough room in pendingBuf.
   */
  var put_short = function put_short(s, w) {
    //    put_byte(s, (uch)((w) & 0xff));
    //    put_byte(s, (uch)((ush)(w) >> 8));
    s.pending_buf[s.pending++] = w & 0xff;
    s.pending_buf[s.pending++] = w >>> 8 & 0xff;
  };

  /* ===========================================================================
   * Send a value on a given number of bits.
   * IN assertion: length <= 16 and value fits in length bits.
   */
  var send_bits = function send_bits(s, value, length) {
    if (s.bi_valid > Buf_size - length) {
      s.bi_buf |= value << s.bi_valid & 0xffff;
      put_short(s, s.bi_buf);
      s.bi_buf = value >> Buf_size - s.bi_valid;
      s.bi_valid += length - Buf_size;
    } else {
      s.bi_buf |= value << s.bi_valid & 0xffff;
      s.bi_valid += length;
    }
  };
  var send_code = function send_code(s, c, tree) {
    send_bits(s, tree[c * 2] /*.Code*/, tree[c * 2 + 1] /*.Len*/);
  };

  /* ===========================================================================
   * Reverse the first len bits of a code, using straightforward code (a faster
   * method would use a table)
   * IN assertion: 1 <= len <= 15
   */
  var bi_reverse = function bi_reverse(code, len) {
    var res = 0;
    do {
      res |= code & 1;
      code >>>= 1;
      res <<= 1;
    } while (--len > 0);
    return res >>> 1;
  };

  /* ===========================================================================
   * Flush the bit buffer, keeping at most 7 bits in it.
   */
  var bi_flush = function bi_flush(s) {
    if (s.bi_valid === 16) {
      put_short(s, s.bi_buf);
      s.bi_buf = 0;
      s.bi_valid = 0;
    } else if (s.bi_valid >= 8) {
      s.pending_buf[s.pending++] = s.bi_buf & 0xff;
      s.bi_buf >>= 8;
      s.bi_valid -= 8;
    }
  };

  /* ===========================================================================
   * Compute the optimal bit lengths for a tree and update the total bit length
   * for the current block.
   * IN assertion: the fields freq and dad are set, heap[heap_max] and
   *    above are the tree nodes sorted by increasing frequency.
   * OUT assertions: the field len is set to the optimal bit length, the
   *     array bl_count contains the frequencies for each bit length.
   *     The length opt_len is updated; static_len is also updated if stree is
   *     not null.
   */
  var gen_bitlen = function gen_bitlen(s, desc) {
    //    deflate_state *s;
    //    tree_desc *desc;    /* the tree descriptor */

    var tree = desc.dyn_tree;
    var max_code = desc.max_code;
    var stree = desc.stat_desc.static_tree;
    var has_stree = desc.stat_desc.has_stree;
    var extra = desc.stat_desc.extra_bits;
    var base = desc.stat_desc.extra_base;
    var max_length = desc.stat_desc.max_length;
    var h; /* heap index */
    var n, m; /* iterate over the tree elements */
    var bits; /* bit length */
    var xbits; /* extra bits */
    var f; /* frequency */
    var overflow = 0; /* number of elements with bit length too large */

    for (bits = 0; bits <= MAX_BITS$1; bits++) {
      s.bl_count[bits] = 0;
    }

    /* In a first pass, compute the optimal bit lengths (which may
     * overflow in the case of the bit length tree).
     */
    tree[s.heap[s.heap_max] * 2 + 1] /*.Len*/ = 0; /* root of the heap */

    for (h = s.heap_max + 1; h < HEAP_SIZE$1; h++) {
      n = s.heap[h];
      bits = tree[tree[n * 2 + 1] /*.Dad*/ * 2 + 1] /*.Len*/ + 1;
      if (bits > max_length) {
        bits = max_length;
        overflow++;
      }
      tree[n * 2 + 1] /*.Len*/ = bits;
      /* We overwrite tree[n].Dad which is no longer needed */

      if (n > max_code) {
        continue;
      } /* not a leaf node */

      s.bl_count[bits]++;
      xbits = 0;
      if (n >= base) {
        xbits = extra[n - base];
      }
      f = tree[n * 2] /*.Freq*/;
      s.opt_len += f * (bits + xbits);
      if (has_stree) {
        s.static_len += f * (stree[n * 2 + 1] /*.Len*/ + xbits);
      }
    }
    if (overflow === 0) {
      return;
    }

    // Tracev((stderr,"\nbit length overflow\n"));
    /* This happens for example on obj2 and pic of the Calgary corpus */

    /* Find the first bit length which could increase: */
    do {
      bits = max_length - 1;
      while (s.bl_count[bits] === 0) {
        bits--;
      }
      s.bl_count[bits]--; /* move one leaf down the tree */
      s.bl_count[bits + 1] += 2; /* move one overflow item as its brother */
      s.bl_count[max_length]--;
      /* The brother of the overflow item also moves one step up,
       * but this does not affect bl_count[max_length]
       */
      overflow -= 2;
    } while (overflow > 0);

    /* Now recompute all bit lengths, scanning in increasing frequency.
     * h is still equal to HEAP_SIZE. (It is simpler to reconstruct all
     * lengths instead of fixing only the wrong ones. This idea is taken
     * from 'ar' written by Haruhiko Okumura.)
     */
    for (bits = max_length; bits !== 0; bits--) {
      n = s.bl_count[bits];
      while (n !== 0) {
        m = s.heap[--h];
        if (m > max_code) {
          continue;
        }
        if (tree[m * 2 + 1] /*.Len*/ !== bits) {
          // Tracev((stderr,"code %d bits %d->%d\n", m, tree[m].Len, bits));
          s.opt_len += (bits - tree[m * 2 + 1] /*.Len*/) * tree[m * 2] /*.Freq*/;
          tree[m * 2 + 1] /*.Len*/ = bits;
        }
        n--;
      }
    }
  };

  /* ===========================================================================
   * Generate the codes for a given tree and bit counts (which need not be
   * optimal).
   * IN assertion: the array bl_count contains the bit length statistics for
   * the given tree and the field len is set for all tree elements.
   * OUT assertion: the field code is set for all tree elements of non
   *     zero code length.
   */
  var gen_codes = function gen_codes(tree, max_code, bl_count) {
    //    ct_data *tree;             /* the tree to decorate */
    //    int max_code;              /* largest code with non zero frequency */
    //    ushf *bl_count;            /* number of codes at each bit length */

    var next_code = new Array(MAX_BITS$1 + 1); /* next code value for each bit length */
    var code = 0; /* running code value */
    var bits; /* bit index */
    var n; /* code index */

    /* The distribution counts are first used to generate the code values
     * without bit reversal.
     */
    for (bits = 1; bits <= MAX_BITS$1; bits++) {
      code = code + bl_count[bits - 1] << 1;
      next_code[bits] = code;
    }
    /* Check that the bit counts in bl_count are consistent. The last code
     * must be all ones.
     */
    //Assert (code + bl_count[MAX_BITS]-1 == (1<<MAX_BITS)-1,
    //        "inconsistent bit counts");
    //Tracev((stderr,"\ngen_codes: max_code %d ", max_code));

    for (n = 0; n <= max_code; n++) {
      var len = tree[n * 2 + 1] /*.Len*/;
      if (len === 0) {
        continue;
      }
      /* Now reverse the bits */
      tree[n * 2] /*.Code*/ = bi_reverse(next_code[len]++, len);

      //Tracecv(tree != static_ltree, (stderr,"\nn %3d %c l %2d c %4x (%x) ",
      //     n, (isgraph(n) ? n : ' '), len, tree[n].Code, next_code[len]-1));
    }
  };

  /* ===========================================================================
   * Initialize the various 'constant' tables.
   */
  var tr_static_init = function tr_static_init() {
    var n; /* iterates over tree elements */
    var bits; /* bit counter */
    var length; /* length value */
    var code; /* code value */
    var dist; /* distance index */
    var bl_count = new Array(MAX_BITS$1 + 1);
    /* number of codes at each bit length for an optimal tree */

    // do check in _tr_init()
    //if (static_init_done) return;

    /* For some embedded targets, global variables are not initialized: */
    /*#ifdef NO_INIT_GLOBAL_POINTERS
      static_l_desc.static_tree = static_ltree;
      static_l_desc.extra_bits = extra_lbits;
      static_d_desc.static_tree = static_dtree;
      static_d_desc.extra_bits = extra_dbits;
      static_bl_desc.extra_bits = extra_blbits;
    #endif*/

    /* Initialize the mapping length (0..255) -> length code (0..28) */
    length = 0;
    for (code = 0; code < LENGTH_CODES$1 - 1; code++) {
      base_length[code] = length;
      for (n = 0; n < 1 << extra_lbits[code]; n++) {
        _length_code[length++] = code;
      }
    }
    //Assert (length == 256, "tr_static_init: length != 256");
    /* Note that the length 255 (match length 258) can be represented
     * in two different ways: code 284 + 5 bits or code 285, so we
     * overwrite length_code[255] to use the best encoding:
     */
    _length_code[length - 1] = code;

    /* Initialize the mapping dist (0..32K) -> dist code (0..29) */
    dist = 0;
    for (code = 0; code < 16; code++) {
      base_dist[code] = dist;
      for (n = 0; n < 1 << extra_dbits[code]; n++) {
        _dist_code[dist++] = code;
      }
    }
    //Assert (dist == 256, "tr_static_init: dist != 256");
    dist >>= 7; /* from now on, all distances are divided by 128 */
    for (; code < D_CODES$1; code++) {
      base_dist[code] = dist << 7;
      for (n = 0; n < 1 << extra_dbits[code] - 7; n++) {
        _dist_code[256 + dist++] = code;
      }
    }
    //Assert (dist == 256, "tr_static_init: 256+dist != 512");

    /* Construct the codes of the static literal tree */
    for (bits = 0; bits <= MAX_BITS$1; bits++) {
      bl_count[bits] = 0;
    }
    n = 0;
    while (n <= 143) {
      static_ltree[n * 2 + 1] /*.Len*/ = 8;
      n++;
      bl_count[8]++;
    }
    while (n <= 255) {
      static_ltree[n * 2 + 1] /*.Len*/ = 9;
      n++;
      bl_count[9]++;
    }
    while (n <= 279) {
      static_ltree[n * 2 + 1] /*.Len*/ = 7;
      n++;
      bl_count[7]++;
    }
    while (n <= 287) {
      static_ltree[n * 2 + 1] /*.Len*/ = 8;
      n++;
      bl_count[8]++;
    }
    /* Codes 286 and 287 do not exist, but we must include them in the
     * tree construction to get a canonical Huffman tree (longest code
     * all ones)
     */
    gen_codes(static_ltree, L_CODES$1 + 1, bl_count);

    /* The static distance tree is trivial: */
    for (n = 0; n < D_CODES$1; n++) {
      static_dtree[n * 2 + 1] /*.Len*/ = 5;
      static_dtree[n * 2] /*.Code*/ = bi_reverse(n, 5);
    }

    // Now data ready and we can init static trees
    static_l_desc = new StaticTreeDesc(static_ltree, extra_lbits, LITERALS$1 + 1, L_CODES$1, MAX_BITS$1);
    static_d_desc = new StaticTreeDesc(static_dtree, extra_dbits, 0, D_CODES$1, MAX_BITS$1);
    static_bl_desc = new StaticTreeDesc(new Array(0), extra_blbits, 0, BL_CODES$1, MAX_BL_BITS);

    //static_init_done = true;
  };

  /* ===========================================================================
   * Initialize a new block.
   */
  var init_block = function init_block(s) {
    var n; /* iterates over tree elements */

    /* Initialize the trees. */
    for (n = 0; n < L_CODES$1; n++) {
      s.dyn_ltree[n * 2] /*.Freq*/ = 0;
    }
    for (n = 0; n < D_CODES$1; n++) {
      s.dyn_dtree[n * 2] /*.Freq*/ = 0;
    }
    for (n = 0; n < BL_CODES$1; n++) {
      s.bl_tree[n * 2] /*.Freq*/ = 0;
    }
    s.dyn_ltree[END_BLOCK * 2] /*.Freq*/ = 1;
    s.opt_len = s.static_len = 0;
    s.sym_next = s.matches = 0;
  };

  /* ===========================================================================
   * Flush the bit buffer and align the output on a byte boundary
   */
  var bi_windup = function bi_windup(s) {
    if (s.bi_valid > 8) {
      put_short(s, s.bi_buf);
    } else if (s.bi_valid > 0) {
      //put_byte(s, (Byte)s->bi_buf);
      s.pending_buf[s.pending++] = s.bi_buf;
    }
    s.bi_buf = 0;
    s.bi_valid = 0;
  };

  /* ===========================================================================
   * Compares to subtrees, using the tree depth as tie breaker when
   * the subtrees have equal frequency. This minimizes the worst case length.
   */
  var smaller = function smaller(tree, n, m, depth) {
    var _n2 = n * 2;
    var _m2 = m * 2;
    return tree[_n2] /*.Freq*/ < tree[_m2] /*.Freq*/ || tree[_n2] /*.Freq*/ === tree[_m2] /*.Freq*/ && depth[n] <= depth[m];
  };

  /* ===========================================================================
   * Restore the heap property by moving down the tree starting at node k,
   * exchanging a node with the smallest of its two sons if necessary, stopping
   * when the heap property is re-established (each father smaller than its
   * two sons).
   */
  var pqdownheap = function pqdownheap(s, tree, k) {
    //    deflate_state *s;
    //    ct_data *tree;  /* the tree to restore */
    //    int k;               /* node to move down */

    var v = s.heap[k];
    var j = k << 1; /* left son of k */
    while (j <= s.heap_len) {
      /* Set j to the smallest of the two sons: */
      if (j < s.heap_len && smaller(tree, s.heap[j + 1], s.heap[j], s.depth)) {
        j++;
      }
      /* Exit if v is smaller than both sons */
      if (smaller(tree, v, s.heap[j], s.depth)) {
        break;
      }

      /* Exchange v with the smallest son */
      s.heap[k] = s.heap[j];
      k = j;

      /* And continue down the tree, setting j to the left son of k */
      j <<= 1;
    }
    s.heap[k] = v;
  };

  // inlined manually
  // const SMALLEST = 1;

  /* ===========================================================================
   * Send the block data compressed using the given Huffman trees
   */
  var compress_block = function compress_block(s, ltree, dtree) {
    //    deflate_state *s;
    //    const ct_data *ltree; /* literal tree */
    //    const ct_data *dtree; /* distance tree */

    var dist; /* distance of matched string */
    var lc; /* match length or unmatched char (if dist == 0) */
    var sx = 0; /* running index in sym_buf */
    var code; /* the code to send */
    var extra; /* number of extra bits to send */

    if (s.sym_next !== 0) {
      do {
        dist = s.pending_buf[s.sym_buf + sx++] & 0xff;
        dist += (s.pending_buf[s.sym_buf + sx++] & 0xff) << 8;
        lc = s.pending_buf[s.sym_buf + sx++];
        if (dist === 0) {
          send_code(s, lc, ltree); /* send a literal byte */
          //Tracecv(isgraph(lc), (stderr," '%c' ", lc));
        } else {
          /* Here, lc is the match length - MIN_MATCH */
          code = _length_code[lc];
          send_code(s, code + LITERALS$1 + 1, ltree); /* send the length code */
          extra = extra_lbits[code];
          if (extra !== 0) {
            lc -= base_length[code];
            send_bits(s, lc, extra); /* send the extra length bits */
          }

          dist--; /* dist is now the match distance - 1 */
          code = d_code(dist);
          //Assert (code < D_CODES, "bad d_code");

          send_code(s, code, dtree); /* send the distance code */
          extra = extra_dbits[code];
          if (extra !== 0) {
            dist -= base_dist[code];
            send_bits(s, dist, extra); /* send the extra distance bits */
          }
        } /* literal or match pair ? */

        /* Check that the overlay between pending_buf and sym_buf is ok: */
        //Assert(s->pending < s->lit_bufsize + sx, "pendingBuf overflow");
      } while (sx < s.sym_next);
    }
    send_code(s, END_BLOCK, ltree);
  };

  /* ===========================================================================
   * Construct one Huffman tree and assigns the code bit strings and lengths.
   * Update the total bit length for the current block.
   * IN assertion: the field freq is set for all tree elements.
   * OUT assertions: the fields len and code are set to the optimal bit length
   *     and corresponding code. The length opt_len is updated; static_len is
   *     also updated if stree is not null. The field max_code is set.
   */
  var build_tree = function build_tree(s, desc) {
    //    deflate_state *s;
    //    tree_desc *desc; /* the tree descriptor */

    var tree = desc.dyn_tree;
    var stree = desc.stat_desc.static_tree;
    var has_stree = desc.stat_desc.has_stree;
    var elems = desc.stat_desc.elems;
    var n, m; /* iterate over heap elements */
    var max_code = -1; /* largest code with non zero frequency */
    var node; /* new node being created */

    /* Construct the initial heap, with least frequent element in
     * heap[SMALLEST]. The sons of heap[n] are heap[2*n] and heap[2*n+1].
     * heap[0] is not used.
     */
    s.heap_len = 0;
    s.heap_max = HEAP_SIZE$1;
    for (n = 0; n < elems; n++) {
      if (tree[n * 2] /*.Freq*/ !== 0) {
        s.heap[++s.heap_len] = max_code = n;
        s.depth[n] = 0;
      } else {
        tree[n * 2 + 1] /*.Len*/ = 0;
      }
    }

    /* The pkzip format requires that at least one distance code exists,
     * and that at least one bit should be sent even if there is only one
     * possible code. So to avoid special checks later on we force at least
     * two codes of non zero frequency.
     */
    while (s.heap_len < 2) {
      node = s.heap[++s.heap_len] = max_code < 2 ? ++max_code : 0;
      tree[node * 2] /*.Freq*/ = 1;
      s.depth[node] = 0;
      s.opt_len--;
      if (has_stree) {
        s.static_len -= stree[node * 2 + 1] /*.Len*/;
      }
      /* node is 0 or 1 so it does not have extra bits */
    }

    desc.max_code = max_code;

    /* The elements heap[heap_len/2+1 .. heap_len] are leaves of the tree,
     * establish sub-heaps of increasing lengths:
     */
    for (n = s.heap_len >> 1 /*int /2*/; n >= 1; n--) {
      pqdownheap(s, tree, n);
    }

    /* Construct the Huffman tree by repeatedly combining the least two
     * frequent nodes.
     */
    node = elems; /* next internal node of the tree */
    do {
      //pqremove(s, tree, n);  /* n = node of least frequency */
      /*** pqremove ***/
      n = s.heap[1 /*SMALLEST*/];
      s.heap[1 /*SMALLEST*/] = s.heap[s.heap_len--];
      pqdownheap(s, tree, 1 /*SMALLEST*/);
      /***/

      m = s.heap[1 /*SMALLEST*/]; /* m = node of next least frequency */

      s.heap[--s.heap_max] = n; /* keep the nodes sorted by frequency */
      s.heap[--s.heap_max] = m;

      /* Create a new node father of n and m */
      tree[node * 2] /*.Freq*/ = tree[n * 2] /*.Freq*/ + tree[m * 2] /*.Freq*/;
      s.depth[node] = (s.depth[n] >= s.depth[m] ? s.depth[n] : s.depth[m]) + 1;
      tree[n * 2 + 1] /*.Dad*/ = tree[m * 2 + 1] /*.Dad*/ = node;

      /* and insert the new node in the heap */
      s.heap[1 /*SMALLEST*/] = node++;
      pqdownheap(s, tree, 1 /*SMALLEST*/);
    } while (s.heap_len >= 2);
    s.heap[--s.heap_max] = s.heap[1 /*SMALLEST*/];

    /* At this point, the fields freq and dad are set. We can now
     * generate the bit lengths.
     */
    gen_bitlen(s, desc);

    /* The field len is now set, we can generate the bit codes */
    gen_codes(tree, max_code, s.bl_count);
  };

  /* ===========================================================================
   * Scan a literal or distance tree to determine the frequencies of the codes
   * in the bit length tree.
   */
  var scan_tree = function scan_tree(s, tree, max_code) {
    //    deflate_state *s;
    //    ct_data *tree;   /* the tree to be scanned */
    //    int max_code;    /* and its largest code of non zero frequency */

    var n; /* iterates over all tree elements */
    var prevlen = -1; /* last emitted length */
    var curlen; /* length of current code */

    var nextlen = tree[0 * 2 + 1] /*.Len*/; /* length of next code */

    var count = 0; /* repeat count of the current code */
    var max_count = 7; /* max repeat count */
    var min_count = 4; /* min repeat count */

    if (nextlen === 0) {
      max_count = 138;
      min_count = 3;
    }
    tree[(max_code + 1) * 2 + 1] /*.Len*/ = 0xffff; /* guard */

    for (n = 0; n <= max_code; n++) {
      curlen = nextlen;
      nextlen = tree[(n + 1) * 2 + 1] /*.Len*/;

      if (++count < max_count && curlen === nextlen) {
        continue;
      } else if (count < min_count) {
        s.bl_tree[curlen * 2] /*.Freq*/ += count;
      } else if (curlen !== 0) {
        if (curlen !== prevlen) {
          s.bl_tree[curlen * 2] /*.Freq*/++;
        }
        s.bl_tree[REP_3_6 * 2] /*.Freq*/++;
      } else if (count <= 10) {
        s.bl_tree[REPZ_3_10 * 2] /*.Freq*/++;
      } else {
        s.bl_tree[REPZ_11_138 * 2] /*.Freq*/++;
      }

      count = 0;
      prevlen = curlen;
      if (nextlen === 0) {
        max_count = 138;
        min_count = 3;
      } else if (curlen === nextlen) {
        max_count = 6;
        min_count = 3;
      } else {
        max_count = 7;
        min_count = 4;
      }
    }
  };

  /* ===========================================================================
   * Send a literal or distance tree in compressed form, using the codes in
   * bl_tree.
   */
  var send_tree = function send_tree(s, tree, max_code) {
    //    deflate_state *s;
    //    ct_data *tree; /* the tree to be scanned */
    //    int max_code;       /* and its largest code of non zero frequency */

    var n; /* iterates over all tree elements */
    var prevlen = -1; /* last emitted length */
    var curlen; /* length of current code */

    var nextlen = tree[0 * 2 + 1] /*.Len*/; /* length of next code */

    var count = 0; /* repeat count of the current code */
    var max_count = 7; /* max repeat count */
    var min_count = 4; /* min repeat count */

    /* tree[max_code+1].Len = -1; */ /* guard already set */
    if (nextlen === 0) {
      max_count = 138;
      min_count = 3;
    }
    for (n = 0; n <= max_code; n++) {
      curlen = nextlen;
      nextlen = tree[(n + 1) * 2 + 1] /*.Len*/;

      if (++count < max_count && curlen === nextlen) {
        continue;
      } else if (count < min_count) {
        do {
          send_code(s, curlen, s.bl_tree);
        } while (--count !== 0);
      } else if (curlen !== 0) {
        if (curlen !== prevlen) {
          send_code(s, curlen, s.bl_tree);
          count--;
        }
        //Assert(count >= 3 && count <= 6, " 3_6?");
        send_code(s, REP_3_6, s.bl_tree);
        send_bits(s, count - 3, 2);
      } else if (count <= 10) {
        send_code(s, REPZ_3_10, s.bl_tree);
        send_bits(s, count - 3, 3);
      } else {
        send_code(s, REPZ_11_138, s.bl_tree);
        send_bits(s, count - 11, 7);
      }
      count = 0;
      prevlen = curlen;
      if (nextlen === 0) {
        max_count = 138;
        min_count = 3;
      } else if (curlen === nextlen) {
        max_count = 6;
        min_count = 3;
      } else {
        max_count = 7;
        min_count = 4;
      }
    }
  };

  /* ===========================================================================
   * Construct the Huffman tree for the bit lengths and return the index in
   * bl_order of the last bit length code to send.
   */
  var build_bl_tree = function build_bl_tree(s) {
    var max_blindex; /* index of last bit length code of non zero freq */

    /* Determine the bit length frequencies for literal and distance trees */
    scan_tree(s, s.dyn_ltree, s.l_desc.max_code);
    scan_tree(s, s.dyn_dtree, s.d_desc.max_code);

    /* Build the bit length tree: */
    build_tree(s, s.bl_desc);
    /* opt_len now includes the length of the tree representations, except
     * the lengths of the bit lengths codes and the 5+5+4 bits for the counts.
     */

    /* Determine the number of bit length codes to send. The pkzip format
     * requires that at least 4 bit length codes be sent. (appnote.txt says
     * 3 but the actual value used is 4.)
     */
    for (max_blindex = BL_CODES$1 - 1; max_blindex >= 3; max_blindex--) {
      if (s.bl_tree[bl_order[max_blindex] * 2 + 1] /*.Len*/ !== 0) {
        break;
      }
    }
    /* Update opt_len to include the bit length tree and counts */
    s.opt_len += 3 * (max_blindex + 1) + 5 + 5 + 4;
    //Tracev((stderr, "\ndyn trees: dyn %ld, stat %ld",
    //        s->opt_len, s->static_len));

    return max_blindex;
  };

  /* ===========================================================================
   * Send the header for a block using dynamic Huffman trees: the counts, the
   * lengths of the bit length codes, the literal tree and the distance tree.
   * IN assertion: lcodes >= 257, dcodes >= 1, blcodes >= 4.
   */
  var send_all_trees = function send_all_trees(s, lcodes, dcodes, blcodes) {
    //    deflate_state *s;
    //    int lcodes, dcodes, blcodes; /* number of codes for each tree */

    var rank; /* index in bl_order */

    //Assert (lcodes >= 257 && dcodes >= 1 && blcodes >= 4, "not enough codes");
    //Assert (lcodes <= L_CODES && dcodes <= D_CODES && blcodes <= BL_CODES,
    //        "too many codes");
    //Tracev((stderr, "\nbl counts: "));
    send_bits(s, lcodes - 257, 5); /* not +255 as stated in appnote.txt */
    send_bits(s, dcodes - 1, 5);
    send_bits(s, blcodes - 4, 4); /* not -3 as stated in appnote.txt */
    for (rank = 0; rank < blcodes; rank++) {
      //Tracev((stderr, "\nbl code %2d ", bl_order[rank]));
      send_bits(s, s.bl_tree[bl_order[rank] * 2 + 1] /*.Len*/, 3);
    }
    //Tracev((stderr, "\nbl tree: sent %ld", s->bits_sent));

    send_tree(s, s.dyn_ltree, lcodes - 1); /* literal tree */
    //Tracev((stderr, "\nlit tree: sent %ld", s->bits_sent));

    send_tree(s, s.dyn_dtree, dcodes - 1); /* distance tree */
    //Tracev((stderr, "\ndist tree: sent %ld", s->bits_sent));
  };

  /* ===========================================================================
   * Check if the data type is TEXT or BINARY, using the following algorithm:
   * - TEXT if the two conditions below are satisfied:
   *    a) There are no non-portable control characters belonging to the
   *       "block list" (0..6, 14..25, 28..31).
   *    b) There is at least one printable character belonging to the
   *       "allow list" (9 {TAB}, 10 {LF}, 13 {CR}, 32..255).
   * - BINARY otherwise.
   * - The following partially-portable control characters form a
   *   "gray list" that is ignored in this detection algorithm:
   *   (7 {BEL}, 8 {BS}, 11 {VT}, 12 {FF}, 26 {SUB}, 27 {ESC}).
   * IN assertion: the fields Freq of dyn_ltree are set.
   */
  var detect_data_type = function detect_data_type(s) {
    /* block_mask is the bit mask of block-listed bytes
     * set bits 0..6, 14..25, and 28..31
     * 0xf3ffc07f = binary 11110011111111111100000001111111
     */
    var block_mask = 0xf3ffc07f;
    var n;

    /* Check for non-textual ("block-listed") bytes. */
    for (n = 0; n <= 31; n++, block_mask >>>= 1) {
      if (block_mask & 1 && s.dyn_ltree[n * 2] /*.Freq*/ !== 0) {
        return Z_BINARY;
      }
    }

    /* Check for textual ("allow-listed") bytes. */
    if (s.dyn_ltree[9 * 2] /*.Freq*/ !== 0 || s.dyn_ltree[10 * 2] /*.Freq*/ !== 0 || s.dyn_ltree[13 * 2] /*.Freq*/ !== 0) {
      return Z_TEXT;
    }
    for (n = 32; n < LITERALS$1; n++) {
      if (s.dyn_ltree[n * 2] /*.Freq*/ !== 0) {
        return Z_TEXT;
      }
    }

    /* There are no "block-listed" or "allow-listed" bytes:
     * this stream either is empty or has tolerated ("gray-listed") bytes only.
     */
    return Z_BINARY;
  };
  var static_init_done = false;

  /* ===========================================================================
   * Initialize the tree data structures for a new zlib stream.
   */
  var _tr_init$1 = function _tr_init(s) {
    if (!static_init_done) {
      tr_static_init();
      static_init_done = true;
    }
    s.l_desc = new TreeDesc(s.dyn_ltree, static_l_desc);
    s.d_desc = new TreeDesc(s.dyn_dtree, static_d_desc);
    s.bl_desc = new TreeDesc(s.bl_tree, static_bl_desc);
    s.bi_buf = 0;
    s.bi_valid = 0;

    /* Initialize the first block of the first file: */
    init_block(s);
  };

  /* ===========================================================================
   * Send a stored block
   */
  var _tr_stored_block$1 = function _tr_stored_block(s, buf, stored_len, last) {
    //DeflateState *s;
    //charf *buf;       /* input block */
    //ulg stored_len;   /* length of input block */
    //int last;         /* one if this is the last block for a file */

    send_bits(s, (STORED_BLOCK << 1) + (last ? 1 : 0), 3); /* send block type */
    bi_windup(s); /* align on byte boundary */
    put_short(s, stored_len);
    put_short(s, ~stored_len);
    if (stored_len) {
      s.pending_buf.set(s.window.subarray(buf, buf + stored_len), s.pending);
    }
    s.pending += stored_len;
  };

  /* ===========================================================================
   * Send one empty static block to give enough lookahead for inflate.
   * This takes 10 bits, of which 7 may remain in the bit buffer.
   */
  var _tr_align$1 = function _tr_align(s) {
    send_bits(s, STATIC_TREES << 1, 3);
    send_code(s, END_BLOCK, static_ltree);
    bi_flush(s);
  };

  /* ===========================================================================
   * Determine the best encoding for the current block: dynamic trees, static
   * trees or store, and write out the encoded block.
   */
  var _tr_flush_block$1 = function _tr_flush_block(s, buf, stored_len, last) {
    //DeflateState *s;
    //charf *buf;       /* input block, or NULL if too old */
    //ulg stored_len;   /* length of input block */
    //int last;         /* one if this is the last block for a file */

    var opt_lenb, static_lenb; /* opt_len and static_len in bytes */
    var max_blindex = 0; /* index of last bit length code of non zero freq */

    /* Build the Huffman trees unless a stored block is forced */
    if (s.level > 0) {
      /* Check if the file is binary or text */
      if (s.strm.data_type === Z_UNKNOWN$1) {
        s.strm.data_type = detect_data_type(s);
      }

      /* Construct the literal and distance trees */
      build_tree(s, s.l_desc);
      // Tracev((stderr, "\nlit data: dyn %ld, stat %ld", s->opt_len,
      //        s->static_len));

      build_tree(s, s.d_desc);
      // Tracev((stderr, "\ndist data: dyn %ld, stat %ld", s->opt_len,
      //        s->static_len));
      /* At this point, opt_len and static_len are the total bit lengths of
       * the compressed block data, excluding the tree representations.
       */

      /* Build the bit length tree for the above two trees, and get the index
       * in bl_order of the last bit length code to send.
       */
      max_blindex = build_bl_tree(s);

      /* Determine the best encoding. Compute the block lengths in bytes. */
      opt_lenb = s.opt_len + 3 + 7 >>> 3;
      static_lenb = s.static_len + 3 + 7 >>> 3;

      // Tracev((stderr, "\nopt %lu(%lu) stat %lu(%lu) stored %lu lit %u ",
      //        opt_lenb, s->opt_len, static_lenb, s->static_len, stored_len,
      //        s->sym_next / 3));

      if (static_lenb <= opt_lenb) {
        opt_lenb = static_lenb;
      }
    } else {
      // Assert(buf != (char*)0, "lost buf");
      opt_lenb = static_lenb = stored_len + 5; /* force a stored block */
    }

    if (stored_len + 4 <= opt_lenb && buf !== -1) {
      /* 4: two words for the lengths */

      /* The test buf != NULL is only necessary if LIT_BUFSIZE > WSIZE.
       * Otherwise we can't have processed more than WSIZE input bytes since
       * the last block flush, because compression would have been
       * successful. If LIT_BUFSIZE <= WSIZE, it is never too late to
       * transform a block into a stored block.
       */
      _tr_stored_block$1(s, buf, stored_len, last);
    } else if (s.strategy === Z_FIXED$1 || static_lenb === opt_lenb) {
      send_bits(s, (STATIC_TREES << 1) + (last ? 1 : 0), 3);
      compress_block(s, static_ltree, static_dtree);
    } else {
      send_bits(s, (DYN_TREES << 1) + (last ? 1 : 0), 3);
      send_all_trees(s, s.l_desc.max_code + 1, s.d_desc.max_code + 1, max_blindex + 1);
      compress_block(s, s.dyn_ltree, s.dyn_dtree);
    }
    // Assert (s->compressed_len == s->bits_sent, "bad compressed size");
    /* The above check is made mod 2^32, for files larger than 512 MB
     * and uLong implemented on 32 bits.
     */
    init_block(s);
    if (last) {
      bi_windup(s);
    }
    // Tracev((stderr,"\ncomprlen %lu(%lu) ", s->compressed_len>>3,
    //       s->compressed_len-7*last));
  };

  /* ===========================================================================
   * Save the match info and tally the frequency counts. Return true if
   * the current block must be flushed.
   */
  var _tr_tally$1 = function _tr_tally(s, dist, lc) {
    //    deflate_state *s;
    //    unsigned dist;  /* distance of matched string */
    //    unsigned lc;    /* match length-MIN_MATCH or unmatched char (if dist==0) */

    s.pending_buf[s.sym_buf + s.sym_next++] = dist;
    s.pending_buf[s.sym_buf + s.sym_next++] = dist >> 8;
    s.pending_buf[s.sym_buf + s.sym_next++] = lc;
    if (dist === 0) {
      /* lc is the unmatched char */
      s.dyn_ltree[lc * 2] /*.Freq*/++;
    } else {
      s.matches++;
      /* Here, lc is the match length - MIN_MATCH */
      dist--; /* dist = match distance - 1 */
      //Assert((ush)dist < (ush)MAX_DIST(s) &&
      //       (ush)lc <= (ush)(MAX_MATCH-MIN_MATCH) &&
      //       (ush)d_code(dist) < (ush)D_CODES,  "_tr_tally: bad match");

      s.dyn_ltree[(_length_code[lc] + LITERALS$1 + 1) * 2] /*.Freq*/++;
      s.dyn_dtree[d_code(dist) * 2] /*.Freq*/++;
    }

    return s.sym_next === s.sym_end;
  };
  var _tr_init_1 = _tr_init$1;
  var _tr_stored_block_1 = _tr_stored_block$1;
  var _tr_flush_block_1 = _tr_flush_block$1;
  var _tr_tally_1 = _tr_tally$1;
  var _tr_align_1 = _tr_align$1;
  var trees = {
    _tr_init: _tr_init_1,
    _tr_stored_block: _tr_stored_block_1,
    _tr_flush_block: _tr_flush_block_1,
    _tr_tally: _tr_tally_1,
    _tr_align: _tr_align_1
  };

  // Note: adler32 takes 12% for level 0 and 2% for level 6.
  // It isn't worth it to make additional optimizations as in original.
  // Small size is preferable.

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.
  var adler32 = function adler32(adler, buf, len, pos) {
    var s1 = adler & 0xffff | 0,
      s2 = adler >>> 16 & 0xffff | 0,
      n = 0;
    while (len !== 0) {
      // Set limit ~ twice less than 5552, to keep
      // s2 in 31-bits, because we force signed ints.
      // in other case %= will fail.
      n = len > 2000 ? 2000 : len;
      len -= n;
      do {
        s1 = s1 + buf[pos++] | 0;
        s2 = s2 + s1 | 0;
      } while (--n);
      s1 %= 65521;
      s2 %= 65521;
    }
    return s1 | s2 << 16 | 0;
  };
  var adler32_1 = adler32;

  // Note: we can't get significant speed boost here.
  // So write code to minimize size - no pregenerated tables
  // and array tools dependencies.

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.

  // Use ordinary array, since untyped makes no boost here
  var makeTable = function makeTable() {
    var c,
      table = [];
    for (var n = 0; n < 256; n++) {
      c = n;
      for (var k = 0; k < 8; k++) {
        c = c & 1 ? 0xEDB88320 ^ c >>> 1 : c >>> 1;
      }
      table[n] = c;
    }
    return table;
  };

  // Create table on load. Just 255 signed longs. Not a problem.
  var crcTable = new Uint32Array(makeTable());
  var crc32 = function crc32(crc, buf, len, pos) {
    var t = crcTable;
    var end = pos + len;
    crc ^= -1;
    for (var i = pos; i < end; i++) {
      crc = crc >>> 8 ^ t[(crc ^ buf[i]) & 0xFF];
    }
    return crc ^ -1; // >>> 0;
  };

  var crc32_1 = crc32;

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.
  var messages = {
    2: 'need dictionary',
    /* Z_NEED_DICT       2  */
    1: 'stream end',
    /* Z_STREAM_END      1  */
    0: '',
    /* Z_OK              0  */
    '-1': 'file error',
    /* Z_ERRNO         (-1) */
    '-2': 'stream error',
    /* Z_STREAM_ERROR  (-2) */
    '-3': 'data error',
    /* Z_DATA_ERROR    (-3) */
    '-4': 'insufficient memory',
    /* Z_MEM_ERROR     (-4) */
    '-5': 'buffer error',
    /* Z_BUF_ERROR     (-5) */
    '-6': 'incompatible version' /* Z_VERSION_ERROR (-6) */
  };

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.
  var constants$2 = {
    /* Allowed flush values; see deflate() and inflate() below for details */
    Z_NO_FLUSH: 0,
    Z_PARTIAL_FLUSH: 1,
    Z_SYNC_FLUSH: 2,
    Z_FULL_FLUSH: 3,
    Z_FINISH: 4,
    Z_BLOCK: 5,
    Z_TREES: 6,
    /* Return codes for the compression/decompression functions. Negative values
    * are errors, positive values are used for special but normal events.
    */
    Z_OK: 0,
    Z_STREAM_END: 1,
    Z_NEED_DICT: 2,
    Z_ERRNO: -1,
    Z_STREAM_ERROR: -2,
    Z_DATA_ERROR: -3,
    Z_MEM_ERROR: -4,
    Z_BUF_ERROR: -5,
    //Z_VERSION_ERROR: -6,

    /* compression levels */
    Z_NO_COMPRESSION: 0,
    Z_BEST_SPEED: 1,
    Z_BEST_COMPRESSION: 9,
    Z_DEFAULT_COMPRESSION: -1,
    Z_FILTERED: 1,
    Z_HUFFMAN_ONLY: 2,
    Z_RLE: 3,
    Z_FIXED: 4,
    Z_DEFAULT_STRATEGY: 0,
    /* Possible values of the data_type field (though see inflate()) */
    Z_BINARY: 0,
    Z_TEXT: 1,
    //Z_ASCII:                1, // = Z_TEXT (deprecated)
    Z_UNKNOWN: 2,
    /* The deflate compression method */
    Z_DEFLATED: 8
    //Z_NULL:                 null // Use -1 or null inline, depending on var type
  };

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.

  var _tr_init = trees._tr_init,
    _tr_stored_block = trees._tr_stored_block,
    _tr_flush_block = trees._tr_flush_block,
    _tr_tally = trees._tr_tally,
    _tr_align = trees._tr_align;

  /* Public constants ==========================================================*/
  /* ===========================================================================*/

  var Z_NO_FLUSH$2 = constants$2.Z_NO_FLUSH,
    Z_PARTIAL_FLUSH = constants$2.Z_PARTIAL_FLUSH,
    Z_FULL_FLUSH$1 = constants$2.Z_FULL_FLUSH,
    Z_FINISH$3 = constants$2.Z_FINISH,
    Z_BLOCK$1 = constants$2.Z_BLOCK,
    Z_OK$3 = constants$2.Z_OK,
    Z_STREAM_END$3 = constants$2.Z_STREAM_END,
    Z_STREAM_ERROR$2 = constants$2.Z_STREAM_ERROR,
    Z_DATA_ERROR$2 = constants$2.Z_DATA_ERROR,
    Z_BUF_ERROR$1 = constants$2.Z_BUF_ERROR,
    Z_DEFAULT_COMPRESSION$1 = constants$2.Z_DEFAULT_COMPRESSION,
    Z_FILTERED = constants$2.Z_FILTERED,
    Z_HUFFMAN_ONLY = constants$2.Z_HUFFMAN_ONLY,
    Z_RLE = constants$2.Z_RLE,
    Z_FIXED = constants$2.Z_FIXED,
    Z_DEFAULT_STRATEGY$1 = constants$2.Z_DEFAULT_STRATEGY,
    Z_UNKNOWN = constants$2.Z_UNKNOWN,
    Z_DEFLATED$2 = constants$2.Z_DEFLATED;

  /*============================================================================*/

  var MAX_MEM_LEVEL = 9;
  /* Maximum value for memLevel in deflateInit2 */
  var MAX_WBITS$1 = 15;
  /* 32K LZ77 window */
  var DEF_MEM_LEVEL = 8;
  var LENGTH_CODES = 29;
  /* number of length codes, not counting the special END_BLOCK code */
  var LITERALS = 256;
  /* number of literal bytes 0..255 */
  var L_CODES = LITERALS + 1 + LENGTH_CODES;
  /* number of Literal or Length codes, including the END_BLOCK code */
  var D_CODES = 30;
  /* number of distance codes */
  var BL_CODES = 19;
  /* number of codes used to transfer the bit lengths */
  var HEAP_SIZE = 2 * L_CODES + 1;
  /* maximum heap size */
  var MAX_BITS = 15;
  /* All codes must not exceed MAX_BITS bits */

  var MIN_MATCH = 3;
  var MAX_MATCH = 258;
  var MIN_LOOKAHEAD = MAX_MATCH + MIN_MATCH + 1;
  var PRESET_DICT = 0x20;
  var INIT_STATE = 42; /* zlib header -> BUSY_STATE */
  //#ifdef GZIP
  var GZIP_STATE = 57; /* gzip header -> BUSY_STATE | EXTRA_STATE */
  //#endif
  var EXTRA_STATE = 69; /* gzip extra block -> NAME_STATE */
  var NAME_STATE = 73; /* gzip file name -> COMMENT_STATE */
  var COMMENT_STATE = 91; /* gzip comment -> HCRC_STATE */
  var HCRC_STATE = 103; /* gzip header CRC -> BUSY_STATE */
  var BUSY_STATE = 113; /* deflate -> FINISH_STATE */
  var FINISH_STATE = 666; /* stream complete */

  var BS_NEED_MORE = 1; /* block not completed, need more input or more output */
  var BS_BLOCK_DONE = 2; /* block flush performed */
  var BS_FINISH_STARTED = 3; /* finish started, need only more output at next deflate */
  var BS_FINISH_DONE = 4; /* finish done, accept no more input or output */

  var OS_CODE = 0x03; // Unix :) . Don't detect, use this default.

  var err = function err(strm, errorCode) {
    strm.msg = messages[errorCode];
    return errorCode;
  };
  var rank = function rank(f) {
    return f * 2 - (f > 4 ? 9 : 0);
  };
  var zero = function zero(buf) {
    var len = buf.length;
    while (--len >= 0) {
      buf[len] = 0;
    }
  };

  /* ===========================================================================
   * Slide the hash table when sliding the window down (could be avoided with 32
   * bit values at the expense of memory usage). We slide even when level == 0 to
   * keep the hash table consistent if we switch back to level > 0 later.
   */
  var slide_hash = function slide_hash(s) {
    var n, m;
    var p;
    var wsize = s.w_size;
    n = s.hash_size;
    p = n;
    do {
      m = s.head[--p];
      s.head[p] = m >= wsize ? m - wsize : 0;
    } while (--n);
    n = wsize;
    //#ifndef FASTEST
    p = n;
    do {
      m = s.prev[--p];
      s.prev[p] = m >= wsize ? m - wsize : 0;
      /* If n is not on any hash chain, prev[n] is garbage but
       * its value will never be used.
       */
    } while (--n);
    //#endif
  };

  /* eslint-disable new-cap */
  var HASH_ZLIB = function HASH_ZLIB(s, prev, data) {
    return (prev << s.hash_shift ^ data) & s.hash_mask;
  };
  // This hash causes less collisions, https://github.com/nodeca/pako/issues/135
  // But breaks binary compatibility
  //let HASH_FAST = (s, prev, data) => ((prev << 8) + (prev >> 8) + (data << 4)) & s.hash_mask;
  var HASH = HASH_ZLIB;

  /* =========================================================================
   * Flush as much pending output as possible. All deflate() output, except for
   * some deflate_stored() output, goes through this function so some
   * applications may wish to modify it to avoid allocating a large
   * strm->next_out buffer and copying into it. (See also read_buf()).
   */
  var flush_pending = function flush_pending(strm) {
    var s = strm.state;

    //_tr_flush_bits(s);
    var len = s.pending;
    if (len > strm.avail_out) {
      len = strm.avail_out;
    }
    if (len === 0) {
      return;
    }
    strm.output.set(s.pending_buf.subarray(s.pending_out, s.pending_out + len), strm.next_out);
    strm.next_out += len;
    s.pending_out += len;
    strm.total_out += len;
    strm.avail_out -= len;
    s.pending -= len;
    if (s.pending === 0) {
      s.pending_out = 0;
    }
  };
  var flush_block_only = function flush_block_only(s, last) {
    _tr_flush_block(s, s.block_start >= 0 ? s.block_start : -1, s.strstart - s.block_start, last);
    s.block_start = s.strstart;
    flush_pending(s.strm);
  };
  var put_byte = function put_byte(s, b) {
    s.pending_buf[s.pending++] = b;
  };

  /* =========================================================================
   * Put a short in the pending buffer. The 16-bit value is put in MSB order.
   * IN assertion: the stream state is correct and there is enough room in
   * pending_buf.
   */
  var putShortMSB = function putShortMSB(s, b) {
    //  put_byte(s, (Byte)(b >> 8));
    //  put_byte(s, (Byte)(b & 0xff));
    s.pending_buf[s.pending++] = b >>> 8 & 0xff;
    s.pending_buf[s.pending++] = b & 0xff;
  };

  /* ===========================================================================
   * Read a new buffer from the current input stream, update the adler32
   * and total number of bytes read.  All deflate() input goes through
   * this function so some applications may wish to modify it to avoid
   * allocating a large strm->input buffer and copying from it.
   * (See also flush_pending()).
   */
  var read_buf = function read_buf(strm, buf, start, size) {
    var len = strm.avail_in;
    if (len > size) {
      len = size;
    }
    if (len === 0) {
      return 0;
    }
    strm.avail_in -= len;

    // zmemcpy(buf, strm->next_in, len);
    buf.set(strm.input.subarray(strm.next_in, strm.next_in + len), start);
    if (strm.state.wrap === 1) {
      strm.adler = adler32_1(strm.adler, buf, len, start);
    } else if (strm.state.wrap === 2) {
      strm.adler = crc32_1(strm.adler, buf, len, start);
    }
    strm.next_in += len;
    strm.total_in += len;
    return len;
  };

  /* ===========================================================================
   * Set match_start to the longest match starting at the given string and
   * return its length. Matches shorter or equal to prev_length are discarded,
   * in which case the result is equal to prev_length and match_start is
   * garbage.
   * IN assertions: cur_match is the head of the hash chain for the current
   *   string (strstart) and its distance is <= MAX_DIST, and prev_length >= 1
   * OUT assertion: the match length is not greater than s->lookahead.
   */
  var longest_match = function longest_match(s, cur_match) {
    var chain_length = s.max_chain_length; /* max hash chain length */
    var scan = s.strstart; /* current string */
    var match; /* matched string */
    var len; /* length of current match */
    var best_len = s.prev_length; /* best match length so far */
    var nice_match = s.nice_match; /* stop if match long enough */
    var limit = s.strstart > s.w_size - MIN_LOOKAHEAD ? s.strstart - (s.w_size - MIN_LOOKAHEAD) : 0 /*NIL*/;

    var _win = s.window; // shortcut

    var wmask = s.w_mask;
    var prev = s.prev;

    /* Stop when cur_match becomes <= limit. To simplify the code,
     * we prevent matches with the string of window index 0.
     */

    var strend = s.strstart + MAX_MATCH;
    var scan_end1 = _win[scan + best_len - 1];
    var scan_end = _win[scan + best_len];

    /* The code is optimized for HASH_BITS >= 8 and MAX_MATCH-2 multiple of 16.
     * It is easy to get rid of this optimization if necessary.
     */
    // Assert(s->hash_bits >= 8 && MAX_MATCH == 258, "Code too clever");

    /* Do not waste too much time if we already have a good match: */
    if (s.prev_length >= s.good_match) {
      chain_length >>= 2;
    }
    /* Do not look for matches beyond the end of the input. This is necessary
     * to make deflate deterministic.
     */
    if (nice_match > s.lookahead) {
      nice_match = s.lookahead;
    }

    // Assert((ulg)s->strstart <= s->window_size-MIN_LOOKAHEAD, "need lookahead");

    do {
      // Assert(cur_match < s->strstart, "no future");
      match = cur_match;

      /* Skip to next match if the match length cannot increase
       * or if the match length is less than 2.  Note that the checks below
       * for insufficient lookahead only occur occasionally for performance
       * reasons.  Therefore uninitialized memory will be accessed, and
       * conditional jumps will be made that depend on those values.
       * However the length of the match is limited to the lookahead, so
       * the output of deflate is not affected by the uninitialized values.
       */

      if (_win[match + best_len] !== scan_end || _win[match + best_len - 1] !== scan_end1 || _win[match] !== _win[scan] || _win[++match] !== _win[scan + 1]) {
        continue;
      }

      /* The check at best_len-1 can be removed because it will be made
       * again later. (This heuristic is not always a win.)
       * It is not necessary to compare scan[2] and match[2] since they
       * are always equal when the other bytes match, given that
       * the hash keys are equal and that HASH_BITS >= 8.
       */
      scan += 2;
      match++;
      // Assert(*scan == *match, "match[2]?");

      /* We check for insufficient lookahead only every 8th comparison;
       * the 256th check will be made at strstart+258.
       */
      do {
        /*jshint noempty:false*/
      } while (_win[++scan] === _win[++match] && _win[++scan] === _win[++match] && _win[++scan] === _win[++match] && _win[++scan] === _win[++match] && _win[++scan] === _win[++match] && _win[++scan] === _win[++match] && _win[++scan] === _win[++match] && _win[++scan] === _win[++match] && scan < strend);

      // Assert(scan <= s->window+(unsigned)(s->window_size-1), "wild scan");

      len = MAX_MATCH - (strend - scan);
      scan = strend - MAX_MATCH;
      if (len > best_len) {
        s.match_start = cur_match;
        best_len = len;
        if (len >= nice_match) {
          break;
        }
        scan_end1 = _win[scan + best_len - 1];
        scan_end = _win[scan + best_len];
      }
    } while ((cur_match = prev[cur_match & wmask]) > limit && --chain_length !== 0);
    if (best_len <= s.lookahead) {
      return best_len;
    }
    return s.lookahead;
  };

  /* ===========================================================================
   * Fill the window when the lookahead becomes insufficient.
   * Updates strstart and lookahead.
   *
   * IN assertion: lookahead < MIN_LOOKAHEAD
   * OUT assertions: strstart <= window_size-MIN_LOOKAHEAD
   *    At least one byte has been read, or avail_in == 0; reads are
   *    performed for at least two bytes (required for the zip translate_eol
   *    option -- not supported here).
   */
  var fill_window = function fill_window(s) {
    var _w_size = s.w_size;
    var n, more, str;

    //Assert(s->lookahead < MIN_LOOKAHEAD, "already enough lookahead");

    do {
      more = s.window_size - s.lookahead - s.strstart;

      // JS ints have 32 bit, block below not needed
      /* Deal with !@#$% 64K limit: */
      //if (sizeof(int) <= 2) {
      //    if (more == 0 && s->strstart == 0 && s->lookahead == 0) {
      //        more = wsize;
      //
      //  } else if (more == (unsigned)(-1)) {
      //        /* Very unlikely, but possible on 16 bit machine if
      //         * strstart == 0 && lookahead == 1 (input done a byte at time)
      //         */
      //        more--;
      //    }
      //}

      /* If the window is almost full and there is insufficient lookahead,
       * move the upper half to the lower one to make room in the upper half.
       */
      if (s.strstart >= _w_size + (_w_size - MIN_LOOKAHEAD)) {
        s.window.set(s.window.subarray(_w_size, _w_size + _w_size - more), 0);
        s.match_start -= _w_size;
        s.strstart -= _w_size;
        /* we now have strstart >= MAX_DIST */
        s.block_start -= _w_size;
        if (s.insert > s.strstart) {
          s.insert = s.strstart;
        }
        slide_hash(s);
        more += _w_size;
      }
      if (s.strm.avail_in === 0) {
        break;
      }

      /* If there was no sliding:
       *    strstart <= WSIZE+MAX_DIST-1 && lookahead <= MIN_LOOKAHEAD - 1 &&
       *    more == window_size - lookahead - strstart
       * => more >= window_size - (MIN_LOOKAHEAD-1 + WSIZE + MAX_DIST-1)
       * => more >= window_size - 2*WSIZE + 2
       * In the BIG_MEM or MMAP case (not yet supported),
       *   window_size == input_size + MIN_LOOKAHEAD  &&
       *   strstart + s->lookahead <= input_size => more >= MIN_LOOKAHEAD.
       * Otherwise, window_size == 2*WSIZE so more >= 2.
       * If there was sliding, more >= WSIZE. So in all cases, more >= 2.
       */
      //Assert(more >= 2, "more < 2");
      n = read_buf(s.strm, s.window, s.strstart + s.lookahead, more);
      s.lookahead += n;

      /* Initialize the hash value now that we have some input: */
      if (s.lookahead + s.insert >= MIN_MATCH) {
        str = s.strstart - s.insert;
        s.ins_h = s.window[str];

        /* UPDATE_HASH(s, s->ins_h, s->window[str + 1]); */
        s.ins_h = HASH(s, s.ins_h, s.window[str + 1]);
        //#if MIN_MATCH != 3
        //        Call update_hash() MIN_MATCH-3 more times
        //#endif
        while (s.insert) {
          /* UPDATE_HASH(s, s->ins_h, s->window[str + MIN_MATCH-1]); */
          s.ins_h = HASH(s, s.ins_h, s.window[str + MIN_MATCH - 1]);
          s.prev[str & s.w_mask] = s.head[s.ins_h];
          s.head[s.ins_h] = str;
          str++;
          s.insert--;
          if (s.lookahead + s.insert < MIN_MATCH) {
            break;
          }
        }
      }
      /* If the whole input has less than MIN_MATCH bytes, ins_h is garbage,
       * but this is not important since only literal bytes will be emitted.
       */
    } while (s.lookahead < MIN_LOOKAHEAD && s.strm.avail_in !== 0);

    /* If the WIN_INIT bytes after the end of the current data have never been
     * written, then zero those bytes in order to avoid memory check reports of
     * the use of uninitialized (or uninitialised as Julian writes) bytes by
     * the longest match routines.  Update the high water mark for the next
     * time through here.  WIN_INIT is set to MAX_MATCH since the longest match
     * routines allow scanning to strstart + MAX_MATCH, ignoring lookahead.
     */
    //  if (s.high_water < s.window_size) {
    //    const curr = s.strstart + s.lookahead;
    //    let init = 0;
    //
    //    if (s.high_water < curr) {
    //      /* Previous high water mark below current data -- zero WIN_INIT
    //       * bytes or up to end of window, whichever is less.
    //       */
    //      init = s.window_size - curr;
    //      if (init > WIN_INIT)
    //        init = WIN_INIT;
    //      zmemzero(s->window + curr, (unsigned)init);
    //      s->high_water = curr + init;
    //    }
    //    else if (s->high_water < (ulg)curr + WIN_INIT) {
    //      /* High water mark at or above current data, but below current data
    //       * plus WIN_INIT -- zero out to current data plus WIN_INIT, or up
    //       * to end of window, whichever is less.
    //       */
    //      init = (ulg)curr + WIN_INIT - s->high_water;
    //      if (init > s->window_size - s->high_water)
    //        init = s->window_size - s->high_water;
    //      zmemzero(s->window + s->high_water, (unsigned)init);
    //      s->high_water += init;
    //    }
    //  }
    //
    //  Assert((ulg)s->strstart <= s->window_size - MIN_LOOKAHEAD,
    //    "not enough room for search");
  };

  /* ===========================================================================
   * Copy without compression as much as possible from the input stream, return
   * the current block state.
   *
   * In case deflateParams() is used to later switch to a non-zero compression
   * level, s->matches (otherwise unused when storing) keeps track of the number
   * of hash table slides to perform. If s->matches is 1, then one hash table
   * slide will be done when switching. If s->matches is 2, the maximum value
   * allowed here, then the hash table will be cleared, since two or more slides
   * is the same as a clear.
   *
   * deflate_stored() is written to minimize the number of times an input byte is
   * copied. It is most efficient with large input and output buffers, which
   * maximizes the opportunites to have a single copy from next_in to next_out.
   */
  var deflate_stored = function deflate_stored(s, flush) {
    /* Smallest worthy block size when not flushing or finishing. By default
     * this is 32K. This can be as small as 507 bytes for memLevel == 1. For
     * large input and output buffers, the stored block size will be larger.
     */
    var min_block = s.pending_buf_size - 5 > s.w_size ? s.w_size : s.pending_buf_size - 5;

    /* Copy as many min_block or larger stored blocks directly to next_out as
     * possible. If flushing, copy the remaining available input to next_out as
     * stored blocks, if there is enough space.
     */
    var len,
      left,
      have,
      last = 0;
    var used = s.strm.avail_in;
    do {
      /* Set len to the maximum size block that we can copy directly with the
       * available input data and output space. Set left to how much of that
       * would be copied from what's left in the window.
       */
      len = 65535 /* MAX_STORED */; /* maximum deflate stored block length */
      have = s.bi_valid + 42 >> 3; /* number of header bytes */
      if (s.strm.avail_out < have) {
        /* need room for header */
        break;
      }
      /* maximum stored block length that will fit in avail_out: */
      have = s.strm.avail_out - have;
      left = s.strstart - s.block_start; /* bytes left in window */
      if (len > left + s.strm.avail_in) {
        len = left + s.strm.avail_in; /* limit len to the input */
      }

      if (len > have) {
        len = have; /* limit len to the output */
      }

      /* If the stored block would be less than min_block in length, or if
       * unable to copy all of the available input when flushing, then try
       * copying to the window and the pending buffer instead. Also don't
       * write an empty block when flushing -- deflate() does that.
       */
      if (len < min_block && (len === 0 && flush !== Z_FINISH$3 || flush === Z_NO_FLUSH$2 || len !== left + s.strm.avail_in)) {
        break;
      }

      /* Make a dummy stored block in pending to get the header bytes,
       * including any pending bits. This also updates the debugging counts.
       */
      last = flush === Z_FINISH$3 && len === left + s.strm.avail_in ? 1 : 0;
      _tr_stored_block(s, 0, 0, last);

      /* Replace the lengths in the dummy stored block with len. */
      s.pending_buf[s.pending - 4] = len;
      s.pending_buf[s.pending - 3] = len >> 8;
      s.pending_buf[s.pending - 2] = ~len;
      s.pending_buf[s.pending - 1] = ~len >> 8;

      /* Write the stored block header bytes. */
      flush_pending(s.strm);

      //#ifdef ZLIB_DEBUG
      //    /* Update debugging counts for the data about to be copied. */
      //    s->compressed_len += len << 3;
      //    s->bits_sent += len << 3;
      //#endif

      /* Copy uncompressed bytes from the window to next_out. */
      if (left) {
        if (left > len) {
          left = len;
        }
        //zmemcpy(s->strm->next_out, s->window + s->block_start, left);
        s.strm.output.set(s.window.subarray(s.block_start, s.block_start + left), s.strm.next_out);
        s.strm.next_out += left;
        s.strm.avail_out -= left;
        s.strm.total_out += left;
        s.block_start += left;
        len -= left;
      }

      /* Copy uncompressed bytes directly from next_in to next_out, updating
       * the check value.
       */
      if (len) {
        read_buf(s.strm, s.strm.output, s.strm.next_out, len);
        s.strm.next_out += len;
        s.strm.avail_out -= len;
        s.strm.total_out += len;
      }
    } while (last === 0);

    /* Update the sliding window with the last s->w_size bytes of the copied
     * data, or append all of the copied data to the existing window if less
     * than s->w_size bytes were copied. Also update the number of bytes to
     * insert in the hash tables, in the event that deflateParams() switches to
     * a non-zero compression level.
     */
    used -= s.strm.avail_in; /* number of input bytes directly copied */
    if (used) {
      /* If any input was used, then no unused input remains in the window,
       * therefore s->block_start == s->strstart.
       */
      if (used >= s.w_size) {
        /* supplant the previous history */
        s.matches = 2; /* clear hash */
        //zmemcpy(s->window, s->strm->next_in - s->w_size, s->w_size);
        s.window.set(s.strm.input.subarray(s.strm.next_in - s.w_size, s.strm.next_in), 0);
        s.strstart = s.w_size;
        s.insert = s.strstart;
      } else {
        if (s.window_size - s.strstart <= used) {
          /* Slide the window down. */
          s.strstart -= s.w_size;
          //zmemcpy(s->window, s->window + s->w_size, s->strstart);
          s.window.set(s.window.subarray(s.w_size, s.w_size + s.strstart), 0);
          if (s.matches < 2) {
            s.matches++; /* add a pending slide_hash() */
          }

          if (s.insert > s.strstart) {
            s.insert = s.strstart;
          }
        }
        //zmemcpy(s->window + s->strstart, s->strm->next_in - used, used);
        s.window.set(s.strm.input.subarray(s.strm.next_in - used, s.strm.next_in), s.strstart);
        s.strstart += used;
        s.insert += used > s.w_size - s.insert ? s.w_size - s.insert : used;
      }
      s.block_start = s.strstart;
    }
    if (s.high_water < s.strstart) {
      s.high_water = s.strstart;
    }

    /* If the last block was written to next_out, then done. */
    if (last) {
      return BS_FINISH_DONE;
    }

    /* If flushing and all input has been consumed, then done. */
    if (flush !== Z_NO_FLUSH$2 && flush !== Z_FINISH$3 && s.strm.avail_in === 0 && s.strstart === s.block_start) {
      return BS_BLOCK_DONE;
    }

    /* Fill the window with any remaining input. */
    have = s.window_size - s.strstart;
    if (s.strm.avail_in > have && s.block_start >= s.w_size) {
      /* Slide the window down. */
      s.block_start -= s.w_size;
      s.strstart -= s.w_size;
      //zmemcpy(s->window, s->window + s->w_size, s->strstart);
      s.window.set(s.window.subarray(s.w_size, s.w_size + s.strstart), 0);
      if (s.matches < 2) {
        s.matches++; /* add a pending slide_hash() */
      }

      have += s.w_size; /* more space now */
      if (s.insert > s.strstart) {
        s.insert = s.strstart;
      }
    }
    if (have > s.strm.avail_in) {
      have = s.strm.avail_in;
    }
    if (have) {
      read_buf(s.strm, s.window, s.strstart, have);
      s.strstart += have;
      s.insert += have > s.w_size - s.insert ? s.w_size - s.insert : have;
    }
    if (s.high_water < s.strstart) {
      s.high_water = s.strstart;
    }

    /* There was not enough avail_out to write a complete worthy or flushed
     * stored block to next_out. Write a stored block to pending instead, if we
     * have enough input for a worthy block, or if flushing and there is enough
     * room for the remaining input as a stored block in the pending buffer.
     */
    have = s.bi_valid + 42 >> 3; /* number of header bytes */
    /* maximum stored block length that will fit in pending: */
    have = s.pending_buf_size - have > 65535 /* MAX_STORED */ ? 65535 /* MAX_STORED */ : s.pending_buf_size - have;
    min_block = have > s.w_size ? s.w_size : have;
    left = s.strstart - s.block_start;
    if (left >= min_block || (left || flush === Z_FINISH$3) && flush !== Z_NO_FLUSH$2 && s.strm.avail_in === 0 && left <= have) {
      len = left > have ? have : left;
      last = flush === Z_FINISH$3 && s.strm.avail_in === 0 && len === left ? 1 : 0;
      _tr_stored_block(s, s.block_start, len, last);
      s.block_start += len;
      flush_pending(s.strm);
    }

    /* We've done all we can with the available input and output. */
    return last ? BS_FINISH_STARTED : BS_NEED_MORE;
  };

  /* ===========================================================================
   * Compress as much as possible from the input stream, return the current
   * block state.
   * This function does not perform lazy evaluation of matches and inserts
   * new strings in the dictionary only for unmatched strings or for short
   * matches. It is used only for the fast compression options.
   */
  var deflate_fast = function deflate_fast(s, flush) {
    var hash_head; /* head of the hash chain */
    var bflush; /* set if current block must be flushed */

    for (;;) {
      /* Make sure that we always have enough lookahead, except
       * at the end of the input file. We need MAX_MATCH bytes
       * for the next match, plus MIN_MATCH bytes to insert the
       * string following the next match.
       */
      if (s.lookahead < MIN_LOOKAHEAD) {
        fill_window(s);
        if (s.lookahead < MIN_LOOKAHEAD && flush === Z_NO_FLUSH$2) {
          return BS_NEED_MORE;
        }
        if (s.lookahead === 0) {
          break; /* flush the current block */
        }
      }

      /* Insert the string window[strstart .. strstart+2] in the
       * dictionary, and set hash_head to the head of the hash chain:
       */
      hash_head = 0 /*NIL*/;
      if (s.lookahead >= MIN_MATCH) {
        /*** INSERT_STRING(s, s.strstart, hash_head); ***/
        s.ins_h = HASH(s, s.ins_h, s.window[s.strstart + MIN_MATCH - 1]);
        hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
        s.head[s.ins_h] = s.strstart;
        /***/
      }

      /* Find the longest match, discarding those <= prev_length.
       * At this point we have always match_length < MIN_MATCH
       */
      if (hash_head !== 0 /*NIL*/ && s.strstart - hash_head <= s.w_size - MIN_LOOKAHEAD) {
        /* To simplify the code, we prevent matches with the string
         * of window index 0 (in particular we have to avoid a match
         * of the string with itself at the start of the input file).
         */
        s.match_length = longest_match(s, hash_head);
        /* longest_match() sets match_start */
      }

      if (s.match_length >= MIN_MATCH) {
        // check_match(s, s.strstart, s.match_start, s.match_length); // for debug only

        /*** _tr_tally_dist(s, s.strstart - s.match_start,
                       s.match_length - MIN_MATCH, bflush); ***/
        bflush = _tr_tally(s, s.strstart - s.match_start, s.match_length - MIN_MATCH);
        s.lookahead -= s.match_length;

        /* Insert new strings in the hash table only if the match length
         * is not too large. This saves time but degrades compression.
         */
        if (s.match_length <= s.max_lazy_match /*max_insert_length*/ && s.lookahead >= MIN_MATCH) {
          s.match_length--; /* string at strstart already in table */
          do {
            s.strstart++;
            /*** INSERT_STRING(s, s.strstart, hash_head); ***/
            s.ins_h = HASH(s, s.ins_h, s.window[s.strstart + MIN_MATCH - 1]);
            hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
            s.head[s.ins_h] = s.strstart;
            /***/
            /* strstart never exceeds WSIZE-MAX_MATCH, so there are
             * always MIN_MATCH bytes ahead.
             */
          } while (--s.match_length !== 0);
          s.strstart++;
        } else {
          s.strstart += s.match_length;
          s.match_length = 0;
          s.ins_h = s.window[s.strstart];
          /* UPDATE_HASH(s, s.ins_h, s.window[s.strstart+1]); */
          s.ins_h = HASH(s, s.ins_h, s.window[s.strstart + 1]);

          //#if MIN_MATCH != 3
          //                Call UPDATE_HASH() MIN_MATCH-3 more times
          //#endif
          /* If lookahead < MIN_MATCH, ins_h is garbage, but it does not
           * matter since it will be recomputed at next deflate call.
           */
        }
      } else {
        /* No match, output a literal byte */
        //Tracevv((stderr,"%c", s.window[s.strstart]));
        /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
        bflush = _tr_tally(s, 0, s.window[s.strstart]);
        s.lookahead--;
        s.strstart++;
      }
      if (bflush) {
        /*** FLUSH_BLOCK(s, 0); ***/
        flush_block_only(s, false);
        if (s.strm.avail_out === 0) {
          return BS_NEED_MORE;
        }
        /***/
      }
    }

    s.insert = s.strstart < MIN_MATCH - 1 ? s.strstart : MIN_MATCH - 1;
    if (flush === Z_FINISH$3) {
      /*** FLUSH_BLOCK(s, 1); ***/
      flush_block_only(s, true);
      if (s.strm.avail_out === 0) {
        return BS_FINISH_STARTED;
      }
      /***/
      return BS_FINISH_DONE;
    }
    if (s.sym_next) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }

    return BS_BLOCK_DONE;
  };

  /* ===========================================================================
   * Same as above, but achieves better compression. We use a lazy
   * evaluation for matches: a match is finally adopted only if there is
   * no better match at the next window position.
   */
  var deflate_slow = function deflate_slow(s, flush) {
    var hash_head; /* head of hash chain */
    var bflush; /* set if current block must be flushed */

    var max_insert;

    /* Process the input block. */
    for (;;) {
      /* Make sure that we always have enough lookahead, except
       * at the end of the input file. We need MAX_MATCH bytes
       * for the next match, plus MIN_MATCH bytes to insert the
       * string following the next match.
       */
      if (s.lookahead < MIN_LOOKAHEAD) {
        fill_window(s);
        if (s.lookahead < MIN_LOOKAHEAD && flush === Z_NO_FLUSH$2) {
          return BS_NEED_MORE;
        }
        if (s.lookahead === 0) {
          break;
        } /* flush the current block */
      }

      /* Insert the string window[strstart .. strstart+2] in the
       * dictionary, and set hash_head to the head of the hash chain:
       */
      hash_head = 0 /*NIL*/;
      if (s.lookahead >= MIN_MATCH) {
        /*** INSERT_STRING(s, s.strstart, hash_head); ***/
        s.ins_h = HASH(s, s.ins_h, s.window[s.strstart + MIN_MATCH - 1]);
        hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
        s.head[s.ins_h] = s.strstart;
        /***/
      }

      /* Find the longest match, discarding those <= prev_length.
       */
      s.prev_length = s.match_length;
      s.prev_match = s.match_start;
      s.match_length = MIN_MATCH - 1;
      if (hash_head !== 0 /*NIL*/ && s.prev_length < s.max_lazy_match && s.strstart - hash_head <= s.w_size - MIN_LOOKAHEAD /*MAX_DIST(s)*/) {
        /* To simplify the code, we prevent matches with the string
         * of window index 0 (in particular we have to avoid a match
         * of the string with itself at the start of the input file).
         */
        s.match_length = longest_match(s, hash_head);
        /* longest_match() sets match_start */

        if (s.match_length <= 5 && (s.strategy === Z_FILTERED || s.match_length === MIN_MATCH && s.strstart - s.match_start > 4096 /*TOO_FAR*/)) {
          /* If prev_match is also MIN_MATCH, match_start is garbage
           * but we will ignore the current match anyway.
           */
          s.match_length = MIN_MATCH - 1;
        }
      }
      /* If there was a match at the previous step and the current
       * match is not better, output the previous match:
       */
      if (s.prev_length >= MIN_MATCH && s.match_length <= s.prev_length) {
        max_insert = s.strstart + s.lookahead - MIN_MATCH;
        /* Do not insert strings in hash table beyond this. */

        //check_match(s, s.strstart-1, s.prev_match, s.prev_length);

        /***_tr_tally_dist(s, s.strstart - 1 - s.prev_match,
                       s.prev_length - MIN_MATCH, bflush);***/
        bflush = _tr_tally(s, s.strstart - 1 - s.prev_match, s.prev_length - MIN_MATCH);
        /* Insert in hash table all strings up to the end of the match.
         * strstart-1 and strstart are already inserted. If there is not
         * enough lookahead, the last two strings are not inserted in
         * the hash table.
         */
        s.lookahead -= s.prev_length - 1;
        s.prev_length -= 2;
        do {
          if (++s.strstart <= max_insert) {
            /*** INSERT_STRING(s, s.strstart, hash_head); ***/
            s.ins_h = HASH(s, s.ins_h, s.window[s.strstart + MIN_MATCH - 1]);
            hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
            s.head[s.ins_h] = s.strstart;
            /***/
          }
        } while (--s.prev_length !== 0);
        s.match_available = 0;
        s.match_length = MIN_MATCH - 1;
        s.strstart++;
        if (bflush) {
          /*** FLUSH_BLOCK(s, 0); ***/
          flush_block_only(s, false);
          if (s.strm.avail_out === 0) {
            return BS_NEED_MORE;
          }
          /***/
        }
      } else if (s.match_available) {
        /* If there was no match at the previous position, output a
         * single literal. If there was a match but the current match
         * is longer, truncate the previous match to a single literal.
         */
        //Tracevv((stderr,"%c", s->window[s->strstart-1]));
        /*** _tr_tally_lit(s, s.window[s.strstart-1], bflush); ***/
        bflush = _tr_tally(s, 0, s.window[s.strstart - 1]);
        if (bflush) {
          /*** FLUSH_BLOCK_ONLY(s, 0) ***/
          flush_block_only(s, false);
          /***/
        }

        s.strstart++;
        s.lookahead--;
        if (s.strm.avail_out === 0) {
          return BS_NEED_MORE;
        }
      } else {
        /* There is no previous match to compare with, wait for
         * the next step to decide.
         */
        s.match_available = 1;
        s.strstart++;
        s.lookahead--;
      }
    }
    //Assert (flush != Z_NO_FLUSH, "no flush?");
    if (s.match_available) {
      //Tracevv((stderr,"%c", s->window[s->strstart-1]));
      /*** _tr_tally_lit(s, s.window[s.strstart-1], bflush); ***/
      bflush = _tr_tally(s, 0, s.window[s.strstart - 1]);
      s.match_available = 0;
    }
    s.insert = s.strstart < MIN_MATCH - 1 ? s.strstart : MIN_MATCH - 1;
    if (flush === Z_FINISH$3) {
      /*** FLUSH_BLOCK(s, 1); ***/
      flush_block_only(s, true);
      if (s.strm.avail_out === 0) {
        return BS_FINISH_STARTED;
      }
      /***/
      return BS_FINISH_DONE;
    }
    if (s.sym_next) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }

    return BS_BLOCK_DONE;
  };

  /* ===========================================================================
   * For Z_RLE, simply look for runs of bytes, generate matches only of distance
   * one.  Do not maintain a hash table.  (It will be regenerated if this run of
   * deflate switches away from Z_RLE.)
   */
  var deflate_rle = function deflate_rle(s, flush) {
    var bflush; /* set if current block must be flushed */
    var prev; /* byte at distance one to match */
    var scan, strend; /* scan goes up to strend for length of run */

    var _win = s.window;
    for (;;) {
      /* Make sure that we always have enough lookahead, except
       * at the end of the input file. We need MAX_MATCH bytes
       * for the longest run, plus one for the unrolled loop.
       */
      if (s.lookahead <= MAX_MATCH) {
        fill_window(s);
        if (s.lookahead <= MAX_MATCH && flush === Z_NO_FLUSH$2) {
          return BS_NEED_MORE;
        }
        if (s.lookahead === 0) {
          break;
        } /* flush the current block */
      }

      /* See how many times the previous byte repeats */
      s.match_length = 0;
      if (s.lookahead >= MIN_MATCH && s.strstart > 0) {
        scan = s.strstart - 1;
        prev = _win[scan];
        if (prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan]) {
          strend = s.strstart + MAX_MATCH;
          do {
            /*jshint noempty:false*/
          } while (prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan] && scan < strend);
          s.match_length = MAX_MATCH - (strend - scan);
          if (s.match_length > s.lookahead) {
            s.match_length = s.lookahead;
          }
        }
        //Assert(scan <= s->window+(uInt)(s->window_size-1), "wild scan");
      }

      /* Emit match if have run of MIN_MATCH or longer, else emit literal */
      if (s.match_length >= MIN_MATCH) {
        //check_match(s, s.strstart, s.strstart - 1, s.match_length);

        /*** _tr_tally_dist(s, 1, s.match_length - MIN_MATCH, bflush); ***/
        bflush = _tr_tally(s, 1, s.match_length - MIN_MATCH);
        s.lookahead -= s.match_length;
        s.strstart += s.match_length;
        s.match_length = 0;
      } else {
        /* No match, output a literal byte */
        //Tracevv((stderr,"%c", s->window[s->strstart]));
        /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
        bflush = _tr_tally(s, 0, s.window[s.strstart]);
        s.lookahead--;
        s.strstart++;
      }
      if (bflush) {
        /*** FLUSH_BLOCK(s, 0); ***/
        flush_block_only(s, false);
        if (s.strm.avail_out === 0) {
          return BS_NEED_MORE;
        }
        /***/
      }
    }

    s.insert = 0;
    if (flush === Z_FINISH$3) {
      /*** FLUSH_BLOCK(s, 1); ***/
      flush_block_only(s, true);
      if (s.strm.avail_out === 0) {
        return BS_FINISH_STARTED;
      }
      /***/
      return BS_FINISH_DONE;
    }
    if (s.sym_next) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }

    return BS_BLOCK_DONE;
  };

  /* ===========================================================================
   * For Z_HUFFMAN_ONLY, do not look for matches.  Do not maintain a hash table.
   * (It will be regenerated if this run of deflate switches away from Huffman.)
   */
  var deflate_huff = function deflate_huff(s, flush) {
    var bflush; /* set if current block must be flushed */

    for (;;) {
      /* Make sure that we have a literal to write. */
      if (s.lookahead === 0) {
        fill_window(s);
        if (s.lookahead === 0) {
          if (flush === Z_NO_FLUSH$2) {
            return BS_NEED_MORE;
          }
          break; /* flush the current block */
        }
      }

      /* Output a literal byte */
      s.match_length = 0;
      //Tracevv((stderr,"%c", s->window[s->strstart]));
      /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
      bflush = _tr_tally(s, 0, s.window[s.strstart]);
      s.lookahead--;
      s.strstart++;
      if (bflush) {
        /*** FLUSH_BLOCK(s, 0); ***/
        flush_block_only(s, false);
        if (s.strm.avail_out === 0) {
          return BS_NEED_MORE;
        }
        /***/
      }
    }

    s.insert = 0;
    if (flush === Z_FINISH$3) {
      /*** FLUSH_BLOCK(s, 1); ***/
      flush_block_only(s, true);
      if (s.strm.avail_out === 0) {
        return BS_FINISH_STARTED;
      }
      /***/
      return BS_FINISH_DONE;
    }
    if (s.sym_next) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }

    return BS_BLOCK_DONE;
  };

  /* Values for max_lazy_match, good_match and max_chain_length, depending on
   * the desired pack level (0..9). The values given below have been tuned to
   * exclude worst case performance for pathological files. Better values may be
   * found for specific files.
   */
  function Config(good_length, max_lazy, nice_length, max_chain, func) {
    this.good_length = good_length;
    this.max_lazy = max_lazy;
    this.nice_length = nice_length;
    this.max_chain = max_chain;
    this.func = func;
  }
  var configuration_table = [/*      good lazy nice chain */
  new Config(0, 0, 0, 0, deflate_stored), /* 0 store only */
  new Config(4, 4, 8, 4, deflate_fast), /* 1 max speed, no lazy matches */
  new Config(4, 5, 16, 8, deflate_fast), /* 2 */
  new Config(4, 6, 32, 32, deflate_fast), /* 3 */

  new Config(4, 4, 16, 16, deflate_slow), /* 4 lazy matches */
  new Config(8, 16, 32, 32, deflate_slow), /* 5 */
  new Config(8, 16, 128, 128, deflate_slow), /* 6 */
  new Config(8, 32, 128, 256, deflate_slow), /* 7 */
  new Config(32, 128, 258, 1024, deflate_slow), /* 8 */
  new Config(32, 258, 258, 4096, deflate_slow) /* 9 max compression */];

  /* ===========================================================================
   * Initialize the "longest match" routines for a new zlib stream
   */
  var lm_init = function lm_init(s) {
    s.window_size = 2 * s.w_size;

    /*** CLEAR_HASH(s); ***/
    zero(s.head); // Fill with NIL (= 0);

    /* Set the default configuration parameters:
     */
    s.max_lazy_match = configuration_table[s.level].max_lazy;
    s.good_match = configuration_table[s.level].good_length;
    s.nice_match = configuration_table[s.level].nice_length;
    s.max_chain_length = configuration_table[s.level].max_chain;
    s.strstart = 0;
    s.block_start = 0;
    s.lookahead = 0;
    s.insert = 0;
    s.match_length = s.prev_length = MIN_MATCH - 1;
    s.match_available = 0;
    s.ins_h = 0;
  };
  function DeflateState() {
    this.strm = null; /* pointer back to this zlib stream */
    this.status = 0; /* as the name implies */
    this.pending_buf = null; /* output still pending */
    this.pending_buf_size = 0; /* size of pending_buf */
    this.pending_out = 0; /* next pending byte to output to the stream */
    this.pending = 0; /* nb of bytes in the pending buffer */
    this.wrap = 0; /* bit 0 true for zlib, bit 1 true for gzip */
    this.gzhead = null; /* gzip header information to write */
    this.gzindex = 0; /* where in extra, name, or comment */
    this.method = Z_DEFLATED$2; /* can only be DEFLATED */
    this.last_flush = -1; /* value of flush param for previous deflate call */

    this.w_size = 0; /* LZ77 window size (32K by default) */
    this.w_bits = 0; /* log2(w_size)  (8..16) */
    this.w_mask = 0; /* w_size - 1 */

    this.window = null;
    /* Sliding window. Input bytes are read into the second half of the window,
     * and move to the first half later to keep a dictionary of at least wSize
     * bytes. With this organization, matches are limited to a distance of
     * wSize-MAX_MATCH bytes, but this ensures that IO is always
     * performed with a length multiple of the block size.
     */

    this.window_size = 0;
    /* Actual size of window: 2*wSize, except when the user input buffer
     * is directly used as sliding window.
     */

    this.prev = null;
    /* Link to older string with same hash index. To limit the size of this
     * array to 64K, this link is maintained only for the last 32K strings.
     * An index in this array is thus a window index modulo 32K.
     */

    this.head = null; /* Heads of the hash chains or NIL. */

    this.ins_h = 0; /* hash index of string to be inserted */
    this.hash_size = 0; /* number of elements in hash table */
    this.hash_bits = 0; /* log2(hash_size) */
    this.hash_mask = 0; /* hash_size-1 */

    this.hash_shift = 0;
    /* Number of bits by which ins_h must be shifted at each input
     * step. It must be such that after MIN_MATCH steps, the oldest
     * byte no longer takes part in the hash key, that is:
     *   hash_shift * MIN_MATCH >= hash_bits
     */

    this.block_start = 0;
    /* Window position at the beginning of the current output block. Gets
     * negative when the window is moved backwards.
     */

    this.match_length = 0; /* length of best match */
    this.prev_match = 0; /* previous match */
    this.match_available = 0; /* set if previous match exists */
    this.strstart = 0; /* start of string to insert */
    this.match_start = 0; /* start of matching string */
    this.lookahead = 0; /* number of valid bytes ahead in window */

    this.prev_length = 0;
    /* Length of the best match at previous step. Matches not greater than this
     * are discarded. This is used in the lazy match evaluation.
     */

    this.max_chain_length = 0;
    /* To speed up deflation, hash chains are never searched beyond this
     * length.  A higher limit improves compression ratio but degrades the
     * speed.
     */

    this.max_lazy_match = 0;
    /* Attempt to find a better match only when the current match is strictly
     * smaller than this value. This mechanism is used only for compression
     * levels >= 4.
     */
    // That's alias to max_lazy_match, don't use directly
    //this.max_insert_length = 0;
    /* Insert new strings in the hash table only if the match length is not
     * greater than this length. This saves time but degrades compression.
     * max_insert_length is used only for compression levels <= 3.
     */

    this.level = 0; /* compression level (1..9) */
    this.strategy = 0; /* favor or force Huffman coding*/

    this.good_match = 0;
    /* Use a faster search when the previous match is longer than this */

    this.nice_match = 0; /* Stop searching when current match exceeds this */

    /* used by trees.c: */

    /* Didn't use ct_data typedef below to suppress compiler warning */

    // struct ct_data_s dyn_ltree[HEAP_SIZE];   /* literal and length tree */
    // struct ct_data_s dyn_dtree[2*D_CODES+1]; /* distance tree */
    // struct ct_data_s bl_tree[2*BL_CODES+1];  /* Huffman tree for bit lengths */

    // Use flat array of DOUBLE size, with interleaved fata,
    // because JS does not support effective
    this.dyn_ltree = new Uint16Array(HEAP_SIZE * 2);
    this.dyn_dtree = new Uint16Array((2 * D_CODES + 1) * 2);
    this.bl_tree = new Uint16Array((2 * BL_CODES + 1) * 2);
    zero(this.dyn_ltree);
    zero(this.dyn_dtree);
    zero(this.bl_tree);
    this.l_desc = null; /* desc. for literal tree */
    this.d_desc = null; /* desc. for distance tree */
    this.bl_desc = null; /* desc. for bit length tree */

    //ush bl_count[MAX_BITS+1];
    this.bl_count = new Uint16Array(MAX_BITS + 1);
    /* number of codes at each bit length for an optimal tree */

    //int heap[2*L_CODES+1];      /* heap used to build the Huffman trees */
    this.heap = new Uint16Array(2 * L_CODES + 1); /* heap used to build the Huffman trees */
    zero(this.heap);
    this.heap_len = 0; /* number of elements in the heap */
    this.heap_max = 0; /* element of largest frequency */
    /* The sons of heap[n] are heap[2*n] and heap[2*n+1]. heap[0] is not used.
     * The same heap array is used to build all trees.
     */

    this.depth = new Uint16Array(2 * L_CODES + 1); //uch depth[2*L_CODES+1];
    zero(this.depth);
    /* Depth of each subtree used as tie breaker for trees of equal frequency
     */

    this.sym_buf = 0; /* buffer for distances and literals/lengths */

    this.lit_bufsize = 0;
    /* Size of match buffer for literals/lengths.  There are 4 reasons for
     * limiting lit_bufsize to 64K:
     *   - frequencies can be kept in 16 bit counters
     *   - if compression is not successful for the first block, all input
     *     data is still in the window so we can still emit a stored block even
     *     when input comes from standard input.  (This can also be done for
     *     all blocks if lit_bufsize is not greater than 32K.)
     *   - if compression is not successful for a file smaller than 64K, we can
     *     even emit a stored file instead of a stored block (saving 5 bytes).
     *     This is applicable only for zip (not gzip or zlib).
     *   - creating new Huffman trees less frequently may not provide fast
     *     adaptation to changes in the input data statistics. (Take for
     *     example a binary file with poorly compressible code followed by
     *     a highly compressible string table.) Smaller buffer sizes give
     *     fast adaptation but have of course the overhead of transmitting
     *     trees more frequently.
     *   - I can't count above 4
     */

    this.sym_next = 0; /* running index in sym_buf */
    this.sym_end = 0; /* symbol table full when sym_next reaches this */

    this.opt_len = 0; /* bit length of current block with optimal trees */
    this.static_len = 0; /* bit length of current block with static trees */
    this.matches = 0; /* number of string matches in current block */
    this.insert = 0; /* bytes at end of window left to insert */

    this.bi_buf = 0;
    /* Output buffer. bits are inserted starting at the bottom (least
     * significant bits).
     */
    this.bi_valid = 0;
    /* Number of valid bits in bi_buf.  All bits above the last valid bit
     * are always zero.
     */

    // Used for window memory init. We safely ignore it for JS. That makes
    // sense only for pointers and memory check tools.
    //this.high_water = 0;
    /* High water mark offset in window for initialized bytes -- bytes above
     * this are set to zero in order to avoid memory check warnings when
     * longest match routines access bytes past the input.  This is then
     * updated to the new high water mark.
     */
  }

  /* =========================================================================
   * Check for a valid deflate stream state. Return 0 if ok, 1 if not.
   */
  var deflateStateCheck = function deflateStateCheck(strm) {
    if (!strm) {
      return 1;
    }
    var s = strm.state;
    if (!s || s.strm !== strm || s.status !== INIT_STATE &&
    //#ifdef GZIP
    s.status !== GZIP_STATE &&
    //#endif
    s.status !== EXTRA_STATE && s.status !== NAME_STATE && s.status !== COMMENT_STATE && s.status !== HCRC_STATE && s.status !== BUSY_STATE && s.status !== FINISH_STATE) {
      return 1;
    }
    return 0;
  };
  var deflateResetKeep = function deflateResetKeep(strm) {
    if (deflateStateCheck(strm)) {
      return err(strm, Z_STREAM_ERROR$2);
    }
    strm.total_in = strm.total_out = 0;
    strm.data_type = Z_UNKNOWN;
    var s = strm.state;
    s.pending = 0;
    s.pending_out = 0;
    if (s.wrap < 0) {
      s.wrap = -s.wrap;
      /* was made negative by deflate(..., Z_FINISH); */
    }

    s.status =
    //#ifdef GZIP
    s.wrap === 2 ? GZIP_STATE :
    //#endif
    s.wrap ? INIT_STATE : BUSY_STATE;
    strm.adler = s.wrap === 2 ? 0 // crc32(0, Z_NULL, 0)
    : 1; // adler32(0, Z_NULL, 0)
    s.last_flush = -2;
    _tr_init(s);
    return Z_OK$3;
  };
  var deflateReset = function deflateReset(strm) {
    var ret = deflateResetKeep(strm);
    if (ret === Z_OK$3) {
      lm_init(strm.state);
    }
    return ret;
  };
  var deflateSetHeader = function deflateSetHeader(strm, head) {
    if (deflateStateCheck(strm) || strm.state.wrap !== 2) {
      return Z_STREAM_ERROR$2;
    }
    strm.state.gzhead = head;
    return Z_OK$3;
  };
  var deflateInit2 = function deflateInit2(strm, level, method, windowBits, memLevel, strategy) {
    if (!strm) {
      // === Z_NULL
      return Z_STREAM_ERROR$2;
    }
    var wrap = 1;
    if (level === Z_DEFAULT_COMPRESSION$1) {
      level = 6;
    }
    if (windowBits < 0) {
      /* suppress zlib wrapper */
      wrap = 0;
      windowBits = -windowBits;
    } else if (windowBits > 15) {
      wrap = 2; /* write gzip wrapper instead */
      windowBits -= 16;
    }
    if (memLevel < 1 || memLevel > MAX_MEM_LEVEL || method !== Z_DEFLATED$2 || windowBits < 8 || windowBits > 15 || level < 0 || level > 9 || strategy < 0 || strategy > Z_FIXED || windowBits === 8 && wrap !== 1) {
      return err(strm, Z_STREAM_ERROR$2);
    }
    if (windowBits === 8) {
      windowBits = 9;
    }
    /* until 256-byte window bug fixed */

    var s = new DeflateState();
    strm.state = s;
    s.strm = strm;
    s.status = INIT_STATE; /* to pass state test in deflateReset() */

    s.wrap = wrap;
    s.gzhead = null;
    s.w_bits = windowBits;
    s.w_size = 1 << s.w_bits;
    s.w_mask = s.w_size - 1;
    s.hash_bits = memLevel + 7;
    s.hash_size = 1 << s.hash_bits;
    s.hash_mask = s.hash_size - 1;
    s.hash_shift = ~~((s.hash_bits + MIN_MATCH - 1) / MIN_MATCH);
    s.window = new Uint8Array(s.w_size * 2);
    s.head = new Uint16Array(s.hash_size);
    s.prev = new Uint16Array(s.w_size);

    // Don't need mem init magic for JS.
    //s.high_water = 0;  /* nothing written to s->window yet */

    s.lit_bufsize = 1 << memLevel + 6; /* 16K elements by default */

    /* We overlay pending_buf and sym_buf. This works since the average size
     * for length/distance pairs over any compressed block is assured to be 31
     * bits or less.
     *
     * Analysis: The longest fixed codes are a length code of 8 bits plus 5
     * extra bits, for lengths 131 to 257. The longest fixed distance codes are
     * 5 bits plus 13 extra bits, for distances 16385 to 32768. The longest
     * possible fixed-codes length/distance pair is then 31 bits total.
     *
     * sym_buf starts one-fourth of the way into pending_buf. So there are
     * three bytes in sym_buf for every four bytes in pending_buf. Each symbol
     * in sym_buf is three bytes -- two for the distance and one for the
     * literal/length. As each symbol is consumed, the pointer to the next
     * sym_buf value to read moves forward three bytes. From that symbol, up to
     * 31 bits are written to pending_buf. The closest the written pending_buf
     * bits gets to the next sym_buf symbol to read is just before the last
     * code is written. At that time, 31*(n-2) bits have been written, just
     * after 24*(n-2) bits have been consumed from sym_buf. sym_buf starts at
     * 8*n bits into pending_buf. (Note that the symbol buffer fills when n-1
     * symbols are written.) The closest the writing gets to what is unread is
     * then n+14 bits. Here n is lit_bufsize, which is 16384 by default, and
     * can range from 128 to 32768.
     *
     * Therefore, at a minimum, there are 142 bits of space between what is
     * written and what is read in the overlain buffers, so the symbols cannot
     * be overwritten by the compressed data. That space is actually 139 bits,
     * due to the three-bit fixed-code block header.
     *
     * That covers the case where either Z_FIXED is specified, forcing fixed
     * codes, or when the use of fixed codes is chosen, because that choice
     * results in a smaller compressed block than dynamic codes. That latter
     * condition then assures that the above analysis also covers all dynamic
     * blocks. A dynamic-code block will only be chosen to be emitted if it has
     * fewer bits than a fixed-code block would for the same set of symbols.
     * Therefore its average symbol length is assured to be less than 31. So
     * the compressed data for a dynamic block also cannot overwrite the
     * symbols from which it is being constructed.
     */

    s.pending_buf_size = s.lit_bufsize * 4;
    s.pending_buf = new Uint8Array(s.pending_buf_size);

    // It is offset from `s.pending_buf` (size is `s.lit_bufsize * 2`)
    //s->sym_buf = s->pending_buf + s->lit_bufsize;
    s.sym_buf = s.lit_bufsize;

    //s->sym_end = (s->lit_bufsize - 1) * 3;
    s.sym_end = (s.lit_bufsize - 1) * 3;
    /* We avoid equality with lit_bufsize*3 because of wraparound at 64K
     * on 16 bit machines and because stored blocks are restricted to
     * 64K-1 bytes.
     */

    s.level = level;
    s.strategy = strategy;
    s.method = method;
    return deflateReset(strm);
  };
  var deflateInit = function deflateInit(strm, level) {
    return deflateInit2(strm, level, Z_DEFLATED$2, MAX_WBITS$1, DEF_MEM_LEVEL, Z_DEFAULT_STRATEGY$1);
  };

  /* ========================================================================= */
  var deflate$2 = function deflate(strm, flush) {
    if (deflateStateCheck(strm) || flush > Z_BLOCK$1 || flush < 0) {
      return strm ? err(strm, Z_STREAM_ERROR$2) : Z_STREAM_ERROR$2;
    }
    var s = strm.state;
    if (!strm.output || strm.avail_in !== 0 && !strm.input || s.status === FINISH_STATE && flush !== Z_FINISH$3) {
      return err(strm, strm.avail_out === 0 ? Z_BUF_ERROR$1 : Z_STREAM_ERROR$2);
    }
    var old_flush = s.last_flush;
    s.last_flush = flush;

    /* Flush as much pending output as possible */
    if (s.pending !== 0) {
      flush_pending(strm);
      if (strm.avail_out === 0) {
        /* Since avail_out is 0, deflate will be called again with
         * more output space, but possibly with both pending and
         * avail_in equal to zero. There won't be anything to do,
         * but this is not an error situation so make sure we
         * return OK instead of BUF_ERROR at next call of deflate:
         */
        s.last_flush = -1;
        return Z_OK$3;
      }

      /* Make sure there is something to do and avoid duplicate consecutive
       * flushes. For repeated and useless calls with Z_FINISH, we keep
       * returning Z_STREAM_END instead of Z_BUF_ERROR.
       */
    } else if (strm.avail_in === 0 && rank(flush) <= rank(old_flush) && flush !== Z_FINISH$3) {
      return err(strm, Z_BUF_ERROR$1);
    }

    /* User must not provide more input after the first FINISH: */
    if (s.status === FINISH_STATE && strm.avail_in !== 0) {
      return err(strm, Z_BUF_ERROR$1);
    }

    /* Write the header */
    if (s.status === INIT_STATE && s.wrap === 0) {
      s.status = BUSY_STATE;
    }
    if (s.status === INIT_STATE) {
      /* zlib header */
      var header = Z_DEFLATED$2 + (s.w_bits - 8 << 4) << 8;
      var level_flags = -1;
      if (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2) {
        level_flags = 0;
      } else if (s.level < 6) {
        level_flags = 1;
      } else if (s.level === 6) {
        level_flags = 2;
      } else {
        level_flags = 3;
      }
      header |= level_flags << 6;
      if (s.strstart !== 0) {
        header |= PRESET_DICT;
      }
      header += 31 - header % 31;
      putShortMSB(s, header);

      /* Save the adler32 of the preset dictionary: */
      if (s.strstart !== 0) {
        putShortMSB(s, strm.adler >>> 16);
        putShortMSB(s, strm.adler & 0xffff);
      }
      strm.adler = 1; // adler32(0L, Z_NULL, 0);
      s.status = BUSY_STATE;

      /* Compression must start with an empty pending buffer */
      flush_pending(strm);
      if (s.pending !== 0) {
        s.last_flush = -1;
        return Z_OK$3;
      }
    }
    //#ifdef GZIP
    if (s.status === GZIP_STATE) {
      /* gzip header */
      strm.adler = 0; //crc32(0L, Z_NULL, 0);
      put_byte(s, 31);
      put_byte(s, 139);
      put_byte(s, 8);
      if (!s.gzhead) {
        // s->gzhead == Z_NULL
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, s.level === 9 ? 2 : s.strategy >= Z_HUFFMAN_ONLY || s.level < 2 ? 4 : 0);
        put_byte(s, OS_CODE);
        s.status = BUSY_STATE;

        /* Compression must start with an empty pending buffer */
        flush_pending(strm);
        if (s.pending !== 0) {
          s.last_flush = -1;
          return Z_OK$3;
        }
      } else {
        put_byte(s, (s.gzhead.text ? 1 : 0) + (s.gzhead.hcrc ? 2 : 0) + (!s.gzhead.extra ? 0 : 4) + (!s.gzhead.name ? 0 : 8) + (!s.gzhead.comment ? 0 : 16));
        put_byte(s, s.gzhead.time & 0xff);
        put_byte(s, s.gzhead.time >> 8 & 0xff);
        put_byte(s, s.gzhead.time >> 16 & 0xff);
        put_byte(s, s.gzhead.time >> 24 & 0xff);
        put_byte(s, s.level === 9 ? 2 : s.strategy >= Z_HUFFMAN_ONLY || s.level < 2 ? 4 : 0);
        put_byte(s, s.gzhead.os & 0xff);
        if (s.gzhead.extra && s.gzhead.extra.length) {
          put_byte(s, s.gzhead.extra.length & 0xff);
          put_byte(s, s.gzhead.extra.length >> 8 & 0xff);
        }
        if (s.gzhead.hcrc) {
          strm.adler = crc32_1(strm.adler, s.pending_buf, s.pending, 0);
        }
        s.gzindex = 0;
        s.status = EXTRA_STATE;
      }
    }
    if (s.status === EXTRA_STATE) {
      if (s.gzhead.extra /* != Z_NULL*/) {
        var beg = s.pending; /* start of bytes to update crc */
        var left = (s.gzhead.extra.length & 0xffff) - s.gzindex;
        while (s.pending + left > s.pending_buf_size) {
          var copy = s.pending_buf_size - s.pending;
          // zmemcpy(s.pending_buf + s.pending,
          //    s.gzhead.extra + s.gzindex, copy);
          s.pending_buf.set(s.gzhead.extra.subarray(s.gzindex, s.gzindex + copy), s.pending);
          s.pending = s.pending_buf_size;
          //--- HCRC_UPDATE(beg) ---//
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32_1(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          //---//
          s.gzindex += copy;
          flush_pending(strm);
          if (s.pending !== 0) {
            s.last_flush = -1;
            return Z_OK$3;
          }
          beg = 0;
          left -= copy;
        }
        // JS specific: s.gzhead.extra may be TypedArray or Array for backward compatibility
        //              TypedArray.slice and TypedArray.from don't exist in IE10-IE11
        var gzhead_extra = new Uint8Array(s.gzhead.extra);
        // zmemcpy(s->pending_buf + s->pending,
        //     s->gzhead->extra + s->gzindex, left);
        s.pending_buf.set(gzhead_extra.subarray(s.gzindex, s.gzindex + left), s.pending);
        s.pending += left;
        //--- HCRC_UPDATE(beg) ---//
        if (s.gzhead.hcrc && s.pending > beg) {
          strm.adler = crc32_1(strm.adler, s.pending_buf, s.pending - beg, beg);
        }
        //---//
        s.gzindex = 0;
      }
      s.status = NAME_STATE;
    }
    if (s.status === NAME_STATE) {
      if (s.gzhead.name /* != Z_NULL*/) {
        var _beg = s.pending; /* start of bytes to update crc */
        var val;
        do {
          if (s.pending === s.pending_buf_size) {
            //--- HCRC_UPDATE(beg) ---//
            if (s.gzhead.hcrc && s.pending > _beg) {
              strm.adler = crc32_1(strm.adler, s.pending_buf, s.pending - _beg, _beg);
            }
            //---//
            flush_pending(strm);
            if (s.pending !== 0) {
              s.last_flush = -1;
              return Z_OK$3;
            }
            _beg = 0;
          }
          // JS specific: little magic to add zero terminator to end of string
          if (s.gzindex < s.gzhead.name.length) {
            val = s.gzhead.name.charCodeAt(s.gzindex++) & 0xff;
          } else {
            val = 0;
          }
          put_byte(s, val);
        } while (val !== 0);
        //--- HCRC_UPDATE(beg) ---//
        if (s.gzhead.hcrc && s.pending > _beg) {
          strm.adler = crc32_1(strm.adler, s.pending_buf, s.pending - _beg, _beg);
        }
        //---//
        s.gzindex = 0;
      }
      s.status = COMMENT_STATE;
    }
    if (s.status === COMMENT_STATE) {
      if (s.gzhead.comment /* != Z_NULL*/) {
        var _beg2 = s.pending; /* start of bytes to update crc */
        var _val;
        do {
          if (s.pending === s.pending_buf_size) {
            //--- HCRC_UPDATE(beg) ---//
            if (s.gzhead.hcrc && s.pending > _beg2) {
              strm.adler = crc32_1(strm.adler, s.pending_buf, s.pending - _beg2, _beg2);
            }
            //---//
            flush_pending(strm);
            if (s.pending !== 0) {
              s.last_flush = -1;
              return Z_OK$3;
            }
            _beg2 = 0;
          }
          // JS specific: little magic to add zero terminator to end of string
          if (s.gzindex < s.gzhead.comment.length) {
            _val = s.gzhead.comment.charCodeAt(s.gzindex++) & 0xff;
          } else {
            _val = 0;
          }
          put_byte(s, _val);
        } while (_val !== 0);
        //--- HCRC_UPDATE(beg) ---//
        if (s.gzhead.hcrc && s.pending > _beg2) {
          strm.adler = crc32_1(strm.adler, s.pending_buf, s.pending - _beg2, _beg2);
        }
        //---//
      }

      s.status = HCRC_STATE;
    }
    if (s.status === HCRC_STATE) {
      if (s.gzhead.hcrc) {
        if (s.pending + 2 > s.pending_buf_size) {
          flush_pending(strm);
          if (s.pending !== 0) {
            s.last_flush = -1;
            return Z_OK$3;
          }
        }
        put_byte(s, strm.adler & 0xff);
        put_byte(s, strm.adler >> 8 & 0xff);
        strm.adler = 0; //crc32(0L, Z_NULL, 0);
      }

      s.status = BUSY_STATE;

      /* Compression must start with an empty pending buffer */
      flush_pending(strm);
      if (s.pending !== 0) {
        s.last_flush = -1;
        return Z_OK$3;
      }
    }
    //#endif

    /* Start a new block or continue the current one.
     */
    if (strm.avail_in !== 0 || s.lookahead !== 0 || flush !== Z_NO_FLUSH$2 && s.status !== FINISH_STATE) {
      var bstate = s.level === 0 ? deflate_stored(s, flush) : s.strategy === Z_HUFFMAN_ONLY ? deflate_huff(s, flush) : s.strategy === Z_RLE ? deflate_rle(s, flush) : configuration_table[s.level].func(s, flush);
      if (bstate === BS_FINISH_STARTED || bstate === BS_FINISH_DONE) {
        s.status = FINISH_STATE;
      }
      if (bstate === BS_NEED_MORE || bstate === BS_FINISH_STARTED) {
        if (strm.avail_out === 0) {
          s.last_flush = -1;
          /* avoid BUF_ERROR next call, see above */
        }

        return Z_OK$3;
        /* If flush != Z_NO_FLUSH && avail_out == 0, the next call
         * of deflate should use the same flush parameter to make sure
         * that the flush is complete. So we don't have to output an
         * empty block here, this will be done at next call. This also
         * ensures that for a very small output buffer, we emit at most
         * one empty block.
         */
      }

      if (bstate === BS_BLOCK_DONE) {
        if (flush === Z_PARTIAL_FLUSH) {
          _tr_align(s);
        } else if (flush !== Z_BLOCK$1) {
          /* FULL_FLUSH or SYNC_FLUSH */

          _tr_stored_block(s, 0, 0, false);
          /* For a full flush, this empty block will be recognized
           * as a special marker by inflate_sync().
           */
          if (flush === Z_FULL_FLUSH$1) {
            /*** CLEAR_HASH(s); ***/ /* forget history */
            zero(s.head); // Fill with NIL (= 0);

            if (s.lookahead === 0) {
              s.strstart = 0;
              s.block_start = 0;
              s.insert = 0;
            }
          }
        }
        flush_pending(strm);
        if (strm.avail_out === 0) {
          s.last_flush = -1; /* avoid BUF_ERROR at next call, see above */
          return Z_OK$3;
        }
      }
    }
    if (flush !== Z_FINISH$3) {
      return Z_OK$3;
    }
    if (s.wrap <= 0) {
      return Z_STREAM_END$3;
    }

    /* Write the trailer */
    if (s.wrap === 2) {
      put_byte(s, strm.adler & 0xff);
      put_byte(s, strm.adler >> 8 & 0xff);
      put_byte(s, strm.adler >> 16 & 0xff);
      put_byte(s, strm.adler >> 24 & 0xff);
      put_byte(s, strm.total_in & 0xff);
      put_byte(s, strm.total_in >> 8 & 0xff);
      put_byte(s, strm.total_in >> 16 & 0xff);
      put_byte(s, strm.total_in >> 24 & 0xff);
    } else {
      putShortMSB(s, strm.adler >>> 16);
      putShortMSB(s, strm.adler & 0xffff);
    }
    flush_pending(strm);
    /* If avail_out is zero, the application will call deflate again
     * to flush the rest.
     */
    if (s.wrap > 0) {
      s.wrap = -s.wrap;
    }
    /* write the trailer only once! */
    return s.pending !== 0 ? Z_OK$3 : Z_STREAM_END$3;
  };
  var deflateEnd = function deflateEnd(strm) {
    if (deflateStateCheck(strm)) {
      return Z_STREAM_ERROR$2;
    }
    var status = strm.state.status;
    strm.state = null;
    return status === BUSY_STATE ? err(strm, Z_DATA_ERROR$2) : Z_OK$3;
  };

  /* =========================================================================
   * Initializes the compression dictionary from the given byte
   * sequence without producing any compressed output.
   */
  var deflateSetDictionary = function deflateSetDictionary(strm, dictionary) {
    var dictLength = dictionary.length;
    if (deflateStateCheck(strm)) {
      return Z_STREAM_ERROR$2;
    }
    var s = strm.state;
    var wrap = s.wrap;
    if (wrap === 2 || wrap === 1 && s.status !== INIT_STATE || s.lookahead) {
      return Z_STREAM_ERROR$2;
    }

    /* when using zlib wrappers, compute Adler-32 for provided dictionary */
    if (wrap === 1) {
      /* adler32(strm->adler, dictionary, dictLength); */
      strm.adler = adler32_1(strm.adler, dictionary, dictLength, 0);
    }
    s.wrap = 0; /* avoid computing Adler-32 in read_buf */

    /* if dictionary would fill window, just replace the history */
    if (dictLength >= s.w_size) {
      if (wrap === 0) {
        /* already empty otherwise */
        /*** CLEAR_HASH(s); ***/
        zero(s.head); // Fill with NIL (= 0);
        s.strstart = 0;
        s.block_start = 0;
        s.insert = 0;
      }
      /* use the tail */
      // dictionary = dictionary.slice(dictLength - s.w_size);
      var tmpDict = new Uint8Array(s.w_size);
      tmpDict.set(dictionary.subarray(dictLength - s.w_size, dictLength), 0);
      dictionary = tmpDict;
      dictLength = s.w_size;
    }
    /* insert dictionary into window and hash */
    var avail = strm.avail_in;
    var next = strm.next_in;
    var input = strm.input;
    strm.avail_in = dictLength;
    strm.next_in = 0;
    strm.input = dictionary;
    fill_window(s);
    while (s.lookahead >= MIN_MATCH) {
      var str = s.strstart;
      var n = s.lookahead - (MIN_MATCH - 1);
      do {
        /* UPDATE_HASH(s, s->ins_h, s->window[str + MIN_MATCH-1]); */
        s.ins_h = HASH(s, s.ins_h, s.window[str + MIN_MATCH - 1]);
        s.prev[str & s.w_mask] = s.head[s.ins_h];
        s.head[s.ins_h] = str;
        str++;
      } while (--n);
      s.strstart = str;
      s.lookahead = MIN_MATCH - 1;
      fill_window(s);
    }
    s.strstart += s.lookahead;
    s.block_start = s.strstart;
    s.insert = s.lookahead;
    s.lookahead = 0;
    s.match_length = s.prev_length = MIN_MATCH - 1;
    s.match_available = 0;
    strm.next_in = next;
    strm.input = input;
    strm.avail_in = avail;
    s.wrap = wrap;
    return Z_OK$3;
  };
  var deflateInit_1 = deflateInit;
  var deflateInit2_1 = deflateInit2;
  var deflateReset_1 = deflateReset;
  var deflateResetKeep_1 = deflateResetKeep;
  var deflateSetHeader_1 = deflateSetHeader;
  var deflate_2$1 = deflate$2;
  var deflateEnd_1 = deflateEnd;
  var deflateSetDictionary_1 = deflateSetDictionary;
  var deflateInfo = 'pako deflate (from Nodeca project)';

  /* Not implemented
  module.exports.deflateBound = deflateBound;
  module.exports.deflateCopy = deflateCopy;
  module.exports.deflateGetDictionary = deflateGetDictionary;
  module.exports.deflateParams = deflateParams;
  module.exports.deflatePending = deflatePending;
  module.exports.deflatePrime = deflatePrime;
  module.exports.deflateTune = deflateTune;
  */

  var deflate_1$2 = {
    deflateInit: deflateInit_1,
    deflateInit2: deflateInit2_1,
    deflateReset: deflateReset_1,
    deflateResetKeep: deflateResetKeep_1,
    deflateSetHeader: deflateSetHeader_1,
    deflate: deflate_2$1,
    deflateEnd: deflateEnd_1,
    deflateSetDictionary: deflateSetDictionary_1,
    deflateInfo: deflateInfo
  };

  function _typeof(obj) {
    "@babel/helpers - typeof";

    return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
      return typeof obj;
    } : function (obj) {
      return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    }, _typeof(obj);
  }

  var _has = function _has(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  };
  var assign = function assign(obj /*from1, from2, from3, ...*/) {
    var sources = Array.prototype.slice.call(arguments, 1);
    while (sources.length) {
      var source = sources.shift();
      if (!source) {
        continue;
      }
      if (_typeof(source) !== 'object') {
        throw new TypeError(source + 'must be non-object');
      }
      for (var p in source) {
        if (_has(source, p)) {
          obj[p] = source[p];
        }
      }
    }
    return obj;
  };

  // Join array of chunks to single array.
  var flattenChunks = function flattenChunks(chunks) {
    // calculate data length
    var len = 0;
    for (var i = 0, l = chunks.length; i < l; i++) {
      len += chunks[i].length;
    }

    // join chunks
    var result = new Uint8Array(len);
    for (var _i = 0, pos = 0, _l = chunks.length; _i < _l; _i++) {
      var chunk = chunks[_i];
      result.set(chunk, pos);
      pos += chunk.length;
    }
    return result;
  };
  var common = {
    assign: assign,
    flattenChunks: flattenChunks
  };

  // String encode/decode helpers

  // Quick check if we can use fast array to bin string conversion
  //
  // - apply(Array) can fail on Android 2.2
  // - apply(Uint8Array) can fail on iOS 5.1 Safari
  //
  var STR_APPLY_UIA_OK = true;
  try {
    String.fromCharCode.apply(null, new Uint8Array(1));
  } catch (__) {
    STR_APPLY_UIA_OK = false;
  }

  // Table with utf8 lengths (calculated by first byte of sequence)
  // Note, that 5 & 6-byte values and some 4-byte values can not be represented in JS,
  // because max possible codepoint is 0x10ffff
  var _utf8len = new Uint8Array(256);
  for (var q = 0; q < 256; q++) {
    _utf8len[q] = q >= 252 ? 6 : q >= 248 ? 5 : q >= 240 ? 4 : q >= 224 ? 3 : q >= 192 ? 2 : 1;
  }
  _utf8len[254] = _utf8len[254] = 1; // Invalid sequence start

  // convert string to array (typed, when possible)
  var string2buf = function string2buf(str) {
    if (typeof TextEncoder === 'function' && TextEncoder.prototype.encode) {
      return new TextEncoder().encode(str);
    }
    var buf,
      c,
      c2,
      m_pos,
      i,
      str_len = str.length,
      buf_len = 0;

    // count binary size
    for (m_pos = 0; m_pos < str_len; m_pos++) {
      c = str.charCodeAt(m_pos);
      if ((c & 0xfc00) === 0xd800 && m_pos + 1 < str_len) {
        c2 = str.charCodeAt(m_pos + 1);
        if ((c2 & 0xfc00) === 0xdc00) {
          c = 0x10000 + (c - 0xd800 << 10) + (c2 - 0xdc00);
          m_pos++;
        }
      }
      buf_len += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
    }

    // allocate buffer
    buf = new Uint8Array(buf_len);

    // convert
    for (i = 0, m_pos = 0; i < buf_len; m_pos++) {
      c = str.charCodeAt(m_pos);
      if ((c & 0xfc00) === 0xd800 && m_pos + 1 < str_len) {
        c2 = str.charCodeAt(m_pos + 1);
        if ((c2 & 0xfc00) === 0xdc00) {
          c = 0x10000 + (c - 0xd800 << 10) + (c2 - 0xdc00);
          m_pos++;
        }
      }
      if (c < 0x80) {
        /* one byte */
        buf[i++] = c;
      } else if (c < 0x800) {
        /* two bytes */
        buf[i++] = 0xC0 | c >>> 6;
        buf[i++] = 0x80 | c & 0x3f;
      } else if (c < 0x10000) {
        /* three bytes */
        buf[i++] = 0xE0 | c >>> 12;
        buf[i++] = 0x80 | c >>> 6 & 0x3f;
        buf[i++] = 0x80 | c & 0x3f;
      } else {
        /* four bytes */
        buf[i++] = 0xf0 | c >>> 18;
        buf[i++] = 0x80 | c >>> 12 & 0x3f;
        buf[i++] = 0x80 | c >>> 6 & 0x3f;
        buf[i++] = 0x80 | c & 0x3f;
      }
    }
    return buf;
  };

  // Helper
  var buf2binstring = function buf2binstring(buf, len) {
    // On Chrome, the arguments in a function call that are allowed is `65534`.
    // If the length of the buffer is smaller than that, we can use this optimization,
    // otherwise we will take a slower path.
    if (len < 65534) {
      if (buf.subarray && STR_APPLY_UIA_OK) {
        return String.fromCharCode.apply(null, buf.length === len ? buf : buf.subarray(0, len));
      }
    }
    var result = '';
    for (var i = 0; i < len; i++) {
      result += String.fromCharCode(buf[i]);
    }
    return result;
  };

  // convert array to string
  var buf2string = function buf2string(buf, max) {
    var len = max || buf.length;
    if (typeof TextDecoder === 'function' && TextDecoder.prototype.decode) {
      return new TextDecoder().decode(buf.subarray(0, max));
    }
    var i, out;

    // Reserve max possible length (2 words per char)
    // NB: by unknown reasons, Array is significantly faster for
    //     String.fromCharCode.apply than Uint16Array.
    var utf16buf = new Array(len * 2);
    for (out = 0, i = 0; i < len;) {
      var c = buf[i++];
      // quick process ascii
      if (c < 0x80) {
        utf16buf[out++] = c;
        continue;
      }
      var c_len = _utf8len[c];
      // skip 5 & 6 byte codes
      if (c_len > 4) {
        utf16buf[out++] = 0xfffd;
        i += c_len - 1;
        continue;
      }

      // apply mask on first byte
      c &= c_len === 2 ? 0x1f : c_len === 3 ? 0x0f : 0x07;
      // join the rest
      while (c_len > 1 && i < len) {
        c = c << 6 | buf[i++] & 0x3f;
        c_len--;
      }

      // terminated by end of string?
      if (c_len > 1) {
        utf16buf[out++] = 0xfffd;
        continue;
      }
      if (c < 0x10000) {
        utf16buf[out++] = c;
      } else {
        c -= 0x10000;
        utf16buf[out++] = 0xd800 | c >> 10 & 0x3ff;
        utf16buf[out++] = 0xdc00 | c & 0x3ff;
      }
    }
    return buf2binstring(utf16buf, out);
  };

  // Calculate max possible position in utf8 buffer,
  // that will not break sequence. If that's not possible
  // - (very small limits) return max size as is.
  //
  // buf[] - utf8 bytes array
  // max   - length limit (mandatory);
  var utf8border = function utf8border(buf, max) {
    max = max || buf.length;
    if (max > buf.length) {
      max = buf.length;
    }

    // go back from last position, until start of sequence found
    var pos = max - 1;
    while (pos >= 0 && (buf[pos] & 0xC0) === 0x80) {
      pos--;
    }

    // Very small and broken sequence,
    // return max, because we should return something anyway.
    if (pos < 0) {
      return max;
    }

    // If we came to start of buffer - that means buffer is too small,
    // return max too.
    if (pos === 0) {
      return max;
    }
    return pos + _utf8len[buf[pos]] > max ? pos : max;
  };
  var strings = {
    string2buf: string2buf,
    buf2string: buf2string,
    utf8border: utf8border
  };

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.
  function ZStream() {
    /* next input byte */
    this.input = null; // JS specific, because we have no pointers
    this.next_in = 0;
    /* number of bytes available at input */
    this.avail_in = 0;
    /* total number of input bytes read so far */
    this.total_in = 0;
    /* next output byte should be put there */
    this.output = null; // JS specific, because we have no pointers
    this.next_out = 0;
    /* remaining free space at output */
    this.avail_out = 0;
    /* total number of bytes output so far */
    this.total_out = 0;
    /* last error message, NULL if no error */
    this.msg = '' /*Z_NULL*/;
    /* not visible by applications */
    this.state = null;
    /* best guess about the data type: binary or text */
    this.data_type = 2 /*Z_UNKNOWN*/;
    /* adler32 value of the uncompressed data */
    this.adler = 0;
  }
  var zstream = ZStream;

  var toString$1 = Object.prototype.toString;

  /* Public constants ==========================================================*/
  /* ===========================================================================*/

  var Z_NO_FLUSH$1 = constants$2.Z_NO_FLUSH,
    Z_SYNC_FLUSH = constants$2.Z_SYNC_FLUSH,
    Z_FULL_FLUSH = constants$2.Z_FULL_FLUSH,
    Z_FINISH$2 = constants$2.Z_FINISH,
    Z_OK$2 = constants$2.Z_OK,
    Z_STREAM_END$2 = constants$2.Z_STREAM_END,
    Z_DEFAULT_COMPRESSION = constants$2.Z_DEFAULT_COMPRESSION,
    Z_DEFAULT_STRATEGY = constants$2.Z_DEFAULT_STRATEGY,
    Z_DEFLATED$1 = constants$2.Z_DEFLATED;

  /* ===========================================================================*/

  /**
   * class Deflate
   *
   * Generic JS-style wrapper for zlib calls. If you don't need
   * streaming behaviour - use more simple functions: [[deflate]],
   * [[deflateRaw]] and [[gzip]].
   **/

  /* internal
   * Deflate.chunks -> Array
   *
   * Chunks of output data, if [[Deflate#onData]] not overridden.
   **/

  /**
   * Deflate.result -> Uint8Array
   *
   * Compressed result, generated by default [[Deflate#onData]]
   * and [[Deflate#onEnd]] handlers. Filled after you push last chunk
   * (call [[Deflate#push]] with `Z_FINISH` / `true` param).
   **/

  /**
   * Deflate.err -> Number
   *
   * Error code after deflate finished. 0 (Z_OK) on success.
   * You will not need it in real life, because deflate errors
   * are possible only on wrong options or bad `onData` / `onEnd`
   * custom handlers.
   **/

  /**
   * Deflate.msg -> String
   *
   * Error message, if [[Deflate.err]] != 0
   **/

  /**
   * new Deflate(options)
   * - options (Object): zlib deflate options.
   *
   * Creates new deflator instance with specified params. Throws exception
   * on bad params. Supported options:
   *
   * - `level`
   * - `windowBits`
   * - `memLevel`
   * - `strategy`
   * - `dictionary`
   *
   * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
   * for more information on these.
   *
   * Additional options, for internal needs:
   *
   * - `chunkSize` - size of generated data chunks (16K by default)
   * - `raw` (Boolean) - do raw deflate
   * - `gzip` (Boolean) - create gzip wrapper
   * - `header` (Object) - custom header for gzip
   *   - `text` (Boolean) - true if compressed data believed to be text
   *   - `time` (Number) - modification time, unix timestamp
   *   - `os` (Number) - operation system code
   *   - `extra` (Array) - array of bytes with extra data (max 65536)
   *   - `name` (String) - file name (binary string)
   *   - `comment` (String) - comment (binary string)
   *   - `hcrc` (Boolean) - true if header crc should be added
   *
   * ##### Example:
   *
   * ```javascript
   * const pako = require('pako')
   *   , chunk1 = new Uint8Array([1,2,3,4,5,6,7,8,9])
   *   , chunk2 = new Uint8Array([10,11,12,13,14,15,16,17,18,19]);
   *
   * const deflate = new pako.Deflate({ level: 3});
   *
   * deflate.push(chunk1, false);
   * deflate.push(chunk2, true);  // true -> last chunk
   *
   * if (deflate.err) { throw new Error(deflate.err); }
   *
   * console.log(deflate.result);
   * ```
   **/
  function Deflate$1(options) {
    this.options = common.assign({
      level: Z_DEFAULT_COMPRESSION,
      method: Z_DEFLATED$1,
      chunkSize: 16384,
      windowBits: 15,
      memLevel: 8,
      strategy: Z_DEFAULT_STRATEGY
    }, options || {});
    var opt = this.options;
    if (opt.raw && opt.windowBits > 0) {
      opt.windowBits = -opt.windowBits;
    } else if (opt.gzip && opt.windowBits > 0 && opt.windowBits < 16) {
      opt.windowBits += 16;
    }
    this.err = 0; // error code, if happens (0 = Z_OK)
    this.msg = ''; // error message
    this.ended = false; // used to avoid multiple onEnd() calls
    this.chunks = []; // chunks of compressed data

    this.strm = new zstream();
    this.strm.avail_out = 0;
    var status = deflate_1$2.deflateInit2(this.strm, opt.level, opt.method, opt.windowBits, opt.memLevel, opt.strategy);
    if (status !== Z_OK$2) {
      throw new Error(messages[status]);
    }
    if (opt.header) {
      deflate_1$2.deflateSetHeader(this.strm, opt.header);
    }
    if (opt.dictionary) {
      var dict;
      // Convert data if needed
      if (typeof opt.dictionary === 'string') {
        // If we need to compress text, change encoding to utf8.
        dict = strings.string2buf(opt.dictionary);
      } else if (toString$1.call(opt.dictionary) === '[object ArrayBuffer]') {
        dict = new Uint8Array(opt.dictionary);
      } else {
        dict = opt.dictionary;
      }
      status = deflate_1$2.deflateSetDictionary(this.strm, dict);
      if (status !== Z_OK$2) {
        throw new Error(messages[status]);
      }
      this._dict_set = true;
    }
  }

  /**
   * Deflate#push(data[, flush_mode]) -> Boolean
   * - data (Uint8Array|ArrayBuffer|String): input data. Strings will be
   *   converted to utf8 byte sequence.
   * - flush_mode (Number|Boolean): 0..6 for corresponding Z_NO_FLUSH..Z_TREE modes.
   *   See constants. Skipped or `false` means Z_NO_FLUSH, `true` means Z_FINISH.
   *
   * Sends input data to deflate pipe, generating [[Deflate#onData]] calls with
   * new compressed chunks. Returns `true` on success. The last data block must
   * have `flush_mode` Z_FINISH (or `true`). That will flush internal pending
   * buffers and call [[Deflate#onEnd]].
   *
   * On fail call [[Deflate#onEnd]] with error code and return false.
   *
   * ##### Example
   *
   * ```javascript
   * push(chunk, false); // push one of data chunks
   * ...
   * push(chunk, true);  // push last chunk
   * ```
   **/
  Deflate$1.prototype.push = function (data, flush_mode) {
    var strm = this.strm;
    var chunkSize = this.options.chunkSize;
    var status, _flush_mode;
    if (this.ended) {
      return false;
    }
    if (flush_mode === ~~flush_mode) _flush_mode = flush_mode;else _flush_mode = flush_mode === true ? Z_FINISH$2 : Z_NO_FLUSH$1;

    // Convert data if needed
    if (typeof data === 'string') {
      // If we need to compress text, change encoding to utf8.
      strm.input = strings.string2buf(data);
    } else if (toString$1.call(data) === '[object ArrayBuffer]') {
      strm.input = new Uint8Array(data);
    } else {
      strm.input = data;
    }
    strm.next_in = 0;
    strm.avail_in = strm.input.length;
    for (;;) {
      if (strm.avail_out === 0) {
        strm.output = new Uint8Array(chunkSize);
        strm.next_out = 0;
        strm.avail_out = chunkSize;
      }

      // Make sure avail_out > 6 to avoid repeating markers
      if ((_flush_mode === Z_SYNC_FLUSH || _flush_mode === Z_FULL_FLUSH) && strm.avail_out <= 6) {
        this.onData(strm.output.subarray(0, strm.next_out));
        strm.avail_out = 0;
        continue;
      }
      status = deflate_1$2.deflate(strm, _flush_mode);

      // Ended => flush and finish
      if (status === Z_STREAM_END$2) {
        if (strm.next_out > 0) {
          this.onData(strm.output.subarray(0, strm.next_out));
        }
        status = deflate_1$2.deflateEnd(this.strm);
        this.onEnd(status);
        this.ended = true;
        return status === Z_OK$2;
      }

      // Flush if out buffer full
      if (strm.avail_out === 0) {
        this.onData(strm.output);
        continue;
      }

      // Flush if requested and has data
      if (_flush_mode > 0 && strm.next_out > 0) {
        this.onData(strm.output.subarray(0, strm.next_out));
        strm.avail_out = 0;
        continue;
      }
      if (strm.avail_in === 0) break;
    }
    return true;
  };

  /**
   * Deflate#onData(chunk) -> Void
   * - chunk (Uint8Array): output data.
   *
   * By default, stores data blocks in `chunks[]` property and glue
   * those in `onEnd`. Override this handler, if you need another behaviour.
   **/
  Deflate$1.prototype.onData = function (chunk) {
    this.chunks.push(chunk);
  };

  /**
   * Deflate#onEnd(status) -> Void
   * - status (Number): deflate status. 0 (Z_OK) on success,
   *   other if not.
   *
   * Called once after you tell deflate that the input stream is
   * complete (Z_FINISH). By default - join collected chunks,
   * free memory and fill `results` / `err` properties.
   **/
  Deflate$1.prototype.onEnd = function (status) {
    // On success - join
    if (status === Z_OK$2) {
      this.result = common.flattenChunks(this.chunks);
    }
    this.chunks = [];
    this.err = status;
    this.msg = this.strm.msg;
  };

  /**
   * deflate(data[, options]) -> Uint8Array
   * - data (Uint8Array|ArrayBuffer|String): input data to compress.
   * - options (Object): zlib deflate options.
   *
   * Compress `data` with deflate algorithm and `options`.
   *
   * Supported options are:
   *
   * - level
   * - windowBits
   * - memLevel
   * - strategy
   * - dictionary
   *
   * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
   * for more information on these.
   *
   * Sugar (options):
   *
   * - `raw` (Boolean) - say that we work with raw stream, if you don't wish to specify
   *   negative windowBits implicitly.
   *
   * ##### Example:
   *
   * ```javascript
   * const pako = require('pako')
   * const data = new Uint8Array([1,2,3,4,5,6,7,8,9]);
   *
   * console.log(pako.deflate(data));
   * ```
   **/
  function deflate$1(input, options) {
    var deflator = new Deflate$1(options);
    deflator.push(input, true);

    // That will never happens, if you don't cheat with options :)
    if (deflator.err) {
      throw deflator.msg || messages[deflator.err];
    }
    return deflator.result;
  }

  /**
   * deflateRaw(data[, options]) -> Uint8Array
   * - data (Uint8Array|ArrayBuffer|String): input data to compress.
   * - options (Object): zlib deflate options.
   *
   * The same as [[deflate]], but creates raw data, without wrapper
   * (header and adler32 crc).
   **/
  function deflateRaw$1(input, options) {
    options = options || {};
    options.raw = true;
    return deflate$1(input, options);
  }

  /**
   * gzip(data[, options]) -> Uint8Array
   * - data (Uint8Array|ArrayBuffer|String): input data to compress.
   * - options (Object): zlib deflate options.
   *
   * The same as [[deflate]], but create gzip wrapper instead of
   * deflate one.
   **/
  function gzip$1(input, options) {
    options = options || {};
    options.gzip = true;
    return deflate$1(input, options);
  }
  var Deflate_1$1 = Deflate$1;
  var deflate_2 = deflate$1;
  var deflateRaw_1$1 = deflateRaw$1;
  var gzip_1$1 = gzip$1;
  var constants$1 = constants$2;
  var deflate_1$1 = {
    Deflate: Deflate_1$1,
    deflate: deflate_2,
    deflateRaw: deflateRaw_1$1,
    gzip: gzip_1$1,
    constants: constants$1
  };

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.

  // See state defs from inflate.js
  var BAD$1 = 16209; /* got a data error -- remain here until reset */
  var TYPE$1 = 16191; /* i: waiting for type bits, including last-flag bit */

  /*
     Decode literal, length, and distance codes and write out the resulting
     literal and match bytes until either not enough input or output is
     available, an end-of-block is encountered, or a data error is encountered.
     When large enough input and output buffers are supplied to inflate(), for
     example, a 16K input buffer and a 64K output buffer, more than 95% of the
     inflate execution time is spent in this routine.

     Entry assumptions:

          state.mode === LEN
          strm.avail_in >= 6
          strm.avail_out >= 258
          start >= strm.avail_out
          state.bits < 8

     On return, state.mode is one of:

          LEN -- ran out of enough output space or enough available input
          TYPE -- reached end of block code, inflate() to interpret next block
          BAD -- error in block data

     Notes:

      - The maximum input bits used by a length/distance pair is 15 bits for the
        length code, 5 bits for the length extra, 15 bits for the distance code,
        and 13 bits for the distance extra.  This totals 48 bits, or six bytes.
        Therefore if strm.avail_in >= 6, then there is enough input to avoid
        checking for available input while decoding.

      - The maximum bytes that a single length/distance pair can output is 258
        bytes, which is the maximum length that can be coded.  inflate_fast()
        requires strm.avail_out >= 258 for each loop to avoid checking for
        output space.
   */
  var inffast = function inflate_fast(strm, start) {
    var _in; /* local strm.input */
    var last; /* have enough input while in < last */
    var _out; /* local strm.output */
    var beg; /* inflate()'s initial strm.output */
    var end; /* while out < end, enough space available */
    //#ifdef INFLATE_STRICT
    var dmax; /* maximum distance from zlib header */
    //#endif
    var wsize; /* window size or zero if not using window */
    var whave; /* valid bytes in the window */
    var wnext; /* window write index */
    // Use `s_window` instead `window`, avoid conflict with instrumentation tools
    var s_window; /* allocated sliding window, if wsize != 0 */
    var hold; /* local strm.hold */
    var bits; /* local strm.bits */
    var lcode; /* local strm.lencode */
    var dcode; /* local strm.distcode */
    var lmask; /* mask for first level of length codes */
    var dmask; /* mask for first level of distance codes */
    var here; /* retrieved table entry */
    var op; /* code bits, operation, extra bits, or */
    /*  window position, window bytes to copy */
    var len; /* match length, unused bytes */
    var dist; /* match distance */
    var from; /* where to copy match from */
    var from_source;
    var input, output; // JS specific, because we have no pointers

    /* copy state to local variables */
    var state = strm.state;
    //here = state.here;
    _in = strm.next_in;
    input = strm.input;
    last = _in + (strm.avail_in - 5);
    _out = strm.next_out;
    output = strm.output;
    beg = _out - (start - strm.avail_out);
    end = _out + (strm.avail_out - 257);
    //#ifdef INFLATE_STRICT
    dmax = state.dmax;
    //#endif
    wsize = state.wsize;
    whave = state.whave;
    wnext = state.wnext;
    s_window = state.window;
    hold = state.hold;
    bits = state.bits;
    lcode = state.lencode;
    dcode = state.distcode;
    lmask = (1 << state.lenbits) - 1;
    dmask = (1 << state.distbits) - 1;

    /* decode literals and length/distances until end-of-block or not enough
       input data or output space */

    top: do {
      if (bits < 15) {
        hold += input[_in++] << bits;
        bits += 8;
        hold += input[_in++] << bits;
        bits += 8;
      }
      here = lcode[hold & lmask];
      dolen: for (;;) {
        // Goto emulation
        op = here >>> 24 /*here.bits*/;
        hold >>>= op;
        bits -= op;
        op = here >>> 16 & 0xff /*here.op*/;
        if (op === 0) {
          /* literal */
          //Tracevv((stderr, here.val >= 0x20 && here.val < 0x7f ?
          //        "inflate:         literal '%c'\n" :
          //        "inflate:         literal 0x%02x\n", here.val));
          output[_out++] = here & 0xffff /*here.val*/;
        } else if (op & 16) {
          /* length base */
          len = here & 0xffff /*here.val*/;
          op &= 15; /* number of extra bits */
          if (op) {
            if (bits < op) {
              hold += input[_in++] << bits;
              bits += 8;
            }
            len += hold & (1 << op) - 1;
            hold >>>= op;
            bits -= op;
          }
          //Tracevv((stderr, "inflate:         length %u\n", len));
          if (bits < 15) {
            hold += input[_in++] << bits;
            bits += 8;
            hold += input[_in++] << bits;
            bits += 8;
          }
          here = dcode[hold & dmask];
          dodist: for (;;) {
            // goto emulation
            op = here >>> 24 /*here.bits*/;
            hold >>>= op;
            bits -= op;
            op = here >>> 16 & 0xff /*here.op*/;

            if (op & 16) {
              /* distance base */
              dist = here & 0xffff /*here.val*/;
              op &= 15; /* number of extra bits */
              if (bits < op) {
                hold += input[_in++] << bits;
                bits += 8;
                if (bits < op) {
                  hold += input[_in++] << bits;
                  bits += 8;
                }
              }
              dist += hold & (1 << op) - 1;
              //#ifdef INFLATE_STRICT
              if (dist > dmax) {
                strm.msg = 'invalid distance too far back';
                state.mode = BAD$1;
                break top;
              }
              //#endif
              hold >>>= op;
              bits -= op;
              //Tracevv((stderr, "inflate:         distance %u\n", dist));
              op = _out - beg; /* max distance in output */
              if (dist > op) {
                /* see if copy from window */
                op = dist - op; /* distance back in window */
                if (op > whave) {
                  if (state.sane) {
                    strm.msg = 'invalid distance too far back';
                    state.mode = BAD$1;
                    break top;
                  }

                  // (!) This block is disabled in zlib defaults,
                  // don't enable it for binary compatibility
                  //#ifdef INFLATE_ALLOW_INVALID_DISTANCE_TOOFAR_ARRR
                  //                if (len <= op - whave) {
                  //                  do {
                  //                    output[_out++] = 0;
                  //                  } while (--len);
                  //                  continue top;
                  //                }
                  //                len -= op - whave;
                  //                do {
                  //                  output[_out++] = 0;
                  //                } while (--op > whave);
                  //                if (op === 0) {
                  //                  from = _out - dist;
                  //                  do {
                  //                    output[_out++] = output[from++];
                  //                  } while (--len);
                  //                  continue top;
                  //                }
                  //#endif
                }

                from = 0; // window index
                from_source = s_window;
                if (wnext === 0) {
                  /* very common case */
                  from += wsize - op;
                  if (op < len) {
                    /* some from window */
                    len -= op;
                    do {
                      output[_out++] = s_window[from++];
                    } while (--op);
                    from = _out - dist; /* rest from output */
                    from_source = output;
                  }
                } else if (wnext < op) {
                  /* wrap around window */
                  from += wsize + wnext - op;
                  op -= wnext;
                  if (op < len) {
                    /* some from end of window */
                    len -= op;
                    do {
                      output[_out++] = s_window[from++];
                    } while (--op);
                    from = 0;
                    if (wnext < len) {
                      /* some from start of window */
                      op = wnext;
                      len -= op;
                      do {
                        output[_out++] = s_window[from++];
                      } while (--op);
                      from = _out - dist; /* rest from output */
                      from_source = output;
                    }
                  }
                } else {
                  /* contiguous in window */
                  from += wnext - op;
                  if (op < len) {
                    /* some from window */
                    len -= op;
                    do {
                      output[_out++] = s_window[from++];
                    } while (--op);
                    from = _out - dist; /* rest from output */
                    from_source = output;
                  }
                }
                while (len > 2) {
                  output[_out++] = from_source[from++];
                  output[_out++] = from_source[from++];
                  output[_out++] = from_source[from++];
                  len -= 3;
                }
                if (len) {
                  output[_out++] = from_source[from++];
                  if (len > 1) {
                    output[_out++] = from_source[from++];
                  }
                }
              } else {
                from = _out - dist; /* copy direct from output */
                do {
                  /* minimum length is three */
                  output[_out++] = output[from++];
                  output[_out++] = output[from++];
                  output[_out++] = output[from++];
                  len -= 3;
                } while (len > 2);
                if (len) {
                  output[_out++] = output[from++];
                  if (len > 1) {
                    output[_out++] = output[from++];
                  }
                }
              }
            } else if ((op & 64) === 0) {
              /* 2nd level distance code */
              here = dcode[(here & 0xffff /*here.val*/) + (hold & (1 << op) - 1)];
              continue dodist;
            } else {
              strm.msg = 'invalid distance code';
              state.mode = BAD$1;
              break top;
            }
            break; // need to emulate goto via "continue"
          }
        } else if ((op & 64) === 0) {
          /* 2nd level length code */
          here = lcode[(here & 0xffff /*here.val*/) + (hold & (1 << op) - 1)];
          continue dolen;
        } else if (op & 32) {
          /* end-of-block */
          //Tracevv((stderr, "inflate:         end of block\n"));
          state.mode = TYPE$1;
          break top;
        } else {
          strm.msg = 'invalid literal/length code';
          state.mode = BAD$1;
          break top;
        }
        break; // need to emulate goto via "continue"
      }
    } while (_in < last && _out < end);

    /* return unused bytes (on entry, bits < 8, so in won't go too far back) */
    len = bits >> 3;
    _in -= len;
    bits -= len << 3;
    hold &= (1 << bits) - 1;

    /* update state and return */
    strm.next_in = _in;
    strm.next_out = _out;
    strm.avail_in = _in < last ? 5 + (last - _in) : 5 - (_in - last);
    strm.avail_out = _out < end ? 257 + (end - _out) : 257 - (_out - end);
    state.hold = hold;
    state.bits = bits;
    return;
  };

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.
  var MAXBITS = 15;
  var ENOUGH_LENS$1 = 852;
  var ENOUGH_DISTS$1 = 592;
  //const ENOUGH = (ENOUGH_LENS+ENOUGH_DISTS);

  var CODES$1 = 0;
  var LENS$1 = 1;
  var DISTS$1 = 2;
  var lbase = new Uint16Array([/* Length codes 257..285 base */
  3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258, 0, 0]);
  var lext = new Uint8Array([/* Length codes 257..285 extra */
  16, 16, 16, 16, 16, 16, 16, 16, 17, 17, 17, 17, 18, 18, 18, 18, 19, 19, 19, 19, 20, 20, 20, 20, 21, 21, 21, 21, 16, 72, 78]);
  var dbase = new Uint16Array([/* Distance codes 0..29 base */
  1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145, 8193, 12289, 16385, 24577, 0, 0]);
  var dext = new Uint8Array([/* Distance codes 0..29 extra */
  16, 16, 16, 16, 17, 17, 18, 18, 19, 19, 20, 20, 21, 21, 22, 22, 23, 23, 24, 24, 25, 25, 26, 26, 27, 27, 28, 28, 29, 29, 64, 64]);
  var inflate_table = function inflate_table(type, lens, lens_index, codes, table, table_index, work, opts) {
    var bits = opts.bits;
    //here = opts.here; /* table entry for duplication */

    var len = 0; /* a code's length in bits */
    var sym = 0; /* index of code symbols */
    var min = 0,
      max = 0; /* minimum and maximum code lengths */
    var root = 0; /* number of index bits for root table */
    var curr = 0; /* number of index bits for current table */
    var drop = 0; /* code bits to drop for sub-table */
    var left = 0; /* number of prefix codes available */
    var used = 0; /* code entries in table used */
    var huff = 0; /* Huffman code */
    var incr; /* for incrementing code, index */
    var fill; /* index for replicating entries */
    var low; /* low bits for current root entry */
    var mask; /* mask for low root bits */
    var next; /* next available space in table */
    var base = null; /* base value table to use */
    //  let shoextra;    /* extra bits table to use */
    var match; /* use base and extra for symbol >= match */
    var count = new Uint16Array(MAXBITS + 1); //[MAXBITS+1];    /* number of codes of each length */
    var offs = new Uint16Array(MAXBITS + 1); //[MAXBITS+1];     /* offsets in table for each length */
    var extra = null;
    var here_bits, here_op, here_val;

    /*
     Process a set of code lengths to create a canonical Huffman code.  The
     code lengths are lens[0..codes-1].  Each length corresponds to the
     symbols 0..codes-1.  The Huffman code is generated by first sorting the
     symbols by length from short to long, and retaining the symbol order
     for codes with equal lengths.  Then the code starts with all zero bits
     for the first code of the shortest length, and the codes are integer
     increments for the same length, and zeros are appended as the length
     increases.  For the deflate format, these bits are stored backwards
     from their more natural integer increment ordering, and so when the
     decoding tables are built in the large loop below, the integer codes
     are incremented backwards.
      This routine assumes, but does not check, that all of the entries in
     lens[] are in the range 0..MAXBITS.  The caller must assure this.
     1..MAXBITS is interpreted as that code length.  zero means that that
     symbol does not occur in this code.
      The codes are sorted by computing a count of codes for each length,
     creating from that a table of starting indices for each length in the
     sorted table, and then entering the symbols in order in the sorted
     table.  The sorted table is work[], with that space being provided by
     the caller.
      The length counts are used for other purposes as well, i.e. finding
     the minimum and maximum length codes, determining if there are any
     codes at all, checking for a valid set of lengths, and looking ahead
     at length counts to determine sub-table sizes when building the
     decoding tables.
     */

    /* accumulate lengths for codes (assumes lens[] all in 0..MAXBITS) */
    for (len = 0; len <= MAXBITS; len++) {
      count[len] = 0;
    }
    for (sym = 0; sym < codes; sym++) {
      count[lens[lens_index + sym]]++;
    }

    /* bound code lengths, force root to be within code lengths */
    root = bits;
    for (max = MAXBITS; max >= 1; max--) {
      if (count[max] !== 0) {
        break;
      }
    }
    if (root > max) {
      root = max;
    }
    if (max === 0) {
      /* no symbols to code at all */
      //table.op[opts.table_index] = 64;  //here.op = (var char)64;    /* invalid code marker */
      //table.bits[opts.table_index] = 1;   //here.bits = (var char)1;
      //table.val[opts.table_index++] = 0;   //here.val = (var short)0;
      table[table_index++] = 1 << 24 | 64 << 16 | 0;

      //table.op[opts.table_index] = 64;
      //table.bits[opts.table_index] = 1;
      //table.val[opts.table_index++] = 0;
      table[table_index++] = 1 << 24 | 64 << 16 | 0;
      opts.bits = 1;
      return 0; /* no symbols, but wait for decoding to report error */
    }

    for (min = 1; min < max; min++) {
      if (count[min] !== 0) {
        break;
      }
    }
    if (root < min) {
      root = min;
    }

    /* check for an over-subscribed or incomplete set of lengths */
    left = 1;
    for (len = 1; len <= MAXBITS; len++) {
      left <<= 1;
      left -= count[len];
      if (left < 0) {
        return -1;
      } /* over-subscribed */
    }

    if (left > 0 && (type === CODES$1 || max !== 1)) {
      return -1; /* incomplete set */
    }

    /* generate offsets into symbol table for each length for sorting */
    offs[1] = 0;
    for (len = 1; len < MAXBITS; len++) {
      offs[len + 1] = offs[len] + count[len];
    }

    /* sort symbols by length, by symbol order within each length */
    for (sym = 0; sym < codes; sym++) {
      if (lens[lens_index + sym] !== 0) {
        work[offs[lens[lens_index + sym]]++] = sym;
      }
    }

    /*
     Create and fill in decoding tables.  In this loop, the table being
     filled is at next and has curr index bits.  The code being used is huff
     with length len.  That code is converted to an index by dropping drop
     bits off of the bottom.  For codes where len is less than drop + curr,
     those top drop + curr - len bits are incremented through all values to
     fill the table with replicated entries.
      root is the number of index bits for the root table.  When len exceeds
     root, sub-tables are created pointed to by the root entry with an index
     of the low root bits of huff.  This is saved in low to check for when a
     new sub-table should be started.  drop is zero when the root table is
     being filled, and drop is root when sub-tables are being filled.
      When a new sub-table is needed, it is necessary to look ahead in the
     code lengths to determine what size sub-table is needed.  The length
     counts are used for this, and so count[] is decremented as codes are
     entered in the tables.
      used keeps track of how many table entries have been allocated from the
     provided *table space.  It is checked for LENS and DIST tables against
     the constants ENOUGH_LENS and ENOUGH_DISTS to guard against changes in
     the initial root table size constants.  See the comments in inftrees.h
     for more information.
      sym increments through all symbols, and the loop terminates when
     all codes of length max, i.e. all codes, have been processed.  This
     routine permits incomplete codes, so another loop after this one fills
     in the rest of the decoding tables with invalid code markers.
     */

    /* set up for code type */
    // poor man optimization - use if-else instead of switch,
    // to avoid deopts in old v8
    if (type === CODES$1) {
      base = extra = work; /* dummy value--not used */
      match = 20;
    } else if (type === LENS$1) {
      base = lbase;
      extra = lext;
      match = 257;
    } else {
      /* DISTS */
      base = dbase;
      extra = dext;
      match = 0;
    }

    /* initialize opts for loop */
    huff = 0; /* starting code */
    sym = 0; /* starting code symbol */
    len = min; /* starting code length */
    next = table_index; /* current table to fill in */
    curr = root; /* current table index bits */
    drop = 0; /* current bits to drop from code for index */
    low = -1; /* trigger new sub-table when len > root */
    used = 1 << root; /* use root table entries */
    mask = used - 1; /* mask for comparing low */

    /* check available table space */
    if (type === LENS$1 && used > ENOUGH_LENS$1 || type === DISTS$1 && used > ENOUGH_DISTS$1) {
      return 1;
    }

    /* process all codes and make table entries */
    for (;;) {
      /* create table entry */
      here_bits = len - drop;
      if (work[sym] + 1 < match) {
        here_op = 0;
        here_val = work[sym];
      } else if (work[sym] >= match) {
        here_op = extra[work[sym] - match];
        here_val = base[work[sym] - match];
      } else {
        here_op = 32 + 64; /* end of block */
        here_val = 0;
      }

      /* replicate for those indices with low len bits equal to huff */
      incr = 1 << len - drop;
      fill = 1 << curr;
      min = fill; /* save offset to next table */
      do {
        fill -= incr;
        table[next + (huff >> drop) + fill] = here_bits << 24 | here_op << 16 | here_val | 0;
      } while (fill !== 0);

      /* backwards increment the len-bit code huff */
      incr = 1 << len - 1;
      while (huff & incr) {
        incr >>= 1;
      }
      if (incr !== 0) {
        huff &= incr - 1;
        huff += incr;
      } else {
        huff = 0;
      }

      /* go to next symbol, update count, len */
      sym++;
      if (--count[len] === 0) {
        if (len === max) {
          break;
        }
        len = lens[lens_index + work[sym]];
      }

      /* create new sub-table if needed */
      if (len > root && (huff & mask) !== low) {
        /* if first time, transition to sub-tables */
        if (drop === 0) {
          drop = root;
        }

        /* increment past last table */
        next += min; /* here min is 1 << curr */

        /* determine length of next table */
        curr = len - drop;
        left = 1 << curr;
        while (curr + drop < max) {
          left -= count[curr + drop];
          if (left <= 0) {
            break;
          }
          curr++;
          left <<= 1;
        }

        /* check for enough space */
        used += 1 << curr;
        if (type === LENS$1 && used > ENOUGH_LENS$1 || type === DISTS$1 && used > ENOUGH_DISTS$1) {
          return 1;
        }

        /* point entry in root table to sub-table */
        low = huff & mask;
        /*table.op[low] = curr;
        table.bits[low] = root;
        table.val[low] = next - opts.table_index;*/
        table[low] = root << 24 | curr << 16 | next - table_index | 0;
      }
    }

    /* fill in remaining table entry if code is incomplete (guaranteed to have
     at most one remaining entry, since if the code is incomplete, the
     maximum code length that was allowed to get this far is one bit) */
    if (huff !== 0) {
      //table.op[next + huff] = 64;            /* invalid code marker */
      //table.bits[next + huff] = len - drop;
      //table.val[next + huff] = 0;
      table[next + huff] = len - drop << 24 | 64 << 16 | 0;
    }

    /* set return parameters */
    //opts.table_index += used;
    opts.bits = root;
    return 0;
  };
  var inftrees = inflate_table;

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.

  var CODES = 0;
  var LENS = 1;
  var DISTS = 2;

  /* Public constants ==========================================================*/
  /* ===========================================================================*/

  var Z_FINISH$1 = constants$2.Z_FINISH,
    Z_BLOCK = constants$2.Z_BLOCK,
    Z_TREES = constants$2.Z_TREES,
    Z_OK$1 = constants$2.Z_OK,
    Z_STREAM_END$1 = constants$2.Z_STREAM_END,
    Z_NEED_DICT$1 = constants$2.Z_NEED_DICT,
    Z_STREAM_ERROR$1 = constants$2.Z_STREAM_ERROR,
    Z_DATA_ERROR$1 = constants$2.Z_DATA_ERROR,
    Z_MEM_ERROR$1 = constants$2.Z_MEM_ERROR,
    Z_BUF_ERROR = constants$2.Z_BUF_ERROR,
    Z_DEFLATED = constants$2.Z_DEFLATED;

  /* STATES ====================================================================*/
  /* ===========================================================================*/

  var HEAD = 16180; /* i: waiting for magic header */
  var FLAGS = 16181; /* i: waiting for method and flags (gzip) */
  var TIME = 16182; /* i: waiting for modification time (gzip) */
  var OS = 16183; /* i: waiting for extra flags and operating system (gzip) */
  var EXLEN = 16184; /* i: waiting for extra length (gzip) */
  var EXTRA = 16185; /* i: waiting for extra bytes (gzip) */
  var NAME = 16186; /* i: waiting for end of file name (gzip) */
  var COMMENT = 16187; /* i: waiting for end of comment (gzip) */
  var HCRC = 16188; /* i: waiting for header crc (gzip) */
  var DICTID = 16189; /* i: waiting for dictionary check value */
  var DICT = 16190; /* waiting for inflateSetDictionary() call */
  var TYPE = 16191; /* i: waiting for type bits, including last-flag bit */
  var TYPEDO = 16192; /* i: same, but skip check to exit inflate on new block */
  var STORED = 16193; /* i: waiting for stored size (length and complement) */
  var COPY_ = 16194; /* i/o: same as COPY below, but only first time in */
  var COPY = 16195; /* i/o: waiting for input or output to copy stored block */
  var TABLE = 16196; /* i: waiting for dynamic block table lengths */
  var LENLENS = 16197; /* i: waiting for code length code lengths */
  var CODELENS = 16198; /* i: waiting for length/lit and distance code lengths */
  var LEN_ = 16199; /* i: same as LEN below, but only first time in */
  var LEN = 16200; /* i: waiting for length/lit/eob code */
  var LENEXT = 16201; /* i: waiting for length extra bits */
  var DIST = 16202; /* i: waiting for distance code */
  var DISTEXT = 16203; /* i: waiting for distance extra bits */
  var MATCH = 16204; /* o: waiting for output space to copy string */
  var LIT = 16205; /* o: waiting for output space to write literal */
  var CHECK = 16206; /* i: waiting for 32-bit check value */
  var LENGTH = 16207; /* i: waiting for 32-bit length (gzip) */
  var DONE = 16208; /* finished check, done -- remain here until reset */
  var BAD = 16209; /* got a data error -- remain here until reset */
  var MEM = 16210; /* got an inflate() memory error -- remain here until reset */
  var SYNC = 16211; /* looking for synchronization bytes to restart inflate() */

  /* ===========================================================================*/

  var ENOUGH_LENS = 852;
  var ENOUGH_DISTS = 592;
  //const ENOUGH =  (ENOUGH_LENS+ENOUGH_DISTS);

  var MAX_WBITS = 15;
  /* 32K LZ77 window */
  var DEF_WBITS = MAX_WBITS;
  var zswap32 = function zswap32(q) {
    return (q >>> 24 & 0xff) + (q >>> 8 & 0xff00) + ((q & 0xff00) << 8) + ((q & 0xff) << 24);
  };
  function InflateState() {
    this.strm = null; /* pointer back to this zlib stream */
    this.mode = 0; /* current inflate mode */
    this.last = false; /* true if processing last block */
    this.wrap = 0; /* bit 0 true for zlib, bit 1 true for gzip,
                      bit 2 true to validate check value */
    this.havedict = false; /* true if dictionary provided */
    this.flags = 0; /* gzip header method and flags (0 if zlib), or
                       -1 if raw or no header yet */
    this.dmax = 0; /* zlib header max distance (INFLATE_STRICT) */
    this.check = 0; /* protected copy of check value */
    this.total = 0; /* protected copy of output count */
    // TODO: may be {}
    this.head = null; /* where to save gzip header information */

    /* sliding window */
    this.wbits = 0; /* log base 2 of requested window size */
    this.wsize = 0; /* window size or zero if not using window */
    this.whave = 0; /* valid bytes in the window */
    this.wnext = 0; /* window write index */
    this.window = null; /* allocated sliding window, if needed */

    /* bit accumulator */
    this.hold = 0; /* input bit accumulator */
    this.bits = 0; /* number of bits in "in" */

    /* for string and stored block copying */
    this.length = 0; /* literal or length of data to copy */
    this.offset = 0; /* distance back to copy string from */

    /* for table and code decoding */
    this.extra = 0; /* extra bits needed */

    /* fixed and dynamic code tables */
    this.lencode = null; /* starting table for length/literal codes */
    this.distcode = null; /* starting table for distance codes */
    this.lenbits = 0; /* index bits for lencode */
    this.distbits = 0; /* index bits for distcode */

    /* dynamic table building */
    this.ncode = 0; /* number of code length code lengths */
    this.nlen = 0; /* number of length code lengths */
    this.ndist = 0; /* number of distance code lengths */
    this.have = 0; /* number of code lengths in lens[] */
    this.next = null; /* next available space in codes[] */

    this.lens = new Uint16Array(320); /* temporary storage for code lengths */
    this.work = new Uint16Array(288); /* work area for code table building */

    /*
     because we don't have pointers in js, we use lencode and distcode directly
     as buffers so we don't need codes
    */
    //this.codes = new Int32Array(ENOUGH);       /* space for code tables */
    this.lendyn = null; /* dynamic table for length/literal codes (JS specific) */
    this.distdyn = null; /* dynamic table for distance codes (JS specific) */
    this.sane = 0; /* if false, allow invalid distance too far */
    this.back = 0; /* bits back of last unprocessed length/lit */
    this.was = 0; /* initial length of match */
  }

  var inflateStateCheck = function inflateStateCheck(strm) {
    if (!strm) {
      return 1;
    }
    var state = strm.state;
    if (!state || state.strm !== strm || state.mode < HEAD || state.mode > SYNC) {
      return 1;
    }
    return 0;
  };
  var inflateResetKeep = function inflateResetKeep(strm) {
    if (inflateStateCheck(strm)) {
      return Z_STREAM_ERROR$1;
    }
    var state = strm.state;
    strm.total_in = strm.total_out = state.total = 0;
    strm.msg = ''; /*Z_NULL*/
    if (state.wrap) {
      /* to support ill-conceived Java test suite */
      strm.adler = state.wrap & 1;
    }
    state.mode = HEAD;
    state.last = 0;
    state.havedict = 0;
    state.flags = -1;
    state.dmax = 32768;
    state.head = null /*Z_NULL*/;
    state.hold = 0;
    state.bits = 0;
    //state.lencode = state.distcode = state.next = state.codes;
    state.lencode = state.lendyn = new Int32Array(ENOUGH_LENS);
    state.distcode = state.distdyn = new Int32Array(ENOUGH_DISTS);
    state.sane = 1;
    state.back = -1;
    //Tracev((stderr, "inflate: reset\n"));
    return Z_OK$1;
  };
  var inflateReset = function inflateReset(strm) {
    if (inflateStateCheck(strm)) {
      return Z_STREAM_ERROR$1;
    }
    var state = strm.state;
    state.wsize = 0;
    state.whave = 0;
    state.wnext = 0;
    return inflateResetKeep(strm);
  };
  var inflateReset2 = function inflateReset2(strm, windowBits) {
    var wrap;

    /* get the state */
    if (inflateStateCheck(strm)) {
      return Z_STREAM_ERROR$1;
    }
    var state = strm.state;

    /* extract wrap request from windowBits parameter */
    if (windowBits < 0) {
      wrap = 0;
      windowBits = -windowBits;
    } else {
      wrap = (windowBits >> 4) + 5;
      if (windowBits < 48) {
        windowBits &= 15;
      }
    }

    /* set number of window bits, free window if different */
    if (windowBits && (windowBits < 8 || windowBits > 15)) {
      return Z_STREAM_ERROR$1;
    }
    if (state.window !== null && state.wbits !== windowBits) {
      state.window = null;
    }

    /* update state and reset the rest of it */
    state.wrap = wrap;
    state.wbits = windowBits;
    return inflateReset(strm);
  };
  var inflateInit2 = function inflateInit2(strm, windowBits) {
    if (!strm) {
      return Z_STREAM_ERROR$1;
    }
    //strm.msg = Z_NULL;                 /* in case we return an error */

    var state = new InflateState();

    //if (state === Z_NULL) return Z_MEM_ERROR;
    //Tracev((stderr, "inflate: allocated\n"));
    strm.state = state;
    state.strm = strm;
    state.window = null /*Z_NULL*/;
    state.mode = HEAD; /* to pass state test in inflateReset2() */
    var ret = inflateReset2(strm, windowBits);
    if (ret !== Z_OK$1) {
      strm.state = null /*Z_NULL*/;
    }

    return ret;
  };
  var inflateInit = function inflateInit(strm) {
    return inflateInit2(strm, DEF_WBITS);
  };

  /*
   Return state with length and distance decoding tables and index sizes set to
   fixed code decoding.  Normally this returns fixed tables from inffixed.h.
   If BUILDFIXED is defined, then instead this routine builds the tables the
   first time it's called, and returns those tables the first time and
   thereafter.  This reduces the size of the code by about 2K bytes, in
   exchange for a little execution time.  However, BUILDFIXED should not be
   used for threaded applications, since the rewriting of the tables and virgin
   may not be thread-safe.
   */
  var virgin = true;
  var lenfix, distfix; // We have no pointers in JS, so keep tables separate

  var fixedtables = function fixedtables(state) {
    /* build fixed huffman tables if first call (may not be thread safe) */
    if (virgin) {
      lenfix = new Int32Array(512);
      distfix = new Int32Array(32);

      /* literal/length table */
      var sym = 0;
      while (sym < 144) {
        state.lens[sym++] = 8;
      }
      while (sym < 256) {
        state.lens[sym++] = 9;
      }
      while (sym < 280) {
        state.lens[sym++] = 7;
      }
      while (sym < 288) {
        state.lens[sym++] = 8;
      }
      inftrees(LENS, state.lens, 0, 288, lenfix, 0, state.work, {
        bits: 9
      });

      /* distance table */
      sym = 0;
      while (sym < 32) {
        state.lens[sym++] = 5;
      }
      inftrees(DISTS, state.lens, 0, 32, distfix, 0, state.work, {
        bits: 5
      });

      /* do this just once */
      virgin = false;
    }
    state.lencode = lenfix;
    state.lenbits = 9;
    state.distcode = distfix;
    state.distbits = 5;
  };

  /*
   Update the window with the last wsize (normally 32K) bytes written before
   returning.  If window does not exist yet, create it.  This is only called
   when a window is already in use, or when output has been written during this
   inflate call, but the end of the deflate stream has not been reached yet.
   It is also called to create a window for dictionary data when a dictionary
   is loaded.

   Providing output buffers larger than 32K to inflate() should provide a speed
   advantage, since only the last 32K of output is copied to the sliding window
   upon return from inflate(), and since all distances after the first 32K of
   output will fall in the output data, making match copies simpler and faster.
   The advantage may be dependent on the size of the processor's data caches.
   */
  var updatewindow = function updatewindow(strm, src, end, copy) {
    var dist;
    var state = strm.state;

    /* if it hasn't been done already, allocate space for the window */
    if (state.window === null) {
      state.wsize = 1 << state.wbits;
      state.wnext = 0;
      state.whave = 0;
      state.window = new Uint8Array(state.wsize);
    }

    /* copy state->wsize or less output bytes into the circular window */
    if (copy >= state.wsize) {
      state.window.set(src.subarray(end - state.wsize, end), 0);
      state.wnext = 0;
      state.whave = state.wsize;
    } else {
      dist = state.wsize - state.wnext;
      if (dist > copy) {
        dist = copy;
      }
      //zmemcpy(state->window + state->wnext, end - copy, dist);
      state.window.set(src.subarray(end - copy, end - copy + dist), state.wnext);
      copy -= dist;
      if (copy) {
        //zmemcpy(state->window, end - copy, copy);
        state.window.set(src.subarray(end - copy, end), 0);
        state.wnext = copy;
        state.whave = state.wsize;
      } else {
        state.wnext += dist;
        if (state.wnext === state.wsize) {
          state.wnext = 0;
        }
        if (state.whave < state.wsize) {
          state.whave += dist;
        }
      }
    }
    return 0;
  };
  var inflate$2 = function inflate(strm, flush) {
    var state;
    var input, output; // input/output buffers
    var next; /* next input INDEX */
    var put; /* next output INDEX */
    var have, left; /* available input and output */
    var hold; /* bit buffer */
    var bits; /* bits in bit buffer */
    var _in, _out; /* save starting available input and output */
    var copy; /* number of stored or match bytes to copy */
    var from; /* where to copy match bytes from */
    var from_source;
    var here = 0; /* current decoding table entry */
    var here_bits, here_op, here_val; // paked "here" denormalized (JS specific)
    //let last;                   /* parent table entry */
    var last_bits, last_op, last_val; // paked "last" denormalized (JS specific)
    var len; /* length to copy for repeats, bits to drop */
    var ret; /* return code */
    var hbuf = new Uint8Array(4); /* buffer for gzip header crc calculation */
    var opts;
    var n; // temporary variable for NEED_BITS

    var order = /* permutation of code lengths */
    new Uint8Array([16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15]);
    if (inflateStateCheck(strm) || !strm.output || !strm.input && strm.avail_in !== 0) {
      return Z_STREAM_ERROR$1;
    }
    state = strm.state;
    if (state.mode === TYPE) {
      state.mode = TYPEDO;
    } /* skip check */

    //--- LOAD() ---
    put = strm.next_out;
    output = strm.output;
    left = strm.avail_out;
    next = strm.next_in;
    input = strm.input;
    have = strm.avail_in;
    hold = state.hold;
    bits = state.bits;
    //---

    _in = have;
    _out = left;
    ret = Z_OK$1;
    inf_leave:
    // goto emulation
    for (;;) {
      switch (state.mode) {
        case HEAD:
          if (state.wrap === 0) {
            state.mode = TYPEDO;
            break;
          }
          //=== NEEDBITS(16);
          while (bits < 16) {
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          if (state.wrap & 2 && hold === 0x8b1f) {
            /* gzip header */
            if (state.wbits === 0) {
              state.wbits = 15;
            }
            state.check = 0 /*crc32(0L, Z_NULL, 0)*/;
            //=== CRC2(state.check, hold);
            hbuf[0] = hold & 0xff;
            hbuf[1] = hold >>> 8 & 0xff;
            state.check = crc32_1(state.check, hbuf, 2, 0);
            //===//

            //=== INITBITS();
            hold = 0;
            bits = 0;
            //===//
            state.mode = FLAGS;
            break;
          }
          if (state.head) {
            state.head.done = false;
          }
          if (!(state.wrap & 1) || /* check if zlib header allowed */
          (((hold & 0xff /*BITS(8)*/) << 8) + (hold >> 8)) % 31) {
            strm.msg = 'incorrect header check';
            state.mode = BAD;
            break;
          }
          if ((hold & 0x0f /*BITS(4)*/) !== Z_DEFLATED) {
            strm.msg = 'unknown compression method';
            state.mode = BAD;
            break;
          }
          //--- DROPBITS(4) ---//
          hold >>>= 4;
          bits -= 4;
          //---//
          len = (hold & 0x0f /*BITS(4)*/) + 8;
          if (state.wbits === 0) {
            state.wbits = len;
          }
          if (len > 15 || len > state.wbits) {
            strm.msg = 'invalid window size';
            state.mode = BAD;
            break;
          }

          // !!! pako patch. Force use `options.windowBits` if passed.
          // Required to always use max window size by default.
          state.dmax = 1 << state.wbits;
          //state.dmax = 1 << len;

          state.flags = 0; /* indicate zlib header */
          //Tracev((stderr, "inflate:   zlib header ok\n"));
          strm.adler = state.check = 1 /*adler32(0L, Z_NULL, 0)*/;
          state.mode = hold & 0x200 ? DICTID : TYPE;
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          break;
        case FLAGS:
          //=== NEEDBITS(16); */
          while (bits < 16) {
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.flags = hold;
          if ((state.flags & 0xff) !== Z_DEFLATED) {
            strm.msg = 'unknown compression method';
            state.mode = BAD;
            break;
          }
          if (state.flags & 0xe000) {
            strm.msg = 'unknown header flags set';
            state.mode = BAD;
            break;
          }
          if (state.head) {
            state.head.text = hold >> 8 & 1;
          }
          if (state.flags & 0x0200 && state.wrap & 4) {
            //=== CRC2(state.check, hold);
            hbuf[0] = hold & 0xff;
            hbuf[1] = hold >>> 8 & 0xff;
            state.check = crc32_1(state.check, hbuf, 2, 0);
            //===//
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          state.mode = TIME;
        /* falls through */
        case TIME:
          //=== NEEDBITS(32); */
          while (bits < 32) {
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          if (state.head) {
            state.head.time = hold;
          }
          if (state.flags & 0x0200 && state.wrap & 4) {
            //=== CRC4(state.check, hold)
            hbuf[0] = hold & 0xff;
            hbuf[1] = hold >>> 8 & 0xff;
            hbuf[2] = hold >>> 16 & 0xff;
            hbuf[3] = hold >>> 24 & 0xff;
            state.check = crc32_1(state.check, hbuf, 4, 0);
            //===
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          state.mode = OS;
        /* falls through */
        case OS:
          //=== NEEDBITS(16); */
          while (bits < 16) {
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          if (state.head) {
            state.head.xflags = hold & 0xff;
            state.head.os = hold >> 8;
          }
          if (state.flags & 0x0200 && state.wrap & 4) {
            //=== CRC2(state.check, hold);
            hbuf[0] = hold & 0xff;
            hbuf[1] = hold >>> 8 & 0xff;
            state.check = crc32_1(state.check, hbuf, 2, 0);
            //===//
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          state.mode = EXLEN;
        /* falls through */
        case EXLEN:
          if (state.flags & 0x0400) {
            //=== NEEDBITS(16); */
            while (bits < 16) {
              if (have === 0) {
                break inf_leave;
              }
              have--;
              hold += input[next++] << bits;
              bits += 8;
            }
            //===//
            state.length = hold;
            if (state.head) {
              state.head.extra_len = hold;
            }
            if (state.flags & 0x0200 && state.wrap & 4) {
              //=== CRC2(state.check, hold);
              hbuf[0] = hold & 0xff;
              hbuf[1] = hold >>> 8 & 0xff;
              state.check = crc32_1(state.check, hbuf, 2, 0);
              //===//
            }
            //=== INITBITS();
            hold = 0;
            bits = 0;
            //===//
          } else if (state.head) {
            state.head.extra = null /*Z_NULL*/;
          }

          state.mode = EXTRA;
        /* falls through */
        case EXTRA:
          if (state.flags & 0x0400) {
            copy = state.length;
            if (copy > have) {
              copy = have;
            }
            if (copy) {
              if (state.head) {
                len = state.head.extra_len - state.length;
                if (!state.head.extra) {
                  // Use untyped array for more convenient processing later
                  state.head.extra = new Uint8Array(state.head.extra_len);
                }
                state.head.extra.set(input.subarray(next,
                // extra field is limited to 65536 bytes
                // - no need for additional size check
                next + copy), /*len + copy > state.head.extra_max - len ? state.head.extra_max : copy,*/
                len);
                //zmemcpy(state.head.extra + len, next,
                //        len + copy > state.head.extra_max ?
                //        state.head.extra_max - len : copy);
              }

              if (state.flags & 0x0200 && state.wrap & 4) {
                state.check = crc32_1(state.check, input, copy, next);
              }
              have -= copy;
              next += copy;
              state.length -= copy;
            }
            if (state.length) {
              break inf_leave;
            }
          }
          state.length = 0;
          state.mode = NAME;
        /* falls through */
        case NAME:
          if (state.flags & 0x0800) {
            if (have === 0) {
              break inf_leave;
            }
            copy = 0;
            do {
              // TODO: 2 or 1 bytes?
              len = input[next + copy++];
              /* use constant limit because in js we should not preallocate memory */
              if (state.head && len && state.length < 65536 /*state.head.name_max*/) {
                state.head.name += String.fromCharCode(len);
              }
            } while (len && copy < have);
            if (state.flags & 0x0200 && state.wrap & 4) {
              state.check = crc32_1(state.check, input, copy, next);
            }
            have -= copy;
            next += copy;
            if (len) {
              break inf_leave;
            }
          } else if (state.head) {
            state.head.name = null;
          }
          state.length = 0;
          state.mode = COMMENT;
        /* falls through */
        case COMMENT:
          if (state.flags & 0x1000) {
            if (have === 0) {
              break inf_leave;
            }
            copy = 0;
            do {
              len = input[next + copy++];
              /* use constant limit because in js we should not preallocate memory */
              if (state.head && len && state.length < 65536 /*state.head.comm_max*/) {
                state.head.comment += String.fromCharCode(len);
              }
            } while (len && copy < have);
            if (state.flags & 0x0200 && state.wrap & 4) {
              state.check = crc32_1(state.check, input, copy, next);
            }
            have -= copy;
            next += copy;
            if (len) {
              break inf_leave;
            }
          } else if (state.head) {
            state.head.comment = null;
          }
          state.mode = HCRC;
        /* falls through */
        case HCRC:
          if (state.flags & 0x0200) {
            //=== NEEDBITS(16); */
            while (bits < 16) {
              if (have === 0) {
                break inf_leave;
              }
              have--;
              hold += input[next++] << bits;
              bits += 8;
            }
            //===//
            if (state.wrap & 4 && hold !== (state.check & 0xffff)) {
              strm.msg = 'header crc mismatch';
              state.mode = BAD;
              break;
            }
            //=== INITBITS();
            hold = 0;
            bits = 0;
            //===//
          }

          if (state.head) {
            state.head.hcrc = state.flags >> 9 & 1;
            state.head.done = true;
          }
          strm.adler = state.check = 0;
          state.mode = TYPE;
          break;
        case DICTID:
          //=== NEEDBITS(32); */
          while (bits < 32) {
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          strm.adler = state.check = zswap32(hold);
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          state.mode = DICT;
        /* falls through */
        case DICT:
          if (state.havedict === 0) {
            //--- RESTORE() ---
            strm.next_out = put;
            strm.avail_out = left;
            strm.next_in = next;
            strm.avail_in = have;
            state.hold = hold;
            state.bits = bits;
            //---
            return Z_NEED_DICT$1;
          }
          strm.adler = state.check = 1 /*adler32(0L, Z_NULL, 0)*/;
          state.mode = TYPE;
        /* falls through */
        case TYPE:
          if (flush === Z_BLOCK || flush === Z_TREES) {
            break inf_leave;
          }
        /* falls through */
        case TYPEDO:
          if (state.last) {
            //--- BYTEBITS() ---//
            hold >>>= bits & 7;
            bits -= bits & 7;
            //---//
            state.mode = CHECK;
            break;
          }
          //=== NEEDBITS(3); */
          while (bits < 3) {
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.last = hold & 0x01 /*BITS(1)*/;
          //--- DROPBITS(1) ---//
          hold >>>= 1;
          bits -= 1;
          //---//

          switch (hold & 0x03 /*BITS(2)*/) {
            case 0:
              /* stored block */
              //Tracev((stderr, "inflate:     stored block%s\n",
              //        state.last ? " (last)" : ""));
              state.mode = STORED;
              break;
            case 1:
              /* fixed block */
              fixedtables(state);
              //Tracev((stderr, "inflate:     fixed codes block%s\n",
              //        state.last ? " (last)" : ""));
              state.mode = LEN_; /* decode codes */
              if (flush === Z_TREES) {
                //--- DROPBITS(2) ---//
                hold >>>= 2;
                bits -= 2;
                //---//
                break inf_leave;
              }
              break;
            case 2:
              /* dynamic block */
              //Tracev((stderr, "inflate:     dynamic codes block%s\n",
              //        state.last ? " (last)" : ""));
              state.mode = TABLE;
              break;
            case 3:
              strm.msg = 'invalid block type';
              state.mode = BAD;
          }
          //--- DROPBITS(2) ---//
          hold >>>= 2;
          bits -= 2;
          //---//
          break;
        case STORED:
          //--- BYTEBITS() ---// /* go to byte boundary */
          hold >>>= bits & 7;
          bits -= bits & 7;
          //---//
          //=== NEEDBITS(32); */
          while (bits < 32) {
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          if ((hold & 0xffff) !== (hold >>> 16 ^ 0xffff)) {
            strm.msg = 'invalid stored block lengths';
            state.mode = BAD;
            break;
          }
          state.length = hold & 0xffff;
          //Tracev((stderr, "inflate:       stored length %u\n",
          //        state.length));
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          state.mode = COPY_;
          if (flush === Z_TREES) {
            break inf_leave;
          }
        /* falls through */
        case COPY_:
          state.mode = COPY;
        /* falls through */
        case COPY:
          copy = state.length;
          if (copy) {
            if (copy > have) {
              copy = have;
            }
            if (copy > left) {
              copy = left;
            }
            if (copy === 0) {
              break inf_leave;
            }
            //--- zmemcpy(put, next, copy); ---
            output.set(input.subarray(next, next + copy), put);
            //---//
            have -= copy;
            next += copy;
            left -= copy;
            put += copy;
            state.length -= copy;
            break;
          }
          //Tracev((stderr, "inflate:       stored end\n"));
          state.mode = TYPE;
          break;
        case TABLE:
          //=== NEEDBITS(14); */
          while (bits < 14) {
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.nlen = (hold & 0x1f /*BITS(5)*/) + 257;
          //--- DROPBITS(5) ---//
          hold >>>= 5;
          bits -= 5;
          //---//
          state.ndist = (hold & 0x1f /*BITS(5)*/) + 1;
          //--- DROPBITS(5) ---//
          hold >>>= 5;
          bits -= 5;
          //---//
          state.ncode = (hold & 0x0f /*BITS(4)*/) + 4;
          //--- DROPBITS(4) ---//
          hold >>>= 4;
          bits -= 4;
          //---//
          //#ifndef PKZIP_BUG_WORKAROUND
          if (state.nlen > 286 || state.ndist > 30) {
            strm.msg = 'too many length or distance symbols';
            state.mode = BAD;
            break;
          }
          //#endif
          //Tracev((stderr, "inflate:       table sizes ok\n"));
          state.have = 0;
          state.mode = LENLENS;
        /* falls through */
        case LENLENS:
          while (state.have < state.ncode) {
            //=== NEEDBITS(3);
            while (bits < 3) {
              if (have === 0) {
                break inf_leave;
              }
              have--;
              hold += input[next++] << bits;
              bits += 8;
            }
            //===//
            state.lens[order[state.have++]] = hold & 0x07; //BITS(3);
            //--- DROPBITS(3) ---//
            hold >>>= 3;
            bits -= 3;
            //---//
          }

          while (state.have < 19) {
            state.lens[order[state.have++]] = 0;
          }
          // We have separate tables & no pointers. 2 commented lines below not needed.
          //state.next = state.codes;
          //state.lencode = state.next;
          // Switch to use dynamic table
          state.lencode = state.lendyn;
          state.lenbits = 7;
          opts = {
            bits: state.lenbits
          };
          ret = inftrees(CODES, state.lens, 0, 19, state.lencode, 0, state.work, opts);
          state.lenbits = opts.bits;
          if (ret) {
            strm.msg = 'invalid code lengths set';
            state.mode = BAD;
            break;
          }
          //Tracev((stderr, "inflate:       code lengths ok\n"));
          state.have = 0;
          state.mode = CODELENS;
        /* falls through */
        case CODELENS:
          while (state.have < state.nlen + state.ndist) {
            for (;;) {
              here = state.lencode[hold & (1 << state.lenbits) - 1]; /*BITS(state.lenbits)*/
              here_bits = here >>> 24;
              here_op = here >>> 16 & 0xff;
              here_val = here & 0xffff;
              if (here_bits <= bits) {
                break;
              }
              //--- PULLBYTE() ---//
              if (have === 0) {
                break inf_leave;
              }
              have--;
              hold += input[next++] << bits;
              bits += 8;
              //---//
            }

            if (here_val < 16) {
              //--- DROPBITS(here.bits) ---//
              hold >>>= here_bits;
              bits -= here_bits;
              //---//
              state.lens[state.have++] = here_val;
            } else {
              if (here_val === 16) {
                //=== NEEDBITS(here.bits + 2);
                n = here_bits + 2;
                while (bits < n) {
                  if (have === 0) {
                    break inf_leave;
                  }
                  have--;
                  hold += input[next++] << bits;
                  bits += 8;
                }
                //===//
                //--- DROPBITS(here.bits) ---//
                hold >>>= here_bits;
                bits -= here_bits;
                //---//
                if (state.have === 0) {
                  strm.msg = 'invalid bit length repeat';
                  state.mode = BAD;
                  break;
                }
                len = state.lens[state.have - 1];
                copy = 3 + (hold & 0x03); //BITS(2);
                //--- DROPBITS(2) ---//
                hold >>>= 2;
                bits -= 2;
                //---//
              } else if (here_val === 17) {
                //=== NEEDBITS(here.bits + 3);
                n = here_bits + 3;
                while (bits < n) {
                  if (have === 0) {
                    break inf_leave;
                  }
                  have--;
                  hold += input[next++] << bits;
                  bits += 8;
                }
                //===//
                //--- DROPBITS(here.bits) ---//
                hold >>>= here_bits;
                bits -= here_bits;
                //---//
                len = 0;
                copy = 3 + (hold & 0x07); //BITS(3);
                //--- DROPBITS(3) ---//
                hold >>>= 3;
                bits -= 3;
                //---//
              } else {
                //=== NEEDBITS(here.bits + 7);
                n = here_bits + 7;
                while (bits < n) {
                  if (have === 0) {
                    break inf_leave;
                  }
                  have--;
                  hold += input[next++] << bits;
                  bits += 8;
                }
                //===//
                //--- DROPBITS(here.bits) ---//
                hold >>>= here_bits;
                bits -= here_bits;
                //---//
                len = 0;
                copy = 11 + (hold & 0x7f); //BITS(7);
                //--- DROPBITS(7) ---//
                hold >>>= 7;
                bits -= 7;
                //---//
              }

              if (state.have + copy > state.nlen + state.ndist) {
                strm.msg = 'invalid bit length repeat';
                state.mode = BAD;
                break;
              }
              while (copy--) {
                state.lens[state.have++] = len;
              }
            }
          }

          /* handle error breaks in while */
          if (state.mode === BAD) {
            break;
          }

          /* check for end-of-block code (better have one) */
          if (state.lens[256] === 0) {
            strm.msg = 'invalid code -- missing end-of-block';
            state.mode = BAD;
            break;
          }

          /* build code tables -- note: do not change the lenbits or distbits
             values here (9 and 6) without reading the comments in inftrees.h
             concerning the ENOUGH constants, which depend on those values */
          state.lenbits = 9;
          opts = {
            bits: state.lenbits
          };
          ret = inftrees(LENS, state.lens, 0, state.nlen, state.lencode, 0, state.work, opts);
          // We have separate tables & no pointers. 2 commented lines below not needed.
          // state.next_index = opts.table_index;
          state.lenbits = opts.bits;
          // state.lencode = state.next;

          if (ret) {
            strm.msg = 'invalid literal/lengths set';
            state.mode = BAD;
            break;
          }
          state.distbits = 6;
          //state.distcode.copy(state.codes);
          // Switch to use dynamic table
          state.distcode = state.distdyn;
          opts = {
            bits: state.distbits
          };
          ret = inftrees(DISTS, state.lens, state.nlen, state.ndist, state.distcode, 0, state.work, opts);
          // We have separate tables & no pointers. 2 commented lines below not needed.
          // state.next_index = opts.table_index;
          state.distbits = opts.bits;
          // state.distcode = state.next;

          if (ret) {
            strm.msg = 'invalid distances set';
            state.mode = BAD;
            break;
          }
          //Tracev((stderr, 'inflate:       codes ok\n'));
          state.mode = LEN_;
          if (flush === Z_TREES) {
            break inf_leave;
          }
        /* falls through */
        case LEN_:
          state.mode = LEN;
        /* falls through */
        case LEN:
          if (have >= 6 && left >= 258) {
            //--- RESTORE() ---
            strm.next_out = put;
            strm.avail_out = left;
            strm.next_in = next;
            strm.avail_in = have;
            state.hold = hold;
            state.bits = bits;
            //---
            inffast(strm, _out);
            //--- LOAD() ---
            put = strm.next_out;
            output = strm.output;
            left = strm.avail_out;
            next = strm.next_in;
            input = strm.input;
            have = strm.avail_in;
            hold = state.hold;
            bits = state.bits;
            //---

            if (state.mode === TYPE) {
              state.back = -1;
            }
            break;
          }
          state.back = 0;
          for (;;) {
            here = state.lencode[hold & (1 << state.lenbits) - 1]; /*BITS(state.lenbits)*/
            here_bits = here >>> 24;
            here_op = here >>> 16 & 0xff;
            here_val = here & 0xffff;
            if (here_bits <= bits) {
              break;
            }
            //--- PULLBYTE() ---//
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
            //---//
          }

          if (here_op && (here_op & 0xf0) === 0) {
            last_bits = here_bits;
            last_op = here_op;
            last_val = here_val;
            for (;;) {
              here = state.lencode[last_val + ((hold & (1 << last_bits + last_op) - 1 /*BITS(last.bits + last.op)*/) >> last_bits)];
              here_bits = here >>> 24;
              here_op = here >>> 16 & 0xff;
              here_val = here & 0xffff;
              if (last_bits + here_bits <= bits) {
                break;
              }
              //--- PULLBYTE() ---//
              if (have === 0) {
                break inf_leave;
              }
              have--;
              hold += input[next++] << bits;
              bits += 8;
              //---//
            }
            //--- DROPBITS(last.bits) ---//
            hold >>>= last_bits;
            bits -= last_bits;
            //---//
            state.back += last_bits;
          }
          //--- DROPBITS(here.bits) ---//
          hold >>>= here_bits;
          bits -= here_bits;
          //---//
          state.back += here_bits;
          state.length = here_val;
          if (here_op === 0) {
            //Tracevv((stderr, here.val >= 0x20 && here.val < 0x7f ?
            //        "inflate:         literal '%c'\n" :
            //        "inflate:         literal 0x%02x\n", here.val));
            state.mode = LIT;
            break;
          }
          if (here_op & 32) {
            //Tracevv((stderr, "inflate:         end of block\n"));
            state.back = -1;
            state.mode = TYPE;
            break;
          }
          if (here_op & 64) {
            strm.msg = 'invalid literal/length code';
            state.mode = BAD;
            break;
          }
          state.extra = here_op & 15;
          state.mode = LENEXT;
        /* falls through */
        case LENEXT:
          if (state.extra) {
            //=== NEEDBITS(state.extra);
            n = state.extra;
            while (bits < n) {
              if (have === 0) {
                break inf_leave;
              }
              have--;
              hold += input[next++] << bits;
              bits += 8;
            }
            //===//
            state.length += hold & (1 << state.extra) - 1 /*BITS(state.extra)*/;
            //--- DROPBITS(state.extra) ---//
            hold >>>= state.extra;
            bits -= state.extra;
            //---//
            state.back += state.extra;
          }
          //Tracevv((stderr, "inflate:         length %u\n", state.length));
          state.was = state.length;
          state.mode = DIST;
        /* falls through */
        case DIST:
          for (;;) {
            here = state.distcode[hold & (1 << state.distbits) - 1]; /*BITS(state.distbits)*/
            here_bits = here >>> 24;
            here_op = here >>> 16 & 0xff;
            here_val = here & 0xffff;
            if (here_bits <= bits) {
              break;
            }
            //--- PULLBYTE() ---//
            if (have === 0) {
              break inf_leave;
            }
            have--;
            hold += input[next++] << bits;
            bits += 8;
            //---//
          }

          if ((here_op & 0xf0) === 0) {
            last_bits = here_bits;
            last_op = here_op;
            last_val = here_val;
            for (;;) {
              here = state.distcode[last_val + ((hold & (1 << last_bits + last_op) - 1 /*BITS(last.bits + last.op)*/) >> last_bits)];
              here_bits = here >>> 24;
              here_op = here >>> 16 & 0xff;
              here_val = here & 0xffff;
              if (last_bits + here_bits <= bits) {
                break;
              }
              //--- PULLBYTE() ---//
              if (have === 0) {
                break inf_leave;
              }
              have--;
              hold += input[next++] << bits;
              bits += 8;
              //---//
            }
            //--- DROPBITS(last.bits) ---//
            hold >>>= last_bits;
            bits -= last_bits;
            //---//
            state.back += last_bits;
          }
          //--- DROPBITS(here.bits) ---//
          hold >>>= here_bits;
          bits -= here_bits;
          //---//
          state.back += here_bits;
          if (here_op & 64) {
            strm.msg = 'invalid distance code';
            state.mode = BAD;
            break;
          }
          state.offset = here_val;
          state.extra = here_op & 15;
          state.mode = DISTEXT;
        /* falls through */
        case DISTEXT:
          if (state.extra) {
            //=== NEEDBITS(state.extra);
            n = state.extra;
            while (bits < n) {
              if (have === 0) {
                break inf_leave;
              }
              have--;
              hold += input[next++] << bits;
              bits += 8;
            }
            //===//
            state.offset += hold & (1 << state.extra) - 1 /*BITS(state.extra)*/;
            //--- DROPBITS(state.extra) ---//
            hold >>>= state.extra;
            bits -= state.extra;
            //---//
            state.back += state.extra;
          }
          //#ifdef INFLATE_STRICT
          if (state.offset > state.dmax) {
            strm.msg = 'invalid distance too far back';
            state.mode = BAD;
            break;
          }
          //#endif
          //Tracevv((stderr, "inflate:         distance %u\n", state.offset));
          state.mode = MATCH;
        /* falls through */
        case MATCH:
          if (left === 0) {
            break inf_leave;
          }
          copy = _out - left;
          if (state.offset > copy) {
            /* copy from window */
            copy = state.offset - copy;
            if (copy > state.whave) {
              if (state.sane) {
                strm.msg = 'invalid distance too far back';
                state.mode = BAD;
                break;
              }
              // (!) This block is disabled in zlib defaults,
              // don't enable it for binary compatibility
              //#ifdef INFLATE_ALLOW_INVALID_DISTANCE_TOOFAR_ARRR
              //          Trace((stderr, "inflate.c too far\n"));
              //          copy -= state.whave;
              //          if (copy > state.length) { copy = state.length; }
              //          if (copy > left) { copy = left; }
              //          left -= copy;
              //          state.length -= copy;
              //          do {
              //            output[put++] = 0;
              //          } while (--copy);
              //          if (state.length === 0) { state.mode = LEN; }
              //          break;
              //#endif
            }

            if (copy > state.wnext) {
              copy -= state.wnext;
              from = state.wsize - copy;
            } else {
              from = state.wnext - copy;
            }
            if (copy > state.length) {
              copy = state.length;
            }
            from_source = state.window;
          } else {
            /* copy from output */
            from_source = output;
            from = put - state.offset;
            copy = state.length;
          }
          if (copy > left) {
            copy = left;
          }
          left -= copy;
          state.length -= copy;
          do {
            output[put++] = from_source[from++];
          } while (--copy);
          if (state.length === 0) {
            state.mode = LEN;
          }
          break;
        case LIT:
          if (left === 0) {
            break inf_leave;
          }
          output[put++] = state.length;
          left--;
          state.mode = LEN;
          break;
        case CHECK:
          if (state.wrap) {
            //=== NEEDBITS(32);
            while (bits < 32) {
              if (have === 0) {
                break inf_leave;
              }
              have--;
              // Use '|' instead of '+' to make sure that result is signed
              hold |= input[next++] << bits;
              bits += 8;
            }
            //===//
            _out -= left;
            strm.total_out += _out;
            state.total += _out;
            if (state.wrap & 4 && _out) {
              strm.adler = state.check = /*UPDATE_CHECK(state.check, put - _out, _out);*/
              state.flags ? crc32_1(state.check, output, _out, put - _out) : adler32_1(state.check, output, _out, put - _out);
            }
            _out = left;
            // NB: crc32 stored as signed 32-bit int, zswap32 returns signed too
            if (state.wrap & 4 && (state.flags ? hold : zswap32(hold)) !== state.check) {
              strm.msg = 'incorrect data check';
              state.mode = BAD;
              break;
            }
            //=== INITBITS();
            hold = 0;
            bits = 0;
            //===//
            //Tracev((stderr, "inflate:   check matches trailer\n"));
          }

          state.mode = LENGTH;
        /* falls through */
        case LENGTH:
          if (state.wrap && state.flags) {
            //=== NEEDBITS(32);
            while (bits < 32) {
              if (have === 0) {
                break inf_leave;
              }
              have--;
              hold += input[next++] << bits;
              bits += 8;
            }
            //===//
            if (state.wrap & 4 && hold !== (state.total & 0xffffffff)) {
              strm.msg = 'incorrect length check';
              state.mode = BAD;
              break;
            }
            //=== INITBITS();
            hold = 0;
            bits = 0;
            //===//
            //Tracev((stderr, "inflate:   length matches trailer\n"));
          }

          state.mode = DONE;
        /* falls through */
        case DONE:
          ret = Z_STREAM_END$1;
          break inf_leave;
        case BAD:
          ret = Z_DATA_ERROR$1;
          break inf_leave;
        case MEM:
          return Z_MEM_ERROR$1;
        case SYNC:
        /* falls through */
        default:
          return Z_STREAM_ERROR$1;
      }
    }

    // inf_leave <- here is real place for "goto inf_leave", emulated via "break inf_leave"

    /*
       Return from inflate(), updating the total counts and the check value.
       If there was no progress during the inflate() call, return a buffer
       error.  Call updatewindow() to create and/or update the window state.
       Note: a memory error from inflate() is non-recoverable.
     */

    //--- RESTORE() ---
    strm.next_out = put;
    strm.avail_out = left;
    strm.next_in = next;
    strm.avail_in = have;
    state.hold = hold;
    state.bits = bits;
    //---

    if (state.wsize || _out !== strm.avail_out && state.mode < BAD && (state.mode < CHECK || flush !== Z_FINISH$1)) {
      if (updatewindow(strm, strm.output, strm.next_out, _out - strm.avail_out)) ;
    }
    _in -= strm.avail_in;
    _out -= strm.avail_out;
    strm.total_in += _in;
    strm.total_out += _out;
    state.total += _out;
    if (state.wrap & 4 && _out) {
      strm.adler = state.check = /*UPDATE_CHECK(state.check, strm.next_out - _out, _out);*/
      state.flags ? crc32_1(state.check, output, _out, strm.next_out - _out) : adler32_1(state.check, output, _out, strm.next_out - _out);
    }
    strm.data_type = state.bits + (state.last ? 64 : 0) + (state.mode === TYPE ? 128 : 0) + (state.mode === LEN_ || state.mode === COPY_ ? 256 : 0);
    if ((_in === 0 && _out === 0 || flush === Z_FINISH$1) && ret === Z_OK$1) {
      ret = Z_BUF_ERROR;
    }
    return ret;
  };
  var inflateEnd = function inflateEnd(strm) {
    if (inflateStateCheck(strm)) {
      return Z_STREAM_ERROR$1;
    }
    var state = strm.state;
    if (state.window) {
      state.window = null;
    }
    strm.state = null;
    return Z_OK$1;
  };
  var inflateGetHeader = function inflateGetHeader(strm, head) {
    /* check state */
    if (inflateStateCheck(strm)) {
      return Z_STREAM_ERROR$1;
    }
    var state = strm.state;
    if ((state.wrap & 2) === 0) {
      return Z_STREAM_ERROR$1;
    }

    /* save header structure */
    state.head = head;
    head.done = false;
    return Z_OK$1;
  };
  var inflateSetDictionary = function inflateSetDictionary(strm, dictionary) {
    var dictLength = dictionary.length;
    var state;
    var dictid;
    var ret;

    /* check state */
    if (inflateStateCheck(strm)) {
      return Z_STREAM_ERROR$1;
    }
    state = strm.state;
    if (state.wrap !== 0 && state.mode !== DICT) {
      return Z_STREAM_ERROR$1;
    }

    /* check for correct dictionary identifier */
    if (state.mode === DICT) {
      dictid = 1; /* adler32(0, null, 0)*/
      /* dictid = adler32(dictid, dictionary, dictLength); */
      dictid = adler32_1(dictid, dictionary, dictLength, 0);
      if (dictid !== state.check) {
        return Z_DATA_ERROR$1;
      }
    }
    /* copy dictionary to window using updatewindow(), which will amend the
     existing dictionary if appropriate */
    ret = updatewindow(strm, dictionary, dictLength, dictLength);
    if (ret) {
      state.mode = MEM;
      return Z_MEM_ERROR$1;
    }
    state.havedict = 1;
    // Tracev((stderr, "inflate:   dictionary set\n"));
    return Z_OK$1;
  };
  var inflateReset_1 = inflateReset;
  var inflateReset2_1 = inflateReset2;
  var inflateResetKeep_1 = inflateResetKeep;
  var inflateInit_1 = inflateInit;
  var inflateInit2_1 = inflateInit2;
  var inflate_2$1 = inflate$2;
  var inflateEnd_1 = inflateEnd;
  var inflateGetHeader_1 = inflateGetHeader;
  var inflateSetDictionary_1 = inflateSetDictionary;
  var inflateInfo = 'pako inflate (from Nodeca project)';

  /* Not implemented
  module.exports.inflateCodesUsed = inflateCodesUsed;
  module.exports.inflateCopy = inflateCopy;
  module.exports.inflateGetDictionary = inflateGetDictionary;
  module.exports.inflateMark = inflateMark;
  module.exports.inflatePrime = inflatePrime;
  module.exports.inflateSync = inflateSync;
  module.exports.inflateSyncPoint = inflateSyncPoint;
  module.exports.inflateUndermine = inflateUndermine;
  module.exports.inflateValidate = inflateValidate;
  */

  var inflate_1$2 = {
    inflateReset: inflateReset_1,
    inflateReset2: inflateReset2_1,
    inflateResetKeep: inflateResetKeep_1,
    inflateInit: inflateInit_1,
    inflateInit2: inflateInit2_1,
    inflate: inflate_2$1,
    inflateEnd: inflateEnd_1,
    inflateGetHeader: inflateGetHeader_1,
    inflateSetDictionary: inflateSetDictionary_1,
    inflateInfo: inflateInfo
  };

  // (C) 1995-2013 Jean-loup Gailly and Mark Adler
  // (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
  //
  // This software is provided 'as-is', without any express or implied
  // warranty. In no event will the authors be held liable for any damages
  // arising from the use of this software.
  //
  // Permission is granted to anyone to use this software for any purpose,
  // including commercial applications, and to alter it and redistribute it
  // freely, subject to the following restrictions:
  //
  // 1. The origin of this software must not be misrepresented; you must not
  //   claim that you wrote the original software. If you use this software
  //   in a product, an acknowledgment in the product documentation would be
  //   appreciated but is not required.
  // 2. Altered source versions must be plainly marked as such, and must not be
  //   misrepresented as being the original software.
  // 3. This notice may not be removed or altered from any source distribution.
  function GZheader() {
    /* true if compressed data believed to be text */
    this.text = 0;
    /* modification time */
    this.time = 0;
    /* extra flags (not used when writing a gzip file) */
    this.xflags = 0;
    /* operating system */
    this.os = 0;
    /* pointer to extra field or Z_NULL if none */
    this.extra = null;
    /* extra field length (valid if extra != Z_NULL) */
    this.extra_len = 0; // Actually, we don't need it in JS,
    // but leave for few code modifications

    //
    // Setup limits is not necessary because in js we should not preallocate memory
    // for inflate use constant limit in 65536 bytes
    //

    /* space at extra (only when reading header) */
    // this.extra_max  = 0;
    /* pointer to zero-terminated file name or Z_NULL */
    this.name = '';
    /* space at name (only when reading header) */
    // this.name_max   = 0;
    /* pointer to zero-terminated comment or Z_NULL */
    this.comment = '';
    /* space at comment (only when reading header) */
    // this.comm_max   = 0;
    /* true if there was or will be a header crc */
    this.hcrc = 0;
    /* true when done reading gzip header (not used when writing a gzip file) */
    this.done = false;
  }
  var gzheader = GZheader;

  var toString = Object.prototype.toString;

  /* Public constants ==========================================================*/
  /* ===========================================================================*/

  var Z_NO_FLUSH = constants$2.Z_NO_FLUSH,
    Z_FINISH = constants$2.Z_FINISH,
    Z_OK = constants$2.Z_OK,
    Z_STREAM_END = constants$2.Z_STREAM_END,
    Z_NEED_DICT = constants$2.Z_NEED_DICT,
    Z_STREAM_ERROR = constants$2.Z_STREAM_ERROR,
    Z_DATA_ERROR = constants$2.Z_DATA_ERROR,
    Z_MEM_ERROR = constants$2.Z_MEM_ERROR;

  /* ===========================================================================*/

  /**
   * class Inflate
   *
   * Generic JS-style wrapper for zlib calls. If you don't need
   * streaming behaviour - use more simple functions: [[inflate]]
   * and [[inflateRaw]].
   **/

  /* internal
   * inflate.chunks -> Array
   *
   * Chunks of output data, if [[Inflate#onData]] not overridden.
   **/

  /**
   * Inflate.result -> Uint8Array|String
   *
   * Uncompressed result, generated by default [[Inflate#onData]]
   * and [[Inflate#onEnd]] handlers. Filled after you push last chunk
   * (call [[Inflate#push]] with `Z_FINISH` / `true` param).
   **/

  /**
   * Inflate.err -> Number
   *
   * Error code after inflate finished. 0 (Z_OK) on success.
   * Should be checked if broken data possible.
   **/

  /**
   * Inflate.msg -> String
   *
   * Error message, if [[Inflate.err]] != 0
   **/

  /**
   * new Inflate(options)
   * - options (Object): zlib inflate options.
   *
   * Creates new inflator instance with specified params. Throws exception
   * on bad params. Supported options:
   *
   * - `windowBits`
   * - `dictionary`
   *
   * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
   * for more information on these.
   *
   * Additional options, for internal needs:
   *
   * - `chunkSize` - size of generated data chunks (16K by default)
   * - `raw` (Boolean) - do raw inflate
   * - `to` (String) - if equal to 'string', then result will be converted
   *   from utf8 to utf16 (javascript) string. When string output requested,
   *   chunk length can differ from `chunkSize`, depending on content.
   *
   * By default, when no options set, autodetect deflate/gzip data format via
   * wrapper header.
   *
   * ##### Example:
   *
   * ```javascript
   * const pako = require('pako')
   * const chunk1 = new Uint8Array([1,2,3,4,5,6,7,8,9])
   * const chunk2 = new Uint8Array([10,11,12,13,14,15,16,17,18,19]);
   *
   * const inflate = new pako.Inflate({ level: 3});
   *
   * inflate.push(chunk1, false);
   * inflate.push(chunk2, true);  // true -> last chunk
   *
   * if (inflate.err) { throw new Error(inflate.err); }
   *
   * console.log(inflate.result);
   * ```
   **/
  function Inflate$1(options) {
    this.options = common.assign({
      chunkSize: 1024 * 64,
      windowBits: 15,
      to: ''
    }, options || {});
    var opt = this.options;

    // Force window size for `raw` data, if not set directly,
    // because we have no header for autodetect.
    if (opt.raw && opt.windowBits >= 0 && opt.windowBits < 16) {
      opt.windowBits = -opt.windowBits;
      if (opt.windowBits === 0) {
        opt.windowBits = -15;
      }
    }

    // If `windowBits` not defined (and mode not raw) - set autodetect flag for gzip/deflate
    if (opt.windowBits >= 0 && opt.windowBits < 16 && !(options && options.windowBits)) {
      opt.windowBits += 32;
    }

    // Gzip header has no info about windows size, we can do autodetect only
    // for deflate. So, if window size not set, force it to max when gzip possible
    if (opt.windowBits > 15 && opt.windowBits < 48) {
      // bit 3 (16) -> gzipped data
      // bit 4 (32) -> autodetect gzip/deflate
      if ((opt.windowBits & 15) === 0) {
        opt.windowBits |= 15;
      }
    }
    this.err = 0; // error code, if happens (0 = Z_OK)
    this.msg = ''; // error message
    this.ended = false; // used to avoid multiple onEnd() calls
    this.chunks = []; // chunks of compressed data

    this.strm = new zstream();
    this.strm.avail_out = 0;
    var status = inflate_1$2.inflateInit2(this.strm, opt.windowBits);
    if (status !== Z_OK) {
      throw new Error(messages[status]);
    }
    this.header = new gzheader();
    inflate_1$2.inflateGetHeader(this.strm, this.header);

    // Setup dictionary
    if (opt.dictionary) {
      // Convert data if needed
      if (typeof opt.dictionary === 'string') {
        opt.dictionary = strings.string2buf(opt.dictionary);
      } else if (toString.call(opt.dictionary) === '[object ArrayBuffer]') {
        opt.dictionary = new Uint8Array(opt.dictionary);
      }
      if (opt.raw) {
        //In raw mode we need to set the dictionary early
        status = inflate_1$2.inflateSetDictionary(this.strm, opt.dictionary);
        if (status !== Z_OK) {
          throw new Error(messages[status]);
        }
      }
    }
  }

  /**
   * Inflate#push(data[, flush_mode]) -> Boolean
   * - data (Uint8Array|ArrayBuffer): input data
   * - flush_mode (Number|Boolean): 0..6 for corresponding Z_NO_FLUSH..Z_TREE
   *   flush modes. See constants. Skipped or `false` means Z_NO_FLUSH,
   *   `true` means Z_FINISH.
   *
   * Sends input data to inflate pipe, generating [[Inflate#onData]] calls with
   * new output chunks. Returns `true` on success. If end of stream detected,
   * [[Inflate#onEnd]] will be called.
   *
   * `flush_mode` is not needed for normal operation, because end of stream
   * detected automatically. You may try to use it for advanced things, but
   * this functionality was not tested.
   *
   * On fail call [[Inflate#onEnd]] with error code and return false.
   *
   * ##### Example
   *
   * ```javascript
   * push(chunk, false); // push one of data chunks
   * ...
   * push(chunk, true);  // push last chunk
   * ```
   **/
  Inflate$1.prototype.push = function (data, flush_mode) {
    var strm = this.strm;
    var chunkSize = this.options.chunkSize;
    var dictionary = this.options.dictionary;
    var status, _flush_mode, last_avail_out;
    if (this.ended) return false;
    if (flush_mode === ~~flush_mode) _flush_mode = flush_mode;else _flush_mode = flush_mode === true ? Z_FINISH : Z_NO_FLUSH;

    // Convert data if needed
    if (toString.call(data) === '[object ArrayBuffer]') {
      strm.input = new Uint8Array(data);
    } else {
      strm.input = data;
    }
    strm.next_in = 0;
    strm.avail_in = strm.input.length;
    for (;;) {
      if (strm.avail_out === 0) {
        strm.output = new Uint8Array(chunkSize);
        strm.next_out = 0;
        strm.avail_out = chunkSize;
      }
      status = inflate_1$2.inflate(strm, _flush_mode);
      if (status === Z_NEED_DICT && dictionary) {
        status = inflate_1$2.inflateSetDictionary(strm, dictionary);
        if (status === Z_OK) {
          status = inflate_1$2.inflate(strm, _flush_mode);
        } else if (status === Z_DATA_ERROR) {
          // Replace code with more verbose
          status = Z_NEED_DICT;
        }
      }

      // Skip snyc markers if more data follows and not raw mode
      while (strm.avail_in > 0 && status === Z_STREAM_END && strm.state.wrap > 0 && data[strm.next_in] !== 0) {
        inflate_1$2.inflateReset(strm);
        status = inflate_1$2.inflate(strm, _flush_mode);
      }
      switch (status) {
        case Z_STREAM_ERROR:
        case Z_DATA_ERROR:
        case Z_NEED_DICT:
        case Z_MEM_ERROR:
          this.onEnd(status);
          this.ended = true;
          return false;
      }

      // Remember real `avail_out` value, because we may patch out buffer content
      // to align utf8 strings boundaries.
      last_avail_out = strm.avail_out;
      if (strm.next_out) {
        if (strm.avail_out === 0 || status === Z_STREAM_END) {
          if (this.options.to === 'string') {
            var next_out_utf8 = strings.utf8border(strm.output, strm.next_out);
            var tail = strm.next_out - next_out_utf8;
            var utf8str = strings.buf2string(strm.output, next_out_utf8);

            // move tail & realign counters
            strm.next_out = tail;
            strm.avail_out = chunkSize - tail;
            if (tail) strm.output.set(strm.output.subarray(next_out_utf8, next_out_utf8 + tail), 0);
            this.onData(utf8str);
          } else {
            this.onData(strm.output.length === strm.next_out ? strm.output : strm.output.subarray(0, strm.next_out));
          }
        }
      }

      // Must repeat iteration if out buffer is full
      if (status === Z_OK && last_avail_out === 0) continue;

      // Finalize if end of stream reached.
      if (status === Z_STREAM_END) {
        status = inflate_1$2.inflateEnd(this.strm);
        this.onEnd(status);
        this.ended = true;
        return true;
      }
      if (strm.avail_in === 0) break;
    }
    return true;
  };

  /**
   * Inflate#onData(chunk) -> Void
   * - chunk (Uint8Array|String): output data. When string output requested,
   *   each chunk will be string.
   *
   * By default, stores data blocks in `chunks[]` property and glue
   * those in `onEnd`. Override this handler, if you need another behaviour.
   **/
  Inflate$1.prototype.onData = function (chunk) {
    this.chunks.push(chunk);
  };

  /**
   * Inflate#onEnd(status) -> Void
   * - status (Number): inflate status. 0 (Z_OK) on success,
   *   other if not.
   *
   * Called either after you tell inflate that the input stream is
   * complete (Z_FINISH). By default - join collected chunks,
   * free memory and fill `results` / `err` properties.
   **/
  Inflate$1.prototype.onEnd = function (status) {
    // On success - join
    if (status === Z_OK) {
      if (this.options.to === 'string') {
        this.result = this.chunks.join('');
      } else {
        this.result = common.flattenChunks(this.chunks);
      }
    }
    this.chunks = [];
    this.err = status;
    this.msg = this.strm.msg;
  };

  /**
   * inflate(data[, options]) -> Uint8Array|String
   * - data (Uint8Array|ArrayBuffer): input data to decompress.
   * - options (Object): zlib inflate options.
   *
   * Decompress `data` with inflate/ungzip and `options`. Autodetect
   * format via wrapper header by default. That's why we don't provide
   * separate `ungzip` method.
   *
   * Supported options are:
   *
   * - windowBits
   *
   * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
   * for more information.
   *
   * Sugar (options):
   *
   * - `raw` (Boolean) - say that we work with raw stream, if you don't wish to specify
   *   negative windowBits implicitly.
   * - `to` (String) - if equal to 'string', then result will be converted
   *   from utf8 to utf16 (javascript) string. When string output requested,
   *   chunk length can differ from `chunkSize`, depending on content.
   *
   *
   * ##### Example:
   *
   * ```javascript
   * const pako = require('pako');
   * const input = pako.deflate(new Uint8Array([1,2,3,4,5,6,7,8,9]));
   * let output;
   *
   * try {
   *   output = pako.inflate(input);
   * } catch (err) {
   *   console.log(err);
   * }
   * ```
   **/
  function inflate$1(input, options) {
    var inflator = new Inflate$1(options);
    inflator.push(input);

    // That will never happens, if you don't cheat with options :)
    if (inflator.err) throw inflator.msg || messages[inflator.err];
    return inflator.result;
  }

  /**
   * inflateRaw(data[, options]) -> Uint8Array|String
   * - data (Uint8Array|ArrayBuffer): input data to decompress.
   * - options (Object): zlib inflate options.
   *
   * The same as [[inflate]], but creates raw data, without wrapper
   * (header and adler32 crc).
   **/
  function inflateRaw$1(input, options) {
    options = options || {};
    options.raw = true;
    return inflate$1(input, options);
  }

  /**
   * ungzip(data[, options]) -> Uint8Array|String
   * - data (Uint8Array|ArrayBuffer): input data to decompress.
   * - options (Object): zlib inflate options.
   *
   * Just shortcut to [[inflate]], because it autodetects format
   * by header.content. Done for convenience.
   **/

  var Inflate_1$1 = Inflate$1;
  var inflate_2 = inflate$1;
  var inflateRaw_1$1 = inflateRaw$1;
  var ungzip$1 = inflate$1;
  var constants = constants$2;
  var inflate_1$1 = {
    Inflate: Inflate_1$1,
    inflate: inflate_2,
    inflateRaw: inflateRaw_1$1,
    ungzip: ungzip$1,
    constants: constants
  };

  var Deflate = deflate_1$1.Deflate,
    deflate = deflate_1$1.deflate,
    deflateRaw = deflate_1$1.deflateRaw,
    gzip = deflate_1$1.gzip;
  var Inflate = inflate_1$1.Inflate,
    inflate = inflate_1$1.inflate,
    inflateRaw = inflate_1$1.inflateRaw,
    ungzip = inflate_1$1.ungzip;
  var Deflate_1 = Deflate;
  var deflate_1 = deflate;
  var deflateRaw_1 = deflateRaw;
  var gzip_1 = gzip;
  var Inflate_1 = Inflate;
  var inflate_1 = inflate;
  var inflateRaw_1 = inflateRaw;
  var ungzip_1 = ungzip;
  var constants_1 = constants$2;
  var pako = {
    Deflate: Deflate_1,
    deflate: deflate_1,
    deflateRaw: deflateRaw_1,
    gzip: gzip_1,
    Inflate: Inflate_1,
    inflate: inflate_1,
    inflateRaw: inflateRaw_1,
    ungzip: ungzip_1,
    constants: constants_1
  };

  exports.Deflate = Deflate_1;
  exports.Inflate = Inflate_1;
  exports.constants = constants_1;
  exports["default"] = pako;
  exports.deflate = deflate_1;
  exports.deflateRaw = deflateRaw_1;
  exports.gzip = gzip_1;
  exports.inflate = inflate_1;
  exports.inflateRaw = inflateRaw_1;
  exports.ungzip = ungzip_1;

  Object.defineProperty(exports, '__esModule', { value: true });

}));

})();
(function () {
!function(){"use strict";var e,t,r,n=e={};function o(){throw new Error("setTimeout has not been defined")}function a(){throw new Error("clearTimeout has not been defined")}function i(e){if(t===setTimeout)return setTimeout(e,0);if((t===o||!t)&&setTimeout)return t=setTimeout,setTimeout(e,0);try{return t(e,0)}catch(r){try{return t.call(null,e,0)}catch(r){return t.call(this,e,0)}}}!function(){try{t="function"==typeof setTimeout?setTimeout:o}catch(e){t=o}try{r="function"==typeof clearTimeout?clearTimeout:a}catch(e){r=a}}();var u,l=[],s=!1,c=-1;function f(){s&&u&&(s=!1,u.length?l=u.concat(l):c=-1,l.length&&y())}function y(){if(!s){var e=i(f);s=!0;for(var t=l.length;t;){for(u=l,l=[];++c<t;)u&&u[c].run();c=-1,t=l.length}u=null,s=!1,function(e){if(r===clearTimeout)return clearTimeout(e);if((r===a||!r)&&clearTimeout)return r=clearTimeout,clearTimeout(e);try{r(e)}catch(t){try{return r.call(null,e)}catch(t){return r.call(this,e)}}}(e)}}function m(e,t){this.fun=e,this.array=t}function T(){}n.nextTick=function(e){var t=new Array(arguments.length-1);if(arguments.length>1)for(var r=1;r<arguments.length;r++)t[r-1]=arguments[r];l.push(new m(e,t)),1!==l.length||s||i(y)},m.prototype.run=function(){this.fun.apply(null,this.array)},n.title="browser",n.browser=!0,n.env={},n.argv=[],n.version="",n.versions={},n.on=T,n.addListener=T,n.once=T,n.off=T,n.removeListener=T,n.removeAllListeners=T,n.emit=T,n.prependListener=T,n.prependOnceListener=T,n.listeners=function(e){return[]},n.binding=function(e){throw new Error("process.binding is not supported")},n.cwd=function(){return"/"},n.chdir=function(e){throw new Error("process.chdir is not supported")},n.umask=function(){return 0};var h={};(function(e){(function(){var t;if("undefined"!=typeof navigator){var r=navigator;t=Array.isArray(r.languages)?r.languages[0]:r.language}else if(void 0!==e){var n=e.env;t=n.LC_ALL||n.LC_MESSAGES||n.LANG||n.LANGUAGE}t||(t="en-US"),h=function(e){e=(e=e.replace(/_/g,"-")).replace(/[.:][\w-]*$/,"");try{return new Intl.Locale(e).baseName}catch(t){return"en-US"}}(t)}).call(this)}).call(this,e);for(var d={},p=1;p<100;p++)d[(p<9?"0":"")+p]=p+(p>51?1900:2e3);var v={"Eastern Daylight Time":-240,"Eastern Standard Time":-300,"Central Daylight Time":-300,"Central Standard Time":-360,"Mountain Daylight Time":-360,"Mountain Standard Time":-420,"Pacific Daylight Time":-420,"Pacific Standard Time":-480,ACDT:630,ACST:570,ACT:480,ADT:-180,AEDT:660,AEST:600,AFT:270,AKDT:-480,AKST:-540,AMST:-180,AMT:-240,ART:-180,AST:180,AWDT:540,AWST:480,AZOST:-60,AZT:240,BDT:360,BIOT:360,BIT:-720,BOT:-240,BRST:-120,BRT:-180,BTT:360,CAT:120,CCT:390,CDT:-300,CEDT:120,CEST:120,CET:60,CHADT:825,CHAST:765,CHOT:480,ChST:600,CHUT:600,CIST:-480,CIT:480,CKT:-600,CLST:-180,CLT:-240,COST:-240,COT:-300,CST:-360,CT:480,CVT:-60,CXT:420,DAVT:420,DDUT:600,DFT:60,EASST:-300,EAST:-360,EAT:180,ECT:-300,EDT:-240,EEDT:180,EEST:180,EET:120,EGST:0,EGT:-60,EIT:540,EST:-300,FET:180,FJT:720,FKST:-180,FKT:-240,FNT:-120,GALT:-360,GAMT:-540,GET:240,GFT:-180,GILT:720,GIT:-540,GMT:0,GST:-120,GYT:-240,HADT:-540,HAEC:120,HAST:-600,HKT:480,HMT:300,HOVT:420,HST:-600,IBST:0,ICT:420,IDT:180,IOT:180,IRDT:270,IRKT:480,IRST:210,IST:120,JST:540,KGT:360,KOST:660,KRAT:420,KST:540,LHST:630,LINT:840,MAGT:720,MART:-510,MAWT:300,MDT:-360,MET:60,MEST:120,MHT:720,MIST:660,MIT:-510,MMT:390,MSK:180,MST:-420,MUT:240,MVT:300,MYT:480,NCT:660,NDT:-90,NFT:660,NPT:345,NST:-150,NT:-150,NUT:-660,NZDT:780,NZST:720,OMST:360,ORAT:300,PDT:-420,PET:-300,PETT:720,PGT:600,PHOT:780,PKT:300,PMDT:-120,PMST:-180,PONT:660,PST:-480,PYST:-180,PYT:-240,RET:240,ROTT:-180,SAKT:660,SAMT:240,SAST:120,SBT:660,SCT:240,SGT:480,SLST:330,SRET:660,SRT:-180,SST:480,SYOT:180,TAHT:-600,THA:420,TFT:300,TJT:300,TKT:780,TLT:540,TMT:300,TOT:780,TVT:720,UCT:0,ULAT:480,USZ1:120,UTC:0,UYST:-120,UYT:-180,UZT:300,VET:-240,VLAT:600,VOLT:240,VOST:360,VUT:660,WAKT:720,WAST:120,WAT:60,WEDT:60,WEST:60,WET:0,WIT:420,WST:480,YAKT:540,YEKT:300,Z:0},g={zone:v,year:d,meridiem:{am:0,pm:12,"a.m.":0,"p.m.":12},month:{january:1,jan:1,february:2,feb:2,march:3,mar:3,april:4,apr:4,may:5,june:6,jun:6,july:7,jul:7,august:8,aug:8,september:9,sep:9,october:10,oct:10,november:11,nov:11,december:12,dec:12},dayname:{sunday:0,sun:0,monday:1,mon:1,tuesday:2,tue:2,wednesday:3,wed:3,thursday:4,thu:4,friday:5,fri:5,saturday:6,sat:6},digit:{}};function b(e,t){var r=Object.keys(e);if(Object.getOwnPropertySymbols){var n=Object.getOwnPropertySymbols(e);t&&(n=n.filter((function(t){return Object.getOwnPropertyDescriptor(e,t).enumerable}))),r.push.apply(r,n)}return r}function A(e){for(var t=1;t<arguments.length;t++){var r=null!=arguments[t]?arguments[t]:{};t%2?b(Object(r),!0).forEach((function(t){S(e,t,r[t])})):Object.getOwnPropertyDescriptors?Object.defineProperties(e,Object.getOwnPropertyDescriptors(r)):b(Object(r)).forEach((function(t){Object.defineProperty(e,t,Object.getOwnPropertyDescriptor(r,t))}))}return e}function S(e,t,r){return t in e?Object.defineProperty(e,t,{value:r,enumerable:!0,configurable:!0,writable:!0}):e[t]=r,e}var w={MONTHNAME:"january|february|march|april|may|june|july|august|september|october|november|december|jan\\.?|feb\\.?|mar\\.?|apr\\.?|may\\.?|jun\\.?|jul\\.?|aug\\.?|sep\\.?|oct\\.?|nov\\.?|dec\\.?",DAYNAME:"sunday|monday|tuesday|wednesday|thursday|friday|saturday|sun\\.?|mon\\.?|tue\\.?|wed\\.?|thu\\.?|fri\\.?|sat\\.?",ZONE:"\\(?("+Object.keys(v).join("|")+")\\)?",MERIDIEM:"[ap]\\.?m?\\.?",ORDINAL:"st|nd|rd|th|\\.",YEAR:"[1-9]\\d{3}|\\d{2}",MONTH:"1[0-2]|0?[1-9]",MONTH2:"1[0-2]|0[1-9]",DAY:"3[01]|[12]\\d|0?[1-9]",DAY2:"3[01]|[12]\\d|0[1-9]",OFFSET:"[+-][01]?\\d?\\:?(?:[0-5]\\d)?",H24:"[01]\\d|2[0-3]",H12:"0?[1-9]|1[012]",MIN:"[0-5]\\d",SEC:"[0-5]\\d|60",MS:"\\d{9}|\\d{6}|\\d{1,3}",SPACE:"[\\s,-]"},C={latn:w,other:A(A({},w),{},{YEAR:"*{4}|*{2}",MONTH:"*{1,2}",MONTH2:"*{2}",DAY:"*{1,2}",DAY2:"*{2}",OFFSET:"[+-]*{1,2}\\:?*{0,2}",H24:"*{2}",H12:"*{1,2}",MIN:"*{2}",SEC:"*{2}",MS:"*{9}|*{6}|*{3}"})};function O(e,t){var r=Object.keys(e);if(Object.getOwnPropertySymbols){var n=Object.getOwnPropertySymbols(e);t&&(n=n.filter((function(t){return Object.getOwnPropertyDescriptor(e,t).enumerable}))),r.push.apply(r,n)}return r}function _(e){for(var t=1;t<arguments.length;t++){var r=null!=arguments[t]?arguments[t]:{};t%2?O(Object(r),!0).forEach((function(t){E(e,t,r[t])})):Object.getOwnPropertyDescriptors?Object.defineProperties(e,Object.getOwnPropertyDescriptors(r)):O(Object(r)).forEach((function(t){Object.defineProperty(e,t,Object.getOwnPropertyDescriptor(r,t))}))}return e}function E(e,t,r){return t in e?Object.defineProperty(e,t,{value:r,enumerable:!0,configurable:!0,writable:!0}):e[t]=r,e}var M={arab:1632,arabext:1776,bali:6992,beng:2534,deva:2406,fullwide:65296,gujr:2790,khmr:6112,knda:3302,laoo:3792,limb:6470,mlym:3430,mong:6160,mymr:4160,orya:2918,tamldec:3046,telu:3174,thai:3664,tibt:3872},D={0:0,1:1,2:2,3:3,4:4,5:5,6:6,7:7,8:8,9:9,"\uff10":0,"\uff11":1,"\uff12":2,"\uff13":3,"\uff14":4,"\uff15":5,"\uff16":6,"\uff17":7,"\uff18":8,"\uff19":9,"\u3007":0,"\u4e00":1,"\u4e8c":2,"\u4e09":3,"\u56db":4,"\u4e94":5,"\u516d":6,"\u4e03":7,"\u516b":8,"\u4e5d":9},j={},U="[\uff10\uff11\uff12\uff13\uff14\uff15\uff16\uff17\uff18\uff19\u3007\u4e00\u4e8c\u4e09\u56db\u4e94\u516d\u4e03\u516b\u4e5d\\d]",I=["year","month","day","hour","minute","second","millisecond"];function P(e,t){(null==t||t>e.length)&&(t=e.length);for(var r=0,n=new Array(t);r<t;r++)n[r]=e[r];return n}function N(e,t){var r=Object.keys(e);if(Object.getOwnPropertySymbols){var n=Object.getOwnPropertySymbols(e);t&&(n=n.filter((function(t){return Object.getOwnPropertyDescriptor(e,t).enumerable}))),r.push.apply(r,n)}return r}function k(e){for(var t=1;t<arguments.length;t++){var r=null!=arguments[t]?arguments[t]:{};t%2?N(Object(r),!0).forEach((function(t){H(e,t,r[t])})):Object.getOwnPropertyDescriptors?Object.defineProperties(e,Object.getOwnPropertyDescriptors(r)):N(Object(r)).forEach((function(t){Object.defineProperty(e,t,Object.getOwnPropertyDescriptor(r,t))}))}return e}function H(e,t,r){return t in e?Object.defineProperty(e,t,{value:r,enumerable:!0,configurable:!0,writable:!0}):e[t]=r,e}function L(e,t){for(var r=0;r<t.length;r++){var n=t[r];n.enumerable=n.enumerable||!1,n.configurable=!0,"value"in n&&(n.writable=!0),Object.defineProperty(e,n.key,n)}}var F=C.latn,Y=C.other,R=function(e){if(j[e])return j[e];if("fullwide"===e||"hanidec"===e)return{group:"[\uff10\uff11\uff12\uff13\uff14\uff15\uff16\uff17\uff18\uff19\u3007\u4e00\u4e8c\u4e09\u56db\u4e94\u516d\u4e03\u516b\u4e5d\\d]",lookup:_({},D)};var t=M[e];if(!t)return{group:"\\d",lookup:_({},D)};for(var r=String.fromCharCode(t),n=String.fromCharCode(t+9),o={},a=0;a<10;a++)o[String.fromCharCode(t+a)]=a;return j[e]={group:"[".concat(r,"-").concat(n,"]"),lookup:o},j[e]},$={},G=function(){function e(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:h;!function(e,t){if(!(e instanceof t))throw new TypeError("Cannot call a class as a function")}(this,e),this.locale=t,this.lookups=k({},g),this.vars=k({},F);var r=new Intl.NumberFormat(this.locale);this.numberingSystem=r.resolvedOptions().numberingSystem,this.build()}var t,r,n;return t=e,n=[{key:"factory",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:h;return $[t.toLowerCase()]||($[t.toLowerCase()]=new e(t)),$[t.toLowerCase()]}}],(r=[{key:"toInt",value:function(e){if("number"==typeof e)return e;if("latn"===this.numberingSystem)return parseInt(e,10);for(var t="",r=0;r<e.length;r++)t+=String(this.lookups.digit[e[r]]);return parseInt(t,10)}},{key:"build",value:function(){"latn"!==this.numberingSystem&&this.buildNumbers(),/^en/i.test(this.locale)||(this.buildMonthNames(),this.buildDaynames(),this.buildMeridiems())}},{key:"buildNumbers",value:function(){var e=this.numberingSystem,t=R(e),r=t.group,n=t.lookup;for(var o in this.lookups.digit=n,Y)Y.hasOwnProperty(o)&&(this.vars[o]=Y[o].replace(/\*/g,r))}},{key:"buildMonthNames",value:function(){var e={},t={};if(/^fi/i.test(this.locale))"tammi|helmi|maalis|huhti|touko|kes\xe4|hein\xe4|elo|syys|loka|marras|joulu".split("|").forEach((function(r,n){["","k","kuu","kuuta"].forEach((function(o,a){e[r+o+(a<2?"\\.?":"")]=!0,t[r+o]=n+1}))}));else{for(var r=[],n=function(e){return"month"===e.type},o=0;o<12;o++)r.push(new Date(2017,o,1));for(var a=0,i=["full","long","medium"];a<i.length;a++)for(var u=i[a],l=Intl.DateTimeFormat(this.locale,{dateStyle:u}),s=0;s<12;s++){var c=l.formatToParts(r[s]).find(n).value.toLowerCase();if(/^ko/i.test(this.locale)&&(c+="\uc6d4"),"medium"===u){if(/^ar|zh/i.test(this.locale))return;c=c.replace(/\.$/,""),e["".concat(c,"\\.?")]=!0}else e[c]=!0;t[c]=s+1}for(var f=Intl.DateTimeFormat(this.locale,{month:"short"}),y=0;y<12;y++){var m=f.formatToParts(r[y]).find(n).value.toLowerCase();m=m.replace(/\.$/,""),e["".concat(m,"\\.?")]=!0,t[m]=y+1}}this.vars.MONTHNAME=Object.keys(e).join("|"),this.lookups.month=t}},{key:"buildDaynames",value:function(){for(var e=[],t=function(e){return"weekday"===e.type},r=0;r<7;r++)e.push(new Date(2017,0,r+1));for(var n=[],o={},a=0,i=["long","short"];a<i.length;a++)for(var u=i[a],l=Intl.DateTimeFormat(this.locale,{weekday:u}),s=0;s<7;s++){var c=l.formatToParts(e[s]).find(t).value.toLowerCase();"short"===u?(c=c.replace(/\.$/,""),n.push("".concat(c,"\\.?"))):n.push(c),o[c]=s}this.vars.DAYNAME=n.join("|"),this.lookups.dayname=o}},{key:"buildMeridiems",value:function(){for(var e=[new Date(2017,0,1),new Date(2017,0,1,23,0,0)],t=function(e){return"dayPeriod"===e.type},r=[],n={},o=Intl.DateTimeFormat(this.locale,{timeStyle:"long"}),a=0;a<2;a++){var i=o.formatToParts(e[a]).find(t);if(!i)return;var u=i.value.toLowerCase();r.push(u),n[u]=12*a}this.vars.MERIDIEM=r.join("|"),this.lookups.meridiem=n}},{key:"getObject",value:function(e,t){var r=this,n={};return e.forEach((function(e,o){if(e){var a=t[o+1];a=(a=a.toLowerCase()).replace(/\.$/,""),"offset"===e?n.offset=r.offsetToMinutes(a):r.lookups[e]?n[e]=r.lookups[e][a]||r.toInt(a):n[e]=r.toInt(a)}})),n}},{key:"castObject",value:function(e){var t=this,r={};return I.forEach((function(n){n in e&&(r[n]=t.toInt(e[n]))})),"string"==typeof e.offset?r.offset=this.offsetToMinutes(e.offset):"number"==typeof e.offset&&(r.offset=e.offset),r}},{key:"offsetToMinutes",value:function(e){var t=e.match(/^([+-])(..?):?(..)?$/);if(t){var r=function(e,t){return function(e){if(Array.isArray(e))return e}(e)||function(e,t){var r=null==e?null:"undefined"!=typeof Symbol&&e[Symbol.iterator]||e["@@iterator"];if(null!=r){var n,o,a=[],i=!0,u=!1;try{for(r=r.call(e);!(i=(n=r.next()).done)&&(a.push(n.value),!t||a.length!==t);i=!0);}catch(l){u=!0,o=l}finally{try{i||null==r.return||r.return()}finally{if(u)throw o}}return a}}(e,t)||function(e,t){if(e){if("string"==typeof e)return P(e,t);var r=Object.prototype.toString.call(e).slice(8,-1);return"Object"===r&&e.constructor&&(r=e.constructor.name),"Map"===r||"Set"===r?Array.from(e):"Arguments"===r||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(r)?P(e,t):void 0}}(e,t)||function(){throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}()}(t,4),n=r[1],o=r[2],a=r[3];return("-"===n?-1:1)*(60*this.toInt(o)+this.toInt(a||0))}return 0}},{key:"compile",value:function(e){var t=this,r=e.replace(/_([A-Z0-9]+)_/g,(function(e,r){if(!t.vars[r])throw new Error("Template string contains invalid variable _".concat(r,"_"));return t.vars[r]}));return new RegExp(r,"i")}}])&&L(t.prototype,r),n&&L(t,n),Object.defineProperty(t,"prototype",{writable:!1}),e}();function x(e,t){for(var r=0;r<t.length;r++){var n=t[r];n.enumerable=n.enumerable||!1,n.configurable=!0,"value"in n&&(n.writable=!0),Object.defineProperty(e,n.key,n)}}var K=function(){function e(t){var r=t.template,n=void 0===r?null:r,o=t.matcher,a=void 0===o?null:o,i=t.units,u=void 0===i?null:i,l=t.handler,s=void 0===l?null:l,c=t.locales,f=void 0===c?null:c;if(function(e,t){if(!(e instanceof t))throw new TypeError("Cannot call a class as a function")}(this,e),!Array.isArray(u)&&"function"!=typeof s)throw new Error('new Format must receive a "units" array or "handler" function');if("string"!=typeof n&&!(a instanceof RegExp))throw new Error('new Format must receive a "template" string or "matcher" RegExp');this.template=n,this.units=u,this.matcher=a,this.handler=s,this.locales=f,this.regexByLocale={}}var t,r;return t=e,(r=[{key:"getRegExp",value:function(){var e=arguments.length>0&&void 0!==arguments[0]?arguments[0]:h;return this.template?(this.regexByLocale[e]||(this.regexByLocale[e]=G.factory(e).compile(this.template)),this.regexByLocale[e]):this.matcher}},{key:"getMatches",value:function(e){var t=arguments.length>1&&void 0!==arguments[1]?arguments[1]:h;return e.match(this.getRegExp(t))}},{key:"toDateTime",value:function(e){var t=arguments.length>1&&void 0!==arguments[1]?arguments[1]:h,r=G.factory(t);if(this.units)return r.getObject(this.units,e);var n=this.handler(e,t);return!n||n.invalid?n:r.castObject(n)}},{key:"attempt",value:function(e){var t=arguments.length>1&&void 0!==arguments[1]?arguments[1]:h;e=String(e).trim();var r=this.getMatches(e,t);if(r){var n=this.toDateTime(r,t);if(n&&!n.invalid)return n}return null}},{key:"now",value:function(){return new Date}}])&&x(t.prototype,r),Object.defineProperty(t,"prototype",{writable:!1}),e}(),B=function(e){return function(t,r){return t instanceof Date?t:"number"==typeof t?new Date(t):e(t,r)}};function W(e,t){var r="undefined"!=typeof Symbol&&e[Symbol.iterator]||e["@@iterator"];if(!r){if(Array.isArray(e)||(r=function(e,t){if(e){if("string"==typeof e)return V(e,void 0);var r=Object.prototype.toString.call(e).slice(8,-1);return"Object"===r&&e.constructor&&(r=e.constructor.name),"Map"===r||"Set"===r?Array.from(e):"Arguments"===r||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(r)?V(e,void 0):void 0}}(e))||t&&e&&"number"==typeof e.length){r&&(e=r);var n=0,o=function(){};return{s:o,n:function(){return n>=e.length?{done:!0}:{done:!1,value:e[n++]}},e:function(e){throw e},f:o}}throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}var a,i=!0,u=!1;return{s:function(){r=r.call(e)},n:function(){var e=r.next();return i=e.done,e},e:function(e){u=!0,a=e},f:function(){try{i||null==r.return||r.return()}finally{if(u)throw a}}}}function V(e,t){(null==t||t>e.length)&&(t=e.length);for(var r=0,n=new Array(t);r<t;r++)n[r]=e[r];return n}function Z(e,t){for(var r=0;r<t.length;r++){var n=t[r];n.enumerable=n.enumerable||!1,n.configurable=!0,"value"in n&&(n.writable=!0),Object.defineProperty(e,n.key,n)}}var z=function(e,t){return function(r){var n=arguments.length>1&&void 0!==arguments[1]?arguments[1]:t,o=e.attempt(r,n);if(o.invalid)return o;var a=new Date(2e3,0,1);return"number"==typeof o.day&&a.setUTCDate(o.day),"number"==typeof o.year&&a.setUTCFullYear(o.year),"number"==typeof o.month&&a.setUTCMonth(o.month-1),a.setUTCHours(o.hour||0),a.setUTCMinutes(o.minute||0),a.setUTCSeconds(o.second||0),a.setUTCMilliseconds(o.millisecond||0),"number"==typeof o.offset?new Date(a-60*o.offset*1e3):a}},J=function(){function e(){!function(e,t){if(!(e instanceof t))throw new TypeError("Cannot call a class as a function")}(this,e),this.formats=[]}var t,r;return t=e,(r=[{key:"addFormat",value:function(e){return this.formats.push(e),e.parser=this,this}},{key:"addFormats",value:function(e){var t=this;return e.forEach((function(e){return t.addFormat(e)})),this}},{key:"removeFormat",value:function(e){var t=this.formats.indexOf(e);if(t>-1){var r=this.formats[t];return this.formats.splice(t,1),r.parser=null,!0}return!1}},{key:"attempt",value:function(e){var t,r=arguments.length>1&&void 0!==arguments[1]?arguments[1]:h,n=W(this.formats);try{for(n.s();!(t=n.n()).done;){var o=t.value;if(!(Array.isArray(o.locales)&&o.locales.length>0)||o.locales.includes(new Intl.Locale(r).baseName)){var a=o.attempt(e,r);if(a)return a}}}catch(u){n.e(u)}finally{n.f()}var i=String(e).slice(0,200);return""===i&&(i="empty string"),{invalid:"Unable to parse ".concat(i)}}},{key:"exportAsFunction",value:function(){var e=arguments.length>0&&void 0!==arguments[0]?arguments[0]:h;return z(this,e)}},{key:"exportAsFunctionAny",value:function(){var e=arguments.length>0&&void 0!==arguments[0]?arguments[0]:h;return B(z(this,e))}}])&&Z(t.prototype,r),Object.defineProperty(t,"prototype",{writable:!1}),e}(),X=new K({template:"^@(\\d+)$",handler:function(e){var t=parseInt(e[1],10),r=new Date(1e3*t);return{year:r.getUTCFullYear(),month:r.getUTCMonth()+1,day:r.getUTCDate(),hour:r.getUTCHours(),minute:r.getUTCMinutes(),second:r.getUTCSeconds()}}}),q=new K({matcher:/^\/Date\((\d+)([+-]\d{4})?\)\/$/,handler:function(e){var t=parseInt(e[1],10),r=new Date(t);return{year:r.getUTCFullYear(),month:r.getUTCMonth()+1,day:r.getUTCDate(),hour:r.getUTCHours(),minute:r.getUTCMinutes(),second:r.getUTCSeconds(),millisecond:r.getUTCMilliseconds(),offset:e[2]||0}}}),Q={y:"year",M:"month",d:"day",w:"week",h:"hour",m:"minute",s:"second",ms:"millisecond"};function ee(e,t){(null==t||t>e.length)&&(t=e.length);for(var r=0,n=new Array(t);r<t;r++)n[r]=e[r];return n}var te=new K({matcher:/^(\+|-|in|) ?([\d.]+) ?(years?|months?|weeks?|days?|hours?|minutes?|seconds?|milliseconds?|ms|s|m|h|w|d|M|y)( ago)?$/i,handler:function(e){var t=function(e,t){return function(e){if(Array.isArray(e))return e}(e)||function(e,t){var r=null==e?null:"undefined"!=typeof Symbol&&e[Symbol.iterator]||e["@@iterator"];if(null!=r){var n,o,a=[],i=!0,u=!1;try{for(r=r.call(e);!(i=(n=r.next()).done)&&(a.push(n.value),!t||a.length!==t);i=!0);}catch(l){u=!0,o=l}finally{try{i||null==r.return||r.return()}finally{if(u)throw o}}return a}}(e,t)||function(e,t){if(e){if("string"==typeof e)return ee(e,t);var r=Object.prototype.toString.call(e).slice(8,-1);return"Object"===r&&e.constructor&&(r=e.constructor.name),"Map"===r||"Set"===r?Array.from(e):"Arguments"===r||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(r)?ee(e,t):void 0}}(e,t)||function(){throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}()}(e,5),r=t[1],n=t[2],o=t[3],a=t[4];n=parseFloat(n),"week"===(o=o.length<=2?Q[o]:(o=o.replace(/s$/,"")).toLowerCase())&&(o="day",n*=7),("-"===r||a)&&(n*=-1);var i=this.now();return"millisecond"===o?i.setUTCMilliseconds(i.getUTCMilliseconds()+n):"second"===o?i.setUTCSeconds(i.getUTCSeconds()+n):"minute"===o?i.setUTCMinutes(i.getUTCMinutes()+n):"hour"===o?i.setUTCHours(i.getUTCHours()+n):"day"===o?i.setUTCDate(i.getUTCDate()+n):"month"===o?i.setUTCMonth(i.getUTCMonth()+n):"year"===o&&i.setUTCFullYear(i.getUTCFullYear()+n),{year:i.getUTCFullYear(),month:i.getUTCMonth()+1,day:i.getUTCDate(),hour:i.getUTCHours(),minute:i.getUTCMinutes(),second:i.getUTCSeconds(),millisecond:i.getUTCMilliseconds()}}});function re(e,t){(null==t||t>e.length)&&(t=e.length);for(var r=0,n=new Array(t);r<t;r++)n[r]=e[r];return n}var ne,oe=U,ae=new K({template:"^(".concat(oe,"{4}|").concat(oe,"{2})\\s*\u5e74\\s*(").concat(oe,"{1,2})\\s*\u6708\\s*(").concat(oe,"{1,2})\\s*\u65e5$"),handler:function(e){var t=function(e,t){return function(e){if(Array.isArray(e))return e}(e)||function(e,t){var r=null==e?null:"undefined"!=typeof Symbol&&e[Symbol.iterator]||e["@@iterator"];if(null!=r){var n,o,a=[],i=!0,u=!1;try{for(r=r.call(e);!(i=(n=r.next()).done)&&(a.push(n.value),!t||a.length!==t);i=!0);}catch(l){u=!0,o=l}finally{try{i||null==r.return||r.return()}finally{if(u)throw o}}return a}}(e,t)||function(e,t){if(e){if("string"==typeof e)return re(e,t);var r=Object.prototype.toString.call(e).slice(8,-1);return"Object"===r&&e.constructor&&(r=e.constructor.name),"Map"===r||"Set"===r?Array.from(e):"Arguments"===r||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(r)?re(e,t):void 0}}(e,t)||function(){throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}()}(e,4),r=t[1],n=t[2],o=t[3];return ne||((ne=new G("zh")).numberingSystem="hanidec",ne.buildNumbers()),ne.castObject({year:r,month:n,day:o})}}),ie=new K({template:"^(_DAY_)[\\/. ](_MONTH_)$",units:["day","month"]}),ue=new K({template:"^(_DAY_)(?:_ORDINAL_)?[ -](_MONTHNAME_)$",units:["day","month"]}),le=new K({template:"^(?:(?:_DAYNAME_),? )?(_DAY_)(?:_ORDINAL_)?([ -])(_MONTHNAME_)\\2(_YEAR_)$",units:["day",null,"month","year"]}),se=new K({template:"^(_DAY_)([\\/. -])(_MONTH_)\\2(_YEAR_)$",units:["day",null,"month","year"]}),ce=new K({template:"^(_MONTH_)(?:[\\/-])(_DAY_)$",units:["month","day"]}),fe=new K({template:"^(_MONTH_)([\\/-])(_DAY_)\\2(_YEAR_)$",units:["month",null,"day","year"],locales:["ee-TG","en-AS","en-CA","en-FM","en-GH","en-GU","en-KE","en-KY","en-MH","en-MP","en-US","en-VI","en-WS","sm-AS","sm-SM"]}),ye=new K({template:"^(?:(?:_DAYNAME_),? )?(_MONTHNAME_)? (_DAY_)(?:_ORDINAL_)?$",units:["month","day"]}),me=new K({template:"^(?:(?:_DAYNAME_),? )?(_MONTHNAME_)? (_DAY_)(?:_ORDINAL_)?,? (_YEAR_)$",units:["month","day","year"]});function Te(e,t){(null==t||t>e.length)&&(t=e.length);for(var r=0,n=new Array(t);r<t;r++)n[r]=e[r];return n}var he=new K({template:"^(.*?)_SPACE_*(?:at|on|T|)_SPACE_*(_H12_|_H24_)(?:\\:(_MIN_)(?:\\:(_SEC_))?)?_SPACE_*(_MERIDIEM_)$",handler:function(e,t){var r=function(e,t){return function(e){if(Array.isArray(e))return e}(e)||function(e,t){var r=null==e?null:"undefined"!=typeof Symbol&&e[Symbol.iterator]||e["@@iterator"];if(null!=r){var n,o,a=[],i=!0,u=!1;try{for(r=r.call(e);!(i=(n=r.next()).done)&&(a.push(n.value),!t||a.length!==t);i=!0);}catch(l){u=!0,o=l}finally{try{i||null==r.return||r.return()}finally{if(u)throw o}}return a}}(e,t)||function(e,t){if(e){if("string"==typeof e)return Te(e,t);var r=Object.prototype.toString.call(e).slice(8,-1);return"Object"===r&&e.constructor&&(r=e.constructor.name),"Map"===r||"Set"===r?Array.from(e):"Arguments"===r||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(r)?Te(e,t):void 0}}(e,t)||function(){throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}()}(e,6),n=r[1],o=r[2],a=r[3],i=r[4],u=r[5],l={};if(n&&(l=this.parser.attempt(n,t)).invalid)return null;var s=G.factory(t);if(u){var c=s.lookups.meridiem[u.toLowerCase()]||0;12===(o=parseFloat(o))?o=c:o+=o>12&&12===c?0:c}return l.hour=o,a&&(l.minute=a),i&&(l.second=i),l}});function de(e,t){(null==t||t>e.length)&&(t=e.length);for(var r=0,n=new Array(t);r<t;r++)n[r]=e[r];return n}var pe=new K({template:"^(.*?)_SPACE_*(?:at|on|T|)_SPACE_*(_H24_)\\:(_MIN_)(?:\\:(_SEC_)(?:[\\.,](_MS_))?)?_SPACE_*(?:GMT)?_SPACE_*(_OFFSET_)?_SPACE_*(_ZONE_)?$",handler:function(e,t){var r=function(e,t){return function(e){if(Array.isArray(e))return e}(e)||function(e,t){var r=null==e?null:"undefined"!=typeof Symbol&&e[Symbol.iterator]||e["@@iterator"];if(null!=r){var n,o,a=[],i=!0,u=!1;try{for(r=r.call(e);!(i=(n=r.next()).done)&&(a.push(n.value),!t||a.length!==t);i=!0);}catch(l){u=!0,o=l}finally{try{i||null==r.return||r.return()}finally{if(u)throw o}}return a}}(e,t)||function(e,t){if(e){if("string"==typeof e)return de(e,t);var r=Object.prototype.toString.call(e).slice(8,-1);return"Object"===r&&e.constructor&&(r=e.constructor.name),"Map"===r||"Set"===r?Array.from(e):"Arguments"===r||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(r)?de(e,t):void 0}}(e,t)||function(){throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}()}(e,8),n=r[1],o=r[2],a=r[3],i=r[4],u=r[5],l=r[6],s=r[7],c={};if(n&&(c=this.parser.attempt(n,t)).invalid)return c;if(c.hour=o,c.minute=a,i&&(c.second=i),u&&u.length>3?c.millisecond=u.slice(0,3):u&&(c.millisecond=u),s&&!l&&s in v)c.offset=v[s];else if(l){var f=G.factory(t);c.offset=f.offsetToMinutes(l)}return c}}),ve=new K({matcher:/^(now|today|tomorrow|yesterday)/i,handler:function(e){var t=this.now(),r=e[1].toLowerCase();switch(r){case"tomorrow":t.setUTCDate(t.getUTCDate()+1);break;case"yesterday":t.setUTCDate(t.getUTCDate()-1)}var n={year:t.getUTCFullYear(),month:t.getUTCMonth()+1,day:t.getUTCDate()};return"now"===r&&(n.hour=t.getUTCHours(),n.minute=t.getUTCMinutes(),n.second=t.getUTCSeconds(),n.millisecond=t.getUTCMilliseconds()),n}}),ge=new K({template:"^(?:_DAYNAME_) (_MONTHNAME_) (_DAY_) (_H24_):(_MIN_):(_SEC_) (_OFFSET_) (_YEAR_)$",units:["month","day","hour","minute","second","offset","year"]}),be=new K({template:"^(_YEAR_)(-?)(_MONTH_)\\2(_DAY_)$",units:["year",null,"month","day"]}),Ae=new J;Ae.addFormats([pe,he,be,le,me,fe,se,ae,ge,ve,te,ye,ue,ce,ie,X,q]),Ae.Parser=J,Ae.Format=K,Ae.LocaleHelper=G,Ae.defaultLocale=h,Ae.fromString=Date.fromString=Ae.exportAsFunction(),Ae.fromAny=Date.fromAny=Ae.exportAsFunctionAny(),"undefined"!=typeof window&&(window.anyDateParser=Ae)}();
})();
(function () {
'use strict';


var app = angular.module('hyresgastApp', [
  'ngResource',
  'ngRoute',
  'ngAnimate',
  'ngSanitize',
  'ngTouch',
  'ngAria',
  'ngCookies',
  'ngLocale',
  'angular-storage',
  'chart.js',
  'uiAccordion',
  'uiCollapse',
  'uiDatepickerPopup',
  'uiDropdown',
  'uiProgressbar',
  'uiTabindex',
  'uiTimepicker',
  'uiTooltip',
  'uiTypeahead',
  'uiModal',
  'dateLocal',
  'httpAuth',
  'hyUtils',
  'domainModals',
  'fileUpload',
  'fileUpload2',
  'flash',
  'selectAllCheckbox',
  'checkbox',
  'stringUtils',
  'modalWizard',
  'wizard',
  'timeline',
  'sorter',
  'breadcrumb',
  'uiSwitch',
  'reportMultiSelect',
  'reportParameters',
  'reportCellValue',
  'rangeInput',
  'treeView',
  'treeNode',
  'hyParticipants',
  'filePlate',
  'PortalSettings',
  'fpConsts',
  'exportUtils'
]);

const parties = {
  HGF: 20,
  DELEGATE: 10,
  EXTERNAL: 0,
};

app.value('LocalApiPort', 3200); // localhost port to API during development

app.value('ObjectTypes', {
  PROPERTY:     { code: 0, name: 'property' },
  APARTMENT:    { code: 1, name: 'apartment' },
  CALL:         { code: 5, name: 'call' },
  NEGOTIATION:  { code: 6, name: 'negotiation' },
  AGREEMENT:    { code: 9, name: 'agreement' },
  PROPERTY_AVTAL: { code: 12, name: 'free' },
});

app.value('Limits', {
  InMemoryMiddleLimit: 4000,
  InMemoryExcessiveLimit: 35000,
});

app.constant('FeatureConfiguration', {
  ENABLE_MEETINGS: false,
});

app.constant('CommonMessages', {
  FILE_DELETE_CONFIRMATION: `Vill du verkligen radera dokumentet?
    Radera inte andras uppladdade dokument utan att informera berörd person.
    Väljer du att radera dokumentet tas den bort från databasen och kan inte återställas. 
    Radera aldrig dokument som är handlingar som styrker framställans giltighet eller viktiga förhandlingsunderlag.`,
  SIGN_WITHOUT_SCRIVE_CONFIRMATION: `Du har nu valt att avsluta denna förhandling utan e-signering, då du signerat din överenskommelse på annat vis. 
    Glöm inte bilägga den signerade överenskommelsen.
    Om ovan är korrekt kan du godkänna, annars välj avbryt och välj den andra knappen för “Börja signera”.`
});

// if feature is enabled and desiredUrl is not specified (undefined) then redirect wont take place.
function maybeBlock (featureIsEnabled, desiredUrl) {
  var path404 = '/404';
  if (featureIsEnabled) {
    return desiredUrl;
  }else{
    return path404;
  }
}

/* jshint -W101 */
app.config(["$routeProvider", 'FeatureConfiguration', function($routeProvider, FeatureConfiguration) {
  $routeProvider
    .when('/resetpassword', {
      templateUrl: 'resources/user/resetPassword/setPasswordWithToken.html',
      controller: 'SetPasswordWithTokenCtrl',
      controllerAs: 'vm'
    })
    .when('/emailreset', {
      templateUrl: 'resources/user/resetPassword/resetPassword.html',
      controller: 'SetPasswordWithTokenCtrl',
      controllerAs: 'vm'
    })
    .when('/verify-email', {
      templateUrl: 'resources/user/verify-email/verify-email.html',
      controller: 'VerifyEmailCtrl',
      controllerAs: 'vm'
    })
    .when('/negotiations', {
      title: 'Alla Förhandlingar',
      templateUrl: 'resources/negotiation/list/list.html',
      controller: 'ListNegotiationCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false
    })
    .when('/negotiations/:id', {
      title: 'Förhandling',
      templateUrl: 'resources/negotiation/show/show.html',
      controller: 'ShowNegotiationCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false
    })
    .when('/negotiations/:negotiationId/agreements', {
      title: 'Slutfas',
      templateUrl: 'resources/negotiation/show/agreements/show.html',
      controller: 'ShowNegotiationAgreementCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      // redirectTo is used because we want breadcrumb to redirect
      // to a specific subpage (tab) ?view=agreements,
      // whose route '/negotiations/:negotiationId/agreements' doesn't exists in client
      redirectTo: function(routeParams) {
        return '/negotiations/' + routeParams.negotiationId + '?view=agreements';
      }
    })
    .when('/negotiations/:negotiationId/agreements/:id', {
      title: 'Överenskommelse',
      templateUrl: 'resources/agreement/show/show.html',
      controller: 'ShowAgreementCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false
    })
    .when('/agreements', {
      title: 'Alla avtal',
      templateUrl: 'resources/agreement/free/list.html',
      controller: 'FreeAgreementsListCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/agreements/new', {
      title: 'Skapa avtal',
      templateUrl: 'resources/agreement/free/new/new.html',
      controller: 'NewFreeAgreementCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF]);
      }]
    })
    .when('/agreements/:id', {
      title: 'Avtal',
      templateUrl: 'resources/agreement/show/show.html',
      controller: 'ShowAgreementCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false
    })
    .when('/agreements/:id/real-estate', {
      title: 'Fastighetsbestånd',
      templateUrl: 'resources/agreement/free/properties/show.html',
      controller: 'ShowFreeAgreementPropertiesCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiations/:negotiationId/agreements/:id/real-estate', {
      title: 'Fastighetsbestånd',
      templateUrl: 'resources/agreement/show/properties/show.html',
      controller: 'ShowAgreementCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiations/:negotiationId/agreements/:id/rentlist/new', {
      title: 'Ladda upp hyreslista',
      templateUrl: 'resources/agreement/rentlist/new/new.html',
      controller: 'ShowAgreementCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiations/:negotiationId/agreements/:id/rentlist/edit', {
      title: 'Ändra hyra',
      templateUrl: 'resources/agreement/rentlist/edit/edit.html',
      controller: 'ShowAgreementCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiations/:negotiationId/agreements/:id/rentlist/view', {
      title: 'Visa',
      templateUrl: 'resources/agreement/rentlist/show.html',
      controller: 'ShowAgreementCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiations/:negotiationId/blockings', {
      title: 'Slutfas',
      templateUrl: 'resources/negotiation/show/agreements/show.html',
      controller: 'ShowNegotiationAgreementCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      // redirectTo is used because we want breadcrumb to redirect
      // to a specific subpage (tab) ?view=agreements,
      // whose route '/negotiations/:negotiationId/blockings' doesn't exists in client
      redirectTo: function(routeParams) {
        return '/negotiations/' + routeParams.negotiationId + '?view=agreements';
      }
    })
    .when('/negotiations/:id/blockings/new', {
      title: 'Skapa strandning',
      templateUrl: 'resources/blocking/new/new.html',
      controller: 'NewBlockingCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiations/:id/blockings/select-property', {
      title: 'Skapa strandning',
      templateUrl: 'resources/blocking/new/properties/select.html',
      controller: 'SelectBlockingPropertiesCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiations/:negotiationId/blockings/:id', {
      title: 'Strandning',
      templateUrl: 'resources/blocking/show/show.html',
      controller: 'ShowBlockingCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiations/:negotiationId/meetings', {
      title: 'Möten',
      templateUrl: 'resources/negotiation/show/meetings/show.html',
      controller: 'ShowNegotiationMeetingsCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      // redirectTo is used because we want breadcrumb to redirect
      // to a specific subpage (tab) ?view=meetings,
      // whose route '/negotiations/:negotiationId/meetings' doesn't exists in client
      redirectTo: routeParams => maybeBlock(FeatureConfiguration.ENABLE_MEETINGS, '/negotiations/' + routeParams.negotiationId + '?view=meetings')
    })
    .when('/negotiations/:negotiationId/meetings/:id', {
      title: 'Möte',
      templateUrl: 'resources/meeting/show/show.html',
      controller: 'ShowMeetingCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      // Redirect to the 404 error page if meetings are disabled. No redirection otherwise
      redirectTo: maybeBlock(FeatureConfiguration.ENABLE_MEETINGS)
    })
    .when('/negotiations/:negotiationId/properties/:id', {
      title: 'Fastighet',
      templateUrl: 'resources/negotiation/show/property/show.html',
      controller: 'ShowNegotiationPropertyCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
    })
    .when('/negotiation-calls', {
      title: 'Alla Framställningar',
      templateUrl: 'resources/negotiation-call/list/list.html',
      controller: 'ListNegotiationCallCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiation-calls/new', {
      title: 'Skapa framställan',
      templateUrl: 'resources/negotiation-call/new/new.html',
      controller: 'NewNegotiationCallCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiation/new-direct', {
      title: 'Skapa hyresförhandling',
      templateUrl: 'resources/negotiation/new-direct/new.html',
      controller: 'NewDirectNegotiationCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF]);
      }]
    })
    .when('/negotiation-calls/:id', {
      title: 'Framställan',
      templateUrl: 'resources/negotiation-call/show/show.html',
      controller: 'ShowNegotiationCallCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/negotiation-calls/:id/edit', {
      title: 'Påkalla hyresförhandling',
      templateUrl: 'resources/negotiation-call/new/new.html',
      controller: 'NewNegotiationCallCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.EXTERNAL]);
      }]
    })
    .when('/negotiation-calls/:id/negotiations/newly-constructed-property', {
      title: 'Skapa förhandling',
      templateUrl: 'resources/negotiation/new/new-newly-constructed-property.html',
      controller: 'NewNegotiationCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF]);
      }]
    })
    .when('/negotiation-calls/:id/negotiations/new', {
      title: 'Skapa förhandling',
      templateUrl: 'resources/negotiation/new/new.html',
      controller: 'NewNegotiationCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF]);
      }]
    })
    .when('/organizations', {
      title: 'Alla fastighetsägare',
      templateUrl: 'resources/organization/list/list.html',
      controller: 'ListOrganizationCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/organizations/new', {
      title: 'Lägg till ny fastighetsägare',
      templateUrl: 'resources/organization/newedit/edit.html',
      controller: 'EditOrganizationCtrl',
      controllerAs: 'vm',
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/organizations/:id', {
      title: 'Fastighetsägare',
      templateUrl: 'resources/organization/show/show.html',
      controller: 'ShowOrganizationCtrl',
      controllerAs: 'vm',
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/organizations/:id/edit', {
      title: 'Redigera',
      templateUrl: 'resources/organization/newedit/edit.html',
      controller: 'EditOrganizationCtrl',
      controllerAs: 'vm',
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/users', {
      title: 'Alla användare',
      templateUrl: 'resources/user/list/list.html',
      controller: 'ListUserCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/users/new', {
      title: 'Lägg till ny användare',
      templateUrl: 'resources/user/new/new.html',
      controller: 'NewUserCtrl',
      controllerAs: 'vm',
    })
    .when('/users/new/thank-you', {
      title: 'Bekräfta e-post',
      templateUrl: 'resources/user/new/thank-you.html',
      controller: 'NewUserCtrl',
      controllerAs: 'vm'
    })
    .when('/users/new/thank-you-invite', {
      title: 'Bekräfta e-post',
      templateUrl: 'resources/user/new/thank-you-invite.html',
      controller: 'NewUserCtrl',
      controllerAs: 'vm'
    })
    .when('/agreement/free/new/thank-you-agreement', {
      title: 'Överenskommelsen skappad',
      templateUrl: 'resources/agreement/free/thank-you.html',
    })
    .when('/users/new/error', {
      title: 'Bekräfta e-post',
      templateUrl: 'resources/user/new/error.html',
      controller: 'NewUserCtrl',
      controllerAs: 'vm'
    })
    .when('/users/:id', {
      title: 'Visa användare',
      templateUrl: 'resources/user/show/show.html',
      controller: 'ShowUserCtrl',
      controllerAs: 'vm'
    })
    .when('/users/:id/edit', {
      title: 'Redigera',
      templateUrl: 'resources/user/edit/edit.html',
      controller: 'EditUserCtrl',
      controllerAs: 'vm',
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.DELEGATE, parties.EXTERNAL]);
      }]
    })
    .when('/users/:id/welcome', {
      title: 'Välkommen',
      templateUrl: 'resources/user/welcome/show.html',
      controller: 'ShowUserWelcomeCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/requested-accounts', {
      title: 'Begärda konton',
      templateUrl: 'resources/requested-accounts/list.html',
      controller: 'RequestedAccountsCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/requested-accounts/organization/:id/approve', {
      title: 'Organisation',
      templateUrl: 'resources/requested-accounts/organizations/approve/approve.html',
      controller: 'ApproveOrganizationCtrl',
      controllerAs: 'vm',
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF]);
      }]
    })
    .when('/requested-accounts/user/:id/approve', {
      title: 'Användare',
      templateUrl: 'resources/requested-accounts/users/approve/approve.html',
      controller: 'ApproveUserCtrl',
      controllerAs: 'vm',
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.EXTERNAL]);
      }]
    })
    .when('/help/faq', {
      title: 'FAQ',
      templateUrl: 'resources/help/show/faq/show.html',
      controller: 'ShowHelpFaqCtrl',
      controllerAs: 'vm'
    })
    .when('/help/process', {
      title: 'Vår gemensamma process',
      templateUrl: 'resources/help/show/process/show.html',
      controller: 'ShowHelpProcessCtrl',
      controllerAs: 'vm'
    })
    .when('/help/permissions', {
      title: 'Inloggningsfel',
      templateUrl: 'resources/help/show/hgfLoginHelp.html'
    })
    .when('/reports', {
      title: 'Statistik',
      templateUrl: 'resources/reports/index.html',
      controller: 'ReportsCtrl',
      controllerAs: 'vm',
      resolve: {
        client: () => 'fp',
      }
    })
    .when('/users/:id/negotiator/:partnerId/organization/:organizationId/welcome/negotiation-table/negotiator', {
      title: 'Förhandlingsbord',
      templateUrl: 'resources/user/welcome/negotiation-table/negotiator/list.html',
      controller: 'NegotiationTableNegotiatorListCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/search/properties', {
      title: 'Fastighetslistan',
      templateUrl: 'resources/real-estate/list/propertyList.html',
      controller: 'PropertyListCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF, parties.EXTERNAL]);
      }]
    })
    .when('/search/owners', {
      title: 'Ägare',
      templateUrl: 'resources/real-estate/list/ownersList.html',
      controller: 'OwnersListCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF]);
      }]
    })
    .when('/search/agreements', {
      title: 'Överenskommelser/avtal',
      templateUrl: 'resources/search/agreements/search.html',
      controller: 'SearchAgreementsCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['UserPermissions', function(UserPermissions) {
        return UserPermissions([parties.HGF]);
      }]
    })
    .when('/properties/:id', {
      title: 'Fastighet',
      templateUrl: 'resources/real-estate/show/show.html',
      controller: 'PropertyShowCtrl',
      controllerAs: 'vm',
      reloadOnSearch: false,
      resolveRedirectTo: ['$q', '$route', 'Auth', 'RealEstate', function ($q, $route, Auth, RealEstate) {
        var deferred = $q.defer();

        Auth.heartbeat(true).then(function() {
          if (!Auth.userHasPermissionForView([parties.HGF, parties.EXTERNAL])) {
            deferred.resolve('/403');
          }

          // just simple check, that id looks like GUID
          if ($route.current.params && $route.current.params.id && $route.current.params.id.length === 36) {

            RealEstate.findByExternalId({ id: $route.current.params.id })
              .$promise.then(
                function (response) {
                  if ($route.current.params.view) {
                    deferred.resolve('/properties/' + response.id + '?view=' + $route.current.params.view);
                  } else {
                    deferred.resolve('/properties/' + response.id);
                  }
                },
                // eslint-disable-next-line no-unused-vars
                function (error) {
                  deferred.reject();
                });
          } else {
            deferred.resolve();
          }
        });

        return deferred.promise;
      }]
    })

    .when('/403', {
      templateUrl: 'components/error/403.html'
    })
    .when('/404', {
      templateUrl: 'components/error/404.html'
    })

    // This one is a bit special. Redirect user on login depending on role
    .when('/', {
      resolve: {
        redirect: ["UserRedirect", function(UserRedirect) {
          // jshint -W064
          return UserRedirect();
          // jshint +W064
        }]
      }
    })
    /*     .otherwise('/negotiations', {
      templateUrl: 'resources/negotiation/list/list.html',
      controller: 'ListNegotiationCtrl',
      controller As: 'vm'
    });*/
    .otherwise('/404', {
      templateUrl: 'components/error/404.html'
    });
}]);
/* jshint +W101 */

app.config(["$locationProvider", function($locationProvider) {
  $locationProvider.html5Mode(true);
}]);

app.config(["$httpProvider", function($httpProvider) {
  //Changes to avoid IE caching ajax requests.
  if (!$httpProvider.defaults.headers.get) {
    $httpProvider.defaults.headers.get = {};
  }

  $httpProvider.defaults.headers.get['If-Modified-Since'] = 'Mon, 26 Jul 1997 05:00:00 GMT';
  $httpProvider.defaults.headers.get['Cache-Control'] = 'no-cache';
  $httpProvider.defaults.headers.get['Pragma'] = 'no-cache';

  $httpProvider.interceptors.push(function() {
    return {
      'request': function(config) {
        if (config.data && config.data.pako) {
          //Here we should set Content-Encoding to gzip
          //But we better handle zipped body explicitly
          config.headers['PAKO!'] = 'PAKO!';
          // eslint-disable-next-line no-undef
          config.data = pako.gzip(JSON.stringify(config.data));
        }
        return config;
      }
    };
  });
  $httpProvider.interceptors.push('AuthInterceptor', 'BaseInterceptor', 'LocalhostInterceptor');
}]);

app.config(["$qProvider", function($qProvider) {
  $qProvider.errorOnUnhandledRejections(false);
}]);

app.config(["storeProvider", function(storeProvider) {
  storeProvider.setStore('sessionStorage');
}]);


})();
(function () {
'use strict';

var app = angular.module('hyresgastApp');


const partiesHGF = 20;
const partiesDELEGATE = 10;
const partiesEXTERNAL = 0;

app.value('RoleLookup', [
  {
    key: 'SPECTATOR',
    value: 50,
  },
  {
    key: 'USER',
    value: 100
  },
  {
    key: 'ADMIN',
    value: 200
  }
]);

app.value('PartyLookup', [
  {
    key: 'EXTERNAL',
    value: 0
  },
  {
    key: 'DELEGATE',
    value: 10
  },
  {
    key: 'HGF',
    value: 20
  }
]);

app.value('Roles', {
  NONE: 0,
  SPECTATOR: 50,
  USER: 100,
  ADMIN: 200
});

app.value('Parties', {
  EXTERNAL: 0,
  DELEGATE: 10,
  HGF: 20
});

app.value('NegotiationCallStates', {
  WAITING_HGF: 51, // created and sent, waiting for Hgf action
  WAITING_EXT: 101, // created and sent, inspected by Hgf and found lacking, waiting for external action
  PARTIALLY_ASSIGNED: 201, // some real estate covered has been assigned to a negotiation, but not all
  ASSIGNED: 301 // all real estate has been assigned to a negotiation, no more action needed
});

app.value('NegotiationStates', {
  NEW: 0, //Ny
  IN_PREPARATION: 22, // 'bereds'
  SETTLEMENT: 44, // uppgörelse
  IN_PROGRESS: 72, // pågår
  FINISHED_OUTSIDE_PORTAL: 592, // Felaktigt avslutad förhandling
  BLOCKED: 922, // strandad
  PARTIALLY_BLOCKED: 972, // delvis strandad
  FINISHED: 5252 // avslutad
});

app.value('CombinedNegotiationStates', {
  NEW: '0|0', //Ny
  IN_PREPARATION: '22|0', // 'bereds'
  SETTLEMENT: '44|0', // uppgörelse
  IN_PROGRESS: '72|0', // pågår
  FINISHED_OUTSIDE_PORTAL: '592|0', // Felaktigt avslutad förhandling
  BLOCKED_PRIVATE: '922|0', // strandad HN
  BLOCKED_PUBLIC: '922|1', // strandad HMK
  BLOCKED_NONE: '922|2', // strandad egensatt
  BLOCKED_MEDIATOR: '922|3', // strandad skiljeman HN
  PARTIALLY_BLOCKED_PRIVATE: '972|0', // delvis strandad HN
  PARTIALLY_BLOCKED_PUBLIC: '972|1', // delvis strandad HMK
  PARTIALLY_BLOCKED_NONE: '972|2', // delvis strandad egensatt
  PARTIALLY_BLOCKED_MEDIATOR: '972|3', // delvis strandad skiljeman HN
  FINISHED: '5252|0' // avslutad
});

app.value('AgreementTypes', {
  AGREEMENT: 0,
  BLOCKING: 1,
  AGREEMENT_WITHOUT_RENTLIST: 2,
});

app.constant('FreeAgreementTypes', {
  FÖRHANDLINGSORDNING:    { code: 9, name: 'Förhandlingsordning' },
  TILLVALSAVTAL:          { code: 10, name: 'Tillvalsavtal' },
  RAMAVTAL:               { code: 11, name: 'Ramavtal' },
  HYRESSÄTTNINGSAVGIFT:   { code: 12, name: 'Hyressättningsavgift' },
  UNTERHÅLLSAVTAL:        { code: 13, name: 'Underhållsavtal' },
  IMD:                    { code: 14, name: 'IMD individuell mätning och debitering' },
  FÖRHANDLINGSPROTOKOLL:  { code: 15, name: 'Förhandlingsprotokoll' },
  MÖTESPROTOKOLL:         { code: 16, name: 'Mötesprotokoll' },
});

app.value('NegotiationProtocolStates', {
  DRAFT: 0,
  SAVED: 14,
  CONFIRMED: 34,
  SIGNED: 64 // signerad
});

app.value('NegotiationCallSuspectedErrors', {
  MISSING: 'missingMedWindProperty',
  NO_OWNER: 'noMedWindOwner',
  NO_NEGOTIATION: 'noMedWindNegotiationAgreement',
  ALREADY_CALLED: 'alreadyCalled',
  IN_NEGOTIATION: 'alreadyInNegotiation'
});

app.constant('NegotiationLandlordSides', {
  LANDLORD: 0,
  LANDLORD_AND_ORGANIZATION: 1
});

app.value('AgreementBlockedByType', {
  PRIVATE: 0, // Hyresnämnden
  PUBLIC: 1, // HMK
  NONE: 2, // Egensatt
  MEDIATOR: 3 // Skiljeman Hyresnämnd
});

app.value('Regions', {
  NORRLAND: 1, // Norrland
  AROS_GEVLE: 2, // Aros - Gävle
  STOCKHOLM: 3, // Stockholm
  MIDDLE: 4, // Mitt
  WEST_SWEDEN: 6, // Västra Sverige
  BESK: 7, // Bäsk
  SOUTH_EAST: 8, // Sydost
  NORTH_SKONE: 9, // Norra Skåne
  SOUTH_SKONE: 10 // Södra Skåne
});

app.value('NegotiationTypes', {
  ANNUAL_PROPERTY: 0, // Årlig hyresförhandling av fastigheter
  STANDARD_PROPERTY: 2, // Standardförändring av fastigheter
  STANDARD_APARTMENT: 3, // Standardförändring av lägenheter
  REBUILD_PROPERTY: 4, // Hela fastigheten ombyggnation/renovering
  REBUILD_APARTMENT: 5, // Ombyggnation/renovering av lägenheter
  NEWLY_CONSTRUCTED_PROPERTY: 6, // Nyproduktion av fastigheter
  NEWLY_CONSTRUCTED_APARTMENT: 7, // Nyproduktion av lägenheter i befintlig fastighet
  UTILITY_VALUE_PROPERTY: 8, // Bruksvärdesförhandling av fastigheter
  UTILITY_VALUE_APARTMENT: 9, // Bruksvärdesförhandling av lägenheter
  
  REBUILD_PROPERTY_2: 1001,   //Enskild lägenhet ombyggnation/renovering
});

app.value('OrderedNegotiationTypes', {
  ANNUAL_PROPERTY: 0, // Årlig hyresförhandling av fastigheter
  STANDARD_PROPERTY: 2, // Standardförändring av fastigheter
  REBUILD_PROPERTY: 4, // Hela fastigheten ombyggnation/renovering
  REBUILD_PROPERTY_2: 1001,   //Enskild lägenhet ombyggnation/renovering
  NEWLY_CONSTRUCTED_APARTMENT: 7, // Nyproduktion av lägenheter i befintlig fastighet
  NEWLY_CONSTRUCTED_PROPERTY: 6, // Nyproduktion av fastigheter
  UTILITY_VALUE_PROPERTY: 8, // Bruksvärdesförhandling av fastigheter
});

app.value('PropertyNegotiationTypes', {
  ANNUAL_PROPERTY: 0, // Årlig hyresförhandling av fastigheter
  STANDARD_PROPERTY: 2, // Standardförändring av fastigheter
  REBUILD_PROPERTY: 4, // Ombyggnation/renovering av fastigheter
  REBUILD_PROPERTY_2: 1001, // Enskild lägenhet obyggnation/renovering
  NEWLY_CONSTRUCTED_PROPERTY: 6, // Nyproduktion av fastigheter
  UTILITY_VALUE_PROPERTY: 8, // Bruksvärdesförhandling av fastigheter
});

app.value('ApartmentNegotiationTypes', {
  STANDARD_APARTMENT: 3, // Standardförändring av lägenheter
  REBUILD_APARTMENT: 5, // Ombyggnation/renovering av lägenheter
  NEWLY_CONSTRUCTED_APARTMENT: 7, // Nyproduktion av lägenheter i befintlig fastighet
  UTILITY_VALUE_APARTMENT: 9 // Bruksvärdesförhandling av lägenheter
});

app.value('ValidFileTypes', 'eml msg odt txt pdf ppt pptx doc docx xml csv jpg gif png tif tiff xls xlsx');

// 'Parties' has been explicitely defined as a constant because
// app.value('Parties') cannot be injected in $routeProvider
app.constant('Parties', {
  EXTERNAL: 0,
  DELEGATE: 10,
  HGF: 20
});

app.constant('KitchenTypes', ['ingen', 'kök', 'kokskåp', 'kokvrå', 'gemensamt', 'mkök']);

app.constant('BuildingTypes', ['övrig', 'radhus', 'villa']);

app.constant('NegotiationRoles', {
  HGF_ASSOC:                { code: 0, party: partiesHGF, isChief: false, name: 'Hyresgästföreningen' },
  HGF_ASSOC_CHIEF:          { code: 92, party: partiesHGF, isChief: true, name: 'Hyresgästföreningen, Ansvarig förhandlare' },
  HGF_ELECTED:              { code: 30, party: partiesHGF, isChief: false, name: 'Förtroendevald HGF' },
  HGF_ELECTED_CHIEF:        { code: 122, party: partiesHGF, isChief: true, name: 'Förtroendevald HGF, Ansvarig förhandlare' },

  FA_POWNER:                { code: 2, party: partiesEXTERNAL, isChief: false, name: 'Hyresvärd' },
  FA_POWNER_CHIEF:          { code: 172, party: partiesEXTERNAL, isChief: true, name: 'Hyresvärd, Ansvarig förhandlare' },

  OMBUD_FA_POWNER:          { code: 42, party: partiesEXTERNAL, isChief: false, name: 'Hyresvärd ombud' },
  OMBUD_FA_POWNER_CHIEF:    { code: 212, party: partiesEXTERNAL, isChief: true, name: 'Hyresvärd ombud, Ansvarig förhandlare' },

  PARTICIPANT_DELEGATION:   { code: 3, party: partiesDELEGATE, name: 'Delegat' },
  PARTICIPANT_SUPERDELEGAT: { code: 5, party: partiesDELEGATE, name: 'Super delegat' }
});

app.constant('ApartmentStatusTypes', {
  PRESUMTIVE_RENT:      { code: 1, name: 'P', longName: 'Presumtionshyra', colouredName: '<b>P</b>' },
  STUCK:                { code: 2, name: 'S', longName: 'Strandad', colouredName: '<b class="red-text">S</b>' },
  WITHOUT_NEGOTIATION:  { code: 4, name: 'UF', longName: 'Utan förhandlingsklausul' },
  TRIBE_DECISION:       { code: 8, name: 'HN', longName: 'Beslutade av hyresnämnd' },
  IN_NEGOTIATION:       { code: 16, name: 'F', longName: 'Förhandling pågår' },
  MANUALLY_CHANGED:     { code: 32, name: 'M', longName: 'Manuellt uppdaterad' },
  M10:                  { code: 64, name: '10m', longName: '10-månadershyra' },
  EG:                   { code: 128, name: 'E', longName: 'Egensatt hyra', colouredName: '<b class="red-text">E</b>' },
  B:                    { code: 256, name: 'B', longName: 'Blockuthyrning', }
});

app.constant('SettingTypes', {
  NUMBER: 0,
  STRING: 1,
  BOOL: 2,
  TEXT: 3,
  RANGE2NUMBER: 10,
  JSON: 11,
});

app.constant('ReportParameterTypes', {
  NUMBER: 1,
  STRING: 2,
  DATE:   3,
  RANGE:  5,
  COLLECTION: 6,
  YEAR: 10,
  NEGOTIATION_STATUS: 20,
  NEGOTIATION_STATUS_GROUP: 21,
  NEGOTIATION_TYPE: 25,
  HGF_OFFICE: 30,
  HGF_OFFICE_MULTIPLE: 33,
  REGION: 40,
  REGION_MUNICIPALITY: 45,
  MUNICIPALITY: 50,
  KITCHEN_TYPE: 60,
  APARTMENT_STATUS: 62,
});

app.constant('ReportChartTypes', {
  BAR: 1,
  LINE: 2,
});

app.constant('AttachmentTypes', {
  NEGOTIATION_ATTACHMENT: { id: 10, name: 'Bilaga' },
  DRAFT_AGREEMENT:        { id: 11, name: 'Överenskommelse (utkast)' },
  SIGNED_AGREEMENT:       { id: 12, name: 'Underskrivet avtal' },
  RECEIPT:                { id: 30, name: 'Förhandlingsframställan kvitto' },
  RENT_LIST:              { id: 35, name: 'Hyreslista (källfil)' },
  PROPERTY_LIST:          { id: 36, name: 'Fastighetslista (källfil)' },
  AGREEMENT_ATTACHMENT:   { id: 77, name: 'Bilaga överenskommelse' },
});

app.constant('CommonIds', {
  tableauAllId: -99999,
});
})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('agreementBlockedByType', ["AgreementBlockedByType", function(AgreementBlockedByType) {
  return function(value) {
    switch (value) {
      case AgreementBlockedByType.PRIVATE:
        return 'Hyresnämnden';
      case AgreementBlockedByType.PUBLIC:
        return 'HMK';
      case AgreementBlockedByType.NONE:
        return 'Egensatt';
      case AgreementBlockedByType.MEDIATOR:
        return 'Skiljeman Hyresnämnd';
      default:
        return 'Typ ej angivet';
    }
  };
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('agreementTypeToDescription', function() {
  return function(value) {
    switch (value) {
      case 0:
        return 'Överenskommelse';
      case 1:
        return 'Strandning';
      default:
        return 'Typ ej angivet';
    }
  };
});

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('daysLeft', ["NegotiationStates", function(NegotiationStates) {
  return function(days, state, daysLimit) {
    if (days === null || state === NegotiationStates.BLOCKED) {
      return '&ndash;';
    }

    if (days > daysLimit) {
      return daysLimit + '+';
    }

    return days;
  };
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('demandTypeToDescription', function() {
  return function(value) {
    switch (value) {
      case '0':
        return '%';
      case '1':
        return 'kr/kvm/år';
      case '2':
        return 'Fritext';
      default:
        return 'Typ ej angivet';
    }
  };
});

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('documentBelongsTo', function() {
  return function(value) {
    return documentBelongsTo(value);
  };
});

function documentBelongsTo(value) {
  switch (value) {
    case 1:
      return 'agreements';
    case 2:
      return 'meetings';
    default:
      return 'undefined';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('documentTypes', function() {
  return function(value) {
    return documentTypes(value);
  };
});

function documentTypes(value) {
  value = parseInt(value);
  switch (value) {
    case 1:
      return 'Allmänna ekonomiska läget';
    case 2:
      return 'Budget kommande år';
    case 3:
      return 'Budget/utfall föregående år';
    case 4:
      return 'Förvaltningskvalitet och service';
    case 5:
      return 'Läge och standard';
    case 6:
      return 'Medlems- och kundundersökning';
    case 7:
      return 'Prognos för innevarande år';
    case 8:
      return 'Underhållsplan';
    case 9:
      return 'Övrigt';
    case 10:
      return 'Bilaga';
    case 11:
      return 'Överenskommelse (utkast)';
    case 12:
      return 'Överenskommelse (signerad)';
    case 13:
      return 'Inlaga';
    case 14:
      return 'Dagordning';
    case 15:
      return 'Protokoll';
    case 30:
      return 'Förhandlingsframställan kvitto';
    case 35:
      return 'Hyreslista (källfil)';
    case 36:
      return 'Fastighetslista (källfil)';
    case 77:
      return 'Bilaga överenskommelse';
    default:
      return 'Underlag';
  }
}

})();
(function () {
/* eslint-disable no-undefined */
'use strict';

angular.module('hyresgastApp').filter('doneTasksCounter', ["AgreementTypes", "NegotiationTypes", function(AgreementTypes, NegotiationTypes) {

  return function(agreement, negotiationType, isFreeAgreement) {
    if (!agreement || (negotiationType === undefined && !isFreeAgreement)) {
      return;
    }

    let agreedByBothPart = agreement.agreedByAssociation && agreement.agreedByOwner;
    let agreementSteps = [];
    
    if (agreement.type === AgreementTypes.AGREEMENT) {
      agreementSteps = [
        agreement.rentEffectiveFrom,
        agreement.releaseDate,
        agreement.name,
        agreement.numApartments,
        agreement.rentFileId || agreement.numApartments && agreement.numApartments === agreement.numAdjustedApartments,
        agreedByBothPart
      ];
    }

    if (agreement.type === AgreementTypes.AGREEMENT_WITHOUT_RENTLIST) {
      agreementSteps = [
        agreement.rentEffectiveFrom,
        agreement.releaseDate,
        agreement.name,
        agreement.numProperties,
        agreement.numDocuments,
        agreedByBothPart
      ];
    }

    if (negotiationType === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY) {
      agreementSteps = [
        agreement.rentEffectiveFrom,
        agreement.releaseDate,
        agreement.name,
        agreement.rentFileId || agreement.numApartments && agreement.numApartments === agreement.numAdjustedApartments,
        agreedByBothPart
      ];
    }

    if (isFreeAgreement) {
      agreementSteps = [
        agreement.rentEffectiveFrom,
        agreement.name,
        agreedByBothPart
      ];
    }
    
    let total = agreementSteps.length;
    let done = agreementSteps.filter(step => step).length;
    let lastStep = ((total - done) === 1);
    let ready = (done === total);

    return {
      total,
      done,
      ready,
      lastStep
    };
  };
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('enSwDictionary', function() {
  return function(value) {
    return enSwDictionary(value);
  };
});

function enSwDictionary(value) {
  switch (value) {
    case 'agreements':
      return 'Överenskommelse';
    case 'meetings':
      return 'Möte';
    default:
      return 'undefined';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hgfUserRoles', function() {
  return function(value) {
    switch (value) {
      case 'admin':
        return 'Handläggare';
      case 'user':
        return 'Användare';
      case 'spectator':
        return 'Läsanvändare';
      case 'none':
        return 'Ingen';
      default:
        return 'Roll ej angiven';
    }
  };
});

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyCombinedNegotiationStates', ["CombinedNegotiationStates", function(CombinedNegotiationStates) {
  return function(value) {
    return hyCombinedNegotiationStates(value, CombinedNegotiationStates);
  };
}]);

function hyCombinedNegotiationStates(value, CombinedNegotiationStates) {
  switch (value) {
    case CombinedNegotiationStates.NEW:
      return 'Ny';
    case CombinedNegotiationStates.IN_PREPARATION:
      return 'Bereds';
    case CombinedNegotiationStates.IN_PROGRESS:
      return 'Pågår';
    case CombinedNegotiationStates.SETTLEMENT:
      return 'Uppgörelse';
    case CombinedNegotiationStates.BLOCKED_PRIVATE:
      return 'Strandad HN';
    case CombinedNegotiationStates.BLOCKED_PUBLIC:
      return 'Strandad HMK';
    case CombinedNegotiationStates.BLOCKED_NONE:
      return 'Strandad Egensatt';
    case CombinedNegotiationStates.BLOCKED_MEDIATOR:
      return 'Strandad Skiljeman HN';
    case CombinedNegotiationStates.PARTIALLY_BLOCKED_PRIVATE:
      return 'Delvis strandad HN';
    case CombinedNegotiationStates.PARTIALLY_BLOCKED_PUBLIC:
      return 'Delvis strandad HMK';
    case CombinedNegotiationStates.PARTIALLY_BLOCKED_NONE:
      return 'Delvis strandad Egensatt';
    case CombinedNegotiationStates.PARTIALLY_BLOCKED_MEDIATOR:
      return 'Delvis strandad Skiljeman HN';
    case CombinedNegotiationStates.FINISHED_OUTSIDE_PORTAL:
      return 'Felaktigt avslutad förhandling - Vänligen återöppna';
    case CombinedNegotiationStates.FINISHED:
      return 'Avslutad';
    default:
      return 'Alla statusar';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyFilterObjectValuesByKeysEndWith', function() {
  return function(negotiationTypes, category) {
    var ret = [];
    for (var key in negotiationTypes) {
      if (key.match(new RegExp(category + '$'))) {
        ret.push(negotiationTypes[key]);
      }
    }
    return ret;
  };
});

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyJoinBy', function() {
  return function(input, delimiter) {
    return (input || []).join(delimiter || ', ');
  };
});

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyNegotiationCallStates', ["NegotiationCallStates", function(NegotiationCallStates) {
  return function(value, isHgfUser) {
    return hyNegotiationCallStates(value, isHgfUser, NegotiationCallStates);
  };
}]);

function hyNegotiationCallStates(value, isHgfUser, NegotiationCallStates) {
  switch (value) {
    case NegotiationCallStates.WAITING_HGF:
      return 'Väntande hos HGF';
    case NegotiationCallStates.WAITING_EXT:
      return 'Återskickad från HGF';
    case NegotiationCallStates.PARTIALLY_ASSIGNED:
      if (isHgfUser) {
        return 'Delvis tilldelad';
      } else {
        return 'Under arbete hos HGF';
      }
    case NegotiationCallStates.ASSIGNED:
      return 'Tilldelad';
    default:
      return 'Alla statusar';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyNegotiationCallSuspectedErrors', ["NegotiationCallSuspectedErrors", function(NegotiationCallSuspectedErrors) {
  return function(value) {
    return hyNegotiationCallSuspectedErrors(value, NegotiationCallSuspectedErrors);
  };
}]);

function hyNegotiationCallSuspectedErrors(value, NegotiationCallSuspectedErrors) {
  switch (value) {
    case NegotiationCallSuspectedErrors.MISSING:
      return 'Saknas i registret.';
    case NegotiationCallSuspectedErrors.NO_OWNER:
      return 'Fler förhandlingar på denna fastighet finns registrerade men med annat ombud/motpart.';
    case NegotiationCallSuspectedErrors.NO_NEGOTIATION:
      return 'Saknar förhandlingsordning.';
    case NegotiationCallSuspectedErrors.ALREADY_CALLED:
      return 'Finns redan på en förhandlingsframställan.';
    case NegotiationCallSuspectedErrors.IN_NEGOTIATION:
      return 'Finns redan på en årsförhandling.';
    default:
      return 'Okänt fel.';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyNegotiationProtocolStates', ["NegotiationProtocolStates", function(NegotiationProtocolStates) {
  return function(value) {
    return hyNegotiationProtocolStates(value, NegotiationProtocolStates);
  };
}]);

function hyNegotiationProtocolStates(value, NegotiationProtocolStates) {
  switch (value) {
    case NegotiationProtocolStates.DRAFT:
      return 'Utkast';
    case NegotiationProtocolStates.SAVED:
      return 'Sparad';
    case NegotiationProtocolStates.CONFIRMED:
      return 'Bekräftad';
    case NegotiationProtocolStates.SIGNED:
      return 'Signerad';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyNegotiationStates', ["NegotiationStates", function(NegotiationStates) {
  return function(value) {
    return hyNegotiationStates(value, NegotiationStates);
  };
}]);

function hyNegotiationStates(value, NegotiationStates) {
  switch (value) {
    case NegotiationStates.NEW:
      return 'Ny';
    case NegotiationStates.IN_PREPARATION:
      return 'Bereds';
    case NegotiationStates.IN_PROGRESS:
      return 'Pågår';
    case NegotiationStates.SETTLEMENT:
      return 'Uppgörelse';
    case NegotiationStates.BLOCKED:
      return 'Strandad';
    case NegotiationStates.PARTIALLY_BLOCKED:
      return 'Delvis strandad';
    case NegotiationStates.FINISHED_OUTSIDE_PORTAL:
      return 'Felaktigt avslutad förhandling - Vänligen återöppna';
    case NegotiationStates.FINISHED:
      return 'Avslutad';
    default:
      return 'Alla statusar';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyNegotiationTypes', ["NegotiationTypes", function(NegotiationTypes) {
  return function(value) {
    return hyNegotiationTypes(value, NegotiationTypes);
  };
}]);

function hyNegotiationTypes(value, NegotiationTypes) {
  switch (value) {
    case NegotiationTypes.ANNUAL_PROPERTY:
      return 'Årlig hyresförhandling';
    case NegotiationTypes.STANDARD_PROPERTY:
      return 'Standardförändring';
    case NegotiationTypes.STANDARD_APARTMENT:
      return 'Standardförändring av lägenheter';
    case NegotiationTypes.REBUILD_PROPERTY:
      return 'Hela fastigheten ombyggnation/renovering';
    case NegotiationTypes.REBUILD_PROPERTY_2:
      return 'Enskild lägenhet ombyggnation/renovering';
    case NegotiationTypes.REBUILD_APARTMENT:
      return 'Ombyggnation/renovering av lägenheter';
    case NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY:
      return 'Nyproduktion (fastighet saknas i register)';
    case NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT:
      return 'Nyproduktion';
    case NegotiationTypes.UTILITY_VALUE_PROPERTY:
      return 'Bruksvärdesförhandling';
    case NegotiationTypes.UTILITY_VALUE_APARTMENT:
      return 'Bruksvärdesförhandling av lägenheter';
    default:
      return 'Ej specifierad förhandlingstyp';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyNegotiationWithBlockingStates', ["NegotiationStates", "AgreementBlockedByType", function(NegotiationStates, AgreementBlockedByType) {
  return function(value, blockingAuthority) {
    return hyNegotiationWithBlockingStates(value, blockingAuthority, NegotiationStates, AgreementBlockedByType);
  };
}]);

function hyNegotiationWithBlockingStates(value, blockingAuthority, NegotiationStates, AgreementBlockedByType) {
  switch (value) {
    case NegotiationStates.NEW:
      return 'Ny';
    case NegotiationStates.IN_PREPARATION:
      return 'Bereds';
    case NegotiationStates.IN_PROGRESS:
      return 'Pågår';
    case NegotiationStates.SETTLEMENT:
      return 'Uppgörelse';
    case NegotiationStates.BLOCKED:
      switch (blockingAuthority) {
        case AgreementBlockedByType.PRIVATE:
          return 'Strandad HN';
        case AgreementBlockedByType.PUBLIC:
          return 'Strandad HMK';
        case AgreementBlockedByType.NONE:
          return 'Strandad Egensatt';
        case AgreementBlockedByType.MEDIATOR:
          return 'Strandad Skiljeman HN';
        default:
          return 'Strandad';
      }
    case NegotiationStates.PARTIALLY_BLOCKED:
      switch (blockingAuthority) {
        case AgreementBlockedByType.PRIVATE:
          return 'Delvis strandad HN';
        case AgreementBlockedByType.PUBLIC:
          return 'Delvis strandad HMK';
        case AgreementBlockedByType.NONE:
          return 'Delvis strandad Egensatt';
        case AgreementBlockedByType.MEDIATOR:
          return 'Delvis strandad Skiljeman HN';
        default:
          return 'Delvis strandad';
        }
    case NegotiationStates.FINISHED_OUTSIDE_PORTAL:
      return 'Felaktigt avslutad förhandling - Vänligen återöppna';
    case NegotiationStates.FINISHED:
      return 'Avslutad';
    default:
      return 'Alla statusar';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyNextNumber', function() {
  return function(input) {
    return Number(input) + 1;
  };
});

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyOrgNr', function() {
  return function(text) {
    return (text && hyOrgNr(text)) || text;
  };
});

function hyOrgNr(orgNumber) {
  return orgNumber.slice(0, -4) + '-' + orgNumber.substr(-4);
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyParties', ["Parties", function(Parties) {
  return function(value) {
    return hyParties(value, Parties);
  };
}]);

function hyParties(value, Parties) {
  switch (value) {
    case Parties.EXTERNAL:
      return 'Fastighetsägare/Ombud';
    case Parties.DELEGATE:
      return 'Delegat';
    case Parties.HGF:
      return 'HGF';
    default:
      return 'saknas';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyRoles', ["Role", function(Role) {
  return function(value) {
    return hyRoles(value, Role);
  };
}]);

function hyRoles(value, Role) {
  switch (value) {
    case Role.SPECTATOR:
      return 'Läsanvändare';
    case Role.USER:
      return 'Användare';
    case Role.ADMIN:
      return 'Admin';
    default:
      return 'Saknar behörighet';
  }
}

})();
(function () {
/* eslint-disable no-undefined */
'use strict';

angular.module('hyresgastApp').filter('nationalNoControl', function() {
  return function(nationalNo) {
    return nationalNo === '0000' ? '📜' : nationalNo;
  };
});

angular.module('hyresgastApp').filter('nationalNoTitleControl', function() {
  return function(nationalNo) {
    return nationalNo === '0000' ? 'Unik adress' : '';
  };
});
})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('hyNegotiationAgreementTypes', function() {
  return function(input, hasNegotiationAgreement) {
    var output;
    if (hasNegotiationAgreement) {
      switch (input) {
        case null:
        case '':
          output = 'Saknar FO';
          break;
        case '2A':
          output = '2-parts Annan';
          break;
        case '2H':
          output = '2-parts HBK';
          break;
        case '2O':
          output = '2-parts Ombud';
          break;
        case '2S':
          output = '2-parts Sabo/HMK';
          break;
        case '2M':
          output = '2-parts Skiljeman';
          break;
        case '3A':
          output = '3-parts A';
          break;
        case '3B':
          output = '3-parts B';
          break;
        case '3C':
          output = '3-parts 15';
          break;
        case '3O':
          output = '3-parts Ombud';
          break;
        default:
          output = 'Ej angivet';
          break;
      }
    } else {
      output = 'Ej angivet';
    }
    return output;
  };
});

})();
(function () {
// Example of usage:
// <span ng-bind-html="vm.yourText | preventDateBreak" ></span>

angular.module('hyresgastApp').filter('preventDateBreak', ["$sce", function($sce) {
  return function(input) {
    if (!input) return input;

    // pattern for multiple type of date formats, case insensitive
    var datePattern = /(\b\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}\b|\b\d{4}[-\/]\d{1,2}[-\/]\d{1,2}\b|\b\d{1,2}\s(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|maj|okt)\s\d{4}\b|\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|maj|okt)\s\d{1,2},\s\d{4}\b|\b\d{1,2}\/\d{1,2}\s\d{4}\b|\b\d{1,2}\s(?:jan|feb|mar|apr|maj|jun|jul|aug|sep|okt|nov|dec)\b|\b\d{1,2}\s(?:januari|februari|mars|april|maj|juni|juli|augusti|september|oktober|november|december)\b|\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s\d{1,2}\s\d{4}\b)/gi;

    var output = input.replace(datePattern, '<span style="white-space: nowrap;">$1</span>');

    return $sce.trustAsHtml(output);
  };
}]);
})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('regionTypeToDescription', ["Regions", function(Regions) {
  return function(value) {
    return regionTypeToDescription(value, Regions);
  };
}]);

function regionTypeToDescription(value, Regions) {
  switch (value) {
    case Regions.NORRLAND:
      return 'Norrland';
    case Regions.AROS_GEVLE:
      return 'Aros - Gävle';
    case Regions.STOCKHOLM:
      return 'Stockholm';
    case Regions.MIDDLE:
      return 'Mitt';
    case Regions.WEST_SWEDEN:
      return 'Västra Sverige';
    case Regions.BESK:
      return 'Bäsk';
    case Regions.SOUTH_EAST:
      return 'Sydost';
    case Regions.NORTH_SKONE:
      return 'Norra Skåne';
    case Regions.SOUTH_SKONE:
      return 'Södra Skåne';
    default:
      return 'Alla regioner';
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('rentalLevelTypeToDescription', function() {
  return function(value) {
    switch (value) {
      case '0':
        return 'Förändring i procent %';
      case '1':
        return 'Förändring i kr/mån';
      case '2':
        return 'Helt ny hyra i kr/mån';
      default:
        return 'Typ ej angivet';
    }
  };
});

})();
(function () {
'use strict';
/*
 Takes the input array and splices it to start from given input index
*/

angular.module('hyresgastApp').filter('startFrom', function() {
  return function(input, start) {
    start = +start; //parse to int
    return input.slice(start);
  };
});

})();
(function () {
'use strict';

PortalSettings.$inject = ["Settings", "SettingTypes"];
angular.module('PortalSettings', []).factory('PortalSettings', PortalSettings);

function PortalSettings(Settings, SettingTypes) {

  let settings = [];

  let load = cb => {
    Settings.load({})
      .$promise.then(result => {
        settings = result;
        cb();
      });
  }

  let maybeLoad = cb => {
    if (!settings.length) {
      load(() => { cb() });
    } else {
      cb();
    }
  };

  let findSetting = key => {
    let setting = settings.find(x => x.key === key);
    let value = null;
    if (!setting || !setting.value) {
      return value;
    }

    try {    
      switch (setting.type) {
        case SettingTypes.NUMBER:
          value = parseFloat(setting.value);
          break;
        case SettingTypes.BOOL:
          value = Boolean(setting.value);
          break;
        case SettingTypes.RANGE2NUMBER:
        case SettingTypes.JSON:
          value = JSON.parse(setting.value);
          break;

        default:
        case SettingTypes.STRING:
        case SettingTypes.TEXT:
          value = setting.value;
          break;
      }
    } catch (err) {
      //relax
    }

    return value;
  }

  let getSetting = (key, cb) => {
    maybeLoad(() => { cb(findSetting(key)) });
  };

  return {
    getSetting,

    getSuggestedRentByNHRange:  cb => getSetting('suggested-rent-by-nh-range', cb),
    getFooterInfo:              cb => {
      maybeLoad(() => {
        let result = {
          footerEmail: findSetting('footer-email'),
          footerPhone: findSetting('footer-phone'),
          footerHours: findSetting('footer-hours'),
        };
        cb(result);
      });
    },
  }
}
})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Reports', ["$resource", function($resource) {
  return $resource(
    '/api/reports/:id',
    { id: '@id' },
    {
      query: {
        url: '/api/reports',
        method: 'GET',
      },
      run: {
        url: '/api/reports/run',
        method: 'POST',
      },
      savePdf: {
        url: '/api/reports/save-pdf',
        method: 'POST',
      },
      saveXls: {
        url: '/api/reports/save-xls',
        method: 'POST',
      }
    }
  );
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Settings', ["$resource", function($resource) {
  return $resource(
    '/api/specops/settings', {},
    {
      load: {
        url: '/api/specops/settings/load',
        method: 'GET',
        isArray: true,
      },
      loadAll: {
        url: '/api/specops/settings/loadAll',
        method: 'GET',
        isArray: true,
      },
      alter: {
        url: '/api/specops/settings/alter',
        method: 'POST',
      }
    }
  );
}]);

})();
(function () {
'use strict';

// https://github.com/witoldsz/angular-http-auth
//
// AuthInterceptor intecepts 401 and buffers the initial request. When user
// eventually log in the buffered request will be sent to the API.
//
// @config
//   Ignoring the 401 interceptor: Sometimes you might not want the interceptor
//   to intercept a 401. In a case like this you can add ignoreAuthModule: true
//   to the request config.

angular.module('httpAuthInterceptor', []).factory('AuthInterceptor', ["$rootScope", "$q", "httpBuffer", function($rootScope, $q, httpBuffer) {
  return {
    responseError: function(rejection) {
      var config = rejection.config || {};
      if (!config.ignoreAuthModule) {
        switch (rejection.status) {
          case 401:
            var deferred = $q.defer();
            var bufferLength = httpBuffer.append(config, deferred);
            if (bufferLength === 1) {
              $rootScope.$broadcast('event:auth-loginRequired', rejection);
            }
            return deferred.promise;
        }
      }
      // otherwise, default behaviour
      return $q.reject(rejection);
    }
  };
}]);

})();
(function () {
'use strict';

// BaseInterceptor contains very basic changes of requests and responses.
// More complex changes to requests should be in its own file

angular.module('hyresgastApp').factory('BaseInterceptor', ["$q", "$location", function($q, $location) {
  return {
    responseError: function(rejection) {
      switch (rejection.status) {
        case 404:
          if ($location.path().indexOf('/resetpassword') !== -1 || $location.path().indexOf('/users/new') !== -1) {
            break;
          }
          $location.path('404').replace(); // back button
          break;
      }

      // otherwise, default behaviour
      return $q.reject(rejection);
    }
  };
}]);

})();
(function () {
'use strict';

// LocalhostInterceptor intercepts requests to the API and change
// the request url from localhost:3000|3344/api/resource to
// localhost:{LocalApiPort}/api/resource

angular.module('hyresgastApp').factory('LocalhostInterceptor', ["LocalApiPort", function(LocalApiPort) {
  var localhost = function() {
    return window.location.hostname === 'localhost';
  };

  var startsWithApi = /^\/api\//;
  var apiCall = function(url) {
    return startsWithApi.test(url);
  };

  return {
    request: function(config) {
      // In development - API is consumed on port other than client.
      if (localhost() && apiCall(config.url)) {
        config.url = 'http://localhost:' + LocalApiPort + config.url;
        config.withCredentials = true; // without this, cookies won't be included with the request
      }
      return config;
    }
  };
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').directive('hyAmendButton', amendButton);

function amendButton() {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/amend-button/amend-button.html',
    scope: {
      title: '@',
      amendTitle: '@',
      buttonClass: '@',
      normalClick: '&',
      amendClick: '&',
      disabled: '=',
      amendDisabled: '=',
    },
  };
  return directive;
}
})();
(function () {
/* eslint-disable no-unused-vars */
/*
 angular-file-upload v1.1.5
 https://github.com/nervgh/angular-file-upload
*/

/* jshint -W116 */
/* jshint -W089 */
/* jshint -W071 */
/* jshint -W106 */

'use strict';

angular
  .module('angularFileUpload', [])

  /**
   * Classes
   *
   * FileUploader
   * FileUploader.FileLikeObject
   * FileUploader.FileItem
   * FileUploader.FileDirective
   * FileUploader.FileSelect
   * FileUploader.FileDrop
   * FileUploader.FileOver
   */

  .value('fileUploaderOptions', {
    url: '/',
    alias: 'file',
    headers: {},
    queue: [],
    progress: 0,
    autoUpload: false,
    removeAfterUpload: false,
    method: 'POST',
    filters: [],
    formData: [],
    queueLimit: Number.MAX_VALUE,
    withCredentials: false
  })

  .factory('FileUploader', ["fileUploaderOptions", "$rootScope", "$http", "$window", "$compile", function(fileUploaderOptions, $rootScope, $http, $window, $compile) {
    /**
     * Creates an instance of FileUploader
     * @param {Object} [options]
     * @constructor
     */
    function FileUploader(options) {
      var settings = angular.copy(fileUploaderOptions);
      angular.extend(this, settings, options, {
        isUploading: false,
        _nextIndex: 0,
        _failFilterIndex: -1,
        _directives: { select: [], drop: [], over: [] }
      });

      // add default filters
      this.filters.unshift({ name: 'queueLimit', fn: this._queueLimitFilter });
      this.filters.unshift({ name: 'folder', fn: this._folderFilter });
    }
    /**********************
     * PUBLIC
     **********************/
    /**
     * Checks a support the html5 uploader
     * @returns {Boolean}
     * @readonly
     */
    FileUploader.prototype.isHTML5 = !!($window.File && $window.FormData);
    /**
     * Adds items to the queue
     * @param {File|HTMLInputElement|Object|FileList|Array<Object>} files
     * @param {Object} [options]
     * @param {Array<Function>|String} filters
     */
    FileUploader.prototype.addToQueue = function(files, options, filters) {
      var list = this.isArrayLikeObject(files) ? files : [files];
      var arrayOfFilters = this._getFilters(filters);
      var count = this.queue.length;
      var addedFileItems = [];

      angular.forEach(
        list,
        function(some /*{File|HTMLInputElement|Object}*/) {
          var temp = new FileUploader.FileLikeObject(some);

          if (this._isValidFile(temp, arrayOfFilters, options)) {
            var fileItem = new FileUploader.FileItem(this, some, options);
            addedFileItems.push(fileItem);
            this.queue.push(fileItem);
            this._onAfterAddingFile(fileItem);
          } else {
            var filter = this.filters[this._failFilterIndex];
            this._onWhenAddingFileFailed(temp, filter, options);
          }
        },
        this
      );

      if (this.queue.length !== count) {
        this._onAfterAddingAll(addedFileItems);
        this.progress = this._getTotalProgress();
      }

      this._render();
      if (this.autoUpload) this.uploadAll();
    };
    /**
     * Remove items from the queue. Remove last: index = -1
     * @param {FileItem|Number} value
     */
    FileUploader.prototype.removeFromQueue = function(value) {
      var index = this.getIndexOfItem(value);
      var item = this.queue[index];
      if (item.isUploading) item.cancel();
      this.queue.splice(index, 1);
      item._destroy();
      this.progress = this._getTotalProgress();
    };
    /**
     * Clears the queue
     */
    FileUploader.prototype.clearQueue = function() {
      while (this.queue.length) {
        this.queue[0].remove();
      }
      this.progress = 0;
    };
    /**
     * Uploads a item from the queue
     * @param {FileItem|Number} value
     */
    FileUploader.prototype.uploadItem = function(value) {
      var index = this.getIndexOfItem(value);
      var item = this.queue[index];

      var transport = '_xhrTransport';
      item._prepareToUploading();
      if (this.isUploading) return;

      this.isUploading = true;
      this[transport](item);
    };
    /**
     * Cancels uploading of item from the queue
     * @param {FileItem|Number} value
     */
    FileUploader.prototype.cancelItem = function(value) {
      var index = this.getIndexOfItem(value);
      var item = this.queue[index];
      var prop = '_xhr';
      if (item && item.isUploading) item[prop].abort();
    };
    /**
     * Uploads all not uploaded items of queue
     */
    FileUploader.prototype.uploadAll = function() {
      var items = this.getNotUploadedItems().filter(function(item) {
        return !item.isUploading;
      });
      if (!items.length) return;

      angular.forEach(items, function(item) {
        item._prepareToUploading();
      });
      items[0].upload();
    };
    /**
     * Cancels all uploads
     */
    FileUploader.prototype.cancelAll = function() {
      var items = this.getNotUploadedItems();
      angular.forEach(items, function(item) {
        item.cancel();
      });
    };
    /**
     * Returns "true" if value an instance of File
     * @param {*} value
     * @returns {Boolean}
     * @private
     */
    FileUploader.prototype.isFile = function(value) {
      var fn = $window.File;
      return fn && value instanceof fn;
    };
    /**
     * Returns "true" if value an instance of FileLikeObject
     * @param {*} value
     * @returns {Boolean}
     * @private
     */
    FileUploader.prototype.isFileLikeObject = function(value) {
      return value instanceof FileUploader.FileLikeObject;
    };
    /**
     * Returns "true" if value is array like object
     * @param {*} value
     * @returns {Boolean}
     */
    FileUploader.prototype.isArrayLikeObject = function(value) {
      return angular.isObject(value) && 'length' in value;
    };
    /**
     * Returns a index of item from the queue
     * @param {Item|Number} value
     * @returns {Number}
     */
    FileUploader.prototype.getIndexOfItem = function(value) {
      return angular.isNumber(value) ? value : this.queue.indexOf(value);
    };
    /**
     * Returns not uploaded items
     * @returns {Array}
     */
    FileUploader.prototype.getNotUploadedItems = function() {
      return this.queue.filter(function(item) {
        return !item.isUploaded;
      });
    };
    /**
     * Returns items ready for upload
     * @returns {Array}
     */
    FileUploader.prototype.getReadyItems = function() {
      return this.queue
        .filter(function(item) {
          return item.isReady && !item.isUploading;
        })
        .sort(function(item1, item2) {
          return item1.index - item2.index;
        });
    };
    /**
     * Destroys instance of FileUploader
     */
    FileUploader.prototype.destroy = function() {
      angular.forEach(
        this._directives,
        function(key) {
          angular.forEach(
            this._directives[key],
            function(object) {
              object.destroy();
            },
            this
          );
        },
        this
      );
    };
    /**
     * Callback
     * @param {Array} fileItems
     */
    FileUploader.prototype.onAfterAddingAll = function(fileItems) {};
    /**
     * Callback
     * @param {FileItem} fileItem
     */
    FileUploader.prototype.onAfterAddingFile = function(fileItem) {};
    /**
     * Callback
     * @param {File|Object} item
     * @param {Object} filter
     * @param {Object} options
     * @private
     */
    FileUploader.prototype.onWhenAddingFileFailed = function(item, filter, options) {};
    /**
     * Callback
     * @param {FileItem} fileItem
     */
    FileUploader.prototype.onBeforeUploadItem = function(fileItem) {};
    /**
     * Callback
     * @param {FileItem} fileItem
     * @param {Number} progress
     */
    FileUploader.prototype.onProgressItem = function(fileItem, progress) {};
    /**
     * Callback
     * @param {Number} progress
     */
    FileUploader.prototype.onProgressAll = function(progress) {};
    /**
     * Callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileUploader.prototype.onSuccessItem = function(item, response, status, headers) {};
    /**
     * Callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileUploader.prototype.onErrorItem = function(item, response, status, headers) {};
    /**
     * Callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileUploader.prototype.onInterruptedItem = function(item, response, status, headers) {};
    /**
     * Callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileUploader.prototype.onCancelItem = function(item, response, status, headers) {};
    /**
     * Callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileUploader.prototype.onCompleteItem = function(item, response, status, headers) {};
    /**
     * Callback
     */
    FileUploader.prototype.onCompleteAll = function() {};
    /**********************
     * PRIVATE
     **********************/
    /**
     * Returns the total progress
     * @param {Number} [value]
     * @returns {Number}
     * @private
     */
    FileUploader.prototype._getTotalProgress = function(value) {
      if (this.removeAfterUpload) return value || 0;

      var notUploaded = this.getNotUploadedItems().length;
      var uploaded = notUploaded ? this.queue.length - notUploaded : this.queue.length;
      var ratio = 100 / this.queue.length;
      var current = (value || 0) * ratio / 100;

      return Math.round(uploaded * ratio + current);
    };
    /**
     * Returns array of filters
     * @param {Array<Function>|String} filters
     * @returns {Array<Function>}
     * @private
     */
    FileUploader.prototype._getFilters = function(filters) {
      if (angular.isUndefined(filters)) return this.filters;
      if (angular.isArray(filters)) return filters;
      var names = filters.match(/[^\s,]+/g);
      return this.filters.filter(function(filter) {
        return names.indexOf(filter.name) !== -1;
      }, this);
    };
    /**
     * Updates html
     * @private
     */
    FileUploader.prototype._render = function() {
      if (!$rootScope.$$phase) $rootScope.$apply();
    };
    /**
     * Returns "true" if item is a file (not folder)
     * @param {File|FileLikeObject} item
     * @returns {Boolean}
     * @private
     */
    FileUploader.prototype._folderFilter = function(item) {
      return !!(item.size || item.type);
    };
    /**
     * Returns "true" if the limit has not been reached
     * @returns {Boolean}
     * @private
     */
    FileUploader.prototype._queueLimitFilter = function() {
      return this.queue.length < this.queueLimit;
    };
    /**
     * Returns "true" if file pass all filters
     * @param {File|Object} file
     * @param {Array<Function>} filters
     * @param {Object} options
     * @returns {Boolean}
     * @private
     */
    FileUploader.prototype._isValidFile = function(file, filters, options) {
      this._failFilterIndex = -1;
      return !filters.length
        ? true
        : filters.every(function(filter) {
          this._failFilterIndex++;
          return filter.fn.call(this, file, options);
        }, this);
    };
    /**
     * Checks whether upload successful
     * @param {Number} status
     * @returns {Boolean}
     * @private
     */
    FileUploader.prototype._isSuccessCode = function(status) {
      return (status >= 200 && status < 300) || status === 304;
    };
    /**
     * Transforms the server response
     * @param {*} response
     * @param {Object} headers
     * @returns {*}
     * @private
     */
    FileUploader.prototype._transformResponse = function(response, headers) {
      var headersGetter = this._headersGetter(headers);
      angular.forEach($http.defaults.transformResponse, function(transformFn) {
        response = transformFn(response, headersGetter);
      });
      return response;
    };
    /**
     * Parsed response headers
     * @param headers
     * @returns {Object}
     * @see https://github.com/angular/angular.js/blob/master/src/ng/http.js
     * @private
     */
    FileUploader.prototype._parseHeaders = function(headers) {
      var parsed = {},
        key,
        val,
        i;

      if (!headers) return parsed;

      angular.forEach(headers.split('\n'), function(line) {
        i = line.indexOf(':');
        key = line
          .slice(0, i)
          .trim()
          .toLowerCase();
        val = line.slice(i + 1).trim();

        if (key) {
          parsed[key] = parsed[key] ? parsed[key] + ', ' + val : val;
        }
      });

      return parsed;
    };
    /**
     * Returns function that returns headers
     * @param {Object} parsedHeaders
     * @returns {Function}
     * @private
     */
    FileUploader.prototype._headersGetter = function(parsedHeaders) {
      return function(name) {
        if (name) {
          return parsedHeaders[name.toLowerCase()] || null;
        }
        return parsedHeaders;
      };
    };
    /**
     * The XMLHttpRequest transport
     * @param {FileItem} item
     * @private
     */
    FileUploader.prototype._xhrTransport = function(item) {
      var xhr = (item._xhr = new XMLHttpRequest());
      var form = new FormData();
      var that = this;

      that._onBeforeUploadItem(item);

      angular.forEach(item.formData, function(obj) {
        angular.forEach(obj, function(value, key) {
          form.append(key, value);
        });
      });

      form.append(item.alias, item._file, item.file.name);

      xhr.upload.onprogress = function(event) {
        var progress = Math.round(event.lengthComputable ? event.loaded * 100 / event.total : 0);
        that._onProgressItem(item, progress);
      };

      xhr.onload = function() {
        var headers = that._parseHeaders(xhr.getAllResponseHeaders());
        var response = that._transformResponse(xhr.response, headers);
        var gist = that._isSuccessCode(xhr.status) ? 'Success' : 'Error';
        var method = '_on' + gist + 'Item';
        that[method](item, response, xhr.status, headers);
        that._onCompleteItem(item, response, xhr.status, headers);
      };

      xhr.onerror = function() {
        var headers = that._parseHeaders(xhr.getAllResponseHeaders());
        var response = that._transformResponse(xhr.response, headers);
        that._onErrorItem(item, response, xhr.status, headers);
        that._onCompleteItem(item, response, xhr.status, headers);
      };

      xhr.onabort = function() {
        var headers = that._parseHeaders(xhr.getAllResponseHeaders());
        var response = that._transformResponse(xhr.response, headers);
        that._onCancelItem(item, response, xhr.status, headers);
        that._onCompleteItem(item, response, xhr.status, headers);
      };

      xhr.open(item.method, item.url, true);

      xhr.withCredentials = item.withCredentials;

      angular.forEach(item.headers, function(value, name) {
        xhr.setRequestHeader(name, value);
      });

      xhr.send(form);
      this._render();
    };
    /**
     * Inner callback
     * @param {File|Object} item
     * @param {Object} filter
     * @param {Object} options
     * @private
     */
    FileUploader.prototype._onWhenAddingFileFailed = function(item, filter, options) {
      this.onWhenAddingFileFailed(item, filter, options);
    };
    /**
     * Inner callback
     * @param {FileItem} item
     */
    FileUploader.prototype._onAfterAddingFile = function(item) {
      this.onAfterAddingFile(item);
    };
    /**
     * Inner callback
     * @param {Array<FileItem>} items
     */
    FileUploader.prototype._onAfterAddingAll = function(items) {
      this.onAfterAddingAll(items);
    };
    /**
     *  Inner callback
     * @param {FileItem} item
     * @private
     */
    FileUploader.prototype._onBeforeUploadItem = function(item) {
      item._onBeforeUpload();
      this.onBeforeUploadItem(item);
    };
    /**
     * Inner callback
     * @param {FileItem} item
     * @param {Number} progress
     * @private
     */
    FileUploader.prototype._onProgressItem = function(item, progress) {
      var total = this._getTotalProgress(progress);
      this.progress = total;
      item._onProgress(progress);
      this.onProgressItem(item, progress);
      this.onProgressAll(total);
      this._render();
    };
    /**
     * Inner callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileUploader.prototype._onSuccessItem = function(item, response, status, headers) {
      item._onSuccess(response, status, headers);
      this.onSuccessItem(item, response, status, headers);
    };
    /**
     * Inner callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileUploader.prototype._onErrorItem = function(item, response, status, headers) {
      item._onError(response, status, headers);
      this.onErrorItem(item, response, status, headers);
    };
    /**
     * Inner callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileUploader.prototype._onInterruptedItem = function(item, response, status, headers) {
      this.isUploading = false;

      item._onInterrupted(response, status, headers);
      this.onInterruptedItem(item, response, status, headers);

      this._render();
    };
    /**
     * Inner callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileUploader.prototype._onCancelItem = function(item, response, status, headers) {
      item._onCancel(response, status, headers);
      this.onCancelItem(item, response, status, headers);
    };
    /**
     * Inner callback
     * @param {FileItem} item
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileUploader.prototype._onCompleteItem = function(item, response, status, headers) {
      item._onComplete(response, status, headers);
      this.onCompleteItem(item, response, status, headers);

      var nextItem = this.getReadyItems()[0];
      this.isUploading = false;

      if (angular.isDefined(nextItem)) {
        nextItem.upload();
        return;
      }

      this.onCompleteAll();
      this.progress = this._getTotalProgress();
      this._render();
    };
    /**********************
     * STATIC
     **********************/
    /**
     * @borrows FileUploader.prototype.isFile
     */
    FileUploader.isFile = FileUploader.prototype.isFile;
    /**
     * @borrows FileUploader.prototype.isFileLikeObject
     */
    FileUploader.isFileLikeObject = FileUploader.prototype.isFileLikeObject;
    /**
     * @borrows FileUploader.prototype.isArrayLikeObject
     */
    FileUploader.isArrayLikeObject = FileUploader.prototype.isArrayLikeObject;
    /**
     * @borrows FileUploader.prototype.isHTML5
     */
    FileUploader.isHTML5 = FileUploader.prototype.isHTML5;
    /**
     * Inherits a target (Class_1) by a source (Class_2)
     * @param {Function} target
     * @param {Function} source
     */
    FileUploader.inherit = function(target, source) {
      target.prototype = Object.create(source.prototype);
      target.prototype.constructor = target;
      target.super_ = source;
    };
    FileUploader.FileLikeObject = FileLikeObject;
    FileUploader.FileItem = FileItem;
    FileUploader.FileDirective = FileDirective;
    FileUploader.FileSelect = FileSelect;
    FileUploader.FileDrop = FileDrop;
    FileUploader.FileOver = FileOver;

    // ---------------------------

    /**
     * Creates an instance of FileLikeObject
     * @param {File|HTMLInputElement|Object} fileOrInput
     * @constructor
     */
    function FileLikeObject(fileOrInput) {
      var isInput = angular.isElement(fileOrInput);
      var fakePathOrObject = isInput ? fileOrInput.value : fileOrInput;
      var postfix = angular.isString(fakePathOrObject) ? 'FakePath' : 'Object';
      var method = '_createFrom' + postfix;
      this[method](fakePathOrObject);
    }

    /**
     * Creates file like object from fake path string
     * @param {String} path
     * @private
     */
    FileLikeObject.prototype._createFromFakePath = function(path) {
      this.lastModifiedDate = null;
      this.size = null;
      this.type = 'like/' + path.slice(path.lastIndexOf('.') + 1).toLowerCase();
      this.name = path.slice(path.lastIndexOf('/') + path.lastIndexOf('\\') + 2);
    };
    /**
     * Creates file like object from object
     * @param {File|FileLikeObject} object
     * @private
     */
    FileLikeObject.prototype._createFromObject = function(object) {
      this.lastModifiedDate = angular.copy(object.lastModifiedDate);
      this.size = object.size;
      this.type = object.type;
      this.name = object.name;
    };

    // ---------------------------

    /**
     * Creates an instance of FileItem
     * @param {FileUploader} uploader
     * @param {File|HTMLInputElement|Object} some
     * @param {Object} options
     * @constructor
     */
    function FileItem(uploader, some, options) {
      var isInput = angular.isElement(some);
      var input = isInput ? angular.element(some) : null;
      var file = !isInput ? some : null;

      angular.extend(
        this,
        {
          url: uploader.url,
          alias: uploader.alias,
          headers: angular.copy(uploader.headers),
          formData: angular.copy(uploader.formData),
          removeAfterUpload: uploader.removeAfterUpload,
          withCredentials: uploader.withCredentials,
          method: uploader.method
        },
        options,
        {
          uploader: uploader,
          file: new FileUploader.FileLikeObject(some),
          isReady: false,
          isUploading: false,
          isUploaded: false,
          isSuccess: false,
          isCancel: false,
          isError: false,
          isInterrupted: false,
          progress: 0,
          index: null,
          _file: file,
          _input: input
        }
      );

      if (input) this._replaceNode(input);
    }
    /**********************
     * PUBLIC
     **********************/
    /**
     * Uploads a FileItem
     */
    FileItem.prototype.upload = function() {
      this.uploader.uploadItem(this);
    };
    /**
     * Cancels uploading of FileItem
     */
    FileItem.prototype.cancel = function() {
      this.uploader.cancelItem(this);
    };
    /**
     * Removes a FileItem
     */
    FileItem.prototype.remove = function() {
      this.uploader.removeFromQueue(this);
    };
    /**
     * Callback
     * @private
     */
    FileItem.prototype.onBeforeUpload = function() {};
    /**
     * Callback
     * @param {Number} progress
     * @private
     */
    FileItem.prototype.onProgress = function(progress) {};
    /**
     * Callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileItem.prototype.onSuccess = function(response, status, headers) {};
    /**
     * Callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileItem.prototype.onError = function(response, status, headers) {};
    /**
     * Callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileItem.prototype.onInterrupted = function(response, status, headers) {};
    /**
     * Callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileItem.prototype.onCancel = function(response, status, headers) {};
    /**
     * Callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     */
    FileItem.prototype.onComplete = function(response, status, headers) {};
    /**********************
     * PRIVATE
     **********************/
    /**
     * Inner callback
     */
    FileItem.prototype._onBeforeUpload = function() {
      this.isReady = true;
      this.isUploading = true;
      this.isUploaded = false;
      this.isSuccess = false;
      this.isCancel = false;
      this.isError = false;
      this.progress = 0;
      this.onBeforeUpload();
    };
    /**
     * Inner callback
     * @param {Number} progress
     * @private
     */
    FileItem.prototype._onProgress = function(progress) {
      this.progress = progress;
      this.onProgress(progress);
    };
    /**
     * Inner callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileItem.prototype._onSuccess = function(response, status, headers) {
      this.isReady = false;
      this.isUploading = false;
      this.isUploaded = true;
      this.isSuccess = true;
      this.isCancel = false;
      this.isError = false;
      this.progress = 100;
      this.index = null;
      this.onSuccess(response, status, headers);
    };
    /**
     * Inner callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileItem.prototype._onError = function(response, status, headers) {
      this.isReady = false;
      this.isUploading = false;
      this.isUploaded = true;
      this.isSuccess = false;
      this.isCancel = false;
      this.isError = true;
      this.progress = 0;
      this.index = null;
      this.onError(response, status, headers);
    };
    /**
     * Inner callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileItem.prototype._onInterrupted = function(response, status, headers) {
      this.isReady = false;
      this.isUploading = false;
      this.isUploaded = false;
      this.isSuccess = false;
      this.isCancel = false;
      this.isError = false;
      this.isInterrupted = true;
      this.onInterrupted(response, status, headers);
    };
    /**
     * Inner callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileItem.prototype._onCancel = function(response, status, headers) {
      this.isReady = false;
      this.isUploading = false;
      this.isUploaded = false;
      this.isSuccess = false;
      this.isCancel = true;
      this.isError = false;
      this.progress = 0;
      this.index = null;
      this.onCancel(response, status, headers);
    };
    /**
     * Inner callback
     * @param {*} response
     * @param {Number} status
     * @param {Object} headers
     * @private
     */
    FileItem.prototype._onComplete = function(response, status, headers) {
      this.onComplete(response, status, headers);
      if (this.removeAfterUpload) this.remove();
    };
    /**
     * Destroys a FileItem
     */
    FileItem.prototype._destroy = function() {
      if (this._input) this._input.remove();
      delete this._input;
    };
    /**
     * Prepares to uploading
     * @private
     */
    FileItem.prototype._prepareToUploading = function() {
      this.index = this.index || ++this.uploader._nextIndex;
      this.isReady = true;
    };
    /**
     * Replaces input element on his clone
     * @param {JQLite|jQuery} input
     * @private
     */
    FileItem.prototype._replaceNode = function(input) {
      var clone = $compile(input.clone())(input.scope());
      clone.prop('value', null); // FF fix
      input.css('display', 'none');
      input.after(clone); // remove jquery dependency
    };

    // ---------------------------

    /**
     * Creates instance of {FileDirective} object
     * @param {Object} options
     * @param {Object} options.uploader
     * @param {HTMLElement} options.element
     * @param {Object} options.events
     * @param {String} options.prop
     * @constructor
     */
    function FileDirective(options) {
      angular.extend(this, options);
      this.uploader._directives[this.prop].push(this);
      this._saveLinks();
      this.bind();
    }
    /**
     * Map of events
     * @type {Object}
     */
    FileDirective.prototype.events = {};
    /**
     * Binds events handles
     */
    FileDirective.prototype.bind = function() {
      for (var key in this.events) {
        var prop = this.events[key];
        if (typeof prop === 'string') {
          this.element.bind(key, this[prop]);
        }
      }
    };
    /**
     * Unbinds events handles
     */
    FileDirective.prototype.unbind = function() {
      for (var key in this.events) {
        if (typeof key === 'string') {
          this.element.unbind(key, this.events[key]);
        }
      }
    };
    /**
     * Destroys directive
     */
    FileDirective.prototype.destroy = function() {
      var index = this.uploader._directives[this.prop].indexOf(this);
      this.uploader._directives[this.prop].splice(index, 1);
      this.unbind();
      // this.element = null;
    };
    /**
     * Saves links to functions
     * @private
     */
    FileDirective.prototype._saveLinks = function() {
      for (var key in this.events) {
        var prop = this.events[key];
        if (typeof prop === 'string') {
          this[prop] = this[prop].bind(this);
        }
      }
    };

    // ---------------------------

    FileUploader.inherit(FileSelect, FileDirective);

    /**
     * Creates instance of {FileSelect} object
     * @param {Object} options
     * @constructor
     */
    function FileSelect(options) {
      FileSelect.super_.apply(this, arguments);

      if (!this.uploader.isHTML5) {
        this.element.removeAttr('multiple');
      }
      this.element.prop('value', null); // FF fix
    }
    /**
     * Map of events
     * @type {Object}
     */
    FileSelect.prototype.events = {
      $destroy: 'destroy',
      change: 'onChange'
    };
    /**
     * Name of property inside uploader._directive object
     * @type {String}
     */
    FileSelect.prototype.prop = 'select';
    /**
     * Returns options
     * @return {Object|undefined}
     */
    FileSelect.prototype.getOptions = function() {};
    /**
     * Returns filters
     * @return {Array<Function>|String|undefined}
     */
    FileSelect.prototype.getFilters = function() {};
    /**
     * If returns "true" then HTMLInputElement will be cleared
     * @returns {Boolean}
     */
    FileSelect.prototype.isEmptyAfterSelection = function() {
      return !!this.element.attr('multiple');
    };
    /**
     * Event handler
     */
    FileSelect.prototype.onChange = function() {
      var files = this.uploader.isHTML5 ? this.element[0].files : this.element[0];
      var options = this.getOptions();
      var filters = this.getFilters();

      if (!this.uploader.isHTML5) this.destroy();
      this.uploader.addToQueue(files, options, filters);
      if (this.isEmptyAfterSelection()) this.element.prop('value', null);
    };

    // ---------------------------

    FileUploader.inherit(FileDrop, FileDirective);

    /**
     * Creates instance of {FileDrop} object
     * @param {Object} options
     * @constructor
     */
    function FileDrop(options) {
      FileDrop.super_.apply(this, arguments);
    }
    /**
     * Map of events
     * @type {Object}
     */
    FileDrop.prototype.events = {
      $destroy: 'destroy',
      drop: 'onDrop',
      dragover: 'onDragOver',
      dragleave: 'onDragLeave'
    };
    /**
     * Name of property inside uploader._directive object
     * @type {String}
     */
    FileDrop.prototype.prop = 'drop';
    /**
     * Returns options
     * @return {Object|undefined}
     */
    FileDrop.prototype.getOptions = function() {};
    /**
     * Returns filters
     * @return {Array<Function>|String|undefined}
     */
    FileDrop.prototype.getFilters = function() {};
    /**
     * Event handler
     */
    FileDrop.prototype.onDrop = function(event) {
      var transfer = this._getTransfer(event);
      if (!transfer) return;
      var options = this.getOptions();
      var filters = this.getFilters();
      this._preventAndStop(event);
      angular.forEach(this.uploader._directives.over, this._removeOverClass, this);
      this.uploader.addToQueue(transfer.files, options, filters);
    };
    /**
     * Event handler
     */
    FileDrop.prototype.onDragOver = function(event) {
      var transfer = this._getTransfer(event);
      if (!this._haveFiles(transfer.types)) return;
      transfer.dropEffect = 'copy';
      this._preventAndStop(event);
      angular.forEach(this.uploader._directives.over, this._addOverClass, this);
    };
    /**
     * Event handler
     */
    FileDrop.prototype.onDragLeave = function(event) {
      if (event.currentTarget !== this.element[0]) return;
      this._preventAndStop(event);
      angular.forEach(this.uploader._directives.over, this._removeOverClass, this);
    };
    /**
     * Helper
     */
    FileDrop.prototype._getTransfer = function(event) {
      return event.dataTransfer ? event.dataTransfer : event.originalEvent.dataTransfer; // jQuery fix;
    };
    /**
     * Helper
     */
    FileDrop.prototype._preventAndStop = function(event) {
      event.preventDefault();
      event.stopPropagation();
    };
    /**
     * Returns "true" if types contains files
     * @param {Object} types
     */
    FileDrop.prototype._haveFiles = function(types) {
      if (!types) return false;
      if (types.indexOf) {
        return types.indexOf('Files') !== -1;
      } else if (types.contains) {
        return types.contains('Files');
      } else {
        return false;
      }
    };
    /**
     * Callback
     */
    FileDrop.prototype._addOverClass = function(item) {
      item.addOverClass();
    };
    /**
     * Callback
     */
    FileDrop.prototype._removeOverClass = function(item) {
      item.removeOverClass();
    };

    // ---------------------------

    FileUploader.inherit(FileOver, FileDirective);

    /**
     * Creates instance of {FileDrop} object
     * @param {Object} options
     * @constructor
     */
    function FileOver(options) {
      FileOver.super_.apply(this, arguments);
    }
    /**
     * Map of events
     * @type {Object}
     */
    FileOver.prototype.events = {
      $destroy: 'destroy'
    };
    /**
     * Name of property inside uploader._directive object
     * @type {String}
     */
    FileOver.prototype.prop = 'over';
    /**
     * Over class
     * @type {string}
     */
    FileOver.prototype.overClass = 'nv-file-over';
    /**
     * Adds over class
     */
    FileOver.prototype.addOverClass = function() {
      this.element.addClass(this.getOverClass());
    };
    /**
     * Removes over class
     */
    FileOver.prototype.removeOverClass = function() {
      this.element.removeClass(this.getOverClass());
    };
    /**
     * Returns over class
     * @returns {String}
     */
    FileOver.prototype.getOverClass = function() {
      return this.overClass;
    };

    return FileUploader;
  }])

  .directive('nvFileSelect', ["$parse", "FileUploader", function($parse, FileUploader) {
    return {
      link: function(scope, element, attributes) {
        var uploader = scope.$eval(attributes.uploader);

        if (!(uploader instanceof FileUploader)) {
          throw new TypeError('"Uploader" must be an instance of FileUploader');
        }

        var object = new FileUploader.FileSelect({
          uploader: uploader,
          element: element
        });

        object.getOptions = $parse(attributes.options).bind(object, scope);
        object.getFilters = function() {
          return attributes.filters;
        };
      }
    };
  }])

  .directive('nvFileDrop', ["$parse", "FileUploader", function($parse, FileUploader) {
    return {
      link: function(scope, element, attributes) {
        var uploader = scope.$eval(attributes.uploader);

        if (!(uploader instanceof FileUploader)) {
          throw new TypeError('"Uploader" must be an instance of FileUploader');
        }

        if (!uploader.isHTML5) return;

        var object = new FileUploader.FileDrop({
          uploader: uploader,
          element: element
        });

        object.getOptions = $parse(attributes.options).bind(object, scope);
        object.getFilters = function() {
          return attributes.filters;
        };
      }
    };
  }])

  .directive('nvFileOver', ["FileUploader", function(FileUploader) {
    return {
      link: function(scope, element, attributes) {
        var uploader = scope.$eval(attributes.uploader);

        if (!(uploader instanceof FileUploader)) {
          throw new TypeError('"Uploader" must be an instance of FileUploader');
        }

        var object = new FileUploader.FileOver({
          uploader: uploader,
          element: element
        });

        object.getOverClass = function() {
          return attributes.overClass || this.overClass;
        };
      }
    };
  }]);

/* jshint +W116 */
/* jshint +W089 */
/* jshint +W071 */
/* jshint +W106 */

})();
(function () {
'use strict';

/* jshint -W018 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

angular
  .module('uiAccordion', ['uiCollapse', 'uiTabindex'])

  .constant('accordionConfig', {
    closeOthers: true
  })

  .controller('UibAccordionController', ["$scope", "$attrs", "accordionConfig", function($scope, $attrs, accordionConfig) {
    // This array keeps track of the accordion groups
    this.groups = [];

    // Ensure that all the groups in this accordion are closed, unless close-others explicitly says not to
    this.closeOthers = function(openGroup) {
      var closeOthers = angular.isDefined($attrs.closeOthers)
        ? $scope.$eval($attrs.closeOthers)
        : accordionConfig.closeOthers;
      if (closeOthers) {
        angular.forEach(this.groups, function(group) {
          if (group !== openGroup) {
            group.isOpen = false;
          }
        });
      }
    };

    // This is called from the accordion-group directive to add itself to the accordion
    this.addGroup = function(groupScope) {
      var that = this;
      this.groups.push(groupScope);

      groupScope.$on('$destroy', function(event) {
        that.removeGroup(groupScope);
      });
    };

    // This is called from the accordion-group directive when to remove itself
    this.removeGroup = function(group) {
      var index = this.groups.indexOf(group);
      if (index !== -1) {
        this.groups.splice(index, 1);
      }
    };
  }])

  // The accordion directive simply sets up the directive controller
  // and adds an accordion CSS class to itself element.
  .directive('uibAccordion', function() {
    return {
      controller: 'UibAccordionController',
      controllerAs: 'accordion',
      transclude: true,
      templateUrl: function(element, attrs) {
        return attrs.templateUrl || 'components/accordion/accordion.html';
      }
    };
  })

  // The accordion-group directive indicates a block of html that will expand and collapse in an accordion
  .directive('uibAccordionGroup', function() {
    return {
      require: '^uibAccordion', // We need this directive to be inside an accordion
      transclude: true, // It transcludes the contents of the directive into the template
      restrict: 'A',
      templateUrl: function(element, attrs) {
        return attrs.templateUrl || 'components/accordion/accordion-group.html';
      },
      scope: {
        heading: '@', // Interpolate the heading attribute onto this scope
        panelClass: '@?', // Ditto with panelClass
        isOpen: '=?',
        isDisabled: '=?'
      },
      controller: function() {
        this.setHeading = function(element) {
          this.heading = element;
        };
      },
      link: function(scope, element, attrs, accordionCtrl) {
        element.addClass('card');
        accordionCtrl.addGroup(scope);

        scope.openClass = attrs.openClass || 'card--open';
        scope.panelClass = attrs.panelClass || 'card--default';
        scope.$watch('isOpen', function(value) {
          element.toggleClass(scope.openClass, !!value);
          if (value) {
            accordionCtrl.closeOthers(scope);
          }
        });

        scope.toggleOpen = function($event) {
          if (!scope.isDisabled) {
            if (!$event || $event.which === 32) {
              scope.isOpen = !scope.isOpen;
            }
          }
        };

        var id = 'accordiongroup-' + scope.$id + '-' + Math.floor(Math.random() * 10000);
        scope.headingId = id + '-tab';
        scope.panelId = id + '-card';
      }
    };
  })

  // Use accordion-heading below an accordion-group to provide a heading containing HTML
  .directive('uibAccordionHeading', function() {
    return {
      transclude: true, // Grab the contents to be used as the heading
      template: '', // In effect remove this element!
      replace: true,
      require: '^uibAccordionGroup',
      link: function(scope, element, attrs, accordionGroupCtrl, transclude) {
        // Pass the heading to the accordion-group controller
        // so that it can be transcluded into the right place in the template
        // [The second parameter to transclude causes the elements to be cloned so that they work in ng-repeat]
        accordionGroupCtrl.setHeading(transclude(scope, angular.noop));
      }
    };
  })

  // Use in the accordion-group template to indicate where you want the heading to be transcluded
  // You must provide the property on the accordion-group controller that will hold the transcluded element
  .directive('uibAccordionTransclude', function() {
    return {
      require: '^uibAccordionGroup',
      link: function(scope, element, attrs, controller) {
        scope.$watch(
          function() {
            return controller[attrs.uibAccordionTransclude];
          },
          function(heading) {
            if (heading) {
              var elem = angular.element(element[0].querySelector(getHeaderSelectors()));
              elem.html('');
              elem.append(heading);
            }
          }
        );
      }
    };

    function getHeaderSelectors() {
      return (
        'uib-accordion-header,' +
        'data-uib-accordion-header,' +
        'x-uib-accordion-header,' +
        'uib\\:accordion-header,' +
        '[uib-accordion-header],' +
        '[data-uib-accordion-header],' +
        '[x-uib-accordion-header]'
      );
    }
  });

/* jshint -W018 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

})();
(function () {
'use strict';

apartmentSelector.$inject = ["$timeout", "$interval", "HyUtils", "ErrorHandler", "$document"];
angular.module('hyresgastApp').directive('hyApartmentSelector', apartmentSelector);

function apartmentSelector($timeout, $interval, HyUtils, ErrorHandler, $document) {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/apartment-selector/apartment-selector.html',
    scope: {
      isModal: '=',
      allowEmptyProperties: '=',
      emptyProperties: '=',
      fGetProperties: '&',
      fGetApartments: '&',
      apartmentsArguments: '=',
      //isJointProperties: '=',
      incomingProperties: '=',
      model: '=',
      isInsane: '=',
    },
    link: link,
  };
  return directive;

  // eslint-disable-next-line no-unused-vars
  function link(scope, element, attrs) {

    scope.properties = [];
    scope.propertiesTimer = null;
    scope.tickedApartments = {};
    scope.tickedProperties = {};
    scope.propertiesLoading = false;
    scope.apartmentsLoading = false;

    scope.model = [];

    scope.canAddEmptyProperty = false;

    scope.canSearchCounterpart = !scope.incomingProperties;

    scope.vm = { apartmentFilter: '' };

    /**
     * gets all ticked ids
     * @param {Object} ticked 
     * @returns {Array<number>}
     */
    let getTicked = ticked => Object.keys(ticked).filter(k => ticked[k]).map(id => parseInt(id));
    /**
     * determines if there is something ticked
     * @param {Object} ticked
     * @returns {boolean} true if something is ticked
     */
    let hasTicked = ticked => !!getTicked(ticked).length;
    
    scope.hasSanePropertyCount = () => {
      let count = getTicked(scope.tickedProperties).length;
      return scope.isInsane === true ? true : (count < 50);
    };
    scope.hasTickedProperties = () => hasTicked(scope.tickedProperties);
    scope.hasTickedApartments = () => hasTicked(scope.tickedApartments);

    scope.apartmentsFilter = el => {
      if (!scope.vm.apartmentFilter || scope.vm.apartmentFilter.length === 0) {
        return true;
      }
      let phrase = scope.vm.apartmentFilter.toLowerCase();
      return (`${el.streetAddress}|${el.nationalNo}|${el.ownerNo}`).toLowerCase().includes(phrase);
    };

    //TODO: Generalize
    let separateByProperty = () => {
      let previous = scope.model[0];
      scope.model.forEach((row, index) => {
        if (index > 0) {
          previous = scope.model[index - 1];
          row.differentProperty = row.property.id !== previous.property.id;
        } else {
          row.differentProperty = true;
        }
      });
    };

    let sortModel = () => {
      let hash = a => `${a.property.district}|${a.property.propertyDesignation}|${a.streetAddress}|${a.nationalNo}|${a.ownerNo}`;
      scope.model.sort((a, b) => hash(a) > hash(b) ? 1 : -1);
      separateByProperty();
    };
    let sortEmptyProperties = () => {
      scope.emptyProperties.sort((a, b) => a.id > b.id ? 1: -1);
    };

    /**
     * add apartments avoiding duplicates
     * @param {Array} newApartments
     */
    let addUniqueApartments = newApartments => {
      let hash = a => `${a.streetAddress}|${a.nationalNo}|${a.ownerNo}`;
      scope.model = HyUtils.unionArraysFn(scope.model, newApartments, (a, b) => {
        return hash(a) === hash(b);
      });
    };

    let firstFieldEl = $document[0].getElementById('propertyFilter');
    let lastVisible = false;
    $interval(() => {
      let visible = HyUtils.isVisible(firstFieldEl);
      if (visible && !lastVisible) {
        firstFieldEl.focus();
      }
      lastVisible = visible;
    }, 500);

    scope.addEveryApartment = () => {
      var ticked = getTicked(scope.tickedProperties);
      if (!ticked.length) {
        return;
      }
      var propertiesDone = 0;
      var apartmentCollector = [];
      scope.busyAddEvery = true;
      scope.properties.forEach(p => {
        if (!ticked.includes(p.id)) return;
        let args = Object.assign({ selectedProperty: p }, scope.apartmentsArguments);
        scope.fGetApartments()(args)
          .$promise.then(response => {
            response.rows.forEach(el => el.property = p);
            apartmentCollector.push(...response.rows);
            propertiesDone++;

            if (propertiesDone === ticked.length) {
              addUniqueApartments(apartmentCollector);
              scope.busyAddEvery = false;
              scope.allPropertiesSelected = false;
              scope.tickedProperties = {};
              sortModel();
            }
          });
      });
    };

    scope.focusOnSingleProperty = () => {
      if (scope.properties.length === 1) {
        scope.selectProperty(scope.properties[0]);
      }
    };

    scope.selectProperty = property => {
      scope.selectedProperty = property;
      scope.getApartments();
      scope.canAddEmptyProperty = canAddEmptyProperty();
    };
    scope.propertiesAfterType = () => {
      if (scope.propertiesTimer) {
        $timeout.cancel(scope.propertiesTimer);
      }
      scope.propertiesTimer = $timeout(scope.getProperties, 500);
    };

    scope.toggleAllProperties = () => {
      if (getTicked(scope.tickedProperties).length < scope.properties.length) {
        scope.tickedProperties = scope.properties.reduce((acc, el) => { acc[el.id] = true; return acc; }, {});
      } else {
        scope.tickedProperties = {};
      }
      scope.canAddEmptyProperty = canAddEmptyProperty();
    };
    scope.toggleAllApartments = () => {
      if (getTicked(scope.tickedApartments).length < scope.apartments.length) {
        scope.tickedApartments = scope.apartments.reduce((acc, el) => { acc[el.id] = true; return acc; }, {});
      } else {
        scope.tickedApartments = {};
      }
    };

    let fixEmptyProperties = () => {
      if (scope.emptyProperties) {
        scope.emptyProperties = scope.emptyProperties.filter(p => !scope.model.find(a => a.property.id === p.id));
      }
    };
    let canAddEmptyProperty = () => {
      if (scope.allowEmptyProperties) {
        if (!scope.emptyProperties) {
          scope.emptyProperties = [];
        }
        let selectedIds = getTicked(scope.tickedProperties);
        if (!selectedIds.length && scope.selectedProperty) {
          selectedIds.push(scope.selectedProperty.id);
        }
        if (!selectedIds.length) {
          return false;
        }
        let presentInEmptyProperties = scope.emptyProperties.some(p => selectedIds.includes(p.id));
        let presentInApartments = scope.model.some(a => selectedIds.includes(a.property.id));
        return !presentInEmptyProperties && !presentInApartments;
      } else {
        return false;
      }
    };
    scope.addEmptyProperty = () => {
      let selectedProperties = getTicked(scope.tickedProperties)
        .map(id => scope.properties.find(p => p.id === id));
      if (!selectedProperties.length) {
        selectedProperties.push(scope.selectedProperty);
      }
      scope.emptyProperties = scope.emptyProperties.concat(selectedProperties);
      scope.tickedProperties = {};
      scope.allPropertiesSelected = false;
      sortEmptyProperties();
      scope.canAddEmptyProperty = canAddEmptyProperty();
    };
    scope.deleteEmptyProperty = id => {
      scope.emptyProperties = scope.emptyProperties.filter(p => p.id !== id);
      sortEmptyProperties();
      scope.canAddEmptyProperty = canAddEmptyProperty();
    };

    scope.addSelected = () => {
      let tickedApartments = getTicked(scope.tickedApartments);
      let newApartments = scope.apartments.filter(el => tickedApartments.includes(el.id));
      newApartments = newApartments.filter(el => scope.apartmentsFilter(el));
      newApartments.forEach(el => el.property = scope.selectedProperty);
      addUniqueApartments(newApartments);
      scope.tickedApartments = {};
      scope.allApartmentsSelected = false;
      sortModel();
      fixEmptyProperties();
    };
    scope.deleteSelected = id => {
      scope.model = scope.model.filter(row => row.id !== id);
      sortModel();
    };

    scope.getOwnersString = p => p.owners ? '<span class="ownercounterpart">Ägare: </span>' + p.owners.map(o => o.name).join(', ') : '';
    scope.getOrganizationsString = p => {
      if (!p.organizations || !p.organizations.length) return '';
      let names = p.organizations.map(o => o.name);
      if (names.length > 3) {
        names.splice(3);
        names.push('...');
      }
      return '<span class="ownercounterpart">Motpart: </span>' + names.join(', ');
    };

    scope.getProperties = () => {
      if (scope.incomingProperties && scope.incomingProperties.length) {
        let filter = scope.propertyFilter.toLowerCase();
        scope.properties = scope.incomingProperties.filter(p =>
          p.propertyDesignation.toLowerCase().includes(filter) ||
          p.municipality.toLowerCase().includes(filter));
        scope.allPropertiesSelected = false;
      } else {
        scope.propertiesLoading = true;
        scope.fGetProperties()({filter: scope.propertyFilter, counterpartFilter: scope.counterpartFilter})
          .$promise.then(response => {
            let myProperties = response.rows;
            /*if (scope.jointProperties) {
              let incomingProperiesExceptMine = scope.incomingProperties
                .filter(r => !myProperties.find(m =>
                  m.propertyDesigation+m.municipality === r.propertyDesigation+r.municipality));
              scope.properties = myProperties.concat(incomingProperiesExceptMine);
            } else {
              scope.properties = myProperties;
            }*/
            scope.properties = myProperties;
            scope.focusOnSingleProperty();
            scope.tickedProperties = {};
            scope.allPropertiesSelected = false;
          }).catch(ErrorHandler('Misslyckades att hämta fastigheter.'))
          .finally(() => { scope.propertiesLoading = false; });
      }
    };
    scope.getApartments = () => {
      scope.apartmentsLoading = true;
      let args = Object.assign({ selectedProperty: scope.selectedProperty }, scope.apartmentsArguments);
      scope.fGetApartments()(args)
        .$promise.then(response => { scope.apartments = response.rows })
        .catch(ErrorHandler('Misslyckades att hämta lägenheter.'))
        .finally(() => {
          scope.apartmentsLoading = false;
          $timeout(() => {
            scope.tickedApartments = {};
            scope.allApartmentsSelected = true;
            scope.toggleAllApartments();
          }, 555);
        });
    };

    if (scope.incomingProperties && scope.incomingProperties.length) {
      scope.properties = scope.incomingProperties;
      scope.focusOnSingleProperty();
    } else {
      scope.getProperties(); //le mikre zey endra sej
    }

    scope.$on("refreshProperties", function (event, args) {
      scope.getProperties();
    });
    scope.$on("refreshApartments", function (event, args) {
      scope.getApartments();
    });
  }
}
})();
(function () {
'use strict';

hyArrayDisplay.$inject = ["$sce"];
angular.module('hyresgastApp').directive('hyArrayDisplay', hyArrayDisplay);

function hyArrayDisplay($sce) {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/array-display/array-display.html',
    scope: {
      model: '=',
    },
    // eslint-disable-next-line no-unused-vars
    link: (scope, element, attrs) => {
      const maxRows = 18;
      // to avoid overflow issues
      const maxDisplayItems = 120;
      
      function insertSoftHyphens(text, length) {
        return text.replace(new RegExp(`(.{${length}})`, 'g'), '$1&shy;');
      }
      
      let split = newValue => {
        if (!(newValue instanceof Array)) {
          newValue = [newValue];
        }

        scope.first = newValue[0];
        scope.elseCount = newValue.length - 1;
        scope.rest = '';

        if (scope.elseCount < 1) {
          return;
        }

        const wordMaxLengthBeforeHyphen = 24;
        const totalItems = newValue.length;
        let omittedCount = 0;
        
        const restItemsCount = Math.min(maxDisplayItems, totalItems) - 1;
        omittedCount = totalItems - 1 - restItemsCount;
  
        scope.columnCount = Math.min(5, Math.ceil((restItemsCount) / maxRows));

        let restHtml = '';
        if (restItemsCount > 0) {
          const displayedItemsCount = restItemsCount + 1
          if (omittedCount > 0) {
            restHtml += `<div style="margin-bottom: 0.25rem"><strong>Visar ${displayedItemsCount} av ${totalItems - 1}</strong></div>`;
          }
          restHtml += `<div style='column-count: ${scope.columnCount}; text-align: left;'>`;
  
          for (let i = 1; i <= displayedItemsCount; i++) {
            let style = '';

            if(newValue[i]){
              if (newValue[i].length <= wordMaxLengthBeforeHyphen) {
                style = 'white-space: nowrap;';
              }else{              
                newValue[i] = insertSoftHyphens(newValue[i], wordMaxLengthBeforeHyphen);
                style = `
                overflow-wrap: break-word;
                word-wrap: break-word;
                `;
              }
              restHtml += `<span style="${style}">${newValue[i]}</span><br/>`;
            }
          }

          restHtml += '</div>';
  
        }
        scope.rest = $sce.trustAsHtml(restHtml);
      };

      scope.$watch('model', newValue => {
        if (newValue) {
          split(newValue);
        }
      });
    },
  };
  return directive;


}
})();
(function () {
'use strict';

// @desc check if a session already been established on app load
// @example <body hy-auth-heartbeat></body>

authHeartbeat.$inject = ["Auth"];
angular.module('httpAuthHeartbeat', []).directive('hyAuthHeartbeat', authHeartbeat);

function authHeartbeat(Auth) {
  var directive = {
    restrict: 'A',
    link: link
  };

  return directive;

  function link(scope, element, attrs) {
    var ignoreAuthModule = true; // don't show login screen if a 401 is returned
    Auth.heartbeat(ignoreAuthModule).then(function() {}, function() {});
  }
}

})();
(function () {
'use strict';

httpBuffer.$inject = ["$injector"];
angular.module('httpAuthBuffer', []).factory('httpBuffer', httpBuffer);

function httpBuffer($injector) {
  // Holds all the requests, so they can be re-requested in future.
  var buffer = [];

  // Service initialized later because of circular dependency problem.
  var $http;

  var service = {
    append: append,
    retryAll: retryAll,
    rejectAll: rejectAll
  };

  return service;

  ////////////

  // Appends HTTP request configuration object with deferred response
  // attached to buffer.
  //   @return {Number} The new length of the buffer.
  function append(config, deferred) {
    return buffer.push({
      config: config,
      deferred: deferred
    });
  }

  // Retries all the buffered requests clears the buffer.
  function retryAll(updater) {
    for (var i = 0; i < buffer.length; ++i) {
      var _cfg = updater(buffer[i].config);
      if (_cfg !== false) {
        retryHttpRequest(_cfg, buffer[i].deferred);
      }
    }
    buffer = [];
  }

  // Abandon or reject (if reason provided) all the buffered requests.
  function rejectAll(reason) {
    if (reason) {
      for (var i = 0; i < buffer.length; ++i) {
        buffer[i].deferred.reject(reason);
      }
    }
    buffer = [];
  }

  function retryHttpRequest(config, deferred) {
    function successCallback(response) {
      deferred.resolve(response);
    }
    function errorCallback(response) {
      deferred.reject(response);
    }

    $http = $http || $injector.get('$http');
    $http(config).then(successCallback, errorCallback);
  }
}

})();
(function () {
'use strict';

Auth.$inject = ["$q", "Session", "httpBuffer", "Role", "Parties"];
angular
  .module('httpAuth', [
    'httpAuthInterceptor',
    'httpAuthBuffer',
    'httpAuthSession',
    'httpAuthHeartbeat',
    'httpRole',
    'httpRoleLookup'
  ])
  .factory('Auth', Auth);

function Auth($q, Session, httpBuffer, 
  Role, Parties) {
  var user;
  var service = {
    create: create,
    destroy: destroy,
    cancel: cancel,
    setCurrentUser: set,
    resetCurrentUser: reset,
    currentUser: show,
    heartbeat: heartbeat,
    isAdmin: isAdmin,
    isSpectator: isSpectator,
    canDo: canDo,
    isHgfUser: isHgfUser,
    isDelegateUser: isDelegateUser,
    isExternalUser: isExternalUser,
    getName: getCurrentUserName,
    userHasPermissionForView: userHasPermissionForView
  };

  return service;

  ////////////

  function create(credentials) {
    return Session.create(credentials).then(function(data) {
      retryDeferredRequests(); // TODO - probably add a CSRF-token to buffered request header
      user = data;
      return user;
    });
  }

  function set(userData) {
    user = userData;
    return user;
  }

  function reset() {
    user = null;
  }

  // Call this function to indicate that authentication should not proceed.
  // All deferred requests will be abandoned or rejected (if reason is provided).
  // @param
  //    reason - if provided, the requests are rejected; abandoned otherwise.
  function cancel(reason) {
    httpBuffer.rejectAll(reason);
  }

  function show() {
    return user;
  }

  function isAdmin() {
    return user && user.role === Role.ADMIN;
  }

  function isSpectator() {
    return user && user.role === Role.SPECTATOR;
  }

  function canDo(actionKey) {
    return !!(user && user.actionsAllowed && user.actionsAllowed.includes(actionKey));
  }

  function isHgfUser() {
    return user && user.party === Parties.HGF;
  }

  function isExternalUser() {
    return user && user.party === Parties.EXTERNAL;
  }

  function isDelegateUser() {
    return user && user.party === Parties.DELEGATE;
  }

  function destroy() {
    return Session.destroy().then(function() {
      user = null;
      return user;
    });
  }

  function userHasPermissionForView(permissions) {
    if(!user) {
      return false;
    }
    if(!permissions || permissions.length === 0) {
      return true;
    }
    var found = false;
    angular.forEach(permissions, function(permission) {
      if (user.party === permission) {
        found = true;
        return;
      }
    });
    return found;
  }

  function heartbeat(ignoreAuth) {
    if (user) {
      // less requests to API
      return $q.resolve();
    } else {
      return Session.show(ignoreAuth).then(
        function(data) {
          user = data;
          return user;
        },
        function() {
          // i.e session timed out
          user = null;
          return $q.reject('No session');
        }
      );
    }
  }

  function getCurrentUserName() {
    if (user === null || typeof user === 'undefined') {
      return '';
    }

    return user.firstname + ' ' + user.lastname;
  }

  // Called on successfull authentication and triggers a retry of all deferred
  // requests.
  // @param
  //      configUpdater - an optional transformation function that can modify the
  //      requests that are retried after having logged in.  This can be used for
  //      example to add an authentication token. It must return the request.
  function retryDeferredRequests(configUpdater) {
    var updater =
      configUpdater ||
      function(config) {
        return config;
      };
    httpBuffer.retryAll(updater);
  }
}

})();
(function () {
'use strict';

// @desc handle roles

angular.module('httpRoleLookup', []).value('RoleLookup', [
  { key: 'USER', value: 100 }, // TODO - remove duplication (see RoleSvc)
  { key: 'ADMIN', value: 200 }
]);

})();
(function () {
'use strict';

// @desc handle roles

angular.module('httpRole', []).value('Role', {
  SPECTATOR: 50,
  USER: 100,
  ADMIN: 200
});

})();
(function () {
'use strict';

// @desc
//   Breadcrumb navigation is configuered through app.js with a title property.
//   Example from app.js: $routeProvider.when('/negotiations', { title: 'Alla Förhandlingar', ... })
// @example
//   <hy-breadcrumb></hy-breadcrumb>
//   <hy-breadcrumb hide-id></hy-breadcrumb> - doesn't show id (numbers) in breadcrumb's link

breadcrumb.$inject = ["$rootScope", "$routeParams", "$route", "$location"];
angular.module('breadcrumb', []).directive('hyBreadcrumb', breadcrumb);

function breadcrumb($rootScope, $routeParams, $route, $location) {
  var directive = {
    scope: {},
    restrict: 'E',
    templateUrl: 'components/breadcrumb/breadcrumb.html',
    link: link
  };
  return directive;

  function link(scope, element, attrs) {
    var urlPath = $location.path();
    urlPath = urlPath.split('/');
    urlPath.splice(0, 1); // remove first element from url which is ""
    var urlPart = '';
    var breadcrumb = [];
    scope.breadcrumb = breadcrumb;

    angular.forEach(urlPath, function(item) {
      urlPart += '/' + item;
      angular.forEach($route.routes, function(route, key) {
        if (isMatchingRoute(key, urlPart)) {
          // check if there is a number after the current urlPart
          var id = urlPart.match(/\/(\d+)$/);
          if (id) {
            id = ' ' + id[1];
          } else {
            id = '';
          }
          // breadcrumb.push({ title: route.title + id, path: urlPart }); // USING ID
          breadcrumb.push({ title: route.title, path: urlPart });
        }
      });
    });

    //////////

    function isMatchingRoute(route, urlPart) {
      route = route.replace(/:\w+/g, '0');
      urlPart = urlPart.replace(/\d+/g, '0');
      return route === urlPart;
    }
  }
}

})();
(function () {
'use strict';

/* jshint -W018 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

angular
  .module('uiCollapse', [])

  .directive('uibCollapse', ["$animate", "$q", "$parse", "$injector", function($animate, $q, $parse, $injector) {
    var $animateCss = $injector.has('$animateCss') ? $injector.get('$animateCss') : null;
    return {
      link: function(scope, element, attrs) {
        var expandingExpr = $parse(attrs.expanding),
          expandedExpr = $parse(attrs.expanded),
          collapsingExpr = $parse(attrs.collapsing),
          collapsedExpr = $parse(attrs.collapsed),
          horizontal = false,
          css = {},
          cssTo = {};

        init();

        function init() {
          horizontal = !!('horizontal' in attrs);
          if (horizontal) {
            css = {
              width: ''
            };
            cssTo = { width: '0' };
          } else {
            css = {
              height: ''
            };
            cssTo = { height: '0' };
          }
          if (!scope.$eval(attrs.uibCollapse)) {
            element
              .addClass('in')
              .addClass('collapse')
              .attr('aria-expanded', true)
              .attr('aria-hidden', false)
              .css(css);
          }
        }

        function getScrollFromElement(element) {
          if (horizontal) {
            return { width: element.scrollWidth + 'px' };
          }
          return { height: element.scrollHeight + 'px' };
        }

        function expand() {
          if (element.hasClass('collapse') && element.hasClass('in')) {
            return;
          }

          $q.resolve(expandingExpr(scope)).then(function() {
            element
              .removeClass('collapse')
              .addClass('collapsing')
              .attr('aria-expanded', true)
              .attr('aria-hidden', false);

            if ($animateCss) {
              $animateCss(element, {
                addClass: 'in',
                easing: 'ease',
                css: {
                  overflow: 'hidden'
                },
                to: getScrollFromElement(element[0])
              })
                .start()
                ['finally'](expandDone);
            } else {
              $animate
                .addClass(element, 'in', {
                  css: {
                    overflow: 'hidden'
                  },
                  to: getScrollFromElement(element[0])
                })
                .then(expandDone);
            }
          }, angular.noop);
        }

        function expandDone() {
          element
            .removeClass('collapsing')
            .addClass('collapse')
            .css(css);
          expandedExpr(scope);
        }

        function collapse() {
          if (!element.hasClass('collapse') && !element.hasClass('in')) {
            return collapseDone();
          }

          $q.resolve(collapsingExpr(scope)).then(function() {
            element
              // IMPORTANT: The width must be set before adding "collapsing" class.
              // Otherwise, the browser attempts to animate from width 0 (in
              // collapsing class) to the given width here.
              .css(getScrollFromElement(element[0]))
              // initially all panel collapse have the collapse class, this removal
              // prevents the animation from jumping to collapsed state
              .removeClass('collapse')
              .addClass('collapsing')
              .attr('aria-expanded', false)
              .attr('aria-hidden', true);

            if ($animateCss) {
              $animateCss(element, {
                removeClass: 'in',
                to: cssTo
              })
                .start()
                ['finally'](collapseDone);
            } else {
              $animate
                .removeClass(element, 'in', {
                  to: cssTo
                })
                .then(collapseDone);
            }
          }, angular.noop);
        }

        function collapseDone() {
          element.css(cssTo); // Required so that collapse works when animation is disabled
          element.removeClass('collapsing').addClass('collapse');
          collapsedExpr(scope);
        }

        scope.$watch(attrs.uibCollapse, function(shouldCollapse) {
          if (shouldCollapse) {
            collapse();
          } else {
            expand();
          }
        });
      }
    };
  }]);

/* jshint +W018 */
/* jshint +W071 */
/* jshint +W101 */
/* jshint +W106 */

})();
(function () {
'use strict';

angular.module('checkbox', [])
  .directive('hyCheckbox', () => {
    return {
      scope: {
        name: '@',
        title: '@',
        model: '=',
        ticked: '&',
        changed: '&',
        disabled: '=',
      },
      restrict: 'E',
      templateUrl: 'components/checkbox/checkbox.html',
    };
  });
})();
(function () {
/* eslint-disable no-undefined */
'use strict';

angular.module('hyresgastApp').directive('hyCombobox', combobox);

function combobox() {
  var directive = {
    scope: {
      items: '<',
      textProperty: '@',
      prompt: '@',
      stickyPrompt: '<',
      currentItemChanged: '&',
      currentItem: '=?',
    },
    restrict: 'E',
    templateUrl: 'components/combobox/combobox.html',
    link: link
  };

  return directive;

  function link(scope) {
    scope.selectedItem = scope.currentItem;
    scope.$watch('currentItem', function(newVal, oldVal) {
      if (newVal !== oldVal) {
        scope.selectedItem = newVal;
      }
    });
    scope.selectItem = function(item) {
      scope.selectedItem = item;
      scope.currentItemChanged({selection: item});
      scope.currentItem = item;
    }
  }
}
})();
(function () {
angular.module('hyresgastApp').directive('hyCompareFormFields', compareFormFields);

function compareFormFields() {
  var directive = {
    require: 'ngModel',
    link: link,
    scope: {
      secondModelValue: '=hyCompareFormFields'
    }
  };
  return directive;

  function link(scope, element, attrs, ngModel) {
    ngModel.$validators.compareFormFields = function(modelValue) {
      return modelValue === scope.secondModelValue;
    };

    scope.$watch('secondModelValue', function() {
      ngModel.$validate();
    });
  }
}

})();
(function () {
'use strict';

/* jshint -W018 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

angular
  .module('uiDateParser', [])

  .service('uibDateParser', ["$log", "$locale", "dateFilter", "orderByFilter", "filterFilter", function($log, $locale, dateFilter, orderByFilter, filterFilter) {
    // Pulled from https://github.com/mbostock/d3/blob/master/src/format/requote.js
    var SPECIAL_CHARACTERS_REGEXP = /[\\\^\$\*\+\?\|\[\]\(\)\.\{\}]/g;

    var localeId;
    var formatCodeToRegex;

    this.init = function() {
      localeId = $locale.id;

      this.parsers = {};
      this.formatters = {};

      formatCodeToRegex = [
        {
          key: 'yyyy',
          regex: '\\d{4}',
          apply: function(value) {
            this.year = +value;
          },
          formatter: function(date) {
            var _date = new Date();
            _date.setFullYear(Math.abs(date.getFullYear()));
            return dateFilter(_date, 'yyyy');
          }
        },
        {
          key: 'yy',
          regex: '\\d{2}',
          apply: function(value) {
            value = +value;
            this.year = value < 69 ? value + 2000 : value + 1900;
          },
          formatter: function(date) {
            var _date = new Date();
            _date.setFullYear(Math.abs(date.getFullYear()));
            return dateFilter(_date, 'yy');
          }
        },
        {
          key: 'y',
          regex: '\\d{1,4}',
          apply: function(value) {
            this.year = +value;
          },
          formatter: function(date) {
            var _date = new Date();
            _date.setFullYear(Math.abs(date.getFullYear()));
            return dateFilter(_date, 'y');
          }
        },
        {
          key: 'M!',
          regex: '0?[1-9]|1[0-2]',
          apply: function(value) {
            this.month = value - 1;
          },
          formatter: function(date) {
            var value = date.getMonth();
            if (/^[0-9]$/.test(value)) {
              return dateFilter(date, 'MM');
            }

            return dateFilter(date, 'M');
          }
        },
        {
          key: 'MMMM',
          regex: $locale.DATETIME_FORMATS.MONTH.join('|'),
          apply: function(value) {
            this.month = $locale.DATETIME_FORMATS.MONTH.indexOf(value);
          },
          formatter: function(date) {
            return dateFilter(date, 'MMMM');
          }
        },
        {
          key: 'MMM',
          regex: $locale.DATETIME_FORMATS.SHORTMONTH.join('|'),
          apply: function(value) {
            this.month = $locale.DATETIME_FORMATS.SHORTMONTH.indexOf(value);
          },
          formatter: function(date) {
            return dateFilter(date, 'MMM');
          }
        },
        {
          key: 'MM',
          regex: '0[1-9]|1[0-2]',
          apply: function(value) {
            this.month = value - 1;
          },
          formatter: function(date) {
            return dateFilter(date, 'MM');
          }
        },
        {
          key: 'M',
          regex: '[1-9]|1[0-2]',
          apply: function(value) {
            this.month = value - 1;
          },
          formatter: function(date) {
            return dateFilter(date, 'M');
          }
        },
        {
          key: 'd!',
          regex: '[0-2]?[0-9]{1}|3[0-1]{1}',
          apply: function(value) {
            this.date = +value;
          },
          formatter: function(date) {
            var value = date.getDate();
            if (/^[1-9]$/.test(value)) {
              return dateFilter(date, 'dd');
            }

            return dateFilter(date, 'd');
          }
        },
        {
          key: 'dd',
          regex: '[0-2][0-9]{1}|3[0-1]{1}',
          apply: function(value) {
            this.date = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'dd');
          }
        },
        {
          key: 'd',
          regex: '[1-2]?[0-9]{1}|3[0-1]{1}',
          apply: function(value) {
            this.date = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'd');
          }
        },
        {
          key: 'EEEE',
          regex: $locale.DATETIME_FORMATS.DAY.join('|'),
          formatter: function(date) {
            return dateFilter(date, 'EEEE');
          }
        },
        {
          key: 'EEE',
          regex: $locale.DATETIME_FORMATS.SHORTDAY.join('|'),
          formatter: function(date) {
            return dateFilter(date, 'EEE');
          }
        },
        {
          key: 'HH',
          regex: '(?:0|1)[0-9]|2[0-3]',
          apply: function(value) {
            this.hours = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'HH');
          }
        },
        {
          key: 'hh',
          regex: '0[0-9]|1[0-2]',
          apply: function(value) {
            this.hours = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'hh');
          }
        },
        {
          key: 'H',
          regex: '1?[0-9]|2[0-3]',
          apply: function(value) {
            this.hours = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'H');
          }
        },
        {
          key: 'h',
          regex: '[0-9]|1[0-2]',
          apply: function(value) {
            this.hours = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'h');
          }
        },
        {
          key: 'mm',
          regex: '[0-5][0-9]',
          apply: function(value) {
            this.minutes = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'mm');
          }
        },
        {
          key: 'm',
          regex: '[0-9]|[1-5][0-9]',
          apply: function(value) {
            this.minutes = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'm');
          }
        },
        {
          key: 'sss',
          regex: '[0-9][0-9][0-9]',
          apply: function(value) {
            this.milliseconds = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'sss');
          }
        },
        {
          key: 'ss',
          regex: '[0-5][0-9]',
          apply: function(value) {
            this.seconds = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 'ss');
          }
        },
        {
          key: 's',
          regex: '[0-9]|[1-5][0-9]',
          apply: function(value) {
            this.seconds = +value;
          },
          formatter: function(date) {
            return dateFilter(date, 's');
          }
        },
        {
          key: 'a',
          regex: $locale.DATETIME_FORMATS.AMPMS.join('|'),
          apply: function(value) {
            if (this.hours === 12) {
              this.hours = 0;
            }

            if (value === 'PM') {
              this.hours += 12;
            }
          },
          formatter: function(date) {
            return dateFilter(date, 'a');
          }
        },
        {
          key: 'Z',
          regex: '[+-]\\d{4}',
          apply: function(value) {
            var matches = value.match(/([+-])(\d{2})(\d{2})/),
              sign = matches[1],
              hours = matches[2],
              minutes = matches[3];
            this.hours += toInt(sign + hours);
            this.minutes += toInt(sign + minutes);
          },
          formatter: function(date) {
            return dateFilter(date, 'Z');
          }
        },
        {
          key: 'ww',
          regex: '[0-4][0-9]|5[0-3]',
          formatter: function(date) {
            return dateFilter(date, 'ww');
          }
        },
        {
          key: 'w',
          regex: '[0-9]|[1-4][0-9]|5[0-3]',
          formatter: function(date) {
            return dateFilter(date, 'w');
          }
        },
        {
          key: 'GGGG',
          regex: $locale.DATETIME_FORMATS.ERANAMES.join('|').replace(/\s/g, '\\s'),
          formatter: function(date) {
            return dateFilter(date, 'GGGG');
          }
        },
        {
          key: 'GGG',
          regex: $locale.DATETIME_FORMATS.ERAS.join('|'),
          formatter: function(date) {
            return dateFilter(date, 'GGG');
          }
        },
        {
          key: 'GG',
          regex: $locale.DATETIME_FORMATS.ERAS.join('|'),
          formatter: function(date) {
            return dateFilter(date, 'GG');
          }
        },
        {
          key: 'G',
          regex: $locale.DATETIME_FORMATS.ERAS.join('|'),
          formatter: function(date) {
            return dateFilter(date, 'G');
          }
        }
      ];

      if (angular.version.major >= 1 && angular.version.minor > 4) {
        formatCodeToRegex.push({
          key: 'LLLL',
          regex: $locale.DATETIME_FORMATS.STANDALONEMONTH.join('|'),
          apply: function(value) {
            this.month = $locale.DATETIME_FORMATS.STANDALONEMONTH.indexOf(value);
          },
          formatter: function(date) {
            return dateFilter(date, 'LLLL');
          }
        });
      }
    };

    this.init();

    function getFormatCodeToRegex(key) {
      return filterFilter(formatCodeToRegex, { key: key }, true)[0];
    }

    this.getParser = function(key) {
      var f = getFormatCodeToRegex(key);
      return (f && f.apply) || null;
    };

    this.overrideParser = function(key, parser) {
      var f = getFormatCodeToRegex(key);
      if (f && angular.isFunction(parser)) {
        this.parsers = {};
        f.apply = parser;
      }
    }.bind(this);

    function createParser(format) {
      var map = [],
        regex = format.split('');

      // check for literal values
      var quoteIndex = format.indexOf("'");
      if (quoteIndex > -1) {
        var inLiteral = false;
        format = format.split('');
        for (var i = quoteIndex; i < format.length; i++) {
          if (inLiteral) {
            if (format[i] === "'") {
              if (i + 1 < format.length && format[i + 1] === "'") {
                // escaped single quote
                format[i + 1] = '$';
                regex[i + 1] = '';
              } else {
                // end of literal
                regex[i] = '';
                inLiteral = false;
              }
            }
            format[i] = '$';
          } else {
            if (format[i] === "'") {
              // start of literal
              format[i] = '$';
              regex[i] = '';
              inLiteral = true;
            }
          }
        }

        format = format.join('');
      }

      angular.forEach(formatCodeToRegex, function(data) {
        var index = format.indexOf(data.key);

        if (index > -1) {
          format = format.split('');

          regex[index] = '(' + data.regex + ')';
          format[index] = '$'; // Custom symbol to define consumed part of format
          for (var i = index + 1, n = index + data.key.length; i < n; i++) {
            regex[i] = '';
            format[i] = '$';
          }
          format = format.join('');

          map.push({
            index: index,
            key: data.key,
            apply: data.apply,
            matcher: data.regex
          });
        }
      });

      return {
        regex: new RegExp('^' + regex.join('') + '$'),
        map: orderByFilter(map, 'index')
      };
    }

    function createFormatter(format) {
      var formatters = [];
      var i = 0;
      var formatter, literalIdx;
      while (i < format.length) {
        if (angular.isNumber(literalIdx)) {
          if (format.charAt(i) === "'") {
            if (i + 1 >= format.length || format.charAt(i + 1) !== "'") {
              formatters.push(constructLiteralFormatter(format, literalIdx, i));
              literalIdx = null;
            }
          } else if (i === format.length) {
            while (literalIdx < format.length) {
              formatter = constructFormatterFromIdx(format, literalIdx);
              formatters.push(formatter);
              literalIdx = formatter.endIdx;
            }
          }

          i++;
          continue;
        }

        if (format.charAt(i) === "'") {
          literalIdx = i;
          i++;
          continue;
        }

        formatter = constructFormatterFromIdx(format, i);

        formatters.push(formatter.parser);
        i = formatter.endIdx;
      }

      return formatters;
    }

    function constructLiteralFormatter(format, literalIdx, endIdx) {
      return function() {
        return format.substr(literalIdx + 1, endIdx - literalIdx - 1);
      };
    }

    function constructFormatterFromIdx(format, i) {
      var currentPosStr = format.substr(i);
      for (var j = 0; j < formatCodeToRegex.length; j++) {
        if (new RegExp('^' + formatCodeToRegex[j].key).test(currentPosStr)) {
          var data = formatCodeToRegex[j];
          return {
            endIdx: i + data.key.length,
            parser: data.formatter
          };
        }
      }

      return {
        endIdx: i + 1,
        parser: function() {
          return currentPosStr.charAt(0);
        }
      };
    }

    this.filter = function(date, format) {
      if (!angular.isDate(date) || isNaN(date) || !format) {
        return '';
      }

      format = $locale.DATETIME_FORMATS[format] || format;

      if ($locale.id !== localeId) {
        this.init();
      }

      if (!this.formatters[format]) {
        this.formatters[format] = createFormatter(format);
      }

      var formatters = this.formatters[format];

      return formatters.reduce(function(str, formatter) {
        return str + formatter(date);
      }, '');
    };

    this.parse = function(input, format, baseDate) {
      if (!angular.isString(input) || !format) {
        return input;
      }

      format = $locale.DATETIME_FORMATS[format] || format;
      format = format.replace(SPECIAL_CHARACTERS_REGEXP, '\\$&');

      if ($locale.id !== localeId) {
        this.init();
      }

      if (!this.parsers[format]) {
        this.parsers[format] = createParser(format, 'apply');
      }

      var parser = this.parsers[format],
        regex = parser.regex,
        map = parser.map,
        results = input.match(regex),
        tzOffset = false;
      if (results && results.length) {
        var fields, dt;
        if (angular.isDate(baseDate) && !isNaN(baseDate.getTime())) {
          fields = {
            year: baseDate.getFullYear(),
            month: baseDate.getMonth(),
            date: baseDate.getDate(),
            hours: baseDate.getHours(),
            minutes: baseDate.getMinutes(),
            seconds: baseDate.getSeconds(),
            milliseconds: baseDate.getMilliseconds()
          };
        } else {
          if (baseDate) {
            $log.warn('dateparser:', 'baseDate is not a valid date');
          }
          fields = { year: 1900, month: 0, date: 1, hours: 0, minutes: 0, seconds: 0, milliseconds: 0 };
        }

        for (var i = 1, n = results.length; i < n; i++) {
          var mapper = map[i - 1];
          if (mapper.matcher === 'Z') {
            tzOffset = true;
          }

          if (mapper.apply) {
            mapper.apply.call(fields, results[i]);
          }
        }

        var datesetter = tzOffset ? Date.prototype.setUTCFullYear : Date.prototype.setFullYear;
        var timesetter = tzOffset ? Date.prototype.setUTCHours : Date.prototype.setHours;

        if (isValid(fields.year, fields.month, fields.date)) {
          if (angular.isDate(baseDate) && !isNaN(baseDate.getTime()) && !tzOffset) {
            dt = new Date(baseDate);
            datesetter.call(dt, fields.year, fields.month, fields.date);
            timesetter.call(dt, fields.hours, fields.minutes, fields.seconds, fields.milliseconds);
          } else {
            dt = new Date(0);
            datesetter.call(dt, fields.year, fields.month, fields.date);
            timesetter.call(dt, fields.hours || 0, fields.minutes || 0, fields.seconds || 0, fields.milliseconds || 0);
          }
        }

        return dt;
      }
    };

    // Check if date is valid for specific month (and year for February).
    // Month: 0 = Jan, 1 = Feb, etc
    function isValid(year, month, date) {
      if (date < 1) {
        return false;
      }

      if (month === 1 && date > 28) {
        return date === 29 && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0);
      }

      if (month === 3 || month === 5 || month === 8 || month === 10) {
        return date < 31;
      }

      return true;
    }

    function toInt(str) {
      return parseInt(str, 10);
    }

    this.toTimezone = toTimezone;
    this.fromTimezone = fromTimezone;
    this.timezoneToOffset = timezoneToOffset;
    this.addDateMinutes = addDateMinutes;
    this.convertTimezoneToLocal = convertTimezoneToLocal;

    function toTimezone(date, timezone) {
      return date && timezone ? convertTimezoneToLocal(date, timezone) : date;
    }

    function fromTimezone(date, timezone) {
      return date && timezone ? convertTimezoneToLocal(date, timezone, true) : date;
    }

    //https://github.com/angular/angular.js/blob/622c42169699ec07fc6daaa19fe6d224e5d2f70e/src/Angular.js#L1207
    function timezoneToOffset(timezone, fallback) {
      timezone = timezone.replace(/:/g, '');
      var requestedTimezoneOffset = Date.parse('Jan 01, 1970 00:00:00 ' + timezone) / 60000;
      return isNaN(requestedTimezoneOffset) ? fallback : requestedTimezoneOffset;
    }

    function addDateMinutes(date, minutes) {
      date = new Date(date.getTime());
      date.setMinutes(date.getMinutes() + minutes);
      return date;
    }

    function convertTimezoneToLocal(date, timezone, reverse) {
      reverse = reverse ? -1 : 1;
      var dateTimezoneOffset = date.getTimezoneOffset();
      var timezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
      return addDateMinutes(date, reverse * (timezoneOffset - dateTimezoneOffset));
    }
  }]);

/* jshint +W018 */
/* jshint +W071 */
/* jshint +W101 */
/* jshint +W106 */

})();
(function () {
'use strict';

angular.module('dateLocal', []).factory('DateLocal', DateLocal);

function DateLocal() {
  var service = {
    fromUTCtoLocal: fromUTCtoLocal
  };

  return service;

  ////////////

  // Remove timezone specific part of a datetime object.
  // Example: The timedate and timezone values will normally be submitted to
  // the server as separate data points, and then the server needs
  // to store them appropriately in the database.
  function fromUTCtoLocal(date) {
    if (date === null || date === undefined) {
      return null;
    }

    var ten = function(i) {
        return (i < 10 ? '0' : '') + i;
      },
      YYYY = date.getFullYear(),
      MM = ten(date.getMonth() + 1),
      DD = ten(date.getDate()),
      HH = ten(date.getHours()),
      II = ten(date.getMinutes()),
      SS = ten(date.getSeconds());

    return YYYY + '-' + MM + '-' + DD + 'T' + HH + ':' + II + ':' + SS;
  }
}

})();
(function () {
'use strict';

/* jshint -W018 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

angular
  .module('uiDatepickerPopup', ['uiDatepicker', 'uiPosition'])

  .value('$datepickerPopupLiteralWarning', true)

  .constant('uibDatepickerPopupConfig', {
    altInputFormats: [],
    appendToBody: false,
    clearText: 'Clear',
    closeOnDateSelection: true,
    closeText: 'Done',
    currentText: 'Today',
    datepickerPopup: 'yyyy-MM-dd',
    datepickerPopupTemplateUrl: 'components/datepicker-popup/popup.html',
    datepickerTemplateUrl: 'components/datepicker/datepicker.html',
    html5Types: {
      date: 'yyyy-MM-dd',
      'datetime-local': 'yyyy-MM-ddTHH:mm:ss.sss',
      month: 'yyyy-MM'
    },
    onOpenFocus: true,
    showButtonBar: true,
    placement: 'auto bottom-right'
  })

  .controller('UibDatepickerPopupController', ["$scope", "$element", "$attrs", "$compile", "$log", "$parse", "$window", "$document", "$rootScope", "$uibPosition", "dateFilter", "uibDateParser", "uibDatepickerPopupConfig", "$timeout", "uibDatepickerConfig", "$datepickerPopupLiteralWarning", function(
    $scope,
    $element,
    $attrs,
    $compile,
    $log,
    $parse,
    $window,
    $document,
    $rootScope,
    $uibPosition,
    dateFilter,
    uibDateParser,
    uibDatepickerPopupConfig,
    $timeout,
    uibDatepickerConfig,
    $datepickerPopupLiteralWarning
  ) {
    var cache = {},
      isHtml5DateInput = false;
    var dateFormat,
      closeOnDateSelection,
      appendToBody,
      onOpenFocus,
      datepickerPopupTemplateUrl,
      datepickerTemplateUrl,
      popupEl,
      datepickerEl,
      scrollParentEl,
      ngModel,
      ngModelOptions,
      $popup,
      altInputFormats,
      watchListeners = [];

    this.init = function(_ngModel_) {
      ngModel = _ngModel_;
      ngModelOptions = extractOptions(ngModel);
      closeOnDateSelection = angular.isDefined($attrs.closeOnDateSelection)
        ? $scope.$parent.$eval($attrs.closeOnDateSelection)
        : uibDatepickerPopupConfig.closeOnDateSelection;
      appendToBody = angular.isDefined($attrs.datepickerAppendToBody)
        ? $scope.$parent.$eval($attrs.datepickerAppendToBody)
        : uibDatepickerPopupConfig.appendToBody;
      onOpenFocus = angular.isDefined($attrs.onOpenFocus)
        ? $scope.$parent.$eval($attrs.onOpenFocus)
        : uibDatepickerPopupConfig.onOpenFocus;
      datepickerPopupTemplateUrl = angular.isDefined($attrs.datepickerPopupTemplateUrl)
        ? $attrs.datepickerPopupTemplateUrl
        : uibDatepickerPopupConfig.datepickerPopupTemplateUrl;
      datepickerTemplateUrl = angular.isDefined($attrs.datepickerTemplateUrl)
        ? $attrs.datepickerTemplateUrl
        : uibDatepickerPopupConfig.datepickerTemplateUrl;
      altInputFormats = angular.isDefined($attrs.altInputFormats)
        ? $scope.$parent.$eval($attrs.altInputFormats)
        : uibDatepickerPopupConfig.altInputFormats;

      $scope.showButtonBar = angular.isDefined($attrs.showButtonBar)
        ? $scope.$parent.$eval($attrs.showButtonBar)
        : uibDatepickerPopupConfig.showButtonBar;

      if (uibDatepickerPopupConfig.html5Types[$attrs.type]) {
        dateFormat = uibDatepickerPopupConfig.html5Types[$attrs.type];
        isHtml5DateInput = true;
      } else {
        dateFormat = $attrs.uibDatepickerPopup || uibDatepickerPopupConfig.datepickerPopup;
        $attrs.$observe('uibDatepickerPopup', function(value, oldValue) {
          var newDateFormat = value || uibDatepickerPopupConfig.datepickerPopup;
          // Invalidate the $modelValue to ensure that formatters re-run
          // FIXME: Refactor when PR is merged: https://github.com/angular/angular.js/pull/10764
          if (newDateFormat !== dateFormat) {
            dateFormat = newDateFormat;
            ngModel.$modelValue = null;

            if (!dateFormat) {
              throw new Error('uibDatepickerPopup must have a date format specified.');
            }
          }
        });
      }

      if (!dateFormat) {
        throw new Error('uibDatepickerPopup must have a date format specified.');
      }

      if (isHtml5DateInput && $attrs.uibDatepickerPopup) {
        throw new Error('HTML5 date input types do not support custom formats.');
      }

      // popup element used to display calendar
      popupEl = angular.element('<div uib-datepicker-popup-wrap><div uib-datepicker></div></div>');

      popupEl.attr({
        'ng-model': 'date',
        'ng-change': 'dateSelection(date)',
        'template-url': datepickerPopupTemplateUrl
      });

      // datepicker element
      datepickerEl = angular.element(popupEl.children()[0]);
      datepickerEl.attr('template-url', datepickerTemplateUrl);

      if (!$scope.datepickerOptions) {
        $scope.datepickerOptions = {};
      }

      if (isHtml5DateInput) {
        if ($attrs.type === 'month') {
          $scope.datepickerOptions.datepickerMode = 'month';
          $scope.datepickerOptions.minMode = 'month';
        }
      }

      datepickerEl.attr('datepicker-options', 'datepickerOptions');

      if (!isHtml5DateInput) {
        // Internal API to maintain the correct ng-invalid-[key] class
        ngModel.$$parserName = 'date';
        ngModel.$validators.date = validator;
        ngModel.$parsers.unshift(parseDate);
        ngModel.$formatters.push(function(value) {
          if (ngModel.$isEmpty(value)) {
            $scope.date = value;
            return value;
          }

          if (angular.isNumber(value)) {
            value = new Date(value);
          }

          $scope.date = uibDateParser.fromTimezone(value, ngModelOptions.getOption('timezone'));

          return uibDateParser.filter($scope.date, dateFormat);
        });
      } else {
        ngModel.$formatters.push(function(value) {
          $scope.date = uibDateParser.fromTimezone(value, ngModelOptions.getOption('timezone'));
          return value;
        });
      }

      // Detect changes in the view from the text box
      ngModel.$viewChangeListeners.push(function() {
        $scope.date = parseDateString(ngModel.$viewValue);
      });

      $element.on('keydown', inputKeydownBind);

      $popup = $compile(popupEl)($scope);
      // Prevent jQuery cache memory leak (template is now redundant after linking)
      popupEl.remove();

      if (appendToBody) {
        $document.find('body').append($popup);
      } else {
        $element.after($popup);
      }

      $scope.$on('$destroy', function() {
        if ($scope.isOpen === true) {
          if (!$rootScope.$$phase) {
            $scope.$apply(function() {
              $scope.isOpen = false;
            });
          }
        }

        $popup.remove();
        $element.off('keydown', inputKeydownBind);
        $document.off('click', documentClickBind);
        if (scrollParentEl) {
          scrollParentEl.off('scroll', positionPopup);
        }
        angular.element($window).off('resize', positionPopup);

        //Clear all watch listeners on destroy
        while (watchListeners.length) {
          watchListeners.shift()();
        }
      });
    };

    $scope.getText = function(key) {
      return $scope[key + 'Text'] || uibDatepickerPopupConfig[key + 'Text'];
    };

    $scope.isDisabled = function(date) {
      if (date === 'today') {
        date = uibDateParser.fromTimezone(new Date(), ngModelOptions.getOption('timezone'));
      }

      var dates = {};
      angular.forEach(['minDate', 'maxDate'], function(key) {
        if (!$scope.datepickerOptions[key]) {
          dates[key] = null;
        } else if (angular.isDate($scope.datepickerOptions[key])) {
          dates[key] = new Date($scope.datepickerOptions[key]);
        } else {
          if ($datepickerPopupLiteralWarning) {
            $log.warn('Literal date support has been deprecated, please switch to date object usage');
          }

          dates[key] = new Date(dateFilter($scope.datepickerOptions[key], 'medium'));
        }
      });

      return (
        ($scope.datepickerOptions && dates.minDate && $scope.compare(date, dates.minDate) < 0) ||
        (dates.maxDate && $scope.compare(date, dates.maxDate) > 0)
      );
    };

    $scope.compare = function(date1, date2) {
      return (
        new Date(date1.getFullYear(), date1.getMonth(), date1.getDate()) -
        new Date(date2.getFullYear(), date2.getMonth(), date2.getDate())
      );
    };

    // Inner change
    $scope.dateSelection = function(dt) {
      $scope.date = dt;
      var date = $scope.date ? uibDateParser.filter($scope.date, dateFormat) : null; // Setting to NULL is necessary for form validators to function
      $element.val(date);
      ngModel.$setViewValue(date);

      if (closeOnDateSelection) {
        $scope.isOpen = false;
        $element[0].focus();
      }
    };

    $scope.keydown = function(evt) {
      if (evt.which === 27) {
        evt.stopPropagation();
        $scope.isOpen = false;
        $element[0].focus();
      }
    };

    $scope.select = function(date, evt) {
      evt.stopPropagation();

      if (date === 'today') {
        var today = new Date();
        if (angular.isDate($scope.date)) {
          date = new Date($scope.date);
          date.setFullYear(today.getFullYear(), today.getMonth(), today.getDate());
        } else {
          date = uibDateParser.fromTimezone(today, ngModelOptions.getOption('timezone'));
          date.setHours(0, 0, 0, 0);
        }
      }
      $scope.dateSelection(date);
    };

    $scope.close = function(evt) {
      evt.stopPropagation();

      $scope.isOpen = false;
      $element[0].focus();
    };

    $scope.disabled = angular.isDefined($attrs.disabled) || false;
    if ($attrs.ngDisabled) {
      watchListeners.push(
        $scope.$parent.$watch($parse($attrs.ngDisabled), function(disabled) {
          $scope.disabled = disabled;
        })
      );
    }

    $scope.$watch('isOpen', function(value) {
      if (value) {
        if (!$scope.disabled) {
          $timeout(
            function() {
              positionPopup();

              if (onOpenFocus) {
                $scope.$broadcast('uib:datepicker.focus');
              }

              $document.on('click', documentClickBind);
              var placement = $attrs.popupPlacement ? $attrs.popupPlacement : uibDatepickerPopupConfig.placement;
              if (appendToBody || $uibPosition.parsePlacement(placement)[2]) {
                scrollParentEl = scrollParentEl || angular.element($uibPosition.scrollParent($element));
                if (scrollParentEl) {
                  scrollParentEl.on('scroll', positionPopup);
                }
              } else {
                scrollParentEl = null;
              }

              angular.element($window).on('resize', positionPopup);
            },
            100,
            false
          );
        } else {
          $scope.isOpen = false;
        }
      } else {
        $document.off('click', documentClickBind);
        if (scrollParentEl) {
          scrollParentEl.off('scroll', positionPopup);
        }
        angular.element($window).off('resize', positionPopup);
      }
    });

    function cameltoDash(string) {
      return string.replace(/([A-Z])/g, function($1) {
        return '-' + $1.toLowerCase();
      });
    }

    function parseDateString(viewValue) {
      var date = uibDateParser.parse(viewValue, dateFormat, $scope.date);
      if (isNaN(date)) {
        for (var i = 0; i < altInputFormats.length; i++) {
          date = uibDateParser.parse(viewValue, altInputFormats[i], $scope.date);
          if (!isNaN(date)) {
            return date;
          }
        }
      }
      return date;
    }

    function parseDate(viewValue) {
      if (angular.isNumber(viewValue)) {
        // presumably timestamp to date object
        viewValue = new Date(viewValue);
      }

      if (!viewValue) {
        return null;
      }

      if (angular.isDate(viewValue) && !isNaN(viewValue)) {
        return viewValue;
      }

      if (angular.isString(viewValue)) {
        var date = parseDateString(viewValue);
        if (!isNaN(date)) {
          return uibDateParser.toTimezone(date, ngModelOptions.getOption('timezone'));
        }
      }

      return ngModelOptions.getOption('allowInvalid') ? viewValue : undefined;
    }

    function validator(modelValue, viewValue) {
      var value = modelValue || viewValue;

      if (!$attrs.ngRequired && !value) {
        return true;
      }

      if (angular.isNumber(value)) {
        value = new Date(value);
      }

      if (!value) {
        return true;
      }

      if (angular.isDate(value) && !isNaN(value)) {
        return true;
      }

      if (angular.isString(value)) {
        return !isNaN(parseDateString(value));
      }

      return false;
    }

    function documentClickBind(event) {
      if (!$scope.isOpen && $scope.disabled) {
        return;
      }

      var popup = $popup[0];
      var dpContainsTarget = $element[0].contains(event.target);
      // The popup node may not be an element node
      // In some browsers (IE) only element nodes have the 'contains' function
      var popupContainsTarget = popup.contains !== undefined && popup.contains(event.target);
      if ($scope.isOpen && !(dpContainsTarget || popupContainsTarget)) {
        $scope.$apply(function() {
          $scope.isOpen = false;
        });
      }
    }

    function inputKeydownBind(evt) {
      if (evt.which === 27 && $scope.isOpen) {
        evt.preventDefault();
        evt.stopPropagation();
        $scope.$apply(function() {
          $scope.isOpen = false;
        });
        $element[0].focus();
      } else if (evt.which === 40 && !$scope.isOpen) {
        evt.preventDefault();
        evt.stopPropagation();
        $scope.$apply(function() {
          $scope.isOpen = true;
        });
      }
    }

    function positionPopup() {
      if ($scope.isOpen) {
        var dpElement = angular.element($popup[0].querySelector('.uib-datepicker-popup'));
        var placement = $attrs.popupPlacement ? $attrs.popupPlacement : uibDatepickerPopupConfig.placement;
        var position = $uibPosition.positionElements($element, dpElement, placement, appendToBody);
        dpElement.css({ top: position.top + 'px', left: position.left + 'px' });
        if (dpElement.hasClass('uib-position-measure')) {
          dpElement.removeClass('uib-position-measure');
        }
      }
    }

    function extractOptions(ngModelCtrl) {
      var ngModelOptions;

      if (angular.version.minor < 6) {
        // in angular < 1.6 $options could be missing
        // guarantee a value
        ngModelOptions = angular.isObject(ngModelCtrl.$options)
          ? ngModelCtrl.$options
          : {
            timezone: null
          };

        // mimic 1.6+ api
        ngModelOptions.getOption = function(key) {
          return ngModelOptions[key];
        };
      } else {
        // in angular >=1.6 $options is always present
        ngModelOptions = ngModelCtrl.$options;
      }

      return ngModelOptions;
    }

    $scope.$on('uib:datepicker.mode', function() {
      $timeout(positionPopup, 0, false);
    });
  }])

  .directive('uibDatepickerPopup', function() {
    return {
      require: ['ngModel', 'uibDatepickerPopup'],
      controller: 'UibDatepickerPopupController',
      scope: {
        datepickerOptions: '=?',
        isOpen: '=?',
        currentText: '@',
        clearText: '@',
        closeText: '@'
      },
      link: function(scope, element, attrs, ctrls) {
        var ngModel = ctrls[0],
          ctrl = ctrls[1];

        ctrl.init(ngModel);
      }
    };
  })

  .directive('uibDatepickerPopupWrap', function() {
    return {
      restrict: 'A',
      transclude: true,
      templateUrl: function(element, attrs) {
        return attrs.templateUrl || 'components/datepickerPopup/popup.html';
      }
    };
  });

/* jshint +W018 */
/* jshint +W071 */
/* jshint +W101 */
/* jshint +W106 */

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('DateCounter', DateCounter);

function DateCounter() {
  var service = {
    getFutureDate: getFutureDate,
    getTheFirstOfNextMonth: getTheFirstOfNextMonth
  };
  return service;

  ////////////

  /**
   * @param {number} days 
   * @param {boolean} skipWeekends
   * @returns {Date}
   */
  function getFutureDate(days, skipWeekends) {
    if (!skipWeekends) {
      return new Date(Date.now() + days * 24 * 3600 * 1000);
    } else {
      let date = new Date(Date.now());
      let i = 1;
      while (days > 0) {
        if (![0,6].includes(date.getDay())) {
          days--;
        }
        date = getFutureDate(i, false);
        i++;
      }
      return date;
    }
  }

  function getTheFirstOfNextMonth(dataObj) {
    var currentDate = dataObj.getDate();
    var currentMonth = dataObj.getMonth();
    var year = dataObj.getFullYear();

    if (currentDate === 1) {
      var month = currentMonth;
    } else {
      var month = (currentMonth + 1) % 12;
    }

    if (currentMonth === 11 && currentDate !== 1) {
      year = year + 1;
    }

    return new Date(year, month, 1);
  }
}

})();
(function () {
'use strict';

/* jshint -W018 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

angular
  .module('uiDatepicker', ['uiDateParser', 'uiIsClass'])

  .value('$datepickerSuppressError', false)

  .value('$datepickerLiteralWarning', true)

  .constant('uibDatepickerConfig', {
    datepickerMode: 'day',
    formatDay: 'dd',
    formatMonth: 'MMMM',
    formatYear: 'yyyy',
    formatDayHeader: 'EEE',
    formatDayTitle: 'MMMM yyyy',
    formatMonthTitle: 'yyyy',
    maxDate: null,
    maxMode: 'year',
    minDate: null,
    minMode: 'day',
    monthColumns: 3,
    ngModelOptions: {},
    shortcutPropagation: false,
    showWeeks: true,
    yearColumns: 5,
    yearRows: 4,
    startingDay: 1,
    noWeekends: false,
  })

  .controller('UibDatepickerController', ["$scope", "$element", "$attrs", "$parse", "$interpolate", "$locale", "$log", "dateFilter", "uibDatepickerConfig", "$datepickerLiteralWarning", "$datepickerSuppressError", "uibDateParser", function(
    $scope,
    $element,
    $attrs,
    $parse,
    $interpolate,
    $locale,
    $log,
    dateFilter,
    uibDatepickerConfig,
    $datepickerLiteralWarning,
    $datepickerSuppressError,
    uibDateParser
  ) {
    var self = this,
      ngModelCtrl = { $setViewValue: angular.noop }, // nullModelCtrl;
      ngModelOptions = {},
      watchListeners = [];

    $element.addClass('uib-datepicker');
    $attrs.$set('role', 'application');

    if (!$scope.datepickerOptions) {
      $scope.datepickerOptions = {};
    }

    // Modes chain
    this.modes = ['day', 'month', 'year'];

    [
      'customClass',
      'dateDisabled',
      'datepickerMode',
      'formatDay',
      'formatDayHeader',
      'formatDayTitle',
      'formatMonth',
      'formatMonthTitle',
      'formatYear',
      'maxDate',
      'maxMode',
      'minDate',
      'minMode',
      'monthColumns',
      'showWeeks',
      'shortcutPropagation',
      'startingDay',
      'yearColumns',
      'yearRows',
      'noWeekends'
    ].forEach(function(key) {
      switch (key) {
        case 'customClass':
        case 'dateDisabled':
          $scope[key] = $scope.datepickerOptions[key] || angular.noop;
          break;
        case 'datepickerMode':
          $scope.datepickerMode = angular.isDefined($scope.datepickerOptions.datepickerMode)
            ? $scope.datepickerOptions.datepickerMode
            : uibDatepickerConfig.datepickerMode;
          break;
        case 'formatDay':
        case 'formatDayHeader':
        case 'formatDayTitle':
        case 'formatMonth':
        case 'formatMonthTitle':
        case 'formatYear':
          self[key] = angular.isDefined($scope.datepickerOptions[key])
            ? $interpolate($scope.datepickerOptions[key])($scope.$parent)
            : uibDatepickerConfig[key];
          break;
        case 'monthColumns':
        case 'showWeeks':
        case 'shortcutPropagation':
        case 'yearColumns':
        case 'yearRows':
          self[key] = angular.isDefined($scope.datepickerOptions[key])
            ? $scope.datepickerOptions[key]
            : uibDatepickerConfig[key];
          break;
        case 'startingDay':
          if (angular.isDefined($scope.datepickerOptions.startingDay)) {
            self.startingDay = $scope.datepickerOptions.startingDay;
          } else if (angular.isNumber(uibDatepickerConfig.startingDay)) {
            self.startingDay = uibDatepickerConfig.startingDay;
          } else {
            self.startingDay = ($locale.DATETIME_FORMATS.FIRSTDAYOFWEEK + 8) % 7;
          }

          break;
        case 'noWeekends':
          self.noWeekends = $scope.datepickerOptions.noWeekends;
          break;
        case 'maxDate':
        case 'minDate':
          $scope.$watch('datepickerOptions.' + key, function(value) {
            if (value) {
              if (angular.isDate(value)) {
                self[key] = uibDateParser.fromTimezone(new Date(value), ngModelOptions.getOption('timezone'));
              } else {
                if ($datepickerLiteralWarning) {
                  $log.warn('Literal date support has been deprecated, please switch to date object usage');
                }

                self[key] = new Date(dateFilter(value, 'medium'));
              }
            } else {
              self[key] = uibDatepickerConfig[key]
                ? uibDateParser.fromTimezone(new Date(uibDatepickerConfig[key]), ngModelOptions.getOption('timezone'))
                : null;
            }

            self.refreshView();
          });

          break;
        case 'maxMode':
        case 'minMode':
          if ($scope.datepickerOptions[key]) {
            $scope.$watch(
              function() {
                return $scope.datepickerOptions[key];
              },
              function(value) {
                self[key] = $scope[key] = angular.isDefined(value) ? value : $scope.datepickerOptions[key];
                if (
                  (key === 'minMode' &&
                    self.modes.indexOf($scope.datepickerOptions.datepickerMode) < self.modes.indexOf(self[key])) ||
                  (key === 'maxMode' &&
                    self.modes.indexOf($scope.datepickerOptions.datepickerMode) > self.modes.indexOf(self[key]))
                ) {
                  $scope.datepickerMode = self[key];
                  $scope.datepickerOptions.datepickerMode = self[key];
                }
              }
            );
          } else {
            self[key] = $scope[key] = uibDatepickerConfig[key] || null;
          }

          break;
      }
    });

    $scope.uniqueId = 'datepicker-' + $scope.$id + '-' + Math.floor(Math.random() * 10000);

    $scope.disabled = angular.isDefined($attrs.disabled) || false;
    if (angular.isDefined($attrs.ngDisabled)) {
      watchListeners.push(
        $scope.$parent.$watch($attrs.ngDisabled, function(disabled) {
          $scope.disabled = disabled;
          self.refreshView();
        })
      );
    }

    $scope.isActive = function(dateObject) {
      if (self.compare(dateObject.date, self.activeDate) === 0) {
        $scope.activeDateId = dateObject.uid;
        return true;
      }
      return false;
    };

    this.init = function(ngModelCtrl_) {
      ngModelCtrl = ngModelCtrl_;
      ngModelOptions = extractOptions(ngModelCtrl);

      if ($scope.datepickerOptions.initDate) {
        self.activeDate =
          uibDateParser.fromTimezone($scope.datepickerOptions.initDate, ngModelOptions.getOption('timezone')) ||
          new Date();
        $scope.$watch('datepickerOptions.initDate', function(initDate) {
          if (initDate && (ngModelCtrl.$isEmpty(ngModelCtrl.$modelValue) || ngModelCtrl.$invalid)) {
            self.activeDate = uibDateParser.fromTimezone(initDate, ngModelOptions.getOption('timezone'));
            self.refreshView();
          }
        });
      } else {
        self.activeDate = new Date();
      }

      var date = ngModelCtrl.$modelValue ? new Date(ngModelCtrl.$modelValue) : new Date();
      this.activeDate = !isNaN(date)
        ? uibDateParser.fromTimezone(date, ngModelOptions.getOption('timezone'))
        : uibDateParser.fromTimezone(new Date(), ngModelOptions.getOption('timezone'));

      ngModelCtrl.$render = function() {
        self.render();
      };
    };

    this.render = function() {
      if (ngModelCtrl.$viewValue) {
        var date = new Date(ngModelCtrl.$viewValue),
          isValid = !isNaN(date);

        if (isValid) {
          this.activeDate = uibDateParser.fromTimezone(date, ngModelOptions.getOption('timezone'));
        } else if (!$datepickerSuppressError) {
          $log.error('Datepicker directive: "ng-model" value must be a Date object');
        }
      }
      this.refreshView();
    };

    this.refreshView = function() {
      if (this.element) {
        $scope.selectedDt = null;
        this._refreshView();
        if ($scope.activeDt) {
          $scope.activeDateId = $scope.activeDt.uid;
        }

        var date = ngModelCtrl.$viewValue ? new Date(ngModelCtrl.$viewValue) : null;
        date = uibDateParser.fromTimezone(date, ngModelOptions.getOption('timezone'));
        ngModelCtrl.$setValidity('dateDisabled', !date || (this.element && !this.isDisabled(date)));
      }
    };

    this.createDateObject = function(date, format) {
      var model = ngModelCtrl.$viewValue ? new Date(ngModelCtrl.$viewValue) : null;
      model = uibDateParser.fromTimezone(model, ngModelOptions.getOption('timezone'));
      var today = new Date();
      today = uibDateParser.fromTimezone(today, ngModelOptions.getOption('timezone'));
      var time = this.compare(date, today);
      var dt = {
        date: date,
        label: uibDateParser.filter(date, format),
        selected: model && this.compare(date, model) === 0,
        disabled: this.isDisabled(date),
        past: time < 0,
        current: time === 0,
        future: time > 0,
        customClass: this.customClass(date) || null
      };

      if (model && this.compare(date, model) === 0) {
        $scope.selectedDt = dt;
      }

      if (self.activeDate && this.compare(dt.date, self.activeDate) === 0) {
        $scope.activeDt = dt;
      }

      return dt;
    };

    this.isDisabled = function(date) {
      return (
        $scope.disabled ||
        (this.minDate && this.compare(date, this.minDate) < 0) ||
        (this.maxDate && this.compare(date, this.maxDate) > 0) ||
        (this.noWeekends && this.isWeekend(date)) ||
        ($scope.dateDisabled && $scope.dateDisabled({ date: date, mode: $scope.datepickerMode }))
      );
    };

    this.customClass = function(date) {
      return $scope.customClass({ date: date, mode: $scope.datepickerMode });
    };

    // Split array into smaller arrays
    this.split = function(arr, size) {
      var arrays = [];
      while (arr.length > 0) {
        arrays.push(arr.splice(0, size));
      }
      return arrays;
    };

    $scope.select = function(date) {
      if ($scope.datepickerMode === self.minMode) {
        var dt = ngModelCtrl.$viewValue
          ? uibDateParser.fromTimezone(new Date(ngModelCtrl.$viewValue), ngModelOptions.getOption('timezone'))
          : new Date(0, 0, 0, 0, 0, 0, 0);
        dt.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
        dt = uibDateParser.toTimezone(dt, ngModelOptions.getOption('timezone'));
        ngModelCtrl.$setViewValue(dt);
        ngModelCtrl.$render();
      } else {
        self.activeDate = date;
        setMode(self.modes[self.modes.indexOf($scope.datepickerMode) - 1]);

        $scope.$emit('uib:datepicker.mode');
      }

      $scope.$broadcast('uib:datepicker.focus');
    };

    $scope.move = function(direction) {
      var year = self.activeDate.getFullYear() + direction * (self.step.years || 0),
        month = self.activeDate.getMonth() + direction * (self.step.months || 0);
      self.activeDate.setFullYear(year, month, 1);
      self.refreshView();
    };

    $scope.toggleMode = function(direction) {
      direction = direction || 1;

      if (
        ($scope.datepickerMode === self.maxMode && direction === 1) ||
        ($scope.datepickerMode === self.minMode && direction === -1)
      ) {
        return;
      }

      setMode(self.modes[self.modes.indexOf($scope.datepickerMode) + direction]);

      $scope.$emit('uib:datepicker.mode');
    };

    // Key event mapper
    $scope.keys = {
      13: 'enter',
      32: 'space',
      33: 'pageup',
      34: 'pagedown',
      35: 'end',
      36: 'home',
      37: 'left',
      38: 'up',
      39: 'right',
      40: 'down'
    };

    var focusElement = function() {
      self.element[0].focus();
    };

    // Listen for focus requests from popup directive
    $scope.$on('uib:datepicker.focus', focusElement);

    $scope.keydown = function(evt) {
      var key = $scope.keys[evt.which];

      if (!key || evt.shiftKey || evt.altKey || $scope.disabled) {
        return;
      }

      evt.preventDefault();
      if (!self.shortcutPropagation) {
        evt.stopPropagation();
      }

      if (key === 'enter' || key === 'space') {
        if (self.isDisabled(self.activeDate)) {
          return; // do nothing
        }
        $scope.select(self.activeDate);
      } else if (evt.ctrlKey && (key === 'up' || key === 'down')) {
        $scope.toggleMode(key === 'up' ? 1 : -1);
      } else {
        self.handleKeyDown(key, evt);
        self.refreshView();
      }
    };

    $element.on('keydown', function(evt) {
      $scope.$apply(function() {
        $scope.keydown(evt);
      });
    });

    $scope.$on('$destroy', function() {
      //Clear all watch listeners on destroy
      while (watchListeners.length) {
        watchListeners.shift()();
      }
    });

    function setMode(mode) {
      $scope.datepickerMode = mode;
      $scope.datepickerOptions.datepickerMode = mode;
    }

    function extractOptions(ngModelCtrl) {
      var ngModelOptions;

      if (angular.version.minor < 6) {
        // in angular < 1.6 $options could be missing
        // guarantee a value
        ngModelOptions =
          ngModelCtrl.$options || $scope.datepickerOptions.ngModelOptions || uibDatepickerConfig.ngModelOptions || {};

        // mimic 1.6+ api
        ngModelOptions.getOption = function(key) {
          return ngModelOptions[key];
        };
      } else {
        // in angular >=1.6 $options is always present
        // ng-model-options defaults timezone to null; don't let its precedence squash a non-null value
        var timezone =
          ngModelCtrl.$options.getOption('timezone') ||
          ($scope.datepickerOptions.ngModelOptions ? $scope.datepickerOptions.ngModelOptions.timezone : null) ||
          (uibDatepickerConfig.ngModelOptions ? uibDatepickerConfig.ngModelOptions.timezone : null);

        // values passed to createChild override existing values
        ngModelOptions = ngModelCtrl.$options // start with a ModelOptions instance
          .createChild(uibDatepickerConfig.ngModelOptions) // lowest precedence
          .createChild($scope.datepickerOptions.ngModelOptions)
          .createChild(ngModelCtrl.$options) // highest precedence
          .createChild({ timezone: timezone }); // to keep from squashing a non-null value
      }

      return ngModelOptions;
    }
  }])

  .controller('UibDaypickerController', ["$scope", "$element", "dateFilter", function($scope, $element, dateFilter) {
    var DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    this.step = { months: 1 };
    this.element = $element;
    function getDaysInMonth(year, month) {
      return month === 1 && year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : DAYS_IN_MONTH[month];
    }

    this.init = function(ctrl) {
      angular.extend(ctrl, this);
      $scope.showWeeks = ctrl.showWeeks;
      ctrl.refreshView();
    };

    this.getDates = function(startDate, n) {
      var dates = new Array(n),
        current = new Date(startDate),
        i = 0,
        date;
      while (i < n) {
        date = new Date(current);
        dates[i++] = date;
        current.setDate(current.getDate() + 1);
      }
      return dates;
    };

    this._refreshView = function() {
      var year = this.activeDate.getFullYear(),
        month = this.activeDate.getMonth(),
        firstDayOfMonth = new Date(this.activeDate);

      firstDayOfMonth.setFullYear(year, month, 1);

      var difference = this.startingDay - firstDayOfMonth.getDay(),
        numDisplayedFromPreviousMonth = difference > 0 ? 7 - difference : -difference,
        firstDate = new Date(firstDayOfMonth);

      if (numDisplayedFromPreviousMonth > 0) {
        firstDate.setDate(-numDisplayedFromPreviousMonth + 1);
      }

      // 42 is the number of days on a six-week calendar
      var days = this.getDates(firstDate, 42);
      for (var i = 0; i < 42; i++) {
        days[i] = angular.extend(this.createDateObject(days[i], this.formatDay), {
          secondary: days[i].getMonth() !== month,
          uid: $scope.uniqueId + '-' + i
        });
      }

      $scope.labels = new Array(7);
      for (var j = 0; j < 7; j++) {
        $scope.labels[j] = {
          abbr: dateFilter(days[j].date, this.formatDayHeader),
          full: dateFilter(days[j].date, 'EEEE')
        };
      }

      $scope.title = dateFilter(this.activeDate, this.formatDayTitle);
      $scope.rows = this.split(days, 7);

      if ($scope.showWeeks) {
        $scope.weekNumbers = [];
        var thursdayIndex = (4 + 7 - this.startingDay) % 7,
          numWeeks = $scope.rows.length;
        for (var curWeek = 0; curWeek < numWeeks; curWeek++) {
          $scope.weekNumbers.push(getISO8601WeekNumber($scope.rows[curWeek][thursdayIndex].date));
        }
      }
    };

    this.compare = function(date1, date2) {
      var _date1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
      var _date2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
      _date1.setFullYear(date1.getFullYear());
      _date2.setFullYear(date2.getFullYear());
      return _date1 - _date2;
    };

    this.isWeekend = function(date) {
      let dayOfTheWeek = date.getDay();
      return dayOfTheWeek === 0 || dayOfTheWeek === 6;
    };

    function getISO8601WeekNumber(date) {
      var checkDate = new Date(date);
      checkDate.setDate(checkDate.getDate() + 4 - (checkDate.getDay() || 7)); // Thursday
      var time = checkDate.getTime();
      checkDate.setMonth(0); // Compare with Jan 1
      checkDate.setDate(1);
      return Math.floor(Math.round((time - checkDate) / 86400000) / 7) + 1;
    }

    this.handleKeyDown = function(key, evt) {
      var date = this.activeDate.getDate();

      if (key === 'left') {
        date = date - 1;
      } else if (key === 'up') {
        date = date - 7;
      } else if (key === 'right') {
        date = date + 1;
      } else if (key === 'down') {
        date = date + 7;
      } else if (key === 'pageup' || key === 'pagedown') {
        var month = this.activeDate.getMonth() + (key === 'pageup' ? -1 : 1);
        this.activeDate.setMonth(month, 1);
        date = Math.min(getDaysInMonth(this.activeDate.getFullYear(), this.activeDate.getMonth()), date);
      } else if (key === 'home') {
        date = 1;
      } else if (key === 'end') {
        date = getDaysInMonth(this.activeDate.getFullYear(), this.activeDate.getMonth());
      }
      this.activeDate.setDate(date);
    };
  }])

  .controller('UibMonthpickerController', ["$scope", "$element", "dateFilter", function($scope, $element, dateFilter) {
    this.step = { years: 1 };
    this.element = $element;

    this.init = function(ctrl) {
      angular.extend(ctrl, this);
      ctrl.refreshView();
    };

    this._refreshView = function() {
      var months = new Array(12),
        year = this.activeDate.getFullYear(),
        date;

      for (var i = 0; i < 12; i++) {
        date = new Date(this.activeDate);
        date.setFullYear(year, i, 1);
        months[i] = angular.extend(this.createDateObject(date, this.formatMonth), {
          uid: $scope.uniqueId + '-' + i
        });
      }

      $scope.title = dateFilter(this.activeDate, this.formatMonthTitle);
      $scope.rows = this.split(months, this.monthColumns);
      $scope.yearHeaderColspan = this.monthColumns > 3 ? this.monthColumns - 2 : 1;
    };

    this.compare = function(date1, date2) {
      var _date1 = new Date(date1.getFullYear(), date1.getMonth());
      var _date2 = new Date(date2.getFullYear(), date2.getMonth());
      _date1.setFullYear(date1.getFullYear());
      _date2.setFullYear(date2.getFullYear());
      return _date1 - _date2;
    };

    this.handleKeyDown = function(key, evt) {
      var date = this.activeDate.getMonth();

      if (key === 'left') {
        date = date - 1;
      } else if (key === 'up') {
        date = date - this.monthColumns;
      } else if (key === 'right') {
        date = date + 1;
      } else if (key === 'down') {
        date = date + this.monthColumns;
      } else if (key === 'pageup' || key === 'pagedown') {
        var year = this.activeDate.getFullYear() + (key === 'pageup' ? -1 : 1);
        this.activeDate.setFullYear(year);
      } else if (key === 'home') {
        date = 0;
      } else if (key === 'end') {
        date = 11;
      }
      this.activeDate.setMonth(date);
    };
  }])

  .controller('UibYearpickerController', ["$scope", "$element", "dateFilter", function($scope, $element, dateFilter) {
    var columns, range;
    this.element = $element;

    function getStartingYear(year) {
      return parseInt((year - 1) / range, 10) * range + 1;
    }

    this.yearpickerInit = function() {
      columns = this.yearColumns;
      range = this.yearRows * columns;
      this.step = { years: range };
    };

    this._refreshView = function() {
      var years = new Array(range),
        date;

      for (var i = 0, start = getStartingYear(this.activeDate.getFullYear()); i < range; i++) {
        date = new Date(this.activeDate);
        date.setFullYear(start + i, 0, 1);
        years[i] = angular.extend(this.createDateObject(date, this.formatYear), {
          uid: $scope.uniqueId + '-' + i
        });
      }

      $scope.title = [years[0].label, years[range - 1].label].join(' - ');
      $scope.rows = this.split(years, columns);
      $scope.columns = columns;
    };

    this.compare = function(date1, date2) {
      return date1.getFullYear() - date2.getFullYear();
    };

    this.handleKeyDown = function(key, evt) {
      var date = this.activeDate.getFullYear();

      if (key === 'left') {
        date = date - 1;
      } else if (key === 'up') {
        date = date - columns;
      } else if (key === 'right') {
        date = date + 1;
      } else if (key === 'down') {
        date = date + columns;
      } else if (key === 'pageup' || key === 'pagedown') {
        date += (key === 'pageup' ? -1 : 1) * range;
      } else if (key === 'home') {
        date = getStartingYear(this.activeDate.getFullYear());
      } else if (key === 'end') {
        date = getStartingYear(this.activeDate.getFullYear()) + range - 1;
      }
      this.activeDate.setFullYear(date);
    };
  }])

  .directive('uibDatepicker', function() {
    return {
      templateUrl: function(element, attrs) {
        return attrs.templateUrl || 'components/datepicker/datepicker.html';
      },
      scope: {
        datepickerOptions: '=?'
      },
      require: ['uibDatepicker', '^ngModel'],
      restrict: 'A',
      controller: 'UibDatepickerController',
      controllerAs: 'datepicker',
      link: function(scope, element, attrs, ctrls) {
        var datepickerCtrl = ctrls[0],
          ngModelCtrl = ctrls[1];

        datepickerCtrl.init(ngModelCtrl);
      }
    };
  })

  .directive('uibDaypicker', function() {
    return {
      templateUrl: function(element, attrs) {
        return attrs.templateUrl || 'components/datepicker/day.html';
      },
      require: ['^uibDatepicker', 'uibDaypicker'],
      restrict: 'A',
      controller: 'UibDaypickerController',
      link: function(scope, element, attrs, ctrls) {
        var datepickerCtrl = ctrls[0],
          daypickerCtrl = ctrls[1];

        daypickerCtrl.init(datepickerCtrl);
      }
    };
  })

  .directive('uibMonthpicker', function() {
    return {
      templateUrl: function(element, attrs) {
        return attrs.templateUrl || 'components/datepicker/month.html';
      },
      require: ['^uibDatepicker', 'uibMonthpicker'],
      restrict: 'A',
      controller: 'UibMonthpickerController',
      link: function(scope, element, attrs, ctrls) {
        var datepickerCtrl = ctrls[0],
          monthpickerCtrl = ctrls[1];

        monthpickerCtrl.init(datepickerCtrl);
      }
    };
  })

  .directive('uibYearpicker', function() {
    return {
      templateUrl: function(element, attrs) {
        return attrs.templateUrl || 'components/datepicker/year.html';
      },
      require: ['^uibDatepicker', 'uibYearpicker'],
      restrict: 'A',
      controller: 'UibYearpickerController',
      link: function(scope, element, attrs, ctrls) {
        var ctrl = ctrls[0];
        angular.extend(ctrl, ctrls[1]);
        ctrl.yearpickerInit();

        ctrl.refreshView();
      }
    };
  });

/* jshint +W018 */
/* jshint +W071 */
/* jshint +W101 */
/* jshint +W106 */

})();
(function () {
angular
  .module('uiDebounce', [])
  /**
   * A helper, internal service that debounces a function
   */
  .factory('$$debounce', [
    '$timeout',
    function($timeout) {
      return function(callback, debounceTime) {
        var timeoutPromise;

        return function() {
          var self = this;
          var args = Array.prototype.slice.call(arguments);
          if (timeoutPromise) {
            $timeout.cancel(timeoutPromise);
          }

          timeoutPromise = $timeout(function() {
            callback.apply(self, args);
          }, debounceTime);
        };
      };
    }
  ]);

})();
(function () {
'use strict';

// @desc
//   Donut chart
//
// @example

donut.$inject = ["$location"];
angular.module('hyresgastApp').directive('hyDonut', donut);

function donut($location) {
  var directive = {
    scope: {
      part: '<',
      total: '<',
      maxHeight: '@',
      fixedWidth: '@',
      statusFinished: '<'
    },
    restrict: 'E',
    templateUrl: 'components/donut/donut.html',
    link: link
  };

  return directive;

  function link(scope, element, attrs) {
    ////////////
    scope.$watch('part', function() {
      if (scope.total) {
        scope.partPercent = scope.part / scope.total * 100;
        scope.remainingPercent = 100 - scope.partPercent;
      }
    });
  }
}

})();
(function () {
angular
  .module('uiDropdown', ['uiMultiMap', 'uiPosition'])

  .constant('uibDropdownConfig', {
    appendToOpenClass: 'uib-dropdown-open',
    openClass: 'open'
  })

  .service('uibDropdownService', [
    '$document',
    '$rootScope',
    '$$multiMap',
    function($document, $rootScope, $$multiMap) {
      var openScope = null;
      var openedContainers = $$multiMap.createNew();

      this.isOnlyOpen = function(dropdownScope, appendTo) {
        var openedDropdowns = openedContainers.get(appendTo);
        if (openedDropdowns) {
          var openDropdown = openedDropdowns.reduce(function(toClose, dropdown) {
            if (dropdown.scope === dropdownScope) {
              return dropdown;
            }

            return toClose;
          }, {});
          if (openDropdown) {
            return openedDropdowns.length === 1;
          }
        }

        return false;
      };

      this.open = function(dropdownScope, element, appendTo) {
        if (!openScope) {
          $document.on('click', closeDropdown);
        }

        if (openScope && openScope !== dropdownScope) {
          openScope.isOpen = false;
        }

        openScope = dropdownScope;

        if (!appendTo) {
          return;
        }

        var openedDropdowns = openedContainers.get(appendTo);
        if (openedDropdowns) {
          var openedScopes = openedDropdowns.map(function(dropdown) {
            return dropdown.scope;
          });
          if (openedScopes.indexOf(dropdownScope) === -1) {
            openedContainers.put(appendTo, {
              scope: dropdownScope
            });
          }
        } else {
          openedContainers.put(appendTo, {
            scope: dropdownScope
          });
        }
      };

      this.close = function(dropdownScope, element, appendTo) {
        if (openScope === dropdownScope) {
          $document.off('click', closeDropdown);
          $document.off('keydown', this.keybindFilter);
          openScope = null;
        }

        if (!appendTo) {
          return;
        }

        var openedDropdowns = openedContainers.get(appendTo);
        if (openedDropdowns) {
          var dropdownToClose = openedDropdowns.reduce(function(toClose, dropdown) {
            if (dropdown.scope === dropdownScope) {
              return dropdown;
            }

            return toClose;
          }, {});
          if (dropdownToClose) {
            openedContainers.remove(appendTo, dropdownToClose);
          }
        }
      };

      var closeDropdown = function(evt) {
        // This method may still be called during the same mouse event that
        // unbound this event handler. So check openScope before proceeding.
        if (!openScope || !openScope.isOpen) {
          return;
        }

        if (evt && openScope.getAutoClose() === 'disabled') {
          return;
        }

        if (evt && evt.which === 3) {
          return;
        }

        var toggleElement = openScope.getToggleElement();
        if (evt && toggleElement && toggleElement[0].contains(evt.target)) {
          return;
        }

        var dropdownElement = openScope.getDropdownElement();
        if (
          evt &&
          openScope.getAutoClose() === 'outsideClick' &&
          dropdownElement &&
          dropdownElement[0].contains(evt.target)
        ) {
          return;
        }

        openScope.focusToggleElement();
        openScope.isOpen = false;

        if (!$rootScope.$$phase) {
          openScope.$apply();
        }
      };

      this.keybindFilter = function(evt) {
        if (!openScope) {
          // see this.close as ESC could have been pressed which kills the scope so we can not proceed
          return;
        }

        var dropdownElement = openScope.getDropdownElement();
        var toggleElement = openScope.getToggleElement();
        var dropdownElementTargeted = dropdownElement && dropdownElement[0].contains(evt.target);
        var toggleElementTargeted = toggleElement && toggleElement[0].contains(evt.target);
        if (evt.which === 27) {
          evt.stopPropagation();
          openScope.focusToggleElement();
          closeDropdown();
        } else if (
          openScope.isKeynavEnabled() &&
          [38, 40].indexOf(evt.which) !== -1 &&
          openScope.isOpen &&
          (dropdownElementTargeted || toggleElementTargeted)
        ) {
          evt.preventDefault();
          evt.stopPropagation();
          openScope.focusDropdownEntry(evt.which);
        }
      };
    }
  ])

  .controller('UibDropdownController', [
    '$scope',
    '$element',
    '$attrs',
    '$parse',
    'uibDropdownConfig',
    'uibDropdownService',
    '$animate',
    '$uibPosition',
    '$document',
    '$compile',
    '$templateRequest',
    function(
      $scope,
      $element,
      $attrs,
      $parse,
      dropdownConfig,
      uibDropdownService,
      $animate,
      $position,
      $document,
      $compile,
      $templateRequest
    ) {
      var self = this,
        scope = $scope.$new(), // create a child scope so we are not polluting original one
        templateScope,
        appendToOpenClass = dropdownConfig.appendToOpenClass,
        openClass = dropdownConfig.openClass,
        getIsOpen,
        setIsOpen = angular.noop,
        toggleInvoker = $attrs.onToggle ? $parse($attrs.onToggle) : angular.noop,
        keynavEnabled = false,
        selectedOption = null,
        body = $document.find('body');

      $element.addClass('dropdown');

      this.init = function() {
        if ($attrs.isOpen) {
          getIsOpen = $parse($attrs.isOpen);
          setIsOpen = getIsOpen.assign;

          $scope.$watch(getIsOpen, function(value) {
            scope.isOpen = !!value;
          });
        }

        keynavEnabled = angular.isDefined($attrs.keyboardNav);
      };

      this.toggle = function(open) {
        scope.isOpen = arguments.length ? !!open : !scope.isOpen;
        if (angular.isFunction(setIsOpen)) {
          setIsOpen(scope, scope.isOpen);
        }

        return scope.isOpen;
      };

      // Allow other directives to watch status
      this.isOpen = function() {
        return scope.isOpen;
      };

      scope.getToggleElement = function() {
        return self.toggleElement;
      };

      scope.getAutoClose = function() {
        return $attrs.autoClose || 'always'; //or 'outsideClick' or 'disabled'
      };

      scope.getElement = function() {
        return $element;
      };

      scope.isKeynavEnabled = function() {
        return keynavEnabled;
      };

      scope.focusDropdownEntry = function(keyCode) {
        var elems = self.dropdownMenu //If append to body is used.
          ? angular.element(self.dropdownMenu).find('a')
          : $element
              .find('ul')
              .eq(0)
              .find('a');

        switch (keyCode) {
          case 40: {
            if (!angular.isNumber(self.selectedOption)) {
              self.selectedOption = 0;
            } else {
              self.selectedOption =
                self.selectedOption === elems.length - 1 ? self.selectedOption : self.selectedOption + 1;
            }
            break;
          }
          case 38: {
            if (!angular.isNumber(self.selectedOption)) {
              self.selectedOption = elems.length - 1;
            } else {
              self.selectedOption = self.selectedOption === 0 ? 0 : self.selectedOption - 1;
            }
            break;
          }
        }
        elems[self.selectedOption].focus();
      };

      scope.getDropdownElement = function() {
        return self.dropdownMenu;
      };

      scope.focusToggleElement = function() {
        if (self.toggleElement) {
          self.toggleElement[0].focus();
        }
      };

      function removeDropdownMenu() {
        $element.append(self.dropdownMenu);
      }

      scope.$watch('isOpen', function(isOpen, wasOpen) {
        var appendTo = null,
          appendToBody = false;

        if (angular.isDefined($attrs.dropdownAppendTo)) {
          var appendToEl = $parse($attrs.dropdownAppendTo)(scope);
          if (appendToEl) {
            appendTo = angular.element(appendToEl);
          }
        }

        if (angular.isDefined($attrs.dropdownAppendToBody)) {
          var appendToBodyValue = $parse($attrs.dropdownAppendToBody)(scope);
          if (appendToBodyValue !== false) {
            appendToBody = true;
          }
        }

        if (appendToBody && !appendTo) {
          appendTo = body;
        }

        if (appendTo && self.dropdownMenu) {
          if (isOpen) {
            appendTo.append(self.dropdownMenu);
            $element.on('$destroy', removeDropdownMenu);
          } else {
            $element.off('$destroy', removeDropdownMenu);
            removeDropdownMenu();
          }
        }

        if (appendTo && self.dropdownMenu) {
          var pos = $position.positionElements($element, self.dropdownMenu, 'bottom-left', true),
            css,
            rightalign,
            scrollbarPadding,
            scrollbarWidth = 0;

          css = {
            top: pos.top + 'px',
            display: isOpen ? 'block' : 'none'
          };

          rightalign = self.dropdownMenu.hasClass('dropdown-menu-right');
          if (!rightalign) {
            css.left = pos.left + 'px';
            css.right = 'auto';
          } else {
            css.left = 'auto';
            scrollbarPadding = $position.scrollbarPadding(appendTo);

            if (scrollbarPadding.heightOverflow && scrollbarPadding.scrollbarWidth) {
              scrollbarWidth = scrollbarPadding.scrollbarWidth;
            }

            css.right = window.innerWidth - scrollbarWidth - (pos.left + $element.prop('offsetWidth')) + 'px';
          }

          // Need to adjust our positioning to be relative to the appendTo container
          // if it's not the body element
          if (!appendToBody) {
            var appendOffset = $position.offset(appendTo);

            css.top = pos.top - appendOffset.top + 'px';

            if (!rightalign) {
              css.left = pos.left - appendOffset.left + 'px';
            } else {
              css.right = window.innerWidth - (pos.left - appendOffset.left + $element.prop('offsetWidth')) + 'px';
            }
          }

          self.dropdownMenu.css(css);
        }

        var openContainer = appendTo ? appendTo : $element;
        var dropdownOpenClass = appendTo ? appendToOpenClass : openClass;
        var hasOpenClass = openContainer.hasClass(dropdownOpenClass);
        var isOnlyOpen = uibDropdownService.isOnlyOpen($scope, appendTo);

        if (hasOpenClass === !isOpen) {
          var toggleClass;
          if (appendTo) {
            toggleClass = !isOnlyOpen ? 'addClass' : 'removeClass';
          } else {
            toggleClass = isOpen ? 'addClass' : 'removeClass';
          }
          $animate[toggleClass](openContainer, dropdownOpenClass).then(function() {
            if (angular.isDefined(isOpen) && isOpen !== wasOpen) {
              toggleInvoker($scope, { open: !!isOpen });
            }
          });
        }

        if (isOpen) {
          if (self.dropdownMenuTemplateUrl) {
            $templateRequest(self.dropdownMenuTemplateUrl).then(function(tplContent) {
              templateScope = scope.$new();
              $compile(tplContent.trim())(templateScope, function(dropdownElement) {
                var newEl = dropdownElement;
                self.dropdownMenu.replaceWith(newEl);
                self.dropdownMenu = newEl;
                $document.on('keydown', uibDropdownService.keybindFilter);
              });
            });
          } else {
            $document.on('keydown', uibDropdownService.keybindFilter);
          }

          scope.focusToggleElement();
          uibDropdownService.open(scope, $element, appendTo);
        } else {
          uibDropdownService.close(scope, $element, appendTo);
          if (self.dropdownMenuTemplateUrl) {
            if (templateScope) {
              templateScope.$destroy();
            }
            var newEl = angular.element('<ul class="dropdown-menu"></ul>');
            self.dropdownMenu.replaceWith(newEl);
            self.dropdownMenu = newEl;
          }

          self.selectedOption = null;
        }

        if (angular.isFunction(setIsOpen)) {
          setIsOpen($scope, isOpen);
        }
      });
    }
  ])

  .directive('uibDropdown', function() {
    return {
      controller: 'UibDropdownController',
      link: function(scope, element, attrs, dropdownCtrl) {
        dropdownCtrl.init();
      }
    };
  })

  .directive('uibDropdownMenu', function() {
    return {
      restrict: 'A',
      require: '?^uibDropdown',
      link: function(scope, element, attrs, dropdownCtrl) {
        if (!dropdownCtrl || angular.isDefined(attrs.dropdownNested)) {
          return;
        }

        element.addClass('dropdown-menu');

        var tplUrl = attrs.templateUrl;
        if (tplUrl) {
          dropdownCtrl.dropdownMenuTemplateUrl = tplUrl;
        }

        if (!dropdownCtrl.dropdownMenu) {
          dropdownCtrl.dropdownMenu = element;
        }
      }
    };
  })

  .directive('uibDropdownToggle', function() {
    return {
      require: '?^uibDropdown',
      link: function(scope, element, attrs, dropdownCtrl) {
        if (!dropdownCtrl) {
          return;
        }

        element.addClass('dropdown-toggle');

        dropdownCtrl.toggleElement = element;

        var toggleDropdown = function(event) {
          event.preventDefault();

          if (!element.hasClass('disabled') && !attrs.disabled) {
            scope.$apply(function() {
              dropdownCtrl.toggle();
            });
          }
        };

        element.on('click', toggleDropdown);

        // WAI-ARIA
        element.attr({ 'aria-haspopup': true, 'aria-expanded': false });
        scope.$watch(dropdownCtrl.isOpen, function(isOpen) {
          element.attr('aria-expanded', !!isOpen);
        });

        scope.$on('$destroy', function() {
          element.off('click', toggleDropdown);
        });
      }
    };
  });

})();
(function () {
angular.module('hyresgastApp').directive('hyEnter', function() {
  return function(scope, element, attrs) {
    element.bind('keydown keypress', function(event) {
      if (event.which === 13) {
        scope.$apply(function() {
          scope.$eval(attrs.hyEnter);
        });
        event.preventDefault();
      }
    });
  };
});

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('ErrorHandler', ["$location", "Flash", function($location, Flash) {
  return function(msg) {
    return function(err) {
      if (err.status === 403) {
        $location.path('/403');
      } else {
        Flash.set(msg, 'error');
      }
    }
  }
}]);

})();
(function () {
'use strict';

hyFilePlate.$inject = ["$location", "$timeout", "LocalApiPort"];
let xhrUploadFile = function(files, url, progressCb, doneCb) {
  let progresses = Array.from({ length: files.length }, () => 0);
  let results = Array.from({ length: files.length });

  for (let i = 0; i < files.length; i++) {
    let file = files[i];
    // eslint-disable-next-line no-undef
    var formdata = new FormData();

    formdata.append('file1', file);

    // eslint-disable-next-line no-undef
    let xhr = new XMLHttpRequest();
    let fileSize = file.size;

    xhr.upload.onprogress = function (e) {
      if (e.loaded <= fileSize) {
        progresses[i] = Math.round(e.loaded / fileSize * 100);
        let percent = progresses.reduce((acc, v, i, a) => (acc+v/a.length), 0);
        progressCb(percent);
      }
    };
    xhr.onload = function () {
      results[i] = xhr.response ? JSON.parse(xhr.response) : '';
      if (results.every(x => x)) {
        doneCb(results);
      }
    }
    xhr.onerror = function () {
      doneCb(null);
    }

    xhr.open('post', url);
    xhr.withCredentials = true;
    xhr.timeout = 4500000;
    xhr.send(formdata);
  }
};

let makeBuffers = function (files) {
  // eslint-disable-next-line no-undef
  return new Promise(resolve => {
    let result = [];
    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      // eslint-disable-next-line no-undef
      let fr = new FileReader();
      fr.onload = function () {
        let data = fr.result;
        // eslint-disable-next-line no-undef
        let array = new Uint8Array(data);
        result.push({
          filename: file.name,
          buffer: Array.from(array)
        });
        if (result.length === files.length) {
          resolve(result);
        }
      };
      fr.readAsArrayBuffer(file);
    }
  });
};

angular.module('filePlate', []).directive('hyFilePlate', hyFilePlate);

function hyFilePlate($location, $timeout,
  LocalApiPort) {
  return {
    restrict: 'E',
    templateUrl: 'components/file-plate/file-plate.html',
    scope: {
      multiple: '=',
      uploadUrl: '@',
      uploadParams: '=',
      validFileTypes: '@',

      buffers: '=',
      blobIds: '=',
      metadata: '=',
      ready: '=',
    },
    // eslint-disable-next-line no-unused-vars
    link: (scope, element, attrs) => {
      scope.busy = false;
      scope.currentFile = null;

      let processFiles = files => {
        if (files.length) {
          if (scope.uploadUrl) {
            scope.currentFileString = files.length === 1
              ? files[0].name
              : `${files[0].name} (+${files.length-1})`;
            scope.currentFiles = files;
            scope.ready = true;
            scope.$apply();
          } else if (scope.buffers) {
            scope.buffers.length = 0;
            makeBuffers(files).then(result => {
              scope.buffers = result;
              scope.$apply();
            });
          }
        }
      };

      let url =  'localhost' === $location.host()
        ? 'http://localhost:' + LocalApiPort + scope.uploadUrl
        : scope.uploadUrl;

      element.bind('drop', event => {
        event.stopPropagation();
        event.preventDefault();

        let files = event.dataTransfer.files
          ? event.dataTransfer.files
          : event.dataTransfer.items.filter(x => x.kind === 'file').map(x => x.getAsFile());

        if (scope.validFileTypes) {
          let validExts = scope.validFileTypes.split(' ');
          files = [...files].filter(x => validExts.some(ext => x.name.includes(ext)));
        }

        processFiles(files);
      });

      // eslint-disable-next-line angular/document-service, no-undef
      let dialogEl = document.getElementById(scope.multiple ? 'file-plate-dialog-multiple' : 'file-plate-dialog');
      scope.$watch('validFileTypes', newValue => {
        if (newValue) {
          dialogEl.accept = '.' + newValue.split(' ').join(',.');
        }
      });
      dialogEl.onchange = e => {
        processFiles(e.target.files);
      };
      scope.select = function () {
        dialogEl.click();
      }

      scope.clear = function () {
        scope.currentFileString = null;
        scope.ready = false;
      }

      scope.$on('do-upload', () => {
        scope.busy = true;
        // eslint-disable-next-line angular/document-service, no-undef
        let plateEl = document.getElementById('file-plate-component');

        if (scope.uploadParams) {
          let paramDipstick = Object.keys(scope.uploadParams)
            .map(k => `${k}=${scope.uploadParams[k]}`).join('&');
          if (paramDipstick.length) {
            url = `${url}?${paramDipstick}`;
          }
        }

        xhrUploadFile(scope.currentFiles, url,
          percent => { plateEl.style.backgroundSize = `${percent}% 100%, 100% 100%` },
          results => {
            plateEl.style.backgroundSize = `100% 100%, 100% 100%`;
            scope.busy = false;
            scope.done = true;
            results.forEach(x => x.lastModified = new Date());
            scope.blobIds = results.map(x => x.blobId);
            scope.metadata = results;
            scope.$apply();
          }
        );
      });

      scope.$on('do-reset', () => {
        let plateEl = document.getElementById('file-plate-component');
        plateEl.style.backgroundSize = `0 100%, 100% 100%`;
        scope.currentFileString = '';
        scope.ready = false;
        dialogEl.value = '';
        scope.done = false;
        console.log('reset');
      });
    },
  };
}
})();
(function () {
'use strict';

// @desc
//   Show messages directly under the navigation. Such as
//   error or success messages. It supports the types
//   'default', 'error, 'success' and 'fullscreen' hcing
//   corresponding CSS styling
//
// @example
//   <main>
//      <hy-flash></hy-flash>
//      <ng-view class="main-content"></ng-view>
//   </main>

flash.$inject = ["$anchorScroll", "$document", "$timeout", "Flash"];
angular.module('flash', ['flashService']).directive('hyFlash', flash);

function flash($anchorScroll, $document, $timeout, Flash) {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/flash/flash.html',
    scope: {},
    link: link
  };

  return directive;

  ////////////

  function link(scope, element, attrs) {
    var modifiers = ['flash--visible', 'flash--default', 'flash--success', 'flash--error'];

    scope.Flash = Flash;

    var mainContent = angular.element($document[0].querySelector('.main-content'));

    scope.close = function() {
      Flash.hide();
    };

    scope.$watch(
      'Flash.get()',
      function(newValue) {
        element.removeClass(modifiers.join(' '));

        if (newValue) {
          $anchorScroll(); // scroll to top
          element.addClass('flash--visible');
          element.addClass('flash--' + newValue.type);
        }
      },
      true
    );
  }
}

})();
(function () {
'use strict';

Flash.$inject = ["$timeout"];
angular.module('flashService', []).factory('Flash', Flash);

function Flash($timeout) {
  var message;
  var service = {
    get: get,
    set: set,
    hide: destroy
  };

  return service;

  ////////////

  function get() {
    return message;
  }

  function set(content, type) {
    // type: success, error
    message = {
      content: content,
      type: type
    };
    autoRemove();
  }

  function destroy() {
    message = null;
  }

  //auto removes the message after 6 seconds
  function autoRemove() {
    $timeout(function() {
      destroy();
    }, 6000);
  }
}

})();
(function () {
'use strict';

// @desc upload files with a nice progressbar and optionally validate files
// @example <hy-file-upload></hy-file-upload>

fileUpload.$inject = ["$timeout", "FileValidatorPolling", "FileUploader", "Flash", "LocalApiPort"];
angular
  .module('fileUpload', ['angularFileUpload', 'fileValidatorPolling', 'flash'])
  .directive('hyFileUpload', fileUpload);

function fileUpload($timeout, FileValidatorPolling, FileUploader, Flash, LocalApiPort) {
  var directive = {
    restrict: 'A',
    scope: {
      blobId: '=', // A successful upload will result in a blob id
      metadata: '=', // Metadata (about the uploaded file) returned from backend
      validFileTypes: '@', // A space separated string 'jpeg gif'
      maxFileSize: '@',
      uploadUrl: '@',
      uploader: '=',
      type: '=?', // Optional type as a parameter
      state: '=?', // State as a parameter
      errorInvalidFile: '=?', // Error message will show in modal window, if upload is from modal window

      // Attempting to generalize the uploader directive by exposing the errors to the parent controller.
      // $watch the validationErrors from parent controller to decide how to present them (ie. a modal).
      validationErrors: '=?',
      validationResponse: '=' // TODO probably merge validationErrors and validationResponse (remember to update $watch in parent controller)
    },
    link: link
  };

  return directive;

  function link(scope, element, attrs) {
    // For upload to work on localhost
    var uploadUrl;
    if (window.location.hostname === 'localhost') {
      uploadUrl = 'http://localhost:' + LocalApiPort + scope.uploadUrl;
    } else {
      uploadUrl = scope.uploadUrl;
    }

    // Some uploaded files are parsed and validated by backend,
    // if validateUpload attribute exist, a polling service started
    var validateUpload = 'validateUpload' in attrs;

    // Choose a file and upload directly
    var autoUpload = 'autoUpload' in attrs;

    var disableFlash = 'disableFlash' in attrs;

    var uploader = new FileUploader({
      queueLimit: 1,
      withCredentials: true,
      url: uploadUrl
    });

    scope.uploader = uploader;

    scope.$on('$destroy', function() {
      uploader.cancelUpload();
    });

    ////////////
    if (scope.validFileTypes !== '') {
      uploader.filters.push({
        name: 'fileTypeFilter',
        fn: function(item /*{File|FileLikeObject}*/, options) {
          var type = '|' + item.name.slice(item.name.lastIndexOf('.') + 1) + '|';
          var extensions = '|' + scope.validFileTypes.split(' ').join('|') + '|'; // "foo bar" => "|foo|bar|"
          return extensions.indexOf(type.toLowerCase()) !== -1;
        }
      });
    }

    uploader.filters.push({
      name: 'fileSizeFilter',
      fn: function(item /*{File|FileLikeObject}*/, options) {
        if (item.size < parseInt(scope.maxFileSize)) {
          return true;
        } else {
          return false;
        }
      }
    });

    uploader.onWhenAddingFileFailed = function(item /*{File|FileLikeObject}*/, filter, options) {
      if (filter.name === 'fileTypeFilter') {
        if (disableFlash) {
          // If in module, disable Flash error message
          scope.errorInvalidFile = 'Felaktigt filformat - försök igen';
        } else {
          Flash.set('Felaktigt filformat', 'error');
        }
      }

      if (filter.name === 'fileSizeFilter') {
        if (disableFlash) {
          scope.errorInvalidFile = 'Filen är för stor - försök igen';
        }
        Flash.set('Filen är för stor - försök igen', 'error');
      }
    };

    uploader.onAfterAddingFile = function(fileItem) {
      uploader.currentFileName = fileItem.file.name;
      scope.validationErrors = null;
      scope.errorInvalidFile = null;
      if (autoUpload) {
        uploader.uploadAll();
      }
    };

    uploader.onInterruptedItem = function(fileItem, response, status, headers) {
      Flash.set('Uppladdningsfel', 'error');
    };

    uploader.onSuccessItem = function(fileItem, response, status, headers) {
      var blobId = response.blobId;
      scope.metadata = response;

      // Backend is not returning lastModified on create but later
      // when the file is queried, thus added for consistency
      scope.metadata.lastModified = new Date();

      // API will parse the uploaded file content which might take
      // up to ~30s
      if (validateUpload) {
        uploader.isValidating = true;
        var validationUrl = uploadUrl + '/' + blobId + '/validate';
        FileValidatorPolling.start(validationUrl)
          .then(
            function(response) {
              if (response.status === 'ok') {
                scope.blobId = blobId;
                scope.validationResponse = response;
                scope.validationResponse.blobId = blobId;
              } else if (response.status === 'error') {
                // $watch validationErrors from parent controller
                // and show i.e in a modal
                scope.validationErrors = response;
                scope.validationErrors.blobId = blobId;
                uploader.cancelUpload();
              }
            },
            function(reason) {
              Flash.set('Uppladdningsfel', 'error');
            }
          )
          .finally(function() {
            uploader.isValidating = false;
          });
      } else {
        scope.blobId = response.blobId; // this flow without validation is not currently used
      }
    };

    uploader.onBeforeUploadItem = function(fileItem) {
      if (scope.type) {
        fileItem.url = fileItem.url + '?type=' + scope.type;
      }
      if (scope.state) {
        fileItem.url = fileItem.url + '?state=' + scope.state;
      }
    };

    uploader.onErrorItem = function(fileItem, response, status, headers) {
      Flash.set('Uppladdningsfel', 'error');
    };

    uploader.onCancelItem = function(fileItem, response, status, headers) {
      uploader.clearQueue();
    };

    uploader.cancelUpload = function() {
      FileValidatorPolling.stop(); // might be running
      uploader.isValidating = false;
      scope.blobId = null;
      uploader.currentFileName = null;
      uploader.cancelAll();
      uploader.clearQueue();
    };
  }
}

})();
(function () {
'use strict';

// Certain uploaded files are parsed and validated by API. I.e is a
// Excel-file missing a column. The parsing might take some time (~30s),
// this service can be used to poll the API in a defined interval
// The service returns a promise

FileValidatorPolling.$inject = ["$http", "$timeout", "$q"];
angular.module('fileValidatorPolling', []).factory('FileValidatorPolling', FileValidatorPolling);

function FileValidatorPolling($http, $timeout, $q) {
  var stopNow;

  var service = {
    start: start,
    stop: stop
  };

  return service;

  ////////////

  function start(url) {
    stopNow = false;

    var delay = 5000; // 5 seconds
    var maxRetries = 60; // 60 * 5000 = 5 minutes
    var retries = 0;

    return validate(url, delay, retries, maxRetries);
  }

  // Stop eventually... (Keep It Simple Stupid)
  function stop() {
    stopNow = true;
  }

  function delayer(delay) {
    delay += Math.floor(Math.random() * 1000); // avoid synchronized waves
    return $timeout(function() {}, delay);
  }

  function fetch(url) {
    return $http
      .get(url, {
        timeout: 15000, // dont be stuck in pending longer than 15 sec if loosing internet connection
        withCredentials: true
      })
      .then(function(response) {
        return response.data;
      });
  }

  function validate(url, delay, retries, maxRetries) {
    return delayer(delay)
      .then(function() {
        if (stopNow) {
          return { status: 'UserAbort' };
        } else if (retries >= maxRetries) {
          return $q.reject('MaxRetries');
        } else {
          return fetch(url);
        }
      })
      .then(
        function(validation) {
          if (validation.status === 'ok' || validation.status === 'error') {
            return validation;
          } else if (validation.status === 'UserAbort') {
            return validation.status; // not used at the moment
          } else {
            return validate(url, delay, ++retries, maxRetries);
          }
        },
        function(reason) {
          if (reason === 'MaxRetries') {
            return $q.reject(reason);
          } else {
            return validate(url, delay, ++retries, maxRetries);
          }
        }
      );
  }
}

})();
(function () {
'use strict';

// @desc Part of the layout
//
// @example
//   <body>
//      <hy-footer></hy-footer>
//   </body>

footer.$inject = ["PortalSettings"];
angular.module('hyresgastApp').directive('hyFooter', footer);

function footer(PortalSettings) {
  var directive = {
    templateUrl: 'components/footer/footer.html',
    scope: {
      email: '@',
      phone: '@',
      phoneDeGapped: '@',
      hours: '@'
    },
    link: function (scope) {
      if ((!scope.email) || (!scope.phone) || (!scope.hours)) {
        scope.email = 'forhandlarportalen@hyresgastforeningen.se';
        scope.phone = '0046104591050';
        scope.hours = 'öppet Mån-Tors kl 08:00-15:00, Fre kl 08:00-13:00';

        PortalSettings.getFooterInfo(response => {
          scope.email = response.footerEmail;
          scope.phone = response.footerPhone;
          scope.phoneDeGapped = scope.phone.replace(/-/g, '');
          scope.hours = response.footerHours;
        });
      }
    },
    restrict: 'E'
  };
  return directive;
}

})();
(function () {
'use strict';

FileUploadModalCtrl.$inject = ["$uibModalInstance", "$scope", "$routeParams", "settings"];
angular.module('hyresgastApp').controller('FileUploadModalCtrl', FileUploadModalCtrl);

function FileUploadModalCtrl($uibModalInstance, $scope, $routeParams, settings) {
  var vm = this;
  vm.cancel = cancel;

  // Use settings to make modal reusable
  // {title: 'Upload file', uploadUrl: '/documents', validFileTypes: 'jpg gif'}
  vm.settings = settings;
  vm.settings.splitedValidFileTypes = settings.validFileTypes.split(' ');

  if (settings.types.length === 1) {
    vm.file = {};
    vm.file.type = settings.types[0].id;
    vm.selectedTypeName = settings.types[0].name;
  }

  $scope.$watch('vm.file.blobId', function(newValue) {
    if (newValue) {
      $uibModalInstance.close(vm.metadata);
    }
  });

  /////////

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

goButton.$inject = ["$location"];
angular.module('hyresgastApp').directive('hyGobutton', goButton);

function goButton($location) {
  var directive = {
    scope: {
      class: '@',
      destination: '@',
      caption: '@',
    },
    restrict: 'E',
    templateUrl: 'components/gobutton/gobutton.html',
    link: link
  };

  return directive;

  function link(scope) {
    scope.go = function() {
      $location.path(scope.destination);
    }
  }
}

})();
(function () {
'use strict';

/* jshint -W106 */
/* jshint -W109 */
/* jshint -W016 */
/* jshint -W116 */
/* jshint -W101 */

angular.module(
  'ngLocale',
  [],
  [
    '$provide',
    function($provide) {
      var PLURAL_CATEGORY = { ZERO: 'zero', ONE: 'one', TWO: 'two', FEW: 'few', MANY: 'many', OTHER: 'other' };
      function getDecimals(n) {
        n = n + '';
        var i = n.indexOf('.');
        return i == -1 ? 0 : n.length - i - 1;
      }

      function getVF(n, opt_precision) {
        var v = opt_precision;

        if (undefined === v) {
          v = Math.min(getDecimals(n), 3);
        }

        var base = Math.pow(10, v);
        var f = ((n * base) | 0) % base;
        return { v: v, f: f };
      }

      $provide.value('$locale', {
        DATETIME_FORMATS: {
          AMPMS: ['fm', 'em'],
          DAY: ['s\u00f6ndag', 'm\u00e5ndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'l\u00f6rdag'],
          ERANAMES: ['f\u00f6re Kristus', 'efter Kristus'],
          ERAS: ['f.Kr.', 'e.Kr.'],
          FIRSTDAYOFWEEK: 0,
          MONTH: [
            'januari',
            'februari',
            'mars',
            'april',
            'maj',
            'juni',
            'juli',
            'augusti',
            'september',
            'oktober',
            'november',
            'december'
          ],
          SHORTDAY: ['s\u00f6n', 'm\u00e5n', 'tis', 'ons', 'tors', 'fre', 'l\u00f6r'],
          SHORTMONTH: ['jan.', 'feb.', 'mars', 'apr.', 'maj', 'juni', 'juli', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'],
          STANDALONEMONTH: [
            'januari',
            'februari',
            'mars',
            'april',
            'maj',
            'juni',
            'juli',
            'augusti',
            'september',
            'oktober',
            'november',
            'december'
          ],
          WEEKENDRANGE: [5, 6],
          fullDate: 'EEEE d MMMM y',
          longDate: 'd MMMM y',
          medium: 'd MMM y HH:mm:ss',
          mediumDate: 'd MMM y',
          mediumTime: 'HH:mm:ss',
          short: 'y-MM-dd HH:mm',
          shortDate: 'y-MM-dd',
          shortTime: 'HH:mm'
        },
        NUMBER_FORMATS: {
          CURRENCY_SYM: 'kr',
          DECIMAL_SEP: ',',
          GROUP_SEP: '\u00a0',
          PATTERNS: [
            {
              gSize: 3,
              lgSize: 3,
              maxFrac: 3,
              minFrac: 0,
              minInt: 1,
              negPre: '-',
              negSuf: '',
              posPre: '',
              posSuf: ''
            },
            {
              gSize: 3,
              lgSize: 3,
              maxFrac: 2,
              minFrac: 2,
              minInt: 1,
              negPre: '-',
              negSuf: '\u00a0\u00a4',
              posPre: '',
              posSuf: '\u00a0\u00a4'
            }
          ]
        },
        id: 'sv-se',
        localeID: 'sv_SE',
        pluralCat: function(n, opt_precision) {
          var i = n | 0;
          var vf = getVF(n, opt_precision);
          if (i == 1 && vf.v == 0) {
            return PLURAL_CATEGORY.ONE;
          }
          return PLURAL_CATEGORY.OTHER;
        }
      });
    }
  ]
);

/* jshint +W106 */
/* jshint +W109 */
/* jshint +W016 */
/* jshint +W116 */
/* jshint +W101 */

})();
(function () {
'use strict';

header.$inject = ["$location"];
angular.module('hyresgastApp').directive('hyHeader', header);

function header($location) {
  var directive = {
    templateUrl: 'components/header/header.html',
    restrict: 'E',
    scope: {
      title: '@',
      tabs: '=',
      buttons: '=',
      buttonPaneCols: '=',
    },
    link: scope => {
      scope.getLeftCols = () => scope.buttonPaneCols ? 12 - scope.buttonPaneCols : 6;
      scope.getRightCols = () => scope.buttonPaneCols ? scope.buttonPaneCols : 6;
      scope.getWidth = button => ` calc(${button.grid}% - 7px)`;
      scope.go = button => button.click ? button.click() : $location.url(button.url);
    },
  };
  return directive;
}
})();
(function () {
'use strict';

HelpModal.$inject = ["$uibModalInstance", "settings"];
angular.module('hyresgastApp').controller('HelpModalCtrl', HelpModal);

function HelpModal($uibModalInstance, settings) {
  var vm = this;
  vm.cancel = cancel;
  vm.confirm = confirm;
  vm.title = settings.title;
  vm.body = settings.body;
  /////////

  function confirm() {
    $uibModalInstance.close(true);
  }

  function cancel() {
    $uibModalInstance.close();
  }
}

})();
(function () {
'use strict';

/*
  Used to render a simple help modal. Inject in controller and call renderHelpModal() with
  a title and a body. Example:
  Help.renderHelpModal('Help title', 'Help text that explains something');
*/

angular.module('hyresgastApp').factory('Help', ["$uibModal", function($uibModal) {
  this.renderHelpModal = function(title, body) {
    var settings = {
      title: title,
      body: body
    };
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'md',
      index: 1075,
      templateUrl: 'components/help-modal/helpModal.html',
      controller: 'HelpModalCtrl',
      controllerAs: 'vm',
      resolve: {
        settings: function() {
          return settings;
        }
      }
    });
  };
  return this;
}]);

})();
(function () {
'use strict';

// @desc Simple info block to limit duplication and increase
//   readabillity of html
//
// @example
//   <div>
//      <hy-info title="'Foo'" content="'bar'"></hy-info>
//   </div>

angular.module('hyresgastApp').directive('hyInfo', info);

function info() {
  var directive = {
    templateUrl: 'components/info/info.html',
    restrict: 'E',
    scope: {
      title: '@',
      content: '='
    }
  };
  return directive;
}

})();
(function () {
'use strict';

angular
  .module('uiMultiMap', [])
  /**
   * A helper, internal data structure that stores all references attached to key
   */
  .factory('$$multiMap', function() {
    return {
      createNew: function() {
        var map = {};

        return {
          entries: function() {
            return Object.keys(map).map(function(key) {
              return {
                key: key,
                value: map[key]
              };
            });
          },
          get: function(key) {
            return map[key];
          },
          hasKey: function(key) {
            return !!map[key];
          },
          keys: function() {
            return Object.keys(map);
          },
          put: function(key, value) {
            if (!map[key]) {
              map[key] = [];
            }

            map[key].push(value);
          },
          remove: function(key, value) {
            var values = map[key];

            if (!values) {
              return;
            }

            var idx = values.indexOf(value);

            if (idx !== -1) {
              values.splice(idx, 1);
            }

            if (!values.length) {
              delete map[key];
            }
          }
        };
      }
    };
  });

})();
(function () {
// Avoiding use of ng-class as it creates a lot of watchers when a class is to be applied to
// at most one element.
angular.module('uiIsClass', []).directive('uibIsClass', ["$animate", function($animate) {
  //                    11111111          22222222
  var ON_REGEXP = /^\s*([\s\S]+?)\s+on\s+([\s\S]+?)\s*$/;
  //                    11111111           22222222
  var IS_REGEXP = /^\s*([\s\S]+?)\s+for\s+([\s\S]+?)\s*$/;

  var dataPerTracked = {};

  return {
    restrict: 'A',
    compile: function(tElement, tAttrs) {
      var linkedScopes = [];
      var instances = [];
      var expToData = {};
      var lastActivated = null;
      var onExpMatches = tAttrs.uibIsClass.match(ON_REGEXP);
      var onExp = onExpMatches[2];
      var expsStr = onExpMatches[1];
      var exps = expsStr.split(',');

      return linkFn;

      function linkFn(scope, element, attrs) {
        linkedScopes.push(scope);
        instances.push({
          scope: scope,
          element: element
        });

        exps.forEach(function(exp, k) {
          addForExp(exp, scope);
        });

        scope.$on('$destroy', removeScope);
      }

      function addForExp(exp, scope) {
        var matches = exp.match(IS_REGEXP);
        var clazz = scope.$eval(matches[1]);
        var compareWithExp = matches[2];
        var data = expToData[exp];
        if (!data) {
          var watchFn = function(compareWithVal) {
            var newActivated = null;
            instances.some(function(instance) {
              var thisVal = instance.scope.$eval(onExp);
              if (thisVal === compareWithVal) {
                newActivated = instance;
                return true;
              }
            });
            if (data.lastActivated !== newActivated) {
              if (data.lastActivated) {
                $animate.removeClass(data.lastActivated.element, clazz);
              }
              if (newActivated) {
                $animate.addClass(newActivated.element, clazz);
              }
              data.lastActivated = newActivated;
            }
          };
          expToData[exp] = data = {
            lastActivated: null,
            scope: scope,
            watchFn: watchFn,
            compareWithExp: compareWithExp,
            watcher: scope.$watch(compareWithExp, watchFn)
          };
        }
        data.watchFn(scope.$eval(compareWithExp));
      }

      function removeScope(e) {
        var removedScope = e.targetScope;
        var index = linkedScopes.indexOf(removedScope);
        linkedScopes.splice(index, 1);
        instances.splice(index, 1);
        if (linkedScopes.length) {
          var newWatchScope = linkedScopes[0];
          angular.forEach(expToData, function(data) {
            if (data.scope === removedScope) {
              data.watcher = newWatchScope.$watch(data.compareWithExp, data.watchFn);
              data.scope = newWatchScope;
            }
          });
        } else {
          expToData = {};
        }
      }
    }
  };
}]);

})();
(function () {
'use strict';
angular.module('reportMultiSelect', ['uiModal'])
  .directive('reportMultiSelect', ['$uibModal', ($uibModal) => {
    return {
      scope: {
        param: '=',
        paramDictionary: '=',
        title: '@',
        idProperty: '@',
        textProperty: '@',
        selectionDone: '&',
      },
      restrict: 'E',
      templateUrl: 'components/multi-select/multi-select.html',
      link: function (scope) {
        scope.beginSelection = function () {
          var dataEmpty = !scope.param.data || scope.param.data === -1;
          var selections = dataEmpty ? [] : scope.param.data.map(item => item[scope.idProperty]);
          $uibModal.open({
            animation: true,
            ariaLabelledBy: 'modal-title-top',
            ariaDescribedBy: 'modal-body-top',
            size: 'lg',
            index: 1075,
            templateUrl: 'components/multi-select/multi-select-modal.html',
            windowClass: 'modal-content--white-border',
            backdrop: 'static',
            controller: 'MultiSelectModalCtrl',
            controllerAs: 'vm',
            resolve: {
              title: () => scope.title,
              allOmit: () => scope.param.allOmit,
              selections: () => selections,
              groups: () => scope.paramDictionary,
              idProperty: () => scope.idProperty,
              textProperty: () => scope.textProperty
            }
          }).result.then(function(res) {
            let result = Object.values(scope.paramDictionary)
              .flat()
              .filter(item => res.includes(item[scope.idProperty].toString()));
            scope.param.data = result;
            scope.param.isSet = true;
            scope.selectionDone();
          });
        }
      },
    };
  }]);
})();
(function () {
'use strict';
MultiSelectModalCtrl.$inject = ["$uibModalInstance", "groups", "selections", "allOmit", "idProperty", "textProperty", "title"];
angular.module('hyresgastApp').controller('MultiSelectModalCtrl', MultiSelectModalCtrl);

function MultiSelectModalCtrl($uibModalInstance, groups, selections, allOmit, idProperty, textProperty, title) {

  var vm = this;

  vm.groups = groups;
  vm.title = title;
  vm.idProperty = idProperty;
  vm.textProperty = textProperty;

  var omitted = value => allOmit && selections.length === 0 ? true : value;

  vm.selection = {};
  vm.selectionGroups = {};
  Object.keys(groups).forEach(key => {
    groups[key].forEach(value => {
      let checkboxKey = value[idProperty];
      vm.selection[checkboxKey] = {
        ticked: omitted(selections.includes(checkboxKey)),
        group: key,
      };
    });
    vm.selectionGroups[key] = omitted(selections.containsArray(groups[key].map(item => item[idProperty])));
  });

  let getCheckboxes = groupKey => Object.keys(vm.selection).filter(chkKey => vm.selection[chkKey].group === groupKey);

  vm.toggleGroup = (key, newValue) => {
    getCheckboxes(key).forEach(chkKey => vm.selection[chkKey].ticked = newValue);
  };

  vm.maybeResetGroup = (groupKey, key, newValue) => {
    if (!newValue) {
      vm.selectionGroups[groupKey] = false;
    // eslint-disable-next-line eqeqeq
    } else if (getCheckboxes(groupKey).every(chk => chk == key || vm.selection[chk].ticked)) {
      vm.selectionGroups[groupKey] = true;
    }
  };

  vm.ok = () => {
    let result = Object.keys(vm.selection).filter(checkboxKey => vm.selection[checkboxKey].ticked);
    $uibModalInstance.close(result);
  };
}
})();
(function () {
'use strict';

omniscrollbar.$inject = ["$document", "$interval"];
angular.module('hyresgastApp').directive('omniscroller', omniscroller);
angular.module('hyresgastApp').directive('omniscrollbar', omniscrollbar);

function omniscroller() {
  return {
    restrict: 'E',
    transclude: true,
    templateUrl: 'components/omniscroller/omniscroller.html',
    scope: {
      active: '<',
    }
  };
}

function omniscrollbar($document, $interval) {
  return {
    restrict: 'E',
    transclude: true,
    templateUrl: 'components/omniscroller/omniscrollbar.html',
    scope: {
      scopeId: '=?',
    },
    link: function ($scope, $element) {
      $scope.isSet = false;
      $interval(function () {
        if ($scope.isSet) {
          $scope.contentWidth = $scope.contentsEl[0].scrollWidth;
          return;
        }

        let prefix = ($scope.scopeId && $scope.scopeId.length) ? `#${$scope.scopeId} > ` : '';

        $scope.scrollbarEl = $element[0].querySelector(prefix + '.omniscroller-scrollbar');
        $scope.contentsEl = $document[0].querySelectorAll(prefix + '.omniscroller-content');

        if ($scope.scrollbarEl && $scope.contentsEl) {
          $scope.scrollbarEl.addEventListener('scroll', function () {
            for (let content of $scope.contentsEl) {
              content.style.transform = `translateX(-${$scope.scrollbarEl.scrollLeft}px)`;
            }
          });

          $scope.isSet = true;
        }
      }, 300);
    }
  };
}
})();
(function () {
/* eslint-disable no-undefined */
'use strict';

//TODO: move to fe-common with apartment-selector-2

multicombobox.$inject = ["$timeout"];
angular.module('hyresgastApp').directive('hyMulticombobox', multicombobox);

function multicombobox($timeout) {
  var directive = {
    scope: {
      items: '<',
      idProperty: '@',
      textProperty: '@',
      prompt: '@',
      buttonText: '@',
      stickyPrompt: '<',
      currentItemsChanged: '&',
      currentItems: '=?',
    },
    restrict: 'E',
    templateUrl: 'components/multicombobox/multicombobox.html',
    link: link
  };

  return directive;

  function getPresentedSelection(selection, idProperty, items) {
    let presentedSelection = idProperty && idProperty.length
      // eslint-disable-next-line eqeqeq
      ? Object.keys(selection).filter(x => selection[x] === true).map(x => items.find(xi => xi[idProperty] == x))
      : Object.keys(selection).filter(x => selection[x] === true);
    return presentedSelection;
  }
  function link(scope) {
    // eslint-disable-next-line no-undef
    scope.uuid = crypto.randomUUID();
    scope.selection = {};
    scope.totalSelected = 0;
    scope.presentSelection = function() {
      let selection = getPresentedSelection(scope.selection, scope.idProperty, scope.items);
      scope.currentItems = selection;
      $timeout(() => { scope.currentItemsChanged({ selection }), 200 });
    };
    scope.updateTotal = function() {
      $timeout(() => {
        scope.totalSelected = getPresentedSelection(scope.selection, scope.idProperty, scope.items).length;
      }, 200);
    };
    let setSelection = () => {
      if (scope.items && !!scope.items.length &&
          scope.currentItems && !!scope.currentItems.length) {
        scope.selection = {};
        scope.currentItems.forEach(ci => {
          let key = (scope.idProperty && scope.idProperty.length)
            ? ci[scope.idProperty]
            : ci;
          scope.selection[key] = true;
        });
        scope.updateTotal();
      }
    };
    scope.$watch('items', () => {
      setSelection();
    });
    scope.$watch('currentItems', newValue => {
      if (!!newValue) {
        setSelection();
      } else {
        Object.keys(scope.selection).map(key => {
          scope.selection[key] = false;
        }); 
        scope.updateTotal();
      }
    });
  }
}
})();
(function () {
'use strict';

/* jshint -W074 */
/* jshint -W101 */

angular
  .module('uiPosition', [])

  /**
   * A set of utility methods for working with the DOM.
   * It is meant to be used where we need to absolute-position elements in
   * relation to another element (this is the case for tooltips, popovers,
   * typeahead suggestions etc.).
   */
  .factory('$uibPosition', ["$document", "$window", function($document, $window) {
    /**
     * Used by scrollbarWidth() function to cache scrollbar's width.
     * Do not access this variable directly, use scrollbarWidth() instead.
     */
    var SCROLLBAR_WIDTH;
    /**
     * scrollbar on body and html element in IE and Edge overlay
     * content and should be considered 0 width.
     */
    var BODY_SCROLLBAR_WIDTH;
    var OVERFLOW_REGEX = {
      normal: /(auto|scroll)/,
      hidden: /(auto|scroll|hidden)/
    };
    var PLACEMENT_REGEX = {
      auto: /\s?auto?\s?/i,
      primary: /^(top|bottom|left|right)$/,
      secondary: /^(top|bottom|left|right|center)$/,
      vertical: /^(top|bottom)$/
    };
    var BODY_REGEX = /(HTML|BODY)/;

    return {
      /**
       * Provides a raw DOM element from a jQuery/jQLite element.
       *
       * @param {element} elem - The element to convert.
       *
       * @returns {element} A HTML element.
       */
      getRawNode: function(elem) {
        return elem.nodeName ? elem : elem[0] || elem;
      },

      /**
       * Provides a parsed number for a style property.  Strips
       * units and casts invalid numbers to 0.
       *
       * @param {string} value - The style value to parse.
       *
       * @returns {number} A valid number.
       */
      parseStyle: function(value) {
        value = parseFloat(value);
        return isFinite(value) ? value : 0;
      },

      /**
       * Provides the closest positioned ancestor.
       *
       * @param {element} element - The element to get the offest parent for.
       *
       * @returns {element} The closest positioned ancestor.
       */
      offsetParent: function(elem) {
        elem = this.getRawNode(elem);

        var offsetParent = elem.offsetParent || $document[0].documentElement;

        function isStaticPositioned(el) {
          return ($window.getComputedStyle(el).position || 'static') === 'static';
        }

        while (offsetParent && offsetParent !== $document[0].documentElement && isStaticPositioned(offsetParent)) {
          offsetParent = offsetParent.offsetParent;
        }

        return offsetParent || $document[0].documentElement;
      },

      /**
       * Provides the scrollbar width, concept from TWBS measureScrollbar()
       * function in https://github.com/twbs/bootstrap/blob/master/js/modal.js
       * In IE and Edge, scollbar on body and html element overlay and should
       * return a width of 0.
       *
       * @returns {number} The width of the browser scollbar.
       */
      scrollbarWidth: function(isBody) {
        if (isBody) {
          if (angular.isUndefined(BODY_SCROLLBAR_WIDTH)) {
            var bodyElem = $document.find('body');
            bodyElem.addClass('uib-position-body-scrollbar-measure');
            BODY_SCROLLBAR_WIDTH = $window.innerWidth - bodyElem[0].clientWidth;
            BODY_SCROLLBAR_WIDTH = isFinite(BODY_SCROLLBAR_WIDTH) ? BODY_SCROLLBAR_WIDTH : 0;
            bodyElem.removeClass('uib-position-body-scrollbar-measure');
          }
          return BODY_SCROLLBAR_WIDTH;
        }

        if (angular.isUndefined(SCROLLBAR_WIDTH)) {
          var scrollElem = angular.element('<div class="uib-position-scrollbar-measure"></div>');
          $document.find('body').append(scrollElem);
          SCROLLBAR_WIDTH = scrollElem[0].offsetWidth - scrollElem[0].clientWidth;
          SCROLLBAR_WIDTH = isFinite(SCROLLBAR_WIDTH) ? SCROLLBAR_WIDTH : 0;
          scrollElem.remove();
        }

        return SCROLLBAR_WIDTH;
      },

      /**
       * Provides the padding required on an element to replace the scrollbar.
       *
       * @returns {object} An object with the following properties:
       *   <ul>
       *     <li>**scrollbarWidth**: the width of the scrollbar</li>
       *     <li>**widthOverflow**: whether the the width is overflowing</li>
       *     <li>**right**: the amount of right padding on the element needed to replace the scrollbar</li>
       *     <li>**rightOriginal**: the amount of right padding currently on the element</li>
       *     <li>**heightOverflow**: whether the the height is overflowing</li>
       *     <li>**bottom**: the amount of bottom padding on the element needed to replace the scrollbar</li>
       *     <li>**bottomOriginal**: the amount of bottom padding currently on the element</li>
       *   </ul>
       */
      scrollbarPadding: function(elem) {
        elem = this.getRawNode(elem);

        var elemStyle = $window.getComputedStyle(elem);
        var paddingRight = this.parseStyle(elemStyle.paddingRight);
        var paddingBottom = this.parseStyle(elemStyle.paddingBottom);
        var scrollParent = this.scrollParent(elem, false, true);
        var scrollbarWidth = this.scrollbarWidth(BODY_REGEX.test(scrollParent.tagName));

        return {
          scrollbarWidth: scrollbarWidth,
          widthOverflow: scrollParent.scrollWidth > scrollParent.clientWidth,
          right: paddingRight + scrollbarWidth,
          originalRight: paddingRight,
          heightOverflow: scrollParent.scrollHeight > scrollParent.clientHeight,
          bottom: paddingBottom + scrollbarWidth,
          originalBottom: paddingBottom
        };
      },

      /**
       * Checks to see if the element is scrollable.
       *
       * @param {element} elem - The element to check.
       * @param {boolean=} [includeHidden=false] - Should scroll style of 'hidden' be considered,
       *   default is false.
       *
       * @returns {boolean} Whether the element is scrollable.
       */
      isScrollable: function(elem, includeHidden) {
        elem = this.getRawNode(elem);

        var overflowRegex = includeHidden ? OVERFLOW_REGEX.hidden : OVERFLOW_REGEX.normal;
        var elemStyle = $window.getComputedStyle(elem);
        return overflowRegex.test(elemStyle.overflow + elemStyle.overflowY + elemStyle.overflowX);
      },

      /**
       * Provides the closest scrollable ancestor.
       * A port of the jQuery UI scrollParent method:
       * https://github.com/jquery/jquery-ui/blob/master/ui/scroll-parent.js
       *
       * @param {element} elem - The element to find the scroll parent of.
       * @param {boolean=} [includeHidden=false] - Should scroll style of 'hidden' be considered,
       *   default is false.
       * @param {boolean=} [includeSelf=false] - Should the element being passed be
       * included in the scrollable llokup.
       *
       * @returns {element} A HTML element.
       */
      scrollParent: function(elem, includeHidden, includeSelf) {
        elem = this.getRawNode(elem);

        var overflowRegex = includeHidden ? OVERFLOW_REGEX.hidden : OVERFLOW_REGEX.normal;
        var documentEl = $document[0].documentElement;
        var elemStyle = $window.getComputedStyle(elem);
        if (includeSelf && overflowRegex.test(elemStyle.overflow + elemStyle.overflowY + elemStyle.overflowX)) {
          return elem;
        }
        var excludeStatic = elemStyle.position === 'absolute';
        var scrollParent = elem.parentElement || documentEl;

        if (scrollParent === documentEl || elemStyle.position === 'fixed') {
          return documentEl;
        }

        while (scrollParent.parentElement && scrollParent !== documentEl) {
          var spStyle = $window.getComputedStyle(scrollParent);
          if (excludeStatic && spStyle.position !== 'static') {
            excludeStatic = false;
          }

          if (!excludeStatic && overflowRegex.test(spStyle.overflow + spStyle.overflowY + spStyle.overflowX)) {
            break;
          }
          scrollParent = scrollParent.parentElement;
        }

        return scrollParent;
      },

      /**
       * Provides read-only equivalent of jQuery's position function:
       * http://api.jquery.com/position/ - distance to closest positioned
       * ancestor.  Does not account for margins by default like jQuery position.
       *
       * @param {element} elem - The element to caclulate the position on.
       * @param {boolean=} [includeMargins=false] - Should margins be accounted
       * for, default is false.
       *
       * @returns {object} An object with the following properties:
       *   <ul>
       *     <li>**width**: the width of the element</li>
       *     <li>**height**: the height of the element</li>
       *     <li>**top**: distance to top edge of offset parent</li>
       *     <li>**left**: distance to left edge of offset parent</li>
       *   </ul>
       */
      position: function(elem, includeMagins) {
        elem = this.getRawNode(elem);

        var elemOffset = this.offset(elem);
        if (includeMagins) {
          var elemStyle = $window.getComputedStyle(elem);
          elemOffset.top -= this.parseStyle(elemStyle.marginTop);
          elemOffset.left -= this.parseStyle(elemStyle.marginLeft);
        }
        var parent = this.offsetParent(elem);
        var parentOffset = { top: 0, left: 0 };

        if (parent !== $document[0].documentElement) {
          parentOffset = this.offset(parent);
          parentOffset.top += parent.clientTop - parent.scrollTop;
          parentOffset.left += parent.clientLeft - parent.scrollLeft;
        }

        return {
          width: Math.round(angular.isNumber(elemOffset.width) ? elemOffset.width : elem.offsetWidth),
          height: Math.round(angular.isNumber(elemOffset.height) ? elemOffset.height : elem.offsetHeight),
          top: Math.round(elemOffset.top - parentOffset.top),
          left: Math.round(elemOffset.left - parentOffset.left)
        };
      },

      /**
       * Provides read-only equivalent of jQuery's offset function:
       * http://api.jquery.com/offset/ - distance to viewport.  Does
       * not account for borders, margins, or padding on the body
       * element.
       *
       * @param {element} elem - The element to calculate the offset on.
       *
       * @returns {object} An object with the following properties:
       *   <ul>
       *     <li>**width**: the width of the element</li>
       *     <li>**height**: the height of the element</li>
       *     <li>**top**: distance to top edge of viewport</li>
       *     <li>**right**: distance to bottom edge of viewport</li>
       *   </ul>
       */
      offset: function(elem) {
        elem = this.getRawNode(elem);

        var elemBCR = elem.getBoundingClientRect();
        return {
          width: Math.round(angular.isNumber(elemBCR.width) ? elemBCR.width : elem.offsetWidth),
          height: Math.round(angular.isNumber(elemBCR.height) ? elemBCR.height : elem.offsetHeight),
          top: Math.round(elemBCR.top + ($window.pageYOffset || $document[0].documentElement.scrollTop)),
          left: Math.round(elemBCR.left + ($window.pageXOffset || $document[0].documentElement.scrollLeft))
        };
      },

      /**
       * Provides offset distance to the closest scrollable ancestor
       * or viewport.  Accounts for border and scrollbar width.
       *
       * Right and bottom dimensions represent the distance to the
       * respective edge of the viewport element.  If the element
       * edge extends beyond the viewport, a negative value will be
       * reported.
       *
       * @param {element} elem - The element to get the viewport offset for.
       * @param {boolean=} [useDocument=false] - Should the viewport be the document element instead
       * of the first scrollable element, default is false.
       * @param {boolean=} [includePadding=true] - Should the padding on the offset parent element
       * be accounted for, default is true.
       *
       * @returns {object} An object with the following properties:
       *   <ul>
       *     <li>**top**: distance to the top content edge of viewport element</li>
       *     <li>**bottom**: distance to the bottom content edge of viewport element</li>
       *     <li>**left**: distance to the left content edge of viewport element</li>
       *     <li>**right**: distance to the right content edge of viewport element</li>
       *   </ul>
       */
      viewportOffset: function(elem, useDocument, includePadding) {
        elem = this.getRawNode(elem);
        includePadding = includePadding !== false ? true : false;

        var elemBCR = elem.getBoundingClientRect();
        var offsetBCR = { top: 0, left: 0, bottom: 0, right: 0 };

        var offsetParent = useDocument ? $document[0].documentElement : this.scrollParent(elem);
        var offsetParentBCR = offsetParent.getBoundingClientRect();

        offsetBCR.top = offsetParentBCR.top + offsetParent.clientTop;
        offsetBCR.left = offsetParentBCR.left + offsetParent.clientLeft;
        if (offsetParent === $document[0].documentElement) {
          offsetBCR.top += $window.pageYOffset;
          offsetBCR.left += $window.pageXOffset;
        }
        offsetBCR.bottom = offsetBCR.top + offsetParent.clientHeight;
        offsetBCR.right = offsetBCR.left + offsetParent.clientWidth;

        if (includePadding) {
          var offsetParentStyle = $window.getComputedStyle(offsetParent);
          offsetBCR.top += this.parseStyle(offsetParentStyle.paddingTop);
          offsetBCR.bottom -= this.parseStyle(offsetParentStyle.paddingBottom);
          offsetBCR.left += this.parseStyle(offsetParentStyle.paddingLeft);
          offsetBCR.right -= this.parseStyle(offsetParentStyle.paddingRight);
        }

        return {
          top: Math.round(elemBCR.top - offsetBCR.top),
          bottom: Math.round(offsetBCR.bottom - elemBCR.bottom),
          left: Math.round(elemBCR.left - offsetBCR.left),
          right: Math.round(offsetBCR.right - elemBCR.right)
        };
      },

      /**
       * Provides an array of placement values parsed from a placement string.
       * Along with the 'auto' indicator, supported placement strings are:
       *   <ul>
       *     <li>top: element on top, horizontally centered on host element.</li>
       *     <li>top-left: element on top, left edge aligned with host element left edge.</li>
       *     <li>top-right: element on top, lerightft edge aligned with host element right edge.</li>
       *     <li>bottom: element on bottom, horizontally centered on host element.</li>
       *     <li>bottom-left: element on bottom, left edge aligned with host element left edge.</li>
       *     <li>bottom-right: element on bottom, right edge aligned with host element right edge.</li>
       *     <li>left: element on left, vertically centered on host element.</li>
       *     <li>left-top: element on left, top edge aligned with host element top edge.</li>
       *     <li>left-bottom: element on left, bottom edge aligned with host element bottom edge.</li>
       *     <li>right: element on right, vertically centered on host element.</li>
       *     <li>right-top: element on right, top edge aligned with host element top edge.</li>
       *     <li>right-bottom: element on right, bottom edge aligned with host element bottom edge.</li>
       *   </ul>
       * A placement string with an 'auto' indicator is expected to be
       * space separated from the placement, i.e: 'auto bottom-left'  If
       * the primary and secondary placement values do not match 'top,
       * bottom, left, right' then 'top' will be the primary placement and
       * 'center' will be the secondary placement.  If 'auto' is passed, true
       * will be returned as the 3rd value of the array.
       *
       * @param {string} placement - The placement string to parse.
       *
       * @returns {array} An array with the following values
       * <ul>
       *   <li>**[0]**: The primary placement.</li>
       *   <li>**[1]**: The secondary placement.</li>
       *   <li>**[2]**: If auto is passed: true, else undefined.</li>
       * </ul>
       */
      parsePlacement: function(placement) {
        var autoPlace = PLACEMENT_REGEX.auto.test(placement);
        if (autoPlace) {
          placement = placement.replace(PLACEMENT_REGEX.auto, '');
        }

        placement = placement.split('-');

        placement[0] = placement[0] || 'top';
        if (!PLACEMENT_REGEX.primary.test(placement[0])) {
          placement[0] = 'top';
        }

        placement[1] = placement[1] || 'center';
        if (!PLACEMENT_REGEX.secondary.test(placement[1])) {
          placement[1] = 'center';
        }

        if (autoPlace) {
          placement[2] = true;
        } else {
          placement[2] = false;
        }

        return placement;
      },

      /**
       * Provides coordinates for an element to be positioned relative to
       * another element.  Passing 'auto' as part of the placement parameter
       * will enable smart placement - where the element fits. i.e:
       * 'auto left-top' will check to see if there is enough space to the left
       * of the hostElem to fit the targetElem, if not place right (same for secondary
       * top placement).  Available space is calculated using the viewportOffset
       * function.
       *
       * @param {element} hostElem - The element to position against.
       * @param {element} targetElem - The element to position.
       * @param {string=} [placement=top] - The placement for the targetElem,
       *   default is 'top'. 'center' is assumed as secondary placement for
       *   'top', 'left', 'right', and 'bottom' placements.  Available placements are:
       *   <ul>
       *     <li>top</li>
       *     <li>top-right</li>
       *     <li>top-left</li>
       *     <li>bottom</li>
       *     <li>bottom-left</li>
       *     <li>bottom-right</li>
       *     <li>left</li>
       *     <li>left-top</li>
       *     <li>left-bottom</li>
       *     <li>right</li>
       *     <li>right-top</li>
       *     <li>right-bottom</li>
       *   </ul>
       * @param {boolean=} [appendToBody=false] - Should the top and left values returned
       *   be calculated from the body element, default is false.
       *
       * @returns {object} An object with the following properties:
       *   <ul>
       *     <li>**top**: Value for targetElem top.</li>
       *     <li>**left**: Value for targetElem left.</li>
       *     <li>**placement**: The resolved placement.</li>
       *   </ul>
       */
      positionElements: function(hostElem, targetElem, placement, appendToBody) {
        hostElem = this.getRawNode(hostElem);
        targetElem = this.getRawNode(targetElem);

        // need to read from prop to support tests.
        var targetWidth = angular.isDefined(targetElem.offsetWidth)
          ? targetElem.offsetWidth
          : targetElem.prop('offsetWidth');
        var targetHeight = angular.isDefined(targetElem.offsetHeight)
          ? targetElem.offsetHeight
          : targetElem.prop('offsetHeight');

        placement = this.parsePlacement(placement);

        var hostElemPos = appendToBody ? this.offset(hostElem) : this.position(hostElem);
        var targetElemPos = { top: 0, left: 0, placement: '' };

        if (placement[2]) {
          var viewportOffset = this.viewportOffset(hostElem, appendToBody);

          var targetElemStyle = $window.getComputedStyle(targetElem);
          var adjustedSize = {
            width:
              targetWidth +
              Math.round(
                Math.abs(this.parseStyle(targetElemStyle.marginLeft) + this.parseStyle(targetElemStyle.marginRight))
              ),
            height:
              targetHeight +
              Math.round(
                Math.abs(this.parseStyle(targetElemStyle.marginTop) + this.parseStyle(targetElemStyle.marginBottom))
              )
          };

          placement[0] =
            placement[0] === 'top' &&
            adjustedSize.height > viewportOffset.top &&
            adjustedSize.height <= viewportOffset.bottom
              ? 'bottom'
              : placement[0] === 'bottom' &&
                adjustedSize.height > viewportOffset.bottom &&
                adjustedSize.height <= viewportOffset.top
                ? 'top'
                : placement[0] === 'left' &&
                  adjustedSize.width > viewportOffset.left &&
                  adjustedSize.width <= viewportOffset.right
                  ? 'right'
                  : placement[0] === 'right' &&
                    adjustedSize.width > viewportOffset.right &&
                    adjustedSize.width <= viewportOffset.left
                    ? 'left'
                    : placement[0];

          placement[1] =
            placement[1] === 'top' &&
            adjustedSize.height - hostElemPos.height > viewportOffset.bottom &&
            adjustedSize.height - hostElemPos.height <= viewportOffset.top
              ? 'bottom'
              : placement[1] === 'bottom' &&
                adjustedSize.height - hostElemPos.height > viewportOffset.top &&
                adjustedSize.height - hostElemPos.height <= viewportOffset.bottom
                ? 'top'
                : placement[1] === 'left' &&
                  adjustedSize.width - hostElemPos.width > viewportOffset.right &&
                  adjustedSize.width - hostElemPos.width <= viewportOffset.left
                  ? 'right'
                  : placement[1] === 'right' &&
                    adjustedSize.width - hostElemPos.width > viewportOffset.left &&
                    adjustedSize.width - hostElemPos.width <= viewportOffset.right
                    ? 'left'
                    : placement[1];

          if (placement[1] === 'center') {
            if (PLACEMENT_REGEX.vertical.test(placement[0])) {
              var xOverflow = hostElemPos.width / 2 - targetWidth / 2;
              if (
                viewportOffset.left + xOverflow < 0 &&
                adjustedSize.width - hostElemPos.width <= viewportOffset.right
              ) {
                placement[1] = 'left';
              } else if (
                viewportOffset.right + xOverflow < 0 &&
                adjustedSize.width - hostElemPos.width <= viewportOffset.left
              ) {
                placement[1] = 'right';
              }
            } else {
              var yOverflow = hostElemPos.height / 2 - adjustedSize.height / 2;
              if (
                viewportOffset.top + yOverflow < 0 &&
                adjustedSize.height - hostElemPos.height <= viewportOffset.bottom
              ) {
                placement[1] = 'top';
              } else if (
                viewportOffset.bottom + yOverflow < 0 &&
                adjustedSize.height - hostElemPos.height <= viewportOffset.top
              ) {
                placement[1] = 'bottom';
              }
            }
          }
        }

        switch (placement[0]) {
          case 'top':
            targetElemPos.top = hostElemPos.top - targetHeight;
            break;
          case 'bottom':
            targetElemPos.top = hostElemPos.top + hostElemPos.height;
            break;
          case 'left':
            targetElemPos.left = hostElemPos.left - targetWidth;
            break;
          case 'right':
            targetElemPos.left = hostElemPos.left + hostElemPos.width;
            break;
        }

        switch (placement[1]) {
          case 'top':
            targetElemPos.top = hostElemPos.top;
            break;
          case 'bottom':
            targetElemPos.top = hostElemPos.top + hostElemPos.height - targetHeight;
            break;
          case 'left':
            targetElemPos.left = hostElemPos.left;
            break;
          case 'right':
            targetElemPos.left = hostElemPos.left + hostElemPos.width - targetWidth;
            break;
          case 'center':
            if (PLACEMENT_REGEX.vertical.test(placement[0])) {
              targetElemPos.left = hostElemPos.left + hostElemPos.width / 2 - targetWidth / 2;
            } else {
              targetElemPos.top = hostElemPos.top + hostElemPos.height / 2 - targetHeight / 2;
            }
            break;
        }

        targetElemPos.top = Math.round(targetElemPos.top);
        targetElemPos.left = Math.round(targetElemPos.left);
        targetElemPos.placement = placement[1] === 'center' ? placement[0] : placement[0] + '-' + placement[1];

        return targetElemPos;
      },

      /**
       * Provides a way to adjust the top positioning after first
       * render to correctly align element to top after content
       * rendering causes resized element height
       *
       * @param {array} placementClasses - The array of strings of classes
       * element should have.
       * @param {object} containerPosition - The object with container
       * position information
       * @param {number} initialHeight - The initial height for the elem.
       * @param {number} currentHeight - The current height for the elem.
       */
      adjustTop: function(placementClasses, containerPosition, initialHeight, currentHeight) {
        if (placementClasses.indexOf('top') !== -1 && initialHeight !== currentHeight) {
          return {
            top: containerPosition.top - currentHeight + 'px'
          };
        }
      },

      /**
       * Provides a way for positioning tooltip & dropdown
       * arrows when using placement options beyond the standard
       * left, right, top, or bottom.
       *
       * @param {element} elem - The tooltip/dropdown element.
       * @param {string} placement - The placement for the elem.
       */
      positionArrow: function(elem, placement) {
        elem = this.getRawNode(elem);

        var innerElem = elem.querySelector('.tooltip-inner, .popover-inner');
        if (!innerElem) {
          return;
        }

        var isTooltip = angular.element(innerElem).hasClass('tooltip-inner');

        var arrowElem = isTooltip ? elem.querySelector('.tooltip-arrow') : elem.querySelector('.arrow');
        if (!arrowElem) {
          return;
        }

        var arrowCss = {
          top: '',
          bottom: '',
          left: '',
          right: ''
        };

        placement = this.parsePlacement(placement);
        if (placement[1] === 'center') {
          // no adjustment necessary - just reset styles
          angular.element(arrowElem).css(arrowCss);
          return;
        }

        var borderProp = 'border-' + placement[0] + '-width';
        var borderWidth = $window.getComputedStyle(arrowElem)[borderProp];

        var borderRadiusProp = 'border-';
        if (PLACEMENT_REGEX.vertical.test(placement[0])) {
          borderRadiusProp += placement[0] + '-' + placement[1];
        } else {
          borderRadiusProp += placement[1] + '-' + placement[0];
        }
        borderRadiusProp += '-radius';
        var borderRadius = $window.getComputedStyle(isTooltip ? innerElem : elem)[borderRadiusProp];

        switch (placement[0]) {
          case 'top':
            arrowCss.bottom = isTooltip ? '0' : '-' + borderWidth;
            break;
          case 'bottom':
            arrowCss.top = isTooltip ? '0' : '-' + borderWidth;
            break;
          case 'left':
            arrowCss.right = isTooltip ? '0' : '-' + borderWidth;
            break;
          case 'right':
            arrowCss.left = isTooltip ? '0' : '-' + borderWidth;
            break;
        }

        arrowCss[placement[1]] = borderRadius;

        angular.element(arrowElem).css(arrowCss);
      }
    };
  }]);

/* jshint +W074 */
/* jshint +W101 */

})();
(function () {
'use strict';

// @desc
// Paging component
//
// @example
//     <hy-paginator [get-list="vm.queryFunc"] OR [enable-param-mode]
//         page-offset="vm.pageOffset"
//         page-limit="vm.pageLimit"
//         total-count="vm.totalCount">
//     </hy-paginator>
//
// Parameters:
//     - vm.queryFunc - reference to controller's query function, which retrieve records from DB
//     - enable-param-mode - use ?offset parameter in URL to pass state btw directive and controller.
//       Works only if controller reloads when changing URL parameters, i.e. reloadOnSearch: true
//     - vm.pageLimit - number of records to be displayed per page
//     - vm.pageOffset - page offset, which is changed by a step of -=/+= vm.pageLimit
//     - vm.totalCount - total number of table rows, returned from backend

paginator.$inject = ["$location", "$timeout"];
angular.module('hyresgastApp').directive('hyPaginator', paginator);

function paginator($location, $timeout) {
  var directive = {
    templateUrl: 'components/paginator/paginator.html',
    restrict: 'E',
    link: link,
    scope: {
      pageOffset: '=',
      pageLimit: '=',
      totalCount: '=',
      getList: '&?' // not needed if using enable-param-mode
    }
  };
  return directive;

  function link(scope, element, attrs) {
    // Setting url parameters to enable browser back/forward buttons
    var enableParamMode = 'enableParamMode' in attrs;

    scope.pageOffset = Number(scope.pageOffset);
    scope.gotoPrevPage = gotoPrevPage;
    scope.gotoNextPage = gotoNextPage;
    scope.firstRecordInCurrentPage = scope.pageOffset + 1;
    scope.computeLastRecord = computeLastRecord;

    function gotoPrevPage() {
      if (scope.firstRecordInCurrentPage > scope.pageLimit) {
        scope.pageOffset = scope.pageOffset - scope.pageLimit;
        if (!enableParamMode) {
          scope.firstRecordInCurrentPage = scope.pageOffset + 1;
          scope.computeLastRecord = computeLastRecord;
        }
        // force angular to update parent scope parameters before calling fetch() function
        $timeout(fetch, 0);
      }
    }

    function gotoNextPage() {
      if (scope.totalCount - scope.firstRecordInCurrentPage >= scope.pageLimit) {
        scope.pageOffset = scope.pageOffset + scope.pageLimit;
        if (!enableParamMode) {
          scope.firstRecordInCurrentPage = scope.pageOffset + 1;
          scope.computeLastRecord = computeLastRecord;
        }
        $timeout(fetch, 0);
      }
    }

    function computeLastRecord() {
      if (scope.totalCount - scope.firstRecordInCurrentPage < scope.pageLimit - 1) {
        return scope.totalCount;
      } else {
        return scope.firstRecordInCurrentPage + scope.pageLimit - 1;
      }
    }

    function fetch() {
      if (enableParamMode) {
        // parent controller will reload and read parameters from URL
        $location.search(angular.extend($location.search(), { offset: scope.pageOffset }));
      } else {
        // not using parameters so call parent controller function
        scope.getList()();
      }
    }

    scope.$watch('pageOffset', function() {
      scope.firstRecordInCurrentPage = scope.pageOffset + 1;
    });
  }
}

})();
(function () {
'use strict';

// @desc
//   Directive is placed inside <main> and will appear when
//   when API returns a 401. It will also hide main content.
//
// @example
//   <main>
//      <hy-resetPassword></hy-resetPassword>
//      <ng-view class="main-content"></ng-view>
//   </main>

ResetPassword.$inject = ["$document", "$rootScope", "$location", "Auth", "User", "Flash"];
angular.module('hyresgastApp').directive('hyResetPassword', ResetPassword);

function ResetPassword($document, $rootScope, $location, Auth, User, Flash) {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/resetPassword/resetPassword.html',
    scope: {},
    link: link
  };
  return directive;

  function link(scope, element, attrs) {
    scope.vm = { email: '' };

    var resetPasswordVisible;
    var mainContent = angular.element($document[0].querySelector('.main-content'));

    scope.resetPassword = resetPassword;

    scope.$on('event:resetPassword', function() {
      show();
    });

    function show() {
      element.addClass('resetPassword--visible');
      mainContent.addClass('main-content--hidden');
    }

    function hide() {
      mainContent.removeClass('main-content--hidden');
    }

    function showLogin() {
      element.removeClass('resetPassword--visible');
      mainContent.addClass('main-content--hidden');
      $rootScope.$broadcast('event:auth-loginRequired', null);
    }

    function resetPassword() {
      scope.saving = true;

      User.resetPassword({
        email: scope.vm.email,
        phone: scope.vm.phone,
      }).$promise.then(
        function(response) {
          Flash.set(
            'Ett mail med en länk för att återställa lösenordet  har skickats till epost-adressen du angav.',
            'success'
          );
        },
        function(reason) {
          Flash.set('Misslyckades. Kontrollera din mailadress.', 'error');
        }
      )
        .finally(function() {
          scope.saving = false;
          scope.vm.resetpasswordForm.$setPristine();
          scope.vm.resetpasswordForm.$setUntouched();
        });
    }
  }
}

})();
(function () {
'use strict';

// @desc
// Progress indeterminate circular element,
// which goal is to show to the user that something
// is in progress of execution and they should wait
// until requested query is processed.
//
// @example
//     <hy-progress-circular size="20px"></hy-progress-circular>

angular.module('hyresgastApp').directive('hyProgressCircular', progressCircular);

function progressCircular() {
  var directive = {
    templateUrl: 'components/progress-circular/progressCircular.html',
    restrict: 'E',
    link: link,
    scope: {
      size: '@'
    }
  };
  return directive;

  function link(scope, element, attrs) {
    if (attrs.hasOwnProperty('centerOnPage')) {
      element.addClass('progress-circular--center-on-page');
    }
    if (attrs.hasOwnProperty('centerOnPageFixed')) {
      element.addClass('progress-circular--center-on-page-fixed');
    }
  }
}

})();
(function () {
'use strict';

// @desc
//   A select all checkbox to handle check all checkboxes and unselect all
//   checkboxes.
//
// @example
//   <hy-select-all-checkbox checkboxes="checkboxes"
//      name="identifier"
//      selected-ids="selectedIds"
//      is-at-least-one-selected="atleastOneSelected"
//      is-master-selected="isMasterSelected"
//      call-on-change="updateCounter()">
//   </hy-select-all-checkbox>

selectAllCheckbox.$inject = ["$location", "$timeout"];
angular
  .module('selectAllCheckbox', []) // TODO add tooltip as a dependency
  .directive('hySelectAllCheckbox', selectAllCheckbox);

function selectAllCheckbox($location, $timeout) {
  var directive = {
    scope: {
      name: '@', // a unique identifier so, if needed, multiple lists can be used
      checkboxes: '=',
      selectedIds: '=',
      isAtLeastOneSelected: '=',
      isMasterSelected: '=',
      isDisabled: '<',
      totalCount: '=',
      callOnChange: '&?' // optional function binding called when a selection changes
    },
    restrict: 'E',
    templateUrl: 'components/select-all-checkbox/select-all-checkbox.html',
    link: link
  };

  return directive;

  function link(scope, element, attrs) {
    scope.toggleSelectMaster = toggleSelectMaster;

    // To add indeterminate property
    var inputElement = element[0].querySelector('input');

    ////////////

    function toggleSelectMaster() {
      angular.forEach(Object.keys(scope.selectedIds), function(id) {
        scope.selectedIds[id] = scope.isMasterSelected;
      });
    }

    scope.$watch('checkboxes', function(newValue) {
      angular.forEach(scope.checkboxes, function(checkbox) {
        if (scope.isMasterSelected && scope.selectedIds[checkbox.id] === undefined) {
          scope.selectedIds[checkbox.id] = true;
        } else if (scope.selectedIds[checkbox.id] === undefined) {
          scope.selectedIds[checkbox.id] = false;
        }
      });
    });

    scope.$watch(
      'selectedIds',
      function(newValue) {
        // if a function has been binded to call on change and newValue is not empty and
        // newValue is not empty object
        if (scope.callOnChange && newValue && !angular.equals(newValue, {})) {
          // wrapped in a timeout so all attributes such as is-at-least-one-selected
          // is having correct state if the called function wants to look at the value
          // of is-at-least-one-selected
          $timeout(function() {
            scope.callOnChange();
          });
        }

        var selectedStates = Object.keys(scope.selectedIds).map(function(id) {
          return scope.selectedIds[id];
        });

        function isFalse(val) {
          return !val;
        }
        function isTrue(val) {
          return Boolean(val);
        }
        var allSelectStatesKnown = selectedStates.length === scope.totalCount;
        var isAllSelected = selectedStates.every(isTrue) && allSelectStatesKnown;
        var isAllCleared = selectedStates.every(isFalse) && allSelectStatesKnown;

        inputElement.indeterminate = false;
        scope.isAtLeastOneSelected = false;
        if (isAllSelected) {
          scope.isMasterSelected = true;
          scope.isAtLeastOneSelected = true;
        } else if (isAllCleared) {
          scope.isMasterSelected = false;
        } else {
          if (
            (!scope.isMasterSelected && selectedStates.some(isTrue)) ||
            (scope.isMasterSelected && selectedStates.some(isFalse))
          ) {
            inputElement.indeterminate = true;
            scope.isAtLeastOneSelected = true;
          } else if (scope.isMasterSelected) {
            scope.isAtLeastOneSelected = true;
          }
        }
      },
      true
    );
  }
}

})();
(function () {
'use strict';

Session.$inject = ["$http"];
angular.module('httpAuthSession', []).factory('Session', Session);

function Session($http) {
  var service = {
    create: create,
    show: show,
    destroy: destroy
  };

  return service;

  ////////////

  function create(credentials) {
    return $http.post('/api/sessions', credentials).then(function(response) {
      return response.data;
    });
  }

  function show(ignoreAuth) {
    var config = {
      url: '/api/sessions/heartbeat',
      method: 'GET',
      ignoreAuthModule: ignoreAuth // true: don't show login page when api returns 401
    };
    return $http(config).then(function(response) {
      return response.data;
    });
  }

  function destroy() {
    return $http.delete('/api/sessions');
  }
}

})();
(function () {
'use strict';

// @desc
//   Sorting component (ASC and DESC order) working on hard-coded table's columns appearing in a dropdown menu
//
// @example
// Use in header <th> column of a table
// <hy-sorter
//   list-columns="vm.listColumns"
//   order = "vm.sortColumn"
//   direction = "vm.sortDirection
//   show-label="true" 
//   label-position="side">
// </hy-sorter>

sorter.$inject = ["$location", "$timeout"];
angular.module('sorter', ['uiDropdown']).directive('hySorter', sorter);

function sorter($location, $timeout) {
  var directive = {
    scope: {
      listColumns: '=',
      order: '=',
      direction: '=',
      skipFetch: '=',
      changed: '&',      
      showLabel: '=?',  
      labelText: '@?', // Optional label other than default
      labelSize: '@?',   
      labelPosition: '@?' // Label position: 'side' or 'top'
    },
    restrict: 'E',
    templateUrl: 'components/sorter/sorter.html',
    link: link
  };
  return directive;

  function link(scope, element, attrs) {
    scope.sort = sort;

    if (scope.listColumns) {
      scope.itemName = scope.listColumns.filter(function(item) {
        return item.column === scope.order;
      })[0].title;
    } else {
      scope.itemName = scope.showLabel ? 'Välj sortering' : 'Sortera på';
    }

    ////////////

    function sort(sortColumn, columnTitle) {
      scope.itemName = columnTitle;
      scope.previousOrder = scope.order;
      scope.order = sortColumn;
      if (scope.order === scope.previousOrder) {
        toggleDirection();
      } else {
        scope.direction = 'ASC';
      }
      $timeout(fetch, 0);
    }

    function toggleDirection() {
      scope.direction === 'ASC' ? (scope.direction = 'DESC') : (scope.direction = 'ASC');
    }

    function fetch() {
      if (!scope.skipFetch) {
        // reset offset in the URL when sorting
        if ($location.search().offset !== undefined) {
          $location.search(angular.extend($location.search(), { offset: 0 }));
        }
        // parent controller will reload and read parameters from URL
        $location.search(angular.extend($location.search(), { order: scope.order, direction: scope.direction }));
      }
      if (scope.changed) {
        scope.changed();
      }
    }
  }
}

})();
(function () {
'use strict';

/* jshint -W074 */
/* jshint -W101 */

angular
  .module('uiStackedMap', [])
  /**
   * A helper, internal data structure that acts as a map but also allows getting / removing
   * elements in the LIFO order
   */
  .factory('$$stackedMap', function() {
    return {
      createNew: function() {
        var stack = [];

        return {
          add: function(key, value) {
            stack.push({
              key: key,
              value: value
            });
          },
          get: function(key) {
            for (var i = 0; i < stack.length; i++) {
              if (key === stack[i].key) {
                return stack[i];
              }
            }
          },
          keys: function() {
            var keys = [];
            for (var i = 0; i < stack.length; i++) {
              keys.push(stack[i].key);
            }
            return keys;
          },
          top: function() {
            return stack[stack.length - 1];
          },
          remove: function(key) {
            var idx = -1;
            for (var i = 0; i < stack.length; i++) {
              if (key === stack[i].key) {
                idx = i;
                break;
              }
            }
            return stack.splice(idx, 1)[0];
          },
          removeTop: function() {
            return stack.pop();
          },
          length: function() {
            return stack.length;
          }
        };
      }
    };
  });

/* jshint +W074 */
/* jshint +W101 */

})();
(function () {
'use strict';

angular.module('hyresgastApp').directive('hyStdDisplay', hyStdDisplay);

function hyStdDisplay() {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/std-display/std-display.html',
    scope: {
      model: '@',
    },
    // eslint-disable-next-line no-unused-vars
    link: (scope, element, attrs) => {
      scope.getCost = () => {
        let objects = JSON.parse(scope.model);
        return objects.map(o => o.value).reduce((acc, el) => (acc += el), 0);
      };

      scope.now = new Date();

      scope.getTitle = () => {
        let objects = JSON.parse(scope.model);
        let getRow = el => `
          <span class='object-name'>
            ${el.name} ${(el.quantity && el.quantity > 1) ? `x${el.quantity}`: ''}
            ${(el.expirationDate && el.expirationDate < scope.now) ? '<b class="red-text">!</b>' : ''}
          </span>
          <span class='object-value'>
            ${el.value}
          </span> 
          <br/>`;
        return objects.reduce((acc, el) => (acc += getRow(el)), '');
      };
    },
  };
  return directive;


}
})();
(function () {
'use strict';

angular.module('hyresgastApp').directive('hyTabcontrol', tabs);

function tabs() {
  var directive = {
    scope: {
      tablist: '<',
      currentTab: '=',
    },
    restrict: 'E',
    templateUrl: 'components/tabcontrol/tabcontrol.html',
    link: link
  };

  return directive;

  function link($scope) {
    $scope.currentTab = $scope.tablist[0].key;
    $scope.selectTab = function (key) {
      $scope.currentTab = key;
    }
  }
}

})();
(function () {
'use strict';

/* jshint -W018 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

angular
  .module('uiTabindex', [])

  .directive('uibTabindexToggle', function() {
    return {
      restrict: 'A',
      link: function(scope, elem, attrs) {
        attrs.$observe('disabled', function(disabled) {
          attrs.$set('tabindex', disabled ? -1 : null);
        });
      }
    };
  });

/* jshint +W018 */
/* jshint +W071 */
/* jshint +W101 */
/* jshint +W106 */

})();
(function () {
'use strict';

hyTableau.$inject = ["$location", "$timeout", "$document", "HelperService", "CommonIds"];
angular.module('hyresgastApp').directive('hyTableau', hyTableau);

/**
 * @typedef TableauColumn
 * @property {string} title
 * @property {string} column
 * @property {number} type
 */

function hyTableau($location, $timeout, $document,
  HelperService, CommonIds) {
  return {
    restrict: 'E',
    transclude: {
      //vanliga namn listade här eftersom det är mycket komplicerat att arbeta med godtyckliga med bara transclude: true
      slot1: '?slot1',
      slot2: '?slot2',
      slot3: '?slot3',
      array: '?array',
      array2: '?array2',
      date1: '?date1',
      date2: '?date2',
      boolean1: '?boolean1',
      boolean2: '?boolean2',
      hyra: '?hyra',
      hyra2: '?hyra2',
      person: '?person',
      comment: '?comment',
      type: '?type',
      status: '?status',
    },
    templateUrl: 'components/tableau/tableau.html',
    scope: {
      columns: '=',
      filtersOverride: '=',
      defaultSorting: '@',

      lastParams: '=?',

      inMemory: '=',
      inMemoryExcessiveLimit: '=',
      checkboxes: '=',
      checkboxesAlternative: '=',
      selectedRows: '=',

      fGetData: '&',
      fPostprocessData: '&',

      limit: '=',
      noSorting: '=',
      noFiltering: '=',
      noPagination: '=',

      fRowClick: '&',
      useUrl: '=',

      idProperty: '@',

      infoText: '@',
      attributes: '=',

      actions: '=?',

      fGetContextInfo: '&',
      contextActions: '=?',
    },
    
    link: (scope) => {

      let init = new tableau.Init(scope, CommonIds.tableauAllId, $location);
      let util = new tableau.Utils(scope, $timeout);

      scope.uuid = crypto.randomUUID();

      const tickedOptionAll = 'Alla';
      const tickedOptionChecked = 'Vald';
      const tickedOptionUnchecked = 'Avvald';

      scope.ready = false;
      scope.offset = 0;
      scope.total = 0;
      scope.initReady = false;
      scope.hasSelectedRows = false;

      scope.rowClass = item => {
        let classes = scope.attributes
          ? scope.attributes.filter(x => x.column ? item[x.column] : x.fn(item)).map(x => x.class)
          : [];
        let readyClass = scope.ready ? '' : 'table__tr--muted';
        return classes.concat(readyClass).join(' ');
      };
      scope.columnClass = column => {
        return column.columnClass ? column.columnClass : '';
      }

      // eslint-disable-next-line no-unused-vars
      scope.$watchGroup(['fGetData', 'columns', 'inMemory', 'inMemoryExcessiveLimit'], newData => {
        if (init.maybeInit()) {
          getData();
        }
      });

      scope.$watch('checkboxes', newData => {
        if (scope.data && newData === true) {
          initCheckboxes();
          setSelectedRows();
        }
      });

      let sortingOrFilteringChanged = () => {
        if (!scope.inMemory) {
          getData(true);
        } else {
          scope.applyFilter();
        }
      };
      if (scope.useUrl) {
        scope.$on('$routeUpdate', sortingOrFilteringChanged);
      } else {
        scope.$watch('sortColumn', () => { scope.timed(sortingOrFilteringChanged) });
        scope.$watch('sortDirection', () => { scope.timed(sortingOrFilteringChanged) });
      }

      let setSelectedRows = () => {
        scope.selectedRows = scope.data.filter(x => scope.ticked[x[scope.idProperty]] === true);
        let disabledAttributes = scope.attributes ? scope.attributes.filter(x => x.disabled) : [];
        let disabledCheck = disabledAttributes.length
          ? scope.selectedRows.every(x => disabledAttributes.some(y => !x[y.column]))
          : true;
        scope.hasSelectedRows = (scope.selectedRows.length > 0) && disabledCheck;
      }
      scope.applyTickedFilterDropdown = () => {
        scope.offset = 0;
        scope.filters = { ticked: scope.filters.ticked };
        util.timed(() => { scope.applyFilter('ticked') });
      };
      scope.tick = () => {
        util.timed(() => { setSelectedRows(); });
      };
      scope.toggle = all => {
        let array = all ? scope.data : scope.filteredData;

        let arrayTicked = array.filter(x => scope.ticked[x[scope.idProperty]]);
        let newValue = arrayTicked.length < array.length;
        array.forEach(x => scope.ticked[x[scope.idProperty]] = newValue);

        util.timed(() => { setSelectedRows(); });
      };
      scope.isFiltered = () => {
        if (scope.filteredData) {
          return scope.filteredData.length < scope.data.length;
        } else {
          return false;
        }
      };
      scope.getFilteredTickedTotal = () => {
        if (scope.filteredData) {
          return scope.filteredData.filter(x => scope.ticked[x[scope.idProperty]]).length;
        }
      };
      scope.getFilteredStateTitle = all => {
        if (!scope.checkboxes || !scope.ticked) {
          return '';
        }
        return all
          ? scope.selectedRows && (scope.selectedRows.length < scope.total)
          : (scope.getFilteredTickedTotal() < scope.getTotal()) ? 'Markera' : 'Avmarkera';
      };
      scope.getTotal = () => {
        if (scope.inMemory && scope.filteredData) {
          return scope.filteredData.length;
        } else {
          return scope.total;
        }
      };
      scope.applyFilterTimed = columnKey => {
        $timeout(() => scope.applyFilter(columnKey), 100);
      }
      scope.applyFilter = columnKey => {
        if (scope.inMemory) {
          scope.offset = 0;
          if (!scope.data) {
            scope.data = [];
          }
          if (!scope.filters) {
            scope.filters = {};
          }
          let filterKeys = Object.keys(scope.filters).filter(k => scope.filters[k]);
          scope.filteredData = scope.data.filter(x => {
            let pass = true;
            filterKeys.filter(x => x !== 'ticked').forEach(k => {
              let column = scope.columns.find(c => c.column === k);
              let type = column.filterType;
              let filterValue = scope.filters[k];
              let dataValue = util.getX(x, k);
              // eslint-disable-next-line no-undefined
              if (dataValue == null) {
                pass = false;
                return;
              }
              if (filterValue) {
                if (column.type === 'array' && dataValue instanceof Array) {
                  pass &= dataValue.some(d => {
                    return column.ffn
                      ? column.ffn(d, filterValue)
                      : util.filterCmp(d, filterValue);
                  });
                } else if (type !== 'none' && filterValue) {
                  pass &= (column.ffn
                    ? column.ffn(dataValue, filterValue)
                    : util.filterCmp(dataValue, filterValue));
                }
              }
            });
            if (scope.checkboxes) {
              let checkedOnly = scope.filters.ticked === tickedOptionChecked;
              let uncheckedOnly = scope.filters.ticked === tickedOptionUnchecked;
              let isTicked = scope.ticked[x[scope.idProperty]];
              if (checkedOnly) {
                pass &= isTicked;
              }
              if (uncheckedOnly) {
                pass &= !isTicked;
              }
            }
            return pass;
          });
          scope.filteredData = scope.filteredData.sort(HelperService.dynamicSortSwe(scope.sortColumn, scope.sortDirection));
          scope.setDataVM();
          if (scope.checkboxes) {
            setSelectedRows();
          }
        } else {
          if (scope.useUrl) {
            let newData = {};
            if (columnKey) {
              newData[columnKey] = scope.filters[columnKey];
              let column = scope.columns.find(c => c.column === columnKey);
              if (column.lookup) {
                newData[columnKey] = util.castProperty(newData[columnKey], column.lookup.idProperty, true);
              }
            }
            $location.search(angular.extend($location.search(), newData));
          } else {
            sortingOrFilteringChanged();
          }
        }
      };
      let applyFilterOrUrl = (clear) => {
        if (scope.useUrl) {
          if (clear) {
            let viewParam = $location.search().view;
            $location.search({});
            //tabval bör stanna kvar
            if (viewParam !== undefined) {
              $location.search('view', viewParam);
            }
          }
          $location.search(angular.extend($location.search(), { order: scope.sortColumn, direction: scope.sortDirection }));
        } else {
          scope.applyFilter();
        }
      };

      scope.flipPage = function () {
        if (scope.inMemory) {
          scope.setDataVM();
        } else {
          getData();
        }
      };
      // eslint-disable-next-line no-unused-vars
      scope.$on("refresh", function (event, data) {
        scope.filteredData = null;
        if (data) {
          scope.filters = data;
        } else {
          scope.filters = {};
        }
        getData(true);
      });

      scope.setDataVM = function () {
        if (scope.inMemory) {
          let data = scope.filteredData ? scope.filteredData : scope.data;
          scope.dataVM = data ? data.slice(scope.offset, scope.offset + scope.limit) : [];
        } else {
          scope.dataVM = scope.data;
        }
      };

      let initCheckboxes = () => {
        if (scope.checkboxes && scope.idProperty) {
          if (scope.checkboxesAlternative) {
            scope.ticked = {};
            scope.selectedRows = [];
          } else {
            scope.ticked = scope.data.reduce(util.reductor, {});
            scope.selectedRows = scope.data;
          }
          scope.tickedFilterItems = [tickedOptionAll, tickedOptionChecked, tickedOptionUnchecked];
          scope.filters.ticked = tickedOptionAll;
        }
      }

      let getData = filterMode => {
        if (!scope.initReady) {
          return;
        }

        if (filterMode) {
          scope.offset = 0;
        }

        let params = {
          limit: scope.inMemory ? scope.inMemoryExcessiveLimit : scope.limit,
          offset: scope.inMemory ? 0 : scope.offset,
          direction: scope.sortDirection,
          order: scope.sortColumn,
        };

        let filters = scope.columns.reduce((acc, column) => {
          let key = column.column;
          if (scope.filters[key]) {
            if (column.lookup) {
              let value = util.castProperty(scope.filters[key], column.lookup.idProperty, false);
              if (value !== CommonIds.tableauAllId) {
                acc[`filters[${key}]`] = value;
              }
            } else {
              acc[`search[${key}]`] = scope.filters[key];
            }
          }
          return acc;
        }, {});

        Object.assign(params, filters);

        // eslint-disable-next-line no-undef
        scope.lastParams = structuredClone(scope.inMemory ? scope.filters : params);

        scope.ready = false;
        let tuAsPromis = scope.fGetData()(params);
        if (tuAsPromis.$promise) {
          tuAsPromis = tuAsPromis.$promise;
        }
        tuAsPromis.then(response => {
          scope.ready = true;
          scope.data = response.rows;
          scope.total = response.count;
          initCheckboxes();
          scope.applyFilter();
          scope.setDataVM();
          if (scope.fPostprocessData && scope.fPostprocessData()) {
            scope.fPostprocessData()(scope.data);
          }
          scope.timed(() => scope.$apply());
        });
      };

      scope.getFilterType = column => {
        if (column.filterType) {
          if (column.filterType === 'none') return null;
          return 'text';
        }
        if (!column.type) {
          return column.lookup
            ? (column.lookup.multi ? 'multicombobox' : 'combobox')
            : 'text';
        }
        if (['boolean'].includes(column.type)) return 'combobox';
        if (['array', 'date'].includes(column.type)) return 'text';
        if (['star'].includes(column.type)) return null;
      };
      scope.filtersActive = () => scope.filters
        ? Object.entries(scope.filters).filter(x => (x[0] !== 'ticked') && x[1]).length > 0
        : false;
      scope.clearFilters = () => {
        scope.filters = {};
        if (scope.checkboxes) {
          scope.filters.ticked = tickedOptionAll;
        }
        applyFilterOrUrl(true);
      };
      scope.getLookupValue = (item, column) => {
        let value = column.lookup.dictionary.find(x => x[column.lookup.idProperty] === item[column.column]);
        if (value) {
          return value[column.lookup.textProperty];
        }
        return null;
      };
      scope.itemSelected = (item, column) => {
        if (!column.template && scope.fRowClick && scope.fRowClick()) {
          scope.fRowClick()(item);
        }
      };
      scope.isColumnSortable = columnKey => !!columnKey && !!scope.sortColumns && !!scope.sortColumns.find(c => c.column === columnKey);
      scope.columnClick = column => {
        if (!scope.isColumnSortable(column.column)) {
          return;
        }
        let isSameColumn = (scope.sortColumn === column.column);
        if (isSameColumn) {
          scope.sortDirection = (scope.sortDirection === 'ASC') ? 'DESC' : 'ASC';
        } else {
          scope.sortColumn = column.column;
          scope.sortDirection = 'ASC';
        }
        applyFilterOrUrl();
      };
      scope.doAction = action => { action.action() };
    },
  }
}
})();
(function () {
'use strict';

class hyTableauInit {
  constructor(scope, tableauAllId, location) {
    this.scope = scope;
    this.tableauAllId = tableauAllId;
    this.location = location;
  }

  initSorting() {
    if (this.scope.useUrl) {
      this.scope.sortColumn = this.location.search().order;
      this.scope.sortDirection = this.location.search().direction;
    }
    if (!this.scope.sortColumn && this.scope.defaultSorting) {
      let tokens = this.scope.defaultSorting.split(' ');
      this.scope.sortColumn = tokens[0];
      this.scope.sortDirection = tokens[1];
    }
    if (!this.scope.sortColumn) {
      this.scope.sortColumn = this.scope.sortColumns[0].column;
    }
    if (!this.scope.sortDirection) {
      this.scope.sortDirection = 'ASC';
    }
  };
  
  initSortingColumns() {
    this.scope.sortColumns = this.scope.columns.filter(column => {
      let nonFilter = column.filterType === 'none';
      let isArray = column.type === 'array';
      return !nonFilter && !isArray;
    });
  };
    
  initFilters() {
    if (!this.scope.useUrl) return {};
    return this.scope.columns.filter(x => x.filterType !== 'none').reduce((acc, el) => {
      let value = this.location.search()[el.column];
      if (!value) {
        return acc;
      }
      acc[el.column] = el.lookup
        ? (el.lookup.multi
          // eslint-disable-next-line eqeqeq
          ? value.split(',').map(idValue => el.lookup.dictionary.find(x => x[el.lookup.idProperty] == idValue))
          // eslint-disable-next-line eqeqeq
          : el.lookup.dictionary.find(x => x[el.lookup.idProperty] == value))
        : value;
      return acc;
    }, {});
  };

  initColumns() {
    this.scope.columns.forEach(column => {
      if (column.type === 'boolean') {
        column.lookup = {
          idProperty: 'id',
          textProperty: 'text',
          dictionary: [
            { id: 0, text: 'nej' },
            { id: 1, text: 'Ja' },
          ]
        };
      }
      if (column.lookup && !column.lookup.multi) {
        let fAll = {  };
        fAll[column.lookup.idProperty] = this.tableauAllId;
        fAll[column.lookup.textProperty] = 'Alla';
        column.lookup.dictionary = [fAll].concat(column.lookup.dictionary);
      }
    });
  };

  initContextmenu() {
    if (!this.scope.fGetContextInfo()) {
      return;
    }
    const tableEl = document.getElementById('table'+this.scope.uuid);
    const contextMenuEl = document.getElementById('context-menu'+this.scope.uuid);

    const self = this;
    tableEl.addEventListener('contextmenu', function(e) {
        e.preventDefault();
        
        let cY = e.clientY;
        let cX = e.clientX;

        let rows = tableEl.rows;
        let start = self.scope.noFiltering ? 1 : 2;
        let hit = false;
        
        for (let rowIndex = start; rowIndex < rows.length; rowIndex++) {
          const rect = rows[rowIndex].getBoundingClientRect();
          if ((cY >= rect.top) && (cY <= rect.bottom)) {
            hit = true;
            let index = self.scope.noFiltering ? rowIndex - 1 : rowIndex - 2;
            self.scope.contextRow = self.scope.dataVM[index];
            self.scope.contextInfo = self.scope.fGetContextInfo()(self.scope.contextRow);
            self.scope.$apply();
          }
        }

        if (hit) {
          setTimeout(function () {
            contextMenuEl.style.left = (cX + 5) + 'px';
            contextMenuEl.style.top = (cY + 5) + 'px';
            contextMenuEl.style.display = 'block';
          }, 100);
        }
    });
    document.addEventListener('click', function() {
        contextMenuEl.style.display = 'none';
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' || e.which === 27) {
        contextMenuEl.style.display = 'none';
      }
    });
    document.addEventListener('mousewheel', function() {
        contextMenuEl.style.display = 'none';
    });

    this.scope.contextMenuitems = [
      {
        title: 'Öppna i ny flik', 
        isVisible: (row, info) => (info && info.link),
        fn: (row, info) => { window.open(info.link, '_blank') } 
      },
      { 
        title: 'Dela',
        fn: (row, info) => {
          if (navigator.share) {
            let shareData = info.link
              ? {
                  title: info.title,
                  text: 'Förhandlarportalen för ditt livet',
                  url: info.link
              } : {
                  title: info.title,
                  text: info.title,
              };
            navigator.share(shareData);
          } else {
            navigator.clipboard.writeText(info.link);
            alert('Kopierad till urklipp');
          }
        }
      },
    ];
    if (this.scope.contextActions && this.scope.contextActions.length) {
      this.scope.contextMenuitems = this.scope.contextMenuitems.concat(this.scope.contextActions);
    }
  };


  maybeInit() {
    if (!(this.scope.fGetData && this.scope.fGetData()) || !this.scope.columns || this.scope.initReady) return false;
    if (this.scope.inMemory && !this.scope.inMemoryExcessiveLimit) return false;
    this.initColumns();
    this.initSortingColumns();
    this.initSorting();
    this.scope.offset = 0;
    this.scope.filters = this.scope.filtersOverride ? this.scope.filtersOverride : this.initFilters();
    this.scope.initReady = true;
    this.initContextmenu();
    return true;
  };
};

window.tableau = window.tableau || {};
window.tableau.Init = hyTableauInit;
})();
(function () {
'use strict';

class hyTableauUtils {
  constructor(scope, timeout) {
    this.scope = scope;
    this.timeout = timeout;

    this.timed = f => this.timeout(f, 200);

    this.filterCmp = (dataValue, filterValue) =>
      dataValue != null && dataValue.toString().toLowerCase().includes(filterValue.toLowerCase());
    
    this.getX = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj);

    this.castProperty = (obj, propertyName, join) => obj instanceof Array
          ? (join ? obj.map(x => x[propertyName]).join() : obj.map(x => x[propertyName]))
          : obj[propertyName];

    this.reductor = (acc, el) => { acc[el[this.scope.idProperty]] = true; return acc; };

    this.scope.getX = this.getX;
    this.scope.timed = this.timed;
  }
};

window.tableau = window.tableau || {};
window.tableau.Utils = hyTableauUtils;
})();
(function () {
'use strict';

// @desc
//   Timeline to show activities and provide routing to these
//   activites by a name attribute
//
// @example
//   <hy-timeline activities="activities"></hy-timeline>

timeline.$inject = ["$location", "$window"];
angular
  .module('timeline', ['uiTooltip']) // TODO add tooltip as a dependency
  .directive('hyTimeline', timeline);

function timeline($location, $window) {
  var directive = {
    scope: {
      activities: '=',
      alternativeWizard: '='
    },
    restrict: 'E',
    templateUrl: 'components/timeline/timeline.html',
    link: link
  };

  return directive;

  function link(scope, element, attrs) {
    scope.progress = progress;
    scope.gotoActivity = gotoActivity;
    scope.currentOpenIdx = currentOpenIdx;
    scope.tooltipClass = scope.alternativeWizard
      ? 'timeline__tooltip alternative-timeline-tooltip'
      : 'timeline__tooltip';
    ////////////

    function progress() {
      return 100 / (scope.activities.length - 1) * currentOpenIdx();
    }

    function gotoActivity(activityName) {
      $location.search({ step: activityName });
    }

    function currentOpenIdx() {
      var idx = 0;
      var res = -1;
      angular.forEach(scope.activities, function(activity) {
        if (activity.isOpen === true) {
          res = idx;
        }
        idx++;
      });
      return res;
    }

    // Tooltip is positioned absolute and not relative because of a IE-bug
    // Reposition the tooltip if browser window is resized
    function repositionTooltip() {
      scope.$apply();
    }

    angular.element($window).on('resize', repositionTooltip);
    scope.$on('$destroy', function() {
      angular.element($window).off('resize', repositionTooltip);
    });
  }
}

})();
(function () {
'use strict';
angular.module('rangeInput', [])
  .directive('rangeInput', () => {
    return {
      scope: {
        min: '=',
        max: '=',
        changed: '&',
        changedInternal: '&',
      },
      restrict: 'E',
      templateUrl: 'components/range-input/range-input.html',
      link: scope => {
        scope.changedInternal = () => { scope.changed({min: scope.min, max: scope.max}) };
      }
    }
  });
})();
(function () {
'use strict';

// Used in getCurrentSubpage() in order to avoid <ng-include> to include non-existing subpage,
// which can happen when the user manually set wrong '?view=' param in the URL.

angular.module('hyresgastApp').factory('SubPage', ["$location", function($location) {
  var service = {
    set: set
  };

  return service;

  function set(routeParamsView, whiteList, defaultSubPage) {
    if (routeParamsView === undefined || !subPageValidation()) {
      // clear URL params in the case when the user manually
      // set in URL a non existing 'view' param for the current tab,
      // because the default subpage is hightlighted when 'view' is undefined.
      $location.search({});
      return defaultSubPage;
    } else {
      return routeParamsView;
    }

    function subPageValidation() {
      return whiteList.indexOf(routeParamsView) > -1;
    }
  }
}]);

})();
(function () {
'use strict';

// @desc
//   Horizontal tab navigation between different subpages (partials) of a given page.
//
// @example
//   <hy-tabs tablist="vm.tablist"></hy-tabs>
//   vm.tablelist contains 2 parameters: title and URL path

tabs.$inject = ["$location", "$routeParams"];
angular.module('hyresgastApp').directive('hyTabs', tabs);

function tabs($location, $routeParams) {
  var directive = {
    scope: {
      tablist: '<'
    },
    restrict: 'E',
    templateUrl: 'components/tabs/tabs.html',
    link: link
  };

  return directive;

  function link(scope, element, attrs) {
    scope.isActive = isActive;

    scope.iconPack = {
      ok: 'icon--ticked-in-circle',
      warning: 'icon--message-warning', 
    };

    scope.getClass = item => {
      if (isActive(item.path)) {
        return 'tabs__item--active';
      } else if (item.irrelevant) {
        return 'tabs__item--irrelevant';
      }
      return '';
    };

    ////////////
    function isActive(viewLocation) {
      if ($routeParams.view === undefined) {
        return viewLocation === $location.path();
      } else {
        return viewLocation === $location.path() + '?view=' + $routeParams.view;
      }
    }
  }
}

})();
(function () {
angular
  .module('uiProgressbar', [])

  .constant('uibProgressConfig', {
    animate: true,
    max: 100
  })

  .controller('UibProgressController', [
    '$scope',
    '$attrs',
    'uibProgressConfig',
    function($scope, $attrs, progressConfig) {
      var self = this,
        animate = angular.isDefined($attrs.animate) ? $scope.$parent.$eval($attrs.animate) : progressConfig.animate;

      this.bars = [];
      $scope.max = getMaxOrDefault();

      this.addBar = function(bar, element, attrs) {
        if (!animate) {
          element.css({ transition: 'none' });
        }

        this.bars.push(bar);

        bar.max = getMaxOrDefault();
        bar.title = attrs && angular.isDefined(attrs.title) ? attrs.title : 'progressbar';

        bar.$watch('value', function(value) {
          bar.recalculatePercentage();
        });

        bar.recalculatePercentage = function() {
          var totalPercentage = self.bars.reduce(function(total, bar) {
            bar.percent = +(100 * bar.value / bar.max).toFixed(2);
            return total + bar.percent;
          }, 0);

          if (totalPercentage > 100) {
            bar.percent -= totalPercentage - 100;
          }
        };

        bar.$on('$destroy', function() {
          element = null;
          self.removeBar(bar);
        });
      };

      this.removeBar = function(bar) {
        this.bars.splice(this.bars.indexOf(bar), 1);
        this.bars.forEach(function(bar) {
          bar.recalculatePercentage();
        });
      };

      //$attrs.$observe('maxParam', function(maxParam) {
      $scope.$watch('maxParam', function(maxParam) {
        self.bars.forEach(function(bar) {
          bar.max = getMaxOrDefault();
          bar.recalculatePercentage();
        });
      });

      function getMaxOrDefault() {
        return angular.isDefined($scope.maxParam) ? $scope.maxParam : progressConfig.max;
      }
    }
  ])

  .directive('uibProgress', function() {
    return {
      replace: true,
      transclude: true,
      controller: 'UibProgressController',
      require: 'uibProgress',
      scope: {
        maxParam: '=?max'
      },
      templateUrl: 'components/progressbar/progress.html'
    };
  })

  .directive('uibBar', function() {
    return {
      replace: true,
      transclude: true,
      require: '^uibProgress',
      scope: {
        value: '=',
        type: '@'
      },
      templateUrl: 'components/progressbar/bar.html',
      link: function(scope, element, attrs, progressCtrl) {
        progressCtrl.addBar(scope, element, attrs);
      }
    };
  })

  .directive('uibProgressbar', function() {
    return {
      replace: true,
      transclude: true,
      controller: 'UibProgressController',
      scope: {
        value: '=',
        maxParam: '=?max',
        type: '@'
      },
      templateUrl: 'components/progressbar/progressbar.html',
      link: function(scope, element, attrs, progressCtrl) {
        progressCtrl.addBar(scope, angular.element(element.children()[0]), { title: attrs.title });
      }
    };
  });

})();
(function () {
angular
  .module('uiTimepicker', [])

  .constant('uibTimepickerConfig', {
    hourStep: 1,
    minuteStep: 1,
    secondStep: 1,
    showMeridian: true,
    showSeconds: false,
    meridians: null,
    readonlyInput: false,
    mousewheel: true,
    arrowkeys: true,
    showSpinners: true,
    templateUrl: 'components/timepicker/timepicker.html'
  })

  .controller('UibTimepickerController', [
    '$scope',
    '$element',
    '$attrs',
    '$parse',
    '$log',
    '$locale',
    'uibTimepickerConfig',
    function($scope, $element, $attrs, $parse, $log, $locale, timepickerConfig) {
      var hoursModelCtrl, minutesModelCtrl, secondsModelCtrl;
      var selected = new Date(),
        watchers = [],
        ngModelCtrl = { $setViewValue: angular.noop }, // nullModelCtrl
        meridians = angular.isDefined($attrs.meridians)
          ? $scope.$parent.$eval($attrs.meridians)
          : timepickerConfig.meridians || $locale.DATETIME_FORMATS.AMPMS,
        padHours = angular.isDefined($attrs.padHours) ? $scope.$parent.$eval($attrs.padHours) : true;

      $scope.tabindex = angular.isDefined($attrs.tabindex) ? $attrs.tabindex : 0;
      $element.removeAttr('tabindex');

      this.init = function(ngModelCtrl_, inputs) {
        ngModelCtrl = ngModelCtrl_;
        ngModelCtrl.$render = this.render;

        ngModelCtrl.$formatters.unshift(function(modelValue) {
          return modelValue ? new Date(modelValue) : null;
        });

        var hoursInputEl = inputs.eq(0),
          minutesInputEl = inputs.eq(1),
          secondsInputEl = inputs.eq(2);

        hoursModelCtrl = hoursInputEl.controller('ngModel');
        minutesModelCtrl = minutesInputEl.controller('ngModel');
        secondsModelCtrl = secondsInputEl.controller('ngModel');

        var mousewheel = angular.isDefined($attrs.mousewheel)
          ? $scope.$parent.$eval($attrs.mousewheel)
          : timepickerConfig.mousewheel;

        if (mousewheel) {
          this.setupMousewheelEvents(hoursInputEl, minutesInputEl, secondsInputEl);
        }

        var arrowkeys = angular.isDefined($attrs.arrowkeys)
          ? $scope.$parent.$eval($attrs.arrowkeys)
          : timepickerConfig.arrowkeys;
        if (arrowkeys) {
          this.setupArrowkeyEvents(hoursInputEl, minutesInputEl, secondsInputEl);
        }

        $scope.readonlyInput = angular.isDefined($attrs.readonlyInput)
          ? $scope.$parent.$eval($attrs.readonlyInput)
          : timepickerConfig.readonlyInput;
        this.setupInputEvents(hoursInputEl, minutesInputEl, secondsInputEl);
      };

      var hourStep = timepickerConfig.hourStep;
      if ($attrs.hourStep) {
        watchers.push(
          $scope.$parent.$watch($parse($attrs.hourStep), function(value) {
            hourStep = +value;
          })
        );
      }

      var minuteStep = timepickerConfig.minuteStep;
      if ($attrs.minuteStep) {
        watchers.push(
          $scope.$parent.$watch($parse($attrs.minuteStep), function(value) {
            minuteStep = +value;
          })
        );
      }

      var min;
      watchers.push(
        $scope.$parent.$watch($parse($attrs.min), function(value) {
          var dt = new Date(value);
          min = isNaN(dt) ? undefined : dt;
        })
      );

      var max;
      watchers.push(
        $scope.$parent.$watch($parse($attrs.max), function(value) {
          var dt = new Date(value);
          max = isNaN(dt) ? undefined : dt;
        })
      );

      var disabled = false;
      if ($attrs.ngDisabled) {
        watchers.push(
          $scope.$parent.$watch($parse($attrs.ngDisabled), function(value) {
            disabled = value;
          })
        );
      }

      $scope.noIncrementHours = function() {
        var incrementedSelected = addMinutes(selected, hourStep * 60);
        return disabled || incrementedSelected > max || (incrementedSelected < selected && incrementedSelected < min);
      };

      $scope.noDecrementHours = function() {
        var decrementedSelected = addMinutes(selected, -hourStep * 60);
        return disabled || decrementedSelected < min || (decrementedSelected > selected && decrementedSelected > max);
      };

      $scope.noIncrementMinutes = function() {
        var incrementedSelected = addMinutes(selected, minuteStep);
        return disabled || incrementedSelected > max || (incrementedSelected < selected && incrementedSelected < min);
      };

      $scope.noDecrementMinutes = function() {
        var decrementedSelected = addMinutes(selected, -minuteStep);
        return disabled || decrementedSelected < min || (decrementedSelected > selected && decrementedSelected > max);
      };

      $scope.noIncrementSeconds = function() {
        var incrementedSelected = addSeconds(selected, secondStep);
        return disabled || incrementedSelected > max || (incrementedSelected < selected && incrementedSelected < min);
      };

      $scope.noDecrementSeconds = function() {
        var decrementedSelected = addSeconds(selected, -secondStep);
        return disabled || decrementedSelected < min || (decrementedSelected > selected && decrementedSelected > max);
      };

      $scope.noToggleMeridian = function() {
        if (selected.getHours() < 12) {
          return disabled || addMinutes(selected, 12 * 60) > max;
        }

        return disabled || addMinutes(selected, -12 * 60) < min;
      };

      var secondStep = timepickerConfig.secondStep;
      if ($attrs.secondStep) {
        watchers.push(
          $scope.$parent.$watch($parse($attrs.secondStep), function(value) {
            secondStep = +value;
          })
        );
      }

      $scope.showSeconds = timepickerConfig.showSeconds;
      if ($attrs.showSeconds) {
        watchers.push(
          $scope.$parent.$watch($parse($attrs.showSeconds), function(value) {
            $scope.showSeconds = !!value;
          })
        );
      }

      // 12H / 24H mode
      $scope.showMeridian = timepickerConfig.showMeridian;
      if ($attrs.showMeridian) {
        watchers.push(
          $scope.$parent.$watch($parse($attrs.showMeridian), function(value) {
            $scope.showMeridian = !!value;

            if (ngModelCtrl.$error.time) {
              // Evaluate from template
              var hours = getHoursFromTemplate(),
                minutes = getMinutesFromTemplate();
              if (angular.isDefined(hours) && angular.isDefined(minutes)) {
                selected.setHours(hours);
                refresh();
              }
            } else {
              updateTemplate();
            }
          })
        );
      }

      // Get $scope.hours in 24H mode if valid
      function getHoursFromTemplate() {
        var hours = +$scope.hours;
        var valid = $scope.showMeridian ? hours > 0 && hours < 13 : hours >= 0 && hours < 24;
        if (!valid || $scope.hours === '') {
          return undefined;
        }

        if ($scope.showMeridian) {
          if (hours === 12) {
            hours = 0;
          }
          if ($scope.meridian === meridians[1]) {
            hours = hours + 12;
          }
        }
        return hours;
      }

      function getMinutesFromTemplate() {
        var minutes = +$scope.minutes;
        var valid = minutes >= 0 && minutes < 60;
        if (!valid || $scope.minutes === '') {
          return undefined;
        }
        return minutes;
      }

      function getSecondsFromTemplate() {
        var seconds = +$scope.seconds;
        return seconds >= 0 && seconds < 60 ? seconds : undefined;
      }

      function pad(value, noPad) {
        if (value === null) {
          return '';
        }

        return angular.isDefined(value) && value.toString().length < 2 && !noPad ? '0' + value : value.toString();
      }

      // Respond on mousewheel spin
      this.setupMousewheelEvents = function(hoursInputEl, minutesInputEl, secondsInputEl) {
        var isScrollingUp = function(e) {
          if (e.originalEvent) {
            e = e.originalEvent;
          }
          //pick correct delta variable depending on event
          var delta = e.wheelDelta ? e.wheelDelta : -e.deltaY;
          return e.detail || delta > 0;
        };

        hoursInputEl.on('mousewheel wheel', function(e) {
          if (!disabled) {
            $scope.$apply(isScrollingUp(e) ? $scope.incrementHours() : $scope.decrementHours());
          }
          e.preventDefault();
        });

        minutesInputEl.on('mousewheel wheel', function(e) {
          if (!disabled) {
            $scope.$apply(isScrollingUp(e) ? $scope.incrementMinutes() : $scope.decrementMinutes());
          }
          e.preventDefault();
        });

        secondsInputEl.on('mousewheel wheel', function(e) {
          if (!disabled) {
            $scope.$apply(isScrollingUp(e) ? $scope.incrementSeconds() : $scope.decrementSeconds());
          }
          e.preventDefault();
        });
      };

      // Respond on up/down arrowkeys
      this.setupArrowkeyEvents = function(hoursInputEl, minutesInputEl, secondsInputEl) {
        hoursInputEl.on('keydown', function(e) {
          if (!disabled) {
            if (e.which === 38) {
              // up
              e.preventDefault();
              $scope.incrementHours();
              $scope.$apply();
            } else if (e.which === 40) {
              // down
              e.preventDefault();
              $scope.decrementHours();
              $scope.$apply();
            }
          }
        });

        minutesInputEl.on('keydown', function(e) {
          if (!disabled) {
            if (e.which === 38) {
              // up
              e.preventDefault();
              $scope.incrementMinutes();
              $scope.$apply();
            } else if (e.which === 40) {
              // down
              e.preventDefault();
              $scope.decrementMinutes();
              $scope.$apply();
            }
          }
        });

        secondsInputEl.on('keydown', function(e) {
          if (!disabled) {
            if (e.which === 38) {
              // up
              e.preventDefault();
              $scope.incrementSeconds();
              $scope.$apply();
            } else if (e.which === 40) {
              // down
              e.preventDefault();
              $scope.decrementSeconds();
              $scope.$apply();
            }
          }
        });
      };

      this.setupInputEvents = function(hoursInputEl, minutesInputEl, secondsInputEl) {
        if ($scope.readonlyInput) {
          $scope.updateHours = angular.noop;
          $scope.updateMinutes = angular.noop;
          $scope.updateSeconds = angular.noop;
          return;
        }

        var invalidate = function(invalidHours, invalidMinutes, invalidSeconds) {
          ngModelCtrl.$setViewValue(null);
          ngModelCtrl.$setValidity('time', false);
          if (angular.isDefined(invalidHours)) {
            $scope.invalidHours = invalidHours;
            if (hoursModelCtrl) {
              hoursModelCtrl.$setValidity('hours', false);
            }
          }

          if (angular.isDefined(invalidMinutes)) {
            $scope.invalidMinutes = invalidMinutes;
            if (minutesModelCtrl) {
              minutesModelCtrl.$setValidity('minutes', false);
            }
          }

          if (angular.isDefined(invalidSeconds)) {
            $scope.invalidSeconds = invalidSeconds;
            if (secondsModelCtrl) {
              secondsModelCtrl.$setValidity('seconds', false);
            }
          }
        };

        $scope.updateHours = function() {
          var hours = getHoursFromTemplate(),
            minutes = getMinutesFromTemplate();

          ngModelCtrl.$setDirty();

          if (angular.isDefined(hours) && angular.isDefined(minutes)) {
            selected.setHours(hours);
            selected.setMinutes(minutes);
            if (selected < min || selected > max) {
              invalidate(true);
            } else {
              refresh('h');
            }
          } else {
            invalidate(true);
          }
        };

        hoursInputEl.on('blur', function(e) {
          ngModelCtrl.$setTouched();
          if (modelIsEmpty()) {
            makeValid();
          } else if ($scope.hours === null || $scope.hours === '') {
            invalidate(true);
          } else if (!$scope.invalidHours && $scope.hours < 10) {
            $scope.$apply(function() {
              $scope.hours = pad($scope.hours, !padHours);
            });
          }
        });

        $scope.updateMinutes = function() {
          var minutes = getMinutesFromTemplate(),
            hours = getHoursFromTemplate();

          ngModelCtrl.$setDirty();

          if (angular.isDefined(minutes) && angular.isDefined(hours)) {
            selected.setHours(hours);
            selected.setMinutes(minutes);
            if (selected < min || selected > max) {
              invalidate(undefined, true);
            } else {
              refresh('m');
            }
          } else {
            invalidate(undefined, true);
          }
        };

        minutesInputEl.on('blur', function(e) {
          ngModelCtrl.$setTouched();
          if (modelIsEmpty()) {
            makeValid();
          } else if ($scope.minutes === null) {
            invalidate(undefined, true);
          } else if (!$scope.invalidMinutes && $scope.minutes < 10) {
            $scope.$apply(function() {
              $scope.minutes = pad($scope.minutes);
            });
          }
        });

        $scope.updateSeconds = function() {
          var seconds = getSecondsFromTemplate();

          ngModelCtrl.$setDirty();

          if (angular.isDefined(seconds)) {
            selected.setSeconds(seconds);
            refresh('s');
          } else {
            invalidate(undefined, undefined, true);
          }
        };

        secondsInputEl.on('blur', function(e) {
          if (modelIsEmpty()) {
            makeValid();
          } else if (!$scope.invalidSeconds && $scope.seconds < 10) {
            $scope.$apply(function() {
              $scope.seconds = pad($scope.seconds);
            });
          }
        });
      };

      this.render = function() {
        var date = ngModelCtrl.$viewValue;

        if (isNaN(date)) {
          ngModelCtrl.$setValidity('time', false);
          $log.error(
            'Timepicker directive: "ng-model" value must be a Date object, a number of milliseconds since 01.01.1970 or a string representing an RFC2822 or ISO 8601 date.'
          );
        } else {
          if (date) {
            selected = date;
          }

          if (selected < min || selected > max) {
            ngModelCtrl.$setValidity('time', false);
            $scope.invalidHours = true;
            $scope.invalidMinutes = true;
          } else {
            makeValid();
          }
          updateTemplate();
        }
      };

      // Call internally when we know that model is valid.
      function refresh(keyboardChange) {
        makeValid();
        ngModelCtrl.$setViewValue(new Date(selected));
        updateTemplate(keyboardChange);
      }

      function makeValid() {
        if (hoursModelCtrl) {
          hoursModelCtrl.$setValidity('hours', true);
        }

        if (minutesModelCtrl) {
          minutesModelCtrl.$setValidity('minutes', true);
        }

        if (secondsModelCtrl) {
          secondsModelCtrl.$setValidity('seconds', true);
        }

        ngModelCtrl.$setValidity('time', true);
        $scope.invalidHours = false;
        $scope.invalidMinutes = false;
        $scope.invalidSeconds = false;
      }

      function updateTemplate(keyboardChange) {
        if (!ngModelCtrl.$modelValue) {
          $scope.hours = null;
          $scope.minutes = null;
          $scope.seconds = null;
          $scope.meridian = meridians[0];
        } else {
          var hours = selected.getHours(),
            minutes = selected.getMinutes(),
            seconds = selected.getSeconds();

          if ($scope.showMeridian) {
            hours = hours === 0 || hours === 12 ? 12 : hours % 12; // Convert 24 to 12 hour system
          }

          $scope.hours = keyboardChange === 'h' ? hours : pad(hours, !padHours);
          if (keyboardChange !== 'm') {
            $scope.minutes = pad(minutes);
          }
          $scope.meridian = selected.getHours() < 12 ? meridians[0] : meridians[1];

          if (keyboardChange !== 's') {
            $scope.seconds = pad(seconds);
          }
          $scope.meridian = selected.getHours() < 12 ? meridians[0] : meridians[1];
        }
      }

      function addSecondsToSelected(seconds) {
        selected = addSeconds(selected, seconds);
        refresh();
      }

      function addMinutes(selected, minutes) {
        return addSeconds(selected, minutes * 60);
      }

      function addSeconds(date, seconds) {
        var dt = new Date(date.getTime() + seconds * 1000);
        var newDate = new Date(date);
        newDate.setHours(dt.getHours(), dt.getMinutes(), dt.getSeconds());
        return newDate;
      }

      function modelIsEmpty() {
        return (
          ($scope.hours === null || $scope.hours === '') &&
          ($scope.minutes === null || $scope.minutes === '') &&
          (!$scope.showSeconds || ($scope.showSeconds && ($scope.seconds === null || $scope.seconds === '')))
        );
      }

      $scope.showSpinners = angular.isDefined($attrs.showSpinners)
        ? $scope.$parent.$eval($attrs.showSpinners)
        : timepickerConfig.showSpinners;

      $scope.incrementHours = function() {
        if (!$scope.noIncrementHours()) {
          addSecondsToSelected(hourStep * 60 * 60);
        }
      };

      $scope.decrementHours = function() {
        if (!$scope.noDecrementHours()) {
          addSecondsToSelected(-hourStep * 60 * 60);
        }
      };

      $scope.incrementMinutes = function() {
        if (!$scope.noIncrementMinutes()) {
          addSecondsToSelected(minuteStep * 60);
        }
      };

      $scope.decrementMinutes = function() {
        if (!$scope.noDecrementMinutes()) {
          addSecondsToSelected(-minuteStep * 60);
        }
      };

      $scope.incrementSeconds = function() {
        if (!$scope.noIncrementSeconds()) {
          addSecondsToSelected(secondStep);
        }
      };

      $scope.decrementSeconds = function() {
        if (!$scope.noDecrementSeconds()) {
          addSecondsToSelected(-secondStep);
        }
      };

      $scope.toggleMeridian = function() {
        var minutes = getMinutesFromTemplate(),
          hours = getHoursFromTemplate();

        if (!$scope.noToggleMeridian()) {
          if (angular.isDefined(minutes) && angular.isDefined(hours)) {
            addSecondsToSelected(12 * 60 * (selected.getHours() < 12 ? 60 : -60));
          } else {
            $scope.meridian = $scope.meridian === meridians[0] ? meridians[1] : meridians[0];
          }
        }
      };

      $scope.blur = function() {
        ngModelCtrl.$setTouched();
      };

      $scope.$on('$destroy', function() {
        while (watchers.length) {
          watchers.shift()();
        }
      });
    }
  ])

  .directive('uibTimepicker', [
    'uibTimepickerConfig',
    function(uibTimepickerConfig) {
      return {
        require: ['uibTimepicker', '?^ngModel'],
        restrict: 'A',
        controller: 'UibTimepickerController',
        controllerAs: 'timepicker',
        scope: {},
        templateUrl: function(element, attrs) {
          return attrs.templateUrl || uibTimepickerConfig.templateUrl;
        },
        link: function(scope, element, attrs, ctrls) {
          var timepickerCtrl = ctrls[0],
            ngModelCtrl = ctrls[1];

          if (ngModelCtrl) {
            timepickerCtrl.init(ngModelCtrl, element.find('input'));
          }
        }
      };
    }
  ]);

})();
(function () {
'use strict';

/* jshint -W018 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

/**
 * The following features are still outstanding: animation as a
 * function, placement as a function, inside, support for more triggers than
 * just mouse enter/leave, html tooltips, and selector delegation.
 */
angular
  .module('uiTooltip', ['uiPosition', 'uiStackedMap'])

  /**
   * The $tooltip service creates tooltip- and popover-like directives as well as
   * houses global options for them.
   */
  .provider('$uibTooltip', function() {
    // The default options tooltip and popover.
    var defaultOptions = {
      placement: 'top',
      placementClassPrefix: '',
      animation: true,
      popupDelay: 0,
      popupCloseDelay: 0,
      useContentExp: false
    };

    // Default hide triggers for each show trigger
    var triggerMap = {
      mouseenter: 'mouseleave',
      click: 'click',
      outsideClick: 'outsideClick',
      focus: 'blur',
      none: ''
    };

    // The options specified to the provider globally.
    var globalOptions = {};

    /**
     * `options({})` allows global configuration of all tooltips in the
     * application.
     *
     *   var app = angular.module( 'App', ['uiTooltip'], function( $tooltipProvider ) {
     *     // place tooltips left instead of top by default
     *     $tooltipProvider.options( { placement: 'left' } );
     *   });
     */
    this.options = function(value) {
      angular.extend(globalOptions, value);
    };

    /**
     * This allows you to extend the set of trigger mappings available. E.g.:
     *
     *   $tooltipProvider.setTriggers( { 'openTrigger': 'closeTrigger' } );
     */
    this.setTriggers = function setTriggers(triggers) {
      angular.extend(triggerMap, triggers);
    };

    /**
     * This is a helper function for translating camel-case to snake_case.
     */
    function snake_case(name) {
      var regexp = /[A-Z]/g;
      var separator = '-';
      return name.replace(regexp, function(letter, pos) {
        return (pos ? separator : '') + letter.toLowerCase();
      });
    }

    /**
     * Returns the actual instance of the $tooltip service.
     * TODO support multiple triggers
     */
    this.$get = [
      '$window',
      '$compile',
      '$timeout',
      '$document',
      '$uibPosition',
      '$interpolate',
      '$rootScope',
      '$parse',
      '$$stackedMap',
      function($window, $compile, $timeout, $document, $position, $interpolate, $rootScope, $parse, $$stackedMap) {
        var openedTooltips = $$stackedMap.createNew();
        $document.on('keyup', keypressListener);

        $rootScope.$on('$destroy', function() {
          $document.off('keyup', keypressListener);
        });

        function keypressListener(e) {
          if (e.which === 27) {
            var last = openedTooltips.top();
            if (last) {
              last.value.close();
              last = null;
            }
          }
        }

        return function $tooltip(ttType, prefix, defaultTriggerShow, options) {
          options = angular.extend({}, defaultOptions, globalOptions, options);

          /**
           * Returns an object of show and hide triggers.
           *
           * If a trigger is supplied,
           * it is used to show the tooltip; otherwise, it will use the `trigger`
           * option passed to the `$tooltipProvider.options` method; else it will
           * default to the trigger supplied to this directive factory.
           *
           * The hide trigger is based on the show trigger. If the `trigger` option
           * was passed to the `$tooltipProvider.options` method, it will use the
           * mapped trigger from `triggerMap` or the passed trigger if the map is
           * undefined; otherwise, it uses the `triggerMap` value of the show
           * trigger; else it will just use the show trigger.
           */
          function getTriggers(trigger) {
            var show = (trigger || options.trigger || defaultTriggerShow).split(' ');
            var hide = show.map(function(trigger) {
              return triggerMap[trigger] || trigger;
            });
            return {
              show: show,
              hide: hide
            };
          }

          var directiveName = snake_case(ttType);

          var startSym = $interpolate.startSymbol();
          var endSym = $interpolate.endSymbol();
          var template =
            '<div ' +
            directiveName +
            '-popup ' +
            'uib-title="' +
            startSym +
            'title' +
            endSym +
            '" ' +
            (options.useContentExp
              ? 'content-exp="contentExp()" '
              : 'content="' + startSym + 'content' + endSym + '" ') +
            'origin-scope="origScope" ' +
            'class="uib-position-measure ' +
            prefix +
            '" ' +
            'tooltip-animation-class="fade"' +
            'uib-tooltip-classes ' +
            'ng-class="{ in: isOpen }" ' +
            '>' +
            '</div>';

          return {
            compile: function(tElem, tAttrs) {
              var tooltipLinker = $compile(template);

              return function link(scope, element, attrs, tooltipCtrl) {
                var tooltip;
                var tooltipLinkedScope;
                var transitionTimeout;
                var showTimeout;
                var hideTimeout;
                var positionTimeout;
                var adjustmentTimeout;
                var appendToBody = angular.isDefined(options.appendToBody) ? options.appendToBody : false;
                var triggers = getTriggers(undefined);
                var hasEnableExp = angular.isDefined(attrs[prefix + 'Enable']);
                var ttScope = scope.$new(true);
                var repositionScheduled = false;
                var isOpenParse = angular.isDefined(attrs[prefix + 'IsOpen'])
                  ? $parse(attrs[prefix + 'IsOpen'])
                  : false;
                var contentParse = options.useContentExp ? $parse(attrs[ttType]) : false;
                var observers = [];
                var lastPlacement;

                var positionTooltip = function() {
                  // check if tooltip exists and is not empty
                  if (!tooltip || !tooltip.html()) {
                    return;
                  }

                  if (!positionTimeout) {
                    positionTimeout = $timeout(
                      function() {
                        if (tooltip.hasClass('uib-position-measure')) {
                          tooltip.removeClass('uib-position-measure');
                        }
                        var ttPosition = $position.positionElements(element, tooltip, ttScope.placement, appendToBody);
                        var elementPos = appendToBody ? $position.offset(element) : $position.position(element);

                        var initialHeight = angular.isDefined(tooltip.offsetHeight)
                          ? tooltip.offsetHeight
                          : tooltip.prop('offsetHeight');
                       
                        var elementRect = element[0].getBoundingClientRect();
                        var elementHeight = elementRect.height;
    
                        var tooltipRect = tooltip[0].getBoundingClientRect();
                        var tooltipHeight = tooltipRect.height;
                        var tooltipWidth = tooltipRect.width;
 
                        var viewportWidth = $window.innerWidth;
                        var viewportHeight = $window.innerHeight;
  
                        var scrollTop = $window.pageYOffset || document.documentElement.scrollTop;
                        var scrollLeft = $window.pageXOffset || document.documentElement.scrollLeft;
  
                        // Calculate available space (relative to viewport)                    
                        var availableSpaceTop = elementRect.top;
                        var availableSpaceBottom = viewportHeight - elementRect.bottom;
                                           
                        if (availableSpaceTop >= tooltipHeight) {
                          // Place above the element (default)
                          ttPosition.top = elementPos.top - tooltipHeight;
                          ttPosition.placement = 'top';
                        } else if (availableSpaceBottom >= tooltipHeight) {
                          // Place below the element
                            ttPosition.top = elementPos.top + elementHeight;
                            ttPosition.placement = 'bottom';
                        } else {
                          // If not enough space in either direction, choose the side with more space
                          if (availableSpaceTop >= availableSpaceBottom) {
                           ttPosition.top = elementPos.top - tooltipHeight;
                           ttPosition.placement = 'top';
                          } else {
                           ttPosition.top = elementPos.top + elementHeight;
                           ttPosition.placement = 'bottom';
                         }
                        }
  
                        var viewportLeft = scrollLeft;
                        var viewportRight = scrollLeft + viewportWidth;
                      
                        // Adjust horizontal position if it overflows the viewport
                        if (ttPosition.left < viewportLeft) {
                          // Adjust if tooltip goes beyond the left edge                          
                          ttPosition.left = viewportLeft;
                        } else if (ttPosition.left + tooltipWidth > (viewportRight - 20)) { // (viewportRight - x) = takes scrollbar in to account
                          // Adjust if tooltip goes beyond the right edge
                          ttPosition.left = viewportRight - tooltipWidth - 30;  
                        } 
  
                        if (appendToBody) {
                          ttPosition.top += scrollTop;
                          ttPosition.left += scrollLeft;
                        }
  
                        tooltip.css({ top: ttPosition.top + 'px', left: ttPosition.left + 'px' });
                        var placementClasses = ttPosition.placement.split('-');
                        

                        if (!tooltip.hasClass(placementClasses[0])) {
                          tooltip.removeClass(lastPlacement.split('-')[0]);
                          tooltip.addClass(placementClasses[0]);
                        }

                        if (!tooltip.hasClass(options.placementClassPrefix + ttPosition.placement)) {
                          tooltip.removeClass(options.placementClassPrefix + lastPlacement);
                          tooltip.addClass(options.placementClassPrefix + ttPosition.placement);
                        }

                        adjustmentTimeout = $timeout(
                          function() {
                            var currentHeight = angular.isDefined(tooltip.offsetHeight)
                              ? tooltip.offsetHeight
                              : tooltip.prop('offsetHeight');
                              
                            var adjustment = $position.adjustTop(
                              placementClasses,
                              elementPos,
                              initialHeight,
                              currentHeight
                            );                            
                            if (adjustment) {
                              tooltip.css(adjustment);
                            }
                            adjustmentTimeout = null;
                          },
                          0,
                          false
                        );
                        
                        //reposition arrow if placement has changed
                        if (lastPlacement !== ttPosition.placement) {
                          $position.positionArrow(tooltip, ttPosition.placement);
                        }
                        lastPlacement = ttPosition.placement;
                        
                        positionTimeout = null;
                      },
                      0,
                      false
                    );
                  }
                };
               
                // Set up the correct scope to allow transclusion later
                ttScope.origScope = scope;

                // By default, the tooltip is not open.
                // TODO add ability to start tooltip opened
                ttScope.isOpen = false;

                function toggleTooltipBind() {
                  if (!ttScope.isOpen) {
                    showTooltipBind();
                  } else {
                    hideTooltipBind();
                  }
                }

                // Show the tooltip with delay if specified, otherwise show it immediately
                function showTooltipBind() {
                  if (hasEnableExp && !scope.$eval(attrs[prefix + 'Enable'])) {
                    return;
                  }

                  cancelHide();
                  prepareTooltip();

                  if (ttScope.popupDelay) {
                    // Do nothing if the tooltip was already scheduled to pop-up.
                    // This happens if show is triggered multiple times before any hide is triggered.
                    if (!showTimeout) {
                      showTimeout = $timeout(show, ttScope.popupDelay, false);
                    }
                  } else {
                    show();
                  }
                }

                function hideTooltipBind() {
                  cancelShow();

                  if (ttScope.popupCloseDelay) {
                    if (!hideTimeout) {
                      hideTimeout = $timeout(hide, ttScope.popupCloseDelay, false);
                    }
                  } else {
                    hide();
                  }
                }

                // Show the tooltip popup element.
                function show() {
                  cancelShow();
                  cancelHide();

                  // Don't show empty tooltips.
                  if (!ttScope.content) {
                    return angular.noop;
                  }

                  createTooltip();

                  // And show the tooltip.
                  ttScope.$evalAsync(function() {
                    ttScope.isOpen = true;
                    assignIsOpen(true);
                    positionTooltip();
                  });
                }

                function cancelShow() {
                  if (showTimeout) {
                    $timeout.cancel(showTimeout);
                    showTimeout = null;
                  }

                  if (positionTimeout) {
                    $timeout.cancel(positionTimeout);
                    positionTimeout = null;
                  }
                }

                // Hide the tooltip popup element.
                function hide() {
                  if (!ttScope) {
                    return;
                  }

                  // First things first: we don't show it anymore.
                  ttScope.$evalAsync(function() {
                    if (ttScope) {
                      ttScope.isOpen = false;
                      assignIsOpen(false);
                      // And now we remove it from the DOM. However, if we have animation, we
                      // need to wait for it to expire beforehand.
                      // FIXME: this is a placeholder for a port of the transitions library.
                      // The fade transition in TWBS is 150ms.
                      if (ttScope.animation) {
                        if (!transitionTimeout) {
                          transitionTimeout = $timeout(removeTooltip, 150, false);
                        }
                      } else {
                        removeTooltip();
                      }
                    }
                  });
                }

                function cancelHide() {
                  if (hideTimeout) {
                    $timeout.cancel(hideTimeout);
                    hideTimeout = null;
                  }

                  if (transitionTimeout) {
                    $timeout.cancel(transitionTimeout);
                    transitionTimeout = null;
                  }
                }

                function createTooltip() {
                  // There can only be one tooltip element per directive shown at once.
                  if (tooltip) {
                    return;
                  }

                  tooltipLinkedScope = ttScope.$new();
                  tooltip = tooltipLinker(tooltipLinkedScope, function(tooltip) {
                    if (appendToBody) {
                      $document.find('body').append(tooltip);
                    } else {
                      element.after(tooltip);
                    }
                  });

                  openedTooltips.add(ttScope, {
                    close: hide
                  });

                  prepObservers();
                }

                function removeTooltip() {
                  cancelShow();
                  cancelHide();
                  unregisterObservers();

                  if (tooltip) {
                    tooltip.remove();

                    tooltip = null;
                    if (adjustmentTimeout) {
                      $timeout.cancel(adjustmentTimeout);
                    }
                  }

                  openedTooltips.remove(ttScope);

                  if (tooltipLinkedScope) {
                    tooltipLinkedScope.$destroy();
                    tooltipLinkedScope = null;
                  }
                }

                /**
                 * Set the initial scope values. Once
                 * the tooltip is created, the observers
                 * will be added to keep things in sync.
                 */
                function prepareTooltip() {
                  ttScope.title = attrs[prefix + 'Title'];
                  if (contentParse) {
                    ttScope.content = contentParse(scope);
                  } else {
                    ttScope.content = attrs[ttType];
                  }

                  ttScope.popupClass = attrs[prefix + 'Class'];
                  ttScope.placement = angular.isDefined(attrs[prefix + 'Placement'])
                    ? attrs[prefix + 'Placement']
                    : options.placement;
                  var placement = $position.parsePlacement(ttScope.placement);
                  lastPlacement = placement[1] ? placement[0] + '-' + placement[1] : placement[0];

                  var delay = parseInt(attrs[prefix + 'PopupDelay'], 10);
                  var closeDelay = parseInt(attrs[prefix + 'PopupCloseDelay'], 10);
                  ttScope.popupDelay = !isNaN(delay) ? delay : options.popupDelay;
                  ttScope.popupCloseDelay = !isNaN(closeDelay) ? closeDelay : options.popupCloseDelay;
                }

                function assignIsOpen(isOpen) {
                  if (isOpenParse && angular.isFunction(isOpenParse.assign)) {
                    isOpenParse.assign(scope, isOpen);
                  }
                }

                ttScope.contentExp = function() {
                  return ttScope.content;
                };

                /**
                 * Observe the relevant attributes.
                 */
                attrs.$observe('disabled', function(val) {
                  if (val) {
                    cancelShow();
                  }

                  if (val && ttScope.isOpen) {
                    hide();
                  }
                });

                if (isOpenParse) {
                  scope.$watch(isOpenParse, function(val) {
                    if (ttScope && !val === ttScope.isOpen) {
                      toggleTooltipBind();
                    }
                  });
                }

                function prepObservers() {
                  observers.length = 0;

                  if (contentParse) {
                    observers.push(
                      scope.$watch(contentParse, function(val) {
                        ttScope.content = val;
                        if (!val && ttScope.isOpen) {
                          hide();
                        }
                      })
                    );

                    observers.push(
                      tooltipLinkedScope.$watch(function() {
                        if (!repositionScheduled) {
                          repositionScheduled = true;
                          tooltipLinkedScope.$$postDigest(function() {
                            repositionScheduled = false;
                            if (ttScope && ttScope.isOpen) {
                              positionTooltip();
                            }
                          });
                        }
                      })
                    );
                  } else {
                    observers.push(
                      attrs.$observe(ttType, function(val) {
                        ttScope.content = val;
                        if (!val && ttScope.isOpen) {
                          hide();
                        } else {
                          positionTooltip();
                        }
                      })
                    );
                  }

                  observers.push(
                    attrs.$observe(prefix + 'Title', function(val) {
                      ttScope.title = val;
                      if (ttScope.isOpen) {
                        positionTooltip();
                      }
                    })
                  );

                  observers.push(
                    attrs.$observe(prefix + 'Placement', function(val) {
                      ttScope.placement = val ? val : options.placement;
                      if (ttScope.isOpen) {
                        positionTooltip();
                      }
                    })
                  );
                }

                function unregisterObservers() {
                  if (observers.length) {
                    angular.forEach(observers, function(observer) {
                      observer();
                    });
                    observers.length = 0;
                  }
                }

                // hide tooltips/popovers for outsideClick trigger
                function bodyHideTooltipBind(e) {
                  if (!ttScope || !ttScope.isOpen || !tooltip) {
                    return;
                  }
                  // make sure the tooltip/popover link or tool tooltip/popover itself were not clicked
                  if (!element[0].contains(e.target) && !tooltip[0].contains(e.target)) {
                    hideTooltipBind();
                  }
                }

                // KeyboardEvent handler to hide the tooltip on Escape key press
                function hideOnEscapeKey(e) {
                  if (e.which === 27) {
                    hideTooltipBind();
                  }
                }

                var unregisterTriggers = function() {
                  triggers.show.forEach(function(trigger) {
                    if (trigger === 'outsideClick') {
                      element.off('click', toggleTooltipBind);
                    } else {
                      element.off(trigger, showTooltipBind);
                      element.off(trigger, toggleTooltipBind);
                    }
                    element.off('keypress', hideOnEscapeKey);
                  });
                  triggers.hide.forEach(function(trigger) {
                    if (trigger === 'outsideClick') {
                      $document.off('click', bodyHideTooltipBind);
                    } else {
                      element.off(trigger, hideTooltipBind);
                    }
                  });
                };

                function prepTriggers() {
                  var showTriggers = [],
                    hideTriggers = [];
                  var val = scope.$eval(attrs[prefix + 'Trigger']);
                  unregisterTriggers();

                  if (angular.isObject(val)) {
                    Object.keys(val).forEach(function(key) {
                      showTriggers.push(key);
                      hideTriggers.push(val[key]);
                    });
                    triggers = {
                      show: showTriggers,
                      hide: hideTriggers
                    };
                  } else {
                    triggers = getTriggers(val);
                  }

                  if (triggers.show !== 'none') {
                    triggers.show.forEach(function(trigger, idx) {
                      if (trigger === 'outsideClick') {
                        element.on('click', toggleTooltipBind);
                        $document.on('click', bodyHideTooltipBind);
                      } else if (trigger === triggers.hide[idx]) {
                        element.on(trigger, toggleTooltipBind);
                      } else if (trigger) {
                        element.on(trigger, showTooltipBind);
                        element.on(triggers.hide[idx], hideTooltipBind);
                      }
                      element.on('keypress', hideOnEscapeKey);
                    });
                  }
                }

                prepTriggers();

                var animation = scope.$eval(attrs[prefix + 'Animation']);
                ttScope.animation = angular.isDefined(animation) ? !!animation : options.animation;

                var appendToBodyVal;
                var appendKey = prefix + 'AppendToBody';
                if (appendKey in attrs && attrs[appendKey] === undefined) {
                  appendToBodyVal = true;
                } else {
                  appendToBodyVal = scope.$eval(attrs[appendKey]);
                }

                appendToBody = angular.isDefined(appendToBodyVal) ? appendToBodyVal : appendToBody;

                // Make sure tooltip is destroyed and removed.
                scope.$on('$destroy', function onDestroyTooltip() {
                  unregisterTriggers();
                  removeTooltip();
                  ttScope = null;
                });
              };
            }
          };
        };
      }
    ];
  })

  // This is mostly ngInclude code but with a custom scope
  .directive('uibTooltipTemplateTransclude', ["$animate", "$sce", "$compile", "$templateRequest", function($animate, $sce, $compile, $templateRequest) {
    return {
      link: function(scope, elem, attrs) {
        var origScope = scope.$eval(attrs.tooltipTemplateTranscludeScope);

        var changeCounter = 0,
          currentScope,
          previousElement,
          currentElement;

        var cleanupLastIncludeContent = function() {
          if (previousElement) {
            previousElement.remove();
            previousElement = null;
          }

          if (currentScope) {
            currentScope.$destroy();
            currentScope = null;
          }

          if (currentElement) {
            $animate.leave(currentElement).then(function() {
              previousElement = null;
            });
            previousElement = currentElement;
            currentElement = null;
          }
        };

        scope.$watch($sce.parseAsResourceUrl(attrs.uibTooltipTemplateTransclude), function(src) {
          var thisChangeId = ++changeCounter;

          if (src) {
            //set the 2nd param to true to ignore the template request error so that the inner
            //contents and scope can be cleaned up.
            $templateRequest(src, true).then(
              function(response) {
                if (thisChangeId !== changeCounter) {
                  return;
                }
                var newScope = origScope.$new();
                var template = response;

                var clone = $compile(template)(newScope, function(clone) {
                  cleanupLastIncludeContent();
                  $animate.enter(clone, elem);
                });

                currentScope = newScope;
                currentElement = clone;

                currentScope.$emit('$includeContentLoaded', src);
              },
              function() {
                if (thisChangeId === changeCounter) {
                  cleanupLastIncludeContent();
                  scope.$emit('$includeContentError', src);
                }
              }
            );
            scope.$emit('$includeContentRequested', src);
          } else {
            cleanupLastIncludeContent();
          }
        });

        scope.$on('$destroy', cleanupLastIncludeContent);
      }
    };
  }])

  /**
   * Note that it's intentional that these classes are *not* applied through $animate.
   * They must not be animated as they're expected to be present on the tooltip on
   * initialization.
   */
  .directive('uibTooltipClasses', ["$uibPosition", function($uibPosition) {
    return {
      restrict: 'A',
      link: function(scope, element, attrs) {
        // need to set the primary position so the
        // arrow has space during position measure.
        // tooltip.positionTooltip()
        if (scope.placement) {
          // // There are no top-left etc... classes
          // // in TWBS, so we need the primary position.
          var position = $uibPosition.parsePlacement(scope.placement);
          element.addClass(position[0]);
        }

        if (scope.popupClass) {
          element.addClass(scope.popupClass);
        }

        if (scope.animation) {
          element.addClass(attrs.tooltipAnimationClass);
        }
      }
    };
  }])

  .directive('uibTooltipPopup', function() {
    return {
      restrict: 'A',
      scope: { content: '@' },
      templateUrl: 'components/tooltip/tooltip-popup.html'
    };
  })

  .directive('uibTooltip', ["$uibTooltip", function($uibTooltip) {
    return $uibTooltip('uibTooltip', 'tooltip', 'mouseenter');
  }])

  .directive('uibTooltipTemplatePopup', function() {
    return {
      restrict: 'A',
      scope: { contentExp: '&', originScope: '&' },
      templateUrl: 'components/tooltip/tooltip-template-popup.html'
    };
  })

  .directive('uibTooltipTemplate', ["$uibTooltip", function($uibTooltip) {
    return $uibTooltip('uibTooltipTemplate', 'tooltip', 'mouseenter', {
      useContentExp: true
    });
  }])

  .directive('uibTooltipHtmlPopup', function() {
    return {
      restrict: 'A',
      scope: { contentExp: '&' },
      templateUrl: 'components/tooltip/tooltip-html-popup.html'
    };
  })

  .directive('uibTooltipHtml', ["$uibTooltip", function($uibTooltip) {
    return $uibTooltip('uibTooltipHtml', 'tooltip', 'mouseenter', {
      useContentExp: true
    });
  }]);

/* jshint +W018 */
/* jshint +W071 */
/* jshint +W101 */
/* jshint +W106 */

})();
(function () {
'use strict';

const cmp = (label, filter) => label.toLowerCase().includes(filter.toLowerCase());

const item_cmp = (item, filter) => cmp(item.label, filter) 
    ? true
    : item.subitems && item.subitems.some(i => item_cmp(i, filter));

const setTickedTree = function (subitems, ticked, newValue, filter) {
  subitems.forEach(element => {
    if ( !(filter && filter.length) || cmp(element.label, filter) ) {
        ticked[element.data] = newValue;
    }
    
    if (element.subitems) {
      setTickedTree(element.subitems, ticked, newValue, filter);
    }
  });
}

const flatTree = (obj, result = [], level = 0) => {
  result.push({ 
    data: obj.data,
    label: obj.label,
    level,
  });
  if (obj.subitems) {
   	obj.subitems.forEach(o => flatTree(o, result, level+1));
  }
  return result;
}

angular.module('treeNode', [])
  .directive('treeNode', () => {
    return {
      scope: {
        label: '@',
        data: '=',
        subitems: '=',
        checkboxes: '<',
        selected: '=',
        model: '=',
        ticked: '=',
        viewbag: '=',
      },
      restrict: 'E',
      templateUrl: 'components/treeview/tree-node.html',
      link: function (scope) {
        scope.filterMatch = function () {
          return item_cmp(scope, scope.viewbag.filter);
        }
        scope.selectMe = function () {
          scope.selected({
            newValue: {
              label: scope.label,
              data: scope.data,
            }
          });
        }
        scope.tickMe = function () {
          setTimeout(() => {
            if (scope.subitems) {
              setTickedTree(scope.subitems, scope.ticked, scope.ticked[scope.data], scope.viewbag.filter);
            }
            scope.selected();
          }, 100);
        }
      }
    }
  });

angular.module('treeView', [])
  .directive('treeView', () => {
    return {
      scope: {
        mode: '@',
        root: '=',
        checkboxes: '<',
        collapseOnSelect: '<',
        showSelectionOnCollapse: '<',
        skipRoot: '<',
        filtering: '<',
        filterPrompt: '@',

        selected: '&',

        model: '=',
        tickedNodes: '=',

        changed: '&',
      },
      restrict: 'E',
      templateUrl: 'components/treeview/tree-view.html',
      link: function (scope, el, attrs) {

        scope.viewbag = { filter: '' };

        scope.$watch('mode', newMode => {
          switch (newMode) {
            case 'field':            
            scope.showSelectionOnCollapse = true;
            scope.collapseOnSelect = true;
              break;
            case 'list': 
            scope.skipRoot = true;
              break;
          }
          //scope.$apply();
        });
        scope.$watch('root', newRoot => {
          scope.ticked = {};
        });

        scope.setEvents = () => {
          let togglers = document.getElementsByClassName("treeview-caret");
          let i;

          for (i = 0; i < togglers.length; i++) {
            togglers[i].addEventListener("click", function () {
              this.parentElement.querySelector(".treeview-nested").classList.toggle("treeview-active");
              this.classList.toggle("treeview-caret-down");
            });
          }
        };
        scope.reset = () => {
          scope.model = undefined;
          setTimeout(() => { scope.setEvents() }, 500);
        };
        scope.selected = eventData => {
          if (eventData && eventData.newValue) {
            scope.model = eventData.newValue;

            if (scope.collapseOnSelect) {
              let togglers = document.getElementsByClassName("treeview-caret-down");
              let i;
              for (i = 0; i < togglers.length; i++) {
                let el = togglers[i];
                el.parentElement.querySelector('.treeview-nested').classList.toggle("treeview-active");
                el.classList.toggle("treeview-caret-down");
              }
            }
          } else {
            if (scope.checkboxes) {
              let flat = flatTree(scope.root);
              scope.tickedNodes = flat.filter(x => scope.ticked[x.data] === true);
              scope.$apply();
            }
          }

          if (angular.isDefined(attrs['changed'])) {
            scope.changed();
          }
        };
        setTimeout(() => { scope.setEvents() }, 500);
      }
    }
  });
})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('HelperService', function() {
  function dynamicSortSwe(property, order) {
    return function(a, b) {
      const aValue = getPropertyValue(a, property);
      const bValue = getPropertyValue(b, property);
      if (aValue == null || bValue == null) {
        return 0;
      }
      return order.toLowerCase() === 'asc' ? compareMixed(aValue, bValue) : compareMixed(bValue, aValue);
    };
  }

  // Compares mixed strings with numbers and letters
  function compareMixed(a, b) {
    const re = /(\d+|\D+)/g;
    const aParts = String(a).match(re);
    const bParts = String(b).match(re);
    if (!aParts || !bParts) {
      return !!aParts - !!bParts;
    } else {
      while (aParts.length && bParts.length) {
        const aPart = aParts.shift();
        const bPart = bParts.shift();
        const aPartNum = parseInt(aPart, 10);
        const bPartNum = parseInt(bPart, 10);
        if (!isNaN(aPartNum) && !isNaN(bPartNum)) {
          if (aPartNum !== bPartNum) return aPartNum - bPartNum;
        } else if (aPart !== bPart) {
          return aPart.localeCompare(bPart, 'sv');
        }
      }
      return aParts.length - bParts.length;
    }
  }

  // Helper function to get the value of a nested property
  function getPropertyValue(obj, property) {
    return property.split('.').reduce((o, i) => (o ? o[i] : null), obj);
  }

  return {
    dynamicSortSwe: dynamicSortSwe,
  };
});
})();
(function () {
'use strict';


/**
 * @typedef {Object} ComplexPhone
 * @property {string} countryCode
 * @property {string} number
 */

/**
 * @typedef {Object} VisualOptions
 * @property {boolean} isGreen
 * @property {string} size
 */

HyUtils.$inject = ["NegotiationRoles", "$window", "$uibModal"];
angular
  .module('hyUtils', [])
  .factory('HyUtils', HyUtils);

function HyUtils(NegotiationRoles, $window, $uibModal) {

  /**
   * determines if element is visible on the screen
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  let isVisible = el => el.getClientRects().length > 0;

  /**
   * unions two arrays of primitive elements
   * @param {Array} a
   * @param {Array} b
   * @returns {Array}
   */
  let unionArrays = (a, b) => a.concat(b.filter((item) => a.indexOf(item) < 0));

  /**
   * unions two arrays of objects using compare function
   * @param {Array} a
   * @param {Array} b
   * @param {function(Object, Object):boolean} fn
   * @returns {Array}
   */
  let unionArraysFn = (a, b, fn) => a.concat(b.filter(item => !a.some(elA => fn(item, elA))));

  /**
   * shows default modal
   * @param {string} ctrl
   * @param {string} template
   * @param {object} variables object with functions that resolves modal ctrl variables
   * @param {*} [result] result callback
   * @param {*} [cancel] cancel callback
   * @param {*} [error] error callback
   * @param {VisualOptions} visualOptions visual options
   */
  let showDefaultModal = (ctrl, template, variables, result, cancel, error, visualOptions) => {
    if (!visualOptions) {
      visualOptions = {};
    }
    visualOptions.size = visualOptions.size ? visualOptions.size : 'lg';

    let resolve = {};
    Object.keys(variables).forEach(k => resolve[k] = () => variables[k]);
    let options = {
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: visualOptions.size,
      templateUrl: template,
      controller: ctrl,
      controllerAs: 'vm',
      windowClass: visualOptions.isGreen ? 'modal-content--green' : 'modal-content--secondary',
      resolve,
    };
    if (ctrl.toLowerCase().includes('modal')) {
      Object.assign(options, {
        backdrop: 'static',
        keyboard: false,
      });
    }
    var instance = $uibModal.open(options);

    if (result) {
      instance.result.then(res => result(res));
    }
    if (cancel) {
      instance.closed.then(() => cancel());
    }
    if (error) {
      instance.result.catch(err => error(err));
    }
  }

  /**
   * shows modal with question and answer buttons. If no answers provided, shows Yes/No
   * @param {string} question
   * @param {Array.<string>} [answers]
   * @param {number} [type] visual appearence type, 1 or 2, 1 is default
   */
  let askQuestion = (question, answers, type) => {
    if (!answers) {
      answers = ['Ja', 'Nej'];
    }
    if (!type) {
      type = 1;
    }

    let windowClass = 'modal-content--secondary';
    if (type === 2) {
      windowClass += ' modal--centred modal-content--green';
    }
    let templateUrl = `components/modal/question/question-${type}.html`;
    return new Promise((resolve, reject) => {
      var instance = $uibModal.open({
        animation: true,
        ariaLabelledBy: 'modal-title-top',
        ariaDescribedBy: 'modal-body-top',
        size: 'md',
        templateUrl,
        controller: 'QuestionCtrl',
        controllerAs: 'vm',
        windowClass,
        resolve: {
          message: () => question,
          answers: () => answers,
        }
      });

      instance.result.then(res => resolve(res));
      instance.result.catch(err => reject(err));
    });
  }

  let spitOutAttachment = (data, filename, contentType) => {
    const supablob = new Blob([new Uint8Array(data)], {
      type: contentType,
    });

    var downloadLink = angular.element('<a></a>');
    downloadLink.attr('href', $window.URL.createObjectURL(supablob));
    downloadLink.attr('download', filename);
    downloadLink[0].click();
  }

  /**
   * Finds role by code.
   * Chief and deChief as needed.
   * No, lookup is not better, deal with it
   *
   * @param {int|string} code
   * @param {boolean|undefined} wantChief
   */
  let findNegotiationRole = (code, wantChief) => {
    // eslint-disable-next-line no-undefined
    if (wantChief === undefined) {
      // eslint-disable-next-line eqeqeq
      return Object.values(NegotiationRoles).find(nr => nr.code == code);
    } else {
      // eslint-disable-next-line eqeqeq
      var key = Object.keys(NegotiationRoles).find(nrk => NegotiationRoles[nrk].code == code);
      var hasChief = (key.indexOf('_CHIEF') > 0);
      if (hasChief === wantChief) {
        return NegotiationRoles[key];
      } else if ((!hasChief) && (wantChief)) {
        return NegotiationRoles[key + '_CHIEF'];
      } else if ((hasChief) && (!wantChief)) {
        return NegotiationRoles[key.substring(0, key.indexOf('_CHIEF'))];
      }
    }
  }

  /**
   * @param {string} phoneString
   * @returns {ComplexPhone} countryCode and number
   */
  let tamePhoneNumber = phoneString => {
    const validCountryCodes = ['+358', '+47', '+45', '+44', '+371'];

    phoneString = phoneString
      .replace(/\s+/g, '')
      .replace(/-+/g, '');

    if (phoneString.startsWith('0')) {
      return { countryCode: '+46', number: phoneString.substring(1) };
    } else if (phoneString.startsWith('+46')) {
      return { countryCode: '+46', number: phoneString.substring(3) };
    } else if (phoneString.startsWith('+')) {
      let countryCode = validCountryCodes.find(code => phoneString.startsWith(code));
      return { countryCode, number: phoneString.substring(countryCode.length) };
    } else {
      return { countryCode: '+46', number: phoneString };
    }
  };

  let parseDate = str => {
    const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{4,}Z?$/;
    let hasHiPrecisionTime = regex.test(str);
    return hasHiPrecisionTime 
      ? new Date(str)
      : new Date.fromString(str);
  };

  /**
   * returns Date object if value is string and contains one
   * @param {string} key
   * @param {any} value
   * @returns {any | Date}
   */
  let dtRev = (key, value) => {
    if (typeof value === 'string') {
      let d = parseDate(value);
      return isNaN(d) ? value : d;
    }
    return value;
  };

  /**
   * searches for object in all controller instances
   * @param {string} objectName 
   */
  let bruteSearch = (scope, objectName) => {
    let child = null;
    for(var cs = scope.$$childHead; cs; cs = cs.$$nextSibling) {
      if (cs.$$childTail && cs.$$childTail[objectName]) {
        child = cs.$$childTail[objectName];
      }
    }
    return child;
  };

  /**
   * gets difference in days between two given dates
   * date strings should be ISO or in current locale, otherwise it will fail
   * @param {string|Date} date1
   * @param {string|Date} date2
   */
  let diffDays = (date1, date2) => {
    date1 = new Date(date1);
    date2 = new Date(date2);
    date1 = new Date(date1.toDateString());
    date2 = new Date(date2.toDateString());
    const diffTime = Math.abs(date2 - date1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  /**
   * determines if luhn is valid
   * @param {string} orgNumber 
   * @returns {boolean}
   */
  let validateLuhn = orgNumber => {
    var trimmed = String(orgNumber).replace(/[\s]/g, "")
      , length = trimmed.length
      , odd = false
      , total = 0
      , calc
      , calc2;

    if (length === 0) {
      return true;
    }

    if (!/^[0-9]+$/.test(trimmed)) {
      return false;
    }

    for (var i = length; i > 0; i--) {
      calc = parseInt(trimmed.charAt(i - 1));
      if (!odd) {
        total += calc;
      } else {
        calc2 = calc * 2;

        switch (calc2) {
          case 10: calc2 = 1; break;
          case 12: calc2 = 3; break;
          case 14: calc2 = 5; break;
          case 16: calc2 = 7; break;
          case 18: calc2 = 9; break;
          default: calc2 = calc2;
        }
        total += calc2;
      }
      odd = !odd;
    }

    return (total !== 0 && (total % 10) === 0);
  };

  /**
   * gets streetAddress from object
   * @param {Object} address 
   * @returns {string}
   */
  const renderStreetAddress = address => [
    address.street,
    address.streetNumber,
    address.entrance
  ].filter(x => x).join(' ');

  const testStreetNumber = str => {
    const numbersOnly = /^\d+$/;
    const numbersHyphenNumbersStrings = /^\d+([A-Za-z]+)?-(\d+)?([A-Za-z]+)?$/;
    return numbersOnly.test(str) || numbersHyphenNumbersStrings.test(str);
  };

  /**
   * parses street address
   * @param {string} streetAddress
   * @returns {{address: string, number: number, entrance: string}}
   */
  const splitStreetAddress = streetAddress => {
    let street = null;
    let streetNumber = null;
    // eslint-disable-next-line no-undefined
    let entrance = undefined;
    let tokens = streetAddress.split(' ');

    if (tokens.length > 2) {
      let last = tokens.pop();
      if (testStreetNumber(last)) {
        streetNumber = last;
      } else {
        entrance = last;
        streetNumber = tokens.pop();
      }
      street = tokens.join(' ');
    } else {
      [street, streetNumber] = tokens;
    }
    if (entrance) {
      entrance = entrance.toUpperCase();
    }
    return { street, streetNumber, entrance };
  };

  let whereAmI = () => {
    const hostMaps = {
      localhost: {
        FP: 'localhost:3000',
        SP: 'localhost:3344',
      },
      test: {
        FP: 'forhandlarportalen-test.azurewebsites.net',
        SP: 'fpstats-test.azurewebsites.net',
      },
      demo: {
        FP: 'forhandlarportalen-demo.azurewebsites.net',
        SP: 'fpstats-demo.azurewebsites.net',
      },
      prod: {
        FP: 'forhandlarportalen.se',
        SP: 'fpstats-prod.azurewebsites.net',
      }
    };
    let myhost = $window.location.hostname;
    let myport = $window.location.port;
    let key = Object.keys(hostMaps).find(k => 
      (hostMaps[k].FP.includes(myhost)) || 
      (hostMaps[k].SP.includes(myhost)));
    let host = hostMaps[key];
    let isFP = host.FP.includes(myhost) && (host.FP.includes(':') ? host.FP.includes(myport) : true);
    let isSP = host.SP.includes(myhost) && (host.SP.includes(':') ? host.SP.includes(myport) : true);
    return key ? { [key]: true, host, isFP, isSP } : {};
  }

  return {
    tamePhoneNumber,
    findNegotiationRole,
    spitOutAttachment,
    askQuestion,
    showDefaultModal,
    unionArrays,
    dtRev,
    unionArraysFn,
    isVisible,
    bruteSearch,
    diffDays,
    validateLuhn,
    renderStreetAddress,
    splitStreetAddress,
    whereAmI,
    parseDate,
    testStreetNumber,
  };
}
})();
(function () {
'use strict';

angular.module('stringUtils', []).factory('StringUtils', StringUtils);

function StringUtils() {
  // Multiple number formats
  //    Match: 3 or 3.3 or 3,3 or 3000 or 3 000;
  var multipleNumberFormatsReg = /^[\d\s]+([,\.]\d+)?$/;
  var numericFormatReg = /^-?[\d\s]+([,\.]\d+)?$/;

  var service = {
    getMultipleNumberFormatsReg: getMultipleNumberFormatsReg,
    getNumericFormatReg: getNumericFormatReg,
    sanitizeMultipleNumberFormats: sanitizeMultipleNumberFormats,
    stringSort: property => (a, b) => a[property] < b[property] ? -1 : a[property] > b[property] ? 1 : 0,
  };

  return service;

  ////////////

  function getMultipleNumberFormatsReg() {
    return multipleNumberFormatsReg;
  }

  function getNumericFormatReg() {
    return numericFormatReg;
  }

  // Remove whitespace and replace comma with a dot
  function sanitizeMultipleNumberFormats(input) {
    if (typeof input === 'string' || input instanceof String) {
      input = input.replace(/[^0-9\.,]/g, '');
      input = input.replace(/,/g, '.');

      // backend does not like empty strings ""
      if (input.length > 0) {
        return input;
      }
      return null;
    }
    // if the input is a number
    return input;
  }
}

})();
(function () {
angular
  .module('uiTypeahead', ['uiDebounce', 'uiPosition'])

  /**
   * A helper service that can parse typeahead's syntax (string provided by users)
   * Extracted to a separate service for ease of unit testing
   */
  .factory('uibTypeaheadParser', [
    '$parse',
    function($parse) {
      //                      000001111111100000000000002222222200000000000000003333333333333330000000000044444444000
      var TYPEAHEAD_REGEXP = /^\s*([\s\S]+?)(?:\s+as\s+([\s\S]+?))?\s+for\s+(?:([\$\w][\$\w\d]*))\s+in\s+([\s\S]+?)$/;
      return {
        parse: function(input) {
          var match = input.match(TYPEAHEAD_REGEXP);
          if (!match) {
            throw new Error(
              'Expected typeahead specification in form of "_modelValue_ (as _label_)? for _item_ in _collection_"' +
                ' but got "' +
                input +
                '".'
            );
          }

          return {
            itemName: match[3],
            source: $parse(match[4]),
            viewMapper: $parse(match[2] || match[1]),
            modelMapper: $parse(match[1])
          };
        }
      };
    }
  ])

  .controller('UibTypeaheadController', [
    '$scope',
    '$element',
    '$attrs',
    '$compile',
    '$parse',
    '$q',
    '$timeout',
    '$document',
    '$window',
    '$rootScope',
    '$$debounce',
    '$uibPosition',
    'uibTypeaheadParser',
    function(
      originalScope,
      element,
      attrs,
      $compile,
      $parse,
      $q,
      $timeout,
      $document,
      $window,
      $rootScope,
      $$debounce,
      $position,
      typeaheadParser
    ) {
      var HOT_KEYS = [9, 13, 27, 38, 40];
      var eventDebounceTime = 200;
      var modelCtrl, ngModelOptions;
      //SUPPORTED ATTRIBUTES (OPTIONS)

      //minimal no of characters that needs to be entered before typeahead kicks-in
      var minLength = originalScope.$eval(attrs.typeaheadMinLength);
      if (!minLength && minLength !== 0) {
        minLength = 1;
      }

      originalScope.$watch(attrs.typeaheadMinLength, function(newVal) {
        minLength = !newVal && newVal !== 0 ? 1 : newVal;
      });

      //minimal wait time after last character typed before typeahead kicks-in
      var waitTime = originalScope.$eval(attrs.typeaheadWaitMs) || 0;

      //should it restrict model values to the ones selected from the popup only?
      var isEditable = originalScope.$eval(attrs.typeaheadEditable) !== false;
      originalScope.$watch(attrs.typeaheadEditable, function(newVal) {
        isEditable = newVal !== false;
      });

      //binding to a variable that indicates if matches are being retrieved asynchronously
      var isLoadingSetter = $parse(attrs.typeaheadLoading).assign || angular.noop;

      //a function to determine if an event should cause selection
      var isSelectEvent = attrs.typeaheadShouldSelect
        ? $parse(attrs.typeaheadShouldSelect)
        : function(scope, vals) {
            var evt = vals.$event;
            return evt.which === 13 || evt.which === 9;
          };

      //a callback executed when a match is selected
      var onSelectCallback = $parse(attrs.typeaheadOnSelect);

      //should it select highlighted popup value when losing focus?
      var isSelectOnBlur = angular.isDefined(attrs.typeaheadSelectOnBlur)
        ? originalScope.$eval(attrs.typeaheadSelectOnBlur)
        : false;

      //binding to a variable that indicates if there were no results after the query is completed
      var isNoResultsSetter = $parse(attrs.typeaheadNoResults).assign || angular.noop;

      var inputFormatter = attrs.typeaheadInputFormatter ? $parse(attrs.typeaheadInputFormatter) : undefined;

      var appendToBody = attrs.typeaheadAppendToBody ? originalScope.$eval(attrs.typeaheadAppendToBody) : false;

      var appendTo = attrs.typeaheadAppendTo ? originalScope.$eval(attrs.typeaheadAppendTo) : null;

      var focusFirst = originalScope.$eval(attrs.typeaheadFocusFirst) !== false;

      //If input matches an item of the list exactly, select it automatically
      var selectOnExact = attrs.typeaheadSelectOnExact ? originalScope.$eval(attrs.typeaheadSelectOnExact) : false;

      //binding to a variable that indicates if dropdown is open
      var isOpenSetter = $parse(attrs.typeaheadIsOpen).assign || angular.noop;

      var showHint = originalScope.$eval(attrs.typeaheadShowHint) || false;

      //INTERNAL VARIABLES

      //model setter executed upon match selection
      var parsedModel = $parse(attrs.ngModel);
      var invokeModelSetter = $parse(attrs.ngModel + '($$$p)');
      var $setModelValue = function(scope, newValue) {
        if (angular.isFunction(parsedModel(originalScope)) && ngModelOptions.getOption('getterSetter')) {
          return invokeModelSetter(scope, { $$$p: newValue });
        }

        return parsedModel.assign(scope, newValue);
      };

      //expressions used by typeahead
      var parserResult = typeaheadParser.parse(attrs.uibTypeahead);

      var hasFocus;

      //Used to avoid bug in iOS webview where iOS keyboard does not fire
      //mousedown & mouseup events
      //Issue #3699
      var selected;

      //create a child scope for the typeahead directive so we are not polluting original scope
      //with typeahead-specific data (matches, query etc.)
      var scope = originalScope.$new();
      var offDestroy = originalScope.$on('$destroy', function() {
        scope.$destroy();
      });
      scope.$on('$destroy', offDestroy);

      // WAI-ARIA
      var popupId = 'typeahead-' + scope.$id + '-' + Math.floor(Math.random() * 10000);
      element.attr({
        'aria-autocomplete': 'list',
        'aria-expanded': false,
        'aria-owns': popupId
      });

      var inputsContainer, hintInputElem;
      //add read-only input to show hint
      if (showHint) {
        inputsContainer = angular.element('<div></div>');
        inputsContainer.css('position', 'relative');
        element.after(inputsContainer);
        hintInputElem = element.clone();
        hintInputElem.attr('placeholder', '');
        hintInputElem.attr('tabindex', '-1');
        hintInputElem.val('');
        hintInputElem.css({
          position: 'absolute',
          top: '0px',
          left: '0px',
          'border-color': 'transparent',
          'box-shadow': 'none',
          opacity: 1,
          background: 'none 0% 0% / auto repeat scroll padding-box border-box rgb(255, 255, 255)',
          color: '#999'
        });
        element.css({
          position: 'relative',
          'vertical-align': 'top',
          'background-color': 'transparent'
        });

        if (hintInputElem.attr('id')) {
          hintInputElem.removeAttr('id'); // remove duplicate id if present.
        }
        inputsContainer.append(hintInputElem);
        hintInputElem.after(element);
      }

      //pop-up element used to display matches
      var popUpEl = angular.element('<div uib-typeahead-popup></div>');
      popUpEl.attr({
        id: popupId,
        matches: 'matches',
        active: 'activeIdx',
        select: 'select(activeIdx, evt)',
        'move-in-progress': 'moveInProgress',
        query: 'query',
        position: 'position',
        'assign-is-open': 'assignIsOpen(isOpen)',
        debounce: 'debounceUpdate'
      });
      //custom item template
      if (angular.isDefined(attrs.typeaheadTemplateUrl)) {
        popUpEl.attr('template-url', attrs.typeaheadTemplateUrl);
      }

      if (angular.isDefined(attrs.typeaheadPopupTemplateUrl)) {
        popUpEl.attr('popup-template-url', attrs.typeaheadPopupTemplateUrl);
      }

      var resetHint = function() {
        if (showHint) {
          hintInputElem.val('');
        }
      };

      var resetMatches = function() {
        scope.matches = [];
        scope.activeIdx = -1;
        element.attr('aria-expanded', false);
        resetHint();
      };

      var getMatchId = function(index) {
        return popupId + '-option-' + index;
      };

      // Indicate that the specified match is the active (pre-selected) item in the list owned by this typeahead.
      // This attribute is added or removed automatically when the `activeIdx` changes.
      scope.$watch('activeIdx', function(index) {
        if (index < 0) {
          element.removeAttr('aria-activedescendant');
        } else {
          element.attr('aria-activedescendant', getMatchId(index));
        }
      });

      var inputIsExactMatch = function(inputValue, index) {
        if (scope.matches.length > index && inputValue) {
          return inputValue.toUpperCase() === scope.matches[index].label.toUpperCase();
        }

        return false;
      };

      var getMatchesAsync = function(inputValue, evt) {
        var locals = { $viewValue: inputValue };
        isLoadingSetter(originalScope, true);
        isNoResultsSetter(originalScope, false);
        $q.when(parserResult.source(originalScope, locals)).then(
          function(matches) {
            //it might happen that several async queries were in progress if a user were typing fast
            //but we are interested only in responses that correspond to the current view value
            var onCurrentRequest = inputValue === modelCtrl.$viewValue;
            if (onCurrentRequest && hasFocus) {
              if (matches && matches.length > 0) {
                scope.activeIdx = focusFirst ? 0 : -1;
                isNoResultsSetter(originalScope, false);
                scope.matches.length = 0;

                //transform labels
                for (var i = 0; i < matches.length; i++) {
                  locals[parserResult.itemName] = matches[i];
                  scope.matches.push({
                    id: getMatchId(i),
                    label: parserResult.viewMapper(scope, locals),
                    model: matches[i]
                  });
                }

                scope.query = inputValue;
                //position pop-up with matches - we need to re-calculate its position each time we are opening a window
                //with matches as a pop-up might be absolute-positioned and position of an input might have changed on a page
                //due to other elements being rendered
                recalculatePosition();

                element.attr('aria-expanded', true);

                //Select the single remaining option if user input matches
                if (selectOnExact && scope.matches.length === 1 && inputIsExactMatch(inputValue, 0)) {
                  if (angular.isNumber(scope.debounceUpdate) || angular.isObject(scope.debounceUpdate)) {
                    $$debounce(function() {
                      scope.select(0, evt);
                    }, angular.isNumber(scope.debounceUpdate) ? scope.debounceUpdate : scope.debounceUpdate['default']);
                  } else {
                    scope.select(0, evt);
                  }
                }

                if (showHint) {
                  var firstLabel = scope.matches[0].label;
                  if (
                    angular.isString(inputValue) &&
                    inputValue.length > 0 &&
                    firstLabel.slice(0, inputValue.length).toUpperCase() === inputValue.toUpperCase()
                  ) {
                    hintInputElem.val(inputValue + firstLabel.slice(inputValue.length));
                  } else {
                    hintInputElem.val('');
                  }
                }
              } else {
                resetMatches();
                isNoResultsSetter(originalScope, true);
              }
            }
            if (onCurrentRequest) {
              isLoadingSetter(originalScope, false);
            }
          },
          function() {
            resetMatches();
            isLoadingSetter(originalScope, false);
            isNoResultsSetter(originalScope, true);
          }
        );
      };

      // bind events only if appendToBody params exist - performance feature
      if (appendToBody) {
        angular.element($window).on('resize', fireRecalculating);
        $document.find('body').on('scroll', fireRecalculating);
      }

      // Declare the debounced function outside recalculating for
      // proper debouncing
      var debouncedRecalculate = $$debounce(function() {
        // if popup is visible
        if (scope.matches.length) {
          recalculatePosition();
        }

        scope.moveInProgress = false;
      }, eventDebounceTime);

      // Default progress type
      scope.moveInProgress = false;

      function fireRecalculating() {
        if (!scope.moveInProgress) {
          scope.moveInProgress = true;
          scope.$digest();
        }

        debouncedRecalculate();
      }

      // recalculate actual position and set new values to scope
      // after digest loop is popup in right position
      function recalculatePosition() {
        scope.position = appendToBody ? $position.offset(element) : $position.position(element);
        scope.position.top += element.prop('offsetHeight');
      }

      //we need to propagate user's query so we can higlight matches
      scope.query = undefined;

      //Declare the timeout promise var outside the function scope so that stacked calls can be cancelled later
      var timeoutPromise;

      var scheduleSearchWithTimeout = function(inputValue) {
        timeoutPromise = $timeout(function() {
          getMatchesAsync(inputValue);
        }, waitTime);
      };

      var cancelPreviousTimeout = function() {
        if (timeoutPromise) {
          $timeout.cancel(timeoutPromise);
        }
      };

      resetMatches();

      scope.assignIsOpen = function(isOpen) {
        isOpenSetter(originalScope, isOpen);
      };

      scope.select = function(activeIdx, evt) {
        //called from within the $digest() cycle
        var locals = {};
        var model, item;

        selected = true;
        locals[parserResult.itemName] = item = scope.matches[activeIdx].model;
        model = parserResult.modelMapper(originalScope, locals);
        $setModelValue(originalScope, model);
        modelCtrl.$setValidity('editable', true);
        modelCtrl.$setValidity('parse', true);

        onSelectCallback(originalScope, {
          $item: item,
          $model: model,
          $label: parserResult.viewMapper(originalScope, locals),
          $event: evt
        });

        resetMatches();

        //return focus to the input element if a match was selected via a mouse click event
        // use timeout to avoid $rootScope:inprog error
        if (scope.$eval(attrs.typeaheadFocusOnSelect) !== false) {
          $timeout(
            function() {
              element[0].focus();
            },
            0,
            false
          );
        }
      };

      //bind keyboard events: arrows up(38) / down(40), enter(13) and tab(9), esc(27)
      element.on('keydown', function(evt) {
        //typeahead is open and an "interesting" key was pressed
        if (scope.matches.length === 0 || HOT_KEYS.indexOf(evt.which) === -1) {
          return;
        }

        var shouldSelect = isSelectEvent(originalScope, { $event: evt });

        /**
         * if there's nothing selected (i.e. focusFirst) and enter or tab is hit
         * or
         * shift + tab is pressed to bring focus to the previous element
         * then clear the results
         */
        if ((scope.activeIdx === -1 && shouldSelect) || (evt.which === 9 && !!evt.shiftKey)) {
          resetMatches();
          scope.$digest();
          return;
        }

        evt.preventDefault();
        var target;
        switch (evt.which) {
          case 27: // escape
            evt.stopPropagation();

            resetMatches();
            originalScope.$digest();
            break;
          case 38: // up arrow
            scope.activeIdx = (scope.activeIdx > 0 ? scope.activeIdx : scope.matches.length) - 1;
            scope.$digest();
            target = popUpEl[0].querySelectorAll('.uib-typeahead-match')[scope.activeIdx];
            target.parentNode.scrollTop = target.offsetTop;
            break;
          case 40: // down arrow
            scope.activeIdx = (scope.activeIdx + 1) % scope.matches.length;
            scope.$digest();
            target = popUpEl[0].querySelectorAll('.uib-typeahead-match')[scope.activeIdx];
            target.parentNode.scrollTop = target.offsetTop;
            break;
          default:
            if (shouldSelect) {
              scope.$apply(function() {
                if (angular.isNumber(scope.debounceUpdate) || angular.isObject(scope.debounceUpdate)) {
                  $$debounce(function() {
                    scope.select(scope.activeIdx, evt);
                  }, angular.isNumber(scope.debounceUpdate) ? scope.debounceUpdate : scope.debounceUpdate['default']);
                } else {
                  scope.select(scope.activeIdx, evt);
                }
              });
            }
        }
      });

      element.on('focus', function(evt) {
        hasFocus = true;
        if (minLength === 0 && !modelCtrl.$viewValue) {
          $timeout(function() {
            getMatchesAsync(modelCtrl.$viewValue, evt);
          }, 0);
        }
      });

      element.on('blur', function(evt) {
        if (isSelectOnBlur && scope.matches.length && scope.activeIdx !== -1 && !selected) {
          selected = true;
          scope.$apply(function() {
            if (angular.isObject(scope.debounceUpdate) && angular.isNumber(scope.debounceUpdate.blur)) {
              $$debounce(function() {
                scope.select(scope.activeIdx, evt);
              }, scope.debounceUpdate.blur);
            } else {
              scope.select(scope.activeIdx, evt);
            }
          });
        }
        if (!isEditable && modelCtrl.$error.editable) {
          modelCtrl.$setViewValue();
          scope.$apply(function() {
            // Reset validity as we are clearing
            modelCtrl.$setValidity('editable', true);
            modelCtrl.$setValidity('parse', true);
          });
          element.val('');
        }
        hasFocus = false;
        selected = false;
      });

      // Keep reference to click handler to unbind it.
      var dismissClickHandler = function(evt) {
        // Issue #3973
        // Firefox treats right click as a click on document
        if (element[0] !== evt.target && evt.which !== 3 && scope.matches.length !== 0) {
          resetMatches();
          if (!$rootScope.$$phase) {
            originalScope.$digest();
          }
        }
      };

      $document.on('click', dismissClickHandler);

      originalScope.$on('$destroy', function() {
        $document.off('click', dismissClickHandler);
        if (appendToBody || appendTo) {
          $popup.remove();
        }

        if (appendToBody) {
          angular.element($window).off('resize', fireRecalculating);
          $document.find('body').off('scroll', fireRecalculating);
        }
        // Prevent jQuery cache memory leak
        popUpEl.remove();

        if (showHint) {
          inputsContainer.remove();
        }
      });

      var $popup = $compile(popUpEl)(scope);

      if (appendToBody) {
        $document.find('body').append($popup);
      } else if (appendTo) {
        angular
          .element(appendTo)
          .eq(0)
          .append($popup);
      } else {
        element.after($popup);
      }

      this.init = function(_modelCtrl) {
        modelCtrl = _modelCtrl;
        ngModelOptions = extractOptions(modelCtrl);

        scope.debounceUpdate = $parse(ngModelOptions.getOption('debounce'))(originalScope);

        //plug into $parsers pipeline to open a typeahead on view changes initiated from DOM
        //$parsers kick-in on all the changes coming from the view as well as manually triggered by $setViewValue
        modelCtrl.$parsers.unshift(function(inputValue) {
          hasFocus = true;

          if (minLength === 0 || (inputValue && inputValue.length >= minLength)) {
            if (waitTime > 0) {
              cancelPreviousTimeout();
              scheduleSearchWithTimeout(inputValue);
            } else {
              getMatchesAsync(inputValue);
            }
          } else {
            isLoadingSetter(originalScope, false);
            cancelPreviousTimeout();
            resetMatches();
          }

          if (isEditable) {
            return inputValue;
          }

          if (!inputValue) {
            // Reset in case user had typed something previously.
            modelCtrl.$setValidity('editable', true);
            return null;
          }

          modelCtrl.$setValidity('editable', false);
          return undefined;
        });

        modelCtrl.$formatters.push(function(modelValue) {
          var candidateViewValue, emptyViewValue;
          var locals = {};

          // The validity may be set to false via $parsers (see above) if
          // the model is restricted to selected values. If the model
          // is set manually it is considered to be valid.
          if (!isEditable) {
            modelCtrl.$setValidity('editable', true);
          }

          if (inputFormatter) {
            locals.$model = modelValue;
            return inputFormatter(originalScope, locals);
          }

          //it might happen that we don't have enough info to properly render input value
          //we need to check for this situation and simply return model value if we can't apply custom formatting
          locals[parserResult.itemName] = modelValue;
          candidateViewValue = parserResult.viewMapper(originalScope, locals);
          locals[parserResult.itemName] = undefined;
          emptyViewValue = parserResult.viewMapper(originalScope, locals);

          return candidateViewValue !== emptyViewValue ? candidateViewValue : modelValue;
        });
      };

      function extractOptions(ngModelCtrl) {
        var ngModelOptions;

        if (angular.version.minor < 6) {
          // in angular < 1.6 $options could be missing
          // guarantee a value
          ngModelOptions = ngModelCtrl.$options || {};

          // mimic 1.6+ api
          ngModelOptions.getOption = function(key) {
            return ngModelOptions[key];
          };
        } else {
          // in angular >=1.6 $options is always present
          ngModelOptions = ngModelCtrl.$options;
        }

        return ngModelOptions;
      }
    }
  ])

  .directive('uibTypeahead', function() {
    return {
      controller: 'UibTypeaheadController',
      require: ['ngModel', 'uibTypeahead'],
      link: function(originalScope, element, attrs, ctrls) {
        ctrls[1].init(ctrls[0]);
      }
    };
  })

  .directive('uibTypeaheadPopup', [
    '$$debounce',
    function($$debounce) {
      return {
        scope: {
          matches: '=',
          query: '=',
          active: '=',
          position: '&',
          moveInProgress: '=',
          select: '&',
          assignIsOpen: '&',
          debounce: '&'
        },
        replace: true,
        templateUrl: function(element, attrs) {
          return attrs.popupTemplateUrl || 'components/typeahead/template/typeahead-popup.html';
        },
        link: function(scope, element, attrs) {
          scope.templateUrl = attrs.templateUrl;

          scope.isOpen = function() {
            var isDropdownOpen = scope.matches.length > 0;
            scope.assignIsOpen({ isOpen: isDropdownOpen });
            return isDropdownOpen;
          };

          scope.isActive = function(matchIdx) {
            return scope.active === matchIdx;
          };

          scope.selectActive = function(matchIdx) {
            scope.active = matchIdx;
          };

          scope.selectMatch = function(activeIdx, evt) {
            var debounce = scope.debounce();
            if (angular.isNumber(debounce) || angular.isObject(debounce)) {
              $$debounce(function() {
                scope.select({ activeIdx: activeIdx, evt: evt });
              }, angular.isNumber(debounce) ? debounce : debounce['default']);
            } else {
              scope.select({ activeIdx: activeIdx, evt: evt });
            }
          };
        }
      };
    }
  ])

  .directive('uibTypeaheadMatch', [
    '$templateRequest',
    '$compile',
    '$parse',
    function($templateRequest, $compile, $parse) {
      return {
        scope: {
          index: '=',
          match: '=',
          query: '='
        },
        link: function(scope, element, attrs) {
          var tplUrl = $parse(attrs.templateUrl)(scope.$parent) || 'components/typeahead/template/typeahead-match.html';
          $templateRequest(tplUrl).then(function(tplContent) {
            var tplEl = angular.element(tplContent.trim());
            element.replaceWith(tplEl);
            $compile(tplEl)(scope);
          });
        }
      };
    }
  ])

  .filter('uibTypeaheadHighlight', [
    '$sce',
    '$injector',
    '$log',
    function($sce, $injector, $log) {
      var isSanitizePresent;
      isSanitizePresent = $injector.has('$sanitize');

      function escapeRegexp(queryToEscape) {
        // Regex: capture the whole query string and replace it with the string that will be used to match
        // the results, for example if the capture is "a" the result will be \a
        return queryToEscape.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
      }

      function containsHtml(matchItem) {
        return /<.*>/g.test(matchItem);
      }

      return function(matchItem, query) {
        if (!isSanitizePresent && containsHtml(matchItem)) {
          $log.warn('Unsafe use of typeahead please use ngSanitize'); // Warn the user about the danger
        }
        matchItem = query
          ? ('' + matchItem).replace(new RegExp(escapeRegexp(query), 'gi'), '<strong>$&</strong>')
          : matchItem; // Replaces the capture string with a the same string inside of a "strong" tag
        if (!isSanitizePresent) {
          matchItem = $sce.trustAsHtml(matchItem); // If $sanitize is not present we pack the string in a $sce object for the ng-bind-html directive
        }
        return matchItem;
      };
    }
  ]);

})();
(function () {
'use strict';

// @desc
//   Wizard steps
//
// @example
//   <hy-wizard wizard-submit="save()">
//      <hy-wizard-step></hy-wizard-step>
//   </hy-wizard>

angular.module('wizard.step', []).directive('hyWizardStep', wizardStep);

function wizardStep() {
  var directive = {
    require: '^hyWizard', // We need this directive to be inside a wizard
    transclude: true, // It transcludes the contents of the directive into the template
    restrict: 'E',
    scope: {
      heading: '@', // Interpolate the heading attribute onto this scope
      headingStepNumber: '@',
      headingShort: '@', // To fit in the timeline
      name: '@', // Used in query parameter ?step=
      isDisabled: '=?',
      isValid: '=', // Validation function that return true to move to next step
      alertHeader: '@',
      alertText: '=',
      alternativeWizard: '='
    },
    templateUrl: 'components/wizard/wizard-step.html',
    link: link
  };

  return directive;

  ////////////
  function link(scope, element, attrs, wizardCtrl) {
    wizardCtrl.addStep(scope);

    scope.hideNextButton = attrs.hasOwnProperty('hideNextButton');
    scope.$watch('isOpen', function(value) {
      element.toggleClass(scope.openClass, !!value);
      if (value) {
        wizardCtrl.closeOthers(scope);
      }
    });

    scope.$watch('isDisabled', function(value) {
      wizardCtrl.setEnabledSteps();
    });

    scope.toggleOpen = function($event) {
      if (!scope.isDisabled) {
        if (!$event || $event.which === 32) {
          scope.isOpen = !scope.isOpen;
        }
      }
    };

    // ARIA
    scope.wizardId = 'wizard-step' + scope.$id + '-' + Math.floor(Math.random() * 10000);
  }
}

})();
(function () {
'use strict';

// @desc
//   Wizard to handle a big task in smaller steps
//
// @example
//   <hy-wizard>
//      <hy-step></hy-step>
//   </hy-wizard>

angular.module('wizard', ['wizard.ctrl', 'wizard.step']).directive('hyWizard', wizard);

function wizard() {
  var directive = {
    controller: 'WizardCtrl',
    controllerAs: 'wizardVm',
    transclude: true,
    scope: {
      wzSubmit: '&',
      submitText: '@?',
      submitDisabled: '=',
      gotoNext: '=',
      beforeGotoNext: '&?',
      isCurrentStepValid: '=',
      headerText: '@',
      alternativeWizard: '='
    },
    restrict: 'E',
    templateUrl: 'components/wizard/wizard.html'
  };
  return directive;
}

})();
(function () {
'use strict';

/* jshint -W040 */

WizardCtrl.$inject = ["$scope", "$attrs", "$location", "$timeout"];
angular.module('wizard.ctrl', []).controller('WizardCtrl', WizardCtrl);

function WizardCtrl($scope, $attrs, $location, $timeout) {
  var wizardVm = this;
  var firstEnabledOpened;

  wizardVm.hideHeader = 'hideHeader' in $attrs;
  wizardVm.alternativeWizard = $attrs.alternativeWizard;
  // This array keeps track of the wizard steps
  wizardVm.steps = [];
  wizardVm.currentStepNumber = 1;
  wizardVm.closeOthers = closeOthers;
  wizardVm.addStep = addStep;
  wizardVm.removeStep = removeStep;
  wizardVm.next = next;
  wizardVm.previous = previous;
  wizardVm.isFirstStep = isFirstStep;
  wizardVm.isLastStep = isLastStep;
  wizardVm.isCurrentStepValid = isCurrentStepValid;
  wizardVm.enabledSteps = [];
  wizardVm.setEnabledSteps = setEnabledSteps;
  wizardVm.hideNextButton = hideNextButton;

  $scope.$on('$locationChangeSuccess', function() {
    if ($location.search().step !== undefined) {
      gotoStepByName($location.search().step);
    }
  });

  $scope.$watch('gotoNext', function(value) {
    if (value) {
      //wrapped inside $timeout so newly enabled steps are included.
      $timeout(function() {
        next();
      });
      $scope.gotoNext = false;
    }
  });

  $scope.isCurrentStepValid = isCurrentStepValid;
  ////////////

  function setCurrentStepNumber(step) {
    wizardVm.currentStepNumber = step.headingStepNumber;
  }

  function isCurrentStepValid() {
    var foundStep;
    angular.forEach(wizardVm.steps, function(step) {
      if (step.isOpen) {
        foundStep = step;
      }
    });

    if (foundStep) {
      // If no validation function is provided, allow the user to enter the step
      if (foundStep.isValid === undefined) {
        return false;
      }
      // If validation is a boolean value instead of a function, return the value
      if (typeof foundStep.isValid === 'boolean') {
        return foundStep.isValid;
      }

      return foundStep.isValid();
    }

    return false;
  }

  // Open next step that is not disabled
  function next() {
    if ($scope.beforeGotoNext) {
      // eslint-disable-next-line angular/controller-as
      $scope.beforeGotoNext({ cb: () => { nextnext() }});
    } else {
      nextnext();
    }
  }

  function nextnext() {
    var nextStep;
    angular.forEach(wizardVm.enabledSteps, function(step, index) {
      if (step.isOpen && index < wizardVm.enabledSteps.length - 1) {
        nextStep = wizardVm.enabledSteps[index + 1];
      }
    });
    setCurrentStepNumber(nextStep);
    $location.search(angular.extend($location.search(), { step: nextStep.name }));
  }

  // Open previous step that is not disabled
  function previous() {
    var previousStep;

    angular.forEach(wizardVm.enabledSteps, function(step, index) {
      if (step.isOpen && index > 0) {
        previousStep = wizardVm.enabledSteps[index - 1];
      }
    });
    setCurrentStepNumber(previousStep);
    $location.search(angular.extend($location.search(), { step: previousStep.name }));
  }

  function gotoStepByName(name) {
    var foundStep;
    var foundAtIndex;
    angular.forEach(wizardVm.enabledSteps, function(step, index) {
      if (step.name === name) {
        foundStep = step;
        foundAtIndex = index;
      }
    });
    // Ensure all prior steps are valid if using browser buttons
    var foundInvalidStep;
    angular.forEach(wizardVm.enabledSteps, function(step, index) {
      if (!isStepValid(step) && index < foundAtIndex) {
        foundInvalidStep = true;
        foundStep = step;
      }
    });

    if (foundInvalidStep) {
      $location.search(angular.extend($location.search(), { step: foundStep.name })); // goto invalid step instead
      $location.replace();
    } else {
      foundStep.isOpen = true;
    }
    setCurrentStepNumber(foundStep);
  }

  function isStepValid(step) {
    // If no validation function is provided, allow the user to enter the step
    if (step.isValid === undefined) {
      return true;
    }
    // If validation is a boolean value instead of a function, return the value
    if (typeof step.isValid === 'boolean') {
      return step.isValid;
    }

    return step.isValid();
  }

  // Ensure that all the steps in this wizard are closed
  function closeOthers(openStep) {
    angular.forEach(this.steps, function(step) {
      if (step !== openStep) {
        step.isOpen = false;
      }
    });
  }

  // This is called from the wizard-step directive to add itself to the wizard
  function addStep(stepScope) {
    var that = this;
    this.steps.push(stepScope);

    // Open first step not being disabled
    if (!firstEnabledOpened && stepScope.isDisabled !== true) {
      $location.search(angular.extend($location.search(), { step: stepScope.name }));
      $location.replace();
      stepScope.isOpen = true; // TODO remove
      firstEnabledOpened = true;
    }

    setEnabledSteps();

    stepScope.$on('$destroy', function(event) {
      that.removeStep(stepScope);
    });
  }

  // This is called from the wizard-step directive when to remove itself
  function removeStep(step) {
    var index = this.steps.indexOf(step);
    if (index !== -1) {
      this.steps.splice(index, 1);
    }
  }

  function isFirstStep() {
    return wizardVm.enabledSteps.length > 0 && wizardVm.enabledSteps[0].isOpen === true;
  }

  function isLastStep() {
    return wizardVm.enabledSteps.length > 0 && wizardVm.enabledSteps[wizardVm.enabledSteps.length - 1].isOpen === true;
  }

  // It's in many cases not to care about disabled steps so filter them out
  // can make tasks simpler
  function setEnabledSteps() {
    wizardVm.enabledSteps = wizardVm.steps.filter(function(step) {
      return !step.isDisabled;
    });
  }

  function hideNextButton() {
    var found;

    angular.forEach(wizardVm.steps, function(step) {
      if (step.isOpen && step.hideNextButton) {
        found = true;
      }
    });

    return found;
  }
}

/* jshint +W040 */

})();
(function () {
'use strict';

DomainModals.$inject = ["HyUtils"];
angular
  .module('domainModals', [])
  .factory('DomainModals', DomainModals);

function DomainModals(HyUtils) {
  return {
    apartmentHistory: apartment => HyUtils.showDefaultModal('ApartmentHistoryCtrl',
      'resources/real-estate/apartment-history.html', { apartment })
  }
}
})();
(function () {
'use strict';

/* jshint -W074 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

angular
  .module('uiModal', ['uiMultiMap', 'uiStackedMap', 'uiPosition'])
  /**
   * Pluggable resolve mechanism for the modal resolve resolution
   * Supports UI Router's $resolve service
   */
  .provider('$uibResolve', function() {
    var resolve = this;
    this.resolver = null;

    this.setResolver = function(resolver) {
      this.resolver = resolver;
    };

    this.$get = [
      '$injector',
      '$q',
      function($injector, $q) {
        var resolver = resolve.resolver ? $injector.get(resolve.resolver) : null;
        return {
          resolve: function(invocables, locals, parent, self) {
            if (resolver) {
              return resolver.resolve(invocables, locals, parent, self);
            }

            var promises = [];

            angular.forEach(invocables, function(value) {
              if (angular.isFunction(value) || angular.isArray(value)) {
                promises.push($q.resolve($injector.invoke(value)));
              } else if (angular.isString(value)) {
                promises.push($q.resolve($injector.get(value)));
              } else {
                promises.push($q.resolve(value));
              }
            });

            return $q.all(promises).then(function(resolves) {
              var resolveObj = {};
              var resolveIter = 0;
              angular.forEach(invocables, function(value, key) {
                resolveObj[key] = resolves[resolveIter++];
              });

              return resolveObj;
            });
          }
        };
      }
    ];
  })

  /**
   * A helper directive for the $modal service. It creates a backdrop element.
   */
  .directive('uibModalBackdrop', [
    '$animate',
    '$injector',
    '$uibModalStack',
    function($animate, $injector, $modalStack) {
      return {
        restrict: 'A',
        compile: function(tElement, tAttrs) {
          tElement.addClass(tAttrs.backdropClass);
          return linkFn;
        }
      };

      function linkFn(scope, element, attrs) {
        if (attrs.modalInClass) {
          $animate.addClass(element, attrs.modalInClass);

          scope.$on($modalStack.NOW_CLOSING_EVENT, function(e, setIsAsync) {
            var done = setIsAsync();
            if (scope.modalOptions.animation) {
              $animate.removeClass(element, attrs.modalInClass).then(done);
            } else {
              done();
            }
          });
        }
      }
    }
  ])

  .directive('uibModalWindow', [
    '$uibModalStack',
    '$q',
    '$animateCss',
    '$document',
    function($modalStack, $q, $animateCss, $document) {
      return {
        scope: {
          index: '@'
        },
        restrict: 'A',
        transclude: true,
        templateUrl: function(tElement, tAttrs) {
          return tAttrs.templateUrl || 'components/modal/window.html';
        },
        link: function(scope, element, attrs) {
          element.addClass(attrs.windowTopClass || '');
          scope.size = attrs.size;

          scope.close = function(evt) {
            var modal = $modalStack.getTop();
            if (
              modal &&
              modal.value.backdrop &&
              modal.value.backdrop !== 'static' &&
              evt.target === evt.currentTarget
            ) {
              evt.preventDefault();
              evt.stopPropagation();
              $modalStack.dismiss(modal.key, 'backdrop click');
            }
          };

          // moved from template to fix issue #2280
          element.on('click', scope.close);

          // This property is only added to the scope for the purpose of detecting when this directive is rendered.
          // We can detect that by using this property in the template associated with this directive and then use
          // {@link Attribute#$observe} on it. For more details please see {@link TableColumnResize}.
          scope.$isRendered = true;

          // Deferred object that will be resolved when this modal is rendered.
          var modalRenderDeferObj = $q.defer();
          // Resolve render promise post-digest
          scope.$$postDigest(function() {
            modalRenderDeferObj.resolve();
          });

          modalRenderDeferObj.promise.then(function() {
            var animationPromise = null;

            if (attrs.modalInClass) {
              animationPromise = $animateCss(element, {
                addClass: attrs.modalInClass
              }).start();

              scope.$on($modalStack.NOW_CLOSING_EVENT, function(e, setIsAsync) {
                var done = setIsAsync();
                $animateCss(element, {
                  removeClass: attrs.modalInClass
                })
                  .start()
                  .then(done);
              });
            }

            $q.when(animationPromise).then(function() {
              // Notify {@link $modalStack} that modal is rendered.
              var modal = $modalStack.getTop();
              if (modal) {
                $modalStack.modalRendered(modal.key);
              }

              /**
               * If something within the freshly-opened modal already has focus (perhaps via a
               * directive that causes focus) then there's no need to try to focus anything.
               */
              if (!($document[0].activeElement && element[0].contains($document[0].activeElement))) {
                var inputWithAutofocus = element[0].querySelector('[autofocus]');
                /**
                 * Auto-focusing of a freshly-opened modal element causes any child elements
                 * with the autofocus attribute to lose focus. This is an issue on touch
                 * based devices which will show and then hide the onscreen keyboard.
                 * Attempts to refocus the autofocus element via JavaScript will not reopen
                 * the onscreen keyboard. Fixed by updated the focusing logic to only autofocus
                 * the modal element if the modal does not contain an autofocus element.
                 */
                if (inputWithAutofocus) {
                  inputWithAutofocus.focus();
                } else {
                  element[0].focus();
                }
              }
            });
          });
        }
      };
    }
  ])

  .directive('uibModalAnimationClass', function() {
    return {
      compile: function(tElement, tAttrs) {
        if (tAttrs.modalAnimation) {
          tElement.addClass(tAttrs.uibModalAnimationClass);
        }
      }
    };
  })

  .directive('uibModalTransclude', [
    '$animate',
    function($animate) {
      return {
        link: function(scope, element, attrs, controller, transclude) {
          transclude(scope.$parent, function(clone) {
            element.empty();
            $animate.enter(clone, element);
          });
        }
      };
    }
  ])

  .factory('$uibModalStack', [
    '$animate',
    '$animateCss',
    '$document',
    '$compile',
    '$rootScope',
    '$q',
    '$$multiMap',
    '$$stackedMap',
    '$uibPosition',
    function($animate, $animateCss, $document, $compile, $rootScope, $q, $$multiMap, $$stackedMap, $uibPosition) {
      var OPENED_MODAL_CLASS = 'modal-open';

      var backdropDomEl, backdropScope;
      var openedWindows = $$stackedMap.createNew();
      var openedClasses = $$multiMap.createNew();
      var $modalStack = {
        NOW_CLOSING_EVENT: 'modal.stack.now-closing'
      };
      var topModalIndex = 0;
      var previousTopOpenedModal = null;
      var ARIA_HIDDEN_ATTRIBUTE_NAME = 'data-bootstrap-modal-aria-hidden-count';

      //Modal focus behavior
      var tabbableSelector =
        "a[href], area[href], input:not([disabled]):not([tabindex='-1']), " +
        "button:not([disabled]):not([tabindex='-1']),select:not([disabled]):not([tabindex='-1']), textarea:not([disabled]):not([tabindex='-1']), " +
        "iframe, object, embed, *[tabindex]:not([tabindex='-1']), *[contenteditable=true]";
      var scrollbarPadding;
      var SNAKE_CASE_REGEXP = /[A-Z]/g;

      // TODO: extract into common dependency with tooltip
      function snake_case(name) {
        var separator = '-';
        return name.replace(SNAKE_CASE_REGEXP, function(letter, pos) {
          return (pos ? separator : '') + letter.toLowerCase();
        });
      }

      function isVisible(element) {
        return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
      }

      function backdropIndex() {
        var topBackdropIndex = -1;
        var opened = openedWindows.keys();
        for (var i = 0; i < opened.length; i++) {
          if (openedWindows.get(opened[i]).value.backdrop) {
            topBackdropIndex = i;
          }
        }

        // If any backdrop exist, ensure that it's index is always
        // right below the top modal
        if (topBackdropIndex > -1 && topBackdropIndex < topModalIndex) {
          topBackdropIndex = topModalIndex;
        }
        return topBackdropIndex;
      }

      $rootScope.$watch(backdropIndex, function(newBackdropIndex) {
        if (backdropScope) {
          backdropScope.index = newBackdropIndex;
        }
      });

      function removeModalWindow(modalInstance, elementToReceiveFocus) {
        var modalWindow = openedWindows.get(modalInstance).value;
        var appendToElement = modalWindow.appendTo;

        //clean up the stack
        openedWindows.remove(modalInstance);
        previousTopOpenedModal = openedWindows.top();
        if (previousTopOpenedModal) {
          topModalIndex = parseInt(previousTopOpenedModal.value.modalDomEl.attr('index'), 10);
        }

        removeAfterAnimate(
          modalWindow.modalDomEl,
          modalWindow.modalScope,
          function() {
            var modalBodyClass = modalWindow.openedClass || OPENED_MODAL_CLASS;
            openedClasses.remove(modalBodyClass, modalInstance);
            var areAnyOpen = openedClasses.hasKey(modalBodyClass);
            appendToElement.toggleClass(modalBodyClass, areAnyOpen);
            if (!areAnyOpen && scrollbarPadding && scrollbarPadding.heightOverflow && scrollbarPadding.scrollbarWidth) {
              if (scrollbarPadding.originalRight) {
                appendToElement.css({ paddingRight: scrollbarPadding.originalRight + 'px' });
              } else {
                appendToElement.css({ paddingRight: '' });
              }
              scrollbarPadding = null;
            }
            toggleTopWindowClass(true);
          },
          modalWindow.closedDeferred
        );
        checkRemoveBackdrop();

        //move focus to specified element if available, or else to body
        if (elementToReceiveFocus && elementToReceiveFocus.focus) {
          elementToReceiveFocus.focus();
        } else if (appendToElement.focus) {
          appendToElement.focus();
        }
      }

      // Add or remove "windowTopClass" from the top window in the stack
      function toggleTopWindowClass(toggleSwitch) {
        var modalWindow;

        if (openedWindows.length() > 0) {
          modalWindow = openedWindows.top().value;
          modalWindow.modalDomEl.toggleClass(modalWindow.windowTopClass || '', toggleSwitch);
        }
      }

      function checkRemoveBackdrop() {
        //remove backdrop if no longer needed
        if (backdropDomEl && backdropIndex() === -1) {
          var backdropScopeRef = backdropScope;
          removeAfterAnimate(backdropDomEl, backdropScope, function() {
            backdropScopeRef = null;
          });
          backdropDomEl = undefined;
          backdropScope = undefined;
        }
      }

      function removeAfterAnimate(domEl, scope, done, closedDeferred) {
        var asyncDeferred;
        var asyncPromise = null;
        var setIsAsync = function() {
          if (!asyncDeferred) {
            asyncDeferred = $q.defer();
            asyncPromise = asyncDeferred.promise;
          }

          return function asyncDone() {
            asyncDeferred.resolve();
          };
        };
        scope.$broadcast($modalStack.NOW_CLOSING_EVENT, setIsAsync);

        // Note that it's intentional that asyncPromise might be null.
        // That's when setIsAsync has not been called during the
        // NOW_CLOSING_EVENT broadcast.
        return $q.when(asyncPromise).then(afterAnimating);

        function afterAnimating() {
          if (afterAnimating.done) {
            return;
          }
          afterAnimating.done = true;

          $animate.leave(domEl).then(function() {
            if (done) {
              done();
            }

            domEl.remove();
            if (closedDeferred) {
              closedDeferred.resolve();
            }
          });

          scope.$destroy();
        }
      }

      $document.on('keydown', keydownListener);

      $rootScope.$on('$destroy', function() {
        $document.off('keydown', keydownListener);
      });

      function keydownListener(evt) {
        if (evt.isDefaultPrevented()) {
          return evt;
        }

        var modal = openedWindows.top();
        if (modal) {
          switch (evt.which) {
            case 27: {
              if (modal.value.keyboard) {
                evt.preventDefault();
                $rootScope.$apply(function() {
                  $modalStack.dismiss(modal.key, 'escape key press');
                });
              }
              break;
            }
            case 9: {
              var list = $modalStack.loadFocusElementList(modal);
              var focusChanged = false;
              if (evt.shiftKey) {
                if ($modalStack.isFocusInFirstItem(evt, list) || $modalStack.isModalFocused(evt, modal)) {
                  focusChanged = $modalStack.focusLastFocusableElement(list);
                }
              } else {
                if ($modalStack.isFocusInLastItem(evt, list)) {
                  focusChanged = $modalStack.focusFirstFocusableElement(list);
                }
              }

              if (focusChanged) {
                evt.preventDefault();
                evt.stopPropagation();
              }

              break;
            }
          }
        }
      }

      $modalStack.open = function(modalInstance, modal) {
        var modalOpener = $document[0].activeElement,
          modalBodyClass = modal.openedClass || OPENED_MODAL_CLASS;

        toggleTopWindowClass(false);

        // Store the current top first, to determine what index we ought to use
        // for the current top modal
        previousTopOpenedModal = openedWindows.top();

        openedWindows.add(modalInstance, {
          deferred: modal.deferred,
          renderDeferred: modal.renderDeferred,
          closedDeferred: modal.closedDeferred,
          modalScope: modal.scope,
          backdrop: modal.backdrop,
          keyboard: modal.keyboard,
          openedClass: modal.openedClass,
          windowTopClass: modal.windowTopClass,
          animation: modal.animation,
          appendTo: modal.appendTo
        });

        openedClasses.put(modalBodyClass, modalInstance);

        var appendToElement = modal.appendTo,
          currBackdropIndex = backdropIndex();

        if (currBackdropIndex >= 0 && !backdropDomEl) {
          backdropScope = $rootScope.$new(true);
          backdropScope.modalOptions = modal;
          backdropScope.index = currBackdropIndex;
          backdropDomEl = angular.element('<div uib-modal-backdrop="modal-backdrop"></div>');
          backdropDomEl.attr({
            class: 'modal-backdrop',
            'ng-style': "{'z-index': 1073 + (index && 1 || 0) + index*10}",
            'uib-modal-animation-class': 'fade',
            'modal-in-class': 'in'
          });
          if (modal.backdropClass) {
            backdropDomEl.addClass(modal.backdropClass);
          }

          if (modal.animation) {
            backdropDomEl.attr('modal-animation', 'true');
          }
          $compile(backdropDomEl)(backdropScope);
          $animate.enter(backdropDomEl, appendToElement);
          if ($uibPosition.isScrollable(appendToElement)) {
            scrollbarPadding = $uibPosition.scrollbarPadding(appendToElement);
            if (scrollbarPadding.heightOverflow && scrollbarPadding.scrollbarWidth) {
              appendToElement.css({ paddingRight: scrollbarPadding.right + 'px' });
            }
          }
        }

        var content;
        if (modal.component) {
          content = document.createElement(snake_case(modal.component.name));
          content = angular.element(content);
          content.attr({
            resolve: '$resolve',
            'modal-instance': '$uibModalInstance',
            close: '$close($value)',
            dismiss: '$dismiss($value)'
          });
        } else {
          content = modal.content;
        }

        // Set the top modal index based on the index of the previous top modal
        topModalIndex = previousTopOpenedModal
          ? parseInt(previousTopOpenedModal.value.modalDomEl.attr('index'), 10) + 1
          : 0;
        var angularDomEl = angular.element('<div uib-modal-window="modal-window"></div>');
        angularDomEl
          .attr({
            class: 'modal',
            'template-url': modal.windowTemplateUrl,
            'window-top-class': modal.windowTopClass,
            role: 'dialog',
            'aria-labelledby': modal.ariaLabelledBy,
            'aria-describedby': modal.ariaDescribedBy,
            size: modal.size,
            index: topModalIndex,
            animate: 'animate',
            'ng-style': "{'z-index': 1075 + $$topModalIndex*10, display: 'block'}",
            tabindex: -1,
            'uib-modal-animation-class': 'fade',
            'modal-in-class': 'in'
          })
          .append(content);
        if (modal.windowClass) {
          angularDomEl.addClass(modal.windowClass);
        }

        if (modal.animation) {
          angularDomEl.attr('modal-animation', 'true');
        }

        appendToElement.addClass(modalBodyClass);
        if (modal.scope) {
          // we need to explicitly add the modal index to the modal scope
          // because it is needed by ngStyle to compute the zIndex property.
          modal.scope.$$topModalIndex = topModalIndex;
        }
        $animate.enter($compile(angularDomEl)(modal.scope), appendToElement);

        openedWindows.top().value.modalDomEl = angularDomEl;
        openedWindows.top().value.modalOpener = modalOpener;

        applyAriaHidden(angularDomEl);

        function applyAriaHidden(el) {
          if (!el || el[0].tagName === 'BODY') {
            return;
          }

          getSiblings(el).forEach(function(sibling) {
            var elemIsAlreadyHidden = sibling.getAttribute('aria-hidden') === 'true',
              ariaHiddenCount = parseInt(sibling.getAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME), 10);

            if (!ariaHiddenCount) {
              ariaHiddenCount = elemIsAlreadyHidden ? 1 : 0;
            }

            sibling.setAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME, ariaHiddenCount + 1);
            sibling.setAttribute('aria-hidden', 'true');
          });

          return applyAriaHidden(el.parent());

          function getSiblings(el) {
            var children = el.parent() ? el.parent().children() : [];

            return Array.prototype.filter.call(children, function(child) {
              return child !== el[0];
            });
          }
        }
      };

      function broadcastClosing(modalWindow, resultOrReason, closing) {
        return !modalWindow.value.modalScope.$broadcast('modal.closing', resultOrReason, closing).defaultPrevented;
      }

      function unhideBackgroundElements() {
        Array.prototype.forEach.call(document.querySelectorAll('[' + ARIA_HIDDEN_ATTRIBUTE_NAME + ']'), function(
          hiddenEl
        ) {
          var ariaHiddenCount = parseInt(hiddenEl.getAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME), 10),
            newHiddenCount = ariaHiddenCount - 1;
          hiddenEl.setAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME, newHiddenCount);

          if (!newHiddenCount) {
            hiddenEl.removeAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME);
            hiddenEl.removeAttribute('aria-hidden');
          }
        });
      }

      $modalStack.close = function(modalInstance, result) {
        var modalWindow = openedWindows.get(modalInstance);
        unhideBackgroundElements();
        if (modalWindow && broadcastClosing(modalWindow, result, true)) {
          modalWindow.value.modalScope.$$uibDestructionScheduled = true;
          modalWindow.value.deferred.resolve(result);
          removeModalWindow(modalInstance, modalWindow.value.modalOpener);
          return true;
        }

        return !modalWindow;
      };

      $modalStack.dismiss = function(modalInstance, reason) {
        var modalWindow = openedWindows.get(modalInstance);
        unhideBackgroundElements();
        if (modalWindow && broadcastClosing(modalWindow, reason, false)) {
          modalWindow.value.modalScope.$$uibDestructionScheduled = true;
          modalWindow.value.deferred.reject(reason);
          removeModalWindow(modalInstance, modalWindow.value.modalOpener);
          return true;
        }
        return !modalWindow;
      };

      $modalStack.dismissAll = function(reason) {
        var topModal = this.getTop();
        while (topModal && this.dismiss(topModal.key, reason)) {
          topModal = this.getTop();
        }
      };

      $modalStack.getTop = function() {
        return openedWindows.top();
      };

      $modalStack.modalRendered = function(modalInstance) {
        var modalWindow = openedWindows.get(modalInstance);
        if (modalWindow) {
          modalWindow.value.renderDeferred.resolve();
        }
      };

      $modalStack.focusFirstFocusableElement = function(list) {
        if (list.length > 0) {
          list[0].focus();
          return true;
        }
        return false;
      };

      $modalStack.focusLastFocusableElement = function(list) {
        if (list.length > 0) {
          list[list.length - 1].focus();
          return true;
        }
        return false;
      };

      $modalStack.isModalFocused = function(evt, modalWindow) {
        if (evt && modalWindow) {
          var modalDomEl = modalWindow.value.modalDomEl;
          if (modalDomEl && modalDomEl.length) {
            return (evt.target || evt.srcElement) === modalDomEl[0];
          }
        }
        return false;
      };

      $modalStack.isFocusInFirstItem = function(evt, list) {
        if (list.length > 0) {
          return (evt.target || evt.srcElement) === list[0];
        }
        return false;
      };

      $modalStack.isFocusInLastItem = function(evt, list) {
        if (list.length > 0) {
          return (evt.target || evt.srcElement) === list[list.length - 1];
        }
        return false;
      };

      $modalStack.loadFocusElementList = function(modalWindow) {
        if (modalWindow) {
          var modalDomE1 = modalWindow.value.modalDomEl;
          if (modalDomE1 && modalDomE1.length) {
            var elements = modalDomE1[0].querySelectorAll(tabbableSelector);
            return elements
              ? Array.prototype.filter.call(elements, function(element) {
                  return isVisible(element);
                })
              : elements;
          }
        }
      };

      return $modalStack;
    }
  ])

  .provider('$uibModal', function() {
    var $modalProvider = {
      options: {
        animation: true,
        backdrop: true, //can also be false or 'static'
        keyboard: true
      },
      $get: [
        '$rootScope',
        '$q',
        '$document',
        '$templateRequest',
        '$controller',
        '$uibResolve',
        '$uibModalStack',
        function($rootScope, $q, $document, $templateRequest, $controller, $uibResolve, $modalStack) {
          var $modal = {};

          function getTemplatePromise(options) {
            return options.template
              ? $q.when(options.template)
              : $templateRequest(angular.isFunction(options.templateUrl) ? options.templateUrl() : options.templateUrl);
          }

          var promiseChain = null;
          $modal.getPromiseChain = function() {
            return promiseChain;
          };

          $modal.open = function(modalOptions) {
            var modalResultDeferred = $q.defer();
            var modalOpenedDeferred = $q.defer();
            var modalClosedDeferred = $q.defer();
            var modalRenderDeferred = $q.defer();

            //prepare an instance of a modal to be injected into controllers and returned to a caller
            var modalInstance = {
              result: modalResultDeferred.promise,
              opened: modalOpenedDeferred.promise,
              closed: modalClosedDeferred.promise,
              rendered: modalRenderDeferred.promise,
              close: function(result) {
                return $modalStack.close(modalInstance, result);
              },
              dismiss: function(reason) {
                return $modalStack.dismiss(modalInstance, reason);
              }
            };

            //merge and clean up options
            modalOptions = angular.extend({}, $modalProvider.options, modalOptions);
            modalOptions.resolve = modalOptions.resolve || {};
            modalOptions.appendTo = modalOptions.appendTo || $document.find('body').eq(0);

            if (!modalOptions.appendTo.length) {
              throw new Error('appendTo element not found. Make sure that the element passed is in DOM.');
            }

            //verify options
            if (!modalOptions.component && !modalOptions.template && !modalOptions.templateUrl) {
              throw new Error('One of component or template or templateUrl options is required.');
            }

            var templateAndResolvePromise;
            if (modalOptions.component) {
              templateAndResolvePromise = $q.when($uibResolve.resolve(modalOptions.resolve, {}, null, null));
            } else {
              templateAndResolvePromise = $q.all([
                getTemplatePromise(modalOptions),
                $uibResolve.resolve(modalOptions.resolve, {}, null, null)
              ]);
            }

            function resolveWithTemplate() {
              return templateAndResolvePromise;
            }

            // Wait for the resolution of the existing promise chain.
            // Then switch to our own combined promise dependency (regardless of how the previous modal fared).
            // Then add to $modalStack and resolve opened.
            // Finally clean up the chain variable if no subsequent modal has overwritten it.
            var samePromise;
            samePromise = promiseChain = $q
              .all([promiseChain])
              .then(resolveWithTemplate, resolveWithTemplate)
              .then(
                function resolveSuccess(tplAndVars) {
                  var providedScope = modalOptions.scope || $rootScope;

                  var modalScope = providedScope.$new();
                  modalScope.$close = modalInstance.close;
                  modalScope.$dismiss = modalInstance.dismiss;

                  modalScope.$on('$destroy', function() {
                    if (!modalScope.$$uibDestructionScheduled) {
                      modalScope.$dismiss('$uibUnscheduledDestruction');
                    }
                  });

                  var modal = {
                    scope: modalScope,
                    deferred: modalResultDeferred,
                    renderDeferred: modalRenderDeferred,
                    closedDeferred: modalClosedDeferred,
                    animation: modalOptions.animation,
                    backdrop: modalOptions.backdrop,
                    keyboard: modalOptions.keyboard,
                    backdropClass: modalOptions.backdropClass,
                    windowTopClass: modalOptions.windowTopClass,
                    windowClass: modalOptions.windowClass,
                    windowTemplateUrl: modalOptions.windowTemplateUrl,
                    ariaLabelledBy: modalOptions.ariaLabelledBy,
                    ariaDescribedBy: modalOptions.ariaDescribedBy,
                    size: modalOptions.size,
                    openedClass: modalOptions.openedClass,
                    appendTo: modalOptions.appendTo
                  };

                  var component = {};
                  var ctrlInstance,
                    ctrlInstantiate,
                    ctrlLocals = {};

                  if (modalOptions.component) {
                    constructLocals(component, false, true, false);
                    component.name = modalOptions.component;
                    modal.component = component;
                  } else if (modalOptions.controller) {
                    constructLocals(ctrlLocals, true, false, true);

                    // the third param will make the controller instantiate later,private api
                    // @see https://github.com/angular/angular.js/blob/master/src/ng/controller.js#L126
                    ctrlInstantiate = $controller(modalOptions.controller, ctrlLocals, true, modalOptions.controllerAs);
                    if (modalOptions.controllerAs && modalOptions.bindToController) {
                      ctrlInstance = ctrlInstantiate.instance;
                      ctrlInstance.$close = modalScope.$close;
                      ctrlInstance.$dismiss = modalScope.$dismiss;
                      angular.extend(
                        ctrlInstance,
                        {
                          $resolve: ctrlLocals.$scope.$resolve
                        },
                        providedScope
                      );
                    }

                    ctrlInstance = ctrlInstantiate();

                    if (angular.isFunction(ctrlInstance.$onInit)) {
                      ctrlInstance.$onInit();
                    }
                  }

                  if (!modalOptions.component) {
                    modal.content = tplAndVars[0];
                  }

                  $modalStack.open(modalInstance, modal);
                  modalOpenedDeferred.resolve(true);

                  function constructLocals(obj, template, instanceOnScope, injectable) {
                    obj.$scope = modalScope;
                    obj.$scope.$resolve = {};
                    if (instanceOnScope) {
                      obj.$scope.$uibModalInstance = modalInstance;
                    } else {
                      obj.$uibModalInstance = modalInstance;
                    }

                    var resolves = template ? tplAndVars[1] : tplAndVars;
                    angular.forEach(resolves, function(value, key) {
                      if (injectable) {
                        obj[key] = value;
                      }

                      obj.$scope.$resolve[key] = value;
                    });
                  }
                },
                function resolveError(reason) {
                  modalOpenedDeferred.reject(reason);
                  modalResultDeferred.reject(reason);
                }
              )
              ['finally'](function() {
                if (promiseChain === samePromise) {
                  promiseChain = null;
                }
              });

            return modalInstance;
          };

          return $modal;
        }
      ]
    };

    return $modalProvider;
  });

/* jshint -W074 */
/* jshint -W071 */
/* jshint -W101 */
/* jshint -W106 */

})();
(function () {
'use strict';

angular.module('reportParameters', []).directive('reportParameters', ['ReportParameterTypes', reportParameters]);

function reportParameters(ReportParameterTypes) {
  var directive = {
    scope: {
      rowIndex: '@',
      params: '=',
      paramDictionaries: '=',
      isValid: '=',
    },
    restrict: 'E',
    templateUrl: 'resources/reports/parameters.html',
    link: link
  };

  return directive;

  function link($scope) {
    $scope.simpleParamsToHtml = {
      [ReportParameterTypes.NUMBER]: 'number',
      [ReportParameterTypes.STRING]: 'text',
      [ReportParameterTypes.DATE]: 'date',
    };
    let objectHasData = obj => (!!Object.values(obj).filter(x => x).length) ||
      obj instanceof Date;
    
    $scope.paramData = {};

    $scope.paramTypes = ReportParameterTypes;

    $scope.checkCanRunReport = function () {
      if (!$scope.params) {
        return;
      }
      let unsetParameters = $scope.params.filter(p => {
        if (p.allOmit) {
          return false;
        }

        let hasData = false;
        if (!!p.data) {
          switch (typeof p.data) {
            case 'number':
              hasData = p.data !== 0;
              break;
            case 'object':
              hasData = objectHasData(p.data);
              break;
            default:
              hasData = true;
              break;
          }
        }
        if (hasData) {
          return false;
        }

        let optionalSelected = !p.mandatory && p.isSet;
        return p.mandatory || optionalSelected;
      });
      $scope.isValid = !unsetParameters.length;
    };

    $scope.checkCanRunReportDelayed = () => {
      setTimeout(() => {
        $scope.checkCanRunReport();
        $scope.$apply();
      }, 300);
    };

    $scope.setDropdownValue = function(param, item) {
      param.data = item;
      param.isSet = (item.id !== -1);
      $scope.checkCanRunReport();
    };

    $scope.setupParams = function () {
      $scope.paramData = {};
      $scope.params.forEach(parameter => {
        if (!parameter.mandatory) {
          parameter.isSet = false;
        }

        $scope.paramData[parameter.name] = Object.assign([], $scope.paramDictionaries[parameter.type]);

        if (parameter.allOmit) {
          parameter.isSet = false;
          //id-value
          if (parameter.type === $scope.paramTypes.HGF_OFFICE ||
              parameter.type === $scope.paramTypes.NEGOTIATION_STATUS ||
              parameter.type === $scope.paramTypes.NEGOTIATION_STATUS_GROUP ||
              parameter.type === $scope.paramTypes.NEGOTIATION_TYPE ||
              parameter.type === $scope.paramTypes.REGION) {
            $scope.paramData[parameter.name].splice(0, 0, { id: -1, name: 'Alla' });
            parameter.data = $scope.paramData[parameter.name][0];
          }
          //raw values
          if (parameter.type === $scope.paramTypes.MUNICIPALITY) {
            $scope.paramData[parameter.name].splice(0, 0, 'Alla');
            parameter.data = $scope.paramData[parameter.name][0];
          }
          if (parameter.type === $scope.paramTypes.HGF_OFFICE_MULTIPLE) {
            parameter.data = -1;
          }
        }
      });
      $scope.checkCanRunReportDelayed();
    }

    let maybeSetup = () => {
      if ($scope.params && $scope.paramDictionaries) {
        $scope.setupParams();
      }
    };

    $scope.$watch('params', maybeSetup);
    $scope.$watch('paramDictionaries', maybeSetup);
    $scope.$on('checkParams', function ($event, data) {
      if (data.rowIndex == $scope.rowIndex) {
        $scope.checkCanRunReport();
      };
    });

    $scope.noParentRefs = param => !param.parentRef;
    $scope.anyNonParentParameters = () => $scope.params && !!$scope.params.filter(param => !param.parentRef).length;
  }
}

})();
(function () {
'use strict';

angular.module('reportCellValue', []).directive('reportCellValue', [reportCellValue]);

function reportCellValue() {
  var directive = {
    scope: {
      value: '=',
      type: '@',
    },
    restrict: 'E',
    templateUrl: 'resources/reports/report-cell-value.html',
    link: function (scope) {
      if (scope.value instanceof Array) {
        let value = scope.value[0];
        let theRest = scope.value.slice(1);
        scope.plusN = theRest.length;
        scope.tooltip = theRest.join('<br/>');
        scope.value = value;
      }
    }
  };

  return directive;
}

})();
(function () {
'use strict';

ReportsCtrl.$inject = ["Reports", "ReportParameterTypes", "ReportChartTypes", "HyUtils", "StringUtils", "Flash", "DateLocal", "client", "$document", "$timeout", "$scope"];
angular.module('hyresgastApp').controller('ReportsCtrl', ReportsCtrl);

function ReportsCtrl(Reports,
  ReportParameterTypes, ReportChartTypes,
  HyUtils, StringUtils, Flash, DateLocal,
  client,
  $document, $timeout, $scope) {

  var vm = this;

  vm.paramTypes = ReportParameterTypes;
  vm.chartTypes = ReportChartTypes;

  vm.resultTabs = [
    { title: 'Översikt', key: 'aggregated' },
    { title: 'Detaljerad information', key: 'raw' },
  ];

  vm.paramDictionaries = {};

  vm.busy = true;


  let setDefault = paramType => {
    let typeDictionary = vm.paramDictionaries[paramType];
    let defaultValue = typeDictionary ? typeDictionary[0] : null;
    vm.reports.forEach(report => {
      report.params
        .filter(param => param.type === paramType)
        .forEach(param => param.data = param.dictionary ? param.dictionary[0] : defaultValue);
    });
  };

  /**
   * Sets label for root for tree-like dictionary
   * @param {string} label 
   * @param {string} dictionaryType 
   */
  let setRooted = (label, dictionaryType) => {
    vm.paramDictionaries[dictionaryType] = {
      label, 
      subitems: vm.paramDictionaries[dictionaryType],
    };
  };

  vm.aggregatedTypes = null;

  vm.getAggregatedColumnType = labelIndex => {
    if (!vm.aggregatedTypes && vm.reportResult.chart.labels) {
      vm.aggregatedTypes = vm.reportResult.chart.labels.map(label => {
        let column = vm.reportResult.columns.find(x => x.name === label);
        return (column && column.type) ? column.type : '';
      });
    }
    return vm.aggregatedTypes[labelIndex];
  };

  Reports.query({client}).$promise.then(data => {
    vm.reports = data.reports.sort(StringUtils.stringSort("name"));
    vm.paramDictionaries = data.paramDictionaries;

    //TODO: depending on how new requirements will flow, consider moving to server side,
    //formalizing "default value for parameter"
    setDefault(ReportParameterTypes.YEAR);
    setDefault(ReportParameterTypes.MUNICIPALITY);
    setDefault(ReportParameterTypes.COLLECTION);

    //TODO: maybe move this to server side too
    setRooted('Alla regioner och kommun', ReportParameterTypes.REGION_MUNICIPALITY);

    vm.busy = false;
  });

  vm.selectedReport = null;
  vm.canRunReport = false;

  vm.isParentParamRes = index => {
    let isParent = vm.reportResult && 
    vm.reportResult.selectedSubreport &&
    vm.reportResult &&
    vm.reportResult.selectedSubreport.params.map(x => x.name).includes(vm.reportResult.columns[index].name);
    return isParent;
  };

  let fillSubreportParams = (subreport, resultRow) => {
    subreport.params.forEach(p => {
      if (p.parentRef) {
        p.data = resultRow[p.parentRef];
        p.mandatory = true;
        p.isSet = true;
      }
    });
  };

  let runSubreport = subreport => {
    Reports.run({ report: {
      id: `${vm.selectedReport.id}-sub-${subreport.id}`,
      params: subreport.params.concat(vm.selectedReport.params),
    }}).$promise.then(reportResult => {
        reportResult.rows = unpackRows(reportResult.rowsPacked);
        HyUtils.showDefaultModal('SubreportCtrl',
          'resources/reports/subreport.html', { reportResult },
        null, null, null, 
        { size: 'huge' });
      })
      .catch(() => Flash.set('Misslyckades att kör subreport', 'error'));
  };

  vm.expandResultRow = function (rowIndex) {
    if (!vm.selectedReport.subs || !vm.selectedReport.subs.length) {
      return;
    }
    let firstSubreport = vm.selectedReport.subs[0];
    if (vm.selectedReport.subs.length === 1 && firstSubreport.params.every(p => p.parentRef)) {
      let subreport = firstSubreport;
      fillSubreportParams(subreport, vm.reportResult.rows[rowIndex]);
      runSubreport(subreport);
    } else {
      vm.reportResult.expandedRow = rowIndex;
      vm.subreportChanged(rowIndex);
    }
  };

  vm.reportChanged = function (report) {
    if(report !== vm.selectedReport){
      //clear previous results on change
      vm.reportResult = null;
    }
    vm.selectedReport = report;
  };

  vm.subreportChanged = function (rowIndex) {
    $timeout(() => {
      fillSubreportParams(vm.reportResult.selectedSubreport, vm.reportResult.rows[rowIndex]);
      $scope.$broadcast('checkParams', { rowIndex });
    }, 200);
  };

  vm.runSubreport = () => runSubreport(vm.reportResult.selectedSubreport);

  let unpackRows = rowsPacked => {
    let jsBinary = atob(rowsPacked);
    let binary = Uint8Array.from(jsBinary.split(',').map(c => parseInt(c)));
    let unpacked = pako.ungzip(binary, { to: 'string' });
    return JSON.parse(unpacked);
  };

  let preprocessRunRequest = report => {
    let clone = structuredClone(report);
    clone.params.filter(x => x.type === ReportParameterTypes.DATE).forEach(p => {
      p.data = DateLocal.fromUTCtoLocal(p.data);
    });
    return clone;
  };

  vm.runReport = function () {
    vm.busy = true;
    Reports.run({ report: preprocessRunRequest(vm.selectedReport) })
      .$promise.then(result => {
        vm.aggregatedTypes = null;
        result.fullRows = unpackRows(result.rowsPacked);

        result.rows = result.fullRows.slice(0, 1000);

        vm.reportResult = result;
        let indext = 0;
        vm.reportResult.rows.forEach(r => r.index = indext++);
        vm.reportResult.params = vm.selectedReport.params.deepCopy();

        if (vm.selectedReport.subs) {
          vm.reportResult.selectedSubreport = vm.selectedReport.subs[0];
          vm.reportResult.colCount = result.rows.length ? Object.keys(result.rows[0]).length : -1;
          vm.reportResult.expandedRow = -1;
        }

        if (vm.reportResult.chart) {
          vm.setupChart();
        } else {
          vm.currentResultTab = 'raw';
        }
        vm.setupDetails();

        vm.busy = false;
    }).catch(() => {
      Flash.set('Misslyckades att kör rapport', 'error');
      vm.busy = false;
    });
  };

  let setChartOptions = (chart, displayLegend) => {
    let options = {
      options: {
        maintainAspectRatio: false,
        responsive: false,
      },
      scales: {
        yAxes: [{
          ticks: {
            beginAtZero: true,
            maxTicksLimit: Math.max(...(chart.data.map(r => Math.max(...r)))) + 2,
          },
        }]
      },
      legend: {
        display: displayLegend,
        position: 'right',
      }};
    let overrides = {};
    let colors = [
      { borderColor: "#ff2626", backgroundColor: "#FF0000" },
      { borderColor: "#ffb226", backgroundColor: "#FFA500" },
      { borderColor: "#ffff26", backgroundColor: "#FFFF00" },
      { borderColor: "#269326", backgroundColor: "#008000" },
      { borderColor: "#2626ff", backgroundColor: "#0000FF" },
      { borderColor: "#662694", backgroundColor: "#4B0082" },
      { borderColor: "#f094f0", backgroundColor: "#EE82EE" },
      { borderColor: "#ffc9d2", backgroundColor: "#FFC0CB" },
      { borderColor: "#b24949", backgroundColor: "#A52A2A" },
      { borderColor: "#26ffff", backgroundColor: "#00FFFF" },
      { borderColor: "#ff26ff", backgroundColor: "#FF00FF" },
      { borderColor: "#26ff26", backgroundColor: "#00FF00" },
      { borderColor: "#269393", backgroundColor: "#008080" },
      { borderColor: "#f2f2ff", backgroundColor: "#E6E6FA" },
      { borderColor: "#ffed26", backgroundColor: "#FFD700" },
      { borderColor: "#d9d9d9", backgroundColor: "#C0C0C0" },
      { borderColor: "#994949", backgroundColor: "#800000" },
      { borderColor: "#999926", backgroundColor: "#808000" },
      { borderColor: "#262699", backgroundColor: "#000080" },
      { borderColor: "#66e9e9", backgroundColor: "#40E0D0" },
      { borderColor: "#ffe2c2", backgroundColor: "#FFDAB9" },
      { borderColor: "#b2ffb2", backgroundColor: "#98FF98" },
      { borderColor: "#ffa992", backgroundColor: "#FF7F50" },
      { borderColor: "#99d5ee", backgroundColor: "#87CEEB" },
      { borderColor: "#e2aee2", backgroundColor: "#DDA0DD" },
      { borderColor: "#d87f3f", backgroundColor: "#D2691E" },
      { borderColor: "#e13759", backgroundColor: "#DC143C" },
      { borderColor: "#f2e99d", backgroundColor: "#F0E68C" },
      { borderColor: "#d3d3ff", backgroundColor: "#CCCCFF" },
      { borderColor: "#fa9387", backgroundColor: "#FA8072" },
      { borderColor: "#92ff26", backgroundColor: "#7FFF00" },
      { borderColor: "#92ffda", backgroundColor: "#7FFFD4" }
    ];

    switch (chart.type) {
      case ReportChartTypes.BAR:
        colors.forEach(c => c.fill = true);
        break;

      case ReportChartTypes.LINE:
        colors.forEach(c => c.fill = false);
        options.bezierCurve = false;
        options.scales.yAxes[0].type ='logarithmic';
        options.scales.yAxes[0].ticks.userCallback = tick => tick.toString();
        options.elements = {
          line: {
            tension: 0
          }
        };
        break;
    }
    chart.options = options;
    chart.colors = colors;
    chart.datasetOverrides = overrides;
  };

  vm.setupChart = function() {
    const tooMuchLabelsTreshold = 8;

    setChartOptions(vm.reportResult.chart, true);
    if (vm.reportResult.chart.subCharts) {
      vm.reportResult.chart.subCharts.forEach((subChart, index) => {
        let last = index === vm.reportResult.chart.subCharts.length - 1;
        setChartOptions(subChart, last);
      });
    }

    if (vm.reportResult.chart.labels.length < tooMuchLabelsTreshold) {
      vm.reportResult.tooMuchLabels = false;
      vm.reportResult.aggregatedColumnWidth = (70 / vm.reportResult.chart.labels.length) + '%';
    } else {
      vm.reportResult.aggregatedColumnWidth = "120px";
      vm.reportResult.tooMuchLabels = true;
      $timeout(prepareMagicScroller, 333);
    }
  };

  vm.setupDetails = function() {
    vm.reportResult.tooMuchDetailColumns = false;
    vm.wideLoadDetails = undefined;
    let columnWidthCount = vm.reportResult.columns.reduce((acc, el) => (acc += el.width), 0);
    if (columnWidthCount > 100) {
      let container = $document[0].getElementById('container');
      vm.reportResult.tooMuchDetailColumns = true;
      let pixels = columnWidthCount / 100 * container.offsetWidth;
      vm.wideLoadDetails = pixels;
    }
  };

  vm.drilldown = rowIndex => {

    let fuzzyFilter = (row, param) => Object.values(row).reduce((acc, el) => {
      acc |= param == el;
      return acc;
    }, false);

    let param = vm.reportResult.chart.series[rowIndex];
    
    let rows = vm.reportResult.fullRows.filter(x => fuzzyFilter(x, param));

    let reportResult = {
      rows,
      columns: vm.reportResult.columns,
      title: `nedskärning för ${param}`
    };
    HyUtils.showDefaultModal('SubreportCtrl',
          'resources/reports/subreport.html', { reportResult }, null, null, null, { size: 'huge' });
  };

  function prepareMagicScroller() {
    var tableContainerEl = $document[0].getElementById("magic-table-container");
    var tableHeaderContainerEl = $document[0].getElementById('magic-table-header-container');
    var tableEl = $document[0].getElementById('magic-table');
    var scrollerEl = $document[0].getElementById("magic-scroller");
    var scrollerWorkingBodyEl = $document[0].getElementById('magic-scroller-working-body');

    scrollerWorkingBodyEl.style.width = tableEl.offsetWidth + "px";
    scrollerWorkingBodyEl.style.height = "20px";

    scrollerEl.style.width = tableContainerEl.clientWidth + "px";
    scrollerEl.addEventListener("scroll", function() {
      tableHeaderContainerEl.scrollLeft = scrollerEl.scrollLeft;
      tableContainerEl.scrollLeft = scrollerEl.scrollLeft;
    });
  }

  vm.export = function (format) {
    let chart = $document[0].getElementById('myChart');
    let image = chart ? chart.toDataURL('image/png') : null;

    let isPdf = format === 'pdf';

    let exportObject = {
      title: vm.reportResult.title,
      paramSummary: $document[0].getElementById('paramSummary').innerText,
      data: vm.reportResult.rowsPacked,
      chartData: vm.reportResult.chart,
      columns: vm.reportResult.columns,
      image,
      report: vm.selectedReport,
    };

    vm.busy = true;
    let filename = (vm.reportResult.title).replace(/:| /g, '') + '.' + format;

    if (isPdf) {
      Reports.savePdf(exportObject)
      .$promise.then(result => {
        vm.busy = false;
        HyUtils.spitOutAttachment(result.buffer.data, filename, 'application/pdf');
      }).catch(() => Flash.set('Misslyckades att spara', 'error'));      
    } else {
      $timeout(() => {
        let columnNames = exportObject.columns.map(c => c.name);
        let sourceKeys = Object.keys(vm.reportResult.fullRows[0]);
        let sheetData = vm.reportResult.fullRows.map(source => {
          let result = {};
          exportObject.columns.forEach((column, i) => {
            let value = source[sourceKeys[i]];
            if (value instanceof Array) {
              value = value[0];
            }
            if (column.type === 'date') {
              value = new Date(value);
            }
            result[column.name] = value;
          });
          return result;
        });

        let workbook = XLSX.utils.book_new();
        let sheet = XLSX.utils.json_to_sheet(sheetData, { header: columnNames });
        let i = 65;
        exportObject.columns.forEach(c => {
          let letter = String.fromCharCode(i);
          if (c.type === 'money') {
            for (let row = 2; row < sheetData.length; row++) {
              let range = sheet[`${letter}${row}`];
              range.z = '#0';
            }
          }
          i++;
        });
        sheet["!cols"] = exportObject.columns.map(x => ({ width: 35 }));
        XLSX.utils.book_append_sheet(workbook, sheet,'DATA');
        let output = XLSX.write(workbook, {type: 'buffer'});

        HyUtils.spitOutAttachment(output, filename, 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet');

        vm.busy = false;
      }, 200);
    }
  };
}
})();
(function () {
'use strict';

SubreportCtrl.$inject = ["reportResult"];
angular.module('hyresgastApp').controller('SubreportCtrl', SubreportCtrl);

function SubreportCtrl(reportResult) {
  var vm = this;
  vm.reportResult = reportResult;

  vm.offset = 0;
  vm.limit = 10;
  vm.total = vm.reportResult.rows.length;

  vm.flipPage = () => {
    vm.rows = vm.reportResult.rows.slice(vm.offset, vm.offset + vm.limit);
  };

  vm.flipPage();
}
})();
(function () {
'use strict';

angular
  .module('uiSwitch', [])

  .directive('switch', function() {
    return {
      restrict: 'AE',
      replace: true,
      transclude: true,
      template: function(element, attrs) {
        var html = '';
        html += '<span';
        html += ' class="switch' + (attrs.class ? ' ' + attrs.class : '') + '"';
        html += attrs.ngModel
          ? ' ng-click="' +
            attrs.disabled +
            ' ? ' +
            attrs.ngModel +
            ' : ' +
            attrs.ngModel +
            '=!' +
            attrs.ngModel +
            (attrs.ngChange ? '; ' + attrs.ngChange + '()"' : '"')
          : '';
        html += ' ng-class="{ checked:' + attrs.ngModel + ', disabled:' + attrs.disabled + ' }"';
        html += '>';
        html += '<small></small>';
        html += '<input type="checkbox"';
        html += attrs.id ? ' id="' + attrs.id + '"' : '';
        html += attrs.name ? ' name="' + attrs.name + '"' : '';
        html += attrs.ngModel ? ' ng-model="' + attrs.ngModel + '"' : '';
        html += ' style="display:none" />';
        html += '<span class="switch-text">'; /*adding new container for switch text*/
        html += attrs.on
          ? '<span class="on">' + attrs.on + '</span>'
          : ''; /*switch text on value set by user in directive html markup*/
        html += attrs.off
          ? '<span class="off">' + attrs.off + '</span>'
          : ' '; /*switch text off value set by user in directive html markup*/
        html += '</span>';
        return html;
      }
    };
  });

})();
(function () {
'use strict';

ApartmentHistoryCtrl.$inject = ["$scope", "$timeout", "$interval", "$filter", "$uibModalInstance", "RealEstate", "HyUtils", "NegotiationTypes", "ApartmentStatusTypes", "apartment"];
angular.module('hyresgastApp').controller('ApartmentHistoryCtrl', ApartmentHistoryCtrl);

function ApartmentHistoryCtrl($scope, $timeout, $interval, $filter, $uibModalInstance,
  RealEstate,
  HyUtils,
  NegotiationTypes, ApartmentStatusTypes,
  apartment) {
  var vm = this;

  let iAmHere = HyUtils.whereAmI();
  let protocol = iAmHere.localhost ? 'http' : 'https';
  vm.host = iAmHere.isSP ? `${protocol}://${iAmHere.host.FP}` : '';

  vm.objectsExtra = apartment.objectsExtra ? JSON.parse(apartment.objectsExtra) : null;

  vm.apartment = apartment;
  if (!vm.apartment.rent && vm.apartment.currentMonthlyRent) {
    vm.apartment.rent = {
      monthlyRent: vm.apartment.currentMonthlyRent,
      effectiveFrom: vm.apartment.currentEffectiveFrom,
    };
  }
  vm.NegotiationTypes = NegotiationTypes;

  vm.statusString = Object.values(ApartmentStatusTypes)
    .filter(s => apartment.status & s.code)
    .map(s => s.longName)
    .join(', ');

  /**
   * sets objects extra floater. if index is not set, uses latest
   * @param {number} [index]
   */
  let setObjectsExtraFloater = index => {
    if (!index) {
      index = vm.history.length - 1;
    }
    let objectsExtra = vm.history[index].objectsExtra;
    vm.objectsExtraFloater = objectsExtra ? JSON.parse(objectsExtra) : null;
  }

  // eslint-disable-next-line no-unused-vars
  vm.chartHover = (points, evt) => {
    if (points.length) {
      setObjectsExtraFloater(points[0]._index);
      $scope.$apply();
      $timeout(setObjectsExtraFloater, 60000);
    }
  };

  let setCurrentLine = () => {
    if (vm.history.length > 0) {
      let currentDate = new Date(apartment.rent.effectiveFrom);
      let diffs = vm.labels.map(d => HyUtils.diffDays(currentDate, d));
      let minDiff = diffs.reduce((acc, el) => Math.min(acc, el), 9999);
      let nearestPointIndex = diffs.findIndex(d => d === minDiff);
      if (minDiff > 0) {
        nearestPointIndex++;
        vm.data[0].splice(nearestPointIndex, 0, apartment.rent.monthlyRent);
        vm.labels.splice(nearestPointIndex, 0, $filter('date')(apartment.rent.effectiveFrom, 'yyyy-MM-dd'));
        vm.history.splice(nearestPointIndex, 0, { type: -1, effectiveFrom: vm.labels[vm.labels.length - 1]});
      }
      vm.currentLineIndex = nearestPointIndex;
    }
  };

  let drawCurrentLine = () => {
    if (vm.currentLineIndex) {
      // eslint-disable-next-line no-undef
      Chart.helpers.each(Chart.instances, graph => {
        let entrails = Object.values(graph.data.datasets[0]._meta)[0];
        let point = entrails.dataset._children[vm.currentLineIndex]._model;
        let canvas = graph.chart.ctx;

        canvas.beginPath();
        canvas.moveTo(point.x, 0);
        canvas.strokeStyle = '#FF0000';
        canvas.lineTo(point.x, graph.scales['y-axis-0'].height);
        canvas.stroke();
      });
    }
  };

  RealEstate.getApartmentHistory({ id: vm.apartment.id })
    .$promise.then(function(response) {
      vm.data = [response.history.map(i => i.monthlyRent)];
      vm.labels = response.history.map(i => i.effectiveFrom);
      vm.series = [''];
      vm.options = {
        maintainAspectRatio: false,
        elements: {
          line: { tension: 0 }
        },
      };
      vm.history = response.history;

      console.log(structuredClone(response.history));
      setCurrentLine();
      console.log(structuredClone(response.history));
      var interval = $interval(drawCurrentLine, 500);
      // eslint-disable-next-line angular/controller-as
      $uibModalInstance.closed.then(() => { $interval.cancel(interval) });

      vm.historyReversed = response.history.slice().reverse();
      console.log(structuredClone(vm.historyReversed));
      setObjectsExtraFloater();
    });
}


})();
(function () {
'use strict';

ConfirmationModalInfoCtrl.$inject = ["$uibModalInstance", "text", "buttonClassSecondary"];
angular.module('hyresgastApp').controller('ConfirmationModalInfoCtrl', ConfirmationModalInfoCtrl);

function ConfirmationModalInfoCtrl($uibModalInstance, text, buttonClassSecondary) {
  var vm = this;
  vm.text = text;
  vm.buttonClassSecondary = buttonClassSecondary;

  vm.confirm = function() {
    $uibModalInstance.close();
  };

  vm.cancel = function() {
    $uibModalInstance.dismiss('cancel');
  };
}

})();
(function () {
'use strict';

InformationModalCtrl.$inject = ["$uibModalInstance", "OrderedNegotiationTypes"];
angular.module('hyresgastApp').controller('InformationModalCtrl', InformationModalCtrl);

function InformationModalCtrl($uibModalInstance, OrderedNegotiationTypes) {
  var vm = this;

  vm.info = [
    {
      groupTitle: 'Beskrivning av förhandlingstyperna',
      items: [
        {
          code: OrderedNegotiationTypes.ANNUAL_PROPERTY,
          desc: 'Årlig allmän hyresförändring av samtliga lägenheter på en fastighet.'
        },
        {
          code: OrderedNegotiationTypes.STANDARD_PROPERTY,
          desc: 'En eller flera enstaka åtgärder i olika lägenheter på en eller flera fastigheter.'
        },
        {
          code: OrderedNegotiationTypes.REBUILD_PROPERTY,
          desc: 'Större ombyggnation på fastigheten som exempelvis stambyte. Där både allmänna utrymmen och samtliga lägenheter omfattas.'
        },
        {
          code: OrderedNegotiationTypes.REBUILD_PROPERTY_2,
          desc: 'Enskild lägenhet/lägenheter som totalrenoveras.'
        },
        {
          code: OrderedNegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT,
          desc: 'Nyproducerade fastigheter eller enskilda lägenheter i befintligt bestånd.'
        },
        {
          code: OrderedNegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY,
          desc: 'Nyproduktionsförhandlingar där fastigheten inte är klar eller inte byggts ännu och inte fått en fastighetsbeteckning som är registrerad.'
        },
        {
          code: OrderedNegotiationTypes.UTILITY_VALUE_PROPERTY,
          desc: 'Omprövning av hyran för alla lägenheter i en fastighet eller en eller flera lägenheter i en fastighet.'
        },
      ]
    }
  ]

  vm.close = function() {
    $uibModalInstance.close();
  };
}
})();
(function () {
'use strict';

QuestionCtrl.$inject = ["$uibModalInstance", "message", "answers"];
angular.module('hyresgastApp').controller('QuestionCtrl', QuestionCtrl);

function QuestionCtrl($uibModalInstance, message, answers) {
  var vm = this;

  vm.message = message;
  vm.answers = answers;

  vm.answer = function(answer) {
    $uibModalInstance.close(answer);
  };

  vm.cancel = function() {
    $uibModalInstance.dismiss('cancel');
  };
}
})();
(function () {
'use strict';

BooleanConfirmationModalCtrl.$inject = ["$uibModalInstance"];
angular.module('hyresgastApp').controller('BooleanConfirmationModalCtrl', BooleanConfirmationModalCtrl);

function BooleanConfirmationModalCtrl($uibModalInstance) {
  var vm = this;
  vm.cancel = cancel;
  vm.confirm = confirm;

  /////////

  function confirm() {
    $uibModalInstance.close(true);
  }

  function cancel() {
    $uibModalInstance.close();
  }
}

})();
(function () {
'use strict';

ConfirmationModalCtrl.$inject = ["$uibModalInstance"];
angular.module('hyresgastApp').controller('ConfirmationModalCtrl', ConfirmationModalCtrl);

function ConfirmationModalCtrl($uibModalInstance) {
  var vm = this;

  vm.confirm = function() {
    $uibModalInstance.close();
  };

  vm.cancel = function() {
    $uibModalInstance.dismiss('cancel');
  };
}

})();
(function () {
'use strict';

ConfirmationModalWithMessageAndButtonsCtrl.$inject = ["$uibModalInstance", "caption", "message", "confirmBtn", "cancelBtn"];
angular.module('hyresgastApp').controller('ConfirmationModalWithMessageAndButtonsCtrl', ConfirmationModalWithMessageAndButtonsCtrl);

function ConfirmationModalWithMessageAndButtonsCtrl($uibModalInstance, caption, message, confirmBtn, cancelBtn) {
  var vm = this;

  vm.caption = caption;
  vm.message = message;
  vm.confirmBtn = confirmBtn;
  vm.cancelBtn = cancelBtn;

  vm.confirm = function() {
    $uibModalInstance.close();
  };

  vm.cancel = function() {
    $uibModalInstance.dismiss('cancel');
  };
}

})();
(function () {
'use strict';

ConfirmationModalWithMessageCtrl.$inject = ["$uibModalInstance", "message"];
angular.module('hyresgastApp').controller('ConfirmationModalWithMessageCtrl', ConfirmationModalWithMessageCtrl);

function ConfirmationModalWithMessageCtrl($uibModalInstance, message) {
  var vm = this;

  vm.message = message;

  vm.confirm = function() {
    $uibModalInstance.close();
  };

  vm.cancel = function() {
    $uibModalInstance.dismiss('cancel');
  };
}

})();
(function () {
'use strict';
NewFreeAgreementModalCtrl.$inject = ["$uibModalInstance", "$scope", "agreement", "users", "invites", "User", "FreeAgreementTypes", "Parties"];
angular.module('hyresgastApp').controller('NewFreeAgreementModalCtrl', NewFreeAgreementModalCtrl);

function NewFreeAgreementModalCtrl($uibModalInstance, $scope,
  agreement, users, invites,
  User,
  FreeAgreementTypes, Parties) {
  var vm = this;

  vm.FreeAgreementTypes = FreeAgreementTypes;

  vm.userContainer = {
    hgfSide: [],
    faSide: [],
  };

  if (users) {
    let hgfSide = users.filter(u => u.party === Parties.HGF);
    hgfSide.forEach(p => p.isChief = p.roleObj.isChief);
    let faSide = users.filter(u => u.party !== Parties.HGF); //we want delegates too
    faSide.forEach(p => p.isChief = p.roleObj.isChief);

    vm.userContainer = {
      hgfSide,
      faSide,
      chiefNegotiatorHGF: hgfSide.find(p => p.isChief),
      chiefNegotiatorExternal: faSide.find(p => p.isChief),
      invites,
    };
    vm.skipOrganizationSet = true;
  }

  $scope.$watch('vm.userContainer.faSide', function (newVal) {
    let id = newVal.find(p => p.isChief).userId;
    User.queryOrganization({ id }).$promise.then(response => {
      vm.userOrganizations = response;

      if (vm.skipOrganizationSet) {
        vm.skipOrganizationSet = false;
        if (agreement && agreement.conditionTags.organization) {
          vm.selectedOrganization = agreement.conditionTags.organization;
        } else {
          vm.selectedOrganization = vm.userOrganizations[0];
        }
      } else {
        vm.selectedOrganization = vm.userOrganizations[0];
      }
    });
  });

  vm.ok = () => {
    $uibModalInstance.close({
      userContainer: vm.userContainer,
      type: vm.type,
      message: vm.message,
      organization: vm.selectedOrganization,
    });
  };
  vm.cancel = () => { $uibModalInstance.close({ cancel: true }) };
}
})();
(function () {
'use strict';

EditBlockingApartmentCtrl.$inject = ["$scope", "$location", "$route", "$routeParams", "Blocking", "Negotiation", "Organization", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('EditBlockingApartmentCtrl', EditBlockingApartmentCtrl);

function EditBlockingApartmentCtrl(
  $scope,
  $location,
  $route,
  $routeParams,
  Blocking,
  Negotiation,
  Organization,
  Flash,
  ErrorHandler
) {
  var vm = this;

  vm.blocking = {
    negotiationId: $routeParams.negotiationId,
    id: $routeParams.id,
    documents: [],
    name: '', // there is a not null constraing on api
    isAllDistrictsSelected: true,
    isAllPropertiesSelected: true,
    isAllApartmentsSelected: true
  };

  vm.gotoNextStep = gotoNextStep;
  vm.save = save;

  // Fetch districts when the user wants to sort by column or direction
  $scope.$watchGroup(['vm.sortDistrictColumn', 'vm.sortDistrictDirection'], function() {
    // Check for undefined 'order' parameter in the URL
    if ($location.search().order) {
      // Reset the offset when changing column or direction of sorting
      vm.districtPageOffset = 0;
      queryDistrict();
    }
  });

  // Fetch properties when the user wants to sort by column or direction
  $scope.$watchGroup(['vm.sortPropertiesColumn', 'vm.sortPropertiesDirection'], function() {
    // Check for undefined 'order' parameter in the URL
    if ($location.search().order) {
      // Reset the offset when changing column or direction of sorting
      vm.propertiesPageOffset = 0;
      queryProperties();
    }
  });

  // Fetch apartments when the user wants to sort by column or direction
  $scope.$watchGroup(['vm.sortPropertiesColumn', 'vm.sortPropertiesDirection'], function() {
    // Check for undefined 'order' parameter in the URL
    if ($location.search().order) {
      // Reset the offset when changing column or direction of sorting
      vm.apartmentsPageOffset = 0;
      queryApartments();
    }
  });

  vm.districtPageOffset = 0;
  vm.propertiesPageOffset = 0;
  vm.apartmentsPageOffset = 0;
  vm.propertiesPageLimit = 30;
  vm.districtPageLimit = 30;
  vm.apartmentsPageLimit = 30;
  vm.blocking.selectedDistrictIds = {};
  vm.blocking.selectedPropertyIds = {};
  vm.blocking.selectedApartmentIds = {};
  vm.queryProperties = queryProperties;
  vm.queryDistrict = queryDistrict;
  vm.queryApartments = queryApartments;

  vm.sortPropertiesColumn = $location.search().order || 'propertyDesignation'; // default
  vm.sortPropertiesDirection = $location.search().direction || 'ASC'; // default
  vm.listPropertiesColumns = [
    { title: 'Fastigheter', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments' }
  ];

  vm.sortDistrictColumn = $location.search().order || 'district'; // default
  vm.sortDistrictDirection = $location.search().direction || 'ASC'; // default
  vm.listDistrictColumns = [
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal fastigheter', column: 'noProperties' }
  ];

  vm.sortApartmentsColumn = $location.search().order || 'property.propertyDesignation'; // default
  vm.sortApartmentsDirection = $location.search().direction || 'ASC'; // default
  vm.listApartmentsColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Fastighet', column: 'property.propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' }
  ];

  queryDistrict();
  getNegotiation();
  // getApartmentsSummary(id);

  // Keep it simple - fetch the properties when navigating to the properties step
  // instead of i.e fetching every time a districts checkbox is checked
  $scope.$on('$routeUpdate', function() {
    if ($location.search().step === 'properties') {
      vm.propertiesPageOffset = 0; // always show first items
      // aFlag is used because when we filter the result from queryProperties(),
      // we don't want selectedPropertyIds = {}; to be reseted.
      // We want reset only when the route is changed and response.count != vm.propertyTotalCount.
      vm.propertyFlag = true;
      queryProperties();
    }

    if ($location.search().step === 'apartments') {
      vm.apartmentsPageOffset = 0;
      vm.apartmentsFlag = true;
      queryApartments();
    }
  });

  ////////////

  function save() {
    vm.saving = true;
    Blocking.saveApartment(vm.blocking)
      .$promise.then(
        // eslint-disable-next-line no-unused-vars
        function(response) {
          // keep it simple for now (instead of reseting all
          // variables and fetching appartmens reload the controller)
          $route.reload();

          Flash.set('Fastighetsbestånd har sparats', 'success');
        })
      .catch(ErrorHandler('Misslyckades att spara fastighetsbestånd.'))
      .finally(function() {
        vm.saving = false;
        $location.search({});
        $location.path('/negotiations/' + $routeParams.negotiationId + '/blockings/' + $routeParams.id);
      });
  }

  function gotoNextStep() {
    vm.gotoNext = true;
  }

  function getNegotiation() {
    return Negotiation.get({
      id: $routeParams.negotiationId
    })
      .$promise.then(function(response) {
        vm.negotiation = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function() {
        vm.readyNegotiation = true;
      });
  }

  function queryDistrict(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.districtPageOffset = 0;
    }
    return Negotiation.queryDistrict({
      id: $routeParams.negotiationId,
      limit: vm.districtPageLimit,
      offset: vm.districtPageOffset,
      order: vm.sortDistrictColumn,
      direction: vm.sortDistrictDirection,
      'search[municipality]': vm.inputDistrictTextMunicipality,
      'search[district]': vm.inputDistrictTextDistrict,
      unassigned: true
    })
      .$promise.then(function(response) {
        vm.districts = response.rows;
        vm.districtTotalCount = response.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta område.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function queryProperties(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.propertiesPageOffset = 0;
    }
    return Negotiation.queryPropertyBySelection(
      {
        id: $routeParams.negotiationId,
        limit: vm.propertiesPageLimit,
        offset: vm.propertiesPageOffset,
        order: vm.sortPropertiesColumn,
        direction: vm.sortPropertiesDirection,
        'search[propertyDesignation]': vm.inputPropertiesTextProperty,
        'search[municipality]': vm.inputPropertiesTextMunicipality,
        'search[district]': vm.inputPropertiesTextDistrict,
      },
      {
        unassigned: true,
        selectedDistrictIds: vm.blocking.selectedDistrictIds,
        isAllDistrictsSelected: true
      }
    )
      .$promise.then(function(response) {
        // If the count of properties has changed from one query to the next,
        // then the user has gone back to the district selector and chosen a different set
        // of districts (or we've never loaded the properties before).
        // When this happens, we need to reset the selection status, because otherwise the
        // "master select box" will not detect the everything/nothing/partial selection states
        // properly, because it'll have the wrong idea of what the full selection is.
        if (vm.propertyFlag && vm.propertyTotalCount !== response.count) {
          vm.blocking.selectedPropertyIds = {};
          vm.propertyFlag = false;
        }
        vm.properties = response.rows;
        vm.propertyTotalCount = response.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta fastigheter.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function queryApartments(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.apartmentsPageOffset = 0;
    }
    return Negotiation.queryApartment(
      {
        id: $routeParams.negotiationId,
        limit: vm.apartmentsPageLimit,
        offset: vm.apartmentsPageOffset,
        order: vm.sortApartmentsColumn,
        direction: vm.sortApartmentsDirection,
        'search[nationalNo]': vm.inputApartmentTextNationalApartmentNo,
        'search[ownerNo]': vm.inputApartmentTextApartmentNo,
        'search[streetAddress]': vm.inputApartmentTextStreetAddress,
        'search[property.propertyDesignation]': vm.inputApartmentTextPropertyDesignation,
        'search[municipality]': vm.inputApartmentTextMunicipality,
        'search[district]': vm.inputApartmentTextDistrict,
        unassigned: true
      },
      {
        selectedDistrictIds: vm.blocking.selectedDistrictIds,
        selectedPropertyIds: vm.blocking.selectedPropertyIds,
        isAllDistrictsSelected: vm.blocking.isAllDistrictsSelected,
        isAllPropertiesSelected: vm.blocking.isAllPropertiesSelected
      }
    )
      .$promise.then(function(response) {
        // If the count of properties has changed from one query to the next,
        // then the user has gone back to the district selector and chosen a different set
        // of districts (or we've never loaded the properties before).
        // When this happens, we need to reset the selection status, because otherwise the
        // "master select box" will not detect the everything/nothing/partial selection states
        // properly, because it'll have the wrong idea of what the full selection is.
        if (vm.apartmentsFlag && vm.apartmentTotalCount !== response.count) {
          vm.blocking.selectedApartmentIds = {};
          vm.apartmentsFlag = false;
        }
        vm.apartments = response.rows;
        vm.apartmentTotalCount = response.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta lägenheter.'))
      .finally(function() {
        vm.ready = true;
      });
  }
}

})();
(function () {
'use strict';

ListBlockingPropertiesCtrl.$inject = ["$scope", "$route", "$routeParams", "$location", "$timeout", "Auth", "Blocking", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListBlockingPropertiesCtrl', ListBlockingPropertiesCtrl);

function ListBlockingPropertiesCtrl($scope, $route, $routeParams, $location, $timeout,
  Auth, Blocking, Flash, ErrorHandler) {
  var vm = this;

  vm.pageOffset = 0;
  vm.pageLimit = 30;
  vm.delete = destroy;
  vm.getProperties = getProperties;

  vm.listColumns = [
    { title: 'Fastigheter', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments' }
  ];
  vm.sortColumn = vm.listColumns.some(function(c) {
    return c.column === $location.search().order;
  }) ? $location.search().order : 'propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getProperties();
    }
  });

  getProperties();

  function destroy(property) {
    // if select subpage is opened close it so it will updated
    // with correct list when re-opened
    $scope.$parent.vm.editPropertiesMode = false;
    vm.deleting = true;

    let params = {
      negotiationId: $routeParams.negotiationId,
      blockingId: $routeParams.id,
      propertiesIds: property ? [property.id] : getIdsFromTicked(vm.selectedProperties),
    };

    Blocking.deleteProperty(params).$promise.then(() => {
      vm.selectedProperties = {};
      vm.properties = vm.properties.filter(x => !params.propertiesIds.includes(x.id));
      vm.pageOffset = 0;
      vm.getPropertiesVirtual();
      vm.propertiesTotalCount -= params.propertiesIds.length;
      $scope.$parent.vm.blocking.numProperties = vm.propertiesTotalCount;
      // i.e interested in notAssignedCount in negotiations
      // object to know if to show or hide buttons
      $scope.$parent.vm.getNegotiation();
      vm.initialTotalCount = vm.propertiesTotalCount;
      if (vm.initialTotalCount === 0) {
        $scope.$parent.vm.editPropertiesMode = true;
      }
      Flash.set('Lyckades att ta bort fastighet', 'success');
    }).catch(new ErrorHandler('Misslyckades att ta bort fastighet.'))
      .finally(() => { vm.deleting = false; });
  }

  vm.getPropertiesVirtual = () => {
    vm.propertiesVirtual = vm.properties.slice(vm.pageOffset, vm.pageOffset + vm.pageLimit);
  };
  function getProperties(filterMode) {
    vm.ready = false;
    if (filterMode) {
      vm.pageOffset = 0;
    }
    return Blocking.queryProperty({
      negotiationId: $routeParams.negotiationId,
      id: $routeParams.id,
      limit: 2000,
      offset: 0,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'search[propertyDesignation]': vm.inputPropertiesTextProperty,
      'search[ownerName]': vm.inputPropertiesTextOwner,
      'search[municipality]': vm.inputPropertiesTextMunicipality,
      'search[district]': vm.inputPropertiesTextDistrict,
      include: ['owners']
    })
      .$promise.then(function(response) {
        vm.properties = response.result.rows;
        vm.propertiesTotalCount = response.result.count;
        // Save initial count of records, to be distinguished from the current count after
        // unsuccessful filtering. This will affect all messages, which are dependent on totalCount = 0.
        if (filterMode) {
          vm.offset = 0;
        } else {
          vm.initialTotalCount = vm.propertiesTotalCount;
        }
        // open the select subpage when properties' list is empty
        if (vm.initialTotalCount === 0) {
          $scope.$parent.vm.editPropertiesMode = true;
        }

        vm.selectedProperties = createTickedObject(vm.properties, false);
        vm.toggledAll = false;
        vm.getPropertiesVirtual();
      })
      .catch(new ErrorHandler('Misslyckades att hämta fastighetsbestånd.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  //TODO: flytta til fe-common
  /**
   * converts ticked object to array of ids
   * @param {Object} ticked
   * @returns {Array<number>}
   */
  const getIdsFromTicked = ticked => Object.keys(ticked).filter(k => ticked[k]).map(id => parseInt(id));

  /**
   * creates ticked object from collection
   * @param {Array} rows
   * @param {boolean} [initialValue]
   * @returns {Object}
   */
  const createTickedObject = (rows, initialValue = true) => rows.reduce((acc, el) => {
    acc[el.id] = initialValue;
    return acc;
  }, {});

  vm.hasSelectedProperties = () =>
    vm.selectedProperties ? getIdsFromTicked(vm.selectedProperties).length : false;

  vm.toggleProperty = () => {
    $timeout(() => {
      vm.toggledAll = getIdsFromTicked(vm.selectedProperties).length === vm.properties.length;
    }, 500);
  };

  vm.toggleAllProperties = () => {
    if (getIdsFromTicked(vm.selectedProperties).length < vm.properties.length) {
      vm.selectedProperties = createTickedObject(vm.properties);
    } else {
      vm.selectedProperties = {};
    }
  };
}
})();
(function () {
'use strict';

ListBlockingApartmentsCtrl.$inject = ["$scope", "$routeParams", "$location", "Blocking", "Auth", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListBlockingApartmentsCtrl', ListBlockingApartmentsCtrl);

function ListBlockingApartmentsCtrl($scope, $routeParams, $location, Blocking, Auth, Flash, ErrorHandler) {
  var vm = this;
  vm.apartmentsPageLimit = 30;
  vm.apartmentsPageOffset = 0;

  vm.delete = destroy;

  vm.getApartments = getApartments;
  vm.sortColumn = $location.search().order || 'property.propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default
  vm.listColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Fastighet', column: 'property.propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'Ingående hyra (kr/månad)', column: 'rent.monthlyRent' }
  ];

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.apartmentsPageOffset = 0;
      getApartments();
    }
  });

  getApartments();

  /////////

  function getApartments(filterMode) {
    vm.readyApartments = false;
    if (filterMode) {
      vm.apartmentsPageOffset = 0;
    }
    return Blocking.queryApartment({
      negotiationId: $routeParams.negotiationId,
      id: $routeParams.id,
      limit: vm.apartmentsPageLimit,
      offset: vm.apartmentsPageOffset,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'search[nationalNo]': vm.inputTextNationalApartmentNo,
      'search[ownerNo]': vm.inputTextApartmentNo,
      'search[streetAddress]': vm.inputTextStreetAddress,
      'search[property.propertyDesignation]': vm.inputTextPropertyDesignation,
      'search[municipality]': vm.inputTextMunicipality
    })
      .$promise.then(function(response) {
        vm.apartments = response.rows;
        vm.apartmentsTotalCount = response.count;
        // Save initial count of records, to be distingueshed from the current count after
        // unsuccessful filtering. This will affect all messages, which are dependent on totalCount = 0.
        if (filterMode !== true) {
          vm.initialTotalCount = vm.apartmentsTotalCount;
        }
        // open the wizard when apartments' list is empty
        if (vm.initialTotalCount === 0) {
          $scope.$parent.vm.editApartmentMode = true;
        }
      })
      .catch(new ErrorHandler('Misslyckades att hämta fastighetsbestånd.'))
      .finally(function() {
        vm.readyApartments = true;
      });
  }

  function destroy(property) {
    // if wizard is open close it so it will updated
    // with correct list when re-opened
    $scope.$parent.vm.editApartmentMode = false;
    vm.deletingApartment = true;
    return Blocking.deleteApartment({
      negotiationId: $routeParams.negotiationId,
      blockingId: $routeParams.id,
      id: property.id
    })
      // eslint-disable-next-line no-unused-vars
      .$promise.then(function(response) {
        var index = vm.apartments.indexOf(property);
        vm.apartments.splice(index, 1);
        vm.apartmentsTotalCount--;
        $scope.$parent.vm.blocking.numApartments = vm.apartmentsTotalCount;
        // i.e interested in notAssignedCount in negotiations
        // object to know if to show or hide buttons
        $scope.$parent.vm.getNegotiation();
        vm.initialTotalCount = vm.apartmentsTotalCount;
        if (vm.initialTotalCount === 0) {
          $scope.$parent.vm.editApartmentMode = true;
        }
        Flash.set('Lyckades att ta bort lägenhet', 'success');
      })
      .catch(new ErrorHandler('Misslyckades att ta bort lägenhet.'))
      .finally(function() {
        vm.deletingApartment = false;
      });
  }
}

})();
(function () {
'use strict';

EditBlockingPropertiesCtrl.$inject = ["$scope", "$route", "$routeParams", "$location", "$timeout", "Negotiation", "Blocking", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('EditBlockingPropertiesCtrl', EditBlockingPropertiesCtrl);

function EditBlockingPropertiesCtrl($scope, $route, $routeParams, $location, $timeout, Negotiation,
  Blocking, Flash, ErrorHandler) {
  var vm = this;

  vm.blocking = {
    negotiationId: $routeParams.negotiationId,
    id: $routeParams.id,
    blockedBy: $location.search().blockedBy,
    documents: [],
    name: '', // there is a not null constraing on api
    isAllPropertiesSelected: true,
  };

  vm.save = save;

  // Fetch properties when the user wants to sort by column or direction
  $scope.$watchGroup(['vm.sortColumn', 'vm.sortDirection'], function () {
    // Check for undefined 'order' parameter in the URL
    if ($location.search().order) {
      // Reset the offset when changing column or direction of sorting
      vm.pageOffset = 0;
      getProperties();
    }
  });

  vm.pageOffset = 0;
  vm.pageLimit = 30;
  vm.blocking.selectedPropertyIds = {};
  vm.getProperties = getProperties;

  vm.listColumns = [
    { title: 'Fastigheter', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments' }
  ];
  vm.sortColumn = getLocationOrderIfValid(vm.listColumns) || 'propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  getProperties();
  getNegotiation();

  // returns sort column from url parameter ($location.search().order) if it's included in the given
  // list of column definitions; null otherwise
  function getLocationOrderIfValid(allowedColumns) {
    if (
      allowedColumns.some(function (c) {
        return c.column === $location.search().order;
      })
    ) {
      return $location.search().order;
    }
    return null;
  }

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getProperties();
    }
  });

  ////////////

  function save() {
    vm.saving = true;
    Blocking.saveProperty(vm.blocking)
      .$promise.then(
        // eslint-disable-next-line no-unused-vars
        function (response) {
          $location.search({}); // remove parameters set by wizard
          $location.path('/negotiations/' + vm.blocking.negotiationId).search('view', 'agreements');
          Flash.set('Fastigheter i förhandlingen har strandats', 'success');
        })
      .catch(new ErrorHandler('Misslyckades att stranda förhandlingen.'))
      .finally(function () {
        vm.saving = false;
      });
  }

  function getNegotiation() {
    return Negotiation.get({
      id: $routeParams.negotiationId
    })
      .$promise.then(function (response) {
        vm.negotiation = response;
      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function () {
        vm.readyNegotiation = true;
      });
  }

  vm.getPropertiesVirtual = () => {
    vm.propertiesVirtual = vm.properties.slice(vm.pageOffset, vm.pageOffset + vm.pageLimit);
  };
  function getProperties(filterMode) {
    vm.ready = false;
    if (filterMode) {
      vm.pageOffset = 0;
    }
    return Negotiation.queryPropertyBySelection(
      {
        id: vm.blocking.negotiationId,
        limit: 2000,
        offset: 0,
        order: vm.sortColumn,
        direction: vm.sortDirection,
        'search[propertyDesignation]': vm.inputPropertiesTextProperty,
        'search[ownerName]': vm.inputPropertiesTextOwner,
        'search[municipality]': vm.inputPropertiesTextMunicipality,
        'search[district]': vm.inputPropertiesTextDistrict,
        include: ['owners']
      },
      {
        unassigned: true,
        selectedDistrictIds: {},
        isAllDistrictsSelected: true,
      }
    )
      .$promise.then(function (response) {
        if (vm.propertiesTotalCount !== response.count) {
          vm.blocking.selectedPropertyIds = {};
        }
        vm.properties = response.rows;
        vm.getPropertiesVirtual();
        vm.propertiesTotalCount = response.count;
      })
      .catch(new ErrorHandler('Misslyckades att hämta fastigheter.'))
      .finally(function () {
        vm.ready = true;
      });
  }
}
})();
(function () {
'use strict';

ListAgreementPropertiesCtrl.$inject = ["$scope", "$route", "$routeParams", "$location", "$timeout", "Agreement", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListAgreementPropertiesCtrl', ListAgreementPropertiesCtrl);

function ListAgreementPropertiesCtrl($scope, $route, $routeParams, $location, $timeout,
  Agreement, Flash, ErrorHandler) {
  var vm = this;

  vm.pageOffset = 0;
  vm.pageLimit = 30;
  vm.delete = destroy;
  vm.getProperties = getProperties;

  vm.listColumns = [
    { title: 'Fastigheter', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments' }
  ];
  vm.sortColumn = vm.listColumns.some(function(c) {
    return c.column === $location.search().order;
  }) ? $location.search().order : 'propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getProperties();
    }
  });

  getProperties();

  function destroy(property) {
    // if select subpage is opened close it so it will updated
    // with correct list when re-opened
    $scope.$parent.vm.editPropertiesMode = false;
    vm.deleting = true;

    let params = {
      negotiationId: $routeParams.negotiationId,
      agreementId: $routeParams.id,
      propertiesIds: property ? [property.id] : getIdsFromTicked(vm.selectedProperties),
    };

    Agreement.deleteProperty(params).$promise.then(() => {
      vm.selectedProperties = {};
      vm.properties = vm.properties.filter(x => !params.propertiesIds.includes(x.id));
      vm.pageOffset = 0;
      vm.getPropertiesVirtual();
      vm.propertiesTotalCount -= params.propertiesIds.length;
      $scope.$parent.vm.agreement.numProperties = vm.propertiesTotalCount;
      // i.e interested in notAssignedCount in negotiations
      // object to know if to show or hide buttons
      $scope.$parent.vm.getNegotiation($routeParams.negotiationId);
      vm.initialTotalCount = vm.propertiesTotalCount;
      if (vm.initialTotalCount === 0) {
        $scope.$parent.vm.editPropertiesMode = true;
      }
      Flash.set('Lyckades ta bort fastighet', 'success');
    }).catch(new ErrorHandler('Misslyckades att bort fastighet.'))
      .finally(() => { vm.deleting = false; });
  }

  vm.getPropertiesVirtual = () => {
    vm.propertiesVirtual = vm.properties.slice(vm.pageOffset, vm.pageOffset + vm.pageLimit);
  };
  function getProperties(filterMode) {
    vm.ready = false;
    if (filterMode) {
      vm.pageOffset = 0;
    }
    return Agreement.queryProperty({
      negotiationId: $routeParams.negotiationId,
      id: $routeParams.id,
      limit: 2000,
      offset: 0,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'search[propertyDesignation]': vm.inputPropertiesTextProperty,
      'search[ownerName]': vm.inputPropertiesTextOwner,
      'search[municipality]': vm.inputPropertiesTextMunicipality,
      'search[district]': vm.inputPropertiesTextDistrict
    })
      .$promise.then(function(response) {
        vm.properties = response.result.rows;
        vm.propertiesTotalCount = response.result.count;
        // Save initial count of records, to be distinguished from the current count after
        // unsuccessful filtering. This will affect all messages, which are dependent on totalCount = 0.
        if (filterMode) {
          vm.offset = 0;
        } else {
          vm.initialTotalCount = vm.propertiesTotalCount;
        }
        // open the select subpage when properties' list is empty
        if (vm.initialTotalCount === 0) {
          $scope.$parent.vm.editPropertiesMode = true;
        }

        vm.selectedProperties = createTickedObject(vm.properties, false);
        vm.toggledAll = false;
        vm.getPropertiesVirtual();
      })
      .catch(new ErrorHandler('Misslyckades att hämta fastighetsbestånd.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  //TODO: flytta til fe-common
  /**
   * converts ticked object to array of ids
   * @param {Object} ticked
   * @returns {Array<number>}
   */
  const getIdsFromTicked = ticked => Object.keys(ticked).filter(k => ticked[k]).map(id => parseInt(id));

  /**
   * creates ticked object from collection
   * @param {Array} rows
   * @param {boolean} [initialValue]
   * @returns {Object}
   */
  const createTickedObject = (rows, initialValue = true) => rows.reduce((acc, el) => {
    acc[el.id] = initialValue;
    return acc;
  }, {});

  vm.hasSelectedProperties = () =>
    vm.selectedProperties ? getIdsFromTicked(vm.selectedProperties).length : false;

  vm.toggleProperty = () => {
    $timeout(() => {
      vm.toggledAll = getIdsFromTicked(vm.selectedProperties).length === vm.properties.length;
    }, 500);
  };

  vm.toggleAllProperties = () => {
    if (getIdsFromTicked(vm.selectedProperties).length < vm.properties.length) {
      vm.selectedProperties = createTickedObject(vm.properties);
    } else {
      vm.selectedProperties = {};
    }
  };
}
})();
(function () {
'use strict';

SelectAgreementPropertiesCtrl.$inject = ["$scope", "$route", "$routeParams", "$location", "Negotiation", "Agreement", "Limits", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('SelectAgreementPropertiesCtrl', SelectAgreementPropertiesCtrl);

function SelectAgreementPropertiesCtrl($scope, $route, $routeParams, $location,
  Negotiation, Agreement,
  Limits,
  Flash, ErrorHandler) {
  var vm = this;
  vm.Limits = Limits;

  vm.agreement = {
    negotiationId: $routeParams.negotiationId,
    id: $routeParams.id,
    documents: [],
    name: '', // there is a not null constraint on api
    isAllPropertiesSelected: true,
  };

  vm.pageLimit = 20;

  vm.tableColumns = [
    { title: 'Fastigheter', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments' }
  ];

  vm.tableFGetData = () => {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      Negotiation.queryPropertyBySelection({
        id: $routeParams.negotiationId,
        include: ['owners']
      }, {
        unassigned: true,
        selectedDistrictIds: {},
        isAllDistrictsSelected: true,
      }).$promise.then(function (response) {
        resolve(response);
      }).catch(new ErrorHandler('Misslyckades att hämta fastigheter.'))
    });
  }

  vm.properties = [];

  vm.save = () => {
    vm.saving = true;
    vm.agreement.selectedPropertyIds = Object.fromEntries(vm.properties.map(p => [p.id, true]));
    Agreement.saveProperty(vm.agreement)
      .$promise.then(
        // eslint-disable-next-line no-unused-vars
        function (response) {
          // keep it simple for now (instead of reseting all
          // variables reload the controller)
          $route.reload();

          Flash.set('Fastighetsbestånd har sparats', 'success');
        })
      .catch(new ErrorHandler('Misslyckades att spara fastighetsbestånd.'))
      .finally(function () {
        vm.saving = false;
        $location.search({});
        $location.path('/negotiations/' + $routeParams.negotiationId + '/agreements/' + $routeParams.id);
      });
  }
}
})();
(function () {
'use strict';

ConditionsModalCtrl.$inject = ["$uibModalInstance", "newRealEstate", "isReadyToSend"];
angular.module('hyresgastApp').controller('ConditionsModalCtrl', ConditionsModalCtrl);

function ConditionsModalCtrl($uibModalInstance,
  newRealEstate, isReadyToSend) {
  var vm = this;

  vm.newRealEstate = newRealEstate;
  vm.isReadyToSend = isReadyToSend;

  vm.confirm = function() {
    $uibModalInstance.close({
      isReadyToSend: vm.isReadyToSend,
      isPresumtiveRent: vm.isPresumtiveRent,
      isInvestmentProperty: vm.isInvestmentProperty,
    });
  };
  vm.cancel = function() { $uibModalInstance.dismiss('cancel') };
}

})();
(function () {
'use strict';

ConfirmationBlockedModalCtrl.$inject = ["$uibModalInstance", "settings"];
angular.module('hyresgastApp').controller('ConfirmationBlockedModalCtrl', ConfirmationBlockedModalCtrl);

function ConfirmationBlockedModalCtrl($uibModalInstance, settings) {
  var vm = this;
  vm.subtitle = settings.subtitle;
  vm.showDropdown = settings.showDropdown;
  vm.blockingAuthorityChoices = [
    {
      id: 0,
      name: 'Hyresnämnden'
    },
    {
      id: 1,
      name: 'HMK'
    },
    {
      id: 3,
      name: 'Skiljeman Hyresnämnd'
    },
    {
      id: 2,
      name: 'Egensatt'
    }
  ];

  vm.confirm = function() {
    $uibModalInstance.close(vm.blockedBy);
  };

  vm.cancel = function() {
    $uibModalInstance.dismiss('cancel');
  };
}

})();
(function () {
'use strict';

RecallItemsCtrl.$inject = ["$scope", "$uibModal", "$uibModalInstance", "Flash", "Negotiation", "negotiationId", "itemIds", "apartmentMode", "ErrorHandler"];
angular.module('hyresgastApp').controller('RecallItemsCtrl', RecallItemsCtrl);

function RecallItemsCtrl(
  $scope,
  $uibModal,
  $uibModalInstance,
  Flash,
  Negotiation,
  negotiationId,
  itemIds,
  apartmentMode,
  ErrorHandler
) {
  var vm = this;
  vm.cancel = cancel;
  vm.recallItems = recallItems;
  vm.apartmentMode = apartmentMode;

  function recallItems() {
    vm.saving = true;
    var item = vm.apartmentMode ? 'lägenhet' : 'fastighet';
    var plural = itemIds.length > 1 ? 'er' : '';

    Negotiation.recallItems({
      id: negotiationId,
      itemIds: itemIds
    })
      .$promise.then(function() {
        Flash.set(itemIds.length + ' ' + item + plural + ' har återkallats från förhandlingen', 'success');
      })
      .catch(ErrorHandler('Misslyckades att flytta ' + item + plural))
      .finally(function() {
        vm.saving = false;
        $uibModalInstance.close();
      });
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

WithoutRentListCtrl.$inject = ["$scope", "$location", "$uibModal", "$uibModalInstance", "withoutRentlist", "isNewlyConstructedApartment"];
angular.module('hyresgastApp').controller('WithoutRentListCtrl', WithoutRentListCtrl);

function WithoutRentListCtrl($scope, $location, $uibModal, $uibModalInstance, withoutRentlist, isNewlyConstructedApartment) {
  var vm = this;
  vm.withoutRentlist = withoutRentlist;
  vm.isNewlyConstructedApartment = isNewlyConstructedApartment;
  vm.close = close;

  function close() {
    $uibModalInstance.dismiss();
  }
}

})();
(function () {
'use strict';

ForgingRentlistModalCtrl.$inject = ["$scope", "negotiation", "negotiationRealEstates", "isNewProperty", "amend", "apartmentCount", "ManualChange", "Negotiation", "RealEstate", "ErrorHandler", "$uibModalInstance"];
angular.module('hyresgastApp').controller('ForgingRentlistModalCtrl', ForgingRentlistModalCtrl);

function ForgingRentlistModalCtrl(
  $scope,
  negotiation, negotiationRealEstates, isNewProperty, amend, apartmentCount,
  ManualChange, Negotiation, RealEstate, ErrorHandler,
  $uibModalInstance) {
  var vm = this;
  vm.rows = [];
  vm.negotiation = negotiation;
  vm.apartmentCount = apartmentCount;

  vm.selector = {
    incomingProperties: negotiationRealEstates,
    fgetProperties: RealEstate.queryPropertiesInMyNegotiations,
    fgetApartments: ManualChange.queryApartments,
  };

  vm.doneSelecting = () => {
    let rows = vm.selectedApartments.map(x => x.id);
    vm.busy = true;
    Negotiation.forgeRentList({
      amend,
      id: vm.negotiation.id,
      rows,
    }).$promise.then(result => {
      vm.busy = false;
      $uibModalInstance.close(result);
    }).catch(() => {
      new ErrorHandler('Misslyckades att välj lägenheter');
      $uibModalInstance.close();
    });
  };

  vm.cancel = () => { $uibModalInstance.dismiss('cancel'); };
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('ManualChange', ["$resource", function($resource) {
  return $resource(
    '/api/specops/manual-change', {},
    {
      queryProperty: {
        url: '/api/specops/manual-change/properties',
        method: 'GET'
      },
      queryPropertiesInMyNegotiations: {
        url: '/api/specops/manual-change/my-properties',
        method: 'GET'
      },
      queryPropertyWithApartments: {
        url: '/api/specops/manual-change/properties-with-apartments',
        method: 'GET'
      },
      queryPropertiesInMyNegotiationsWithApartments: {
        url: '/api/specops/manual-change/my-properties-with-apartments',
        method: 'GET'
      },
      queryApartments: {
        url: '/api/specops/manual-change/apartments',
        method: 'GET'
      },
    }
  );
}]);

})();
(function () {
'use strict';

MoveItemsCtrl.$inject = ["$scope", "$uibModal", "$uibModalInstance", "Negotiation", "NegotiationCall", "Flash", "currentNegotiation", "itemIds", "apartmentMode", "ErrorHandler"];
angular.module('hyresgastApp').controller('MoveItemsCtrl', MoveItemsCtrl);

function MoveItemsCtrl(
  $scope,
  $uibModal,
  $uibModalInstance,
  Negotiation,
  NegotiationCall,
  Flash,
  currentNegotiation,
  itemIds,
  apartmentMode,
  ErrorHandler
) {
  var vm = this;
  vm.parentCtrl = $scope.$parent;
  vm.cancel = cancel;
  vm.moveToNewNegotiation = false;
  vm.moveItems = moveItems;
  vm.currentNegotiation = currentNegotiation;
  vm.apartmentMode = apartmentMode;

  getNegotiationsByCallId();

  function getNegotiationsByCallId() {
    vm.ready = false;

    return Negotiation.query({
      id: currentNegotiation.id,
      'filters[callId]': currentNegotiation.callId
    })
      .$promise.then(function(data) {
        vm.targetNegotiations = data.rows;
        vm.targetNegotiations.push({ name: 'Flytta till ny förhandling', id: '_moveToNewNegotiation' });
      })
      .catch(ErrorHandler('Misslyckades att  hitta förhandlingar.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function moveItems() {
    var item = vm.apartmentMode ? 'Lägenhet' : 'Fastighet';
    var plural = itemIds.length > 1 ? 'er' : '';

    if (vm.selectedNegotiation.id === '_moveToNewNegotiation') {
      moveToNewNegotiation(item, plural);
    } else {
      moveToExistingNegotiation(item, plural);
    }
  }

  function moveToNewNegotiation(item, plural) {
    vm.saving = true;
    Negotiation.createNewNegotiationAndMoveItems({
      id: currentNegotiation.id,
      currentNegotiation: currentNegotiation,
      itemIds: itemIds
    })
      // eslint-disable-next-line no-unused-vars
      .$promise.then(function(response) {
        vm.saving = false;
        Flash.set(item + plural + ' har flyttats till ny förhandling', 'success');
      })
      .catch(ErrorHandler('Misslyckades att flytta ' + item + plural))
      .finally(function() {
        vm.saving = false;
        $uibModalInstance.close();
      });
  }

  function moveToExistingNegotiation(item, plural) {
    vm.saving = true;

    Negotiation.moveItems({
      id: currentNegotiation.id,
      newNegotiationId: vm.selectedNegotiation.id,
      itemIds: itemIds
    })
      .$promise.then(function() {
        Flash.set(item + plural + ' har flyttats', 'success');
      })
      .catch(ErrorHandler('Misslyckades att flytta ' + item + plural))
      .finally(function() {
        vm.saving = false;
        $uibModalInstance.close();
      });
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

ImportRentlistCtrl.$inject = ["$scope", "$uibModalInstance", "$timeout", "Negotiation", "ErrorHandler", "HyUtils", "negotiation", "amend"];
angular.module('hyresgastApp').controller('ImportRentlistCtrl', ImportRentlistCtrl);

function ImportRentlistCtrl(
  $scope, $uibModalInstance, $timeout,
  Negotiation, ErrorHandler, HyUtils,
  negotiation, amend
) {
  var vm = this;
  vm.negotiation = negotiation;
  vm.save = save;
  vm.showSaveButton = true;
  vm.showAdditionalInfoPane = false;
  vm.cancel = cancel;
  vm.showPage = 'Start';
  vm.goToNextPage = goToNextPage;
  vm.toggleAdditionalInfoPane = toggleAdditionalInfoPane;
  vm.onPending = onPending;
  vm.getColumnTitle = title => title instanceof Array ? title[0] : title.split(',')[0].trim();
  vm.status = {
    phase: 'read excel file',
    total: 0,
    errors: 0,
    warnings: 0,
    progress: 0,
    showProgress: false,
  };

  //if the list is uploaded take the summary of the validation response and show in the summary page.
  $scope.$watch('vm.validationResponse', function(validationResponse) {
    if (validationResponse) {
      vm.status = validationResponse;
      vm.validationExcelId = validationResponse.blobId;
      if (vm.validationResponse.status === 'ok') {
        vm.status.phase = 'Success';
        vm.status.comment = '';
        vm.inputRentListBlobId = vm.negotiation.rentListBlobId;

        vm.apartmentsSummary = {};
        vm.apartmentsSummary.apartments = validationResponse.summary.numApartments;
        vm.apartmentsSummary.municipalities = validationResponse.summary.numMunicipalities;
        vm.apartmentsSummary.properties = validationResponse.summary.numProperties;
      } else if (vm.validationResponse.status === 'error') {
        $uibModalInstance.dismiss();
        HyUtils.showDefaultModal('ErrorModalCtrl', 'resources/validation/errors.html', {
          validationResponse, realEstateChoiceMethod: null,
        });
      }
    }
  });

  ////////////

  function goToNextPage(page) {
    vm.showPage = page;
  }

  function save() {
    vm.saving = true;
    Negotiation.importRentList({
      id: vm.negotiation.id,
      rentListBlobId: vm.negotiation.rentListBlobId,
      amend,
    }).$promise.then(() => { $uibModalInstance.close('Hyreslistan har laddats upp till förhandlingen.'); })
      .catch(new ErrorHandler('Misslyckades att ladda upp hyreslistan.'))
      .finally(() => { vm.saving = false; });
  }

  function cancel() {
    vm.negotiation.rentListBlobId = null;
    $uibModalInstance.dismiss();
  }

  function toggleAdditionalInfoPane() {
    vm.showAdditionalInfoPane = !vm.showAdditionalInfoPane;
  }

  function onPending(validation) {
    if (vm.status.showProgress && vm.status.progress > 0 && vm.status.phase !== validation.phase) {
      vm.status.progress = 100;
      $timeout(() => {
        vm.status.progress = 0;
        vm.status.phase = validation.phase;
        vm.status.comment = validation.comment;
        $timeout(() => vm.status = validation, 500);
      }, 300);

    } else {
      vm.status = validation;
    }
  }
}

})();
(function () {
'use strict';

NegotiationTableNegotiatorListCtrl.$inject = ["$scope", "$location", "$routeParams", "$uibModal", "FeatureConfiguration", "Organization", "SubPage", "User", "Auth", "ErrorHandler"];
angular.module('hyresgastApp').controller('NegotiationTableNegotiatorListCtrl', NegotiationTableNegotiatorListCtrl);

function NegotiationTableNegotiatorListCtrl(
  $scope, $location, $routeParams, $uibModal,
  FeatureConfiguration, Organization, SubPage, User,
  Auth, ErrorHandler
) {
  var vm = this;
  vm.gotoShow = gotoShow;
  vm.addMeeting = addMeeting;
  vm.pageLimit = 30;
  vm.pageOffset = 0;
  vm.Auth = Auth;

  vm.negotiationUserId = $routeParams.partnerId;
  vm.FeatureConfiguration = FeatureConfiguration;

  vm.tablist = [
    {
      title: 'Förhandlingar',
      path: '/users/' + $routeParams.id + '/negotiator/' + $routeParams.partnerId + '/organization/' + $routeParams.organizationId + '/welcome/negotiation-table/negotiator?view=list'
    },
    {
      title: 'Fastigheter',
      path:
        '/users/' +
        $routeParams.id +
        '/negotiator/' +
        $routeParams.partnerId +
        '/organization/' +
        $routeParams.organizationId +
        '/welcome/negotiation-table/negotiator?view=properties'
    },
    {
      title: 'Avslutat',
      path:
         '/users/' +
          $routeParams.id +
          '/negotiator/' +
          $routeParams.partnerId +
          '/organization/' +
          $routeParams.organizationId +
          '/welcome/negotiation-table/negotiator?view=finished'
    }
  ];

  if (vm.FeatureConfiguration.ENABLE_MEETINGS) {
    vm.tablist.splice(2, 0, {
      title: 'Möten',
      path:
          '/users/' +
          $routeParams.id +
          '/negotiator/' +
          $routeParams.partnerId +
          '/organization/' +
          $routeParams.organizationId +
          '/welcome/negotiation-table/negotiator?view=meetings'
    });
  }

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
    }
  });

  $scope.$on('$routeUpdate', function() {
    // avoid unecessary calling of getCurrentSubpage(); when sorting
    if (!$location.search().order) {
      // reset offset when sorting
      getCurrentSubpage();
    }
  });

  getCurrentSubpage();
  getUser();
  getOrganization();

  /////////

  function getOrganization() {
    return Organization.get({ id: $routeParams.organizationId })
      .$promise.then(function(response) {
        vm.organization = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta organisation.'))
  }

  function getUser() {
    return User.queryNegotiationPartners({ id: $routeParams.partnerId })
      .$promise.then(function(response) {
        vm.user = response.rows.filter(function(user) {
          return user.id === Number($routeParams.partnerId)
        })[0];

        if (!vm.user) {
          throw new Error("User not found");
        }
      })
      .catch(ErrorHandler('Misslyckades att hämta användaren.'))
      .finally(function() {
        vm.userReady = true;
      });
  }

  function gotoShow(id) {
    $location.search({});
    $location.path('/negotiations/' + id);
  }

  function getCurrentSubpage() {
    var defaultSubPage = 'list';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = ['list', 'properties', 'finished'];
    if (vm.FeatureConfiguration.ENABLE_MEETINGS) {
      whiteList.push('meetings');
    }
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }

  function addMeeting() {
    // fake object in order to pass two users
    var userObj = {
      externalParticipants: [],
      hgfParticipants: [Auth.currentUser(), vm.user]
    };
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'lg',
      index: 1075, // position above tooltip
      templateUrl: 'resources/meeting/edit/edit-meetings-modal.html',
      controller: 'EditMeetingModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      backdrop: 'static',
      resolve: {
        meetingObj: function() {
          return null;
        },
        negotiationObj: function() {
          return userObj;
        }
      }
    });
    // eslint-disable-next-line no-unused-vars
    modalInstance.result.then(function(meeting) {});
  }
}

})();
(function () {
'use strict';

ListAgreementApartmentsCtrl.$inject = ["$scope", "$routeParams", "$location", "$timeout", "Agreement", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListAgreementApartmentsCtrl', ListAgreementApartmentsCtrl);

function ListAgreementApartmentsCtrl($scope, $routeParams, $location, $timeout,
  Agreement, Flash, ErrorHandler) {
  var vm = this;
  vm.apartmentsPageLimit = 30;
  vm.apartmentsPageOffset = 0;

  vm.delete = destroy;
  vm.getApartments = getApartments;
  vm.getMonthlyRent = getMonthlyRent;
  vm.getNewMonthlyRent = getNewMonthlyRent;
  vm.getChangeDescription = getChangeDescription;

  vm.listColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Fastighet', column: 'property.propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'Ingående hyra (kr/månad)', column: 'currentMonthlyRent' }
  ];
  vm.sortColumn = vm.listColumns.some(function(c) {
    return c.column === $location.search().order;
  })
    ? $location.search().order
    : 'property.propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.apartmentsPageOffset = 0;
      getApartments();
    }
  });

  getApartments();

  /////////

  function destroy(apartment) {
    // if wizard is open close it so it will updated
    // with correct list when re-opened
    $scope.$parent.vm.editApartmentMode = false;
    vm.deletingApartment = true;

    let params = {
      negotiationId: $routeParams.negotiationId,
      agreementId: $routeParams.id,
      apartmentsIds: apartment ? [apartment.id] : getIdsFromTicked(vm.selectedApartments),
    };

    Agreement.deleteApartment(params).$promise.then(() => {
      vm.selectedApartments = {};
      vm.apartments = vm.apartments.filter(x => !params.apartmentsIds.includes(x.id));
      vm.apartmentsPageOffset = 0;
      vm.getApartmentsVirtual();
      vm.apartmentsTotalCount -= params.apartmentsIds.length;
      $scope.$parent.vm.agreement.numApartments = vm.apartmentsTotalCount;
      // i.e interested in notAssignedCount in negotiations
      // object to know if to show or hide buttons
      $scope.$parent.vm.getNegotiation($routeParams.negotiationId);
      vm.initialTotalCount = vm.apartmentsTotalCount;
      if (vm.initialTotalCount === 0) {
        $scope.$parent.vm.editApartmentMode = true;
      }
      Flash.set('Lyckades ta bort lägenhet', 'success');
    }).catch(new ErrorHandler('Misslyckades att ta bort fastighet.'))
      .finally(() => { vm.deletingApartment = false });
  }

  vm.getApartmentsVirtual = () => {
    vm.apartmentsVirtual = vm.apartments.slice(vm.apartmentsPageOffset, vm.apartmentsPageOffset + vm.apartmentsPageLimit);
  };
  function getApartments(filterMode) {
    vm.readyApartments = false;
    return Agreement.queryApartment({
      negotiationId: $routeParams.negotiationId,
      id: $routeParams.id,
      limit: 2000,
      offset: 0,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'search[nationalNo]': vm.inputTextNationalApartmentNo,
      'search[ownerNo]': vm.inputTextApartmentNo,
      'search[streetAddress]': vm.inputTextStreetAddress,
      'search[property.propertyDesignation]': vm.inputTextPropertyDesignation,
      'search[municipality]': vm.inputTextMunicipality
    })
      .$promise.then(function(response) {
        let agreement = $scope.$parent.vm.agreement;
        vm.isSigningStarted = agreement
          ? agreement.scriveId && (agreement.scriveId !== 'bubkis')
          : false;
        vm.apartments = response.rows;
        vm.apartmentsTotalCount = response.count;
        // Save initial count of records, to be distingueshed from the current count after
        // unsuccessful filtering. This will affect all messages, which are dependent on totalCount = 0.
        if (filterMode) {
          vm.apartmentsPageOffset = 0;
        } else {
          vm.initialTotalCount = vm.apartmentsTotalCount;
        }
        // open the wizard when apartments' list is empty
        if (vm.initialTotalCount === 0) {
          $scope.$parent.vm.editApartmentMode = true;
        }

        vm.selectedApartments = createTickedObject(vm.apartments, false);
        vm.toggledAll = false;
        vm.getApartmentsVirtual();
      })
      .catch(ErrorHandler('Misslyckades att hämta fastighetsbestånd.'))
      .finally(function() {
        vm.readyApartments = true;
      });
  }

  function getMonthlyRent(apartment) {
    if (apartment.currentMonthlyRent) {
      return apartment.currentMonthlyRent;
    } else {
      return parseFloat(apartment.currentYearlyRent / 12).toFixed(2);
    }
  }

  function getNewMonthlyRent(apartment) {
    if (apartment.newMonthlyRent) {
      return apartment.newMonthlyRent;
    } else {
      return apartment.newYearlyRent / 12;
    }
  }

  function getChangeDescription(apartment) {
    if (apartment.changeDescription) {
      return apartment.changeDescription;
    } else {
      const monthlyRent = getMonthlyRent(apartment);
      const diff = getNewMonthlyRent(apartment) - monthlyRent;
      const increasePercentage = diff === 0 ? 0 : diff / monthlyRent * 100;
      const numberParsed = parseFloat(increasePercentage).toFixed(2);
      return numberParsed + '%';
    }
  }

  //TODO: flytta til fe-common
  /**
   * converts ticked object to array of ids
   * @param {Object} ticked
   * @returns {Array<number>}
   */
  const getIdsFromTicked = ticked => Object.keys(ticked).filter(k => ticked[k]).map(id => parseInt(id));

  /**
   * creates ticked object from collection
   * @param {Array} rows
   * @param {boolean} [initialValue]
   * @returns {Object}
   */
  const createTickedObject = (rows, initialValue = true) => rows.reduce((acc, el) => {
    acc[el.id] = initialValue;
    return acc;
  }, {});

  vm.hasSelectedApartments = () => vm.selectedApartments
    ? getIdsFromTicked(vm.selectedApartments).length
    : false;
  vm.toggleApartment = () => {
    $timeout(() => {
      vm.toggledAll = getIdsFromTicked(vm.selectedApartments).length === vm.apartments.length;
    }, 500);
  };
  vm.toggleAllApartments = () => {
    if (getIdsFromTicked(vm.selectedApartments).length < vm.apartments.length) {
      vm.selectedApartments = createTickedObject(vm.apartments);
    } else {
      vm.selectedApartments = {};
    }
  };
}

})();
(function () {
'use strict';

EditAgreementApartmentCtrl.$inject = ["$scope", "$location", "$route", "$routeParams", "$document", "Agreement", "Negotiation", "Flash", "ErrorHandler", "FPConsts", "CommonIds", "Limits"];
angular.module('hyresgastApp').controller('EditAgreementApartmentCtrl', EditAgreementApartmentCtrl);

function EditAgreementApartmentCtrl(
  $scope, $location, $route, $routeParams, $document,
  Agreement, Negotiation,
  Flash, ErrorHandler,
  FPConsts, CommonIds, Limits) {
  var vm = this;

  vm.Limits = Limits;
  vm.validStatuses = FPConsts.apartmentStatusesForNegotation;

  vm.agreement = {
    negotiationId: $routeParams.negotiationId,
    id: $routeParams.id,
    documents: [],
    name: '', // there is a not null constraing on api
    isAllDistrictsSelected: true,
    isAllPropertiesSelected: true,
    isAllApartmentsSelected: true
  };

  vm.listPropertiesColumns = [
    { title: 'Fastigheter', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments', filterType: 'none' },
    { title: 'Fastighetsägare', column: 'owners', type: 'array' }
  ];
  vm.listApartmentsColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Yta', column: 'livingSpace' },
    { title: 'Antal Rum', column: 'rooms' },
    { title: 'Aktuell hyra (kr/månad)', column: 'currentMonthlyRent', template: 'hyra' },
    { title: 'Fastighet', column: 'property.propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    {
      title: 'Status',
      column: 'status',
      template: 'status',
      lookup: {
        idProperty: 'code',
        textProperty: 'longName',
        dictionary: vm.validStatuses,
      },
      ffn: (value, filter) => [(value & filter.code), CommonIds.tableauAllId].includes(filter.code),
    },
    { title: 'Komment', column: 'comment', template: 'comment', filterType: 'none' },
  ];

  vm.gotoNextStep = () => { vm.gotoNext = true }
  vm.save = save;

  vm.propertiesPageLimit = 30;
  vm.apartmentsPageLimit = 30;

  let refreshTableau = id => angular.element($document[0].querySelector("#"+id)).scope().$broadcast('refresh');

  $scope.$on('$routeUpdate', function() {
    if ($location.search().step === 'apartments') {
      refreshTableau('tableauApartments');
    }
  });

  ////////////
  getNegotiation();

  function getNegotiation() {
    return Negotiation.get({
      id: $routeParams.negotiationId
    })
      .$promise.then(function(response) {
        vm.negotiation = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function() {
        vm.readyNegotiation = true;
      });
  }

  vm.fGetProperties = () => {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      Negotiation.queryPropertyBySelection({ id: $routeParams.negotiationId }, {
        unassigned: true,
        selectedDistrictIds: vm.agreement.selectedDistrictIds,
        isAllDistrictsSelected: true
      }).$promise.then(function(response) {
        vm.properties = response.rows;
        resolve(response);
      }).catch(ErrorHandler('Misslyckades att hämta fastigheter.'))
    });
  };

  let packEntity = (selectedArray, array) => {
    let selectedIds = array.reduce((acc, el) => {
      acc[el.id] = selectedArray.some(x => x.id === el.id);
      return acc;
    }, {});
    let isAllSelected = array.length === selectedArray.length;
    return { selectedIds, isAllSelected };
  };

  vm.fGetApartments = () => {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      if ($location.search().step !== 'apartments') {
        resolve({ rows: [], count: 0 });
        return;
      }
      let pack = packEntity(vm.selectedProperties, vm.properties);
      Negotiation.queryApartment({
        id: $routeParams.negotiationId,
        unassigned: true
      }, {
        isAllDistrictsSelected: true,
        selectedPropertyIds: pack.selectedIds,
        isAllPropertiesSelected: pack.isAllSelected,
      }).$promise.then(function(response) {
        vm.apartments = response.rows;
        resolve(response);
      }).catch(ErrorHandler('Misslyckades att hämta lägenheter.'));
    });
  };

  function save() {
    vm.saving = true;
    let pack = packEntity(vm.selectedApartments, vm.apartments);
    let payload = Object.assign(vm.agreement, {
      isAllApartmentsSelected: false,
      selectedApartmentIds: pack.selectedIds,
    });
    // eslint-disable-next-line no-unused-vars
    Agreement.saveApartment(payload).$promise.then(response => {
      $route.reload();
      Flash.set('Fastighetsbestånd har sparats', 'success');
    }).catch(ErrorHandler('Misslyckades att spara fastighetsbestånd.'))
      .finally(() => {
        vm.saving = false;
        $location.search({});
        $location.path('/negotiations/' + $routeParams.negotiationId + '/agreements/' + $routeParams.id);
      });
  }
}
})();
(function () {
'use strict';

NegotiationTableNegotiatorCurrentListCtrl.$inject = ["$location", "$routeParams", "$filter", "FeatureConfiguration", "CombinedNegotiationStates", "NegotiationTypes", "HyUtils", "User", "Negotiation"];
angular
  .module('hyresgastApp')
  .controller('NegotiationTableNegotiatorCurrentListCtrl', NegotiationTableNegotiatorCurrentListCtrl);

function NegotiationTableNegotiatorCurrentListCtrl(
  $location, $routeParams, $filter,
  FeatureConfiguration, CombinedNegotiationStates, NegotiationTypes,
  HyUtils,
  User, Negotiation
) {
  var vm = this;
  vm.FeatureConfiguration = FeatureConfiguration;

  vm.statusChoices = Object.values(CombinedNegotiationStates).map(x => ({
    id: x,
    text: $filter('hyCombinedNegotiationStates')(x)
  }));
  vm.negotiationTypeChoices = Object.values(NegotiationTypes).map(x => ({
    id: x,
    text: $filter('hyNegotiationTypes')(x)
  }));
  vm.columns = [
    { title: 'Id', column: 'caseNumber' },
    { title: 'Namn', column: 'negotiationName' },
    {
      title: 'Förhandlingstyp',
      template: 'slot1',
      column: 'type',
      lookup: {
        idProperty: 'id',
        textProperty: 'text',
        dictionary: vm.negotiationTypeChoices,
        multi: true,
      },
    },
    { title: 'Datum för framställan', column: 'createdAt' },
    {
      title: 'Status',
      template: 'status',
      column: 'combinedState',
      lookup: {
        idProperty: 'id',
        textProperty: 'text',
        dictionary: vm.statusChoices,
        multi: true,
      },
    },
    { title: 'Önskad hyresändring', column: 'rentEffectiveFrom' }
  ];
  if (vm.FeatureConfiguration.ENABLE_MEETINGS) {
    vm.listColumns.splice(4, 0, { title: 'Nästa möte', column: 'updatedAt' });
  }

  vm.gotoNegotiation = function (negotiation) {
    $location.search({});
    $location.path('/negotiations/' + negotiation.id);
  }

  vm.getData = function (params) {
    // eslint-disable-next-line no-undef, no-unused-vars
    return new Promise((resolve, reject) => {
      User.queryNegotiationsWithMe(Object.assign({
        partnerId: $routeParams.partnerId,
        'filters[counterpartOrganizationId]': $routeParams.organizationId,
      }, params)).$promise.then(function(data) {
        //TODO: replace with column.type = 'date'
        data.rows.forEach(x => {
          x.rentEffectiveFrom = $filter('date')(x.rentEffectiveFrom, 'yyyy-MM-dd');
          x.createdAt = $filter('date')(x.createdAt, 'yyyy-MM-dd');
        });
        resolve(data);
      });
    });
  }

  vm.editComment = negotiation => {
    HyUtils.showDefaultModal('EditCommentModalCtrl',
      'resources/agreement/rentlist/edit/edit-comment-modal.html',
      { comment: negotiation.comment, multipleComments: '' },
      result => {
        if (result.delete || result.comment) {
          negotiation.comment = result.delete ? '' : result.comment;
          Negotiation.saveComments([{
            negotiationId: negotiation.id,
            comment: negotiation.comment,
          }]);
          vm.isDirty = true;
        }
      }, null, null,
      { size: 'md' });
  };

  vm.getContextInfo = row => ({
    title: `Förhandling ${row.caseNumber}`,
    link: '/negotiations/'+row.id,
  });
}

})();
(function () {
'use strict';

NegotiationTableNegotiatorPropertiesListCtrl.$inject = ["$location", "$routeParams", "FeatureConfiguration", "NegotiationStates", "RealEstate", "Auth"];
angular
  .module('hyresgastApp')
  .controller('NegotiationTableNegotiatorPropertiesListCtrl', NegotiationTableNegotiatorPropertiesListCtrl);

function NegotiationTableNegotiatorPropertiesListCtrl(
  $location, $routeParams,
  FeatureConfiguration, NegotiationStates, RealEstate,
  Auth
) {
  var vm = this;
  vm.FeatureConfiguration = FeatureConfiguration;
  vm.isHgfUser = Auth.isHgfUser();

  vm.columns = [
    { title: 'Fastighetsbeteckning', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Lägenheter jag förhandlar', column: 'numNegotiatedApartments', template: 'slot1' }
  ];
  if (vm.FeatureConfiguration.ENABLE_MEETINGS) {
    vm.listColumns.splice(4, 0, { title: 'Nästa möte', column: 'updatedAt' });
  }

  vm.gotoProperty = property => {
    if (Auth.isHgfUser()) {
      $location.search({});
      $location.path('/properties/' + property.id);
    }
  }

  vm.getData = function (params) {
    // eslint-disable-next-line no-undef, no-unused-vars
    return new Promise((resolve, reject) => {
      params = Object.assign({
        partnerId: $routeParams.partnerId,
        'filters[state]': [
          NegotiationStates.IN_PROGRESS,
          NegotiationStates.NEW,
          NegotiationStates.SETTLEMENT,
          NegotiationStates.IN_PREPARATION,
          NegotiationStates.BLOCKED,
          NegotiationStates.PARTIALLY_BLOCKED
        ]
      }, params);
      RealEstate.queryPropertiesInNegotiationsWithMe(params, {})
        .$promise.then(function(data) { resolve(data) });
    });
  }

  vm.getContextInfo = row => ({
    title: `Fastighet ${row.propertyDesignation}`,
    link: '/properties/'+row.id,
  });
}

})();
(function () {
'use strict';

NegotiationTableNegotiatorMeetingListCtrl.$inject = ["$scope", "$location", "$routeParams", "Flash", "Meeting", "NegotiationStates", "SubPage", "ErrorHandler"];
angular
  .module('hyresgastApp')
  .controller('NegotiationTableNegotiatorMeetingListCtrl', NegotiationTableNegotiatorMeetingListCtrl);

function NegotiationTableNegotiatorMeetingListCtrl(
  $scope,
  $location,
  $routeParams,
  Flash,
  Meeting,
  NegotiationStates,
  SubPage,
  ErrorHandler
) {
  var vm = this;

  vm.gotoShow = gotoShow;
  vm.pageLimit = 30;
  vm.pageOffset = 0;
  vm.sortColumn = $location.search().order || 'caseNumber'; // default
  vm.sortDirection = $location.search().direction || 'DESC'; // default
  vm.listColumns = [
    { title: 'Id', column: 'caseNumber' },
    { title: 'Namn', column: 'name' },
    { title: 'Typ', column: 'counterpart.name' },
    { title: 'Dagar kvar', column: 'rentEffectiveFrom' },
    { title: 'Nästa möte', column: 'updatedAt' },
    { title: 'Datum för framställan', column: 'updatedAt' },
    { title: 'Status', column: 'state' },
    { title: 'Begärt ändringsdatum', column: 'updatedAt' }
  ];

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getMeetingsWithMe();
    }
  });

  /////////
  getCurrentSubpage();
  getMeetingsWithMe();

  function gotoShow(id) {
    $location.search({});
    $location.path('/negotiations/' + id).search('view', 'meetings');
  }

  function getMeetingsWithMe() {
    vm.readyMeetings = false;

    return Meeting.queryMeetingsWithMe({
      partnerId: $routeParams.partnerId,
      limit: vm.pageLimit,
      offset: vm.pageOffset,
      order: vm.sortColumn,
      direction: vm.sortDirection
    })
      .$promise.then(function(data) {
        vm.myMeetings = data.rows;
        vm.totalCount = data.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta lista.'))
      .finally(function() {
        vm.readyMeetings = true;
      });
  }

  function getCurrentSubpage() {
    var defaultSubPage = 'list';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = ['meetings', 'properties', 'finished'];
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }
}

})();
(function () {
'use strict';

NegotiationTableNegotiatorFinishedListCtrl.$inject = ["$scope", "$location", "$routeParams", "$filter", "FeatureConfiguration", "CombinedNegotiationStates", "NegotiationStates", "NegotiationTypes", "SubPage", "User"];
angular
  .module('hyresgastApp')
  .controller('NegotiationTableNegotiatorFinishedListCtrl', NegotiationTableNegotiatorFinishedListCtrl);

function NegotiationTableNegotiatorFinishedListCtrl(
  $scope, $location, $routeParams, $filter,
  FeatureConfiguration, CombinedNegotiationStates, NegotiationStates, NegotiationTypes, SubPage, User
) {
  var vm = this;
  vm.FeatureConfiguration = FeatureConfiguration;

  vm.statusChoices = Object.values(CombinedNegotiationStates).map(x => ({
    id: x,
    text: $filter('hyCombinedNegotiationStates')(x)
  }));
  vm.negotiationTypeChoices = Object.values(NegotiationTypes).map(x => ({
    id: x,
    text: $filter('hyNegotiationTypes')(x)
  }));
  vm.columns = [
    { title: 'Id', column: 'caseNumber' },
    { title: 'Namn', column: 'negotiationName' },
    {
      title: 'Förhandlingstyp',
      template: 'slot1',
      column: 'type',
      lookup: {
        idProperty: 'id',
        textProperty: 'text',
        dictionary: vm.negotiationTypeChoices,
        multi: true,
      },
    },
    { title: 'Hyresändring', column: 'createdAt' },
    {
      title: 'Status',
      template: 'status',
      column: 'combinedState',
      lookup: {
        idProperty: 'id',
        textProperty: 'text',
        dictionary: vm.statusChoices,
        multi: true,
      },
    },
    { title: 'Senast ändrad', column: 'rentEffectiveFrom' }
  ];
  if (vm.FeatureConfiguration.ENABLE_MEETINGS) {
    vm.listColumns.splice(4, 0, { title: 'Nästa möte', column: 'updatedAt' });
  }

  vm.gotoNegotiation = function (negotiation) {
    $location.search({});
    $location.path('/negotiations/' + negotiation.id);
  }

  vm.getData = function (params) {
    // eslint-disable-next-line no-undef, no-unused-vars
    return new Promise((resolve, reject) => {
      User.queryNegotiationsWithMe(Object.assign({
        partnerId: $routeParams.partnerId,
        'filters[counterpartOrganizationId]': $routeParams.organizationId,
        'filters[state]': [NegotiationStates.FINISHED],
      }, params)).$promise.then(function(data) {
        //TODO: replace with column.type = 'date'
        data.rows.forEach(x => {
          x.rentEffectiveFrom = $filter('date')(x.rentEffectiveFrom, 'yyyy-MM-dd');
          x.createdAt = $filter('date')(x.createdAt, 'yyyy-MM-dd');
        });
        resolve(data);
      });
    });
  }

  vm.getContextInfo = row => ({
    title: `Förhandling ${row.caseNumber}`,
    link: '/negotiations/'+row.id,
  });
}

})();
(function () {
'use strict';

function deepEqual(firstObject, secondObject) {
  for(var prop in firstObject) {
    // eslint-disable-next-line eqeqeq
    if (firstObject[prop] != secondObject[prop])
      return false;
  }
  return true;
}
Array.prototype.deepIncludes = function(needle) {
  for(var i = 0; i < this.length; i++) {
    var el = this[i];
    if (deepEqual(needle, el)) {
      return true;
    }
    return false;
  }
};

Array.prototype.deepCopy = function() {
  var result = [];
  for(var i = 0; i < this.length; i++) {
    result.push(Object.assign({}, this[i]));
  }
  return result;
}

Array.prototype.unique = function() {
  var arr = [];
  for(var i = 0; i < this.length; i++) {
    if(!arr.deepIncludes(this[i])) {
      arr.push(this[i]);
    }
  }
  return arr;
};

/**
 * Gives intersection between this and given array
 * @param {Array.<Object>} otherArray
 */
Array.prototype.intersect = function(otherArray) {
  return this.filter(value => -1 !== otherArray.indexOf(value));
};

/**
 * checks if otherArray is contained in given array
 * @param {Array.<Object>} otherArray
 */
Array.prototype.containsArray = function(otherArray) {
  if (otherArray.length === 0) {
    return false;
  }
  return this.intersect(otherArray).length === otherArray.length;
}

})();
(function () {
/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
/* eslint-disable no-undefined */
/* eslint-disable strict */

if ('NodeList' in window && !NodeList.prototype.forEach) {
  NodeList.prototype.forEach = function (callback, thisArg) {
    thisArg = thisArg || window;
    for (var i = 0; i < this.length; i++) {
      callback.call(thisArg, this[i], i, this);
    }
  };
}

if (!Array.prototype.find) {
  Array.prototype.find = function(predicate) {
    if (this == null) {
      throw new TypeError('Array.prototype.find called on null or undefined');
    }
    if (typeof predicate !== 'function') {
      throw new TypeError('predicate must be a function');
    }
    var list = Object(this);
    var length = list.length >>> 0;
    var thisArg = arguments[1];
    var value;

    for (var i = 0; i < length; i++) {
      value = list[i];
      if (predicate.call(thisArg, value, i, list)) {
        return value;
      }
    }
    return undefined;
  };
}

if (!Object.values) {
  Object.values = function(o) {
    return Object.keys(o).map(function(k) {
      return o[k];
    });
  }
}

if (!String.prototype.startsWith) {
  Object.defineProperty(String.prototype, 'startsWith', {
    value: function(search, rawPos) {
      var pos = rawPos > 0 ? rawPos|0 : 0;
      return this.substring(pos, pos + search.length) === search;
    }
  });
}

if (typeof Object.assign !== 'function') {
  // Must be writable: true, enumerable: false, configurable: true
  Object.defineProperty(Object, "assign", {
    value: function assign(target, varArgs) { // .length of function is 2
      'use strict';
      if (target === null || target === undefined) {
        throw new TypeError('Cannot convert undefined or null to object');
      }

      var to = Object(target);

      for (var index = 1; index < arguments.length; index++) {
        var nextSource = arguments[index];

        if (nextSource !== null && nextSource !== undefined) {
          for (var nextKey in nextSource) {
            // Avoid bugs when hasOwnProperty is shadowed
            if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
              to[nextKey] = nextSource[nextKey];
            }
          }
        }
      }
      return to;
    },
    writable: true,
    configurable: true
  });
}


Array.prototype.flat || Object.defineProperty(Array.prototype, "flat", {
  configurable:!0, value:function r() {
    var t = isNaN(arguments[0]) ? 1:Number(arguments[0]);return t?Array.prototype.reduce.call(this, function(a, e) {
      return Array.isArray(e)?a.push.apply(a, r.call(e, t-1)):a.push(e), a
    }, []):Array.prototype.slice.call(this)
  }, writable:!0}), Array.prototype.flatMap||Object.defineProperty(Array.prototype, "flatMap", {
  configurable:!0, value:function(r) {
    return Array.prototype.map.apply(this, arguments).flat()
  }, writable:!0})
})();
(function () {
'use strict';

angular.module('exportUtils', []).factory('ExportUtils', ExportUtils);

function ExportUtils() {
  return {
    prettyExcel: (sheetData) => {
      sheetData = sheetData.map(d => {
        Object.keys(d).forEach(key => {
          // eslint-disable-next-line no-undefined
          if (d[key] === undefined) {
            d[key] = ' ';
          }
        });
        return d;
      });
      let columnNames = Object.keys(sheetData[0]);
      // eslint-disable-next-line no-undef
      let xlsx = XLSX;
      let workbook = xlsx.utils.book_new();
      let sheet = xlsx.utils.json_to_sheet(sheetData, { header: columnNames });
      const colLengths = Object.keys(sheetData[0]).map(k => k.toString().length);
      for (const d of sheetData) {
        Object.values(d).forEach((element, index) => {
          colLengths[index] = Math.max(element.toString().length, colLengths[index]);
          colLengths[index] = Math.min(colLengths[index], 100);
        });
      }
      sheet["!cols"] = colLengths.map((l) => ({ wch: l }));

      const range = xlsx.utils.decode_range(sheet['!ref']);


      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          let cell_address = { c: C, r: R };
          const cell_ref = xlsx.utils.encode_cell(cell_address);
          if (!sheet[cell_ref]) continue;

          let topRow = (R === 0);

          sheet[cell_ref].s = {
            fill: { fgColor: { rgb: topRow ? 'C6EFCE' : 'FFFFFF' } },
            font: { bold: topRow },
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } }
            }
          };
        }
      }


      xlsx.utils.book_append_sheet(workbook, sheet, 'DATA');
      let output = xlsx.write(workbook, {type: 'buffer'});
      return output;
    },
  }
}
})();
(function () {
'use strict';

FPConsts.$inject = ["ApartmentStatusTypes"];
angular.module('fpConsts', [])
  .factory('FPConsts', FPConsts);

function FPConsts(ApartmentStatusTypes) {
  return {
    apartmentStatusesForNegotation: [
      ApartmentStatusTypes.PRESUMTIVE_RENT,
      ApartmentStatusTypes.STUCK,
      ApartmentStatusTypes.WITHOUT_NEGOTIATION,
      ApartmentStatusTypes.TRIBE_DECISION,
      ApartmentStatusTypes.M10,
      ApartmentStatusTypes.EG,
      ApartmentStatusTypes.B
    ],
    agreeableApartmentStatuses: [
      ApartmentStatusTypes.STUCK,
      ApartmentStatusTypes.WITHOUT_NEGOTIATION,
      ApartmentStatusTypes.TRIBE_DECISION,
      ApartmentStatusTypes.B
    ],
    attachmentLimits: {
      numWarning: 5,
      sizeWarning: 20 * 1024 * 1024,
    },
  };
}
})();
(function () {
'use strict';

angular.module('hyresgastApp').filter('size', function() {
  return function(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
});

})();
(function () {
'use strict';

//TODO: flytta til fe+common

angular.module('hyresgastApp').filter('hyStatusString', ["FPConsts", function(FPConsts) {
  return function(value, statusSet) {
    let validStatusTypes = [];

    switch (statusSet) {
      // statuses, that are relevant in negotiation - all, excluding F and M
      case 1:
        validStatusTypes = FPConsts.apartmentStatusesForNegotation;
        break;
    }

    return validStatusTypes
      .filter(s => (value & s.code))
      .map(s => s.colouredName || s.name)
      .join(', ');
  };
}]);


})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Artifacts', ["$resource", function($resource) {
  return $resource(
    '/api/artifacts',
    { id: '@id', uuid: '@uuid' },
    {
      list: {
        url: '/api/artifacts',
        isArray: true,
        method: 'GET',
      },
      download: {
        url: '/api/artifacts/:uuid',
        method: 'GET',
        responseType: 'arraybuffer',
        transformResponse: (data, headers) => ({ data, headers })
      },
      destroy: {
        url: '/api/artifacts',
        method: 'DELETE',
      },
    }
  );
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Microblog', ["$resource", function($resource) {
  return $resource(
    '/api/blog',
    { id: '@id' },
    {
      list: {
        url: '/api/blog',
        isArray: false,
        method: 'GET',
      },
      create: {
        url: '/api/blog',
        isArray: false,
        method: 'POST',
      },
      update: {
        url: '/api/blog/edit',
        method: 'POST',
      },
      destroy: {
        url: '/api/blog',
        method: 'DELETE',
      },
    }
  );
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Seenby', ["$resource", function($resource) {
  return $resource(
    '/api/seenby', {
      objectType: '@objectType',
      objectId: '@objectId',
    }, {
      // POST /api/seenby/:objectType/:objectId
      set: {
        url: '/api/seenby/:objectType/:objectId',
        method: 'POST',
      },
      // POST /api/seenby/:objectType
      markAsRead: {
        url: '/api/seenby/:objectType',
        method: 'POST',
      }
    }
  );
}]);

})();
(function () {
'use strict';

hyArtifacts.$inject = ["$timeout", "$location", "Artifacts", "HyUtils", "ValidFileTypes", "CommonMessages", "Auth"];
angular.module('hyresgastApp').directive('hyArtifacts', hyArtifacts);

function hyArtifacts($timeout, $location,
  Artifacts,
  HyUtils,
  ValidFileTypes, CommonMessages,
  Auth ) {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/artifacts/artifacts.html',
    scope: {
      objectId: '=',
      objectType: '=',
      fileLabels: '=',
      group: '=',
      count: '=?',
    },
    // eslint-disable-next-line no-unused-vars
    link: (scope, element, attrs) => {

      scope.Auth = Auth;
      scope.artifacts = [];

      let sortArtifacts = function() {
        let maybeInvert = result => scope.sortDirection === 'ASC' ? -result : result;
        let fn;
        switch (scope.sortColumn) {
          case scope.sortColumns[0].column:
            fn = (a, b) => maybeInvert(b.date - a.date);
            break;
          default:
          case scope.sortColumns[1].column:
            fn = (a, b) => maybeInvert(('' + a.label.name).localeCompare(b.label.name) || (b.date - a.date));
            break;
        }
        scope.artifacts.sort(fn);
        scope.count = scope.artifacts.length;
      };

      scope.sortColumns = [
        {
          title: 'Datum',
          column: 'S1'
        },
        {
          title: 'Typ och datum',
          column: 'S2'
        }
      ];
      scope.sortColumn = scope.sortColumns[1].column;
      scope.sortDirection = 'DESC';
      scope.$on('$routeUpdate', () => {
        if ($location.search().order) {
          sortArtifacts();
          scope.getArtifactsVirtual();
        }
      });

      const fall = { id: -1, name: 'Alla' };
      scope.offset = 0;
      scope.limit = 15;
      scope.filters = { filename: '', label: fall };
      scope.getArtifactsVirtual = function () {
        scope.artifactsFiltered = scope.artifacts
          .filter(x => scope.filters.filename.length ? x.filename.toLowerCase().includes(scope.filters.filename) : true)
          .filter(x => scope.filters.label === fall ? true : x.label === scope.filters.label);
        scope.artifactsVirtual = scope.artifactsFiltered.slice(scope.offset, scope.offset + scope.limit);
        scope.offset = 0;
      };
      scope.changeFilterTimed = function () {
        $timeout(scope.getArtifactsVirtual, 200);
      };

      scope.getArtifacts = function () {
        Artifacts.list({
          objectType: scope.objectType,
          objectId: scope.objectId,
        }).$promise.then(response => {
          scope.artifacts = response.map(x => ({
            date: new Date(x.properties.createdOn),
            filename: decodeURIComponent(x.metadata.filename),
            label: x.metadata.label ? scope.fileLabels.find(l => l.id === parseInt(x.metadata.label)) : '',
            uuid: x.name,
          }));
          sortArtifacts();
          scope.getArtifactsVirtual();
          if (scope.group) {
            scope.artifacts.forEach((el, index) => {
              el.groupBreak = (index === 0 || el.label !== scope.artifacts[index-1].label);
            });
          }
        });
      };

      scope.$watch('fileLabels', newValue => {
        scope.fileLabelsWithAll = [fall].concat(newValue);
      });

      scope.upload = () => {
        let settings = {
          title: 'Ladda upp ett dokument',
          uploadUrl: `/api/artifacts/${scope.objectType}/${scope.objectId}`,
          validFileTypes: ValidFileTypes,
          types: scope.fileLabels,
          typeTitle: 'Välj typ av underlag'
        };
        HyUtils.showDefaultModal('FileUploadExModalCtrl',
          'components/file-upload-ex-modal/file-upload-ex-modal.html',
          // eslint-disable-next-line no-unused-vars
          { settings }, result => {
            scope.getArtifacts();
          }, null, null, { size: 'sm' });
      };

      scope.download = item => {
        Artifacts.download({
          objectType: scope.objectType,
          objectId: scope.objectId,
          uuid: item.uuid,
        }).$promise.then(response => {
          HyUtils.spitOutAttachment(response.data, item.filename);
        });
      };

      scope.destroy = item => {
        HyUtils.showDefaultModal('WarningConfirmationModalCtrl',
          'components/modal/confirmation/warningConfirmationModal.html',
          {
            message: CommonMessages.FILE_DELETE_CONFIRMATION,
            button: 'Ta bort',
            critical: true,
          },
          // eslint-disable-next-line no-unused-vars
          result => {

            Artifacts.destroy({
              objectType: scope.objectType,
              objectId: scope.objectId,
              uuid: item.uuid,
            // eslint-disable-next-line no-unused-vars
            }).$promise.then(response => {
              scope.getArtifacts();
            });
          }, null, null, { size: 'md' });
      };

      scope.getArtifacts();
    }
  }
  return directive;
}
})();
(function () {
'use strict';

// @desc
//   Counter, used for the navigation on the top menu.
//
// @example
//   <hy-counter></hy-counter>

counter.$inject = ["Auth"];
angular.module('hyresgastApp').directive('hyCounter', counter);

function counter(Auth) {
  var directive = {
    scope: {
      countValue: '@',
      tooltipText: '@',
    },
    templateUrl: 'components/counter/counter.html',
    restrict: 'E',
    link: link,
  };
  return directive;

  // eslint-disable-next-line no-unused-vars
  function link(scope, element, attrs) {
    scope.Auth = Auth;
  }
}

})();
(function () {
'use strict';

// @desc upload files with a nice progressbar and optionally validate files
// @example <hy-file-upload-2></hy-file-upload-2>

fileUpload2.$inject = ["$timeout", "FileValidatorPolling2", "FileUploader", "Flash", "LocalApiPort"];
angular
  .module('fileUpload2', ['angularFileUpload', 'fileValidatorPolling2', 'flash'])
  .directive('hyFileUpload2', fileUpload2);

function fileUpload2($timeout, FileValidatorPolling2, FileUploader, Flash, LocalApiPort) {
  var directive = {
    restrict: 'A',
    scope: {
      blobId: '=', // A successful upload will result in a blob id
      metadata: '=', // Metadata (about the uploaded file) returned from backend
      validFileTypes: '@', // A space separated string 'jpeg gif'
      maxFileSize: '@',
      uploadUrl: '@',
      uploader: '=',
      type: '=?', // Optional type as a parameter
      state: '=?', // State as a parameter
      errorInvalidFile: '=?', // Error message will show in modal window, if upload is from modal window
      validationUrl: '@',
      fOnPending: '&',
      validationResponse: '='
    },
    link: link
  };

  return directive;

  function link(scope, element, attrs) {
    // For upload to work on localhost
    var uploadUrl;
    // eslint-disable-next-line angular/window-service, no-undef
    if (window.location.hostname === 'localhost') {
      uploadUrl = 'http://localhost:' + LocalApiPort + scope.uploadUrl;
    } else {
      uploadUrl = scope.uploadUrl;
    }

    var validationUrl;
    // eslint-disable-next-line angular/window-service, no-undef
    if (window.location.hostname === 'localhost') {
      validationUrl = 'http://localhost:' + LocalApiPort + scope.validationUrl;
    } else {
      validationUrl = scope.validationUrl;
    }

    // Some uploaded files are parsed and validated by backend,
    // if validateUpload attribute exist, a polling service started
    var validateUpload = 'validateUpload' in attrs;

    // Choose a file and upload directly
    var autoUpload = 'autoUpload' in attrs;

    var disableFlash = 'disableFlash' in attrs;

    var uploader = new FileUploader({
      queueLimit: 1,
      withCredentials: true,
      url: uploadUrl
    });

    scope.uploader = uploader;

    scope.$on('$destroy', function() {
      uploader.cancelUpload();
    });

    ////////////
    if (scope.validFileTypes !== '') {
      uploader.filters.push({
        name: 'fileTypeFilter',
        // eslint-disable-next-line no-unused-vars
        fn: function(item /*{File|FileLikeObject}*/, options) {
          var type = '|' + item.name.slice(item.name.lastIndexOf('.') + 1) + '|';
          var extensions = '|' + scope.validFileTypes.split(' ').join('|') + '|'; // "foo bar" => "|foo|bar|"
          return extensions.indexOf(type.toLowerCase()) !== -1;
        }
      });
    }

    uploader.filters.push({
      name: 'fileSizeFilter',
      // eslint-disable-next-line no-unused-vars
      fn: function(item /*{File|FileLikeObject}*/, options) {
        if (item.size < parseInt(scope.maxFileSize)) {
          return true;
        } else {
          return false;
        }
      }
    });

    // eslint-disable-next-line no-unused-vars
    uploader.onWhenAddingFileFailed = function(item /*{File|FileLikeObject}*/, filter, options) {
      if (filter.name === 'fileTypeFilter') {
        if (disableFlash) {
          // If in module, disable Flash error message
          scope.errorInvalidFile = 'Felaktigt filformat - försök igen';
        } else {
          Flash.set('Felaktigt filformat', 'error');
        }
      }

      if (filter.name === 'fileSizeFilter') {
        if (disableFlash) {
          scope.errorInvalidFile = 'Filen är för stor - försök igen';
        }
        Flash.set('Filen är för stor - försök igen', 'error');
      }
    };

    uploader.onAfterAddingFile = function(fileItem) {
      uploader.currentFileName = fileItem.file.name;
      scope.validationResponse = null;
      if (autoUpload) {
        uploader.uploadAll();
      }
    };

    // eslint-disable-next-line no-unused-vars
    uploader.onInterruptedItem = function(fileItem, response, status, headers) {
      Flash.set('Uppladdningsfel', 'error');
    };

    // eslint-disable-next-line no-unused-vars
    uploader.onSuccessItem = function(fileItem, response, status, headers) {
      var blobId = response.blobId;
      scope.metadata = response;

      // Backend is not returning lastModified on create but later
      // when the file is queried, thus added for consistency
      scope.metadata.lastModified = new Date();
      scope.blobId = response.blobId;

      // API will parse the uploaded file content which might take
      // up to ~30s
      if (validateUpload) {
        uploader.isValidating = true;
        FileValidatorPolling2.start(validationUrl + '/' + blobId + '/validate', scope.fOnPending)
          .then(
            function(response) {
              scope.validationResponse = response;
              if (response.status === 'ok') {
                scope.validationResponse.blobId = blobId;
              } else if (response.status === 'error') {
                scope.validationResponse.blobId = blobId;
                uploader.cancelUpload();
              }
            },
            // eslint-disable-next-line no-unused-vars
            function(reason) {
              Flash.set('Uppladdningsfel', 'error');
            }
          )
          .finally(function() {
            uploader.isValidating = false;
          });
      }
    };

    uploader.onBeforeUploadItem = function(fileItem) {
      if (scope.type) {
        fileItem.url = fileItem.url + '?type=' + scope.type;
      }
      if (scope.state) {
        fileItem.url = fileItem.url + '?state=' + scope.state;
      }
    };

    // eslint-disable-next-line no-unused-vars
    uploader.onErrorItem = function(fileItem, response, status, headers) {
      Flash.set('Uppladdningsfel', 'error');
    };

    // eslint-disable-next-line no-unused-vars
    uploader.onCancelItem = function(fileItem, response, status, headers) {
      uploader.clearQueue();
    };

    uploader.cancelUpload = function() {
      FileValidatorPolling2.stop(); // might be running
      uploader.isValidating = false;
      scope.blobId = null;
      uploader.currentFileName = null;
      uploader.cancelAll();
      uploader.clearQueue();
    };
  }
}

})();
(function () {
'use strict';

FileUploadExModalCtrl.$inject = ["$uibModalInstance", "$scope", "$document", "settings"];
angular.module('hyresgastApp').controller('FileUploadExModalCtrl', FileUploadExModalCtrl);

function FileUploadExModalCtrl($uibModalInstance, $scope, $document,
  settings) {
  var vm = this;

  vm.settings = settings;
  vm.settings.splitedValidFileTypes = settings.validFileTypes.split(' ');

  if (settings.types.length === 1) {
    vm.file = {};
    vm.file.type = settings.types[0].id;
    vm.selectedTypeName = settings.types[0].name;
  }

  $scope.$watch('vm.file.blobIds', function(newValue) {
    if (newValue) {
      $uibModalInstance.close(vm.file.metadata);
    }
  });
  $scope.$watch('vm.type', function (newValue) {
    if (newValue) {
      vm.uploadParams = { type: newValue.id };
    }
  });

  vm.doUpload = () => {
    let filePlateEl = angular.element($document[0].querySelector("#file-plate"));
    filePlateEl.scope().$broadcast('do-upload');
  };
  vm.cancel = () => { $uibModalInstance.dismiss('cancel') }
}

})();
(function () {
'use strict';

// Certain uploaded files are parsed and validated by API. I.e is a
// Excel-file missing a column. The parsing might take some time (~30s),
// this service can be used to poll the API in a defined interval
// The service returns a promise

FileValidatorPolling2.$inject = ["$http", "$timeout", "$q"];
angular.module('fileValidatorPolling2', []).factory('FileValidatorPolling2', FileValidatorPolling2);

function FileValidatorPolling2($http, $timeout, $q) {
  var stopNow;

  var service = {
    start: start,
    stop: stop
  };

  return service;

  ////////////

  function start(url, fOnPending) {
    stopNow = false;

    var delay = 5000; // 5 seconds
    var maxRetries = 60; // 60 * 5000 = 5 minutes
    var retries = 0;

    return validate(url, delay, retries, maxRetries, fOnPending);
  }

  // Stop eventually... (Keep It Simple Stupid)
  function stop() {
    stopNow = true;
  }

  function delayer(delay) {
    delay += Math.floor(Math.random() * 1000); // avoid synchronized waves
    return $timeout(function() {}, delay);
  }

  function fetch(url) {
    return $http
      .get(url, {
        timeout: 15000, // dont be stuck in pending longer than 15 sec if loosing internet connection
        withCredentials: true
      })
      .then(function(response) {
        return response.data;
      });
  }

  function validate(url, delay, retries, maxRetries, fOnPending) {
    return delayer(delay)
      .then(function() {
        if (stopNow) {
          return { status: 'UserAbort' };
        } else if (retries >= maxRetries) {
          return $q.reject('MaxRetries');
        } else {
          return fetch(url);
        }
      })
      .then(
        function(validation) {
          if (validation.status === 'pending') {
            if (fOnPending && fOnPending()) {
              fOnPending()(validation);
            }
          } else if (validation.status === 'ok' || validation.status === 'error') {
            return validation;
          } else if (validation.status === 'UserAbort') {
            return validation.status; // not used at the moment
          }
          return validate(url, delay, ++retries, maxRetries, fOnPending);
        },
        function(reason) {
          if (reason === 'MaxRetries') {
            return $q.reject(reason);
          } else {
            return validate(url, delay, ++retries, maxRetries, fOnPending);
          }
        }
      );
  }
}

})();
(function () {
'use strict';

apartmentSelector2.$inject = ["$window", "$timeout", "$filter", "$cookies", "HyUtils", "ApartmentSelector2"];
angular.module('hyresgastApp').directive('hyApartmentSelector2', apartmentSelector2);

function cleanObject(obj) {
  for (const key in obj) {
    if (typeof obj[key] === 'undefined' || (Array.isArray(obj[key]) && obj[key].length === 0)) {
      delete obj[key];
    }
  }
  return obj;
}

function apartmentSelector2($window, $timeout, $filter, $cookies,
  HyUtils,
  ApartmentSelector2) {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/apartment-selector-2/apartment-selector-2.html',
    scope: {
      isModal: '=',
      allowEmptyProperties: '=',
      emptyOnly: '=',
      apartmentsArguments: '=',
      incomingProperties: '=',
      userId: '=',

      model: '=',
      emptyProperties: '=',
    },
    link: link,
  };
  return directive;

  // eslint-disable-next-line no-unused-vars
  function link(scope, element, attrs) {

    let filtersExpanded = $cookies.get('apartmentSelector2:filtersExpanded');
    scope.filtersExpanded = filtersExpanded ? JSON.parse(filtersExpanded) : { };
    scope.expandFilter = filterName => {
      scope.filtersExpanded[filterName] = !scope.filtersExpanded[filterName];
      $cookies.put('apartmentSelector2:filtersExpanded', JSON.stringify(scope.filtersExpanded));
    };

    scope.model = [];
    scope.emptyProperties = [];
    scope.getNames = arr => arr ? arr.map(x=>x.name).slice(0, 2).join(', ') : null;
    scope.getHtmlTooltip = arr => arr ? arr.slice(1).join('<br/>') : null;
    //TODO: extract to fe-common
    scope.timed = f => $timeout(() => { f() }, 400);

    scope.offset = 0;
    scope.limit = 24;

    scope.getPropertiesVirtual = () => {
      scope.propertiesVirtual = scope.properties.slice(scope.offset, scope.offset + scope.limit);
    }

    scope.onlyMyProperties = true;
    scope.changePropertiesMethod = onlyMyProperties => {
      scope.timed(() => {
        scope.onlyMyProperties = !onlyMyProperties;
        scope.offset = 0;
        scope.getProperties();
        scope.getPropertiesVirtual();
      });
    };

    scope.getProperties = () => {
      scope.propertiesLoading = true;
      let params = {
        offset: 0,
        limit: scope.limit * 18,
        order: 'propertyDesignation',
        direction: 'ASC',
      };
      if (scope.onlyMyProperties && scope.userId) {
        params.userId = scope.userId;
      }
      if (scope.filters) {
        params.filters = {
          regionId: scope.filters.regionIds,
          municipality: scope.filters.municipalities,
          propertyDesignation: scope.filters.propertyDesignations,
        };
        params.search = {
          owners: scope.filters.owners,
          counterparts: scope.filters.counterparts,
        };
        params.filters = cleanObject(params.filters);
        params.search = cleanObject(params.search);
      }
      ApartmentSelector2.getProperties(params)
        .$promise.then(response => {
          scope.properties = response.rows;
          scope.propertiesTotalCount = response.count;
          scope.propertiesTotalCountVirtual = scope.properties.length;
          for (let item of scope.properties) {
            item.selectionState = PROPERTY_CARD_STATE.NOT_SELECTED;
          }
          scope.tickedProperties = {};
          scope.allProperties = true;
          scope.toggleAllProperties();
          scope.propertiesLoading = false;
          scope.getPropertiesVirtual();
          scope.checkBasket();
        });
    };

    scope.getDictionaries = sendCurrent => {
      let params = sendCurrent ? {
        filters: scope.filters,
        filterSearches: scope.filterSearches,
      } : {};
      ApartmentSelector2.getDictionaries(params)
        .$promise.then(response => {
          delete response[scope.skipDictionary];
          Object.assign(scope.filterDictionaries, response);
          //scope.filterDictionaries = response;
        });
    };

    scope.properties = [];
    scope.filterSearches = {
      municipality: '',
      propertyDesignation: '',
      owner: '',
      counterpart: '',
    };
    scope.filterDictionaries = {
      regionId: [],
      municipalities: [],
      propertyDesignations: [],
      owners: [],
      counterparts: [],
    };
    function resetFilters() {
      scope.filters = {
        regionIds: [],
        municipalities: [],
        propertyDesignations: [],
        owners: [],
        counterparts: [],
      };
    }
    resetFilters();
    scope.getDictionaries(false);
    scope.getProperties();
    scope.isSomethingSelected = () => {
      scope.filters.present =
       (scope.filters.regionIds.length ||
        scope.filters.propertyDesignations.length ||
        scope.filters.municipalities.length ||
        scope.filters.owners.length ||
        scope.filters.counterparts.length);
    };

    let filtersChanged = () => {
      scope.isSomethingSelected();
      scope.getProperties();
      scope.getDictionaries(true);
      scope.offset = 0;
    };
    scope.skipDictionary = null;
    let maybePush = (filterType, value) => {
      let arr = scope.filters[filterType];
      scope.skipDictionary = filterType;
      if (!arr.includes(value)) {
        arr.push(value);
        filtersChanged();
      }
    };
    let remove = (filterType, value) => {
      let arr = scope.filters[filterType];
      scope.skipDictionary = filterType;
      arr.splice(arr.findIndex(x => x === value), 1);
      filtersChanged();
    }

    scope.addRegionFilter = item => maybePush('regionIds', item);
    scope.removeRegionFilter = item => remove('regionIds', item);

    scope.addMunicipalityFilter = item => maybePush('municipalities', item);
    scope.removeMunicipalityFilter = item => remove('municipalities', item);

    scope.addPropertyFilter = item => maybePush('propertyDesignations', item);
    scope.removePropertyFilter = item => remove('propertyDesignations', item);

    scope.addOwnersFilter = item => maybePush('owners', item);
    scope.removeOwnersFilter = item => remove('owners', item);

    scope.addCounterpartsFilter = item => maybePush('counterparts', item);
    scope.removeCounterpartsFilter = item => remove('counterparts', item);

    scope.removeAllFilters = () => {
      resetFilters();
      filtersChanged();
    };

    //setup scroll
    // eslint-disable-next-line angular/document-service, no-undef
    let stickyEl = document.getElementById('floatingFilters');
    let stickyTop = stickyEl.offsetTop;

    let onScrollAction = function () {
      // eslint-disable-next-line eqeqeq
      if (stickyTop < 300) {
        stickyTop = stickyEl.offsetTop;
      }
      // eslint-disable-next-line angular/window-service, no-undef
      let windowTop = window.scrollY;
      //console.log('scroll', windowTop, stickyTop);
      if (windowTop >= stickyTop) {
        stickyEl.classList.add('floatingReally');
      } else {
        stickyEl.classList.remove('floatingReally');
      }
    };

    angular.element($window).on('scroll', onScrollAction);
    scope.$on('$destroy', function () {
      angular.element($window).off('scroll', onScrollAction);
    });

    const PROPERTY_CARD_STATE = {
      NOT_SELECTED: 0,
      LOADING: 1,
      READY_NO_APARTMENTS: 2,
      READY_APARTMENTS: 3,
    };
    scope.PROPERTY_CARD_STATE = PROPERTY_CARD_STATE;

    scope.selectProperty = property => {
      if (scope.emptyOnly) {
        return;
      }
      if (scope.selectedProperty) {
        if (scope.selectedProperty === property) {
          return;
        }
        scope.selectedProperty.selectionState = PROPERTY_CARD_STATE.NOT_SELECTED;
      }
      for (let item of scope.properties) {
        item.selectionState = PROPERTY_CARD_STATE.NOT_SELECTED;
      }
      property.selectionState = PROPERTY_CARD_STATE.LOADING;
      scope.selectedProperty = property;
      scope.getApartments();
    };

    /**
     * gets all ticked ids
     * @param {Object} ticked
     * @returns {Array<number>}
     */
    let getTicked = ticked => Object.keys(ticked).filter(k => ticked[k]).map(id => parseInt(id));
    /**
   * determines if there is something ticked
   * @param {Object} ticked
   * @returns {boolean} true if something is ticked
   */
    let hasTicked = ticked => !!getTicked(ticked).length;

    scope.hasTickedApartments = () => hasTicked(scope.tickedApartments);
    scope.hasTickedProperties = () => hasTicked(scope.tickedProperties);

    scope.vm = { apartmentFilterPhrase: '', basketFilterPhrase: '', selectedAgreementCodes: [] };
    scope.basketFilter = el => {
      let gotText = scope.vm.basketFilterPhrase.length > 0;
      let gotType = scope.vm.selectedAgreementCodes.length > 0;
      if (!gotText && !gotType) {
        return true;
      }

      let { text, type } = el.property ?
        {
          text: `${el.streetAddress}|${el.nationalNo}|${el.ownerNo}`,
          type: el.property.agreementCode
        } : {
          text: `${el.propertyDesignation}|${el.municipality}`,
          type: el.agreementCode
        };

      if (gotText && !text.toLowerCase().includes(scope.vm.basketFilterPhrase.toLowerCase())) {
        return false;
      }
      if (gotType && !scope.vm.selectedAgreementCodes.includes(type)) {
        return false;
      }

      return true;
    };
    scope.apartmentsFilter = el => {
      return !scope.vm.apartmentFilterPhrase.length ||
              el.streetAddress.includes(scope.vm.apartmentFilterPhrase) ||
              el.nationalNo.includes(scope.vm.apartmentFilterPhrase) ||
              el.ownerNo.includes(scope.vm.apartmentFilterPhrase);
    };

    scope.getApartments = () => {
      let propertyId = scope.selectedProperty.id;
      ApartmentSelector2.getApartments({ propertyId })
        .$promise.then(response => {
          scope.apartments = response;
          scope.selectedProperty.selectionState = scope.apartments.length > 0
            ? PROPERTY_CARD_STATE.READY_APARTMENTS
            : PROPERTY_CARD_STATE.READY_NO_APARTMENTS;

          scope.allApartments = true;
          scope.tickedApartments = {};
          scope.toggleAllApartments();

          /*
          $timeout(() => {
            // eslint-disable-next-line angular/document-service, no-undef
            let el = document.getElementById(`property${propertyId}`);
            el.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
          }, 500);
          */
        });
    };

    let reductor = (acc, el) => { acc[el.id] = true; return acc; };
    scope.toggleAllProperties = () => {
      if (getTicked(scope.tickedProperties).length < scope.properties.length) {
        scope.tickedProperties = scope.properties.reduce(reductor, {});
      } else {
        scope.tickedProperties = {};
      }
      scope.checkBasket();
    };
    scope.toggleAllApartments = () => {
      if (getTicked(scope.tickedApartments).length < scope.apartments.length) {
        scope.tickedApartments = scope.apartments.reduce(reductor, {});
      } else {
        scope.tickedApartments = {};
      }
    };


    let fixEmptyProperties = () => {
      if (scope.emptyProperties) {
        scope.emptyProperties = scope.emptyProperties.filter(p => !scope.model.find(a => a.property.id === p.id));
      }
    };

    /**
     * add apartments avoiding duplicates
     * @param {Array} newApartments
     */
    let addUniqueApartments = newApartments => {
      let hash = a => `${a.streetAddress}|${a.nationalNo}|${a.ownerNo}`;
      scope.model = HyUtils.unionArraysFn(scope.model, newApartments, (a, b) => {
        return hash(a) === hash(b);
      });
    };
    scope.addSelected = () => {
      let tickedApartments = getTicked(scope.tickedApartments);
      let newApartments = scope.apartments.filter(el => tickedApartments.includes(el.id));
      newApartments = newApartments.filter(el => scope.apartmentsFilter(el));
      newApartments.forEach(el => el.property = scope.selectedProperty);
      addUniqueApartments(newApartments);
      scope.tickedApartments = {};
      scope.tickedProperties[scope.selectedProperty.id] = false;
      scope.allApartments = false;
      if (!scope.hasTickedProperties()) {
        scope.allProperties = false;
      }
      sortModel();
      scope.checkBasket();
      fixEmptyProperties();
    };
    scope.addEmptyProperty = () => {
      let selectedProperties = getTicked(scope.tickedProperties)
        .filter(id => !scope.emptyProperties.find(e => e.id === id))
        .map(id => scope.properties.find(p => p.id === id));
      if (!selectedProperties.length &&
           scope.selectedProperty && !scope.emptyProperties.find(x => x.id === scope.selectProperty.id)) {
        selectedProperties.push(scope.selectedProperty);
      }
      scope.emptyProperties = scope.emptyProperties.concat(selectedProperties);
      scope.tickedProperties = {};
      scope.allProperties = false;
      sortEmptyProperties();
      scope.checkBasket();
    };
    scope.addEveryApartment = () => {
      var ticked = getTicked(scope.tickedProperties);
      if (!ticked.length) {
        return;
      }
      var propertiesDone = 0;
      var apartmentCollector = [];
      scope.busyAddEvery = true;
      scope.properties.forEach(p => {
        if (!ticked.includes(p.id)) return;
        ApartmentSelector2.getApartments({ propertyId: p.id })
          .$promise.then(response => {
            response.forEach(el => el.property = p);
            apartmentCollector.push(...response);
            propertiesDone++;

            if (propertiesDone === ticked.length) {
              addUniqueApartments(apartmentCollector);
              scope.busyAddEvery = false;
              scope.allProperties = false;
              scope.tickedProperties = {};
              sortModel();
              scope.checkBasket();
            }
          });
      });
    };

    scope.doSearch = (filter, dictionaryType, dictionary) => {
      scope.filtersLoading = true;
      ApartmentSelector2.getDictionary({
        dictionaryType,
        filter,
        filters: scope.filters,
      }).$promise.then(response => {
        dictionary.length = 0;
        dictionary.push.apply(dictionary, response);
        scope.filtersLoading = false;
      });
    };
    scope.doSearchMunicipality = filter => scope.doSearch(filter, 'municipality', scope.filterDictionaries.municipalities);
    scope.doSearchPropertyDesignation = filter => scope.doSearch(filter, 'propertyDesignation', scope.filterDictionaries.propertyDesignations);
    scope.doSearchOwner = filter => scope.doSearch(filter, 'owner', scope.filterDictionaries.owners);
    scope.doSearchCounterpart = filter => scope.doSearch(filter, 'counterpart', scope.filterDictionaries.counterparts);

    let agreementCode = property => property.negotiationAgreementCodes instanceof Array
      ? property.negotiationAgreementCodes.map(code => $filter('hyNegotiationAgreementTypes')(code, true)).join(', ')
      : 'Ej angivet';
    let setAgreementCodes = () => {
      scope.emptyProperties.forEach(x => x.agreementCode = agreementCode(x));
      scope.model.forEach(x => x.property.agreementCode = agreementCode(x.property));
      let x1 = scope.emptyProperties.map(x => x.agreementCode);
      let x2 = scope.model.map(x => agreementCode(x.property));
      // eslint-disable-next-line no-undef
      scope.agreementCodes = Array.from(new Set(x1.concat(x2)));
    }
    scope.checkBasket = () => {
      scope.hasSelectedProperties = (scope.model && !!scope.model.length) || (scope.emptyProperties && !!scope.emptyProperties.length);
      scope.basketVisible = scope.hasSelectedProperties || scope.hasTickedProperties();
      if (!scope.basketVisible || !scope.hasSelectedProperties) {
        scope.basketOpened = false;
      }
      setAgreementCodes();
    };
    scope.toggleBasketOpened = () => {
      if (scope.hasSelectedProperties) {
        scope.basketOpened = !scope.basketOpened;
      }
      if (scope.basketOpened) {
        // eslint-disable-next-line angular/document-service, no-undef
        let el = document.getElementById('basketFilter');
        el.focus();
      }
    };

    let separateByProperty = () => {
      let previous = scope.model[0];
      scope.model.forEach((row, index) => {
        if (index > 0) {
          previous = scope.model[index - 1];
          row.differentProperty = row.property.id !== previous.property.id;
        } else {
          row.differentProperty = true;
        }
      });
    };
    let sortModel = () => {
      let hash = a => `${a.property.district}|${a.property.propertyDesignation}|${a.streetAddress}|${a.nationalNo}|${a.ownerNo}`;
      scope.model.sort((a, b) => hash(a) > hash(b) ? 1 : -1);
      separateByProperty();
    };
    let sortEmptyProperties = () => {
      scope.emptyProperties.sort((a, b) => a.id > b.id ? 1: -1);
    };


    scope.deleteSelected = id => {
      scope.model = scope.model.filter(row => row.id !== id);
      sortModel();
    };
    scope.deleteProperty = id => {
      scope.model = scope.model.filter(a => a.property.id === id);
      sortEmptyProperties();
      sortModel();
      scope.checkBasket();
    }
    scope.deleteEmptyProperty = id => {
      scope.emptyProperties = scope.emptyProperties.filter(p => p.id !== id);
      sortEmptyProperties();
      scope.checkBasket();
    };
    scope.deleteEverything = () => {
      if ((scope.vm.basketFilterPhrase && scope.vm.basketFilterPhrase.length > 0) ||
          (scope.vm.selectedAgreementCodes && scope.vm.selectedAgreementCodes.length)) {
        scope.emptyProperties = scope.emptyProperties.filter(x => !scope.basketFilter(x));
        scope.model = scope.model.filter(x => !scope.basketFilter(x));
        scope.vm.basketFilterPhrase = '';
        scope.vm.selectedAgreementCodes = [];
      } else {
        scope.emptyProperties = [];
        scope.model = [];
      }
      scope.checkBasket();
    };
  }
}
})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('ApartmentSelector2', ["$resource", function($resource) {
  return $resource(
    '/api/real-estate/apartment-selector/',
    {
      id: '@id',
      dictionaryType: '@dictionaryType',
    },
    {
      getProperties: {
        url: '/api/real-estate/apartment-selector/properties',
        isArray: false,
        method: 'POST',
      },
      getDictionaries: {
        url: '/api/real-estate/apartment-selector/dictionaries',
        method: 'POST',
      },
      getDictionary: {
        url: '/api/real-estate/apartment-selector/dictionary/:dictionaryType/dictionary',
        isArray: true,
        method: 'POST',
      },
      getApartments: {
        url: '/api/real-estate/apartment-selector/property/:propertyId/apartments',
        isArray: true,
        method: 'GET',
      }
    }
  );
}]);

})();
(function () {
'use strict';

// @desc
//   Wizard steps
//
// @example
//   <hy-modal-wizard wizard-submit="save()">
//      <hy-modal-wizard-step></hy-modal-wizard-step>
//   </hy-modal-wizard>

angular.module('modalWizard.step', []).directive('hyModalWizardStep', modalWizardStep);

function modalWizardStep() {
  var directive = {
    require: '^hyModalWizard', // We need this directive to be inside a wizard
    transclude: true, // It transcludes the contents of the directive into the template
    restrict: 'E',
    scope: {
      heading: '@', // Interpolate the heading attribute onto this scope
      headingStepNumber: '@',
      headingShort: '@', // To fit in the timeline
      name: '@', // Used in query parameter ?step=
      isDisabled: '=?',
      isValid: '=', // Validation function that return true to move to next step
      alertHeader: '@',
      alertText: '=',
      alternativeWizard: '='
    },
    templateUrl: 'components/modalwizard/modal-wizard-step.html',
    link: link
  };

  return directive;

  ////////////
  function link(scope, element, attrs, wizardCtrl) {
    wizardCtrl.addStep(scope);

    scope.hideNextButton = attrs.hasOwnProperty('hideNextButton');
    scope.$watch('isOpen', function(value) {
      element.toggleClass(scope.openClass, !!value);
      if (value) {
        wizardCtrl.closeOthers(scope);
      }
    });

    scope.$watch('isDisabled', function(value) {
      wizardCtrl.setEnabledSteps();
    });

    scope.toggleOpen = function($event) {
      if (!scope.isDisabled) {
        if (!$event || $event.which === 32) {
          scope.isOpen = !scope.isOpen;
        }
      }
    };

    // ARIA
    scope.wizardId = 'modal-wizard-step' + scope.$id + '-' + Math.floor(Math.random() * 10000);
  }
}

})();
(function () {
'use strict';

// @desc
//   Wizard to handle a big task in smaller steps
//
// @example
//   <hy-modal-wizard>
//      <hy-modal-wizard-step></hy-modal-wizard-step>
//   </hy-modal-wizard>

angular.module('modalWizard', ['modalWizard.ctrl', 'modalWizard.step']).directive('hyModalWizard', modalWizard);

function modalWizard() {
  var directive = {
    controller: 'ModalWizardCtrl',
    controllerAs: 'modalWizardVm',
    transclude: true,
    scope: {
      wzSubmit: '&',
      wzCancel: '&',
      submitText: '@?',
      submitDisabled: '=',
      gotoNext: '=',
      beforeGotoNext: '&?',
      currentStepNumber: '=',
      isCurrentStepValid: '=',
      headerText: '@',
      alternativeWizard: '='
    },
    restrict: 'E',
    templateUrl: 'components/modalwizard/modal-wizard.html'
  };
  return directive;
}

})();
(function () {
'use strict';

/* jshint -W040 */

ModalWizardCtrl.$inject = ["$scope", "$attrs", "$location", "$timeout"];
angular.module('modalWizard.ctrl', []).controller('ModalWizardCtrl', ModalWizardCtrl);

function ModalWizardCtrl($scope, $attrs, $location, $timeout) {
  var wizardVm = this;
  var firstEnabledOpened;

  wizardVm.hideHeader = 'hideHeader' in $attrs;
  wizardVm.alternativeWizard = $attrs.alternativeWizard;
  // This array keeps track of the wizard steps
  wizardVm.steps = [];
  wizardVm.currentStepNumber = 1;
  wizardVm.closeOthers = closeOthers;
  wizardVm.addStep = addStep;
  wizardVm.removeStep = removeStep;
  wizardVm.next = next;
  wizardVm.previous = previous;
  wizardVm.isFirstStep = isFirstStep;
  wizardVm.isLastStep = isLastStep;
  wizardVm.isCurrentStepValid = isCurrentStepValid;
  wizardVm.enabledSteps = [];
  wizardVm.setEnabledSteps = setEnabledSteps;
  wizardVm.hideNextButton = hideNextButton;

  /*$scope.$on('$locationChangeSuccess', function() {
    if ($location.search().step !== undefined) {
      gotoStepByName($location.search().step);
    }
  });*/

  $scope.$watch('gotoNext', function(value) {
    if (value) {
      //wrapped inside $timeout so newly enabled steps are included.
      $timeout(function() {
        next();
      });
      $scope.gotoNext = false;
    }
  });

  $scope.isCurrentStepValid = isCurrentStepValid;
  ////////////

  function setCurrentStepNumber(step) {
    wizardVm.currentStepNumber = step.headingStepNumber;
  }

  function isCurrentStepValid() {
    var foundStep;
    angular.forEach(wizardVm.steps, function(step) {
      if (step.isOpen) {
        foundStep = step;
      }
    });

    if (foundStep) {
      // If no validation function is provided, allow the user to enter the step
      if (foundStep.isValid === undefined) {
        return false;
      }
      // If validation is a boolean value instead of a function, return the value
      if (typeof foundStep.isValid === 'boolean') {
        return foundStep.isValid;
      }

      return foundStep.isValid();
    }

    return false;
  }

  // Open next step that is not disabled
  function next() {
    if ($scope.beforeGotoNext) {
      // eslint-disable-next-line angular/controller-as
      $scope.beforeGotoNext({ cb: () => { nextnext() }, currentStepNumber: wizardVm.currentStepNumber });
    } else {
      nextnext();
    }
  }

  function nextnext() {
    var nextStep;
    angular.forEach(wizardVm.enabledSteps, function(step, index) {
      if (step.isOpen && index < wizardVm.enabledSteps.length - 1) {
        nextStep = wizardVm.enabledSteps[index + 1];
      }
    });
    setCurrentStepNumber(nextStep);
    gotoStepByName(nextStep.name);
   //$location.search(angular.extend($location.search(), { step: nextStep.name }));
  }

  // Open previous step that is not disabled
  function previous() {
    var previousStep;

    angular.forEach(wizardVm.enabledSteps, function(step, index) {
      if (step.isOpen && index > 0) {
        previousStep = wizardVm.enabledSteps[index - 1];
      }
    });
    setCurrentStepNumber(previousStep);
    gotoStepByName(previousStep.name);
    //$location.search(angular.extend($location.search(), { step: previousStep.name }));
  }

  function gotoStepByName(name) {
    var foundStep;
    var foundAtIndex;
    angular.forEach(wizardVm.enabledSteps, function(step, index) {
      if (step.name === name) {
        foundStep = step;
        foundAtIndex = index;
      }
    });
    // Ensure all prior steps are valid if using browser buttons
    var foundInvalidStep;
    angular.forEach(wizardVm.enabledSteps, function(step, index) {
      if (!isStepValid(step) && index < foundAtIndex) {
        foundInvalidStep = true;
        foundStep = step;
      }
    });

    if (foundInvalidStep) {
      //$location.search(angular.extend($location.search(), { step: foundStep.name })); // goto invalid step instead
      //$location.replace();
    } else {
      foundStep.isOpen = true;
    }
    setCurrentStepNumber(foundStep);
  }

  function isStepValid(step) {
    // If no validation function is provided, allow the user to enter the step
    if (step.isValid === undefined) {
      return true;
    }
    // If validation is a boolean value instead of a function, return the value
    if (typeof step.isValid === 'boolean') {
      return step.isValid;
    }

    return step.isValid();
  }

  // Ensure that all the steps in this wizard are closed
  function closeOthers(openStep) {
    angular.forEach(this.steps, function(step) {
      if (step !== openStep) {
        step.isOpen = false;
      }
    });
  }

  // This is called from the wizard-step directive to add itself to the wizard
  function addStep(stepScope) {
    var that = this;
    this.steps.push(stepScope);

    // Open first step not being disabled
    if (!firstEnabledOpened && stepScope.isDisabled !== true) {
      //$location.search(angular.extend($location.search(), { step: stepScope.name }));
      //$location.replace();
      stepScope.isOpen = true; // TODO remove
      firstEnabledOpened = true;
    }

    setEnabledSteps();

    stepScope.$on('$destroy', function(event) {
      that.removeStep(stepScope);
    });
  }

  // This is called from the wizard-step directive when to remove itself
  function removeStep(step) {
    var index = this.steps.indexOf(step);
    if (index !== -1) {
      this.steps.splice(index, 1);
    }
  }

  function isFirstStep() {
    return wizardVm.enabledSteps.length > 0 && wizardVm.enabledSteps[0].isOpen === true;
  }

  function isLastStep() {
    return wizardVm.enabledSteps.length > 0 && wizardVm.enabledSteps[wizardVm.enabledSteps.length - 1].isOpen === true;
  }

  // It's in many cases not to care about disabled steps so filter them out
  // can make tasks simpler
  function setEnabledSteps() {
    wizardVm.enabledSteps = wizardVm.steps.filter(function(step) {
      return !step.isDisabled;
    });
  }

  function hideNextButton() {
    var found;

    angular.forEach(wizardVm.steps, function(step) {
      if (step.isOpen && step.hideNextButton) {
        found = true;
      }
    });

    return found;
  }
}

/* jshint +W040 */

})();
(function () {
'use strict';

// @desc Part of the layout
//
// @example
//   <body>
//      <hy-navigation></hy-navigation>
//   </body>

navigation.$inject = ["$location", "$window", "$rootScope", "$interval", "$timeout", "Auth", "User", "Roles", "Flash"];
angular.module('hyresgastApp').directive('hyNavigation', navigation);

function navigation($location, $window, $rootScope, $interval, $timeout, Auth, User, Roles, Flash) {
  var directive = {
    link: link,
    templateUrl: 'components/navigation/navigation.html',
    restrict: 'E'
  };
  return directive;

  // eslint-disable-next-line no-unused-vars
  function link(scope, element, attrs) {
    scope.Auth = Auth;
    scope.isActive = isActive;
    scope.isOrganizationAdmin = isOrganizationAdmin;
    scope.logout = logout;

    scope.$window = $window;

    scope.counters = {
      freeAgreements: 0,
      pendingOrganizations: 0,
      pendingUsers: 0,
      negotiationCalls: 0,
    }
    var stop;

    $timeout(() => {
      startSync();
    }, 500);

    ////////////

    function isActive(viewLocation) {
      return viewLocation === $location.path();
    }

    function isOrganizationAdmin(user) {
      if (!user || !user.roleByOrganization) {
        return false;
      }
      var isAdmin = false;
      for (var key in user.roleByOrganization) {
        // eslint-disable-next-line no-prototype-builtins
        if (!user.roleByOrganization.hasOwnProperty(key)) {
          continue;
        }
        if (user.roleByOrganization[key] === Roles.ADMIN) {
          isAdmin = true;
          break;
        }
      }
      return isAdmin;
    }

    function logout() {
      Auth.destroy().then(
        function() {
          $location.path('/'); // clear sensitive content on page

          // redirect to login page
          Flash.set('Du har blivit utloggad.', 'success');
          $rootScope.$broadcast('event:auth-loginRequired', null);
        },
        function() {}
      );
    }

    function getMyCounters() {
      if (!Auth.currentUser()) {
        return;
      }
      User.getMyCounters({}).$promise.then(response => {
        Object.assign(scope.counters, response);
      });
    }

    function startSync() {
      getMyCounters();
      // Don't start a new update counter if we are already counting
      if (angular.isDefined(stop)) return;
      stop = $interval(getMyCounters, 180000);
    }

    function stopSync() {
      if (angular.isDefined(stop)) {
        $interval.cancel(stop);
        // eslint-disable-next-line no-undefined
        stop = undefined;
      }
    }

    scope.$on('$destroy', function() {
      // Make sure that the interval is destroyed too
      stopSync();
    });

    // stop and start sync when i.e going to different tab in Chrome
    function handleVisibilityChange() {
      if ($window.document.hidden) {
        stopSync();
      } else {
        startSync();
      }
    }

    $window.document.addEventListener('visibilitychange', handleVisibilityChange, false);

    scope.$watch('Auth.currentUser()', function(newValue, oldValue) {
      // call sync once when a user signs in to update the counter directly
      // (instead of waiting on interval)
      if (newValue && !oldValue) {
        getMyCounters();
      }
    });

  }
}

})();
(function () {
'use strict';

objectFooter.$inject = ["$routeParams", "Flash", "HyUtils"];
angular.module('hyresgastApp').directive('objectFooter', objectFooter);

//footer for every object that has ID
//the only feature now is object deletion

function objectFooter($routeParams,
  Flash, HyUtils) {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/object-footer/object-footer.html',
    scope: {
      objectName: '@',

      warningMessageDelete: '@',
      fDelete: '&',
      redirectOnDelete: '@',
    },
    // eslint-disable-next-line no-unused-vars
    link: (scope, element, attrs) => {
      scope.deleteObject = () => {

        let customMessage = scope.warningMessageDelete && scope.warningMessageDelete.length;

        let message = customMessage ? scope.warningMessageDelete : 'Är du säker?';
        let params = customMessage ? {} : { size: 'xsm' };

        HyUtils.showDefaultModal('WarningConfirmationModalCtrl',
          'components/modal/confirmation/warningConfirmationModal.html', {
            message,
            button: 'Ta bort',
            critical: true,
          // eslint-disable-next-line no-unused-vars
          }, result => {
            scope.fDelete()({ id: $routeParams.id })
            // eslint-disable-next-line no-unused-vars
              .$promise.then(result => {
                Flash.set(`Lyckades att ta bort ${scope.objectName}`, 'success');
                // eslint-disable-next-line angular/window-service, no-undef
                window.location.href = scope.redirectOnDelete && scope.redirectOnDelete
                  ? scope.redirectOnDelete
                  : '/';
              }).catch(err =>
                Flash.set(err.status === 501
                  ? err.data.message
                  : `Misslyckades att ta bort ${scope.objectName}`, 'error'));
          }, null, null, params);
      };
    }
  };
  return directive;
}
})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Blocking', ["$resource", function($resource) {
  return $resource(
    '/api/negotiations/:negotiationId/blockings/:id',
    { negotiationId: '@negotiationId', blockingId: '@blockingId', id: '@id' },
    {
      queryApartment: {
        url: '/api/negotiations/:negotiationId/blockings/:id/apartments'
      },
      deleteApartment: {
        url: '/api/negotiations/:negotiationId/blockings/:blockingId/apartments/:id/unassign',
        method: 'POST'
      },
      saveApartment: {
        url: '/api/negotiations/:negotiationId/blockings/:id/apartments',
        method: 'POST'
      },
      resume: {
        url: '/api/negotiations/:negotiationId/blockings/:id/resume',
        method: 'POST'
      },
      queryProperty: {
        url: '/api/negotiations/:negotiationId/blockings/:id/properties'
      },
      saveProperty: {
        url: '/api/negotiations/:negotiationId/blockings/:id/properties',
        method: 'POST'
      },
      deleteProperty: {
        url: '/api/negotiations/:negotiationId/blockings/:blockingId/properties/:id/unassign',
        method: 'POST'
      },
      getBlockingConfirmation: {
        url: '/api/negotiations/:negotiationId/blockings/:blockingId/blocking-confirmation',
        method: 'POST',
        responseType: 'arraybuffer',
        transformResponse: function(data, headersGetter) {
          return {
            data: data,
            headers: headersGetter
          };
        }
      },
    }
  );
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Meeting', ["$resource", function($resource) {
  return $resource(
    '/api/meetings/:id',
    { negotiationId: '@negotiationId', id: '@id' },
    {
      queryByNegotiation: {
        url: '/api/negotiations/:negotiationId/meetings/:id',
        method: 'GET'
      },
      deleteProtocol: {
        url: '/api/meetings/:id/protocol/:blobId',
        method: 'DELETE'
      },
      queryMeetingsWithMe: {
        url: '/api/users/:partnerId/meetings-with/me',
        method: 'GET'
      },
      queryByUser: {
        url: '/api/users/:userId/meetings',
        method: 'GET',
        isArray: true
      }
    }
  );
}]);

})();
(function () {
/* eslint-disable no-undefined */
'use strict';

angular.module('hyresgastApp').factory('Agreement', ["$resource", function($resource) {
  return $resource(
    '/api/negotiations/:negotiationId/agreements/:id',
    {
      negotiationId: '@negotiationId',
      agreementId: '@agreementId',
      id: '@id'
    },
    {
      downloadDocument: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/documents/:id',
        method: 'GET',
        responseType: 'arraybuffer',
        transformResponse: function(data, headersGetter) {
          return {
            data: data,
            headers: headersGetter
          };
        }
      },
      deleteDocument: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/documents/:id',
        method: 'DELETE'
      },
      changeDocumentOrder: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/documents/:id/order',
        method: 'PUT'
      },
      queryDocument: {
        url: '/api/negotiations/:negotiationId/agreements/:id/documents',
        isArray: true
      },
      queryApartment: {
        url: '/api/negotiations/:negotiationId/agreements/:id/apartments'
      },
      saveApartment: {
        url: '/api/negotiations/:negotiationId/agreements/:id/apartments',
        method: 'POST'
      },
      deleteApartment: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/apartments/:id/unassign',
        method: 'POST'
      },
      queryProperty: {
        url: '/api/negotiations/:negotiationId/agreements/:id/properties'
      },
      saveProperty: {
        url: '/api/negotiations/:negotiationId/agreements/:id/properties',
        method: 'POST'
      },
      deleteProperty: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/properties/:id/unassign',
        method: 'POST'
      },
      importRentlist: {
        url: '/api/negotiations/:negotiationId/agreements/:id/rent-list/import',
        method: 'POST'
      },
      editRentlist: {
        url: '/api/negotiations/:negotiationId/agreements/:id/rent-list/edit',
        method: 'POST'
      },
      deleteAgreement: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId',
        method: 'DELETE'
      },
      getScriveStatus: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/scrive-status',
        method: 'GET'
      },
      signAgreement: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/sign-agreement',
        method: 'POST',
      },
      dropAgreement: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/drop-agreement',
        method: 'GET',
      },
      pullOut: {
        url: '/api/agreements/:agreementId/pull-out',
        method: 'GET',
      },
      getPreview: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/sign-agreement',
        method: 'POST',
        responseType: 'arraybuffer',
        transformResponse: function(data, headersGetter) {
          return {
            data: data,
            headers: headersGetter
          };
        }
      },
      getSignedAgreement: {
        url: '/api/negotiations/:negotiationId/agreements/:agreementId/export-agreement-pdf',
        method: 'GET',
        responseType: 'arraybuffer',
        transformResponse: function(data, headersGetter) {
          return {
            data: data,
            headers: headersGetter
          };
        }
      },
      find: {
        url: '/api/negotiations/agreements/find',
        method: 'GET',
      },
      getFigurants: {
        url: '/api/agreements/:agreementId/figurants',
        method: 'GET',
      },
      cancelAgreement: {
        url: '/api/agreements/:agreementId/cancel',
        method: 'GET',
      },
    }
  );
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('FreeAgreement', ["$resource", function($resource) {
  return $resource(
    '/api/agreements/:agreementId',
    {
      agreementId: '@agreementId',
      id: '@id',
      userId: '@userId',
    },
    {
      list: {
        url: '/api/agreements/list/:userId',
        method: 'GET',
      },
      find: {
        url: '/api/agreements/find',
        method: 'GET',
      },
      create: {
        url: '/api/agreements/create',
        method: 'POST',
      },
      finishCreate: {
        url: '/api/agreements/finish-create',
        method: 'POST',
      },
      changeParticipants: {
        url: '/api/agreements/change-participants',
        method: 'POST',
      },
      queryDocument: {
        url: '/api/agreements/:agreementId/documents',
        method: 'GET',
        isArray: true
      },
      queryProperty: {
        url: '/api/agreements/:agreementId/real-estate',
        method: 'GET',
      },
      deleteProperties: {
        url: '/api/agreements/:agreementId/real-estate/delete',
        method: 'POST'
      },
      mergeProperties: {
        url: '/api/agreements/:agreementId/real-estate',
        method: 'POST',
      },
      unlockPending: {
        url: '/api/agreements/:agreementId/unlock-pending',
        method: 'POST',
      },
      deletePending: {
        url: '/api/agreements/:agreementId/delete-pending',
        method: 'POST',
      },
      signAgreement: {
        url: '/api/agreements/:agreementId/sign-agreement',
        method: 'POST',
      },
      deleteAgreement: {
        url: '/api/agreements/:agreementId',
        method: 'DELETE'
      },
      dropAgreement: {
        url: '/api/agreements/:agreementId/drop-agreement',
        method: 'GET',
      },
      pullOut: {
        url: '/api/agreements/:agreementId/pull-out',
        method: 'GET',
      },
      downloadDocument: {
        url: '/api/agreements/:agreementId/documents/:id',
        method: 'GET',
        responseType: 'arraybuffer',
        transformResponse: function(data, headersGetter) {
          return {
            data: data,
            headers: headersGetter
          };
        }
      },
      deleteDocument: {
        url: '/api/agreements/:agreementId/documents/:id',
        method: 'DELETE'
      },
      changeDocumentOrder: {
        url: '/api/agreements/:agreementId/documents/:id/order',
        method: 'PUT'
      },
      getScriveStatus: {
        url: '/api/agreements/:agreementId/scrive-status',
        method: 'GET'
      },
      getSignedAgreement: {
        url: '/api/agreements/:agreementId/export-agreement-pdf',
        method: 'GET',
        responseType: 'arraybuffer',
        transformResponse: function(data, headersGetter) {
          return {
            data: data,
            headers: headersGetter
          };
        }
      },
      getPreview: {
        url: '/api/agreements/:agreementId/sign-agreement',
        method: 'POST',
        responseType: 'arraybuffer',
        transformResponse: function(data, headersGetter) {
          return {
            data: data,
            headers: headersGetter
          };
        }
      },
      getMyPendingCount: {
        url: '/api/agreements/get-my-pending-count',
        method: 'GET',
      },
      checkDelegation: {
        url: '/api/agreements/:id/delegation/check',
        method: 'POST'
      },
      inviteDelegation: {
        url: '/api/agreements/:id/delegation/invite',
        method: 'POST'
      },
      deleteDelegation: {
        url: '/api/agreements/:id/delegation/remove',
        method: 'POST'
      },
      addVoidUser: {
        url: '/api/agreements/:id/void-user',
        method: 'POST'
      },
      deleteVoidUser: {
        url: '/api/agreements/:id/void-user',
        method: 'DELETE'
      },
    }
  );
}]);

})();
(function () {
/* eslint-disable no-unused-vars */
'use strict';

// @desc
//   Directive is placed inside <main> and will appear when
//   when API returns a 401. It will also hide main content.
//
// @example
//   <main>
//      <hy-login></hy-login>
//      <ng-view class="main-content"></ng-view>
//   </main>

login.$inject = ["$document", "$rootScope", "$location", "$timeout", "Auth", "Flash"];
angular.module('hyresgastApp').directive('hyLogin', login);

function login($document, $rootScope, $location, $timeout, Auth, Flash) {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/login/login.html',
    scope: {},
    link: link
  };
  return directive;

  function link(scope, element, attrs) {
    var loginVisible;
    var mainContent = angular.element($document[0].querySelector('.main-content'));

    scope.login = login;
    scope.redirectToADFSIfHgf = redirectToADFSIfHgf;
    ////////////

    scope.$on('event:auth-loginRequired', function() {
      showLogin();
      scope.credentials = {};
      scope.loginForm.$setPristine();
      scope.loginForm.$setUntouched();
    });

    scope.$on('$locationChangeSuccess', function() {
      if (loginVisible) {
        cancelLogin();
      }
    });

    function login() {
      scope.saving = true;

      Auth.create(scope.credentials)
        .then(
          function(user) {
            hideLogin();
          },
          function(reason) {
            Flash.set('Kunde inte logga in. Kontrollera din e-post och ditt lösenord.', 'error');
            scope.credentials.password = null;
          }
        )
        .finally(function() {
          scope.saving = false;
          scope.loginForm.$setPristine();
          scope.loginForm.$setUntouched();
        });
    }

    function showLogin() {
      Auth.resetCurrentUser();
      var tooltip = angular.element($document[0].querySelector('.tooltip'));

      loginVisible = true;
      element.addClass('login--visible');
      mainContent.addClass('main-content--hidden');
      if (tooltip) {
        tooltip.remove();
      }
    }

    function cancelLogin() {
      Auth.cancel();
      scope.credentials = {};
      scope.loginForm.$setPristine();
      scope.loginForm.$setUntouched();
      hideLogin();
    }

    function hideLogin() {
      loginVisible = false;
      mainContent.removeClass('main-content--hidden');
      element.removeClass('login--visible');
    }

    function redirectToADFSIfHgf() {
      if (scope.credentials.email && scope.credentials.email.match(/@hyresgastforeningen\.se$/i)) {
        scope.redirecting = true;
        $timeout(function() {
          scope.redirecting = false;
          window.location.href =
            window.location.hostname === 'localhost' ? 'http://localhost:3200/api/sessions/auth' : '/api/sessions/auth';
        }, 1337);
      }
    }
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Negotiation', ["$resource", function($resource) {
  return $resource(
    '/api/negotiations/:id',
    { id: '@id' },
    {
      query: {
        url: '/api/negotiations',
        isArray: false
      },
      getDelegateRoles: {
        url: '/api/negotiations/delegate-roles',
        method: 'GET',
      },
      finish: {
        url: '/api/negotiations/:id/finish',
        isArray: false,
        method: 'POST'
      },
      finishAsHgfUser: {
        url: '/api/negotiations/:id/finish-hgf',
        isArray: false,
        method: 'POST'
      },
      reopen: {
        url: '/api/negotiations/:id/reopen',
        isArray: false,
        method: 'POST'
      },
      listMeetings: {
        url: '/api/negotiations/:id/meetings',
        isArray: true
      },
      listDocuments: {
        url: '/api/negotiations/:id/documents',
        isArray: false
      },
      deleteDocument: {
        url: '/api/negotiations/:negotiationId/documents/:id',
        method: 'DELETE'
      },
      saveParticipants: {
        url: '/api/negotiations/:id/participants',
        method: 'POST'
      },
      checkDelegation: {
        url: '/api/negotiations/:id/delegation/check',
        method: 'POST'
      },
      inviteDelegation: {
        url: '/api/negotiations/:id/delegation/invite',
        method: 'POST'
      },
      deleteDelegation: {
        url: '/api/negotiations/:id/delegation/remove',
        method: 'POST'
      },
      queryDistrict: {
        url: '/api/negotiations/:id/districts',
        isArray: false
      },
      queryProperty: {
        url: '/api/negotiations/:id/properties',
        isArray: false,
        method: 'POST'
      },
      // used in the agreement wizard, sends checkbox states in request body
      queryPropertyBySelection: {
        url: '/api/negotiations/:id/properties',
        isArray: false,
        method: 'POST'
      },
      queryHistory: {
        url: '/api/negotiations/:id/history',
        isArray: true
      },
      countApartment: {
        url: '/api/negotiations/:id/rent-list/apartment-count',
        method: 'GET'
      },
      queryApartment: {
        url: '/api/negotiations/:id/apartments',
        isArray: false,
        method: 'POST'
      },
      importRentList: {
        url: '/api/negotiations/:id/rent-list/import',
        method: 'POST'
      },
      forgeRentList: {
        url: '/api/negotiations/:id/rent-list/forge',
        method: 'POST',
      },
      queryByChief: {
        url: '/api/negotiations/by-chief-negotiator',
        method: 'GET'
      },
      queryNegotiationsByParticipant: {
        url: '/api/negotiations/by-participants',
        method: 'GET'
      },
      savePropertyDemands: {
        url: '/api/negotiations/:id/properties/demands',
        method: 'POST'
      },
      queryWithoutRentList: {
        url: '/api/negotiations/:id/properties/without-rent-list',
        method: 'GET'
      },
      moveItems: {
        url: '/api/negotiations/:id/move-items',
        method: 'POST'
      },
      createNewNegotiationAndMoveItems: {
        url: '/api/negotiations/:id/move-items-create-new-negotiation',
        method: 'POST'
      },
      recallItems: {
        url: '/api/negotiations/:id/recall-items',
        method: 'POST'
      },
      addProperties: {
        url: '/api/negotiations/:id/add-properties',
        method: 'POST'
      },
      queryApartmentsInProperty: {
        url: '/api/negotiations/:negotiationId/properties/:id/apartments',
        method: 'GET'
      },
      createDirect: {
        url: '/api/negotiations/create-direct',
        method: 'POST'
      },
      delete: {
        url: '/api/negotiations/:id',
        method: 'DELETE',
      },
      unforgeRentlist: {
        url: '/api/negotiations/:id/rent-list/unforge',
        method: 'POST',
      },
      changeNegotiators: {
        url: '/api/negotiations/change-negotiators',
        method: 'POST',
      },
      getSeenbyCounters: {
        url: '/api/negotiations/counters-seenby',
        method: 'GET',
      },
      saveComments: {
        url: '/api/negotiations/comments',
        method: 'POST',
      }
    }
  );
}]);

})();
(function () {
'use strict';

hyMicroblog.$inject = ["$document", "$timeout", "Microblog", "HyUtils", "Auth"];
angular.module('hyresgastApp').directive('stopPropagation', function() {
  return {
    restrict: 'A',
    link: function(scope, element) {
      element.on('click', function(event) {
        event.stopPropagation();
      });
    }
  };
});

angular.module('hyresgastApp').directive('hyMicroblog', hyMicroblog);

function hyMicroblog($document, $timeout, Microblog, HyUtils, Auth) {
  var directive = {
    restrict: 'E',
    templateUrl: 'components/microblog/microblog.html',
    scope: {
      objectId: '=',
      objectType: '=',
      count: '=?',
    },
    // eslint-disable-next-line no-unused-vars
    link: (scope, element, attrs) => {

      // eslint-disable-next-line no-undef
      var converter = new showdown.Converter({
        simplifiedAutoLink: true,
        literalMidWordUnderscores: true
      });

      let makeHtml = text => {
        let html = converter.makeHtml(text);
        html = html.replace(/<a /g, '<a target="_blank" ');
        return html;
      }

      scope.Auth = Auth;
      scope.gd = twit => new Date(twit.updatedAt ? twit.updatedAt : twit.createdAt);

      scope.sortTwits = function() {
        scope.twits.sort((a, b) => scope.gd(b) - scope.gd(a));
        scope.count = scope.twits.length;
      }

      scope.hover = function(twit) {
        for(let t of scope.twits) {
          t.hovered = false;
        }
        twit.hovered = true;
      }

      scope.saveNew = () => {
        Microblog.create({
          objectType: scope.objectType,
          objectId: scope.objectId,
          text: scope.newTwit.text,
        }).$promise.then(response => {
          scope.newTwit.text = '';
          response.html = makeHtml(response.text);
          scope.twits.push(response);
          scope.sortTwits();
        });
      };

      scope.update = twit => {
        Microblog.update({
          id: twit.id,
          text: twit.text,
        }).$promise.then(() => {
          twit.updatedAt = new Date();
          twit.dirty = false;
          twit.html = makeHtml(twit.text);
          twit.editing = false;
          scope.sortTwits();
        });
      };

      scope.edit = twit => {
        scope.twits.forEach(t => t.editing = false);
        twit.editing = true;
        $timeout(() => {
          $document[0].getElementById(`twit-${twit.id}`).focus();
        }, 500);
      };

      scope.destroy = twit => {

        let message = `Vill du verkligen radera anteckningen?
        Radera inte andras anteckningar utan att informera berörd person.
        Väljer du att radera anteckningen tas den bort från databasen och kan inte återställas.`;

        HyUtils.showDefaultModal('WarningConfirmationModalCtrl',
          'components/modal/confirmation/warningConfirmationModal.html',
          {
            message,
            button: 'Ta bort',
            critical: true,
          },
          // eslint-disable-next-line no-unused-vars
          result => {

            Microblog.destroy({ id: twit.id })
              .$promise.then(() => {
                scope.twits = scope.twits.filter(x => x.id !== twit.id);
                scope.sortTwits();
              });
          }, null, null, { size: 'md' });
      };

      scope.getTwits = function () {
        Microblog.list({
          objectId: scope.objectId,
        }).$promise.then(response => {
          scope.twits = response.rows;
          scope.twits.forEach(twit => {
            twit.html = makeHtml(twit.text);
          });
          scope.sortTwits();
        });
      }

      scope.getTwits();
    }
  }
  return directive;
}
})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Office', ["$resource", function($resource) {
  return $resource('/api/offices/:id', { id: '@id' });
}]);

})();
(function () {
'use strict';

// 'Påkallan' in Swedish

angular.module('hyresgastApp').factory('NegotiationCall', ["$resource", function($resource) {
  return $resource(
    '/api/negotiation-calls/:id',
    { id: '@id', blobId: '@blobId' },
    {
      query: {
        url: '/api/negotiation-calls',
        isArray: false
      },
      queryProperty: {
        url: '/api/negotiation-calls/:id/properties',
        method: 'POST'
      },
      queryApartment: {
        url: '/api/negotiation-calls/:id/apartments',
        method: 'POST'
      },
      queryPropertyByRentList: {
        url: '/api/negotiation-calls/rent-list/:blobId/properties',
        method: 'GET'
      },
      queryPropertyByPropertyList: {
        url: '/api/negotiation-calls/property-list/:blobId/properties',
        method: 'GET'
      },
      queryPropertyListValidation: {
        url: '/api/negotiation-calls/property-list/:blobId/validate',
        method: 'GET',
        isArray: false
      },
      getPropertyListErrorReport: {
        url: '/api/negotiation-calls/property-list/:blobId/validate-excel',
        method: 'GET',
        responseType: 'arraybuffer',
        transformResponse: (data, headers) => ({ data, headers })
      },
      queryApartmentByRentList: {
        url: '/api/negotiation-calls/rent-list/:blobId/apartments',
        method: 'GET'
      },
      queryRentListValidation: {
        url: '/api/negotiation-calls/rent-list/:blobId/validate',
        method: 'GET',
        isArray: false
      },
      getRentListErrorReport: {
        url: '/api/negotiation-calls/rent-list/:blobId/validate-excel',
        method: 'GET',
        responseType: 'arraybuffer',
        transformResponse: (data, headers) => ({ data, headers })
      },
      queryDistrict: {
        url: '/api/negotiation-calls/:id/districts'
      },
      createNegotiation: {
        url: '/api/negotiation-calls/:id/create-negotiation',
        method: 'POST'
      },
      returnNegotiation: {
        url: '/api/negotiation-calls/:id/respond',
        method: 'POST'
      },
      queryNegotiation: {
        url: '/api/negotiation-calls/:id/negotiations'
      },
      queryErrors: {
        url: '/api/negotiation-calls/:id/possible-errors',
        method: 'GET'
      },
      queryErrorsInProperties: {
        url: '/api/real-estate/properties/possible-negotiation-conflicts',
        method: 'POST'
      },
      queryErrorsInApartments: {
        url: '/api/real-estate/apartments/possible-negotiation-conflicts',
        method: 'POST'
      },
      listDocuments: {
        url: '/api/negotiation-calls/:id/documents',
        isArray: true
      },
      deleteDocument: {
        url: '/api/negotiation-calls/:id/documents/:blobId',
        method: 'DELETE'
      },
      getApartmentsSummaryBySelection: {
        url: '/api/negotiation-calls/:id/properties/summary',
        isArray: false,
        method: 'POST'
      },
      setOffice: {
        url: '/api/negotiation-calls/:id/office',
        method: 'POST'
      },
      updateHandler: {
        url: '/api/negotiation-calls/:id/update-handler',
        method: 'POST'
      },
      delete: {
        url: '/api/negotiation-calls/:id',
        method: 'DELETE',
      },
    }
  );
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('Organization', ["$resource", function($resource) {
  return $resource(
    '/api/organizations/:id',
    { id: '@id', organizationId: '@organizationId', email: '@email' },
    {
      query: {
        url: '/api/organizations',
        isArray: false
      },
      approve: {
        url: '/api/organizations/:id/approve',
        method: 'POST'
      },
      queryUser: {
        url: '/api/organizations/:id/users',
        isArray: true
      },
      updateUserRole: {
        url: '/api/organizations/:organizationId/users/:id',
        method: 'POST'
      },
      deleteUser: {
        url: '/api/organizations/:organizationId/users/:id',
        method: 'DELETE'
      },
      queryInvite: {
        url: '/api/organizations/:id/users/invited',
        isArray: true
      },
      deleteInvite: {
        url: '/api/organizations/:organizationId/users/invited/:id',
        method: 'DELETE'
      },
      saveInvite: {
        url: '/api/organizations/:id/users',
        method: 'POST'
      },
      getApartmentsSummary: {
        url: '/api/organizations/:id/apartments/summary',
        isArray: false
      },
      getApartmentsSummaryBySelection: {
        url: '/api/organizations/:id/apartments/summary',
        isArray: false,
        method: 'POST'
      },
      queryDistrict: {
        url: '/api/organizations/:id/districts'
      },
      queryProperty: {
        url: '/api/organizations/:id/districts/properties',
        method: 'POST'
      },
      queryByOrganizationNumber: {
        url: '/api/organizations/by-organization-number'
      },
      queryProperties: {
        url: '/api/organizations/:id/properties',
        method: 'GET'
      },
      unbindProperty: {
        url: '/api/organizations/:id/properties/:propertyId',
        method: 'DELETE'
      }
    }
  );
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('RealEstate', ["$resource", function($resource) {
  return $resource(
    '/api/real-estate/properties/:id',
    { id: '@id' },
    {
      query: {
        url: '/api/real-estate/properties',
        isArray: false,
      },
      querySimple: {
        url: '/api/real-estate/properties-simple',
        isArray: false,
      },
      queryPropertiesInMyNegotiations: {
        url: '/api/real-estate/properties/in-my-negotiations',
        method: 'GET',
      },
      queryPropertiesInNegotiationsWithMe: {
        url: '/api/real-estate/properties/in-my-negotiations/with-user/:partnerId',
        method: 'GET',
      },
      queryNegotiationsForProperty: {
        url: '/api/real-estate/properties/:id/negotiations',
        method: 'GET',
      },
      queryApartmentsInProperty: {
        url: '/api/real-estate/properties/:id/apartments',
        method: 'GET',
      },
      queryPropertiesStatusCount: {
        url: '/api/real-estate/properties/status-count',
        method: 'POST',
      },
      queryFreeAgreementsForProperty: {
        url: '/api/real-estate/properties/:id/agreements',
        method: 'GET',
      },
      getApartmentHistory: {
        url: '/api/real-estate/apartments/:id/history',
        method: 'GET',
      },
      getObjects: {
        url: '/api/real-estate/objects',
        method: 'GET'
      },
      queryOwners: {
        url: '/api/real-estate/owners',
        method: 'GET',
      },
      findByExternalId: {
        url: '/api/real-estate/properties/findByExternalId/:id',
        method: 'GET',
      },
      calculateNH: {
        url: '/api/real-estate/calc-nh',
        method: 'POST',
      },
    }
  );
}]);

})();
(function () {
'use strict';

RequestedAccountsCtrl.$inject = ["$scope", "$location", "$routeParams", "Auth", "RequestedAccount", "Parties", "SubPage"];
angular.module('hyresgastApp').controller('RequestedAccountsCtrl', RequestedAccountsCtrl);

function RequestedAccountsCtrl($scope, $location, $routeParams, Auth, RequestedAccount, Parties, SubPage) {
  var vm = this;
  vm.userParties = Parties;

  getCurrentSubpage();

  $scope.$on('$routeUpdate', function() {
    // avoid unecessary calling of getCurrentSubpage(); when sorting
    if (!$location.search().order) {
      // reset offset when sorting
      getCurrentSubpage();
    }
  });

  function getCurrentSubpage() {
    var defaultSubPage = 'organizations';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = ['external'];
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('RequestedAccount', ["$resource", function($resource) {
  return $resource(
    '/api/requested-accounts',
    { id: '@id', organizationId: '@organizationId' },
    {
      query: {
        url: '/api/organizations/requested',
        isArray: false
      }
    }
  );
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('SpecUser', ["$resource", function($resource) {
  return $resource(
    '/api/specops/users', { id: '@id' },
    {
      createUser: {
        url: '/api/specops/users/create-user',
        method: 'POST'
      },
      updateUser: {
        url: '/api/specops/users/:id',
        method: 'POST'
      },
      deleteUser: {
        url: '/api/specops/users/:id',
        method: 'DELETE'
      },
    }
  );
}]);

})();
(function () {
'use strict';

// Used in App.js to redirect users depending on their party
// if they have access rights to a given route/page

angular.module('hyresgastApp').factory('UserPermissions', ["Auth", function(Auth) {
  return function(permissions) {
    return Auth.heartbeat(true).then(function() {
      if (!Auth.userHasPermissionForView(permissions)) {
        return '/403';
      }
    });
  };
}]);

})();
(function () {
'use strict';

// Used in App.js to redirect users depending on role

angular.module('hyresgastApp').factory('UserRedirect', ["$location", "Auth", "Parties", function($location, Auth, Parties) {
  return function() {
    Auth.heartbeat().then(function() {
      if (Auth.currentUser().party === Parties.HGF) {
        $location.path('/users/' + Auth.currentUser().id + '/welcome');
      } else if (Auth.currentUser().party === Parties.EXTERNAL) {
        // FÄ
        $location.path('/users/' + Auth.currentUser().id + '/welcome');
      } else {
        // Delegate
        $location.path('/negotiations');
      }
    });
  };
}]);

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('User', ["$resource", function($resource) {
  return $resource(
    '/api/users/:id',
    { id: '@id', organizationId: '@organizationId', email: '@email' },
    {
      query: {
        url: '/api/users',
        isArray: false
      },
      queryOp: {
        url: '/api/users/op',
        isArray: false
      },
      queryByRegion: {
        url: '/api/users/by-region/:regionId'
      },
      queryRequestedByOrganization: {
        url: '/api/users/requested-by-organization/:organizationId',
        isArray: false
      },
      setPassword: {
        url: '/api/users/:id/set-password',
        method: 'POST'
      },
      resetPassword: {
        url: '/api/users/reset-password',
        method: 'POST'
      },
      setPasswordWithToken: {
        url: '/api/users/:id/set-password-with-token',
        method: 'POST'
      },
      queryOrganization: {
        url: '/api/users/:id/organizations',
        isArray: true
      },
      queryNegotiationTables: {
        url: '/api/users/negotiation-tables',
        method: 'GET'
      },
      queryNegotiationPartners: {
        url: '/api/users/me/negotiation-partners',
        method: 'GET'
      },
      queryNegotiationsWithMe: {
        url: '/api/negotiations/mine/with-user/:partnerId',
        method: 'GET'
      },
      verifyEmail: {
        url: '/api/users/verify-email',
        method: 'POST'
      },
      getPhoneByInvite: {
        url: '/api/users/get-phone-by-invite',
        method: 'GET'
      },
      checkEmail: {
        url: '/api/users/check-email',
        method: 'POST'
      },
      hgfOverview: {
        url: '/api/users/hgf-overview',
        method: 'GET',
      },
      exportProperties: {
        url: '/api/users/export-properties',
        method: 'POST',
      },
      getMyCounters: {
        url: '/api/users/my-counters',
        method: 'GET',
      },
    }
  );
}]);

})();
(function () {
'use strict';

ErrorModalCtrl.$inject = ["$uibModalInstance", "RentFiles", "NegotiationCall", "HyUtils", "validationResponse", "realEstateChoiceMethod"];
angular.module('hyresgastApp').controller('ErrorModalCtrl', ErrorModalCtrl);

function ErrorModalCtrl($uibModalInstance, RentFiles, NegotiationCall, HyUtils, validationResponse, realEstateChoiceMethod) {
  var vm = this;
  vm.status = validationResponse;
  vm.showDetailedInfo = vm.status.total ? vm.status.total > 5000 : false;

  vm.hasCommonFailure = vm.status.failedPhase ? true : false;
  vm.missingMandatoryColumns = !vm.hasCommonFailure && vm.status.missingMandatoryColumns && vm.status.missingMandatoryColumns.length > 0;
  vm.wrongApartments = !vm.hasCommonFailure && !vm.missingMandatoryColumns && (vm.status.summary.numApartments === null || vm.status.summary.numApartments === 0);

  vm.showDownloadReport = vm.status.missingMandatoryColumns ? vm.status.missingMandatoryColumns.length === 0 : (!vm.hasCommonFailure && !vm.wrongApartments);

  let sources = 0;
  let errorTypeNames = [
    "failedRows",
    "duplicates",
    "nulls",
    "missingApartmentsFromAgreement",
    "misspelledMunicipalities",
    "misspelledPropertyDesignations",
    "notExistingApartments",
    "misspelledAddresses",
    "alreadyAssigned",
  ];

  for (let i = 0; i < errorTypeNames.length; i++) {
    if (vm.status[errorTypeNames[i]] && vm.status[errorTypeNames[i]].length > 0) {
      sources++;
    }
  }

  vm.hasMultipleErrors = !vm.hasCommonFailure && !vm.missingMandatoryColumns && !vm.wrongApartments && sources > 1;
  let convert = (name) => { return "has" + name.charAt(0).toUpperCase() + name.slice(1); };
  for (let i = 0; i < errorTypeNames.length; i++) {
    vm[convert(errorTypeNames[i])] = sources === 1 && vm.status[errorTypeNames[i]] && vm.status[errorTypeNames[i]].length > 0;
  }

  vm.cancel = cancel;

  vm.validationExcelId = validationResponse.blobId;

  vm.downloadErrorReport = () => {
    if (realEstateChoiceMethod === 'RENT_LIST') {
      NegotiationCall.getRentListErrorReport({
        blobId: vm.validationExcelId,
      }).$promise.then(response => {
        HyUtils.spitOutAttachment(response.data, 'error_report.xlsx', 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet');
      });
    } else if (realEstateChoiceMethod === 'PROPERTY_LIST') {
      NegotiationCall.getPropertyListErrorReport({
        blobId: vm.validationExcelId,
      }).$promise.then(response => {
        HyUtils.spitOutAttachment(response.data, 'error_report.xlsx', 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet');
      });
    } else {
      RentFiles.getErrorReport({
        blobId: vm.validationExcelId,
      }).$promise.then(response => {
        HyUtils.spitOutAttachment(response.data, 'error_report.xlsx', 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet');
      });
    }
  };

  function cancel() {
    $uibModalInstance.dismiss();
  }
}

})();
(function () {
'use strict';

angular.module('hyresgastApp').factory('RentFiles', ["$resource", function ($resource) {
  return $resource(
    '/api/rent-files',
    { blobId: '@blobId' },
    {
      getErrorReport: {
        url: '/api/rent-files/:blobId/validate-excel',
        method: 'GET',
        responseType: 'arraybuffer',
        transformResponse: (data, headers) => ({ data, headers })
      },
    }
  );
}]);

})();
(function () {
'use strict';

EditBlockingOverviewModalCtrl.$inject = ["$uibModalInstance", "blockingObject", "Blocking", "Flash", "$location"];
angular.module('hyresgastApp').controller('EditBlockingOverviewModalCtrl', EditBlockingOverviewModalCtrl);

function EditBlockingOverviewModalCtrl($uibModalInstance, blockingObject, Blocking, Flash, $location) {
  var vm = this;
  vm.blocking = angular.copy(blockingObject);

  vm.cancel = cancel;
  vm.save = save;

  /////////

  function save() {
    vm.saving = true;
    Blocking.save(vm.blocking)
      .$promise.then(
        function(response) {
          angular.extend(blockingObject, vm.blocking);
          $uibModalInstance.close();
        })
      .catch(function(reason) {
        $uibModalInstance.dismiss('error');
        if(reason.status === 403) {
          $location.path('/403')
        } else {
          Flash.set('Misslyckades att spara', 'error');
        }
      })
      .finally(function() {
        vm.saving = false;
      });
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

NewBlockingCtrl.$inject = ["$scope", "$location", "$routeParams", "Blocking", "Negotiation", "Organization", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('NewBlockingCtrl', NewBlockingCtrl);

function NewBlockingCtrl($scope, $location, $routeParams, Blocking, Negotiation, Organization, Flash, ErrorHandler) {
  var vm = this;

  vm.blocking = {
    negotiationId: $routeParams.id,
    blockedBy: $location.search().blockedBy,
    documents: [],
    name: '', // there is a not null constraing on api
    isAllDistrictsSelected: true,
    isAllPropertiesSelected: true,
    isAllApartmentsSelected: true
  };

  vm.gotoNextStep = gotoNextStep;
  vm.save = save;

  // Fetch districts when the user wants to sort by column or direction
  $scope.$watchGroup(['vm.sortDistrictColumn', 'vm.sortDistrictDirection'], function() {
    // Check for undefined 'order' parameter in the URL
    if ($location.search().order) {
      // Reset the offset when changing column or direction of sorting
      vm.districtPageOffset = 0;
      queryDistrict();
    }
  });

  // Fetch properties when the user wants to sort by column or direction
  $scope.$watchGroup(['vm.sortPropertiesColumn', 'vm.sortPropertiesDirection'], function() {
    // Check for undefined 'order' parameter in the URL
    if ($location.search().order) {
      // Reset the offset when changing column or direction of sorting
      vm.propertiesPageOffset = 0;
      queryProperties();
    }
  });

  // Fetch apartments when the user wants to sort by column or direction
  $scope.$watchGroup(['vm.sortApartmentsColumn', 'vm.sortApartmentsDirection'], function() {
    // Check for undefined 'order' parameter in the URL
    if ($location.search().order) {
      // Reset the offset when changing column or direction of sorting
      vm.apartmentsPageOffset = 0;
      queryApartments();
    }
  });

  vm.districtPageOffset = 0;
  vm.propertiesPageOffset = 0;
  vm.apartmentsPageOffset = 0;
  vm.propertiesPageLimit = 30;
  vm.districtPageLimit = 30;
  vm.apartmentsPageLimit = 30;
  vm.blocking.selectedDistrictIds = {};
  vm.blocking.selectedPropertyIds = {};
  vm.blocking.selectedApartmentIds = {};
  vm.queryProperties = queryProperties;
  vm.queryDistrict = queryDistrict;
  vm.queryApartments = queryApartments;

  vm.sortPropertiesColumn = $location.search().order || 'propertyDesignation'; // default
  vm.sortPropertiesDirection = $location.search().direction || 'ASC'; // default
  vm.listPropertiesColumns = [
    { title: 'Fastigheter', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments' }
  ];

  vm.sortDistrictColumn = $location.search().order || 'district'; // default
  vm.sortDistrictDirection = $location.search().direction || 'ASC'; // default
  vm.listDistrictColumns = [
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal fastigheter', column: 'noProperties' }
  ];

  vm.sortApartmentsColumn = $location.search().order || 'property.propertyDesignation'; // default
  vm.sortApartmentsDirection = $location.search().direction || 'ASC'; // default
  vm.listApartmentsColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Fastighet', column: 'property.propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' }
  ];

  queryDistrict();
  getNegotiation();
  // getApartmentsSummary(id);

  // Keep it simple - fetch the properties when navigating to the properties step
  // instead of i.e fetching every time a districts checkbox is checked
  $scope.$on('$routeUpdate', function() {
    if ($location.search().step === 'properties') {
      vm.propertiesPageOffset = 0; // always show first items
      // aFlag is used because when we filter the result from queryProperties(),
      // we don't want selectedPropertyIds = {}; to be reseted.
      // We want reset only when the route is changed and response.count != vm.propertyTotalCount.
      vm.propertyFlag = true;
      queryProperties();
    }

    if ($location.search().step === 'apartments') {
      vm.apartmentsPageOffset = 0;
      vm.apartmentsFlag = true;
      queryApartments();
    }
  });

  ////////////

  function save() {
    vm.saving = true;
    Blocking.save(vm.blocking)
      .$promise.then(
        // eslint-disable-next-line no-unused-vars
        function(response) {
          $location.search({}); // remove parameters set by wizard
          $location.path('/negotiations/' + vm.blocking.negotiationId).search('view', 'agreements');
          Flash.set('Fastigheter i förhandlingen har strandats', 'success');
        })
      .catch(ErrorHandler('Misslyckades att stranda förhandlingen.'))
      .finally(function() {
        vm.saving = false;
      });
  }

  function gotoNextStep() {
    vm.gotoNext = true;
  }

  function getNegotiation() {
    return Negotiation.get({
      id: $routeParams.id
    })
      .$promise.then(function(response) {
        vm.negotiation = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function() {
        vm.readyNegotiation = true;
      });
  }

  function queryDistrict(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.districtPageOffset = 0;
    }
    return Negotiation.queryDistrict({
      id: $routeParams.id,
      limit: vm.districtPageLimit,
      offset: vm.districtPageOffset,
      order: vm.sortDistrictColumn,
      direction: vm.sortDistrictDirection,
      'search[municipality]': vm.inputDistrictTextMunicipality,
      'search[district]': vm.inputDistrictTextDistrict,
      unassigned: true
    })
      .$promise.then(function(response) {
        vm.districts = response.rows;
        vm.districtTotalCount = response.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta område.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function queryProperties(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.propertiesPageOffset = 0;
    }
    return Negotiation.queryProperty(
      {
        id: $routeParams.id,
        limit: vm.propertiesPageLimit,
        offset: vm.propertiesPageOffset,
        order: vm.sortPropertiesColumn,
        direction: vm.sortPropertiesDirection,
        'search[propertyDesignation]': vm.inputPropertiesTextProperty,
        'search[municipality]': vm.inputPropertiesTextMunicipality,
        'search[district]': vm.inputPropertiesTextDistrict,
      },
      {
        selectedDistrictIds: vm.blocking.selectedDistrictIds,
        isAllDistrictsSelected: vm.blocking.isAllDistrictsSelected,
        unassigned: true
      }
    )
      .$promise.then(function(response) {
        // If the count of properties has changed from one query to the next,
        // then the user has gone back to the district selector and chosen a different set
        // of districts (or we've never loaded the properties before).
        // When this happens, we need to reset the selection status, because otherwise the
        // "master select box" will not detect the everything/nothing/partial selection states
        // properly, because it'll have the wrong idea of what the full selection is.
        if (vm.propertyFlag && vm.propertyTotalCount !== response.count) {
          vm.blocking.selectedPropertyIds = {};
          vm.propertyFlag = false;
        }
        vm.properties = response.rows;
        vm.propertyTotalCount = response.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta fastigheter.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function queryApartments(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.apartmentsPageOffset = 0;
    }
    return Negotiation.queryApartment(
      {
        id: $routeParams.id,
        limit: vm.apartmentsPageLimit,
        offset: vm.apartmentsPageOffset,
        order: vm.sortApartmentsColumn,
        direction: vm.sortApartmentsDirection,
        'search[nationalNo]': vm.inputApartmentTextNationalApartmentNo,
        'search[ownerNo]': vm.inputApartmentTextApartmentNo,
        'search[streetAddress]': vm.inputApartmentTextStreetAddress,
        'search[property.propertyDesignation]': vm.inputApartmentTextPropertyDesignation,
        'search[municipality]': vm.inputApartmentTextMunicipality,
        'search[district]': vm.inputApartmentTextDistrict,
        unassigned: true
      },
      {
        selectedDistrictIds: vm.blocking.selectedDistrictIds,
        selectedPropertyIds: vm.blocking.selectedPropertyIds,
        isAllDistrictsSelected: vm.blocking.isAllDistrictsSelected,
        isAllPropertiesSelected: vm.blocking.isAllPropertiesSelected
      }
    )
      .$promise.then(function(response) {
        // If the count of properties has changed from one query to the next,
        // then the user has gone back to the district selector and chosen a different set
        // of districts (or we've never loaded the properties before).
        // When this happens, we need to reset the selection status, because otherwise the
        // "master select box" will not detect the everything/nothing/partial selection states
        // properly, because it'll have the wrong idea of what the full selection is.
        if (vm.apartmentsFlag && vm.apartmentTotalCount !== response.count) {
          vm.blocking.selectedApartmentIds = {};
          vm.apartmentsFlag = false;
        }
        vm.apartments = response.rows;
        vm.apartmentTotalCount = response.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta lägenheter.'))
      .finally(function() {
        vm.ready = true;
      });
  }
}

})();
(function () {
'use strict';

//TODO: move to fe-common

WarningConfirmationModalCtrl.$inject = ["$uibModalInstance", "message", "button", "critical"];
angular.module('hyresgastApp').controller('WarningConfirmationModalCtrl', WarningConfirmationModalCtrl);

function WarningConfirmationModalCtrl($uibModalInstance, message, button, critical) {
  var vm = this;

  vm.message = message;
  vm.button = button;
  vm.critical = critical;

  vm.confirm = function() {
    $uibModalInstance.close();
  };

  vm.cancel = function() {
    $uibModalInstance.dismiss('cancel');
  };
}

})();
(function () {
'use strict';

FreeAgreementsListCtrl.$inject = ["$scope", "$location", "$window", "$uibModal", "$routeParams", "FreeAgreement", "ErrorHandler", "HyUtils", "Auth", "SubPage", "FreeAgreementTypes", "NegotiationRoles"];
angular.module('hyresgastApp').controller('FreeAgreementsListCtrl', FreeAgreementsListCtrl);

function FreeAgreementsListCtrl($scope, $location, $window, $uibModal, $routeParams,
  FreeAgreement, ErrorHandler,
  HyUtils, Auth, SubPage,
  FreeAgreementTypes, NegotiationRoles) {
  var vm = this;

  if (Auth.isHgfUser()) {
    vm.headerButtons = [
      {
        title: 'Skapa avtal',
        url: '/agreements/new',
        isVisible: () => Auth && Auth.canDo('createAgreement'),
        icon: 'agreement',
      },
      {
        title: 'Intern E-signering',
        url: '/agreements/new?internal=true',
        isVisible: () => Auth && Auth.canDo('createAgreement'),
        icon: 'agreement',
      }
    ];
  } else {
    vm.headerButtons = [
      {
        title: 'Nytt avtal',
        click: () => { vm.newAgreement(); },
        url: '#',
        isVisible: () => Auth && Auth.canDo('createAgreement'),
        icon: 'agreement',
      }
    ];
  }
  vm.tablist = [
    { title: 'Mina', path: '/agreements' },
  ];

  if (Auth.isHgfUser()) {
    vm.tablist.push({ title: 'På mina kontor', path: '/agreements?view=my-office' });
    vm.tablist.push({ title: 'Alla', path: '/agreements?view=all' });
  }

  getCurrentSubpage();

  $scope.$on('$routeUpdate', function() {
    // avoid unecessary calling of getCurrentSubpage(); when sorting
    if (!$location.search().order) {
      // reset offset when sorting
      getCurrentSubpage();
    }
    vm.refreshList();
  });

  function getCurrentSubpage() {
    var defaultSubPage = 'my';
    var whiteList = ['my', 'my-office'];
    if (Auth.isHgfUser()) {
      whiteList.push('all');
    }
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }

  vm.FreeAgreementTypes = Object.values(FreeAgreementTypes)
    .reduce((dict, el) => { dict[el.code] = el.name; return dict; }, {});

  function setUserString(agreements) {
    agreements.forEach(agreement => {
      var chiefHGF = agreement.users.find(u => NegotiationRoles.HGF_ASSOC_CHIEF.code === u.agreementRole);
      var chiefExternal = agreement.users.find(u => [NegotiationRoles.FA_POWNER_CHIEF.code, NegotiationRoles.OMBUD_FA_POWNER_CHIEF.code].includes(u.agreementRole));
      if (chiefHGF && chiefExternal) {
        agreement.user1 = {
          name: `${chiefHGF.firstname} ${chiefHGF.lastname}`,
          email: chiefHGF.email,
        };
        agreement.user2 = {
          name: `${chiefExternal.firstname} ${chiefExternal.lastname}`,
          email: chiefExternal.email,
        };
      }
    });
  }

  vm.refreshList = () => {
    let params = {};
    let isHgf = Auth.isHgfUser();

    if (vm.currentSubpage !== 'all') {
      params.userId = Auth.currentUser().id;
    }
    if (vm.currentSubpage === 'my-office') {
      params.myOffice = true;
    }

    FreeAgreement.list(params).$promise.then(results => {
      results.agreements.forEach(a => a.conditionTags = JSON.parse(a.conditionTags));
      vm.agreements = results.agreements.filter(a => !a.signed);
      vm.signedAgreements = results.agreements.filter(a => a.signed);
      setUserString(vm.agreements);
      setUserString(vm.signedAgreements);

      params.showPending = true;
      FreeAgreement.list(params).$promise.then(results => {
        vm.pendingAgreements = results.agreements;
        setUserString(vm.pendingAgreements);
      });

      vm.canApprovePending = isHgf;
      vm.canDeletePending = isHgf || vm.currentSubpage === 'my';
    });
  };

  vm.deleteNew = id => FreeAgreement.deletePending({ agreementId: id });

  vm.newAgreement = () => {
    let initialUser = angular.copy(Auth.currentUser());
    initialUser.userId = initialUser.id;
    initialUser.roleObj = Auth.isHgfUser() ? NegotiationRoles.HGF_ASSOC_CHIEF : NegotiationRoles.FA_POWNER_CHIEF;

    FreeAgreement.create({}).$promise.then(createResult => {
      // eslint-disable-next-line no-undef
      localStorage.setItem('newAvtalId', createResult.id);
      $window.onbeforeunload = () => { vm.deleteNew(createResult.id); };
      let modal = $uibModal.open({
        animation: true,
        ariaLabelledBy: 'modal-title-top',
        ariaDescribedBy: 'modal-body-top',
        size: 'md',
        index: 1075,
        templateUrl: 'resources/agreement/free/new/modal/new-free-agreement-modal.html',
        windowClass: 'modal-content--white-border',
        backdrop: 'static',
        controller: 'NewFreeAgreementModalCtrl',
        controllerAs: 'vm',
        resolve: {
          agreement: () => null,
          users: () => [initialUser],
          invites: () => [],
        }
      });
      modal.result.then(result => {
        $window.onbeforeunload = () => {};
        if (result.cancel) {
          vm.deleteNew(createResult.id);
          $location.search({});
          $location.path('agreements');
        } else {
          FreeAgreement.finishCreate({
            id: createResult.id,
            userContainer: result.userContainer,
            type: result.type.code,
            message: result.message,
          }).$promise.then(result => {
            if (Auth.isHgfUser()) {
              vm.selectAgreement(result.agreementId);
            } else {
              $location.search({});
              $location.path('/agreement/free/new/thank-you-agreement');
            }
          });
        }
      });
    });
  };

  vm.selectAgreement = agreementId => {
    $location.search({});
    $location.path('/agreements/' + agreementId);
  };

  vm.unlockPending = agreement => {
    FreeAgreement.unlockPending({ agreementId: agreement.id })
      .$promise.then(vm.refreshList);
  };

  vm.deletePending = agreement => {
    let message = 'Är du säker?';

    HyUtils.showDefaultModal('ConfirmationModalWithMessageCtrl',
      'components/modal/confirmation/confirmationModalWithMessage.html',
      // eslint-disable-next-line no-unused-vars
      { message }, result => {
        FreeAgreement.deletePending({ agreementId: agreement.id })
          .$promise.then(vm.refreshList);
      }, null, null, { size: 'xsm' });
  };

  vm.downloadPDF = (agreementId) => {
    FreeAgreement.getSignedAgreement({ agreementId })
      .$promise.then(function(response) {
        let filename = `Avtal_${agreementId}.pdf`;
        HyUtils.spitOutAttachment(response.data, filename, 'application/pdf');
      }).catch(
        new ErrorHandler('Misslyckades att hamta avtal.')
      ).finally(() => { vm.saving = false });
  };

  vm.refreshList();

  if ($routeParams.new) {
    vm.newAgreement();
  }
}
})();
(function () {
'use strict';

ShowAgreementRentlistCtrl.$inject = ["$scope", "$location", "$routeParams", "Agreement", "Auth", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowAgreementRentlistCtrl', ShowAgreementRentlistCtrl);

function ShowAgreementRentlistCtrl($scope, $location, $routeParams, Agreement, Auth, Flash, ErrorHandler) {
  var vm = this;
  vm.agreement = {
    negotiationId: $routeParams.negotiationId,
    id: $routeParams.id,
    documents: []
  };
  vm.apartments = [];
  vm.getApartments = getApartments;

  vm.listColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Fastighet', column: 'property.propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'Överenskommen ny hyra (kr/månad)', column: 'newMonthlyRent' },
  ];
  vm.sortColumn = vm.listColumns.some(function(c) {
    return c.column === $location.search().order;
  })
    ? $location.search().order
    : 'property.propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.currentPage = 0;
      getApartments();
    }
  });

  getApartments();

  vm.pageLimit = 30;
  vm.currentPage = 0;
  vm.firstRecordInCurrentPage = 1;

  function getApartments(filterMode) {
    vm.readyApartments = false;
    if (filterMode) {
      vm.currentPage = 0;
    }
    return Agreement.queryApartment({
      negotiationId: $routeParams.negotiationId,
      id: $routeParams.id,
      limit: vm.pageLimit,
      offset: vm.currentPage,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'search[nationalNo]': vm.inputTextNationalApartmentNo,
      'search[ownerNo]': vm.inputTextApartmentNo,
      'search[streetAddress]': vm.inputTextStreetAddress,
      'search[property.propertyDesignation]': vm.inputTextPropertyDesignation,
      'search[municipality]': vm.inputTextMunicipality
    })
      .$promise.then(function(response) {
        vm.apartments = response.rows;
        vm.apartmentsTotalCount = response.count;
        // Save initial count of records, to be distingueshed from the current count after
        // unsuccessful filtering. This will affect all messages, which are dependent on totalCount = 0.
        if (filterMode !== true) {
          vm.initialTotalCount = vm.apartmentsTotalCount;
        }
      })
      .catch(ErrorHandler('Misslyckades att hämta fastighetsbestånd.'))
      .finally(function() {
        vm.readyApartments = true;
      });
  }
}

})();
(function () {
'use strict';

EditMeetingModalCtrl.$inject = ["$uibModalInstance", "$scope", "$routeParams", "$location", "Auth", "DateLocal", "Flash", "Negotiation", "Meeting", "meetingObj", "negotiationObj", "User", "ErrorHandler"];
angular.module('hyresgastApp').controller('EditMeetingModalCtrl', EditMeetingModalCtrl);

function EditMeetingModalCtrl(
  $uibModalInstance,
  $scope,
  $routeParams,
  $location,
  Auth,
  DateLocal,
  Flash,
  Negotiation,
  Meeting,
  meetingObj,
  negotiationObj,
  User,
  ErrorHandler
) {
  var vm = this;
  vm.meeting = meetingObj;
  vm.negotiationListReady = true;

  if (vm.meeting === null && negotiationObj !== null) {
    vm.meeting = {
      negotiationId: negotiationObj.id,
      participants: [],
      negotiations: []
    };

    // When creating a new meeting and the modal is opened on top of a negotiation page =>
    // populate the participants and the current negotiation.
    vm.meeting.participants = negotiationObj.hgfParticipants.concat(negotiationObj.externalParticipants);

    var currentParticipantUserIds = vm.meeting.participants.map(function(participant) {
      return participant.id;
    });
    var currentNegotiation = {
      id: negotiationObj.id,
      name: negotiationObj.name,
      participantUserIds: currentParticipantUserIds,
      state: negotiationObj.state,
      type: negotiationObj.type,
      caseNumber: negotiationObj.caseNumber
    };
    vm.meeting.negotiations.push(currentNegotiation);
    // Time picker component should not be used with the same model
    // as the datepicker.
    // https://github.com/angular-ui/bootstrap/issues/5485
    vm.startAt = new Date();
    vm.startTimeAt = initDate(8);
    vm.endTimeAt = initDate(9);
    vm.negotiationList = [];
  } else if (vm.meeting === null && negotiationObj === null) {
    // for case when we come from negotiation table
    vm.meeting = {
      participants: [Auth.currentUser()],
      negotiations: []
    };
    vm.startAt = new Date();
    vm.startTimeAt = initDate(8);
    vm.endTimeAt = initDate(9);
  } else {
    vm.editMode = true;
    vm.meeting = angular.copy(meetingObj);
    vm.startAt = new Date(vm.meeting.startAt);
    vm.startTimeAt = new Date(vm.meeting.startAt);
    vm.endTimeAt = new Date(vm.meeting.endAt);
  }

  vm.save = save;
  vm.cancel = cancel;
  vm.addParticipant = addParticipant;
  vm.removeParticipant = removeParticipant;
  vm.addNegotiation = addNegotiation;
  vm.removeNegotiation = removeNegotiation;
  vm.isNegotiationSelected = isNegotiationSelected;
  vm.hasUserRelatedNegotiation = hasUserRelatedNegotiation;

  getNegotiationList(vm.meeting.participants);
  getNegotiationPartners();

  /////////

  function getNegotiationPartners() {
    return User.queryNegotiationPartners({}).$promise.then(function(response) {
      vm.users = response.rows;
      vm.users.push(Auth.currentUser());
    })
      .catch(ErrorHandler('Misslyckades att hämta data.'));
  }

  function initDate(hour) {
    var date = new Date();
    date.setHours(hour, 0, 0, 0);
    return date;
  }

  function setTime(date, time) {
    var to = new Date(date);
    to.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return to;
  }

  function addParticipant() {
    var foundParticipant = vm.meeting.participants.filter(function(user) {
      return user.id === vm.selected.id;
    });

    // look through all participants to check if they had been added before or not
    if (foundParticipant.length === 0) {
      vm.meeting.participants.push(vm.selected);
      getNegotiationList(vm.meeting.participants);
    }

    // clear
    vm.selected = null;
  }

  function removeParticipant(participant) {
    var index = vm.meeting.participants.indexOf(participant);
    vm.meeting.participants.splice(index, 1);
    getNegotiationList(vm.meeting.participants); // update the list after delete a participant
  }

  function getNegotiationList(participants) {
    var participantsId = participants.map(function(item) {
      return item.id;
    });

    // don't make a query in case where there are no participants
    if (participantsId.length === 0) {
      vm.negotiationList = [];
      vm.meeting.negotiations = [];
      return;
    }
    vm.negotiationListReady = false;
    return Negotiation.queryNegotiationsByParticipant({
      'filter[userId]': participantsId
    }).$promise.then(function(response) {
      vm.negotiationList = response.rows;
      vm.negotiationListReady = true;

      // check if all selected negotiations exist in list of selectable negotiations in dropdown.
      // remove selected negotiations that are no longer selectable.
      vm.meeting.negotiations = vm.meeting.negotiations.filter(function(selectedNegotiation) {
        return vm.negotiationList.some(function(negotiation) {
          return negotiation.id === selectedNegotiation.id;
        });
      });

      // add participant information to vm.meeting.negotiations - it is missing when first loading
      // an existing meeting
      vm.meeting.negotiations = vm.meeting.negotiations.map(function(negotiation) {
        var selectableNegotiation = vm.negotiationList.filter(function(otherNegotiation) {
          return otherNegotiation.id === negotiation.id;
        })[0];
        negotiation.participantUserIds = selectableNegotiation.participantUserIds;
        return negotiation;
      });
    })
      .catch(ErrorHandler('Misslyckades att hämta förhandling.'));
  }

  function addNegotiation(negotiation) {
    var foundNegotiation = vm.meeting.negotiations.filter(function(item) {
      return item.id === negotiation.id;
    });

    // look through all negotiations to check if they had been added before or not
    if (foundNegotiation.length === 0) {
      vm.meeting.negotiations.push(negotiation);
    }
  }

  function removeNegotiation(negotiation) {
    var index = vm.meeting.negotiations.indexOf(negotiation);
    vm.meeting.negotiations.splice(index, 1);
  }

  function isNegotiationSelected(negotiationId) {
    return Boolean(
      vm.meeting.negotiations.filter(function(negotiation) {
        return negotiation.id === negotiationId;
      })[0]
    );
  }

  function hasUserRelatedNegotiation(participant, selectedNegotiations) {
    function isOurParticipant(userId) {
      return userId === participant.id;
    }

    function isParticipantInNegotiation(negotiation) {
      return negotiation.participantUserIds && negotiation.participantUserIds.some(isOurParticipant);
    }

    return selectedNegotiations.some(isParticipantInNegotiation);
  }

  function save() {
    if (vm.meeting.negotiations.length > 0) {
      vm.saving = true;

      // Both dates use startAt as base because View does not have
      // an endAt datepicker.
      vm.meeting.startAt = setTime(vm.startAt, vm.startTimeAt);
      vm.meeting.endAt = setTime(vm.startAt, vm.endTimeAt);

      // Set endAt to tomorrow if endTime is less than startTime
      if (
        vm.endTimeAt.getHours() < vm.startTimeAt.getHours() ||
        (vm.endTimeAt.getHours() === vm.startTimeAt.getHours() &&
          vm.endTimeAt.getMinutes() < vm.startTimeAt.getMinutes())
      ) {
        vm.meeting.endAt.setDate(vm.meeting.endAt.getDate() + 1);
      }

      // Remove timezone part of date
      vm.meeting.startAt = DateLocal.fromUTCtoLocal(vm.meeting.startAt);
      vm.meeting.endAt = DateLocal.fromUTCtoLocal(vm.meeting.endAt);

      Meeting.save(vm.meeting).$promise.then(
        function(response) {
          vm.saving = false;
          Flash.set('Mötet har sparats', 'success');
          $uibModalInstance.close(response);
        })
        .catch(function(reason) {
          ErrorHandler('Misslyckades att spara mötet')(reason);
          $uibModalInstance.dismiss('error');
        });
    } else {
      Flash.set('Minst en förhandling måste väljas', 'error');
    }
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

EditProtocolModal.$inject = ["$uibModalInstance", "$scope", "$routeParams", "$location", "$window", "DateLocal", "Flash", "Meeting", "meetingObj", "negotiationObj", "NegotiationStates", "ErrorHandler"];
angular.module('hyresgastApp').controller('EditProtocolModal', EditProtocolModal);

function EditProtocolModal(
  $uibModalInstance,
  $scope,
  $routeParams,
  $location,
  $window,
  DateLocal,
  Flash,
  Meeting,
  meetingObj,
  negotiationObj,
  NegotiationStates,
  ErrorHandler
) {
  var vm = this;
  vm.NegotiationStates = NegotiationStates;
  vm.counterpartName = negotiationObj.counterpart.name;
  vm.negotiationState = negotiationObj.state;
  vm.meeting = angular.copy(meetingObj);

  if (meetingObj.hasProtocol) {
    vm.protocolDate = new Date(vm.meeting.protocolDate);
  } else {
    // init new protocol
    vm.protocolDate = new Date(vm.meeting.startAt);
    vm.meeting.protocolLocation = meetingObj.location;
    vm.meeting.protocolParticipants = !meetingObj.participants
      ? ''
      : meetingObj.participants
          .map(function(participant) {
            return participant.firstname + ' ' + participant.lastname;
          })
          .join(', ');
    vm.meeting.protocolAttestors = null;
    vm.meeting.protocolChairPerson = null;
    vm.meeting.protocolDecisionPoints = [];
    vm.meeting.protocolDiscussionPoints = [];
    vm.meeting.protocolSecretary = null;
  }

  vm.cancelProtocol = cancelProtocol;
  vm.saveProtocol = saveProtocol;
  vm.addDiscussionItem = addDiscussionItem;
  vm.closeDiscussion = closeDiscussion;

  /////////

  function addDiscussionItem() {
    vm.discussionSubject = vm.discussionSubject;
    vm.meeting.protocolDiscussionPoints.push({
      subject: vm.discussionSubject,
      text: vm.discussionText || ' '
    });
    vm.discussionSubject = vm.discussionText = null;
  }

  function closeDiscussion() {
    vm.discussionSubject = vm.discussionText = null;
  }

  function saveProtocol(hideFlash) {
    vm.saving = true;
    vm.meeting.protocolDate = DateLocal.fromUTCtoLocal(vm.protocolDate);

    // TODO - probably move to backend, not up to client to decide, ie.
    // what happens if saving fails?
    vm.meeting.hasProtocol = true;

    return Meeting.save(vm.meeting)
      .$promise.then(
        function(response) {
          angular.extend(meetingObj, response);
          if (!hideFlash) {
            Flash.set('Lyckades att spara protokoll', 'success');
          }
          $uibModalInstance.dismiss('close');
        })
      .catch(function(reason) {
        ErrorHandler('Misslyckades att spara protokoll')(reason);
        $uibModalInstance.dismiss('error');
      })
      .finally(function() {
        vm.saving = false;
      });
  }

  function cancelProtocol() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

ShowMeetingCtrl.$inject = ["$scope", "$uibModal", "$routeParams", "$location", "Auth", "Meeting", "Flash", "Negotiation", "NegotiationStates", "ValidFileTypes", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowMeetingCtrl', ShowMeetingCtrl);

function ShowMeetingCtrl(
  $scope,
  $uibModal,
  $routeParams,
  $location,
  Auth,
  Meeting,
  Flash,
  Negotiation,
  NegotiationStates,
  ValidFileTypes,
  ErrorHandler
) {
  var vm = this;
  vm.saveMeeting = saveMeeting;
  vm.getNegotiation = getNegotiation;
  vm.NegotiationStates = NegotiationStates;
  vm.editMeeting = editMeeting;
  vm.deleteMeeting = deleteMeeting;
  vm.addEditProtocol = addEditProtocol;
  vm.addProtocolFile = addProtocolFile;

  vm.deleteProtocol = deleteProtocol;
  vm.Auth = Auth;

  vm.pdfPath = '';

  getNegotiation();
  getMeeting();

  ////////

  function getNegotiation() {
    return Negotiation.get({
      id: $routeParams.negotiationId
    })
      .$promise.then(function(response) {
        vm.negotiation = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function() {
        vm.readyNegotiation = true;
      });
  }

  function getMeeting() {
    return Meeting.get({
      id: $routeParams.id
    })
      .$promise.then(function(response) {
        vm.meeting = response;
        setPdfPath();
      })
      .catch(ErrorHandler('Misslyckades att hämta möten.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function saveMeeting() {
    Meeting.save(vm.meeting)
      .$promise.then(function(response) {})
      .catch(ErrorHandler('Misslyckades att spara möten.'));
  }

  function editMeeting() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'lg',
      index: 1075, // position above tooltip
      templateUrl: 'resources/meeting/edit/edit-meetings-modal.html',
      controller: 'EditMeetingModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      backdrop: 'static',
      resolve: {
        meetingObj: function() {
          return vm.meeting;
        },
        negotiationObj: function() {
          return null;
        }
      }
    });

    modalInstance.result.then(function(meeting) {
      vm.meeting = meeting;
    });
  }

  function deleteMeeting() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'xsm',
      templateUrl: 'components/modal/confirmation/confirmationModal.html',
      controller: 'ConfirmationModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--secondary'
    });

    modalInstance.result.then(function() {
      Meeting.delete({
        id: $routeParams.id
      })
        .$promise.then(function(response) {
          $location.path('/negotiations/' + $routeParams.negotiationId).search('view', 'meetings');
          Flash.set('Lyckades att ta bort mötet', 'success');
        })
        .catch(new ErrorHandler('Misslyckades att ta bort möten.'));
    });
  }

  function deleteProtocol() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'xsm',
      templateUrl: 'components/modal/confirmation/confirmationModal.html',
      controller: 'ConfirmationModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--secondary'
    });

    modalInstance.result.then(function() {
      if (vm.meeting.protocolDocument) {
        Meeting.deleteProtocol({
          id: $routeParams.id,
          blobId: vm.meeting.protocolDocument.name
        })
          .$promise.then(function(response) {
            vm.meeting.protocolDocument = null;
            vm.meeting.hasProtocol = false;
            Flash.set('Lyckades att ta bort protokollet', 'success');
          })
          .catch(new ErrorHandler('Misslyckades att ta bort protokollet.'));
      } else {
        vm.meeting.hasProtocol = false;
        Meeting.save(vm.meeting)
          .$promise.then(function(response) {
            vm.meeting = response;
            Flash.set('Lyckades att ta bort protokollet', 'success');
          })
          .catch(new ErrorHandler('Misslyckades att ta bort protokollet.'));
      }
    });
  }

  function addProtocolFile() {
    var settings = {
      title: 'Ladda upp protokoll',
      uploadUrl: '/api/meetings/' + $routeParams.id + '/protocol',

      // valid extension to upload
      validFileTypes: ValidFileTypes,

      // a file is associated with one type
      types: [{ id: 15, name: 'Protokoll' }],

      typeTitle: 'Välj typ av underlag'
    };

    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'components/file-upload-modal/file-upload-modal.html',
      controller: 'FileUploadModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      resolve: {
        settings: function() {
          return settings;
        }
      }
    });
    modalInstance.result.then(function(file) {
      vm.meeting.protocolDocument = file;
      vm.meeting.hasProtocol = true;
      setPdfPath();
      Flash.set('Lyckades att lägga till protokoll', 'success');
    });
  }

  function addEditProtocol() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'xlg',
      templateUrl: 'resources/meeting/edit/edit-protocol-modal.html',
      controller: 'EditProtocolModal',
      controllerAs: 'vm',
      backdrop: 'static',
      resolve: {
        meetingObj: function() {
          setPdfPath();
          return vm.meeting;
        },
        negotiationObj: function() {
          return vm.negotiation;
        }
      }
    });
  }
  function setPdfPath() {
    vm.meeting.protocolDocument
      ? (vm.pdfPath = 'javascript:void(0);')
      : (vm.pdfPath = '/api/meetings/' + vm.meeting.id + '/protocol-pdf');
  }
}

})();
(function () {
'use strict';

ShowBlockingCtrl.$inject = ["$scope", "$routeParams", "Auth", "Blocking", "Flash", "Negotiation", "NegotiationStates", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowBlockingCtrl', ShowBlockingCtrl);

function ShowBlockingCtrl($scope, $routeParams, Auth, Blocking, Flash, Negotiation, NegotiationStates, ErrorHandler) {
  var vm = this;
  vm.NegotiationStates = NegotiationStates;
  vm.skipRentlist = false;

  vm.getNegotiation = getNegotiation;
  getBlocking();
  getNegotiation();

  Auth.heartbeat().then(function() {
    vm.currentUserParty = Auth.currentUser().party;
  });

  ////////

  function getNegotiation() {
    return Negotiation.get({
      id: $routeParams.negotiationId
    })
      .$promise.then(function(response) {
        vm.negotiation = response;

        vm.negotiation.isFinished = vm.negotiation.state === NegotiationStates.FINISHED ||
          vm.negotiation.state === NegotiationStates.FINISHED_OUTSIDE_PORTAL;

        vm.skipRentlist = vm.negotiation.canSkipRentlist;
      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function() {
        vm.readyNegotiation = true;
      });
  }

  function getBlocking() {
    return Blocking.get({
      negotiationId: $routeParams.negotiationId,
      id: $routeParams.id
    })
      .$promise.then(function(response) {
        vm.blocking = response.agreement;
      })
      .catch(new ErrorHandler('Misslyckades att hämta strandning.'))
      .finally(function() {
        vm.ready = true;
      });
  }
}

})();
(function () {
/* eslint-disable no-undef */
/* eslint-disable no-console */
'use strict';
ConversionErrorsModalCtrl.$inject = ["$uibModalInstance", "conversionErrors"];
angular.module('hyresgastApp').controller('ConversionErrorsModalCtrl', ConversionErrorsModalCtrl);

function ConversionErrorsModalCtrl($uibModalInstance,
  conversionErrors) {
  var vm = this;
  vm.conversionErrors = conversionErrors;

  console.log('CONVERSION ERRORS!\n');
  console.log(conversionErrors);

  vm.ok = () => $uibModalInstance.close();
}
})();
(function () {
/* eslint-disable no-console */
'use strict';

ShowAgreementCtrl.$inject = ["$scope", "$uibModal", "$routeParams", "$route", "$cookies", "$timeout", "store", "Auth", "Flash", "SubPage", "ErrorHandler", "HyUtils", "Agreement", "FreeAgreement", "Negotiation", "Seenby", "CommonMessages", "NegotiationTypes", "NegotiationStates", "NegotiationRoles", "Parties", "AgreementTypes", "FreeAgreementTypes", "ObjectTypes"];
angular.module('hyresgastApp').controller('ShowAgreementCtrl', ShowAgreementCtrl);

function ShowAgreementCtrl($scope, $uibModal, $routeParams, $route, $cookies, $timeout, store,
  Auth, Flash, SubPage, ErrorHandler, HyUtils,
  Agreement, FreeAgreement, Negotiation, Seenby,
  CommonMessages, NegotiationTypes, NegotiationStates, NegotiationRoles, Parties, AgreementTypes, FreeAgreementTypes, ObjectTypes) {

  var vm = this;

  vm.findNegotiationRole = HyUtils.findNegotiationRole;
  vm.NegotiationStates = NegotiationStates;
  vm.NegotiationTypes = NegotiationTypes;
  vm.FreeAgreementTypes = Object.values(FreeAgreementTypes);
  vm.AgreementTypes = AgreementTypes;

  vm.downloadPDF = downloadPDF;
  vm.previewPDF = previewPDF;
  vm.toggleAttachmentsPane = toggleAttachmentsPane;
  vm.getNegotiation = getNegotiation;
  vm.getAgreement = getAgreement;
  vm.saveAgreement = saveAgreement;
  vm.signStart = signStart;
  vm.pullOut = pullOut;
  vm.signDrop = signDrop;
  vm.getScriveStatus = getScriveStatus;
  vm.alterRole = alterRole;
  vm.toggleCheck = toggleCheck;
  vm.toggleAll = toggleAll;
  vm.isPageLoading = isPageLoading;

  vm.addVoidUser = addVoidUser;
  vm.deleteVoidUser = deleteVoidUser;

  vm.changeFreeParticipants = changeFreeParticipants;
  vm.changeFreeAgreementType = changeFreeAgreementType;
  vm.setEndDate = setEndDate;

  vm.negotiation = { id: $routeParams.negotiationId };
  vm.participants = [];
  vm.canStartSigning = false;
  vm.canDownloadPDF = false;
  vm.skipRentlist = false;

  Auth.heartbeat().then(function() {
    vm.currentUser = Auth.currentUser();
    let negotiationId = $routeParams.negotiationId;
    let agreementId = $routeParams.id;

    vm.isFreeAgreement = !negotiationId;
    vm.Resource = vm.isFreeAgreement ? FreeAgreement : Agreement;

    getCurrentSubpage();
    if (!vm.isFreeAgreement) {
      getNegotiation(negotiationId);
    }
    getAgreement(negotiationId, agreementId);
    $timeout(() => { getScriveStatus(negotiationId, agreementId) }, 2000);
  });

  ////////
  const EXTERNAL_TITLES = [
    'Ombud för hyresvärd, enligt fullmakt',
    'Fastighetsägarna (part i 3-parts FO)',
    'Ombud för Fastighetsägarna, enligt fullmakt (3-parts FO)',
    'Ombud för hyresvärd och Fastighetsägarna, enligt 2 fullmakter (3-parts FO)',
    'Förtroendevald Hyresgästföreningen',
    'Undertecknande part',
  ];
  const EXTERNAL_OWNER = EXTERNAL_TITLES[2];
  const STUCK_AGREEMENT = 'bubkis';

  function alterRole(party) {

    var userParty = Object.values(NegotiationRoles)
      .find(nr => nr.code === party.roleObj.code).party;

    var possibleRoles = Object.values(NegotiationRoles)
      .filter(nr => nr.party === userParty && !nr.isChief)
      .map(nr => nr.name);

    switch(userParty) {
      case Parties.HGF:
        //no need for Hyresgästföreningen
        possibleRoles.splice(0, 1);
        possibleRoles.push('Ordförande förhandlingsdelegation');
        break;
      case Parties.EXTERNAL:
        Array.prototype.push.apply(possibleRoles, EXTERNAL_TITLES);
        break;
    }

    $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'md',
      index: 1075,
      templateUrl: 'resources/agreement/show/overview/alterRole.html',
      windowClass: 'modal-content--white-border',
      backdrop: 'static',
      controller: 'AlterRoleModalCtrl',
      controllerAs: 'vm',
      resolve: {
        selectedRole: () => party.roleObj.alternateRole,
        possibleRoles: () => possibleRoles
      }
    }).result.then(function(res) {
      party.roleObj.alternateRole = res;
      saveCookies();
      checkWarnings();
    });
  }

  function toggleCheck() {
    vm.noParticipantsSelected = !vm.participants.reduce((a, p) => a = p.selected ? true : a, false);
    saveCookies();
  }

  function toggleAll() {
    var state = vm.participants.reduce((a, p) => a = !p.selected ? false : a, true);
    vm.participants.forEach(p => p.selected = !state);
    vm.toggleCheck();
  }

  function getNegotiation(negotiationId) {
    Negotiation.get({ id: negotiationId })
      .$promise.then(function (response) {
        vm.negotiation = response;

        vm.isNewlyConstructedProperty = vm.negotiation.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY;
        vm.isNewlyConstructedApartment = vm.negotiation.type === NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT;
        vm.negotiation.isFinished = vm.negotiation.state === NegotiationStates.FINISHED ||
                                    vm.negotiation.state === NegotiationStates.FINISHED_OUTSIDE_PORTAL;
        vm.counterpart = vm.negotiation.counterpart;
        setupUI();
      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(() => { vm.readyNegotiation = true });
  }

  function checkWarnings() {
    const codes3part = [ '3A', '3B', '3C', '3O' ];
    if (vm.isFreeAgreement) { return; }
    let warning3C = false;
    let warningOwner = false;
    let has3Cproperties = vm.agreement.properties
      .some(p => p.propertyProcedures
        .some(pp => codes3part.includes(pp.negotiationAgreementCode)));
    if (has3Cproperties) {
      warning3C = !(vm.participants.every(p => p.party === Parties.HGF ||
        (p.roleObj.alternateRole &&
         p.roleObj.alternateRole.includes('3-parts'))));
    }

    let ownerNames = vm.agreement.properties.map(p => p.owners).map(o => o.map(oo => oo.name)).flat();
    let ownerParticipants = vm.participants.filter(p => p.party === Parties.EXTERNAL && ownerNames.includes(p.name));
    if (ownerParticipants.length) {
      warningOwner = !ownerParticipants.every(p => p.roleObj.alternateRole === EXTERNAL_OWNER);
    }

    vm.roleWarning = warning3C || warningOwner;
  }

  /**
   * retrieves agreement
   * @param {number} agreementId
   */
  function getAgreement(negotiationId, agreementId) {
    vm.Resource.get({
      agreementId,
      negotiationId,
      id: agreementId,
    }).$promise.then(function (response) {
      vm.agreement = response.agreement ? response.agreement : response;
      vm.stuckAgreement = vm.agreement.scriveId === STUCK_AGREEMENT;
      vm.agreement.conditionTags = vm.agreement.conditionTags
        ? JSON.parse(vm.agreement.conditionTags) :
        {};
      vm.isPresumtiveRent = vm.agreement.conditionTags.isPresumtiveRent;
      if (vm.isFreeAgreement) {
        if (vm.agreement.invites) {
          vm.agreement.invites.forEach(p => p.roleObj = vm.findNegotiationRole(p.role));
        }
        vm.freeAgreementType = vm.FreeAgreementTypes.find(t => t.code === vm.agreement.type);
      }
      if (vm.agreement.releaseDate) {
        vm.agreement.releaseDate = new Date(vm.agreement.releaseDate);
      }
      if (vm.agreement.rentEffectiveFrom) {
        vm.agreement.rentEffectiveFrom = new Date(vm.agreement.rentEffectiveFrom);
      }
      vm.skipRentlist = vm.agreement.type === AgreementTypes.AGREEMENT_WITHOUT_RENTLIST;
      getDocuments(negotiationId, agreementId);
      setupUI();
    })
      .catch(new ErrorHandler('Misslyckades att hämta överenskommelse.'))
      .finally(function () {
        vm.ready = true;
        Seenby.set({
          objectType: ObjectTypes.AGREEMENT.code,
          objectId: $routeParams.id,
        });
      });
  }

  function getDocuments(negotiationId, agreementId, cb) {
    vm.Resource.queryDocument({
      id: agreementId,
      agreementId,
      negotiationId
    }).$promise.then(response => {
      vm.agreement.documents = response;
      vm.agreement.numDocuments = vm.agreement.documents ? vm.agreement.documents.length : 0;
      vm.agreement.sizeDocuments = vm.agreement.documents ? vm.agreement.documents.map(x => parseInt(x.contentLength)).reduce((acc, el) => acc+el, 0) : 0;
    }).catch(new ErrorHandler('Misslyckades att hämta dokument'))
      .finally(() => {
        vm.readyDocument = true;
        if (cb) {
          return cb();
        }
      });
  }
  vm.getDocuments = getDocuments;

  function getCurrentSubpage() {
    var defaultSubPage = 'overview';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = ['properties', 'documents'];
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }

  function setupUI() {
    if (!vm.agreement) { return; }
    if (!vm.isFreeAgreement && !vm.negotiation.callId) { return; }
    prepareParticipants();
    setPermissions();
    if (vm.agreement.counterpart) {
      vm.counterpart = vm.agreement.counterpart;
      setCounterpartNumber();
    } else if (vm.negotiation.counterpart) {
      vm.counterpart = vm.negotiation.counterpart;
      setCounterpartNumber();
    }
    checkWarnings();
  }

  function prepareParticipants() {
    //get zey ale
    if (!vm.isFreeAgreement) {
      vm.participants = vm.negotiation.hgfParticipants
        .concat(vm.negotiation.externalParticipants)
        .concat(vm.negotiation.delegationParticipants);
      vm.participants.forEach(x => x.canBeDeleted = x.disabled);
      let agreementUsers = vm.agreement.users;
      agreementUsers.forEach(x => x.canBeDeleted = true);
      vm.participants = vm.participants.concat(agreementUsers);
    } else {
      vm.participants = vm.agreement.users;
      vm.participants.forEach(x => x.canBeDeleted = x.disabled);
    }

    //set roles
    vm.participants.forEach(p => {
      p.selected = true;
      let role = p.negotiationsUsers ? p.negotiationsUsers.role : p.agreementRole;
      let roleObj = vm.findNegotiationRole(role);
      if (roleObj) {
        //findNegotiationRole returns reference, but we need value here
        p.roleObj = JSON.parse(JSON.stringify(roleObj));
      } else {
        // eslint-disable-next-line no-undef
        console.log('oops, unknown role');
      }
    });

    vm.participants = vm.participants.filter(p => p.roleObj);

    vm.participants = vm.participants.sort((a, b) => b.roleObj.party - a.roleObj.party);

    //group parties
    var prevP = vm.participants[0];
    vm.participants.forEach(p => {
      if(p.roleObj.party !== prevP.roleObj.party) {
        p.groupBreak = true;
      }
      prevP = p;
    });

    restoreCookies();
  }

  function setCounterpartNumber() {
    vm.counterpartNumber = vm.counterpart.organizationNumber.slice(0, -4) + '-' +
                           vm.counterpart.organizationNumber.substr(-4);
  }

  function setPermissions() {

    var currentUserInNegotiation = vm.negotiation.id
      ? vm.participants.find(p => p.id === vm.currentUser.id)
      : vm.participants.find(p => p.userId === vm.currentUser.id);

    if (currentUserInNegotiation) {
      var currentUserRole = currentUserInNegotiation.roleObj.code;

      if ((currentUserRole !== NegotiationRoles.HGF_ELECTED.code) &&
          (currentUserRole !== NegotiationRoles.HGF_ELECTED_CHIEF.code)) {
        vm.canStartSigning = true;
        vm.canDownloadPDF = true;
      }
      vm.canFastClose = Auth.isHgfUser();
    }
  }

  function formatDate(date) {
    if (!date) {
      return null;
    }
    return date.getFullYear() + '-' + (date.getMonth()+1) + '-' + date.getDate();
  }

  function saveAgreement(callback) {
    if (!callback) {
      vm.saving = true;
    }

    // Sequelize save do not perform custom actions, neiter do users change, so we preserve users for performance sake
    var tempUsers = vm.agreement.users;

    // eslint-disable-next-line no-undef
    let payload = structuredClone(vm.agreement);
    payload.releaseDate = formatDate(vm.agreement.releaseDate);
    payload.rentEffectiveFrom = formatDate(vm.agreement.rentEffectiveFrom);
    payload.agreementId = vm.agreement.id;
    payload.conditionTags = JSON.stringify(payload.conditionTags);

    FreeAgreement.save(payload)
      .$promise.then(
        function (response) {
          vm.agreement = response;
          vm.agreement.conditionTags = JSON.parse(vm.agreement.conditionTags);
          vm.agreement.users = tempUsers;
          vm.agreement.rentEffectiveFrom = new Date(vm.agreement.rentEffectiveFrom);
          if (vm.agreement.releaseDate) {
            vm.agreement.releaseDate = new Date(vm.agreement.releaseDate);
          }
          getDocuments(vm.negotiation.id, vm.agreement.id);

          callback();
        },
        function (reason) { // eslint-disable-line no-unused-vars
          Flash.set('Misslyckades att spara', 'error');
          getAgreement(vm.negotiation.id, vm.agreement.id);
        }
      )
      .finally(function () {
        if (!callback) {
          vm.saving = false;
        }
      });
  }

  function getScriveStatus(negotiationId, agreementId) {

    return vm.Resource.getScriveStatus({
      negotiationId: negotiationId,
      agreementId: agreementId,
    }).$promise.then(function (response) {

      vm.signStartVisible = false;
      vm.signStatusVisible = false;
      vm.signDownloadVisible = false;
      vm.signDropAvailable = false;

      vm.finishing = response.finishing;
      if (vm.finishing) {
        $timeout(() => { getScriveStatus(negotiationId, agreementId) }, 30000);
      }
      vm.agreement.signed = response.signed;
      if (vm.agreement.signed) {
        vm.agreement.agreedByAssociation = vm.agreement.agreedByOwner = true;
      }

      if (!response.scriveResponse) {
        /**
         * we have not started scrive process yet.
         * no id in the database, no pdf uploaded to scrive.
         * lets do it
         */
        if (response.mandatoryRolesPresent) {
          vm.signStartVisible = true;
        } else {
          vm.signError = 'Kan inte börja signera. Inte alla nödvändiga roller närvarande';
          vm.signErrorVisible = true;
        }

      } else {
        /**
         * we have document in scrive for that agreement
         */

        if (response.scriveResponse.status.status) {
          var status = response.scriveResponse.status.status;
          vm.rejectedReactionsVisible = false;

          if (status.indexOf('The resource was not found. A document with id') !== -1) {
            status = 'Dokumentet laddas upp, men statusen är okänd';
          }

          if (status==='rejected') {
            let rejectedEvent = response.scriveResponse.history.events.find(e => e.status === 'rejected');
            status = `<b>[${rejectedEvent.party}]:</b> ${rejectedEvent.text}`;

            vm.rejectedReactionsVisible = true;
          }

          vm.signDropAvailable = status !== 'closed';

          vm.scriveStatus = status;
          vm.signStatusVisible = true;
        }

        if (response.scriveResponse.status.parties) {
          vm.scriveParties = response.scriveResponse.status.parties.slice(1).reduce(
            function (accumulator, party) {
              let knownSigner = vm.participants.find(x => x.email === party.email);
              if (knownSigner) {
                let roleObj = vm.participants.find(x => x.email === party.email).roleObj;
                return accumulator +
                  '<div class="inline-block--55">' +
                  ((party['sign_time']) ? '<b>' : '<i>') +
                  party.fields[0].value + ' ' +
                  party.fields[1].value +
                  ((party['sign_time']) ? '</b>' : '</i>') +
                  ' (' + party.fields[2].value + ') ' +
                  '</div>' +
                  (roleObj.alternateRole ? roleObj.alternateRole : roleObj.name) +' <br/>';
              }

              // it could be that another person has signed agreement
              return accumulator +
                '<div class="inline-block--55">' +
                ((party['sign_time']) ? '<b>' : '<i>') +
                party.fields[0].value + ' ' +
                party.fields[1].value +
                ((party['sign_time']) ? '</b>' : '</i>') +
                ' (' + party.fields[2].value + ') ' +
                '</div>&nbsp;<br/>';
            }, '');
        }

        vm.signDownloadVisible = true;
      }
    }).catch(new ErrorHandler('Misslyckades att hämta Scrive status.'))
      .finally(() => { vm.readyScrive = true });
  }

  function decideAboutConversionErrors(conversionErrors) {
    $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'md',
      index: 1075,
      templateUrl: 'resources/agreement/show/conversion-errors-modal.html',
      windowClass: 'modal-content--white-border',
      backdrop: 'static',
      controller: 'ConversionErrorsModalCtrl',
      controllerAs: 'vm',
      resolve: {
        conversionErrors: () => conversionErrors
      }
    }).result.then(() => signStart(true));
  }

  function singStartDeeper(skipConversionErrors, fastClose, insertNH) {
    vm.saving = true;
    saveAgreement(() => {
      return vm.Resource.signAgreement({
        negotiationId: vm.negotiation && vm.negotiation.id,
        agreementId: vm.agreement.id,
        selectedParticipants: JSON.stringify(vm.participants.filter(p => p.selected)),
        insertNH,
        skipConversionErrors,
        fastClose,
      }).$promise.then(response => {  // eslint-disable-line no-unused-vars
        vm.saving = false;
        vm.signStartVisible = false;
        vm.signDownloadVisible = true;
        vm.finishing = response.finishing;
        $timeout(() => getScriveStatus(vm.negotiation && vm.negotiation.id, vm.agreement.id), 5000);
      }).catch(e => {
        if (e.status === 400) {
          decideAboutConversionErrors(e.data.conversionErrors);
        } else {
          new ErrorHandler('Misslyckades att börja signera.');
        }
      }
      ).finally(() => { vm.saving = false });
    });
  }

  function signStartInner(skipConversionErrors, fastClose) {
    if (vm.isFreeAgreement) {
      singStartDeeper(skipConversionErrors, fastClose, false);
      return;
    }
    if (!skipConversionErrors) {
      HyUtils.askQuestion('Vill du visa utgående Normhyra i överenskommelsen?', null, 2)
        .then(result => {
          let insertNH = (result === 'Ja');
          singStartDeeper(skipConversionErrors, fastClose, insertNH);
        });
    }
  }

  function signStart(skipConversionErrors, fastClose) {

    if (fastClose) {
      HyUtils.showDefaultModal('WarningConfirmationModalCtrl',
        'components/modal/confirmation/warningConfirmationModal.html',
        {
          message: CommonMessages.SIGN_WITHOUT_SCRIVE_CONFIRMATION,
          button: 'Ja',
          critical: false,
        // eslint-disable-next-line no-unused-vars
        }, result => {
          signStartInner(skipConversionErrors, true);
        });
      return;
    } else {
      signStartInner(skipConversionErrors, false);
    }
  }

  function pullOut() {
    vm.saving = true;

    vm.Resource.pullOut({ agreementId: vm.agreement.id})
      .$promise.then(() => { $route.reload() });
  }

  function signDrop() {
    vm.saving = true;

    return vm.Resource.dropAgreement({
      negotiationId: $routeParams.negotiationId,
      agreementId: vm.agreement.id
    }).$promise.then(response => {  // eslint-disable-line no-unused-vars
      $route.reload();
    }).catch(
      new ErrorHandler('Misslyckades att avbryta överenskommelsen.')
    ).finally(() => { vm.saving = false });
  }

  function toggleAttachmentsPane() {
    vm.showAgreementsPane = !vm.showAgreementsPane;
  }

  function previewPDF() {
    vm.saving = true;
    vm.Resource.getPreview({
      negotiationId: vm.negotiation && vm.negotiation.id,
      agreementId: vm.agreement.id,
      selectedParticipants: JSON.stringify(vm.participants.filter(p => p.selected)),
      skipConversionErrors: true,
      isPreview: true,
    }).$promise.then(function(response) {
      let filename = `Förhandsvisning_${vm.agreement.id}.pdf`;
      HyUtils.spitOutAttachment(response.data, filename, 'application/pdf');
      vm.saving = false;
    }).catch(
      new ErrorHandler('Misslyckades att hamta förhandsvisning.')
    ).finally(() => { vm.saving = false });
  }

  function downloadPDF() {
    vm.saving = true;
    vm.Resource.getSignedAgreement({
      negotiationId: $routeParams.negotiationId,
      agreementId: vm.agreement.id
    }).$promise.then(function(response) {
      let filename = `Överenskommelse_${vm.agreement.id}.pdf`;
      HyUtils.spitOutAttachment(response.data, filename, 'application/pdf');
      vm.saving = false;
    }).catch(
      new ErrorHandler('Misslyckades att hamta överenskommelsen PDF.')
    ).finally(() => { vm.saving = false });
  }

  function saveCookies() {
    let state = vm.participants.map(p => ({
      id: p.id,
      alternateRole: p.roleObj.alternateRole,
      selected: p.selected
    }));

    $cookies.put('agreementState'+$routeParams.id, JSON.stringify(state));
    store.set('agreementAttachment'+$routeParams.id, vm.attachment);
  }

  function restoreCookies() {
    let cookie = $cookies.get('agreementState'+$routeParams.id);
    if (cookie) {
      let checkboxState = JSON.parse(cookie);
      if (checkboxState) {
        checkboxState.forEach(cs => {
          let participant = vm.participants.find(p => p.id === cs.id);
          if (participant) {
            participant.selected = cs.selected;
            participant.roleObj.alternateRole = cs.alternateRole;
          }
        });
      }
    }
  }

  function changeFreeParticipants() {
    $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'md',
      index: 1075,
      templateUrl: 'resources/agreement/free/change-participants-modal.html',
      windowClass: 'modal-content--white-border',
      backdrop: 'static',
      controller: 'NewFreeAgreementModalCtrl',
      controllerAs: 'vm',
      resolve: {
        agreement: () => vm.agreement,
        users: () => vm.agreement.users,
        invites: () => vm.agreement.invites,
      }
    }).result.then(result => {
      FreeAgreement.changeParticipants({
        agreementId: vm.agreement.id,
        userContainer: result.userContainer,
        organization: result.organization,
      }).$promise.then(() => $route.reload());
    });
  }

  function changeFreeAgreementType (selection) {
    vm.freeAgreementType = selection;
    vm.agreement.type = selection.code;
  }

  function setEndDate (selection) {
    var d = new Date(vm.agreement.rentEffectiveFrom);
    d.setFullYear(d.getFullYear() + selection);
    vm.agreement.releaseDate = d;
  }

  function isPageLoading() {
    return !(vm.ready && (vm.isFreeAgreement || vm.readyNegotiation) && vm.readyScrive);
  }

  function addVoidUser() {
    FreeAgreement.addVoidUser(Object.assign({ id: vm.agreement.id }, vm.voidUser))
      .$promise.then(() => { $route.reload() });
  }

  function deleteVoidUser(participant) {
    let message = 'Är du säker?';

    HyUtils.showDefaultModal('ConfirmationModalWithMessageCtrl',
      'components/modal/confirmation/confirmationModalWithMessage.html',
      // eslint-disable-next-line no-unused-vars
      { message }, result => {
        FreeAgreement.deleteVoidUser({ id: vm.agreement.id, userId: participant.userId })
          .$promise.then(() => { $route.reload() });
      }, null, null, { size: 'xsm' });
  }

  vm.changeFigurants = withoutRentlist => {
    HyUtils.showDefaultModal('AgreementFigurantsModalCtrl',
      'resources/agreement/show/figurants/show.html',
      {
        agreement: vm.agreement,
        caseNumber: vm.negotiation.caseNumber,
        withoutRentlist,
      }, result => {
        vm.agreement.conditionTags.figurants = result.figurants;
        saveAgreement();
      }
    );
  };
}

})();
(function () {
'use strict';

EditNegotiationDelegatesModalCtrl.$inject = ["$uibModalInstance", "$routeParams", "$location", "Flash", "negotiationObject", "Negotiation", "User", "Auth", "Parties", "NegotiationRoles", "$q", "ErrorHandler"];
angular
  .module('hyresgastApp')
  .controller('EditNegotiationDelegatesModalCtrl', EditNegotiationDelegatesModalCtrl);

function EditNegotiationDelegatesModalCtrl(
  $uibModalInstance,
  $routeParams,
  $location,
  Flash,
  negotiationObject,
  Negotiation,
  User,
  Auth,
  Parties,
  NegotiationRoles,
  $q,
  ErrorHandler
) {
  var vm = this;
  vm.cancel = cancel;
  vm.saveDelegation = saveDelegation;
  vm.addDelegateInvitation = addDelegateInvitation;
  vm.chooseRole = chooseRole;
  vm.removeDelegationParticipant = removeDelegationParticipant;
  vm.delegateRole = { name: 'Rollen', code: -1 };

  vm.delegationParticipants = angular.copy(negotiationObject.delegationParticipants);
  vm.delegationInvitations = angular.copy(negotiationObject.delegationInvitations);

  vm.rolesList = NegotiationRoles;

  var roleUnfurling = function(del) {
    var roleCode = del.role;
    if (del.negotiationsUsers) { roleCode = del.negotiationsUsers.role; }

    //this should please UglifyJS
    del.role = {};
    del.role.code = roleCode;
    for (var r in vm.rolesList) {
      var rObj = vm.rolesList[r];
      if(rObj.code===roleCode) {
        del.role.name = rObj.name;
      }
    }
  };

  vm.delegationInvitations.forEach(roleUnfurling);
  vm.delegationParticipants.forEach(roleUnfurling);

  var toBeInvited = [];
  var toBeDeleted = [];

  Auth.heartbeat().then(function() {
    if (Auth.currentUser().party === Parties.HGF) {
      setInviterFullName();
    }
  });

  /////////

  function chooseRole(name, code) {
    vm.delegateRole = { name: name, code: code };
  }

  function setInviterFullName() {
    vm.getInvitors = true;
    vm.delegationParticipants.forEach(function(delegate) {
      return User.get({ id: delegate.negotiationsUsers.inviterId })
        .$promise.then(function(response) {
          delegate.negotiationsUsers.fullName = response.fullName;
        })
        .finally(function() {
          vm.getInvitors = false;
        });
    });
  }

  function removeDelegationParticipant(list, delegate) {
    removeDelegate(list, delegate);
    if (findDelegateByEmail(toBeInvited, delegate.email).length > 0) {
      removeDelegate(toBeInvited, delegate);
    } else {
      toBeDeleted.push(delegate);
    }
  }

  function removeDelegate(list, delegate) {
    var index = list.indexOf(delegate);
    list.splice(index, 1);
  }

  function findDelegateByEmail(list, email) {
    return list.filter(function(delegate) {
      return delegate.email === email;
    });
  }

  function addDelegateInvitation() {

    // look through all invitations to check if they had been added before or not
    var existingInvitationEmail = findDelegateByEmail(vm.delegationInvitations, vm.delegateEmail);
    var existingDelegationEmail = findDelegateByEmail(vm.delegationParticipants, vm.delegateEmail);

    // avoid inviting twice with already existing email
    if (existingDelegationEmail.length === 0 && existingInvitationEmail.length === 0) {
      vm.inviting = true;
      Negotiation.checkDelegation({
        id: $routeParams.id,
        email: vm.delegateEmail,
      })
        .$promise.then(function(response) {
          var delegate = response;
          delegate.role = vm.delegateRole;
          if (delegate.party) {
            vm.delegationParticipants.push(delegate);
          } else {
            vm.delegationInvitations.push(delegate);
          }
          toBeInvited.push(delegate);
          // vm.delegateEmail=""
        })
        .catch(function(error) {
          var delegate = {};
          if (error.data.code === 1) {
            delegate = findDelegateByEmail(negotiationObject.delegationParticipants, vm.delegateEmail)[0];
            if (delegate) {
              vm.delegationParticipants.push(delegate);
              removeDelegate(toBeDeleted, delegate);
              removeDelegate(toBeInvited, delegate);
            }
          } else if (error.data.code === 2) {
            delegate = findDelegateByEmail(negotiationObject.delegationInvitations, vm.delegateEmail)[0];
            if(delegate) {
              vm.delegationInvitations.push(delegate);
              removeDelegate(toBeDeleted, delegate);
              removeDelegate(toBeInvited, delegate);
            }
          } else if (error.data.code === 3) {
            Flash.set('Du får inte bjuda in HGF eller FÄ användare.', 'error');
          } else if (error.status === 403) {
            $location.path('/403');
          } else {
            Flash.set('Misslyckades att bjuda in delegat.', 'error');
          }
        })
        .finally(function() {
          vm.inviting = false;
        });
    } else {
      Flash.set('Denna mailadress finns redan registrerad på delegat i denna förhandling.', 'error');
    }
  }

  function saveDelegation() {
    vm.saving = true;
    $q.all([
      Negotiation.inviteDelegation({
        id: $routeParams.id,
        delegates: toBeInvited
      }).$promise,
      Negotiation.deleteDelegation({
        id: $routeParams.id,
        emails: toBeDeleted
      }).$promise
    ])
      .then(function() {
        $uibModalInstance.close();
        Flash.set('Ändringen har registrerats.', 'success');
      })
      .catch(new ErrorHandler('Misslyckades att spara ändring.'))
      .finally(function() {
        vm.saving = false;
        $uibModalInstance.dismiss('cancel');
      });
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }


}

})();
(function () {
'use strict';

EditNegotiationRepresentativesModalCtrl.$inject = ["$uibModalInstance", "$routeParams", "$location", "$q", "Auth", "Flash", "ErrorHandler", "HyUtils", "negotiationObject", "userParty", "isFreeAgreement", "User", "Negotiation", "FreeAgreement", "Parties", "NegotiationRoles"];
angular
  .module('hyresgastApp')
  .controller('EditNegotiationRepresentativesModalCtrl', EditNegotiationRepresentativesModalCtrl);

function EditNegotiationRepresentativesModalCtrl(
  $uibModalInstance, $routeParams, $location, $q,
  Auth, Flash, ErrorHandler, HyUtils,
  negotiationObject, userParty, isFreeAgreement,
  User, Negotiation, FreeAgreement,
  Parties, NegotiationRoles) {

  var vm = this;
  vm.Parties = Parties;
  vm.NegotiationRoles = NegotiationRoles;

  vm.currentUserParty = Auth.currentUser().party;

  vm.editUserParty = userParty;
  vm.isFreeAgreement = isFreeAgreement;

  vm.invitationResource = vm.isFreeAgreement ? FreeAgreement : Negotiation;

  vm.canEdit = (vm.currentUserParty === vm.editUserParty) ||
               (vm.currentUserParty === vm.Parties.HGF) ||
               vm.isFreeAgreement;

  vm.inviteEmail = '';
  vm.id = $routeParams.id;
  if (!vm.id) {
    // eslint-disable-next-line no-undef
    vm.id = localStorage.getItem('newAvtalId');
  }
  vm.negotiation = {
    id: vm.id,
    party: vm.editUserParty
  };
  processNegotiationObject(negotiationObject);
  var toBeInvited = [];
  var toBeDeleted = [];

  //view actions
  vm.save = save;
  vm.cancel = cancel;
  vm.addParticipant = addParticipant;
  vm.removeParticipant = removeParticipant;
  vm.inviteParticipant = inviteParticipant;
  vm.removeDelegationParticipant = removeDelegationParticipant;

  vm.tagChief = tagChief;
  vm.clearOpposite = clearOpposite;

  //view helpers
  vm.findNegotiationRole = HyUtils.findNegotiationRole;
  vm.deChief = name => {
    if(!name) { return name; }
    var occ = name.indexOf(',');
    if (occ === -1) { return name; }
    return name.substring(0, occ);
  };
  vm.canBeChief = participant =>
    !(participant.roleObj && participant.roleObj.name.includes('Förtroendevald HGF'));
  vm.canDelete = participant => vm.canEdit && !participant.isChief;

  //start
  getUsers();


  function processNegotiationObject(bundle) {
    vm.negotiation.participants = vm.editUserParty === Parties.HGF
      ? angular.copy(bundle.hgfParticipants)
      : angular.copy(bundle.externalParticipants);

    let chief = vm.negotiation.participants.find(participant => participant.isChief === true);

    if (chief) {
      vm.chiefId = chief.id;
    }

    vm.delegationParticipants = angular.copy(bundle.delegationParticipants.filter(p => p.roleObj.party === vm.editUserParty));
    vm.waitingInvites = angular.copy(bundle.delegationInvitations);
    vm.waitingInvites = vm.waitingInvites.filter(wi => wi.roleObj.party === vm.editUserParty);
  }

  function getUsers() {
    return User.query({'filters[party]': vm.editUserParty
    }).$promise.then(function (response) {
      if(vm.editUserParty === vm.Parties.HGF) {
        vm.users = response.rows.filter(r => !r.disabled);
      } else {
        vm.users = response.rows;
      }

      if(vm.editUserParty === vm.Parties.EXTERNAL) {
        User.query({'filters[party]': vm.Parties.DELEGATE}).$promise.then(function(responseDelegates) {
          vm.users = vm.users.concat(responseDelegates.rows);
        });
      }
    }).catch(new ErrorHandler('Misslyckades att hämta data.'));
  }

  function save() {
    if (vm.isFreeAgreement) {
      saveInvites();
      $uibModalInstance.close({
        participants: vm.negotiation.participants,
        waitingInvites: vm.waitingInvites
      });
    } else {
      let joint = vm.negotiation.participants.concat(vm.delegationParticipants);
      let isChiefSelected = joint.length === 0 || joint.some(participant => participant.isChief);

      if (isChiefSelected) {
        vm.working = true;
        Negotiation.saveParticipants(
          { id: vm.id },
          {
            party: vm.editUserParty,
            participants: vm.negotiation.participants,
            delegates: vm.delegationParticipants.filter(d => d.roleObj.party===vm.editUserParty)
          }
        ).$promise.then(() => {
          Flash.set('Lyckades att spara representanterna.', 'success');
          if (vm.editUserParty === Parties.HGF) {
            negotiationObject.hgfParticipants = vm.negotiation.participants;
            negotiationObject.chiefNegotiatorHGF = vm.negotiation.participants.filter(function (participant) {
              return participant.isChief;
            })[0];
          } else if (vm.editUserParty === Parties.EXTERNAL) {
            negotiationObject.externalParticipants = vm.negotiation.participants;
            negotiationObject.chiefNegotiatorExternal = vm.negotiation.participants.filter(function (participant) {
              return participant.isChief;
            })[0];
          }

          saveInvites();
        }).catch(new ErrorHandler('Misslyckades att spara representanterna.'))
          .finally(() => {
            vm.working = false;
            $uibModalInstance.close();
          });
      } else {
        Flash.set('En ansvarig förhandlare måste väljas', 'error');
      }
    }
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }

  function addParticipant(role) {
    let foundParticipant = vm.negotiation.participants.find(user => user.id === vm.selectedParticipant.id);

    // look through all participants to check if they had been added before or not
    if (!foundParticipant) {
      if (vm.negotiation.participants.length === 0) {
        // only one participant who becomes a chief
        vm.selectedParticipant.isChief = true;
        vm.chiefId = vm.selectedParticipant.id;
      } else {
        // keep the current chief not changed
        vm.selectedParticipant.isChief = false;
      }

      vm.selectedParticipant.roleObj = vm.findNegotiationRole(role.code, vm.selectedParticipant.isChief);

      vm.negotiation.participants.push(vm.selectedParticipant);
    }

    vm.selectedParticipant = null;
  }

  function removeParticipant(participant) {
    var index = vm.negotiation.participants.indexOf(participant);
    if (index >= 0) {
      vm.negotiation.participants.splice(index, 1);
    } else {
      index = vm.delegationParticipants.indexOf(participant);
      vm.delegationParticipants.splice(index, 1);
    }
  }

  /**
   * Chiefs selected participant and deChiefs all others
   * @param {Participant} selectedParticipant
   */
  function tagChief(selectedParticipant) {

    var chiefIndex = vm.negotiation.participants.concat(vm.delegationParticipants).indexOf(selectedParticipant);
    vm.chiefId = selectedParticipant.id;

    var firstLength = vm.negotiation.participants.length;

    var index;

    for(index = 0; index < firstLength; index++) {
      let participant = vm.negotiation.participants[index];
      participant.isChief = (index === chiefIndex);
      participant.roleObj = vm.findNegotiationRole(participant.roleObj.code, participant.isChief);
    }
    for(index = 0; index < vm.delegationParticipants.length; index++) {
      let participant = vm.delegationParticipants[index];
      participant.isChief = (index === (chiefIndex - firstLength));
      participant.roleObj = vm.findNegotiationRole(participant.roleObj.code, participant.isChief);
    }
  }

  function clearOpposite(field) {
    if (field === 1) {
      vm.inviteEmail = null;
    } else {
      vm.selectedParticipant = null;
    }
  }


  //==========INVITATION=================================
  function inviteParticipant(role) {

    var email = vm.inviteEmail;

    // look through all invitations to check if they had been added before or not
    var existingInvitationEmail = findDelegatesByEmail(vm.waitingInvites, email);
    var existingDelegationEmail = findDelegatesByEmail(vm.negotiation.participants, email);

    // avoid inviting twice with already existing email
    if (existingDelegationEmail.length === 0 && existingInvitationEmail.length === 0) {
      vm.working = true;
      vm.invitationResource.checkDelegation({
        email,
        id: vm.id,
      }).$promise.then(response => {
        response.roleObj = role;
        if (response.party) {
          vm.negotiation.participants.push(response);
        } else {
          vm.waitingInvites.push(response);
        }
        toBeInvited.push(response);
        vm.inviteEmail = '';
      }).catch(error => {
        var delegate = {};
        if (error.data.code === 1) {
          delegate = findDelegatesByEmail(negotiationObject.delegationParticipants, vm.delegateEmail)[0];
          if (delegate) {
            vm.delegationParticipants.push(delegate);
            removeDelegate(toBeDeleted, delegate);
            removeDelegate(toBeInvited, delegate);
          }
        } else if (error.data.code === 2) {
          delegate = findDelegatesByEmail(vm.waitingInvites, vm.delegateEmail)[0];
          if (delegate) {
            vm.waitingInvites.push(delegate);
            removeDelegate(toBeDeleted, delegate);
            removeDelegate(toBeInvited, delegate);
          }
        } else if (error.data.code === 3) {
          Flash.set('Du får inte bjuda in HGF eller FÄ användare.', 'error');
        } else if (error.status === 403) {
          $location.path('/403');
        } else {
          Flash.set('Misslyckades att bjuda in delegat.', 'error');
        }
      }).finally(() => { vm.working = false });
    } else {
      Flash.set('Denna mailadress finns redan registrerad på delegat i denna förhandling.', 'error');
    }
  }

  function removeDelegationParticipant(list, delegate) {
    removeDelegate(list, delegate);
    if (findDelegatesByEmail(toBeInvited, delegate.email).length > 0) {
      removeDelegate(toBeInvited, delegate);
    } else {
      toBeDeleted.push(delegate);
    }
  }

  function saveInvites() {
    if ((!toBeInvited || toBeInvited.length === 0) &&
        (!toBeDeleted || toBeDeleted.length === 0)) {
      return;
    }
    vm.working = true;
    $q.all([
      vm.invitationResource.inviteDelegation({
        id: vm.id,
        delegates: toBeInvited
      }).$promise,
      vm.invitationResource.deleteDelegation({
        id: vm.id,
        emails: toBeDeleted
      }).$promise
    ])
      .then(() => { Flash.set('Ändringen har registrerats.', 'success') })
      .catch(new ErrorHandler('Misslyckades att spara ändring.'));
  }


  /**
   * @private
   * @param {Array.<object>} list
   * @param {object} delegate
   */
  function removeDelegate(list, delegate) {
    var index = list.indexOf(delegate);
    list.splice(index, 1);
  }

  /**
   * @private
   * @param {Array.<object>} delegates
   * @param {string} email
   * @returns {object}
   */
  function findDelegatesByEmail(delegates, email) {
    return delegates.filter(delegate => delegate.email === email);
  }
}

})();
(function () {
'use strict';

EditNegotiationTypeModalCtrl.$inject = ["$uibModalInstance", "$scope", "$location", "negotiationObject", "Negotiation", "User", "NegotiationTypes", "Flash"];
angular
  .module('hyresgastApp')
  .controller('EditNegotiationTypeModalCtrl', EditNegotiationTypeModalCtrl);

function EditNegotiationTypeModalCtrl(
  $uibModalInstance,
  $scope,
  $location,
  negotiationObject,
  Negotiation, User,
  NegotiationTypes,
  Flash
) {
  var vm = this;
  vm.cancel = cancel;
  vm.save = save;
  vm.NegotiationTypes = NegotiationTypes;

  vm.isPropertyBased =
  negotiationObject.type === NegotiationTypes.ANNUAL_PROPERTY ||
  negotiationObject.type === NegotiationTypes.STANDARD_PROPERTY ||
  negotiationObject.type === NegotiationTypes.REBUILD_PROPERTY ||
  negotiationObject.type === NegotiationTypes.REBUILD_PROPERTY_2 ||
  negotiationObject.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY ||
  negotiationObject.type === NegotiationTypes.UTILITY_VALUE_PROPERTY;

  vm.name = negotiationObject.name;
  vm.type = negotiationObject.type;

  vm.counterpartOrganization = negotiationObject.counterpart;

  let chiefLandlord = negotiationObject.faSide.find(x => x.isChief);
  if (chiefLandlord) {
    User.queryOrganization({ id: chiefLandlord.id })
      .$promise.then(response => {
        vm.possibleCounterparts = response;
      });
  }

  /////////

  function save() {
    vm.saving = true;

    var negotiation= {
      id: negotiationObject.id,
      name: vm.name,
      type: vm.type,
      counterpartOrganizationId: vm.counterpartOrganization.id,
      canSkipRentlist: vm.canSkipRentlist,
    }

    Negotiation.save(negotiation)
      .$promise.then(
        function(response) {
          $uibModalInstance.close(response);
          negotiationObject.counterpart = vm.counterpartOrganization;
          Flash.set('Förhandlingsinformationen har sparats', 'success');
        }
      )
      .catch(function(reason) {
        $uibModalInstance.dismiss('error');
        if (reason.status === 403) {
          $location.path('/403')
        } else {
          Flash.set('Misslyckades att spara förhandlingsinformation', 'error');
        }
      })
      .finally(function() {
        vm.saving = false;
      });
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}
})();
(function () {
'use strict';

SkipRentlistModalCtrl.$inject = ["$uibModalInstance", "$scope", "$location", "negotiationObject", "hasApartments", "Negotiation", "User", "NegotiationTypes", "Flash"];
angular
  .module('hyresgastApp')
  .controller('SkipRentlistModalCtrl', SkipRentlistModalCtrl);

function SkipRentlistModalCtrl(
  $uibModalInstance,
  $scope,
  $location,
  negotiationObject,
  hasApartments,
  Negotiation, User,
  NegotiationTypes,
  Flash
) {
  var vm = this;
  vm.cancel = cancel;
  vm.save = save;

  vm.canSkipRentlist = !negotiationObject.canSkipRentlist;
  vm.hasApartments = hasApartments;

  /////////

  function save() {
    vm.saving = true;

    var negotiation= {
      id: negotiationObject.id,
      name: negotiationObject.name,
      type: negotiationObject.type,
      counterpartOrganizationId: negotiationObject.counterpartOrganizationId,
      canSkipRentlist: vm.canSkipRentlist,
    }

    Negotiation.save(negotiation)
      .$promise.then(
        function(response) {
          $uibModalInstance.close(response);
          negotiationObject.canSkipRentlist = vm.canSkipRentlist;
          Flash.set('Förhandlingsinformationen har sparats', 'success');
        }
      )
      .catch(function(reason) {
        $uibModalInstance.dismiss('error');
        if (reason.status === 403) {
          $location.path('/403')
        } else {
          Flash.set('Misslyckades att spara förhandlingsinformation', 'error');
        }
      })
      .finally(function() {
        vm.saving = false;
      });
  }

  function cancel() {
    $uibModalInstance.close(negotiationObject);
  }
}
})();
(function () {
'use strict';

NewNegotiationCtrl.$inject = ["$scope", "$location", "$routeParams", "$document", "$uibModal", "Flash", "ErrorHandler", "NegotiationCall", "User", "NegotiationTypes", "Parties"];
angular.module('hyresgastApp').controller('NewNegotiationCtrl', NewNegotiationCtrl);

function NewNegotiationCtrl(
  $scope, $location, $routeParams, $document, $uibModal,
  Flash, ErrorHandler,
  NegotiationCall, User,
  NegotiationTypes, Parties
) {
  var vm = this;
  vm.negotiation = {
    id: $routeParams.id, // this is actually the negotiationCall ID, but that is correct here
    isAllDistrictsSelected: true,
    isAllPropertiesSelected: true,
    selectedDistrictIds: {},
    selectedPropertyIds: {},
    conditionTags: { isReadyToSend: true },
  };

  let getTableau = id => angular.element($document[0].querySelector("#"+id));

  vm.setRadioModelByOutsideClick = setRadioModelByOutsideClick;
  function setRadioModelByOutsideClick(choice) {
    vm.choiceMethod = choice;
  }

  vm.rentListBasedNegotiationCall = $routeParams.rentListBased;
  if (vm.rentListBasedNegotiationCall === 'yes') {
    vm.negotiation.isAllApartmentsSelected = true;
    vm.negotiation.selectedApartmentIds = {};
  }

  vm.isPropertyBased =
    $routeParams.negotiationCallType === NegotiationTypes.ANNUAL_PROPERTY ||
    $routeParams.negotiationCallType === NegotiationTypes.STANDARD_PROPERTY ||
    $routeParams.negotiationCallType === NegotiationTypes.REBUILD_PROPERTY ||
    $routeParams.negotiationCallType === NegotiationTypes.REBUILD_PROPERTY_2 ||
    $routeParams.negotiationCallType === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY ||
    $routeParams.negotiationCallType === NegotiationTypes.UTILITY_VALUE_PROPERTY;

  vm.apartmentsSummaryBySelection = {};
  vm.gotoNextStep = gotoNextStep;
  vm.save = save;
  vm.saving = false;
  vm.negotiationTypeInfo = negotiationTypeInfo;

  vm.listDistrictColumns = [
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal fastigheter', column: 'noProperties', filterType: 'none' }
  ];

  vm.listPropertiesColumns = [
    { title: 'Fastigheter', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments', filterType: 'none' },
    { title: 'Fastighetsägare', column: 'owners', type: 'array' }
  ];

  vm.listApartmentsColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Fastighet', column: 'property.propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' }
  ];

  vm.addParticipant = addParticipant;
  vm.removeParticipant = removeParticipant;
  vm.tagChief = tagChief;
  if (vm.isPropertyBased && $routeParams.negotiationCallType !== NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY) {
    vm.allowPartialChoices = [
      { id: 0, name: 'Fastighetsförhandling' },
      { id: 1, name: 'Ägarförhandling' }
    ];
  } else {
    vm.allowPartialChoices = [
      { id: 0, name: 'Fastighetsförhandling' }
    ];
    vm.negotiation.allowPartial = true;
    vm.selectedAllowPartialName = 'Fastighetsförhandling';
  }

  vm.negotiation.participants = [];
  queryUser();

  // Keep it simple - fetch the properties when navigating to the properties step
  // or when some of the other URL parameters for sorting changes
  // instead of i.e fetching every time a districts checkbox is checked
  $scope.$on('$routeUpdate', function() {
    if ($location.search().step === 'properties') {
      // aFlag is used because when we filter the result from queryProperties(),
      // we don't want selectedPropertyIds = {}; to be reseted.
      // We want reset only when the route is changed and response.count != vm.propertyTotalCount.
      vm.propertyFlag = true;
      getTableau('tableauProperties').scope().$broadcast('refresh');
    }

    if ($location.search().step === 'apartments') {
      vm.apartmentsFlag = true;
      getTableau('tableauApartments').scope().$broadcast('refresh');
    }
  });

  ////////////

  function gotoNextStep() {
    vm.gotoNext = true;
  }

  function queryUser() {
    return User.query({
      id: $routeParams.id,
      'filters[party]': Parties.HGF
    }).$promise.then(function(response) {
      vm.users = response.rows;
    })
      .catch(ErrorHandler('Misslyckades att hämta data.'));
  }

  vm.fGetDistricts = function () {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      NegotiationCall.queryDistrict({
        id: $routeParams.id,
        unassigned: true
      }).$promise.then(response => {
        vm.districts = response.rows;
        resolve(response);
      }).catch(ErrorHandler('Misslyckades att hämta område.'));
    });
  }

  vm.fGetProperties = () => {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      if (!vm.selectedDistricts || !vm.selectedDistricts.length) {
        resolve({ rows: [], count: 0 });
        return;
      }
      packNegotiationDistricts();
      NegotiationCall.queryProperty({
        id: $routeParams.id,
        unassigned: true // only list unassigned
      }, {
        selectedDistrictIds: vm.negotiation.selectedDistrictIds,
        isAllDistrictsSelected: vm.negotiation.isAllDistrictsSelected,
      }).$promise.then(response => {
        vm.properties = response.rows;
        resolve(response);
      });
    });
  };

  vm.fGetApartments = () => {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      let apartmentsDisabled = !vm.choiceMethod || vm.choiceMethod === 'ALL_PROPERTIES' || (vm.choiceMethod === 'SELECT_PROPERTIES' && vm.rentListBasedNegotiationCall==='no');
      if (apartmentsDisabled || !vm.selectedDistricts || !vm.selectedProperties) {
        resolve({ rows: [], count: 0 });
        return;
      }
      packNegotiationDistricts();
      packNegotiationProperties();
      NegotiationCall.queryApartment({
        id: $routeParams.id,
        unassigned: true
      }, {
        selectedDistrictIds: vm.negotiation.selectedDistrictIds,
        selectedPropertyIds: vm.negotiation.selectedPropertyIds,
        isAllDistrictsSelected: vm.negotiation.isAllDistrictsSelected,
        isAllPropertiesSelected: vm.negotiation.isAllPropertiesSelected
      }).$promise.then(function(response) {
        vm.apartments = response.rows;
        resolve(response);
      }).catch(ErrorHandler('Misslyckades att hämta lägenhet.'));
    });
  };

  function addParticipant() {
    var foundParticipant = vm.negotiation.participants.filter(function(user) {
      return user.id === vm.selectedParticipant.id;
    });

    // look through all participants to check if they had been added before or not
    if (foundParticipant.length === 0) {
      if (vm.negotiation.participants.length === 0) {
        // only one participant who becomes a chief
        vm.selectedParticipant.isChief = true;
        vm.chiefId = vm.selectedParticipant.id;
      } else {
        // keep the current chief not changed
        vm.selectedParticipant.isChief = false;
      }
      vm.negotiation.participants.push(vm.selectedParticipant);
    }

    // clear input field
    vm.selectedParticipant = null;
  }

  function tagChief(taggedParticipant) {
    vm.negotiation.participants.forEach(function(participant) {
      participant.isChief = false;
    });

    var index = vm.negotiation.participants.indexOf(taggedParticipant);
    vm.negotiation.participants[index].isChief = true;
    vm.chiefId = taggedParticipant.id;
  }

  function removeParticipant(participant) {
    var index = vm.negotiation.participants.indexOf(participant);
    vm.negotiation.participants.splice(index, 1);

    if (!isChiefSelected()) {
      if (vm.negotiation.participants.length > 0) {
        vm.negotiation.participants[0].isChief = true;
        vm.chiefId = vm.negotiation.participants[0].id;
      } else {
        vm.chiefId = null
      }
    }
  }

  function isChiefSelected() {
    return vm.negotiation.participants.some(function(participant) {
      return participant.isChief === true;
    })
  }

  let packEntity = (selectedArray, array) => {
    let selectedIds = array.reduce((acc, el) => {
      acc[el.id] = selectedArray.some(x => x.id === el.id);
      return acc;
    }, {});
    let isAllSelected = array.length === vm.negotiation.selectedDistrictIds.length;
    return { selectedIds, isAllSelected };
  };
  function packNegotiationDistricts() {
    let pack = packEntity(vm.selectedDistricts, vm.districts);
    vm.negotiation.selectedDistrictIds = pack.selectedIds;
    vm.negotiation.isAllDistrictsSelected = pack.isAllSelected;
  }
  function packNegotiationProperties() {
    let pack = packEntity(vm.selectedProperties, vm.properties);
    vm.negotiation.selectedPropertyIds = pack.selectedIds;
    vm.negotiation.isAllPropertiesSelected = pack.isAllSelected;
  }
  function packNegotiationApartments() {
    let pack = packEntity(vm.selectedApartments, vm.apartments);
    vm.negotiation.selectedApartmentIds = pack.selectedIds;
    vm.negotiation.isAllApartmentsSelected = pack.isAllSelected;
  }

  function save() {
    if (isChiefSelected()) {
      vm.saving = true;
      var selectPropertiesMode = vm.choiceMethod === 'SELECT_PROPERTIES' ? true : false;
      vm.negotiation.selectPropertiesMode = selectPropertiesMode;
      if (selectPropertiesMode) {
        packNegotiationDistricts();
        packNegotiationProperties();
      }
      if (vm.selectedApartments && vm.selectedApartments.length > 0) {
        packNegotiationApartments();
      }

      NegotiationCall.createNegotiation(vm.negotiation)
        // eslint-disable-next-line no-unused-vars
        .$promise.then(function(response) {
          $location.search({});
          $location.path('/negotiation-calls/' + $routeParams.id).search({ view: 'negotiations' });
          Flash.set('Lyckades skapa förhandling', 'success');
        })
        .catch(function() {
          Flash.set('Misslyckades att skapa förhandling', 'error');
        })
        .finally(function() {
          vm.saving = false;
        });
    } else {
      Flash.set('En ansvarig förhandlare måste väljas', 'error');
    }
  }

  function negotiationTypeInfo() {
    $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'md',
      index: 1075,
      templateUrl: 'components/modal/information/negotiationTypeInfoModal.html',
      controller: 'InformationModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--green'
    });
  }
}

})();
(function () {
'use strict';

NewDirectNegotiationCtrl.$inject = ["$scope", "$location", "$document", "Flash", "ErrorHandler", "HyUtils", "DateLocal", "User", "Negotiation", "ManualChange", "RealEstate", "OrderedNegotiationTypes", "PropertyNegotiationTypes", "ApartmentNegotiationTypes", "Limits"];
angular.module('hyresgastApp').controller('NewDirectNegotiationCtrl', NewDirectNegotiationCtrl);

function NewDirectNegotiationCtrl(
  $scope, $location, $document,
  Flash, ErrorHandler, HyUtils, DateLocal,
  User, Negotiation, ManualChange, RealEstate,
  OrderedNegotiationTypes, PropertyNegotiationTypes, ApartmentNegotiationTypes, Limits) {
  var vm = this;
  vm.onlyMyProperties = true;
  vm.Limits = Limits;
  vm.NegotiationTypes = OrderedNegotiationTypes;
  vm.ApartmentNegotiationTypes = ApartmentNegotiationTypes;

  vm.negotiation = {
    participants: [],
    conditionTags: { isReadyToSend: true },
  };

  vm.selector = {
    fgetProperties: ManualChange.queryPropertiesInMyNegotiations,
    fgetApartments: ManualChange.queryApartments,
  };
  vm.changePropertiesMethod = onlyMyProperties => {
    vm.selector.fgetProperties = onlyMyProperties
      ? ManualChange.queryPropertiesInMyNegotiations
      : ManualChange.queryProperty;

    let selector = angular.element($document[0].querySelector("#apartmentSelector"));
    selector.scope().$broadcast('refreshProperties');
  };

  vm.partyDescription = 'hyresvärd/ombud';
  vm.showAddNegotiatorLink = true;

  vm.showNegotiationTypeInfo = () => {
    HyUtils.showDefaultModal('InformationModalCtrl',
      'components/modal/information/negotiationCallInfoModal.html',
      {}, null, null, null,
      { isGreen: true });
  };

  vm.negotiationTypeInfo = () => {
    HyUtils.showDefaultModal('InformationModalCtrl',
      'components/modal/information/negotiationTypeInfoModal.html',
      {}, null, null, null,
      { isGreen: true });
  }

  vm.allowPartialChoices = [
    { id: 0, name: 'Fastighetsförhandling' },
    { id: 1, name: 'Ägarförhandling' }
  ];

  User.queryOp({'filters[party]': 0})
    .$promise.then(result => {
      vm.users = result.rows.filter(u => u.organizations && u.organizations.length);
    });

  let changeChiefOrganizations = () => {
    let chief = vm.negotiation.participants.find(p => p.isChief);
    vm.chiefParticipantOrganizations = chief ? chief.organizations : null;
    if (vm.chiefParticipantOrganizations && vm.chiefParticipantOrganizations.length === 1) {
      vm.selectedOrganization = vm.chiefParticipantOrganizations[0];
    }else{
      vm.selectedOrganization = null;
    }
  };

  vm.tagChief = (taggedParticipant) => {
    vm.negotiation.participants.forEach(p => p.isChief = (p.id === taggedParticipant.id));
    vm.chiefId = taggedParticipant.id;
    changeChiefOrganizations();
  };

  vm.addParticipant = () => {
    let foundParticipant = vm.negotiation.participants.filter(user => user.id === vm.selectedParticipant.id);

    if (foundParticipant.length === 0) {
      if (vm.negotiation.participants.length === 0) {
        vm.selectedParticipant.isChief = true;
        vm.chiefId = vm.selectedParticipant.id;
      } else {
        vm.selectedParticipant.isChief = false;
      }
      vm.negotiation.participants.push(vm.selectedParticipant);
    }
    vm.selectedParticipant = null;
    changeChiefOrganizations();
  };

  vm.motivFGetData = function () {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      resolve({ rows: vm.properties, count: vm.properties.length });
    });
  };
  let refreshTableau = id => angular.element($document[0].querySelector("#"+id))
    .scope().$broadcast('refresh');
  vm.motivColumns = [
    { title: 'Namn', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'Fastighetsägare', column: 'owners', type: 'array' },
    { title: 'Yrkande', column: 'formattedDemand' },
    { title: 'Motiv', column: 'demand.motivation' },
  ];
  vm.selectedProperties = [];
  vm.motivActions = [
    {
      title: 'Lägg till yrkande och motiv',
      action: () => { vm.editDemandAndMotivation(); }
    }
  ];
  $scope.$on('$routeUpdate', function() {
    if ($location.search().step === 'motiv') {
      refreshTableau('motivTableau');
    }
  });
  // eslint-disable-next-line eqeqeq
  vm.getFormattedDemand = demand => demand.unit == 2
    ? demand.stringValue
    // eslint-disable-next-line eqeqeq
    : `${demand.numericValue} ${demand.unit == 1 ? 'kr/kvm/år' : '%'}`;
  vm.editDemandAndMotivation = () => {
    HyUtils.showDefaultModal('EditMotivationModalCtrl',
      'resources/negotiation-call/new/modals/edit-motivation-modal.html', {
        propertiesArr: vm.selectedProperties,
        negotiationType: vm.negotiation.type,
      }, demand => {
        vm.selectedProperties.forEach(property => {
          angular.extend(property, { demand });
          property.formattedDemand = vm.getFormattedDemand(demand);
        });
        vm.demandsCreated = vm.properties.every(p => p.demand && p.demand.unit);
        refreshTableau('motivTableau');
      }, null, null, { size: 'md' });
  };

  $scope.$watch('vm.negotiationType', function() {
    let negotiationTypes = Object.values(PropertyNegotiationTypes);
    vm.isPropertyBased = negotiationTypes.includes(vm.negotiationType);
  });

  let isChiefSelected = () =>
    vm.negotiation.participants.some(participant => participant.isChief === true);

  vm.removeParticipant = (participant) => {
    var index = vm.negotiation.participants.indexOf(participant);
    vm.negotiation.participants.splice(index, 1);

    if (!isChiefSelected()) {
      if (vm.negotiation.participants.length > 0) {
        vm.negotiation.participants[0].isChief = true;
        vm.chiefId = vm.negotiation.participants[0].id;
      } else {
        vm.chiefId = null;
      }
    }
    changeChiefOrganizations();
  }

  vm.saving = false;
  vm.save = () => {
    HyUtils.showDefaultModal('SelectOfficeModalCtrl',
      'resources/negotiation/new-direct/modals/select-office-modal.html',
      {},
      res => {
        vm.negotiation.hgfOfficeId = res.selectedOffice.id;
        persistNegotiation();
      }, null, null, { size: 'sm' });
  }

  function persistNegotiation() {
    vm.saving = true;
    Negotiation.createDirect({
      name: vm.negotiationName,
      rentEffectiveFrom: DateLocal.fromUTCtoLocal(vm.rentEffectiveFrom),
      type: vm.negotiationType,
      hgfOfficeId: vm.negotiation.hgfOfficeId,
      message: vm.negotiationMessage,
      allowPartial: vm.negotiation.allowPartial,
      participants: vm.negotiation.participants,
      apartments: vm.selectedApartments,
      emptyProperties: vm.properties,
      selectedOrganization: vm.selectedOrganization,
      conditionTags: vm.negotiation.conditionTags,
    }).$promise.then(result => {
      Flash.set('Förhandling har skapats', 'success');
      $location.path("/negotiations/" + result.id);
    }).catch(ErrorHandler('Misslyckades skapa förhandling'))
      .finally(() => { vm.saving = false; });
  }

  vm.showAddNegotiatorModal = () => {
    HyUtils.showDefaultModal('NewUserModalCtrl',
      'resources/user/new/new-user-modal.html',
      { fullMode: false },
      result => {
        let newUser = result.user;
        if (vm.negotiation.participants.length === 0) {
          newUser.isChief = true;
          vm.chiefId = newUser.id;
        } else {
          newUser.isChief = false;
        }
        vm.negotiation.participants.push(newUser);
        changeChiefOrganizations();
      });
  }

  vm.searchProperties = () => {
    vm.toggleMenu = false;
    vm.searching = true;
    RealEstate.querySimple({ 'search[complex]': vm.inputTextComplex }, {})
      .$promise.then(response => {
        vm.propertiesTypeahead = response.rows;
        if (vm.propertiesTypeahead.length > 0) {
          vm.toggleMenu = true;
        }
      })
      .catch(new ErrorHandler('Fel'))
      .finally(() => { vm.searching = false });
  };
  vm.addProperty = property => {
    vm.readyProperties = true;
    if (!vm.properties) {
      vm.properties = [];
    }

    let exists = vm.properties.filter(item => item.id === property.id).length;
    if (!exists) {
      vm.properties.push(property);
    }
  };
}
})();
(function () {
'use strict';

ListNegotiationCtrl.$inject = ["$scope", "$location", "$routeParams", "$route", "$filter", "Negotiation", "Seenby", "HyUtils", "SubPage", "Auth", "Flash", "ErrorHandler", "ExportUtils", "Parties", "ObjectTypes", "CombinedNegotiationStates"];
angular.module('hyresgastApp').controller('ListNegotiationCtrl', ListNegotiationCtrl);

function ListNegotiationCtrl($scope, $location, $routeParams, $route, $filter,
  Negotiation, Seenby,
  HyUtils, SubPage, Auth, Flash, ErrorHandler, ExportUtils,
  Parties, ObjectTypes, CombinedNegotiationStates) {
  var vm = this;
  vm.Auth = Auth;

  Auth.heartbeat().then(function() {
    vm.headerButtons = [
      {
        title: 'Skapa en framställan',
        url: '/negotiation-calls/new',
        isVisible: () => (Auth && Auth.canDo('createCall')),
        icon: 'call'
      },
    ];
    vm.isHgfUser = Auth.currentUser().party === Parties.HGF;

    getSeenbyCounters();
  });

  let setMarkAsRead = () => {
    if (vm.seenbyCounters) {
      const pages = {
        'my-negotiations': 'c_mine',
        'my-office': 'c_myoffices',
        'all': 'c_all',
      };
      vm.showMarkAsRead = vm.currentSubpage === 'my-negotiations' &&
        vm.seenbyCounters[pages[vm.currentSubpage]] > 0;
    }
  }

  getCurrentSubpage();

  let setupTabs = () => {
    vm.tablist = [
      { title: 'Mina', path: '/negotiations?view=my-negotiations',
        number: vm.seenbyCounters.c_mine, numberTooltip: 'Antal inkomna förhandlingar som inte öppnats' },
      { title: 'På mina kontor', path: '/negotiations?view=my-office' },
      { title: 'Per förhandlare', path: '/negotiations?view=responsibles' },
      { title: 'Alla', path: '/negotiations?view=all' },
    ];
    if (!vm.isHgfUser) {
      vm.tablist.splice(1, 2);
    }
  };

  let getSeenbyCounters = () => {
    Negotiation.getSeenbyCounters({})
      .$promise.then(response => {
        vm.seenbyCounters = response;
        setupTabs();
        setMarkAsRead();
      });
  }

  $scope.$on('$routeUpdate', function() {
    // avoid unecessary calling of getCurrentSubpage(); when sorting
    if (!$location.search().order) {
      // reset offset when sorting
      getCurrentSubpage();
    }
  });

  /////////

  function getCurrentSubpage() {
    var defaultSubPage = 'my-negotiations';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = ['my-negotiations', 'my-office', 'responsibles', 'all'];
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
    setMarkAsRead();
  }

  const vmList = {
    'my-negotiations': 'myNegVM',
    'my-office': 'officesVM',
    'responsibles': 'responsiblesVM',
    'all': 'allVM'
  };
  vm.changeNegotiator = function () {
    let child = HyUtils.bruteSearch($scope, vmList[vm.currentSubpage]);
    if (child) {
      child.getAllNegotiations(negotiations => {
        HyUtils.showDefaultModal('ChangeNegotiatorModalCtrl',
          'resources/negotiation/list/change-negotiator/change-negotiator-modal.html', {
            negotiations,
          }, result => { Negotiation.changeNegotiators(result) });
      });
    }
  };
  vm.markAsRead = function () {
    let child = HyUtils.bruteSearch($scope, vmList[vm.currentSubpage]);
    if (child) {
      child.getAllNegotiations(negotiations => {
        let negotiationsToMark = negotiations.rows
          .filter(x => !x.seenByMe)
          .map(x => x.id);
        Seenby.markAsRead({
          objectType: ObjectTypes.NEGOTIATION.code,
          objectIds: negotiationsToMark,
        // eslint-disable-next-line no-unused-vars
        }).$promise.then(response => {
          Flash.set('Markerad som läst', 'success');
          $route.reload();
        }).catch(ErrorHandler('Misslyckades att markerad som läst'));
      });
    }
  };

  vm.beginExport = function () {
    vm.exportStatuses = Object.values(CombinedNegotiationStates).map(code => ({ code, isSet: true }));
    vm.showExportPopup = true;
    vm.exportBusy = false;
  };
  vm.export = function () {
    vm.exportBusy = true;
    const propertyText = property => `${property.propertyDesignation}, ${property.municipality}`;
    const fullName = user => `${user.firstname} ${user.lastname}`;
    let child = HyUtils.bruteSearch($scope, vmList[vm.currentSubpage]);
    if (child) {
      child.getAllNegotiations(negotiations => {
        let selectedStatuses = vm.exportStatuses.filter(x => x.isSet).map(x => x.code);
        negotiations.rows = negotiations.rows.filter(n => selectedStatuses.includes(n.combinedState));
        let sheetData = [];
        negotiations.rows.forEach(x => {

          let excelRow = {
            'Id': x.caseNumber,
            'Namn': x.name,
            'Förhandlingstyp': $filter('hyNegotiationTypes')(x.type),
            'Organisation': x.counterpart.name,
            'Förhandlare': fullName(x.hgfNegotiator ? x.hgfNegotiator : x.participants),
            'Begärt ändringsdatum': x.rentEffectiveFrom ? $filter('date')(x.rentEffectiveFrom, 'yyyy-MM-dd') : 'Ej angivet',
            'Önskat datum för förhandling': x.callForNegotiation.negotiationDate ? $filter('date')(x.callForNegotiation.negotiationDate, 'yyyy-MM-dd') : 'Ej angivet',
            'Slutdatum': x.finishDate ? $filter('date')(x.finishDate, 'yyyy-MM-dd') : 'Ej angivet',
            'Fastighet': x.properties && x.properties.length ? (propertyText(x.properties[0]) + (x.properties[0].used ? ' (avslutad)': ' ')) : '',
            'Ägare': x.properties && x.properties.length && x.properties[0].owners.length
              ? x.properties[0].owners.map(o => o.name).join(', ')
              : '',
            'Status': $filter('hyCombinedNegotiationStates')(x.combinedState),
            'Kommentar': x.comment ? x.comment : '',
          };
          if (x.extNegotiator) {
            excelRow['Ansvarig motpart'] = fullName(x.extNegotiator);
          }
          sheetData.push(excelRow);

          if (x.properties && x.properties.length > 1) {
            x.properties.slice(1).forEach(p => {
              // eslint-disable-next-line no-undef
              let copyRow = structuredClone(excelRow);
              copyRow['Fastighet'] = (propertyText(p) + (p.used ? ' (avslutad)': ' '));
              copyRow['Ägare'] = p.owners.length
                ? p.owners.map(o => o.name).join(', ')
                : '';
              delete copyRow['Id'];
              delete copyRow['Namn'];
              sheetData.push(copyRow);
            });
          }
        });

        let buffer = ExportUtils.prettyExcel(sheetData);

        HyUtils.spitOutAttachment(buffer, 'Förhandlingarlista export.xlsx', 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet');
        vm.showExportPopup = false;

      }, true);
    }
  };

  vm.editComment = negotiation => {
    HyUtils.showDefaultModal('EditCommentModalCtrl',
      'resources/agreement/rentlist/edit/edit-comment-modal.html',
      { comment: negotiation.comment, multipleComments: '' },
      result => {
        if (result.delete || result.comment) {
          negotiation.comment = result.delete ? '' : result.comment;
          Negotiation.saveComments([{
            negotiationId: negotiation.id,
            comment: negotiation.comment,
          }]);
        }
      }, null, null,
      { size: 'md' });
  };

  vm.getContextInfo = row => ({
    title: `Förhandling ${row.caseNumber}`,
    link: '/negotiations/'+row.id,
  });
}

})();
(function () {
'use strict';

ListNegotiationCallCtrl.$inject = ["$scope", "$location", "$routeParams", "Auth", "SubPage"];
angular.module('hyresgastApp').controller('ListNegotiationCallCtrl', ListNegotiationCallCtrl);

function ListNegotiationCallCtrl($scope, $location, $routeParams, Auth, SubPage) {
  var vm = this;

  vm.headerButtons = [
    {
      title: 'Skapa en framställan',
      url: '/negotiation-calls/new',
      isVisible: () => (Auth && Auth.canDo('createCall')),
      icon: 'call',
    }
  ];
  vm.tabs = Auth.isHgfUser() ? [
    { title: 'Nya', path: '/negotiation-calls?view=list-new' },
    { title: 'Alla', path: '/negotiation-calls?view=list-all' }
  ] : [];

  getCurrentSubpage();

  $scope.$on('$routeUpdate', function() {
    // avoid unecessary calling of getCurrentSubpage(); when sorting
    if (!$location.search().order) {
      // reset offset when sorting
      getCurrentSubpage();
    }
  });

  /////////

  function getCurrentSubpage() {
    var defaultSubPage = 'list-all';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = ['list-new', 'list-all'];
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }

  vm.getContextInfo = row => ({
    title: `Framställning ${row.caseNumber}`,
    link: '/negotiation-calls/'+row.id,
  });
}

})();
(function () {
'use strict';

EditOrganizationCtrl.$inject = ["$filter", "$location", "$routeParams", "$uibModal", "Flash", "Organization", "Office", "ErrorHandler", "HyUtils"];
angular.module('hyresgastApp').controller('EditOrganizationCtrl', EditOrganizationCtrl);

function EditOrganizationCtrl($filter, $location, $routeParams, $uibModal,
  Flash, Organization, Office, ErrorHandler, HyUtils) {
  // Responsible for create, update and delete

  var vm = this;
  vm.organization = {};
  vm.save = save;
  vm.delete = destroy;
  vm.displayBlockingData = displayBlockingData;

  get();

  if ($routeParams.id) {
    vm.editMode = true;
    get();
    getOffices();
  } else {
    vm.ready = true;
    getOffices();
  }

  ////////////

  function get() {
    Organization.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.ready = true;
        vm.organization = response;
        vm.organization.organizationNumber = $filter('hyOrgNr')(vm.organization.organizationNumber);
      })
      .catch(ErrorHandler('Misslyckades att hämta organisation.'))
  }

  function getOffices() {
    Office.query().$promise.then(function(response) {
      vm.offices = response;
    })
      .catch(ErrorHandler('Misslyckades att hämta kontor.'));
  }

  function save() {
    vm.saving = true;

    Organization.save(vm.organization)
      .$promise.then(
        function(response) {
          Flash.set('Informationen har sparats.', 'success');
          $location.path('/organizations/' + response.id);
        })
      .catch(function(reason) {
        if (reason.status === 409) {
          Flash.set('Organisationsnumret existerar redan', 'error');
        } else if (reason.status === 403) {
          $location.path('/403')
        } else {
          Flash.set('Kontrollera organisationsnumret', 'error');
        }
      })
      .finally(function() {
        vm.saving = false;
      });
  }

  function destroy() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'xsm',
      templateUrl: 'components/modal/confirmation/confirmationModal.html',
      controller: 'ConfirmationModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--secondary'
    });

    modalInstance.result.then(function() {
      Organization.delete(vm.organization).$promise.then(
        function(response) {
          Flash.set('Organisationen borttagen', 'success');
          $location.path('/organizations');
        })
        .catch(function(response) {
          if(response.data === undefined || Object.keys(response.data).length === 0) {
            new ErrorHandler('Okänt fel.');
          }

          for (const [key, value] of Object.entries(response.data)) {
            if(value.length > 0 && response.status === 403) {
              displayBlockingData(response.data);
              break;
            }
          }
        });
    });
  }

  function displayBlockingData(blockingData) {
    HyUtils.showDefaultModal('BlockingDeletionModalCtrl',
      'resources/organization/show/modals/blocking-deletion-modal.html', { blockingData });
  }
}

})();
(function () {
'use strict';

NewNegotiationCallCtrl.$inject = ["$scope", "$location", "$uibModal", "$routeParams", "$timeout", "NegotiationTypes", "OrderedNegotiationTypes", "ValidFileTypes", "AttachmentTypes", "NegotiationCall", "RealEstate", "Organization", "User", "ManualChange", "DateLocal", "Auth", "HyUtils", "DateCounter", "Flash", "ErrorHandler", "Help"];
angular.module('hyresgastApp').controller('NewNegotiationCallCtrl', NewNegotiationCallCtrl);

function NewNegotiationCallCtrl(
  $scope, $location, $uibModal, $routeParams, $timeout,
  NegotiationTypes, OrderedNegotiationTypes, ValidFileTypes, AttachmentTypes,
  NegotiationCall, RealEstate, Organization, User,
  ManualChange,
  DateLocal, Auth, HyUtils,
  DateCounter, Flash, ErrorHandler,
  Help
) {
  // ------------------------------------------------------------------------------------------------------------
  // In many places in this file, we deal with objects that can be either a property (fastighet) or an apartment.
  // For historical reasons, in most places the variables used are named as if these objects are always properties.
  // For example, vm.properties should really be called vm.propertiesOrApartments or something similar.
  // ------------------------------------------------------------------------------------------------------------
  const propertyTypes = [
    NegotiationTypes.ANNUAL_PROPERTY,
    NegotiationTypes.STANDARD_PROPERTY,
    NegotiationTypes.REBUILD_PROPERTY,
    NegotiationTypes.REBUILD_PROPERTY_2,
    NegotiationTypes.UTILITY_VALUE_PROPERTY,
  ];
  const apartmentTypes = [
    NegotiationTypes.STANDARD_APARTMENT,
    NegotiationTypes.REBUILD_APARTMENT,
    NegotiationTypes.UTILITY_VALUE_APARTMENT,
  ];

  var vm = this;
  vm.NegotiationTypes = OrderedNegotiationTypes;
  vm.isHgf = Auth.isHgfUser();
  vm.lastHeader = vm.isHgf ? 'Förhandsgranska och skapa' : 'Förhandsgranska och skicka in';
  vm.lastSubheader = vm.isHgf ? 'Skapa' : 'Skicka in';
  vm.lastButton = vm.isHgf ? 'Skapa' : 'Skicka';
  if (vm.isHgf) {
    vm.showAddNegotiatorLink = true;
    vm.partyDescription = 'hyresvärd/ombud';
  }

  vm.apartmentSelector = {
    fgetProperties: ManualChange.queryProperty,
    fgetApartments: ManualChange.queryApartments,
  };

  vm.saving = false;
  vm.attachments = [];
  vm.negotiationCall = {};
  vm.selectedPropertyIds = {};
  vm.properties = [];
  vm.readyOwners = {};
  vm.toggleOwnersList = {};
  vm.uploadedDocumentsList = [];
  vm.rentListAlreadyImported = false;
  vm.pageLimit = 30;
  vm.currentPage = 0;
  vm.firstRecordInCurrentPage = 1;

  vm.filePlateBuffers = [];

  vm.showNegotiationTypeInfo = showNegotiationTypeInfo;
  vm.isRealEstateChoiceMethodEnabled = isRealEstateChoiceMethodEnabled;
  vm.setRadioModelByOutsideClick = setRadioModelByOutsideClick;
  vm.searchProperties = searchProperties;
  vm.getPropertyOwners = getPropertyOwners;
  vm.addProperty = addProperty;
  vm.removeProperty = removeProperty;
  vm.editDemandAndMotivation = editDemandAndMotivation;
  vm.toggleAllPropertiesSelection = toggleAllPropertiesSelection;
  vm.updateAllPropertiesBoxState = updateAllPropertiesBoxState;
  vm.deleteUploadedFile = deleteUploadedFile;
  vm.deleteDocument = deleteDocument;
  vm.save = save;
  vm.showHelpProperties = (title, bodyText) => Help.renderHelpModal(title, bodyText);

  // used to disable 'Ange yrkande & motiv' button
  vm.atLeastOneSelectedPropertyId = () => Object.keys(vm.selectedPropertyIds)
    .some(propertyId => vm.selectedPropertyIds[propertyId]);
  vm.getColumnTitle = title => title instanceof Array ? title[0] : title.split(',')[0].trim();

  vm.getMotivationType = () => {
    let type = vm.isPropertyBased ? 'property' : (vm.isApartmentBased ? 'apartment' : 'bubkis');

    if (vm.isHgf && vm.realEstateChoiceMethod === 'DIRECT_APARTMENTS' && vm.emptyProperties && vm.emptyProperties.length === 0) {
      type = 'apartment';
    }

    return type;
  };

  vm.filterProperties = filterProperties;
  vm.sortProperties = sortProperties;

  vm.sortPropertyColumn = 'propertyDesignation';
  vm.sortPropertyTitle = 'Fastighetsbeteckning';
  vm.sortPropertyReverse = false;
  vm.listPropertyColumns = [{
    title: 'Ägare',
    column: Auth.isHgfUser() ? 'owners' : 'primaryOwner'
  },
  {
    title: 'Fastighetsbeteckning',
    column: 'propertyDesignation'
  },
  {
    title: 'Kommun',
    column: 'municipality'
  },
  {
    title: 'LM distrikt',
    column: 'district'
  },
  ];

  vm.listApartmentColumns = [{
    title: 'Ägare',
    column: 'owners'
  },
  {
    title: 'Kommun',
    column: 'municipality'
  },
  {
    title: 'Fastighetsbeteckning',
    column: 'propertyDesignation'
  },
  {
    title: 'Gatuadress',
    column: 'streetAddress'
  },
  {
    title: 'Nationella lägenhetsnr',
    column: 'nationalNo'
  },
  {
    title: 'Lägenhetsnr',
    column: 'ownerNo'
  },
  ];

  let setupDatepickers = () => {
    vm.incomingDateOptions = {
      minDate: null,
    };
    let rentEffectiveFromAdvance = vm.negotiationCall.type === propertyTypes[0] ? 90 : 0;
    vm.rentEffectiveFromDateOptions = {
      minDate: vm.isHgf ? null : DateCounter.getFutureDate(rentEffectiveFromAdvance),
      initDate: DateCounter.getFutureDate(rentEffectiveFromAdvance),
    };
    vm.negotiationDateDateOptions = {
      // get 5 working days after today
      minDate: vm.isHgf ? null : DateCounter.getFutureDate(5, true),
      noWeekends: true,
    };
  }

  function start() {
    if ($routeParams.id) {
      vm.editMode = true;
      getNegotiationCall();
    }
    //query organization
    Organization.query()
      .$promise.then(function(response) {
        vm.organizations = response.rows;
        if (vm.organizations.length === 1) {
          vm.selectedOrganizationName = response.rows[0].name;
          vm.negotiationCall.counterpartOrganizationId = response.rows[0].id;
        }
      })
      .catch(function() {
        new ErrorHandler('Misslyckades att hämta organisation.');
      });
    //negotiator for HGF-type call
    User.queryOp({'filters[party]': 0})
      .$promise.then(result => {
        vm.users = result.rows.filter(u => u.organizations && u.organizations.length);
        //vi har template från nya-direkt
        //bättre göra komponent med deltagarval och chefstaggning
        vm.negotiation = { participants: [] };
      });
    setupDatepickers();
  }
  start();


  $scope.$watch('vm.negotiationCall.type', function() {
    // set default choice mode to 'RENT_LIST' if there are more than 1 choice available
    if (!vm.editMode) {
      var activeRealEstateChoiceMethods = Object.keys(
        getRealEstateChoiceMethodsByNegotiationType(vm.negotiationCall.type)
      );
      if (activeRealEstateChoiceMethods.length > 1) {
        let bestand = vm.isHgf ? 'DIRECT_APARTMENTS' : 'DIRECT_PROPERTIES';
        vm.realEstateChoiceMethod = activeRealEstateChoiceMethods.includes('DIRECT_APARTMENTS')
          ? bestand
          : 'RENT_LIST';
      } else {
        vm.realEstateChoiceMethod = activeRealEstateChoiceMethods[0];
      }
    }
    setupDatepickers();
    vm.isPropertyBased = propertyTypes.includes(vm.negotiationCall.type);
    vm.isApartmentBased = apartmentTypes.includes(vm.negotiationCall.type);
  });

  // The wizard uses a file upload directive and the directive is validating the content
  // of uploaded files (excel). If the file upload directive receives errors from the backend
  // we want to open a modal to show the errors to the user
  $scope.$watch('vm.validationResponse', function(validationResponse) {
    let isPropertyFile = vm.realEstateChoiceMethod === 'PROPERTY_LIST';
    if (validationResponse) {
      vm.status = validationResponse;
      if (vm.validationResponse.status === 'ok') {
        vm.status.phase = 'Success';
        vm.status.comment = '';
        vm.inputBlobId = isPropertyFile ? vm.negotiationCall.propertyListBlobId : vm.negotiationCall.rentListBlobId;
      } else if (vm.validationResponse.status === 'error') {
        if (isPropertyFile) {
          vm.propertyFileUploader.cancelUpload();
        } else {
          vm.uploader.cancelUpload();
        }
        HyUtils.showDefaultModal('ErrorModalCtrl', 'resources/validation/errors.html', {
          validationResponse, realEstateChoiceMethod: vm.realEstateChoiceMethod,
        });
      } else {
        if (isPropertyFile) {
          vm.propertyFileUploader.cancelUpload();
        } else {
          vm.uploader.cancelUpload();
        }
      }
    } else {
      vm.status = {
        phase: 'read excel file',
        total: 0,
        errors: 0,
        warnings: 0,
        progress: 0,
        showProgress: false,
      };
    }
  });

  $scope.$watch('vm.attachmentBlobId', function(blobId) {
    if (blobId) {
      vm.uploadedDocumentsList.push({
        blobId: blobId,
        fileName: vm.uploaderOptionalDocuments.currentFileName
      });
      // After upload of file we have to do cancelUpload on uploader
      // This makes the uploader ready for another upload.
      Flash.set('Lyckades lägga till dokumentet', 'success');
      vm.uploaderOptionalDocuments.cancelUpload();
    }
  });

  $scope.$on('$routeUpdate', function() {
    if ($location.search().step === 'properties') {
      vm.demandsCreated = false;
      // fetch properties in edit mode and set the flag vm.directPropertiesMode = true,
      // which plays similar role for the acknowledge modal as uploaded list's blobId.
      // call only once
      if (vm.editMode && !vm.fetchedPropertyList && vm.realEstateChoiceMethod === 'DIRECT_PROPERTIES') {
        vm.fetchedPropertyList = true;
        getProperties();
      }
    }

    // TODO: add another conditions for 'PROPERTY_LIST' and 'OPTIONAL_DOCUMENTS'
    if ($location.search().step === 'property-motivation' && !vm.fetchedPropertyList) {
      vm.allPropertiesBoxChecked = true;
      vm.sortPropertyColumn = 'propertyDesignation';
      vm.sortPropertyTitle = 'Fastighetsbeteckning';
      vm.sortPropertyReverse = false;
      vm.properties.forEach(property => {
        property.filtered = true;
        vm.selectedPropertyIds[property.id] = true;
      });
      if (vm.editMode && vm.rentListAlreadyImported) {
        vm.fetchedPropertyList = true;
        getProperties();
      } else if (vm.realEstateChoiceMethod === 'RENT_LIST' || vm.realEstateChoiceMethod === 'PROPERTY_LIST') {
        // fetch properties from rent-list (only once)
        getPropertyList();
      } else if (vm.realEstateChoiceMethod === 'DIRECT_APARTMENTS' && !vm.demandsCreated) {
        // fetch empty properties from apartments selector
        getPropertiesFromSelector();
      }
    }

    if ($location.search().step === 'apartment-motivation' && !vm.fetchedApartmentList) {
      vm.allPropertiesBoxChecked = true;
      vm.sortPropertyColumn = 'propertyDesignation';
      vm.sortPropertyTitle = 'Fastighetsbeteckning';
      vm.sortPropertyReverse = false;
      if (vm.editMode && vm.rentListAlreadyImported) {
        vm.fetchedApartmentList = true;
        getApartments();
      } else if (vm.realEstateChoiceMethod === 'RENT_LIST') {
        // fetch apartments from rent-list (only once)
        getApartmentList();
      } else if (vm.realEstateChoiceMethod === 'DIRECT_APARTMENTS' && !vm.demandsCreated) {
        getRealEstateFromSelector();
      }
    }

    if ($location.search().step === 'properties-preview-and-submit') {
      getPossibleErrors('properties');
    }

    if ($location.search().step === 'apartments-preview-and-submit') {
      getPossibleErrors('apartments');
      //TODO: apartment summary from backend here
    }
  });

  function deleteDocument(index) {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'xsm',
      templateUrl: 'components/modal/confirmation/confirmationModal.html',
      controller: 'ConfirmationModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--secondary'
    });

    modalInstance.result.then(function() {
      vm.uploadedDocumentsList.splice(index, 1);
      Flash.set('Lyckades ta bort dokumentet', 'success');
    });
  }

  // used in edit mode to select previous choice if more than 1 available
  function initializeRealEstateChoiceMethod() {
    if (vm.negotiationCall.rentListBlobId) {
      vm.realEstateChoiceMethod = 'RENT_LIST';
    } else if (vm.negotiationCall.propertyListBlobId) {
      vm.realEstateChoiceMethod = 'PROPERTY_LIST';
    } else if (vm.negotiationCall.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY) {
      vm.realEstateChoiceMethod = 'OPTIONAL_DOCUMENTS';
    } else {
      vm.realEstateChoiceMethod = 'DIRECT_PROPERTIES';
      vm.directPropertiesMode = true;
    }
  }

  // in edit mode
  function getRentListPreview() {
    vm.ready = false;

    return NegotiationCall.queryRentListValidation({
      blobId: vm.negotiationCall.rentListBlobId
    })
      .$promise.then(function(response) {
        vm.validationResponse = response;
        vm.rentListAlreadyImported = true;
      })
      .catch(new ErrorHandler('Misslyckades att kontrollera status för hyreslistan.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  // in edit mode
  function getPropertyListPreview() {
    vm.ready = false;

    return NegotiationCall.queryPropertyListValidation({
      blobId: vm.negotiationCall.propertyListBlobId
    })
      .$promise.then(function(response) {
        vm.validationResponse = response;
        vm.rentListAlreadyImported = true;
      })
      .catch(new ErrorHandler('Misslyckades att kontrollera status för fastighetslistan.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  // in edit mode
  function getNegotiationCall() {
    vm.ready = false;
    return NegotiationCall.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.negotiationCall = response;
        // XXX hack alert!
        // The rent file blob name is a path - 'rent-file/<GUID, 36 characters>'.
        // The slash is sent URL-encoded (i.e. as %2F), but on Azure IIS attempts to be 'helpful' by
        // url-decoding it before the request hits NodeJS, so the API gets confused about routing.
        // We work around this by replacing the slash by ___ and letting backend translate back again.
        // Reinventing wheels!
        vm.negotiationCall.rentListBlobId = response.rentFile
          ? response.rentFile.blobContainer + ':' + response.rentFile.blobName.replace('/', '___')
          : null;
        vm.negotiationCall.propertyListBlobId = response.propertyFile
          ? response.propertyFile.blobContainer + ':' + response.propertyFile.blobName.replace('/', '___')
          : null;
        vm.selectedOrganizationName = response.counterpart.name;

        vm.isPropertyBased = propertyTypes.includes(vm.negotiationCall.type);
        vm.isApartmentBased = apartmentTypes.includes(vm.negotiationCall.type);

        vm.rentEffectiveFrom = new Date(vm.negotiationCall.rentEffectiveFrom);
        initializeRealEstateChoiceMethod();
        if (vm.negotiationCall.rentListBlobId) {
          return getRentListPreview();
        } else if (vm.negotiationCall.propertyListBlobId) {
          return getPropertyListPreview();
        } else if (vm.negotiationCall.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY) {
          return getExistingDocuments();
        }
        return null;
      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandlingsframställan.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function createDemand(property) {
    let isNumeric = n => !isNaN(parseFloat(n)) && isFinite(n);

    let unitType = '2';
    property.demand = {};

    if (property.demandValue) {
      let valueWOpercent = property.demandValue.replace('%', '');
      let isNumber = isNumeric(valueWOpercent);

      if(isNumber) {
        unitType = '0'; //default unit type is procent
        property.demand.numericValue = parseFloat(valueWOpercent);
      } else {
        unitType = '2';
        property.demand.stringValue = property.demandValue;
      }

      if(property.demandValue.includes('%')) {
        unitType = '0';
      }

      property.demand.unit = unitType;
      property.demand.motivation = property.demandDescription;
    }
  }

  // fetch properties in edit mode when vm.realEstateChoiceMethod === 'DIRECT_PROPERTIES',
  // or when keeping the same rent list as previously
  function getProperties() {
    vm.readyProperties = false;
    return NegotiationCall.queryProperty(
      {
        id: $routeParams.id,
        include: 'demands'
      },
      {}
    )
      .$promise.then(function(response) {
        vm.properties = response.rows;
        vm.properties.forEach(function(property) {
          property.filtered = true;
          vm.selectedPropertyIds[property.id] = true;
        });
        updateAllPropertiesBoxState();
      })
      .catch(function() {
        new ErrorHandler('Misslyckades att hämta fastigheter.');
      })
      .finally(function() {
        vm.readyProperties = true;
      });
  }

  function getApartments() {
    vm.readyApartments = false;
    return NegotiationCall.queryApartment(
      {
        id: $routeParams.id,
        include: 'demands'
      },
      {}
    )
      .$promise.then(function(response) {
        vm.properties = response.rows;
        vm.properties.forEach(function(property) {
          property.filtered = true;
          vm.selectedPropertyIds[property.id] = true;
        });
        updateAllPropertiesBoxState();
      })
      .catch(new ErrorHandler('Misslyckades att hämta lägenheter.'))
      .finally(function() {
        vm.readyApartments = true;
      });
  }

  // fetch properties when (vm.realEstateChoiceMethod === 'RENT_LIST' || vm.realEstateChoiceMethod === 'PROPERTY_LIST')
  function getPropertyList() {
    var query;
    if (vm.realEstateChoiceMethod === 'RENT_LIST') {
      query = NegotiationCall.queryPropertyByRentList({
        blobId: vm.negotiationCall.rentListBlobId
      });
    } else if (vm.realEstateChoiceMethod === 'PROPERTY_LIST') {
      query = NegotiationCall.queryPropertyByPropertyList({
        blobId: vm.negotiationCall.propertyListBlobId
      });
    }
    vm.readyProperties = false;
    query.$promise.then(response => {
      vm.fetchedPropertyList = true;
      vm.properties = response.rows;
      vm.properties.forEach(property => {
        property.filtered = true;
        vm.selectedPropertyIds[property.id] = true;
        createDemand(property);
      });
      updateAllPropertiesBoxState();
    })
      .catch(() => { new ErrorHandler('Misslyckades att hämta fastighetslistan.') })
      .finally(function() { vm.readyProperties = true });
  }

  function getApartmentList() {
    vm.readyApartments = false;
    NegotiationCall.queryApartmentByRentList({
      blobId: vm.negotiationCall.rentListBlobId,
      include: 'demands'
    }).$promise.then(response => {
      vm.fetchedApartmentList = true;
      vm.properties = response.rows;
      vm.apartmentsTotalCount = response.count;
      setupAllPropertiesBoxState();
      updateAllPropertiesBoxState();
    })
      .catch(new ErrorHandler('Misslyckades att hämta lägenheter.'))
      .finally(() => { vm.readyApartments = true });
  }

  function getRealEstateFromSelector() {
    let apartments = vm.apartmentSelector.apartments;
    vm.properties = apartments;

    vm.apartmentsTotalCount = apartments.length;
    setupAllPropertiesBoxState();
    updateAllPropertiesBoxState();

    vm.propertySummary = {
      // eslint-disable-next-line no-undef
      numProperties: new Set(apartments.map(x => x.property)).size,
      // eslint-disable-next-line no-undef
      numMunicipalities: new Set(apartments.map(x => x.municipality)).size,
      // eslint-disable-next-line no-undef
      numOwners: new Set(apartments.map(x => x.property.owners.map(y => y.name)).flat()).size,
    };

    vm.readyApartments = true;
  }

  function getPropertiesFromSelector() {
    let properties = vm.emptyProperties;
    vm.properties = properties;

    vm.apartmentsTotalCount = 0;
    setupAllPropertiesBoxState();
    updateAllPropertiesBoxState();

    vm.propertySummary = {
      // eslint-disable-next-line no-undef
      numProperties: properties.length,
      // eslint-disable-next-line no-undef
      numMunicipalities: new Set(properties.map(x => x.municipality)).size,
      // eslint-disable-next-line no-undef
      numOwners: new Set(properties.map(x => x.owners).flat()).size,
    };

    vm.readyProperties = true;
  }

  function getPropertyOwners(propertyId) {
    let property = vm.properties.find(property => property.id === propertyId);

    if (!property.owners) {
      vm.readyOwners[property.id] = false;
      RealEstate.get({ id: propertyId }, {})
        .$promise.then(response => { property.owners = response.owners })
        .catch(new ErrorHandler('Misslyckades att hämta fastighetsägare.'))
        .finally(() => { vm.readyOwners[propertyId] = true });
    }
  }

  function getExistingDocuments() {
    vm.readyDocuments = false;
    NegotiationCall.listDocuments({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.uploadedDocumentsList = response.map(file => ({
          blobId: file.name,
          fileName: file.filename
        }));
      })
      .catch(new ErrorHandler('Misslyckades att hämta dokument.'))
      .finally(function() { vm.readyDocuments = true });
  }

  function getPossibleErrors(type) {
    vm.searching = true;

    let propertyIds = vm.properties.map(property => property.id);
    let ignoreCurrentCall = vm.editMode
      ? { callId: vm.negotiationCall.id }
      : null;

    if (type === 'properties') {
      return NegotiationCall.queryErrorsInProperties(ignoreCurrentCall, propertyIds)
        .$promise.then(function(response) {
          vm.possibleErrorsList = response.rows;
          if (vm.realEstateChoiceMethod !== 'DIRECT_APARTMENTS') {
            vm.propertySummary = response.propertySummary;
          }
        })
        .catch(new ErrorHandler('Misslyckades att hämta fel på fastigheter.'))
        .finally(function() {
          vm.searching = false;
        });
    } else if (type === 'apartments') {
      return NegotiationCall.queryErrorsInApartments(ignoreCurrentCall, propertyIds)
        .$promise.then(function(response) {
          vm.possibleErrorsList = response.rows;
          vm.apartmentSummary = response.apartmentSummary;
        })
        .catch(new ErrorHandler('Misslyckades att hämta fel på lägenheter.'))
        .finally(function() {
          vm.searching = false;
        });
    }
  }

  // info modal in basic page
  function showNegotiationTypeInfo() {
    $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'lg',
      index: 1075,
      templateUrl: 'components/modal/information/negotiationCallInfoModal.html',
      controller: 'InformationModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--green'
    });
  }

  function getRealEstateChoiceMethodsByNegotiationType(type) {
    switch (type) {
      case NegotiationTypes.ANNUAL_PROPERTY:
      case NegotiationTypes.STANDARD_PROPERTY:
      case NegotiationTypes.REBUILD_PROPERTY:
      case NegotiationTypes.REBUILD_PROPERTY_2:
      case NegotiationTypes.UTILITY_VALUE_PROPERTY:
        return {
          RENT_LIST: false,
          PROPERTY_LIST: true,
          DIRECT_PROPERTIES: true,
          DIRECT_APARTMENTS: true
        };
      case NegotiationTypes.STANDARD_APARTMENT:
      case NegotiationTypes.REBUILD_APARTMENT:
      case NegotiationTypes.UTILITY_VALUE_APARTMENT:
        return {
          RENT_LIST: true
        };
      case NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY:
        return {
          OPTIONAL_DOCUMENTS: true
        };
      case NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT:
        return {
          DIRECT_PROPERTIES: true
        };
      default:
        return {};
    }
  }

  function isRealEstateChoiceMethodEnabled(realEstateChoiceMethod, negotiationType) {
    var activeRealEstateChoiceMethods = getRealEstateChoiceMethodsByNegotiationType(negotiationType);
    return activeRealEstateChoiceMethods[realEstateChoiceMethod];
  }

  function setRadioModelByOutsideClick(value) {
    // save the previous choice method
    var previousRealEstateChoiceMethod = vm.realEstateChoiceMethod;

    // set a new choice method, which can be canceled if not confirmed
    if (vm.isRealEstateChoiceMethodEnabled(value, vm.negotiationCall.type)) {
      vm.realEstateChoiceMethod = value;
    }

    if (
      (vm.negotiationCall.rentListBlobId && vm.realEstateChoiceMethod !== 'RENT_LIST') ||
      (vm.negotiationCall.propertyListBlobId && vm.realEstateChoiceMethod !== 'PROPERTY_LIST') ||
      (vm.directPropertiesMode && vm.realEstateChoiceMethod !== 'DIRECT_PROPERTIES')
    ) {
      // show acknowlegdement modal when we want to change the properties' source,
      // which leads to reseting the properties array filled in from the previous choice path
      var modalInstance = $uibModal.open({
        animation: true,
        ariaLabelledBy: 'modal-title-top',
        ariaDescribedBy: 'modal-body-top',
        backdrop: 'static',
        size: 'md',
        index: 1075,
        templateUrl: 'resources/negotiation-call/new/modals/change-real-estate-source-modal.html',
        controller: 'BooleanConfirmationModalCtrl',
        controllerAs: 'vm',
        windowClass: 'modal-content--green'
      });
      modalInstance.result.then(function(acknowledgement) {
        // confirm the new choice
        if (acknowledgement) {
          vm.properties = [];
          vm.negotiationCall.rentListBlobId = null;
          vm.negotiationCall.propertyListBlobId = null;
          vm.fetchedPropertyList = false;
          vm.fetchedApartmentList = false;
          vm.negotiationCall.documentListBlobId = null;
          vm.directPropertiesMode = false;
          vm.rentListAlreadyImported = false;
          vm.selectedPropertyIds = {};
          vm.uploader.cancelUpload();
          vm.propertyFileUploader.cancelUpload();
        } else {
          // refuse the new choice
          vm.realEstateChoiceMethod = previousRealEstateChoiceMethod;
        }
      });
    }
  }

  function deleteUploadedFile() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'md',
      index: 1075,
      templateUrl: 'resources/negotiation-call/new/modals/delete-rent-list-modal.html',
      controller: 'ConfirmationModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--green'
    });
    modalInstance.result.then(function() {
      vm.uploader.cancelUpload();
      vm.propertyFileUploader.cancelUpload();
      vm.properties = [];
      vm.selectedPropertyIds = {};
      vm.negotiationCall.rentListBlobId = null;
      vm.negotiationCall.propertyListBlobId = null;
      vm.rentListAlreadyImported = false;
      vm.fetchedPropertyList = false;
      vm.fetchedApartmentList = false;
    });
  }

  // when vm.realEstateChoiceMethod === 'DIRECT_PROPERTIES'
  function searchProperties() {
    if (vm.inputTextComplex) {
      vm.toggleMenu = false;
      vm.searching = true;
      return RealEstate.querySimple({ 'search[complex]': vm.inputTextComplex }, {})
        .$promise.then(function(response) {
          vm.propertiesTypeahead = response.rows;
          if (vm.propertiesTypeahead.length > 0) {
            vm.toggleMenu = true;
          }
        })
        .catch(new ErrorHandler('Fel'))
        .finally(function() {
          vm.searching = false;
        });
    }
  }

  function addProperty(property) {
    vm.readyProperties = true;
    var foundProperty = vm.properties.filter(function(item) {
      return item.id === property.id;
    });

    // look through all properties to check if they had been added before or not
    if (foundProperty.length === 0) {
      vm.properties.push(property);
      vm.directPropertiesMode = true;
      vm.selectedPropertyIds[property.id] = false;
      updateAllPropertiesBoxState();
    }
  }

  function removeProperty(property) {
    var index = vm.properties.indexOf(property);
    // reset arrow state to closed (down) when remove-add the same
    vm.toggleOwnersList[property.id] = false;
    vm.properties.splice(index, 1);
    delete vm.selectedPropertyIds[property.id];
    updateAllPropertiesBoxState();
  }

  function editDemandAndMotivation() {
    let propertiesArr = vm.properties
      .filter(property => property.filtered && vm.selectedPropertyIds[property.id]);

    HyUtils.showDefaultModal('EditMotivationModalCtrl',
      'resources/negotiation-call/new/modals/edit-motivation-modal.html', {
        propertiesArr,
        negotiationType: vm.negotiationCall.type,
      }, demand => {
        vm.properties
          .filter(property => property.filtered && vm.selectedPropertyIds[property.id])
          .forEach(property => {
            angular.extend(property, { demand });
            vm.selectedPropertyIds[property.id] = false;
          });
        updateAllPropertiesBoxState();
        vm.demandsCreated = vm.properties.every(p => p.demand && p.demand.unit);
      }, null, null, { size: 'md' });
  }

  function toggleAllPropertiesSelection() {
    vm.properties.forEach(function(property) {
      vm.selectedPropertyIds[property.id] = vm.allPropertiesBoxChecked;
    });
  }

  function setupAllPropertiesBoxState() {
    vm.properties.forEach(property => {
      property.filtered = true;
      vm.selectedPropertyIds[property.id] = true;
      createDemand(property);
    });
  }

  function updateAllPropertiesBoxState() {
    // auto select master checkbox if all other checkboxes are selected.
    // deselect master checkbox if at least one checkbox is not selected
    vm.allPropertiesBoxChecked = Object.keys(vm.selectedPropertyIds).every(function(propertyId) {
      return vm.selectedPropertyIds[propertyId];
    });
  }

  function save() {
    HyUtils.showDefaultModal('ReceivingOfficeModalCtrl',
      'resources/negotiation-call/new/modals/receiving-office-modal.html',
      { negotiationCallObj: vm.negotiationCall },
      receivingOfficeResult => {
        vm.negotiationCall.hgfOfficeId = receivingOfficeResult.selectedOffice.id;
        vm.negotiationCall.landlordSide = receivingOfficeResult.landlordSide;
        persistNegotiationCall();
      }, null, null, { size: 'sm' });
  }

  function persistNegotiationCall() {
    vm.saving = true;
    vm.negotiationCall.rentEffectiveFrom = DateLocal.fromUTCtoLocal(vm.rentEffectiveFrom);
    vm.negotiationCall.negotiationDate = DateLocal.fromUTCtoLocal(vm.negotiationDate);
    vm.negotiationCall.incomingDate = DateLocal.fromUTCtoLocal(vm.negotiationCall.incomingDate);
    vm.negotiationCall.attachments = vm.attachments;
    if (vm.readyProperties) {
      angular.extend(vm.negotiationCall, { properties: vm.properties });
    }

    if (vm.readyApartments) {
      angular.extend(vm.negotiationCall, { apartments: vm.properties });
    }

    if (vm.negotiationCall.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY) {
      angular.extend(vm.negotiationCall, { documents: vm.uploadedDocumentsList });
    }

    if (vm.isHgf) {
      vm.negotiationCall.participants = vm.negotiation.participants;
      vm.negotiationCall.counterpartOrganizationId = vm.selectedOrganization.id;
    }
    NegotiationCall.save(vm.negotiationCall)
      .$promise.then(function(response) {
        $location.search({});
        $location.path('/negotiation-calls/' + response.id);
        Flash.set('Förhandlingsframställan har skickats till Hyresgästföreningen', 'success');
      })
      .catch(new ErrorHandler('Misslyckades att skapa förhandlingsframställan.'))
      .finally(function() {
        vm.saving = false;
      });
  }

  //Custom pagination controlls for this view.
  vm.goToPrevPage = () => {
    vm.currentPage = vm.currentPage - 1;
    vm.firstRecordInCurrentPage = vm.currentPage * vm.pageLimit + 1;
  };
  vm.goToNextPage = () => {
    vm.currentPage = vm.currentPage + 1;
    vm.firstRecordInCurrentPage = vm.currentPage * vm.pageLimit + 1;
  };
  vm.computeLastRecord = () => {
    if (vm.apartmentsTotalCount - vm.firstRecordInCurrentPage < vm.pageLimit - 1) {
      return vm.apartmentsTotalCount;
    } else {
      return vm.firstRecordInCurrentPage + vm.pageLimit - 1;
    }
  };
  vm.gotoNextStep = () => { vm.gotoNext = true };

  let changeChiefOrganizations = () => {
    let chief = vm.negotiation.participants.find(p => p.isChief);
    vm.chiefParticipantOrganizations = chief ? chief.organizations : null;
    if (vm.chiefParticipantOrganizations && vm.chiefParticipantOrganizations.length === 1) {
      vm.selectedOrganization = vm.chiefParticipantOrganizations[0];
    }else{
      vm.selectedOrganization = null;
    }
  };

  vm.tagChief = (taggedParticipant) => {
    vm.negotiation.participants.forEach(p => p.isChief = (p.id === taggedParticipant.id));
    vm.chiefId = taggedParticipant.id;
    changeChiefOrganizations();
  };

  vm.addParticipant = () => {
    let foundParticipant = vm.negotiation.participants.filter(user => user.id === vm.selectedParticipant.id);

    if (foundParticipant.length === 0) {
      if (vm.negotiation.participants.length === 0) {
        vm.selectedParticipant.isChief = true;
        vm.chiefId = vm.selectedParticipant.id;
      } else {
        vm.selectedParticipant.isChief = false;
      }
      vm.negotiation.participants.push(vm.selectedParticipant);
    }
    vm.selectedParticipant = null;
    changeChiefOrganizations();
  }

  vm.removeParticipant = participant => {
    let isChiefSelected = vm.negotiation.participants.some(participant => participant.isChief === true);
    let index = vm.negotiation.participants.indexOf(participant);
    vm.negotiation.participants.splice(index, 1);

    if (!isChiefSelected) {
      if (vm.negotiation.participants.length > 0) {
        vm.negotiation.participants[0].isChief = true;
        vm.chiefId = vm.negotiation.participants[0].id;
      } else {
        vm.chiefId = null;
      }
    }
    changeChiefOrganizations();
  };

  vm.doUpload = () => {
    let settings = {
      title: 'Ladda upp underlag',
      uploadUrl: '/api/negotiation-calls/new-call-attachments',
      validFileTypes: ValidFileTypes,
      types: [
        AttachmentTypes.NEGOTIATION_ATTACHMENT,
        { id: 9, name: 'Övrigt' },
      ],
      typeTitle: 'Välj typ av underlag',
    };

    HyUtils.showDefaultModal('FileUploadExModalCtrl',
      'components/file-upload-ex-modal/file-upload-ex-modal.html',
      { settings },
      files => {
        vm.attachments.push(...files);
        Flash.set('Lyckades lägga till underlag', 'success');
      }, null, null, { size: 'sm' }
    );
  };

  vm.deleteAttachment = file => {
    vm.attachments = vm.attachments.filter(x => x !== file);
    $scope.$apply();
  };


  vm.showAddNegotiatorModal = () => {
    HyUtils.showDefaultModal('NewUserModalCtrl',
      'resources/user/new/new-user-modal.html',
      { fullMode: false },
      result => {
        let newUser = result.user;
        if (vm.negotiation.participants.length === 0) {
          newUser.isChief = true;
          vm.chiefId = newUser.id;
        } else {
          newUser.isChief = false;
        }
        vm.negotiation.participants.push(newUser);
        changeChiefOrganizations();
      });
  };

  function filterDemand(el, phrase) {
    if (phrase && (!el || !el.demand)) {
      return false;
    }
    if (el.demand.unit === '2') {
      return (el.demand.stringValue).toLowerCase().includes(phrase);
    } else {
      return ('' + el.demand.numericValue).toLowerCase().includes(phrase);
    }
  }

  vm.propertiesFilter = el => {
    if (vm.filterChanging) {
      return el.filtered;
    }

    el.filtered = true;
    if (!vm.filterOwnerEnabled && !vm.filterPropertyEnabled && !vm.filterMunicipalityEnabled && !vm.filterDistrictEnabled
      && !vm.filterStreetsEnabled && !vm.filterDemandEnabled) {
      return true;
    }

    if (vm.filterOwnerEnabled) {
      if (vm.inputTextOwners && vm.inputTextOwners.length > 0) {
        let phrase = vm.inputTextOwners.toLowerCase();
        const containsPhrase = (owners) => {
          return owners
            .filter(owner => owner && owner.trim() !== "")
            .some(owner => owner.toLowerCase().includes(phrase));
        };

        if (el.owners && el.owners.length > 0) {
          el.filtered = containsPhrase(el.owners);
        } else if (el.property && el.property.owners && el.property.owners.length > 0) {
          el.filtered = containsPhrase(el.property.owners);
        } else {
          el.filtered = false;
        }
      }
    }

    if (!el.filtered) {
      return false;
    }

    if (vm.filterPropertyEnabled) {
      if (vm.inputTextProperty && vm.inputTextProperty.length > 0) {
        let phrase = vm.inputTextProperty.toLowerCase();
        if (el.propertyDesignation) {
          el.filtered = el.filtered && (el.propertyDesignation).toLowerCase().includes(phrase);
        } else {
          el.filtered = el.filtered && (el.property.propertyDesignation).toLowerCase().includes(phrase);
        }
      }
    }

    if (!el.filtered) {
      return false;
    }

    if (vm.filterDemandEnabled) {
      if (vm.inputTextDemand && vm.inputTextDemand.length > 0) {
        let phrase = vm.inputTextDemand.toLowerCase();
        el.filtered = el.filtered && filterDemand(el, phrase); //
      }
    }

    if (!el.filtered) {
      return false;
    }

    if (vm.filterMunicipalityEnabled) {
      if (vm.inputTextMunicipality && vm.inputTextMunicipality.length > 0) {
        let phrase = vm.inputTextMunicipality.toLowerCase();
        el.filtered = el.filtered && (el.municipality).toLowerCase().includes(phrase);
      }
    }

    if (!el.filtered) {
      return false;
    }

    if (vm.filterDistrictEnabled) {
      if (vm.inputTextDistrict && vm.inputTextDistrict.length > 0) {
        let phrase = vm.inputTextDistrict.toLowerCase();
        el.filtered = el.filtered && (el.district).toLowerCase().includes(phrase);
      }
    }

    if (!el.filtered) {
      return false;
    }

    if (vm.filterStreetsEnabled) {
      if (vm.inputTextStreets && vm.inputTextStreets.length > 0) {
        let phrase = vm.inputTextStreets.toLowerCase();
        el.filtered = el.filtered && (el.streetAddress).toLowerCase().includes(phrase);
      }
    }

    return el.filtered;
  };

  function filterProperties(field, value) {
    if (field === 0) {
      vm.filterOwnerEnabled = value;
    }

    if (field === 1) {
      vm.filterPropertyEnabled = value;
    }

    if (field === 2) {
      vm.filterMunicipalityEnabled = value;
    }

    if (field === 3) {
      vm.filterDistrictEnabled = value;
    }

    if (field === 4) {
      vm.filterStreetsEnabled = value;
    }

    if (field === 5) {
      vm.filterDemandEnabled = value;
    }

    vm.filterChanging = false;
  }

  function sortProperties(sortColumn, columnTitle) {
    vm.sortPropertyTitle = columnTitle;
    let sortPreviousColumn = vm.sortPropertyColumn;
    vm.sortPropertyColumn = sortColumn;
    if (vm.sortPropertyColumn === sortPreviousColumn) {
      vm.sortPropertyReverse = !vm.sortPropertyReverse;
    } else {
      vm.sortPropertyReverse = false;
    }
  }

  /*
  vm.openRedWarning = () => {
    HyUtils.showDefaultModal('RedLetterWarningModalCtrl',
      'resources/negotiation-call/new/modals/red-letter-warning.html', {});
  };
  */

  vm.toggleAdditionalInfoPane = () => {
    vm.showAdditionalInfoPane = !vm.showAdditionalInfoPane;
  };

  vm.onPending = (validation) => {
    if (vm.status.showProgress && vm.status.progress > 0 && vm.status.phase !== validation.phase) {
      vm.status.progress = 100;
      $timeout(() => {
        vm.status.progress = 0;
        vm.status.phase = validation.phase;
        vm.status.comment = validation.comment;
        $timeout(() => vm.status = validation, 500);
      }, 300);

    } else {
      vm.status = validation;
    }
  };

}

})();
(function () {
'use strict';

ShowOrganizationCtrl.$inject = ["$location", "$routeParams", "Flash", "Organization", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowOrganizationCtrl', ShowOrganizationCtrl);

function ShowOrganizationCtrl($location, $routeParams, Flash, Organization, ErrorHandler) {
  var vm = this;
  vm.organization = {};

  getOrganization();

  ////////////

  function getOrganization() {
    return Organization.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.organization = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta organisation.'))
      .finally(function() {
        vm.ready = true;
      });
  }
}

})();
(function () {
'use strict';

OwnersListCtrl.$inject = ["$scope", "$document", "$location", "$filter", "$timeout", "RealEstate"];
angular.module('hyresgastApp').controller('OwnersListCtrl', OwnersListCtrl);

function OwnersListCtrl($scope, $document, $location, $filter, $timeout,
  RealEstate) {
  var vm = this;

  vm.tableColumns = [
    {
      title: 'Namn',
      column: 'name',
    },
    {
      title: 'Organisationsnummer',
      column: 'organizationNumber',
    }
  ];
  vm.tableFGetData = RealEstate.queryOwners;
  let getTableau = () => angular.element($document[0].querySelector("#tableau")).scope();

  vm.gotoOwner = owner => {
    $location.path('/search/properties').search({ owners: owner.name });
  };

  vm.getContextInfo = row => ({
    title: `Ägare ${row.name}`,
    link: '/search/properties?owners='+encodeURI(row.name),
  });

  vm.refreshList = () => {
    let filters = $location.search();
    $timeout(() => { getTableau().$broadcast('refresh', filters) }, 200);
  }
}
})();
(function () {
'use strict';

PropertyListCtrl.$inject = ["$document", "$filter", "$location", "$timeout", "RealEstate", "Regions", "Auth"];
angular.module('hyresgastApp').controller('PropertyListCtrl', PropertyListCtrl);

function PropertyListCtrl(
  $document, $filter, $location, $timeout,
  RealEstate, Regions,
  Auth) {
  var vm = this;

  vm.isHgfUser = Auth.isHgfUser();
  vm.onlyInMyNegotiations = !vm.isHgfUser;
  vm.header = 'Fastigheter' + (vm.isHgfUser ? '' : ' i mina förhandlingar');
  vm.sortColumn = $location.search().order || 'propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  vm.regionOptions = Object.values(Regions).map(id => ({
    id,
    name: $filter('regionTypeToDescription')(id, Regions)
  }));

  vm.tableColumns = [
    {
      title: 'Fastigheter',
      column: 'propertyDesignation',
    },
    {
      title: 'Region',
      column: 'regionId',
      lookup: {
        idProperty: 'id',
        textProperty: 'name',
        dictionary: vm.regionOptions,
      },
    },
    {
      title: 'Kommun',
      column: 'municipality',
    },
    {
      title: 'LM distrikt',
      column: 'district',
    },
    {
      title: 'Gatuaddress',
      column: 'addresses',
      type: 'array',
    },
    {
      title: 'Ägare',
      column: 'owners',
      type: 'array',
    },
    {
      title: 'Motpart',
      column: 'counterparts',
      type: 'array',
    },
    {
      title: 'Antal lägenheter jag förhandlar',
      column: 'numNegotiatedApartments',
      filterType: 'none',
    },
    {
      title: 'Info',
      column: 'status',
      filterType: 'none',
    },
  ];
  vm.tableAttributes = [
    {
      fn: row => !row.active,
      legend: 'Inaktiv',
      class: 'table__tr--inactive',
    },
  ];

  let getTableau = () => angular.element($document[0].querySelector("#tableau"));
  let setTableFGetData = () => {
    vm.tableFGetData = vm.onlyInMyNegotiations
      ? RealEstate.queryPropertiesInMyNegotiations
      : RealEstate.query;
    getTableau().scope().$broadcast('refresh');
  }

  vm.checkboxTicked = () => $timeout(setTableFGetData, 300);
  setTableFGetData();

  vm.tableFPostprocessData = rows => {
    // eslint-disable-next-line no-undef
    return new Promise((resolve, reject) => {
      let propertyIds = rows.map(p => p.id);
      RealEstate.queryPropertiesStatusCount({ propertyIds })
        .$promise.then(response => {
          rows.forEach(property => {
            property.status = '';
            // eslint-disable-next-line eqeqeq
            if (response.statusCount.find(a => a.propertyId == property.id && a.p)) {
              property.status += 'P';
            }
            // eslint-disable-next-line eqeqeq
            if (response.statusCount.find(a => a.propertyId == property.id && a.i)) {
              property.status += 'I';
            }
          });
          resolve(rows);
        }).catch(() => reject());
    });
  };

  if ($location.search().owners) {
    vm.filters = { owners: $location.search().owners };
  }

  vm.gotoProperty = property => {
    $location.search({});
    $location.path('/properties/' + property.id);
  };

  vm.getContextInfo = row => ({
    title: `Fastighet ${row.propertyDesignation}`,
    link: '/properties/' + row.id,
  });

  vm.refreshList = () => {
    let filters = $location.search();
    $timeout(() => { getTableau().scope().$broadcast('refresh', filters) }, 200);
  }
}

})();
(function () {
'use strict';

ListOrganizationCtrl.$inject = ["$scope", "$location", "Organization", "Flash", "Auth", "Office", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListOrganizationCtrl', ListOrganizationCtrl);

function ListOrganizationCtrl($scope, $location, Organization, Flash, Auth, Office, ErrorHandler) {
  var vm = this;
  vm.Auth = Auth;
  vm.user = {};
  vm.organizations = [];
  vm.getOrganizations = getOrganizations;
  vm.gotoShow = gotoShow;
  vm.pageLimit = 30;
  vm.pageOffset = 0;
  vm.sortColumn = $location.search().order || 'name'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  vm.headerButtons = [
    {
      title: 'Lägg till',
      url: '/organizations/new',
      icon: 'landlord',
      isVisible: () => (Auth && Auth.canDo('createOrganization'))
    }
  ];
  vm.listColumns = [
    { title: 'Företag', column: 'name' },
    { title: 'Organisationsnummer', column: 'organizationNumber' },
    { title: 'Godkänd av HGF', column: 'approved' },
    { title: 'HGF kontor', column: 'office.name' }
  ];

  vm.approvedChoices = {
    YES: true,
    NO: false
  };

  getOrganizations();
  getOffices();

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getOrganizations();
    }
  });

  ////////////

  function gotoShow(id) {
    $location.search({});
    $location.path('/organizations/' + id);
  }

  function getOrganizations(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.pageOffset = 0;
    }
    return Organization.query({
      limit: vm.pageLimit,
      offset: vm.pageOffset,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'search[name]': vm.inputTextOrgName,
      'search[organizationNumber]': vm.inputTextOrgNumber,
      'filters[approved]': vm.approved,
      'filters[office.id]': vm.officeId
    })
      .$promise.then(function(data) {
        vm.organizations = data.rows;
        vm.totalCount = data.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta organisationer.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function getOffices() {
    Office.query().$promise.then(function(response) {
      vm.offices = response;
    })
      .catch(ErrorHandler('Misslyckades att hämta kontor.'));
  }
}

})();
(function () {
'use strict';

PropertyShowCtrl.$inject = ["$scope", "$location", "$routeParams", "Flash", "HyUtils", "RealEstate", "SubPage", "Auth", "ErrorHandler"];
angular.module('hyresgastApp').controller('PropertyShowCtrl', PropertyShowCtrl);

function PropertyShowCtrl(
  $scope, $location, $routeParams,
  Flash, HyUtils, RealEstate, SubPage,
  Auth, ErrorHandler) {
  var vm = this;
  vm.isHgf = Auth.isHgfUser();

  vm.tablist = [
    {
      title: 'Aktuella förhandlingar',
      path: '/properties/' + $routeParams.id
    },
    {
      title: 'Avslutade förhandlingar',
      path: '/properties/' + $routeParams.id + '?view=finished-negotiations'
    },
    {
      title: 'Lägenheter i fastigheten',
      path: '/properties/' + $routeParams.id + '?view=apartments'
    }
  ];
  if (vm.isHgf) {
    vm.tablist.push({
      id: 'twits',
      title: 'Anteckningar',
      path: '/properties/' + $routeParams.id + '?view=blog'
    });
    vm.tablist.push({
      id: 'avtal',
      title: 'Avtal',
      path: '/properties/' + $routeParams.id + '?view=agreements'
    });
    vm.tablist.push({
      id: 'artifacts',
      title: 'Dokument',
      path: '/properties/' + $routeParams.id + '?view=artifacts'
    });
  }

  vm.gotoNegotiation = gotoNegotiation;

  $scope.$on('$routeUpdate', function() {
    // avoid unecessary calling of getCurrentSubpage() when sorting
    if (!$location.search().order) {
      // reset offset when sorting
      getCurrentSubpage();
    }
  });

  getCurrentSubpage();
  getProperty();

  ////////////

  function setConnections() {
    let twitsTab = vm.tablist.find(x => x.id === 'twits');
    if (twitsTab) {
      twitsTab.number = vm.property.connections.twitCount;
    }
    /*
    let docsTab = vm.tablist.find(x => x.id === 'avtal');
    if (docsTab) {
      docsTab.number = vm.property.connections.avtalCount;
    }
    */
    let artifactsTab = vm.tablist.find(x => x.id === 'artifacts');
    if (artifactsTab) {
      artifactsTab.number = vm.property.connections.artifactCount;
    }
  }

  function getProperty() {
    return RealEstate.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.property = response;
        let propertyIds = [...vm.property.newPropertyIds, parseInt($routeParams.id)];
        getStatusCount(propertyIds);
        setFormattedPropsForTooltip();
        setConnections();
      })
      .catch(ErrorHandler('Misslyckades att hämta fastigheten.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function setFormattedPropsForTooltip() {
    if(!vm.property) return;

    if(vm.property.owners) {
      vm.formattedOwners = vm.property.owners.map(function(owner) {
        return owner.name + ', orgnr: ' + owner.organizationNumber;
      });
    }

    if(vm.property.landlords) {
      vm.formattedLandlords = vm.property.landlords.map(function(landlord) {
        return landlord.name;
      });
    }
    if(vm.property.address || vm.property.addresses) {
      vm.formattedAddresses = ([vm.property.address]
        .concat(vm.property.addresses))
        .filter(x => x)
        .map(a => HyUtils.renderStreetAddress(a));
    }
  }

  function getStatusCount(propertyIds) {
    RealEstate.queryPropertiesStatusCount({ propertyIds })
      .$promise.then((response) => {
        let findStatus = k => {
          // eslint-disable-next-line eqeqeq
          let o = response.statusCount.find(a => a.propertyId == $routeParams.id && a[k]);
          return o ? o[k] : 0;
        };
        vm.statusCount = {
          p: findStatus('p'),
          i: findStatus('i'),
        };
      });
  }

  function getCurrentSubpage() {
    var defaultSubPage = 'active-negotiations';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = vm.isHgf
      ? ['finished-negotiations', 'apartments', 'blog', 'artifacts', 'agreements']
      : ['finished-negotiations', 'apartments'];
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }

  function gotoNegotiation(negotiationId) {
    $location.search({});
    $location.path('/negotiations/' + negotiationId);
  }

}

})();
(function () {
'use strict';

ShowNegotiationCtrl.$inject = ["$scope", "$location", "$routeParams", "$uibModal", "$route", "Negotiation", "Seenby", "NegotiationStates", "NegotiationCallStates", "ObjectTypes", "NegotiationTypes", "FeatureConfiguration", "Flash", "HyUtils", "SubPage", "Auth", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationCtrl', ShowNegotiationCtrl);

function ShowNegotiationCtrl(
  $scope, $location, $routeParams, $uibModal, $route,
  Negotiation, Seenby,
  NegotiationStates, NegotiationCallStates, ObjectTypes, NegotiationTypes, FeatureConfiguration,
  Flash, HyUtils, SubPage, Auth, ErrorHandler
) {
  var vm = this;

  vm.NegotiationStates = NegotiationStates;
  vm.NegotiationCallStates = NegotiationCallStates;
  vm.NegotiationTypes = NegotiationTypes;
  vm.FeatureConfiguration = FeatureConfiguration;


  vm.tablist = [
    { title: 'Översikt', path: '/negotiations/' + $routeParams.id },
    { title: 'Överenskommelse', path: '/negotiations/' + $routeParams.id + '?view=agreements' },
    { id: 'docs', title: 'Bilagor', party: 'both', path: '/negotiations/' + $routeParams.id + '?view=documents' },
    { title: 'Historik', path: '/negotiations/' + $routeParams.id + '?view=history' },
  ];

  if (vm.FeatureConfiguration.ENABLE_MEETINGS) {
    vm.tablist.splice(2, 0, { title: 'Möten', path: '/negotiations/' + $routeParams.id + '?view=meetings' });
  }

  Auth.heartbeat().then(() => {
    vm.isHgf = Auth.isHgfUser();
    if (vm.isHgf) {
      vm.tablist.push({
        id: 'twits',
        title: 'Anteckningar',
        path: '/negotiations/' + $routeParams.id + '?view=blog',
        party: 'hgf',
      });
      vm.tablist.push({
        id: 'artifacts',
        title: 'Dokument',
        path: '/negotiations/' + $routeParams.id + '?view=artifacts',
        party: 'hgf',
      });
    }
  });

  vm.tablistForNewProperties = [
    { title: 'Översikt', path: '/negotiations/' + $routeParams.id },
    { title: 'Överenskommelse', path: '/negotiations/' + $routeParams.id + '?view=agreements' },
    { id: 'docs', title: 'Bilagor', path: '/negotiations/' + $routeParams.id + '?view=documents' },
    { title: 'Historik', path: '/negotiations/' + $routeParams.id + '?view=history' },
  ];

  if (vm.FeatureConfiguration.ENABLE_MEETINGS) {
    vm.tablistForNewProperties.splice(2, 0, { title: 'Möten', path: ('/negotiations/' + $routeParams.id + '?view=meetings') });
  }

  vm.importRentlist = importRentlist;
  vm.forgeRentlist = forgeRentlist;
  vm.showWithoutRentList = showWithoutRentList;
  vm.gotoMotiv = () => $location.path('/negotiations/' + vm.negotiation.id).search('view', 'motiv');
  vm.addItems = () => $location.path('/negotiations/' + vm.negotiation.id).search('view', 'addproperties');

  vm.findNegotiationRole = HyUtils.findNegotiationRole;

  $scope.$on('$routeUpdate', function() {
    // avoid unecessary calling of getCurrentSubpage() when sorting
    if (!$location.search().order) {
      getCurrentSubpage();
      getNegotiation();
    }
  });

  vm.isRentListFileType = isRentListFileType;
  function isRentListFileType (fileType) {
    return (fileType === 35 || fileType === 36);
  }

  getCurrentSubpage();
  getNegotiation();

  vm.getNegotiation = getNegotiation;

  /////////

  function getRentlistStatus(summary) {
    let status = {};

    status.missing = summary.properties === 0 || summary.lacksRentList > 0;

    if (status.missing) {
      status.partiallyMissing = summary.lacksRentList < summary.properties;
      if (status.partiallyMissing) {
        status.missingCount = summary.lacksRentList;
      }
    }

    let countRealEstate = vm.negotiation.canSkipRentlist ? summary.properties : summary.apartments;
    status.agreementsStarted = countRealEstate > 0
      && (vm.negotiation.realEstateStatus.notAssignedCount < countRealEstate);

    status.emptyRentlist = summary.apartments === 0;

    return status;
  }

  function setStatuses() {
    vm.negotiation.rentlistStatus = getRentlistStatus(vm.negotiation.realEstateSummary);

    let overviewOk = vm.negotiation.canSkipRentlist || !vm.negotiation.rentlistStatus.missing;
    if (!vm.isNewlyConstructedApartment) {
      overviewOk &= vm.negotiation.realEstateSummary.lacksDemands === 0;
    } else {
      //ett hack, men jag är för lat för att kolla efter isNewApartments överallt
      vm.negotiation.realEstateSummary.lacksDemands = 0;
    }
    let agreementsOk = vm.negotiation.rentlistStatus.agreementsStarted;
    if (vm.negotiation.canSkipRentlist) {
      agreementsOk = vm.negotiation.realEstateSummary.properties > 0
        && (vm.negotiation.realEstateStatus.notAssignedCount < vm.negotiation.realEstateSummary.properties)
    }
    vm.tablist[0].statusIcon = overviewOk ? 'ok' : 'warning';
    vm.tablist[1].statusIcon = agreementsOk ? 'ok' : 'warning';

    vm.hasApartments = vm.negotiation.realEstateSummary.uploadedApartmentsCount > 0;
    vm.apartmentsOrProperties = vm.negotiation.canSkipRentlist ? vm.negotiation.realEstateSummary.properties :
      vm.negotiation.realEstateSummary.apartments;

    vm.unfinishedAgreementsCount = vm.negotiation.unfinishedAgreementsCount.count;
  }

  function getNegotiation() {
    vm.negotiationQueryPromise = Negotiation.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.negotiation = response;

        let twitsTab = vm.tablist.find(x => x.id === 'twits');
        if (twitsTab) {
          twitsTab.number = vm.negotiation.connections.twitCount;
        }
        let artifactsTab = vm.tablist.find(x => x.id === 'artifacts');
        if (artifactsTab) {
          artifactsTab.number = vm.negotiation.connections.artifactCount;
        }
        let docsTab = vm.tablist.find(x => x.id === 'docs');
        if (docsTab) {
          docsTab.number = vm.negotiation.connections.documentCount;
        }

        vm.negotiation.hgfParticipants.forEach(d => d.roleObj = vm.findNegotiationRole(d.negotiationsUsers.role));
        vm.negotiation.externalParticipants.forEach(d => d.roleObj = vm.findNegotiationRole(d.negotiationsUsers.role));
        vm.negotiation.delegationParticipants.forEach(
          d => d.roleObj = vm.findNegotiationRole(d.negotiationsUsers.role));
        vm.negotiation.delegationInvitations.forEach(d => d.roleObj = vm.findNegotiationRole(d.role));

        let partyFilter = (party) => (participant) => participant.roleObj.party === party;

        vm.negotiation.hgfSide = vm.negotiation.hgfParticipants
          .concat(vm.negotiation.delegationParticipants.filter(partyFilter(20)));
        vm.negotiation.faSide = vm.negotiation.externalParticipants
          .concat(vm.negotiation.delegationParticipants.filter(partyFilter(0)));

        vm.negotiation.chiefNegotiatorHGF = vm.negotiation.hgfSide.filter(participant => participant.isChief)[0];
        vm.negotiation.chiefNegotiatorExternal = vm.negotiation.faSide.filter(participant => participant.isChief)[0];

        vm.negotiation.isRentListMissing =
          vm.negotiation.realEstateSummary.lacksRentList < vm.negotiation.realEstateSummary.properties;

        vm.apartmentMode = (vm.negotiation.type === NegotiationTypes.STANDARD_APARTMENT ||
                            vm.negotiation.type === NegotiationTypes.REBUILD_APARTMENT ||
                            vm.negotiation.type === NegotiationTypes.UTILITY_VALUE_APARTMENT ||
                          (vm.negotiation.type === NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT &&
                           vm.negotiation.isRentListMissing));

        vm.isNewlyConstructedProperty = vm.negotiation.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY;
        vm.isNewlyConstructedApartment = vm.negotiation.type === NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT;
        vm.negotiation.isFinished = vm.negotiation.state === NegotiationStates.FINISHED ||
                                    vm.negotiation.state === NegotiationStates.FINISHED_OUTSIDE_PORTAL;

        setStatuses();

        return getWithoutRentList();
      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function() {
        vm.ready = true;
        Seenby.set({
          objectType: ObjectTypes.NEGOTIATION.code,
          objectId: $routeParams.id,
        });
      });
    return vm.negotiationQueryPromise;
  }

  vm.editComment = () => {
    HyUtils.showDefaultModal('EditCommentModalCtrl',
      'resources/agreement/rentlist/edit/edit-comment-modal.html',
      { comment: vm.negotiation.comment, multipleComments: '' },
      result => {
        if (result.delete || result.comment) {
          vm.negotiation.comment = result.delete ? '' : result.comment;
          Negotiation.saveComments([{
            negotiationId: vm.negotiation.id,
            comment: vm.negotiation.comment,
          }]);
          vm.isDirty = true;
        }
      }, null, null,
      { size: 'md' });
  };

  function getWithoutRentList() {
    return Negotiation.queryWithoutRentList({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.numWithoutRentlist = response.count;
        vm.withoutRentlist = response.rows;
      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandlingar.'));
  }

  vm.finish = () => {
    let caption = 'Varning';
    let message = 'Om du väljer att avsluta förhandlingen utan att göra klart den i Förhandlarportalen, kommer den inte gå att återöppna.';
    let confirmBtn = 'Avsluta förhandling';
    let cancelBtn = 'Avbryt';

    HyUtils.showDefaultModal('ConfirmationModalWithMessageAndButtonsCtrl',
      'components/modal/confirmation/confirmationModalWithMessageAndButtons.html',
      // eslint-disable-next-line no-unused-vars
      { caption, message, confirmBtn, cancelBtn }, result => {
        Negotiation.finish({ id: $routeParams.id })
          .$promise.then(function(response) {     // eslint-disable-line no-unused-vars
            getNegotiation(); // updated with new state
            Flash.set('Lyckades att avsluta förhandling.', 'success');
          })
          .catch(new ErrorHandler('Misslyckades att avsluta förhandlingar.'))
          .finally(function() {
            vm.ready = true;
          });
      }, null, null, { size: 'md' });
  }

  vm.finishAsHgfUser = () => {
    let caption = 'Varning';
    let confirmBtn = 'Avsluta förhandling';
    let cancelBtn = 'Avbryt';
    let message = 'Du kommer inte kunna genomföra digitala överenskommelser om du stänger denna förhandling.';

    if (vm.negotiation.finishable.flag) {
      message = 'Om du väljer att avsluta förhandlingen utan att göra klart den i Förhandlarportalen, kommer den inte gå att återöppna.';
    }

    HyUtils.showDefaultModal('ConfirmationModalWithMessageAndButtonsCtrl',
      'components/modal/confirmation/confirmationModalWithMessageAndButtons.html',
      // eslint-disable-next-line no-unused-vars
      { caption, message, confirmBtn, cancelBtn }, result => {
        Negotiation.finishAsHgfUser({ id: $routeParams.id })
          .$promise.then(function(response) {     // eslint-disable-line no-unused-vars
            getNegotiation(); // updated with new state
            Flash.set('Lyckades att avsluta förhandling.', 'success');
          })
          .catch(new ErrorHandler('Misslyckades att avsluta förhandlingar.'))
          .finally(function() {
            vm.ready = true;
          });
      }, null, null, { size: 'md' });
  }

  vm.reopen = () => {
    let caption = 'Varning';
    let confirmBtn = 'Återöppna förhandling';
    let cancelBtn = 'Avbryt';
    let message = 'Är du säker?';

    HyUtils.showDefaultModal('ConfirmationModalWithMessageAndButtonsCtrl',
      'components/modal/confirmation/confirmationModalWithMessageAndButtons.html',
      // eslint-disable-next-line no-unused-vars
      { caption, message, confirmBtn, cancelBtn }, result => {
        Negotiation.reopen({ id: $routeParams.id })
          .$promise.then(function(response) {     // eslint-disable-line no-unused-vars
            getNegotiation(); // updated with new state
            Flash.set('Lyckades att återöppna förhandling.', 'success');
          })
          .catch(new ErrorHandler('Misslyckades att återöppna förhandlingar.'))
          .finally(function() {
            vm.ready = true;
          });
      }, null, null, { size: 'sm' });
  }

  function getCurrentSubpage() {
    var defaultSubPage = 'overview';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = ['motiv', 'documents', 'history', 'agreements', 'blog', 'addproperties', 'artifacts'];
    if (vm.FeatureConfiguration.ENABLE_MEETINGS) {
      whiteList.push('meetings');
    }
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }

  /**
   * displays banner and reloads negotiation
   * @param {string} message
   */
  function rentlistOkReload(message) {
    getNegotiation().then(function() {
      $scope.$broadcast('rentListImported');
    });
    Flash.set(message, 'success');
  }

  function importRentlist(amend) {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      backdrop: 'static',
      size: 'lg',
      index: 1075,
      templateUrl: 'resources/negotiation/show/modals/import-rentlist/importRentlistModal.html',
      controller: 'ImportRentlistCtrl',
      controllerAs: 'vm',
      resolve: {
        amend: () => amend,
        negotiation: function() {
          return vm.negotiation;
        }
      },
      windowClass: 'modal-content--green'
    });

    modalInstance.result.then(function(message) {
      if (message) {
        rentlistOkReload(message);
      }
    });
  }

  function forgeRentlist(amend) {

    let params = { id: $routeParams.id };

    let openForgeListModal = apartmentCount => {
      vm.ready = false;
      (vm.apartmentMode ? Negotiation.queryApartment : Negotiation.queryProperty)(params, {})
        .$promise.then(response => {
          vm.ready = true;
          HyUtils.showDefaultModal('ForgingRentlistModalCtrl',
            'resources/negotiation/show/modals/forging-rentlist/forging-rentlist-modal.html', {
              negotiation: vm.negotiation,
              negotiationRealEstates: response.rows,
              isNewProperty: vm.isNewlyConstructedApartment,
              amend,
              apartmentCount,
            }, result => {
              if (result.numImported) {
                rentlistOkReload("Lägenheter har lagts till förhandlingen");
              }
            }, null, null,
            { isGreen: true });
        });
    }

    Negotiation.countApartment(params)
      .$promise.then(response => {
        if (parseInt(response.count) > 0) {
          openForgeListModal(response.count);
        } else {
          HyUtils.askQuestion('Ingen lägenheter finns i vald fastigheter!', [ 'OK' ]);
        }
      });
  }

  function showWithoutRentList() {
    // eslint-disable-next-line no-unused-vars
    $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'lg',
      index: 1075,
      templateUrl: 'resources/negotiation/show/modals/without-rentlist/withoutRentlistModal.html',
      controller: 'WithoutRentListCtrl',
      controllerAs: 'vm',
      resolve: {
        withoutRentlist: function() {
          return vm.withoutRentlist;
        },
        isNewlyConstructedApartment: function() {
          return vm.isNewlyConstructedApartment;
        }
      },
      windowClass: 'modal-content--green'
    });
  }

  /**
 *
 * Shows edit negotiation type and counterpart popup
 *
 */
  vm.editNegotiation = () => {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      index: 1075, // position above tooltip
      templateUrl: 'resources/negotiation/edit/edit-negotiationType-modal.html',
      controller: 'EditNegotiationTypeModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      resolve: {
        negotiationObject: function() {
          return vm.negotiation;
        },
        isFreeAgreement: () => false,
      }
    });
    modalInstance.result.then(function(negotiation) {
      vm.negotiation.name = negotiation.name;
      vm.negotiation.type = negotiation.type;
    });
  }

  /**
 *
 * Shows Warning and confirmation popup for the checkbox
 *
 */
  vm.confirmNegotiationWithoutApartments = () => {
    if (vm.negotiation.isFinished || vm.negotiation.rentlistStatus.agreementsStarted) {
      return;
    }
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      index: 1075, // position above tooltip
      templateUrl: 'resources/negotiation/edit/skip-rentlist-modal.html',
      controller: 'SkipRentlistModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      resolve: {
        negotiationObject: function() {
          return vm.negotiation;
        },
        hasApartments: function() {
          return vm.hasApartments;
        },
      }
    });
    modalInstance.result.then(function() {
      getNegotiation();
    });
  }

  vm.deleteNegotiation = Negotiation.delete;
  vm.warningMessageDelete = `<p>Du kommer nu att <b><u>RADERA</u></b> hela förhandlingen som är skapad från databasen.
Detta val går ej att ångra eller återskapa, och om du tar bort förhandlingen felaktigt måste du själv återskapa förhandlingen på nytt igen.
</p>
<p>Önskar du endast återkalla vissa fastigheter i en förhandling då det blivit fel, ska du välja avbryt.</p>
<p><b>Instruktion om återkallelse finns i lathund på "Mina sidor"</b></p>`;

  vm.unforgeRentlist = unassignedOnly => {
    vm.busy = true;
    Negotiation.unforgeRentlist({
      id: vm.negotiation.id,
      unassignedOnly,
    // eslint-disable-next-line no-unused-vars
    }).$promise.then(result => {
      vm.busy = false;
      $route.reload();
    }).catch(() => {
      new ErrorHandler('Misslyckades att avvälj lägenheter');
    });
  };

}
})();
(function () {
'use strict';

SearchAgreementsCtrl.$inject = ["$scope", "$routeParams", "$location", "SubPage", "Auth"];
angular.module('hyresgastApp').controller('SearchAgreementsCtrl', SearchAgreementsCtrl);

function SearchAgreementsCtrl($scope, $routeParams, $location,
  SubPage, Auth) {
  var vm = this;
  vm.Auth = Auth;

  let setupTabs = () => {
    vm.tablist = [
      { title: 'Överenskommelse', path: '/search/agreements?view=agreements' },
      { title: 'Avtal', path: '/search/agreements?view=free' },
    ];
  };

  function getCurrentSubpage() {
    var defaultSubPage = 'agreements';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = ['agreements', 'free'];
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }

  getCurrentSubpage();
  setupTabs();

  $scope.$on('$routeUpdate', function() {
    if (!$location.search().order) {
      getCurrentSubpage();
    }
  });
}

})();
(function () {
'use strict';

ShowNegotiationCallCtrl.$inject = ["$scope", "$location", "$routeParams", "$uibModal", "Auth", "Flash", "SubPage", "HyUtils", "Parties", "NegotiationCallStates", "NegotiationTypes", "NegotiationCall", "Seenby", "ObjectTypes", "Help", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationCallCtrl', ShowNegotiationCallCtrl);

function ShowNegotiationCallCtrl(
  $scope, $location, $routeParams, $uibModal,
  Auth, Flash, SubPage, HyUtils,
  Parties, NegotiationCallStates, NegotiationTypes,
  NegotiationCall, Seenby, ObjectTypes,
  Help, ErrorHandler
) {
  var vm = this;

  vm.statistics = {
    municipalities: 0,
    properties: 0,
    apartments: 0
  };

  vm.updateOffice = updateOffice;
  vm.NegotiationCallStates = NegotiationCallStates;
  vm.returnNegotiationCall = returnNegotiationCall;
  vm.NegotiationTypes = NegotiationTypes;
  vm.handleToggle = handleToggle;
  vm.Auth = Auth;
  vm.renderHelpModal = renderHelpModal;
  vm.gotoNewNegotiation = gotoNewNegotiation;

  Auth.heartbeat().then(function() {
    vm.currentUserId = Auth.currentUser().id;
    if (Auth.currentUser().party === Parties.HGF) {
      vm.isHgfUser = true;
      vm.tablist = [
        {
          title: 'Bestånd',
          path: '/negotiation-calls/' + $routeParams.id
        },
        {
          title: 'Kvar att tilldela',
          path: '/negotiation-calls/' + $routeParams.id + '?view=remaining'
        },
        {
          title: 'Misstänkta fel',
          path: '/negotiation-calls/' + $routeParams.id + '?view=errors',
          hasErrors: false
        },
        {
          title: 'Underlag',
          path: '/negotiation-calls/' + $routeParams.id + '?view=documents'
        },
        {
          title: 'Förhandlingar',
          path: '/negotiation-calls/' + $routeParams.id + '?view=negotiations'
        }
      ];
    } else {
      vm.tablist = [
        {
          title: 'Bestånd',
          path: '/negotiation-calls/' + $routeParams.id
        },
        {
          title: 'Misstänkta fel',
          path: '/negotiation-calls/' + $routeParams.id + '?view=errors',
          hasErrors: false
        },
        {
          title: 'Underlag',
          path: '/negotiation-calls/' + $routeParams.id + '?view=documents'
        },
        {
          title: 'Förhandlingar',
          path: '/negotiation-calls/' + $routeParams.id + '?view=negotiations'
        }
      ];
    }
  });

  $scope.$on('$routeUpdate', function() {
    // avoid unecessary calling of getCurrentSubpage() when sorting
    if (!$location.search().order) {
      // reset offset when sorting
      getCurrentSubpage();
    }
  });
  queryErrors();
  getCurrentSubpage();
  getNegotiationCall();
  ////////////
  function queryErrors() {
    vm.readyErrors = false;

    return NegotiationCall.queryErrors({
      id: $routeParams.id,
      limit: vm.pageLimit,
      offset: vm.pageOffset
    })
      .$promise.then(function(data) {
        let errorTab = vm.tablist.find(element => element.title === 'Misstänkta fel');
        if (errorTab) {
          errorTab.hasErrors = !!data.rows.length;
        }
      })
      .catch(ErrorHandler('Misslyckades att hämta lista med fel.'))
      .finally(function() {
        vm.readyErrors = true;
      });
  }

  function handleToggle() {
    vm.saving = true;
    vm.negotiationCall.handledByUserId = vm.toggleValue ? vm.currentUserId : null;

    NegotiationCall.updateHandler(vm.negotiationCall)
      .$promise.then(function() {
        setTogglerText();
      })
      .catch(ErrorHandler('Misslyckades att  bereda förhandling.'))
      .finally(function() {
        vm.saving = false;
      });
  }

  function setTogglerText() {
    if (!vm.negotiationCall.handledByUserId) {
      vm.togglerText = 'Bered denna framställan (lås för andra)';
    } else if (vm.negotiationCall.handledByUserId === vm.currentUserId) {
      vm.togglerText = 'Jag bereder denna framställan (låst för andra)';
    } else {
      vm.toggleValue = false;
      vm.togglerText = `${vm.negotiationCall.handler.firstname} ${vm.negotiationCall.handler.lastname} bereder denna framställan`;
    }
  }

  function getNegotiationCall() {

    vm.negotiationCallPromise = NegotiationCall.get({
      id: $routeParams.id
    }).$promise.then(function(response) {
      vm.negotiationCall = response;

      vm.isPropertyBased =
        vm.negotiationCall.type === NegotiationTypes.ANNUAL_PROPERTY ||
        vm.negotiationCall.type === NegotiationTypes.STANDARD_PROPERTY ||
        vm.negotiationCall.type === NegotiationTypes.REBUILD_PROPERTY ||
        vm.negotiationCall.type === NegotiationTypes.REBUILD_PROPERTY_2 ||
        vm.negotiationCall.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY ||
        vm.negotiationCall.type === NegotiationTypes.UTILITY_VALUE_PROPERTY;

      vm.negotiationCall.isNewlyConstructedApartment =
        vm.negotiationCall.type === NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT;

      if (vm.negotiationCall.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY) {
        vm.negotiationCall.isNewlyConstructedProperty = true;
        vm.tablist = [
          {
            title: 'Underlag',
            path: '/negotiation-calls/' + $routeParams.id + '?view=documents'
          },
          {
            title: 'Förhandlingar',
            path: '/negotiation-calls/' + $routeParams.id + '?view=negotiations'
          }
        ];
        $location.search({
          view: 'documents'
        });
      }

      vm.toggleValue = response.handler && response.handler.id === vm.currentUserId;
      setTogglerText();
    })
      .catch(ErrorHandler('Misslyckades att hämta förhandlingsframställan.'))
      .finally(function() {
        vm.ready = true;
        Seenby.set({
          objectType: ObjectTypes.CALL.code,
          objectId: $routeParams.id,
        });
      });
    return vm.negotiationCallPromise;
  }

  function updateOffice() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      index: 1075,
      templateUrl: 'resources/negotiation-call/show/modals/change-office-modal.html',
      controller: 'ChangeOfficeModalCtrl',
      controllerAs: 'vm',
    });

    modalInstance.result.then(function(selectedOffice) {
      // Update the object with the office chosen in the modal
      vm.negotiationCall.hgfOfficeId = selectedOffice.id;
      vm.negotiationCall.office = {
        name: selectedOffice.name,
        id: selectedOffice.id
      };
      NegotiationCall.setOffice(vm.negotiationCall)
        .$promise.then(function() {
          Flash.set('Ändring av kontor lyckades', 'success');
        })
        .catch(ErrorHandler('Misslyckades att ändra kontor.'));
    });
  }

  function returnNegotiationCall() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'md',
      index: 1075,
      templateUrl: 'resources/negotiation-call/show/modals/return-negotiation-call-modal.html',
      controller: 'ReturnNegotiationCallModal',
      controllerAs: 'vm'
    });
    modalInstance.result.then(function(message) {
      NegotiationCall.returnNegotiation({
        id: vm.negotiationCall.id,
        message: message
      }).$promise.then(function() {
        Flash.set('Skicka tillbaka framställan lyckades', 'success');
        return getNegotiationCall();
      })
        .catch(ErrorHandler('Misslyckades att skicka tillbaka framställan.'));
    });
  }

  vm.deleteCall = NegotiationCall.delete;

  function getCurrentSubpage() {
    var defaultSubPage = 'properties';
    // create a validation list against manually change of URL '?view=' parameter
    var allowList = [];
    if (vm.isHgfUser) {
      allowList = ['remaining', 'errors', 'documents', 'negotiations'];
    } else {
      allowList = ['errors', 'documents', 'negotiations'];
    }
    vm.currentSubpage = SubPage.set($routeParams.view, allowList, defaultSubPage);
  }

  function renderHelpModal(title, bodyText) {
    Help.renderHelpModal(title, bodyText);
  }

  function gotoNewNegotiation(id) {
    var type =
      vm.negotiationCall.isNewlyConstructedProperty || vm.negotiationCall.isNewlyConstructedApartment
        ? 'newly-constructed-property'
        : 'new';
    var rentListBasedNegotiationCall = vm.negotiationCall.realEstateSummary.apartments > 0 ? 'yes' : 'no'
    $location.path('/negotiation-calls/' + id + '/negotiations/' + type).search({
      negotiationCallType: vm.negotiationCall.type,
      rentListBased: rentListBasedNegotiationCall
    });
  }
}

})();
(function () {
'use strict';

ListOrganizationsPendingCtrl.$inject = ["$scope", "$location", "RequestedAccount", "Flash", "Auth", "Office", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListOrganizationsPendingCtrl', ListOrganizationsPendingCtrl);

function ListOrganizationsPendingCtrl($scope, $location, RequestedAccount, Flash, Auth, Office, ErrorHandler) {
  var vm = this;
  vm.Auth = Auth;
  vm.user = {};
  vm.organizations = [];
  vm.getOrganizations = getOrganizations;
  vm.gotoShow = gotoShow;
  vm.pageLimit = 30;
  vm.pageOffset = 0;
  vm.sortColumn = $location.search().order || 'officeName'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  vm.listColumns = [
    { title: 'Kontor', column: 'officeName' }, 
    { title: 'Företagsnamn', column: 'name' },
    { title: 'Organisationsnummer', column: 'organizationNumber' },
    { title: 'Förnamn', column: 'firstname' }, 
    { title: 'Efternamn', column: 'lastname' },
    { title: 'E-post', column: 'email' }
  ];

  getOrganizations();

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getOrganizations();
    }
  });

  ////////////

  function gotoShow(id) {
    $location.search({});
    $location.path('/requested-accounts/organization/' + id + '/approve');
  }

  function getOrganizations(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.pageOffset = 0;
    }
    return RequestedAccount.query({
      limit: vm.pageLimit,
      offset: vm.pageOffset,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'search[officeName]': vm.inputTextOfficeName,
      'search[name]': vm.inputTextOrgName,
      'search[organizationNumber]': vm.inputTextOrgNumber,
      'search[firstname]': vm.inputTextFirstName,
      'search[lastname]': vm.inputTextLastName,
      'search[email]': vm.inputTextEmail
    })
      .$promise.then(function(data) {
        vm.organizations = data.rows;
        vm.totalCount = data.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta organisationer.'))
      .finally(function() {
        vm.ready = true;
      });
  }
}

})();
(function () {
'use strict';

ListUsersPendingCtrl.$inject = ["$scope", "$location", "$routeParams", "Auth", "Flash", "User", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListUsersPendingCtrl', ListUsersPendingCtrl);
// external = list of users wanting in to organization
function ListUsersPendingCtrl($scope, $location, $routeParams, Auth, Flash, User, ErrorHandler) {
  var vm = this;
  vm.gotoShow = gotoShow;
  vm.getUsers = getUsers;
  vm.pageLimit = 30;
  vm.pageOffset = 0;
  vm.sortColumn = $location.search().order || 'lastname'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  vm.listColumns = [
    { title: 'Förnamn', column: 'firstname' },
    { title: 'Efternamn', column: 'lastname' },
    { title: 'E-post', column: 'email' }
  ];

  Auth.heartbeat().then(function () {
    vm.organizationId = Object.keys(Auth.currentUser().roleByOrganization)[0];
    getUsers();
  });

  $scope.$on('$routeUpdate', function () {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getUsers();
    }
  });

  ////////////

  function gotoShow(id) {
    $location.search({});
    $location.path('/requested-accounts/user/' + id + '/approve');
  }

  function getUsers(filterMode) {
    vm.ready = false;
    if (filterMode) {
      vm.pageOffset = 0;
    }
    return User.queryRequestedByOrganization({
      organizationId: vm.organizationId,
      limit: vm.pageLimit,
      offset: vm.pageOffset,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'search[firstname]': vm.inputTextFirstName,
      'search[lastname]': vm.inputTextLastName,
      'search[email]': vm.inputTextEmail
    })
      .$promise.then(function (response) {
        vm.users = response.rows;
        vm.totalCount = response.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta användaren.'))
      .finally(function () {
        vm.ready = true;
      });
  }
}

})();
(function () {
'use strict';

EditUserCtrl.$inject = ["$location", "$routeParams", "Auth", "Flash", "User", "ErrorHandler"];
angular.module('hyresgastApp').controller('EditUserCtrl', EditUserCtrl);

function EditUserCtrl($location, $routeParams, Auth, Flash, User, ErrorHandler) {
  // Responsible for create, update and delete

  var vm = this;
  vm.user = {};
  vm.save = save;
  vm.setPassword = setPassword;

  getUser();

  ////////////

  function getUser() {
    return User.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.ready = true;
        vm.user = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta användaren.'));
  }

  function save() {
    vm.saving = true;
    User.save(vm.user)
      .$promise.then(
        function(response) {
          Flash.set('Ändring av användare lyckades.', 'success');
          $location.path('/users/' + response.id);
        })
      .catch(ErrorHandler('Användare med denna e-postadress är redan registrerad.'))
      .finally(function() {
        vm.saving = false;
      });
  }

  function setPassword() {
    vm.savingPassword = true;
    vm.credentials['id'] = Auth.currentUser().id;

    User.setPassword(vm.credentials)
      .$promise.then(
        // eslint-disable-next-line no-unused-vars
        function(response) {
          Flash.set('Ändring av lösenord lyckades.', 'success');
        })
      .catch(ErrorHandler('Ändring av lösenord misslyckades. Kontrollera ditt nuvarande lösenord.'))
      .finally(function() {
        vm.credentials = {};
        vm.verifyPassword = '';
        vm.passwordForm.$setPristine();
        vm.passwordForm.$setUntouched();
        vm.savingPassword = false;
      });
  }
}

})();
(function () {
'use strict';

EditUserModalCtrl.$inject = ["$uibModalInstance", "$timeout", "userId", "User", "SpecUser", "Organization", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('EditUserModalCtrl', EditUserModalCtrl);

function EditUserModalCtrl(
  $uibModalInstance, $timeout,
  userId,
  User, SpecUser, Organization,
  Flash, ErrorHandler
) {
  // Responsible for create, update and delete

  var vm = this;
  vm.tabs = [
    { title: 'Grundinformation', key: 'main' },
    { title: 'Organisation', key: 'organization' },
    { title: 'Behörigheter', key: 'permissions' },
  ];
  vm.user = {};
  vm.roles = [
    { id: 0,   text: 'Medlem (ej godkänd)' },
    { id: 100, text: 'Medlem' },
    { id: 200, text: 'Admin' },
  ];

  ////////////
  function queryOrganization(cb) {
    vm.loading = true;
    Organization.query({
      order: 'name',
      direction: 'ASC',
      'search[name]': vm.inputTextOrgName
    }).$promise.then(function (response) {
      vm.organizations = response.rows;
      vm.loading = false;
      if (cb) {
        return cb();
      }
    }).catch(new ErrorHandler('Misslyckades att hämta organisation.'))
      .finally(() => { vm.loading = false });
  }
  function getUser() {
    return User.get({ id: userId })
      .$promise.then(function (response) {
        vm.ready = true;
        vm.user = response;
        vm.user.roleByOrganization = vm.user.roleByOrganizationUnleashed;
        queryOrganization(() => {
          let organizationIds = Object.keys(vm.user.roleByOrganization);
          vm.selectedOrganizations = vm.organizations.filter(x => organizationIds.includes(x.id.toString()));
          vm.selectedOrganizations.forEach(x => x.role = vm.roles.find(r => r.id === vm.user.roleByOrganization[x.id]) );
        });
      })
      .catch(ErrorHandler('Misslyckades att hämta användaren.'));
  }

  vm.orgAfterType = () => {
    if (vm.orgTimer) {
      $timeout.cancel(vm.orgTimer);
    }
    vm.orgTimer = $timeout(queryOrganization, 500);
  };
  vm.selectOrganization = organization => {
    if (vm.selectedOrganizations.find(x => x.id === organization.id)) {
      return;
    }
    let newOrganization = Object.assign({ role: vm.roles[0] }, organization);
    vm.selectedOrganizations.push(newOrganization);
  };
  vm.deselectOrganization = organization => {
    vm.selectedOrganizations = vm.selectedOrganizations.filter(x => x.id !== organization.id);
  };

  vm.whyDisabled = () => {
    if (!vm.selectedOrganizations || vm.selectedOrganizations.length === 0) {
      return 'Minst en organisation måste väljas';
    }
    return '';
  };

  vm.changePassword = () => {

    vm.savingPassword = true;

    let credentials = {
      id: vm.user.id,
      password: vm.newPassword,
    };

    User.setPassword(credentials)
      // eslint-disable-next-line no-unused-vars
      .$promise.then(response => { Flash.set('Ändring av lösenord lyckades.', 'success') })
      .catch(ErrorHandler('Ändring av lösenord misslyckades. Kontrollera ditt nytt lösenord.'))
      .finally(() => {
        vm.newPassword = null;
        vm.newPasswordAgain = null;
        vm.unterpopupNewPassword = false;
        vm.savingPassword = false;
      });
  };

  vm.saving = false;
  vm.save = () => {
    vm.saving = true;
    if (vm.selectedOrganizations) {
      vm.user.roleByOrganization = vm.selectedOrganizations.reduce((acc, el) => {
        acc[el.id] = el.role.id;
        return acc;
      }, {});
    }

    SpecUser.updateUser({ id: userId, data: vm.user })
      .$promise.then(function (response) {
        Flash.set('Användaren har sparats', 'success');
        $uibModalInstance.close(response);
      })
      .catch(function () {
        Flash.set('Misslyckades att spara användare.', 'error');
        $uibModalInstance.dismiss('cancel');
      })
      .finally(function () {
        vm.saving = false;
      });
  };

  vm.cancel = () => { $uibModalInstance.dismiss() };

  //start
  getUser();
}

})();
(function () {
'use strict';

NewUserCtrl.$inject = ["$location", "$routeParams", "$filter", "$timeout", "Auth", "HyUtils", "Flash", "User", "Organization", "Office", "ErrorHandler"];
angular.module('hyresgastApp').controller('NewUserCtrl', NewUserCtrl);

function NewUserCtrl($location, $routeParams, $filter, $timeout, Auth, HyUtils, Flash, User, Organization, Office, ErrorHandler) {
  var vm = this;
  vm.user = { email: '', phone: '' };
  vm.selectedOffice = {};

  vm.startCheckEmail = startCheckEmail;
  vm.confirmingEmail = false;
  vm.isMailConfirmed = 'unknown';

  vm.save = save;
  vm.isFromInvite = false;

  if ($routeParams.email) {
    // when register a new user via link
    vm.user.email = $routeParams.email;
    vm.verifyEmail = $routeParams.email;
  }
  if ($routeParams.organizationNumber) {
    vm.user.organization = Object.assign(vm.user.organization || {}, {
      organizationNumber: $routeParams.organizationNumber
    });
    findOrganization();
  }

  if ($routeParams.token) {
    vm.user.token = $routeParams.token;
  }
  if ($routeParams.delegationParticipant) {
    vm.user.delegationParticipant = true;
  }

  function getPhone() {
    if ($routeParams.email && $routeParams.organizationNumber) {
      User.getPhoneByInvite({
        email: vm.user.email,
        organizationId: vm.user.organization.id
      }).$promise.then(response => {
        vm.user.phone = response.phone;
        tamePhoneProcess();
      });
    }
  }

  function findOrganization() {
    vm.readyOrganization = false;

    return Organization.queryByOrganizationNumber({
      organizationNumber: vm.user.organization.organizationNumber.replace('-', '')
    })
      .$promise.then(function(data) {
        if (data.id) {
          vm.user.organization = data;
          getPhone();
        }
        queryOffice();
        vm.newOrgNumber = !data.id;
      })
      .catch(function() {
        Flash.set('Misslyckades att hämta organisationer.', 'error');
      })
      .finally(function() {
        vm.readyOrganizations = true;
      });
  }

  vm.checkForExistingOrgNumber = checkForExistingOrgNumber;
  function checkForExistingOrgNumber() {
    findOrganization();
  }

  vm.tamePhoneNumber = () => {
    $timeout(() => {
      tamePhoneProcess();
    }, 3000);
  };

  function tamePhoneProcess() {
    let tamedPhone = HyUtils.tamePhoneNumber(vm.user.phone);
    vm.foreignPhone = tamedPhone.countryCode !== '+46';
    vm.user.phone = vm.foreignPhone ? tamedPhone.countryCode + tamedPhone.number :  tamedPhone.number;
  }

  function startCheckEmail() {
    if (vm.emailCheckScheduled) {
      return;
    }

    vm.emailCheckScheduled = true;
    $timeout(() => {
      checkEmail();
      vm.emailCheckScheduled = false;
    }, 3000);
  }

  function checkEmail() {
    vm.confirmingEmail = true;
    User.checkEmail({ email: vm.user.email}).$promise.then(
      function(response) {
        vm.isMailConfirmed = response.isValid;
        vm.confirmingEmail = false;
      });
  }

  function queryOffice() {
    return Office.query().$promise.then(function(response) {
      vm.offices = response;
    })
      .catch(ErrorHandler('Misslyckades att hämta kontor.'));
  }

  function save() {
    if (vm.user.email.match(/@hyresgastforeningen\.se$/i)) {
      Flash.set('Epostadress berkräftades inte', 'error');
      return;
    }

    if (vm.newOrgNumber && !vm.selectedOffice.id) {
      Flash.set('HGF kontor måste väljas', 'error');
    } else {

      if(vm.selectedOffice.id) {
        vm.user.organization.hgfOfficeId = vm.selectedOffice.id;
      }

      vm.saving = true;
      let tamedPhone = HyUtils.tamePhoneNumber(vm.user.phone);
      let userToSave = Object.assign({}, vm.user);
      userToSave.phone = tamedPhone.countryCode + tamedPhone.number;
      User.save(userToSave)
        .$promise.then(
          function(response) {
            if (response.isfrominvite || vm.user.delegationParticipant) {
              $location.path('users/new/thank-you-invite');
            } else {
              $location.path('users/new/thank-you');
            }
          })
        .catch(function(reason) {
          if (reason.status === 404) {
            Flash.set(
              'Länken du använder för registreringen är förbrukad. Vänligen be HGF om en ny inbjudan till portalen.',
              'error'
            );
          } else {
            if (reason.data.code) {
              switch (reason.data.code) {
                case 0:
                  Flash.set('Registrering misslyckades. Kontrollera din e-post och ditt lösenord.', 'error');
                  break;
                case 1:
                  Flash.set('Registrering misslyckades. Ogiltigt organsiationsnummer.', 'error');
                  break;
                case 2:
                  Flash.set('Registrering misslyckades. Kontakta Förhandlarportalen.', 'error');
                  $location.path('users/new/error');
                  break;
              }
            } else {
              Flash.set('Registrering misslyckades.', 'error');
            }
          }
        })
        .finally(function() {
          vm.saving = false;
        });
    }
  }
}

})();
(function () {
'use strict';

NewUserModalCtrl.$inject = ["$uibModalInstance", "$timeout", "fullMode", "Organization", "RoleLookup", "Office", "Flash", "SpecUser", "ErrorHandler"];
angular.module('hyresgastApp').controller('NewUserModalCtrl', NewUserModalCtrl);

function NewUserModalCtrl(
  $uibModalInstance,
  $timeout,
  fullMode,
  Organization,
  RoleLookup,
  Office,
  Flash,
  SpecUser,
  ErrorHandler
) {
  var vm = this;

  vm.saving = false;
  vm.fullMode = fullMode;
  if (!fullMode) {
    vm.selectedRole = { key: 'NONE', value: 0 };
  }
  vm.orgTimer = null;

  vm.save = () => {
    vm.saving = true;

    SpecUser.createUser({
      user: vm.user,
      selectedOrganization: vm.selectedOrganization
        ? { id: vm.selectedOrganization.id, name: vm.selectedOrganization.name }
        : vm.selectedOrganization,
      newOrganizationData: vm.organization,
      role: vm.selectedRole
    })
      .$promise.then(function (response) {
        Flash.set('Användaren har sparats', 'success');
        $uibModalInstance.close(response);
      })
      .catch(function () {
        Flash.set('Misslyckades att spara användare.', 'error');
        $uibModalInstance.dismiss('cancel');
      })
      .finally(function () {
        vm.saving = false;
      });
  };

  vm.cancel = () => {
    $uibModalInstance.dismiss();
  };

  vm.setRadioModelByOutsideClick = setRadioModelByOutsideClick;
  function setRadioModelByOutsideClick(choice) {
    vm.choiceMethod = choice;
  }

  queryOrganization();

  function queryOrganization() {
    vm.loading = true;
    return Organization.query({
      order: 'name',
      direction: 'ASC',
      'search[name]': vm.inputTextOrgName
    })
      .$promise.then(function (response) {
        vm.loading = false;
        vm.organizations = response.rows;
        if (vm.organizations.length === 1) {
          vm.selectedOrganization = response.rows[0];
        }
      })
      .catch(new ErrorHandler('Misslyckades att hämta organisation.'))
      .finally(() => {
        vm.loading = false;
      });
  }

  queryOffice();

  function queryOffice() {
    return Office.query()
      .$promise.then(function (response) {
        vm.offices = response;
      })
      .catch(new ErrorHandler('Misslyckades att hämta kontor.'));
  }

  vm.roles = RoleLookup;

  vm.selectOrganization = org => {
    vm.selectedOrganization = org;
    vm.inputTextOrgName = null;
  };

  vm.orgAfterType = () => {
    if (vm.orgTimer) {
      $timeout.cancel(vm.orgTimer);
    }
    vm.orgTimer = $timeout(queryOrganization, 500);
  };
}

})();
(function () {
'use strict';

SetPasswordWithTokenCtrl.$inject = ["Flash", "User", "$routeParams", "$location", "$rootScope"];
angular.module('hyresgastApp').controller('SetPasswordWithTokenCtrl', SetPasswordWithTokenCtrl);

function SetPasswordWithTokenCtrl(Flash, User, $routeParams, $location, $rootScope) {
  var vm = this;
  vm.user = {};
  vm.title = 'Ändra lösenord';
  vm.setPassword = setPassword;
  vm.ready = true;
  vm.credentials = {};
  ////////////

  function setPassword() {
    vm.credentials.token = $routeParams.token;
    vm.credentials.id = $routeParams.userId;

    vm.saving = true;
    User.setPasswordWithToken(vm.credentials)
      .$promise.then(
        function(response) {
          Flash.set('Ändring av lösenord lyckades.' + ' Varsågod att logga in med ditt nya lösenord.', 'success');
          $location.url('/');
          $rootScope.$broadcast('event:auth-loginRequired', null);
        },
        function(reason) {
          if (reason.status === 404) {
            Flash.set(
              'Länken du använder har gått ut. ' + 'Vänligen generera en ny genom att återställa lösenordet.',
              'error'
            );
            $location.url('/emailreset');
            $rootScope.$broadcast('event:auth-loginRequired', null);
          } else if (reason.status === 403) {
            Flash.set(
              'Lösenordet har redan ändrats via den här länken. ' +
                'Vänligen generera en ny länk om du har glömt bort ditt nya lösenord.',
              'error'
            );
            $location.url('/emailreset');
            $rootScope.$broadcast('event:auth-loginRequired', null);
          } else {
            Flash.set('Ändring av lösenord misslyckades. Försök igen.', 'error');
          }
        }
      )
      .finally(function() {
        vm.credentials = {};
        vm.passwordForm.$setPristine();
        vm.passwordForm.$setUntouched();
        vm.saving = false;
      });
  }
}

})();
(function () {
'use strict';

ListUserCtrl.$inject = ["$scope", "$location", "$routeParams", "$timeout", "$document", "$route", "Parties", "SpecUser", "SubPage", "Auth", "HyUtils", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListUserCtrl', ListUserCtrl);

function ListUserCtrl($scope, $location, $routeParams, $timeout, $document, $route,
  Parties,
  SpecUser,
  SubPage, Auth, HyUtils, Flash, ErrorHandler) {
  var vm = this;
  vm.userParties = Parties;
  vm.tablist = [
    { title: 'Hyresgästföreningen', path: '/users' },
    { title: 'Organisation', path: '/users?view=external' },
    { title: 'Inbjudna representanter', path: '/users?view=delegate' }
  ];
  vm.child = null;

  getCurrentSubpage();

  $timeout(function() {
    setButtons();
  }, 150);

  $scope.$on('$routeUpdate', function() {
    setButtons();
    // avoid unecessary calling of getCurrentSubpage(); when sorting
    if (!$location.search().order) {
      // reset offset when sorting
      getCurrentSubpage();
    }
  });

  /////////

  function getCurrentSubpage() {
    var defaultSubPage = 'hgf';
    // create a validation list against manually change of URL '?view=' parameter
    var whiteList = ['external', 'delegate'];
    vm.currentSubpage = SubPage.set($routeParams.view, whiteList, defaultSubPage);
  }

  function setButtons() {
    if ($location.search().view === 'external') {
      if (!vm.child) {
        vm.child = HyUtils.bruteSearch($scope, 'userExternalVM');
      }

      vm.buttons = [
        {
          title: 'Ny användare',
          url: '#',
          click: () => { vm.child.newUser() },
          isVisible: () => (Auth && Auth.canDo('createUser')),
          icon: 'newuser',
        }
      ];
    } else {
      vm.buttons = [];
    }
  }

  vm.beginEditEmail = user => {
    vm.editedUser = user;
    vm.newEmail = user.email;
    $timeout(() => {
      let el = $document[0].querySelector('#emailEdit'+user.id);
      el.focus();
    }, 300);
  };
  vm.endEditEmail = () => {
    SpecUser.updateUser({
      id: vm.editedUser.id,
      data: { email: vm.newEmail },
    // eslint-disable-next-line no-unused-vars
    }).$promise.then(response => {
      vm.editedUser.email = vm.newEmail;
      vm.editedUser = null;
      Flash.set('E-post sparad.', 'info');
    }).catch(ErrorHandler('Misslyckades att spara e-post.'));
  };

  vm.deleteUser = userId => {
    HyUtils.showDefaultModal('ConfirmationModalWithMessageCtrl',
      'components/modal/confirmation/confirmationModalWithMessage.html',
      { message: `Vill du verkligen ta bort användare?` },
      // eslint-disable-next-line no-unused-vars
      result => {
        SpecUser.deleteUser({ id: userId })
          // eslint-disable-next-line no-unused-vars
          .$promise.then(response => { $route.reload() })
          .catch(ErrorHandler('Användaren har organisationer eller förhandlingar.'));
      }, null, null, { size: 'md' });
  };
}

})();
(function () {
'use strict';

ShowUserWelcomeCtrl.$inject = ["$location", "$routeParams", "$uibModal", "FreeAgreement", "Meeting", "Negotiation", "NegotiationCall", "User", "Auth", "ErrorHandler", "Flash", "FeatureConfiguration", "FreeAgreementTypes", "NegotiationCallStates", "Parties"];
angular.module('hyresgastApp').controller('ShowUserWelcomeCtrl', ShowUserWelcomeCtrl);

function ShowUserWelcomeCtrl(
  $location, $routeParams, $uibModal,
  FreeAgreement, Meeting, Negotiation, NegotiationCall, User,
  Auth, ErrorHandler, Flash,
  FeatureConfiguration, FreeAgreementTypes, NegotiationCallStates, Parties
) {
  var vm = this;

  vm.Auth = Auth;
  vm.parties = Parties;
  vm.NegotiationCallStates = NegotiationCallStates;
  vm.FreeAgreementTypes = Object.values(FreeAgreementTypes)
    .reduce((dict, el) => { dict[el.code] = el.name; return dict; }, {});
  vm.FeatureConfiguration = FeatureConfiguration;

  vm.user = {};
  vm.myMeetings = [];
  vm.isOrphan = false;

  vm.headerButtons = [
    {
      title: 'Skapa framställan',
      url: '/negotiation-calls/new',
      isVisible: () => (Auth && Auth.canDo('createCall')),
      icon: 'call'
    },
    {
      title: 'Skapa förhandling',
      url: '/negotiation/new-direct',
      isVisible: () => (Auth && Auth.canDo('createDirectNegotiation') && !vm.isOrphan),
      icon: 'negotiation'
    },
    {
      title: 'Skapa avtal',
      url: Auth.isHgfUser() ? '/agreements/new' : '/agreements?new',
      isVisible: () => Auth && Auth.canDo('createAgreement'),
      icon: 'agreement'
    }
  ];

  if (Auth.isHgfUser()) {
    vm.headerButtons.push({
      title: 'Intern E-signering',
      url: '/agreements/new?internal=true',
      isVisible: () => Auth && Auth.canDo('createAgreement'),
      icon: 'agreement',
    });
  }

  vm.dateOptions = {
    customClass: function(options) {
      return hasDayMeeting(options.date).length > 0 ? 'active-date' : '';
    }
  };

  vm.agreementColumns = [
    { title: 'Nummer', column: 'fancyNumber' },
    { title: 'Typ',    column: 'type', template: 'type' },
    { title: 'Text',   template: 'slot1' },
  ];

  getUser();
  getNegotiationCalls();
  getNegotiations();
  getMyMeetings();

  ////////////
  vm.gotoMeeting = function (negotiationId, meetingId) {
    $location.path('/negotiations/' + negotiationId + '/meetings/' + meetingId);
  }
  vm.gotoNegotiationCall = function (negotiationCallId) {
    $location.path('/negotiation-calls/' + negotiationCallId);
  }
  vm.gotoNegotiation = function (negotiationId) {
    $location.path('/negotiations/' + negotiationId);
  }
  vm.gotoAgreement = agreement => {
    $location.search({});
    $location.path('/agreements/' + agreement.id);
  };

  vm.getMyAgreements = function () {
    // eslint-disable-next-line no-undef, no-unused-vars
    return new Promise((resolve, reject) => {
      let userId = Auth.currentUser().id;
      FreeAgreement.list({
        userId,
        minaSidor: true
      }).$promise.then(result => {
        resolve({ rows: result.agreements, count: result.agreements.length });
      });
    });
  }
  vm.getAgreementContextInfo = row => ({
    title: 'Avtal '+row.fancyNumber,
    link: '/agreements/'+row.id,
  });

  function getUser() {
    vm.userReady = false;
    return User.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.user = response;
        let noOffices = !vm.user.offices || !Object.keys(vm.user.offices).length;
        if (vm.user.party === Parties.HGF && noOffices) {
          vm.isOrphan = true;
          Flash.set('Ditt användarkonto är inte kopplat till kontor. Det innebär begränsningar i Portalen', 'error');
        }
      })
      .catch(new ErrorHandler('Misslyckades att hämta användaren.'))
      .finally(function() {
        vm.userReady = true;
      });
  }

  function getNegotiationCalls() {
    vm.negotiationCallsReady = false;

    return NegotiationCall.query({
      limit: 3,
      order: 'sentAt',
      direction: 'DESC',
      welcome: true
    })
      .$promise.then(function(data) {
        vm.negotiationCalls = data.rows;
      })
      .catch(new ErrorHandler('Misslyckades att hämta framställan.'))
      .finally(function() {
        vm.negotiationCallsReady = true;
      });
  }

  function getNegotiations() {
    vm.negotiationsReady = false;
    return Negotiation.query({
      limit: 3,
      order: 'createdAt',
      direction: 'DESC',
      mine: true
    })
      .$promise.then(function(data) {
        vm.negotiations = data.rows;
      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandlingar.'))
      .finally(function() {
        vm.negotiationsReady = true;
      });
  }


  //#region Meetings
  function getMyMeetings() {
    if(!vm.FeatureConfiguration.ENABLE_MEETINGS) {
      //set in order to work with html validation, when meetings are disabled
      vm.meetingsReady = true;
      return;
    }

    vm.meetingsReady = false;
    return Meeting.queryByUser({
      userId: $routeParams.id,
      order: 'startAt',
      direction: 'ASC'
    })
      .$promise.then(function(data) {
        vm.myMeetings = data;
        vm.myMeetings.forEach(function(meeting) {
          meeting.startAt = new Date(meeting.startAt);
        });
        vm.dateOptions.initDate = dateForNextMeeting();
      })
      .catch(new ErrorHandler('Misslyckades att hämta mina möten.'))
      .finally(function() {
        vm.meetingsReady = true;
      });
  }

  function hasDayMeeting(date) {
    return vm.myMeetings.filter(function(meeting) {
      return (
        meeting.startAt.getDate() === date.getDate() &&
        meeting.startAt.getMonth() === date.getMonth() &&
        meeting.startAt.getFullYear() === date.getFullYear()
      );
    });
  }

  function dateForNextMeeting() {
    var now = new Date();
    var futureMeetings = vm.myMeetings.filter(function(meeting) {
      return meeting.startAt > now;
    });
    if (futureMeetings.length > 0) {
      // copy the date object so the date picker won't change it behind us
      return new Date(futureMeetings[0].startAt.getTime());
    }
    return now;
  }

  vm.getMyDailyMeetings = getMyDailyMeetings;
  function getMyDailyMeetings(date) {
    vm.toggleMenu = true;
    vm.myDailyMeetings = hasDayMeeting(date);
  }

  vm.addMeeting = function () {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'lg',
      index: 1075, // position above tooltip
      templateUrl: 'resources/meeting/edit/edit-meetings-modal.html',
      controller: 'EditMeetingModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      backdrop: 'static',
      resolve: {
        meetingObj: function() {
          return null;
        },
        negotiationObj: function() {
          return null;
        }
      }
    });
    modalInstance.result.then(() => getMyMeetings());
  }
  //#endregion
}

})();
(function () {
'use strict';

OtherUserSelectModalCtrl.$inject = ["$uibModalInstance", "users"];
angular.module('hyresgastApp').controller('OtherUserSelectModalCtrl', OtherUserSelectModalCtrl);

function OtherUserSelectModalCtrl($uibModalInstance, users) {
  var vm = this;
  vm.users = users;

  vm.select = user => { $uibModalInstance.close(user) };
  vm.cancel = () => { $uibModalInstance.close() };
}
})();
(function () {
'use strict';

ShowUserCtrl.$inject = ["$location", "$routeParams", "Auth", "HyUtils", "User", "Parties", "Negotiation", "RealEstate", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowUserCtrl', ShowUserCtrl);

function ShowUserCtrl($location, $routeParams, Auth, HyUtils, User, Parties, Negotiation, RealEstate, ErrorHandler) {
  var vm = this;
  vm.user = {};
  vm.gotoShowOrganization = gotoShowOrganization;
  vm.gotoShowNegotiation = gotoShowNegotiation;
  vm.parties = Parties;
  vm.exportProperties = exportProperties;

  Auth.heartbeat().then(function() {
    if (parseInt($routeParams.id) === Auth.currentUser().id) {
      vm.meMode = true; // updating my own user
    }
    getUser();
  });

  ////////////

  function getUser() {
    return User.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.user = response;
        getProperties();
        // Convert response.offices object to an array for easily sorting and using length property
        var offices = [];
        for (var office in response.offices) {
          offices.push(response.offices[office]);
        }
        vm.user.offices = offices;
        if (vm.user.party === Parties.EXTERNAL) {
          getOrganizations();
        }
        if (vm.user.party === Parties.DELEGATE) {
          getDelegateParticipantNegotiations();
        }
      })
      .catch(ErrorHandler('Misslyckades att hämta användaren.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function getProperties() {
    RealEstate.queryPropertiesInMyNegotiations({
      id: vm.user.id
    }).$promise.then(properties => {
      vm.user.properties = properties.rows;
    });
  }

  function exportProperties() {
    User.hgfOverview({
      'filters[party]': Parties.HGF,
      enchancements: 'properties',
    }).$promise.then(result => {
      HyUtils.showDefaultModal('OtherUserSelectModalCtrl',
        'resources/user/show/other-user-select-modal.html', {
          users: result.users,
        }, result => {
          User.exportProperties({
            sourceUserId: result.id,
            targetUserId: vm.user.id,
          }).$promise.then(() => getProperties());
        }, null, null, { size: 'md' });
    });
  }

  function getOrganizations() {
    return User.queryOrganization({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.organizations = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta organisation.'))
      .finally(function() {
        vm.readyOrganizations = true;
      });
  }

  function gotoShowOrganization(id) {
    $location.search({});
    $location.path('/organizations/' + id);
  }

  function getDelegateParticipantNegotiations() {
    vm.readyNegotiations = false;
    return Negotiation.query({
      participant: $routeParams.id,
    },
    {}
    )
      .$promise.then(function(data) {
        vm.negotiations = data.rows;
      })
      .catch(ErrorHandler('Misslyckades att hämta förhandlingar.'))
      .finally(function() {
        vm.readyNegotiations = true;
      });
  }

  function gotoShowNegotiation(id) {
    $location.search({});
    $location.path('/negotiations/' + id);
  }
}

})();
(function () {
'use strict';

VerifyEmailCtrl.$inject = ["$scope", "$location", "$routeParams", "Auth", "User", "Flash"];
angular.module('hyresgastApp').controller('VerifyEmailCtrl', VerifyEmailCtrl);

function VerifyEmailCtrl($scope, $location, $routeParams, Auth, User, Flash) {
  var vm = this;
  vm.user = {};
  vm.login = login;
  vm.loginVisible = true;
  vm.credentials = {};

  vm.credentials.token = $routeParams.token;
  vm.credentials.email = $routeParams.email;

  function login() {
    vm.saving = true;
    vm.credentials.password = vm.password;

    User.verifyEmail(vm.credentials)
      .$promise.then(function(user) {
        Auth.setCurrentUser(user);
        $location.search({});
        $location.path('/users/' + user.id + '/welcome');
      })
      .catch(function() {
        Flash.set('Kunde inte logga in. Kontrollera ditt lösenord', 'error');
        vm.password = null;
        vm.credentials.password = null;
      })
      .finally(function() {
        vm.saving = false;
      });
  }
}

})();
(function () {
'use strict';

ShowHelpFaqCtrl.$inject = ["Auth"];
angular.module('hyresgastApp').controller('ShowHelpFaqCtrl', ShowHelpFaqCtrl);

function ShowHelpFaqCtrl(Auth) {
  var vm = this;

  Auth.heartbeat().then(function() {
    vm.ready = true;
  });

  /////////
}

})();
(function () {
'use strict';

SelectBlockingPropertiesCtrl.$inject = ["$scope", "$route", "$routeParams", "$location", "$timeout", "Negotiation", "Blocking", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('SelectBlockingPropertiesCtrl', SelectBlockingPropertiesCtrl);

function SelectBlockingPropertiesCtrl($scope, $route, $routeParams, $location, $timeout, Negotiation,
  Blocking, Flash, ErrorHandler) {
  var vm = this;

  vm.blocking = {
    negotiationId: $routeParams.id,
    blockedBy: $location.search().blockedBy,
    documents: [],
    name: '', // there is a not null constraing on api
    isAllPropertiesSelected: true,
  };

  vm.save = save;

  // Fetch properties when the user wants to sort by column or direction
  $scope.$watchGroup(['vm.sortColumn', 'vm.sortDirection'], function () {
    // Check for undefined 'order' parameter in the URL
    if ($location.search().order) {
      // Reset the offset when changing column or direction of sorting
      vm.pageOffset = 0;
      getProperties();
    }
  });

  vm.pageOffset = 0;
  vm.pageLimit = 30;
  vm.blocking.selectedPropertyIds = {};
  vm.getProperties = getProperties;

  vm.listColumns = [
    { title: 'Fastigheter', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments' }
  ];
  vm.sortColumn = getLocationOrderIfValid(vm.listColumns) || 'propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  getProperties();
  getNegotiation();

  // returns sort column from url parameter ($location.search().order) if it's included in the given
  // list of column definitions; null otherwise
  function getLocationOrderIfValid(allowedColumns) {
    if (
      allowedColumns.some(function (c) {
        return c.column === $location.search().order;
      })
    ) {
      return $location.search().order;
    }
    return null;
  }

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getProperties();
    }
  });

  ////////////

  function save() {
    vm.saving = true;
    Blocking.save(vm.blocking)
      .$promise.then(
        // eslint-disable-next-line no-unused-vars
        function (response) {
          $location.search({}); // remove parameters set by wizard
          $location.path('/negotiations/' + vm.blocking.negotiationId).search('view', 'agreements');
          Flash.set('Fastigheter i förhandlingen har strandats', 'success');
        })
      .catch(new ErrorHandler('Misslyckades att stranda förhandlingen.'))
      .finally(function () {
        vm.saving = false;
      });
  }

  function getNegotiation() {
    return Negotiation.get({
      id: $routeParams.negotiationId
    })
      .$promise.then(function (response) {
        vm.negotiation = response;
      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function () {
        vm.readyNegotiation = true;
      });
  }

  vm.getPropertiesVirtual = () => {
    vm.propertiesVirtual = vm.properties.slice(vm.pageOffset, vm.pageOffset + vm.pageLimit);
  };
  function getProperties(filterMode) {
    vm.ready = false;
    if (filterMode) {
      vm.pageOffset = 0;
    }
    return Negotiation.queryPropertyBySelection(
      {
        id: vm.blocking.negotiationId,
        limit: 2000,
        offset: 0,
        order: vm.sortColumn,
        direction: vm.sortDirection,
        'search[propertyDesignation]': vm.inputPropertiesTextProperty,
        'search[ownerName]': vm.inputPropertiesTextOwner,
        'search[municipality]': vm.inputPropertiesTextMunicipality,
        'search[district]': vm.inputPropertiesTextDistrict,
        include: ['owners']
      },
      {
        unassigned: true,
        selectedDistrictIds: {},
        isAllDistrictsSelected: true,
      }
    )
      .$promise.then(function (response) {
        if (vm.propertiesTotalCount !== response.count) {
          vm.blocking.selectedPropertyIds = {};
        }
        vm.properties = response.rows;
        vm.getPropertiesVirtual();
        vm.propertiesTotalCount = response.count;
      })
      .catch(new ErrorHandler('Misslyckades att hämta fastigheter.'))
      .finally(function () {
        vm.ready = true;
      });
  }
}
})();
(function () {
'use strict';

angular.module('hyresgastApp').controller('ShowHelpProcessCtrl', ShowHelpProcessCtrl);

function ShowHelpProcessCtrl() {
  var vm = this;

  vm.toggleActive = toggleActive;

  /////////

  function toggleActive(state) {
    vm.active = vm.active !== state ? state : null;
  }
}

})();
(function () {
'use strict';

NewFreeAgreementCtrl.$inject = ["$scope", "$routeParams", "$location", "Auth", "Flash", "ErrorHandler", "NegotiationRoles", "FreeAgreement", "FreeAgreementTypes"];
angular.module('hyresgastApp').controller('NewFreeAgreementCtrl', NewFreeAgreementCtrl);

function NewFreeAgreementCtrl(
  $scope, $routeParams, $location, Auth, Flash, ErrorHandler, NegotiationRoles, FreeAgreement,
  FreeAgreementTypes
) {
  var vm = this;

  vm.FreeAgreementTypes = FreeAgreementTypes;
  vm.realEstateChoiceMethod = 'DIRECT_PROPERTIES';
  vm.internal = $routeParams.internal === 'true';

  let initialUser = angular.copy(Auth.currentUser());
  initialUser.userId = initialUser.id;
  initialUser.roleObj = NegotiationRoles.HGF_ASSOC_CHIEF;
  initialUser.isChief = true;

  vm.userContainer = {
    hgfSide: [initialUser],
    faSide: [],
    chiefNegotiatorHGF: initialUser,
    chiefNegotiatorExternal: null,
    invites: []
  };

  vm.setRadioModelByOutsideClick = setRadioModelByOutsideClick;
  vm.gotoNextStep = gotoNextStep;
  vm.save = save;
  vm.saving = false;
  vm.freeAgreementId;

  FreeAgreement.create({}).$promise.then(createResult => {
    // eslint-disable-next-line no-undef
    localStorage.setItem('newAvtalId', createResult.id);
    vm.freeAgreementId = createResult.id;
    vm.needClearGarbage = true;
  });

  $scope.$on("$destroy", function() {
    if (vm.freeAgreementId && vm.needClearGarbage) {
      // eslint-disable-next-line no-undef
      localStorage.removeItem('newAvtalId');
      FreeAgreement.deletePending({ agreementId: vm.freeAgreementId })
    }
  });


  ////////////

  function setRadioModelByOutsideClick(choice) {
    vm.realEstateChoiceMethod = choice;
  }

  function gotoNextStep() {
    vm.gotoNext = true;
  }

  function save() {
    vm.saving = true;
    vm.needClearGarbage = false;
    FreeAgreement.finishCreate({
      id: vm.freeAgreementId,
      userContainer: vm.userContainer,
      type: vm.avtalType.code,
      properties: vm.properties,
      internal: vm.internal,
    // eslint-disable-next-line no-unused-vars
    }).$promise.then(result => {
      $location.search({});
      $location.path('/agreements/' + vm.freeAgreementId);
      Flash.set('Avtal har sparats', 'success');
    }).catch(new ErrorHandler('Misslyckades att skapa avtal.'))
      .finally(function() {
        vm.saving = false;
      });
  }
}

})();
(function () {
'use strict';
angular.module('hyParticipants', ['uiModal'])
  .directive('hyParticipants', ['$uibModal', 'Parties', ($uibModal, Parties) => {
    return {
      scope: {
        userContainer: '=',
        internal: '=',
      },
      restrict: 'E',
      templateUrl: 'resources/agreement/free/participants/hy-participants.html',
      link: function (scope) {

        scope.toggleOwnersList = [false, false, false];
        scope.Parties = Parties;

        scope.edit = (modalSize, userParty) => {
          var modalInstance = $uibModal.open({
            animation: true,
            ariaLabelledBy: 'modal-title-top',
            ariaDescribedBy: 'modal-body-top',
            size: modalSize,
            index: 1075, // position above tooltip
            templateUrl: 'resources/negotiation/edit/edit-representatives-modal.html',
            controller: 'EditNegotiationRepresentativesModalCtrl',
            controllerAs: 'vm',
            windowClass: 'modal-content--white-border',
            resolve: {
              negotiationObject: () => ({
                delegationParticipants: [],
                delegationInvitations: scope.userContainer.invites,
                hgfParticipants: scope.userContainer.hgfSide,
                externalParticipants: scope.userContainer.faSide,
                chiefNegotiatorHGF: scope.userContainer.chiefNegotiatorHGF,
                chiefNegotiatorExternal: scope.userContainer.chiefNegotiatorExternal,
              }),
              isFreeAgreement: () => true,
              userParty: () => userParty
            }
          });
          modalInstance.result.then(result => {
            let setUserId = arr => arr.forEach(el => { el.userId = el.userId ? el.userId : el.id });
            if (userParty === Parties.HGF) {
              scope.userContainer.hgfSide = result.participants;
              scope.userContainer.chiefNegotiatorHGF = result.participants.find(p => p.isChief);
              setUserId(scope.userContainer.hgfSide);
            } else {
              scope.userContainer.faSide = result.participants;
              scope.userContainer.chiefNegotiatorExternal = result.participants.find(p => p.isChief);
              setUserId(scope.userContainer.faSide);
            }
            scope.userContainer.invites = result.waitingInvites;
          });
        };

      },
    };
  }]);
})();
(function () {
'use strict';

EditAgreementRentlistCtrl.$inject = ["$scope", "$location", "$route", "$routeParams", "$filter", "$document", "$timeout", "Negotiation", "Agreement", "RealEstate", "NegotiationTypes", "ApartmentStatusTypes", "FPConsts", "HyUtils", "Flash", "ErrorHandler"];
angular.module('hyresgastApp').controller('EditAgreementRentlistCtrl', EditAgreementRentlistCtrl);

function EditAgreementRentlistCtrl($scope, $location, $route, $routeParams, $filter, $document, $timeout,
  Negotiation, Agreement, RealEstate,
  NegotiationTypes, ApartmentStatusTypes, FPConsts,
  HyUtils, Flash, ErrorHandler) {
  const S = ApartmentStatusTypes.STUCK.code;
  const M10 = ApartmentStatusTypes.M10.code;
  var vm = this;
  vm.getApartments = getApartments;
  vm.agreement = {
    negotiationId: $routeParams.negotiationId,
    id: $routeParams.id,
    documents: []
  };
  vm.selectedApartmentIds = {};
  vm.apartments = [];
  // statuses, that can be changed
  vm.validStatusTypes = FPConsts.agreeableApartmentStatuses;

  // statuses, that should be seen for negotiation
  vm.filterStatusTypes = [{ code: -1, name: 'Utan status' }]
    .concat(FPConsts.apartmentStatusesForNegotation);

  let index = 0;
  vm.filterStatusTypes.forEach(x => x.id = index++);
  vm.inputStatuses = [...vm.filterStatusTypes];
  vm.listColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Fastighet', column: 'property.propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'Normhyra', column: 'nh' },
    { title: 'Hyra (kr/månad) nu', column: 'currentMonthlyRent' },
    { title: 'Strandad nu', column: 'currentIsBlocked' },
    { title: 'Förändring/mån', column: 'changeDescription' }
  ];
  vm.nh = {
    items: [
      { id: 'nh91', text: '-91' },
      { id: 'nh78', text: '-78' },
      { id: 'nh12', text: '-12' },
    ],
  };
  vm.nh.current = vm.nh.items[0];
  vm.sortColumn = vm.listColumns.some(c => c.column === $location.search().order)
    ? $location.search().order
    : 'currentIsBlocked'; // default
  vm.sortDirection = $location.search().direction || 'DESC'; // default
  vm.pageLimit = 30;
  vm.offset = 0;
  vm.firstRecordInCurrentPage = 1;
  vm.isDirty = false;

  $scope.$on('$routeUpdate', () => {
    if ($location.search().order) {
      // reset offset when sorting
      vm.offset = 0;
      getApartments();
    }
  });

  function getNegotiation() {
    return Negotiation.get({
      id: $routeParams.negotiationId
    })
      .$promise.then(function(response) {
        vm.isNewConstruction = response.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY
        || response.type === NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT;

      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function() {
        vm.readyNegotiation = true;
      });
  }

  vm.filterNH = () => {
    let focus = element => {
      if (element) {
        element.focus();
        element.select();
      }
    };
    if (!vm.inputNHMin && vm.inputNHMax) {
      focus($document[0].getElementById('inputNHMin'));
    } else if (!vm.inputNHMax && vm.inputNHMin) {
      focus($document[0].getElementById('inputNHMax'));
    } else {
      getApartments();
    }
  };

  function getApartments(readyCb) {
    vm.readyApartments = false;
    Agreement.queryApartment({
      negotiationId: $routeParams.negotiationId,
      id: $routeParams.id,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'search[nationalNo]': vm.inputTextNationalApartmentNo,
      'search[ownerNo]': vm.inputTextApartmentNo,
      'search[streetAddress]': vm.inputTextStreetAddress,
      'search[property.propertyDesignation]': vm.inputTextPropertyDesignation,
      'search[municipality]': vm.inputTextMunicipality,
      'filters[rooms]': vm.inputTextRooms,
      'filters[livingSpace]': vm.inputTextLivingSpace,
    }).$promise.then(response => {
      vm.offset = 0;
      vm.apartments = response.rows;

      vm.apartments.forEach(a => {
        vm.selectedApartmentIds[a.id] = true;
        a.currentIsBlocked = (a.currentStatus & S) === S;
        if (!a.status) {
          a.status = a.currentStatus;
        }
      });

      getNewNH(() => {

        if (vm.inputNHMin && vm.inputNHMax) {
          vm.apartments = vm.apartments.filter(a => {
            let fitInCurrent = (a[vm.nh.current.id] >= vm.inputNHMin) && (a[vm.nh.current.id] <= vm.inputNHMax);
            let fitInProposed = (a[vm.nh.id] >= vm.inputNHMin) && (a[vm.nh.id] <= vm.inputNHMax);
            return fitInCurrent || fitInProposed;
          });
        }
        const checkStatus = status => {
          let ok = false;
          if (vm.inputStatuses.find(x => x.code === -1)) {
            ok = FPConsts.apartmentStatusesForNegotation.every(x => (status & x.code) !== x.code);
          }
          if (vm.inputStatuses.find(x => x.code !== -1)) {
            ok |= !!vm.inputStatuses.find(x => (status & x.code) === x.code);
          }
          return ok;
        };
        if (vm.inputStatuses.length) {
          vm.apartments = vm.apartments.filter(a => checkStatus(a.status) || checkStatus(a.currentStatus));
        }

        let specialSortColumn = ['currentIsBlocked', 'nh'].find(x => x === vm.sortColumn);
        if (specialSortColumn) {
          let desc = vm.sortDirection !== 'ASC';
          specialSortColumn = specialSortColumn === 'nh' ? vm.nh.current.id : specialSortColumn;
          vm.apartments = vm.apartments.sort((a, b) => desc
            ? (b[specialSortColumn] - a[specialSortColumn])
            : (a[specialSortColumn] - b[specialSortColumn]));
        }

        vm.apartmentsTotalCount = response.count;
        vm.allApartmentsBoxChecked = true;
        vm.isDirty = false;
        vm.hasNewRents = vm.apartments.some(a => a.newMonthlyRent);
        vm.hasNewStatuses = vm.apartments.some(a => a.currentStatus !== a.status);
        if (readyCb) {
          readyCb();
        } else {
          vm.setSlice();
        }
      });
    }).catch(new ErrorHandler('Misslyckades att hämta fastighetsbestånd'))
      .finally(() => { vm.readyApartments = true });
  }

  vm.setSlice = () => {
    vm.apartmentsSlice = vm.apartments.slice(vm.offset, vm.offset + vm.pageLimit);
  };

  vm.paginatorGetApartments = () => {
    if (!vm.readyApartments) {
      getApartments();
    } else {
      vm.setSlice();
    }
  };

  vm.toggleAllApartmentsSelection = () => {
    vm.apartments.forEach(apartment => {
      if (vm.selectedApartmentIds[apartment.id] !== null) {
        vm.selectedApartmentIds[apartment.id] = vm.allApartmentsBoxChecked;
      }
    });
  };

  vm.updateAllPropertiesBoxState = () => {
    // auto select master checkbox if all other checkboxes are selected
    // deselect master checkbox if at least one checkbox is not selected
    vm.allApartmentsBoxChecked = Object.keys(vm.selectedApartmentIds)
      .map(apartmentId => vm.selectedApartmentIds[apartmentId] === true || vm.selectedApartmentIds[apartmentId] === null)
      .every(isSelected => isSelected);
  };

  vm.atLeastOneSelectedApartmentId = () => Object.keys(vm.selectedApartmentIds)
    .map(apartmentId => vm.selectedApartmentIds[apartmentId] === true)
    .some(isSelected => isSelected);


  let getSelectedRows = () => vm.apartments.filter(apartment => vm.selectedApartmentIds[apartment.id] === true);

  let isS = status => status ? ((status & S) === S) : false;
  vm.countSelectedS = () => getSelectedRows()
    .filter(x => isS(x.status)).length;
  vm.canSetRent = () => {
    //3. if apartment has S - rent setting is not possible
    let hasS = getSelectedRows().some(x => isS(x.status));
    return !hasS;
  };
  vm.canSetStatus = () => {
    let hasNewRent = getSelectedRows()
      .some(x => x.newMonthlyRent && (x.newMonthlyRent !== x.currentMonthlyRent));
    return !hasNewRent;
  };

  let getNewYearlyRent = apartment => apartment.newMonthlyRent * (((apartment.status & M10) === M10) ? 10 : 12);

  let getNewNH = readyCb => {
    RealEstate.calculateNH(vm.apartments.map(a => ({
      id: a.id,
      rooms: a.rooms,
      livingSpace: a.livingSpace,
      kitchenType: a.kitchenType,
      yearlyRent: getNewYearlyRent(a),
    }))).$promise.then(response => {
      vm.apartments.forEach(a => a.newNH = response.rows.find(r => r.id === a.id));
      if (readyCb) {
        readyCb();
      }
    });
  };

  vm.editRentalLevels = () => {
    let plusDipstick = (desc, dipstick) => (desc ? `${desc} +` : '') + dipstick;
    let plusDipstickZero = (desc, dipstick) => desc && (desc.includes('+0 %') || desc === dipstick)
      ? desc
      : plusDipstick(desc, dipstick);
    let percentMultiplier = percent => 1 + (parseFloat(percent) / 100);
    let setZero = apartment => {
      angular.extend(apartment, {
        newMonthlyRent: apartment.currentMonthlyRent,
        changeDescription: plusDipstickZero(apartment.changeDescription, `${0} %`),
      });
    };

    let selectedRows = getSelectedRows();
    let nhRange = { min: 9999999, max: -99999999, table: vm.nh.current.text };
    selectedRows.forEach(row => {
      nhRange.min = Math.min(nhRange.min, row[vm.nh.current.id]);
      nhRange.max = Math.max(nhRange.max, row[vm.nh.current.id]);
    });
    if (vm.countSelectedS() === selectedRows.length) {
      selectedRows.forEach(apartment => setZero(apartment));
      vm.isDirty = true;
      getNewNH();
      return;
    }
    HyUtils.showDefaultModal('EditRentalLevelModalCtrl',
      'resources/agreement/rentlist/edit/edit-rental-level-modal.html', {
        isNewConstruction: vm.isNewConstruction,
        nhRange
      }, result => {
        selectedRows.forEach(apartment => {
          if (isS(apartment.status)) {
            setZero(apartment);
            return;
          }
          switch (result.changeMethod) {
            case 0: //percent
              var percent = result.percent;
              if (result.percentByNhRange) {
                let rangeMatch = result.percentByNhRange.find(r => (apartment[vm.nh.current.id] >= r.min) &&
                  (apartment[vm.nh.current.id] <= r.max));
                if (rangeMatch) {
                  percent = rangeMatch.number;
                } else {
                  return;
                }
              }
              var multiplier = percentMultiplier(percent);
              angular.extend(apartment, {
                newMonthlyRent: apartment.newMonthlyRent
                  ? apartment.newMonthlyRent * multiplier
                  : apartment.currentMonthlyRent * multiplier,
                changeDescription: plusDipstick(apartment.changeDescription, `${percent} %`)
              });
              break;
            case 1: //kr
              if (result.isRelative) {
                angular.extend(apartment, {
                  newMonthlyRent: apartment.newMonthlyRent
                    ? apartment.newMonthlyRent + result.monthly
                    : apartment.currentMonthlyRent + result.monthly,
                  changeDescription: plusDipstick(apartment.changeDescription, `${result.monthly} kr`)
                });
              } else {
                angular.extend(apartment, {
                  newMonthlyRent: result.monthly,
                  changeDescription: ''
                });
              }
              break;
            case 2: //krm2
              // TODO: consider using unificated math function for rounding
              var rent = Math.round((result.monthly * apartment.livingSpace + Number.EPSILON) * 100) / 100;
              if (result.isRelative) {
                angular.extend(apartment, {
                  newMonthlyRent: apartment.newMonthlyRent
                    ? apartment.newMonthlyRent + rent
                    : apartment.currentMonthlyRent + rent,
                  changeDescription: plusDipstick(apartment.changeDescription, `${rent} kr`)
                });
              } else {
                angular.extend(apartment, {
                  newMonthlyRent: rent,
                  changeDescription: ''
                });
              }
              break;
          }
        });
        getNewNH();
        vm.isDirty = true;
        $timeout(() => $scope.$apply(), 200);
      }, null, null, { size: 'md' });
  };

  vm.editComment = apartment => {
    HyUtils.showDefaultModal('EditCommentModalCtrl',
      'resources/agreement/rentlist/edit/edit-comment-modal.html',
      { comment: apartment.comment, multipleComments: '' },
      result => {
        if (result.delete || result.comment) {
          apartment.comment = result.delete ? '' : result.comment;
          vm.isDirty = true;
        }
      }, null, null,
      { size: 'md' });
  };

  vm.massComment = () => {
    // eslint-disable-next-line no-undef
    let comments = new Set(vm.apartments.map(a => a.comment).filter(a => a));
    let comment = comments.size === 1 ? Array.from(comments)[0] : '';
    let multipleComments = comments.size > 1 ? Array.from(comments).join('; ') : null;
    HyUtils.showDefaultModal('EditCommentModalCtrl',
      'resources/agreement/rentlist/edit/edit-comment-modal.html',
      { comment, multipleComments },
      result => {
        if (result.delete || result.comment) {
          let selectedRows = getSelectedRows();
          let newComment = result.delete ? '' : result.comment;
          selectedRows.forEach(apartment => { apartment.comment = newComment });
          vm.isDirty = true;
        }
      }, null, null,
      { size: 'md' });
  }

  vm.editObjects = () => {
    let selectedRows = getSelectedRows();
    selectedRows.forEach(a => a.oldSum = vm.getExtraObjectsCost(a.objectsExtra));
    // eslint-disable-next-line no-undef
    let currentObjects = new Set(selectedRows.map(a => a.currentObjectsExtra).filter(a => a));
    // eslint-disable-next-line no-undef
    let proposedObjects = new Set(selectedRows.map(a => a.objectsExtra).filter(a => a));
    let params = {
      objects: proposedObjects.size === 1 ? JSON.parse([...proposedObjects][0], HyUtils.dtRev) : [],
      oldObjects: currentObjects.size === 1 ? JSON.parse([...currentObjects][0], HyUtils.dtRev) : [],
    };
    HyUtils.showDefaultModal('EditObjectsModalCtrl',
      'resources/agreement/rentlist/edit/edit-objects-modal.html', params,
      result => {
        if (result.delete) {
          selectedRows.forEach(apartment => {
            apartment.newMonthlyRent -= apartment.oldSum;
            apartment.objectsExtra = null;
          });
        } else {
          selectedRows.forEach(apartment => {
            apartment.objectsExtra = JSON.stringify(result.objects);
            let newSum = vm.getExtraObjectsCost(apartment.objectsExtra);
            if (!apartment.newMonthlyRent || apartment.newMonthlyRent === 0) {
              apartment.newMonthlyRent = apartment.currentMonthlyRent;
            }
            apartment.newMonthlyRent -= apartment.oldSum;
            apartment.newMonthlyRent += newSum;
            delete apartment.oldSum;
          });
        }
        vm.isDirty = true;
      });
  };

  vm.editStatus = () => {
    let selectedRows = getSelectedRows();
    HyUtils.showDefaultModal('EditStatusModalCtrl', 'resources/agreement/rentlist/edit/edit-status-modal.html', {
      validStatuses: vm.validStatusTypes,
      currentStatuses: selectedRows.map(x => x.status)
    }, result => {
      selectedRows.forEach(row => {
        if (result.set) {
          row.status |= result.code;
        }
        if (result.reset) {
          row.status &= ~result.code;
        }
        if ((result.set || result.reset) && !row.newMonthlyRent) {
          row.newMonthlyRent = row.currentMonthlyRent;
        }
      });
      vm.isDirty = true;
    }, null, null, { size: 'sm' });
  };

  vm.revert = () => {
    getApartments();
    vm.isDirty = false;
  };

  vm.startOver = (isCorrection) => {
    let selectedApartments = vm.apartments.filter(a => vm.selectedApartmentIds[a.id] === true);
    selectedApartments.forEach(a => {
      a.newMonthlyRent = isCorrection ? a.currentMonthlyRent : null;
      a.changeDescription = null;
      a.status = a.currentStatus;
      a.objectsExtra = a.currentObjectsExtra;
    });
    if (selectedApartments.length) {
      vm.isDirty = false;
      vm.save();
    }
  };

  vm.startOverCorrection = () => {
    let selectedApartments = vm.apartments.filter(a => vm.selectedApartmentIds[a.id] === true);
    let params = (selectedApartments.length === vm.apartments.length)
      ? { agreementId: vm.agreement.id }
      : {
        agreementId: vm.agreement.id,
        apartmentIds: selectedApartments.map(x => x.id),
      };
    Agreement.resetCorrection(params).$promise.then(() => { $route.reload() });
  };

  vm.save = () => {
    vm.saving = true;
    Agreement.editRentlist({
      negotiationId: $routeParams.negotiationId,
      id: $routeParams.id,
    }, { apartments: vm.apartments, pako: true })
      .$promise.then(() => Flash.set('Lyckades att spara överenskommelse.', 'success'))
      .catch(new ErrorHandler('Misslyckades att spara överenskommelse.'))
      .finally(() => {
        getApartments(() => {
          vm.offset = 0;
          vm.firstRecordInCurrentPage = 1;
          vm.saving = false;
          vm.isDirty = false;
          vm.paginatorGetApartments();
        });
      });
  };

  /**
   * get fancy monthly rent
   * @param {object} apartment
   * @returns {string} fancy monthly rent
   */
  vm.getMonthlyRent = (apartment, raw) => {
    let rent;
    if (apartment.currentMonthlyRent) {
      rent = apartment.currentMonthlyRent;
    } else {
      if (apartment.currentYearlyRent) {
        const monthlyRent = apartment.currentYearlyRent / 12;
        apartment.currentMonthlyRent = monthlyRent;
        rent = monthlyRent;
      } else {
        rent = Number.NaN;
      }
    }

    let text = Number.isNaN(rent) ? 'Ej angivet' : $filter('number')(rent, 0);
    return raw ? rent : text;
  };

  /**
   * counts sum of all extras
   * @param {string} objectsExtraJson
   * @returns {number}
   */
  vm.getExtraObjectsCost = objectsExtraJson => {
    try {
      let objects = JSON.parse(objectsExtraJson);
      return objects.map(o => o.value).reduce((acc, el) => (acc += el), 0);
    } catch (e) {
      return 0;
    }
  };

  //START
  getNegotiation();
  vm.paginatorGetApartments();
}

})();
(function () {
'use strict';

EditCommentModalCtrl.$inject = ["$uibModalInstance", "comment", "multipleComments"];
angular.module('hyresgastApp').controller('EditCommentModalCtrl', EditCommentModalCtrl);

function EditCommentModalCtrl($uibModalInstance, comment, multipleComments) {
  var vm = this;

  vm.multipleComments = multipleComments;
  vm.hadSomething = !!comment || multipleComments;
  vm.comment = comment;

  vm.delete = () => $uibModalInstance.close({ delete: true });
  vm.ok = () => $uibModalInstance.close({ comment: vm.comment });
  vm.cancel = () => $uibModalInstance.dismiss();
}
})();
(function () {
'use strict';

EditObjectsModalCtrl.$inject = ["$uibModalInstance", "objects", "oldObjects", "RealEstate"];
angular.module('hyresgastApp').controller('EditObjectsModalCtrl', EditObjectsModalCtrl);

function EditObjectsModalCtrl($uibModalInstance,
  objects, oldObjects,
  RealEstate) {
  var vm = this;

  RealEstate.getObjects({}).$promise.then(result => {
    vm.objectRoot = {
      label: 'STANDARD FÖRÄNDRING',
      subitems: result.objects.map(x => ({
        label: x.name,
        data: x.code,
        subitems: x.objects.map(x => ({
          label: x.name,
          data: x.code,
        })),
      })),
    };
  });

  const sum = money => money.reduce((acc, el) => { acc += el; return acc }, 0);
  vm.recalculate = () => {
    vm.sumOld = sum(vm.objects.filter(x => !x.isNew).map(o => o.value));
    vm.sum = sum(vm.objects.map(o => o.value));
  };

  let sortObjects = () => {
    vm.objects = vm.objects.sort((a, b) =>
      b.isNew === a.isNew
        ? a.code.localeCompare(b.code)
        : b.isNew ? -1 : 1
    );
    vm.objects.forEach(x => x.separator = false);
    let firstNew = vm.objects.find(x => x.isNew);
    if (firstNew) {
      firstNew.separator = true;
    }
  };

  vm.objects = objects.map(x => Object.assign({
    isNew: !oldObjects.find(o => o.code === x.code),
    quantity: x.quantity ? x.quantity : 1,
  }, x));
  sortObjects();
  vm.canDelete = !!objects && objects.length > 0;
  vm.recalculate();

  vm.addObjects = () => {
    let newObjects = vm.treeViewNodes
      .filter(x => x.level > 1)
      .filter(x => !vm.objects.find(o => o.code === x.data))
      .map(x => ({
        code: x.data,
        name: x.label,
        value: 0,
        quantity: 1,
        isNew: !oldObjects.find(o => o.code === x.data),
      }));
    vm.objects.push(...newObjects);
    sortObjects();
  };

  vm.deleteObject = object => {
    vm.objects = vm.objects.filter(o => o !== object);
    vm.recalculate();
  };

  vm.delete = () => $uibModalInstance.close({ delete: true });
  vm.ok = () => $uibModalInstance.close({
    objects: vm.objects.map(o => ({
      code: o.code,
      name: o.name,
      parameter: o.parameter,
      value: o.value,
      quantity: o.quantity,
    }))});
  vm.cancel = () => $uibModalInstance.dismiss();
}
})();
(function () {
'use strict';

EditRentalLevelModalCtrl.$inject = ["$uibModalInstance", "$timeout", "$document", "PortalSettings", "isNewConstruction", "nhRange"];
angular.module('hyresgastApp').controller('EditRentalLevelModalCtrl', EditRentalLevelModalCtrl);

function EditRentalLevelModalCtrl($uibModalInstance, $timeout, $document,
  PortalSettings,
  isNewConstruction, nhRange) {
  var vm = this;
  vm.nhRange = nhRange;

  vm.ranges = [{ min: nhRange.min }];

  vm.percentMode = 'single';

  PortalSettings.getSuggestedRentByNHRange(ranges => {
    if (ranges && ranges.length) {
      let rangeMatch = ranges.find(r => nhRange.min >= r.min && nhRange.max <= r.max);
      if (rangeMatch) {
        vm.suggestedPercent = rangeMatch.number;
      }
    }
  });

  vm.tabs = isNewConstruction ? [] : [
    { title: 'Förändring', key:'relative' },
    { title: 'Ny hyra', key:'absolute' },
  ];

  vm.currentTab = isNewConstruction ? 'absolute' : 'relative';

  vm.months = 12;
  vm.toggleMonths = () => {
    vm.months = vm.months === 12 ? 10 : 12;
    vm.setMonthly();
  };

  var percentTimer = null;
  vm.setPercent = () => {
    if (percentTimer) {
      $timeout.cancel(percentTimer);
    }
    percentTimer = $timeout(() => {
      let el = $document[0].getElementById('percent');
      if (el) {
        let value = el.value;
        vm.percent = parseFloat(parseFloat(value).toFixed(2));
        if (isNaN(vm.percent)) {
          vm.percent = value;
        }
      }
    }, 1000);
  };

  vm.focusOn = () => {
    $timeout(() => {
      let el = $document[0].getElementById(vm.changeMethod === '0' ? 'percent' : 'monthly');
      if (el) {
        el.focus();
      }
    }, 300);
  }

  vm.setYearly = () => {
    // TODO: consider using unificated math function for rounding
    vm.monthly = Math.round((vm.yearly / vm.months + Number.EPSILON) * 100) / 100;
    vm.percent = null;
  };
  vm.setMonthly = () => {
    // TODO: consider using unificated math function for rounding
    vm.yearly = Math.round((vm.monthly * vm.months + Number.EPSILON) * 100) / 100;
    vm.percent = null;
  };

  let isRangeValid = range => range.min && range.max && range.number;
  vm.addRange = () => {
    if (!vm.ranges) {
      vm.ranges = [{ min: nhRange.min }];
      return;
    }
    let lastRow = vm.ranges[vm.ranges.length-1];
    if (isRangeValid(lastRow)) {
      vm.ranges.push({ min: lastRow.max + 1 });
    }
  };
  vm.deleteRange = range => { vm.ranges = vm.ranges.filter(x => x !== range) };

  vm.okToSave = () => {
    let changeMethod = parseInt(vm.changeMethod);
    switch (changeMethod) {
      case 0:
        var singlePercent = typeof(vm.percent) === "number" && vm.percent >= 0 && vm.percent <= 100;
        var range = vm.ranges && vm.ranges.every(range => isRangeValid(range));
        return vm.percentMode === 'single' ? singlePercent : range;
      case 1:
      case 2:
        return vm.monthly && vm.yearly;
    }
  };
  vm.ok = () => {
    $uibModalInstance.close({
      isRelative: vm.currentTab === 'relative',
      changeMethod: parseInt(vm.changeMethod),
      percent: vm.percentMode === 'single' ? vm.percent : null,
      percentByNhRange: vm.percentMode === 'nhrange' ? vm.ranges : null,
      yearly: vm.yearly,
      monthly: vm.monthly,
      months: vm.months,
    });
  }
  vm.cancel = () => { $uibModalInstance.dismiss(); };
}
})();
(function () {
'use strict';

EditStatusModalCtrl.$inject = ["$uibModalInstance", "validStatuses", "currentStatuses"];
angular.module('hyresgastApp').controller('EditStatusModalCtrl', EditStatusModalCtrl);

function EditStatusModalCtrl($uibModalInstance, validStatuses, currentStatuses) {
  var vm = this;

  let checkStatus = (statusObj, status) => (status & statusObj.code) === statusObj.code;
  vm.statuses = validStatuses.map(x => ({
    name: x.name,
    code: x.code,
    canSet: !currentStatuses.every(s => checkStatus(x, s)),
    canReset: currentStatuses.some(s => checkStatus(x, s)),
    setCount: currentStatuses.filter(s => checkStatus(x, s)).length,
  }));

  vm.set = code => $uibModalInstance.close({ set: true, code });
  vm.reset = code => $uibModalInstance.close({ reset: true, code });
  vm.cancel = () => $uibModalInstance.dismiss();
}
})();
(function () {
'use strict';

ShowFreeAgreementPropertiesCtrl.$inject = ["$routeParams", "$location", "$document", "$timeout", "RealEstate", "FreeAgreement", "Flash", "Help", "HyUtils", "ErrorHandler", "Limits"];
angular.module('hyresgastApp').controller('ShowFreeAgreementPropertiesCtrl', ShowFreeAgreementPropertiesCtrl);

function ShowFreeAgreementPropertiesCtrl($routeParams, $location, $document, $timeout,
  RealEstate, FreeAgreement,
  Flash, Help, HyUtils, ErrorHandler,
  Limits) {
  var vm = this;
  vm.Limits = Limits;
  vm.agreementId = $routeParams.id;
  vm.ready = true;
  vm.properties = [];
  vm.toggleOwnersList = {};

  vm.sortColumn = $location.search().order || 'propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  vm.tableColumns = [
    {
      title: 'Fastigheter',
      column: 'propertyDesignation',
    },
    {
      title: 'Fastighetsägare',
      column: 'owners',
      type: 'array',
    },
    {
      title: 'Kommun',
      column: 'municipality',
    },
    {
      title: 'LM distrikt',
      column: 'district',
    },
    {
      title: 'Antal lägenheter',
      column: 'numNegotiatedApartments',
    },
  ];

  vm.showHelpProperties = (title, bodyText) => Help.renderHelpModal(title, bodyText);
  vm.searchProperties = searchProperties;
  vm.addProperty = addProperty;

  /**
   * retrieves agreement
   * @param {number} agreementId
   */
  function getAgreement(agreementId) {
    FreeAgreement.get({
      agreementId,
      id: agreementId,
    }).$promise.then(function (response) {
      vm.agreement = response.agreement ? response.agreement : response;
      vm.readonly = vm.agreement.agreedByOwner || vm.agreement.agreedByAssociation;
    })
      .catch(new ErrorHandler('Misslyckades att hämta överenskommelse.'))
      .finally(function () {
        vm.ready = true;
      });
  }

  /**
   * retrieves properties for agreement
   * @param {Object} params
   */
  vm.getProperties = (params) => {
    params.agreementId = vm.agreementId;
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      FreeAgreement.queryProperty(params)
        .$promise.then(response => {
          resolve(response.result);
        }).catch(new ErrorHandler('Misslyckades att hämta fastigheter.'));
    });
  };

  getAgreement(vm.agreementId);

  let getTableau = () => angular.element($document[0].querySelector("#tableau"));
  let setTableFGetData = () => {
    vm.tableFGetData = vm.getProperties;
    getTableau().scope().$broadcast('refresh');
  }

  vm.agreementPropertyActions = [
    {
      btnClass: 'btn--icon btn-fu btn--icon-bin',
      action: () => {
        vm.removeProperty(vm.selectedProperties);
      }
    },
    {
      title: 'Lägg till fler',
      common: true,
      isVisible: () => !(!vm.ready || vm.readonly),
      action: () => {
        vm.editPropertiesMode = true;
      }
    }
  ];

  vm.checkboxTicked = () => $timeout(setTableFGetData, 300);
  setTableFGetData();

  vm.gotoProperty = property => {
    $location.search({});
    $location.path('/properties/' + property.id);
  };

  vm.removeProperty = properties => {
    let message = 'Är du säker?';

    HyUtils.showDefaultModal('ConfirmationModalWithMessageCtrl',
      'components/modal/confirmation/confirmationModalWithMessage.html',
      // eslint-disable-next-line no-unused-vars
      { message }, result => {
        FreeAgreement.deleteProperties({
          agreementId: vm.agreementId,
          propertyIds: properties.map(x => x.id),
        // eslint-disable-next-line no-unused-vars
        }).$promise.then(response => {
          getTableau().scope().$broadcast('refresh');
        });
      }, null, null, { size: 'xsm' });
  };

  vm.saveProperties = () => {
    vm.saving = true;
    FreeAgreement.mergeProperties({
      agreementId: vm.agreementId,
      properties: vm.properties,
    // eslint-disable-next-line no-unused-vars
    }).$promise.then(result => {
      Flash.set('Avtal har sparats', 'success');
      getTableau().scope().$broadcast('refresh');
      vm.editPropertiesMode = false;
    }).catch(new ErrorHandler('Misslyckades att spara fastigheter.'))
      .finally(function() {
        vm.saving = false;
      });
  }

  function searchProperties() {
    if (vm.inputTextComplex) {
      vm.toggleMenu = false;
      vm.searching = true;
      return RealEstate.querySimple({ 'search[complex]': vm.inputTextComplex }, {})
        .$promise.then(function(response) {
          vm.propertiesTypeahead = response.rows;
          if (vm.propertiesTypeahead.length > 0) {
            vm.toggleMenu = true;
          }
        })
        .catch(new ErrorHandler('Fel'))
        .finally(function() {
          vm.searching = false;
        });
    }
  }

  function addProperty(property) {
    var foundProperty = vm.properties.filter(function(item) {
      return item.id === property.id;
    });

    // look through all properties to check if they had been added before or not
    if (foundProperty.length === 0) {
      vm.properties.push(property);
    }
  }

}
})();
(function () {
'use strict';

NewAgreementRentlistCtrl.$inject = ["$scope", "$location", "$routeParams", "$uibModal", "$timeout", "Agreement", "Auth", "Flash", "HyUtils", "ErrorHandler"];
angular.module('hyresgastApp').controller('NewAgreementRentlistCtrl', NewAgreementRentlistCtrl);

function NewAgreementRentlistCtrl($scope, $location, $routeParams, $uibModal, $timeout,
  Agreement, Auth, Flash, HyUtils, ErrorHandler) {
  var vm = this;
  vm.agreement = {
    negotiationId: $routeParams.negotiationId,
    id: $routeParams.id,
    documents: []
  };

  vm.showAdditionalInfoPane = false;
  vm.save = save;
  vm.toggleAdditionalInfoPane = toggleAdditionalInfoPane;
  vm.onPending = onPending;
  vm.gotoNextStep = gotoNextStep;
  vm.getColumnTitle = title => title instanceof Array ? title[0] : title.split(',')[0].trim();

  // The wizard uses a file upload directive and the directive is validating the content
  // of uploaded files (excel). If the file upload directive receives errors from the backend
  // we want to open a modal to show the errors to the user
  $scope.$watch('vm.validationResponse', function(validationResponse) {
    if (validationResponse) {
      vm.status = validationResponse;
      if (vm.validationResponse.status === 'ok') {
        vm.status.phase = 'Success';
        vm.status.comment = '';
        vm.inputRentListBlobId = vm.agreement.rentListBlobId;
      } else if (vm.validationResponse.status === 'error') {
        vm.uploader.cancelUpload();
        HyUtils.showDefaultModal('ErrorModalCtrl', 'resources/validation/errors.html', {
          validationResponse, realEstateChoiceMethod: null,
        });
      } else {
        vm.uploader.cancelUpload();
      }
    } else {
      vm.status = {
        phase: 'read excel file',
        total: 0,
        errors: 0,
        warnings: 0,
        progress: 0,
        showProgress: false,
      };
    }
  });

  ////////////
  function toggleAdditionalInfoPane() {
    vm.showAdditionalInfoPane = !vm.showAdditionalInfoPane;
  }

  function onPending(validation) {
    if (vm.status.showProgress && vm.status.progress > 0 && vm.status.phase !== validation.phase) {
      vm.status.progress = 100;
      $timeout(() => {
        vm.status.progress = 0;
        vm.status.phase = validation.phase;
        vm.status.comment = validation.comment;
        $timeout(() => vm.status = validation, 500);
      }, 300);

    } else {
      vm.status = validation;
    }
  }

  function save() {
    vm.saving = true;
    Agreement.importRentlist(vm.agreement)
      .$promise.then(
        // eslint-disable-next-line no-unused-vars
        function(response) {
          $location.search({}); // remove parameters set by wizard
          $location.path('/negotiations/' + vm.agreement.negotiationId + '/agreements/' + vm.agreement.id);
          Flash.set('Hyreslista har laddats upp', 'success');
        })
      .catch(new ErrorHandler('Misslyckades att ladda upp hyreslista.'))
      .finally(function() {
        vm.saving = false;
      });
  }

  function gotoNextStep() {
    vm.gotoNext = true;
  }
}

})();
(function () {
'use strict';

ShowAgreementDocumentsCtrl.$inject = ["$scope", "$routeParams", "$uibModal", "Flash", "HyUtils", "ErrorHandler", "AttachmentTypes", "ValidFileTypes", "FPConsts"];
angular.module('hyresgastApp').controller('ShowAgreementDocumentsCtrl', ShowAgreementDocumentsCtrl);

function ShowAgreementDocumentsCtrl($scope, $routeParams, $uibModal,
  Flash, HyUtils, ErrorHandler,
  AttachmentTypes, ValidFileTypes, FPConsts) {
  var vm = this;
  vm.FPConsts = FPConsts;

  vm.attachmentTypes = AttachmentTypes;
  vm.deleteAgreementFile = deleteAgreementFile;
  vm.addAgreementFile = addAgreementFile;
  vm.downloadAgreementFile = downloadAgreementFile;

  var parentCtrlVM = $scope.$parent.vm;

  /////////

  function addAgreementFile() {
    let negotiationId = $routeParams.negotiationId;
    let agreementId = $routeParams.id;
    let settings = {
      title: 'Ladda upp ett dokument',
      uploadUrl: negotiationId
        ? `/api/negotiations/${negotiationId}/agreements/${agreementId}/documents`
        : `/api/agreements/${agreementId}/documents`,

      // valid extension to upload
      validFileTypes: ValidFileTypes,

      // a file is associated with one type
      types: [vm.attachmentTypes.AGREEMENT_ATTACHMENT,
        vm.attachmentTypes.NEGOTIATION_ATTACHMENT,
        vm.attachmentTypes.DRAFT_AGREEMENT],

      typeTitle: 'Välj typ av underlag'
    };

    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'components/file-upload-ex-modal/file-upload-ex-modal.html',
      controller: 'FileUploadExModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      resolve: {
        settings: function() {
          return settings;
        }
      }
    });

    modalInstance.result.then(function(result) {
      parentCtrlVM.getDocuments(parentCtrlVM.negotiation.id, parentCtrlVM.agreement.id);
      if (result.type === 12) {
        parentCtrlVM.agreement.blobName = result.name;
        parentCtrlVM.agreement.fileName = result.filename;
        parentCtrlVM.agreement.fileUploadedByUserParty = result.agreementObj ? result.agreementObj.fileUploadedByUserParty : null;
        Flash.set('Lyckades lägga till signerad överenskommelse', 'success');
      }
    });
  }

  // removes the signed uploaded file and sets signed bool to false
  function deleteAgreementFile(file) {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'xsm',
      templateUrl: 'components/modal/confirmation/confirmationModal.html',
      controller: 'ConfirmationModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--secondary'
    });

    modalInstance.result.then(function() {
      parentCtrlVM.Resource.deleteDocument({
        negotiationId: $routeParams.negotiationId,
        agreementId: $routeParams.id,
        id: file.name
      })
        .$promise.then(function(response) {
          let index = parentCtrlVM.agreement.documents.indexOf(file);
          parentCtrlVM.agreement.documents.splice(index, 1);
          if (file.type === 12) {
            parentCtrlVM.agreement.signed = false;
            parentCtrlVM.agreement.blobName = null;
          }
          parentCtrlVM.agreement.numDocuments = response.numDocuments;
          Flash.set('Lyckades att ta bort dokument', 'success');
        })
        .catch(new ErrorHandler('Misslyckades att ta bort dokument.'));
    });
  }

  function downloadAgreementFile(file) {
    parentCtrlVM.Resource.downloadDocument({
      negotiationId: $routeParams.negotiationId,
      agreementId: $routeParams.id,
      id: file.name
    }).$promise.then(function(response) {
      HyUtils.spitOutAttachment(response.data, file.filename, 'application/pdf');
      vm.saving = false;
    }).catch(
      new ErrorHandler('Misslyckades att hamta dokument.')
    ).finally(() => { vm.saving = false });
  }

  vm.changeOrder = (file, up) => {
    if ((up && (file.order === 1)) ||
        (!up && (file.order === parentCtrlVM.agreement.documents.length)))  {
      return;
    }
    vm.busy = true;
    parentCtrlVM.Resource.changeDocumentOrder({
      negotiationId: $routeParams.negotiationId,
      agreementId: $routeParams.id,
      id: file.name,
      up,
    }).$promise.then(() => {
      parentCtrlVM.getDocuments(parentCtrlVM.negotiation.id, parentCtrlVM.agreement.id, () => {
        vm.busy = false;
      });
    });
  };
}

})();
(function () {
'use strict';
AlterRoleModalCtrl.$inject = ["$uibModalInstance", "possibleRoles", "selectedRole"];
angular.module('hyresgastApp').controller('AlterRoleModalCtrl', AlterRoleModalCtrl);


function AlterRoleModalCtrl($uibModalInstance, possibleRoles, selectedRole) {

    var vm = this;

    vm.possibleRoles = possibleRoles;
    vm.selectedRole = selectedRole;
    vm.selectRole = role => { vm.selectedRole = role; };
    vm.ok = () => { $uibModalInstance.close(vm.selectedRole); };
}
})();
(function () {
'use strict';

ShowAgreementOverviewCtrl.$inject = ["$scope", "$uibModal", "$routeParams", "$location", "Flash", "Agreement", "Auth", "NegotiationTypes", "Parties"];
angular.module('hyresgastApp').controller('ShowAgreementOverviewCtrl', ShowAgreementOverviewCtrl);

function ShowAgreementOverviewCtrl(
  $scope,
  $uibModal,
  $routeParams,
  $location,
  Flash,
  Agreement,
  Auth,
  NegotiationTypes,
  Parties
) {
  var vm = this;
  vm.NegotiationTypes = NegotiationTypes;
  vm.haveWeAgreed = haveWeAgreed;
  vm.deleteAgreement = deleteAgreement;
  vm.isRentListFileType = isRentListFileType;

  var parentCtrlVM = $scope.$parent.vm;

  Auth.heartbeat().then(function() {
    vm.currentUserParty = Auth.currentUser().party;
    if (vm.currentUserParty === Parties.HGF) {
      vm.isHgfUser = true;
    }
  });

  function isRentListFileType (fileType) {
    return (fileType === 35 || fileType === 36);
  }

  /////////

  function haveWeAgreed(flag) {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'xsm',
      templateUrl: 'components/modal/confirmation/confirmationYesNoModal.html',
      controller: 'ConfirmationModalCtrl',
      controllerAs: 'vm'
    });

    modalInstance.result.then(function() {
      if (vm.isHgfUser) {
        parentCtrlVM.agreement.agreedByAssociation = flag;
      } else {
        parentCtrlVM.agreement.agreedByOwner = flag;
      }
      parentCtrlVM.saveAgreement();
    });
  }

  function deleteAgreement() {

    var warningMessage = parentCtrlVM.agreement.numApartments > 0 ?
      'ÖVERENSKOMMELSEN HAR EN HYRESLISTA UPPLADDAT. RADERA ÄNDÅ?' :
      'Är du säker på att du vill ta bort?';

    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'xsm',
      templateUrl: 'components/modal/confirmation/confirmationModalWithMessage.html',
      controller: 'ConfirmationModalWithMessageCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--secondary',
      resolve: {
        message: function() { return warningMessage; }
      }
    });

    modalInstance.result.then(function() {
      var negotiationId = $routeParams.negotiationId;
      parentCtrlVM.Resource.deleteAgreement({
        negotiationId,
        agreementId: $routeParams.id})
        .$promise.then(function() {
          if (negotiationId) {
            $location.path('/negotiations/' + negotiationId).search('view', 'agreements');
            Flash.set('Lyckades att ta bort överenskommelsen', 'success');
          } else {
            $location.path('/agreements/');
            Flash.set('Lyckades att ta bort avtal', 'success');
          }
        })
        .catch(function() {
          if (negotiationId) {
            Flash.set('Misslyckades att ta bort överenskommelsen', 'error');
          } else {
            Flash.set('Misslyckades att ta bort avtal', 'error');
          }
        });
    });
  }
}

})();
(function () {
'use strict';

SelectOfficeModalCtrl.$inject = ["$uibModalInstance", "ErrorHandler", "Office"];
angular.module('hyresgastApp').controller('SelectOfficeModalCtrl', SelectOfficeModalCtrl);

function SelectOfficeModalCtrl($uibModalInstance, ErrorHandler, Office) {
  var vm = this;

  vm.canSave = () => vm.selectedOffice && !!vm.selectedOffice.id;

  vm.ok = ok;
  vm.cancel = cancel;

  queryOffice();

  ////////////

  function queryOffice() {
    return Office.query().$promise.then(function(response) {
      vm.offices = response;
    })
      .catch(ErrorHandler('Misslyckades att hämta kontor.'));
  }

  function ok() {
    $uibModalInstance.close({
      selectedOffice : vm.selectedOffice,
    });
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

ListNegotiationAllCtrl.$inject = ["$location", "$filter", "$timeout", "$document", "Auth", "Parties", "CombinedNegotiationStates", "Regions", "NegotiationTypes", "Limits", "CommonIds", "Negotiation"];
angular.module('hyresgastApp').controller('ListNegotiationAllCtrl', ListNegotiationAllCtrl);

function ListNegotiationAllCtrl(
  $location, $filter, $timeout, $document,
  Auth,
  Parties, CombinedNegotiationStates, Regions, NegotiationTypes, Limits, CommonIds,
  Negotiation
) {
  var vm = this;

  vm.statusChoices = Object.values(CombinedNegotiationStates).map(x => ({
    id: x,
    text: $filter('hyCombinedNegotiationStates')(x)
  }));
  vm.negotiationTypeChoices = Object.values(NegotiationTypes).map(x => ({
    id: x,
    text: $filter('hyNegotiationTypes')(x)
  }));
  vm.regionChoices = Object.values(Regions).map(r => ({
    id: r,
    text: $filter('regionTypeToDescription')(r)
  }));
  vm.regionChoices.unshift({ id: CommonIds.tableauAllId, text: 'Alla regioner' });
  vm.regionFilter = vm.regionChoices[0];
  vm.gotoNegotiation = gotoNegotiation;
  vm.pageLimit = 30;

  Auth.heartbeat().then(function() {
    if (Auth.currentUser().party === Parties.HGF) {
      vm.isHgfUser = true;
      vm.tableColumns = [
        {
          title: 'Id',
          column: 'caseNumber'
        },
        {
          title: 'Namn',
          column: 'name'
        },
        {
          title: 'Organisation',
          column: 'counterpart.name'
        },
        {
          title: 'Förhandlingstyp',
          template: 'slot1',
          column: 'type',
          lookup: {
            idProperty: 'id',
            textProperty: 'text',
            dictionary: vm.negotiationTypeChoices,
            multi: true,
          },
        },
        {
          title: 'Status',
          template: 'status',
          column: 'combinedState',
          lookup: {
            idProperty: 'id',
            textProperty: 'text',
            dictionary: vm.statusChoices,
            multi: true,
          },
        },
        {
          title: 'Begärt ändringsdatum',
          column: 'rentEffectiveFrom'
        },
        {
          title: 'Önskat datum för förhandling',
          column: 'callForNegotiation.negotiationDate'
        },
        {
          title: '',
          template: 'comment',
          filterType: 'none'
        }
      ];
    } else {
      vm.tableColumns = [
        {
          title: 'Id',
          column: 'caseNumber'
        },
        {
          title: 'Namn',
          column: 'name'
        },
        {
          title: 'Organisation',
          column: 'counterpart.name'
        },
        {
          title: 'Förhandlingstyp',
          column: 'type'
        },
        {
          title: 'Status',
          column: 'combinedState'
        },
        {
          title: 'Ansvarig hos oss',
          column: 'extNegotiator.firstname'
        },
        {
          title: 'Ansvarig HGF',
          column: 'hgfNegotiator.firstname'
        },
        {
          title: 'Önskat datum för förhandling',
          column: 'callForNegotiation.negotiationDate'
        }
      ];
    }
  });

  function gotoNegotiation(negotiation) {
    $location.search({});
    $location.path('/negotiations/' + negotiation.id);
  }

  let getTableau = () => angular.element($document[0].querySelector("#negotiationsAllTableau")).scope();

  let getParams = (includeProperties) => {
    let tableauParams = vm.lastParams;
    tableauParams.limit = Limits.InMemoryMiddleLimit;
    tableauParams.offset = 0;
    if (includeProperties) {
      tableauParams.includeProperties = includeProperties;
    }
    return tableauParams;
  };
  vm.getAllNegotiations = (cb, includeProperties) => {
    Negotiation.query(getParams(includeProperties), {})
      .$promise.then(function (data) { cb(data) });
  };

  vm.tableFGetData = params => {
    // eslint-disable-next-line no-undef
    return new Promise((resolve, reject) => {
      if (vm.regionFilter.id !== CommonIds.tableauAllId) {
        params['filters[office.regionId]'] = vm.regionFilter.id;
      }
      Negotiation.query(params)
        .$promise.then(result => resolve(result))
        .catch(err => reject(err));
    });
  };
  vm.changeRegion = () => {
    $timeout(() => { getTableau().$broadcast('refresh') }, 200);
  };
}

})();
(function () {
'use strict';

ChangeNegotiatorModalCtrl.$inject = ["$uibModalInstance", "$filter", "$document", "negotiations", "Parties", "NegotiationRoles", "NegotiationStates", "Limits", "User", "ErrorHandler"];
angular.module('hyresgastApp').controller('ChangeNegotiatorModalCtrl', ChangeNegotiatorModalCtrl);

function ChangeNegotiatorModalCtrl($uibModalInstance, $filter, $document,
  negotiations,
  Parties, NegotiationRoles, NegotiationStates, Limits,
  User,
  ErrorHandler) {
  var vm = this;
  vm.Limits = Limits;
  vm.NegotiationRoles = NegotiationRoles;
  vm.selectedHgfParticipantRole = null;
  vm.selectedExtParticipantRole = null;
  let badStates = [NegotiationStates.FINISHED, NegotiationStates.FINISHED_OUTSIDE_PORTAL];
  let rows = negotiations.rows.filter(x => !badStates.includes(x.state));
  let hasNegotiator = false;
  let getFullname = negotiator => `${negotiator.firstname} ${negotiator.lastname}`;
  rows.forEach(row => {
    row.type = $filter('hyNegotiationTypes')(row.type);
    if (row.extNegotiator) {
      row.extNegotiator = getFullname(row.extNegotiator);
      hasNegotiator = true;
    }
    if (row.hgfNegotiator) {
      row.hgfNegotiator = getFullname(row.hgfNegotiator);
      hasNegotiator = true;
    }
  });
  let count = Math.min(rows.length, 1000);
  vm.negotiations = { rows, count };

  vm.columns = [
    { title: 'Id',                    column: 'caseNumber' },
    { title: 'Namn',                  column: 'name' },
    { title: 'Förhandlingstyp',       column: 'type' },
  ];
  if (hasNegotiator) {
    vm.columns.push({ title: 'Förhandlare HGF',       column: 'hgfNegotiator' });
    vm.columns.push({ title: 'Förhandlare motpart',   column: 'extNegotiator' });
  }
  //no type, we need space
  vm.confirmationColumns = vm.columns.filter((x, i) => i !== 2);
  vm.fGetData = function () {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      resolve(vm.negotiations);
    });
  }
  vm.newHgf = false;
  vm.newExt = false;
  vm.fGetConfirmation = function () {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      if (!vm.selectedNegotiations || !vm.selectedNegotiations.length) {
        resolve({ rows: [], count: 0 });
      } else {
        let confirmation = vm.selectedNegotiations.map(x => {
          let row = Object.assign({}, x);
          row.newHgfNegotiator = vm.newHgf ? `${vm.selectedHgfParticipant.firstname} ${vm.selectedHgfParticipant.lastname}`: '';
          row.newExtNegotiator = vm.newExt ? `${vm.selectedExtParticipant.firstname} ${vm.selectedExtParticipant.lastname}`: '';
          return row;
        });
        resolve({ rows: confirmation, count: confirmation.length } );
      }
    });
  }

  vm.beforeGotoNext = (cb, currentStepNumber) => {
    let step = parseInt(currentStepNumber);
    switch (step) {
      case 1:
        // eslint-disable-next-line no-undef
        vm.currentHgfNegotiators = Array.from(new Set(vm.selectedNegotiations.map(x => x.hgfNegotiator))).join(', ');
        // eslint-disable-next-line no-undef
        vm.currentExtNegotiators = Array.from(new Set(vm.selectedNegotiations.map(x => x.extNegotiator))).join(', ');
        break;
      case 2:
        vm.newHgf = vm.selectedHgfParticipantRole !== null;
        vm.newExt = vm.selectedExtParticipantRole !== null;
        vm.confirmationColumns.length = vm.columns.length - 1;
        if (vm.newHgf) {
          vm.confirmationColumns.push({ title: 'Ny förhandlare HGF',          column: 'newHgfNegotiator' });
        }
        if (vm.newExt) {
          vm.confirmationColumns.push({ title: 'Ny förhandlare motpart',      column: 'newExtNegotiator' });
        }

        angular.element($document[0].querySelector("#tableauConfirmation")).scope().$broadcast('refresh');

        break;
      default:
        break;
    }
    cb();
  };

  vm.selectHgfParticipant = role => {
    vm.selectedHgfParticipant = vm.hgfParticipant;
    vm.selectedHgfParticipantRole = role;

    vm.hgfParticipant = null;
  }
  vm.selectExtParticipant = role => {
    vm.selectedExtParticipant = vm.extParticipant;
    vm.selectedExtParticipantRole = role;

    vm.extParticipant = null;
  }

  function getUsers() {
    return User.query({})
      .$promise.then(function (response) {
        let users = response.rows;
        vm.hgfUsers = users.filter(u => u.party === Parties.HGF);
        vm.extUsers = users.filter(u => u.party !== Parties.HGF);
      }).catch(new ErrorHandler('Misslyckades att hämta data.'));
  }

  getUsers();

  vm.ok = () => {
    let result = { negotiationIds: vm.selectedNegotiations.map(x => x.id) };
    if (vm.selectedHgfParticipantRole) {
      result.hgf = {
        newChiefId: vm.selectedHgfParticipant.id,
        newRole: vm.selectedHgfParticipantRole.code,
        unassignExistingChief: vm.unassignExistingHgfChief,
      };
    }
    if (vm.selectedExtParticipantRole) {
      result.ext = {
        newChiefId: vm.selectedExtParticipant.id,
        newRole: vm.selectedExtParticipantRole.code,
        unassignExistingChief: vm.unassignExistingExtChief,
      }
    }
    $uibModalInstance.close(result);
  };
  vm.cancel = function() { $uibModalInstance.dismiss('cancel') };
}
})();
(function () {
'use strict';

ListNegotiationMyCtrl.$inject = ["$location", "$filter", "Negotiation", "CombinedNegotiationStates", "NegotiationTypes", "Parties", "Limits", "Auth"];
angular.module('hyresgastApp').controller('ListNegotiationMyCtrl', ListNegotiationMyCtrl);

function ListNegotiationMyCtrl($location, $filter,
  Negotiation,
  CombinedNegotiationStates, NegotiationTypes, Parties, Limits,
  Auth) {
  var vm = this;

  Auth.heartbeat().then(function () {
    vm.isHgfUser = Auth.currentUser().party === Parties.HGF;
    vm.tableColumns = [
      {
        title: 'Id',
        column: 'caseNumber'
      },
      {
        title: 'Namn',
        column: 'name'
      },
      {
        title: 'Organisation',
        column: 'counterpart.name'
      },
      {
        title: 'Förhandlingstyp',
        template: 'slot1',
        column: 'type',
        lookup: {
          idProperty: 'id',
          textProperty: 'text',
          dictionary: vm.negotiationTypeChoices,
          multi: true,
        },
      },
      {
        title: 'Status',
        template: 'status',
        column: 'combinedState',
        lookup: {
          idProperty: 'id',
          textProperty: 'text',
          dictionary: vm.statusChoices,
          multi: true,
        },
      },
      {
        title: 'Begärt ändringsdatum',
        column: 'rentEffectiveFrom'
      },
      {
        title: 'Önskat datum för förhandling',
        column: 'callForNegotiation.negotiationDate'
      },
      (vm.isHgfUser ? {
        title: 'Ansvarig motpart',
        column: 'extNegotiator.fullName',
        template: 'person',
      } : {
        title: 'Ansvarig HGF',
        column: 'hgfNegotiator.fullName',
        template: 'person',
      }),
      {
        title: '',
        template: 'comment',
        filterType: 'none'
      }
    ];
    vm.tableAttributes = [
      {
        fn: row => !row.seenByMe,
        legend: 'Oläst',
        class: 'table__tr--notseen',
      },
    ];
  });

  vm.statusChoices = Object.values(CombinedNegotiationStates).map(x => ({
    id: x,
    text: $filter('hyCombinedNegotiationStates')(x)
  }));
  vm.negotiationTypeChoices = Object.values(NegotiationTypes).map(x => ({
    id: x,
    text: $filter('hyNegotiationTypes')(x)
  }));
  vm.pageLimit = 30;
  vm.auth = Auth;

  ////////////

  vm.gotoNegotiation = (negotiation) => {
    $location.search({});
    $location.path('/negotiations/' + negotiation.id);
  }
  vm.tableFGetData = params => {
    // eslint-disable-next-line no-undef
    return new Promise((resolve, reject) => {
      params.mine = true;
      Negotiation.query(params)
        .$promise.then(result => resolve(result))
        .catch(err => reject(err));
    });
  };

  let getParams = (includeProperties) => {
    let tableauParams = vm.lastParams;
    tableauParams.limit = Limits.InMemoryMiddleLimit;
    tableauParams.offset = 0;
    if (includeProperties) {
      tableauParams.includeProperties = includeProperties;
    }
    return tableauParams;
  };
  vm.getAllNegotiations = (cb, includeProperties) => {
    Negotiation.query(getParams(includeProperties), {})
      .$promise.then(function (data) { cb(data) });
  };
}
})();
(function () {
'use strict';

ListNegotiationCallAllCtrl.$inject = ["$location", "$filter", "NegotiationCall", "Office", "NegotiationCallStates", "NegotiationTypes"];
angular.module('hyresgastApp').controller('ListNegotiationCallAllCtrl', ListNegotiationCallAllCtrl);

function ListNegotiationCallAllCtrl($location, $filter,
  NegotiationCall, Office,
  NegotiationCallStates, NegotiationTypes) {
  var vm = this;
  vm.pageLimit = 30;

  Office.query().$promise.then(response => {
    vm.allOffices = response;
    setupTableau();
  });

  let setupTableau = () => {
    vm.statusChoices = Object.values(NegotiationCallStates).map(x => ({
      id: x,
      text: $filter('hyNegotiationCallStates')(x)
    }));
    vm.typeChoices = Object.values(NegotiationTypes).map(x => ({
      id: x,
      text: $filter('hyNegotiationTypes')(x)
    }));
    vm.tableColumns = [
      {
        title: 'Id',
        column: 'caseNumber'
      },
      {
        title: 'Namn',
        column: 'name'
      },
      {
        title: 'Förhandlingstyp',
        template: 'slot1',
        column: 'type',
        lookup: {
          idProperty: 'id',
          textProperty: 'text',
          dictionary: vm.typeChoices,
          multi: true,
        },
      },
      {
        title: 'Skickad till kontor',
        column: 'office.id',
        template: 'slot2',
        lookup: {
          idProperty: 'id',
          textProperty: 'name',
          dictionary: vm.allOffices,
          multi: true,
        },
      },
      {
        title: 'Organisation',
        column: 'counterpart.name'
      },
      {
        title: 'Status',
        template: 'status',
        column: 'state',
        lookup: {
          idProperty: 'id',
          textProperty: 'text',
          dictionary: vm.statusChoices,
          multi: true,
        },
      },
      {
        title: 'Bereds av',
        column: 'handler.fullName',
      },
      {
        title: 'Inskickad',
        column: 'sentAt',
        template: 'slot3',
        filterType: 'none'
      },
    ];
  }

  vm.gotoCall = call => {
    $location.search({});
    $location.path('/negotiation-calls/' + call.id);
  }

  vm.tableFGetData = NegotiationCall.query;
}
})();
(function () {
'use strict';

ListNegotiationResponsiblesCtrl.$inject = ["$scope", "$location", "$filter", "Negotiation", "CombinedNegotiationStates", "NegotiationTypes", "Limits"];
angular.module('hyresgastApp').controller('ListNegotiationResponsiblesCtrl', ListNegotiationResponsiblesCtrl);

function ListNegotiationResponsiblesCtrl(
  $scope, $location, $filter,
  Negotiation,
  CombinedNegotiationStates, NegotiationTypes, Limits
) {
  var vm = this;
  vm.statusChoices = Object.values(CombinedNegotiationStates).map(x => ({
    id: x,
    text: $filter('hyCombinedNegotiationStates')(x)
  }));
  vm.negotiationTypeChoices = Object.values(NegotiationTypes).map(x => ({
    id: x,
    text: $filter('hyNegotiationTypes')(x)
  }));
  vm.pageLimit = 30;
  vm.tableColumns = [{
    title: 'Id',
    column: 'caseNumber'
  },
  {
    title: 'Förhandlare',
    column: 'participants.firstname',
    template: 'person',
  },
  {
    title: 'Namn',
    column: 'name'
  },
  {
    title: 'Organisation',
    column: 'counterpart.name'
  },
  {
    title: 'Kontor',
    column: 'office.name'
  },
  {
    title: 'Förhandlingstyp',
    template: 'slot1',
    column: 'type',
    lookup: {
      idProperty: 'id',
      textProperty: 'text',
      dictionary: vm.negotiationTypeChoices,
      multi: true,
    },
  },
  {
    title: 'Status',
    template: 'status',
    column: 'combinedState',
    lookup: {
      idProperty: 'id',
      textProperty: 'text',
      dictionary: vm.statusChoices,
      multi: true,
    },
  },
  {
    title: 'Önskat datum för förhandling',
    column: 'callForNegotiation.negotiationDate'
  }
  ];

  vm.gotoNegotiation = (negotiation) => {
    $location.search({});
    $location.path('/negotiations/' + negotiation.id);
  }

  let getParams = (includeProperties) => {
    let tableauParams = vm.lastParams;
    tableauParams.limit = Limits.InMemoryMiddleLimit;
    tableauParams.offset = 0;
    if (includeProperties) {
      tableauParams.includeProperties = includeProperties;
    }
    return tableauParams;
  };

  vm.getAllNegotiations = (cb, includeProperties) => {
    Negotiation.queryByChief(getParams(includeProperties), {})
      .$promise.then(function (data) { cb(data) });
  };

  vm.tableFGetData = Negotiation.queryByChief;
}
})();
(function () {
'use strict';

EditMotivationModalCtrl.$inject = ["$uibModalInstance", "propertiesArr", "StringUtils"];
angular.module('hyresgastApp').controller('EditMotivationModalCtrl', EditMotivationModalCtrl);

function EditMotivationModalCtrl($uibModalInstance, propertiesArr, StringUtils) {
  var vm = this;
  vm.cancel = cancel;
  vm.add = add;
  vm.StringUtils = StringUtils;

  vm.demand = {
    unit: '0',
    numericValue: null,
    stringValue: null,
    motivation: ''
  };

  var firstCheckBox = propertiesArr[0];
  // check if all selected properties have same demand and motivation.
  // If so => run in EDIT mode.
  // compare in relation to the first in the group of selected checkboxes
  var checkForSameDemandAndMotivation = propertiesArr.every(function(property) {
    return (
      property.demand && property.demand.unit &&
      property.demand.unit === firstCheckBox.demand.unit &&
      property.demand.numericValue === firstCheckBox.demand.numericValue &&
      property.demand.stringValue === firstCheckBox.demand.stringValue &&
      property.demand.motivation === firstCheckBox.demand.motivation
    );
  });

  if (checkForSameDemandAndMotivation) {
    vm.demand = {
      unit: firstCheckBox.demand.unit,
      numericValue: firstCheckBox.demand.numericValue,
      stringValue: firstCheckBox.demand.stringValue,
      motivation: firstCheckBox.demand.motivation
    };
  }

  function add() {
    if (vm.demand.numericValue && typeof vm.demand.numericValue === 'string') {
      vm.demand.numericValue = Number(vm.demand.numericValue.replace(/ /g, '').replace(/,/g, '.'));
    }
    $uibModalInstance.close(vm.demand);
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

ReceivingOfficeModalCtrl.$inject = ["$uibModalInstance", "ErrorHandler", "Auth", "Office", "NegotiationTypes", "NegotiationCallStates", "NegotiationLandlordSides", "negotiationCallObj"];
angular.module('hyresgastApp').controller('ReceivingOfficeModalCtrl', ReceivingOfficeModalCtrl);

function ReceivingOfficeModalCtrl($uibModalInstance, ErrorHandler,
  Auth, Office,
  NegotiationTypes, NegotiationCallStates, NegotiationLandlordSides,
  negotiationCallObj) {
  var vm = this;
  vm.isHgf = Auth.isHgfUser();
  vm.editOfficeOnly = vm.isHgf;

  vm.canSave = () => vm.editOfficeOnly
    ? vm.selectedOffice && !!vm.selectedOffice.id
    // eslint-disable-next-line eqeqeq
    : vm.selectedOffice && vm.selectedOffice.id && vm.checkOne && vm.checkTwo && vm.landlordSide != -1;

  vm.ok = ok;
  vm.cancel = cancel;

  vm.LandlordSides = NegotiationLandlordSides;
  vm.landlordSide = -1;

  vm.NegotiationTypes = NegotiationTypes;
  vm.negotiationCallType = negotiationCallObj.type;

  vm.showCheckTwo = false;
  if (vm.negotiationCallType === vm.NegotiationTypes.STANDARD_PROPERTY ||
      vm.negotiationCallType === vm.NegotiationTypes.STANDARD_APARTMENT ||
      vm.negotiationCallType === vm.NegotiationTypes.REBUILD_PROPERTY ||
      vm.negotiationCallType === vm.NegotiationTypes.REBUILD_PROPERTY_2 ||
      vm.negotiationCallType === vm.NegotiationTypes.REBUILD_APARTMENT) {
    vm.showCheckTwo = true;
    vm.checkTwo = false;
  } else {
    vm.checkTwo = true;
  }

  if (negotiationCallObj.state === NegotiationCallStates.WAITING_EXT) {
    vm.editMode = true;
    vm.checkOne = true;
    vm.selectedOffice = {
      id: negotiationCallObj.office.id,
      name: negotiationCallObj.office.name
    };
  }

  queryOffice();

  ////////////

  function queryOffice() {
    return Office.query().$promise.then(function(response) {
      vm.offices = response;
    })
      .catch(ErrorHandler('Misslyckades att hämta kontor.'));
  }

  function ok() {
    $uibModalInstance.close({
      selectedOffice : vm.selectedOffice,
      landlordSide : vm.editOfficeOnly ? NegotiationLandlordSides.LANDLORD : vm.landlordSide
    });
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

ListNegotiationOfficesCtrl.$inject = ["$location", "$filter", "Auth", "ErrorHandler", "User", "Negotiation", "CombinedNegotiationStates", "NegotiationTypes", "Limits"];
angular.module('hyresgastApp').controller('ListNegotiationOfficesCtrl', ListNegotiationOfficesCtrl);

function ListNegotiationOfficesCtrl(
  $location, $filter,
  Auth, ErrorHandler,
  User, Negotiation,
  CombinedNegotiationStates, NegotiationTypes, Limits
) {
  var vm = this;
  vm.statusChoices = Object.values(CombinedNegotiationStates).map(x => ({
    id: x,
    text: $filter('hyCombinedNegotiationStates')(x)
  }));
  vm.negotiationTypeChoices = Object.values(NegotiationTypes).map(x => ({
    id: x,
    text: $filter('hyNegotiationTypes')(x)
  }));
  vm.pageLimit = 30;
  vm.user = {};
  vm.tableColumns = [
    {
      title: 'Id',
      column: 'caseNumber'
    },
    {
      title: 'Namn',
      column: 'name'
    },
    {
      title: 'Organisation',
      column: 'counterpart.name'
    },
    {
      title: 'Förhandlingstyp',
      template: 'slot1',
      column: 'type',
      lookup: {
        idProperty: 'id',
        textProperty: 'text',
        dictionary: vm.negotiationTypeChoices,
        multi: true,
      },
    },
    {
      title: 'Kontor',
      column: 'office.name'
    },
    {
      title: 'Status',
      template: 'status',
      column: 'combinedState',
      lookup: {
        idProperty: 'id',
        textProperty: 'text',
        dictionary: vm.statusChoices,
        multi: true,
      },
    },
    {
      title: 'Begärt ändringsdatum',
      column: 'rentEffectiveFrom'
    },
    {
      title: 'Önskat datum för förhandling',
      column: 'callForNegotiation.negotiationDate'
    },
    {
      title: '',
      template: 'comment',
      filterType: 'none'
    }
  ];

  Auth.heartbeat().then(function () {
    getUserOffices();
  });

  function getUserOffices() {
    return User.get({
      id: Auth.currentUser().id
    })
      .$promise.then(function (response) {
        vm.user = response;
        // Convert response.offices object to an array for easily sorting and using length property
        var offices = [];
        for (var office in response.offices) {
          offices.push(response.offices[office]);
        }
        vm.user.offices = offices;
      })
      .catch(ErrorHandler('Misslyckades att hämta data.'))
      .finally(() => { vm.ready = true });
  }

  vm.gotoNegotiation = (negotiation) => {
    $location.search({});
    $location.path('/negotiations/' + negotiation.id);
  }

  let getParams = (includeProperties) => {
    let tableauParams = vm.lastParams;
    tableauParams.limit = Limits.InMemoryMiddleLimit;
    tableauParams.offset = 0;
    if (includeProperties) {
      tableauParams.includeProperties = includeProperties;
    }
    return tableauParams;
  };

  vm.getAllNegotiations = (cb, includeProperties) => {
    Negotiation.query(getParams(includeProperties), {})
      .$promise.then(function (data) { cb(data) });
  };

  vm.tableFGetData = params => {
    // eslint-disable-next-line no-undef
    return new Promise((resolve, reject) => {
      params.myOffices = 'true';
      Negotiation.query(params)
        .$promise.then(result => resolve(result))
        .catch(err => reject(err));
    });
  };
}
})();
(function () {
'use strict';

ListNegotiationCallNewCtrl.$inject = ["$location", "$filter", "Office", "NegotiationCallStates", "NegotiationTypes", "NegotiationCall"];
angular.module('hyresgastApp').controller('ListNegotiationCallNewCtrl', ListNegotiationCallNewCtrl);

function ListNegotiationCallNewCtrl(
  $location, $filter,
  Office,
  NegotiationCallStates, NegotiationTypes,
  NegotiationCall
) {
  var vm = this;

  vm.pageLimit = 30;

  let newStates = {
    WAITING_HGF: NegotiationCallStates.WAITING_HGF,
    PARTIALLY_ASSIGNED: NegotiationCallStates.PARTIALLY_ASSIGNED
  };

  Office.query().$promise.then(response => {
    vm.allOffices = response;
    setupTableau();
  });

  let setupTableau = () => {
    vm.statusChoices = Object.values(newStates).map(x => ({
      id: x,
      text: $filter('hyNegotiationCallStates')(x)
    }));
    vm.typeChoices = Object.values(NegotiationTypes).map(x => ({
      id: x,
      text: $filter('hyNegotiationTypes')(x)
    }));
    vm.tableColumns = [
      {
        title: 'Id',
        column: 'caseNumber'
      },
      {
        title: 'Namn',
        column: 'name'
      },
      {
        title: 'Förhandlingstyp',
        template: 'slot1',
        column: 'type',
        lookup: {
          idProperty: 'id',
          textProperty: 'text',
          dictionary: vm.typeChoices,
          multi: true,
        },
      },
      {
        title: 'Skickad till kontor',
        column: 'office.id',
        template: 'slot2',
        lookup: {
          idProperty: 'id',
          textProperty: 'name',
          dictionary: vm.allOffices,
          multi: true,
        },
      },
      {
        title: 'Organisation',
        column: 'counterpart.name'
      },
      {
        title: 'Status',
        template: 'status',
        column: 'state',
        lookup: {
          idProperty: 'id',
          textProperty: 'text',
          dictionary: vm.statusChoices,
          multi: true,
        },
      },
      {
        title: 'Bereds av',
        column: 'handler.fullName',
      },
      {
        title: 'Inskickad',
        column: 'sentAt',
        template: 'slot3',
        filterType: 'none'
      },
    ];
  }

  vm.gotoCall = call => {
    $location.search({});
    $location.path('/negotiation-calls/' + call.id);
  }

  // eslint-disable-next-line no-undef
  vm.tableFGetData = params => new Promise((resolve, reject) => {
    params.mine = true;
    if (!params['filters[state]'] || (params['filters[state]'].length === 0)) {
      params['filters[state]'] = Object.values(newStates);
    }
    NegotiationCall.query(params)
      .$promise.then(response => resolve(response))
      .catch(err => reject(err));
  });
}
})();
(function () {
'use strict';
AgreementFigurantsModalCtrl.$inject = ["$uibModalInstance", "$document", "Agreement", "Limits", "agreement", "caseNumber", "withoutRentlist"];
angular.module('hyresgastApp').controller('AgreementFigurantsModalCtrl', AgreementFigurantsModalCtrl);

const RoleNames = {
  0: 'Ägare',
  1: 'Hyresvärd',
  2: 'Ombud',
};

function AgreementFigurantsModalCtrl($uibModalInstance, $document,
  Agreement,
  Limits,
  agreement, caseNumber, withoutRentlist) {
  var vm = this;
  vm.Limits = Limits;
  vm.agreement = agreement;
  vm.caseNumber = caseNumber;

  let source = (agreement.conditionTags.figurants && agreement.conditionTags.figurants.length)
    ? agreement.conditionTags.figurants
    : agreement.owners;
  vm.figurants = source.map(s => {
    let figurant = {
      id: s.id,
      name: s.name,
      organizationNumber: s.organizationNumber,
      role: (typeof s.role === 'undefined') ? 0 : s.role,
    }
    figurant.roleStr = RoleNames[figurant.role];
    return figurant;
  });

  let refreshTableau = id => angular.element($document[0].querySelector("#"+id))
    .scope().$broadcast('refresh');

  vm.columnsAllFigurants = [
    { title: 'Namn',        column: 'name' },
    { title: 'Org nummer',  column: 'organizationNumber' },
    { title: 'Typ',         column: 'roleStr' },
    { title: 'Fastigheter', column: 'properties', type: 'array' },
  ];

  vm.columnsFigurants = [
    { title: 'Namn',        column: 'name' },
    { title: 'Org nummer',  column: 'organizationNumber' },
    { title: 'Typ',         column: 'roleStr' },
  ];

  vm.actionsAllFigurants = [
    {
      title: 'Lägg till',
      action: () => {
        let currentOrganizationNumbers = vm.figurants.map(x => x.organizationNumber);
        let newValues = vm.selectedAllFigurants.filter(x => !currentOrganizationNumbers.includes(x.organizationNumber));
        vm.figurants.push(...newValues);
        refreshTableau('tableauFigurants');
      }
    }
  ];

  vm.actionsFigurants = [
    {
      btnClass: 'btn--icon btn-fu btn--icon-bin',
      action: () => {
        let selectedOrganizationNumbers = vm.selectedFigurants.map(x => x.organizationNumber);
        vm.figurants = vm.figurants.filter(x => !selectedOrganizationNumbers.includes(x.organizationNumber));
        refreshTableau('tableauFigurants');
      }
    },
    {
      title: 'Ny avtalspart',
      common: true,
      action: () => {
        vm.unterpopupNewFigurant = true;
      },
    }
  ];

  vm.createNewFigurant = () => {
    let newFigurant = {
      name: vm.newFigurantName,
      organizationNumber: vm.newFigurantOrganizationNumber.replace('-', ''),
      role: 0,
      roleStr: RoleNames[0],
    };
    vm.figurants.push(newFigurant);
    vm.unterpopupNewFigurant = false;
    vm.newFigurantName = vm.newFigurantOrganizationNumber = null;
    refreshTableau('tableauFigurants');
  }

  vm.selectedFigurants = [];
  vm.selectedAllFigurants = [];

  vm.getFigurants = function () {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      resolve({ rows: vm.figurants, count: vm.figurants.length });
    });
  }

  vm.getAllFigurants = function() {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      Agreement.getFigurants({ agreementId: agreement.id, withoutRentlist })
        .$promise.then(response => {
          response.figurants.forEach(x => {
            x.roleStr = RoleNames[x.role];
            x.properties = x.properties.map(p => `${p.propertyDesignation} ${p.municipality}`);
          });
          resolve({
            rows: response.figurants,
            count: response.figurants.length,
          });
        });
    });
  }

  vm.ok = () => $uibModalInstance.close({
    figurants: vm.figurants.map(f => ({
      id: f.id,
      name: f.name,
      organizationNumber: f.organizationNumber,
      role: f.role,
    })),
  });

  vm.delete = () => $uibModalInstance.close({ figurants: null });
}
})();
(function () {
'use strict';

// @desc
//   Manage deletion blocking users belonging to an organization.
//
//
// @example
//   <list-blocking-apartments
//      organization="vm.organization">
//      list="vm.apartments">
//      cancel="vm.cancel()">
//   </list-blocking-apartments>

listBlockingApartments.$inject = ["$uibModal", "Flash", "UserByOrganization", "Auth", "HyUtils", "Roles"];
angular.module('hyresgastApp').directive('listBlockingApartments', listBlockingApartments);

function listBlockingApartments($uibModal, Flash, UserByOrganization, Auth, HyUtils, Roles) {
  var directive = {
    restrict: 'E',
    templateUrl: 'resources/organization/show/blocking-data/list-blocking-apartments.html',
    scope: {
      organization: '=',
      list: '=',
      cancel: '='
    },
    link: link
  };

  return directive;

  function link(scope) {
    scope.delete = destroy;
    scope.Auth = Auth;
    scope.roles = Roles;
    ////////////
    function destroy(apartment) {
      HyUtils.askQuestion('Are you sure you want to delete this apartment?', null, 2)
        .then(pRes => {
          if(pRes === 'Ja') {
            //TODO: DELETE APARTMENTS
          }
        });
    }
  }
}



})();
(function () {
'use strict';

// @desc
//   Manage deletion blocking users belonging to an organization.
//
//
// @example
//   <list-blocking-negotiated-apartment-calls
//      organization="vm.organization">
//      list="vm.negotiatedApartmentCalls">
//      cancel="vm.cancel()">
//   </list-blocking-negotiated-apartment-c>

listBlockingNegotiatedApartmentsCalls.$inject = ["$uibModal", "Flash", "UserByOrganization", "Auth", "HyUtils", "Roles"];
angular.module('hyresgastApp').directive('listBlockingNegotiatedApartmentsCalls', listBlockingNegotiatedApartmentsCalls);

function listBlockingNegotiatedApartmentsCalls($uibModal, Flash, UserByOrganization, Auth, HyUtils, Roles) {
  var directive = {
    restrict: 'E',
    templateUrl: 'resources/organization/show/blocking-data/list-blocking-negotiated-apartments-calls.html',
    scope: {
      organization: '=',
      list: '=',
      cancel: '='
    },
    link: link
  };

  return directive;

  function link(scope) {
    scope.delete = destroy;
    scope.Auth = Auth;
    scope.roles = Roles;
    ////////////
    function destroy(negotiatedApartmentCall) {
      HyUtils.askQuestion('Are you sure you want to delete this negotiatedApartmentCall?', null, 2)
        .then(pRes => {
          if(pRes === 'Ja') {
              //TODO: DELETE NEGOTIATED APARTMENT CALLS
          }
        });
    }
  }
}



})();
(function () {
'use strict';

// @desc
//   Manage deletion blocking users belonging to an organization.
//
//
// @example
//   <list-blocking-negotiated-apartments
//      organization="vm.organization">
//      list="vm.negotiatedApartments">
//      cancel="vm.cancel()">
//   </list-blocking-negotiated-apartments>

listBlockingNegotiatedApartments.$inject = ["$uibModal", "Flash", "UserByOrganization", "Auth", "HyUtils", "Roles"];
angular.module('hyresgastApp').directive('listBlockingNegotiatedApartments', listBlockingNegotiatedApartments);

function listBlockingNegotiatedApartments($uibModal, Flash, UserByOrganization, Auth, HyUtils, Roles) {
  var directive = {
    restrict: 'E',
    templateUrl: 'resources/organization/show/blocking-data/list-blocking-negotiated-apartments.html',
    scope: {
      organization: '=',
      list: '=',
      cancel: '='
    },
    link: link
  };

  return directive;

  function link(scope) {
    scope.delete = destroy;
    scope.Auth = Auth;
    scope.roles = Roles;
    ////////////
    function destroy(negotiatedApartment) {
      HyUtils.askQuestion('Are you sure you want to delete this negotiatedApartment?', null, 2)
        .then(pRes => {
          if(pRes === 'Ja') {
            //TODO: DELETE NEGOTIATIONS APARTMENTS
          }
        });
    }
  }
}



})();
(function () {
'use strict';

// @desc
//   Manage deletion blocking users belonging to an organization.
//
//
// @example
//   <list-blocking-negotiation-calls
//      organization="vm.organization">
//      list="vm.negotiationCalls">
//      cancel="vm.cancel()">
//   </list-blocking-negotiation-calls>

listBlockingNegotiationCalls.$inject = ["$uibModal", "Flash", "UserByOrganization", "Auth", "HyUtils", "Roles"];
angular.module('hyresgastApp').directive('listBlockingNegotiationCalls', listBlockingNegotiationCalls);

function listBlockingNegotiationCalls($uibModal, Flash, UserByOrganization, Auth, HyUtils, Roles) {
  var directive = {
    restrict: 'E',
    templateUrl: 'resources/organization/show/blocking-data/list-blocking-negotiation-calls.html',
    scope: {
      organization: '=',
      list: '=',
      cancel: '='
    },
    link: link
  };

  return directive;

  function link(scope) {
    scope.delete = destroy;
    scope.Auth = Auth;
    scope.roles = Roles;
    ////////////
    function destroy(callForNegotiation) {
      HyUtils.askQuestion('Are you sure you want to delete this callForNegotiation?', null, 2)
        .then(pRes => {
          if(pRes === 'Ja') {
            //TODO: DELETE NEGOTIATIONS CALLS
          }
        });
    }
  }
}



})();
(function () {
'use strict';

// @desc
//   Manage deletion blocking users belonging to an organization.
//
//
// @example
//   <list-blocking-negotiations
//      organization="vm.organization">
//      list="vm.negotiations">
//      cancel="vm.cancel()">
//   </list-blocking-negotiations>

listBlockingNegotiations.$inject = ["$uibModal", "Flash", "UserByOrganization", "Auth", "HyUtils", "Roles"];
angular.module('hyresgastApp').directive('listBlockingNegotiations', listBlockingNegotiations);

function listBlockingNegotiations($uibModal, Flash, UserByOrganization, Auth, HyUtils, Roles) {
  var directive = {
    restrict: 'E',
    templateUrl: 'resources/organization/show/blocking-data/list-blocking-negotiations.html',
    scope: {
      organization: '=',
      list: '=',
      cancel: '='
    },
    link: link
  };

  return directive;

  function link(scope) {
    scope.delete = destroy;
    scope.Auth = Auth;
    scope.roles = Roles;
    ////////////
    function destroy(negotiation) {
      HyUtils.askQuestion('Are you sure you want to delete this negotiation?', null, 2)
        .then(pRes => {
          //TODO: DELETE NEGOTIATIONS
        });
    }
  }
}



})();
(function () {
'use strict';

// @desc
//   Manage deletion blocking users belonging to an organization.
//
//
// @example
//   <list-blocking-users
//      organization="vm.organization">
//      list="vm.users">
//      cancel="vm.cancel()">
//   </list-blocking-users>

listBlockingUsers.$inject = ["$uibModal", "Flash", "UserByOrganization", "Auth", "HyUtils", "Roles"];
angular.module('hyresgastApp').directive('listBlockingUsers', listBlockingUsers);

function listBlockingUsers($uibModal, Flash, UserByOrganization, Auth, HyUtils, Roles) {
  var directive = {
    restrict: 'E',
    templateUrl: 'resources/organization/show/blocking-data/list-blocking-users.html',
    scope: {
      organization: '=',
      list: '=',
      cancel: '='
    },
    link: link
  };

  return directive;

  function link(scope) {
    scope.delete = destroy;
    scope.Auth = Auth;
    scope.roles = Roles;
    ////////////
    function destroy(user) {
      HyUtils.askQuestion('Are you sure you want to remove this user from organization?', null, 2)
        .then(pRes => {
          if(pRes === 'Ja') {
            UserByOrganization.delete(scope.organization.id, user.id, user.invited).then(function(response) {
              scope.users = scope.users.filter(function(u) {
                return u.id !== user.id;
              });
              Flash.set('Användare har tagits bort från organisationen', 'success');
            })
          }
        });
    }
  }
}



})();
(function () {
'use strict';

BlockingDeletionModalCtrl.$inject = ["$uibModalInstance", "$location", "ErrorHandler", "Organization", "Flash", "blockingData"];
angular.module('hyresgastApp').controller('BlockingDeletionModalCtrl', BlockingDeletionModalCtrl);

function BlockingDeletionModalCtrl($uibModalInstance, $location,
  ErrorHandler, Organization, Flash, blockingData) {
  var vm = this;

  /////////
  vm.ok = ok;
  vm.cancel = cancel;
  vm.delete = destroy;
  vm.organization = {};

  vm.users = blockingData['blockingUsers'];
  vm.apartments = blockingData['blockingApartments'];
  vm.negotiations = blockingData['blockingNegotiations'];
  vm.negotiatedApartments = blockingData['blockingNegotiatedApartments'];
  vm.negotiatedApartmentsForCalls = blockingData['blockingNegotiatedApartmentsForCalls'];
  vm.callsForNegotiations = blockingData['blockingCallsForNegotiations'];

  ////////////
  vm.tablist = [
    { title: 'Användare', key: 1, show: vm.users.length > 0 },
    { title: 'Lägenheter', key: 2, show: vm.apartments.length > 0 },
    { title: 'Framställningar', key: 3, show: vm.callsForNegotiations.length > 0},
    { title: 'Förhandlingar', key: 4, show: vm.negotiations.length > 0 },
    { title: 'Överenskomen lägenhetsinformation', key: 5, show: vm.negotiatedApartments.length > 0 },
    { title: 'Lägenhetsinformation från framställan', key: 6, show: vm.negotiatedApartmentsForCalls.length > 0 }
  ];

  vm.selectedTab = vm.tablist.find(obj => obj.show).key;
  ////////////
  function ok() {
    $uibModalInstance.close(vm.selectedOffice);
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }

  function destroy() {
    Organization.delete(vm.organization).$promise.then(
      function() {
        Flash.set('Organisationen borttagen', 'success');
        $location.path('/organizations');
      })
      .catch(function() {
        new ErrorHandler('Du kan endast ta bort om det inte finns användare kopplade till organisationen.');
      });
  }
}


})();
(function () {
'use strict';

ChangeUserRoleModal.$inject = ["$uibModalInstance", "RoleLookup", "Organization", "Flash", "organizationId", "userId", "foundAdmin"];
angular.module('hyresgastApp').controller('ChangeUserRoleModal', ChangeUserRoleModal);

function ChangeUserRoleModal($uibModalInstance, RoleLookup, Organization, Flash, organizationId, userId, foundAdmin) {
  var vm = this;
  vm.roles = RoleLookup;
  vm.foundAdmin = foundAdmin;

  vm.user = { role: 100 }; // Default: User role
  vm.selectRole = selectRole;
  vm.save = save;

  /////////

  function save() {
    vm.saving = true;
    Organization.updateUserRole({
      organizationId: organizationId,
      id: userId,
      role: vm.user.role
    })
      .$promise.then(
        function(response) {
          Flash.set('Användarens roll har ändrats', 'success');
          $uibModalInstance.close({ id: userId, role: vm.user.role });
        })
      .catch(function(reason) {
        if (reason.status === 403) {
          Flash.set('Måste finnas minst en Admin', 'error');
        } else {
          Flash.set('Användarens roll misslyckades ändras', 'error');
        }
        $uibModalInstance.dismiss('cancel');
      })
      .finally(function() {
        vm.saving = false;
      });
  }

  vm.cancel = function() {
    $uibModalInstance.dismiss('cancel');
  };

  function selectRole(role) {
    vm.user.role = role;
  }
}

})();
(function () {
'use strict';

InviteUsersModalCtrl.$inject = ["$uibModalInstance", "$location", "$timeout", "RoleLookup", "Organization", "User", "Flash", "HyUtils", "selectedOrganization", "foundAdmin"];
angular.module('hyresgastApp').controller('InviteUsersModalCtrl', InviteUsersModalCtrl);

function InviteUsersModalCtrl($uibModalInstance, $location, $timeout,
  RoleLookup,
  Organization, User, Flash, HyUtils,
  selectedOrganization, foundAdmin) {

  var vm = this;
  vm.organization = selectedOrganization.name;
  vm.roles = RoleLookup;
  vm.foundAdmin = foundAdmin;

  // the first invited user have to be admin (avoid dropdown)
  if (vm.foundAdmin && vm.foundAdmin.length > 0) {
    vm.user = { role: 100 }; // Default: User role
  } else {
    vm.user = { role: 200 }; // Default: Admin role
  }

  vm.startCheckEmail = startCheckEmail;
  vm.confirmingEmail = false;
  vm.isMailConfirmed = 'unknown';

  vm.tamePhoneNumber = () => {
    if (vm.user.phone) {
      $timeout(() => {
        let tamedPhone = HyUtils.tamePhoneNumber(vm.user.phone);
        vm.foreignPhone = tamedPhone.countryCode !== '+46';
        vm.user.phone = vm.foreignPhone ? tamedPhone.countryCode + tamedPhone.number :  tamedPhone.number;
      }, 3000);
    }
  };

  vm.selectRole = selectRole;
  vm.save = function() {
    vm.saving = true;
    vm.tamePhoneNumber();
    let invite = {
      id: selectedOrganization.id, //add the org id to the object sent to backend.
      email: vm.user.email,
      phone: vm.user.phone,
      role: vm.user.role
    };
    Organization.saveInvite(invite)
      .$promise.then(response => {
        Flash.set('Användaren inbjuden', 'success');
        $uibModalInstance.close(response);
      }).catch(reason => {
        if (reason.status === 409) {
          Flash.set('Användaren finns redan i organisationen', 'error');
        } else if (reason.status === 403) {
          $location.path('/403')
        } else if (reason.data.code === 10) {
          Flash.set('Kan inte bjuda in en HGF-användare till en extern organisation', 'error');
        } else {
          Flash.set('Misslyckades att bjuda in användare.', 'error');
        }
        $uibModalInstance.dismiss('cancel');
      }).finally(() => { vm.saving = false });
  };

  vm.cancel = function() {
    $uibModalInstance.dismiss('cancel');
  };

  function selectRole(role) {
    vm.user.role = role;
  }

  function startCheckEmail() {
    if (vm.emailCheckScheduled) {
      return;
    }

    vm.emailCheckScheduled = true;
    $timeout(() => {
      checkEmail();
      vm.emailCheckScheduled = false;
    }, 3000);
  }

  function checkEmail() {
    vm.confirmingEmail = true;
    User.checkEmail({ email: vm.user.email}).$promise.then(
      function(response) {
        vm.isMailConfirmed = response.isValid;
        vm.confirmingEmail = false;
      });
  }
}

})();
(function () {
'use strict';

// @desc
//   Manage users belonging to an organization. For example, list
//   users, invite new users to the organization.
//
//   Note: Users with flag invited set to TRUE is another way to say
//   a person has been invited but the person has not created a user account.
//
// @example
//   <list-user-by-organization
//      organization="vm.organization">
//   </list-user-by-organization>

listUserByOrganization.$inject = ["$q", "Flash", "Auth", "HyUtils", "ErrorHandler", "Roles", "UserByOrganization", "Organization"];
angular.module('hyresgastApp').directive('listUserByOrganization', listUserByOrganization);

function listUserByOrganization($q,
  Flash, Auth, HyUtils, ErrorHandler,
  Roles,
  UserByOrganization, Organization) {
  var directive = {
    restrict: 'E',
    templateUrl: 'resources/organization/show/user/list-user-by-organization.html',
    scope: {
      organization: '=',
      foundAdmin: '='
    },
    link: link
  };

  return directive;

  function link(scope) {
    scope.add = add;
    scope.invite = invite;
    scope.delete = destroy;
    scope.deleteBinding = deleteBinding;
    scope.Auth = Auth;
    scope.changeUserRole = changeUserRole;
    scope.tabs = [
      { title: 'Förhandlare', key: 'users' },
      { title: 'Fastigheter', key: 'properties' },
    ];
    scope.$watch('organization.id', function(newValue) {
      if (newValue) {
        getUsers();
        getProperties();
      }
    });

    ////////////

    function getUsers() {
      var user = Auth.currentUser();

      var omitInvitedUsers = false;
      if (!Auth.isHgfUser()) {
        var role = user.roleByOrganization[scope.organization.id];
        var isOrganizationAdmin = role === Roles.ADMIN;
        omitInvitedUsers = !isOrganizationAdmin;
      }

      UserByOrganization.query(scope.organization.id, omitInvitedUsers)
        .then(function(response) {
          scope.users = response;
          scope.usersCount = response.length;
          scope.foundAdmin = scope.users.filter(user => user.role === Roles.ADMIN);
        })
        .catch(function() {
          Flash.set('Misslyckades att hämta användare.', 'error');
        })
        .finally(function() {
          scope.ready = true;
        });
    }

    function getProperties() {
      Organization.queryProperties({id: scope.organization.id})
        .$promise.then(result => {
          scope.properties = result.properties;
        });
    }

    function invite() {
      HyUtils.showDefaultModal('InviteUsersModalCtrl',
        'resources/organization/show/user/inviteUsersModal.html',
        {
          selectedOrganization: scope.organization,
          foundAdmin: scope.foundAdmin,
        },
        result => {
          scope.users.push(result);
        });
    }

    function changeUserRole(userId) {
      HyUtils.showDefaultModal('ChangeUserRoleModal',
        'resources/organization/show/user/changeUserRoleModal.html',
        {
          organizationId: scope.organization.id,
          userId: userId,
          foundAdmin: scope.foundAdmin,
        },
        result => {
          angular.forEach(scope.users, function(item, index) {
            if (item.id === result.id) {
              scope.users[index].role = result.role;
            }
          });
        },
        null,
        null,
        { size: 'sm' });
    }

    function destroy(user) {
      HyUtils.showDefaultModal('ConfirmationModalCtrl',
        'components/modal/confirmation/confirmationModal.html',
        {},
        () => {

          let killUser = killOrphan => {
            UserByOrganization.delete(scope.organization.id, user.id, user.invited, killOrphan).then(() => {
              scope.users = scope.users.filter(u => u.id !== user.id);
              let message = killOrphan
                ? 'Användare har tagits bort från organisationen och raderat'
                : 'Användare har tagits bort från organisationen';
              Flash.set(message, 'success');
            }).catch(err => {
              ErrorHandler(err);
            });
          };

          if (user.dedicated) {
            HyUtils.askQuestion('Användaren tillhör inte längre någon organisation, vill du radera användaren helt?', null, 2).then(res => {
              killUser(res === 'Ja');
            });
          } else {
            killUser();
          }
        },
        null,
        null,
        { size: 'xsm', isGreen: false });
    }

    function add() {
      HyUtils.showDefaultModal('NewUserModalCtrl',
        'resources/user/new/new-user-modal.html',
        {},
        result => {
          scope.users.push(result);
        });
    }

    function deleteBinding(property) {
      Organization.unbindProperty({
        id: scope.organization.id,
        propertyId: property.id,
        userIds: scope.users.map(u => u.id),
      }).$promise.then(result => {
        if (result.ongoingExists) {
          HyUtils.askQuestion('Förhandlingar pågår med denna organisation och fastighet', ['OK'], 1);
        } else {
          getProperties();
        }
      });
    }
  }
}

})();
(function () {
'use strict';

// Manages users belonging to an organization.
//
// Note: Users are not fetched from one but two API-endpoints because
// there are two types of users that can belong to an organization:
//   - Users
//   - Invited users (not having a user account setup yet)
//
// This service is responsible to abstract away the 'two resource'-complexity
// to consumers of this service by returning the two resources as one.

UserByOrganization.$inject = ["$q", "Organization", "ErrorHandler"];
angular.module('hyresgastApp').factory('UserByOrganization', UserByOrganization);

function UserByOrganization($q, Organization, ErrorHandler) {
  var service = {
    query: query,
    delete: destroy
  };

  return service;

  ////////////

  // Users and Invited users are fetched in parallel and returned as
  // one list. Invited users are differentiated to users with a flag
  // named invited set to TRUE
  function query(organizationId, ommitInvitation) {
    var promises = {
      users: queryUser(organizationId),
      usersInvited: ommitInvitation ? [] : queryInvite(organizationId)
    };

    return $q.all(promises).then(function(response) {
      response.usersInvited.map(function(user) {
        user.invited = true;
        return user;
      });
      return response.users.concat(response.usersInvited);
    });
  }

  function destroy(organizationId, userId, invited, killOrphan) {
    if (invited) {
      return deleteInvite(organizationId, userId);
    } else {
      return deleteUser(organizationId, userId, killOrphan);
    }
  }

  function queryUser(organizationId) {
    return Organization.queryUser({ id: organizationId })
      .$promise.then(function(users) {
        return users;
      })
      .catch(ErrorHandler('Misslyckades att hämta användare.'));
  }

  function deleteUser(organizationId, userId, killOrphan) {
    return Organization.deleteUser({
      organizationId: organizationId,
      id: userId,
      killOrphan
    }).$promise.then(function(response) {
      return response;
    }).catch(err => { throw err });
  }

  function queryInvite(organizationId) {
    return Organization.queryInvite({ id: organizationId })
      .$promise.then(function(invites) {
        return invites;
      })
      .catch(ErrorHandler('Misslyckades att hämta organisation.'));
  }

  function deleteInvite(organizationId, userId) {
    return Organization.deleteInvite({
      organizationId: organizationId,
      id: userId
    }).$promise.then(function(response) {
      return response;
    })
      .catch(ErrorHandler('Misslyckades att ta bort organisation.'));
  }
}

})();
(function () {
'use strict';

ShowRealEstateActiveNegotiationsCtrl.$inject = ["$scope", "$document", "$routeParams", "$filter", "$location", "NegotiationTypes", "CombinedNegotiationStates", "NegotiationStates", "Limits", "HyUtils", "RealEstate", "Negotiation", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowRealEstateActiveNegotiationsCtrl', ShowRealEstateActiveNegotiationsCtrl);

function ShowRealEstateActiveNegotiationsCtrl(
  $scope, $document, $routeParams, $filter, $location,
  NegotiationTypes, CombinedNegotiationStates, NegotiationStates, Limits,
  HyUtils,
  RealEstate, Negotiation,
  ErrorHandler) {

  var vm = this;
  vm.Limits = Limits;
  vm.property = null;

  vm.activeStates = {
    NEW: NegotiationStates.NEW,
    IN_PREPARATION: NegotiationStates.IN_PREPARATION,
    SETTLEMENT: NegotiationStates.SETTLEMENT,
    IN_PROGRESS: NegotiationStates.IN_PROGRESS,
    BLOCKED: NegotiationStates.BLOCKED,
    PARTIALLY_BLOCKED: NegotiationStates.PARTIALLY_BLOCKED,
  };

  vm.statusChoices = Object.values(CombinedNegotiationStates)
    .filter(x => ![CombinedNegotiationStates.FINISHED,
      CombinedNegotiationStates.FINISHED_OUTSIDE_PORTAL].includes(x))
    .map(x => ({
      id: x,
      text: $filter('hyCombinedNegotiationStates')(x)
    }));
  vm.negotiationTypeChoices = Object.values(NegotiationTypes).map(x => ({
    id: x,
    text: $filter('hyNegotiationTypes')(x)
  }));

  let getTableau = () => angular.element($document[0].querySelector("#tableauActiveNegotiations"));
  const ffnArrayId = (value, filter) => !!(!filter.length || filter.find(x => x.id === value));
  vm.columns = [
    {
      title: 'Id',
      column: 'caseNumber'
    },
    {
      title: 'Namn',
      column: 'name'
    },
    {
      title: 'Organisation',
      column: 'counterpartOrganization.name'
    },
    {
      title: 'Förhandlingstyp',
      template: 'slot1',
      column: 'type',
      lookup: {
        idProperty: 'id',
        textProperty: 'text',
        dictionary: vm.negotiationTypeChoices,
        multi: true,
      },
      ffn: ffnArrayId,
    },
    {
      title: 'Status',
      template: 'status',
      column: 'combinedState',
      lookup: {
        idProperty: 'id',
        textProperty: 'text',
        dictionary: vm.statusChoices,
        multi: true,
      },
      ffn: ffnArrayId,
    },
    {
      title: 'Ansvarig hos HGF',
      column: 'hgfNegotiator.fullName'
    },
    {
      title: 'Ansvarig hos organisation',
      column: 'extNegotiator.fullName'
    },
    {
      title: '',
      template: 'comment',
      filterType: 'none'
    }
  ];


  $scope.$parent.$watch('vm.property', property => {
    if (property) {
      vm.property = property;
      getTableau().scope().$broadcast('refresh');
    }
  });

  vm.getData = function() {
    // eslint-disable-next-line no-undef, no-unused-vars
    return new Promise((resolve, reject) => {
      if (!vm.property) {
        resolve('no property');
        return;
      }
      var activeStates = Object.values(vm.activeStates);
      RealEstate.queryNegotiationsForProperty({
        id: $routeParams.id,
        newPropertyIds: vm.property.newPropertyIds,
        'filters[state]': activeStates,
      }).$promise.then(function(response) { resolve(response) })
        .catch(ErrorHandler('Misslyckades att hämta förhandlingar.'));
    });
  }

  vm.editComment = negotiation => {
    HyUtils.showDefaultModal('EditCommentModalCtrl',
      'resources/agreement/rentlist/edit/edit-comment-modal.html',
      { comment: negotiation.comment, multipleComments: '' },
      result => {
        if (result.delete || result.comment) {
          negotiation.comment = result.delete ? '' : result.comment;
          Negotiation.saveComments([{
            negotiationId: negotiation.id,
            comment: negotiation.comment,
          }]);
          vm.isDirty = true;
        }
      }, null, null,
      { size: 'md' });
  };

  vm.gotoNegotiation = (negotiation) => {
    $location.search({});
    $location.path('/negotiations/' + negotiation.id);
  };

  vm.getContextInfo = row => ({
    title: `Förhandling ${row.caseNumber}`,
    link: '/negotiations/'+row.id,
  });
}


})();
(function () {
'use strict';

ShowRealEstateFreeAgreementsCtrl.$inject = ["$scope", "$location", "$routeParams", "RealEstate", "FreeAgreementTypes", "FreeAgreement", "HyUtils", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowRealEstateFreeAgreementsCtrl', ShowRealEstateFreeAgreementsCtrl);

function ShowRealEstateFreeAgreementsCtrl($scope, $location, $routeParams,
  RealEstate, FreeAgreementTypes, FreeAgreement,
  HyUtils, ErrorHandler) {
  var vm = this;

  vm.gotoAgreement = gotoAgreement;
  vm.downloadAgreementFile = downloadAgreementFile;
  vm.toggleAttachmentsPane = toggleAttachmentsPane;

  vm.FreeAgreementTypes = Object.values(FreeAgreementTypes)
    .reduce((dict, el) => { dict[el.code] = el.name; return dict; }, {});

  $scope.$parent.$watch('vm.property', property => {
    if (property) {
      getFreeAgreementsForProperty();
    }
  });

  function getFreeAgreementsForProperty() {
    RealEstate.queryFreeAgreementsForProperty({
      id: $routeParams.id,
    })
      .$promise.then(function(response) {
        vm.agreements = response.rows;
        vm.agreementsCount = response.count;

        vm.documents = [];
        response.rows.forEach(agreement => {
          if (agreement.documents) {
            vm.documents.push(...agreement.documents);
          }
        });
      })
      .catch(new ErrorHandler('Misslyckades att hämta avtal.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function gotoAgreement(agreementId) {
    $location.search({});
    $location.path('/agreements/' + agreementId);
  }

  function downloadAgreementFile(file) {
    FreeAgreement.downloadDocument({
      agreementId: file.agreementId,
      id: file.name
    }).$promise.then(function(response) {
      HyUtils.spitOutAttachment(response.data, file.filename);
    });
  }

  function toggleAttachmentsPane() {
    vm.showAgreementsPane = !vm.showAgreementsPane;
  }
}

})();
(function () {
'use strict';

ShowArtifactsCtrl.$inject = ["$routeParams", "$scope", "ObjectTypes"];
angular
  .module('hyresgastApp')
  .controller('ShowArtifactsCtrl', ShowArtifactsCtrl);

function ShowArtifactsCtrl($routeParams, $scope, ObjectTypes) {
  var vm = this;
  vm.objectId = $routeParams.id;
  vm.objectType = ObjectTypes.PROPERTY.name;
  vm.fileLabels = [
    { id: 1, name: 'Allmänna utrymmen' },
    { id: 2, name: 'Lägenheter' },
    { id: 3, name: 'Övrig dokumentation rörande fastigheten' },
  ];

  $scope.$watch('vm.artifactsCount', newValue => {
    if (newValue) {
      let tab = $scope.$parent.vm.tablist.find(x => x.id === 'artifacts');
      if (tab) {
        tab.number = newValue;
      }
    }
  });
}
})();
(function () {
'use strict';

ShowRealEstateApartmentsCtrl.$inject = ["$scope", "$location", "$routeParams", "$timeout", "RealEstate", "DomainModals", "ErrorHandler", "ApartmentStatusTypes"];
angular.module('hyresgastApp').controller('ShowRealEstateApartmentsCtrl', ShowRealEstateApartmentsCtrl);

function ShowRealEstateApartmentsCtrl($scope, $location, $routeParams, $timeout,
  RealEstate,
  DomainModals, ErrorHandler,
  ApartmentStatusTypes) {
  var vm = this;

  vm.pageLimit = 20;
  vm.pageOffset = 0;
  vm.sortColumn = $location.search().order || 'nationalNo'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default
  vm.nh = {
    items: [
      { id: 'nh91', text: '-91' },
      { id: 'nh78', text: '-78' },
      { id: 'nh12', text: '-12' },
    ],
  };
  vm.nh.current = vm.nh.items[0];
  vm.listColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Normhyra', column: 'nh' },
    { title: 'Aktuell hyra', column: 'rent.monthlyRent' },
    { title: 'Hyra gäller fr.o.m', column: 'rent.effectiveFrom' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Hustyp', column: 'buildingType' },
    { title: 'Yta (kvm)', column: 'livingSpace' },
    { title: 'Antal rum', column: 'rooms' },
    { title: 'Notering', column: 'rentFactors' }
  ];

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getPropertyApartments();
    }
  });
  vm.nhTableChange = () => {
    $timeout(() => {
      vm.pageOffset = 0;
      getPropertyApartments();
    }, 200);
  };

  let allTypes = {code:0, name:'', longName:'Alla'};
  vm.selectedStatusType = allTypes;
  vm.apartmentStatusTypes = [allTypes]
    .concat(Object.values(ApartmentStatusTypes));

  vm.statusTooltip = 'Övrig information:<br/>' +
    Object.values(ApartmentStatusTypes).map(t => `${t.name}-${t.longName}`).join('<br/>');

  vm.filterByStatusType = (row) => vm.selectedStatusType.code===0 ||
    row.statusString.includes(vm.selectedStatusType.name);

  vm.getPropertyApartments = getPropertyApartments;
  vm.getMonthlyRent = getMonthlyRent;
  vm.showApartmentHistory = showApartmentHistory;

  vm.property = null;
  $scope.$parent.$watch('vm.property', property => {
    if (property) {
      vm.property = property;
      vm.paginatorGetPropertyApartments();
    }
  });

  vm.setSlice = () => {
    vm.apartmentsSlice = vm.apartments.slice(vm.pageOffset, vm.pageOffset + vm.pageLimit);
  };
  vm.paginatorGetPropertyApartments = () => {
    if (!vm.ready) {
      getPropertyApartments();
    } else vm.setSlice();
  };
  function getPropertyApartments(filterMode) {
    if (filterMode) {
      vm.pageOffset = 0;
    }
    vm.ready = false;
    vm.id = $routeParams.id;
    RealEstate.queryApartmentsInProperty({
      id: vm.id,
      order: (vm.sortColumn === 'nh') ? ('rent.'+vm.nh.current.id) : vm.sortColumn,
      direction: vm.sortDirection,
      'search[nationalNo]': vm.inputNationalNo,
      'search[ownerNo]': vm.inputOwnerNo,
      newPropertyIds: vm.property.newPropertyIds,
    }).$promise.then(response => {
      vm.apartments = response.rows;
      vm.apartments.forEach(apartment => {
        apartment.statusString = Object.values(ApartmentStatusTypes)
          .filter(s => apartment.status & s.code)
          .map(s => s.colouredName || s.name)
          .join(', ');
      });
      vm.setSlice();
      vm.apartmentsCount = response.count;
    }).catch(ErrorHandler('Misslyckades att hämta lägenheter.'))
      .finally(() => { vm.ready = true });
  }

  function showApartmentHistory(apartment) {
    DomainModals.apartmentHistory(apartment);
  }

  function getMonthlyRent(apartment) {
    if (apartment.rent.monthlyRent) {
      return apartment.rent.monthlyRent;
    } else {
      return apartment.rent.yearlyRent / 12;
    }
  }
}

})();
(function () {
'use strict';

ShowBlogCtrl.$inject = ["$routeParams", "$scope", "ObjectTypes"];
angular
  .module('hyresgastApp')
  .controller('ShowBlogCtrl', ShowBlogCtrl);

function ShowBlogCtrl($routeParams, $scope, ObjectTypes) {
  var vm = this;
  vm.objectId = $routeParams.id;
  vm.objectType = ObjectTypes.PROPERTY.code;

  $scope.$watch('vm.count', newValue => {
    if (newValue) {
      let tab = $scope.$parent.vm.tablist.find(x => x.id === 'twits');
      if (tab) {
        tab.number = newValue;
      }
    }
  });
}
})();
(function () {
'use strict';

ShowRealEstateFinishedNegotiationsCtrl.$inject = ["$scope", "$document", "$routeParams", "$filter", "$location", "NegotiationTypes", "CombinedNegotiationStates", "NegotiationStates", "Limits", "HyUtils", "RealEstate", "Negotiation", "ErrorHandler"];
angular
  .module('hyresgastApp')
  .controller('ShowRealEstateFinishedNegotiationsCtrl', ShowRealEstateFinishedNegotiationsCtrl);

function ShowRealEstateFinishedNegotiationsCtrl(
  $scope, $document, $routeParams, $filter, $location,
  NegotiationTypes, CombinedNegotiationStates, NegotiationStates, Limits,
  HyUtils,
  RealEstate, Negotiation,
  ErrorHandler) {

  var vm = this;
  vm.Limits = Limits;
  vm.property = null;

  vm.statuses = [
    NegotiationStates.FINISHED,
    NegotiationStates.FINISHED_OUTSIDE_PORTAL
  ];
  vm.statusChoices = Object.values(CombinedNegotiationStates)
    .filter(x => vm.statuses.includes(x))
    .map(x => ({
      id: x,
      text: $filter('hyCombinedNegotiationStates')(x)
    }));
  vm.negotiationTypeChoices = Object.values(NegotiationTypes).map(x => ({
    id: x,
    text: $filter('hyNegotiationTypes')(x)
  }));

  let getTableau = () => angular.element($document[0].querySelector("#tableauActiveNegotiations"));
  const ffnArrayId = (value, filter) => !!(!filter.length || filter.find(x => x.id === value));
  vm.columns = [
    {
      title: 'Id',
      column: 'caseNumber'
    },
    {
      title: 'Namn',
      column: 'name'
    },
    {
      title: 'Organisation',
      column: 'counterpartOrganization.name'
    },
    {
      title: 'Förhandlingstyp',
      template: 'slot1',
      column: 'type',
      lookup: {
        idProperty: 'id',
        textProperty: 'text',
        dictionary: vm.negotiationTypeChoices,
        multi: true,
      },
      ffn: ffnArrayId,
    },
    {
      title: 'Ansvarig hos HGF',
      column: 'hgfNegotiator.fullName'
    },
    {
      title: 'Ansvarig hos organisation',
      column: 'extNegotiator.fullName'
    },
    {
      title: 'Avslutad datum',
      column: 'finishDate',
      template: 'date1',
    },
    {
      title: '',
      template: 'comment',
      filterType: 'none'
    }
  ];


  $scope.$parent.$watch('vm.property', property => {
    if (property) {
      vm.property = property;
      getTableau().scope().$broadcast('refresh');
    }
  });

  vm.getData = function() {
    // eslint-disable-next-line no-undef, no-unused-vars
    return new Promise((resolve, reject) => {
      if (!vm.property) {
        resolve('no property');
        return;
      }
      RealEstate.queryNegotiationsForProperty({
        id: $routeParams.id,
        newPropertyIds: vm.property.newPropertyIds,
        'filters[state]': Object.values(vm.statuses),
      }).$promise.then(function(response) { resolve(response) })
        .catch(ErrorHandler('Misslyckades att hämta förhandlingar.'));
    });
  }

  vm.editComment = negotiation => {
    HyUtils.showDefaultModal('EditCommentModalCtrl',
      'resources/agreement/rentlist/edit/edit-comment-modal.html',
      { comment: negotiation.comment, multipleComments: '' },
      result => {
        if (result.delete || result.comment) {
          negotiation.comment = result.delete ? '' : result.comment;
          Negotiation.saveComments([{
            negotiationId: negotiation.id,
            comment: negotiation.comment,
          }]);
          vm.isDirty = true;
        }
      }, null, null,
      { size: 'md' });
  };

  vm.gotoNegotiation = (negotiation) => {
    $location.search({});
    $location.path('/negotiations/' + negotiation.id);
  };

  vm.getContextInfo = row => ({
    title: `Förhandling ${row.caseNumber}`,
    link: '/negotiations/'+row.id,
  });
}
})();
(function () {
'use strict';

AddPropertiesCtrl.$inject = ["$scope", "$routeParams", "$location", "Auth", "Negotiation", "PropertyNegotiationTypes", "ErrorHandler"];
angular
  .module('hyresgastApp')
  .controller('AddPropertiesCtrl', AddPropertiesCtrl);

function AddPropertiesCtrl($scope, $routeParams, $location,
  Auth, Negotiation, PropertyNegotiationTypes, ErrorHandler) {
  var vm = this;

  vm.busy = false;
  vm.Auth = Auth;
  vm.selectedApartments = [];
  vm.emptyProperties = [];

  let negotiationTypes = Object.values(PropertyNegotiationTypes);
  let negotiationType = $scope.$parent.vm.negotiation.type
  vm.isPropertyBased = negotiationTypes.includes(negotiationType);

  vm.save = () => {
    // eslint-disable-next-line no-undef
    let newPropertyIds = Array.from(new Set(vm.emptyProperties.map(x => x.id)
      .concat(vm.selectedApartments.map(x => x.property.id))));
    vm.busy = true;
    Negotiation.addProperties({
      id: $routeParams.id,
      newPropertyIds,
    // eslint-disable-next-line no-unused-vars
    }).$promise.then(response => {
      vm.busy = false;
      $location.path('/negotiations/' + $routeParams.id).search('view', 'overview');
    }).catch(() => {
      vm.busy = false;
      new ErrorHandler('Misslyckades att spara förhandling.');
    });
  };
}
})();
(function () {
'use strict';

ShowNegotiationAgreementCtrl.$inject = ["$scope", "$location", "$route", "$routeParams", "$uibModal", "$timeout", "Negotiation", "Agreement", "Blocking", "AgreementTypes", "Parties", "AgreementBlockedByType", "NegotiationTypes", "Auth", "Flash", "ErrorHandler", "HyUtils"];
angular.module('hyresgastApp').controller('ShowNegotiationAgreementCtrl', ShowNegotiationAgreementCtrl);

function ShowNegotiationAgreementCtrl($scope, $location, $route, $routeParams, $uibModal, $timeout,
  Negotiation, Agreement, Blocking,
  AgreementTypes, Parties, AgreementBlockedByType, NegotiationTypes,
  Auth, Flash, ErrorHandler, HyUtils) {

  //this controller was expected to only fetch agreements, however now we fetch blocking and apartment history also
  var vm = this;

  vm.parentCtrlVM = $scope.$parent.vm;
  vm.AgreementBlockedByType = AgreementBlockedByType;
  vm.AgreementTypes = AgreementTypes;
  vm.gotoShowAgreement = gotoShowAgreement;
  vm.gotoShowBlocking = gotoShowBlocking;
  vm.resume = resume;
  vm.createAgreement = createAgreement;
  vm.acceptBlockedAgreement = acceptBlockedAgreement;
  vm.blockAgreement = blockAgreement;
  vm.getBlockingConfirmation = getBlockingConfirmation;

  Auth.heartbeat().then(function() {
    vm.currentUserParty = Auth.currentUser().party;
    if (vm.currentUserParty === Parties.HGF) {
      vm.isHgfUser = true;
    }
  });

  getAgreements();
  getBlockings();

  /////////

  function setPropertySummary() {
    // eslint-disable-next-line no-undef
    const countUniq = arr => Array.from(new Set(arr)).length;
    const propertiesInAgreements = agreements => countUniq(agreements.map(a => a.properties.map(x => x.id)).flat());
    vm.propertySummary = {};
    vm.propertySummary.total = vm.parentCtrlVM.negotiation.realEstateSummary.properties;
    vm.propertySummary.inAgreement = propertiesInAgreements(vm.agreements.filter(a => !a.signed));
    vm.propertySummary.inAgreementFinished = propertiesInAgreements(vm.agreements.filter(a => a.signed && !a.conditionTags.revertId));
    vm.propertySummary.left = vm.propertySummary.total - vm.propertySummary.inAgreement - vm.propertySummary.inAgreementFinished;
  }

  function getAgreements() {
    return Agreement.query({ negotiationId: $routeParams.id })
      .$promise.then(function(response) {
        vm.agreements = response;

        vm.agreements
          .filter(a => a.conditionTags)
          .forEach(a => a.conditionTags = JSON.parse(a.conditionTags));

        vm.agreements.sort((a, b) => a.sequenceNumber - b.sequenceNumber);

        // eslint-disable-next-line no-undef
        const map = new Map();
        vm.agreements.forEach(obj => {
          // eslint-disable-next-line no-undefined
          if (obj.conditionTags.revertSourceId !== undefined) {
            map.set(parseInt(obj.conditionTags.revertSourceId), parseInt(obj.id));
          }
          obj.propertiesArray = obj.properties.map(x => x.propertyDesignation);
        });

        const result = [];
        // eslint-disable-next-line no-undef
        const visited = new Set();

        vm.agreements.forEach(obj => {
          if (!visited.has(obj.id)) {
            result.push(obj);
            visited.add(obj.id);
            if (map.has(obj.id)) {
              const descendantId = map.get(obj.id);
              const descendant = vm.agreements.find(o => o.id === descendantId);
              if (descendant) {
                result.push(descendant);
                visited.add(descendant.id);
              }
            }
          }
        });

        vm.agreements = result;

        $timeout(setPropertySummary, 1000);
      })
      .catch(new ErrorHandler('Misslyckades att hämta överenskommelser.'))
      .finally(function() {
        vm.readyAgreements = true;
      });
  }

  function getBlockings() {
    return Blocking.query({ negotiationId: $routeParams.id })
      .$promise.then(function(response) {
        vm.blockings = response;
      })
      .catch(new ErrorHandler('Misslyckades att hämta strandningar.'))
      .finally(function() {
        vm.readyBlockings = true;
      });
  }

  function refreshNegotiation() {
    return Negotiation.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.parentCtrlVM.negotiation = response;
      })
      .catch(new ErrorHandler('Misslyckades att hämta förhandling.'));
  }

  function resume($event, blocking) {
    // dont want to propagate to parent ng-click which would redirect
    // user to blocking show page
    $event.stopPropagation();

    return Blocking.resume({
      negotiationId: $routeParams.id,
      id: blocking.id
    })
      .$promise.then(function() {
        // Remove item from blockings list
        var index = vm.blockings.indexOf(blocking);
        vm.blockings.splice(index, 1);
        refreshNegotiation();
        Flash.set('Lyckades återuppta strandning', 'success');
      })
      .catch(new ErrorHandler('Misslyckades att återuppta strandning.'));
  }

  function gotoShowAgreement(id) {
    $location.search({});
    $location.path('/negotiations/' + $routeParams.id + '/agreements/' + id);
  }

  function gotoShowBlocking(id) {
    $location.search({});
    $location.path('/negotiations/' + $routeParams.id + '/blockings/' + id);
  }

  vm.cancelAgreement = agreementId => {
    vm.busyRevertingAgreement = true;
    HyUtils.showDefaultModal('WarningConfirmationModalCtrl',
      'components/modal/confirmation/warningConfirmationModal.html',
      {
        message: `<p><b>Annullering av överenskommelsen</b></p>
          <p>Om du väljer att annullera denna överenskommelse kommer den att 
          raderas permanent från databasen. 
          Du kommer därefter att kunna skapa en helt ny överenskommelse från början.</p>
          <p><b>Observera:</b> Annulleringen går inte att ångra!</p>`,
        button: 'Annullera',
        critical: true,
      },
      // eslint-disable-next-line no-unused-vars
      result => {
        $timeout(() => {
          Agreement.cancelAgreement({ agreementId })
            .$promise.then(() => { $route.reload() })
            .catch(() => { Flash.set('Misslyckades att annullera', 'error') });
        }, 300);
      },
      () => { vm.busyRevertingAgreement = false });
  };

  function createAgreement () {
    let negotiation = vm.parentCtrlVM.negotiation;
    let newRealEstate = negotiation.type === NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY ||
      negotiation.type === NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT;
    let negotiationConditionTags = negotiation.conditionTags ? JSON.parse(negotiation.conditionTags) : {}

    if (!newRealEstate && Auth.isExternalUser()) {
      _createAgreement({ isReadyToSend: true });
    } else {
      HyUtils.showDefaultModal('ConditionsModalCtrl',
        'resources/negotiation/show/agreements/modal/conditions.html',
        {
          newRealEstate,
          isReadyToSend: negotiationConditionTags.isReadyToSend,
        },
        result => {
          let conditionTags = {
            isReadyToSend: Auth.isExternalUser() ? true : result.isReadyToSend,
          };
          if (newRealEstate) {
            conditionTags.isPresumtiveRent = result.isPresumtiveRent;
            conditionTags.isInvestmentProperty = result.isInvestmentProperty;
          }
          _createAgreement(conditionTags);
        },
        null, null, { size: 'md' }
      );
    }
  }

  function _createAgreement (conditionTags) {
    vm.savingAgreement = true;
    let agreement = {
      negotiationId: $routeParams.id,
      name: '', // backend requires it
      conditionTags: JSON.stringify(conditionTags),
    };

    Agreement.save(agreement)
      .$promise.then(
        function(response) {
          Flash.set('Överenskommelse har skapats', 'success');
          $location.search({});
          $location.path('/negotiations/' + $routeParams.id + '/agreements/' + response.agreement.id)
        })
      .catch(function() {
        Flash.set('Misslyckades att skapa överenskommelse', 'error')
      })
      .finally(function() {
        vm.savingAgreement = false;
      });
  }

  function acceptBlockedAgreement(blocking) {
    var settings = {
      subtitle: 'Är du säker på att du vill gå vidare?',
      showDropdown: false
    };
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'resources/negotiation/show/agreements/modal/confirmationBlockedModal.html',
      controller: 'ConfirmationBlockedModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      resolve: {
        settings: function() {
          return settings;
        }
      }
    });
    modalInstance.result.then(function() {
      if (vm.isHgfUser) {
        blocking.agreedByAssociation = true;
      } else {
        blocking.agreedByOwner = true;
      }
      Blocking.save(blocking)
        .$promise.then(function() {
          if (blocking.agreedByAssociation && blocking.agreedByOwner) {
            blocking.isAgreed = true;
          }
        })
        .catch(new ErrorHandler('Misslyckades att spara strandning.'));
    });
  }

  function blockAgreement() {
    var settings = {
      subtitle: 'Är du säker på att du vill stranda lägenheter?',
      showDropdown: true
    };

    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'resources/negotiation/show/agreements/modal/confirmationBlockedModal.html',
      controller: 'ConfirmationBlockedModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      resolve: {
        settings: function() {
          return settings;
        }
      }
    });
    modalInstance.result.then(function(blockedBy) {
      let path = vm.parentCtrlVM.negotiation.canSkipRentlist === true ? '/negotiations/' + $routeParams.id + '/blockings/select-property'
        : '/negotiations/' + $routeParams.id + '/blockings/new';
      $location.path(path).search({ blockedBy: blockedBy });
    });
  }

  function getBlockingConfirmation(blocking) {
    vm.busy = true;
    Blocking.getBlockingConfirmation({
      negotiationId: $routeParams.id,
      blockingId: blocking.id,
    }).$promise.then(function(response) {
      let filename = `Bekräftelse_${blocking.id}.pdf`;
      HyUtils.spitOutAttachment(response.data, filename, 'application/pdf');
      vm.busy = false;
    }).catch(
      new ErrorHandler('Misslyckades att hamta bekräftelse PDF.')
    ).finally(() => { vm.busy = false });
  }
}

})();
(function () {
'use strict';

ShowNegotiationArtifactsCtrl.$inject = ["$scope", "$routeParams", "ObjectTypes"];
angular
  .module('hyresgastApp')
  .controller('ShowNegotiationArtifactsCtrl', ShowNegotiationArtifactsCtrl);

function ShowNegotiationArtifactsCtrl($scope, $routeParams, ObjectTypes) {
  var vm = this;
  vm.objectId = $routeParams.id;
  vm.objectType = ObjectTypes.NEGOTIATION.code;
  vm.fileLabels = [
    { id: 1, name: 'Protokoll internt' },
    { id: 2, name: 'Hyreslistor' },
    { id: 3, name: 'Arbetsdokument' },
    { id: 4, name: 'Övriga dokument' },
  ];

  $scope.$watch('vm.count', newValue => {
    if (newValue) {
      let tab = $scope.$parent.vm.tablist.find(x => x.id === 'artifacts');
      if (tab) {
        tab.number = newValue;
      }
    }
  });
}
})();
(function () {
'use strict';

ShowNegotiationBlogCtrl.$inject = ["$scope", "$routeParams", "ObjectTypes"];
angular
  .module('hyresgastApp')
  .controller('ShowNegotiationBlogCtrl', ShowNegotiationBlogCtrl);

function ShowNegotiationBlogCtrl($scope, $routeParams, ObjectTypes) {
  var vm = this;
  vm.objectId = $routeParams.id;
  vm.objectType = ObjectTypes.NEGOTIATION.code;

  $scope.$watch('vm.count', newValue => {
    if (newValue) {
      let twitsTab = $scope.$parent.vm.tablist.find(x => x.id === 'twits');
      if (twitsTab) {
        twitsTab.number = newValue;
      }
    }
  });
}
})();
(function () {
'use strict';

ShowNegotiationHistoryCtrl.$inject = ["$scope", "$location", "$routeParams", "Negotiation", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationHistoryCtrl', ShowNegotiationHistoryCtrl);

function ShowNegotiationHistoryCtrl($scope, $location, $routeParams, Negotiation, ErrorHandler) {
  // Don't want client to break if API returns new types
  // we have not created a html partial for
  var ALLOWED_TYPES = ['agreement', 'meeting', 'negotiation', 'negotiationParticipant', 'rentFile', 'property', 'apartment'];

  var vm = this;
  vm.isAllowed = isAllowed;

  queryHistory();

  /////////

  function queryHistory() {
    vm.ready = false;

    return Negotiation.queryHistory(
      {
        id: $routeParams.id
      },
      {}
    )
      .$promise.then(function(response) {
        vm.events = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta historik.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function isAllowed(type) {
    return ALLOWED_TYPES.indexOf(type) > -1;
  }
}

})();
(function () {
'use strict';

ShowNegotiationMeetingsCtrl.$inject = ["$scope", "$uibModal", "$location", "$routeParams", "Flash", "Negotiation", "ErrorHandler"];
angular.module('uiTimepicker').controller('ShowNegotiationMeetingsCtrl', ShowNegotiationMeetingsCtrl);

function ShowNegotiationMeetingsCtrl($scope, $uibModal, $location, $routeParams, Flash, Negotiation, ErrorHandler) {
  var vm = this;
  vm.addMeeting = addMeeting;
  vm.gotoShowMeeting = gotoShowMeeting;
  var parentCtrlVM = $scope.$parent.vm;
  getMeetings();

  /////////

  function gotoShowMeeting(id) {
    $location.search({});
    $location.path('/negotiations/' + $routeParams.id + '/meetings/' + id);
  }

  function getMeetings() {
    vm.readyMeetings = false;
    return Negotiation.listMeetings({
      id: $routeParams.id
    })
      .$promise.then(function(response) {
        vm.meetings = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta möten.'))
      .finally(function() {
        vm.readyMeetings = true;
      });
  }

  function addMeeting() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'lg',
      index: 1075, // position above tooltip
      templateUrl: 'resources/meeting/edit/edit-meetings-modal.html',
      controller: 'EditMeetingModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      backdrop: 'static',
      resolve: {
        meetingObj: function() {
          return null;
        },
        negotiationObj: function() {
          return parentCtrlVM.negotiation;
        }
      }
    });
    modalInstance.result.then(function(meeting) {
      $location.search({});
      $location.path('/negotiations/' + $routeParams.id + '/meetings/' + meeting.id);
    });
  }
}

})();
(function () {
'use strict';

ShowNegotiationMotivCtrl.$inject = ["$scope", "$location", "$timeout", "$uibModal", "$routeParams", "Auth", "Flash", "Negotiation", "RealEstate", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationMotivCtrl', ShowNegotiationMotivCtrl);

function ShowNegotiationMotivCtrl(
  $scope,
  $location,
  $timeout,
  $uibModal,
  $routeParams,
  Auth,
  Flash,
  Negotiation,
  RealEstate,
  ErrorHandler
) {
  // ------------------------------------------------------------------------------------------------------------
  // In many places in this file, we deal with objects that can be either a property (fastighet) or an apartment.
  // For historical reasons, in most places the variables used are named as if these objects are always properties.
  // ------------------------------------------------------------------------------------------------------------
  var vm = this;
  vm.parentCtrl = $scope.$parent.vm;
  vm.selectedPropertyIds = {};
  vm.readyOwners = {};
  vm.getPropertyOwners = getPropertyOwners;
  vm.editDemandAndMotivation = editDemandAndMotivation;
  vm.realEstates = [];
  vm.queryRealEstate = queryRealEstate;

  vm.pageLimit = 30;
  vm.currentPage = 0;
  vm.firstRecordInCurrentPage = 1;

  vm.sortColumn = $location.search().order || 'propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  $scope.$on('$routeUpdate', function () {
    if ($location.search().order) {
      // reset offset when sorting
      queryRealEstate();
    }
  });

  vm.parentCtrl.negotiationQueryPromise.then(function () {
    if (vm.parentCtrl.apartmentMode) {
      vm.listColumns = [{
          title: 'Fastighetsbeteckning',
          column: 'propertyDesignation'
        },
        {
          title: 'Gatuaddress',
          column: 'streetAddress'
        },
        {
          title: 'Nationella lägenhetsnr',
          column: 'nationalNo'
        },
        {
          title: 'Lägenhetsnr',
          column: 'ownerNo'
        },
        {
          title: 'Kommun',
          column: 'municipality'
        },
        {
          title: 'Fastighetsägare',
          column: 'owners',
          type: 'array'
        }
      ];
    } else {
      vm.listColumns = [{
          title: 'Fastighetsbeteckning',
          column: 'propertyDesignation'
        },
        {
          title: 'Kommun',
          column: 'municipality'
        },
        {
          title: 'Antal lägenheter',
          column: 'numNegotiatedApartments'
        },
        {
          title: 'Fastighetsägare',
          column: 'owners',
          type: 'array'
        }
      ];
    }

    return queryRealEstate();
  });

  ////////////

  function closeAllOwnersLists() {
    for (var key in vm.toggleOwnersList) {
      vm.toggleOwnersList[key] = false;
    }
  }

  function queryRealEstate(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.pageOffset = 0;
    }
    closeAllOwnersLists();

    if (vm.parentCtrl.apartmentMode) {
      return Negotiation.queryApartment({
          id: $routeParams.id,
          order: vm.sortColumn,
          direction: vm.sortDirection,
          'search[property.propertyDesignation]': vm.inputTextProperty,
          'search[streetAddress]': vm.inputTextStreetAddress,
          'search[nationalNo]': vm.inputTextNationalNumber,
          'search[ownerNo]': vm.inputTextApartmentNumber,
          'search[municipality]': vm.inputTextMunicipality,
          'search[ownerName]': vm.inputTextOwner,
          include: ['demands', 'owners']
        }, {})
        .$promise.then(function (response) {
          vm.realEstates = response.rows;
          vm.totalCount = response.count;
          // vm.rentFile = response.rentFile;
          initializeCheckboxList();
        })
        .catch(ErrorHandler('Misslyckades att hämta lägenheter.'))
        .finally(function () {
          vm.ready = true;
          vm.readyProgressCircular = true;
        });
    } else {
      return Negotiation.queryProperty({
          id: $routeParams.id,
          order: vm.sortColumn,
          direction: vm.sortDirection,
          'search[propertyDesignation]': vm.inputTextProperty,
          'search[municipality]': vm.inputTextMunicipality,
          'search[district]': vm.inputTextDistrict,
          'search[ownerName]': vm.inputTextOwner,
          include: ['demands', 'owners']
        }, {})
        .$promise.then(function (response) {
          vm.realEstates = response.rows;
          vm.totalCount = response.count;
          initializeCheckboxList();
        })
        .catch(ErrorHandler('Misslyckades att hämta fastigheter.'))
        .finally(function () {
          vm.ready = true;
          vm.readyProgressCircular = true;
        });
    }
  }

  function getPropertyOwners(propertyId) {
    var property = vm.realEstates.filter(function (property) {
      return property.id === propertyId;
    })[0];

    if (!property.owners) {
      vm.readyOwners[property.id] = false;
      return RealEstate.get({
        id: propertyId
      }, {})
        .$promise.then(function (response) {
          property.owners = response.owners;
        })
        .catch(ErrorHandler('Misslyckades att hämta fastighetsägare.'))
        .finally(function () {
          vm.readyOwners[propertyId] = true;
        });
    }
  }

  function initializeCheckboxList() {
    vm.realEstates.forEach(function (property) {
      vm.selectedPropertyIds[property.id] = true;
    });
    vm.allPropertiesBoxChecked = true;
  }

  function editDemandAndMotivation() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'md',
      index: 1075, // position above tooltip
      templateUrl: 'resources/negotiation-call/new/modals/edit-motivation-modal.html',
      controller: 'EditMotivationModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      resolve: {
        propertiesArr: function () {
          var properties = vm.realEstates.filter(function (property) {
            return vm.selectedPropertyIds[property.id] === true;
          });
          return properties;
        },
        negotiationType: function() {
          return vm.parentCtrl.negotiation.type;
        }
      }
    });
    modalInstance.result.then(function (demandAndMotivation) {
      vm.realEstates
        .filter(function (property) {
          return vm.selectedPropertyIds[property.id] === true;
        })
        .forEach(function (property) {
          angular.extend(property, {
            demand: demandAndMotivation
          });
          vm.selectedPropertyIds[property.id] = false;
          vm.allPropertiesBoxChecked = false;
        });
    });
  }

  vm.computeLastRecord = computeLastRecord;

  function computeLastRecord() {
    if (vm.totalCount - vm.firstRecordInCurrentPage < vm.pageLimit - 1) {
      return vm.totalCount;
    } else {
      return vm.firstRecordInCurrentPage + vm.pageLimit - 1;
    }
  }

  vm.toggleAllPropertiesSelection = toggleAllPropertiesSelection;

  function toggleAllPropertiesSelection() {
    vm.realEstates.forEach(function (property) {
      if (vm.selectedPropertyIds[property.id] !== null) {
        vm.selectedPropertyIds[property.id] = vm.allPropertiesBoxChecked;
      }
    });
  }

  vm.updateAllPropertiesBoxState = updateAllPropertiesBoxState;

  function updateAllPropertiesBoxState() {
    // auto select master checkbox if all other checkboxes are selected
    // deselect master checkbox if at least one checkbox is not selected
    vm.allPropertiesBoxChecked = Object.keys(vm.selectedPropertyIds)
      .map(function (propertyId) {
        return vm.selectedPropertyIds[propertyId] === true || vm.selectedPropertyIds[propertyId] === null;
      })
      .every(function (isSelected) {
        return isSelected;
      });
  }

  vm.atLeastOneSelectedPropertyId = atLeastOneSelectedPropertyId;

  function atLeastOneSelectedPropertyId() {
    return Object.keys(vm.selectedPropertyIds)
      .map(function (propertyId) {
        return vm.selectedPropertyIds[propertyId] === true;
      })
      .some(function (isSelected) {
        return isSelected;
      });
  }

  vm.saveProperties = saveProperties;

  function saveProperties() {
    vm.saving = true;

    return Negotiation.savePropertyDemands({
      id: $routeParams.id
    }, vm.realEstates)
      .$promise.then(
        function (response) {
          Flash.set('Yrkande och motiv har sparats', 'success');
          return vm.parentCtrl.negotiationQueryPromise;
        })
      .catch(ErrorHandler('Misslyckades att hämta fastigheter.'))
      .finally(function () {
        vm.parentCtrl.getNegotiation();
        vm.currentPage = 0;
        vm.firstRecordInCurrentPage = 1;
        vm.saving = false;
        vm.editMode = !vm.editMode;
      });
  }

  //Custom pagination controlls for this view.
  vm.goToPrevPage = goToPrevPage;
  vm.goToNextPage = goToNextPage;

  function goToPrevPage() {
    vm.currentPage = vm.currentPage - 1;
    vm.firstRecordInCurrentPage = vm.currentPage * vm.pageLimit + 1;
  }

  function goToNextPage() {
    vm.currentPage = vm.currentPage + 1;
    vm.firstRecordInCurrentPage = vm.currentPage * vm.pageLimit + 1;
  }
}
})();
(function () {
'use strict';

ShowNegotiationOverviewCtrl.$inject = ["$scope", "$location", "$routeParams", "$timeout", "$document", "Negotiation", "Auth", "HyUtils", "ExportUtils", "FeatureConfiguration", "NegotiationStates", "NegotiationTypes", "Parties", "Limits", "CommonIds"];
angular.module('hyresgastApp').controller('ShowNegotiationOverviewCtrl', ShowNegotiationOverviewCtrl);

function ShowNegotiationOverviewCtrl(
  $scope, $location, $routeParams, $timeout, $document,
  Negotiation,
  Auth, HyUtils, ExportUtils,
  FeatureConfiguration, NegotiationStates, NegotiationTypes, Parties, Limits, CommonIds) {
  var vm = this;
  vm.parentCtrl = $scope.$parent.vm;

  vm.NegotiationStates = NegotiationStates;
  vm.Parties = Parties;
  vm.Limits = Limits;
  vm.FeatureConfiguration = FeatureConfiguration;

  vm.edit = edit;
  vm.editDelegates = editDelegates;
  vm.editNegotiation = editNegotiation;
  vm.moveItems = moveItems;
  vm.recallItems = recallItems;
  vm.gotoProperty = gotoProperty;

  vm.agreementStatuses = [
    { id: 0, name: 'Kvar att förhandla' },
    { id: 1, name: 'Pågående FÖK' },
    { id: 2, name: 'Överens' },
  ];

  vm.selectedRows = [];
  vm.pageLimit = 15;
  vm.pageOffset = 0;
  vm.attributesProperties = [
    {
      fn: row => row.agreementStatus === 2,
      legend: vm.agreementStatuses[2].name,
      class: 'table__tr--slime table__tr--border-reset',
      disabled: true,
    },
    {
      fn: row => row.agreementStatus === 1,
      legend: vm.agreementStatuses[1].name,
      class: 'table__tr--grey table__tr--border-reset',
      disabled: true,
    },
    {
      fn: row => !row.active,
      legend: 'Inaktiv',
      class: 'table__tr--inactive',
    },
  ];
  vm.actionsProperties = [
    {
      title: 'Lägg till',
      common: true,
      action: () => { vm.parentCtrl.addItems() },
    },
    {
      title: 'Flytta till',
      action: () => { moveItems(false) },
    },
    {
      btnClass: 'btn--icon btn-fu btn--icon-bin',
      action: () => { recallItems() },
    },
  ];
  vm.actionsApartments = [
    {
      title: 'Flytta till',
      action: () => { moveItems(true) },
    },
  ];

  vm.isNewProduction = type =>
    [NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY, NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT].includes(type);

  let getTableau = () => angular.element($document[0].querySelector("#negotiationTableauProperties"));
  let refreshTableau = () => getTableau().scope().$broadcast('refresh');

  $scope.$on('rentListImported', function() {
    refreshTableau();
  });

  Auth.heartbeat().then(function() {
    if (Auth.currentUser().party === Parties.HGF) {
      vm.isHgfUser = true;
    }
  });

  function gotoProperty(item) {
    if (!vm.apartmentMode) {
      $location.search({});
      $location.path('/negotiations/' + $routeParams.id + '/properties/' + item.id);
    }
  }

  vm.parentCtrl.negotiationQueryPromise.then(function() {
    vm.columnsProperties = [
      {
        title: 'Överensstatus',
        column: 'agreementStatus',
        lookup: {
          dictionary: vm.agreementStatuses,
          idProperty: 'id',
          textProperty: 'name',
        },
        ffn: (value, filter) => [value, CommonIds.tableauAllId].includes(filter.id),
      },
      {
        title: 'Fastighetsbeteckning',
        column: 'propertyDesignation'
      },
      {
        title: 'Kommun',
        column: 'municipality'
      },
      {
        title: 'LM distrikt',
        column: 'district'
      },
      {
        title: 'Antal lägenheter',
        column: 'numNegotiatedApartments'
      },
      {
        title: 'Fastighetsägare',
        column: 'owners',
        type: 'array',
      }
    ];

    vm.columnsApartments = [
      {
        title: 'Fastighetsbeteckning',
        column: 'propertyDesignation'
      },
      {
        title: 'Gatuaddress',
        column: 'streetAddress'
      },
      {
        title: 'Nationella lägenhetsnr',
        column: 'nationalNo'
      },
      {
        title: 'Lägenhetsnr',
        column: 'ownerNo'
      },
      {
        title: 'Kommun',
        column: 'municipality'
      },
    ];
  });

  vm.bestandTabs = [
    { title: 'Fastigheter', key: 'properties' },
    { title: 'Lägenheter', key: 'apartments' },
  ];

  /**
   *
   * @param {string} modalSize size css class for modal
   * @param {int} userParty party to edit. 20 for HGF, 0 for FÄ
   */
  function edit(size, userParty) {
    HyUtils.showDefaultModal('EditNegotiationRepresentativesModalCtrl',
      'resources/negotiation/edit/edit-representatives-modal.html', {
        negotiationObject: vm.parentCtrl.negotiation,
        isFreeAgreement: false,
        userParty,
      }, () => {
        $timeout(() => vm.parentCtrl.getNegotiation(), 1200);
      }, null, null, { size }
    );
  }

  function editDelegates() {
    HyUtils.showDefaultModal('EditNegotiationDelegatesModalCtrl',
      'resources/negotiation/edit/edit-delegates-modal.html', {
        negotiationObject: vm.parentCtrl.negotiation,
        isFreeAgreement: false,
      }, () => {
        vm.parentCtrl.getNegotiation();
      });
  }

  function editNegotiation() {
    HyUtils.showDefaultModal('EditNegotiationTypeModalCtrl',
      'resources/negotiation/edit/edit-negotiationType-modal.html', {
        negotiationObject: vm.parentCtrl.negotiation,
        isFreeAgreement: false,
      }, (negotiation) => {
        vm.parentCtrl.negotiation.name = negotiation.name;
        vm.parentCtrl.negotiation.type = negotiation.type;
      });
  }

  vm.fGetProperties = () => {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      let params = {
        id: $routeParams.id,
        include: ['demands', 'owners'],
      };
      let bodyParams = { showInactive: true, getAgreementStatus: true };

      let request = Negotiation.queryProperty(params, bodyParams);

      request.$promise.then(response => { resolve(response) });
    });
  }

  vm.fGetApartments = () => {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {

      let params = {
        id: $routeParams.id,
        include: ['demands', 'owners'],
      };
      let bodyParams = { };

      let request = Negotiation.queryApartment(params, bodyParams);

      request.$promise.then(response => {
        // eslint-disable-next-line no-undef
        let propertyIds = Array.from(new Set(response.rows.map(row => row.property.id)));
        vm.properties = propertyIds.map(id => response.rows.find(row => row.property.id === id).property.propertyDesignation);

        resolve(response);
      });
    });
  }

  let getSelectedIds = () => vm.selectedRows.map(row => row.id);

  function moveItems(apartmentMode) {
    HyUtils.showDefaultModal('MoveItemsCtrl',
      'resources/negotiation/show/modals/move-items/moveItemsModal.html', {
        itemIds: getSelectedIds(),
        currentNegotiation: vm.parentCtrl.negotiation,
        apartmentMode,
      }, () => {
        refreshTableau();
        vm.parentCtrl.getNegotiation();
      }
    );
  }

  function recallItems() {
    HyUtils.showDefaultModal('RecallItemsCtrl',
      'resources/negotiation/show/modals/recall-items/recallItemsModal.html', {
        itemIds: getSelectedIds(),
        negotiationId: parseInt($routeParams.id),
        apartmentMode: vm.parentCtrl.apartmentMode,
      }, () => {
        refreshTableau();
        vm.parentCtrl.getNegotiation();
      }
    );
  }

  vm.exportRealEstate = () => {
    let sheetData = getTableau().isolateScope().data.map(x => ({
      'Forhandling': vm.parentCtrl.negotiation.caseNumber,
      'Fastighetsbeteckning': x.propertyDesignation,
      'Fastighetsägare': x.owners[0],
      'Antal lägenheter': x.numApartmentsInAgreement,
      'Överensstatus': vm.agreementStatuses[x.agreementStatus].name,
      'Kommun': x.municipality,
      'LM Distrikt': x.district,
    }));

    let buffer = ExportUtils.prettyExcel(sheetData);

    HyUtils.spitOutAttachment(buffer, `Fastighetsbestånd i förhandling ${vm.parentCtrl.negotiation.caseNumber}.xlsx`, 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet');
  };

  vm.getContextInfo = row => ({
    title: `Fastighet ${row.propertyDesignation}`,
    link: '/properties/'+row.id,
  });
}

})();
(function () {
'use strict';

AgreementsListCtrl.$inject = ["$location", "Agreement"];
angular.module('hyresgastApp').controller('AgreementsListCtrl', AgreementsListCtrl);

function AgreementsListCtrl($location,
  Agreement) {
  var vm = this;

  vm.tableColumns = [
    {
      title: 'ID',
      column: 'id',
    },
    {
      title: 'Text',
      column: 'name',
    },
    {
      title: 'Antal fastigheter',
      column: 'numProperties',
    },
    {
      title: 'Antal lägenheter',
      column: 'numApartments',
    },
    {
      title: 'Ansvarig hos HGF',
      column: 'hgfNegotiator.fullName',
    },
    {
      title: 'Ansvarig hos organisation',
      column: 'extNegotiator.fullName',
    },
    {
      title: 'Signerad',
      column: 'signed',
      type: 'boolean',
    },
    {
      title: 'Skapad den',
      column: 'createdAt',
      type: 'date',
    },
  ];

  vm.tableFGetData = Agreement.find;

  vm.gotoAgreement = agreement => {
    $location.search({});
    $location.path('/negotiations/' + agreement.negotiationId + '/agreements/' + agreement.id);
  };

  vm.getContextInfo = row => ({
    title: `Överenskommelse ${row.id}`,
    link: '/negotiations/' + row.negotiationId + '/agreements/' + row.id,
  });
}
})();
(function () {
'use strict';

ShowNegotiationPropertyCtrl.$inject = ["$filter", "$location", "$routeParams", "$scope", "DomainModals", "HyUtils", "Negotiation", "RealEstate", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationPropertyCtrl', ShowNegotiationPropertyCtrl);

function ShowNegotiationPropertyCtrl(
  $filter, $location, $routeParams, $scope,
  DomainModals, HyUtils, Negotiation, RealEstate,
  ErrorHandler) {
  var vm = this;
  vm.pageLimit = 30;
  vm.pageOffset = 0;
  vm.sortColumn = $location.search().order || 'nationalNo'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default
  vm.listColumns = [
    { title: 'Nationellt lägenhetsnr', column: 'nationalNo' },
    { title: 'Lägenhetsnr', column: 'ownerNo' },
    { title: 'Årshyra', column: 'currentYearlyRent' },
    { title: 'Månadshyra', column: 'currentMonthlyRent' },
    { title: 'Gatuadress', column: 'streetAddress' },
    { title: 'Yta (kvm)', column: 'livingSpace' },
    { title: 'Antal rum', column: 'rooms' },
    { title: 'Kökstyp', column: 'kitchenType' },
    { title: 'Notering', column: 'rentFactors' }
  ];

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getPropertyApartments();
    }
  });

  vm.getPropertyApartments = getPropertyApartments;
  vm.showApartmentHistory = showApartmentHistory;

  getProperty();

  ////////////

  function getProperty() {
    return RealEstate.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.property = response;
        getPropertyApartments();
        setFormattedPropsForTooltip();
      })
      .catch(ErrorHandler('Misslyckades att hämta fastigheten.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function showApartmentHistory(apartment) {
    DomainModals.apartmentHistory(apartment);
  }

  function getPropertyApartments(filterMode) {
    vm.ready = false;
    if (filterMode) {
      vm.pageOffset = 0;
    }
    Negotiation.queryApartmentsInProperty({
      negotiationId: $routeParams.negotiationId,
      id: $routeParams.id,
      limit: vm.pageLimit,
      offset: vm.pageOffset,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      newPropertyIds: vm.property.newPropertyIds,
      'search[nationalNo]': vm.inputNationalNo,
      'search[ownerNo]': vm.inputOwnerNo
    })
      .$promise.then(function(response) {
        vm.apartments = response.rows;
        vm.apartmentsCount = response.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta lägenheter.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function setFormattedPropsForTooltip() {
    if(!vm.property) return;

    if(vm.property.address || vm.property.addresses) {
      vm.formattedAddresses = ([vm.property.address]
        .concat(vm.property.addresses))
        .filter(x => x)
        .map(a => HyUtils.renderStreetAddress(a));
    }

    if(vm.property.landlords) {
      vm.formattedLandlords = vm.property.landlords.map(function(landlord) {
        return landlord.name;
      });
    }
  }

  vm.formatNumber = formatNumber;

  function formatNumber(num) {
    if (!num) {
      return 'Ej angivet';
    }
    return $filter('number')(num, 0);
  }

  /**
   * Gets price class
   * @property {number}
   * @property {number}
   * @returns {string} css class
   */
  vm.priceColour = (newPrice, currentPrice) => {
    let colour = 'price-same';
    if (!newPrice) {
      return colour;
    }
    if (newPrice > currentPrice) {
      colour = 'price-up';
    } else if (currentPrice > newPrice) {
      colour = 'price-down';
    }
    return colour;
  };
}

})();
(function () {
'use strict';

FreeListCtrl.$inject = ["$document", "$location", "$timeout", "FreeAgreement"];
angular.module('hyresgastApp').controller('FreeListCtrl', FreeListCtrl);

function FreeListCtrl($document, $location, $timeout,
  FreeAgreement) {
  var vm = this;

  vm.tableColumns = [
    {
      title: 'ID',
      column: 'id',
    },
    {
      title: 'Text',
      column: 'name',
    },
    {
      title: 'Antal fastigheter',
      column: 'numProperties',
    },
    {
      title: 'Antal lägenheter',
      column: 'numApartments',
    },
    {
      title: 'Ansvarig hos HGF',
      column: 'hgfNegotiator.fullName',
    },
    {
      title: 'Ansvarig hos organisation',
      column: 'extNegotiator.fullName',
    },
    {
      title: 'Signerad',
      column: 'signed',
      type: 'boolean',
    },
    {
      title: 'Skapad den',
      column: 'createdAt',
      type: 'date',
    },
  ];
  vm.tableFGetData = FreeAgreement.find;

  vm.gotoFreeAgreement = agreement => {
    $location.search({});
    $location.path('/agreements/' + agreement.id);
  };

  vm.getContextInfo = row => ({
    title: `Avtal ${row.id}`,
    link: '/agreements/' + row.id,
  });
}
})();
(function () {
'use strict';

ShowNegotiationCallNegotiationsCtrl.$inject = ["$scope", "$routeParams", "$location", "NegotiationCall", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationCallNegotiationsCtrl', ShowNegotiationCallNegotiationsCtrl);

function ShowNegotiationCallNegotiationsCtrl($scope, $routeParams, $location, NegotiationCall, ErrorHandler) {
  var vm = this;
  vm.gotoShow = gotoShow;

  vm.negotiationsPageLimit = 30;
  vm.negotiationsPageOffset = 0; // always show first items
  vm.queryNegotiations = queryNegotiations;
  vm.sortColumn = $location.search().order || 'createdAt'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default
  vm.listColumns = [
    { title: 'Id', column: 'caseNumber' },
    { title: 'Namn', column: 'name' },
    { title: 'Kontor HGF', column: 'office.name' },
    { title: 'Status', column: 'state' },
    { title: 'Skapad den', column: 'createdAt' }
  ];

  queryNegotiations();

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.negotiationsPageOffset = 0;
      queryNegotiations();
    }
  });

  /////////

  function gotoShow(id) {
    $location.search({});
    $location.path('/negotiations/' + id);
  }

  function queryNegotiations(filterMode) {
    vm.readyNegotiations = false;
    if (filterMode) {
      vm.negotiationsPageOffset = 0;
    }
    return NegotiationCall.queryNegotiation(
      {
        id: $routeParams.id,
        limit: vm.negotiationsPageLimit,
        offset: vm.negotiationsPageOffset,
        order: vm.sortColumn,
        direction: vm.sortDirection,
        'search[name]': vm.inputTextNegotiations
      },
      {}
    )
      .$promise.then(function(response) {
        vm.negotiations = response.rows;
        vm.negotiationsTotalCount = response.count;

        // Save initial count of records, to be distingueshed from the current count after
        // unsuccessful filtering. This will affect all messages, which are dependent on totalCount = 0.
        if (filterMode !== true) {
          vm.initialTotalCount = vm.negotiationsTotalCount;
        }
      })
      .catch(ErrorHandler('Misslyckades att hämta förhandling.'))
      .finally(function() {
        vm.readyNegotiations = true;
      });
  }
}

})();
(function () {
'use strict';

ShowNegotiationCallPropertiesCtrl.$inject = ["$scope", "$location", "$routeParams", "NegotiationCall", "RealEstate", "Auth", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationCallPropertiesCtrl', ShowNegotiationCallPropertiesCtrl);

function ShowNegotiationCallPropertiesCtrl($scope, $location, $routeParams, NegotiationCall, RealEstate, Auth, ErrorHandler) {
  var vm = this;
  vm.readyOwners = {};
  vm.parentCtrl = $scope.$parent.vm;
  vm.toggleOwnersList = {};
  vm.propertiesPageLimit = 30;
  vm.propertiesPageOffset = 0; // always show first items
  vm.queryProperties = queryProperties;
  vm.parentCtrl.negotiationCallPromise.then(function() {
    queryProperties();
    if (vm.parentCtrl.isPropertyBased) {
      vm.getPropertyOwners = getPropertyOwners;
      vm.sortColumn = $location.search().order || 'propertyDesignation'; // default
      vm.sortDirection = $location.search().direction || 'ASC'; // default
      vm.listColumns = [
        { title: 'Fastighetsbeteckning', column: 'propertyDesignation' },
        { title: 'Kommun', column: 'municipality' },
        { title: 'LM distrikt', column: 'district' },
        { title: 'Antal lägenheter', column: 'numNegotiatedApartments' },
        { title: 'Fastighetsägare', column: 'owners' }
      ];
    } else {
      vm.sortColumn = $location.search().order || 'propertyDesignation'; // default
      vm.sortDirection = $location.search().direction || 'ASC'; // default
      vm.listColumns = [
        { title: 'Fastighetsbeteckning', column: 'propertyDesignation' },
        { title: 'Gatuadress', column: 'streetAddress' },
        { title: 'Nationella lägenhetsnr', column: 'nationalNo' },
        { title: 'Lägenhetsnr', column: 'ownerNo' },
        { title: 'Kommun', column: 'municipality' },
        { title: 'Fastighetsägare', column: 'owners' }
      ];
    }
  });
  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.propertiesPageOffset = 0;
      queryProperties();
    }
  });

  /////////

  function queryProperties(filterMode) {
    if (filterMode) {
      vm.propertiesPageOffset = 0;
    }
    if (vm.parentCtrl.isPropertyBased || vm.parentCtrl.negotiationCall.isNewlyConstructedApartment) {
      vm.readyProperties = false;
      return NegotiationCall.queryProperty(
        {
          id: $routeParams.id,
          limit: vm.propertiesPageLimit,
          offset: vm.propertiesPageOffset,
          order: vm.sortColumn,
          direction: vm.sortDirection,
          'search[propertyDesignation]': vm.inputTextProperty,
          'search[municipality]': vm.inputTextMunicipality,
          'search[district]': vm.inputTextDistrict,
          'search[ownerName]': vm.inputTextOwner,
          include: ['owners', 'demands']
        },
        {}
      )
        .$promise.then(function(response) {
          vm.properties = response.rows;
          vm.propertyTotalCount = response.count;
          vm.rentFile = response.rentFile;
        })
        .catch(ErrorHandler('Misslyckades att hämta fastighet.'))
        .finally(function() {
          vm.readyProperties = true;
        });
    } else {
      vm.readyApartments = false;
      return NegotiationCall.queryApartment(
        {
          id: $routeParams.id,
          limit: vm.propertiesPageLimit,
          offset: vm.propertiesPageOffset,
          order: vm.sortColumn,
          direction: vm.sortDirection,
          'search[propertyDesignation]': vm.inputTextProperty,
          'search[streetAddress]': vm.inputTextStreetAddress,
          'search[nationalNo]': vm.inputTextNationalNumber,
          'search[ownerNo]': vm.inputTextApartmentNumber,
          'search[municipality]': vm.inputTextMunicipality,
          'search[ownerName]': vm.inputTextOwner,
          include: ['owners', 'demands']
        },
        {}
      )
        .$promise.then(function(response) {
          vm.apartments = response.rows;
          vm.apartmentsTotalCount = response.count;
          vm.rentFile = response.rentFile;
        })
        .catch(ErrorHandler('Misslyckades att hämta lägenhet.'))
        .finally(function() {
          vm.readyApartments = true;
        });
    }
  }

  function getPropertyOwners(propertyId) {
    var property = vm.properties.filter(function(property) {
      return property.id === propertyId;
    })[0];
    if (!property.owners) {
      vm.readyOwners[property.id] = false;
      return RealEstate.get(
        {
          id: propertyId
        },
        {}
      )
        .$promise.then(function(response) {
          property.owners = response.owners;
        })
        .catch(ErrorHandler('Misslyckades att hämta fastighetsägare.'))
        .finally(function() {
          vm.readyOwners[propertyId] = true;
        });
    }
  }
}

})();
(function () {
'use strict';

ShowNegotiationCallRemainingCtrl.$inject = ["$scope", "$location", "$routeParams", "NegotiationCall", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationCallRemainingCtrl', ShowNegotiationCallRemainingCtrl);

function ShowNegotiationCallRemainingCtrl($scope, $location, $routeParams, NegotiationCall, ErrorHandler) {
  var vm = this;

  vm.propertiesPageLimit = 30;
  vm.propertiesPageOffset = 0; // always show first items
  vm.queryProperties = queryProperties;
  vm.sortColumn = $location.search().order || 'propertyDesignation'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default
  vm.listColumns = [
    { title: 'Fastighetsbeteckning', column: 'propertyDesignation' },
    { title: 'Kommun', column: 'municipality' },
    { title: 'LM distrikt', column: 'district' },
    { title: 'Antal lägenheter', column: 'numNegotiatedApartments' }
  ];

  queryProperties();

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.propertiesPageOffset = 0;
      queryProperties();
    }
  });

  /////////

  function queryProperties(resetOffset) {
    vm.readyProperties = false;
    if (resetOffset) {
      vm.propertiesPageOffset = 0;
    }
    return NegotiationCall.queryProperty(
      {
        id: $routeParams.id,
        limit: vm.propertiesPageLimit,
        offset: vm.propertiesPageOffset,
        order: vm.sortColumn,
        direction: vm.sortDirection,
        'search[propertyDesignation]': vm.inputTextProperty,
        'search[municipality]': vm.inputTextMunicipality,
        'search[district]': vm.inputTextDistrict,
        unassigned: true
      },
      {}
    )
      .$promise.then(function(response) {
        vm.properties = response.rows;
        vm.propertyTotalCount = response.count;
        vm.rentFile = response.rentFile;
      })
      .catch(ErrorHandler('Misslyckades att hämta fastighet.'))
      .finally(function() {
        vm.readyProperties = true;
      });
  }
}

})();
(function () {
'use strict';

ChangeOfficeModalCtrl.$inject = ["$uibModalInstance", "ErrorHandler", "Office"];
angular.module('hyresgastApp').controller('ChangeOfficeModalCtrl', ChangeOfficeModalCtrl);

function ChangeOfficeModalCtrl($uibModalInstance, ErrorHandler, Office) {
  var vm = this;

  vm.ok = ok;
  vm.cancel = cancel;

  queryOffice();

  ////////////

  function queryOffice() {
    return Office.query().$promise.then(function(response) {
      vm.offices = response;
    })
      .catch(ErrorHandler('Misslyckades att hämta kontor.'));
  }

  function ok() {
    $uibModalInstance.close(vm.selectedOffice);
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

ReturnNegotiationCallModal.$inject = ["$uibModalInstance"];
angular.module('hyresgastApp').controller('ReturnNegotiationCallModal', ReturnNegotiationCallModal);

function ReturnNegotiationCallModal($uibModalInstance) {
  var vm = this;
  vm.cancel = cancel;
  vm.send = send;
  vm.message = '';

  /////////

  function send() {
    $uibModalInstance.close(vm.message);
  }

  function cancel() {
    $uibModalInstance.dismiss('cancel');
  }
}

})();
(function () {
'use strict';

ShowNegotiationCallErrorsCtrl.$inject = ["$scope", "$routeParams", "$location", "NegotiationCall", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationCallErrorsCtrl', ShowNegotiationCallErrorsCtrl);

function ShowNegotiationCallErrorsCtrl($scope, $routeParams, $location, NegotiationCall, ErrorHandler) {
  var vm = this;

  vm.errors = [];
  vm.queryErrors = queryErrors;

  vm.pageLimit = 30;
  vm.pageOffset = $location.search().offset || 0;

  vm.toggleBogusTooltip = (element) => {
    let id = element.error.id;
    let tooltip = document.getElementById('bogusTooltip'+id).firstElementChild;
    tooltip.style.visibility = tooltip.style.visibility==='visible' ? 'hidden' : 'visible';
  };

  queryErrors();

  /////////

  function queryErrors() {
    vm.readyErrors = false;

    return NegotiationCall.queryErrors({
      id: $routeParams.id,
      limit: vm.pageLimit,
      offset: vm.pageOffset
    })
      .$promise.then(function(data) {
        vm.errors = data.rows.unique();
        vm.errorsCount = vm.errors.length;
      })
      .catch(ErrorHandler('Misslyckades att hämta lista med fel.'))
      .finally(function() {
        vm.readyErrors = true;
      });
  }
}

})();
(function () {
'use strict';

ShowNegotiationDocumentsCtrl.$inject = ["$routeParams", "$uibModal", "Negotiation", "ValidFileTypes", "CommonMessages", "Flash", "HyUtils", "Auth", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationDocumentsCtrl', ShowNegotiationDocumentsCtrl);

function ShowNegotiationDocumentsCtrl($routeParams, $uibModal,
  Negotiation,
  ValidFileTypes, CommonMessages,
  Flash, HyUtils, Auth, ErrorHandler) {
  var vm = this;
  vm.Auth = Auth;
  vm.add = add;
  vm.delete = destroy;

  /////////

  getDocuments();

  function getDocuments() {
    vm.readyDocuments = false;
    return Negotiation.listDocuments({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.files = response;
      })
      .catch(new ErrorHandler('Misslyckades att hämta underlag.'))
      .finally(function() {
        vm.readyDocuments = true;
      });
  }

  function add() {
    var settings = {
      title: 'Ladda upp underlag',
      uploadUrl: '/api/negotiations/' + $routeParams.id + '/documents',

      // valid extension to upload
      validFileTypes: ValidFileTypes,

      // a file is associated with one type
      types: [
        { id: 13, name: 'Inlaga' },
        { id: 1, name: 'Allmänna ekonomiska läget' },
        { id: 2, name: 'Budget kommande år' },
        { id: 3, name: 'Budget/utfall föregående år' },
        { id: 4, name: 'Förvaltningskvalitet och service' },
        { id: 5, name: 'Läge och standard' },
        { id: 6, name: 'Medlems- och kundundersökning' },
        { id: 7, name: 'Prognos för innevarande år' },
        { id: 8, name: 'Underhållsplan' },
        { id: 9, name: 'Övrigt' }
      ],
      typeTitle: 'Välj typ av underlag'
    };

    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'components/file-upload-ex-modal/file-upload-ex-modal.html',
      controller: 'FileUploadExModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      resolve: {
        settings: function() {
          return settings;
        }
      }
    });

    modalInstance.result.then(function(files) {
      vm.files.mainDocuments.push(...files);
      Flash.set('Lyckades lägga till dokument', 'success');
    });
  }

  function destroy(file) {
    HyUtils.showDefaultModal('WarningConfirmationModalCtrl',
      'components/modal/confirmation/warningConfirmationModal.html',
      {
        message: CommonMessages.FILE_DELETE_CONFIRMATION,
        button: 'Ta bort',
        critical: true,
      },
      // eslint-disable-next-line no-unused-vars
      result => {
        Negotiation.deleteDocument({
          negotiationId: $routeParams.id,
          id: file.name
        // eslint-disable-next-line no-unused-vars
        }).$promise.then(function(response) {
          var index = vm.files.mainDocuments.indexOf(file);
          vm.files.mainDocuments.splice(index, 1);
          Flash.set('Lyckades ta bort dokument', 'success');
        }).catch(new ErrorHandler('Misslyckades att ta bort dokument.'));
      }, null, null, { size: 'md' });
  }
}

})();
(function () {
'use strict';

ApproveUserCtrl.$inject = ["$location", "$routeParams", "$uibModal", "Flash", "Organization", "User", "Roles", "ErrorHandler"];
angular.module('hyresgastApp').controller('ApproveUserCtrl', ApproveUserCtrl);

function ApproveUserCtrl($location, $routeParams, $uibModal, Flash, Organization, User, Roles, ErrorHandler) {
  var vm = this;
  vm.organization = {};
  vm.save = save;
  vm.destroy = destroy;
  vm.user = {};

  getUser();

  ////////////

  function getUser() {
    return User.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.ready = true;
        vm.user = response;
      })
      .catch(new ErrorHandler('Misslyckades att hämta användaren.'));
  }

  function save() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'components/modal/confirmation-info/confirmationModalInfo.html',
      controller: 'ConfirmationModalInfoCtrl',
      controllerAs: 'vm',
      resolve: {
        text: function() {
          return {
            question: 'Är du säker på att du vill godkänna',
            subject: vm.user.fullName,
            additionalInfo: '',
            abort: 'Avbryt',
            confirm: 'Godkänn'
          };
        },
        buttonClassSecondary: function() {
          return false;
        }
      }
    });

    modalInstance.result.then(function() {
      Organization.updateUserRole({
        organizationId: Object.keys(vm.user.roleByOrganization)[0],
        id: vm.user.id,
        role: Roles.USER
      }).$promise.then(
        function() {
          Flash.set('Konto godkänt - bekräftelse skickad', 'success');
          $location.search({});
          $location.path('/users/' + vm.user.id);
        })
        .catch(new ErrorHandler('Misslyckades att godkänna konto.'))
    });
  }

  function destroy() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'components/modal/confirmation-info/confirmationModalInfo.html',
      controller: 'ConfirmationModalInfoCtrl',
      controllerAs: 'vm',
      resolve: {
        text: function() {
          return {
            question: 'Är du säker på att du vill neka',
            subject: vm.user.fullName,
            additionalInfo: '',
            abort: 'Avbryt',
            confirm: 'Neka'
          };
        },
        buttonClassSecondary: function() {
          return true;
        }
      }
    });

    modalInstance.result.then(function() {
      User.delete({
        id: vm.user.id
      }).$promise.then(
        function() {
          Flash.set('Konto nekat - bekräftelse skickad', 'success');
          $location.path('/requested-accounts');
        }
      )
        .catch(new ErrorHandler('Misslyckades att neka konto.'));
    });
  }
}

})();
(function () {
'use strict';

ApproveOrganizationCtrl.$inject = ["$location", "$routeParams", "$uibModal", "Flash", "Organization", "UserByOrganization", "ErrorHandler"];
angular.module('hyresgastApp').controller('ApproveOrganizationCtrl', ApproveOrganizationCtrl);

function ApproveOrganizationCtrl($location, $routeParams, $uibModal, Flash, Organization, UserByOrganization, ErrorHandler) {
  var vm = this;
  vm.organization = {};
  vm.save = save;
  vm.destroy = destroy;

  getOrganization();

  ////////////

  function getOrganization() {
    return Organization.get({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.organization = response;
        getUsers();
      })
      .catch(new ErrorHandler('Misslyckades att hämta organisation.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  function getUsers() {
    UserByOrganization.query(vm.organization.id)
      .then(function(response) {
        vm.users = response;
        vm.usersCount = response.length;
        vm.foundAdmin = vm.users.filter(function(user) {
          return user.role === 200;
        });
      })
      .catch(function() {
        Flash.set('Misslyckades att hämta användare.', 'error');
      })
      .finally(function() {
        vm.ready = true;
      });
  }

  function save() {
    vm.organization.approved = true;

    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'components/modal/confirmation-info/confirmationModalInfo.html',
      controller: 'ConfirmationModalInfoCtrl',
      controllerAs: 'vm',
      resolve: {
        text: function() {
          return {
            question: 'Är du säker på att du vill godkänna',
            subject: vm.organization.name,
            additionalInfo: '',
            abort: 'Avbryt',
            confirm: 'Godkänn'
          };
        },
        buttonClassSecondary: function() {
          return false;
        }
      }
    });

    modalInstance.result.then(function() {
      Organization.approve(vm.organization).$promise.then(
        function(organization) {
          Flash.set('Konto godkänt - bekräftelse skickad', 'success');
          $location.path('/organizations/' + organization.id);
        })
        .catch(function(reason) {
          if (reason.status === 409) {
            Flash.set('Organisationsnumret existerar redan', 'error');
          } else if (reason.status === 403)  {
            $location.path('/403');
          } else {
            Flash.set('Kontrollera organisationsnumret', 'error');
          }
        });
    });
  }

  function destroy() {
    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'components/modal/confirmation-info/confirmationModalInfo.html',
      controller: 'ConfirmationModalInfoCtrl',
      controllerAs: 'vm',
      resolve: {
        text: function() {
          return {
            question: 'Är du säker på att du vill neka',
            subject: vm.organization.name,
            additionalInfo: 'Organisationen kommer att tas bort från portalen.',
            abort: 'Avbryt',
            confirm: 'Neka'
          };
        },
        buttonClassSecondary: function() {
          return true;
        }
      }
    });

    modalInstance.result.then(function() {
      Organization.delete({
        id: vm.organization.id
      })
        .$promise.then(function() {
          Flash.set('Organisation nekad - bekräftelse skickad', 'success');
          $location.search({});
          $location.path('/requested-accounts');
        })
        .catch(new ErrorHandler('Misslyckades att neka organisation.'));
    });
  }
}

})();
(function () {
'use strict';

ShowNegotiationCallDocumentsCtrl.$inject = ["$routeParams", "$uibModal", "NegotiationCall", "ValidFileTypes", "CommonMessages", "HyUtils", "Flash", "Auth", "ErrorHandler"];
angular.module('hyresgastApp').controller('ShowNegotiationCallDocumentsCtrl', ShowNegotiationCallDocumentsCtrl);

function ShowNegotiationCallDocumentsCtrl(
  $routeParams, $uibModal,
  NegotiationCall,
  ValidFileTypes, CommonMessages,
  HyUtils, Flash, Auth, ErrorHandler) {
  var vm = this;
  vm.add = add;
  vm.delete = destroy;
  vm.Auth = Auth;

  /////////

  getDocuments();

  function getDocuments() {
    vm.readyDocuments = false;

    return NegotiationCall.listDocuments({ id: $routeParams.id })
      .$promise.then(function(response) {
        vm.files = response;
      })
      .catch(ErrorHandler('Misslyckades att hämta underlag.'))
      .finally(function() {
        vm.readyDocuments = true;
      });
  }

  function add() {
    var settings = {
      title: 'Ladda upp underlag',
      uploadUrl: '/api/negotiation-calls/' + $routeParams.id + '/documents',

      // valid extension to upload
      validFileTypes: ValidFileTypes,

      // a file is associated with one type
      types: [
        { id: 13, name: 'Inlaga' },
        { id: 1, name: 'Allmänna ekonomiska läget' },
        { id: 2, name: 'Budget kommande år' },
        { id: 3, name: 'Budget/utfall föregående år' },
        { id: 4, name: 'Förvaltningskvalitet och service' },
        { id: 5, name: 'Läge och standard' },
        { id: 6, name: 'Medlems- och kundundersökning' },
        { id: 7, name: 'Prognos för innevarande år' },
        { id: 8, name: 'Underhållsplan' },
        { id: 9, name: 'Övrigt' }
      ],
      typeTitle: 'Välj typ av underlag'
    };

    var modalInstance = $uibModal.open({
      animation: true,
      ariaLabelledBy: 'modal-title-top',
      ariaDescribedBy: 'modal-body-top',
      size: 'sm',
      templateUrl: 'components/file-upload-ex-modal/file-upload-ex-modal.html',
      controller: 'FileUploadExModalCtrl',
      controllerAs: 'vm',
      windowClass: 'modal-content--white-border',
      resolve: {
        settings: function() {
          return settings;
        }
      }
    });

    modalInstance.result.then(function(result) {
      if (!result || !!result.find(item => item.error)) {
        Flash.set('Misslyckades att ladda upp underlag', 'error');
        return;
      } else {
        vm.files.push(...result);
        Flash.set('Lyckades lägga till underlag', 'success');
      }
    });
  }

  function destroy(file) {
    HyUtils.showDefaultModal('WarningConfirmationModalCtrl',
      'components/modal/confirmation/warningConfirmationModal.html',
      {
        message: CommonMessages.FILE_DELETE_CONFIRMATION,
        button: 'Ta bort',
        critical: true,
      },
      // eslint-disable-next-line no-unused-vars
      result => {
        NegotiationCall.deleteDocument({
          id: $routeParams.id,
          blobId: file.name
        // eslint-disable-next-line no-unused-vars
        }).$promise.then(function(response) {
          var index = vm.files.indexOf(file);
          vm.files.splice(index, 1);
          Flash.set('Lyckades ta bort underlag', 'success');
        }).catch(new ErrorHandler('Misslyckades att ta bort underlag.'));
      }, null, null, { size: 'md' });


  }
}

})();
(function () {
'use strict';

ListUserExternalCtrl.$inject = ["$scope", "$location", "User", "HyUtils", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListUserExternalCtrl', ListUserExternalCtrl);

function ListUserExternalCtrl($scope, $location,
  User,
  HyUtils, ErrorHandler) {
  var vm = this;
  vm.users = [];
  vm.getUsers = getUsers;
  vm.gotoShow = gotoShow;
  vm.pageLimit = 30;
  vm.pageOffset = 0;
  vm.sortColumn = $location.search().order || 'lastname'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default

  vm.listColumns = [
    { title: 'Förnamn', column: 'firstname' },
    { title: 'Efternamn', column: 'lastname' },
    { title: 'Telefonnummer', column: 'phone' },
    { title: 'E-post', column: 'email' },
    { title: 'Part', column: 'party' }
  ];

  getUsers();

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getUsers();
    }
  });

  ////////////

  function gotoShow(id) {
    $location.search({});
    $location.path('/users/' + id);
  }

  function getUsers(resetOffset) {
    vm.ready = false;
    if (resetOffset) {
      vm.pageOffset = 0;
    }
    return User.query({
      limit: vm.pageLimit,
      offset: vm.pageOffset,
      order: vm.sortColumn,
      direction: vm.sortDirection,
      'filters[party]': 0,
      'search[firstname]': vm.inputTextFirstName,
      'search[lastname]': vm.inputTextLastName,
      'search[email]': vm.inputTextEmail
    })
      .$promise.then(function(data) {
        vm.users = data.rows;
        vm.totalCount = data.count;
      })
      .catch(ErrorHandler('Misslyckades att hämta användaren.'))
      .finally(function() {
        vm.ready = true;
      });
  }

  vm.newUser = () => {
    HyUtils.showDefaultModal('NewUserModalCtrl',
      'resources/user/new/new-user-modal.html',
      { fullMode: true },
      () => {
        getUsers();
      });
  };

  vm.editUser = userId => {
    HyUtils.showDefaultModal('EditUserModalCtrl',
      'resources/user/edit/edit-user-modal.html',
      { userId },
      () => {
        getUsers();
      });
  };
}

})();
(function () {
'use strict';

ListUserDelegateCtrl.$inject = ["$scope", "$location", "Negotiation", "User", "Limits", "HyUtils"];
angular.module('hyresgastApp').controller('ListUserDelegateCtrl', ListUserDelegateCtrl);

function ListUserDelegateCtrl($scope, $location,
  Negotiation, User,
  Limits,
  HyUtils) {
  var vm = this;
  vm.Limits = Limits;
  vm.getUsers = getUsers;
  vm.gotoShow = gotoShow;

  vm.columns = [
    { title: 'Förnamn', column: 'firstname' },
    { title: 'Efternamn', column: 'lastname' },
    { title: 'Telefonnummer', column: 'phone' },
    { title: 'E-post', column: 'email' },
    { title: 'Roll', column: 'roll' }
  ];

  ////////////

  function gotoShow(user) {
    $location.search({});
    $location.path('/users/' + user.id);
  }

  function getUsers() {
    // eslint-disable-next-line no-undef
    return new Promise(resolve => {
      User.query({ 'filters[party]': 10 }).$promise.then(data => {
        let users = data;

        Negotiation.getDelegateRoles({}).$promise.then(function (users2negs) {
          for (let user of users.rows) {
            let user2neg = users2negs.users.find(un => un.userId === user.id);
            user.roll = user2neg
              ? HyUtils.findNegotiationRole(user2neg.roll).name
              : 'Delegat';
          }
          resolve(users);
        });
      });
    });
  }
}

})();
(function () {
'use strict';

ListUserHgfCtrl.$inject = ["$scope", "$location", "$routeParams", "Flash", "User", "Regions", "ErrorHandler"];
angular.module('hyresgastApp').controller('ListUserHgfCtrl', ListUserHgfCtrl);

function ListUserHgfCtrl($scope, $location, $routeParams, Flash, User, Regions, ErrorHandler) {
  var vm = this;
  vm.users = [];
  vm.getUsers = getUsers;
  vm.gotoShow = gotoShow;
  vm.pageLimit = 30;
  vm.pageOffset = 0;
  vm.sortColumn = $location.search().order || 'lastname'; // default
  vm.sortDirection = $location.search().direction || 'ASC'; // default
  vm.listColumns = [
    { title: 'Förnamn', column: 'firstname' },
    { title: 'Efternamn', column: 'lastname' },
    { title: 'Telefonnummer', column: 'phone' },
    { title: 'E-post', column: 'email' },
    { title: 'Part', column: 'party' }
  ];
  vm.regionChoices = angular.extend({ ALL: '' }, Regions);

  getUsers();

  $scope.$on('$routeUpdate', function() {
    if ($location.search().order) {
      // reset offset when sorting
      vm.pageOffset = 0;
      getUsers();
    }
  });

  ////////////

  function gotoShow(id) {
    $location.search({});
    $location.path('/users/' + id);
  }

  function getUsers(filterMode) {
    vm.ready = false;
    if (filterMode) {
      vm.pageOffset = 0;
    }
    if (vm.regionId) {
      return User.queryByRegion({
        regionId: vm.regionId,
        limit: vm.pageLimit,
        offset: vm.pageOffset,
        order: vm.sortColumn,
        direction: vm.sortDirection,
        'filters[party]': 20,
        'search[firstname]': vm.inputTextFirstName,
        'search[lastname]': vm.inputTextLastName,
        'search[email]': vm.inputTextEmail
      })
        .$promise.then(function(data) {
          vm.users = data.rows;
          vm.totalCount = data.count;
        })
        .catch(ErrorHandler('Misslyckades att hämta användaren.'))
        .finally(function() {
          vm.ready = true;
        });
    } else {
      return User.query({
        limit: vm.pageLimit,
        offset: vm.pageOffset,
        order: vm.sortColumn,
        direction: vm.sortDirection,
        'filters[party]': 20,
        'search[firstname]': vm.inputTextFirstName,
        'search[lastname]': vm.inputTextLastName,
        'search[email]': vm.inputTextEmail
      })
        .$promise.then(function(data) {
          vm.users = data.rows;
          vm.totalCount = data.count;
        })
        .catch(ErrorHandler('Misslyckades att hämta användaren.'))
        .finally(function() {
          vm.ready = true;
        });
    }
  }
}

})();
(function () {
'use strict';

NegotiationTableCtrl.$inject = ["$location", "$routeParams", "FeatureConfiguration", "User"];
angular.module('hyresgastApp').controller('NegotiationTableCtrl', NegotiationTableCtrl);

function NegotiationTableCtrl(
  $location, $routeParams,
  FeatureConfiguration, User) {
  var vm = this;
  vm.FeatureConfiguration = FeatureConfiguration;

  vm.personsColumns = [
    { title: 'Person', column: 'fullName' },
    { title: 'Organisation', column: 'organizationName' },
    { title: 'Gemensamma förhandlingar', column: 'numNegotiations' },
    { title: 'Antal fastigheter', column: 'numProperties' }
  ];

  vm.gotoShow = row => {
    $location.path('/users/' + $routeParams.id + '/negotiator/' + row.userId + '/organization/' + row.organizationId + '/welcome/negotiation-table/negotiator?view=list');
  }
  vm.getContextInfo = row => ({
    title: `Person `+row.fullName,
    link: '/users/' + $routeParams.id + '/negotiator/' + row.userId + '/organization/' + row.organizationId + '/welcome/negotiation-table/negotiator?view=list'
  });

  vm.getData = function () {
    // eslint-disable-next-line no-undef, no-unused-vars
    return new Promise(resolve => {
      User.queryNegotiationTables({})
        .$promise.then(function(data) {
          data.rows.forEach(x => x.fullName = `${x.firstname} ${x.lastname}`);
          resolve(data);
        });
    });
  }
}

})();
(function () {
/* jshint -W071 */
/* jshint -W109 */
/* jshint -W101 */
angular.module('hyresgastApp').run(['$templateCache', function($templateCache) {$templateCache.put('components/apartment-selector/apartment-selector.html','<style>.ownercounterpart {\r\n    display: inline-block;\r\n    min-width: 70px;\r\n  }</style><div class="row spacer-mb-large"><div class="col-6 padding-reset"><div class="row spacer-margin-reset"><input class="form-control" ng-class="{\'col-6\': canSearchCounterpart}" placeholder="S\xF6k efter fastigheter eller \xE4gare" name="propertyFilter" ng-model="propertyFilter" id="propertyFilter" ng-change="propertiesAfterType()"> <input ng-show="canSearchCounterpart" class="form-control col-6" placeholder="S\xF6k efter organisation" name="counterpartFilter" ng-model="counterpartFilter" id="counterpartFilter" ng-change="propertiesAfterType()"></div><hy-progress-circular ng-if="propertiesLoading" size="30px"></hy-progress-circular><div class="scroller-selector dropdown-menu--filter" ng-class="{\'scroller-selector--modal\': isModal}"><table class="table"><tr class="table__tr" ng-repeat="property in properties" ng-click="selectProperty(property)"><td class="table__td"><div class="modal-pillar"><hy-checkbox title=" " name="p{{property.id}}" model="tickedProperties[property.id]"></hy-checkbox></div><div class="modal-pillar">{{ property.municipality }}, <b>{{ property.propertyDesignation }}</b>, {{ property.district }}<div><small ng-bind-html="getOwnersString(property)"></small></div><div><small ng-bind-html="getOrganizationsString(property)"></small></div></div></td></tr></table></div><hy-checkbox title=" " class="check-all-apartments" name="allPropertiesSelected" model="allPropertiesSelected" ticked="toggleAllProperties()"></hy-checkbox><hy-progress-circular size="20px" ng-if="busyAddEvery"></hy-progress-circular><small ng-if="!hasSanePropertyCount()">F\xF6r m\xE5nga fastigheter (max 50st) </small><button ng-if="hasTickedProperties() && hasSanePropertyCount()" class="btn spacer-mt-xsmall" ng-click="addEveryApartment()">L\xE4gg till l\xE4genheter</button> <button ng-if="canAddEmptyProperty" class="btn spacer-mt-xsmall" ng-click="addEmptyProperty()">L\xE4gg till fastighet</button></div><div class="col-6 padding-reset" ng-if="!apartmentsLoading && selectedProperty && apartments.length === 0"><div class="spacer-ml-small">Ingen l\xE4genheter p\xE5 <b>{{ selectedProperty.municipality }}, {{ selectedProperty.propertyDesignation }}</b>!</div></div><hy-progress-circular ng-if="apartmentsLoading" size="30px"></hy-progress-circular><div class="col-6" ng-if="!apartmentsLoading && selectedProperty && apartments.length > 0"><input class="form-control di" placeholder="S\xF6k efter gatuadress, nationellt nummer eller l\xE4genhetsnummer" name="apartmentFilter" ng-model="vm.apartmentFilter"><div class="scroller-selector dropdown-menu--filter spacer-mb-xsmall" ng-class="{\'scroller-selector--modal\': isModal}"><table class="table"><tr class="table__tr" ng-repeat="apartment in apartments | filter:apartmentsFilter"><td class="table__td"><hy-checkbox title="{{ apartment.streetAddress }}, {{ apartment.nationalNo }}, {{ apartment.ownerNo }}" name="a{{apartment.id}}" model="tickedApartments[apartment.id]"></hy-checkbox></td></tr></table></div><hy-checkbox title="" class="check-all-apartments" name="toggleAllApartments" model="allApartmentsSelected" ticked="toggleAllApartments()"></hy-checkbox><button class="btn" ng-disabled="!hasTickedApartments()" ng-click="addSelected()">L\xE4gg till</button></div></div><div class="row"><table class="table spacer-margin-reset spacer-mr-xsmall" ng-if="model.length > 0"><tr><th class="table__th table__td--width-45 spacer-margin-reset">Fastigheter</th><th class="table__th table__td--width-35 spacer-margin-reset">L\xE4genheter</th><th class="table__th table__td--width-10 spacer-margin-reset"></th></tr></table><div class="btn--full-width" ng-class="{\'scroller-selector scroller-selector--modal\': isModal}" id="selectedApartmentsScroller" ng-if="model.length > 0 || emptyProperties.length > 0"><table class="table spacer-margin-reset spacer-mr-xsmall btn--full-width"><tr ng-repeat="property in emptyProperties"><td colspan="3"><b class="spacer-ml-medium"><i>{{property.municipality}}, {{property.district}}, {{property.propertyDesignation}}</i></b> <button class="btn btn--icon btn--icon-bin btn-fu" style="float: right" ng-click="deleteEmptyProperty(property.id)"></button></td></tr></table><table class="table spacer-margin-reset spacer-mr-xsmall"><tr ng-repeat-start="apartment in model" ng-if="apartment.differentProperty"><td colspan="3"><b class="spacer-ml-medium"><i>{{apartment.municipality}}, {{apartment.property.district}}, {{apartment.property.propertyDesignation}}</i></b></td></tr><tr ng-repeat-end><td class="table__td table__td--width-45 table__td--minimalistic spacer-margin-reset">{{apartment.municipality}}, {{apartment.property.district}}, {{apartment.property.propertyDesignation}}</td><td class="table__td table__td--width-35 table__td--minimalistic spacer-margin-reset">{{apartment.streetAddress}}, {{apartment.nationalNo}}, {{apartment.ownerNo}}</td><td class="table__td table__td--width-10 table__td--minimalistic spacer-margin-reset"><button class="btn btn--icon btn--icon-bin btn-fu" ng-click="deleteSelected(apartment.id)"></button></td></tr></table></div></div>');
$templateCache.put('components/accordion/accordion-group.html','<div role="tab" id="{{::headingId}}" aria-selected="{{isOpen}}" class="card__header" ng-click="toggleOpen()"><h5 uib-accordion-header ng-class="{\'text-gray\': isDisabled}" class="card__title">{{heading}}</h5></div><div id="{{::panelId}}" aria-labelledby="{{::headingId}}" aria-hidden="{{!isOpen}}" role="tabpanel" class="card-collapse" uib-collapse="!isOpen"><div class="card__block" ng-transclude></div></div>');
$templateCache.put('components/accordion/accordion.html','<div role="tablist" class="accordion" ng-transclude></div>');
$templateCache.put('components/amend-button/amend-button.html','<button type="button" ng-click="normalClick()" class="btn {{buttonClass}} btn--preamend" title="" ng-disabled="disabled">{{title}}</button><button type="button" ng-click="amendClick()" class="btn btn--amend" title="{{amendTitle}}" ng-disabled="amendDisabled">+</button>');
$templateCache.put('components/combobox/combobox.html','<div><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" uib-dropdown-toggle class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow"><span class="text-gray-dark" ng-if="!stickyPrompt">{{ selectedItem ? (textProperty ? selectedItem[textProperty] : selectedItem) : prompt }} </span><span class="text-gray-dark" ng-if="stickyPrompt">{{ prompt }}</span></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="item in items" type="button" class="dropdown-item" ng-click="selectItem(item)"><span>{{ textProperty ? item[textProperty] : item }}</span></button></div></div></div>');
$templateCache.put('components/checkbox/checkbox.html','<div class="custom-control custom-control--checkbox spacer-mt-xsmall"><input class="custom-control__input" type="checkbox" id="check{{name}}" name="check{{name}}" ng-click="ticked()" ng-change="changed()" ng-model="model" ng-disabled="disabled"> <label class="custom-control__label" for="check{{name}}" data-title="{{title}}">{{title}}</label></div>');
$templateCache.put('components/datepicker/datepicker.html','<div ng-switch="datepickerMode"><div uib-daypicker ng-switch-when="day" tabindex="0"></div><div uib-monthpicker ng-switch-when="month" tabindex="0"></div><div uib-yearpicker ng-switch-when="year" tabindex="0"></div></div>');
$templateCache.put('components/datepicker/day.html','<table cellspacing="0" role="grid" aria-labelledby="{{::uniqueId}}-title" aria-activedescendant="{{activeDateId}}"><thead><tr><th class="border-bottom-grey arrow-bar-height"><button type="button" class="btn btn--white-yellow btn--no-border-radius btn-datepicker btn-datepicker--no-padding" ng-click="move(-1)" tabindex="-1"><div class="icon icon--arrow-left"></div></button></th><th class="border-bottom-grey arrow-bar-height" colspan="{{::5 + showWeeks}}"><button id="{{::uniqueId}}-title" class="btn btn-datepicker btn--white-yellow btn--no-border-radius btn--full-width text-capitalize" ng-click="toggleMode()" ng-disabled="datepickerMode === maxMode" tabindex="-1"><strong>{{title}}</strong></button></th><th class="border-bottom-grey arrow-bar-height"><button type="button" class="btn btn--white-yellow btn--no-border-radius btn-datepicker btn-datepicker--no-padding" ng-click="move(1)" tabindex="-1"><div class="icon icon--arrow-right"></div></button></th></tr><tr><th class="text-center border-bottom-grey text-small week-bar" ng-if="showWeeks">V</th><th class="text-center border-bottom-grey week-bar text-capitalize" ng-repeat="label in ::labels track by $index"><small aria-label="{{::label.full}}">{{::label.abbr}}</small></th></tr></thead><tbody class="datepicker__day borders-last-child"><tr ng-repeat="row in rows track by $index" role="row"><td ng-if="showWeeks" class="text-center border-right-grey text-small weeks-col"><strong>{{ weekNumbers[$index] }}</strong></td><td ng-repeat="dt in row" class="text-center border-r-b-white" role="gridcell" id="{{::dt.uid}}" ng-class="::dt.customClass"><button type="button" class="btn btn-datepicker--day-btn border-grey btn--white-turquoise btn--no-border-radius btn--full-width" uib-is-class="\'active\' for activeDt on dt" ng-click="select(dt.date)" ng-disabled="::dt.disabled" ng-class="{\'btn--selected-date\': dt.selected}" tabindex="-1"><span class="text-normal" ng-class="::{\'text-muted\': dt.secondary, \'text-info\': dt.current}">{{::dt.label}}</span></button></td></tr></tbody></table>');
$templateCache.put('components/datepicker/month.html','<table cellspacing="0" role="grid" aria-labelledby="{{::uniqueId}}-title" aria-activedescendant="{{activeDateId}}"><thead><tr><th class="border-bottom-grey arrow-bar-height"><button type="button" class="btn btn-datepicker btn--white-yellow btn--no-border-radius btn--relative" ng-click="move(-1)" tabindex="-1"><div class="icon icon--arrow-left"></div></button></th><th class="border-bottom-grey arrow-bar-height" colspan="{{::yearHeaderColspan}}"><button id="{{::uniqueId}}-title" role="heading" class="btn btn-datepicker btn--white-yellow btn--no-border-radius btn--full-width" ng-click="toggleMode()" ng-disabled="datepickerMode === maxMode" tabindex="-1"><strong>{{title}}</strong></button></th><th class="border-bottom-grey arrow-bar-height"><button type="button" class="btn btn-datepicker btn--white-yellow btn--no-border-radius btn--relative" ng-click="move(1)" tabindex="-1"><div class="icon icon--arrow-right"></div></button></th></tr></thead><tbody class="datepicker__month borders-last-child"><tr ng-repeat="row in rows track by $index" role="row"><td ng-repeat="dt in row" class="text-center border-r-b-white" role="gridcell" id="{{::dt.uid}}" ng-class="::dt.customClass"><button type="button" class="btn btn-datepicker-month-year btn--no-border-radius btn--full-width text-capitalize" uib-is-class="\'active\' for activeDt on dt" ng-click="select(dt.date)" ng-disabled="::dt.disabled" tabindex="-1"><span ng-class="::{\'text-info\': dt.current}">{{::dt.label}}</span></button></td></tr></tbody></table>');
$templateCache.put('components/datepicker/year.html','<table cellspacing="0" role="grid" aria-labelledby="{{::uniqueId}}-title" aria-activedescendant="{{activeDateId}}"><thead><tr><th class="border-bottom-grey arrow-bar-height"><button type="button" class="btn btn-datepicker btn--white-yellow btn--no-border-radius btn--relative" ng-click="move(-1)" tabindex="-1"><div class="icon icon--arrow-left"></div></button></th><th class="border-bottom-grey text-center arrow-bar-height" colspan="{{::columns - 2}}"><strong>{{title}}</strong></th><th class="border-bottom-grey arrow-bar-height"><button type="button" class="btn btn-datepicker btn--white-yellow btn--no-border-radius btn--relative" ng-click="move(1)" tabindex="-1"><div class="icon icon--arrow-right"></div></button></th></tr></thead><tbody class="datepicker__year borders-last-child"><tr ng-repeat="row in rows track by $index" role="row"><td ng-repeat="dt in row" class="text-center border-r-b-white" role="gridcell" id="{{::dt.uid}}" ng-class="::dt.customClass"><button type="button" class="btn btn-datepicker-month-year btn--no-border-radius btn--full-width" uib-is-class="\'active\' for activeDt on dt" ng-click="select(dt.date)" ng-disabled="::dt.disabled" tabindex="-1"><span ng-class="::{\'text-info\': dt.current}">{{::dt.label}}</span></button></td></tr></tbody></table>');
$templateCache.put('components/array-display/array-display.html','<span uib-tooltip-html="rest" tooltip-class="tt{{columnCount}}" ng-if="first">{{first}}<xsmall class="price-same" ng-if="elseCount > 0">+ {{elseCount}}</xsmall></span>');
$templateCache.put('components/breadcrumb/breadcrumb.html','<ul class="breadcrumb" ng-show="breadcrumb.length > 1"><li class="breadcrumb__item" ng-repeat="item in breadcrumb"><span ng-show="$last" class="breadcrumb__item--last">{{item.title}}</span> <a ng-hide="$last" class="breadcrumb__item--link" href="{{item.path}}">{{item.title}}</a><div ng-hide="$last" class="breadcrumb__item--icon">/</div></li></ul>');
$templateCache.put('components/datepicker-popup/popup.html','<ul role="presentation" class="uib-datepicker-popup dropdown-menu uib-position-measure" dropdown-nested ng-if="isOpen" ng-keydown="keydown($event)" ng-click="$event.stopPropagation()"><li ng-transclude></li><li ng-if="showButtonBar" class="uib-button-bar"><div class="row"><div class="col"></div><div class="col text-right"><a href="" class="text-small text-uppercase" ng-click="close($event)"><strong>St\xE4ng</strong></a></div></div></li></ul>');
$templateCache.put('components/donut/donut.html','<div ng-style="{\'width\': fixedWidth}"><svg viewBox="0 0 37 37" class="donut" ng-style="{\'max-height\': maxHeight}"><circle class="donut-hole" cx="18.5" cy="18.5" r="15.91549430918954" fill="#fff"></circle><circle class="donut-ring" cx="18.5" cy="18.5" r="15.91549430918954" fill="transparent" stroke="#d2d3d4" stroke-width="5"></circle><circle class="donut__segment" cx="18.5" cy="18.5" r="15.91549430918954" fill="transparent" stroke-width="5" stroke-dasharray="{{partPercent}} {{remainingPercent}}" stroke-dashoffset="25" ng-class="{\'donut__segment--gray\' : (part===total &amp;&amp; statusFinished)}"></circle><g class="donut__chart"><text x="50%" y="50%" class="donut__chart-number" ng-hide="part===total">{{part}}</text><text x="50%" y="70%" class="donut__chart-label" ng-hide="part===total">av {{total}}</text><text x="50%" y="58%" class="donut__chart-number donut__chart-number--h3" ng-show="part===total">KLAR</text></g></svg></div>');
$templateCache.put('components/error/403.html','<div class="container"><h1 class="text-danger"><u>403</u></h1><h3><p>Ups!</p><p>Du har kommit till en sida du inte har beh\xF6righet till.</p><p>Om n\xE5got \xE4r fel, h\xF6r av dig till <a class="link--turquoise link--underline" href="https://www.hyresgastforeningen.se/kontakta-oss/hela-sverige/" target="_blank">Hyresg\xE4stf\xF6reningen</a>.</p></h3></div>');
$templateCache.put('components/error/404.html','<div class="container"><h1 class="text-danger"><u>404</u></h1><h3><p>Ups!</p><p>Kunde inte hitta resursen du s\xF6kte.</p><p>Resursen har antingen tagits bort eller aldrig existerat.</p></h3></div>');
$templateCache.put('components/file-plate/file-plate.html','<div class="file-plate" id="file-plate-component" ondragover="event.preventDefault()"><input type="file" ng-show="false" id="file-plate-dialog"> <input type="file" ng-show="false" name="file[]" id="file-plate-dialog-multiple" multiple="multiple"><div class="file-plate-header" ng-if="!currentFileString">SL\xC4PP {{ multiple ? \'FILER\' : \'FIL\' }} H\xC4R</div><div class="file-plate-file" ng-if="currentFileString">{{ currentFileString }}</div><div ng-show="!busy"><button ng-if="!ready" class="btn di" ng-click="select()">V\xE4lj {{ multiple ? \'filer\' : \'fil\' }}</button> <button ng-if="ready && !done" class="btn btn--secondary di" ng-click="clear()">Avv\xE4lj {{ multiple ? \'filer\' : \'fil\' }}</button></div><div ng-show="busy"><hy-progress-circular size="32px"></hy-progress-circular></div></div>');
$templateCache.put('components/flash/flash.html','<div class="container" ng-click="close()"><h3 class="flash__title text-lowercase">{{Flash.get().content}}</h3></div>');
$templateCache.put('components/footer/footer.html','<footer class="footer"><div class="container"><a href="https://www.hyresgastforeningen.se/" target="_blank"><div class="footer__logo"></div></a><div><a class="link--underline btn--icon btn--icon-email" href="mailto:{{email}}">{{email}}</a><br><a href="tel:{{phoneDeGapped}}" target="_self" class="link--underline btn--icon btn--icon-telephone">{{phone}}</a>, {{hours}}</div></div></footer>');
$templateCache.put('components/gobutton/gobutton.html','<button class="btn {{class}}" ng-click="go()">{{caption}}</button>');
$templateCache.put('components/help-modal/helpModal.html','<div class="modal-body border--full border--turquoise" id="modal-body"><div class="display-flex space-between flex-grow-1 spacer-mt-medium"><h3 class="text-margin-reset">{{vm.title}}</h3><div clas="display-flex align-items-center"><a class="link--turquoise link--icon link--icon-close text-bold" ng-click="vm.cancel()">St\xE4ng</a></div></div><p>{{vm.body}}</p></div>');
$templateCache.put('components/file-upload-modal/file-upload-modal.html','<form name="vm.fileUploadForm" ng-submit="vm.fileUploadForm.$valid" novalidate><div class="modal-header modal-header--small"><h4 class="text-margin-reset">{{vm.settings.title}}</h4></div><div class="modal-body modal-body--small" id="modal-body" hy-file-upload blob-id="vm.file.blobId" metadata="vm.metadata" valid-file-types="{{vm.settings.validFileTypes}}" max-file-size="1073741824" upload-url="{{vm.settings.uploadUrl}}" uploader="uploader" type="vm.file.type" disable-flash error-invalid-file="vm.errorInvalidFile"><div ng-if="vm.settings.types"><input name="inputType" id="inputType" type="hidden" ng-model="vm.file.type" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width" uib-dropdown-toggle>{{vm.file.type ? vm.selectedTypeName : vm.settings.typeTitle}}</button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="type in vm.settings.types" type="button" class="dropdown-item" ng-click="vm.file.type = type.id; vm.selectedTypeName = type.name">{{type.name}}</button></div></div></div><div ng-if="uploader &amp;&amp; !uploader.currentFileName"><div class="display-flex justify-content-end"><label class="btn btn--cursor-pointer text-reset spacer-mb-medium spacer-mt-medium"><input type="file" nv-file-select="" uploader="uploader"> <strong>V\xE4lj fil</strong></label></div><span ng-show="vm.errorInvalidFile">{{vm.errorInvalidFile}}</span><!-- showing error message in modal instead of flash --></div><div class="info__content info__content--narrow display-flex align-items-top space-between" ng-show="uploader.currentFileName"><div><span>{{uploader.currentFileName | limitTo: 35}}</span></div><div><a href="#" class="btn--icon btn--icon-bin btn--icon--no-padding" ng-hide="uploader.isUploading" ng-click="uploader.cancelUpload(); $event.preventDefault();"><span class="sr-only">Ta bort</span></a></div></div><div class="spacer-mt-small" ng-show="uploader.isUploading"><uib-progressbar type="turquoise" value="uploader.progress" ng-class="\'progress--square\'"></uib-progressbar></div></div><div class="modal-footer modal-footer--small"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline" ng-click="uploader.cancelAll(); vm.cancel();">Avbryt</a> <button class="btn btn--icon btn--icon-upload" type="button" ng-disabled="vm.fileUploadForm.$invalid || !uploader.currentFileName" ng-click="uploader.uploadAll();">Ladda upp</button></div></form>');
$templateCache.put('components/header/header.html','<div class="header-box"><div class="row"><div class="col-12 col-lg-{{ getLeftCols() }}"><h1 class="header-title text-margin-reset h1-line-height-reset">{{ title }}</h1></div><div class="col-12 col-lg-{{ getRightCols() }}"><div class="header-buttons"><span ng-repeat="button in buttons"><a ng-if="!button.icon && button.isVisible()" class="btn spacer-ml-xsmall" ng-style="{\'width\':\'\'+getWidth(button) }" href="{{ button.url }}" ng-click="button.click()">{{ button.title }} </a><button ng-if="button.icon && button.isVisible()" class="header-icon-button" ng-click="go(button)"><div><img src="/img/icons/{{button.icon}}.png"><br>{{ button.title }}</div></button></span></div></div></div><div class="row" ng-if="tabs && tabs.length > 0"><div class="col col-sm-{{tabs.length * 3}} col-lg-{{tabs.length * 2}} tabs-container header-tabs"><hy-tabs tablist="tabs"></hy-tabs></div></div></div><hr class="hr--full-width spacer-mt-reset" ng-class="(!tabs || tabs.length === 0) ? \'hr--grey-light\' : \'hr--tortoise\'">');
$templateCache.put('components/info/info.html','<div class="info"><label class="info__title">{{title}}</label><p class="info__content">{{content ? content : \'&mdash;\'}}</p></div>');
$templateCache.put('components/modal/window.html','<div class="modal-dialog {{size ? \'modal-\' + size : \'\'}}"><div class="modal-content" uib-modal-transclude></div></div>');
$templateCache.put('components/multi-select/multi-select-modal.html','<div class="modal-header modal-header--small"><h2 class="text-margin-top-reset text-margin-bottom-reset">{{vm.title}}</h2></div><div class="modal-body modal-body--small" id="modal-body"><div class="region-selection-column" ng-repeat="(key, o) in vm.groups"><div class="bold-label"><hy-checkbox title="{{key}}" name="chkGroup{{key}}" model="vm.selectionGroups[key]" ticked="vm.toggleGroup(key, !vm.selectionGroups[key])"></hy-checkbox></div><hr class="hr--thin"><div ng-repeat="value in o"><hy-checkbox title="{{value[vm.textProperty]}}" name="chk{{value[vm.idProperty]}}" model="vm.selection[value[vm.idProperty]].ticked" ticked="vm.maybeResetGroup(vm.selection[value[vm.idProperty]].group, value[vm.idProperty], !vm.selection[value[vm.idProperty]].ticked)"></hy-checkbox></div></div></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="$dismiss()">Avbryt</a> <button class="btn" ng-click="vm.ok()">Spara</button></div>');
$templateCache.put('components/multi-select/multi-select.html','<div><div ng-if="param.data && (param.mandatory || param.isSet || param.allOmit)"><span ng-if="param.data != -1" ng-repeat="data in param.data">{{data.name}},</span></div><input type="button" class="btn" value="{{title}}" ng-disabled="(!param.isSet && !param.mandatory && !param.allOmit) || vm.paramDictionaries[param.type].length === 0" ng-click="beginSelection()"></div>');
$templateCache.put('components/paginator/paginator.html','<strong>{{ firstRecordInCurrentPage }}-{{ computeLastRecord() }}</strong> av <strong>{{ totalCount }}</strong> <button class="btn btn--paginator-left" type="button" ng-disabled="firstRecordInCurrentPage <= pageLimit" ng-click="gotoPrevPage()"></button> <button class="btn btn--paginator-right" type="button" ng-disabled="computeLastRecord() >= totalCount" ng-click="gotoNextPage()"></button>');
$templateCache.put('components/multicombobox/multicombobox.html','<div><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" uib-dropdown-toggle class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow"><span class="text-gray-dark" ng-if="!stickyPrompt">{{ (totalSelected===items.length ? \'Alla\' : totalSelected) + \' vald\' }} </span><span class="text-gray-dark" ng-if="stickyPrompt">{{ prompt }}</span></button><div class="dropdown-menu dropdown-menu-left dropdown-menu-filter dropdown-menu--scroll" uib-dropdown-menu role="menu"><div ng-repeat="item in items track by $index" type="button" class="dropdown-item" ng-click="$event.stopPropagation()"><hy-checkbox title="{{ textProperty ? item[textProperty] : item }}" name="mc{{uuid}}{{$index}}" model="selection[idProperty ? item[idProperty] : item]" ticked="updateTotal()"></hy-checkbox></div><div class="dropdown-item dropdown-item--check" ng-click="presentSelection()"><strong>{{buttonText}}</strong></div></div></div></div>');
$templateCache.put('components/progress-circular/progressCircular.html','<div class="progress-circular" ng-style="{\'width\':size, \'height\':size}"></div>');
$templateCache.put('components/range-input/range-input.html','<div><input class="form-control form-control--narrow form-control--gray-border small-input di" type="number" ng-change="changedInternal()" ng-model="min"> <input class="form-control form-control--narrow form-control--gray-border small-input di" type="number" ng-change="changedInternal()" ng-model="max"></div>');
$templateCache.put('components/progressbar/bar.html','<div class="progress__bar" ng-class="type && \'progress__bar--\' + type" role="progressbar" aria-valuenow="{{value}}" aria-valuemin="0" aria-valuemax="{{max}}" ng-style="{width: (percent < 100 ? percent : 100) + \'%\'}" aria-valuetext="{{percent | number:0}}%" aria-labelledby="{{::title}}" ng-transclude></div>');
$templateCache.put('components/progressbar/progress.html','<div class="progress" ng-transclude aria-labelledby="{{::title}}"></div>');
$templateCache.put('components/progressbar/progressbar.html','<div class="progress"><div class="progress__bar" ng-class="type && \'progress__bar--\' + type" role="progressbar" aria-valuenow="{{value}}" aria-valuemin="0" aria-valuemax="{{max}}" ng-style="{width: (percent < 100 ? percent : 100) + \'%\'}" aria-valuetext="{{percent | number:0}}%" aria-labelledby="{{::title}}" ng-transclude></div></div>');
$templateCache.put('components/resetPassword/resetPassword.html','<div class="container"><div class="row"><div class="col-6 push-3 col-lg-4 push-lg-4"><h1>\xC5terst\xE4ll l\xF6senord</h1><p>Ange e-postadressen som \xE4r kopplad till ditt konto s\xE5 skickar vi en l\xE4nk f\xF6r att \xE5terst\xE4lla ditt l\xF6senord.</p><form name="resetPasswordForm" ng-submit="resetPasswordForm.$valid &amp;&amp; resetPassword()" novalidate><div class="form-group" ng-class="{\r\n          \'form-group--error\': (resetPasswordForm.inputEmail.$touched || resetPasswordForm.$submitted) &amp;&amp; resetPasswordForm.inputEmail.$invalid\r\n        }"><label for="inputEmail">E-post</label> <input type="email" class="form-control form-control--narrow form-control--gray-border" id="inputEmail" name="inputEmail" ng-model="vm.email" ng-pattern="/^(?!.*@hyresgastforeningen\\.se)/i" placeholder="E-post" required></div><div class="form-group" ng-class="{\'form-group--error\': (resetPasswordForm.inputPhone.$touched || resetPasswordForm.$submitted) &amp;&amp; resetPasswordForm.inputPhone.$invalid}"><label for="inputPhone">Telefon</label> <input type="phone" class="form-control form-control--narrow form-control--gray-border" id="inputPhone" name="inputPhone" ng-model="vm.phone" ng-pattern="/(\\+46|0)[0-9]{7,}/im" placeholder="Telefon"></div><p ng-show="resetPasswordForm.$error.pattern">Du kan inte \xE5terst\xE4lla l\xF6senordet f\xF6r ditt Hyresg\xE4stf\xF6reningen-konto via Portalen. Kontakta intern IT om du beh\xF6ver hj\xE4lp med att \xE5terst\xE4lla ditt l\xF6senord.</p><div><button type="submit" class="btn spacer-mr-medium">Skicka</button><hy-progress-circular size="20px" ng-show="saving"></hy-progress-circular><a class="link--turquoise link--no-underline" href="/">Avbryt</a></div></form></div></div></div>');
$templateCache.put('components/select-all-checkbox/select-all-checkbox.html','<input class="custom-control__input" type="checkbox" id="{{name}}MasterCheckbox" ng-model="isMasterSelected" ng-change="toggleSelectMaster()" ng-disabled="isDisabled"> <label class="custom-control__label" for="{{name}}MasterCheckbox" ng-class="{\'form-check__checkbox-label--disabled\' : isDisabled}"></label>');
$templateCache.put('components/sorter/sorter.html','<div class="display-flex" ng-class="labelPosition === \'side\' ? \'\': \'flex-direction-column\'" ng-style="{\'margin-left\': labelPosition !== \'side\' ? \'3px\' : \'\'}"><div ng-if="showLabel" class="display-flex align-items-center" ng-style="{\r\n        \'margin-right\': labelPosition === \'side\' ? \'3px\' : \'\', \r\n        \'margin-left\': labelPosition !== \'side\' ? \'3px\' : \'\',\r\n        \'font-size\': labelSize ? labelSize : \'\'\r\n       }">{{labelText ? labelText : \'Sortera p\xE5:\'}}</div><div class="dropdown dropdown--hide-caret" uib-dropdown><button type="button" class="btn btn--icon btn--icon-sort btn--squary btn--squary-white btn--squary-white__sort" uib-dropdown-toggle>{{itemName}}</button><div class="dropdown-menu dropdown-menu--right dropdown-menu--sort" uib-dropdown-menu role="menu"><button ng-repeat="item in listColumns" type="button" class="dropdown-item dropdown-item--sort" ng-click="sort(item.column, item.title)">{{item.title}} <span ng-show="order === item.column" class="btn--icon" ng-class="direction === \'ASC\' ? \'btn--icon-sort-up\' : \'btn--icon-sort-down\'"></span></button></div></div></div>');
$templateCache.put('components/std-display/std-display.html','<style>.tooltip-inner {\r\n          max-width: 500px;\r\n          min-width: 330px;\r\n          white-space: nowrap !important;\r\n     }\r\n     .object-name {\r\n          float: left;\r\n     }\r\n     .object-value {\r\n          margin-left: 20px;\r\n          float: right;\r\n     }</style><div ng-if="model" tooltip-placement="bottom" uib-tooltip-html="getTitle()"><small>std. {{ getCost() | number : 2 }}</small></div>');
$templateCache.put('components/tabcontrol/tabcontrol.html','<ul class="tabs spacer-ml-medium spacer-mr-medium"><li class="col tabs__item" ng-click="selectTab(item.key)" ng-class="{\'tabs__item--active\': item.key === currentTab}" ng-show="item.show !== false" ng-repeat="item in tablist"><a class="tabs__item--link" ng-class="{\'tabs__item--active-link\': item.key === currentTab}" href="#">{{item.title}}</a></li></ul><hr class="hr--thin hr--margin-reset spacer-mb-medium">');
$templateCache.put('components/tabs/tabs.html','<ul class="tabs"><li class="col tabs__item" ng-class="getClass(item)" ng-repeat="item in tablist"><a class="tabs__item--link" ng-class="{\'tabs__item--has-errors\': item.hasErrors, \'tabs__item--active-link\': isActive(item.path)}" href="{{item.path}}"><div ng-if="item.party && item.party !== \'both\'" class="icon spacer-ml-small icon--party icon--party-{{item.party}}"></div><div ng-if="item.party && item.party === \'both\'" class="icon spacer-ml-small icon--party icon--party-hgf"></div><div ng-if="item.party && item.party === \'both\'" class="icon spacer-ml-small icon--party icon--party-fa"></div>{{item.title}}<div ng-if="item.statusIcon" class="icon spacer-ml-small {{ iconPack[item.statusIcon] }}"></div><hy-counter class="spacer-ml-small" count-value="{{ item.number }}" ng-if="item.number" tooltip-text="{{ item.numberTooltip }}"></hy-counter></a></li></ul>');
$templateCache.put('components/tableau/tableau.html','<div class="display-flex space-between align-items-bottom"><div><div ng-if="infoText || attributes.length" class="alert alert--success-bordered alert--white-bg alert--slim text-margin-reset di block1"><span class="spacer-mr-xsmall" ng-if="infoText"><div class="icon icon--inline icon--info"></div><b>*</b> {{ infoText }} </span><span ng-repeat="attribute in attributes" class="spacer-mr-xsmall"><span class="{{attribute.class}} tableau-legendbox">A</span> <small>{{ attribute.legend }}</small></span></div><div class="di block2" ng-show="checkboxes">{{ selectedRows.length }} vald / <span ng-if="getTotal() !== total">{{ getTotal() }} urval / </span>{{ total }} totalt <button class="btn spacer-ml-medium" ng-show="isFiltered()" ng-click="toggle(false)">{{ getFilteredStateTitle(false) }} urval</button> <button class="btn" ng-class="{ \'btn--secondary\' : isFiltered() }" ng-click="toggle(true)">{{ getFilteredStateTitle(true) ? \'Markera\' : \'Avmarkera\' }} alla</button><div ng-show="actions" class="di spacer-ml-medium"><button ng-repeat="action in actions" ng-if="!action.isVisible || action.isVisible()" class="btn spacer-mr-xsmall {{ action.btnClass }}" ng-disabled="(!hasSelectedRows) && !action.common" ng-click="doAction(action)">{{action.title}} <img ng-if="action.icon" src="/img/icons/{{action.icon}}.png"></button></div></div><!--common actions for non-selector--><div class="di block2" ng-show="!checkboxes"><div ng-show="actions" class="di spacer-ml-medium"><button ng-repeat="action in actions" ng-if="(!action.isVisible || action.isVisible()) && action.common" class="btn spacer-mr-xsmall {{ action.btnClass }}" ng-click="doAction(action)">{{action.title}} <img ng-if="action.icon" src="/img/icons/{{action.icon}}.png"></button></div></div></div><!-- tray --><div><div ng-if="filtersActive()" class="tableau-filters-active di" ng-click="clearFilters()"></div></div></div><table id="table{{uuid}}" class="table table--margin-b-reset"><thead class="table__header"><tr><th ng-if="checkboxes" class="table__th"></th><th ng-repeat="column in columns" class="table__th" ng-class="{ \'clickable\' : isColumnSortable(column.column) }" ng-click="columnClick(column)">{{ column.title }} <span ng-if="(sortColumn === column.column) && (sortDirection ===\'DESC\')">\u2193</span> <span ng-if="(sortColumn === column.column) && (sortDirection ===\'ASC\')">\u2191</span></th></tr><tr ng-if="!noFiltering"><th class="table__th" ng-if="checkboxes" width="40"><hy-combobox items="tickedFilterItems" current-item="filters.ticked" current-item-changed="applyTickedFilterDropdown(); $event.preventDefault();"></hy-combobox></th><th class="table__th" ng-repeat="column in columns"><div class="display-flex" ng-if="getFilterType(column) === \'text\'"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="filters[column.column]" hy-enter="applyFilter(column.column);"> <a href="#" class="btn--icon btn--icon-search" ng-click="applyFilter(column.column); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div><div class="display-flex" ng-if="getFilterType(column) === \'combobox\'"><hy-combobox prompt="V\xE4lj {{ column.title.toLowerCase() }}" items="column.lookup.dictionary" id-property="{{ column.lookup.idProperty }}" text-property="{{ column.lookup.textProperty }}" current-item-changed="applyFilterTimed(column.column); $event.preventDefault();" current-item="filters[column.column]"></hy-combobox></div><div class="display-flex" ng-if="getFilterType(column) === \'multicombobox\'"><hy-multicombobox name="mcb{{column.column}}" items="column.lookup.dictionary" id-property="{{ column.lookup.idProperty }}" text-property="{{ column.lookup.textProperty }}" current-items-changed="applyFilterTimed(column.column); $event.preventDefault();" current-items="filters[column.column]" button-text="S\xF6k"></hy-multicombobox></div></th></tr></thead><tr class="table__tr" ng-repeat="item in dataVM" ng-class="rowClass(item)"><td ng-if="checkboxes"><hy-checkbox name="item{{uuid}}{{item[idProperty]}}" model="ticked[item[idProperty]]" ticked="tick(); $event.preventDefault();"></hy-checkbox></td><td ng-repeat="column in columns" class="table__td table__td--minimalistic" ng-class="columnClass(column)" ng-click="itemSelected(item, column)"><div ng-if="column.column && !column.template"><div ng-if="!column.type"><span ng-if="!column.lookup">{{getX(item, column.column)}} </span><span ng-if="column.lookup">{{ getLookupValue(item, column) }}</span></div><div ng-if="column.type === \'star\'"><span ng-if="getX(item, column.column)"><b>*</b></span></div><div ng-if="column.type === \'date\'"><span class="wordbreak-stay-puft">{{ getX(item, column.column) | date:\'yyyy-MM-dd\' }}</span></div><div ng-if="column.type === \'boolean\'"><span>{{ getX(item, column.column) ? \'Ja\' : \'nej\' }}</span></div><div ng-if="column.type === \'array\'"><hy-array-display model="getX(item, column.column)"></hy-array-display></div></div><div ng-if="column.template"><ng-transclude ng-transclude-slot="{{column.template}}"></ng-transclude></div></td></tr></table><div class="tableau-context-menu" id="context-menu{{uuid}}"><div class="tableau-context-menu-header">{{ contextInfo.title }}</div><ul><li ng-repeat="mi in contextMenuitems" ng-if="!mi.isVisible || mi.isVisible(contextRow, contextInfo)" ng-click="mi.fn(contextRow, contextInfo)">{{ mi.title }}</li></ul></div><div class="display-flex justify-content-center"><hy-progress-circular size="40px" ng-if="!ready"></hy-progress-circular></div><div class="text-right spacer-mt-medium" ng-show="!noPagination && (getTotal() > limit)"><hy-paginator get-list="flipPage" page-offset="offset" page-limit="limit" total-count="getTotal()"></hy-paginator></div><div class="text-right spacer-mt-medium" ng-if="noPagination || ((total > 0) && (total < limit))"><strong>Totalt: </strong>{{total}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="total === 0 && ready">Inga rader hittades.</div>');
$templateCache.put('components/timeline/timeline.html','<ul class="timeline"><li ng-repeat="activity in activities" class="timeline__activity" uib-tooltip-html="\'{{activity.heading}}\'" tooltip-placement="top" tooltip-class="{{tooltipClass}}" tooltip-is-open="activity.isOpen" tooltip-trigger="\'none\'" tooltip-append-to-body="true" ng-click="$index <= currentOpenIdx() &amp;&amp; gotoActivity(activity.name)" ng-class="{\'timeline__activity--active\': activity.isOpen, \'timeline__activity--disabled\': $index > currentOpenIdx()}"><div class="timeline__activity-label"><span class="text-success" ng-class="{\'text-turquoise-light\': ($index < currentOpenIdx()), \'text-gray\': ($index > currentOpenIdx()), \'text-bold\': ($index === currentOpenIdx())}">{{activity.headingShort}}</span></div></li></ul><uib-progressbar class="timeline__progressbar" value="progress()"></uib-progressbar>');
$templateCache.put('components/treeview/tree-node.html','<li ng-show="filterMatch()"><span ng-class="{ \'treeview-caret\' : (subitems && subitems.length) }"></span><hy-checkbox class="di" ng-if="checkboxes" name="checkbox{{data}}" model="ticked[data]" changed="tickMe()"></hy-checkbox><span class="treeview-label" ng-class="{ \'treeview-selected\': model.data === data }" ng-click="selectMe()">{{ label }}</span><ul ng-if="subitems && subitems.length" class="treeview-ul treeview-nested"><tree-node ng-repeat="node in subitems" label="{{node.label}}" model="model" ticked="ticked" data="node.data" subitems="node.subitems" viewbag="viewbag" checkboxes="checkboxes" selected="selected"></tree-node></ul></li>');
$templateCache.put('components/treeview/tree-view.html','<div ng-if="mode"><input ng-if="filtering" placeholder="{{filterPrompt}}" type="text" class="form-control" ng-model="viewbag.filter"><ul ng-if="!showSelectionOnCollapse || (showSelectionOnCollapse && !model)" ng-class="{ \'treeview-list-mode\': mode === \'list\' }" class="form-control treeview-ul"><tree-node ng-if="!skipRoot" model="model" label="{{root.label}}" data="root.data" subitems="root.subitems" checkboxes="checkboxes" viewbag="viewbag" ticked="ticked" selected="selected"></tree-node><tree-node ng-if="skipRoot" model="model" ng-repeat="node in root.subitems" label="{{node.label}}" data="node.data" ticked="ticked" viewbag="viewbag" subitems="node.subitems" checkboxes="checkboxes" selected="selected"></tree-node></ul><div ng-if="showSelectionOnCollapse && model" class="form-control treeview-selection" ng-click="reset()">{{ model.label }}</div></div>');
$templateCache.put('components/omniscroller/omniscrollbar.html','<div class="omniscroller-scrollbar"><div class="scrollbar-thumb" ng-style="{\'width\': contentWidth + \'px\', \'height\': \'1px\' }"></div></div>');
$templateCache.put('components/omniscroller/omniscroller.html','<div ng-if="active" class="omniscroller-container"><div class="omniscroller-content" ng-transclude></div></div><div ng-if="!active" ng-transclude></div>');
$templateCache.put('components/wizard/wizard-step.html','<div class="wizard-step" id="{{::wizardId}}" ng-show="isOpen"><h2 ng-if="alternativeWizard" class="spacer-mb-small spacer-mt-xlarge">{{heading}} - <span class="text-success">{{headingStepNumber}}. </span>{{headingShort}}</h2><h2 ng-if="!alternativeWizard" class="spacer-mb-small spacer-mt-xlarge"><span class="text-success">{{headingStepNumber}}.</span>{{heading}}</h2><div class="alert alert--warning-bordered alert--white-bg alert--slim display-flex align-items-center" ng-if="alertText"><div class="icon icon--message-warning spacer-mr-small"></div><strong class="spacer-mr-small">{{alertHeader}}:</strong>{{alertText}}</div><div ng-transclude></div></div>');
$templateCache.put('components/wizard/wizard.html','<h1 class="text-margin-reset">{{headerText}}</h1><div class="row align-items-center spacer-mt-medium" ng-if="!wizardVm.hideHeader"><div class="col spacer-mt-xlarge"><button class="btn btn--reset-transition btn--arrow-left" ng-click="wizardVm.previous()" ng-hide="wizardVm.isFirstStep()">Tillbaka</button></div><div class="col-12 col-lg-9 spacer-mt-large"><hy-timeline activities="wizardVm.enabledSteps" alternative-wizard="wizardVm.alternativeWizard"></hy-timeline></div><div class="col text-right spacer-mt-xlarge"><button class="btn btn--reset-transition btn--arrow-right" ng-click="wizardVm.next()" ng-hide="wizardVm.isLastStep() || wizardVm.hideNextButton()" ng-disabled="!wizardVm.isCurrentStepValid()">N\xE4sta</button> <button class="btn btn--reset-transition" ng-click="wzSubmit()" ng-show="wizardVm.isLastStep()" ng-disabled="!wizardVm.isCurrentStepValid() || submitDisabled"><span>{{submitText ? submitText : \'Skicka\'}}</span></button></div></div><div ng-transclude></div>');
$templateCache.put('components/timepicker/timepicker.html','<table class="timepicker"><tbody><tr class="text-center"><td><a href="#" ng-click="incrementHours()" ng-class="{disabled: noIncrementHours()}" class="btn--icon btn--icon-triangle-up-turquoise" ng-disabled="noIncrementHours()" tabindex="-1"></a></td><td>&nbsp;</td><td><a href="#" ng-click="incrementMinutes()" ng-class="{disabled: noIncrementMinutes()}" class="btn--icon btn--icon-triangle-up-turquoise" ng-disabled="noIncrementMinutes()" tabindex="-1"></a></td></tr><tr><td><input type="text" placeholder="HH" ng-model="hours" ng-change="updateHours()" ng-class="{\'timepicker__input--error\': invalidHours}" class="form-control form-control--gray-border form-control--narrow" ng-readonly="::readonlyInput" maxlength="2" tabindex="{{::tabindex}}" ng-disabled="noIncrementHours()"></td><td>:</td><td><input type="text" placeholder="MM" ng-model="minutes" ng-change="updateMinutes()" ng-class="{\'timepicker__input--error\': invalidMinutes}" class="form-control form-control--gray-border form-control--narrow" ng-readonly="::readonlyInput" maxlength="2" tabindex="{{::tabindex}}" ng-disabled="noIncrementMinutes()"></td></tr><tr class="text-center"><td><a href="#" ng-click="decrementHours()" ng-class="{disabled: noDecrementHours()}" class="btn--icon btn--icon-triangle-down-turquoise" ng-disabled="noDecrementHours()" tabindex="-1"></a></td><td>&nbsp;</td><td><a href="#" ng-click="decrementMinutes()" ng-class="{disabled: noDecrementMinutes()}" class="btn--icon btn--icon-triangle-down-turquoise" ng-disabled="noDecrementMinutes()" tabindex="-1"></a></td></tr></tbody></table>');
$templateCache.put('components/tooltip/tooltip-html-popup.html','<div class="tooltip-arrow"></div><div class="tooltip-inner" ng-bind-html="contentExp()"></div>');
$templateCache.put('components/tooltip/tooltip-popup.html','<div class="tooltip-arrow"></div><div class="tooltip-inner" ng-bind="content"></div>');
$templateCache.put('components/tooltip/tooltip-template-popup.html','<div class="tooltip-arrow"></div><div class="tooltip-inner" uib-tooltip-template-transclude="contentExp()" tooltip-template-transclude-scope="originScope()"></div>');
$templateCache.put('resources/real-estate/apartment-history.html','<div class="spacer-large"><h3 class="text-margin-reset">L\xE4genhetsinformation</h3><h2 class="text-margin-reset" title="{{vm.apartment.nationalNo | nationalNoTitleControl}}">{{vm.apartment.nationalNo | nationalNoControl}}, {{ vm.apartment.streetAddress }}, {{ vm.apartment.municipality }}</h2><div>{{vm.statusString}}</div><div ng-if="vm.history.length === 1" class="spacer-large">Det finns bara en avslutad f\xF6rhandling eller \xF6verenskommelse:<br><br><a href="{{vm.host}}/negotiations/{{vm.history[0].id}}" target="_blank">{{ vm.history[0].type | hyNegotiationTypes }}, {{vm.history[0].name}}, {{ vm.history[0].effectiveFrom }}, {{ vm.data[0][0] }} kr</a><br><br><div>Aktuell hyra: <b>{{ vm.apartment.rent.monthlyRent }} kr</b></div></div><div ng-if="vm.history.length === 0" class="spacer-large">Ingen avslutad f\xF6rhandling eller \xF6verenskommelse!</div><div ng-if="vm.history.length > 1"><div class="modal-pillar" style="height: 370px; width: 470px;"><canvas id="myChart" class="chart chart-line" chart-data="vm.data" chart-series="vm.series" chart-labels="vm.labels" chart-options="vm.options" chart-hover="vm.chartHover"></canvas></div><div class="modal-pillar spacer-ml-small" style="width:400px;"><div><b>Aktuell hyra: <span class="objectValue">{{ vm.apartment.rent.monthlyRent.toFixed(0) }} kr</span></b></div><div ng-if="vm.objectsExtra"><div ng-repeat="item in vm.objectsExtra">{{ item.name }} <span class="objectValue">{{ item.value }} kr</span></div></div><br><div ng-repeat="item in vm.historyReversed"><a ng-if="item.id" href="{{vm.host}}/negotiations/{{item.id}}" target="_blank" uib-tooltip="{{item.name}}">{{ item.type | hyNegotiationTypes }}, {{ item.effectiveFrom }} </a><span ng-if="!item.id && item.type >= 0">{{ item.type | hyNegotiationTypes }}, {{ item.effectiveFrom }} </span><span ng-if="item.type === -1">Hyra fr\xE5n, {{ item.effectiveFrom }} </span><span ng-if="item.caseNumber.includes(\'void\')">M</span></div><div class="apartmenthistory-floater" ng-if="vm.objectsExtraFloater"><div ng-repeat="item in vm.objectsExtraFloater">{{ item.name }} <span class="apartmenthistory-objectValue">{{ item.value }}</span></div></div></div></div></div>');
$templateCache.put('resources/reports/index.html','<div class="container" id="container"></div><div class="container" ng-show="!vm.busy"><div><h1>Statistik</h1></div><div class="no-gutters row"><div class="col-5">Rapport:<div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle ng-disabled="vm.reports.length === 0"><span class="text-gray-dark">{{vm.selectedReport.name || \'V\xE4lj rapport\'}}</span></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="report in vm.reports" type="button" class="dropdown-item" ng-click="vm.reportChanged(report)">{{report.name}}</button></div></div><h4>V\xE4lj rapport, st\xE4ll in parametrar och tryck p\xE5 "K\xF6r" knappen</h4></div><div class="col-1"></div><div class="col-5" ng-show="vm.selectedReport"><report-parameters params="vm.selectedReport.params" param-dictionaries="vm.paramDictionaries" is-valid="vm.canRunReport"></report-parameters><button class="btn" ng-click="vm.runReport()" ng-disabled="!vm.canRunReport">K\xF6r</button></div></div></div><hr class="hr--thin"><div class="container" ng-show="vm.reportResult && !vm.reportResult.rows.length && !vm.busy">Ingen data</div><div class="container" ng-show="vm.reportResult && vm.reportResult.rows.length && !vm.busy"><div ng-if="vm.reportResult.chart && !vm.reportResult.chart.dataOptions.hideMainChart" class="chart-container spacer-centered report-chart"><canvas id="myChart" ng-if="vm.reportResult.chart.type === vm.chartTypes.BAR" width="1100" height="350" class="chart chart-bar" chart-data="vm.reportResult.chart.data" chart-colors="vm.reportResult.chart.colors" chart-options="vm.reportResult.chart.options" chart-labels="vm.reportResult.chart.labels" chart-series="vm.reportResult.chart.series"></canvas><canvas id="myChart" ng-if="vm.reportResult.chart.type === vm.chartTypes.LINE" width="1100" height="350" class="chart chart-line" chart-data="vm.reportResult.chart.data" chart-colors="vm.reportResult.chart.colors" chart-options="vm.reportResult.chart.options" chart-labels="vm.reportResult.chart.labels" chart-series="vm.reportResult.chart.series"></canvas></div><div ng-repeat="subChart in vm.reportResult.chart.subCharts track by $index" class="di" ng-if="vm.reportResult.chart && vm.reportResult.chart.subCharts"><div class="chart-container report-subchart{{ subChart.options.legend.display ? \'-last\' : \'\' }}"><!-- chart.js, why are you so mean? --><canvas ng-if="!subChart.options.legend.display && (subChart.type === vm.chartTypes.BAR)" width="305" height="350" class="chart chart-bar" chart-data="subChart.data" chart-colors="subChart.colors" chart-options="subChart.options" chart-labels="subChart.labels" chart-series="subChart.series"></canvas><canvas ng-if="subChart.options.legend.display && (subChart.type === vm.chartTypes.BAR)" width="1000" height="350" class="chart chart-bar" chart-data="subChart.data" chart-colors="subChart.colors" chart-options="subChart.options" chart-labels="subChart.labels" chart-series="subChart.series"></canvas><canvas ng-if="!subChart.options.legend.display && (subChart.type === vm.chartTypes.LINE)" width="305" height="350" class="chart chart-line" chart-data="subChart.data" chart-colors="subChart.colors" chart-options="subChart.options" chart-labels="subChart.labels" chart-series="subChart.series"></canvas><canvas ng-if="subChart.options.legend.display && (subChart.type === vm.chartTypes.LINE)" width="1000" height="350" class="chart chart-line" chart-data="subChart.data" chart-colors="subChart.colors" chart-options="subChart.options" chart-labels="subChart.labels" chart-series="subChart.series"></canvas></div></div><hy-tabcontrol tablist="vm.resultTabs" current-tab="vm.currentResultTab" ng-if="vm.reportResult.chart"></hy-tabcontrol><div class="report-summary"><div class="export-box"><button type="button" class="btn btn--icon-download-round" ng-click="vm.export(\'pdf\')">PDF</button> <button type="button" class="btn btn--icon-download-round" ng-click="vm.export(\'xlsx\')">XLS</button></div><div ng-if="vm.currentResultTab == \'raw\'" class="text-gray-dark"><span ng-if="vm.reportResult.rows.length === 1000">Max 1000 rader visas, f\xF6r fler rader ladda ner XLS fil </span><span ng-if="vm.reportResult.rows.length !== 1000">{{vm.reportResult.rows.length}} rader</span></div><h3>{{vm.reportResult.title}}</h3><div id="paramSummary"><span ng-repeat="param in vm.reportResult.params"><span ng-if="param.data && (param.isSet || param.mandatory)"><b>{{param.name}}</b>: <span ng-if="param.type === vm.paramTypes.YEAR">{{param.data}} </span><span ng-if="param.type === vm.paramTypes.MUNICIPALITY ||\r\n                       param.type === vm.paramTypes.KITCHEN_TYPE">{{ param.data }} </span><span ng-if="param.type === vm.paramTypes.RANGE">{{ param.data.min }}-{{ param.data.max }} </span><span ng-if="param.type === vm.paramTypes.HGF_OFFICE || \r\n                       param.type === vm.paramTypes.NEGOTIATION_STATUS ||\r\n                       param.type === vm.paramTypes.NEGOTIATION_STATUS_GROUP ||\r\n                       param.type === vm.paramTypes.NEGOTIATION_TYPE ||\r\n                       param.type === vm.paramTypes.REGION ||\r\n                       param.type === vm.paramTypes.APARTMENT_STATUS">{{param.data.name}} </span><span ng-if="param.type === vm.paramTypes.HGF_OFFICE_MULTIPLE"><span ng-repeat="o in param.data">{{o.name}},</span></span></span></span></div><div ng-bind-html="vm.reportResult.summary"></div><omniscroller active="vm.reportResult.tooMuchDetailColumns"><div ng-style="{ \'width\': vm.wideLoadDetails + \'px\' }" ng-show="vm.currentResultTab == \'raw\'"><table class="table table--fixed table--margin-b-reset spacer-mt-medium" width="100%"><tr><th ng-repeat="column in vm.reportResult.columns" width="{{column.width}}%" class="table__th table__th--minimalistic">{{column.name}}</th><!--\r\n        <th ng-if="vm.selectedReport.subs"> </th>\r\n        --></tr></table></div></omniscroller><div ng-show="vm.currentResultTab == \'aggregated\'" id="magic-table-header-container" class="table__magic-table-container"><table class="table table--margin-b-reset spacer-mt-medium" width="100%"><tr><th width="30%" ng-class="{\'table__td--fixed-aggregated\': vm.reportResult.tooMuchLabels}"></th><th ng-repeat="label in vm.reportResult.chart.labels" width="{{vm.reportResult.aggregatedColumnWidth}}" ng-class="{\'table__td--fixed-aggregated\': vm.reportResult.tooMuchLabels}" class="table__th table__th--minimalistic">{{label}}</th></tr></table></div></div><div ng-show="vm.currentResultTab==\'raw\'" id="detailedTab"><omniscroller active="vm.reportResult.tooMuchDetailColumns"><div ng-style="{ \'width\': vm.wideLoadDetails + \'px\' }"><table class="table table--fixed table--margin-b-reset spacer-mt-medium" width="100%"><tbody ng-repeat="row in vm.reportResult.rows"><tr><td ng-repeat="cell in row track by $index" ng-if="$index &lt; vm.reportResult.columns.length" width="{{vm.reportResult.columns[$index].width}}%" class="table__td table__td--minimalistic" ng-class="{ \'parentParam\': vm.isParentParamRes($index) }" ng-click="vm.expandResultRow(row.index)"><report-cell-value type="{{vm.reportResult.columns[$index].type}}" value="cell"></report-cell-value></td><!--\r\n            <td ng-if="vm.selectedReport.subs">\r\n              <span class="report-sub-call" ng-click="vm.expandResultRow($index)">\u25BA</span>\r\n            </td>\r\n            --></tr><tr ng-if="vm.reportResult.expandedRow == $index"><td colspan="{{vm.reportResult.colCount}}"><div class="row"><div class="col"><hy-combobox prompt="V\xE4lj subrapport" items="vm.selectedReport.subs" id-property="id" text-property="name" current-item="vm.reportResult.selectedSubreport" current-item-changed="vm.subreportChanged($index)"></hy-combobox></div><div class="col"><report-parameters row-index="{{$index}}" params="vm.reportResult.selectedSubreport.params" param-dictionaries="vm.paramDictionaries" is-valid="vm.canRunSubreport"></report-parameters><button class="btn" ng-click="vm.runSubreport()" ng-disabled="!vm.canRunSubreport">K\xF6r</button></div></div></td></tr></tbody></table></div></omniscroller><omniscrollbar scope-id="detailedTab" ng-if="vm.reportResult.tooMuchDetailColumns"></omniscrollbar></div><div ng-show="vm.currentResultTab==\'aggregated\'"><div id="magic-table-container" class="table__magic-table-container"><table id="magic-table" class="table table--margin-b-reset spacer-mt-medium" width="100%"><tr ng-repeat="row in vm.reportResult.chart.data track by $index" ng-init="parentIndex = $index" ng-click="vm.drilldown($index)"><td width="30%" ng-class="{\'table__td--fixed-aggregated\': vm.reportResult.tooMuchLabels}"><u>{{vm.reportResult.chart.series[$index]}}</u></td><td ng-repeat="cell in row track by $index" ng-class="{\'table__td--disabled\' : cell === 0,\r\n                         \'table__td--fixed-aggregated\': vm.reportResult.tooMuchLabels}" width="{{vm.reportResult.aggregatedColumnWidth}}" class="table__td table__td--minimalistic"><report-cell-value type="{{vm.getAggregatedColumnType($index)}}" value="cell"></report-cell-value></td></tr></table></div><div ng-show="vm.reportResult.tooMuchLabels" id="magic-scroller" class="table__magic--scroller"><div id="magic-scroller-working-body"></div></div></div></div><hy-progress-circular size="200px" center-on-page ng-show="vm.busy">');
$templateCache.put('resources/reports/parameters.html','<span ng-if="anyNonParentParameters()">Parametrar:</span><table width="100%"><tr ng-repeat="param in params | filter: noParentRefs"><td width="35%"><div ng-if="!param.mandatory && !param.allOmit && !param.parentRef" class="custom-control custom-control--checkbox di"><input class="custom-control__input" type="checkbox" checked="checked" id="isSet_{{$index}}" name="isSet_{{$index}}" ng-model="param.isSet" ng-change="checkCanRunReport()"> <label class="custom-control__label" for="isSet_{{$index}}"></label></div>{{param.name}}</td><td ng-if="param.type === paramTypes.NUMBER || \r\n               param.type === paramTypes.STRING ||\r\n               param.type === paramTypes.DATE"><input class="form-control" type="{{ simpleParamsToHtml[param.type] }}" ng-model="param.data" placeholder="{{ param.type === paramTypes.NUMBER ? \'nummer\' : \'\' }}" ng-disabled="(!param.isSet && !param.mandatory) || param.parentRef" ng-change="checkCanRunReport()"></td><td ng-if="param.type === paramTypes.YEAR || \r\n               param.type === paramTypes.HGF_OFFICE || \r\n               param.type === paramTypes.NEGOTIATION_STATUS ||\r\n               param.type === paramTypes.NEGOTIATION_STATUS_GROUP ||\r\n               param.type === paramTypes.NEGOTIATION_TYPE ||\r\n               param.type === paramTypes.REGION ||\r\n               param.type === paramTypes.MUNICIPALITY ||\r\n               param.type === paramTypes.KITCHEN_TYPE ||\r\n               param.type === paramTypes.APARTMENT_STATUS"><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" uib-dropdown-toggle class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" ng-disabled="(!param.isSet && !param.mandatory && !param.allOmit) || paramDictionaries[param.type].length === 0"><span class="text-gray-dark" ng-if="param.type === paramTypes.YEAR">{{param.data || \'V\xE4lj \xC5r\' }} </span><span class="text-gray-dark" ng-if="param.type === paramTypes.HGF_OFFICE">{{param.data.name || \'V\xE4lj HGF kontor\' }} </span><span class="text-gray-dark" ng-if="param.type === paramTypes.NEGOTIATION_STATUS ||\r\n                                              param.type === paramTypes.NEGOTIATION_STATUS_GROUP ||\r\n                                              param.type === paramTypes.APARTMENT_STATUS">{{param.data.name || \'V\xE4lj status\' }} </span><span class="text-gray-dark" ng-if="param.type === paramTypes.NEGOTIATION_TYPE">{{param.data.name || \'V\xE4lj type\' }} </span><span class="text-gray-dark" ng-if="param.type === paramTypes.REGION">{{param.data.name || \'V\xE4lj region\' }} </span><span class="text-gray-dark" ng-if="param.type === paramTypes.MUNICIPALITY">{{param.data || \'V\xE4lj kommun\' }} </span><span class="text-gray-dark" ng-if="param.type === paramTypes.KITCHEN_TYPE">{{param.data || \'V\xE4lj k\xF6kstyp\' }}</span></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="item in paramData[param.name]" type="button" class="dropdown-item" ng-click="setDropdownValue(param, item)"><span ng-if="param.type === paramTypes.YEAR ||\r\n                         param.type === paramTypes.MUNICIPALITY ||\r\n                         param.type === paramTypes.KITCHEN_TYPE">{{item}} </span><span ng-if="param.type === paramTypes.HGF_OFFICE ||\r\n                         param.type === paramTypes.NEGOTIATION_STATUS ||\r\n                         param.type === paramTypes.NEGOTIATION_STATUS_GROUP ||\r\n                         param.type === paramTypes.NEGOTIATION_TYPE ||\r\n                         param.type === paramTypes.REGION ||\r\n                         param.type === paramTypes.APARTMENT_STATUS">{{item.name}}</span></button></div></div></td><td ng-if="param.type === paramTypes.RANGE"><range-input min="param.data.min" max="param.data.max" changed="checkCanRunReportDelayed()"></range-input></td><td ng-if="param.type === paramTypes.COLLECTION"><hy-combobox name="param.name" items="param.dictionary" text-property="text" current-item="param.data"></hy-combobox></td><td ng-if="param.type === paramTypes.REGION_MUNICIPALITY"><tree-view mode="field" root="paramDictionaries[param.type]" model="param.data" changed="checkCanRunReportDelayed()"></tree-view></td><td ng-if="param.type === paramTypes.HGF_OFFICE_MULTIPLE"><report-multi-select param="param" param-dictionary="paramDictionaries[param.type]" title="V\xE4lj HGF kontor" id-property="id" text-property="name" selection-done="checkCanRunReport()"></report-multi-select></td></tr></table>');
$templateCache.put('resources/reports/report-cell-value.html','<span ng-switch="type" uib-tooltip-html="tooltip"><span ng-switch-when="date">{{value | date:\'yyyy-MM-dd\'}} </span><span ng-switch-when="money"><span ng-if="value">{{value | number:0}}<xsmall>kr</xsmall></span><span ng-if="!value">---</span> </span><span ng-switch-when="money2"><span ng-if="value">{{value | number:2}}<xsmall>kr</xsmall></span><span ng-if="!value">---</span> </span><span ng-switch-default>{{value}}</span></span><xsmall ng-if="plusN > 0"><b>+<span class="text-gray">{{ plusN }}</span></b></xsmall>');
$templateCache.put('resources/reports/subreport.html','<div class="modal-header modal-header--medium"><h2 class="text-center text-margin-reset">{{ vm.reportResult.title }}</h2></div><div class="modal-body modal-body--medium" id="modal-body"><div id="subreport-omniscroller"><omniscroller active="true"><table class="table table--margin-b-reset spacer-mt-medium" width="100%"><tr><th ng-repeat="column in vm.reportResult.columns" width="{{column.width}}%" class="table__th table__th--minimalistic">{{column.name}}</th></tr><tr ng-repeat="row in vm.rows"><td ng-repeat="cell in row track by $index" ng-if="$index &lt; vm.reportResult.columns.length" width="{{vm.reportResult.columns[$index].width}}%" class="table__td table__td--minimalistic"><report-cell-value type="{{vm.reportResult.columns[$index].type}}" value="cell"></report-cell-value></td></tr></table></omniscroller><omniscrollbar scope-id="subreport-omniscroller"></omniscrollbar></div><div class="text-right spacer-mt-medium spacer-mb-large" ng-if="vm.total > vm.limit"><hy-paginator get-list="vm.flipPage" page-offset="vm.offset" page-limit="vm.limit" total-count="vm.total"></hy-paginator></div></div>');
$templateCache.put('components/modal/confirmation/confirmationModal.html','<div class="modal-header modal-header--xsmall"><h4 class="text-margin-reset">\xC4r du s\xE4ker p\xE5 att du vill ta bort?</h4></div><div class="modal-footer modal-footer--xsmall modal-footer--xsmall-right"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button type="button" class="btn btn--secondary" ng-click="vm.confirm()">Ta bort</button></div>');
$templateCache.put('components/modal/confirmation/confirmationModalWithMessage.html','<div class="modal-header modal-header--xsmall"><h4 class="text-margin-reset">{{vm.message}}</h4></div><div class="modal-footer modal-footer--xsmall modal-footer--xsmall-right"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button type="button" class="btn btn--secondary" ng-click="vm.confirm()">Ta bort</button></div>');
$templateCache.put('components/modal/confirmation/confirmationModalWithMessageAndButtons.html','<div class="modal-header modal-header--medium"><h2 class="text-margin-top-reset text-margin-bottom-reset">{{vm.caption}}</h2></div><div class="modal-body modal-body--medium" id="modal-body"><div class="row"><div class="col-12"><h5>{{vm.message}}</h5></div></div></div><div class="modal-footer modal-footer--xsmall modal-footer--xsmall-right"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">{{vm.cancelBtn}}</a> <button type="button" class="btn btn--secondary" ng-click="vm.confirm()">{{vm.confirmBtn}}</button></div>');
$templateCache.put('components/modal/confirmation/confirmationYesNoModal.html','<div class="modal-header modal-header--xsmall"><h4 class="text-margin-reset">\xC4r du s\xE4ker?</h4></div><div class="modal-footer modal-footer--xsmall modal-footer--xsmall-right"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn spacer-mr-xsmall" type="button" ng-click="vm.confirm()">Ja</button></div>');
$templateCache.put('components/modal/information/negotiationCallInfoModal.html','<div class="modal-header"><div class="display-flex justify-content-end flex-grow-1 spacer-mt-small"><a class="link--turquoise link--icon link--icon-close text-bold" ng-click="vm.close()">St\xE4ng</a></div></div><div class="modal-body" id="modal-body"><div ng-repeat="group in vm.info"><h3 class="text-margin-reset text-transform-none"><u>{{ group.groupTitle }}</u></h3><ul class="spacer-mb-large padding-reset"><li class="spacer-mb-small" ng-repeat="item in group.items"><strong title="{{item.code}}">{{item.code | hyNegotiationTypes}}</strong>: {{ item.desc }}</li></ul></div></div>');
$templateCache.put('components/modal/information/negotiationTypeInfoModal.html','<div class="modal-header"><div class="display-flex justify-content-end flex-grow-1 spacer-mt-small"><a class="link--turquoise link--icon link--icon-close text-bold" ng-click="vm.close()">St\xE4ng</a></div></div><div class="modal-body" id="modal-body"><div class="spacer-mb-xlarge"><h4 class="text-margin-bottom-reset text-transform-none"><u>Fastighetsf\xF6rhandling</u></h4><span>\xC4HS skapar ett \xE4rende per fastighet. Varje enskilt \xE4rende kan avslutas n\xE4r det finns en \xF6verenskommelse f\xF6r den fastighet \xE4rendet avser.</span><h4 class="text-margin-bottom-reset text-transform-none"><u>\xC4garf\xF6rhandling</u></h4><span>\xC4HS skapar ett \xE4rende omfattande samtliga fastigheter. \xC4rendet kan avslutas f\xF6rst n\xE4r \xF6verenskommelse finns f\xF6r samtliga fastigheter som omfattas av \xE4rendet.</span></div></div>');
$templateCache.put('components/modal/question/question-1.html','<div class="modal-header modal-header--xsmall"><h4 class="text-margin-reset">{{vm.message}}</h4></div><div class="modal-footer modal-footer--xsmall modal-footer--xsmall-right"><button ng-repeat="answer in vm.answers" class="btn spacer-mr-xsmall" type="button" ng-click="vm.answer(answer)">{{answer}}</button></div>');
$templateCache.put('components/modal/question/question-2.html','<div><div class="fake-ceiling-border"><div class="modal-header modal-header--xsmall"><h4 class="text-margin-reset">{{vm.message}}</h4></div><div class="modal-footer modal-footer--xsmall modal-footer--xsmall-center"><button ng-repeat="answer in vm.answers" class="btn spacer-mr-xsmall" type="button" ng-click="vm.answer(answer)">{{answer}}</button></div></div></div>');
$templateCache.put('components/modal/confirmation-info/confirmationModalInfo.html','<div class="modal-header modal-header--xsmall"><h4 class="text-margin-reset">{{ vm.text.question }} {{ vm.text.subject }}?</h4></div><div class="modal-body modal-body--xsmall" ng-show="vm.text.additionalInfo"><p>{{ vm.text.additionalInfo }}</p></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">{{ vm.text.abort }}</a> <button type="button" class="btn" ng-class="{\'btn--secondary\': vm.buttonClassSecondary}" ng-click="vm.confirm()">{{ vm.text.confirm }}</button></div>');
$templateCache.put('components/typeahead/template/typeahead-match.html','<a href tabindex="-1" ng-bind-html="match.label | uibTypeaheadHighlight:query" ng-attr-title="{{match.label}}"></a>');
$templateCache.put('components/typeahead/template/typeahead-popup.html','<ul class="dropdown-menu" ng-show="isOpen() && !moveInProgress" ng-style="{top: position().top+\'px\', left: position().left+\'px\'}" role="listbox" aria-hidden="{{!isOpen()}}"><li class="uib-typeahead-match" ng-repeat="match in matches track by $index" ng-class="{active: isActive($index) }" ng-mouseenter="selectActive($index)" ng-click="selectMatch($index, $event)" role="option" id="{{::match.id}}"><div uib-typeahead-match index="$index" match="match" query="query" template-url="templateUrl"></div></li></ul>');
$templateCache.put('resources/negotiation-call/new/partials/subpartials/_preview-and-submit-general-col-1.html','<h4 class="text-margin-bottom-reset">Typ av f\xF6rhandling</h4>{{vm.negotiationCall.type | hyNegotiationTypes}}<h4 class="text-margin-bottom-reset">Meddelande till hyresg\xE4stf\xF6reningen</h4><div class="spacer-mb-medium"><span class="word-break-balanced" ng-if="vm.negotiationCall.message" ng-bind-html="vm.negotiationCall.message | preventDateBreak"></span> <span class="word-break-balanced" ng-if="!vm.negotiationCall.message">Ej angivet</span></div><h4 class="text-margin-reset">\xD6nskat datum f\xF6r hyres\xE4ndring</h4>{{vm.rentEffectiveFrom | date:\'yyyy-MM-dd\'}}');
$templateCache.put('resources/negotiation-call/new/partials/subpartials/_preview-and-submit-general-col-2.html','<h4 class="text-margin-reset">\xD6nskat datum f\xF6r hyres\xE4ndring</h4>{{vm.rentEffectiveFrom | date:\'yyyy-MM-dd\'}}');
$templateCache.put('resources/negotiation-call/new/partials/subpartials/_preview-and-submit-general-header.html','<div class="row"><div class="col-8 spacer-mb-small"><h3 class="spacer-mt-medium">F\xF6rhandlingsframst\xE4llan: <span class="text-capitalize">{{vm.negotiationCall.name}}</span></h3></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/subpartials/_preview-and-submit-warning.html','<div class="row"><div class="col-12"><h3 class="text-danger text-margin-top-reset">OBS! Endast f\xF6rhandsgranskning</h3></div></div>');
$templateCache.put('resources/negotiation/show/modals/move-items/moveItemsModal.html','<form name="moveItemsForm"><div class="modal-header modal-header--small"><h2 class="text-margin-top-reset text-margin-bottom-reset">Flytta {{ vm.apartmentMode ? \'l\xE4genheter\' : \'fastigheter\' }}</h2></div><div ng-if="vm.targetNegotiations &amp;&amp; vm.ready" class="modal-body modal-body--small"><p class="line-height-narrow" id="modal-body">Flytta de markerade {{ vm.apartmentMode ? \'l\xE4genheterna\' : \'fastigheterna\' }} till en annan f\xF6rhandling som skapats fr\xE5n samma framst\xE4llan. Relaterade dokument som t ex underlag och m\xF6tesprotokoll flyttas inte med fastigheterna till den nya f\xF6rhandlingen, om ni \xF6nskar s\xE5 kan ni ladda upp dem manuellt till den nya f\xF6rhandlingen.</p><div class="row spacer-mb-medium"><div class="col-6"><div class="form-group spacer-mb-reset"><label for="inputMeetingTitle">V\xE4lj f\xF6rhandling att flytta {{ vm.apartmentMode ? \'l\xE4genheterna\' : \'fastigheterna\' }} till</label><div class="dropdown dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width" uib-dropdown-toggle><hy-progress-circular size="20px" ng-hide="!vm.negotiationListReady"></hy-progress-circular><div ng-show="!vm.moveToNewNegotiation">{{ vm.selectedNegotiation.id ? vm.selectedNegotiation.caseNumber + \' \' + vm.selectedNegotiation.name : \'V\xE4lj f\xF6rhandling\' }}</div></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="negotiation in vm.targetNegotiations | filter: { id : \'!\' + vm.currentNegotiation.id }" type="button" class="dropdown-item" ng-click="vm.selectedNegotiation = negotiation">{{ negotiation.caseNumber }} {{ negotiation.name }}</button></div></div></div></div></div></div><div ng-if="!vm.targetNegotiations &amp;&amp; vm.ready" class="modal-body modal-body--small"><p class="line-height-narrow" id="modal-body">Det finns inga f\xF6rhandlingar att flytta {{ vm.apartmentMode ? \'l\xE4genheter\' : \'fastigheter\' }} till. Inga andra f\xF6rhandlingar skapade fr\xE5n samma framst\xE4llan som denna f\xF6rhandling hittades.</p></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt </a><button ng-if="vm.targetNegotiations" class="btn spacer-mr-xsmall" ng-click="vm.moveItems()">Flytta {{ vm.apartmentMode ? \'l\xE4genheter\' : \'fastigheter\' }}</button></div></form>');
$templateCache.put('resources/agreement/rentlist/new/partials/_examine-first-line.html','<ng-include src="\'resources/validation/partials/_examine-first-line-body.html\'"></ng-include><div class="row"><div class="col text-right"><button class="btn" ng-click="vm.save();" ng-disabled="!vm.isCurrentStepValid() || vm.saving">Skicka</button></div></div>');
$templateCache.put('resources/agreement/rentlist/new/partials/_file-upload-rent-list.html','<div hy-file-upload-2 ng-if="vm.agreement.negotiationId" blob-id="vm.agreement.rentListBlobId" metadata="vm.metadata" valid-file-types="xlsx" max-file-size="1073741824" upload-url="/api/negotiations/{{vm.agreement.negotiationId}}/agreements/{{vm.agreement.id}}/rent-list" uploader="vm.uploader" auto-upload validate-upload validation-url="/api/rent-files" f-on-pending="vm.onPending" disable-flash error-invalid-file="vm.errorInvalidFile" validation-response="vm.validationResponse" class="alert alert--border-turq alert--white-bg alert--squary"><form name="vm.fileUploadRentListForm" ng-submit="vm.fileUploadRentListForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><input name="inputRentListBlobId" id="inputRentListBlobId" type="hidden" ng-model="vm.inputRentListBlobId" required><div class="position-relative row"><div class="file-upload__progress-bar"><uib-progressbar type="turquoise" value="vm.uploader.progress" ng-show="vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating" ng-class="\'progress--square\'"></uib-progressbar></div></div><div class="row"><div class="col-12 col-lg-8"><div class="display-flex align-items-center"><div class="file-upload__button"><label ng-if="vm.uploader &amp;&amp; !vm.agreement.rentListBlobId &amp;&amp; !vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating" class="btn btn--reset-transition btn--upload"><input type="file" nv-file-select="" uploader="vm.uploader"></label> <button class="btn btn--reset-transition btn--cancel btn-spinning-flack" ng-show="vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating" ng-click="vm.uploader.cancelUpload()"></button><hy-progress-circular size="90px" ng-show="vm.uploader.isValidating"></hy-progress-circular><div class="btn btn--reset-transition btn--done" ng-show="vm.agreement.rentListBlobId &amp;&amp; !vm.uploader.isValidating"></div></div><div class="file-upload__status"><h4 class="text-margin-reset"><span ng-show="vm.uploader &amp;&amp; !vm.agreement.rentListBlobId &amp;&amp; !vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating &amp;&amp; !vm.errorInvalidFile">Ladda upp hyreslista med de nya hyrorna</span> <span ng-show="vm.errorInvalidFile">{{vm.errorInvalidFile}}</span> <span ng-show="vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating">Laddar upp...</span> <span ng-show="vm.uploader.isValidating">Analyserar din fil...</span> <span ng-show="vm.agreement.rentListBlobId &amp;&amp; !vm.uploader.isValidating">Uppladdat!</span></h4><p ng-show="vm.uploader.currentFileName">{{vm.uploader.currentFileName}}</p><button class="btn btn--secondary btn--reset-transition" ng-show="vm.agreement.rentListBlobId || vm.uploader.isUploading || vm.uploader.isValidating" ng-click="vm.uploader.cancelUpload()">{{vm.agreement.rentListBlobId ? \'Ta bort\' : \'Avbryt\'}}</button> <button class="btn btn--reset-transition" ng-show="vm.agreement.rentListBlobId || vm.uploader.isValidating" ng-click="vm.toggleAdditionalInfoPane(); $event.preventDefault();">{{ vm.showAdditionalInfoPane ? \'D\xF6lj\' : \'Visa\' }} info</button></div></div><div ng-show="vm.showAdditionalInfoPane"><ng-include src="\'resources/validation/partials/_additional-info.html\'"></ng-include></div></div></div></form></div><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/agreement/show/overview/partials/_free-agreement-side-actions.html','<div class="alert alert--narrow-gray alert--squary" ng-hide="vm.isPageLoading() || vm.agreement.signed"><div class="spacer-mb-medium text-uppercase"><strong>Avtal g\xE4ller f\xF6r best\xE5nd</strong></div><div class="spacer-mb-small" ng-if="Auth.canDo(\'editAgreement\')"><a class="btn btn--full-width" href="/agreements/{{vm.agreement.id}}/real-estate" ng-class="{\'btn--info\': vm.agreement.numProperties > 0}">{{vm.agreement.numProperties > 0 ? (vm.signStatusVisible ? \'Visa best\xE5nd\' : \'\xC4ndra best\xE5nd\') : \'L\xE4gg till\'}}</a></div><div class="spacer-mb-small">Totalt fastigheter: {{ vm.agreement.numProperties }}<br></div></div><div class="alert alert--narrow-gray alert--squary" ng-if="!vm.agreement.signed && !vm.agreement.conditionTags.internal && Auth.canDo(\'editAgreement\') && vm.signStartVisible" ng-hide="vm.isPageLoading() || Auth.isExternalUser()"><div class="spacer-mb-small text-uppercase"><strong>Representanter</strong></div><button class="btn btn--full-width btn--info spacer-mb-small" ng-click="vm.changeFreeParticipants()">\xC4ndra deltagare</button><!--\r\n    \xD6verenskommelse typ\r\n    <hy-combobox prompt="V\xE4lj typ"\r\n                 items="vm.FreeAgreementTypes"\r\n                 text-property="name"\r\n                 current-item-changed="vm.changeFreeAgreementType(selection)"\r\n                 current-item="vm.freeAgreementType">\r\n    </hy-combobox>\r\n--></div>');
$templateCache.put('resources/agreement/show/overview/partials/_side-actions.html','<div class="alert alert--squary" ng-hide="vm.negotiation.isFinished || Auth.isDelegateUser() || vm.agreement.agreedByAssociation || vm.agreement.agreedByOwner\r\n  || vm.agreement.type !== vm.AgreementTypes.AGREEMENT_WITHOUT_RENTLIST"><div class="spacer-mb-small text-uppercase"><strong>Processbeskrivning f\xF6r \xF6verenskommelse</strong></div><div><p class="spacer-mt-small"><strong>1.</strong> V\xE4lj Best\xE5nd</p><p class="spacer-mt-small"><strong>2.</strong> Ange Ikrafttr\xE4dandedatum</p><p class="spacer-mt-small"><strong>3.</strong> Ange Offentligg\xF6randedatum</p><p class="spacer-mt-small"><strong>4.</strong> Skriv \xD6verenskommelsetext<br>&ensp;<strong>&#9679;</strong> Skriv Ytterligare text (ej tvingande)</p><p class="spacer-mt-small spacer-mb-medium"><strong>Spara</strong></p><p class="spacer-mt-small"><strong>5.</strong> Visa bilagor \u2013 Ladda upp dokument.<br>(<b>Hyreslista i PDF</b> + andra dokument)</p><p class="spacer-mt-small"><strong>6. B\xF6rja signera</strong></p></div></div><div class="alert alert--narrow-gray alert--squary" ng-if="!vm.signDownloadVisible && !vm.agreement.signed && Auth.canDo(\'editAgreement\')"><div class="spacer-mb-medium text-uppercase"><strong>\xC4ndra avtalspart</strong></div><button class="btn btn--full-width btn--info" ng-click="vm.changeFigurants(vm.negotiation.canSkipRentlist)">\xC4ndra</button></div><div class="alert alert--narrow-gray alert--squary" ng-hide="vm.agreement.conditionTags.revertSourceId || vm.agreement.signed || vm.isPageLoading() || Auth.isDelegateUser() || vm.negotiation.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY"><div class="spacer-mb-medium text-uppercase"><strong>\xD6verenskommelsen g\xE4ller f\xF6r best\xE5nd</strong></div><div class="spacer-mb-small" ng-if="Auth.canDo(\'editAgreement\')"><a class="btn btn--full-width" ng-if="!vm.skipRentlist" href="/negotiations/{{vm.negotiation.id}}/agreements/{{vm.agreement.id}}/real-estate" ng-class="{\'btn--info\': vm.agreement.numApartments > 0}">{{vm.agreement.numApartments > 0 ? (vm.signStatusVisible ? \'Visa best\xE5nd\' : \'\xC4ndra\') : \'L\xE4gg till\'}} </a><a class="btn btn--full-width" ng-if="vm.skipRentlist" href="/negotiations/{{vm.negotiation.id}}/agreements/{{vm.agreement.id}}/real-estate" ng-class="{\'btn--info\': vm.agreement.numProperties > 0}">{{vm.agreement.numProperties > 0 ? (vm.signStatusVisible ? \'Visa best\xE5nd\' : \'\xC4ndra\') : \'L\xE4gg till\'}}</a></div><input type="hidden" name="inputNumApartments" ng-model="vm.agreement.numApartments" required ng-pattern="/^[1-9]/"></div><div class="alert alert--narrow-gray alert--squary" ng-hide="(Auth.isDelegateUser() || vm.isNewlyConstructedProperty) || (!vm.agreement.agreedByAssociation &amp;&amp; !vm.agreement.agreedByOwner)"><div class="spacer-mb-small text-uppercase"><strong>\xC4r vi \xF6verens?</strong></div><div ng-hide="\r\n    (vm.agreement.agreedByAssociation &amp;&amp; vm.agreement.agreedByOwner) ||\r\n    ((Auth.isHgfUser()) &amp;&amp; vm.agreement.agreedByAssociation) ||\r\n    (Auth.isExternalUser() &amp;&amp; vm.agreement.agreedByOwner)"><div class="spacer-mt-small" ng-show="vm.agreement.agreedByAssociation || vm.agreement.agreedByOwner">En part har godk\xE4nt, v\xE4ntar nu p\xE5 ditt godk\xE4nnande!</div></div><div class="text-danger text-uppercase"><strong ng-show="vm.agreement.agreedByAssociation &amp;&amp; vm.agreement.agreedByOwner">Alla parter har godk\xE4nt</strong></div></div><div class="alert alert--narrow-gray alert--squary" ng-hide="vm.agreement.signed || vm.isPageLoading() || vm.skipRentlist || Auth.isDelegateUser()"><div class="spacer-mb-small text-uppercase"><strong>Uppdatera hyror</strong></div><div class="spacer-mb-medium">Uppdatera hyrorna i \xF6verenskommelsen till vad som best\xE4mts mellan parterna. H\xE4r kan \xE4ven status anges om exempelvis en l\xE4genhet strandas. Efter uppladdning av hyreslistan g\xE5r det ej att \xE4ndra fastighetsbest\xE5ndet med hyror och statusar. Hyrorna \xE4ndras i databasen efter att \xF6verenskommelsen \xE4r signerad av samtliga parter och f\xF6rhandlingen \xE4r avslutad</div><div ng-if="vm.agreement.numApartments !== vm.agreement.numAdjustedApartments" class="red-text spacer-mb-small">Totalt: {{ vm.agreement.numApartments }}<br>Med ny hyra: {{ vm.agreement.numAdjustedApartments }}</div><div class="spacer-mb-medium"><a href="/negotiations/{{vm.agreement.negotiationId}}/agreements/{{vm.agreement.id}}/rentlist/edit" class="btn btn--full-width" ng-class="{\'abtn--disabled\': vm.signStatusVisible || !(vm.agreement.numApartments > 0)}" ng-show="vm.negotiation.id && !vm.negotiation.isFinished && Auth.canDo(\'editAgreement\')">\xC4ndra hyror och status</a></div><div ng-show="!(vm.agreement.numApartments > 0 || vm.isNewlyConstructedProperty)">V\xE4lj best\xE5nd f\xF6rst</div><a href="/negotiations/{{vm.agreement.negotiationId}}/agreements/{{vm.agreement.id}}/rentlist/new" class="btn btn--full-width btn--info" ng-class="{\'abtn--disabled\': vm.signStatusVisible || (!(vm.agreement.numApartments > 0 || vm.isNewlyConstructedProperty))}" ng-show="vm.negotiation.id && !vm.negotiation.isFinished && Auth.canDo(\'editAgreement\')">{{vm.agreement.rentFile.blobId ? \'Skriv \xF6ver lista\' : \'Ladda upp lista\'}}</a><div ng-hide="!vm.agreement.rentFile.blobId"><a href="/api/negotiations/{{vm.agreement.negotiationId}}/agreements/{{vm.agreement.id}}/rent-list/{{vm.agreement.rentFile.blobId}}" target="_self" download class="link--turquoise btn--icon btn--icon-download--dark-turq" ng-show="vm.agreement.rentFile.blobId && Auth.canDo(\'editAgreement\')">H\xE4mta ny hyreslista</a></div><div class="spacer-mt-medium"><a href="/negotiations/{{vm.agreement.negotiationId}}/agreements/{{vm.agreement.id}}/rentlist/view" class="btn btn--full-width btn--info" ng-class="{\'abtn--disabled\': !(vm.agreement.numApartments > 0)}" ng-show="vm.negotiation.id && (vm.signStatusVisible || vm.negotiation.isFinished) && vm.isNewlyConstructedProperty">Visa</a></div></div><div class="alert alert--narrow-gray alert--squary" ng-if="Auth.isHgfUser()"><div class="spacer-mb-small text-uppercase"><strong>Villkor</strong></div>Medlemsutskick: {{ vm.agreement.conditionTags.isReadyToSend ? \'Ja\' : \'Nej\' }}</div><div class="alert alert--narrow-gray alert--squary" ng-if="vm.roleWarning"><div class="red-text"><b>Varning!</b></div><div>PART OCH ROLL</div><div>Kom ih\xE5g att \xE4ndra part och roll f\xF6r den som ska skriva under. Valet av undertecknande part baseras p\xE5 fastighetens aktuella f\xF6rhandlingsordning och personens roll i f\xF6rhandlingen.</div></div>');
$templateCache.put('resources/agreement/free/new/modal/new-free-agreement-modal.html','<div class="modal-header modal-header--small"><h2 class="text-margin-top-reset text-margin-bottom-reset">Nytt avtal</h2></div><div class="modal-body modal-body--small" id="modal-body"><label for="inputType">Avtal typ</label><hy-combobox id="inputType" prompt="V\xE4lj typ" items="vm.FreeAgreementTypes" text-property="name" current-item="vm.type"></hy-combobox><hy-participants user-container="vm.userContainer"></hy-participants><!--\r\n  \xD6verenskommelsetext\r\n  <textarea class="form-control form-control--gray-border"\r\n            ng-model="vm.message"\r\n            maxlength="255">\r\n  </textarea>\r\n--></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" ng-click="vm.ok()" ng-disabled="!vm.type || vm.userContainer.hgfSide.length===0 || vm.userContainer.faSide.length===0">Spara</button></div>');
$templateCache.put('resources/blocking/show/apartment/edit/edit.html','<div class="spacer-mb-xxxlarge" ng-controller="EditBlockingApartmentCtrl as editApartmentsVM"><hy-wizard wz-submit="editApartmentsVM.save()" goto-next="editApartmentsVM.gotoNext" is-current-step-valid="editApartmentsVM.isCurrentStepValid" welcome-message="Ut\xF6ka fastighetsbest\xE5nd" submit-text="{{vm.blocking.numApartments > 0 ? \'L\xE4gg till\' : \'Spara\'}}" submit-disabled="editApartmentsVM.saving" alternative-wizard="true"><div class="text-center spacer-mt-large" ng-show="editApartmentsVM.saving"><hy-progress-circular size="60px"></hy-progress-circular></div><div class="alert alert--border-turq alert--squary alert--white-bg spacer-mt-large"><hy-wizard-step heading="Ut\xF6ka fastighetsbest\xE5nd" heading-short="Distrikt" heading-step-number="1" name="district" is-valid="editApartmentsVM.districts.length > 0 &amp;&amp; editApartmentsVM.districtForm.$valid &amp;&amp; editApartmentsVM.atleastOneDistrictSelected" alternative-wizard="true"><ng-include src="\'resources/blocking/show/apartment/edit/partials/_district.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="Ut\xF6ka fastighetsbest\xE5nd" heading-short="Fastigheter" heading-step-number="2" name="properties" is-valid="editApartmentsVM.properties.length > 0 &amp;&amp; editApartmentsVM.propertiesForm.$valid &amp;&amp; editApartmentsVM.atleastOnePropertySelected" alternative-wizard="true"><ng-include src="\'resources/blocking/show/apartment/edit/partials/_properties.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="Ut\xF6ka fastighetsbest\xE5nd" heading-short="L\xE4genheter" heading-step-number="3" name="apartments" is-valid="editApartmentsVM.apartments.length > 0 &amp;&amp; editApartmentsVM.apartmentsForm.$valid &amp;&amp; editApartmentsVM.atleastOneApartmentSelected" alternative-wizard="true"><ng-include src="\'resources/blocking/show/apartment/edit/partials/_apartments.html\'"></ng-include></hy-wizard-step></div></hy-wizard></div>');
$templateCache.put('resources/agreement/free/new/partials/_basic.html','<div class="alert alert--border-turq alert--squary"><form name="vm.basicForm" ng-submit="vm.basicForm.$valid &amp;&amp; vm.gotoNextStep()" class="spacer-mb-xlarge" novalidate><div class="row"><div class="col-4"><div class="form-group" ng-class="{\r\n              \'form-group--error\': (vm.basicForm.inputAvtalType.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputAvtalType.$invalid,\r\n            }"><label for="inputAvtalType"><h4 class="text-margin-bottom-reset">Avtal typ</h4></label><hy-combobox prompt="V\xE4lj typ" items="vm.FreeAgreementTypes" text-property="name" current-item="vm.avtalType" required></hy-combobox></div></div><div class="col-4"><hy-participants user-container="vm.userContainer" internal="vm.internal"></hy-participants></div><div class="col-4"><img src="/img/logo-red-text.png" width="317" class="spacer-mt-medium"><div class="info text-gray-dark spacer-mt-small">H\xE4r kan du skapa ett avtal fr\xE5n givna typer mellan fastighets\xE4gare och HGF.</div></div></div></form></div><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/agreement/free/new/partials/_choose-process.html','<form name="vm.chooseForm" ng-submit="vm.chooseForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><!--V\xE4lj best\xE5nd--><div class="wizard-choice display-flex flex-direction-row spacer-mb-small" ng-class="{\'wizard-choice--active\' : vm.realEstateChoiceMethod === \'DIRECT_PROPERTIES\'}" ng-click="vm.setRadioModelByOutsideClick(\'DIRECT_PROPERTIES\')"><div class="custom-control custom-control--radio custom-control--radio--no-margin"><input class="custom-control__input custom-control__input--hidden" type="radio" id="inputSelectProperties" name="radio-group" tabindex="2" ng-model="vm.realEstateChoiceMethod" value="DIRECT_PROPERTIES" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="inputSelectProperties"></label></div><div class="flex-grow-1"><h3 class="spacer-mt-reset">V\xE4lj best\xE5nd</h3><ul class="wizard-choice__list"><li class="wizard-choice__list-item--yes">Skriv in fastighetsbeteckningarna direkt i portalen utan att ladda upp n\xE5gon fil</li></ul></div></div><!--Utan best\xE5nd--><div class="wizard-choice display-flex flex-direction-row spacer-mb-small" ng-class="{\'wizard-choice--active\' : vm.realEstateChoiceMethod === \'SKIP_PROPERTIES\'}" ng-click="vm.setRadioModelByOutsideClick(\'SKIP_PROPERTIES\')"><div class="custom-control custom-control--radio custom-control--radio--no-margin"><input class="custom-control__input custom-control__input--hidden" type="radio" id="inputSkipProperties" name="radio-group" tabindex="2" ng-model="vm.realEstateChoiceMethod" value="SKIP_PROPERTIES" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="inputSkipProperties"></label></div><div class="flex-grow-1"><h3 class="spacer-mt-reset">Utan best\xE5nd</h3><ul class="wizard-choice__list"><li class="wizard-choice__list-item--yes">Skapa avtal utan best\xE5nd</li></ul></div></div></form><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/blocking/show/apartment/list/list.html','<div ng-controller="ListBlockingApartmentsCtrl as listApartmentsVM"><hy-progress-circular size="60px" center-on-page ng-hide="listApartmentsVM.readyApartments"></hy-progress-circular><div class="row"><div class="col display-flex space-between"><h3 class="text-margin-reset">Valt fastighetsbest\xE5nd</h3><button class="btn" ng-click="vm.editApartmentMode = true" ng-hide="vm.editApartmentMode || vm.negotiation.realEstateStatus.notAssignedCount === 0 ||\r\n        vm.blocking.isAgreed || (vm.currentUserParty != vm.blocking.creatorParty)" ng-disabled="vm.negotiation.isFinished">L\xE4gg till fler</button></div></div><hr class="spacer-mt-small"><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="listApartmentsVM.listColumns" order="listApartmentsVM.sortColumn" direction="listApartmentsVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div><table class="table table--margin-reset"><tr class="table__header"><th class="table__th table__td--width-10">Nationellt<br>l\xE4genhetsnr<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextNationalApartmentNo" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">L\xE4genhetsnr<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextApartmentNo" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Gatuadress<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextStreetAddress" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Fastighet<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextPropertyDesignation" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Kommun<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextMunicipality" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Ing\xE5ende hyra (kr/m\xE5nad)</th><th class="table__th table__td--width-15">\xD6verenskommen ny hyra (kr/m\xE5nad)</th><th class="table__th"><hy-progress-circular size="20px" ng-show="listApartmentsVM.deletingApartment"></hy-progress-circular></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="apartment in listApartmentsVM.apartments" ng-class="{\'table__tr--muted\' : !listApartmentsVM.readyApartments || listApartmentsVM.deletingApartment}"><td class="table__td table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{apartment.nationalNo | nationalNoControl}}</td><td class="table__td table__td--nowrap">{{apartment.ownerNo}}</td><td class="table__td">{{apartment.streetAddress}}</td><td class="table__td">{{apartment.property.propertyDesignation}}</td><td class="table__td">{{apartment.municipality}}</td><td class="table__td">{{apartment.rent.monthlyRent | number : 2}}</td><td class="table__td">{{apartment.newRent.monthlyRent | number : 2}}</td><td class="table__td text-right"><a href="#" ng-click="listApartmentsVM.delete(apartment); $event.preventDefault();" class="btn--icon btn--icon-bin btn--icon-bin btn--icon--no-padding" ng-hide="vm.negotiation.isFinished || vm.blocking.isAgreed || (vm.currentUserParty !== vm.blocking.creatorParty) || listApartmentsVM.deletingApartment"><span class="sr-only">Ta bort</span></a></td></tr></table><div class="display-flex space-between spacer-mt-medium" ng-show="listApartmentsVM.apartmentsTotalCount > 0"><div>Ladda ner listan <a class="link--turquoise link--underline spacer-ml-xsmall" target="_self" download href="/api/negotiations/{{vm.blocking.negotiationId}}/blockings/{{vm.blocking.id}}/export-rent-list">h\xE4r</a></div><div ng-show="listApartmentsVM.apartmentsTotalCount > 0 &amp;&amp; listApartmentsVM.apartmentsTotalCount <= listApartmentsVM.apartmentsPageLimit"><strong>Totalt: </strong>{{listApartmentsVM.apartmentsTotalCount}}</div><hy-paginator ng-show="listApartmentsVM.apartmentsTotalCount > listApartmentsVM.apartmentsPageLimit" get-list="listApartmentsVM.getApartments" page-offset="listApartmentsVM.apartmentsPageOffset" page-limit="listApartmentsVM.apartmentsPageLimit" total-count="listApartmentsVM.apartmentsTotalCount"></hy-paginator></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="listApartmentsVM.apartmentsTotalCount === 0">Inga l\xE4genheter hittades.</div></div>');
$templateCache.put('resources/blocking/show/properties/edit/edit.html','<div class="spacer-mb-xxxlarge" ng-controller="EditBlockingPropertiesCtrl as propertiesVM"><div class="text-center spacer-mt-large" ng-show="propertiesVM.saving"><hy-progress-circular size="60px"></hy-progress-circular></div><div class="row spacer-mt-negative-xlarge"><div class="col text-right"><button class="btn btn--reset-transition" ng-disabled="propertiesVM.properties.length === 0 || !propertiesVM.propertiesForm.$valid || !propertiesVM.atleastOnePropertySelected" ng-click="propertiesVM.save()"><span>Spara</span></button></div></div><ng-include src="\'resources/blocking/show/properties/partials/_properties.html\'"></ng-include><div class="row spacer-mt-large align-items-center"><div class="col text-right"><a class="link--turquoise link--no-underline spacer-mr-medium" ng-click="vm.editPropertiesMode = false">Avbryt </a><button class="btn btn--reset-transition" ng-disabled="propertiesVM.properties.length === 0 || !propertiesVM.propertiesForm.$valid || !propertiesVM.atleastOnePropertySelected" ng-click="propertiesVM.save()"><span>Spara</span></button></div></div></div>');
$templateCache.put('resources/blocking/show/properties/partials/_properties.html','<div class="alert alert--border-turq alert--squary alert--white-bg spacer-mt-large"><hy-progress-circular size="60px" center-on-page ng-hide="propertiesVM.ready"></hy-progress-circular><h2 class="text-margin-reset">{{vm.blocking.numProperties > 0 ? \'Ut\xF6ka fastighetsbest\xE5nd\' : \'V\xE4lj fastighetsbest\xE5nd\'}}</h2><div class="row"><div class="col-12 spacer-mb-medium"><form name="propertiesVM.propertiesForm" ng-submit="propertiesVM.propertiesForm.$valid" novalidate><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="propertiesVM.listColumns" order="propertiesVM.sortColumn" direction="propertiesVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Alla</th><th class="table__th">Fastigheter</th><th class="table__th">Fastighets\xE4gare</th><th class="table__th">Kommun</th><th class="table__th">LM distrikt</th><th class="table__th">Antal l\xE4genheter</th></tr><tr><th class="table__th table__td--width-6"><div class="custom-control custom-control--checkbox"><hy-select-all-checkbox checkboxes="propertiesVM.properties" name="properties" selected-ids="propertiesVM.blocking.selectedPropertyIds" is-master-selected="propertiesVM.blocking.isAllPropertiesSelected" is-at-least-one-selected="propertiesVM.atleastOnePropertySelected" total-count="propertiesVM.propertyTotalCount" is-disabled="propertiesVM.inputPropertiesTextProperty || propertiesVM.inputPropertiesTextMunicipality || propertiesVM.inputPropertiesTextDistrict"></hy-select-all-checkbox></div></th><th class="table__th table__td--width-25"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="propertiesVM.inputPropertiesTextProperty" hy-enter="propertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-30"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="propertiesVM.inputPropertiesTextOwner" hy-enter="propertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-20"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="propertiesVM.inputPropertiesTextMunicipality" hy-enter="propertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-20"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="propertiesVM.inputPropertiesTextDistrict" hy-enter="propertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="property in propertiesVM.propertiesVirtual" ng-class="{\'table__tr--muted\' : !propertiesVM.ready,\r\n                  \'text-black\': propertiesVM.blocking.selectedPropertyIds[property.id],\r\n                  \'text-gray-dark\': !propertiesVM.blocking.selectedPropertyIds[property.id]}"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{property.id}}" ng-model="propertiesVM.blocking.selectedPropertyIds[property.id]"> <label class="custom-control__label" for="{{property.id}}"></label></div></td><td class="table__td table__td--minimalistic">{{ property.propertyDesignation }}</td><td class="table__td table__td--minimalistic"><hy-array-display model="property.owners"></hy-array-display></td><td class="table__td table__td--minimalistic">{{ property.municipality }}</td><td class="table__td table__td--minimalistic">{{ property.district }}</td><td class="table__td table__td--minimalistic">{{ property.numNegotiatedApartments }}</td><td class="table__td table__td--minimalistic"></td></tr></table><div class="text-right spacer-mt-medium" ng-show="propertiesVM.propertiesTotalCount > propertiesVM.pageLimit"><hy-paginator get-list="propertiesVM.getPropertiesVirtual" page-offset="propertiesVM.pageOffset" page-limit="propertiesVM.pageLimit" total-count="propertiesVM.propertiesTotalCount"></hy-paginator></div><div class="text-right spacer-mt-medium" ng-show="propertiesVM.propertiesTotalCount > 0 &amp;&amp; propertiesVM.propertiesTotalCount <= propertiesVM.pageLimit"><strong>Totalt: </strong>{{propertiesVM.propertiesTotalCount}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="propertiesVM.propertiesTotalCount === 0">Inga fastigheter hittades.</div></form></div></div></div>');
$templateCache.put('resources/blocking/show/properties/list/list.html','<div ng-controller="ListBlockingPropertiesCtrl as listPropertiesVM"><hy-progress-circular size="60px" center-on-page ng-hide="listPropertiesVM.ready"></hy-progress-circular><div class="row"><div class="col display-flex space-between"><h3 class="text-margin-reset">Valt fastighetsbest\xE5nd</h3><span ng-hide="vm.editPropertiesMode || vm.blocking.isAgreed || (vm.currentUserParty != vm.blocking.creatorParty)"><button class="btn" ng-hide="vm.negotiation.realEstateStatus.notAssignedCount === 0" ng-disabled="vm.negotiation.isFinished" ng-click="vm.editPropertiesMode = true">L\xE4gg till fler</button></span></div></div><hr class="spacer-mt-small"><!-- TODO: use HyTableau --><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="listPropertiesVM.listColumns" order="listPropertiesVM.sortColumn" direction="listPropertiesVM.sortDirection"></hy-sorter></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Fastigheter</th><th class="table__th">Fastighets\xE4gare</th><th class="table__th">Kommun</th><th class="table__th">LM distrikt</th><th class="table__th">Antal l\xE4genheter</th></tr><tr><th class="table__th table__td--width-25"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="listPropertiesVM.inputPropertiesTextProperty" hy-enter="listPropertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listPropertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-30"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="listPropertiesVM.inputPropertiesTextOwner" hy-enter="listPropertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listPropertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-20"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="listPropertiesVM.inputPropertiesTextMunicipality" hy-enter="listPropertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listPropertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-20"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="listPropertiesVM.inputPropertiesTextDistrict" hy-enter="listPropertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listPropertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th></th></tr></thead><tr class="table__tr--minimalistic table__tr--disable-click" ng-repeat="property in listPropertiesVM.propertiesVirtual" ng-class="{\'table__tr--muted\' : !listPropertiesVM.ready || listPropertiesVM.deleting}"><td class="table__td table__td--minimalistic">{{ property.propertyDesignation }}</td><td class="table__td table__td--minimalistic"><hy-array-display model="property.owners"></hy-array-display></td><td class="table__td table__td--minimalistic">{{ property.municipality }}</td><td class="table__td table__td--minimalistic">{{ property.district }}</td><td class="table__td table__td--minimalistic">{{ property.numNegotiatedApartments }}</td><td class="table__td text-right"><a href="#" ng-click="listPropertiesVM.delete(property); $event.preventDefault();" class="btn--icon btn--icon-bin btn--icon-bin btn--icon--no-padding" ng-hide="vm.negotiation.isFinished || vm.blocking.isAgreed || (vm.currentUserParty !== vm.blocking.creatorParty) || listPropertiesVM.deletingApartment"><span class="sr-only">Ta bort</span></a></td></tr></table><div class="display-flex space-between spacer-mt-medium" ng-show="listPropertiesVM.propertiesTotalCount > 0"><div>Ladda ner listan <a class="link--turquoise link--underline spacer-ml-xsmall" target="_self" download href="/api/negotiations/{{vm.blocking.negotiationId}}/blockings/{{vm.blocking.id}}/export-property-list">h\xE4r</a></div><div ng-show="listPropertiesVM.propertiesTotalCount > 0 &amp;&amp; listPropertiesVM.propertiesTotalCount <= listPropertiesVM.pageLimit"><strong>Totalt: </strong>{{listPropertiesVM.propertiesTotalCount}}</div><hy-paginator ng-show="listPropertiesVM.propertiesTotalCount > listPropertiesVM.pageLimit" get-list="listPropertiesVM.getPropertiesVirtual" page-offset="listPropertiesVM.pageOffset" page-limit="listPropertiesVM.pageLimit" total-count="listPropertiesVM.propertiesTotalCount"></hy-paginator></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="listPropertiesVM.propertiesTotalCount === 0">Inga fastigheter hittades.</div></div>');
$templateCache.put('resources/user/welcome/negotiation-table/negotiator/list.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.userReady"></hy-progress-circular><div class="container" ng-show="vm.userReady"><div class="display-flex space-between spacer-mt-xxlarge spacer-mb-large"><div class="spacer-mr-small"><h2 class="text-margin-reset">F\xF6rhandlingsbord: {{ vm.user.fullName }} och mig</h2><h3 class="text-margin-top-reset">{{vm.organization.name}}</h3><div>H\xE4r listas f\xF6rhandlingar {{vm.FeatureConfiguration.ENABLE_MEETINGS ? \',fastigheter och m\xF6ten\' : \'och fastigheter\'}} som {{vm.user.fullName}} och jag har gemensamt.</div></div><div ng-if="vm.FeatureConfiguration.ENABLE_MEETINGS"><button class="btn" ng-click="vm.addMeeting()" type="button">Skapa nytt m\xF6te</button></div></div><div class="row"><div class="col col-sm-12 col-lg-8 tabs-container"><hy-tabs tablist="vm.tablist"></hy-tabs></div></div><hr class="hr--full-width spacer-mt-reset"><ng-include src="\'resources/user/welcome/negotiation-table/negotiator/\' + vm.currentSubpage + \'/list.html\'"></ng-include></div>');
$templateCache.put('resources/user/list/external/partials/_table-for-external.html','<table class="table table--margin-b-reset spacer-mt-large"><thead class="table__header"><tr><th class="table__th">F\xF6rnamn</th><th class="table__th">Efternamn</th><th class="table__th">E-post</th><th class="table__th">Telefonnummer</th><th class="table__th">Part</th></tr><tr><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="userExternalVM.inputTextFirstName" hy-enter="userExternalVM.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="userExternalVM.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="userExternalVM.inputTextLastName" hy-enter="userExternalVM.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="userExternalVM.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="userExternalVM.inputTextEmail" hy-enter="userExternalVM.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="userExternalVM.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th></th><th></th></tr></thead><tr class="table__tr" ng-repeat="user in userExternalVM.users" ng-click="userExternalVM.gotoShow(user.id)" ng-class="{\'table__tr--muted\' : !userExternalVM.ready}"><td class="table__td">{{ user.firstname }}</td><td class="table__td">{{ user.lastname }}</td><td class="table__td">{{ user.email }}</td><td class="table__td">{{ user.phone }}</td><td class="table__td">{{ user.party | hyParties }}</td></tr></table>');
$templateCache.put('resources/user/list/external/partials/_table-for-hgf.html','<table class="table table--margin-b-reset spacer-mt-large"><thead class="table__header"><tr><th class="table__th">F\xF6rnamn</th><th class="table__th">Efternamn</th><th class="table__th">E-post</th><th class="table__th">Telefonnummer</th><th class="table__th">Inaktiv profil</th><th class="table__th">EPOST notiser fr\xE5n FP</th></tr><tr><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="userExternalVM.inputTextFirstName" hy-enter="userExternalVM.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="userExternalVM.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="userExternalVM.inputTextLastName" hy-enter="userExternalVM.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="userExternalVM.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="userExternalVM.inputTextEmail" hy-enter="userExternalVM.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="userExternalVM.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th></th><th></th></tr></thead><tr class="table__tr" ng-repeat="user in userExternalVM.users" ng-class="{\'table__tr--muted\' : !userExternalVM.ready}" ng-click="userExternalVM.gotoShow(user.id);"><td class="table__td">{{ user.firstname }}</td><td class="table__td">{{ user.lastname }}</td><td class="table__td">{{ user.email }}</td><td class="table__td">{{ user.phone }}</td><td class="table__td">{{ user.disabled ? \'Ja\' : \'Nej\' }}</td><td class="table__td">{{ user.mailNotificationsEnabled ? \'Ja\' : \'Nej\' }}</td><td class="table__td"><a ng-if="Auth.canDo(\'editUser\')" href="#" ng-click="userExternalVM.editUser(user.id); $event.preventDefault(); $event.stopPropagation()" class="btn--icon btn--icon-pen"></a> <a href="#" class="btn--icon btn--icon-bin" ng-if="Auth.canDo(\'deleteUser\')" ng-click="vm.deleteUser(user.id); $event.preventDefault(); $event.stopPropagation()"></a></td></tr></table>');
$templateCache.put('resources/agreement/show/properties/select/list.html','<div ng-controller="ListAgreementPropertiesCtrl as listPropertiesVM"><hy-progress-circular size="60px" center-on-page ng-hide="listPropertiesVM.ready"></hy-progress-circular><div class="row"><div class="col display-flex space-between"><h3 class="text-margin-reset">Valt fastighetsbest\xE5nd</h3><span ng-hide="vm.editPropertiesMode || vm.agreement.agreedByAssociation || vm.agreement.agreedByOwner || vm.agreement.rentFile.blobId"><button class="btn btn--secondary" ng-click="listPropertiesVM.delete()" ng-disabled="!listPropertiesVM.hasSelectedProperties()">Ta bort</button> <button class="btn" ng-hide="vm.negotiation.realEstateStatus.notAssignedCount === 0" ng-click="vm.editPropertiesMode = true">L\xE4gg till fler</button></span></div></div><hr class="spacer-mt-small"><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="listPropertiesVM.listColumns" order="listPropertiesVM.sortColumn" direction="listPropertiesVM.sortDirection"></hy-sorter></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Alla</th><th class="table__th">Fastigheter</th><th class="table__th">Fastighets\xE4gare</th><th class="table__th">Kommun</th><th class="table__th">LM distrikt</th><th class="table__th">Antal l\xE4genheter</th></tr><tr><th class="table__th"><hy-checkbox name="chkDeleteAll" model="listPropertiesVM.toggledAll" ticked="listPropertiesVM.toggleAllProperties()"></hy-checkbox></th><th class="table__th table__td--width-25"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="listPropertiesVM.inputPropertiesTextProperty" hy-enter="listPropertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listPropertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-30"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="listPropertiesVM.inputPropertiesTextOwner" hy-enter="listPropertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listPropertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-20"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="listPropertiesVM.inputPropertiesTextMunicipality" hy-enter="listPropertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listPropertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-20"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="listPropertiesVM.inputPropertiesTextDistrict" hy-enter="listPropertiesVM.getProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listPropertiesVM.getProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th></th></tr></thead><tr class="table__tr--minimalistic table__tr--disable-click" ng-repeat="property in listPropertiesVM.propertiesVirtual" ng-class="{\'table__tr--muted\' : !listPropertiesVM.ready || listPropertiesVM.deleting}"><td class="table__td"><hy-checkbox name="chkDelete{{property.id}}" model="listPropertiesVM.selectedProperties[property.id]" ticked="listPropertiesVM.toggleProperty()"></hy-checkbox></td><td class="table__td table__td--minimalistic">{{ property.propertyDesignation }}</td><td class="table__td table__td--minimalistic"><hy-array-display model="property.owners"></hy-array-display></td><td class="table__td table__td--minimalistic">{{ property.municipality }}</td><td class="table__td table__td--minimalistic">{{ property.district }}</td><td class="table__td table__td--minimalistic">{{ property.numNegotiatedApartments }}</td><td class="table__td table__td--minimalistic"></td></tr></table><div class="text-right spacer-mt-medium" ng-show="listPropertiesVM.propertiesTotalCount > listPropertiesVM.pageLimit"><hy-paginator get-list="listPropertiesVM.getPropertiesVirtual" page-offset="listPropertiesVM.pageOffset" page-limit="listPropertiesVM.pageLimit" total-count="listPropertiesVM.propertiesTotalCount"></hy-paginator></div><div class="text-right spacer-mt-medium" ng-show="listPropertiesVM.propertiesTotalCount > 0 &amp;&amp; listPropertiesVM.propertiesTotalCount <= listPropertiesVM.pageLimit"><strong>Totalt: </strong>{{listPropertiesVM.propertiesTotalCount}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="listPropertiesVM.propertiesTotalCount === 0">Inga fastigheter hittades.</div></div>');
$templateCache.put('resources/agreement/show/properties/select/select.html','<div class="spacer-mb-xxxlarge" ng-controller="SelectAgreementPropertiesCtrl as selectPropertiesVM"><div class="text-center spacer-mt-large" ng-show="selectPropertiesVM.saving"><hy-progress-circular size="60px"></hy-progress-circular></div><div class="row spacer-mt-negative-xlarge"><div class="col text-right"><button class="btn btn--reset-transition" ng-disabled="selectPropertiesVM.properties.length === 0" ng-click="selectPropertiesVM.save()"><span>Spara</span></button></div></div><div class="alert alert--border-turq alert--squary alert--white-bg spacer-mt-large"><h2 class="text-margin-reset">{{vm.agreement.numProperties > 0 ? \'Ut\xF6ka fastighetsbest\xE5nd\' : \'V\xE4lj fastighetsbest\xE5nd\'}}</h2><div class="row"><div class="col-12 spacer-mb-medium"><hy-tableau id="agreementPropertiesTableau" columns="selectPropertiesVM.tableColumns" f-get-data="selectPropertiesVM.tableFGetData" use-url="false" in-memory="true" in-memory-excessive-limit="selectPropertiesVM.Limits.InMemoryMiddleLimit" checkboxes="true" checkboxes-alternative="false" id-property="id" selected-rows="selectPropertiesVM.properties" limit="selectPropertiesVM.pageLimit"></hy-tableau></div></div></div><div class="row spacer-mt-large align-items-center"><div class="col text-right"><a class="link--turquoise link--no-underline spacer-mr-medium" ng-click="vm.editPropertiesMode = false">Avbryt </a><button class="btn btn--reset-transition" ng-disabled="selectPropertiesVM.properties.length === 0" ng-click="selectPropertiesVM.save()"><span>Spara</span></button></div></div></div>');
$templateCache.put('resources/negotiation/show/modals/import-rentlist/partials/_examine-first-line.html','<div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">2. F\xF6rhandsgranska 1:a raden</h2></div><div class="modal-body modal-body--medium modal-body--medium--padding-tb" id="modal-body"><hr class="text-margin-top-reset"><ng-include src="\'resources/validation/partials/_examine-first-line-body.html\'"></ng-include></div><div class="modal-footer modal-footer--medium"><button class="btn btn--secondary" type="button" ng-click="vm.cancel()">St\xE4ng</button> <button class="btn" ng-click="vm.save()" ng-disabled="vm.saving">Spara</button></div>');
$templateCache.put('resources/negotiation/show/modals/import-rentlist/partials/_file-upload-rent-list.html','<div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">1. Ladda upp och validera</h2></div><div class="modal-body modal-body--medium modal-body--medium--padding-tb" id="modal-body"><div class="spacer-mb-medium">Om filen du laddar upp inneh\xE5ller m\xE5nga rader, kan det h\xE4r steget ta s\xE5 l\xE5ng tid som upp till 10 minuter. Var beredd p\xE5 att v\xE4nta.</div><div hy-file-upload-2 ng-if="vm.negotiation.id" blob-id="vm.negotiation.rentListBlobId" valid-file-types="xlsx" max-file-size="1073741824" upload-url="/api/negotiations/{{vm.negotiation.id}}/rent-list" metadata="vm.metadata" uploader="vm.uploader" auto-upload disable-flash error-invalid-file="vm.errorInvalidFile" validate-upload validation-url="/api/rent-files" f-on-pending="vm.onPending" validation-response="vm.validationResponse" class="alert alert--border-turq alert--white-bg alert--squary alert--reset-margin"><form name="vm.fileUploadRentListForm" ng-submit="vm.fileUploadRentListForm.$valid" novalidate><input name="inputRentListBlobId" id="inputRentListBlobId" type="hidden" ng-model="vm.negotiation.rentListBlobId" required><div class="position-relative row"><div class="file-upload__progress-bar"><uib-progressbar type="turquoise" value="vm.uploader.progress" ng-show="vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating" ng-class="\'progress--square\'"></uib-progressbar></div></div><div class="row"><div class="col-12 col-lg-7"><div class="display-flex align-items-center"><div class="file-upload__button"><label ng-if="vm.uploader &amp;&amp; !vm.negotiation.rentListBlobId &amp;&amp; !vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating" class="btn btn--reset-transition btn--upload"><input type="file" nv-file-select="" uploader="vm.uploader"></label> <button class="btn btn--reset-transition btn--cancel btn-spinning-flack" ng-show="vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating" ng-click="vm.uploader.cancelUpload()"></button><hy-progress-circular size="90px" ng-show="vm.uploader.isValidating"></hy-progress-circular><div class="btn btn--reset-transition btn--done" ng-show="vm.negotiation.rentListBlobId &amp;&amp; !vm.uploader.isValidating"></div></div><div class="file-upload__status"><h4 class="text-margin-reset"><span ng-show="vm.uploader &amp;&amp; !vm.negotiation.rentListBlobId &amp;&amp; !vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating">Ladda upp hyreslista</span> <span ng-show="vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating">Laddar upp...</span> <span ng-show="vm.uploader.isValidating">Analyserar din fil...</span> <span ng-show="vm.negotiation.rentListBlobId &amp;&amp; !vm.uploader.isValidating">Uppladdat!</span></h4><p ng-show="vm.uploader.currentFileName">{{vm.uploader.currentFileName}}</p><button class="btn btn--secondary btn--reset-transition" ng-show="vm.negotiation.rentListBlobId || vm.uploader.isUploading || vm.uploader.isValidating" ng-click="vm.uploader.cancelUpload()">{{vm.negotiation.rentListBlobId ? \'Ta bort\' : \'Avbryt\'}}</button> <button class="btn btn--reset-transition" ng-show="vm.negotiation.rentListBlobId || vm.uploader.isValidating" ng-click="vm.toggleAdditionalInfoPane()">{{ vm.showAdditionalInfoPane ? \'D\xF6lj\' : \'Visa\' }} info</button></div></div><div ng-show="vm.showAdditionalInfoPane"><ng-include src="\'resources/validation/partials/_additional-info.html\'"></ng-include></div></div><div class="col-12 col-lg-5"><div ng-hide="vm.uploader.isValidating"><h4 class="text-margin-top-reset">Kom ih\xE5g!</h4><span>Filen m\xE5ste vara i Excel-format (XLSX).</span><br><span>Ladda ner v\xE5r <a href="/docs/hyreslista-mall.xlsx" target="_self" download class="link--turquoise">exempelmall</a> f\xF6r att se vilket format som kr\xE4vs f\xF6r att kunna importera.</span></div></div></div></form></div></div><div class="modal-footer modal-footer--medium"><button class="btn btn--secondary" type="button" ng-click="vm.cancel()">St\xE4ng</button> <button class="btn btn--arrow-right" ng-if="vm.inputRentListBlobId" ng-click="vm.goToNextPage(\'Success\')">N\xE4sta</button></div>');
$templateCache.put('resources/agreement/show/properties/apartment/newedit/edit.html','<div class="spacer-mb-xxxlarge" ng-controller="EditAgreementApartmentCtrl as editApartmentsVM"><hy-wizard wz-submit="editApartmentsVM.save()" goto-next="editApartmentsVM.gotoNext" is-current-step-valid="editApartmentsVM.isCurrentStepValid" welcome-message="V\xE4lj fastighetsbest\xE5nd" submit-text="{{vm.agreement.numApartments > 0 ? \'L\xE4gg till\' : \'Spara\'}}" submit-disabled="editApartmentsVM.saving" alternative-wizard="true"><div class="text-center spacer-mt-large" ng-show="editApartmentsVM.saving"><hy-progress-circular size="60px"></hy-progress-circular></div><div class="alert alert--border-turq alert--squary alert--white-bg spacer-mt-large"><hy-wizard-step heading="{{vm.agreement.numApartments > 0 ? \'Ut\xF6ka fastighetsbest\xE5nd\' : \'V\xE4lj fastighetsbest\xE5nd\'}}" heading-short="Fastigheter" heading-step-number="1" name="properties" is-valid="editApartmentsVM.selectedProperties.length > 0" alternative-wizard="true"><ng-include src="\'resources/agreement/show/properties/apartment/newedit/partials/_properties.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="{{vm.agreement.numApartments > 0 ? \'Ut\xF6ka fastighetsbest\xE5nd\' : \'V\xE4lj fastighetsbest\xE5nd\'}}" heading-short="L\xE4genheter" heading-step-number="2" name="apartments" is-valid="editApartmentsVM.selectedApartments.length > 0" alternative-wizard="true"><ng-include src="\'resources/agreement/show/properties/apartment/newedit/partials/_apartments.html\'"></ng-include></hy-wizard-step></div></hy-wizard></div>');
$templateCache.put('resources/agreement/show/properties/apartment/list/list.html','<div ng-controller="ListAgreementApartmentsCtrl as listApartmentsVM"><hy-progress-circular size="60px" center-on-page ng-hide="listApartmentsVM.readyApartments"></hy-progress-circular><div class="row"><div class="col display-flex space-between"><h3 class="text-margin-reset">Valt fastighetsbest\xE5nd</h3><span style="text-align: right;" ng-hide="listApartmentsVM.isSigningStarted || vm.editApartmentMode || vm.agreement.agreedByAssociation || vm.agreement.agreedByOwner"><div ng-if="vm.agreement.rentFile.blobId" style="width: 500px;"><span class="red-text">Varning!</span> Hyror \xE4r inte valda fr\xE5n databasen utan uppladdade via excelfil. N\xE4r hyror l\xE4ses in via fil ska inte knappen \u201C\xC4ndra hyror & status\u201D anv\xE4ndas.</div><button class="btn btn--secondary" ng-click="listApartmentsVM.delete()" ng-disabled="!listApartmentsVM.hasSelectedApartments()">Ta bort</button> <button class="btn" ng-hide="vm.negotiation.realEstateStatus.notAssignedCount === 0" ng-click="vm.editApartmentMode = true">L\xE4gg till fler</button></span></div></div><hr class="spacer-mt-small"><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="listApartmentsVM.listColumns" order="listApartmentsVM.sortColumn" direction="listApartmentsVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Alla<hy-checkbox name="chkDeleteAll" model="listApartmentsVM.toggledAll" ticked="listApartmentsVM.toggleAllApartments()"></hy-checkbox></th><th class="table__th table__td--width-10">Nationellt<br>l\xE4genhetsnr<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextNationalApartmentNo" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">L\xE4genhetsnr<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextApartmentNo" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Gatuadress<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextStreetAddress" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Fastighet<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextPropertyDesignation" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Kommun<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="listApartmentsVM.inputTextMunicipality" hy-enter="listApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="listApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Ing\xE5ende hyra (kr/m\xE5nad)</th><th class="table__th table__td--width-15">\xD6verenskommen ny hyra (kr/m\xE5nad)</th><th class="table__th table__td--width-15">F\xF6r\xE4ndring/m\xE5nad</th><!--\r\n        <th class="table__th">\r\n          <hy-progress-circular size="20px" ng-show="listApartmentsVM.deletingApartment"></hy-progress-circular>\r\n        </th>\r\n        --></tr></thead><tr class="table__tr--minimalistic table__tr--disable-click" ng-repeat="apartment in listApartmentsVM.apartmentsVirtual" ng-class="{\'table__tr--muted\' : !listApartmentsVM.readyApartments || listApartmentsVM.deletingApartment}"><td class="table__td"><hy-checkbox name="chkDelete{{apartment.id}}" model="listApartmentsVM.selectedApartments[apartment.id]" ticked="listApartmentsVM.toggleApartment()"></hy-checkbox></td><td class="table__td table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{ apartment.nationalNo | nationalNoControl }}</td><td class="table__td table__td--nowrap">{{ apartment.ownerNo }}</td><td class="table__td">{{ apartment.streetAddress }}</td><td class="table__td">{{ apartment.property.propertyDesignation }}</td><td class="table__td">{{ apartment.municipality }}</td><td class="table__td">{{ listApartmentsVM.getMonthlyRent(apartment) | number : 2 }}</td><td class="table__td">{{ listApartmentsVM.getNewMonthlyRent(apartment) | number : 2 }}</td><td class="table__td">{{ listApartmentsVM.getChangeDescription(apartment) }}</td><!--\r\n      <td class="table__td text-right table__td--nowrap">\r\n        <a href="#"\r\n            ng-click="listApartmentsVM.delete(apartment); $event.preventDefault();"\r\n            class="btn--icon btn--icon-bin btn--icon-bin btn--icon--no-padding"\r\n            ng-hide="vm.agreement.agreedByAssociation || vm.agreement.agreedByOwner || vm.agreement.rentFile.blobId || listApartmentsVM.deletingApartment">\r\n          <span class="sr-only">Ta bort</span>\r\n        </a>\r\n      </td>\r\n    --></tr></table><div class="display-flex space-between spacer-mt-medium" ng-show="listApartmentsVM.apartmentsTotalCount > 0"><div>Ladda ner listan <a class="link--turquoise link--underline spacer-ml-xsmall" target="_self" download href="/api/negotiations/{{vm.agreement.negotiationId}}/agreements/{{vm.agreement.id}}/export-rent-list-bestand">h\xE4r</a></div><div ng-show="listApartmentsVM.apartmentsTotalCount > 0 &amp;&amp; listApartmentsVM.apartmentsTotalCount <= listApartmentsVM.apartmentsPageLimit"><strong>Totalt: </strong>{{listApartmentsVM.apartmentsTotalCount}}</div><hy-paginator ng-show="listApartmentsVM.apartmentsTotalCount > listApartmentsVM.apartmentsPageLimit" get-list="listApartmentsVM.getApartmentsVirtual" page-offset="listApartmentsVM.apartmentsPageOffset" page-limit="listApartmentsVM.apartmentsPageLimit" total-count="listApartmentsVM.apartmentsTotalCount"></hy-paginator></div></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="listApartmentsVM.apartmentsTotalCount === 0">Inga l\xE4genheter hittades.</div>');
$templateCache.put('resources/blocking/show/apartment/edit/partials/_apartments.html','<div class="row"><div class="col-12 spacer-mb-medium"><form name="editApartmentsVM.apartmentsForm" ng-submit="editApartmentsVM.apartmentsForm.$valid &amp;&amp; editApartmentsVM.gotoNextStep()" novalidate><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="editApartmentsVM.listApartmentsColumns" order="editApartmentsVM.sortApartmentsColumn" direction="editApartmentsVM.sortApartmentsDirection" show-label="true" label-position="side"></hy-sorter></div><hy-progress-circular size="60px" center-on-page ng-hide="editApartmentsVM.ready"></hy-progress-circular><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Alla</th><th class="table__th">Nationellt l\xE4genhetsnr</th><th class="table__th">L\xE4genhetsnr</th><th class="table__th">Gatuadress</th><th class="table__th">Fastighet</th><th class="table__th">Kommun</th><th class="table__th">LM distrikt</th></tr><tr><th class="table__th table__td--width-6"><div class="custom-control custom-control--checkbox"><hy-select-all-checkbox checkboxes="editApartmentsVM.apartments" name="apartments" selected-ids="editApartmentsVM.blocking.selectedApartmentIds" is-master-selected="editApartmentsVM.blocking.isAllApartmentsSelected" is-at-least-one-selected="editApartmentsVM.atleastOneApartmentSelected" total-count="editApartmentsVM.apartmentTotalCount" is-disabled="editApartmentsVM.inputApartmentTextNationalApartmentNo || editApartmentsVM.inputApartmentTextApartmentNo || editApartmentsVM.inputApartmentTextStreetAddress ||\r\n                    editApartmentsVM.inputApartmentTextPropertyDesignation || editApartmentsVM.inputApartmentTextMunicipality || editApartmentsVM.inputApartmentTextDistrict"></hy-select-all-checkbox></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputApartmentTextNationalApartmentNo" hy-enter="editApartmentsVM.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputApartmentTextApartmentNo" hy-enter="editApartmentsVM.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputApartmentTextStreetAddress" hy-enter="editApartmentsVM.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputApartmentTextPropertyDesignation" hy-enter="editApartmentsVM.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputApartmentTextMunicipality" hy-enter="editApartmentsVM.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputApartmentTextDistrict" hy-enter="editApartmentsVM.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="apartment in editApartmentsVM.apartments" ng-class="{\'table__tr--muted\' : !editApartmentsVM.ready,\r\n              \'text-black\': editApartmentsVM.blocking.selectedApartmentIds[apartment.id],\r\n              \'text-gray-dark\': !editApartmentsVM.blocking.selectedApartmentIds[apartment.id]}"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{apartment.id}}" ng-model="editApartmentsVM.blocking.selectedApartmentIds[apartment.id]"> <label class="custom-control__label" for="{{apartment.id}}"></label></div></td><td class="table__td table__td--minimalistic table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{apartment.nationalNo | nationalNoControl}}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{apartment.ownerNo}}</td><td class="table__td table__td--minimalistic">{{apartment.streetAddress}}</td><td class="table__td table__td--minimalistic">{{apartment.property.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{apartment.municipality}}</td><td class="table__td table__td--minimalistic">{{apartment.district}}</td></tr></table><div class="text-right spacer-mt-medium" ng-show="editApartmentsVM.apartmentTotalCount > editApartmentsVM.apartmentsPageLimit"><hy-paginator get-list="editApartmentsVM.queryApartments" page-offset="editApartmentsVM.apartmentsPageOffset" page-limit="editApartmentsVM.apartmentsPageLimit" total-count="editApartmentsVM.apartmentTotalCount"></hy-paginator></div><div class="text-right spacer-mt-medium" ng-show="editApartmentsVM.apartmentTotalCount > 0 &amp;&amp; editApartmentsVM.apartmentTotalCount <= editApartmentsVM.apartmentsPageLimit"><strong>Totalt: </strong>{{editApartmentsVM.apartmentTotalCount}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="editApartmentsVM.apartmentTotalCount === 0">Inga l\xE4genheter hittades.</div></form></div></div><div class="row spacer-mt-large spacer-mb-negative-xxxlarge justify-content-end align-items-center"><a class="link--turquoise link--no-underline spacer-mr-medium" ng-click="vm.editApartmentMode = false">Avbryt </a><button class="btn" ng-click="editApartmentsVM.save()" ng-disabled="!editApartmentsVM.isCurrentStepValid() || editApartmentsVM.saving">{{vm.blocking.numApartments > 0 ? \'L\xE4gg till\' : \'Spara\'}}</button></div>');
$templateCache.put('resources/blocking/show/apartment/edit/partials/_district.html','<div class="row"><div class="col-12 spacer-mb-medium"><form name="editApartmentsVM.districtForm" ng-submit="editApartmentsVM.districtForm.$valid &amp;&amp; editApartmentsVM.gotoNextStep()" novalidate><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="editApartmentsVM.listDistrictColumns" order="editApartmentsVM.sortDistrictColumn" direction="editApartmentsVM.sortDistrictDirection" show-label="true" label-position="side"></hy-sorter></div><hy-progress-circular size="60px" center-on-page ng-hide="editApartmentsVM.ready"></hy-progress-circular><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Alla</th><th class="table__th">LM distrikt</th><th class="table__th">Kommun</th><th class="table__th">Antal fastigheter</th></tr><tr><th class="table__th table__td--width-6"><div class="custom-control custom-control--checkbox"><hy-select-all-checkbox checkboxes="editApartmentsVM.districts" name="district" selected-ids="editApartmentsVM.blocking.selectedDistrictIds" is-master-selected="editApartmentsVM.blocking.isAllDistrictsSelected" is-at-least-one-selected="editApartmentsVM.atleastOneDistrictSelected" total-count="editApartmentsVM.districtTotalCount"></hy-select-all-checkbox></div></th><th class="table__th table__td--width-40"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputDistrictTextDistrict" hy-enter="editApartmentsVM.queryDistrict(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryDistrict(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-40"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputDistrictTextMunicipality" hy-enter="editApartmentsVM.queryDistrict(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryDistrict(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="district in editApartmentsVM.districts" ng-class="{\'table__tr--muted\' : !editApartmentsVM.ready,\r\n              \'text-black\': editApartmentsVM.blocking.selectedDistrictIds[district.id],\r\n              \'text-gray-dark\': !editApartmentsVM.blocking.selectedDistrictIds[district.id]}"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{district.id}}" ng-model="editApartmentsVM.blocking.selectedDistrictIds[district.id]"> <label class="custom-control__label" for="{{district.id}}"></label></div></td><td class="table__td table__td--minimalistic">{{ district.district }}</td><td class="table__td table__td--minimalistic">{{ district.municipality }}</td><td class="table__td table__td--minimalistic">{{ district.noProperties }}</td><td class="table__td table__td--minimalistic"></td></tr></table><div class="text-right spacer-mt-medium" ng-show="editApartmentsVM.districtTotalCount > editApartmentsVM.districtPageLimit"><hy-paginator get-list="editApartmentsVM.queryDistrict" page-offset="editApartmentsVM.districtPageOffset" page-limit="editApartmentsVM.districtPageLimit" total-count="editApartmentsVM.districtTotalCount"></hy-paginator></div><div class="text-right spacer-mt-medium" ng-show="editApartmentsVM.districtTotalCount > 0 &amp;&amp; editApartmentsVM.districtTotalCount <= editApartmentsVM.districtPageLimit"><strong>Totalt: </strong>{{editApartmentsVM.districtTotalCount}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="editApartmentsVM.districtTotalCount === 0">Inget omr\xE5de hittades.</div></form></div></div><div class="row spacer-mt-large spacer-mb-negative-xxxlarge justify-content-end align-items-center"><a class="link--turquoise link--no-underline spacer-mr-medium" ng-click="vm.editApartmentMode = false">Avbryt </a><button class="btn btn--reset-transition btn--arrow-right" ng-click="editApartmentsVM.gotoNext = true" ng-disabled="!editApartmentsVM.isCurrentStepValid()">N\xE4sta</button></div>');
$templateCache.put('resources/blocking/show/apartment/edit/partials/_properties.html','<div class="row"><div class="col-12 spacer-mb-medium"><form name="editApartmentsVM.propertiesForm" ng-submit="editApartmentsVM.propertiesForm.$valid &amp;&amp; editApartmentsVM.gotoNextStep()" novalidate><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="editApartmentsVM.listPropertiesColumns" order="editApartmentsVM.sortPropertiesColumn" direction="editApartmentsVM.sortPropertiesDirection" show-label="true" label-position="side"></hy-sorter></div><hy-progress-circular size="60px" center-on-page ng-hide="editApartmentsVM.ready"></hy-progress-circular><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Alla</th><th class="table__th">Fastigheter</th><th class="table__th">Kommun</th><th class="table__th">LM distrikt</th><th class="table__th">Antal l\xE4genheter</th></tr><tr><th class="table__th table__td--width-6"><div class="custom-control custom-control--checkbox"><hy-select-all-checkbox checkboxes="editApartmentsVM.properties" name="properties" selected-ids="editApartmentsVM.blocking.selectedPropertyIds" is-master-selected="editApartmentsVM.blocking.isAllPropertiesSelected" is-at-least-one-selected="editApartmentsVM.atleastOnePropertySelected" total-count="editApartmentsVM.propertyTotalCount" is-disabled="editApartmentsVM.inputPropertiesTextProperty || editApartmentsVM.inputPropertiesTextMunicipality || editApartmentsVM.inputPropertiesTextDistrict"></hy-select-all-checkbox></div></th><th class="table__th table__td--width-30"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputPropertiesTextProperty" hy-enter="editApartmentsVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-25"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputPropertiesTextMunicipality" hy-enter="editApartmentsVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-25"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search-white" placeholder="S\xF6k" ng-model="editApartmentsVM.inputPropertiesTextDistrict" hy-enter="editApartmentsVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="property in editApartmentsVM.properties" ng-class="{\'table__tr--muted\' : !editApartmentsVM.ready,\r\n                \'text-black\': editApartmentsVM.blocking.selectedPropertyIds[property.id],\r\n                \'text-gray-dark\': !editApartmentsVM.blocking.selectedPropertyIds[property.id]}"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{property.id}}" ng-model="editApartmentsVM.blocking.selectedPropertyIds[property.id]"> <label class="custom-control__label" for="{{property.id}}"></label></div></td><td class="table__td table__td--minimalistic">{{ property.propertyDesignation }}</td><td class="table__td table__td--minimalistic">{{ property.municipality }}</td><td class="table__td table__td--minimalistic">{{ property.district }}</td><td class="table__td table__td--minimalistic">{{ property.numNegotiatedApartments }}</td><td class="table__td table__td--minimalistic"></td></tr></table><div class="text-right spacer-mt-medium" ng-show="editApartmentsVM.propertyTotalCount > editApartmentsVM.propertiesPageLimit"><hy-paginator get-list="editApartmentsVM.queryProperties" page-offset="editApartmentsVM.propertiesPageOffset" page-limit="editApartmentsVM.propertiesPageLimit" total-count="editApartmentsVM.propertyTotalCount"></hy-paginator></div><div class="text-right spacer-mt-medium" ng-show="editApartmentsVM.propertyTotalCount > 0 &amp;&amp; editApartmentsVM.propertyTotalCount <= editApartmentsVM.propertiesPageLimit"><strong>Totalt: </strong>{{editApartmentsVM.propertyTotalCount}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="editApartmentsVM.propertyTotalCount === 0">Inga fastigheter hittades.</div></form></div></div><div class="row spacer-mt-large spacer-mb-negative-xxxlarge justify-content-end align-items-center"><a class="link--turquoise link--no-underline spacer-mr-medium" ng-click="vm.editApartmentMode = false">Avbryt </a><button class="btn btn--reset-transition btn--arrow-right" ng-click="editApartmentsVM.gotoNext = true" ng-disabled="!editApartmentsVM.isCurrentStepValid()">N\xE4sta</button></div>');
$templateCache.put('resources/user/welcome/negotiation-table/negotiator/finished/list.html','<div ng-controller="NegotiationTableNegotiatorFinishedListCtrl as finishedListVM"><hy-tableau id="finishedNegotiationsTableau" columns="finishedListVM.columns" f-get-data="finishedListVM.getData" f-row-click="finishedListVM.gotoNegotiation" use-url="true" limit="30" f-get-context-info="finishedListVM.getContextInfo"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><status>{{$parent.item.combinedState | hyCombinedNegotiationStates}}</status><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && finishedListVM.editComment(item)"></comment></hy-tableau></div>');
$templateCache.put('resources/user/welcome/negotiation-table/negotiator/list/list.html','<div ng-controller="NegotiationTableNegotiatorCurrentListCtrl as currentlistVM"><hy-tableau id="negotiationsTableau" columns="currentlistVM.columns" f-get-data="currentlistVM.getData" f-row-click="currentlistVM.gotoNegotiation" use-url="true" limit="30" f-get-context-info="currentlistVM.getContextInfo"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><status>{{$parent.item.combinedState | hyCombinedNegotiationStates}}</status><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && currentlistVM.editComment(item)"></comment></hy-tableau></div>');
$templateCache.put('resources/user/welcome/negotiation-table/negotiator/meetings/list.html','<div ng-controller="NegotiationTableNegotiatorMeetingListCtrl as meetinglistVM"><hy-progress-circular ng-hide="meetinglistVM.readyMeetings" center-on-page size="60px"></hy-progress-circular><table class="table table--margin-b-reset spacer-mt-xlarge"><tr class="table__header"><th class="table__th table__td--width-20">Titel p\xE5 m\xF6tet</th><th class="table__th">Datum</th><th class="table__th">Tid</th><th class="table__th">Plats</th><th class="table__th">Antal deltagare</th><th class="table__th">F\xF6rhandlingar</th><th class="table__th">Fritext</th></tr><tr class="table__tr" ng-repeat="meeting in meetinglistVM.myMeetings" ng-click="meetinglistVM.gotoShow(meeting.negotiations[0].id)" ng-class="{\'table__tr--muted\' : !meetinglistVM.readyMeetings}"><td class="table__td table__td--align-top">{{ meeting.title }}</td><td class="table__td table__td--align-top">{{ meeting.startAt | date:\'yyyy-MM-dd\' }}</td><td class="table__td table__td--align-top">{{ meeting.startAt | date:\'shortTime\' }} - {{ meeting.endAt | date:\'shortTime\' }}</td><td class="table__td table__td--align-top">{{ meeting.location }}</td><td class="table__td table__td--align-top"><div ng-repeat="participant in meeting.participants">{{ participant.fullName}} ({{ participant.party | hyParties}})</div></td><td class="table__td table__td--align-top"><div ng-repeat="negotiation in meeting.negotiations">{{ negotiation.caseNumber }}</div></td><td class="table__td table__td--align-top">{{ meeting.text }}</td></tr></table><div class="alert alert--hide-border alert--slim alert--squary" ng-show="meetinglistVM.totalCount === 0">Inga m\xF6ten hittades.</div></div>');
$templateCache.put('resources/user/welcome/negotiation-table/negotiator/properties/list.html','<div ng-controller="NegotiationTableNegotiatorPropertiesListCtrl as propertyListVM"><hy-tableau id="propertiesTableau" columns="propertyListVM.columns" f-get-data="propertyListVM.getData" f-row-click="propertyListVM.gotoNegotiation" use-url="true" limit="30" f-get-context-info="propertyListVM.getContextInfo"><slot1>{{ $parent.item.numNegotiatedApartments || \'-\' }}</slot1></hy-tableau></div>');
$templateCache.put('resources/agreement/show/properties/apartment/newedit/partials/_apartments.html','<div class="row"><div class="col-12 spacer-mb-medium"><hy-tableau id="tableauApartments" name="tableauApartments" columns="editApartmentsVM.listApartmentsColumns" f-get-data="editApartmentsVM.fGetApartments" use-url="false" in-memory="true" in-memory-excessive-limit="editApartmentsVM.Limits.InMemoryExcessiveLimit" checkboxes="true" id-property="id" selected-rows="editApartmentsVM.selectedApartments" limit="30"><status><span ng-bind-html="$parent.item.status | hyStatusString : 1"></span></status><hyra>{{$parent.item.currentMonthlyRent | number: 0}}<xsmall>{{ $parent.item.currentEffectiveFrom | date: \'yyyy-MM-dd\'}}</xsmall><hy-std-display model="{{$parent.item.currentObjectsExtra}}"></hy-std-display></hyra><comment> <img src="/img/comment{{ $parent.item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="$parent.item.comment" tooltip-placement="bottom"></comment></hy-tableau></div></div><div class="row spacer-mt-large spacer-mb-negative-xxxlarge space-between align-items-center"><a class="btn btn--reset-transition btn--arrow-left" href="/negotiations/{{vm.negotiation.id}}/agreements/{{vm.agreement.id}}/rentlist?step=properties">Tillbaka</a><div><a class="link--turquoise link--no-underline spacer-mr-medium" ng-click="vm.editApartmentMode = false">Avbryt </a><button class="btn" ng-click="editApartmentsVM.save()" ng-disabled="!editApartmentsVM.isCurrentStepValid() || editApartmentsVM.saving">{{vm.agreement.numApartments > 0 ? \'L\xE4gg till\' : \'Spara\'}}</button></div></div>');
$templateCache.put('resources/agreement/show/properties/apartment/newedit/partials/_properties.html','<div class="row"><div class="col-12 spacer-mb-medium"><hy-tableau id="tableauProperties" name="tableauProperties" columns="editApartmentsVM.listPropertiesColumns" f-get-data="editApartmentsVM.fGetProperties" use-url="false" in-memory="true" in-memory-excessive-limit="editApartmentsVM.Limits.InMemoryMiddleLimit" checkboxes="true" id-property="id" selected-rows="editApartmentsVM.selectedProperties" limit="30"></hy-tableau></div></div><div class="row spacer-mt-large spacer-mb-negative-xxxlarge justify-content-end align-items-center"><a class="link--turquoise link--no-underline spacer-mr-medium" ng-click="vm.editApartmentMode = false">Avbryt </a><button class="btn btn--reset-transition btn--arrow-right" ng-click="editApartmentsVM.gotoNext = true" ng-disabled="!editApartmentsVM.isCurrentStepValid()">N\xE4sta</button></div>');
$templateCache.put('index.html','<!DOCTYPE html><html lang="sv" ng-app="hyresgastApp"><head><base href="/"><meta charset="utf-8"><meta http-equiv="X-UA-Compatible" content="IE=edge"><meta name="viewport" content="width=device-width,initial-scale=1,user-scalable=yes"><meta property="og:image" content="/img/hgf_open_graph.png"><link rel="apple-touch-icon" sizes="180x180" href="/img/favicon/apple-touch-icon.png"><link rel="icon" type="image/png" sizes="32x32" href="/img/favicon/favicon-32x32.png"><link rel="icon" type="image/png" sizes="16x16" href="/img/favicon/favicon-16x16.png"><link rel="shortcut icon" href="/img/favicon/favicon.ico"><link rel="manifest" href="/manifests/manifest.json"><script src="http://localhost:3200/js/md.min.js"></script><script src="http://localhost:3200/js/xlsx.min.js"></script><meta http-equiv="Cache-control" content="no-cache, no-store, must-revalidate"><meta http-equiv="Pragma" content="no-cache"><meta http-equiv="expires" content="0"><!-- inject:css --><link rel="stylesheet" href="/css/application.css"><!-- endinject --><title>F\xF6rhandlarportalen</title></head><body ng-cloak hy-auth-heartbeat><hy-flash class="flash"></hy-flash><hy-navigation></hy-navigation><main><hy-login class="login"></hy-login><div class="main-content"><ng-view></ng-view></div></main><hy-footer></hy-footer><!-- inject:js --><!-- endinject --></body></html>');
$templateCache.put('components/apartment-selector-2/apartment-selector-2.html','<style>.paneFilters {\r\n        user-select: none;\r\n        width: 270px;\r\n        display: inline-block;\r\n        vertical-align: top;\r\n    }\r\n    .paneResults {\r\n        user-select: none;\r\n        display: inline-block;\r\n        vertical-align: top;\r\n        /*width: 850px;*/\r\n        width: calc(100% - 295px);\r\n        padding-top: 18px;\r\n        margin-left: 20px;\r\n    }\r\n    .floatingFilters {\r\n        background-color: lightseagreen;\r\n        color: white;\r\n        border: darkcyan 2px solid;\r\n        border-radius: 20px;\r\n        padding: 15px;\r\n        width: 100%;\r\n        font-weight: 900;\r\n    }\r\n    .topFilterItem {\r\n        font-weight: 400;\r\n    }\r\n    .floatingReally {\r\n        position: fixed;\r\n        top: 5px;\r\n        z-index: 99999999;\r\n\r\n        width: 840px;\r\n        left: calc(50% - 420px + 142px);\r\n    }\r\n    .boxProperties {\r\n        overflow-y: auto;\r\n        margin-top: 20px;\r\n    }\r\n    .propertyCard {\r\n        transition: all 0.2s ease-in;\r\n        border-radius: 18px;\r\n        padding: 10px;\r\n        background-color: #fcfcfc;\r\n        border: 1px lightgray solid;\r\n        margin-right: 9px;\r\n        margin-bottom: 9px;\r\n        width: 295px;\r\n        height: 166px;\r\n        vertical-align: top;\r\n        cursor: pointer;\r\n\r\n        display: inline-flex;\r\n        flex-direction: column;\r\n    }\r\n    .propertyCardDown {\r\n        flex: 1;\r\n    }\r\n    .propertyCard:hover {\r\n        border: 1px darkcyan solid;\r\n    }\r\n    .propertyCardSelected {\r\n        border: 2px darkcyan solid;\r\n        background: white;\r\n    }\r\n    .propertyCardSelected:hover {\r\n        border: 2px darkcyan solid;\r\n        background: white;\r\n    }\r\n    .propertyCardWithoutApartments {\r\n        width: calc(295px * 2 + 10px);\r\n        height: 166px;\r\n    }\r\n    .propertyCardWithApartments {\r\n        width: calc(295px * 2 + 10px);\r\n        min-height: 166px;\r\n        max-height: auto;\r\n        height: calc(166px * 3 + 18px);\r\n        float: left;\r\n    }\r\n    .minicell {\r\n        display: inline-block;\r\n        width: 60px;\r\n        color: gray;\r\n    }\r\n\r\n    .filterName {\r\n        font-weight: 900;\r\n        margin-top: 20px;\r\n    }\r\n    .filterList {\r\n        height: 200px;\r\n    }\r\n    .filterList-extra-height {\r\n        height: max-content;\r\n        min-height: 200px;\r\n        max-height: 300px;\r\n    }\r\n    .scroller-apartments {\r\n        height: 350px;\r\n    }\r\n\r\n\r\n    .btn--tiny {\r\n        width: 12px;\r\n        height: 12px;\r\n        padding: 0 !important;\r\n        line-height: 12px;\r\n        margin-right: 18px;\r\n        margin-left: 18px;\r\n    }\r\n    .floatingFilters .btn--tiny {\r\n        margin-right: 4px;\r\n        margin-left: 10px;\r\n    }\r\n\r\n    .paneBasket {\r\n        background: linear-gradient(to top, rgb(250, 250, 250), rgb(230, 230, 230));\r\n        border: 2px solid darkcyan;\r\n\r\n        transition: bottom 0.5s;\r\n\r\n        position: fixed;\r\n\r\n        width: 840px;\r\n\r\n        left: calc(50% - 420px + 142px);\r\n\r\n        bottom: calc(-30vh - 45px);\r\n\r\n        max-height: calc(30vh + 95px);\r\n\r\n        z-index: 99999999;\r\n\r\n        padding: 10px;\r\n        border-radius: 18px;\r\n    }\r\n    .paneBasketOpened {\r\n        bottom: 0;\r\n    }\r\n    .basketHeader {\r\n        height: 40px;\r\n    }\r\n    .basketScroller {\r\n        background: white;\r\n    }\r\n    .basketHeader > * {\r\n        vertical-align: middle;\r\n    }</style><div><div class="paneFilters"><hy-checkbox ng-if="userId" name="onlyMyProperties" title="Bara mina fastigheter" model="onlyMyProperties" ticked="changePropertiesMethod(onlyMyProperties)"></hy-checkbox><button ng-if="filters.present" class="btn btn--secondary spacer-mt-small" ng-click="removeAllFilters()">Rensa</button><div class="filterName" ng-click="expandFilter(\'region\')">Region <span ng-if="filtersExpanded[\'region\']">-</span> <span ng-if="!filtersExpanded[\'region\']">+</span></div><hy-progress-circular size="20px" ng-if="filtersLoading"></hy-progress-circular><div ng-if="filtersExpanded[\'region\']" class="scroller-selector dropdown-menu--filter filterList-extra-height"><div ng-repeat="item in filterDictionaries.regionIds"><button class="btn--icon-plus" ng-show="!propertiesLoading" ng-click="addRegionFilter(item)"></button> {{item | regionTypeToDescription}}</div></div><div class="spacer-ml-xsmall" ng-repeat="item in filters.regionIds"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removeRegionFilter(item)"></button> {{item | regionTypeToDescription}}</div><div class="filterName" ng-click="expandFilter(\'municipality\')">Kommun <span ng-if="filtersExpanded[\'municipality\']">-</span> <span ng-if="!filtersExpanded[\'municipality\']">+</span></div><div ng-if="filtersExpanded[\'municipality\']" class="display-flex spacer-mb-xsmall"><input class="form-control form-control--search form-control--search--large" hy-enter="doSearchMunicipality(filterSearches.municipality)" tabindex="200" autofocus placeholder="S\xF6k" ng-model="filterSearches.municipality"> <a href="#" class="btn--icon btn--icon-search" ng-click="doSearchMunicipality(filterSearches.municipality); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div><hy-progress-circular size="20px" ng-if="filtersLoading"></hy-progress-circular><div ng-if="filtersExpanded[\'municipality\']" class="scroller-selector dropdown-menu--filter filterList"><div ng-repeat="item in filterDictionaries.municipalities"><button class="btn--icon-plus" ng-show="!propertiesLoading" ng-click="addMunicipalityFilter(item)"></button> {{item}}</div></div><div class="spacer-ml-xsmall" ng-repeat="item in filters.municipalities"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removeMunicipalityFilter(item)"></button> {{item}}</div><div class="filterName" ng-click="expandFilter(\'propertyDesignation\')">Namn <span ng-if="filtersExpanded.propertyDesignation">-</span> <span ng-if="!filtersExpanded.propertyDesignation">+</span></div><div ng-if="filtersExpanded.propertyDesignation" class="display-flex spacer-mb-xsmall"><input class="form-control form-control--search form-control--search--large" hy-enter="doSearchPropertyDesignation(filterSearches.propertyDesignation)" tabindex="201" placeholder="S\xF6k" ng-model="filterSearches.propertyDesignation"> <a href="#" class="btn--icon btn--icon-search" ng-click="doSearchPropertyDesignation(filterSearches.propertyDesignation); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div><hy-progress-circular size="20px" ng-if="filtersLoading"></hy-progress-circular><div ng-if="filtersExpanded.propertyDesignation" class="scroller-selector dropdown-menu--filter filterList"><div ng-repeat="item in filterDictionaries.propertyDesignations"><button class="btn--icon-plus" ng-show="!propertiesLoading" ng-click="addPropertyFilter(item)"></button> {{item}}</div></div><div class="spacer-ml-xsmall" ng-repeat="item in filters.propertyDesignations"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removePropertyFilter(item)"></button> {{item}}</div><div class="filterName" ng-click="expandFilter(\'owners\')">\xC4gare <span>{{ filtersExpanded.owners ? \'-\' : \'+\' }}</span></div><div ng-if="filtersExpanded.owners" class="display-flex spacer-mb-xsmall"><input class="form-control form-control--search form-control--search--large" hy-enter="doSearchOwner(filterSearches.owner)" tabindex="202" placeholder="S\xF6k" ng-model="filterSearches.owner"> <a href="#" class="btn--icon btn--icon-search" ng-click="doSearchOwner(filterSearches.owner); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div><hy-progress-circular size="20px" ng-if="filtersLoading"></hy-progress-circular><div ng-if="filtersExpanded.owners" class="scroller-selector dropdown-menu--filter filterList"><div ng-repeat="item in filterDictionaries.owners"><button class="btn--icon-plus" ng-show="!propertiesLoading" ng-click="addOwnersFilter(item)"></button> {{item}}</div></div><div class="spacer-ml-xsmall" ng-repeat="item in filters.owners"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removeOwnersFilter(item)"></button> {{item}}</div><div class="filterName" ng-click="expandFilter(\'counterparts\')">Motpart <span>{{ filtersExpanded.counterparts ? \'-\' : \'+\' }}</span></div><div ng-if="filtersExpanded.counterparts" class="display-flex spacer-mb-xsmall"><input class="form-control form-control--search form-control--search--large" hy-enter="doSearchCounterpart(filterSearches.counterpart)" tabindex="203" placeholder="S\xF6k" ng-model="filterSearches.counterpart"> <a href="#" class="btn--icon btn--icon-search" ng-click="doSearchCounterpart(filterSearches.counterpart); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div><hy-progress-circular size="20px" ng-if="filtersLoading"></hy-progress-circular><div ng-if="filtersExpanded.counterparts" class="scroller-selector dropdown-menu--filter filterList"><div ng-repeat="item in filterDictionaries.counterparts"><button class="btn--icon-plus" ng-show="!propertiesLoading" ng-click="addCounterpartsFilter(item)"></button> {{item}}</div></div><div class="spacer-ml-xsmall" ng-repeat="item in filters.counterparts"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removeCounterpartsFilter(item)"></button> {{item}}</div></div><div class="paneResults"><div id="floatingFilters" class="floatingFilters" ng-show="filters.present">Filtrera p\xE5 <span ng-if="filters.regionIds.length">region: <span ng-repeat="item in filters.regionIds" class="topFilterItem"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removeRegionFilter(item)"></button> {{item | regionTypeToDescription}} </span></span><span ng-if="filters.municipalities.length">kommun: <span ng-repeat="item in filters.municipalities" class="topFilterItem"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removeMunicipalityFilter(item)"></button> {{item}} </span></span><span ng-if="filters.propertyDesignations.length">namn: <span ng-repeat="item in filters.propertyDesignations" class="topFilterItem"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removePropertyFilter(item)"></button> {{item}} </span></span><span ng-if="filters.owners.length">\xE4gare: <span ng-repeat="item in filters.owners" class="topFilterItem"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removeOwnersFilter(item)"></button> {{item}} </span></span><span ng-if="filters.counterparts.length">motpart: <span ng-repeat="item in filters.counterparts" class="topFilterItem"><button class="btn--icon-minus" ng-show="!propertiesLoading" ng-click="removeCounterpartsFilter(item)"></button> {{item}}</span></span></div><div class="alert alert--info alert--hide-border alert--squary spacer-mt-large" ng-show="propertiesTotalCount === 0 && !propertiesLoading">Inga fastigheter hittades.</div><div ng-show="propertiesTotalCount > 0 && !propertiesLoading" class="display-flex space-between spacer-mt-small"><hy-checkbox title="Alla fastigheter" name="allProperties" model="allProperties" ticked="toggleAllProperties()"></hy-checkbox><span><hy-paginator class="di" get-list="getPropertiesVirtual" page-offset="offset" page-limit="limit" total-count="propertiesTotalCountVirtual"></hy-paginator><span class="red-text" ng-if="propertiesTotalCount > propertiesTotalCountVirtual"><b>!{{propertiesTotalCount}}</b></span></span></div><hy-progress-circular size="50px" ng-if="propertiesLoading"></hy-progress-circular><div ng-show="propertiesTotalCount > 0 && !propertiesLoading" class="boxProperties"><div id="property{{property.id}}" class="propertyCard" ng-repeat="property in propertiesVirtual" ng-class="{ \'propertyCardSelected\'          : property.selectionState !== PROPERTY_CARD_STATE.NOT_SELECTED,\r\n                             \'propertyCardWithoutApartments\' : property.selectionState === PROPERTY_CARD_STATE.READY_NO_APARTMENTS,\r\n                             \'propertyCardWithApartments\'    : property.selectionState === PROPERTY_CARD_STATE.READY_APARTMENTS }"><div class="propertyCardUp"><hy-checkbox model="tickedProperties[property.id]" name="property{{property.id}}" ticked="timed(checkBasket)" class="di"></hy-checkbox><span ng-click="selectProperty(property)">{{property.municipality}}, <b>{{property.propertyDesignation}}</b>, {{property.district}}</span></div><div class="propertyCardDown" ng-click="selectProperty(property)"><small class="spacer-mt-xsmall"><div ng-if="property.owners.length"><span class="minicell">\xC4gare:</span><hy-array-display model="property.owners"></hy-array-display></div><div ng-if="property.counterparts.length"><span class="minicell">Motpart:</span><hy-array-display model="property.counterparts"></hy-array-display></div><div ng-if="property.landlords.length"><span class="minicell">Hyresv\xE4rd:</span><hy-array-display model="property.landlords"></hy-array-display></div></small><hy-progress-circular ng-if="property.selectionState === PROPERTY_CARD_STATE.LOADING" size="20px"></hy-progress-circular><div ng-if="property.selectionState === PROPERTY_CARD_STATE.READY_APARTMENTS"><input class="form-control di" placeholder="Filtrera efter gatuadress eller nationellt nummer" name="apartmentFilter" ng-model="vm.apartmentFilterPhrase"><div class="scroller-selector dropdown-menu--filter scroller-apartments spacer-mb-xsmall"><table class="table"><tr class="table__tr" ng-repeat="apartment in apartments | filter:apartmentsFilter"><td class="table__td"><hy-checkbox title="{{ apartment.streetAddress }}, {{ apartment.nationalNo }}, yta: {{ apartment.livingSpace }}, antal rum: {{ apartment.rooms }}" name="a{{apartment.id}}" model="tickedApartments[apartment.id]"></hy-checkbox></td></tr></table></div><div class="spacer-ml-small" ng-if="!emptyOnly"><hy-checkbox class="di" title=" " model="allApartments" ticked="toggleAllApartments()"></hy-checkbox><button class="btn di" ng-click="addSelected()">L\xE4gg till</button></div></div><div ng-if="property.selectionState === PROPERTY_CARD_STATE.READY_NO_APARTMENTS">Ingen l\xE4genheter p\xE5 {{property.municipality}}, <b>{{property.propertyDesignation}}</b> {{property.district}}</div></div></div></div><div ng-show="propertiesTotalCount > 12 && !propertiesLoading" class="display-flex space-between"><hy-checkbox title="Alla fastigheter" name="allProperties" model="allProperties" ticked="toggleAllProperties()"></hy-checkbox><span><hy-paginator class="di" get-list="getPropertiesVirtual" page-offset="offset" page-limit="limit" total-count="propertiesTotalCountVirtual"></hy-paginator><span class="red-text" ng-if="propertiesTotalCount > propertiesTotalCountVirtual"><b>!{{propertiesTotalCount}}</b></span></span></div><div class="paneBasket" ng-class="{ \'paneBasketOpened\' : basketOpened }" ng-if="basketVisible"><div class="basketHeader display-flex space-between"><div><button class="btn spacer-mb-xsmall" ng-if="allowEmptyProperties && hasTickedProperties()" ng-click="$event.preventDefault(); addEmptyProperty()">L\xE4gg till fastigheter</button> <button class="btn spacer-mb-xsmall" ng-if="hasTickedProperties() && !emptyOnly" ng-click="$event.preventDefault(); addEveryApartment()">L\xE4gg till l\xE4genheter</button><hy-progress-circular ng-if="busyAddEvery" size="20px"></hy-progress-circular><span ng-if="model && (model.length > 0)" class="spacer-ml-small">{{model.length}} l\xE4genheter vald</span> <span ng-if="emptyProperties && (emptyProperties.length > 0)" class="spacer-ml-small">{{emptyProperties.length}} fastigheter valda</span></div><div><button ng-show="!basketOpened && hasSelectedProperties" class="btn basketToggler" ng-click="toggleBasketOpened()">\u25B2</button> <button ng-show="basketOpened" class="btn basketToggler" ng-click="toggleBasketOpened()">\u25BC</button></div></div><table class="table spacer-margin-reset spacer-mr-xsmall"><tr class="spacer-margin-reset"><td class="table__th table__td--width-60 spacer-margin-reset" style="padding: 0 !important"><input class="form-control spacer-margin-reset" name="basketFilterPhrase" ng-model="vm.basketFilterPhrase" id="basketFilter"></td><td class="table__th table__td--width-30 spacer-margin-reset" style="padding: 0 !important"><hy-multicombobox items="agreementCodes" current-items="vm.selectedAgreementCodes" button-text="V\xE4lj"></hy-multicombobox></td><td class="table__th table__td--width-10 spacer-margin-reset"><button class="btn btn--icon btn--icon-bin btn-fu spacer-mr-medium" style="float: right" ng-click="$event.preventDefault(); deleteEverything(property.id)"></button></td></tr></table><div class="scroller-selector dropdown-menu--filter spacer-mb-xsmall basketScroller"><div class="btn--full-width" ng-class="{\'scroller-selector scroller-selector--modal\': isModal}" id="selectedApartmentsScroller" ng-if="model.length > 0 || emptyProperties.length > 0"><table class="table spacer-margin-reset spacer-mr-xsmall btn--full-width"><tr ng-repeat="property in emptyProperties | filter:basketFilter"><td class="table__td table__td--width-90 spacer-margin-reset"><b class="spacer-ml-xsmall"><i>{{property.propertyDesignation}}, {{property.municipality}}, {{property.district}}</i></b> <small style="float: right">{{ property.agreementCode }}</small></td><td class="table__td table__td--width-10 spacer-margin-reset"><button class="btn btn--icon btn--icon-bin btn-fu" style="float: right" ng-click="$event.preventDefault(); deleteEmptyProperty(property.id)"></button></td></tr></table><table class="table spacer-margin-reset spacer-mr-xsmall"><tr ng-repeat-start="apartment in model | filter:basketFilter" ng-if="apartment.differentProperty"><td class="table__td table__td--width-90 spacer-margin-reset" colspan="2"><b class="spacer-ml-xsmall"><i>{{apartment.property.propertyDesignation}}, {{apartment.municipality}}, {{apartment.property.district}}</i></b> <small style="float: right">{{ property.agreementCode }}</small></td><td class="table__td table__td--width-10 spacer-margin-reset"><button class="btn btn--icon btn--icon-bin btn-fu" style="float: right" ng-click="$event.preventDefault(); deleteProperty(property.id)"></button></td></tr><tr ng-repeat-end><td class="table__td table__td--width-45 table__td--minimalistic spacer-margin-reset"><span class="spacer-ml-medium">{{apartment.municipality}}, {{apartment.property.district}}, {{apartment.property.propertyDesignation}}</span></td><td class="table__td table__td--width-35 table__td--minimalistic spacer-margin-reset">{{apartment.streetAddress}}, {{apartment.nationalNo}}</td><td class="table__td table__td--width-10 table__td--minimalistic spacer-margin-reset"><button class="btn btn--icon btn--icon-bin btn-fu" ng-click="$event.preventDefault(); deleteSelected(apartment.id)"></button></td></tr></table></div></div></div></div></div>');
$templateCache.put('components/counter/counter.html','<div class="counter" uib-tooltip="{{tooltipText}}" tooltip-placement="bottom" ng-show="Auth.currentUser()" ng-class="{\'counter--pink\' : !Auth.isHgfUser(), \'counter--small\' : countValue > 100}"><span>{{countValue}}</span></div>');
$templateCache.put('components/artifacts/artifacts.html','<div class="row"><div class="col-8"><button class="btn" ng-if="Auth.canDo(\'createArtifacts\')" ng-click="upload()">Ladda upp</button></div><div class="col-4 display-flex justify-content-end sort-container spacer-mb-medium"><hy-sorter list-columns="sortColumns" order="sortColumn" direction="sortDirection" show-label="true" label-position="side"></hy-sorter></div></div><hr ng-if="artifacts.length > 0" class="hr--thin"><table class="table"><tr><th width="20%" align="left"><div class="spacer-mb-small">Datum</div></th><th width="50%" align="left">Filnamn</th><th width="35%" ng-if="!group" align="left">Typ</th><th></th></tr><tr ng-if="!group"><th></th><th><div class="spacer-mr-medium"><input ng-model="filters.filename" class="form-control" placeholder="Filtrera" ng-change="getArtifactsVirtual()"></div></th><th><hy-combobox prompt="V\xE4lj typ" text-property="name" current-item="filters.label" current-item-changed="changeFilterTimed()" items="fileLabelsWithAll"></hy-combobox></th></tr><tr class="table__tr" ng-repeat="item in artifactsVirtual"><td class="table__td table__td--minimalistic"><div ng-if="group && item.groupBreak"><small><b><i>{{ item.label }}</i></b></small></div>{{ item.date | date:\'yyyy-MM-dd HH:mm\' }}</td><td class="table__td table__td--minimalistic"><a href="#" ng-click="download(item)">{{ item.filename }}</a></td><td class="table__td table__td--minimalistic" ng-if="!group">{{ item.label.name }}</td><td class="table__td table__td--minimalistic"><div class="text-right"><a href="#" ng-if="Auth.canDo(\'deleteArtifacts\')" ng-click="destroy(item)" class="btn--icon btn--icon--no-padding btn--icon-bin"><span class="sr-only">Ta bort</span></a></div></td></tr></table><div class="display-flex space-between spacer-mt-medium" ng-if="artifactsFiltered.length > limit"><div></div><hy-paginator get-list="getArtifactsVirtual" page-offset="offset" page-limit="limit" total-count="artifactsFiltered.length"></hy-paginator></div>');
$templateCache.put('components/file-upload-ex-modal/file-upload-ex-modal.html','<form name="vm.fileUploadForm" ng-submit="vm.fileUploadForm.$valid" novalidate><div class="modal-header modal-header--small"><h4 class="text-margin-reset">{{vm.settings.title}}</h4></div><div class="modal-body modal-body--small" id="modal-body" blob-id="vm.file.blobId" metadata="vm.metadata" type="vm.file.type"><div ng-if="vm.settings.types" class="spacer-mb-small"><hy-combobox prompt="{{vm.settings.typeTitle}}" current-item="vm.type" current-item-changed="vm.file.type = vm.type.id; vm.selectedTypeName = vm.type.name" items="vm.settings.types" id-property="id" text-property="name"></hy-combobox></div><hy-file-plate id="file-plate" upload-url="{{vm.settings.uploadUrl}}" upload-params="vm.uploadParams" valid-file-types="{{vm.settings.validFileTypes}}" ready="vm.ready" blob-ids="vm.file.blobIds" multiple="true" metadata="vm.file.metadata"></hy-file-plate></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="vm.cancel();">Avbryt</a> <button class="btn btn--icon btn--icon-upload" type="button" ng-disabled="!vm.ready || !vm.uploadParams" ng-click="vm.doUpload()">Ladda upp</button></div></form>');
$templateCache.put('components/login/login.html','<div class="container"><div class="row spacer-mt-xxxlarge"><div class="col-12 col-lg-4"><div class="text-center"><a href="https://www.hyresgastforeningen.se/" target="_blank"><img src="/img/logo-red-text.png" width="317" class="spacer-mb-large"></a></div><div class="alert alert--success alert--squary alert--white-bg alert--thin-border"><h2 class="spacer-mt-small">Logga in</h2><form name="loginForm" class="spacer-mb-large" ng-submit="loginForm.$valid &amp;&amp; login()" novalidate><div class="form-group" ng-class="{\r\n          \'form-group--error\': (loginForm.inputLoginEmail.$touched || loginForm.$submitted) &amp;&amp; loginForm.inputLoginEmail.$invalid}"><label for="inputLoginEmail">E-post</label> <input type="email" class="form-control form-control--narrow form-control--gray-border" id="inputLoginEmail" name="inputLoginEmail" ng-model="credentials.email" ng-change="redirectToADFSIfHgf()" placeholder="E-post" required> <span ng-show="redirecting">Omdirigerar...</span><hy-progress-circular size="20px" ng-show="redirecting"></hy-progress-circular></div><div class="form-group" ng-class="{\r\n            \'form-group--error\': (loginForm.inputLoginPassword.$touched || loginForm.$submitted) &amp;&amp; loginForm.inputLoginPassword.$invalid\r\n          }"><label for="inputLoginPassword">L\xF6senord</label> <input type="password" class="form-control form-control--narrow form-control--gray-border" id="inputLoginPassword" name="inputLoginPassword" ng-model="credentials.password" placeholder="L\xF6senord" required></div><div class="text-center"><button type="submit" class="btn btn--full-width">Logga in</button><div class="spacer-mt-xsmall spacer-mb-xsmall"><hy-progress-circular size="20px" ng-show="saving"></hy-progress-circular></div><p>Har du gl\xF6mt ditt l\xF6senord? <a href="/emailreset" class="link--turquoise link--underline">\xC5terst\xE4ll det h\xE4r.</a></p></div></form><hr class="hr--grey-light hr--thin"><div><h4 class="alert__title">Har du inte ett konto?</h4><div class="text-right spacer-mb-large"><a href="/users/new" class="btn btn--full-width btn--turq-border">Registrera dig</a></div></div></div></div><div class="col-12 col-lg-8"><h1 class="text-margin-reset">F\xF6rhandlarportalen</h1><!--\r\n      <h3 class="text-margin-top-reset">V\xE4lkommen till Hyresf\xF6rhandlingar genom det digitala f\xF6rhandlingsbordet</h3>\r\n      --><p><b>F\xF6rhandlarportalen \xE4r Hyresg\xE4stf\xF6reningens digitala tj\xE4nst f\xF6r hyresf\xF6rhandling. H\xE4r kan du som hyresv\xE4rd, ombud eller bostadsr\xE4ttsf\xF6rening enkelt hantera era hyresf\xF6rhandingar och andra avtal.</b></p><h4 class="spacer-margin-reset">S\xE5 anv\xE4nder du f\xF6rhandlarportalen</h4><p>F\xF6r att anv\xE4nda f\xF6rhandlarportalen beh\xF6ver du registrera din organisation, d\xE4refter kan du logga in och administrera dina l\xE4genheter p\xE5 ett enkelt s\xE4tt.</p><p>Efter en f\xF6rhandlingsframst\xE4llan har skickats in s\xE5 blir du tilldelad en f\xF6rhandlare som kontaktar dig och f\xF6rhandling p\xE5b\xF6rjas. N\xE4r ni kommit \xF6verens i f\xF6rhandlingen s\xE5 signeras hyrorna digitalt och f\xF6rhandlingen avslutas.</p><h4 class="spacer-margin-reset">F\xF6rdelar med f\xF6rhandlarportalen</h4><p>Portalen har sin bakgrund i ett regeringsuppdrag om att g\xF6ra f\xF6rhandlingsprocessen snabbare och mer transparent.</p><ul class="list-style--disk"><li>F\xF6rhandlingsprocessen blir mer tydlig, effektiv och transparent</li><li>Underl\xE4ttar att f\xF6rhandlingsunderlaget blir korrekt och komplett</li><li>S\xE4kerst\xE4ller hela f\xF6rhandlingsprocessen</li><li>M\xF6jligg\xF6r att l\xE4tt kunna f\xF6lja var i processen en f\xF6rhandling befinner sig</li><li>Skapar f\xF6ruts\xE4ttningar att en f\xF6rhandlings\xF6verenskommelse kan tr\xE4ffas inom 90 dagar</li></ul><p></p></div></div></div>');
$templateCache.put('components/microblog/microblog.html','<style>.twit > p {\r\n        margin-top: 0;\r\n        word-break: break-all;\r\n    }</style><div class="row" ng-show="Auth.canDo(\'editMicroblog\')"><div class="col-2"></div><div class="col-8"><textarea class="form-control form-control--gray-border" ng-model="newTwit.text"></textarea></div><div class="col-2"><button class="btn" ng-disabled="!newTwit || newTwit.text.length < 2" ng-click="saveNew()">Skapa ny<br>anteckning</button></div></div><hr class="hr--thin"><div ng-repeat="twit in twits"><div class="row"><div class="col-2">{{(twit.updatedAt ? twit.updatedAt : twit.createdAt) | date:\'yyyy-MM-dd HH:mm\'}}</div><div class="col-8 twit" ng-if="!twit.editing" ng-bind-html="twit.html"></div><div class="col-8" ng-if="twit.editing"><textarea class="form-control" rows="8" id="twit-{{ twit.id }}" ng-disabled="!Auth.canDo(\'editMicroblog\')" ng-mouseover="hover(twit)" ng-change="twit.dirty = true" ng-model="twit.text"></textarea></div><div class="col-1"><button ng-if="Auth.canDo(\'editMicroblog\') && !twit.editing" class="btn" ng-click="edit(twit)">Redigera</button> <button ng-if="Auth.canDo(\'editMicroblog\') && twit.dirty" class="btn" ng-click="update(twit)">Spara</button> <a class="link--turquoise link--no-underline spacer-mr-medium" ng-if="Auth.canDo(\'editMicroblog\') && twit.editing && !twit.dirty" ng-click="twit.editing = false">Avbryt</a></div><div class="col-1"><div ng-if="Auth.canDo(\'editMicroblog\') && !twit.editing" class="text-right"><a href="#" ng-click="destroy(twit)" class="btn--icon btn--icon--no-padding btn--icon-bin"><span class="sr-only">Ta bort</span></a></div></div></div><hr class="hr--thin hr--grey-light"></div>');
$templateCache.put('components/modalwizard/modal-wizard-step.html','<div class="wizard-step" id="{{::wizardId}}" ng-show="isOpen"><h3 ng-if="alternativeWizard" class="spacer-mb-small spacer-mt-xlarge">{{heading}} - <span class="text-success">{{headingStepNumber}}. </span>{{headingShort}}</h3><h3 ng-if="!alternativeWizard" class="spacer-mb-small spacer-mt-xlarge"><span class="text-success">{{headingStepNumber}}.</span>{{heading}}</h3><div class="alert alert--warning-bordered alert--white-bg alert--slim display-flex align-items-center" ng-if="alertText"><div class="icon icon--message-warning spacer-mr-small"></div><strong class="spacer-mr-small">{{alertHeader}}:</strong>{{alertText}}</div><div ng-transclude></div></div>');
$templateCache.put('components/modalwizard/modal-wizard.html','<h2 class="text-margin-reset">{{headerText}}</h2><div ng-transclude></div><div class="modal-footer modal-footer--medium"><a class="link--turquoise link--no-underline" ng-click="wzCancel()">Avbryt</a> <button class="btn btn--reset-transition btn--arrow-left" ng-click="modalWizardVm.previous()" ng-hide="modalWizardVm.isFirstStep()">Tillbaka</button> <button class="btn btn--reset-transition btn--arrow-right" ng-click="modalWizardVm.next()" ng-hide="modalWizardVm.isLastStep() || modalWizardVm.hideNextButton()" ng-disabled="!modalWizardVm.isCurrentStepValid()">N\xE4sta</button> <button class="btn btn--reset-transition" ng-click="wzSubmit()" ng-show="modalWizardVm.isLastStep()" ng-disabled="!modalWizardVm.isCurrentStepValid() || submitDisabled"><span>{{submitText ? submitText : \'Skicka\'}}</span></button></div>');
$templateCache.put('components/object-footer/object-footer.html','<div class="row spacer-mt-xlarge"><div class="col-6 col-lg-4 spacer-mt-xlarge"><hr class="hr--pink"><h4 class="text-danger">Ta bort {{objectName}}</h4><p>N\xE4r du tar bort din {{objectName}}, finns det ingen \xE5terv\xE4ndo. Var s\xE4ker.</p><button class="btn btn--secondary" ng-click="deleteObject()">Ta bort {{objectName}}</button></div></div>');
$templateCache.put('components/navigation/navigation.html','<header class="navigation" ng-class="{ \'navigation--test\': $window.location.hostname===\'forhandlarportalen-test.azurewebsites.net\' || $window.location.hostname===\'forhandlarportalen-demo.azurewebsites.net\' }"><nav class="container"><a href="/" class="navigation__logo text-uppercase"><span class="spacer-ml-small" ng-if="$window.location.hostname===\'forhandlarportalen-test.azurewebsites.net\'">Test</span> <span class="spacer-ml-small" ng-if="$window.location.hostname===\'forhandlarportalen-demo.azurewebsites.net\'">Demo</span> F<span class="hidden-md-down">\xF6rhandlar</span><span class="text-success"> p<span class="hidden-md-down">ortalen</span></span></a><ul class="navigation__list"><li ng-if="Auth.currentUser() &amp;&amp; !Auth.isDelegateUser()" class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/users/\' + Auth.currentUser().id + \'/welcome\')}"><a class="navigation__list-item--link" ng-class="{ \'navigation__list-item--link-active\': isActive(\'/users/\' + Auth.currentUser().id + \'/welcome\')}" href="/">Mina sidor</a></li><li ng-if="Auth.currentUser() &amp;&amp; !Auth.isDelegateUser()" class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/negotiation-calls\') }"><div class="navigation__list-item--link"><a ng-class="{ \'navigation__list-item--link-active\': isActive(\'/negotiation-calls\') }" href="{{ Auth.isHgfUser() ? \'/negotiation-calls?view=list-new\' : \'/negotiation-calls?view=list-all\' }}">Framst\xE4llningar</a><hy-counter class="spacer-ml-small" count-value="{{ counters.negotiationCalls }}" ng-show="counters.negotiationCalls > 0" tooltip-text="{{ !Auth.isHgfUser() ? \'Antal \xE5terskickade framst\xE4llningar. Var god skicka in dem p\xE5 nytt genom att \xE5ter\xF6ppna dem och r\xE4tta dem enligt kommentar fr\xE5n HGF.\'\r\n            : \'Antal inkomna framst\xE4llningar som \xE4nnu inte tilldelats.\'}}"></hy-counter></div></li><li ng-show="Auth.currentUser()" class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/negotiations\') }"><a class="navigation__list-item--link" ng-class="{ \'navigation__list-item--link-active\': isActive(\'/negotiations\') }" href="/negotiations?view=my-negotiations">F\xF6rhandlingar</a></li><li ng-show="Auth.currentUser()" class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/agreements\') }"><div class="navigation__list-item--link"><a class="navigation__list-item--link" ng-class="{ \'navigation__list-item--link-active\': isActive(\'/agreements\') }" href="/agreements/">Avtal</a><hy-counter class="spacer-ml-small" count-value="{{ counters.freeAgreements }}" ng-show="counters.freeAgreements > 0 &amp;&amp; Auth.isHgfUser()" tooltip-text="Avtal fr\xE5n motpart"></hy-counter></div></li><li class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/search/properties\') || isActive(\'/search/owners\') || isActive(\'/search/agreements\') }" ng-if="Auth.currentUser() &amp;&amp; !Auth.isDelegateUser()"><div class="navigation__list-item--link" uib-dropdown><a ng-class="{ \'navigation__list-item--link-active\': isActive(\'/search/properties\') || isActive(\'/search/owners\') || isActive(\'/search/agreements\') }" href="javascript:void(0);" uib-dropdown-toggle>S\xF6k</a><div class="dropdown-menu dropdown-menu--arrow-center" uib-dropdown-menu role="menu"><a class="dropdown-item" href="/search/properties/">Fastigheter</a><div class="dropdown-divider" ng-if="Auth.currentUser() &amp;&amp; Auth.isHgfUser()"></div><a class="dropdown-item" href="/search/owners/" ng-if="Auth.currentUser() &amp;&amp; Auth.isHgfUser()">\xC4gare</a><div class="dropdown-divider" ng-if="Auth.currentUser() &amp;&amp; Auth.isHgfUser()"></div><a class="dropdown-item" href="/search/agreements?view=agreements" ng-if="Auth.currentUser() &amp;&amp; Auth.isHgfUser()">\xD6verenskommelse/avtal</a></div></div></li><li ng-show="Auth.currentUser()" class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/help/faq\') }"><a class="navigation__list-item--link" ng-class="{ \'navigation__list-item--link-active\': isActive(\'/help/faq\') }" href="/help/faq">FAQ</a></li><li class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/organizations\') || isActive(\'/users\') }" ng-if="Auth.currentUser() &amp;&amp; !Auth.isDelegateUser()"><div class="navigation__list-item--link" uib-dropdown><a ng-class="{ \'navigation__list-item--link-active\': isActive(\'/organizations\') || isActive(\'/users\') }" href="javascript:void(0);" uib-dropdown-toggle>Administration</a><hy-counter class="spacer-ml-small" count-value="{{ counters.pendingOrganizations }}" ng-show="counters.pendingOrganizations > 0 &amp;&amp; Auth.isHgfUser()"></hy-counter><hy-counter class="spacer-ml-small" count-value="{{ counters.pendingUsers }}" ng-show="counters.pendingUsers > 0 &amp;&amp; Auth.isExternalUser() &amp;&amp; isOrganizationAdmin(Auth.currentUser())"></hy-counter><div class="dropdown-menu dropdown-menu--arrow-center" uib-dropdown-menu role="menu"><a class="dropdown-item" href="/organizations/">Fastighets\xE4gare &amp; ombud</a><div class="dropdown-divider"></div><a class="dropdown-item" href="/users/">Anv\xE4ndare</a><div class="dropdown-divider" ng-show=" Auth.isHgfUser() || isOrganizationAdmin(Auth.currentUser())"></div><a class="dropdown-item" href="/requested-accounts/" ng-show=" Auth.isHgfUser() || isOrganizationAdmin(Auth.currentUser())">Beg\xE4rda konton &nbsp;<hy-counter count-value="{{ counters.pendingOrganizations }}" ng-show="counters.pendingOrganizations > 0 &amp;&amp; Auth.isHgfUser()"></hy-counter><hy-counter count-value="{{ counters.pendingUsers }}" ng-show="counters.pendingUsers > 0 &amp;&amp; Auth.isExternalUser()"></hy-counter></a><div class="dropdown-divider" ng-show=" Auth.isHgfUser()"></div><a class="dropdown-item" ng-show=" Auth.isHgfUser()" href="/reports/">Statistik</a></div></div></li><li ng-show="Auth.currentUser()" class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/users/\' + Auth.currentUser().id)}"><div class="navigation__list-item--link"><a class="navigation__list-item--icon" ng-class="{ \'navigation__list-item--icon-active\': isActive(\'/users/\' + Auth.currentUser().id) }" href="/users/{{Auth.currentUser().id}}/"></a> <a href="/users/{{Auth.currentUser().id}}/" class="hidden-lg-down text-margin-negative-left" ng-class="{ \'navigation__list-item--link-active\': isActive(\'/users/\' + Auth.currentUser().id)}">{{Auth.currentUser().firstname}} {{Auth.currentUser().lastname}}</a></div></li><li class="navigation__list-item hidden-xl-up" ng-show="Auth.currentUser()"><a class="navigation__list-item--icon-logout" ng-click="logout(); $event.preventDefault();" href="#"></a></li><li class="navigation__list-item hidden-lg-down" ng-show="Auth.currentUser()"><a class="navigation__list-item--link" ng-click="logout(); $event.preventDefault();" href="#">Logga ut</a></li><li class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/help/process\') }" ng-hide="Auth.currentUser()"><a class="navigation__list-item--link" ng-class="{ \'navigation__list-item--link-active\': isActive(\'/help/process\') }" href="/help/process">Visa processen</a></li><li class="navigation__list-item" ng-class="{ \'navigation__list-item--active\': isActive(\'/\') }" ng-hide="Auth.currentUser()"><a class="navigation__list-item--link" ng-class="{ \'navigation__list-item--link-active\': isActive(\'/\') }" href="/">Logga in</a></li></ul></nav></header>');
$templateCache.put('resources/requested-accounts/list.html','<div class="container"><hy-header title="Beg\xE4rda konton"></hy-header><div ng-if="Auth.currentUser().party === vm.userParties.HGF"><ng-include src="\'resources/requested-accounts/\' + vm.currentSubpage + \'/list.html\'"></ng-include></div><div ng-if="Auth.currentUser().party === vm.userParties.EXTERNAL"><ng-include src="\'resources/requested-accounts/users/list.html\'"></ng-include></div></div>');
$templateCache.put('resources/validation/errors.html','<div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">Uppladdning misslyckades</h2><h4 class="modal-title text-danger text-margin-reset">F\xF6ljande fel har<br>identifierats</h4></div><div class="modal-body modal-body--medium modal-body--medium--padding-tb" id="modal-body"><ng-include ng-if="vm.hasCommonFailure" src="\'resources/validation/partials/_failure.html\'"></ng-include><ng-include ng-if="vm.missingMandatoryColumns" src="\'resources/validation/partials/_missing-columns.html\'"></ng-include><div ng-if="vm.wrongApartments"><p class="text-margin-top-reset">Den uppladdade listans l\xE4genheter verkar inte st\xE4mma \xF6verens med best\xE5ndet. Var god kontrollera listan.</p></div><ng-include ng-if="vm.hasMultipleErrors" src="\'resources/validation/partials/_multiple-errors.html\'"></ng-include><ng-include ng-if="vm.hasFailedRows" src="\'resources/validation/partials/_failed-rows.html\'"></ng-include><ng-include ng-if="vm.hasDuplicates" src="\'resources/validation/partials/_duplicates.html\'"></ng-include><ng-include ng-if="vm.hasNulls" src="\'resources/validation/partials/_nulls.html\'"></ng-include><ng-include ng-if="vm.hasMissingApartmentsFromAgreement" src="\'resources/validation/partials/_missing_apartments_from_agreement.html\'"></ng-include><ng-include ng-if="vm.hasMisspelledMunicipalities" src="\'resources/validation/partials/_misspelled-municipalities.html\'"></ng-include><ng-include ng-if="vm.hasMisspelledPropertyDesignations" src="\'resources/validation/partials/_misspelled-property-designations.html\'"></ng-include><ng-include ng-if="vm.hasNotExistingApartments" src="\'resources/validation/partials/_not-existing.html\'"></ng-include><ng-include ng-if="vm.hasMisspelledAddresses" src="\'resources/validation/partials/_misspelled-addresses.html\'"></ng-include><ng-include ng-if="vm.hasAlreadyAssigned" src="\'resources/validation/partials/_already_assigned.html\'"></ng-include></div><div class="modal-footer modal-footer--medium"><div ng-if="vm.showDownloadReport"><a href="#" ng-click="vm.downloadErrorReport(); $event.preventDefault();" class="btn btn--icon btn--icon-download">Ladda ner fellista <span class="sr-only">Ladda ner</span></a></div><button class="btn btn--secondary" type="button" ng-click="vm.cancel()">St\xE4ng</button></div>');
$templateCache.put('resources/meeting/edit/edit-meetings-modal.html','<form name="bookMeetingForm" ng-submit="bookMeetingForm.$valid  &amp;&amp; vm.save()" novalidate><div class="modal-header modal-header--small"><h2 class="text-margin-top-reset text-margin-bottom-reset">{{::vm.editMode ? \'Redigera\': \'Skapa nytt\'}} m\xF6te</h2></div><div class="modal-body modal-body--small" id="modal-body"><div class="spacer-mb-reset1 alert alert--success-bordered alert--white-bg display-flex align-items-center alert--slim"><div class="icon icon--info"></div><strong class="spacer-ml-small spacer-mr-xsmall">Tips!</strong> H\xE4r skapar ni m\xF6ten f\xF6r att kunna dokumentera era f\xF6rhandlingsm\xF6ten. Dag, tid och plats best\xE4mmer ni utanf\xF6r portalen.</div><div class="row"><div class="col-sm-4 col-12"><div class="form-group spacer-mb-reset" ng-class="{\r\n              \'form-group--error\': (bookMeetingForm.inputMeetingTitle.$touched || bookMeetingForm.$submitted) &amp;&amp; bookMeetingForm.inputMeetingTitle.$invalid,\r\n            }"><label for="inputMeetingTitle">Titel p\xE5 m\xF6tet</label> <input name="inputMeetingTitle" id="inputMeetingTitle" type="text" class="form-control form-control--gray-border form-control--narrow" ng-model="vm.meeting.title" required></div></div><div class="col-sm-4 col-12"><div class="form-group spacer-mb-reset" ng-class="{\r\n            \'form-group--error\': (bookMeetingForm.inputMeetingDate.$touched || bookMeetingForm.$submitted) &amp;&amp; bookMeetingForm.inputMeetingDate.$invalid,\r\n          }"><label for="inputMeetingDate">Datum f\xF6r m\xF6te</label><div class="input-group datepicker"><input name="inputMeetingDate" id="inputMeetingDate" type="text" class="form-control form-control--full-width form-control--narrow form-control--gray-border" uib-datepicker-popup is-open="opened" ng-model="vm.startAt" required close-on-date-selection="true"> <button type="button" class="btn btn--squary btn--icon btn--icon-calendar" ng-click="opened = !opened"></button></div></div></div><div class="col-sm-4 col-12"><div class="form-group spacer-mb-reset" ng-class="{\r\n            \'form-group--error\': (bookMeetingForm.inputTime.$touched || bookMeetingForm.$submitted) &amp;&amp; bookMeetingForm.inputTime.$invalid,\r\n          }"><label for="inputTime" class="text-margin-bottom-reset">Tid</label><div class="display-flex align-items-center spacer-mt-negative-15">Fr\xE5n<div uib-timepicker ng-model="vm.startTimeAt" show-meridian="false" minute-step="5"></div>Till<div uib-timepicker ng-model="vm.endTimeAt" show-meridian="false" minute-step="5"></div></div></div></div><div class="col-12 spacer-mt-negative-15"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (bookMeetingForm.inputPlace.$touched || bookMeetingForm.$submitted) &amp;&amp; bookMeetingForm.inputPlace.$invalid,\r\n          }"><label for="inputPlace">Plats</label> <textarea name="inputPlace" id="inputPlace" rows="1" placeholder="Adress [tex ditt kontor]" class="form-control form-control--gray-border" ng-model="vm.meeting.location" required>\r\n          </textarea></div></div><div class="col-12"><div class="form-group" ng-class="{\r\n             \'form-group--error\': (bookMeetingForm.inputText.$touched || bookMeetingForm.$submitted) &amp;&amp; bookMeetingForm.inputText.$invalid,\r\n          }"><label for="inputText">Fritext</label> <textarea name="inputText" id="inputText" rows="3" placeholder="" class="form-control form-control--gray-border" ng-model="vm.meeting.text">\r\n          </textarea></div></div><div class="col-sm-6 col-12 form-group"><div class="form-group spacer-mb-reset" ng-class="{\r\n             \'form-group--error\': (bookMeetingForm.inputSelectParticipant.$touched || bookMeetingForm.$submitted)\r\n              &amp;&amp; vm.meeting.participants.length === 0,\r\n          }"><label for="inputSelectParticipant">L\xE4gg till {{vm.meeting.participants.length > 0 ? \'ytterligare\' : \'\'}} deltagare</label> <input type="text" name="inputSelectParticipant" id="inputSelectParticipant" autocomplete="off" ng-model="vm.selected" typeahead-editable="false" placeholder="S\xF6k efter en person" typeahead-on-select="vm.addParticipant()" uib-typeahead="user as (user.fullName+\' \'+user.email) for user in vm.users | filter:$viewValue | limitTo:8" class="form-control form-control--gray-border form-control--narrow" ng-required="vm.meeting.participants.length === 0"> <label class="spacer-mb-reset spacer-mt-medium" for="inputSelectParticipant">Tillagda deltagare</label><div class="border--bottom spacer-padding-vertical-small" ng-class="{\'border--top\': $first, \'spacer-mt-small\': $first}" ng-repeat="participant in vm.meeting.participants"><div class="display-flex space-between align-items-center">{{participant.firstname}} {{participant.lastname}}<div class="display-flex align-items-center"><div class="icon icon--info spacer-mr-small" ng-hide="vm.hasUserRelatedNegotiation(participant, vm.meeting.negotiations)" uib-tooltip="Personen \xE4r inte kopplad till n\xE5gon av de valda f\xF6rhandlingarna." tooltip-placement="top"></div><a href="#" class="btn--icon btn--icon-bin btn--icon-bin--size btn--icon--no-padding" ng-click="vm.removeParticipant(participant); $event.preventDefault();"><span class="sr-only">Ta bort</span></a></div></div></div></div></div><div class="col-sm-6 col-12 spacer-mb-reset"><div class="form-group" ng-class="{\r\n             \'form-group--error\': (bookMeetingForm.inputMeetingNegotiation.$touched || bookMeetingForm.$submitted)\r\n             &amp;&amp; vm.meeting.participants.length > 0 &amp;&amp; vm.meeting.negotiations.length === 0,\r\n          }"><label for="inputMeetingNegotiation">L\xE4gg till {{vm.meeting.negotiations.length > 0 ? \'ytterligare\' : \'\'}} f\xF6rhandlingar</label><div class="dropdown dropdown--fancy-caret border--reset" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width" uib-dropdown-toggle ng-disabled="vm.negotiationList.length === 0"><hy-progress-circular size="20px" ng-hide="vm.negotiationListReady"></hy-progress-circular>V\xE4lj F\xF6rhandlingar</button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="negotiation in vm.negotiationList" type="button" class="dropdown-item" ng-click="vm.addNegotiation(negotiation)" ng-disabled="vm.isNegotiationSelected(negotiation.id)">{{ negotiation.caseNumber }} {{ negotiation.name }}</button></div></div><label class="spacer-mb-reset spacer-mt-medium" for="inputSelectParticipant">Tillagda f\xF6rhandlingar</label><div class="border--bottom spacer-padding-vertical-small" ng-class="{\'border--top\': $first, \'spacer-mt-small\': $first}" ng-repeat="negotiation in vm.meeting.negotiations"><div class="display-flex align-items-center space-between"><div><span>{{ negotiation.caseNumber }} {{ negotiation.name }}</span></div><div class="info__content--narrow-block"><a href="#" class="btn--icon btn--icon-bin btn--icon--no-padding" ng-click="vm.removeNegotiation(negotiation); $event.preventDefault();"><span class="sr-only">Ta bort</span></a></div></div></div></div></div></div></div><div class="modal-footer modal-footer--small"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="submit" ng-disabled="vm.saving">Spara</button></div></form>');
$templateCache.put('resources/meeting/edit/edit-protocol-modal.html','<form name="protocolForm" ng-submit="protocolForm.$valid &amp;&amp; vm.saveProtocol()" novalidate><div class="modal-header modal-header--medium"><div class="full-width"><h2 class="text-margin-reset">M\xF6tesprotokoll</h2><h4 class="text-margin-top-reset text-lowercase">{{vm.counterpartName}} och Hyresg\xE4stf\xF6reningen</h4><div class="spacer-mb-reset alert alert--success-bordered alert--white-bg display-flex align-items-center alert--slim" ng-hide="vm.negotiationState === vm.NegotiationStates.FINISHED"><div class="icon icon--info"></div><strong class="spacer-ml-small spacer-mr-xsmall">Tips!</strong>L\xE4gg till protokollpunkter i f\xF6rv\xE4g s\xE5 kan de anv\xE4ndas som dagordning till m\xF6tet.</div></div></div><div class="modal-body modal-body--medium" id="modal-body"><div class="row" ng-hide="vm.negotiationState === vm.NegotiationStates.FINISHED"><div class="col-sm-6 col-12"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (protocolForm.inputDate.$touched || protocolForm.$submitted) &amp;&amp; protocolForm.inputDate.$invalid,\r\n          }"><label for="inputDate" class="text-lowercase text-margin-bottom-reset">Datum</label><div class="input-group datepicker"><input name="inputDate" id="inputDate" type="text" class="form-control form-control--full-width form-control--narrow form-control--gray-border" uib-datepicker-popup is-open="opened" ng-model="vm.protocolDate" close-on-date-selection="false"> <button type="button" class="btn btn--squary btn--icon btn--icon-calendar" ng-click="opened = !opened"></button></div></div></div><div class="col-sm-6 col-12"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (protocolForm.inputLocation.$touched || protocolForm.$submitted) &amp;&amp; protocolForm.inputLocation.$invalid,\r\n          }"><label for="inputLocation" class="text-lowercase text-margin-bottom-reset">Plats</label> <input name="inputLocation" id="inputLocation" type="text" class="form-control form-control--gray-border" ng-model="vm.meeting.protocolLocation" autocomplete="off"></div></div><div class="col-12"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (protocolForm.inputParticipants.$touched || protocolForm.$submitted) &amp;&amp; protocolForm.inputParticipants.$invalid,\r\n          }"><label for="inputParticipants" class="text-lowercase text-margin-bottom-reset">N\xE4rvarande</label> <input name="inputParticipants" id="inputParticipants" type="text" class="form-control form-control--gray-border" ng-model="vm.meeting.protocolParticipants" autocomplete="off"></div></div><div class="col-sm-6 col-12"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (protocolForm.inputChairPerson.$touched || protocolForm.$submitted) &amp;&amp; protocolForm.inputChairPerson.$invalid,\r\n          }"><label for="inputChairPerson" class="text-lowercase text-margin-bottom-reset">Ordf\xF6rande</label> <input name="inputChairPerson" id="inputChairPerson" type="text" class="form-control form-control--gray-border" ng-model="vm.meeting.protocolChairPerson" autocomplete="off"></div></div><div class="col-sm-6 col-12"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (protocolForm.inputSecretary.$touched || protocolForm.$submitted) &amp;&amp; protocolForm.inputSecretary.$invalid,\r\n          }"><label for="inputSecretary" class="text-lowercase text-margin-bottom-reset">Protokollf\xF6rare</label> <input name="inputSecretary" id="inputSecretary" type="text" class="form-control form-control--gray-border" ng-model="vm.meeting.protocolSecretary" autocomplete="off"></div></div><div class="col-12"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (protocolForm.inputAttestors.$touched || protocolForm.$submitted) &amp;&amp; protocolForm.inputAttestors.$invalid,\r\n          }"><label for="inputAttestors" class="text-lowercase text-margin-bottom-reset">Justeringspersoner</label> <input name="inputAttestors" id="inputAttestors" type="text" class="form-control form-control--gray-border" ng-model="vm.meeting.protocolAttestors" autocomplete="off"></div></div><div class="col-12 spacer-mb-large"><strong>Protokollspunkter</strong><div class="spacer-mb-large" ng-repeat="discussion in vm.meeting.protocolDiscussionPoints"><div class="display-flex align-items-center space-between spacer-mb-small"><input name="inputDiscussionSubject" id="inputDiscussionSubject" type="text" class="form-control form-control form-control--gray-border form-control--cursor-default spacer-mr-small" ng-model="discussion.subject" autocomplete="off" ng-disabled="!vm.editDiscussionSubject[$index]"><div><a href="#" class="btn--icon btn--icon-pen" ng-click="vm.editDiscussionSubject[$index]=!vm.editDiscussionSubject[$index]; $event.preventDefault();"><span class="sr-only">Redigera</span></a></div></div><div class="display-flex align-items-center space-between"><textarea name="inputDiscussionText" id="inputDiscussionText" rows="3" class="form-control form-control form-control--gray-border form-control--cursor-default spacer-mr-small" ng-model="discussion.text" ng-disabled="!vm.editDiscussionText[$index]">\r\n            </textarea><div><a href="#" class="btn--icon btn--icon-pen" ng-click="vm.editDiscussionText[$index]=!vm.editDiscussionText[$index]; $event.preventDefault();"><span class="sr-only">Redigera</span></a></div></div><hr class="hr--grey-light hr--thin" ng-hide="$last" ng-if="vm.meeting.protocolDiscussionPoints.length > 1"></div><div class="text-right" ng-hide="vm.showDiscussion"><button class="btn" type="button" ng-click="vm.showDiscussion=true; vm.discussionNumber=vm.meeting.protocolDiscussionPoints.length + 1">L\xE4gg till punkt</button></div><ng-form name="discussionForm" ng-if="vm.showDiscussion" novalidate><div class="spacer-mb-small" ng-class="{\r\n            \'form-group--error\': (discussionForm.inputDiscussionSubject.$touched || discussionForm.$submitted) &amp;&amp; discussionForm.inputDiscussionSubject.$invalid,\r\n          }"><input name="inputDiscussionSubject" id="inputDiscussionSubject" type="text" class="form-control form-control--gray-border" ng-model="vm.discussionSubject" autocomplete="off" placeholder="Titel p\xE5 punkten" required></div><div class="spacer-mb-small"><textarea name="inputDiscussionText" id="inputDiscussionText" rows="6" placeholder="Text som h\xF6r till punkten." class="form-control form-control--gray-border" ng-model="vm.discussionText">\r\n          </textarea></div><div class="display-flex justify-content-end"><button class="btn btn--secondary" type="button" ng-click="vm.showDiscussion=false; vm.closeDiscussion()">St\xE4ng</button> <button class="btn spacer-ml-small" type="button" ng-click="vm.showDiscussion=false; vm.addDiscussionItem()" ng-disabled="discussionForm.$invalid">L\xE4gg till</button></div></ng-form></div></div><!--Read-only mode for negotiation status = FINISHED--><div class="row" ng-show="vm.negotiationState === vm.NegotiationStates.FINISHED"><div class="col-sm-6 col-12 spacer-mb-small"><h4 class="text-margin-reset text-lowercase">Datum</h4>{{vm.protocolDate | date:\'yyyy-MM-dd\'}}</div><div class="col-sm-6 col-12 spacer-mb-small"><h4 class="text-margin-reset text-lowercase">Ort</h4>{{vm.meeting.protocolLocation}}</div><div class="col-12 spacer-mb-small"><h4 class="text-margin-reset text-lowercase">N\xE4rvarande</h4>{{vm.meeting.protocolParticipants}}</div><div class="col-sm-6 col-12 spacer-mb-small"><h4 class="text-margin-reset text-lowercase">Ordf\xF6rande</h4>{{vm.meeting.protocolChairPerson}}</div><div class="col-sm-6 col-12 spacer-mb-small"><h4 class="text-margin-reset text-lowercase">Protokollf\xF6rare</h4>{{vm.meeting.protocolSecretary}}</div><div class="col-12 spacer-mb-small"><h4 class="text-margin-reset text-lowercase">Justeringspersoner</h4>{{vm.meeting.protocolAttestors}}</div><div class="col-12 spacer-mb-large"><hr class="hr--grey-light"><strong>Protokollspunkter</strong><div class="spacer-mb-large" ng-repeat="discussion in vm.meeting.protocolDiscussionPoints"><div class="col-12 alert alert--info-bordered alert--hide-border alert--squary alert--padding-small alert--mb-small">{{discussion.subject}}</div><div class="alert alert--info-bordered alert--hide-border alert--squary alert--padding-small alert--mb-small">{{discussion.text}}</div></div></div></div><hr class="spacer-mt-reset"></div><div class="modal-footer modal-footer--medium"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline" ng-click="vm.cancelProtocol()">St\xE4ng</a> <button class="btn" type="submit" ng-disabled="vm.saving" ng-hide="vm.negotiationState === vm.NegotiationStates.FINISHED">Spara</button></div></form>');
$templateCache.put('resources/negotiation/new/new-newly-constructed-property.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><div class="spacer-mt-large"><hy-wizard wz-submit="vm.save()" goto-next="vm.gotoNext" is-current-step-valid="vm.isCurrentStepValid" header-text="Skapa f\xF6rhandling" submit-text="Skapa" submit-disabled="vm.saving"><hy-wizard-step heading="V\xE4lj f\xF6rhandlare och f\xF6rhandlingss\xE4tt" heading-short="F\xF6rhandlare" heading-step-number="1" name="negotiator" is-valid="vm.negotiation.participants.length > 0 &amp;&amp; vm.negotiatorForm.$valid"><ng-include src="\'resources/negotiation/new/partials/_negotiator.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="Villkor" heading-short="Villkor" heading-step-number="2" name="conditionTags" is-valid="true"><ng-include src="\'resources/negotiation/new/partials/_conditions.html\'"></ng-include></hy-wizard-step></hy-wizard></div></div>');
$templateCache.put('resources/negotiation/new/new.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><div class="spacer-mt-large"><hy-wizard wz-submit="vm.save()" goto-next="vm.gotoNext" is-current-step-valid="vm.isCurrentStepValid" header-text="Skapa f\xF6rhandling" submit-text="Skapa" submit-disabled="vm.saving"><hy-wizard-step heading="Skapa f\xF6rhandling" heading-short="Dela upp" heading-step-number="1" name="choose-process" is-valid="vm.choiceMethod !== undefined"><ng-include src="\'resources/negotiation/new/partials/_choose-process.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj distrikt" heading-short="Omr\xE5de" heading-step-number="2A" name="district" is-disabled="!vm.choiceMethod || vm.choiceMethod === \'ALL_PROPERTIES\'" is-valid="vm.selectedDistricts.length > 0"><ng-include src="\'resources/negotiation/new/partials/_district.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj fastigheter" heading-short="Fastigheter" heading-step-number="2B" name="properties" is-disabled="!vm.choiceMethod || vm.choiceMethod === \'ALL_PROPERTIES\'" is-valid="vm.selectedProperties.length > 0"><ng-include src="\'resources/negotiation/new/partials/_properties.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj l\xE4genheter" heading-short="L\xE4genheter" heading-step-number="2C" name="apartments" is-disabled="!vm.choiceMethod || vm.choiceMethod === \'ALL_PROPERTIES\' || (vm.choiceMethod === \'SELECT_PROPERTIES\' &amp;&amp; vm.rentListBasedNegotiationCall===\'no\')" is-valid="vm.selectedApartments.length > 0"><ng-include src="\'resources/negotiation/new/partials/_apartments.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj f\xF6rhandlare och f\xF6rhandlingss\xE4tt" heading-short="F\xF6rhandlare" heading-step-number="3" name="negotiator" is-valid="vm.negotiation.participants.length > 0 &amp;&amp; vm.negotiatorForm.$valid"><ng-include src="\'resources/negotiation/new/partials/_negotiator.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="Villkor" heading-short="Villkor" heading-step-number="4" name="conditionTags" is-valid="true"><ng-include src="\'resources/negotiation/new/partials/_conditions.html\'"></ng-include></hy-wizard-step></hy-wizard></div></div>');
$templateCache.put('resources/negotiation/edit/edit-delegates-modal.html','<div class="modal-header modal-header--small"><h3 class="text-margin-reset">Representanter F\xF6rhandlingsdelegation</h3></div><div class="modal-body modal-body--small" id="modal-body"><hr class="hr--turquoise hr--margin-reset"><div class="row spacer-mt-xlarge" ng-class="Auth.isHgfUser() || Auth.isExternalUser() ? \'spacer-mb-xlarge\' : \'spacer-mb-small\'"><div class="col-12" ng-hide="Auth.isDelegateUser()"><div class="display-flex align-items-center spacer-mb-xsmall"><label class="spacer-mb-reset" for="inputEmail">Bjud in person via mejl</label><div class="icon icon--info icon--small" uib-tooltip="Ange mailadress h\xE4r f\xF6r att l\xE4gga till i listan p\xE5 delegater. N\xE4r du trycker Spara skickas inbjudan." tooltip-placement="top"></div></div><form name="vm.invitationForm" ng-submit="vm.invitationForm.$valid &amp;&amp; vm.inviteDelegation()" novalidate><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.invitationForm.inputEmail.$touched || vm.invitationForm.$submitted) &amp;&amp; vm.invitationForm.inputEmail.$invalid}"><input name="inputEmail" id="inputEmail" type="email" class="form-control form-control--narrow form-control--gray-border spacer-mr-small" ng-model="vm.delegateEmail" ng-pattern="/^(([^<>()\\[\\]\\\\.,;:\\s@\u201C]+(\\.[^<>()\\[\\]\\\\.,;:\\s@\u201C]+)*)|(\u201C.+\u201C))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$/" placeholder="Exempel: hgf@hgf.se" autocomplete="off" maxlength="128" required><hy-progress-circular size="20px" ng-show="vm.inviting"></hy-progress-circular><br><div class="display-flex align-items-center"><div class="dropdown space-between dropdown-menu--full-width dropdown--fancy-caret border--reset" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width" uib-dropdown-toggle ng-disabled="vm.rolesList.length === 0">{{vm.delegateRole.name}}</button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="role in vm.rolesList" type="button" class="dropdown-item" ng-click="vm.chooseRole(role.name, role.code)">{{ role.name }}</button></div></div><button class="btn" type="submit" ng-disabled="vm.inviting" ng-click="vm.invitationForm.inputEmail.$invalid || vm.addDelegateInvitation()" style="margin-left: 20px">L\xE4gg till</button></div><small ng-show="(vm.invitationForm.inputEmail.$touched || vm.invitationForm.$submitted) &amp;&amp; vm.invitationForm.inputEmail.$invalid">Ange en fullst\xE4ndig giltig epostadress</small></div></form></div></div><div ng-show="vm.delegationParticipants.length > 0" class="spacer-mb-xlarge"><h4 class="text-margin-reset">Delegation</h4><hr class="hr--turquoise hr--thin text-margin-top-reset"><div ng-repeat="delegate in vm.delegationParticipants"><div class="row"><div class="col display-flex align-items-center"><div><div><strong>{{delegate.firstname}} {{delegate.lastname}}</strong></div><div><a href="mailto:{{delegate.email}}" class="link--turquoise text-reset word-break-all">{{delegate.email}}</a></div><div>{{delegate.phone}}</div><div>Rollen: {{delegate.role.name}}</div></div></div><div class="col display-flex align-items-center space-between"><div ng-hide="Auth.isDelegateUser()"><div><strong>Tillagd av</strong></div><hy-progress-circular size="20px" ng-show="vm.getInvitors"></hy-progress-circular><div>{{delegate.negotiationsUsers.fullName}}</div></div><div ng-hide="Auth.isDelegateUser()"><a href="#" class="btn--icon btn--icon-bin btn--icon--no-padding" ng-click="vm.removeDelegationParticipant(vm.delegationParticipants, delegate); $event.preventDefault();"><span class="sr-only">Ta bort</span></a></div></div></div><hr class="hr--grey-light hr--thin"></div></div><div ng-show="vm.delegationInvitations.length > 0"><h4 class="text-margin-reset">V\xE4ntande inbjudningar</h4><hr class="hr--turquoise hr--thin text-margin-top-reset"><div ng-repeat="delegate in vm.delegationInvitations | orderBy: \'email\'"><div class="row"><div class="col display-flex align-items-center"><div><div><a href="mailto:{{delegate.email}}" class="link--turquoise text-reset word-break-all">{{delegate.email}}</a><br><span>Rollen: {{delegate.role.name}}</span></div></div></div><div class="col text-right"><div ng-hide="Auth.isDelegateUser()"><a href="#" class="btn--icon btn--icon-bin btn--icon--no-padding" ng-click="vm.removeDelegationParticipant(vm.delegationInvitations, delegate); $event.preventDefault();"><span class="sr-only">Ta bort</span></a></div></div></div><hr class="hr--grey-light hr--thin"></div></div></div><div class="modal-footer modal-footer--small" ng-hide="Auth.isDelegateUser()"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="button" ng-click="vm.saveDelegation()" ng-disabled="vm.saving">Spara</button></div>');
$templateCache.put('resources/negotiation/edit/edit-negotiationType-modal.html','<form name="vm.negotiationInfoForm" ng-submit="vm.negotiationInfoForm.$valid &amp;&amp; vm.save()" novalidate><div class="modal-header modal-header--medium"><h3 class="text-margin-reset">\xC4ndra f\xF6rhandlings-information</h3></div><div class="modal-body modal-body--medium" id="modal-body"><div class="row"><div class="col-12"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.negotiationInfoForm.inputName.$touched || vm.negotiationInfoForm.$submitted) &amp;&amp; vm.negotiationInfoForm.inputName.$invalid,\r\n          }"><label for="inputName">Namn p\xE5 f\xF6rhandling</label> <input name="inputName" id="inputName" type="text" class="form-control form-control--narrow form-control--gray-border" placeholder="Skriv in namn" ng-model="vm.name" autocomplete="off" required> <small class="form-group__help text-xsmall">Exempel: Lind Fastigheter - \xC5rlig hyresf\xF6rhandling 2017</small></div></div><div class="col-12"><label for="inputNegotiationType">F\xF6rhandlingstyp</label> <input name="inputNegotiationType" id="inputNegotiationType" type="hidden" ng-model="vm.type" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle ng-disabled="(vm.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY || vm.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT)"><span ng-class="{\'text-gray\': vm.type === undefined}">{{vm.type ? (vm.type | hyNegotiationTypes) : \'V\xE4lj typ av hyresf\xF6rhandling\'}}</span></button><div class="dropdown-menu dropdown-menu--full-width" uib-dropdown-menu role="menu"><button ng-repeat="value in vm.NegotiationTypes | hyFilterObjectValuesByKeysEndWith:\'PROPERTY\'" type="button" class="dropdown-item" ng-click="vm.type = value" ng-hide="value === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY" ng-if="vm.isPropertyBased">{{value | hyNegotiationTypes}}</button> <button ng-repeat="value in vm.NegotiationTypes | hyFilterObjectValuesByKeysEndWith:\'APARTMENT\'" type="button" class="dropdown-item" ng-click="vm.type = value" ng-hide="value === vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT" ng-if="!vm.isPropertyBased">{{value | hyNegotiationTypes}}</button></div></div></div><div class="col-12 spacer-mt-small"><label for="inputCounterpart">Motpart</label> <input name="inputCounterpart" id="inputCounterpart" type="hidden" ng-model="vm.type" required><hy-combobox prompt="V\xE4lj organisation" items="vm.possibleCounterparts" text-property="name" id-property="id" current-item="vm.counterpartOrganization"></hy-combobox></div></div></div><div class="modal-footer modal-footer--medium"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="submit" ng-disabled="vm.saving || !vm.counterpartOrganization">Spara</button></div></form>');
$templateCache.put('resources/negotiation/edit/edit-representatives-modal.html','<div class="modal-header modal-header--small"><h3 class="text-margin-reset">Representanter {{vm.editUserParty === vm.Parties.HGF ? \'Hyresg\xE4stf\xF6reningen\' : \'Hyresv\xE4rd\'}}</h3></div><div class="modal-body modal-body--small" id="modal-body"><hr class="hr--turquoise hr--margin-reset"><div ng-show="vm.canEdit"><div class="row spacer-mt-xlarge spacer-mb-xsmall"><div class="col-12"><label class="spacer-mb-xsmall" for="inputSelectParticipant">S\xF6k efter person</label> <input type="text" name="inputSelectParticipant" id="inputSelectParticipant" autocomplete="off" ng-model="vm.selectedParticipant" ng-change="vm.clearOpposite(1)" typeahead-editable="false" placeholder="S\xF6k" uib-typeahead="user as user.fullName for user in vm.users | filter: {fullName: $viewValue} | limitTo:8" class="form-control form-control--narrow form-control--gray-border"></div></div><div class="row spacer-mt-xsmall spacer-mb-xsmall"><div class="col-12"><div class="display-flex align-items-center spacer-mb-xsmall"><label class="spacer-mb-reset" for="inputEmail">...eller bjud in ny person via mejl</label><div class="icon icon--info icon--small" uib-tooltip="Ange mailadress h\xE4r f\xF6r att bjuda in representanter till f\xF6rhandlingen. N\xE4r du trycker Spara s\xE5 skickas inbjudan." tooltip-placement="top"></div></div><form name="vm.invitationForm" ng-submit="vm.invitationForm.$valid &amp;&amp; vm.inviteDelegation()" novalidate><div class="form-group" ng-class="{\r\n                \'form-group--error\': (vm.invitationForm.inputEmail.$touched || vm.invitationForm.$submitted) &amp;&amp; vm.invitationForm.inputEmail.$invalid}"><input name="inputEmail" id="inputEmail" type="email" class="form-control form-control--narrow form-control--gray-border spacer-mr-small" ng-change="vm.clearOpposite(2)" ng-model="vm.inviteEmail" ng-pattern="/^(([^<>()\\[\\]\\\\.,;:\\s@\u201C]+(\\.[^<>()\\[\\]\\\\.,;:\\s@\u201C]+)*)|(\u201C.+\u201C))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$/" placeholder="Exempel: hgf@hgf.se" autocomplete="off" maxlength="128"><hy-progress-circular size="20px" ng-show="vm.working"></hy-progress-circular><small ng-show="(vm.invitationForm.inputEmail.$touched || vm.invitationForm.$submitted) &amp;&amp; vm.invitationForm.inputEmail.$invalid">Ange en fullst\xE4ndig giltig epostadress</small></div></form></div></div><div class="row" ng-show="vm.editUserParty == vm.Parties.HGF"><div class="col-12"><button class="btn btn--half-width" type="submit" ng-disabled="vm.working || !vm.selectedParticipant" ng-click="vm.addParticipant(vm.NegotiationRoles.HGF_ASSOC)">L\xE4gg till som representant f\xF6r HGF</button> <button class="btn btn--half-width" type="submit" ng-disabled="vm.working || !vm.inviteEmail" ng-click="vm.inviteParticipant(vm.NegotiationRoles.HGF_ELECTED)">Bjud in som f\xF6rtroendevald HGF</button></div></div><div class="row" ng-show="vm.editUserParty !== vm.Parties.HGF"><div class="col-12"><button class="btn btn--half-width spacer-mb-xsmall" type="submit" ng-disabled="vm.working || !vm.selectedParticipant" ng-click="vm.addParticipant(vm.NegotiationRoles.FA_POWNER)">L\xE4gg till som hyresv\xE4rd</button> <button class="btn btn--half-width spacer-mb-xsmall" type="submit" ng-disabled="vm.working || !vm.selectedParticipant" ng-click="vm.addParticipant(vm.NegotiationRoles.OMBUD_FA_POWNER)">L\xE4gg till som hyresv\xE4rd ombud</button> <button class="btn btn--half-width spacer-mb-xsmall" type="submit" ng-disabled="vm.working || !vm.inviteEmail" ng-click="vm.inviteParticipant(vm.NegotiationRoles.FA_POWNER)">Bjud in som hyresv\xE4rd</button> <button class="btn btn--half-width spacer-mb-xsmall" type="submit" ng-disabled="vm.working || !vm.inviteEmail" ng-click="vm.inviteParticipant(vm.NegotiationRoles.OMBUD_FA_POWNER)">Bjud in som hyresv\xE4rd ombud</button></div></div></div><div class="spacer-mt-xlarge"><div ng-repeat="participant in combo = vm.negotiation.participants.concat(vm.delegationParticipants)"><div class="row"><div class="col"><div><strong>{{participant.firstname}} {{participant.lastname}}</strong></div><div>{{vm.deChief(participant.roleObj.name)}}</div><div><a href="mailto:{{participant.email}}" class="link--turquoise text-reset word-break-all">{{participant.email}}</a></div><div>{{participant.phone}}</div></div><div class="col display-flex align-items-center space-between"><div class="custom-control custom-control--radio custom-control--radio--no-margin"><input class="custom-control__input" type="radio" id="inputIsChief_{{participant.id}}" name="inputIsChief" ng-model="vm.chiefId" ng-value="{{participant.id}}" ng-click="vm.tagChief(participant)" ng-disabled="(!vm.canEdit || !vm.canBeChief(participant))"> <label class="custom-control__label" for="inputIsChief_{{participant.id}}">Ansvarig f\xF6rhandlare</label></div><div ng-show="vm.canDelete(participant)"><a href="#" class="btn--icon btn--icon-bin btn--icon--no-padding" ng-click="vm.removeParticipant(participant); $event.preventDefault();"><span class="sr-only">Ta bort</span></a></div></div></div><hr class="hr--grey-light hr--thin"></div></div></div><div class="col-12" ng-show="vm.waitingInvites.length > 0"><h4 class="text-margin-reset">V\xE4ntande inbjudningar</h4><hr class="hr--turquoise hr--thin text-margin-top-reset"><div ng-repeat="delegate in vm.waitingInvites | orderBy: \'email\'"><div class="row align-items-center"><div class="col display-flex align-items-center"><div><div><a href="mailto:{{delegate.email}}" class="link--turquoise text-reset word-break-all">{{delegate.email}}</a><br><span>{{delegate.roleObj.name}}</span></div></div></div><div class="col text-right" ng-show="vm.canEdit"><div ng-hide="Auth.isDelegateUser()"><a href="#" class="btn--icon btn--icon-bin btn--icon--no-padding" ng-click="vm.removeDelegationParticipant(vm.waitingInvites, delegate); $event.preventDefault();"><span class="sr-only">Ta bort</span></a></div></div></div><hr class="hr--grey-light hr--thin"></div></div><div class="modal-footer modal-footer--small" ng-show="vm.canEdit"><hy-progress-circular size="20px" ng-show="vm.working"></hy-progress-circular><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="button" ng-click="vm.save()" ng-disabled="vm.working">Spara</button></div>');
$templateCache.put('resources/negotiation/edit/skip-rentlist-modal.html','<form name="vm.negotiationSkipRentlistForm" ng-submit="vm.save()" novalidate><div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">Varning</h2></div><div class="modal-body modal-body--medium" id="modal-body"><div class="row"><div class="col-12"><input name="inputCanSkipRentlist" id="inputCanSkipRentlist" type="hidden" ng-model="vm.canSkipRentlist" required><div ng-if="vm.canSkipRentlist && vm.hasApartments"><p class="text-danger">Det finns l\xE4genheter registrerade p\xE5 denna fastighet och d\xE5 ska INTE denna \xF6verenskommelsetyp anv\xE4ndas. G\xE5 tillbaka och se \xF6ver vilken typ av data som redan \xE4r inl\xE4st innan du v\xE4ljer att kryssa i denna ruta.</p></div><div ng-hide="vm.canSkipRentlist && vm.hasApartments"><p>\xC4r du s\xE4ker p\xE5 det h\xE4r?</p></div></div><div class="modal-footer modal-footer--medium"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="submit" ng-disabled="vm.saving">Spara</button></div></div></div></form>');
$templateCache.put('resources/negotiation-call/list/list.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><hy-header title="Framst\xE4llningar" buttons="vm.headerButtons" tabs="vm.tabs"></hy-header><ng-include src="\'resources/negotiation-call/list/\' + vm.currentSubpage + \'/list.html\'"></ng-include></div>');
$templateCache.put('resources/meeting/show/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-show="vm.ready"><hy-breadcrumb></hy-breadcrumb><div class="row spacer-mt-xlarge display-flex space-between"><div class="col-8 col-lg-9"><h1 class="text-margin-bottom-reset spacer-mt-small word-break-all"><span ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}">M\xF6te <span class="text-success">{{vm.meeting.endAt | date:\'yyyy-MM-dd\'}}</span></span></h1><h3 class="text-margin-reset spacer-mb-large">{{vm.meeting.title}}</h3></div></div><hr class="hr--grey-light hr--full-width"><div class="row spacer-mb-large"><div class="col-8"><div class="display-flex align-items-center space-between"><h3 class="text-margin-reset">M\xF6tesinformation</h3><div ng-hide="Auth.isDelegateUser() || vm.negotiation.isFinished"><a href="#" class="btn--icon btn--icon-pen" ng-click="vm.editMeeting(); $event.preventDefault();"><span class="sr-only">Redigera</span></a></div></div><div class="alert alert--narrow-gray alert--squary spacer-mt-small alert--min-height-236"><div class="row spacer-mt-small"><div class="col-4 col-lg-3 spacer-mb-medium"><h4 class="text-margin-reset text-lowercase">F\xF6rhandlingar</h4><div ng-repeat="negotiation in vm.meeting.negotiations">{{negotiation.name}}</div><h4 class="text-margin-bottom-reset text-lowercase">Plats</h4>{{vm.meeting.location}}</div><div class="col-4 col-lg-3 spacer-mb-medium"><h4 class="text-margin-reset text-lowercase">Datum</h4>{{vm.meeting.startAt | date:\'yyyy-MM-dd\'}}<h4 class="text-margin-bottom-reset text-lowercase">Tid</h4>{{vm.meeting.startAt | date:\'shortTime\'}} - {{vm.meeting.endAt | date:\'shortTime\'}}</div><div class="col-4 col-lg-3"><h4 class="text-margin-reset text-lowercase">Deltagare</h4><div ng-repeat="participant in vm.meeting.participants">{{participant.firstname}} {{participant.lastname}} ({{ participant.party | hyParties }})</div></div><div class="col-12 col-lg-3 spacer-mb-small"><h4 class="text-margin-reset text-lowercase">Fritext</h4>{{vm.meeting.text}}</div></div></div></div><div class="col-4"><div class="alert alert--hide-border alert--squary" ng-class="{\'spacer-mt-xlarge\' : Auth.isDelegateUser()}"><h3 class="text-margin-top-reset spacer-ml-medium">M\xF6tesprotokoll</h3><div class="col-12 spacer-mb-small"><p>Ska du f\xF6ra m\xF6tesprotokoll, anv\xE4nd g\xE4rna protokollfunktionen. D\xE5 f\xE5r du en f\xE4rdig mall med halva jobbet gjort. Fyll i det som saknas och spara sedan som pdf.</p></div><div class="col-12" ng-hide="Auth.isDelegateUser()"><button type="button" class="btn btn--full-width spacer-mb-small" ng-click="vm.addEditProtocol()" ng-disabled="vm.meeting.protocolDocument">{{vm.meeting.hasProtocol || vm.negotiation.isFinished ? \'\xD6ppna\' : \'Skapa\'}}</button> <button type="button" class="btn btn--icon btn--icon-upload btn--full-width" ng-disabled="vm.meeting.hasProtocol" ng-click="vm.addProtocolFile()">Ladda upp protokoll</button></div></div></div></div><div class="row" ng-show="vm.meeting.hasProtocol" ng-hide="vm.negotiation.isFinished"><div class="col-12"><div class="alert alert--narrow-gray alert--squary spacer-mt-small"><table ng-show="vm.meeting.hasProtocol" class="table table--margin-b-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th"><div class="spacer-mb-small">Namn</div></th><th class="table__th"><div class="spacer-mb-small">\xC4gare</div></th><th class="table__th"><div class="spacer-mb-small">Digitalt signerad av</div></th><th class="table__th"><div class="spacer-mb-small">Uppladdat den</div></th><th class="table__th"></th></tr><tr ng-show="vm.meeting.protocolDocument" class="table__tr table__tr--minimalistic"><td class="table__td table__td--minimalistic table__td--width-25"><a href="/api/meetings/{{vm.meeting.id}}/protocol/{{vm.meeting.protocolDocument.name}}" target="_self" download>{{ vm.meeting.protocolDocument.filename }}</a></td><td class="table__td table__td--minimalistic table__td--width-25">{{ vm.meeting.protocolDocument.createdBy }}</td><td class="table__td table__td--minimalistic table__td--width-25">...</td><td class="table__td table__td--minimalistic table__td--width-15">{{ vm.meeting.protocolDocument.lastModified | date:\'yyyy-MM-dd\'}}</td><td class="table__td table__td--minimalistic table__td--width-10"><div class="text-right" ng-hide="Auth.isDelegateUser()"><a href="#" ng-click="vm.deleteProtocol(); $event.preventDefault();" class="btn--icon btn--icon--no-padding btn--icon-bin" ng-hide="vm.Auth.currentUser().id === vm.meeting.protocolDocument.creatorId"><span class="sr-only">Ta bort</span></a></div></td></tr><tr ng-show="!vm.meeting.protocolDocument || !vm.meeting.hasProtocol" class="table__tr table__tr--minimalistic"><td class="table__td table__td--minimalistic table__td--width-25">{{ vm.meeting.endAt | date:\'yyyy-MM-dd\'}}</td><td class="table__td table__td--minimalistic table__td--width-25">{{ vm.meeting.protocolChairPerson }}</td><td class="table__td table__td--minimalistic table__td--width-25">...</td><td class="table__td table__td--minimalistic table__td--width-15">{{ vm.meeting.protocolDate | date:\'yyyy-MM-dd\'}}</td><td class="table__td table__td--minimalistic table__td--width-10"><div class="text-right" ng-hide="Auth.isDelegateUser()"><a href="#" ng-click="vm.deleteProtocol(); $event.preventDefault();" class="btn--icon btn--icon--no-padding btn--icon-bin"><span class="sr-only">Ta bort</span></a></div></td></tr></table></div></div></div><div class="text-right"><div ng-show="!vm.meeting.protocolDocument">{{ vm.meeting.protocolDocument }}</div><a class="btn" ng-show="vm.meeting.hasProtocol" ng-class="{\'disabled\': vm.meeting.protocolDocument}" target="_self" download href="{{vm.pdfPath}}">Generera pdf</a></div><div class="row" ng-hide="Auth.isDelegateUser() || vm.negotiation.isFinished"><div class="col-6 col-lg-4"><hr class="text-margin-top-reset hr--pink"><h4 class="text-danger">Ta bort m\xF6te?</h4><p>OBS! N\xE4r du tar bort m\xF6tet kommer \xE4ven alla dokument som \xE4r kopplade till m\xF6tet att f\xF6rsvinna.</p><button class="btn btn--secondary" ng-click="vm.deleteMeeting()">Ta bort m\xF6te</button></div></div></div>');
$templateCache.put('resources/negotiation/list/list.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><hy-header title="F\xF6rhandlingar" buttons="vm.headerButtons" tabs="vm.tablist"></hy-header><ng-include src="\'resources/negotiation/list/\' + vm.currentSubpage + \'/list.html\'"></ng-include><div class="spacer-mt-small"><button class="btn di" ng-if="Auth.canDo(\'changeNegotiators\')" ng-click="vm.changeNegotiator()">\xC4ndra ansvarig f\xF6rhandlare</button> <button class="btn di" ng-if="Auth.canDo(\'markNegotiationAsRead\') && vm.showMarkAsRead" ng-click="vm.markAsRead()">Markera som l\xE4st</button><div ng-if="vm.showExportPopup" class="unterpopup di col-6"><hy-progress-circular ng-if="vm.exportBusy" size="30px"></hy-progress-circular><hy-checkbox class="di spacer-mr-medium" ng-if="!vm.exportBusy" ng-repeat="status in vm.exportStatuses" title="{{status.code | hyCombinedNegotiationStates }}" name="s{{status.code}}" model="status.isSet"></hy-checkbox><div class="unterpopup-footer" ng-if="!vm.exportBusy"><button class="btn spacer-mt-xsmall" ng-click="vm.export()">OK</button> <a class="link--turquoise link--no-underline" ng-click="vm.showExportPopup = false">Avbryt</a></div></div><button class="btn di" ng-click="vm.beginExport()">Exportera lista</button></div></div>');
$templateCache.put('resources/negotiation/show/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div ng-class="{\'disabled-background\': (vm.negotiation.isFinished)}"><div class="container" ng-show="vm.ready"><hy-breadcrumb></hy-breadcrumb><div class="row spacer-mt-xlarge spacer-mb-small"><div class="col-9"><h1 class="text-margin-bottom-reset spacer-mt-small word-break-all">F\xF6rhandling <span class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}">{{vm.negotiation.caseNumber}}</span></h1><h3 class="text-margin-reset">{{vm.negotiation.counterpart.name}} - {{vm.negotiation.type | hyNegotiationTypes}} <a href="#" ng-if="Auth.canDo(\'editNegotiation\')" class="btn--icon btn--icon-pen btn--icon-bottom-right spacer-ml-xsmall" ng-hide="vm.negotiation.isFinished" ng-click="vm.editNegotiation(); $event.preventDefault();"><span class="sr-only">Redigera</span></a></h3></div><div ng-if="vm.FeatureConfiguration.ENABLE_MEETINGS" class="col-3 col-lg-2" ng-hide="(!vm.negotiation.isFinished) &amp;&amp;  vm.negotiation.type !== vm.NegotiationTypes.ANNUAL_PROPERTY"><div class="statistics-box__item-squary-turquoise margin-left-auto" ng-class="{\'statistics-box__item-squary-turquoise--light-grey\' : (vm.negotiation.isFinished)}"><h1 class="statistics-box__item-title h1-large" ng-hide="vm.negotiation.isFinished"><span ng-bind-html="vm.negotiation.daysLeft | daysLeft:vm.negotiation.state:90"></span></h1><h3 class="statistics-box__item-title text-black" ng-show="vm.negotiation.isFinished"><strong>avslutad</strong></h3><h5 class="statistics-box__item-content" ng-class="{\'text-black\': (vm.negotiation.isFinished)}"><span ng-bind-html="vm.negotiation.daysLeft | daysLeft:vm.negotiation.state:90" ng-show="vm.negotiation.isFinished"></span> {{vm.negotiation.daysLeft === 0 ? \'dag\' : \'dagar\'}} kvar</h5></div></div><div class="col-2" ng-if="Auth.canDo(\'viewNegotiationComment\')"><img src="/img/comment{{ vm.negotiation.comment ? \'-filled\' : \'\' }}.png" width="48" height="48" class="di spacer-mt-medium cursor-pointer" uib-tooltip-html="vm.negotiation.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && vm.editComment()"><br><small>Skriv kommentar/anteckning</small></div></div><div class="row spacer-mb-medium"><div class="col"><div class="display-flex"><span class="spacer-mr-medium">Status: <strong class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}">{{vm.negotiation.state | hyNegotiationWithBlockingStates:vm.negotiation.blockingAuthority}}</strong> </span><span class="spacer-mr-medium">Fastigheter: <strong class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}">{{vm.negotiation.realEstateSummary.properties}} </strong></span><span class="spacer-mr-medium">L\xE4genheter: <strong class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}">{{vm.negotiation.realEstateSummary.apartments || \'Ej angivet\'}}</strong> </span><span ng-if="vm.FeatureConfiguration.ENABLE_MEETINGS" class="spacer-mr-medium">N\xE4sta m\xF6te: <strong class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}" ng-hide="vm.negotiation.isFinished">{{vm.negotiation.nextMeeting | date: \'yyyy-MM-dd\' }}</strong> <strong class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}" ng-show="(vm.negotiation.isFinished) || !vm.negotiation.nextMeeting">&mdash;</strong> </span><span ng-if="vm.FeatureConfiguration.ENABLE_MEETINGS" class="spacer-mr-medium" ng-show="vm.negotiation.type === vm.NegotiationTypes.ANNUAL_PROPERTY">Dagar kvar: <strong class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}" ng-show="vm.negotiation.isFinished">Avslutad</strong> <strong class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}" ng-bind-html="vm.negotiation.daysLeft | daysLeft:vm.negotiation.state:90"></strong> <strong class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}">{{vm.negotiation.daysLeft == 1 &amp;&amp; \'dag\' || \'dagar\'}} kvar</strong></span></div></div></div><div class="row"><div class="col tabs-container" ng-if="vm.isNewlyConstructedProperty"><hy-tabs tablist="vm.tablistForNewProperties"></hy-tabs></div><div class="col tabs-container" ng-if="!vm.isNewlyConstructedProperty"><hy-tabs tablist="vm.tablist"></hy-tabs></div></div></div></div><div class="container" ng-show="vm.ready"><hr class="hr--full-width hr--turquoise spacer-mt-reset"><ng-include src="\'resources/negotiation/show/\' + vm.currentSubpage + \'/show.html\'"></ng-include><object-footer ng-if="Auth.canDo(\'deleteNegotiation\') && vm.currentSubpage === \'overview\'" object-name="f\xF6rhandling" warning-message-delete="{{vm.warningMessageDelete}}" f-delete="vm.deleteNegotiation"></object-footer></div>');
$templateCache.put('resources/negotiation/new-direct/basic.html','<div class="alert alert--border-turq alert--squary"><form name="vm.basicForm" ng-submit="vm.basicForm.$valid &amp;&amp; vm.gotoNextStep()" class="spacer-mb-xlarge" novalidate><div class="row"><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputName.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputName.$invalid,\r\n          }"><label for="inputName">Namn p\xE5 f\xF6rhandling</label> <input name="inputName" id="inputName" type="text" class="form-control form-control--narrow form-control--gray-border" placeholder="Skriv in ett namn" ng-model="vm.negotiationName" autocomplete="off" required> <small class="form-group__help text-xsmall">Exempel: Lind Fastigheter - \xC5rlig hyresf\xF6rhandling 2017</small></div></div><div class="col-6 col-lg-3"><label for="inputNegotiationType" class="label--icon"><a href="#" class="display-inline-flex" ng-click="vm.showNegotiationTypeInfo(); $event.preventDefault();"><div>F\xF6rhandlingstyp</div><div class="icon icon--info icon--small cursor-pointer"></div></a></label> <input name="inputNegotiationType" id="inputNegotiationType" type="hidden" ng-model="vm.negotiationType" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle><span ng-class="{\'text-gray\': vm.negotiationType === undefined}">{{(vm.negotiationType !== undefined) ? (vm.negotiationType | hyNegotiationTypes) : \'Ange f\xF6rhandlingstyp\'}}</span></button><div class="dropdown-menu dropdown-menu--left" uib-dropdown-menu role="menu"><!--<span class="dropdown-header"><strong>Fastighetsvisa f\xF6rhandlingar</strong></span>  | hyFilterObjectValuesByKeysEndWith:\'PROPERTY\' --> <button ng-repeat="value in vm.NegotiationTypes" type="button" class="dropdown-item dropdown-item--indent" ng-click="vm.negotiationType = value">{{value | hyNegotiationTypes}}</button><!--\r\n            <span class="dropdown-header"><strong>L\xE4genhetsvisa f\xF6rhandlingar</strong></span>\r\n            <button ng-repeat="value in vm.NegotiationTypes | hyFilterObjectValuesByKeysEndWith:\'APARTMENT\'"\r\n              type="button"\r\n              class="dropdown-item dropdown-item--indent"\r\n              ng-click="vm.negotiationType = value">\r\n              {{value | hyNegotiationTypes}}\r\n            </button>\r\n            --></div></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputRentEffectiveFrom.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputRentEffectiveFrom.$invalid,\r\n          }"><label for="inputName">\xD6nskat datum f\xF6r hyresf\xF6r\xE4ndring</label><div class="input-group datepicker"><input name="inputRentEffectiveFrom" id="inputRentEffectiveFrom" type="text" class="form-control form-control--full-width form-control--narrow form-control--gray-border" uib-datepicker-popup datepicker-options="vm.rentEffectiveFromDateOptions" popupplacement="bottom-left" is-open="opened" ng-model="vm.rentEffectiveFrom" required readonly="readonly" close-on-date-selection="true"> <button type="button" class="btn btn--squary btn--icon btn--icon-calendar" ng-click="opened = !opened"></button></div></div></div><div class="col-12" ng-class="{\'col-lg-6\': vm.negotiationType === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY}"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputMessage.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputMessage.$invalid,\r\n          }"><label for="inputMessage">\xD6vrig information</label> <textarea name="inputMessage" id="inputMessage" rows="6" placeholder="H\xE4r kan du till exempel skriva &ldquo;Jag vill p\xE5kalla f\xF6rhandling om n\xE4stkommande \xE5rs hyror p\xE5 v\xE5ra bost\xE4der&rdquo;" class="form-control" ng-model="vm.negotiationMessage">\r\n          </textarea></div></div></div></form></div><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation/new-direct/new.html','<div class="container"><div class="spacer-mt-large"><pre> </pre></div><div class="spacer-mt-large"><hy-wizard wz-submit="vm.save()" goto-next="vm.gotoNext" is-current-step-valid="vm.isCurrentStepValid" header-text="Skapa f\xF6rhandling" submit-text="Skapa" submit-disabled="vm.saving"><hy-wizard-step heading="Grundsinformation" heading-short="Grundsinformation" heading-step-number="1" name="basic" is-valid="vm.basicForm.$valid"><ng-include src="\'resources/negotiation/new-direct/basic.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj best\xE5nd" heading-short="V\xE4lj best\xE5nd" heading-step-number="2A" name="selection" is-disabled="vm.negotiationType === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY ||\r\n                                   vm.negotiationType === vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT " is-valid="vm.selectedApartments.length > 0 || vm.properties.length > 0"><hy-apartment-selector-2 id="apartmentSelector" allow-empty-properties="!vm.ApartmentNegotiationTypes.includes(vm.negotiationType)" empty-properties="vm.properties" empty-only="vm.isPropertyBased" user-id="Auth.currentUser().id" model="vm.selectedApartments"></hy-apartment-selector-2></hy-wizard-step><hy-wizard-step heading="V\xE4lj fastigheter" heading-short="V\xE4lj fastigheter" heading-step-number="2B" name="property-selection" is-disabled="vm.negotiationType !== vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT" is-valid="true"><ng-include src="\'resources/negotiation/new-direct/properties.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="Yrkande och motiv" heading-short="Yrkande och motiv" heading-step-number="3" name="motiv" is-disabled="vm.negotiationType === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY" is-valid="vm.demandsCreated"><ng-include src="\'resources/negotiation/new/partials/_motiv.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj f\xF6rhandlare" heading-short="V\xE4lj f\xF6rhandlare" heading-step-number="4" name="negotiator" is-valid="vm.negotiatorForm.$valid && vm.negotiation.participants.length > 0"><ng-include src="\'resources/negotiation/new/partials/_negotiator.html\'"></ng-include><div style="float: right; width: 200px; position: relative; top: -50px; left: -20px;" ng-if="vm.negotiation.participants.length > 0"><hy-combobox prompt="V\xE4lj organisation" items="vm.chiefParticipantOrganizations" id-property="id" text-property="name" current-item="vm.selectedOrganization"></hy-combobox></div></hy-wizard-step><hy-wizard-step heading="Villkor" heading-short="Villkor" heading-step-number="5" name="conditionTags" is-valid="true"><ng-include src="\'resources/negotiation/new/partials/_conditions.html\'"></ng-include></hy-wizard-step></hy-wizard></div></div>');
$templateCache.put('resources/negotiation/new-direct/properties.html','<form name="vm.propertiesForm" ng-submit="vm.propertiesForm.$valid && vm.gotoNextStep()" class="spacer-mb-xlarge" novalidate><div class="row"><p class="spacer-pl-medium">Ange de fastigheter du vill p\xE5kalla f\xF6rhandling f\xF6r genom att s\xF6ka fram dem i portalens register genom att skriva in fastighetsbeteckningen i f\xE4ltet nedan. De fastigheter du har valt visas i listan nedan. Om informationen inte st\xE4mmer i listan s\xE5 f\xE5r du g\xE5 tillbaka ett steg och v\xE4lja att ange best\xE5ndet genom en annan k\xE4lla.</p><div class="col-10 col-md-4"><div class="spacer-mb-xxlarge"><div class="dropdown dropdown--hide-caret dropdown--no-border" ng-class="{\'open\' : vm.toggleMenu}"><label class="display-flex align-items-center">S\xF6k fram och l\xE4gg till fastigheter<div ng-click="vm.showHelpProperties(\'S\xF6k fastigheter\', \'Om inte den fastighet du s\xF6ker finns med i resultatet, prova att specifiera din s\xF6kning (fler bokst\xE4ver).\')" class="icon icon--info icon--small cursor-pointer display-inline-block spacer-ml-xsmall"></div></label><div class="display-flex spacer-mb-xsmall"><input type="text" class="form-control form-control--search form-control--search--large" placeholder="S\xF6k p\xE5 alla" ng-model="vm.inputTextComplex" hy-enter="vm.searchProperties()" ng-focus="vm.toggleMenu = false"> <a href="#" class="btn--icon btn--icon-search btn--icon-search" ng-click="vm.searchProperties(); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--typeahead" role="menu"><button ng-repeat="property in vm.propertiesTypeahead" type="button" class="dropdown-item" ng-click="vm.addProperty(property); vm.toggleMenu = false">{{property.propertyDesignation}} ({{property.municipality}})</button></div></div></div></div><div class="col-2 col-md-6 display-flex align-items-center"><div ng-show="vm.searching"><hy-progress-circular size="40px"></hy-progress-circular></div></div><div class="col-12"><h3 class="spacer-mt-reset">Valda fastigheter</h3><hy-progress-circular size="40px" ng-hide="!vm.editMode || vm.readyProperties"></hy-progress-circular><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__td--width-20">Fastigheter</th><th class="table__th table__td--width-20">Kommun</th><th class="table__th table__td--width-20">LM distrikt</th><th class="table__th table__td--width-30">Fastighets\xE4gare</th><th class="table__th table__th--width-icon"></th></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.readyProperties" ng-repeat="property in vm.properties | orderBy:\'propertyDesignation\'"><td class="table__td table__td--minimalistic">{{property.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{property.municipality}}</td><td class="table__td table__td--minimalistic">{{property.district}}</td><td class="table__td table__td--minimalistic"><div ng-show="property.numOwners === 1 &amp;&amp; property.primaryOwner.name !== null">{{property.primaryOwner.name}}</div><div ng-show="property.numOwners === 1 &amp;&amp; property.primaryOwner.name === null">{{property.owners[0].name}}</div><div ng-show="property.numOwners > 1" ng-click="vm.toggleOwnersList[property.id]=!vm.toggleOwnersList[property.id]; vm.getPropertyOwners(property.id); $event.preventDefault();"><strong>{{property.primaryOwner.name || \'Fakturamottagare ok\xE4nd\'}}</strong> <a href="#" class="btn--icon btn--icon-expand ellipsis" ng-class="!vm.toggleOwnersList[property.id] ? \'btn--icon-expand\' : \'btn--icon-collapse\'"></a><div ng-show="vm.toggleOwnersList[property.id]"><div ng-hide="vm.readyOwners[property.id]"><hy-progress-circular size="20px"></hy-progress-circular></div><div ng-repeat="owner in property.owners">{{owner.name}}</div></div></div></td><td class="table__td table__td--minimalistic"><div class="text-right"><a href="#" ng-click="vm.removeProperty(property); $event.preventDefault();" class="btn--icon btn--icon--no-padding btn--icon-bin"><span class="sr-only">Ta bort</span></a></div></td></tr></table></div></div></form><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/new/new.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><div class="spacer-mt-large"><hy-wizard wz-submit="vm.save()" goto-next="vm.gotoNext" is-current-step-valid="vm.isCurrentStepValid" header-text="Skapa framst\xE4llan" submit-text="{{vm.lastButton}}" submit-disabled="vm.saving"><hy-wizard-step heading="Grundinformation" heading-short="Grundinformation" heading-step-number="1" name="basic" is-valid="vm.basicForm.$valid" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_basic.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="Viktig information om fastighet och region!" heading-short="Information" heading-step-number="1.5" name="obs" is-disabled="vm.isHgf" is-valid="true"><ng-include src="\'resources/negotiation-call/new/partials/red-letter-warning.html\'"></ng-include></hy-wizard-step><!-- Choose-process--><hy-wizard-step heading="V\xE4lj k\xE4lla f\xF6r att ange ditt fastighetsbest\xE5nd" heading-short="K\xE4lla" heading-step-number="2" name="choose-process" is-disabled="vm.negotiationCall.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT" is-valid="vm.chooseForm.$valid" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_choose-process.html\'"></ng-include></hy-wizard-step><!-- End Choose-process--><!-- Properties --><hy-wizard-step heading="Ange fastigheter direkt i portalen" heading-short="Ange fastigheter" heading-step-number="{{vm.negotiationCall.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT ? 2 : 3}}" name="properties" is-disabled="vm.realEstateChoiceMethod !== \'DIRECT_PROPERTIES\'" is-valid="vm.properties.length > 0" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_properties.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="Ange best\xE5nd direkt i portalen" heading-short="Ange best\xE5nd" heading-step-number="3A" name="properties" is-disabled="vm.realEstateChoiceMethod !== \'DIRECT_APARTMENTS\'" is-valid="vm.apartmentSelector.apartments.length > 0 && vm.emptyProperties.length === 0 || vm.apartmentSelector.apartments.length === 0 && vm.emptyProperties.length > 0" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><div class="row" ng-if="vm.apartmentSelector.apartments.length > 0 && vm.emptyProperties.length > 0"><p class="text-alert spacer-pl-medium">Ange l\xE4genheter eller fastigheter, det fungerar inte med b\xE5da typerna!</p></div><hy-apartment-selector-2 id="apartmentSelector" allow-empty-properties="true" empty-only="vm.isPropertyBased" empty-properties="vm.emptyProperties" model="vm.apartmentSelector.apartments"></hy-apartment-selector-2></hy-wizard-step><!-- End Properties --><!-- File-upload --><hy-wizard-step heading="Ladda upp fastighetslista" heading-short="Fastighetslista" heading-step-number="3A" name="file-upload" is-disabled="vm.realEstateChoiceMethod !== \'PROPERTY_LIST\'" is-valid="vm.propertyListUploadForm.$valid" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_upload-property-list.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="Ladda upp hyreslista" heading-short="Hyreslista" heading-step-number="3A" name="file-upload" is-disabled="vm.realEstateChoiceMethod !== \'RENT_LIST\'" is-valid="vm.rentListUploadForm.$valid" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_upload-rent-list.html\'"></ng-include></hy-wizard-step><!-- File-upload --><!-- Examine-first-line --><hy-wizard-step heading="F\xF6rhandsgranska f\xF6rsta raden" heading-short="F\xF6rhandsgranska" heading-step-number="3B" name="examine-first-line" is-disabled="!(vm.realEstateChoiceMethod === \'PROPERTY_LIST\' || vm.realEstateChoiceMethod === \'RENT_LIST\')" is-valid="vm.propertyListUploadForm.$valid || vm.rentListUploadForm.$valid" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_examine-first-line.html\'"></ng-include></hy-wizard-step><!-- End Examine-first-line --><!-- Property-motivation --><hy-wizard-step heading="Ange yrkande &amp; motiv f\xF6r fastigheter" heading-short="Yrkande &amp; motiv" heading-step-number="4" name="property-motivation" is-disabled="vm.getMotivationType() !== \'property\'" is-valid="vm.demandsCreated" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_property-motivation.html\'"></ng-include></hy-wizard-step><!-- End Property-motivation --><!-- Apartment-motivation --><hy-wizard-step heading="Ange yrkande &amp; motiv f\xF6r l\xE4genheter" heading-short="Yrkande &amp; motiv" heading-step-number="4" name="apartment-motivation" is-disabled="vm.getMotivationType() !== \'apartment\'" is-valid="true || vm.apartmentMotivationForm.$valid" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_apartment-motivation.html\'"></ng-include></hy-wizard-step><!-- End Apartment-motivation --><!-- File-upload --><hy-wizard-step heading="Ladda upp valfritt dokument" heading-short="Valfria dokument" heading-step-number="3" name="file-upload" is-disabled="vm.realEstateChoiceMethod !== \'OPTIONAL_DOCUMENTS\'" is-valid="true || vm.fileUploadForm.$valid" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_upload-document-list.html\'"></ng-include></hy-wizard-step><!-- End File-upload --><!-- Select-landlord --><hy-wizard-step heading="V\xE4lj hyresv\xE4rd/ombud" heading-short="V\xE4lj hyresv\xE4rd/ombud" heading-step-number="3" name="select-landlord" is-disabled="!vm.isHgf || vm.negotiationCall.type !== vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT" is-valid="!!vm.selectedOrganization"><ng-include src="\'resources/negotiation/new/partials/_negotiator.html\'"></ng-include><div style="float: right; width: 200px; position: relative; top: -110px; left: -20px;" ng-if="vm.negotiation.participants.length > 0"><hy-combobox prompt="V\xE4lj organisation" items="vm.chiefParticipantOrganizations" id-property="id" text-property="name" current-item="vm.selectedOrganization"></hy-combobox></div></hy-wizard-step><hy-wizard-step heading="V\xE4lj hyresv\xE4rd/ombud" heading-short="V\xE4lj hyresv\xE4rd/ombud" heading-step-number="4B" name="select-landlord" is-disabled="!vm.isHgf || vm.negotiationCall.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT" is-valid="!!vm.selectedOrganization"><ng-include src="\'resources/negotiation/new/partials/_negotiator.html\'"></ng-include><div style="float: right; width: 200px; position: relative; top: -110px; left: -20px;" ng-if="vm.negotiation.participants.length > 0"><hy-combobox prompt="V\xE4lj organisation" items="vm.chiefParticipantOrganizations" id-property="id" text-property="name" current-item="vm.selectedOrganization"></hy-combobox></div></hy-wizard-step><!-- End Select-landlord --><!-- Preview-and-submit --><hy-wizard-step heading="{{vm.lastHeader}}" heading-short="{{vm.lastSubheader}}" heading-step-number="5" name="preview-and-submit" is-disabled="vm.negotiationCall.type !== vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY" is-valid="true" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_preview-and-submit-properties-new-built.html\'"></ng-include></hy-wizard-step><!-- End Preview-and-submit --><!-- Properties-preview-and-submit --><hy-wizard-step heading="{{vm.lastHeader}}" heading-short="{{vm.lastSubheader}}" heading-step-number="{{vm.isHgf ? 4 : 3}}" name="properties-preview-and-submit" is-disabled="vm.negotiationCall.type !== vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT" is-valid="true" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_preview-and-submit-properties.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="{{vm.lastHeader}}" heading-short="{{vm.lastSubheader}}" heading-step-number="5" name="properties-preview-and-submit" is-disabled="!vm.isPropertyBased" is-valid="true" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_preview-and-submit-properties.html\'"></ng-include></hy-wizard-step><!-- End Properties-preview-and-submit --><!-- Apartments-preview-and-submit --><hy-wizard-step heading="{{vm.lastHeader}}" heading-short="{{vm.lastSubheader}}" heading-step-number="5" name="apartments-preview-and-submit" is-disabled="!vm.isApartmentBased" is-valid="true" alert-header="Meddelande fr\xE5n HGF" alert-text="vm.negotiationCall.response"><ng-include src="\'resources/negotiation-call/new/partials/_preview-and-submit-apartments.html\'"></ng-include></hy-wizard-step></hy-wizard><!-- End Apartments-preview-and-submit --></div></div>');
$templateCache.put('components/modal/confirmation/warningConfirmationModal.html','<!-- move to fe-common --><div class="modal-header modal-header--xsmall"><h4 class="text-margin-reset red-text">VARNING!</h4></div><div class="modal-body modal-body--small" id="modal-body" ng-bind-html="vm.message"></div><div class="modal-footer modal-footer--xsmall modal-footer--xsmall-right"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button type="button" class="btn" ng-class="{ \'btn--secondary\': vm.critical }" ng-click="vm.confirm()">{{vm.button}}</button></div>');
$templateCache.put('resources/negotiation-call/show/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div ng-class="{\'disabled-background\': (vm.negotiationCall.state === vm.NegotiationCallStates.ASSIGNED)}"><div class="container" ng-show="vm.ready"><hy-breadcrumb></hy-breadcrumb><div class="row spacer-mb-medium spacer-mt-xlarge"><div class="col-8"><h1 class="text-margin-bottom-reset text-margin-top-reset word-break-all">Framst\xE4llan <span class="text-success" ng-class="{\'text-gray\' : (vm.negotiationCall.state === vm.NegotiationCallStates.ASSIGNED)}">{{vm.negotiationCall.caseNumber}}</span></h1><h3 class="text-margin-top-reset spacer-mb-small">{{vm.negotiationCall.counterpart.name}} - {{vm.negotiationCall.type | hyNegotiationTypes}}</h3><div class="display-flex">Status: <strong class="text-success spacer-ml-small" ng-class="{\'text-danger\' : (vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_EXT), \'text-gray\' : (vm.negotiationCall.state === vm.NegotiationCallStates.ASSIGNED)}">{{vm.negotiationCall.state | hyNegotiationCallStates:Auth.isHgfUser()}}</strong><div class="icon icon--small icon--info spacer-ml-small cursor-pointer" ng-show="vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_EXT" ng-click="vm.renderHelpModal(\'\xC5terskickad Framst\xE4llan\', \'HGF har skickat tillbaka framst\xE4llan f\xF6r de anser att den beh\xF6vs justeras innan f\xF6rhandling kan skapas. Var god skicka in den p\xE5 nytt genom att \xE5ter\xF6ppna och g\xF6ra de \xE4ndringar HGF efterfr\xE5gar.\')"></div></div></div><div class="col-4 text-lg-right display-flex flex-direction-column justify-content-end"><div class="statistics-box__item-squary-turquoise statistics-box__item-squary-turquoise--light-grey-wide text-center margin-left-auto" ng-show="vm.negotiationCall.state===vm.NegotiationCallStates.ASSIGNED"><h3 class="text-margin-reset text-black">Framst\xE4llan<br>\xE4r tilldelad</h3></div><div class="statistics-box__item-squary-turquoise statistics-box__item-squary-turquoise--light-grey-wide text-center margin-left-auto" ng-show="vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_EXT  &amp;&amp; vm.isHgfUser"><h3 class="text-margin-reset text-black">Framst\xE4llan<br>\xE4r<br>\xE5terskickad</h3></div><div><button type="button" ng-if="Auth.canDo(\'createDirectNegotiation\')" ng-click="vm.gotoNewNegotiation(vm.negotiationCall.id)" class="btn" ng-disabled="!(vm.toggleValue &amp;&amp; vm.currentUserId === vm.negotiationCall.handledByUserId)" ng-show="vm.isHgfUser &amp;&amp; (vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_HGF || vm.negotiationCall.state === vm.NegotiationCallStates.PARTIALLY_ASSIGNED)"><span class="text-uppercase">Skapa en f\xF6rhandling</span></button></div><div><a ng-show="vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_EXT &amp;&amp; !vm.isHgfUser" href="negotiation-calls/{{vm.negotiationCall.id}}/edit" class="btn">\xC5ter\xF6ppna</a></div></div></div></div><hr class="hr--grey-light hr--full-width"></div><div class="container" ng-show="vm.ready"><div class="display-flex flex-direction-row space-between spacer-mt-large spacer-mb-medium spacer"><h3 class="text-margin-reset">Framst\xE4llaninformation</h3><div class="display-flex flex-direction-row align-items-center" ng-if="Auth.canDo(\'updateHandler\') &amp;&amp; \r\n      !(vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_EXT ||\r\n      vm.negotiationCall.state === vm.NegotiationCallStates.ASSIGNED)"><span class="spacer-mr-xsmall">{{ vm.togglerText }}</span><switch id="enabled" name="enabled" ng-model="vm.toggleValue" ng-change="vm.handleToggle()" class="green"></switch></div></div><div class="alert alert--narrow-gray alert--squary"><div class="row"><div class="col-4"><h4 class="text-margin-reset text-transform-none">Namn</h4>{{vm.negotiationCall.name}}<h4 class="text-margin-bottom-reset text-transform-none">Typ av f\xF6rhandling</h4>{{vm.negotiationCall.type | hyNegotiationTypes}}<h4 class="text-margin-bottom-reset text-transform-none">\xD6nskat datum f\xF6r hyres\xE4ndring</h4>{{vm.negotiationCall.rentEffectiveFrom | date:\'yyyy-MM-dd\'}}<h4 class="text-margin-bottom-reset text-transform-none">Meddelande till hyresg\xE4stf\xF6reningen</h4><span class="word-break-balanced" ng-if="vm.negotiationCall.message" ng-bind-html="vm.negotiationCall.message| preventDateBreak"></span> <span class="word-break-balanced" ng-if="!vm.negotiationCall.message">Ej angivet</span><h4 ng-if="vm.negotiationCall.negotiationDate" class="text-margin-bottom-reset text-transform-none">\xD6nskat datum f\xF6r f\xF6rhandling</h4><span ng-if="vm.negotiationCall.negotiationDate" class="word-break-all">{{vm.negotiationCall.negotiationDate | date:\'yyyy-MM-dd\' }}</span><h4 ng-if="vm.negotiationCall.negotiationLocation" class="text-margin-bottom-reset text-transform-none">\xD6nskad plats f\xF6r f\xF6rhandling</h4><span ng-if="vm.negotiationCall.negotiationLocation" class="word-break-all">{{vm.negotiationCall.negotiationLocation}}</span></div><div class="col-4"><h4 class="text-margin-reset text-transform-none">Skickad till HGF kontor</h4>{{vm.negotiationCall.office.name}}<h4 class="text-margin-bottom-reset text-transform-none">Skapad den</h4>{{vm.negotiationCall.sentAt | date:\'yyyy-MM-dd\'}}<h4 class="text-margin-bottom-reset text-transform-none">Skickad av</h4>{{vm.negotiationCall.creator.firstname + \' \' + vm.negotiationCall.creator.lastname}}<div ng-if="Auth.isHgfUser()"><h4 class="text-margin-bottom-reset text-transform-none">Ankomstdatum till HGF</h4>{{vm.negotiationCall.incomingDate | date : "yyyy-MM-dd"}}</div><h4 class="text-margin-bottom-reset text-transform-none">F\xF6rhandlingsordningens part p\xE5 hyresv\xE4rdsidan</h4><span ng-show="vm.negotiationCall.landlordSide==0">Hyresv\xE4rd </span><span ng-show="vm.negotiationCall.landlordSide==1">Hyresv\xE4rd och organisation av fastighets\xE4gare, i vilken hyresv\xE4rden \xE4r medlem</span><div ng-show="vm.negotiationCall.handler &amp;&amp; !vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_EXT"><h4 class="text-margin-bottom-reset text-transform-none">Ansvarig p\xE5 HGF</h4>{{vm.negotiationCall.handler.fullName}}</div><div ng-show="vm.negotiationCall.rejecter &amp;&amp; vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_EXT"><h4 class="text-margin-bottom-reset text-transform-none">\xC5terskickad av</h4>{{vm.negotiationCall.rejecter.fullName}}</div></div><div class="col-4 spacer-pr-xxxlarge" ng-if="!vm.negotiationCall.isNewlyConstructedProperty"><h4 class="text-margin-top-reset spacer-mb-xsmall">Valt fastighetsbest\xE5nd</h4><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">Kommuner: <span class="text-success text-bold">{{vm.negotiationCall.realEstateSummary.municipalities}}</span></div><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">Fastigheter: <span class="text-success text-bold">{{vm.negotiationCall.realEstateSummary.properties}}</span></div><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">L\xE4genheter: <span class="text-success text-bold">{{vm.negotiationCall.realEstateSummary.apartments || \'-\'}}</span></div><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">Fastighets\xE4gare: <span class="text-success text-bold">{{vm.negotiationCall.realEstateSummary.owners || \'-\'}}</span></div><div ng-show="vm.negotiationCall.response"><div class="display-flex spacer-mt-medium spacer-mb-xsmall"><h4 class="text-margin-reset text-transform-none">Meddelande fr\xE5n HGF (\xC5terskickad)</h4></div>{{vm.negotiationCall.response}}</div></div><div class="col-4" ng-if="vm.negotiationCall.isNewlyConstructedProperty"><h4 class="text-margin-bottom-reset text-transform-none">Kommun</h4>{{vm.negotiationCall.municipalityText}}<h4 class="text-margin-bottom-reset text-transform-none">Fastighets\xE4gare</h4>{{vm.negotiationCall.ownersText}}</div></div></div><div class="display-flex flex-direction-row justify-content-end"><a class="link--turquoise link--underline spacer-mb-medium spacer-mr-xlarge" ng-click="vm.returnNegotiationCall()" ng-show="Auth.canDo(\'returnNegotiationCall\') &&\r\n      vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_HGF\r\n      &amp;&amp; vm.toggleValue &amp;&amp; vm.currentUserId === vm.negotiationCall.handledByUserId">Skicka tillbaka </a><a href="/api/negotiation-calls/{{vm.negotiationCall.id}}/receipt" target="_blank" class="link--turquoise link--underline spacer-mb-medium spacer-mr-xlarge">Visa PDF </a><a class="link--turquoise link--underline spacer-mb-medium" ng-click="vm.updateOffice()" ng-hide="!Auth.canDo(\'setOfficeNegotiationCall\') || vm.negotiationCall.state === vm.NegotiationCallStates.ASSIGNED || \r\n      vm.negotiationCall.state === vm.NegotiationCallStates.WAITING_EXT">\xC4ndra kontor</a></div><div class="row" ng-if="!vm.negotiationCall.isNewlyConstructedProperty"><div class="col tabs-container"><hy-tabs tablist="vm.tablist"></hy-tabs></div><div class="col-12"><hr class="spacer-mt-reset"></div></div><div class="row" ng-if="vm.negotiationCall.isNewlyConstructedProperty"><div class="col-6 tabs-container"><hy-tabs tablist="vm.tablist"></hy-tabs></div><div class="col-12"><hr class="spacer-mt-reset"></div></div><ng-include src="\'resources/negotiation-call/show/\' + vm.currentSubpage + \'/show.html\'"></ng-include><div class="spacer-mt-xlarge" ng-show="Auth.canDo(\'deleteCall\') && (vm.negotiationCall.state < 200)"><object-footer object-name="framst\xE4llan" f-delete="vm.deleteCall"></object-footer></div></div>');
$templateCache.put('resources/agreement/rentlist/show.html','<div class="container" ng-controller="ShowAgreementRentlistCtrl as showApartmentsVM"><hy-breadcrumb></hy-breadcrumb><div class="display-flex space-between spacer-mb-xlarge"><div><a href="/negotiations/{{vm.negotiation.id}}/agreements/{{vm.agreement.id}}" class="btn btn--reset-transition btn--arrow-left">Till \xF6verenskommelsen</a></div></div><div class="display-flex"><div class="spacer-mr-small"><h3 class="text-margin-reset">\xD6verenskommelse <span class="text-success">{{ vm.agreement.sequenceNumber }}</span> - F\xF6rhandling <span class="text-success">{{ vm.negotiation.caseNumber }}</span></h3></div></div><hy-progress-circular size="60px" center-on-page ng-hide="showApartmentsVM.readyApartments"></hy-progress-circular><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="showApartmentsVM.listColumns" order="showApartmentsVM.sortColumn" direction="showApartmentsVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th table__td--width-10">Nationellt<br>l\xE4genhetsnr<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="showApartmentsVM.inputTextNationalApartmentNo" hy-enter="showApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="showApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">L\xE4genhetsnr<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="showApartmentsVM.inputTextApartmentNo" hy-enter="showApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="showApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Gatuadress<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="showApartmentsVM.inputTextStreetAddress" hy-enter="showApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="showApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Fastighet<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="showApartmentsVM.inputTextPropertyDesignation" hy-enter="showApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="showApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Kommun<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="showApartmentsVM.inputTextMunicipality" hy-enter="showApartmentsVM.getApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="showApartmentsVM.getApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">\xD6verenskommen ny hyra (kr/m\xE5nad)</th></tr></thead><tr class="table__tr--minimalistic table__tr--disable-click" ng-repeat="apartment in showApartmentsVM.apartments" ng-class="{\'table__tr--muted\' : !showApartmentsVM.readyApartments}"><td class="table__td table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{ apartment.nationalNo | nationalNoControl }}</td><td class="table__td table__td--nowrap">{{ apartment.ownerNo }}</td><td class="table__td">{{ apartment.streetAddress }}</td><td class="table__td">{{ apartment.property.propertyDesignation }}</td><td class="table__td">{{ apartment.municipality }}</td><td class="table__td">{{ apartment.newMonthlyRent | number : 2 }}</td></tr></table><div class="display-flex space-between spacer-mt-medium" ng-show="showApartmentsVM.apartmentsTotalCount > 0"><div>Ladda ner listan <a class="link--turquoise link--underline spacer-ml-xsmall" target="_self" download href="/api/negotiations/{{vm.agreement.negotiationId}}/agreements/{{vm.agreement.id}}/export-rent-list">h\xE4r</a></div><div ng-show="showApartmentsVM.apartmentsTotalCount > 0 &amp;&amp; showApartmentsVM.apartmentsTotalCount <= showApartmentsVM.pageLimit"><strong>Totalt: </strong>{{showApartmentsVM.apartmentsTotalCount}}</div><hy-paginator ng-show="showApartmentsVM.apartmentsTotalCount > showApartmentsVM.pageLimit" get-list="showApartmentsVM.getApartments" page-offset="showApartmentsVM.currentPage" page-limit="showApartmentsVM.pageLimit" total-count="showApartmentsVM.apartmentsTotalCount"></hy-paginator></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="showApartmentsVM.apartmentsTotalCount === 0">Inga l\xE4genheter hittades.</div></div>');
$templateCache.put('resources/agreement/show/conversion-errors-modal.html','<div class="modal-header modal-header--small"><h2 class="text-margin-top-reset text-margin-bottom-reset">Konvertering misslyckades</h2></div><div class="modal-body modal-body--small" id="modal-body">Dessa filer har inte konverterats till PDF f\xF6r Scrive behov:<br><div ng-repeat="error in vm.conversionErrors"><strong>{{ error.filename }}</strong></div></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="$dismiss()">Avbryt</a> <button class="btn" ng-click="vm.ok()">Forts\xE4tt \xE4nd\xE5</button></div>');
$templateCache.put('resources/agreement/show/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready &amp;&amp; vm.readyDocument"></hy-progress-circular><div class="container" ng-show="vm.ready &amp;&amp; vm.readyDocument"><hy-breadcrumb></hy-breadcrumb><div class="row spacer-mt-xlarge display-flex space-between spacer-mb-large"><div class="col-8 col-lg-9"><h1 class="text-margin-bottom-reset spacer-mt-small word-break-all">{{vm.freeAgreementType ? \'Avtal\' : \'\xD6verenskommelse\'}} <span class="text-success spacer-ml-small" ng-if="!vm.freeAgreementType" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}">{{ vm.agreement.sequenceNumber}}</span></h1><div ng-if="vm.negotiation.id"><h3 class="text-margin-reset spacer-mb-large">{{vm.negotiation.counterpart.name}} och Hyresg\xE4stf\xF6reningen -</h3><h3 class="spacer-mt-reset spacer-mb-xlarge">F\xF6rhandling <span class="text-success">{{ vm.negotiation.caseNumber }}</span></h3></div><!--\r\n      <div ng-if="vm.negotiation.id === undefined">\r\n        <h3 class="text-margin-reset spacer-mb-large">{{vm.counterpart.name}} och Hyresg\xE4stf\xF6reningen</h3>\r\n      </div>\r\n    --></div><div class="col-4 col-lg-3 spacer-mt-medium display-flex justify-content-end" ng-if="vm.negotiation.id"><hy-donut part="(vm.agreement | doneTasksCounter: vm.negotiation.type).done" total="(vm.agreement | doneTasksCounter: vm.negotiation.type).total" max-height="145px" fixed-width="145px" status-finished="vm.negotiation.isFinished"></hy-donut></div></div><ng-include src="\'resources/agreement/show/overview/show.html\'"></ng-include></div>');
$templateCache.put('resources/agreement/free/change-participants-modal.html','<div class="modal-header modal-header--small"><h2 class="text-margin-top-reset text-margin-bottom-reset">V\xE4lj personer</h2></div><div class="modal-body modal-body--small" id="modal-body"><hy-participants user-container="vm.userContainer"></hy-participants><h4 class="spacer-reset">Organisation</h4><hy-combobox items="vm.userOrganizations" id-property="id" text-property="name" current-item="vm.selectedOrganization"></hy-combobox></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="$dismiss()">Avbryt</a> <button class="btn" ng-click="vm.ok()" ng-disabled="vm.userContainer.hgfSide.length===0 || vm.userContainer.faSide.length===0">Spara</button></div>');
$templateCache.put('resources/agreement/free/list.html','<div class="container"><hy-header title="Avtal" buttons="vm.headerButtons" tabs="vm.tablist"></hy-header><table class="table"><tr><th class="table__th">Nummer</th><th class="table__th">Typ</th><th class="table__th">Text</th><th class="table__th">Tidsperiod</th><th class="table__th">Parter</th></tr><tr ng-if="vm.pendingAgreements.length > 0"><td colspan="6"><div class="spacer-mb-large"></div><b>P\xE5kallade avtal fr\xE5n motpart</b> <span class="icon icon--inline icon--info" uib-tooltip="{{ Auth.isHgfUser() ? \'N\xE4r du trycker p\xE5 P\xE5b\xF6rja kan motpart p\xE5b\xF6rja avtalet\' : \'V\xE4nta tills HGF kommer att p\xE5b\xF6rja detta avtal\' }}" tooltip-placement="bottom"></span><div class="spacer-mb-large"></div></td></tr><tr class="table__tr" ng-repeat="agreement in vm.pendingAgreements"><td class="table__td table__td--width-10">{{agreement.fancyNumber}}</td><td class="table__td table__td--no-wordbreak table__td--width-15">{{vm.FreeAgreementTypes[agreement.type]}}</td><td class="table__td" ng-bind-html="agreement.name | preventDateBreak"></td><td class="table__td table__td--width-20">{{agreement.releaseDate | date:\'yyyy-MM-dd\'}} - {{agreement.rentEffectiveFrom | date:\'yyyy-MM-dd\'}}</td><td ng-if="agreement.conditionTags.internal" class="table__td table__td--width-25">&nbsp;</td><td ng-if="!agreement.conditionTags.internal" class="table__td table__td--width-25"><span uib-tooltip="{{agreement.user1.email}}"><u>{{agreement.user1.name}}</u></span> + <span uib-tooltip="{{agreement.user2.email}}"><u>{{agreement.user2.name}}</u></span></td><td class="table__td table__td--width-5"><button class="btn" ng-if="vm.canApprovePending" ng-click="vm.unlockPending(agreement)">P\xE5b\xF6rja</button> <a href="#" ng-if="vm.canDeletePending" class="btn--icon btn--icon-padding btn--icon-bin" ng-click="vm.deletePending(agreement)"></a></td></tr><tr ng-if="vm.agreements.length > 0"><td colspan="6"><div class="spacer-mb-large"></div><b>P\xE5g\xE5ende avtal</b><div class="spacer-mb-large"></div></td></tr><tr class="table__tr" ng-repeat="agreement in vm.agreements" ng-click="vm.selectAgreement(agreement.id)"><td class="table__td table__td--width-10">{{agreement.fancyNumber}}</td><td class="table__td table__td--no-wordbreak table__td--width-15">{{vm.FreeAgreementTypes[agreement.type]}}</td><td class="table__td" ng-bind-html="agreement.name | preventDateBreak"></td><td class="table__td table__td--width-20">{{agreement.rentEffectiveFrom | date:\'yyyy-MM-dd\'}} - {{agreement.releaseDate | date:\'yyyy-MM-dd\'}}</td><td ng-if="agreement.conditionTags.internal" class="table__td table__td--width-25">&nbsp;</td><td ng-if="!agreement.conditionTags.internal" class="table__td table__td--width-25"><span uib-tooltip="{{agreement.user1.email}}"><u>{{agreement.user1.name}}</u></span> + <span uib-tooltip="{{agreement.user2.email}}"><u>{{agreement.user2.name}}</u></span></td><td class="table__td table__td--width-5"><span ng-if="!agreement.signed && agreement.scriveId"><div class="inprocess" ng-click="$event.stopPropagation(); vm.downloadPDF(agreement.id)"></div></span></td></tr><tr ng-if="vm.signedAgreements.length > 0"><td colspan="6"><div class="spacer-mb-large"></div><b>Signerade avtal</b><div class="spacer-mb-large"></div></td></tr><tr class="table__tr" ng-repeat="agreement in vm.signedAgreements" ng-click="vm.selectAgreement(agreement.id)"><td class="table__td table__td--width-10">{{agreement.fancyNumber}}</td><td class="table__td table__td--no-wordbreak table__td--width-15">{{vm.FreeAgreementTypes[agreement.type]}}</td><td class="table__td" ng-bind-html="agreement.name | preventDateBreak"></td><td class="table__td table__td--width-20">{{agreement.releaseDate | date:\'yyyy-MM-dd\'}} - {{agreement.rentEffectiveFrom | date:\'yyyy-MM-dd\'}}</td><td ng-if="agreement.conditionTags.internal" class="table__td table__td--width-25">&nbsp;</td><td ng-if="!agreement.conditionTags.internal" class="table__td table__td--width-25"><span uib-tooltip="{{agreement.user1.email}}"><u>{{agreement.user1.name}}</u></span> + <span uib-tooltip="{{agreement.user2.email}}"><u>{{agreement.user2.name}}</u></span></td><td class="table__td table__td--width-5"><span ng-if="agreement.signed"><div class="checkmark" ng-click="$event.stopPropagation(); vm.downloadPDF(agreement.id)"></div></span></td></tr></table></div>');
$templateCache.put('resources/agreement/free/thank-you.html','<div class="container"><h1>Tack!</h1><p>Det p\xE5b\xF6rjade avtalet kommer nu handl\xE4ggas av Hyresg\xE4stf\xF6reningen, d\xE4refter kan du b\xF6rja skriva avtalet</p></div>');
$templateCache.put('resources/blocking/new/new.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><div class="spacer-mt-large"><hy-wizard wz-submit="vm.save()" goto-next="vm.gotoNext" is-current-step-valid="vm.isCurrentStepValid" submit-text="Skapa" header-text="Skapa strandning" submit-disabled="vm.saving"><div class="text-center spacer-mt-large" ng-show="vm.saving"><hy-progress-circular size="60px"></hy-progress-circular></div><hy-wizard-step heading="V\xE4lj fastighetsbest\xE5nd - Distrikt" heading-short="Distrikt" heading-step-number="1" name="district" is-valid="vm.districts.length > 0 &amp;&amp; vm.districtForm.$valid &amp;&amp; vm.atleastOneDistrictSelected"><ng-include src="\'resources/blocking/new/partials/_district.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj fastighetsbest\xE5nd - Fastigheter" heading-short="Fastigheter" heading-step-number="2" name="properties" is-valid="vm.properties.length > 0 &amp;&amp; vm.propertiesForm.$valid &amp;&amp; vm.atleastOnePropertySelected"><ng-include src="\'resources/blocking/new/partials/_properties.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj fastighetsbest\xE5nd - L\xE4genheter" heading-short="L\xE4genheter" heading-step-number="3" name="apartments" is-valid="vm.apartments.length > 0 &amp;&amp; vm.apartmentsForm.$valid &amp;&amp; vm.atleastOneApartmentSelected"><ng-include src="\'resources/blocking/new/partials/_apartments.html\'"></ng-include></hy-wizard-step></hy-wizard></div></div>');
$templateCache.put('resources/blocking/edit/edit-name-modal.html','<form name="vm.blockingForm" ng-submit="vm.blockingForm.$valid &amp;&amp; vm.save()" novalidate><div class="modal-header modal-header--small"><h4 class="text-margin-reset">\xC4ndra namn p\xE5 strandning</h4></div><div class="modal-body modal-body--small" id="modal-body"><div class="form-group"><label for="inputName" class="sr-only">Namn</label> <input name="inputName" id="inputName" type="text" placeholder="{{vm.blocking.name}}" class="form-control" ng-model="vm.blocking.name" autocomplete="off"></div></div><div class="modal-footer modal-footer--small"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline spacer-mr-small" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="submit" ng-disabled="vm.saving">Spara</button></div></form>');
$templateCache.put('resources/organization/list/list.html','<div class="container"><hy-progress-circular ng-hide="vm.ready" center-on-page size="60px"></hy-progress-circular><hy-header title="Fastighets\xE4gare & ombud" buttons="vm.headerButtons" button-pane-cols="3"></hy-header><div class="row spacer-mb-small"><div class="col text-right" ng-show="vm.totalCount > vm.pageLimit"><hy-paginator get-list="vm.getOrganizations" page-offset="vm.pageOffset" page-limit="vm.pageLimit" total-count="vm.totalCount"></hy-paginator></div></div><div class="row spacer-mb-medium"><div class="col sort-container"><hy-sorter list-columns="vm.listColumns" order="vm.sortColumn" direction="vm.sortDirection" show-label="true" label-position="side"></hy-sorter></div></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">F\xF6retag</th><th class="table__th">Organisationsnummer</th><th class="table__th">Godk\xE4nd av HGF</th><th class="table__th">HGF kontor</th></tr><tr><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextOrgName" hy-enter="vm.getOrganizations(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getOrganizations(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextOrgNumber" hy-enter="vm.getOrganizations(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getOrganizations(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="dropdown dropdown--fancy-caret dropdown--fancy-caret--right dropdown--inside-th" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__filter" uib-dropdown-toggle><span class="text-normal" ng-class="{\'text-gray\': allVM.typeId === undefined}">{{ vm.approved !== undefined ? vm.statusName : \'V\xE4lj status\' }}</span></button><div class="dropdown-menu dropdown-menu--left dropdown-menu--filter" uib-dropdown-menu role="menu"><button ng-repeat="choice in vm.approvedChoices" type="button" class="dropdown-item dropdown-item--filter" ng-click="vm.approved = choice; vm.statusName = choice ? \'Ja\' : \'Nej\'; vm.getOrganizations()">{{ choice ? \'Ja\' : \'Nej\' }}</button></div></div></th><th class="table__th hidden-md-down"><div class="dropdown dropdown--fancy-caret dropdown--fancy-caret--right dropdown--inside-th" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__filter" uib-dropdown-toggle><span class="text-normal" ng-class="{\'text-gray\': allVM.typeId === undefined}">{{ vm.office !== undefined ? vm.office.name : \'V\xE4lj kontor\' }}</span></button><div class="dropdown-menu dropdown-menu--left dropdown-menu--filter dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="choice in vm.offices" type="button" class="dropdown-item dropdown-item--filter" ng-click="vm.office = choice; vm.officeId = choice.id; vm.getOrganizations()">{{ choice.name }}</button></div></div></th></tr></thead><tr class="table__tr" ng-repeat="org in vm.organizations" ng-click="vm.gotoShow(org.id)" ng-class="{\'table__tr--muted\' : !vm.ready}"><td class="table__td word-break-all">{{ org.name }}</td><td class="table__td">{{ org.organizationNumber | hyOrgNr }}</td><td class="table__td">{{ org.approved ? \'Ja\' : \'Nej\' }}</td><td class="table__td">{{ org.office.name }}</td></tr></table><div class="row spacer-mt-medium" ng-show="vm.totalCount > vm.pageLimit"><div class="col text-right"><hy-paginator get-list="vm.getOrganizations" page-offset="vm.pageOffset" page-limit="vm.pageLimit" total-count="vm.totalCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="vm.totalCount > 0 &amp;&amp; vm.totalCount <= vm.pageLimit"><div class="col text-right"><strong>Totalt: </strong>{{vm.totalCount}}</div></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="vm.totalCount === 0">Ingen fastighets\xE4gare hittades.</div></div>');
$templateCache.put('resources/organization/newedit/edit.html','<hy-progress-circular size="60px" center-on-page ng-hide="!vm.editMode || vm.ready"></hy-progress-circular><div class="container" ng-show="!vm.editMode || vm.ready"><hy-breadcrumb></hy-breadcrumb><h2><span ng-hide="vm.editMode">L\xE4gg till</span> <span ng-show="vm.editMode">Uppdatera</span> fastighets\xE4gare eller ombud</h2><form name="vm.organizationForm" ng-submit="vm.organizationForm.$valid &amp;&amp; vm.save()" class="alert alert--narrow-gray alert--squary" novalidate><div class="row"><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.organizationForm.inputCompanyName.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputCompanyName.$invalid,\r\n          }"><label for="inputCompanyName">F\xF6retagsnamn</label> <input name="inputCompanyName" id="inputCompanyName" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.name" autocomplete="off" maxlength="250" required></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.organizationForm.inputOrganizationNumber.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputOrganizationNumber.$invalid\r\n          }"><label for="inputOrganizationNumber">Organisationsnummer</label> <input name="inputOrganizationNumber" id="inputOrganizationNumber" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.organizationNumber" ng-pattern="/^\\d{6}-?\\d{4}$/" autocomplete="off" required> <small class="form-group__help">Enligt formatet 1234561234</small> <small ng-show="vm.organizationForm.inputOrganizationNumber.$error.pattern">Ange ett giltigt organisationsnummer</small></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.organizationForm.inputOrganizationNumber.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputOrganizationNumber.$invalid\r\n          }"><label for="inputHgfOfficeId"><div class="display-inline-flex"><div class="spacer-mr-xsmall">HGF kontor</div></div></label> <input name="inputHgfOfficeId" id="inputHgfOfficeId" type="hidden" ng-model="vm.selectedOffice.name"><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle>{{ vm.selectedOffice ? vm.selectedOffice.name : \'V\xE4lj mottagande kontor\' }}</button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="office in vm.offices" type="button" class="dropdown-item" ng-click="vm.organization.hgfOfficeId = office.id; vm.selectedOffice.name = office.name">{{office.name}}</button></div></div></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.organizationForm.inputPhone.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputPhone.$invalid\r\n          }"><label for="inputPhone">Telefonnummer</label> <input name="inputPhone" id="inputPhone" type="tel" class="form-control form-control form-control--narrow form-control--gray-border" ng-pattern="/^[0-9]+$/" ng-model="vm.organization.phone" autocomplete="off"> <small class="form-group__help">Exempel: 0701112233</small> <small ng-show="vm.organizationForm.inputPhone.$error.pattern">Ange ett giltigt telefonnummer</small></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.organizationForm.inputStreetAddress.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputStreetAddress.$invalid\r\n          }"><label for="inputStreetAddress">Gatuadress</label> <input name="inputStreetAddress" id="inputStreetAddress" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.streetAddress" autocomplete="off" maxlength="40"></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.organizationForm.inputPostalCode.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputPostalCode.$invalid\r\n          }"><label for="inputPostalCode">Postnummer</label> <input name="inputPostalCode" id="inputPostalCode" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.postalCode" autocomplete="off" maxlength="5"></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.organizationForm.inputCity.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputCity.$invalid\r\n          }"><label for="inputCity">Ort</label> <input name="inputCity" id="inputCity" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.city" autocomplete="off" maxlength="40"></div></div></div><div class="row spacer-mt-medium"><div class="col text-right"><a class="link--turquoise link--no-underline spacer-mr-medium" href="/organizations/{{vm.organization.id}}">Avbryt</a><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><button type="submit" class="btn" ng-disabled="!Auth.canDo(\'editOrganization\') || vm.saving">Spara</button></div></div></form><div class="row"><div class="col-6 col-lg-4"><div ng-show="vm.editMode"><hr class="hr--pink"><h4 class="text-danger">Ta bort organisation</h4><p>N\xE4r du tar bort din organisation, finns det ingen \xE5terv\xE4ndo. Var s\xE4ker.</p><button class="btn btn--secondary" ng-click="vm.delete()">Ta bort organisation</button></div></div></div></div>');
$templateCache.put('resources/help/show/hgfLoginHelp.html','<div class="container"><div class="row"><div class="col-12 col-lg-6"><h1>Inloggningsfel</h1><p>Ditt konto verkar tyv\xE4rr inte ha r\xE4tt beh\xF6righet f\xF6r att kunna anv\xE4nda F\xF6rhandlarportalen. Beh\xF6righeten styrs via ditt ADDin och kan best\xE4llas via IT Servicedesk. Om du vet att du har blivit tilldelad beh\xF6righet av Servicedesk men \xE4nd\xE5 hamnat h\xE4r, ta kontakt med dem f\xF6r hj\xE4lp att \xE5tg\xE4rda felet. Om du \xE4r os\xE4ker p\xE5 om du har beh\xF6righet till Portalen, prata med din chef. Det \xE4r hon/han som \xE4r ansvarig best\xE4llare.</p></div></div></div>');
$templateCache.put('resources/real-estate/list/ownersList.html','<div class="container"><hy-header title="\xC4gare"></hy-header><div class="row"><div class="col-12"><hy-tableau id="tableau" limit="30" columns="vm.tableColumns" f-get-data="vm.tableFGetData" f-row-click="vm.gotoOwner" f-get-context-info="vm.getContextInfo" use-url="true"></hy-tableau></div></div></div>');
$templateCache.put('resources/real-estate/list/propertyList.html','<div class="container"><h1>{{vm.header}}</h1><hr class="hr--full-width spacer-mt-reset hr--grey-light"><hy-checkbox ng-if="vm.isHgfUser" title="Bara i mina f\xF6rhandlingar" model="vm.onlyInMyNegotiations" ticked="vm.checkboxTicked()"></hy-checkbox><div class="row spacer-mt-small"><div class="col-12"><hy-tableau id="tableau" columns="vm.tableColumns" attributes="vm.tableAttributes" filters-override="vm.filters" f-get-data="vm.tableFGetData" f-postprocess-data="vm.tableFPostprocessData" f-row-click="vm.gotoProperty" use-url="true" f-get-context-info="vm.getContextInfo" info-text="Fastigheter \xE4r nya och inte \xE4nnu kopplade till fastighetsregistret" limit="30"></hy-tableau></div></div></div>');
$templateCache.put('resources/organization/show/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-show="vm.ready"><div class="row"><div class="col"><hy-breadcrumb></hy-breadcrumb><h1 class="text-margin-reset">{{vm.organization.name}}</h1></div></div><hr class="hr--grey-light hr--full-width"><h3>F\xF6retagsinformation</h3><div class="alert alert--narrow-gray alert--squary"><div class="row spacer-mb-medium"><div class="col-6 col-lg-3"><hy-info title="F\xF6retag" content="vm.organization.name"></hy-info></div><div class="col-6 col-lg-3"><hy-info title="Organisationsnummer" content="vm.organization.organizationNumber | hyOrgNr"></hy-info></div><div class="col-6 col-lg-3"><hy-info title="HGF kontor" content="vm.organization.office.name"></hy-info></div><div ng-if="Auth.canDo(\'editOrganization\')" class="col-6 col-lg-3 text-right"><a href="/organizations/{{vm.organization.id}}/edit" class="btn--icon btn--icon-pen"><span class="sr-only">Redigera</span></a></div></div><div class="row"><div class="col-6 col-lg-3"><hy-info title="Gatuadress" content="vm.organization.streetAddress"></hy-info></div><div class="col-6 col-lg-3 info"><hy-info title="Postnummer" content="vm.organization.postalCode"></hy-info></div><div class="col-6 col-lg-3 info"><hy-info title="Ort" content="vm.organization.city"></hy-info></div><div class="col-6 col-lg-3 info"><hy-info title="Telefon" content="vm.organization.phone"></hy-info></div></div></div><div class="row"><div class="col-12"><list-user-by-organization organization="vm.organization" found-admin="vm.foundAdmin"></list-user-by-organization></div></div></div>');
$templateCache.put('resources/blocking/show/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready &amp;&amp; vm.readyNegotiation"></hy-progress-circular><div class="container" ng-show="vm.ready &amp;&amp; vm.readyNegotiation"><hy-breadcrumb></hy-breadcrumb><div class="display-flex space-between spacer-mt-xlarge spacer-mb-xxxlarge"><div class="spacer-mr-small"><h1 class="text-margin-reset"><span class="text-success" ng-class="{\'text-gray\' : (vm.negotiation.isFinished)}">{{vm.blocking.sequenceNumber}}. </span>Strandning</h1><h3 class="text-margin-reset">{{vm.negotiation.counterpart.name}} och Hyresg\xE4stf\xF6reningen</h3></div><div><a href="/negotiations/{{vm.negotiation.id}}?view=agreements" class="btn btn--arrow-left spacer-mb-medium">Till \xF6verenskommelser</a></div></div><div ng-if="vm.readyNegotiation && !vm.skipRentlist"><ng-include class="display-block animate-if" ng-if="vm.editApartmentMode" src="\'resources/blocking/show/apartment/edit/edit.html\'"></ng-include><ng-include src="\'resources/blocking/show/apartment/list/list.html\'"></ng-include></div><div ng-if="vm.readyNegotiation && vm.skipRentlist"><ng-include class="display-block animate-if" ng-if="vm.editPropertiesMode" src="\'resources/blocking/show/properties/edit/edit.html\'"></ng-include><ng-include src="\'resources/blocking/show/properties/list/list.html\'"></ng-include></div></div>');
$templateCache.put('resources/search/agreements/search.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><hy-header title="\xD6verenskommelse/avtal" tabs="vm.tablist"></hy-header><ng-include src="\'resources/search/agreements/\' + vm.currentSubpage + \'/list.html\'"></ng-include></div>');
$templateCache.put('resources/real-estate/show/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-show="vm.ready"><hy-breadcrumb></hy-breadcrumb><div class="row"><div class="col-12"><h1 class="spacer-mt-xlarge spacer-mb-large">{{vm.property.propertyDesignation}}, {{vm.property.municipality}}</h1><div ng-if="!vm.property.active" class="red-text">Inaktiv fastighet</div></div><div class="col-12"><div class="alert alert--narrow-gray alert--squary"><div class="row" style="margin-top: -20px;"><div class="col-4"><div ng-if="vm.property.address.street"><h4 class="text-margin-bottom-reset text-transform-none">Adress</h4><hy-array-display model="vm.formattedAddresses"></hy-array-display></div><div ng-if="vm.property.address.postalCode"><h4 class="text-margin-bottom-reset text-transform-none">Postnummer</h4>{{vm.property.address.postalCode}}</div><div ng-if="vm.property.regionId"><h4 class="text-margin-bottom-reset text-transform-none">Region</h4>{{vm.property.regionId | regionTypeToDescription}}</div><div ng-if="vm.property.municipality"><h4 class="text-margin-bottom-reset text-transform-none">Kommun</h4>{{vm.property.municipality}}</div><div ng-if="vm.property.district"><h4 class="text-margin-bottom-reset text-transform-none">Distrikt</h4>{{vm.property.district}}</div><div><h4 class="text-margin-bottom-reset text-transform-none">Hyresv\xE4rd</h4><hy-array-display model="vm.formattedLandlords"></hy-array-display></div></div><div class="col-4"><div><h4 class="text-margin-bottom-reset text-transform-none">Fastighets\xE4gare</h4><hy-array-display model="vm.formattedOwners"></hy-array-display></div><div ng-if="vm.property.builtYear"><h4 class="text-margin-bottom-reset text-transform-none">Byggnads\xE5r</h4>{{vm.property.builtYear == 0 ? \'&mdash;\' : vm.property.builtYear}}</div><div ng-if="vm.property.valuationYear"><h4 class="text-margin-bottom-reset text-transform-none">V\xE4rde\xE5r</h4>{{vm.property.valuationYear === 0 ? \'&mdash;\' : vm.property.valuationYear}}</div><div ng-if="vm.property.renovatedYear"><h4 class="text-margin-bottom-reset text-transform-none">Ombyggnads\xE5r</h4>{{vm.property.renovatedYear === 0 ? \'&mdash;\' : vm.property.renovatedYear}}</div><div ng-if="vm.property.propertyProcedures && vm.property.propertyProcedures.length > 0"><h4 class="text-margin-bottom-reset text-transform-none">F\xF6rhandlingsordning</h4><div ng-repeat="procedure in vm.property.propertyProcedures">{{procedure.negotiationAgreementCode | hyNegotiationAgreementTypes:vm.property.hasNegotiationAgreement}}</div></div></div><div class="col-4"><h4 class="text-margin-bottom-reset text-transform-none">Registrerade l\xE4genheter</h4>{{vm.property.apartmentSummary.count}} st<h4 class="text-margin-bottom-reset text-transform-none">L\xE4genheter i p\xE5g\xE5ende f\xF6rhandling</h4>{{vm.property.apartmentSummary.inNegotiation}} st<h4 class="text-margin-bottom-reset text-transform-none">L\xE4genheter med f\xF6rhandlingsordning</h4>{{vm.property.numNegotiatedApartments ? vm.property.numNegotiatedApartments + \' st\': \'&mdash;\'}}<h4 class="text-margin-bottom-reset text-transform-none">Strandade l\xE4genheter</h4>{{vm.property.apartmentSummary.blocked}} st<h4 class="text-margin-bottom-reset text-transform-none">L\xE4genheter med presumtionshyra {{vm.statusCount.i ? \'(Investeringsst\xF6d)\': \'\'}}</h4>{{vm.statusCount.p}} st<h4 ng-if="vm.property.nhStats" class="text-margin-bottom-reset text-transform-none">Normhyra</h4><span ng-if="vm.property.nhStats" title="Medel {{vm.property.nhStats.avg}}"><b>-91: </b>{{vm.property.nhStats.min91}}-{{vm.property.nhStats.max91}}<br><b>-78: </b>{{vm.property.nhStats.min78}}-{{vm.property.nhStats.max78}}<br><b>-12: </b>{{vm.property.nhStats.min12}}-{{vm.property.nhStats.max12}}</span></div></div></div></div></div><div class="row"><div class="col-12 col-md-10 col-lg-8 tabs-container"><hy-tabs tablist="vm.tablist"></hy-tabs></div></div><hr class="hr--full-width hr--turquoise spacer-mt-reset" ng-class="{\'border--white\': !vm.ready}"><ng-include src="\'resources/real-estate/show/\' + vm.currentSubpage + \'/show.html\'" ng-show="vm.ready"></ng-include></div>');
$templateCache.put('resources/requested-accounts/organizations/list.html','<div class="container" ng-controller="ListOrganizationsPendingCtrl as vm"><hy-breadcrumb></hy-breadcrumb><hy-progress-circular ng-hide="vm.ready" center-on-page size="60px"></hy-progress-circular><div class="row spacer-mb-small"><div class="col text-right" ng-show="vm.totalCount > vm.pageLimit"><hy-paginator get-list="vm.getOrganizations" page-offset="vm.pageOffset" page-limit="vm.pageLimit" total-count="vm.totalCount"></hy-paginator></div></div><div class="row spacer-mb-medium"><div class="col display-flex space-between"><h3 class="text-margin-reset">Fastighets\xE4gare &amp; Ombud lista</h3><hy-sorter list-columns="vm.listColumns" order="vm.sortColumn" direction="vm.sortDirection" show-label="true" label-position="side"></hy-sorter></div></div><table class="table table--margin-reset"><tr class="table__header"><th class="table__th">Kontor<div class="display-flex spacer-mt-small"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextOfficeName" hy-enter="vm.getOrganizations(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getOrganizations(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th">F\xF6retagsnamn<div class="display-flex spacer-mt-small"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextOrgName" hy-enter="vm.getOrganizations(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getOrganizations(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th">Organisationsnummer<div class="display-flex spacer-mt-small"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextOrgNumber" hy-enter="vm.getOrganizations(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getOrganizations(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th">F\xF6rnamn<div class="display-flex spacer-mt-small"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextFirstName" hy-enter="vm.getOrganizations(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getOrganizations(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th">Efternamn<div class="display-flex spacer-mt-small"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextLastName" hy-enter="vm.getOrganizations(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getOrganizations(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th">E-post<div class="display-flex spacer-mt-small"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextEmail" hy-enter="vm.getOrganizations(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getOrganizations(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th></tr><tr class="table__tr" ng-repeat="org in vm.organizations" ng-click="vm.gotoShow(org.id)" ng-class="{\'table__tr--muted\' : !vm.ready}"><td class="table__td">{{org.officeName}}</td><td class="table__td word-break-all">{{ org.name }}</td><td class="table__td">{{ org.organizationNumber | hyOrgNr }}</td><td class="table__td">{{ org.firstname }}</td><td class="table__td">{{ org.lastname }}</td><td class="table__td">{{ org.email }}</td></tr></table><div class="row spacer-mt-medium" ng-show="vm.totalCount > vm.pageLimit"><div class="col text-right"><hy-paginator get-list="vm.getOrganizations" page-offset="vm.pageOffset" page-limit="vm.pageLimit" total-count="vm.totalCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="vm.totalCount > 0 &amp;&amp; vm.totalCount <= vm.pageLimit"><div class="col text-right"><strong>Totalt: </strong>{{vm.totalCount}}</div></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="vm.totalCount === 0">Ingen fastighets\xE4gare hittades.</div></div>');
$templateCache.put('resources/validation/partials/_additional-info.html','<div class="container spacer-mt-medium"><div class="row"><h4>Statistics</h4><table class="table table--margin-reset"><tr><td class="table__td table__td--width-10 table__td--minimalistic" align="right">Total:</td><td class="table__td table__td--minimalistic">{{vm.status.total}}</td></tr><tr><td class="table__td table__td--width-10 table__td--minimalistic" align="right">Errors:</td><td class="table__td table__td--minimalistic">{{vm.status.errors}}</td></tr><tr ng-if="vm.status.warnings > 0"><td class="table__td table__td--width-10 table__td--minimalistic" align="right">Warnings:</td><td class="table__td table__td--minimalistic">{{vm.status.warnings}}</td></tr><tr ng-if="vm.showDetailedInfo"><td class="table__td table__td--width-10 table__td--minimalistic"><h5>Details</h5></td></tr><tr ng-if="vm.showDetailedInfo"><td class="table__td table__td--width-10 table__td--minimalistic" align="right">Step:</td><td class="table__td table__td--minimalistic">{{vm.status.phase}}</td></tr><tr ng-if="vm.showDetailedInfo"><td class="table__td table__td--width-10 table__td--minimalistic" align="right">Information:</td><td class="table__td table__td--minimalistic">{{vm.status.comment}}<uib-progressbar type="turquoise" value="vm.status.progress" ng-show="vm.status.showProgress" ng-class="\'progress--square\'"></uib-progressbar></td></tr></table></div></div>');
$templateCache.put('resources/validation/partials/_already_assigned.html','<div><div class="alert alert--danger alert--hide-border alert--squary"><span>L\xE4genheter som redan finns i \xF6verenskommelse.</span></div><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic">Radnummer</th><th class="table__th table__th--minimalistic">Fastighetsbeteckning</th><th class="table__th table__th--minimalistic">Kommun</th><th class="table__th table__th--minimalistic">Nationellt<br>l\xE4genhetsnr</th><th class="table__th table__th--minimalistic">L\xE4genhetsnr</th><th class="table__th table__th--minimalistic">Gatuadress</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="row in vm.status.alreadyInAgreement"><td class="table__td table__td--minimalistic">{{row.rowNumber| hyNextNumber}}</td><td class="table__td table__td--minimalistic">{{row.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{row.municipality}}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{row.nationalNo}}</td><td class="table__td table__td--minimalistic">{{row.ownerNo}}</td><td class="table__td table__td--minimalistic">{{row.streetAddress}}</td></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.status.numAlreadyInAgreement > 10"><td class="table__td table__td--width-25 table__td--minimalistic">...</td><td class="table__td table__td--minimalistic text-danger">F\xF6r m\xE5nga {{vm.status.numAlreadyInAgreement}} l\xE4genheter.</td><td></td><td></td><td></td></tr></table></div>');
$templateCache.put('resources/validation/partials/_duplicates.html','<div><h3>Duplicates</h3><div class="alert alert--danger alert--hide-border alert--squary"><span>Duplicates found</span></div><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic">Radnummer</th><th class="table__th table__th--minimalistic">Fastighetsbeteckning</th><th class="table__th table__th--minimalistic">Kommun</th><th class="table__th table__th--minimalistic">Nationellt<br>l\xE4genhetsnr</th><th class="table__th table__th--minimalistic">L\xE4genhetsnr</th><th class="table__th table__th--minimalistic">Gatuadress</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="row in vm.status.duplicates"><td class="table__td table__td--minimalistic">{{row.rowNumber| hyNextNumber}}</td><td class="table__td table__td--minimalistic">{{row.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{row.municipality}}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{row.nationalNo}}</td><td class="table__td table__td--minimalistic">{{row.ownerNo}}</td><td class="table__td table__td--minimalistic">{{row.streetAddress}}</td></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.status.numDuplicates > 10"><td class="table__td table__td--minimalistic">...</td><td class="table__td table__td--minimalistic text-danger">F\xF6r m\xE5nga ({{vm.status.numDuplicates}}) fel hittade i filen</td></tr></table></div>');
$templateCache.put('resources/validation/partials/_examine-first-line-body.html','<div class="row"><div class="col-8"><table class="table table--margin-reset"><tr class="table__header"><th class="table__th">Ben\xE4mning</th><th class="table__th">Inl\xE4st</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="(colTitle, colValue) in vm.status.firstRow"><td class="table__td table__td--width-40 table__td--minimalistic">{{ vm.getColumnTitle(colTitle) }}</td><td class="table__td table__td--minimalistic">{{colValue}}</td></tr></table></div><div class="col-4"><table class="table table--margin-reset"><tr class="table__header"><th class="table__th">Information</th></tr><tr class="table__tr table__tr--minimalistic table__tr--border-reset"><td class="table__td table__td--width-40 table__td--minimalistic"><p class="text-margin-top-reset">Bredvid ser du en f\xF6rhandsvisning av f\xF6rsta raden i din uppladdade fil. Om f\xF6rhandsvisningen inte st\xE4mmer, justera filen och g\xE5 tillbaka i ett steg f\xF6r att ladda upp filen p\xE5 nytt.</p><div class="alert alert--success alert--hide-border alert--squary alert--max-width-250"><h4 class="text-margin-top-reset">Inl\xE4ses fr\xE5n listan:</h4><div ng-hide="vm.hideApartmentCounter"><strong>{{vm.status.summary.numApartments}}</strong> l\xE4genheter f\xF6rdelat p\xE5<br></div><strong>{{vm.status.summary.numProperties}}</strong> fastigheter bel\xE4gna i<br><strong>{{vm.status.summary.numMunicipalities}}</strong> kommuner</div></td></tr></table></div></div>');
$templateCache.put('resources/validation/partials/_failed-rows.html','<div><h3>Valideringsfel</h3><div class="alert alert--danger alert--hide-border alert--squary"><span>Datat i filen \xE4r inte i enligt \xF6nskat format, felen visas i listan nedan. Fels\xF6k filen och pr\xF6va att ladda upp igen.</span></div><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic">Radnummer</th><th class="table__th table__th--minimalistic">Kolumn</th><th class="table__th table__th--minimalistic">Value</th><th class="table__th table__th--minimalistic">Fel</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="row in vm.status.failedRows"><td class="table__td table__td--width-25 table__td--minimalistic">{{row.rowNumber| hyNextNumber}}</td><td class="table__td table__td--width-25 table__td--minimalistic">{{row.column}}</td><td class="table__td table__td--width-25 table__td--minimalistic">{{row.value}}</td><td class="table__td table__td--width-25 table__td--minimalistic">{{row.info}}</td></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.status.numFailedRows > 10"><td class="table__td table__td--width-25 table__td--minimalistic">...</td><td class="table__td table__td--minimalistic text-danger">F\xF6r m\xE5nga fel hittade i filen.</td></tr></table></div>');
$templateCache.put('resources/validation/partials/_failure.html','<style>.scrollable-box {\r\n      width: 600px;\r\n      height: 150px;\r\n      border: 1px solid #ccc;\r\n      padding: 10px;\r\n      overflow-y: scroll;\r\n      background-color: #f9f9f9;\r\n  }</style><div ng-if="vm.status.failedPhase">Misslyckad fas: {{ vm.status.failedPhase.key }}<br><small>{{ vm.status.failedPhase.instructions }}</small><div ng-if="vm.status.failure"><h4>Fel information</h4><div class="scrollable-box">{{vm.status.failure}}</div></div></div>');
$templateCache.put('resources/validation/partials/_missing-columns.html','<div class="alert alert--danger alert--hide-border alert--squary"><span>F\xF6ljande obligatoriska<br>kolumner saknas</span></div><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic">Kolumn</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="item in vm.status.missingMandatoryColumns"><td class="table__td table__td--minimalistic">{{item}}</td></tr></table>');
$templateCache.put('resources/validation/partials/_missing_apartments_from_agreement.html','<div><h3>L\xE4genheter saknas</h3><div class="alert alert--danger alert--hide-border alert--squary"><span>F\xF6ljande l\xE4genheter som omfattas av f\xF6rhandlingen saknas i hyreslistan. Hyreslistan m\xE5ste inneh\xE5lla samma l\xE4genheter som ing\xE5r i \xF6verenskommelse. G\xE5 igenom filen och pr\xF6va att ladda upp igen.</span></div><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic">Fastighetsbeteckning</th><th class="table__th table__th--minimalistic">Kommun</th><th class="table__th table__th--minimalistic">Nationellt<br>l\xE4genhetsnr</th><th class="table__th table__th--minimalistic">L\xE4genhetsnr</th><th class="table__th table__th--minimalistic">Gatuadress</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="row in vm.status.missingApartmentsFromAgreement"><td class="table__td table__td--width-25 table__td--minimalistic">{{row.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{row.municipality}}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{row.nationalNo}}</td><td class="table__td table__td--minimalistic">{{row.ownerNo}}</td><td class="table__td table__td--minimalistic">{{row.streetAddress}}</td></tr><tr class="table__tr table__tr--minimalistic"><td class="table__td table__td--minimalistic text-danger" colspan="3">Visar endast de {{vm.status.missingApartmentsFromAgreement.length}} f\xF6rsta raderna. Totalt saknas det <strong>{{vm.status.numMissingApartmentsFromAgreement}}</strong> l\xE4genheter.</td></tr></table></div>');
$templateCache.put('resources/validation/partials/_misspelled-addresses.html','<div><p>Den angivna adressen finns inte registrerad p\xE5 den fastigheten, kanske har du stavat fel?</p><table class="table table--margin-b-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic table__td--width-25">Radnummer</th><th class="table__th table__th--minimalistic">Fastighetsbeteckning</th><th class="table__th table__th--minimalistic">Adress (gata / nr)</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="row in vm.status.misspelledAddresses"><td class="table__td table__td--minimalistic">{{row.rowNumber| hyNextNumber}}</td><td class="table__td table__td--minimalistic">{{row.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{row.street}} {{row.streetNumber}}</td></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.status.numMisspelledAddresses > 10"><td class="table__td table__td--minimalistic">...</td><td class="table__td table__td--minimalistic text-danger">F\xF6r m\xE5nga ({{vm.status.numMisspelledAddresses}}) fel hittade i filen</td><td></td></tr></table></div>');
$templateCache.put('resources/validation/partials/_misspelled-municipalities.html','<div><p>Misspelled municipalities, kanske har du stavat fel?</p><table class="table table--margin-b-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic table__td--width-25">Radnummer</th><th class="table__th table__th--minimalistic">Kommun</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="row in vm.status.misspelledMunicipalities"><td class="table__td table__td--minimalistic">{{row.rowNumber| hyNextNumber}}</td><td class="table__td table__td--minimalistic">{{row.municipality}}</td></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.status.numMisspelledMunicipalities > 10"><td class="table__td table__td--minimalistic">...</td><td class="table__td table__td--minimalistic text-danger">F\xF6r m\xE5nga ({{vm.status.numMisspelledMunicipalities}}) fel hittade i filen</td><td></td></tr></table></div>');
$templateCache.put('resources/validation/partials/_misspelled-property-designations.html','<div><p>Misspelled Fastighetsbeteckning, kanske har du stavat fel?</p><table class="table table--margin-b-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic table__td--width-25">Radnummer</th><th class="table__th table__th--minimalistic">Fastighetsbeteckning</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="row in vm.status.misspelledPropertyDesignations"><td class="table__td table__td--minimalistic">{{row.rowNumber| hyNextNumber}}</td><td class="table__td table__td--minimalistic">{{row.propertyDesignation}}</td></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.status.numMisspelledPropertyDesignations > 10"><td class="table__td table__td--minimalistic">...</td><td class="table__td table__td--minimalistic text-danger">F\xF6r m\xE5nga ({{vm.status.numMisspelledPropertyDesignations}}) fel hittade i filen</td><td></td></tr></table></div>');
$templateCache.put('resources/validation/partials/_multiple-errors.html','<div><p>Det finns flera fel i filen</p><ng-include src="\'resources/validation/partials/_additional-info.html\'"></ng-include></div>');
$templateCache.put('resources/validation/partials/_not-existing.html','<div><p></p><p>Den angivna fastighetsbeteckningen och kommun finns inte registrerad.</p><ul><li>Kontrollera att stavningen \xE4r korrekt.</li><li>Kontrollera att fastigheten har ett avtal om f\xF6rhandlingsordning</li></ul>Kontakta Hyresg\xE4stf\xF6reningen om ni avser att teckna f\xF6rhandlingsordning f\xF6r fastigheten (systemet kommer att ignorera fastigheten)<p></p><table class="table" class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic table__td--width-25">Radnummer</th><th class="table__th table__th--minimalistic">Kommun</th><th class="table__th table__th--minimalistic">Fastighetsbeteckning</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="row in vm.status.notExistingApartments"><td class="table__td table__td--minimalistic">{{row.rowNumber| hyNextNumber}}</td><td class="table__td table__td--minimalistic">{{row.municipality}}</td><td class="table__td table__td--minimalistic">{{row.propertyDesignation}}</td></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.status.numNotExistingApartments > 10"><td class="table__td table__td--minimalistic">...</td><td class="table__td table__td--minimalistic text-danger">F\xF6r m\xE5nga ({{vm.status.numNotExistingApartments}}) fel hittade i filen</td></tr></table></div>');
$templateCache.put('resources/validation/partials/_nulls.html','<div><h3>NULLS</h3><div class="alert alert--danger alert--hide-border alert--squary"><span>Nulls found</span></div><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic table__td--width-25">Radnummer</th><th class="table__th table__th--minimalistic">Kolumn</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="row in vm.status.nulls"><td class="table__td table__td--minimalistic">{{row.rowNumber| hyNextNumber}}</td><td class="table__td table__td--minimalistic">{{row.kolumn}}</td></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.status.numNulls > 10"><td class="table__td table__td--minimalistic">...</td><td class="table__td table__td--minimalistic text-danger">F\xF6r m\xE5nga ({{vm.status.numNulls}}) fel hittade i filen</td></tr></table></div>');
$templateCache.put('resources/requested-accounts/users/list.html','<div ng-controller="ListUsersPendingCtrl as vm"><hy-progress-circular ng-hide="vm.ready" center-on-page size="60px"></hy-progress-circular><div class="row"><div class="col sort-container"><hy-sorter list-columns="vm.listColumns" order="vm.sortColumn" direction="vm.sortDirection" show-label="true" label-position="side"></hy-sorter></div></div><div class="row spacer-mt-small spacer-mb-small"><div class="col text-right" ng-show="vm.totalCount > vm.pageLimit"><hy-paginator get-list="vm.getUsers" page-offset="vm.pageOffset" page-limit="vm.pageLimit" total-count="vm.totalCount"></hy-paginator></div></div><table class="table table--margin-b-reset spacer-mt-large"><tr class="table__header"><th class="table__th">F\xF6rnamn<div class="display-flex spacer-mt-small"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextFirstName" hy-enter="vm.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th">Efternamn<div class="display-flex spacer-mt-small"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextLastName" hy-enter="vm.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th">E-post<div class="display-flex spacer-mt-small"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextEmail" hy-enter="vm.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th">Skapad</th></tr><tr class="table__tr" ng-repeat="user in vm.users" ng-click="vm.gotoShow(user.id)" ng-class="{\'table__tr--muted\' : !vm.ready}"><td class="table__td">{{ user.firstname }}</td><td class="table__td">{{ user.lastname }}</td><td class="table__td">{{ user.email }}</td><td class="table__td">{{ user.createdAt | date : \'yyyy-MM-dd\' }}</td></tr></table><div class="row spacer-mt-medium" ng-show="vm.totalCount > vm.pageLimit"><div class="col text-right"><hy-paginator get-list="vm.getUsers" page-offset="vm.pageOffset" page-limit="vm.pageLimit" total-count="vm.totalCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="vm.totalCount > 0 &amp;&amp; vm.totalCount <= vm.pageLimit"><div class="col text-right"><strong>Totalt: </strong>{{vm.totalCount}}</div></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="vm.totalCount === 0">Ingen anv\xE4ndare hittades.</div></div>');
$templateCache.put('resources/user/edit/edit-user-modal.html','<style>/* TODO: move to fe-common when apartment selector 2.0 will be done */\r\n  .btn--tiny {\r\n    width: 18px;\r\n    height: 18px;\r\n    padding: 0 !important;\r\n    line-height: 18px;\r\n    margin-right: 12px;\r\n    margin-left: 12px;\r\n  }</style><div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">Uppdatera anv\xE4ndare</h2></div><form name="vm.userForm" novalidate><div class="modal-body modal-body--medium" id="modal-body"><hy-tabcontrol tablist="vm.tabs" current-tab="vm.currentTab"></hy-tabcontrol><div ng-show="vm.currentTab === \'main\'"><div class="row"><div class="col-6 col-lg-6"><div class="form-group" ng-class="{\r\n                \'form-group--error\': (vm.userForm.inputFirstname.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputFirstname.$invalid,\r\n              }"><label for="inputFirstname">F\xF6rnamn</label> <input name="inputFirstname" id="inputFirstname" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.firstname" autocomplete="off" maxlength="32" ng-disabled="!Auth.canDo(\'editUser\')" required></div></div><div class="col-6 col-lg-6"><div class="form-group" ng-class="{\r\n                \'form-group--error\': (vm.userForm.inputLastname.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputLastname.$invalid\r\n              }"><label for="inputLastname">Efternamn</label> <input name="inputLastname" id="inputLastname" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.lastname" autocomplete="off" maxlength="32" ng-disabled="!Auth.canDo(\'editUser\')" required></div></div></div><div class="row"><div class="col-6 col-lg-6"><div class="form-group" ng-class="{\r\n                \'form-group--error\': (vm.userForm.inputEmail.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputEmail.$invalid\r\n              }"><label for="inputEmail">E-post</label> <input name="inputEmail" id="inputEmail" type="email" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.email" maxlength="40" ng-disabled="!Auth.canDo(\'editUserEmail\')"></div></div><div class="col-6 col-lg-6"><div class="form-group" ng-class="{\r\n                \'form-group--error\': (vm.userForm.inputPhone.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputPhone.$invalid\r\n              }"><label for="inputPhone">Telefonnummer</label> <input name="inputPhone" id="inputPhone" type="tel" class="form-control form-control--narrow form-control--gray-border" ng-pattern="/^[\\+]?[(]?[0-9]{3}[)]?[-\\s\\.]?[0-9]{3}[-\\s\\.]?[0-9]{4,6}$/im" ng-model="vm.user.phone" maxlength="32" autocomplete="off" ng-disabled="!Auth.canDo(\'editUser\')"> <small>Exempel: 0701112233 eller +46701112233</small><br><small ng-show="vm.userForm.inputPhone.$error.pattern">Ange ett giltigt telefonnummer</small></div></div></div></div><div ng-show="vm.currentTab === \'organization\'"><div class="row"><div class="col spacer-mb-medium"><div class="row spacer-margin-reset"><input class="form-control" placeholder="S\xF6k efter organisation" name="orgFilter" ng-model="vm.inputTextOrgName" id="orgFilter" ng-change="vm.orgAfterType()" hy-enter="$event.preventDefault()"></div><hy-progress-circular ng-if="vm.loading" size="30px"></hy-progress-circular><div class="scroller-selector dropdown-menu--filter scroller-selector--modal"><table class="table"><thead class="table__header sticky"><tr><th class="table__th table__th--width-10"></th><th class="table__th table__th--width-5">Godk\xE4nd</th><th class="table__th table__th--width-25">F\xF6retag</th><th class="table__th table__th--width-20">Organisationsnummer</th><th class="table__th table__th--width-20">Telefonnummer</th><th class="table__th table__th--width-20">Gatuadress</th></tr></thead><tr class="table__tr" ng-repeat="organization in vm.organizations"><td><button class="btn btn--tiny" ng-disabled="!Auth.canDo(\'editUser\')" ng-click="$event.preventDefault(); vm.selectOrganization(organization)"><b>+</b></button></td><td><span ng-if="organization.approved">ja</span> <span ng-if="!organization.approved" class="red-text">nej</span></td><td class="table__td"><div>{{ organization.name }}</div></td><td class="table__td"><div>{{ organization.organizationNumber }}</div></td><td class="table__td"><div>{{ organization.phone }}</div></td><td class="table__td"><div>{{ organization.streetAddress }}</div></td></tr></table></div><div class="spacer-mt-medium" ng-if="vm.selectedOrganizations"><table class="table"><tr ng-repeat="organization in vm.selectedOrganizations"><td class="table__td table__td--width-10"><button ng-disabled="!Auth.canDo(\'editUser\')" class="btn btn--secondary btn--tiny" ng-click="$event.preventDefault(); vm.deselectOrganization(organization)"><b>-</b></button></td><td class="table__td"><div>{{ organization.name }}, {{ organization.organizationNumber }}</div></td><td class="table__td table__td--width-30"><hy-combobox items="vm.roles" id-property="id" text-property="text" current-item="organization.role"></hy-combobox></td></tr></table></div></div></div></div><div ng-show="vm.currentTab === \'permissions\'"><hy-checkbox title="Inaktiv profil" name="chkInactiveProfile" disabled="!Auth.canDo(\'editUser\')" model="vm.user.disabled"></hy-checkbox><br><hy-checkbox title="ePost notiser fr\xE5n Portalen" name="chkPortalNotifications" disabled="!Auth.canDo(\'editUser\')" model="vm.user.mailNotificationsEnabled"></hy-checkbox><br><div ng-if="vm.unterpopupNewPassword" class="unterpopup"><input class="form-control" required placeholder="nytt l\xF6senord" type="password" autocomplete="off" ng-model="vm.newPassword"> <input class="form-control" required placeholder="nytt l\xF6senord igen" type="password" autocomplete="off" ng-model="vm.newPasswordAgain"><xsmall class="red-text" ng-if="vm.newPassword && (vm.newPassword !== vm.newPasswordAgain)">l\xF6senord \xE4r inte samma sak</xsmall><div class="unterpopup-footer"><button class="btn spacer-mt-xsmall" ng-disabled="!vm.newPassword || !vm.newPassword.length || (vm.newPassword !== vm.newPasswordAgain)" ng-click="vm.changePassword()">OK</button> <a class="link--turquoise link--no-underline" ng-click="vm.unterpopupNewPassword = false; vm.newPassword = null; vm.newPasswordAgain = null">Avbryt</a></div></div><a ng-if="Auth.canDo(\'setPassword\')" class="link--turquoise link--no-underline" ng-click="vm.unterpopupNewPassword = true">\xC4ndra l\xF6senord</a></div></div><div class="modal-footer modal-footer--medium"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="submit" ng-disabled="!Auth.canDo(\'editUser\') || vm.selectedOrganizations.length === 0 || vm.saving" title="{{vm.whyDisabled()}}" ng-click="vm.save()">Spara</button></div></form>');
$templateCache.put('resources/user/edit/edit.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-show="vm.ready"><hy-breadcrumb></hy-breadcrumb><h2>Uppdatera anv\xE4ndare</h2><form name="vm.userForm" ng-submit="vm.userForm.$valid &amp;&amp; vm.save()" class="spacer-mb-xlarge spacer-mt-xxlarge" novalidate><div class="row"><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputFirstname.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputFirstname.$invalid,\r\n          }"><label for="inputFirstname">F\xF6rnamn</label> <input name="inputFirstname" id="inputFirstname" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.firstname" autocomplete="off" maxlength="32" required></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputLastname.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputLastname.$invalid\r\n          }"><label for="inputLastname">Efternamn</label> <input name="inputLastname" id="inputLastname" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.lastname" autocomplete="off" maxlength="32" required></div></div></div><div class="row"><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputPhone.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputPhone.$invalid\r\n          }"><label for="inputPhone">Telefonnummer</label> <input name="inputPhone" id="inputPhone" type="tel" class="form-control form-control--narrow form-control--gray-border" ng-pattern="/^[\\+]?[(]?[0-9]{3}[)]?[-\\s\\.]?[0-9]{3}[-\\s\\.]?[0-9]{4,6}$/im" ng-model="vm.user.phone" maxlength="32" autocomplete="off"> <small>Exempel: 0701112233 eller +46701112233</small><br><small ng-show="vm.userForm.inputPhone.$error.pattern">Ange ett giltigt telefonnummer</small></div></div></div><div class="row"><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputEmail.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputEmail.$invalid\r\n          }"><label for="inputEmail">E-post</label> <input name="inputEmail" id="inputEmail" type="email" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.email" maxlength="40" disabled="disabled"> <small>E-postadress kan inte \xE4ndras - kontakta servicedesk om du beh\xF6ver \xE4ndra epost.</small></div></div></div><button type="submit" class="btn spacer-mr-medium" ng-disabled="vm.saving">Spara</button><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline" href="/users/{{vm.user.id}}">Avbryt</a></form><div class="row"><div class="col-6 col-lg-3"><hr><h3>\xC4ndra l\xF6senord</h3><form name="vm.passwordForm" ng-submit="vm.passwordForm.$valid &amp;&amp; vm.setPassword()" novalidate><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.passwordForm.inputOldPassword.$touched || vm.passwordForm.$submitted) &amp;&amp; vm.passwordForm.inputOldPassword.$invalid,\r\n          }"><label for="inputOldPassword">Nuvarande l\xF6senord</label> <input name="inputOldPassword" id="inputOldPassword" type="password" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.credentials.oldPassword" autocomplete="off" required></div><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.passwordForm.inputPassword.$touched || vm.passwordForm.$submitted) &amp;&amp; vm.passwordForm.inputPassword.$invalid}"><label for="inputPassword">Nytt l\xF6senord</label> <input name="inputPassword" id="inputPassword" type="password" ng-minlength="6" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.credentials.password" autocomplete="off" required> <small ng-show="(vm.passwordForm.inputPassword.$touched || vm.passwordForm.$submitted) &amp;&amp; vm.passwordForm.inputPassword.$invalid">Minst 6 tecken</small></div><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.passwordForm.verifyPassword.$touched || vm.passwordForm.$submitted) &amp;&amp; vm.passwordForm.verifyPassword.$invalid}"><label for="verifyPassword">Repetera nytt l\xF6senord</label> <input name="verifyPassword" id="verifyPassword" type="password" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.verifyPassword" hy-compare-form-fields="vm.credentials.password" autocomplete="off" required> <small ng-show="(vm.passwordForm.verifyPassword.$touched || vm.passwordForm.$submitted) &amp;&amp; vm.passwordForm.verifyPassword.$invalid">L\xF6senorden matchar inte</small></div><button type="submit" class="btn spacer-mr-medium" ng-disabled="vm.savingPassword">\xC4ndra l\xF6senord</button><hy-progress-circular size="20px" ng-show="vm.savingPassword"></hy-progress-circular><a class="link--turquoise link--no-underline" href="/users/{{vm.user.id}}">Avbryt</a></form></div></div></div>');
$templateCache.put('resources/user/partials/displayUserInfo.html','<h3>Min systeminformation</h3><ul class="list-group"><li class="list-group-item"><label class="list-group-item__title">R\xE4ttigheter</label> <span class="list-group-item__content">{{vm.user.role | hyRoles}}</span></li><li class="list-group-item"><label class="list-group-item__title">Part</label> <span class="list-group-item__content">{{vm.user.party | hyParties}}</span></li></ul>');
$templateCache.put('resources/user/resetPassword/resetPassword.html','<hy-reset-password></hy-reset-password>');
$templateCache.put('resources/user/resetPassword/setPasswordWithToken.html','<div class="container" ng-show="vm.ready"><div class="row"><div class="col-6 push-3 col-lg-4 push-lg-4"><h1>{{vm.title}}</h1><form name="vm.passwordForm" ng-submit="vm.passwordForm.$valid &amp;&amp; vm.setPassword()" novalidate><div class="form-group" ng-class="{\'form-group--error\': (vm.passwordForm.inputPassword.$touched ||vm.passwordForm.$submitted) &amp;&amp; vm.passwordForm.inputPassword.$invalid}"><label for="inputPassword">Nytt l\xF6senord</label> <input name="inputPassword" id="inputPassword" type="password" ng-minlength="6" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.credentials.password" hy-verify-password="vm.verifyPassword" autocomplete="off" required> <small>Minst 6 tecken</small></div><div class="form-group" ng-class="{\r\n              \'form-group--error\': (vm.passwordForm.verifyPassword.$touched || vm.passwordForm.$submitted) &amp;&amp; vm.passwordForm.verifyPassword.$invalid}"><label for="verifyPassword">Repetera nytt l\xF6senord</label> <input name="verifyPassword" id="verifyPassword" type="password" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.verifyPassword" autocomplete="off" required> <small ng-show="vm.credentials.password != vm.verifyPassword &amp;&amp; vm.passwordForm.verifyPassword.$touched">L\xF6senorden matchar inte</small></div><button type="submit" class="btn" ng-disabled="saving">\xC4ndra l\xF6senord</button></form></div></div></div>');
$templateCache.put('resources/user/show/other-user-select-modal.html','<div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">V\xE4lj anv\xE4ndare</h2></div><div class="modal-body modal-body--small" id="modal-body"><div class="scroller-selector"><table class="table"><tr class="sticky"><th class="table__th">Namn</th><th class="table__th">Fastigheter antal</th></tr><tr class="table__tr" ng-repeat="user in vm.users" ng-click="vm.select(user)"><td class="table__td">{{ user.firstname }} {{ user.lastname }}</td><td class="table__td">{{ user.propertyCount }}</td></tr></table></div></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a></div>');
$templateCache.put('resources/user/show/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-show="vm.ready"><hy-breadcrumb ng-hide="Auth.isDelegateUser()"></hy-breadcrumb><div class="display-flex space-between spacer-mt-large spacer-mb-xxxlarge"><div class="spacer-mr-small"><h1 class="text-margin-reset">{{vm.user.firstname}} {{vm.user.lastname}}</h1></div><div ng-hide="Auth.isDelegateUser()"><a href="/users" class="btn btn--arrow-left">Visa alla anv\xE4ndare</a></div></div><hr><div class="alert alert--info-bordered alert--hide-border alert--squary"><div class="row"><div class="col text-right"><a href="/users/{{vm.user.id}}/edit" ng-show="vm.meMode &amp;&amp; (Auth.isExternalUser() || Auth.isDelegateUser())" class="btn--icon btn--icon-pen"><span class="sr-only">Redigera</span></a></div></div><div class="row spacer-mb-medium"><div class="col-6 col-lg-4 spacer-mb-large"><hy-info title="F\xF6rnamn" content="vm.user.firstname"></hy-info></div><div class="col-6 col-lg-4 spacer-mb-large"><hy-info title="Efternamn" content="vm.user.lastname"></hy-info></div><div class="col-6 col-lg-4 spacer-mb-large"><hy-info title="Telefonnummer" content="vm.user.phone ? vm.user.phone : \'Saknas\'"></hy-info></div><div class="col-6 col-lg-4"><hy-info title="E-post" content="vm.user.email"></hy-info></div><div class="col-6 col-lg-4"><hy-info title="Part" content="vm.user.party | hyParties"></hy-info></div><div class="col-6 col-lg-4" ng-hide="vm.user.role === 0"><hy-info title="Beh\xF6righet" content="vm.user.role | hyRoles"></hy-info></div></div></div><div class="row"><div class="col-12 col-lg-6"><div ng-show="vm.user.party===vm.parties.DELEGATE"><hy-progress-circular size="60px" ng-hide="vm.readyNegotiations"></hy-progress-circular><div ng-show="vm.readyNegotiations"><h3 class="text-margin-top-small">F\xF6rhandlingar</h3><table class="table" ng-show="vm.negotiations.length > 0"><tr class="table__header"><th class="table__th">Namn</th><th class="table__th">Skapad</th></tr><tr class="table__tr" ng-repeat="negotiation in vm.negotiations | orderBy:[\'createdAt\']:true" ng-click="vm.gotoShowNegotiation(negotiation.id)"><td class="table__td">{{ negotiation.name }}</td><td class="table__td">{{ negotiation.createdAt | date : \'yyyy-MM-dd\' }}</td></tr></table></div></div><div ng-show="vm.user.party===vm.parties.EXTERNAL"><hy-progress-circular size="60px" ng-hide="vm.readyOrganizations"></hy-progress-circular><div ng-show="vm.readyOrganizations"><h3 class="text-margin-top-small">Organisationer</h3><table class="table" ng-show="vm.organizations.length > 0"><tr class="table__header"><th class="table__th">Namn</th><th class="table__th">Beh\xF6righet</th></tr><tr class="table__tr" ng-click="vm.gotoShowOrganization(org.id)" ng-repeat="org in vm.organizations | orderBy:[\'name\']"><td class="table__td">{{ org.name }}</td><td class="table__td">{{ org.role |\xA0hyRoles }}</td></tr></table><div class="alert alert--info" ng-hide="vm.organizations.length > 0">{{vm.user.firstname}} \xE4r inte kopplad till n\xE5gra organisationer</div></div></div><div ng-show="vm.user.party===vm.parties.HGF &amp;&amp; vm.user.displayResponsibilities"><table class="table spacer-mt-small" ng-show="vm.user.displayResponsibilities.appFP &amp;&amp; vm.user.displayResponsibilities.appFP.length > 0"><tr class="table__header"><th class="table__th"><h3 class="text-margin-reset">Roll (FP)</h3></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="role in vm.user.displayResponsibilities.appFP"><td class="table__td">{{ role.name | hgfUserRoles }}</td></tr></table></div><div ng-show="vm.user.party===vm.parties.HGF &amp;&amp; vm.user.displayResponsibilities"><table class="table spacer-mt-small" ng-show="vm.user.displayResponsibilities.appSP &amp;&amp; vm.user.displayResponsibilities.appSP.length > 0"><tr class="table__header"><th class="table__th"><h3 class="text-margin-reset">Roll (SP)</h3></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="role in vm.user.displayResponsibilities.appSP"><td class="table__td">{{ role.name | hgfUserRoles }}</td></tr></table></div><div ng-show="vm.user.party===vm.parties.HGF &amp;&amp; vm.user.regions.length > 0"><table class="table spacer-mt-large"><tr class="table__header"><th class="table__th"><h3 class="text-margin-reset">Region ({{vm.user.regions.length}})</h3></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="region in vm.user.regions"><td class="table__td">{{ region | regionTypeToDescription }}</td></tr></table></div><div ng-show="vm.user.party===vm.parties.HGF &amp;&amp; vm.user.offices.length > 0"><table class="table spacer-mt-xlarge"><tr class="table__header"><th class="table__th"><h3 class="text-margin-reset">Kontor ({{vm.user.offices.length}})</h3></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="office in vm.user.offices | orderBy"><td class="table__td">{{ office }}</td></tr></table></div></div><div class="col-12 col-lg-6"><div ng-show="vm.user.party===vm.parties.HGF &amp;&amp; vm.user.properties.length > 0"><table class="table spacer-mt-small"><tr class="table__header"><th class="table__th"><h3 class="text-margin-reset">Fastigheter ({{vm.user.properties.length}})</h3></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="property in vm.user.properties"><td class="table__td">{{ property.propertyDesignation }}</td></tr></table><button class="btn di icon--inline" ng-click="vm.exportProperties()">H\xE4mta fastigheter fr\xE5n annan anv\xE4ndare</button></div></div></div></div>');
$templateCache.put('resources/user/verify-email/verify-email.html','<div class="container"><div class="col col-6 spacer-centered spacer-mt-xxxlarge"><div class="alert alert--success alert--squary alert--white-bg alert--thin-border"><h4 class="spacer-mt-small">Fullf\xF6lj din registrering genom att logga in.</h4><form name="loginForm" class="spacer-mb-large" ng-submit="vm.login()" novalidate><div class="form-group" ng-class="{\r\n        \'form-group--error\': (loginForm.inputLoginEmail.$touched || loginForm.$submitted) &amp;&amp; loginForm.inputLoginEmail.$invalid}"><label for="inputLoginEmail">E-post</label><p>{{vm.credentials.email}}</p><span ng-show="redirecting">Omdirigerar...</span><hy-progress-circular size="20px" ng-show="redirecting"></hy-progress-circular></div><div class="form-group" ng-class="{\r\n        \'form-group--error\': (loginForm.inputLoginPassword.$touched || \r\n        loginForm.$submitted) &amp;&amp; loginForm.inputLoginPassword.$invalid}"><label for="inputLoginPassword">L\xF6senord</label> <input type="password" class="form-control form-control--narrow form-control--gray-border" id="inputVerifyLoginPassword" name="inputLoginPassword" ng-model="vm.password" placeholder="L\xF6senord" required></div><div class="text-center"><button type="submit" class="btn btn--full-width">Logga in</button><div class="spacer-mt-xsmall spacer-mb-xsmall"><hy-progress-circular size="20px" ng-show="saving"></hy-progress-circular></div></div></form></div></div></div>');
$templateCache.put('resources/user/welcome/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.userReady &amp;&amp; vm.negotiationCallsReady &amp;&amp; vm.negotiationsReady  &amp;&amp; vm.meetingsReady"></hy-progress-circular><div class="container" ng-show="vm.userReady &amp;&amp; vm.negotiationCallsReady &amp;&amp; vm.negotiationsReady &amp;&amp; vm.meetingsReady"><hy-header title="Mina sidor" buttons="vm.headerButtons" button-pane-cols="6"></hy-header><div class="row"><div class="col-12 col-lg-8 col-xl-9"><div><h3 class="text-margin-reset"><a href="/agreements/">Mina avtal</a></h3><hy-tableau id="tableauAgreements" columns="vm.agreementColumns" f-get-data="vm.getMyAgreements" default-sorting="fancyNumber DESC" f-get-context-info="vm.getAgreementContextInfo" f-row-click="vm.gotoAgreement" use-url="false" limit="3" in-memory="true" in-memory-excessive-limit="100"><type>{{vm.FreeAgreementTypes[$parent.item.type]}}</type><slot1>{{$parent.item.name | preventDateBreak}}</slot1></hy-tableau></div><ng-include src="\'resources/user/welcome/negotiation-table/list.html\'"></ng-include><div class="row spacer-mb-large spacer-mt-medium"><div class="col"><div class="border--bottom display-flex flex-direction-column"><strong class="text-uppercase spacer-mb-small">Senast skapade f\xF6rhandlingar</strong> <span class="spacer-mb-small">Mina f\xF6rhandlingar</span></div><div ng-repeat="negotiation in vm.negotiations" class="dropdown-item position-relative cursor-pointer" ng-click="vm.gotoNegotiation(negotiation.id)"><a href="/negotiations/{{negotiation.id}}" class="link link--no-underline-hover">{{negotiation.caseNumber}} {{negotiation.counterpart.name}}<br>{{negotiation.name}}<br>{{negotiation.type | hyNegotiationTypes}}<br>Status: <span class="text-bold text-success">{{negotiation.combinedState | hyCombinedNegotiationStates}}</span></a><hr class="hr--grey-light hr--list-divider hr--list-divider--full-width"></div></div><div class="col"><div ng-show="vm.user.party === vm.parties.EXTERNAL" class="border--bottom display-flex flex-direction-column"><strong class="text-uppercase spacer-mb-small">H\xE4ndelser i framst\xE4llningar</strong> <span class="spacer-mb-small">inom min organisation</span></div><div ng-show="vm.user.party === vm.parties.HGF" class="border--bottom display-flex flex-direction-column"><strong class="text-uppercase spacer-mb-small">Framst\xE4llningar</strong> <span class="spacer-mb-small">som bereds av mig</span></div><div ng-repeat="negotiationCall in vm.negotiationCalls track by $index" class="dropdown-item position-relative cursor-pointer" ng-click="vm.gotoNegotiationCall(negotiationCall.id)"><a href="/negotiation-calls/{{negotiationCall.id}}" class="link link--no-underline-hover">{{negotiationCall.caseNumber}} {{negotiationCall.counterpart.name}}<br>{{negotiationCall.name}}<br>{{negotiationCall.type | hyNegotiationTypes}}<br><span>Status: <span class="text-bold" ng-class="(negotiationCall.state === vm.NegotiationCallStates.WAITING_EXT) ? \'text-danger\' : \'text-success\'">{{negotiationCall.state | hyNegotiationCallStates}}</span></span></a><hr class="hr--grey-light hr--list-divider hr--list-divider--full-width"></div></div></div></div><div class="col-12 col-lg-4 col-xl-3"><div class="row"><div ng-if="vm.FeatureConfiguration.ENABLE_MEETINGS" class="col-6 col-lg-12 spacer-mb-medium"><div class="display-flex space-between align-items-bottom spacer-mb-small"><h4 class="text-margin-reset text-heavy">Mina m\xF6ten</h4><a class="link--turquoise link--no-underline" ng-click="vm.addMeeting()">Skapa nytt m\xF6te</a></div><div uib-datepicker ng-model="vm.dt" ng-change="vm.getMyDailyMeetings(vm.dt)" class="datepicker datepicker--border-grey" ng-if="vm.meetingsReady" datepicker-options="vm.dateOptions"></div><div class="dropdown dropdown--hide-caret dropdown--no-border spacer-mt-small" ng-class="{\'open\' :  vm.toggleMenu}"><div class="dropdown-menu dropdown-menu--date dropdown-menu--scroll dropdown-menu--arrow-center dropdown-menu--arrow-center-datepicker" role="menu"><div ng-repeat="meeting in vm.myDailyMeetings" class="dropdown-item dropdown-item--date" ng-click="vm.gotoMeeting(meeting.negotiations[0].id, meeting.id); vm.toggleMenu = false" ng-show="vm.myDailyMeetings.length > 0"><div class="display-flex space-between"><div class="col-6 padding-reset spacer-mr-xsmall"><div class="spacer-mb-xsmall text-bold ellipsis">{{meeting.title}}</div><div>Datum: <span class="text-bold text-success">{{ meeting.startAt | date:\'yyyy-MM-dd\' }}</span></div><div>Tid: <span class="text-bold text-success">{{ meeting.startAt | date:\'shortTime\' }} - {{ meeting.endAt | date:\'shortTime\' }}</span></div></div><div class="col-6 padding-reset"><div class="spacer-mb-xsmall"><strong>Deltagare</strong></div><div ng-repeat="participant in meeting.participants">{{participant.fullName}}</div></div></div><strong>F\xF6rhandlingar</strong><div ng-repeat="negotiation in meeting.negotiations">{{negotiation.name}}</div><hr class="hr--list-divider" ng-hide="$last"></div><div ng-if="vm.FeatureConfiguration.ENABLE_MEETINGS" ng-show="vm.myDailyMeetings.length === 0" class="dropdown-item dropdown-item--date" ng-click="vm.toggleMenu = false"><div class="text-center"><strong>Du har inga m\xF6ten p\xE5 denna dag!</strong></div></div></div></div></div><div class="col-6 col-lg-12"><div class="display-inline-block full-width"><h4 class="text-margin-reset">L\xE4nkar</h4><div class="link-box"><a href="/help/process" class="link--turquoise link--underline">Visa v\xE5r gemensamma process</a></div><div class="link-box" ng-show="vm.user.party === vm.parties.HGF"><a href="/docs/SKRIFTLIG-LATHUND-SKAPA-FRAMSTALLAN-I-FORHANDLARPORTALEN-20240229.pdf" class="link--turquoise link--underline btn--icon btn--icon-download--dark-turq" download target="_self">Lathund Skapa Framst\xE4llan.pdf</a></div><div class="link-box" ng-show="vm.user.party === vm.parties.HGF"><a href="/docs/SKRIFTLIG-LATHUND-SKAPA-EN-FORHANDLING-20240304.pdf" class="link--turquoise link--underline btn--icon btn--icon-download--dark-turq" download target="_self">Lathund Skapa F\xF6rhandling.pdf</a></div><div class="link-box" ng-show="vm.user.party === vm.parties.HGF"><a href="/docs/SKRIFTLIG-LATHUND-FORHANDLING-20240423.pdf" class="link--turquoise link--underline btn--icon btn--icon-download--dark-turq" download target="_self">Lathund F\xF6rhandling.pdf</a></div><div class="link-box"><a href="/docs/SKRIFTLIG-LATHUND-FASTIGHETSAGARE-ELLER-OMBUD-FP-20240420.pdf" class="link--turquoise link--underline btn--icon btn--icon-download--dark-turq" download target="_self">Lathund Fastighets\xE4gare Eller Ombud.pdf</a></div><div class="link-box"><a href="/docs/hyreslista-mall.xlsx" class="link--turquoise link--underline btn--icon btn--icon-download--dark-turq" download target="_self">Hyreslista (enkel) mall</a></div><div class="link-box"><a href="/docs/fastighetslista-mall.xlsx" class="link--turquoise link--underline btn--icon btn--icon-download--dark-turq" download target="_self">Ladda ner Fastighetslista mall (Excel)</a></div></div></div></div></div></div></div>');
$templateCache.put('resources/user/list/list.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><div ng-show="Auth.currentUser().party === vm.userParties.HGF"><hy-header title="Anv\xE4ndare" buttons="vm.buttons" tabs="vm.tablist"></hy-header><ng-include src="\'resources/user/list/\' + vm.currentSubpage + \'/list.html\'"></ng-include></div><div ng-show="Auth.currentUser().party === vm.userParties.EXTERNAL"><hy-header title="Anv\xE4ndare" buttons="vm.buttons"></hy-header><ng-include src="\'resources/user/list/external/list.html\'"></ng-include></div></div>');
$templateCache.put('resources/user/new/error.html','<div class="container"><h2 class="text-center">Hej!<br><br>Du f\xF6rs\xF6ker skapa konto, i detta fall beh\xF6ver du f\xE5 hj\xE4lp av F\xF6rhandlarportalen f\xF6r att komma vidare.</h2><h3 class="text-center">V\xE4nligen maila <a href="mailto:forhandlarportalen@hyresgastforeningen.se" class="link--turquoise text-reset1">forhandlarportalen@hyresgastforeningen.se</a><br>och beskriv vem du \xE4r och vilken organisation du tillh\xF6r.</h3></div>');
$templateCache.put('resources/user/new/new-user-modal.html','<div class="container"><div class="spacer-mt-large"><hy-modal-wizard wz-submit="vm.save()" wz-cancel="vm.cancel()" goto-next="vm.gotoNext" is-current-step-valid="vm.isCurrentStepValid" header-text="Ny anv\xE4ndare" submit-text="Skapa" submit-disabled="vm.saving"><hy-modal-wizard-step heading="Information" heading-short="Information" heading-step-number="1" name="user" is-valid="vm.userForm.$valid"><ng-include src="\'resources/user/new/partials/_user-data.html\'"></ng-include></hy-modal-wizard-step><hy-modal-wizard-step heading="V\xE4lj organisation" heading-short="V\xE4lj organisation" heading-step-number="2" name="process" is-valid="vm.chooseForm.$valid"><ng-include src="\'resources/user/new/partials/_choose-process.html\'"></ng-include></hy-modal-wizard-step><hy-modal-wizard-step heading="S\xF6k organisation" heading-short="S\xF6k organisation" heading-step-number="3" name="selectOrganization" is-valid="vm.chooseOrganizationForm.$valid" is-disabled="vm.choiceMethod !== \'SELECT_EXISTING\'"><ng-include src="\'resources/user/new/partials/_choose-organization.html\'"></ng-include></hy-modal-wizard-step><hy-modal-wizard-step heading="Ny organisation" heading-short="Ny organisation" heading-step-number="3" name="newOrganization" is-valid="vm.organizationForm.$valid" is-disabled="vm.choiceMethod !== \'CREATE_NEW\'"><ng-include src="\'resources/user/new/partials/_organization-data.html\'"></ng-include></hy-modal-wizard-step><hy-modal-wizard-step heading="V\xE4lj roll" heading-short="V\xE4lj roll" heading-step-number="4" name="role" is-valid="vm.roleForm.$valid" is-disabled="!vm.fullMode"><ng-include src="\'resources/user/new/partials/_choose-role.html\'"></ng-include></hy-modal-wizard-step></hy-modal-wizard></div></div>');
$templateCache.put('resources/user/new/new.html','<div class="container"><h2>Registrera dig</h2><form name="vm.userForm" ng-submit="vm.userForm.$valid &amp;&amp; vm.save()" class="spacer-mb-xlarge spacer-mt-xxlarge" novalidate><div class="row spacer-mb-xlarge" ng-if="!vm.user.delegationParticipant"><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputCompanyName.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputCompanyName.$invalid,\r\n          }"><label for="inputCompanyName">F\xF6retagsnamn</label> <input name="inputCompanyName" id="inputCompanyName" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.organization.name" ng-disabled="vm.user.token" autocomplete="off" maxlength="250" required></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputOrganizationNumber.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputOrganizationNumber.$invalid\r\n          }"><label for="inputOrganizationNumber">Organisationsnummer</label> <input name="inputOrganizationNumber" id="inputOrganizationNumber" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.organization.organizationNumber" ng-change="!vm.userForm.inputOrganizationNumber.$error.pattern &amp;&amp; vm.checkForExistingOrgNumber()" ng-pattern="/^\\d{6}-?\\d{4}$/" ng-disabled="vm.user.token" autocomplete="off" required> <small ng-hide="vm.userForm.inputOrganizationNumber.$valid" class="form-group__help">Enligt formatet 1234561234</small> <small ng-show="vm.userForm.inputOrganizationNumber.$error.pattern">Ange ett giltigt organisationsnummer</small> <small ng-show="vm.userForm.inputOrganizationNumber.$valid &amp;&amp; vm.newOrgNumber">Din organisation \xE4r ny i portalen. Hyresg\xE4stf\xF6reningen kommer behandla din beg\xE4ran. Blir ni godk\xE4nda kommer du att s\xE4ttas som administrat\xF6r (kan \xE4ndras i inst\xE4llningar). </small><small ng-show="vm.userForm.inputOrganizationNumber.$valid &amp;&amp; !vm.newOrgNumber">Din organisation \xE4r redan registrerad i portalen. Administrat\xF6r inom din organisation kommer behandla din beg\xE4ran.</small></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': vm.userForm.$submitted &amp;&amp; vm.newOrgNumber &amp;&amp; !vm.selectedOffice.id\r\n          }"><label for="inputHgfOfficeId" class="spacer-mb-xsmall"><div class="display-inline-flex"><div class="spacer-mr-xsmall" ng-class="{\'text-gray\' : vm.userForm.inputOrganizationNumber.$invalid || !vm.newOrgNumber}">HGF kontakt (kontor)</div><div class="icon icon--info icon--small" uib-tooltip="F\xF6r att underl\xE4tta och skynda p\xE5 godk\xE4nnande ber vi dig v\xE4lja det kontor du har \r\n                b\xE4st kontakt med s\xE5 de kan verifiera att du \xE4r beh\xF6rig att anv\xE4nda F\xF6rhandlarportalen." tooltip-placement="top" tooltip-enable="vm.userForm.inputOrganizationNumber.$valid &amp;&amp; vm.newOrgNumber" tooltip-is-open="vm.userForm.inputOrganizationNumber.$valid &amp;&amp; vm.newOrgNumber"></div></div></label> <input name="inputHgfOfficeId" id="inputHgfOfficeId" type="hidden" ng-model="vm.selectedOffice.id"><div class="dropdown dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white__filter btn--gray btn--full-width" uib-dropdown-toggle ng-disabled="vm.userForm.inputOrganizationNumber.$invalid || !vm.newOrgNumber"><span class="text-normal">{{vm.selectedOffice.id ? vm.selectedOffice.name : \'V\xE4lj mottagande kontor\'}}</span></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="office in vm.offices" type="button" class="dropdown-item" ng-click="vm.selectedOffice.id = office.id; vm.selectedOffice.name = office.name">{{office.name}}</button></div></div></div></div></div><div class="row"><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputFirstname.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputFirstname.$invalid,\r\n          }"><label for="inputFirstname">F\xF6rnamn</label> <input name="inputFirstname" id="inputFirstname" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.firstname" autocomplete="off" maxlength="32" required></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputLastname.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputLastname.$invalid\r\n          }"><label for="inputLastname">Efternamn</label> <input name="inputLastname" id="inputLastname" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.lastname" autocomplete="off" maxlength="32" required></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputPhone.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputPhone.$invalid\r\n          }"><label for="inputPhone">Telefonnummer</label><br><div ng-hide="vm.foreignPhone" class="form-control-prepend">+46</div><input name="inputPhone" id="inputPhone" type="tel" ng-pattern="/[0-9]{7,}/im" ng-change="vm.tamePhoneNumber()" class="form-control form-control--prepend form-control--gray-border" ng-model="vm.user.phone" placeholder="Exempel: 706532452" autocomplete="off"><br><small ng-show="vm.userForm.inputPhone.$error.pattern">Ange ett giltigt telefonnummer</small></div></div></div><div class="row"><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.inputEmail.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputEmail.$invalid\r\n          }"><label for="inputEmail">E-post</label> <input name="inputEmail" id="inputEmail" type="email" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.email" ng-disabled="vm.user.token" maxlength="128" ng-change="vm.startCheckEmail()" required><hy-progress-circular size="20px" ng-show="vm.confirmingEmail"></hy-progress-circular><small ng-show="(vm.userForm.inputEmail.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputEmail.$invalid">Ange en fullst\xE4ndig giltig epostadress</small><br><small style="color: red" ng-show="vm.isMailConfirmed===false">Epostadress berkr\xE4ftades inte</small> <small style="color: green" ng-show="vm.isMailConfirmed===true">Epost bekr\xE4ftad</small></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.userForm.verifyEmail.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.verifyEmail.$invalid\r\n          }"><label for="inputEmail">Upprepa E-post</label> <input name="verifyEmail" id="verifyEmail" type="email" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.verifyEmail" hy-compare-form-fields="vm.user.email" ng-disabled="vm.user.token" maxlength="128" required> <small ng-show="(vm.userForm.verifyEmail.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.verifyEmail.$invalid">E-post matchar inte</small></div></div></div><div class="row"><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n          \'form-group--error\': (vm.userForm.inputPassword.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputPassword.$invalid,\r\n        }"><label for="inputPassword">L\xF6senord</label> <input name="inputPassword" id="inputPassword" type="password" ng-minlength="6" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.password" autocomplete="off" required> <small ng-show="(vm.userForm.inputPassword.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputPassword.$invalid">Minst 6 tecken</small></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n          \'form-group--error\': (vm.userForm.verifyPassword.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.verifyPassword.$invalid,\r\n        }"><label for="verifyPassword">Repetera l\xF6senord</label> <input name="verifyPassword" id="verifyPassword" type="password" ng-minlength="6" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.verifyPassword" hy-compare-form-fields="vm.user.password" autocomplete="off" ng-disabled="vm.userForm.inputPassword.$invalid" required> <small ng-show="(vm.userForm.verifyPassword.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.verifyPassword.$invalid">L\xF6senorden matchar inte</small></div></div></div><div class="row"><div class="col-10 col-lg-5"><div ng-show="!vm.readGDPR">V\xE4nligen l\xE4s och godk\xE4nn anv\xE4ndarvillkoren genom att klicka p\xE5 knappen</div><a class="btn spacer-mb-xsmall" href="/docs/Anv\xE4ndarvillkor.pdf" ng-click="vm.readGDPR = true" title="Anv\xE4ndarvillkor F\xF6rhandlarportalen" target="_blank">L\xE4sa anv\xE4ndarvillkoren</a><div class="custom-control custom-control--checkbox" ng-show="vm.readGDPR"><input class="custom-control__input" type="checkbox" id="acceptedGDPR" ng-model="vm.acceptedGDPR"> <label class="custom-control__label" for="acceptedGDPR">Jag har tagit del av anv\xE4ndarvillkor F\xF6rhandlarportalen</label></div></div></div><div class="text-right spacer-mt-medium"><a class="link--turquoise link--no-underline spacer-mr-medium" href="/">Avbryt</a><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><button type="submit" class="btn" ng-disabled="vm.saving || !vm.acceptedGDPR">Skicka</button></div></form></div>');
$templateCache.put('resources/user/new/thank-you-invite.html','<div class="container"><h1 class="text-center">Tack f\xF6r att du valt att<br>registrera dig i portalen!</h1></div>');
$templateCache.put('resources/user/new/thank-you.html','<div class="container"><h1 class="text-center">Tack f\xF6r att du valt att<br>registrera dig i portalen!</h1><h3 class="text-center">Du kommer att f\xE5 ett mail n\xE4r det \xE4r dags att logga in.</h3></div>');
$templateCache.put('resources/negotiation-call/list/list-new/list.html','<div ng-controller="ListNegotiationCallNewCtrl as listNewVM"><hy-tableau id="callsNewTableau" columns="listNewVM.tableColumns" default-sorting="caseNumber DESC" f-get-data="listNewVM.tableFGetData" f-row-click="listNewVM.gotoCall" use-url="true" f-get-context-info="vm.getContextInfo" limit="30"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><slot2>{{ $parent.item.office.name }}</slot2><slot3 ng-init="call = $parent.item">{{ call.sentAt | date: "yyyy-MM-dd" }}<br>{{ call.negotiationDate | date: "yyyy-MM-dd" }}</slot3><status>{{$parent.item.state | hyNegotiationCallStates:Auth.isHgfUser()}}</status><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && vm.editComment(item)"></comment></hy-tableau></div>');
$templateCache.put('resources/negotiation-call/list/list-all/list.html','<div ng-controller="ListNegotiationCallAllCtrl as listAllVM"><hy-tableau id="callsAllTableau" columns="listAllVM.tableColumns" default-sorting="caseNumber DESC" f-get-data="listAllVM.tableFGetData" f-row-click="listAllVM.gotoCall" f-get-context-info="vm.getContextInfo" use-url="true" limit="30"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><slot2>{{ $parent.item.office.name }}</slot2><slot3 ng-init="call = $parent.item">{{ call.sentAt | date: "yyyy-MM-dd" }}<br>{{ call.negotiationDate | date: "yyyy-MM-dd" }}</slot3><status>{{$parent.item.state | hyNegotiationCallStates:Auth.isHgfUser()}}</status><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && vm.editComment(item)"></comment></hy-tableau></div>');
$templateCache.put('resources/negotiation/list/change-negotiator/change-negotiator-modal.html','<div class="modal-header modal-header--medium"></div><div class="modal-body modal-body--medium" id="modal-body"><hy-modal-wizard wz-submit="vm.ok()" wz-cancel="vm.cancel()" is-current-step-valid="vm.isCurrentStepValid" current-step-number="vm.currentStepNumber" welcome-message="\xC4ndra f\xF6rhandlare" header-text="\xC4ndra ansvarig f\xF6rhandlare" before-goto-next="vm.beforeGotoNext(cb, currentStepNumber)" submit-text="\xC4ndra" submit-disabled="vm.busy"><hy-progress-circular ng-show="vm.busy" center-on-page size="60px"></hy-progress-circular><hy-modal-wizard-step heading="V\xE4lj f\xF6rhandlingar" heading-short="V\xE4lj f\xF6rhandlingar" heading-step-number="1" name="select-negotiations" is-valid="vm.selectedNegotiations.length > 0"><div class="spacer-mb-small"><small><span class="red-text">Varning!</span> Endast aktiva f\xF6rhandlingar g\xE5r att flytta. Dina val g\xE5r inte att \xE5terkalla. Vi ber dig d\xE4rf\xF6r att vara extra noggrann och kontrollera dina val!</small></div><hy-tableau id="tableauNegotiations" columns="vm.columns" f-get-data="vm.fGetData" use-url="false" in-memory="true" checkboxes="true" checkboxes-alternative="true" id-property="id" in-memory-excessive-limit="vm.Limits.InMemoryMiddleLimit" selected-rows="vm.selectedNegotiations" f-get-context-info="vm.getContextInfo" limit="7"></hy-tableau></hy-modal-wizard-step><hy-modal-wizard-step heading="V\xE4lj ny ansvarig f\xF6rhandlare (internt eller externt)" heading-short="V\xE4lj ny ansvarig f\xF6rhandlare" heading-step-number="2" name="select-negotiator" is-valid="vm.selectedHgfParticipantRole !== null || \r\n                                        vm.selectedExtParticipantRole !== null"><table width="100%"><tr aria-label="current negotiators"><td width="50%" valign="top"><div ng-if="vm.currentHgfNegotiators"><b>Ansvarig f\xF6rhandlare HGF nu</b>: {{ vm.currentHgfNegotiators }}</div></td><td width="50%" valign="top"><div ng-if="vm.currentExtNegotiators"><b>Ansvarig f\xF6rhandlare motpart nu</b>: {{ vm.currentExtNegotiators }}</div></td></tr><tr aria-label="search fields"><td><label class="spacer-mb-xsmall" for="inputSelectParticipant">S\xF6k efter f\xF6rhandlare HGF person</label> <input type="text" name="inputSelectParticipant" id="inputSelectParticipant" autocomplete="off" ng-model="vm.hgfParticipant" typeahead-editable="false" placeholder="S\xF6k" uib-typeahead="user as user.fullName for user in vm.hgfUsers | filter: {fullName: $viewValue} | limitTo:8" class="form-control form-control--narrow form-control--gray-border"></td><td><label class="spacer-mb-xsmall" for="inputSelectParticipant">S\xF6k efter motpart person</label> <input type="text" name="inputExtSelectParticipant" id="inputExtSelectParticipant" autocomplete="off" ng-model="vm.extParticipant" typeahead-editable="false" placeholder="S\xF6k" uib-typeahead="user as user.fullName for user in vm.extUsers | filter: {fullName: $viewValue} | limitTo:8" class="form-control form-control--narrow form-control--gray-border"></td></tr><tr aria-label="add buttons"><td valign="top"><button class="btn btn--full-width spacer-mb-xsmall" type="submit" ng-disabled="vm.busy || !vm.hgfParticipant" ng-click="vm.selectHgfParticipant(vm.NegotiationRoles.HGF_ASSOC_CHIEF)">L\xE4gg till som ansvarig HGF f\xF6rhandlare</button></td><td valign="top"><button class="btn btn--full-width spacer-mb-xsmall" type="submit" ng-disabled="vm.busy || !vm.extParticipant" ng-click="vm.selectExtParticipant(vm.NegotiationRoles.FA_POWNER_CHIEF)">L\xE4gg till som ansvarig hyresv\xE4rd</button> <button class="btn btn--full-width spacer-mb-xsmall" type="submit" ng-disabled="vm.busy || !vm.extParticipant" ng-click="vm.selectExtParticipant(vm.NegotiationRoles.OMBUD_FA_POWNER_CHIEF)">L\xE4gg till som ansvarig hyresv\xE4rd ombud</button></td></tr><tr aria-label="checkboxes"><td><hy-checkbox title="ta bort tilldelningen av befintlig ansvarig HGF anv\xE4ndare" name="unassignExistingHgfChief" model="vm.unassignExistingHgfChief"></hy-checkbox></td><td><hy-checkbox title="ta bort tilldelningen av befintlig ansvarig anv\xE4ndare" name="unassignExistingExtChief" model="vm.unassignExistingExtChief"></hy-checkbox></td></tr><tr aria-label="currents"><td><div ng-if="vm.selectedHgfParticipant"><b>Ansvarig f\xF6rhandlare HGF vald</b>: {{ vm.selectedHgfParticipant.firstname }} {{ vm.selectedHgfParticipant.lastname }} <span class="red-text parentParam" ng-click="vm.selectedHgfParticipantRole = null; vm.selectedHgfParticipant = null;">Avv\xE4lj</span></div></td><td><div ng-if="vm.selectedExtParticipant"><b>Ansvarig f\xF6rhandlare motpart vald</b>: {{ vm.selectedExtParticipant.firstname }} {{ vm.selectedExtParticipant.lastname }} <span class="red-text parentParam" ng-click="vm.selectedExtParticipantRole = null; vm.selectedExtParticipant = null;">Avv\xE4lj</span></div></td></tr></table></hy-modal-wizard-step><hy-modal-wizard-step heading="\xD6versikt" heading-short="\xD6versikt" heading-step-number="3" name="confirmation" is-valid="true"><div class="alert alert--narrow-turq alert--squary"><h3 class="text-danger spacer-margin-reset">OBS!</h3>Kontrollera noggrant dina val!<hy-tableau id="tableauConfirmation" columns="vm.confirmationColumns" f-get-data="vm.fGetConfirmation" use-url="false" in-memory="true" no-sorting="true" no-filtering="true" in-memory-excessive-limit="vm.Limits.InMemoryMiddleLimit" limit="7"></hy-tableau></div></hy-modal-wizard-step></hy-modal-wizard></div>');
$templateCache.put('resources/negotiation/list/all/list.html','<div ng-controller="ListNegotiationAllCtrl as allVM"><div class="row"><div class="col-9"></div><div class="col-3"><hy-combobox items="allVM.regionChoices" prompt="V\xE4lj region" text-property="text" current-item="allVM.regionFilter" current-item-changed="allVM.changeRegion()"></hy-combobox></div></div><hy-tableau id="negotiationsAllTableau" columns="allVM.tableColumns" default-sorting="caseNumber DESC" f-get-data="allVM.tableFGetData" f-row-click="allVM.gotoNegotiation" use-url="true" limit="30" f-get-context-info="vm.getContextInfo" last-params="allVM.lastParams"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><status>{{$parent.item.combinedState | hyCombinedNegotiationStates}}</status><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && vm.editComment(item)"></comment></hy-tableau></div>');
$templateCache.put('resources/negotiation/list/my-negotiations/list.html','<div ng-controller="ListNegotiationMyCtrl as myNegVM"><hy-tableau id="negotiationsMyTableau" columns="myNegVM.tableColumns" default-sorting="caseNumber DESC" f-get-data="myNegVM.tableFGetData" f-row-click="myNegVM.gotoNegotiation" attributes="myNegVM.tableAttributes" use-url="true" f-get-context-info="vm.getContextInfo" limit="myNegVM.pageLimit" last-params="myNegVM.lastParams"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><person ng-init="person = vm.isHgfUser ? $parent.item.extNegotiator : $parent.item.hgfNegotiator">{{ person.firstname }} {{ person.lastname }}</person><status>{{$parent.item.combinedState | hyCombinedNegotiationStates}}</status><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && vm.editComment(item)"></comment></hy-tableau></div>');
$templateCache.put('resources/negotiation/show/artifacts/show.html','<div ng-controller="ShowNegotiationArtifactsCtrl as tablelistVM"><hy-artifacts object-id="tablelistVM.objectId" object-type="tablelistVM.objectType" file-labels="tablelistVM.fileLabels" group="false" count="vm.count"></hy-artifacts></div>');
$templateCache.put('resources/negotiation/new/partials/_apartments.html','<div class="row"><div class="col-12"><hy-tableau id="tableauApartments" columns="vm.listApartmentsColumns" f-get-data="vm.fGetApartments" use-url="false" in-memory="true" in-memory-excessive-limit="4000" checkboxes="true" id-property="id" selected-rows="vm.selectedApartments" limit="30"></hy-tableau></div></div>');
$templateCache.put('resources/negotiation/new/partials/_choose-process.html','<div class="alert alert--info-bordered alert--hide-border alert--squary alert--no-top-bottom-padding"><div class="row"><div class="col-6 col-lg-5 wizard-choice" ng-class="{\'wizard-choice--active\': vm.choiceMethod === \'ALL_PROPERTIES\'}" ng-click="vm.setRadioModelByOutsideClick(\'ALL_PROPERTIES\')"><div class="wizard-choice__container spacer-mt-large"><div class="custom-control custom-control--radio custom-control--radio"><input class="custom-control__input custom-control__input--hidden" type="radio" id="allProperties" name="radio-group" tabindex="0" ng-model="vm.choiceMethod" value="ALL_PROPERTIES" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="allProperties"><span>V\xE4lj alla fastigheter</span></label></div><ul class="wizard-choice__list"><li class="wizard-choice__list-item--yes">Skapa endast en f\xF6rhandling</li><li class="wizard-choice__list-item--yes">Alla fastigheter fr\xE5n framst\xE4llan tas med i f\xF6rhandlingen</li></ul></div></div><div class="col-2 col-lg-2 wizard-choice__middle-col"><h3 class="text-margin-reset">eller</h3></div><div class="col-6 col-lg-5 wizard-choice" ng-class="{\'wizard-choice--active\': vm.choiceMethod === \'SELECT_PROPERTIES\'}" ng-click="vm.setRadioModelByOutsideClick(\'SELECT_PROPERTIES\')"><div class="wizard-choice__container spacer-mt-large"><div class="custom-control custom-control--radio custom-control--radio"><input class="custom-control__input custom-control__input--hidden" type="radio" id="selectProperties" name="radio-group" tabindex="1" ng-model="vm.choiceMethod" value="SELECT_PROPERTIES" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="selectProperties"><span>G\xF6r urval av fastigheter</span></label></div><ul class="wizard-choice__list"><li class="wizard-choice__list-item--yes">Skapa en eller flera delf\xF6rhandlingar</li><li class="wizard-choice__list-item--yes">V\xE4lj ett urval av fastigheter fr\xE5n framst\xE4llan som ska f\xF6rhandlas</li></ul></div></div></div></div>');
$templateCache.put('resources/negotiation/new/partials/_conditions.html','<hy-checkbox title="Medlemsutskick" model="vm.negotiation.conditionTags.isReadyToSend"></hy-checkbox><xsmall><i>F\xF6r information till Kvarteret om utskick ska ske.<br>Ibockad ruta = JA / Urbockad ruta = NEJ</i></xsmall>');
$templateCache.put('resources/negotiation/new/partials/_district.html','<div class="row"><div class="col-12"><hy-tableau id="tableauDistricts" columns="vm.listDistrictColumns" f-get-data="vm.fGetDistricts" use-url="false" in-memory="true" in-memory-excessive-limit="4000" checkboxes="true" id-property="id" selected-rows="vm.selectedDistricts" limit="30"></hy-tableau></div></div>');
$templateCache.put('resources/negotiation/new/partials/_motiv.html','<hy-tableau id="motivTableau" columns="vm.motivColumns" f-get-data="vm.motivFGetData" use-url="false" checkboxes="true" checkboxes-alternative="false" id-property="id" in-memory="true" selected-rows="vm.selectedProperties" in-memory-excessive-limit="vm.Limits.InMemoryMiddleLimit" actions="vm.motivActions" limit="30"></hy-tableau>');
$templateCache.put('resources/negotiation/new/partials/_negotiator.html','<script type="text/ng-template" id="negotiatorUser"><span ng-bind-html="match.label.fullName | uibTypeaheadHighlight:query"></span>\r\n  <br/>\r\n  <small ng-repeat="org in match.label.organizations">\r\n    {{org.name}}<br/>\r\n  </small></script><hy-progress-circular size="60px" center-on-page ng-show="vm.saving"></hy-progress-circular><div class="alert alert--border-turq alert--squary"><form name="vm.negotiatorForm" ng-submit="vm.negotiatorForm.$valid &amp;&amp; vm.save()" class="spacer-mb-xlarge" novalidate><div class="row"><div class="col-6 col-lg-4 spacer-mb-large" ng-if="!vm.isHgf"><label for="inputNegotiationType"><a href="#" class="display-inline-flex" ng-click="vm.negotiationTypeInfo(); $event.preventDefault();"><div>V\xE4lj f\xF6rhandlingss\xE4tt</div><div class="icon icon--info icon--small cursor-pointer"></div></a></label> <input name="inputNegotiationType" id="inputNegotiationType" type="hidden" ng-model="vm.negotiation.allowPartial" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width" uib-dropdown-toggle>{{vm.negotiation.allowPartial !== undefined ? vm.selectedAllowPartialName : \'V\xE4lj f\xF6rhandlingss\xE4tt\'}}</button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="allowPartial in vm.allowPartialChoices" type="button" class="dropdown-item" ng-click="vm.negotiation.allowPartial = !allowPartial.id; vm.selectedAllowPartialName = allowPartial.name">{{allowPartial.name}}</button></div></div></div><div class="col-6 col-lg-4 form-group" ng-class="{\'form-group--error\': vm.negotiation.participants.length == 0}"><label for="inputSelectParticipant">Representanter {{ vm.partyDescription }}</label> <input type="text" name="inputSelectParticipant" id="inputSelectParticipant" autocomplete="off" ng-model="vm.selectedParticipant" typeahead-editable="false" placeholder="S\xF6k efter en person" typeahead-on-select="vm.addParticipant()" typeahead-template-url="negotiatorUser" uib-typeahead="user as user for user in vm.users | filter: {fullName: $viewValue} | limitTo:8" class="form-control form-control--narrow form-control--gray-border"><div ng-if="vm.showAddNegotiatorLink" class="info__content--narrow-block"><span>Saknas personen, registrera ny </span><span><a href="#" ng-click="vm.showAddNegotiatorModal(); $event.preventDefault();" class="link--turquoise">h\xE4r</a></span></div></div><div class="col-6 col-lg-4"><div class="info__content info__content--narrow display-flex align-items-center space-between" ng-repeat="participant in vm.negotiation.participants"><div><span class="info__content--narrow-block"><strong>{{participant.firstname}} {{participant.lastname}}</strong></span> <span class="info__content--narrow-block"><a href="javascript:void(0);" target="_blank" class="link--turquoise text-reset word-break-all">{{participant.email}}</a></span> <span class="info__content--narrow-block">{{participant.phone}}</span><div class="custom-control custom-control--radio"><input class="custom-control__input" type="radio" id="inputIsChief_{{participant.id}}" name="inputIsChief" ng-model="vm.chiefId" ng-value="{{participant.id}}" ng-change="vm.tagChief(participant)"> <label class="custom-control__label" for="inputIsChief_{{participant.id}}">Ansvarig f\xF6rhandlare</label></div></div><div class="info__content--narrow-block"><a href="#" class="btn--icon btn--icon-bin btn--icon--no-padding" ng-click="vm.removeParticipant(participant); $event.preventDefault();"><span class="sr-only">Ta bort</span></a></div></div></div></div></form></div>');
$templateCache.put('resources/negotiation/new/partials/_properties.html','<div class="row"><div class="col-12"><hy-tableau id="tableauProperties" columns="vm.listPropertiesColumns" f-get-data="vm.fGetProperties" use-url="false" in-memory="true" in-memory-excessive-limit="4000" checkboxes="true" id-property="id" selected-rows="vm.selectedProperties" limit="30"></hy-tableau></div></div>');
$templateCache.put('resources/negotiation/show/blog/show.html','<div ng-controller="ShowNegotiationBlogCtrl as tablelistVM"><hy-microblog object-id="tablelistVM.objectId" object-type="tablelistVM.objectType" count="vm.count"></hy-microblog></div>');
$templateCache.put('resources/negotiation/show/documents/show.html','<div ng-controller="ShowNegotiationDocumentsCtrl as filesVM"><div class="row" ng-hide="Auth.isDelegateUser()"><div class="col-8"><h3 class="text-margin-top-reset spacer-mb-reset">Underlag</h3><p class="spacer-mt-reset">H\xE4r kan ni ladda upp dokument som ska anv\xE4ndas som underlag till f\xF6rhandlingen.<br>\xC4ven andra dokument som r\xF6r f\xF6rhandlingen samlas h\xE4r tex Framst\xE4llankvittot och avtalet.</p></div><div class="col-4 text-right" ng-show="filesVM.Auth.canDo(\'uploadDocument\')"><button type="button" class="btn btn--icon btn--icon-upload" ng-hide="vm.negotiation.isFinished" ng-click="filesVM.add()">Ladda upp underlag</button></div></div><div class="spacer-mt-medium"><hy-progress-circular size="60px" ng-hide="filesVM.readyDocuments"></hy-progress-circular></div><table class="table spacer-mt-medium" ng-show="filesVM.files.mainDocuments.length > 0" ng-class="{\'spacer-mt-xxlarge\' : Auth.isDelegateUser()}"><tr class="table__header"><th class="table__th table__td--width-30">Dokument</th><th class="table__th table__td--width-15">Typ</th><th class="table__th table__td--width-15">Dokument\xE4gare</th><th class="table__th table__td--width-20">Part</th><th class="table__th table__td--width-15">Uppladdad den</th><th class="table__th table__td--width-5"></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="file in filesVM.files.mainDocuments | orderBy:\'-lastModified\'"><td class="table__td"><span ng-hide="Auth.isDelegateUser() && vm.isRentListFileType(file.type)">{{ file.filename }}</span> <span ng-show="Auth.isDelegateUser() && vm.isRentListFileType(file.type)">{{ file.filename }}</span></td><td class="table__td">{{ file.type | documentTypes}}</td><td class="table__td">{{ file.createdBy }}</td><td class="table__td">{{ file.party === 20 ? \'HGF\' : vm.negotiation.counterpart.name }}</td><td class="table__td">{{ file.lastModified | date : "yyyy-MM-dd"}}</td><td class="table__td"><div class="display-flex space-between align-items-center"><div class="spacer-mr-medium"><a href="/api/negotiations/{{vm.negotiation.id}}/documents/{{file.name}}" target="_self" ng-hide="Auth.isDelegateUser() && vm.isRentListFileType(file.type)" download class="btn--icon btn--icon-download--dark-turq btn--icon--no-padding"><span class="sr-only">Ladda ner</span></a></div><div ng-show="Auth.canDo(\'deleteDocument\') && (file.party===Auth.currentUser().party || (filesVM.Auth.isHgfUser() && file.isCallFile == \'true\'))"><a href="#" ng-click="filesVM.delete(file); $event.preventDefault();" class="btn--icon btn--icon--no-padding btn--icon-bin" ng-hide="Auth.isDelegateUser() || vm.negotiation.isFinished || file.type==30"><span class="sr-only">Ta bort</span></a></div></div></td></tr></table><h3 ng-show="filesVM.files.relatedDocuments.length > 0">\xD6vriga dokument</h3><table class="table spacer-mt-medium" ng-show="filesVM.files.relatedDocuments.length > 0"><tr class="table__header"><th class="table__th table__td--width-25">Dokument</th><th class="table__th table__td--width-15">Typ</th><th class="table__th table__td--width-15">Dokument\xE4gare</th><th class="table__th table__td--width-15">Part</th><th class="table__th table__td--width-15">Uppladdad den</th><th class="table__th table__td--width-10">Tillh\xF6r</th><th class="table__th table__td--width-5"></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="file in filesVM.files.relatedDocuments | orderBy:\'-lastModified\'"><td class="table__td"><span ng-hide="Auth.isDelegateUser() && vm.isRentListFileType(file.type)">{{ file.filename }}</span> <span ng-show="Auth.isDelegateUser() && vm.isRentListFileType(file.type)">{{ file.filename }}</span></td><td class="table__td">{{ file.type | documentTypes}}</td><td class="table__td">{{ file.createdBy }}</td><td class="table__td">{{ file.party === 20 ? \'HGF\' : vm.negotiation.counterpart.name }}</td><td class="table__td">{{ file.lastModified | date : "yyyy-MM-dd"}}</td><td class="table__td">{{ file.entity | documentBelongsTo | enSwDictionary }}</td><td class="table__td"><div class="display-flex space-between align-items-center"><div><a href="/api/negotiations/{{vm.negotiation.id}}/{{ file.entity | documentBelongsTo}}/{{file.entityId}}/documents/{{file.name}}" target="_self" download ng-if="file.entityId" ng-hide="Auth.isDelegateUser() && vm.isRentListFileType(file.type)" class="btn--icon btn--icon-download--dark-turq"><span class="sr-only">Ladda ner</span> </a><a href="/api/negotiations/{{vm.negotiation.id}}/documents/{{file.prefix}}:{{file.name}}" target="_self" download ng-if="!file.entityId" ng-hide="Auth.isDelegateUser() && vm.isRentListFileType(file.type)" class="btn--icon btn--icon-download--dark-turq"><span class="sr-only">Ladda ner</span></a></div></div></td></tr></table></div>');
$templateCache.put('resources/negotiation/show/meetings/show.html','<div ng-controller="ShowNegotiationMeetingsCtrl as meetingsVM"><div><div class="row spacer-mb-small"><div class="col-12 col-lg-7"><div class="alert alert--hide-border alert--squary"><h3 class="text-margin-reset">Dags f\xF6r m\xF6te?</h3><p>Dokumentera de m\xF6ten ni genomf\xF6r genom att f\xF6ra protokoll och ladda upp dem i portalen. Anv\xE4nd g\xE4rna protokollmallen f\xF6r att skriva och signera protokollet.</p><div class="display-flex space-between align-items-center"><div><a href="/docs/Protokollmall.docx" download target="_self" class="link--turquoise link--underline btn--icon btn--icon-download--dark-turq">Ladda ner mall</a></div><button class="btn" ng-hide="Auth.isDelegateUser()" ng-click="meetingsVM.addMeeting()" type="button" ng-disabled="vm.negotiation.isFinished">L\xE4gg till m\xF6te</button></div></div></div><div class="col-12 col-lg-5"><div class="alert alert--narrow-gray alert--squary alert--white-bg" ng-show="meetingsVM.meetings.length === 0"><h3 class="text-margin-top-reset">Inga m\xF6ten har lagts till f\xF6rhandlingen!</h3><p class="spacer-mt-medium text-margin-bottom-reset">F\xF6rhandlingen p\xE5b\xF6rjas n\xE4r det f\xF6rsta f\xF6rhandlingsm\xF6tet genomf\xF6rs. Det \xE4r d\xE5 de 90 dagarna b\xF6rjar r\xE4knas, s\xE5 var noggrann med att fylla i tiden f\xF6r det f\xF6rsta f\xF6rhandlingsm\xF6tet i portalen.</p></div></div></div><hy-progress-circular size="60px" ng-hide="meetingsVM.readyMeetings"></hy-progress-circular><table class="table" ng-show="meetingsVM.meetings.length > 0"><tr class="table__header"><th class="table__th">Titel p\xE5 m\xF6tet</th><th class="table__th">Datum</th><th class="table__th">Tid</th><th class="table__th">Plats</th><th class="table__th">Deltagare</th><th class="table__th">F\xF6rhandlingar</th><th class="table__th">Fritext</th></tr><tr class="table__tr" ng-repeat="meeting in meetingsVM.meetings" ng-click="meetingsVM.gotoShowMeeting(meeting.id)"><td class="table__td table__td--width-15">{{meeting.title}}</td><td class="table__td table__td--width-10">{{meeting.startAt | date:\'yyyy-MM-dd\'}}</td><td class="table__td table__td--width-10">{{meeting.startAt | date:\'shortTime\'}} - {{meeting.endAt | date:\'shortTime\'}}</td><td class="table__td table__td--width-10">{{meeting.location}}</td><td class="table__td table__td--width-20"><div ng-repeat="participant in meeting.participants">{{participant.firstname}} {{participant.lastname}} ({{ participant.party | hyParties }})</div></td><td class="table__td table__td--width-15"><div ng-repeat="negotiation in meeting.negotiations">{{negotiation.name}}</div></td><td class="table__td table__td--width-20">{{meeting.text}}</td></tr></table></div></div>');
$templateCache.put('resources/negotiation/list/my-office/list.html','<div ng-controller="ListNegotiationOfficesCtrl as officesVM"><hy-tableau id="negotiationsMyOfficeTableau" columns="officesVM.tableColumns" default-sorting="caseNumber DESC" f-get-data="officesVM.tableFGetData" f-row-click="officesVM.gotoNegotiation" use-url="true" limit="officesVM.pageLimit" f-get-context-info="vm.getContextInfo" last-params="officesVM.lastParams"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><status>{{$parent.item.combinedState | hyCombinedNegotiationStates}}</status><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && vm.editComment(item)"></comment></hy-tableau></div>');
$templateCache.put('resources/negotiation/show/agreements/show.html','<style>.revert-source {\r\n    background: linear-gradient(180deg, white, white, #DDD);\r\n    margin-top: 20px !important;\r\n  }\r\n  .cancelled {\r\n    background: lightgray;\r\n  }</style><div ng-controller="ShowNegotiationAgreementCtrl as agreementVM"><div class="row"><div class="col col-10 spacer-centered"><div class="alert alert--hide-border alert--centered" ng-hide="Auth.isHgfUser() || vm.negotiation.canSkipRentlist || vm.negotiation.isRentListMissing || vm.negotiation.isFinished || vm.isNewlyConstructedProperty"><h3>L\xE4genhetslista saknas!</h3><p class="spacer-mt-medium text-margin-bottom-reset">Innan f\xF6rhandlingen kan g\xE5 in i slutfasen beh\xF6ver l\xE4genhetslistan laddas upp.</p></div></div><div class="col-12 col-lg-8" ng-show="Auth.canDo(\'negotiationCreateAgreement\') && (Auth.isHgfUser() || vm.negotiation.isRentListMissing || vm.negotiation.isFinished || vm.isNewlyConstructedProperty)"><div class="alert alert--danger alert--hide-border alert--squary"><div ng-show="vm.negotiation.realEstateStatus.notAssignedCount > 0 || vm.isNewlyConstructedProperty"><h3 class="text-margin-reset">Dags att skriva \xF6verenskommelse?</h3><div ng-hide="vm.negotiation.canSkipRentlist" class="spacer-mb-xlarge">N\xE4r ni \xE4r \xF6verens om ny hyresniv\xE5 s\xE5 \xE4r det dags att skapa digital \xF6verenskommelse. Det g\xE5r att v\xE4lja hela eller delar av fastighetsbest\xE5ndet till flera \xF6verenskommelser.</div><div ng-show="vm.negotiation.canSkipRentlist" class="red-text spacer-mb-xlarge">Du kommer nu att skapa en \xF6verenskommelse d\xE4r hyror inte \xE4r inl\xE4sta i databasen. Om du v\xE4ljer att g\xE5 vidare M\xC5STE hyreslista bil\xE4ggas till \xF6verenskommelsen. Du ansvarar vidare f\xF6r att hyrorna blir inl\xE4sta och uppdaterade i Statistikportalen.</div><div><b><p class="red-text">VARNING!</p><p>Om du har flera fastigheter i denna f\xF6rhandling men saknar inl\xE4sta l\xE4genheter i databasen eller inv\xE4ntar en hyreslista f\xF6r vissa av dem:</p></b><p>\xD6nskar du g\xF6ra klart en \xF6verenskommelse f\xF6r en viss fastighet men vid ett senare skede uppdatera \xF6versiktsfliken med \xF6vriga fastigheters l\xE4genheter, beh\xF6ver du skapa en tom \xF6verenskommelse s\xE5 att inte f\xF6rhandlingen avslutas n\xE4r du signerat f\xE4rdigt din f\xF6rsta fastighet.</p><p class="red-text">Du g\xF6r det genom att trycka p\xE5 den gula knappen "Skapa \xF6verenskommelse" och l\xE4mnar den helt tom. G\xE5 tillbaka och tryck p\xE5 den gula knappen "Skapa \xF6verenskommelse" igen och slutf\xF6r din \xF6verenskommelse i den nya \xF6verenskommelse som du skapat.</p></div></div><div class="text-center" ng-hide="vm.negotiation.realEstateStatus.notAssignedCount > 0 || vm.isNewlyConstructedProperty"><h3 class="spacer-mb-xlarge">Inga {{vm.negotiation.canSkipRentlist ? \'fastigheter\' : \'l\xE4genheter\'}} finns att f\xF6rhandla om</h3></div><div class="display-flex space-between align-items-center spacer-mt-xxxlarge"><div></div><div><hy-progress-circular size="20px" ng-show="agreementVM.savingAgreement"></hy-progress-circular><button type="button" ng-show="!Auth.isDelegateUser() &amp;&amp; (vm.negotiation.realEstateStatus.notAssignedCount > 0 || vm.isNewlyConstructedProperty)" ng-click="agreementVM.createAgreement()" class="btn spacer-ml-small" title="{{vm.negotiation.realEstateSummary.lacksDemands > 0 ? \'Saknar yrkande och motiv\': \'\'}}" ng-disabled="vm.negotiation.isFinished || vm.negotiation.realEstateSummary.lacksDemands > 0">{{vm.negotiation.canSkipRentlist ? \'Skapa \xF6verenskommelse utan hyreslista\' : \'Skapa \xF6verenskommelse\'}}</button></div></div></div></div><div class="col col-lg-4" ng-show="Auth.isHgfUser() || vm.negotiation.isRentListMissing || vm.negotiation.isFinished || vm.isNewlyConstructedProperty"><div class="alert alert--narrow-gray alert--squary"><div class="row"><div class="col-12"><h3 class="text-margin-top-reset spacer-mb-medium">{{vm.negotiation.canSkipRentlist ? \'Antal fastigheter\' : \'Antal l\xE4genheter\'}}</h3><div class="display-flex space-between align-items-bottom spacer-mb-xsmall"><div><strong>Totalt</strong></div><div class="display-flex align-items-center"><div class="spacer-mr-small"><strong class="text-margin-reset">{{vm.apartmentsOrProperties}}</strong></div></div></div><div class="display-flex space-between align-items-bottom spacer-mb-xsmall"><div><strong>\xD6verens</strong></div><div class="display-flex align-items-center"><div class="spacer-mr-small"><strong class="text-success">{{vm.negotiation.realEstateStatus.agreedCount}}</strong></div></div></div><div class="display-flex space-between align-items-bottom spacer-mb-xsmall"><div><strong>Strandade</strong></div><div class="display-flex align-items-center"><div class="spacer-mr-small"><strong class="text-danger">{{vm.negotiation.realEstateStatus.blockedCount}}</strong></div></div></div><div class="display-flex space-between align-items-bottom spacer-mb-large"><div><strong>Kvar att f\xF6rhandla</strong></div><div class="display-flex align-items-center"><div class="spacer-mr-small"><strong class="text-gray">{{vm.apartmentsOrProperties - vm.negotiation.realEstateStatus.blockedCount - vm.negotiation.realEstateStatus.agreedCount}}</strong></div></div></div></div><div class="col-12" ng-if="!vm.negotiation.canSkipRentlist"><h3 class="text-margin-top-reset spacer-mb-medium">Antal fastigheter</h3><div class="display-flex space-between align-items-bottom spacer-mb-xsmall"><div><strong>Totalt</strong></div><div class="display-flex align-items-center"><div class="spacer-mr-small"><strong class="text-margin-reset">{{agreementVM.propertySummary.total}}</strong></div></div></div><div class="display-flex space-between align-items-bottom spacer-mb-xsmall"><div><strong>P\xE5g\xE5ende F\xD6K</strong></div><div class="display-flex align-items-center"><div class="spacer-mr-small"><strong class="text-success">{{agreementVM.propertySummary.inAgreement}}</strong></div></div></div><div class="display-flex space-between align-items-bottom spacer-mb-xsmall"><div><strong>\xD6verens</strong></div><div class="display-flex align-items-center"><div class="spacer-mr-small"><strong class="text-success">{{agreementVM.propertySummary.inAgreementFinished}}</strong></div></div></div><div class="display-flex space-between align-items-bottom spacer-mb-large"><div><strong>Kvar att f\xF6rhandla</strong></div><div class="display-flex align-items-center"><div class="spacer-mr-small"><strong class="text-gray">{{agreementVM.propertySummary.left}}</strong></div></div></div></div><div class="col-12"><div class="display-flex justify-content-end flex-grow-1"><button type="button" ng-show="Auth.isExternalUser() && Auth.canDo(\'finishNegotiation\')" ng-disabled="!vm.negotiation.finishable.flag" ng-click="vm.finish()" class="btn btn--secondary">Avsluta f\xF6rhandling</button> <button type="button" ng-show="!vm.negotiation.isFinished && Auth.isHgfUser() && Auth.canDo(\'finishNegotiation\')" ng-disabled="vm.negotiation.isFinished || vm.unfinishedAgreementsCount > 0" ng-click="vm.finishAsHgfUser()" class="btn btn--secondary">Avsluta f\xF6rhandling</button> <button type="button" ng-show="vm.negotiation.isFinished && Auth.canDo(\'reopenNegotiation\')" ng-click="vm.reopen()" class="btn">\xC5ter\xF6ppna f\xF6rhandling</button></div></div></div></div></div><hy-progress-circular size="60px" ng-hide="agreementVM.readyAgreements &amp;&amp; agreementVM.readyBlockings"></hy-progress-circular><div class="col-12 spacer-mt-large" ng-show="agreementVM.readyAgreements &amp;&amp; agreementVM.agreements.length > 0"><h3>\xD6verenskommelser</h3><div class="spacer-mt-medium spacer-mb-medium alert alert--padding-large alert--narrow-gray alert--squary" ng-class="{ \'spacer-margin-reset\': agreement.conditionTags.revertSourceId || agreement.conditionTags.revertId, \r\n                       \'revert-source\': agreement.conditionTags.revertId,\r\n                       \'cancelled\': agreement.conditionTags.cancelled }" ng-repeat="agreement in agreementVM.agreements"><ng-include ng-if="!vm.isNewlyConstructedProperty" src="\'resources/negotiation/show/agreements/partials/_agreement.html\'"></ng-include><ng-include ng-if="vm.isNewlyConstructedProperty" src="\'resources/negotiation/show/agreements/partials/_agreementNewProduction.html\'"></ng-include><div class="row"><div class="col col-sm-12"><div class="display-flex space-between align-items-bottom"><div><span ng-if="agreement.conditionTags.revertSourceId" class="red-text"><b>R\xE4ttelse</b></span> Skapad: {{ agreement.createdAt | date : "yyyy-MM-dd" }} av {{ agreement.creator.fullName }}</div><div class="display-flex flex-direction-column align-items-bottom"><span ng-if="agreement.signed">{{ agreement.cancelContraindications }}</span><hy-progress-circular size="20px" ng-if="agreementVM.busyRevertingAgreement"></hy-progress-circular><button ng-if="!agreementVM.busyRevertingAgreement && Auth.canDo(\'cancelAgreement\') && \r\n                               !vm.negotiation.isFinished &&\r\n                               agreement.signed && !agreement.conditionTags.cancelled && !agreement.cancelContraindications" type="button" ng-click="agreementVM.cancelAgreement(agreement.id)" title="Ers\xE4tt \xF6verenskommelse med ny \xF6verenskommelse" class="btn btn--secondary spacer-mb-small">Annullera</button> <button type="button" ng-click="agreementVM.gotoShowAgreement(agreement.id)" class="btn">\xD6ppna</button></div></div></div></div></div></div><div class="col-12 spacer-mt-xsmall" ng-show="agreementVM.readyBlockings &amp;&amp; agreementVM.blockings.length > 0"><h3 class="text-margin-reset">Strandade</h3><div class="spacer-mt-medium alert alert--pink-dark-bg alert--hide-border alert--squary" ng-repeat="blocking in agreementVM.blockings"><div class="row"><div class="col-3"><strong>Fastighetsbest\xE5nd: </strong>{{vm.negotiation.canSkipRentlist ? blocking.numProperties : blocking.numApartments}} {{ vm.negotiation.canSkipRentlist ? blocking.numProperties === 1 ? \'fastighet\' : \'fastigheter\' : blocking.numApartments === 1 ? \'l\xE4genhet\' : \'l\xE4genheter\'}}</div><div class="col-5"><strong>Status: </strong><span ng-show="(agreementVM.currentUserParty === blocking.creatorParty) &amp;&amp; !(blocking.isAgreed)">V\xE4ntar p\xE5 att motparten ska bekr\xE4fta strandningen </span><span ng-show="((agreementVM.currentUserParty != blocking.creatorParty) &amp;&amp; !(blocking.isAgreed))">Var god bekr\xE4fta strandningen </span><span ng-show="blocking.isAgreed">Bekr\xE4ftad</span> <span ng-show="!blocking.isAgreed"><span ng-show="blocking.agreedByAssociation">; bekr\xE4ftad av HGF</span> <span ng-show="blocking.agreedByOwner">; bekr\xE4ftad av motpart</span></span></div><div class="col-4"><strong>Skapad: </strong>{{ blocking.createdAt | date : \'yyyy-MM-dd\' }} av {{ blocking.creator.fullName }} <strong class="red-text spacer-ml-small" ng-if="agreement.conditionTags.cancelled">Annullerad</strong></div></div><div class="row spacer-mt-medium"><div class="col"><strong>Strandningsinstans: </strong>{{blocking.blockedBy | agreementBlockedByType}}</div><div class="col text-right" ng-hide="Auth.isDelegateUser()"><hy-progress-circular size="20px" ng-show="agreementVM.busy"></hy-progress-circular><button class="btn btn--info spacer-mr-small" ng-click="agreementVM.getBlockingConfirmation(blocking)" ng-disabled="agreementVM.busy">Bekr\xE4ftelse PDF</button> <button type="button" ng-if="Auth.canDo(\'acceptBlockedAgreement\')" class="btn spacer-mr-small" ng-click="agreementVM.acceptBlockedAgreement(blocking)" ng-hide="blocking.isAgreed || agreementVM.currentUserParty === blocking.creatorParty" ng-disabled="vm.negotiation.isFinished || (blocking.agreedByAssociation && blocking.agreedByOwner)">Bekr\xE4fta</button> <button class="btn spacer-mr-small" ng-if="Auth.canDo(\'resumeNegotiation\')" ng-click="agreementVM.resume($event, blocking);" ng-show="agreementVM.currentUserParty === blocking.creatorParty" ng-disabled="vm.negotiation.isFinished">\xC5teruppta</button> <button type="button" ng-click="agreementVM.gotoShowBlocking(blocking.id)" class="btn">\xD6ppna</button></div></div></div></div><div ng-if="Auth.canDo(\'createBlocking\')" class="col-6 col-lg-4 push-6 push-lg-8 spacer-mt-medium" ng-show="!Auth.isDelegateUser() &amp;&amp; vm.negotiation.realEstateStatus.notAssignedCount > 0\r\n         &amp;&amp; !vm.negotiation.isFinished &amp;&amp; !vm.isNewlyConstructedProperty"><div class="alert alert--white-bg alert--hide-border alert--squary"><h4 class="text-margin-reset text-reset"><strong>Kommer ni inte \xF6verens?</strong></h4><p>Stranda det fastighetsbest\xE5nd som ni ej \xE4r \xF6verens om. Ut\xF6ver att markera best\xE5ndet som strandat i portalen ska strandningen ske p\xE5 vanligt vis utanf\xF6r portalen genom att bl a skicka inlaga till den aktuella instansen.</p><a ng-click="agreementVM.blockAgreement()" class="link--turquoise link--underline">Stranda{{vm.negotiation.canSkipRentlist ? \' F\xF6rhandling f\xF6r fastigheter\' : \'\'}}</a></div></div></div></div>');
$templateCache.put('resources/negotiation/show/motiv/show.html','<div ng-controller="ShowNegotiationMotivCtrl as motivVM"><hy-progress-circular size="60px" ng-hide="motivVM.readyProgressCircular || vm.isNewlyConstructedApartment"></hy-progress-circular><div class="row" ng-if="vm.isNewlyConstructedApartment"><div class="col col-10 spacer-centered"><div class="alert alert--hide-border alert--centered"><h3>Yrkande och motiv</h3><p class="spacer-mt-medium text-margin-bottom-reset">Yrkande och motiv kan anges genom att ladda upp en textfil under dokument</p></div></div></div><div ng-show="motivVM.readyProgressCircular &amp;&amp; !vm.isNewlyConstructedApartment"><div class="row"><div class="col-12"><div class="spacer-mb-medium display-flex space-between" ng-hide="Auth.isDelegateUser()"><p class="spacer-mt-reset">{{motivVM.editMode ? \'Markera de fastigheter nedan till vilka du vill l\xE4gga till yrkande och motiv. Tryck sedan \\\'Ange yrkande &amp; motiv\\\'. N\xE4r du \xE4r klar, tryck p\xE5 \\\'Spara\\\'.\': \'H\xE4r anger ni ett f\xF6rsta yrkande och motiv, g\xE4rna innan f\xF6rsta f\xF6rhandlingsm\xF6tet. De yrkanden och motiv som kommer in l\xF6pande i f\xF6rhandlingen kan skrivas i m\xF6tesprotokollet.\'}}</p><div ng-hide="motivVM.editMode || !motivVM.realEstates.length"><button class="btn" ng-click="motivVM.editMode = !motivVM.editMode" ng-disabled="vm.negotiation.isFinished">L\xE4gg till yrkande &amp; motiv</button></div></div><div class="sort-container" ng-class="{\'spacer-mt-large\' : Auth.isDelegateUser()}"><hy-sorter list-columns="motivVM.listColumns" order="motivVM.sortColumn" direction="motivVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div><div ng-show="vm.apartmentMode"><ng-include src="\'resources/negotiation/show/motiv/_apartment-table.html\'"></ng-include></div><div ng-hide="vm.apartmentMode"><ng-include src="\'resources/negotiation/show/motiv/_property-table.html\'"></ng-include></div><div class="text-right spacer-mt-medium spacer-mb-medium" ng-show="motivVM.totalCount > 0 &amp;&amp; motivVM.totalCount <= motivVM.pageLimit"><strong>Totalt: </strong>{{motivVM.totalCount}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="motivVM.totalCount === 0">Ingen fastighet hittades.</div></div></div><div class="row" ng-hide="Auth.isDelegateUser()"><div class="col text-right" ng-hide="motivVM.editMode || !motivVM.realEstates.length"><button class="btn" ng-click="motivVM.editMode = !motivVM.editMode;" ng-disabled="vm.negotiation.isFinished">L\xE4gg till yrkande &amp; motiv</button></div><div class="col text-right" ng-show="motivVM.editMode"><hy-progress-circular size="20px" ng-show="motivVM.saving"></hy-progress-circular><button class="btn spacer-mr-small" ng-click="motivVM.saveProperties()" ng-disabled="motivVM.saving">Spara</button> <button class="btn spacer-mr-small" ng-click="motivVM.editDemandAndMotivation()" ng-disabled="!motivVM.atLeastOneSelectedPropertyId()">Ange yrkande &amp; motiv</button> <a class="link--turquoise link--no-underline" ng-click="motivVM.editMode = !motivVM.editMode; motivVM.queryRealEstate(true)">Avbryt</a></div></div></div></div>');
$templateCache.put('resources/negotiation/show/motiv/_apartment-table.html','<table class="table table--margin-reset"><thead class="table__header"><tr><th ng-if="motivVM.editMode" class="table__th">Alla</th><th class="table__th">Fastighetsbeteckning</th><th class="table__th">Gatuadress</th><th class="table__th">Nationella L\xE4genhetsnr</th><th class="table__th">L\xE4genhetsnr</th><th class="table__th">Kommun</th><th class="table__th">Fastighets\xE4gare</th><th class="table__th">Yrkande</th><th class="table__th">Motiv</th></tr><tr><th class="table__th table__td--width-5" ng-if="motivVM.editMode"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="allPropertiesBox" ng-model="motivVM.allPropertiesBoxChecked" ng-change="motivVM.toggleAllPropertiesSelection()"> <label class="custom-control__label" for="allPropertiesBox"></label></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="motivVM.inputTextProperty" hy-enter="motivVM.queryRealEstate(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="motivVM.queryRealEstate(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="motivVM.inputTextStreetAddress" hy-enter="motivVM.queryRealEstate(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="motivVM.queryRealEstate(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="motivVM.inputTextNationalNumber" hy-enter="motivVM.queryRealEstate(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="motivVM.queryRealEstate(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="motivVM.inputTextApartmentNumber" hy-enter="motivVM.queryRealEstate(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="motivVM.queryRealEstate(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="motivVM.inputTextMunicipality" hy-enter="motivVM.queryRealEstate(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="motivVM.queryRealEstate(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="motivVM.inputTextOwner" hy-enter="motivVM.queryRealEstate(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="motivVM.queryRealEstate(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-25"></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="apartment in motivVM.realEstates | startFrom: motivVM.currentPage * motivVM.pageLimit | limitTo: motivVM.pageLimit" ng-class="{\'table__tr--muted\' : !motivVM.ready}"><td class="table__td table__td--minimalistic" ng-show="motivVM.editMode"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{apartment.id}}" ng-model="motivVM.selectedPropertyIds[apartment.id]" ng-change="motivVM.updateAllPropertiesBoxState()"> <label class="custom-control__label" for="{{apartment.id}}"></label></div></td><td class="table__td table__td--minimalistic">{{ apartment.property.propertyDesignation }}</td><td class="table__td table__td--minimalistic">{{ apartment.streetAddress }}</td><td class="table__td table__td--minimalistic table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{ apartment.nationalNo | nationalNoControl }}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{ apartment.ownerNo }}</td><td class="table__td table__td--minimalistic">{{ apartment.municipality }}</td><td class="table__td table__td--minimalistic"><div ng-show="apartment.numOwners === 1 &amp;&amp; apartment.primaryOwner.name !== null">{{apartment.primaryOwner.name}}</div><div ng-show="apartment.numOwners === 1 &amp;&amp; apartment.primaryOwner.name === null">{{apartment.owners[0].name}}</div><div ng-show="apartment.numOwners > 1"><div class="spacer-mb-xsmall"><a href="#" class="btn--icon btn--icon-expand ellipsis" ng-class="!motivVM.toggleOwnersList[apartment.id] ? \'btn--icon-expand\' : \'btn--icon-collapse\'" ng-click="motivVM.toggleOwnersList[apartment.id]=!motivVM.toggleOwnersList[apartment.id]; motivVM.getPropertyOwners(apartment.id); $event.preventDefault();"><span>{{apartment.primaryOwner.name || \'Fakturamottagare ok\xE4nd\'}}</span></a></div><div ng-show="motivVM.toggleOwnersList[apartment.id]"><div ng-hide="motivVM.readyOwners[apartment.id]"><hy-progress-circular size="20px"></hy-progress-circular></div><div ng-repeat="owner in apartment.owners">{{owner.name}}</div></div></div></td><td class="table__td table__td--minimalistic table__td--nowrap"><span ng-show="apartment.demand.unit === \'2\'">{{apartment.demand.stringValue}} </span><span ng-hide="apartment.demand.unit === \'2\'">{{apartment.demand.numericValue | number}} <span ng-show="apartment.demand.numericValue">{{apartment.demand.unit | demandTypeToDescription}}</span></span></td><td class="table__td table__td--minimalistic">{{ apartment.demand.motivation }}</td></tr></table><div class="row spacer-mt-medium spacer-mb-medium" ng-if="motivVM.totalCount > motivVM.pageLimit"><div class="col-12 display-flex justify-content-end"><div><strong>{{ motivVM.firstRecordInCurrentPage }}-{{ motivVM.computeLastRecord() }}</strong> av <strong>{{ motivVM.totalCount }}</strong> <button class="btn btn--paginator-left" type="button" ng-disabled="motivVM.firstRecordInCurrentPage === 1" ng-click="motivVM.goToPrevPage()"></button> <button class="btn btn--paginator-right" type="button" ng-disabled="motivVM.computeLastRecord() >= motivVM.totalCount" ng-click="motivVM.goToNextPage()"></button></div></div></div>');
$templateCache.put('resources/negotiation/show/motiv/_property-table.html','<table class="table table--margin-reset"><thead class="table__header"><tr><th ng-if="motivVM.editMode" class="table__th">Alla</th><th class="table__th">Fastighetsbeteckning</th><th class="table__th">Kommun</th><th class="table__th">Antal l\xE4genheter i f\xF6rhandling</th><th class="table__th">Fastighets\xE4gare</th><th class="table__th">Yrkande</th><th class="table__th">Motiv</th></tr><tr><th class="table__th table__td--width-5" ng-if="motivVM.editMode"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="allPropertiesBox" ng-model="motivVM.allPropertiesBoxChecked" ng-change="motivVM.toggleAllPropertiesSelection()"> <label class="custom-control__label" for="allPropertiesBox"></label></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="motivVM.inputTextProperty" hy-enter="motivVM.queryRealEstate(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="motivVM.queryRealEstate(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="motivVM.inputTextMunicipality" hy-enter="motivVM.queryRealEstate(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="motivVM.queryRealEstate(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="motivVM.inputTextOwner" hy-enter="motivVM.queryRealEstate(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="motivVM.queryRealEstate(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-25"></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="property in motivVM.realEstates | startFrom: motivVM.pageLimit * motivVM.currentPage | limitTo: motivVM.pageLimit" ng-class="{\'table__tr--muted\' : !motivVM.ready}"><td class="table__td table__td--minimalistic" ng-show="motivVM.editMode"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{property.id}}" ng-model="motivVM.selectedPropertyIds[property.id]" ng-change="motivVM.updateAllPropertiesBoxState()"> <label class="custom-control__label" for="{{property.id}}"></label></div></td><td class="table__td table__td--minimalistic">{{property.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{property.municipality}}</td><td class="table__td table__td--minimalistic"><div ng-show="vm.negotiation.realEstateSummary.lacksRentList === 0">{{property.numNegotiatedApartments}}</div><div ng-hide="vm.negotiation.realEstateSummary.lacksRentList === 0">ej angivet</div></td><td class="table__td table__td--minimalistic"><hy-array-display model="property.owners"></hy-array-display></td><td class="table__td table__td--minimalistic"><span ng-show="property.demand.unit === \'2\'">{{property.demand.stringValue}} </span><span ng-hide="property.demand.unit === \'2\'">{{property.demand.numericValue | number}} <span ng-show="property.demand.numericValue">{{property.demand.unit | demandTypeToDescription}}</span></span></td><td class="table__td table__td--minimalistic">{{ property.demand.motivation }}</td></tr></table><div class="row spacer-mt-medium spacer-mb-medium" ng-if="motivVM.totalCount > motivVM.pageLimit"><div class="col-12 display-flex justify-content-end"><div><strong>{{ motivVM.firstRecordInCurrentPage }}-{{ motivVM.computeLastRecord() }}</strong> av <strong>{{ motivVM.totalCount }}</strong> <button class="btn btn--paginator-left" type="button" ng-disabled="motivVM.firstRecordInCurrentPage === 1" ng-click="motivVM.goToPrevPage()"></button> <button class="btn btn--paginator-right" type="button" ng-disabled="motivVM.computeLastRecord() >= motivVM.totalCount" ng-click="motivVM.goToNextPage()"></button></div></div></div>');
$templateCache.put('resources/negotiation/show/overview/show.html','<style>.table__tr--slime {\r\n    background-color: lightseagreen !important;\r\n    color: white;\r\n  }</style><div ng-controller="ShowNegotiationOverviewCtrl as overviewVM"><div class="row" ng-hide="Auth.isDelegateUser()"><div class="col-12 col-lg-8"><ng-include src="\'resources/negotiation/show/partials/_rentListBanner.html\'"></ng-include><div class="display-flex align-items-center spacer-mt-small" ng-if="!overviewVM.isNewProduction(vm.negotiation.type)"><button ng-if="Auth.canDo(\'editDemands\')" class="btn spacer-ml-medium spacer-mr-small" ng-click="vm.gotoMotiv()">Yrkande & motiv</button><div ng-if="vm.negotiation.realEstateSummary.lacksDemands > 0" class="display-flex"><div class="icon icon--message-warning spacer-mr-small"></div>Saknar yrkande och motiv</div><div ng-if="vm.negotiation.realEstateSummary.lacksDemands === 0" class="display-flex"><div class="icon icon--ticked-in-circle spacer-mr-small"></div>Yrkande och motiv klart</div></div></div></div><div class="row" ng-class="Auth.isDelegateUser() ? \'spacer-mt-xxlarge\' : \'spacer-mt-large\'"><div class="col-12 col-lg-8"><h2 class="text-margin-top-reset spacer-mb-small" ng-class="{\'spacer-mt-medium\' : vm.isNewlyConstructedApartment}">Information fr\xE5n framst\xE4llan</h2><div class="alert alert--narrow-gray alert--squary"><div class="row"><div class="col"><div class="display-flex"><h4 class="text-margin-bottom-reset text-margin-top-reset text-lowercase spacer-mr-xsmall">Namn</h4><a href="#" ng-if="Auth.canDo(\'editNegotiation\')" class="btn--icon btn--icon-pen btn--icon-bottom-right" ng-hide="vm.negotiation.isFinished" ng-click="overviewVM.editNegotiation(\'sm\'); $event.preventDefault();"><span class="sr-only">Redigera</span></a></div>{{vm.negotiation.name}}<div class="display-flex"><h4 class="text-margin-bottom-reset text-lowercase spacer-mr-xsmall">Typ av f\xF6rhandling</h4><a href="#" ng-if="Auth.canDo(\'editNegotiation\')" class="btn--icon btn--icon-pen btn--icon-bottom-right" ng-hide="vm.negotiation.isFinished" ng-click="overviewVM.editNegotiation(\'sm\'); $event.preventDefault();"><span class="sr-only">Redigera</span></a></div>{{vm.negotiation.type | hyNegotiationTypes}}<h4 class="text-margin-bottom-reset text-lowercase">\xD6nskat datum f\xF6r hyresf\xF6r\xE4ndring</h4>{{vm.negotiation.rentEffectiveFrom | date : "yyyy-MM-dd"}}<div class="spacer-mt-medium" ng-hide="Auth.isDelegateUser()"><a href="/negotiation-calls/{{vm.negotiation.callId}}?view=negotiations" class="link--turquoise link--underline" ng-if="vm.negotiation.callState !== vm.NegotiationCallStates.AUXILARY">F\xF6rhandlingsframst\xE4llan</a></div><h4 class="text-margin-bottom-reset text-lowercase">\xD6nskat datum f\xF6r f\xF6rhandling</h4>{{vm.negotiation.negotiationDate | date : "yyyy-MM-dd"}}<h4 class="text-margin-bottom-reset text-lowercase">\xD6nskad plats f\xF6r f\xF6rhandling</h4>{{vm.negotiation.negotiationLocation}}</div><div class="col"><h4 class="text-margin-reset text-lowercase">Meddelande till Hyresg\xE4stf\xF6reningen</h4><span class="word-break-balanced" ng-if="vm.negotiation.message" ng-bind-html="vm.negotiation.message | preventDateBreak"></span> <span class="word-break-balanced" ng-if="!vm.negotiation.message">Ej angivet</span><h4 class="text-margin-bottom-reset text-lowercase">F\xF6rhandlingss\xE4tt</h4><span class="word-break-all">{{vm.negotiation.allowPartial ? \'Fastighetsf\xF6rhandling\' : \'\xC4garf\xF6rhandling\'}}</span><h4 class="text-margin-bottom-reset text-lowercase">Skapad den</h4>{{vm.negotiation.createdAt | date : "yyyy-MM-dd"}}<div ng-if="Auth.isHgfUser()"><h4 class="text-margin-bottom-reset text-lowercase">Ankomstdatum till HGF</h4>{{vm.negotiation.incomingDate | date : "yyyy-MM-dd"}}</div><div ng-if="vm.isNewlyConstructedApartment"><h4 class="text-margin-bottom-reset text-lowercase">Valda befintliga fastigheter</h4><hy-array-display model="overviewVM.properties"></hy-array-display></div></div></div></div></div><div class="col-12 col-lg-4"><div class="info"><label class="info__title--edit"><h4 class="text-margin-bottom-reset"><div class="icon spacer-ml-small icon--party icon--party-hgf icon--tiny di"></div>Representanter Hyresg\xE4stf\xF6reningen</h4><a href="#" ng-if="Auth.canDo(\'addParticipants\')" ng-class="Auth.isHgfUser() ? \'btn--icon-pen\' : \'btn--icon-eye\'" class="btn--icon btn--icon-bottom-right" ng-hide="vm.negotiation.isFinished" ng-click="overviewVM.edit(\'md\', overviewVM.Parties.HGF); $event.preventDefault();"><span class="sr-only">Redigera</span></a></label><div class="alert alert--narrow-gray alert--squary alert--slim alert--white-bg alert--mb-small"><div class="display-flex flex-direction-row space-between"><span ng-if="vm.negotiation.hgfSide.length === 0">Inga f\xF6rhandlare \xE4nnu</span><div ng-hide="vm.negotiation.hgfSide.length === 0"><a href="/users/{{vm.negotiation.chiefNegotiatorHGF.id}}" class="link--turquoise link--underline" ng-hide="!Auth.isHgfUser()">{{vm.negotiation.chiefNegotiatorHGF.fullName}}</a> <span ng-show="!Auth.isHgfUser()">{{vm.negotiation.chiefNegotiatorHGF.fullName}}</span> <span>(<em>Ansvarig f\xF6rhandlare</em>)</span></div><a href="#" class="btn--icon btn--icon-expand ellipsis" ng-hide="vm.negotiation.hgfSide.length < 2" ng-class="!overviewVM.toggleOwnersList[0] ? \'btn--icon-expand\' : \'btn--icon-collapse\'" ng-click="overviewVM.toggleOwnersList[0]=!overviewVM.toggleOwnersList[0]; overviewVM.toggleOwnersList[1]=false; overviewVM.toggleOwnersList[2]=false; $event.preventDefault();"></a></div><div ng-repeat="participant in vm.negotiation.hgfSide | filter: {isChief: false}" ng-show="overviewVM.toggleOwnersList[0]"><a href="/users/{{participant.id}}" class="link--turquoise link--underline" ng-hide="!Auth.isHgfUser()">{{participant.fullName}}</a> <span ng-show="!Auth.isHgfUser()">{{participant.fullName}}</span> <span>(<em>F\xF6rhandlare</em>)</span> <span ng-show="vm.negotiation.hgfParticipants.length === 0">Representanter saknas</span></div><div ng-repeat="participant in vm.negotiation.delegationInvitations | filter: {roleObj: {party: 20}}"><span>{{participant.email}} ({{participant.roleObj.name}})</span></div></div></div><div class="info"><label class="info__title--edit"><h4 class="text-margin-bottom-reset"><div class="icon spacer-ml-small icon--party icon--party-fa icon--tiny di"></div>Representanter hyresv\xE4rd</h4><a href="#" ng-if="Auth.canDo(\'addParticipants\')" class="btn--icon-pen btn--icon btn--icon-bottom-right" ng-hide="vm.negotiation.isFinished" ng-click="overviewVM.edit(\'md\', overviewVM.Parties.EXTERNAL); $event.preventDefault();"><span class="sr-only">Redigera</span></a></label><div class="alert alert--narrow-gray alert--squary alert--slim alert--white-bg alert--mb-small"><div class="display-flex flex-direction-row space-between"><span ng-if="vm.negotiation.faSide.length === 0">Inga f\xF6rhandlare \xE4nnu</span><div ng-hide="vm.negotiation.faSide.length === 0"><a href="/users/{{vm.negotiation.chiefNegotiatorExternal.id}}" class="link--turquoise link--underline" ng-hide="Auth.isDelegateUser()">{{vm.negotiation.chiefNegotiatorExternal.fullName}}</a> <span ng-show="Auth.isDelegateUser()">{{vm.negotiation.chiefNegotiatorExternal.fullName}}</span> <span>(<em>Ansvarig f\xF6rhandlare</em>)</span></div><a href="#" class="btn--icon btn--icon-expand ellipsis" ng-hide="vm.negotiation.faSide.length < 2" ng-class="!overviewVM.toggleOwnersList[1] ? \'btn--icon-expand\' : \'btn--icon-collapse\'" ng-click="overviewVM.toggleOwnersList[1]=!overviewVM.toggleOwnersList[1]; overviewVM.toggleOwnersList[0]=false; overviewVM.toggleOwnersList[2]=false; $event.preventDefault();"></a></div><div ng-repeat="participant in vm.negotiation.faSide | filter: {isChief: false}" ng-show="overviewVM.toggleOwnersList[1]"><a href="/users/{{participant.id}}" class="link--turquoise link--underline" ng-hide="Auth.isDelegateUser()">{{participant.fullName}}</a> <span ng-show="Auth.isDelegateUser()">{{participant.fullName}}</span> <span>(<em>F\xF6rhandlare</em>)</span> <span ng-show="vm.negotiation.faSide.length === 0">Representanter saknas</span></div><div ng-repeat="participant in vm.negotiation.delegationInvitations | filter: {roleObj: {party: \'!20\'}}"><span>{{participant.email}} ({{participant.roleObj.name}})</span></div></div></div><div ng-hide="true" class="info"><label class="info__title--edit"><h4 class="text-margin-bottom-reset">Representanter F\xF6rhandlardelegation</h4><a href="#" ng-class="Auth.isDelegateUser() ? \'btn--icon-eye\' : \'btn--icon-pen\'" class="btn--icon btn--icon-bottom-right" ng-hide="vm.negotiation.isFinished || (Auth.isDelegateUser() &amp;&amp; vm.negotiation.delegationParticipants.length === 0)" ng-click="overviewVM.editDelegates(\'md\'); $event.preventDefault();"><span class="sr-only">Redigera</span></a></label><div class="alert alert--narrow-gray alert--squary alert--slim alert--white-bg"><div class="display-flex flex-direction-row space-between"><span ng-if="vm.negotiation.delegationParticipants.length === 0">Inga f\xF6rhandlare \xE4nnu</span><div ng-hide="vm.negotiation.delegationParticipants.length === 0"><a href="/users/{{vm.negotiation.delegationParticipants[0].id}}" class="link--turquoise link--underline" ng-hide="!Auth.isHgfUser()">{{vm.negotiation.delegationParticipants[0].fullName}} </a><span ng-show="!Auth.isHgfUser()">{{vm.negotiation.delegationParticipants[0].fullName}}</span></div><a href="#" class="btn--icon btn--icon-expand ellipsis" ng-hide="vm.negotiation.delegationParticipants.length < 2" ng-class="!overviewVM.toggleOwnersList[2] ? \'btn--icon-expand\' : \'btn--icon-collapse\'" ng-click="overviewVM.toggleOwnersList[2] = !overviewVM.toggleOwnersList[2]; overviewVM.toggleOwnersList[0] = false; overviewVM.toggleOwnersList[1] = false; $event.preventDefault();"></a></div><div ng-repeat="participant in vm.negotiation.delegationParticipants" ng-show="!$first &amp;&amp; overviewVM.toggleOwnersList[2]"><a href="/users/{{participant.id}}" class="link--turquoise link--underline" ng-hide="!Auth.isHgfUser()">{{participant.fullName}}</a> <span ng-show="!Auth.isHgfUser()">{{participant.fullName}}</span> <span ng-show="vm.negotiation.delegationParticipantss.length === 0">Delegater saknas</span></div></div></div><div class="info" ng-if="vm.negotiation.rentlistStatus && !vm.negotiation.rentlistStatus.missing"><label class="info__title--edit"><h4 class="text-margin-bottom-reset">Aktuell hyra</h4></label> <a href="/api/negotiations/{{vm.negotiation.id}}/export-rent-list" target="_self" download class="link--turquoise btn--icon btn--icon-download--dark-turq">H\xE4mta hyreslista</a></div></div></div><div class="row spacer-mt-large"><div class="col"><h2 class="text-margin-reset">Best\xE5nd</h2><hy-tabcontrol tablist="overviewVM.bestandTabs" current-tab="overviewVM.bestandCurrentTab"></hy-tabcontrol><div ng-show="overviewVM.bestandCurrentTab === \'properties\'"><hy-tableau id="negotiationTableauProperties" columns="overviewVM.columnsProperties" f-get-data="overviewVM.fGetProperties" use-url="false" checkboxes="overviewVM.editMode" default-sorting="propertyDesignation" selected-rows="overviewVM.selectedRows" f-row-click="overviewVM.gotoProperty" f-get-context-info="overviewVM.getContextInfo" id-property="id" in-memory="true" in-memory-excessive-limit="overviewVM.Limits.InMemoryMiddleLimit" info-text="{{ !overviewVM.editMode ? \'Tryck p\xE5 \xC4ndra i best\xE5ndet knappen f\xF6r att l\xE4gga till eller ta bort \' + (vm.apartmentMode ? \'l\xE4genheter\' : \'fastigheter\') : \'\' }}" limit="overviewVM.pageLimit" attributes="overviewVM.attributesProperties" actions="overviewVM.actionsProperties"></hy-tableau><div ng-if="!vm.negotiation.isFinished" class="display-flex space-between spacer-mt-small"><div>Ladda <a class="link--turquoise link--no-underline" ng-click="overviewVM.exportRealEstate()">ner</a> fastighetlista</div><div><button ng-if="!overviewVM.editMode" class="btn" ng-click="overviewVM.editMode = true">\xC4ndra i best\xE5ndet</button> <a ng-if="overviewVM.editMode" class="link--turquoise link--no-underline" ng-click="overviewVM.editMode = false">Avbryt</a></div></div></div><div ng-show="overviewVM.bestandCurrentTab === \'apartments\'"><hy-tableau id="negotiationTableauApartments" columns="overviewVM.columnsApartments" f-get-data="overviewVM.fGetApartments" use-url="false" checkboxes="overviewVM.editMode" default-sorting="streetAddress" selected-rows="overviewVM.selectedRows" id-property="id" in-memory="true" in-memory-excessive-limit="overviewVM.Limits.InMemoryMiddleLimit" limit="overviewVM.pageLimit" attributes="overviewVM.attributesApartments" actions="overviewVM.actionsApartments"></hy-tableau><div ng-if="!vm.negotiation.isFinished" class="display-flex space-between spacer-mt-small"><div></div><div><button ng-if="!overviewVM.editMode" class="btn" ng-click="overviewVM.editMode = true">\xC4ndra i best\xE5ndet</button> <a ng-if="overviewVM.editMode" class="link--turquoise link--no-underline" ng-click="overviewVM.editMode = false">Avbryt</a></div></div></div></div></div></div>');
$templateCache.put('resources/negotiation/show/partials/_rentListBanner.html','<hy-progress-circular size="60px" center-on-page ng-if="vm.busy"></hy-progress-circular><div ng-if="!vm.isNewlyConstructedProperty"><div class="alert alert--white-bg alert--slim" ng-if="Auth.canDo(\'forgeRentlist\') || Auth.canDo(\'unforgeRentlist\') || Auth.canDo(\'importRentlist\') || Auth.canDo(\'canSkipRentlist\')" ng-class="!vm.negotiation.canSkipRentlist && vm.negotiation.rentlistStatus.missing ? \'alert--warning-bordered\' : \'alert--success-bordered\' "><div class="display-flex space-between align-items-center"><div class="spacer-mr-small" style="width: 290px"><hy-amend-button ng-if="Auth.canDo(\'forgeRentlist\') && !vm.isNewlyConstructedApartment" title="V\xE4lj l\xE4genheter" amend-title="{{ vm.negotiation.rentlistStatus.agreementsStarted ? \'\xD6verenskommelse har p\xE5b\xF6rjats\' : \'L\xE4gg till l\xE4genheter i listan nedanf\xF6r\' }}" normal-click="vm.forgeRentlist()" amend-click="vm.forgeRentlist(true)" disabled="vm.negotiation.canSkipRentlist || vm.negotiation.isFinished || vm.negotiation.rentlistStatus.agreementsStarted" amend-disabled="vm.negotiation.canSkipRentlist || vm.negotiation.isFinished"></hy-amend-button><hy-amend-button ng-if="Auth.canDo(\'importRentlist\')" title="Ladda upp {{vm.isNewlyConstructedApartment ? \'l\xE4genhetslista\': \'hyreslista\'}}" amend-title="{{ vm.negotiation.rentlistStatus.agreementsStarted ? \'\xD6verenskommelse har p\xE5b\xF6rjats\' : \'L\xE4gg till l\xE4genheter i listan nedanf\xF6r\' }}" button-class="btn--icon btn--icon-upload" normal-click="vm.importRentlist()" amend-click="vm.importRentlist(true)" disabled="vm.negotiation.canSkipRentlist || vm.negotiation.isFinished || vm.negotiation.rentlistStatus.agreementsStarted" amend-disabled="vm.negotiation.canSkipRentlist || vm.negotiation.isFinished"></hy-amend-button><button ng-if="Auth.canDo(\'unforgeRentlist\') && (vm.negotiation.realEstateSummary.apartments > 0) && !(vm.negotiation.canSkipRentlist || vm.negotiation.isFinished || vm.negotiation.rentlistStatus.agreementsStarted)" class="btn btn--secondary spacer-mt-xsmall" ng-click="vm.unforgeRentlist()">Avv\xE4lj l\xE4genheter</button> <button ng-if="Auth.canDo(\'unforgeRentlist\') && \r\n                        (vm.negotiation.realEstateStatus.notAssignedCount > 0) && \r\n                        vm.negotiation.rentlistStatus.agreementsStarted && \r\n                        !(vm.negotiation.canSkipRentlist || vm.negotiation.isFinished)" class="btn btn--secondary spacer-mt-xsmall" ng-click="vm.unforgeRentlist(true)">Avv\xE4lj oanv\xE4nd l\xE4genheter</button></div><div ng-if="!vm.negotiation.canSkipRentlist && vm.negotiation.rentlistStatus.missing" style="flex: 1"><div class="display-flex align-items-center"><div class="icon icon--message-warning spacer-mr-small"></div><span ng-if="!vm.negotiation.rentlistStatus.partiallyMissing">{{vm.isNewlyConstructedApartment ? \'L\xE4genhetslista med l\xE4genhetsinformation beh\xF6ver laddas upp f\xF6r att kunna p\xE5b\xF6rja \xF6verenskommelse\' : \'L\xE4genheter beh\xF6ver l\xE4ggas till i f\xF6rhandlingen f\xF6r att kunna p\xE5b\xF6rja \xF6verenskommelse.\'}} </span><span ng-if="vm.negotiation.rentlistStatus.partiallyMissing">Ing\xE5ende {{vm.isNewlyConstructedApartment ? \'l\xE4genhetsinformation\' : \'hyresinformation\'}} saknas f\xF6r delar av best\xE5ndet. Ladda upp kompletterande {{vm.isNewlyConstructedApartment ? \'l\xE4genhetslista\': \'hyreslista\'}} f\xF6r att kunna p\xE5b\xF6rja \xF6verenskommelse.</span></div><div ng-if="vm.negotiation.rentlistStatus.partiallyMissing" class="spacer-ml-small spacer-mr-medium"><a href="#" class="link--turquoise link--underline" ng-click="vm.showWithoutRentList(); $event.preventDefault();">{{vm.negotiation.rentlistStatus.missingCount}} fastigheter som saknar {{vm.isNewlyConstructedApartment ? \'l\xE4genhetslista\': \'hyreslista\'}}</a></div></div><div ng-if="!vm.negotiation.rentlistStatus.missing" style="flex: 1;"><div class="display-flex align-items-center"><div class="icon icon--ticked-in-circle spacer-mr-small"></div><span ng-if="vm.isNewlyConstructedApartment" class="spacer-mr-xsmall">L\xE4genhetslista med l\xE4genhetsinformation f\xF6r best\xE5ndet kan laddas upp h\xE4r </span><span ng-if="!vm.isNewlyConstructedApartment && vm.negotiation.rentlistStatus.emptyRentlist" class="spacer-mr-xsmall">Hyreslista finns uppladdad. Det \xE4r m\xF6jligt att l\xE4gga till eller \xE4ndra information f\xF6r det best\xE5nd som \xE4nnu inte \xE4r i \xF6verenskommelse. Ladda ner listan <a class="link--turquoise link--underline" target="_self" download href="/api/negotiations/{{vm.negotiation.id}}/export-rent-list" ng-if="!vm.isNewlyConstructedApartment">h\xE4r. </a></span><span ng-if="!vm.isNewlyConstructedApartment && !vm.negotiation.rentlistStatus.emptyRentlist" class="spacer-mr-xsmall">L\xE4genheter har lagts till i f\xF6rhandlingen. N\xE4r l\xE4genheter har valts till en \xF6verenskommelse g\xE5r det inte l\xE4ngre att \xE4ndra i best\xE5ndet. Ladda ner listan <a class="link--turquoise link--underline" target="_self" download href="/api/negotiations/{{vm.negotiation.id}}/export-rent-list" ng-if="!vm.isNewlyConstructedApartment">h\xE4r.</a></span></div></div></div><hy-checkbox class="di" model="vm.negotiation.canSkipRentlist" ng-show="Auth.canDo(\'canSkipRentlist\') && !vm.isNewlyConstructedApartment && !vm.isNewlyConstructedProperty" ng-click="vm.confirmNegotiationWithoutApartments(); $event.preventDefault();" title="Markera i rutan om hyreslista enligt \xF6nskat format ej g\xE5tt att f\xE5 fram" disabled="vm.negotiation.isFinished || vm.negotiation.rentlistStatus.agreementsStarted"></hy-checkbox><!--<button class="btn btn--secondary di" disabled="!vm.hasApartments">Avv\xE4lj l\xE4genheter</button>--></div></div><div ng-if="vm.isNewlyConstructedProperty"><div class="alert alert--success-bordered alert--white-bg alert--slim"><div class="display-flex align-items-center"><div class="icon icon--warning spacer-mr-small"></div>{{vm.isNewlyConstructedProperty ? \'Hyreslista laddas upp i \xD6verenskommelse\' : \'Hyreslistan laddas upp i \xF6verenskommelse (Slutfas) av f\xF6rhandlingen.\' }}</div></div></div>');
$templateCache.put('resources/negotiation/show/property/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-show="vm.ready"><hy-breadcrumb></hy-breadcrumb><div class="row"><div class="col-12"><h1 class="spacer-mt-xlarge spacer-mb-large">{{vm.property.propertyDesignation}}, {{vm.property.municipality}}</h1><div ng-if="!vm.property.active" class="red-text">Inaktiv fastighet</div></div><div class="col-12"><div class="alert alert--narrow-gray alert--squary"><div class="row"><div class="col-4"><h4 class="text-margin-reset text-transform-none">Adress</h4><hy-array-display model="vm.formattedAddresses"></hy-array-display><h4 class="text-margin-bottom-reset text-transform-none">Postnummer</h4>{{vm.property.address.postalCode}}<h4 class="text-margin-bottom-reset text-transform-none">Region</h4>{{vm.property.regionId | regionTypeToDescription}}<h4 class="text-margin-bottom-reset text-transform-none">Kommun</h4>{{vm.property.municipality}}<h4 class="text-margin-bottom-reset text-transform-none">Distrikt</h4>{{vm.property.district}}<h4 class="text-margin-bottom-reset text-transform-none">Hyresv\xE4rd</h4><hy-array-display model="vm.formattedLandlords"></hy-array-display></div><div class="col-4"><h4 class="text-margin-reset text-transform-none">Fastighets\xE4gare</h4><div ng-repeat="owner in vm.property.owners"><span>{{owner.name + \', orgnr: \' + owner.organizationNumber}}</span></div><h4 class="text-margin-bottom-reset text-transform-none">Byggnads\xE5r</h4>{{vm.property.builtYear == 0 ? \'&mdash;\' : vm.property.builtYear}}<h4 class="text-margin-bottom-reset text-transform-none">V\xE4rde\xE5r</h4>{{vm.property.valuationYear === 0 ? \'&mdash;\' : vm.property.valuationYear}}<h4 class="text-margin-bottom-reset text-transform-none">Ombyggnads\xE5r</h4>{{vm.property.renovatedYear === 0 ? \'&mdash;\' : vm.property.renovatedYear}}<h4 class="text-margin-bottom-reset text-transform-none">F\xF6rhandlingsordning</h4><div ng-if="vm.property.propertyProcedures && vm.property.propertyProcedures.length > 0" ng-repeat="procedure in vm.property.propertyProcedures">{{procedure.negotiationAgreementCode | hyNegotiationAgreementTypes:vm.property.hasNegotiationAgreement}}</div></div><div class="col-4"><h4 class="text-margin-reset text-transform-none">Antal l\xE4genheter i fastigheten</h4>{{vm.property.apartmentSummary.count}}<h4 class="text-margin-bottom-reset text-transform-none">Antal l\xE4genheter i f\xF6rhandling</h4>{{vm.property.apartmentSummary.inNegotiation}}<h4 class="text-margin-bottom-reset text-transform-none">Antal l\xE4genheter med FO</h4>{{vm.property.numNegotiatedApartments}}<h4 class="text-margin-bottom-reset text-transform-none">Antal strandade l\xE4genheter</h4>{{vm.property.apartmentSummary.blocked}}</div></div></div></div></div><ng-include src="\'resources/negotiation/show/property/_apartmentList.html\'" ng-show="vm.ready"></ng-include></div>');
$templateCache.put('resources/negotiation/show/property/_apartmentList.html','<style>/* whisker focus group decision */\r\n  .price-up, .price-down {\r\n    color: #d54 !important;\r\n  }</style><div ng-controller="ShowNegotiationPropertyCtrl as tablelistVM"><div class="row"><div class="col-12 display-flex space-between align-items-center"><h2 class="spacer-mt-medium">L\xE4genheter</h2><div class="sort-column" style="text-align: right;"><hy-sorter list-columns="tablelistVM.listColumns" order="tablelistVM.sortColumn" direction="tablelistVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div></div><div class="col-12 spacer-mb-medium"><table class="table table--margin-b-reset spacer-mt-medium"><thead class="table__header"><tr><th class="table__th table__td--width-5">Nationellt l\xE4genhetsnr</th><th class="table__th table__td--width-5">L\xE4genhetsnr</th><th class="table__th table__td--width-10">\xC5rshyra</th><th class="table__th table__td--width-10">M\xE5nadshyra</th><th class="table__th table__td--width-10">Gatuadress</th><th class="table__th table__td--width-5">Yta (kvm)</th><th class="table__th table__td--width-5">Antal rum</th><th class="table__th table__td--width-5">K\xF6kstyp</th><th class="table__th table__td--width-10">Notering</th><th class="table__th table__td--width-10">Status</th></tr><tr><th class="table__th table__td--width-5"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="tablelistVM.inputNationalNo" hy-enter="tablelistVM.getPropertyApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="tablelistVM.getPropertyApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-5"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="tablelistVM.inputOwnerNo" hy-enter="tablelistVM.getPropertyApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="tablelistVM.getPropertyApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-5"></th><th class="table__th table__td--width-5"></th><th class="table__th table__td--width-5"></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-10"></th></tr></thead><tbody><tr class="table__tr" ng-repeat="apartment in tablelistVM.apartments" ng-class="{\'table__tr--muted\': !tablelistVM.ready}" ng-click="tablelistVM.showApartmentHistory(apartment)"><td class="table__td table__td--minimalistic table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{apartment.nationalNo | nationalNoControl}}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{apartment.ownerNo}}</td><td class="table__td table__td--minimalistic"><span ng-class="vm.priceColour(apartment.newYearlyRent, apartment.currentYearlyRent)">{{tablelistVM.formatNumber(apartment.newYearlyRent)}}</span><br>{{tablelistVM.formatNumber(apartment.currentYearlyRent)}}</td><td class="table__td table__td--minimalistic"><span ng-class="vm.priceColour(apartment.newMonthlyRent, apartment.currentMonthlyRent)">{{tablelistVM.formatNumber(apartment.newMonthlyRent)}}</span><br>{{tablelistVM.formatNumber(apartment.currentMonthlyRent)}}<hy-std-display model="{{apartment.objectsExtra}}"></hy-std-display></td><td class="table__td table__td--minimalistic">{{apartment.streetAddress}}</td><td class="table__td table__td--minimalistic">{{apartment.livingSpace}}</td><td class="table__td table__td--minimalistic">{{apartment.rooms}}</td><td class="table__td table__td--minimalistic">{{apartment.kitchenType}}</td><td class="table__td table__td--minimalistic">{{apartment.rentFactors}}</td><td class="table__td table__td--minimalistic"><b><span ng-bind-html="apartment.status | hyStatusString : 1"></span></b><br><span ng-bind-html="apartment.currentStatus | hyStatusString : 1"></span></td></tr></tbody></table><div class="row spacer-mt-medium" ng-show="tablelistVM.apartmentsCount > tablelistVM.pageLimit"><div class="col text-right"><hy-paginator get-list="tablelistVM.getPropertyApartments" page-offset="tablelistVM.pageOffset" page-limit="tablelistVM.pageLimit" total-count="tablelistVM.apartmentsCount"></hy-paginator></div></div><div ng-show="tablelistVM.apartmentsCount === 0 &amp;&amp; tablelistVM.ready" class="alert alert--hide-border alert--slim alert--squary">Inga l\xE4genheter finns att visa</div><div ng-show="!tablelistVM.ready" class="col-12 display-flex justify-content-center"><hy-progress-circular size="40px"></hy-progress-circular></div></div></div></div>');
$templateCache.put('resources/negotiation/show/history/show.html','<div ng-controller="ShowNegotiationHistoryCtrl as historyVM"><hy-progress-circular size="60px" ng-hide="historyVM.ready"></hy-progress-circular><div class="row" ng-show="historyVM.ready"><div class="col"><table class="table spacer-mt-medium"><tr class="table__header"><th class="table__th">N\xE4r</th><th class="table__th">Vem</th><th class="table__th">Vad</th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="event in historyVM.events" ng-if="historyVM.isAllowed(event.what)"><td class="table__td table__td--width-20">{{event.when | date:\'short\'}}</td><td class="table__td table__td--width-20">{{event.who.firstname}} {{event.who.lastname}}</td><td class="table__td table__td--width-60" ng-include="\'resources/negotiation/show/history/partials/_\' + event.what + \'.html\'"></td></tr></table></div></div></div>');
$templateCache.put('resources/negotiation/list/responsibles/list.html','<div ng-controller="ListNegotiationResponsiblesCtrl as responsiblesVM"><hy-tableau id="negotiationsResponsiblesTableau" columns="responsiblesVM.tableColumns" default-sorting="caseNumber DESC" f-get-data="responsiblesVM.tableFGetData" f-row-click="responsiblesVM.gotoNegotiation" use-url="true" limit="responsiblesVM.pageLimit" f-get-context-info="vm.getContextInfo" last-params="responsiblesVM.lastParams"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><person ng-init="person = $parent.item.participants">{{ person.firstname }} {{ person.lastname }}</person><status>{{$parent.item.combinedState | hyCombinedNegotiationStates}}</status><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && vm.editComment(item)"></comment></hy-tableau></div>');
$templateCache.put('resources/negotiation/new-direct/modals/select-office-modal.html','<form name="officeForm" ng-submit="officeForm.$valid &amp;&amp; vm.ok()" novalidate><div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">V\xE4lj mottagande HGF-kontor</h2></div><div class="modal-body modal-body--medium" id="modal-body"><label for="inputHgfOfficeId">HGF kontor</label> <input name="inputHgfOfficeId" id="inputHgfOfficeId" type="hidden" ng-model="vm.selectedOffice.id" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle><span>{{vm.selectedOffice.id ? vm.selectedOffice.name : \'V\xE4lj mottagande kontor\'}}</span></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="office in vm.offices" type="button" class="dropdown-item" ng-click="vm.selectedOffice.id = office.id; vm.selectedOffice.name = office.name">{{office.name}}</button></div></div></div><div class="modal-footer modal-footer--medium"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="submit" ng-disabled="vm.officeForm.$invalid || !vm.canSave() ">Spara</button></div></form>');
$templateCache.put('resources/negotiation/show/addproperties/show.html','<div ng-controller="AddPropertiesCtrl as tablelistVM"><button class="btn" ng-click="tablelistVM.save()" ng-disabled="tablelistVM.busy || (tablelistVM.emptyProperties.length < 1)">Spara</button><hy-apartment-selector-2 ng-if="!tablelistVM.busy" id="apartmentSelector" allow-empty-properties="true" user-id="Auth.currentUser().id" empty-properties="tablelistVM.emptyProperties" empty-only="tablelistVM.isPropertyBased" model="tablelistVM.selectedApartments"></hy-apartment-selector-2><hy-progress-circular size="100px" ng-if="tablelistVM.busy"></hy-progress-circular><button class="btn" ng-click="tablelistVM.save()" ng-disabled="tablelistVM.busy || (tablelistVM.emptyProperties.length < 1)">Spara</button></div>');
$templateCache.put('resources/negotiation-call/show/errors/show.html','<div ng-controller="ShowNegotiationCallErrorsCtrl as errorsVM"><hy-progress-circular size="60px" ng-hide="errorsVM.readyErrors"></hy-progress-circular><div ng-show="errorsVM.readyErrors &amp;&amp; errorsVM.errorsCount > 0"><div class="row spacer-mt-xlarge"><div class="col"><table class="table"><tr class="table__header"><th class="table__th">Fastighet</th><th class="table__th">Kommun</th><th class="table__th">LM distrikt</th><th class="table__th">Misst\xE4nkt fel p\xE5 fastigheten</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="error in errorsVM.errors"><td class="table__td">{{error.propertyDesignation}}</td><td class="table__td">{{error.municipality}}</td><td class="table__td">{{error.district}}</td><td class="table__td">{{error.error | hyNegotiationCallSuspectedErrors}} <span ng-show="error.error == \'alreadyInNegotiation\'"><div class="icon icon--info bogus-tooltip-icon" data-tooltipid="{{error.id}}" ng-click="errorsVM.toggleBogusTooltip(this)"></div><span class="bogus-tooltip" id="bogusTooltip{{error.id}}"><div><span ng-repeat="x in error.errorData" class="bogus-tooltip-item"><a href="/negotiations/{{x.id}}">{{x.caseNumber}}</a></span></div></span></span></td></tr></table><div class="col-12 text-right" ng-show="errorsVM.errorsCount > errorsVM.pageLimit"><hy-paginator get-list="errorsVM.queryErrors" page-offset="errorsVM.pageOffset" page-limit="errorsVM.pageLimit" total-count="errorsVM.errorsCount"></hy-paginator></div><div class="col-12 text-right" ng-show="errorsVM.errorsCount > 0 &amp;&amp; errorsVM.errorsCount <= errorsVM.pageLimit"><strong>Totalt: </strong>{{errorsVM.errorsCount}}</div></div></div></div><div class="row"><div class="col"><div class="alert alert--info-bordered alert--squary display-flex align-items-center alert--slim" ng-show="errorsVM.readyErrors &amp;&amp; errorsVM.errors.length === 0"><div class="icon icon--info spacer-mr-small"></div>Inga misst\xE4nkta fel har identifierats.</div></div></div></div>');
$templateCache.put('resources/negotiation-call/show/modals/change-office-modal.html','<form name="officeForm" ng-submit="officeForm.$valid &amp;&amp; vm.ok()" novalidate><div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">V\xE4lj mottagande HGF-kontor</h2></div><div class="modal-body modal-body--medium" id="modal-body"><label for="inputHgfOfficeId">Skickas till HGF kontor</label> <input name="inputHgfOfficeId" id="inputHgfOfficeId" type="hidden" ng-model="vm.selectedOffice.id" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle ng-disabled="vm.editMode"><span ng-class="{\'text-gray\': !vm.negotiationCall.counterpartOrganizationId &amp;&amp; !vm.editMode}">{{vm.selectedOffice.id ? vm.selectedOffice.name : \'V\xE4lj mottagande kontor\'}}</span></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="office in vm.offices" type="button" class="dropdown-item" ng-click="vm.selectedOffice.id = office.id; vm.selectedOffice.name = office.name">{{office.name}}</button></div></div></div><div class="modal-footer modal-footer--medium"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="submit" ng-disabled="vm.officeForm.$invalid || !vm.selectedOffice.id">Skicka</button></div></form>');
$templateCache.put('resources/negotiation-call/show/modals/return-negotiation-call-modal.html','<form name="vm.sendBackForm" ng-submit="vm.sendBackForm.$valid  &amp;&amp; vm.send()" novalidate><div class="modal-header modal-header--medium"><h2 class="text-center text-margin-reset">St\xE4mmer inte informationen p\xE5 framst\xE4llan?</h2></div><div class="modal-body modal-body--medium" id="modal-body"><div class="row"><div class="col-12"><p class="spacer-mb-xlarge">Skicka tillbaka framst\xE4llan s\xE5 att den person som skapade den kan g\xF6ra \xE4ndringar. Skriv ett meddelande s\xE5 att personen vet vad den ska \xE4ndra innan hen skickar in den p\xE5 nytt. Framst\xE4llan kommer f\xF6rsvinna fr\xE5n framst\xE4llningslistan tills personen skickar in den p\xE5 nytt.</p><div class="form-group" ng-class="{\'form-group--error\': (vm.sendBackForm.inputMessage.$touched || vm.sendBackForm.$submitted)\r\n              &amp;&amp; vm.sendBackForm.inputMessage.$invalid}"><label for="inputMessage">Meddelande till person som skickade framst\xE4llan</label> <textarea name="inputMessage" id="inputMessage" rows="10" placeholder="H\xE4r kan du tex skriva att fastigheterna Myran 1 och Tr\xE4dg\xE5rdsm\xE4staren 11 redan f\xF6rhandlas av annan part i den \xE5rliga hyresf\xF6rhandlingen." class="form-control form-control--gray-border" ng-model="vm.message" required>\r\n            </textarea></div></div></div></div><div class="modal-footer modal-footer--medium"><a class="link--turquoise link--no-underline spacer-mr-small" ng-click="vm.cancel()">Avbryt</a> <button class="btn" ng-disabled="vm.sendBackForm.$invalid" type="submit">Skicka tillbaka</button></div></form>');
$templateCache.put('resources/negotiation-call/new/modals/change-real-estate-source-modal.html','<div class="modal-header modal-header--medium"><h2 class="text-center text-margin-reset">Nollst\xE4ller det valda best\xE5ndet</h2></div><div class="modal-body modal-body--medium" id="modal-body"><div class="row"><div class="col-12"><p class="spacer-mb-xlarge">N\xE4r du v\xE4ljer att byta k\xE4lla nollst\xE4lls tidigare valt best\xE5nd och eventuellt yrkande &amp; motiv kopplat till detta best\xE5nd. Du m\xE5ste d\xE5 ange best\xE5ndet p\xE5 nytt igen och ange yrkande &amp; motiv f\xF6r nya best\xE5ndet.</p><p><strong>Vill du forts\xE4tta?</strong></p></div></div></div><div class="modal-footer modal-footer--medium"><a class="link--turquoise link--no-underline spacer-mr-small" ng-click="vm.cancel()">Avbryt</a> <button class="btn spacer-mr-small" type="button" ng-click="vm.confirm()">Byt k\xE4lla</button></div>');
$templateCache.put('resources/negotiation-call/new/modals/delete-rent-list-modal.html','<div class="modal-header modal-header--medium"><h2 class="text-center text-margin-reset">Raderar tidigare hyreslista</h2></div><div class="modal-body modal-body--medium" id="modal-body"><div class="row"><div class="col-12"><p>N\xE4r du v\xE4ljer att radera den tidigare uppladdade hyreslistan kommer du beh\xF6va ange yrkande &amp; motiv igen f\xF6r nya best\xE5ndet.</p><p class="spacer-mb-xlarge">OBS! Om du vill anv\xE4nda din tidigare uppladdade hyreslista som grund f\xF6r nya best\xE5ndet, gl\xF6m inte ladda ner den innan du trycker \u201CLadda upp\u201D h\xE4r nedan.</p><p><strong>Vill du forts\xE4tta?</strong></p></div></div></div><div class="modal-footer modal-footer--medium"><a class="link--turquoise link--no-underline spacer-mr-small" ng-click="vm.cancel()">Avbryt</a> <button class="btn btn--secondary" type="button" ng-click="vm.confirm()">Ta bort</button></div>');
$templateCache.put('resources/negotiation-call/new/modals/edit-motivation-modal.html','<form name="motivationForm" ng-submit="motivationForm.$valid  &amp;&amp; vm.add()" novalidate><div class="modal-header modal-header--small spacer-mb-reset"><h2 class="text-margin-top-reset spacer-mb-reset">L\xE4gg till yrkande &amp; motiv</h2></div><div class="col-12"><p class="spacer-mt-reset">L\xE4gger till yrkande &amp; motiv p\xE5 de markerade objekten</p></div><div class="modal-body modal-body--small" id="modal-body"><div class="row"><div class="col-12"><div class="form-group"><label for="inputDemandType">Yrkande</label><div class="display-flex flex-direction-column spacer-mt-small"><div class="custom-control custom-control--radio spacer-mr-medium"><input class="custom-control__input" type="radio" id="inputDemandTypePercent" name="rb_group" tabindex="0" ng-model="vm.demand.unit" value="0" ng-click="vm.demand.numericValue = null; vm.demand.stringValue = null;"> <label class="custom-control__label" for="inputDemandTypePercent">Procent %</label></div><div class="custom-control custom-control--radio spacer-mr-medium"><input class="custom-control__input" type="radio" id="inputDemandTypeSqm" name="rb_group" tabindex="1" ng-model="vm.demand.unit" value="1" ng-click="vm.demand.numericValue = null; vm.demand.stringValue = null;"> <label class="custom-control__label" for="inputDemandTypeSqm">kr/kvm/\xE5r</label></div><div class="custom-control custom-control--radio spacer-mr-medium"><input class="custom-control__input" type="radio" id="inputDemandTypeFree" name="rb_group" tabindex="2" ng-model="vm.demand.unit" value="2" ng-click="vm.demand.numericValue = null; vm.demand.stringValue = null;"> <label class="custom-control__label" for="inputDemandTypeFree">Fritext</label></div></div><div ng-show="vm.demand.unit"><div class="form-group" ng-class="{\r\n              \'form-group--error\': (motivationForm.inputDemandData.$error.pattern),\r\n            }"><div ng-if="vm.demand.unit === \'2\'"><input id="inputDemandData" name="inputDemandData" type="text" autofocus ng-model="vm.demand.stringValue" class="form-control form-control--gray-border" placeholder="{{vm.demand.unit | demandTypeToDescription}}" autocomplete="off" required></div><div ng-if="vm.demand.unit !== \'2\'"><input id="inputDemandData" name="inputDemandData" autofocus ng-model="vm.demand.numericValue" class="form-control form-control--gray-border" ng-pattern="vm.StringUtils.getMultipleNumberFormatsReg()" placeholder="{{vm.demand.unit | demandTypeToDescription}}" autocomplete="off" required> <small ng-show="motivationForm.inputDemandData.$error.pattern &amp;&amp; vm.demand.unit === \'0\'">Ange en giltig procentsats</small> <small ng-show="motivationForm.inputDemandData.$error.pattern &amp;&amp; vm.demand.unit === \'1\'">Ange en giltig summa</small></div></div></div></div></div><div class="col-12"><div class="form-group"><label for="inputMotivation">Motiv</label> <textarea name="inputMotivation" id="inputMotivation" rows="3" placeholder="Ange motiv" class="form-control form-control--gray-border" type="text" ng-model="vm.demand.motivation" required>\r\n          </textarea></div></div></div></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline spacer-mr-small" ng-click="vm.cancel()">Avbryt</a> <button class="btn" ng-click="vm.add()" ng-disabled="(vm.demand.unit !== \'2\' && !vm.demand.numericValue) || (vm.demand.unit === \'2\' && !vm.demand.stringValue)" type="submit">L\xE4gg till</button></div></form>');
$templateCache.put('resources/negotiation-call/new/modals/receiving-office-modal.html','<form name="officeForm" ng-submit="officeForm.$valid &amp;&amp; vm.ok()" novalidate><div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">V\xE4lj mottagande HGF-kontor</h2></div><div class="modal-body modal-body--medium" id="modal-body"><label for="inputHgfOfficeId">Skickas till HGF kontor</label> <input name="inputHgfOfficeId" id="inputHgfOfficeId" type="hidden" ng-model="vm.selectedOffice.id" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle ng-disabled="vm.editMode"><span ng-class="{\'text-gray\': !vm.negotiationCall.counterpartOrganizationId &amp;&amp; !vm.editMode}">{{vm.selectedOffice.id ? vm.selectedOffice.name : \'V\xE4lj mottagande kontor\'}}</span></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="office in vm.offices" type="button" class="dropdown-item" ng-click="vm.selectedOffice.id = office.id; vm.selectedOffice.name = office.name">{{office.name}}</button></div></div><div ng-if="!vm.editOfficeOnly" class="spacer-mt-medium"><div class="display-flex align-items-top"><div class="custom-control custom-control--checkbox spacer-mt-xsmall"><input class="custom-control__input" type="checkbox" id="checkOne" name="checkOne" ng-model="vm.checkOne"> <label class="custom-control__label" for="checkOne"></label></div><span>Jag bekr\xE4ftar att jag har till\xE5telse att f\xF6rhandla f\xF6r det angivna best\xE5ndet</span></div><div class="display-flex align-items-top spacer-mt-small" ng-show="vm.showCheckTwo"><div class="custom-control custom-control--checkbox spacer-mt-xsmall"><input class="custom-control__input" type="checkbox" id="checkTwo" name="checkTwo" ng-model="vm.checkTwo"> <label class="custom-control__label" for="checkTwo"></label></div><span>Hyresg\xE4stgodk\xE4nnande finns f\xF6r de l\xE4genheter jag p\xE5kallar f\xF6rhandling f\xF6r eller s\xE5 st\xE5r de tomma</span></div><div class="spacer-mt-medium">F\xF6rhandlingsordningens part p\xE5 hyresv\xE4rdsidan:</div><div class="display-flex align-items-top"><div class="custom-control custom-control--radio spacer-mt-xsmall"><input class="custom-control__input" type="radio" id="partOne" name="partOne" ng-model="vm.landlordSide" ng-value="vm.LandlordSides.LANDLORD"> <label class="custom-control__label" for="partOne">Hyresv\xE4rd</label></div></div><div class="display-flex align-items-top"><div class="custom-control custom-control--radio spacer-mt-xsmall"><input class="custom-control__input" type="radio" id="partTwo" name="partTwo" ng-model="vm.landlordSide" ng-value="vm.LandlordSides.LANDLORD_AND_ORGANIZATION"> <label class="custom-control__label" for="partTwo">Hyresv\xE4rd och organisation av fastighets\xE4gare, i vilken hyresv\xE4rden \xE4r medlem</label></div></div></div></div><div class="modal-footer modal-footer--medium"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="submit" ng-disabled="vm.officeForm.$invalid || !vm.canSave() ">Skicka</button></div></form>');
$templateCache.put('resources/negotiation-call/new/partials/red-letter-warning.html','<h2 class="modal-title text-margin-reset red-text">OBS!</h2><p>N\xE4r framst\xE4llan registreras och fastighetsbest\xE5nd anges, ber vi dig att dela upp den i flera framst\xE4llningar s\xE5 att fastigheterna hamnar p\xE5 r\xE4tt <b><i>kontor</i></b> i r\xE4tt <b><i>region</i></b> (\xE4r du os\xE4ker p\xE5 kontor \u2013 v\xE4lj ett kontor i den regionen din fastighet tillh\xF6r).<br><b>En</b> framst\xE4llan kan <b>inte</b> skapas f\xF6r hela Sverige om fastigheterna \xE4r utspridda i landet. Ligger fastigheterna i olika regioner m\xE5ste <b>flera</b> framst\xE4llningar skickas in via F\xF6rhandlarportalen.</p><p>Se nedan f\xF6r vilka kontor som finns i respektive region:</p><div class="row"><div class="col"><b>Region Norrland</b><ul><li>Kontor Ume\xE5</li><li>Kontor Lule\xE5</li><li>Kontor Sundsvall</li><li>Kontor \xD6stersund</li><li>Kontor Skellefte\xE5</li></ul><b>Region Mitt</b><ul><li>Kontor Borl\xE4nge</li><li>Kontor Eskilstuna</li><li>Kontor Karlstad</li><li>Kontor \xD6rebro</li></ul><b>Region Stockholm / Gotland</b><ul><li>Kontor Stockholm</li></ul></div><div class="col"><b>Region V\xE4stra Sverige</b><ul><li>Kontor G\xF6teborg</li></ul><b>Region Aros-G\xE4vle</b><ul><li>Kontor V\xE4ster\xE5s</li><li>Kontor Uppsala</li><li>Kontor G\xE4vle</li><li>Kontor Hudiksvall</li></ul></div><div class="col"><b>Region B\xE4sk</b><ul><li>Kontor Bor\xE5s</li><li>Kontor V\xE4nersborg</li></ul><b>Region Sydost</b><ul><li>Kontor Link\xF6ping</li><li>Kontor J\xF6nk\xF6ping</li><li>Kontor V\xE4xj\xF6</li></ul><b>Region Norra Sk\xE5ne</b><ul><li>Kontor Helsingborg</li></ul><b>Region S\xF6dra Sk\xE5ne</b><ul><li>Kontor Malm\xF6</li></ul></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_apartment-motivation.html','<form name="vm.propertyMotivationForm" ng-submit="vm.propertyMotivationForm.$valid &amp;&amp; vm.gotoNextStep()" class="spacer-mb-xlarge" novalidate><div class="row"><div class="col-9"><p>Om du\xA0vill\xA0ange\xA0yrkande\xA0och\xA0motiv\xA0till\xA0det\xA0valda\xA0best\xE5ndet\xA0s\xE5\xA0g\xF6rs\xA0det\xA0genom\xA0att\xA0markera\xA0objekten\xA0i\xA0listan\xA0nedan\xA0och\xA0tryck\xA0sedan\xA0p\xE5\xA0L\xE4gg\xA0till yrkande & motiv.\xA0 Vill\xA0du\xA0avvakta\xA0med\xA0att\xA0ange\xA0yrkande\xA0och\xA0motiv,\xA0tryck\xA0p\xE5\xA0N\xE4sta. Det finns m\xF6jlighet att ange yrkande och motiv vid senare tillf\xE4lle i f\xF6rhandlingen.</p></div><div class="col-3 spacer-mt-medium" ng-show="vm.readyApartments"><div class="col sort-container"><div class="dropdown dropdown--hide-caret" uib-dropdown><button type="button" class="btn btn--icon btn--icon-sort btn--squary btn--squary-white btn--squary-white__sort" uib-dropdown-toggle>{{vm.sortPropertyTitle}}</button><div class="dropdown-menu dropdown-menu--right dropdown-menu--sort" uib-dropdown-menu role="menu"><button ng-repeat="item in vm.listApartmentColumns" type="button" class="dropdown-item dropdown-item--sort" ng-click="vm.sortProperties(item.column, item.title)">{{item.title}} <span ng-show="vm.sortPropertyColumn === item.column" class="btn--icon" ng-class="vm.sortPropertyReverse ? \'btn--icon-sort-down\' : \'btn--icon-sort-up\'"></span></button></div></div></div></div><div class="col-12"><hy-progress-circular size="40px" ng-hide="vm.readyApartments"></hy-progress-circular><table class="table table--margin-reset" ng-show="vm.readyApartments"><thead class="table__header"><tr><th class="table__th table__td--width-6">Alla</th><th ng-hide="vm.fetchedApartmentList" class="table__th table__td--width-15">\xC4gare</th><th class="table__th table__td--width-15">Kommun</th><th class="table__th table__td--width-15">Fastighetsbeteckning</th><th class="table__th table__td--width-15">Gatuadress</th><th class="table__th table__td--width-10">Nationella<br>l\xE4genhetsnr</th><th class="table__th table__td--width-10">L\xE4genhetsnr</th><th class="table__th table__td--width-15">Yrkande</th><th class="table__th table__td--width-15">Motiv</th></tr><tr><th class="table__th"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="allApartmentsBox" ng-model="vm.allPropertiesBoxChecked" ng-change="vm.toggleAllPropertiesSelection()"> <label class="custom-control__label" for="allApartmentsBox"></label></div></th><th ng-hide="vm.fetchedApartmentList" class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextOwners" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(0, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(0, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextMunicipality" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(2, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(2, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextProperty" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(1, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(1, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextStreets" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(4, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(4, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"></th><th class="table__th"></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextDemand" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(5, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(5, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-class="{\'text-black\': vm.selectedPropertyIds[property.id],\r\n            \'text-gray\': !vm.selectedPropertyIds[property.id]}" ng-repeat="property in vm.properties | filter:vm.propertiesFilter | orderBy:vm.sortPropertyColumn:vm.sortPropertyReverse | startFrom: vm.currentPage * vm.pageLimit | limitTo: vm.pageLimit"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="apartment-{{property.id}}" ng-model="vm.selectedPropertyIds[property.id]" ng-change="vm.updateAllPropertiesBoxState()"> <label class="custom-control__label" for="apartment-{{property.id}}"></label></div></td><td ng-hide="vm.fetchedApartmentList" class="table__td table__td--minimalistic"><hy-array-display model="property.property.owners"></hy-array-display></td><td class="table__td table__td--minimalistic">{{ property.municipality }}</td><td class="table__td table__td--minimalistic">{{ property.property.propertyDesignation }}</td><td class="table__td table__td--minimalistic">{{ property.streetAddress }}</td><td class="table__td table__td--minimalistic table__td--nowrap" title="{{property.nationalNo | nationalNoTitleControl}}">{{ property.nationalNo | nationalNoControl }}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{ property.ownerNo }}</td><td class="table__td table__td--minimalistic"><span ng-show="property.demand.unit === \'2\'">{{property.demand.stringValue}} </span><span ng-hide="property.demand.unit === \'2\'">{{property.demand.numericValue | number}} <span ng-show="property.demand.numericValue">{{ property.demand.unit | demandTypeToDescription }}</span></span></td><td class="table__td table__td--minimalistic">{{ property.demand.motivation }}</td></tr></table><div class="text-right spacer-mt-medium" ng-show="vm.readyApartments &amp;&amp; vm.properties.length > 0"><strong>Totalt: </strong>{{vm.properties.length}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="vm.readyApartments &amp;&amp; vm.properties.length === 0">Inga l\xE4genheter hittades.</div></div></div></form><div class="row spacer-mt-medium spacer-mb-medium" ng-if="vm.apartmentsTotalCount > vm.pageLimit"><div class="col-12 display-flex justify-content-end"><div><strong>{{ vm.firstRecordInCurrentPage }}-{{ vm.computeLastRecord() }}</strong> av <strong>{{ vm.apartmentsTotalCount }}</strong> <button class="btn btn--paginator-left" type="button" ng-disabled="vm.firstRecordInCurrentPage === 1" ng-click="vm.goToPrevPage()"></button> <button class="btn btn--paginator-right" type="button" ng-disabled="vm.computeLastRecord() >= vm.apartmentsTotalCount" ng-click="vm.goToNextPage()"></button></div></div></div><div class="row"><div class="col text-right"><button class="btn spacer-mr-small" ng-click="vm.editDemandAndMotivation()" ng-disabled="!vm.atLeastOneSelectedPropertyId()">L\xE4gg till yrkande &amp; motiv</button> <button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_basic.html','<style>/*TODO: move to fe-common*/\r\n  .btn--delete-date {\r\n    position: absolute;\r\n    right: 60px;\r\n  }</style><hy-progress-circular size="60px" center-on-page ng-hide="!vm.editMode || vm.ready"></hy-progress-circular><div class="alert alert--border-turq alert--squary" ng-show="!vm.editMode || vm.ready"><form name="vm.basicForm" ng-submit="vm.basicForm.$valid &amp;&amp; vm.gotoNextStep()" class="spacer-mb-xlarge" novalidate><div class="row"><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputName.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputName.$invalid,\r\n          }"><label for="inputName">Namn p\xE5 f\xF6rhandlingsframst\xE4llan</label> <input name="inputName" id="inputName" type="text" class="form-control form-control--narrow form-control--gray-border" placeholder="Skriv in ett namn" ng-model="vm.negotiationCall.name" autocomplete="off" required> <small class="form-group__help text-xsmall">Exempel: Lind Fastigheter - \xC5rlig hyresf\xF6rhandling 2017</small></div></div><div class="col-6 col-lg-3" ng-if="!vm.isHgf"><div class="display-flex align-items-center"><label for="inputCounterpartOrganizationId">Organisation</label><div ng-hide="vm.editMode || vm.organizations.length>0" class="icon icon--info--urgent icon--small label--icon" uib-tooltip="Du tillh\xF6r inte n\xE5gon organisation. Det kan bero p\xE5 att organisationen\r\n              \xE4nnu inte blivit upplagd i F\xF6rhandlarportalen eller att din administat\xF6r inte har \r\n              godk\xE4nt ditt anv\xE4nderkonto." tooltip-is-open="!vm.editMode &amp;&amp; vm.organizations.length===0" tooltip-placement="top" tooltip-class="tooltip--warning"></div></div><input name="inputCounterpartOrganizationId" id="inputCounterpartOrganizationId" type="hidden" ng-model="vm.negotiationCall.counterpartOrganizationId" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle ng-disabled="vm.editMode || vm.organizations.length===0"><span ng-class="{\'text-gray-dark\': !vm.negotiationCall.counterpartOrganizationId}">{{vm.negotiationCall.counterpartOrganizationId ? vm.selectedOrganizationName : \'V\xE4lj organisation\'}}</span></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="organization in vm.organizations" type="button" class="dropdown-item" ng-click="vm.negotiationCall.counterpartOrganizationId = organization.id; vm.selectedOrganizationName = organization.name">{{organization.name}}</button></div></div></div><div class="col-6 col-lg-3"><label for="inputNegotiationType" class="label--icon"><a href="#" class="display-inline-flex" ng-click="vm.showNegotiationTypeInfo(); $event.preventDefault();"><div>F\xF6rhandlingstyp</div><div class="icon icon--info icon--small cursor-pointer"></div></a></label> <input name="inputNegotiationType" id="inputNegotiationType" type="hidden" ng-model="vm.negotiationCall.type" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle ng-disabled="vm.editMode &amp;&amp; (vm.negotiationCall.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY || vm.negotiationCall.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_APARTMENT)"><span ng-class="{\'text-gray\': vm.negotiationCall.type === undefined}">{{(vm.negotiationCall.type !== undefined) ? (vm.negotiationCall.type | hyNegotiationTypes) : \'Ange f\xF6rhandlingstyp\'}}</span></button><div class="dropdown-menu dropdown-menu--left" uib-dropdown-menu role="menu" ng-if="!vm.editMode"><button ng-repeat="value in vm.NegotiationTypes" type="button" class="dropdown-item dropdown-item--indent" ng-click="vm.negotiationCall.type = value">{{value | hyNegotiationTypes}}</button></div><div class="dropdown-menu dropdown-menu--left" uib-dropdown-menu role="menu" ng-if="vm.editMode"><button ng-repeat="value in vm.NegotiationTypes" type="button" class="dropdown-item" ng-click="vm.negotiationCall.type = value; ">{{value | hyNegotiationTypes}}</button></div></div></div><div class="col-6 col-lg-3" ng-if="vm.isHgf"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputIncomingDate.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputIncomingDate.$invalid,\r\n          }"><label for="inputName">Ankomstdatum till HGF</label><div class="input-group datepicker"><input name="inputIncomingDate" id="inputIncomingDate" type="text" class="form-control form-control--full-width form-control--narrow form-control--gray-border" uib-datepicker-popup datepicker-options="vm.incomingDateOptions" popupplacement="bottom-left" is-open="opened3" ng-model="vm.negotiationCall.incomingDate" required readonly="readonly" close-on-date-selection="true"> <button type="button" class="btn btn--delete-date btn--squary btn--icon btn--icon-cross" ng-if="vm.negotiationCall.incomingDate" ng-click="vm.negotiationCall.incomingDate = null"></button> <button type="button" class="btn btn--squary btn--icon btn--icon-calendar" ng-click="opened3 = !opened3"></button></div></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputRentEffectiveFrom.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputRentEffectiveFrom.$invalid,\r\n          }"><label for="inputName">\xD6nskat datum f\xF6r hyresf\xF6r\xE4ndring</label><div class="input-group datepicker"><input name="inputRentEffectiveFrom" id="inputRentEffectiveFrom" type="text" class="form-control form-control--full-width form-control--narrow form-control--gray-border" uib-datepicker-popup datepicker-options="vm.rentEffectiveFromDateOptions" popupplacement="bottom-left" is-open="opened" ng-model="vm.rentEffectiveFrom" required readonly="readonly" close-on-date-selection="true"> <button type="button" class="btn btn--delete-date btn--squary btn--icon btn--icon-cross" ng-if="vm.rentEffectiveFrom" ng-click="vm.rentEffectiveFrom = null"></button> <button type="button" class="btn btn--squary btn--icon btn--icon-calendar" ng-click="opened = !opened"></button></div></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputNegotiationDate.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputNegotiationDate.$invalid,\r\n          }"><label for="inputName">\xD6nskat datum f\xF6r f\xF6rhandling</label><div class="input-group datepicker"><input name="inputNegotiationDate" id="inputNegotiationDate" type="text" class="form-control form-control--full-width form-control--narrow form-control--gray-border" uib-datepicker-popup datepicker-options="vm.negotiationDateDateOptions" popupplacement="bottom-left" is-open="opened2" ng-model="vm.negotiationDate" ng-required="!vm.isHgf" readonly="readonly" close-on-date-selection="true"> <button type="button" class="btn btn--delete-date btn--squary btn--icon btn--icon-cross" ng-if="vm.negotiationDate" ng-click="vm.negotiationDate = null"></button> <button type="button" class="btn btn--squary btn--icon btn--icon-calendar" ng-click="opened2 = !opened2"></button></div></div></div><div class="col-6 col-lg-3"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputNegotiationDate.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputNegotiationDate.$invalid,\r\n          }"><label for="inputName">\xD6nskad plats f\xF6r f\xF6rhandling</label> <input id="inputNegotiationLocation" name="inputNegotiationLocation" type="text" class="form-control form-control--full-width form-control--narrow form-control--gray-border" ng-model="vm.negotiationCall.negotiationLocation"></div></div><div class="col-12" ng-class="{\'col-lg-6\': vm.negotiationCall.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY}"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputMessage.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputMessage.$invalid,\r\n          }"><label for="inputMessage" ng-if="!vm.isHgf">Meddelande till Hyresg\xE4stf\xF6reningen (Valfritt). Yrkande och motiv anges i senare steg</label> <label for="inputMessage" ng-if="vm.isHgf">Information om framst\xE4llan / \xF6vrig information</label> <textarea name="inputMessage" id="inputMessage" rows="6" placeholder="H\xE4r kan du till exempel skriva &ldquo;Jag vill p\xE5kalla f\xF6rhandling om n\xE4stkommande \xE5rs hyror p\xE5 v\xE5ra bost\xE4der&rdquo;" class="form-control" ng-model="vm.negotiationCall.message">\r\n          </textarea></div></div><div class="col-6 col-lg-3" ng-if="vm.negotiationCall.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputMunicipality.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputMunicipality.$invalid,\r\n          }"><label for="inputMunicipality">Kommun</label> <textarea name="inputMunicipality" id="inputMunicipality" rows="6" placeholder="Ange de kommuner som de nya fastigheterna ligger i. Tex Nacka, Malm\xF6 eller \xD6stersund" class="form-control" ng-model="vm.negotiationCall.municipalityText" required>\r\n          </textarea></div></div><div class="col-6 col-lg-3" ng-if="vm.negotiationCall.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.basicForm.inputPropertyOwner.$touched || vm.basicForm.$submitted) &amp;&amp; vm.basicForm.inputPropertyOwner.$invalid,\r\n          }"><label for="inputPropertyOwner">Fastighets\xE4gare</label> <textarea name="inputPropertyOwner" id="inputPropertyOwner" rows="6" placeholder="Ange fastighets\xE4garen/best\xE4llaren till den eller de nya fastigheterna" class="form-control" ng-model="vm.negotiationCall.ownersText" required>\r\n          </textarea></div></div></div></form><div ng-if="vm.isHgf"><div class="alert alert--narrow-gray alert--squary spacer-mt-large"><div class="display-flex space-between"><div><h3 class="text-margin-reset">Bilagor</h3><p class="spacer-mr-small">Det skriftliga underlaget (tex. brev eller e-post) som inneh\xE5ller den eller de fr\xE5gor som det beg\xE4rs f\xF6rhandling om samt f\xF6rslag p\xE5 tidpunkter och plats f\xF6r sammantr\xE4de f\xF6r f\xF6rhandling, HFL 15\xA7.</p></div><div><button class="spacer-mt-small btn btn--icon btn--icon-upload" ng-click="vm.doUpload()">Ladda upp</button></div></div><table class="table spacer-mt-large"><tr class="table__header"><th class="table__th table__td--width-80">Namn</th><th class="table__th table__td--width-5"></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="file in vm.attachments"><td class="table__td">{{ file.filename }}</td><td class="table__td"><a href="#" ng-click="vm.deleteAttachment(file); $event.preventDefault();" class="btn--icon btn--icon--no-padding btn--icon-bin"><span class="sr-only">Ta bort</span></a></td></tr></table><div ng-if="vm.attachments.length === 0">Inga dokument uppladdade</div></div></div></div><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_choose-process.html','<form name="vm.chooseForm" ng-submit="vm.chooseForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><!--Ange fastigheter direkt i portalen--><div ng-if="!vm.isHgf" class="wizard-choice display-flex flex-direction-row spacer-mb-small" ng-class="{\'wizard-choice--active\' : vm.realEstateChoiceMethod === \'DIRECT_PROPERTIES\',\r\n  \'wizard-choice--disabled\': !vm.isRealEstateChoiceMethodEnabled(\'DIRECT_PROPERTIES\', vm.negotiationCall.type)}" ng-click="vm.isRealEstateChoiceMethodEnabled(\'DIRECT_PROPERTIES\', vm.negotiationCall.type) &amp;&amp;\r\n  vm.setRadioModelByOutsideClick(\'DIRECT_PROPERTIES\')"><div class="custom-control custom-control--radio custom-control--radio--no-margin"><input class="custom-control__input custom-control__input--hidden" type="radio" id="inputSelectProperties" name="radio-group" tabindex="2" ng-model="vm.realEstateChoiceMethod" value="DIRECT_PROPERTIES" ng-disabled="!vm.isRealEstateChoiceMethodEnabled(\'DIRECT_PROPERTIES\', vm.negotiationCall.type)" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="inputSelectProperties"><span ng-if="!vm.isRealEstateChoiceMethodEnabled(\'DIRECT_PROPERTIES\', vm.negotiationCall.type)">Ange fastigheter direkt i portalen</span></label></div><div class="flex-grow-1" ng-hide="!vm.isRealEstateChoiceMethodEnabled(\'DIRECT_PROPERTIES\', vm.negotiationCall.type)"><h3 class="spacer-mt-reset">Ange fastigheter direkt i portalen</h3><ul class="wizard-choice__list"><li class="wizard-choice__list-item--yes">Skriv in fastighetsbeteckningarna direkt i portalen utan att ladda upp n\xE5gon fil</li><li class="wizard-choice__list-item--yes">Hyreslistan (med ing\xE5ende hyror p\xE5 l\xE4genhetsniv\xE5) laddas upp vid senare tillf\xE4lle under f\xF6rhandlingen</li></ul></div><!--\r\n    <div ng-if="!vm.isHgf && vm.isRealEstateChoiceMethodEnabled(\'DIRECT_PROPERTIES\', vm.negotiationCall.type)">\r\n      <div class="red-letter-day" ng-click="vm.openRedWarning()">i</div>\r\n    </div>\r\n    --></div><!--V\xE4lj best\xE5nd--><div ng-if="vm.isHgf" class="wizard-choice display-flex flex-direction-row spacer-mb-small" ng-class="{\'wizard-choice--active\' : vm.realEstateChoiceMethod === \'DIRECT_APARTMENTS\',\r\n  \'wizard-choice--disabled\': !vm.isRealEstateChoiceMethodEnabled(\'DIRECT_APARTMENTS\', vm.negotiationCall.type)}" ng-click="vm.isRealEstateChoiceMethodEnabled(\'DIRECT_APARTMENTS\', vm.negotiationCall.type) &amp;&amp;\r\n  vm.setRadioModelByOutsideClick(\'DIRECT_APARTMENTS\')"><div class="custom-control custom-control--radio custom-control--radio--no-margin"><input class="custom-control__input custom-control__input--hidden" type="radio" id="inputSelectApartments" name="radio-group" tabindex="2" ng-model="vm.realEstateChoiceMethod" value="DIRECT_APARTMENTS" ng-disabled="!vm.isRealEstateChoiceMethodEnabled(\'DIRECT_APARTMENTS\', vm.negotiationCall.type)" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="inputSelectApartments"><span ng-if="!vm.isRealEstateChoiceMethodEnabled(\'DIRECT_APARTMENTS\', vm.negotiationCall.type)">Ange fastigheter direkt i portalen</span></label></div><div class="flex-grow-1" ng-hide="!vm.isRealEstateChoiceMethodEnabled(\'DIRECT_APARTMENTS\', vm.negotiationCall.type)"><h3 class="spacer-mt-reset">V\xE4lj best\xE5nd</h3><ul class="wizard-choice__list"><li class="wizard-choice__list-item--yes">Skriv in fastigheter och l\xE4genheter direkt i portalen utan att ladda upp n\xE5gon fil</li><li class="wizard-choice__list-item--yes">Hyreslistan (med ing\xE5ende hyror p\xE5 l\xE4genhetsniv\xE5) laddas upp vid senare tillf\xE4lle under f\xF6rhandlingen</li></ul></div></div><!--LADDA UPP HYRESLISTA--><div class="wizard-choice display-flex flex-direction-row spacer-mb-small" ng-class="{\'wizard-choice--active\' : vm.realEstateChoiceMethod === \'RENT_LIST\',\r\n       \'wizard-choice--disabled\': !vm.isRealEstateChoiceMethodEnabled(\'RENT_LIST\', vm.negotiationCall.type)}" ng-click="vm.isRealEstateChoiceMethodEnabled(\'RENT_LIST\', vm.negotiationCall.type) &amp;&amp;\r\n       vm.setRadioModelByOutsideClick(\'RENT_LIST\')"><div class="custom-control custom-control--radio custom-control--radio"><input class="custom-control__input custom-control__input--hidden" type="radio" id="inputRentList" name="radio-group" tabindex="0" ng-model="vm.realEstateChoiceMethod" value="RENT_LIST" ng-disabled="!vm.isRealEstateChoiceMethodEnabled(\'RENT_LIST\', vm.negotiationCall.type)" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="inputRentList"><span ng-if="!vm.isRealEstateChoiceMethodEnabled(\'RENT_LIST\', vm.negotiationCall.type)">Ladda upp hyreslista</span></label></div><div class="flex-grow-1" ng-hide="!vm.isRealEstateChoiceMethodEnabled(\'RENT_LIST\', vm.negotiationCall.type)"><h3 class="spacer-mt-reset">Ladda upp hyreslista</h3><ul class="wizard-choice__list"><li class="wizard-choice__list-item--yes">Ladda upp excelfil som listar alla l\xE4genheter inklusive aktuell hyra</li><li class="wizard-choice__list-item--yes">Excelfilen m\xE5ste f\xF6lja ett strukturerat format, mall finns att ladda ner till h\xF6ger</li></ul><div class="text-right spacer-mt-small"><a href="/docs/hyreslista-mall.xlsx" target="_self" download class="link--turquoise btn--icon btn--icon-download--dark-turq" ng-click="$event.stopPropagation()">Ladda ner enkel Hyreslista mall (Excel)</a></div></div><!--\r\n    <div ng-if="!vm.isHgf && vm.isRealEstateChoiceMethodEnabled(\'RENT_LIST\', vm.negotiationCall.type)">\r\n      <div class="red-letter-day" ng-click="vm.openRedWarning()">i</div>\r\n    </div>\r\n    --></div><!--LADDA UPP FASTIGHETSLISTA--><div class="wizard-choice display-flex flex-direction-row spacer-mb-small" ng-class="{\'wizard-choice--active\' : vm.realEstateChoiceMethod === \'PROPERTY_LIST\',\r\n       \'wizard-choice--disabled\': !vm.isRealEstateChoiceMethodEnabled(\'PROPERTY_LIST\', vm.negotiationCall.type)}" ng-click="vm.isRealEstateChoiceMethodEnabled(\'PROPERTY_LIST\', vm.negotiationCall.type) &amp;&amp;\r\n       vm.setRadioModelByOutsideClick(\'PROPERTY_LIST\')"><div class="custom-control custom-control--radio custom-control--radio--no-margin"><input class="custom-control__input custom-control__input--hidden" type="radio" id="inputPropertyList" name="radio-group" tabindex="1" ng-model="vm.realEstateChoiceMethod" value="PROPERTY_LIST" ng-disabled="!vm.isRealEstateChoiceMethodEnabled(\'PROPERTY_LIST\', vm.negotiationCall.type)" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="inputPropertyList"><span ng-if="!vm.isRealEstateChoiceMethodEnabled(\'PROPERTY_LIST\', vm.negotiationCall.type)">Ladda upp fastighetslista</span></label></div><div class="flex-grow-1" ng-hide="!vm.isRealEstateChoiceMethodEnabled(\'PROPERTY_LIST\', vm.negotiationCall.type)"><h3 class="spacer-mt-reset">Ladda upp fastighetslista</h3><ul class="wizard-choice__list"><li class="wizard-choice__list-item--yes">Ladda upp excelfil som listar alla fastigheter (aktuell hyra anges inte)</li><li class="wizard-choice__list-item--yes">Excelfilen m\xE5ste f\xF6lja ett strukturerat format, mall finns att ladda ner till h\xF6ger</li><li class="wizard-choice__list-item--yes">Hyreslistan (med ing\xE5ende hyror p\xE5 l\xE4genhetsniv\xE5) laddas upp vid senare tillf\xE4lle under f\xF6rhandlingen</li></ul><div class="text-right spacer-mt-small"><a href="/docs/fastighetslista-mall.xlsx" target="_self" download class="link--turquoise btn--icon btn--icon-download--dark-turq" ng-click="$event.stopPropagation()">Ladda ner Fastighetslista mall (Excel)</a></div></div><!--\r\n    <div ng-if="!vm.isHgf && vm.isRealEstateChoiceMethodEnabled(\'PROPERTY_LIST\', vm.negotiationCall.type)">\r\n      <div class="red-letter-day" ng-click="vm.openRedWarning()">i</div>\r\n    </div>\r\n    --></div><!--Ladda upp valfritt dokument--><div class="wizard-choice display-flex flex-direction-row spacer-mb-small" ng-class="{\'wizard-choice--active\' : vm.realEstateChoiceMethod === \'OPTIONAL_DOCUMENTS\',\r\n       \'wizard-choice--disabled\': !vm.isRealEstateChoiceMethodEnabled(\'OPTIONAL_DOCUMENTS\', vm.negotiationCall.type)}" ng-click="vm.isRealEstateChoiceMethodEnabled(\'OPTIONAL_DOCUMENTS\', vm.negotiationCall.type) &amp;&amp;\r\n       vm.setRadioModelByOutsideClick(\'OPTIONAL_DOCUMENTS\')"><div class="custom-control custom-control--radio custom-control--radio--no-margin"><input class="custom-control__input custom-control__input--hidden" type="radio" id="inputOptionalDocuments" name="radio-group" tabindex="4" ng-model="vm.realEstateChoiceMethod" value="OPTIONAL_DOCUMENTS" ng-disabled="!vm.isRealEstateChoiceMethodEnabled(\'OPTIONAL_DOCUMENTS\', vm.negotiationCall.type)" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="inputOptionalDocuments"><span ng-if="!vm.isRealEstateChoiceMethodEnabled(\'OPTIONAL_DOCUMENTS\', vm.negotiationCall.type)">Ladda upp valfritt dokument</span></label></div><div ng-hide="!vm.isRealEstateChoiceMethodEnabled(\'OPTIONAL_DOCUMENTS\', vm.negotiationCall.type)"><h3 class="spacer-mt-reset">Ladda upp valfritt dokument</h3><ul class="wizard-choice__list"><li class="wizard-choice__list-item--yes">Ladda upp dokument som du anser \xE4r relevant att bifoga framst\xE4llan</li><li class="wizard-choice__list-item--yes">Valfritt format \xE4r till\xE5tet</li><li class="wizard-choice__list-item--yes">Excelfil som listar ing\xE5ende l\xE4genheter laddas upp vid senare tillf\xE4lle under f\xF6rhandlingen</li></ul></div></div></form><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_examine-first-line.html','<ng-include src="\'resources/validation/partials/_examine-first-line-body.html\'"></ng-include><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_preview-and-submit-apartments.html','<div class="text-center"><hy-progress-circular size="60px" ng-show="vm.saving"></hy-progress-circular></div><ng-include src="\'resources/negotiation-call/new/partials/subpartials/_preview-and-submit-general-header.html\'"></ng-include><div class="alert alert--narrow-turq alert--squary" ng-hide="vm.saving"><ng-include src="\'resources/negotiation-call/new/partials/subpartials/_preview-and-submit-warning.html\'"></ng-include><div class="row"><div class="col-12 col-lg-6"><ng-include src="\'resources/negotiation-call/new/partials/subpartials/_preview-and-submit-general-col-1.html\'"></ng-include></div><div class="col-6 col-lg-4"><h4 class="spacer-mb-xsmall">Valt fastighetsbest\xE5nd</h4><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">L\xE4genheter: <span class="text-success text-bold">{{vm.validationResponse.summary.numApartments}}</span></div><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">Fastigheter: <span class="text-success text-bold">{{vm.validationResponse.summary.numProperties}}</span></div><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">Kommuner: <span class="text-success text-bold">{{vm.validationResponse.summary.numMunicipalities}}</span></div></div></div></div><div class="alert alert--narrow-red alert--squary" ng-show="vm.possibleErrorsList.length > 0"><div class="row spacer-mt-small"><div class="col-12"><div class="display-flex align-items-center spacer-mb-medium"><div class="icon icon--warning spacer-mr-small"></div><h4 class="text-margin-reset text-danger">De angivna l\xE4genheterna nedan \xE4r med i en annan p\xE5g\xE5ende f\xF6rhandling</h4></div><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__th--minimalistic table__td--width-10">Nationellt l\xE4genhetsnr</th><th class="table__th table__th--minimalistic table__td--width-10">L\xE4genhetsnr</th><th class="table__th table__th--minimalistic table__td--width-15">Fastighetsbeteckning</th><th class="table__th table__th--minimalistic table__td--width-15">Adress</th><th class="table__th table__th--minimalistic table__td--width-10">Kommun</th><th class="table__th table__th--minimalistic table__td--width-15">F\xF6rhandlas av</th><th class="table__th table__th--minimalistic table__td--width-25">Typ av f\xF6rhandling</th></tr><tr ng-hide="vm.searching" class="table__tr table__tr--minimalistic" ng-repeat="apartment in vm.possibleErrorsList"><td class="table__td table__td--minimalistic table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{ apartment.nationalNo | nationalNoControl }}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{ apartment.ownerNo }}</td><td class="table__td table__td--minimalistic">{{ apartment.propertyDesignation }}</td><td class="table__td table__td--minimalistic">{{ apartment.streetAddress }}</td><td class="table__td table__td--minimalistic">{{ apartment.municipality }}</td><td class="table__td table__td--minimalistic">{{ apartment.organizationName }}</td><td class="table__td table__td--minimalistic">{{ apartment.negotiationType | hyNegotiationTypes }}</td></tr></table><div ng-show="vm.searching" class="text-center spacer-mt-medium"><hy-progress-circular size="40px"></hy-progress-circular></div></div></div></div><div class="row"><div class="col text-right"><button class="btn" ng-click="vm.save()" ng-disabled="vm.saving">{{vm.lastButton}}</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_preview-and-submit-properties-new-built.html','<div class="text-center"><hy-progress-circular size="60px" ng-show="vm.saving"></hy-progress-circular></div><ng-include src="\'resources/negotiation-call/new/partials/subpartials/_preview-and-submit-general-header.html\'"></ng-include><div class="alert alert--narrow-turq alert--squary" ng-hide="vm.saving"><div class="row"><div class="col-12 col-lg-6"><ng-include src="\'resources/negotiation-call/new/partials/subpartials/_preview-and-submit-general-col-1.html\'"></ng-include><h4 class="text-margin-bottom-reset">Kommun</h4>{{ vm.negotiationCall.municipalityText }}</div><div class="col-12 col-lg-6"><h4 class="text-margin-bottom-reset">Fastighets\xE4gare</h4><div class="spacer-mb-medium">{{ vm.negotiationCall.ownersText }}</div><h4 class="spacer-mb-small">Uppladdade dokument</h4><div ng-repeat="document in vm.uploadedDocumentsList">{{ $index + 1 }}. {{ document.fileName }}</div></div></div></div><div class="row"><div class="col text-right"><button class="btn" ng-click="vm.save()" ng-disabled="vm.saving">{{vm.lastButton}}</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_preview-and-submit-properties.html','<div class="text-center"><hy-progress-circular size="60px" ng-show="vm.saving"></hy-progress-circular></div><ng-include src="\'resources/negotiation-call/new/partials/subpartials/_preview-and-submit-general-header.html\'"></ng-include><div class="alert alert--narrow-turq alert--squary" ng-hide="vm.saving"><ng-include src="\'resources/negotiation-call/new/partials/subpartials/_preview-and-submit-warning.html\'"></ng-include><div class="row"><div class="col-12 col-lg-6"><ng-include src="\'resources/negotiation-call/new/partials/subpartials/_preview-and-submit-general-col-1.html\'"></ng-include></div><div class="col-6 col-lg-4"><h4 class="spacer-mb-xsmall">Valt fastighetsbest\xE5nd</h4><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">L\xE4genheter: <span class="text-success text-bold">{{ vm.validationResponse.summary.numApartments ? vm.validationResponse.summary.numApartments : vm.apartmentsTotalCount ? vm.apartmentsTotalCount : \'Ej angivet\' }}</span></div><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">Fastigheter: <span class="text-success text-bold">{{vm.propertySummary.numProperties}}</span></div><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">Kommuner: <span class="text-success text-bold">{{vm.propertySummary.numMunicipalities}}</span></div><div class="display-flex flex-direction-row space-between spacer-mr-xxxlarge">Fastighets\xE4gare: <span class="text-success text-bold">{{vm.propertySummary.numOwners}}</span></div></div></div></div><div class="alert alert--narrow-red alert--squary" ng-show="vm.possibleErrorsList.length > 0"><div class="row spacer-mt-small"><div class="col-12"><div class="display-flex align-items-center spacer-mb-medium"><div class="icon icon--warning spacer-mr-small"></div><h4 class="text-margin-reset text-danger">De angivna l\xE4genheterna nedan \xE4r med i en annan p\xE5g\xE5ende f\xF6rhandling</h4></div><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__td--width-25">Fastighetsbeteckning</th><th class="table__th table__td--width-20">Kommun</th><th class="table__th table__td--width-20">F\xF6rhandlas av</th><th class="table__th table__td--width-35">Typ av f\xF6rhandling</th></tr><tr ng-hide="vm.searching" class="table__tr table__tr--minimalistic" ng-repeat="property in vm.possibleErrorsList"><td class="table__td table__td--minimalistic">{{ property.propertyDesignation }}</td><td class="table__td table__td--minimalistic">{{ property.municipality }}</td><td class="table__td table__td--minimalistic">{{ property.organizationName }}</td><td class="table__td table__td--minimalistic">{{ property.negotiationType | hyNegotiationTypes }}</td></tr></table><div ng-show="vm.searching" class="text-center spacer-mt-medium"><hy-progress-circular size="40px"></hy-progress-circular></div></div></div></div><div class="row"><div class="col text-right"><button class="btn" ng-click="vm.save()" ng-disabled="vm.saving">{{vm.lastButton}}</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_properties.html','<form name="vm.propertiesForm" ng-submit="vm.propertiesForm.$valid &amp;&amp; vm.gotoNextStep()" class="spacer-mb-xlarge" novalidate><div class="row"><p class="spacer-pl-medium">Ange de fastigheter du vill p\xE5kalla f\xF6rhandling f\xF6r genom att s\xF6ka fram dem i portalens register genom att skriva in fastighetsbeteckningen i f\xE4ltet nedan. De fastigheter du har valt visas i listan nedan. Om informationen inte st\xE4mmer i listan s\xE5 f\xE5r du g\xE5 tillbaka ett steg och v\xE4lja att ange best\xE5ndet genom en annan k\xE4lla.</p><div class="col-10 col-md-4"><div class="spacer-mb-xxlarge"><div class="dropdown dropdown--hide-caret dropdown--no-border" ng-class="{\'open\' : vm.toggleMenu}"><label class="display-flex align-items-center">S\xF6k fram och l\xE4gg till fastigheter<div ng-click="vm.showHelpProperties(\'S\xF6k fastigheter\', \'Om inte den fastighet du s\xF6ker finns med i resultatet, prova att specifiera din s\xF6kning (fler bokst\xE4ver).\')" class="icon icon--info icon--small cursor-pointer display-inline-block spacer-ml-xsmall"></div></label><div class="display-flex spacer-mb-xsmall"><input type="text" class="form-control form-control--search form-control--search--large" placeholder="S\xF6k p\xE5 alla" ng-model="vm.inputTextComplex" hy-enter="vm.searchProperties()" ng-focus="vm.toggleMenu = false"> <a href="#" class="btn--icon btn--icon-search btn--icon-search" ng-click="vm.searchProperties(); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--typeahead" role="menu"><button ng-repeat="property in vm.propertiesTypeahead" type="button" class="dropdown-item" ng-click="vm.addProperty(property); vm.toggleMenu = false">{{property.propertyDesignation}} ({{property.municipality}})</button></div></div></div></div><div class="col-2 col-md-6 display-flex align-items-center"><div ng-show="vm.searching"><hy-progress-circular size="40px"></hy-progress-circular></div></div><div class="col-12"><h3 class="spacer-mt-reset">Valda fastigheter</h3><hy-progress-circular size="40px" ng-hide="!vm.editMode || vm.readyProperties"></hy-progress-circular><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__td--width-20">Fastigheter</th><th class="table__th table__td--width-20">Kommun</th><th class="table__th table__td--width-20">LM distrikt</th><th class="table__th table__td--width-30">Fastighets\xE4gare</th><th class="table__th table__th--width-icon"></th></tr><tr class="table__tr table__tr--minimalistic" ng-show="vm.readyProperties" ng-repeat="property in vm.properties | orderBy:\'propertyDesignation\'"><td class="table__td table__td--minimalistic">{{property.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{property.municipality}}</td><td class="table__td table__td--minimalistic">{{property.district}}</td><td class="table__td table__td--minimalistic"><div ng-show="property.numOwners === 1">{{property.primaryOwner.name || \'Fakturamottagare ok\xE4nd\'}}</div><div ng-show="property.numOwners > 1" ng-click="vm.toggleOwnersList[property.id]=!vm.toggleOwnersList[property.id]; vm.getPropertyOwners(property.id); $event.preventDefault();"><strong>{{property.primaryOwner.name || \'Fakturamottagare ok\xE4nd\'}}</strong> <a href="#" class="btn--icon btn--icon-expand ellipsis" ng-class="!vm.toggleOwnersList[property.id] ? \'btn--icon-expand\' : \'btn--icon-collapse\'"></a><div ng-show="vm.toggleOwnersList[property.id]"><div ng-hide="vm.readyOwners[property.id]"><hy-progress-circular size="20px"></hy-progress-circular></div><div ng-repeat="owner in property.owners">{{owner.name}}</div></div></div></td><td class="table__td table__td--minimalistic"><div class="text-right"><a href="#" ng-click="vm.removeProperty(property); $event.preventDefault();" class="btn--icon btn--icon--no-padding btn--icon-bin"><span class="sr-only">Ta bort</span></a></div></td></tr></table></div></div></form><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_property-motivation.html','<form name="vm.propertyMotivationForm" ng-submit="vm.propertyMotivationForm.$valid &amp;&amp; vm.gotoNextStep()" class="spacer-mb-xlarge" novalidate><div class="row"><div class="col-9"><p>Om du\xA0vill\xA0ange\xA0yrkande\xA0och\xA0motiv\xA0till\xA0det\xA0valda\xA0best\xE5ndet\xA0s\xE5\xA0g\xF6rs\xA0det\xA0genom\xA0att\xA0markera\xA0objekten\xA0i\xA0listan\xA0nedan\xA0och\xA0tryck\xA0sedan\xA0p\xE5\xA0L\xE4gg\xA0till yrkande & motiv.\xA0 Vill\xA0du\xA0avvakta\xA0med\xA0att\xA0ange\xA0yrkande\xA0och\xA0motiv,\xA0tryck\xA0p\xE5\xA0N\xE4sta. Det finns m\xF6jlighet att ange yrkande och motiv vid senare tillf\xE4lle i f\xF6rhandlingen.</p></div><div class="col-3 spacer-mt-medium" ng-show="vm.readyProperties"><div class="col sort-container"><div class="dropdown dropdown--hide-caret" uib-dropdown><button type="button" class="btn btn--icon btn--icon-sort btn--squary btn--squary-white btn--squary-white__sort" uib-dropdown-toggle>{{vm.sortPropertyTitle}}</button><div class="dropdown-menu dropdown-menu--right dropdown-menu--sort" uib-dropdown-menu role="menu"><button ng-repeat="item in vm.listPropertyColumns" type="button" class="dropdown-item dropdown-item--sort" ng-click="vm.sortProperties(item.column, item.title)">{{item.title}} <span ng-show="vm.sortPropertyColumn === item.column" class="btn--icon" ng-class="vm.sortPropertyReverse ? \'btn--icon-sort-down\' : \'btn--icon-sort-up\'"></span></button></div></div></div></div><div class="col-12"><hy-progress-circular size="40px" ng-hide="vm.readyProperties"></hy-progress-circular><table class="table table--margin-reset" ng-show="vm.readyProperties"><thead class="table__header"><tr><th class="table__th table__td--width-6">Alla</th><th ng-hide="vm.fetchedPropertyList" class="table__th table__td--width-20">\xC4gare</th><th class="table__th table__td--width-20">Fastighetsbeteckning</th><th class="table__th table__td--width-15">Kommun</th><th class="table__th table__td--width-15">LM distrikt</th><th class="table__th table__td--width-15">Yrkande</th><th class="table__th table__td--width-25">Motiv</th></tr><tr><th class="table__th"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="allPropertiesBox" ng-model="vm.allPropertiesBoxChecked" ng-change="vm.toggleAllPropertiesSelection()"> <label class="custom-control__label" for="allPropertiesBox"></label></div></th><th ng-hide="vm.fetchedPropertyList" class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextOwners" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(0, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(0, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextProperty" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(1, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(1, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextMunicipality" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(2, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(2, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextDistrict" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(3, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(3, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputTextDemand" ng-change="vm.filterChanging=true;" hy-enter="vm.filterProperties(5, true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.filterProperties(5, true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-class="{\'text-black\': vm.selectedPropertyIds[property.id],\r\n            \'text-gray\': !vm.selectedPropertyIds[property.id]}" ng-repeat="property in vm.properties | filter:vm.propertiesFilter | orderBy:vm.sortPropertyColumn:vm.sortPropertyReverse"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{property.id}}" ng-model="vm.selectedPropertyIds[property.id]" ng-change="vm.updateAllPropertiesBoxState()"> <label class="custom-control__label" for="{{property.id}}"></label></div></td><td ng-hide="vm.fetchedPropertyList || Auth.isHgfUser()" class="table__td table__td--minimalistic"><hy-array-display model="property.owners"></hy-array-display></td><td ng-hide="vm.fetchedPropertyList || !Auth.isHgfUser()" class="table__td table__td--minimalistic"><hy-array-display model="property.owners"></hy-array-display></td><td class="table__td table__td--minimalistic">{{ property.propertyDesignation }}</td><td class="table__td table__td--minimalistic">{{ property.municipality }}</td><td class="table__td table__td--minimalistic">{{ property.district }}</td><td class="table__td table__td--minimalistic"><span ng-show="property.demand.unit === \'2\'">{{property.demand.stringValue}} </span><span ng-hide="property.demand.unit === \'2\'">{{property.demand.numericValue | number}} <span ng-show="property.demand.numericValue">{{ property.demand.unit | demandTypeToDescription }}</span></span></td><td class="table__td table__td--minimalistic">{{ property.demand.motivation }}</td></tr></table><div class="text-right spacer-mt-medium" ng-show="vm.readyProperties &amp;&amp; vm.properties.length > 0"><strong>Totalt: </strong>{{vm.properties.length}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="vm.readyProperties &amp;&amp; vm.properties.length === 0">Inga fastigheter hittades.</div></div></div></form><div class="row"><div class="col text-right"><button class="btn spacer-mr-small" ng-click="vm.editDemandAndMotivation()" ng-disabled="!vm.atLeastOneSelectedPropertyId()">L\xE4gg till yrkande &amp; motiv</button> <button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_upload-document-list.html','<p>Ladda\xA0upp\xA0dokument\xA0som\xA0du\xA0anser\xA0\xE4r\xA0relevant\xA0att\xA0bifoga\xA0framst\xE4llan.\xA0Det\xA0kan\xA0tex\xA0vara\xA0ritningar, l\xE4genhetsytor, fastighetsbeteckningar\xA0och\xA0kostnader mm.</p><div hy-file-upload blob-id="vm.attachmentBlobId" metadata="vm.metadata" valid-file-types="" max-file-size="1073741824" upload-url="/api/negotiation-calls/new-call-attachments" uploader="vm.uploaderOptionalDocuments" auto-upload disable-flash class="alert alert--border-turq alert--white-bg alert--squary"><form name="vm.fileUploadForm" ng-submit="vm.fileUploadForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><input name="inputDocumentListBlobId" id="inputDocumentListBlobId" type="hidden" ng-model="vm.attachmentBlobId" required><div class="position-relative row"><div class="file-upload__progress-bar"><uib-progressbar type="turquoise" value="vm.uploaderOptionalDocuments.progress" ng-show="vm.uploaderOptionalDocuments.isUploading" ng-class="\'progress--square\'"></uib-progressbar></div></div><div class="row"><div class="col-12 col-lg-5 display-flex align-items-center"><div class="file-upload__button"><label ng-if="vm.uploaderOptionalDocuments &amp;&amp; !vm.uploaderOptionalDocuments.isUploading" class="btn btn--reset-transition btn--upload"><input type="file" nv-file-select="" uploader="vm.uploaderOptionalDocuments"></label></div><div class="file-upload__status"><h4 class="text-margin-reset"><span ng-show="vm.uploaderOptionalDocuments &amp;&amp; !vm.tempBlobId.documentListBlobId\r\n              &amp;&amp; !vm.uploaderOptionalDocuments.isUploading">Ladda upp valfritt dokument</span> <span ng-show="vm.uploaderOptionalDocuments.isUploading">Laddar upp...</span></h4><p ng-show="vm.uploaderOptionalDocuments.currentFileName">{{vm.uploaderOptionalDocuments.currentFileName}}</p><button class="btn btn--secondary btn--reset-transition" ng-show="vm.tempBlobId.documentListBlobId || vm.uploaderOptionalDocuments.isUploading" ng-click="vm.uploaderOptionalDocuments.cancelUpload()">{{vm.tempBlobId.documentListBlobId ? \'Ta bort\' : \'Avbryt\'}}</button></div></div><div class="col-12 col-lg-5"><div ng-repeat="document in vm.uploadedDocumentsList" class="info__content info__content--narrow display-flex align-items-center space-between"><div><strong>{{ $index + 1 }}. </strong>{{document.fileName | limitTo: 40}}</div><div class="info__content--narrow-block"><a href="#" class="btn--icon btn--icon-bin btn--icon--no-padding" ng-click="vm.deleteDocument($index); $event.preventDefault();"><span class="sr-only">Ta bort</span></a></div></div></div><div class="col-7 col-lg-2 display-flex align-items-center"><div>Filerna kan laddas upp i valfritt format t. ex. pdf, pptx, xlxs, docx mm.</div></div></div></form></div><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_upload-property-list.html','<div hy-file-upload-2 blob-id="vm.negotiationCall.propertyListBlobId" metadata="vm.metadata" valid-file-types="xlsx" max-file-size="1073741824" upload-url="/api/negotiation-calls/property-list" uploader="vm.propertyFileUploader" auto-upload disable-flash error-invalid-file="vm.errorInvalidFile" validate-upload validation-url="/api/negotiation-calls/property-list" f-on-pending="vm.onPending" validation-response="vm.validationResponse" class="alert alert--border-turq alert--white-bg alert--squary"><form name="vm.propertyListUploadForm" ng-submit="vm.propertyListUploadForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><input name="inputPropertyListBlobId" id="inputPropertyListBlobId" type="hidden" ng-model="vm.inputBlobId" required><div class="position-relative row"><div class="file-upload__progress-bar"><uib-progressbar type="turquoise" value="vm.propertyFileUploader.progress" ng-show="vm.propertyFileUploader.isUploading &amp;&amp; !vm.propertyFileUploader.isValidating" ng-class="\'progress--square\'"></uib-progressbar></div></div><div class="row"><div class="col-12 col-lg-8"><div class="display-flex align-items-center"><div class="file-upload__button"><label ng-if="vm.propertyFileUploader &amp;&amp; !vm.negotiationCall.propertyListBlobId &amp;&amp; !vm.propertyFileUploader.isUploading &amp;&amp; !vm.propertyFileUploader.isValidating" class="btn btn--reset-transition btn--upload"><input type="file" nv-file-select="" uploader="vm.propertyFileUploader"></label> <button class="btn btn--reset-transition btn--cancel btn-spinning-flack" ng-show="vm.propertyFileUploader.isUploading &amp;&amp; !vm.propertyFileUploader.isValidating" ng-click="vm.propertyFileUploader.cancelUpload()"></button><hy-progress-circular size="90px" ng-show="vm.propertyFileUploader.isValidating"></hy-progress-circular><div class="btn btn--reset-transition btn--done" ng-show="vm.negotiationCall.propertyListBlobId &amp;&amp; !vm.propertyFileUploader.isValidating"></div></div><div class="file-upload__status"><h4 class="text-margin-reset spacer-mb-small"><span ng-show="vm.propertyFileUploader &amp;&amp; !vm.negotiationCall.propertyListBlobId &amp;&amp; !vm.propertyFileUploader.isUploading\r\n              &amp;&amp; !vm.propertyFileUploader.isValidating &amp;&amp; !vm.errorInvalidFile">Ladda upp fastighetslista</span> <span ng-show="vm.errorInvalidFile">{{vm.errorInvalidFile}}</span> <span ng-show="vm.propertyFileUploader.isUploading &amp;&amp; !vm.propertyFileUploader.isValidating">Laddar upp...</span> <span ng-show="vm.propertyFileUploader.isValidating">Analyserar din fil...</span> <span ng-show="vm.negotiationCall.propertyListBlobId &amp;&amp; !vm.propertyFileUploader.isValidating">Uppladdat!</span></h4><p ng-show="vm.propertyFileUploader.currentFileName">{{vm.propertyFileUploader.currentFileName}}</p><button class="btn btn--secondary btn--reset-transition" ng-show="vm.negotiationCall.propertyListBlobId || vm.propertyFileUploader.isUploading || vm.propertyFileUploader.isValidating" ng-click="vm.propertyFileUploader.cancelUpload()">{{vm.negotiationCall.propertyListBlobId ? \'Ta bort\' : \'Avbryt\'}}</button> <button class="btn btn--reset-transition" ng-show="vm.negotiationCall.propertyListBlobId || vm.propertyFileUploader.isValidating" ng-click="vm.toggleAdditionalInfoPane(); $event.preventDefault();">{{ vm.showAdditionalInfoPane ? \'D\xF6lj\' : \'Visa\' }} info</button></div></div><div ng-show="vm.showAdditionalInfoPane"><ng-include src="\'resources/validation/partials/_additional-info.html\'"></ng-include></div></div><div class="col-12 col-lg-4"><div ng-hide="vm.propertyFileUploader.isValidating"><h4 class="text-margin-top-reset spacer-mb-small">Kom ih\xE5g!</h4><span>Filen m\xE5ste vara i Excel-format (XLSX).</span> <span>Ladda ner v\xE5r <a href="/docs/fastighetslista-mall.xlsx" target="_self" download class="link--turquoise">exempelmall</a> f\xF6r att se vilket format som kr\xE4vs f\xF6r att kunna importera.</span></div></div></div></form></div><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/new/partials/_upload-rent-list.html','<div hy-file-upload-2 blob-id="vm.negotiationCall.rentListBlobId" metadata="vm.metadata" valid-file-types="xlsx" max-file-size="1073741824" upload-url="/api/negotiation-calls/rent-list" uploader="vm.uploader" auto-upload disable-flash error-invalid-file="vm.errorInvalidFile" validate-upload validation-url="/api/negotiation-calls/rent-list" f-on-pending="vm.onPending" validation-response="vm.validationResponse" state="vm.negotiationCall.type" class="alert alert--border-turq alert--white-bg alert--squary"><form name="vm.rentListUploadForm" ng-submit="vm.rentListUploadForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><input name="inputRentListBlobId" id="inputRentListBlobId" type="hidden" ng-model="vm.inputBlobId" required><div class="position-relative row"><div class="file-upload__progress-bar"><uib-progressbar type="turquoise" value="vm.uploader.progress" ng-show="vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating" ng-class="\'progress--square\'"></uib-progressbar></div></div><div class="row"><div class="col-12 col-lg-8"><div class="display-flex align-items-center"><div class="file-upload__button"><label ng-if="vm.uploader &amp;&amp; !vm.negotiationCall.rentListBlobId &amp;&amp; !vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating" class="btn btn--reset-transition btn--upload"><input type="file" nv-file-select="" uploader="vm.uploader"></label> <button class="btn btn--reset-transition btn--cancel btn-spinning-flack" ng-show="vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating" ng-click="vm.uploader.cancelUpload()"></button><hy-progress-circular size="90px" ng-show="vm.uploader.isValidating"></hy-progress-circular><div class="btn btn--reset-transition btn--done" ng-show="vm.negotiationCall.rentListBlobId &amp;&amp; !vm.uploader.isValidating"></div></div><div class="file-upload__status"><h4 class="text-margin-reset"><span ng-show="vm.uploader &amp;&amp; !vm.negotiationCall.rentListBlobId &amp;&amp; !vm.uploader.isUploading\r\n                &amp;&amp; !vm.uploader.isValidating &amp;&amp; !vm.errorInvalidFile">Ladda upp hyreslista</span> <span ng-show="vm.errorInvalidFile">{{vm.errorInvalidFile}}</span> <span ng-show="vm.uploader.isUploading &amp;&amp; !vm.uploader.isValidating">Laddar upp...</span> <span ng-show="vm.uploader.isValidating">Analyserar din fil...</span> <span ng-show="vm.negotiationCall.rentListBlobId &amp;&amp; !vm.uploader.isValidating">Uppladdat!</span></h4><p ng-show="vm.uploader.currentFileName">{{vm.uploader.currentFileName}}</p><button class="btn btn--secondary btn--reset-transition" ng-show="vm.uploader.isUploading || vm.uploader.isValidating" ng-click="vm.uploader.cancelUpload()" type="button">Avbryt</button> <button class="btn btn--secondary btn--reset-transition" ng-show="vm.negotiationCall.rentListBlobId" ng-click="vm.deleteUploadedFile()" type="button">Ta bort</button> <button class="btn btn--reset-transition" ng-show="vm.negotiationCall.rentListBlobId || vm.uploader.isValidating" ng-click="vm.toggleAdditionalInfoPane(); $event.preventDefault();">{{ vm.showAdditionalInfoPane ? \'D\xF6lj\' : \'Visa\' }} info</button><p class="spacer-mt-large" ng-show="vm.editMode &amp;&amp; vm.negotiationCall.rentListBlobId">Om du vill \xE4ndra i din tidigare uppladdade hyrelslista g\xE5 till underlag och<br>ladda ner orginalet f\xF6rst, innan du trycker p\xE5 "Ta bort".</p></div></div><div ng-show="vm.showAdditionalInfoPane"><ng-include src="\'resources/validation/partials/_additional-info.html\'"></ng-include></div></div><div class="col-12 col-lg-4"><div ng-hide="vm.uploader.isValidating"><h4 class="text-margin-top-reset spacer-mb-small">Kom ih\xE5g!</h4><span>Filen m\xE5ste vara i Excel-format (XLSX).</span> <span>Ladda ner v\xE5r <a href="/docs/hyreslista-mall.xlsx" target="_self" download class="link--turquoise">exempelmall</a> f\xF6r att se vilket format som kr\xE4vs f\xF6r att kunna importera.</span></div></div></div></form></div><div class="row"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/negotiation-call/show/documents/show.html','<div ng-controller="ShowNegotiationCallDocumentsCtrl as filesVM"><hy-progress-circular ng-hide="filesVM.readyDocuments" size="60px"></hy-progress-circular><div class="row" ng-show="filesVM.files.length === 0"><div class="col"><p ng-show="filesVM.files.length === 0">Det finns inga underlag f\xF6r den h\xE4r f\xF6rhandlingen \xE4nnu.</p></div></div><div class="row spacer-mb-large" ng-show="filesVM.Auth.canDo(\'uploadDocument\') && filesVM.readyDocuments"><div class="col-12 display-flex justify-content-end"><button type="button" class="btn btn--icon btn--icon-upload spacer-mt-medium" ng-click="filesVM.add()">Ladda upp underlag</button></div></div><table class="table spacer-mt-small col" ng-show="filesVM.files.length > 0 &amp;&amp; filesVM.readyDocuments"><tr class="table__header"><th class="table__th table__td--width-30">Dokument</th><th class="table__th table__td--width-15">Typ</th><th class="table__th table__td--width-15">Dokument\xE4gare</th><th class="table__th table__td--width-15">Part</th><th class="table__th table__td--width-15">Uppladdad den</th><th class="table__th table__td--width-10"></th></tr><tr class="table__distance"></tr><tr class="table__tr table__tr--disable-click" ng-repeat="file in filesVM.files | orderBy:\'-lastModified\'"><td class="table__td">{{ file.filename }}</td><td class="table__td">{{ file.type | documentTypes}}</td><td class="table__td">{{ file.createdBy }}</td><td class="table__td">{{ file.party === 20 ? \'HGF\' : vm.negotiationCall.counterpart.name }}</td><td class="table__td">{{ file.lastModified | date : "yyyy-MM-dd"}}</td><td class="table__td"><div class="display-flex space-between align-items-center"><div><a href="/api/negotiation-calls/{{vm.negotiationCall.id}}/documents/{{file.name}}" target="_self" download class="btn--icon btn--icon-download--dark-turq"><span class="sr-only">Ladda ner</span></a></div><div ng-show="Auth.canDo(\'deleteDocument\') && ((file.party === filesVM.Auth.currentUser().party || filesVM.Auth.isHgfUser()))"><a href="#" ng-click="filesVM.delete(file);$event.preventDefault();" class="btn--icon btn--icon-bin"><span class="sr-only">Ta bort</span></a></div></div></td></tr></table></div>');
$templateCache.put('resources/negotiation-call/show/remaining/show.html','<div ng-controller="ShowNegotiationCallRemainingCtrl as remainingVM"><div class="row"><div class="col"><p ng-show="remainingVM.rentFile.blobId">Hyreslista \xE4r uppladdad i f\xF6rhandlingsframst\xE4llan och kan hittas som dokument under fliken Underlag</p></div></div><div class="row"><div class="col"><p ng-hide="remainingVM.rentFile.blobId">F\xF6rhandlingsframst\xE4llan har skapats genom att v\xE4lja fastigheter ur portalen. En hyreslista kommer att beh\xF6va laddas upp innan f\xF6rsta f\xF6rhandlingsm\xF6tet.</p></div></div><hy-progress-circular ng-hide="remainingVM.readyProperties" center-on-page size="60px"></hy-progress-circular><div class="row"><div class="col"><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="remainingVM.listColumns" order="remainingVM.sortColumn" direction="remainingVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Fastighetsbeteckning</th><th class="table__th">Kommun</th><th class="table__th">LM distrikt</th><th class="table__th">Antal l\xE4genheter</th></tr><tr><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="remainingVM.inputTextProperty" hy-enter="remainingVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="remainingVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="remainingVM.inputTextMunicipality" hy-enter="remainingVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="remainingVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="remainingVM.inputTextDistrict" hy-enter="remainingVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="remainingVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="property in remainingVM.properties" ng-class="{\'table__tr--muted\' : !remainingVM.readyProperties}"><td class="table__td table__td--minimalistic">{{property.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{property.municipality}}</td><td class="table__td table__td--minimalistic">{{property.district}}</td><td class="table__td table__td--minimalistic">{{property.numNegotiatedApartments}}</td></tr></table><div class="row spacer-mt-medium" ng-show="remainingVM.propertyTotalCount > remainingVM.propertiesPageLimit"><div class="col-12 text-right"><hy-paginator get-list="remainingVM.queryProperties" page-offset="remainingVM.propertiesPageOffset" page-limit="remainingVM.propertiesPageLimit" total-count="remainingVM.propertyTotalCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="remainingVM.propertyTotalCount > 0 &amp;&amp; remainingVM.propertyTotalCount <= remainingVM.propertiesPageLimit"><div class="col-12 text-right"><strong>Totalt: </strong>{{remainingVM.propertyTotalCount}}</div></div><div class="row"><div class="col-12"><div class="alert alert--hide-border alert--slim alert--squary" ng-show="remainingVM.propertyTotalCount === 0 &amp;&amp; vm.negotiationCall.state != vm.negotiationCallStateAssigned">Ingen fastighet kvar att tilldela.</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="remainingVM.propertyTotalCount === 0 &amp;&amp; vm.negotiationCall.state === vm.negotiationCallStateAssigned">Alla fastigheter p\xE5 f\xF6rhandlingsframst\xE4llan har tilldelats en f\xF6rhandling.</div></div></div></div></div></div>');
$templateCache.put('resources/negotiation-call/show/properties/show.html','<div ng-controller="ShowNegotiationCallPropertiesCtrl as propertiesVM"><div class="row"><div class="col"><div ng-show="propertiesVM.rentFile.blobId"><p>Hyreslista \xE4r uppladdad i f\xF6rhandlingsframst\xE4llan och kan hittas som dokument under fliken Underlag</p></div></div></div><div ng-if="vm.isPropertyBased || vm.negotiationCall.isNewlyConstructedApartment" ng-include="\'resources/negotiation-call/show/properties/_property-table.html\'"></div><div ng-if="!vm.isPropertyBased" ng-hide="vm.negotiationCall.isNewlyConstructedApartment" ng-include="\'resources/negotiation-call/show/properties/_apartment-table.html\'"></div></div>');
$templateCache.put('resources/negotiation-call/show/properties/_apartment-table.html','<div class="row"><hy-progress-circular ng-hide="propertiesVM.readyApartments" center-on-page size="60px"></hy-progress-circular><div class="col-12"><div class="display-flex flex-direction-row space-between align-items-center spacer-mb-medium"><h3 class="text-margin-reset">L\xE4genheter</h3><div class="sort-container"><hy-sorter list-columns="propertiesVM.listColumns" order="propertiesVM.sortColumn" direction="propertiesVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Fastighetsbeteckning</th><th class="table__th">Gatuadress</th><th class="table__th">Nationella l\xE4genhetsnr</th><th class="table__th">L\xE4genhetsnr</th><th class="table__th">Kommun</th><th class="table__th">Fastighets\xE4gare</th><th class="table__th">Yrkande</th><th class="table__th">Motiv</th></tr><tr class="table__tr table__tr--minimalistic"><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="propertiesVM.inputTextProperty" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="propertiesVM.inputTextStreetAddress" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="propertiesVM.inputTextNationalNumber" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="propertiesVM.inputTextApartmentNumber" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="propertiesVM.inputTextMunicipality" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="propertiesVM.inputTextOwner" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"></th><th class="table__th"></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="apartment in propertiesVM.apartments" ng-class="{\'table__tr--muted\' : !propertiesVM.readyApartments}"><td class="table__td table__td--minimalistic">{{ apartment.property.propertyDesignation }}</td><td class="table__td table__td--minimalistic">{{ apartment.streetAddress }}</td><td class="table__td table__td--minimalistic table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{ apartment.nationalNo | nationalNoControl }}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{ apartment.ownerNo }}</td><td class="table__td table__td--minimalistic">{{ apartment.municipality }}</td><td class="table__td table__td--minimalistic"><div ng-show="apartment.numOwners === 1 &amp;&amp; apartment.primaryOwner.name !== null">{{apartment.primaryOwner.name}}</div><div ng-show="apartment.numOwners === 1 &amp;&amp; apartment.primaryOwner.name === null">{{apartment.owners[0].name}}</div><div ng-show="apartment.numOwners > 1"><div class="spacer-mb-xsmall"><a href="#" class="btn--icon btn--icon-expand ellipsis" ng-class="!overviewVM.toggleOwnersList[apartment.id] ? \'btn--icon-expand\' : \'btn--icon-collapse\'" ng-click="overviewVM.toggleOwnersList[apartment.id]=!overviewVM.toggleOwnersList[apartment.id]; overviewVM.getRealEstateOwners(apartment.property.id, false); $event.preventDefault();"><span>{{apartment.primaryOwner.name || \'Fakturamottagare ok\xE4nd\'}}</span></a></div><div ng-show="overviewVM.toggleOwnersList[apartment.id]"><div ng-hide="overviewVM.readyOwners[apartment.property.id]"><hy-progress-circular size="20px"></hy-progress-circular></div><div ng-repeat="owner in apartment.owners">{{owner.name}}</div></div></div></td><td class="table__td table__td--minimalistic table__td--nowrap"><span ng-show="apartment.demand.unit === \'2\'">{{apartment.demand.stringValue}} </span><span ng-hide="apartment.demand.unit === \'2\'">{{apartment.demand.numericValue | number}} <span ng-show="apartment.demand.numericValue">{{apartment.demand.unit | demandTypeToDescription}}</span></span></td><td class="table__td table__td--minimalistic">{{ apartment.demand.motivation }}</td></tr></table><div class="row spacer-mt-medium" ng-show="propertiesVM.apartmentsTotalCount > propertiesVM.propertiesPageLimit"><div class="col-12 text-right"><hy-paginator get-list="propertiesVM.queryProperties" page-offset="propertiesVM.propertiesPageOffset" page-limit="propertiesVM.propertiesPageLimit" total-count="propertiesVM.apartmentsTotalCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="propertiesVM.apartmentsTotalCount > 0 &amp;&amp; propertiesVM.apartmentsTotalCount <= propertiesVM.propertiesPageLimit"><div class="col-12 text-right"><strong>Totalt: </strong>{{propertiesVM.apartmentsTotalCount}}</div></div><div class="row"><div class="col-12"><div class="alert alert--hide-border alert--slim alert--squary" ng-show="propertiesVM.apartmentsTotalCount === 0">Ingen fastighet hittades.</div></div></div></div></div>');
$templateCache.put('resources/negotiation-call/show/properties/_property-table.html','<div class="row" ng-show="vm.negotiationCall.type === vm.NegotiationTypes.NEWLY_CONSTRUCTED_PROPERTY"><div class="col col-sm-12 col-lg-8"><p>Fastighetsbest\xE5ndet \xE4r \xE4nnu inte k\xE4nt i detalj. Se information under fliken "Underlag".</p></div></div><hy-progress-circular ng-hide="propertiesVM.readyProperties" center-on-page size="60px"></hy-progress-circular><div class="row"><div class="col-12"><div class="display-flex flex-direction-row space-between align-items-center spacer-mb-medium"><h3 class="text-margin-reset">{{vm.negotiationCall.isNewlyConstructedApartment ? \'Valda befintliga fastigheter\' : \'Fastigheter\'}}</h3><div class="sort-container"><hy-sorter list-columns="propertiesVM.listColumns" order="propertiesVM.sortColumn" direction="propertiesVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Fastighetsbeteckning</th><th class="table__th table__td--width-10">Kommun</th><th class="table__th table__td--width-20">LM distrikt</th><th class="table__th">Antal<br>l\xE4genheter</th><th class="table__th">Fastighets\xE4gare</th><th class="table__th">Yrkande</th><th class="table__th">Motiv</th></tr><tr><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search--full-width" placeholder="S\xF6k" ng-model="propertiesVM.inputTextProperty" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search--full-width" placeholder="S\xF6k" ng-model="propertiesVM.inputTextMunicipality" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search--full-width" placeholder="S\xF6k" ng-model="propertiesVM.inputTextDistrict" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search form-control--search--full-width" placeholder="S\xF6k" ng-model="propertiesVM.inputTextOwner" hy-enter="propertiesVM.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="propertiesVM.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"></th><th class="table__th"></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="property in propertiesVM.properties" ng-class="{\'table__tr--muted\' : !propertiesVM.readyProperties}"><td class="table__td table__td--minimalistic">{{property.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{property.municipality}}</td><td class="table__td table__td--minimalistic">{{property.district}}</td><td class="table__td table__td--minimalistic">{{property.numNegotiatedApartments ? property.numNegotiatedApartments : \'Ej angivet\'}}</td><td class="table__td table__td--minimalistic"><hy-array-display model="property.owners"></hy-array-display></td><td class="table__td table__td--minimalistic table__td--nowrap"><span ng-show="property.demand.unit === \'2\'">{{property.demand.stringValue}} </span><span ng-hide="property.demand.unit === \'2\'">{{property.demand.numericValue | number}} <span ng-show="property.demand.numericValue">{{property.demand.unit | demandTypeToDescription}}</span></span></td><td class="table__td table__td--minimalistic">{{property.demand.motivation}}</td></tr></table><div class="row spacer-mt-medium" ng-show="propertiesVM.propertyTotalCount > propertiesVM.propertiesPageLimit"><div class="col-12 text-right"><hy-paginator get-list="propertiesVM.queryProperties" page-offset="propertiesVM.propertiesPageOffset" page-limit="propertiesVM.propertiesPageLimit" total-count="propertiesVM.propertyTotalCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="propertiesVM.propertyTotalCount > 0 &amp;&amp; propertiesVM.propertyTotalCount <= propertiesVM.propertiesPageLimit"><div class="col-12 text-right"><strong>Totalt: </strong>{{propertiesVM.propertyTotalCount}}</div></div><div class="row"><div class="col-12"><div class="alert alert--hide-border alert--slim alert--squary" ng-show="propertiesVM.propertyTotalCount === 0">Ingen fastighet hittades.</div></div></div></div></div>');
$templateCache.put('resources/agreement/show/documents/show.html','<div ng-controller="ShowAgreementDocumentsCtrl as filesVM" class="alert alert--narrow-gray alert--squary spacer-mt-large"><div class="display-flex space-between"><div><h3 class="text-margin-reset di">Bilagor</h3><hy-progress-circular class="di" size="20px" ng-if="filesVM.busy"></hy-progress-circular></div><button type="button" class="btn btn--icon btn--icon-upload" ng-hide="Auth.isDelegateUser() || vm.negotiation.isFinished" ng-click="filesVM.addAgreementFile()">Ladda upp dokument</button></div><table class="table spacer-mt-large"><tr class="table__header"><th class="table__th table__td--width-15">\u2116</th><th class="table__th table__td--width-25">Namn</th><th class="table__th table__td--width-15">Typ av underlag</th><th class="table__th table__td--width-20">Skapad av</th><th class="table__th table__td--width-20">Storlek</th><th class="table__th table__td--width-5"></th></tr><tr class="table__tr table__tr--disable-click" ng-mouseover="filesVM.hoverFile = file" ng-repeat="file in vm.agreement.documents | orderBy:\'order\'"><td class="table__td" align="center"><h3 ng-click="filesVM.changeOrder(file, false)" class="cursor-pointer spacer-margin-reset di spacer-mr-small" ng-show="(vm.agreement.documents.length > 1) && !filesVM.busy && (filesVM.hoverFile === file)">\u2193</h3><span>{{ file.order }}</span><h3 ng-click="filesVM.changeOrder(file, true)" class="cursor-pointer spacer-margin-reset di spacer-ml-small" ng-show="(vm.agreement.documents.length > 1) && !filesVM.busy && (filesVM.hoverFile === file)">\u2191</h3></td><td class="table__td"><a href="#" ng-click="filesVM.downloadAgreementFile(file); $event.preventDefault();">{{ file.filename }} </a><span ng-show="Auth.isDelegateUser() &amp;&amp; overviewVM.isRentListFileType(file.type)">{{ file.filename }}</span></td><td class="table__td">{{file.type | documentTypes}}</td><td class="table__td">{{ file.createdBy }}</td><td class="table__td">{{ file.contentLength | size }}</td><td class="table__td"><div class="display-flex space-between align-items-center"><div ng-hide="Auth.isDelegateUser() &amp;&amp; overviewVM.isRentListFileType(file.type)"><a href="#" ng-click="filesVM.downloadAgreementFile(file); $event.preventDefault();" class="btn--icon btn--icon--no-padding btn--icon-download--dark-turq"><span class="sr-only">Ha</span></a></div><div ng-show="!Auth.isDelegateUser() &amp;&amp; file.party===Auth.currentUser().party &amp;&amp; !vm.negotiation.isFinished"><a href="#" ng-if="Auth.canDo(\'deleteAgreementDocument\')" ng-click="filesVM.deleteAgreementFile(file); $event.preventDefault();" class="btn--icon btn--icon--no-padding btn--icon-bin" ng-disabled="!vm.signStartVisible" ng-class="{\'abtn--disabled\' : !vm.signStartVisible}"><span class="sr-only">Ta bort</span></a></div></div></td></tr><tr ng-if="vm.agreement.numDocuments > 0"><td class="table__td"></td><td class="table__td">{{ vm.agreement.numDocuments }}<div ng-if="vm.agreement.numDocuments > filesVM.FPConsts.attachmentLimits.numWarning" class="icon icon--message-warning spacer-margin-reset bogus-tooltip-icon" uib-tooltip="Det finns f\xF6r m\xE5nga bifogade filer ({{ filesVM.FPConsts.attachmentLimits.numWarning }} max)"></div></td><td class="table__td"></td><td class="table__td"></td><td class="table__td">{{ vm.agreement.sizeDocuments | size }}<div ng-if="vm.agreement.sizeDocuments > filesVM.FPConsts.attachmentLimits.sizeWarning" class="icon icon--message-warning spacer-margin-reset bogus-tooltip-icon" uib-tooltip="Bifogade filer \xE4r f\xF6rstora. V\xE4nligen komprimera dem ({{ filesVM.FPConsts.attachmentLimits.sizeWarning | size }} max)"></div></td><td class="table__td"></td><td class="table__td"></td></tr></table><div ng-if="vm.agreement.documents.length === 0">Inga dokument uppladdade</div></div>');
$templateCache.put('resources/agreement/rentlist/edit/edit-comment-modal.html','<div class="modal-header modal-header--medium"><h2 class="text-center text-margin-reset">redigera kommentar</h2></div><div class="modal-body modal-body--medium" id="modal-body"><div class="row"><textarea class="form-control form-control--gray-border" placeholder="{{ vm.multipleComments ? \'Varning, det finns flera kommentarer p\xE5 l\xE4genheter som kommer skrivas \xF6ver : \'+vm.multipleComments:\'\' }}" ng-model="vm.comment" ng-change="vm.isDirty = true" autofocus></textarea></div></div><div class="modal-footer"><button class="btn btn--secondary" ng-if="vm.hadSomething" ng-click="vm.delete()">Rensa</button> <button class="btn" ng-disabled="!vm.isDirty" ng-click="vm.ok()">OK</button> <a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a></div>');
$templateCache.put('resources/agreement/rentlist/edit/edit-objects-modal.html','<div class="modal-header modal-header--medium"><h2 class="text-center text-margin-reset">Ange objektsinformation</h2></div><div class="modal-body modal-body--medium" id="modal-body"><div class="row"><div class="col-5"><tree-view root="vm.objectRoot" model="vm.treeViewModel" filtering="true" checkboxes="true" ticked-nodes="vm.treeViewNodes" mode="list"></tree-view><button class="btn spacer-mt-small" ng-click="vm.addObjects()" ng-disabled="!vm.treeViewNodes || !vm.treeViewNodes.length">L\xE4gg till</button></div><div class="col-7" ng-if="vm.objects && vm.objects.length"><table class="table"><tr><th align="left">Namn</th><th width="60px" align="left">Antal</th><th width="10px" align="left"></th><th width="80px" align="left">Utg\xE5ngsdatum</th><th width="15px"></th><th width="70px" align="left">Belopp</th><th width="25px"></th><th width="30px"></th></tr><tr><td colspan="7"><small><center><br>Tidigare f\xF6rhandlade standardf\xF6r\xE4ndringar</center></small></td></tr><tr ng-repeat-start="object in vm.objects" ng-if="object.separator"><td colspan="7" align="right"><hr class="hr--thin">+{{vm.sumOld}} <small>kr/m\xE5</small><br><br><small><center>Standardf\xF6r\xE4ndringar i denna f\xF6rhandling</center></small></td></tr><tr ng-repeat-end><td align="left" title="{{object.code}}">{{object.name}}</td><td align="right"><input type="number" min="1" class="form-control" ng-model="object.quantity"></td><td></td><td align="left"><input type="date" class="form-control" ng-model="object.expirationDate"></td><td align="right">+</td><td align="right"><input type="number" min="0" class="form-control" ng-model="object.value" ng-change="vm.recalculate()"></td><td>kr/m\xE5</td><td><a href="#" class="btn--icon btn--icon-bin" ng-click="vm.deleteObject(object)"></a></td></tr><tr><td colspan="7"><hr class="hr--thin"></td></tr><tr><td align="left">Totalt</td><td colspan="6" align="right">+{{vm.sum}} <small>kr/m\xE5</small></td></tr></table></div></div></div><div class="modal-footer"><button class="btn btn--secondary" ng-if="vm.canDelete" ng-click="vm.delete()">Rensa</button> <button class="btn" ng-disabled="isNaN(vm.sum) || !(vm.objects && vm.objects.length)" ng-click="vm.ok()">OK</button> <a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a></div>');
$templateCache.put('resources/agreement/rentlist/edit/edit-rental-level-modal.html','<form name="rentalLevelForm" ng-submit="rentalLevelForm.$valid  &amp;&amp; vm.add()" novalidate><div class="modal-header modal-header--medium"><h2 class="text-center text-margin-reset">\xC4ndra hyror</h2></div><div class="modal-body modal-body--medium" id="modal-body"><hy-tabcontrol ng-if="vm.tabs" tablist="vm.tabs" current-tab="vm.currentTab" ng-click="vm.changeMethod = null"></hy-tabcontrol><div ng-if="vm.currentTab === \'relative\'"><div class="display-flex flex-direction-column spacer-mt-small"><div class="custom-control custom-control--radio spacer-mr-medium"><input class="custom-control__input" type="radio" id="inputTypePercent" name="rb_group" tabindex="0" ng-model="vm.changeMethod" value="0" ng-click="vm.focusOn()"> <label class="custom-control__label" for="inputTypePercent">Procent %</label></div><div class="custom-control custom-control--radio spacer-mr-medium"><input class="custom-control__input" type="radio" id="inputTypeKronor" name="rb_group" tabindex="1" ng-model="vm.changeMethod" value="1" ng-click="vm.focusOn()"> <label class="custom-control__label" for="inputTypeKronor">F\xF6r\xE4ndring kr/m\xE5n</label></div><div class="custom-control custom-control--radio spacer-mr-medium"><input class="custom-control__input" type="radio" id="inputTypeNyHyra" name="rb_group" tabindex="2" ng-model="vm.changeMethod" value="2" ng-click="vm.focusOn()"> <label class="custom-control__label" for="inputTypeNyHyra">F\xF6r\xE4ndring kr/m<sup>2</sup></label></div></div></div><div ng-if="vm.currentTab === \'absolute\'"><div class="display-flex flex-direction-column spacer-mt-small"><div class="custom-control custom-control--radio spacer-mr-medium"><input class="custom-control__input" type="radio" id="inputTypeKronor" name="rb_group" tabindex="1" ng-model="vm.changeMethod" value="1" ng-click="vm.focusOn()"> <label class="custom-control__label" for="inputTypeKronor">Ny hyra kr/m\xE5n</label></div><div class="custom-control custom-control--radio spacer-mr-medium"><input class="custom-control__input" type="radio" id="inputTypeNyHyra" name="rb_group" tabindex="2" ng-model="vm.changeMethod" value="2" ng-click="vm.focusOn()"> <label class="custom-control__label" for="inputTypeNyHyra">Ny hyra kr/m<sup>2</sup></label></div></div></div><div ng-if="vm.changeMethod"><div ng-if="vm.changeMethod === \'0\'"><div ng-if="vm.suggestedPercent">normhyra inom intervall ({{vm.nhRange.table}}) {{ vm.nhRange.min }}-{{ vm.nhRange.max }} \xE4ndring {{ vm.suggestedPercent }}%</div><table><tr ng-if="vm.percentMode === \'single\'"><td><input type="number" id="percent" min="0" max="100" step="0.01" class="form-control" ng-change="vm.setPercent()" ng-model="vm.percent"></td><td>%</td><td valign="top"><button class="btn spacer-ml-large" ng-click="vm.percentMode = \'nhrange\'">NH intervall</button></td></tr><tr ng-if="vm.percentMode === \'nhrange\'"><td>Normhyra intervall ({{vm.nhRange.table}}) {{ vm.nhRange.min }}-{{ vm.nhRange.max }}<div ng-repeat="range in vm.ranges"><input placeholder="min" type="number" class="form-control form-control--narrow form-control--gray-border xsmall-input" ng-model="range.min"> <input placeholder="max" type="number" class="form-control form-control--narrow form-control--gray-border xsmall-input" ng-model="range.max"> <input placeholder="%" type="number" class="form-control form-control--narrow form-control--gray-border xsmall-input" ng-model="range.number"> <button class="btn btn--secondary" ng-click="vm.deleteRange(range)"><b>X</b></button></div><button class="btn" ng-click="vm.addRange()"><b>+</b></button></td><td valign="top"><br><button class="btn spacer-ml-large" ng-click="vm.percentMode = \'single\'">Enbart %</button></td></tr></table></div><div ng-if="vm.changeMethod > 0"><table><tr><td width="30" align="right" ng-if="vm.currentTab === \'relative\'">+</td><td><input type="number" class="form-control" ng-change="vm.setMonthly()" ng-model="vm.monthly"></td><td>kr/m\xE5<span ng-if="vm.changeMethod == 2">/m<sup>2</sup></span></td><td ng-if="vm.currentTab === \'relative\'">+</td><td><input type="number" class="form-control" ng-change="vm.setYearly()" ng-model="vm.yearly"></td><td>kr/\xE5r<span ng-if="vm.changeMethod == 2">/m<sup>2</sup></span></td></tr></table></div></div></div><div class="modal-footer"><button class="btn" ng-disabled="!vm.okToSave()" ng-click="vm.ok()">OK</button> <a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a></div></form>');
$templateCache.put('resources/agreement/rentlist/edit/edit-status-modal.html','<div class="modal-header modal-header--medium"><h2 class="text-center text-margin-reset">Ange status</h2></div><div class="modal-body modal-body--medium" id="modal-body"><table align="center"><tr ng-repeat="status in vm.statuses"><td><button class="btn btn--full-width" ng-disabled="!status.canReset" ng-click="vm.reset(status.code)">Rensa {{ status.name }}</button></td><td><button class="btn btn--full-width" ng-disabled="!status.canSet" ng-click="vm.set(status.code)">Till\xE4mpa {{ status.name }}</button></td><td>{{ status.setCount }} l\xE4genheter</td></tr></table></div><div class="modal-footer"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a></div>');
$templateCache.put('resources/agreement/rentlist/edit/edit.html','<div class="container" ng-controller="EditAgreementRentlistCtrl as editApartmentsVM"><hy-breadcrumb></hy-breadcrumb><div class="display-flex space-between spacer-mb-xlarge"><div><a href="/negotiations/{{vm.negotiation.id}}/agreements/{{vm.agreement.id}}" class="btn btn--reset-transition btn--arrow-left">Till \xF6verenskommelsen</a></div><div class="col text-right" ng-hide="Auth.isDelegateUser() || !editApartmentsVM.readyApartments"><hy-progress-circular size="20px" ng-show="editApartmentsVM.saving"></hy-progress-circular><button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.startOver()" ng-if="(editApartmentsVM.hasNewRents || editApartmentsVM.hasNewStatuses)" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId()" title="Denna knapp inneb\xE4r att du anv\xE4nder dig av senast uppr\xE4knade hyror och endast avser g\xF6ra en r\xE4ttelse av exempelvis avtalstext eller ikrafttr\xE4dandedatum"><span ng-if="!vm.agreement.conditionTags.revertSourceId">Nollst\xE4ll</span> <span ng-if="vm.agreement.conditionTags.revertSourceId">Anv\xE4nd hyra</span></button> <button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.startOverCorrection()" ng-if="vm.agreement.conditionTags.revertSourceId" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId()" title="Denna knapp inneb\xE4r att du \xE5terg\xE5r till den hyran som fanns i systemet innan uppr\xE4kning">\xC5terst\xE4ll hyra</button> <button class="btn spacer-mr-xsmall btn--secondary btn-revert" ng-hide="editApartmentsVM.saving || Auth.isDelegateUser() || !editApartmentsVM.isDirty" ng-click="editApartmentsVM.revert()" ng-disabled="editApartmentsVM.saving || vm.negotiation.isFinished"></button> <button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.save()" ng-if="editApartmentsVM.isDirty" ng-disabled="editApartmentsVM.saving">Spara</button> <button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.editRentalLevels()" title="{{ (!editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetRent()) ? \'Hyreslistan inneh\xE5ller l\xE5st statusmarkering. Om \xE4ndring ska g\xF6ras, nollst\xE4ll f\xF6r att ta bort statusmarkering samt f\xF6r att kunna g\xF6ra hyresf\xF6r\xE4ndring\' : \'\' }}" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId()">Ange hyra</button> <button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.editObjects()" title="{{ (!editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetRent()) ? \'Hyreslistan inneh\xE5ller l\xE5st statusmarkering. Om \xE4ndring ska g\xF6ras, nollst\xE4ll f\xF6r att ta bort statusmarkering samt f\xF6r att kunna g\xF6ra hyresf\xF6r\xE4ndring\' : \'\' }}" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetRent()">Ange objektsinformation</button> <button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.editStatus()" title="{{ (!editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetStatus()) ? \'Utvalda l\xE4genheter inneh\xE5ller ny hyra. Detta f\xF6rhindrar att ny status st\xE4lls in\' : \'\' }}" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetStatus()">Ange status</button> <button class="btn" ng-click="editApartmentsVM.massComment()" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId()"><img src="/img/comment-filled.png" height="20" width="20"></button></div></div><div class="display-flex"><div class="spacer-mr-small"><h3 class="text-margin-reset">\xD6verenskommelse <span class="text-success">{{ vm.agreement.sequenceNumber }}</span> - F\xF6rhandling <span class="text-success">{{ vm.negotiation.caseNumber }}</span></h3></div></div><hy-progress-circular size="60px" center-on-page ng-hide="editApartmentsVM.readyApartments"></hy-progress-circular><div class="sort-container spacer-mb-medium align-items-center"><div class="di legend spacer-mr-medium"><b>Ny data</b> &nbsp; Aktuell data</div>NH:<hy-combobox class="di spacer-mr-small" current-item="editApartmentsVM.nh.current" current-item-changed="editApartmentsVM.nhTableChange()" id-property="id" text-property="text" items="editApartmentsVM.nh.items"></hy-combobox><hy-sorter ng-if="!editApartmentsVM.isDirty" class="di" list-columns="editApartmentsVM.listColumns" order="editApartmentsVM.sortColumn" direction="editApartmentsVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th table__td--width-5">Alla<div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="allApartmentsBox" ng-model="editApartmentsVM.allApartmentsBoxChecked" ng-change="editApartmentsVM.toggleAllApartmentsSelection()"> <label class="custom-control__label" for="allApartmentsBox"></label></div></th><th class="table__th table__td--width-10">Nationellt<br>l\xE4genhetsnr<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="editApartmentsVM.inputTextNationalApartmentNo" hy-enter="editApartmentsVM.getApartments(editApartmentsVM.setSlice)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.getApartments(editApartmentsVM.setSlice); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10">L\xE4genhetsnr<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="editApartmentsVM.inputTextApartmentNo" hy-enter="editApartmentsVM.getApartments(editApartmentsVM.setSlice)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.getApartments(editApartmentsVM.setSlice); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Gatuadress<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="editApartmentsVM.inputTextStreetAddress" hy-enter="editApartmentsVM.getApartments(editApartmentsVM.setSlice)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.getApartments(editApartmentsVM.setSlice); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Fastighet<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="editApartmentsVM.inputTextPropertyDesignation" hy-enter="editApartmentsVM.getApartments(editApartmentsVM.setSlice)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.getApartments(editApartmentsVM.setSlice); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15">Kommun<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="editApartmentsVM.inputTextMunicipality" hy-enter="editApartmentsVM.getApartments(editApartmentsVM.setSlice)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.getApartments(editApartmentsVM.setSlice); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10">Rum<br>antal<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="editApartmentsVM.inputTextRooms" hy-enter="editApartmentsVM.getApartments(editApartmentsVM.setSlice)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.getApartments(editApartmentsVM.setSlice); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10">Yta<div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="editApartmentsVM.inputTextLivingSpace" hy-enter="editApartmentsVM.getApartments(editApartmentsVM.setSlice)"> <a href="#" class="btn--icon btn--icon-search" ng-click="editApartmentsVM.getApartments(editApartmentsVM.setSlice); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10" align="center">NH ({{ editApartmentsVM.nh.current.text }})<div><input type="text" class="form-control" placeholder="min" id="inputNHMin" ng-model="editApartmentsVM.inputNHMin" hy-enter="editApartmentsVM.filterNH()"> <input type="text" class="form-control" placeholder="max" id="inputNHMax" ng-model="editApartmentsVM.inputNHMax" hy-enter="editApartmentsVM.filterNH()"></div></th><th class="table__th table__td--width-10">Hyra<br>(kr/m\xE5nad)</th><th class="table__th table__td--width-20">Status<hy-multicombobox items="editApartmentsVM.filterStatusTypes" current-items="editApartmentsVM.inputStatuses" current-items-changed="editApartmentsVM.getApartments()" button-text="S\xF6k" id-property="id" text-property="name"></hy-multicombobox></th><th class="table__th table__td--width-15">F\xF6r\xE4ndring/m\xE5n</th><th class="table__th table__td--width-5"></th></tr></thead><tr class="table__tr--minimalistic table__tr--disable-click" ng-repeat="apartment in editApartmentsVM.apartmentsSlice" ng-class="{\'table__tr--muted\' : !editApartmentsVM.readyApartments}"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{apartment.id}}" ng-model="editApartmentsVM.selectedApartmentIds[apartment.id]" ng-change="editApartmentsVM.updateAllPropertiesBoxState()"> <label class="custom-control__label" for="{{apartment.id}}"></label></div></td><td class="table__td immutable table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{ apartment.nationalNo | nationalNoControl }}</td><td class="table__td immutable table__td--nowrap">{{ apartment.ownerNo }}</td><td class="table__td immutable">{{ apartment.streetAddress }}</td><td class="table__td immutable">{{ apartment.property.propertyDesignation }}</td><td class="table__td immutable">{{ apartment.municipality }}</td><td class="table__td immutable">{{ apartment.rooms }}</td><td class="table__td immutable">{{ apartment.livingSpace }}</td><td class="table__td immutable" align="center"><b ng-if="apartment.newMonthlyRent">{{ apartment.newNH[editApartmentsVM.nh.current.id] }}<br></b>{{ apartment[editApartmentsVM.nh.current.id] }}</td><td class="table__td" align="center"><div><b><span ng-if="!apartment.newMonthlyRent" class="text-gray">Ej angivet<br></span>{{ apartment.newMonthlyRent | number:0 }}<hy-std-display ng-if="apartment.newMonthlyRent" model="{{apartment.objectsExtra}}"></hy-std-display></b></div><div>{{ editApartmentsVM.getMonthlyRent(apartment) }}<hy-std-display model="{{apartment.currentObjectsExtra}}"></hy-std-display></div></td><td class="table__td" align="right"><br ng-if="(apartment.status | hyStatusString:1).length === 0"><b><div ng-bind-html="apartment.status | hyStatusString:1"></div></b><br ng-if="(apartment.currentStatus | hyStatusString:1).length === 0"><div ng-bind-html="apartment.currentStatus | hyStatusString:1"></div></td><td class="table__td" align="right">{{ apartment.changeDescription }}</td><td><img src="/img/comment{{ apartment.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="apartment.comment" tooltip-placement="left" ng-click="Auth.canDo(\'editNegotiationComment\') && editApartmentsVM.editComment(apartment)"></td></tr></table><div class="display-flex space-between spacer-mt-medium" ng-show="editApartmentsVM.apartmentsTotalCount > 0"><div>Ladda ner listan <a class="link--turquoise link--underline spacer-ml-xsmall" target="_self" download href="/api/negotiations/{{vm.agreement.negotiationId}}/agreements/{{vm.agreement.id}}/export-rent-list">h\xE4r</a></div><div ng-show="editApartmentsVM.apartmentsTotalCount > 0 &amp;&amp; editApartmentsVM.apartmentsTotalCount <= editApartmentsVM.pageLimit"><strong>Totalt: </strong>{{editApartmentsVM.apartmentsTotalCount}}</div><hy-paginator ng-show="editApartmentsVM.apartmentsTotalCount > editApartmentsVM.pageLimit" get-list="editApartmentsVM.paginatorGetApartments" page-offset="editApartmentsVM.offset" page-limit="editApartmentsVM.pageLimit" total-count="editApartmentsVM.apartmentsTotalCount"></hy-paginator></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="editApartmentsVM.apartmentsTotalCount === 0">Inga l\xE4genheter hittades.</div><div class="row spacer-mt-medium" ng-hide="Auth.isDelegateUser() || !editApartmentsVM.readyApartments"><div class="col text-right" ng-if="vm.isDirty"><button class="btn btn--secondary" ng-hide="editApartmentsVM.saving" ng-click="editApartmentsVM.startOver()" ng-disabled="vm.negotiation.isFinished"></button></div><div class="col text-right"><hy-progress-circular size="20px" ng-show="editApartmentsVM.saving"></hy-progress-circular><button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.startOver()" ng-if="(editApartmentsVM.hasNewRents || editApartmentsVM.hasNewStatuses)" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId()" title="Denna knapp inneb\xE4r att du anv\xE4nder dig av senast uppr\xE4knade hyror och endast avser g\xF6ra en r\xE4ttelse av exempelvis avtalstext eller ikrafttr\xE4dandedatum"><span ng-if="!vm.agreement.conditionTags.revertSourceId">Nollst\xE4ll</span> <span ng-if="vm.agreement.conditionTags.revertSourceId">Anv\xE4nd hyra</span></button> <button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.startOverCorrection()" ng-if="vm.agreement.conditionTags.revertSourceId" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId()" title="Denna knapp inneb\xE4r att du \xE5terg\xE5r till den hyran som fanns i systemet innan uppr\xE4kning">\xC5terst\xE4ll hyra</button> <button class="btn spacer-mr-xsmall btn--secondary btn-revert" ng-hide="editApartmentsVM.saving || Auth.isDelegateUser() || !editApartmentsVM.isDirty" ng-click="editApartmentsVM.revert()" ng-disabled="editApartmentsVM.saving || vm.negotiation.isFinished"></button> <button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.save()" ng-if="editApartmentsVM.isDirty" ng-disabled="editApartmentsVM.saving">Spara</button> <button class="btn spacer-mr-small" ng-click="editApartmentsVM.editRentalLevels()" title="{{ (!editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetRent()) ? \'Hyreslistan inneh\xE5ller l\xE5st statusmarkering. Om \xE4ndring ska g\xF6ras, nollst\xE4ll f\xF6r att ta bort statusmarkering samt f\xF6r att kunna g\xF6ra hyresf\xF6r\xE4ndring\' : \'\' }}" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId()">Ange hyra</button> <button class="btn spacer-mr-small" ng-click="editApartmentsVM.editObjects()" title="{{ (!editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetRent()) ? \'Hyreslistan inneh\xE5ller l\xE5st statusmarkering. Om \xE4ndring ska g\xF6ras, nollst\xE4ll f\xF6r att ta bort statusmarkering samt f\xF6r att kunna g\xF6ra hyresf\xF6r\xE4ndring\' : \'\' }}" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetRent()">Ange objektsinformation</button> <button class="btn spacer-mr-xsmall" ng-click="editApartmentsVM.editStatus()" title="{{ (!editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetStatus()) ? \'Utvalda l\xE4genheter inneh\xE5ller ny hyra. Detta f\xF6rhindrar att ny status st\xE4lls in\' : \'\' }}" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId() || !editApartmentsVM.canSetStatus()">Ange status</button> <button class="btn" ng-click="editApartmentsVM.massComment()" ng-disabled="editApartmentsVM.saving || !editApartmentsVM.atLeastOneSelectedApartmentId()"><img src="/img/comment-filled.png" height="20" width="20"></button></div></div></div>');
$templateCache.put('resources/negotiation-call/show/negotiations/show.html','<div ng-controller="ShowNegotiationCallNegotiationsCtrl as negotiationsVM"><div class="row"><div class="col"><p ng-show="negotiationsVM.negotiationsTotalCount === 0">Inga f\xF6rhandlingar har skapats fr\xE5n f\xF6rhandlingsframst\xE4llan \xE4n.</p></div></div><hy-progress-circular ng-hide="negotiationsVM.readyNegotiations" center-on-page size="60px"></hy-progress-circular><div class="row"><div class="col"><div class="sort-container spacer-mt-medium spacer-mb-medium"><hy-sorter list-columns="negotiationsVM.listColumns" order="negotiationsVM.sortColumn" direction="negotiationsVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Id</th><th class="table__th">Namn</th><th class="table__th">Kontor HGF</th><th class="table__th">Status</th><th class="table__th">Skapad den</th></tr><tr><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="negotiationsVM.inputTextNegotiations" hy-enter="negotiationsVM.queryNegotiations(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="negotiationsVM.queryNegotiations(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th></th><th></th><th></th><th></th></tr></thead><tr class="table__tr" ng-repeat="negotiation in negotiationsVM.negotiations" ng-click="negotiationsVM.gotoShow(negotiation.id)" ng-class="{\'table__tr--muted\' : !negotiationsVM.readyNegotiations}"><td class="table__td">{{negotiation.caseNumber}}</td><td class="table__td">{{negotiation.name}}</td><td class="table__td">{{negotiation.office.name}}</td><td class="table__td">{{negotiation.state | hyNegotiationStates}}</td><td class="table__td">{{negotiation.createdAt | date : "yyyy-MM-dd"}}</td></tr></table><div class="row spacer-mt-medium" ng-show="negotiationsVM.negotiationsTotalCount > negotiationsVM.negotiationsPageLimit"><div class="col-12 text-right"><hy-paginator get-list="negotiationsVM.queryNegotiation" page-offset="negotiationsVM.negotiationsPageOffset" page-limit="negotiationsVM.negotiationsPageLimit" total-count="negotiationsVM.negotiationsTotalCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="negotiationsVM.negotiationsTotalCount > 0 &amp;&amp; negotiationsVM.negotiationsTotalCount <= negotiationsVM.negotiationsPageLimit"><div class="col-12 text-right"><strong>Totalt: </strong>{{negotiationsVM.negotiationsTotalCount}}</div></div><div class="row"><div class="col-12"><div class="alert alert--hide-border alert--slim alert--squary" ng-show="negotiationsVM.negotiationsTotalCount === 0">Ingen f\xF6rhandling hittades.</div></div></div></div></div></div>');
$templateCache.put('resources/agreement/show/figurants/show.html','<style>.new-figurant {\r\n    margin-left: 330px;\r\n  }</style><div class="modal-header modal-header--small"><h2 class="text-margin-top-reset text-margin-bottom-reset">Avtalsparter f\xF6r {{vm.caseNumber}}-{{vm.agreement.sequenceNumber}}</h2></div><div class="modal-body modal-body--small" id="modal-body"><h4>Avtalsparter</h4><div ng-if="vm.unterpopupNewFigurant" class="unterpopup new-figurant"><form name="vm.newFigurantForm" ng-submit="event.preventDefault()" novalidate>Namn: <input class="form-control" required placeholder="namn" ng-model="vm.newFigurantName"> Org nummer: <input class="form-control" required placeholder="XXXXXX-XXXX" ng-pattern="/^(\\d{6}|\\d{8})-?\\d{4}$/" ng-model="vm.newFigurantOrganizationNumber"><div class="unterpopup-footer"><button class="btn spacer-mt-xsmall" ng-disabled="!vm.newFigurantForm.$valid" ng-click="vm.createNewFigurant()">OK</button> <a class="link--turquoise link--no-underline" ng-click="vm.unterpopupNewFigurant = false">Avbryt</a></div></form></div><hy-tableau id="tableauFigurants" columns="vm.columnsFigurants" f-get-data="vm.getFigurants" use-url="false" in-memory="true" checkboxes="true" checkboxes-alternative="true" id-property="id" actions="vm.actionsFigurants" in-memory-excessive-limit="vm.Limits.InMemoryMiddleLimit" selected-rows="vm.selectedFigurants" limit="4"></hy-tableau><div class="spacer-mt-medium"><hr class="hr-thin"></div><h4 class="spacer-mt-large">Samtliga parter som kan anv\xE4ndas som avtalspart f\xF6r fastigheten</h4><hy-tableau id="tableauAllFigurants" columns="vm.columnsAllFigurants" f-get-data="vm.getAllFigurants" use-url="false" in-memory="true" checkboxes="true" checkboxes-alternative="true" id-property="id" actions="vm.actionsAllFigurants" in-memory-excessive-limit="vm.Limits.InMemoryMiddleLimit" selected-rows="vm.selectedAllFigurants" limit="4"></hy-tableau></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="$dismiss()">Avbryt</a> <button class="btn btn--secondary" ng-click="vm.delete()">Inst\xE4lld som standard</button> <button class="btn" ng-click="vm.ok()">Spara</button></div>');
$templateCache.put('resources/agreement/rentlist/new/new.html','<div class="container" ng-controller="NewAgreementRentlistCtrl as vm"><hy-breadcrumb></hy-breadcrumb><div class="spacer-mt-large"><hy-wizard wz-submit="vm.save()" goto-next="vm.gotoNext" is-current-step-valid="vm.isCurrentStepValid" welcome-message="V\xE4lkommen att ladda upp hyreslista" header-text="Ladda upp hyreslista" submit-disabled="vm.saving"><hy-progress-circular ng-show="vm.saving" center-on-page size="60px"></hy-progress-circular><hy-wizard-step heading="Ladda upp hyreslista" heading-short="Ladda upp" heading-step-number="1" name="file-upload-rent-list" is-valid="vm.fileUploadRentListForm.$valid"><ng-include src="\'resources/agreement/rentlist/new/partials/_file-upload-rent-list.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="F\xF6rhandsgranska 1:a raden" heading-short="F\xF6rsta raden" heading-step-number="2" name="examine-first-line" is-valid="true"><ng-include src="\'resources/agreement/rentlist/new/partials/_examine-first-line.html\'"></ng-include></hy-wizard-step></hy-wizard></div></div>');
$templateCache.put('resources/agreement/show/overview/alterRole.html','<div class="modal-header modal-header--small"><h2 class="text-margin-top-reset text-margin-bottom-reset">\xC4ndra roll</h2></div><div class="modal-body modal-body--small" id="modal-body"><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle ng-disabled="vm.possibleRoles.length===0"><span class="text-gray-dark">{{vm.selectedRole ? vm.selectedRole : \'V\xE4lj roll\'}}</span></button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="role in vm.possibleRoles" type="button" class="dropdown-item" ng-click="vm.selectRole(role)">{{role}}</button></div></div></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="$dismiss()">Avbryt</a> <button class="btn" ng-click="vm.ok()">V\xE4lj</button></div>');
$templateCache.put('resources/agreement/show/overview/show.html','<!-- TODO: move to styles if this stays --><style>.btn--delete-date {\r\n    position: absolute;\r\n    right: 60px;\r\n  }\r\n  .signError {\r\n    margin-top: 10px; \r\n    margin-bottom: 40px;\r\n  }</style><div ng-controller="ShowAgreementOverviewCtrl as overviewVM"><div class="row spacer-mt-xxlarge"><hy-progress-circular size="100px" center-on-page-fixed ng-show="vm.saving"></hy-progress-circular><div class="col-12 col-lg-9"><form name="agreementForm" ng-submit="agreementForm.$valid &amp;&amp; vm.saveAgreement()" class="position-relative display-flex flex-direction-column full-height alert alert--narrow-turq-dark alert--squary" novalidate><h2 ng-if="!vm.isFreeAgreement" class="text-margin-reset text-center"><u>F\xF6rhandlings\xF6verenskommelse</u></h2><h2 ng-if="vm.isFreeAgreement" class="text-margin-reset text-center">{{ vm.freeAgreementType.name }} <span class="text-success">{{ vm.agreement.fancyNumber }}</span></h2><div ng-if="!vm.agreement.conditionTags.internal" class="spacer-mb-xlarge spacer-mt-large text-center text-large text-uppercase">\xD6verenskommelse mellan <strong>802001-5106, Hyresg\xE4stf\xF6reningen</strong> och <span ng-if="!vm.isFreeAgreement"><div ng-if="!vm.agreement.conditionTags.figurants" ng-repeat="owner in vm.agreement.owners"><b>{{ owner.organizationNumber | hyOrgNr }}, {{ owner.name }}</b></div><div ng-if="vm.agreement.conditionTags.figurants" ng-repeat="figurant in vm.agreement.conditionTags.figurants"><b>{{ figurant.organizationNumber | hyOrgNr }}, {{ figurant.name }}</b></div>f\xF6rhandlingspart: </span><strong>{{vm.counterpartNumber}}, {{vm.counterpart.name}}</strong></div><hr class="text-margin-top-reset text-margin-bottom-reset hr--thin hr--negative-margin-medium"><hr class="text-margin-top-reset text-margin-bottom-reset hr--thin hr--negative-margin-medium spacer-mb-xxlarge"><div ng-if="!vm.agreement.conditionTags.internal" class="row"><div class="col-12"><strong>\xC4ndra avtalspart</strong> <input name="counterpartName" id="counterpartName" type="text" placeholder="{{vm.negotiation.counterpart.name ? vm.negotiation.counterpart.name : \'ange org.nummer och f\xF6retagsnamn\'}}" class="form-control form-control--gray-border" ng-readonly="!Auth.canDo(\'editAgreement\')" ng-disabled="vm.signStatusVisible" ng-model="vm.agreement.counterpartName"></div></div><div class="row"><div class="col-sm-6 col-12 spacer-mb-medium"><div class="form-group" ng-class="{\r\n                  \'form-group--error\': (vm.agreementForm.inputRentEffectiveFrom.$touched || vm.agreementForm.$submitted) &amp;&amp; vm.agreementForm.inputRentEffectiveFrom.$invalid\r\n                }"><strong>Ikrafttr\xE4dandedatum</strong><div class="input-group datepicker"><input name="inputRentEffectiveFrom" id="inputRentEffectiveFrom" type="text" class="form-control form-control--narrow form-control--gray-border" uib-datepicker-popup is-open="openedRentEffectiveFrom" ng-model="vm.agreement.rentEffectiveFrom" ng-disabled="vm.signStatusVisible" ng-readonly="!Auth.canDo(\'editAgreement\')" close-on-date-selection="true"> <button type="button" class="btn btn--squary btn--icon btn--icon-calendar" ng-click="openedRentEffectiveFrom = !openedRentEffectiveFrom" ng-disabled="!Auth.canDo(\'editAgreement\') || vm.negotiation.isFinished || Auth.isDelegateUser() \r\n                                    || vm.agreement.agreedByAssociation || vm.agreement.agreedByOwner"></button></div></div></div><div class="col-sm-6 col-12 spacer-mb-medium"><div class="form-group" ng-class="{\r\n            \'form-group--error\': (vm.agreementForm.inputReleaseDate.$touched || vm.agreementForm.$submitted) &amp;&amp; vm.agreementForm.inputReleaseDate.$invalid\r\n              }"><strong>{{ vm.isFreeAgreement ? "Slutdatum" : "Offentligg\xF6randedatum" }}</strong><div class="input-group datepicker"><input name="inputReleaseDate" id="inputReleaseDate" type="text" class="form-control form-control--narrow form-control--gray-border" uib-datepicker-popup is-open="openedReleaseDate" placeholder="{{ vm.isFreeAgreement ? \'Tillsvidare\' : \'\' }}" ng-model="vm.agreement.releaseDate" ng-disabled="vm.signStatusVisible" ng-readonly="!Auth.canDo(\'editAgreement\')" close-on-date-selection="true"> <button type="button" class="btn btn--delete-date btn--squary btn--icon btn--icon-cross" ng-if="Auth.canDo(\'editAgreement\') && vm.isFreeAgreement && !vm.signStatusVisible && vm.agreement.releaseDate" ng-click="vm.agreement.releaseDate = null"></button> <button type="button" class="btn btn--squary btn--icon btn--icon-calendar" ng-click="openedReleaseDate = !openedReleaseDate" ng-disabled="!Auth.canDo(\'editAgreement\') || vm.negotiation.isFinished || Auth.isDelegateUser() \r\n                                    || vm.agreement.agreedByAssociation || vm.agreement.agreedByOwner"></button></div><hy-combobox ng-if="Auth.canDo(\'editAgreement\') && vm.isFreeAgreement" ng-hide="vm.signStatusVisible" prompt="Ange slutdatum i \xE5r:" sticky-prompt="true" items="[1,2,5,10,15]" current-item-changed="vm.setEndDate(selection)"></hy-combobox></div></div><div class="col-12 spacer-mb-medium"><div class="form-group"><strong ng-if="!vm.isFreeAgreement">\xD6verenskommelsetext</strong> <strong ng-if="vm.isFreeAgreement">Avtalstext</strong> <textarea name="inputName" id="inputName" rows="4" placeholder="Sammanfattning, max 255 tecken" class="form-control form-control--gray-border" ng-model="vm.agreement.name" ng-disabled="vm.signStatusVisible" maxlength="255" ng-readonly="!Auth.canDo(\'editAgreement\') || vm.negotiation.isFinished || Auth.isDelegateUser() \r\n                          || vm.agreement.agreedByAssociation || vm.agreement.agreedByOwner">\r\n                </textarea><div ng-if="vm.isPresumtiveRent">F\xF6rhandlings\xF6verenskommelse om nyproduktion med presumtionshyror enligt 12 kap. 55 c \xA7 jordabalken</div><textarea name="inputAdditionalText" id="inputAdditionalText" rows="8" placeholder="ytterligare text" class="form-control form-control--gray-border" ng-model="vm.agreement.additionalText" ng-disabled="vm.signStatusVisible" maxlength="8192" ng-readonly="!Auth.canDo(\'editAgreement\') || vm.negotiation.isFinished || Auth.isDelegateUser() \r\n                          || vm.agreement.agreedByAssociation || vm.agreement.agreedByOwner">\r\n                </textarea></div></div></div><div class="text-right flex-grow-1 spacer-mb-xsmall"><input class="btn" type="button" value="{{ vm.showAgreementsPane ? \'D\xF6lj\' : \'Visa\' }} bilagor ({{vm.agreement.documents.length}})" ng-click="vm.toggleAttachmentsPane()"> <button class="btn" type="submit" ng-hide="!Auth.canDo(\'editAgreement\') || Auth.isDelegateUser() || \r\n                             vm.negotiation.isFinished || \r\n                             vm.agreement.agreedByAssociation || \r\n                             vm.agreement.agreedByOwner || \r\n                             vm.signStatusVisible" ng-click="vm.saveAgreement()" ng-disabled="vm.saving">Spara</button></div><div class="spacer-mb-large" ng-show="vm.showAgreementsPane"><ng-include src="\'resources/agreement/show/documents/show.html\'"></ng-include></div><div class="signError" ng-show="vm.signErrorVisible"><b>Fel:</b> {{vm.signError}}<br></div><div ng-show="vm.finishing"><div class="red-text">Varning!</div>Din data i \xF6verenskommelsen behandlas, detta kan ta ett tag. V\xE4nligen kom tillbaka om en stund och l\xE5t sidan vara \xF6ppen.</div><div ng-show="vm.signStatusVisible && !vm.finishing"><b>Status:</b> <span ng-bind-html="vm.scriveStatus"></span><br><b>Signerande parter:</b><br><span ng-bind-html="vm.scriveParties"></span></div><div ng-show="vm.agreement.signed && !vm.agreement.scriveId && !vm.signStatusVisible && !vm.finishing" class="spacer-mt-medium">Avslutad utan e-signering<br><a href="" class="btn btn--info btn--md btn--full-chopped-width" ng-click="vm.downloadPDF()">H\xE4mta \xD6K (Pdf)</a></div><div ng-show="vm.signStartVisible && !vm.agreement.signed  && !vm.finishing"><button type="button" ng-if="Auth.canDo(\'editAgreement\')" class="btn btn-default-outline spacer-mb-medium" ng-click="vm.toggleAll()">V\xE4xla alla</button><div class="spacer-mb-medium"><div ng-repeat="participant in vm.participants"><hr class="hr--thin hr--thin-margin" ng-show="participant.groupBreak"><div class="signing-party-item spacer-mb-xxsmall"><div class="custom-control custom-control--checkbox" style="min-width: 350px"><input class="custom-control__input" type="checkbox" id="{{$index}}" ng-disabled="!Auth.canDo(\'editAgreement\')" ng-change="vm.toggleCheck()" ng-model="participant.selected"> <label style="color: initial;" class="custom-control__label" for="{{$index}}">{{participant.firstname}} {{participant.lastname}} ({{participant.email}})</label></div><div class="spacer-ml-medium"><button type="button" class="btn btn--no-padding btn-default-outline" ng-if="Auth.canDo(\'editAgreement\')" ng-hide="participant.roleObj.code==0 || participant.roleObj.code==92" ng-click="vm.alterRole(participant)">Part</button> <button ng-if="Auth.canDo(\'editAgreement\') && participant.canBeDeleted" ng-click="vm.deleteVoidUser(participant)" type="button" class="btn--icon btn--icon-bin btn--icon--no-borders"></button></div></div><div class="spacer-mb-small" style="margin-left: 2.125rem;"><i>{{participant.roleObj.alternateRole ? participant.roleObj.alternateRole : participant.roleObj.name}}</i></div></div></div><div class="spacer-mb-medium spacer-mt-reset" ng-if="Auth.canDo(\'editAgreement\') && vm.canStartSigning"><input class="form-control table__td--width-30 di" ng-model="vm.voidUser.name" placeholder="Namn"> <input class="form-control table__td--width-30 di" ng-model="vm.voidUser.email" type="email" placeholder="E-post"> <button class="btn di" ng-click="vm.addVoidUser()">L\xE4gg till</button></div><div ng-if="vm.stuckAgreement"><small>Dokumentet har fastnat. L\xE5s upp den f\xF6r att forts\xE4tta arbeta</small> <button ng-click="vm.pullOut()" class="btn btn--md btn--full-chopped-width" type="button">L\xE5sa upp</button></div><div ng-if="!vm.stuckAgreement && !vm.agreement.signed"><div ng-click="vm.previewPDF()" class="spacer-mb-small" ng-show="vm.canStartSigning && \r\n                            (vm.agreement | doneTasksCounter: vm.negotiation.type: vm.isFreeAgreement).lastStep &&\r\n                            !vm.noParticipantsSelected"><div class="inprocess"></div>F\xF6rhandsvisning</div><button ng-if="Auth.canDo(\'editAgreement\')" ng-disabled="((vm.noParticipantsSelected) || \r\n                                  !((vm.agreement | doneTasksCounter: vm.negotiation.type: vm.isFreeAgreement).lastStep))" ng-show="vm.canStartSigning" ng-click="vm.signStart()" class="btn btn--info btn--md btn--full-chopped-width" type="button">B\xF6rja signera</button> <button ng-if="Auth.canDo(\'editAgreement\')" ng-disabled="((vm.noParticipantsSelected) || \r\n                          !((vm.agreement | doneTasksCounter: vm.negotiation.type: vm.isFreeAgreement).lastStep))" ng-show="vm.canFastClose" ng-click="vm.signStart(false, true)" style="background-color: orange;" class="btn btn--info btn--md btn--full-chopped-width spacer-mt-small" type="button">Avslut utan e-signering</button> <span ng-show="vm.canStartSigning===false"><b>Fel:</b> Kan inte b\xF6rja signera eftersom du \xE4r inbjuden anv\xE4ndare</span></div></div><div ng-show="vm.signDownloadVisible && !vm.finishing" class="spacer-mr-sign spacer-mt-small"><a href="" class="btn btn--info btn--md btn--full-width" ng-show="vm.canDownloadPDF" ng-class="{\'btn--info\': vm.agreement.numApartments > 0}" ng-click="vm.downloadPDF()">H\xE4mta \xD6K (Pdf)</a> <span ng-show="vm.canDownloadPDF===false"><b>Fel:</b> Kan inte ladda fil eftersom du ing\xE5r inte i denna \xF6verenskommelse eller du \xE4r f\xF6rtroendevald</span></div><div ng-if="Auth.isHgfUser() && vm.signDropAvailable && !vm.finishing" class="spacer-mr-sign spacer-mt-small"><div ng-if="vm.rejectedReactionsVisible">\xD6verenskommelsen har avvisats. Tryck p\xE5 \u201CAvbryt signering och justera \xF6verenskommelsen\u201D och s\xE4nd ut f\xF6r signering igen.</div><button ng-if="Auth.canDo(\'editAgreement\')" ng-click="vm.signDrop()" class="btn btn--md btn--secondary btn--full-width" type="button">Avbryt signering och justera \xF6verenskommelsen</button></div><div class="icon--triangle"></div></form></div><div class="col-12 col-lg-3" ng-if="Auth.canDo(\'editAgreement\') && !vm.isFreeAgreement"><ng-include src="\'resources/agreement/show/overview/partials/_side-actions.html\'"></ng-include></div><div class="col-12 col-lg-3" ng-if="vm.isFreeAgreement"><ng-include src="\'resources/agreement/show/overview/partials/_free-agreement-side-actions.html\'"></ng-include></div></div><h2 class="text-center" ng-if="(vm.agreement | doneTasksCounter: vm.negotiation.type: vm.isFreeAgreement).ready"><span ng-if="!vm.isFreeAgreement">\xD6verenskommelsen \xE4r klar.<br>F\xF6r att g\xE5 tillbaka till slutfas, klicka </span><a ng-if="!vm.isFreeAgreement" class="link--turquoise link--no-underline" ng-href="/negotiations/{{vm.negotiation.id}}?view=agreements">h\xE4r</a> <span ng-if="vm.isFreeAgreement">Avtal \xE4r klar.</span></h2><div class="row spacer-mt-xlarge" ng-if="Auth.canDo(\'deleteAgreement\')" ng-show="(vm.isFreeAgreement && Auth.isHgfUser() || !vm.isFreeAgreement && !Auth.isDelegateUser()) && !((vm.agreement | doneTasksCounter: vm.negotiation.type: vm.isFreeAgreement).ready)"><div ng-if="!vm.isFreeAgreement" class="col-6 col-lg-4"><hr class="text-margin-top-reset hr--pink"><h4 class="text-danger">Ta bort \xF6verenskommelse</h4><p>N\xE4r du tar bort din \xF6verenskommelse, finns det ingen \xE5terv\xE4ndo. Var s\xE4ker.</p><button class="btn btn--secondary" ng-click="overviewVM.deleteAgreement()">Ta bort \xF6verenskommelse</button></div><div ng-if="vm.isFreeAgreement" class="col-6 col-lg-4"><hr class="text-margin-top-reset hr--pink"><h4 class="text-danger">Ta bort avtal</h4><p>N\xE4r du tar bort ditt avtal, finns det ingen \xE5terv\xE4ndo. Var s\xE4ker.</p><button class="btn btn--secondary" ng-click="overviewVM.deleteAgreement()">Ta bort avtal</button></div></div></div>');
$templateCache.put('resources/agreement/show/properties/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready &amp;&amp; vm.readyNegotiation"></hy-progress-circular><div class="container" ng-show="vm.ready &amp;&amp; vm.readyNegotiation"><hy-breadcrumb></hy-breadcrumb><div class="display-flex space-between spacer-mt-xlarge spacer-mb-xxxlarge"><div class="spacer-mr-small"><h1 class="text-margin-reset">Fastighetsbest\xE5nd</h1><h3 class="text-margin-reset">\xD6verenskommelse <span class="text-success">{{ vm.agreement.sequenceNumber }}</span> - F\xF6rhandling <span class="text-success">{{ vm.negotiation.caseNumber }}</span></h3></div><div><a href="/negotiations/{{vm.negotiation.id}}/agreements/{{vm.agreement.id}}" class="btn btn--reset-transition btn--arrow-left">Till \xF6verenskommelsen</a></div></div><div ng-if="!vm.skipRentlist"><ng-include class="display-block animate-if" ng-if="vm.editApartmentMode" src="\'resources/agreement/show/properties/apartment/newedit/edit.html\'"></ng-include><ng-include src="\'resources/agreement/show/properties/apartment/list/list.html\'"></ng-include></div><div ng-if="vm.skipRentlist"><ng-include class="display-block animate-if" ng-if="vm.editPropertiesMode" src="\'resources/agreement/show/properties/select/select.html\'"></ng-include><ng-include src="\'resources/agreement/show/properties/select/list.html\'"></ng-include></div></div>');
$templateCache.put('resources/agreement/free/new/new.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><div class="spacer-mt-large"><hy-wizard wz-submit="vm.save()" goto-next="vm.gotoNext" is-current-step-valid="vm.isCurrentStepValid" header-text="Skapa avtal" submit-text="Skapa" submit-disabled="vm.saving"><hy-wizard-step heading="Grundinformation" heading-short="Grundinformation" heading-step-number="1" name="basic" is-valid="vm.avtalType !== undefined && vm.userContainer.hgfSide.length > 0 && (vm.internal || vm.userContainer.faSide.length > 0)"><ng-include src="\'resources/agreement/free/new/partials/_basic.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj process" heading-short="V\xE4lj process" heading-step-number="2" name="choose-process" is-valid="true"><ng-include src="\'resources/agreement/free/new/partials/_choose-process.html\'"></ng-include></hy-wizard-step><hy-wizard-step heading="V\xE4lj best\xE5nd" heading-short="V\xE4lj best\xE5nd" heading-step-number="3" name="property-selection" is-disabled="vm.realEstateChoiceMethod !== \'DIRECT_PROPERTIES\'" is-valid="vm.properties.length > 0"><hy-apartment-selector-2 id="apartmentSelector" allow-empty-properties="true" empty-properties="vm.properties" empty-only="true" user-id="Auth.currentUser().id" model="vm.selectedApartments"></hy-apartment-selector-2></hy-wizard-step></hy-wizard></div></div>');
$templateCache.put('resources/agreement/free/participants/hy-participants.html','<div class="info"><label class="info__title--edit"><h4 class="text-margin-bottom-reset"><div class="icon spacer-ml-small icon--party icon--party-hgf icon--tiny di"></div>Representanter Hyresg\xE4stf\xF6reningen</h4><a href="#" class="btn--icon btn--icon-bottom-right btn--icon-pen" ng-hide="userContainer.isFinished" ng-click="edit(\'md\', Parties.HGF); $event.preventDefault();"><span class="sr-only">Redigera</span></a></label><div class="alert alert--narrow-gray alert--squary alert--slim alert--white-bg alert--mb-small"><div class="display-flex flex-direction-row space-between"><span ng-if="userContainer.hgfSide.length === 0">Inga f\xF6rhandlare \xE4nnu</span><div ng-hide="userContainer.hgfSide.length === 0"><a href="/users/{{userContainer.chiefNegotiatorHGF.id}}" class="link--turquoise link--underline">{{userContainer.chiefNegotiatorHGF.firstname}} {{userContainer.chiefNegotiatorHGF.lastname}} </a><span>(<em>Ansvarig f\xF6rhandlare</em>)</span></div><a href="#" class="btn--icon btn--icon-expand ellipsis" ng-hide="userContainer.hgfSide.length < 2" ng-class="!toggleOwnersList[0] ? \'btn--icon-expand\' : \'btn--icon-collapse\'" ng-click="toggleOwnersList[0] =! toggleOwnersList[0]; toggleOwnersList[1] = false; toggleOwnersList[2] = false; $event.preventDefault();"></a></div><div ng-repeat="participant in userContainer.hgfSide | filter: {isChief: false}" ng-show="toggleOwnersList[0]"><a href="/users/{{participant.id}}" class="link--turquoise link--underline" ng-hide="!Auth.isHgfUser()">{{participant.firstname}} {{participant.lastname}}</a> <span ng-show="!Auth.isHgfUser()">{{participant.fullName}} {{participant.lastname}}</span> <span>(<em>F\xF6rhandlare</em>)</span> <span ng-show="userContainer.hgfParticipants.length === 0">Representanter saknas</span></div><div ng-repeat="participant in userContainer.delegationInvitations | filter: {roleObj: {party: 20}}"><span>{{participant.email}} ({{participant.roleObj.name}})</span></div></div></div><div ng-if="!internal" class="info"><label class="info__title--edit"><h4 class="text-margin-bottom-reset"><div class="icon spacer-ml-small icon--party icon--party-fa icon--tiny di"></div>Representanter hyresv\xE4rd</h4><a href="#" class="btn--icon-pen btn--icon btn--icon-bottom-right" ng-hide="userContainer.isFinished" ng-click="edit(\'md\', Parties.EXTERNAL); $event.preventDefault();"><span class="sr-only">Redigera</span></a></label><div class="alert alert--narrow-gray alert--squary alert--slim alert--white-bg alert--mb-small"><div class="display-flex flex-direction-row space-between"><span ng-if="userContainer.faSide.length === 0">Inga f\xF6rhandlare \xE4nnu</span><div ng-hide="userContainer.faSide.length === 0"><a href="/users/{{userContainer.chiefNegotiatorExternal.id}}" class="link--turquoise link--underline" ng-hide="Auth.isDelegateUser()">{{userContainer.chiefNegotiatorExternal.firstname}} {{userContainer.chiefNegotiatorExternal.lastname}}</a> <span ng-show="Auth.isDelegateUser()">{{userContainer.chiefNegotiatorExternal.firstname}} {{userContainer.chiefNegotiatorExternal.lastname}}</span> <span>(<em>Ansvarig f\xF6rhandlare</em>)</span></div><a href="#" class="btn--icon btn--icon-expand ellipsis" ng-hide="userContainer.faSide.length < 2" ng-class="!overviewVM.toggleOwnersList[1] ? \'btn--icon-expand\' : \'btn--icon-collapse\'" ng-click="overviewVM.toggleOwnersList[1]=!overviewVM.toggleOwnersList[1]; overviewVM.toggleOwnersList[0]=false; overviewVM.toggleOwnersList[2]=false; $event.preventDefault();"></a></div><div ng-repeat="participant in userContainer.faSide | filter: {isChief: false}" ng-show="overviewVM.toggleOwnersList[1]"><a href="/users/{{participant.id}}" class="link--turquoise link--underline" ng-hide="Auth.isDelegateUser()">{{participant.firstname}} {{participant.lastname}}</a> <span ng-show="Auth.isDelegateUser()">{{participant.firstname}} {{participant.lastname}}</span> <span>(<em>F\xF6rhandlare</em>)</span> <span ng-show="userContainer.faSide.length === 0">Representanter saknas</span></div><div ng-repeat="participant in userContainer.delegationInvitations | filter: {roleObj: {party: \'!20\'}}"><span>{{participant.email}} ({{participant.roleObj.name}})</span></div></div></div>');
$templateCache.put('resources/agreement/free/properties/select-properties.html','<div><h2 class="text-margin-top-reset text-margin-bottom-reset">L\xE4gg till fastigheter</h2></div><div class="row"><div class="col display-flex space-between align-items-center"><div></div><div><hy-progress-circular ng-show="vm.saving" size="20px"></hy-progress-circular><a class="link--turquoise link--no-underline spacer-mr-medium" ng-click="$parent.vm.editPropertiesMode = false">Tillbaka </a><button class="btn" ng-click="$parent.vm.saveProperties()">Spara</button></div></div></div><hr class="spacer-mt-small"><div ng-if="Auth.isHgfUser()"><hy-apartment-selector-2 id="apartmentSelector" allow-empty-properties="true" empty-properties="$parent.vm.properties" empty-only="true" user-id="Auth.currentUser().id" model="$parent.vm.selectedApartments"></hy-apartment-selector-2></div><div ng-if="Auth.isExternalUser()"><div class="col-10 col-md-4"><div class="dropdown dropdown--hide-caret dropdown--no-border" ng-class="{\'open\' : vm.toggleMenu}"><label class="display-flex align-items-center">S\xF6k fram och l\xE4gg till fastigheter<div ng-click="vm.showHelpProperties(\'S\xF6k fastigheter\', \'Om inte den fastighet du s\xF6ker finns med i resultatet, prova att specifiera din s\xF6kning (fler bokst\xE4ver).\')" class="icon icon--info icon--small cursor-pointer display-inline-block spacer-ml-xsmall"></div></label><div class="display-flex spacer-mb-xsmall"><input type="text" class="form-control form-control--search form-control--search--large" placeholder="S\xF6k p\xE5 alla" ng-model="vm.inputTextComplex" hy-enter="vm.searchProperties()" ng-focus="vm.toggleMenu = false"> <a href="#" class="btn--icon btn--icon-search btn--icon-search" ng-click="vm.searchProperties(); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--typeahead" role="menu"><button ng-repeat="property in vm.propertiesTypeahead" type="button" class="dropdown-item" ng-click="vm.addProperty(property); vm.toggleMenu = false">{{property.propertyDesignation}} ({{property.municipality}})</button></div></div></div><div class="col-12"><h3 class="spacer-mt-reset">Valda fastigheter</h3><table class="table table--margin-reset"><tr class="table__tr table__tr--minimalistic"><th class="table__th table__td--width-20">Fastigheter</th><th class="table__th table__td--width-20">Kommun</th><th class="table__th table__td--width-20">LM distrikt</th><th class="table__th table__td--width-30">Fastighets\xE4gare</th></tr><tr class="table__tr table__tr--minimalistic" ng-repeat="property in vm.properties | orderBy:\'propertyDesignation\'"><td class="table__td table__td--minimalistic">{{property.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{property.municipality}}</td><td class="table__td table__td--minimalistic">{{property.district}}</td><td class="table__td table__td--minimalistic"><hy-array-display model="property.owners"></hy-array-display></td></tr></table></div></div>');
$templateCache.put('resources/agreement/free/properties/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-show="vm.ready"><hy-breadcrumb></hy-breadcrumb><ng-include class="display-block animate-if" ng-if="vm.editPropertiesMode" src="\'resources/agreement/free/properties/select-properties.html\'"></ng-include><div ng-hide="vm.editPropertiesMode" class="display-flex space-between spacer-mt-xlarge spacer-mb-xxxlarge"><div class="spacer-mr-small"><h1 class="text-margin-reset">Fastighetsbest\xE5nd</h1></div><div><a href="/agreements/{{vm.agreementId}}" class="btn btn--reset-transition btn--arrow-left">Till avtal</a></div></div><div ng-hide="vm.editPropertiesMode"><div class="row spacer-mt-large"><div class="col-12"><hy-tableau id="tableau" columns="vm.tableColumns" filters-override="vm.filters" f-get-data="vm.tableFGetData" use-url="true" checkboxes="!vm.readonly" checkboxes-alternative="true" id-property="id" in-memory="true" f-row-click="vm.gotoProperty" selected-rows="vm.selectedProperties" in-memory-excessive-limit="vm.Limits.InMemoryMiddleLimit" actions="vm.agreementPropertyActions" limit="30"></hy-tableau></div></div></div></div>');
$templateCache.put('resources/blocking/new/properties/select.html','<div class="container"><hy-breadcrumb></hy-breadcrumb><div class="spacer-mt-large"><h1 class="text-margin-reset">Skapa strandning</h1><div class="spacer-mb-xxxlarge" ng-controller="SelectBlockingPropertiesCtrl as propertiesVM"><div class="text-center spacer-mt-large" ng-show="propertiesVM.saving"><hy-progress-circular size="60px"></hy-progress-circular></div><div class="row spacer-mt-negative-xlarge"><div class="col text-right"><button class="btn btn--reset-transition" ng-disabled="propertiesVM.properties.length === 0 || !propertiesVM.propertiesForm.$valid || !propertiesVM.atleastOnePropertySelected" ng-click="propertiesVM.save()"><span>Spara</span></button></div></div><ng-include src="\'resources/blocking/show/properties/partials/_properties.html\'"></ng-include><div class="row spacer-mt-large align-items-center"><div class="col text-right"><a class="link--turquoise link--no-underline spacer-mr-medium" href="/negotiations/{{vm.blocking.negotiationId}}?view=agreements">Avbryt </a><button class="btn btn--reset-transition" ng-disabled="propertiesVM.properties.length === 0 || !propertiesVM.propertiesForm.$valid || !propertiesVM.atleastOnePropertySelected" ng-click="propertiesVM.save()"><span>Spara</span></button></div></div></div></div></div>');
$templateCache.put('resources/blocking/new/partials/_apartments.html','<form name="vm.apartmentsForm" ng-submit="vm.apartmentsForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><div class="row"><div class="col"><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="vm.listApartmentsColumns" order="vm.sortApartmentsColumn" direction="vm.sortApartmentsDirection" show-label="true" label-position="side"></hy-sorter></div><hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Alla</th><th class="table__th">Nationellt l\xE4genhetsnr</th><th class="table__th">L\xE4genhetsnr</th><th class="table__th">Gatuadress</th><th class="table__th">Fastighet</th><th class="table__th">Kommun</th><th class="table__th">LM distrikt</th></tr><tr><th class="table__th table__td--width-6"><div class="custom-control custom-control--checkbox"><hy-select-all-checkbox checkboxes="vm.apartments" name="apartments" selected-ids="vm.blocking.selectedApartmentIds" is-master-selected="vm.blocking.isAllApartmentsSelected" is-at-least-one-selected="vm.atleastOneApartmentSelected" total-count="vm.apartmentTotalCount" is-disabled="vm.inputApartmentTextNationalApartmentNo || vm.inputApartmentTextApartmentNo || vm.inputApartmentTextStreetAddress ||\r\n                    vm.inputApartmentTextPropertyDesignation || vm.inputApartmentTextMunicipality || vm.inputApartmentTextDistrict"></hy-select-all-checkbox></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputApartmentTextNationalApartmentNo" hy-enter="vm.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputApartmentTextApartmentNo" hy-enter="vm.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputApartmentTextStreetAddress" hy-enter="vm.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputApartmentTextPropertyDesignation" hy-enter="vm.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputApartmentTextMunicipality" hy-enter="vm.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputApartmentTextDistrict" hy-enter="vm.queryApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="apartment in vm.apartments" ng-class="{\'table__tr--muted\' : !vm.ready,\r\n              \'text-black\': vm.blocking.selectedApartmentIds[apartment.id],\r\n              \'text-gray\': !vm.blocking.selectedApartmentIds[apartment.id]}"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{apartment.id}}" ng-model="vm.blocking.selectedApartmentIds[apartment.id]"> <label class="custom-control__label" for="{{apartment.id}}"></label></div></td><td class="table__td table__td--minimalistic table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{apartment.nationalNo | nationalNoControl}}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{apartment.ownerNo}}</td><td class="table__td table__td--minimalistic">{{apartment.streetAddress}}</td><td class="table__td table__td--minimalistic">{{apartment.property.propertyDesignation}}</td><td class="table__td table__td--minimalistic">{{apartment.municipality}}</td><td class="table__td table__td--minimalistic">{{apartment.district}}</td></tr></table><div class="text-right spacer-mt-medium" ng-show="vm.apartmentTotalCount > vm.apartmentsPageLimit"><hy-paginator get-list="vm.queryApartments" page-offset="vm.apartmentsPageOffset" page-limit="vm.apartmentsPageLimit" total-count="vm.apartmentTotalCount"></hy-paginator></div><div class="text-right spacer-mt-medium" ng-show="vm.apartmentTotalCount > 0 &amp;&amp; vm.apartmentTotalCount <= vm.apartmentsPageLimit"><strong>Totalt: </strong>{{vm.apartmentTotalCount}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="vm.apartmentTotalCount === 0">Inga l\xE4genheter hittades.</div></div></div></form><div class="row spacer-mt-large"><div class="col text-right"><button class="btn" ng-click="vm.save()" ng-disabled="!vm.isCurrentStepValid() || vm.saving">Skapa</button></div></div>');
$templateCache.put('resources/blocking/new/partials/_district.html','<form name="vm.districtForm" ng-submit="vm.districtForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><div class="row"><div class="col"><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="vm.listDistrictColumns" order="vm.sortDistrictColumn" direction="vm.sortDistrictDirection" show-label="true" label-position="side"></hy-sorter></div><hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Alla</th><th class="table__th">LM distrikt</th><th class="table__th">Kommun</th><th class="table__th">Antal fastigheter</th></tr><tr><th class="table__th table__td--width-6"><div class="custom-control custom-control--checkbox"><hy-select-all-checkbox checkboxes="vm.districts" name="district" selected-ids="vm.blocking.selectedDistrictIds" is-master-selected="vm.blocking.isAllDistrictsSelected" is-at-least-one-selected="vm.atleastOneDistrictSelected" total-count="vm.districtTotalCount"></hy-select-all-checkbox></div></th><th class="table__th table__td--width-40"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputDistrictTextDistrict" hy-enter="vm.queryDistrict(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryDistrict(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-40"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputDistrictTextMunicipality" hy-enter="vm.queryDistrict(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryDistrict(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="district in vm.districts" ng-class="{\'table__tr--muted\' : !vm.ready,\r\n              \'text-black\': vm.blocking.selectedDistrictIds[district.id],\r\n              \'text-gray-dark\': !vm.blocking.selectedDistrictIds[district.id]}"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{district.id}}" ng-model="vm.blocking.selectedDistrictIds[district.id]"> <label class="custom-control__label" for="{{district.id}}"></label></div></td><td class="table__td table__td--minimalistic">{{ district.district }}</td><td class="table__td table__td--minimalistic">{{ district.municipality }}</td><td class="table__td table__td--minimalistic">{{ district.noProperties }}</td></tr></table><div class="text-right spacer-mt-medium" ng-show="vm.districtTotalCount > vm.districtPageLimit"><hy-paginator get-list="vm.queryDistrict" page-offset="vm.districtPageOffset" page-limit="vm.districtPageLimit" total-count="vm.districtTotalCount"></hy-paginator></div><div class="text-right spacer-mt-medium" ng-show="vm.districtTotalCount > 0 &amp;&amp; vm.districtTotalCount <= vm.districtPageLimit"><strong>Totalt: </strong>{{vm.districtTotalCount}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="vm.districtTotalCount === 0">Inget omr\xE5de hittades.</div></div></div></form><div class="row spacer-mt-large"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/blocking/new/partials/_properties.html','<form name="vm.propertiesForm" ng-submit="vm.propertiesForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><div class="row"><div class="col"><div class="sort-container spacer-mb-medium"><hy-sorter list-columns="vm.listPropertiesColumns" order="vm.sortPropertiesColumn" direction="vm.sortPropertiesDirection" show-label="true" label-position="side"></hy-sorter></div><hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><table class="table table--margin-reset"><thead class="table__header"><tr><th class="table__th">Alla</th><th class="table__th">Fastigheter</th><th class="table__th">Kommun</th><th class="table__th">LM distrikt</th><th class="table__th">Antal l\xE4genheter</th></tr><tr><th class="table__th table__td--width-6"><div class="custom-control custom-control--checkbox"><hy-select-all-checkbox checkboxes="vm.properties" name="properties" selected-ids="vm.blocking.selectedPropertyIds" is-master-selected="vm.blocking.isAllPropertiesSelected" is-at-least-one-selected="vm.atleastOnePropertySelected" total-count="vm.propertyTotalCount" is-disabled="vm.inputPropertiesTextProperty || vm.inputPropertiesTextMunicipality || vm.inputPropertiesTextDistrict"></hy-select-all-checkbox></div></th><th class="table__th table__td--width-30"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputPropertiesTextProperty" hy-enter="vm.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-25"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputPropertiesTextMunicipality" hy-enter="vm.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-25"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="vm.inputPropertiesTextDistrict" hy-enter="vm.queryProperties(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="vm.queryProperties(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-15"></th></tr></thead><tr class="table__tr table__tr--minimalistic" ng-repeat="property in vm.properties" ng-class="{\'table__tr--muted\' : !vm.ready,\r\n              \'text-black\': vm.blocking.selectedPropertyIds[property.id],\r\n              \'text-gray\': !vm.blocking.selectedPropertyIds[property.id]}"><td class="table__td table__td--minimalistic"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" id="{{property.id}}" ng-model="vm.blocking.selectedPropertyIds[property.id]"> <label class="custom-control__label" for="{{property.id}}"></label></div></td><td class="table__td table__td--minimalistic">{{ property.propertyDesignation }}</td><td class="table__td table__td--minimalistic">{{ property.municipality }}</td><td class="table__td table__td--minimalistic">{{ property.district }}</td><td class="table__td table__td--minimalistic">{{ property.numNegotiatedApartments }}</td><td class="table__td table__td--minimalistic"></td></tr></table><div class="text-right spacer-mt-medium" ng-show="vm.propertyTotalCount > vm.propertiesPageLimit"><hy-paginator get-list="vm.queryProperties" page-offset="vm.propertiesPageOffset" page-limit="vm.propertiesPageLimit" total-count="vm.propertyTotalCount"></hy-paginator></div><div class="text-right spacer-mt-medium" ng-show="vm.propertyTotalCount > 0 &amp;&amp; vm.propertyTotalCount <= vm.propertiesPageLimit"><strong>Totalt: </strong>{{vm.propertyTotalCount}}</div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="vm.propertyTotalCount === 0">Inga fastigheter hittades.</div></div><div class="col-3" ng-show="false"><h4 class="text-margin-top-reset">Valt best\xE5nd fr\xE5n<br>fastighetsregistret</h4><div class="statistics-box"><div class="statistics-box__item"><h5 class="statistics-box__item-title">Kommuner</h5><h3 class="statistics-box__item-content--big-text statistics-box__item-content--margin-reset">XX1</h3>{{"/XX"}}</div><div class="statistics-box__item"><h5 class="statistics-box__item-title">Fastigheter</h5><h3 class="statistics-box__item-content--big-text statistics-box__item-content--margin-reset">XXX1</h3>{{"/XXX"}}</div><div class="statistics-box__item"><h5 class="statistics-box__item-title">L\xE4genheter</h5><h3 class="statistics-box__item-content--big-text statistics-box__item-content--margin-reset">XXXX1</h3>{{"/XXXX"}}</div></div></div></div></form><div class="row spacer-mt-large"><div class="col text-right"><button class="btn btn--arrow-right" ng-click="vm.gotoNext = true" ng-disabled="!vm.isCurrentStepValid()">N\xE4sta</button></div></div>');
$templateCache.put('resources/help/show/process/show.html','<div class="container"><div class="row"><div class="col-12"><h1>V\xE5r gemensamma process</h1></div></div><div class="row"><div class="col-4 col-lg-3">Beh\xF6ver du hj\xE4lp? H\xE4r ser du vilka aktiviteter du kan genomf\xF6ra i F\xF6rhandlarportalen.<p class="spacer-mt-large"><strong>F\xF6rklaring</strong></p><div class="process-legend"><div class="process-legend__item" ng-class="{\'process-legend__item--active\': vm.active === \'before\'}" ng-click="vm.toggleActive(\'before\')"><div class="process-legend__item-box"></div>Innan f\xF6rhandling</div><div class="process-legend__item process-legend__item--pink" ng-class="{\'process-legend__item--pink-active\': vm.active === \'prepare\'}" ng-click="vm.toggleActive(\'prepare\')"><div class="process-legend__item-box"></div>Bereds</div><div class="process-legend__item process-legend__item--turq" ng-class="{\'process-legend__item--turq-active\': vm.active === \'ongoing\'}" ng-click="vm.toggleActive(\'ongoing\')"><div class="process-legend__item-box"></div>P\xE5g\xE5r</div><div class="process-legend__item process-legend__item--yellow" ng-class="{\'process-legend__item--yellow-active\': vm.active === \'agreed\'}" ng-click="vm.toggleActive(\'agreed\')"><div class="process-legend__item-box"></div>Uppg\xF6relse</div><div class="process-legend__item process-legend__item--peach" ng-class="{\'process-legend__item--peach-active\': vm.active === \'blocked\'}" ng-click="vm.toggleActive(\'blocked\')"><div class="process-legend__item-box"></div>Strandad/delvis strandad</div><div class="process-legend__item process-legend__item--gray-dark" ng-class="{\'process-legend__item--gray-dark-active\': vm.active === \'finished\'}" ng-click="vm.toggleActive(\'finished\')"><div class="process-legend__item-box"></div>Avslutad</div></div></div><div class="col-8 col-lg-6"><div class="row text-center spacer-mb-medium"><div class="col text-uppercase"><strong>Hyresg\xE4stf\xF6reningen</strong></div><div class="col text-uppercase"><strong>Fastighets\xE4gare</strong></div></div><div class="process-tree"><div class="row"><div class="col-6 push-6"><div class="process-tree__item" ng-class="vm.active === \'before\' ? \'process-tree__item--gray-dark\' : \'\'">Skicka en f\xF6rhandlingsframst\xE4llan i god tid innan f\xF6rhandlingsstart.</div></div></div><div class="row"><div class="col-6"><div class="process-tree__item" ng-class="vm.active === \'before\' ? \'process-tree__item--gray-dark\' : \'\'">Ta emot framst\xE4llan, skapa f\xF6rhandling och tilldela f\xF6rhandlare.</div></div></div><div class="row spacer-mb-small"><div class="col-6 push-6"><div class="process-tree__item process-tree__item--pink-light" ng-class="vm.active === \'prepare\' ? \'process-tree__item--pink-dark\' : \'\'">F\xE5 mailnotis om att f\xF6rhandling har skapats.</div></div></div><div class="row spacer-mb-medium"><div class="col-6 self-align-flex-end"><div class="process-tree__item process-tree__item--pink-light" ng-class="vm.active === \'prepare\' ? \'process-tree__item--pink-dark\' : \'\'">Ladda upp relevant underlag p\xE5 f\xF6rhandlingssidan.</div></div><div class="col-6"><div class="process-tree__item process-tree__item--pink-light" ng-class="vm.active === \'prepare\' ? \'process-tree__item--pink-dark\' : \'\'">Ange ett f\xF6rsta yrkande och motiv om det inte redan \xE4r gjort, ladda upp hyreslista samt annat relevant underlag p\xE5 f\xF6rhandlingssidan.</div></div></div><div class="row spacer-mb-medium"><div class="col-6 push-3"><div class="process-tree__item process-tree__item--pink-light" ng-class="vm.active === \'prepare\' ? \'process-tree__item--pink-dark\' : \'\'">Boka in f\xF6rhandlingsm\xF6ten.</div></div></div><div class="row spacer-mb-medium"><div class="col-6 push-3"><div class="process-tree__item process-tree__item--turq" ng-class="vm.active === \'ongoing\' ? \'process-tree__item--turq-dark\' : \'\'">P\xE5b\xF6rja f\xF6rhandlingen genom att ha f\xF6rhandlingsm\xF6ten. De 90 dagarna b\xF6rjar ticka vid f\xF6rsta f\xF6rhandlingsm\xF6tet. F\xF6r protokoll till m\xF6tena.</div></div></div><div class="row spacer-mb-medium"><div class="col-6 push-3"><div class="process-tree__item process-tree__item--yellow" ng-class="vm.active === \'agreed\' ? \'process-tree__item--yellow-dark\' : \'\'">Skriv \xF6verenskommelser f\xF6r de best\xE5nd ni \xE4r \xF6verens om.<br>B\xE5da parter godk\xE4nner \xF6verenskommelser.</div></div></div><div class="row spacer-mb-medium"><div class="col-6 push-3"><div class="process-tree__item process-tree__item--peach" ng-class="vm.active === \'blocked\' ? \'process-tree__item--peach-dark\' : \'\'">Om ni mot f\xF6rmodan inte kommer \xF6verens s\xE5 markera det relevanta best\xE5ndet som strandat i portalen men sk\xF6t processen vid sidan av portalen p\xE5 vanligt vis.</div></div></div><div class="row"><div class="col-6 push-3"><div class="process-tree__item process-tree__item--gray" ng-class="vm.active === \'finished\' ? \'process-tree__item--gray-xdark\' : \'\'">Avsluta f\xF6rhandling inom 90 dagar genom att trycka p\xE5 avslutaknappen.</div></div></div></div></div></div></div>');
$templateCache.put('resources/organization/show/modals/blocking-deletion-modal.html','<div class="modal-header modal-header--medium"><h2 class="modal-title text-margin-reset">Du kan endast ta bort om det inte finns anv\xE4ndare kopplade till organisationen</h2></div><div class="modal-body modal-body--medium" id="modal-body"><div>F\xF6ljande objekt blockerar borttagning</div><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown></div></div><hy-tabcontrol tablist="vm.tablist" current-tab="vm.selectedTab"></hy-tabcontrol><div class="row spacer-mb-xlarge"></div><list-blocking-users ng-show="vm.selectedTab === 1" organization="vm.organization" list="vm.users" cancel="vm.cancel"></list-blocking-users><list-blocking-apartments ng-show="vm.selectedTab === 2" organization="vm.organization" list="vm.apartments" cancel="vm.cancel"></list-blocking-apartments><list-blocking-negotiation-calls ng-show="vm.selectedTab === 3" organization="vm.organization" list="vm.callsForNegotiations" cancel="vm.cancel"></list-blocking-negotiation-calls><list-blocking-negotiations ng-show="vm.selectedTab === 4" organization="vm.organization" list="vm.negotiations" cancel="vm.cancel"></list-blocking-negotiations><list-blocking-negotiated-apartments ng-show="vm.selectedTab === 5" organization="vm.organization" list="vm.negotiatedApartments" cancel="vm.cancel"></list-blocking-negotiated-apartments><list-blocking-negotiated-apartments-calls ng-show="vm.selectedTab === 6" organization="vm.organization" list="vm.negotiatedApartmentsForCalls" cancel="vm.cancel"></list-blocking-negotiated-apartments-calls><div class="modal-footer modal-footer--medium"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button class="btn btn--secondary" type="submit" ng-click="vm.delete()">Ta bort \xE4nd\xE5</button></div>');
$templateCache.put('resources/organization/show/user/changeUserRoleModal.html','<form name="changeUserRoleForm" ng-submit="changeUserRoleForm.$valid &amp;&amp; vm.save()" novalidate><div class="modal-header modal-header--small"><h2 class="text-margin-reset" id="modal-title">\xC4ndra beh\xF6righet</h2></div><div class="modal-body modal-body--small" id="modal-body"><div class="row"><div class="col-12"><label for="inputRole">Beh\xF6righet</label><div class="dropdown dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__filter btn--full-width" uib-dropdown-toggle><span class="text-normal">{{vm.user.role | hyRoles}}</span></button><div class="dropdown-menu dropdown-menu--full-width" uib-dropdown-menu role="menu"><button ng-repeat="role in vm.roles" type="button" class="dropdown-item" ng-click="vm.selectRole(role.value)">{{role.value | hyRoles}}</button></div></div><small>*Med beh\xF6righet <em>Anv\xE4ndare</em> kan de arbeta med organisationens f\xF6rhandlingar och fastighetsbest\xE5nd. Med den ut\xF6kade beh\xF6righeten <em>Admin</em> kan de \xE4ven bjuda in andra medarbetare till denna organisation.</small></div></div></div><div class="modal-footer modal-footer--small"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline spacer-mr-small" ng-click="vm.cancel()">Avbryt</a> <button class="btn" type="submit" ng-disabled="vm.saving">Spara</button></div></form>');
$templateCache.put('resources/organization/show/user/inviteUsersModal.html','<form name="inviteForm" ng-submit="inviteForm.$valid &amp;&amp; vm.save()" novalidate><div class="modal-header"><h2 class="modal-title" id="modal-title">Bjud in anv\xE4ndare</h2></div><div class="modal-body" id="modal-body"><div class="row"><div class="col-12"><p class="line-height-narrow spacer-mt-reset"><strong>Beh\xF6righet</strong> - Med beh\xF6righet <em>Anv\xE4ndare</em> kan de arbeta med organisationens f\xF6rhandlingar och fastighetsbest\xE5nd. Med den ut\xF6kade beh\xF6righeten <em>Admin</em> kan de \xE4ven bjuda in andra medarbetare till denna organisation.</p></div><div class="col-12 col-md-6"><div class="form-group spacer-mb-reset" ng-class="{\'form-group--error\': (inviteForm.inputEmail.$touched || inviteForm.$submitted) &amp;&amp; inviteForm.inputEmail.$invalid\r\n      }"><label for="inputEmail spacer-mr-xsmall">E-post</label> <small style="color: red" ng-show="vm.isMailConfirmed===false">E-postadress bekr\xE4ftad inte</small> <small style="color: green" ng-show="vm.isMailConfirmed===true">E-postadress bekr\xE4ftad</small><br><input name="inputEmail" id="inputEmail" type="email" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.email" placeholder="Exempel: hgf@hgf.se" autocomplete="off" ng-pattern="/^(([^<>()\\[\\]\\\\.,;:\\s@\u201C]+(\\.[^<>()\\[\\]\\\\.,;:\\s@\u201C]+)*)|(\u201C.+\u201C))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$/" ng-change="vm.startCheckEmail()" required><hy-progress-circular size="20px" ng-show="vm.confirmingEmail"></hy-progress-circular><br><label for="inputPhone">Telefonnummer</label><br><div ng-hide="vm.foreignPhone" class="form-control-prepend">+46</div><input name="inputPhone" id="inputPhone" type="tel" ng-pattern="/[0-9]{7,}/im" ng-change="vm.tamePhoneNumber()" class="form-control form-control--prepend form-control--gray-border" ng-model="vm.user.phone" placeholder="Exempel: 706532452" autocomplete="off"></div></div><div class="col-12 col-md-6"><div ng-show="vm.foundAdmin.length > 0"><label for="inputRole">Beh\xF6righet</label><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width" uib-dropdown-toggle>{{vm.user.role | hyRoles}}</button><div class="dropdown-menu dropdown-menu--full-width" uib-dropdown-menu role="menu"><button ng-repeat="role in vm.roles" type="button" class="dropdown-item" ng-click="vm.selectRole(role.value)">{{role.value | hyRoles}}</button></div></div></div><div ng-show="vm.foundAdmin.length === 0"><label for="inputRole">Beh\xF6righet</label> <button type="button" class="btn btn--squary btn--squary-reset-hover btn--full-width">Admin</button> <small>*Med den ut\xF6kade beh\xF6righeten <em>Admin</em> kan de \xE4ven bjuda in andra medarbetare till denna organisation.</small></div></div></div></div><div class="modal-footer"><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular><a class="link--turquoise link--no-underline-hover" ng-click="vm.cancel()">Avbryt</a> <button class="btn spacer-ml-medium" type="submit" ng-disabled="!vm.user.email">Skicka inbjudan</button></div></form>');
$templateCache.put('resources/organization/show/user/list-user-by-organization.html','<hy-tabcontrol tablist="tabs" current-tab="currentTab"></hy-tabcontrol><div ng-if="currentTab===\'users\'"><div class="row spacer-mb-xlarge"><div class="col-10"><p class="spacer-mb-reset">Bjud in anv\xE4ndare som ska arbeta med denna organisations f\xF6rhandlingar. Med beh\xF6righet <em>Anv\xE4ndare</em> kan de arbeta med organisationens f\xF6rhandlingar och fastighetsbest\xE5nd. Med den ut\xF6kade beh\xF6righeten <em>Admin</em> kan de \xE4ven bjuda in andra medarbetare till denna organisation.</p></div><div class="col-2 display-flex align-items-bottom justify-content-end"><button class="btn spacer-mr-small" ng-click="invite()" ng-show="!Auth.isSpectator() && (Auth.currentUser().roleByOrganization[organization.id]==200 || Auth.isHgfUser())">Bjud in</button></div></div><div ng-show="ready"><table class="table" ng-show="users.length > 0"><tr class="table__header"><th class="table__th">Namn</th><th class="table__th">E-post</th><th class="table__th" colspan="3">Beh\xF6righet</th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="user in users | orderBy:[\'firstname\', \'lastname\', \'email\']"><td class="table__td table__td--width-25"><span ng-hide="user.invited"><a href="/users/{{user.id}}">{{ user.firstname }} {{ user.lastname }}</a></span> <span ng-show="user.invited" class="text-success">Inbjuden via e-post/telefon</span></td><td class="table__td display-flex table__td--width-45"><div class="icon icon--warning spacer-mr-small" ng-show="user.bounced" uib-tooltip="Inbjudan kunde inte levereras. Kontrollera att mailadressen \xE4r r\xE4ttstavad."></div>{{ user.email }}</td><td class="table__td table__td--width-15">{{ user.role |\xA0hyRoles }}</td><td class="table__td"><a class="link--turquoise link--underline link--icon link--icon-edit" ng-click="changeUserRole(user.id)" ng-show="!user.invited &amp;&amp; (Auth.currentUser().role >= 200 || Auth.currentUser().roleByOrganization[organization.id]==200)">\xC4ndra roll</a></td><td class="table__td text-right"><a href="#" class="btn--icon btn--icon-bin btn--icon--no-padding" ng-click="delete(user); $event.preventDefault();" ng-show="Auth.currentUser().role >= 200 || Auth.currentUser().roleByOrganization[organization.id] == 200"><span class="sr-only">Ta bort</span></a></td></tr></table><div class="alert alert--info" ng-hide="users.length > 0">Inga anv\xE4ndare \xE4r kopplade till {{organization.name}}. <span ng-show="Auth.currentUser().roleByOrganization[organization.id] == 200 || Auth.isHgfUser()">Du kan l\xE4gga till nya anv\xE4ndare genom att klicka p\xE5 "Bjud in".</span></div></div></div><div ng-if="currentTab===\'properties\'"><table class="table" ng-show="properties.length > 0"><tr class="table__header"><th class="table__th">Namn</th><th class="table__th">Kommun</th><th class="table__th">Distrikt</th><th></th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="property in properties"><td class="table__td">{{ property.propertyDesignation }}</td><td class="table__td">{{ property.municipality }}</td><td class="table__td">{{ property.district }}</td><td><a ng-if="Auth.canDo(\'unbindProperty\')" href="#" class="btn--icon btn--icon-padding btn--icon-bin ng-scope" ng-click="deleteBinding(property)"></a></td></tr></table></div>');
$templateCache.put('resources/help/show/faq/show.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-show="vm.ready"><div class="row"><div class="col-12 col-lg-8"><h1>Beh\xF6ver du hj\xE4lp?</h1></div><div class="col-12 col-lg-4"><h3 class="text-margin-bottom-reset spacer-mt-xxxlarge">L\xE4nkar</h3><div class="link-box spacer-mt-small"><a href="/help/process" class="link--turquoise link--underline">Visa v\xE5r gemensamma process</a></div></div></div><div class="row"><div class="col-12 col-lg-4"><h3 class="text-margin-top-large">Komma ig\xE5ng</h3><uib-accordion><div uib-accordion-group class="card--pink" heading="Hur f\xE5r jag inloggning till f\xF6rhandlarportalen?"><p class="card__text">Kontakta den som \xE4r admin f\xF6r din organisation och be om att bli inbjuden. \xC4r du den f\xF6rsta i din organisation som anv\xE4nder portalen, kontakta Hyresg\xE4stf\xF6reningen:</p><a href="mailto:forhandlarportalen@hyresgastforeningen.se" class="card__link link--turquoise text-reset">forhandlarportalen@hyresgastforeningen.se</a></div><div uib-accordion-group class="card--pink" heading="Hur g\xF6r jag n\xE4r jag gl\xF6mt mina inloggningsuppgifter?"><p class="card__text">P\xE5 inloggningssidan finns en l\xE4nk f\xF6r att \xE5terst\xE4lla l\xF6senordet. Anv\xE4ndarnamnet \xE4r alltid din mejladress.</p></div><div uib-accordion-group class="card--pink" heading="Hur g\xF6ra jag f\xF6r att \xE4ndra anv\xE4ndarinformation?"><p class="card__text">Logga in p\xE5 portalen och klicka p\xE5 ditt namn i det \xF6vre h\xF6gra h\xF6rnet. Klicka p\xE5 pennan och \xE4ndra dina uppgifter.</p></div><div uib-accordion-group class="card--pink" heading="Vilka webbl\xE4sare st\xF6djer f\xF6rhandlarportalen?"><p class="card__text">Du kan anv\xE4nda Internet Explorer, Chrome, och Safari.</p></div><div uib-accordion-group class="card--pink" heading="Kan jag anv\xE4nda f\xF6rhandlarportalen p\xE5 b\xE5de dator, surfplatta och smartphone?"><p class="card__text">F\xF6rhandlarportalen har reponsiv design men vi rekommenderar inte att anv\xE4nda smartphone. Dator och surfplatta funkar bra.</p></div><div uib-accordion-group class="card--pink" heading="Var kan jag f\xE5 hj\xE4lp om jag har problem med portalen?"><p class="card__text">Du kan mejla till <a href="mailto:forhandlarportalen@hyresgastforeningen.se" class="link--turquoise text-reset">forhandlarportalen@hyresgastforeningen.se</a></p></div><div uib-accordion-group class="card--pink" heading="Var l\xE4r jag mig att anv\xE4nda portalen?"><p class="card__text">Det finns <a href="/help/videos" class="link--turquoise text-reset">instruktionsvideor</a> som visar hur du anv\xE4nder portalen. Det finns \xE4ven en manual i textform (PDF-format). Eftersom anv\xE4ndargr\xE4nssnittet skiljer sig p\xE5 n\xE5gra punkter beroende p\xE5 vilken part du representerar, \xE4r manualen uppdelad i tv\xE5 delar. V\xE4lj mellan att ladda ner <a href="/docs/anvandarinstruktioner-hgf-forhandlarportalen.pdf" class="link--turquoise link--underline text-reset" download target="_self">Hyresg\xE4stf\xF6reningens del</a> eller <a href="/docs/anvandarinstruktioner-foretag-forhandlarportalen.pdf" class="link--turquoise link--underline text-reset" download target="_self">Fastighets\xE4garens del</a>.</p></div></uib-accordion></div><div class="col-12 col-lg-4"><h3 class="text-margin-top-large">N\xE4r du \xE4r i g\xE5ng</h3><uib-accordion><div uib-accordion-group heading="Vad g\xF6r jag om vi saknar n\xE5gon funktionalitet i systemet?"><p class="card__text">Du kan mejla f\xF6rslag och synpunkter till <a href="mailto:forhandlarportalen@hyresgastforeningen.se" class="link--turquoise text-reset">forhandlarportalen@hyresgastforeningen.se</a></p></div><div uib-accordion-group heading="Vilka hj\xE4lpfunktioner finns?"><p class="card__text">Det finns denna FAQ som visar hur du anv\xE4nder portalen och m\xF6jlighet att mejla fr\xE5gor och problem till <a href="mailto:forhandlarportalen@hyresgastforeningen.se" class="link--turquoise text-reset">forhandlarportalen@hyresgastforeningen.se</a></p></div><div uib-accordion-group heading="Vem kan se mina f\xF6rhandlingar i portalen?"><p class="card__text">De som \xE4r tillagda som f\xF6rhandlare i den f\xF6rhandlingen f\xF6r respektive part och de som \xE4r admin f\xF6r systemet f\xF6r respektive part</p></div><div uib-accordion-group heading="Vilken typ av filer kan jag dela p\xE5 en f\xF6rhandling i portalen?"><p class="card__text">Alla vanliga filformat g\xE5r att dela word, excel, ppt, pdf, bilder etc</p></div></uib-accordion></div><div class="col-12 col-lg-4"><h3 class="text-margin-top-large">Generellt</h3><uib-accordion><div uib-accordion-group class="card--turq" heading="Vilka funktioner har portalen?"><p class="card__text">Portalen har mycket funktionalitet men kort beskrivet s\xE5 kan du skapa en framst\xE4llan med yrkanden, hyreslista och andra underlag till f\xF6rhandlingen. Under f\xF6rhandlingen bokar du m\xF6ten och skriver protokoll. N\xE4r ni \xE4r \xF6verens skriver du \xF6verenskommelse och l\xE4gger upp nya hyror.</p></div><div uib-accordion-group class="card--turq" heading="Hur \xE4r portalen kopplad till Hyresg\xE4stf\xF6reningens?"><p class="card__text">Hyresg\xE4stf\xF6reningen har tagit initiativ till utvecklat f\xF6rhandlarportalen. Men det \xE4r inte en del av Hyresg\xE4stf\xF6reningens infrastruktur utan \xE4r helt frist\xE5ende och lagrad i Azure.</p></div><div uib-accordion-group class="card--turq" heading="Kan f\xF6rhandlarportalen tillhandah\xE5lla data till v\xE5rt BI-system?"><p class="card__text">Inte nu under testperioden. Men till n\xE4sta f\xF6rhandlingsomg\xE5ng kommer den m\xF6jligheten att finnas.</p></div><div uib-accordion-group class="card--turq" heading="Kan man jobba offline?"><p class="card__text">Nej, du m\xE5ste han tillg\xE5ng till internet f\xF6r att jobba i portalen.</p></div><div uib-accordion-group class="card--turq" heading="Kr\xE4vs installation av n\xE5gon programvara?"><p class="card__text">Nej, portalen \xE4r webbaserad och kr\xE4ver inga andra program \xE4n en webbl\xE4sare.</p></div></uib-accordion></div></div><div class="row"><div class="col-6 col-lg-4"><hr class="spacer-mt-xxlarge hr--turquoise"><h3 class="text-margin-top-reset">Kontakta oss</h3><div class="spacer-mb-small"><a class="link--turquoise link--underline spacer-mb-xsmall" href="mailto:forhandlarportalen@hyresgastforeningen.se">forhandlarportalen@hyresgastforeningen.se</a></div><div><a href="tel:0046104591050" class="link--underline btn--icon btn--icon-telephone">010-4591050</a></div></div></div></div>');
$templateCache.put('resources/search/agreements/free/list.html','<div ng-controller="FreeListCtrl as freeVM"><hy-tableau id="tableau" limit="30" columns="freeVM.tableColumns" default-sorting="id DESC" f-get-data="freeVM.tableFGetData" f-row-click="freeVM.gotoFreeAgreement" f-get-context-info="freeVM.getContextInfo" use-url="true"></hy-tableau></div>');
$templateCache.put('resources/real-estate/show/active-negotiations/show.html','<div ng-controller="ShowRealEstateActiveNegotiationsCtrl as tablelistVM"><div class="row"><div class="col-12"><h2 class="spacer-mt-medium">Aktiva f\xF6rhandlingar</h2><hy-tableau id="tableauActiveNegotiations" columns="tablelistVM.columns" f-get-data="tablelistVM.getData" use-url="false" in-memory="true" in-memory-excessive-limit="tablelistVM.Limits.InMemoryMiddleLimit" f-row-click="tablelistVM.gotoNegotiation" limit="15" f-get-context-info="tablelistVM.getContextInfo"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><status>{{$parent.item.combinedState | hyCombinedNegotiationStates}}</status><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && tablelistVM.editComment(item)"></comment></hy-tableau></div></div></div>');
$templateCache.put('resources/real-estate/show/finished-negotiations/show.html','<div ng-controller="ShowRealEstateFinishedNegotiationsCtrl as tablelistVM"><div class="row"><div class="col-12"><h2 class="spacer-mt-medium">Avslutade f\xF6rhandlingar</h2><hy-tableau id="tableauActiveNegotiations" columns="tablelistVM.columns" f-get-data="tablelistVM.getData" use-url="false" in-memory="true" f-row-click="tablelistVM.gotoNegotiation" in-memory-excessive-limit="tablelistVM.Limits.InMemoryMiddleLimit" limit="15" f-get-context-info="tablelistVM.getContextInfo"><slot1>{{$parent.item.type | hyNegotiationTypes}}</slot1><date1>{{$parent.item.finishDate | date: "yyyy-MM-dd" }}</date1><comment ng-init="item = $parent.item"> <img ng-if="Auth.canDo(\'viewNegotiationComment\')" src="/img/comment{{ item.comment ? \'-filled\' : \'\' }}.png" width="24" height="24" class="cursor-pointer" uib-tooltip-html="item.comment" tooltip-placement="bottom" ng-click="Auth.canDo(\'editNegotiationComment\') && tablelistVM.editComment(item)"></comment></hy-tableau></div></div></div>');
$templateCache.put('resources/organization/show/blocking-data/list-blocking-apartments.html','<div><table class="modal-table"><tr class="modal-table__header"><th class="modal-table__th">Gatuadress</th><th class="modal-table__th">Kommun</th><th class="modal-table__th">Nationella L\xE4genhetsnr</th></tr><tr class="modal-table__tr modal-table__tr--disable-click" ng-repeat="apartment in list"><td class="modal-table__td modal-table__td--width-15" ng-click="cancel()"><span><a ng-click="cancel()" ng-href="/properties/{{apartment.propertyId}}">{{ apartment.streetAddress }}</a></span></td><td class="modal-table__td display-flex modal-table__td--width-50">{{ apartment.district }}</td><td class="modal-table__td modal-table__td--width-15 table__td--nowrap">{{ apartment.nationalNo }}</td><td class="modal-table__td text-right"><span class="sr-only">Ta bort</span></td></tr></table></div>');
$templateCache.put('resources/organization/show/blocking-data/list-blocking-negotiated-apartments-calls.html','<div><table class="modal-table"><tr class="modal-table__header"><th class="modal-table__th">Gatuadress</th><th class="modal-table__th">Kommun</th><th class="modal-table__th">Nationella L\xE4genhetsnr</th></tr><tr class="modal-table__tr modal-table__tr--disable-click" ng-repeat="obj in list"><td class="modal-table__td modal-table__td--width-15" ng-click="cancel()"><span><a ng-click="cancel()">{{ obj.streetAddress }}</a></span></td><td class="modal-table__td display-flex modal-table__td--width-50">{{ obj.district }}</td><td class="modal-table__td modal-table__td--width-15 table__td--nowrap">{{ obj.nationalNo }}</td><td class="modal-table__td text-right"><span class="sr-only">Ta bort</span></td></tr></table></div>');
$templateCache.put('resources/organization/show/blocking-data/list-blocking-negotiated-apartments.html','<div><table class="modal-table"><tr class="modal-table__header"><th class="modal-table__th">Gatuadress</th><th class="modal-table__th">Kommun</th><th class="modal-table__th">Nationella L\xE4genhetsnr</th></tr><tr class="modal-table__tr modal-table__tr--disable-click" ng-repeat="obj in list"><td class="modal-table__td modal-table__td--width-15" ng-click="cancel()"><span><a ng-click="cancel()">{{ obj.streetAddress }}</a></span></td><td class="modal-table__td display-flex modal-table__td--width-50">{{ obj.district }}</td><td class="modal-table__td modal-table__td--width-15 table__td--nowrap">{{ obj.nationalNo }}</td><td class="modal-table__td text-right"><span class="sr-only">Ta bort</span></td></tr></table></div>');
$templateCache.put('resources/organization/show/blocking-data/list-blocking-negotiation-calls.html','<div><table class="modal-table"><tr class="modal-table__header"><th class="modal-table__th">Namn</th><th class="modal-table__th">Meddelande</th><th class="modal-table__th">Status</th></tr><tr class="modal-table__tr modal-table__tr--disable-click" ng-repeat="obj in list"><td class="modal-table__td modal-table__td--width-15" ng-click="cancel()"><span><a ng-click="cancel()" href="/negotiation-calls/{{obj.id}}">{{ obj.name }}</a></span></td><td class="modal-table__td display-flex modal-table__td--width-50">{{ obj.message }}</td><td class="modal-table__td modal-table__td--width-15">{{ obj.state }}</td><td class="modal-table__td text-right"><span class="sr-only">Ta bort</span></td></tr></table></div>');
$templateCache.put('resources/organization/show/blocking-data/list-blocking-negotiations.html','<div><table class="modal-table"><tr class="modal-table__header"><th class="modal-table__th">Namn</th><th class="modal-table__th">Meddelande</th><th class="modal-table__th">Status</th></tr><tr class="modal-table__tr modal-table__tr--disable-click" ng-repeat="obj in list | orderBy:[\'firstname\', \'lastname\', \'email\']"><td class="modal-table__td modal-table__td--width-15" ng-click="cancel()"><span><a ng-click="cancel()" href="/negotiations/{{obj.id}}">{{ obj.name }}</a></span></td><td class="modal-table__td display-flex modal-table__td--width-50">{{ obj.message }}</td><td class="modal-table__td modal-table__td--width-15">{{ obj.state }}</td><td class="modal-table__td text-right"><span class="sr-only">Ta bort</span></td></tr></table></div>');
$templateCache.put('resources/organization/show/blocking-data/list-blocking-users.html','<div><table class="modal-table"><tr class="modal-table__header"><th class="modal-table__th">Namn</th><th class="modal-table__th">E-post</th><th class="modal-table__th">Beh\xF6righet</th></tr><tr class="modal-table__tr modal-table__tr--disable-click" ng-repeat="user in list | orderBy:[\'firstname\', \'lastname\', \'email\']" ng-show="user"><td class="modal-table__td modal-table__td--width-15" ng-click="cancel()"><span><a ng-click="cancel()" href="/users/{{user.id}}">{{ user.firstname }} {{ user.lastname }}</a></span></td><td class="modal-table__td display-flex modal-table__td--width-50">{{ user.email }}</td><td class="modal-table__td modal-table__td--width-15">{{ user.role |\xA0hyRoles }}</td><td class="modal-table__td text-right"><a href="#" class="btn--icon btn--icon-bin btn--icon--padding" ng-click="delete(user); $event.preventDefault();" ng-show="Auth.currentUser().role == {{ roles.ADMIN }} || Auth.currentUser().roleByOrganization[organization.id] == {{ roles.ADMIN }}"><span class="sr-only">Ta bort</span></a></td></tr></table></div>');
$templateCache.put('resources/real-estate/show/apartments/show.html','<div ng-controller="ShowRealEstateApartmentsCtrl as tablelistVM"><div class="row"><div class="col-12 display-flex space-between align-items-center"><h2 class="spacer-mt-medium">L\xE4genheter</h2><div class="sort-column">NH:<hy-combobox class="di spacer-mr-small" current-item="tablelistVM.nh.current" current-item-changed="tablelistVM.nhTableChange()" id-property="id" text-property="text" items="tablelistVM.nh.items"></hy-combobox><hy-sorter class="di" list-columns="tablelistVM.listColumns" order="tablelistVM.sortColumn" direction="tablelistVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div></div><div class="col-12 spacer-mb-medium"><table class="table table--margin-b-reset spacer-mt-medium"><thead class="table__header"><tr><th class="table__th table__td--width-5">Info <span class="icon icon--inline icon--info" uib-tooltip-html="tablelistVM.statusTooltip" tooltip-placement="bottom"></span></th><th class="table__th table__td--width-10">Nationellt l\xE4genhetsnr</th><th class="table__th table__td--width-10">L\xE4genhetsnr</th><th class="table__th table__td--width-10">Normhyra ({{tablelistVM.nh.current.text}})</th><th class="table__th table__td--width-10">Aktuell hyra (kr/m\xE5nad)</th><th class="table__th table__td--width-10">Hyra g\xE4ller fr.o.m</th><th class="table__th table__td--width-10">Gatuadress</th><th class="table__th table__td--width-5">Hustyp</th><th class="table__th table__td--width-5">Yta (kvm)</th><th class="table__th table__td--width-5">Antal rum</th><th class="table__th table__td--width-10">Notering/<br>Kommentar</th></tr><tr><th class="table__th table__td--width-10"><hy-combobox prompt="V\xE4lj typ" items="tablelistVM.apartmentStatusTypes" current-item="tablelistVM.selectedStatusType" current-item-changed="tablelistVM.statusTypeChanged" text-property="longName"></hy-combobox></th><th class="table__th table__td--width-10"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="tablelistVM.inputNationalNo" hy-enter="tablelistVM.getPropertyApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="tablelistVM.getPropertyApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="tablelistVM.inputOwnerNo" hy-enter="tablelistVM.getPropertyApartments(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="tablelistVM.getPropertyApartments(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-5"></th><th class="table__th table__td--width-5"></th></tr></thead><tbody><tr class="table__tr" ng-repeat="apartment in tablelistVM.apartmentsSlice | filter: tablelistVM.filterByStatusType" ng-class="{\'table__tr--muted\': !tablelistVM.ready}" ng-click="tablelistVM.showApartmentHistory(apartment)"><td class="table__td table__td--minimalistic" align="center" ng-bind-html="apartment.statusString"></td><td class="table__td table__td--minimalistic table__td--nowrap" title="{{apartment.nationalNo | nationalNoTitleControl}}">{{apartment.nationalNo | nationalNoControl}}</td><td class="table__td table__td--minimalistic table__td--nowrap">{{apartment.ownerNo}}</td><td class="table__td table__td--minimalistic">{{ apartment.rent[tablelistVM.nh.current.id] }}</td><td class="table__td table__td--minimalistic">{{tablelistVM.getMonthlyRent(apartment) | number:0}}<hy-std-display model="{{apartment.objectsExtra}}"></hy-std-display></td><td class="table__td table__td--minimalistic">{{apartment.rent.effectiveFrom | date:\'yyyy-MM-dd\'}}</td><td class="table__td table__td--minimalistic">{{apartment.streetAddress}}</td><td class="table__td table__td--minimalistic">{{apartment.buildingType}}</td><td class="table__td table__td--minimalistic">{{apartment.livingSpace}}</td><td class="table__td table__td--minimalistic">{{apartment.rooms}}</td><td class="table__td table__td--minimalistic">{{apartment.rentFactors}}{{apartment.comment}}</td></tr></tbody></table><div class="row spacer-mt-medium" ng-show="tablelistVM.apartmentsCount > tablelistVM.pageLimit"><div class="col text-right"><hy-paginator get-list="tablelistVM.paginatorGetPropertyApartments" page-offset="tablelistVM.pageOffset" page-limit="tablelistVM.pageLimit" total-count="tablelistVM.apartmentsCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="tablelistVM.myListOfNegotiationsCount > 0 &amp;&amp; tablelistVM.myListOfNegotiationsCount <= tablelistVM.pageLimit"><div class="col text-right"><strong>Totalt: </strong>{{tablelistVM.myListOfNegotiationsCount}}</div></div><div ng-show="tablelistVM.apartmentsCount === 0 &amp;&amp; tablelistVM.ready" class="alert alert--hide-border alert--slim alert--squary">Inga l\xE4genheter finns att visa</div><div ng-show="!tablelistVM.ready" class="col-12 display-flex justify-content-center"><hy-progress-circular size="40px"></hy-progress-circular></div></div></div><div class="row"><div class="col-12"><span>Ladda ner listan <a class="link--turquoise link--underline" target="_self" download ng-click="$event.stopPropagation()" href="/api/real-estate/properties/{{tablelistVM.id}}/export-rent-list">h\xE4r.</a></span></div></div></div>');
$templateCache.put('resources/real-estate/show/artifacts/show.html','<div ng-controller="ShowArtifactsCtrl as tablelistVM"><hy-artifacts object-id="tablelistVM.objectId" object-type="tablelistVM.objectType" file-labels="tablelistVM.fileLabels" group="false" count="vm.artifactsCount"></hy-artifacts></div>');
$templateCache.put('resources/search/agreements/agreements/list.html','<div ng-controller="AgreementsListCtrl as agreementsVM"><hy-tableau id="tableau" limit="20" columns="agreementsVM.tableColumns" default-sorting="id DESC" f-get-data="agreementsVM.tableFGetData" f-row-click="agreementsVM.gotoAgreement" f-get-context-info="agreementsVM.getContextInfo" use-url="true"></hy-tableau></div>');
$templateCache.put('resources/requested-accounts/organizations/approve/approve.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-controller="ApproveOrganizationCtrl as vm"><hy-breadcrumb></hy-breadcrumb><h1>BEG\xC4RT KONTO - {{vm.organization.name}}</h1><form name="vm.organizationForm" ng-submit="vm.organizationForm.$valid &amp;&amp; vm.save()" class="spacer-mb-xlarge alert alert--narrow-gray alert--squary" novalidate><div class="row spacer-mb-medium"><div class="col-6 col-lg-3"><hy-info title="F\xF6retag" content="vm.organization.name"></hy-info></div><div class="col-6 col-lg-3"><hy-info title="Organisationsnummer" content="vm.organization.organizationNumber | hyOrgNr"></hy-info></div><div class="col-6 col-lg-3"><hy-info title="HGF kontor" content="vm.organization.office.name"></hy-info></div></div><div class="row spacer-mb-medium"><div class="col-6 col-lg-3"><hy-info title="Namn" content="vm.foundAdmin[0].firstname"></hy-info></div><div class="col-6 col-lg-3"><hy-info title="Namn" content="vm.foundAdmin[0].lastname"></hy-info></div><div class="col-6 col-lg-3"><hy-info title="Namn" content="vm.foundAdmin[0].email"></hy-info></div></div><div class="row"><div class="col-6 col-lg-3"><hy-info title="Gatuadress" content="vm.organization.streetAddress"></hy-info></div><div class="col-6 col-lg-3 info"><hy-info title="Postnummer" content="vm.organization.postalCode"></hy-info></div><div class="col-6 col-lg-3 info"><hy-info title="Ort" content="vm.organization.city"></hy-info></div><div class="col-6 col-lg-3 info"><hy-info title="Telefon" content="vm.organization.phone"></hy-info></div></div><div class="row spacer-mt-large"><div class="col text-right"><button type="button" class="btn btn--secondary spacer-mr-xsmall" ng-click="vm.destroy()" ng-disabled="vm.saving">Neka</button> <button type="button" class="btn spacer-mr-xsmall" ng-click="vm.save()" ng-disabled="vm.saving">Godk\xE4nn</button><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular></div></div></form></div>');
$templateCache.put('resources/real-estate/show/blog/show.html','<div ng-controller="ShowBlogCtrl as tablelistVM"><hy-microblog object-id="tablelistVM.objectId" object-type="tablelistVM.objectType" count="vm.count"></hy-microblog></div>');
$templateCache.put('resources/user/welcome/negotiation-table/list.html','<div ng-controller="NegotiationTableCtrl as tablelistVM"><div class="display-flex flex-direction-row space-between align-items-center spacer-mt-medium"><h3 class="text-margin-reset">De personer jag f\xF6rhandlar med</h3></div><div class="row"><div class="col"><hy-tableau id="tableauPersons" columns="tablelistVM.personsColumns" f-get-data="tablelistVM.getData" default-sorting="numNegotiations DESC" use-url="false" limit="5" in-memory="true" in-memory-excessive-limit="100" f-row-click="tablelistVM.gotoShow" f-get-context-info="tablelistVM.getContextInfo"></hy-tableau></div></div></div>');
$templateCache.put('resources/user/list/external/list.html','<div ng-controller="ListUserExternalCtrl as userExternalVM"><hy-progress-circular ng-hide="userExternalVM.ready" center-on-page size="60px"></hy-progress-circular><div class="row"><div class="col sort-container"><hy-sorter list-columns="userExternalVM.listColumns" order="userExternalVM.sortColumn" direction="userExternalVM.sortDirection" show-label="true" label-position="side"></hy-sorter></div></div><div class="row spacer-mt-small spacer-mb-small"><div class="col text-right" ng-show="userExternalVM.totalCount > userExternalVM.pageLimit"><hy-paginator get-list="userExternalVM.getUsers" page-offset="userExternalVM.pageOffset" page-limit="userExternalVM.pageLimit" total-count="userExternalVM.totalCount"></hy-paginator></div></div><ng-include ng-if="Auth.isExternalUser()" src="\'resources/user/list/external/partials/_table-for-external.html\'"></ng-include><ng-include ng-if="Auth.isHgfUser()" src="\'resources/user/list/external/partials/_table-for-hgf.html\'"></ng-include><div class="row spacer-mt-medium" ng-show="userExternalVM.totalCount > userExternalVM.pageLimit"><div class="col text-right"><hy-paginator get-list="userExternalVM.getUsers" page-offset="userExternalVM.pageOffset" page-limit="userExternalVM.pageLimit" total-count="userExternalVM.totalCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="userExternalVM.totalCount > 0 &amp;&amp; userExternalVM.totalCount <= userExternalVM.pageLimit"><div class="col text-right"><strong>Totalt: </strong>{{userExternalVM.totalCount}}</div></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="userExternalVM.totalCount === 0">Ingen anv\xE4ndare hittades.</div></div>');
$templateCache.put('resources/real-estate/show/agreements/show.html','<div ng-controller="ShowRealEstateFreeAgreementsCtrl as tablelistVM"><div class="row"><div class="col-12"><h2 class="spacer-mt-medium">Avtal</h2><table class="table table--margin-b-reset spacer-mt-medium"><thead class="table__header"><tr><th class="table__th table__td--width-5">Nummer</th><th class="table__th table__td--width-10">Text</th><th class="table__th table__td--width-10">Avtalstyp</th><th class="table__th table__td--width-10">Ansvarig hos HGF</th><th class="table__th table__td--width-10">Ansvarig hos organisation</th></tr><tr><th class="table__th"></th><th class="table__th table__td--width-10"><div class="display-flex"><input type="text" class="form-control form-control--narrow form-control--gray-border" placeholder="Filtrera" ng-model="search.name"></div></th><th class="table__th table__td--width-10"></th><th class="table__th table__td--width-10"><div class="display-flex"><input type="text" class="form-control form-control--narrow form-control--gray-border" placeholder="Filtrera" ng-model="search.hgfNegotiator.fullName"></div></th><th class="table__th table__td--width-10"><div class="display-flex"><input type="text" class="form-control form-control--narrow form-control--gray-border" placeholder="Filtrera" ng-model="search.extNegotiator.fullName"></div></th></tr></thead><tbody><tr class="table__tr" ng-repeat="agreement in tablelistVM.agreements | filter:search" ng-click="tablelistVM.gotoAgreement(agreement.id)"><td class="table__td table__td--nowrap">{{agreement.fancyNumber}}</td><td class="table__td" ng-bind-html="agreement.name | preventDateBreak"></td><td class="table__td">{{tablelistVM.FreeAgreementTypes[agreement.type]}}</td><td class="table__td">{{agreement.hgfNegotiator.fullName}}</td><td class="table__td">{{agreement.extNegotiator.fullName}}</td></tr></tbody></table><div class="alert alert--hide-border alert--slim alert--squary" ng-show="tablelistVM.agreements.length === 0 &amp;&amp; tablelistVM.ready">Inga avtal finns att visa</div><div ng-show="!tablelistVM.ready" class="col-12 display-flex justify-content-center"><hy-progress-circular size="40px"></hy-progress-circular></div></div></div><div class="text-right spacer-mt-medium"><input class="btn" type="button" value="{{ tablelistVM.showAgreementsPane ? \'D\xF6lj\' : \'Visa\' }} bilagor ({{tablelistVM.documents.length}})" ng-click="tablelistVM.toggleAttachmentsPane()"></div><div class="row" ng-show="tablelistVM.showAgreementsPane"><div class="col-12"><h2 class="spacer-mt-medium">Bilagor</h2><table class="table table--margin-b-reset spacer-mt-medium"><thead class="table__header"><tr><th class="table__th table__td--width-40">Namn</th><th class="table__th table__td--width-20">Typ av underlag</th><th class="table__th table__td--width-20">Skapad av</th><th class="table__th table__td--width-20">Uppladdad den</th></tr></thead><tbody><tr class="table__tr table__tr--disable-click" ng-repeat="file in tablelistVM.documents | orderBy:\'-lastModified\'"><td class="table__td"><a href="#" ng-click="tablelistVM.downloadAgreementFile(file); $event.preventDefault();">{{ file.filename }}</a></td><td class="table__td">{{ file.type | documentTypes }}</td><td class="table__td">{{ file.createdBy }}</td><td class="table__td">{{ file.lastModified | date : \'yyyy-MM-dd\' }}</td></tr></tbody></table><div class="alert alert--hide-border alert--slim alert--squary" ng-show="tablelistVM.documents.length === 0 &amp;&amp; tablelistVM.ready">Inga bilagor finns att visa</div><div ng-show="!tablelistVM.ready" class="col-12 display-flex justify-content-center"><hy-progress-circular size="40px"></hy-progress-circular></div></div></div></div>');
$templateCache.put('resources/user/list/delegate/list.html','<div ng-controller="ListUserDelegateCtrl as userDelegateVM"><hy-tableau f-get-data="userDelegateVM.getUsers" f-row-click="userDelegateVM.gotoShow" columns="userDelegateVM.columns" limit="20" in-memory="true" in-memory-excessive-limit="userDelegateVM.Limits.InMemoryMiddleLimit" use-url="true"></hy-tableau></div>');
$templateCache.put('resources/requested-accounts/users/approve/approve.html','<hy-progress-circular size="60px" center-on-page ng-hide="vm.ready"></hy-progress-circular><div class="container" ng-show="vm.ready"><hy-breadcrumb></hy-breadcrumb><div class="row"><div class="col-lg-9 col-12"><h1 class="spacer-mt-large spacer-mb-small">BEG\xC4RT KONTO - {{vm.user.firstname}} {{vm.user.lastname}}</h1></div></div><hr><div class="alert alert--info-bordered alert--hide-border alert--squary"><div class="row spacer-mb-large"><div class="col-12 col-sm-6 col-lg-4"><hy-info title="F\xF6rnamn" content="vm.user.firstname"></hy-info></div><div class="col-12 col-sm-6 col-lg-4"><hy-info title="Efternamn" content="vm.user.lastname"></hy-info></div></div><div class="row"><div class="col-12 col-sm-6 col-lg-4"><hy-info title="E-post" content="vm.user.email"></hy-info></div><div class="col-12 col-sm-6 col-lg-4"><hy-info title="Telefonnummer" content="vm.user.phone ? vm.user.phone : \'Saknas\'"></hy-info></div></div></div><div class="row spacer-mt-large"><div class="col text-right"><button type="button" class="btn btn--secondary spacer-mr-xsmall" ng-click="vm.destroy()" ng-disbled="vm.saving">Neka</button> <button type="button" class="btn spacer-mr-xsmall" ng-click="vm.save()" ng-disabled="vm.saving">Godk\xE4nn</button><hy-progress-circular size="20px" ng-show="vm.saving"></hy-progress-circular></div></div></div>');
$templateCache.put('resources/user/new/partials/_choose-organization.html','<form name="vm.chooseOrganizationForm" ng-submit="vm.chooseOrganizationForm.$valid &amp;&amp; vm.gotoNextStep()"><div class="row"><div class="col spacer-mb-medium"><input name="selectedOrganizationId" id="selectedOrganizationId" type="hidden" ng-model="vm.selectedOrganization.id" required><div class="row spacer-margin-reset"><input class="form-control" placeholder="S\xF6k efter organisation" name="orgFilter" ng-model="vm.inputTextOrgName" id="orgFilter" ng-change="vm.orgAfterType()"></div><hy-progress-circular ng-if="vm.loading" size="30px"></hy-progress-circular><div class="scroller-selector dropdown-menu--filter scroller-selector--modal"><table class="table"><thead class="table__header sticky"><tr><th class="table__th">F\xF6retag</th><th class="table__th">Organisationsnummer</th><th class="table__th">Telefonnummer</th><th class="table__th">Gatuadress</th></tr></thead><tr class="table__tr" ng-repeat="organization in vm.organizations" ng-click="vm.selectOrganization(organization)"><td class="table__td"><div>{{ organization.name }}</div></td><td class="table__td"><div>{{ organization.organizationNumber }}</div></td><td class="table__td"><div>{{ organization.phone }}</div></td><td class="table__td"><div>{{ organization.streetAddress }}</div></td></tr></table></div><div class="spacer-mt-medium"><label>ORGANISATION:</label><div>{{ vm.selectedOrganization.name }}</div></div></div></div></form>');
$templateCache.put('resources/user/new/partials/_choose-process.html','<form name="vm.chooseForm" ng-submit="vm.chooseForm.$valid &amp;&amp; vm.gotoNextStep()" novalidate><div class="row"><div class="col-6 col-lg-5 wizard-choice" ng-class="{\'wizard-choice--active\': vm.choiceMethod === \'SELECT_EXISTING\'}" ng-click="vm.setRadioModelByOutsideClick(\'SELECT_EXISTING\')"><div class="wizard-choice__container"><div class="custom-control custom-control--radio custom-control--radio"><input class="custom-control__input custom-control__input--hidden" type="radio" id="selectExisting" name="radio-group" tabindex="0" ng-model="vm.choiceMethod" value="SELECT_EXISTING" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="selectExisting"><span>S\xF6k organisation</span></label></div></div></div><div class="col-2 col-lg-2 wizard-choice__middle-col"><h4 class="text-margin-reset">eller</h4></div><div class="col-6 col-lg-5 wizard-choice" ng-class="{\'wizard-choice--active\': vm.choiceMethod === \'CREATE_NEW\'}" ng-click="vm.setRadioModelByOutsideClick(\'CREATE_NEW\')"><div class="wizard-choice__container"><div class="custom-control custom-control--radio custom-control--radio"><input class="custom-control__input custom-control__input--hidden" type="radio" id="nyOrganization" name="radio-group" tabindex="1" ng-model="vm.choiceMethod" value="CREATE_NEW" ng-click="$event.stopPropagation()" required> <label class="custom-control__label custom-control__label--large" for="nyOrganization"><span>Ny organisation</span></label></div></div></div></div></form>');
$templateCache.put('resources/user/new/partials/_choose-role.html','<form name="vm.roleForm" ng-submit="vm.roleForm.$valid &amp;&amp; vm.gotoNextStep()"><div class="row"><div class="col-6 col-lg-3 spacer-mb-medium"><input name="inputRoleId" id="inputRoleId" type="hidden" ng-model="vm.selectedRole.value" required><hy-combobox prompt="V\xE4lj roll" items="vm.roles" id-property="value" text-property="key" current-item="vm.selectedRole"></hy-combobox></div></div></form>');
$templateCache.put('resources/user/new/partials/_organization-data.html','<form name="vm.organizationForm" ng-submit="vm.organizationForm.$valid &amp;&amp; vm.gotoNextStep()"><div class="row"><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.organizationForm.inputCompanyName.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputCompanyName.$invalid,\r\n      }"><label for="inputCompanyName">F\xF6retagsnamn</label> <input name="inputCompanyName" id="inputCompanyName" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.name" autocomplete="off" maxlength="250" required></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.organizationForm.inputOrganizationNumber.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputOrganizationNumber.$invalid\r\n      }"><label for="inputOrganizationNumber">Organisationsnummer</label> <input name="inputOrganizationNumber" id="inputOrganizationNumber" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.organizationNumber" ng-pattern="/^\\d{6}-?\\d{4}$/" autocomplete="off" required> <small class="form-group__help">Enligt formatet 1234561234</small> <small ng-show="vm.organizationForm.inputOrganizationNumber.$error.pattern">Ange ett giltigt organisationsnummer</small></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.organizationForm.inputOrganizationNumber.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputOrganizationNumber.$invalid\r\n      }"><label for="inputHgfOfficeId"><div class="display-inline-flex"><div class="spacer-mr-xsmall">HGF kontor</div></div></label> <input name="inputHgfOfficeId" id="inputHgfOfficeId" type="hidden" ng-model="vm.selectedOffice.name"><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width btn--overflow" uib-dropdown-toggle>{{ vm.selectedOffice ? vm.selectedOffice.name : \'V\xE4lj mottagande kontor\' }}</button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="office in vm.offices" type="button" class="dropdown-item" ng-click="vm.organization.hgfOfficeId = office.id; vm.selectedOffice.name = office.name">{{office.name}}</button></div></div></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.organizationForm.inputPhone.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputPhone.$invalid\r\n      }"><label for="inputPhone">Telefonnummer</label> <input name="inputPhone" id="inputPhone" type="tel" class="form-control form-control form-control--narrow form-control--gray-border" ng-pattern="/^[0-9]+$/" ng-model="vm.organization.phone" autocomplete="off"> <small class="form-group__help">Exempel: 0701112233</small> <small ng-show="vm.organizationForm.inputPhone.$error.pattern">Ange ett giltigt telefonnummer</small></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.organizationForm.inputStreetAddress.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputStreetAddress.$invalid\r\n      }"><label for="inputStreetAddress">Gatuadress</label> <input name="inputStreetAddress" id="inputStreetAddress" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.streetAddress" autocomplete="off" maxlength="40"></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.organizationForm.inputPostalCode.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputPostalCode.$invalid\r\n      }"><label for="inputPostalCode">Postnummer</label> <input name="inputPostalCode" id="inputPostalCode" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.postalCode" autocomplete="off" maxlength="5"></div></div><div class="col-6 col-lg-3 spacer-mb-medium"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.organizationForm.inputCity.$touched || vm.organizationForm.$submitted) &amp;&amp; vm.organizationForm.inputCity.$invalid\r\n      }"><label for="inputCity">Ort</label> <input name="inputCity" id="inputCity" type="text" class="form-control form-control form-control--narrow form-control--gray-border" ng-model="vm.organization.city" autocomplete="off" maxlength="40"></div></div></div></form>');
$templateCache.put('resources/user/new/partials/_user-data.html','<form name="vm.userForm" ng-submit="vm.userForm.$valid &amp;&amp; vm.gotoNextStep()"><div class="row"><div class="col-6"><div class="form-group" ng-class="{\r\n      \'form-group--error\': (vm.userForm.inputFirstname.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputFirstname.$invalid,\r\n    }"><label for="inputFirstname">F\xF6rnamn</label> <input name="inputFirstname" id="inputFirstname" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.firstname" autocomplete="off" maxlength="32" required></div></div><div class="col-6"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.userForm.inputEmail.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputEmail.$invalid\r\n      }"><label for="inputEmail">E-post</label> <input name="inputEmail" id="inputEmail" type="email" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.email" ng-disabled="vm.user.token" maxlength="128" ng-change="vm.startCheckEmail()" required><hy-progress-circular size="20px" ng-show="vm.confirmingEmail"></hy-progress-circular><small ng-show="(vm.userForm.inputEmail.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputEmail.$invalid">Ange en fullst\xE4ndig giltig epostadress</small><br><small style="color: red" ng-show="vm.isMailConfirmed===false">Epostadress berkr\xE4ftades inte</small> <small style="color: green" ng-show="vm.isMailConfirmed===true">Epost bekr\xE4ftad</small></div></div></div><div class="row"><div class="col-6"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.userForm.inputLastname.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputLastname.$invalid\r\n    }"><label for="inputLastname">Efternamn</label> <input name="inputLastname" id="inputLastname" type="text" class="form-control form-control--narrow form-control--gray-border" ng-model="vm.user.lastname" autocomplete="off" maxlength="32" required></div></div><div class="col-6"><div class="form-group" ng-class="{\r\n        \'form-group--error\': (vm.userForm.inputPhone.$touched || vm.userForm.$submitted) &amp;&amp; vm.userForm.inputPhone.$invalid\r\n    }"><label for="inputPhone">Telefonnummer</label><br><div ng-hide="vm.foreignPhone" class="form-control-prepend">+46</div><input name="inputPhone" id="inputPhone" type="tel" ng-pattern="/[0-9]{7,}/im" ng-change="vm.tamePhoneNumber()" class="form-control form-control--prepend form-control--gray-border" ng-model="vm.user.phone" placeholder="Exempel: 706532452" autocomplete="off"><br><small ng-show="vm.userForm.inputPhone.$error.pattern">Ange ett giltigt telefonnummer</small></div></div></div></form>');
$templateCache.put('resources/user/list/hgf/list.html','<style>.newMail {\r\n    width: 350px;\r\n  }</style><div ng-controller="ListUserHgfCtrl as userHgfVM"><hy-progress-circular ng-hide="userHgfVM.ready" center-on-page size="60px"></hy-progress-circular><div class="row"><div class="col-12 sort-container"><div style="align-self: end;" class="dropdown dropdown--fancy-caret dropdown--fancy-caret--right" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__filter" uib-dropdown-toggle><span class="text-normal">{{userHgfVM.regionId !== undefined ? userHgfVM.regionName : \'V\xE4lj region\'}}</span></button><div class="dropdown-menu dropdown-menu--right dropdown-menu--filter" uib-dropdown-menu role="menu"><button ng-repeat="choice in userHgfVM.regionChoices" type="button" class="dropdown-item dropdown-item--filter" ng-click="userHgfVM.regionId = choice; userHgfVM.regionName = (choice | regionTypeToDescription); userHgfVM.getUsers(true)">{{choice | regionTypeToDescription}}</button></div></div><div class="spacer-ml-small"><hy-sorter list-columns="userHgfVM.listColumns" order="userHgfVM.sortColumn" direction="userHgfVM.sortDirection" show-label="true" label-position="top"></hy-sorter></div></div></div><div class="row spacer-mt-small spacer-mb-small"><div class="col text-right" ng-show="userHgfVM.totalCount > userHgfVM.pageLimit"><hy-paginator get-list="userHgfVM.getUsers" page-offset="userHgfVM.pageOffset" page-limit="userHgfVM.pageLimit" total-count="userHgfVM.totalCount"></hy-paginator></div></div><table class="table table--margin-b-reset spacer-mt-large"><thead class="table__header"><tr><th class="table__th">F\xF6rnamn</th><th class="table__th">Efternamn</th><th class="table__th">E-post</th><th class="table__th">Telefonnummer</th><th class="table__th">Part</th><th></th></tr><tr><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="userHgfVM.inputTextFirstName" hy-enter="userHgfVM.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="userHgfVM.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="userHgfVM.inputTextLastName" hy-enter="userHgfVM.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="userHgfVM.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th class="table__th"><div class="display-flex"><input type="text" class="form-control form-control--search" placeholder="S\xF6k" ng-model="userHgfVM.inputTextEmail" hy-enter="userHgfVM.getUsers(true)"> <a href="#" class="btn--icon btn--icon-search" ng-click="userHgfVM.getUsers(true); $event.preventDefault();"><span class="sr-only">S\xF6k</span></a></div></th><th></th><th></th><th></th></tr></thead><tr class="table__tr" ng-repeat="user in userHgfVM.users" ng-class="{\'table__tr--muted\' : !userHgfVM.ready}"><td ng-click="userHgfVM.gotoShow(user.id)" class="table__td">{{ user.firstname }}</td><td ng-click="userHgfVM.gotoShow(user.id)" class="table__td">{{ user.lastname }}</td><td class="table__td"><div ng-if="vm.editedUser.id === user.id" class="unterpopup newMail"><form name="vm.newMailForm" ng-submit="event.preventDefault()" novalidate><input class="form-control" type="email" ng-pattern="/^(([^<>()\\[\\]\\\\.,;:\\s@\u201C]+(\\.[^<>()\\[\\]\\\\.,;:\\s@\u201C]+)*)|(\u201C.+\u201C))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$/" id="emailEdit{{user.id}}" ng-model="vm.newEmail"><div class="unterpopup-footer"><button class="btn spacer-mt-xsmall" ng-disabled="!vm.newMailForm.$valid || (vm.newEmail === vm.editedUser.email)" ng-click="vm.endEditEmail()">\xC4ndra e-post</button> <a class="link--turquoise link--no-underline" ng-click="vm.editedUser = null">Avbryt</a></div></form></div><div class="display-flex full-width"><div ng-click="userHgfVM.gotoShow(user.id)" class="action-buttons">{{ user.email }}</div><a href="#" ng-if="Auth.canDo(\'editUserEmail\')" class="btn--icon btn--icon-pen btn--icon-bottom-right" ng-click="vm.beginEditEmail(user); $event.preventDefault();"></a></div></td><td ng-click="userHgfVM.gotoShow(user.id)" class="table__td">{{ user.phone }}</td><td ng-click="userHgfVM.gotoShow(user.id)" class="table__td">{{ user.party | hyParties }}</td><td class="table__td"><a href="#" class="btn--icon btn--icon-bin" ng-if="Auth.canDo(\'deleteUser\')" ng-click="vm.deleteUser(user.id);"></a></td></tr></table><div class="row spacer-mt-medium" ng-show="userHgfVM.totalCount > userHgfVM.pageLimit"><div class="col text-right"><hy-paginator get-list="userHgfVM.getUsers" page-offset="userHgfVM.pageOffset" page-limit="userHgfVM.pageLimit" total-count="userHgfVM.totalCount"></hy-paginator></div></div><div class="row spacer-mt-medium" ng-show="userHgfVM.totalCount > 0 &amp;&amp; userHgfVM.totalCount <= userHgfVM.pageLimit"><div class="col text-right"><strong>Totalt: </strong>{{userHgfVM.totalCount}}</div></div><div class="alert alert--hide-border alert--slim alert--squary" ng-show="userHgfVM.totalCount === 0">Ingen anv\xE4ndare hittades.</div></div>');
$templateCache.put('resources/negotiation/show/modals/recall-items/recallItemsModal.html','<form name="moveItemsForm"><div class="modal-header modal-header--small"><h2 class="text-margin-top-reset text-margin-bottom-reset">\xC5terkalla {{ vm.apartmentMode ? \'l\xE4genheter\' : \'fastigheter\'}}</h2></div><div class="modal-body modal-body--small"><p class="line-height-narrow" id="modal-body">{{ vm.apartmentMode ? \'L\xE4genheterna\' : \'Fastigheterna\' }} tas bort fr\xE5n f\xF6rhandlingen om du v\xE4ljer att \xE5terkalla dem. De g\xE5r inte att \xE5terinf\xF6ra.</p></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt </a><button class="btn spacer-mr-xsmall" ng-click="vm.recallItems()">\xC5terkalla {{ vm.apartmentMode ? \'l\xE4genheter\' : \'fastigheter\' }}</button></div></form>');
$templateCache.put('resources/negotiation/show/modals/without-rentlist/withoutRentlistModal.html','<div class="container"><div class="modal-body modal-body--medium" id="modal-body"><h2 class="spacer-mb-reset spacer-mt-medium">Fastigheter som saknar ing\xE5ende {{vm.isNewlyConstructedApartment ? \'l\xE4genhetslista\':\'hyreslista\'}}</h2><p>Ing\xE5ende hyresinformation saknas f\xF6r det listade best\xE5ndet nedan. Ladda upp kompletterande {{vm.isNewlyConstructedApartment ? \'l\xE4genhetslista\':\'hyreslista\'}} f\xF6r att kunna p\xE5b\xF6rja \xF6verenskommelse.</p><table class="table spacer-mt-medium"><tr class="table__header"><th class="table__th table__td--width-30">Fastighetsbeteckning</th><th class="table__th table__td--width-15">Kommun</th><th class="table__th table__td--width-15">LM distrikt</th></tr><tr class="table__tr table__tr--disable-click" ng-repeat="property in vm.withoutRentlist"><td class="table__td">{{property.propertyDesignation}}</td><td class="table__td">{{property.municipality}}</td><td class="table__td">{{property.district}}</td></tr></table></div><div class="modal-footer modal-footer--medium"><button type="button" class="btn btn--secondary" ng-click="vm.close()">St\xE4ng</button></div></div>');
$templateCache.put('resources/negotiation/show/agreements/modal/conditions.html','<div class="modal-header modal-header--small"><h3 class="text-margin-reset">Villkor f\xF6r \xF6verenskommelse</h3></div><div class="modal-body modal-body--small"><hy-checkbox name="setR" ng-if="Auth.isHgfUser()" title="Medlemsutskick" model="vm.isReadyToSend"></hy-checkbox><xsmall><i>F\xF6r information till Kvarteret om utskick ska ske.<br>Ibockad ruta = JA / Urbockad ruta = NEJ</i></xsmall><div ng-if="vm.newRealEstate"><hy-checkbox name="setP" title="G\xE4ller presumtionshyra f\xF6r best\xE5ndet?" model="vm.isPresumtiveRent"></hy-checkbox><hy-checkbox name="setI" title="S\xF6ks investeringsst\xF6d?" model="vm.isInvestmentProperty"></hy-checkbox></div></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button type="button" class="btn btn--medium-width" ng-click="vm.confirm()">Ja</button></div>');
$templateCache.put('resources/negotiation/show/agreements/modal/confirmationBlockedModal.html','<div class="modal-header modal-header--small"><h3 class="text-margin-reset">Har f\xF6ljande \xE5tg\xE4rder vidtagits?</h3></div><div class="modal-body modal-body--small"><p>Det \xE4r de lokala parterna som har ansvaret f\xF6r att f\xF6rhandlingarna genomf\xF6rs p\xE5 ett ansvarfullt s\xE4tt:</p><div class="display-flex align-items-top"><div class="custom-control custom-control--checkbox spacer-mt-xsmall"><input class="custom-control__input" type="checkbox" name="checkOne" id="checkOne" ng-model="vm.checkOne"> <label class="custom-control__label" for="checkOne"></label></div><span>N\xE4r en lokal tvist uppkommer rekommenderas parterna att h\xF6ra av sig till respektive huvudorganisation</span></div><div class="display-flex align-items-top spacer-mt-small"><div class="custom-control custom-control--checkbox"><input class="custom-control__input" type="checkbox" name="checkTwo" id="checkTwo" ng-model="vm.checkTwo"> <label class="custom-control__label" for="checkTwo"></label></div><span>F\xF6rhandlingarna ska vara helt utt\xF6mda lokalt</span></div><div class="spacer-mt-large" ng-if="vm.showDropdown"><label for="inputBlockingAuthority">Strandningsinstans</label> <input name="inputBlockingAuthority" id="inputBlockingAuthority" type="hidden" ng-model="vm.blockedBy" required><div class="dropdown dropdown--no-border dropdown--fancy-caret" uib-dropdown><button type="button" class="btn btn--squary btn--squary-white btn--squary-white__sort btn--full-width" uib-dropdown-toggle>{{vm.blockedBy !== undefined ? vm.blockingAuthorityName : \'V\xE4lj strandningsinstans\'}}</button><div class="dropdown-menu dropdown-menu--full-width dropdown-menu--scroll" uib-dropdown-menu role="menu"><button ng-repeat="choice in vm.blockingAuthorityChoices" type="button" class="dropdown-item" ng-click="vm.blockedBy = choice.id; vm.blockingAuthorityName = choice.name">{{choice.name}}</button></div></div></div><div class="spacer-mt-large">{{vm.subtitle}}</div></div><div class="modal-footer modal-footer--small"><a class="link--turquoise link--no-underline" ng-click="vm.cancel()">Avbryt</a> <button type="button" class="btn btn--medium-width" ng-click="vm.confirm()" ng-disabled="!(vm.checkOne &amp;&amp; vm.checkTwo &amp;&amp; (!vm.showDropdown ||\xA0vm.blockedBy !== undefined))">Ja</button></div>');
$templateCache.put('resources/negotiation/show/modals/import-rentlist/importRentlistModal.html','<hy-progress-circular size="60px" ng-show="vm.saving" center-on-page="true"></hy-progress-circular><ng-include ng-if="vm.showPage===\'Start\'" src="\'resources/negotiation/show/modals/import-rentlist/partials/_file-upload-rent-list.html\'"></ng-include><ng-include ng-if="vm.showPage===\'Success\'" src="\'resources/negotiation/show/modals/import-rentlist/partials/_examine-first-line.html\'"></ng-include>');
$templateCache.put('resources/negotiation/show/modals/forging-rentlist/forging-rentlist-modal.html','<div class="container"><div class="modal-body modal-body--medium" id="modal-body"><div class="row spacer-mt-small"><h2 class="spacer-margin-reset">V\xE4lj l\xE4genheter ({{vm.apartmentCount}})</h2></div><div class="row" ng-if="!vm.busy"><p>Hitta \xF6nskad egendom, kontrollera sedan n\xF6dv\xE4ndiga l\xE4genheter och tryck p\xE5 <b>L\xE4gg till</b> -knappen f\xF6r att l\xE4gga till dem i \xE4ndringslistan</p></div><hy-apartment-selector ng-if="!vm.busy" is-modal="true" incoming-properties="vm.selector.incomingProperties" f-get-properties="vm.selector.fgetProperties" f-get-apartments="vm.selector.fgetApartments" apartments-arguments="{ exceptM: !Auth.isHgfUser() }" model="vm.selectedApartments" is-insane="true"></hy-apartment-selector><div class="col text-right spacer-mb-small spacer-mt-small"><hy-progress-circular size="20px" ng-if="vm.busy"></hy-progress-circular><button class="btn" ng-click="vm.doneSelecting()" ng-if="vm.selectedApartments && vm.selectedApartments.length" ng-disabled="vm.busy">Spara</button> <button class="btn btn--secondary" ng-disabled="vm.busy" ng-click="vm.cancel()">St\xE4ng</button></div></div></div>');
$templateCache.put('resources/negotiation/show/history/partials/_agreement.html','<span ng-if="event.action === \'created\'">Skapade en <a class="link--turquoise text-reset" href="/negotiations/{{event.negotiationId}}/agreements/{{event.resource.id}}">\xF6verenskommelse</a> </span><span ng-if="event.action === \'updated\'">Uppdaterade en <a class="link--turquoise text-reset" href="/negotiations/{{event.negotiationId}}/agreements/{{event.resource.id}}">\xF6verenskommelse</a>.<ul class="list list--unstyled display-inline text-gray"><li class="display-inline" ng-if="(event.resource.agreedByAssociation ||\xA0event.resource.agreedByOwner) &amp;&amp; !(event.resource.type || event.resource.signed)">Klickade p\xE5 "Vi \xE4r \xF6verens".</li><li class="display-inline" ng-if="event.resource.numApartments">Fastighetsbest\xE5nd har uppdaterats.</li><li class="display-inline" ng-if="event.resource.type">Strandningen har \xE5terupptagits.</li><li class="display-inline" ng-if="event.resource.signedAt">Signeratdatum sattes till {{event.resource.signedAt.cur | date : "yyyy-MM-dd"}}.</li><li class="display-inline" ng-if="event.resource.signed">Signerat avtal har {{event.resource.signed.cur ? \'laddats upp\': \'tagits bort\'}}.</li><li class="display-inline" ng-if="event.resource.name">\xD6verenskommelsetext har uppdaterats.</li><li class="display-inline" ng-if="event.resource.rentEffectiveFrom"><span ng-if="event.resource.rentEffectiveFrom.cur">Ikrafttr\xE4dandedatum sattes till {{event.resource.rentEffectiveFrom.cur | date : "yyyy-MM-dd"}}.</span> <span ng-if="!event.resource.rentEffectiveFrom.cur">Ikrafttr\xE4dandedatum har tagits bort.</span></li><li class="display-inline" ng-if="event.resource.releaseDate"><span ng-if="event.resource.releaseDate.cur">Offentligg\xF6randedatum sattes till {{event.resource.releaseDate.cur | date : "yyyy-MM-dd"}}.</span></li><li class="display-inline" ng-if="event.resource.rentFileId">Hyreslista med nya \xF6verenskomna hyror har laddats upp.</li></ul></span><span ng-if="event.action === \'deleted\'">Tog bort en \xF6verenskommelse.</span>');
$templateCache.put('resources/negotiation/show/history/partials/_apartment.html','<span ng-if="event.action === \'movedIn\'">Flyttade l\xE4genheten {{event.resource.nationalNo.cur}} ({{event.resource.ownerNo.cur}}), {{event.resource.streetAddress.cur}} ({{event.resource.municipality.cur}} kommun) fr\xE5n f\xF6rhandlingen <a class="link--turquoise text-reset" href="/negotiations/{{event.resource.negotiationId.prev}}">{{event.resource.caseNumber.prev}} </a>till denna f\xF6rhandling. </span><span ng-if="event.action === \'movedIn\'">Flyttade l\xE4genheten {{event.resource.nationalNo.cur}} ({{event.resource.ownerNo.cur}}), {{event.resource.streetAddress.cur}} ({{event.resource.municipality.cur}} kommun) fr\xE5n f\xF6rhandlingen <a class="link--turquoise text-reset" href="/negotiations/{{event.resource.negotiationId.prev}}">{{event.resource.caseNumber.prev}} </a>till denna f\xF6rhandling. </span><span ng-if="event.action === \'recalled\'">\xC5terkallade l\xE4genheten {{event.resource.nationalNo.cur}} ({{event.resource.ownerNo.cur}}), {{event.resource.streetAddress.cur}} ({{event.resource.municipality.cur}} kommun) fr\xE5n f\xF6rhandlingen.</span>');
$templateCache.put('resources/negotiation/show/history/partials/_meeting.html','<span ng-if="event.action === \'created\'">Skapade ett <a class="link--turquoise text-reset" href="/negotiations/{{event.negotiationId}}?view=meetings">f\xF6rhandlingsm\xF6te.</a> </span><span ng-if="event.action === \'updated\'">Uppdaterade ett <a class="link--turquoise text-reset" href="/negotiations/{{event.negotiationId}}?view=meetings">f\xF6rhandlingsm\xF6te.</a><ul class="list list--unstyled display-inline text-gray"><li class="display-inline" ng-if="event.resource.fileName"><span ng-if="event.resource.fileName.cur">Dagordningen <i>{{event.resource.fileName.cur}}</i> har laddats upp.</span> <span ng-if="!event.resource.fileName.cur">Dagordning <i>{{event.resource.fileName.prev}}</i> har tagits bort.</span></li><li class="display-inline" ng-if="event.resource.location">Plats har \xE4ndrats.</li><li class="display-inline" ng-if="event.resource.startAt || event.resource.endAt">Tid har \xE4ndrats.</li><li class="display-inline" ng-if="event.resource.hgfParticipants || event.resource.externalParticipants">Uppdaterade m\xF6tesdeltagare.</li></ul></span><span ng-if="event.action === \'deleted\'">Tog bort ett f\xF6rhandlingsm\xF6te.</span>');
$templateCache.put('resources/negotiation/show/history/partials/_negotiation.html','<span ng-if="event.action === \'updated\'">Uppdaterade f\xF6rhandlingen.<ul class="list list--unstyled display-inline text-gray"><li class="display-inline" ng-if="event.resource.state">Status \xE4ndrades fr\xE5n <i>{{event.resource.state.prev | hyNegotiationStates}}</i> till <i>{{event.resource.state.cur | hyNegotiationStates}}</i>.</li><li class="display-inline" ng-if="event.resource.firstNegotiationMeeting &amp;&amp; event.resource.firstNegotiationMeeting.cur">F\xF6rsta f\xF6rhandlingsm\xF6tet sattes till {{event.resource.firstNegotiationMeeting.cur | date:\'short\'}}.</li><li class="display-inline" ng-if="event.resource.motivation">Motiv har uppdaterats.</li><li class="display-inline" ng-if="event.resource.initialDemandNumber || event.resource.initialDemandUnit || event.resource.initialDemandString">Yrkande har uppdaterats.</li></ul></span>');
$templateCache.put('resources/negotiation/show/history/partials/_negotiationParticipant.html','<span ng-if="event.action === \'created\'">La till representanten {{event.resource.firstname.cur}} {{event.resource.lastname.cur}} till f\xF6rhandlingen </span><span ng-if="event.action === \'updated\'">Uppdaterade en representant.<ul class="list list--unstyled display-inline text-gray"><li class="display-inline" ng-if="event.resource.role"><span ng-if="event.resource.role.cur === 172">La till rollen Ansvarig f\xF6rhandlare.</span></li><li class="display-inline" ng-if="event.resource.role"><span ng-if="event.resource.role.cur === 2">Tog bort rollen Ansvarig f\xF6rhandlare fr\xE5n en representant.</span></li></ul></span><span ng-if="event.action === \'deleted\'">Tog bort representanten {{event.resource.firstname.cur}} {{event.resource.firstname.lastname}}</span>');
$templateCache.put('resources/negotiation/show/history/partials/_property.html','<span ng-if="event.action === \'movedIn\'">Flyttade fastigheten <a class="link--turquoise text-reset" href="/properties/{{event.resource.id}}">{{event.resource.propertyDesignation.cur}} ({{event.resource.municipality.cur}} kommun) </a>fr\xE5n f\xF6rhandlingen <a class="link--turquoise text-reset" href="/negotiations/{{event.resource.negotiationId.prev}}">{{event.resource.caseNumber.prev}} </a>till denna f\xF6rhandling. </span><span ng-if="event.action === \'movedOut\'">Flyttade fastigheten <a class="link--turquoise text-reset" href="/properties/{{event.resource.id}}">{{event.resource.propertyDesignation.cur}} ({{event.resource.municipality.cur}} kommun) </a>fr\xE5n denna f\xF6rhandling till f\xF6rhandlingen <a class="link--turquoise text-reset" href="/negotiations/{{event.resource.negotiationId.cur}}">{{event.resource.caseNumber.cur}} </a>. </span><span ng-if="event.action === \'recalled\'">\xC5terkallade fastigheten <a class="link--turquoise text-reset" href="/properties/{{event.resource.id}}">{{event.resource.propertyDesignation.cur}} ({{event.resource.municipality.cur}} kommun) </a>fr\xE5n f\xF6rhandlingen.</span>');
$templateCache.put('resources/negotiation/show/history/partials/_rentFile.html','<span ng-if="event.action === \'created\'">Laddade upp en hyreslista inf\xF6r f\xF6rhandlingen. <a class="link--turquoise text-reset" href="/negotiations/{{event.negotiationId}}?view=overview">\xD6versiktssidan</a>.</span>');
$templateCache.put('resources/negotiation/show/agreements/partials/_agreement.html','<div class="row"><div class="col-2"><b>{{ agreement.sequenceNumber }}</b><hy-donut part="(agreement | doneTasksCounter: vm.negotiation.type).done" total="(agreement | doneTasksCounter: vm.negotiation.type).total" max-height="115px" status-finished="vm.negotiation.isFinished"></hy-donut>{{(agreement | doneTasksCounter: vm.negotiation.type).done === (agreement | doneTasksCounter: vm.negotiation.type).total ? \'Alla steg \xE4r avklarade\' : \'Steg avklarade\'}}<div class="spacer-mt-medium"><strong>Fastigheter:</strong><br><hy-array-display model="agreement.propertiesArray"></hy-array-display></div></div><div class="col-5"><div class="spacer-mb-medium"><div><strong class="text-margin-reset">1. Ikrafttr\xE4dandedatum:</strong></div>{{ agreement.rentEffectiveFrom ? (agreement.rentEffectiveFrom | date : "yyyy-MM-dd") : \'&mdash;\' }}</div><div class="spacer-mb-medium"><div><strong class="text-margin-reset">2. Offentligg\xF6randedatum:</strong></div>{{agreement.releaseDate ? (agreement.releaseDate | date : "yyyy-MM-dd") : \'&mdash;\'}}</div><div class="spacer-mb-medium word-break-balanced"><div><strong class="text-margin-reset">3. \xD6verenskommelsetext:</strong></div><span ng-if="agreement.name" ng-bind-html="agreement.name | preventDateBreak"></span> <span ng-if="!agreement.name">&mdash;</span></div><div ng-show="agreement.type === agreementVM.AgreementTypes.AGREEMENT" class="spacer-mb-medium"><div><strong class="text-margin-reset">4. Fastighetsbest\xE5nd:</strong></div>{{ agreement.numApartments > 0 ? agreement.numApartments + (agreement.numApartments === 1 ? \' l\xE4genhet\' : \' l\xE4genheter\') : \'&mdash;\' }}<br>{{ agreement.properties.length > 0 ? agreement.properties.length + (agreement.numApartments === 1 ? \' fastighet\' : \' fastigheter\') : \'&mdash;\' }}</div><div ng-show="agreement.type === agreementVM.AgreementTypes.AGREEMENT_WITHOUT_RENTLIST" class="spacer-mb-medium"><div><strong class="text-margin-reset">4. Fastighetsbest\xE5nd:</strong></div>{{ agreement.numProperties > 0 ? agreement.numProperties + (agreement.numProperties === 1 ? \' fastighet\' : \' fastigheter\') : \'&mdash;\' }}</div></div><div class="col-5"><div ng-show="agreement.type === agreementVM.AgreementTypes.AGREEMENT" class="spacer-mb-medium"><div><strong class="text-margin-reset">5. Hyreslista:</strong></div>{{agreement.rentFileId ? \'Uppladdad\' : \'&mdash;\'}}</div><div ng-show="agreement.type === agreementVM.AgreementTypes.AGREEMENT_WITHOUT_RENTLIST" class="spacer-mb-medium"><div><strong class="text-margin-reset">5. Bilagor:</strong></div>{{ agreement.numDocuments > 0 ? \'Uppladdad\' : \'&mdash;\' }}</div><div class="spacer-mb-medium"><div><strong class="text-margin-reset">6. Signerat \xF6verenskommelse:</strong></div><div ng-show="agreement.agreedByAssociation &amp;&amp; agreement.agreedByOwner">Alla parter signerat</div><div ng-show="(agreement.agreedByAssociation || agreement.agreedByOwner) &amp;&amp; !(agreement.isAgreed)"><div ng-show="agreementVM.isHgfUser">{{ agreement.agreedByAssociation ? \'V\xE4ntar p\xE5 motpart\' : \'V\xE4ntar p\xE5 dig\' }}</div><div ng-hide="agreementVM.isHgfUser">{{ agreement.agreedByOwner ? \'V\xE4ntar p\xE5 motpart\' : \'V\xE4ntar p\xE5 dig\' }}</div></div><div ng-show="!(agreement.agreedByAssociation || agreement.agreedByOwner)">&mdash;</div>\xD6verenskommelse {{agreement.signed ? \'signerad\' : \'inte signerad\'}}<br>{{ agreement.signed &amp;&amp; agreement.signedAt ? \'(Signeratdatum: \'+(agreement.signedAt | date : "yyyy-MM-dd")+\')\' : \'\' }}</div></div></div>');
$templateCache.put('resources/negotiation/show/agreements/partials/_agreementNewProduction.html','<div class="row"><div class="col-2"><hy-donut part="(agreement | doneTasksCounter: vm.negotiation.type).done" total="(agreement | doneTasksCounter: vm.negotiation.type).total" max-height="115px" status-finished="vm.negotiation.isFinished"></hy-donut><div class="text-center">{{(agreement | doneTasksCounter: vm.negotiation.type).done === (agreement | doneTasksCounter: vm.negotiation.type).total ? \'Alla steg \xE4r avklarade\' : \'Steg avklarade\'}}</div></div><div class="col-5"><div class="spacer-mb-medium"><div><strong class="text-margin-reset">1. Ikrafttr\xE4dandedatum:</strong></div>{{ agreement.rentEffectiveFrom ? (agreement.rentEffectiveFrom | date : "yyyy-MM-dd") : \'&mdash;\' }}</div><div class="spacer-mb-medium"><div><strong class="text-margin-reset">2. Offentligg\xF6randedatum:</strong></div>{{agreement.releaseDate ? (agreement.releaseDate | date : "yyyy-MM-dd") : \'&mdash;\'}}</div><div class="spacer-mb-medium word-break-balanced"><div><strong class="text-margin-reset">3. \xD6verenskommelsetext:</strong></div><span ng-if="agreement.name" ng-bind-html="agreement.name | preventDateBreak"></span> <span ng-if="!agreement.name">&mdash;</span></div></div><div class="col-5"><div class="spacer-mb-medium"><div><strong class="text-margin-reset">4. Hyreslista:</strong></div>{{agreement.rentFileId ? \'Uppladdad\' : \'&mdash;\'}}</div><div class="spacer-mb-medium"><div><strong class="text-margin-reset">5. Signerat \xF6verenskommelse:</strong></div>{{(agreement.signed || (agreement.agreedByAssociation &amp;&amp; agreement.agreedByOwner) ) ? \'Signerat\' : \'&mdash;\'}}<br>{{ agreement.signed &amp;&amp; agreement.signedAt ? \'(Signeratdatum: \'+(agreement.signedAt | date : "yyyy-MM-dd")+\')\' : \'\' }}</div></div></div>');}]);
/* jshint +W071 */
/* jshint +W109 */
/* jshint +W101 */

})();